import React, { useEffect, useRef, useState, useCallback, useMemo, lazy, Suspense } from 'react'
import { createPortal } from 'react-dom'
import './App.css'
import { getClientDisplayName, getClientInitial } from './utils/clientDisplay'
import { startManagedInterval } from './polling'
import { reloadOnceForStaleChunk, isStaleChunkError } from './utils/staleChunk'
import { captureCrashSnapshot, reportAppCrash } from './reliability'
import { resolveCallActivityOwner } from './lib/phone/callRouting'
import { BP_BRANDING_VERSION, BP_LOGO_DARK, BP_LOGO_ALT } from './lib/branding/brand'
import { normalizeCallOutcome } from './lib/phone/callOutcome'
import { formatPhoneDisplay, formatPhoneField, formatCallTimer, ambiguousContactLabel } from './lib/phone/format'
import { computePhoneScale } from './lib/phone/zoom'
import { LiveTranscriber, type TranscriptLine } from './lib/phone/sttStream'
import { groupTranscript } from './lib/phone/groupTranscript'
// Post-call wrap-up assignment picker (call-assign-link-v1). The picker search /
// nested-project probe / row-flatten mechanics now live in the shared
// CallAssignPicker component; App only keeps the AssignRow type for the
// selectAssignRow callback.
import { type AssignRow } from './lib/phone/assignSearch'
import CallAssignPicker from './shared/CallAssignPicker'
import { applyCallOwnerOptimistic, ownerPatchFromSelection, restoreCallOwnerRow, type CallOwnerSelection } from './lib/calls/callAssignCell'
import { isProjectOwnedRow } from './lib/calls/dealRollup'
import { resolveMeResponse } from './lib/identity/resolveMeResponse'
import { sameIdSeq, sameTimeCounts } from './lib/polling/pollerIdentity'
// Contacts name-fix + multi-contact-person model (canonical spec: marker
// cp-name-normalize-v1). Shared by the Smart Create panel (agent E) and the
// record-detail + admin surfaces (agent F). Server mirrors this same spec.
import {
  parsePersonName,
  validatePersonNameInput,
  validateOrgNameInput,
  splitCoupleName,
  composeHouseholdDisplay,
  classifyLegacyName,
  type NameValidationResult,
  type NameSuggestion,
  type ParsedName,
  type SplitCoupleResult,
} from './lib/contacts/nameNormalize'
// Profile-card first/last derivation (marker cp-couple-card-names-v1). Prefers
// the PRIMARY contact_person over parsing the composed household preferred_name
// (which yields garbage like FIRST 'Elaine &' / LAST '& Peter Lowe' for couples).
import { deriveCardNames, DERIVE_CARD_NAMES_MARKER } from './lib/contacts/cardNames'
// Smart Create uniform person-card payload mapper (marker cp-create-cards-v1):
// card 0 = primary contact; one primary email + one primary phone across ALL
// cards become the record's preferred_email/preferred_phone.
import { buildCreatePersonsPayload, CREATE_CARDS_MARKER, CREATE_MAIN_MARKER } from './lib/contacts/createCards'
// Smart Create Full-name autocomplete type-aware selection mapper (marker
// cp-suggest-orgmap-v1): org suggestions route their display name to the
// Organization Name field and prefill the person card from the org's primary
// contact person — never pasting an org name into the person Full-name field.
import { mapSuggestionSelection, CREATE_SUGGEST_ORGMAP_MARKER } from './lib/contacts/suggestSelect'
import { KnowledgeBasePanel } from './views/knowledge-base/KnowledgeBasePanel'
import PhoneTabSkeleton from './shared/PhoneTabSkeleton'
import PhotoCropModal, { fileFromImageUrl } from './shared/PhotoCropModal'
import { FinancialsCards } from './views/financials/FinancialsCards'
import { LABELS } from './views/financials/labels'
import type { FinancialDoc, FinancialGroup } from './views/financials/types'
import { parseWorkspaceHash, workspacePath } from './views/projects/helpers'
const AdminKnowledgeBase = lazy(() => import('./views/knowledge-base/AdminKnowledgeBase'))
const MyDocuments = lazy(() => import('./views/my-documents/MyDocuments'))
const MyTimeView  = lazy(() => import('./views/my-time/MyTime'))
// Entity 360 — additive relationship-graph + rollups card on the org/contact
// record. Lazy chunk; gated behind FLAG_ENTITY360 and renders nothing on
// error/empty so it never blocks the underlying record.
const Entity360 = lazy(() => import('./views/entity360/Entity360'))
const ClientPortalCard = lazy(() => import('./views/entity360/ClientPortalCard'))
const DealDetailAbout = lazy(() => import('./views/deals/detail/DealDetailAbout'))
const DealDetailActivities = lazy(() => import('./views/deals/detail/DealDetailActivities'))
const ContactDetailAbout = lazy(() => import('./views/contacts/detail/ContactDetailAbout'))
const ContactDetailActivities = lazy(() => import('./views/contacts/detail/ContactDetailActivities'))
const ProjectDetailAbout = lazy(() => import('./views/projects/detail/ProjectDetailAbout'))
// (Standalone org "Relationships" section removed — the single relationships
// surface is now the compact "RELATIONSHIPS" box inside the Organization
// profile card; the old RelationshipsSection chunk is no longer rendered.)
// Stackable nested mini-360 pop-up. The hook is eager (must run at the top of
// the component); the host renders nothing while the stack is empty.
import { useEntityPeek, EntityQuickPeekHost, type PeekEntity } from './shared/EntityQuickPeek'
// Support & feedback hub — topbar lifebuoy (marker: support-hub-icon). Eager
// button + unseen-answers badge; the modal itself is a lazy chunk loaded on
// first click (src/shared/SupportHubModal.tsx, marker: support-hub-modal).
import SupportHub from './shared/SupportHub'
// Roll-out flag: ship OFF first, flip ON after tester PASS. Removing this block
// reverts to the exact current record with zero residue.
const FLAG_ENTITY360 = true

// Strangler-fig migration: Carly-meeting admin pages live in their own
// folder so they ship as separate JS chunks loaded on demand. See
// `docs/ENTERPRISE_MIGRATION.md` for the migration plan.
const PrefsOrganization       = lazy(() => import('./views/preferences/admin/Organization'))
const PrefsPermissions        = lazy(() => import('./views/preferences/admin/Permissions'))
const TimesheetsScheduleEditor = lazy(() => import('./views/hr-center/ScheduleEditor'))
const PrefsTerritories        = lazy(() => import('./views/preferences/admin/Territories'))
const PrefsLeadRouting        = lazy(() => import('./views/preferences/admin/LeadRouting'))
const PrefsInboxes            = lazy(() => import('./views/preferences/admin/Inboxes'))
const PrefsSavedViews         = lazy(() => import('./views/preferences/admin/SavedViews'))
const PrefsDuplicates         = lazy(() => import('./views/preferences/admin/Duplicates'))
const PrefsNumbering          = lazy(() => import('./views/preferences/admin/Numbering'))
// R8 strangler-fig (May 2026): Invoice Scanner settings became a real,
// persisted, lazy-loaded chunk (/api/admin/org-settings, invoice_scanner.*
// prefix, org_settings KV table from migration 094). The sibling
// subcontractor/estimation tabs of that era were later excised.
const PrefsInvoiceScannerSettings = lazy(() => import('./views/preferences/admin/InvoiceScannerSettings'))
// R5 stateful prefs tabs (May 2026 strangler-fig). Audit-log ships in two
// pieces (header tabs + body card) — both share the same dynamic import
// so Vite emits a single chunk. Header is loaded as a named export.
const PrefsAuditLog           = lazy(() => import('./views/preferences/admin/AuditLog'))
const PrefsAuditLogHeader     = lazy(() => import('./views/preferences/admin/AuditLog').then(m => ({ default: m.AuditLogHeader })))
const PrefsImportExport       = lazy(() => import('./views/preferences/admin/ImportExport'))
const PrefsBackup             = lazy(() => import('./views/preferences/admin/Backup'))
// R5 stateful prefs round 2 (May 2026 strangler-fig): the five remaining
// heavyweight prefs surfaces — Account Defaults (4 sub-tabs + currency
// modal), Users & Teams CRUD, RingCentral Calling root, Notifications
// (email/desktop delivery), and General profile (7 sub-tabs).
// State stays in App.tsx and threads through props.
const PrefsAccount            = lazy(() => import('./views/preferences/admin/Account'))
const PrefsUsers              = lazy(() => import('./views/preferences/admin/Users'))
const PrefsCallingRoot        = lazy(() => import('./views/preferences/admin/CallingRoot'))
const PrefsNotifications      = lazy(() => import('./views/preferences/admin/Notifications'))
const PrefsGeneral            = lazy(() => import('./views/preferences/admin/General'))
// R7 strangler-fig: Marketing Email/Forms + Object Field editor —
// see migrations 097-099. Each ships as its own JS chunk.
// (Calling.tsx is now nested inside CallingRoot.tsx and loaded by it.)
const PrefsEmailTemplates     = lazy(() => import('./views/preferences/admin/EmailTemplates'))
const PrefsMarketingForms     = lazy(() => import('./views/preferences/admin/MarketingForms'))
const PrefsObjectFields       = lazy(() => import('./views/preferences/admin/ObjectFields'))
// Final prefs follow-up: extract the last 7 inline tabs that were still
// living in App.tsx (document mgmt, n8n integration, marketing sending /
// social, accounting defaults, report defaults, HR documents, automation).
// Several of these were also showing hardcoded "fake" data (n8n
// heartbeat, SPF/DKIM/DMARC verified badges, social account list,
// onboarding workflow rows) — those were replaced with honest
// admin-managed pointers and real reachability probes where feasible.
const PrefsDocumentMgmtSettings = lazy(() => import('./views/preferences/admin/DocumentMgmtSettings'))
const PrefsN8nIntegration       = lazy(() => import('./views/preferences/admin/N8nIntegration'))
const PrefsMarketingSending     = lazy(() => import('./views/preferences/admin/MarketingSending'))
const PrefsMarketingSocial      = lazy(() => import('./views/preferences/admin/MarketingSocial'))
const PrefsAccountingDefaults   = lazy(() => import('./views/preferences/admin/AccountingDefaults'))
const PrefsReportDefaults       = lazy(() => import('./views/preferences/admin/ReportDefaults'))
const PrefsHrDocuments          = lazy(() => import('./views/preferences/admin/HrDocuments'))
const PrefsAutomationSettings   = lazy(() => import('./views/preferences/admin/AutomationSettings'))
const PrefsConnectedApps        = lazy(() => import('./views/preferences/admin/ConnectedApps'))
const PrefsOrgTypes             = lazy(() => import('./views/preferences/admin/OrgTypes'))
const PrefsErrorCenter          = lazy(() => import('./views/preferences/admin/ErrorCenter'))
// bp-mkt-v1 — Marketing Hub (lazy chunk; UI-CORE ships the full router). Props: marketingSubView + setter + nav helper.
const MarketingView           = lazy(() => import('./views/marketing/MarketingView'))
// bp-camp-v1 - Campaigns list/create shell; the view owns its hash router.
const CampaignsView           = lazy(() => import('./views/campaigns/CampaignsView'))
const ProjectWorkspace        = lazy(() => import('./views/projects/ProjectWorkspace'))

// HR Center sub-tabs (R6 strangler-fig — extraction in progress).
// Each sub-tab ships as its own JS chunk loaded on demand.
const HrDashboard             = lazy(() => import('./views/hr-center/Dashboard'))
const HrTemplates             = lazy(() => import('./views/hr-center/Templates'))
const HrEmployeesList         = lazy(() => import('./views/hr-center/EmployeesList'))
const HrEmployeesDetail       = lazy(() => import('./views/hr-center/EmployeesDetail'))
const HrPipelines             = lazy(() => import('./views/hr-center/Pipelines'))
const HrAssignments           = lazy(() => import('./views/hr-center/Assignments'))
const HrSettings              = lazy(() => import('./views/hr-center/Settings'))
const HrForms                 = lazy(() => import('./views/hr-center/Forms'))

// === PHONE: LAZY-CHUNK IMPORTS === 6 stateless tab chunks (props contracts live in each file's
// header); Suspense fallback MUST be PhoneTabSkeleton (its flex:1 is load-bearing — tab-bar flash).
// Region index lives at the STATE — CALLING PANEL CORE banner. See docs/PHONE_ARCHITECTURE.md.
// Phone calling-panel sub-tabs (R7 strangler-fig). State (dialerInput,
// callsData, voicemails, smsConversations, etc.) stays in App.tsx and
// is threaded through props — these are pure presentational components.
const PhoneKeypad             = lazy(() => import('./views/phone/Keypad'))
const PhoneFavorites          = lazy(() => import('./views/phone/Favorites'))
const PhoneRecents            = lazy(() => import('./views/phone/Recents'))
const PhoneContacts           = lazy(() => import('./views/phone/Contacts'))
const PhoneVoicemail          = lazy(() => import('./views/phone/Voicemail'))
const PhoneSms                = lazy(() => import('./views/phone/Sms'))

// CRM core (R8 strangler-fig). State (previewLead, selectedDeal,
// previewClient, etc.) stays in App.tsx — threaded through props as
// pure presentational chunks. Detail/preview panels split off first
// since they are the largest cleanly-separable JSX subtrees.
const LeadPreviewPane         = lazy(() => import('./views/leads/PreviewPane'))
const LeadModals              = lazy(() => import('./views/leads/Modals'))

// Dashboards view (OPTIMIZATION_ROADMAP §2). Heavy renderer with KPI
// widgets, charts, and aggregate data. State (dashboardData, dashboardView,
// summaryKPIs, savedDashboards, etc.) stays in App.tsx — threaded
// through props as a pure presentational chunk.
const Dashboard               = lazy(() => import('./views/dashboard/Dashboard'))

// Email/Inbox view (OPTIMIZATION_ROADMAP §2). Multi-folder reader with
// thread list, search, snooze, AI summarize, link popover, and reply
// composer trigger. Local state (folders/threads/snooze/etc.) lives
// inside the chunk — only `userEmail`, `userName`, and `onOpenCompose`
// thread in from App.tsx, plus the shared GmailSearch/ThreadCard/
// MessageBubble components which still live in App.tsx.
const Inbox                   = lazy(() => import('./views/inbox/Inbox'))

// ComposeModal — Gmail-style email compose surface. Lazy-loaded because
// it pulls in TipTap (~80KB gzipped) plus AI/template/schedule plumbing.
// Mounted once at App.tsx top level; opened from ~5 call sites via
// setComposeOpen(true). The `ComposeContext` type is the source-of-truth
// for the compose payload shape and is re-imported for typing the
// composeContext state + UndoSendToast restore handler.
const ComposeModal            = lazy(() => import('./views/inbox/ComposeModal'))
import type { ComposeContext } from './views/inbox/ComposeModal'

// Calendar view — multi-source calendar grid (Google Calendar + legacy
// prior-CRM calendar fetch + mock fallback). State (calendarMonth,
// selectedDate, enabledCalendars/Employees, popups, modal meeting,
// reel/agenda mode) all stays in App.tsx and threads through props.
// Fetch lifecycle and managed polling stay in App.tsx — this chunk is a
// presentational extraction. The event create/edit modal lives in
// App.tsx because it's reused from contact / deal detail pages.
const Calendar                = lazy(() => import('./views/calendar/Calendar'))

// Subcontractor (sub-admin) view — extracted to lift the ~4,000-line
// portal-style subtree (dashboard/calendar/projects/draws/team/time/
// notifications/compliance/settings + project detail with chat) out of
// the main App.tsx bundle. State, polling, and the shared chat modal
// stay in App.tsx; this chunk is a presentational extraction. API
// functions are imported directly from src/api in the chunk so it has
// no data-fetching dep back into App.tsx.

// Accounting module — extracted to lift the ~3,000-line render subtree
// (chart of accounts/journal entries/AP+vendors/AR/bank recon/job costing/
// WIP/reports) out of the main App.tsx bundle. State/effects stay in
// App.tsx and thread through props; the journal-entry create/post form
// ships as its own sibling chunk import inside Accounting.tsx.
const Accounting               = lazy(() => import('./views/accounting/Accounting'))

// R8b CRM list shell + per-view bodies. The shell component wraps the
// shared "chrome" (topbar/toolbar/quick-filters/content/loading) for the
// 6 CRM list views (contacts/companies/leads/deals/tickets/calls) so
// each view body can be peeled out as its own lazy chunk. Calls is the
// proof-of-pattern; the other 5 are queued for follow-up rounds.
import CrmListShell from './views/crm-shell/CrmListShell'
const DeliveryView = lazy(() => import('./views/delivery/DeliveryView')) // bp-devws-v1
const CallsList               = lazy(() => import('./views/calls/CallsList'))
const MeetsList               = lazy(() => import('./views/meets/MeetsList'))
// Compact toolbar stats badge for #/calls — lives OUTSIDE the table's
// x-scroll container so it can never scroll with the table
// (docs/REGRESSIONS.md 2026-07-01; guard: src/lib/calls/callsStatsPlacement).
const CallsStatsBadge         = lazy(() => import('./views/calls/CallsStatsBadge'))
const TicketsList             = lazy(() => import('./views/tickets/TicketsList'))
const LeadsList               = lazy(() => import('./views/leads/LeadsList'))
const CompaniesList           = lazy(() => import('./views/companies/CompaniesList'))
const ContactsList            = lazy(() => import('./views/contacts/ContactsList'))
const DealsList               = lazy(() => import('./views/deals/DealsList'))

// Honest stubs — pages whose previous controls were localStorage placebos
// or HubSpot remnants. They redirect or surface "coming soon" copy.
const PrefsSecurity           = lazy(() => import('./views/preferences/stubs/Security'))
const PrefsAi                 = lazy(() => import('./views/preferences/stubs/Ai'))
const PrefsMeetings           = lazy(() => import('./views/preferences/stubs/Meetings'))

// Lightweight fallback for lazy chunks. Matches existing card surface
// so route swaps don't reflow.
const PrefsLazyFallback = (
  <div className="prefs-card" style={{ minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 12 }}>
    Loading…
  </div>
)
// `ringcentral-web-phone` is loaded on demand inside `initWebPhone` (see below)
// so users who never open the phone widget don't pay the ~316KB gzipped cost
// on first paint. Vite's manualChunks config keeps the dynamic chunk named
// `vendor-phone` for cacheability.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import type WebPhoneType from 'ringcentral-web-phone'
import DOMPurify from 'dompurify'
import { safeHtml } from './lib/safeHtml'
import { navAvatarSource, teamPhotoSrc, photoVersionFromKey } from './lib/identity/avatarSource'
import { resolveApptDateSource } from './lib/deals/apptDate'
import { sanitizeColWidths, CALL_COL_DEFAULT_WIDTHS } from './lib/calls/colWidths'
import { shouldApplyCallsResponse, callsQueryKey, type CallsViewQuery } from './lib/calls/callsViewOwnership'
import { DISPOSITION_OUTCOME, CALLS_DISPOSITION_MARKER } from './lib/calls/dispositions'
import { parseFeedIntent } from './lib/feed/feedIntent'
import { computeProjectOwnerGate } from './lib/owners/projectOwnerGate'
import { widgetStatus as homeWidgetStatus, partitionChips as homePartitionChips, orderChipsForPopup as homeOrderChipsForPopup, type SignalChip as HomeSignalChipData, type WidgetStatus as HomeWidgetStatus } from './lib/home/homeSignals'
import { resolveCanonicalClientTarget, CANONICAL_CLIENT_MARKER } from './lib/crm/canonicalClient'
export const HOME_BENTO_MARKER = 'home-bento-v3'
import { shouldUseOptimisticOwnerUpdate, buildOptimisticOwnersMembers } from './lib/owners/optimisticOwners'
import { toggleOwnerPendingIds, isOwnerSelected as isOwnerSelectedFn, sameOwnerIdSet } from './lib/owners/ownerSelection'
import { shouldPersistCreateDraft } from './lib/create/draftGuard'
import { requestPunchLocation, punchMapsUrl, punchAccuracyLabel } from './lib/timeClockGeo'
import { apReadCols, AP_BILL_COL_DEFS, AP_VENDOR_COL_DEFS, AP_SCANNER_COL_DEFS } from './views/accounting/apColumns'
import { calendarGridBounds, toYmd } from './lib/calendar/calendarGridBounds'
import { shouldSkipStaleTutorialFetch } from './lib/routing/detailRouteResolution'
import { homeRouteHash } from './lib/routing/homeRoute'
import { parseAccountingHash } from './lib/accounting/accountingHash'
import { getParentHoldCheckState, toggleHoldType, toggleParentHold } from './lib/holdTypeFilter'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { Underline } from '@tiptap/extension-underline'
import { TextStyle } from '@tiptap/extension-text-style'
import { Color } from '@tiptap/extension-color'
import { TextAlign } from '@tiptap/extension-text-align'
import Link from '@tiptap/extension-link'
import type { ParsedAddress } from './google-maps'
import {
  accounts,
  calendarSources,
  employeeColors,
  meetings as mockMeetings,
  projects,
  type CalendarId,
  type Meeting,
  type ProjectHealth,
  type Stage,
} from './data'
// bp-cal-roster-v1: deterministic per-teammate calendar colors + shared roster
// helpers, so a dynamically-provisioned @botpros.ai teammate renders a stable,
// distinct color and can be seeded into the (formerly hardcoded) roster maps.
import { resolveEmployeeColor } from './lib/calendar/employeeColor'
import { initialsFromName } from './lib/calendar/roster'
import {
  fetchCalendarEvents,
  fetchUserDayEvents,
  createCalendarEvent,
  updateCalendarEvent,
  deleteCalendarEvent,
  fetchCalendarUsers,
  createTimeOffRequest,
  listTimeOffRequests,
  approveTimeOff,
  denyTimeOff,
  createHoliday,
  listHolidays,
  approveHoliday,
  listRecurringEvents,
  updateRecurringEvent,
  deleteRecurringEvent,
  deleteHoliday,
  type TimeOffRequest,
  type CompanyHoliday,
  type RecurringEventRule,
  fetchClient,
  fetchClients,
  setOrganizationVendor,
  getCommunities,
  archiveClients,
  fetchRelatedContacts,
  type RelatedContact,
  fetchDeal,
  fetchDeals,
  fetchLead,
  fetchPipeline,
  fetchContacts,
  fetchLeads,
  fetchLeadsPaginated,
  batchArchiveLeads,
  fetchLeadStatuses,
  fetchWorkgroups,
  updateDeal,
  updateDealStage, deleteDeal, fetchDealDeletePreview,
  fetchProjectDeletePreview, deleteProject,
  updateTicket,
  updateLead,
  updateClient,
  searchAll,
  createClient,
  deleteClient,
  createLead,
  createDeal,
  convertLead,
  createNote,
  fetchTasks,
  createTask,
  updateTask,
  deleteTask,
  fetchTickets,
  fetchTicketCounts,
  createTicket,
  deleteTicket,
  fetchCallsPaginated,
  fetchMeetsPaginated,
  fetchProducts,
  createProduct,
  deleteProduct,
  fetchQuotes,
  createQuote,
  approveQuote,
  deleteQuote,
  fetchWorkflows,
  fetchConversations,
  fetchNotifications,
  fetchUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
  fetchPipelineReport,
  fetchRevenueReport,
  fetchActivitiesReport,
  fetchLeadSourcesReport,
  fetchLeaderboard,
  fetchLeadsByEstimator,
  fetchProperties,
  createProperty,
  updateProperty,
  deleteProperty,
  fetchTeams,
  fetchRoles,
  fetchAccountSettings,
  fetchImports,
  createImport,
  fetchExports,
  createExport,
  type RawCalendarUser,
  type CrmDeal,
  type PipelineStage,
  type CrmContact,
  type CrmClient,
  type CrmLead,
  type LeadStatus,
  type Workgroup,
  type SearchResults,
  type CrmUser,
  type CrmTask,
  type CrmTicket,
  type CrmActivity,
  type CrmMeeting,
  type CrmProduct,
  type CrmQuote,
  type Workflow,
  type CrmNotification,
  type PipelineReportRow,
  type RevenueReportRow,
  type ActivitiesReportRow,
  type LeadSourceReportRow,
  type LeaderboardRow,
  type LeadsByEstimatorRow,
  fetchUsers,
  fetchClientInvoices,
  fetchClientDocuments,
  fetchClientJobs,
  listFinancialsByProject,
  listFinancialsByClient,
  type ClientInvoice,
  type ClientDocument,
  type ClientJob,
  fetchRcUserMappings,
  fetchCallOutcomes,
  fetchBlockedNumbers,
  fetchRcConfig,
  updateRcConfig,
  createRcUserMapping,
  deleteRcUserMapping,
  createCallOutcome,
  updateCallOutcome,
  deleteCallOutcome,
  blockNumber,
  unblockNumber,
  fetchRcExtensions,
  triggerRcSync,
  fetchPendingAssignments,
  resolvePendingAssignment,
  dismissPendingAssignment,
  getRecordingUrl,
  startRingOut,
  getRingOutStatus,
  cancelRingOut,
  fetchRcActiveCall,
  type RcActiveCall,
  submitTranscription,
  createUser,
  updateUser,
  deleteUser,
  fetchSipProvision,
  matchPhoneNumber,
  lookupContactsByPhone,
  fetchPhoneFavorites,
  addPhoneFavorite,
  deletePhoneFavorite,
  reorderPhoneFavorites,
  fetchLiveTranscriptionCapability,
  type PhoneFavorite,
  fetchVoicemails,
  voicemailAudioUrl,
  updateVoicemailReadStatus,
  deleteVoicemail,
  type Voicemail,
  fetchRcExtensionCallLog,
  type DeviceCall,
  fetchHrProfile,
  updateHrProfile,
  revealHrPii,
  uploadHrProfilePhoto,
  uploadOrgLogo,
  deleteOrgLogo,
  fetchHrAdminEmployees,
  fetchHrAdminEmployee,
  fetchHrAdminEmployeeDocuments,
  downloadHrAdminFile,
  uploadHrAdminFile,
  updateHrAdminEmployee,
  fetchHrAdminEmployeeAlerts,
  fetchHrAdminApplications,
  fetchHrAdminIncompleteApps,
  fetchHrAdminEmailSettings,
  updateHrAdminEmailSettings,
  fetchHrAdminAdmins,
  removeHrAdmin,
  promoteHrAdmin,
  fetchHrAdminFieldRules,
  createHrAdminFieldRule,
  updateHrAdminFieldRule,
  deleteHrAdminFieldRule,
  deleteHrAdminFile,
  fetchHrAdminPositions,
  fetchHrAdminEmailTemplates,
  updateHrAdminEmailTemplate,
  fetchHrAdminPipelines,
  fetchHrAdminProfiles,
  updateHrAdminProfileStage,
  fetchHrAdminProfileDocs,
  fetchHrAdminTemplates,
  uploadHrAdminTemplate,
  updateHrAdminTemplate,
  deleteHrAdminTemplate,
  fetchHrAdminAssignments,
  createHrAdminAssignment,
  deleteHrAdminAssignment,
  fetchHrAdminAssignmentDetails,
  fetchHrAdminAssignmentPdf,
  signHrAdminDocument,
  fetchMyAssignments,
  fetchMyAssignmentDetails,
  fetchMyAssignmentPdf,
  signMyDocument,
  downloadHrAdminTemplate,
  updateHrAdminTemplateFields,
  fetchHrAdminPendingEmployer,
  dismissEmployerDoc,
  fetchHrAdminOnboardingDocPdf,
  submitHrAdminEmployerSign,
  fetchHrAdminOnboardingTokens,
  fetchHrAdminOnboardingDocs,
  fetchHrAdminProfileDetail,
  fetchHrAdminFileDownload,
  fetchHrAdminDashboard,
  fetchHrAdminEmployerSignature,
  saveHrAdminEmployerSignature,
  deleteHrAdminEmployerSignature,
  updateCrmUser,
  deleteCrmUser,
  deactivateCrmUser,
  reactivateCrmUser,
  fetchDealAutomationRuns,
  runDealAutomationManually,
  fetchDealAutomationSettings,
  updateDealAutomationSettings,
  type DealAutomationRun,
  type DealWonSettings,
  updateMyNotificationChannel,
  updateMyEmailNotifyScope,
  type EmailNotifyScope,
  sendNotifDeliveryTest,
  type EmailNotifChannel,
  type RcUserMapping,
  type CallOutcome,
  type BlockedNumber,
  type RcExtension,
  type PendingCallAssignment,
  type HrProfile,
  type SmsConversation,
  type SmsMessage,
  fetchSmsConversations,
  fetchSmsMessages,
  sendSmsMessage,
  fetchSmsNumbers,
  markSmsConversationRead,
  fetchClientLeads,
  fetchClientDeals,
  fetchClientActivities,
  updateActivity,
  fetchLeadActivities,
  fetchLeadRoutingConfig,
  markLeadRead,
  markLeadNoteLogged,
  type LeadRoutingConfig,
  fetchOrgSettings,
  updateOrgSettings,
  fetchClientAttachments,
  uploadDealAttachments,
  fetchDealActivities,
  fetchDealHistory,
  fetchSnippets,
  createSnippet,
  updateSnippet,
  deleteSnippet,
  fetchTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  fetchPlaybooks,
  fetchPlaybook,
  createPlaybook,
  updatePlaybook,
  deletePlaybook,
  publishPlaybook,
  unpublishPlaybook,
  addPlaybookQuestion,
  deletePlaybookQuestion,
  fetchOrders,
  createOrder,
  deleteOrder,
  fetchCrmProjects,
  fetchCrmProject,
  createCrmProject,
  updateCrmProject,
  deleteCrmProject,
  listProjectWorkOrders,
  getProjectWorkOrder,
  createProjectWorkOrder,
  updateProjectWorkOrder,
  deleteProjectWorkOrder,
  setWorkOrderFeatured,
  uploadWorkOrderFile,
  listProjectFiles,
  uploadProjectFile,
  deleteProjectFile,
  renameProjectFile,
  setProjectFileWorkOrder,
  type CrmProject,
  type CrmSnippet,
  type CrmTemplate,
  type CrmPlaybook,
  type CrmPlaybookQuestion,
  type CrmOrder,
  type CrmOrderLineItem as _CrmOrderLineItem,
  fetchSequences,
  fetchSequence,
  createSequence,
  updateSequence,
  deleteSequence,
  addSequenceStep,
  deleteSequenceStep,
  fetchSequenceEnrollments,
  fetchSequenceStats,
  createWorkflow,
  updateWorkflow,
  deleteWorkflow,
  fetchWorkflow,
  saveWorkflowCanvas,
  fetchChatflows,
  fetchChatflow,
  createChatflow,
  updateChatflow,
  deleteChatflow,
  replaceChatflowActions,
  type Sequence,
  type SequenceStep,
  type SequenceEnrollment,
  type Chatflow,
  type ChatflowBotAction,
  type WorkflowAction,
  fetchMarketingEmails,
  fetchMarketingRuns,
  fetchMarketingDeliverables,
  createMarketingEmail,
  deleteMarketingEmail,
  fetchEmailAnalytics,
  fetchContactLists,
  fetchAdAccounts,
  createAdAccount,
  deleteAdAccount,
  fetchAdCampaigns,
  createAdCampaign,
  updateAdCampaign,
  deleteAdCampaign,
  fetchSocialPosts,
  createSocialPost,
  updateSocialPost,
  deleteSocialPost,
  publishSocialPost,
  fetchForms,
  createForm,
  updateForm,
  deleteForm,
  fetchFormSubmissions,
  fetchLandingPages,
  createLandingPage,
  deleteLandingPage,
  publishLandingPage,
  fetchForecasts,
  saveForecastGoal,
  fetchPaymentLinks,
  createPaymentLink,
  fetchPayments,
  fetchKbArticles,
  createKbArticle,
  updateKbArticle,
  publishKbArticle,
  unpublishKbArticle,
  fetchKbCategories,
  createKbCategory,
  deleteKbCategory,
  deleteKbArticle,
  fetchPortalSettings,
  updatePortalSettings,
  fetchPortalTickets,
  fetchPortalTicket,
  addPortalTicketComment,
  fetchSurveys,
  fetchSurvey,
  createSurvey,
  updateSurvey,
  activateSurvey,
  pauseSurvey,
  closeSurvey,
  fetchSurveyResponses,
  fetchSurveyAnalytics,
  deleteAcctAccount as _deleteAcctAccount, voidJournalEntry as _voidJournalEntry, fetchAcctVendors,
  createBillPayment as _createBillPayment, sendAcctInvoice as _sendAcctInvoice,
  createCustomerPayment as _createCustomerPayment, createBankTransaction as _createBankTransaction, fetchAcctJobs,
  fetchProfitLoss, fetchBalanceSheet, fetchTrialBalance, fetchArAging,
  fetchArSummary, fetchArAgingSummary,
  fetchWipSchedule, fetchInvoiceScans, approveInvoiceScan, rejectInvoiceScan,
  updateInvoiceScanMatch, crmFilePdfUrl,
  fetchSummaryKPIs, fetchDealsCreated, fetchAvgDealSize, fetchDealVelocity, fetchActivityByType, fetchTeamActivity,
  fetchDashboardAggregate,
  type SummaryKPIs,
  type DocTypeCount,
  fetchSubAdminUsers,
  fetchSubAdminProjects, updateSubAdminProjectStaff,
  fetchSubAdminAssignableUsers, updateSubAdminProjectOwners, updateSubAdminProjectSeniors,
  fetchSubAdminProjectChat, sendSubAdminChatMessage,
  fetchTargetAccounts, markTargetAccount, unmarkTargetAccount, updateTargetAccount, recalculateEngagementScores,
  type TargetAccount,
  fetchSalesDocuments, createSalesDocument, updateSalesDocument as _updateSalesDocument, deleteSalesDocument,
  type SalesDocument,
  fetchActivityFeed,
  fetchMeetingTypes, createMeetingType, updateMeetingType as _updateMeetingType, deleteMeetingType,
  fetchMeetingBookings, createMeetingBooking as _createMeetingBooking,
  type MeetingType as MeetingTypeRecord, type MeetingBooking,
  fetchCoachingPlaylists, fetchCoachingPlaylist, createCoachingPlaylist, updateCoachingPlaylist as _updateCoachingPlaylist, deleteCoachingPlaylist,
  addPlaylistItem, removePlaylistItem,
  type CoachingPlaylist, type PlaylistItem,
  fetchProspectingSummary,
  fetchMyPreferences, setMyPreference,
  getDealLead,
  getDealActivitiesMerged,
  getClientProjects,
  updateProjectStage,
  updateProjectStageWithHold,
  getProjectActivities,
  searchUnified,
  type UnifiedSearchResult,
  fetchContactSummary,
  type ContactSummary,
  fetchContactTickets,
  fetchDealTickets,
  fetchProjectTickets,
  createSavedView,
  updateSavedView,
  deleteSavedView,
  fetchSavedViews,
  // Time tracking (unified Shifts custom)
  clockInTime,
  clockOutTime,
  fetchOpenTimeEntry,
  listTimeEntries,
  fetchDealLaborSummary,
  patchTimeEntry,
  approveTimeEntry,
  unapproveTimeEntry,
  deleteTimeEntry,
  bulkApproveTime,
  timeExportCsvUrl,
  type TimeEntry,
  type LaborSummary,
  type TimeCategory,
  type TimeListFilters,
  fetchHrEmployeeActivityRollup,
  fetchCallLinkedContext,
  fetchHrEmployeeRcExtension,
  fetchUserHrProfile,
  fetchTimeCountsByOwner,
  createContactFromCall,
  type ActivityRollup,
  type HrEmployeeRcExtension,
  type UserHrProfile,
  type LinkedContext,
  type TimeCountsByOwner,
  // Team 3 — reader-side email features
  fetchEmailThreadLinks,
  addEmailThreadLink,
  removeEmailThreadLink,
  fetchEmailThreadsByEntity,
  snoozeEmailThread,
  unsnoozeEmailThread,
  fetchSnoozedThreads,
  summarizeEmailThread,
  createEmailThreadReminder,
  fetchEmailThreadTracking,
  searchEntitiesForLinking,
  type EmailThreadEntityType,
  type EmailThreadLink,
  type SnoozedThread,
  type EmailThreadSummary,
  type EmailReminderCondition,
  type EmailTrackingRow,
  type EmailThreadSummaryLite,
  type EntitySearchHit,
  // Team 4 — compose-side email features
  fetchEmailSignatures,
  fetchDefaultEmailSignature,
  createEmailSignature,
  updateEmailSignature,
  deleteEmailSignature,
  fetchCrmEmailTemplates,
  renderCrmEmailTemplate,
  createCrmEmailTemplate,
  updateCrmEmailTemplate,
  deleteCrmEmailTemplate,
  scheduleSendEmail,
  fetchScheduledEmails,
  cancelScheduledEmail,
  generateAiDraftReply,
  fetchBccToken,
  rotateBccToken,
  type EmailSignature,
  type CrmEmailTemplate,
  type ScheduledEmail,
  fetchMyEmailSignature,
  saveMyEmailSignature,
  deleteMyEmailSignature,
  uploadMyEmailSignatureImage,
  type UserEmailSignature,
  fetchMyCapabilities,
  tcPunch,
  tcStatus,
  tcOverview,
  tcUpdateEntry,
  tcDeleteEntry,
  tcListCorrections,
  tcReviewCorrection,
  tcCreateCorrection,
  tcMyCorrections,
  type TimeClockPunch,
  type TimeClockCorrectionRequest,
  type Capability,
  listOrgSubTypes,
  listOrgRoleTypes,
  getOrgRelationships,
  createOrgRelationship,
  updateOrgRelationship,
  endOrgRelationship,
  getOrgPeople,
  getContactOrgRoles,
  createContactOrgRole,
  updateContactOrgRole,
  getJobBillingPeriods,
  type OrgSubType,
  type OrgRoleType,
  type OrgRelationship,
  type ContactOrgRole,
  type JobBillingPeriod,
  fetchEntityInsight,
  type EntityInsight,
  loadTerritoriesCached,
  matchTerritoryEstimators,
  // Contacts multi-person model + legacy name-fix admin job (cp-name-normalize-v1).
  // New typed wrappers used by the create panel (E) + detail/admin (F); existing
  // inline emails/phones fetches stay untouched.
  type ContactPerson,
  type ContactEmail,
  type ContactPhone,
  type CreateContactPersonInput,
  type UpdateContactPersonInput,
  listContactPersons,
  createContactPerson,
  updateContactPerson,
  deleteContactPerson,
  listContactEmails,
  createContactEmail,
  updateContactEmail,
  deleteContactEmail,
  listContactPhones,
  createContactPhone,
  updateContactPhone,
  deleteContactPhone,
  nameFixDryRun,
  nameFixApply,
  nameFixUndo,
  listNameFixReview,
  resolveNameFixReview,
  type NameFixDryRunResult,
  type NameFixDryRunRow,
  type NameFixApplyResult,
  type NameFixReviewRow,
} from './api'
import { CallingSetupCard } from './views/preferences/admin/General'

// Module-level fetch interception for admin impersonation.
// Runs at import time (before any component mounts) so even the first /api/me
// call in main.tsx carries the impersonation header.
const _origFetch = window.fetch

// ── Session-expiry detection ──
// The Cloudflare Access cookie (CF_Authorization) eventually expires while
// the SPA sits open; auth is only checked once at boot (/api/me in main.tsx),
// so afterwards EVERY /api call 401s and the pollers loop errors forever
// without telling the user. Detect the 401 burst here, CONFIRM with the
// /api/me probe (some routes can 401 for non-Access reasons), then announce
// it — App shows a blocking "sign back in" overlay and this patch
// short-circuits further /api calls so the console/network storm stops.
const SESSION_EXPIRED_EVENT = 'har:session-expired'
let sessionExpiredFired = false
let sessionExpiryProbing = false
let recentApi401s: number[] = []
const noteApi401 = (url: string) => {
  if (sessionExpiredFired || sessionExpiryProbing) return
  if (!url.includes('/api/') || url.includes('/api/me')) return
  const now = Date.now()
  recentApi401s = recentApi401s.filter(t => now - t < 15_000)
  recentApi401s.push(now)
  if (recentApi401s.length < 3) return
  sessionExpiryProbing = true
  _origFetch.call(window, '/api/me', { credentials: 'include' })
    .then(r => (r.ok ? r.json() : { authenticated: false }))
    .then((d: any) => {
      if (!d?.authenticated) {
        sessionExpiredFired = true
        window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT))
      } else {
        recentApi401s = [] // 401s weren't auth-layer — reset and keep watching
      }
    })
    .catch(() => { /* network blip — re-evaluate on the next 401 burst */ })
    .finally(() => { sessionExpiryProbing = false })
}

window.fetch = function(input: RequestInfo | URL, init?: RequestInit) {
  const url = typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url
  // Once the session is known-dead, answer /api calls locally instead of
  // letting every poller keep hammering the worker with doomed requests.
  if (sessionExpiredFired && url.includes('/api/') && !url.includes('/api/me')) {
    return Promise.resolve(new Response(JSON.stringify({ error: 'Session expired — sign back in' }), {
      status: 401,
      headers: { 'Content-Type': 'application/json' },
    }))
  }
  try {
    const stored = localStorage.getItem('crm_impersonate')
    if (stored) {
      const t = JSON.parse(stored)
      if (t?.userId) {
        if (url.includes('/api/')) {
          init = { ...init, headers: { ...(init?.headers || {}), 'X-Impersonate-User-Id': String(t.userId) } }
        }
      }
    }
  } catch { /* ignore parse errors */ }
  const result = _origFetch.call(window, input, init)
  result.then(resp => { if (resp.status === 401) noteApi401(url) }).catch(() => { /* caller handles */ })
  return result
} as typeof window.fetch

type LocalLoaderProps = {
  style?: React.CSSProperties
  src?: string
  loop?: boolean
  autoplay?: boolean
}

export function DotLottieReact({ style }: LocalLoaderProps) {
  return (
    <span
      className="har-inline-loader"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#94a3b8',
        ...style,
      }}
      aria-hidden="true"
    >
      <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83">
          <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.9s" repeatCount="indefinite" />
        </path>
      </svg>
    </span>
  )
}

type SemanticsSource = { semantics?: string | null }

const TERMINAL_SEMANTIC_VALUES = new Set(['s', 'f', 'success', 'failure'])
const SUCCESS_SEMANTIC_VALUES = new Set(['s', 'success'])

const normalizeSemantic = (semantics?: string | null) => String(semantics || '').trim().toLowerCase()
const isTerminalSemantic = (semantics?: string | null) => TERMINAL_SEMANTIC_VALUES.has(normalizeSemantic(semantics))
const isSuccessSemantic = (semantics?: string | null) => SUCCESS_SEMANTIC_VALUES.has(normalizeSemantic(semantics))
const isTerminalPipelineStage = (stage?: SemanticsSource | null) => isTerminalSemantic(stage?.semantics)
const isSuccessPipelineStage = (stage?: SemanticsSource | null) => isSuccessSemantic(stage?.semantics)

const CRP_ACTIVITY_FILTER_OPTIONS = [
  { key: 'calls', label: 'Calls' },
  { key: 'emails', label: 'Emails' },
  { key: 'meetings', label: 'Meetings' },
  { key: 'notes', label: 'Notes' },
  { key: 'tasks', label: 'Tasks' },
] as const

const CRP_DEFAULT_ACTIVITY_FILTER_CHECKS: Record<string, boolean> = Object.fromEntries(
  CRP_ACTIVITY_FILTER_OPTIONS.map((option) => [option.key, true]),
)

// Colored token palette for an organization's primary sub-type (record header chip).
const ORG_SUBTYPE_PALETTE: Record<string, { bg: string; fg: string }> = {
  community: { bg: '#ecfdf5', fg: '#047857' },
  hoa: { bg: '#f5f3ff', fg: '#6d28d9' },
  management_company: { bg: '#eff6ff', fg: '#1d4ed8' },
  builder: { bg: '#fff7ed', fg: '#c2410c' },
  vendor: { bg: '#f1f5f9', fg: '#475569' },
  standard: { bg: '#f8fafc', fg: '#334155' },
}
function orgSubTypeColors(key: string | null | undefined): { bg: string; fg: string } {
  if (key && ORG_SUBTYPE_PALETTE[key]) return ORG_SUBTYPE_PALETTE[key]
  return { bg: '#f1f5f9', fg: '#475569' } // sensible fallback for unknown keys
}

// bp-mkt-v1 — Marketing Hub workspace sub-views (all render inside the single lazy `marketing` view via marketingSubView)
type MarketingSubView = 'hub' | 'strategy' | 'council' | 'content' | 'seo' | 'outbound' | 'paid' | 'conversion' | 'growth' | 'research' | 'library'
// bp-camp-v1 - Parent tracks the campaign route for shell/deep-link state.
type CampaignsSubView = 'list' | 'detail' | 'flow'

type AccountingSubView = 'overview' | 'chart-of-accounts' | 'journal-entries' | 'accounts-payable' | 'vendors' | 'accounts-receivable' | 'expenses' | 'bank-reconciliation' | 'job-costing' | 'wip-schedule' | 'profit-loss' | 'balance-sheet' | 'trial-balance' | 'ar-aging' | 'banking' | 'feed-review'

type ViewMode = 'command' | 'calendar' | 'deals' | 'leads' | 'projects' | 'contacts' | 'companies' | 'tasks' | 'preferences' | 'tickets' | 'calls' | 'meets' | 'products' | 'quotes' | 'reports' | 'dashboards' | 'analytics' | 'workflows' | 'campaigns' | 'marketing' | 'conversations' | 'hr-center' | 'snippets' | 'templates' | 'playbooks' | 'orders' | 'sequences' | 'chatflows' | 'marketing-email' | 'ads' | 'social' | 'forms' | 'landing-pages' | 'forecasts' | 'payments' | 'knowledge-base' | 'customer-portal' | 'feedback-surveys' | 'accounting' | 'target-accounts' | 'sales-documents' | 'activity-feed' | 'meetings-scheduler' | 'coaching-playlists' | 'prospecting' | 'team' | 'email' | 'feed' | 'profile' | 'timesheets' | 'my-documents' | 'my-time' | 'delivery'
type PrefsTab ='general' | 'security' | 'account' | 'properties' | 'calling' | 'users' | 'notifications' | 'audit-log' | 'ai' | 'import-export' | 'backup' | 'meetings' | 'duplicates'
  | 'connected-apps' | 'deal-automation'
  | 'obj-contacts' | 'obj-companies' | 'obj-leads' | 'obj-deals' | 'obj-tickets' | 'obj-products' | 'obj-quotes' | 'obj-projects' | 'obj-services' | 'obj-appointments' | 'obj-invoices' | 'obj-activities' | 'obj-workflows'
  | 'inboxes'
  | 'mkt-forms'
  | 'com-tax'
  | 'invoice-scanner-settings' | 'document-mgmt-settings'
  | 'n8n-integration'
  | 'mkt-sending' | 'mkt-social'
  | 'com-accounting'
  | 'report-defaults'
  | 'hr-documents' | 'automation-settings'
  // BotPros-specific admin pages (Carly's 4/28 + 4/30 requirements)
  | 'organization' | 'permissions' | 'territories' | 'lead-routing' | 'numbering'
  | 'saved-views'
  | 'company-events'
  | 'error-center'
  | 'org-types' | 'email-templates'

type PrefsNavChild = {
  id: string
  label: string
  tab?: PrefsTab
  badge?: string
}

type PrefsNavItem = {
  id: string
  label: string
  tab?: PrefsTab
  disabled?: boolean
  external?: boolean
  expandable?: boolean
  keywords?: string[]
  badge?: string
  children?: PrefsNavChild[]
}

// Mirrors canonicalRole() in api/src/index.ts. Used to gate visibility of
// Preferences nav sections and other role-aware UI in the SPA shell. The
// `is_admin=1` lift (Ashley-style admin overrides on a non-admin role) is
// not represented here because /api/me doesn't return is_admin to the
// client; callers that have it can pass it through (matches Users.tsx
// `displayTier` signature).
type CanonicalRole = 'super_admin' | 'admin' | 'office' | 'salesperson' | 'accounting' | 'other'

function canonicalRoleClient(role: string | null | undefined, isAdmin?: number): CanonicalRole {
  const r = (role || '').toLowerCase().trim()
  if (r === 'super admin' || r === 'super_admin' || r === 'super-admin' || r === 'superadmin') return 'super_admin'
  if (r === 'owner') return 'super_admin'
  if (r === 'admin' || r === 'vp' || r === 'consultant') return 'super_admin'
  if (isAdmin === 1) return 'admin'
  if (r === 'office') return 'office'
  // 'estimator' is the legacy job-title string still in production user rows
  if (r === 'salesperson' || r === 'estimator') return 'salesperson'
  if (r === 'accounting') return 'accounting'
  return 'other'
}

type PrefsNavSection = {
  title: string
  items: PrefsNavItem[]
  /** Roles allowed to see this section. Omit to allow everyone (default
   * behavior for the 'My Preferences' section). When provided, the
   * impersonated user's role is used when impersonating; otherwise the
   * signed-in user's role. */
  allowedRoles?: readonly CanonicalRole[]
  /** @deprecated Kept for migration; use allowedRoles. When set without
   * allowedRoles, treated as ['super_admin', 'admin']. */
  adminOnly?: boolean
}

// Field Alert Catalog — trackable employee fields by section (keys match legacy field keys in employee_cache.data)
const HR_FIELD_CATALOG: { section: string; sectionLabel: string; key: string; label: string; type: 'date'|'text'|'file'|'select' }[] = [
  // Personal Information
  { section: 'personal', sectionLabel: 'Personal Information', key: 'ufCrm6Name', label: 'First Name', type: 'text' },
  { section: 'personal', sectionLabel: 'Personal Information', key: 'ufCrm6SecondName', label: 'Middle Name', type: 'text' },
  { section: 'personal', sectionLabel: 'Personal Information', key: 'ufCrm6LastName', label: 'Last Name', type: 'text' },
  { section: 'personal', sectionLabel: 'Personal Information', key: 'ufCrm6PreferredName', label: 'Preferred Name', type: 'text' },
  { section: 'personal', sectionLabel: 'Personal Information', key: 'ufCrm6PersonalBirthday', label: 'Date of Birth', type: 'date' },
  { section: 'personal', sectionLabel: 'Personal Information', key: 'ufCrm6PersonalGender', label: 'Gender', type: 'select' },
  { section: 'personal', sectionLabel: 'Personal Information', key: 'ufCrm6MaritalStatus', label: 'Marital Status', type: 'select' },
  { section: 'personal', sectionLabel: 'Personal Information', key: 'ufCrm6AddressStreet', label: 'Street Address', type: 'text' },
  { section: 'personal', sectionLabel: 'Personal Information', key: 'ufCrm6AddressCity', label: 'City', type: 'text' },
  { section: 'personal', sectionLabel: 'Personal Information', key: 'ufCrm6AddressState', label: 'State', type: 'text' },
  { section: 'personal', sectionLabel: 'Personal Information', key: 'ufCrm6AddressZip', label: 'ZIP', type: 'text' },
  { section: 'personal', sectionLabel: 'Personal Information', key: 'ufCrm6Email', label: 'Email', type: 'text' },
  { section: 'personal', sectionLabel: 'Personal Information', key: 'ufCrm6PersonalMobile', label: 'Phone', type: 'text' },
  // Emergency Contact
  { section: 'emergency', sectionLabel: 'Emergency Contact', key: 'ufCrm6EmergencyContactName', label: 'Contact Name', type: 'text' },
  { section: 'emergency', sectionLabel: 'Emergency Contact', key: 'ufCrm6EmergencyContactPhone', label: 'Contact Phone', type: 'text' },
  { section: 'emergency', sectionLabel: 'Emergency Contact', key: 'ufCrm6Relationship', label: 'Relationship', type: 'text' },
  // Employment Details
  { section: 'employment', sectionLabel: 'Employment Details', key: 'ufCrm6BadgeNumber', label: 'Badge Number', type: 'text' },
  { section: 'employment', sectionLabel: 'Employment Details', key: 'ufCrm6WorkPosition', label: 'Position', type: 'text' },
  { section: 'employment', sectionLabel: 'Employment Details', key: 'ufCrm6Subsidiary', label: 'Department', type: 'select' },
  { section: 'employment', sectionLabel: 'Employment Details', key: 'ufCrm6EmploymentType', label: 'Employment Type', type: 'select' },
  { section: 'employment', sectionLabel: 'Employment Details', key: 'ufCrm6EmploymentStartDate', label: 'Start Date', type: 'date' },
  { section: 'employment', sectionLabel: 'Employment Details', key: 'ufCrm6PayRate', label: 'Pay Rate', type: 'text' },
  { section: 'employment', sectionLabel: 'Employment Details', key: 'ufCrm6Shift', label: 'Shift', type: 'text' },
  { section: 'employment', sectionLabel: 'Employment Details', key: 'ufCrm6Sales', label: 'Sales Territory', type: 'select' },
  { section: 'employment', sectionLabel: 'Employment Details', key: 'ufCrm6ProjectCategory', label: 'Project Category', type: 'select' },
  // Citizenship & Work Authorization
  { section: 'citizenship', sectionLabel: 'Citizenship & Work Authorization', key: 'ufCrm6Citizenship', label: 'Citizenship Status', type: 'select' },
  { section: 'citizenship', sectionLabel: 'Citizenship & Work Authorization', key: 'ufCrm6WorkVisa', label: 'Work Visa', type: 'file' },
  { section: 'citizenship', sectionLabel: 'Citizenship & Work Authorization', key: 'ufCrm6VisaExpiry', label: 'Visa Expiry', type: 'date' },
  // Compensation & Benefits
  { section: 'compensation', sectionLabel: 'Compensation & Benefits', key: 'ufCrm6Ssn', label: 'SSN', type: 'text' },
  { section: 'compensation', sectionLabel: 'Compensation & Benefits', key: 'ufCrm6PtoDays', label: 'PTO Days', type: 'text' },
  { section: 'compensation', sectionLabel: 'Compensation & Benefits', key: 'ufCrm6HealthInsurance', label: 'Health Insurance', type: 'select' },
  { section: 'compensation', sectionLabel: 'Compensation & Benefits', key: 'ufCrm6_401K_ENROLLMENT', label: '401(k) Enrollment', type: 'select' },
  { section: 'compensation', sectionLabel: 'Compensation & Benefits', key: 'ufCrm6LifeBeneficiaries', label: 'Life Insurance Beneficiaries', type: 'text' },
  // Tax & Payroll
  { section: 'tax', sectionLabel: 'Tax & Payroll Information', key: 'ufCrm6TaxFilingStatus', label: 'Filing Status', type: 'select' },
  { section: 'tax', sectionLabel: 'Tax & Payroll Information', key: 'ufCrm6W4Exemptions', label: 'W-4 Exemptions', type: 'text' },
  { section: 'tax', sectionLabel: 'Tax & Payroll Information', key: 'ufCrm6AdditionalFedWithhold', label: 'Additional Federal', type: 'text' },
  { section: 'tax', sectionLabel: 'Tax & Payroll Information', key: 'ufCrm6AdditionalStateWithhold', label: 'Additional State', type: 'text' },
  { section: 'tax', sectionLabel: 'Tax & Payroll Information', key: 'ufCrm6UfUsr1737120507262', label: 'Payment Method', type: 'select' },
  // Banking & Direct Deposit
  { section: 'banking', sectionLabel: 'Banking & Direct Deposit', key: 'ufCrm6BankName', label: 'Bank Name', type: 'text' },
  { section: 'banking', sectionLabel: 'Banking & Direct Deposit', key: 'ufCrm6BankAccountName', label: 'Account Holder', type: 'text' },
  { section: 'banking', sectionLabel: 'Banking & Direct Deposit', key: 'ufCrm6BankAccountType', label: 'Account Type', type: 'select' },
  { section: 'banking', sectionLabel: 'Banking & Direct Deposit', key: 'ufCrm6BankRouting', label: 'Routing Number', type: 'text' },
  { section: 'banking', sectionLabel: 'Banking & Direct Deposit', key: 'ufCrm6BankAccountNumber', label: 'Account Number', type: 'text' },
  // Dependents & Beneficiaries
  { section: 'dependents', sectionLabel: 'Dependents & Beneficiaries', key: 'ufCrm6DependentsInfo', label: 'Dependents Info', type: 'text' },
  { section: 'dependents', sectionLabel: 'Dependents & Beneficiaries', key: 'ufCrm6DependentNames', label: 'Dependent Names', type: 'text' },
  { section: 'dependents', sectionLabel: 'Dependents & Beneficiaries', key: 'ufCrm6DependentSsns', label: 'Dependent SSNs', type: 'text' },
  { section: 'dependents', sectionLabel: 'Dependents & Beneficiaries', key: 'ufCrm6DependentRelationships', label: 'Dependent Relationships', type: 'text' },
  // Education & Skills
  { section: 'education', sectionLabel: 'Education & Skills', key: 'ufCrm6EducationLevel', label: 'Education Level', type: 'select' },
  { section: 'education', sectionLabel: 'Education & Skills', key: 'ufCrm6SchoolName', label: 'School', type: 'text' },
  { section: 'education', sectionLabel: 'Education & Skills', key: 'ufCrm6GraduationYear', label: 'Graduation Year', type: 'text' },
  { section: 'education', sectionLabel: 'Education & Skills', key: 'ufCrm6FieldOfStudy', label: 'Field of Study', type: 'text' },
  { section: 'education', sectionLabel: 'Education & Skills', key: 'ufCrm6Skills', label: 'Skills', type: 'text' },
  { section: 'education', sectionLabel: 'Education & Skills', key: 'ufCrm6CertText', label: 'Certifications', type: 'text' },
  { section: 'education', sectionLabel: 'Education & Skills', key: 'ufCrm6SkillsLevel', label: 'Skills Level', type: 'select' },
  // Training & Compliance
  { section: 'training', sectionLabel: 'Training & Compliance', key: 'ufCrm6RequiredTraining', label: 'Required Training', type: 'select' },
  { section: 'training', sectionLabel: 'Training & Compliance', key: 'ufCrm6SafetyTraining', label: 'Safety Training', type: 'select' },
  { section: 'training', sectionLabel: 'Training & Compliance', key: 'ufCrm6ComplianceTraining', label: 'Compliance Training', type: 'select' },
  { section: 'training', sectionLabel: 'Training & Compliance', key: 'ufCrm6TrainingDate', label: 'Training Date', type: 'date' },
  { section: 'training', sectionLabel: 'Training & Compliance', key: 'ufCrm6NextTrainingDue', label: 'Next Training Due', type: 'date' },
  { section: 'training', sectionLabel: 'Training & Compliance', key: 'ufCrm6TrainingNotes', label: 'Training Notes', type: 'text' },
  // IT & Equipment
  { section: 'it', sectionLabel: 'IT & Equipment', key: 'ufCrm6SoftwareExperience', label: 'Software Experience', type: 'text' },
  { section: 'it', sectionLabel: 'IT & Equipment', key: 'ufCrm6EquipmentAssigned', label: 'Equipment Assigned', type: 'select' },
  { section: 'it', sectionLabel: 'IT & Equipment', key: 'ufCrm6EquipmentStatus', label: 'Equipment Status', type: 'select' },
  { section: 'it', sectionLabel: 'IT & Equipment', key: 'ufCrm6SoftwareAccess', label: 'Software Access', type: 'select' },
  { section: 'it', sectionLabel: 'IT & Equipment', key: 'ufCrm6AccessLevel', label: 'Access Level', type: 'select' },
  { section: 'it', sectionLabel: 'IT & Equipment', key: 'ufCrm6SecurityClearance', label: 'Security Clearance', type: 'select' },
  { section: 'it', sectionLabel: 'IT & Equipment', key: 'ufCrm6NetworkStatus', label: 'Network Status', type: 'select' },
  { section: 'it', sectionLabel: 'IT & Equipment', key: 'ufCrm6VpnAccess', label: 'VPN Access', type: 'text' },
  { section: 'it', sectionLabel: 'IT & Equipment', key: 'ufCrm6RemoteAccess', label: 'Remote Access', type: 'text' },
  { section: 'it', sectionLabel: 'IT & Equipment', key: 'ufCrm6PhoneImei', label: 'Phone IMEI', type: 'text' },
  { section: 'it', sectionLabel: 'IT & Equipment', key: 'ufCrm6PhonePassword', label: 'Phone Password', type: 'text' },
  { section: 'it', sectionLabel: 'IT & Equipment', key: 'ufCrm6TabletImei', label: 'Tablet IMEI', type: 'text' },
  { section: 'it', sectionLabel: 'IT & Equipment', key: 'ufCrm6TabletPassword', label: 'Tablet Password', type: 'text' },
  // Vehicle & Licensing
  { section: 'vehicle', sectionLabel: 'Vehicle & Licensing', key: 'ufCrm6AutoTagNumber', label: 'Tag Number', type: 'text' },
  { section: 'vehicle', sectionLabel: 'Vehicle & Licensing', key: 'ufCrm6TagExpiration', label: 'Tag Expiration', type: 'date' },
  { section: 'vehicle', sectionLabel: 'Vehicle & Licensing', key: 'ufCrm6UfUsr1747966315398', label: "Driver's License", type: 'file' },
  { section: 'vehicle', sectionLabel: 'Vehicle & Licensing', key: 'ufCrm6UfUsr1747966315398Expiry', label: "Driver's License Expiry", type: 'date' },
  { section: 'vehicle', sectionLabel: 'Vehicle & Licensing', key: 'ufCrm6UfUsr1737120327618', label: 'Auto Insurance', type: 'file' },
  { section: 'vehicle', sectionLabel: 'Vehicle & Licensing', key: 'ufCrm6UfUsr1737120327618Expiry', label: 'Auto Insurance Expiry', type: 'date' },
  // Performance & Reviews
  { section: 'performance', sectionLabel: 'Performance & Reviews', key: 'ufCrm6ReviewDate', label: 'Review Date', type: 'date' },
  { section: 'performance', sectionLabel: 'Performance & Reviews', key: 'ufCrm6TerminationDate', label: 'Termination Date', type: 'date' },
  { section: 'performance', sectionLabel: 'Performance & Reviews', key: 'ufCrm6RehireEligible', label: 'Rehire Eligible', type: 'text' },
  // Additional Information
  { section: 'additional', sectionLabel: 'Additional Information', key: 'ufCrm6AdditionalInfo', label: 'Additional Info', type: 'text' },
]
const HR_FIELD_SECTIONS = [...new Set(HR_FIELD_CATALOG.map(f => f.section))].map(s => ({
  value: s, label: HR_FIELD_CATALOG.find(f => f.section === s)!.sectionLabel
}))

const VALID_VIEWS = new Set<ViewMode>(['command', 'calendar', 'deals', 'leads', 'projects', 'contacts', 'companies', 'tasks', 'preferences', 'tickets', 'calls', 'meets', 'products', 'quotes', 'reports', 'dashboards', 'analytics', 'workflows', 'campaigns', 'marketing', 'conversations', 'hr-center', 'snippets', 'templates', 'playbooks', 'orders', 'sequences', 'chatflows', 'marketing-email', 'ads', 'social', 'forms', 'landing-pages', 'forecasts', 'payments', 'knowledge-base', 'customer-portal', 'feedback-surveys', 'accounting', 'target-accounts', 'sales-documents', 'activity-feed', 'meetings-scheduler', 'coaching-playlists', 'prospecting', 'team', 'email', 'feed', 'profile', 'timesheets', 'my-documents', 'my-time', 'delivery'])

type GeneralSubTab = 'profile' | 'email' | 'calling' | 'calendar' | 'tasks' | 'security' | 'automation'
type NotifSubTab = 'email_desktop' | 'other_apps' | 'mobile'
type AcctDefaultsSubTab = 'general' | 'user_defaults' | 'notification_profiles'

const NOTIF_SUB_TABS: Array<{ key: NotifSubTab; label: string }> = [
  { key: 'email_desktop', label: 'Email & Desktop' },
  { key: 'other_apps', label: 'Other apps' },
  { key: 'mobile', label: 'Mobile app' },
]

const ACCT_DEFAULTS_SUB_TABS: Array<{ key: AcctDefaultsSubTab; label: string }> = [
  { key: 'general', label: 'General' },
  { key: 'user_defaults', label: 'User Defaults' },
  { key: 'notification_profiles', label: 'Notification Profiles' },
]

const US_TIMEZONES = [
  { value: 'US/Eastern', label: 'Eastern (UTC -05/-04)' },
  { value: 'US/Central', label: 'Central (UTC -06/-05)' },
  { value: 'US/Mountain', label: 'Mountain (UTC -07/-06)' },
  { value: 'US/Pacific', label: 'Pacific (UTC -08/-07)' },
  { value: 'US/Alaska', label: 'Alaska (UTC -09/-08)' },
  { value: 'US/Hawaii', label: 'UTC -10:00 Hawaii' },
]

const FISCAL_YEAR_OPTIONS = [
  'January - December', 'February - January', 'March - February', 'April - March',
  'May - April', 'June - May', 'July - June', 'August - July',
  'September - August', 'October - September', 'November - October', 'December - November',
]

const INDUSTRY_OPTIONS = [
  'Construction', 'Professional Services', 'Real Estate', 'Manufacturing', 'Retail',
  'Technology', 'Healthcare', 'Finance', 'Education', 'Government', 'Non-Profit', 'Other',
]

type NotifChannel = 'popup' | 'browser' | 'bell' | 'email'
// type NotifTopicId = string

interface NotifTopicGroup {
  id: string
  label: string
  channels: NotifChannel[]
  items: Array<{
    id: string
    label: string
    description: string
    channels: NotifChannel[]
  }>
}

const NOTIF_TOPIC_GROUPS: NotifTopicGroup[] = [
  { id: 'CRM', label: 'CRM', channels: ['bell', 'email'], items: [
    { id: 'contact-assigned', label: 'Contact assigned to you', description: 'Get notified when a contact is assigned to you.', channels: ['bell', 'email'] },
    { id: 'company-mention', label: "You're mentioned on a client record", description: 'Get notified when a teammate @mentions you in a note or logged activity on a client record.', channels: ['bell', 'email'] },
    { id: 'contact-mention', label: "You're mentioned on a contact record", description: 'Get notified when a teammate @mentions you in a note or logged activity on a contact record.', channels: ['bell', 'email'] },
  ]},
  { id: 'CALLING', label: 'Calling', channels: ['popup', 'browser', 'bell', 'email'], items: [
    { id: 'missed-call', label: 'Missed calls', description: 'Get notified when an inbound call to your number has been missed.', channels: ['popup', 'bell', 'email'] },
    { id: 'voicemail', label: 'Voicemails', description: 'Get notified when someone leaves a voicemail on your number.', channels: ['popup', 'bell', 'email'] },
  ]},
  { id: 'DEALS', label: 'Deals', channels: ['popup', 'browser', 'bell', 'email'], items: [
    { id: 'deal-assigned', label: 'Deal assigned to you', description: 'Get notified when a deal is assigned to you.', channels: ['popup', 'browser', 'bell', 'email'] },
    { id: 'deal-stage-update', label: 'Deal stage update', description: 'Get notified when a deal you follow moves to a new stage.', channels: ['popup', 'browser', 'bell', 'email'] },
    { id: 'deal-mention', label: "You're mentioned on a deal record", description: 'Get notified when a teammate @mentions you in a note or logged activity on a deal record.', channels: ['popup', 'browser', 'bell', 'email'] },
  ]},
  { id: 'LEADS', label: 'Leads', channels: ['bell', 'email'], items: [
    { id: 'lead-assigned', label: 'Lead assigned to you', description: 'Get notified when a lead is assigned to you.', channels: ['bell', 'email'] },
  ]},
  { id: 'TASKS', label: 'Tasks', channels: ['popup', 'browser', 'bell', 'email'], items: [
    { id: 'task-reminder', label: 'Task reminder', description: "Get notified when a task is due. You'll also need to set a reminder on the task.", channels: ['popup', 'browser', 'bell', 'email'] },
    { id: 'task-mention', label: "You're mentioned on a task record", description: 'Get notified when a teammate @mentions you in a note or logged activity on a task record.', channels: ['popup', 'browser', 'bell', 'email'] },
  ]},
  { id: 'TICKETS', label: 'Tickets', channels: ['popup', 'browser', 'bell', 'email'], items: [
    { id: 'ticket-assigned', label: 'Ticket assigned to you', description: 'Get notified when a ticket is assigned to you.', channels: ['popup', 'browser', 'bell', 'email'] },
    { id: 'ticket-status-update', label: 'Ticket status update', description: 'Get notified when a ticket you follow changes status.', channels: ['popup', 'browser', 'bell', 'email'] },
    { id: 'ticket-mention', label: "You're mentioned on a ticket record", description: 'Get notified when a teammate @mentions you in a note or logged activity on a ticket record.', channels: ['popup', 'browser', 'bell', 'email'] },
  ]},
  { id: 'COMMENTS', label: 'Comments', channels: ['popup', 'browser', 'bell', 'email'], items: [
    { id: 'comment-activity', label: "Comments on activity you're involved in", description: 'Get notified when there is a new comment on an activity you own, commented on, or were mentioned in.', channels: ['popup', 'browser', 'bell', 'email'] },
    { id: 'comment-mention', label: "Comments you're mentioned in", description: 'Get notified when a teammate @mentions you in a comment.', channels: ['popup', 'browser', 'bell', 'email'] },
  ]},
  { id: 'CONVERSATIONS', label: 'Chat and email conversations', channels: ['popup', 'browser', 'bell', 'email'], items: [
    { id: 'chat-assigned', label: 'Chat conversation assigned to you', description: 'Get notified when a new live chat conversation is assigned to you.', channels: ['popup', 'browser', 'bell', 'email'] },
    { id: 'chat-reply', label: 'Chat conversation reply', description: 'Get notified when someone replies to a chat conversation assigned to you.', channels: ['popup', 'browser', 'bell', 'email'] },
    { id: 'email-conv-assigned', label: 'Email conversation assigned to you', description: 'Get notified when an email conversation is assigned to you.', channels: ['popup', 'browser', 'bell', 'email'] },
    { id: 'email-conv-reply', label: 'Email conversation reply', description: 'Get notified when someone replies to an email conversation assigned to you.', channels: ['popup', 'browser', 'bell', 'email'] },
  ]},
  { id: 'EMAIL', label: 'Email', channels: ['popup', 'browser', 'bell', 'email'], items: [
    { id: 'email-click', label: 'Email click', description: 'Get notified when someone clicks a link in an email you sent.', channels: ['popup', 'browser', 'bell', 'email'] },
    { id: 'email-open', label: 'Email open', description: 'Get notified when someone opens an email you sent.', channels: ['popup', 'browser', 'bell', 'email'] },
    { id: 'email-reply', label: 'Email reply', description: 'Get notified when a contact replies to an email sent from your personal email.', channels: ['popup', 'browser', 'bell', 'email'] },
  ]},
  { id: 'FORM_SUBMISSIONS', label: 'Form submissions', channels: ['popup', 'browser', 'email'], items: [
    { id: 'form-submission', label: 'Form submission', description: 'Get notified when a visitor or contact submits a form.', channels: ['popup', 'browser', 'email'] },
  ]},
  { id: 'GENERAL', label: 'General', channels: ['popup', 'browser', 'bell', 'email'], items: [
    { id: 'assigned-activity', label: 'Assigned activity', description: 'Get notified when an activity is assigned to you.', channels: ['popup', 'browser', 'bell', 'email'] },
    { id: 'follow-record', label: 'Record you follow updated', description: 'Get notified when a record you follow is updated.', channels: ['popup', 'browser', 'bell', 'email'] },
  ]},
  { id: 'WORKFLOWS', label: 'Workflows', channels: ['bell', 'email'], items: [
    { id: 'workflow-notification', label: 'Custom notification from workflows', description: 'Get notified when a workflow sends an internal notification.', channels: ['bell', 'email'] },
  ]},
]

const NOTIF_CHANNEL_LABELS: Record<NotifChannel, string> = { popup: 'Pop-up', browser: 'Browser', bell: 'Bell', email: 'Email' }

const GENERAL_SUB_TABS: Array<{ key: GeneralSubTab; label: string }> = [
  { key: 'profile', label: 'Profile' },
  { key: 'email', label: 'Email' },
  { key: 'calling', label: 'Calling' },
  { key: 'calendar', label: 'Calendar' },
  { key: 'tasks', label: 'Tasks' },
  { key: 'security', label: 'Security' },
  { key: 'automation', label: 'Automation' },
]

const PREFS_TAB_LABELS: Partial<Record<PrefsTab, string>> = {
  duplicates: 'Possible Duplicates',
  general: 'General',
  security: 'Security',
  account: 'Account Defaults',
  properties: 'Properties',
  calling: 'Calling',
  users: 'Users & Teams',
  notifications: 'Notifications',
  'audit-log': 'Audit Log',
  ai: 'AI Providers',
  'import-export': 'Import & Export',
  backup: 'Backup & Restore',
  meetings: 'Meetings',
  'connected-apps': 'Connected Apps',
  'deal-automation': 'Deal Automation',
  'obj-contacts': 'Contacts',
  'obj-companies': 'Clients',
  'obj-leads': 'Leads',
  'obj-deals': 'Deals',
  'obj-tickets': 'Tickets',
  'obj-products': 'Products',
  'obj-quotes': 'Quotes',
  'obj-projects': 'Projects',
  'obj-services': 'Services',
  'obj-appointments': 'Appointments',
  'obj-invoices': 'Invoices',
  'obj-activities': 'Activities',
  'obj-workflows': 'Workflows',
  inboxes: 'Inboxes',
  'mkt-forms': 'Forms',
  'com-tax': 'Tax',
  'invoice-scanner-settings': 'Invoice Scanner',
  'document-mgmt-settings': 'Document Management',
  'n8n-integration': 'n8n Integration',
  'mkt-sending': 'Email',
  'mkt-social': 'Social Connections',
  'com-accounting': 'Accounting Defaults',
  'report-defaults': 'Report Defaults',
  'company-events': 'Company Events',
  'hr-documents': 'HR Documents',
  'automation-settings': 'Automation',
  'org-types': 'Client Types',
  'email-templates': 'Email Templates',
  organization: 'Organization',
  permissions: 'Roles & Permissions',
  territories: 'Territories',
  'lead-routing': 'Lead Routing',
  numbering: 'Numbering',
  'saved-views': 'Saved Views',
  'error-center': 'Error Center',
}

const PREFS_TAB_DESCRIPTIONS: Partial<Record<PrefsTab, string>> = {
  general: 'These preferences only apply to you.',
  security: 'How you sign in to BotPros Ops — Google SSO via Cloudflare Access.',
  account: 'View and manage account-level settings.',
  properties: 'Manage custom properties for your CRM objects.',
  calling: 'Configure RingCentral integration, call routing, and recording settings.',
  users: 'Add, edit, and manage CRM user accounts.',
  notifications: 'These preferences will only be applied to you.',
  'audit-log': 'View user actions taken in your account over the last 365 days.',
  'connected-apps': 'Manage third-party apps connected to your account.',
  'deal-automation': 'Automation that runs when a deal is marked Won — timeline activity and company-feed announcement.',
  'obj-contacts': 'Configure how contact records are stored and displayed.',
  'obj-companies': 'Configure how client records are stored and displayed.',
  'obj-leads': 'Configure how lead records are stored and displayed.',
  'obj-deals': 'Configure how deal records are stored and displayed.',
  'obj-tickets': 'Configure how ticket records are stored and displayed.',
  'obj-products': 'Configure how product records are stored and displayed.',
  'obj-quotes': 'Configure how quote records are stored and displayed.',
  'obj-projects': 'Configure how project records are stored and displayed.',
  'obj-services': 'Configure how service records are stored and displayed.',
  'obj-appointments': 'Configure how appointment records are stored and displayed.',
  'obj-invoices': 'Configure how invoice records are stored and displayed.',
  'obj-activities': 'Configure how activity records are stored and displayed.',
  'obj-workflows': 'Configure how workflow records are stored and displayed.',
  inboxes: 'Manage shared inboxes for team communication.',
  'mkt-forms': 'Manage form settings, submissions, and follow-up actions.',
  'com-tax': 'Manage tax rates, regions, and tax calculation rules.',
  'invoice-scanner-settings': 'Configure auto-approval thresholds, vendor matching, and default accounts for scanned invoices.',
  'document-mgmt-settings': 'Set file size limits, allowed types, retention policies, and auto-archive rules for documents.',
  'n8n-integration': 'View and configure your n8n automation webhooks, processing status, and error logs.',
  'mkt-sending': 'Configure your email sending domain, default from address, send limits, and bounce handling.',
  'mkt-social': 'Manage connected social media accounts and auto-post settings.',
  'com-accounting': 'Set fiscal year, accounting method, default payment terms, and aging bucket definitions.',
  'report-defaults': 'Configure default date ranges, dashboard refresh intervals, and scheduled report delivery.',
  'hr-documents': 'Configure document template defaults, e-signature settings, and onboarding workflow steps.',
  'automation-settings': 'Set workflow execution limits, sequence sending windows, and chatflow widget appearance.',
  ai: 'AI provider status — which model keys are configured and what they unlock.',
  'import-export': 'Import and export your CRM data.',
  backup: 'Create and restore database backups.',
  meetings: 'Configure meeting scheduling defaults.',
  'org-types': 'Organization sub-types (community, HOA, management company…) and role types used by client and contact forms.',
  'email-templates': 'Admin-managed email templates available to every user in the compose template picker.',
  organization: 'BotPros Companies parent + Painting / Accounting / Construction / Windows & Doors child organizations and reporting structure.',
  permissions: 'Define role capabilities for super-admin, admin, office, salesperson, and accounting users.',
  territories: 'Sales territories (zones A/B/C/D) with commercial and residential rosters per zone.',
  'lead-routing': 'Configure how Base Code marketing leads route to assistants — shared inbox, claim flow, read/unread state, required notes.',
  numbering: 'Auto-generated project ID format (year + division code + 4-digit serial, e.g. 26BP0001).',
  'saved-views': 'Your saved table layouts — column widths, filters, sorts, and density. Manages views stored in D1 so they follow you across devices.',
  duplicates: 'Review likely-duplicate organizations grouped by name, confirm the survivor, and merge — with full invoice / balance context before any permanent merge.',
  'error-center': 'Crash and error tickets captured from the app — who hit it, where, what they were doing, plus the AI-written resolution and a sanitized page snapshot.',
}

// Internal BotPros tool — not a SaaS — so the IA is pruned to what actually
// applies (single tenant, USD only, no marketplace, no website CMS, no
// HubSpot dev portal). Sections marked adminOnly are hidden when the user
// is not super-admin or admin.
const PREFS_NAV_SECTIONS: PrefsNavSection[] = [
  {
    title: 'My Preferences',
    items: [
      { id: 'general', label: 'General', tab: 'general', keywords: ['general', 'personal', 'user preferences', 'profile'] },
      { id: 'notifications', label: 'Notifications', tab: 'notifications' },
      { id: 'security', label: 'Security & Sign-in', tab: 'security', keywords: ['sign in', 'sso', 'google', 'sessions', 'password'] },
    ],
  },
  {
    title: 'Administration',
    allowedRoles: ['super_admin', 'admin'],
    items: [
      { id: 'account-defaults', label: 'Account Defaults', tab: 'account', keywords: ['account'] },
      { id: 'organization', label: 'Organization', tab: 'organization', keywords: ['org', 'company', 'department', 'parent', 'painting', 'accounting'] },
      { id: 'users-teams', label: 'Users & Teams', tab: 'users', keywords: ['users', 'teams'] },
      { id: 'permissions', label: 'Roles & Permissions', tab: 'permissions', keywords: ['roles', 'permissions', 'access', 'security'] },
      { id: 'org-types', label: 'Client Types', tab: 'org-types', keywords: ['client types', 'organization types', 'sub-types', 'role types', 'hoa', 'vocabulary'] },
    ],
  },
  {
    title: 'Data',
    allowedRoles: ['super_admin', 'admin'],
    items: [
      { id: 'objects', label: 'Objects', expandable: true, children: [
        { id: 'properties', label: 'Field Definitions', tab: 'properties' as PrefsTab },
        { id: 'obj-contacts', label: 'Contacts', tab: 'obj-contacts' as PrefsTab },
        { id: 'obj-companies', label: 'Clients', tab: 'obj-companies' as PrefsTab },
        { id: 'obj-leads', label: 'Leads', tab: 'obj-leads' as PrefsTab },
        { id: 'obj-deals', label: 'Deals', tab: 'obj-deals' as PrefsTab },
        { id: 'obj-tickets', label: 'Tickets', tab: 'obj-tickets' as PrefsTab },
        { id: 'obj-products', label: 'Products', tab: 'obj-products' as PrefsTab },
        { id: 'obj-quotes', label: 'Quotes', tab: 'obj-quotes' as PrefsTab },
        { id: 'obj-projects', label: 'Projects', tab: 'obj-projects' as PrefsTab },
        { id: 'obj-services', label: 'Services', tab: 'obj-services' as PrefsTab },
        { id: 'obj-appointments', label: 'Appointments', tab: 'obj-appointments' as PrefsTab },
        { id: 'obj-invoices', label: 'Invoices', tab: 'obj-invoices' as PrefsTab },
        { id: 'obj-activities', label: 'Activities', tab: 'obj-activities' as PrefsTab },
        { id: 'obj-workflows', label: 'Workflows', tab: 'obj-workflows' as PrefsTab },
      ] },
      { id: 'duplicates', label: 'Duplicates', tab: 'duplicates', keywords: ['duplicate', 'dedup', 'merge', 'clusters', 'data quality'] },
      { id: 'import-export', label: 'Import & Export', tab: 'import-export' },
      { id: 'backup', label: 'Backup & Restore', tab: 'backup', keywords: ['backup', 'restore', 'snapshot', 'export'] },
      { id: 'saved-views', label: 'Saved Views', tab: 'saved-views', keywords: ['columns', 'filters', 'layout', 'table', 'view'] },
      { id: 'numbering', label: 'Numbering', tab: 'numbering', keywords: ['project id', 'numbering', 'serial'] },
      { id: 'audit-log', label: 'Audit Log', tab: 'audit-log' },
      { id: 'report-defaults', label: 'Report Defaults', tab: 'report-defaults' },
    ],
  },
  {
    title: 'Communications',
    // Office can manage Inboxes / Calling / Meetings since they handle
    // phone + email day-to-day.
    allowedRoles: ['super_admin', 'admin', 'office'],
    items: [
      { id: 'inboxes', label: 'Inboxes', tab: 'inboxes', keywords: ['shared inbox', 'availability', 'allow', 'deny', 'senders'] },
      { id: 'calling', label: 'Calling', tab: 'calling', expandable: true, children: [
        { id: 'call-setup', label: 'Call Setup', tab: 'calling' },
        { id: 'blocked-numbers-list', label: 'Blocked Numbers', tab: 'calling' },
      ] },
      { id: 'meetings', label: 'Meetings', tab: 'meetings' },
      { id: 'email-templates', label: 'Email Templates', tab: 'email-templates', keywords: ['templates', 'compose', 'merge fields'] },
    ],
  },
  {
    title: 'Marketing',
    allowedRoles: ['super_admin', 'admin', 'office'],
    items: [
      { id: 'mkt-sending', label: 'Email', tab: 'mkt-sending', keywords: ['marketing email', 'sending domain', 'from address'] },
      { id: 'mkt-forms', label: 'Forms', tab: 'mkt-forms' },
      { id: 'mkt-social', label: 'Social', tab: 'mkt-social' },
    ],
  },
  {
    title: 'Operations',
    allowedRoles: ['super_admin', 'admin'],
    items: [
      { id: 'lead-routing', label: 'Lead Routing', tab: 'lead-routing', keywords: ['leads', 'inbox', 'base code', 'marketing'] },
      { id: 'territories', label: 'Territories', tab: 'territories', keywords: ['territory', 'zones', 'regions', 'commercial', 'residential'] },
      { id: 'deal-automation', label: 'Deal Automation', tab: 'deal-automation', keywords: ['won', 'automation', 'kickoff'] },
      { id: 'automation-settings', label: 'Automation Rules', tab: 'automation-settings' },
      { id: 'com-accounting', label: 'Accounting Defaults', tab: 'com-accounting' },
      { id: 'com-tax', label: 'Tax', tab: 'com-tax' },
      { id: 'document-mgmt-settings', label: 'Documents', tab: 'document-mgmt-settings' },
      { id: 'hr-documents', label: 'HR Documents', tab: 'hr-documents' },
      { id: 'invoice-scanner-settings', label: 'Invoice Scanner', tab: 'invoice-scanner-settings' },
      { id: 'company-events', label: 'Company Events', tab: 'company-events' },
    ],
  },
  {
    title: 'Integrations',
    allowedRoles: ['super_admin', 'admin'],
    items: [
      { id: 'connected-apps', label: 'Connected Apps', tab: 'connected-apps' },
      { id: 'ai', label: 'AI Providers', tab: 'ai', keywords: ['anthropic', 'openai', 'grok', 'transcription'] },
      { id: 'n8n-integration', label: 'n8n Integration', tab: 'n8n-integration' },
      { id: 'error-center', label: 'Error Center', tab: 'error-center', keywords: ['errors', 'crash', 'tickets', 'reliability', 'ai', 'snapshot', 'breadcrumbs'] },
    ],
  },
]

// ONE canonical deep-link whitelist, derived from the nav registry so the nav
// and hash routing can never drift apart. Legacy/renamed tabs resolve via
// aliases; anything unknown lands on General.
const PREFS_TAB_ALIASES: Record<string, PrefsTab> = {
  'mkt-email': 'mkt-sending',
  'marketplace': 'connected-apps',
  'availability-mgmt': 'inboxes',
  'allow-deny-list': 'inboxes',
}
const VALID_PREFS_TABS: Set<string> = new Set(
  PREFS_NAV_SECTIONS.flatMap(s => s.items.flatMap(i => [
    ...(i.tab ? [i.tab] : []),
    ...((i.children ?? []).map(ch => ch.tab).filter((t): t is PrefsTab => !!t)),
  ]))
)
function resolvePrefsTab(seg: string | undefined): PrefsTab {
  if (!seg) return 'general'
  const tab = PREFS_TAB_ALIASES[seg] ?? seg
  return VALID_PREFS_TABS.has(tab) ? (tab as PrefsTab) : 'general'
}
// Sanitize HTML to prevent XSS — strips script tags, event handlers, and dangerous attributes
// ENT-002: route the playbook/knowledge-base preview through the centralized
// DOMPurify helper (src/lib/safeHtml.ts) instead of the previous hand-rolled
// DOMParser strip, which was weaker than DOMPurify (missed svg/mathml/data:
// vectors). Kept as a thin alias so the call site stays readable.
function sanitizeHtml(html: string): string {
  return safeHtml(html)
}

// Click-to-call link for phone numbers
function PhoneLink({ phone, style }: { phone: string | null | undefined; style?: React.CSSProperties }) {
  if (!phone) return <span className="cell-empty">--</span>
  return <a href={`tel:${phone}`} style={{ color: 'inherit', textDecoration: 'none', ...style }} onClick={(e) => e.stopPropagation()} title={`Call ${phone}`}>{phone}</a>
}

// Google Maps directions link for addresses
function MapsLink({ parts, style }: { parts: (string | null | undefined)[]; style?: React.CSSProperties }) {
  const addr = parts.filter(Boolean).join(', ')
  if (!addr) return <span className="cell-empty">--</span>
  return <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addr)}`} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none', ...style }} onClick={(e) => e.stopPropagation()} title={`Get directions to ${addr}`}>{addr}</a>
}

// ── Home dashboard signal badge (home-bento-v3) ─────────────────────────────
// Chips-family widgets (HR Center / Today's work queue / Accounting snapshot)
// rendered as a compact QUARTER-WIDTH signal badge card. NO hover-reveal (user
// directive): at rest the badge shows its section title + only the LOUD (non-zero)
// chips as inline stats (or a single "All clear" pill when quiet); a caret CLICK
// opens a breakdown popover (reusing the calls-stats-badge popover pattern —
// aria-haspopup="dialog", outside-click + Esc close, no portal) holding the FULL
// chip grid INCLUDING zeros plus the section's "Open …" action. A FAILED fetch
// never collapses to "all clear" — it shows an error+retry row (no popover).
// All data/count/link/permission behavior stays in the caller; this is presentation.
interface HomeSignalChip {
  key: string
  label: string
  value: number
  displayValue?: string
  tone: string
  action: () => void
  icon?: React.ReactNode
  iconBg?: string
  iconColor?: string
  helper?: string | null
  attention?: boolean
}
function HomeSignalSection({
  title, subtitle, openLabel, onOpen, chips, loading, loaded, error, variant = 'icon', onRetry,
}: {
  title: string
  subtitle: string
  openLabel: string
  onOpen: () => void
  chips: HomeSignalChip[]
  loading: boolean
  loaded: boolean
  error: boolean
  variant?: 'icon' | 'plain'
  onRetry?: () => void
}) {
  const [open, setOpen] = React.useState(false)
  const wrapRef = React.useRef<HTMLElement | null>(null)
  const caretRef = React.useRef<HTMLButtonElement | null>(null)
  const signalChips: HomeSignalChipData[] = chips.map((c) => ({
    key: c.key,
    value: c.value,
    attention: c.attention ?? (c.tone === 'warn' || c.tone === 'bad' || c.tone === 'attention'),
  }))
  const status: HomeWidgetStatus = homeWidgetStatus(signalChips, { loading, loaded, error })
  const { loud } = homePartitionChips(chips)
  const popupChips = homeOrderChipsForPopup(chips) // loud-then-muted; every chip incl. zeros

  // Click-only popover: outside-pointerdown + Esc close (NO hover triggers).
  React.useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false) }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setOpen(false); caretRef.current?.focus() } }
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('pointerdown', onDown); document.removeEventListener('keydown', onKey) }
  }, [open])

  const renderChip = (c: HomeSignalChip) => {
    const close = () => setOpen(false)
    if (variant === 'plain') {
      return (
        <button type="button" key={c.key} className={`command-queue-card tone-${c.tone}${c.attention ? ' command-queue-card--attention' : ''}`} onClick={() => { c.action(); close() }}>
          {c.attention && (
            <span className="command-queue-attn-icon" aria-hidden="true">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
            </span>
          )}
          <strong>{c.displayValue ?? c.value}</strong>
          <span>{c.label}</span>
        </button>
      )
    }
    return (
      <button type="button" key={c.key} className={`command-queue-card tone-${c.tone}`} onClick={() => { c.action(); close() }} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: c.iconBg, color: c.iconColor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{c.icon}</div>
        <div style={{ display: 'grid', gap: 1, minWidth: 0 }}>
          <strong>{c.displayValue ?? c.value}</strong>
          <span>{c.label}{c.helper ? ` · ${c.helper}` : ''}</span>
        </div>
      </button>
    )
  }

  const caret = (
    <button
      ref={caretRef}
      type="button"
      className={`home-signal-caret${open ? ' is-open' : ''}`}
      aria-haspopup="dialog"
      aria-label={open ? `Close ${title} breakdown` : `Open ${title} breakdown`}
      aria-expanded={open}
      onClick={(e) => { e.stopPropagation(); setOpen((v) => !v) }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
    </button>
  )

  return (
    <section ref={wrapRef} className={`home-signal-badge home-signal home-signal--${status}${open ? ' is-open' : ''}`} data-signal={status}>
      <div className="home-signal-badge-head">
        <div className="home-signal-badge-titles">
          <h2>{title}</h2>
          <p>{subtitle}</p>
        </div>
        {status !== 'loading' && status !== 'error' && caret}
      </div>

      {loading ? (
        <div className="home-signal-badge-body">
          <div className="home-signal-badge-skel">
            <span className="har-skeleton command-queue-skel-value" />
            <span className="har-skeleton command-queue-skel-label" />
          </div>
        </div>
      ) : status === 'error' ? (
        <div className="home-signal-error" role="status">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
          <span>Couldn’t load {title.toLowerCase()}.</span>
          {onRetry && <button type="button" className="home-signal-retry" onClick={onRetry}>Retry</button>}
        </div>
      ) : status === 'quiet' ? (
        <div className="home-signal-badge-body">
          <button type="button" className="home-signal-allclear-main" onClick={() => setOpen(true)} title={`${title} — all clear`}>
            <span className="home-signal-check" aria-hidden="true">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
            </span>
            All clear
          </button>
        </div>
      ) : (
        <div className="home-signal-badge-body">
          <div className="home-signal-badge-summary">
            {loud.map((c) => (
              <button key={c.key} type="button" className={`home-signal-stat tone-${c.tone}${c.attention ? ' is-attention' : ''}`} onClick={c.action} title={c.helper ? `${c.label} · ${c.helper}` : c.label}>
                <strong>{c.value}</strong>
                <span>{c.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <button type="button" className="command-ghost-btn home-signal-badge-open" onClick={onOpen}>{openLabel}</button>

      {open && status !== 'loading' && status !== 'error' && (
        <div className="home-signal-pop" role="dialog" aria-label={`${title} breakdown`}>
          <div className="home-signal-pop-head">
            <span className="home-signal-pop-title">{title}</span>
            <span className="home-signal-pop-sub">{subtitle}</span>
          </div>
          <div className="home-signal-pop-grid">
            {popupChips.map(renderChip)}
          </div>
          <button type="button" className="home-signal-pop-open" onClick={() => { setOpen(false); onOpen() }}>{openLabel}</button>
        </div>
      )}
    </section>
  )
}

// ── Home dashboard condensed KPI strip (home-bento-v3) ──────────────────────
// The headline KPIs collapse from tall cards into ONE slim segmented
// strip (≤48px): each segment is `value + label` inline, hairline-divided, with a
// single caret at the strip's end that CLICK-opens a small popover (same pattern
// as the signal badges) listing every segment's sub-line (helper/delta). Purely
// presentational — the caller builds + permission-filters the `items` array.
interface HomeKpiItem { key: string; label: string; value: string; helper?: string }
function HomeKpiStrip({ items, loading }: { items: HomeKpiItem[]; loading: boolean }) {
  const [open, setOpen] = React.useState(false)
  const wrapRef = React.useRef<HTMLDivElement | null>(null)
  const caretRef = React.useRef<HTMLButtonElement | null>(null)
  React.useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false) }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setOpen(false); caretRef.current?.focus() } }
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('pointerdown', onDown); document.removeEventListener('keydown', onKey) }
  }, [open])
  if (items.length === 0) return null
  return (
    <div className="command-kpi-strip" ref={wrapRef}>
      <div className="command-kpi-strip-inner">
        {items.map((kpi, i) => (
          <React.Fragment key={kpi.key}>
            {i > 0 && <span className="command-kpi-strip-div" aria-hidden="true" />}
            <div className="command-kpi-seg">
              {loading ? (
                <span className="har-skeleton command-kpi-seg-skel" />
              ) : (
                <>
                  <strong>{kpi.value}</strong>
                  <span>{kpi.label}</span>
                </>
              )}
            </div>
          </React.Fragment>
        ))}
      </div>
      <button
        ref={caretRef}
        type="button"
        className={`home-signal-caret command-kpi-caret${open ? ' is-open' : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={open ? 'Close metric details' : 'Open metric details'}
        onClick={() => setOpen((v) => !v)}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
      </button>
      {open && (
        <div className="home-signal-pop command-kpi-pop" role="dialog" aria-label="Metric details">
          <div className="home-signal-pop-head">
            <span className="home-signal-pop-title">Metrics</span>
            <span className="home-signal-pop-sub">Company overview</span>
          </div>
          <div className="command-kpi-pop-list">
            {items.map((kpi) => (
              <div className="command-kpi-pop-row" key={kpi.key}>
                <span className="command-kpi-pop-label">{kpi.label}</span>
                <span className="command-kpi-pop-value">{kpi.value}</span>
                {kpi.helper && <span className="command-kpi-pop-help">{kpi.helper}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

type HomeMarketingItem = {
  id: string
  title: string
  kind: 'Run' | 'Deliverable'
  status?: string | null
  createdAt: string | null
}

// The Marketing Hub is intentionally optional on older or restricted tenants.
// Keep its lifecycle inside widgetStatus, but render an unavailable response as
// a quiet empty state so a missing marketing route never becomes a Home alert.
function HomeMarketingPulse({
  items,
  loading,
  error,
  onOpen,
}: {
  items: HomeMarketingItem[] | null
  loading: boolean
  error: boolean
  onOpen: () => void
}) {
  const status = homeWidgetStatus(
    (items || []).map((item) => ({ key: item.id, value: 1 })),
    { loading, loaded: items !== null, error },
  )
  const formatDate = (value: string | null) => {
    if (!value) return ''
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  return (
    <section className={`panel home-marketing-pulse home-signal home-signal--${status}`} data-signal={status}>
      <div className="panel-head">
        <div>
          <h2>Marketing pulse</h2>
          <p>Recent runs and deliverables</p>
        </div>
        <button type="button" className="panel-open-btn" onClick={onOpen}>Open marketing</button>
      </div>
      <div className="panel-body">
        {loading ? (
          <div className="agenda-skeleton-list">
            {Array.from({ length: 3 }).map((_, idx) => (
              <div className="agenda-skeleton-item" key={`marketing-skeleton-${idx}`}>
                <span className="har-skeleton agenda-skeleton-time" />
                <div>
                  <span className="har-skeleton agenda-skeleton-title" />
                  <span className="har-skeleton agenda-skeleton-copy" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div style={{ padding: '28px 20px', textAlign: 'center', color: '#94a3b8' }}>
            <div style={{ fontWeight: 600, fontSize: '0.86rem', color: '#64748b', marginBottom: 4 }}>Marketing is unavailable</div>
            <div style={{ fontSize: '0.76rem' }}>Recent work will appear here when the module is enabled.</div>
          </div>
        ) : !items || items.length === 0 ? (
          <div style={{ padding: '28px 20px', textAlign: 'center', color: '#94a3b8' }}>
            <div style={{ fontWeight: 600, fontSize: '0.86rem', color: '#64748b', marginBottom: 4 }}>No recent marketing work</div>
            <div style={{ fontSize: '0.76rem' }}>Runs and deliverables will appear here as the team ships.</div>
          </div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {items.slice(0, 5).map((item) => (
              <li key={item.id} style={{ padding: '10px 16px', borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                  <strong style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.82rem', color: '#33475b' }} title={item.title}>{item.title}</strong>
                  <span style={{ flexShrink: 0, fontSize: '0.7rem', color: '#94a3b8' }}>{formatDate(item.createdAt)}</span>
                </div>
                <div style={{ marginTop: 3, fontSize: '0.72rem', color: '#7c98b6' }}>{item.kind}{item.status ? ` · ${item.status}` : ''}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}

// Auto-linkify plain text: split on http(s):// or www. URLs and return an array
// of plain strings + <a> elements (REACT NODES — never dangerouslySetInnerHTML,
// so user-typed note text carries zero XSS surface). Trailing punctuation is
// left outside the link; bare www. hosts are prefixed with https://. Used where
// saved call notes / activity descriptions render in record timelines.
function linkifyText(text: string | null | undefined): React.ReactNode[] {
  if (!text) return []
  const re = /((?:https?:\/\/|www\.)[^\s<]+[^\s<.,;:!?)\]}'"])/gi
  const out: React.ReactNode[] = []
  let last = 0
  let key = 0
  let m: RegExpExecArray | null
  while ((m = re.exec(text)) !== null) {
    const url = m[0]
    if (m.index > last) out.push(text.slice(last, m.index))
    const href = url.toLowerCase().startsWith('www.') ? `https://${url}` : url
    out.push(
      <a key={`lk-${key++}`} href={href} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: '#0091ae', textDecoration: 'underline' }}>{url}</a>
    )
    last = m.index + url.length
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}

// Tabs whose settings persist via /api/admin/org-settings (migration 094).
// Listed at module scope so the useEffect deps can stay clean.
const ORG_SETTINGS_TABS = ['com-tax', 'com-accounting', 'mkt-forms', 'account', 'report-defaults', 'automation-settings', 'document-mgmt-settings', 'hr-documents'] as const

// Org-level pref write-through helper (Tax / Forms / Accounting Defaults).
// Persists to localStorage immediately (so UI doesn't flicker on tab
// switches) and queues a debounced PATCH to /api/admin/org-settings so
// the value follows the user across devices. The debounce coalesces
// rapid edits (a Tax-ID input typing burst) into a single API call;
// the storage event also fires immediately so all other read-sites
// pick up the new value without a round-trip.
const orgSettingsQueue: Record<string, string | null> = {}
let orgSettingsTimer: number | null = null
function flushOrgSettings() {
  orgSettingsTimer = null
  const patch = { ...orgSettingsQueue }
  for (const k of Object.keys(orgSettingsQueue)) delete orgSettingsQueue[k]
  if (Object.keys(patch).length === 0) return
  updateOrgSettings(patch).catch(e => console.warn('org-settings save:', e?.message || e))
}
function saveOrgSetting(key: string, value: string | null) {
  // Write-through cache: localStorage immediately for snappy UI.
  try {
    if (value === null) localStorage.removeItem(key)
    else localStorage.setItem(key, value)
  } catch { /* private mode, quota — ignore */ }
  // Debounce server PATCH (300ms) — coalesces typing bursts.
  orgSettingsQueue[key] = value
  if (orgSettingsTimer !== null) window.clearTimeout(orgSettingsTimer)
  orgSettingsTimer = window.setTimeout(flushOrgSettings, 300) as unknown as number
  // Notify any listening components.
  try { window.dispatchEvent(new Event('storage')) } catch { /* SSR */ }
}

function getHashSegments(): string[] {
  // Strip any `?query=...` portion before splitting so deep-link query params
  // (e.g. `#/calls?activity=12`, `#/hr-center?employee=BX-123`) don't pollute
  // the segment array. Use `getHashQuery()` to read the query side.
  const raw = window.location.hash.replace('#/', '').replace('#', '')
  const path = raw.split('?')[0]
  const segs = path.split('/').filter(Boolean)
  // Legacy-alias rewrite: the view used to be called `inbox` and every old
  // notification + deep link points there. Swap it to `email` at the
  // segment level so the new routing / view-check sees the new name. Also
  // clean up the URL bar so refreshes show the new path.
  if (segs[0] === 'inbox') {
    segs[0] = 'email'
    try {
      const newHash = '#/' + segs.join('/') + (window.location.hash.includes('?') ? '?' + window.location.hash.split('?')[1] : '')
      if (window.location.hash !== newHash) window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search + newHash)
    } catch { /* non-fatal */ }
  }
  return segs
}

// Returns the URLSearchParams from the current hash (`#/foo?x=1` → ?x=1).
// Empty when no `?` is present. Safe to call from any hash-aware effect.
function getHashQuery(): URLSearchParams {
  const raw = window.location.hash.replace('#/', '').replace('#', '')
  const idx = raw.indexOf('?')
  return new URLSearchParams(idx >= 0 ? raw.slice(idx + 1) : '')
}

// Strip the `?...` portion from the current hash and replace history entry
// (no scroll, no hashchange event). Used after a deep-link param has been
// consumed so a refresh doesn't keep re-applying it.
function clearHashQuery(): void {
  const raw = window.location.hash
  const idx = raw.indexOf('?')
  if (idx < 0) return
  const cleaned = raw.slice(0, idx)
  try {
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}${cleaned}`)
  } catch { /* old browser — non-fatal */ }
}
function getViewFromHash(): ViewMode {
  let hash = getHashSegments()[0] || ''
  if (hash === 'accounts') hash = 'contacts' // backwards compat
  if (hash === 'clients' || hash === 'organizations') hash = 'companies' // canonical client route (+legacy alias) → companies view
  if (hash === 'settings') hash = 'preferences' // merged into preferences
  if (hash === 'subcontractors') hash = 'command' // BotPros: subcontractors subsystem disabled (Hartzell leftover) → home
  return VALID_VIEWS.has(hash as ViewMode) ? (hash as ViewMode) : 'command'
}

// ---------------------------------------------------------------------------
// HR Center secondary-nav metadata (CRM-style flyout + deep URLs)
// ---------------------------------------------------------------------------
// These power the sidebar flyout submenu (same component/markup/CSS the CRM item
// uses) AND the nested hash routing #/hr-center/<tab>[/<selection>].
// Keys mirror the top in-view tab strips.
// (hrPortalView). `navKey` (when set) is the per-role visibility
// key used by isNavHidden(); `cap` (when set) gates the whole tab on a capability.
type HrTabKey = 'dashboard'|'templates'|'forms'|'assignments'|'pipelines'|'employees'|'change-requests'|'timesheets'|'settings'
const HR_TABS: { key: HrTabKey; label: string; navKey?: string }[] = [
  { key: 'dashboard',       label: 'Dashboard',       navKey: 'hr-center.dashboard' },
  { key: 'templates',       label: 'Templates',       navKey: 'hr-center.templates' },
  { key: 'forms',           label: 'Forms',           navKey: 'hr-center.forms' },
  { key: 'assignments',     label: 'Assignments',     navKey: 'hr-center.assignments' },
  { key: 'pipelines',       label: 'Pipelines',       navKey: 'hr-center.pipelines' },
  { key: 'employees',       label: 'Employees',       navKey: 'hr-center.employees' },
  { key: 'change-requests', label: 'Change Requests', navKey: 'hr-center.change-requests' },
  // Timesheets is its OWN top-level view (#/timesheets) surfaced as an HR sub-tab;
  // gated by the legacy crm.timesheets nav key. The flyout routes to #/timesheets.
  { key: 'timesheets',      label: 'Timesheets',      navKey: 'crm.timesheets' },
  { key: 'settings',        label: 'Settings',        navKey: 'hr-center.settings' },
]
const HR_DEFAULT_TAB: HrTabKey = 'dashboard'
const HR_PORTAL_TABS = new Set(['dashboard','templates','forms','assignments','pipelines','employees','change-requests','settings'])

// `/clients` is the canonical hash route; `/companies` and `/organizations`
// (both former canonical names) are backward-compat aliases rewritten to the `/clients` equivalent so
// existing deep links, bookmarks and saved views keep working. Returns true if
// a redirect was performed.
function redirectLegacyCompaniesHash(): boolean {
  const raw = window.location.hash || ''
  // Match a leading `#/companies` segment (root, /record/..., or with query).
  if (!/^#\/(companies|organizations)(\/|\?|$)/.test(raw)) return false
  const rewritten = raw.replace(/^#\/(companies|organizations)/, '#/clients')
  try {
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}${rewritten}`)
  } catch {
    window.location.hash = rewritten.slice(1)
  }
  return true
}

// bp-mkt-ia-v1 — legacy marketing member-tab deep links fold into their 5-stage home, rewriting the
// URL before parse so the address bar stays canonical (council/research→strategy, seo→content,
// paid→outbound, growth→conversion). Returns true if a redirect was performed.
function redirectLegacyMarketingHash(): boolean {
  const raw = window.location.hash || ''
  const m = raw.match(/^#\/marketing\/(council|research|seo|paid|growth)(\/|\?|$)/)
  if (!m) return false
  const home: Record<string, string> = { council: 'strategy', research: 'strategy', seo: 'content', paid: 'outbound', growth: 'conversion' }
  const rewritten = raw.replace(/^#\/marketing\/(council|research|seo|paid|growth)/, `#/marketing/${home[m[1]]}`)
  try {
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}${rewritten}`)
  } catch {
    window.location.hash = rewritten.slice(1)
  }
  return true
}

// ---------------------------------------------------------------------------
// Email-thread grouping (Contact, Deal, Lead activity timelines)
// ---------------------------------------------------------------------------
// Inbound emails populate `graph_conversation_id` via the change-notify
// webhook; outbound emails populate it via the Sent-Items poll inside
// /api/crm/rc/email/send. A 10-reply thread therefore generates 10 activity
// rows that share the same conversation id — without grouping the timeline
// becomes a wall of duplicates.
//
// `EmailThread` aggregates all rows that share a conversation id into one
// virtual entry. The newest reply is kept as `latest`; all rows (newest
// first) hang under `messages`. Legacy emails with no conversation id flow
// through as singleton entries — same shape, count=1.
//
// `groupActivitiesWithEmailThreads` walks an activity list (already in
// newest-first order) and replaces same-conversation email rows with a
// single `EmailThread` entry positioned at the original chronological slot
// of the newest message — so a fresh reply bubbles to the top exactly like
// a single email would. Non-email rows pass through untouched.
// ---------------------------------------------------------------------------
type EmailThread = {
  __kind: 'thread'
  conversationId: string
  latest: CrmActivity
  messages: CrmActivity[] // newest-first, includes `latest`
  count: number
}

type TimelineEntry = CrmActivity | EmailThread

function isEmailThread(entry: TimelineEntry): entry is EmailThread {
  return (entry as EmailThread).__kind === 'thread'
}

function isEmailActivityForThread(act: CrmActivity): boolean {
  // Match the same provider-type detection used by getActType / getDealActType.
  const pt = (act.provider_type || '').toUpperCase()
  return pt === '4' || pt === 'EMAIL' || pt.includes('EMAIL')
}

function groupActivitiesWithEmailThreads(activities: CrmActivity[]): TimelineEntry[] {
  // Pass 1 — bucket every email row into its conversation; remember the index
  // of the FIRST occurrence so we preserve chronological position.
  const buckets = new Map<string, { firstIdx: number; rows: CrmActivity[] }>()
  const result: (TimelineEntry | null)[] = activities.map(() => null)

  for (let i = 0; i < activities.length; i++) {
    const act = activities[i]
    if (!isEmailActivityForThread(act)) {
      result[i] = act
      continue
    }
    const convId = act.graph_conversation_id
    if (!convId) {
      // Legacy email — render standalone exactly like a non-grouped row.
      result[i] = act
      continue
    }
    const existing = buckets.get(convId)
    if (existing) {
      existing.rows.push(act)
    } else {
      buckets.set(convId, { firstIdx: i, rows: [act] })
    }
  }

  // Pass 2 — drop a thread entry at the FIRST index for each conversation
  // (which is the newest message because the input is newest-first), and
  // erase the remaining indices that were grouped.
  for (const [conversationId, bucket] of buckets) {
    const sorted = [...bucket.rows].sort((a, b) => {
      const ta = a.created ? new Date(a.created).getTime() : 0
      const tb = b.created ? new Date(b.created).getTime() : 0
      return tb - ta
    })
    const thread: EmailThread = {
      __kind: 'thread',
      conversationId,
      latest: sorted[0],
      messages: sorted,
      count: sorted.length,
    }
    result[bucket.firstIdx] = thread
    // Erase the other slots — those rows are now inside `messages`.
    for (let k = 1; k < bucket.rows.length; k++) {
      const idx = activities.indexOf(bucket.rows[k])
      if (idx !== bucket.firstIdx && idx >= 0) result[idx] = null
    }
  }

  return result.filter((e): e is TimelineEntry => e !== null)
}

// Pull a short snippet from an email row's description. The change-notify
// path stores `From: ...\nTo: ...\n\n<snippet>` so we strip the header lines
// to keep the timeline rows readable.
function emailSnippetFromActivity(act: CrmActivity): string {
  const desc = (act.description || '').toString()
  if (!desc) return ''
  const blank = desc.indexOf('\n\n')
  const body = blank >= 0 ? desc.slice(blank + 2) : desc
  return body.replace(/\s+/g, ' ').trim().slice(0, 220)
}

// Pull the "From: ..." line from the same description. Falls back to the
// activity subject when missing.
function emailSenderFromActivity(act: CrmActivity): string {
  const desc = (act.description || '').toString()
  const m = /^From:\s*(.+)$/m.exec(desc)
  if (m) return m[1].trim().replace(/<[^>]+>$/, '').trim() || m[1].trim()
  return act.subject ? '' : 'Unknown sender'
}

type CalendarDay = {
  key: string
  dayNumber: number
  inMonth: boolean
  isToday: boolean
  meetings: Meeting[]
}

const stageOrder: Stage[] = ['Lead', 'Qualified', 'Proposal', 'Negotiation', 'Active']
function getTodayKey() {
  return toDateKey(new Date())
}
const todayKey = getTodayKey()

// Universal Company Event types (was "Company Holiday"). Keep keys aligned with
// the api/server allow-list + the company_holidays.event_type column.
const COMPANY_EVENT_TYPE_LABEL: Record<string, string> = {
  holiday: 'Holiday',
  day_off: 'Day Off',
  closure: 'Closure',
  maintenance: 'Maintenance',
  event: 'Event',
  other: 'Other',
}

const monthFormatter = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' })

const MAX_CAL_EVENTS = 3

const calendarColors: Record<CalendarId, string> = {
  estimates: '#ed6c02',
  projects: '#2e7d32',
  internal: '#1976d2',
  sub_project_starts: '#9c27b0',
}

// Per-team-member colors — derive from employeeColors (data.ts) keyed by first name for quick lookup
const _memberColorsByFirst: Record<string, string> = {}
Object.entries(employeeColors).forEach(([name, color]) => {
  _memberColorsByFirst[name] = color
  _memberColorsByFirst[name.split(' ')[0]] = color
})

function getEventColor(ev: { calendar: CalendarId; type?: string; owner?: string }): string {
  // Check owner first (most reliable — matches calendar filter colors).
  // bp-cal-roster-v1: resolveEmployeeColor keeps the curated employeeColors as
  // overrides but derives a STABLE, distinct hash color for a dynamically-
  // provisioned teammate not in the map (instead of collapsing to the calendar
  // default), so two new teammates never share one color.
  if (ev.owner) {
    return resolveEmployeeColor(ev.owner, employeeColors)
  }
  // Fall back to event type (estimator name from deal title)
  if (ev.type && _memberColorsByFirst[ev.type]) {
    return _memberColorsByFirst[ev.type]
  }
  return calendarColors[ev.calendar] || '#1976d2'
}

// Derive 1-2 char initials from a display name ("Drew BotPros" -> "DH", "Max" -> "M")
function getInitials(name?: string | null): string {
  if (!name) return ''
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return ''
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || ''
  return ((parts[0][0] || '') + (parts[parts.length - 1][0] || '')).toUpperCase()
}

// Keep estimatorColors as alias for backward compatibility (modal tags etc)
const estimatorColors = _memberColorsByFirst

const mockEmployeeNames = ['Avery Sloan', 'Jordan Lee', 'Mina Ortiz', 'Theo Barnes']

const STAGE_COLORS: Record<string, string> = {
  NEW: '#3b82f6',
  PREPAYMENT_INVOICE: '#8b5cf6',
  EXECUTING: '#06b6d4',
  FINAL_INVOICE: '#f59e0b',
  UC_JWD2FR: '#ed6c02',
  UC_X1T387: '#ef4444',
  UC_PY80KD: '#22c55e',
}

// (HR_PIPELINES, HR_STAGES, getHrVisibleStages removed — replaced by portal-style tabs backed by API data)

const PIPELINE_STAGES = [
  { id: 'NEW', label: 'New Deal', color: '#39a8ef' },
  { id: 'PREPAYMENT_INVOICE', label: 'Appt Set', color: '#55d0e0' },
  { id: 'EXECUTING', label: 'Appt Scheduled', color: '#47e4c2' },
  { id: 'FINAL_INVOICE', label: 'Site Visit', color: '#ffa900' },
  { id: 'UC_JWD2FR', label: 'Proposal Sent', color: '#468ee5' },
  { id: 'UC_X1T387', label: 'Not Signed', color: '#1eae43' },
  { id: 'UC_PY80KD', label: 'Signed', color: '#00c4fb' },
]

const DELIVERY_PIPELINE_STAGES = [
  // IDs are persisted API values; only the BotPros display labels belong here.
  { id: 'P_CONTRACT_SIGNED', label: 'Kickoff', color: '#0891b2', sort: 10 },
  { id: 'P_SCHEDULING', label: 'Discovery', color: '#1976d2', sort: 20 },
  { id: 'P_CREW_ASSIGNED', label: 'Scoping', color: '#7b1fa2', sort: 30 },
  { id: 'P_IN_PROGRESS', label: 'Build', color: '#ed6c02', sort: 40 },
  { id: 'P_INSPECTION', label: 'Review', color: '#ff9800', sort: 50 },
  { id: 'P_COMPLETED', label: 'Completed', color: '#2e7d32', sort: 60 },
]
const DELIVERY_TERMINAL_STAGES = [
  { id: 'P_ON_HOLD', label: 'On Hold', color: '#9e9e9e', sort: 70 },
  { id: 'P_CANCELLED', label: 'Cancelled', color: '#d32f2f', sort: 80 },
]
const ALL_DELIVERY_STAGES = [...DELIVERY_PIPELINE_STAGES, ...DELIVERY_TERMINAL_STAGES]
const DELIVERY_STAGE_COLORS: Record<string, string> = Object.fromEntries(ALL_DELIVERY_STAGES.map(s => [s.id, s.color]))
const DELIVERY_STAGE_LABELS: Record<string, string> = Object.fromEntries(ALL_DELIVERY_STAGES.map(s => [s.id, s.label]))

// Project hold types — easily-editable list reused on frontend + server-side validation
const HOLD_TYPES = [
  { value: 'needs_paint_sub', label: 'Needs Paint' },
  { value: 'customer_hold', label: 'Customer Hold' },
  { value: 'other', label: 'Other' },
] as const

// bp-cal-roster-v1: id→name map used to resolve owner/assignee names on tasks,
// tickets and calls. Now a SEED that `applyCalendarRoster()` merges the live
// /api/calendar/users fetch on top of — legacy id→name entries are kept so
// historical rows still render a name, while every active @botpros.ai user (incl.
// newly-provisioned teammates) is layered in from D1. See applyCalendarRoster.
const CALENDAR_USERS_MAP_SEED: Record<number, string> = {
  1: 'Admin', 6: 'Leandro', 8: 'Maxwell Ritchie', 31: 'Kadell Labs Bot',
  35: 'Carly BotPros', 41: 'Ira', 42: 'Nancy', 48: 'Drew', 49: 'Tony',
  52: 'HiperDevs', 73: 'Leo', 79: 'Jermane',
}
let CALENDAR_USERS_MAP: Record<number, string> = { ...CALENDAR_USERS_MAP_SEED }

// Post-call wrap-up assignment (call-assign-link-v1) — the staged deal/project
// chip for the review sheet. `type` discriminates a deal owner (owner_type_id=2)
// from a project owner (owner_type_id=3 with a non-empty string owner_id); the
// picker searches DEALS only and nests projects under them (buildAssignRows).
type CallAssignSelection = { type: 'deal' | 'project'; id: string; label: string; sublabel?: string; parentDealId?: number | string | null } | null

// bp-cal-roster-v1: selectable calendar/estimator roster (owner + attendee
// pickers, meeting/appointment forms, assignment dropdowns). Now a SEED that
// `applyCalendarRoster()` replaces from the live /api/calendar/users fetch, so a
// newly-provisioned @botpros.ai teammate becomes selectable with no code edit.
// `let` (not `const`) so the fetched roster can replace the binding; all readers
// reference it at render time and the calendar fetch's setState re-renders.
type CalendarRosterEntry = { email: string; userId: number; section: number; name: string; initials: string }
const CALENDAR_USERS_RAW_SEED: CalendarRosterEntry[] = [
  { email: 'mritchie@botpros.ai', userId: 1, section: 0, name: 'Maxwell Ritchie', initials: 'MR' },
]
let CALENDAR_USERS_RAW: CalendarRosterEntry[] = [...CALENDAR_USERS_RAW_SEED]

// Merge the fetched /api/calendar/users roster into the module-level roster maps.
// Shape-preserving + idempotent: RAW is rebuilt from the seed with fetched entries
// winning by userId (initials derived from the name); MAP layers fetched names
// over the legacy id→name seed. A empty/failed fetch leaves the seeds intact.
function applyCalendarRoster(
  users: Array<{ email: string; userId: number; section: number; name: string }> | null | undefined,
): void {
  if (!users || users.length === 0) return
  const byId = new Map<number, CalendarRosterEntry>()
  for (const u of CALENDAR_USERS_RAW_SEED) byId.set(u.userId, u)
  for (const u of users) {
    byId.set(u.userId, {
      email: u.email,
      userId: u.userId,
      section: u.section ?? 0,
      name: u.name,
      initials: initialsFromName(u.name),
    })
  }
  CALENDAR_USERS_RAW = [...byId.values()]
  const map: Record<number, string> = { ...CALENDAR_USERS_MAP_SEED }
  for (const u of users) map[u.userId] = u.name
  CALENDAR_USERS_MAP = map
}

// Estimator roster for deal assignment (the pills in the create panel and
// the availability picker). Updated 2026-06-10 per Max: Ira removed;
// Eddie, Mike, Chuck and Phillip added (users 101-104, emails confirmed,
// wired into CALENDAR_USERS_RAW above + CALENDAR_USERS in the API so
// availability, appointment events and invites work for them).
const ESTIMATOR_USERS: Array<{ userId: number; name: string }> = [
  { userId: 1, name: 'Maxwell Ritchie' },
]
const ESTIMATOR_IDS = ESTIMATOR_USERS.map(u => u.userId)

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

// Time-tracking helpers (shared by Deal Record + Timesheets view).
function formatHHMMSS(totalSeconds: number): string {
  if (!isFinite(totalSeconds) || totalSeconds < 0) return '0:00:00'
  const h = Math.floor(totalSeconds / 3600)
  const m = Math.floor((totalSeconds % 3600) / 60)
  const s = Math.floor(totalSeconds % 60)
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}
function formatMinutesLabel(minutes: number | null | undefined): string {
  if (minutes == null || !isFinite(minutes) || minutes < 0) return '0 m'
  if (minutes < 60) return `${Math.round(minutes)} m`
  const hrs = minutes / 60
  return `${(Math.round(hrs * 10) / 10).toFixed(1)} h`
}
function formatTimeOfDay(iso: string | null | undefined): string {
  if (!iso) return '—'
  try { return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) }
  catch { return '—' }
}
function formatDateShort(iso: string | null | undefined): string {
  if (!iso) return ''
  try { return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' }) }
  catch { return '' }
}

// ── Forgot-to-clock-out detection + correction prompt ─────────────────────
// The shift clock keeps climbing until someone clocks out; staff routinely
// forget at end of day, so a shift open since a PRIOR day (NY business tz) is
// almost certainly a missed clock-out (BotPros runs no overnight shifts).
// Per policy (2026-06-12) the employee can't self-edit payroll time — the
// prompt files an approval-gated correction request via tcCreateCorrection.
const NY_TZ = 'America/New_York'
// YYYY-MM-DD for an instant in NY, so two timestamps can be compared by calendar day.
function nyDateKey(d: Date): string {
  const p = new Intl.DateTimeFormat('en-CA', { timeZone: NY_TZ, year: 'numeric', month: '2-digit', day: '2-digit' })
    .formatToParts(d).reduce<Record<string, string>>((a, x) => (a[x.type] = x.value, a), {})
  return `${p.year}-${p.month}-${p.day}`
}
// "YYYY-MM-DDTHH:mm" wall-clock string in NY for a datetime-local input value.
function isoToNyLocalInput(iso: string): string {
  const p = new Intl.DateTimeFormat('en-CA', { timeZone: NY_TZ, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })
    .formatToParts(new Date(iso)).reduce<Record<string, string>>((a, x) => (a[x.type] = x.value, a), {})
  const hh = p.hour === '24' ? '00' : p.hour
  return `${p.year}-${p.month}-${p.day}T${hh}:${p.minute}`
}
// Inverse: a NY wall-clock "YYYY-MM-DDTHH:mm" back to a UTC ISO instant
// (DST-correct via fixed-point, same approach as the TimeClock view's helper).
function nyLocalInputToIso(local: string): string {
  const [datePart, timePart] = local.split('T')
  const [y, m, d] = datePart.split('-').map(Number)
  const [hh, mi] = timePart.split(':').map(Number)
  const wallUtc = Date.UTC(y, m - 1, d, hh, mi)
  let guess = wallUtc
  for (let i = 0; i < 3; i++) {
    const back = isoToNyLocalInput(new Date(guess).toISOString())
    const [bd, bt] = back.split('T')
    const [by, bm, bdd] = bd.split('-').map(Number)
    const [bh, bmi] = bt.split(':').map(Number)
    const diff = wallUtc - Date.UTC(by, bm - 1, bdd, bh, bmi)
    if (diff === 0) break
    guess += diff
  }
  return new Date(guess).toISOString()
}
// Default clock-out guess: 5:00 PM NY on the clock-in day; if clocked in at or
// after 5 PM, fall back to one hour after clock-in (still that same day).
function suggestedClockOutLocal(clockInIso: string): string {
  const inLocal = isoToNyLocalInput(clockInIso) // YYYY-MM-DDTHH:mm
  const [datePart, timePart] = inLocal.split('T')
  const [inH] = timePart.split(':').map(Number)
  if (inH < 17) return `${datePart}T17:00`
  const plusHour = new Date(Date.parse(nyLocalInputToIso(inLocal)) + 3600_000)
  return isoToNyLocalInput(plusHour.toISOString())
}

// Open WORK shift that began on an earlier NY calendar day AND has run ≥ 8h =
// forgot. The prior-day test is the core signal (no overnight shifts here); the
// 8h floor avoids false-flagging a late-evening clock-in glanced at just after
// midnight. A genuinely forgotten shift is always many hours old by next login.
function isForgottenShift(open: { clock_in: string; kind: string } | null, nowMs: number): boolean {
  if (!open || open.kind !== 'work') return false
  if (nyDateKey(new Date(open.clock_in)) >= nyDateKey(new Date(nowMs))) return false
  return nowMs - Date.parse(open.clock_in) >= 8 * 3600_000
}

function ForgotClockOutModal(props: {
  open: { id: number; clock_in: string }
  onDismiss: () => void
  onFiled: () => void
}) {
  const { open, onDismiss, onFiled } = props
  const [outLocal, setOutLocal] = React.useState(() => suggestedClockOutLocal(open.clock_in))
  const [reason, setReason] = React.useState('Forgot to clock out')
  const [submitting, setSubmitting] = React.useState(false)
  const [err, setErr] = React.useState<string | null>(null)
  const clockInLocal = isoToNyLocalInput(open.clock_in)
  const elapsedH = Math.floor((Date.now() - Date.parse(open.clock_in)) / 3600_000)
  const elapsedLabel = elapsedH >= 48 ? `${Math.floor(elapsedH / 24)} days` : `${elapsedH} hours`

  const submit = async () => {
    setErr(null)
    const outIso = nyLocalInputToIso(outLocal)
    if (Date.parse(outIso) <= Date.parse(open.clock_in)) { setErr('Clock-out must be after your clock-in time.'); return }
    if (!reason.trim()) { setErr('Please add a short reason.'); return }
    setSubmitting(true)
    try {
      await tcCreateCorrection({ punchId: open.id, clockIn: open.clock_in, clockOut: outIso, reason: reason.trim() })
      onFiled()
    } catch (e: any) {
      const msg = e?.message || 'Could not submit the correction.'
      // Already filed (dupe guard) — treat as success so we stop nagging.
      if (/already pending/i.test(msg)) { onFiled(); return }
      setErr(msg)
      setSubmitting(false)
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 10020, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 96 }}
      onClick={() => !submitting && onDismiss()}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 24, width: 380, boxShadow: '0 16px 44px rgba(0,0,0,0.32)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <span style={{ width: 34, height: 34, borderRadius: '50%', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 2"/></svg>
          </span>
          <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#1e293b' }}>Did you forget to clock out?</h3>
        </div>
        <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#475569', lineHeight: 1.5 }}>
          You've been clocked in since <strong>{formatDateShort(open.clock_in)}, {formatTimeOfDay(open.clock_in)}</strong> — about <strong>{elapsedLabel}</strong> ago.
          Set when you actually finished and we'll send it to the office to confirm your hours.
        </p>
        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#334155', marginBottom: 4 }}>When did you clock out?</label>
        <input type="datetime-local" value={outLocal} min={clockInLocal} onChange={e => setOutLocal(e.target.value)}
          style={{ width: '100%', border: '1px solid #cbd5e1', borderRadius: 8, padding: '8px 10px', fontSize: '0.85rem', boxSizing: 'border-box', marginBottom: 12 }} />
        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#334155', marginBottom: 4 }}>Reason</label>
        <input type="text" value={reason} maxLength={500} onChange={e => setReason(e.target.value)}
          style={{ width: '100%', border: '1px solid #cbd5e1', borderRadius: 8, padding: '8px 10px', fontSize: '0.85rem', boxSizing: 'border-box', marginBottom: err ? 6 : 16 }} />
        {err && <p style={{ margin: '0 0 12px', fontSize: '12px', color: '#dc2626' }}>{err}</p>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button type="button" disabled={submitting} onClick={submit}
            style={{ padding: '10px 14px', borderRadius: 8, border: 'none', background: '#0f172a', color: '#fff', fontWeight: 700, fontSize: '0.86rem', cursor: submitting ? 'default' : 'pointer', opacity: submitting ? 0.7 : 1 }}>
            {submitting ? 'Sending…' : 'Send clock-out for approval'}
          </button>
          <button type="button" disabled={submitting} onClick={onDismiss}
            style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: 'none', color: '#64748b', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}>
            I'm still working — not now
          </button>
        </div>
      </div>
    </div>
  )
}
const TIME_CATEGORY_LABELS: Record<TimeCategory, string> = {
  'field': 'Field',
  'admin': 'Admin',
  'drive-time': 'Drive time',
  'pto': 'PTO',
  'overtime': 'Overtime',
  'other': 'Other',
}
const TIME_CATEGORY_COLORS: Record<TimeCategory, { bg: string; fg: string }> = {
  'field':      { bg: '#e6f4ea', fg: '#137333' },
  'admin':      { bg: '#e8f0fe', fg: '#1a73e8' },
  'drive-time': { bg: '#fef7e0', fg: '#b06000' },
  'pto':        { bg: '#f3e8fd', fg: '#7627bb' },
  'overtime':   { bg: '#fde7e9', fg: '#c5221f' },
  'other':      { bg: '#e8eaed', fg: '#5f6368' },
}

const shortDateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
})

const longDateFormatter = new Intl.DateTimeFormat('en-US', {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
})

function EditableField({ value, onSave, type = 'text', display, placeholder }: {
  value: string | number
  onSave: (val: string) => void
  type?: 'text' | 'currency' | 'date'
  display?: string
  placeholder?: string
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(String(value ?? ''))
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => { if (editing) inputRef.current?.focus() }, [editing])

  const commit = () => {
    setEditing(false)
    const trimmed = draft.trim()
    if (trimmed !== String(value ?? '')) onSave(trimmed)
  }

  if (!editing) {
    return (
      <span className="editable-field" onClick={() => { setDraft(String(value ?? '')); setEditing(true) }} title="Click to edit">
        {display || String(value) || <span className="editable-placeholder">{placeholder || 'Add...'}</span>}
        <svg className="editable-pencil" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
      </span>
    )
  }

  return (
    <input
      ref={inputRef}
      className="editable-input"
      type={type === 'date' ? 'date' : type === 'currency' ? 'number' : 'text'}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false) }}
      step={type === 'currency' ? '0.01' : undefined}
    />
  )
}

// Error boundary to prevent blank white screens on unhandled errors
class AppErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean; error: Error | null; staleReloadPending: boolean }> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { hasError: false, error: null, staleReloadPending: false }
  }
  static getDerivedStateFromError(error: Error) {
    // err-breadcrumbs-v1: stash the SANITIZED page snapshot NOW — this is the
    // last moment the crashed page's DOM is still committed (componentDidCatch
    // runs after React swaps in the fallback UI below). Read-only, never throws.
    captureCrashSnapshot()
    return { hasError: true, error, staleReloadPending: isStaleChunkError(error?.message) }
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // A failed route lazy-import (stale chunk after a deploy) lands here.
    // Recover with a one-time reload to pick up the fresh chunk graph
    // instead of stranding the user on the crash screen.
    if (reloadOnceForStaleChunk(error?.message)) return
    if (this.state.staleReloadPending) this.setState({ staleReloadPending: false })
    console.error('App crashed:', error, info)
    // Error-ticket pipeline: severity=crash event with breadcrumbs + the
    // stashed sanitized snapshot (additive — the console.error mirror above
    // is unchanged and existing dashboards keep working).
    reportAppCrash(error, info)
  }
  render() {
    if (this.state.hasError) {
      if (this.state.staleReloadPending) {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#1e293b', color: '#e2e8f0', fontFamily: 'system-ui, sans-serif', padding: '20px', textAlign: 'center' }}>
            <h1 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: '8px' }}>Updating&hellip;</h1>
            <p style={{ fontSize: '0.9rem', color: '#94a3b8', maxWidth: '400px' }}>A new version of the app just deployed. Refreshing to pick it up.</p>
          </div>
        )
      }
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#1e293b', color: '#e2e8f0', fontFamily: 'system-ui, sans-serif', padding: '20px', textAlign: 'center' }}>
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '16px' }}>
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '8px' }}>Something went wrong</h1>
          <p style={{ fontSize: '0.9rem', color: '#94a3b8', marginBottom: '16px', maxWidth: '400px' }}>
            The application encountered an unexpected error. Please try refreshing the page.
          </p>
          <button onClick={() => { this.setState({ hasError: false, error: null }); window.location.hash = '/'; window.location.reload() }} style={{ padding: '10px 24px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600 }}>
            Refresh Page
          </button>
          {this.state.error && (
            <pre style={{ marginTop: '20px', padding: '12px', background: '#0f172a', borderRadius: '6px', fontSize: '0.75rem', color: '#f87171', maxWidth: '600px', overflow: 'auto', textAlign: 'left' }}>
              {this.state.error.message}
            </pre>
          )}
        </div>
      )
    }
    return this.props.children
  }
}

// Reusable datetime picker: wraps the native datetime-local input in a
// trigger button + popover that adds an explicit OK button to commit the
// selection. The calendar widget itself is still the browser's native one.
function InlineDateTimePicker({
  value,
  onChange,
  placeholder = 'Pick a date and time',
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
}) {
  const [open, setOpen] = useState(false)
  const [pending, setPending] = useState(value)
  const ref = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  useEffect(() => { if (open) setPending(value || '') }, [open, value])
  // Auto-open the native browser picker when the popover appears, so the user
  // doesn't have to click the tiny calendar icon on the right of the input.
  useEffect(() => {
    if (!open) return
    const el = inputRef.current
    if (!el) return
    const t = setTimeout(() => {
      try { (el as any).showPicker?.() } catch { /* some browsers throw if not in user gesture */ }
    }, 30)
    return () => clearTimeout(t)
  }, [open])
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const display = value
    ? (() => { try { return new Date(value).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) } catch { return value } })()
    : ''

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem', background: '#fff', textAlign: 'left', cursor: 'pointer', color: value ? '#1e293b' : '#94a3b8' }}
      >
        {display || placeholder}
      </button>
      {open && (
        <div
          style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, background: '#fff', border: '1px solid #cbd6e2', borderRadius: 6, boxShadow: '0 8px 24px rgba(15, 23, 42, 0.15)', padding: 12, zIndex: 9999, minWidth: 260 }}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            ref={inputRef}
            type="datetime-local"
            autoFocus
            value={pending}
            onChange={(e) => setPending(e.target.value)}
            onClick={(e) => { try { (e.currentTarget as any).showPicker?.() } catch { /* ignore */ } }}
            style={{ width: '100%', padding: '8px 10px', borderRadius: 4, border: '1px solid #cbd6e2', fontSize: 13, boxSizing: 'border-box' }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 10 }}>
            <button
              type="button"
              onClick={() => setPending('')}
              style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 11, textDecoration: 'underline' }}
            >Clear</button>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={() => setOpen(false)}
                style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, background: '#fff', border: '1px solid #cbd6e2', borderRadius: 4, cursor: 'pointer', color: '#33475b' }}
              >Cancel</button>
              <button
                type="button"
                onClick={() => { onChange(pending); setOpen(false) }}
                style={{ padding: '6px 16px', fontSize: 12, fontWeight: 600, background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
              >OK</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Side pop-out datetime picker with per-estimator availability. Drop-in
// replacement for InlineDateTimePicker in contexts where the user is also
// picking an estimator (deal creation, lead conversion, no-show reschedule,
// meeting modals). Shows six estimators × N duration-wide slots for the
// selected weekday; slot width tracks the duration dropdown (30/60/90/120
// min). Click any empty slot to set estimator + time; popover stays open so
// the user can try multiple options. Eagerly loads each estimator's real events
// for the viewed day (per-user /events/:userId, in parallel + cached) so event
// names render at rest; inaccessible calendars come back redacted as "Busy".
function AvailabilityDateTimePicker({
  value,
  onChange,
  estimatorId,
  onEstimatorChange,
  durationMinutes = 60,
  onDurationChange,
  estimatorIds = ESTIMATOR_IDS,
  attendeeIds,
  onAttendeesChange,
  placeholder = 'Pick a date and time',
  disabled = false,
  excludeTitle,
  dataTourId,
  territoryLocation,
  territorySegment = 'residential',
}: {
  value: string
  onChange: (dt: string) => void
  estimatorId: number | null
  onEstimatorChange: (id: number) => void
  durationMinutes?: number
  onDurationChange?: (m: number) => void
  estimatorIds?: number[]
  // Multi-attendee support: when both are provided, clicking a slot on a row
  // OTHER than the primary estimator's, AT THE SAME TIME as the primary pick,
  // toggles that user as an attendee instead of replacing the primary.
  // Picking a different time clears attendees.
  attendeeIds?: number[]
  onAttendeesChange?: (ids: number[]) => void
  placeholder?: string
  disabled?: boolean
  // When editing an existing event, its own busy blocks (host copy AND the
  // attendee fanout copies on other calendars) shouldn't paint the
  // grid red — pass the event's title to filter them out.
  excludeTitle?: string
  // Optional tutorial hook: rendered on the trigger button (as-is) and on the
  // open availability popover (suffixed "-grid"), so the walkthrough player can
  // resolve the "pick a date/time" + in-picker steps by stable selector.
  dataTourId?: string
  // Territory highlight: when an estimate's location is provided, the
  // estimator(s) whose territory roster covers that location are flagged with a
  // green "Territory" cue so the rep can see whose zone it is. Resolution reuses
  // the same city/state(/zip)→territory→user_ids match the lead-routing list
  // uses. Omitted/unknown city → nothing highlighted (no error). The segment
  // selects which roster (residential vs commercial) of the matched territory
  // is highlighted; residential by default.
  territoryLocation?: { city?: string | null; state?: string | null; zip?: string | null } | null
  territorySegment?: 'residential' | 'commercial'
}) {
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLDivElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number; maxHeight: number; width: number } | null>(null)
  // Snapshot of the selection when the popover opens, so ✕ / Esc can CANCEL
  // (picks apply live as you click — without this, closing always kept them).
  const openSnapshotRef = useRef<{ value: string; estimatorId: number | null; attendees: number[]; duration: number } | null>(null)

  // Duration is controlled by the parent when onDurationChange is provided,
  // otherwise self-hosted. This lets the dropdown always work even when
  // call sites don't wire the change callback.
  const [internalDuration, setInternalDuration] = useState(durationMinutes)
  const effectiveDuration = onDurationChange ? durationMinutes : internalDuration
  const changeDuration = (m: number) => { setInternalDuration(m); onDurationChange?.(m) }

  // Attendees: controlled when parent wires attendeeIds/onAttendeesChange,
  // otherwise internal-only so the multi-select click behavior works
  // visually in every call site (Create Deal, Lead Convert, No-Show). If
  // the parent doesn't persist them, they reset when the form closes —
  // that's acceptable since those flows don't store attendees today.
  const [internalAttendees, setInternalAttendees] = useState<number[]>(attendeeIds ?? [])
  const effectiveAttendees = attendeeIds ?? internalAttendees
  const changeAttendees = (ids: number[]) => { setInternalAttendees(ids); onAttendeesChange?.(ids) }

  // Territory highlight: when the picker is shown for an estimate that carries a
  // location, resolve which estimator(s) own that zone so their rows can wear a
  // green "Territory" cue. Reuses the lead-routing match basis (city/state, with
  // a zip_prefix fallback) over the cached territory roster. Resolved lazily on
  // open; unknown/blank city → empty set (highlight nothing, no error).
  const [territoryUserIds, setTerritoryUserIds] = useState<Set<number>>(new Set())
  const [territoryLabel, setTerritoryLabel] = useState<string | null>(null)
  const terrCity = territoryLocation?.city || ''
  const terrState = territoryLocation?.state || ''
  const terrZip = territoryLocation?.zip || ''
  useEffect(() => {
    let cancelled = false
    const hasLoc = !!(terrCity.trim() || terrZip.trim())
    if (!open || !hasLoc) {
      setTerritoryUserIds(new Set())
      setTerritoryLabel(null)
      return
    }
    loadTerritoriesCached()
      .then((territories) => {
        if (cancelled) return
        const { territory, userIds } = matchTerritoryEstimators(
          territories,
          { city: terrCity, state: terrState, zip: terrZip },
          territorySegment,
        )
        setTerritoryUserIds(new Set(userIds))
        setTerritoryLabel(territory ? (territory.display_name || territory.zone_code) : null)
      })
      .catch(() => {
        if (cancelled) return
        setTerritoryUserIds(new Set())
        setTerritoryLabel(null)
      })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, terrCity, terrState, terrZip, territorySegment])

  // Helper: build a YYYY-MM-DD string from Date (local).
  const dateKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  // Robust: only accept a YYYY-MM-DD prefix that parses to a real date.
  // Anything else (empty, junk, time-only string) falls back to today so
  // the picker never shows "Invalid Date" pills.
  const safeDateKeyFromValue = (v: string | undefined | null): string => {
    if (!v || typeof v !== 'string') return dateKey(new Date())
    const slice = v.slice(0, 10)
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(slice)
    if (!m) return dateKey(new Date())
    const probe = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    if (isNaN(probe.getTime())) return dateKey(new Date())
    return slice
  }
  const [viewDate, setViewDate] = useState<string>(() => safeDateKeyFromValue(value))

  // When the popover opens, if the current value has a date, jump to it.
  useEffect(() => { if (open && value) setViewDate(safeDateKeyFromValue(value)) }, [open, value])

  // Given a YYYY-MM-DD and an offset in weekdays, advance skipping Sat/Sun.
  const shiftWeekday = (isoDate: string, offset: number): string => {
    const [y, m, d] = isoDate.split('-').map(Number)
    let cursor = new Date(y, m - 1, d)
    if (isNaN(cursor.getTime())) cursor = new Date()
    let remaining = Math.abs(offset)
    const step = offset >= 0 ? 1 : -1
    while (remaining > 0) {
      cursor.setDate(cursor.getDate() + step)
      const dow = cursor.getDay()
      if (dow !== 0 && dow !== 6) remaining--
    }
    return dateKey(cursor)
  }

  // Build the 5-pill strip centered on viewDate. If viewDate is a weekend,
  // snap to the nearest Monday.
  const centerWeekday = (() => {
    const [y, m, d] = viewDate.split('-').map(Number)
    let cursor = new Date(y, m - 1, d)
    if (isNaN(cursor.getTime())) cursor = new Date()
    const dow = cursor.getDay()
    if (dow === 0) cursor.setDate(cursor.getDate() + 1)       // Sun → Mon
    else if (dow === 6) cursor.setDate(cursor.getDate() + 2)  // Sat → Mon
    return dateKey(cursor)
  })()
  const weekPills = [-2, -1, 0, 1, 2].map((off) => shiftWeekday(centerWeekday, off))

  type DayEvent = { title: string; dateFrom: string; dateTo: string; isAllDay?: boolean }
  // Cache key is `${userId}:${yyyy-mm-dd}`. Missing key = not yet fetched.
  const [cache, setCache] = useState<Map<string, DayEvent[]>>(new Map())
  const [loadingUserIds, setLoadingUserIds] = useState<Set<number>>(new Set())
  const [errorUserIds, setErrorUserIds] = useState<Set<number>>(new Set())

  // NAMES-AT-REST (fix #17): the grid now EAGERLY loads each displayed
  // estimator's REAL events for the viewed day via GET /api/calendar/events/:userId
  // (in parallel, one mailbox per request), so actual event titles render WITHOUT
  // hovering. This is more fetches than the batched busy-only getSchedule for a
  // see-all viewer, but the per-(userId,day) cache + the in-flight dedup below keep
  // repeat nav/open cheap (each pair is fetched at most once).
  //
  // PRIVACY (#3 preserved by construction): /events/:userId is #3-redacted
  // server-side — accessible calendars return real titles, inaccessible calendars
  // return title "Busy". So a real title can only ever surface for an ACCESSIBLE
  // calendar; inaccessible rows stay "Busy". The frontend never knows the scope;
  // the endpoint enforces it. We just render whatever we get.
  //
  // `enrichedKeys` marks `${userId}:${day}` pairs already loaded with real events;
  // `enrichInFlightRef` dedupes concurrent in-flight loads (state updates are async,
  // so a Set in state alone can't gate a rapid second request before the first
  // resolves — the #14 in-flight/token dedup pattern).
  const [enrichedKeys, setEnrichedKeys] = useState<Set<string>>(new Set())
  const enrichInFlightRef = useRef<Set<string>>(new Set())

  // Load one (userId, day) pair's real events into the cache. Idempotent: skips if
  // already enriched or in-flight. Returns a promise so the day-load effect can
  // fan out in parallel and track per-row loading state. excludeTitle filters out
  // the event being edited (its host + attendee busy copies).
  const loadRowDay = (userId: number, day: string): Promise<void> => {
    const key = `${userId}:${day}`
    if (enrichedKeys.has(key) || enrichInFlightRef.current.has(key)) return Promise.resolve()
    enrichInFlightRef.current.add(key)
    return fetchUserDayEvents(userId, day)
      .then((real) => {
        const ex = excludeTitle ? excludeTitle.trim().toLowerCase() : null
        let events = (real || []) as DayEvent[]
        if (ex) events = events.filter(e => (e.title || '').trim().toLowerCase() !== ex)
        setCache((prev) => {
          const next = new Map(prev)
          next.set(key, events)
          return next
        })
        setEnrichedKeys((prev) => {
          const next = new Set(prev)
          next.add(key)
          return next
        })
      })
      .finally(() => {
        enrichInFlightRef.current.delete(key)
      })
  }

  // Back-compat alias: the hover/focus handlers still call enrichRowDay, but it's
  // now a harmless no-op once the eager day-load has warmed the cache (loadRowDay
  // early-returns on an enriched/in-flight key).
  const enrichRowDay = (userId: number, day: string) => { void loadRowDay(userId, day) }

  useEffect(() => {
    if (!open) return
    const missing = estimatorIds.filter((id) => !enrichedKeys.has(`${id}:${viewDate}`))
    if (missing.length === 0) return
    setLoadingUserIds(new Set(missing))
    setErrorUserIds(new Set())
    let cancelled = false
    // EAGER per-user real-event load IN PARALLEL — restores titles at rest. Each
    // mailbox is fetched independently so one slow/failing calendar doesn't block
    // the rest, and per-row error state is tracked. The cache + in-flight dedup in
    // loadRowDay make this cheap on repeat opens/nav.
    Promise.all(
      missing.map((id) =>
        loadRowDay(id, viewDate)
          .then(() => ({ id, ok: true }))
          .catch(() => ({ id, ok: false })),
      ),
    ).then((settled) => {
      if (cancelled) return
      const failed = settled.filter((s) => !s.ok).map((s) => s.id)
      if (failed.length) {
        setErrorUserIds((prev) => {
          const next = new Set(prev)
          for (const id of failed) next.add(id)
          return next
        })
      }
      setLoadingUserIds(new Set())
    })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, viewDate, estimatorIds.join(',')])

  // CALENDAR_USERS_RAW is defined at the top of App.tsx (line ~1291).
  const rowsToRender = estimatorIds
    .map((id) => CALENDAR_USERS_RAW.find((u) => u.userId === id))
    .filter((u): u is typeof CALENDAR_USERS_RAW[number] => !!u)

  // Work day is 7am–7pm. Slots step by a fixed 30 minutes regardless of
  // duration, so events can start on any half hour (10:30–11:30, 12:30–1:30…).
  // The selected duration determines how many consecutive 30-min slots a pick
  // occupies — and how many must be free for a slot to be clickable.
  const SLOT_START_HOUR = 7
  const SLOT_END_HOUR = 19
  const WINDOW_MINUTES = (SLOT_END_HOUR - SLOT_START_HOUR) * 60
  const slotMinutes = 30
  const slotCount = Math.max(1, Math.floor(WINDOW_MINUTES / slotMinutes))
  const durationSpan = Math.max(1, Math.ceil(effectiveDuration / slotMinutes))

  // Return the slot index a minute-of-day falls into, or -1/slotCount if outside.
  const minutesToSlotIndex = (minutes: number): number => {
    const startMin = SLOT_START_HOUR * 60
    const endMin = SLOT_END_HOUR * 60
    if (minutes < startMin) return -1
    if (minutes >= endMin) return slotCount
    return Math.floor((minutes - startMin) / slotMinutes)
  }

  // "HH:MM" → minutes-of-day.
  const hhmmToMinutes = (hhmm: string): number | null => {
    const m = hhmm.match(/^(\d{1,2}):(\d{2})/)
    if (!m) return null
    return parseInt(m[1]) * 60 + parseInt(m[2])
  }

  // Extract minutes-of-day from an ISO datetime string.
  const eventMinutes = (isoDt: string): number | null => {
    const m = isoDt.match(/T(\d{2}):(\d{2})/)
    if (!m) return null
    return parseInt(m[1]) * 60 + parseInt(m[2])
  }

  // An event's [startSlot, endSlot) range in the grid — slots that OVERLAP the event
  // at all are included. If the event starts mid-slot, that slot is still busy.
  const eventToSlotRange = (ev: DayEvent): { start: number; end: number } | null => {
    const startMin = eventMinutes(ev.dateFrom)
    const endMin = eventMinutes(ev.dateTo)
    if (startMin === null || endMin === null) return null
    const slotWindowStart = SLOT_START_HOUR * 60
    const slotWindowEnd = SLOT_END_HOUR * 60
    const evStart = Math.max(startMin, slotWindowStart)
    const evEnd = Math.min(endMin, slotWindowEnd)
    if (evEnd <= evStart) return null
    const startIdx = Math.floor((evStart - slotWindowStart) / slotMinutes)
    // End is exclusive — a slot is "busy" if any part of the event overlaps it,
    // so we take the ceiling of the end minute.
    const endIdx = Math.min(slotCount, Math.ceil((evEnd - slotWindowStart) / slotMinutes))
    if (endIdx <= startIdx) return null
    return { start: startIdx, end: endIdx }
  }

  // Currently-selected slot index (or null if selection is on a different day / outside 7a-7p).
  const selectedSlotIndex: number | null = (() => {
    if (!value || value.slice(0, 10) !== viewDate) return null
    const t = value.slice(11, 16)
    const m = hhmmToMinutes(t)
    if (m === null) return null
    const i = minutesToSlotIndex(m)
    return i >= 0 && i < slotCount ? i : null
  })()

  // Defensive: new Date(badString).toLocaleString() returns the literal
  // string "Invalid Date" instead of throwing — so a try/catch alone won't
  // protect us. Probe with isNaN(getTime()) and fall back to '' so the
  // placeholder ("Pick a date and time") renders instead.
  //
  // IMPORTANT: ECMAScript parses date-only strings (YYYY-MM-DD, no T component)
  // as UTC midnight, which renders one day early in US Eastern (EDT = UTC-4).
  // Normalize date-only values to local midnight by appending 'T00:00' so that
  // e.g. "2026-06-16" displays as Jun 16, not Jun 15 8:00 PM.
  const display = (() => {
    if (!value || typeof value !== 'string') return ''
    try {
      const normalized = /^\d{4}-\d{2}-\d{2}$/.test(value) ? value + 'T00:00' : value
      const d = new Date(normalized)
      if (isNaN(d.getTime())) return ''
      return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
    } catch {
      return ''
    }
  })()

  // Capture the pre-open selection for cancel (✕ / Esc).
  useEffect(() => {
    if (open) {
      openSnapshotRef.current = { value, estimatorId, attendees: [...effectiveAttendees], duration: effectiveDuration }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const cancelAndClose = () => {
    const snap = openSnapshotRef.current
    if (snap) {
      onChange(snap.value)
      onEstimatorChange(snap.estimatorId ?? 0)
      changeAttendees(snap.attendees)
      changeDuration(snap.duration)
    }
    setOpen(false)
  }

  // Compute popover placement relative to the anchor. Clamp to viewport so
  // the popover is never pushed off-screen when the trigger sits low in the
  // viewport, and re-compute on scroll/resize so it stays glued to the anchor.
  useEffect(() => {
    if (!open) return
    const reposition = () => {
      if (!anchorRef.current) return
      const r = anchorRef.current.getBoundingClientRect()
      const width = Math.min(880, window.innerWidth - 24)
      // Prefer placing to the left of the anchor. If it would clip off the
      // left edge, center the popover in the viewport instead of pinning to
      // left:12 (which looks awful when the anchor is already near-center in
      // a small modal like the Lead Convert dialog).
      const preferredLeft = r.left - width - 12
      const left = preferredLeft >= 12 ? preferredLeft : Math.max(12, Math.round((window.innerWidth - width) / 2))
      // Vertical: align top with anchor, but clamp so the popover fits in the
      // viewport. The popover's maxHeight is recomputed to exactly fill the
      // space between `top` and the viewport bottom (minus 12px gutter).
      const desiredHeight = Math.min(560, window.innerHeight - 24)
      const top = Math.max(12, Math.min(r.top, window.innerHeight - desiredHeight - 12))
      const maxHeight = window.innerHeight - top - 12
      setPos({ top, left, maxHeight, width })
    }
    reposition()
    window.addEventListener('resize', reposition)
    window.addEventListener('scroll', reposition, true) // capture = catches ancestor scrolls
    return () => {
      window.removeEventListener('resize', reposition)
      window.removeEventListener('scroll', reposition, true)
    }
  }, [open])

  // Close on outside click or Esc.
  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (popoverRef.current?.contains(e.target as Node)) return
      if (anchorRef.current?.contains(e.target as Node)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') cancelAndClose() }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={anchorRef} style={{ position: 'relative' }}>
      <button
        type="button"
        disabled={disabled}
        data-tour-id={dataTourId}
        onClick={() => !disabled && setOpen((v) => !v)}
        style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem', background: disabled ? '#f8fafc' : '#fff', textAlign: 'left', cursor: disabled ? 'not-allowed' : 'pointer', color: value ? '#1e293b' : '#94a3b8' }}
      >
        {display || placeholder}
      </button>
      {open && pos && (
        <div
          ref={popoverRef}
          data-tour-id={dataTourId ? `${dataTourId}-grid` : undefined}
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, maxHeight: pos.maxHeight, background: '#fff', border: '1px solid #cbd6e2', borderRadius: 6, boxShadow: '0 8px 24px rgba(15, 23, 42, 0.18)', zIndex: 100001, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
          onClick={(e) => e.stopPropagation()}
        >
          <style>{`@keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }`}</style>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', flexShrink: 0 }}>
            <div style={{ minWidth: 0, flex: '1 1 auto', paddingRight: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>Estimator Availability</div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Click a slot to set the estimator; click another row at the same time to add an attendee (green).</div>
              {territoryUserIds.size > 0 && (
                <div style={{ fontSize: 11, color: '#15803d', marginTop: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                  <span className="estimator-territory-tag">Territory</span>
                  <span>= covers {terrCity.trim() || 'this location'}{territoryLabel ? ` · ${territoryLabel}` : ''}</span>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 24, flexShrink: 0 }}>
              <label style={{ fontSize: 11, color: '#64748b' }}>Duration:</label>
              <select
                value={effectiveDuration}
                onChange={(e) => changeDuration(parseInt(e.target.value))}
                style={{ padding: '3px 8px', fontSize: 11, border: '1px solid #cbd6e2', borderRadius: 3, background: '#fff', width: 96, maxWidth: 96, flexShrink: 0, flexGrow: 0, boxSizing: 'border-box' }}
              >
                <option value={30}>30 min</option>
                <option value={60}>60 min</option>
                <option value={90}>90 min</option>
                <option value={120}>120 min</option>
              </select>
              <button
                type="button"
                onClick={() => setOpen(false)}
                style={{ padding: '4px 14px', fontSize: 11, fontWeight: 700, background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                aria-label="Done"
              >Done</button>
              <button
                type="button"
                onClick={cancelAndClose}
                title="Cancel — discard picks made since opening"
                style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 18, padding: '0 4px' }}
                aria-label="Cancel"
              >✕</button>
            </div>
          </div>
          <div style={{ flex: 1, overflow: 'auto', padding: 0 }}>
            <div style={{ display: 'flex', gap: 4, padding: '10px 14px', borderBottom: '1px solid #e2e8f0', background: '#fafbfc', alignItems: 'center' }}>
              <button
                type="button"
                onClick={() => setViewDate(shiftWeekday(viewDate, -5))}
                style={{ padding: '4px 6px', background: '#fff', border: '1px solid #cbd6e2', borderRadius: 3, cursor: 'pointer', fontSize: 11, fontWeight: 600 }}
                aria-label="Previous week"
                title="Previous week"
              >«</button>
              <button
                type="button"
                onClick={() => setViewDate(shiftWeekday(viewDate, -1))}
                style={{ padding: '4px 8px', background: '#fff', border: '1px solid #cbd6e2', borderRadius: 3, cursor: 'pointer', fontSize: 11 }}
                aria-label="Previous weekday"
                title="Previous weekday"
              >‹</button>
              <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 4, margin: '0 8px' }}>
                {weekPills.map((dk) => {
                  const [yy, mm, dd] = dk.split('-').map(Number)
                  const d = new Date(yy, mm - 1, dd)
                  const isSelected = dk === viewDate
                  const valid = !isNaN(d.getTime())
                  const weekday = valid ? d.toLocaleDateString('en-US', { weekday: 'short' }) : '--'
                  const monthDay = valid ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '--'
                  return (
                    <button
                      key={dk}
                      type="button"
                      onClick={() => setViewDate(dk)}
                      style={{ padding: '6px 4px', borderRadius: 3, background: isSelected ? '#2563eb' : '#fff', color: isSelected ? '#fff' : '#33475b', border: isSelected ? 'none' : '1px solid #e2e8f0', fontWeight: 600, fontSize: 11, cursor: 'pointer', textAlign: 'center' }}
                    >
                      <div style={{ fontSize: 9, opacity: 0.75, fontWeight: 400 }}>{weekday}</div>
                      <div>{monthDay}</div>
                    </button>
                  )
                })}
              </div>
              <button
                type="button"
                onClick={() => setViewDate(shiftWeekday(viewDate, 1))}
                style={{ padding: '4px 8px', background: '#fff', border: '1px solid #cbd6e2', borderRadius: 3, cursor: 'pointer', fontSize: 11 }}
                aria-label="Next weekday"
                title="Next weekday"
              >›</button>
              <button
                type="button"
                onClick={() => setViewDate(shiftWeekday(viewDate, 5))}
                style={{ padding: '4px 6px', background: '#fff', border: '1px solid #cbd6e2', borderRadius: 3, cursor: 'pointer', fontSize: 11, fontWeight: 600 }}
                aria-label="Next week"
                title="Next week"
              >»</button>
              <button
                type="button"
                onClick={() => setViewDate(dateKey(new Date()))}
                style={{ padding: '4px 10px', marginLeft: 4, background: '#eff6ff', border: '1px solid #bfdbfe', color: '#2563eb', borderRadius: 3, cursor: 'pointer', fontSize: 10, fontWeight: 700 }}
                aria-label="Jump to today"
                title="Jump to today"
              >Today</button>
            </div>
            <div style={{ padding: '10px 14px' }}>
              {/* Slot labels row — one label per slot, formatted by density */}
              <div style={{ display: 'grid', gridTemplateColumns: `70px repeat(${slotCount}, 1fr)`, gap: 0, fontSize: 9, color: '#94a3b8', marginBottom: 3 }}>
                <div></div>
                {Array.from({ length: slotCount }, (_, i) => {
                  const startMin = SLOT_START_HOUR * 60 + i * slotMinutes
                  const h = Math.floor(startMin / 60)
                  const m = startMin % 60
                  // Label only the on-the-hour columns; half-hour columns stay
                  // blank (24 columns are too cramped for ":30" labels).
                  const ampm = h < 12 ? 'a' : 'p'
                  const dispH = h === 0 ? 12 : h > 12 ? h - 12 : h
                  const label = m === 0 ? `${dispH}${ampm}` : ''
                  return <div key={i} style={{ textAlign: 'center', padding: '2px 0', overflow: 'hidden', whiteSpace: 'nowrap' }}>{label}</div>
                })}
              </div>

              {rowsToRender.map((user) => {
                const cacheKey = `${user.userId}:${viewDate}`
                const isLoading = loadingUserIds.has(user.userId) || !cache.has(cacheKey)
                const hasError = errorUserIds.has(user.userId)
                const events = cache.get(cacheKey) || []
                const allDayEvent = events.find((e) => e.isAllDay)
                const isPrimaryRow = estimatorId === user.userId
                const isAttendeeRow = !isPrimaryRow && effectiveAttendees.includes(user.userId)
                // Territory match: this estimator owns the estimate's zone. An
                // ADDITIVE overlay (green ring + labeled tag) that co-exists with
                // primary/attendee state and is intentionally distinct from the
                // attendee's solid-green avatar fill so the two greens never read
                // as the same thing.
                const isTerritoryRow = territoryUserIds.has(user.userId)

                // Build a Set<number> of busy slot indices for this row.
                // A slot is busy if ANY event overlaps its [start, start+duration) window.
                const busySlots = new Set<number>()
                const slotToEventTitle = new Map<number, string>()
                if (!allDayEvent) {
                  for (const ev of events) {
                    if (ev.isAllDay) continue
                    const r = eventToSlotRange(ev)
                    if (!r) continue
                    for (let i = r.start; i < r.end; i++) {
                      busySlots.add(i)
                      if (!slotToEventTitle.has(i)) slotToEventTitle.set(i, ev.title)
                    }
                  }
                }

                // A slot is pickable only if the WHOLE duration window starting
                // there is free and fits before end-of-day.
                const canFit = (i: number): boolean => {
                  if (i + durationSpan > slotCount) return false
                  for (let k = i; k < i + durationSpan; k++) if (busySlots.has(k)) return false
                  return true
                }

                // Row header cell — primary = blue, attendee = green, neither = neutral
                const rowAccent = isPrimaryRow ? '#2563eb' : isAttendeeRow ? '#10b981' : null
                const rowBg = isPrimaryRow ? '#eff6ff' : isAttendeeRow ? '#ecfdf5' : '#f1f5f9'
                // Territory ring (a darker, labeled green — distinct from the
                // attendee's #10b981 fill) wraps the whole row when this estimator
                // owns the zone.
                const rowBorder = isTerritoryRow ? '2px solid #15803d' : '1px solid #cbd6e2'
                const headerCell = (
                  <div style={{ background: rowBg, padding: '8px 6px', display: 'flex', flexWrap: isTerritoryRow ? 'wrap' : 'nowrap', alignItems: 'center', gap: 4, borderRight: '1px solid #cbd6e2', boxShadow: isTerritoryRow ? 'inset 3px 0 0 #15803d' : undefined }}>
                    <span style={{ width: 20, height: 20, borderRadius: '50%', background: rowAccent || '#fff', color: rowAccent ? '#fff' : '#64748b', fontSize: 8, fontWeight: 700, border: rowAccent ? 'none' : '1px solid #cbd6e2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{user.initials}</span>
                    <span style={{ fontSize: 10, fontWeight: 700, color: rowAccent || '#33475b' }}>{user.name.split(' ')[0]}</span>
                    {isTerritoryRow && (
                      <span className="estimator-territory-tag" title={territoryLabel ? `${territoryLabel} — this is their territory` : 'This is their territory'}>Territory</span>
                    )}
                  </div>
                )

                // Loading row: shimmer
                if (isLoading) {
                  return (
                    <div key={user.userId} style={{ display: 'grid', gridTemplateColumns: '70px 1fr', gap: 0, background: '#e2e8f0', border: rowBorder, borderRadius: 3, overflow: 'hidden', marginBottom: 3 }}>
                      {headerCell}
                      <div style={{ background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s linear infinite', minHeight: 36 }} />
                    </div>
                  )
                }

                // Error row
                if (hasError) {
                  return (
                    <div key={user.userId} style={{ display: 'grid', gridTemplateColumns: '70px 1fr', gap: 0, background: '#e2e8f0', border: rowBorder, borderRadius: 3, overflow: 'hidden', marginBottom: 3 }}>
                      {headerCell}
                      <div style={{ background: '#f1f5f9', padding: '10px 12px', fontSize: 10, color: '#94a3b8' }}>⚠ Couldn't load availability — pick a different day to retry.</div>
                    </div>
                  )
                }

                // All-day event row
                if (allDayEvent) {
                  return (
                    <div key={user.userId} style={{ display: 'grid', gridTemplateColumns: '70px 1fr', gap: 0, background: '#e2e8f0', border: rowBorder, borderRadius: 3, overflow: 'hidden', marginBottom: 3 }} title={`All day: ${allDayEvent.title}`}>
                      {headerCell}
                      <div style={{ background: 'repeating-linear-gradient(45deg, #fecaca, #fecaca 6px, #fca5a5 6px, #fca5a5 12px)', padding: '10px 12px', fontSize: 10, color: '#7f1d1d', fontWeight: 600 }}>🏖️ All day: {allDayEvent.title}</div>
                    </div>
                  )
                }

                // Slot click behavior:
                // - First pick on any row: set primary (estimatorId + value). Clear attendees.
                // - Click on a DIFFERENT row at the SAME TIME as primary, if parent wired
                //   onAttendeesChange: toggle the user as an attendee (green color).
                // - Click at a different time: replace primary, clear attendees.
                // - Click on the primary's already-selected slot: no-op (stays selected).
                // Popover stays open so the user can assemble the team.
                const pickSlot = (i: number) => {
                  const startMin = SLOT_START_HOUR * 60 + i * slotMinutes
                  const hh = String(Math.floor(startMin / 60)).padStart(2, '0')
                  const mm = String(startMin % 60).padStart(2, '0')
                  const newTime = `${viewDate}T${hh}:${mm}`
                  const userId = user.userId
                  const sameTime = value === newTime
                  const isPrimary = userId === estimatorId
                  if (sameTime && isPrimary) {
                    // Click primary's own slot → deselect everything. User can
                    // then pick a fresh primary (possibly a different estimator).
                    onChange('')
                    onEstimatorChange(0)
                    changeAttendees([])
                    return
                  }
                  if (sameTime) {
                    // Toggle attendee membership at the already-picked time.
                    const current = effectiveAttendees
                    const next = current.includes(userId)
                      ? current.filter((id) => id !== userId)
                      : [...current, userId]
                    changeAttendees(next)
                    return
                  }
                  // New time → fresh primary pick, clear attendees.
                  onChange(newTime)
                  onEstimatorChange(userId)
                  changeAttendees([])
                }
                return (
                  <div key={user.userId} style={{ display: 'grid', gridTemplateColumns: `70px repeat(${slotCount}, 1fr)`, gap: 0, background: '#e2e8f0', border: rowBorder, borderRadius: 3, overflow: 'hidden', marginBottom: 3 }}>
                    {headerCell}
                    {Array.from({ length: slotCount }, (_, i) => {
                      const busy = busySlots.has(i)
                      const title = busy ? slotToEventTitle.get(i) : undefined
                      // The pick highlights its full duration span, not just the start slot.
                      const inSelectedSpan = selectedSlotIndex !== null && i >= selectedSlotIndex && i < selectedSlotIndex + durationSpan
                      const isPrimarySlot = isPrimaryRow && inSelectedSpan
                      const isAttendeeSlot = isAttendeeRow && inSelectedSpan
                      const slotAccent = isPrimarySlot ? '#2563eb' : isAttendeeSlot ? '#10b981' : null
                      const clickable = !busy && canFit(i)
                      const defaultBg = slotAccent || (busy ? '#fecaca' : '#fff')
                      // Lazily fetch this user's real events the first time a busy
                      // block is hovered/focused, so an accessible calendar's true
                      // title replaces the generic "Busy" (fix #16). No-op once the
                      // (userId, day) pair is enriched or in-flight. Inaccessible
                      // calendars stay "Busy" because the endpoint redacts them.
                      const enrichOnInteract = busy ? () => enrichRowDay(user.userId, viewDate) : undefined
                      return (
                        <div
                          key={i}
                          title={title || (!busy && !clickable ? `Not enough free time for ${effectiveDuration} min here` : undefined)}
                          role={clickable ? 'button' : undefined}
                          tabIndex={clickable ? 0 : busy ? 0 : -1}
                          onClick={!clickable ? undefined : () => pickSlot(i)}
                          onFocus={enrichOnInteract}
                          onKeyDown={!clickable ? undefined : (e) => {
                            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pickSlot(i) }
                          }}
                          style={{
                            background: defaultBg,
                            // Darker divider at hour boundaries, faint at the half hour.
                            borderRight: i % 2 === 1 ? '1px solid #b6c2d2' : '1px solid #eef2f7',
                            fontSize: 9,
                            color: slotAccent ? '#fff' : busy ? '#7f1d1d' : '#33475b',
                            padding: '6px 4px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            minHeight: 36,
                            cursor: clickable ? 'pointer' : 'default',
                            opacity: !busy && !clickable && !slotAccent ? 0.45 : 1,
                            outline: 'none',
                            fontWeight: 600,
                            textAlign: 'center',
                          }}
                          onMouseEnter={(e) => {
                            if (busy) { enrichOnInteract?.(); return }
                            if (clickable && !slotAccent) (e.currentTarget as HTMLDivElement).style.background = '#dbeafe'
                          }}
                          onMouseLeave={!clickable ? undefined : (e) => { if (!slotAccent) (e.currentTarget as HTMLDivElement).style.background = '#fff' }}
                        >{busy ? (title || 'Busy') : ''}</div>
                      )
                    })}
                  </div>
                )
              })}

              {/* Legend */}
              <div style={{ display: 'flex', gap: 14, fontSize: 10, marginTop: 10, color: '#64748b' }}>
                <span><span style={{ display: 'inline-block', width: 10, height: 8, background: '#fff', border: '1px solid #cbd6e2', verticalAlign: 'middle', marginRight: 3 }}></span>Available</span>
                <span><span style={{ display: 'inline-block', width: 10, height: 8, background: '#fecaca', verticalAlign: 'middle', marginRight: 3 }}></span>Busy</span>
                <span><span style={{ display: 'inline-block', width: 10, height: 8, background: '#2563eb', verticalAlign: 'middle', marginRight: 3 }}></span>Primary</span>
                <span><span style={{ display: 'inline-block', width: 10, height: 8, background: '#10b981', verticalAlign: 'middle', marginRight: 3 }}></span>Attendee</span>
                {territoryUserIds.size > 0 && (
                  <span><span style={{ display: 'inline-block', width: 10, height: 8, background: '#fff', border: '2px solid #15803d', verticalAlign: 'middle', marginRight: 3 }}></span>Territory</span>
                )}
                <span style={{ marginLeft: 'auto', color: '#94a3b8' }}>Event names show for calendars you can see; others appear as Busy.</span>
              </div>
            </div>
          </div>
          {/* Footer — OK button appears once a primary has been chosen. */}
          {!!estimatorId && !!value && (
            <div style={{ flexShrink: 0, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, padding: '10px 14px', borderTop: '1px solid #e2e8f0', background: '#f8fafc' }}>
              <div style={{ fontSize: 11, color: '#64748b', marginRight: 'auto' }}>
                {(() => {
                  const u = CALENDAR_USERS_RAW.find((x) => x.userId === estimatorId)
                  const primaryName = u ? u.name.split(' ')[0] : 'User'
                  const attendees = effectiveAttendees
                    .map((id) => CALENDAR_USERS_RAW.find((x) => x.userId === id)?.name.split(' ')[0])
                    .filter(Boolean)
                  try {
                    const when = new Date(value).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
                    return `${primaryName}${attendees.length ? ' + ' + attendees.join(', ') : ''} · ${when}`
                  } catch { return `${primaryName} selected` }
                })()}
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                style={{ padding: '6px 18px', fontSize: 12, fontWeight: 700, background: '#2563eb', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
              >OK</button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Address-aware <input>. Drop-in replacement for an existing text input on
 * an address field. When the user picks a Google suggestion, onAddressPicked
 * fires with the parsed street/city/state/zip. Free-form typing is still
 * accepted — callers get onChange for every keystroke just like a normal
 * input.
 *
 * Uses a Worker-proxied REST Places API (/api/places/*) for predictions so
 * we don't depend on the Maps JS library (which has flaky behavior on newer
 * GCP accounts and no CORS support on the raw REST endpoint).
 */
// ============================================================================
// Modern date & time pickers — in-app popovers that replace the browser's
// native <input type="date"> / <input type="time"> chrome (which looks dated
// and is unstyleable). Same value contract as the native inputs:
//   ModernDatePicker: value/onChange are "YYYY-MM-DD"
//   ModernTimePicker: value/onChange are "HH:MM" (24-hour)
// Drop-in reusable; popovers portal to <body> with fixed positioning so they
// are never clipped by a scrollable modal body.
// ============================================================================
const MDP_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const MDP_DOW = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

function mdpParse(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || '')
  if (!m) return null
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return isNaN(d.getTime()) ? null : d
}
function mdpFmt(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function mdpFmtDisplay(s: string): string {
  const d = mdpParse(s)
  return d ? `${MDP_MONTHS[d.getMonth()].slice(0, 3)} ${d.getDate()}, ${d.getFullYear()}` : ''
}
function mtpFmtDisplay(s: string): string {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s || '')
  if (!m) return ''
  let h = Number(m[1])
  const ampm = h >= 12 ? 'PM' : 'AM'
  h = h % 12
  if (h === 0) h = 12
  return `${h}:${m[2]} ${ampm}`
}
// Shared fixed-position anchor: returns viewport coords for a popover anchored
// to `rect`, flipping above the trigger when there isn't room below.
function mdpAnchor(rect: DOMRect, popHeight: number, popWidth: number): { left: number; top: number; openUp: boolean } {
  const spaceBelow = window.innerHeight - rect.bottom
  const openUp = spaceBelow < popHeight + 12 && rect.top > popHeight
  const left = Math.max(8, Math.min(rect.left, window.innerWidth - popWidth - 8))
  return { left, top: openUp ? rect.top : rect.bottom, openUp }
}

function ModernDatePicker({ value, onChange, ariaLabel, style }: { value: string; onChange: (v: string) => void; ariaLabel?: string; style?: React.CSSProperties }) {
  const [open, setOpen] = useState(false)
  const [view, setView] = useState(() => {
    const d = mdpParse(value) || new Date()
    return { y: d.getFullYear(), m: d.getMonth() }
  })
  const [pos, setPos] = useState<{ left: number; top: number; openUp: boolean } | null>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const popRef = useRef<HTMLDivElement>(null)

  const openPicker = () => {
    const d = mdpParse(value) || new Date()
    setView({ y: d.getFullYear(), m: d.getMonth() })
    const r = wrapRef.current?.getBoundingClientRect()
    if (r) setPos(mdpAnchor(r, 326, 248))
    setOpen(true)
  }

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (wrapRef.current?.contains(t) || popRef.current?.contains(t)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    const onScroll = (e: Event) => {
      if (popRef.current && e.target instanceof Node && popRef.current.contains(e.target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDown, true)
    document.addEventListener('keydown', onKey, true)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onScroll, true)
    return () => {
      document.removeEventListener('mousedown', onDown, true)
      document.removeEventListener('keydown', onKey, true)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onScroll, true)
    }
  }, [open])

  const selected = mdpParse(value)
  const today = new Date()
  const firstDow = new Date(view.y, view.m, 1).getDay()
  const gridStart = new Date(view.y, view.m, 1 - firstDow)
  const cells = Array.from({ length: 42 }, (_, i) => {
    const dt = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i)
    return { y: dt.getFullYear(), m: dt.getMonth(), d: dt.getDate(), out: dt.getMonth() !== view.m }
  })
  const same = (a: Date | null, y: number, m: number, d: number) => !!a && a.getFullYear() === y && a.getMonth() === m && a.getDate() === d
  const prevMonth = () => setView(v => ({ y: v.m === 0 ? v.y - 1 : v.y, m: v.m === 0 ? 11 : v.m - 1 }))
  const nextMonth = () => setView(v => ({ y: v.m === 11 ? v.y + 1 : v.y, m: v.m === 11 ? 0 : v.m + 1 }))
  const pick = (c: { y: number; m: number; d: number }) => { onChange(mdpFmt(new Date(c.y, c.m, c.d))); setOpen(false) }

  return (
    <div className="mdp-wrap" ref={wrapRef} style={style}>
      <button type="button" className="mdp-trigger" aria-label={ariaLabel || 'Select date'} onClick={() => (open ? setOpen(false) : openPicker())}>
        <span className={value ? 'mdp-trigger-val' : 'mdp-trigger-ph'}>{value ? mdpFmtDisplay(value) : 'Select date'}</span>
        <svg className="mdp-trigger-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      </button>
      {open && pos && createPortal(
        <div className="mdp-pop" ref={popRef} style={{ left: pos.left, top: pos.top, transform: pos.openUp ? 'translateY(calc(-100% - 6px))' : 'translateY(6px)' }}>
          <div className="mdp-head">
            <button type="button" className="mdp-nav" aria-label="Previous month" onClick={prevMonth}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6" /></svg>
            </button>
            <div className="mdp-head-label">{MDP_MONTHS[view.m]} {view.y}</div>
            <button type="button" className="mdp-nav" aria-label="Next month" onClick={nextMonth}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6" /></svg>
            </button>
          </div>
          <div className="mdp-dow">{MDP_DOW.map(d => <span key={d}>{d}</span>)}</div>
          <div className="mdp-grid">
            {cells.map((c, i) => {
              const cls = ['mdp-day']
              if (c.out) cls.push('mdp-day-out')
              if (same(today, c.y, c.m, c.d)) cls.push('mdp-day-today')
              if (same(selected, c.y, c.m, c.d)) cls.push('mdp-day-sel')
              return <button key={i} type="button" className={cls.join(' ')} onClick={() => pick(c)}>{c.d}</button>
            })}
          </div>
          <div className="mdp-foot">
            <button type="button" className="mdp-today-btn" onClick={() => pick({ y: today.getFullYear(), m: today.getMonth(), d: today.getDate() })}>Today</button>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

function ModernTimePicker({ value, onChange, ariaLabel, style, options: optionsProp }: { value: string; onChange: (v: string) => void; ariaLabel?: string; style?: React.CSSProperties; options?: string[] }) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ left: number; top: number; openUp: boolean } | null>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const popRef = useRef<HTMLDivElement>(null)

  const defaultOptions = useMemo(() => {
    const out: string[] = []
    for (let h = 0; h < 24; h++) for (let m = 0; m < 60; m += 15) out.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`)
    return out
  }, [])
  const options = optionsProp ?? defaultOptions

  const openPicker = () => {
    const r = wrapRef.current?.getBoundingClientRect()
    if (r) setPos(mdpAnchor(r, 240, 168))
    setOpen(true)
  }

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (wrapRef.current?.contains(t) || popRef.current?.contains(t)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    const onScroll = (e: Event) => {
      if (popRef.current && e.target instanceof Node && popRef.current.contains(e.target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDown, true)
    document.addEventListener('keydown', onKey, true)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onScroll, true)
    return () => {
      document.removeEventListener('mousedown', onDown, true)
      document.removeEventListener('keydown', onKey, true)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onScroll, true)
    }
  }, [open])

  // Center the selected time in the scroll list when the popover opens.
  useEffect(() => {
    if (!open) return
    const cont = popRef.current
    const el = cont?.querySelector('.mtp-opt-sel') as HTMLElement | null
    if (cont && el) cont.scrollTop = el.offsetTop - cont.clientHeight / 2 + el.clientHeight / 2
  }, [open])

  return (
    <div className="mtp-wrap" ref={wrapRef} style={style}>
      <button type="button" className="mdp-trigger" aria-label={ariaLabel || 'Select time'} onClick={() => (open ? setOpen(false) : openPicker())}>
        <span className={value ? 'mdp-trigger-val' : 'mdp-trigger-ph'}>{value ? mtpFmtDisplay(value) : 'Select time'}</span>
        <svg className="mdp-trigger-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15.5 14" />
        </svg>
      </button>
      {open && pos && createPortal(
        <div className="mtp-pop" ref={popRef} style={{ left: pos.left, top: pos.top, transform: pos.openUp ? 'translateY(calc(-100% - 6px))' : 'translateY(6px)' }}>
          {options.map(t => (
            <button key={t} type="button" className={'mtp-opt' + (t === value ? ' mtp-opt-sel' : '')} onClick={() => { onChange(t); setOpen(false) }}>{mtpFmtDisplay(t)}</button>
          ))}
        </div>,
        document.body
      )}
    </div>
  )
}

// Fetch a call recording through the app's own same-origin authenticated
// request and hand back a local blob URL. The native <audio src="/api/...">
// element made its OWN request, which was unreliable for cookie auth + HTTP
// range on Safari/iOS — the player stuck at 0:00 and would not start, even
// though opening the URL directly played fine. A blob URL is local, fully
// seekable, and plays in every browser.
function useRecordingBlob(url: string | null): { blobUrl: string | null; error: boolean } {
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [error, setError] = useState(false)
  useEffect(() => {
    if (!url) { setBlobUrl(null); setError(false); return }
    let cancelled = false
    let objectUrl: string | null = null
    setBlobUrl(null)
    setError(false)
    ;(async () => {
      try {
        const resp = await fetch(url, { credentials: 'same-origin' })
        if (!resp.ok) throw new Error(String(resp.status))
        const blob = await resp.blob()
        if (cancelled) return
        objectUrl = URL.createObjectURL(blob)
        setBlobUrl(objectUrl)
      } catch {
        if (!cancelled) setError(true)
      }
    })()
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [url])
  return { blobUrl, error }
}

function RecordingPlayer({ recordingId }: { recordingId: string }) {
  const { blobUrl, error } = useRecordingBlob(getRecordingUrl(recordingId))
  if (error) {
    return <div style={{ fontSize: 12, color: '#c0392b', padding: '4px 2px' }}>Couldn’t load recording.</div>
  }
  if (!blobUrl) {
    return <div style={{ fontSize: 12, color: '#7c98b6', padding: '4px 2px' }}>Loading recording…</div>
  }
  return (
    <audio className="har-audio-player" controls src={blobUrl}>
      Your browser does not support audio playback.
    </audio>
  )
}

function AddressInput(props: {
  value: string
  onChange: (v: string) => void
  onAddressPicked?: (addr: ParsedAddress) => void
  placeholder?: string
  className?: string
  style?: React.CSSProperties
  disabled?: boolean
  autoFocus?: boolean
  onBlur?: () => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  dataTourId?: string
}) {
  const [predictions, setPredictions] = useState<Array<{ place_id: string; main_text: string; secondary_text: string }>>([])
  const [open, setOpen] = useState(false)
  const [highlight, setHighlight] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const sessionTokenRef = useRef<string>('')
  const fetchPredictions = useCallback(async (q: string) => {
    if (!q || q.trim().length < 3) { setPredictions([]); return }
    try {
      const resp = await fetch(`/api/places/autocomplete?q=${encodeURIComponent(q)}&token=${sessionTokenRef.current}`)
      const data: any = await resp.json()
      setPredictions(data.predictions || [])
      setHighlight(0)
    } catch {
      setPredictions([])
    }
  }, [])

  // Debounced fetch on value change.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (!open || !props.value || props.value.trim().length < 3) return
    debounceRef.current = setTimeout(() => fetchPredictions(props.value), 200)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [props.value, open, fetchPredictions])

  // Close on click-outside.
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      const target = e.target as Node
      if (wrapperRef.current?.contains(target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handler, true)
    return () => document.removeEventListener('mousedown', handler, true)
  }, [open])

  const pickPrediction = async (p: { place_id: string; main_text: string }) => {
    setOpen(false)
    setPredictions([])
    try {
      const resp = await fetch(`/api/places/details?place_id=${encodeURIComponent(p.place_id)}&token=${sessionTokenRef.current}`)
      const addr: any = await resp.json()
      if (addr?.error) throw new Error(addr.error)
      const parsed: ParsedAddress = {
        street: addr.street || p.main_text,
        city: addr.city || '',
        state: addr.state || '',
        zip: addr.zip || '',
        country: addr.country || 'US',
        formatted: addr.formatted || p.main_text,
        lat: addr.lat,
        lng: addr.lng,
      }
      props.onChange(parsed.street || parsed.formatted)
      props.onAddressPicked?.(parsed)
    } catch (e) {
      console.error('places-details failed:', e)
      props.onChange(p.main_text)
    }
    sessionTokenRef.current = crypto.randomUUID()
  }

  return (
    <div ref={wrapperRef} style={{ position: 'relative', width: '100%' }}>
      <input
        ref={inputRef}
        type="text"
        data-tour-id={props.dataTourId}
        value={props.value}
        onChange={e => {
          props.onChange(e.target.value)
          if (!open) setOpen(true)
        }}
        onFocus={() => {
          if (!sessionTokenRef.current) sessionTokenRef.current = crypto.randomUUID()
          setOpen(true)
        }}
        onKeyDown={e => {
          if (open && predictions.length > 0) {
            if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, predictions.length - 1)); return }
            if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); return }
            if (e.key === 'Enter') {
              const chosen = predictions[highlight]
              if (chosen) { e.preventDefault(); pickPrediction(chosen); return }
            }
            if (e.key === 'Escape') { setOpen(false); return }
          }
          props.onKeyDown?.(e)
        }}
        placeholder={props.placeholder}
        className={props.className}
        style={{ ...props.style, width: '100%', boxSizing: 'border-box' }}
        disabled={props.disabled}
        autoFocus={props.autoFocus}
        autoComplete="off"
      />
      {open && predictions.length > 0 && (
        <div
          ref={dropdownRef}
          data-tour-id={props.dataTourId ? `${props.dataTourId}-suggestions` : undefined}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 2,
            maxHeight: 260,
            overflowY: 'auto',
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 6,
            boxShadow: '0 8px 24px rgba(15,23,42,0.15)',
            zIndex: 100000,
          }}
          onMouseDown={e => e.preventDefault()}
        >
          {predictions.map((p, idx) => (
            <div
              key={p.place_id}
              data-tour-id={props.dataTourId && idx === 0 ? `${props.dataTourId}-suggestion` : undefined}
              onClick={() => pickPrediction(p)}
              onMouseEnter={() => setHighlight(idx)}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                background: idx === highlight ? '#f1f5f9' : '#fff',
                borderBottom: idx < predictions.length - 1 ? '1px solid #f1f5f9' : 'none',
                fontSize: '0.85rem',
              }}
            >
              <div style={{ color: '#33475b', fontWeight: 500 }}>{p.main_text}</div>
              {p.secondary_text && <div style={{ color: '#7c98b6', fontSize: '0.75rem', marginTop: 1 }}>{p.secondary_text}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ============================================================================
// TeamChatWidget — floating chat pill/panel (Phase 2 of team chat)
// Powered by Phase 1 /api/chat/* endpoints on client-ops-crm-api.
// Renders once globally inside <App/>. All state is local to this component.
// ============================================================================
type TCWUser = { id: number; name: string; email: string; role: string }
type TCWThreadMember = { id: number; name: string }
type TCWThread = {
  id: string
  type: 'dm' | 'group'
  title: string | null
  members: TCWThreadMember[]
  last_message_at: string | null
  last_message_preview: string | null
  unread_count: number
}
type TCWMessage = { id: string; user_id: number; body: string; created_at: string }

function tcwRelativeTime(iso: string | null): string {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const diffMs = Date.now() - then
  if (diffMs < 60_000) return 'now'
  const mins = Math.floor(diffMs / 60_000)
  if (mins < 60) return `${mins}m`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h`
  const days = Math.floor(hrs / 24)
  if (days === 1) return 'Yesterday'
  if (days < 7) return `${days}d`
  const d = new Date(iso)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function tcwInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function tcwColorFromId(id: number | string): string {
  const palette = ['#2563eb', '#dc2626', '#16a34a', '#ea580c', '#9333ea', '#0891b2', '#d97706', '#db2777']
  const n = typeof id === 'number' ? id : Array.from(String(id)).reduce((a, c) => a + c.charCodeAt(0), 0)
  return palette[Math.abs(n) % palette.length]
}

function tcwFormatMsgTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

function TeamChatWidget() {
  const [expanded, setExpanded] = useState(false)
  const [view, setView] = useState<'list' | 'conversation' | 'newThread'>('list')
  const [me, setMe] = useState<TCWUser | null>(null)
  const [allUsers, setAllUsers] = useState<TCWUser[]>([])
  const [usersLoaded, setUsersLoaded] = useState(false)
  const [threads, setThreads] = useState<TCWThread[]>([])
  const [threadsLoading, setThreadsLoading] = useState(false)
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null)
  const [messages, setMessages] = useState<TCWMessage[]>([])
  const [messagesLoading, setMessagesLoading] = useState(false)
  const [hasMoreHistory, setHasMoreHistory] = useState(false)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const [wsStatus, setWsStatus] = useState<'idle' | 'connecting' | 'open' | 'polling' | 'disconnected'>('idle')
  // New thread picker state
  const [newMode, setNewMode] = useState<'dm' | 'group'>('dm')
  const [newSearch, setNewSearch] = useState('')
  const [newSelected, setNewSelected] = useState<number[]>([])
  const [newTitle, setNewTitle] = useState('')
  const [newSubmitting, setNewSubmitting] = useState(false)

  const wsRef = useRef<WebSocket | null>(null)
  const wsReconnectRef = useRef<{ attempts: number; timer: number | null }>({ attempts: 0, timer: null })
  const pollRef = useRef<(() => void) | null>(null)
  const heartbeatRef = useRef<number | null>(null)
  const messagesBoxRef = useRef<HTMLDivElement | null>(null)
  const wasAtBottomRef = useRef<boolean>(true)
  const activeThreadIdRef = useRef<string | null>(null)
  // Tracks user_ids we've already triggered a user-refetch for, so we don't
  // hammer /api/chat/users on every render when a name is genuinely missing.
  const refetchedMissingIdsRef = useRef<Set<number>>(new Set())

  useEffect(() => { activeThreadIdRef.current = activeThreadId }, [activeThreadId])

  const totalUnread = threads.reduce((sum, t) => sum + (t.unread_count || 0), 0)

  // ---- Load threads ----
  const loadThreads = useCallback(async (showSpinner = false) => {
    if (showSpinner) setThreadsLoading(true)
    try {
      const r = await fetch('/api/chat/threads')
      if (!r.ok) return
      const data = await r.json() as { threads: TCWThread[] }
      setThreads(data.threads || [])
    } catch {
      // swallow
    } finally {
      if (showSpinner) setThreadsLoading(false)
    }
  }, [])

  // ---- Load users (once on first expand; force=true refetches on demand) ----
  const loadUsers = useCallback(async (force = false) => {
    if (usersLoaded && !force) return
    try {
      const r = await fetch('/api/chat/users')
      if (!r.ok) return
      const data = await r.json() as { me: TCWUser; users: TCWUser[] }
      setMe(data.me)
      setAllUsers(data.users || [])
      setUsersLoaded(true)
    } catch {
      // swallow
    }
  }, [usersLoaded])

  // ---- When widget is expanded, poll threads every 20s (unless conv open) ----
  useEffect(() => {
    if (!expanded) return
    loadThreads(true)
    loadUsers()
    if (view === 'conversation') return // handled by WS
    return startManagedInterval({ name: 'team-chat-thread-list', intervalMs: 20_000, run: loadThreads })
  }, [expanded, view, loadThreads, loadUsers])

  // Even when collapsed, refresh the unread badge occasionally so the pill is
  // accurate without requiring the user to open the panel.
  useEffect(() => {
    if (expanded) return
    loadThreads()
    return startManagedInterval({ name: 'team-chat-unread-badge', intervalMs: 60_000, run: loadThreads })
  }, [expanded, loadThreads])

  // ---- Load messages for active thread ----
  const loadInitialMessages = useCallback(async (threadId: string) => {
    setMessagesLoading(true)
    try {
      const r = await fetch(`/api/chat/threads/${threadId}/messages?limit=50`)
      if (!r.ok) { setMessages([]); setHasMoreHistory(false); return }
      const data = await r.json() as { messages: TCWMessage[] }
      // server returns newest-first; reverse for oldest->newest display
      const ordered = [...(data.messages || [])].reverse()
      setMessages(ordered)
      setHasMoreHistory((data.messages || []).length >= 50)
      // mark-read after history loads
      try {
        await fetch(`/api/chat/threads/${threadId}/read`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        })
        setThreads(prev => prev.map(t => t.id === threadId ? { ...t, unread_count: 0 } : t))
      } catch { /* noop */ }
    } finally {
      setMessagesLoading(false)
      // scroll to bottom
      requestAnimationFrame(() => {
        const box = messagesBoxRef.current
        if (box) box.scrollTop = box.scrollHeight
      })
    }
  }, [])

  const loadOlderMessages = useCallback(async () => {
    const threadId = activeThreadIdRef.current
    if (!threadId || loadingOlder || !hasMoreHistory || messages.length === 0) return
    const oldest = messages[0]
    setLoadingOlder(true)
    const box = messagesBoxRef.current
    const prevHeight = box ? box.scrollHeight : 0
    try {
      const r = await fetch(`/api/chat/threads/${threadId}/messages?before=${encodeURIComponent(oldest.created_at)}&limit=50`)
      if (!r.ok) return
      const data = await r.json() as { messages: TCWMessage[] }
      const older = [...(data.messages || [])].reverse()
      if (older.length === 0) { setHasMoreHistory(false); return }
      setMessages(prev => [...older, ...prev])
      if (older.length < 50) setHasMoreHistory(false)
      requestAnimationFrame(() => {
        const box2 = messagesBoxRef.current
        if (box2) box2.scrollTop = box2.scrollHeight - prevHeight
      })
    } finally {
      setLoadingOlder(false)
    }
  }, [hasMoreHistory, loadingOlder, messages])

  // ---- WebSocket management ----
  const cleanupRealtime = useCallback(() => {
    if (wsRef.current) {
      try { wsRef.current.close(1000, 'cleanup') } catch { /* noop */ }
      wsRef.current = null
    }
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current)
      heartbeatRef.current = null
    }
    if (wsReconnectRef.current.timer) {
      clearTimeout(wsReconnectRef.current.timer)
      wsReconnectRef.current.timer = null
    }
    wsReconnectRef.current.attempts = 0
    if (pollRef.current) {
      pollRef.current()
      pollRef.current = null
    }
  }, [])

  const handleIncomingMessage = useCallback((msg: TCWMessage, threadId: string) => {
    const box = messagesBoxRef.current
    const atBottom = box ? (box.scrollHeight - box.scrollTop - box.clientHeight < 50) : true
    wasAtBottomRef.current = atBottom
    if (activeThreadIdRef.current === threadId) {
      setMessages(prev => {
        if (prev.some(m => m.id === msg.id)) return prev
        return [...prev, msg]
      })
      // auto-scroll + mark-read only if at bottom
      if (atBottom) {
        requestAnimationFrame(() => {
          const box2 = messagesBoxRef.current
          if (box2) box2.scrollTop = box2.scrollHeight
        })
        fetch(`/api/chat/threads/${threadId}/read`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        }).catch(() => { /* noop */ })
      }
    }
    // Update thread list preview / last message time
    setThreads(prev => prev.map(t => t.id === threadId
      ? {
        ...t,
        last_message_at: msg.created_at,
        last_message_preview: msg.body.slice(0, 80),
        unread_count: (activeThreadIdRef.current === threadId && atBottom) ? 0 : (t.unread_count || 0) + (msg.user_id !== me?.id ? 1 : 0),
      }
      : t))
  }, [me])

  const startPolling = useCallback((threadId: string) => {
    setWsStatus('polling')
    if (pollRef.current) pollRef.current()
    let lastSeen = messages.length > 0 ? messages[messages.length - 1].created_at : new Date(Date.now() - 60_000).toISOString()
    pollRef.current = startManagedInterval({
      name: 'team-chat-active-thread',
      intervalMs: 5_000,
      run: async () => {
        if (activeThreadIdRef.current !== threadId) return
        try {
          const r = await fetch(`/api/chat/threads/${threadId}/messages?limit=50`)
          if (!r.ok) return
          const data = await r.json() as { messages: TCWMessage[] }
          const ordered = [...(data.messages || [])].reverse()
          const newOnes = ordered.filter(m => m.created_at > lastSeen)
          if (newOnes.length > 0) {
            lastSeen = newOnes[newOnes.length - 1].created_at
            for (const m of newOnes) handleIncomingMessage(m, threadId)
          }
        } catch { /* noop */ }
      },
    })
  }, [messages, handleIncomingMessage])

  const connectWs = useCallback((threadId: string) => {
    cleanupRealtime()
    setWsStatus('connecting')
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const url = `${proto}//${window.location.host}/api/chat/threads/${threadId}/ws`
    let ws: WebSocket
    try {
      ws = new WebSocket(url)
    } catch {
      startPolling(threadId)
      return
    }
    wsRef.current = ws
    ws.onopen = () => {
      setWsStatus('open')
      wsReconnectRef.current.attempts = 0
      if (heartbeatRef.current) clearInterval(heartbeatRef.current)
      heartbeatRef.current = window.setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          try { ws.send(JSON.stringify({ type: 'ping' })) } catch { /* noop */ }
        }
      }, 30000)
    }
    ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data)
        if (data && data.type === 'message' && typeof data.id === 'string') {
          handleIncomingMessage(
            { id: data.id, user_id: Number(data.user_id), body: data.body, created_at: data.created_at },
            data.thread_id || threadId,
          )
        }
      } catch { /* ignore malformed */ }
    }
    ws.onclose = (ev) => {
      if (heartbeatRef.current) { clearInterval(heartbeatRef.current); heartbeatRef.current = null }
      // normal close or intentional cleanup
      if (ev.code === 1000 || activeThreadIdRef.current !== threadId) return
      // auth failure — fall back to polling
      if (ev.code === 1008 || ev.code === 4401 || ev.code === 4403) {
        startPolling(threadId)
        return
      }
      // retry with backoff
      const attempts = wsReconnectRef.current.attempts
      if (attempts >= 5) {
        setWsStatus('disconnected')
        return
      }
      const delay = Math.min(15000, 1000 * Math.pow(2, attempts))
      wsReconnectRef.current.attempts = attempts + 1
      wsReconnectRef.current.timer = window.setTimeout(() => {
        if (activeThreadIdRef.current === threadId) connectWs(threadId)
      }, delay)
    }
    ws.onerror = () => {
      // onclose will handle
    }
  }, [cleanupRealtime, handleIncomingMessage, startPolling])

  // Open a thread
  const openThread = useCallback(async (threadId: string) => {
    setActiveThreadId(threadId)
    setView('conversation')
    setMessages([])
    setHasMoreHistory(false)
    await loadInitialMessages(threadId)
    connectWs(threadId)
  }, [loadInitialMessages, connectWs])

  // Back to list
  const backToList = useCallback(() => {
    cleanupRealtime()
    setActiveThreadId(null)
    setView('list')
    setMessages([])
    setDraft('')
    setWsStatus('idle')
    loadThreads()
  }, [cleanupRealtime, loadThreads])

  // Widget collapse
  const collapseWidget = useCallback(() => {
    cleanupRealtime()
    setExpanded(false)
    setView('list')
    setActiveThreadId(null)
    setMessages([])
    setDraft('')
    setWsStatus('idle')
  }, [cleanupRealtime])

  // ---- Leave / delete thread ----
  const deleteThread = useCallback(async (threadId: string) => {
    if (!threadId) return
    if (!window.confirm('Leave this conversation? Messages will remain for other members.')) return
    try {
      const r = await fetch(`/api/chat/threads/${threadId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      })
      if (!r.ok) return
      // Remove locally
      setThreads(prev => prev.filter(t => t.id !== threadId))
      // If we were viewing it, tear down and return to the list
      if (activeThreadIdRef.current === threadId) {
        cleanupRealtime()
        setActiveThreadId(null)
        setMessages([])
        setDraft('')
        setWsStatus('idle')
        setView('list')
      }
    } catch {
      // swallow
    }
  }, [cleanupRealtime])

  // Cleanup on unmount
  useEffect(() => {
    return () => { cleanupRealtime() }
  }, [cleanupRealtime])

  // ---- Send message ----
  const sendMessage = useCallback(async () => {
    const body = draft.trim()
    if (!body || sending || !activeThreadId) return
    setSending(true)
    try {
      const r = await fetch(`/api/chat/threads/${activeThreadId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body }),
      })
      if (!r.ok) return
      // Backend returns { message: { id, thread_id, user_id, body, created_at } }.
      // Unwrap defensively so we still work if the shape is ever flattened.
      const payload = await r.json() as { message?: TCWMessage } & Partial<TCWMessage>
      const raw = payload.message ?? (payload as TCWMessage)
      if (!raw || !raw.id) return
      const msg: TCWMessage = {
        id: raw.id,
        user_id: Number(raw.user_id),
        body: raw.body,
        created_at: raw.created_at,
      }
      // Optimistic append if WS echo hasn't arrived yet
      setMessages(prev => prev.some(m => m.id === msg.id) ? prev : [...prev, msg])
      setDraft('')
      requestAnimationFrame(() => {
        const box = messagesBoxRef.current
        if (box) box.scrollTop = box.scrollHeight
      })
    } finally {
      setSending(false)
    }
  }, [draft, sending, activeThreadId])

  // ---- Scroll handler for load-older ----
  const onMessagesScroll = useCallback(() => {
    const box = messagesBoxRef.current
    if (!box) return
    if (box.scrollTop < 40 && hasMoreHistory && !loadingOlder) {
      loadOlderMessages()
    }
  }, [hasMoreHistory, loadingOlder, loadOlderMessages])

  // ---- Create new thread ----
  const submitNewThread = useCallback(async () => {
    if (newSubmitting) return
    if (newMode === 'dm' && newSelected.length !== 1) return
    if (newMode === 'group' && newSelected.length < 1) return
    setNewSubmitting(true)
    try {
      const body: { type: 'dm' | 'group'; title?: string; member_ids: number[] } = {
        type: newMode,
        member_ids: newSelected,
      }
      if (newMode === 'group' && newTitle.trim()) body.title = newTitle.trim()
      const r = await fetch('/api/chat/threads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!r.ok) return
      const data = await r.json() as { id: string; existed?: boolean }
      // reset picker state
      setNewMode('dm')
      setNewSelected([])
      setNewTitle('')
      setNewSearch('')
      await loadThreads()
      openThread(data.id)
    } finally {
      setNewSubmitting(false)
    }
  }, [newMode, newSelected, newTitle, newSubmitting, loadThreads, openThread])

  // ---- Derived: resolved thread title / avatar ----
  const threadTitle = useCallback((t: TCWThread): string => {
    if (t.type === 'group') {
      if (t.title && t.title.trim()) return t.title
      return t.members.filter(m => m.id !== me?.id).map(m => m.name).join(', ') || 'Group chat'
    }
    const other = t.members.find(m => m.id !== me?.id)
    return other ? other.name : (t.title || 'Direct message')
  }, [me])

  const threadAvatar = useCallback((t: TCWThread): { initials: string; color: string; isGroup: boolean } => {
    if (t.type === 'group') {
      return { initials: '', color: '#64748b', isGroup: true }
    }
    const other = t.members.find(m => m.id !== me?.id)
    if (!other) return { initials: '?', color: '#64748b', isGroup: false }
    return { initials: tcwInitials(other.name), color: tcwColorFromId(other.id), isGroup: false }
  }, [me])

  // ---- Render ----
  const activeThread = threads.find(t => t.id === activeThreadId) || null

  const filteredUsers = allUsers
    .filter(u => u.id !== me?.id)
    .filter(u => {
      if (!newSearch.trim()) return true
      return u.name.toLowerCase().includes(newSearch.toLowerCase()) || u.email.toLowerCase().includes(newSearch.toLowerCase())
    })

  return (
    <div style={{ position: 'fixed', right: 20, bottom: 20, zIndex: 9000, fontFamily: 'inherit' }}>
      {!expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          aria-label="Open team chat"
          style={{
            position: 'relative',
            width: 56,
            height: 56,
            borderRadius: '50%',
            border: 'none',
            background: '#2563eb',
            color: '#fff',
            boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          {totalUnread > 0 && (
            <span
              style={{
                position: 'absolute',
                top: -2,
                right: -2,
                minWidth: 20,
                height: 20,
                padding: '0 5px',
                borderRadius: 10,
                background: '#dc2626',
                color: '#fff',
                fontSize: 11,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid #fff',
                boxSizing: 'border-box',
              }}
            >
              {totalUnread >= 10 ? '9+' : totalUnread}
            </span>
          )}
        </button>
      )}
      {expanded && (
        <div
          style={{
            width: 380,
            height: 560,
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 12px 48px rgba(0,0,0,0.22)',
            border: '1px solid #e5e7eb',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {view === 'list' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>Messages</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <button
                    type="button"
                    onClick={() => { setView('newThread'); loadUsers() }}
                    style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                  >
                    New
                  </button>
                  <button
                    type="button"
                    onClick={collapseWidget}
                    aria-label="Close chat"
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                  </button>
                </div>
              </div>
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {threadsLoading && threads.length === 0 && (
                  <div style={{ padding: 20, textAlign: 'center', color: '#64748b', fontSize: 13 }}>Loading…</div>
                )}
                {!threadsLoading && threads.length === 0 && (
                  <div style={{ padding: '28px 20px', textAlign: 'center', color: '#64748b', fontSize: 13 }}>
                    No conversations yet. Click <strong>New</strong> to start one.
                  </div>
                )}
                {threads.map(t => {
                  const av = threadAvatar(t)
                  return (
                    <div
                      key={t.id}
                      onClick={() => openThread(t.id)}
                      style={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '10px 14px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f3f4f6',
                        background: '#fff',
                      }}
                      onMouseEnter={e => {
                        (e.currentTarget as HTMLDivElement).style.background = '#f9fafb'
                        const btn = (e.currentTarget as HTMLDivElement).querySelector('[data-tcw-leave]') as HTMLElement | null
                        if (btn) btn.style.opacity = '1'
                      }}
                      onMouseLeave={e => {
                        (e.currentTarget as HTMLDivElement).style.background = '#fff'
                        const btn = (e.currentTarget as HTMLDivElement).querySelector('[data-tcw-leave]') as HTMLElement | null
                        if (btn) btn.style.opacity = '0'
                      }}
                    >
                      <div
                        style={{
                          width: 38,
                          height: 38,
                          borderRadius: '50%',
                          background: av.color,
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 13,
                          fontWeight: 600,
                          flexShrink: 0,
                        }}
                      >
                        {av.isGroup ? (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                        ) : av.initials}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, alignItems: 'baseline' }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {threadTitle(t)}
                          </div>
                          <div style={{ fontSize: 11, color: '#9ca3af', flexShrink: 0 }}>
                            {tcwRelativeTime(t.last_message_at)}
                          </div>
                        </div>
                        <div style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 2 }}>
                          {t.last_message_preview || '—'}
                        </div>
                      </div>
                      {t.unread_count > 0 && (
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#2563eb', flexShrink: 0 }} />
                      )}
                      <button
                        data-tcw-leave
                        type="button"
                        aria-label="Leave conversation"
                        title="Leave conversation"
                        onClick={e => { e.stopPropagation(); deleteThread(t.id) }}
                        style={{
                          position: 'absolute',
                          top: '50%',
                          right: 8,
                          transform: 'translateY(-50%)',
                          opacity: 0,
                          transition: 'opacity 120ms ease',
                          background: '#fff',
                          border: '1px solid #e5e7eb',
                          borderRadius: 4,
                          width: 22,
                          height: 22,
                          padding: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          color: '#6b7280',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                        }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                      </button>
                    </div>
                  )
                })}
              </div>
            </>
          )}

          {view === 'conversation' && activeThread && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  <button
                    type="button"
                    onClick={backToList}
                    aria-label="Back"
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: '#374151', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
                  </button>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {threadTitle(activeThread)}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={collapseWidget}
                  aria-label="Close chat"
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                </button>
              </div>
              {wsStatus === 'disconnected' && (
                <div style={{ padding: '6px 12px', background: '#fef2f2', color: '#991b1b', fontSize: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #fee2e2' }}>
                  <span>Disconnected</span>
                  <button
                    type="button"
                    onClick={() => { wsReconnectRef.current.attempts = 0; if (activeThreadId) connectWs(activeThreadId) }}
                    style={{ background: 'transparent', border: '1px solid #991b1b', color: '#991b1b', borderRadius: 4, padding: '2px 8px', fontSize: 11, cursor: 'pointer' }}
                  >Retry</button>
                </div>
              )}
              <div
                ref={messagesBoxRef}
                onScroll={onMessagesScroll}
                style={{ flex: 1, overflowY: 'auto', padding: '10px 12px', background: '#f3f4f6', display: 'flex', flexDirection: 'column', gap: 4 }}
              >
                {loadingOlder && (
                  <div style={{ textAlign: 'center', fontSize: 11, color: '#6b7280', padding: 6 }}>Loading older…</div>
                )}
                {messagesLoading && messages.length === 0 && (
                  <div style={{ textAlign: 'center', fontSize: 12, color: '#6b7280', padding: 20 }}>Loading…</div>
                )}
                {!messagesLoading && messages.length === 0 && (
                  <div style={{ textAlign: 'center', fontSize: 12, color: '#6b7280', padding: 20 }}>No messages yet. Say hi.</div>
                )}
                {messages.map((m, idx) => {
                  // Coerce once — ids can arrive as "5" from JSON in some paths.
                  const msgUserId = Number(m.user_id)
                  const isMe = !!(me && msgUserId === Number(me.id))
                  const prev = idx > 0 ? messages[idx - 1] : null
                  const sameBurst = prev && Number(prev.user_id) === msgUserId && (new Date(m.created_at).getTime() - new Date(prev.created_at).getTime()) < 5 * 60_000
                  const showHeader = !sameBurst
                  const sender =
                    activeThread.members.find(mem => Number(mem.id) === msgUserId) ||
                    allUsers.find(u => Number(u.id) === msgUserId)
                  let senderName: string
                  if (sender) {
                    senderName = sender.name
                  } else if (isMe) {
                    senderName = me?.name || 'Me'
                  } else if (Number.isFinite(msgUserId)) {
                    senderName = `User ${msgUserId}`
                    // Refetch /users once per unknown id in case a new member joined
                    // after our initial users fetch.
                    if (!refetchedMissingIdsRef.current.has(msgUserId)) {
                      refetchedMissingIdsRef.current.add(msgUserId)
                      loadUsers(true)
                    }
                  } else {
                    senderName = 'Unknown user'
                  }
                  return (
                    <div key={m.id} style={{ display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start', marginTop: showHeader ? 8 : 0 }}>
                      {showHeader && !isMe && (
                        <div style={{ fontSize: 11, color: '#6b7280', margin: '0 4px 2px' }}>
                          {senderName} · {tcwFormatMsgTime(m.created_at)}
                        </div>
                      )}
                      {showHeader && isMe && (
                        <div style={{ fontSize: 11, color: '#6b7280', margin: '0 4px 2px' }}>
                          {tcwFormatMsgTime(m.created_at)}
                        </div>
                      )}
                      <div
                        style={{
                          maxWidth: '78%',
                          padding: '7px 11px',
                          borderRadius: 14,
                          background: isMe ? '#2563eb' : '#fff',
                          color: isMe ? '#fff' : '#111827',
                          fontSize: 13,
                          lineHeight: 1.35,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          border: isMe ? 'none' : '1px solid #e5e7eb',
                          borderBottomRightRadius: isMe ? 4 : 14,
                          borderBottomLeftRadius: isMe ? 14 : 4,
                        }}
                      >
                        {m.body}
                      </div>
                    </div>
                  )
                })}
              </div>
              <div style={{ padding: 10, borderTop: '1px solid #e5e7eb', background: '#fff', display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  value={draft}
                  onChange={e => setDraft(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage() } }}
                  placeholder="Message…"
                  style={{ flex: 1, padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, outline: 'none' }}
                />
                <button
                  type="button"
                  onClick={sendMessage}
                  disabled={!draft.trim() || sending}
                  style={{
                    background: !draft.trim() || sending ? '#93c5fd' : '#2563eb',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    padding: '8px 14px',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: !draft.trim() || sending ? 'not-allowed' : 'pointer',
                  }}
                >Send</button>
              </div>
            </>
          )}

          {view === 'newThread' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => { setView('list'); setNewSelected([]); setNewSearch(''); setNewTitle('') }}
                    aria-label="Back"
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: '#374151', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
                  </button>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>New conversation</div>
                </div>
                <button
                  type="button"
                  onClick={collapseWidget}
                  aria-label="Close chat"
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                </button>
              </div>
              <div style={{ padding: 12, borderBottom: '1px solid #f3f4f6' }}>
                <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                  {(['dm', 'group'] as const).map(m => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => { setNewMode(m); setNewSelected([]) }}
                      style={{
                        flex: 1,
                        padding: '6px 10px',
                        borderRadius: 6,
                        border: '1px solid ' + (newMode === m ? '#2563eb' : '#e5e7eb'),
                        background: newMode === m ? '#eff6ff' : '#fff',
                        color: newMode === m ? '#2563eb' : '#374151',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >{m === 'dm' ? 'Direct Message' : 'Group'}</button>
                  ))}
                </div>
                {newMode === 'group' && (
                  <input
                    type="text"
                    value={newTitle}
                    onChange={e => setNewTitle(e.target.value)}
                    placeholder="Group title (optional)"
                    style={{ width: '100%', padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 13, marginBottom: 8, boxSizing: 'border-box' }}
                  />
                )}
                <input
                  type="text"
                  value={newSearch}
                  onChange={e => setNewSearch(e.target.value)}
                  placeholder="Search people…"
                  style={{ width: '100%', padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {filteredUsers.length === 0 && (
                  <div style={{ padding: 20, textAlign: 'center', color: '#64748b', fontSize: 13 }}>No users found.</div>
                )}
                {filteredUsers.map(u => {
                  const selected = newSelected.includes(u.id)
                  return (
                    <div
                      key={u.id}
                      onClick={() => {
                        if (newMode === 'dm') {
                          setNewSelected([u.id])
                        } else {
                          setNewSelected(prev => prev.includes(u.id) ? prev.filter(x => x !== u.id) : [...prev, u.id])
                        }
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '8px 14px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f3f4f6',
                        background: selected ? '#eff6ff' : '#fff',
                      }}
                    >
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          background: tcwColorFromId(u.id),
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 12,
                          fontWeight: 600,
                          flexShrink: 0,
                        }}
                      >{tcwInitials(u.name)}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.name}</div>
                        <div style={{ fontSize: 11, color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.email}</div>
                      </div>
                      {selected && (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      )}
                    </div>
                  )
                })}
              </div>
              <div style={{ padding: 10, borderTop: '1px solid #e5e7eb', background: '#fff', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button
                  type="button"
                  onClick={submitNewThread}
                  disabled={
                    newSubmitting ||
                    (newMode === 'dm' && newSelected.length !== 1) ||
                    (newMode === 'group' && newSelected.length < 1)
                  }
                  style={{
                    background: newSubmitting || (newMode === 'dm' && newSelected.length !== 1) || (newMode === 'group' && newSelected.length < 1) ? '#93c5fd' : '#2563eb',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                    padding: '7px 16px',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >{newSubmitting ? 'Starting…' : 'Start'}</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Shared hub components — HubHeader, FilterChips, avatar helpers
// (used by the Feed and inbox avatar surfaces).
// ---------------------------------------------------------------------------

function m365FormatBytes(n: number): string {
  if (!n) return '0 B'
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`
}

function m365Initials(name: string): string {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/).slice(0, 2)
  return parts.map(p => p[0]?.toUpperCase() || '').join('') || '?'
}

const m365AvatarPalette = ['#0054a9', '#0ea5e9', '#14b8a6', '#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#ef4444']
function m365AvatarColor(seed: string): string {
  let h = 0
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) & 0xffffffff
  return m365AvatarPalette[Math.abs(h) % m365AvatarPalette.length]
}

function HubHeader(props: {
  icon: React.ReactNode
  title: string
  subtitle?: string
  search?: { value: string; onChange: (v: string) => void; placeholder?: string }
  onRefresh?: () => void
  refreshing?: boolean
  right?: React.ReactNode
  /** Rendered between the refresh button and `right`. Use this for primary
   *  CTAs (e.g. the Files hub upload split-button) so they sit at the end of
   *  the header-actions row. Existing call sites that don't pass this get
   *  the same layout as before. */
  primaryAction?: React.ReactNode
  /** Extra class on the header root — e.g. the Feed passes `feed-glass-header`
   *  for the frosted-glass sticky treatment. Call sites that omit it are
   *  byte-for-byte unaffected. */
  className?: string
}) {
  return (
    <div className={`bp-hub-header${props.className ? ` ${props.className}` : ''}`}>
      <div className="bp-hub-header-row">
        <div className="bp-hub-title-wrap">
          <span className="bp-hub-icon" aria-hidden="true">{props.icon}</span>
          <div>
            <h1 className="bp-hub-title">{props.title}</h1>
            {props.subtitle && <div className="bp-hub-subtitle">{props.subtitle}</div>}
          </div>
        </div>
        <div className="bp-hub-header-actions">
          {props.search && (
            <div className="bp-search-wrap">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              <input
                className="bp-search-input"
                placeholder={props.search.placeholder || 'Search'}
                value={props.search.value}
                onChange={(e) => props.search!.onChange(e.target.value)}
              />
              {props.search.value && (
                <button className="bp-search-clear" onClick={() => props.search!.onChange('')} aria-label="Clear search">&times;</button>
              )}
            </div>
          )}
          {props.onRefresh && (
            <button className={`bp-refresh-btn${props.refreshing ? ' spinning' : ''}`} onClick={props.onRefresh} disabled={props.refreshing} title="Refresh" aria-label="Refresh">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 0 0-15-6.7L3 8"/><path d="M3 3v5h5"/><path d="M3 12a9 9 0 0 0 15 6.7l3-2.7"/><path d="M21 21v-5h-5"/></svg>
            </button>
          )}
          {props.primaryAction}
          {props.right}
        </div>
      </div>
    </div>
  )
}

function FilterChips(props: {
  chips: Array<{ key: string; label: string; count?: number }>
  activeKey: string
  onChange: (key: string) => void
  right?: React.ReactNode
}) {
  return (
    <div className="bp-chips-row">
      <div className="bp-chips-wrap">
        {props.chips.map(chip => (
          <button
            key={chip.key}
            className={`bp-chip${props.activeKey === chip.key ? ' active' : ''}`}
            onClick={() => props.onChange(chip.key)}
          >
            {chip.label}
            {typeof chip.count === 'number' && <span className="bp-chip-count">{chip.count}</span>}
          </button>
        ))}
      </div>
      {props.right}
    </div>
  )
}

// ViewToggle is used by Files hub, Deal files, and Team hub. When `showTree`
// is true (Team hub), a third "Tree" button is rendered for the Org Explorer.
// Callers that never pass 'tree' can continue to use the narrower 'grid'|'list'
// union — TypeScript widens the mode type here so the Tree button works, but
// the compiler still allows both existing and new callers.
type ViewToggleMode = 'grid' | 'list' | 'tree'
function ViewToggle<M extends ViewToggleMode = ViewToggleMode>(props: {
  mode: M
  onChange: (m: M) => void
  showTree?: boolean
}) {
  return (
    <div className="bp-view-toggle" role="group" aria-label="View mode">
      <button
        className={`bp-view-toggle-btn${props.mode === 'grid' ? ' active' : ''}`}
        onClick={() => props.onChange('grid' as M)}
        title="Grid view"
        aria-label="Grid view"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
      </button>
      <button
        className={`bp-view-toggle-btn${props.mode === 'list' ? ' active' : ''}`}
        onClick={() => props.onChange('list' as M)}
        title="List view"
        aria-label="List view"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
      </button>
      {props.showTree && (
        <button
          className={`bp-view-toggle-btn${props.mode === 'tree' ? ' active' : ''}`}
          onClick={() => props.onChange('tree' as M)}
          title="Org chart view"
          aria-label="Org chart view"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="8" y="2" width="8" height="5" rx="1" />
            <rect x="1" y="17" width="7" height="5" rx="1" />
            <rect x="9" y="17" width="6" height="5" rx="1" />
            <rect x="16" y="17" width="7" height="5" rx="1" />
            <path d="M12 7v3" />
            <path d="M4.5 17v-4h15v4" />
          </svg>
        </button>
      )}
    </div>
  )
}

type M365Person = {
  id: string
  displayName: string
  givenName?: string
  surname?: string
  mail?: string
  userPrincipalName?: string
  jobTitle?: string
  department?: string
  officeLocation?: string
  mobilePhone?: string
  businessPhones?: string[]
  companyName?: string
  photoUrl?: string
  division?: string | null
  startDate?: string | null
  pronouns?: string | null
}

function PersonAvatar(props: { person: Pick<M365Person, 'id' | 'displayName' | 'photoUrl'>; size?: number }) {
  const size = props.size || 56
  const [failed, setFailed] = React.useState(false)
  // Reset the failure flag whenever the photoUrl changes — otherwise a
  // freshly-uploaded photo never shows up if the prior url errored.
  React.useEffect(() => { setFailed(false) }, [props.person.photoUrl])
  const init = m365Initials(props.person.displayName)
  const bg = m365AvatarColor(props.person.id || props.person.displayName || 'x')

  if (failed || !props.person.photoUrl) {
    return (
      <span className="ppl-avatar-wrap" style={{ width: size, height: size }}>
        <span className="bp-avatar" style={{ width: size, height: size, background: bg, fontSize: size * 0.4 }} aria-hidden="true">{init}</span>
      </span>
    )
  }
  return (
    <span className="ppl-avatar-wrap" style={{ width: size, height: size }}>
      <span className="bp-avatar-img-wrap" style={{ width: size, height: size }}>
        <img
          key={props.person.photoUrl}
          src={props.person.photoUrl}
          alt=""
          loading="lazy"
          style={{ width: size, height: size }}
          onError={() => setFailed(true)}
        />
      </span>
    </span>
  )
}

// ---------------------------------------------------------------------------
// Social Feed + Profile Hub (#/feed, #/profile/:id)
// A company-wide community space: text posts, kudos, auto-celebrations,
// announcements. Right sidebar highlights birthdays, anniversaries, new hires
// and top kudos recipients. Profile pages aggregate Graph user data + HR
// social fields + post history + kudos received + active work.
// ---------------------------------------------------------------------------

type FeedPostType = 'text' | 'kudos' | 'celebration' | 'announcement' | 'job_win' | 'safety'
type FeedReactionActor = { email: string; name: string }
type FeedReaction = { emoji: string; count: number; userReacted: boolean; reactors?: FeedReactionActor[] }
type FeedPost = {
  id: number
  authorEmail: string
  authorName?: string | null
  authorPhotoUrl?: string | null
  postType: FeedPostType
  body: string
  subjectEmails: string[]
  mediaUrls: string[]
  metadata: Record<string, any>
  pinned: boolean
  pinnedBy?: string | null
  pinnedUntil?: string | null
  requiresAcknowledgment: boolean
  createdAt: string
  updatedAt?: string | null
  reactions: FeedReaction[]
  commentCount: number
  acknowledged: boolean
}
type FeedComment = {
  id: number
  postId: number
  authorEmail: string
  authorName?: string | null
  authorPhotoUrl?: string | null
  body: string
  createdAt: string
}
type FeedAckRoster = {
  postId: number
  requiredAudience: string[]
  acked: Array<{ email: string; name: string; acknowledgedAt: string }>
  pending: Array<{ email: string; name: string }>
  stats: {
    totalRequired: number
    acknowledged: number
    pending: number
    percentComplete: number
  }
}
type FeedCelebrationPerson = {
  id: number
  email: string
  displayName: string
  jobTitle?: string | null
  department?: string | null
  photoUrl?: string | null
  month?: number
  day?: number
  years?: number
  isToday?: boolean
  daysUntil?: number
  startDate?: string
  count?: number
}
type FeedCelebrations = {
  birthdays: FeedCelebrationPerson[]
  anniversaries: FeedCelebrationPerson[]
  newHires: FeedCelebrationPerson[]
  topKudos: FeedCelebrationPerson[]
}

const KUDOS_CATEGORIES: Array<{ key: string; label: string; emoji: string }> = [
  { key: 'safety', label: 'Safety', emoji: '\u{1F6E1}\u{FE0F}' },
  { key: 'quality', label: 'Quality', emoji: '\u2B50' },
  { key: 'collaboration', label: 'Collaboration', emoji: '\u{1F91D}' },
  { key: 'excellence', label: 'Excellence', emoji: '\u{1F3C6}' },
]

const REACTION_EMOJIS = ['\u{1F44D}', '\u2764\u{FE0F}', '\u{1F389}', '\u{1F525}', '\u{1F44F}', '\u{1F64C}', '\u{1F602}', '\u{1F4AF}']

function applySingleFeedReaction(reactions: FeedReaction[], emoji: string, actor: FeedReactionActor): FeedReaction[] {
  const actorEmail = actor.email.toLowerCase()
  const clickedMine = reactions.some(r => r.emoji === emoji && r.userReacted)
  const next: FeedReaction[] = []

  for (const reaction of reactions) {
    const withoutActor = (reaction.reactors || []).filter(r => r.email.toLowerCase() !== actorEmail)
    const wasMine = reaction.userReacted
    const shouldRemoveMine = wasMine && (clickedMine || reaction.emoji !== emoji)
    const nextCount = Math.max(0, reaction.count - (shouldRemoveMine ? 1 : 0))
    if (nextCount > 0) {
      next.push({
        ...reaction,
        count: nextCount,
        userReacted: false,
        reactors: withoutActor,
      })
    }
  }

  if (clickedMine) return next

  const target = next.find(r => r.emoji === emoji)
  if (target) {
    target.count += 1
    target.userReacted = true
    target.reactors = [...(target.reactors || []), actor]
    return [...next]
  }

  return [...next, { emoji, count: 1, userReacted: true, reactors: [actor] }]
}

function feedRelativeTime(iso: string | null | undefined): string {
  if (!iso) return '\u2014'
  const then = new Date(iso).getTime()
  if (!Number.isFinite(then)) return '\u2014'
  const diffMs = Date.now() - then
  const sec = Math.round(diffMs / 1000)
  if (sec < 60) return 'just now'
  const min = Math.round(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.round(min / 60)
  if (hr < 24) return `${hr}h ago`
  const day = Math.round(hr / 24)
  if (day < 7) return `${day}d ago`
  if (day < 30) return `${Math.round(day / 7)}w ago`
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

// Render markdown-lite: **bold**, *italic*, line breaks, @mentions (as chips).
// Apply formatting tags then pass through DOMPurify with an allow-list. No raw HTML
// in source — prevents XSS from user-posted body content.
function FeedBody(props: { body: string; subjectEmails?: string[]; onOpenPerson?: (email: string) => void }) {
  const { body, subjectEmails = [], onOpenPerson } = props
  const mentionSet = new Set(subjectEmails.map(s => s.toLowerCase()))
  const nodes: React.ReactNode[] = []
  const parts = body.split(/(\n|\s+)/)
  parts.forEach((part, i) => {
    if (/^@\S+$/.test(part)) {
      const raw = part.slice(1)
      const isEmail = mentionSet.has(raw.toLowerCase())
      nodes.push(
        <span
          key={`m-${i}`}
          className={`feed-mention${isEmail ? ' clickable' : ''}`}
          onClick={isEmail && onOpenPerson ? () => onOpenPerson(raw) : undefined}
        >
          @{raw.split('@')[0]}
        </span>
      )
    } else {
      const withFormat = part
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*([^*\n]+)\*/g, '<em>$1</em>')
      // DOMPurify with a tight allow-list — only our two formatting tags pass
      const clean = DOMPurify.sanitize(withFormat, {
        ALLOWED_TAGS: ['strong', 'em'],
        ALLOWED_ATTR: [],
      })
      nodes.push(<span key={`t-${i}`} dangerouslySetInnerHTML={{ __html: clean }} />)
    }
  })
  return <div className="feed-body">{nodes}</div>
}

function FeedLightbox(props: { src: string | null; onClose: () => void }) {
  React.useEffect(() => {
    if (!props.src) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') props.onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [props.src, props.onClose])
  if (!props.src) return null
  return (
    <div className="feed-lightbox" onClick={props.onClose}>
      <img src={props.src} alt="" onClick={(e) => e.stopPropagation()} />
      <button className="feed-lightbox-close" onClick={props.onClose} aria-label="Close">&times;</button>
    </div>
  )
}

function FeedPostMediaGrid(props: { urls: string[]; onPreview: (src: string) => void; altBase?: string }) {
  const { urls, onPreview, altBase } = props
  if (!urls || urls.length === 0) return null
  const count = urls.length
  const shown = Math.min(count, 4)
  return (
    <div className={`feed-media feed-media-${shown}`}>
      {urls.slice(0, 4).map((u, i) => (
        <button
          type="button"
          key={`${u}-${i}`}
          className="feed-media-cell"
          onClick={() => onPreview(u)}
          aria-label={altBase ? `View image ${i + 1} of ${shown} from ${altBase}` : `View image ${i + 1}`}
        >
          <img src={u} alt={altBase ? `${altBase} — attachment ${i + 1}` : ''} loading="lazy" />
        </button>
      ))}
    </div>
  )
}

function FeedPersonPicker(props: { people: M365Person[]; onPick: (p: M365Person) => void; placeholder?: string }) {
  const [query, setQuery] = React.useState('')
  const [open, setOpen] = React.useState(false)
  const ref = React.useRef<HTMLDivElement | null>(null)
  React.useEffect(() => {
    const onClick = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])
  const filtered = React.useMemo(() => {
    if (!query.trim()) return props.people.slice(0, 10)
    const q = query.toLowerCase()
    return props.people.filter(p =>
      (p.displayName || '').toLowerCase().includes(q) ||
      (p.mail || '').toLowerCase().includes(q)
    ).slice(0, 10)
  }, [query, props.people])
  return (
    <div className="feed-person-picker" ref={ref}>
      <input
        className="feed-person-picker-input"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => { if (e.key === 'Escape' && open) { e.preventDefault(); e.stopPropagation(); setOpen(false) } }}
        placeholder={props.placeholder || 'Search person\u2026'}
      />
      {open && filtered.length > 0 && (
        <div className="feed-person-picker-pop">
          {filtered.map(p => (
            <button key={p.id} className="feed-person-picker-row" onClick={() => { props.onPick(p); setQuery(''); setOpen(false) }}>
              <PersonAvatar person={p} size={24} />
              <div className="feed-person-picker-name">{p.displayName}</div>
              <div className="feed-person-picker-sub">{p.jobTitle || ''}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function FeedComposer(props: {
  currentUser: { email: string; name: string; photoUrl?: string | null }
  isAdmin: boolean
  team: M365Person[]
  onPosted: (post: FeedPost) => void
  // Deep-link prefill (#/feed?kudos=<email> from a profile's "Give kudos"):
  // flips the composer into kudos mode with the target chip set. Nonce-keyed
  // so each hash intent applies exactly once per composer instance.
  kudosPrefill?: { person: M365Person; nonce: number } | null
}) {
  const { currentUser, isAdmin, team, onPosted, kudosPrefill } = props
  const DRAFT_KEY = 'feed-composer-draft'
  const [focused, setFocused] = React.useState(false)
  const [mode, setMode] = React.useState<'text' | 'kudos' | 'announcement'>('text')
  const [body, setBody] = React.useState<string>(() => {
    try { return localStorage.getItem(DRAFT_KEY) || '' } catch { return '' }
  })
  const [media, setMedia] = React.useState<Array<{ file: File; url: string; uploadedUrl?: string; uploading?: boolean }>>([])
  const [kudosTargets, setKudosTargets] = React.useState<M365Person[]>([])
  const [kudosCategory, setKudosCategory] = React.useState<string>('quality')
  const [pinned, setPinned] = React.useState(false)
  const [requiresAck, setRequiresAck] = React.useState(false)
  const [mentionQuery, setMentionQuery] = React.useState<string | null>(null)
  const [showEmoji, setShowEmoji] = React.useState(false)
  const [submitting, setSubmitting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null)
  const fileInputRef = React.useRef<HTMLInputElement | null>(null)
  const [dragOver, setDragOver] = React.useState(false)

  React.useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(320, el.scrollHeight)}px`
  }, [body, focused])

  React.useEffect(() => {
    try {
      if (body) localStorage.setItem(DRAFT_KEY, body)
      else localStorage.removeItem(DRAFT_KEY)
    } catch { /* ignore */ }
  }, [body])

  React.useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    const pos = el.selectionStart || 0
    const upTo = body.slice(0, pos)
    const m = /@([\w.-]*)$/.exec(upTo)
    if (m) setMentionQuery(m[1])
    else setMentionQuery(null)
  }, [body])

  const filteredTeam = React.useMemo(() => {
    if (mentionQuery == null) return [] as M365Person[]
    const q = mentionQuery.toLowerCase()
    if (!q) return team.slice(0, 8)
    return team
      .filter(p => (p.displayName || '').toLowerCase().includes(q) || (p.mail || '').toLowerCase().includes(q))
      .slice(0, 8)
  }, [mentionQuery, team])

  // Profile → feed interlink: consume the "Give kudos" deep-link intent.
  const appliedKudosNonceRef = React.useRef(0)
  React.useEffect(() => {
    if (!kudosPrefill || kudosPrefill.nonce === appliedKudosNonceRef.current) return
    appliedKudosNonceRef.current = kudosPrefill.nonce
    const person = kudosPrefill.person
    setMode('kudos')
    setFocused(true)
    setKudosTargets(prev => {
      const email = (person.mail || '').toLowerCase()
      if (!email) return prev
      if (prev.some(t => (t.mail || '').toLowerCase() === email)) return prev
      return [...prev, person]
    })
    window.setTimeout(() => {
      const el = textareaRef.current
      // Skip focus when this composer instance is CSS-hidden (the desktop
      // composer is display:none in the mobile layout — the overlay one is
      // the visible instance there).
      if (el && el.offsetParent !== null) {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' })
        el.focus()
      }
    }, 60)
  }, [kudosPrefill])

  function insertMention(p: M365Person) {
    const email = p.mail || ''
    if (!email) return
    const el = textareaRef.current
    if (!el) return
    const pos = el.selectionStart || body.length
    const before = body.slice(0, pos)
    const after = body.slice(pos)
    const replaced = before.replace(/@([\w.-]*)$/, `@${email} `)
    const newBody = replaced + after
    setBody(newBody)
    setMentionQuery(null)
    setTimeout(() => {
      if (!el) return
      el.focus()
      const np = replaced.length
      el.setSelectionRange(np, np)
    }, 0)
  }

  async function uploadFile(file: File): Promise<string> {
    const fd = new FormData()
    fd.append('file', file)
    const resp = await fetch('/api/social/feed/upload', { method: 'POST', body: fd, credentials: 'include' })
    if (!resp.ok) {
      const j = await resp.json().catch(() => ({} as any))
      throw new Error(j.error || `Upload HTTP ${resp.status}`)
    }
    const data = await resp.json() as { url: string }
    return data.url
  }

  async function handleFiles(files: FileList | File[]) {
    const arr = Array.from(files).filter(f => f.type.startsWith('image/')).slice(0, 4 - media.length)
    if (!arr.length) return
    const newEntries = arr.map(f => ({ file: f, url: URL.createObjectURL(f), uploading: true as const }))
    setMedia(prev => [...prev, ...newEntries])
    for (const entry of newEntries) {
      try {
        const url = await uploadFile(entry.file)
        setMedia(prev => prev.map(m => m.file === entry.file ? { ...m, uploadedUrl: url, uploading: false } : m))
      } catch (e: any) {
        setError(e?.message || 'Upload failed')
        setMedia(prev => prev.filter(m => m.file !== entry.file))
      }
    }
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault()
    setDragOver(false)
    if (e.dataTransfer?.files?.length) void handleFiles(e.dataTransfer.files)
  }

  function insertEmoji(emoji: string) {
    const el = textareaRef.current
    if (!el) { setBody(prev => prev + emoji); return }
    const pos = el.selectionStart || body.length
    const before = body.slice(0, pos)
    const after = body.slice(pos)
    const newBody = before + emoji + after
    setBody(newBody)
    setShowEmoji(false)
    setTimeout(() => {
      if (!el) return
      el.focus()
      const np = (before + emoji).length
      el.setSelectionRange(np, np)
    }, 0)
  }

  const canPost = (() => {
    if (!body.trim() && media.length === 0) return false
    if (mode === 'kudos' && kudosTargets.length === 0) return false
    if (media.some(m => m.uploading)) return false
    return !submitting
  })()

  async function submit() {
    if (!canPost) return
    setSubmitting(true)
    setError(null)
    try {
      const subjectEmails = mode === 'kudos'
        ? kudosTargets.map(t => t.mail || '').filter(Boolean)
        : (body.match(/@[\w.+-]+@[\w.-]+\.[a-z]{2,}/gi) || []).map(s => s.replace(/^@/, ''))
      const mediaUrls = media.map(m => m.uploadedUrl).filter((u): u is string => !!u)
      const metadata = mode === 'kudos' ? { category: kudosCategory } : undefined
      const postType: FeedPostType = mode === 'kudos' ? 'kudos' : mode === 'announcement' ? 'announcement' : 'text'
      const resp = await fetch('/api/social/feed', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          postType,
          body: body.trim(),
          subjectEmails,
          mediaUrls,
          metadata,
          pinned: isAdmin && (mode === 'announcement' || pinned) ? true : false,
          requiresAcknowledgment: isAdmin && requiresAck,
        }),
      })
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({} as any))
        throw new Error(j.error || `HTTP ${resp.status}`)
      }
      const { post } = await resp.json() as { post: FeedPost }
      setBody('')
      setMedia([])
      setKudosTargets([])
      setPinned(false)
      setRequiresAck(false)
      setFocused(false)
      try { localStorage.removeItem(DRAFT_KEY) } catch { /* ignore */ }
      onPosted(post)
    } catch (e: any) {
      setError(e?.message || 'Failed to post')
    } finally {
      setSubmitting(false)
    }
  }

  const currentUserAvatar: M365Person = {
    id: currentUser.email, displayName: currentUser.name || currentUser.email, mail: currentUser.email, photoUrl: currentUser.photoUrl || undefined,
  }
  return (
    <div
      className={`feed-composer${focused ? ' focused' : ''}${dragOver ? ' drag-over' : ''}`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
    >
      <div className="feed-composer-top">
        <PersonAvatar person={currentUserAvatar} size={38} />
        <div className="feed-composer-main">
          {focused && (
            <div className="feed-composer-mode">
              <button className={`feed-mode-chip${mode === 'text' ? ' active' : ''}`} onClick={() => setMode('text')}>Post</button>
              <button className={`feed-mode-chip${mode === 'kudos' ? ' active' : ''}`} onClick={() => setMode('kudos')}>Kudos</button>
              {isAdmin && (
                <button className={`feed-mode-chip${mode === 'announcement' ? ' active' : ''}`} onClick={() => setMode('announcement')}>Announcement</button>
              )}
            </div>
          )}

          {mode === 'kudos' && (
            <div className="feed-kudos-setup">
              <div className="feed-kudos-label">For</div>
              <div className="feed-kudos-targets">
                {kudosTargets.map(t => (
                  <span key={t.id} className="feed-kudos-chip">
                    <PersonAvatar person={t} size={20} />
                    <span>{t.displayName}</span>
                    <button onClick={() => setKudosTargets(prev => prev.filter(p => p.id !== t.id))} aria-label="Remove">&times;</button>
                  </span>
                ))}
                <FeedPersonPicker
                  people={team.filter(p => !kudosTargets.find(t => t.id === p.id))}
                  onPick={(p) => setKudosTargets(prev => [...prev, p])}
                  placeholder={kudosTargets.length ? 'Add another\u2026' : 'Who deserves this?'}
                />
              </div>
              <div className="feed-kudos-label" style={{ marginTop: 10 }}>Category</div>
              <div className="feed-kudos-cats">
                {KUDOS_CATEGORIES.map(c => (
                  <button key={c.key} className={`feed-kudos-cat${kudosCategory === c.key ? ' active' : ''}`} onClick={() => setKudosCategory(c.key)}>
                    <span className="feed-kudos-cat-emoji">{c.emoji}</span>
                    <span>{c.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <textarea
            ref={textareaRef}
            className="feed-composer-textarea"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onFocus={() => setFocused(true)}
            onKeyDown={(e) => { if (e.key === 'Escape' && mentionQuery != null) { e.preventDefault(); setMentionQuery(null) } }}
            placeholder={
              mode === 'kudos' ? 'Say something nice\u2026' :
              mode === 'announcement' ? 'Share an announcement\u2026' :
              "Share a job site win, a shoutout, or just say hi\u2026"
            }
            rows={focused ? 3 : 1}
          />

          {!focused && body.trim().length > 0 && (
            <div className="feed-composer-draft-row">
              <span className="feed-composer-draft-tag">Draft</span>
              <span className="feed-composer-draft-hint">Saved — not posted yet</span>
              <button
                type="button"
                className="feed-composer-draft-clear"
                onClick={() => { setBody(''); try { localStorage.removeItem(DRAFT_KEY) } catch { /* ignore */ } }}
              >
                Clear
              </button>
            </div>
          )}

          {mentionQuery != null && filteredTeam.length > 0 && (
            <div className="feed-mention-pop">
              {filteredTeam.map(p => (
                <button key={p.id} className="feed-mention-row" onClick={() => insertMention(p)}>
                  <PersonAvatar person={p} size={28} />
                  <div>
                    <div className="feed-mention-name">{p.displayName}</div>
                    <div className="feed-mention-sub">{p.jobTitle || p.department || p.mail || ''}</div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {media.length > 0 && (
            <div className="feed-composer-media">
              {media.map((m, i) => (
                <div key={i} className={`feed-composer-media-item${m.uploading ? ' uploading' : ''}`}>
                  <img src={m.url} alt="" />
                  {m.uploading && <div className="feed-composer-media-spinner" />}
                  <button className="feed-composer-media-remove" onClick={() => setMedia(prev => prev.filter((_, idx) => idx !== i))} aria-label="Remove">&times;</button>
                </div>
              ))}
            </div>
          )}

          {focused && (
            <div className="feed-composer-actions">
              <div className="feed-composer-tools">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => { if (e.target.files) void handleFiles(e.target.files); e.target.value = '' }}
                />
                <button
                  className="feed-tool-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={media.length >= 4}
                  title={media.length >= 4 ? 'Max 4 photos' : 'Add photos'}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21,15 16,10 5,21"/></svg>
                  <span>Photo</span>
                </button>
                <div className="feed-emoji-wrap">
                  <button className="feed-tool-btn" onClick={() => setShowEmoji(v => !v)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>
                    <span>Emoji</span>
                  </button>
                  {showEmoji && (
                    <div className="feed-emoji-pop">
                      {['\u{1F600}','\u{1F602}','\u{1F389}','\u2764\u{FE0F}','\u{1F525}','\u{1F44D}','\u{1F44F}','\u{1F64C}','\u{1F4AF}','\u{1F680}','\u{1F3C6}','\u2B50','\u{1F6E1}\u{FE0F}','\u{1F91D}','\u2705','\u{1F440}'].map(e => (
                        <button key={e} className="feed-emoji-choice" onClick={() => insertEmoji(e)}>{e}</button>
                      ))}
                    </div>
                  )}
                </div>
                {isAdmin && mode !== 'kudos' && (
                  <>
                    <label className="feed-checkbox">
                      <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} />
                      <span>Pin</span>
                    </label>
                    <label className="feed-checkbox">
                      <input type="checkbox" checked={requiresAck} onChange={(e) => setRequiresAck(e.target.checked)} />
                      <span>Require ack</span>
                    </label>
                  </>
                )}
              </div>
              <div className="feed-composer-submit">
                {error && <div className="feed-composer-error">{error}</div>}
                <button className="feed-post-btn" disabled={!canPost} onClick={submit}>
                  {submitting ? 'Posting\u2026' : 'Post'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function FeedPostCard(props: {
  post: FeedPost
  currentUserEmail: string
  currentUserPhotoUrl?: string | null
  isAdmin: boolean
  onReact: (postId: number, emoji: string) => void
  onAck: (postId: number) => void
  onOpenPerson: (email: string) => void
  onPreviewImage: (url: string) => void
  onDeleted: (postId: number) => void
  onEdited?: (post: FeedPost) => void
}) {
  const { post, currentUserEmail, currentUserPhotoUrl, isAdmin, onReact, onAck, onOpenPerson, onPreviewImage, onDeleted, onEdited } = props
  const [editing, setEditing] = React.useState(false)
  const [editBody, setEditBody] = React.useState(post.body)
  const [savingEdit, setSavingEdit] = React.useState(false)
  const [editError, setEditError] = React.useState<string | null>(null)
  const [showComments, setShowComments] = React.useState(false)
  const [comments, setComments] = React.useState<FeedComment[]>([])
  const [commentsLoading, setCommentsLoading] = React.useState(false)
  const [newComment, setNewComment] = React.useState('')
  const [showEmoji, setShowEmoji] = React.useState(false)
  const [menuOpen, setMenuOpen] = React.useState(false)
  const menuRef = React.useRef<HTMLDivElement | null>(null)
  const [showRoster, setShowRoster] = React.useState(false)
  const [roster, setRoster] = React.useState<FeedAckRoster | null>(null)
  const [rosterLoading, setRosterLoading] = React.useState(false)
  const [rosterTab, setRosterTab] = React.useState<'acked' | 'pending'>('acked')
  const isOwner = post.authorEmail.toLowerCase() === currentUserEmail.toLowerCase()
  const canModify = isOwner || isAdmin
  const authorPerson: M365Person = {
    id: post.authorEmail,
    displayName: post.authorName || post.authorEmail,
    mail: post.authorEmail,
    photoUrl: post.authorPhotoUrl || (isOwner ? currentUserPhotoUrl || undefined : undefined),
  }
  const typeLabel = post.postType === 'kudos' ? 'Kudos' :
    post.postType === 'celebration' ? 'Celebration' :
    post.postType === 'announcement' ? 'Announcement' :
    post.postType === 'safety' ? 'Safety' :
    post.postType === 'job_win' ? 'Job win' : null
  const kudosCat = post.postType === 'kudos' ? KUDOS_CATEGORIES.find(c => c.key === post.metadata?.category) : null

  React.useEffect(() => {
    const onClick = (e: MouseEvent) => { if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false) }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  async function loadComments() {
    setCommentsLoading(true)
    try {
      const resp = await fetch(`/api/social/feed/${post.id}/comments`, { credentials: 'include' })
      if (resp.ok) {
        const data = await resp.json() as { comments: FeedComment[] }
        setComments(data.comments)
      }
    } finally {
      setCommentsLoading(false)
    }
  }

  async function loadRoster() {
    if (!post.requiresAcknowledgment || !isAdmin) return
    setRosterLoading(true)
    setRosterTab('acked')
    setShowRoster(true)
    try {
      const resp = await fetch(`/api/social/feed/${post.id}/roster`, { credentials: 'include' })
      if (resp.ok) {
        const data = await resp.json() as FeedAckRoster
        setRoster(data)
      } else {
        setRoster(null)
      }
    } catch {
      setRoster(null)
    } finally {
      setRosterLoading(false)
    }
  }

  function toggleComments() {
    if (!showComments) void loadComments()
    setShowComments(v => !v)
  }

  async function postComment() {
    const trimmed = newComment.trim()
    if (!trimmed) return
    try {
      const resp = await fetch(`/api/social/feed/${post.id}/comments`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: trimmed }),
      })
      if (resp.ok) {
        const { comment } = await resp.json() as { comment: FeedComment }
        setComments(prev => [...prev, comment])
        setNewComment('')
      }
    } catch { /* noop */ }
  }

  async function handleDelete() {
    if (!confirm('Delete this post?')) return
    try {
      const resp = await fetch(`/api/social/feed/${post.id}`, { method: 'DELETE', credentials: 'include' })
      if (resp.ok) onDeleted(post.id)
    } catch { /* noop */ }
    setMenuOpen(false)
  }

  function startEdit() {
    setEditBody(post.body)
    setEditError(null)
    setEditing(true)
    setMenuOpen(false)
  }

  async function saveEdit() {
    const trimmed = editBody.trim()
    if (!trimmed) { setEditError('Post can’t be empty'); return }
    if (trimmed === post.body) { setEditing(false); return }
    setSavingEdit(true)
    setEditError(null)
    try {
      const resp = await fetch(`/api/social/feed/${post.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: trimmed }),
      })
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({} as any))
        throw new Error(j.error || `HTTP ${resp.status}`)
      }
      const { post: updated } = await resp.json() as { post: FeedPost }
      onEdited?.(updated)
      setEditing(false)
    } catch (e: any) {
      setEditError(e?.message || 'Failed to save')
    } finally {
      setSavingEdit(false)
    }
  }

  const isEdited = !!post.updatedAt && new Date(post.updatedAt).getTime() - new Date(post.createdAt).getTime() > 2000

  const reactionsSummary = post.reactions.slice(0, 8)
  const reactionNames = (reaction: FeedReaction) => {
    const actors = reaction.reactors || []
    if (!actors.length) return `${reaction.count} reaction${reaction.count === 1 ? '' : 's'}`
    const visible = actors.slice(0, 8).map(a => a.name || a.email)
    if (actors.length > visible.length) visible.push(`and ${actors.length - visible.length} more`)
    return visible.join('\n')
  }

  return (
    <article className={`feed-post${post.pinned ? ' pinned' : ''}${post.postType === 'announcement' ? ' announcement' : ''}${post.postType === 'celebration' ? ' celebration' : ''}${post.postType === 'kudos' ? ' kudos' : ''}`}>
      <header className="feed-post-head">
        <button className="feed-post-author" onClick={() => onOpenPerson(post.authorEmail)}>
          <PersonAvatar person={authorPerson} size={40} />
          <div className="feed-post-author-text">
            <div className="feed-post-author-name">
              {post.authorName || post.authorEmail}
              {post.authorEmail === 'system' && <span className="feed-post-system-badge">BotPros</span>}
            </div>
            <div className="feed-post-meta">
              <span>{feedRelativeTime(post.createdAt)}</span>
              {typeLabel && (
                <>
                  <span className="feed-post-dot">·</span>
                  <span className={`feed-post-type feed-post-type-${post.postType}`}>
                    {kudosCat && <span className="feed-kudos-badge-emoji">{kudosCat.emoji}</span>}
                    {typeLabel}{kudosCat ? ` · ${kudosCat.label}` : ''}
                  </span>
                </>
              )}
              {post.pinned && (
                <>
                  <span className="feed-post-dot">·</span>
                  <span className="feed-pinned-badge">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 17v5"/><path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z"/></svg>
                    Pinned
                  </span>
                </>
              )}
              {isEdited && (
                <>
                  <span className="feed-post-dot">·</span>
                  <span className="feed-post-edited" title={`Edited ${feedRelativeTime(post.updatedAt)}`}>edited</span>
                </>
              )}
            </div>
          </div>
        </button>
        {canModify && (
          <div className="feed-post-menu-wrap" ref={menuRef}>
            <button className="feed-post-menu-btn" onClick={() => setMenuOpen(v => !v)} aria-label="Post menu">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="5" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="12" cy="19" r="1"/></svg>
            </button>
            {menuOpen && (
              <div className="feed-post-menu">
                {post.requiresAcknowledgment && isAdmin && (
                  <button className="feed-post-menu-item" onClick={() => { setMenuOpen(false); void loadRoster() }}>View acknowledgments</button>
                )}
                <button className="feed-post-menu-item" onClick={startEdit}>Edit</button>
                <button className="feed-post-menu-item danger" onClick={handleDelete}>Delete</button>
              </div>
            )}
          </div>
        )}
      </header>

      <div className="feed-post-body-wrap">
        {editing ? (
          <div className="feed-post-edit">
            <textarea
              className="feed-post-edit-input"
              value={editBody}
              autoFocus
              onChange={(e) => setEditBody(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') { e.preventDefault(); setEditing(false); setEditError(null) }
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void saveEdit() }
              }}
              rows={Math.min(8, Math.max(2, editBody.split('\n').length))}
            />
            {editError && <div className="feed-composer-error">{editError}</div>}
            <div className="feed-post-edit-actions">
              <button className="feed-post-edit-cancel" onClick={() => { setEditing(false); setEditError(null) }} disabled={savingEdit}>Cancel</button>
              <button className="feed-post-edit-save" onClick={() => void saveEdit()} disabled={savingEdit || !editBody.trim()}>{savingEdit ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        ) : (
          <FeedBody body={post.body} subjectEmails={post.subjectEmails} onOpenPerson={onOpenPerson} />
        )}
        {post.subjectEmails.length > 0 && post.postType === 'kudos' && (
          <div className="feed-kudos-recipients">
            {post.subjectEmails.map(email => (
              <button key={email} className="feed-kudos-recipient" onClick={() => onOpenPerson(email)}>
                <PersonAvatar person={{ id: email, displayName: email.split('@')[0] }} size={22} />
                <span>{email.split('@')[0].replace(/[._]/g, ' ')}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <FeedPostMediaGrid urls={post.mediaUrls} onPreview={onPreviewImage} altBase={post.authorName || 'Post'} />

      {post.requiresAcknowledgment && (
        <div className="feed-ack-row">
          {post.acknowledged ? (
            <span className="feed-ack-done">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20,6 9,17 4,12"/></svg>
              Acknowledged
            </span>
          ) : (
            <button className="feed-ack-btn" onClick={() => onAck(post.id)}>I acknowledge</button>
          )}
        </div>
      )}

      <footer className="feed-post-foot">
        <div className="feed-reaction-row">
          {reactionsSummary.map(r => (
            <button
              key={r.emoji}
              className={`feed-reaction-chip${r.userReacted ? ' mine' : ''}`}
              onClick={() => onReact(post.id, r.emoji)}
              aria-label={`${r.emoji} reaction from ${reactionNames(r)}`}
            >
              <span className="feed-reaction-emoji">{r.emoji}</span>
              <span className="feed-reaction-count">{r.count}</span>
              {/* Roster tooltip: each named reactor deep-links to their profile.
                  stopPropagation keeps a roster click from toggling the reaction. */}
              <span className="feed-reaction-tooltip" role="tooltip" onClick={(e) => e.stopPropagation()}>
                {(r.reactors && r.reactors.length > 0) ? (
                  <>
                    {r.reactors.slice(0, 8).map(a => (
                      <span
                        key={a.email}
                        className="feed-reaction-tooltip-name"
                        role="link"
                        tabIndex={-1}
                        onClick={(e) => { e.stopPropagation(); onOpenPerson(a.email) }}
                      >
                        {a.name || a.email}
                      </span>
                    ))}
                    {r.reactors.length > 8 && <span>and {r.reactors.length - 8} more</span>}
                  </>
                ) : (
                  <span>{r.count} reaction{r.count === 1 ? '' : 's'}</span>
                )}
              </span>
            </button>
          ))}
          <div className="feed-reaction-add-wrap">
            <button className="feed-reaction-add" onClick={() => setShowEmoji(v => !v)} aria-label="Add reaction">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
            </button>
            {showEmoji && (
              <div className="feed-reaction-picker">
                {REACTION_EMOJIS.map(e => (
                  <button key={e} className="feed-reaction-picker-btn" onClick={() => { onReact(post.id, e); setShowEmoji(false) }}>
                    {e}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <button className="feed-comment-toggle" onClick={toggleComments}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
          <span>{post.commentCount > 0 ? `${post.commentCount} comment${post.commentCount === 1 ? '' : 's'}` : 'Comment'}</span>
        </button>
      </footer>

      {showComments && (
        <div className="feed-comments">
          {commentsLoading && comments.length === 0 && <div className="feed-comments-loading">{'Loading\u2026'}</div>}
          {comments.map(c => (
            <div key={c.id} className="feed-comment">
              <button
                type="button"
                className="feed-comment-avatar"
                onClick={() => onOpenPerson(c.authorEmail)}
                aria-label={`View ${c.authorName || c.authorEmail}'s profile`}
              >
                <PersonAvatar
                  person={{
                    id: c.authorEmail,
                    displayName: c.authorName || c.authorEmail,
                    photoUrl: c.authorPhotoUrl || (c.authorEmail.toLowerCase() === currentUserEmail.toLowerCase() ? currentUserPhotoUrl || undefined : undefined),
                  }}
                  size={28}
                />
              </button>
              <div className="feed-comment-body">
                <div className="feed-comment-head">
                  <button className="feed-comment-author" onClick={() => onOpenPerson(c.authorEmail)}>{c.authorName || c.authorEmail}</button>
                  <span className="feed-comment-time">{feedRelativeTime(c.createdAt)}</span>
                </div>
                <div className="feed-comment-text">{c.body}</div>
              </div>
            </div>
          ))}
          <div className="feed-comment-composer">
            <input
              className="feed-comment-input"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void postComment() } }}
              placeholder="Write a comment..."
            />
            <button className="feed-comment-submit" onClick={postComment} disabled={!newComment.trim()}>Reply</button>
          </div>
        </div>
      )}

      {showRoster && (
        <div className="feed-roster-overlay" onClick={() => setShowRoster(false)}>
          <div className="feed-roster-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Acknowledgment roster">
            <div className="feed-roster-head">
              <h3>Acknowledgment roster</h3>
              <button className="feed-roster-close" onClick={() => setShowRoster(false)} aria-label="Close">✕</button>
            </div>

            {rosterLoading && !roster ? (
              <div className="feed-roster-loading">Loading…</div>
            ) : !roster ? (
              <div className="feed-roster-loading">Couldn’t load the roster.</div>
            ) : (
              <>
                <div className="feed-roster-stats">
                  <div className="feed-roster-stat-item">
                    <div className="feed-roster-stat-number">{roster.stats.acknowledged}</div>
                    <div className="feed-roster-stat-label">Acknowledged</div>
                  </div>
                  <div className="feed-roster-stat-item">
                    <div className="feed-roster-stat-number">{roster.stats.pending}</div>
                    <div className="feed-roster-stat-label">Pending</div>
                  </div>
                  <div className="feed-roster-stat-item">
                    <div className="feed-roster-stat-number">{roster.stats.percentComplete}%</div>
                    <div className="feed-roster-stat-label">Complete</div>
                  </div>
                </div>

                <div className="feed-roster-tabs">
                  <button
                    className={`feed-roster-tab${rosterTab === 'acked' ? ' active' : ''}`}
                    onClick={() => setRosterTab('acked')}
                  >Acknowledged ({roster.acked.length})</button>
                  <button
                    className={`feed-roster-tab${rosterTab === 'pending' ? ' active' : ''}`}
                    onClick={() => setRosterTab('pending')}
                  >Pending ({roster.pending.length})</button>
                </div>

                <div className="feed-roster-list">
                  {rosterTab === 'acked' ? (
                    roster.acked.length === 0 ? (
                      <div className="feed-roster-empty">No one has acknowledged yet.</div>
                    ) : roster.acked.map(person => (
                      <button key={person.email} className="feed-roster-row acked" onClick={() => { setShowRoster(false); onOpenPerson(person.email) }}>
                        <PersonAvatar person={{ id: person.email, displayName: person.name }} size={28} />
                        <div className="feed-roster-row-text">
                          <div className="feed-roster-name">{person.name}</div>
                          <div className="feed-roster-meta">{person.email}</div>
                        </div>
                        <div className="feed-roster-time">{feedRelativeTime(person.acknowledgedAt)}</div>
                      </button>
                    ))
                  ) : (
                    roster.pending.length === 0 ? (
                      <div className="feed-roster-empty">Everyone has acknowledged. 🎉</div>
                    ) : roster.pending.map(person => (
                      <button key={person.email} className="feed-roster-row pending" onClick={() => { setShowRoster(false); onOpenPerson(person.email) }}>
                        <PersonAvatar person={{ id: person.email, displayName: person.name }} size={28} />
                        <div className="feed-roster-row-text">
                          <div className="feed-roster-name">{person.name}</div>
                          <div className="feed-roster-meta">{person.email}</div>
                        </div>
                        <div className="feed-roster-time pending">Pending</div>
                      </button>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </article>
  )
}

function FeedPostSkeleton() {
  return (
    <div className="feed-post feed-post-skeleton">
      <div className="feed-post-head">
        <div className="bp-shimmer" style={{ width: 40, height: 40, borderRadius: '50%' }} />
        <div style={{ flex: 1 }}>
          <div className="bp-shimmer bp-skeleton-line" style={{ width: '32%' }} />
          <div className="bp-shimmer bp-skeleton-line" style={{ width: '20%', height: 10 }} />
        </div>
      </div>
      <div className="bp-shimmer bp-skeleton-line" style={{ width: '95%' }} />
      <div className="bp-shimmer bp-skeleton-line" style={{ width: '70%' }} />
    </div>
  )
}

function FeedSidebarWidget(props: {
  title: string
  icon: React.ReactNode
  tone?: 'birthday' | 'anniversary' | 'new-hire' | 'kudos'
  people: FeedCelebrationPerson[]
  emptyText: string
  meta: (p: FeedCelebrationPerson) => string
  onOpenPerson: (emailOrId: string) => void
  showConfettiOnToday?: boolean
}) {
  const cardClass = `feed-side-card${props.tone ? ` feed-side-card-${props.tone}` : ''}`
  if (!props.people || props.people.length === 0) {
    return (
      <div className={cardClass}>
        <div className="feed-side-head">{props.icon}<span>{props.title}</span></div>
        <div className="feed-side-empty">{props.emptyText}</div>
      </div>
    )
  }
  return (
    <div className={cardClass}>
      <div className="feed-side-head">{props.icon}<span>{props.title}</span></div>
      <div className="feed-side-list">
        {props.people.map(p => (
          <button key={`${p.id}-${p.email}`} className={`feed-side-row${p.isToday ? ' today' : ''}`} onClick={() => props.onOpenPerson(p.email)}>
            <PersonAvatar person={{ id: p.email, displayName: p.displayName, photoUrl: p.photoUrl || undefined }} size={28} />
            <div className="feed-side-row-text">
              <div className="feed-side-row-name">{p.displayName}</div>
              <div className="feed-side-row-meta">{props.meta(p)}</div>
            </div>
            {p.isToday && props.showConfettiOnToday && (
              <span className="feed-side-confetti" aria-hidden="true">{'\u{1F389}'}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  )
}

function FeedDirectoryPanel(props: {
  people: M365Person[]
  loading: boolean
  onOpenPerson: (emailOrId: string) => void
  onOpenOrgMap: () => void
}) {
  const [query, setQuery] = React.useState('')
  const [department, setDepartment] = React.useState('')
  const [expanded, setExpanded] = React.useState(false)

  const departmentStats = React.useMemo(() => {
    const counts = new Map<string, number>()
    for (const p of props.people) {
      const name = (p.department || 'Unassigned').trim() || 'Unassigned'
      counts.set(name, (counts.get(name) || 0) + 1)
    }
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
  }, [props.people])

  const filteredPeople = React.useMemo(() => {
    const q = query.trim().toLowerCase()
    return props.people
      .filter(p => {
        const personDepartment = (p.department || 'Unassigned').trim() || 'Unassigned'
        if (department && personDepartment !== department) return false
        if (!q) return true
        const haystack = [
          p.displayName,
          p.jobTitle,
          p.department,
          p.officeLocation,
          p.mail,
          p.userPrincipalName,
          p.division || '',
        ].filter(Boolean).join(' ').toLowerCase()
        return haystack.includes(q)
      })
      .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''))
  }, [props.people, query, department])

  const visiblePeople = expanded ? filteredPeople.slice(0, 36) : filteredPeople.slice(0, 10)
  const hasMore = !expanded && filteredPeople.length > visiblePeople.length

  React.useEffect(() => {
    setExpanded(false)
  }, [query, department])

  return (
    <div className="feed-side-card feed-directory-card">
      {/* Head + search pin INSIDE the rail's internal scroll (mini-glass) so
        * the directory stays searchable while long rosters scroll beneath. */}
      <div className="feed-directory-sticky">
        <div className="feed-side-head">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
          <span>People</span>
          <span className="feed-directory-count">{props.loading ? 'Syncing' : `${filteredPeople.length}/${props.people.length}`}</span>
        </div>

        <div className="feed-directory-controls">
          <label className="feed-directory-search">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Find a teammate"
              aria-label="Find a teammate"
            />
          </label>
        </div>
      </div>

      {departmentStats.length > 0 && (
        <div className="feed-directory-map">
          <div className="feed-directory-map-head">
            <span>Team map</span>
            <button type="button" onClick={props.onOpenOrgMap}>Open company map</button>
          </div>
          <div className="feed-directory-depts" role="group" aria-label="Filter directory by department">
            <button
              type="button"
              className={`feed-directory-dept feed-directory-dept-all${department === '' ? ' active' : ''}`}
              onClick={() => setDepartment('')}
              aria-pressed={department === ''}
              title="Show everyone"
            >
              <span>All departments</span>
              <strong>{props.people.length}</strong>
            </button>
            {departmentStats.slice(0, 6).map(d => (
              <button
                type="button"
                key={d.name}
                className={`feed-directory-dept${department === d.name ? ' active' : ''}`}
                onClick={() => setDepartment(department === d.name ? '' : d.name)}
                aria-pressed={department === d.name}
                title={`Show ${d.name}`}
              >
                <span>{d.name}</span>
                <strong>{d.count}</strong>
              </button>
            ))}
          </div>
        </div>
      )}

      {props.loading && props.people.length === 0 ? (
        <div className="feed-directory-loading">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="feed-directory-skeleton">
              <span className="bp-shimmer" />
              <div>
                <span className="bp-shimmer" />
                <span className="bp-shimmer" />
              </div>
            </div>
          ))}
        </div>
      ) : visiblePeople.length === 0 ? (
        <div className="feed-side-empty">No teammates match that search.</div>
      ) : (
        <>
          <div className={`feed-directory-list${expanded ? ' expanded' : ''}`}>
            {visiblePeople.map(p => {
              const email = p.mail || p.userPrincipalName || p.id
              return (
                <div
                  key={p.id || email}
                  className="feed-directory-row"
                  role="button"
                  tabIndex={0}
                  onClick={() => props.onOpenPerson(email)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      props.onOpenPerson(email)
                    }
                  }}
                >
                  <PersonAvatar person={{ id: p.id || email, displayName: p.displayName, photoUrl: p.photoUrl }} size={30} />
                  <span className="feed-directory-person">
                    <span className="feed-directory-name">{p.displayName || email}</span>
                    <span className="feed-directory-meta">{p.jobTitle || p.department || p.officeLocation || email}</span>
                  </span>
                  {email && email.includes('@') && (
                    <a
                      className="feed-directory-mail"
                      href={`mailto:${email}`}
                      title={`Email ${p.displayName || email}`}
                      aria-label={`Email ${p.displayName || email}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                    </a>
                  )}
                </div>
              )
            })}
          </div>
          {hasMore && (
            <button className="feed-directory-more" onClick={() => setExpanded(true)}>
              Show {filteredPeople.length - visiblePeople.length} more
            </button>
          )}
        </>
      )}
    </div>
  )
}

function FeedOrgMapModal(props: {
  people: M365Person[]
  onClose: () => void
  onOpenPerson: (emailOrId: string) => void
}) {
  const departments = React.useMemo(() => {
    const grouped = new Map<string, M365Person[]>()
    for (const person of props.people) {
      const key = (person.department || 'Unassigned').trim() || 'Unassigned'
      const list = grouped.get(key) || []
      list.push(person)
      grouped.set(key, list)
    }
    return Array.from(grouped.entries())
      .map(([name, people]) => ({
        name,
        people: people.sort((a, b) => (a.displayName || '').localeCompare(b.displayName || '')),
      }))
      .sort((a, b) => b.people.length - a.people.length || a.name.localeCompare(b.name))
  }, [props.people])

  function openPerson(person: M365Person) {
    const email = person.mail || person.userPrincipalName || person.id
    props.onClose()
    props.onOpenPerson(email)
  }

  return (
    <div className="feed-org-modal-backdrop" role="dialog" aria-modal="true" aria-label="Company map" onMouseDown={props.onClose}>
      <div className="feed-org-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="feed-org-modal-head">
          <div>
            <div className="feed-org-eyebrow">Company map</div>
            <h2>People by team</h2>
            <p>{props.people.length} teammates across {departments.length} departments. Click anyone to open their profile.</p>
          </div>
          <button type="button" className="feed-org-close" onClick={props.onClose} aria-label="Close company map">&times;</button>
        </div>
        {departments.length === 0 ? (
          <div className="feed-side-empty">No team data is available yet.</div>
        ) : (
          <div className="feed-org-grid">
            {departments.map(dept => (
              <section key={dept.name} className="feed-org-section">
                <div className="feed-org-section-head">
                  <span>{dept.name}</span>
                  <strong>{dept.people.length}</strong>
                </div>
                <div className="feed-org-people">
                  {dept.people.slice(0, 10).map(person => {
                    const email = person.mail || person.userPrincipalName || person.id
                    return (
                      <button key={person.id || email} type="button" className="feed-org-person" onClick={() => openPerson(person)}>
                        <PersonAvatar person={{ id: person.id || email, displayName: person.displayName, photoUrl: person.photoUrl }} size={28} />
                        <span>
                          <span>{person.displayName || email}</span>
                          <small>{person.jobTitle || person.officeLocation || email}</small>
                        </span>
                      </button>
                    )
                  })}
                  {dept.people.length > 10 && (
                    <div className="feed-org-more">+{dept.people.length - 10} more in {dept.name}</div>
                  )}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function FeedLeftRail(props: {
  currentUser: { email: string; name: string; photoUrl?: string | null }
  postCount: number
  peopleCount: number
  departmentCount: number
  celebrationCount: number
  celebrations: FeedCelebrations | null
  teamLoading: boolean
  onCompose: () => void
  onOpenOrgMap: () => void
  onOpenPerson: (emailOrId: string) => void
  onTop: () => void
}) {
  const withCurrentUserPhoto = React.useCallback((people: FeedCelebrationPerson[] | undefined) => {
    const photoUrl = props.currentUser.photoUrl
    const currentEmail = props.currentUser.email.toLowerCase()
    return (people || []).map(p => (
      photoUrl && p.email?.toLowerCase() === currentEmail
        ? { ...p, photoUrl }
        : p
    ))
  }, [props.currentUser.email, props.currentUser.photoUrl])

  return (
    <aside className="feed-left-rail" aria-label="Feed shortcuts">
      <button type="button" className="feed-left-profile" onClick={() => props.onOpenPerson(props.currentUser.email)}>
        <PersonAvatar person={{ id: props.currentUser.email, displayName: props.currentUser.name, photoUrl: props.currentUser.photoUrl || undefined }} size={36} />
        <span>
          <strong>{props.currentUser.name || props.currentUser.email}</strong>
          <small>View profile</small>
        </span>
      </button>

      <nav className="feed-left-card" aria-label="Feed navigation">
        <button type="button" className="feed-left-link active" onClick={props.onTop}>
          <span className="feed-left-glyph feed-left-glyph-blue">N</span>
          <span>News feed</span>
          <strong>{props.postCount}</strong>
        </button>
        <button type="button" className="feed-left-link" onClick={props.onOpenOrgMap}>
          <span className="feed-left-glyph feed-left-glyph-green">O</span>
          <span>Org map</span>
          <strong>{props.teamLoading ? '...' : props.departmentCount}</strong>
        </button>
        <button type="button" className="feed-left-link" onClick={props.onOpenOrgMap}>
          <span className="feed-left-glyph feed-left-glyph-gold">P</span>
          <span>People</span>
          <strong>{props.teamLoading ? '...' : props.peopleCount}</strong>
        </button>
        <button type="button" className="feed-left-link" onClick={props.onCompose}>
          <span className="feed-left-glyph feed-left-glyph-slate">+</span>
          <span>New post</span>
        </button>
      </nav>

      <div className="feed-left-card feed-left-summary">
        <div>
          <span>Today at BotPros</span>
          <strong>{props.celebrationCount}</strong>
        </div>
        <p>Birthdays, anniversaries, new hires, and kudos stay one click away.</p>
      </div>

      <div className="feed-left-celebrations">
        <FeedSidebarWidget
          title="Birthdays this week"
          tone="birthday"
          icon={<span className="feed-side-icon" aria-hidden="true">{'\u{1F382}'}</span>}
          people={withCurrentUserPhoto(props.celebrations?.birthdays)}
          emptyText="No birthdays coming up."
          meta={(p) => p.isToday ? 'Today' : (p.daysUntil === 1 ? 'Tomorrow' : `${p.daysUntil} days`)}
          onOpenPerson={props.onOpenPerson}
          showConfettiOnToday
        />
        <FeedSidebarWidget
          title="Work anniversaries"
          tone="anniversary"
          icon={<span className="feed-side-icon" aria-hidden="true">{'\u{1F389}'}</span>}
          people={withCurrentUserPhoto(props.celebrations?.anniversaries)}
          emptyText="No anniversaries this week."
          meta={(p) => `${p.years} year${p.years === 1 ? '' : 's'} \u00B7 ${p.isToday ? 'Today' : (p.daysUntil === 1 ? 'Tomorrow' : `${p.daysUntil}d`)}`}
          onOpenPerson={props.onOpenPerson}
          showConfettiOnToday
        />
        <FeedSidebarWidget
          title="New hires"
          tone="new-hire"
          icon={<span className="feed-side-icon" aria-hidden="true">{'\u{1F195}'}</span>}
          people={withCurrentUserPhoto(props.celebrations?.newHires)}
          emptyText="No recent new hires."
          meta={(p) => p.jobTitle || 'Joined recently'}
          onOpenPerson={props.onOpenPerson}
        />
        <FeedSidebarWidget
          title="Top kudos this month"
          tone="kudos"
          icon={<span className="feed-side-icon" aria-hidden="true">{'\u{1F44F}'}</span>}
          people={withCurrentUserPhoto(props.celebrations?.topKudos)}
          emptyText="No kudos yet - be the first!"
          meta={(p) => `${p.count || 0} kudos`}
          onOpenPerson={props.onOpenPerson}
        />
      </div>
    </aside>
  )
}

function FeedHubView(props: {
  currentUser: { email: string; name: string; role: string; photoUrl?: string | null }
  onOpenPerson: (emailOrId: string) => void
}) {
  const [posts, setPosts] = React.useState<FeedPost[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [nextCursor, setNextCursor] = React.useState<string | null>(null)
  const [loadingMore, setLoadingMore] = React.useState(false)
  const [team, setTeam] = React.useState<M365Person[]>([])
  const [teamLoading, setTeamLoading] = React.useState(true)
  const [celebrations, setCelebrations] = React.useState<FeedCelebrations | null>(null)
  const [lightbox, setLightbox] = React.useState<string | null>(null)
  const [composerOpenMobile, setComposerOpenMobile] = React.useState(false)
  const [refreshedAt, setRefreshedAt] = React.useState<number>(Date.now())
  const [orgMapOpen, setOrgMapOpen] = React.useState(false)
  // Deep-link intent (#/feed?kudos=<email> | #/feed?compose=1) from the
  // profile hero — parsed on mount/hashchange, then the query is scrubbed so
  // refresh/back don't re-fire it. See src/lib/feed/feedIntent.ts.
  const [feedIntent, setFeedIntent] = React.useState<{ kudos: string | null; compose: boolean; nonce: number } | null>(null)

  const isAdmin = props.currentUser.role === 'admin' || props.currentUser.role === 'Super Admin'

  const fetchFeed = React.useCallback(async (cursor: string | null = null) => {
    if (cursor) setLoadingMore(true)
    else setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ limit: '20' })
      if (cursor) params.set('before', cursor)
      const resp = await fetch(`/api/social/feed?${params}`, { credentials: 'include' })
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({} as any))
        throw new Error(j.error || `HTTP ${resp.status}`)
      }
      const data = await resp.json() as { posts: FeedPost[]; nextCursor: string | null }
      if (cursor) {
        setPosts(prev => [...prev, ...data.posts])
      } else {
        setPosts(data.posts)
        setRefreshedAt(Date.now())
      }
      setNextCursor(data.nextCursor)
    } catch (e: any) {
      setError(e?.message || 'Failed to load feed')
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }, [])

  React.useEffect(() => { void fetchFeed(null) }, [fetchFeed])

  React.useEffect(() => {
    setTeamLoading(true)
    // primaryOnly=1 scopes the feed directory to BotPros staff (drops the
    // sister company / external accounts that share the M365 tenant).
    fetch('/api/crm/team?limit=300&primaryOnly=1', { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then((d: any) => { if (d?.people) setTeam(d.people) })
      .catch(() => { /* noop */ })
      .finally(() => setTeamLoading(false))
    fetch('/api/social/celebrations', { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then((d: any) => { if (d) setCelebrations(d) })
      .catch(() => { /* noop */ })
  }, [])

  React.useEffect(() => {
    const consume = () => {
      const intent = parseFeedIntent(window.location.hash)
      if (!intent.kudos && !intent.compose) return
      clearHashQuery()
      setFeedIntent({ ...intent, nonce: Date.now() })
    }
    consume()
    window.addEventListener('hashchange', consume)
    return () => window.removeEventListener('hashchange', consume)
  }, [])

  // Resolve the kudos target against the roster once it has loaded so the
  // chip carries the person's real name + photo; unknown emails still get a
  // minimal person so the kudos posts against the right address.
  const kudosPrefill = React.useMemo(() => {
    if (!feedIntent?.kudos || teamLoading) return null
    const em = feedIntent.kudos.toLowerCase()
    const person: M365Person = team.find(p => (p.mail || p.userPrincipalName || '').toLowerCase() === em)
      || { id: feedIntent.kudos, displayName: feedIntent.kudos.split('@')[0].replace(/[._]/g, ' '), mail: feedIntent.kudos }
    return { person, nonce: feedIntent.nonce }
  }, [feedIntent, team, teamLoading])

  // In the mobile layout the inline composer is CSS-hidden — surface the
  // overlay composer instead. On desktop a plain compose intent just focuses.
  React.useEffect(() => {
    if (!feedIntent) return
    const inline = document.querySelector<HTMLElement>('.feed-layout > .feed-main > .feed-composer')
    const inlineHidden = !inline || inline.offsetParent === null
    if (inlineHidden) { setComposerOpenMobile(true); return }
    if (feedIntent.compose && !feedIntent.kudos) focusComposer()
  }, [feedIntent])

  React.useEffect(() => {
    return startManagedInterval({
      name: 'social-feed-refresh',
      intervalMs: 45_000,
      run: async () => {
        try {
          const resp = await fetch('/api/social/feed?limit=5', { credentials: 'include' })
          if (!resp.ok) return
          const data = await resp.json() as { posts: FeedPost[] }
          setPosts(prev => {
            const seen = new Set(prev.map(p => p.id))
            const fresh = data.posts.filter(p => !seen.has(p.id))
            if (fresh.length === 0) return prev
            // Cap at 200 so a long-open dashboard doesn't grow this list
            // unboundedly (each FeedCard is non-trivial DOM).
            return [...fresh, ...prev].slice(0, 200)
          })
        } catch { /* noop */ }
      },
    })
  }, [])

  function handleNewPost(post: FeedPost) {
    setPosts(prev => [post, ...prev])
    setComposerOpenMobile(false)
  }

  async function handleReact(postId: number, emoji: string) {
    const actor = { email: props.currentUser.email, name: props.currentUser.name || 'You' }
    setPosts(prev => prev.map(p => {
      if (p.id !== postId) return p
      return { ...p, reactions: applySingleFeedReaction(p.reactions, emoji, actor) }
    }))
    try {
      await fetch(`/api/social/feed/${postId}/react`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emoji }),
      })
    } catch { /* noop */ }
  }

  async function handleAck(postId: number) {
    try {
      const resp = await fetch(`/api/social/feed/${postId}/ack`, { method: 'POST', credentials: 'include' })
      if (resp.ok) {
        setPosts(prev => prev.map(p => p.id === postId ? { ...p, acknowledged: true } : p))
      }
    } catch { /* noop */ }
  }

  function handleDeleted(postId: number) {
    setPosts(prev => prev.filter(p => p.id !== postId))
  }

  function handleEdited(updated: FeedPost) {
    setPosts(prev => prev.map(p => p.id === updated.id ? updated : p))
  }

  const pinnedPosts = posts.filter(p => p.pinned)
  const regularPosts = posts.filter(p => !p.pinned)
  const departmentCount = React.useMemo(() => new Set(team.map(p => (p.department || '').trim()).filter(Boolean)).size, [team])
  const celebrationCount = (celebrations?.birthdays?.length || 0) + (celebrations?.anniversaries?.length || 0) + (celebrations?.newHires?.length || 0) + (celebrations?.topKudos?.length || 0)
  const teamWithCurrentPhoto = React.useMemo(() => {
    const photoUrl = props.currentUser.photoUrl
    if (!photoUrl) return team
    const currentEmail = props.currentUser.email.toLowerCase()
    return team.map(p => {
      const email = (p.mail || p.userPrincipalName || p.id || '').toLowerCase()
      return email === currentEmail ? { ...p, photoUrl } : p
    })
  }, [props.currentUser.email, props.currentUser.photoUrl, team])

  function focusComposer() {
    const composer = document.querySelector<HTMLTextAreaElement>('.feed-composer-textarea')
    if (!composer) return
    composer.scrollIntoView({ block: 'center', behavior: 'smooth' })
    composer.focus()
  }

  function scrollFeedTop() {
    document.querySelector<HTMLElement>('.feed-hub')?.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const icon = (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
  )
  const subtitle = loading && posts.length === 0 ? 'Loading feed\u2026' : `${posts.length} post${posts.length === 1 ? '' : 's'} \u00B7 Updated ${feedRelativeTime(new Date(refreshedAt).toISOString())}`

  return (
    <div className="bp-hub feed-hub">
      <HubHeader
        icon={icon}
        title="Feed"
        subtitle={subtitle}
        className="feed-glass-header"
        onRefresh={() => void fetchFeed(null)}
        refreshing={loading}
        primaryAction={(
          <button type="button" className="feed-header-compose-btn" onClick={focusComposer}>
            New post
          </button>
        )}
        right={(
          <div className="feed-header-metrics">
            <button type="button" className="feed-header-pill feed-header-pill-action" onClick={() => setOrgMapOpen(true)}>
              Company map
            </button>
            <span className="feed-header-pill"><strong>{teamLoading ? '...' : team.length}</strong> people</span>
            {!teamLoading && departmentCount > 0 && <span className="feed-header-pill"><strong>{departmentCount}</strong> {departmentCount === 1 ? 'department' : 'departments'}</span>}
            {pinnedPosts.length > 0 && <span className="feed-header-pill"><strong>{pinnedPosts.length}</strong> pinned</span>}
          </div>
        )}
      />
      <div className="feed-layout">
        <FeedLeftRail
          currentUser={{ email: props.currentUser.email, name: props.currentUser.name, photoUrl: props.currentUser.photoUrl || undefined }}
          postCount={posts.length}
          peopleCount={team.length}
          departmentCount={departmentCount}
          celebrationCount={celebrationCount}
          celebrations={celebrations}
          teamLoading={teamLoading}
          onCompose={focusComposer}
          onOpenOrgMap={() => setOrgMapOpen(true)}
          onOpenPerson={props.onOpenPerson}
          onTop={scrollFeedTop}
        />

        <div className="feed-main">
          <FeedComposer
            currentUser={{ email: props.currentUser.email, name: props.currentUser.name, photoUrl: props.currentUser.photoUrl || undefined }}
            isAdmin={isAdmin}
            team={teamWithCurrentPhoto}
            onPosted={handleNewPost}
            kudosPrefill={kudosPrefill}
          />

          {error && (
            <div className="bp-hub-banner bp-hub-banner-error" style={{ margin: '12px 0' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              {error}
            </div>
          )}

          {loading && posts.length === 0 ? (
            <div className="feed-list">
              {Array.from({ length: 3 }).map((_, i) => <FeedPostSkeleton key={i} />)}
            </div>
          ) : posts.length === 0 ? (
            <div className="feed-empty">
              <div className="feed-empty-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
              </div>
              <h3 className="feed-empty-title">The feed is quiet.</h3>
              <p className="feed-empty-text">{'Be the first to kick things off \u2014 share a job site photo, give someone kudos, or just say hi.'}</p>
            </div>
          ) : (
            <div className="feed-list">
              {pinnedPosts.map(p => (
                <FeedPostCard
                  key={`pinned-${p.id}`}
                  post={p}
                  currentUserEmail={props.currentUser.email}
                  currentUserPhotoUrl={props.currentUser.photoUrl || undefined}
                  isAdmin={isAdmin}
                  onReact={handleReact}
                  onAck={handleAck}
                  onOpenPerson={(email) => props.onOpenPerson(email)}
                  onPreviewImage={setLightbox}
                  onDeleted={handleDeleted}
                  onEdited={handleEdited}
                />
              ))}
              {regularPosts.map(p => (
                <FeedPostCard
                  key={p.id}
                  post={p}
                  currentUserEmail={props.currentUser.email}
                  currentUserPhotoUrl={props.currentUser.photoUrl || undefined}
                  isAdmin={isAdmin}
                  onReact={handleReact}
                  onAck={handleAck}
                  onOpenPerson={(email) => props.onOpenPerson(email)}
                  onPreviewImage={setLightbox}
                  onDeleted={handleDeleted}
                  onEdited={handleEdited}
                />
              ))}
              {nextCursor && (
                <div className="feed-load-more">
                  <button className="feed-load-more-btn" onClick={() => void fetchFeed(nextCursor)} disabled={loadingMore}>
                    {loadingMore ? 'Loading\u2026' : 'Load more'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <aside className="feed-side">
          <FeedDirectoryPanel
            people={teamWithCurrentPhoto}
            loading={teamLoading}
            onOpenPerson={props.onOpenPerson}
            onOpenOrgMap={() => setOrgMapOpen(true)}
          />
        </aside>
      </div>

      <button className="feed-fab" onClick={() => setComposerOpenMobile(true)} aria-label="New post">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
      </button>
      {composerOpenMobile && (
        <div className="feed-composer-mobile-overlay" onClick={() => setComposerOpenMobile(false)}>
          <div className="feed-composer-mobile" onClick={(e) => e.stopPropagation()}>
            <div className="feed-composer-mobile-head">
              <span>New post</span>
              <button onClick={() => setComposerOpenMobile(false)} aria-label="Close">&times;</button>
            </div>
            <FeedComposer
              currentUser={{ email: props.currentUser.email, name: props.currentUser.name, photoUrl: props.currentUser.photoUrl || undefined }}
              isAdmin={isAdmin}
              team={teamWithCurrentPhoto}
              onPosted={handleNewPost}
              kudosPrefill={kudosPrefill}
            />
          </div>
        </div>
      )}

      <FeedLightbox src={lightbox} onClose={() => setLightbox(null)} />
      {orgMapOpen && (
        <FeedOrgMapModal
          people={teamWithCurrentPhoto}
          onClose={() => setOrgMapOpen(false)}
          onOpenPerson={props.onOpenPerson}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Profile Hub (#/profile/:id)
// ---------------------------------------------------------------------------

type ProfileApiResponse = {
  person: M365Person & { mobilePhone?: string; officeLocation?: string; companyName?: string; accountEnabled?: boolean }
  hrSocial: {
    bio: string | null
    pronouns: string | null
    skills: string[]
    hobbies: string | null
    funFact: string | null
    remotePolicy: string | null
    linkedinUrl: string | null
    socialUpdatedAt: string | null
    startDate: string | null
  } | null
  stats: { tenureDays: number; postsCount: number; kudosReceivedCount: number }
  posts: FeedPost[]
  kudosReceived: FeedPost[]
  activeDeals: Array<{ id: number; title: string; amount: number; stage: string; pipeline_name: string }>
}

function ProfileAboutEditor(props: {
  profile: ProfileApiResponse
  onCancel: () => void
  onSaved: (updated: ProfileApiResponse['hrSocial']) => void
}) {
  const s = props.profile.hrSocial
  const [bio, setBio] = React.useState(s?.bio || '')
  const [pronouns, setPronouns] = React.useState(s?.pronouns || '')
  const [skills, setSkills] = React.useState<string[]>(s?.skills || [])
  const [skillInput, setSkillInput] = React.useState('')
  const [hobbies, setHobbies] = React.useState(s?.hobbies || '')
  const [funFact, setFunFact] = React.useState(s?.funFact || '')
  const [remotePolicy, setRemotePolicy] = React.useState(s?.remotePolicy || '')
  const [linkedinUrl, setLinkedinUrl] = React.useState(s?.linkedinUrl || '')
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  function addSkill() {
    const v = skillInput.trim()
    if (!v) return
    if (skills.includes(v)) { setSkillInput(''); return }
    setSkills(prev => [...prev, v])
    setSkillInput('')
  }

  async function save() {
    setSaving(true)
    setError(null)
    try {
      const resp = await fetch('/api/social/profile/me', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bio, pronouns, skills, hobbies, funFact, remotePolicy, linkedinUrl,
        }),
      })
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({} as any))
        throw new Error(j.error || `HTTP ${resp.status}`)
      }
      const data = await resp.json() as { hrSocial: ProfileApiResponse['hrSocial'] }
      props.onSaved(data.hrSocial)
      props.onCancel()
    } catch (e: any) {
      setError(e?.message || 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="profile-edit-inline">
      <div className="profile-edit-head">
        <h3>Edit profile</h3>
      </div>
      <div className="profile-edit-body">
          <div className="profile-edit-field">
            <label>Bio</label>
            <textarea data-tour-id="profile-bio" value={bio} onChange={(e) => setBio(e.target.value)} rows={3} placeholder="Tell your teammates a bit about yourself..." />
          </div>
          <div className="profile-edit-grid">
            <div className="profile-edit-field">
              <label>Pronouns</label>
              <input value={pronouns} onChange={(e) => setPronouns(e.target.value)} placeholder="she/her, he/him, they/them" />
            </div>
            <div className="profile-edit-field">
              <label>Remote policy</label>
              <div className="profile-edit-radios">
                {[
                  { k: 'office', l: 'Office' }, { k: 'remote', l: 'Remote' }, { k: 'hybrid', l: 'Hybrid' },
                ].map(o => (
                  <label key={o.k} className={`profile-edit-radio${remotePolicy === o.k ? ' active' : ''}`}>
                    <input type="radio" checked={remotePolicy === o.k} onChange={() => setRemotePolicy(o.k)} />
                    <span>{o.l}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="profile-edit-field">
            <label>Skills / Ask me about</label>
            <div className="profile-edit-skills">
              {skills.map(s => (
                <span key={s} className="profile-edit-skill">
                  {s}
                  <button onClick={() => setSkills(prev => prev.filter(x => x !== s))} aria-label="Remove">&times;</button>
                </span>
              ))}
              <input
                value={skillInput}
                onChange={(e) => setSkillInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addSkill() }
                }}
                placeholder={skills.length === 0 ? 'Add a skill and press Enter' : 'Add another\u2026'}
              />
            </div>
          </div>
          <div className="profile-edit-field">
            <label>Hobbies</label>
            <input value={hobbies} onChange={(e) => setHobbies(e.target.value)} placeholder="Reading, hiking, cooking..." />
          </div>
          <div className="profile-edit-field">
            <label>Fun fact</label>
            <input value={funFact} onChange={(e) => setFunFact(e.target.value)} placeholder="Something surprising about you..." />
          </div>
          <div className="profile-edit-field">
            <label>LinkedIn URL</label>
            <input value={linkedinUrl} onChange={(e) => setLinkedinUrl(e.target.value)} placeholder="https://linkedin.com/in/..." />
          </div>
        {error && <div className="profile-edit-error">{error}</div>}
      </div>
      <div className="profile-edit-foot">
        <button className="bp-btn bp-btn-secondary" onClick={props.onCancel}>Cancel</button>
        <button className="bp-btn bp-btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving\u2026' : 'Save'}</button>
      </div>
    </div>
  )
}

function ProfileHubView(props: {
  profileId: string
  currentUserEmail: string
  onOpenPerson: (emailOrId: string) => void
  onBack: () => void
  // wave-2: admin gate for the "View HR Record" button. When true the
  // button shows on every profile and opens the HR Center deep-link.
  isAdmin?: boolean
  // Called when the admin clicks "View HR Record →". Router lifts the
  // navigation so this child stays presentational.
  onOpenHrRecord?: (employeeId: string) => void
  // Called from the HR Record tab's 'Sign now' button on an unsigned
  // document_assignments row. Parent opens the in-CRM sign modal.
  onSignAssignment?: (assignmentId: number) => void
  // Fired when the *self* avatar's photo is changed via the camera overlay,
  // so the App-level header avatar (prefsPhoto) refreshes in lock-step.
  onMyPhotoChanged?: (photoUrl: string) => void
}) {
  const [data, setData] = React.useState<ProfileApiResponse | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [tab, setTab] = React.useState<'about' | 'feed' | 'kudos' | 'work' | 'hrRecord'>('about')
  const [editOpen, setEditOpen] = React.useState(false)
  const [lightbox, setLightbox] = React.useState<string | null>(null)
  // Just-picked profile photo awaiting crop/zoom in PhotoCropModal; the
  // upload only happens after the user confirms the crop.
  const [photoCropFile, setPhotoCropFile] = React.useState<File | null>(null)

  const fetchProfile = React.useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const resp = await fetch(`/api/social/profile/${encodeURIComponent(props.profileId)}`, { credentials: 'include' })
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({} as any))
        throw new Error(j.error || `HTTP ${resp.status}`)
      }
      const p = await resp.json() as ProfileApiResponse
      setData(p)
    } catch (e: any) {
      setError(e?.message || 'Failed to load profile')
    } finally {
      setLoading(false)
    }
  }, [props.profileId])

  React.useEffect(() => { void fetchProfile() }, [fetchProfile])

  // Graph's jobTitle/department are frequently null for BotPros users (no
  // Entra HR sync — see enrichTeamWithDivision in the worker), which left the
  // hero identity-less. The team roster already merges the hr_profiles
  // department/name server-side (and is memory-cached there), so borrow it as
  // a per-field fallback. Best-effort: the profile renders fine without it.
  const [teamRoster, setTeamRoster] = React.useState<M365Person[] | null>(null)
  React.useEffect(() => {
    let cancelled = false
    fetch('/api/crm/team?limit=300&primaryOnly=1', { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then((d: any) => { if (!cancelled && d?.people) setTeamRoster(d.people) })
      .catch(() => { /* non-fatal — hero falls back to the payload fields */ })
    return () => { cancelled = true }
  }, [])
  const rosterMatch = React.useMemo(() => {
    if (!teamRoster) return null
    const em = (data?.person?.mail || data?.person?.userPrincipalName || props.profileId || '').toLowerCase()
    if (!em || !em.includes('@')) return null
    return teamRoster.find(p => (p.mail || p.userPrincipalName || '').toLowerCase() === em) || null
  }, [teamRoster, data, props.profileId])

  const isMe = (data?.person?.mail || '').toLowerCase() === props.currentUserEmail.toLowerCase()

  async function handleReact(postId: number, emoji: string) {
    const actor = {
      email: props.currentUserEmail,
      name: props.currentUserEmail.split('@')[0].replace(/[._]/g, ' '),
    }
    setData(prev => {
      if (!prev) return prev
      const update = (list: FeedPost[]) => list.map(p => {
        if (p.id !== postId) return p
        return { ...p, reactions: applySingleFeedReaction(p.reactions, emoji, actor) }
      })
      return { ...prev, posts: update(prev.posts), kudosReceived: update(prev.kudosReceived) }
    })
    try {
      await fetch(`/api/social/feed/${postId}/react`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emoji }),
      })
    } catch { /* noop */ }
  }

  function handleDeleted(postId: number) {
    setData(prev => prev ? { ...prev, posts: prev.posts.filter(p => p.id !== postId) } : prev)
  }

  function handleEdited(updated: FeedPost) {
    setData(prev => prev ? {
      ...prev,
      posts: prev.posts.map(p => p.id === updated.id ? updated : p),
      kudosReceived: prev.kudosReceived.map(p => p.id === updated.id ? updated : p),
    } : prev)
  }

  if (loading && !data) {
    return (
      <div className="bp-hub profile-hub">
        <div className="profile-skeleton">
          <div className="bp-shimmer" style={{ width: 120, height: 120, borderRadius: '50%' }} />
          <div className="bp-shimmer bp-skeleton-line" style={{ width: '30%' }} />
          <div className="bp-shimmer bp-skeleton-line" style={{ width: '20%' }} />
        </div>
      </div>
    )
  }
  if (error || !data) {
    return (
      <div className="bp-hub profile-hub">
        <div className="profile-error">
          <div>{error || 'Profile not found'}</div>
          <button className="bp-btn bp-btn-secondary" onClick={props.onBack}>Go back</button>
        </div>
      </div>
    )
  }

  const { person, hrSocial, stats, posts, kudosReceived, activeDeals } = data
  // Per-field identity fallback (hero + Employment card): payload first, then
  // the roster row (which carries the hr_profiles department merge).
  const jobTitle = person.jobTitle || rosterMatch?.jobTitle || null
  const department = person.department || rosterMatch?.department || null
  const division = person.division || rosterMatch?.division || null
  // Compact activity preview for the About tab — latest 3 across their own
  // posts + kudos they received (both already in the profile payload).
  const recentActivity = (() => {
    const seen = new Set<number>()
    const merged: Array<{ post: FeedPost; kind: 'post' | 'kudos' }> = []
    for (const p of posts) { if (!seen.has(p.id)) { seen.add(p.id); merged.push({ post: p, kind: 'post' }) } }
    for (const p of kudosReceived) { if (!seen.has(p.id)) { seen.add(p.id); merged.push({ post: p, kind: 'kudos' }) } }
    merged.sort((a, b) => new Date(b.post.createdAt).getTime() - new Date(a.post.createdAt).getTime())
    return merged.slice(0, 3)
  })()
  const kudosByCategory: Record<string, FeedPost[]> = {}
  for (const p of kudosReceived) {
    const cat = p.metadata?.category || 'other'
    if (!kudosByCategory[cat]) kudosByCategory[cat] = []
    kudosByCategory[cat].push(p)
  }
  const tenureLabel = (() => {
    const days = stats.tenureDays
    if (!days) return null
    const years = Math.floor(days / 365)
    const months = Math.floor((days % 365) / 30)
    if (years >= 1) return `${years} year${years === 1 ? '' : 's'}${months > 0 && years < 2 ? ` ${months} months` : ''}`
    if (months >= 1) return `${months} month${months === 1 ? '' : 's'}`
    return `${days} day${days === 1 ? '' : 's'}`
  })()
  const startYear = hrSocial?.startDate ? new Date(hrSocial.startDate).getFullYear() : null

  return (
    <div className="bp-hub profile-hub">
      {/* Sticky frosted Back bar — the hero + tabs scroll beneath it (shared
          recipe + traps: App.css glass-header region; guarded by
          src/lib/shell/glassHeader). The hub root itself is the scroller. */}
      <div className="profile-topbar glass-header">
        <button className="profile-back-btn" onClick={props.onBack}>
          <svg className="pf-ic" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12,19 5,12 12,5"/></svg>
          <span>Back</span>
        </button>
      </div>
      <div className="profile-hero">
        <div className="profile-hero-avatar" style={{ position: 'relative' }}>
          {/* Own profile with a photo → clicking it reopens the crop/zoom
              modal on the CURRENT image (fetched back into a File). The
              remove (×) and camera buttons are siblings, not children, so
              their clicks don't land here. */}
          <div
            role={isMe && person.photoUrl ? 'button' : undefined}
            title={isMe && person.photoUrl ? 'Adjust photo' : undefined}
            style={isMe && person.photoUrl ? { cursor: 'pointer' } : undefined}
            onClick={isMe && person.photoUrl ? async () => {
              try {
                setPhotoCropFile(await fileFromImageUrl(person.photoUrl!, 'photo'))
              } catch (err: any) {
                alert(err?.message || 'Could not load the current photo')
              }
            } : undefined}
          >
            <PersonAvatar person={person} size={120} />
          </div>
          {isMe && person.photoUrl && (
            <button
              type="button"
              className="profile-photo-badge"
              title="Remove profile photo"
              onClick={async () => {
                if (!confirm('Remove your profile photo?')) return
                try {
                  const resp = await fetch('/api/hr/profile/photo', { method: 'DELETE', credentials: 'include' })
                  if (!resp.ok) {
                    const j = await resp.json().catch(() => ({} as any))
                    throw new Error(j.error || `HTTP ${resp.status}`)
                  }
                  setData(prev => prev ? { ...prev, person: { ...prev.person, photoUrl: undefined } } : prev)
                  if (props.onMyPhotoChanged) props.onMyPhotoChanged('')
                  void fetchProfile()
                } catch (err: any) {
                  alert(err?.message || 'Failed to remove photo')
                }
              }}
              style={{
                position: 'absolute', top: 4, left: 4, width: 32, height: 32,
                borderRadius: '50%', background: '#fff', border: '2px solid #fff',
                boxShadow: '0 2px 6px rgba(15,23,42,0.15)', display: 'flex',
                alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                color: '#dc2626', zIndex: 2, padding: 0, lineHeight: 1,
              }}
            >
              <svg className="pf-ic" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
            </button>
          )}
          {isMe && (
            <label
              className="profile-photo-badge"
              title="Change profile photo"
              style={{
                position: 'absolute', top: 4, right: 4, width: 32, height: 32,
                borderRadius: '50%', background: '#fff', border: '2px solid #fff',
                boxShadow: '0 2px 6px rgba(15,23,42,0.15)', display: 'flex',
                alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                color: '#475569', zIndex: 2,
              }}
            >
              <svg className="pf-ic" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                <circle cx="12" cy="13" r="4"/>
              </svg>
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.currentTarget.files?.[0]
                  e.currentTarget.value = ''
                  if (!file) return
                  if (file.size > 25 * 1024 * 1024) { alert('Image too large (25MB max)'); return }
                  // Open the crop/zoom modal — upload happens on Save there.
                  setPhotoCropFile(file)
                }}
              />
            </label>
          )}
          {photoCropFile && (
            <PhotoCropModal
              file={photoCropFile}
              title="Adjust profile photo"
              round
              onCancel={() => setPhotoCropFile(null)}
              onSave={async (blob, name) => {
                const fd = new FormData()
                fd.append('file', new File([blob], name, { type: blob.type }))
                const resp = await fetch('/api/hr/profile/photo', { method: 'POST', credentials: 'include', body: fd })
                if (!resp.ok) {
                  const j = await resp.json().catch(() => ({} as any))
                  throw new Error(j.error || `HTTP ${resp.status}`)
                }
                // Apply the returned photoUrl directly + add a unique
                // cache-buster so the <img> definitely reloads. Then refetch
                // in the background to pick up any server-side derived fields.
                const body = await resp.json().catch(() => ({} as any)) as any
                if (body?.photoUrl) {
                  const bust = (body.photoUrl as string) + (String(body.photoUrl).includes('?') ? '&' : '?') + `_t=${Date.now()}`
                  setData(prev => prev ? { ...prev, person: { ...prev.person, photoUrl: bust } } : prev)
                  // Tell App so the header trigger avatar (prefsPhoto) also
                  // refreshes — otherwise it keeps the cached localStorage URL.
                  if (isMe && props.onMyPhotoChanged) props.onMyPhotoChanged(bust)
                }
                void fetchProfile()
                setPhotoCropFile(null)
              }}
            />
          )}
        </div>
        <div className="profile-hero-text">
          <h1 className="profile-name">{person.displayName}</h1>
          {(hrSocial?.pronouns || jobTitle || department) && (
            <div className="profile-sub">
              {hrSocial?.pronouns && <span className="profile-pronouns">{hrSocial.pronouns}</span>}
              {hrSocial?.pronouns && jobTitle && <span className="profile-dot">·</span>}
              {jobTitle && <span className="profile-role">{jobTitle}</span>}
              {department && <>{(hrSocial?.pronouns || jobTitle) && <span className="profile-dot">·</span>}<span>{department}</span></>}
            </div>
          )}
          <div className="profile-actions">
            {/* Give kudos = the primary social action on a COLLEAGUE's profile.
                Deep-links into the feed composer pre-set to kudos mode with
                this person as the target (#/feed?kudos=<email> — parsed by
                parseFeedIntent in FeedHubView, then the param is scrubbed). */}
            {!isMe && person.mail && (
              <a
                className="profile-action profile-action-primary pf-give-kudos"
                href={`#/feed?kudos=${encodeURIComponent(person.mail)}`}
                title={`Give ${(person.givenName || person.displayName || '').split(' ')[0] || 'them'} kudos on the company feed`}
              >
                <svg className="pf-ic" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11"/></svg>
                <span>Give kudos</span>
              </a>
            )}
            {person.mail && (
              <a className="profile-action" href={`mailto:${person.mail}`}>
                <svg className="pf-ic" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                <span>Email</span>
              </a>
            )}
            {person.mobilePhone && (
              <a className="profile-action" href={`tel:${person.mobilePhone}`}>
                <svg className="pf-ic" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.81.36 1.61.7 2.36a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.75.34 1.55.57 2.36.7A2 2 0 0 1 22 16.92z"/></svg>
                <span>Call</span>
              </a>
            )}
            {hrSocial?.linkedinUrl && (
              <a className="profile-action" href={hrSocial.linkedinUrl} target="_blank" rel="noopener noreferrer">
                <svg className="pf-ic" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                <span>LinkedIn</span>
              </a>
            )}
            {(isMe || props.isAdmin) && !editOpen && (
              <button data-tour-id="profile-edit-btn" className="profile-action profile-action-primary" onClick={() => { setTab('about'); setEditOpen(true) }}>
                <svg className="pf-ic" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                <span>Edit profile</span>
              </button>
            )}
          </div>
          {(hrSocial?.remotePolicy || person.officeLocation) && (
            <div className="pf-chips">
              {hrSocial?.remotePolicy && <span className="pf-chip pf-chip-accent">{hrSocial.remotePolicy[0].toUpperCase() + hrSocial.remotePolicy.slice(1)}</span>}
              {person.officeLocation && (
                <span className="pf-chip">
                  <svg className="pf-ic" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  {person.officeLocation}
                </span>
              )}
            </div>
          )}
          {(startYear || tenureLabel) && (
            <div className="profile-tenure">
              {startYear && `Joined ${startYear}`}{startYear && tenureLabel ? ' \u00B7 ' : ''}{tenureLabel && `${tenureLabel} at BotPros`}
            </div>
          )}
        </div>
      </div>

      {/* Stats interlink: POSTS/KUDOS jump to their tabs; an empty own POSTS
          stat invites the first post via the feed-composer deep link. TENURE
          stays static (nothing to open). */}
      <div className="profile-stats">
        {tenureLabel && <div className="profile-stat"><span className="profile-stat-value">{tenureLabel}</span><span className="profile-stat-label">Tenure</span></div>}
        {isMe && stats.postsCount === 0 ? (
          <a className="profile-stat profile-stat-link pf-stat-posts" href="#/feed?compose=1" title="Share your first post on the company feed">
            <span className="profile-stat-value">0</span>
            <span className="profile-stat-label">Posts</span>
            <span className="profile-stat-cta">Share your first post →</span>
          </a>
        ) : (
          <button
            type="button"
            className={`profile-stat profile-stat-link pf-stat-posts${tab === 'feed' ? ' active' : ''}`}
            onClick={() => setTab('feed')}
            title="View posts"
          >
            <span className="profile-stat-value">{stats.postsCount}</span>
            <span className="profile-stat-label">Posts</span>
          </button>
        )}
        <button
          type="button"
          className={`profile-stat profile-stat-link pf-stat-kudos${tab === 'kudos' ? ' active' : ''}`}
          onClick={() => setTab('kudos')}
          title="View kudos received"
        >
          <span className="profile-stat-value">{stats.kudosReceivedCount}</span>
          <span className="profile-stat-label">Kudos received</span>
        </button>
      </div>

      <div className="profile-tabs">
        {((['about', 'feed', 'kudos', 'work', 'hrRecord'] as const)
          .filter(t => t !== 'hrRecord' || isMe || props.isAdmin))
          .map(t => (
            <button key={t} className={`profile-tab${tab === t ? ' active' : ''}`} onClick={() => setTab(t)}>
              {t === 'about' ? 'About' : t === 'feed' ? 'Feed' : t === 'kudos' ? 'Kudos' : t === 'work' ? 'Work' : 'HR Record'}
            </button>
          ))}
      </div>

      <div className="profile-tab-body">
        {tab === 'about' && (
          <div className="profile-about">
            {editOpen && (isMe || props.isAdmin) ? (
              <ProfileAboutEditor
                profile={data}
                onCancel={() => setEditOpen(false)}
                onSaved={(updated) => setData(prev => {
                  if (!prev || !updated) return prev
                  return { ...prev, hrSocial: { ...updated, startDate: prev.hrSocial?.startDate || null } }
                })}
              />
            ) : (
              <div className="pf-overview-grid">
                {/* Left column — stable facts + recent activity (balances the
                    3-card narrative column on the right) */}
                <div className="pf-overview-col">
                  <section className="pf-card">
                    <div className="pf-card-h"><h3>Contact</h3></div>
                    <div className="pf-card-body">
                      {(person.mail || person.mobilePhone || person.officeLocation || hrSocial?.remotePolicy || hrSocial?.linkedinUrl) ? (
                        <dl className="ppl-dl">
                          {person.mail && (<><dt>Email</dt><dd className="pf-trunc"><a className="pf-link" href={`mailto:${person.mail}`} title={person.mail}>{person.mail}</a></dd></>)}
                          {person.mobilePhone && (<><dt>Mobile</dt><dd><a className="pf-link" href={`tel:${person.mobilePhone}`}>{person.mobilePhone}</a></dd></>)}
                          {person.officeLocation && (<><dt>Office</dt><dd>{person.officeLocation}</dd></>)}
                          {hrSocial?.remotePolicy && (<><dt>Work style</dt><dd>{hrSocial.remotePolicy[0].toUpperCase() + hrSocial.remotePolicy.slice(1)}</dd></>)}
                          {hrSocial?.linkedinUrl && (<><dt>LinkedIn</dt><dd><a className="pf-link" href={hrSocial.linkedinUrl} target="_blank" rel="noopener noreferrer">View profile ↗</a></dd></>)}
                        </dl>
                      ) : (
                        <div className="pf-empty">
                          <svg className="pf-ic" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                          <span>No contact details on file.</span>
                        </div>
                      )}
                    </div>
                  </section>
                  <section className="pf-card">
                    <div className="pf-card-h"><h3>Employment</h3></div>
                    <div className="pf-card-body">
                      {(jobTitle || department || startYear || division || person.companyName) ? (
                        <dl className="ppl-dl">
                          {jobTitle && (<><dt>Title</dt><dd>{jobTitle}</dd></>)}
                          {department && (<><dt>Department</dt><dd>{department}</dd></>)}
                          {startYear && (<><dt>Started</dt><dd>{startYear}{tenureLabel ? ` · ${tenureLabel}` : ''}</dd></>)}
                          {division && (<><dt>Division</dt><dd>{division}</dd></>)}
                          {person.companyName && (<><dt>Company</dt><dd>{person.companyName}</dd></>)}
                        </dl>
                      ) : (
                        <div className="pf-empty">
                          <svg className="pf-ic" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
                          <span>No employment details on file.</span>
                        </div>
                      )}
                    </div>
                  </section>
                  {/* Interlink: latest feed activity previewed on the About tab,
                      each row jumping into the matching profile tab, with a
                      one-click path back out to the company feed. Lives in the
                      LEFT column so the two columns land at similar heights
                      (Contact+Employment alone left a dead gap under them). */}
                  <section className="pf-card pf-activity-card">
                    <div className="pf-card-h"><h3>Recent activity</h3></div>
                    <div className="pf-card-body">
                      {recentActivity.length > 0 ? (
                        <>
                          <div className="pf-activity-list">
                            {recentActivity.map(item => {
                              const p = item.post
                              const label = item.kind === 'kudos'
                                ? `Received kudos${p.authorName ? ` from ${p.authorName}` : ''}`
                                : p.postType === 'kudos' ? 'Gave kudos'
                                : p.postType === 'announcement' ? 'Posted an announcement'
                                : p.postType === 'celebration' ? 'Celebrated a milestone'
                                : 'Posted to the feed'
                              const glyph = item.kind === 'kudos' || p.postType === 'kudos' ? '\u{1F44F}'
                                : p.postType === 'announcement' ? '\u{1F4E3}'
                                : p.postType === 'celebration' ? '\u{1F389}'
                                : '\u{1F4AC}'
                              const plain = p.body.replace(/\*\*?/g, '').replace(/\s+/g, ' ').trim()
                              const snippet = plain
                                ? (plain.length > 90 ? `${plain.slice(0, 90)}…` : plain)
                                : (p.mediaUrls.length > 0 ? `Shared ${p.mediaUrls.length} photo${p.mediaUrls.length === 1 ? '' : 's'}` : '')
                              return (
                                <button
                                  key={`${item.kind}-${p.id}`}
                                  type="button"
                                  className="pf-activity-row"
                                  onClick={() => setTab(item.kind === 'kudos' ? 'kudos' : 'feed')}
                                  title={item.kind === 'kudos' ? 'View in the Kudos tab' : 'View in the Feed tab'}
                                >
                                  <span className="pf-activity-glyph" aria-hidden="true">{glyph}</span>
                                  <span className="pf-activity-text">
                                    <span className="pf-activity-line">{label}</span>
                                    {snippet && <span className="pf-activity-snippet">{snippet}</span>}
                                  </span>
                                  <span className="pf-activity-time">{feedRelativeTime(p.createdAt)}</span>
                                </button>
                              )
                            })}
                          </div>
                          <a className="pf-activity-all" href="#/feed">View all in company feed →</a>
                        </>
                      ) : (
                        isMe
                          ? <a className="pf-empty-cta" href="#/feed?compose=1">Nothing yet — share your first post →</a>
                          : (
                            <div className="pf-empty">
                              <svg className="pf-ic" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
                              <span>No recent activity.</span>
                            </div>
                          )
                      )}
                    </div>
                  </section>
                </div>
                {/* Right column — narrative */}
                <div className="pf-overview-col">
                  <section className="pf-card">
                    <div className="pf-card-h"><h3>About</h3>{(isMe || props.isAdmin) && <button className="pf-card-edit" onClick={() => setEditOpen(true)}>Edit</button>}</div>
                    <div className="pf-card-body">
                      {hrSocial?.bio ? (<p className="pf-bio">{hrSocial.bio}</p>) : (isMe ? <button className="pf-empty-cta" onClick={() => setEditOpen(true)}>Add a bio so colleagues get to know you →</button> : (
                        <div className="pf-empty">
                          <svg className="pf-ic" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                          <span>No bio yet.</span>
                        </div>
                      ))}
                    </div>
                  </section>
                  <section className="pf-card">
                    <div className="pf-card-h"><h3>Skills</h3>{(isMe || props.isAdmin) && <button className="pf-card-edit" onClick={() => setEditOpen(true)}>Edit</button>}</div>
                    <div className="pf-card-body">
                      {hrSocial?.skills && hrSocial.skills.length > 0 ? (<div className="ppl-chiplist">{hrSocial.skills.map(s => <span key={s} className="ppl-chiplist-chip">{s}</span>)}</div>) : (isMe ? <button className="pf-empty-cta" onClick={() => setEditOpen(true)}>Add your skills →</button> : (
                        <div className="pf-empty">
                          <svg className="pf-ic" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.83z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
                          <span>No skills listed.</span>
                        </div>
                      ))}
                    </div>
                  </section>
                  <section className="pf-card">
                    <div className="pf-card-h"><h3>More about me</h3>{(isMe || props.isAdmin) && <button className="pf-card-edit" onClick={() => setEditOpen(true)}>Edit</button>}</div>
                    <div className="pf-card-body">
                      {(hrSocial?.hobbies || hrSocial?.funFact) ? (
                        <dl className="ppl-dl">
                          {hrSocial?.hobbies && (<><dt>Hobbies</dt><dd>{hrSocial.hobbies}</dd></>)}
                          {hrSocial?.funFact && (<><dt>Fun fact</dt><dd>{hrSocial.funFact}</dd></>)}
                        </dl>
                      ) : (isMe ? <button className="pf-empty-cta" onClick={() => setEditOpen(true)}>Add a hobby or fun fact →</button> : (
                        <div className="pf-empty">
                          <svg className="pf-ic" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>
                          <span>Nothing here yet.</span>
                        </div>
                      ))}
                    </div>
                  </section>
                </div>
                {/* Full width — owner-only self-service settings */}
                {isMe && (
                  <div className="pf-overview-full pf-settings">
                    <h3 className="pf-section-title">Your calling settings <span className="pf-section-hint">· only you can see this</span></h3>
                    <div className="pf-calling-cards">
                      <CallingSetupCard />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {tab === 'feed' && (
          <div className="profile-feed">
            {posts.length === 0 ? (
              <div className="bp-empty-state">
                <div className="bp-empty-icon">
                  <svg className="pf-ic" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                </div>
                <h3 className="bp-empty-title">No posts yet</h3>
                <p className="bp-empty-text">{isMe ? 'Updates you share on the company feed will appear here.' : 'When they post to the company feed, it’ll show up here.'}</p>
                {isMe && (
                  <a className="bp-btn bp-btn-primary pf-first-post-cta" href="#/feed?compose=1">
                    Share your first post →
                  </a>
                )}
              </div>
            ) : (
              <div className="feed-list">
                {posts.map(p => (
                  <FeedPostCard
                    key={p.id}
                    post={p}
                    currentUserEmail={props.currentUserEmail}
                    isAdmin={false}
                    onReact={handleReact}
                    onAck={() => { /* ack not supported on profile view */ }}
                    onOpenPerson={props.onOpenPerson}
                    onPreviewImage={setLightbox}
                    onDeleted={handleDeleted}
                    onEdited={handleEdited}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'kudos' && (
          <div className="profile-kudos">
            {kudosReceived.length === 0 ? (
              <div className="bp-empty-state">
                <div className="bp-empty-icon">
                  <svg className="pf-ic" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11"/></svg>
                </div>
                <h3 className="bp-empty-title">No kudos yet</h3>
                <p className="bp-empty-text">{isMe ? 'Recognition from your teammates will collect here.' : 'Kudos they receive will show up here.'}</p>
              </div>
            ) : (
              <>
                <div className="profile-kudos-summary">
                  Received {stats.kudosReceivedCount} kudos this quarter
                </div>
                {Object.entries(kudosByCategory).map(([cat, items]) => {
                  const catMeta = KUDOS_CATEGORIES.find(c => c.key === cat)
                  return (
                    <div key={cat} className="profile-kudos-group">
                      <div className="profile-kudos-group-head">
                        <span className="profile-kudos-group-emoji">{catMeta?.emoji || '\u{1F44F}'}</span>
                        <span>{catMeta?.label || 'Other'}</span>
                        <span className="profile-kudos-group-count">{items.length}</span>
                      </div>
                      <div className="feed-list">
                        {items.map(p => (
                          <FeedPostCard
                            key={p.id}
                            post={p}
                            currentUserEmail={props.currentUserEmail}
                            isAdmin={false}
                            onReact={handleReact}
                            onAck={() => { /* noop */ }}
                            onOpenPerson={props.onOpenPerson}
                            onPreviewImage={setLightbox}
                            onDeleted={() => { /* noop */ }}
                            onEdited={handleEdited}
                          />
                        ))}
                      </div>
                    </div>
                  )
                })}
              </>
            )}
          </div>
        )}

        {tab === 'work' && (
          <div className="profile-work">
            <h3 className="profile-work-h">Active deals ({activeDeals.length})</h3>
            {activeDeals.length === 0 ? (
              <div className="bp-empty-state bp-empty-inline">
                <div className="bp-empty-icon">
                  <svg className="pf-ic" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
                </div>
                <h3 className="bp-empty-title">No active deals</h3>
                <p className="bp-empty-text">{isMe ? 'Deals you own will appear here while they’re in flight.' : 'Open deals they own will appear here.'}</p>
              </div>
            ) : (
              <div className="profile-work-list">
                {activeDeals.map(d => (
                  <a key={d.id} className="profile-work-row" href={`#/deals/record/${d.id}`}>
                    <div className="profile-work-row-title">{d.title}</div>
                    <div className="profile-work-row-meta">
                      <span>{d.stage || 'No stage'}</span>
                      {d.pipeline_name && <><span className="profile-dot">·</span><span>{d.pipeline_name}</span></>}
                      {typeof d.amount === 'number' && d.amount > 0 && <><span className="profile-dot">·</span><span>${d.amount.toLocaleString()}</span></>}
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'hrRecord' && (isMe || props.isAdmin) && (
          <ProfileHrRecordTab
            personEmail={person.mail || person.userPrincipalName || ''}
            isAdmin={!!props.isAdmin}
            isMe={isMe}
            onSignAssignment={props.onSignAssignment}
          />
        )}
      </div>

      <FeedLightbox src={lightbox} onClose={() => setLightbox(null)} />
    </div>
  )
}

// HR Record tab on the profile page — read-only, self-service view of the
// caller's own employee record (or any record when an admin is viewing).
// Source of truth: hr_profiles in client-ops-db (NOT employee_cache, which
// is a legacy-shaped cache that does not cover all employees —
// shipping HR Record off employee_cache made Max's tab fail with "no
// profile linked"). We read via /api/hr/profile (self) or
// /api/hr/employees/by-email/:email (admin) and render the camelCase
// shape produced by buildProfileResponse.
//
// Hides 6 sensitive sections per spec: IT Provisioning, Compensation &
// Benefits, Banking & Direct Deposit, Tax & Payroll, Training & Compliance,
// Performance & Reviews. Editability + required uploads (insurance / DL /
// vehicle registration) ship in a follow-up.
const HR_RECORD_SECTIONS = [
  { key: 'personalInfo', label: 'Personal Information' },
  { key: 'emergencyContacts', label: 'Emergency Contacts' },
  { key: 'employmentInfo', label: 'Employment Details' },
  { key: 'compensationInfo', label: 'Compensation & Benefits' },
  { key: 'taxInfo', label: 'Tax & Payroll' },
  { key: 'bankingInfo', label: 'Banking & Direct Deposit' },
  { key: 'educationInfo', label: 'Education & Skills' },
  { key: 'trainingInfo', label: 'Training & Compliance' },
  { key: 'equipmentInfo', label: 'Equipment Assigned' },
  { key: 'licensingInfo', label: 'Vehicle & Licensing' },
  { key: 'dependents', label: 'Dependents & Beneficiaries' },
  { key: 'performanceInfo', label: 'Performance & Reviews' },
  { key: 'onboardingInfo', label: 'Onboarding' },
] as const

// Canonical field set per HR section. Rendered automatically in the edit
// form (even when the section JSON is empty) so admins / employees know
// what info exists to fill in. Fields not in this list still render when
// they already exist in the data.
const HR_SECTION_FIELDS: Record<string, string[]> = {
  employmentInfo: ['startDate', 'hireDate', 'jobTitle', 'employmentType', 'shift', 'workPhone', 'supervisor', 'workLocation'],
  compensationInfo: ['payRate', 'payFrequency', 'salary', 'ptoAccrualPerPeriod', 'ptoBalance', 'sickLeaveBalance', 'bonusEligibility'],
  taxInfo: ['federalFilingStatus', 'federalAllowances', 'federalAdditionalWithholding', 'stateFilingStatus', 'stateAllowances', 'stateAdditionalWithholding', 'w4SignedDate'],
  bankingInfo: ['bankName', 'accountType', 'routingNumber', 'accountNumber', 'depositPercentage', 'secondaryBankName', 'secondaryAccountNumber'],
  trainingInfo: ['oshaCertExpiry', 'firstAidCertExpiry', 'cprCertExpiry', 'forkliftCertExpiry', 'otherCertifications', 'lastSafetyTraining'],
  equipmentInfo: ['laptop', 'monitor', 'phone', 'tablet', 'keyCard', 'uniformSize', 'accessories'],
  licensingInfo: ['driversLicenseNumber', 'driversLicenseState', 'driversLicenseExpiry', 'autoInsuranceCarrier', 'autoInsurancePolicyNumber', 'autoInsuranceExpiry', 'vehicleRegistrationExpiry', 'autoTagNumber'],
  performanceInfo: ['lastReviewDate', 'nextReviewDate', 'lastReviewRating', 'currentGoals', 'developmentPlan'],
  onboardingInfo: ['offerSignedDate', 'handbookSignedDate', 'w4SignedDate', 'i9SignedDate', 'dayOnePlanned', 'dayOneCompleted', 'orientationCompleted'],
}

// Cosmetic only — turn camelCase JSON keys into "Camel Case" labels.
function humanizeKey(k: string): string {
  return k
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
}

// --- Display-layer humanizers (cosmetic only — never written back) -----------

// Duplicate-detection reason codes → human labels. Single source of truth so the
// legacy preview pane and the Related contacts card on the 360 record can't drift.
function reasonLabel(r: string): string {
  switch (r) {
    case 'phone': return 'Same phone'
    case 'email': return 'Same email'
    case 'address': return 'Same address'
    case 'exact_name': return 'Same name'
    case 'similar_name': return 'Similar name'
    default: return humanizeKey(String(r || ''))
  }
}

// match_status raw values → friendly labels. Covers legacy/import provenance codes
// (e.g. 'imported', 'legacy_only') that otherwise render as raw machine strings.
function humanizeMatchStatus(s: string | null | undefined): string {
  const v = String(s || '').trim().toLowerCase()
  switch (v) {
    case '': return '--'
    case 'matched': return 'Matched'
    case 'imported': return 'Imported'
    case 'legacy': return 'Legacy'
    case 'legacy_only': return 'Legacy'
    case 'manual': return 'Manual entry'
    default: return humanizeKey(v)
  }
}

// data_quality raw values → friendly labels.
function humanizeDataQuality(q: string | null | undefined): string {
  const v = String(q || '').trim().toLowerCase()
  switch (v) {
    case '': return '--'
    case 'good': return 'Good'
    case 'fair': return 'Fair'
    case 'poor': return 'Poor'
    case 'junk': return 'Junk'
    case 'duplicate': return 'Duplicate'
    case 'review': return 'Needs review'
    case 'deleted': return 'Deleted'
    default: return humanizeKey(v)
  }
}

// client_type raw values → friendly labels.
function humanizeClientType(t: string | null | undefined): string {
  const v = String(t || '').trim().toLowerCase()
  if (v === 'organization') return 'Organization'
  if (v === 'person') return 'Person'
  return v ? humanizeKey(v) : '--'
}

// Strip obvious data artifacts from a contact's display name: trailing call-log
// suffixes ("- Incoming Call", "— Outgoing Call") and bare-email-as-name. Returns
// the cleaned name, or '' if nothing usable remains (callers fall back to id/email).
function cleanContactName(name: string | null | undefined): string {
  let n = String(name || '').trim()
  if (!n) return ''
  // Drop trailing call-log annotations the dialer appended to the name.
  n = n.replace(/\s*[-–—]\s*(incoming|outgoing|missed)\s+call\s*$/i, '').trim()
  // A bare email used as a name is an artifact — suppress it (let caller fall back).
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(n)) return ''
  return n
}

// Display name for a contact/org record with obvious artifacts stripped. Falls back
// to the raw display name (then '--') so a record never renders blank.
function clientDisplayNameClean(client: Parameters<typeof getClientDisplayName>[0]): string {
  const raw = getClientDisplayName(client)
  return cleanContactName(raw) || raw || '--'
}

function HrSectionBody({ value }: { value: any }) {
  if (value == null) return <p style={{ color: '#94a3b8', fontSize: '0.82rem', margin: 0 }}>Not filled in yet.</p>
  const entries: Array<[string, any]> = []
  const cards: Array<Record<string, any>> = []
  if (Array.isArray(value)) {
    for (const item of value) {
      if (item && typeof item === 'object') cards.push(item as Record<string, any>)
    }
  } else if (typeof value === 'object') {
    for (const [k, v] of Object.entries(value)) {
      if (v == null || v === '') continue
      if (typeof v === 'object' && !Array.isArray(v)) {
        // Flatten nested objects (e.g. address) one level deep
        for (const [nk, nv] of Object.entries(v as Record<string, any>)) {
          if (nv == null || nv === '') continue
          entries.push([`${k}.${nk}`, nv])
        }
      } else {
        entries.push([k, v])
      }
    }
  }
  if (!entries.length && !cards.length) return <p style={{ color: '#94a3b8', fontSize: '0.82rem', margin: 0 }}>Not filled in yet.</p>
  const fmt = (v: any) => {
    if (v == null) return ''
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) return v.split('T')[0]
    if (typeof v === 'object') return JSON.stringify(v)
    return String(v)
  }
  return (
    <>
      {entries.length > 0 && (
        <div className="profile-hr-section-grid">
          {entries.map(([k, v]) => (
            <div key={k} className="profile-hr-field">
              <div className="profile-hr-field-label">{humanizeKey(k.split('.').pop() || k)}</div>
              <div className="profile-hr-field-value">{fmt(v)}</div>
            </div>
          ))}
        </div>
      )}
      {cards.map((card, idx) => (
        <div key={idx} className="profile-hr-section-grid" style={{ marginTop: idx === 0 && entries.length === 0 ? 0 : 10, paddingTop: idx === 0 && entries.length === 0 ? 0 : 10, borderTop: idx === 0 && entries.length === 0 ? 'none' : '1px dashed #e2e8f0' }}>
          {Object.entries(card)
            .filter(([, v]) => v != null && v !== '')
            .map(([k, v]) => (
              <div key={k} className="profile-hr-field">
                <div className="profile-hr-field-label">{humanizeKey(k)}</div>
                <div className="profile-hr-field-value">{fmt(v)}</div>
              </div>
            ))}
        </div>
      ))}
    </>
  )
}

// Inline editor for any flat-object HR section without a hand-written form.
// Renders one row per existing key (label + text input) plus "Add field"
// so admins can add new keys without a schema migration. Nested objects
// flatten to "parent.child" dotted keys for editing convenience.
// Set of section keys whose fields require admin approval when an employee
// tries to change them. Mirrors HR_LOCKED_FIELDS on the server.
const HR_LOCKED_SECTIONS = new Set([
  'employmentInfo', 'compensationInfo', 'taxInfo', 'bankingInfo',
  'trainingInfo', 'performanceInfo', 'onboardingInfo',
])

type ChangeRequest = {
  id: number
  section: string
  field_key: string
  current_value: string | null
  requested_value: string
  reason: string | null
  status: 'pending' | 'approved' | 'denied' | 'cancelled'
  created_at: string
}

function HrGenericSectionEditor({
  value, onChange, defaultKeys = [], sectionKey,
  locked = false, pendingRequests = [], onRequestChange, onCancelRequest,
}: {
  value: any
  onChange: (next: any) => void
  defaultKeys?: string[]
  sectionKey: string
  // When true, the editor renders fields read-only with a 'Request change'
  // affordance instead of an editable input. Used in self-view for sections
  // that require admin approval.
  locked?: boolean
  pendingRequests?: ChangeRequest[]
  onRequestChange?: (fieldKey: string, currentValue: string) => void
  onCancelRequest?: (id: number) => void
}) {
  const obj: Record<string, any> = (value && typeof value === 'object' && !Array.isArray(value)) ? value : {}
  // Flatten one level of nested objects (e.g. address) into "parent.child"
  const rows: Array<{ key: string; val: string }> = []
  const seenKeys = new Set<string>()
  for (const [k, v] of Object.entries(obj)) {
    if (v != null && typeof v === 'object' && !Array.isArray(v)) {
      for (const [nk, nv] of Object.entries(v as Record<string, any>)) {
        const composite = `${k}.${nk}`
        rows.push({ key: composite, val: nv == null ? '' : String(nv) })
        seenKeys.add(composite)
      }
    } else if (!Array.isArray(v)) {
      rows.push({ key: k, val: v == null ? '' : String(v) })
      seenKeys.add(k)
    }
  }
  // Append canonical default fields that aren't already present, so the
  // editor renders pre-defined inputs even when the section is empty.
  for (const dk of defaultKeys) {
    if (!seenKeys.has(dk)) {
      rows.push({ key: dk, val: '' })
      seenKeys.add(dk)
    }
  }
  const setRow = (idx: number, nextKey: string, nextVal: string) => {
    const next: Record<string, any> = {}
    rows.forEach((r, i) => {
      const k = i === idx ? nextKey : r.key
      const val = i === idx ? nextVal : r.val
      if (!k) return
      if (k.includes('.')) {
        const [parent, child] = k.split('.', 2)
        next[parent] = { ...(next[parent] || {}), [child]: val }
      } else {
        next[k] = val
      }
    })
    onChange(next)
  }
  const removeRow = (idx: number) => {
    const next: Record<string, any> = {}
    rows.forEach((r, i) => {
      if (i === idx || !r.key) return
      if (r.key.includes('.')) {
        const [parent, child] = r.key.split('.', 2)
        next[parent] = { ...(next[parent] || {}), [child]: r.val }
      } else {
        next[r.key] = r.val
      }
    })
    onChange(next)
  }
  const addRow = () => {
    onChange({ ...obj, '': '' })
  }
  // Default keys keep their canonical label; custom rows fall back to the
  // humanized key. Empty key = unsaved custom row pending a name.
  const defaultSet = new Set(defaultKeys)
  const labelInputStyle: React.CSSProperties = {
    width: '100%', padding: '6px 2px', border: 'none', borderBottom: '1px solid #cbd5e1',
    borderRadius: 0, fontSize: '0.82rem', background: 'transparent', color: '#1e293b', outline: 'none',
  }
  const labelStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.68rem', fontWeight: 500,
    color: '#64748b', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.04em',
  }
  // Fast-lookup for pending requests by field_key within this section.
  const pendingByField = new Map<string, ChangeRequest>()
  for (const r of pendingRequests) {
    if (r.section === sectionKey && r.status === 'pending') pendingByField.set(r.field_key, r)
  }
  return (
    <div>
      <div className="hr-portal-detail-grid">
        {rows.map((r, idx) => {
          const isCustom = !defaultSet.has(r.key)
          const labelText = r.key ? humanizeKey(r.key.split('.').pop() || r.key) : ''
          const pending = r.key ? pendingByField.get(r.key) : undefined
          // Locked + canonical field + non-empty key → read-only display
          // with no per-field button. A single "Request change" button at
          // the bottom of the section opens a multi-field modal.
          if (locked && !isCustom && r.key) {
            return (
              <div key={idx} style={{ position: 'relative' }}>
                <label style={{ ...labelStyle, gap: 6 }}>
                  {labelText}
                  <svg className="pf-ic" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                </label>
                <div style={{ ...labelInputStyle, color: r.val ? '#1e293b' : '#94a3b8', cursor: 'default' }}>
                  {r.val || '—'}
                </div>
                {pending && (
                  <div style={{ marginTop: 4, padding: '4px 8px', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 4, fontSize: '0.7rem', color: '#92400e', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <strong>Pending →</strong> {pending.requested_value}
                      {pending.reason && <span style={{ opacity: 0.75 }}> · {pending.reason}</span>}
                    </span>
                    {onCancelRequest && (
                      <button
                        type="button"
                        title="Cancel request"
                        onClick={() => onCancelRequest(pending.id)}
                        style={{ background: 'none', border: 'none', color: '#92400e', cursor: 'pointer', fontSize: '0.85rem', lineHeight: 1, padding: 0 }}
                      >&times;</button>
                    )}
                  </div>
                )}
              </div>
            )
          }
          return (
            <div key={idx} style={{ position: 'relative' }}>
              {isCustom ? (
                <input
                  value={r.key}
                  onChange={e => setRow(idx, e.target.value, r.val)}
                  placeholder="Field name"
                  style={{ ...labelInputStyle, fontSize: '0.68rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottomStyle: 'dashed', marginBottom: 3 }}
                />
              ) : (
                <label style={labelStyle}>{labelText}</label>
              )}
              <input
                type="text"
                value={r.val}
                onChange={e => setRow(idx, r.key, e.target.value)}
                style={labelInputStyle}
              />
              {isCustom && (
                <button
                  type="button"
                  onClick={() => removeRow(idx)}
                  title="Remove field"
                  style={{ position: 'absolute', top: 0, right: 0, background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: 2, fontSize: '0.85rem', lineHeight: 1 }}
                >&times;</button>
              )}
              {pending && (
                <div style={{ marginTop: 4, padding: '4px 8px', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 4, fontSize: '0.7rem', color: '#92400e' }}>
                  <strong>Pending →</strong> {pending.requested_value}
                </div>
              )}
            </div>
          )
        })}
      </div>
      <div style={{ padding: '4px 16px 12px', display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
        {locked && onRequestChange ? (
          <button
            type="button"
            onClick={() => {
              // Pass an empty fieldKey — the modal interprets this as a
              // section-wide request and lets the user pick which fields.
              onRequestChange('', '')
            }}
            className="bp-btn bp-btn-primary"
            style={{ fontSize: '0.74rem', padding: '5px 12px' }}
          >Request change</button>
        ) : <span />}
        <button
          type="button"
          onClick={addRow}
          className="bp-btn bp-btn-secondary"
          style={{ fontSize: '0.74rem', padding: '4px 10px' }}
        >+ Add custom field</button>
      </div>
    </div>
  )
}

// Doc-centric section: lists the employee's signed/assigned documents from
// document_assignments. Used for Onboarding (and any other section where
// employees really want to see the actual signed PDF + signed date, not
// a flat date input). View opens the signed PDF in a new tab.
function HrSignedDocumentsList({ assignments, missingTemplates, loading, isMe, onSign }: {
  assignments: any[]
  missingTemplates: any[]
  loading: boolean
  isMe: boolean
  onSign?: (assignmentId: number) => void
}) {
  if (loading) return <p style={{ color: '#94a3b8', fontSize: '0.82rem', margin: 0, padding: '0 16px 12px' }}>Loading documents…</p>
  if ((!assignments || assignments.length === 0) && (!missingTemplates || missingTemplates.length === 0)) {
    return <p style={{ color: '#94a3b8', fontSize: '0.82rem', margin: 0, padding: '0 16px 12px' }}>No documents assigned.</p>
  }
  const fmtDate = (s?: string | null) => {
    if (!s) return '—'
    const d = new Date(s); return isNaN(d.getTime()) ? s : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }
  const viewPdf = async (a: any) => {
    try {
      // Synthetic id 'eod-<n>' = employee_onboarding_documents row (W-4 / I-9 /
      // Handbook signed via the magic-link onboarding flow). Real numeric id =
      // document_assignments row.
      let url: string
      if (typeof a.id === 'string' && a.id.startsWith('eod-')) {
        const eodId = a.id.slice(4)
        url = `/api/hr-admin/onboarding-docs/${encodeURIComponent(eodId)}/pdf`
      } else {
        url = isMe
          ? `/api/hr/my-assignments/${a.id}/pdf`
          : `/api/hr-admin/assignments/${a.id}/pdf`
      }
      const resp = await fetch(url, { credentials: 'include' })
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      const blob = await resp.blob()
      const objUrl = URL.createObjectURL(blob)
      window.open(objUrl, '_blank')
      setTimeout(() => URL.revokeObjectURL(objUrl), 60_000)
    } catch (e: any) {
      alert(e?.message || 'Failed to open document')
    }
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '4px 16px 12px' }}>
      {assignments.map(a => {
        const signed = a.status === 'signed'
        const due = a.due_date && !signed && new Date(a.due_date) < new Date()
        return (
          <div key={`a-${a.id}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff' }}>
            <div style={{ width: 30, height: 30, borderRadius: 6, background: signed ? '#dcfce7' : due ? '#fee2e2' : '#fef3c7', color: signed ? '#15803d' : due ? '#b91c1c' : '#92400e', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: '0.84rem', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.template_title || 'Document'}</div>
              <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 2 }}>
                {signed ? `Signed ${fmtDate(a.signed_at)}` : a.due_date ? `${due ? 'Overdue' : 'Due'} ${fmtDate(a.due_date)}` : 'Pending'}
                {a.category ? ` · ${a.category}` : ''}
              </div>
            </div>
            {/* Sign now appears for unsigned, real-assignment rows when viewing
                your own profile (synthetic 'eod-' rows are already signed). */}
            {!signed && isMe && onSign && typeof a.id === 'number' && (
              <button
                onClick={() => onSign(a.id)}
                className="bp-btn bp-btn-primary"
                style={{ fontSize: '0.74rem', padding: '4px 12px', flexShrink: 0 }}
              >Sign now</button>
            )}
            <button
              onClick={() => viewPdf(a)}
              className="bp-btn bp-btn-secondary"
              style={{ fontSize: '0.74rem', padding: '4px 10px', flexShrink: 0 }}
            >View</button>
          </div>
        )
      })}
      {(missingTemplates || []).map((t: any) => (
        <div key={`t-${t.id}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: '1px dashed #e2e8f0', borderRadius: 8, background: '#f8fafc' }}>
          <div style={{ width: 30, height: 30, borderRadius: 6, background: '#f1f5f9', color: '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: '0.84rem', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title || 'Document'}</div>
            <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 2 }}>
              Not assigned yet{t.category ? ` · ${t.category}` : ''}
            </div>
          </div>
          <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8', padding: '3px 8px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 999, flexShrink: 0 }}>Pending HR</span>
        </div>
      ))}
    </div>
  )
}

function HrSectionView({ label, value }: { label: string; value: any }) {
  // Always render the section header so employees can see what info exists
  // to fill in. HrSectionBody already renders a "Not filled in yet" stub.
  return (
    <div className="profile-hr-section">
      <div className="profile-hr-section-hdr">{label}</div>
      <HrSectionBody value={value} />
    </div>
  )
}

function ProfileHrRecordTab(props: { personEmail: string; isAdmin: boolean; isMe: boolean; onSignAssignment?: (assignmentId: number) => void }) {
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [profile, setProfile] = React.useState<any>(null)
  const [editing, setEditing] = React.useState(false)
  const [draft, setDraft] = React.useState<any>(null)
  const [saving, setSaving] = React.useState(false)
  const [saveErr, setSaveErr] = React.useState<string | null>(null)
  // Signed/assigned documents for this employee — surfaced in the
  // Onboarding section instead of generic text inputs, so employees see
  // their actual signed PDFs with the View button. `missingTemplates`
  // are required-for-onboarding templates not yet assigned, rendered as
  // 'Not assigned' rows.
  const [docAssignments, setDocAssignments] = React.useState<any[]>([])
  const [docMissingTemplates, setDocMissingTemplates] = React.useState<any[]>([])
  const [docsLoading, setDocsLoading] = React.useState(true)
  // Pending change requests for locked fields — surfaced as amber chips
  // beside each affected field in the section editor.
  const [pendingRequests, setPendingRequests] = React.useState<ChangeRequest[]>([])
  const [requestModal, setRequestModal] = React.useState<{ section: string } | null>(null)
  // Section-wide modal: one entry per canonical field in the section,
  // user toggles a checkbox + enters new value + optional per-field reason.
  // State keyed by fieldKey.
  const [requestValues, setRequestValues] = React.useState<Record<string, { selected: boolean; value: string; reason: string }>>({})
  const [requestSubmitting, setRequestSubmitting] = React.useState(false)

  const reloadRequests = React.useCallback(async () => {
    if (!props.isMe) { setPendingRequests([]); return }
    try {
      const r = await fetch('/api/hr/profile/change-requests', { credentials: 'include' })
      if (r.ok) {
        const j = await r.json() as { requests: ChangeRequest[] }
        setPendingRequests((j.requests || []).filter(x => x.status === 'pending'))
      }
    } catch { /* ignore */ }
  }, [props.isMe])
  React.useEffect(() => { void reloadRequests() }, [reloadRequests])

  // Open the section-wide modal. (fieldKey/currentValue are ignored now —
  // kept in the signature so HrGenericSectionEditor stays generic.)
  const openRequestChange = (section: string, _fieldKey: string, _currentValue: string) => {
    setRequestModal({ section })
    setRequestValues({})
  }
  const submitRequest = async () => {
    if (!requestModal) return
    // Collect selected rows with non-empty new values.
    const toSubmit = Object.entries(requestValues)
      .filter(([, v]) => v.selected && v.value.trim())
      .map(([k, v]) => ({ fieldKey: k, requestedValue: v.value.trim(), reason: v.reason.trim() || null }))
    if (toSubmit.length === 0) { alert('Select at least one field + enter a new value.'); return }
    setRequestSubmitting(true)
    const failures: string[] = []
    try {
      for (const r of toSubmit) {
        const resp = await fetch('/api/hr/profile/change-request', {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            section: requestModal.section,
            fieldKey: r.fieldKey,
            currentValue: (profile?.[requestModal.section]?.[r.fieldKey] != null
              ? String(profile[requestModal.section][r.fieldKey])
              : null),
            requestedValue: r.requestedValue,
            reason: r.reason,
          }),
        })
        if (!resp.ok) failures.push(r.fieldKey)
      }
      await reloadRequests()
      if (failures.length === 0) setRequestModal(null)
      else alert(`Failed for: ${failures.join(', ')}`)
    } catch (e: any) {
      alert(e?.message || 'Failed to submit')
    } finally {
      setRequestSubmitting(false)
    }
  }
  const cancelRequest = async (id: number) => {
    if (!confirm('Cancel this pending change request?')) return
    try {
      const resp = await fetch(`/api/hr/profile/change-requests/${id}`, { method: 'DELETE', credentials: 'include' })
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      await reloadRequests()
    } catch (e: any) {
      alert(e?.message || 'Failed to cancel request')
    }
  }

  React.useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError(null)
      try {
        const url = props.isMe
          ? '/api/hr/profile'
          : `/api/hr/employees/by-email/${encodeURIComponent(props.personEmail)}`
        const resp = await fetch(url, { credentials: 'include' })
        if (resp.status === 404) throw new Error('No HR record on file for this person yet')
        if (!resp.ok) {
          const j = await resp.json().catch(() => ({} as any))
          throw new Error(j.error || `HTTP ${resp.status}`)
        }
        const data = await resp.json() as any
        if (!cancelled) setProfile(data)
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load HR record')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => { cancelled = true }
  }, [props.personEmail, props.isMe])

  // Load this employee's document_assignments so the Onboarding section
  // shows actual signed/pending PDFs rather than a flat date editor.
  React.useEffect(() => {
    let cancelled = false
    async function loadDocs() {
      setDocsLoading(true)
      try {
        if (props.isMe) {
          const r = await fetch('/api/hr/my-assignments', { credentials: 'include' })
          if (r.ok) {
            const j = await r.json() as any
            if (!cancelled) {
              setDocAssignments(j?.data?.assignments || [])
              setDocMissingTemplates(j?.data?.missingTemplates || [])
            }
          }
        } else if (props.isAdmin && profile?.id) {
          // Admin viewing someone else: filter the admin list by employee_id.
          const r = await fetch(`/api/hr-admin/assignments?employee_id=${encodeURIComponent(profile.id)}`, { credentials: 'include' })
          if (r.ok) {
            const j = await r.json() as any
            if (!cancelled) {
              setDocAssignments(j?.data?.assignments || [])
              setDocMissingTemplates(j?.data?.missingTemplates || [])
            }
          }
        }
      } catch {
        if (!cancelled) setDocAssignments([])
      } finally {
        if (!cancelled) setDocsLoading(false)
      }
    }
    void loadDocs()
    return () => { cancelled = true }
  }, [props.isMe, props.isAdmin, profile?.id])

  const startEdit = () => {
    if (!profile) return
    setDraft({
      personalInfo: { ...(profile.personalInfo || {}), address: { ...((profile.personalInfo || {}).address || {}) } },
      emergencyContacts: Array.isArray(profile.emergencyContacts) ? profile.emergencyContacts.map((c: any) => ({ ...c })) : [],
      educationInfo: { ...(profile.educationInfo || {}) },
      dependents: Array.isArray(profile.dependents) ? profile.dependents.map((d: any) => ({ ...d })) : [],
      // Generic key/value drafts for the other sections so the inline
      // section editor (HrGenericSectionEditor) can mutate them.
      employmentInfo: { ...(profile.employmentInfo || {}) },
      compensationInfo: { ...(profile.compensationInfo || {}) },
      taxInfo: { ...(profile.taxInfo || {}) },
      bankingInfo: { ...(profile.bankingInfo || {}) },
      trainingInfo: { ...(profile.trainingInfo || {}) },
      equipmentInfo: { ...(profile.equipmentInfo || {}) },
      licensingInfo: { ...(profile.licensingInfo || {}) },
      performanceInfo: { ...(profile.performanceInfo || {}) },
      onboardingInfo: { ...(profile.onboardingInfo || {}) },
    })
    setEditing(true)
    setSaveErr(null)
  }
  // Generic setter for the section editors below: replaces a section in
  // the draft with the next value (typically a flat object).
  const setSectionDraft = (sectionKey: string, value: any) =>
    setDraft((d: any) => ({ ...d, [sectionKey]: value }))

  const cancelEdit = () => { setEditing(false); setDraft(null); setSaveErr(null) }

  const setPersonal = (key: string, value: any) =>
    setDraft((d: any) => ({ ...d, personalInfo: { ...(d.personalInfo || {}), [key]: value } }))
  const setAddress = (key: string, value: any) =>
    setDraft((d: any) => ({ ...d, personalInfo: { ...(d.personalInfo || {}), address: { ...((d.personalInfo || {}).address || {}), [key]: value } } }))
  const setEducation = (key: string, value: any) =>
    setDraft((d: any) => ({ ...d, educationInfo: { ...(d.educationInfo || {}), [key]: value } }))
  const setContact = (idx: number, key: string, value: any) =>
    setDraft((d: any) => {
      const arr = [...(d.emergencyContacts || [])]
      while (arr.length <= idx) arr.push({ name: '', phone: '', relationship: '' })
      arr[idx] = { ...arr[idx], [key]: value }
      return { ...d, emergencyContacts: arr }
    })
  const addContact = () =>
    setDraft((d: any) => ({ ...d, emergencyContacts: [...(d.emergencyContacts || []), { name: '', phone: '', relationship: '' }] }))
  const removeContact = (idx: number) =>
    setDraft((d: any) => ({ ...d, emergencyContacts: (d.emergencyContacts || []).filter((_: any, i: number) => i !== idx) }))
  const setDependent = (idx: number, key: string, value: any) =>
    setDraft((d: any) => {
      const arr = [...(d.dependents || [])]
      while (arr.length <= idx) arr.push({ name: '', relationship: '', dateOfBirth: '' })
      arr[idx] = { ...arr[idx], [key]: value }
      return { ...d, dependents: arr }
    })
  const addDependent = () =>
    setDraft((d: any) => ({ ...d, dependents: [...(d.dependents || []), { name: '', relationship: '', dateOfBirth: '' }] }))
  const removeDependent = (idx: number) =>
    setDraft((d: any) => ({ ...d, dependents: (d.dependents || []).filter((_: any, i: number) => i !== idx) }))

  const save = async () => {
    if (!draft) return
    setSaving(true)
    setSaveErr(null)
    try {
      // Strip empty-string values inside an object so we don't store noise.
      const pruneObj = (o: any) => {
        if (!o || typeof o !== 'object') return o
        const out: any = {}
        for (const [k, v] of Object.entries(o)) {
          if (v == null || v === '') continue
          if (typeof v === 'object' && !Array.isArray(v)) {
            const nested = pruneObj(v)
            if (nested && Object.keys(nested).length > 0) out[k] = nested
          } else {
            out[k] = v
          }
        }
        return out
      }
      const sections: Record<string, any> = {
        personalInfo: draft.personalInfo,
        // Drop fully-blank rows so we don't store noise
        emergencyContacts: (draft.emergencyContacts || []).filter((c: any) => c && (c.name || c.phone || c.relationship)),
        educationInfo: draft.educationInfo,
        dependents: (draft.dependents || []).filter((d: any) => d && (d.name || d.relationship || d.dateOfBirth)),
        employmentInfo: pruneObj(draft.employmentInfo),
        compensationInfo: pruneObj(draft.compensationInfo),
        taxInfo: pruneObj(draft.taxInfo),
        bankingInfo: pruneObj(draft.bankingInfo),
        trainingInfo: pruneObj(draft.trainingInfo),
        equipmentInfo: pruneObj(draft.equipmentInfo),
        licensingInfo: pruneObj(draft.licensingInfo),
        performanceInfo: pruneObj(draft.performanceInfo),
        onboardingInfo: pruneObj(draft.onboardingInfo),
      }
      const url = props.isMe ? '/api/hr/profile' : `/api/hr/employees/${profile.id}`
      const resp = await fetch(url, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sections }),
      })
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({} as any))
        throw new Error(j.error || `HTTP ${resp.status}`)
      }
      const updated = await resp.json() as any
      setProfile(updated)
      setEditing(false)
      setDraft(null)
    } catch (e: any) {
      setSaveErr(e?.message || 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <div className="profile-hr-tab"><div className="bp-shimmer bp-skeleton-line" style={{ width: '60%' }} /></div>
  }
  if (error) {
    return <div className="profile-hr-tab"><div className="profile-error" style={{ padding: 12 }}>{error}</div></div>
  }
  if (!profile) {
    return <div className="profile-hr-tab"><p style={{ color: '#64748b' }}>No HR record on file.</p></div>
  }

  const canEdit = props.isMe || props.isAdmin
  const personal = (editing ? draft?.personalInfo : profile.personalInfo) || {}
  const address = personal.address || {}
  const contacts = (editing ? draft?.emergencyContacts : profile.emergencyContacts) || []

  return (
    <div className="profile-hr-tab">
      {props.isMe && pendingRequests.length > 0 && (
        <div style={{ margin: '0 0 12px', padding: '10px 14px', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8, color: '#92400e', fontSize: '0.84rem', display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
          <span><strong>{pendingRequests.length}</strong> change request{pendingRequests.length === 1 ? '' : 's'} pending HR approval.</span>
        </div>
      )}
      <div className="profile-hr-banner">
        <div>
          <strong>HR Record</strong>
          {profile.badgeNumber && (
            <span style={{ color: '#64748b', marginLeft: 8, fontSize: '0.82rem' }}>
              Badge #{profile.badgeNumber}
            </span>
          )}
        </div>
        {canEdit && !editing && (
          <button className="bp-btn bp-btn-secondary" onClick={startEdit}>Edit</button>
        )}
      </div>

      {saveErr && <div className="profile-edit-error" style={{ padding: '8px 12px' }}>{saveErr}</div>}

      {/*
        TODO(licensing/uploads): Required Uploads (DL / insurance / registration)
        and the missing-uploads banner are hidden because they're position-
        specific (only drivers / field employees need them). Re-enable both
        the banner above and the <HrUploadSlots/> section below once we wire
        position-aware gating (likely employmentInfo.subsidiary +
        employmentInfo.employmentType, or a per-position policy table).
        Backend endpoints + the licensing_info storage stay intact so flipping
        this back on is just UI.
      */}

      <div className="profile-hr-section">
        <div className="profile-hr-section-hdr">Basic Info</div>
        <div className="profile-hr-section-grid">
          <div className="profile-hr-field"><div className="profile-hr-field-label">Name</div><div className="profile-hr-field-value">{[profile.firstName, profile.lastName].filter(Boolean).join(' ') || '—'}</div></div>
          {profile.preferredName && <div className="profile-hr-field"><div className="profile-hr-field-label">Preferred Name</div><div className="profile-hr-field-value">{profile.preferredName}</div></div>}
          {profile.position && <div className="profile-hr-field"><div className="profile-hr-field-label">Position</div><div className="profile-hr-field-value">{profile.position}</div></div>}
          {profile.department && <div className="profile-hr-field"><div className="profile-hr-field-label">Department</div><div className="profile-hr-field-value">{profile.department}</div></div>}
          {profile.email && <div className="profile-hr-field"><div className="profile-hr-field-label">Email</div><div className="profile-hr-field-value">{profile.email}</div></div>}
          {profile.phone && <div className="profile-hr-field"><div className="profile-hr-field-label">Phone</div><div className="profile-hr-field-value">{profile.phone}</div></div>}
          {profile.status && <div className="profile-hr-field"><div className="profile-hr-field-label">Status</div><div className="profile-hr-field-value">{profile.status}</div></div>}
        </div>
      </div>

      {/* Personal Information — editable */}
      <div className="profile-hr-section">
        <div className="profile-hr-section-hdr">Personal Information</div>
        {editing ? (
          <div className="profile-hr-section-grid">
            <HrInput label="Middle Name" value={personal.middleName || ''} onChange={v => setPersonal('middleName', v)} />
            <HrInput label="Date of Birth" type="date" value={personal.dateOfBirth || ''} onChange={v => setPersonal('dateOfBirth', v)} />
            <HrSelect label="Gender" value={personal.gender || ''} onChange={v => setPersonal('gender', v)} options={['', 'Female', 'Male', 'Non-binary', 'Prefer not to say']} />
            <HrSelect label="Marital Status" value={personal.maritalStatus || ''} onChange={v => setPersonal('maritalStatus', v)} options={['', 'Single', 'Married', 'Divorced', 'Widowed', 'Separated']} />
            <HrSelect label="Citizenship" value={personal.citizenship || ''} onChange={v => setPersonal('citizenship', v)} options={['', 'US Citizen', 'Permanent Resident', 'Work Visa', 'Other']} />
            <HrInput label="Street Address" value={address.street || ''} onChange={v => setAddress('street', v)} />
            <HrInput label="City" value={address.city || ''} onChange={v => setAddress('city', v)} />
            <HrInput label="State" value={address.state || ''} onChange={v => setAddress('state', v)} />
            <HrInput label="ZIP" value={address.zip || ''} onChange={v => setAddress('zip', v)} />
          </div>
        ) : (
          <HrSectionBody value={profile.personalInfo} />
        )}
      </div>

      {/* Emergency Contacts — editable list */}
      <div className="profile-hr-section">
        <div className="profile-hr-section-hdr" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Emergency Contacts</span>
          {editing && <button className="hr-add-btn" onClick={addContact} style={{ fontSize: '0.7rem', padding: '2px 8px' }}>+ Add contact</button>}
        </div>
        {editing ? (
          contacts.length === 0 ? (
            <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: 0 }}>No contacts yet. Add one with the button above.</p>
          ) : (
            contacts.map((c: any, idx: number) => (
              <div key={idx} className="profile-hr-section-grid" style={{ marginBottom: 8, paddingBottom: 8, borderBottom: idx < contacts.length - 1 ? '1px dashed #e2e8f0' : 'none' }}>
                <HrInput label="Name" value={c.name || ''} onChange={v => setContact(idx, 'name', v)} />
                <HrInput label="Phone" value={c.phone || ''} onChange={v => setContact(idx, 'phone', v)} />
                <HrInput label="Relationship" value={c.relationship || ''} onChange={v => setContact(idx, 'relationship', v)} />
                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <button className="bp-btn bp-btn-secondary" style={{ fontSize: '0.7rem', padding: '4px 10px' }} onClick={() => removeContact(idx)}>Remove</button>
                </div>
              </div>
            ))
          )
        ) : (
          <HrSectionBody value={profile.emergencyContacts} />
        )}
      </div>

      {/* Education & Skills — editable */}
      <div className="profile-hr-section">
        <div className="profile-hr-section-hdr">Education &amp; Skills</div>
        {editing ? (
          <div className="profile-hr-section-grid">
            <HrInput label="Education Level" value={(draft?.educationInfo?.educationLevel) || ''} onChange={v => setEducation('educationLevel', v)} />
            <HrInput label="School" value={(draft?.educationInfo?.school) || ''} onChange={v => setEducation('school', v)} />
            <HrInput label="Graduation Year" value={(draft?.educationInfo?.graduationYear) || ''} onChange={v => setEducation('graduationYear', v)} />
            <HrInput label="Field of Study" value={(draft?.educationInfo?.fieldOfStudy) || ''} onChange={v => setEducation('fieldOfStudy', v)} />
            <HrInput label="Skills" value={(draft?.educationInfo?.skills) || ''} onChange={v => setEducation('skills', v)} />
            <HrInput label="Certifications" value={(draft?.educationInfo?.certifications) || ''} onChange={v => setEducation('certifications', v)} />
          </div>
        ) : (
          <HrSectionBody value={profile.educationInfo} />
        )}
      </div>

      {/* Required Uploads (DL / insurance / registration) hidden — see TODO above. */}

      {/* Dependents & Beneficiaries — editable list */}
      <div className="profile-hr-section">
        <div className="profile-hr-section-hdr" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Dependents &amp; Beneficiaries</span>
          {editing && <button className="hr-add-btn" onClick={addDependent} style={{ fontSize: '0.7rem', padding: '2px 8px' }}>+ Add dependent</button>}
        </div>
        {editing ? (
          (draft?.dependents || []).length === 0 ? (
            <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: 0 }}>No dependents yet. Add one with the button above.</p>
          ) : (
            (draft?.dependents || []).map((dep: any, idx: number) => (
              <div key={idx} className="profile-hr-section-grid" style={{ marginBottom: 8, paddingBottom: 8, borderBottom: idx < (draft?.dependents || []).length - 1 ? '1px dashed #e2e8f0' : 'none' }}>
                <HrInput label="Name" value={dep.name || ''} onChange={v => setDependent(idx, 'name', v)} />
                <HrInput label="Relationship" value={dep.relationship || ''} onChange={v => setDependent(idx, 'relationship', v)} />
                <HrInput label="Date of Birth" type="date" value={dep.dateOfBirth || ''} onChange={v => setDependent(idx, 'dateOfBirth', v)} />
                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <button className="bp-btn bp-btn-secondary" style={{ fontSize: '0.7rem', padding: '4px 10px' }} onClick={() => removeDependent(idx)}>Remove</button>
                </div>
              </div>
            ))
          )
        ) : (
          <HrSectionBody value={profile.dependents} />
        )}
      </div>

      {/* Sections without a hand-written form. In view mode each renders
          read-only with a "Not filled in yet" stub when empty. In edit
          mode each becomes a generic key/value editor so admins can fill
          in any field without a schema migration. */}
      {HR_RECORD_SECTIONS
        .filter(s => !['personalInfo', 'emergencyContacts', 'educationInfo', 'dependents'].includes(s.key))
        .map(s => {
          // Onboarding: render the actual signed/assigned documents (W-4,
          // I-9, Handbook etc.) instead of a generic date editor. Users
          // care about seeing the PDF + signed date, not raw text fields.
          if (s.key === 'onboardingInfo') {
            return (
              <div className="profile-hr-section" key={s.key}>
                <div className="profile-hr-section-hdr">{s.label}</div>
                <HrSignedDocumentsList
                  assignments={docAssignments}
                  missingTemplates={docMissingTemplates}
                  loading={docsLoading}
                  isMe={props.isMe}
                  onSign={props.onSignAssignment}
                />
              </div>
            )
          }
          return (
            <div className="profile-hr-section" key={s.key}>
              <div className="profile-hr-section-hdr">{s.label}</div>
              {editing && canEdit ? (
                <HrGenericSectionEditor
                  value={draft?.[s.key]}
                  onChange={(next) => setSectionDraft(s.key, next)}
                  defaultKeys={HR_SECTION_FIELDS[s.key] || []}
                  sectionKey={s.key}
                  locked={props.isMe && HR_LOCKED_SECTIONS.has(s.key)}
                  pendingRequests={pendingRequests}
                  onRequestChange={(fieldKey, currentValue) => openRequestChange(s.key, fieldKey, currentValue)}
                  onCancelRequest={cancelRequest}
                />
              ) : (
                <HrSectionBody value={profile[s.key]} />
              )}
            </div>
          )
        })}
      {/* Section-wide Request Change modal — compact list, per-field reason */}
      {requestModal && (() => {
        const sectionKey = requestModal.section
        const sectionLabel = HR_RECORD_SECTIONS.find(s => s.key === sectionKey)?.label || sectionKey
        const sectionData = profile?.[sectionKey] || {}
        const defaultKeys = HR_SECTION_FIELDS[sectionKey] || []
        const presentKeys = Object.keys(sectionData).filter(k => typeof sectionData[k] !== 'object' || Array.isArray(sectionData[k]))
        const allKeys = Array.from(new Set([...defaultKeys, ...presentKeys]))
        const pendingForSection = new Set(pendingRequests.filter(r => r.section === sectionKey && r.status === 'pending').map(r => r.field_key))
        const selectedCount = Object.values(requestValues).filter(v => v.selected).length
        const toggle = (k: string, checked: boolean) =>
          setRequestValues(prev => ({ ...prev, [k]: { selected: checked, value: prev[k]?.value || '', reason: prev[k]?.reason || '' } }))
        const setVal = (k: string, val: string) =>
          setRequestValues(prev => ({ ...prev, [k]: { selected: prev[k]?.selected ?? true, value: val, reason: prev[k]?.reason || '' } }))
        const setReason = (k: string, reason: string) =>
          setRequestValues(prev => ({ ...prev, [k]: { selected: prev[k]?.selected ?? true, value: prev[k]?.value || '', reason } }))
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }} onClick={() => !requestSubmitting && setRequestModal(null)}>
            <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 600, maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(15,23,42,0.28)', overflow: 'hidden' }}>
              {/* Header */}
              <div style={{ padding: '18px 22px', background: 'linear-gradient(180deg, #f8fafc, #ffffff)', borderBottom: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: '#eff6ff', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 style={{ margin: 0, fontSize: '1rem', color: '#0f172a', fontWeight: 700 }}>Request changes</h3>
                    <p style={{ margin: '1px 0 0', fontSize: '0.78rem', color: '#64748b' }}>{sectionLabel} · HR will review each change</p>
                  </div>
                  {selectedCount > 0 && (
                    <span style={{ fontSize: '0.72rem', color: '#2563eb', background: '#dbeafe', padding: '4px 10px', borderRadius: 999, fontWeight: 700, letterSpacing: '0.02em' }}>{selectedCount} selected</span>
                  )}
                </div>
              </div>

              {/* List */}
              <div style={{ overflowY: 'auto', padding: '4px 0' }}>
                {allKeys.map((k, idx) => {
                  const label = k.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                  const currentValue = sectionData[k] != null ? String(sectionData[k]) : ''
                  const row = requestValues[k] || { selected: false, value: '', reason: '' }
                  const alreadyPending = pendingForSection.has(k)
                  const isFirst = idx === 0
                  return (
                    <div
                      key={k}
                      style={{
                        position: 'relative',
                        padding: '12px 22px 12px 18px',
                        borderTop: isFirst ? 'none' : '1px solid #f1f5f9',
                        background: row.selected ? '#f8fafc' : '#fff',
                        transition: 'background 0.15s',
                        cursor: alreadyPending ? 'not-allowed' : 'pointer',
                      }}
                      onClick={() => { if (!alreadyPending) toggle(k, !row.selected) }}
                    >
                      {/* Selected accent bar */}
                      {row.selected && (
                        <span style={{ position: 'absolute', left: 0, top: 8, bottom: 8, width: 3, background: '#2563eb', borderRadius: '0 3px 3px 0' }} />
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <input
                          type="checkbox"
                          disabled={alreadyPending}
                          checked={row.selected}
                          onChange={e => toggle(k, e.target.checked)}
                          onClick={e => e.stopPropagation()}
                          style={{ width: 16, height: 16, accentColor: '#2563eb', flexShrink: 0, cursor: alreadyPending ? 'not-allowed' : 'pointer' }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                            <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#0f172a' }}>{label}</span>
                            {alreadyPending && (
                              <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#92400e', background: '#fef3c7', padding: '1px 7px', borderRadius: 999, letterSpacing: '0.04em', textTransform: 'uppercase' }}>Pending</span>
                            )}
                          </div>
                          <div style={{ fontSize: '0.76rem', color: currentValue ? '#64748b' : '#cbd5e1', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {currentValue || 'Not set'}
                          </div>
                        </div>
                      </div>
                      {row.selected && !alreadyPending && (
                        <div onClick={e => e.stopPropagation()} style={{ marginTop: 10, marginLeft: 28, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                          <div>
                            <label style={{ display: 'block', fontSize: '0.64rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>New value</label>
                            <input
                              type="text"
                              placeholder="—"
                              value={row.value}
                              onChange={e => setVal(k, e.target.value)}
                              style={{ width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: '0.82rem', background: '#fff', boxShadow: 'inset 0 1px 2px rgba(15,23,42,0.04)' }}
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', fontSize: '0.64rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Reason <span style={{ color: '#94a3b8', textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                            <input
                              type="text"
                              placeholder="Why this change?"
                              value={row.reason}
                              onChange={e => setReason(k, e.target.value)}
                              style={{ width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: '0.82rem', color: '#475569', background: '#fff' }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>

              {/* Footer */}
              <div style={{ padding: '14px 22px', borderTop: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '0.74rem', color: '#64748b' }}>
                  {selectedCount === 0 ? 'No changes selected' : `Submitting ${selectedCount} change request${selectedCount === 1 ? '' : 's'}`}
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="bp-btn bp-btn-secondary" disabled={requestSubmitting} onClick={() => setRequestModal(null)}>Cancel</button>
                  <button className="bp-btn bp-btn-primary" disabled={requestSubmitting || selectedCount === 0} onClick={submitRequest}>
                    {requestSubmitting ? 'Submitting…' : 'Submit'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      })()}
      {editing && (() => {
        const sectionKeys = [
          'personalInfo', 'emergencyContacts', 'educationInfo', 'dependents',
          'employmentInfo', 'compensationInfo', 'taxInfo', 'bankingInfo',
          'trainingInfo', 'equipmentInfo', 'licensingInfo', 'performanceInfo',
          'onboardingInfo',
        ]
        const normalize = (v: any): any => {
          if (v == null) return null
          if (Array.isArray(v)) {
            const arr = v.map(normalize).filter(x => x !== null)
            return arr.length ? arr : null
          }
          if (typeof v === 'object') {
            const out: any = {}
            for (const [k, val] of Object.entries(v)) {
              const nv = normalize(val)
              if (nv !== null) out[k] = nv
            }
            return Object.keys(out).length ? out : null
          }
          if (typeof v === 'string') return v.trim() === '' ? null : v
          return v
        }
        let changed = 0
        for (const k of sectionKeys) {
          const a = JSON.stringify(normalize((draft as any)?.[k]))
          const b = JSON.stringify(normalize((profile as any)?.[k]))
          if (a !== b) changed++
        }
        const msg = changed === 0 ? 'No changes yet.' : `You've changed ${changed} setting${changed === 1 ? '' : 's'}.`
        return (
          <div className="prefs-save-footer crp-save-footer">
            <div className="prefs-save-footer-inner">
              <div className="prefs-save-footer-btns">
                <button className="prefs-save-btn" onClick={save} disabled={saving || changed === 0}>{saving ? 'Saving…' : 'Save'}</button>
                <button className="prefs-cancel-btn" onClick={cancelEdit} disabled={saving}>Cancel</button>
              </div>
              <p className="prefs-save-footer-msg">{msg}</p>
            </div>
          </div>
        )
      })()}
    </div>
  )
}

const HR_UPLOAD_DEFS = [
  { slot: 'driverLicense', label: "Driver's License" },
  { slot: 'autoInsurance', label: 'Auto Insurance Card' },
  { slot: 'vehicleRegistration', label: 'Vehicle Registration' },
] as const

function HrUploadSlots(props: {
  profileId: number
  isMe: boolean
  isAdmin: boolean
  licensingInfo: Record<string, any>
  onChanged: (updatedLicensing: Record<string, any>) => void
}) {
  const canEdit = props.isMe || props.isAdmin
  return (
    <div className="profile-hr-upload-row">
      {HR_UPLOAD_DEFS.map(d => (
        <HrUploadSlotCard
          key={d.slot}
          profileId={props.profileId}
          slot={d.slot}
          label={d.label}
          isMe={props.isMe}
          canEdit={canEdit}
          fileKey={props.licensingInfo[`${d.slot}FileKey`] || null}
          fileName={props.licensingInfo[`${d.slot}FileName`] || null}
          uploadedAt={props.licensingInfo[`${d.slot}UploadedAt`] || null}
          onChanged={(next) => props.onChanged({ ...props.licensingInfo, ...next })}
        />
      ))}
    </div>
  )
}

function HrUploadSlotCard(props: {
  profileId: number
  slot: string
  label: string
  isMe: boolean
  canEdit: boolean
  fileKey: string | null
  fileName: string | null
  uploadedAt: string | null
  onChanged: (next: Record<string, any>) => void
}) {
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [busy, setBusy] = React.useState(false)
  const [err, setErr] = React.useState<string | null>(null)

  const upload = async (file: File) => {
    setBusy(true); setErr(null)
    try {
      const url = props.isMe
        ? `/api/hr/profile/upload/${props.slot}`
        : `/api/hr/employees/${props.profileId}/upload/${props.slot}`
      const fd = new FormData()
      fd.append('file', file)
      const resp = await fetch(url, { method: 'POST', credentials: 'include', body: fd })
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({} as any))
        throw new Error(j.error || `HTTP ${resp.status}`)
      }
      const data = await resp.json() as { key: string; fileName: string; uploadedAt: string }
      props.onChanged({
        [`${props.slot}FileKey`]: data.key,
        [`${props.slot}FileName`]: data.fileName,
        [`${props.slot}UploadedAt`]: data.uploadedAt,
      })
    } catch (e: any) {
      setErr(e?.message || 'Upload failed')
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    if (!confirm(`Remove the uploaded ${props.label.toLowerCase()}?`)) return
    setBusy(true); setErr(null)
    try {
      const url = props.isMe
        ? `/api/hr/profile/upload/${props.slot}`
        : `/api/hr/employees/${props.profileId}/upload/${props.slot}`
      const resp = await fetch(url, { method: 'DELETE', credentials: 'include' })
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({} as any))
        throw new Error(j.error || `HTTP ${resp.status}`)
      }
      props.onChanged({
        [`${props.slot}FileKey`]: undefined,
        [`${props.slot}FileName`]: undefined,
        [`${props.slot}UploadedAt`]: undefined,
      })
    } catch (e: any) {
      setErr(e?.message || 'Remove failed')
    } finally {
      setBusy(false)
    }
  }

  const isUploaded = !!props.fileKey
  const viewUrl = isUploaded ? `/api/hr/profile-file/${props.profileId}/${props.slot}` : null
  const dateStr = props.uploadedAt ? new Date(props.uploadedAt).toLocaleDateString() : ''

  return (
    <div className={`profile-hr-upload-card${isUploaded ? ' is-uploaded' : ' is-missing'}`}>
      <div className="profile-hr-upload-label">
        {props.label}
        {!isUploaded && <span className="profile-hr-upload-required">Required</span>}
      </div>
      {isUploaded ? (
        <>
          <div className="profile-hr-upload-meta">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            <span>{props.fileName || 'Uploaded'}{dateStr ? ` · ${dateStr}` : ''}</span>
          </div>
          {props.canEdit && (
            <div className="profile-hr-upload-actions">
              {viewUrl && <a href={viewUrl} target="_blank" rel="noopener noreferrer" className="profile-hr-upload-link">View</a>}
              <button className="profile-hr-upload-link" onClick={() => inputRef.current?.click()} disabled={busy}>{busy ? 'Working\u2026' : 'Replace'}</button>
              <button className="profile-hr-upload-link" onClick={remove} disabled={busy}>Remove</button>
            </div>
          )}
        </>
      ) : props.canEdit ? (
        <div className="profile-hr-upload-actions">
          <button className="bp-btn bp-btn-primary" style={{ fontSize: '0.78rem', padding: '6px 12px' }} onClick={() => inputRef.current?.click()} disabled={busy}>{busy ? 'Uploading\u2026' : 'Upload'}</button>
        </div>
      ) : (
        <div className="profile-hr-upload-meta" style={{ color: '#94a3b8' }}>Not uploaded</div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        style={{ display: 'none' }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); e.target.value = '' }}
      />
      {err && <div className="profile-hr-upload-err">{err}</div>}
    </div>
  )
}

function HrInput(props: { label: string; value: string; onChange: (v: string) => void; type?: string }) {
  return (
    <div className="profile-hr-field">
      <div className="profile-hr-field-label">{props.label}</div>
      <input
        type={props.type || 'text'}
        value={props.value}
        onChange={e => props.onChange(e.target.value)}
        className="profile-hr-input"
      />
    </div>
  )
}

function HrSelect(props: { label: string; value: string; onChange: (v: string) => void; options: string[] }) {
  return (
    <div className="profile-hr-field">
      <div className="profile-hr-field-label">{props.label}</div>
      <select
        value={props.value}
        onChange={e => props.onChange(e.target.value)}
        className="profile-hr-input"
      >
        {props.options.map(o => <option key={o} value={o}>{o || '—'}</option>)}
      </select>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Email — unified email client surface (#/email)
// 3-pane layout (folders · thread list · reader) + functional Compose modal
// using Graph Mail.Send. Falls back to mock threads when Mail.Read isn't
// granted yet; detects dynamically via /api/crm/email/folders.
// Visual language matches M365_SURFACE_DESIGN.md: crisp, dynamic,
// down-to-earth. Close.com-style SafeEmailFrame for hostile HTML isolation.
// ---------------------------------------------------------------------------

type InboxFolder = { key: string; label: string; type: 'personal' | 'shared'; icon: string; unreadCount: number }
type InboxParticipant = { name?: string; email: string }
type InboxThreadSummary = {
  id: string
  subject: string
  preview: string
  participants: InboxParticipant[]
  lastMessageAt: string
  messageCount: number
  isRead: boolean
  hasAttachments: boolean
  folder: string
}
type InboxMessage = {
  id: string
  threadId: string
  from: InboxParticipant
  to: InboxParticipant[]
  cc?: InboxParticipant[]
  subject: string
  snippet: string
  bodyHtml: string
  receivedAt: string
  isRead: boolean
  hasAttachments: boolean
  folder: string
}
type InboxThreadFull = InboxThreadSummary & { messages: InboxMessage[] }

// --- Utilities --------------------------------------------------------------

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
function isEmail(s: string): boolean { return EMAIL_REGEX.test(s.trim()) }

// Domains BotPros owns in the M365 tenant. Only these addresses have a
// shot at a Graph profile photo; every other sender (external customers,
// noreply@*, automation bots) would 404 the photo endpoint and pollute
// the console. Gate the <img> tag on this so we don't make the request
// at all for non-tenant senders.
const BOTPROS_TENANT_DOMAINS = new Set([
  'botpros.ai',
])
// Local-parts that are automation mailboxes, not real users. These 404
// the Graph photo endpoint even though the DOMAIN is in-tenant
// (no-reply@botpros.ai etc.). Add more patterns as they show up.
const AUTOMATION_LOCAL_PART = /^(no-?reply|postmaster|mailer-daemon|bounces?|dmarc|abuse|notifications?|alerts?|billing|admin|root|info|mail|news|service(?:s|desk)?|support)(?:[-._+]|$)/i

// Remember 404s across renders AND across refreshes. localStorage is
// sync + cheap; the set is small (tens of entries at most, one per
// employee without an M365 photo). Cap at 200 to keep quota sane
// against hostile input. Set value lifetime: 7 days (photos can
// change — someone uploads a picture — and we don't want a stale
// miss sticking forever).
const PHOTO_MISS_STORAGE_KEY = 'hz:knownMissingPhotos:v1'
const PHOTO_MISS_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000
const KNOWN_MISSING_PHOTOS: Set<string> = (() => {
  const s = new Set<string>()
  try {
    const raw = localStorage.getItem(PHOTO_MISS_STORAGE_KEY)
    if (!raw) return s
    const parsed = JSON.parse(raw) as Record<string, number>
    const now = Date.now()
    for (const [email, ts] of Object.entries(parsed || {})) {
      if (typeof ts === 'number' && now - ts < PHOTO_MISS_MAX_AGE_MS) s.add(email)
    }
  } catch { /* corrupt blob — ignore */ }
  return s
})()
function rememberMissingPhoto(email: string) {
  const key = email.toLowerCase()
  KNOWN_MISSING_PHOTOS.add(key)
  try {
    const raw = localStorage.getItem(PHOTO_MISS_STORAGE_KEY)
    const map = raw ? (JSON.parse(raw) as Record<string, number>) : {}
    map[key] = Date.now()
    // Trim oldest entries if we exceed the cap.
    const keys = Object.keys(map)
    if (keys.length > 200) {
      keys.sort((a, b) => (map[a] ?? 0) - (map[b] ?? 0))
      for (const k of keys.slice(0, keys.length - 200)) delete map[k]
    }
    localStorage.setItem(PHOTO_MISS_STORAGE_KEY, JSON.stringify(map))
  } catch { /* quota — non-fatal */ }
}

function isTenantEmail(email: string | null | undefined): boolean {
  if (!email) return false
  const at = email.lastIndexOf('@')
  if (at < 0) return false
  const domain = email.slice(at + 1).toLowerCase()
  if (!BOTPROS_TENANT_DOMAINS.has(domain)) return false
  // In-tenant but an automation address? Skip — those never have photos.
  const local = email.slice(0, at)
  if (AUTOMATION_LOCAL_PART.test(local)) return false
  return true
}

// True only if we should actually request this address's photo right
// now. Checks both the tenant gate and the session-level 404 cache.
function shouldShowTenantPhoto(email: string | null | undefined): boolean {
  if (!isTenantEmail(email)) return false
  return !KNOWN_MISSING_PHOTOS.has(email!.toLowerCase())
}

// Shared per-address photo probe. Many inbox rows render the same teammate's
// avatar at once; without coordination each <img> fired its own request, so a
// cold inbox produced a burst of duplicate 404s for the same few addresses.
// We resolve each address exactly once (a shared promise) and cache the
// verdict, so a teammate without a photo is probed a single time per session,
// not once per row. KNOWN_MISSING_PHOTOS (persisted, 7-day) still short-circuits
// across sessions; this Map de-dupes the concurrent first-paint within one.
//   value: true = has photo · false = missing · Promise = probe in flight
const TENANT_PHOTO_PROBE = new Map<string, boolean | Promise<boolean>>()
function teamPhotoUrl(email: string): string {
  return `/api/crm/team/${encodeURIComponent(email)}/photo?noFallback=1`
}
function probeTenantPhoto(email: string): boolean | Promise<boolean> {
  const key = email.toLowerCase()
  if (KNOWN_MISSING_PHOTOS.has(key)) return false
  const cached = TENANT_PHOTO_PROBE.get(key)
  if (cached !== undefined) return cached
  const p = new Promise<boolean>((resolve) => {
    const img = new Image()
    img.onload = () => { TENANT_PHOTO_PROBE.set(key, true); resolve(true) }
    img.onerror = () => { TENANT_PHOTO_PROBE.set(key, false); rememberMissingPhoto(email); resolve(false) }
    img.src = teamPhotoUrl(email)
  })
  TENANT_PHOTO_PROBE.set(key, p)
  return p
}

// Teammate avatar photo, de-duped across rows (see probeTenantPhoto). Renders
// nothing — letting the colored-initials div show through — unless the address
// is confirmed to have a photo, so missing-photo teammates never trigger a
// per-row 404. Replaces the previous inline <img> in ThreadCard / MessageBubble.
function TenantAvatarPhoto({ email, className }: { email: string | null | undefined; className?: string }) {
  const [hasPhoto, setHasPhoto] = React.useState<boolean | null>(() => {
    if (!shouldShowTenantPhoto(email)) return false
    const c = TENANT_PHOTO_PROBE.get(email!.toLowerCase())
    return typeof c === 'boolean' ? c : null
  })
  React.useEffect(() => {
    if (!shouldShowTenantPhoto(email)) { setHasPhoto(false); return }
    const r = probeTenantPhoto(email!)
    if (typeof r === 'boolean') { setHasPhoto(r); return }
    let alive = true
    r.then(v => { if (alive) setHasPhoto(v) })
    return () => { alive = false }
  }, [email])
  if (hasPhoto !== true) return null
  return (
    <img
      className={className}
      src={teamPhotoUrl(email!)}
      alt=""
      decoding="async"
      onLoad={(e) => { (e.currentTarget as HTMLImageElement).style.opacity = '1' }}
      onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
    />
  )
}

// Employee photo avatar by email — shows the user's photo when one exists
// (HR-uploaded first, then M365 Graph), else the caller's colored-initials
// fallback. Used by the profile trigger, profile dropdown, and impersonation
// picker. Deliberately bypasses probeTenantPhoto/KNOWN_MISSING_PHOTOS: those
// persist "missing" verdicts up to 7 days, which would mask the HR photo
// source. `v=2` busts browser-cached 404s from the Graph-only era; `version`
// (per-upload token from /api/me, appended as `&ver=` by teamPhotoSrc) makes
// the URL unique per upload so a pre-upload cached response — photo OR
// noFallback 404 — can never pin a stale avatar. The failed latch resets when
// email OR version changes (a new upload = new version = re-attempt), never
// sticking initials for the rest of the session (docs/REGRESSIONS.md 2026-07-01).
function UserPhotoAvatar({ email, version, imgClassName, imgStyle, fallback }: {
  email: string | null | undefined
  version?: string | null
  imgClassName?: string
  imgStyle?: React.CSSProperties
  fallback: React.ReactNode
}) {
  const [failed, setFailed] = React.useState(false)
  React.useEffect(() => { setFailed(false) }, [email, version])
  if (!email || failed) return <>{fallback}</>
  return (
    <img
      className={imgClassName}
      style={imgStyle}
      src={teamPhotoSrc(email, version)}
      alt=""
      decoding="async"
      onError={() => setFailed(true)}
    />
  )
}

function inboxRelativeTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const then = new Date(iso).getTime()
  if (!Number.isFinite(then)) return '—'
  const diffMs = Date.now() - then
  const min = Math.round(diffMs / 60000)
  if (min < 1) return 'now'
  if (min < 60) return `${min}m`
  const hr = Math.round(min / 60)
  if (hr < 24) return `${hr}h`
  const day = Math.round(hr / 24)
  if (day < 7) return `${day}d`
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function formatInboxTime(iso: string): string {
  const d = new Date(iso)
  const now = new Date()
  const sameDay = d.toDateString() === now.toDateString()
  if (sameDay) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  const yr = d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: yr, hour: 'numeric', minute: '2-digit' })
}

// `formatScheduleTime` formatter is used by the Settings → Email
// scheduling table. Its sibling copy lives inside ComposeModal.tsx (so the
// lazy chunk has no cross-module dependency back into App.tsx).
function formatScheduleTime(iso: string): string {
  try {
    const d = new Date(iso)
    if (!Number.isFinite(d.getTime())) return iso
    const now = new Date()
    const sameDay = d.toDateString() === now.toDateString()
    const tmr = new Date(now); tmr.setDate(now.getDate() + 1)
    const isTmr = d.toDateString() === tmr.toDateString()
    const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    if (sameDay) return `Today at ${time}`
    if (isTmr) return `Tomorrow at ${time}`
    const yr = d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: yr as any }) + ` at ${time}`
  } catch { return iso }
}

// --- Snooze preset date helpers --------------------------------------------
// Produce ISO strings for each preset relative to `now` (defaults to Date.now()).
// Exposed as a factory so we can expose stable "Later today / Tomorrow /
// Weekend / Next week" labels in the dropdown.
type SnoozePresetKey = 'inHour' | 'laterToday' | 'tomorrow' | 'weekend' | 'nextWeek'
type SnoozePreset = { key: SnoozePresetKey; label: string; hint: string; iso: string }
function buildSnoozePresets(now: Date = new Date()): SnoozePreset[] {
  const presets: SnoozePreset[] = []
  // +1 hour
  const inHour = new Date(now.getTime() + 60 * 60 * 1000)
  presets.push({ key: 'inHour', label: 'In 1 hour', hint: inHour.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }), iso: inHour.toISOString() })
  // Later today = 6 PM today; only offered if it's still before 5 PM
  const laterToday = new Date(now)
  laterToday.setHours(18, 0, 0, 0)
  if (laterToday.getTime() > now.getTime() + 60 * 60 * 1000) {
    presets.push({ key: 'laterToday', label: 'Later today', hint: laterToday.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }), iso: laterToday.toISOString() })
  }
  // Tomorrow 8 AM
  const tomorrow = new Date(now)
  tomorrow.setDate(tomorrow.getDate() + 1)
  tomorrow.setHours(8, 0, 0, 0)
  presets.push({ key: 'tomorrow', label: 'Tomorrow', hint: `${tomorrow.toLocaleDateString([], { weekday: 'short' })} ${tomorrow.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`, iso: tomorrow.toISOString() })
  // This weekend = next Saturday 9 AM; skip if we're already past Sat morning
  const weekend = new Date(now)
  const dow = weekend.getDay() // 0=Sun, 6=Sat
  const daysToSat = dow === 6 ? (now.getHours() < 9 ? 0 : 7) : (6 - dow)
  weekend.setDate(weekend.getDate() + daysToSat)
  weekend.setHours(9, 0, 0, 0)
  if (weekend.getTime() > now.getTime() + 60 * 60 * 1000) {
    presets.push({ key: 'weekend', label: 'This weekend', hint: `Sat ${weekend.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`, iso: weekend.toISOString() })
  }
  // Next week = next Monday 8 AM
  const nextWeek = new Date(now)
  const dow2 = nextWeek.getDay()
  const daysToMon = dow2 === 1 ? 7 : ((8 - dow2) % 7 || 7)
  nextWeek.setDate(nextWeek.getDate() + daysToMon)
  nextWeek.setHours(8, 0, 0, 0)
  presets.push({ key: 'nextWeek', label: 'Next week', hint: `Mon ${nextWeek.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`, iso: nextWeek.toISOString() })
  return presets
}

function formatSnoozeUntil(iso: string): string {
  try {
    const d = new Date(iso)
    const now = new Date()
    const sameDay = d.toDateString() === now.toDateString()
    const tomorrow = new Date(now); tomorrow.setDate(tomorrow.getDate() + 1)
    const isTomorrow = d.toDateString() === tomorrow.toDateString()
    const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    if (sameDay) return `today at ${time}`
    if (isTomorrow) return `tomorrow at ${time}`
    return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} at ${time}`
  } catch { return iso }
}

// --- SafeEmailFrame ---------------------------------------------------------
// Renders hostile external HTML in an iframe with no scripts, no tokens, with
// all external images proxied through /api/email/img to strip Referer + block
// trackers. Follows the Close.com pattern described in the sprint brief.

function SafeEmailFrame(props: { html: string; className?: string }) {
  const iframeRef = React.useRef<HTMLIFrameElement | null>(null)
  const [height, setHeight] = React.useState(320)

  const sanitized = React.useMemo(() => {
    if (!props.html) return ''
    // Run through DOMPurify — strips <script>, inline event handlers, etc.
    let clean = DOMPurify.sanitize(props.html, {
      USE_PROFILES: { html: true },
      FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'meta', 'link', 'base', 'noscript'],
      FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'onchange', 'onsubmit', 'srcdoc', 'formaction', 'action'],
      ADD_ATTR: ['target'],
    })
    // Belt-and-suspenders: strip any residual script/handler patterns that
    // DOMPurify sometimes leaves in malformed HTML (conditional comments,
    // broken nesting). These warnings clutter the console even though the
    // sandboxed iframe blocks execution anyway.
    clean = clean
      .replace(/<script\b[\s\S]*?<\/script\s*>/gi, '')
      .replace(/<script\b[^>]*\/>/gi, '')
      .replace(/\son[a-z]+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, '')
      .replace(/\bjavascript\s*:/gi, '#blocked-js:')
      .replace(/<!--\[if[\s\S]*?<!\[endif\]-->/gi, '')
    // Rewrite all <img src> through the image proxy so tokens don't leak.
    clean = clean.replace(/<img\b([^>]*?)\bsrc\s*=\s*(["'])(https?:\/\/[^"']+)\2/gi, (_m, attrs, q, url) => {
      return `<img${attrs}src=${q}/api/email/img?url=${encodeURIComponent(url)}${q}`
    })
    // Client-side fallback for inline `cid:` attachment refs. When the thread
    // fetch endpoint replaces cid: URLs with data: URLs server-side this is a
    // no-op; when the backend hasn't landed yet, any surviving cid: refs would
    // trigger CSP violations + broken image icons. Swap them for a 1x1
    // transparent SVG so the layout doesn't shift and the console stays clean.
    clean = clean.replace(/<img\b([^>]*?)\bsrc\s*=\s*(["'])cid:[^"']+\2/gi, (_m, attrs) => {
      const placeholder = 'data:image/svg+xml;utf8,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="1" height="1"/>')
      return `<img${attrs}src="${placeholder}"`
    })
    // Force all anchors to open in new tab with no referrer.
    clean = clean.replace(/<a\b([^>]*?)>/gi, (_m, attrs) => {
      const hasTarget = /\btarget\s*=/.test(attrs)
      const hasRel = /\brel\s*=/.test(attrs)
      let updated = attrs
      if (!hasTarget) updated += ' target="_blank"'
      if (!hasRel) updated += ' rel="noopener noreferrer nofollow"'
      return `<a${updated}>`
    })
    return clean
  }, [props.html])

  // NOTE: no <script> in srcDoc — sandbox does NOT include allow-scripts, so
  // any script would be blocked anyway. Auto-sizing is done from the parent
  // side via the iframe's load event + periodic polling of its scrollHeight
  // (which works because srcDoc inherits same-origin-to-parent only when
  // allow-same-origin is set — which we do set, intentionally, to read the
  // content height. No parsed script = no JS exec context from the email).
  const srcDoc = React.useMemo(() => `<!doctype html>
<html><head>
<meta charset="utf-8" />
<base target="_blank" />
<meta name="referrer" content="no-referrer" />
<!-- CSP: block scripts at the browser level (belt & suspenders vs. the
     sandbox attribute) so residual <script> tags in weirdly-formatted emails
     don't trigger the 'Blocked script execution in about:srcdoc' console
     warning. The sandbox already prevents execution; this stops the log. -->
<meta http-equiv="Content-Security-Policy" content="default-src 'self' data: blob: https:; script-src 'none'; style-src 'unsafe-inline' https:; img-src * data: blob:; font-src * data: https:; connect-src 'none'; frame-src 'none'; object-src 'none'; base-uri 'none'; form-action 'none';" />
<style>
  :root { color-scheme: light; }
  html, body { margin:0; padding:0; font-family: -apple-system, Segoe UI, Helvetica, Arial, sans-serif; font-size: 14px; line-height: 1.55; color: #0f172a; background:#fff; overflow-x:auto; }
  body { padding: 14px 18px; }
  img { max-width: 100%; height: auto; }
  a { color: #0a4f95; }
  blockquote { border-left: 3px solid #e2e8f0; padding-left: 12px; color: #475569; margin: 8px 0; }
  pre, code { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
  table { max-width: 100%; }
</style>
</head><body>${sanitized}</body></html>`, [sanitized])

  // Parent-side auto-size: read the iframe's body scrollHeight on load plus a
  // few timed samples to catch late-loading images. No JS in the frame.
  const measure = React.useCallback(() => {
    const el = iframeRef.current
    if (!el) return
    try {
      const doc = el.contentDocument
      if (!doc) return
      const h = Math.max(
        doc.body?.scrollHeight || 0,
        doc.documentElement?.scrollHeight || 0,
        doc.body?.offsetHeight || 0,
        doc.documentElement?.offsetHeight || 0,
      )
      if (h <= 0) return
      // No upper bound — long marketing emails can be 6k-12k px tall, the
      // outer .inbox-reader-thread owns the scrollbar. Min 100 so the iframe
      // doesn't collapse while content is still loading.
      const target = Math.max(h, 100)
      // Hysteresis threshold. Without it, setting iframe height causes body
      // reflow, which triggers ResizeObserver with a slightly-different height,
      // which sets iframe height again, feedback-looping upward a few pixels
      // per tick. Only accept changes of at least 3px so sub-pixel drift
      // doesn't keep nudging the iframe taller.
      setHeight(prev => Math.abs(target - prev) < 3 ? prev : target)
    } catch { /* cross-origin — shouldn't happen with srcDoc + same-origin sandbox */ }
  }, [])

  // Track all per-load observers + timers so cleanup is bulletproof.
  // Without this, opening & closing many threads over a long session
  // accumulates ResizeObservers + orphan setTimeouts in the renderer,
  // which can contribute to tab-hang / crash (Chrome shows "This page
  // is having a problem"). See cleanup effect below.
  const loadResourcesRef = React.useRef<{
    ro?: ResizeObserver
    timers: number[]
    imgListeners: Array<{ img: HTMLImageElement; handler: () => void }>
  }>({ timers: [], imgListeners: [] })

  const cleanupLoadResources = React.useCallback(() => {
    const r = loadResourcesRef.current
    try { r.ro?.disconnect() } catch { /* ignore */ }
    r.ro = undefined
    for (const t of r.timers) { try { clearTimeout(t) } catch { /* ignore */ } }
    r.timers = []
    for (const { img, handler } of r.imgListeners) {
      try {
        img.removeEventListener('load', handler)
        img.removeEventListener('error', handler)
      } catch { /* ignore */ }
    }
    r.imgListeners = []
  }, [])

  const onLoad = React.useCallback(() => {
    // Purge any prior-load state before starting fresh (switching messages
    // within a thread or re-rendering on content change both trigger onLoad).
    cleanupLoadResources()
    measure()
    const timers: number[] = []
    timers.push(window.setTimeout(measure, 120))
    timers.push(window.setTimeout(measure, 400))
    timers.push(window.setTimeout(measure, 1000))
    timers.push(window.setTimeout(measure, 2500))
    loadResourcesRef.current.timers = timers
    try {
      const el = iframeRef.current
      const body = el?.contentDocument?.body
      if (body && typeof ResizeObserver !== 'undefined') {
        const ro = new ResizeObserver(() => measure())
        ro.observe(body)
        loadResourcesRef.current.ro = ro
        // Re-measure as each inline image loads. Tracked so we can remove
        // the listeners on cleanup (once:true helps but doesn't cover
        // images that never resolve before unmount).
        body.querySelectorAll('img').forEach((img) => {
          if (!(img as HTMLImageElement).complete) {
            const el = img as HTMLImageElement
            const handler = () => measure()
            el.addEventListener('load', handler, { once: true })
            el.addEventListener('error', handler, { once: true })
            loadResourcesRef.current.imgListeners.push({ img: el, handler })
          }
        })
      }
    } catch { /* defensive — shouldn't fail on srcDoc */ }
  }, [measure, cleanupLoadResources])

  // Clean up every attached observer / timer / listener on unmount or when
  // the sanitized content changes (the next onLoad will set up fresh ones).
  React.useEffect(() => cleanupLoadResources, [sanitized, cleanupLoadResources])

  // Re-measure on sanitized content change
  React.useEffect(() => {
    setHeight(320)
  }, [sanitized])

  return (
    <iframe
      ref={iframeRef}
      className={props.className || 'inbox-safe-frame'}
      srcDoc={srcDoc}
      sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin"
      referrerPolicy="no-referrer"
      title="Email content"
      onLoad={onLoad}
      style={{ width: '100%', height: `${height}px`, border: 'none', display: 'block' }}
    />
  )
}


// --- UndoSendToast ---------------------------------------------------------
// Global 8-second undo toast. Registers a window.__harUndoSend manager that
// the ComposeModal talks to. When the user clicks Send, ComposeModal calls
// .enqueue({ fire, restore }) and closes itself. This toast starts a visible
// 8s countdown; on Undo, it cancels the fire and calls onUndo(restoreCtx)
// so the App shell can reopen the compose with the same content. Otherwise
// the fire() runs and a "Sent" toast briefly takes its place.

function UndoSendToast(props: {
  onUndoRestore: (ctx: ComposeContext) => void
  onToast: (msg: string) => void
}) {
  type Queued = { fire: () => Promise<void>; restore: ComposeContext }
  const [queued, setQueued] = React.useState<Queued | null>(null)
  const [progress, setProgress] = React.useState(100)
  const timeoutRef = React.useRef<number | null>(null)
  const intervalRef = React.useRef<number | null>(null)

  // Register the global enqueue API on mount.
  React.useEffect(() => {
    const win: any = window as any
    win.__harUndoSend = {
      enqueue: (q: Queued) => {
        // If something is already queued, flush it immediately.
        if (timeoutRef.current) {
          window.clearTimeout(timeoutRef.current)
          timeoutRef.current = null
        }
        if (intervalRef.current) {
          window.clearInterval(intervalRef.current)
          intervalRef.current = null
        }
        setQueued(q)
        setProgress(100)
        const started = Date.now()
        const duration = 8000
        intervalRef.current = window.setInterval(() => {
          const elapsed = Date.now() - started
          const pct = Math.max(0, 100 - (elapsed / duration) * 100)
          setProgress(pct)
        }, 100)
        timeoutRef.current = window.setTimeout(async () => {
          // 8s elapsed — stop the undo toast UI first so the user sees a
          // quick handoff to the "Email sent" confirmation pill, then fire
          // the actual send in the background.
          if (intervalRef.current) window.clearInterval(intervalRef.current)
          timeoutRef.current = null
          intervalRef.current = null
          setQueued(null)
          try {
            await q.fire()
            props.onToast('Email sent')
          } catch (e) {
            console.error('undo-send fire error:', e)
          }
        }, duration)
      },
    }
    return () => {
      // Real cleanup on unmount: cancel in-flight timers + detach the
      // global so a stale closure capturing the unmounted component's
      // setState can't fire. StrictMode's double-invoke immediately
      // re-runs this effect and re-registers the global, so nothing is
      // actually lost in dev; in production this prevents a leak when
      // the user navigates away mid-send.
      if (timeoutRef.current) { window.clearTimeout(timeoutRef.current); timeoutRef.current = null }
      if (intervalRef.current) { window.clearInterval(intervalRef.current); intervalRef.current = null }
      try { win.__harUndoSend = undefined } catch { /* ignore */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const undo = () => {
    if (timeoutRef.current) window.clearTimeout(timeoutRef.current)
    if (intervalRef.current) window.clearInterval(intervalRef.current)
    timeoutRef.current = null
    intervalRef.current = null
    const q = queued
    setQueued(null)
    if (q) props.onUndoRestore(q.restore)
  }

  if (!queued) return null

  return (
    <div className="inbox-undo-toast" role="status" aria-live="polite">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
        <polyline points="22 4 12 14.01 9 11.01"/>
      </svg>
      <span className="inbox-undo-toast-text">Email sent</span>
      <button type="button" className="inbox-undo-toast-btn" onClick={undo}>Undo</button>
      <div className="inbox-undo-toast-progress" aria-hidden="true">
        <div className="inbox-undo-toast-progress-fill" style={{ width: `${progress}%` }} />
      </div>
    </div>
  )
}

// ============================================================================
// Settings · Email section — Signatures / Templates / BCC / Scheduled list.
// These are rendered inside the Preferences > General > Email sub-tab and
// only mount when that sub-tab is active, so their fetch cost is lazy.
// ============================================================================

// Merge-field reference (shown in the Templates editor for authors).
const EMAIL_MERGE_FIELDS: Array<{ token: string; description: string }> = [
  { token: '{{firstName}}', description: 'Recipient first name' },
  { token: '{{lastName}}', description: 'Recipient last name' },
  { token: '{{fullName}}', description: 'Recipient full name' },
  { token: '{{email}}', description: 'Recipient email address' },
  { token: '{{dealTitle}}', description: 'Linked deal title' },
  { token: '{{dealValue}}', description: 'Linked deal value (formatted)' },
  { token: '{{companyName}}', description: 'Linked client / account' },
  { token: '{{userFirstName}}', description: 'Your first name' },
  { token: '{{userFullName}}', description: 'Your full name' },
  { token: '{{today}}', description: "Today's date" },
]

// Shared sanitize helper — used in signature + template previews so the
// compose/reader sanitization stays consistent across surfaces. The allowed
// tag set matches the editor's output capabilities (TipTap StarterKit + Link).
function sanitizePreviewHtml(raw: string): string {
  return DOMPurify.sanitize(raw || '', {
    ALLOWED_TAGS: ['b','strong','i','em','u','a','br','span','p','ul','ol','li','div','h1','h2','h3','img','blockquote','code','pre'],
    ALLOWED_ATTR: ['href','target','rel','src','alt','style','class'],
  })
}

// Tiny HTML preview component wrapping DOMPurify so lint hooks warn once here
// instead of on every use-site. All content passed through this is sanitized.
function SanitizedHtmlPreview(props: { html: string; className?: string }) {
  const safe = React.useMemo(() => sanitizePreviewHtml(props.html || ''), [props.html])
  // eslint-disable-next-line react/no-danger
  return <div className={props.className} dangerouslySetInnerHTML={{ __html: safe }} />
}

// ---------------------------------------------------------------------------
// RunningClockBadge — the fixed-position "you're clocked in" pill at the
// bottom-left. It used to be an inline IIFE in App's JSX, and the 1-second
// tick was driven by a top-level `setTimeTick` useState which forced the
// entire App component (~50K lines) to re-render every second. After a
// few hours that pressure caused Chromium's Crashpad_HandlerDidNotRespond
// tab crash. Pulling the ticker into its own component means only this
// tiny subtree re-renders per second — App stays quiet when nothing else
// has actually changed.
// ---------------------------------------------------------------------------

// Elapsed time for the header shift-clock button. Same visibility-gated
// ticker pattern — 30s resolution is enough for h:mm.
function TcElapsed({ since }: { since: string }) {
  const [now, setNow] = React.useState(() => Date.now())
  React.useEffect(() => {
    let id: number | null = null
    const start = () => { if (id == null) { setNow(Date.now()); id = window.setInterval(() => setNow(Date.now()), 30_000) } }
    const stop = () => { if (id != null) { window.clearInterval(id); id = null } }
    const onVis = () => { if (document.visibilityState === 'visible') start(); else stop() }
    onVis()
    document.addEventListener('visibilitychange', onVis)
    return () => { stop(); document.removeEventListener('visibilitychange', onVis) }
  }, [])
  const mins = Math.max(0, Math.floor((now - new Date(since).getTime()) / 60000))
  return <>{Math.floor(mins / 60)}:{String(mins % 60).padStart(2, '0')}</>
}

type RunningClockEntry = { id: number; clock_in_at: string; deal_id?: number | null; payroll_category?: string }

function RunningClockBadge(props: {
  entry: RunningClockEntry
  disabled: boolean
  onOpenDeal: () => void
  onClockOut: () => void
}) {
  const { entry, disabled, onOpenDeal, onClockOut } = props
  const [now, setNow] = React.useState(() => Date.now())
  React.useEffect(() => {
    // Visibility-gate so a backgrounded tab doesn't burn a setState per
    // second while nobody's looking. Re-syncs on focus.
    let id: number | null = null
    const start = () => {
      if (id != null) return
      setNow(Date.now())
      id = window.setInterval(() => setNow(Date.now()), 1000)
    }
    const stop = () => {
      if (id != null) { window.clearInterval(id); id = null }
    }
    const onVis = () => { if (document.visibilityState === 'visible') start(); else stop() }
    onVis()
    document.addEventListener('visibilitychange', onVis)
    return () => {
      stop()
      document.removeEventListener('visibilitychange', onVis)
    }
  }, [])
  const startMs = new Date(entry.clock_in_at).getTime()
  const secs = Math.max(0, Math.floor((now - startMs) / 1000))
  const hh = Math.floor(secs / 3600)
  const mm = Math.floor((secs % 3600) / 60)
  const ss = secs % 60
  const label = `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`
  const onDeal = !!entry.deal_id
  return (
    <div
      aria-live="polite"
      style={{
        position: 'fixed', left: 20, bottom: 20, zIndex: 10005,
        display: 'flex', alignItems: 'center', gap: 10,
        background: '#0f172a', color: '#fff', padding: '8px 12px',
        borderRadius: 999, boxShadow: '0 6px 16px rgba(0,0,0,0.22)',
        fontSize: '0.82rem', fontWeight: 500,
      }}
    >
      <span
        style={{
          width: 8, height: 8, borderRadius: '50%',
          background: '#ef4444', boxShadow: '0 0 0 0 rgba(239,68,68,0.6)',
          animation: 'pulse 1.5s infinite',
        }}
      />
      <span style={{ fontVariantNumeric: 'tabular-nums', minWidth: 68 }}>{label}</span>
      {onDeal && (
        <button
          onClick={onOpenDeal}
          style={{ background: 'none', border: 'none', color: '#cbd5e1', fontSize: '0.74rem', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
        >Deal #{entry.deal_id}</button>
      )}
      <button
        onClick={onClockOut}
        disabled={disabled}
        style={{ padding: '4px 10px', background: '#c5221f', color: '#fff', border: 'none', borderRadius: 999, fontSize: '0.74rem', fontWeight: 600, cursor: 'pointer' }}
      >Clock out</button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// CallPlayback — premium audio + karaoke transcript for logged call cards.
// Takes a recording URL, a fallback flat transcript, and an optional word
// array shaped like the Grok STT response (compact form: {t, s, e, sp}).
// When words are present we render each word as a clickable span and
// highlight the one whose [s, e] contains audio.currentTime, plus dim the
// already-spoken words so the eye has a clear running cursor. Without
// words we render the flat transcript (no UI jank). rAF drives highlight
// updates (smoother than the native "timeupdate" event which fires every
// ~250ms). Auto-scrolls the active word to center; click a word to seek.
// ---------------------------------------------------------------------------

type CallPlaybackWord = { t: string; s: number; e: number; sp?: number }

function CallPlayback(props: {
  recordingUrl: string | null
  transcript: string | null
  words: CallPlaybackWord[] | null
  summary?: string | null
}) {
  const { recordingUrl, transcript, summary } = props
  // Play from a same-origin authed blob, not the raw /api URL on the native
  // element (Safari/iOS choke on cookie auth + range there). See useRecordingBlob.
  const { blobUrl: recordingBlobUrl } = useRecordingBlob(recordingUrl)
  const audioRef = React.useRef<HTMLAudioElement | null>(null)
  const rafRef = React.useRef<number | null>(null)
  const scrollerRef = React.useRef<HTMLDivElement | null>(null)
  const [activeIndex, setActiveIndex] = React.useState<number>(-1)
  const [isPlaying, setIsPlaying] = React.useState(false)
  const [autoScroll, setAutoScroll] = React.useState(true)
  const lastIndexRef = React.useRef<number>(-1)

  // Stabilize the words reference across parent re-renders. The parent
  // builds `words` via JSON.parse on every render, so the array identity
  // changes every time — even when the underlying timings are identical.
  // Without this memo, the rAF tick effect below would tear down and
  // rebuild on every parent render, and activeIndex would reset to -1,
  // producing the "karaoke works sometimes" bug the user reported.
  //
  // Keying the memo on the length + first+last word start times is
  // cheap and uniquely identifies a given transcript without deep-
  // comparing the whole array. `words` is always pushed in order from
  // a single Grok response, so these three values collide only if
  // someone transcribes the EXACT same audio twice — a no-op anyway.
  const stableWords = React.useMemo(() => props.words, [
    props.words?.length,
    props.words?.[0]?.s,
    props.words?.[(props.words?.length || 1) - 1]?.s,
  ])
  // Hold the live array in a ref so the tick function doesn't need it
  // in its closure (and thus the rAF effect doesn't need it in deps).
  const wordsRef = React.useRef<CallPlaybackWord[] | null>(stableWords || null)
  React.useEffect(() => { wordsRef.current = stableWords || null }, [stableWords])

  // Active word = the last one whose `start` has already passed. That way
  // the highlight fills smoothly across the gaps between words (Grok's
  // `end` timestamps are tight to the word, leaving 30-300ms dead zones
  // between words where the old algorithm un-highlighted everything).
  // We also nudge the lookup time forward by ~120ms so the highlight
  // appears right as the ear hears the word rather than after. ~60ms
  // covers browser audio buffer latency, ~60ms covers phoneme-onset
  // delay on 16kHz MP3 RC recordings.
  const LEAD_MS = 0.12
  const findWordIndex = React.useCallback((rawTime: number): number => {
    const ws = wordsRef.current
    if (!ws || ws.length === 0) return -1
    const time = rawTime + LEAD_MS
    const prev = lastIndexRef.current
    // Fast path: audio usually advances ~16ms per rAF tick. Check if we're
    // still inside the current word's [start, next_start) window, then
    // scan up to 4 words ahead. Falls through to binary search on seek
    // or large jumps.
    if (prev >= 0 && prev < ws.length) {
      const curStart = ws[prev].s
      const nextStart = prev + 1 < ws.length ? ws[prev + 1].s : Infinity
      if (time >= curStart && time < nextStart) return prev
      if (time >= nextStart) {
        const cap = Math.min(prev + 5, ws.length)
        for (let i = prev + 1; i < cap; i++) {
          const thisStart = ws[i].s
          const nxt = i + 1 < ws.length ? ws[i + 1].s : Infinity
          if (time >= thisStart && time < nxt) return i
        }
      }
    }
    // Binary search: greatest index whose start <= time
    let lo = 0, hi = ws.length - 1
    while (lo <= hi) {
      const mid = (lo + hi) >> 1
      if (ws[mid].s <= time) lo = mid + 1
      else hi = mid - 1
    }
    return hi
  }, [])

  // The rAF effect deliberately has an empty deps array so it ONLY runs
  // on mount and cleans up on unmount. The previous version re-ran on
  // every `words` prop reference change, which happened on every parent
  // render — killing the rAF loop and causing the intermittent-highlight
  // bug. Anything mutable (`words`, `findWordIndex`) is accessed via
  // refs inside `tick` so the closure stays fresh without re-binding.
  React.useEffect(() => {
    const audio = audioRef.current
    if (!audio) return
    const tick = () => {
      const ws = wordsRef.current
      if (!ws || ws.length === 0) {
        rafRef.current = requestAnimationFrame(tick)
        return
      }
      const idx = findWordIndex(audio.currentTime)
      if (idx !== lastIndexRef.current) {
        lastIndexRef.current = idx
        setActiveIndex(idx)
      }
      rafRef.current = requestAnimationFrame(tick)
    }
    const onPlay = () => {
      setIsPlaying(true)
      if (rafRef.current == null) rafRef.current = requestAnimationFrame(tick)
    }
    const onPause = () => {
      setIsPlaying(false)
      if (rafRef.current != null) { cancelAnimationFrame(rafRef.current); rafRef.current = null }
    }
    const onSeek = () => { lastIndexRef.current = -1 }
    audio.addEventListener('play', onPlay)
    audio.addEventListener('pause', onPause)
    audio.addEventListener('ended', onPause)
    audio.addEventListener('seeking', onSeek)
    return () => {
      audio.removeEventListener('play', onPlay)
      audio.removeEventListener('pause', onPause)
      audio.removeEventListener('ended', onPause)
      audio.removeEventListener('seeking', onSeek)
      if (rafRef.current != null) { cancelAnimationFrame(rafRef.current); rafRef.current = null }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    if (!autoScroll || activeIndex < 0) return
    const scroller = scrollerRef.current
    if (!scroller) return
    const el = scroller.querySelector<HTMLSpanElement>(`[data-widx="${activeIndex}"]`)
    if (!el) return
    const sRect = scroller.getBoundingClientRect()
    const eRect = el.getBoundingClientRect()
    const target = eRect.top - sRect.top - sRect.height / 2 + eRect.height / 2
    scroller.scrollBy({ top: target, behavior: 'smooth' })
  }, [activeIndex, autoScroll])

  const seekTo = (secs: number) => {
    const audio = audioRef.current
    if (!audio) return
    const target = Math.max(0, secs)
    const applySeek = () => {
      try {
        audio.currentTime = target
        // Force the karaoke highlight to re-evaluate on the next rAF tick.
        lastIndexRef.current = -1
        const idx = findWordIndex(target)
        if (idx !== activeIndex) setActiveIndex(idx)
        audio.play().catch(() => {})
      } catch { /* ignore */ }
    }
    // With `preload="metadata"` the browser may not have loaded enough yet
    // to honor an assignment to `currentTime`. Wait for metadata in that
    // case, otherwise seek immediately.
    if (audio.readyState < 1 /* HAVE_METADATA */) {
      const onReady = () => { audio.removeEventListener('loadedmetadata', onReady); applySeek() }
      audio.addEventListener('loadedmetadata', onReady)
      try { audio.load() } catch { /* ignore */ }
    } else {
      applySeek()
    }
  }

  const turns = React.useMemo(() => {
    const ws = stableWords
    if (!ws || ws.length === 0) return [] as { speaker: number | null; words: { w: CallPlaybackWord; idx: number }[] }[]
    const out: { speaker: number | null; words: { w: CallPlaybackWord; idx: number }[] }[] = []
    let current: { speaker: number | null; words: { w: CallPlaybackWord; idx: number }[] } | null = null
    ws.forEach((w, i) => {
      const sp = w.sp ?? null
      if (!current || current.speaker !== sp) {
        current = { speaker: sp, words: [] }
        out.push(current)
      }
      current.words.push({ w, idx: i })
    })
    return out
  }, [stableWords])

  const hasWords = !!(stableWords && stableWords.length > 0)
  const hasTranscriptText = !!(transcript && transcript.trim())
  if (!recordingUrl && !hasTranscriptText && !summary) return null

  return (
    <div className="callpb">
      {recordingUrl && (
        <div className="callpb-audio-wrap">
          <audio ref={audioRef} src={recordingBlobUrl ?? undefined} preload="metadata" controls />
          {hasWords && (
            <label className="callpb-autoscroll" title="Keep the current word centered in the transcript">
              <input type="checkbox" checked={autoScroll} onChange={(e) => setAutoScroll(e.target.checked)} />
              <span>Follow transcript</span>
            </label>
          )}
        </div>
      )}

      {summary && (
        <div className="callpb-summary">
          <div className="callpb-summary-header">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
            AI Summary
          </div>
          <p className="callpb-summary-text">{summary}</p>
        </div>
      )}

      {(hasWords || hasTranscriptText) && (
        <div className="callpb-transcript" ref={scrollerRef}>
          {hasWords ? (
            turns.map((turn, ti) => (
              <div key={ti} className={`callpb-turn${turn.speaker === 1 ? ' callpb-turn-b' : ''}`}>
                {turn.speaker !== null && (
                  <div className="callpb-turn-label">Speaker {turn.speaker + 1}</div>
                )}
                <p className="callpb-turn-body">
                  {turn.words.map(({ w, idx }) => (
                    <React.Fragment key={idx}>
                      <span
                        data-widx={idx}
                        className={`callpb-word${idx === activeIndex ? ' callpb-word-active' : ''}${idx < activeIndex ? ' callpb-word-spoken' : ''}`}
                        onClick={(e) => { e.stopPropagation(); seekTo(w.s) }}
                        title={`Jump to ${w.s.toFixed(1)}s`}
                      >{w.t}</span>
                      {' '}
                    </React.Fragment>
                  ))}
                </p>
              </div>
            ))
          ) : (
            <p className="callpb-flat">{transcript}</p>
          )}
        </div>
      )}
    </div>
  )
}

// --- EmailSignaturesSection -------------------------------------------------

function EmailSignaturesSection(props: { onToast: (msg: string) => void }) {
  const [loading, setLoading] = React.useState(true)
  const [unavailable, setUnavailable] = React.useState(false)
  const [items, setItems] = React.useState<EmailSignature[]>([])
  const [editing, setEditing] = React.useState<Partial<EmailSignature> | null>(null)
  const [saving, setSaving] = React.useState(false)

  const load = React.useCallback(async () => {
    setLoading(true)
    const res = await fetchEmailSignatures()
    setItems(res.signatures)
    setUnavailable(!!res.unavailable)
    setLoading(false)
  }, [])

  React.useEffect(() => { load() }, [load])

  const startCreate = () => setEditing({ id: undefined, name: '', html: '', is_default: items.length === 0 })
  const startEdit = (sig: EmailSignature) => setEditing({ ...sig })

  const save = async () => {
    if (!editing || !editing.name?.trim() || !editing.html?.trim()) return
    setSaving(true)
    const ok = editing.id
      ? await updateEmailSignature(editing.id, { name: editing.name, html: editing.html, is_default: !!editing.is_default })
      : await createEmailSignature({ name: editing.name, html: editing.html, is_default: !!editing.is_default })
    setSaving(false)
    if (ok) { props.onToast(editing.id ? 'Signature updated' : 'Signature created'); setEditing(null); load() }
    else props.onToast('Save failed — try again')
  }

  const remove = async (id: number) => {
    if (!window.confirm('Delete this signature?')) return
    const ok = await deleteEmailSignature(id)
    if (ok) { props.onToast('Signature deleted'); load() }
    else props.onToast('Delete failed')
  }

  const setDefault = async (id: number) => {
    const ok = await updateEmailSignature(id, { is_default: true })
    if (ok) { props.onToast('Default signature updated'); load() }
  }

  return (
    <div className="prefs-card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <h2 className="prefs-card-title" style={{ margin: 0 }}>Signatures</h2>
        {!unavailable && !loading && (
          <button type="button" className="prefs-save-btn" onClick={startCreate}>+ New signature</button>
        )}
      </div>
      <p className="prefs-card-desc" style={{ marginBottom: 14 }}>
        Your default signature is auto-appended to every new email, reply, and forward.
      </p>

      {unavailable ? (
        <div className="email-prefs-empty">
          <div className="email-prefs-empty-title">Coming soon</div>
          <div>Signatures will be available once the email features API is deployed.</div>
        </div>
      ) : loading ? (
        <div className="email-prefs-empty">
          <span className="bp-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
          Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="email-prefs-empty">
          <div className="email-prefs-empty-title">No signatures yet</div>
          <div style={{ marginBottom: 10 }}>Create one to be auto-appended to every message you send.</div>
          <button type="button" className="prefs-save-btn" onClick={startCreate}>Create signature</button>
        </div>
      ) : (
        <ul className="email-prefs-list">
          {items.map(s => (
            <li key={s.id} className="email-prefs-row">
              <div className="email-prefs-row-main">
                <div className="email-prefs-row-title">
                  {s.name}
                  {s.is_default ? <span className="email-prefs-badge email-prefs-badge-default">Default</span> : null}
                </div>
                <SanitizedHtmlPreview className="email-prefs-row-sub" html={s.html || ''} />
              </div>
              <div className="email-prefs-row-actions">
                {!s.is_default && (
                  <button type="button" className="email-prefs-btn" onClick={() => setDefault(s.id)}>Set default</button>
                )}
                <button type="button" className="email-prefs-btn" onClick={() => startEdit(s)}>Edit</button>
                <button type="button" className="email-prefs-btn email-prefs-btn-danger" onClick={() => remove(s.id)}>Delete</button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <div className="email-prefs-editor-overlay" onClick={() => { if (!saving) setEditing(null) }}>
          <div className="email-prefs-editor" onClick={(e) => e.stopPropagation()}>
            <div className="email-prefs-editor-head">
              <div className="email-prefs-editor-title">{editing.id ? 'Edit signature' : 'New signature'}</div>
              <button className="inbox-compose-icon-btn" onClick={() => setEditing(null)} aria-label="Close"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
            </div>
            <div className="email-prefs-editor-body">
              <div className="prefs-field prefs-field-full">
                <label htmlFor="sig-name">Name</label>
                <input id="sig-name" type="text" value={editing.name || ''} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="e.g. Default, Client-facing, Follow-up" />
              </div>
              <div className="prefs-field prefs-field-full">
                <label htmlFor="sig-html">HTML</label>
                <textarea id="sig-html" rows={8} value={editing.html || ''} onChange={(e) => setEditing({ ...editing, html: e.target.value })} placeholder="&lt;p&gt;Best,&lt;br/&gt;&lt;strong&gt;Max Ritchie&lt;/strong&gt;&lt;br/&gt;VP, BotPros&lt;/p&gt;" style={{ fontFamily: 'monospace', fontSize: '0.82rem' }} />
              </div>
              <div className="prefs-field prefs-field-full">
                <label>Preview</label>
                <SanitizedHtmlPreview className="email-prefs-preview" html={editing.html || '<em style="color:#94a3b8">Preview appears here</em>'} />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.84rem', color: '#334155' }}>
                <input type="checkbox" checked={!!editing.is_default} onChange={(e) => setEditing({ ...editing, is_default: e.target.checked })} />
                Set as default signature
              </label>
            </div>
            <div className="email-prefs-editor-foot">
              <button type="button" className="inbox-compose-secondary" onClick={() => setEditing(null)} disabled={saving}>Cancel</button>
              <button type="button" className="inbox-compose-send" onClick={save} disabled={saving || !editing.name?.trim() || !editing.html?.trim()}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// --- SystemEmailSignatureSection -------------------------------------------
//
// Per-user signature appended by the WORKER to programmatic system emails
// (send-application-link, public-proposal "send to homeowner"). Distinct
// from the compose-time multi-sig store above — admin sets this once and
// the worker auto-applies it to anything env.EMAIL.send'd on their behalf.
// Persists via /api/me/email-signature; saves on blur.

function SystemEmailSignatureSection(props: { onToast: (msg: string) => void }) {
  const [loading, setLoading] = React.useState(true)
  const [sig, setSig] = React.useState<UserEmailSignature | null>(null)
  const [htmlDraft, setHtmlDraft] = React.useState('')
  const [plainDraft, setPlainDraft] = React.useState('')
  const [saving, setSaving] = React.useState(false)
  const [imageUploading, setImageUploading] = React.useState(false)
  const fileInputRef = React.useRef<HTMLInputElement | null>(null)

  React.useEffect(() => {
    let cancelled = false
    fetchMyEmailSignature().then((s) => {
      if (cancelled) return
      setSig(s)
      setHtmlDraft(s?.html || '')
      setPlainDraft(s?.plain || '')
      setLoading(false)
    })
    return () => { cancelled = true }
  }, [])

  const save = React.useCallback(async (overrides?: Partial<UserEmailSignature>) => {
    setSaving(true)
    try {
      const html = overrides?.html !== undefined ? overrides.html : htmlDraft
      const plain = overrides?.plain !== undefined ? overrides.plain : plainDraft
      const imageKey = overrides?.image_r2_key !== undefined ? overrides.image_r2_key : sig?.image_r2_key ?? null
      const r = await saveMyEmailSignature({ html, plain, image_r2_key: imageKey })
      setSig({
        user_id: sig?.user_id || 0,
        html: html || null,
        plain: plain || null,
        image_r2_key: imageKey,
        updated_at: r.updated_at || new Date().toISOString(),
      })
      props.onToast('Signature saved')
    } catch (e: any) {
      props.onToast(e?.message || 'Failed to save signature')
    } finally {
      setSaving(false)
    }
  }, [htmlDraft, plainDraft, sig, props])

  const remove = async () => {
    if (!window.confirm('Delete your system email signature?')) return
    setSaving(true)
    try {
      await deleteMyEmailSignature()
      setSig(null)
      setHtmlDraft('')
      setPlainDraft('')
      props.onToast('Signature deleted')
    } catch (e: any) {
      props.onToast(e?.message || 'Failed to delete signature')
    } finally {
      setSaving(false)
    }
  }

  const onPickImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 1024 * 1024) {
      props.onToast('Image must be 1 MB or smaller')
      return
    }
    setImageUploading(true)
    try {
      const r = await uploadMyEmailSignatureImage(file)
      // Persist the new image key alongside the current draft body so a
      // subsequent blur doesn't clobber the upload.
      await save({ image_r2_key: r.key })
    } catch (err: any) {
      props.onToast(err?.message || 'Failed to upload image')
    } finally {
      setImageUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  // Sanitize the HTML preview — prevents inline script execution and other
  // injection vectors when an admin pastes hostile HTML by accident. The
  // server still emits the raw HTML to recipients (email clients have
  // their own sanitizers); this is purely a CRM-side preview safeguard.
  const previewHtml = React.useMemo(() => DOMPurify.sanitize(htmlDraft || ''), [htmlDraft])

  return (
    <div className="prefs-card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, gap: 12, flexWrap: 'wrap' }}>
        <h2 className="prefs-card-title" style={{ margin: 0 }}>System email signature</h2>
        {sig && (
          <button type="button" className="prefs-save-btn" onClick={remove} disabled={saving} style={{ background: '#fff', color: '#b91c1c', border: '1px solid #fecaca' }}>
            Delete signature
          </button>
        )}
      </div>
      <p className="prefs-card-desc" style={{ marginBottom: 12 }}>
        Auto-appended to system emails sent on your behalf — application invites, homeowner proposal links, and other one-click sends. Saves on blur.
      </p>
      {loading ? (
        <div style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: 13 }}>Loading…</div>
      ) : (
        <>
          <label style={{ display: 'block', fontSize: 12, color: '#475569', fontWeight: 500, marginBottom: 4 }}>HTML signature</label>
          <textarea
            value={htmlDraft}
            onChange={(e) => setHtmlDraft(e.target.value)}
            onBlur={() => { if ((sig?.html || '') !== htmlDraft) save() }}
            placeholder='<p style="font-size:13px;color:#475569;margin:0">Max Ritchie<br/>BotPros<br/><a href="tel:5555551212">555-555-1212</a></p>'
            style={{ width: '100%', minHeight: 120, padding: '8px 10px', fontSize: 13, fontFamily: 'monospace', border: '1px solid #cbd6e2', borderRadius: 4, resize: 'vertical' }}
          />
          <label style={{ display: 'block', fontSize: 12, color: '#475569', fontWeight: 500, marginTop: 12, marginBottom: 4 }}>Plain-text fallback</label>
          <textarea
            value={plainDraft}
            onChange={(e) => setPlainDraft(e.target.value)}
            onBlur={() => { if ((sig?.plain || '') !== plainDraft) save() }}
            placeholder={'Max Ritchie\nBotPros\n555-555-1212'}
            style={{ width: '100%', minHeight: 80, padding: '8px 10px', fontSize: 13, border: '1px solid #cbd6e2', borderRadius: 4, resize: 'vertical' }}
          />
          <div style={{ marginTop: 12, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              onChange={onPickImage}
              style={{ display: 'none' }}
            />
            <button
              type="button"
              className="prefs-save-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={imageUploading || saving}
              style={{ background: '#fff', color: '#1976d2', border: '1px solid #cbd6e2' }}
            >
              {imageUploading ? 'Uploading…' : sig?.image_r2_key ? 'Replace inline image' : '+ Upload inline image'}
            </button>
            {sig?.image_r2_key && (
              <span style={{ fontSize: 11, color: '#64748b', fontFamily: 'monospace' }}>
                {sig.image_r2_key}
              </span>
            )}
            {saving && <span style={{ fontSize: 11, color: '#94a3b8' }}>Saving…</span>}
            {!saving && sig?.updated_at && (
              <span style={{ fontSize: 11, color: '#94a3b8' }}>Saved {sig.updated_at.slice(0, 16).replace('T', ' ')}</span>
            )}
          </div>
          <div style={{ marginTop: 14, padding: 12, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 4 }}>
            <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Preview</div>
            {htmlDraft.trim() ? (
              <div style={{ fontSize: 13, lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: previewHtml }} />
            ) : (
              <div style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>(Add an HTML signature above to preview)</div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

// --- EmailTemplatesSection --------------------------------------------------

function EmailTemplatesSection(props: { onToast: (msg: string) => void; userEmail: string }) {
  const [loading, setLoading] = React.useState(true)
  const [unavailable, setUnavailable] = React.useState(false)
  const [items, setItems] = React.useState<CrmEmailTemplate[]>([])
  const [editing, setEditing] = React.useState<Partial<CrmEmailTemplate> | null>(null)
  const [saving, setSaving] = React.useState(false)
  const [showMergeRef, setShowMergeRef] = React.useState(true)

  const load = React.useCallback(async () => {
    setLoading(true)
    const res = await fetchCrmEmailTemplates()
    setItems(res.templates)
    setUnavailable(!!res.unavailable)
    setLoading(false)
  }, [])

  React.useEffect(() => { load() }, [load])

  const canEdit = (t: CrmEmailTemplate): boolean => {
    if (!t.is_shared) return true
    if (t.owner_email && props.userEmail && t.owner_email.toLowerCase() === props.userEmail.toLowerCase()) return true
    return false
  }

  const startCreate = () => setEditing({ id: undefined, name: '', subject: '', body_html: '', category: '', is_shared: false })
  const startEdit = (tpl: CrmEmailTemplate) => setEditing({ ...tpl })

  const save = async () => {
    if (!editing || !editing.name?.trim() || !editing.subject?.trim() || !editing.body_html?.trim()) return
    setSaving(true)
    const payload = {
      name: editing.name,
      subject: editing.subject,
      body_html: editing.body_html,
      category: editing.category || undefined,
      is_shared: !!editing.is_shared,
    }
    const ok = editing.id
      ? await updateCrmEmailTemplate(editing.id, payload)
      : await createCrmEmailTemplate(payload)
    setSaving(false)
    if (ok) { props.onToast(editing.id ? 'Template updated' : 'Template created'); setEditing(null); load() }
    else props.onToast('Save failed — try again')
  }

  const remove = async (id: number) => {
    if (!window.confirm('Delete this template?')) return
    const ok = await deleteCrmEmailTemplate(id)
    if (ok) { props.onToast('Template deleted'); load() }
    else props.onToast('Delete failed')
  }

  const insertMergeToken = (token: string) => {
    if (!editing) return
    setEditing({ ...editing, body_html: (editing.body_html || '') + token })
  }

  const owned = items.filter(t => canEdit(t))
  const shared = items.filter(t => !canEdit(t))

  return (
    <div className="prefs-card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <h2 className="prefs-card-title" style={{ margin: 0 }}>Templates</h2>
        {!unavailable && !loading && (
          <button type="button" className="prefs-save-btn" onClick={startCreate}>+ New template</button>
        )}
      </div>
      <p className="prefs-card-desc" style={{ marginBottom: 14 }}>
        Reusable email drafts with merge fields like <code>{'{{firstName}}'}</code> and <code>{'{{dealTitle}}'}</code>. Insert from the compose toolbar.
      </p>

      {unavailable ? (
        <div className="email-prefs-empty">
          <div className="email-prefs-empty-title">Coming soon</div>
          <div>Templates will be available once the email features API is deployed.</div>
        </div>
      ) : loading ? (
        <div className="email-prefs-empty">
          <span className="bp-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
          Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="email-prefs-empty">
          <div className="email-prefs-empty-title">No templates yet</div>
          <div style={{ marginBottom: 10 }}>Create one to speed up repetitive replies.</div>
          <button type="button" className="prefs-save-btn" onClick={startCreate}>Create template</button>
        </div>
      ) : (
        <>
          {owned.length > 0 && (
            <>
              <div className="email-prefs-subhead">Your templates</div>
              <ul className="email-prefs-list">
                {owned.map(t => (
                  <li key={t.id} className="email-prefs-row">
                    <div className="email-prefs-row-main">
                      <div className="email-prefs-row-title">
                        {t.name}
                        {t.category ? <span className="email-prefs-badge">{t.category}</span> : null}
                        {t.is_shared ? <span className="email-prefs-badge email-prefs-badge-shared">Shared</span> : null}
                      </div>
                      <div className="email-prefs-row-sub">{t.subject}</div>
                    </div>
                    <div className="email-prefs-row-actions">
                      <button type="button" className="email-prefs-btn" onClick={() => startEdit(t)}>Edit</button>
                      <button type="button" className="email-prefs-btn email-prefs-btn-danger" onClick={() => remove(t.id)}>Delete</button>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
          {shared.length > 0 && (
            <>
              <div className="email-prefs-subhead">Shared templates (read-only)</div>
              <ul className="email-prefs-list">
                {shared.map(t => (
                  <li key={t.id} className="email-prefs-row">
                    <div className="email-prefs-row-main">
                      <div className="email-prefs-row-title">
                        {t.name}
                        {t.category ? <span className="email-prefs-badge">{t.category}</span> : null}
                        {t.owner_email ? <span className="email-prefs-row-owner">by {t.owner_email}</span> : null}
                      </div>
                      <div className="email-prefs-row-sub">{t.subject}</div>
                    </div>
                    <div className="email-prefs-row-actions">
                      <button type="button" className="email-prefs-btn" onClick={() => startEdit({ ...t, id: undefined as any, name: `${t.name} (copy)`, is_shared: false })}>Duplicate</button>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}

      {editing && (
        <div className="email-prefs-editor-overlay" onClick={() => { if (!saving) setEditing(null) }}>
          <div className="email-prefs-editor email-prefs-editor-wide" onClick={(e) => e.stopPropagation()}>
            <div className="email-prefs-editor-head">
              <div className="email-prefs-editor-title">{editing.id ? 'Edit template' : 'New template'}</div>
              <button className="inbox-compose-icon-btn" onClick={() => setEditing(null)} aria-label="Close"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
            </div>
            <div className="email-prefs-editor-body email-prefs-editor-body-split">
              <div className="email-prefs-editor-main">
                <div className="prefs-field prefs-field-full">
                  <label htmlFor="tpl-name">Name</label>
                  <input id="tpl-name" type="text" value={editing.name || ''} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="e.g. Follow-up after estimate" />
                </div>
                <div className="prefs-field prefs-field-full">
                  <label htmlFor="tpl-category">Category (optional)</label>
                  <input id="tpl-category" type="text" value={editing.category || ''} onChange={(e) => setEditing({ ...editing, category: e.target.value })} placeholder="e.g. Follow-ups, Proposals" />
                </div>
                <div className="prefs-field prefs-field-full">
                  <label htmlFor="tpl-subject">Subject</label>
                  <input id="tpl-subject" type="text" value={editing.subject || ''} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} placeholder="e.g. Following up on {{dealTitle}}" />
                </div>
                <div className="prefs-field prefs-field-full">
                  <label htmlFor="tpl-body">Body (HTML)</label>
                  <textarea id="tpl-body" rows={10} value={editing.body_html || ''} onChange={(e) => setEditing({ ...editing, body_html: e.target.value })} placeholder="&lt;p&gt;Hi {{firstName}},&lt;/p&gt;&lt;p&gt;Following up on {{dealTitle}}…&lt;/p&gt;" style={{ fontFamily: 'monospace', fontSize: '0.82rem' }} />
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.84rem', color: '#334155' }}>
                  <input type="checkbox" checked={!!editing.is_shared} onChange={(e) => setEditing({ ...editing, is_shared: e.target.checked })} />
                  Share with team (read-only for others)
                </label>
              </div>
              <aside className="email-prefs-editor-side">
                <button type="button" className="email-prefs-merge-toggle" onClick={() => setShowMergeRef(v => !v)}>
                  {showMergeRef ? '▾' : '▸'} Merge Fields
                </button>
                {showMergeRef && (
                  <div className="email-prefs-merge-list">
                    <div style={{ fontSize: '0.76rem', color: '#64748b', marginBottom: 8 }}>Click to insert into body.</div>
                    {EMAIL_MERGE_FIELDS.map(f => (
                      <button key={f.token} type="button" className="email-prefs-merge-item" onClick={() => insertMergeToken(f.token)}>
                        <code>{f.token}</code>
                        <span>{f.description}</span>
                      </button>
                    ))}
                  </div>
                )}
              </aside>
            </div>
            <div className="email-prefs-editor-foot">
              <button type="button" className="inbox-compose-secondary" onClick={() => setEditing(null)} disabled={saving}>Cancel</button>
              <button type="button" className="inbox-compose-send" onClick={save} disabled={saving || !editing.name?.trim() || !editing.subject?.trim() || !editing.body_html?.trim()}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// --- EmailBccTokenSection ---------------------------------------------------

function EmailBccTokenSection(props: { onToast: (msg: string) => void }) {
  const [loading, setLoading] = React.useState(true)
  const [unavailable, setUnavailable] = React.useState(false)
  const [address, setAddress] = React.useState<string>('')
  const [rotating, setRotating] = React.useState(false)
  const [copied, setCopied] = React.useState(false)

  React.useEffect(() => {
    let cancelled = false
    ;(async () => {
      const res = await fetchBccToken()
      if (cancelled) return
      if (!res) { setUnavailable(true); setLoading(false); return }
      setAddress(res.address)
      setLoading(false)
    })()
    return () => { cancelled = true }
  }, [])

  const copy = async () => {
    if (!address) return
    try {
      await navigator.clipboard.writeText(address)
      setCopied(true)
      setTimeout(() => setCopied(false), 1600)
    } catch { props.onToast('Copy failed — select the text manually') }
  }

  const rotate = async () => {
    if (!window.confirm('Rotate your BCC token? Any existing emails using the current address will stop being auto-logged.')) return
    setRotating(true)
    const res = await rotateBccToken()
    setRotating(false)
    if (res) { setAddress(res.address); props.onToast('BCC token rotated') }
    else props.onToast('Rotate failed')
  }

  return (
    <div className="prefs-card">
      <h2 className="prefs-card-title">Email-to-CRM BCC address</h2>
      <p className="prefs-card-desc" style={{ marginBottom: 14 }}>
        BCC this address on any email you send from Gmail (or anywhere) to auto-log it to the matching deal/contact in the CRM.
      </p>
      {unavailable ? (
        <div className="email-prefs-empty">
          <div className="email-prefs-empty-title">Coming soon</div>
          <div>The BCC-to-log feature will be available once the email features API is deployed.</div>
        </div>
      ) : loading ? (
        <div className="email-prefs-empty">
          <span className="bp-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
          Loading…
        </div>
      ) : (
        <div className="email-prefs-bcc">
          <div className="email-prefs-bcc-box">
            <code className="email-prefs-bcc-addr">{address || '—'}</code>
            <button type="button" className="email-prefs-btn" onClick={copy} disabled={!address}>{copied ? 'Copied!' : 'Copy'}</button>
            <button type="button" className="email-prefs-btn email-prefs-btn-danger" onClick={rotate} disabled={rotating}>{rotating ? 'Rotating…' : 'Rotate'}</button>
          </div>
          <div className="email-prefs-bcc-hint">
            Rotating will invalidate the previous address. Distribute the new one to any automated systems that BCC it.
          </div>
        </div>
      )}
    </div>
  )
}

// --- EmailScheduledListSection ---------------------------------------------

function EmailScheduledListSection(props: { onToast: (msg: string) => void }) {
  const [loading, setLoading] = React.useState(true)
  const [unavailable, setUnavailable] = React.useState(false)
  const [items, setItems] = React.useState<ScheduledEmail[]>([])

  const load = React.useCallback(async () => {
    setLoading(true)
    const res = await fetchScheduledEmails()
    setItems(res.scheduled)
    setUnavailable(!!res.unavailable)
    setLoading(false)
  }, [])

  React.useEffect(() => { load() }, [load])

  const cancel = async (id: number) => {
    if (!window.confirm('Cancel this scheduled email?')) return
    const ok = await cancelScheduledEmail(id)
    if (ok) { props.onToast('Scheduled send cancelled'); load() }
    else props.onToast('Cancel failed')
  }

  const recips = (val: string[] | string | undefined): string => {
    if (!val) return ''
    if (Array.isArray(val)) return val.join(', ')
    return String(val)
  }

  return (
    <div className="prefs-card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <h2 className="prefs-card-title" style={{ margin: 0 }}>Scheduled emails</h2>
        {!unavailable && (
          <button type="button" className="email-prefs-btn" onClick={load}>Refresh</button>
        )}
      </div>
      <p className="prefs-card-desc" style={{ marginBottom: 14 }}>
        Emails queued for later delivery. Cancel them here before they send.
      </p>
      {unavailable ? (
        <div className="email-prefs-empty">
          <div className="email-prefs-empty-title">Coming soon</div>
          <div>Scheduled sends will appear here once the email features API is deployed.</div>
        </div>
      ) : loading ? (
        <div className="email-prefs-empty">
          <span className="bp-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
          Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="email-prefs-empty">
          <div className="email-prefs-empty-title">Nothing scheduled</div>
          <div>Use the ▾ next to Send in the compose panel to schedule a message.</div>
        </div>
      ) : (
        <table className="email-prefs-sched-table">
          <thead>
            <tr>
              <th>To</th>
              <th>Subject</th>
              <th>Send at</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map(s => (
              <tr key={s.id}>
                <td className="email-prefs-sched-to" title={recips(s.to)}>{recips(s.to)}</td>
                <td className="email-prefs-sched-subj">{s.subject || '(no subject)'}</td>
                <td className="email-prefs-sched-when">{formatScheduleTime(s.send_at)}</td>
                <td><span className={`email-prefs-sched-status email-prefs-sched-status-${s.status || 'pending'}`}>{s.status || 'pending'}</span></td>
                <td className="email-prefs-sched-action">
                  {(s.status || 'pending') === 'pending' && (
                    <button type="button" className="email-prefs-btn email-prefs-btn-danger" onClick={() => cancel(s.id)}>Cancel</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

// --- GmailSearch ------------------------------------------------------------
// Pill-shaped search bar that mirrors Gmail's compose search: rounded
// capsule, sparkle icon on the left, filter toggle on the right, and a
// focus-triggered dropdown with quick-filter chips and per-user search
// history. Matches the reference screenshot the user shared.

type QuickFilter = { key: 'has_attachment' | 'last_7d' | 'from_me'; label: string; token: string }
const GMAIL_QUICK_FILTERS: QuickFilter[] = [
  { key: 'has_attachment', label: 'Has attachment', token: 'has:attachment' },
  { key: 'last_7d', label: 'Last 7 days', token: 'newer:7d' },
  { key: 'from_me', label: 'From me', token: 'from:me' },
]

// Advanced-search structured state. Round-tripped when the panel
// re-opens so the user's work persists across close-reopen cycles.
type AdvancedSearchState = {
  from: string
  to: string
  subject: string
  hasWords: string
  doesntHave: string
  sizeOp: 'gt' | 'lt'
  sizeValue: string
  sizeUnit: 'MB' | 'KB' | 'B'
  dateWithin: '' | '1d' | '3d' | '1w' | '2w' | '1m' | '2m' | '6m' | '1y'
  dateAnchor: string
  scope: '' | 'inbox' | 'sent' | 'drafts' | 'snoozed'
  hasAttachment: boolean
}
const EMPTY_ADV: AdvancedSearchState = {
  from: '', to: '', subject: '', hasWords: '', doesntHave: '',
  sizeOp: 'gt', sizeValue: '', sizeUnit: 'MB',
  dateWithin: '', dateAnchor: '', scope: '', hasAttachment: false,
}
// Render structured state → single query string using Gmail's operator
// grammar. Backend converts these to Graph $search / $filter clauses.
function buildAdvancedQuery(s: AdvancedSearchState): string {
  const parts: string[] = []
  if (s.from.trim()) parts.push(`from:${s.from.trim()}`)
  if (s.to.trim()) parts.push(`to:${s.to.trim()}`)
  if (s.subject.trim()) parts.push(`subject:(${s.subject.trim()})`)
  if (s.hasWords.trim()) parts.push(s.hasWords.trim())
  if (s.doesntHave.trim()) parts.push(...s.doesntHave.trim().split(/\s+/).map(w => `-${w}`))
  if (s.sizeValue.trim() && Number.isFinite(Number(s.sizeValue))) {
    parts.push(`size:${s.sizeOp === 'gt' ? 'larger' : 'smaller'}_${s.sizeValue}${s.sizeUnit}`)
  }
  if (s.dateWithin) parts.push(`newer_than:${s.dateWithin}`)
  if (s.scope) parts.push(`in:${s.scope}`)
  if (s.hasAttachment) parts.push('has:attachment')
  return parts.join(' ')
}

function AdvancedSearchPanel(props: {
  initial?: AdvancedSearchState
  onSearch: (q: string, state: AdvancedSearchState) => void
  onClose: () => void
}) {
  const [s, setS] = React.useState<AdvancedSearchState>(props.initial || EMPTY_ADV)
  const update = <K extends keyof AdvancedSearchState>(k: K, v: AdvancedSearchState[K]) =>
    setS(prev => ({ ...prev, [k]: v }))
  return (
    <div className="gmail-adv-panel" role="dialog" aria-label="Advanced search options" onClick={e => e.stopPropagation()}>
      <div className="gmail-adv-grid">
        <label className="gmail-adv-row">
          <span className="gmail-adv-label">From</span>
          <input className="gmail-adv-input" value={s.from} onChange={e => update('from', e.target.value)} />
        </label>
        <label className="gmail-adv-row">
          <span className="gmail-adv-label">To</span>
          <input className="gmail-adv-input" value={s.to} onChange={e => update('to', e.target.value)} />
        </label>
        <label className="gmail-adv-row">
          <span className="gmail-adv-label">Subject</span>
          <input className="gmail-adv-input" value={s.subject} onChange={e => update('subject', e.target.value)} />
        </label>
        <label className="gmail-adv-row">
          <span className="gmail-adv-label">Has the words</span>
          <input className="gmail-adv-input" value={s.hasWords} onChange={e => update('hasWords', e.target.value)} />
        </label>
        <label className="gmail-adv-row">
          <span className="gmail-adv-label">Doesn't have</span>
          <input className="gmail-adv-input" value={s.doesntHave} onChange={e => update('doesntHave', e.target.value)} />
        </label>
        <div className="gmail-adv-row">
          <span className="gmail-adv-label">Size</span>
          <div className="gmail-adv-split">
            <select className="gmail-adv-select" value={s.sizeOp} onChange={e => update('sizeOp', e.target.value as any)}>
              <option value="gt">greater than</option>
              <option value="lt">less than</option>
            </select>
            <input className="gmail-adv-input" type="number" min="0" value={s.sizeValue} onChange={e => update('sizeValue', e.target.value)} />
            <select className="gmail-adv-select" value={s.sizeUnit} onChange={e => update('sizeUnit', e.target.value as any)}>
              <option value="MB">MB</option>
              <option value="KB">KB</option>
              <option value="B">bytes</option>
            </select>
          </div>
        </div>
        <div className="gmail-adv-row">
          <span className="gmail-adv-label">Date within</span>
          <div className="gmail-adv-split">
            <select className="gmail-adv-select" value={s.dateWithin} onChange={e => update('dateWithin', e.target.value as any)}>
              <option value="">—</option>
              <option value="1d">1 day</option>
              <option value="3d">3 days</option>
              <option value="1w">1 week</option>
              <option value="2w">2 weeks</option>
              <option value="1m">1 month</option>
              <option value="2m">2 months</option>
              <option value="6m">6 months</option>
              <option value="1y">1 year</option>
            </select>
            <input className="gmail-adv-input" type="date" value={s.dateAnchor} onChange={e => update('dateAnchor', e.target.value)} />
          </div>
        </div>
        <label className="gmail-adv-row">
          <span className="gmail-adv-label">Search</span>
          <select className="gmail-adv-select" value={s.scope} onChange={e => update('scope', e.target.value as any)}>
            <option value="">All Mail</option>
            <option value="inbox">Inbox</option>
            <option value="sent">Sent</option>
            <option value="drafts">Drafts</option>
            <option value="snoozed">Snoozed</option>
          </select>
        </label>
      </div>
      <label className="gmail-adv-checkbox">
        <input type="checkbox" checked={s.hasAttachment} onChange={e => update('hasAttachment', e.target.checked)} />
        <span>Has attachment</span>
      </label>
      <div className="gmail-adv-footer">
        <button type="button" className="gmail-adv-create" title="Saved filters — coming soon" disabled>
          Create filter
        </button>
        <button type="button" className="gmail-adv-search" onClick={() => {
          const q = buildAdvancedQuery(s)
          props.onSearch(q, s)
          props.onClose()
        }}>
          Search
        </button>
      </div>
    </div>
  )
}

function GmailSearch(props: {
  value: string
  onChange: (next: string) => void
  onClear: () => void
  onSubmit?: (q: string) => void
  // Stored in localStorage per user email; recent searches only
  // surface the last ~8 entries and are deduped by query string.
  historyKey: string
}) {
  const [focused, setFocused] = React.useState(false)
  const [advancedOpen, setAdvancedOpen] = React.useState(false)
  const [advancedState, setAdvancedState] = React.useState<AdvancedSearchState>(EMPTY_ADV)
  const [history, setHistory] = React.useState<string[]>([])
  const wrapperRef = React.useRef<HTMLDivElement | null>(null)
  const inputRef = React.useRef<HTMLInputElement | null>(null)

  // Load history on mount. localStorage is sync + cheap; we keep the
  // list short (8 entries) so no perf worry even if we read on every
  // focus cycle.
  React.useEffect(() => {
    try {
      const raw = localStorage.getItem(props.historyKey)
      if (raw) {
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed)) setHistory(parsed.filter(x => typeof x === 'string').slice(0, 8))
      }
    } catch { /* ignore corrupt blob */ }
  }, [props.historyKey])

  const pushHistory = React.useCallback((q: string) => {
    const clean = q.trim()
    if (!clean) return
    setHistory(prev => {
      const next = [clean, ...prev.filter(x => x.toLowerCase() !== clean.toLowerCase())].slice(0, 8)
      try { localStorage.setItem(props.historyKey, JSON.stringify(next)) } catch { /* quota */ }
      return next
    })
  }, [props.historyKey])

  // Close dropdown + advanced panel on outside click. Using mousedown so
  // the blur inside the input fires first — Escape is handled on the
  // input directly.
  React.useEffect(() => {
    if (!focused && !advancedOpen) return
    const onOut = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setFocused(false)
        setAdvancedOpen(false)
      }
    }
    document.addEventListener('mousedown', onOut)
    return () => document.removeEventListener('mousedown', onOut)
  }, [focused, advancedOpen])

  const applyChip = (token: string) => {
    // Toggle the token in the query — click to add, click again to
    // remove. Graph $search treats our chip tokens as free text until
    // the backend learns to parse them; the user still sees matching
    // behavior because Graph searches the whole message.
    const current = props.value.trim()
    const parts = current.split(/\s+/).filter(Boolean)
    const idx = parts.indexOf(token)
    if (idx >= 0) parts.splice(idx, 1)
    else parts.push(token)
    props.onChange(parts.join(' '))
    inputRef.current?.focus()
  }

  const activeTokens = new Set(props.value.trim().split(/\s+/).filter(Boolean))
  const showDropdown = focused

  return (
    <div className="gmail-search-wrap" ref={wrapperRef}>
      <div className={`gmail-search-pill${focused ? ' is-focused' : ''}`}>
        <span className="gmail-search-ai" aria-hidden="true">
          {/* Sparkle glyph matches Gmail's left-hand search icon. */}
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l1.6 4.8L18 8l-4.8 1.6L12 14l-1.6-4.4L6 8l4.4-1.6L12 2zm7 9l.9 2.6L22 14l-2.1.6L19 17l-.9-2.4L16 14l2.1-.6L19 11zM5 13l.6 1.8L7 15l-1.4.4L5 17l-.6-1.6L3 15l1.4-.4L5 13z"/></svg>
        </span>
        <input
          ref={inputRef}
          type="search"
          className="gmail-search-input"
          placeholder="Search"
          aria-label="Search email"
          value={props.value}
          onChange={(e) => props.onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); pushHistory(props.value); props.onSubmit?.(props.value); setFocused(false); inputRef.current?.blur() }
            else if (e.key === 'Escape') { setFocused(false); inputRef.current?.blur() }
          }}
          autoComplete="off"
          spellCheck={false}
        />
        {props.value && (
          <button type="button" className="gmail-search-clear" onClick={() => { props.onClear(); inputRef.current?.focus() }} aria-label="Clear search" title="Clear search">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        )}
        <button
          type="button"
          className={`gmail-search-adv${advancedOpen ? ' is-active' : ''}`}
          aria-label="Advanced search options"
          aria-expanded={advancedOpen}
          title="Show search options"
          onClick={() => {
            // Opening the advanced panel supersedes the focus
            // dropdown — close the suggestions so the panel has the
            // full real estate, matching Gmail's behavior.
            setAdvancedOpen(v => !v)
            setFocused(false)
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M3 17v2h6v-2H3zM3 5v2h10V5H3zm10 16v-2h8v-2h-8v-2h-2v6h2zM7 9v2H3v2h4v2h2V9H7zm14 4v-2H11v2h10zm-6-4h2V7h4V5h-4V3h-2v6z"/></svg>
        </button>
      </div>
      {showDropdown && (
        <div className="gmail-search-dropdown" role="listbox" aria-label="Search suggestions">
          <div className="gmail-search-chips">
            {GMAIL_QUICK_FILTERS.map(f => (
              <button
                key={f.key}
                type="button"
                className={`gmail-search-chip${activeTokens.has(f.token) ? ' is-active' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); applyChip(f.token) }}
              >
                {f.label}
              </button>
            ))}
          </div>
          {history.length > 0 && (
            <div className="gmail-search-history">
              {history.map(item => (
                <button
                  key={item}
                  type="button"
                  className="gmail-search-history-item"
                  onMouseDown={(e) => { e.preventDefault(); props.onChange(item); pushHistory(item); props.onSubmit?.(item); setFocused(false); inputRef.current?.blur() }}
                  title={item}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="gmail-search-history-icon"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  <span>{item}</span>
                </button>
              ))}
            </div>
          )}
          {history.length === 0 && (
            <div className="gmail-search-empty">Start typing to search your email.</div>
          )}
        </div>
      )}
      {advancedOpen && (
        <AdvancedSearchPanel
          initial={advancedState}
          onSearch={(q, state) => {
            setAdvancedState(state)
            if (q) {
              props.onChange(q)
              pushHistory(q)
              props.onSubmit?.(q)
            }
          }}
          onClose={() => setAdvancedOpen(false)}
        />
      )}
    </div>
  )
}

// --- ThreadCard -------------------------------------------------------------

function ThreadCard(props: {
  thread: InboxThreadSummary
  active: boolean
  currentUserEmail: string
  onClick: () => void
  onAction?: (action: 'markRead' | 'markUnread' | 'flag' | 'unflag' | 'delete') => void
  busyAction?: string | null
  selected?: boolean
  onToggleSelect?: (shiftKey: boolean) => void
}) {
  const { thread } = props
  // Display name: use first non-self participant's name/email
  const other = thread.participants.find(p => p.email.toLowerCase() !== props.currentUserEmail.toLowerCase()) || thread.participants[0]
  const displayName = other?.name || other?.email || '(unknown)'
  const init = m365Initials(displayName)
  const bg = m365AvatarColor(other?.email || displayName || 'x')
  const stop = (e: React.MouseEvent) => { e.stopPropagation(); e.preventDefault() }
  const isBusy = (a: string) => props.busyAction === thread.id + ':' + a

  return (
    <div
      className={`inbox-thread-card${props.active ? ' active' : ''}${thread.isRead ? '' : ' unread'}${(thread as any).isFlagged ? ' flagged' : ''}${props.selected ? ' selected' : ''}`}
      onClick={props.onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); props.onClick() } }}
    >
      {props.onToggleSelect && (
        <label
          className="inbox-thread-select"
          onClick={(e) => { e.stopPropagation() }}
          aria-label={props.selected ? 'Deselect thread' : 'Select thread'}
        >
          <input
            type="checkbox"
            checked={!!props.selected}
            onChange={(e) => { props.onToggleSelect!((e.nativeEvent as MouseEvent).shiftKey) }}
            tabIndex={-1}
          />
          <span className="inbox-thread-select-box" aria-hidden="true">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          </span>
        </label>
      )}
      <div className="inbox-thread-avatar" style={{ background: bg }} aria-hidden="true">
        {init}
        <TenantAvatarPhoto email={other?.email} className="inbox-avatar-photo" />
      </div>
      <div className="inbox-thread-body">
        <div className="inbox-thread-head">
          <div className="inbox-thread-from" title={other?.email}>{displayName}</div>
          {/* Right cluster — paperclip + relative time. Used to be an
              absolutely-positioned badge div that overlapped the time
              stamp; now it shares the flex row so the icon sits just
              to the left of the time with a small gap. */}
          <div className="inbox-thread-right">
            {thread.hasAttachments && (
              <span className="inbox-thread-attach" aria-label="Has attachment" title="Has attachment">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
              </span>
            )}
            <div className="inbox-thread-time">{inboxRelativeTime(thread.lastMessageAt)}</div>
          </div>
        </div>
        <div className="inbox-thread-subject" title={thread.subject}>
          {thread.subject || '(no subject)'}
          {thread.messageCount > 1 && <span className="inbox-thread-count">{thread.messageCount}</span>}
        </div>
        <div className="inbox-thread-preview" title={thread.preview}>{thread.preview}</div>
      </div>
      {props.onAction && (
        <div className="inbox-thread-hover-actions" onClick={stop}>
          <button
            className="inbox-thread-hover-action"
            onClick={(e) => { stop(e); props.onAction!(thread.isRead ? 'markUnread' : 'markRead') }}
            aria-label={thread.isRead ? 'Mark as unread' : 'Mark as read'}
            title={thread.isRead ? 'Mark as unread' : 'Mark as read'}
            disabled={isBusy('markRead') || isBusy('markUnread')}
          >
            {thread.isRead ? (
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
            ) : (
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10.5V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8.5"/><polyline points="22,6 12,13 2,6"/><circle cx="18" cy="17" r="3"/></svg>
            )}
          </button>
          <button
            className={`inbox-thread-hover-action${(thread as any).isFlagged ? ' flagged' : ''}`}
            onClick={(e) => { stop(e); props.onAction!((thread as any).isFlagged ? 'unflag' : 'flag') }}
            aria-label={(thread as any).isFlagged ? 'Unflag' : 'Flag'}
            title={(thread as any).isFlagged ? 'Remove flag' : 'Flag for follow-up'}
            disabled={isBusy('flag') || isBusy('unflag')}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill={(thread as any).isFlagged ? '#ef4444' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>
          </button>
          <button
            className="inbox-thread-hover-action inbox-thread-hover-danger"
            onClick={(e) => { stop(e); props.onAction!('delete') }}
            aria-label="Delete"
            title="Delete"
            disabled={isBusy('delete')}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
          </button>
        </div>
      )}
    </div>
  )
}

// --- MessageBubble ----------------------------------------------------------

function MessageBubble(props: { message: InboxMessage; currentUserEmail: string; defaultExpanded?: boolean; collapsible?: boolean; tracking?: EmailTrackingRow | null }) {
  const collapsible = props.collapsible !== false
  const [expanded, setExpanded] = React.useState(!!props.defaultExpanded || !collapsible)
  const [attachments, setAttachments] = React.useState<Array<{ id: string; name: string; contentType: string; size: number }> | null>(null)
  const [attachLoading, setAttachLoading] = React.useState(false)
  const { message } = props
  const tracking = props.tracking || null
  const init = m365Initials(message.from.name || message.from.email)
  const bg = m365AvatarColor(message.from.email)
  const recipientsSummary = message.to.map(r => r.name || r.email).join(', ')

  // Lazy-load attachment list when the message expands + has attachments.
  React.useEffect(() => {
    if (!expanded || !message.hasAttachments || attachments !== null) return
    let cancel = false
    setAttachLoading(true)
    fetch(`/api/crm/email/messages/${encodeURIComponent(message.id)}/attachments`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : r.json().then((d: any) => Promise.reject(new Error(d.error || `HTTP ${r.status}`))))
      .then((data: any) => { if (!cancel) setAttachments(data.attachments || []) })
      .catch(() => { if (!cancel) setAttachments([]) })
      .finally(() => { if (!cancel) setAttachLoading(false) })
    return () => { cancel = true }
  }, [expanded, message.hasAttachments, message.id, attachments])

  const formatBytes = (n: number) => {
    if (!n) return ''
    if (n < 1024) return `${n} B`
    if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
    return `${(n / 1024 / 1024).toFixed(1)} MB`
  }
  const attachIcon = (ct: string, name: string) => {
    const ext = (name.split('.').pop() || '').toLowerCase()
    const isImg = ct.startsWith('image/')
    const isPdf = ct.includes('pdf') || ext === 'pdf'
    const isDoc = /(word|document|msword)/i.test(ct) || /^(docx?|rtf|odt)$/.test(ext)
    const isXls = /(excel|spreadsheet)/i.test(ct) || /^(xlsx?|csv|ods)$/.test(ext)
    const isZip = /(zip|compressed|x-rar|7z)/i.test(ct) || /^(zip|rar|7z|tar|gz)$/.test(ext)
    const color = isPdf ? '#ef4444' : isDoc ? '#1d4ed8' : isXls ? '#16a34a' : isImg ? '#7c3aed' : isZip ? '#b45309' : '#64748b'
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
      </svg>
    )
  }

  const HeadTag = collapsible ? 'button' : 'div'
  return (
    <div className={`inbox-msg${expanded ? ' expanded' : ''}${collapsible ? '' : ' inbox-msg-static'}`}>
      <HeadTag
        className="inbox-msg-head"
        {...(collapsible
          ? { onClick: () => setExpanded(v => !v), 'aria-expanded': expanded, type: 'button' as const }
          : {})}
      >
        <div className="inbox-msg-avatar" style={{ background: bg }} aria-hidden="true">
          {init}
          <TenantAvatarPhoto email={message.from.email} className="inbox-avatar-photo" />
        </div>
        <div className="inbox-msg-head-info">
          <div className="inbox-msg-head-row">
            <span className="inbox-msg-from-name">{message.from.name || message.from.email}</span>
            <span className="inbox-msg-from-email">&lt;{message.from.email}&gt;</span>
          </div>
          {expanded ? (
            <div className="inbox-msg-recipients">
              to {recipientsSummary}
              {message.cc && message.cc.length > 0 && <> · cc {message.cc.map(r => r.name || r.email).join(', ')}</>}
            </div>
          ) : (
            <div className="inbox-msg-snippet">{message.snippet}</div>
          )}
        </div>
        {/* Paperclip indicator. Gmail reports `hasAttachments: true` for
            messages whose only attachments are INLINE images (signature
            logos, embedded screenshots) — those don't show as
            downloadable files since the backend filters them out. So we
            optimistically show the icon, but hide it the moment the
            attachment fetch comes back with zero real attachments. */}
        {message.hasAttachments && (attachments == null || attachments.length > 0) && (
          <span className="inbox-msg-attach-indicator" aria-label="Has attachments" title="Has attachments">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
          </span>
        )}
        <div className="inbox-msg-time">{formatInboxTime(message.receivedAt)}</div>
      </HeadTag>
      {expanded && tracking && (tracking.open_count > 0 || tracking.click_count > 0) && (() => {
        const parts: string[] = []
        if (tracking.open_count > 0) {
          parts.push(`Opened ${tracking.open_count} time${tracking.open_count === 1 ? '' : 's'}`)
          if (tracking.first_opened_at) {
            try {
              const d = new Date(tracking.first_opened_at)
              const now = new Date()
              const sameDay = d.toDateString() === now.toDateString()
              const when = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) + (sameDay ? ' today' : `, ${d.toLocaleDateString([], { month: 'short', day: 'numeric' })}`)
              parts[parts.length - 1] += `, first at ${when}`
            } catch { /* noop */ }
          }
        }
        if (tracking.click_count > 0) {
          parts.push(`${tracking.click_count} link${tracking.click_count === 1 ? '' : 's'} clicked`)
        }
        const ttip = [
          tracking.first_opened_at ? `First open: ${new Date(tracking.first_opened_at).toLocaleString()}` : '',
          tracking.last_opened_at ? `Last open: ${new Date(tracking.last_opened_at).toLocaleString()}` : '',
          tracking.sent_at ? `Sent: ${new Date(tracking.sent_at).toLocaleString()}` : '',
        ].filter(Boolean).join('\n')
        return (
          <div className="inbox-msg-tracking" title={ttip || undefined}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
            <span>{parts.join(' · ')}</span>
          </div>
        )
      })()}
      {expanded && (
        <>
          <div className="inbox-msg-body">
            <SafeEmailFrame html={message.bodyHtml} />
          </div>
          {/* Render the attachments panel only when we either are
              still loading them or have at least one real (non-inline)
              attachment to show. Avoids the misleading "No attachments"
              label appearing alongside the paperclip indicator on
              messages whose only payload is inline signature images. */}
          {message.hasAttachments && (attachLoading || (attachments && attachments.length > 0)) && (
            <div className="inbox-msg-attachments">
              <div className="inbox-msg-attachments-label">
                {attachLoading
                  ? 'Loading attachments…'
                  : `${attachments!.length} attachment${attachments!.length === 1 ? '' : 's'}`}
              </div>
              <div className="inbox-msg-attachments-grid">
                {(attachments || []).map(a => (
                  <a
                    key={a.id}
                    className="inbox-msg-attachment-card"
                    href={`/api/crm/email/messages/${encodeURIComponent(message.id)}/attachments/${encodeURIComponent(a.id)}/download`}
                    target="_blank"
                    rel="noopener"
                    download={a.name}
                    title={`${a.name} · ${formatBytes(a.size)}`}
                  >
                    <div className="inbox-msg-attachment-icon">{attachIcon(a.contentType, a.name)}</div>
                    <div className="inbox-msg-attachment-meta">
                      <div className="inbox-msg-attachment-name" title={a.name}>{a.name}</div>
                      <div className="inbox-msg-attachment-sub">{formatBytes(a.size)}</div>
                    </div>
                    <svg className="inbox-msg-attachment-dl" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  </a>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

// --- LinkedEmailsSection ----------------------------------------------------
// Used on contact/deal/lead/project detail pages' Activities tab to display a
// small section of email threads that link to the current record. Soft-fails
// to silent-empty on 404 (endpoint not yet deployed by backend team).

function LinkedEmailsSection(props: {
  entityType: EmailThreadEntityType
  entityId: number | string
  /** When true, renders nothing if zero results (caller handles its own empty state). */
  hideIfEmpty?: boolean
}) {
  const [threads, setThreads] = React.useState<EmailThreadSummaryLite[] | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [unavailable, setUnavailable] = React.useState(false)

  React.useEffect(() => {
    let cancel = false
    setLoading(true)
    setUnavailable(false)
    fetchEmailThreadsByEntity(props.entityType, props.entityId).then(rows => {
      if (cancel) return
      if (rows === null) { setUnavailable(true); setThreads([]); return }
      setThreads(rows)
    }).finally(() => { if (!cancel) setLoading(false) })
    return () => { cancel = true }
  }, [props.entityType, props.entityId])

  // Endpoint 404 → render nothing at all. Activities timeline already handles
  // email activities via the webhook/sent-poll, so missing this enhancement
  // isn't a functional regression.
  if (unavailable) return null
  // Silent during first fetch: previously we rendered a "Loading email
  // threads…" header on every tab switch, which flashed for ~300ms then
  // disappeared when the endpoint 404'd or returned zero rows. Users on
  // the Contact Record Activities tab saw it every click and it felt
  // wonky. Only render once we actually have rows to show.
  if (loading && !threads) return null
  if (!loading && (!threads || threads.length === 0) && props.hideIfEmpty) return null

  return (
    <div className="crp-linked-emails" style={{ border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', margin: '0 0 12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid #f0f2f5', fontSize: '0.82rem', fontWeight: 600, color: '#33475b' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
          Linked email threads
        </span>
        {threads && threads.length > 0 && (
          <span style={{ display: 'inline-flex', alignItems: 'center', padding: '1px 8px', borderRadius: 10, background: '#f3e8ff', color: '#6d28d9', fontSize: '0.68rem', fontWeight: 700, letterSpacing: 0.2 }}>
            {threads.length}
          </span>
        )}
      </div>
      {loading ? (
        <div style={{ padding: '10px 14px', fontSize: '0.78rem', color: '#7c98b6' }}>Loading email threads…</div>
      ) : !threads || threads.length === 0 ? (
        <div style={{ padding: '10px 14px', fontSize: '0.78rem', color: '#7c98b6' }}>No email threads linked yet.</div>
      ) : (
        <div>
          {threads.slice(0, 8).map(t => {
            const other = (t.participants || []).find(p => !!p.email) || null
            const when = t.lastMessageAt ? new Date(t.lastMessageAt) : null
            return (
              <a
                key={t.id}
                href={`#/email?thread=${encodeURIComponent(t.id)}`}
                style={{ display: 'flex', gap: 10, padding: '10px 14px', borderTop: '1px solid #f5f7fa', textDecoration: 'none', color: 'inherit' }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#f8fafc')}
                onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}
                title={t.subject || '(no subject)'}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#33475b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.subject || '(no subject)'}
                    {t.messageCount > 1 && <span style={{ marginLeft: 6, fontSize: '0.68rem', fontWeight: 700, color: '#6d28d9', background: '#f3e8ff', borderRadius: 10, padding: '1px 6px' }}>{t.messageCount}</span>}
                  </div>
                  {(other?.name || other?.email || t.preview) && (
                    <div style={{ fontSize: '0.75rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>
                      {other?.name || other?.email ? <span style={{ fontWeight: 500, color: '#475569' }}>{other?.name || other?.email} · </span> : null}
                      {t.preview}
                    </div>
                  )}
                </div>
                <div style={{ fontSize: '0.72rem', color: '#94a3b8', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {when ? when.toLocaleDateString([], { month: 'short', day: 'numeric' }) : ''}
                </div>
              </a>
            )
          })}
          {threads.length > 8 && (
            <div style={{ padding: '8px 14px', borderTop: '1px solid #f5f7fa', fontSize: '0.72rem', color: '#7c98b6' }}>
              + {threads.length - 8} more in inbox
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// DealAutomationCard — right-sidebar card on the Deal Record that surfaces
// the most recent deal_automation_runs row + action links.
//
// Behavior:
//   - success run  → compact status card.
//   - partial/failed run → step-by-step status with a re-run button (admin).
//   - no run, deal is in a configured won stage, automation enabled  →
//                    "Automation didn't run" + reason + Run now (admin).
//   - otherwise    → render nothing.
// ---------------------------------------------------------------------------
function DealAutomationCard(props: { deal: CrmDeal }) {
  const { deal } = props
  const [loading, setLoading] = React.useState(true)
  const [run, setRun] = React.useState<DealAutomationRun | null>(null)
  const [settings, setSettings] = React.useState<DealWonSettings | null>(null)
  const [running, setRunning] = React.useState(false)
  const [err, setErr] = React.useState<string | null>(null)
  const [role, setRole] = React.useState<string>(() => {
    try { return localStorage.getItem('userRole') || 'user' } catch { return 'user' }
  })

  // Track role from /api/me like the rest of the app does.
  React.useEffect(() => {
    fetch('/api/me', { credentials: 'include' })
      .then(r => r.json())
      .then((data: any) => setRole(data?.role || 'user'))
      .catch(() => {})
  }, [])
  const isAdmin = role === 'admin'

  const load = React.useCallback(async () => {
    setLoading(true)
    setErr(null)
    try {
      const [runsData, settingsData] = await Promise.all([
        fetchDealAutomationRuns(Number(deal.id), 5).catch(() => ({ runs: [] })),
        isAdmin ? fetchDealAutomationSettings().catch(() => null) : Promise.resolve(null),
      ])
      const rows = runsData?.runs ?? []
      setRun(rows[0] || null)
      setSettings(settingsData?.resolved ?? null)
    } catch (e: any) {
      setErr(e?.message || 'Load failed')
    } finally {
      setLoading(false)
    }
  }, [deal.id, isAdmin])

  React.useEffect(() => { void load() }, [load])

  const handleRun = async () => {
    if (!isAdmin) return
    setRunning(true)
    setErr(null)
    try {
      await runDealAutomationManually(Number(deal.id))
      // Reload once the run has finished; the endpoint is synchronous so by
      // the time we get here, the run row is fully settled.
      await load()
    } catch (e: any) {
      setErr(e?.message || 'Run failed')
    } finally {
      setRunning(false)
    }
  }

  // Decide whether to show at all.
  const isInWonStage = !!(settings?.stageIds || []).includes(String(deal.stage || ''))
  const isAutomationEnabled = !!settings?.enabled
  const hasRun = !!run
  const shouldShowEmpty = !hasRun && isAutomationEnabled && isInWonStage // admin prompt
  // Non-admin + no run + nothing to say → render nothing.
  if (loading) {
    return (
      <div className="crp-card">
        <div className="crp-card-header"><h3>Automation</h3></div>
        <div className="crp-card-body" style={{ fontSize: '0.78rem', color: '#94a3b8', padding: '8px 0' }}>Loading…</div>
      </div>
    )
  }
  if (!hasRun && !shouldShowEmpty) return null

  // Parse the stored step list + settings blob for each run.
  const steps = Array.isArray(run?.steps) ? run!.steps : []

  const statusColor =
    run?.status === 'success' ? '#16a34a' :
    run?.status === 'partial' ? '#ca8a04' :
    run?.status === 'failed' ? '#dc2626' :
    '#64748b'
  const statusLabel = run?.status === 'success' ? 'Ran successfully' : run?.status === 'partial' ? 'Ran with issues' : run?.status === 'failed' ? 'Run failed' : 'In progress'

  return (
    <div className="crp-card" data-crp-card="deal-automation">
      <div className="crp-card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3>Automation</h3>
        {hasRun && (
          <span
            title={run?.completed_at ? `Completed ${new Date(run.completed_at).toLocaleString()}` : 'Running'}
            style={{ fontSize: '0.68rem', padding: '1px 8px', borderRadius: 10, background: '#f8fafc', color: statusColor, border: `1px solid ${statusColor}`, fontWeight: 600 }}
          >{statusLabel}</span>
        )}
      </div>
      <div className="crp-card-body">
        {shouldShowEmpty && !hasRun && (
          <div style={{ fontSize: '0.82rem', color: '#475569' }}>
            <div style={{ color: '#b45309', fontWeight: 500, marginBottom: 6 }}>Automation didn't run</div>
            <div style={{ fontSize: '0.76rem', color: '#64748b', marginBottom: 10 }}>
              This deal is in a Won stage but no automation run exists yet. This usually means the deal was promoted before the feature was enabled, or the chain failed before writing a run row.
            </div>
            {isAdmin ? (
              <button
                type="button"
                onClick={handleRun}
                disabled={running}
                style={{ padding: '6px 12px', border: 'none', borderRadius: 4, background: '#0054a9', color: '#fff', fontSize: '0.78rem', fontWeight: 600, cursor: running ? 'default' : 'pointer', opacity: running ? 0.7 : 1 }}
              >{running ? 'Running…' : 'Run now'}</button>
            ) : (
              <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>Ask an admin to run the automation.</div>
            )}
          </div>
        )}

        {hasRun && (
          <>
            {/* Step-by-step for partial/failed runs */}
            {(run?.status === 'partial' || run?.status === 'failed') && steps.length > 0 && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #f1f5f9' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#475569', marginBottom: 4 }}>Steps</div>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: '0.72rem', color: '#475569' }}>
                  {steps.map((s, i) => (
                    <li key={i} style={{ marginBottom: 2 }}>
                      <span style={{ color: s.status === 'ok' ? '#16a34a' : s.status === 'skipped' ? '#ca8a04' : '#dc2626' }}>
                        {s.status === 'ok' ? '✓' : s.status === 'skipped' ? '~' : '✗'}
                      </span>{' '}
                      <span style={{ fontWeight: 500 }}>{s.step}</span>
                      {s.error ? <span style={{ color: '#b91c1c' }}> — {s.error}</span> : null}
                    </li>
                  ))}
                </ul>
                {isAdmin && (
                  <button
                    type="button"
                    onClick={handleRun}
                    disabled={running}
                    style={{ marginTop: 10, padding: '6px 12px', border: 'none', borderRadius: 4, background: '#0054a9', color: '#fff', fontSize: '0.78rem', fontWeight: 600, cursor: running ? 'default' : 'pointer', opacity: running ? 0.7 : 1 }}
                  >{running ? 'Re-running…' : 'Re-run automation'}</button>
                )}
              </div>
            )}
          </>
        )}

        {err && (
          <div style={{ marginTop: 8, padding: '6px 10px', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 4, fontSize: '0.72rem', color: '#991b1b' }}>{err}</div>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// DealAutomationSettingsPanel — admin-only Settings card for the "Deal moves
// to Won" automation chain. Editable keys live in integration_settings
// (deal_won_enabled / deal_won_stage_ids) and are
// surfaced through /api/crm/deal-automation/settings.
//
// Provides:
//   - Enable / disable toggle
//   - Stage multi-select sourced from pipeline_stages
//   - "Run now for a specific deal" admin action (picks a deal, forces the chain)
// ---------------------------------------------------------------------------
function DealAutomationSettingsPanel() {
  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const [err, setErr] = React.useState<string | null>(null)
  const [enabled, setEnabled] = React.useState(false)
  const [stageIds, setStageIds] = React.useState<string[]>([])

  const [stageOptions, setStageOptions] = React.useState<Array<{ id: string; label: string; category: number }>>([])
  const [stageOptionsLoading, setStageOptionsLoading] = React.useState(false)

  // Manual-run state
  const [runDealId, setRunDealId] = React.useState('')
  const [running, setRunning] = React.useState(false)
  const [runResult, setRunResult] = React.useState<any | null>(null)

  const load = React.useCallback(async () => {
    setLoading(true)
    setErr(null)
    try {
      const data = await fetchDealAutomationSettings()
      setEnabled(!!data.resolved.enabled)
      setStageIds(Array.isArray(data.resolved.stageIds) ? data.resolved.stageIds : [])
    } catch (e: any) {
      setErr(e?.message || 'Failed to load settings')
    } finally {
      setLoading(false)
    }
  }, [])

  const loadStages = React.useCallback(async () => {
    setStageOptionsLoading(true)
    try {
      // Sales pipeline (category 0) = deals in flight. Delivery pipeline
      // (category 1) = production-side project stages. The admin is most
      // likely to pick CONTRACT_SIGNED from Sales as the won trigger.
      const [salesResp, deliveryResp] = await Promise.all([
        fetch('/api/crm/pipeline/0', { credentials: 'include' }),
        fetch('/api/crm/pipeline/1', { credentials: 'include' }),
      ])
      const opts: Array<{ id: string; label: string; category: number }> = []
      if (salesResp.ok) {
        const rows = await salesResp.json() as Array<{ status_id: string; name: string; category_id: number }>
        for (const r of rows) opts.push({ id: r.status_id, label: `${r.name} (Sales)`, category: 0 })
      }
      if (deliveryResp.ok) {
        const rows = await deliveryResp.json() as Array<{ status_id: string; name: string; category_id: number }>
        for (const r of rows) opts.push({ id: r.status_id, label: `${r.name} (Delivery)`, category: 1 })
      }
      if (opts.length === 0) {
        opts.push(
          { id: 'CONTRACT_SIGNED', label: 'Contract Signed (Sales)', category: 0 },
          { id: 'P_CONTRACT_SIGNED', label: 'Contract Signed (Delivery)', category: 1 },
        )
      }
      setStageOptions(opts)
    } catch {
      setStageOptions([
        { id: 'CONTRACT_SIGNED', label: 'Contract Signed (Sales)', category: 0 },
        { id: 'P_CONTRACT_SIGNED', label: 'Contract Signed (Delivery)', category: 1 },
      ])
    } finally {
      setStageOptionsLoading(false)
    }
  }, [])

  React.useEffect(() => {
    load()
    loadStages()
  }, [load, loadStages])

  const toggleStage = (id: string) => {
    setStageIds(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id])
  }

  const handleSave = async () => {
    setSaving(true)
    setErr(null)
    try {
      await updateDealAutomationSettings({
        enabled,
        stageIds,
      })
    } catch (e: any) {
      setErr(e?.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const handleRunNow = async () => {
    const id = Number(runDealId)
    if (!Number.isFinite(id) || id <= 0) {
      setErr('Enter a numeric deal id')
      return
    }
    setRunning(true)
    setRunResult(null)
    setErr(null)
    try {
      const result = await runDealAutomationManually(id)
      setRunResult(result)
    } catch (e: any) {
      setErr(e?.message || 'Run failed')
    } finally {
      setRunning(false)
    }
  }

  if (loading) {
    return (
      <div className="prefs-card">
        <h2 className="prefs-card-title">Deal Automation</h2>
        <p className="prefs-card-desc">Loading current configuration…</p>
      </div>
    )
  }

  return (
    <>
      <div className="prefs-card">
        <h2 className="prefs-card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          Deal Automation — When a deal moves to Won
          <span style={{ fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px', background: enabled ? '#e4f7e8' : '#f5f8fa', color: enabled ? '#2e7d32' : '#7c98b6', border: `1px solid ${enabled ? '#bce3c5' : '#e0e6ed'}`, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {enabled ? 'Enabled' : 'Disabled'}
          </span>
        </h2>
        <p className="prefs-card-desc">
          When a deal transitions INTO one of the configured stages, BotPros Ops automatically
          writes a consolidated MILESTONE activity on the deal's timeline and posts a job-win
          announcement to the Social Feed.
        </p>

        {/* Enable toggle */}
        <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: '#f8fafc', border: '1px solid #e5e9f0', borderRadius: 6 }}>
          <label style={{ position: 'relative', display: 'inline-block', width: 38, height: 22 }}>
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              style={{ opacity: 0, width: 0, height: 0 }}
            />
            <span style={{ position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0, background: enabled ? '#0054a9' : '#cbd5e1', transition: '.2s', borderRadius: 22 }}>
              <span style={{ position: 'absolute', height: 16, width: 16, left: enabled ? 20 : 3, bottom: 3, background: '#fff', transition: '.2s', borderRadius: '50%' }} />
            </span>
          </label>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#33475b' }}>Enable Deal Won automation</div>
            <div style={{ fontSize: 11, color: '#7c98b6' }}>
              Master switch. Flip this on after you've picked the won stages below.
            </div>
          </div>
        </div>

        {/* Stage multi-select */}
        <div style={{ marginTop: 20 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6 }}>
            Stages that trigger the automation
          </label>
          {stageOptionsLoading ? (
            <div style={{ fontSize: 12, color: '#7c98b6' }}>Loading stages…</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 6 }}>
              {stageOptions.map(opt => (
                <label key={opt.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', border: `1px solid ${stageIds.includes(opt.id) ? '#0054a9' : '#e5e9f0'}`, borderRadius: 6, background: stageIds.includes(opt.id) ? '#f0f7ff' : '#fff', cursor: 'pointer', fontSize: 12 }}>
                  <input
                    type="checkbox"
                    checked={stageIds.includes(opt.id)}
                    onChange={() => toggleStage(opt.id)}
                  />
                  <span style={{ fontWeight: 500, color: '#33475b' }}>{opt.label}</span>
                </label>
              ))}
            </div>
          )}
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
            Pick the stage(s) that represent "Won" or "In Production". The chain fires only on transitions INTO these stages.
          </div>
        </div>

        {err && (
          <div style={{ marginTop: 16, padding: 10, borderRadius: 6, background: '#fee2e2', border: '1px solid #fecaca', fontSize: 12, color: '#991b1b' }}>{err}</div>
        )}

        <div style={{ marginTop: 20, display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            type="button"
            className="prefs-save-btn"
            onClick={handleSave}
            disabled={saving}
            style={{ opacity: saving ? 0.7 : 1 }}
          >{saving ? 'Saving…' : 'Save settings'}</button>
          <button
            type="button"
            onClick={load}
            style={{ padding: '8px 14px', border: '1px solid #cbd5e1', background: '#fff', color: '#334155', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}
          >Revert</button>
        </div>
      </div>

      {/* Manual run card */}
      <div className="prefs-card">
        <h2 className="prefs-card-title">Run now for a specific deal</h2>
        <p className="prefs-card-desc">
          Force the won-automation chain for a given deal. Use this when a deal was flipped to Won before automation was enabled, or when a previous run failed.
        </p>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <input
            type="number"
            value={runDealId}
            onChange={(e) => setRunDealId(e.target.value)}
            placeholder="Deal id (e.g. 1234)"
            style={{ flex: 1, padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 13, fontFamily: 'ui-monospace, monospace' }}
          />
          <button
            type="button"
            onClick={handleRunNow}
            disabled={running || !runDealId}
            className="prefs-save-btn"
            style={{ opacity: (running || !runDealId) ? 0.6 : 1 }}
          >{running ? 'Running…' : 'Run automation'}</button>
        </div>
        {runResult && (
          <div style={{ marginTop: 14, padding: 12, borderRadius: 6, background: runResult.status === 'success' ? '#dcfce7' : runResult.status === 'partial' ? '#fef3c7' : '#fee2e2', border: `1px solid ${runResult.status === 'success' ? '#86efac' : runResult.status === 'partial' ? '#fcd34d' : '#fecaca'}`, fontSize: 12 }}>
            <div style={{ fontWeight: 600, color: '#14532d', marginBottom: 6 }}>
              Run #{runResult.runId} — {runResult.status}
              {runResult.skippedReason ? ` (skipped: ${runResult.skippedReason})` : ''}
            </div>
            {Array.isArray(runResult.steps) && runResult.steps.length > 0 && (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {runResult.steps.map((s: any, i: number) => (
                  <li key={i} style={{ marginBottom: 2 }}>
                    <strong>{s.step}</strong>: {s.status}
                    {s.error ? ` — ${s.error}` : ''}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </>
  )
}

function TutorialCreationModeBanner() {
  const [on, setOn] = useState<boolean>(() => {
    try { return localStorage.getItem('kbTutorialCreationMode') === '1' } catch { return false }
  })
  useEffect(() => {
    const recheck = () => {
      try { setOn(localStorage.getItem('kbTutorialCreationMode') === '1') } catch { /* */ }
    }
    window.addEventListener('storage', recheck)
    window.addEventListener('kbtc-changed', recheck as EventListener)
    return () => {
      window.removeEventListener('storage', recheck)
      window.removeEventListener('kbtc-changed', recheck as EventListener)
    }
  }, [])
  useEffect(() => {
    if (on) {
      const prev = document.body.style.paddingTop
      document.body.style.paddingTop = '24px'
      return () => { document.body.style.paddingTop = prev }
    }
    return undefined
  }, [on])
  if (!on) return null
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: 24, lineHeight: '24px', textAlign: 'center', fontSize: 12, fontWeight: 600, background: '#f59e0b', color: '#000', zIndex: 999998 }}>
      TUTORIAL CREATION MODE — writes will not save
    </div>
  )
}

function App() {
  // Tutorial Creation Mode auto-install: re-install the fetch interceptor on
  // every App mount if localStorage flag is on, so the interceptor survives
  // page reloads.
  useEffect(() => {
    import('./views/knowledge-base/useTutorialCreationMode').then(m => {
      if (m.isTutorialCreationModeEnabled()) m.installTutorialCreationModeFetch()
    })
  }, [])
  const [activeView, setActiveViewRaw] = useState<ViewMode>(getViewFromHash)
  const [selectedDate, setSelectedDate] = useState(todayKey)
  const [calendarMonth, setCalendarMonth] = useState(() => { const now = new Date(); return new Date(now.getFullYear(), now.getMonth(), 1) })
  const [query] = useState('')
  const [enabledCalendars, setEnabledCalendars] = useState<Set<CalendarId>>(
    () => new Set(calendarSources.map((c) => c.id)),
  )
  const [enabledEmployees, setEnabledEmployees] = useState<Set<string>>(() => new Set<string>())
  const [calendarFilterMenu, setCalendarFilterMenu] = useState<'calendars' | 'team' | null>(null)
  const [popupDay, setPopupDay] = useState<string | null>(null)
  const [popupPosition, setPopupPosition] = useState<{ x: number; y: number } | null>(null)
  const [modalMeeting, setModalMeeting] = useState<Meeting | null>(null)
  const modalMeetingRef = useRef<Meeting | null>(null)
  useEffect(() => { modalMeetingRef.current = modalMeeting }, [modalMeeting])
  const [calEventModal, setCalEventModal] = useState<{ mode: 'create' | 'edit'; eventId?: string; dealId?: number; originalTitle?: string } | null>(null)
  const [calEventForm, setCalEventForm] = useState({ ownerUserId: null as number | null, eventName: '', clientName: '', address: '', date: todayKey, time: '', durationMinutes: 60, attendeeIds: [] as number[], notes: '', clientId: null as string | null, clientEmail: null as string | null, inviteClient: false })
  const [calEventSaving, setCalEventSaving] = useState(false)
  const [calEventError, setCalEventError] = useState<string | null>(null)
  const [calEventDeleteConfirm, setCalEventDeleteConfirm] = useState(false)
  // New-event modal: which kind of entry the user is creating.
  const [calEventType, setCalEventType] = useState<'appointment' | 'holiday'>('appointment')
  // ── Time-off + company-holiday state (calendar feature) ──
  const [timeOffRequests, setTimeOffRequests] = useState<TimeOffRequest[]>([])
  const [pendingHolidays, setPendingHolidays] = useState<CompanyHoliday[]>([])
  const [timeOffModalOpen, setTimeOffModalOpen] = useState(false)
  const [holidayModalOpen, setHolidayModalOpen] = useState(false)
  const [approvalsOpen, setApprovalsOpen] = useState(false)
  // Request-time-off form
  const [toForm, setToForm] = useState({ start_date: todayKey, end_date: todayKey, segment: 'full' as 'full' | 'partial', start_time: '09:00', end_time: '12:00', reason: '' })
  const [toSaving, setToSaving] = useState(false)
  const [toError, setToError] = useState<string | null>(null)
  // Add/Request-holiday form
  const [holForm, setHolForm] = useState<{ name: string; start_date: string; end_date: string; event_type: 'holiday' | 'day_off' | 'closure' | 'maintenance' | 'event' | 'other'; is_all_day: boolean; start_time: string; end_time: string; recurrence_freq: 'none' | 'weekly' | 'monthly' | 'annual'; recurrence_end_date: string }>({ name: '', start_date: todayKey, end_date: todayKey, event_type: 'holiday', is_all_day: true, start_time: '09:00', end_time: '12:00', recurrence_freq: 'none', recurrence_end_date: '' })
  const [holSaving, setHolSaving] = useState(false)
  const [holError, setHolError] = useState<string | null>(null)
  const [recurringEvents, setRecurringEvents] = useState<RecurringEventRule[]>([])
  const [recurringEventsLoading, setRecurringEventsLoading] = useState(false)
  const [holRecurDeleteScope, setHolRecurDeleteScope] = useState<{ parentId: number; name: string; occurrenceDate: string } | null>(null)
  const [recurEditId, setRecurEditId] = useState<number | null>(null)
  const [recurEditForm, setRecurEditForm] = useState<{ recurrence_freq: 'weekly' | 'monthly' | 'annual'; recurrence_end_date: string }>({ recurrence_freq: 'annual', recurrence_end_date: '' })
  const [recurEditSaving, setRecurEditSaving] = useState(false)
  const [recurEditError, setRecurEditError] = useState<string | null>(null)
  // Per-row action spinner in the approvals inbox (keyed `to:<id>` / `hol:<id>`)
  const [approvalBusy, setApprovalBusy] = useState<string | null>(null)
  // Lightweight calendar-feature toast (no global toast host at App scope).
  const [calToast, setCalToast] = useState<{ kind: 'success' | 'error'; message: string } | null>(null)
  const calToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const showCalToast = useCallback((kind: 'success' | 'error', message: string) => {
    setCalToast({ kind, message })
    if (calToastTimerRef.current) clearTimeout(calToastTimerRef.current)
    calToastTimerRef.current = setTimeout(() => setCalToast(null), 4500)
  }, [])
  // ── "New version available" detector ──────────────────────────────────
  // After a deploy, an already-open tab keeps running the OLD JS bundle in
  // memory (the SPA re-fetches DATA on navigation but never reloads its own
  // CODE). index.html is served no-store and asset filenames are content-
  // hashed, so the corrected bundle IS available — this just notices it.
  //
  // How it works: capture the CURRENTLY-loaded main bundle filename once on
  // mount (the executing module script's src). On a visible-only managed
  // interval, fetch the SPA shell with cache-busting, parse the live main
  // bundle filename out of the HTML, and compare. If they differ, a newer
  // deploy shipped → show a non-intrusive banner with a Reload button. We
  // never auto-reload (the user may be mid-edit). On any fetch/parse failure
  // we do nothing (no false-positive banner).
  const [updateAvailable, setUpdateAvailable] = useState(false)
  const loadedBundleRef = useRef<string | null>(null)
  if (loadedBundleRef.current === null) {
    // Read the executing module script tag once (synchronously, first render).
    try {
      const tag = document.querySelector('script[type=module][src*="/assets/index-"]')
      const src = tag?.getAttribute('src') || ''
      const m = src.match(/index-[A-Za-z0-9_-]+\.js/)
      // Empty string (not null) means "looked, found nothing" → never poll.
      loadedBundleRef.current = m ? m[0] : ''
    } catch { loadedBundleRef.current = '' }
  }
  useEffect(() => {
    const loaded = loadedBundleRef.current
    // If we couldn't determine the loaded bundle (dev server, no hashed entry,
    // unexpected markup), don't run the detector at all — avoids false alarms.
    if (!loaded) return
    const check = async () => {
      try {
        const res = await fetch('/?_v=' + Date.now(), { cache: 'no-store' })
        if (!res.ok) return
        const html = await res.text()
        const m = html.match(/index-[A-Za-z0-9_-]+\.js/)
        if (!m) return // couldn't parse a main bundle — do nothing
        const live = m[0]
        if (live !== loaded) setUpdateAvailable(true)
      } catch {
        // Network/parse failure → no banner.
      }
    }
    const stop = startManagedInterval({
      name: 'app-version-check',
      intervalMs: 4 * 60_000, // ~4 min, visible-only (default)
      run: check,
    })
    return stop
  }, [])
  // Client-name autocomplete for the calendar event modal
  const [calClientSearchResults, setCalClientSearchResults] = useState<UnifiedSearchResult | null>(null)
  const [calClientSearchLoading, setCalClientSearchLoading] = useState(false)
  const [calClientSearchFocused, setCalClientSearchFocused] = useState(false)
  // Reset the delete confirmation whenever the meeting modal closes, so the
  // next time a modal opens it doesn't start in the "confirming" state.
  useEffect(() => {
    if (!modalMeeting) setCalEventDeleteConfirm(false)
  }, [modalMeeting])
  const [modalDeal, setModalDeal] = useState<CrmDeal | null>(null)
  const [modalContact, setModalContact] = useState<CrmContact | null>(null)
  const popupRef = useRef<HTMLDivElement>(null)

  // Reel state
  const [reelMode, _setReelMode] = useState(true)
  void _setReelMode
  const [calendarDayView, setCalendarDayView] = useState(false)
  // const reelScrollRef = useRef<HTMLDivElement>(null)
  const timelineRef = useRef<HTMLDivElement>(null)

  // Current time tracking for red line
  const [currentTime, setCurrentTime] = useState(new Date())
  const [nowLineTop, setNowLineTop] = useState<number | null>(null)
  const nowLineRaf = useRef<number>(0)
  const measureNowLine = useCallback(() => {
    const el = timelineRef.current
    if (!el) return
    const rows = el.querySelectorAll('.cal-hour-row')
    if (rows.length < 24) return
    const now = new Date()
    const h = now.getHours()
    const mFrac = now.getMinutes() / 60
    const row = rows[h] as HTMLElement
    const nextRow = rows[h + 1] as HTMLElement | undefined
    if (!row) return
    const rowTop = row.offsetTop
    const rowH = nextRow ? nextRow.offsetTop - rowTop : row.offsetHeight
    setNowLineTop(rowTop + rowH * mFrac)
  }, [])
  useEffect(() => {
    if (activeView !== 'calendar') return
    let cancelled = false
    let ro: ResizeObserver | undefined
    let frames = 0
    // Two timing hazards make the red "now" line wrong, both handled here:
    //  (1) DELAY — the lazy Calendar chunk + timeline render AFTER this effect
    //      first runs, so a one-shot measure races the render and loses (line
    //      stays blank until something re-measures — was the 15s tick).
    //  (2) WRONG HOUR — hour rows GROW as their events lay out into them, which
    //      shifts every row's offsetTop; measuring before that settles puts the
    //      line at the wrong hour (e.g. 2:30 instead of 3:59).
    // The rAF settle loop measures until the rows exist + through a short settle
    // window, and the ResizeObserver re-measures on any later relayout.
    // Observe the element whose HEIGHT actually changes as rows grow to fit their
    // events: `.cal-timeline-inner` (the scroll CONTENT), NOT the outer
    // `.cal-timeline` (a fixed-height scroll viewport that never resizes —
    // observing it misses the row growth, which left the line stuck at the wrong
    // hour until the 15s tick). Attach once it exists (fixes the original
    // null-at-setup miss). Its initial fire + every later relayout re-measures.
    const attachRO = () => {
      if (ro || cancelled) return
      const inner = timelineRef.current?.querySelector('.cal-timeline-inner')
      if (!inner) return
      ro = new ResizeObserver(() => { cancelAnimationFrame(nowLineRaf.current); nowLineRaf.current = requestAnimationFrame(measureNowLine) })
      ro.observe(inner)
    }
    const settle = () => {
      if (cancelled) return
      measureNowLine()
      attachRO()
      const ready = (timelineRef.current?.querySelectorAll('.cal-hour-row').length ?? 0) >= 24
      // Keep measuring until the rows exist AND through a short settle window
      // (~30 frames) so the line tracks the rows growing to fit their events with
      // no visible jump (the 2:30→3:59 hop). The RO covers anything later. Hard
      // frame cap so this can never loop forever if the rows never materialize.
      if ((!ready || frames < 30) && frames++ < 180) nowLineRaf.current = requestAnimationFrame(settle)
    }
    settle()
    const tick = () => {
      if (document.visibilityState !== 'visible') return
      setCurrentTime(new Date())
      measureNowLine()
    }
    const stopNowLineTick = startManagedInterval({
      name: 'calendar-now-line',
      intervalMs: 15_000,
      run: tick,
      runImmediately: false, // settle() already measures immediately + on layout
    })
    return () => { cancelled = true; cancelAnimationFrame(nowLineRaf.current); stopNowLineTick(); ro?.disconnect() }
  }, [activeView, selectedDate, measureNowLine])

  // Tasks due today (placeholder count)
  const [tasksDueExpanded, setTasksDueExpanded] = useState(false)

  // Live API state
  const [liveMeetings, setLiveMeetings] = useState<Meeting[] | null>(null)
  const [calendarDataLoading, setCalendarDataLoading] = useState(true)
  const [liveUsers, setLiveUsers] = useState<RawCalendarUser[] | null>(null)
  const [, setApiError] = useState<string | null>(null)

  // CRM data
  const [liveDeals, setLiveDeals] = useState<CrmDeal[] | null>(null)
  const [dealSort, setDealSort] = useState<{field: string; order: 'ASC'|'DESC'}>(() => {
    try { const s = localStorage.getItem('botpros-deals-sort'); if (s) return JSON.parse(s) } catch { /* ignore */ }
    return { field: 'closeDate', order: 'DESC' }
  })
  const DEAL_COL_DEFS: { key: string; label: string; sortField: string; defaultWidth: number; sortable: boolean }[] = [
    { key: 'title',       label: 'Deal name',  sortField: 'title',          defaultWidth: 240, sortable: true },
    { key: 'stage',       label: 'Stage',      sortField: 'stageLabel',     defaultWidth: 155, sortable: true },
    { key: 'opportunity', label: 'Amount',     sortField: 'opportunity',    defaultWidth: 110, sortable: true },
    { key: 'closeDate',   label: 'Appt. Date', sortField: 'closeDate',     defaultWidth: 130, sortable: true },
    { key: 'contact',     label: 'Contact',    sortField: 'contact_name',  defaultWidth: 160, sortable: true },
    { key: 'projects',    label: 'Projects',   sortField: 'project_count', defaultWidth: 90,  sortable: true },
    { key: 'tickets',     label: 'Tickets',    sortField: 'ticket_count',  defaultWidth: 90,  sortable: true },
    { key: 'owner',       label: 'Owner',      sortField: 'assignedTo',    defaultWidth: 130, sortable: true },
    { key: 'dateCreated', label: 'Date Created', sortField: 'dateCreated',  defaultWidth: 130, sortable: true },
  ]
  const DEAL_COL_DEFAULT_WIDTHS: Record<string, number> = Object.fromEntries(DEAL_COL_DEFS.map(d => [d.key, d.defaultWidth]))
  const [dealColWidths, setDealColWidths] = useState<Record<string, number>>(() => {
    // Persisted per-user config may predate column redesigns or be corrupt —
    // heal it on load (docs/REGRESSIONS.md 2026-07-02).
    let raw: string | null = null
    try { raw = localStorage.getItem('botpros-deals-col-widths') } catch { /* storage unavailable */ }
    return sanitizeColWidths(raw, DEAL_COL_DEFAULT_WIDTHS)
  })
  const DEFAULT_DEAL_COLS = ['title','stage','opportunity','closeDate','contact','owner','dateCreated']
  const [dealVisibleCols, setDealVisibleCols] = useState<string[]>(() => {
    try { const s = localStorage.getItem('botpros-deals-visible-cols'); if (s) { const parsed = JSON.parse(s); if (Array.isArray(parsed) && parsed.includes('title')) return parsed } } catch { /* ignore */ }
    return DEFAULT_DEAL_COLS
  })
  const [dealEditColsOpen, setDealEditColsOpen] = useState(false)
  const [dealEditColsDraft, setDealEditColsDraft] = useState<string[]>([])
  const [dealEditColsSearch, setDealEditColsSearch] = useState('')
  const resizingDealCol = useRef<{ key: string; startX: number; startWidth: number } | null>(null)
  const onDealResizeStart = (key: string, e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation()
    const def = DEAL_COL_DEFS.find(d => d.key === key)
    resizingDealCol.current = { key, startX: e.clientX, startWidth: dealColWidths[key] ?? def?.defaultWidth ?? 120 }
    const onMouseMove = (ev: MouseEvent) => {
      // Snapshot the drag ref — the queued updater replays after mouseup may
      // have nulled it (2026-07-01 #/calls P0; same race here).
      const drag = resizingDealCol.current
      if (!drag) return
      const newWidth = Math.max(60, drag.startWidth + (ev.clientX - drag.startX))
      setDealColWidths(prev => ({ ...prev, [drag.key]: newWidth }))
    }
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
      if (resizingDealCol.current) {
        setDealColWidths(prev => { try { localStorage.setItem('botpros-deals-col-widths', JSON.stringify(prev)) } catch { /* ignore */ } return prev })
        resizingDealCol.current = null
      }
      const swallow = (ev: MouseEvent) => { ev.preventDefault(); ev.stopPropagation(); document.removeEventListener('click', swallow, true) }
      document.addEventListener('click', swallow, true)
      setTimeout(() => document.removeEventListener('click', swallow, true), 200)
    }
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }
  const toggleDealSort = (field: string) => {
    setDealSort(prev => {
      const next: {field: string; order: 'ASC'|'DESC'} = prev.field === field
        ? { field, order: prev.order === 'ASC' ? 'DESC' : 'ASC' }
        : { field, order: 'ASC' }
      try { localStorage.setItem('botpros-deals-sort', JSON.stringify(next)) } catch { /* ignore */ }
      return next
    })
  }
  const [livePipeline, setLivePipeline] = useState<PipelineStage[] | null>(null)
  // Home owns a broad, agency-wide stage snapshot. The Deals view keeps its
  // subsidiary-aware pipeline state separately for its own filters.
  const [homePipeline, setHomePipeline] = useState<PipelineStage[] | null>(null)
  // Incremented after any deal mutation to force the dashboard "Pipeline by
  // stage" panel to refetch so stage counts + values reflect the latest state.
  const [pipelineRefreshTick, setPipelineRefreshTick] = useState(0)
  // Bumped after a deal is created with an appointment so the calendar refetches
  // and shows the freshly-created event without a manual refresh.
  const [calendarRefreshTick, setCalendarRefreshTick] = useState(0)
  const [dashboardOwner, setDashboardOwner] = useState('all')
  const [dashboardLastRefresh, setDashboardLastRefresh] = useState<Date | null>(null)
  // Retained for the Deals view's legacy filter state; Home no longer exposes
  // or applies a subsidiary selector.
  const [dashboardSubsidiary, setDashboardSubsidiary] = useState<string>(() => {
    try { return localStorage.getItem('dashboard_subsidiary') || 'painting' } catch { return 'painting' }
  })
  // Time-window filter for "Won This Period" KPI. Affects only the Won cards —
  // Active Deals / Pipeline Value remain current-as-of-now regardless.
  type DashboardPeriod = 'today' | 'week' | 'month' | 'quarter' | 'year' | 'all'
  const [dashboardPeriod, setDashboardPeriod] = useState<DashboardPeriod>(() => {
    try {
      const v = localStorage.getItem('dashboard_period')
      if (v === 'today' || v === 'week' || v === 'month' || v === 'quarter' || v === 'year' || v === 'all') return v
    } catch { /* ignore */ }
    return 'month'
  })
  const [commandOwnerFilter, setCommandOwnerFilter] = useState<string>(() => {
    try { return localStorage.getItem('command_owner_filter') || 'all' } catch { return 'all' }
  })
  const [commandRefreshTick, setCommandRefreshTick] = useState(0)
  const [commandLastUpdated, setCommandLastUpdated] = useState<Date | null>(null)
  const [commandError, setCommandError] = useState<string | null>(null)
  const [commandTasks, setCommandTasks] = useState<CrmTask[]>([])
  const [commandTasksLoading, setCommandTasksLoading] = useState(false)
  // Home dashboard panel expansion — only one panel can be expanded at a time.
  // When a panel is expanded it takes the full command-grid area; the others
  // are hidden. Interactive controls on a panel are disabled unless that panel
  // is expanded, preventing accidental changes from the compact view.
  const [expandedPanel, setExpandedPanel] = useState<string | null>(null)
  // Esc / back-chevron returns from an expanded home panel to the bento grid
  // (spec: "Esc / back-chevron returns"). Only bound while a panel is expanded.
  useEffect(() => {
    if (!expandedPanel) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        setExpandedPanel(null)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [expandedPanel])
  const [scheduleTab, setScheduleTab] = useState<'today' | 'upcoming'>('today')
  // Delivery watch — in-progress projects on the home dashboard. Polled every
  // 60s so a newly auto-advanced project (on owner approval) surfaces without
  // a page reload. Fetches via the standard /api/crm/projects endpoint with
  // pipelineStages=P_IN_PROGRESS so filtering happens server-side.
  const [deliveryProjects, setDeliveryProjects] = useState<any[] | null>(null)
  const [deliveryLoading, setDeliveryLoading] = useState(false)
  const [homeAccounting, setHomeAccounting] = useState<{ openAr: number; overdueInvoices: number; mtdRevenue: number | null } | null>(null)
  const [homeAccountingLoading, setHomeAccountingLoading] = useState(false)
  const [homeAccountingError, setHomeAccountingError] = useState(false)
  const [marketingPulse, setMarketingPulse] = useState<HomeMarketingItem[] | null>(null)
  const [marketingPulseLoading, setMarketingPulseLoading] = useState(false)
  const [marketingPulseError, setMarketingPulseError] = useState(false)
  const [, setLiveContacts] = useState<CrmContact[] | null>(null)
  const [liveLeads, setLiveLeads] = useState<CrmLead[] | null>(null)
  const [, setLeadTotal] = useState(0)
  const [dealTotal, setDealTotal] = useState(0)
  const [dealPage, setDealPage] = useState(1)
  const [dealPages, setDealPages] = useState(1)
  const [dealPageSize, setDealPageSize] = useState(200)
  const [, setContactTotal] = useState(0)
  const [modalLead, setModalLead] = useState<CrmLead | null>(null)
  const [selectedDeal, setSelectedDeal] = useState<CrmDeal | null>(null)
  const [dealActivities, setDealActivities] = useState<CrmActivity[]>([])
  const [dealActivitiesLoading, setDealActivitiesLoading] = useState(false)
  const [dealTranscriptOpen, setDealTranscriptOpen] = useState<number | null>(null)
  const [dealViewMode, setDealViewMode] = useState<'table' | 'board'>('table')
  // Archived deals mode — surfaces the ~14k legacy deals filtered out by
  // exclude_closed=true on the API. Forces table view + smaller page size.
  // Always reset to false on every Deals view enter so the user lands on
  // "All deals" — clicking the Archived pill is the ONLY way in. (Without
  // this reset the state survives nav-away-and-back since App never
  // unmounts, which the user perceived as "initial load is Archive".)
  // Deals list scope — three tabs in the topbar:
  //   'open'   — active pipeline (excludes Contract Signed / Lost) [default]
  //   'closed' — CONTRACT_SIGNED only (deals that have advanced to projects)
  //   'all'    — every deal including pre-migration historical (~14k)
  const [dealsScope, setDealsScope] = useState<'open' | 'closed' | 'all'>('open')
  const dealsArchiveMode = dealsScope !== 'open'
  const [dealSelectedRows, setDealSelectedRows] = useState<Set<string>>(new Set())
  const [dealMoveStageOpen, setDealMoveStageOpen] = useState(false)
  const [dealsDeleteConfirm, setDealsDeleteConfirm] = useState<number | null>(null)
  const [dealsDeleteRunning, setDealsDeleteRunning] = useState(false)
  const [projectsDeleteConfirm, setProjectsDeleteConfirm] = useState<number | null>(null)
  const [projectsDeleteRunning, setProjectsDeleteRunning] = useState(false)
  const [leadsArchiveConfirm, setLeadsArchiveConfirm] = useState<number | null>(null)
  const [leadsArchiveRunning, setLeadsArchiveRunning] = useState(false)
  const [dealDeleteModal, setDealDeleteModal] = useState<
    | null
    | {
        deal: { id: string | number; title: string }
        preview: { linked_projects: number; linked_tickets: number }
        step: 1 | 2
        typed: string
        afterDelete?: () => void
      }
  >(null)
  const [dealStageFilter, setDealStageFilter] = useState<Set<string>>(new Set())
  const [dealsStageFilterPopover, setDealsStageFilterPopover] = useState<{ x: number; y: number } | null>(null)
  const [dealOwnerFilter, setDealOwnerFilter] = useState<Set<string>>(new Set())
  const [dealAmountFilter, setDealAmountFilter] = useState<string>('') // '', 'lt5k', '5k-25k', '25k-100k', 'gt100k'
  const [dealCloseDateFilter, setDealCloseDateFilter] = useState<string>('') // '', 'overdue', 'today', 'tomorrow', 'this-week', 'this-month'
  const [dealQfPillOpen, setDealQfPillOpen] = useState<string | null>(null)
  const dealQfPillRef = useRef<HTMLDivElement>(null)
  const [dealSearch, setDealSearch] = useState('')
  const [dealSearchDebounced, setDealSearchDebounced] = useState('')
  const dealSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [dealDetailOpen, setDealDetailOpen] = useState(() => {
    const seg = getHashSegments()
    return seg[0] === 'deals' && seg[1] === 'record' && !!seg[2]
  })
  const [dealDetailTab, setDealDetailTab] = useState<'about' | 'activities' | 'financials'>('about')
  const [dealActionsOpen, setDealActionsOpen] = useState(false)
  useEffect(() => {
    if (!dealsStageFilterPopover) return
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('.deals-stage-filter-popover')) setDealsStageFilterPopover(null)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [dealsStageFilterPopover])
  const [dealLinkedLead, setDealLinkedLead] = useState<any>(null)
  const [dealLinkedLeadTried, setDealLinkedLeadTried] = useState(false)
  const [dealLinkedClient, setDealLinkedClient] = useState<CrmClient | null>(null)
  // Tracks whether the deal's linked-client fetch has completed (success OR failure).
  // Used so the sidebar Contacts legacy fallback can render a name-only row instead
  // of perpetual "Loading…" when the contact is missing / the endpoint 404s.
  const [dealLinkedClientTried, setDealLinkedClientTried] = useState(false)
  const [dealLinkedProjects, setDealLinkedProjects] = useState<any[]>([])
  const [dealTickets, setDealTickets] = useState<any[]>([])
  const [dealAllocatedTotal, setDealAllocatedTotal] = useState(0)
  const [dealCreateProjectOpen, setDealCreateProjectOpen] = useState(false)
  const [dealCreateProjectForm, setDealCreateProjectForm] = useState({ name: '', contractor_price: '', work_order_price: '', address: '', address_street: '', address_city: '', address_state: '', address_zip: '', services_requested: '', customer_name: '' })
  const [dealCreateProjectSaving, setDealCreateProjectSaving] = useState(false)
  const [dealAddresses, setDealAddresses] = useState<any[]>([])
  const [dealAddressAddOpen, setDealAddressAddOpen] = useState(false)
  const [dealNewAddrForm, setDealNewAddrForm] = useState({ label: '', street: '', city: '', state: '', zip: '', formatted: '' })
  const [dealAddrImportOpen, setDealAddrImportOpen] = useState(false)
  const [dealAddrImportSelected, setDealAddrImportSelected] = useState<Set<number>>(new Set())
  const [dealNewAddrFormOpen, setDealNewAddrFormOpen] = useState(false)
  const [dealAddrExpandedId, setDealAddrExpandedId] = useState<number | null>(null)
  // Project addresses (mirrors deal_addresses for the project record card)
  const [projectAddresses, setProjectAddresses] = useState<any[]>([])
  const [projectNewAddrForm, setProjectNewAddrForm] = useState({ label: '', street: '', city: '', state: '', zip: '', formatted: '' })
  const [projectAddrImportOpen, setProjectAddrImportOpen] = useState(false)
  const [projectAddrImportSelected, setProjectAddrImportSelected] = useState<Set<number>>(new Set())
  const [projectNewAddrFormOpen, setProjectNewAddrFormOpen] = useState(false)
  const [projectAddrExpandedId, setProjectAddrExpandedId] = useState<number | null>(null)
  const [dealApptAddrPickerOpen, setDealApptAddrPickerOpen] = useState(false)
  const [dealAttachUploading, setDealAttachUploading] = useState(false)

  // Multi-email support for contacts (mirrors deal_addresses pattern)
  const [contactEmails, setContactEmails] = useState<Array<{ id: number; contact_id: string; address: string; is_primary: number; is_billing: number; created_at: string }>>([])
  // Multi-address + multi-phone (migrations 049/050)
  const [contactAddresses, setContactAddresses] = useState<Array<{ id: number; contact_id: string; label: string | null; street: string | null; city: string | null; state: string | null; zip: string | null; is_primary: number; is_billing: number; is_job_site?: number; created_at: string }>>([])
  const [contactPhones, setContactPhones] = useState<Array<{ id: number; contact_id: string; number: string; label: string | null; ext: string | null; is_primary: number; created_at: string }>>([])
  // Inline add/edit UI state for address + phone lists on the Contact Record page.
  const [contactAddrEditId, setContactAddrEditId] = useState<number | null>(null)
  const [contactAddrDraft, setContactAddrDraft] = useState<{ label: string; street: string; city: string; state: string; zip: string; is_primary: boolean; is_billing: boolean; is_job_site: boolean }>({ label: '', street: '', city: '', state: '', zip: '', is_primary: false, is_billing: false, is_job_site: false })
  const [contactAddrAdding, setContactAddrAdding] = useState(false)
  // Org/Person 360: which non-primary address groups are expanded (default collapsed).
  const [contactAddrExpandedGroups, setContactAddrExpandedGroups] = useState<Set<string>>(new Set())
  const [contactPhoneEditId, setContactPhoneEditId] = useState<number | null>(null)
  const [contactPhoneDraft, setContactPhoneDraft] = useState<{ number: string; label: string; ext: string; is_primary: boolean }>({ number: '', label: 'Mobile', ext: '', is_primary: false })
  const [contactPhoneAdding, setContactPhoneAdding] = useState(false)
  // === Contact people (multi-contact-person model; marker cp-detail-persons-v1) ===
  // Named child person rows on the client record — loaded on record-detail / preview
  // open beside contactEmails/contactPhones. Within a person, "primary" = the
  // is_primary row (server keeps one per client). Renders on both person and org
  // detail (org = named people at the org, with role_label).
  const [contactPersons, setContactPersons] = useState<ContactPerson[]>([])
  const [contactPersonAdding, setContactPersonAdding] = useState(false)
  const [contactPersonDraft, setContactPersonDraft] = useState<{ full_name: string; role_label: string; email: string; phone: string; is_primary: boolean }>({ full_name: '', role_label: '', email: '', phone: '', is_primary: false })
  const [contactPersonEditId, setContactPersonEditId] = useState<number | null>(null)
  const [contactPersonEditDraft, setContactPersonEditDraft] = useState<{ full_name: string; role_label: string }>({ full_name: '', role_label: '' })
  const [contactPersonBusy, setContactPersonBusy] = useState(false)
  // Inline validation for the detail crp-name editor: a person/couple guard that
  // runs before the PATCH (comma → swap suggestion, '&'/' and ' → split affordance).
  const [crpNameError, setCrpNameError] = useState<{ message: string; suggestion?: NameSuggestion } | null>(null)

  // Contact Record page highlight-area inline editing (name / type / primary email).
  // Also covers the Key Info card's Phone (inline) + Type (inline select) rows.
  // `null` = not editing that field; otherwise holds the draft value while editing.
  const [crpEditingField, setCrpEditingField] = useState<null | 'name' | 'type' | 'email' | 'keyinfo_type'>(null)
  const [crpEditDraft, setCrpEditDraft] = useState<string>('')
  const [crpEditSaving, setCrpEditSaving] = useState(false)

  useEffect(() => {
    if (dealAddrExpandedId === null) return
    const handler = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null
      if (!t) return
      // Ignore clicks inside any deal-addr-row (so clicking another row still
      // toggles via its own onClick) or inside any modal/overlay that was
      // opened from the row (e.g. the switch-confirm dialog).
      if (t.closest('.deal-addr-row') || t.closest('.sub-admin-overlay') || t.closest('.sub-admin-dialog')) return
      setDealAddrExpandedId(null)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [dealAddrExpandedId])
  const [dealHistory, setDealHistory] = useState<any[]>([])
  const [dealTimelineLimit, setDealTimelineLimit] = useState(5)
  // Server-side fetch window for deal activities (default 300; "Load older
  // activity" grows it up to the API's 1000 cap).
  const [dealActFetchLimit, setDealActFetchLimit] = useState(300)
  const [dealActFetchBusy, setDealActFetchBusy] = useState(false)
  // Inline edit state for the Contacts sidebar card on the deal record page.
  // Fields: name (preferred_name), phone (preferred_phone), email (preferred_email).
  const [dealContactEditField, setDealContactEditField] = useState<null | 'name' | 'phone' | 'email'>(null)
  const [dealContactEditValue, setDealContactEditValue] = useState('')
  const [dealContactSaving, setDealContactSaving] = useState(false)
  // Consolidated edit mode for the Contacts sidebar card — single pencil
  // opens a form editing name + phone + email + address at once.
  const [dealContactEditAll, setDealContactEditAll] = useState(false)
  const [dealContactEditAllForm, setDealContactEditAllForm] = useState<{ name: string; phone: string; email: string; street: string; city: string; state: string; zip: string }>({ name: '', phone: '', email: '', street: '', city: '', state: '', zip: '' })
  const [dealContactPickerOpen, setDealContactPickerOpen] = useState(false)
  const [dealContactSearch, setDealContactSearch] = useState('')
  const [dealContactResults, setDealContactResults] = useState<any[]>([])
  const [dealContactSearching, setDealContactSearching] = useState(false)
  const [dealContacts, setDealContacts] = useState<any[]>([])
  // Emails for the deal's currently-selected primary_contact_id. Loaded by a
  // useEffect triggered by the Contact card whenever the pinned contact
  // changes. Each row: { id, contact_id, address, is_primary, is_billing, created_at }
  const [dealContactEmails, setDealContactEmails] = useState<any[]>([])
  // Key Information card popover state (Contact / Primary / Billing / Phone rows).
  // Only one popover open at a time; null = closed.
  const [dealKeyInfoPopover, setDealKeyInfoPopover] = useState<null | 'contact' | 'primary' | 'billing' | 'phone'>(null)
  // Deal Contact sub-panel edit-mode toggle. When true, all 4 row values
  // (Contact / Primary / Billing / Phone) become click-to-open popover
  // triggers; when false, they are plain display text. Toggled by the single
  // pencil on the sub-panel's "Deal Contact" subheader.
  const [dealContactPanelEditing, setDealContactPanelEditing] = useState(false)
  // Viewport-fixed position of the open popover, computed from the anchor row's
  // bounding rect so the popover escapes parent stacking contexts / overflow:hidden
  // on .crp-card / .crp-right. Recomputed on scroll/resize while open.
  const [dealKeyInfoPopoverPos, setDealKeyInfoPopoverPos] = useState<{ top: number; left: number; maxHeight?: number } | null>(null)
  // Refs to each anchor span so scroll/resize can recompute position.
  const dealKeyInfoAnchorRefs = useRef<Record<string, HTMLSpanElement | null>>({})
  // Ref to the currently-rendered popover div so recompute() can read its
  // actual offsetHeight for the flip calculation.
  const dealKeyInfoPopoverRef = useRef<HTMLDivElement | null>(null)
  const [dealKeyInfoAddEmail, setDealKeyInfoAddEmail] = useState('')
  const [dealKeyInfoAddEmailOpen, setDealKeyInfoAddEmailOpen] = useState(false)
  const [dealKeyInfoPhoneDraft, setDealKeyInfoPhoneDraft] = useState('')
  // Key Info → Contact popover: search-all-contacts + add-new-contact
  // sub-states. Both panels live inside the contact popover (below the
  // associated-contacts list). Reset whenever the popover closes.
  const [dealKeyInfoContactSearch, setDealKeyInfoContactSearch] = useState('')
  const [dealKeyInfoContactSearchResults, setDealKeyInfoContactSearchResults] = useState<any[]>([])
  const [dealKeyInfoContactSearching, setDealKeyInfoContactSearching] = useState(false)
  const [dealKeyInfoAddContactOpen, setDealKeyInfoAddContactOpen] = useState(false)
  const [dealKeyInfoAddContactForm, setDealKeyInfoAddContactForm] = useState<{ name: string; email: string; phone: string }>({ name: '', email: '', phone: '' })
  const [dealKeyInfoAddContactSaving, setDealKeyInfoAddContactSaving] = useState(false)
  // Dedup lookup hints — as the user types an email/phone in any of the
  // Deal Contact sub-panel "add" flows (Primary/Billing Email popover,
  // Phone popover, Add-new-contact inline form), we debounce-check the
  // backend for an existing contact with that email/phone and surface a
  // "Use existing [Name]" suggestion. Each input tracks its own match so
  // hints don't cross-contaminate.
  type DedupMatch = { id: string; name: string; email: string | null; phone: string | null }
  const [dealKeyInfoAddEmailMatch, setDealKeyInfoAddEmailMatch] = useState<DedupMatch | null>(null)
  const [dealKeyInfoPhoneMatch, setDealKeyInfoPhoneMatch] = useState<DedupMatch | null>(null)
  const [dealKeyInfoAddContactEmailMatch, setDealKeyInfoAddContactEmailMatch] = useState<DedupMatch | null>(null)
  const [dealKeyInfoAddContactPhoneMatch, setDealKeyInfoAddContactPhoneMatch] = useState<DedupMatch | null>(null)
  // Dedup confirm modal — opened when the user clicks a hint card to pick
  // an action (add-to-current, switch-to-existing, or cancel). Shared across
  // the 4 hint sites; `source` tells the confirm handlers which popover /
  // form to close on success.
  type DedupConfirm = {
    match: DedupMatch
    field: 'email' | 'phone'
    value: string  // the typed value (email address or phone digits)
    // 'add-flow'      = Primary/Billing Email popover or Phone popover — has a
    //                   current contact to add-to. Offers all 3 options.
    // 'new-contact'   = + Add new contact inline form — no current contact;
    //                   swaps "add to current" for "use instead of creating new".
    source: 'add-email' | 'phone' | 'new-contact-email' | 'new-contact-phone'
    currentContactId: string | null
  }
  const [dedupConfirm, setDedupConfirm] = useState<DedupConfirm | null>(null)
  const [dedupConfirmSaving, setDedupConfirmSaving] = useState(false)
  // Tracks the deal id we already auto-picked for so the auto-pick effect
  // doesn't fight a user who manually unpinned. Reset when the deal changes.
  const dealKeyInfoAutoPickedRef = useRef<{ contact: number | null; email: number | null }>({ contact: null, email: null })
  const [dealCompanyPickerOpen, setDealCompanyPickerOpen] = useState(false)
  const [dealCompanySearch, setDealCompanySearch] = useState('')
  const [dealCompanyResults, setDealCompanyResults] = useState<any[]>([])
  const [dealCompanySearching, setDealCompanySearching] = useState(false)

  useEffect(() => {
    if (!dealCompanyPickerOpen) return
    const q = dealCompanySearch.trim()
    if (!q) { setDealCompanyResults([]); return }
    setDealCompanySearching(true)
    const t = setTimeout(() => {
      fetchClients(1, 15, { search: q, type: 'organization' })
        .then((r: any) => setDealCompanyResults(r.clients || r.data || []))
        .catch(() => setDealCompanyResults([]))
        .finally(() => setDealCompanySearching(false))
    }, 250)
    return () => clearTimeout(t)
  }, [dealCompanyPickerOpen, dealCompanySearch])

  // Dedup lookup helper — hits the lightweight /api/crm/contacts/lookup
  // endpoint. Returns the first match (or null). Errors/empty → null, which
  // renders nothing (no noisy error UI).
  // Returns the full match list (up to 5). Caller picks the best one — typically
  // by preferring a non-self match over the current contact, so a phone shared
  // by Max Test AND the current contact will surface Max Test as the suggestion.
  const lookupContactDedup = async (opts: { email?: string; phone?: string }): Promise<DedupMatch[]> => {
    const params = new URLSearchParams()
    if (opts.email) params.set('email', opts.email)
    if (opts.phone) params.set('phone', opts.phone)
    if ([...params.keys()].length === 0) return []
    try {
      const r = await fetch(`/api/crm/contacts/lookup?${params.toString()}`)
      if (!r.ok) return []
      const body = await r.json() as { matches?: DedupMatch[] }
      return body?.matches || []
    } catch {
      return []
    }
  }
  // Helper: pick the best match — prefer one whose id != currentContactId, fall
  // back to the first (which may be the current contact, rendered as "(current)").
  const pickBestDedup = (matches: DedupMatch[]): DedupMatch | null => {
    if (!matches.length) return null
    const currentId = (selectedDeal as any)?.primaryContactId || null
    const other = currentId ? matches.find(m => m.id !== currentId) : null
    return other || matches[0]
  }

  // (1) Primary/Billing Email popover "+ Add email" input — debounce 300ms.
  useEffect(() => {
    if (!dealKeyInfoAddEmailOpen) { setDealKeyInfoAddEmailMatch(null); return }
    const q = dealKeyInfoAddEmail.trim().toLowerCase()
    if (q.length < 3) { setDealKeyInfoAddEmailMatch(null); return }
    let cancelled = false
    const t = setTimeout(() => {
      lookupContactDedup({ email: q }).then(ms => {
        if (cancelled) return
        // pickBestDedup prefers a non-self match so "Found existing: Max"
        // wins over "Already on: current contact" when both match the typed
        // value. Falls back to self if that's the only match.
        setDealKeyInfoAddEmailMatch(pickBestDedup(ms))
      })
    }, 300)
    return () => { cancelled = true; clearTimeout(t) }
  }, [dealKeyInfoAddEmail, dealKeyInfoAddEmailOpen, selectedDeal])

  // (2) Phone popover input — debounce 300ms. Compares digits only.
  useEffect(() => {
    if (dealKeyInfoPopover !== 'phone') { setDealKeyInfoPhoneMatch(null); return }
    const digits = dealKeyInfoPhoneDraft.replace(/\D/g, '')
    if (digits.length < 4) { setDealKeyInfoPhoneMatch(null); return }
    let cancelled = false
    const t = setTimeout(() => {
      lookupContactDedup({ phone: digits }).then(ms => {
        if (cancelled) return
        setDealKeyInfoPhoneMatch(pickBestDedup(ms))
      })
    }, 300)
    return () => { cancelled = true; clearTimeout(t) }
  }, [dealKeyInfoPhoneDraft, dealKeyInfoPopover, selectedDeal])

  // (3a) Add-new-contact inline form — email input.
  useEffect(() => {
    if (!dealKeyInfoAddContactOpen) { setDealKeyInfoAddContactEmailMatch(null); return }
    const q = dealKeyInfoAddContactForm.email.trim().toLowerCase()
    if (q.length < 3) { setDealKeyInfoAddContactEmailMatch(null); return }
    let cancelled = false
    const t = setTimeout(() => {
      lookupContactDedup({ email: q }).then(ms => {
        if (cancelled) return
        setDealKeyInfoAddContactEmailMatch(pickBestDedup(ms))
      })
    }, 300)
    return () => { cancelled = true; clearTimeout(t) }
  }, [dealKeyInfoAddContactForm.email, dealKeyInfoAddContactOpen])

  // (3b) Add-new-contact inline form — phone input.
  useEffect(() => {
    if (!dealKeyInfoAddContactOpen) { setDealKeyInfoAddContactPhoneMatch(null); return }
    const digits = dealKeyInfoAddContactForm.phone.replace(/\D/g, '')
    if (digits.length < 4) { setDealKeyInfoAddContactPhoneMatch(null); return }
    let cancelled = false
    const t = setTimeout(() => {
      lookupContactDedup({ phone: digits }).then(ms => {
        if (cancelled) return
        setDealKeyInfoAddContactPhoneMatch(pickBestDedup(ms))
      })
    }, 300)
    return () => { cancelled = true; clearTimeout(t) }
  }, [dealKeyInfoAddContactForm.phone, dealKeyInfoAddContactOpen])

  useEffect(() => {
    if (!dealContactPickerOpen) return
    const q = dealContactSearch.trim()
    if (!q) { setDealContactResults([]); return }
    setDealContactSearching(true)
    const t = setTimeout(() => {
      fetchClients(1, 15, { search: q })
        .then((r: any) => setDealContactResults(r.clients || r.data || []))
        .catch(() => setDealContactResults([]))
        .finally(() => setDealContactSearching(false))
    }, 250)
    return () => clearTimeout(t)
  }, [dealContactPickerOpen, dealContactSearch])
  // Contact hover card (deals table view): shows name/phone/email/address on hover
  const [contactHoverCard, setContactHoverCard] = useState<null | { clientId: string; x: number; y: number; flipX: boolean; client: CrmClient | null }>(null)
  const contactHoverCacheRef = useRef<Map<string, CrmClient>>(new Map())
  const contactHoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const contactHoverCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // No-Show / Reschedule modal for the deal detail page — separate from the
  // kanban board's modal so they don't interfere with each other.
  const [dealDetailNoShowModal, setDealDetailNoShowModal] = useState<{ dealId: number; dealTitle: string } | null>(null)
  const [dealDetailNoShowReason, setDealDetailNoShowReason] = useState('Customer no-show')
  const [dealDetailNoShowNewAppt, setDealDetailNoShowNewAppt] = useState('')
  const [dealDetailNoShowEstimatorId, setDealDetailNoShowEstimatorId] = useState<number | null>(null)
  // "Send email to client" checkbox for the deal-record No-Show/Reschedule modal.
  // Default-on; reset to true each time the modal opens. The backend picks the
  // copy (reschedule vs cancellation) based on whether a new time was provided.
  const [dealDetailNoShowSendEmail, setDealDetailNoShowSendEmail] = useState(true)
  useEffect(() => {
    if (dealDetailNoShowModal && selectedDeal) {
      const raw = (selectedDeal as any).ownerId ?? (selectedDeal as any).assignedUserId ?? null
      setDealDetailNoShowEstimatorId(raw ? parseInt(String(raw)) : null)
      setDealDetailNoShowSendEmail(true)
    }
  }, [dealDetailNoShowModal, selectedDeal])
  const [dealDetailNoShowSaving, setDealDetailNoShowSaving] = useState(false)
  const [dealNotesEditing, setDealNotesEditing] = useState(false)
  const [dealNotesValue, setDealNotesValue] = useState('')
  // Files staged for upload when the deal note is saved. Each file becomes
  // part of a new NOTE activity in the timeline via /api/crm/deals/:id/attachments.
  const [dealNotesFiles, setDealNotesFiles] = useState<File[]>([])
  const [dealNotesUploading, setDealNotesUploading] = useState(false)
  const dealNotesFileInputRef = useRef<HTMLInputElement>(null)

  // Contact preview sidebar
  const [previewContact, setPreviewContact] = useState<CrmContact | null>(null)
  const [, setPreviewClosing] = useState(false)

  // Contacts page (D1 clients — persons only)
  const [clientList, setClientList] = useState<CrmClient[]>([])
  const [clientTotal, setClientTotal] = useState(0)
  const [clientPages, setClientPages] = useState(1)
  const userKey = useRef((() => {
    try { const p = JSON.parse(localStorage.getItem('prefsSettings') || '{}'); return `${(p.firstName || 'default')}_${(p.lastName || '')}`.toLowerCase().replace(/\s+/g, '') }
    catch { return 'default' }
  })())
  const uKey = (k: string) => `${userKey.current}:${k}`

  const [clientPage, setClientPage] = useState(() => Number(sessionStorage.getItem(uKey('clientPage'))) || 1)
  const [clientSearch, setClientSearch] = useState(() => sessionStorage.getItem(uKey('clientSearch')) || '')
  const [clientSearchDebounced, setClientSearchDebounced] = useState('')
  const [clientSort, setClientSort] = useState<{ field: string; order: 'ASC' | 'DESC' }>({ field: 'created_at', order: 'DESC' })
  const [clientViewMode, setClientViewMode] = useState<'table' | 'board'>('table')
  const [clientStatusFilter, setClientStatusFilter] = useState('')
  const [clientLoading, setClientLoading] = useState(false)
  const [clientExporting, setClientExporting] = useState(false)
  const [clientSelectedRows, setClientSelectedRows] = useState<Set<string>>(new Set())
  // Clear selections when leaving the view
  useEffect(() => { setClientSelectedRows(new Set()) }, [activeView])
  // Always reset Deals to "All deals" (active) on every Deals enter — the
  // Archived pill is the ONLY way to enter archive mode. Without this the
  // state survives nav-away-and-back (App never unmounts on hash change).
  useEffect(() => { if (activeView === 'deals') setDealsScope('open') }, [activeView])
  const [previewClient, setPreviewClient] = useState<CrmClient | null>(null)
  const [previewClientClosing, setPreviewClientClosing] = useState(false)
  // Organization logos use the same crop/zoom flow as profile photos. The
  // modal owns the in-progress file; the upload only starts after Save.
  const [orgLogoCropFile, setOrgLogoCropFile] = useState<File | null>(null)
  // Staged (unsaved) changes for the Contact Record page. Key is the
  // camelCase API field; value is the new value (null = clear). While this
  // is non-empty, the sticky "Save changes" footer is shown.
  const [contactPendingChanges, setContactPendingChanges] = useState<Record<string, string | null>>({})
  const [contactSavingChanges, setContactSavingChanges] = useState(false)
  // Clear any pending edits when the contact switches (or the page closes)
  useEffect(() => { setContactPendingChanges({}) }, [previewClient?.unified_client_id])
  // Load contact_emails rows whenever the active contact changes.
  // Shared helper so the email editor can refresh after save/delete.
  const refreshContactEmails = useCallback(async (contactId: string | null | undefined) => {
    if (!contactId) { setContactEmails([]); return }
    try {
      const r = await fetch(`/api/crm/contacts/${encodeURIComponent(contactId)}/emails`)
      const rows = await r.json()
      setContactEmails(Array.isArray(rows) ? rows : [])
    } catch {
      setContactEmails([])
    }
  }, [])
  useEffect(() => {
    refreshContactEmails(previewClient?.unified_client_id)
  }, [previewClient?.unified_client_id, refreshContactEmails])
  // Refresh helpers + load effect for contact_addresses / contact_phones.
  const refreshContactAddresses = useCallback(async (contactId: string | null | undefined) => {
    if (!contactId) { setContactAddresses([]); return }
    try {
      const r = await fetch(`/api/crm/contacts/${encodeURIComponent(contactId)}/addresses`)
      const rows = await r.json()
      setContactAddresses(Array.isArray(rows) ? rows : [])
    } catch { setContactAddresses([]) }
  }, [])
  const refreshContactPhones = useCallback(async (contactId: string | null | undefined) => {
    if (!contactId) { setContactPhones([]); return }
    try {
      const r = await fetch(`/api/crm/contacts/${encodeURIComponent(contactId)}/phones`)
      const rows = await r.json()
      setContactPhones(Array.isArray(rows) ? rows : [])
    } catch { setContactPhones([]) }
  }, [])
  useEffect(() => {
    refreshContactAddresses(previewClient?.unified_client_id)
    refreshContactPhones(previewClient?.unified_client_id)
  }, [previewClient?.unified_client_id, refreshContactAddresses, refreshContactPhones])
  // Load contact_persons (with their attributed emails/phones) whenever the active
  // record changes — covers both the record-detail card and the peek preview pane.
  const refreshContactPersons = useCallback(async (contactId: string | null | undefined) => {
    if (!contactId) { setContactPersons([]); return }
    try {
      const rows = await listContactPersons(contactId)
      setContactPersons(Array.isArray(rows) ? rows : [])
    } catch { setContactPersons([]) }
  }, [])
  useEffect(() => {
    refreshContactPersons(previewClient?.unified_client_id)
  }, [previewClient?.unified_client_id, refreshContactPersons])
  // Commit a validated display name from the detail crp-name editor (shared by the
  // valid-blur path and the one-click "Use ‹First Last›" swap suggestion).
  const commitContactName = useCallback(async (client: any, value: string) => {
    const next = (value || '').trim()
    if (!client || !next) return
    setCrpNameError(null)
    setCrpEditSaving(true)
    setPreviewClient({ ...client, preferred_name: next })
    try {
      const updated = await updateClient(client.unified_client_id, { preferredName: next })
      setPreviewClient(updated)
    } catch (e: any) {
      setPreviewClient(client)
      alert('Failed to save name: ' + (e?.message || String(e)))
    } finally {
      setCrpEditSaving(false)
      setCrpEditingField(null)
      setCrpEditDraft('')
    }
  }, [])
  // Split a couple/household name into individual contact_persons (source='split',
  // first is_primary). The server recomposes the client's household display when
  // 2+ persons exist, so we refetch the client after creating the person rows.
  const splitContactNameIntoPersons = useCallback(async (client: any, persons: string[]) => {
    if (!client || !Array.isArray(persons) || persons.length < 2) return
    setCrpEditSaving(true)
    try {
      for (let i = 0; i < persons.length; i++) {
        await createContactPerson(client.unified_client_id, { full_name: persons[i], is_primary: i === 0 })
      }
      await refreshContactPersons(client.unified_client_id)
      const cl = await fetchClient(client.unified_client_id)
      if (cl) setPreviewClient(cl)
    } catch (e: any) {
      alert('Failed to split into contact people: ' + (e?.message || String(e)))
    } finally {
      setCrpEditSaving(false)
      setCrpEditingField(null)
      setCrpEditDraft('')
      setCrpNameError(null)
    }
  }, [refreshContactPersons])
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: string; id: string; name: string; ready: boolean; timer?: ReturnType<typeof setTimeout> } | null>(null)
  const [bulkDeleteModal, setBulkDeleteModal] = useState<{ ids: Set<string>; deleting: boolean } | null>(null)
  const [contactDetailOpen, setContactDetailOpen] = useState(() => {
    const seg = getHashSegments()
    return seg[0] === 'contacts' && seg[1] === 'record' && !!seg[2]
  })
  const contactDetailReturnHash = useRef<string>('#/contacts')
  // Stale-entity redirect plumbing (merged-loser orgs/contacts + converted leads).
  // AbortControllers cancel in-flight record fetches so a slow stale load can't
  // clobber the survivor we just redirected to; the guard Sets stop redirect
  // loops by remembering loser ids we've already bounced away from.
  const clientRedirectAbortRef = useRef<AbortController | null>(null)
  const mergeRedirectGuardRef = useRef<Set<string>>(new Set())
  // Centralized merged-loser → survivor guard. Every record-open site that does
  // `fetchClient(id).then(setPreviewClient)` should route the result through
  // this instead, so a merged-away duplicate (which carries `merged_into`)
  // redirects to the survivor from ANY entry point (deep link, list-row click,
  // related-row click, 360/peek "open full record") rather than rendering the
  // dead shell. Returns true if it redirected (caller should not setPreviewClient).
  const applyClientOrRedirect = useCallback((c: any, opts?: { segRoot?: 'contacts' | 'clients' }): boolean => {
    if (!c) return false
    // The id the URL currently points at. For the server-redirect path the server
    // returns the SURVIVOR row (its own unified_client_id == canonical_id) and
    // echoes the originally-requested loser id as `requested_id` — that loser is
    // what the URL shows, so it is the correct redirect-dedup key. For the
    // `merged_into` tombstone path (no requested_id) the returned row IS the loser,
    // so unified_client_id is the same thing.
    const requestedFrom = String(c.requested_id ?? c.unified_client_id ?? '')
    // canonical-client-redirect-v2: a single pure decision covers BOTH a merged
    // loser still in-view (`merged_into` tombstone) AND a since-removed loser the
    // server recovered via merge_log (`redirected` + `canonical_id` + `requested_id`),
    // so a stale id lands on its surviving canonical record instead of dead-ending.
    const target = resolveCanonicalClientTarget(c)
    if (target.redirect && target.canonicalId && !mergeRedirectGuardRef.current.has(requestedFrom)) {
      mergeRedirectGuardRef.current.add(requestedFrom)
      const survivor = target.canonicalId
      const base = target.isOrg ? 'clients' : (opts?.segRoot === 'clients' ? 'clients' : 'contacts')
      setDealToast({ id: Date.now(), message: 'Merged record — showing the current one.', type: 'info' })
      try {
        window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}#/${base}/record/${encodeURIComponent(survivor)}`)
      } catch { window.location.hash = `/${base}/record/${encodeURIComponent(survivor)}` }
      // Observable live-verify hook (no-op if unlistened); keeps the marker
      // string literal in the minified SPA bundle for deploy verification.
      window.dispatchEvent(new CustomEvent(CANONICAL_CLIENT_MARKER, { detail: { from: requestedFrom, to: survivor } }))
      window.dispatchEvent(new HashChangeEvent('hashchange'))
      return true
    }
    setPreviewClient(c)
    return false
  }, [])
  const [contactDetailTab, setContactDetailTab] = useState<'about' | 'activities' | 'revenue'>('about')
  const [contactDetailLeads, setContactDetailLeads] = useState<CrmLead[]>([])
  const [contactDetailDeals, setContactDetailDeals] = useState<CrmDeal[]>([])
  const [contactDetailActivities, setContactDetailActivities] = useState<CrmActivity[]>([])
  // Server-side fetch window for the contact timeline (default 300; "Load
  // older activity" grows it up to the API's 1000 cap).
  const [contactActFetchLimit, setContactActFetchLimit] = useState(300)
  const [contactActFetchBusy, setContactActFetchBusy] = useState(false)
  const [contactDetailLoading, setContactDetailLoading] = useState(false)
  const [contactDetailProjects, setContactDetailProjects] = useState<any[]>([])
  const [contactDetailJobs, setContactDetailJobs] = useState<ClientJob[]>([])
  const [contactDetailAttachments, setContactDetailAttachments] = useState<any[]>([])
  const [crpExpandedCard, setCrpExpandedCard] = useState<string | null>(null)
  // Real AI record-summary insight (Track B). null = not yet loaded for the
  // current record; the card shimmers while loading, renders the LLM summary +
  // next-best-actions on success, and falls back to the deterministic templated
  // summary when the endpoint is unavailable or errors.
  const [recordInsight, setRecordInsight] = useState<EntityInsight | null>(null)
  const [recordInsightLoading, setRecordInsightLoading] = useState(false)
  // Org 360: relationships + people roster (Task 14)
  const [orgRelationships, setOrgRelationships] = useState<{ outbound: OrgRelationship[]; inbound: OrgRelationship[] } | null>(null)
  const [orgPeople, setOrgPeople] = useState<ContactOrgRole[]>([])
  const [orgRelShowPast, setOrgRelShowPast] = useState(false)
  const [orgPeopleShowPast, setOrgPeopleShowPast] = useState(false)
  const [orgRelModalOpen, setOrgRelModalOpen] = useState(false)
  const [orgRoleModalOpen, setOrgRoleModalOpen] = useState(false)
  const [orgRoleTypeOptions, setOrgRoleTypeOptions] = useState<OrgRoleType[]>([])
  // Add-relationship modal form
  const [relForm, setRelForm] = useState<{ to_org_id: string; to_org_name: string; rel_type: string; start_date: string; note: string; is_billing_master: boolean }>({ to_org_id: '', to_org_name: '', rel_type: 'managed_by', start_date: '', note: '', is_billing_master: false })
  const [relOrgSearch, setRelOrgSearch] = useState('')
  const [relOrgResults, setRelOrgResults] = useState<CrmClient[]>([])
  // Appoint-person modal form (org side)
  const [appointForm, setAppointForm] = useState<{ contact_id: string; contact_name: string; role_key: string; role_label: string; start_date: string; is_primary: boolean; note: string }>({ contact_id: '', contact_name: '', role_key: '', role_label: '', start_date: '', is_primary: false, note: '' })
  const [appointSearch, setAppointSearch] = useState('')
  const [appointResults, setAppointResults] = useState<CrmClient[]>([])
  // Organization Owner contact picker (org record About grid) — owner_contact_id
  const [ownerContactSearch, setOwnerContactSearch] = useState('')
  const [ownerContactResults, setOwnerContactResults] = useState<CrmClient[]>([])
  const [ownerContactPickerOpen, setOwnerContactPickerOpen] = useState(false)
  const [ownerContactSaving, setOwnerContactSaving] = useState(false)
  const [orgRelSaving, setOrgRelSaving] = useState(false)
  const [orgRoleSaving, setOrgRoleSaving] = useState(false)
  // Contact 360: organizations & roles (Task 15)
  const [contactOrgRoles, setContactOrgRoles] = useState<ContactOrgRole[]>([])
  const [contactRoleShowPast, setContactRoleShowPast] = useState(false)
  // (Relationships stat-tile hover popovers retired — the contact's org
  // memberships now render inline as the 'Organizations' profile field.)
  // Relationship roster modal — 'orgs' = contact's org roster (persons-side
  // 'Organizations' profile field opens this). The org-side 'people'/'org-rels'
  // tiles + inline disclosure were retired in favour of <RelationshipsSection>.
  const [relRosterModal, setRelRosterModal] = useState<'people' | 'orgs' | 'org-rels' | null>(null)
  // Stackable mini-360 peek controller (drilling a related entity pushes a peek
  // with an Org > Person > Deal breadcrumb instead of swapping the whole record).
  const entityPeek = useEntityPeek()
  // Bump to force <RelationshipsSection> to re-fetch after an inline mutation.
  const [relSectionRefreshTick, setRelSectionRefreshTick] = useState(0)
  const [appointOrgModalOpen, setAppointOrgModalOpen] = useState(false)
  const [appointOrgForm, setAppointOrgForm] = useState<{ org_id: string; org_name: string; org_subtype: string; role_key: string; role_label: string; start_date: string; is_primary: boolean; note: string }>({ org_id: '', org_name: '', org_subtype: '', role_key: '', role_label: '', start_date: '', is_primary: false, note: '' })
  const [appointOrgSearch, setAppointOrgSearch] = useState('')
  const [appointOrgResults, setAppointOrgResults] = useState<CrmClient[]>([])
  const [appointOrgSaving, setAppointOrgSaving] = useState(false)
  // Inline "Edit dates" affordance on a role row (assigned/end date correction)
  const [roleDateEdit, setRoleDateEdit] = useState<{ id: number; start_date: string; end_date: string } | null>(null)
  const [roleDateSaving, setRoleDateSaving] = useState(false)
  // Shared record header stats popover
  const [crpStatsOpen, setCrpStatsOpen] = useState(false)
  const [contactSummary, setContactSummary] = useState<ContactSummary | null>(null)
  // Record stat cluster (deals/projects/won/tickets + popover). Rendered here in App
  // scope (contactSummary/crpStatsOpen/setContactDetailTab live here) and passed to
  // ContactDetailAbout via ctx as a finished node — moved from the header strip to the
  // About-card header 2026-07-17 (user-directed).
  const crpStatClusterNode = contactSummary ? (() => {
    const scrollToCard = (card: string) => document.querySelector(`[data-crp-card="${card}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    const stats = [
      {
        key: 'deals', label: 'Open Deals', tone: 'green', count: contactSummary.open_deals.count,
        compactMeta: null,
        detailValue: contactSummary.open_deals.count === 0 ? 'No open deals' : `${contactSummary.open_deals.count} open deal${contactSummary.open_deals.count === 1 ? '' : 's'}`,
        detailMeta: contactSummary.open_deals.count > 0 ? currencyFormatter.format(contactSummary.open_deals.total) : null,
        onClick: () => scrollToCard('deals'),
        icon: () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>,
      },
      {
        key: 'projects', label: 'Active Projects', tone: 'blue', count: contactSummary.active_projects.count,
        compactMeta: contactSummary.active_projects.count > 0 ? currencyFormatter.format(contactSummary.active_projects.total) : null,
        detailValue: contactSummary.active_projects.count === 0 ? 'No active projects' : `${contactSummary.active_projects.count} active project${contactSummary.active_projects.count === 1 ? '' : 's'}`,
        detailMeta: contactSummary.active_projects.count > 0 ? currencyFormatter.format(contactSummary.active_projects.total) : null,
        onClick: () => scrollToCard('projects'),
        icon: () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z"/><path d="M8 10v4M12 10v2M16 10v6"/></svg>,
      },
      {
        key: 'won', label: 'Lifetime Won', tone: 'slate', count: contactSummary.lifetime_won.total,
        compactMeta: null,
        detailValue: contactSummary.lifetime_won.total === 0 ? 'No lifetime won revenue' : currencyFormatter.format(contactSummary.lifetime_won.total),
        detailMeta: contactSummary.lifetime_won.total > 0 ? `CRM signed ${currencyFormatter.format(contactSummary.lifetime_won.crm_signed)} · Legacy ${currencyFormatter.format(contactSummary.lifetime_won.legacy_paid)}` : null,
        onClick: () => setContactDetailTab('revenue'),
        icon: () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>,
      },
      {
        key: 'tickets', label: 'Open Tickets', tone: 'red', count: contactSummary.open_tickets.count,
        compactMeta: null,
        detailValue: contactSummary.open_tickets.count === 0 ? 'No open tickets' : `${contactSummary.open_tickets.count} open ticket${contactSummary.open_tickets.count === 1 ? '' : 's'}`,
        detailMeta: null,
        onClick: () => scrollToCard('tickets'),
        icon: () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"/><path d="M13 5v2M13 17v2M13 11v2"/></svg>,
      },
    ]
    return (
      <div className="crp-stat-cluster" onClick={() => setCrpStatsOpen(true)}>
        <div className="crp-stat-mini-group" aria-label="Record activity summary">
          {stats.map(stat => (
            <button
              key={stat.key}
              type="button"
              className={`crp-stat-mini crp-stat-mini--${stat.tone}`}
              title={stat.label}
              onClick={e => { e.stopPropagation(); stat.onClick() }}
            >
              {stat.icon()}
              <span>{stat.key === 'won' ? (stat.count > 0 ? currencyFormatter.format(stat.count) : '—') : (stat.count > 0 ? stat.count : '—')}</span>
              {stat.compactMeta && <small>{stat.compactMeta}</small>}
            </button>
          ))}
        </div>
        <button type="button" className="crp-stat-cluster-toggle" aria-label="Show full activity summary" aria-expanded={crpStatsOpen} onClick={e => { e.stopPropagation(); setCrpStatsOpen(open => !open) }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
        </button>
        {crpStatsOpen && (
          <>
            <div className="cmd-backdrop-light" onClick={e => { e.stopPropagation(); setCrpStatsOpen(false) }} />
            <div className="hs-viewtype-panel crp-stat-popover" onClick={e => e.stopPropagation()}>
              <div className="crp-stat-popover-header">Record summary</div>
              {stats.map(stat => (
                <button key={stat.key} type="button" className={`crp-stat-detail crp-stat-detail--${stat.tone}`} onClick={() => { setCrpStatsOpen(false); stat.onClick() }}>
                  <span className="crp-stat-detail-icon">{stat.icon()}</span>
                  <span className="crp-stat-detail-copy">
                    <span className="crp-stat-detail-label">{stat.label}</span>
                    <span className="crp-stat-detail-value">{stat.detailValue}</span>
                    {stat.detailMeta && <span className="crp-stat-detail-meta">{stat.detailMeta}</span>}
                  </span>
                  <svg className="crp-stat-detail-arrow" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    )
  })() : null
  // Notes composer state for the Contact Record's Activities tab
  const [contactNoteOpen, setContactNoteOpen] = useState(false)
  const [contactNoteText, setContactNoteText] = useState('')
  const [contactNoteFiles, setContactNoteFiles] = useState<File[]>([])
  const [contactNoteSaving, setContactNoteSaving] = useState(false)
  const contactNoteFileInputRef = useRef<HTMLInputElement>(null)
  // Task composer state
  const [contactTaskOpen, setContactTaskOpen] = useState(false)
  const [contactTaskForm, setContactTaskForm] = useState<{ title: string; body: string; priority: string; dueDate: string; assignedTo: number | null }>({ title: '', body: '', priority: 'medium', dueDate: '', assignedTo: null })
  const [contactTaskSaving, setContactTaskSaving] = useState(false)
  // Inline validation/error + success feedback for the record-detail "Create task" forms
  // (shared by the contact/company form and the deal form — only one is open at a time).
  const [contactTaskError, setContactTaskError] = useState<string | null>(null)
  const [contactTaskSuccess, setContactTaskSuccess] = useState(false)
  // Meeting composer state
  const [contactMeetingOpen, setContactMeetingOpen] = useState(false)
  const [contactMeetingForm, setContactMeetingForm] = useState<{
    title: string
    datetime: string
    durationMinutes: number
    location: string
    notes: string
    ownerUserId: number
    attendeeIds: number[]
  }>({ title: '', datetime: '', durationMinutes: 60, location: '', notes: '', ownerUserId: 8, attendeeIds: [] })
  const [contactMeetingSaving, setContactMeetingSaving] = useState(false)
  // Tracks which entity (`contact:<id>` or `deal:<id>`) the meeting form was last
  // initialized for. Opening the composer a second time for the SAME entity keeps
  // the user's in-progress selections (datetime, owner, attendees). Switching to
  // a different entity resets to defaults for that entity.
  const meetingFormEntityRef = useRef<string>('')
  // Quick-create popup modals (lightweight alternative to the full slide-out)
  // triggered from the right-sidebar "+" buttons. Only one open at a time.
  const [quickCreate, setQuickCreate] = useState<null | 'ticket' | 'project' | 'attachment'>(null)
  const [quickCreateSaving, setQuickCreateSaving] = useState(false)
  const [qcTicketForm, setQcTicketForm] = useState({ title: '', description: '', priority: 'medium', status: 'open' })
  const [qcProjectForm, setQcProjectForm] = useState({ name: '', dealId: '', status: 'active', value: '' })
  const [qcAttachmentFiles, setQcAttachmentFiles] = useState<File[]>([])
  const [qcAttachmentNote, setQcAttachmentNote] = useState('')
  const [qcAttachmentDealId, setQcAttachmentDealId] = useState('')
  const qcAttachmentFileInputRef = useRef<HTMLInputElement>(null)
  const [editingField, setEditingField] = useState<string | null>(null)
  const [editingValue, setEditingValue] = useState('')
  const [savingField, setSavingField] = useState<string | null>(null)
  // Inline edit state for the Deal "About this deal" card
  const [dealEditingField, setDealEditingField] = useState<string | null>(null)
  const [dealEditingValue, setDealEditingValue] = useState('')
  const [dealSavingField, setDealSavingField] = useState<string | null>(null)
  // Staged edits for the Deal "About this deal" card — mirrors the Contact record's
  // contactPendingChanges pattern (sticky Save/Cancel footer; persists via updateDeal).
  const [dealPendingChanges, setDealPendingChanges] = useState<Record<string, unknown>>({})
  const [dealSavingChanges, setDealSavingChanges] = useState(false)
  // Clear staged deal edits whenever a different deal record is opened.
  useEffect(() => { setDealPendingChanges({}); setDealEditingField(null); setDealEditingValue('') }, [selectedDeal?.id])
  // Deal title inline-edit state (hover-overlay pencil on the highlight card name)
  const [dealTitleEditing, setDealTitleEditing] = useState(false)
  const [dealTitleDraft, setDealTitleDraft] = useState('')
  // (Cascade ticket-create panel state removed — Create Ticket now opens the
  // unified Smart Create slide-out panel with the Ticket chip pre-selected.
  // See openSmartCreateForTicket below.)
  const [ticketsRefreshTick, setTicketsRefreshTick] = useState(0)

  useEffect(() => {
    if (!previewClient?.unified_client_id) { setContactSummary(null); return }
    let cancelled = false
    fetchContactSummary(previewClient.unified_client_id)
      .then(s => { if (!cancelled) setContactSummary(s) })
      .catch(() => { if (!cancelled) setContactSummary(null) })
    return () => { cancelled = true }
  }, [previewClient?.unified_client_id, ticketsRefreshTick])

  // Real AI record-summary insight (Track B). Fetches the LLM-generated summary
  // + next-best-actions on record open (and on Regenerate). AbortController so a
  // stale request is dropped when the record id changes. recordInsightTick is
  // bumped (with forceInsightRefresh=true) by the Regenerate affordance.
  const [recordInsightTick, setRecordInsightTick] = useState(0)
  const forceInsightRefresh = useRef(false)
  const regenerateRecordInsight = useCallback(() => {
    forceInsightRefresh.current = true
    setRecordInsightTick(t => t + 1)
  }, [])
  useEffect(() => {
    const id = previewClient?.unified_client_id
    if (!id) {
      setRecordInsight(null); setRecordInsightLoading(false)
      return
    }
    const refresh = forceInsightRefresh.current
    forceInsightRefresh.current = false
    const entityType: 'org' | 'person' = previewClient?.client_type === 'organization' ? 'org' : 'person'
    const controller = new AbortController()
    setRecordInsightLoading(true)
    fetchEntityInsight(entityType, id, { refresh, signal: controller.signal })
      .then(ins => {
        if (controller.signal.aborted) return
        setRecordInsight(ins || { unavailable: true })
        setRecordInsightLoading(false)
      })
      .catch((e) => {
        if (controller.signal.aborted || (e && e.name === 'AbortError')) return
        // Any error → fall back to the deterministic templated summary.
        setRecordInsight(null)
        setRecordInsightLoading(false)
      })
    return () => { controller.abort() }
  }, [previewClient?.unified_client_id, previewClient?.client_type, recordInsightTick])

  const [contactTickets, setContactTickets] = useState<any[]>([])
  const [activeTicketDrawerId, setActiveTicketDrawerId] = useState<number | null>(null)
  const [ticketDrawerData, setTicketDrawerData] = useState<any | null>(null)
  const [ticketDrawerSaving, setTicketDrawerSaving] = useState<string | null>(null)
  const [ticketDrawerDraft, setTicketDrawerDraft] = useState<Record<string, any> | null>(null)
  // Inline deal-picker state for the ticket drawer's "Link to a deal…" flow.
  // When `ticketLinkPickerOpen` is true we render a search input + list of
  // deals below the link row. Pre-populated with the contact's own deals;
  // the user can type to search any deal in the system.
  const [ticketLinkPickerOpen, setTicketLinkPickerOpen] = useState(false)
  const [ticketLinkPickerQuery, setTicketLinkPickerQuery] = useState('')
  const [ticketLinkPickerResults, setTicketLinkPickerResults] = useState<Array<{ id: number; title: string; stage_label?: string | null; customer_name?: string | null }>>([])
  const [ticketLinkPickerLoading, setTicketLinkPickerLoading] = useState(false)

  useEffect(() => {
    if (!previewClient?.unified_client_id) { setContactTickets([]); return }
    let cancelled = false
    fetchContactTickets(previewClient.unified_client_id)
      .then(list => { if (!cancelled) setContactTickets(Array.isArray(list) ? list : []) })
      .catch(() => { if (!cancelled) setContactTickets([]) })
    return () => { cancelled = true }
  }, [previewClient?.unified_client_id, ticketsRefreshTick])

  // Load the ticket whenever the drawer opens or id changes.
  useEffect(() => {
    if (activeTicketDrawerId == null) { setTicketDrawerData(null); return }
    let cancelled = false
    fetch(`/api/crm/tickets/${activeTicketDrawerId}`).then(r => r.json())
      .then(data => { if (!cancelled) { setTicketDrawerData(data); setTicketDrawerDraft({ ...data }) } })
      .catch(() => { if (!cancelled) { setTicketDrawerData(null); setTicketDrawerDraft(null) } })
    return () => { cancelled = true }
  }, [activeTicketDrawerId])

  async function saveTicketField(field: string, value: any) {
    if (!ticketDrawerData) return
    setTicketDrawerSaving(field)
    // Optimistic
    setTicketDrawerData((prev: any) => prev ? { ...prev, [field]: value } : prev)
    try {
      const apiField = field === 'unified_client_id' ? 'unifiedClientId'
        : field === 'deal_id' ? 'dealId'
        : field === 'project_id' ? 'projectId'
        : field === 'due_date' ? 'dueDate'
        : field === 'assigned_to' ? 'assignedTo'
        : field
      await updateTicket(ticketDrawerData.id, { [apiField]: value })
    } catch (e: any) {
      alert('Save failed: ' + (e?.message || String(e)))
      // Reload to revert optimistic
      const resp = await fetch(`/api/crm/tickets/${ticketDrawerData.id}`)
      if (resp.ok) setTicketDrawerData(await resp.json())
    } finally {
      setTicketDrawerSaving(null)
    }
  }

  function closeTicketDrawer() {
    setActiveTicketDrawerId(null)
    setTicketDrawerData(null)
    setTicketDrawerDraft(null)
    setTicketLinkPickerOpen(false)
    setTicketLinkPickerQuery('')
    setTicketLinkPickerResults([])
    setTicketsRefreshTick(t => t + 1)
  }

  // Load deal candidates for the ticket's link-to-deal picker. When the
  // picker first opens with no query, we pre-fill with the contact's own
  // deals — that's usually what the user wants. As they type we switch to
  // a global search. Debounced 200ms.
  useEffect(() => {
    if (!ticketLinkPickerOpen || !ticketDrawerData) return
    let cancelled = false
    const q = ticketLinkPickerQuery.trim()
    setTicketLinkPickerLoading(true)
    const t = setTimeout(async () => {
      try {
        if (!q && ticketDrawerData.unified_client_id) {
          const list = await fetchClientDeals(ticketDrawerData.unified_client_id)
          if (!cancelled) setTicketLinkPickerResults(
            (list || []).map((d: any) => ({ id: Number(d.id), title: d.title, stage_label: d.stage_label, customer_name: d.customer_name }))
          )
        } else {
          const resp = await fetchDeals(-1, 1, 20, { excludeClosed: false })
          const matches = (resp.deals || []).filter((d: any) => {
            if (!q) return true
            const s = q.toLowerCase()
            return (d.title || '').toLowerCase().includes(s) || (d.customer_name || '').toLowerCase().includes(s) || String(d.id).includes(s)
          }).slice(0, 15)
          if (!cancelled) setTicketLinkPickerResults(matches.map((d: any) => ({ id: Number(d.id), title: d.title, stage_label: d.stage_label, customer_name: d.customer_name })))
        }
      } catch {
        if (!cancelled) setTicketLinkPickerResults([])
      } finally {
        if (!cancelled) setTicketLinkPickerLoading(false)
      }
    }, q ? 200 : 0)
    return () => { cancelled = true; clearTimeout(t) }
  }, [ticketLinkPickerOpen, ticketLinkPickerQuery, ticketDrawerData?.id, ticketDrawerData?.unified_client_id])

  async function linkTicketToDeal(dealId: number) {
    await saveTicketField('deal_id', dealId)
    setTicketLinkPickerOpen(false)
    setTicketLinkPickerQuery('')
  }

  // Open the unified Smart Create panel with the Ticket chip pre-selected.
  // Callers used to pass a locked contact/deal/project — we now just pre-fill
  // the contact (the smart panel doesn't yet support locking a ticket to a
  // specific deal or project, but the user can still type the reference
  // manually in the Description). If a contact is locked, use it as the base;
  // otherwise open the panel with no pre-fill and let the user search.
  const openSmartCreateForTicket = (locked?: { contact?: any; deal?: any; project?: any }) => {
    if (locked?.contact) {
      openSmartCreateForClient(locked.contact as CrmClient, 'ticket')
      return
    }
    // No locked contact — open the panel fresh with Ticket pre-selected.
    setCreateFormData({})
    setCreateEmails([''])
    setCreatePhones([{ type: 'Mobile', number: '', ext: '' }])
    setEmailTouched(false)
    setSmartCreateSearch('')
    setSmartCreateResults(null)
    setCreateError(null)
    setSmartCreateType('ticket')
    setShowCreateModal('ticket')
    setSmartCreateOpen(true)
  }
  // Pending value for the Appointment Date popover picker (gated by OK button)
  const [dealApptPickerPending, setDealApptPickerPending] = useState('')
  const [dealApptPickerPos, setDealApptPickerPos] = useState<{ top: number; left: number; width: number } | null>(null)
  const dealApptTriggerRef = useRef<HTMLDivElement>(null)
  const dealApptPopoverRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (dealEditingField !== 'estimateAppointment') return
    const handler = (e: MouseEvent) => {
      const insideTrigger = dealApptTriggerRef.current?.contains(e.target as Node)
      const insidePopover = dealApptPopoverRef.current?.contains(e.target as Node)
      if (!insideTrigger && !insidePopover) {
        setDealEditingField(null)
        setDealEditingValue('')
        setDealApptPickerPos(null)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [dealEditingField])
  const [crpActionsOpen, setCrpActionsOpen] = useState(false)
  const [crpActivityFilter, setCrpActivityFilter] = useState<'all' | 'notes' | 'emails' | 'calls' | 'tasks' | 'meetings' | 'portal'>('all')
  const [crpActivitySearch, setCrpActivitySearch] = useState('')
  const [crpFilterDropdownOpen, setCrpFilterDropdownOpen] = useState(false)
  const [crpEditingActivityId, setCrpEditingActivityId] = useState<number | null>(null)
  const [crpEditingActivityVal, setCrpEditingActivityVal] = useState('')
  const [crpEditingDescId, setCrpEditingDescId] = useState<number | null>(null)
  const [crpEditingDescVal, setCrpEditingDescVal] = useState('')
  const [crpActivitySavingId, setCrpActivitySavingId] = useState<number | null>(null)
  const [crpCommentOpenId, setCrpCommentOpenId] = useState<number | null>(null)
  const [crpCommentVal, setCrpCommentVal] = useState('')
  const [crpThreadExpanded, setCrpThreadExpanded] = useState<Set<number>>(new Set())
  const [crpUsersDropdownOpen, setCrpUsersDropdownOpen] = useState(false)
  const [crpSelectedUser, setCrpSelectedUser] = useState<number | null>(null)
  const [crpExpandedCards, setCrpExpandedCards] = useState<Set<number>>(new Set())
  // Activity-id of the call to scroll-into-view + briefly ring on the
  // Contact Record activities tab (driven by `?call=<id>` from the bell-link
  // path so the rep lands directly on the right call instead of "somewhere
  // in this contact's timeline"). Cleared 2.4s later so subsequent renders
  // don't keep highlighting it. Reused on the Calls view too.
  const [highlightedCallActivityId, setHighlightedCallActivityId] = useState<number | null>(null)
  const [crpFilterChecks, setCrpFilterChecks] = useState<Record<string, boolean>>(CRP_DEFAULT_ACTIVITY_FILTER_CHECKS)
  const [previewInvoices, setPreviewInvoices] = useState<ClientInvoice[]>([])
  const [previewDocuments, setPreviewDocuments] = useState<ClientDocument[]>([])
  const [previewJobs, setPreviewJobs] = useState<ClientJob[]>([])
  const [previewFinancials, setPreviewFinancials] = useState<{ groups: FinancialGroup[]; flat: FinancialDoc[] }>({ groups: [], flat: [] })
  const [financialLoading, setFinancialLoading] = useState(false)
  // Task calendar popover state
  const [taskCalOpenId, setTaskCalOpenId] = useState<number | null>(null)
  const [taskCalMonth, setTaskCalMonth] = useState(new Date().getMonth())
  const [taskCalYear, setTaskCalYear] = useState(new Date().getFullYear())
  const [taskCalDates, setTaskCalDates] = useState<Record<number, string>>({}) // actId -> YYYY-MM-DD
  const taskCalRef = useRef<HTMLDivElement>(null)
  const saveCrpActivityPatch = async (activityId: number, fields: { subject?: string | null; description?: string | null }) => {
    setCrpActivitySavingId(activityId)
    try {
      const updated = await updateActivity(activityId, fields)
      setContactDetailActivities(prev => prev.map(act => act.id === activityId ? { ...act, ...updated } : act))
      setDealActivities(prev => prev.map(act => act.id === activityId ? { ...act, ...updated } : act))
      return updated
    } finally {
      setCrpActivitySavingId(null)
    }
  }
  const clientSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Companies page (D1 clients — organizations only)
  const [companyList, setCompanyList] = useState<CrmClient[]>([])
  const [companyTotal, setCompanyTotal] = useState(0)
  const [companyPages, setCompanyPages] = useState(1)
  const [companyPage, setCompanyPage] = useState(() => Number(sessionStorage.getItem(uKey('companyPage'))) || 1)
  const [companySearch, setCompanySearch] = useState(() => sessionStorage.getItem(uKey('companySearch')) || '')
  const [companySearchDebounced, setCompanySearchDebounced] = useState('')
  const [companySort, setCompanySort] = useState<{ field: string; order: 'ASC' | 'DESC' }>({ field: 'created_at', order: 'DESC' })
  const [companyStatusFilter, setCompanyStatusFilter] = useState('')
  const [companyAccountingRoleFilter, setCompanyAccountingRoleFilter] = useState('')
  const [companyLoading, setCompanyLoading] = useState(false)
  const [companySelectedRows, setCompanySelectedRows] = useState<Set<string>>(new Set())
  const [previewCompany, setPreviewCompany] = useState<CrmClient | null>(null)
  const [previewCompanyClosing, setPreviewCompanyClosing] = useState(false)
  const [companyInvoices, setCompanyInvoices] = useState<ClientInvoice[]>([])
  const [companyDocuments, setCompanyDocuments] = useState<ClientDocument[]>([])
  const [companyJobs, setCompanyJobs] = useState<ClientJob[]>([])
  const [companyFinLoading, setCompanyFinLoading] = useState(false)
  const companySearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Leads page (D1 leads — server-paginated)
  const [leadList, setLeadList] = useState<CrmLead[]>([])
  // Lead-routing config — shared inbox toggles. Loaded once, falls back
  // to spec defaults so the UI stays usable even if the endpoint errors.
  const [leadRoutingConfig, setLeadRoutingConfig] = useState<LeadRoutingConfig>({
    routing_model: 'shared-inbox',
    require_note_on_touch: 1,
    show_notepad_icon: 1,
    show_unread_state: 1,
    base_code_assignee_group: 'office',
  })
  useEffect(() => {
    fetchLeadRoutingConfig().then(setLeadRoutingConfig).catch(() => { /* keep defaults */ })
  }, [])
  // Track whether the user logged a note while a given lead was open in
  // the preview drawer — used by the close-without-note prompt when the
  // org has require_note_on_touch=1.
  const previewLeadNoteLoggedRef = useRef<boolean>(false)
  const [inlineStatusLeadId, setInlineStatusLeadId] = useState<string | null>(null)
  const [inlineStatusPos, setInlineStatusPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
  const [disqualifyModal, setDisqualifyModal] = useState<{ leadId: string; leadTitle: string } | null>(null)
  const [disqualifyComment, setDisqualifyComment] = useState('')
  const [disqualifySaving, setDisqualifySaving] = useState(false)
  // Bulk disqualify (multi-select on leads list)
  const [bulkDisqualifyOpen, setBulkDisqualifyOpen] = useState(false)
  const [bulkDisqualifySameReason, setBulkDisqualifySameReason] = useState(true)
  const [bulkDisqualifyShared, setBulkDisqualifyShared] = useState('')
  const [bulkDisqualifyPerLead, setBulkDisqualifyPerLead] = useState<Record<string, string>>({})
  const [bulkDisqualifySaving, setBulkDisqualifySaving] = useState(false)
  const [leadPageTotal, setLeadPageTotal] = useState(0)
  const [leadPageCount, setLeadPageCount] = useState(1)
  const [leadPage, setLeadPage] = useState(() => Number(sessionStorage.getItem(uKey('leadPage'))) || 1)
  const [leadSearch, setLeadSearch] = useState(() => sessionStorage.getItem(uKey('leadSearch')) || '')
  const [leadSearchDebounced, setLeadSearchDebounced] = useState('')
  const [leadBadgeFilter, setLeadBadgeFilter] = useState<Set<string>>(() => {
    try {
      const raw = sessionStorage.getItem(uKey('leadBadgeFilter'))
      if (raw) return new Set(JSON.parse(raw))
    } catch { /* */ }
    return new Set()
  })
  useEffect(() => {
    try { sessionStorage.setItem(uKey('leadBadgeFilter'), JSON.stringify(Array.from(leadBadgeFilter))) } catch { /* */ }
  }, [leadBadgeFilter])
  const leadBadgeParam = useMemo(() => Array.from(leadBadgeFilter).sort().join(','), [leadBadgeFilter])
  const [leadSort, setLeadSort] = useState<{ field: string; order: 'ASC' | 'DESC' }>({ field: 'date_modify', order: 'DESC' })
  const [leadStatusFilter, setLeadStatusFilter] = useState('')
  const [leadIncludeConverted, setLeadIncludeConverted] = useState(false)
  const [leadIncludeDisqualified, setLeadIncludeDisqualified] = useState(false)
  const [leadLoading, setLeadLoading] = useState(false)
  const [leadSelectedRows, setLeadSelectedRows] = useState<Set<string>>(new Set())
  const [previewLead, setPreviewLead] = useState<CrmLead | null>(null)
  // Edit mode state for the lead preview sidebar
  const [previewLeadEditing, setPreviewLeadEditing] = useState(false)
  const [previewLeadForm, setPreviewLeadForm] = useState<Record<string, string>>({})
  const [previewLeadSaving, setPreviewLeadSaving] = useState(false)
  // Comments / notes state
  const [previewLeadActivities, setPreviewLeadActivities] = useState<CrmActivity[]>([])
  const [previewLeadNewComment, setPreviewLeadNewComment] = useState('')
  const [previewLeadCommentSaving, setPreviewLeadCommentSaving] = useState(false)
  // Reset edit state + fetch activities whenever a different lead is previewed
  useEffect(() => {
    if (!previewLead) {
      setPreviewLeadEditing(false)
      setPreviewLeadForm({})
      setPreviewLeadActivities([])
      setPreviewLeadNewComment('')
      previewLeadNoteLoggedRef.current = false
      return
    }
    setPreviewLeadEditing(false)
    setPreviewLeadForm({})
    setPreviewLeadNewComment('')
    // Reset on each new lead so the close-without-note prompt fires fresh.
    previewLeadNoteLoggedRef.current = false
    fetchLeadActivities(Number(previewLead.id))
      .then(acts => setPreviewLeadActivities(Array.isArray(acts) ? acts : []))
      .catch(() => setPreviewLeadActivities([]))
  }, [previewLead?.id])
  const [previewLeadClosing, setPreviewLeadClosing] = useState(false)
  const [leadStatuses, setLeadStatuses] = useState<LeadStatus[]>([])
  const [leadStatusDropdownOpen, setLeadStatusDropdownOpen] = useState(false)
  const leadStatusDropdownRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!leadStatusDropdownOpen) return
    const onDoc = (e: MouseEvent) => {
      if (leadStatusDropdownRef.current && !leadStatusDropdownRef.current.contains(e.target as Node)) setLeadStatusDropdownOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [leadStatusDropdownOpen])
  const [leadSortDropdownOpen, setLeadSortDropdownOpen] = useState(false)
  const leadSortDropdownRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!leadSortDropdownOpen) return
    const onDoc = (e: MouseEvent) => {
      if (leadSortDropdownRef.current && !leadSortDropdownRef.current.contains(e.target as Node)) setLeadSortDropdownOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [leadSortDropdownOpen])
  const [leadBadgeDropdownOpen, setLeadBadgeDropdownOpen] = useState(false)
  const leadBadgeDropdownRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!leadBadgeDropdownOpen) return
    const onDoc = (e: MouseEvent) => {
      if (leadBadgeDropdownRef.current && !leadBadgeDropdownRef.current.contains(e.target as Node)) setLeadBadgeDropdownOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [leadBadgeDropdownOpen])
  const leadStatusMeta = useMemo(() => new Map(leadStatuses.map(s => [s.status_id, s])), [leadStatuses])
  const isTerminalLeadStatus = (status?: LeadStatus | null) => isTerminalSemantic(status?.semantics)
  const leadPipelineStatuses = leadStatuses.filter(s => !isTerminalLeadStatus(s))
  const getLeadStatusColor = (statusId?: string | null) => leadStatusMeta.get(String(statusId || ''))?.color || '#94a3b8'
  const getLeadStatusLabel = (statusId?: string | null, currentLabel?: string | null) => {
    if (currentLabel) return currentLabel
    const status = leadStatusMeta.get(String(statusId || ''))
    return status?.name || String(statusId || 'Unknown').replace(/_/g, ' ')
  }
  const leadSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Inline cell editing state
  type CellEdit = { clientId: string; field: string; value: string }
  const [cellEdits, setCellEdits] = useState<Record<string, Record<string, string>>>({}) // clientId -> { field -> newValue }
  const [activeCell, setActiveCell] = useState<CellEdit | null>(null)
  const [savingEdits, setSavingEdits] = useState(false)
  // emailRows is the source of truth for the email editor (multi-email + roles).
  // `emails` (string[]) is retained only for backward-compat with legacy call sites
  // that still seed a plain list; on modal open we hydrate `emailRows` from the
  // contact_emails API if available, falling back to the seeded strings.
  type ContactEmailRow = { id?: number; address: string; is_primary: boolean; is_billing: boolean }
  const [cellEditModal, setCellEditModal] = useState<{
    clientId: string
    field: 'email' | 'phone'
    emails: string[]
    emailRows?: ContactEmailRow[]
    phones: { type: string; number: string; ext: string }[]
  } | null>(null)
  const activeCellInputRef = useRef<HTMLInputElement>(null)

  // Column context menu state
  const [colContextMenu, setColContextMenu] = useState<{ field: string; x: number; y: number } | null>(null)

  // Resizable columns
  const COLUMN_KEYS = ['name', 'email', 'phone', 'city', 'status', 'type', 'deals', 'invoiced'] as const
  type ColKey = typeof COLUMN_KEYS[number]
  const DEFAULT_COL_WIDTHS: Record<ColKey, number> = {
    name: 220, email: 220, phone: 130, city: 120, status: 90, type: 80, deals: 70, invoiced: 110,
  }
  const [colWidths, setColWidths] = useState<Record<ColKey, number>>(() => {
    // Persisted per-user config may predate column redesigns or be corrupt —
    // heal it on load (docs/REGRESSIONS.md 2026-07-02).
    let raw: string | null = null
    try { raw = localStorage.getItem('botpros-crm-column-widths') } catch { /* storage unavailable */ }
    return sanitizeColWidths(raw, DEFAULT_COL_WIDTHS)
  })
  const resizingCol = useRef<{ key: string; startX: number; startWidth: number } | null>(null)

  const tableScrollRef = useRef<HTMLDivElement>(null)
  const [tableScrollHeight, setTableScrollHeight] = useState(0)

  // ===== Edit Columns modal =====
  const CONTACT_COLUMN_DEFS: { key: string; label: string; category: string; sortField?: string; width: number; accessor: (c: CrmClient) => React.ReactNode }[] = [
    { key: 'name', label: 'Name', category: 'Contact Information', sortField: 'preferred_name', width: 220, accessor: () => null /* handled specially */ },
    { key: 'email', label: 'Email', category: 'Contact Information', sortField: 'preferred_email', width: 220, accessor: (c) => c.preferred_email || null },
    { key: 'phone', label: 'Phone', category: 'Contact Information', sortField: 'preferred_phone', width: 130, accessor: (c) => c.preferred_phone || null },
    { key: 'city', label: 'City', category: 'Contact Information', sortField: 'preferred_city', width: 120, accessor: (c) => c.preferred_city || null },
    { key: 'state', label: 'State', category: 'Contact Information', sortField: 'preferred_state', width: 80, accessor: (c) => c.preferred_state || null },
    { key: 'zip', label: 'Zip', category: 'Contact Information', sortField: 'preferred_zip', width: 80, accessor: (c) => c.preferred_zip || null },
    { key: 'address', label: 'Address', category: 'Contact Information', sortField: 'preferred_street', width: 200, accessor: (c) => c.preferred_street || null },
    { key: 'type', label: 'Type', category: 'Contact Information', sortField: 'client_type', width: 80, accessor: (c) => c.client_type === 'organization' ? 'Org' : 'Person' },
    { key: 'company', label: 'Company', category: 'Contact Information', width: 200, accessor: (c) => {
      const row = c as any
      const orgName = typeof row.org_name === 'string' ? row.org_name.trim() : ''
      const orgId = typeof row.org_id === 'string' ? row.org_id.trim() : ''
      const role = typeof row.org_role_label === 'string' ? row.org_role_label.trim() : ''
      if (!orgName) return '—'
      const content = (
        <span title={role || undefined} style={{ display: 'inline-flex', alignItems: 'baseline', minWidth: 0, maxWidth: '100%' }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{orgName}</span>
          {role && <span style={{ color: '#94a3b8', fontSize: '0.72rem', marginLeft: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>— {role}</span>}
        </span>
      )
      return orgId
        ? <span className="hs-name-link" style={{ display: 'inline-flex', minWidth: 0, maxWidth: '100%', cursor: 'pointer' }} title={role || undefined} onClick={e => { e.stopPropagation(); window.location.hash = `/clients/record/${encodeURIComponent(orgId)}` }}>{content}</span>
        : content
    }},
    { key: 'contactType', label: 'Contact type', category: 'Contact Information', sortField: 'contact_type', width: 110, accessor: (c) => {
      const ct = c.contact_type || 'customer'
      const colors: Record<string, { bg: string; fg: string }> = { customer: { bg: '#dbeafe', fg: '#1d4ed8' }, vendor: { bg: '#ffedd5', fg: '#c2410c' }, subcontractor: { bg: '#f3e8ff', fg: '#7c3aed' }, employee: { bg: '#dcfce7', fg: '#16a34a' } }
      const col = colors[ct] || colors.customer
      return <span style={{ display: 'inline-block', padding: '1px 8px', borderRadius: 9999, fontSize: '0.7rem', fontWeight: 600, background: col.bg, color: col.fg, textTransform: 'capitalize' }}>{ct}</span>
    }},
    { key: 'contactStatus', label: 'Contact status', category: 'Contact Information', sortField: 'contact_status', width: 110, accessor: (c) => {
      const cs = c.contact_status || 'active'
      const dots: Record<string, string> = { active: '#22c55e', prospect: '#eab308', inactive: '#94a3b8' }
      const dot = dots[cs] || dots.active
      return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.78rem', textTransform: 'capitalize' }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: dot, display: 'inline-block', flexShrink: 0 }} />{cs}</span>
    }},
    { key: 'status', label: 'Match status', category: 'Contact Information', sortField: 'match_status', width: 90, accessor: () => null /* handled specially */ },
    { key: 'deals', label: 'Deals', category: 'CRM Activity', sortField: 'deal_count', width: 70, accessor: (c) => c.deal_count || null },
    { key: 'leads', label: 'Leads', category: 'CRM Activity', sortField: 'lead_count', width: 70, accessor: (c) => c.lead_count || null },
    { key: 'latestDealStage', label: 'Latest deal stage', category: 'CRM Activity', sortField: 'latest_deal_stage', width: 140, accessor: (c) => c.latest_deal_stage || null },
    { key: 'latestDealTitle', label: 'Latest deal', category: 'CRM Activity', sortField: 'latest_deal_title', width: 160, accessor: (c) => c.latest_deal_title || null },
    { key: 'latestLeadTitle', label: 'Latest lead', category: 'CRM Activity', sortField: 'latest_lead_title', width: 160, accessor: (c) => c.latest_lead_title || null },
    { key: 'invoiced', label: 'Total Invoiced', category: 'Financial Summary', sortField: 'legacy_total_invoiced', width: 110, accessor: (c) => c.legacy_total_invoiced ? currencyFormatter.format(c.legacy_total_invoiced) : null },
    { key: 'totalPaid', label: 'Total Paid', category: 'Financial Summary', sortField: 'legacy_total_paid', width: 110, accessor: (c) => c.legacy_total_paid ? currencyFormatter.format(c.legacy_total_paid) : null },
    { key: 'balance', label: 'Balance', category: 'Financial Summary', sortField: 'legacy_balance', width: 100, accessor: (c) => c.legacy_balance ? currencyFormatter.format(c.legacy_balance) : null },
    { key: 'invoiceCount', label: 'Invoice count', category: 'Financial Summary', sortField: 'legacy_total_invoices', width: 100, accessor: (c) => c.legacy_total_invoices || null },
    { key: 'estimateCount', label: 'Estimate count', category: 'Financial Summary', sortField: 'legacy_total_estimates', width: 100, accessor: (c) => c.legacy_total_estimates || null },
    { key: 'matchStatus', label: 'Data quality', category: 'Data Management', sortField: 'match_status', width: 100, accessor: () => null },
    { key: 'dataSources', label: 'Data sources', category: 'Data Management', sortField: 'legacy_sources', width: 120, accessor: (c) => c.legacy_sources || null },
    { key: 'docCount', label: 'Document count', category: 'Data Management', sortField: 'legacy_total_docs', width: 100, accessor: (c) => c.legacy_total_docs || null },
    { key: 'jobCount', label: 'Job count', category: 'Data Management', sortField: 'legacy_job_count', width: 90, accessor: (c) => c.legacy_job_count || null },
    { key: 'createdAt', label: 'Create date', category: 'Dates', sortField: 'created_at', width: 110, accessor: (c) => c.created_at ? formatDatePref(c.created_at) : null },
  ]
  const CONTACT_COLUMN_MAP = Object.fromEntries(CONTACT_COLUMN_DEFS.map(d => [d.key, d]))
  const CONTACT_COL_CATEGORIES = [...new Set(CONTACT_COLUMN_DEFS.map(d => d.category))]
  const DEFAULT_VISIBLE_COLS = ['name', 'email', 'phone', 'city', 'status', 'company', 'deals', 'invoiced']
  // bp-contacts-hide-invoiced: Total Invoiced is a client/company-level rollup, not
  // meaningful per person — hide it from the Contacts (people) list + its column picker.
  // Companies ("Clients") shares CONTACT_COLUMN_DEFS/contactVisibleCols and KEEPS it, so
  // this is a contacts-view display filter, never a shared-def removal.
  const CONTACTS_HIDDEN_COLS = new Set<string>(['invoiced'])
  const [contactVisibleCols, setContactVisibleCols] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('botpros-crm-visible-columns')
      if (saved) { const parsed = JSON.parse(saved); if (Array.isArray(parsed) && parsed.length > 0) return parsed }
    } catch { /* ignore */ }
    return [...DEFAULT_VISIBLE_COLS]
  })
  // Freeze boundary: number of columns frozen from the left (minimum 1 = Name always frozen)
  const [freezeCount, setFreezeCount] = useState<number>(() => {
    try {
      const stored = localStorage.getItem('botpros-crm-freeze-count')
      if (stored) return Math.max(1, parseInt(stored, 10))
    } catch {}
    return 1
  })
  // The contacts table renders contactVisibleCols MINUS CONTACTS_HIDDEN_COLS
  // (the ContactsList call filters them out), so sticky offsets must be
  // derived over the same filtered list or they drift by the hidden widths.
  const frozenBasisCols = activeView === 'contacts' ? contactVisibleCols.filter(k => !CONTACTS_HIDDEN_COLS.has(k)) : contactVisibleCols
  // Derived: which columns are frozen (first N columns in contactVisibleCols)
  const frozenCols = new Set(frozenBasisCols.slice(0, freezeCount))
  const frozenLefts: Record<string, number> = {}
  {
    let accLeft = 48 // checkbox column width (.hs-th-check/.hs-td-check are 48px, sticky left:0)
    for (let i = 0; i < freezeCount && i < frozenBasisCols.length; i++) {
      const ck = frozenBasisCols[i]
      frozenLefts[ck] = accLeft
      accLeft += (colWidths as Record<string,number>)[ck] ?? CONTACT_COLUMN_MAP[ck]?.width ?? 120
    }
  }
  const lastFrozenCol = freezeCount > 0 && freezeCount <= frozenBasisCols.length ? frozenBasisCols[freezeCount - 1] : null
  const getFrozenStyle = (ck: string): React.CSSProperties | undefined => {
    if (!frozenCols.has(ck)) return undefined
    const isLast = ck === lastFrozenCol
    return {
      position: 'sticky',
      left: frozenLefts[ck] ?? 0,
      zIndex: 4,
      ...(isLast ? { boxShadow: '2px 0 4px rgba(0,0,0,0.06)' } : {}),
    }
  }
  const getFrozenThStyle = (ck: string): React.CSSProperties | undefined => {
    if (!frozenCols.has(ck)) return undefined
    const isLast = ck === lastFrozenCol
    return {
      position: 'sticky',
      left: frozenLefts[ck] ?? 0,
      top: 0,
      zIndex: 15,
      background: '#f8fafc',
      ...(isLast ? { boxShadow: '2px 0 4px rgba(0,0,0,0.06)' } : {}),
    }
  }
  const [editColumnsOpen, setEditColumnsOpen] = useState(false)
  const [editColsSearch, setEditColsSearch] = useState('')
  const [editColsDraft, setEditColsDraft] = useState<string[]>([])
  const [editColsDragIdx, setEditColsDragIdx] = useState<number | null>(null)
  const [editColsDragOverIdx, setEditColsDragOverIdx] = useState<number | null>(null)

  // Contact owner typeahead
  const [crmUsers, setCrmUsers] = useState<CrmUser[]>([])
  const [ownerDropdownOpen, setOwnerDropdownOpen] = useState(false)
  const [ownerSearch, setOwnerSearch] = useState('')
  const [ownerSelected, setOwnerSelected] = useState<Set<number>>(new Set())
  const ownerDropdownRef = useRef<HTMLDivElement>(null)

  // Object switcher dropdown
  const [objSwitcherOpen, setObjSwitcherOpen] = useState(false)
  const [objSwitcherSearch, setObjSwitcherSearch] = useState('')
  const objSwitcherRef = useRef<HTMLDivElement>(null)

  // Tab edit dropdown
  const [tabEditOpen, setTabEditOpen] = useState(false)
  const tabEditRef = useRef<HTMLDivElement>(null)

  // Saved view tabs per entity — stores which non-"All" filter tab keys are visible (max 5 per view)
  const [savedViewTabs, setSavedViewTabs] = useState<Record<string, string[]>>(() => {
    try {
      const stored = localStorage.getItem('savedViewTabs');
      if (stored) return JSON.parse(stored);
    } catch {}
    return {};
  })

  // Active custom-view tab id on the Leads list — separate from leadStatusFilter
  // because applying a saved custom view writes the actual status filter and we
  // still need to know which tab to highlight.
  const [activeLeadCustomViewId, setActiveLeadCustomViewId] = useState<string | null>(null)

  // Custom views per entity — user-created views with custom names + optional filter snapshot (currently used by leads)
  const [customViews, setCustomViews] = useState<Record<string, { id: string; label: string; serverId?: number; filters?: { status?: string; sortField?: string; sortOrder?: 'ASC' | 'DESC'; search?: string; includeConverted?: boolean; includeDisqualified?: boolean; assigned?: string; statuses?: string[] } }[]>>(() => {
    try {
      const stored = localStorage.getItem('botpros-crm-custom-views')
      if (stored) return JSON.parse(stored)
    } catch {}
    return {}
  })
  const [viewAddMenuOpen, setViewAddMenuOpen] = useState(false)
  const [viewAddMenuMode, setViewAddMenuMode] = useState<'menu' | 'type'>('menu')
  const viewAddMenuRef = useRef<HTMLDivElement>(null)
  const [renamingViewId, setRenamingViewId] = useState<string | null>(null)
  const [renamingViewName, setRenamingViewName] = useState('')
  const [saveViewConfirm, setSaveViewConfirm] = useState(false)
  const [saveViewSaving, setSaveViewSaving] = useState(false)
  const renamingViewRef = useRef<HTMLInputElement>(null)

  // Email validation — show error on blur or after 1.5s debounce
  const [emailTouched, setEmailTouched] = useState(false)
  const emailDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Multi-email and multi-phone for create panel — these back CARD 0 (the
  // primary contact card) of the uniform person-card list (cp-create-cards-v1).
  const [createEmails, setCreateEmails] = useState<string[]>([''])
  const [createPhones, setCreatePhones] = useState<{ type: string; number: string; ext: string }[]>([{ type: 'Mobile', number: '', ext: '' }])
  // Additional named contact people for the Smart Create panel
  // (markers cp-create-persons-v1 + cp-create-cards-v1). Person #1 IS card 0
  // (createFormData.name + createEmails + createPhones); these are the EXTRA
  // person CARDS on the SAME client record (same unified_client_id, additive
  // contact_persons rows). Each card mirrors card 0 exactly: full name
  // (+ role label in org mode) + N email rows + N phone rows (type/number/ext).
  const [createPersons, setCreatePersons] = useState<{ name: string; roleLabel?: string; emails: string[]; phones: { type: string; number: string; ext: string }[] }[]>([])
  const newCreatePersonCard = (name = '') => ({ name, emails: [''], phones: [{ type: 'Mobile', number: '', ext: '' }] })
  // Movable MAIN card (marker cp-create-main-v1): 0 = card 0, i+1 =
  // createPersons[i]. The amber Main badge renders only with 2+ cards; the
  // main card is the non-removable one; contact_persons.is_primary, the
  // person record's display name, and the DEFAULT primary email/phone (chips
  // never clicked) all follow it via buildCreatePersonsPayload({ mainIndex }).
  const [createMainIndex, setCreateMainIndex] = useState(0)
  // Exactly ONE email and ONE phone across ALL cards may be primary for the
  // record (they become the client's preferred_email / preferred_phone).
  // card 0 = the first contact card; card i+1 = createPersons[i]. NULL =
  // the user never clicked a chip → the DEFAULT (the MAIN card's first
  // filled row) applies. Clicking a Primary chip moves primacy explicitly;
  // a dangling pointer (row/card removed) re-heals back to null so the
  // main-card default reapplies.
  const [createPrimaryEmailAt, setCreatePrimaryEmailAt] = useState<{ card: number; idx: number } | null>(null)
  const [createPrimaryPhoneAt, setCreatePrimaryPhoneAt] = useState<{ card: number; idx: number } | null>(null)
  const createCardRowExists = (kind: 'email' | 'phone', card: number, idx: number): boolean => {
    if (card === 0) return kind === 'email' ? idx < Math.max(createEmails.length, 1) : idx < Math.max(createPhones.length, 1)
    const person = createPersons[card - 1]
    if (!person) return false
    return kind === 'email' ? idx < person.emails.length : idx < person.phones.length
  }
  // The DEFAULT primacy target when no chip was ever clicked: the MAIN card's
  // first FILLED row (falling back to its row 0).
  const defaultCreatePrimary = (kind: 'email' | 'phone'): { card: number; idx: number } => {
    const mainIdx = createMainIndex > 0 && createMainIndex <= createPersons.length ? createMainIndex : 0
    const rows: string[] = mainIdx === 0
      ? (kind === 'email' ? createEmails : createPhones.map(p => p.number))
      : (kind === 'email' ? createPersons[mainIdx - 1].emails : createPersons[mainIdx - 1].phones.map(p => p.number))
    const firstFilled = rows.findIndex(v => (v || '').trim() !== '')
    return { card: mainIdx, idx: firstFilled >= 0 ? firstFilled : 0 }
  }
  const effectiveCreatePrimary = (
    kind: 'email' | 'phone',
    pointer: { card: number; idx: number } | null,
  ): { card: number; idx: number } => {
    if (pointer && createCardRowExists(kind, pointer.card, pointer.idx)) return pointer
    return defaultCreatePrimary(kind)
  }
  // Keep the primacy pointers coherent when rows/cards are removed: a pointer
  // at the removed row re-heals to null (the main-card default); pointers past
  // it shift down.
  const adjustPrimaryOnRowRemove = (
    setter: React.Dispatch<React.SetStateAction<{ card: number; idx: number } | null>>,
    cardNo: number,
    removedIdx: number,
  ) => {
    setter(p => {
      if (!p || p.card !== cardNo) return p
      if (p.idx === removedIdx) return null
      return p.idx > removedIdx ? { card: p.card, idx: p.idx - 1 } : p
    })
  }
  const removeCreatePersonCard = (i: number) => {
    setCreatePersons(prev => prev.filter((_, j) => j !== i))
    const cardNo = i + 1
    const adjust = (p: { card: number; idx: number } | null) =>
      !p ? p : p.card === cardNo ? null : p.card > cardNo ? { card: p.card - 1, idx: p.idx } : p
    setCreatePrimaryEmailAt(adjust)
    setCreatePrimaryPhoneAt(adjust)
    setCreateMainIndex(m => (m === cardNo ? 0 : m > cardNo ? m - 1 : m))
  }
  // Remove card 0 (only offered while Main sits on ANOTHER card — the main
  // card is the non-removable one): promote the first added card into the
  // card-0 backing state (createFormData.name + createEmails/createPhones) and
  // shift everything down. Any matched-contact linkage is broken — the
  // promoted person is not the matched contact.
  const removeCreateCard0 = () => {
    const promoted = createPersons[0]
    if (!promoted) return
    setCreateFormData(p => {
      const { _matched, _matchSnapshot, _autoFilled, ...rest } = p as any
      return { ...rest, name: promoted.name || '', email: promoted.emails[0] || '', phone: promoted.phones[0]?.number || '' }
    })
    setCreateEmails(promoted.emails.length > 0 ? [...promoted.emails] : [''])
    setCreatePhones(promoted.phones.length > 0 ? promoted.phones.map(ph => ({ ...ph })) : [{ type: 'Mobile', number: '', ext: '' }])
    setCreatePersons(prev => prev.slice(1))
    const adjust = (p: { card: number; idx: number } | null) =>
      !p ? p : p.card === 0 ? null : { card: p.card - 1, idx: p.idx }
    setCreatePrimaryEmailAt(adjust)
    setCreatePrimaryPhoneAt(adjust)
    setCreateMainIndex(m => (m > 0 ? m - 1 : 0))
    setEmailTouched(false)
    setSmartCreateSearch('')
    setSmartCreateResults(null)
  }
  // Live person-name validation hint shared by the primary Contact Name input
  // and each contact-person row: a "Last, First" comma offers a one-click swap
  // to "First Last"; an "&"/" and " couple offers a one-click split into two
  // contact people. Reuses the cp-validation-error styling. Empty input is not
  // an error (required-field logic is separate). See src/lib/contacts/nameNormalize.
  const renderPersonNameValidation = (
    value: string,
    onUseSwap: (swapped: string) => void,
    onSplit: (persons: string[]) => void,
  ) => {
    const res = validatePersonNameInput(value)
    if (res.ok) return null
    const sugg = res.suggestion
    return (
      <div className="cp-validation-error" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginTop: 3 }}>
        <span>{res.message}</span>
        {sugg?.kind === 'swap' && (
          <button
            type="button"
            className="cp-add-link"
            style={{ padding: 0, marginTop: 0, fontWeight: 600 }}
            onClick={() => onUseSwap(sugg.value)}
          >Use &ldquo;{sugg.value}&rdquo;</button>
        )}
        {sugg?.kind === 'split' && (
          <button
            type="button"
            className="cp-add-link"
            style={{ padding: 0, marginTop: 0, fontWeight: 600 }}
            onClick={() => onSplit(sugg.persons)}
          >Split into 2 contact people</button>
        )}
      </div>
    )
  }
  // Primary chip shared by every email/phone row on every person card
  // (cp-create-cards-v1). Identical geometry to the shipped cp-primary-badge;
  // the ACTIVE chip is static, an inactive chip is a click-to-move-primacy
  // button (muted until hovered).
  const renderCreatePrimaryChip = (active: boolean, onMakePrimary: () => void) => (
    active
      ? <span className="cp-primary-badge">Primary</span>
      : (
        <button
          type="button"
          className="cp-primary-badge"
          title="Make primary"
          onClick={onMakePrimary}
          style={{ background: '#f1f5f9', color: '#94a3b8', border: 'none', cursor: 'pointer' }}
        >Primary</button>
      )
  )
  // Card-header MAIN badge (marker cp-create-main-v1). Renders ONLY when 2+
  // person cards exist: the main card gets the amber Main badge (the app's
  // amber chip idiom, e.g. the lead-count chips) and no remove ×; every other
  // card gets a subtle ghost "Make main" chip that MOVES Main to it. A sole
  // card shows NO badge. The email/phone Primary CHIPS above are unchanged.
  const renderCreateMainBadge = (cardNo: number) => {
    const totalCards = 1 + createPersons.length
    if (totalCards < 2) return null
    return createMainIndex === cardNo
      ? <span className="cp-primary-badge" data-cp-main={CREATE_MAIN_MARKER} style={{ background: '#fef3c7', color: '#92400e' }}>Main</span>
      : (
        <button
          type="button"
          className="cp-primary-badge"
          title="Make this the main contact"
          onClick={() => setCreateMainIndex(cardNo)}
          style={{ background: '#f1f5f9', color: '#94a3b8', border: 'none', cursor: 'pointer' }}
        >Make main</button>
      )
  }
  // Additional addresses typed in the smart-create panel. Unlike the extra
  // email/phone rows these persist: each non-empty row POSTs to
  // /api/crm/contacts/:id/addresses for the matched-or-created contact.
  const [createExtraAddresses, setCreateExtraAddresses] = useState<{ street: string; city: string; state: string; zip: string }[]>([])
  const persistExtraAddresses = async (contactId: string | undefined, rows: { street: string; city: string; state: string; zip: string }[]) => {
    if (!contactId) return
    for (const a of rows) {
      if (!a.street.trim() && !a.city.trim() && !a.zip.trim()) continue
      try {
        await fetch(`/api/crm/contacts/${encodeURIComponent(contactId)}/addresses`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ street: a.street.trim() || null, city: a.city.trim() || null, state: a.state.trim() || null, zip: a.zip.trim() || null }),
        })
      } catch { /* non-fatal — the record itself is already saved */ }
    }
  }
  // Confirmation state for switching between matched contacts / replacing
  // data the user has already entered.
  const [pendingMatchSwitch, setPendingMatchSwitch] = useState<{
    newName: string
    currentLabel: string
    apply: () => void
  } | null>(null)

  // (cpOwner* state removed — was only used by the standalone Create Company panel, now deleted.)

  // Create panel close animation
  const [createPanelClosing, setCreatePanelClosing] = useState(false)
  const closeCreatePanel = () => {
    setCreatePanelClosing(true)
    setTimeout(() => { setShowCreateModal(null); setCreatePanelClosing(false) }, 200)
  }

  // Auto-composed Deal/Lead title: "Name - Street" (name falls back to
  // company; either part alone if the other is missing).
  const composeRecordTitle = (name: string, street: string) => {
    const n = (name || '').trim()
    const s = (street || '').trim()
    return n && s ? `${n} - ${s}` : (n || s)
  }
  // Patch for createFormData that keeps the title in sync with the composed
  // "Name - Street" value. _titleAuto remembers the last auto-generated
  // title; while the current title is empty or still equals it, we keep
  // syncing. The moment the user types their own title (title !== _titleAuto)
  // we stop touching it. Clearing the title resumes auto-fill.
  const autoTitlePatch = (p: Record<string, any>): Record<string, any> => {
    const auto = composeRecordTitle(p.name || p.company || '', p.street || '')
    const current = String(p.title || '')
    const lastAuto = String((p as any)._titleAuto || '')
    if (current !== '' && current !== lastAuto) return {}
    return { title: auto, _titleAuto: auto }
  }

  // Apply a matched contact to the smart-create form, prompting for
  // confirmation first ONLY if the user has already selected a different
  // matched contact. Plain typed data is replaced silently.
  const confirmMatchSwitch = (newContactName: string, matchedId: string | null, apply: () => void) => {
    const currentMatchId = (createFormData as any)._matched as string | undefined
    // Same contact clicked again — no-op
    if (currentMatchId && matchedId && currentMatchId === matchedId) return
    // No prior match — apply immediately
    if (!currentMatchId) {
      apply()
      return
    }
    // Prior match exists — confirm before replacing
    const currentLabel =
      ((createFormData as any)._matchSnapshot?.name as string) ||
      ((createFormData as any)._matchSnapshot?.company as string) ||
      createFormData.name ||
      createFormData.company ||
      'the current contact'
    setPendingMatchSwitch({ newName: newContactName, currentLabel, apply })
  }

  // "Use this contact" on the already-on-file dedup warnings: fetch the
  // matched contact and apply it to the form exactly like picking them
  // from the autocomplete dropdown would have.
  const applyDedupMatch = async (id: string, fallbackName: string) => {
    try {
      const client = await fetchClient(id)
      const apply = () => {
        setCreateFormData((prev) => ({
          ...prev,
          name: client.preferred_name || '',
          email: client.preferred_email || '',
          phone: client.preferred_phone || prev.phone,
          street: client.preferred_street || '',
          city: client.preferred_city || '',
          state: client.preferred_state || '',
          zip: client.preferred_zip || '',
          clientType: client.client_type === 'organization' ? 'organization' : 'person',
          _matched: client.unified_client_id,
          _matchSnapshot: { name: client.preferred_name || '', company: client.client_type === 'organization' ? (client.preferred_name || '') : '', email: client.preferred_email || '', phone: client.preferred_phone || '', street: client.preferred_street || '', city: client.preferred_city || '', state: client.preferred_state || '', zip: client.preferred_zip || '' } as any,
          _autoFilled: { email: !!client.preferred_email, phone: !!client.preferred_phone, street: !!client.preferred_street, city: !!client.preferred_city, state: !!client.preferred_state } as any,
        }))
        setCreateEmails([client.preferred_email || ''])
        setCreatePhones([{ type: 'Mobile', number: client.preferred_phone || '', ext: '' }])
        setSmartCreateSearch('')
        setSmartCreateResults(null)
        setSmartCreateActiveField(null)
      }
      confirmMatchSwitch(client.preferred_name || fallbackName, client.unified_client_id, apply)
    } catch {
      setDealToast({ id: Date.now(), message: 'Could not load that contact', type: 'error' })
    }
  }

  const closeSmartCreate = () => {
    setSmartCreateClosing(true)
    setTimeout(() => {
      setSmartCreateOpen(false)
      setSmartCreateClosing(false)
      setSmartCreateSearch('')
      setSmartCreateResults(null)
      setSmartCreateType(null)
      setShowCreateModal(null)
      setCreateFormData({})
      setCreateError(null)
      setCreateEmails([''])
      setCreatePhones([{ type: 'Mobile', number: '', ext: '' }])
      setCreateExtraAddresses([])
      setCreatePersons([])
      setCreatePrimaryEmailAt(null)
      setCreatePrimaryPhoneAt(null)
      setCreateMainIndex(0)
      setEmailTouched(false)
      setSmartCreateNotesOpen(false)
      setSmartCreateNotesList([])
      setCreateOrgSubType([])
      setCreateOrgAccountingRole('customer')
    }, 200)
  }

  // Open the smart-create slide-out pre-filled with data from a client (the
  // current Contact Record), and auto-select the "Also create" chip for the
  // card that was clicked. Used by the + button on the right-sidebar cards.
  const openSmartCreateForClient = (
    client: CrmClient,
    chip: 'deal' | 'lead' | 'task' | 'ticket',
  ) => {
    const autoTitle = chip === 'ticket'
      ? ''
      : chip === 'task'
        ? (client.preferred_name || '')
        : composeRecordTitle(client.preferred_name || '', client.preferred_street || '')
    const base: Record<string, unknown> = {
      name: client.preferred_name || '',
      email: client.preferred_email || '',
      phone: client.preferred_phone || '',
      street: client.preferred_street || '',
      city: client.preferred_city || '',
      state: client.preferred_state || '',
      zip: client.preferred_zip || '',
      clientType: client.client_type === 'organization' ? 'organization' : 'person',
      title: autoTitle,
      _titleAuto: autoTitle,
      _matched: client.unified_client_id,
      _autoFilled: {
        email: !!client.preferred_email,
        phone: !!client.preferred_phone,
        street: !!client.preferred_street,
        city: !!client.preferred_city,
        state: !!client.preferred_state,
      },
    }
    // Chip-specific defaults (mirrors the "Also create" chip click logic)
    if (chip === 'deal') {
      const dd = new Date(); dd.setMonth(dd.getMonth() + 1, 0)
      base.dealPipeline = '0'
      base.closeDate = dd.toISOString().slice(0, 10)
    }
    setCreateFormData(base)
    setCreateEmails([client.preferred_email || ''])
    setCreatePhones([{ type: 'Mobile', number: client.preferred_phone || '', ext: '' }])
    setEmailTouched(false)
    setSmartCreateSearch('')
    setSmartCreateResults(null)
    setCreateError(null)
    setSmartCreateType(chip)
    setShowCreateModal(chip)
    setSmartCreateOpen(true)
  }

  // (ccIndustry*/ccType* state removed — was only used by the standalone Create Company panel, now deleted.
  // The Company Profile detail page reuses companyIndustryOptions / companyTypeOptions for inline edit.)

  // (ct*/ticket*Options state removed — was only used by the standalone Create Ticket panel, now deleted.
  // Create Ticket now opens the unified Smart Create panel with Ticket pre-selected.)

  const companyIndustryOptions = [
    { value: 'construction', label: 'Construction' },
    { value: 'real_estate', label: 'Real Estate' },
    { value: 'painting', label: 'Painting' },
    { value: 'residential_services', label: 'Residential Services' },
    { value: 'commercial_services', label: 'Commercial Services' },
    { value: 'general_contractor', label: 'General Contractor' },
    { value: 'subcontractor', label: 'Subcontractor' },
    { value: 'property_management', label: 'Property Management' },
    { value: 'government', label: 'Government' },
    { value: 'education', label: 'Education' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'retail', label: 'Retail' },
    { value: 'technology', label: 'Technology' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'other', label: 'Other' },
  ]

  const companyTypeOptions = [
    { value: 'prospect', label: 'Prospect' },
    { value: 'partner', label: 'Partner' },
    { value: 'reseller', label: 'Reseller' },
    { value: 'vendor', label: 'Vendor' },
    { value: 'customer', label: 'Customer' },
    { value: 'other', label: 'Other' },
  ]

  // "Add contacts" dropdown
  const [addBtnOpen, setAddBtnOpen] = useState(false)
  const addBtnRef = useRef<HTMLDivElement>(null)

  // Quick filters bar toggle
  const [quickFiltersOpen, setQuickFiltersOpen] = useState(false)

  // Pipeline selector dropdown (deals only)
  const [pipelineSelectorOpen, setPipelineSelectorOpen] = useState(false)
  const [pipelineSelectorSearch, setPipelineSelectorSearch] = useState('')
  const pipelineSelectorRef = useRef<HTMLDivElement>(null)

  // "+ More" quick filter popover
  const [qfMoreOpen, setQfMoreOpen] = useState(false)
  const [qfMoreSearch, setQfMoreSearch] = useState('')
  const [qfMoreView, setQfMoreView] = useState<'add' | 'edit'>('add')
  const [qfDragIdx, setQfDragIdx] = useState<number | null>(null)
  const [qfDragOverIdx, setQfDragOverIdx] = useState<number | null>(null)
  const qfMoreRef = useRef<HTMLDivElement>(null)
  const defaultQuickFilters = [
    'preferred_zip', 'preferred_city',
    'preferred_phone', 'client_type',
    'deal_count', 'lead_count',
    'legacy_total_invoiced', 'legacy_total_paid', 'legacy_balance',
    'legacy_total_invoices', 'legacy_total_estimates',
    'legacy_sources', 'legacy_job_count', 'legacy_total_docs', 'created_at',
  ]
  const [activeQuickFilters, setActiveQuickFilters] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem('activeQuickFilters')
      if (stored) {
        const parsed = JSON.parse(stored)
        if (Array.isArray(parsed)) return parsed
      }
    } catch {}
    return defaultQuickFilters
  })

  // Available quick filter properties grouped by category
  const quickFilterProperties = [
    { category: 'Contact information', items: [
      { key: 'preferred_city', label: 'City', icon: 'text' },
      { key: 'preferred_zip', label: 'Zip code', icon: 'text' },
      { key: 'preferred_phone', label: 'Phone number', icon: 'text' },
      { key: 'community_name', label: 'Community', icon: 'text' },
    ]},
    { category: 'Classification', items: [
      { key: 'client_type', label: 'Contact type', icon: 'enum' },
    ]},
    { category: 'Activity & deals', items: [
      { key: 'deal_count', label: 'Deal count', icon: 'number' },
      { key: 'lead_count', label: 'Lead count', icon: 'number' },
    ]},
    { category: 'Financial', items: [
      { key: 'legacy_total_invoiced', label: 'Total invoiced', icon: 'number' },
      { key: 'legacy_total_paid', label: 'Total paid', icon: 'number' },
      { key: 'legacy_balance', label: 'Balance', icon: 'number' },
      { key: 'legacy_total_invoices', label: 'Invoice count', icon: 'number' },
      { key: 'legacy_total_estimates', label: 'Estimate count', icon: 'number' },
    ]},
    { category: 'Legacy data', items: [
      { key: 'legacy_sources', label: 'Data sources', icon: 'enum' },
      { key: 'legacy_job_count', label: 'Job count', icon: 'number' },
      { key: 'legacy_total_docs', label: 'Document count', icon: 'number' },
    ]},
    { category: 'Dates', items: [
      { key: 'created_at', label: 'Create date', icon: 'date' },
    ]},
  ]

  // Quick filter pill dropdown state
  const [qfPillOpen, setQfPillOpen] = useState<string | null>(null)
  const [qfPillSearch, setQfPillSearch] = useState('')
  const qfPillRef = useRef<HTMLDivElement>(null)
  const [qfPillValues, setQfPillValues] = useState<Record<string, string[]>>({})

  // Per-tab filter storage: { "contacts:": {...}, "contacts:matched": {...}, "contacts:custom:abc": {...} }
  const [tabFilterStore, setTabFilterStore] = useState<Record<string, Record<string, string[]>>>(() => {
    try {
      const stored = localStorage.getItem('botpros-crm-tab-filters')
      if (stored) return JSON.parse(stored)
    } catch {}
    return {}
  })
  const prevTabKeyRef = useRef<string>('')

  // When the active tab changes, save old tab's filters and restore new tab's filters
  useEffect(() => {
    const currentFilter = activeView === 'contacts' ? clientStatusFilter : activeView === 'companies' ? companyStatusFilter : ''
    const tabKey = `${activeView}:${currentFilter}`
    const prevKey = prevTabKeyRef.current

    // Save old tab's filters (only if we had a previous tab)
    if (prevKey && prevKey !== tabKey) {
      setTabFilterStore(prev => ({ ...prev, [prevKey]: qfPillValues }))
    }

    // Restore new tab's filters
    const restored = tabFilterStore[tabKey] || {}
    setQfPillValues(restored)
    prevTabKeyRef.current = tabKey
  }, [activeView, clientStatusFilter, companyStatusFilter])

  // Persist tab filter store
  useEffect(() => {
    // Also save current tab's values into the store before persisting
    const currentFilter = activeView === 'contacts' ? clientStatusFilter : activeView === 'companies' ? companyStatusFilter : ''
    const tabKey = `${activeView}:${currentFilter}`
    const toSave = { ...tabFilterStore, [tabKey]: qfPillValues }
    localStorage.setItem('botpros-crm-tab-filters', JSON.stringify(toSave))
  }, [qfPillValues, tabFilterStore])

  // Reset page on filter change
  const qfPillValuesRef = useRef(qfPillValues)
  useEffect(() => {
    if (qfPillValuesRef.current !== qfPillValues) {
      setClientPage(1)
      setCompanyPage(1)
    }
    qfPillValuesRef.current = qfPillValues
  }, [qfPillValues])

  const toggleQfPillValue = (filterKey: string, value: string) => {
    setQfPillValues(prev => {
      const current = prev[filterKey] || []
      const next = current.includes(value) ? current.filter(v => v !== value) : [...current, value]
      return { ...prev, [filterKey]: next }
    })
  }

  // Convert qfPillValues into API query params for fetchClients
  const buildQuickFilterOpts = (vals: Record<string, string[]>): Record<string, string> => {
    const opts: Record<string, string> = {}
    // Text filters — pass first value as LIKE search
    const textMap: Record<string, string> = {
      preferred_state: 'state', preferred_zip: 'zip',
      preferred_city: 'city', preferred_phone: 'phone',
      latest_lead_status: 'lead_status', latest_deal_stage: 'deal_stage',
      latest_pipeline_name: 'pipeline', community_name: 'community',
    }
    for (const [fk, param] of Object.entries(textMap)) {
      const v = vals[fk]
      if (v && v.length > 0 && v[0]) opts[param] = v[0]
    }
    // Enum filters — join as comma-separated
    const enumMap: Record<string, string> = {
      match_status: 'match_status', client_type: 'client_type_filter',
      legacy_sources: 'data_sources',
    }
    for (const [fk, param] of Object.entries(enumMap)) {
      const v = vals[fk]
      if (v && v.length > 0) opts[param] = v.join(',')
    }
    // Number filters — parse preset values into min/max
    const numMap: Record<string, [string, string]> = {
      deal_count: ['min_deals', 'max_deals'],
      lead_count: ['min_leads', 'max_leads'],
      legacy_total_invoiced: ['min_invoiced', 'max_invoiced'],
      legacy_total_paid: ['min_paid', 'max_paid'],
      legacy_balance: ['min_balance', 'max_balance'],
      legacy_total_invoices: ['min_invoices', 'max_invoices'],
      legacy_total_estimates: ['min_estimates', 'max_estimates'],
      legacy_total_docs: ['min_docs', 'max_docs'],
      legacy_job_count: ['min_jobs', 'max_jobs'],
    }
    for (const [fk, [minP, maxP]] of Object.entries(numMap)) {
      const v = vals[fk]
      if (v && v.length > 0) {
        const preset = v[0]
        if (preset === 'eq_0') { opts[minP] = '0'; opts[maxP] = '0' }
        else if (preset === 'gt_0') { opts[minP] = '1' }
        else if (preset === 'gte_1') { opts[minP] = '1' }
        else if (preset === 'gte_2') { opts[minP] = '2' }
        else if (preset === 'gte_3') { opts[minP] = '3' }
        else if (preset === 'gte_5') { opts[minP] = '5' }
        else if (preset === 'gt_10' || preset === 'gte_10') { opts[minP] = '10' }
        else if (preset === 'gte_25') { opts[minP] = '25' }
        else if (preset === 'gt_100') { opts[minP] = '101' }
        else if (preset === 'gt_1000') { opts[minP] = '1001' }
        else if (preset === 'gt_5000') { opts[minP] = '5001' }
        else if (preset === 'gt_10000') { opts[minP] = '10001' }
        else if (preset === 'gt_50000') { opts[minP] = '50001' }
        else if (preset === 'gt_100000') { opts[minP] = '100001' }
      }
    }
    // Date filters — resolve presets into from/to ISO dates
    const dateVal = vals['created_at']
    if (dateVal && dateVal.length > 0 && dateVal[0]) {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const iso = (d: Date) => d.toISOString().slice(0, 10)
      const endIso = (d: Date) => { d.setHours(23, 59, 59, 999); return d.toISOString().replace('Z', '') }
      let from: Date | null = null
      let to: Date | null = null
      const preset = dateVal[0]
      if (preset === 'TODAY') { from = new Date(today); to = new Date(today) }
      else if (preset === 'YESTERDAY') { from = new Date(today); from.setDate(from.getDate() - 1); to = new Date(from) }
      else if (preset === 'TOMORROW') { from = new Date(today); from.setDate(from.getDate() + 1); to = new Date(from) }
      else if (preset === 'THIS_WEEK') { from = new Date(today); from.setDate(from.getDate() - from.getDay()); to = new Date(from); to.setDate(to.getDate() + 6) }
      else if (preset === 'LAST_WEEK') { from = new Date(today); from.setDate(from.getDate() - from.getDay() - 7); to = new Date(from); to.setDate(to.getDate() + 6) }
      else if (preset === 'NEXT_WEEK') { from = new Date(today); from.setDate(from.getDate() - from.getDay() + 7); to = new Date(from); to.setDate(to.getDate() + 6) }
      else if (preset === 'THIS_MONTH') { from = new Date(today.getFullYear(), today.getMonth(), 1); to = new Date(today.getFullYear(), today.getMonth() + 1, 0) }
      else if (preset === 'LAST_MONTH') { from = new Date(today.getFullYear(), today.getMonth() - 1, 1); to = new Date(today.getFullYear(), today.getMonth(), 0) }
      else if (preset === 'NEXT_MONTH') { from = new Date(today.getFullYear(), today.getMonth() + 1, 1); to = new Date(today.getFullYear(), today.getMonth() + 2, 0) }
      else if (preset === 'THIS_QUARTER') { const q = Math.floor(today.getMonth() / 3); from = new Date(today.getFullYear(), q * 3, 1); to = new Date(today.getFullYear(), q * 3 + 3, 0) }
      else if (preset === 'LAST_QUARTER') { const q = Math.floor(today.getMonth() / 3) - 1; const y = q < 0 ? today.getFullYear() - 1 : today.getFullYear(); const qq = q < 0 ? 3 : q; from = new Date(y, qq * 3, 1); to = new Date(y, qq * 3 + 3, 0) }
      else if (preset === 'THIS_YEAR') { from = new Date(today.getFullYear(), 0, 1); to = new Date(today.getFullYear(), 11, 31) }
      else if (preset === 'LAST_YEAR') { from = new Date(today.getFullYear() - 1, 0, 1); to = new Date(today.getFullYear() - 1, 11, 31) }
      else if (preset.startsWith('LAST_')) {
        const days = parseInt(preset.replace('LAST_', '').replace('_DAYS', ''))
        if (!isNaN(days)) { from = new Date(today); from.setDate(from.getDate() - days); to = new Date(today) }
      }
      if (from) opts['date_from'] = iso(from)
      if (to) opts['date_to'] = iso(to) + 'T23:59:59'
    }
    return opts
  }

  // Enum options for classification filters
  const qfEnumOptions: Record<string, { value: string; label: string }[]> = {
    match_status: [
      { value: 'matched', label: 'Matched' },
      { value: 'imported', label: 'Imported' },
      { value: 'legacy_only', label: 'Legacy Only' },
    ],
    client_type: [
      { value: 'person', label: 'Person' },
      { value: 'organization', label: 'Client' },
    ],
    data_quality: [
      { value: 'good', label: 'Good' },
      { value: 'fair', label: 'Fair' },
      { value: 'poor', label: 'Poor' },
      { value: 'junk', label: 'Junk' },
      { value: 'duplicate', label: 'Duplicate' },
      { value: 'review', label: 'Review' },
      { value: 'deleted', label: 'Deleted' },
    ],
    legacy_sources: [
      { value: 'qb', label: 'QuickBooks' },
      { value: 'act', label: 'ACT' },
      { value: 'ebridge', label: 'eBridge' },
    ],
  }

  // Date preset options (HubSpot-style) — future: true for presets only relevant to future-facing date fields
  const qfDatePresets = [
    { value: 'TODAY', label: 'Today', desc: 'All of today', future: false },
    { value: 'YESTERDAY', label: 'Yesterday', desc: 'The previous 24 hour day', future: false },
    { value: 'TOMORROW', label: 'Tomorrow', desc: 'The next 24 hour day', future: true },
    { value: 'THIS_WEEK', label: 'This week', desc: 'The current calendar week', future: false },
    { value: 'LAST_WEEK', label: 'Last week', desc: 'The previous calendar week', future: false },
    { value: 'NEXT_WEEK', label: 'Next week', desc: 'The next calendar week', future: true },
    { value: 'THIS_MONTH', label: 'This month', desc: 'The current calendar month', future: false },
    { value: 'LAST_MONTH', label: 'Last month', desc: 'The previous calendar month', future: false },
    { value: 'NEXT_MONTH', label: 'Next month', desc: 'The next calendar month', future: true },
    { value: 'THIS_QUARTER', label: 'This quarter', desc: 'The current quarter', future: false },
    { value: 'LAST_QUARTER', label: 'Last quarter', desc: 'The previous full quarter', future: false },
    { value: 'THIS_YEAR', label: 'This year', desc: 'The current calendar year', future: false },
    { value: 'LAST_YEAR', label: 'Last year', desc: 'The previous calendar year', future: false },
    { value: 'LAST_7_DAYS', label: 'Last 7 days', desc: 'The previous 7 days before today', future: false },
    { value: 'LAST_14_DAYS', label: 'Last 14 days', desc: 'The previous 14 days before today', future: false },
    { value: 'LAST_30_DAYS', label: 'Last 30 days', desc: 'The previous 30 days before today', future: false },
    { value: 'LAST_60_DAYS', label: 'Last 60 days', desc: 'The previous 60 days before today', future: false },
    { value: 'LAST_90_DAYS', label: 'Last 90 days', desc: 'The previous 90 days before today', future: false },
    { value: 'LAST_180_DAYS', label: 'Last 180 days', desc: 'The previous 180 days before today', future: false },
    { value: 'LAST_365_DAYS', label: 'Last 365 days', desc: 'The previous 365 days before today', future: false },
  ]
  // Fields where future dates don't apply
  const pastOnlyDateFields = ['created_at']

  // View type dropdown + table settings panel
  const [viewTypeDropdownOpen, setViewTypeDropdownOpen] = useState(false)
  const viewTypeDropdownRef = useRef<HTMLDivElement>(null)
  const [tableSettingsOpen, setTableSettingsOpen] = useState(false)
  const [tablePageSize, setTablePageSize] = useState<100 | 200 | 500>(() => {
    const saved = localStorage.getItem('tablePageSize')
    const n = Number(saved)
    return (n === 100 || n === 200 || n === 500) ? n as 100 | 200 | 500 : 200
  })
  const [pageSizeOpen, setPageSizeOpen] = useState(false)
  const [tableRowHeight, setTableRowHeight] = useState<'small' | 'medium' | 'large'>(() => {
    return (localStorage.getItem('tableRowHeight') as 'small' | 'medium' | 'large') || 'medium'
  })
  const [tableZebraStriping, setTableZebraStriping] = useState(() => localStorage.getItem('tableZebraStriping') === 'true')

  // Sidebar collapse. Auto-collapses on narrow viewports (< 900 CSS px):
  // there's not enough horizontal room for sidebar + docked phone + main
  // content without crowding. Collapses we made AUTOMATICALLY are undone
  // automatically when the viewport widens back past the threshold; a
  // MANUAL collapse (toggle click) is the user's choice and sticks at any
  // width. Only the manual toggle persists to localStorage.
  const SIDEBAR_AUTO_COLLAPSE_PX = 900
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    const saved = localStorage.getItem('sidebarCollapsed')
    if (saved === 'true') return true
    if (saved === 'false') return false
    // Initial load with no saved preference — collapse if the viewport
    // is too narrow.
    return typeof window !== 'undefined' && window.innerWidth < SIDEBAR_AUTO_COLLAPSE_PX
  })
  // True while the CURRENT collapse came from us (narrow viewport), not the
  // user. Seeded to match the initializer's auto path so a narrow first
  // load also re-expands when the window is widened.
  const sidebarAutoCollapsedRef = useRef<boolean>(
    localStorage.getItem('sidebarCollapsed') === null &&
    typeof window !== 'undefined' && window.innerWidth < SIDEBAR_AUTO_COLLAPSE_PX
  )
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth < SIDEBAR_AUTO_COLLAPSE_PX) {
        if (!sidebarCollapsed) {
          sidebarAutoCollapsedRef.current = true
          setSidebarCollapsed(true)
        }
      } else if (sidebarCollapsed && sidebarAutoCollapsedRef.current) {
        // Undo only OUR automatic collapse — a manual collapse stays.
        sidebarAutoCollapsedRef.current = false
        setSidebarCollapsed(false)
      }
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [sidebarCollapsed])
  const [sidebarHovered, setSidebarHovered] = useState(false)

  // Sidebar drag-resize. Persisted width clamped to [220, 275] — 220 is
  // the legacy default + minimum; 275 caps growth so the docked phone +
  // sidebar nav still fit on common laptop widths without crowding the
  // main content. Match this constant in CSS (--sidebar-max-width).
  const SIDEBAR_MIN_PX = 220
  const SIDEBAR_MAX_PX = 275
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    const saved = parseInt(localStorage.getItem('sidebarWidthPx') || '', 10)
    if (!Number.isFinite(saved)) return SIDEBAR_MIN_PX
    return Math.min(SIDEBAR_MAX_PX, Math.max(SIDEBAR_MIN_PX, saved))
  })
  const [sidebarResizing, setSidebarResizing] = useState(false)
  // === PHONE: SCALE PUBLISHER (sidebar-owned) === --phone-scale = max(0.6, min(sidebarWidth/220,
  // (innerHeight/2 - 32)/370)); consumed as `zoom:` on .phone-device. Scaling with the sidebar is
  // INTENTIONAL — do not remove. docs/PHONE_ARCHITECTURE.md §scale
  // TRAP(phone/zoom-cap-in-js): the 50vh-32px cap must stay in JS — a CSS max-height on a zoomed box squashes the layout instead of shrinking it uniformly.
  // Apply the width to the CSS var on every change (including drag in
  // progress). Persist only on dragend (no localStorage thrash on every
  // pixel of mousemove). Also publish a unitless --phone-scale so the
  // phone-device (floating AND docked) grows with the sidebar — scale=1 at
  // the 220px min, ~1.25 at the 275px max. The phone renders its FIXED base
  // layout (200x370 docked) magnified by CSS `zoom: var(--phone-scale)`, so
  // every element inside scales with the frame instead of floating in
  // whitespace. That also means the old CSS max-height cap (docked top edge
  // must stay below the sidebar midpoint: height <= 50vh - 32px) has to be
  // baked into the scale itself — a max-height on a zoomed box would squash
  // the layout, not shrink it uniformly. Hence the viewport clamp + resize
  // listener here. The docked-phone nav reservation
  // (.sidebar-has-docked-phone .sidebar-scroll-wrap) tracks this same var so
  // the scroll-down arrow stays just above the phone at any sidebar width.
  useEffect(() => {
    document.documentElement.style.setProperty('--sidebar-width', `${sidebarWidth}px`)
    // TRAP(phone/scale-rendered-width): the scale input is the LESSER of the
    // sidebar state and the aside's RENDERED width. State and reality can
    // diverge (grid transitions, route-scoped layouts, anything another
    // surface does to the rail) — and the phone must never overflow the
    // sidebar it docks in. The ResizeObserver re-clamps whenever the aside's
    // box changes for reasons no React state tracks.
    const applyPhoneScale = () => {
      const rendered = document.querySelector('aside.sidebar')?.getBoundingClientRect().width
      // Ignore sub-60px readings: that's the collapsed 56px rail mid-
      // transition, where the phone is unmounted anyway.
      const effectiveWidth = rendered && rendered > 60 ? Math.min(sidebarWidth, rendered) : sidebarWidth
      const scale = computePhoneScale(effectiveWidth, window.innerHeight)
      document.documentElement.style.setProperty('--phone-scale', String(scale))
    }
    applyPhoneScale()
    window.addEventListener('resize', applyPhoneScale)
    let ro: ResizeObserver | null = null
    const aside = document.querySelector('aside.sidebar')
    if (aside && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(applyPhoneScale)
      ro.observe(aside)
    }
    return () => { window.removeEventListener('resize', applyPhoneScale); ro?.disconnect() }
  }, [sidebarWidth])
  const startSidebarResize = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault()
    setSidebarResizing(true)
    const startX = e.clientX
    const startW = sidebarWidth
    const onMove = (ev: PointerEvent) => {
      const dx = ev.clientX - startX
      const next = Math.min(SIDEBAR_MAX_PX, Math.max(SIDEBAR_MIN_PX, startW + dx))
      setSidebarWidth(next)
    }
    const onUp = (ev: PointerEvent) => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      setSidebarResizing(false)
      const final = Math.min(SIDEBAR_MAX_PX, Math.max(SIDEBAR_MIN_PX, startW + (ev.clientX - startX)))
      localStorage.setItem('sidebarWidthPx', String(final))
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }, [sidebarWidth])
  const sidebarScrollRef = useRef<HTMLDivElement>(null)
  const navListRef = useRef<HTMLElement>(null)
  const sidebarScrollInterval = useRef<ReturnType<typeof setInterval> | null>(null)
  const [sidebarScrollUp, setSidebarScrollUp] = useState(false)
  const [sidebarScrollDown, setSidebarScrollDown] = useState(false)
  const [secondaryMenu, setSecondaryMenu] = useState<string | null>(null)
  const [secondaryMenuTop, setSecondaryMenuTop] = useState(0)
  // Resolved top after viewport-fit adjustment (shifts the flyout UP so its
  // full list shows without scrolling when anchored near the bottom edge).
  const [secondaryMenuFitTop, setSecondaryMenuFitTop] = useState(0)
  const [, setFlyoutActivated] = useState(false)
  const secondaryRef = useRef<HTMLDivElement>(null)
  const hoverCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Keep the secondary-menu flyout fully within the viewport. Anchored at the
  // nav item's top by default, but for items near the bottom of the sidebar
  // (e.g. Subcontractors) the full list would overflow the bottom edge and get
  // cut off / require scrolling. After open (and on resize) we measure the
  // flyout height and shift its top UP so its bottom sits within the viewport
  // with an 8px margin, clamped so the top never goes above 8px.
  React.useLayoutEffect(() => {
    if (!secondaryMenu) return
    const MARGIN = 8
    const fit = () => {
      const el = secondaryRef.current
      if (!el) return
      // scrollHeight = full natural content height, ignoring the maxHeight clamp,
      // so the upward shift is computed against the real list height.
      const h = el.scrollHeight
      const vh = window.innerHeight
      const maxTop = Math.max(MARGIN, vh - h - MARGIN)
      setSecondaryMenuFitTop(Math.min(secondaryMenuTop, maxTop))
    }
    fit()
    window.addEventListener('resize', fit)
    return () => window.removeEventListener('resize', fit)
  }, [secondaryMenu, secondaryMenuTop])

  // Profile dropdown
  const [profileMenuOpen, setProfileMenuOpen] = useState(false)
  const [createMenuOpen, setCreateMenuOpen] = useState(false)
  const createMenuRef = useRef<HTMLDivElement>(null)
  const profileRef = useRef<HTMLDivElement>(null)
  // Portaled to <body> so it stacks above the docked phone (--phone-z 9998),
  // same as the notification dropdown. notifPos/profilePos anchor under the bar.
  const profilePopRef = useRef<HTMLDivElement>(null)
  const [profilePos, setProfilePos] = useState<{ top: number; right: number } | null>(null)

  // Create modals
  const [showCreateModal, setShowCreateModal] = useState<'contact' | 'company' | 'deal' | 'lead' | 'task' | 'ticket' | 'quote' | 'product' | null>(null)
  // Duplicate-lead warning for the Create Lead panel — populated by a debounced
  // search against email / phone / contact name when the panel is open.
  // (Effect lives below createFormData declaration to avoid TDZ on deps.)
  const [existingLeadMatches, setExistingLeadMatches] = useState<any[]>([])
  const [smartCreateOpen, setSmartCreateOpen] = useState(false)
  const [smartCreateClosing, setSmartCreateClosing] = useState(false)
  const [smartCreateSearch, setSmartCreateSearch] = useState('')
  const [smartCreateResults, setSmartCreateResults] = useState<UnifiedSearchResult | null>(null)
  const [smartCreateSearching, setSmartCreateSearching] = useState(false)
  const [smartCreateType, setSmartCreateType] = useState<'contact' | 'company' | 'deal' | 'lead' | 'task' | 'ticket' | 'quote' | 'product' | null>(null)
  const [smartCreateActiveField, setSmartCreateActiveField] = useState<'contact' | 'company' | null>(null)
  // Persistent dedup warnings shown UNDER the phone/email/address fields when the
  // entered value already belongs to another contact — stays visible after blur,
  // unlike the focus-driven suggestion dropdown.
  const [smartCreateDupPhone, setSmartCreateDupPhone] = useState<{ id: string; name: string } | null>(null)
  const [smartCreateDupEmail, setSmartCreateDupEmail] = useState<{ id: string; name: string } | null>(null)
  const [smartCreateDupAddress, setSmartCreateDupAddress] = useState<{ id: string; name: string } | null>(null)
  const [createFormData, setCreateFormData] = useState<Record<string, any>>({})
  // Organization classification (Smart Create — company path)
  const [orgSubTypes, setOrgSubTypes] = useState<OrgSubType[]>([])
  const [createOrgSubType, setCreateOrgSubType] = useState<string[]>([])
  const [createOrgAccountingRole, setCreateOrgAccountingRole] = useState<string>('customer')

  // ── Create-panel Community typeahead (distinct names from /communities) ──
  const [createCommunityOptions, setCreateCommunityOptions] = useState<string[]>([])
  const [createCommunityOpen, setCreateCommunityOpen] = useState(false)
  const createCommunityDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    const q = (createFormData.community || '').toString().trim()
    if (!createCommunityOpen || q.length < 1) { setCreateCommunityOptions([]); return }
    if (createCommunityDebounceRef.current) clearTimeout(createCommunityDebounceRef.current)
    createCommunityDebounceRef.current = setTimeout(async () => {
      try {
        const r = await fetch(`/api/crm/communities?search=${encodeURIComponent(q)}&limit=8`)
        const d: any = await r.json()
        const list = (d?.communities || []) as string[]
        setCreateCommunityOptions(list.filter(n => n.toLowerCase() !== q.toLowerCase()).slice(0, 8))
      } catch { setCreateCommunityOptions([]) }
    }, 200)
    return () => { if (createCommunityDebounceRef.current) clearTimeout(createCommunityDebounceRef.current) }
  }, [createFormData.community, createCommunityOpen])

  // ── Create-panel drafts (3-day TTL) ─────────────────────────────────────
  type CreateDraft = { id: string; createdAt: number; updatedAt: number; type: string | null; formData: Record<string, any> }
  const DRAFTS_KEY = 'crm.createDrafts.v1'
  const DRAFTS_TTL_MS = 3 * 24 * 60 * 60 * 1000
  const [createDrafts, setCreateDrafts] = useState<CreateDraft[]>(() => {
    try {
      const raw = localStorage.getItem(DRAFTS_KEY)
      if (!raw) return []
      const arr = JSON.parse(raw) as CreateDraft[]
      const cutoff = Date.now() - DRAFTS_TTL_MS
      const live = (Array.isArray(arr) ? arr : []).filter(d => d && d.createdAt > cutoff)
      if (live.length !== arr.length) localStorage.setItem(DRAFTS_KEY, JSON.stringify(live))
      return live
    } catch { return [] }
  })
  const [currentDraftId, setCurrentDraftId] = useState<string | null>(null)
  const [draftsMenuOpen, setDraftsMenuOpen] = useState(false)
  const [draftDeleteConfirmId, setDraftDeleteConfirmId] = useState<string | null>(null)
  const persistDrafts = (next: CreateDraft[]) => {
    setCreateDrafts(next)
    try { localStorage.setItem(DRAFTS_KEY, JSON.stringify(next)) } catch { /* ignore */ }
  }
  // Auto-save current form as a draft when there's meaningful content.
  // GUARD: never persist a draft while a walkthrough/tutorial is active — the
  // tutorial drives the Create panel and localStorage writes BYPASS the
  // walkthrough's window.fetch write-suppression, so an unguarded autosave left
  // a real "Drafts (1)" entry that survived teardown. See draftGuard.ts +
  // docs/REGRESSIONS.md (2026-07-01).
  useEffect(() => {
    if (!smartCreateOpen) return
    const walkthroughActive = !!(window as any).__kbWalkthroughActive
    const meaningful = Object.entries(createFormData).some(([k, v]) => {
      if (k.startsWith('_')) return false
      if (v == null) return false
      if (typeof v === 'string') return v.trim().length > 0
      return true
    })
    if (!shouldPersistCreateDraft(smartCreateOpen, walkthroughActive, meaningful)) return
    const handle = setTimeout(() => {
      // Re-check at fire time: a walkthrough may have started during the debounce.
      if ((window as any).__kbWalkthroughActive) return
      const now = Date.now()
      setCreateDrafts(prev => {
        let next: CreateDraft[]
        if (currentDraftId && prev.some(d => d.id === currentDraftId)) {
          next = prev.map(d => d.id === currentDraftId ? { ...d, updatedAt: now, type: showCreateModal, formData: { ...createFormData } } : d)
        } else {
          const id = `${now.toString(36)}-${Math.random().toString(36).slice(2, 6)}`
          setCurrentDraftId(id)
          next = [{ id, createdAt: now, updatedAt: now, type: showCreateModal, formData: { ...createFormData } }, ...prev]
        }
        try { localStorage.setItem(DRAFTS_KEY, JSON.stringify(next)) } catch { /* ignore */ }
        return next
      })
    }, 800)
    return () => clearTimeout(handle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smartCreateOpen, showCreateModal, createFormData])
  const deleteDraft = (id: string) => {
    persistDrafts(createDrafts.filter(d => d.id !== id))
    if (currentDraftId === id) setCurrentDraftId(null)
  }
  const restoreDraft = (d: CreateDraft) => {
    setCreateFormData({ ...d.formData })
    setShowCreateModal((d.type as any) || null)
    setCurrentDraftId(d.id)
    setDraftsMenuOpen(false)
  }

  // Debounced duplicate-lead search for the Create Lead panel.
  useEffect(() => {
    if (showCreateModal !== 'lead') { setExistingLeadMatches([]); return }
    const tokens = [createFormData.name, createFormData.email, createFormData.phone]
      .map(v => String(v || '').trim())
      .filter(v => v.length >= 3)
    if (tokens.length === 0) { setExistingLeadMatches([]); return }
    let cancelled = false
    const handle = setTimeout(async () => {
      try {
        const results = await Promise.all(tokens.map(t =>
          fetch(`/api/crm/leads?search=${encodeURIComponent(t)}&pageSize=10`, { credentials: 'include' })
            .then(r => r.ok ? r.json() : null)
            .catch(() => null)
        ))
        if (cancelled) return
        const seen = new Set<string>()
        const merged: any[] = []
        for (const r of results) {
          const arr = Array.isArray(r?.leads) ? r.leads : Array.isArray(r?.data) ? r.data : []
          for (const l of arr) {
            const id = String(l.id ?? l.lead_id ?? '')
            if (!id || seen.has(id)) continue
            const st = String(l.status || l.statusId || l.status_id || '').toUpperCase()
            if (st === 'JUNK' || st === 'CONVERTED' || st === 'DEAD') continue
            seen.add(id)
            merged.push(l)
          }
        }
        setExistingLeadMatches(merged.slice(0, 10))
      } catch { if (!cancelled) setExistingLeadMatches([]) }
    }, 400)
    return () => { cancelled = true; clearTimeout(handle) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreateModal, createFormData.name, createFormData.email, createFormData.phone])

  // Live auto-compose of the Deal/Lead title ("Name - Street") while the
  // create panel is open. Covers typing in Contact Name / Street Address and
  // picking a contact from the autocomplete (which fills name+street but
  // intentionally preserves title). autoTitlePatch only writes while the
  // title is empty or still equals the last auto-generated value, so a
  // manually edited title is never overwritten.
  useEffect(() => {
    if (!smartCreateOpen) return
    if (showCreateModal !== 'deal' && showCreateModal !== 'lead') return
    setCreateFormData(p => {
      const patch = autoTitlePatch(p)
      if (!('title' in patch) || (patch.title === (p.title || '') && patch._titleAuto === (p as any)._titleAuto)) return p
      return { ...p, ...patch }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smartCreateOpen, showCreateModal, createFormData.name, createFormData.company, createFormData.street])
  const [createFormAttendees, setCreateFormAttendees] = useState<number[]>([])
  const [createSaving, setCreateSaving] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [smartCreateNotesOpen, setSmartCreateNotesOpen] = useState(false)
  const [smartCreateNotesList, setSmartCreateNotesList] = useState<any[]>([])
  const [smartCreateNotesLoading, setSmartCreateNotesLoading] = useState(false)
  const [smartCreateNoteEditId, setSmartCreateNoteEditId] = useState<number | null>(null)
  const [smartCreateNoteEditSubject, setSmartCreateNoteEditSubject] = useState('')
  const [smartCreateNoteEditDesc, setSmartCreateNoteEditDesc] = useState('')
  const [smartCreateNoteEditSaving, setSmartCreateNoteEditSaving] = useState(false)
  const [smartCreateNoteCollapsed, setSmartCreateNoteCollapsed] = useState<Set<string>>(new Set())
  const [smartCreateNoteShowAll, setSmartCreateNoteShowAll] = useState<Set<string>>(new Set())
  // Ref to the Company Name input in the unified create panel. When the panel
  // is opened via the "Add companies" → "Create new" flow (smartCreateType === 'company'),
  // we focus + select this input on mount so the user can type the company name immediately
  // instead of the default Contact Name field.
  const smartCreateCompanyInputRef = useRef<HTMLInputElement | null>(null)
  useEffect(() => {
    if (smartCreateOpen && smartCreateType === 'company') {
      // Defer one frame so the input is mounted in the DOM before focusing.
      const id = window.setTimeout(() => {
        smartCreateCompanyInputRef.current?.focus()
        smartCreateCompanyInputRef.current?.select()
      }, 0)
      return () => window.clearTimeout(id)
    }
  }, [smartCreateOpen, smartCreateType])
  // Load org sub-type vocab once when it's first needed (companies view or
  // creating an organization). Cheap, cached in state.
  useEffect(() => {
    if (orgSubTypes.length > 0) return
    // Load when the companies list needs it, OR whenever the Smart Create panel
    // is open — the Contact panel's Organization field classifies a new org and
    // displays a matched org's sub-type, both of which need this vocab.
    if (activeView === 'companies' || smartCreateOpen) {
      listOrgSubTypes().then(r => setOrgSubTypes(r.data || [])).catch(() => {})
    }
  }, [activeView, smartCreateOpen, smartCreateType, orgSubTypes.length])
  // When an existing contact is matched via autocomplete click, the click
  // handler calls setCreateFormData directly with email/phone. Sync those
  // values into the multi-row email/phone arrays so the Primary row reflects
  // the matched contact's data.
  useEffect(() => {
    const matched = (createFormData as any)._matched
    if (matched === undefined) return
    const fdEmail = createFormData.email || ''
    setCreateEmails(prev => {
      const rest = prev.slice(1)
      return [fdEmail, ...rest]
    })
    const fdPhone = createFormData.phone || ''
    setCreatePhones(prev => {
      const rest = prev.slice(1)
      const existingType = prev[0]?.type || 'Mobile'
      const existingExt = prev[0]?.ext || ''
      return [{ type: existingType, number: fdPhone, ext: existingExt }, ...rest]
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(createFormData as any)._matched])

  const [ticketCreateDeals, setTicketCreateDeals] = useState<any[]>([])
  const [ticketCreateProjects, setTicketCreateProjects] = useState<any[]>([])
  const [ticketCreateContactProjects, setTicketCreateContactProjects] = useState<any[]>([])
  // Task "Link to" picker — typeahead by selected type.
  const [taskLinkQuery, setTaskLinkQuery] = useState('')
  const [taskLinkResults, setTaskLinkResults] = useState<any[]>([])
  // Task chained-link pickers: Contact → Deal → Project. Each filters the next.
  const [taskChainContactQuery, setTaskChainContactQuery] = useState('')
  const [taskChainDealQuery, setTaskChainDealQuery] = useState('')
  const [taskChainProjectQuery, setTaskChainProjectQuery] = useState('')
  const [taskChainContactResults, setTaskChainContactResults] = useState<any[]>([])
  const [taskChainDealResults, setTaskChainDealResults] = useState<any[]>([])
  const [taskChainProjectResults, setTaskChainProjectResults] = useState<any[]>([])
  const [taskChainLeadQuery, setTaskChainLeadQuery] = useState('')
  const [taskChainLeadResults, setTaskChainLeadResults] = useState<any[]>([])
  useEffect(() => {
    if (showCreateModal !== 'task') { setTaskChainLeadResults([]); return }
    const q = taskChainLeadQuery.trim()
    if (q.length < 1) { setTaskChainLeadResults([]); return }
    let cancelled = false
    const h = setTimeout(async () => {
      try {
        const r = await fetchLeadsPaginated(1, 8, { search: q, includeConverted: true })
        if (!cancelled) setTaskChainLeadResults((r.leads || []).slice(0, 8))
      } catch { if (!cancelled) setTaskChainLeadResults([]) }
    }, 300)
    return () => { cancelled = true; clearTimeout(h) }
  }, [showCreateModal, taskChainLeadQuery])
  useEffect(() => {
    if (showCreateModal !== 'task') { setTaskChainContactResults([]); return }
    const q = taskChainContactQuery.trim()
    if (q.length < 1) { setTaskChainContactResults([]); return }
    let cancelled = false
    const h = setTimeout(async () => {
      try {
        const r = await fetchClients(1, 8, { search: q })
        const list = (((r as any).clients) || ((r as any).data) || []) as any[]
        if (!cancelled) setTaskChainContactResults(list.slice(0, 8))
      } catch { if (!cancelled) setTaskChainContactResults([]) }
    }, 300)
    return () => { cancelled = true; clearTimeout(h) }
  }, [showCreateModal, taskChainContactQuery])
  useEffect(() => {
    if (showCreateModal !== 'task') { setTaskChainDealResults([]); return }
    const q = taskChainDealQuery.trim()
    const contactId = (createFormData as any).linkContactId as string | undefined
    if (q.length < 1 && !contactId) { setTaskChainDealResults([]); return }
    let cancelled = false
    const h = setTimeout(async () => {
      try {
        let list: any[] = []
        if (contactId) {
          const r = await fetch(`/api/crm/clients/${encodeURIComponent(contactId)}/deals`).then(x => x.ok ? x.json() : [])
          list = (Array.isArray(r) ? r : ((r as any)?.deals || [])) as any[]
          if (q) {
            const lq = q.toLowerCase()
            list = list.filter((d: any) => String(d.title || '').toLowerCase().includes(lq) || String(d.customer_name || d.client_name || '').toLowerCase().includes(lq))
          }
        } else {
          const r = await fetchDeals(0, 1, 8, { search: q, excludeClosed: false })
          list = (r.deals || []) as any[]
        }
        if (!cancelled) setTaskChainDealResults(list.slice(0, 8))
      } catch { if (!cancelled) setTaskChainDealResults([]) }
    }, 300)
    return () => { cancelled = true; clearTimeout(h) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreateModal, taskChainDealQuery, (createFormData as any).linkContactId])
  useEffect(() => {
    if (showCreateModal !== 'task') { setTaskChainProjectResults([]); return }
    const dealId = (createFormData as any).linkDealId as string | undefined
    const contactId = (createFormData as any).linkContactId as string | undefined
    const q = taskChainProjectQuery.trim()
    if (!dealId && !contactId && q.length < 1) { setTaskChainProjectResults([]); return }
    let cancelled = false
    const h = setTimeout(async () => {
      try {
        let dealIdsCsv = ''
        if (dealId) {
          dealIdsCsv = String(dealId)
        } else if (contactId) {
          // No specific deal — fan out to ALL of this contact's deals so we can list every project they touch.
          try {
            const dealsResp = await fetch(`/api/crm/clients/${encodeURIComponent(contactId)}/deals`).then(x => x.ok ? x.json() : [])
            const deals = (Array.isArray(dealsResp) ? dealsResp : ((dealsResp as any)?.deals || [])) as any[]
            const ids = deals.map(d => d.id).filter(Boolean)
            if (ids.length > 0) dealIdsCsv = ids.join(',')
            else if (!q) { if (!cancelled) setTaskChainProjectResults([]); return }
          } catch { /* fall through to plain search */ }
        }
        const params = new URLSearchParams({ limit: '8' })
        if (q) params.set('search', q)
        if (dealIdsCsv) params.set('crm_deal_ids', dealIdsCsv)
        const r = await fetchSubAdminProjects(params)
        const list = (((r?.data?.projects)) || r?.projects || r?.data || []) as any[]
        if (!cancelled) setTaskChainProjectResults(list.slice(0, 8))
      } catch { if (!cancelled) setTaskChainProjectResults([]) }
    }, 300)
    return () => { cancelled = true; clearTimeout(h) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreateModal, taskChainProjectQuery, (createFormData as any).linkDealId, (createFormData as any).linkContactId])
  useEffect(() => {
    if (showCreateModal !== 'task') { setTaskLinkResults([]); return }
    const type = createFormData.ownerType as string | undefined
    const q = taskLinkQuery.trim()
    if (!type || q.length < 1) { setTaskLinkResults([]); return }
    let cancelled = false
    const handle = setTimeout(async () => {
      try {
        let list: any[] = []
        if (type === 'client') {
          const r = await fetchClients(1, 8, { search: q })
          list = (((r as any).clients) || ((r as any).data) || []) as any[]
        } else if (type === 'deal') {
          const r = await fetchDeals(0, 1, 8, { search: q, excludeClosed: false })
          list = (r.deals || []) as any[]
        } else if (type === 'project') {
          const params = new URLSearchParams({ search: q, limit: '8' })
          const r = await fetchSubAdminProjects(params)
          list = (((r?.data?.projects)) || r?.projects || r?.data || []) as any[]
        } else if (type === 'ticket') {
          const r = await fetch(`/api/crm/tickets?search=${encodeURIComponent(q)}&limit=8`).then(x => x.ok ? x.json() : null)
          list = (((r as any)?.tickets) || ((r as any)?.data) || []) as any[]
        }
        if (!cancelled) setTaskLinkResults(list.slice(0, 8))
      } catch { if (!cancelled) setTaskLinkResults([]) }
    }, 300)
    return () => { cancelled = true; clearTimeout(handle) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreateModal, createFormData.ownerType, taskLinkQuery])
  useEffect(() => {
    const matchedId = (createFormData as any)._matched
    if (showCreateModal === 'ticket' && matchedId) {
      fetch(`/api/crm/clients/${matchedId}/deals`)
        .then(r => r.ok ? r.json() : [])
        .then(data => setTicketCreateDeals(Array.isArray(data) ? data : []))
        .catch(() => setTicketCreateDeals([]))
    } else {
      setTicketCreateDeals([])
      setTicketCreateProjects([])
      setTicketCreateContactProjects([])
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(createFormData as any)._matched, showCreateModal])
  // Pre-load this contact's projects (across all their deals) so the create
  // form can offer a one-click "their projects" quick-pick beside the
  // global search.
  useEffect(() => {
    if (showCreateModal !== 'ticket' || ticketCreateDeals.length === 0) { setTicketCreateContactProjects([]); return }
    const dealIds = ticketCreateDeals.map((d: any) => d.id).filter(Boolean).join(',')
    if (!dealIds) { setTicketCreateContactProjects([]); return }
    const params = new URLSearchParams({ crm_deal_ids: dealIds, limit: '20' })
    fetchSubAdminProjects(params)
      .then((r: any) => {
        const list = ((r?.data?.projects) || r?.projects || (r?.data) || []) as any[]
        setTicketCreateContactProjects(Array.isArray(list) ? list : [])
      })
      .catch(() => setTicketCreateContactProjects([]))
  }, [showCreateModal, ticketCreateDeals])

  const [createPanelTickets, setCreatePanelTickets] = useState<any[]>([])
  const [createPanelDeals, setCreatePanelDeals] = useState<any[]>([])
  const [createPanelLeads, setCreatePanelLeads] = useState<any[]>([])
  const [createPanelTasks, setCreatePanelTasks] = useState<any[]>([])
  const [createPanelProjects, setCreatePanelProjects] = useState<any[]>([])
  const [createPanelExpanded, setCreatePanelExpanded] = useState<'deals' | 'leads' | 'tasks' | 'tickets' | 'projects' | null>(null)
  useEffect(() => {
    const matchedId = (createFormData as any)._matched
    if (!matchedId) { setCreatePanelTickets([]); setCreatePanelDeals([]); setCreatePanelLeads([]); setCreatePanelTasks([]); setCreatePanelProjects([]); setCreatePanelExpanded(null); return }
    let cancelled = false
    fetchContactTickets(matchedId)
      .then(list => { if (!cancelled) setCreatePanelTickets((Array.isArray(list) ? list : []).filter((t: any) => t.status !== 'closed' && t.status !== 'resolved')) })
      .catch(() => { if (!cancelled) setCreatePanelTickets([]) })
    fetchClientDeals(matchedId)
      .then(list => { if (!cancelled) setCreatePanelDeals(Array.isArray(list) ? list : []) })
      .catch(() => { if (!cancelled) setCreatePanelDeals([]) })
    fetchClientLeads(matchedId)
      .then(list => { if (!cancelled) setCreatePanelLeads(Array.isArray(list) ? list : []) })
      .catch(() => { if (!cancelled) setCreatePanelLeads([]) })
    fetchTasks({ ownerType: 'client', ownerId: matchedId, limit: 50 })
      .then(res => { if (!cancelled) setCreatePanelTasks((res.tasks || []).filter((t: any) => t.status !== 'completed')) })
      .catch(() => { if (!cancelled) setCreatePanelTasks([]) })
    getClientProjects(matchedId)
      .then(list => { if (!cancelled) setCreatePanelProjects(Array.isArray(list) ? list : []) })
      .catch(() => { if (!cancelled) setCreatePanelProjects([]) })
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(createFormData as any)._matched, ticketsRefreshTick])

  // Tasks state
  const [tasksData, setTasksData] = useState<CrmTask[]>([])
  const [tasksTotal, setTasksTotal] = useState(0)
  const [tasksPage, setTasksPage] = useState(1)
  const [tasksFilter, setTasksFilter] = useState<string>('open')
  const [tasksLoading, setTasksLoading] = useState(false)
  const [tasksSearch, setTasksSearch] = useState('')
  const [taskSelectedRows, setTaskSelectedRows] = useState<Set<number>>(new Set())
  const [tasksPriorityFilter, setTasksPriorityFilter] = useState('')
  const [tasksSort, setTasksSort] = useState({ field: 'due_date' as string, order: 'ASC' as string })
  const [taskColWidths, setTaskColWidths] = useState({ title: 260, status: 120, priority: 110, due_date: 140, assigned_to: 160, linked_to: 200, actions: 90 })
  const [previewTask, setPreviewTask] = useState<CrmTask | null>(null)
  const [deleteTaskConfirm, setDeleteTaskConfirm] = useState<CrmTask | null>(null)
  const [deleteTaskSaving, setDeleteTaskSaving] = useState(false)
  const [bulkDeleteTaskConfirm, setBulkDeleteTaskConfirm] = useState<number | null>(null)
  const [bulkDeleteTaskSaving, setBulkDeleteTaskSaving] = useState(false)
  const [previewTaskClosing, setPreviewTaskClosing] = useState(false)
  const closeTaskPreview = () => { setPreviewTaskClosing(true); setTimeout(() => { setPreviewTask(null); setPreviewTaskClosing(false) }, 200) }

  // Tickets state (HubSpot-style)
  const [ticketsData, setTicketsData] = useState<CrmTicket[]>([])
  const [ticketsTotal, setTicketsTotal] = useState(0)
  const [ticketsPageCount, setTicketsPageCount] = useState(1)
  const [ticketsPage, setTicketsPage] = useState(() => Number(sessionStorage.getItem(uKey('ticketsPage'))) || 1)
  const [ticketsSearch, setTicketsSearch] = useState(() => sessionStorage.getItem(uKey('ticketsSearch')) || '')
  const [ticketsSearchDebounced, setTicketsSearchDebounced] = useState('')
  const [ticketsFilter, setTicketsFilter] = useState<string>('open')
  const [ticketTabCounts, setTicketTabCounts] = useState<{ all: number; open: number; closed: number }>({ all: 0, open: 0, closed: 0 })
  const [ticketsSort, setTicketsSort] = useState<{ field: string; order: 'ASC' | 'DESC' }>({ field: 'created_at', order: 'DESC' })
  const [ticketsLoading, setTicketsLoading] = useState(false)
  const [ticketSelectedRows, setTicketSelectedRows] = useState<Set<number>>(new Set())
  const [previewTicket, setPreviewTicket] = useState<CrmTicket | null>(null)
  const [previewTicketClosing, setPreviewTicketClosing] = useState(false)
  // Ticket "Edit links" modal — independent typeahead pickers for Contact / Deal / Project.
  // Changing one does NOT auto-clear the others; cross-contact linking is allowed.
  const [ticketLinksOpen, setTicketLinksOpen] = useState(false)
  const [ticketLinksDraft, setTicketLinksDraft] = useState<{ contactId: string | null; contactLabel: string; dealId: number | null; dealLabel: string; projectId: string | null; projectLabel: string }>({ contactId: null, contactLabel: '', dealId: null, dealLabel: '', projectId: null, projectLabel: '' })
  const [ticketLinksContactQuery, setTicketLinksContactQuery] = useState('')
  const [ticketLinksContactResults, setTicketLinksContactResults] = useState<any[]>([])
  const [ticketLinksDealQuery, setTicketLinksDealQuery] = useState('')
  const [ticketLinksDealResults, setTicketLinksDealResults] = useState<any[]>([])
  const [ticketLinksProjectQuery, setTicketLinksProjectQuery] = useState('')
  const [ticketLinksProjectResults, setTicketLinksProjectResults] = useState<any[]>([])
  const [ticketLinksSaving, setTicketLinksSaving] = useState(false)
  // "Attach existing ticket" picker, opened from a Project Record or Deal Record.
  const [attachTicketTarget, setAttachTicketTarget] = useState<{ kind: 'project' | 'deal'; id: number | string; name?: string } | null>(null)
  const [attachTicketQuery, setAttachTicketQuery] = useState('')
  const [attachTicketResults, setAttachTicketResults] = useState<any[]>([])
  const [attachTicketSaving, setAttachTicketSaving] = useState<number | null>(null)
  // "+" button on Project/Deal Tickets card opens a tiny popup with two choices.
  const [ticketPlusChoice, setTicketPlusChoice] = useState<
    | { kind: 'project'; id: string; name?: string; createPayload: any }
    | { kind: 'deal'; id: number; name?: string; createPayload: any }
    | null
  >(null)
  useEffect(() => {
    if (!attachTicketTarget) { setAttachTicketResults([]); return }
    let cancelled = false
    const handle = setTimeout(async () => {
      try {
        const missingParam = attachTicketTarget.kind === 'project' ? 'missing_project=1' : 'missing_deal=1'
        const q = attachTicketQuery.trim()
        const url = `/api/crm/tickets?${missingParam}&limit=20${q ? `&search=${encodeURIComponent(q)}` : ''}`
        const r = await fetch(url)
        const d = r.ok ? await r.json() : null
        if (!cancelled) setAttachTicketResults(((d as any)?.tickets || (d as any)?.data || []) as any[])
      } catch { if (!cancelled) setAttachTicketResults([]) }
    }, 300)
    return () => { cancelled = true; clearTimeout(handle) }
  }, [attachTicketTarget, attachTicketQuery])
  useEffect(() => {
    if (!ticketLinksOpen || ticketLinksContactQuery.trim().length < 1) { setTicketLinksContactResults([]); return }
    let cancelled = false
    const handle = setTimeout(async () => {
      try {
        const r = await fetchClients(1, 8, { search: ticketLinksContactQuery.trim() })
        if (!cancelled) setTicketLinksContactResults(((r as any).clients || (r as any).data || []) as any[])
      } catch { if (!cancelled) setTicketLinksContactResults([]) }
    }, 300)
    return () => { cancelled = true; clearTimeout(handle) }
  }, [ticketLinksOpen, ticketLinksContactQuery])
  useEffect(() => {
    if (!ticketLinksOpen || ticketLinksDealQuery.trim().length < 1) { setTicketLinksDealResults([]); return }
    let cancelled = false
    const handle = setTimeout(async () => {
      try {
        const r = await fetchDeals(0, 1, 8, { search: ticketLinksDealQuery.trim(), excludeClosed: false })
        if (!cancelled) setTicketLinksDealResults((r.deals || []) as any[])
      } catch { if (!cancelled) setTicketLinksDealResults([]) }
    }, 300)
    return () => { cancelled = true; clearTimeout(handle) }
  }, [ticketLinksOpen, ticketLinksDealQuery])
  useEffect(() => {
    if (!ticketLinksOpen || ticketLinksProjectQuery.trim().length < 1) { setTicketLinksProjectResults([]); return }
    let cancelled = false
    const handle = setTimeout(async () => {
      try {
        const p = new URLSearchParams({ search: ticketLinksProjectQuery.trim(), limit: '8' })
        const r = await fetchSubAdminProjects(p)
        const list = ((r?.data?.projects) || r?.projects || (r?.data) || []) as any[]
        if (!cancelled) setTicketLinksProjectResults(Array.isArray(list) ? list.slice(0, 8) : [])
      } catch { if (!cancelled) setTicketLinksProjectResults([]) }
    }, 300)
    return () => { cancelled = true; clearTimeout(handle) }
  }, [ticketLinksOpen, ticketLinksProjectQuery])
  // Cross-contact deal / project pickers for the Create Ticket form.
  const [ticketCreateDealQuery, setTicketCreateDealQuery] = useState('')
  const [ticketCreateDealResults, setTicketCreateDealResults] = useState<any[]>([])
  const [ticketCreateProjectQuery, setTicketCreateProjectQuery] = useState('')
  const [ticketCreateProjectResults, setTicketCreateProjectResults] = useState<any[]>([])
  useEffect(() => {
    if (showCreateModal !== 'ticket' || ticketCreateDealQuery.trim().length < 1) { setTicketCreateDealResults([]); return }
    let cancelled = false
    const handle = setTimeout(async () => {
      try {
        const r = await fetchDeals(0, 1, 8, { search: ticketCreateDealQuery.trim(), excludeClosed: false })
        if (!cancelled) setTicketCreateDealResults((r.deals || []) as any[])
      } catch { if (!cancelled) setTicketCreateDealResults([]) }
    }, 300)
    return () => { cancelled = true; clearTimeout(handle) }
  }, [showCreateModal, ticketCreateDealQuery])
  useEffect(() => {
    if (showCreateModal !== 'ticket' || ticketCreateProjectQuery.trim().length < 1) { setTicketCreateProjectResults([]); return }
    let cancelled = false
    const handle = setTimeout(async () => {
      try {
        const p = new URLSearchParams({ search: ticketCreateProjectQuery.trim(), limit: '8' })
        const r = await fetchSubAdminProjects(p)
        const list = ((r?.data?.projects) || r?.projects || (r?.data) || []) as any[]
        if (!cancelled) setTicketCreateProjectResults(Array.isArray(list) ? list.slice(0, 8) : [])
      } catch { if (!cancelled) setTicketCreateProjectResults([]) }
    }, 300)
    return () => { cancelled = true; clearTimeout(handle) }
  }, [showCreateModal, ticketCreateProjectQuery])
  const [ticketCloseForm, setTicketCloseForm] = useState<{ open: boolean; category: string; note: string; saving: boolean }>({ open: false, category: '', note: '', saving: false })
  const ticketSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Calls state (HubSpot-style)
  const [callsData, setCallsData] = useState<CrmActivity[]>([])
  // Live per-extension RC call log — powers the emulated iPhone Recents tab so
  // it mirrors the user's own deskphone / RC app history (including internal
  // calls), independent of the tenant-wide synced `callsData` used by /#/calls.
  const [deviceCallLog, setDeviceCallLog] = useState<DeviceCall[]>([])
  const [callsTotal, setCallsTotal] = useState(0)
  const [callsPageCount, setCallsPageCount] = useState(1)
  // calls-pg-loadstate-v1: the query key the CURRENTLY-RENDERED callsData rows
  // were fetched for. When it differs from the query the view now wants (page/
  // filter/sort changed and the new fetch is still in flight), the rows on screen
  // are STALE — the table shows a loading/error state instead of the previous
  // page's rows (see callsListDisplayState + the `callsStale` prop below).
  const [callsAppliedKey, setCallsAppliedKey] = useState<string>('')
  const [callsPage, setCallsPage] = useState(() => Number(sessionStorage.getItem(uKey('callsPage'))) || 1)
  const [callsSearch, setCallsSearch] = useState(() => sessionStorage.getItem(uKey('callsSearch')) || '')
  const [callsSearchDebounced, setCallsSearchDebounced] = useState('')
  const [callsDirectionFilter, setCallsDirectionFilter] = useState<string>('')
  const [callsSort, setCallsSort] = useState<{ field: string; order: 'ASC' | 'DESC' }>({ field: 'created', order: 'DESC' })
  const [callsLoading, setCallsLoading] = useState(false)
  // True when the LAST calls fetch failed — lets CallsList render a distinct
  // error + Retry row instead of the "No calls match these filters" empty state
  // (a swallowed/failed fetch must not masquerade as no-results). See
  // docs/REGRESSIONS.md (calls list empty-state on a swallowed list fetch).
  const [callsError, setCallsError] = useState(false)
  // Bumped by the CallsList Retry button to re-trigger the fetch effect below.
  const [callsReloadNonce, setCallsReloadNonce] = useState(0)
  const [callSelectedRows, setCallSelectedRows] = useState<Set<number>>(new Set())
  const [callAnalytics, setCallAnalytics] = useState<{ totalCalls: number; callsThisWeek: number; avgDurationSeconds: number; inboundCalls: number; outboundCalls: number; missedCalls: number; missedRate: number; callsByDay: { day: string; cnt: number }[]; callsByUser: { responsible_id: number; cnt: number; avg_dur: number }[] } | null>(null)
  // Column order: subject | contact (person) | phone (external party) |
  // direction | duration | outcome | sentiment | dealproject (deal/lead/
  // project) | rec | assigned | date. Defaults live in src/lib/calls/
  // colWidths.ts (shared with the vitest contract). NOT persisted today —
  // if persistence is ever added it MUST load through sanitizeColWidths()
  // (see the 2026-07-02 row in docs/REGRESSIONS.md).
  const [callColWidths, setCallColWidths] = useState<Record<string, number>>(() => ({ ...CALL_COL_DEFAULT_WIDTHS }))
  const resizingCallCol = useRef<{ key: string; startX: number; startWidth: number } | null>(null)
  // === Calls list column config — gold-standard "Edit columns" parity ===
  // (calls-edit-columns-v1) Drives the SHARED Edit-columns popover (label +
  // category) and the `callVisibleCols` visibility/order set that CallsList
  // honors. Subject is the frozen, always-present first column (mirrors the
  // Contacts "Name" convention). Persistence key: botpros-crm-calls-visible-columns.
  const CALL_COLUMN_DEFS: { key: string; label: string; category: string; sortField?: string; width: number }[] = [
    { key: 'subject', label: 'Subject', category: 'Call Details', sortField: 'subject', width: 240 },
    { key: 'contact', label: 'Contact', category: 'Call Details', width: 150 },
    { key: 'phone', label: 'Phone number', category: 'Call Details', width: 130 },
    { key: 'direction', label: 'Direction', category: 'Call Details', sortField: 'direction', width: 90 },
    { key: 'duration', label: 'Duration', category: 'Call Details', width: 80 },
    { key: 'outcome', label: 'Outcome', category: 'Call Details', width: 110 },
    { key: 'sentiment', label: 'Sentiment', category: 'Call Details', width: 100 },
    { key: 'dealproject', label: 'Deal or project', category: 'CRM Activity', width: 170 },
    { key: 'rec', label: 'Rec', category: 'Call Details', width: 50 },
    { key: 'assigned', label: 'Assigned', category: 'Call Details', sortField: 'responsible_id', width: 90 },
    { key: 'date', label: 'Date', category: 'Dates', sortField: 'created', width: 100 },
  ]
  const CALL_COLUMN_MAP = Object.fromEntries(CALL_COLUMN_DEFS.map(d => [d.key, d]))
  const CALL_COL_CATEGORIES = [...new Set(CALL_COLUMN_DEFS.map(d => d.category))]
  const DEFAULT_CALL_VISIBLE_COLS = CALL_COLUMN_DEFS.map(d => d.key)
  const [callVisibleCols, setCallVisibleCols] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('botpros-crm-calls-visible-columns')
      if (saved) {
        const parsed = JSON.parse(saved)
        // Keep only known keys, always force 'subject' first (frozen).
        if (Array.isArray(parsed) && parsed.length > 0) {
          const known = new Set(DEFAULT_CALL_VISIBLE_COLS)
          const cleaned = parsed.filter((k: unknown): k is string => typeof k === 'string' && known.has(k) && k !== 'subject')
          return ['subject', ...cleaned]
        }
      }
    } catch { /* ignore */ }
    return [...DEFAULT_CALL_VISIBLE_COLS]
  })
  const [previewCall, setPreviewCall] = useState<CrmActivity | null>(null)
  const [previewCallClosing, setPreviewCallClosing] = useState(false)
  const callSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Calls view: which row currently has the inline "Create contact" form open.
  // Limited to one at a time so the table doesn't grow unbounded. Driven by
  // the `?activity=` deep-link path so unmatched-call notifications can offer
  // an immediate "create contact" CTA on the right row.
  const [callCreateContactRowId, setCallCreateContactRowId] = useState<number | null>(null)

  // Meets state — CRM meetings are paginated activity rows (provider_type=MEETING).
  const [meetsData, setMeetsData] = useState<CrmMeeting[]>([])
  const [meetsTotal, setMeetsTotal] = useState(0)
  const [meetsPageCount, setMeetsPageCount] = useState(1)
  const [meetsPage, setMeetsPage] = useState(1)
  const [meetsSearch, setMeetsSearch] = useState('')
  const [meetsSearchDebounced, setMeetsSearchDebounced] = useState('')
  const [meetsWhen, setMeetsWhen] = useState<'all' | 'upcoming' | 'past'>('all')
  const [meetsSort, setMeetsSort] = useState<{ field: 'start_time' | 'subject' | 'created'; order: 'ASC' | 'DESC' }>({ field: 'start_time', order: 'DESC' })
  const [meetsLoading, setMeetsLoading] = useState(false)
  const [meetsError, setMeetsError] = useState(false)
  const [meetsReloadNonce, setMeetsReloadNonce] = useState(0)
  const [meetsSelectedRows, setMeetsSelectedRows] = useState<Set<number>>(new Set())
  const [meetsLogOpen, setMeetsLogOpen] = useState(false)
  const meetsSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Timesheets — sidebar chip + view header chip. Refreshed on view change
  // and after every approve/unapprove so the count stays honest.
  const [timesheetCounts, setTimesheetCounts] = useState<TimeCountsByOwner | null>(null)
  // Tab selection for the Timesheets view ("pending" is the default —
  // matches the chip count + the action the user typically takes).
  const [timesheetsTab, setTimesheetsTab] = useState<'pending' | 'approved' | 'all'>('pending')
  // Timesheets view source: deal-based time entries (approval flow) vs the
  // internal shift clock (live who's-in board + week punches).
  const [timesheetsSource, setTimesheetsSource] = useState<'deals' | 'shift' | 'schedules'>('deals')

  // Products state
  const [productsData, setProductsData] = useState<CrmProduct[]>([])
  const [productsTotal, setProductsTotal] = useState(0)
  const [productsLoading, setProductsLoading] = useState(false)
  const [productsSearch, setProductsSearch] = useState('')
  const [productsPage, setProductsPage] = useState(1)
  const [productsPages, setProductsPages] = useState(1)
  const [productsSort, setProductsSort] = useState<{ field: string; order: 'ASC' | 'DESC' }>({ field: 'created_at', order: 'DESC' })
  const [productsSelectedRows, setProductsSelectedRows] = useState<Set<number>>(new Set())
  const [productsStatusFilter, setProductsStatusFilter] = useState('')
  const [productColWidths, setProductColWidths] = useState({ name: 260, sku: 140, category: 150, unit_price: 130, unit: 100, status: 110, created_at: 150 })
  const [productsExporting, setProductsExporting] = useState(false)
  const [previewProduct, setPreviewProduct] = useState<CrmProduct | null>(null)
  const [previewProductClosing, setPreviewProductClosing] = useState(false)
  const closeProductPreview = () => { setPreviewProductClosing(true); setTimeout(() => { setPreviewProduct(null); setPreviewProductClosing(false) }, 200) }

  // Quotes state
  const [quotesData, setQuotesData] = useState<CrmQuote[]>([])
  const [quotesTotal, setQuotesTotal] = useState(0)
  const [quotesLoading, setQuotesLoading] = useState(false)
  const [quotesSearch, setQuotesSearch] = useState('')
  const [quotesPage, setQuotesPage] = useState(1)
  const [quotesSort, setQuotesSort] = useState<{ field: string; order: 'ASC' | 'DESC' }>({ field: 'created_at', order: 'DESC' })
  const [quotesSelectedRows, setQuotesSelectedRows] = useState<Set<number>>(new Set())
  const [quotesStatusFilter, setQuotesStatusFilter] = useState('')
  const [quotesColWidths, setQuotesColWidths] = useState({ title: 260, status: 120, total: 120, deal: 130, client: 150, expires: 130, created: 130 })
  const [previewQuote, setPreviewQuote] = useState<CrmQuote | null>(null)
  const [previewQuoteClosing, setPreviewQuoteClosing] = useState(false)
  const closeQuotePreview = () => { setPreviewQuoteClosing(true); setTimeout(() => { setPreviewQuote(null); setPreviewQuoteClosing(false) }, 200) }

  // Documents browser state
  const [docsData, setDocsData] = useState<ClientDocument[]>([])
  const [docsTotal, setDocsTotal] = useState(0)
  const [docsTotalDocs, setDocsTotalDocs] = useState(0)
  const [docsLoading, setDocsLoading] = useState(false)
  const [docsPage, setDocsPage] = useState(1)
  const [docsPageSize] = useState(200)
  const [docsSearch, setDocsSearch] = useState('')
  const [docsSearchInput, setDocsSearchInput] = useState('')
  const [docsAddressFilter, setDocsAddressFilter] = useState('')
  const [docsAddressInput, setDocsAddressInput] = useState('')
  const [docsTypeSet, setDocsTypeSet] = useState<Set<string>>(new Set())
  const [docsTypeDropdownOpen, setDocsTypeDropdownOpen] = useState(false)
  const docsTypeDropdownRef = useRef<HTMLDivElement>(null)
  const [docsDateFrom, setDocsDateFrom] = useState('')
  const [docsDateTo, setDocsDateTo] = useState('')
  const [docsSort, setDocsSort] = useState('doc_date')
  const [docsOrder, setDocsOrder] = useState<'asc' | 'desc'>('desc')
  const [docsTypes, setDocsTypes] = useState<DocTypeCount[]>([])
  const [docsSelectedRows, setDocsSelectedRows] = useState<Set<number>>(new Set())
  const [docsColWidths, setDocsColWidths] = useState({ client: 220, type: 120, address: 200, date: 120, pages: 80, size: 100, qb_ref: 110, actions: 90 })
  const [docsQuickFiltersOpen, setDocsQuickFiltersOpen] = useState(false)
  const [docsSettingsOpen, setDocsSettingsOpen] = useState(false)
  const [docsExporting, setDocsExporting] = useState(false)
  const [docsPreview, setDocsPreview] = useState<any>(null)
  const [docsPreviewClosing, setDocsPreviewClosing] = useState(false)
  const [docsExpandedClients, setDocsExpandedClients] = useState<Set<string>>(new Set())
  const [docsPdfFullscreen, setDocsPdfFullscreen] = useState(false)
  const [docsViewTab, setDocsViewTab] = useState('')
  const [docsTabEditOpen, setDocsTabEditOpen] = useState(false)

  // Reports state
  const [reportPipeline, setReportPipeline] = useState<PipelineReportRow[]>([])
  const [reportRevenue, setReportRevenue] = useState<RevenueReportRow[]>([])
  const [reportActivities, setReportActivities] = useState<ActivitiesReportRow[]>([])
  const [reportLeadSources, setReportLeadSources] = useState<LeadSourceReportRow[]>([])
  const [reportLeaderboard, setReportLeaderboard] = useState<LeaderboardRow[]>([])
  const [reportLeadsByEstimator, setReportLeadsByEstimator] = useState<LeadsByEstimatorRow[]>([])
  const [reportsLoading, setReportsLoading] = useState(false)

  // Enhanced Reporting state
  const [reportingTab, setReportingTab] = useState<'overview' | 'sales' | 'deals' | 'activities' | 'leads' | 'team' | 'builder'>('overview')
  const [reportDateRange, setReportDateRange] = useState<'this_month' | 'last_month' | 'this_quarter' | 'this_year'>('this_month')
  const [summaryKPIs, setSummaryKPIs] = useState<SummaryKPIs | null>(null)
  const [dashboardData, setDashboardData] = useState<any>({})
  const [dashboardLoading, setDashboardLoading] = useState(false)
  const [dashboardError, setDashboardError] = useState<string | null>(null)
  const [dashboardView, setDashboardView] = useState<'executive' | 'sales' | 'operations' | 'team'>('executive')
  const [dashboardOwnerFilter, setDashboardOwnerFilter] = useState<string>('all')
  const [dashboardRefreshTick, setDashboardRefreshTick] = useState(0)
  const [savedDashboards, setSavedDashboards] = useState<any[]>([])
  const [analyticsTab, setAnalyticsTab] = useState<'sales' | 'activity' | 'pipeline'>('sales')
  const [analyticsData, setAnalyticsData] = useState<any>({})
  const [analyticsLoading, setAnalyticsLoading] = useState(false)

  // Report Builder state
  const [builderSource, setBuilderSource] = useState<'deals' | 'leads' | 'contacts' | 'activities'>('deals')
  const [builderChart, setBuilderChart] = useState<'bar' | 'line' | 'pie' | 'donut' | 'table' | 'number' | 'funnel'>('bar')
  const [builderMetric, setBuilderMetric] = useState<'count' | 'sum' | 'average'>('count')
  const [builderGroupBy, setBuilderGroupBy] = useState('status')
  const [builderResults, setBuilderResults] = useState<any[]>([])
  const [builderLoading, setBuilderLoading] = useState(false)

  // Workflows state
  const [workflowsData, setWorkflowsData] = useState<Workflow[]>([])
  const [workflowsLoading, setWorkflowsLoading] = useState(false)
  const [workflowTab, setWorkflowTab] = useState<'all' | 'active' | 'inactive'>('all')
  const [editingWorkflowId, setEditingWorkflowId] = useState<number | null>(null)
  const [workflowEditorActions, setWorkflowEditorActions] = useState<(WorkflowAction & { canvas_x?: number; canvas_y?: number; parent_action_id?: number | null; branch_label?: string | null })[]>([])
  const [workflowEditorWorkflow, setWorkflowEditorWorkflow] = useState<Workflow | null>(null)
  const [wfSelectedNode, setWfSelectedNode] = useState<number | null>(null)
  const [wfSidebarMode, setWfSidebarMode] = useState<'actions' | 'config' | 'trigger' | null>(null)
  const [wfInsertIndex, setWfInsertIndex] = useState<number>(0)
  const [showCreateWorkflow, setShowCreateWorkflow] = useState(false)
  const [newWorkflowName, setNewWorkflowName] = useState('')
  const [newWorkflowObjectType, setNewWorkflowObjectType] = useState('contact')
  const [newWorkflowTrigger, setNewWorkflowTrigger] = useState('manual')
  // Workflows list-view state (6-row template)
  const [wfSearch, setWfSearch] = useState('')
  const [wfSort, setWfSort] = useState<{ field: string; order: 'ASC' | 'DESC' }>({ field: 'updated_at', order: 'DESC' })
  const [wfSelectedRows, setWfSelectedRows] = useState<Set<number>>(new Set())
  const [wfPage, setWfPage] = useState(1)
  const [wfSettingsOpen, setWfSettingsOpen] = useState(false)
  const [wfPageSize, setWfPageSize] = useState(200)
  const [wfRowHeight, setWfRowHeight] = useState<'small' | 'medium' | 'large'>('medium')
  const [wfZebra, setWfZebra] = useState(false)
  const [wfQuickFiltersOpen, setWfQuickFiltersOpen] = useState(false)
  const [wfStatusFilter, setWfStatusFilter] = useState<string>('')
  const [wfPageSizeOpen, setWfPageSizeOpen] = useState(false)
  const [wfColWidths, setWfColWidths] = useState({ name: 280, status: 100, object_type: 130, trigger: 160, created_at: 120, updated_at: 120 })
  const [wfPreview, setWfPreview] = useState<any>(null)

  // Sequences state
  const [sequencesData, setSequencesData] = useState<Sequence[]>([])
  const [sequencesLoading, setSequencesLoading] = useState(false)
  const [sequenceTab, setSequenceTab] = useState<'all' | 'active' | 'drafts'>('all')
  const [editingSequenceId, setEditingSequenceId] = useState<number | null>(null)
  const [editingSequence, setEditingSequence] = useState<(Sequence & { steps: SequenceStep[] }) | null>(null)
  const [seqEditorTab, setSeqEditorTab] = useState<'steps' | 'enrollments' | 'performance'>('steps')
  const [seqEnrollments, setSeqEnrollments] = useState<SequenceEnrollment[]>([])
  const [seqStats, setSeqStats] = useState<{ total_enrolled: number; active: number; completed: number; replied: number; bounced: number } | null>(null)
  const [showAddStep, setShowAddStep] = useState(false)
  const [showCreateSequence, setShowCreateSequence] = useState(false)
  const [newSequenceName, setNewSequenceName] = useState('')
  const [seqEditingStep, setSeqEditingStep] = useState<SequenceStep | null>(null)
  // Sequences list-view state (6-row template)
  const [seqSearch, setSeqSearch] = useState('')
  const [seqSort, setSeqSort] = useState<{ field: string; order: 'ASC' | 'DESC' }>({ field: 'updated_at', order: 'DESC' })
  const [seqSelectedRows, setSeqSelectedRows] = useState<Set<number>>(new Set())
  const [seqPage, setSeqPage] = useState(1)
  const [seqSettingsOpen, setSeqSettingsOpen] = useState(false)
  const [seqPageSize, setSeqPageSize] = useState(200)
  const [seqRowHeight, setSeqRowHeight] = useState<'small' | 'medium' | 'large'>('medium')
  const [seqZebra, setSeqZebra] = useState(false)
  const [seqQuickFiltersOpen, setSeqQuickFiltersOpen] = useState(false)
  const [seqStatusFilter, setSeqStatusFilter] = useState<string>('')
  const [seqPageSizeOpen, setSeqPageSizeOpen] = useState(false)
  const [seqColWidths, setSeqColWidths] = useState({ name: 280, status: 100, type: 130, created_at: 120, updated_at: 120 })
  const [seqPreview, setSeqPreview] = useState<any>(null)

  // Chatflows state
  const [chatflowsData, setChatflowsData] = useState<Chatflow[]>([])
  const [chatflowsLoading, setChatflowsLoading] = useState(false)
  const [chatflowTab, setChatflowTab] = useState<'web_chat' | 'bot'>('web_chat')
  const [editingChatflowId, setEditingChatflowId] = useState<number | null>(null)
  const [editingChatflow, setEditingChatflow] = useState<(Chatflow & { actions: ChatflowBotAction[] }) | null>(null)
  const [cfEditorTab, setCfEditorTab] = useState<'build' | 'target' | 'display' | 'options'>('build')
  const [showCreateChatflow, setShowCreateChatflow] = useState(false)
  const [newChatflowName, setNewChatflowName] = useState('')
  const [newChatflowType, setNewChatflowType] = useState<'live_chat' | 'bot'>('live_chat')
  const [cfSort, setCfSort] = useState<{ field: string; order: 'ASC' | 'DESC' }>({ field: 'created_at', order: 'DESC' })
  const [cfPage, setCfPage] = useState(1)
  const [cfSelectedRows, setCfSelectedRows] = useState<Set<number>>(new Set())
  const [cfExporting, setCfExporting] = useState(false)
  const [cfStatusFilter, setCfStatusFilter] = useState<'all' | 'active' | 'inactive'>('all')
  const [cfSearch, setCfSearch] = useState('')
  const [cfColWidths, setCfColWidths] = useState({ name: 260, chatflow_type: 100, is_active: 90, language: 100, created_at: 140, actions: 80 })

  // Snippets state
  const [snippetsData, setSnippetsData] = useState<CrmSnippet[]>([])
  const [snippetsTotal, setSnippetsTotal] = useState(0)
  const [snippetsLoading, setSnippetsLoading] = useState(false)
  const [snippetsSearch, setSnippetsSearch] = useState('')
  const [showSnippetModal, setShowSnippetModal] = useState<'create' | 'edit' | null>(null)
  const [editingSnippet, setEditingSnippet] = useState<CrmSnippet | null>(null)
  const [snippetForm, setSnippetForm] = useState<{ name: string; content: string; shortcut: string }>({ name: '', content: '', shortcut: '' })
  const [snippetSaving, setSnippetSaving] = useState(false)
  const [snippetError, setSnippetError] = useState<string | null>(null)
  const [snipSort, setSnipSort] = useState<{ field: string; order: 'ASC' | 'DESC' }>({ field: 'updated_at', order: 'DESC' })
  const [snipPage, setSnipPage] = useState(1)
  const [snipSelectedRows, setSnipSelectedRows] = useState<Set<number>>(new Set())
  const [snipExporting, setSnipExporting] = useState(false)
  const [snipColWidths, setSnipColWidths] = useState({ name: 260, shortcut: 110, content: 300, updated_at: 140, actions: 80 })

  // Templates state
  const [templatesData, setTemplatesData] = useState<CrmTemplate[]>([])
  const [templatesTotal, setTemplatesTotal] = useState(0)
  const [templatesLoading, setTemplatesLoading] = useState(false)
  const [templatesSearch, setTemplatesSearch] = useState('')
  const [templatesCategoryFilter, setTemplatesCategoryFilter] = useState('')
  const [showTemplateModal, setShowTemplateModal] = useState<'create' | 'edit' | null>(null)
  const [editingTemplate, setEditingTemplate] = useState<CrmTemplate | null>(null)
  const [templateForm, setTemplateForm] = useState<{ name: string; category: string; subject: string; body: string }>({ name: '', category: 'email', subject: '', body: '' })
  const [templateSaving, setTemplateSaving] = useState(false)
  const [templateError, setTemplateError] = useState<string | null>(null)
  const [tmplPage, setTmplPage] = useState(1)
  const [tmplSelectedRows, setTmplSelectedRows] = useState<Set<number>>(new Set())
  const [tmplSort, setTmplSort] = useState({ field: 'updated_at' as string, order: 'DESC' as string })
  const [tmplExporting, setTmplExporting] = useState(false)

  // Playbooks state
  const [playbooksData, setPlaybooksData] = useState<CrmPlaybook[]>([])
  const [playbooksTotal, setPlaybooksTotal] = useState(0)
  const [playbooksLoading, setPlaybooksLoading] = useState(false)
  const [playbooksSearch, setPlaybooksSearch] = useState('')
  const [showPlaybookModal, setShowPlaybookModal] = useState<'create' | null>(null)
  const [playbookForm, setPlaybookForm] = useState<{ title: string; content: string; engagement_type: string }>({ title: '', content: '', engagement_type: '' })
  const [playbookSaving, setPlaybookSaving] = useState(false)
  const [playbookError, setPlaybookError] = useState<string | null>(null)
  const [playbookDetail, setPlaybookDetail] = useState<CrmPlaybook | null>(null)
  const [playbookDetailOpen, setPlaybookDetailOpen] = useState(false)
  const [newQuestionText, setNewQuestionText] = useState('')
  const [pbPage, setPbPage] = useState(1)
  const [pbSelectedRows, setPbSelectedRows] = useState<Set<number>>(new Set())
  const [pbStatusFilter, setPbStatusFilter] = useState('')
  const [pbSort, setPbSort] = useState({ field: 'updated_at' as string, order: 'DESC' as string })
  const [pbEngagementFilter, setPbEngagementFilter] = useState('')
  const [pbExporting, setPbExporting] = useState(false)
  const [pbViewTab, setPbViewTab] = useState('')
  const [pbPreview, setPbPreview] = useState<CrmPlaybook | null>(null)

  // Orders state
  const [ordersData, setOrdersData] = useState<CrmOrder[]>([])
  const [ordersTotal, setOrdersTotal] = useState(0)
  const [ordersLoading, setOrdersLoading] = useState(false)
  const [ordersSearch, setOrdersSearch] = useState('')
  const [ordersStatusFilter, setOrdersStatusFilter] = useState('')
  const [showOrderPanel, setShowOrderPanel] = useState(false)
  const [orderForm, setOrderForm] = useState<Record<string, any>>({})
  const [orderLineItems, setOrderLineItems] = useState<Array<{ description: string; quantity: number; unit_price: number }>>([])
  const [orderSaving, setOrderSaving] = useState(false)
  const [orderError, setOrderError] = useState<string | null>(null)

  // CRM Projects state
  const [projectsData, setProjectsData] = useState<CrmProject[]>([])
  const [projectsTotal, setProjectsTotal] = useState(0)
  const [projectsLoading, setProjectsLoading] = useState(false)
  const [projectsSearch, setProjectsSearch] = useState('')
  const [projectsStatusFilter, setProjectsStatusFilter] = useState('')
  const [projectsPipelineStageFilter, setProjectsPipelineStageFilter] = useState('')
  // Completed and Cancelled projects are hidden by default — same pattern as
  // the leads "Converted" + "Disqualified" toggles — so the list focuses on
  // active work. Each pill is an independent filter.
  const [projectsIncludeCompleted, setProjectsIncludeCompleted] = useState(false)
  const [projectsIncludeCancelled, setProjectsIncludeCancelled] = useState(false)
  // Rich pill filters (parity with the deals view's pill set).
  const [projectsOwnerSet, setProjectsOwnerSet] = useState<Set<string>>(new Set())
  const [projectsStatusSet, setProjectsStatusSet] = useState<Set<string>>(new Set())
  const [projectsValueFilter, setProjectsValueFilter] = useState('')
  const [projectsStartDateFilter, setProjectsStartDateFilter] = useState('')
  const [projectsEndDateFilter, setProjectsEndDateFilter] = useState('')
  const [projectsCityFilter, setProjectsCityFilter] = useState('')
  const [projectsZipFilter, setProjectsZipFilter] = useState('')
  const [projectsQfPillOpen, setProjectsQfPillOpen] = useState<string | null>(null)
  // Shared ref for the open Status / Pipeline Stage quick-filter multi-select
  // dropdown, used to close it on outside click.
  const projectsQfMultiRef = useRef<HTMLDivElement>(null)
  // Multi-select stage filter triggered from the Pipeline Stage column kebab.
  // `null` = show all stages (default). Non-null = only show rows whose
  // pipeline_stage is in the Set.
  const [projectsStageFilterSet, setProjectsStageFilterSet] = useState<Set<string> | null>(null)
  const [projectsStageFilterPopover, setProjectsStageFilterPopover] = useState<{ x: number; y: number } | null>(null)
  const [projectsStageCounts, setProjectsStageCounts] = useState<Record<string, number>>({})
  // Hold types filter: empty Set = no hold types selected (show all on-hold projects if P_ON_HOLD is selected).
  // Non-empty Set = filter to projects with hold_type in the Set. Only applies when P_ON_HOLD stage is selected.
  const [projectsHoldTypesFilter, setProjectsHoldTypesFilter] = useState<Set<string>>(new Set())
  const PROJECTS_COL_DEFAULTS: Record<string, number> = {
    name: 220, pipeline_stage: 160, client_name: 110, contract_value: 120,
    estimator: 110, owner: 160, progress: 120, start_date: 120,
    end_date: 120, value: 110, assigned_to: 140, po_number: 100,
  }
  const [projectColWidths, setProjectColWidths] = useState<Record<string, number>>(() => {
    // Persisted per-user config may predate column redesigns or be corrupt —
    // heal it on load (docs/REGRESSIONS.md 2026-07-02). The old raw
    // JSON.parse could make a persisted `null`/number/array the state itself.
    let raw: string | null = null
    try { raw = localStorage.getItem('botpros-projects-col-widths') } catch { /* storage unavailable */ }
    return sanitizeColWidths(raw, PROJECTS_COL_DEFAULTS)
  })
  const resizingProjectCol = useRef<{ key: string; startX: number; startWidth: number } | null>(null)
  const onProjectResizeStart = (key: string, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const startWidth = projectColWidths[key] ?? PROJECTS_COL_DEFAULTS[key] ?? 120
    resizingProjectCol.current = { key, startX: e.clientX, startWidth }
    const onMouseMove = (ev: MouseEvent) => {
      // Snapshot the drag ref — the queued updater replays after mouseup may
      // have nulled it (2026-07-01 #/calls P0; same race here).
      const drag = resizingProjectCol.current
      if (!drag) return
      const delta = ev.clientX - drag.startX
      const newWidth = Math.max(60, drag.startWidth + delta)
      setProjectColWidths(prev => ({ ...prev, [drag.key]: newWidth }))
    }
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
      if (resizingProjectCol.current) {
        setProjectColWidths(prev => {
          try { localStorage.setItem('botpros-projects-col-widths', JSON.stringify(prev)) } catch { /* ignore */ }
          return prev
        })
        resizingProjectCol.current = null
      }
      const swallow = (ev: MouseEvent) => { ev.preventDefault(); ev.stopPropagation(); document.removeEventListener('click', swallow, true) }
      document.addEventListener('click', swallow, true)
      setTimeout(() => document.removeEventListener('click', swallow, true), 200)
    }
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }
  useEffect(() => {
    if (!projectsStageFilterPopover) return
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('.projects-stage-filter-popover')) setProjectsStageFilterPopover(null)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [projectsStageFilterPopover])
  // Fetch per-stage counts the first time the filter popover opens in this session
  useEffect(() => {
    if (!projectsStageFilterPopover || Object.keys(projectsStageCounts).length > 0) return
    fetch('/api/crm/projects/stage-counts')
      .then(r => r.ok ? r.json() : {})
      .then(data => setProjectsStageCounts(data as Record<string, number>))
      .catch(() => { /* non-fatal */ })
  }, [projectsStageFilterPopover])
  const [projectsPage, setProjectsPage] = useState(1)
  const [projectsSort, setProjectsSort] = useState<{ field: string; order: 'ASC' | 'DESC' }>({ field: 'created_at', order: 'DESC' })
  const [showProjectPanel, setShowProjectPanel] = useState(false)
  const [projectForm, setProjectForm] = useState<Record<string, any>>({})
  const [projectSaving, setProjectSaving] = useState(false)
  const [projectError, setProjectError] = useState<string | null>(null)
  const [projectSelectedRows, setProjectSelectedRows] = useState<Set<string | number>>(new Set())
  const [projectExporting, setProjectExporting] = useState(false)
  const [projectsAssignedFilter, setProjectsAssignedFilter] = useState('')

  // Project preview sidebar state
  const [previewProject, setPreviewProject] = useState<any>(null)
  const [previewProjectClosing, setPreviewProjectClosing] = useState(false)
  const closeProjectPreview = () => { setPreviewProjectClosing(true); setTimeout(() => { setPreviewProject(null); setPreviewProjectClosing(false) }, 200) }

  // Project detail/record page state
  const [projectWorkspaceId, setProjectWorkspaceId] = useState<string | null>(() => parseWorkspaceHash(window.location.hash)?.projectId ?? null)
  const [projectDetailOpen, setProjectDetailOpen] = useState(() => {
    const seg = getHashSegments()
    return seg[0] === 'projects' && seg[1] === 'record' && !!seg[2]
  })
  const [projectDetailData, setProjectDetailData] = useState<any>(null)
  const [projectKeyInfoEditField, setProjectKeyInfoEditField] = useState<string | null>(null)
  const [projectDetailTab, setProjectDetailTab] = useState<'about' | 'activities' | 'revenue'>('about')
  // Project attachments tab — aggregated list from direct uploads, deal notes, and deal emails
  const [projectAttachments, setProjectAttachments] = useState<any[]>([])
  const [projectAttachmentsLoading, setProjectAttachmentsLoading] = useState(false)
  const [projectAttachmentsUploading, setProjectAttachmentsUploading] = useState(false)
  const projectAttachmentsInputRef = useRef<HTMLInputElement | null>(null)
  // The Revenue view uses this same project-scoped financials endpoint. Keep
  // the record summary tiles on that source so they reconcile with the detail.
  const [projectFinancialDocs, setProjectFinancialDocs] = useState<FinancialDoc[]>([])
  const [projectFinancialsLoading, setProjectFinancialsLoading] = useState(false)
  // Upload modal: ask the user what kind of doc they're attaching so the row
  // can be badge-colored the same as legacy QB docs.
  const [projectUploadModal, setProjectUploadModal] = useState<{ docType: string; files: File[] } | null>(null)
  const [projectDetailLoading, setProjectDetailLoading] = useState(false)
  const [projectDetailActivities, setProjectDetailActivities] = useState<any[]>([])
  const [projectDetailRefreshTick, setProjectDetailRefreshTick] = useState(0)
  const [projectDetailTasks, setProjectDetailTasks] = useState<any[]>([])
  const [prjActionsOpen, setPrjActionsOpen] = useState(false)
  const [prjActivityFilter, setPrjActivityFilter] = useState('all')
  const [prjActivitySearch, setPrjActivitySearch] = useState('')
  const [prjExpandedCards, setPrjExpandedCards] = useState<Set<number>>(new Set())
  const [prjNotesEditing, setPrjNotesEditing] = useState(false)
  const [prjNotesValue, setPrjNotesValue] = useState('')
  const [prjStageUpdating, setPrjStageUpdating] = useState(false)
  const [prjStaffPickerOpen, setPrjStaffPickerOpen] = useState(false)
  const prjStaffBtnRef = useRef<HTMLButtonElement | null>(null)
  const [prjOwnerPickerOpen, setPrjOwnerPickerOpen] = useState(false)
  // Local in-dropdown owner selection: clicking a row toggles this immediately
  // (checkbox highlights + dropdown stays open); committed on the explicit
  // "Assign" action, not per-click. null = no open picker / no pending edits.
  const [prjOwnerPending, setPrjOwnerPending] = useState<string[] | null>(null)
  const prjOwnerBtnRef = useRef<HTMLButtonElement | null>(null)
  const [prjSeniorPickerOpen, setPrjSeniorPickerOpen] = useState(false)
  const prjSeniorBtnRef = useRef<HTMLButtonElement | null>(null)
  const [prjAssignableUsers, setPrjAssignableUsers] = useState<any[]>([])
  const [prjProjectCompanyId, setPrjProjectCompanyId] = useState<string|null>(null)
  const [prjProjectCompanyName, setPrjProjectCompanyName] = useState<string|null>(null)
  const [prjLinkedDeal, setPrjLinkedDeal] = useState<any>(null)
  const [prjLinkedContact, setPrjLinkedContact] = useState<any>(null)
  const [prjLinkedClientCache, setPrjLinkedClientCache] = useState<{ id: string; preferred_email: string | null; preferred_phone: string | null; preferred_street?: string | null; preferred_city?: string | null; preferred_state?: string | null } | null>(null)
  const [prjEditingField, setPrjEditingField] = useState<string | null>(null)
  const [prjEditingValue, setPrjEditingValue] = useState<string>('')
  // Ref to the currently-mounted project-record date <input> (start_date / end_date).
  // Only one date field is ever in edit mode at a time (prjEditingField is a single
  // key), so a single ref is sufficient. Used to open the native calendar via
  // showPicker() so a click anywhere on the field opens the picker on the FIRST click,
  // not only when the tiny calendar glyph is hit. See openPrjDatePicker below.
  const prjDateInputRef = useRef<HTMLInputElement | null>(null)
  // Tickets linked to this project (Task 16). Re-runs when tickets are
  // created/updated elsewhere via ticketsRefreshTick.
  const [projectTickets, setProjectTickets] = useState<any[]>([])
  useEffect(() => {
    if (!projectDetailData?.id) { setProjectTickets([]); return }
    let cancelled = false
    fetchProjectTickets(String(projectDetailData.id))
      .then(list => { if (!cancelled) setProjectTickets(Array.isArray(list) ? list : []) })
      .catch(() => { if (!cancelled) setProjectTickets([]) })
    return () => { cancelled = true }
  }, [projectDetailData?.id, ticketsRefreshTick])
  // When navigating here with a #ticket-{id} anchor (e.g. from the contact
  // page's rollup), scroll to that ticket row and flash-highlight it.
  useEffect(() => {
    const match = window.location.hash.match(/#ticket-(\d+)$/)
    if (!match) return
    if (projectTickets.length === 0) return
    const el = document.getElementById(`ticket-${match[1]}`)
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    el.style.transition = 'background-color 600ms ease'
    el.style.backgroundColor = '#fef3c7'
    setTimeout(() => { el.style.backgroundColor = 'transparent' }, 1800)
  }, [projectTickets])
  // Fetch project attachments when the project loads.
  // Unified list: direct uploads + deal note attachments + deal email attachments.
  useEffect(() => {
    if (!projectDetailData?.id) return
    const pid = projectDetailData.id
    setProjectAttachmentsLoading(true)
    fetch(`/api/crm/projects/${encodeURIComponent(pid)}/attachments`)
      .then(r => r.json())
      .then(d => setProjectAttachments(Array.isArray(d.attachments) ? d.attachments : []))
      .catch(() => setProjectAttachments([]))
      .finally(() => setProjectAttachmentsLoading(false))
  }, [projectDetailData?.id])
  useEffect(() => {
    if (!projectDetailData?.id) {
      setProjectFinancialDocs([])
      setProjectFinancialsLoading(false)
      return
    }
    let cancelled = false
    setProjectFinancialsLoading(true)
    listFinancialsByProject(projectDetailData.id, { limit: 500 })
      .then((result) => { if (!cancelled) setProjectFinancialDocs(result.items ?? []) })
      .catch(() => { if (!cancelled) setProjectFinancialDocs([]) })
      .finally(() => { if (!cancelled) setProjectFinancialsLoading(false) })
    return () => { cancelled = true }
  }, [projectDetailData?.id])
  // Fetch project addresses (mirrors deal_addresses fetch).
  useEffect(() => {
    if (!projectDetailData?.id) { setProjectAddresses([]); return }
    const pid = projectDetailData.id
    let cancelled = false
    fetch(`/api/crm/projects/${encodeURIComponent(pid)}/addresses`)
      .then(r => r.json())
      .then((list: any) => { if (!cancelled) setProjectAddresses(Array.isArray(list) ? list : []) })
      .catch(() => { if (!cancelled) setProjectAddresses([]) })
    return () => { cancelled = true }
  }, [projectDetailData?.id])
  // Fetch assignable users (owner-team-scoped) whenever the project changes.
  // Used by Owner, Supervisor, and Staff multi-select pickers.
  useEffect(() => {
    if (!projectDetailData?.id) { setPrjAssignableUsers([]); setPrjProjectCompanyId(null); setPrjProjectCompanyName(null); return }
    const pid = String(projectDetailData.id)
    let cancelled = false
    fetchSubAdminAssignableUsers(pid)
      .then((r: any) => {
        if (!cancelled) {
          setPrjAssignableUsers(Array.isArray(r?.data?.users) ? r.data.users : [])
          setPrjProjectCompanyId(r?.data?.project_company_id ?? null)
          setPrjProjectCompanyName(r?.data?.project_company_name ?? null)
        }
      })
      .catch(() => { if (!cancelled) { setPrjAssignableUsers([]); setPrjProjectCompanyId(null); setPrjProjectCompanyName(null) } })
    return () => { cancelled = true }
  }, [projectDetailData?.id])
  // Lazy-fetch phone/email for the project's direct client when the merged
  // `contacts` array from /api/crm/projects/:id is empty but client_id is set.
  // Populates the synthetic fallback row so the Contacts card shows real data.
  useEffect(() => {
    const cid = (projectDetailData as any)?.client_id
    const apiC = Array.isArray((projectDetailData as any)?.contacts) ? (projectDetailData as any).contacts : []
    if (!cid || apiC.length > 0) { setPrjLinkedClientCache(null); return }
    if (prjLinkedClientCache?.id === String(cid)) return
    fetchClient(String(cid))
      .then((c) => {
        if (!c) return
        setPrjLinkedClientCache({ id: String(cid), preferred_email: c.preferred_email ?? null, preferred_phone: c.preferred_phone ?? null })
      })
      .catch(() => setPrjLinkedClientCache(null))
  }, [(projectDetailData as any)?.client_id, (projectDetailData as any)?.contacts])
  // Pending-changes bag for every editable scalar field on the project About tab.
  // Keys map to DB column names (name, address, contract_value, services_requested,
  // start_date, end_date, progress, description). The save footer shows whenever
  // this object has any unsaved changes.
  const [prjPendingChanges, setPrjPendingChanges] = useState<Record<string, unknown>>({})
  const [prjSavingBatch, setPrjSavingBatch] = useState(false)
  // Clear staged edits whenever the user navigates to a different project record
  // or closes the modal — staged values shouldn't leak between projects.
  useEffect(() => { setPrjPendingChanges({}) }, [projectDetailData?.id])
  const [prjContactPickerOpen, setPrjContactPickerOpen] = useState(false)
  const [prjContactSearch, setPrjContactSearch] = useState('')
  const [prjContactResults, setPrjContactResults] = useState<any[]>([])
  const [prjContactSearching, setPrjContactSearching] = useState(false)
  // Project Record: Work Orders + Files
  const [prjWorkOrders, setPrjWorkOrders] = useState<any[]>([])
  const [prjWorkOrdersLoading, setPrjWorkOrdersLoading] = useState(false)
  const [prjAdminDocs, setPrjAdminDocs] = useState<any[]>([])
  const [prjFieldPre, setPrjFieldPre] = useState<any[]>([])
  const [prjFieldPost, setPrjFieldPost] = useState<any[]>([])
  const [prjFieldNotes, setPrjFieldNotes] = useState<any[]>([])
  const [prjFieldTab, setPrjFieldTab] = useState<'pre_work' | 'post_work' | 'field_notes'>('pre_work')
  const [prjWoModalOpen, setPrjWoModalOpen] = useState(false)
  const [prjWoEditing, setPrjWoEditing] = useState<any>(null) // null = create mode, object = edit mode
  const [prjWoForm, setPrjWoForm] = useState<{ title: string; description: string }>({ title: '', description: '' })
  const [prjWoFiles, setPrjWoFiles] = useState<any[]>([])
  const [prjWoSaving, setPrjWoSaving] = useState(false)
  const prjAdminDocsInputRef = useRef<HTMLInputElement | null>(null)
  const [prjDocsUploadOpen, setPrjDocsUploadOpen] = useState(false)
  const [prjDocsUploadFiles, setPrjDocsUploadFiles] = useState<File[]>([])
  const [prjDocsUploading, setPrjDocsUploading] = useState(false)
  const [prjDocsUploadProgress, setPrjDocsUploadProgress] = useState<Record<string, 'pending' | 'uploading' | 'done' | 'error'>>({})
  const [prjDocsUploadType, setPrjDocsUploadType] = useState<string | null>(null)
  useEffect(() => { if (!prjDocsUploadOpen) setPrjDocsUploadType(null) }, [prjDocsUploadOpen])
  const prjFieldInputRef = useRef<HTMLInputElement | null>(null)
  const prjWoFileInputRef = useRef<HTMLInputElement | null>(null)
  // File preview / hover / rename
  const [prjFilePreview, setPrjFilePreview] = useState<any>(null) // file object or null
  const [prjFileHover, setPrjFileHover] = useState<{ file: any; x: number; y: number } | null>(null)
  const [prjFileRenaming, setPrjFileRenaming] = useState<string | null>(null)
  const [prjFileRenameValue, setPrjFileRenameValue] = useState('')
  const [pdfThumbs, setPdfThumbs] = useState<Record<string, string>>({})
  const [docxHtml, setDocxHtml] = useState<Record<string, string>>({})
  const pdfPreviewContainerRef = useRef<HTMLDivElement | null>(null)
  // True when the in-modal PDF/docx canvas render failed (so we show the
  // download fallback card instead of a blank pane). Keyed implicitly by the
  // current prjFilePreview — reset whenever the previewed file changes.
  const [prjPreviewLoadError, setPrjPreviewLoadError] = useState(false)
  // True while the in-modal PDF/docx canvas is fetching/converting (the cold
  // docx→PDF conversion can take several seconds), so we show a "Generating
  // preview…" spinner instead of a blank pane. Cleared on first page paint/error.
  const [prjPreviewLoading, setPrjPreviewLoading] = useState(false)
  // Resolve the renderable PDF URL for a previewed project file. Native PDFs
  // serve directly from their download URL; .doc/.docx render via the
  // /api/crm/files/pdf sidecar route (crm/- and projects/-prefixed
  // HR_DOCUMENTS keys). Anything else falls back to file_url.
  const prjPreviewPdfUrl = (f: any): string => {
    if (!f) return ''
    if ((f.content_type || '') === 'application/pdf') return f.file_url
    if (f.r2_key && (f.r2_key.startsWith('crm/') || f.r2_key.startsWith('projects/'))) return crmFilePdfUrl(f.r2_key)
    return f.file_url
  }

  useEffect(() => {
    if (!prjContactPickerOpen) return
    const q = prjContactSearch.trim()
    if (!q) { setPrjContactResults([]); return }
    setPrjContactSearching(true)
    const t = setTimeout(() => {
      fetchClients(1, 15, { search: q })
        .then((r: any) => setPrjContactResults(r.clients || r.data || []))
        .catch(() => setPrjContactResults([]))
        .finally(() => setPrjContactSearching(false))
    }, 250)
    return () => clearTimeout(t)
  }, [prjContactPickerOpen, prjContactSearch])

  // Load work orders + all four file folders when the project record opens
  const reloadPrjWorkOrders = async (projId: string | number) => {
    setPrjWorkOrdersLoading(true)
    try { setPrjWorkOrders(await listProjectWorkOrders(projId)) } catch { setPrjWorkOrders([]) }
    finally { setPrjWorkOrdersLoading(false) }
  }
  const reloadPrjFiles = async (projId: string | number) => {
    try {
      const [admin, pre, post, notes] = await Promise.all([
        listProjectFiles(projId, 'admin_docs').catch(() => []),
        listProjectFiles(projId, 'pre_work').catch(() => []),
        listProjectFiles(projId, 'post_work').catch(() => []),
        listProjectFiles(projId, 'field_notes').catch(() => []),
      ])
      setPrjAdminDocs(admin); setPrjFieldPre(pre); setPrjFieldPost(post); setPrjFieldNotes(notes)
    } catch { /* swallow */ }
  }
  useEffect(() => {
    const pid = (projectDetailData as any)?.id
    if (!projectDetailOpen || !pid) {
      setPrjWorkOrders([]); setPrjAdminDocs([]); setPrjFieldPre([]); setPrjFieldPost([]); setPrjFieldNotes([])
      return
    }
    reloadPrjWorkOrders(pid)
    reloadPrjFiles(pid)
  }, [projectDetailOpen, (projectDetailData as any)?.id])


  const renderPrjFileRow = (file: any, onRefresh: () => Promise<void>) => {
    const isRenaming = prjFileRenaming === file.id
    const isWorkOrder = !!file.is_work_order
    const submitRename = async () => {
      const nextName = prjFileRenameValue.trim()
      if (!nextName || nextName === file.file_name) { setPrjFileRenaming(null); return }
      try { await renameProjectFile(projectDetailData.id, file.id, nextName); await onRefresh() }
      catch (err: any) { alert(err?.message || 'Rename failed') }
      finally { setPrjFileRenaming(null) }
    }
    return (
      <div
        key={file.id}
        onClick={() => { if (!isRenaming) setPrjFilePreview(file) }}
        onMouseEnter={e => setPrjFileHover({ file, x: e.clientX, y: e.clientY })}
        onMouseMove={e => setPrjFileHover(prev => prev && prev.file.id === file.id ? { file, x: e.clientX, y: e.clientY } : prev)}
        onMouseLeave={() => setPrjFileHover(prev => (prev && prev.file.id === file.id) ? null : prev)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 8px', border: '1px solid ' + (isWorkOrder ? '#f59e0b' : '#e2e8f0'), borderRadius: 6, background: isWorkOrder ? '#fffbeb' : '#fff', gap: 6, cursor: isRenaming ? 'text' : 'pointer' }}
      >
        {isRenaming ? (
          <input
            autoFocus
            value={prjFileRenameValue}
            onChange={e => setPrjFileRenameValue(e.target.value)}
            onClick={e => e.stopPropagation()}
            onKeyDown={e => { if (e.key === 'Enter') submitRename(); else if (e.key === 'Escape') setPrjFileRenaming(null) }}
            onBlur={submitRename}
            style={{ flex: 1, padding: '2px 6px', fontSize: '0.78rem', border: '1px solid #0891b2', borderRadius: 4, outline: 'none' }}
          />
        ) : (
          <span title={file.file_name} style={{ flex: 1, fontSize: '0.78rem', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 5 }}>
            {isWorkOrder && (
              <span title="Marked as SOW" style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: '#f59e0b', color: '#fff', fontSize: '0.6rem', fontWeight: 700, padding: '1px 5px', borderRadius: 3, textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0 }}>SOW</span>
            )}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.file_name}</span>
          </span>
        )}
        {!isRenaming && (
          <>
            <button
              title={isWorkOrder ? 'Unmark as SOW' : 'Mark as SOW (replaces any previous SOW document on this project)'}
              onClick={async e => {
                e.stopPropagation()
                try { await setProjectFileWorkOrder(projectDetailData.id, file.id, !isWorkOrder); await onRefresh() }
                catch (err: any) { alert(err?.message || 'Failed to update') }
              }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex' }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill={isWorkOrder ? '#f59e0b' : 'none'} stroke={isWorkOrder ? '#f59e0b' : '#94a3b8'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
            </button>
            <button
              title="Rename"
              onClick={e => { e.stopPropagation(); setPrjFileRenaming(file.id); setPrjFileRenameValue(file.file_name) }}
              style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: 2, display: 'flex' }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            </button>
            <button
              title="Delete"
              onClick={async e => {
                e.stopPropagation()
                if (!confirm(`Delete "${file.file_name}"?`)) return
                try { await deleteProjectFile(projectDetailData.id, file.id); await onRefresh() }
                catch (err: any) { alert(err?.message || 'Failed to delete') }
              }}
              style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: 2, fontSize: '1rem', lineHeight: 1 }}
            >&times;</button>
          </>
        )}
      </div>
    )
  }
  const [leadNotesEditing, setLeadNotesEditing] = useState(false)
  const [leadNotesValue, setLeadNotesValue] = useState('')
  const [leadActivities, setLeadActivities] = useState<CrmActivity[]>([])
  const [leadActivitiesLoading, setLeadActivitiesLoading] = useState(false)

  // Conversations state
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [conversationsData, setConversationsData] = useState<any[]>([])
  const [conversationsLoading, setConversationsLoading] = useState(false)
  const [convFilter, setConvFilter] = useState('')
  const [convSearch, setConvSearch] = useState('')
  const [convPage, setConvPage] = useState(1)
  const [convSelectedRows, setConvSelectedRows] = useState<Set<number>>(new Set())
  const [convChannelFilter, setConvChannelFilter] = useState('')
  const [convSort, setConvSort] = useState({ field: 'last_message_at' as string, order: 'DESC' as string })

  // Marketing state
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [mktgEmails, setMktgEmails] = useState<any[]>([])
  const [mktgEmailsTotal, setMktgEmailsTotal] = useState(0)
  const [mktgEmailsLoading, setMktgEmailsLoading] = useState(false)
  const [mktgEmailFilter, setMktgEmailFilter] = useState('all')
  const [mktgEmailSearch, setMktgEmailSearch] = useState('')
  const [mktgEmailTab, setMktgEmailTab] = useState<'manage' | 'analyze'>('manage')
  const [showCreateEmail, setShowCreateEmail] = useState(false)
  const [newEmailName, setNewEmailName] = useState('')
  const [newEmailSubject, setNewEmailSubject] = useState('')
  const [newEmailFrom, setNewEmailFrom] = useState('BotPros')
  const [newEmailFromAddr, setNewEmailFromAddr] = useState('noreply@botpros.ai')
  const [newEmailType, setNewEmailType] = useState('simple')
  const [newEmailBody, setNewEmailBody] = useState('')
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [mktgAnalytics, setMktgAnalytics] = useState<any>({})
  const [mktgEmailPage, setMktgEmailPage] = useState(1)
  const [mktgEmailSort, setMktgEmailSort] = useState<{ field: string; order: string }>({ field: 'updated_at', order: 'DESC' })
  const [mktgEmailSelectedRows, setMktgEmailSelectedRows] = useState<Set<number>>(new Set())
  const [mktgEmailExporting, setMktgEmailExporting] = useState(false)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [, setContactLists] = useState<any[]>([])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [adAccounts, setAdAccounts] = useState<any[]>([])
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [adCampaignsData, setAdCampaignsData] = useState<any[]>([])
  const [adCampaignsTotal, setAdCampaignsTotal] = useState(0)
  const [adsLoading, setAdsLoading] = useState(false)
  const [adsTab, setAdsTab] = useState<'manage' | 'analyze'>('manage')
  const [adsAddBtnOpen, setAdsAddBtnOpen] = useState(false)
  const adsAddBtnRef = useRef<HTMLDivElement>(null)
  const [showCreateAd, setShowCreateAd] = useState(false)
  const [newAdName, setNewAdName] = useState('')
  const [newAdNetwork, setNewAdNetwork] = useState('google')
  const [newAdBudget, setNewAdBudget] = useState('')
  const [newAdObjective, setNewAdObjective] = useState('traffic')
  const [showConnectAd, setShowConnectAd] = useState(false)
  const [newAdAcctNetwork, setNewAdAcctNetwork] = useState('google')
  const [newAdAcctName, setNewAdAcctName] = useState('')
  const [newAdAcctId, setNewAdAcctId] = useState('')
  // Ads view — full template state
  const [adsSearch, setAdsSearch] = useState('')
  const [adsSort, setAdsSort] = useState<{ field: string; order: string }>({ field: 'created_at', order: 'DESC' })
  const [adsSelectedRows, setAdsSelectedRows] = useState<Set<number>>(new Set())
  const [adsPage, setAdsPage] = useState(1)
  const [adsPlatformFilter, setAdsPlatformFilter] = useState('')
  const [adsStatusFilter, setAdsStatusFilter] = useState('')
  const [adsExporting, setAdsExporting] = useState(false)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [socialPosts, setSocialPosts] = useState<any[]>([])
  const [socialPostsTotal, setSocialPostsTotal] = useState(0)
  const [socialLoading, setSocialLoading] = useState(false)
  const [socialFilter, setSocialFilter] = useState('all')
  const [socialTab, setSocialTab] = useState<'posts' | 'calendar'>('posts')
  const [showCreatePost, setShowCreatePost] = useState(false)
  const [newPostContent, setNewPostContent] = useState('')
  const [newPostPlatforms, setNewPostPlatforms] = useState<string[]>([])
  const [newPostSchedule, setNewPostSchedule] = useState('')
  const [socialSearch, setSocialSearch] = useState('')
  const [socialSort, setSocialSort] = useState<{ field: string; order: string }>({ field: 'created_at', order: 'DESC' })
  const [socialPage, setSocialPage] = useState(1)
  const [socialSelectedRows, setSocialSelectedRows] = useState<Set<number>>(new Set())
  const [socialViewTab, setSocialViewTab] = useState('')
  const [socialPlatformFilter, setSocialPlatformFilter] = useState('')
  const [socialStatusFilter, setSocialStatusFilter] = useState('')
  const [socialExporting, setSocialExporting] = useState(false)
  const [socialPreview, setSocialPreview] = useState<any>(null)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [formsData, setFormsData] = useState<any[]>([])
  const [formsTotal, setFormsTotal] = useState(0)
  const [formsLoading, setFormsLoading] = useState(false)
  const [formsFilter, setFormsFilter] = useState('all')
  const [formsSearch, setFormsSearch] = useState('')
  const [showFormBuilder, setShowFormBuilder] = useState(false)
  const [formBuilderName, setFormBuilderName] = useState('Untitled Form')
  const [formBuilderDesc, setFormBuilderDesc] = useState('')
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [formBuilderFields, setFormBuilderFields] = useState<any[]>([
    { id: 'f1', type: 'text', label: 'First Name', placeholder: 'Enter first name', required: true, width: 'half' },
    { id: 'f2', type: 'text', label: 'Last Name', placeholder: 'Enter last name', required: true, width: 'half' },
    { id: 'f3', type: 'email', label: 'Email', placeholder: 'Enter email address', required: true, width: 'full' },
  ])
  const [formBuilderSelected, setFormBuilderSelected] = useState<string | null>(null)
  const [formThankYou, setFormThankYou] = useState('Thank you for your submission!')
  const [formNotifyEmails, setFormNotifyEmails] = useState('')
  const [formBuilderTab, setFormBuilderTab] = useState<'fields' | 'settings'>('fields')
  const [showFormSubmissions, setShowFormSubmissions] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [formSubmissionsData, setFormSubmissionsData] = useState<any[]>([])
  const [formSubmissionsTotal, setFormSubmissionsTotal] = useState(0)
  const [, setFormSubmissionsFormId] = useState<number | null>(null)
  const [editingFormId, setEditingFormId] = useState<number | null>(null)
  const [formsPage, setFormsPage] = useState(1)
  const [formsSort, setFormsSort] = useState<{ field: string; order: 'ASC' | 'DESC' }>({ field: 'created_at', order: 'DESC' })
  const [formSelectedRows, setFormSelectedRows] = useState<Set<number>>(new Set())
  const [formsSettingsOpen, setFormsSettingsOpen] = useState(false)
  const [formsColWidths, setFormsColWidths] = useState({ name: 280, submissions: 120, status: 120, type: 140, created: 150, actions: 160 })

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [landingPagesData, setLandingPagesData] = useState<any[]>([])
  const [landingPagesTotal, setLandingPagesTotal] = useState(0)
  const [landingPagesLoading, setLandingPagesLoading] = useState(false)
  const [lpFilter, setLpFilter] = useState('all')
  const [lpSearch, setLpSearch] = useState('')
  const [showCreateLP, setShowCreateLP] = useState(false)
  const [newLpName, setNewLpName] = useState('')
  const [newLpSlug, setNewLpSlug] = useState('')
  const [newLpTitle, setNewLpTitle] = useState('')
  const [newLpMeta, setNewLpMeta] = useState('')
  const [lpPage, setLpPage] = useState(1)
  const [lpSort, setLpSort] = useState<{ field: string; order: 'ASC' | 'DESC' }>({ field: 'updated_at', order: 'DESC' })
  const [lpSelectedRows, setLpSelectedRows] = useState<Set<number>>(new Set())
  const [lpSettingsOpen, setLpSettingsOpen] = useState(false)
  const [lpColWidths, setLpColWidths] = useState({ name: 280, slug: 180, status: 120, views: 100, submissions: 120, updated: 150, actions: 140 })

  // Settings state (merged into Preferences)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [settingsProperties, setSettingsProperties] = useState<any[]>([])
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [settingsTeams, setSettingsTeams] = useState<any[]>([])
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [settingsRoles, setSettingsRoles] = useState<any[]>([])
  const [_settingsAccountData, setSettingsAccountData] = useState<Record<string, string>>({})
  const [settingsLoading, setSettingsLoading] = useState(false)

  // RingCentral / Calling settings state
  const [rcMappings, setRcMappings] = useState<RcUserMapping[]>([])
  const [rcMappingsLoaded, setRcMappingsLoaded] = useState(false)
  const [rcOutcomes, setRcOutcomes] = useState<CallOutcome[]>([])
  const [rcBlockedNumbers, setRcBlockedNumbers] = useState<BlockedNumber[]>([])
  const [rcConfig, setRcConfig] = useState<Record<string, string>>({})
  const [_rcCallingLoading, _setRcCallingLoading] = useState(false)
  // Add mapping form
  const [rcMapUserId, setRcMapUserId] = useState('')
  const [rcMapExtId, setRcMapExtId] = useState('')
  const [rcMapExtNum, setRcMapExtNum] = useState('')
  const [rcMapPhone, setRcMapPhone] = useState('')
  const [_rcMapName, setRcMapName] = useState('')
  // Add outcome form
  const [rcOutcomeLabel, setRcOutcomeLabel] = useState('')
  const [rcOutcomeColor, setRcOutcomeColor] = useState('#7c98b6')
  // Block number form
  const [rcBlockPhone, setRcBlockPhone] = useState('')
  const [rcBlockReason, setRcBlockReason] = useState('')
  // RC extension typeahead
  const [rcExtensions, setRcExtensions] = useState<RcExtension[]>([])
  const [rcExtSearch, setRcExtSearch] = useState('')
  const [rcExtDropdownOpen, setRcExtDropdownOpen] = useState(false)
  const [rcExtLoading, setRcExtLoading] = useState(false)
  // RC sync inline message
  const [rcSyncMsg, setRcSyncMsg] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null)
  const [rcSyncing, setRcSyncing] = useState(false)
  const [rcBackfilling, setRcBackfilling] = useState(false)
  // Calls additional filters (Tier 2)
  const [callsOutcomeFilter, setCallsOutcomeFilter] = useState<string>('')
  const [callsHasRecording, setCallsHasRecording] = useState<boolean>(false)
  const [callsHasTranscript, setCallsHasTranscript] = useState<boolean>(false)
  // Click-to-call state (Tier 4)
  const [ringout, setRingout] = useState<{ ringoutId: string; status: string; callerStatus: string; calleeStatus: string; toNumber: string; contactName?: string } | null>(null)
  const ringoutPollRef = useRef<(() => void) | null>(null)

  // ---- Preferences: Import & Export state ----
  const [importHistory, setImportHistory] = useState<any[]>([])
  const [importHistoryLoading, setImportHistoryLoading] = useState(false)
  const [exportHistory, setExportHistory] = useState<any[]>([])
  const [exportHistoryLoading, setExportHistoryLoading] = useState(false)
  const [importWizardOpen, setImportWizardOpen] = useState(false)
  const [importWizardStep, setImportWizardStep] = useState(1)
  const [importFile, setImportFile] = useState<File | null>(null)
  const [importParsedData, setImportParsedData] = useState<Record<string, unknown>[]>([])
  const [importParsedHeaders, setImportParsedHeaders] = useState<string[]>([])
  const [importObjectType, setImportObjectType] = useState('client')
  const [importColumnMapping, setImportColumnMapping] = useState<Record<string, string>>({})
  const [importProcessing, setImportProcessing] = useState(false)
  const [importResult, setImportResult] = useState<any>(null)
  const [exportObjectType, setExportObjectType] = useState('client')
  const [exportFormat, setExportFormat] = useState('csv')
  const [exportProcessing, setExportProcessing] = useState(false)

  // ---- Preferences: Backup & Restore state ----
  const [backups, setBackups] = useState<any[]>([])
  const [backupsLoading, setBackupsLoading] = useState(false)
  const [backupProcessing, setBackupProcessing] = useState(false)
  const [restoreConfirmText, setRestoreConfirmText] = useState('')

  // ---- Preferences: Properties CRUD state ----
  const [propModalOpen, setPropModalOpen] = useState(false)
  const [propEditing, setPropEditing] = useState<any>(null)
  const [propFormName, setPropFormName] = useState('')
  const [propFormDesc, setPropFormDesc] = useState('')
  const [propFormObject, setPropFormObject] = useState('contact')
  const [propFormType, setPropFormType] = useState('text')
  const [propFormRequired, setPropFormRequired] = useState(false)
  const [propFormOptions, setPropFormOptions] = useState('')
  const [propFilterObject, setPropFilterObject] = useState('')
  const [propSearch, setPropSearch] = useState('')
  const [propSaving, setPropSaving] = useState(false)

  // === PHONE: STATE — CALLING PANEL CORE === ~60 useState/useRef atoms for the emulated softphone. Read docs/PHONE_ARCHITECTURE.md before ANY phone work. No line numbers in phone docs/banners — grep the tokens.
  // BANNER INDEX (file order): LAZY-CHUNK IMPORTS · SCALE PUBLISHER · STATE — CALLING PANEL CORE · PERSISTENCE · EFFECTS — ESC + BEFOREUNLOAD · ENGINE — RINGOUT · ENGINE — CLICK-TO-CALL ENTRY · ENGINE — DESK MIRROR POLL · ENGINE — INBOUND ANSWER/DECLINE · ENGINE — WEBPHONE INIT (SIP) ·
  // POLLERS — PANEL DATA FEEDS · ENGINE — OUTBOUND DIAL · ENGINE — ATTENDED TRANSFER · ENGINE — AUDIO SYNTH · JSX — TOPBAR PHONE BUTTON · JSX — DEVICE ROOT · JSX — IDLE SCREEN + OVERLAY STACK · JSX — APP GATE CASCADE · JSX — IN-CALL SCREEN · JSX — REVIEW SHEET · JSX — INCOMING TOAST (root)
  // Find: grep -n "=== PHONE:" src/App.tsx src/App.css ; grep -rn "TRAP(phone/" src/
  // Calling Panel state (HubSpot-style softphone widget)
  const [callingPanelOpen, setCallingPanelOpen] = useState(() => localStorage.getItem('phoneOpen') === 'true')
  const [callingState, setCallingState] = useState<'idle' | 'dialing' | 'ringing' | 'connected' | 'review'>('idle')
  const [dialerInput, setDialerInput] = useState('')
  const [dialerSearch, setDialerSearch] = useState('')
  const [dialerSearchResults, setDialerSearchResults] = useState<Array<{ id: string; title: string; subtitle: string; type: string; phone?: string }>>([])
  const [dialerSuggestExpanded, setDialerSuggestExpanded] = useState(false)
  const [_activeCallRingoutId, setActiveCallRingoutId] = useState('')
  const [activeCallTimer, setActiveCallTimer] = useState(0)
  const [activeCallContact, setActiveCallContact] = useState<{ name: string; company?: string; phone?: string; id?: string } | null>(null)
  const [callNotes, setCallNotes] = useState('')
  const [callOutcomeSelected, setCallOutcomeSelected] = useState('')
  const [callTypeSelected, setCallTypeSelected] = useState('')
  const [callMuted, setCallMuted] = useState(false)
  const [showDialpad, setShowDialpad] = useState(false)
  const [callOnHold, setCallOnHold] = useState(false)
  const [showTransferPicker, setShowTransferPicker] = useState(false)
  // ATTENDED (warm/consultative) transfer — PHONE_ARCHITECTURE §6.6 +
  // TRAP(phone/attended-transfer). transferMode picks the affordance inside the
  // Transfer panel: 'attended' holds the caller and dials the target FIRST so the
  // agent can talk; 'blind' REFERs immediately (the original behavior). The held
  // caller stays on the ORIGINAL session (activeSessionRef); the consult call is a
  // SECOND simultaneous CallSession (consultSessionRef). consultState drives the
  // sub-flow; consultTarget is who we're consulting; consultError surfaces failures.
  const [transferMode, setTransferMode] = useState<'attended' | 'blind'>('attended')
  const [transferNumberInput, setTransferNumberInput] = useState('')
  const [consultState, setConsultState] = useState<'idle' | 'dialing' | 'ringing' | 'connected' | 'completing' | 'failed'>('idle')
  const [consultTarget, setConsultTarget] = useState<{ name: string; number: string } | null>(null)
  const [consultError, setConsultError] = useState<string | null>(null)
  // Mid-call notes panel (writes to callNotes — shared with the review sheet).
  const [showInCallNotes, setShowInCallNotes] = useState(false)
  const [showTranscript, setShowTranscript] = useState(false)
  // Incoming-call screen: Forward (transfer the ringing call) + Reply (text back) pickers.
  const [showIncomingForward, setShowIncomingForward] = useState(false)
  const [showIncomingReply, setShowIncomingReply] = useState(false)
  const [callingPanelTab, setCallingPanelTab] = useState<'favorites' | 'recents' | 'contacts' | 'keypad' | 'voicemail' | 'sms'>(() => (localStorage.getItem('phoneTab') as any) || 'keypad')
  const [recentsFilter, setRecentsFilter] = useState<'all' | 'missed'>('all')
  const [voicemails, setVoicemails] = useState<Voicemail[]>([])
  const [voicemailsLoading, setVoicemailsLoading] = useState(false)
  const [voicemailPlayingId, setVoicemailPlayingId] = useState<string | null>(null)
  const [voicemailExpandedId, setVoicemailExpandedId] = useState<string | null>(null)
  const voicemailAudioRef = useRef<HTMLAudioElement | null>(null)
  const [phoneScreenMode, setPhoneScreenMode] = useState<'home' | 'app'>(() => {
    const saved = localStorage.getItem('phoneScreen')
    return saved === 'app' ? 'app' : 'home'
  })
  // The phone is always docked. Floating/undocked mode was retired 2026-05-01;
  // setPhoneDocked is retained as a no-op-ish setter for code paths that still
  // call it (drag-to-undock, etc. — those paths are now neutralized).
  const [phoneDocked, setPhoneDocked] = useState(true)
  const [phoneIdle, setPhoneIdle] = useState(false)
  const phoneIdleTimer = React.useRef<ReturnType<typeof setTimeout>|null>(null)
  const resetPhoneIdle = React.useCallback(() => {
    setPhoneIdle(false)
    if (phoneIdleTimer.current) clearTimeout(phoneIdleTimer.current)
    phoneIdleTimer.current = setTimeout(() => setPhoneIdle(true), 60000) // 60s idle → dim
  }, [])
  React.useEffect(() => { resetPhoneIdle(); return () => { if (phoneIdleTimer.current) clearTimeout(phoneIdleTimer.current) } }, [])
  const [phoneDockHover, setPhoneDockHover] = useState(false)
  const [phoneDragging, setPhoneDragging] = useState(false)
  const [phoneUndocking, setPhoneUndocking] = useState(false)
  const [phoneContactsSearch, setPhoneContactsSearch] = useState('')
  const [phoneContactsList, setPhoneContactsList] = useState<Array<{ id: string; name: string; phone: string | null; city: string | null }>>([])
  const [phoneContactsLoading, setPhoneContactsLoading] = useState(false)
  const phoneContactsDebounce = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [iphoneNotif, setIphoneNotif] = useState<{ name: string; message: string; time: string; type?: 'call' | 'sms' } | null>(null)
  const iphoneNotifTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastSmsCountRef = useRef(0)
  const phoneTouchRef = useRef<HTMLDivElement>(null)
  const [phoneTouchVisible, setPhoneTouchVisible] = useState(false)
  const [phoneTouchPressed, setPhoneTouchPressed] = useState(false)
  const [smsConversations, setSmsConversations] = useState<SmsConversation[]>([])
  const [smsActiveThread, setSmsActiveThread] = useState<number | null>(() => {
    const saved = Number(localStorage.getItem('phoneSmsActiveThread') || 0)
    return Number.isFinite(saved) && saved > 0 ? saved : null
  })
  const [smsMessages, setSmsMessages] = useState<SmsMessage[]>([])
  const [smsCompose, setSmsCompose] = useState('')
  const [smsSending, setSmsSending] = useState(false)
  const [smsLoading, setSmsLoading] = useState(false)
  const [smsTotalUnread, setSmsTotalUnread] = useState(0)
  const [smsNewMode, setSmsNewMode] = useState(() => localStorage.getItem('phoneSmsNewMode') === 'true')
  const [smsNewTo, setSmsNewTo] = useState('')
  const [smsContactPicker, setSmsContactPicker] = useState(false)
  const [smsContactSearch, setSmsContactSearch] = useState('')
  const [smsToResults, setSmsToResults] = useState<Array<{ id: string; name: string; phone: string }>>([])
  const [smsSelectedContactName, setSmsSelectedContactName] = useState('')
  const [smsSearchQuery, _setSmsSearchQuery] = useState('')
  const [callSaved, setCallSaved] = useState(false)
  const [reviewTab, setReviewTab] = useState<'notes' | 'transcript'>('notes')
  const [callTranscriptText, setCallTranscriptText] = useState('')
  const [callTranscriptSummary, setCallTranscriptSummary] = useState('')
  const [callTranscriptSentiment, setCallTranscriptSentiment] = useState('')
  const [callTranscriptLoading, setCallTranscriptLoading] = useState(false)
  const [callingPanelMinimized, setCallingPanelMinimized] = useState(false)
  const [callingSettingsOpen, setCallingSettingsOpen] = useState(false)
  const [callingSettingsPos, setCallingSettingsPos] = useState<{ top: number; left: number } | null>(null)
  const [callingSettingsView, setCallingSettingsView] = useState<'main' | 'microphone' | 'speaker'>('main')
  const [audioDevices, setAudioDevices] = useState<{ inputs: MediaDeviceInfo[]; outputs: MediaDeviceInfo[] }>({ inputs: [], outputs: [] })
  const [selectedMic, setSelectedMic] = useState('default')
  const [selectedSpeaker, setSelectedSpeaker] = useState('default')
  const callingPanelRef = useRef<HTMLDivElement>(null)
  const [callingPanelPos, setCallingPanelPos] = useState<{ x: number; y: number } | null>(() => {
    try { const s = localStorage.getItem('phonePos'); return s ? JSON.parse(s) : null } catch { return null }
  })
  const callingDragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null)
  // Phone favorites (starred contacts) — loaded lazily when the panel opens
  const [phoneFavorites, setPhoneFavorites] = useState<PhoneFavorite[]>([])
  const [phoneFavoritesLoading, setPhoneFavoritesLoading] = useState(false)
  const [phoneFavoriteDragId, setPhoneFavoriteDragId] = useState<number | null>(null)
  // Ref to the (stable) reloadPhoneFavorites callback so effects defined earlier
  // in the function body can call it without hitting TDZ on hoisting.
  const reloadPhoneFavoritesRef = useRef<(() => Promise<void>) | null>(null)
  // Live transcription capability + running transcript lines
  const [phoneTranscriptCap, setPhoneTranscriptCap] = useState<{ enabled: boolean; note: string } | null>(null)
  const [phoneTranscript, setPhoneTranscript] = useState<Array<{ channel: number; text: string; isFinal: boolean; ts: number }>>([])
  const phoneTranscriptRef = useRef<HTMLDivElement>(null)
  const liveTranscriberRef = useRef<LiveTranscriber | null>(null)
  // Transparent transcript overlay: per-user opacity, persisted.
  const [transcriptOpacity, setTranscriptOpacity] = useState<number>(() => { const v = parseFloat(localStorage.getItem('phone.transcriptOverlay.opacity') || ''); return Number.isFinite(v) ? v : 0.9 })
  // Draggable overlay position (px from top-left). null = default CSS anchor.
  const [transcriptPos, setTranscriptPos] = useState<{ x: number; y: number } | null>(() => { try { const v = localStorage.getItem('phone.transcriptOverlay.pos'); return v ? JSON.parse(v) : null } catch { return null } })
  const transcriptDragRef = useRef<{ dx: number; dy: number } | null>(null)
  // === PHONE: post-call wrap-up assignment (call-assign-link-v1) ===
  // Staged deal/project chip for the review sheet. Persisted ONLY on Done
  // (saveCallReview); dismissing the sheet discards it. The picker searches
  // DEALS only — projects appear nested under matching deals (buildAssignRows,
  // src/lib/phone/assignSearch.ts). All of these reset on entering review AND in
  // closeCallingPanel. Owner-write invariant: ingest never rewrites an existing
  // row's owner; a manual wrap-up assignment is authoritative (last-write-wins).
  const [callAssignSelection, setCallAssignSelection] = useState<CallAssignSelection>(null)
  // The hydrated initial chip value (dial-time linkage) — saveCallReview only
  // sends owner fields when the staged chip DIFFERS from this.
  const callAssignInitialRef = useRef<CallAssignSelection>(null)
  const [assignPickerOpen, setAssignPickerOpen] = useState(false)
  // Pre-seeded suggested deals for this call's contact (empty-query state).
  // The picker's own search/probe/keyboard state now lives inside the shared
  // CallAssignPicker (src/shared/CallAssignPicker.tsx); the parent keeps only the
  // staged selection + open flag + suggested seed + the review-sheet error.
  const [assignSuggestedDeals, setAssignSuggestedDeals] = useState<CrmDeal[]>([])
  const [assignSuggestName, setAssignSuggestName] = useState('')
  const assignSuggestedFetchedRef = useRef(false)
  // Inline "Couldn't save assignment" error surfaced in the review sheet.
  const [assignSaveError, setAssignSaveError] = useState<string | null>(null)
  const assignTriggerRef = useRef<HTMLButtonElement>(null)
  // Incoming-call UI state — derived from inboundCallSession + iphoneNotif caller info
  const [incomingCallInfo, setIncomingCallInfo] = useState<{
    name: string
    number: string
    photoUrl?: string | null
    entityKind?: 'contact' | 'lead' | 'deal' | null
    entityId?: string | null
    entityLabel?: string | null
  } | null>(null)
  const incomingRingtoneRef = useRef<HTMLAudioElement | null>(null)
  const callingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  // Stashed by handleClickToCall so the WebRTC call-start activity row can
  // be linked back to the originating lead / deal / contact. Cleared after
  // the call begins (consumed once).
  const pendingCallContextRef = useRef<{ leadId?: number; dealId?: number; contactId?: string } | null>(null)
  // Id of the activity row created by /call-start so we can patch its
  // duration_seconds when the WebRTC session ends.
  const currentCallActivityIdRef = useRef<number | null>(null)
  // Holds the most recently ended call's activity id so the post-call
  // review (description/outcome/type) folds back into the SAME row
  // instead of creating a duplicate note activity.
  const reviewableCallActivityIdRef = useRef<number | null>(null)
  // Timestamp (ms) when the call was actually picked up — used to compute
  // talk duration on hangup since activeCallTimer state may be stale inside
  // the event handler closure.
  const callConnectedAtRef = useRef<number | null>(null)
  // Timestamp (ms) when the call was dialed. Used as a fallback when the
  // call is never answered, so the row still shows total session time
  // (ring + hangup) instead of a blank duration.
  const callDialedAtRef = useRef<number | null>(null)
  const callingPollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const dialerSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // WebRTC Web Phone state
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const webPhoneRef = useRef<any>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const activeSessionRef = useRef<any>(null)
  // The consult leg of an ATTENDED transfer — a SECOND simultaneous CallSession
  // (PHONE_ARCHITECTURE §6.6). Null whenever no warm transfer is in flight. The
  // held caller stays on activeSessionRef; completeWarmTransfer REFERs that caller
  // into THIS dialog (SIP REFER w/ Replaces) so caller + target stay connected.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const consultSessionRef = useRef<any>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [inboundCallSession, setInboundCallSession] = useState<any>(null)
  const [_webPhoneReady, setWebPhoneReady] = useState(false)
  const [webPhoneError, setWebPhoneError] = useState<string | null>(null)
  const webPhoneInitingRef = useRef(false)
  const remoteAudioRef = useRef<HTMLAudioElement>(null)
  // Pending / unassigned calls (surfaced inside the notification-center "Calls" tab)
  const [pendingAssignments, setPendingAssignments] = useState<PendingCallAssignment[]>([])
  // How many unassigned-call GROUPS to render in the Calls tab (cap + "show more" so the
  // dropdown stays responsive with hundreds of pending items).
  const [pendingShowLimit, setPendingShowLimit] = useState(30)
  // Which caller-groups are expanded (set of group keys) to reveal their individual calls.
  const [pendingExpanded, setPendingExpanded] = useState<Set<string>>(new Set())
  // User management state
  const [userMgmtMode, setUserMgmtMode] = useState<'list' | 'add' | 'edit'>('list')
  const [usersStatusDropdown, setUsersStatusDropdown] = useState(false)
  const [usersStatusFilter, setUsersStatusFilter] = useState<Set<string>>(new Set(['invite_accepted', 'invite_pending', 'invite_bounced']))
  const [usersLastActiveDropdown, setUsersLastActiveDropdown] = useState(false)
  const [usersLastActiveFilter, setUsersLastActiveFilter] = useState('')
  const [userMgmtTarget, setUserMgmtTarget] = useState<CrmUser | null>(null)
  const [userMgmtForm, setUserMgmtForm] = useState({ name: '', email: '', initials: '', role: 'Estimator' })
  const [userMgmtSaving, setUserMgmtSaving] = useState(false)
  const [userMgmtError, setUserMgmtError] = useState('')

  // Notification bell
  const [notifCount, setNotifCount] = useState(0)
  const [incomingCallToast, setIncomingCallToast] = useState<{ title: string; body: string; linkUrl: string; phone?: string } | null>(null)
  const lastNotifIdRef = useRef(0)
  const notifInitializedRef = useRef(false)
  const [notifOpen, setNotifOpen] = useState(false)
  const [kbOpen, setKbOpen] = useState(false)
  const [kbAdminRoute, setKbAdminRoute] = useState(() => window.location.hash === '#/admin/knowledge-base' || window.location.hash.startsWith('#/admin/knowledge-base?'))
  useEffect(() => {
    const onHash = () => setKbAdminRoute(window.location.hash === '#/admin/knowledge-base' || window.location.hash.startsWith('#/admin/knowledge-base?'))
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])
  const [notifList, setNotifList] = useState<CrmNotification[]>([])
  const [notifFilter, setNotifFilter] = useState<'all' | 'unread' | 'call' | 'email' | 'mention'>('all')
  const [notifMarkingAll, setNotifMarkingAll] = useState(false)
  // Email tab — actual UNREAD inbox emails (Gmail), NOT the `notifications`
  // table. The bell's Email tab used to filter the notifications table for titles
  // containing 'email' (none are ever created → always empty). Instead we read the
  // same source the Inbox view uses: GET /api/crm/email/threads?folder=inbox.
  // Fetched lazily only when the Email tab is the active filter AND the dropdown is
  // open. Unread COUNT comes from GET /api/crm/email/folders (Inbox unreadCount).
  const [notifEmailThreads, setNotifEmailThreads] = useState<InboxThreadSummary[] | null>(null)
  const [notifEmailLoading, setNotifEmailLoading] = useState(false)
  const [notifEmailError, setNotifEmailError] = useState<string | null>(null)
  const [notifEmailUnreadCount, setNotifEmailUnreadCount] = useState<number | null>(null)
  const notifEmailAbortRef = useRef<AbortController | null>(null)
  const notifRef = useRef<HTMLDivElement>(null)
  // The dropdown is portaled to <body> so it can stack above the docked phone
  // (a root-level fixed element at --phone-z that otherwise wins over the
  // toolbar's z-index:100 stacking context). notifPos anchors the fixed popover
  // under the bell; notifPopRef lets the outside-click handler treat clicks
  // inside the portaled panel as "inside".
  const notifPopRef = useRef<HTMLDivElement>(null)
  const [notifPos, setNotifPos] = useState<{ top: number; right: number } | null>(null)

  // Eager-load UNREAD inbox emails for the bell's Email tab. Fires once when the
  // dropdown OPENS (not gated on the Email tab being active) so the Email tab —
  // and its "N Email" count chip — are already populated the instant the user
  // switches to it; no extra click-to-load. We still only hit Graph while the
  // dropdown is open, and only fetch once per open (the effect re-runs solely on
  // notifOpen flipping true). Reuses the exact endpoint the Inbox view uses
  // (GET /api/crm/email/threads?folder=inbox) plus /api/crm/email/folders for
  // the authoritative unread count. AbortController cancels an in-flight fetch
  // if the dropdown closes, and guards against overlapping fetches on rapid toggles.
  useEffect(() => {
    if (!notifOpen) return
    const ac = new AbortController()
    notifEmailAbortRef.current?.abort()
    notifEmailAbortRef.current = ac
    setNotifEmailLoading(true)
    setNotifEmailError(null)
    // Threads (newest first) → keep only unread, cap at 25 for the dropdown.
    const threadsP = fetch('/api/crm/email/threads?folder=inbox', { credentials: 'include', signal: ac.signal })
      .then(r => r.ok ? r.json() : r.json().then((d: any) => Promise.reject(new Error(d?.error || `HTTP ${r.status}`))))
      .then((data: any) => {
        const list: InboxThreadSummary[] = Array.isArray(data?.threads) ? data.threads : []
        return list.filter(t => !t.isRead).slice(0, 25)
      })
    // Folders → Inbox unreadCount (message-level, authoritative for the chip).
    const countP = fetch('/api/crm/email/folders', { credentials: 'include', signal: ac.signal })
      .then(r => r.ok ? r.json() : null)
      .then((data: any) => {
        const inbox = data?.folders?.personal?.find((f: any) => f.key === 'inbox')
        return typeof inbox?.unreadCount === 'number' ? inbox.unreadCount : null
      })
      .catch(() => null)
    Promise.all([threadsP, countP])
      .then(([threads, count]) => {
        if (ac.signal.aborted) return
        setNotifEmailThreads(threads)
        // Prefer the folder count; fall back to the fetched unread count.
        setNotifEmailUnreadCount(count != null ? count : threads.length)
      })
      .catch((e: any) => {
        if (ac.signal.aborted || e?.name === 'AbortError') return
        setNotifEmailError(e?.message || 'Failed to load unread email')
        setNotifEmailThreads([])
      })
      .finally(() => { if (!ac.signal.aborted) setNotifEmailLoading(false) })
    return () => ac.abort()
  }, [notifOpen])

  // Note modal
  const [showNoteModal, setShowNoteModal] = useState<{ ownerTypeId: number; ownerId: number } | null>(null)
  const [noteText, setNoteText] = useState('')
  const [noteSaving, setNoteSaving] = useState(false)

  // Preferences tab
  const [prefsTab, setPrefsTabRaw] = useState<PrefsTab>(() => {
    const seg = getHashSegments()
    if (seg[0] === 'preferences' && seg[1]) return resolvePrefsTab(seg[1])
    return 'general'
  })
  const [prefsSearch, setPrefsSearch] = useState('')
  const [prefsExpandedSections, setPrefsExpandedSections] = useState<Set<string>>(new Set())
  // Narrow-screen (≤920px) Settings shows ONE panel at a time: the nav menu
  // first, then the selected tab's content full-width with a back button.
  // Has no effect on desktop — the .prefs-mobile-main class only matters
  // inside the stacked-layout media query in App.css.
  const [prefsMobileShowMain, setPrefsMobileShowMain] = useState(false)
  const [generalSubTab, setGeneralSubTabRaw] = useState<GeneralSubTab>(() => {
    const seg = getHashSegments()
    if (seg[0] === 'preferences' && seg[1] === 'general' && seg[2]) {
      const valid: GeneralSubTab[] = ['profile', 'email', 'calling', 'calendar', 'tasks', 'security', 'automation']
      if (valid.includes(seg[2] as GeneralSubTab)) return seg[2] as GeneralSubTab
    }
    return 'profile'
  })
  const [notifSubTab, setNotifSubTabRaw] = useState<NotifSubTab>(() => {
    const seg = getHashSegments()
    if (seg[0] === 'preferences' && seg[1] === 'notifications' && seg[2]) {
      const valid: NotifSubTab[] = ['email_desktop', 'other_apps', 'mobile']
      if (valid.includes(seg[2] as NotifSubTab)) return seg[2] as NotifSubTab
    }
    return 'email_desktop'
  })
  const [acctDefaultsSubTab, setAcctDefaultsSubTabRaw] = useState<AcctDefaultsSubTab>(() => {
    const seg = getHashSegments()
    if (seg[0] === 'preferences' && seg[1] === 'account' && seg[2]) {
      const valid: AcctDefaultsSubTab[] = ['general', 'user_defaults', 'notification_profiles']
      if (valid.includes(seg[2] as AcctDefaultsSubTab)) return seg[2] as AcctDefaultsSubTab
    }
    return 'general'
  })
  const [acctDefaults, setAcctDefaults] = useState<Record<string, string>>(() => {
    try { return JSON.parse(localStorage.getItem('acctDefaults') || '{}') } catch { return {} }
  })
  // Notification state: notifGlobalToggles, notifTopicPrefs, notifExpandedGroups, and
  // notifTopicSearch previously backed the per-channel + per-topic matrix on
  // Preferences > Notifications > Email & Desktop. That matrix was a placebo (localStorage
  // only, no consumer) and has been replaced with a "Coming soon" card, so those states
  // were removed. Real routing lives on users.notifications_channel (see emailNotifChannel).
  const [prefsDirtyCount, setPrefsDirtyCount] = useState(0)
  const [prefsSaving, setPrefsSaving] = useState(false)
  // Email notification channel (email / none)
  const [emailNotifChannel, setEmailNotifChannel] = useState<EmailNotifChannel>(() => {
    const stored = localStorage.getItem('emailNotifChannel')
    if (stored === 'email' || stored === 'none') return stored
    const legacy = localStorage.getItem('teamsNotifChannel')
    if (legacy !== null) {
      const migrated: EmailNotifChannel = legacy === 'teams' || legacy === 'both' || legacy === 'email'
        ? 'email'
        : legacy === 'none' ? 'none' : 'email'
      localStorage.setItem('emailNotifChannel', migrated)
      localStorage.removeItem('teamsNotifChannel')
      return migrated
    }
    return 'email'
  })
  const emailNotifBaselineRef = useRef<EmailNotifChannel>(emailNotifChannel)
  // "New email -> bell" scope (off / matched / all). Saved via the same
  // prefs dirty/save/cancel machinery as emailNotifChannel below.
  const [emailNotifyScope, setEmailNotifyScope] = useState<EmailNotifyScope>(() => {
    const stored = localStorage.getItem('emailNotifyScope')
    if (stored === 'off' || stored === 'matched' || stored === 'all') return stored
    return 'matched'
  })
  const emailNotifyScopeBaselineRef = useRef<EmailNotifyScope>(emailNotifyScope)
  const [notifPrefsDirtyCount, setNotifPrefsDirtyCountRaw] = useState(0)
  const notifPrefsDirtyCountRef = useRef(0)
  const setNotifPrefsDirtyCount = (count: number) => {
    notifPrefsDirtyCountRef.current = count
    setNotifPrefsDirtyCountRaw(count)
  }
  const [emailNotifSaving, setEmailNotifSaving] = useState(false)
  const [emailNotifSaveError, setEmailNotifSaveError] = useState<string | null>(null)
  const [emailNotifTestStatus, setEmailNotifTestStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
  const [emailNotifTestError, setEmailNotifTestError] = useState<string | null>(null)
  const [prefsUnsavedBlocked, setPrefsUnsavedBlocked] = useState(false)
  const [prefsUnsavedNudge, setPrefsUnsavedNudge] = useState(false)
  const prefsUnsavedNudgeTimerRef = useRef<number | null>(null)
  const notificationPrefsSafeHashRef = useRef(window.location.hash || '#/preferences/notifications/email_desktop')
  useEffect(() => {
    // Pull the authoritative value from /api/me on mount so we don't
    // drift from the server if the user changed it in another session.
    let cancelled = false
    fetch('/api/me', { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(me => {
        if (cancelled || !me) return
        const ch = String(me.notificationsChannel || 'email').toLowerCase()
        if (ch === 'teams' || ch === 'email' || ch === 'both' || ch === 'none') {
          const nextChannel: EmailNotifChannel = (ch === 'teams' || ch === 'both') ? 'email' : (ch === 'none' ? 'none' : 'email')
          if (notifPrefsDirtyCountRef.current === 0) {
            emailNotifBaselineRef.current = nextChannel
            setEmailNotifChannel(nextChannel)
            localStorage.setItem('emailNotifChannel', nextChannel)
          }
        }
        const sc = String(me.emailNotifyScope || 'matched').toLowerCase()
        if (sc === 'off' || sc === 'matched' || sc === 'all') {
          const nextScope = sc as EmailNotifyScope
          if (notifPrefsDirtyCountRef.current === 0) {
            emailNotifyScopeBaselineRef.current = nextScope
            setEmailNotifyScope(nextScope)
            localStorage.setItem('emailNotifyScope', nextScope)
          }
        }
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])
  useEffect(() => {
    return () => {
      if (prefsUnsavedNudgeTimerRef.current) window.clearTimeout(prefsUnsavedNudgeTimerRef.current)
    }
  }, [])
  // Audit Log state — passed through to PrefsAuditLog/PrefsAuditLogHeader.
  const [auditLogTab, setAuditLogTab] = useState<'all' | 'login' | 'security'>('all')
  const [auditCategoryFilter, setAuditCategoryFilter] = useState('all')
  const [auditDateFilter, setAuditDateFilter] = useState('30d')
  const [auditPage, setAuditPage] = useState(1)
  const [prefsPhoto, setPrefsPhoto] = useState<string | null>(() => localStorage.getItem('prefsPhoto'))
  const prefsPhotoRef = useRef<HTMLInputElement>(null)
  const prefsPhotoBaselineRef = useRef<string | null>(localStorage.getItem('prefsPhoto'))
  const prefsPhotoSyncStartedRef = useRef(false)
  const [prefsPhotoUploading, setPrefsPhotoUploading] = useState(false)
  const [prefsPhotoError, setPrefsPhotoError] = useState<string | null>(null)
  // Profile photo cache is browser-global localStorage, but a single PC is shared
  // by multiple employees (they sign in/out of the same browser). Tag the cached
  // photo with its owner's email so a stale photo from the PREVIOUS user is never
  // shown as the CURRENT user (Leo seeing Max's face, 2026-06-12). persistPrefsPhoto
  // is the ONLY writer — it keeps prefsPhoto + prefsPhotoOwner in lockstep.
  const persistPrefsPhoto = (url: string | null, ownerEmail: string | null | undefined) => {
    try {
      if (url) {
        localStorage.setItem('prefsPhoto', url)
        if (ownerEmail) localStorage.setItem('prefsPhotoOwner', ownerEmail.toLowerCase())
      } else {
        localStorage.removeItem('prefsPhoto')
        localStorage.removeItem('prefsPhotoOwner')
      }
    } catch { /* ignore quota / disabled storage */ }
  }
  // Just-picked Preferences profile photo awaiting crop/zoom (PhotoCropModal)
  const [prefsCropFile, setPrefsCropFile] = useState<File | null>(null)
  // CF Access session died mid-session (fetch patch detected the 401 burst
  // and confirmed via /api/me) — blocks the UI with a sign-back-in overlay.
  const [sessionExpired, setSessionExpired] = useState(false)
  useEffect(() => {
    const onExpired = () => setSessionExpired(true)
    window.addEventListener('har:session-expired', onExpired)
    return () => window.removeEventListener('har:session-expired', onExpired)
  }, [])
  const [prefsBaseline] = useState(() => {
    const defaults: Record<string, string> = {
      firstName: 'Max', lastName: 'Ritchie', phone: '',
      language: 'en', dateFormat: 'MM/DD/YYYY', timeFormat: '12h', numberFormat: '1,234.56',
      homePage: 'home', pipeline: 'estimation', workDays: 'monday', workStart: '08:00', workEnd: '17:00',
      emailUnsubLink: '0',
      callingRingPref: 'browser', callingTranscriptLang: 'en-US',
      calendarSync: '0', calendarScheduling: '0', meetingsUrl: '', defaultMeetingLink: '',
      taskDefaultDueDate: 'none', taskDefaultDueTime: 'none', taskDefaultReminder: 'none', taskFollowUpCall: '1', taskFollowUpEmail: '0',
      autoMinimap: '1', autoZoomLevel: '100',
    }
    try { return { ...defaults, ...JSON.parse(localStorage.getItem('prefsSettings') || '{}') } }
    catch { return defaults }
  })
  const prefsBaselineRef = useRef(prefsBaseline)
  const [prefsValues, setPrefsValues] = useState({ ...prefsBaseline })

  // HR Profile state
  const [hrProfile, setHrProfile] = useState<HrProfile | null>(null)
  const [hrProfileBaseline, setHrProfileBaseline] = useState<HrProfile | null>(null)
  const [hrProfileDirty, setHrProfileDirty] = useState(0)
  const [hrProfileLoading, setHrProfileLoading] = useState(false)
  const [hrProfileSaving, setHrProfileSaving] = useState(false)
  const [hrCollapsed, setHrCollapsed] = useState<Set<string>>(new Set(['personal','emergency','employment','tax','banking','education','dependents','documents','immigration']))
  // HR Center Portal state (replaces old hrSubView system)
  const [hrPortalView, setHrPortalView] = useState<'dashboard'|'templates'|'forms'|'assignments'|'pipelines'|'employees'|'settings'|'applications'|'profiles'|'employer-signing'|'onboarding'|'change-requests'|'timesheets'>('dashboard')
  // Forms builder — active form key (so the sub-tab survives nav / deep-link).
  const [hrPortalFormKey, setHrPortalFormKey] = useState<string|null>(null)
  // Pending change-request queue for the HR admin Change Requests sub-tab.
  const [hrChangeRequests, setHrChangeRequests] = useState<any[]>([])
  const [hrChangeRequestsLoading, setHrChangeRequestsLoading] = useState(false)
  const [hrChangeRequestsError, setHrChangeRequestsError] = useState<string | null>(null)
  const [hrChangeRequestsTab, setHrChangeRequestsTab] = useState<'pending' | 'approved' | 'denied' | 'cancelled'>('pending')
  const [hrChangeRequestActing, setHrChangeRequestActing] = useState<number | null>(null)
  const [hrDenyModal, setHrDenyModal] = useState<{ id: number; employeeName: string; field: string; requestedValue: string } | null>(null)
  const [hrDenyNote, setHrDenyNote] = useState('')
  const loadHrChangeRequests = React.useCallback(async (status: 'pending' | 'approved' | 'denied' | 'cancelled') => {
    setHrChangeRequestsLoading(true)
    setHrChangeRequestsError(null)
    try {
      const r = await fetch(`/api/hr-admin/change-requests?status=${encodeURIComponent(status)}`, { credentials: 'include' })
      if (!r.ok) {
        const j = await r.json().catch(() => ({} as any))
        throw new Error(j.error || `HTTP ${r.status}`)
      }
      const j = await r.json() as { requests: any[] }
      setHrChangeRequests(j.requests || [])
    } catch (e: any) {
      setHrChangeRequestsError(e?.message || 'Failed to load')
    } finally {
      setHrChangeRequestsLoading(false)
    }
  }, [])
  useEffect(() => {
    if (activeView === 'hr-center' && hrPortalView === 'change-requests') {
      void loadHrChangeRequests(hrChangeRequestsTab)
    }
  }, [activeView, hrPortalView, hrChangeRequestsTab, loadHrChangeRequests])
  const [hrPortalEmployees, setHrPortalEmployees] = useState<any[]>([])
  const [hrPortalEmployeesTotal, setHrPortalEmployeesTotal] = useState(0)
  const [hrPortalEmployeesLoading, setHrPortalEmployeesLoading] = useState(false)
  const [hrPortalSearch, setHrPortalSearch] = useState('')
  const [hrPortalDeptFilter, setHrPortalDeptFilter] = useState('2013')
  const [hrPortalStatusFilter, setHrPortalStatusFilter] = useState('')
  const [hrPortalPage, setHrPortalPage] = useState(1)
  const [hrPortalSelectedId, setHrPortalSelectedId] = useState<string|null>(null)
  const [hrPortalDetail, setHrPortalDetail] = useState<any>(null)
  const [hrPortalDetailLoading, setHrPortalDetailLoading] = useState(false)
  const [hrPortalDetailTab, setHrPortalDetailTab] = useState<string>('personal')
  const [hrPortalFieldSearch, setHrPortalFieldSearch] = useState('')
  const [hrPortalSaving, setHrPortalSaving] = useState<string|null>(null)
  const [hrPortalEditingField, setHrPortalEditingField] = useState<{key:string;draft:string}|null>(null)
  const [hrPortalAlerts, setHrPortalAlerts] = useState<any[]>([])
  const [hrPortalDocs, setHrPortalDocs] = useState<{assignments: any[], fileFields: any[]}>({assignments:[], fileFields:[]})
  const [hrDocUploadModal, setHrDocUploadModal] = useState(false)
  const [hrDocUploadFile, setHrDocUploadFile] = useState<File | null>(null)
  const [hrDocUploadType, setHrDocUploadType] = useState('')
  const [hrDocUploadCustom, setHrDocUploadCustom] = useState('')
  const [hrDocUploading, setHrDocUploading] = useState(false)
  // Integrations: activity rollup + RC-extension lazy cache + HR profile drawer
  const [hrPortalActivityRollup, setHrPortalActivityRollup] = useState<ActivityRollup | null>(null)
  const [hrPortalActivityLoading, setHrPortalActivityLoading] = useState(false)
  const hrPortalRcExtCacheRef = React.useRef<Map<string, HrEmployeeRcExtension>>(new Map())
  const [hrPortalRcExtMap, setHrPortalRcExtMap] = useState<Record<string, HrEmployeeRcExtension>>({})
  const hrProfileDrawerCacheRef = React.useRef<Map<string, UserHrProfile>>(new Map())
  const [hrProfileDrawer, setHrProfileDrawer] = useState<{ userId: number | string; userName?: string; loading: boolean; profile: UserHrProfile; notFound: boolean } | null>(null)
  // Calls deep-link linked-context lazy cache (by activity id)
  const callLinkedContextCacheRef = React.useRef<Map<number, LinkedContext>>(new Map())
  const [callLinkedContextMap, setCallLinkedContextMap] = useState<Record<number, LinkedContext>>({})
  // IT Provisioning UI state
  const [itEmailCheck, setItEmailCheck] = useState<{prefix:string; available:boolean; candidate?:string}|null>(null)
  const [itEmailChecking, setItEmailChecking] = useState(false)
  const [itSending, setItSending] = useState(false)
  const [itToast, setItToast] = useState<{kind:'success'|'error'; msg:string}|null>(null)
  const [itProvModal, setItProvModal] = useState<{profileId:string; firstName:string; stageId:string}|null>(null)
  const [itProvForm, setItProvForm] = useState<{needsEmail:boolean; emailPrefix:string; tablet:boolean; tabletType:string; phone:boolean; phoneType:string; laptop:boolean}>({ needsEmail: true, emailPrefix: '', tablet: false, tabletType: 'iPad', phone: false, phoneType: 'iPhone', laptop: false })
  // Pipelines/Kanban state
  const [hrPortalPipelines, setHrPortalPipelines] = useState<any[]>([])
  const [hrPortalStages, setHrPortalStages] = useState<any[]>([])
  const [hrPortalProfiles, setHrPortalProfiles] = useState<any[]>([])
  const [hrPortalActivePipeline, setHrPortalActivePipeline] = useState('18')
  const [hrPortalPipelinesLoading, setHrPortalPipelinesLoading] = useState(false)
  const [hrPortalPipelineSearch, setHrPortalPipelineSearch] = useState('')
  const [hrPortalDraggedId, setHrPortalDraggedId] = useState<string|null>(null)
  const [hrPortalDragOverStage, setHrPortalDragOverStage] = useState<string|null>(null)
  const [hrPortalCardMenuOpen, setHrPortalCardMenuOpen] = useState<string|null>(null)
  // hrPortalPipelineDropdownOpen removed — reverted to pill tabs
  // Subcontractor application pipeline state (rendered in #/subcontractors → Applications tab)
  const [hrPortalSubSearch, setHrPortalSubSearch] = useState('')
  const [hrPortalSubStatusFilter, setHrPortalSubStatusFilter] = useState('all')
  const [hrPortalSubCardMenuOpen, setHrPortalSubCardMenuOpen] = useState<string|null>(null)
  // Profiles state
  const [hrPortalProfilesList, setHrPortalProfilesList] = useState<any[]>([])
  const [hrPortalProfilesLoading, setHrPortalProfilesLoading] = useState(false)
  const [hrPortalProfileDetail, setHrPortalProfileDetail] = useState<any>(null)
  const [hrPortalProfileDocs, setHrPortalProfileDocs] = useState<any[]>([])
  const [hrPortalViewProfileId, setHrPortalViewProfileId] = useState<string|null>(null)
  const [hrPortalViewProfileData, setHrPortalViewProfileData] = useState<any>(null)
  const [hrPortalViewProfileLoading, setHrPortalViewProfileLoading] = useState(false)
  const [hrPortalViewProfileTab, setHrPortalViewProfileTab] = useState<'application'|'documents'|'onboarding'>('application')
  // Templates state
  const [hrPortalTemplates, setHrPortalTemplates] = useState<any[]>([])
  const [hrPortalTemplatesLoading, setHrPortalTemplatesLoading] = useState(false)
  const [hrPortalTemplateUploadOpen, setHrPortalTemplateUploadOpen] = useState(false)
  const [hrPortalTplSearch, setHrPortalTplSearch] = useState('')
  const [hrPortalTplCatFilter, setHrPortalTplCatFilter] = useState('all')
  const [hrPortalTplPreviews, setHrPortalTplPreviews] = useState<Record<string, string>>({})
  // Assignments state
  const [hrPortalAssignmentsList, setHrPortalAssignmentsList] = useState<any[]>([])
  const [hrPortalAssignmentsLoading2, setHrPortalAssignmentsLoading2] = useState(false)
  const [hrPortalAssignmentFilter2, setHrPortalAssignmentFilter2] = useState('')
  const [hrPortalAssignExpandedEmployees, setHrPortalAssignExpandedEmployees] = useState<Set<string>>(new Set())
  const [hrPortalAssignSelectedIds, setHrPortalAssignSelectedIds] = useState<Set<number>>(new Set())
  const [hrPortalConfirmModal, setHrPortalConfirmModal] = useState<{title:string,message:string,onConfirm:()=>void,destructive?:boolean}|null>(null)
  // Employer Signing state
  const [hrPortalPendingEmployer, setHrPortalPendingEmployer] = useState<any[]>([])
  const [hrPortalSelectedSignDoc, setHrPortalSelectedSignDoc] = useState<any>(null)
  const [hrEmployerSignSubmitDocId, setHrEmployerSignSubmitDocId] = useState<number|null>(null)
  const [hrEmployerPreviewUrl, setHrEmployerPreviewUrl] = useState<string|null>(null)
  const [hrEmployerPreviewLoading, setHrEmployerPreviewLoading] = useState(false)
  const [hrEmployerCancelBusy, setHrEmployerCancelBusy] = useState(false)
  // Employee Detail expanded sections
  const [hrPortalExpandedSections, setHrPortalExpandedSections] = useState<Record<string,boolean>>({})
  const [hrPortalPendingEmployerLoading, setHrPortalPendingEmployerLoading] = useState(false)
  // Onboarding state
  const [hrPortalOnboardingTokens, setHrPortalOnboardingTokens] = useState<any[]>([])
  const [hrPortalOnboardingDocs, setHrPortalOnboardingDocs] = useState<any[]>([])
  const [hrPortalOnboardingLoading, setHrPortalOnboardingLoading] = useState(false)
  const [hrPortalOnboardingTab, setHrPortalOnboardingTab] = useState<'tokens'|'documents'>('tokens')
  const [hrPortalApps, setHrPortalApps] = useState<any[]>([])
  const [hrPortalAppsLoading, setHrPortalAppsLoading] = useState(false)
  const [hrPortalAppsSearch, setHrPortalAppsSearch] = useState('')
  const [hrPortalAppsStatus, setHrPortalAppsStatus] = useState('')
  const [hrPortalIncompleteApps, setHrPortalIncompleteApps] = useState<any[]>([])
  const [hrPortalAppsTab, setHrPortalAppsTab] = useState<'submitted'|'incomplete'>('submitted')
  const [hrPortalSettingsTab, setHrPortalSettingsTab] = useState<'email'|'admins'|'field-alerts'|'email-templates'>('email')
  const [hrPortalEmailSettings, setHrPortalEmailSettings] = useState<any>(null)
  const [hrPortalAdmins, setHrPortalAdmins] = useState<any[]>([])
  const [hrPortalCrmUsers, setHrPortalCrmUsers] = useState<any[]>([])
  const [hrPortalAllEmployees, setHrPortalAllEmployees] = useState<any[]>([])
  const [hrPortalAddAdminOpen, setHrPortalAddAdminOpen] = useState(false)
  const [hrPortalAddAdminSearch, setHrPortalAddAdminSearch] = useState('')
  const [hrPortalCrmUsersOpen, setHrPortalCrmUsersOpen] = useState(false)
  const [hrPortalCrmUsersSearch, setHrPortalCrmUsersSearch] = useState('')
  const [hrPortalCrmUsersModal, setHrPortalCrmUsersModal] = useState(false)
  const [hrPortalEditCrmUser, setHrPortalEditCrmUser] = useState<any>(null)
  // Deactivation modal: user being deactivated + optional reassignment target
  const [hrPortalDeactivateUser, setHrPortalDeactivateUser] = useState<any>(null)
  const [hrPortalDeactivateReassignTo, setHrPortalDeactivateReassignTo] = useState<number | null>(null)
  const [hrPortalDeactivateReassignSearch, setHrPortalDeactivateReassignSearch] = useState('')
  const [hrPortalDeactivateSaving, setHrPortalDeactivateSaving] = useState(false)
  const [hrPortalDeactivateError, setHrPortalDeactivateError] = useState<string | null>(null)
  const [hrPortalFieldRules, setHrPortalFieldRules] = useState<any[]>([])
  const [hrPortalFieldAlertForm, setHrPortalFieldAlertForm] = useState<any>(null) // null = closed, {} = adding, {id:..} = editing
  const [hrPortalFieldAlertSaving, setHrPortalFieldAlertSaving] = useState(false)
  const [hrPortalFieldAlertError, setHrPortalFieldAlertError] = useState<string|null>(null)
  const [hrPortalPositions, setHrPortalPositions] = useState<string[]>([])
  const [hrPortalEmailTemplates, setHrPortalEmailTemplates] = useState<any[]>([])
  const [hrPortalSettingsLoading, setHrPortalSettingsLoading] = useState(false)
  // Automation section state
  const [hrPortalAutomationTab, setHrPortalAutomationTab] = useState<'onboarding'|'emails'>('onboarding')
  const [hrPortalExpandedEmail, setHrPortalExpandedEmail] = useState<string|null>(null)
  const [hrPortalEditingEmail, setHrPortalEditingEmail] = useState<string|null>(null)
  const [hrPortalEditEmailSubject, setHrPortalEditEmailSubject] = useState('')
  const [hrPortalEditEmailBody, setHrPortalEditEmailBody] = useState('')
  const [hrPortalSavingEmail, setHrPortalSavingEmail] = useState<string|null>(null)
  const [hrPortalTogglingTemplate, setHrPortalTogglingTemplate] = useState<string|null>(null)
  const [hrPortalEmailStatsDays, setHrPortalEmailStatsDays] = useState(30)
  const [hrPortalShowAddDoc, setHrPortalShowAddDoc] = useState(false)

  // Signing modal state (ported from NativeSignatureModal)
  const [hrSignModalOpen, setHrSignModalOpen] = useState(false)
  const [hrSignAssignmentId, setHrSignAssignmentId] = useState<string|null>(null)
  // 'admin' = signing on someone else's behalf via the HR admin tabs.
  // 'employee' = the caller is signing their own assignment (opened from #/my-documents).
  const [hrSignMode, setHrSignMode] = useState<'admin' | 'employee'>('admin')
  const [hrSignAssignment, setHrSignAssignment] = useState<any>(null)
  const [hrSignPages, setHrSignPages] = useState<any[]>([])
  const [hrSignNumPages, setHrSignNumPages] = useState(0)
  const [hrSignLoading, setHrSignLoading] = useState(false)
  const [hrSignParsedFields, setHrSignParsedFields] = useState<any[]>([]) // raw field positions from template
  const [hrSignConvertedFields, setHrSignConvertedFields] = useState<any[]>([]) // percentage-based for overlay
  const [hrSignFilledFields, setHrSignFilledFields] = useState<Map<string, any>>(new Map())
  const [hrSignActiveField, setHrSignActiveField] = useState<any>(null)
  const [hrSignPopupOpen, setHrSignPopupOpen] = useState(false)
  const [hrSignPopupTab, setHrSignPopupTab] = useState<'style'|'draw'|'upload'>('style')
  const [hrSignTempSig, setHrSignTempSig] = useState<string|null>(null)
  const [hrSignSelectedFont, setHrSignSelectedFont] = useState(-1)
  const [hrSignCustomName, setHrSignCustomName] = useState('')
  const [hrSignUploadedSig, setHrSignUploadedSig] = useState<string|null>(null)
  const [hrSignSubmitting, setHrSignSubmitting] = useState(false)
  const [hrSignSuccess, setHrSignSuccess] = useState(false)
  const [hrSignCurrentSigner, setHrSignCurrentSigner] = useState(0)
  const [hrSignError, setHrSignError] = useState<string|null>(null)
  const [hrSignEditingTextField, setHrSignEditingTextField] = useState<string|null>(null)
  const [hrSignEditingTextValue, setHrSignEditingTextValue] = useState('')
  const hrSignFileInputRef = useRef<HTMLInputElement>(null)

  // Template field editor state (ported from FieldEditorModal)
  const [hrFieldEditorOpen, setHrFieldEditorOpen] = useState(false)
  const [hrFieldEditorTemplateId, setHrFieldEditorTemplateId] = useState<string|null>(null)
  const [hrFieldEditorFields, setHrFieldEditorFields] = useState<any[]>([])
  const [hrFieldEditorPages, setHrFieldEditorPages] = useState<any[]>([])
  const [hrFieldEditorNumPages, setHrFieldEditorNumPages] = useState(0)
  const [hrFieldEditorLoading, setHrFieldEditorLoading] = useState(false)
  const [hrFieldEditorTool, setHrFieldEditorTool] = useState<string|null>(null)
  const [hrFieldEditorSelected, setHrFieldEditorSelected] = useState<string|null>(null)
  const [hrFieldEditorSaving, setHrFieldEditorSaving] = useState(false)
  const [hrFieldEditorDragging, setHrFieldEditorDragging] = useState<any>(null)
  const [hrFieldEditorResizing, setHrFieldEditorResizing] = useState<any>(null)
  const [hrFieldEditorZoom, setHrFieldEditorZoom] = useState(1.0)
  const [hrFieldEditorSidebarSection, setHrFieldEditorSidebarSection] = useState<Record<string,boolean>>({ signerGroups: true, fieldTypes: true, checkboxGroups: true, properties: true })
  const [hrFieldEditorHistory, setHrFieldEditorHistory] = useState<any[][]>([])
  const [hrFieldEditorFuture, setHrFieldEditorFuture] = useState<any[][]>([])
  const [hrFieldEditorCustomSigners, setHrFieldEditorCustomSigners] = useState<{idx:number,label:string,color:string}[]>([])
  const [hrFieldEditorAddSignerOpen, setHrFieldEditorAddSignerOpen] = useState(false)
  const [hrFieldEditorNewSignerName, setHrFieldEditorNewSignerName] = useState('')
  const [hrEmployerSignature, setHrEmployerSignature] = useState<string|null>(null)
  const [hrEmployerInitials, setHrEmployerInitials] = useState<string|null>(null)
  const [hrFieldEditorEditingTitle, setHrFieldEditorEditingTitle] = useState(false)
  const [hrFieldEditorTitleDraft, setHrFieldEditorTitleDraft] = useState('')
  const [hrFieldEditorMarquee, setHrFieldEditorMarquee] = useState<{startX:number,startY:number,currentX:number,currentY:number,pageIdx:number}|null>(null)
  const [hrFieldEditorMarqueeSelected, setHrFieldEditorMarqueeSelected] = useState<string[]>([])
  const [hrFieldEditorGroupPrompt, setHrFieldEditorGroupPrompt] = useState(false)
  const [hrFieldEditorNewGroupName, setHrFieldEditorNewGroupName] = useState('')
  const [hrFieldEditorShowSettings, setHrFieldEditorShowSettings] = useState(false)
  const [hrFieldEditorSettingsForm, setHrFieldEditorSettingsForm] = useState<any>({})
  const [hrFieldEditorSavingSettings, setHrFieldEditorSavingSettings] = useState(false)
  const [hrFieldEditorAutoMapping, setHrFieldEditorAutoMapping] = useState(false)
  const [hrFieldEditorAutoMapStatus, setHrFieldEditorAutoMapStatus] = useState('')
  const [hrFieldEditorWorkflowMode, setHrFieldEditorWorkflowMode] = useState<'single_signer'|'multi_signer'>('single_signer')
  const [hrFieldEditorWorkflowSigners, setHrFieldEditorWorkflowSigners] = useState<{order:number,signerType:string,roleName:string}[]>([])

  // bp-mkt-v1 — Marketing Hub sub-view (mirrors the estimation subview pattern; #/marketing/<sub>)
  const [marketingSubView, setMarketingSubView] = useState<MarketingSubView>('hub')
  // bp-camp-v1 - CampaignsView owns rendering and data; App mirrors the URL.
  const [campaignsSubView, setCampaignsSubView] = useState<CampaignsSubView>('list')

  // Subcontractor Portal Admin state
  const [subAdminMe, setSubAdminMe] = useState<any>(null)
  const [subAdminUsers, setSubAdminUsers] = useState<any[]>([])
  const [subAdminCompanies, setSubAdminCompanies] = useState<any[]>([])
  const [subAdminLoading, setSubAdminLoading] = useState(false)

  // Deep-link plumbing for the HR Center flyout + nested hash
  // routing. When the hash parser lands on a non-default tab (or a selection),
  // it stashes the target here so the "reset to dashboard on entry" effects
  // don't clobber the deep link. `suppressHrHashSync`
  // stops the reverse hash-sync effect from firing while we apply a parsed URL.
  const pendingHrTabRef = useRef<HrTabKey | null>(null)
  const suppressHrHashSyncRef = useRef(false)
  const [subPreviewUserId, setSubPreviewUserId] = useState<string|null>(null)
  const [subPreviewRole, setSubPreviewRole] = useState<string|null>(null)
  useEffect(() => {
    ;(window as any).__resetSubPortalPreview = () => {
      setSubPreviewUserId(null)
      setSubPreviewRole(null)
    }
    return () => { delete (window as any).__resetSubPortalPreview }
  }, [])

  const [subPortalProjectId, setSubPortalProjectId] = useState<string|null>(null)
  const [subProjectAdminDocs, setSubProjectAdminDocs] = useState<any[]>([])

  // PDF first-page thumbnails for Project Documents cards.
  useEffect(() => {
    const candidates: any[] = [...prjAdminDocs, ...subProjectAdminDocs]
    const pdfs = candidates.filter(f => (f?.content_type || '') === 'application/pdf' && !pdfThumbs[f.id])
    if (pdfs.length === 0) return
    let cancelled = false
    ;(async () => {
      const pdfjs = (window as any).pdfjsLib || await (window as any).pdfjsLoaded?.catch(() => null)
      if (!pdfjs || cancelled) return
      for (const f of pdfs) {
        if (cancelled) break
        try {
          const resp = await fetch(f.file_url)
          if (!resp.ok) continue
          const buf = await resp.arrayBuffer()
          const pdf = await pdfjs.getDocument({ data: buf }).promise
          const page = await pdf.getPage(1)
          const vp = page.getViewport({ scale: 0.6 })
          const canvas = document.createElement('canvas')
          canvas.width = vp.width; canvas.height = vp.height
          const ctx = canvas.getContext('2d')
          if (!ctx) continue
          await page.render({ canvasContext: ctx, viewport: vp }).promise
          if (cancelled) break
          setPdfThumbs(prev => ({ ...prev, [f.id]: canvas.toDataURL('image/png') }))
        } catch { /* skip */ }
      }
    })()
    return () => { cancelled = true }
  }, [prjAdminDocs, subProjectAdminDocs])

  // DOCX HTML preview for Project Documents cards.
  useEffect(() => {
    const candidates: any[] = [...prjAdminDocs, ...subProjectAdminDocs]
    const docxFiles = candidates.filter(f => {
      const ext = (f?.file_name || '').split('.').pop()?.toLowerCase()
      return (ext === 'docx' || ext === 'doc') && !docxHtml[f.id]
    })
    if (docxFiles.length === 0) return
    let cancelled = false
    ;(async () => {
      const mam = (window as any).mammoth
      if (!mam || cancelled) return
      for (const f of docxFiles) {
        if (cancelled) break
        try {
          const resp = await fetch(f.file_url)
          if (!resp.ok) continue
          const buf = await resp.arrayBuffer()
          const result = await mam.convertToHtml({ arrayBuffer: buf })
          if (cancelled) break
          // ENT-002: Mammoth DOC/DOCX->HTML output is R2-derived untrusted
          // content; sanitize before it reaches dangerouslySetInnerHTML in the
          // subcontractor project-document cards.
          setDocxHtml(prev => ({ ...prev, [f.id]: safeHtml(result.value) }))
        } catch { /* skip */ }
      }
    })()
    return () => { cancelled = true }
  }, [prjAdminDocs, subProjectAdminDocs])

  // Render all pages of the previewed document into the modal container via
  // pdf.js canvas. Handles BOTH native PDFs and .doc/.docx (the latter through
  // the server-side → PDF conversion route resolved by prjPreviewPdfUrl). This
  // JS-fetch + canvas path is what actually issues the network request and
  // paints the doc — an inert <object data> never reliably fetched, leaving
  // docx stuck on the download card. On failure we flip prjPreviewLoadError so
  // the modal shows a graceful download fallback instead of a blank pane.
  useEffect(() => {
    const f = prjFilePreview
    // Clear any stale failure/loading state when the modal closes.
    if (!f) { setPrjPreviewLoadError(false); setPrjPreviewLoading(false); return }
    const container = pdfPreviewContainerRef.current
    if (!container) return
    const isPdfDoc = (f.content_type || '') === 'application/pdf'
    const isDocxDoc = /\.docx?$/i.test(f.file_name || '')
    if (!isPdfDoc && !isDocxDoc) return
    const url = prjPreviewPdfUrl(f)
    if (!url) return
    let cancelled = false
    setPrjPreviewLoadError(false)
    setPrjPreviewLoading(true)
    ;(async () => {
      const pdfjs = (window as any).pdfjsLib || await (window as any).pdfjsLoaded?.catch(() => null)
      if (!pdfjs || cancelled) { if (!cancelled) { setPrjPreviewLoadError(true); setPrjPreviewLoading(false) } return }
      try {
        container.innerHTML = ''
        const resp = await fetch(url)
        if (!resp.ok) { if (!cancelled) { setPrjPreviewLoadError(true); setPrjPreviewLoading(false) } return }
        const buf = await resp.arrayBuffer()
        const pdf = await pdfjs.getDocument({ data: buf }).promise
        const targetWidth = Math.min(window.innerWidth * 0.82, 900)
        for (let i = 1; i <= pdf.numPages; i++) {
          if (cancelled) break
          const page = await pdf.getPage(i)
          const unscaled = page.getViewport({ scale: 1 })
          const scale = targetWidth / unscaled.width
          const vp = page.getViewport({ scale })
          const canvas = document.createElement('canvas')
          canvas.width = vp.width; canvas.height = vp.height
          canvas.style.display = 'block'
          canvas.style.margin = i > 1 ? '12px auto 0' : '0 auto'
          canvas.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)'
          canvas.style.background = '#fff'
          const ctx = canvas.getContext('2d')
          if (!ctx) continue
          await page.render({ canvasContext: ctx, viewport: vp }).promise
          if (cancelled) break
          container.appendChild(canvas)
          // First page painted — hide the "Generating preview…" overlay.
          if (i === 1 && !cancelled) setPrjPreviewLoading(false)
        }
        if (!cancelled) setPrjPreviewLoading(false)
      } catch { if (!cancelled) { setPrjPreviewLoadError(true); setPrjPreviewLoading(false) } }
    })()
    return () => { cancelled = true }
  }, [prjFilePreview])
  const [subProjectChat, setSubProjectChat] = useState<any[]>([])
  const [subChatMessage, setSubChatMessage] = useState('')
  const [subChatModalOpen, setSubChatModalOpen] = useState(false)
  const [subConfirmDialog, setSubConfirmDialog] = useState<{title:string,message?:string,content?:React.ReactNode,onConfirm:()=>void,onCancel?:()=>void,destructive?:boolean,confirmLabel?:string}|null>(null)

  // Target Accounts state
  const [targetAccountsData, setTargetAccountsData] = useState<TargetAccount[]>([])
  const [targetAccountsLoading, setTargetAccountsLoading] = useState(false)
  const [targetAccountsSearch, setTargetAccountsSearch] = useState('')
  const [targetTierFilter, setTargetTierFilter] = useState('')
  const [targetStatusFilter, setTargetStatusFilter] = useState('')
  const [showMarkTargetModal, setShowMarkTargetModal] = useState(false)
  const [markTargetClientId, setMarkTargetClientId] = useState('')
  const [markTargetTier, setMarkTargetTier] = useState(2)
  const [targetAccountsPage, setTargetAccountsPage] = useState(1)
  const [targetAccountsSort, setTargetAccountsSort] = useState<{ field: string; order: 'ASC' | 'DESC' }>({ field: 'engagement_score', order: 'DESC' })
  const [targetAccountsSelectedRows, setTargetAccountsSelectedRows] = useState<Set<number>>(new Set())
  const [targetAccountsViewTab, setTargetAccountsViewTab] = useState('')
  const [taColWidths, setTaColWidths] = useState<Record<string, number>>({ name: 220, tier: 80, status: 100, engagement: 120, deals: 80, deal_value: 110, activities: 90, last_activity: 130 })

  // Sales Documents state
  const [salesDocsData, setSalesDocsData] = useState<SalesDocument[]>([])
  const [salesDocsLoading, setSalesDocsLoading] = useState(false)
  const [salesDocsSearch, setSalesDocsSearch] = useState('')
  const [salesDocsTypeFilter, setSalesDocsTypeFilter] = useState('')
  const [showCreateSalesDoc, setShowCreateSalesDoc] = useState(false)
  const [salesDocForm, setSalesDocForm] = useState<Record<string, string>>({ title: '', doc_type: 'proposal', description: '' })
  const [salesDocSaving, setSalesDocSaving] = useState(false)
  const [salesDocsPage, setSalesDocsPage] = useState(1)
  const [salesDocsSelectedRows, setSalesDocsSelectedRows] = useState<Set<number>>(new Set())
  const [salesDocsSort, setSalesDocsSort] = useState<{ field: string; order: 'ASC' | 'DESC' }>({ field: 'created_at', order: 'DESC' })
  const [salesDocsColWidths, setSalesDocsColWidths] = useState<Record<string, number>>({ title: 260, type: 120, views: 80, last_viewed: 150, created_by: 130, share: 100, created: 150 })
  const [salesDocsViewTab, setSalesDocsViewTab] = useState('all')
  const [salesDocsQfOpen, setSalesDocsQfOpen] = useState(false)

  // Activity Feed state
  const [activityFeedData, setActivityFeedData] = useState<any[]>([])
  const [activityFeedLoading, setActivityFeedLoading] = useState(false)
  const [activityFeedPage, setActivityFeedPage] = useState(1)
  const [activityFeedTotal, setActivityFeedTotal] = useState(0)
  const [activityFeedTypeFilter, setActivityFeedTypeFilter] = useState('')
  const [activityFeedSearch, setActivityFeedSearch] = useState('')
  const [activityFeedSelectedRows, setActivityFeedSelectedRows] = useState<Set<number>>(new Set())
  const [activityFeedSort, setActivityFeedSort] = useState<{ field: string; order: 'ASC' | 'DESC' }>({ field: 'created', order: 'DESC' })
  const [activityFeedColWidths, setActivityFeedColWidths] = useState<Record<string, number>>({ type: 120, subject: 260, contact: 200, date: 170, duration: 100, owner: 130 })
  const [activityFeedViewTab, setActivityFeedViewTab] = useState('all')
  const [activityFeedQfOpen, setActivityFeedQfOpen] = useState(false)

  // Meetings Scheduler state
  const [meetingTypesData, setMeetingTypesData] = useState<MeetingTypeRecord[]>([])
  const [meetingTypesLoading, setMeetingTypesLoading] = useState(false)
  const [meetingBookingsData, setMeetingBookingsData] = useState<MeetingBooking[]>([])
  const [meetingSchedulerTab, setMeetingSchedulerTab] = useState<'types' | 'bookings'>('types')
  const [showCreateMeetingType, setShowCreateMeetingType] = useState(false)
  const [meetingTypeForm, setMeetingTypeForm] = useState<Record<string, any>>({ name: '', duration_minutes: 30, description: '', location: 'Office', color: '#2563eb' })
  const [meetingTypeSaving, setMeetingTypeSaving] = useState(false)
  const [meetingsSearch, setMeetingsSearch] = useState('')
  const [mtSelectedRows, setMtSelectedRows] = useState<Set<number>>(new Set())
  const [mtSort, setMtSort] = useState<{ field: string; order: 'ASC' | 'DESC' }>({ field: 'name', order: 'ASC' })
  const [mtPage, setMtPage] = useState(1)
  const [mtColWidths, setMtColWidths] = useState({ name: 240, duration: 110, location: 160, status: 100, bookings: 100, actions: 160 })
  const [bkSelectedRows, setBkSelectedRows] = useState<Set<number>>(new Set())
  const [bkSort, setBkSort] = useState<{ field: string; order: 'ASC' | 'DESC' }>({ field: 'booking_date', order: 'DESC' })
  const [bkPage, setBkPage] = useState(1)
  const [bkColWidths, setBkColWidths] = useState({ guest: 180, email: 200, type: 160, date: 120, time: 120, status: 100, notes: 150 })

  // Coaching Playlists state
  const [playlistsData, setPlaylistsData] = useState<CoachingPlaylist[]>([])
  const [playlistsLoading, setPlaylistsLoading] = useState(false)
  const [playlistsSearch, setPlaylistsSearch] = useState('')
  const [editingPlaylistId, setEditingPlaylistId] = useState<number | null>(null)
  const [editingPlaylistData, setEditingPlaylistData] = useState<(CoachingPlaylist & { items: PlaylistItem[] }) | null>(null)
  const [showCreatePlaylist, setShowCreatePlaylist] = useState(false)
  const [playlistForm, setPlaylistForm] = useState<{ name: string; description: string }>({ name: '', description: '' })
  const [playlistSaving, setPlaylistSaving] = useState(false)
  const [plSelectedRows, setPlSelectedRows] = useState<Set<number>>(new Set())
  const [plSort, setPlSort] = useState<{ field: string; order: 'ASC' | 'DESC' }>({ field: 'created_at', order: 'DESC' })
  const [plPage, setPlPage] = useState(1)
  const [plColWidths, setPlColWidths] = useState({ name: 260, items: 100, duration: 110, created_by: 140, created_at: 130, actions: 100 })
  const [showAddPlaylistItem, setShowAddPlaylistItem] = useState(false)
  const [playlistItemForm, setPlaylistItemForm] = useState<{ title: string; recording_url: string; duration_seconds: number; notes: string }>({ title: '', recording_url: '', duration_seconds: 0, notes: '' })

  // Prospecting Workspace state
  const [prospectingData, setProspectingData] = useState<any>(null)
  const [prospectingLoading, setProspectingLoading] = useState(false)
  const [prospectingPage, setProspectingPage] = useState(1)
  const [prospectingSort, setProspectingSort] = useState<{ field: string; order: 'ASC' | 'DESC' }>({ field: 'date_create', order: 'DESC' })
  const [prospectingSelectedRows, setProspectingSelectedRows] = useState<Set<number>>(new Set())
  const [prospectingViewTab, setProspectingViewTab] = useState('')
  const [prospectingSearch, setProspectingSearch] = useState('')
  const [prospColWidths, setProspColWidths] = useState<Record<string, number>>({ title: 220, status: 100, source: 110, contact: 160, assigned_to: 130, created: 130, updated: 130 })

  // Accounting state
  const [accountingSubView, setAccountingSubView] = useState<AccountingSubView>('overview')
  const [acctAccounts, setAcctAccounts] = useState<any[]>([])
  const [acctAccountsLoading, setAcctAccountsLoading] = useState(false)
  const [acctSummary, setAcctSummary] = useState<Record<string, number>>({})
  const [acctTypeFilter, setAcctTypeFilter] = useState('')
  const [acctSearch, setAcctSearch] = useState('')
  // Shared 300ms debounce for the accounting search boxes — they previously
  // fired a D1-backed fetch on every keystroke. One ref is enough: only one
  // search input has focus at a time.
  const acctSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const debounceAcctSearch = (fn: () => void) => {
    if (acctSearchDebounceRef.current) clearTimeout(acctSearchDebounceRef.current)
    acctSearchDebounceRef.current = setTimeout(fn, 300)
  }
  const [acctCreateOpen, setAcctCreateOpen] = useState(false)
  const [acctEditItem, setAcctEditItem] = useState<any>(null)
  const [jeEntries, setJeEntries] = useState<any[]>([])
  const [jeLoading, setJeLoading] = useState(false)
  const [jeStatusFilter, setJeStatusFilter] = useState('')
  const [jeSearch, setJeSearch] = useState('')
  const [jeCreateOpen, setJeCreateOpen] = useState(false)
  // JE detail drawer (drill-through wave B)
  const [jeDetailId, setJeDetailId] = useState<number | null>(null)
  // Account Ledger drawer (drill-through wave C)
  const [ledgerTarget, setLedgerTarget] = useState<{ accountId: number; start?: string; end?: string } | null>(null)
  const [apBills, setApBills] = useState<any[]>([])
  const [apLoading, setApLoading] = useState(false)
  const [apStatusFilter, setApStatusFilter] = useState('')
  const [apSearch, setApSearch] = useState('')
  const [apSummary, setApSummary] = useState<any>({})
  // Bills is the default AP table. The scanner remains a separate workflow, while
  // the bills/vendors tables render their settled empty states without refetching.
  const [apTabBase, setApTabBase] = useState<'bills' | 'vendors' | 'payments' | 'scanner'>('bills')
  // Vendors graduated from an AP tab to its own Accounting sub-view ("Vendors").
  // The Accounts Payable surface shows Bills. `apTab` is the EFFECTIVE tab the
  // AP/Vendors render block consumes: on the Vendors sub-view
  // it is forced to 'vendors'; otherwise it follows the base state (never 'vendors',
  // since AP no longer offers that tab).
  const apTab: 'bills' | 'vendors' | 'payments' | 'scanner' = accountingSubView === 'vendors' ? 'vendors' : (apTabBase === 'vendors' ? 'bills' : apTabBase)
  const setApTab = setApTabBase
  const [apVendors, setApVendors] = useState<any[]>([])
  const [apCreateOpen, setApCreateOpen] = useState(false)
  const [apCreateType, setApCreateType] = useState<'bill' | 'vendor' | 'payment'>('bill')
  const [scanLoading, setScanLoading] = useState(false)
  const [scanSummary, setScanSummary] = useState<any>({})
  const [scanStatusFilter, setScanStatusFilter] = useState('')
  // Invoice-scanner DIVISION multi-select filter. Stored values:
  // paint | windows_doors | construction | __unassigned__ (NULL/empty division).
  // Empty array = show EVERY division (default, current behavior). Comma-joined
  // into the queue's division= param; the backend ORs the real divisions with the
  // unassigned NULL/'' group. (s.division per-row still drives the detail panel.)
  const [scanDivisionFilters, setScanDivisionFilters] = useState<string[]>([])
  const [scanDivisionPickerOpen, setScanDivisionPickerOpen] = useState(false)
  const [scanIncludeApproved, setScanIncludeApproved] = useState(false)
  const [scanIncludeRejected, setScanIncludeRejected] = useState(false)
  const [scanStatusOpen, setScanStatusOpen] = useState(false)
  const [scanDateRange, setScanDateRange] = useState<{ from: string; to: string }>({ from: '', to: '' })
  const [scanDateOpen, setScanDateOpen] = useState(false)
  // Unified "Bills" status quick-filter. DEFAULT = Pending only (the actionable
  // AP queue). Chip cycles Pending → Approved → Rejected → All. 'All' is encoded
  // as empty filters + both include flags (see billStatusChip / setBillStatusChip).
  const [scanStatusFilters, setScanStatusFilters] = useState<string[]>(['pending'])
  const [scanVendorFilters, setScanVendorFilters] = useState<Array<{ id: number; name: string }>>([])
  const [scanVendorPickerOpen, setScanVendorPickerOpen] = useState(false)
  const [scanVendorPickerQuery, setScanVendorPickerQuery] = useState('')
  const [scanJobFilters, setScanJobFilters] = useState<Array<{ id: number; name: string }>>([])
  const [scanJobPickerOpen, setScanJobPickerOpen] = useState(false)
  const [scanJobPickerQuery, setScanJobPickerQuery] = useState('')
  const [scanSearch, setScanSearch] = useState('')
  // Invoice-Scanner sort. DEFAULT: source_email_date (RECEIVED) DESC so the most
  // recently RECEIVED invoices are on top of page 1 (invoice_date can be a future
  // printed date and is misleading for a review queue). The client-side sortFn over
  // the loaded rows (parsed-date comparator, b7833f5) is authoritative.
  const [scanSort, setScanSort] = useState<{field:string;order:'ASC'|'DESC'}>({field:'source_email_date',order:'DESC'})
  // ── Canonical Invoice-Scanner queue loader (SINGLE source of truth) ──────────
  // EVERY scanner fetch routes through this. It (a) fetch-ALLs (large pageSize so
  // the loaded set holds every matching row, matching the bills/vendors client-paginate
  // model), (b) always passes sortField/sortOrder from scanSort, and (c) always
  // passes ALL active scanner filters (status/search/division/date-range/vendor/job
  // + include flags). Pass `extra` to override a single value within the same call
  // (e.g. a freshly-set filter not yet flushed to state). Optionally toggles the
  // table spinner. The client-side sortFn re-sorts the loaded rows afterward
  // (parsed-date comparator) so the server sort here is a harmless pre-sort.
  // Declared BEFORE the scanner keyboard/load effects that reference it (TDZ-safe).
  const scanReloadQueue = useCallback((extra: any = {}, withLoading = false) => {
    if (withLoading) setScanLoading(true)
    return fetchInvoiceScans({
      status: scanStatusFilters.length ? scanStatusFilters.join(',') : (scanStatusFilter || undefined),
      division: scanDivisionFilters.length ? scanDivisionFilters.join(',') : undefined,
      search: scanSearch || undefined,
      includeApproved: scanIncludeApproved,
      includeRejected: scanIncludeRejected,
      dateFrom: scanDateRange.from || undefined,
      dateTo: scanDateRange.to || undefined,
      vendorId: scanVendorFilters.length ? scanVendorFilters.map(v => v.id).join(',') as any : undefined,
      jobId: scanJobFilters.length ? scanJobFilters.map(j => j.id).join(',') as any : undefined,
      sortField: scanSort.field,
      sortOrder: scanSort.order,
      pageSize: 1000,
      ...extra,
    }).then(r => r)
      .finally(() => { if (withLoading) setScanLoading(false) })
  }, [scanStatusFilters, scanStatusFilter, scanDivisionFilters, scanSearch, scanIncludeApproved, scanIncludeRejected, scanDateRange.from, scanDateRange.to, scanVendorFilters, scanJobFilters, scanSort.field, scanSort.order])
  const [scanDetailOpen, setScanDetailOpen] = useState(false)
  const [scanDetailItem, setScanDetailItem] = useState<any>(null)
  const [scanApproving, setScanApproving] = useState(false)
  // Backlog-drain UI: loading flags for the create-vendor-from-scan shortcut
  // and the scanner-table bulk-approve action.
  const [scanCreatingVendor, setScanCreatingVendor] = useState(false)
  const [scanBatchApproving, setScanBatchApproving] = useState(false)
  // Auto-preselect of confidence>=0.8 + matched-vendor rows is run once per
  // freshly-loaded scanner queue; this nonce guards against re-running it after
  // the user has manually changed the selection.
  const [scanAutoSelectKey, setScanAutoSelectKey] = useState('')
  const [scanPdfPreview, setScanPdfPreview] = useState<{ id: number; title: string } | null>(null)
  // Generic file-preview modal used by project / contact attachment cards.
  // Reuses the .pdf-preview-overlay / .pdf-preview-modal styles from the
  // invoice scanner. Holds a direct URL + title; iframe streams the file.
  // Preview modal carries the whole visible attachment list so the user can
  // page through with arrow buttons / keys without closing & reopening once
  // per file. `index` is which entry is currently shown.
  const [attachmentPreview, setAttachmentPreview] = useState<{
    items: Array<{ url: string; title: string }>
    index: number
  } | null>(null)
  const [scanPickerProjects, setScanPickerProjects] = useState<any[]>([])
  const [scanDirty, setScanDirty] = useState<Record<string, any>>({})
  const [scanOriginal, setScanOriginal] = useState<any>(null)
  const [scanSaving, setScanSaving] = useState(false)
  const [scanRevertNonce, setScanRevertNonce] = useState(0)
  const [scanVendorEdit, setScanVendorEdit] = useState<{ mode: 'new' | 'edit'; data: any } | null>(null)
  const [scanVendorSaving, setScanVendorSaving] = useState(false)
  const [scanVendorManageOpen, setScanVendorManageOpen] = useState(false)
  const [scanVendorManageSearch, setScanVendorManageSearch] = useState('')
  // Draggable split between the scan-detail fields pane (left) and the PDF pane (right).
  // Persisted as a left-pane width percentage; restored on open and CLAMPED to the 30–60%
  // bounds the drag enforces (so a stale out-of-range value can't collapse a pane). Default
  // ~38% (≈ the old fixed 540px left pane). Drag-time also applies a 320px px floor per pane.
  const [scanSplitPct, setScanSplitPct] = useState<number>(() => {
    try {
      const v = parseFloat(localStorage.getItem('botpros-scan-detail-split') || '')
      if (!Number.isNaN(v)) return Math.max(30, Math.min(60, v))
    } catch {}
    return 38
  })
  const scanShellRef = useRef<HTMLDivElement | null>(null)
  // The fields (left) pane element — its width is driven DIRECTLY on the DOM node during a
  // drag (no per-frame setState) so the ~80K-line component isn't re-rendered every mousemove.
  const scanLeftRef = useRef<HTMLDivElement | null>(null)
  // Holds the teardown for an in-flight resize drag so it can be cleaned up on unmount.
  const scanResizeCleanup = useRef<(() => void) | null>(null)
  useEffect(() => () => { scanResizeCleanup.current?.() }, [])
  useEffect(() => {
    if (scanDetailItem) {
      setScanOriginal(JSON.parse(JSON.stringify(scanDetailItem)))
      setScanDirty({})
    }
  }, [scanDetailItem?.id])
  // Auto-match the AI-detected vendor name to an existing vendor record. Exact
  // (normalized) first, then a fuzzy fallback so already-scanned rows whose names
  // differ only by punctuation / "Inc/LLC" / a sub-phrase still link (new scans
  // are matched server-side by the model; this covers the existing queue).
  // 3-tier chained picker: Contact -> Deal -> Project
  const [scanLinkContactQuery, setScanLinkContactQuery] = useState('')
  const [scanLinkContactResults, setScanLinkContactResults] = useState<any[]>([])
  const [scanLinkContact, setScanLinkContact] = useState<{ id: any; name: string } | null>(null)
  const [scanLinkDealQuery, setScanLinkDealQuery] = useState('')
  const [scanLinkDealResults, setScanLinkDealResults] = useState<any[]>([])
  const [scanLinkDeal, setScanLinkDeal] = useState<{ id: any; title: string } | null>(null)
  const [scanLinkProjectQuery, setScanLinkProjectQuery] = useState('')
  useEffect(() => {
    // Reset on scan change
    setScanLinkContactQuery(''); setScanLinkContactResults([]); setScanLinkContact(null)
    setScanLinkDealQuery(''); setScanLinkDealResults([]); setScanLinkDeal(null)
    setScanLinkProjectQuery('')
  }, [scanDetailItem?.id])
  useEffect(() => {
    if (!scanDetailOpen) return
    const q = scanLinkContactQuery.trim()
    if (q.length < 1) { setScanLinkContactResults([]); return }
    let cancelled = false
    const h = setTimeout(async () => {
      try {
        const r = await fetchClients(1, 8, { search: q })
        const list = (((r as any).clients) || ((r as any).data) || []) as any[]
        if (!cancelled) setScanLinkContactResults(list.slice(0, 8))
      } catch { if (!cancelled) setScanLinkContactResults([]) }
    }, 250)
    return () => { cancelled = true; clearTimeout(h) }
  }, [scanDetailOpen, scanLinkContactQuery])
  useEffect(() => {
    if (!scanDetailOpen) return
    const q = scanLinkDealQuery.trim()
    if (q.length < 1 && !scanLinkContact) { setScanLinkDealResults([]); return }
    let cancelled = false
    const h = setTimeout(async () => {
      try {
        let list: any[] = []
        if (scanLinkContact) {
          const r = await fetch(`/api/crm/clients/${encodeURIComponent(scanLinkContact.id)}/deals`).then(x => x.ok ? x.json() : [])
          list = (Array.isArray(r) ? r : ((r as any)?.deals || [])) as any[]
          if (q) {
            const lq = q.toLowerCase()
            list = list.filter((d: any) => String(d.title || '').toLowerCase().includes(lq))
          }
        } else {
          const r = await fetchDeals(0, 1, 8, { search: q, excludeClosed: false })
          list = (r.deals || []) as any[]
        }
        if (!cancelled) setScanLinkDealResults(list.slice(0, 8))
      } catch { if (!cancelled) setScanLinkDealResults([]) }
    }, 250)
    return () => { cancelled = true; clearTimeout(h) }
  }, [scanDetailOpen, scanLinkDealQuery, scanLinkContact])
  useEffect(() => {
    if (!scanDetailOpen && apTab !== 'scanner') return
    if (scanPickerProjects.length > 0) return
    fetchCrmProjects({ pageSize: 500 })
      .then(r => {
        const active = (r.data || []).filter((p: any) => p.pipeline_stage !== 'P_COMPLETED' && p.status !== 'completed' && p.status !== 'cancelled' && p.status !== 'lost')
        setScanPickerProjects(active)
      })
      .catch(() => {})
  }, [scanDetailOpen, apTab])
  // Esc closes the generic attachment-preview modal; arrow keys page through
  // the rest of the list without leaving the modal.
  useEffect(() => {
    if (!attachmentPreview) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setAttachmentPreview(null); return }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        setAttachmentPreview(prev => {
          if (!prev) return prev
          const len = prev.items.length
          if (len < 2) return prev
          const delta = e.key === 'ArrowLeft' ? -1 : 1
          const nextIdx = (prev.index + delta + len) % len
          return { ...prev, index: nextIdx }
        })
        e.preventDefault()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [attachmentPreview])
  // .docx/.doc attachments now render via the server-side PDF conversion route
  // (`/api/crm/files/pdf?key=…`) wired into the preview <object> below — no
  // client-side mammoth HTML conversion needed for this modal.
  const [arInvoices, setArInvoices] = useState<any[]>([])
  const [arLoading, setArLoading] = useState(false)
  const [arStatusFilter, setArStatusFilter] = useState('')
  const [arSearch, setArSearch] = useState('')
  const [arSummary, setArSummary] = useState<any>({})
  const [arCreateOpen, setArCreateOpen] = useState(false)
  const [arNewLines, setArNewLines] = useState<{description: string; quantity: number; rate: number}[]>([{description: '', quantity: 1, rate: 0}])
  const [bankAccounts, setBankAccounts] = useState<any[]>([])
  const [bankTxns, setBankTxns] = useState<any[]>([])
  const [bankReconAccount, setBankReconAccount] = useState<any>(null)
  const [bankReconId, setBankReconId] = useState<number | null>(null)
  const [bankClearedIds, setBankClearedIds] = useState<Set<number>>(new Set())
  const [bankStmtDate, setBankStmtDate] = useState('')
  const [bankStmtBalance, setBankStmtBalance] = useState('')
  const [jcJobs, setJcJobs] = useState<any[]>([])
  const [jcLoading, setJcLoading] = useState(false)
  const [jcStatusFilter, setJcStatusFilter] = useState('')
  const [jcSearch, setJcSearch] = useState('')
  const [jcCreateOpen, setJcCreateOpen] = useState(false)
  const [jcSelectedJob, setJcSelectedJob] = useState<any>(null)
  // Job billing-company history (Task 16)
  const [jobBillingPeriods, setJobBillingPeriods] = useState<{ data: JobBillingPeriod[]; job: any } | null>(null)
  const [billingChangeModalOpen, setBillingChangeModalOpen] = useState(false)
  const [billingChangeForm, setBillingChangeForm] = useState<{ billing_org_id: string; billing_org_name: string; effective_date: string }>({ billing_org_id: '', billing_org_name: '', effective_date: new Date().toISOString().slice(0, 10) })
  const [billingOrgSearch, setBillingOrgSearch] = useState('')
  const [billingOrgResults, setBillingOrgResults] = useState<CrmClient[]>([])
  const [billingChangeSaving, setBillingChangeSaving] = useState(false)
  const [plData, setPlData] = useState<any[]>([])
  const [plStartDate, setPlStartDate] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01` })
  const [plEndDate, setPlEndDate] = useState(() => new Date().toISOString().split('T')[0])
  const [bsData, setBsData] = useState<any[]>([])
  const [bsDate, setBsDate] = useState(() => new Date().toISOString().split('T')[0])
  const [tbData, setTbData] = useState<any[]>([])
  const [tbDate, setTbDate] = useState(() => new Date().toISOString().split('T')[0])
  const [tbShowZero, setTbShowZero] = useState(false)
  const [agingData, setAgingData] = useState<any[]>([])
  const [agingDate, setAgingDate] = useState(() => new Date().toISOString().split('T')[0])
  const [wipIncludeCompleted, setWipIncludeCompleted] = useState(false)
  const [acctSort, setAcctSort] = useState<{field:string;order:'ASC'|'DESC'}>({field:'account_number',order:'ASC'})
  const [jeSort, setJeSort] = useState<{field:string;order:'ASC'|'DESC'}>({field:'entry_date',order:'DESC'})
  const [apBillSort, setApBillSort] = useState<{field:string;order:'ASC'|'DESC'}>({field:'bill_date',order:'DESC'})
  const [apVendorSort, setApVendorSort] = useState<{field:string;order:'ASC'|'DESC'}>({field:'name',order:'ASC'})
  // ===== AP editable columns (parallels the Contacts column-editor) =====
  // Each def: key (matches a row field / a special render branch), label, width, optional sortField, optional frozen/pinned (non-removable), category.

  const [apBillVisibleCols, setApBillVisibleCols] = useState<string[]>(() => apReadCols('botpros-ap-bills-cols', AP_BILL_COL_DEFS))
  const [apVendorVisibleCols, setApVendorVisibleCols] = useState<string[]>(() => apReadCols('botpros-ap-vendors-cols', AP_VENDOR_COL_DEFS))
  const [apScannerVisibleCols, setApScannerVisibleCols] = useState<string[]>(() => apReadCols('botpros-ap-scanner-cols', AP_SCANNER_COL_DEFS))
  const [apEditColumnsOpen, setApEditColumnsOpen] = useState(false)
  const [apEditColsSearch, setApEditColsSearch] = useState('')
  const [apEditColsDraft, setApEditColsDraft] = useState<string[]>([])
  const [apEditColsDragIdx, setApEditColsDragIdx] = useState<number | null>(null)
  const [apEditColsDragOverIdx, setApEditColsDragOverIdx] = useState<number | null>(null)
  const [jcSort, setJcSort] = useState<{field:string;order:'ASC'|'DESC'}>({field:'job_number',order:'ASC'})
  const [wipSort, setWipSort] = useState<{field:string;order:'ASC'|'DESC'}>({field:'job_number',order:'ASC'})
  // Bank Reconciliation 6-row state
  const [bankSelectedRows, setBankSelectedRows] = useState<Set<number>>(new Set())
  const [bankPage, setBankPage] = useState(1)
  const [bankSearch, setBankSearch] = useState('')
  const [bankFilterOpen, setBankFilterOpen] = useState(false)
  const [bankSettingsOpen, setBankSettingsOpen] = useState(false)
  // Job Costing 6-row state
  const [jcSelectedRows, setJcSelectedRows] = useState<Set<number>>(new Set())
  const [jcPage, setJcPage] = useState(1)
  const [jcFilterOpen, setJcFilterOpen] = useState(false)
  const [jcSettingsOpen, setJcSettingsOpen] = useState(false)
  // WIP Schedule 6-row state
  const [wipSelectedRows, setWipSelectedRows] = useState<Set<number>>(new Set())
  const [wipPage, setWipPage] = useState(1)
  const [wipSearch, setWipSearch] = useState('')
  const [wipStatusFilter, setWipStatusFilter] = useState('')
  const [wipData, setWipData] = useState<any[]>([])
  const [wipFilterOpen, setWipFilterOpen] = useState(false)
  const [wipSettingsOpen, setWipSettingsOpen] = useState(false)
  const [tbSort, _setTbSort] = useState<{field:string;order:'ASC'|'DESC'}>({field:'account_number',order:'ASC'})
  void _setTbSort
  const [arSort, setArSort] = useState<{field:string;order:'ASC'|'DESC'}>({field:'invoice_date',order:'DESC'})
  // QBO report state
  const [qboCollapsed, setQboCollapsed] = useState<Record<string,boolean>>({})
  const [qboExportOpen, setQboExportOpen] = useState<string|null>(null)
  const [agingExpandedClients, setAgingExpandedClients] = useState<Record<string,boolean>>({})
  const [plLoading, setPlLoading] = useState(false)
  const [plSearch, setPlSearch] = useState('')
  const [plPeriodPreset, setPlPeriodPreset] = useState('this-month')
  const [bsLoading, setBsLoading] = useState(false)
  const [bsSearch, setBsSearch] = useState('')
  const [bsPeriodPreset, setBsPeriodPreset] = useState('today')
  const [tbLoading, setTbLoading] = useState(false)
  const [tbSearch, setTbSearch] = useState('')
  const [tbPeriodPreset, setTbPeriodPreset] = useState('today')
  const [agingLoading, setAgingLoading] = useState(false)
  const [agingSearch, setAgingSearch] = useState('')
  const [agingPeriodPreset, setAgingPeriodPreset] = useState('today')
  const [agingSort, setAgingSort] = useState<{field:string;order:'ASC'|'DESC'}>({field:'client_name',order:'ASC'})
  // Accounting sub-view table state (6-row template)
  const [acctSelectedRows, setAcctSelectedRows] = useState<Set<number>>(new Set())
  const [acctPage, setAcctPage] = useState(1)
  const [jeSelectedRows, setJeSelectedRows] = useState<Set<number>>(new Set())
  const [jePage, setJePage] = useState(1)
  const [apSelectedRows, setApSelectedRows] = useState<Set<number>>(new Set())
  const [apPage, setApPage] = useState(1)
  const [arSelectedRows, setArSelectedRows] = useState<Set<number>>(new Set())
  const [arPage, setArPage] = useState(1)
  const [acctSettingsOpen, setAcctSettingsOpen] = useState(false)
  const [acctPageSizeOpen, setAcctPageSizeOpen] = useState(false)
  const [acctQuickFiltersOpen, setAcctQuickFiltersOpen] = useState(true)
  // (old HR state removed — replaced by hrPortal* variables above)

  useEffect(() => {
    if (activeView !== 'accounting' || accountingSubView !== 'accounts-payable') return
    let cancelled = false
    if (apTab !== 'scanner') return () => { cancelled = true }
    // Canonical loader: fetch-all + sort + all active filters.
    scanReloadQueue({}, true).catch((e) => console.error('accounting invoice scanner load failed', e))
    if (!jcJobs.length) fetchAcctJobs({}).then((r) => { if (!cancelled) setJcJobs(r.data) }).catch(() => {})
    if (!apVendors.length) fetchAcctVendors().then((r) => { if (!cancelled) setApVendors(r.data) }).catch(() => {})
    return () => { cancelled = true }
  }, [activeView, accountingSubView, apTab, scanStatusFilter, scanDivisionFilters, scanSearch, jcJobs.length, apVendors.length, scanReloadQueue])

  // Vendors is now its own Accounting sub-view (graduated from the AP "All vendors"
  // tab). Load the vendor list when the page opens so it renders as a full management view.
  useEffect(() => {
    if (activeView !== 'accounting' || accountingSubView !== 'vendors') return
    let cancelled = false
    setApPage(1)
    fetchAcctVendors().then((r) => { if (!cancelled) setApVendors(r.data) }).catch(() => {})
    return () => { cancelled = true }
  }, [activeView, accountingSubView])

  // WIP schedule dataset — loaded when the subview opens (mirrors Hartzell).
  useEffect(() => {
    if (activeView !== 'accounting' || accountingSubView !== 'wip-schedule') return
    let cancelled = false
    fetchWipSchedule({ include_completed: wipIncludeCompleted })
      .then((r) => { if (!cancelled) setWipData(r.data) })
      .catch((e) => console.error('accounting WIP schedule load failed', e))
    return () => { cancelled = true }
  }, [activeView, accountingSubView, wipIncludeCompleted])

  // Scanner rows are NEVER auto-selected. Selection is driven solely by the
  // user's checkbox clicks (and the select-all header). The old backlog-drain
  // effect here auto-preselected every high-confidence/matched-vendor row when
  // the scanner opened (and re-fired whenever the now-removed "View queue"
  // button reset scanAutoSelectKey), surprising users with ~13 rows already
  // selected. Removed so nothing gets auto-selected; Batch Approve still works
  // on whatever the user manually selects.


  useEffect(() => {
    if (activeView !== 'accounting' || accountingSubView !== 'profit-loss') return
    let cancelled = false
    setPlLoading(true)
    fetchProfitLoss({ start_date: plStartDate, end_date: plEndDate })
      .then((r) => { if (!cancelled) setPlData(r.data) })
      .catch((e) => console.error('accounting profit and loss load failed', e))
      .finally(() => { if (!cancelled) setPlLoading(false) })
    return () => { cancelled = true }
  }, [activeView, accountingSubView, plStartDate, plEndDate])

  useEffect(() => {
    if (activeView !== 'accounting' || accountingSubView !== 'balance-sheet') return
    let cancelled = false
    setBsLoading(true)
    fetchBalanceSheet({ as_of_date: bsDate })
      .then((r) => { if (!cancelled) setBsData(r.data) })
      .catch((e) => console.error('accounting balance sheet load failed', e))
      .finally(() => { if (!cancelled) setBsLoading(false) })
    return () => { cancelled = true }
  }, [activeView, accountingSubView, bsDate])

  useEffect(() => {
    if (activeView !== 'accounting' || accountingSubView !== 'trial-balance') return
    let cancelled = false
    setTbLoading(true)
    fetchTrialBalance({ as_of_date: tbDate, show_zero: tbShowZero })
      .then((r) => { if (!cancelled) setTbData(r.data) })
      .catch((e) => console.error('accounting trial balance load failed', e))
      .finally(() => { if (!cancelled) setTbLoading(false) })
    return () => { cancelled = true }
  }, [activeView, accountingSubView, tbDate, tbShowZero])

  useEffect(() => {
    if (activeView !== 'accounting' || accountingSubView !== 'ar-aging') return
    let cancelled = false
    setAgingLoading(true)
    fetchArAging({ as_of_date: agingDate })
      .then((r) => { if (!cancelled) setAgingData(r.data) })
      .catch((e) => console.error('accounting AR aging load failed', e))
      .finally(() => { if (!cancelled) setAgingLoading(false) })
    return () => { cancelled = true }
  }, [activeView, accountingSubView, agingDate])

  // (old HR Document Management state removed — these modals are now part of Phase 2/3)
  // (old HR e-signature, employer signing, settings, and onboarding state removed — replaced by hrPortal* variables)

  const [userRole, setUserRole] = useState<string>(() => localStorage.getItem('userRole') || 'user')
  // photoUrl/photoVersion: VERSIONED identity photo from /api/me (effective —
  // impersonation-aware — user). Feeds navAvatarSource so the top-right avatar
  // is fresh on EVERY route and busts per upload (docs/REGRESSIONS.md 2026-07-01).
  const [currentUser, setCurrentUser] = useState<{ email: string; name: string; role: string; userId: number | null; isAdmin?: number; hrProfileId?: number | null; photoUrl?: string | null; photoVersion?: string | null } | null>(null)
  // Capability map from /api/me/capabilities — drives UI button gating
  // (disabled + tooltip) so users don't click into 403s. Backend is the
  // actual security boundary; this is UX clarity only. null = not yet
  // loaded (treat all as allowed during the brief flash before resolve).
  const [myCapabilities, setMyCapabilities] = useState<Record<Capability, boolean> | null>(null)
  const [myRole, setMyRole] = useState<string | null>(null)
  // Per-role hidden nav keys from /api/me/capabilities (role_nav_visibility,
  // editable in Settings → Roles & Permissions). Empty set = everything
  // visible. super_admin always gets an empty set from the API.
  // Seeded synchronously from the last-known map cached in localStorage so
  // restricted tabs never flash in the sidebar while the fetch is in
  // flight; on a truly first paint (no cache) isNavHidden() hides all
  // gateable surfaces until the server map arrives.
  const [hiddenNav, setHiddenNav] = useState<Set<string>>(() => {
    try {
      const cached = localStorage.getItem('crmHiddenNav')
      return cached ? new Set(JSON.parse(cached) as string[]) : new Set()
    } catch { return new Set() }
  })
  const [navScopeReady, setNavScopeReady] = useState<boolean>(() => {
    try { return localStorage.getItem('crmHiddenNav') != null } catch { return false }
  })
  // Shift time clock (header button) — the caller's open segment, if any.
  // kind: 'work' | 'lunch' | 'break'. Separate from the deal-based
  // timesheet clock (RunningClockBadge).
  const [tcOpen, setTcOpen] = useState<{ id: number; clock_in: string; kind: 'work' | 'lunch' | 'break' } | null>(null)
  const [tcPunching, setTcPunching] = useState(false)
  // Confirmation pop-out: clicking the button while clocked in opens this
  // instead of punching immediately (lunch / break / clock out choices).
  const [tcMenuOpen, setTcMenuOpen] = useState(false)
  // Forgot-to-clock-out prompt: shown when the open work shift began on a prior
  // NY calendar day. Suppressed once the user snoozes it for the session or a
  // correction for this punch is already pending office approval.
  const [tcForgotShow, setTcForgotShow] = useState(false)
  const tcForgotCheckedRef = useRef<number | null>(null)

  // Admin impersonation — "login as" another employee
  const [impersonating, setImpersonating] = useState<{ userId: number; email: string; name: string; role: string; isAdmin?: number; adminName?: string; adminEmail?: string } | null>(() => {
    try { const s = localStorage.getItem('crm_impersonate'); return s ? JSON.parse(s) : null } catch { return null }
  })
  const [impersonatePickerOpen, setImpersonatePickerOpen] = useState(false)
  const [impersonateSearch, setImpersonateSearch] = useState('')
  const impersonateRef = useRef<HTMLDivElement>(null)
  // Portaled to <body> so it stacks above the docked phone (--phone-z 9998).
  const impersonatePopRef = useRef<HTMLDivElement>(null)
  const [impersonatePos, setImpersonatePos] = useState<{ top: number; right: number } | null>(null)
  // Use the effective user's role — when impersonating, this is the impersonated
  // user's role; otherwise it's the actually-authenticated user's role. The
  // whole point of impersonation is to see the world as the other user, so
  // admin-gated UI (incl. the Preferences nav and the impersonation picker
  // itself) follows the impersonated identity.
  const effectiveRole = impersonating ? impersonating.role : (currentUser?.role || '')
  const effectiveIsAdmin = impersonating ? (impersonating.isAdmin ?? 0) : (currentUser?.isAdmin ?? 0)
  const effectiveCanonicalRole = canonicalRoleClient(effectiveRole, effectiveIsAdmin)
  const isRealAdmin = effectiveCanonicalRole === 'super_admin' || effectiveCanonicalRole === 'admin'
  const effectiveUser = impersonating ? { email: impersonating.email, name: impersonating.name, role: impersonating.role, userId: impersonating.userId } : currentUser

  // Close impersonate picker on outside click
  useEffect(() => {
    if (!impersonatePickerOpen) return
    const handler = (e: MouseEvent) => {
      const t = e.target as Node
      if (impersonateRef.current?.contains(t) || impersonatePopRef.current?.contains(t)) return
      setImpersonatePickerOpen(false)
    }
    const reposition = () => {
      const r = impersonateRef.current?.getBoundingClientRect()
      if (r) setImpersonatePos({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) })
    }
    document.addEventListener('mousedown', handler)
    window.addEventListener('resize', reposition)
    return () => { document.removeEventListener('mousedown', handler); window.removeEventListener('resize', reposition) }
  }, [impersonatePickerOpen])

  const currentUserId = effectiveUser?.userId ?? 0

  // Inbox — compose modal state (shared across views so "Email" quick-action
  // buttons on deal/lead/contact pages can open it with prefilled context).
  const [composeOpen, setComposeOpen] = useState(false)
  const [composeContext, setComposeContext] = useState<ComposeContext | undefined>(undefined)
  const [composeToast, setComposeToast] = useState<string | null>(null)
  useEffect(() => {
    if (!composeToast) return
    const t = setTimeout(() => setComposeToast(null), 3200)
    return () => clearTimeout(t)
  }, [composeToast])


  // -------------------------------------------------------------------------
  // Time tracking — open entry + ticking clock. Sticky across views once a
  // clock-in is in flight; the floating clock-out pill in the shell reads
  // this state. The ticker uses a single setInterval while an entry is open.
  // -------------------------------------------------------------------------
  const [timeOpenEntry, setTimeOpenEntry] = useState<TimeEntry | null>(null)
  const [timeTick, setTimeTick] = useState(0)
  const [timeLaborSummary, setTimeLaborSummary] = useState<LaborSummary | null>(null)
  const [timeLaborLoading, setTimeLaborLoading] = useState(false)
  const [timeClocking, setTimeClocking] = useState<'in' | 'out' | null>(null)
  const [timeEntriesModalOpen, setTimeEntriesModalOpen] = useState<{ dealId: number | null; title?: string } | null>(null)
  const [timeClockOutPrompt, setTimeClockOutPrompt] = useState<{ entryId: number; defaultCategory: TimeCategory } | null>(null)
  const [timeClockOutNotes, setTimeClockOutNotes] = useState('')
  const [timeToast, setTimeToast] = useState<string | null>(null)
  useEffect(() => {
    if (!timeToast) return
    const t = setTimeout(() => setTimeToast(null), 3200)
    return () => clearTimeout(t)
  }, [timeToast])

  // Bootstrap the open entry on login so the sticky pill renders after a
  // refresh. Manual only — no polling.
  useEffect(() => {
    if (!currentUser?.userId) return
    fetchOpenTimeEntry().then((e) => setTimeOpenEntry(e)).catch(() => { /* non-fatal */ })
    // We specifically only want this to run once on mount (after auth). Any
    // re-auth changes currentUser.userId so dep on .userId is enough.
  }, [currentUser?.userId])

  // The 1Hz ticker that drives the running-clock badge used to live as a
  // top-level `setTimeTick` useState here, which forced the entire App
  // component (~50K lines of JSX) to re-render every second. After a few
  // hours open, the renderer process accumulated enough detached-DOM +
  // intermediate-render pressure to trigger Chromium's
  // Crashpad_HandlerDidNotRespond tab crash. The tick is now isolated
  // inside <RunningClockBadge> below so only the badge re-renders each
  // second — App runs at 0Hz while nothing else changes.
  void timeTick // legacy; kept to avoid unused-var lint until we delete
                // the useState call on line 18947 in a follow-up cleanup.

  // Best-effort geolocation. Resolves in <=10s and never rejects — returns
  // `null` on timeout, denial, or browser without support. Callers treat a
  // missing fix as "no GPS available".
  const captureGeo = (): Promise<{ lat: number; lng: number; accuracy: number } | null> => {
    return new Promise((resolve) => {
      if (!('geolocation' in navigator)) { resolve(null); return }
      let done = false
      const to = window.setTimeout(() => { if (!done) { done = true; resolve(null) } }, 10000)
      navigator.geolocation.getCurrentPosition(
        (pos) => { if (done) return; done = true; window.clearTimeout(to); resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: Math.round(pos.coords.accuracy || 0) }) },
        () => { if (done) return; done = true; window.clearTimeout(to); resolve(null) },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 },
      )
    })
  }

  // Clock-in handler: captures GPS best-effort, POSTs, updates open-entry,
  // refreshes activity timeline + labor summary for the deal.
  const handleClockIn = async (opts: { dealId?: number | null; category?: TimeCategory; notes?: string | null }) => {
    if (timeOpenEntry) { setTimeToast('You already have an open timer'); return }
    setTimeClocking('in')
    try {
      const geo = await captureGeo()
      const res = await clockInTime({
        deal_id: opts.dealId ?? null,
        payroll_category: opts.category || 'field',
        notes: opts.notes || null,
        lat: geo?.lat ?? null,
        lng: geo?.lng ?? null,
        accuracy_m: geo?.accuracy ?? null,
      })
      // Re-fetch the full open entry row so the pill has everything.
      const open = await fetchOpenTimeEntry()
      setTimeOpenEntry(open)
      setTimeToast(`Clocked in${geo ? ' (GPS locked)' : ' (no GPS)'}`)
      void res
      if (opts.dealId) {
        // Pull new activity + summary so the UI updates without polling.
        fetchDealActivities(opts.dealId).then(setDealActivities).catch(() => {})
        fetchDealLaborSummary(opts.dealId).then(setTimeLaborSummary).catch(() => {})
      }
    } catch (e: any) {
      if (e && typeof e.openId === 'number') {
        // Race — reconcile with server state and try to show a useful message.
        const open = await fetchOpenTimeEntry().catch(() => null)
        if (open) setTimeOpenEntry(open)
      }
      setTimeToast(e?.message || 'Clock-in failed')
    } finally {
      setTimeClocking(null)
    }
  }

  // Clock-out handler. Pops a small inline prompt for notes; the submit
  // branch is separate below so the prompt can be re-used from the modal.
  const handleClockOutSubmit = async (notes: string | null) => {
    if (!timeOpenEntry) return
    setTimeClocking('out')
    try {
      const geo = await captureGeo()
      const updated = await clockOutTime(timeOpenEntry.id, {
        lat: geo?.lat ?? null,
        lng: geo?.lng ?? null,
        accuracy_m: geo?.accuracy ?? null,
        notes: notes || null,
      })
      setTimeOpenEntry(null)
      setTimeClockOutPrompt(null)
      setTimeClockOutNotes('')
      setTimeToast(`Clocked out · ${formatMinutesLabel(updated.duration_minutes || 0)}`)
      if (updated.deal_id) {
        fetchDealActivities(updated.deal_id).then(setDealActivities).catch(() => {})
        fetchDealLaborSummary(updated.deal_id).then(setTimeLaborSummary).catch(() => {})
      }
    } catch (e: any) {
      setTimeToast(e?.message || 'Clock-out failed')
    } finally {
      setTimeClocking(null)
    }
  }

  // Sync view ↔ URL hash (with preferences sub-path support)
  const updatePrefsHash = (tab?: string, subTab?: string, extra?: string) => {
    const parts = ['preferences']
    if (tab) parts.push(tab)
    if (subTab) parts.push(subTab)
    if (extra) parts.push(extra)
    window.location.hash = `/${parts.join('/')}`
  }
  const isNotificationPrefsHash = (hash: string) => {
    const raw = hash.replace('#/', '').replace('#', '')
    const seg = raw.split('?')[0].split('/').filter(Boolean)
    return seg[0] === 'preferences' && seg[1] === 'notifications'
  }
  const triggerPrefsUnsavedWarning = () => {
    setPrefsUnsavedBlocked(true)
    if (prefsUnsavedNudgeTimerRef.current) {
      window.clearTimeout(prefsUnsavedNudgeTimerRef.current)
      prefsUnsavedNudgeTimerRef.current = null
    }
    setPrefsUnsavedNudge(false)
    window.requestAnimationFrame(() => {
      setPrefsUnsavedNudge(true)
      prefsUnsavedNudgeTimerRef.current = window.setTimeout(() => setPrefsUnsavedNudge(false), 1100)
    })
  }
  const blockNotificationPrefsExit = () => {
    if (notifPrefsDirtyCountRef.current <= 0) return false
    triggerPrefsUnsavedWarning()
    return true
  }
  // Combined dirtiness of the notifications-prefs panel — both the delivery
  // channel and the email->bell scope feed the one dirty counter that gates
  // the footer save/cancel + navigation guard.
  const recomputeNotifDirty = (nextChannel: EmailNotifChannel, nextScope: EmailNotifyScope) => {
    const dirty = (nextChannel !== emailNotifBaselineRef.current ? 1 : 0)
      + (nextScope !== emailNotifyScopeBaselineRef.current ? 1 : 0)
    setNotifPrefsDirtyCount(dirty)
  }
  const setEmailNotifChannelDraft = (channel: EmailNotifChannel) => {
    if (channel === emailNotifChannel) return
    setEmailNotifChannel(channel)
    setEmailNotifSaveError(null)
    setEmailNotifTestStatus('idle')
    setEmailNotifTestError(null)
    recomputeNotifDirty(channel, emailNotifyScope)
  }
  const setEmailNotifyScopeDraft = (scope: EmailNotifyScope) => {
    if (scope === emailNotifyScope) return
    setEmailNotifyScope(scope)
    setEmailNotifSaveError(null)
    recomputeNotifDirty(emailNotifChannel, scope)
  }
  const setPrefsTab = (tab: PrefsTab) => {
    if (tab !== 'notifications' && blockNotificationPrefsExit()) return false
    setPrefsTabRaw(tab)
    updatePrefsHash(tab, tab === 'general' ? 'profile' : tab === 'notifications' ? 'email_desktop' : tab === 'account' ? 'general' : undefined)
    if (tab === 'general') setGeneralSubTabRaw('profile')
    if (tab === 'notifications') setNotifSubTabRaw('email_desktop')
    if (tab === 'account') setAcctDefaultsSubTabRaw('general')
    return true
  }
  const setGeneralSubTab = (sub: GeneralSubTab) => {
    if (blockNotificationPrefsExit()) return
    setGeneralSubTabRaw(sub)
    updatePrefsHash('general', sub)
  }
  const setNotifSubTab = (sub: NotifSubTab) => {
    setNotifSubTabRaw(sub)
    notificationPrefsSafeHashRef.current = `#/preferences/notifications/${sub}`
    updatePrefsHash('notifications', sub)
  }
  const setAcctDefaultsSubTab = (sub: AcctDefaultsSubTab) => {
    if (blockNotificationPrefsExit()) return
    setAcctDefaultsSubTabRaw(sub)
    updatePrefsHash('account', sub)
  }
  // Forecasts state
  const [forecastData, setForecastData] = useState<any>(null)
  const [forecastLoading, setForecastLoading] = useState(false)
  const [forecastPipeline, setForecastPipeline] = useState('Estimation')
  const [forecastPeriod, setForecastPeriod] = useState<'monthly' | 'quarterly'>('monthly')
  const [forecastMonth, setForecastMonth] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` })
  const [forecastTeamView, setForecastTeamView] = useState(false)
  const [showGoalModal, setShowGoalModal] = useState(false)
  const [goalAmount, setGoalAmount] = useState('')

  // Payments state
  const [paymentLinksData, setPaymentLinksData] = useState<any[]>([])
  const [paymentLinksTotal, setPaymentLinksTotal] = useState(0)
  const [paymentLinksLoading, setPaymentLinksLoading] = useState(false)
  const [paymentsData, setPaymentsData] = useState<any[]>([])
  const [paymentsTotal, setPaymentsTotal] = useState(0)
  const [paymentsLoading, setPaymentsLoading] = useState(false)
  const [paymentsSubView, setPaymentsSubView] = useState<'links' | 'history'>('links')
  const [paymentsSearch, setPaymentsSearch] = useState('')
  const [showCreatePaymentLink, setShowCreatePaymentLink] = useState(false)
  const [newLinkName, setNewLinkName] = useState('')
  const [newLinkDesc, setNewLinkDesc] = useState('')
  const [newLinkItems, setNewLinkItems] = useState<{name: string; quantity: number; unit_price: number}[]>([{ name: '', quantity: 1, unit_price: 0 }])

  // Forecasts 6-row template state
  const [forecastSelectedRows, setForecastSelectedRows] = useState<Set<number>>(new Set())
  const [forecastSort, setForecastSort] = useState<{field: string; order: 'ASC' | 'DESC'}>({ field: 'close_date', order: 'DESC' })
  const [forecastPage, setForecastPage] = useState(1)
  const [forecastQuickFiltersOpen, setForecastQuickFiltersOpen] = useState(false)
  const [forecastSearch, setForecastSearch] = useState('')
  const [forecastStatusFilter, setForecastStatusFilter] = useState('')

  // Payments 6-row template state
  const [paymentSelectedRows, setPaymentSelectedRows] = useState<Set<number>>(new Set())
  const [paymentSort, setPaymentSort] = useState<{field: string; order: 'ASC' | 'DESC'}>({ field: 'created_at', order: 'DESC' })
  const [paymentPage, setPaymentPage] = useState(1)
  const [paymentQuickFiltersOpen, setPaymentQuickFiltersOpen] = useState(false)

  // Knowledge Base state
  const [kbArticles, setKbArticles] = useState<any[]>([])
  const [kbArticlesTotal, setKbArticlesTotal] = useState(0)
  const [kbArticlesLoading, setKbArticlesLoading] = useState(false)
  const [kbCategories, setKbCategories] = useState<any[]>([])
  const [kbStatusFilter, setKbStatusFilter] = useState('')
  const [kbSearch, setKbSearch] = useState('')
  const [kbPage, setKbPage] = useState(1)
  const [kbSort, setKbSort] = useState<{ field: string; order: 'ASC' | 'DESC' }>({ field: 'updated_at', order: 'DESC' })
  const [kbSelectedRows, setKbSelectedRows] = useState<Set<number>>(new Set())
  const [kbEditingArticle, setKbEditingArticle] = useState<any>(null)
  const [kbArticleTitle, setKbArticleTitle] = useState('')
  const [kbArticleBody, setKbArticleBody] = useState('')
  const [kbArticleCategory, setKbArticleCategory] = useState<number | null>(null)
  const [kbArticleTags, setKbArticleTags] = useState('')
  const [kbArticleStatus, setKbArticleStatus] = useState('draft')
  const [showKbEditor, setShowKbEditor] = useState(false)
  const [showKbCategoryModal, setShowKbCategoryModal] = useState(false)
  const [newCategoryName, setNewCategoryName] = useState('')

  // Customer Portal state
  const [portalSettings, setPortalSettingsData] = useState<Record<string, string>>({})
  const [portalTickets, setPortalTicketsData] = useState<any[]>([])
  const [portalTicketsTotal, setPortalTicketsTotal] = useState(0)
  const [portalTicketsLoading, setPortalTicketsLoading] = useState(false)
  const [portalSubView, setPortalSubView] = useState<'settings' | 'tickets'>('tickets')
  const [portalTicketSearch, setPortalTicketSearch] = useState('')
  const [portalTicketPage, setPortalTicketPage] = useState(1)
  const [portalTicketSort, setPortalTicketSort] = useState<{ field: string; order: 'ASC' | 'DESC' }>({ field: 'created_at', order: 'DESC' })
  const [portalTicketSelectedRows, setPortalTicketSelectedRows] = useState<Set<number>>(new Set())
  const [portalTicketStatusFilter, setPortalTicketStatusFilter] = useState('')
  const [portalTicketDetail, setPortalTicketDetail] = useState<any>(null)
  const [portalTicketComments, setPortalTicketComments] = useState<any[]>([])
  const [portalNewComment, setPortalNewComment] = useState('')

  // Feedback Surveys state
  const [surveysData, setSurveysData] = useState<any[]>([])
  const [surveysTotal, setSurveysTotal] = useState(0)
  const [surveysLoading, setSurveysLoading] = useState(false)
  const [surveyDetail, setSurveyDetail] = useState<any>(null)
  const [surveySearch, setSurveySearch] = useState('')
  const [surveyPage, setSurveyPage] = useState(1)
  const [surveySort, setSurveySort] = useState<{ field: string; order: 'ASC' | 'DESC' }>({ field: 'created_at', order: 'DESC' })
  const [surveySelectedRows, setSurveySelectedRows] = useState<Set<number>>(new Set())
  const [surveyTypeFilter, setSurveyTypeFilter] = useState('')
  const [_surveyQuestions, setSurveyQuestions] = useState<any[]>([]); void _surveyQuestions
  const [surveyStats, setSurveyStats] = useState<any>(null)
  const [surveyResponses, setSurveyResponses] = useState<any[]>([])
  const [surveyAnalytics, setSurveyAnalytics] = useState<any>(null)
  const [surveyDetailTab, setSurveyDetailTab] = useState<'overview' | 'responses' | 'settings'>('overview')
  const [showCreateSurvey, setShowCreateSurvey] = useState(false)
  const [newSurveyName, setNewSurveyName] = useState('')
  const [newSurveyType, setNewSurveyType] = useState<'nps' | 'csat' | 'ces' | 'custom'>('nps')
  const [newSurveyTitle, setNewSurveyTitle] = useState('')

  // Empty/root loads land on the command dashboard, but explicit hash routes
  // must survive browser refreshes. The hash parser below owns deep-link state.
  useEffect(() => {
    const hash = window.location.hash
    if (!hash || hash === '#/' || hash === '#') {
      setActiveViewRaw('command')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setActiveView = (view: ViewMode) => {
    if (blockNotificationPrefsExit()) return false
    setActiveViewRaw(view)
    setSecondaryMenu(null)
    if (view === 'preferences') {
      updatePrefsHash('general', 'profile')
      setPrefsTabRaw('general')
      setGeneralSubTabRaw('profile')
    } else {
      // `companies` view → canonical `/clients` hash.
      const seg = view === 'command' ? '' : view === 'companies' ? 'clients' : view
      window.location.hash = view === 'command' ? '/' : `/${seg}`
    }
    return true
  }

  // --- HR Center flyout + deep-URL navigation helpers --------
  // navigateHrTab drives BOTH the flyout submenu and the in-view
  // top tab strip. They update the hash (shareable URL) AND the local tab state.
  // `selection` adds a third path segment (#/hr-center/forms/<formKey>, etc.).
  const navigateHrTab = (tab: HrTabKey, selection?: string | null) => {
    if (blockNotificationPrefsExit()) return
    setSecondaryMenu(null)
    setFlyoutActivated(false)
    // Timesheets is a standalone top-level view, not an hrPortalView.
    if (tab === 'timesheets') {
      setActiveView('timesheets')
      return
    }
    suppressHrHashSyncRef.current = true
    setActiveViewRaw('hr-center')
    setHrPortalView(tab)
    if (tab === 'forms') setHrPortalFormKey(selection ?? null)
    if (tab === 'employees') setHrPortalSelectedId(selection ?? null)
    if (tab === 'pipelines' && selection) setHrPortalActivePipeline(selection)
    const path = selection ? `/hr-center/${tab}/${encodeURIComponent(selection)}` : `/hr-center/${tab}`
    if (window.location.hash !== `#${path}`) window.location.hash = path
  }

  useEffect(() => {
    let prevHashForBack = window.location.hash || '#/contacts'
    const onHash = () => {
      // Rewrite the legacy `/companies` + `/organizations` aliases → canonical `/clients`
      // before any parsing so the URL the user sees is always canonical.
      redirectLegacyCompaniesHash()
      redirectLegacyMarketingHash() // bp-mkt-ia-v1 — fold legacy marketing member tabs → stage home
      const seg = getHashSegments()
      const currentHash = window.location.hash
      if (notifPrefsDirtyCountRef.current > 0 && !isNotificationPrefsHash(currentHash)) {
        triggerPrefsUnsavedWarning()
        const fallbackHash = isNotificationPrefsHash(notificationPrefsSafeHashRef.current)
          ? notificationPrefsSafeHashRef.current
          : '#/preferences/notifications/email_desktop'
        try {
          window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}${fallbackHash}`)
        } catch {
          window.location.hash = fallbackHash
        }
        return
      }
      if (isNotificationPrefsHash(currentHash)) {
        notificationPrefsSafeHashRef.current = currentHash
      }
      // Track return hash for contact / organization detail back button
      if ((seg[0] === 'contacts' || seg[0] === 'clients') && seg[1] === 'record' && seg[2]) {
        if (!prevHashForBack.includes('/contacts/record/') && !prevHashForBack.includes('/clients/record/') && !prevHashForBack.includes('/organizations/record/')) {
          contactDetailReturnHash.current = prevHashForBack
        }
      }
      prevHashForBack = currentHash
      setActiveViewRaw(getViewFromHash())
      if (seg[0] === 'preferences') {
        if (seg[1]) {
          const resolved = resolvePrefsTab(seg[1])
          setPrefsTabRaw(resolved)
          if (resolved !== seg[1]) {
            try { window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}#/preferences/${resolved}`) } catch { /* non-fatal */ }
          }
        }
        if (seg[1] === 'general' && seg[2]) setGeneralSubTabRaw(seg[2] as GeneralSubTab)
        if (seg[1] === 'notifications' && seg[2]) setNotifSubTabRaw(seg[2] as NotifSubTab)
        if (seg[1] === 'account' && seg[2]) setAcctDefaultsSubTabRaw(seg[2] as AcctDefaultsSubTab)
      }
      if (seg[0] === 'accounting') {
        // Drill-through wave B: parseAccountingHash is the single shared
        // parser (also called at the initial-mount site below) so
        // `#/accounting/<sub>?key=value` deep links resolve identically
        // regardless of which site handled the navigation. Params are
        // applied here, not scrubbed from the URL — links stay shareable
        // and re-applying on a repeat hashchange is idempotent.
        const { sub, params } = parseAccountingHash(currentHash.replace(/^#\/?/, ''))
        if (sub) setAccountingSubView(sub as AccountingSubView)
        else setAccountingSubView('overview')
        // `!== undefined` (not a truthiness check) so an explicitly-empty
        // value (e.g. `?invoice=` from JEDetailDrawer's source link) still
        // clears a stale search rather than being ignored.
        if (sub === 'accounts-receivable' && params.invoice !== undefined) setArSearch(params.invoice)
        if (sub === 'accounts-payable' && params.bill !== undefined) setApSearch(params.bill)
        if (sub === 'journal-entries' && params.search) setJeSearch(params.search)
        if (sub === 'journal-entries' && params.je) setJeDetailId(Number(params.je) || null)
      }
      // bp-mkt-v1 — Marketing Hub deep link: #/marketing or #/marketing/<sub>
      if (seg[0] === 'marketing') {
        const sub = seg[1] as MarketingSubView | undefined
        setMarketingSubView(sub || 'hub')
      }
      // bp-camp-v1 - Campaigns deep links are normalized by CampaignsView;
      // keep the shell's sub-view state aligned for nav highlighting.
      if (seg[0] === 'campaigns') {
        setCampaignsSubView(seg[2] === 'flow' ? 'flow' : seg[1] ? 'detail' : 'list')
      }
      // Contact / Organization record detail deep link: #/clients/record/{clientId}
      // (`/companies/record/...` and `/organizations/record/...` are redirected above.)
      // Both map to the same full-page record view — `preferred_client.client_type`
      // determines whether it's displayed as a person or an organization.
      if ((seg[0] === 'contacts' || seg[0] === 'clients') && seg[1] === 'record' && seg[2]) {
        const clientId = decodeURIComponent(seg[2])
        const tab = seg[3] as 'about' | 'activities' | 'revenue' | undefined
        setContactDetailOpen(true)
        setContactDetailTab(tab === 'activities' || tab === 'revenue' ? tab : 'about')
        // Abort any in-flight client fetch so a slow stale load can't clobber the
        // record we're now navigating to (route changes rapidly via redirects).
        try { clientRedirectAbortRef.current?.abort() } catch { /* non-fatal */ }
        const ctrl = new AbortController()
        clientRedirectAbortRef.current = ctrl
        const segRoot = seg[0] as 'contacts' | 'clients'
        fetchClient(clientId).then(c => {
          if (ctrl.signal.aborted) return
          // MERGED-LOSER REDIRECT (centralized): a merged-away duplicate shell
          // points at its survivor via `merged_into`. applyClientOrRedirect
          // redirects to the survivor (never for a legitimately-archived-but-
          // active org — that has no merged_into), else sets previewClient.
          applyClientOrRedirect(c, { segRoot })
        }).catch(() => {})
      } else if (seg[0] !== 'clients' || !seg[1]) {
        // Left the contact/organization detail — close it
        setContactDetailOpen(false)
      }
      // Deal record detail deep link: #/deals/record/{id}
      if (seg[0] === 'deals' && seg[1] === 'record' && seg[2]) {
        const dealRawId = decodeURIComponent(seg[2])
        if (shouldSkipStaleTutorialFetch(dealRawId, !!(window as any).__kbWalkthroughActive)) {
          // Stale TUT- tutorial fixture id on a shim-less boot — the real API
          // 404s (by design). Don't open/fetch; bounce to the deals list so the
          // `dealDetailOpen && !selectedDeal` spinner can never hang.
          setDealDetailOpen(false); setSelectedDeal(null)
          try { window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}#/deals`) } catch { window.location.hash = '/deals' }
        } else {
          setDealDetailOpen(true)
          setDealDetailTab('about')
          // Pass the RAW id (not Number()) so the walkthrough Test Deal fixture
          // id `TUT-test-deal-deal` fetches /api/crm/deals/TUT-test-deal-deal
          // (served by the shim) instead of /api/crm/deals/NaN → 404 → revert.
          // Mirrors the projects handler (fetchCrmProject(projId)). Numeric ids
          // interpolate identically, so real deals are unaffected.
          fetchDeal(dealRawId).then(d => setSelectedDeal(d)).catch(() => {
            // 404 / fetch error: leave the loading state instead of swallowing —
            // close the detail + reset the hash so the spinner never hangs.
            setDealDetailOpen(false); setSelectedDeal(null)
            try { window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}#/deals`) } catch { window.location.hash = '/deals' }
          })
        }
      } else if (seg[0] !== 'deals' || seg[1] !== 'record') {
        setDealDetailOpen(false)
      }
      // There is NO full-page lead record anymore — clicking a lead row opens the
      // PREVIEW SIDE PANEL only. A legacy/stale #/leads/record/{id} deep link is
      // normalized to the leads list and the lead is opened in the preview pane.
      if (seg[0] === 'leads' && seg[1] === 'record' && seg[2]) {
        const leadId = seg[2]
        try {
          window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}#/leads`)
        } catch { window.location.hash = '/leads' }
        fetchLead(Number(leadId)).then(l => { if (l) setPreviewLead(l) }).catch(() => {})
      }
      // Project routes: App chooses record vs workspace; workspace owns all
      // segments beneath /workspace.
      const workspaceRoute = parseWorkspaceHash(currentHash)
      if (workspaceRoute) {
        setProjectWorkspaceId(workspaceRoute.projectId)
        setProjectDetailOpen(false)
        setProjectDetailData(null)
      } else if (seg[0] === 'projects' && seg[1] === 'record' && seg[2]) {
        const projId = decodeURIComponent(seg[2])
        setProjectWorkspaceId(null)
        if (shouldSkipStaleTutorialFetch(projId, !!(window as any).__kbWalkthroughActive)) {
          // Stale TUT- tutorial fixture id on a shim-less boot — the real API
          // 404s (by design). Don't open/fetch; bounce to the projects list so
          // the `projectDetailOpen && !projectDetailData` spinner can never hang.
          setProjectDetailOpen(false); setProjectDetailData(null)
          try { window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}#/projects`) } catch { window.location.hash = '/projects' }
        } else {
          const tab = seg[3] as 'about' | 'activities' | 'revenue' | undefined
          setProjectDetailOpen(true)
          setProjectDetailTab(tab === 'activities' || tab === 'revenue' ? tab : 'about')
          fetchCrmProject(projId).then(p => setProjectDetailData(p)).catch(() => {
            // 404 / fetch error: leave the loading state instead of swallowing —
            // close the detail + reset the hash so the spinner never hangs.
            setProjectDetailOpen(false); setProjectDetailData(null)
            try { window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}#/projects`) } catch { window.location.hash = '/projects' }
          })
        }
      } else {
        setProjectWorkspaceId(null)
        setProjectDetailOpen(false)
      }
      // HR Center nested routing: #/hr-center/<tab>[/<selection>]
      //   #/hr-center                       -> default tab (dashboard)
      //   #/hr-center/forms/<formKey>       -> Forms tab + selected form
      //   #/hr-center/employees/<empId>     -> Employees tab + employee detail
      //   #/hr-center/pipelines/<pipelineId>-> Pipelines tab + active pipeline
      //   #/hr-center/<tab>                 -> that tab
      // Legacy `#/hr-center?employee={id}` query deep-link still honored.
      if (seg[0] === 'hr-center') {
        const q = getHashQuery()
        const empId = q.get('employee')
        if (empId) {
          // Legacy query form: rewrite to the canonical nested path.
          suppressHrHashSyncRef.current = true
          setHrPortalView('employees')
          setHrPortalSelectedId(empId)
          clearHashQuery()
          try { window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}#/hr-center/employees/${encodeURIComponent(empId)}`) } catch { /* non-fatal */ }
        } else {
          const rawTab = seg[1]
          const selection = seg[2] ? decodeURIComponent(seg[2]) : null
          if (!rawTab) {
            suppressHrHashSyncRef.current = true
            setHrPortalView(HR_DEFAULT_TAB)
            if (window.location.hash === '#/hr-center') {
              try { window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}#/hr-center/${HR_DEFAULT_TAB}`) } catch { /* non-fatal */ }
            }
          } else if (HR_PORTAL_TABS.has(rawTab)) {
            suppressHrHashSyncRef.current = true
            setHrPortalView(rawTab as any)
            if (rawTab === 'forms') setHrPortalFormKey(selection)
            if (rawTab === 'employees') setHrPortalSelectedId(selection)
            if (rawTab === 'pipelines' && selection) setHrPortalActivePipeline(selection)
          }
          // Unknown HR tab → leave hrPortalView as-is (graceful no-op).
        }
      }
      // wave-2: Calls deep-link — #/calls?activity={id} → scroll/highlight
      if (seg[0] === 'calls') {
        const q = getHashQuery()
        const aid = q.get('activity')
        if (aid && /^\d+$/.test(aid)) {
          const n = Number(aid)
          setHighlightedCallActivityId(n)
          window.setTimeout(() => setHighlightedCallActivityId(null), 2000)
          clearHashQuery()
        }
      }
      // wave-2: Contact Record deep-link — #/contacts/record/{id}?call={activityId}
      if ((seg[0] === 'contacts' || seg[0] === 'clients') && seg[1] === 'record') {
        const q = getHashQuery()
        const callId = q.get('call')
        if (callId && /^\d+$/.test(callId)) {
          const n = Number(callId)
          setHighlightedCallActivityId(n)
          // Switch to the Activities tab so the user sees the row immediately.
          setContactDetailTab('activities')
          window.setTimeout(() => setHighlightedCallActivityId(null), 2000)
          clearHashQuery()
        }
      }
    }
    window.addEventListener('hashchange', onHash)
    // Also handle initial page load — rewrite the legacy `/companies`+`/organizations` aliases first.
    redirectLegacyCompaniesHash()
    redirectLegacyMarketingHash() // bp-mkt-ia-v1 — fold legacy marketing member tabs → stage home
    const initSeg = getHashSegments()
    if (initSeg[0] === 'accounting') {
      // Same shared parser as the hashchange site above — a fresh page
      // load on a deep link (e.g. `#/accounting/journal-entries?je=42`)
      // must resolve identically to navigating there while already open.
      const { sub: initAcctSub, params: initAcctParams } = parseAccountingHash(window.location.hash.replace(/^#\/?/, ''))
      if (initAcctSub) setAccountingSubView(initAcctSub as AccountingSubView)
      else setAccountingSubView('overview')
      if (initAcctSub === 'accounts-receivable' && initAcctParams.invoice !== undefined) setArSearch(initAcctParams.invoice)
      if (initAcctSub === 'accounts-payable' && initAcctParams.bill !== undefined) setApSearch(initAcctParams.bill)
      if (initAcctSub === 'journal-entries' && initAcctParams.search) setJeSearch(initAcctParams.search)
      if (initAcctSub === 'journal-entries' && initAcctParams.je) setJeDetailId(Number(initAcctParams.je) || null)
    }
    // bp-mkt-v1 — Marketing Hub deep link on initial load (mirrors the hashchange parser)
    if (initSeg[0] === 'marketing') {
      const sub = initSeg[1] as MarketingSubView | undefined
      setMarketingSubView(sub || 'hub')
    }
    // bp-camp-v1 - Campaigns deep link on initial load.
    if (initSeg[0] === 'campaigns') {
      setCampaignsSubView(initSeg[2] === 'flow' ? 'flow' : initSeg[1] ? 'detail' : 'list')
    }
    // HR Center nested deep-link on initial load (mirrors the hashchange parser)
    if (initSeg[0] === 'hr-center' && initSeg[1] && HR_PORTAL_TABS.has(initSeg[1])) {
      const selection = initSeg[2] ? decodeURIComponent(initSeg[2]) : null
      suppressHrHashSyncRef.current = true
      setHrPortalView(initSeg[1] as any)
      if (initSeg[1] === 'forms') setHrPortalFormKey(selection)
      if (initSeg[1] === 'employees') setHrPortalSelectedId(selection)
      if (initSeg[1] === 'pipelines' && selection) setHrPortalActivePipeline(selection)
    } else if (initSeg[0] === 'hr-center' && !initSeg[1] && window.location.hash === '#/hr-center') {
      suppressHrHashSyncRef.current = true
      setHrPortalView(HR_DEFAULT_TAB)
      try { window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}#/hr-center/${HR_DEFAULT_TAB}`) } catch { /* non-fatal */ }
    }
    if (initSeg[0] === 'preferences') {
      if (initSeg[1]) {
        const resolved = resolvePrefsTab(initSeg[1])
        setPrefsTabRaw(resolved)
        if (resolved !== initSeg[1]) {
          try { window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}#/preferences/${resolved}`) } catch { /* non-fatal */ }
        }
      }
      if (initSeg[1] === 'general' && initSeg[2]) setGeneralSubTabRaw(initSeg[2] as GeneralSubTab)
      if (initSeg[1] === 'notifications' && initSeg[2]) setNotifSubTabRaw(initSeg[2] as NotifSubTab)
      if (initSeg[1] === 'account' && initSeg[2]) setAcctDefaultsSubTabRaw(initSeg[2] as AcctDefaultsSubTab)
    }
    if ((initSeg[0] === 'contacts' || initSeg[0] === 'clients') && initSeg[1] === 'record' && initSeg[2]) {
      const clientId = decodeURIComponent(initSeg[2])
      const initTab = initSeg[3] as 'about' | 'activities' | 'revenue' | undefined
      setContactDetailOpen(true)
      setContactDetailTab(initTab === 'activities' || initTab === 'revenue' ? initTab : 'about')
      const initSegRoot = initSeg[0] as 'contacts' | 'clients'
      fetchClient(clientId).then(c => applyClientOrRedirect(c, { segRoot: initSegRoot })).catch(() => {})
    }
    if (initSeg[0] === 'deals' && initSeg[1] === 'record' && initSeg[2]) {
      const initDealRawId = decodeURIComponent(initSeg[2])
      if (shouldSkipStaleTutorialFetch(initDealRawId, !!(window as any).__kbWalkthroughActive)) {
        // Stale TUT- fixture id left in the hash after a walkthrough hard-refresh
        // /teardown — the real API 404s. Don't open/fetch; reset to the list so
        // the `dealDetailOpen && !selectedDeal` spinner can never hang.
        setDealDetailOpen(false); setSelectedDeal(null)
        try { window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}#/deals`) } catch { window.location.hash = '/deals' }
      } else {
        setDealDetailOpen(true)
        setDealDetailTab('about')
        // RAW id (not Number()) so the TUT- fixture id resolves via the shim
        // instead of NaN → 404 → revert. See the hashchange handler above.
        fetchDeal(initDealRawId).then(d => setSelectedDeal(d)).catch(() => {
          // 404 / fetch error: leave the loading state instead of swallowing.
          setDealDetailOpen(false); setSelectedDeal(null)
          try { window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}#/deals`) } catch { window.location.hash = '/deals' }
        })
      }
    }
    const initialWorkspaceRoute = parseWorkspaceHash(window.location.hash)
    if (initialWorkspaceRoute) {
      setProjectWorkspaceId(initialWorkspaceRoute.projectId)
      setProjectDetailOpen(false)
      setProjectDetailData(null)
    } else if (initSeg[0] === 'projects' && initSeg[1] === 'record' && initSeg[2]) {
      const initProjId = decodeURIComponent(initSeg[2])
      setProjectWorkspaceId(null)
      if (shouldSkipStaleTutorialFetch(initProjId, !!(window as any).__kbWalkthroughActive)) {
        // Stale TUT- fixture id left in the hash after a walkthrough hard-refresh
        // /teardown — the real API 404s. Don't open/fetch; reset to the list so
        // the `projectDetailOpen && !projectDetailData` spinner can never hang.
        setProjectDetailOpen(false); setProjectDetailData(null)
        try { window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}#/projects`) } catch { window.location.hash = '/projects' }
      } else {
        const initProjTab = initSeg[3] as 'about' | 'activities' | 'revenue' | undefined
        setProjectDetailOpen(true)
        setProjectDetailTab(initProjTab === 'activities' || initProjTab === 'revenue' ? initProjTab : 'about')
        fetchCrmProject(initProjId).then(p => setProjectDetailData(p)).catch(() => {
          // 404 / fetch error: leave the loading state instead of swallowing.
          setProjectDetailOpen(false); setProjectDetailData(null)
          try { window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}#/projects`) } catch { window.location.hash = '/projects' }
        })
      }
    } else {
      setProjectWorkspaceId(null)
    }
    // wave-2: HR Center deep-link on initial load
    if (initSeg[0] === 'hr-center') {
      const q = getHashQuery()
      const empId = q.get('employee')
      if (empId) {
        setHrPortalView('employees')
        setHrPortalSelectedId(empId)
        clearHashQuery()
      }
    }
    // wave-2: Calls deep-link on initial load
    if (initSeg[0] === 'calls') {
      const q = getHashQuery()
      const aid = q.get('activity')
      if (aid && /^\d+$/.test(aid)) {
        const n = Number(aid)
        setHighlightedCallActivityId(n)
        window.setTimeout(() => setHighlightedCallActivityId(null), 2000)
        clearHashQuery()
      }
    }
    // wave-2: Contact Record `?call=` deep-link on initial load
    if ((initSeg[0] === 'contacts' || initSeg[0] === 'clients') && initSeg[1] === 'record') {
      const q = getHashQuery()
      const callId = q.get('call')
      if (callId && /^\d+$/.test(callId)) {
        const n = Number(callId)
        setHighlightedCallActivityId(n)
        setContactDetailTab('activities')
        window.setTimeout(() => setHighlightedCallActivityId(null), 2000)
        clearHashQuery()
      }
    }
    return () => window.removeEventListener('hashchange', onHash)
  }, [])

  // Reverse hash-sync — keep the URL shareable as the user moves around the HR
  // Center via the in-view tab strip, dashboard cards, or selection clicks.
  // Whenever hrPortalView (or the forms/employees/pipelines selection) changes
  // while HR Center is active, rewrite the hash to match. suppressHrHashSyncRef
  // skips the rewrite for the one render where WE just applied a parsed URL, so
  // we never fight the parser or push a redundant history entry.
  useEffect(() => {
    if (activeView !== 'hr-center') return
    if (suppressHrHashSyncRef.current) { suppressHrHashSyncRef.current = false; return }
    let selection: string | null = null
    if (hrPortalView === 'forms') selection = hrPortalFormKey
    else if (hrPortalView === 'employees') selection = hrPortalSelectedId
    // (pipelines selection is intentionally NOT mirrored on every change — the
    //  active pipeline defaults to '18' and would spam history; deep links to a
    //  specific pipeline still load via the parser.)
    const valid = HR_PORTAL_TABS.has(hrPortalView)
    const tab = valid ? hrPortalView : HR_DEFAULT_TAB
    const path = selection ? `/hr-center/${tab}/${encodeURIComponent(selection)}` : `/hr-center/${tab}`
    if (window.location.hash !== `#${path}`) {
      try { window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}#${path}`) } catch { window.location.hash = path }
    }
  }, [activeView, hrPortalView, hrPortalFormKey, hrPortalSelectedId])

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (notifPrefsDirtyCountRef.current <= 0) return
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [])

  // Load user role on mount from CF Access JWT via /api/me. Resilient: a
  // transient non-OK (esp. a 5xx) or a thrown fetch/parse must NOT blank the
  // avatar to "U" or downgrade the role — we retry briefly and PRESERVE the
  // resolved identity, treating ONLY an explicit {authenticated:false} body as a
  // real sign-out. The response-to-state decision lives in the pure, unit-tested
  // helper resolveMeResponse (docs/REGRESSIONS.md: identity blanked when a
  // transient /api/me is swallowed).
  useEffect(() => {
    let cancelled = false
    const RETRIES = 2
    const attempt = async (triesLeft: number): Promise<void> => {
      let httpOk = false
      let body: any = null
      try {
        const r = await fetch('/api/me')
        httpOk = r.ok
        body = await r.json().catch(() => null)
      } catch { httpOk = false; body = null }
      if (cancelled) return
      const decision = resolveMeResponse(currentUser, httpOk, body)
      if (decision.action === 'set') {
        setUserRole(decision.user.role)
        localStorage.setItem('userRole', decision.user.role)
        setCurrentUser(decision.user)
        if (body && body.impersonating && (body.adminName || body.adminEmail)) {
          try {
            const stored = localStorage.getItem('crm_impersonate')
            if (stored) {
              const imp = JSON.parse(stored)
              if (body.adminName) imp.adminName = body.adminName
              if (body.adminEmail) imp.adminEmail = body.adminEmail
              localStorage.setItem('crm_impersonate', JSON.stringify(imp))
              setImpersonating(imp)
            }
          } catch { /* ignore */ }
        }
        return
      }
      if (decision.action === 'signed-out') {
        setUserRole('user')
        localStorage.setItem('userRole', 'user')
        return
      }
      // preserve: retry a transient non-OK/parse blip before giving up.
      if (triesLeft > 0) {
        await new Promise(res => setTimeout(res, (RETRIES - triesLeft + 1) * 400))
        if (!cancelled) return attempt(triesLeft - 1)
      }
      // Retries exhausted. PRESERVE the existing identity + role (never clear on
      // a blip). Local dev has no CF Access / no /api/me, so default the role to
      // admin so the dev UI is usable; production keeps the last-known role.
      const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      if (isLocal) {
        setUserRole('admin')
        localStorage.setItem('userRole', 'admin')
      }
    }
    attempt(RETRIES)
    return () => { cancelled = true }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Load capability map from /api/me/capabilities — gates UI buttons
  // (Reassign / Merge / Archive / Export / Assign Subcontractor /
  // Approve Discount / schedule date inputs) so disallowed actions are
  // disabled with a tooltip rather than clicked into 403. Re-fetches
  // when the impersonation state flips so the picker actually swaps
  // capabilities (an admin login-as-salesperson must see the
  // salesperson allow-map, not the admin one).
  useEffect(() => {
    fetchMyCapabilities().then(r => {
      if (!r) { setNavScopeReady(true); return }
      setMyCapabilities(r.capabilities)
      setMyRole(r.role)
      setHiddenNav(new Set(r.hiddenNav || []))
      // Cache so the next page load applies the role's nav visibility on
      // first paint instead of flashing restricted tabs.
      try { localStorage.setItem('crmHiddenNav', JSON.stringify(r.hiddenNav || [])) } catch { /* private mode */ }
      setNavScopeReady(true)
    }).catch(() => { setNavScopeReady(true) })
  }, [impersonating])

  // Shift-clock status — initial load + slow background refresh so a punch
  // made in another tab/device is reflected here within a couple minutes.
  useEffect(() => {
    const refresh = () => tcStatus()
      .then(s => setTcOpen(s.open ? { id: s.open.id, clock_in: s.open.clock_in, kind: (s.open.kind || 'work') as 'work' | 'lunch' | 'break' } : null))
      .catch(() => {})
    refresh()
    return startManagedInterval({ name: 'time-clock-status', intervalMs: 120_000, run: refresh })
  }, [impersonating])

  // Detect a forgotten clock-out (open work shift from a prior NY day). Only
  // prompt once per punch per session, and never when a correction for that
  // punch is already awaiting office approval (don't nag — it's handled).
  useEffect(() => {
    if (impersonating) { setTcForgotShow(false); return }
    if (!isForgottenShift(tcOpen, Date.now())) { setTcForgotShow(false); return }
    const punchId = tcOpen!.id
    if (tcForgotCheckedRef.current === punchId) return // already resolved this punch this session
    let snoozed = false
    try { snoozed = sessionStorage.getItem(`tcForgotSnooze:${punchId}`) === '1' } catch { /* ignore */ }
    if (snoozed) { tcForgotCheckedRef.current = punchId; return }
    let cancelled = false
    tcMyCorrections()
      .then(({ requests }) => {
        if (cancelled) return
        const pending = (requests || []).some(r => r.punch_id === punchId && r.status === 'pending')
        tcForgotCheckedRef.current = punchId
        setTcForgotShow(!pending)
      })
      .catch(() => { if (!cancelled) { tcForgotCheckedRef.current = punchId; setTcForgotShow(true) } })
    return () => { cancelled = true }
  }, [tcOpen, impersonating])

  const doTcPunch = async (action: 'in' | 'out' | 'lunch' | 'break' | 'resume') => {
    if (tcPunching) return
    setTcPunching(true)
    // MANDATE location on every shift-clock event. The browser geo prompt is
    // BLOCKING: on denial / unsupported / timeout we abort the punch entirely
    // (no best-effort fallback). Mirrors the subcontractor portal.
    let geo: { lat: number; lng: number; accuracy_m: number | null }
    try {
      setDealToast({ id: Date.now(), message: 'Getting location…', type: 'info' })
      geo = await requestPunchLocation()
    } catch (e: any) {
      setDealToast({ id: Date.now(), message: e?.message || 'Location is required to clock in/out.', type: 'error' })
      setTcPunching(false)
      return
    }
    try {
      const r = await tcPunch(action, geo)
      setTcOpen(r.open ? { id: r.open.id, clock_in: r.open.clock_in, kind: r.open.kind } : null)
      setTcMenuOpen(false)
      const msg = r.status === 'clocked_in'
        ? (action === 'resume' ? 'Back on the clock' : `Clocked in at ${new Date(r.open!.clock_in).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`)
        : r.status === 'on_lunch' ? 'On lunch — your work time is paused'
        : r.status === 'on_break' ? 'On break — your work time is paused'
        : 'Clocked out'
      setDealToast({ id: Date.now(), message: msg, type: 'success' })
    } catch (e: any) {
      setDealToast({ id: Date.now(), message: e?.message || 'Clock punch failed', type: 'error' })
    } finally {
      setTcPunching(false)
    }
  }

  // ── Per-role nav visibility (Settings → Roles & Permissions) ────────
  // A key is hidden when its own row OR any ancestor is hidden — works
  // for nav tabs ('crm.deals' under 'crm') and dashboard widgets
  // ('widget.kpis.won' under 'widget.kpis'). Used by the sidebar, the
  // secondary menus, in-view sub-tab bars, the home-dashboard widgets,
  // and the route guard below.
  const isNavHidden = (key: string): boolean => {
    // First-ever paint in this browser (no cached map yet): hide every
    // gateable surface until /api/me/capabilities answers — flashing
    // nothing is safe, flashing restricted tabs is not.
    if (!navScopeReady) return true
    if (hiddenNav.size === 0) return false
    let k = key
    for (;;) {
      if (hiddenNav.has(k)) return true
      const i = k.lastIndexOf('.')
      if (i <= 0) return false
      k = k.slice(0, i)
    }
  }
  // ViewMode → nav-registry key. Views not listed here (phone, quotes,
  // settings, record pages, …) are never nav-gated.
  const VIEW_TO_NAV_KEY: Record<string, string> = {
    calendar: 'calendar',
    contacts: 'crm.contacts', companies: 'crm.companies', leads: 'crm.leads',
    deals: 'crm.deals', tickets: 'crm.tickets', calls: 'crm.calls',
    projects: 'crm.projects', email: 'crm.email',
    tasks: 'crm.tasks', meets: 'crm.meets', 'my-documents': 'crm.my-documents', timesheets: 'crm.timesheets',
    dashboards: 'reporting.dashboards', reports: 'reporting.reports', analytics: 'reporting.analytics',
    accounting: 'accounting', feed: 'feed', team: 'feed',
    'hr-center': 'hr-center',
  }
  // Route guard: bounce to Home when the current view's tab is hidden for
  // this role — covers direct #/links, restored sessions, and any UI path
  // the filters above don't reach.
  useEffect(() => {
    if (hiddenNav.size === 0) return
    const navKey = VIEW_TO_NAV_KEY[activeView]
    let hidden = navKey ? isNavHidden(navKey) : false
    if (!hidden && activeView === 'accounting') hidden = isNavHidden(`accounting.${accountingSubView}`)
    if (hidden) window.location.hash = '/'
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, hiddenNav, accountingSubView])

  // Load HR profile when General > Profile tab is active
  const [hrProfileError, setHrProfileError] = useState<string | null>(null)
  useEffect(() => {
    if (activeView === 'preferences' && prefsTab === 'general' && generalSubTab === 'profile' && !hrProfile && !hrProfileLoading && !hrProfileError) {
      setHrProfileLoading(true)
      setHrProfileError(null)
      fetchHrProfile().then(p => {
        setHrProfile(p)
        setHrProfileBaseline(JSON.parse(JSON.stringify(p)))
        // While impersonating, `p` is the IMPERSONATED user's profile — do NOT
        // mirror their photo into the signed-in admin's prefsPhoto/localStorage
        // (that clobbers the admin's own avatar). The Profile card reads the
        // impersonated photo straight from `hrProfile.photoUrl`.
        if (p.photoUrl && !impersonating) {
          setPrefsPhoto(p.photoUrl)
          prefsPhotoBaselineRef.current = p.photoUrl
          persistPrefsPhoto(p.photoUrl, currentUser?.email)
        }
        setHrProfileLoading(false)
      }).catch((e) => {
        setHrProfileLoading(false)
        setHrProfileError(e?.message || 'Failed to load HR profile')
      })
    }
  }, [activeView, prefsTab, generalSubTab])

  useEffect(() => {
    if (prefsPhotoSyncStartedRef.current) return
    if (!currentUser) return
    // While impersonating, fetchHrProfile() is interception-rewritten to the
    // impersonated user — syncing would cache THEIR photo (or wipe the cache)
    // as the admin's own. The trigger never reads prefsPhoto while
    // impersonating anyway, so just skip; sync resumes on return-to-admin.
    if (impersonating) return
    // Skip when the signed-in (or impersonated) user has no HR profile.
    // /api/me populates `hrProfileId`; null means the lookup will 404 and
    // shows up red in the Network panel even though the catch block
    // swallows it silently.
    if (!currentUser.hrProfileId) return
    prefsPhotoSyncStartedRef.current = true
    let cancelled = false

    async function syncProfilePhoto() {
      try {
        const profile = await fetchHrProfile()
        if (cancelled) return
        if (profile.photoUrl) {
          setPrefsPhoto(profile.photoUrl)
          prefsPhotoBaselineRef.current = profile.photoUrl
          persistPrefsPhoto(profile.photoUrl, currentUser?.email)
          return
        }

        const cachedPhoto = localStorage.getItem('prefsPhoto')
        if (!cachedPhoto?.startsWith('data:image/')) {
          persistPrefsPhoto(null, null)
          setPrefsPhoto(null)
          prefsPhotoBaselineRef.current = null
          return
        }

        const res = await fetch(cachedPhoto)
        const blob = await res.blob()
        const uploaded = await uploadHrProfilePhoto(new File([blob], 'profile-photo.png', { type: blob.type || 'image/png' }))
        if (cancelled) return
        if (uploaded.photoUrl) {
          setPrefsPhoto(uploaded.photoUrl)
          prefsPhotoBaselineRef.current = uploaded.photoUrl
          persistPrefsPhoto(uploaded.photoUrl, currentUser?.email)
          setHrProfile(prev => prev ? { ...prev, profilePhotoKey: uploaded.key, photoUrl: uploaded.photoUrl } : prev)
          setHrProfileBaseline(prev => prev ? { ...prev, profilePhotoKey: uploaded.key, photoUrl: uploaded.photoUrl } : prev)
          // Photo key changed (legacy data-URL migrated to R2) — refresh the
          // versioned me-photo state so navAvatarSource never serves the
          // pre-migration version from /api/me.
          setCurrentUser(prev => prev ? { ...prev, photoUrl: uploaded.photoUrl, photoVersion: photoVersionFromKey(uploaded.key) || String(Date.now()) } : prev)
        }
      } catch {
        // No linked HR profile, auth not ready, or upload failed. Keep the app usable.
      }
    }

    void syncProfilePhoto()
    return () => { cancelled = true }
  }, [])

  // Cross-user cache guard. The moment the real signed-in identity is known
  // (NOT impersonation — that path manages its own avatar), drop any cached
  // photo whose owner email doesn't match. Kills the "previous user's photo on
  // a shared PC" leak and the stuck-forever case where hrProfileId is null so
  // the sync above never runs. UserPhotoAvatar then fetches the correct photo
  // from /api/crm/team/<email>/photo until the sync repopulates the cache.
  useEffect(() => {
    if (impersonating) return
    const email = currentUser?.email
    if (!email) return
    let owner: string | null = null
    try { owner = localStorage.getItem('prefsPhotoOwner') } catch { /* ignore */ }
    if (owner !== null && owner === email.toLowerCase()) return
    // Owner missing (legacy cache) or different user — purge so we never paint
    // the wrong face. Self-heals via syncProfilePhoto / UserPhotoAvatar.
    persistPrefsPhoto(null, null)
    setPrefsPhoto(null)
    prefsPhotoBaselineRef.current = null
  }, [currentUser?.email, impersonating])

  // Load user preferences from D1 API on first visit to preferences
  const [prefsApiLoaded, setPrefsApiLoaded] = useState(false)
  useEffect(() => {
    if (activeView === 'preferences' && !prefsApiLoaded) {
      setPrefsApiLoaded(true)
      fetchMyPreferences().then(res => {
        if (res?.preferences && Object.keys(res.preferences).length > 0) {
          const merged = { ...prefsBaselineRef.current, ...res.preferences }
          prefsBaselineRef.current = merged
          setPrefsValues(merged)
          localStorage.setItem('prefsSettings', JSON.stringify(merged))
        }
      }).catch(e => {
        console.error('Failed to load user preferences from API:', e)
      })
    }
  }, [activeView])

  // Load import/export history and backups when those preference tabs are active
  useEffect(() => {
    if (activeView !== 'preferences') return
    if (prefsTab === 'import-export') {
      setImportHistoryLoading(true)
      setExportHistoryLoading(true)
      fetchImports().then(r => { setImportHistory(r.imports || []); setImportHistoryLoading(false) }).catch(() => setImportHistoryLoading(false))
      fetchExports().then(r => { setExportHistory(r.exports || []); setExportHistoryLoading(false) }).catch(() => setExportHistoryLoading(false))
    }
    if (prefsTab === 'backup') {
      setBackupsLoading(true)
      fetch('/api/crm/backups').then(r => r.json()).then((r: any) => { setBackups(r.backups || []); setBackupsLoading(false) }).catch(() => setBackupsLoading(false))
    }
  }, [activeView, prefsTab])

  // Org-level settings (Tax / Forms / Accounting Defaults) — prefetch
  // when an admin opens any of those tabs. The settings live in D1
  // (org_settings table, migration 094) but we keep localStorage as a
  // write-through cache so the UI doesn't flicker on tab switches.
  // Each `pref_*` read still hits localStorage; writes flow through
  // saveOrgSetting() which patches the API and updates localStorage.
  useEffect(() => {
    if (activeView !== 'preferences') return
    if (!(ORG_SETTINGS_TABS as readonly string[]).includes(prefsTab)) return
    let cancelled = false
    fetchOrgSettings()
      .then(({ settings }) => {
        if (cancelled) return
        // Seed localStorage with the latest server values so subsequent
        // reads return the cross-device-shared answer. We only overwrite
        // when the server has a non-empty value — never clobber a local
        // edit the user has typed but hasn't saved yet (defensive).
        for (const [key, val] of Object.entries(settings)) {
          if (val !== null && val !== undefined) {
            try { localStorage.setItem(key, val) } catch { /* quota / private mode */ }
          }
        }
        // Trigger a storage event so the inputs reading via
        // localStorage.getItem() pick up the fresh values.
        window.dispatchEvent(new Event('storage'))
        // Account Defaults live in React state (not read per-render from
        // localStorage) — re-hydrate from the server blob so the tab shows
        // the cross-device answer.
        if (typeof settings['acctDefaults'] === 'string') {
          try { setAcctDefaults(JSON.parse(settings['acctDefaults'])) } catch { /* corrupt blob — keep local */ }
        }
      })
      .catch(e => {
        // Non-fatal; the page falls back to localStorage defaults.
        console.warn('org-settings prefetch:', e?.message || e)
      })
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, prefsTab])

  // HR Portal: Employee list
  useEffect(() => {
    if (activeView !== 'hr-center' || hrPortalView !== 'employees') return
    if (hrPortalSelectedId) return
    setHrPortalEmployeesLoading(true)
    fetchHrAdminEmployees({ search: hrPortalSearch, subsidiary: hrPortalDeptFilter, status: hrPortalStatusFilter, page: hrPortalPage, limit: 50 })
      .then((r: any) => { if (r?.success) { setHrPortalEmployees(r.data.employees); setHrPortalEmployeesTotal(r.data.total) } })
      .catch(() => {})
      .finally(() => setHrPortalEmployeesLoading(false))
  }, [activeView, hrPortalView, hrPortalSearch, hrPortalDeptFilter, hrPortalStatusFilter, hrPortalPage, hrPortalSelectedId])

  // HR Portal: Applications
  useEffect(() => {
    if (activeView !== 'hr-center' || hrPortalView !== 'applications') return
    setHrPortalAppsLoading(true)
    if (hrPortalAppsTab === 'submitted') {
      fetchHrAdminApplications({ search: hrPortalAppsSearch, status: hrPortalAppsStatus })
        .then((r: any) => { if (r?.success) setHrPortalApps(r.data.applications) })
        .catch(() => {})
        .finally(() => setHrPortalAppsLoading(false))
    } else {
      fetchHrAdminIncompleteApps()
        .then((r: any) => { if (r?.success) setHrPortalIncompleteApps(r.data.applications) })
        .catch(() => {})
        .finally(() => setHrPortalAppsLoading(false))
    }
  }, [activeView, hrPortalView, hrPortalAppsTab, hrPortalAppsSearch, hrPortalAppsStatus])

  // HR Portal: Settings — fetch each independently so one failure doesn't block others
  useEffect(() => {
    if (activeView !== 'hr-center' || hrPortalView !== 'settings') return
    setHrPortalSettingsLoading(true)
    let done = 0
    const checkDone = () => { done++; if (done >= 6) setHrPortalSettingsLoading(false) }
    fetchHrAdminEmailSettings().then(r => setHrPortalEmailSettings(r?.success ? r.data : { settings: {}, stats: {} })).catch(() => setHrPortalEmailSettings({ settings: {}, stats: {} })).finally(checkDone)
    fetchHrAdminAdmins().then(r => { if (r?.success) { setHrPortalAdmins(r.data?.admins || []); setHrPortalCrmUsers(r.data?.crmUsers || []); setHrPortalAllEmployees(r.data?.employees || []) } }).catch(() => setHrPortalAdmins([])).finally(checkDone)
    fetchHrAdminFieldRules().then(r => { if (r?.success) setHrPortalFieldRules(r.data?.rules || []) }).catch(() => setHrPortalFieldRules([])).finally(checkDone)
    fetchHrAdminPositions().then(r => { if (r?.success) setHrPortalPositions(r.data?.positions || []) }).catch(() => setHrPortalPositions([])).finally(checkDone)
    fetchHrAdminEmailTemplates().then(r => { if (r?.success) setHrPortalEmailTemplates(r.data?.templates || []) }).catch(() => setHrPortalEmailTemplates([])).finally(checkDone)
    fetchHrAdminTemplates().then(r => { if (r?.success) setHrPortalTemplates(r.data?.templates || []) }).catch(() => {}).finally(checkDone)
    // Load employer signature from R2
    fetchHrAdminEmployerSignature().then(r => {
      if (r?.success) {
        if (r.data.signature) setHrEmployerSignature(r.data.signature)
        if (r.data.initials) setHrEmployerInitials(r.data.initials)
      }
    }).catch(() => {})
  }, [activeView, hrPortalView])

  // HR Portal: Employee Detail
  useEffect(() => {
    if (!hrPortalSelectedId) { setHrPortalDetail(null); setHrPortalAlerts([]); setHrPortalActivityRollup(null); setHrPortalDocs({assignments:[],fileFields:[]}); return }
    setHrPortalDetailLoading(true)
    fetchHrAdminEmployee(hrPortalSelectedId).then((r: any) => {
      if (r?.success) setHrPortalDetail(r.data)
    }).catch(() => {}).finally(() => setHrPortalDetailLoading(false))
    fetchHrAdminEmployeeAlerts(hrPortalSelectedId).then((r: any) => {
      if (r?.success) setHrPortalAlerts(r.data?.alerts || [])
    }).catch(() => {})
    fetchHrAdminEmployeeDocuments(hrPortalSelectedId).then((r: any) => {
      if (r?.success) setHrPortalDocs(r.data || {assignments:[], fileFields:[]})
    }).catch(() => {})
  }, [hrPortalSelectedId])

  // HR Portal: Activity Rollup — fetched lazily the first time the Activity tab
  // is opened for a given employee. Resets whenever a different employee is
  // selected. A null result (404 or fetch error) renders the empty-state copy.
  useEffect(() => {
    if (!hrPortalSelectedId || hrPortalDetailTab !== 'activity') return
    // Already loaded for this employee? Skip.
    if (hrPortalActivityRollup && String(hrPortalActivityRollup.employee?.employeeId) === String(hrPortalSelectedId)) return
    let cancel = false
    setHrPortalActivityLoading(true)
    fetchHrEmployeeActivityRollup(hrPortalSelectedId).then(r => {
      if (cancel) return
      setHrPortalActivityRollup(r)
    }).catch(() => {
      if (cancel) return
      setHrPortalActivityRollup(null)
    }).finally(() => {
      if (!cancel) setHrPortalActivityLoading(false)
    })
    return () => { cancel = true }
  }, [hrPortalSelectedId, hrPortalDetailTab, hrPortalActivityRollup])

  // Integration: open HR profile drawer for a CRM user id. Caches the
  // resolved profile (or notFound flag) per session so repeat clicks on the
  // same owner are instant.
  const openHrProfileDrawer = React.useCallback(async (userId: number | string, userName?: string) => {
    if (!userId) return
    const key = String(userId)
    // Cache hit — resolve instantly.
    if (hrProfileDrawerCacheRef.current.has(key)) {
      const cached = hrProfileDrawerCacheRef.current.get(key) || null
      setHrProfileDrawer({
        userId,
        userName,
        loading: false,
        profile: cached,
        notFound: !cached,
      })
      return
    }
    // Cache miss — show loading, fetch, cache the result.
    setHrProfileDrawer({ userId, userName, loading: true, profile: null, notFound: false })
    try {
      const p = await fetchUserHrProfile(userId)
      hrProfileDrawerCacheRef.current.set(key, p)
      setHrProfileDrawer({
        userId,
        userName,
        loading: false,
        profile: p,
        notFound: !p,
      })
    } catch {
      hrProfileDrawerCacheRef.current.set(key, null)
      setHrProfileDrawer({
        userId,
        userName,
        loading: false,
        profile: null,
        notFound: true,
      })
    }
  }, [])

  // Integration: ensure RC extension data is loaded for an employee id.
  // Coalesces duplicate in-flight lookups via the ref cache + map. Silent on
  // failure — the chip simply doesn't render.
  const ensureRcExtension = React.useCallback((employeeId: string | number) => {
    if (!employeeId) return
    const key = String(employeeId)
    if (hrPortalRcExtCacheRef.current.has(key)) return
    // Mark as in-flight with null so we don't refetch.
    hrPortalRcExtCacheRef.current.set(key, null)
    fetchHrEmployeeRcExtension(employeeId).then(res => {
      hrPortalRcExtCacheRef.current.set(key, res)
      setHrPortalRcExtMap(prev => ({ ...prev, [key]: res }))
    }).catch(() => {
      // Keep the null placeholder — prevents re-fetch storms.
    })
  }, [])

  // Integration: ensure linked-context is loaded for a call activity id.
  // Again coalesced via ref cache + map.
  const ensureCallLinkedContext = React.useCallback((activityId: number) => {
    if (!activityId) return
    if (callLinkedContextCacheRef.current.has(activityId)) return
    callLinkedContextCacheRef.current.set(activityId, null)
    fetchCallLinkedContext(activityId).then(res => {
      const link = res?.linked || null
      callLinkedContextCacheRef.current.set(activityId, link)
      setCallLinkedContextMap(prev => ({ ...prev, [activityId]: link }))
    }).catch(() => {})
  }, [])

  // HR Portal: Pipelines/Kanban
  useEffect(() => {
    if (activeView !== 'hr-center' || hrPortalView !== 'pipelines') return
    setHrPortalPipelinesLoading(true)
    Promise.all([fetchHrAdminPipelines(), fetchHrAdminProfiles(hrPortalActivePipeline)])
      .then(([p, pr]) => {
        if (p?.success) { setHrPortalPipelines(p.data.pipelines); setHrPortalStages(p.data.stages) }
        if (pr?.success) setHrPortalProfiles(pr.data.profiles)
      }).catch(() => {}).finally(() => setHrPortalPipelinesLoading(false))
  }, [activeView, hrPortalView, hrPortalActivePipeline])

  // HR Portal: Profile detail (View Details panel)
  useEffect(() => {
    if (!hrPortalViewProfileId) { setHrPortalViewProfileData(null); return }
    setHrPortalViewProfileLoading(true)
    setHrPortalViewProfileTab('application')
    fetchHrAdminProfileDetail(hrPortalViewProfileId)
      .then((r: any) => { if (r?.success) setHrPortalViewProfileData(r.data) })
      .catch(() => {})
      .finally(() => setHrPortalViewProfileLoading(false))
  }, [hrPortalViewProfileId])

  // HR Portal: Profiles list
  useEffect(() => {
    if (activeView !== 'hr-center' || hrPortalView !== 'profiles') return
    setHrPortalProfilesLoading(true)
    fetchHrAdminProfiles().then((r: any) => {
      if (r?.success) setHrPortalProfilesList(r.data.profiles)
    }).catch(() => {}).finally(() => setHrPortalProfilesLoading(false))
  }, [activeView, hrPortalView])

  // HR Portal: Templates
  useEffect(() => {
    if (activeView !== 'hr-center' || hrPortalView !== 'templates') return
    setHrPortalTemplatesLoading(true)
    fetchHrAdminTemplates().then((r: any) => {
      if (r?.success) setHrPortalTemplates(r.data.templates || r.data || [])
    }).catch(() => {}).finally(() => setHrPortalTemplatesLoading(false))
  }, [activeView, hrPortalView])

  // HR Portal: Template PDF canvas previews (renders page 1 via PDF.js to avoid browser PDF viewer signature warnings)
  useEffect(() => {
    if (activeView !== 'hr-center' || hrPortalView !== 'templates') return
    if (!hrPortalTemplates.length) return
    const renderThumbnails = async () => {
      const pdfjs = (window as any).pdfjsLib || await (window as any).pdfjsLoaded?.catch(() => null)
      if (!pdfjs) return
      for (const t of hrPortalTemplates as any[]) {
        if (hrPortalTplPreviews[t.id]) continue
        try {
          const resp = await fetch(`/api/hr-admin/templates/${t.id}/download`)
          if (!resp.ok) continue
          const arrayBuffer = await resp.arrayBuffer()
          const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise
          const page = await pdf.getPage(1)
          const vp = page.getViewport({ scale: 0.6 })
          const canvas = document.createElement('canvas')
          canvas.width = vp.width; canvas.height = vp.height
          const ctx = canvas.getContext('2d')
          if (!ctx) continue
          await page.render({ canvasContext: ctx, viewport: vp }).promise
          const dataUrl = canvas.toDataURL('image/png')
          setHrPortalTplPreviews(prev => ({ ...prev, [t.id]: dataUrl }))
        } catch { /* skip */ }
      }
    }
    renderThumbnails()
  }, [activeView, hrPortalView, hrPortalTemplates])

  // HR Portal: Assignments — always fetch ALL, filter client-side so stat cards show correct totals
  useEffect(() => {
    if (activeView !== 'hr-center' || hrPortalView !== 'assignments') return
    setHrPortalAssignmentsLoading2(true)
    fetchHrAdminAssignments().then((r: any) => {
      if (r?.success) setHrPortalAssignmentsList(r.data.assignments || r.data || [])
    }).catch(() => {}).finally(() => setHrPortalAssignmentsLoading2(false))
  }, [activeView, hrPortalView])

  // HR Portal: Employer Signing (also fetch on assignments view for the banner)
  useEffect(() => {
    if (activeView !== 'hr-center' || (hrPortalView !== 'employer-signing' && hrPortalView !== 'assignments')) return
    if (hrPortalView === 'employer-signing') setHrPortalPendingEmployerLoading(true)
    fetchHrAdminPendingEmployer().then((r: any) => {
      if (r?.success) {
        const docs = r.data.documents || r.data || []
        setHrPortalPendingEmployer(docs)
        if (hrPortalView === 'employer-signing' && !hrPortalSelectedSignDoc && docs.length > 0) setHrPortalSelectedSignDoc(docs[0])
      }
    }).catch(() => {}).finally(() => setHrPortalPendingEmployerLoading(false))
  }, [activeView, hrPortalView])

  // HR Portal: Onboarding
  useEffect(() => {
    if (activeView !== 'hr-center' || hrPortalView !== 'onboarding') return
    setHrPortalOnboardingLoading(true)
    Promise.all([fetchHrAdminOnboardingTokens(), fetchHrAdminOnboardingDocs()])
      .then(([t, d]) => {
        if (t?.success) setHrPortalOnboardingTokens(t.data.tokens || t.data || [])
        if (d?.success) setHrPortalOnboardingDocs(d.data.documents || d.data || [])
      }).catch(() => {}).finally(() => setHrPortalOnboardingLoading(false))
  }, [activeView, hrPortalView])

  // HR Portal: Dashboard
  const [hrPortalDashboard, setHrPortalDashboard] = useState<any>(null)
  const [hrPortalDashboardLoading, setHrPortalDashboardLoading] = useState(false)
  // home-bento-v3: track fetch failure so the Home HR widget can show an error
  // state instead of collapsing all-zeros to a misleading "all clear" line.
  const [hrPortalDashboardError, setHrPortalDashboardError] = useState(false)
  const [hrPortalActivityExpanded, setHrPortalActivityExpanded] = useState(false)
  useEffect(() => {
    if (activeView === 'command' || (activeView === 'hr-center' && hrPortalView === 'dashboard')) {
      setHrPortalDashboardLoading(true)
      setHrPortalDashboardError(false)
      fetchHrAdminDashboard()
        .then((r: any) => { if (r?.success) { setHrPortalDashboard(r.data); setHrPortalDashboardError(false) } else { setHrPortalDashboardError(true) } })
        .catch(() => { setHrPortalDashboardError(true) })
        .finally(() => setHrPortalDashboardLoading(false))
    }
  }, [activeView, hrPortalView, commandRefreshTick]) // home-bento-v3: honor manual Refresh / widget Retry


  // Dashboard widget for all users: their own pending HR documents.
  // Loads on mount / route re-entry / manual Refresh only — no auto-refresh timer
  // (the command center is manual-refresh only via commandRefreshTick).
  const [myDocsAssignments, setMyDocsAssignments] = useState<any[]>([])
  const [myDocsLoading, setMyDocsLoading] = useState(false)
  useEffect(() => {
    if (activeView !== 'command') return
    let cancelled = false
    const load = async () => {
      setMyDocsLoading(true)
      try {
        const r: any = await fetchMyAssignments()
        if (!cancelled) setMyDocsAssignments(r?.success ? (r.data?.assignments || []) : [])
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setMyDocsLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [activeView, commandRefreshTick])

  // === Signature helper functions (ported from NativeSignatureModal) ===

  // Trim transparent whitespace around drawn canvas to maximize signature size
  const hrSignTrimCanvas = (canvas: HTMLCanvasElement): string => {
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) return canvas.toDataURL('image/png')
    const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height)
    const l = pixels.data.length
    let bound = { top: canvas.height, left: canvas.width, right: 0, bottom: 0 }
    for (let i = 0; i < l; i += 4) {
      if (pixels.data[i + 3] !== 0) {
        const x = (i / 4) % canvas.width
        const y = Math.floor((i / 4) / canvas.width)
        if (x < bound.left) bound.left = x
        if (x > bound.right) bound.right = x
        if (y < bound.top) bound.top = y
        if (y > bound.bottom) bound.bottom = y
      }
    }
    const padding = 10
    bound.left = Math.max(0, bound.left - padding)
    bound.top = Math.max(0, bound.top - padding)
    bound.right = Math.min(canvas.width, bound.right + padding)
    bound.bottom = Math.min(canvas.height, bound.bottom + padding)
    const trimHeight = bound.bottom - bound.top
    const trimWidth = bound.right - bound.left
    if (trimWidth <= 0 || trimHeight <= 0) return canvas.toDataURL('image/png')
    const trimmed = document.createElement('canvas')
    trimmed.width = trimWidth
    trimmed.height = trimHeight
    const trimmedCtx = trimmed.getContext('2d')
    trimmedCtx?.putImageData(ctx.getImageData(bound.left, bound.top, trimWidth, trimHeight), 0, 0)
    return trimmed.toDataURL('image/png')
  }

  // Generate signature from typed name with dynamic sizing (ported exactly)
  const hrSignGenerateTyped = (name: string, isInitials: boolean = false, fontIndex: number = 0): string => {
    const FONTS = [
      { family: 'Dancing Script' },
      { family: 'Great Vibes' },
      { family: 'Allura' },
    ]
    const canvas = document.createElement('canvas')
    canvas.width = isInitials ? 300 : 500
    canvas.height = isInitials ? 150 : 200
    const ctx = canvas.getContext('2d')
    if (!ctx) return ''
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    const fontFamily = FONTS[fontIndex]?.family || 'Dancing Script'
    let fontSize = isInitials ? 48 : 64
    const minFontSize = isInitials ? 24 : 32
    const maxWidth = canvas.width * 0.9
    ctx.fillStyle = '#000000'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    while (fontSize > minFontSize) {
      ctx.font = `${fontSize}px "${fontFamily}", cursive`
      if (ctx.measureText(name).width <= maxWidth) break
      fontSize -= 2
    }
    ctx.font = `${fontSize}px "${fontFamily}", cursive`
    ctx.fillText(name, canvas.width / 2, canvas.height / 2)
    return hrSignTrimCanvas(canvas)
  }

  // Apply format mask for text fields (ported exactly)
  const hrSignApplyFormatMask = (raw: string, mask?: string): string => {
    if (!mask) return raw
    const digits = raw.replace(/\D/g, '')
    switch (mask) {
      case 'ssn': { const p = digits.slice(0, 9); if (p.length <= 3) return p; if (p.length <= 5) return `${p.slice(0, 3)}-${p.slice(3)}`; return `${p.slice(0, 3)}-${p.slice(3, 5)}-${p.slice(5)}` }
      case 'phone': { const p = digits.slice(0, 10); if (p.length <= 3) return p; if (p.length <= 6) return `${p.slice(0, 3)}-${p.slice(3)}`; return `${p.slice(0, 3)}-${p.slice(3, 6)}-${p.slice(6)}` }
      case 'date': { const p = digits.slice(0, 8); if (p.length <= 2) return p; if (p.length <= 4) return `${p.slice(0, 2)}/${p.slice(2)}`; return `${p.slice(0, 2)}/${p.slice(2, 4)}/${p.slice(4)}` }
      default: return raw
    }
  }

  // Open signing modal for an assignment (ported from NativeSignatureModal)
  const openHrSignModal = async (assignmentId: string, signerIndex?: number, mode: 'admin' | 'employee' = 'admin') => {
    setHrSignAssignmentId(assignmentId)
    setHrSignMode(mode)
    setHrSignModalOpen(true)
    setHrSignLoading(true)
    setHrSignFilledFields(new Map())
    setHrSignSuccess(false)
    setHrSignError(null)
    setHrSignCurrentSigner(signerIndex || 0)
    setHrSignPopupOpen(false)
    setHrSignActiveField(null)
    setHrSignCustomName('')
    setHrSignSelectedFont(-1)
    setHrSignTempSig(null)
    setHrSignUploadedSig(null)
    try {
      const detailsResp = mode === 'employee'
        ? await fetchMyAssignmentDetails(assignmentId)
        : await fetchHrAdminAssignmentDetails(assignmentId)
      if (!detailsResp?.success) throw new Error('Failed to load assignment')
      setHrSignAssignment(detailsResp.data)

      // Parse field positions
      const rawFp = detailsResp.data.assignment.field_positions || []
      const parsedFields = (Array.isArray(rawFp) ? rawFp : typeof rawFp === 'string' ? (() => { try { return JSON.parse(rawFp) } catch { return [] } })() : [])
        .map((f: any, i: number) => ({ ...f, id: f.id || `field-${i}` }))
      setHrSignParsedFields(parsedFields)

      // Load PDF via PDF.js
      const pdfBlob = mode === 'employee'
        ? await fetchMyAssignmentPdf(assignmentId)
        : await fetchHrAdminAssignmentPdf(assignmentId)
      const arrayBuffer = await pdfBlob.arrayBuffer()
      const pdfjs = (window as any).pdfjsLib || await (window as any).pdfjsLoaded
      const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise
      setHrSignNumPages(pdf.numPages)
      const pages: any[] = []
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i)
        const scale1vp = page.getViewport({ scale: 1 })
        const nativeWidth = scale1vp.width
        const nativeHeight = scale1vp.height
        // Render at scale for quality
        const vp = page.getViewport({ scale: 1.5 })
        const canvas = document.createElement('canvas')
        canvas.width = vp.width; canvas.height = vp.height
        const ctx = canvas.getContext('2d')!
        await page.render({ canvasContext: ctx, viewport: vp }).promise
        pages.push({
          dataUrl: canvas.toDataURL('image/png'),
          width: vp.width, height: vp.height,
          nativeWidth, nativeHeight,
        })
      }
      setHrSignPages(pages)

      // Convert fields from stored coordinates to percentages (EXACT same as NativeSignatureModal)
      const converted = parsedFields.map((f: any, i: number) => {
        const pageIndex = (f.page || f.pageNumber || 1) - 1
        const pg = pages[pageIndex]
        if (!pg) return null
        // Fields stored in scaled PDF coords (pageWidth=800, pageHeight~1035) with BOTTOM-LEFT origin
        // Must flip Y axis for CSS top-left positioning
        const refWidth = f.pageWidth || 800
        const refHeight = f.pageHeight || 1035.29
        const percentX = (f.x / refWidth) * 100
        const percentHeight = (f.height / refHeight) * 100
        const percentY = ((refHeight - f.y - f.height) / refHeight) * 100
        const percentWidth = (f.width / refWidth) * 100
        return {
          ...f,
          id: f.id || `field-${i}`,
          x: percentX, y: percentY,
          width: percentWidth, height: percentHeight,
          page: f.page || f.pageNumber || 1,
        }
      }).filter(Boolean)
      setHrSignConvertedFields(converted)

      // Auto-fill from employee data (ported from NativeSignatureModal)
      const empData = detailsResp.data.assignment?.employee_data
      if (empData) {
        const getValueByPath = (obj: any, path: string) => path.split('.').reduce((acc: any, part: string) => acc && acc[part], obj)
        const autoFilled = new Map<string, any>()
        parsedFields.forEach((f: any) => {
          // Static value fields
          if (f.staticValue && !autoFilled.has(f.id)) {
            autoFilled.set(f.id, { fieldId: f.id, type: 'text', data: f.staticValue, label: f.label })
          }
          if (!f.employeeDataSource) return
          if (f.type !== 'text' && f.type !== 'date' && f.type !== 'checkbox') return
          let value = getValueByPath(empData, f.employeeDataSource)
          if (value === undefined || value === null) return
          if (Array.isArray(value)) value = value[0]
          // Apply transforms (ported exactly)
          if (f.transform) {
            switch (f.transform) {
              case 'splitSSN': {
                const parts = String(value).replace(/\D/g, '').match(/(\d{3})(\d{2})(\d{4})/)
                if (parts && f.transformIndex !== undefined) value = parts[f.transformIndex + 1] || ''
                break
              }
              case 'extractYear': value = new Date(value).getFullYear().toString(); break
              case 'extractMonth': value = (new Date(value).getMonth() + 1).toString().padStart(2, '0'); break
              case 'extractDay': value = new Date(value).getDate().toString().padStart(2, '0'); break
              case 'formatDate': {
                try { const [y, m, d] = String(value).split('-'); if (y && m && d) value = `${m}/${d}/${y}`; else value = new Date(value).toLocaleDateString('en-US') } catch { /* keep */ }
                break
              }
              case 'uppercase': value = String(value).toUpperCase(); break
              case 'lowercase': value = String(value).toLowerCase(); break
              case 'checkbox': value = String(value) === 'true'; break
            }
          }
          if (f.type === 'checkbox') {
            let isChecked = false
            if (f.checkIfEquals) isChecked = String(value).toLowerCase() === f.checkIfEquals.toLowerCase()
            else if (typeof value === 'boolean') isChecked = value
            else if (String(value) === 'true' || String(value) === '1') isChecked = true
            if (isChecked) autoFilled.set(f.id, { fieldId: f.id, type: 'checkbox', data: 'true', label: f.label })
          } else {
            const sv = String(value)
            if (sv) autoFilled.set(f.id, { fieldId: f.id, type: f.type, data: sv, label: f.label })
          }
        })
        if (autoFilled.size > 0) setHrSignFilledFields(autoFilled)

        // Pre-set the custom signer name
        const fn = empData.firstName || empData.first_name || ''
        const ln = empData.lastName || empData.last_name || ''
        if (fn && ln) setHrSignCustomName(`${fn} ${ln}`)
      }
    } catch (e) {
      console.error('Failed to open signing modal:', e)
      setHrSignError(e instanceof Error ? e.message : 'Failed to load document')
    } finally {
      setHrSignLoading(false)
    }
  }

  // Open employer signing modal for onboarding docs (reuses the signing modal infrastructure)
  const openHrEmployerSignModal = async (doc: any) => {
    setHrSignAssignmentId(String(doc.id))
    setHrEmployerSignSubmitDocId(doc.id)
    setHrSignModalOpen(true)
    setHrSignLoading(true)
    setHrSignFilledFields(new Map())
    setHrSignSuccess(false)
    setHrSignError(null)
    setHrSignCurrentSigner(1)
    setHrSignPopupOpen(false)
    setHrSignActiveField(null)
    setHrSignCustomName('')
    setHrSignSelectedFont(-1)
    setHrSignTempSig(null)
    setHrSignUploadedSig(null)
    try {
      const rawFp = doc.field_positions || []
      const parsedFields = (Array.isArray(rawFp) ? rawFp : typeof rawFp === 'string' ? (() => { try { return JSON.parse(rawFp) } catch { return [] } })() : [])
        .map((f: any, i: number) => ({ ...f, id: f.id || `field-${i}` }))
      setHrSignParsedFields(parsedFields)
      setHrSignAssignment({ assignment: { template_title: doc.template_title || doc.document_type, field_positions: rawFp } })
      const pdfBlob = await fetchHrAdminOnboardingDocPdf(String(doc.id))
      const arrayBuffer = await pdfBlob.arrayBuffer()
      const pdfjs = (window as any).pdfjsLib || await (window as any).pdfjsLoaded
      const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise
      setHrSignNumPages(pdf.numPages)
      const pages: any[] = []
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i)
        const scale1vp = page.getViewport({ scale: 1 })
        const vp = page.getViewport({ scale: 1.5 })
        const canvas = document.createElement('canvas')
        canvas.width = vp.width; canvas.height = vp.height
        const ctx = canvas.getContext('2d')!
        await page.render({ canvasContext: ctx, viewport: vp }).promise
        pages.push({ dataUrl: canvas.toDataURL('image/png'), width: vp.width, height: vp.height, nativeWidth: scale1vp.width, nativeHeight: scale1vp.height })
      }
      setHrSignPages(pages)
      const converted = parsedFields.map((f: any, i: number) => {
        const pageIndex = (f.page || f.pageNumber || 1) - 1
        const pg = pages[pageIndex]
        if (!pg) return null
        const refWidth = f.pageWidth || 800
        const refHeight = f.pageHeight || 1035.29
        const percentX = (f.x / refWidth) * 100
        const percentHeight = (f.height / refHeight) * 100
        const percentY = ((refHeight - f.y - f.height) / refHeight) * 100
        const percentWidth = (f.width / refWidth) * 100
        return { ...f, id: f.id || `field-${i}`, x: percentX, y: percentY, width: percentWidth, height: percentHeight, page: f.page || f.pageNumber || 1 }
      }).filter(Boolean)
      setHrSignConvertedFields(converted)
    } catch (e) {
      console.error('Failed to open employer signing modal:', e)
      setHrSignError(e instanceof Error ? e.message : 'Failed to load document')
    } finally {
      setHrSignLoading(false)
    }
  }

  // Load PDF preview when employer signing doc is selected
  useEffect(() => {
    if (!hrPortalSelectedSignDoc) { setHrEmployerPreviewUrl(null); return }
    let cancelled = false
    setHrEmployerPreviewLoading(true)
    setHrEmployerPreviewUrl(null)
    ;(async () => {
      try {
        const blob = await fetchHrAdminOnboardingDocPdf(String(hrPortalSelectedSignDoc.id))
        if (cancelled) return
        const arrayBuffer = await blob.arrayBuffer()
        const pdfjs = (window as any).pdfjsLib || await (window as any).pdfjsLoaded
        const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise
        const page = await pdf.getPage(1)
        const vp = page.getViewport({ scale: 1.5 })
        const canvas = document.createElement('canvas')
        canvas.width = vp.width; canvas.height = vp.height
        await page.render({ canvasContext: canvas.getContext('2d')!, viewport: vp }).promise
        if (!cancelled) setHrEmployerPreviewUrl(canvas.toDataURL('image/png'))
      } catch (e) {
        console.error('Failed to load employer doc preview:', e)
      } finally {
        if (!cancelled) setHrEmployerPreviewLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [hrPortalSelectedSignDoc?.id])

  // Open field editor for a template (ported from FieldEditorModal)
  const openHrFieldEditor = async (templateId: string) => {
    setHrFieldEditorTemplateId(templateId)
    setHrFieldEditorOpen(true)
    setHrFieldEditorLoading(true)
    setHrFieldEditorTool(null)
    setHrFieldEditorSelected(null)
    setHrFieldEditorDragging(null)
    setHrFieldEditorResizing(null)
    setHrFieldEditorHistory([])
    setHrFieldEditorFuture([])
    setHrFieldEditorSidebarSection({ signerGroups: false, fieldTypes: true, checkboxGroups: false, properties: false })
    try {
      const template = hrPortalTemplates.find((t: any) => String(t.id) === templateId)
      if (!template) throw new Error('Template not found')
      let fields = template.field_positions || []
      if (typeof fields === 'string') { try { fields = JSON.parse(fields) } catch { fields = [] } }

      // Load PDF via PDF.js
      const pdfBlob = await downloadHrAdminTemplate(templateId)
      const arrayBuffer = await pdfBlob.arrayBuffer()
      const pdfjs = (window as any).pdfjsLib || await (window as any).pdfjsLoaded
      const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise
      setHrFieldEditorNumPages(pdf.numPages)
      const pages: any[] = []
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i)
        const scale1vp = page.getViewport({ scale: 1 })
        const nativeWidth = scale1vp.width
        const nativeHeight = scale1vp.height
        const vp = page.getViewport({ scale: 1.5 })
        const canvas = document.createElement('canvas')
        canvas.width = vp.width; canvas.height = vp.height
        const ctx = canvas.getContext('2d')!
        await page.render({ canvasContext: ctx, viewport: vp }).promise
        pages.push({
          dataUrl: canvas.toDataURL('image/png'),
          width: vp.width, height: vp.height,
          nativeWidth, nativeHeight,
        })
      }
      setHrFieldEditorPages(pages)

      // Convert stored PDF-point fields to percentage-based (EXACT same as FieldEditorModal)
      const convertedFields = (Array.isArray(fields) ? fields : []).map((f: any, i: number) => {
        const pageIndex = (f.page || f.pageNumber || 1) - 1
        const pg = pages[pageIndex]
        if (!pg) return { ...f, id: f.id || `field_${i}`, pageNumber: f.page || f.pageNumber || 1 }
        // Fields stored in scaled PDF coords (bottom-left origin) — flip Y for CSS
        const refWidth = f.pageWidth || 800
        const refHeight = f.pageHeight || 1035.29
        const percentX = (f.x / refWidth) * 100
        const percentHeight = (f.height / refHeight) * 100
        const percentY = ((refHeight - f.y - f.height) / refHeight) * 100
        const percentWidth = (f.width / refWidth) * 100
        return {
          ...f,
          id: f.id || `field_${i}`,
          x: percentX, y: percentY,
          width: percentWidth, height: percentHeight,
          pageNumber: f.page || f.pageNumber || 1,
        }
      })
      setHrFieldEditorFields(convertedFields)
      // Load settings form from template
      const signerConfig = template.default_signer_config ? (typeof template.default_signer_config === 'string' ? JSON.parse(template.default_signer_config) : template.default_signer_config) : null
      setHrFieldEditorSettingsForm({ title: template.title || '', description: template.description || '', category: template.category || '', requiresSignature: !!template.requires_signature, requiredForOnboarding: !!template.required_for_onboarding })
      setHrFieldEditorWorkflowMode(signerConfig?.mode === 'multi_signer' ? 'multi_signer' : 'single_signer')
      setHrFieldEditorWorkflowSigners(signerConfig?.signers || [])
      setHrFieldEditorShowSettings(false)
    } catch (e) {
      console.error('Failed to open field editor:', e)
    } finally {
      setHrFieldEditorLoading(false)
    }
  }

  // Keyboard shortcuts for field editor
  useEffect(() => {
    if (!hrFieldEditorOpen) return
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') return
      const ctrl = e.ctrlKey || e.metaKey
      // Undo
      if (ctrl && e.key === 'z' && !e.shiftKey) {
        e.preventDefault()
        setHrFieldEditorHistory((h: any[][]) => {
          if (h.length === 0) return h
          const prev = h[h.length - 1]
          setHrFieldEditorFuture((f: any[][]) => [hrFieldEditorFields, ...f])
          setHrFieldEditorFields(prev)
          setHrFieldEditorSelected(null)
          setHrFieldEditorSidebarSection((p: Record<string,boolean>) => ({ ...p, fieldTypes: true }))
          return h.slice(0, -1)
        })
      }
      // Redo
      if (ctrl && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault()
        setHrFieldEditorFuture((f: any[][]) => {
          if (f.length === 0) return f
          const next = f[0]
          setHrFieldEditorHistory((h: any[][]) => [...h, hrFieldEditorFields])
          setHrFieldEditorFields(next)
          setHrFieldEditorSelected(null)
          setHrFieldEditorSidebarSection((p: Record<string,boolean>) => ({ ...p, fieldTypes: true }))
          return f.slice(1)
        })
      }
      // Escape — deselect
      if (e.key === 'Escape') { setHrFieldEditorSelected(null); setHrFieldEditorTool(null); setHrFieldEditorSidebarSection((p: Record<string,boolean>) => ({ ...p, fieldTypes: true })) }
      // Delete / Backspace — remove selected field
      if ((e.key === 'Delete' || e.key === 'Backspace') && hrFieldEditorSelected) {
        e.preventDefault()
        setHrFieldEditorHistory((h: any[][]) => [...h, hrFieldEditorFields])
        setHrFieldEditorFuture([])
        setHrFieldEditorFields((prev: any[]) => prev.filter((f: any) => f.id !== hrFieldEditorSelected))
        setHrFieldEditorSelected(null)
        setHrFieldEditorSidebarSection((p: Record<string,boolean>) => ({ ...p, fieldTypes: true }))
      }
      // Ctrl+D — duplicate selected field
      if (ctrl && e.key === 'd' && hrFieldEditorSelected) {
        e.preventDefault()
        const sel = hrFieldEditorFields.find((f: any) => f.id === hrFieldEditorSelected)
        if (sel) {
          const dup = { ...sel, id: `field_${Date.now()}_${Math.random().toString(36).slice(2,6)}`, x: Math.min(100 - sel.width, sel.x + 2), y: Math.min(100 - sel.height, sel.y + 2) }
          setHrFieldEditorHistory((h: any[][]) => [...h, hrFieldEditorFields])
          setHrFieldEditorFuture([])
          setHrFieldEditorFields((prev: any[]) => [...prev, dup])
          setHrFieldEditorSelected(dup.id)
        }
      }
      // Arrow keys — nudge
      if (['ArrowUp','ArrowDown','ArrowLeft','ArrowRight'].includes(e.key) && hrFieldEditorSelected) {
        e.preventDefault()
        const step = e.shiftKey ? 2 : 0.5
        setHrFieldEditorFields((prev: any[]) => prev.map((f: any) => {
          if (f.id !== hrFieldEditorSelected) return f
          if (e.key === 'ArrowLeft') return { ...f, x: Math.max(0, f.x - step) }
          if (e.key === 'ArrowRight') return { ...f, x: Math.min(100 - f.width, f.x + step) }
          if (e.key === 'ArrowUp') return { ...f, y: Math.max(0, f.y - step) }
          if (e.key === 'ArrowDown') return { ...f, y: Math.min(100 - f.height, f.y + step) }
          return f
        }))
      }
      // Zoom: Ctrl+= / Ctrl+- / Ctrl+0
      if (ctrl && (e.key === '=' || e.key === '+')) { e.preventDefault(); setHrFieldEditorZoom((z: number) => Math.min(2.0, z + 0.25)) }
      if (ctrl && e.key === '-') { e.preventDefault(); setHrFieldEditorZoom((z: number) => Math.max(0.5, z - 0.25)) }
      if (ctrl && e.key === '0') { e.preventDefault(); setHrFieldEditorZoom(1.0) }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [hrFieldEditorOpen, hrFieldEditorSelected, hrFieldEditorFields])

  const toggleSidebar = () => {
    const next = !sidebarCollapsed
    // A manual toggle supersedes any auto-collapse — the resize listener
    // must not undo (or re-apply) what the user chose by hand.
    sidebarAutoCollapsedRef.current = false
    setSidebarCollapsed(next)
    localStorage.setItem('sidebarCollapsed', String(next))
    if (next) setSecondaryMenu(null)
  }

  // === PHONE: PERSISTENCE === frozen localStorage contract: phoneOpen / phoneDocked / phonePos /
  // phoneTab / phoneScreen / phoneSmsActiveThread / phoneSmsNewMode
  // (+ webPhoneInstanceId and rcAutoConnectAt written elsewhere).
  // Persist phone open/docked state
  useEffect(() => { localStorage.setItem('phoneOpen', String(callingPanelOpen)) }, [callingPanelOpen])
  useEffect(() => { localStorage.setItem('phoneDocked', String(phoneDocked)) }, [phoneDocked])
  useEffect(() => { localStorage.setItem('phonePos', callingPanelPos ? JSON.stringify(callingPanelPos) : '') }, [callingPanelPos])
  useEffect(() => { localStorage.setItem('phoneTab', callingPanelTab) }, [callingPanelTab])
  useEffect(() => { localStorage.setItem('phoneScreen', phoneScreenMode) }, [phoneScreenMode])
  useEffect(() => { localStorage.setItem('phoneSmsActiveThread', smsActiveThread ? String(smsActiveThread) : '') }, [smsActiveThread])
  useEffect(() => { localStorage.setItem('phoneSmsNewMode', String(smsNewMode)) }, [smsNewMode])

  // Re-clamp floating phone position to viewport on window resize (debounced 150ms)
  // so the panel doesn't get stranded off-screen after a window shrink.
  useEffect(() => {
    let t: ReturnType<typeof setTimeout> | null = null
    const onResize = () => {
      if (t) clearTimeout(t)
      t = setTimeout(() => {
        if (phoneDocked) return
        setCallingPanelPos(prev => {
          if (!prev) return prev
          const pw = 296, ph = 580
          const maxX = Math.max(0, window.innerWidth - pw)
          const maxY = Math.max(0, window.innerHeight - ph)
          const nx = Math.max(0, Math.min(prev.x, maxX))
          const ny = Math.max(0, Math.min(prev.y, maxY))
          return (nx === prev.x && ny === prev.y) ? prev : { x: nx, y: ny }
        })
      }, 150)
    }
    window.addEventListener('resize', onResize)
    return () => { window.removeEventListener('resize', onResize); if (t) clearTimeout(t) }
  }, [phoneDocked])

  // === PHONE: EFFECTS — ESC + BEFOREUNLOAD === ESC handler below; its companion leave-page guard
  // (wired only while a call is live) sits just before the OUTBOUND DIAL region — grep 'You have an active call'.
  // TRAP(phone/esc-minimize): ESC minimizes, NEVER closes (closing kills a live WebRTC session); suppressed while typing, while an incoming call presents, and while ringing/connected.
  // Escape-key minimizes the phone panel (never closes — closing would kill
  // a live WebRTC session). Only fires when NOT in an active ringing/connected
  // call so accidental presses during a call don't surprise-hang-up.
  useEffect(() => {
    if (!callingPanelOpen || callingPanelMinimized) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      // Skip if user is typing in an input field (let ESC just blur it)
      const active = document.activeElement as HTMLElement | null
      if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.tagName === 'SELECT' || active.isContentEditable)) return
      // Skip if an incoming call is being presented — ESC shouldn't decline it accidentally
      if (inboundCallSession) return
      // Skip when an active call is underway — keep the user in their call
      if (callingState === 'ringing' || callingState === 'connected') return
      setCallingPanelMinimized(true)
      setCallingSettingsOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [callingPanelOpen, callingPanelMinimized, inboundCallSession, callingState])

  // TRAP(phone/tdz-favorites): the reloadPhoneFavoritesRef indirection dodges a TDZ crash (the callback is declared much later in the file) — do not inline the call or reorder the declarations.
  // Load favorites whenever the phone panel opens *or* the user switches into
  // the Favorites tab. The panel-open trigger covers the warm-cache case for
  // updates from another session; the tab-switch trigger covers the cold-open
  // case where the user clicks Favorites first and the panel-open fetch
  // hasn't resolved yet (otherwise the tab shows empty until they click away
  // and back). reloadPhoneFavorites is declared further down in the file; we
  // reference it via the ref below (assigned in its useCallback) to avoid the
  // TDZ hoisting warning.
  useEffect(() => {
    if (!callingPanelOpen) return
    if (callingPanelTab !== 'favorites' && callingPanelTab !== 'recents') return
    reloadPhoneFavoritesRef.current?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callingPanelOpen, callingPanelTab])

  // One-time: probe whether live call transcription is enabled on this account
  useEffect(() => {
    if (phoneTranscriptCap) return
    fetchLiveTranscriptionCapability()
      .then(cap => setPhoneTranscriptCap({ enabled: !!cap.enabled, note: cap.note || '' }))
      .catch(() => setPhoneTranscriptCap({ enabled: false, note: 'Live transcription capability unknown.' }))
  }, [phoneTranscriptCap])

  // Live transcription (Grok streaming STT via the /api/crm/phone/stt-stream
  // Worker bridge). Runs whenever connected + the admin flag is on (NOT gated
  // on the panel being open) so the transcript captures the call from its very
  // start; the overlay just displays the accumulating text when opened.
  // Captures the agent mic (ch0) + the caller's remote stream (ch1), mixes to
  // stereo PCM16, and streams it up; lines come back via onLine. Taps copies of
  // the audio — never the call's media path — so a failure can't affect the call.
  // Capture does NOT begin on the bare 'connected' flip — it waits for a
  // SETTLED two-way audio condition (see the gate below) so ringback / a
  // connect transient / a call that answers into voicemail is NOT transcribed.
  useEffect(() => {
    if (callingState !== 'connected' || !phoneTranscriptCap?.enabled) return
    // Fresh call → drop the previous call's transcript (it persisted through
    // the post-call review so it stayed readable). Capture starts once the
    // connection settles (below), independent of whether the panel is open, so
    // opening it mid-call still shows the conversation from its real beginning.
    setPhoneTranscript([])
    let t: LiveTranscriber | null = null
    let cancelled = false
    let waitTimer: number | null = null
    const handleLine = (line: TranscriptLine) => {
      setPhoneTranscript(prev => {
        const arr = prev.slice()
        // Streaming STT interleaves the two legs (ch0 = you, ch1 = caller), so a
        // channel's in-progress (interim) line is NOT always the very last entry —
        // the other speaker's line can land in between. Find the last line for THIS
        // channel: if it's still interim, update it in place (interim→interim, or
        // interim→final commit); otherwise it's a new segment → append. Looking only
        // at arr[last] (the old logic) stranded interims next to their own finals and
        // appended duplicate finals whenever the channels interleaved.
        let idx = -1
        for (let i = arr.length - 1; i >= 0; i--) { if (arr[i].channel === line.channel) { idx = i; break } }
        if (idx >= 0 && !arr[idx].isFinal) {
          arr[idx] = { channel: line.channel, text: line.text, isFinal: line.isFinal, ts: arr[idx].ts }
        } else if (idx >= 0 && arr[idx].isFinal && line.isFinal && arr[idx].text.trim().toLowerCase() === line.text.trim().toLowerCase()) {
          // Exact-duplicate final for this channel with no new interim between them
          // (xAI restates a segment on speech_final) — drop it.
          return arr
        } else {
          arr.push({ channel: line.channel, text: line.text, isFinal: line.isFinal, ts: Date.now() })
        }
        return arr.length > 200 ? arr.slice(-200) : arr
      })
    }
    // Caller audio source (verified against ringcentral-web-phone@2.4.3 source):
    // the v2 CallSession has NO `remoteMediaStream`, and the app's remoteAudioRef
    // is never populated — the SDK plays remote audio through its OWN hidden
    // `session.audioElement`, set on `rtcPeerConnection.ontrack`. So pull the
    // track straight off the session's peer connection (fallback: its audioEl).
    const getRemoteStream = (): MediaStream | undefined => {
      const s: any = activeSessionRef.current
      if (!s) return undefined
      try {
        const pc: RTCPeerConnection | undefined = s.rtcPeerConnection
        const tracks = pc?.getReceivers?.()
          .map(r => r.track)
          .filter((t): t is MediaStreamTrack => !!t && t.kind === 'audio' && t.readyState === 'live') ?? []
        if (tracks.length) return new MediaStream(tracks)
      } catch { /* fall through */ }
      const el: HTMLAudioElement | undefined = s.audioElement
      if (el?.srcObject instanceof MediaStream && el.srcObject.getAudioTracks().length) return el.srcObject
      return undefined
    }
    // SETTLED-CONNECTION GATE (do NOT start on the connect transient).
    // `callingState` flips to 'connected' the instant the v2 session emits
    // 'answered' (or the post-await SIP-403 reconcile fires) — but that point
    // can be early-media / ringback / a momentary connect transient BEFORE
    // genuine two-way audio (e.g. a call that "answers" into voicemail). The
    // old logic started capture the instant ANY live remote track appeared, so
    // it transcribed the agent pre-talking + a voicemail greeting. We now wait
    // for a genuinely-connected, settled, two-way condition before starting:
    //   1. the session peer connection has BOTH a live outbound (agent mic)
    //      track AND a live inbound (caller) track — confirmed bidirectional
    //      audio, not a one-way early-media/ringback stream;
    //   2. that two-way condition holds continuously for STABILIZE_MS;
    //   3. for OUTBOUND, the connect anchor (callConnectedAtRef, set at
    //      'answered' in onAccepted) is itself at least STABILIZE_MS old — the
    //      'connected' state has settled, not just flickered. INBOUND answers
    //      (answerInboundCall, user-initiated pickup) never set the anchor and
    //      have no ringback window, so they rely on (1)+(2) only.
    // Anything captured before this point is discarded (panel starts empty),
    // so a ring-out / voicemail call yields NO pre-talk transcript.
    const STABILIZE_MS = 1500
    const POLL_MS = 250
    const ACQUIRE_TIMEOUT_MS = 20000
    const effectStartedAt = Date.now()
    let bidiSince = 0 // when live two-way audio first held continuously
    // Returns the live caller stream ONLY when a confirmed two-way audio path
    // exists (live mic sender + live caller receiver); undefined otherwise.
    const liveBidiRemote = (): MediaStream | undefined => {
      const s: any = activeSessionRef.current
      if (!s) return undefined
      let pc: RTCPeerConnection | undefined
      try { pc = s.rtcPeerConnection } catch { /* */ }
      const micLive = !!pc?.getSenders?.().some(
        (snd) => !!snd.track && snd.track.kind === 'audio' && snd.track.readyState === 'live'
      )
      if (!micLive) return undefined
      const remote = getRemoteStream() // already filtered to live caller audio tracks
      if (!remote || remote.getAudioTracks().length === 0) return undefined
      return remote
    }
    // ── OUTBOUND remote-audio ENERGY gate (2026-06-26) ──────────────────────
    // The bidi gate above is satisfied by a live-but-SILENT remote track. On an
    // OUTBOUND call that answers into VOICEMAIL the caller leg goes live at
    // connect but stays silent while the agent keeps pre-talking ("Yes / this
    // one is / time and effort…"); the old gate started capture then, so the
    // agent's pre-greeting chatter got transcribed before the voicemail prompt.
    // Fix: for OUTBOUND, after the bidi+settle gate, additionally wait until the
    // REMOTE (caller) leg is ACTUALLY PRODUCING SOUND — an AnalyserNode taps a
    // COPY of the remote stream (analysis-only, never connected to destination,
    // never in the call media path) and we only start the LiveTranscriber once
    // the remote level holds above a silence floor for a brief sustain window
    // (the voicemail greeting, or a human voice). Capture STARTS only then, so
    // the agent's pre-answer mic audio is never transcribed (panel stays empty).
    // INBOUND (callConnectedAtRef null — the user picked up an already-talking
    // caller) keeps the immediate start; no regression there.
    // Threshold: normalized time-domain RMS (Uint8 samples, 128-centered → 0..1).
    // 0.02 ignores digital silence + line/codec comfort-noise (typically <0.005)
    // but trips reliably on a voicemail greeting or speech (0.03–0.2+); sustain
    // 250ms rejects a single comfort-noise blip. NO time-based fallback: a real
    // conversation always has remote audio, so we wait indefinitely for it and
    // the effect cleanup tears the gate down when the call ends. Both tunable.
    const ENERGY_RMS_THRESHOLD = 0.02
    const ENERGY_SUSTAIN_MS = 250
    const ENERGY_SAMPLE_MS = 50
    let energyCtx: AudioContext | null = null
    let energyTimer: number | null = null
    let energyAboveSince = 0
    const teardownEnergyGate = () => {
      if (energyTimer) { clearTimeout(energyTimer); energyTimer = null }
      if (energyCtx) { try { void energyCtx.close() } catch { /* */ } energyCtx = null }
    }
    // Resolves (calls onAudible) once the remote leg is audibly producing sound.
    const waitForRemoteAudio = (remote: MediaStream, onAudible: () => void) => {
      try {
        const Ctx = window.AudioContext || (window as any).webkitAudioContext
        energyCtx = new Ctx()
        const src = energyCtx.createMediaStreamSource(remote)
        const analyser = energyCtx.createAnalyser()
        analyser.fftSize = 512
        src.connect(analyser) // analysis-only sink — NOT wired to destination; cannot affect call audio
        const buf = new Uint8Array(analyser.fftSize)
        const sample = () => {
          if (cancelled) return
          analyser.getByteTimeDomainData(buf)
          let sumSq = 0
          for (let i = 0; i < buf.length; i++) { const d = (buf[i] - 128) / 128; sumSq += d * d }
          const rms = Math.sqrt(sumSq / buf.length)
          if (rms >= ENERGY_RMS_THRESHOLD) {
            if (!energyAboveSince) energyAboveSince = Date.now()
            if (Date.now() - energyAboveSince >= ENERGY_SUSTAIN_MS) {
              teardownEnergyGate()
              onAudible()
              return
            }
          } else {
            energyAboveSince = 0 // reset — sustained, not cumulative
          }
          energyTimer = window.setTimeout(sample, ENERGY_SAMPLE_MS)
        }
        sample()
      } catch (e) {
        // Web Audio unavailable (constructor threw) — fail OPEN to the prior
        // behavior rather than silently losing transcription. This is an API
        // error path, NOT a time-based silence fallback.
        console.warn('[LiveTranscript] energy gate unavailable, starting', e)
        teardownEnergyGate()
        onAudible()
      }
    }
    const tick = () => {
      if (cancelled) return
      const remote = liveBidiRemote()
      if (!remote) {
        bidiSince = 0
        if (Date.now() - effectStartedAt >= ACQUIRE_TIMEOUT_MS) {
          console.warn('[LiveTranscript] no settled two-way audio after ~20s — not starting')
          return
        }
        waitTimer = window.setTimeout(tick, POLL_MS)
        return
      }
      if (!bidiSince) bidiSince = Date.now()
      // Outbound: the 'answered' anchor must itself be settled. Inbound (anchor
      // null) relies purely on the two-way audio holding for the window.
      const connectAnchor = callConnectedAtRef.current
      const connectSettled = !connectAnchor || Date.now() - connectAnchor >= STABILIZE_MS
      if (Date.now() - bidiSince < STABILIZE_MS || !connectSettled) {
        waitTimer = window.setTimeout(tick, POLL_MS)
        return
      }
      // Confirmed connected + settled two-way audio. Start capture, discarding
      // anything accumulated before this point so the panel only holds the
      // genuine conversation (it should already be empty — capture hasn't begun).
      const startTranscriber = () => {
        if (cancelled) return
        setPhoneTranscript([])
        console.info('[LiveTranscript] starting transcription')
        t = new LiveTranscriber({
          remoteStream: remote,
          onLine: handleLine,
          onError: (msg) => console.warn('[LiveTranscript]', msg),
        })
        liveTranscriberRef.current = t
        void t.start()
      }
      // OUTBOUND (connect anchor set): wait for the caller leg to actually
      // produce sound before capturing, so a voicemail/ring-out call never
      // transcribes the agent's pre-answer chatter. INBOUND (anchor null):
      // the caller is already on the line — start immediately (no regression).
      const isOutbound = callConnectedAtRef.current != null
      if (isOutbound) {
        console.info('[LiveTranscript] two-way audio settled — waiting for remote-audio energy (outbound)')
        waitForRemoteAudio(remote, startTranscriber)
      } else {
        startTranscriber()
      }
    }
    tick()
    return () => {
      cancelled = true
      if (waitTimer) clearTimeout(waitTimer)
      teardownEnergyGate() // close the analyser AudioContext + stop its sampling loop
      t?.stop()
      if (liveTranscriberRef.current === t) liveTranscriberRef.current = null
    }
  }, [callingState, phoneTranscriptCap?.enabled])

  // Auto-scroll transcript panel to newest line
  useEffect(() => {
    const el = phoneTranscriptRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [phoneTranscript])

  // Enrich the seeded incoming-call info with a CRM match, then "reveal".
  // The SIP handler seeds name/number synchronously WITH the session (one
  // surface, no banner swap); this effect adds the CRM name + entity link
  // by MERGING into the seed — never replacing it — and flips incomingReady
  // once the match settles (or after 450ms, whichever is first) so the
  // caller identity paints exactly once instead of flashing raw → CRM.
  useEffect(() => {
    if (!inboundCallSession) {
      setIncomingCallInfo(null)
      setIncomingReady(true)
      // Stop ringtone if it was playing
      try {
        if (incomingRingtoneRef.current) {
          incomingRingtoneRef.current.pause()
          incomingRingtoneRef.current.currentTime = 0
        }
      } catch { /* ignore */ }
      return
    }
    const seedNumber = incomingSeedRef.current?.number || ''
    let cancelled = false
    const revealTimer = window.setTimeout(() => setIncomingReady(true), 450)
    if (seedNumber) {
      matchPhoneNumber(seedNumber).then(res => {
        if (cancelled) return
        const topMatch = res.matches?.[0]
        const crmName = topMatch?.client?.preferred_name?.trim() || ''
        const goodName = !!crmName && !/^[\s+\-().\d]+$/.test(crmName)
        const entityId = topMatch?.client?.unified_client_id || null
        const topDeal = topMatch?.deals?.[0]
        let entityLabel: string | null = null
        let entityKind: 'contact' | 'lead' | 'deal' | null = null
        if (topDeal) { entityLabel = `Deal #${topDeal.id}`; entityKind = 'deal' }
        else if (entityId) { entityLabel = 'Contact'; entityKind = 'contact' }
        setIncomingCallInfo(prev => prev ? {
          ...prev,
          name: goodName ? crmName : prev.name,
          entityKind,
          entityId: entityId ? String(entityId) : (topDeal ? String(topDeal.id) : null),
          entityLabel,
        } : prev)
        if (goodName) setIncomingCallToast(prev => prev ? { ...prev, title: `Incoming call: ${crmName}` } : prev)
      }).catch(() => { /* ignore — the seed info stands */ })
        .finally(() => { if (!cancelled) setIncomingReady(true) })
    } else {
      setIncomingReady(true)
    }
    return () => { cancelled = true; window.clearTimeout(revealTimer) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inboundCallSession])

  // Lazy-init incoming ringtone element. Uses Web Audio to synthesize a simple
  // two-tone ring so we don't need to ship an MP3 asset. Plays only when
  // incomingCallInfo is set and no existing ringer is heard from the SIP SDK.
  useEffect(() => {
    if (!incomingCallInfo) return
    // The RingCentral WebRTC SDK plays its own inbound ringer; keep ours subtle
    // so we don't double-ring. If the SDK hooked up a native ringer, our extra
    // ringtone is a complement — browsers mix both at ~30-40% volume.
    let osc1: OscillatorNode | null = null
    let osc2: OscillatorNode | null = null
    let gain: GainNode | null = null
    let interval: ReturnType<typeof setInterval> | null = null
    let ctx: AudioContext | null = null
    try {
      const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext
      if (!AudioCtx) return
      const localCtx: AudioContext = new AudioCtx()
      ctx = localCtx
      gain = localCtx.createGain()
      gain.gain.value = 0
      gain.connect(localCtx.destination)
      const ring = () => {
        if (!ctx || !gain) return
        osc1 = ctx.createOscillator(); osc1.frequency.value = 440
        osc2 = ctx.createOscillator(); osc2.frequency.value = 480
        osc1.connect(gain); osc2.connect(gain)
        const now = ctx.currentTime
        gain.gain.cancelScheduledValues(now)
        gain.gain.setValueAtTime(0, now)
        gain.gain.linearRampToValueAtTime(0.04, now + 0.08)
        gain.gain.setValueAtTime(0.04, now + 1.9)
        gain.gain.linearRampToValueAtTime(0, now + 2.0)
        osc1.start(now); osc2.start(now)
        osc1.stop(now + 2.0); osc2.stop(now + 2.0)
      }
      ring()
      interval = setInterval(ring, 3000)
    } catch { /* audio blocked — silent */ }
    return () => {
      if (interval) clearInterval(interval)
      try { osc1?.stop(); osc2?.stop() } catch { /* ignore */ }
      try { ctx?.close() } catch { /* ignore */ }
    }
  }, [incomingCallInfo])

  // Track sidebar scroll position for arrows
  useEffect(() => {
    const el = navListRef.current
    if (!el) return
    const check = () => {
      setSidebarScrollUp(el.scrollTop > 4)
      setSidebarScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 4)
    }
    check()
    el.addEventListener('scroll', check, { passive: true })
    const ro = new ResizeObserver(check)
    ro.observe(el)
    return () => { el.removeEventListener('scroll', check); ro.disconnect() }
  }, [phoneDocked])

  const startSidebarScroll = useCallback((dir: number) => {
    if (sidebarScrollInterval.current) return
    const el = navListRef.current
    if (!el) return
    const step = () => {
      el.scrollTop += dir * 2
      sidebarScrollInterval.current = requestAnimationFrame(step) as unknown as ReturnType<typeof setInterval>
    }
    sidebarScrollInterval.current = requestAnimationFrame(step) as unknown as ReturnType<typeof setInterval>
  }, [])
  const stopSidebarScroll = useCallback(() => {
    if (sidebarScrollInterval.current) { cancelAnimationFrame(sidebarScrollInterval.current as unknown as number); sidebarScrollInterval.current = null }
  }, [])

  // Close secondary menu on outside click (but not on sidebar nav clicks — those toggle via onClick)
  useEffect(() => {
    if (!secondaryMenu) return
    function handleClick(e: MouseEvent) {
      const target = e.target as Node
      if (secondaryRef.current && secondaryRef.current.contains(target)) return
      if (sidebarScrollRef.current && sidebarScrollRef.current.contains(target)) return
      setSecondaryMenu(null); setFlyoutActivated(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [secondaryMenu])

  // Deals kanban enhancements
  const dealCategory = 0 // Single Sales pipeline — always category 0
  const setDealCategory = (_v: number) => {} // no-op — single pipeline
  const [allPipelineStages, setAllPipelineStages] = useState<PipelineStage[] | null>(null)
  const [draggedDealId, setDraggedDealId] = useState<string | null>(null)
  const [dragOverStageId, setDragOverStageId] = useState<string | null>(null)
  const [dealConfirmMove, setDealConfirmMove] = useState<{ dealId: string; dealTitle: string; stageId: string; stageName: string } | null>(null)
  // Loss reason modal (shown when moving to LOST)
  const [lossReasonModal, setLossReasonModal] = useState<{ dealId: string; dealTitle: string } | null>(null)
  const [lossReasonValue, setLossReasonValue] = useState('Price Too High')
  const [lossReasonNotes, setLossReasonNotes] = useState('')
  // No-show / reschedule modal (shown when moving to NO_SHOW_RESCHEDULE)
  const [noShowModal, setNoShowModal] = useState<{ dealId: string; dealTitle: string } | null>(null)
  // Contract signed confirmation modal
  const [contractSignedModal, setContractSignedModal] = useState<{ dealId: string; dealTitle: string } | null>(null)
  // "Send confirmation email to client" checkbox state for the Contract Signed
  // modal. Defaults to true so the email fires unless the user opts out.
  const [contractSignedEmail, setContractSignedEmail] = useState(true)
  const [stageConfirmModal, setStageConfirmModal] = useState<{ dealId: string; dealTitle: string; fromStage: string; fromLabel: string; toStage: string; toLabel: string; toColor: string } | null>(null)
  const [dealStagePicker, setDealStagePicker] = useState<{ dealId: string; dealTitle: string; currentStage: string; currentLabel: string } | null>(null)
  const [apptSetModal, setApptSetModal] = useState<{ dealId: string; dealTitle: string; fromStage: string; fromLabel: string } | null>(null)
  const [apptSetDate, setApptSetDate] = useState('')
  const [apptSetEstimator, setApptSetEstimator] = useState('')
  const [apptShowAllUsers, setApptShowAllUsers] = useState(false)
  const [dealStageDropdownPos, setDealStageDropdownPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
  // Project stage picker — click the badge on the Projects table to change
  const [projectStagePicker, setProjectStagePicker] = useState<{ projectId: number; currentStage: string } | null>(null)
  const [projectStagePickerPos, setProjectStagePickerPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
  const [projectStageSaving, setProjectStageSaving] = useState(false)
  // Hold modal state — shown when transitioning to P_ON_HOLD stage
  const [projectHoldModal, setProjectHoldModal] = useState<{ projectId: number } | null>(null)
  const [projectHoldType, setProjectHoldType] = useState<string>('')
  const [projectHoldReason, setProjectHoldReason] = useState('')
  const [projectHoldSaving, setProjectHoldSaving] = useState(false)
  const [noShowReason, setNoShowReason] = useState('Customer no-show')
  const [noShowNewAppt, setNoShowNewAppt] = useState('')
  const [noShowEstimatorId, setNoShowEstimatorId] = useState<number | null>(null)
  useEffect(() => {
    if (!noShowModal) return
    const dealId = noShowModal.dealId
    const deal = (liveDeals || []).find((d) => String(d.id) === String(dealId))
    if (!deal) { setNoShowEstimatorId(null); return }
    const raw = (deal as any).ownerId ?? (deal as any).assignedUserId ?? null
    setNoShowEstimatorId(raw ? parseInt(String(raw)) : null)
  }, [noShowModal, liveDeals])
  // Lead conversion modal
  const [leadConvertModal, setLeadConvertModal] = useState<{ leadId: number | string; leadTitle: string } | null>(null)
  const [leadConvertAppt, setLeadConvertAppt] = useState('')
  const [leadConvertEstimator, setLeadConvertEstimator] = useState<number | null>(null)
  // Additional attendees on the converted deal's appointment. Follows the
  // same convention as the Create New meeting modal: owner = blue,
  // attendees = green. Picking a chip promotes it to owner if none is set
  // yet, otherwise toggles it as an attendee.
  const [leadConvertAttendees, setLeadConvertAttendees] = useState<number[]>([])
  const [dealContextMenu, setDealContextMenu] = useState<{ mouseX: number; mouseY: number; isBottomHalf: boolean; dealId: string } | null>(null)
  const [dealToast, setDealToast] = useState<{ id: number; message: string; type: 'success' | 'error' | 'info' } | null>(null)
  const [drawerClosing, setDrawerClosing] = useState(false)
  const kanbanScrollRef = useRef<HTMLDivElement>(null)
  const [showLeftEar, setShowLeftEar] = useState(false)
  const [showRightEar, setShowRightEar] = useState(false)

  // Legacy workgroup state (retained for initial CRM load and view-reset)
  const [, setLiveWorkgroups] = useState<Workgroup[] | null>(null)
  const [, setSelectedWorkgroup] = useState<Workgroup | null>(null)

  // Command palette
  const [cmdOpen, setCmdOpen] = useState(false)
  const [cmdQuery, setCmdQuery] = useState(() => sessionStorage.getItem(uKey('cmdQuery')) || '')
  const [cmdResults, setCmdResults] = useState<SearchResults | null>(null)
  const [cmdLoading, setCmdLoading] = useState(false)
  const [cmdSelected, setCmdSelected] = useState(0)
  const [cmdFilters, setCmdFilters] = useState<{ clients: boolean; leads: boolean; deals: boolean; employees: boolean }>({ clients: true, leads: true, deals: true, employees: true })
  const cmdInputRef = useRef<HTMLInputElement>(null)

  // Derived: use live data when available, fall back to mock
  const meetings = liveMeetings ?? mockMeetings
  const allEmployeeNames = liveUsers
    ? liveUsers.map((u) => u.name)
    : mockEmployeeNames
  const prefsSearchValue = prefsSearch.trim().toLowerCase()
  const visiblePrefsSections = PREFS_NAV_SECTIONS
    .filter((section) => {
      // Sections without allowedRoles are visible to everyone (e.g. 'My
      // Preferences'). Legacy `adminOnly: true` sections without
      // allowedRoles fall back to ['super_admin', 'admin'].
      const allowed: readonly CanonicalRole[] | undefined =
        section.allowedRoles ?? (section.adminOnly ? ['super_admin', 'admin'] : undefined)
      if (!allowed) return true
      return allowed.includes(effectiveCanonicalRole)
    })
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => {
        if (!prefsSearchValue) return true
        if (section.title.toLowerCase().includes(prefsSearchValue)) return true
        const searchParts = [item.label, ...(item.keywords ?? [])]
        return searchParts.some((part) => part.toLowerCase().includes(prefsSearchValue))
      }),
    }))
    .filter((section) => section.items.length > 0)
  const activePrefsTitle = PREFS_TAB_LABELS[prefsTab] || prefsTab
  const activePrefsDescription = PREFS_TAB_DESCRIPTIONS[prefsTab] || ''
  const prefsTotalDirtyCount = prefsDirtyCount + notifPrefsDirtyCount
  const prefsFooterWarningActive = prefsUnsavedBlocked && prefsTab === 'notifications' && notifPrefsDirtyCount > 0
  // Fetch live CRM data — re-fetch when month changes
  useEffect(() => {
    let cancelled = false
    // Fetch the FULL displayed grid (gridStart..gridEnd), not just the month.
    // The month view shows trailing prev-month + leading next-month days (e.g.
    // May 31 and July 1-4 around June 2026), and those "outside" cells must
    // receive their events too — otherwise they render empty and the user can't
    // see an appointment that lands on them. Bounds come from the same helper
    // buildCalendar uses, so the fetched range and the rendered grid can never
    // drift. gridEnd stays inclusive (the backend already includes the `to`
    // day, exactly as the month-last day did before).
    const { gridStart, gridEnd } = calendarGridBounds(calendarMonth)
    const from = toYmd(gridStart)
    const to = toYmd(gridEnd)
    setCalendarDataLoading(true)

    async function load() {
      try {
        const [users, events] = await Promise.all([
          liveUsers ? Promise.resolve(liveUsers) : fetchCalendarUsers(),
          fetchCalendarEvents(from, to),
        ])
        if (cancelled) return
        // bp-cal-roster-v1: seed the module-level roster maps (assignment
        // dropdowns, meeting/owner pickers, id→name resolution) from the SAME
        // fetched /api/calendar/users list the calendar grid uses, so a
        // newly-provisioned @botpros.ai teammate appears everywhere. The
        // setLiveUsers state update below re-renders the tree so readers pick up
        // the refreshed bindings.
        applyCalendarRoster(users)
        if (!liveUsers) setLiveUsers(users)
        setLiveMeetings(events)
        setApiError(null)
        // Enable all employees by default on first load
        setEnabledEmployees((prev) =>
          prev.size === 0 ? new Set(users.map((u) => u.name)) : prev,
        )
      } catch (err) {
        if (cancelled) return
        console.error('API fetch failed, using mock data:', err)
        setApiError(err instanceof Error ? err.message : 'API error')
        setLiveMeetings(null)
        setEnabledEmployees((prev) =>
          prev.size === 0 ? new Set(mockEmployeeNames) : prev,
        )
      } finally {
        if (!cancelled) setCalendarDataLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [calendarMonth, calendarRefreshTick]) // eslint-disable-line react-hooks/exhaustive-deps

  // Refetch calendar events whenever the user enters the calendar view, so events
  // created elsewhere (e.g. a deal appointment from the create panel) show up
  // without a manual page reload. Path-agnostic — doesn't matter how the event
  // was created, navigating to the calendar always pulls fresh data.
  useEffect(() => {
    if (activeView === 'calendar') setCalendarRefreshTick(t => t + 1)
  }, [activeView])

  // Fetch CRM data (deals, pipeline, contacts) once on mount
  useEffect(() => {
    let cancelled = false
    async function loadCrm() {
      try {
        const [dealsRes, pipeline, contactsRes, leadsRes, workgroupsRes] = await Promise.all([
          fetchDeals(0, 1, dealPageSize),
          fetchPipeline(0, false, dashboardSubsidiary, periodDateRange?.from, periodDateRange?.to),
          fetchContacts(),
          fetchLeads(),
          fetchWorkgroups().catch(() => ({ total: 0, workgroups: [] })),
        ])
        if (cancelled) return
        setLiveDeals(dealsRes.deals)
        setDealTotal(dealsRes.total)
        setDealPages(dealsRes.pages)
        setLivePipeline(pipeline)
        setDashboardLastRefresh(new Date())
        setLiveContacts(contactsRes.contacts)
        setContactTotal(contactsRes.total)
        setLiveLeads(leadsRes.leads)
        setLeadTotal(leadsRes.total)
        if (workgroupsRes.workgroups.length > 0) setLiveWorkgroups(workgroupsRes.workgroups)
        setCommandLastUpdated(new Date())
        setCommandError(null)
      } catch (err) {
        console.error('CRM fetch failed:', err)
        if (!cancelled) setCommandError(err instanceof Error ? err.message : 'CRM data failed to load')
      }
    }
    loadCrm()
    return () => { cancelled = true }
  }, [commandRefreshTick]) // eslint-disable-line react-hooks/exhaustive-deps

  // Convert a period key to a half-open [from, to) date window (YYYY-MM-DD
  // strings). Returns null for 'all' → no filter → backend counts every deal.
  const periodDateRange = useMemo((): { from: string; to: string } | null => {
    if (dashboardPeriod === 'all') return null
    const now = new Date()
    const y = now.getFullYear()
    const m = now.getMonth()
    const d = now.getDate()
    const pad = (n: number) => String(n).padStart(2, '0')
    const fmt = (dt: Date) => `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`
    if (dashboardPeriod === 'today') {
      return { from: fmt(new Date(y, m, d)), to: fmt(new Date(y, m, d + 1)) }
    }
    if (dashboardPeriod === 'week') {
      // ISO week starts Monday. JS getDay(): Sun=0..Sat=6. Treat Sun as 7.
      const day = now.getDay() || 7
      return { from: fmt(new Date(y, m, d - (day - 1))), to: fmt(new Date(y, m, d - (day - 1) + 7)) }
    }
    if (dashboardPeriod === 'month') {
      return { from: fmt(new Date(y, m, 1)), to: fmt(new Date(y, m + 1, 1)) }
    }
    if (dashboardPeriod === 'quarter') {
      const qStart = Math.floor(m / 3) * 3
      return { from: fmt(new Date(y, qStart, 1)), to: fmt(new Date(y, qStart + 3, 1)) }
    }
    if (dashboardPeriod === 'year') {
      return { from: fmt(new Date(y, 0, 1)), to: fmt(new Date(y + 1, 0, 1)) }
    }
    return null
  }, [dashboardPeriod])

  // Home pipeline snapshot: agency-wide stage metadata, including terminal
  // stages for the Won KPI. It is deliberately separate from the Deals view's
  // subsidiary-aware pipeline state and drives both Home pipeline panels.
  useEffect(() => {
    if (activeView !== 'command') return
    let cancelled = false
    setHomePipeline(null)
    fetchPipeline(0, true, undefined, periodDateRange?.from, periodDateRange?.to)
      .then((stages) => {
        if (cancelled) return
        setHomePipeline(stages)
        setCommandLastUpdated(new Date())
      })
      .catch(() => {
        if (!cancelled) setHomePipeline([])
      })
    return () => { cancelled = true }
  }, [activeView, periodDateRange, pipelineRefreshTick, commandRefreshTick])

  // Accounting snapshot: AR + aging are required; month-to-date revenue is a
  // cheap optional read from the current-month P&L report.
  useEffect(() => {
    if (activeView !== 'command') return
    let cancelled = false
    setHomeAccountingLoading(true)
    setHomeAccountingError(false)
    const load = async () => {
      try {
        const [ar, aging] = await Promise.all([fetchArSummary(), fetchArAgingSummary()])
        if (cancelled) return
        let mtdRevenue: number | null = null
        try {
          const pl = await fetchProfitLoss()
          mtdRevenue = (pl.data || [])
            .filter((account: any) => account.account_type === 'revenue')
            .reduce((sum: number, account: any) => sum + (Number(account.total_credit || 0) - Number(account.total_debit || 0)), 0)
        } catch {
          // The AR snapshot remains useful when a tenant has no P&L route.
        }
        const buckets = Object.entries(aging.data || {})
        const overdueInvoices = buckets
          .filter(([bucket]) => bucket !== 'current')
          .reduce((sum, [, value]) => sum + Number((value as any)?.count || 0), 0)
        setHomeAccounting({
          openAr: Number(ar.data?.total || 0),
          overdueInvoices,
          mtdRevenue,
        })
        setCommandLastUpdated(new Date())
      } catch {
        if (!cancelled) setHomeAccountingError(true)
      } finally {
        if (!cancelled) setHomeAccountingLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [activeView, commandRefreshTick])

  // Marketing Hub is optional. Use whichever collection endpoint is available
  // and hide the failure behind the pulse's quiet empty state when both are
  // absent or unavailable.
  useEffect(() => {
    if (activeView !== 'command') return
    let cancelled = false
    setMarketingPulseLoading(true)
    setMarketingPulseError(false)
    const load = async () => {
      const [runsResult, deliverablesResult] = await Promise.allSettled([
        fetchMarketingRuns({ limit: 5 }),
        fetchMarketingDeliverables({ limit: 5 }),
      ])
      if (cancelled) return
      const runs = runsResult.status === 'fulfilled' ? (runsResult.value.runs || []) : []
      const deliverables = deliverablesResult.status === 'fulfilled' ? (deliverablesResult.value.deliverables || []) : []
      if (runsResult.status === 'rejected' && deliverablesResult.status === 'rejected') {
        setMarketingPulseError(true)
        setMarketingPulse([])
        setMarketingPulseLoading(false)
        return
      }
      const items: HomeMarketingItem[] = [
        ...deliverables.map((item: any) => ({
          id: `deliverable-${item.id}`,
          title: item.title || `Deliverable #${item.id}`,
          kind: 'Deliverable' as const,
          status: item.status,
          createdAt: item.created_at || null,
        })),
        ...runs.map((item: any) => ({
          id: `run-${item.id}`,
          title: item.title || item.skill_slug || `Marketing run #${item.id}`,
          kind: 'Run' as const,
          status: item.status,
          createdAt: item.created_at || null,
        })),
      ].sort((left, right) => {
        const leftTime = Date.parse(left.createdAt || '')
        const rightTime = Date.parse(right.createdAt || '')
        return (Number.isFinite(rightTime) ? rightTime : 0) - (Number.isFinite(leftTime) ? leftTime : 0)
      }).slice(0, 5)
      setMarketingPulse(items)
      setMarketingPulseLoading(false)
    }
    load().catch(() => {
      if (!cancelled) {
        setMarketingPulseError(true)
        setMarketingPulse([])
        setMarketingPulseLoading(false)
      }
    })
    return () => { cancelled = true }
  }, [activeView, commandRefreshTick])

  // Refetch the pipeline when the dashboard subsidiary/period filter changes,
  // or after any deal mutation (pipelineRefreshTick bumps).
  useEffect(() => {
    try { localStorage.setItem('dashboard_subsidiary', dashboardSubsidiary) } catch { /* ignore */ }
    try { localStorage.setItem('dashboard_period', dashboardPeriod) } catch { /* ignore */ }
    let cancelled = false
    fetchPipeline(0, false, dashboardSubsidiary, periodDateRange?.from, periodDateRange?.to)
      .then((p) => { if (!cancelled) { setLivePipeline(p); setDashboardLastRefresh(new Date()); setCommandLastUpdated(new Date()); setCommandError(null) } })
      .catch((err) => { console.error('Pipeline refetch failed:', err); if (!cancelled) setCommandError(err instanceof Error ? err.message : 'Pipeline data failed to load') })
    return () => { cancelled = true }
  }, [dashboardSubsidiary, dashboardPeriod, periodDateRange, pipelineRefreshTick, commandRefreshTick])

  // Delivery watch — load in-progress projects on mount / route re-entry / manual
  // Refresh only. Uses /api/crm/projects with server-side pipelineStages filter +
  // start_date DESC sort so the newest starts surface first. Empty list → panel
  // renders the "No active projects" placeholder. We only need a handful
  // (pageSize=8) so the fetch stays cheap. No auto-refresh timer: the command
  // center is manual-refresh only (commandRefreshTick bumps via the Refresh button).
  useEffect(() => {
    if (activeView !== 'command') return
    let cancelled = false
    const load = () => {
      setDeliveryLoading(true)
      return fetch('/api/crm/projects?pipelineStages=P_IN_PROGRESS&sortField=start_date&sortOrder=DESC&pageSize=8&page=1')
        .then((r) => r.json())
        .then((res: any) => { if (!cancelled) { setDeliveryProjects(Array.isArray(res?.data) ? res.data : []); setCommandLastUpdated(new Date()) } })
        .catch((err) => { console.error('Delivery watch fetch failed:', err); if (!cancelled) setCommandError(err instanceof Error ? err.message : 'Delivery watch failed to load') })
        .finally(() => { if (!cancelled) setDeliveryLoading(false) })
    }
    load()
    return () => { cancelled = true }
  }, [activeView, commandRefreshTick])

  useEffect(() => {
    try { localStorage.setItem('command_owner_filter', commandOwnerFilter) } catch { /* ignore */ }
  }, [commandOwnerFilter])

  useEffect(() => {
    if (activeView !== 'command') return
    let cancelled = false
    setCommandTasksLoading(true)
    fetchTasks({
      page: 1,
      limit: 50,
      status: 'open',
      assignedTo: commandOwnerFilter !== 'all' && /^\d+$/.test(commandOwnerFilter) ? Number(commandOwnerFilter) : undefined,
    })
      .then((res) => {
        if (cancelled) return
        setCommandTasks(res.tasks || [])
        setCommandLastUpdated(new Date())
      })
      .catch((err) => {
        console.error('Home task fetch failed:', err)
        if (!cancelled) setCommandError(err instanceof Error ? err.message : 'Task queue failed to load')
      })
      .finally(() => { if (!cancelled) setCommandTasksLoading(false) })
    return () => { cancelled = true }
  }, [activeView, commandOwnerFilter, commandRefreshTick])

  // Reload deals + full pipeline stages when category changes
  useEffect(() => {
    let cancelled = false
    async function loadCategory() {
      try {
        if (dealsScope === 'closed') {
          // Closed Deals tab — CONTRACT_SIGNED only. Uses the single-stage
          // query param + exclude_closed=0 so the terminal-stage filter
          // doesn't strip the results. Capped page size because Contract
          // Signed + historical can be ~14k.
          const dealsRes = await fetchDeals(-1, dealPage, Math.min(50, dealPageSize), { excludeClosed: false, stage: 'CONTRACT_SIGNED', search: dealSearchDebounced || undefined })
          if (cancelled) return
          setLiveDeals(dealsRes.deals)
          setDealTotal(dealsRes.total)
          setDealPages(dealsRes.pages)
          setCommandLastUpdated(new Date())
        } else if (dealsScope === 'all') {
          // All Deals tab — every row including pre-migration historical.
          const dealsRes = await fetchDeals(-1, dealPage, Math.min(50, dealPageSize), { excludeClosed: false, search: dealSearchDebounced || undefined })
          if (cancelled) return
          setLiveDeals(dealsRes.deals)
          setDealTotal(dealsRes.total)
          setDealPages(dealsRes.pages)
          setCommandLastUpdated(new Date())
        } else if (dealCategory < 0) {
          // "All deals" — fetch all deals, keep current pipeline stages
          const dealsRes = await fetchDeals(dealCategory, dealPage, dealPageSize, { search: dealSearchDebounced || undefined })
          if (cancelled) return
          setLiveDeals(dealsRes.deals)
          setDealTotal(dealsRes.total)
          setDealPages(dealsRes.pages)
          setCommandLastUpdated(new Date())
        } else {
          // Pipeline respects the dashboard's subsidiary selector.
          // includeTerminal=true so we can populate allPipelineStages (used by
          // the stage picker dropdown which needs every stage including terminal
          // ones like Contract Signed and Lost).
          const [dealsRes, stages] = await Promise.all([
            fetchDeals(dealCategory, dealPage, dealPageSize, { search: dealSearchDebounced || undefined }),
            fetchPipeline(dealCategory, true, dashboardSubsidiary, periodDateRange?.from, periodDateRange?.to),
          ])
          if (cancelled) return
          setLiveDeals(dealsRes.deals)
          setDealTotal(dealsRes.total)
          setDealPages(dealsRes.pages)
          setAllPipelineStages(stages)
          // Local filter for livePipeline must match the backend's filter when
          // includeTerminal=false — the DB stores semantics as full words
          // ('success'/'failure'), not single letters. Earlier this was a silent
          // bug that let Contract Signed and Lost leak into the Pipeline Value
          // KPI card on the home dashboard.
          setLivePipeline(stages.filter((s) => !isTerminalPipelineStage(s)))
          setCommandLastUpdated(new Date())
        }
      } catch (err) {
        console.error('Pipeline load failed:', err)
        if (!cancelled) setCommandError(err instanceof Error ? err.message : 'Deal data failed to load')
      }
    }
    loadCategory()
    return () => { cancelled = true }
  }, [dealCategory, dashboardSubsidiary, periodDateRange, dealPage, dealPageSize, dealsScope, dealSearchDebounced, commandRefreshTick])

  function refetchCurrentDeals() {
    const searchOpt = dealSearchDebounced || undefined
    if (dealsScope === 'closed') {
      return fetchDeals(-1, dealPage, Math.min(50, dealPageSize), { excludeClosed: false, stage: 'CONTRACT_SIGNED', search: searchOpt })
    } else if (dealsScope === 'all') {
      return fetchDeals(-1, dealPage, Math.min(50, dealPageSize), { excludeClosed: false, search: searchOpt })
    }
    return fetchDeals(dealCategory, dealPage, dealPageSize, { search: searchOpt })
  }

  // Fetch tasks when tasks view is active or filter changes
  useEffect(() => {
    if (activeView !== 'tasks') return
    let cancelled = false
    setTasksLoading(true)
    fetchTasks({ page: tasksPage, limit: tablePageSize, status: tasksFilter === 'all' ? undefined : tasksFilter })
      .then((res) => {
        if (cancelled) return
        setTasksData(res.tasks)
        setTasksTotal(res.total)
      })
      .catch((err) => console.error('Tasks fetch failed:', err))
      .finally(() => { if (!cancelled) setTasksLoading(false) })
    return () => { cancelled = true }
  }, [activeView, tasksPage, tasksFilter, tablePageSize])

  // Debounce ticket search
  useEffect(() => {
    if (ticketSearchTimer.current) clearTimeout(ticketSearchTimer.current)
    ticketSearchTimer.current = setTimeout(() => {
      setTicketsSearchDebounced(ticketsSearch)
      setTicketsPage(1)
    }, 350)
    return () => { if (ticketSearchTimer.current) clearTimeout(ticketSearchTimer.current) }
  }, [ticketsSearch])

  // Fetch tickets when tickets view is active or filter/page/search/sort changes
  useEffect(() => {
    if (activeView !== 'tickets') return
    let cancelled = false
    setTicketsLoading(true)
    fetchTickets({
      page: ticketsPage,
      limit: tablePageSize,
      status: ticketsFilter === 'all' ? undefined : ticketsFilter,
      search: ticketsSearchDebounced || undefined,
      sort: ticketsSort.field,
      order: ticketsSort.order,
    })
      .then((res) => {
        if (cancelled) return
        setTicketsData(res.tickets)
        setTicketsTotal(res.total)
        setTicketsPageCount(res.pages)
      })
      .catch((err) => console.error('Tickets fetch failed:', err))
      .finally(() => { if (!cancelled) setTicketsLoading(false) })
    fetchTicketCounts().then(c => { if (!cancelled) setTicketTabCounts(c) }).catch(() => {})
    return () => { cancelled = true }
  }, [activeView, ticketsPage, ticketsFilter, ticketsSearchDebounced, ticketsSort, tablePageSize])

  // Persist ticket page/search to sessionStorage
  useEffect(() => { sessionStorage.setItem(uKey('ticketsPage'), String(ticketsPage)) }, [ticketsPage])
  useEffect(() => { sessionStorage.setItem(uKey('ticketsSearch'), ticketsSearch) }, [ticketsSearch])

  // Debounce Meets search so the activity list keeps the same responsive CRM
  // search behavior as Contacts, Calls, and Tickets.
  useEffect(() => {
    if (meetsSearchTimer.current) clearTimeout(meetsSearchTimer.current)
    meetsSearchTimer.current = setTimeout(() => {
      setMeetsSearchDebounced(meetsSearch)
      setMeetsPage(1)
    }, 350)
    return () => { if (meetsSearchTimer.current) clearTimeout(meetsSearchTimer.current) }
  }, [meetsSearch])

  useEffect(() => {
    if (activeView !== 'meets') return
    let cancelled = false
    setMeetsLoading(true)
    setMeetsError(false)
    const useUpcomingAwareDefault = meetsSort.field === 'start_time' && meetsSort.order === 'DESC'
    fetchMeetsPaginated({
      page: meetsPage,
      limit: tablePageSize,
      search: meetsSearchDebounced || undefined,
      when: meetsWhen === 'all' ? undefined : meetsWhen,
      sort: useUpcomingAwareDefault ? undefined : meetsSort.field,
      order: useUpcomingAwareDefault ? undefined : meetsSort.order,
    })
      .then((res) => {
        if (cancelled) return
        setMeetsData(res.meetings)
        setMeetsTotal(res.total)
        setMeetsPageCount(res.pages)
        if (res.total > 0 && res.meetings.length === 0 && meetsPage > 1) setMeetsPage(1)
      })
      .catch((err) => {
        if (cancelled) return
        console.error('Meets fetch failed:', err)
        setMeetsError(true)
      })
      .finally(() => { if (!cancelled) setMeetsLoading(false) })
    return () => { cancelled = true }
  }, [activeView, meetsPage, meetsSearchDebounced, meetsWhen, meetsSort, tablePageSize, meetsReloadNonce])

  // Debounce calls search
  useEffect(() => {
    if (callSearchTimer.current) clearTimeout(callSearchTimer.current)
    callSearchTimer.current = setTimeout(() => {
      setCallsSearchDebounced(callsSearch)
      setCallsPage(1)
    }, 350)
    return () => { if (callSearchTimer.current) clearTimeout(callSearchTimer.current) }
  }, [callsSearch])

  // Fetch call analytics when calls view loads
  useEffect(() => {
    if (activeView !== 'calls') return
    fetch('/api/crm/calls/analytics').then(r => r.json()).then(setCallAnalytics).catch(() => {})
  }, [activeView])

  // calls-page-isolation (docs/REGRESSIONS.md 2026-07-02): the #/calls view is
  // the SOLE owner of callsData/callsTotal/callsPageCount. `callsViewQueryRef`
  // mirrors the query the view currently wants; the fetch effect below tags each
  // response with the query it fired for and applies it ONLY while that tag still
  // matches this ref — so a stale in-flight response (or any foreign-query write)
  // can never render page-1 rows under page N. The docked-phone pollers no longer
  // touch this state at all (the phone Recents reads deviceCallLog); the poller's
  // calls-view-skip trap holds the matching invariant on that side.
  const callsViewQueryRef = useRef<CallsViewQuery | null>(null)

  // Fetch calls when calls view is active or filter/page/search/sort changes
  // (callsReloadNonce re-triggers it for the CallsList Retry button).
  useEffect(() => {
    if (activeView !== 'calls') return
    // The exact query this run fetches for — the response's ownership tag. Mirror
    // it to the ref so any LATER run (page/filter/sort change) is detectable when
    // this run's response lands.
    const firedQuery: CallsViewQuery = {
      page: callsPage,
      limit: tablePageSize,
      search: callsSearchDebounced || '',
      direction: callsDirectionFilter || '',
      outcome: callsOutcomeFilter || '',
      hasRecording: !!callsHasRecording,
      hasTranscript: !!callsHasTranscript,
      sortField: callsSort.field,
      sortOrder: callsSort.order,
    }
    callsViewQueryRef.current = firedQuery
    let cancelled = false
    setCallsLoading(true)
    setCallsError(false)
    fetchCallsPaginated({
      page: callsPage,
      limit: tablePageSize,
      search: callsSearchDebounced || undefined,
      direction: callsDirectionFilter || undefined,
      outcome: callsOutcomeFilter || undefined,
      has_recording: callsHasRecording || undefined,
      has_transcript: callsHasTranscript || undefined,
      sort: callsSort.field,
      order: callsSort.order,
    })
      .then((res) => {
        if (cancelled) return
        // Ownership gate (calls-page-isolation): apply ONLY a response still
        // matching the view's live query. Drops a stale in-flight response or any
        // foreign-query write instead of letting page N render another query's
        // rows. See src/lib/calls/callsViewOwnership.ts.
        if (!callsViewQueryRef.current || !shouldApplyCallsResponse(callsViewQueryRef.current, firedQuery)) {
          console.debug('[calls] calls-page-isolation: dropped stale/foreign-query response')
          return
        }
        setCallsData(res.calls)
        setCallsTotal(res.total)
        setCallsPageCount(res.pages)
        // calls-pg-loadstate-v1: tag the now-rendered rows with the query they
        // belong to, so a later page/filter/sort change is detectable as "stale".
        setCallsAppliedKey(callsQueryKey(firedQuery))
        // Stale-page self-heal: if the server has rows but THIS page is past the
        // end (page size shrank, or filters cut the count), snap back to page 1
        // so the list never shows empty under a non-zero total.
        if (res.total > 0 && res.calls.length === 0 && callsPage > 1) setCallsPage(1)
      })
      .catch((err) => {
        if (cancelled) return
        console.error('Calls fetch failed:', err)
        // Surface the failure so CallsList renders an error + Retry row instead
        // of the "No calls match these filters" empty copy (failure ≠ no-results).
        setCallsError(true)
      })
      .finally(() => { if (!cancelled) setCallsLoading(false) })
    return () => { cancelled = true }
  }, [activeView, callsPage, callsDirectionFilter, callsOutcomeFilter, callsHasRecording, callsHasTranscript, callsSearchDebounced, callsSort, tablePageSize, callsReloadNonce])

  // Fetch pending (unassigned) call assignments — the office-wide routing queue
  // surfaced on the notifications-bell Calls tab. Refetch when the effective user
  // changes (e.g. impersonation switch) so the Calls count reflects the current
  // session rather than a stale value captured at mount. The queue itself is global
  // (no per-user target column on pending_call_assignments), so the number is the
  // same for everyone — but it must re-pull on identity change, not freeze at mount.
  useEffect(() => {
    fetchPendingAssignments().then((res) => setPendingAssignments(res.pending)).catch(() => {})
  }, [currentUserId])

  // Cleanup ringout poll on unmount
  useEffect(() => {
    return () => { ringoutPollRef.current?.(); ringoutPollRef.current = null }
  }, [])

  // Cleanup calling panel timer/poll on unmount
  useEffect(() => {
    return () => {
      if (callingTimerRef.current) clearInterval(callingTimerRef.current)
      if (callingPollRef.current) clearInterval(callingPollRef.current)
    }
  }, [])

  // Debounce dialer search — phone-like input goes to the contacts/lookup
  // endpoint (matches the global top-search), name input to the clients list.
  // This is the KEYPAD live lookup-as-you-type: every keypress sets dialerSearch
  // (the formatted display string), which fires this effect → populates
  // dialerSearchResults → the Keypad renders the matched contact NAME under the
  // typed number (Keypad.tsx suggestions block). For a phone-like value we
  // search by the RAW DIGITS, not the formatted "(123) 456-7890" — the clients
  // search strips ()-. from preferred_phone before matching, so a formatted term
  // would never hit the stripped column. Non-phone text (a name typed in the
  // search box) still searches verbatim. Require ≥3 digits before firing so we
  // don't spam the search API on the first one or two keys.
  useEffect(() => {
    if (!dialerSearch || dialerSearch.length < 2) { setDialerSearchResults([]); setDialerSuggestExpanded(false); return }
    const digitsOnly = dialerSearch.replace(/\D/g, '')
    const isPhoneLike = digitsOnly.length > 0 && /^[\s+\-().\d]+$/.test(dialerSearch)
    if (isPhoneLike && digitsOnly.length < 3) { setDialerSearchResults([]); setDialerSuggestExpanded(false); return }
    const searchTerm = isPhoneLike ? digitsOnly : dialerSearch
    if (dialerSearchTimer.current) clearTimeout(dialerSearchTimer.current)
    dialerSearchTimer.current = setTimeout(() => {
      if (isPhoneLike) {
        // Phone-like input routes through the contacts/lookup endpoint — the SAME
        // source the global top-search uses — NOT the clients LIST endpoint. The
        // list endpoint filters out data_quality IN (junk,duplicate,deleted), which
        // silently drops rows like BX-00306 (Maxwell Ritchie / (954) 558-9228),
        // flagged data_quality='deleted' even though deleted_at IS NULL. Using the
        // lookup endpoint makes the keypad suggestion surface the same contact the
        // top search finds for a typed number.
        lookupContactsByPhone(digitsOnly).then((data) => {
          const results = (data.matches || [])
            .filter(m => m.phone)
            .map(m => ({ id: `client-${m.id}`, title: m.name, subtitle: m.phone || '', type: 'client', phone: m.phone || '' }))
          setDialerSearchResults(results)
        }).catch(() => {})
      } else {
        fetchClients(1, 50, { search: searchTerm }).then((data) => {
          const results = data.clients
            .filter(c => c.preferred_phone)
            .map(c => ({ id: `client-${c.unified_client_id}`, title: c.preferred_name, subtitle: c.preferred_phone || '', type: 'client', phone: c.preferred_phone || '' }))
          setDialerSearchResults(results)
        }).catch(() => {})
      }
    }, 250)
    return () => { if (dialerSearchTimer.current) clearTimeout(dialerSearchTimer.current) }
  }, [dialerSearch])

  // Fetch RC mappings on mount so the green icon shows immediately (retry once if CF Access blocks first attempt)
  useEffect(() => {
    const load = () => fetchRcUserMappings().then((res) => { if (res?.mappings) setRcMappings(res.mappings); setRcMappingsLoaded(true) }).catch(() => setRcMappingsLoaded(true))
    load().catch(() => { setTimeout(() => load().catch(() => {}), 3000) })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Load RC outcomes + retry mappings when calling panel opens
  useEffect(() => {
    if (callingPanelOpen && rcOutcomes.length === 0) {
      fetchCallOutcomes().then((res) => setRcOutcomes(res.outcomes)).catch(() => {})
    }
    if (callingPanelOpen && rcMappings.length === 0) {
      fetchRcUserMappings().then((res) => { if (res?.mappings) setRcMappings(res.mappings) }).catch(() => {})
    }
  }, [callingPanelOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  // Persist calls page/search to sessionStorage
  useEffect(() => { sessionStorage.setItem(uKey('callsPage'), String(callsPage)) }, [callsPage])
  useEffect(() => { sessionStorage.setItem(uKey('callsSearch'), callsSearch) }, [callsSearch])

  // Fetch products when products view is active
  useEffect(() => {
    if (activeView !== 'products') return
    let cancelled = false
    setProductsLoading(true)
    fetchProducts({ page: productsPage, limit: tablePageSize, search: productsSearch || undefined, active: productsStatusFilter === 'active' ? '1' : productsStatusFilter === 'inactive' ? '0' : undefined })
      .then((res) => {
        if (cancelled) return
        setProductsData(res.products)
        setProductsTotal(res.total)
        setProductsPages(res.pages)
      })
      .catch((err) => console.error('Products fetch failed:', err))
      .finally(() => { if (!cancelled) setProductsLoading(false) })
    return () => { cancelled = true }
  }, [activeView, productsPage, tablePageSize, productsSearch, productsStatusFilter])

  // Fetch quotes when quotes view is active
  useEffect(() => {
    if (activeView !== 'quotes') return
    let cancelled = false
    setQuotesLoading(true)
    fetchQuotes({ page: quotesPage, limit: tablePageSize, status: quotesStatusFilter || undefined })
      .then((res) => {
        if (cancelled) return
        setQuotesData(res.quotes)
        setQuotesTotal(res.total)
      })
      .catch((err) => console.error('Quotes fetch failed:', err))
      .finally(() => { if (!cancelled) setQuotesLoading(false) })
    return () => { cancelled = true }
  }, [activeView, quotesPage, tablePageSize, quotesStatusFilter])

  // Documents view helpers
  const toggleDocsSort = (field: string) => {
    if (docsSort === field) {
      setDocsOrder(docsOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setDocsSort(field)
      setDocsOrder(field === 'client_name' || field === 'doc_type' || field === 'address' ? 'asc' : 'desc')
    }
    setDocsPage(1)
  }
  const toggleDocRow = (id: number) => {
    setDocsSelectedRows(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }
  const toggleAllDocRows = () => {
    if (docsData.length > 0 && docsSelectedRows.size === docsData.length) {
      setDocsSelectedRows(new Set())
    } else {
      setDocsSelectedRows(new Set(docsData.map(d => d.id)))
    }
  }
  const onDocResizeStart = (col: string, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const startX = e.clientX
    const startW = (docsColWidths as any)[col] || 120
    const onMove = (ev: MouseEvent) => {
      const diff = ev.clientX - startX
      setDocsColWidths(prev => ({ ...prev, [col]: Math.max(60, startW + diff) }))
    }
    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp) }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }
  const closeDocsPreview = () => {
    setDocsPreviewClosing(true)
    setTimeout(() => { setDocsPreview(null); setDocsPreviewClosing(false) }, 200)
  }
  const docsPageCount = Math.max(1, Math.ceil(docsTotal / tablePageSize))

  // Fetch forecasts
  useEffect(() => {
    if (activeView !== 'forecasts') return
    let cancelled = false
    setForecastLoading(true)
    const [year, month] = forecastMonth.split('-').map(Number)
    const start = `${forecastMonth}-01`
    const lastDay = new Date(year, month, 0).getDate()
    const end = `${forecastMonth}-${String(lastDay).padStart(2, '0')}`
    fetchForecasts({ pipeline: forecastPipeline, period_start: start, period_end: end })
      .then((res) => { if (!cancelled) setForecastData(res) })
      .catch((err) => console.error('Forecasts fetch failed:', err))
      .finally(() => { if (!cancelled) setForecastLoading(false) })
    return () => { cancelled = true }
  }, [activeView, forecastPipeline, forecastMonth])

  // Fetch payment links
  useEffect(() => {
    if (activeView !== 'payments') return
    let cancelled = false
    setPaymentLinksLoading(true)
    fetchPaymentLinks({}).then((res) => { if (!cancelled) { setPaymentLinksData(res.data); setPaymentLinksTotal(res.total) } }).catch(console.error).finally(() => { if (!cancelled) setPaymentLinksLoading(false) })
    setPaymentsLoading(true)
    fetchPayments({}).then((res) => { if (!cancelled) { setPaymentsData(res.data); setPaymentsTotal(res.total) } }).catch(console.error).finally(() => { if (!cancelled) setPaymentsLoading(false) })
    return () => { cancelled = true }
  }, [activeView])

  // Fetch KB articles
  useEffect(() => {
    if (activeView !== 'knowledge-base') return
    let cancelled = false
    setKbArticlesLoading(true)
    fetchKbArticles({ search: kbSearch || undefined, status: kbStatusFilter || undefined, page: kbPage, pageSize: tablePageSize }).then((res) => { if (!cancelled) { setKbArticles(res.data); setKbArticlesTotal(res.total) } }).catch(console.error).finally(() => { if (!cancelled) setKbArticlesLoading(false) })
    fetchKbCategories().then((res) => { if (!cancelled) setKbCategories(res.categories) }).catch(console.error)
    return () => { cancelled = true }
  }, [activeView, kbSearch, kbStatusFilter, kbPage, tablePageSize])

  // Fetch portal data
  useEffect(() => {
    if (activeView !== 'customer-portal') return
    let cancelled = false
    fetchPortalSettings().then((res) => { if (!cancelled) setPortalSettingsData(res.settings) }).catch(console.error)
    setPortalTicketsLoading(true)
    fetchPortalTickets({ search: portalTicketSearch || undefined, status: portalTicketStatusFilter || undefined, page: portalTicketPage, pageSize: tablePageSize }).then((res) => { if (!cancelled) { setPortalTicketsData(res.data); setPortalTicketsTotal(res.total) } }).catch(console.error).finally(() => { if (!cancelled) setPortalTicketsLoading(false) })
    return () => { cancelled = true }
  }, [activeView, portalTicketSearch, portalTicketStatusFilter, portalTicketPage, tablePageSize])

  // Fetch surveys
  useEffect(() => {
    if (activeView !== 'feedback-surveys') return
    let cancelled = false
    setSurveysLoading(true)
    fetchSurveys({ search: surveySearch || undefined, survey_type: surveyTypeFilter || undefined, page: surveyPage, pageSize: tablePageSize }).then((res) => { if (!cancelled) { setSurveysData(res.data); setSurveysTotal(res.total) } }).catch(console.error).finally(() => { if (!cancelled) setSurveysLoading(false) })
    return () => { cancelled = true }
  }, [activeView, surveySearch, surveyTypeFilter, surveyPage, tablePageSize])

  // Fetch snippets when snippets view is active
  const loadSnippets = useCallback(() => {
    setSnippetsLoading(true)
    fetchSnippets({ limit: 100, search: snippetsSearch || undefined })
      .then((res) => { setSnippetsData(res.snippets); setSnippetsTotal(res.total) })
      .catch((err) => console.error('Snippets fetch failed:', err))
      .finally(() => setSnippetsLoading(false))
  }, [snippetsSearch])

  useEffect(() => {
    if (activeView !== 'snippets') return
    loadSnippets()
  }, [activeView, loadSnippets])

  // Fetch templates when templates view is active
  const loadTemplates = useCallback(() => {
    setTemplatesLoading(true)
    fetchTemplates({ limit: 100, search: templatesSearch || undefined, category: templatesCategoryFilter || undefined })
      .then((res) => { setTemplatesData(res.templates); setTemplatesTotal(res.total) })
      .catch((err) => console.error('Templates fetch failed:', err))
      .finally(() => setTemplatesLoading(false))
  }, [templatesSearch, templatesCategoryFilter])

  useEffect(() => {
    if (activeView !== 'templates') return
    loadTemplates()
  }, [activeView, loadTemplates])

  // Fetch playbooks when playbooks view is active
  const loadPlaybooks = useCallback(() => {
    setPlaybooksLoading(true)
    fetchPlaybooks({ limit: 100, search: playbooksSearch || undefined })
      .then((res) => { setPlaybooksData(res.playbooks); setPlaybooksTotal(res.total) })
      .catch((err) => console.error('Playbooks fetch failed:', err))
      .finally(() => setPlaybooksLoading(false))
  }, [playbooksSearch])

  useEffect(() => {
    if (activeView !== 'playbooks') return
    loadPlaybooks()
  }, [activeView, loadPlaybooks])

  // Fetch orders when orders view is active
  const loadOrders = useCallback(() => {
    setOrdersLoading(true)
    fetchOrders({ limit: 100, search: ordersSearch || undefined, status: ordersStatusFilter || undefined })
      .then((res) => { setOrdersData(res.orders); setOrdersTotal(res.total) })
      .catch((err) => console.error('Orders fetch failed:', err))
      .finally(() => setOrdersLoading(false))
  }, [ordersSearch, ordersStatusFilter])

  useEffect(() => {
    if (activeView !== 'orders') return
    loadOrders()
  }, [activeView, loadOrders])

  // Fetch CRM projects when projects view is active
  const loadProjects = useCallback(() => {
    if (projectsStageFilterSet && projectsStageFilterSet.size === 0) {
      setProjectsData([]); setProjectsTotal(0); setProjectsLoading(false)
      return
    }
    setProjectsLoading(true)
    const onHoldView = projectsStatusFilter === 'on_hold'
    fetchCrmProjects({
      page: projectsPage,
      pageSize: tablePageSize,
      search: projectsSearch || undefined,
      // statuses (multi-select) takes precedence over the single legacy status
      status: projectsStatusSet.size > 0 || onHoldView ? undefined : (projectsStatusFilter || undefined),
      sortField: projectsSort.field,
      sortOrder: projectsSort.order,
      assignedTo: projectsAssignedFilter || undefined,
      pipelineStages: onHoldView ? ['P_ON_HOLD'] : (projectsStageFilterSet ? [...projectsStageFilterSet] : undefined),
      holdTypes: projectsHoldTypesFilter.size > 0 ? [...projectsHoldTypesFilter] : undefined,
      includeCompleted: projectsIncludeCompleted,
      includeCancelled: projectsIncludeCancelled,
      ownerIds: projectsOwnerSet.size > 0 ? [...projectsOwnerSet] : undefined,
      statuses: projectsStatusSet.size > 0 ? [...projectsStatusSet] : undefined,
      valueRange: projectsValueFilter || undefined,
      startDateRange: projectsStartDateFilter || undefined,
      endDateRange: projectsEndDateFilter || undefined,
      city: projectsCityFilter || undefined,
      zip: projectsZipFilter || undefined,
    })
      .then((res) => { setProjectsData(res.data); setProjectsTotal(res.total) })
      .catch((err) => console.error('CRM projects fetch failed:', err))
      .finally(() => setProjectsLoading(false))
  }, [projectsSearch, projectsStatusFilter, projectsAssignedFilter, projectsPage, projectsSort, tablePageSize, projectsStageFilterSet, projectsHoldTypesFilter, projectsIncludeCompleted, projectsIncludeCancelled, projectsOwnerSet, projectsStatusSet, projectsValueFilter, projectsStartDateFilter, projectsEndDateFilter, projectsCityFilter, projectsZipFilter])

  useEffect(() => {
    if (activeView !== 'projects') return
    loadProjects()
  }, [activeView, loadProjects])

  const projectsPageCount = Math.max(1, Math.ceil(projectsTotal / tablePageSize))

  const toggleProjectSort = (field: string) => {
    setProjectsSort(prev => ({
      field,
      order: prev.field === field && prev.order === 'ASC' ? 'DESC' : 'ASC',
    }))
    setProjectsPage(1)
  }
  const setProjectViewFilter = (key: string) => {
    setProjectsStatusFilter(key)
    setProjectsStatusSet(new Set())
    setProjectsStageFilterSet(key === 'on_hold' ? new Set(['P_ON_HOLD']) : null)
    setProjectsIncludeCompleted(key === 'completed')
    setProjectsIncludeCancelled(key === 'cancelled')
    setProjectsPage(1)
  }

  const toggleAllProjectRows = () => {
    if (projectSelectedRows.size === projectsData.length) {
      setProjectSelectedRows(new Set())
    } else {
      setProjectSelectedRows(new Set(projectsData.map(p => p.id)))
    }
  }

  const toggleProjectRow = (id: string | number) => {
    setProjectSelectedRows(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  // Fetch activities/tasks when project detail page opens
  useEffect(() => {
    if (!projectDetailOpen || !projectDetailData) {
      setProjectDetailActivities([])
      setProjectDetailTasks([])
      setPrjLinkedDeal(null)
      setPrjLinkedContact(null)
      return
    }
    setProjectDetailLoading(true)
    Promise.all([
      fetchTasks({ ownerType: 'project', ownerId: String(projectDetailData.id) }).catch(() => ({ tasks: [] })),
      getProjectActivities(projectDetailData.id).catch(() => []),
    ]).then(([tasksRes, activitiesRes]) => {
      setProjectDetailTasks(Array.isArray(tasksRes) ? tasksRes : tasksRes?.tasks || [])
      setProjectDetailActivities(Array.isArray(activitiesRes) ? activitiesRes : [])
    }).finally(() => setProjectDetailLoading(false))
    // Fetch linked deal and contact
    if (projectDetailData.deal_id) {
      fetchDeal(projectDetailData.deal_id).then(d => setPrjLinkedDeal(d)).catch(() => setPrjLinkedDeal(null))
    } else { setPrjLinkedDeal(null) }
    if (projectDetailData.client_id) {
      fetchClient(projectDetailData.client_id).then(c => setPrjLinkedContact(c)).catch(() => setPrjLinkedContact(null))
    } else { setPrjLinkedContact(null) }
  }, [projectDetailOpen, projectDetailData?.id, projectDetailRefreshTick])

  // Fetch reports data when reports view is active
  useEffect(() => {
    if (activeView !== 'reports') return
    let cancelled = false
    setReportsLoading(true)
    Promise.all([
      fetchPipelineReport().catch(() => []),
      fetchRevenueReport().catch(() => []),
      fetchActivitiesReport().catch(() => []),
      fetchLeadSourcesReport().catch(() => []),
      fetchLeaderboard().catch(() => []),
      fetchLeadsByEstimator(reportDateRange).catch(() => []),
    ]).then(([pipeline, revenue, activities, sources, leaderboard, leadsByEstimator]) => {
      if (cancelled) return
      setReportPipeline(pipeline)
      setReportRevenue(revenue)
      setReportActivities(activities)
      setReportLeadSources(sources)
      setReportLeaderboard(leaderboard)
      setReportLeadsByEstimator(leadsByEstimator)
    }).catch((err) => console.error('Reports fetch failed:', err))
      .finally(() => { if (!cancelled) setReportsLoading(false) })
    return () => { cancelled = true }
  }, [activeView, reportDateRange])

  const reportWindow = useMemo(() => {
    const now = new Date()
    const pad = (n: number) => String(n).padStart(2, '0')
    const fmt = (dt: Date) => `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`
    const y = now.getFullYear()
    const m = now.getMonth()
    if (reportDateRange === 'last_month') return { from: fmt(new Date(y, m - 1, 1)), to: fmt(new Date(y, m, 1)), label: 'Last month' }
    if (reportDateRange === 'this_quarter') {
      const qStart = Math.floor(m / 3) * 3
      return { from: fmt(new Date(y, qStart, 1)), to: fmt(new Date(y, qStart + 3, 1)), label: 'This quarter' }
    }
    if (reportDateRange === 'this_year') return { from: fmt(new Date(y, 0, 1)), to: fmt(new Date(y + 1, 0, 1)), label: 'This year' }
    return { from: fmt(new Date(y, m, 1)), to: fmt(new Date(y, m + 1, 1)), label: 'This month' }
  }, [reportDateRange])

  // Fetch dashboard data
  useEffect(() => {
    if (activeView !== 'dashboards') return
    let cancelled = false
    setDashboardLoading(true)
    setDashboardError(null)
    fetchDashboardAggregate({
      range: reportDateRange,
      from: reportWindow.from,
      to: reportWindow.to,
      ownerId: currentUserId || undefined,
      revenuePeriod: reportDateRange === 'this_year' ? 'monthly' : 'weekly',
      refresh: dashboardRefreshTick > 0,
    }).then((aggregate) => {
      if (cancelled) return
      setSummaryKPIs(aggregate?.kpis ?? null)
      setDashboardData({
        funnel: aggregate?.funnel ?? { stages: [], total: 0 },
        wonLost: aggregate?.wonLost ?? [],
        actByType: aggregate?.actByType ?? [],
        leadFunnel: aggregate?.leadFunnel ?? { by_status: [], total_leads: 0, converted_leads: 0, conversion_rate: 0 },
        forecast: aggregate?.forecast ?? { pipeline_stages: [], total_pipeline_value: 0, total_open_deals: 0, total_won_revenue: 0, total_won_deals: 0 },
        leaderboard: aggregate?.leaderboard ?? [],
        revenue: aggregate?.revenue ?? [],
      })
      setSavedDashboards(Array.isArray(aggregate?.dashboards) ? aggregate.dashboards : [])
    }).catch((err) => {
      console.error('Dashboard fetch failed:', err)
      if (!cancelled) setDashboardError(err instanceof Error ? err.message : 'Dashboard data failed to load')
    })
      .finally(() => { if (!cancelled) setDashboardLoading(false) })
    return () => { cancelled = true }
  }, [activeView, reportDateRange, reportWindow, currentUserId, dashboardRefreshTick])

  // Fetch analytics data
  useEffect(() => {
    if (activeView !== 'analytics') return
    let cancelled = false
    setAnalyticsLoading(true)
    const yr = new Date().getFullYear()
    Promise.all([
      fetchDealVelocity({ from: `${yr}-01-01` }).catch(() => ({ by_rep: [], overall_avg_days: 0, total_closed: 0 })),
      fetchAvgDealSize().catch(() => ({ by_rep: [], overall_avg: 0, total_deals: 0 })),
      fetchTeamActivity({ from: `${yr}-01-01` }).catch(() => []),
      fetchDealsCreated({ group_by: 'rep' }).catch(() => []),
      fetchActivitiesReport({ period: 'monthly' }).catch(() => []),
      fetchPipelineReport().catch(() => []),
    ]).then(([velocity, avgSize, teamAct, dealsCreated, actTimeline, pipeline]) => {
      if (cancelled) return
      setAnalyticsData({ velocity, avgSize, teamAct, dealsCreated, actTimeline, pipeline })
    }).catch((err) => console.error('Analytics fetch failed:', err))
      .finally(() => { if (!cancelled) setAnalyticsLoading(false) })
    return () => { cancelled = true }
  }, [activeView])

  // Fetch workflows when workflows view is active
  useEffect(() => {
    if (activeView !== 'workflows' || editingWorkflowId !== null) return
    let cancelled = false
    setWorkflowsLoading(true)
    fetchWorkflows({ limit: 50, isActive: workflowTab === 'active' ? 1 : workflowTab === 'inactive' ? 0 : undefined })
      .then((res) => { if (!cancelled) setWorkflowsData(res.workflows) })
      .catch((err) => console.error('Workflows fetch failed:', err))
      .finally(() => { if (!cancelled) setWorkflowsLoading(false) })
    return () => { cancelled = true }
  }, [activeView, workflowTab, editingWorkflowId])

  useEffect(() => {
    if (editingWorkflowId === null || editingWorkflowId === 0) return
    fetchWorkflow(editingWorkflowId).then((wf) => { setWorkflowEditorWorkflow(wf); setWorkflowEditorActions(wf.actions || []) }).catch(() => {})
  }, [editingWorkflowId])

  useEffect(() => {
    if (activeView !== 'sequences' || editingSequenceId !== null) return
    let cancelled = false; setSequencesLoading(true)
    fetchSequences({ limit: 50, is_active: sequenceTab === 'active' ? 1 : sequenceTab === 'drafts' ? 0 : undefined })
      .then((res) => { if (!cancelled) setSequencesData(res.sequences) })
      .catch((err) => console.error('Sequences fetch failed:', err))
      .finally(() => { if (!cancelled) setSequencesLoading(false) })
    return () => { cancelled = true }
  }, [activeView, sequenceTab, editingSequenceId])

  useEffect(() => {
    if (editingSequenceId === null || editingSequenceId === 0) return
    fetchSequence(editingSequenceId).then((s) => setEditingSequence(s)).catch(() => {})
    fetchSequenceEnrollments(editingSequenceId, { limit: 50 }).then((r) => setSeqEnrollments(r.enrollments)).catch(() => {})
    fetchSequenceStats(editingSequenceId).then((s) => setSeqStats(s)).catch(() => {})
  }, [editingSequenceId])

  useEffect(() => {
    if (activeView !== 'chatflows' || editingChatflowId !== null) return
    let cancelled = false; setChatflowsLoading(true)
    fetchChatflows({ limit: 200 })
      .then((res) => { if (!cancelled) setChatflowsData(res.chatflows) })
      .catch((err) => console.error('Chatflows fetch failed:', err))
      .finally(() => { if (!cancelled) setChatflowsLoading(false) })
    return () => { cancelled = true }
  }, [activeView, editingChatflowId])

  useEffect(() => {
    if (editingChatflowId === null || editingChatflowId === 0) return
    fetchChatflow(editingChatflowId).then((cf) => setEditingChatflow(cf)).catch(() => {})
  }, [editingChatflowId])

  // Fetch conversations when conversations view is active
  useEffect(() => {
    if (activeView !== 'conversations') return
    let cancelled = false
    setConversationsLoading(true)
    fetchConversations({ limit: 50 })
      .then((res) => {
        if (cancelled) return
        setConversationsData(res.threads)
      })
      .catch((err) => console.error('Conversations fetch failed:', err))
      .finally(() => { if (!cancelled) setConversationsLoading(false) })
    return () => { cancelled = true }
  }, [activeView])

  // Fetch marketing emails
  useEffect(() => {
    if (activeView !== 'marketing-email') return
    let cancelled = false
    setMktgEmailsLoading(true)
    const opts: { status?: string; search?: string } = {}
    if (mktgEmailFilter !== 'all') opts.status = mktgEmailFilter
    if (mktgEmailSearch) opts.search = mktgEmailSearch
    fetchMarketingEmails(opts)
      .then((res) => { if (!cancelled) { setMktgEmails(res.emails); setMktgEmailsTotal(res.total) } })
      .catch((err) => console.error('Marketing emails fetch failed:', err))
      .finally(() => { if (!cancelled) setMktgEmailsLoading(false) })
    if (mktgEmailTab === 'analyze') {
      fetchEmailAnalytics().then((res) => { if (!cancelled) setMktgAnalytics(res) }).catch(() => {})
    }
    fetchContactLists().then((res) => { if (!cancelled) setContactLists(res.lists) }).catch(() => {})
    return () => { cancelled = true }
  }, [activeView, mktgEmailFilter, mktgEmailSearch, mktgEmailTab])

  // Fetch ads
  useEffect(() => {
    if (activeView !== 'ads') return
    let cancelled = false
    setAdsLoading(true)
    const opts: { network?: string; status?: string; page?: number; pageSize?: number } = { page: adsPage, pageSize: tablePageSize }
    if (adsPlatformFilter) opts.network = adsPlatformFilter
    if (adsStatusFilter) opts.status = adsStatusFilter
    Promise.all([fetchAdAccounts(), fetchAdCampaigns(opts)])
      .then(([accts, camps]) => {
        if (cancelled) return
        setAdAccounts(accts.accounts)
        setAdCampaignsData(camps.campaigns)
        setAdCampaignsTotal(camps.total)
      })
      .catch((err) => console.error('Ads fetch failed:', err))
      .finally(() => { if (!cancelled) setAdsLoading(false) })
    return () => { cancelled = true }
  }, [activeView, adsPage, tablePageSize, adsPlatformFilter, adsStatusFilter, adsTab])

  // Fetch social posts
  useEffect(() => {
    if (activeView !== 'social') return
    let cancelled = false
    setSocialLoading(true)
    const opts: { status?: string } = {}
    if (socialFilter !== 'all') opts.status = socialFilter
    fetchSocialPosts(opts)
      .then((res) => { if (!cancelled) { setSocialPosts(res.posts); setSocialPostsTotal(res.total) } })
      .catch((err) => console.error('Social posts fetch failed:', err))
      .finally(() => { if (!cancelled) setSocialLoading(false) })
    return () => { cancelled = true }
  }, [activeView, socialFilter])

  // Fetch forms
  useEffect(() => {
    if (activeView !== 'forms') return
    let cancelled = false
    setFormsLoading(true)
    const opts: { status?: string; search?: string } = {}
    if (formsFilter !== 'all') opts.status = formsFilter
    if (formsSearch) opts.search = formsSearch
    fetchForms(opts)
      .then((res) => { if (!cancelled) { setFormsData(res.forms); setFormsTotal(res.total) } })
      .catch((err) => console.error('Forms fetch failed:', err))
      .finally(() => { if (!cancelled) setFormsLoading(false) })
    return () => { cancelled = true }
  }, [activeView, formsFilter, formsSearch])

  // Fetch landing pages
  useEffect(() => {
    if (activeView !== 'landing-pages') return
    let cancelled = false
    setLandingPagesLoading(true)
    const opts: { status?: string; search?: string } = {}
    if (lpFilter !== 'all') opts.status = lpFilter
    if (lpSearch) opts.search = lpSearch
    fetchLandingPages(opts)
      .then((res) => { if (!cancelled) { setLandingPagesData(res.pages); setLandingPagesTotal(res.total) } })
      .catch((err) => console.error('Landing pages fetch failed:', err))
      .finally(() => { if (!cancelled) setLandingPagesLoading(false) })
    return () => { cancelled = true }
  }, [activeView, lpFilter, lpSearch])

  // Fetch settings data when preferences view is active
  useEffect(() => {
    if (activeView !== 'preferences') return
    let cancelled = false
    setSettingsLoading(true)
    Promise.all([
      fetchProperties('client').catch(() => ({ properties: [] })),
      fetchTeams().catch(() => ({ teams: [] })),
      fetchRoles().catch(() => ({ roles: [] })),
      fetchAccountSettings().catch(() => ({ settings: {} })),
      fetchRcUserMappings().catch(() => ({ mappings: [] })),
      fetchCallOutcomes().catch(() => ({ outcomes: [] })),
      fetchBlockedNumbers().catch(() => ({ blockedNumbers: [] })),
      fetchRcConfig().catch(() => ({ config: {} })),
    ]).then(([propsRes, teamsRes, rolesRes, acctRes, mappingsRes, outcomesRes, blockedRes, rcConfigRes]) => {
      if (cancelled) return
      setSettingsProperties(propsRes.properties)
      setSettingsTeams(teamsRes.teams)
      setSettingsRoles(rolesRes.roles)
      setSettingsAccountData(acctRes.settings)
      setRcMappings((mappingsRes as { mappings: RcUserMapping[] }).mappings)
      setRcOutcomes((outcomesRes as { outcomes: CallOutcome[] }).outcomes)
      setRcBlockedNumbers((blockedRes as { blockedNumbers: BlockedNumber[] }).blockedNumbers)
      setRcConfig((rcConfigRes as { config: Record<string, string> }).config)
    }).catch((err) => console.error('Settings fetch failed:', err))
      .finally(() => { if (!cancelled) setSettingsLoading(false) })
    return () => { cancelled = true }
  }, [activeView])

  // Fetch notification count on mount + poll for incoming call notifications.
  // Previously hardcoded to user 8 (Max) — so everyone's UI showed Max's
  // notifications. Now keyed on the authenticated user once /api/me resolves.
  useEffect(() => {
    if (!currentUserId) return
    fetchUnreadCount(currentUserId).then((res) => setNotifCount(res.unreadCount)).catch(() => {})
    const stop = startManagedInterval({
      name: 'incoming-call-notifications',
      intervalMs: 5_000,
      run: () => fetch(`/api/crm/notifications?user_id=${currentUserId}&limit=1&unread=1`).then(r => r.json()).then((data: any) => {
        const notifs = data?.notifications || []
        // On first poll, just record the latest ID so we don't replay old notifications
        if (!notifInitializedRef.current) {
          notifInitializedRef.current = true
          if (notifs.length > 0) lastNotifIdRef.current = notifs[0].id
          return
        }
        // Only react to INCOMING CALL notifications created in the last 30 seconds
        if (notifs.length > 0 && notifs[0].id > lastNotifIdRef.current && notifs[0].title?.startsWith('Incoming call:') && notifs[0].created_at && (Date.now() - new Date(notifs[0].created_at).getTime()) < 30000) {
          lastNotifIdRef.current = notifs[0].id
          const callerName = notifs[0].title.replace('Incoming call: ', '')
          // Skip poller-driven toast if SIP already triggered one, UNLESS the poller has a real
          // CRM name (not just a phone number) — in that case enrich the existing banner.
          const isJustPhoneNumber = /^[\s+\-().\d]+$/.test(callerName) || callerName === 'Unknown'
          const hasRealName = callerName && !isJustPhoneNumber && callerName.length > 2
          if (!incomingCallToast) {
            // SIP didn't fire — the call is ringing on the user's other
            // devices. Present through the SAME iOS banner surface (kind
            // 'remote'); the white iphone-notif banner is SMS-only now.
            setIncomingCallToast({ title: notifs[0].title, body: notifs[0].body || '', linkUrl: notifs[0].link_url || '#/calls' })
            const bodyNumber = (notifs[0].body || '').match(/\+?\d[\d\-() .]{6,}\d/)?.[0] || ''
            setPollerIncoming({ name: hasRealName ? callerName : '', number: bodyNumber })
            if (!callingPanelOpen) setCallingPanelOpen(true)
            // Phone-ring tone for inbound calls (poller fallback path).
            try { playPhoneRing() } catch { /* ignore */ }
            setTimeout(() => { setIncomingCallToast(null); setPollerIncoming(null) }, 10000)
          } else if (hasRealName) {
            // SIP already presented the ring; upgrade whichever surface is
            // showing with the CRM-matched name — but never downgrade a
            // name that's already good.
            setIncomingCallToast(prev => prev ? { ...prev, title: notifs[0].title, body: notifs[0].body || prev.body, linkUrl: notifs[0].link_url || prev.linkUrl } : prev)
            setPollerIncoming(prev => prev ? { ...prev, name: callerName } : prev)
            setIncomingCallInfo(prev => prev && (!prev.name || /^[\s+\-().\d]+$/.test(prev.name)) ? { ...prev, name: callerName } : prev)
          }
          setNotifCount(prev => prev + 1)
        }
      }).catch(() => {}),
    })
    // Keep the unread badge live. Previously it only refreshed on mount and on
    // dropdown-open, so notifications arriving while the tab sits open (e.g. a
    // new-email bell) didn't bump the badge until reload. Light 15s refresh.
    const stopCount = startManagedInterval({
      name: 'notif-unread-count',
      intervalMs: 15_000,
      run: () => fetchUnreadCount(currentUserId).then((res) => setNotifCount(res.unreadCount)).catch(() => {}),
    })
    // Keep the bell's CALLS count live for the UNIFIED badge. The pending/unassigned
    // queue already loads at mount (separate effect keyed on currentUserId), but the
    // badge needs it to stay fresh without opening the dropdown — so refresh it on the
    // same 15s cadence. fetchPendingAssignments has no abort handle; the .catch + the
    // managed interval's non-overlapping guarantee keep this from looping tightly.
    // TRAP(shell/poller-identity): every App-root poller that stores an ARRAY/OBJECT
    // MUST identity-guard its setState (sameIdSeq / sameTimeCounts from
    // src/lib/polling/pollerIdentity) so an unchanged poll returns `prev` and React
    // bails out. Setting a fresh reference every tick re-rendered the entire ~88K-line
    // App shell forever on every visible tab — the app-side contributor to idle-tab
    // renderer deaths (docs/REGRESSIONS.md 2026-07-01). Number-valued setters (e.g.
    // setNotifCount) are safe as-is — React bails out on identical primitives.
    // Mirrors TRAP(phone/sameidseq-flash) in the phone-panel pollers.
    const stopPending = startManagedInterval({
      name: 'notif-calls-count',
      intervalMs: 15_000,
      run: () => fetchPendingAssignments().then((res) => setPendingAssignments((prev) => sameIdSeq(prev, res.pending) ? prev : res.pending)).catch(() => {}),
    })
    // Keep the bell's EMAIL count live for the UNIFIED badge. The dropdown-open effect
    // fetches the full thread list lazily (Graph) only while open; here we fetch ONLY
    // the cheap folder summary (GET /api/crm/email/folders) at mount + every 15s to
    // populate notifEmailUnreadCount eagerly, so the badge reflects unread inbox email
    // before the bell is ever opened. AbortController cancels in-flight on identity
    // change / unmount; the .catch guards against tight looping on transient failure.
    const emailCountAc = new AbortController()
    const refreshEmailCount = () => {
      if (document.visibilityState !== 'visible') return
      return fetch('/api/crm/email/folders', { credentials: 'include', signal: emailCountAc.signal })
        .then(r => r.ok ? r.json() : null)
        .then((data: any) => {
          if (emailCountAc.signal.aborted) return
          const inbox = data?.folders?.personal?.find((f: any) => f.key === 'inbox')
          if (typeof inbox?.unreadCount === 'number') setNotifEmailUnreadCount(inbox.unreadCount)
        })
        .catch(() => {})
    }
    refreshEmailCount()
    const stopEmailCount = startManagedInterval({
      name: 'notif-email-count',
      intervalMs: 15_000,
      run: () => refreshEmailCount(),
    })
    return () => { stop(); stopCount(); stopPending(); stopEmailCount(); emailCountAc.abort() }
  }, [currentUserId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Close notification dropdown on outside click
  useEffect(() => {
    if (!notifOpen) return
    const handler = (e: MouseEvent) => {
      const t = e.target as Node
      if (notifRef.current?.contains(t) || notifPopRef.current?.contains(t)) return
      setNotifOpen(false)
    }
    const reposition = () => {
      const r = notifRef.current?.getBoundingClientRect()
      if (r) setNotifPos({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) })
    }
    document.addEventListener('mousedown', handler)
    window.addEventListener('resize', reposition)
    return () => { document.removeEventListener('mousedown', handler); window.removeEventListener('resize', reposition) }
  }, [notifOpen])

  // Timesheets — pending-count for the sidebar chip + view header. Refreshes
  // every 60s so a long-running tab still shows fresh counts without forcing
  // the rep to navigate away & back. Cheap call, single COUNT(*) per metric.
  useEffect(() => {
    if (!currentUserId) return
    let cancelled = false
    const refresh = () => {
      if (document.visibilityState !== 'visible') return
      // TRAP(shell/poller-identity): identity-guarded — an unchanged poll returns `prev`
      // so the App shell doesn't re-render every 60s forever (see the notif pollers).
      return fetchTimeCountsByOwner().then(c => { if (!cancelled) setTimesheetCounts(prev => sameTimeCounts(prev, c) ? prev : c) }).catch(() => {})
    }
    refresh()
    const stop = startManagedInterval({ name: 'timesheet-counts', intervalMs: 60_000, run: refresh })
    return () => { cancelled = true; stop() }
  }, [currentUserId])

  // Refresh counts whenever the Timesheets view becomes active so an approve
  // / unapprove inside the panel decrements the chip without waiting for the
  // 60s tick.
  useEffect(() => {
    if (activeView !== 'timesheets' || !currentUserId) return
    fetchTimeCountsByOwner().then(c => setTimesheetCounts(prev => sameTimeCounts(prev, c) ? prev : c)).catch(() => {})
  }, [activeView, currentUserId])

  // Debounce client search
  useEffect(() => {
    if (clientSearchTimer.current) clearTimeout(clientSearchTimer.current)
    clientSearchTimer.current = setTimeout(() => {
      setClientSearchDebounced(clientSearch)
      setClientPage(1)
    }, 350)
    return () => { if (clientSearchTimer.current) clearTimeout(clientSearchTimer.current) }
  }, [clientSearch])

  // Debounce deal search — clear instantly so results reload without delay
  useEffect(() => {
    if (dealSearchTimer.current) clearTimeout(dealSearchTimer.current)
    if (!dealSearch) {
      setDealSearchDebounced('')
      setDealPage(1)
    } else {
      dealSearchTimer.current = setTimeout(() => {
        setDealSearchDebounced(dealSearch)
        setDealPage(1)
      }, 350)
    }
    return () => { if (dealSearchTimer.current) clearTimeout(dealSearchTimer.current) }
  }, [dealSearch])

  // Fetch D1 clients (persons) when page, search, sort, or filters change
  useEffect(() => {
    if (activeView !== 'contacts') return
    let cancelled = false
    setClientLoading(true)
    setClientTotal(0)
    const qfOpts = buildQuickFilterOpts(qfPillValues)
    fetchClients(clientPage, tablePageSize, {
      search: clientSearchDebounced || undefined,
      status: (clientStatusFilter && clientStatusFilter !== 'archived' && !clientStatusFilter.startsWith('custom:')) ? clientStatusFilter : undefined,
      type: 'person',
      sort: clientSort.field,
      order: clientSort.order,
      archived: clientStatusFilter === 'archived' ? true : undefined,
      ...qfOpts,
    }).then((res) => {
      if (cancelled) return
      setClientList(res.clients)
      setClientTotal(res.total)
      setClientPages(res.pages)
      setClientLoading(false)
    }).catch((err) => {
      console.error('Client fetch failed:', err)
      if (!cancelled) setClientLoading(false)
    })
    return () => { cancelled = true }
  }, [activeView, clientPage, clientSearchDebounced, clientSort, clientStatusFilter, tablePageSize, qfPillValues])

  // Debounce company search
  useEffect(() => {
    if (companySearchTimer.current) clearTimeout(companySearchTimer.current)
    companySearchTimer.current = setTimeout(() => {
      setCompanySearchDebounced(companySearch)
      setCompanyPage(1)
    }, 350)
    return () => { if (companySearchTimer.current) clearTimeout(companySearchTimer.current) }
  }, [companySearch])

  // Fetch D1 clients (organizations) when page, search, sort, or filters change
  useEffect(() => {
    if (activeView !== 'companies') return
    let cancelled = false
    setCompanyLoading(true)
    setCompanyTotal(0)
    const qfOpts = buildQuickFilterOpts(qfPillValues)
    fetchClients(companyPage, tablePageSize, {
      search: companySearchDebounced || undefined,
      status: (companyStatusFilter && companyStatusFilter !== 'archived' && !companyStatusFilter.startsWith('custom:')) ? companyStatusFilter : undefined,
      type: 'organization',
      sort: companySort.field,
      order: companySort.order,
      archived: companyStatusFilter === 'archived' ? true : undefined,
      accounting_role: companyAccountingRoleFilter || undefined,
      ...qfOpts,
    }).then((res) => {
      if (cancelled) return
      setCompanyList(res.clients)
      setCompanyTotal(res.total)
      setCompanyPages(res.pages)
      setCompanyLoading(false)
    }).catch((err) => {
      console.error('Company fetch failed:', err)
      if (!cancelled) setCompanyLoading(false)
    })
    return () => { cancelled = true }
  }, [activeView, companyPage, companySearchDebounced, companySort, companyStatusFilter, companyAccountingRoleFilter, tablePageSize, qfPillValues])

  // Debounce lead search
  useEffect(() => {
    if (leadSearchTimer.current) clearTimeout(leadSearchTimer.current)
    leadSearchTimer.current = setTimeout(() => {
      setLeadSearchDebounced(leadSearch)
      setLeadPage(1)
    }, 350)
    return () => { if (leadSearchTimer.current) clearTimeout(leadSearchTimer.current) }
  }, [leadSearch])

  // Fetch D1 leads (paginated) when page, search, sort, or filters change
  useEffect(() => {
    if (activeView !== 'leads') return
    let cancelled = false
    setLeadLoading(true)
    fetchLeadsPaginated(leadPage, tablePageSize, {
      search: leadSearchDebounced || undefined,
      status: leadStatusFilter || undefined,
      sort: leadSort.field,
      order: leadSort.order,
      includeConverted: leadIncludeConverted,
      includeJunk: leadIncludeDisqualified,
      badge: leadBadgeParam || undefined,
    }).then((res) => {
      if (cancelled) return
      setLeadList(res.leads)
      setLeadPageTotal(res.total)
      setLeadPageCount(res.pages)
      setLeadLoading(false)
    }).catch((err) => {
      console.error('Lead fetch failed:', err)
      if (!cancelled) setLeadLoading(false)
    })
    return () => { cancelled = true }
  }, [activeView, leadPage, leadSearchDebounced, leadSort, leadStatusFilter, leadIncludeConverted, leadIncludeDisqualified, tablePageSize, leadBadgeParam])

  // Fetch lead statuses once on mount
  useEffect(() => {
    fetchLeadStatuses().then(setLeadStatuses).catch(() => {})
  }, [])

  // Scroll table to top on page change
  useEffect(() => {
    tableScrollRef.current?.scrollTo(0, 0)
  }, [clientPage, companyPage, leadPage, ticketsPage, callsPage])

  // Persist search, page, and scroll to sessionStorage (per-user)
  useEffect(() => { sessionStorage.setItem(uKey('clientPage'), String(clientPage)) }, [clientPage])
  useEffect(() => { sessionStorage.setItem(uKey('clientSearch'), clientSearch) }, [clientSearch])
  useEffect(() => { sessionStorage.setItem(uKey('companyPage'), String(companyPage)) }, [companyPage])
  useEffect(() => { sessionStorage.setItem(uKey('companySearch'), companySearch) }, [companySearch])
  useEffect(() => { sessionStorage.setItem(uKey('leadPage'), String(leadPage)) }, [leadPage])
  useEffect(() => { sessionStorage.setItem(uKey('leadSearch'), leadSearch) }, [leadSearch])
  useEffect(() => { setLeadPage(1) }, [leadBadgeParam])
  useEffect(() => { sessionStorage.setItem(uKey('cmdQuery'), cmdQuery) }, [cmdQuery])
  useEffect(() => {
    const el = tableScrollRef.current
    if (!el) return
    const saved = sessionStorage.getItem(uKey('tableScrollTop'))
    if (saved) el.scrollTop = Number(saved)
    const onScroll = () => sessionStorage.setItem(uKey('tableScrollTop'), String(el.scrollTop))
    el.addEventListener('scroll', onScroll, { passive: true })
    const ro = new ResizeObserver(() => setTableScrollHeight(el.offsetHeight))
    ro.observe(el)
    setTableScrollHeight(el.offsetHeight)
    return () => { el.removeEventListener('scroll', onScroll); ro.disconnect() }
  }, [])

  // Compute explicit height for the table scroll container so sticky headers work
  useEffect(() => {
    const el = tableScrollRef.current
    if (!el) return
    const compute = () => {
      const rect = el.getBoundingClientRect()
      const pagination = el.parentElement?.querySelector('.hs-pagination')
      const paginationH = pagination ? pagination.getBoundingClientRect().height : 40
      const available = window.innerHeight - rect.top - paginationH - 8
      if (available > 100) el.style.maxHeight = `${available}px`
      // Sync side panel bottom to align with table scroll bottom (not pagination)
      const contentEl = el.closest('.hs-content')
      if (contentEl) {
        const sidePanel = contentEl.querySelector('.hs-settings-panel, .hs-preview') as HTMLElement | null
        if (sidePanel) {
          const contentBottom = contentEl.getBoundingClientRect().bottom
          const tableBottom = el.getBoundingClientRect().bottom
          sidePanel.style.bottom = `${Math.max(0, Math.round(contentBottom - tableBottom))}px`
        }
      }
    }
    compute()
    window.addEventListener('resize', compute)
    // rAF-coalesced debounce — document-wide mutation observers can fire
    // thousands of times per minute in a live SPA (chat pollers, presence
    // updates, feed ticks). Running `compute` directly on every mutation
    // causes layout thrash and contributes to long-tab hangs. Batch into
    // one layout read per frame.
    let rafPending = false
    const coalesced = () => {
      if (rafPending) return
      rafPending = true
      requestAnimationFrame(() => { rafPending = false; compute() })
    }
    // Scope the observer to the nearest content container rather than
    // document.body — we only care about layout shifts in the active
    // view's content area, not global mutations.
    const observeRoot = el.closest('.hs-content') || el.parentElement || document.body
    const mo = new MutationObserver(coalesced)
    mo.observe(observeRoot, { childList: true, subtree: true })
    // Recompute after short delay for layout settle
    const timer = setTimeout(compute, 500)
    return () => { window.removeEventListener('resize', compute); mo.disconnect(); clearTimeout(timer) }
  }, [activeView, clientViewMode])

  // Fetch CRM users for owner dropdown
  useEffect(() => {
    fetchUsers().then(setCrmUsers).catch(() => {})
  }, [])

  // Close owner dropdown on outside click
  useEffect(() => {
    if (!ownerDropdownOpen) return
    const handler = (e: MouseEvent) => {
      if (ownerDropdownRef.current && !ownerDropdownRef.current.contains(e.target as Node)) {
        setOwnerDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [ownerDropdownOpen])

  // Close the Status / Pipeline Stage quick-filter multi-selects on outside click
  useEffect(() => {
    if (projectsQfPillOpen !== 'status' && projectsQfPillOpen !== 'stage') return
    const handler = (e: MouseEvent) => {
      if (projectsQfMultiRef.current && !projectsQfMultiRef.current.contains(e.target as Node)) {
        setProjectsQfPillOpen(null)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [projectsQfPillOpen])

  // Close the Documents doc-type multi-select dropdown on outside click
  useEffect(() => {
    if (!docsTypeDropdownOpen) return
    const handler = (e: MouseEvent) => {
      if (docsTypeDropdownRef.current && !docsTypeDropdownRef.current.contains(e.target as Node)) {
        setDocsTypeDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [docsTypeDropdownOpen])

  // Close object switcher on outside click
  useEffect(() => {
    if (!objSwitcherOpen) return
    const handler = (e: MouseEvent) => {
      if (objSwitcherRef.current && !objSwitcherRef.current.contains(e.target as Node)) {
        setObjSwitcherOpen(false)
        setObjSwitcherSearch('')
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [objSwitcherOpen])

  // Close view type dropdown on outside click
  useEffect(() => {
    if (!viewTypeDropdownOpen) return
    const handler = (e: MouseEvent) => {
      if (viewTypeDropdownRef.current && !viewTypeDropdownRef.current.contains(e.target as Node)) {
        setViewTypeDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [viewTypeDropdownOpen])

  useEffect(() => {
    // subAdminUsers feeds the owner picker on
    // the Project Record page, so load it whenever that view is active.
    if (activeView !== 'projects') return
    if (subAdminUsers.length > 0) return
    setSubAdminLoading(true)
    fetchSubAdminUsers()
      .then((usersResp: any) => {
        if (usersResp?.success) { setSubAdminUsers(usersResp.data.users); setSubAdminCompanies(usersResp.data.companies) }
      })
      .finally(() => setSubAdminLoading(false))
  }, [activeView]) // eslint-disable-line react-hooks/exhaustive-deps

  // Sync sub-admin Johnny Test demo seed whenever Tutorial Creation Mode or a
  // Walkthrough toggles. Apply the change to local state IMMEDIATELY (no wait
  // for the network roundtrip) so toggling off makes the seed vanish on the
  // next paint, and toggling on makes it appear without refetching.
  useEffect(() => {
    const DEMO_USER_ID = 'demo-user-delete-me'
    const DEMO_SUPERVISOR_ID = 'demo-user-supervisor-1'
    const DEMO_STAFF_ID = 'demo-user-staff-1'
    const DEMO_IDS = new Set([DEMO_USER_ID, DEMO_SUPERVISOR_ID, DEMO_STAFF_ID])
    const DEMO_COMPANY_ID = 'demo-company-delete-me'
    const isActive = () => {
      try { if (localStorage.getItem('kbTutorialCreationMode') === '1') return true } catch { /* */ }
      return !!(window as any).__kbWalkthroughActive
    }
    const apply = () => {
      const active = isActive()
      setSubAdminUsers((prev: any[]) => {
        const stripped = (prev || []).filter(u => !DEMO_IDS.has(u?.id))
        if (!active) return stripped
        return [
          {
            id: DEMO_USER_ID, name: 'Johnny Test', last_name: '', email: 'johnnytest@example.com',
            phone: '5555550000', role: 'owner', owner_discount_percent: 0, avatar_url: null,
            company_id: DEMO_COMPANY_ID, must_reset_password: 1, company_inherited: 0,
            company_name: 'Johnny Test LLC', reporting_manager_id: null, manager_name: null,
            manager_last_name: null, manager_email: null, is_active: 1, last_login_at: null,
            created_at: null, _demo: true,
          },
          {
            id: DEMO_SUPERVISOR_ID, name: 'Supervisor', last_name: '1', email: 'supervisor1@example.com',
            phone: '5555550001', role: 'senior', owner_discount_percent: 0, avatar_url: null,
            company_id: null, must_reset_password: 1, company_inherited: 0,
            company_name: null, reporting_manager_id: null,
            manager_name: null, manager_last_name: null, manager_email: null,
            is_active: 1, last_login_at: null, created_at: null, _demo: true,
          },
          {
            id: DEMO_STAFF_ID, name: 'Staff', last_name: '1', email: 'staff1@example.com',
            phone: '5555550002', role: 'staff', owner_discount_percent: 0, avatar_url: null,
            company_id: null, must_reset_password: 1, company_inherited: 0,
            company_name: null, reporting_manager_id: null,
            manager_name: null, manager_last_name: null, manager_email: null,
            is_active: 1, last_login_at: null, created_at: null, _demo: true,
          },
          ...stripped,
        ]
      })
      setSubAdminCompanies((prev: any[]) => {
        const stripped = (prev || []).filter(c => c?.id !== DEMO_COMPANY_ID)
        if (!active) return stripped
        return [{ id: DEMO_COMPANY_ID, name: 'Johnny Test LLC' }, ...stripped]
      })
    }
    window.addEventListener('kbtc-changed', apply)
    window.addEventListener('kb-walkthrough-changed', apply)
    return () => {
      window.removeEventListener('kbtc-changed', apply)
      window.removeEventListener('kb-walkthrough-changed', apply)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Target Accounts data loading
  useEffect(() => {
    if (activeView !== 'target-accounts') return
    let cancelled = false; setTargetAccountsLoading(true)
    fetchTargetAccounts({ search: targetAccountsSearch || undefined, tier: targetTierFilter || undefined, status: targetStatusFilter || undefined } as any)
      .then(r => { if (!cancelled) { setTargetAccountsData(r.data); } })
      .catch(err => console.error('Target accounts fetch failed:', err))
      .finally(() => { if (!cancelled) setTargetAccountsLoading(false) })
    return () => { cancelled = true }
  }, [activeView, targetAccountsSearch, targetTierFilter, targetStatusFilter])

  // Sales Documents data loading
  useEffect(() => {
    if (activeView !== 'sales-documents') return
    let cancelled = false; setSalesDocsLoading(true)
    fetchSalesDocuments({ search: salesDocsSearch || undefined, type: salesDocsTypeFilter || undefined } as any)
      .then(r => { if (!cancelled) setSalesDocsData(r.data) })
      .catch(err => console.error('Sales docs fetch failed:', err))
      .finally(() => { if (!cancelled) setSalesDocsLoading(false) })
    return () => { cancelled = true }
  }, [activeView, salesDocsSearch, salesDocsTypeFilter])

  // Activity Feed data loading
  useEffect(() => {
    if (activeView !== 'activity-feed') return
    let cancelled = false; setActivityFeedLoading(true)
    fetchActivityFeed({ page: String(activityFeedPage), pageSize: String(tablePageSize), type: activityFeedTypeFilter || undefined } as any)
      .then(r => { if (!cancelled) { setActivityFeedData(r.data); setActivityFeedTotal(r.total) } })
      .catch(err => console.error('Activity feed fetch failed:', err))
      .finally(() => { if (!cancelled) setActivityFeedLoading(false) })
    return () => { cancelled = true }
  }, [activeView, activityFeedPage, activityFeedTypeFilter, tablePageSize])

  // Meeting Types data loading
  useEffect(() => {
    if (activeView !== 'meetings-scheduler') return
    let cancelled = false; setMeetingTypesLoading(true)
    fetchMeetingTypes()
      .then(r => { if (!cancelled) setMeetingTypesData(r.data) })
      .catch(err => console.error('Meeting types fetch failed:', err))
      .finally(() => { if (!cancelled) setMeetingTypesLoading(false) })
    fetchMeetingBookings()
      .then(r => { if (!cancelled) setMeetingBookingsData(r.data) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [activeView, meetingSchedulerTab])

  // Coaching Playlists data loading
  useEffect(() => {
    if (activeView !== 'coaching-playlists' || editingPlaylistId !== null) return
    let cancelled = false; setPlaylistsLoading(true)
    fetchCoachingPlaylists()
      .then(r => { if (!cancelled) setPlaylistsData(r.data) })
      .catch(err => console.error('Playlists fetch failed:', err))
      .finally(() => { if (!cancelled) setPlaylistsLoading(false) })
    return () => { cancelled = true }
  }, [activeView, editingPlaylistId])

  // Coaching Playlist detail loading
  useEffect(() => {
    if (editingPlaylistId === null) return
    fetchCoachingPlaylist(editingPlaylistId).then(r => setEditingPlaylistData(r.data)).catch(() => {})
  }, [editingPlaylistId])

  // Prospecting Workspace data loading
  useEffect(() => {
    if (activeView !== 'prospecting') return
    let cancelled = false; setProspectingLoading(true)
    fetchProspectingSummary()
      .then(r => { if (!cancelled) setProspectingData(r) })
      .catch(err => console.error('Prospecting fetch failed:', err))
      .finally(() => { if (!cancelled) setProspectingLoading(false) })
    return () => { cancelled = true }
  }, [activeView])

  // Close tab edit dropdown on outside click
  useEffect(() => {
    if (!tabEditOpen) return
    const handler = (e: MouseEvent) => {
      if (tabEditRef.current && !tabEditRef.current.contains(e.target as Node)) {
        setTabEditOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [tabEditOpen])

  // Close "Add" button dropdown on outside click
  useEffect(() => {
    if (!addBtnOpen) return
    const handler = (e: MouseEvent) => {
      if (addBtnRef.current && !addBtnRef.current.contains(e.target as Node)) {
        setAddBtnOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [addBtnOpen])

  // Close ads add button dropdown on outside click
  useEffect(() => {
    if (!adsAddBtnOpen) return
    const handle = (e: MouseEvent) => {
      if (adsAddBtnRef.current && !adsAddBtnRef.current.contains(e.target as Node)) {
        setAdsAddBtnOpen(false)
      }
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [adsAddBtnOpen])

  // Close column context menu on outside click
  useEffect(() => {
    if (!colContextMenu) return
    const handler = () => setColContextMenu(null)
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [colContextMenu])

  // Close "+ More" quick filter popover on outside click
  useEffect(() => {
    if (!qfMoreOpen) return
    const handler = (e: MouseEvent) => {
      if (qfMoreRef.current && !qfMoreRef.current.contains(e.target as Node)) {
        setQfMoreOpen(false)
        setQfMoreSearch('')
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [qfMoreOpen])

  // Persist activeQuickFilters to localStorage
  useEffect(() => {
    localStorage.setItem('activeQuickFilters', JSON.stringify(activeQuickFilters))
  }, [activeQuickFilters])

  // Close deal quick-filter pill dropdown on outside click
  useEffect(() => {
    if (!dealQfPillOpen) return
    const handler = (e: MouseEvent) => {
      if (dealQfPillRef.current && !dealQfPillRef.current.contains(e.target as Node)) {
        setDealQfPillOpen(null)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [dealQfPillOpen])

  // Close quick filter pill dropdown on outside click
  useEffect(() => {
    if (!qfPillOpen) return
    const handler = (e: MouseEvent) => {
      if (qfPillRef.current && !qfPillRef.current.contains(e.target as Node)) {
        setQfPillOpen(null)
        setQfPillSearch('')
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [qfPillOpen])

  // (cpOwner / ccIndustry / ccType outside-click handlers removed — typeaheads were only used by the deleted standalone Create Company panel.)

  // (ct* outside-click handlers removed — typeaheads were only used by the deleted standalone Create Ticket panel.)

  // Debounced unified search for smart create panel
  useEffect(() => {
    if (!smartCreateOpen || smartCreateSearch.length < 2) {
      setSmartCreateResults(null)
      return
    }
    setSmartCreateSearching(true)
    const timer = setTimeout(() => {
      searchUnified(smartCreateSearch)
        .then((r) => { console.log('unified-search results:', r); setSmartCreateResults(r) })
        .catch((e) => { console.error('unified-search error:', e); setSmartCreateResults(null) })
        .finally(() => setSmartCreateSearching(false))
    }, 350)
    return () => clearTimeout(timer)
  }, [smartCreateSearch, smartCreateOpen])

  // Persistent duplicate detection for the smart create panel — looks up the
  // entered phone/email/address against existing contacts (independent of focus)
  // so an "already on file" warning persists under the field after you click away.
  useEffect(() => {
    if (!smartCreateOpen) { setSmartCreateDupPhone(null); setSmartCreateDupEmail(null); setSmartCreateDupAddress(null); return }
    const matchedId = (createFormData as any)._matched || null
    // Dedup warnings exist to stop a NEW contact from duplicating an existing
    // one. Once the form is matched to an existing contact, they're noise —
    // even if the phone/email also appears on some other record.
    if (matchedId) { setSmartCreateDupPhone(null); setSmartCreateDupEmail(null); setSmartCreateDupAddress(null); return }
    const phoneDigits = (createFormData.phone || '').replace(/\D/g, '')
    const email = (createFormData.email || '').trim()
    const street = (createFormData.street || '').trim()
    const t = setTimeout(async () => {
      if (phoneDigits.length >= 7) {
        try { const m = (await lookupContactDedup({ phone: phoneDigits })).find(x => x.id !== matchedId); setSmartCreateDupPhone(m ? { id: m.id, name: m.name } : null) } catch { setSmartCreateDupPhone(null) }
      } else setSmartCreateDupPhone(null)
      if (/^[^@]+@[^@]+\.[^@]+$/.test(email)) {
        try { const m = (await lookupContactDedup({ email })).find(x => x.id !== matchedId); setSmartCreateDupEmail(m ? { id: m.id, name: m.name } : null) } catch { setSmartCreateDupEmail(null) }
      } else setSmartCreateDupEmail(null)
      const ns = street.toLowerCase().replace(/[^a-z0-9]/g, '')
      if (ns.length >= 8) {
        try {
          const r = await searchUnified(street)
          const hit = (r.contacts || []).find(c => c.unified_client_id !== matchedId && c.preferred_street && c.preferred_street.toLowerCase().replace(/[^a-z0-9]/g, '').includes(ns))
          setSmartCreateDupAddress(hit ? { id: hit.unified_client_id, name: hit.preferred_name } : null)
        } catch { setSmartCreateDupAddress(null) }
      } else setSmartCreateDupAddress(null)
    }, 400)
    return () => clearTimeout(t)
  }, [smartCreateOpen, createFormData.phone, createFormData.email, createFormData.street, (createFormData as any)._matched]) // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced client-name search for the calendar event modal
  useEffect(() => {
    if (!calEventModal || !calClientSearchFocused || (calEventForm.clientName ?? '').length < 2) {
      setCalClientSearchResults(null)
      return
    }
    setCalClientSearchLoading(true)
    const timer = setTimeout(() => {
      searchUnified(calEventForm.clientName)
        .then(r => setCalClientSearchResults(r))
        .catch(() => setCalClientSearchResults(null))
        .finally(() => setCalClientSearchLoading(false))
    }, 300)
    return () => clearTimeout(timer)
  }, [calEventForm.clientName, calClientSearchFocused, calEventModal])

  // Clear client search state when modal closes
  useEffect(() => {
    if (!calEventModal) {
      setCalClientSearchResults(null)
      setCalClientSearchFocused(false)
    }
  }, [calEventModal])

  // Close pipeline selector on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (pipelineSelectorRef.current && !pipelineSelectorRef.current.contains(e.target as Node)) {
        setPipelineSelectorOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [pipelineSelectorOpen])

  // Close task calendar popover on click outside
  useEffect(() => {
    if (!taskCalOpenId) return
    const handler = (e: MouseEvent) => {
      if (taskCalRef.current && !taskCalRef.current.contains(e.target as Node)) {
        setTaskCalOpenId(null)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [taskCalOpenId])

  // Delete a view tab (saved filter)
  const handleDeleteViewTab = (tabKey: string) => {
    const allKey = activeView === 'tickets' ? 'all' : activeView === 'deals' ? '-1' : '';
    if (tabKey === allKey) return;
    setSavedViewTabs(prev => ({
      ...prev,
      [activeView]: (prev[activeView] || []).filter(k => k !== tabKey)
    }));
    const filter = activeView === 'projects' ? projectsStatusFilter : activeView === 'calls' ? callsDirectionFilter : activeView === 'tickets' ? ticketsFilter : activeView === 'deals' ? String(dealCategory) : activeView === 'leads' ? leadStatusFilter : activeView === 'contacts' ? clientStatusFilter : companyStatusFilter;
    if (filter === tabKey) {
      if (activeView === 'deals') {
        setDealCategory(Number(allKey));
      } else if (activeView === 'projects') {
        setProjectsStatusFilter('');
      } else {
        const setFilter = activeView === 'calls' ? setCallsDirectionFilter : activeView === 'tickets' ? setTicketsFilter : activeView === 'leads' ? setLeadStatusFilter : activeView === 'contacts' ? setClientStatusFilter : setCompanyStatusFilter;
        setFilter(allKey);
      }
    }
    setTabEditOpen(false);
  };

  // Initialize lead view tabs from API data
  useEffect(() => {
    if (leadStatuses.length > 0 && !savedViewTabs.leads) {
      setSavedViewTabs(prev => ({
        ...prev,
        leads: leadStatuses.filter(s => !isTerminalLeadStatus(s)).map(s => s.status_id)
      }));
    }
  }, [leadStatuses])

  // Persist saved view tabs
  useEffect(() => { localStorage.setItem('savedViewTabs', JSON.stringify(savedViewTabs)) }, [savedViewTabs])
  useEffect(() => { localStorage.setItem('botpros-crm-custom-views', JSON.stringify(customViews)) }, [customViews])
  useEffect(() => {
    const SYNCED_VIEW_KEYS = ['projects', 'deals', 'contacts', 'companies', 'leads']
    let cancelled = false
    ;(async () => {
      try {
        const views = await fetchSavedViews()
        if (cancelled) return
        const synced = views.filter(v => SYNCED_VIEW_KEYS.includes(v.object_type))
        setCustomViews(prev => {
          const next = { ...prev }
          for (const key of SYNCED_VIEW_KEYS) {
            const serverViews = synced.filter(v => v.object_type === key)
            const local = (prev[key] || []).filter(entry => !entry.serverId || serverViews.some(v => v.id === entry.serverId))
            for (const v of serverViews) {
              if (local.some(entry => entry.serverId === v.id)) continue
              let filters: typeof local[number]['filters']
              try { filters = v.filters_json ? JSON.parse(v.filters_json) || undefined : undefined } catch { filters = undefined }
              local.push({ id: `sv-${v.id}`, label: v.name, serverId: v.id, ...(filters ? { filters } : {}) })
            }
            next[key] = local
          }
          return next
        })
        for (const key of SYNCED_VIEW_KEYS) {
          for (const entry of customViews[key] || []) {
            if (cancelled || entry.serverId) continue
            try {
              const sv = await createSavedView({ name: entry.label, objectType: key, filtersJson: entry.filters ?? {} })
              if (!cancelled) setCustomViews(prev => ({ ...prev, [key]: (prev[key] || []).map(v => v.id === entry.id ? { ...v, serverId: sv.id } : v) }))
            } catch {}
          }
        }
      } catch {}
    })()
    return () => { cancelled = true }
  }, [])

  // Close view add menu on outside click
  useEffect(() => {
    if (!viewAddMenuOpen) return
    const handler = (e: MouseEvent) => {
      if (viewAddMenuRef.current && !viewAddMenuRef.current.contains(e.target as Node)) setViewAddMenuOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [viewAddMenuOpen])

  // Persist table settings
  useEffect(() => { localStorage.setItem('tablePageSize', String(tablePageSize)) }, [tablePageSize])
  useEffect(() => { localStorage.setItem('tableRowHeight', tableRowHeight) }, [tableRowHeight])
  useEffect(() => { localStorage.setItem('tableZebraStriping', String(tableZebraStriping)) }, [tableZebraStriping])

  // Auto-focus active cell input
  useEffect(() => {
    if (activeCell) {
      setTimeout(() => activeCellInputRef.current?.focus(), 0)
    }
  }, [activeCell])

  // Close context menu on outside click
  useEffect(() => {
    if (!dealContextMenu) return
    const handleClick = () => setDealContextMenu(null)
    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [dealContextMenu])

  // Close profile menu on outside click
  useEffect(() => {
    if (!profileMenuOpen) return
    function handleClick(e: MouseEvent) {
      const t = e.target as Node
      if (profileRef.current?.contains(t) || profilePopRef.current?.contains(t)) return
      setProfileMenuOpen(false)
    }
    function reposition() {
      const r = profileRef.current?.getBoundingClientRect()
      if (r) setProfilePos({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) })
    }
    document.addEventListener('mousedown', handleClick)
    window.addEventListener('resize', reposition)
    return () => { document.removeEventListener('mousedown', handleClick); window.removeEventListener('resize', reposition) }
  }, [profileMenuOpen])
  useEffect(() => {
    if (!createMenuOpen) return
    function handleClick(e: MouseEvent) {
      if (createMenuRef.current && !createMenuRef.current.contains(e.target as Node)) setCreateMenuOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [createMenuOpen])

  // Auto-dismiss deal toast (longer for project-created toasts)
  useEffect(() => {
    if (!dealToast) return
    const duration = dealToast.message.startsWith('__PROJECT_CREATED__') ? 6000 : 3000
    const timer = setTimeout(() => setDealToast(null), duration)
    return () => clearTimeout(timer)
  }, [dealToast])

  // Command palette: Cmd+K / Ctrl+K global shortcut
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setCmdOpen((prev) => {
          if (!prev) { setCmdQuery(''); setCmdResults(null); setCmdSelected(0) }
          return !prev
        })
      }
      // '/' shortcut when no input is focused
      if (e.key === '/' && !['INPUT', 'TEXTAREA', 'SELECT'].includes((e.target as HTMLElement).tagName)) {
        e.preventDefault()
        setCmdOpen(true)
        setCmdQuery('')
        setCmdResults(null)
        setCmdSelected(0)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Focus input when palette opens
  useEffect(() => {
    if (cmdOpen) {
      requestAnimationFrame(() => cmdInputRef.current?.focus())
    }
  }, [cmdOpen])

  // Debounced server search (200ms)
  useEffect(() => {
    if (!cmdOpen || cmdQuery.length < 2) {
      setCmdResults(null)
      setCmdLoading(false)
      return
    }
    setCmdLoading(true)
    const controller = new AbortController()
    const timer = setTimeout(() => {
      searchAll(cmdQuery, 6).then((data) => {
        if (!controller.signal.aborted) {
          setCmdResults(data)
          setCmdLoading(false)
          setCmdSelected(0)
        }
      }).catch(() => {
        if (!controller.signal.aborted) setCmdLoading(false)
      })
    }, 200)
    return () => { clearTimeout(timer); controller.abort() }
  }, [cmdQuery, cmdOpen])

  // Build flat list of all selectable command palette items
  type CmdItem = { type: 'client'; id: string; label: string; subtitle: string }
    | { type: 'lead'; id: string; label: string; subtitle: string }
    | { type: 'deal'; id: string; label: string; subtitle: string }
    | { type: 'employee'; id: string; label: string; subtitle: string }

  const cmdAllItems: CmdItem[] = [
    ...(cmdFilters.clients ? (cmdResults?.results.clients ?? []) : []).map((c) => ({ type: 'client' as const, id: `client-${c.id}`, label: c.title, subtitle: c.subtitle })),
    ...(cmdFilters.leads ? (cmdResults?.results.leads ?? []) : []).map((l) => ({ type: 'lead' as const, id: `lead-${l.id}`, label: l.title, subtitle: l.subtitle })),
    ...(cmdFilters.deals ? (cmdResults?.results.deals ?? []) : []).map((d) => ({ type: 'deal' as const, id: `deal-${d.id}`, label: d.title, subtitle: d.subtitle })),
    ...(cmdFilters.employees ? (cmdResults?.results.employees ?? []) : []).map((e) => ({ type: 'employee' as const, id: `employee-${e.id}`, label: e.title, subtitle: e.subtitle })),
  ]

  const handleCmdSelect = (item: CmdItem) => {
    setCmdOpen(false)
    setCmdQuery('')
    setCmdResults(null)
    if (item.type === 'client') {
      skipDrawerReset.current = true
      setActiveView('contacts')
      setContactDetailOpen(true)
      setContactDetailTab('about')
      const clientId = item.id.replace('client-', '')
      window.location.hash = `/contacts/record/${encodeURIComponent(clientId)}`
      const cached = clientList.find((c) => c.unified_client_id === clientId)
      if (cached) { applyClientOrRedirect(cached) } else {
        fetchClient(clientId).then((c) => applyClientOrRedirect(c)).catch(() => {})
      }
    } else if (item.type === 'lead') {
      skipDrawerReset.current = true
      setActiveView('leads')
      const leadId = Number(item.id.replace('lead-', ''))
      const lead = (liveLeads ?? []).find((l) => String(l.id) === String(leadId))
      if (lead) { setModalLead(lead) } else {
        fetchLead(leadId).then((l) => setModalLead(l)).catch(() => {})
      }
    } else if (item.type === 'employee') {
      skipDrawerReset.current = true
      setActiveView('hr-center')
      setHrPortalView('employees')
      const empId = item.id.replace('employee-', '')
      setHrPortalSelectedId(empId)
    } else if (item.type === 'deal') {
      skipDrawerReset.current = true
      setActiveView('deals')
      const dealId = item.id.replace('deal-', '')
      const deal = (liveDeals ?? []).find((d) => d.id === dealId)
      if (deal) {
        if (deal.categoryId !== dealCategory) setDealCategory(deal.categoryId)
        setSelectedDeal(deal)
        setDealDetailOpen(true)
        window.location.hash = `/deals/record/${deal.id}`
      } else {
        fetchDeal(Number(dealId)).then((d) => {
          if (d.categoryId !== dealCategory) setDealCategory(d.categoryId)
          setSelectedDeal(d)
          setDealDetailOpen(true)
          window.location.hash = `/deals/record/${d.id}`
        }).catch(() => {})
      }
    }
  }

  const handleCmdKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setCmdSelected((prev) => Math.min(prev + 1, cmdAllItems.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setCmdSelected((prev) => Math.max(prev - 1, 0))
    } else if (e.key === 'Enter' && cmdAllItems[cmdSelected]) {
      e.preventDefault()
      handleCmdSelect(cmdAllItems[cmdSelected])
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setCmdOpen(false)
    }
  }

  // Kanban scroll ears — update visibility on scroll + resize
  const updateEarVisibility = () => {
    const el = kanbanScrollRef.current
    if (!el) { setShowLeftEar(false); setShowRightEar(false); return }
    setShowLeftEar(el.scrollLeft > 2)
    setShowRightEar(el.scrollLeft + el.clientWidth < el.scrollWidth - 2)
  }

  useEffect(() => {
    const el = kanbanScrollRef.current
    if (!el || activeView !== 'deals') return
    updateEarVisibility()
    el.addEventListener('scroll', updateEarVisibility, { passive: true })
    const ro = new ResizeObserver(updateEarVisibility)
    ro.observe(el)
    return () => {
      el.removeEventListener('scroll', updateEarVisibility)
      ro.disconnect()
    }
  }, [activeView, livePipeline, dealCategory]) // eslint-disable-line react-hooks/exhaustive-deps

  const earScrollRef = useRef<number | null>(null)

  const startEarScroll = (direction: 'left' | 'right') => {
    const el = kanbanScrollRef.current
    if (!el) return
    const speed = 11
    const step = () => {
      el.scrollLeft += direction === 'left' ? -speed : speed
      earScrollRef.current = requestAnimationFrame(step)
    }
    earScrollRef.current = requestAnimationFrame(step)
  }

  const stopEarScroll = () => {
    if (earScrollRef.current !== null) {
      cancelAnimationFrame(earScrollRef.current)
      earScrollRef.current = null
    }
  }

  const goToPrevMonth = () => {
    setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))
  }
  const goToNextMonth = () => {
    setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))
  }
  const goToToday = () => {
    const today = new Date()
    setCalendarMonth(new Date(today.getFullYear(), today.getMonth(), 1))
    setSelectedDate(toDateKey(today))
  }

  useEffect(() => {
    if (!popupDay) return
    function handleClickOutside(e: MouseEvent) {
      // Don't close popup while meeting detail modal is open
      if (modalMeetingRef.current) return
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        setPopupDay(null)
        setPopupPosition(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [popupDay])

  useEffect(() => {
    const anyModalOpen = modalMeeting || modalDeal || modalContact || modalLead || selectedDeal || previewContact || previewClient || previewCompany || previewLead || previewTicket || previewCall
    if (!anyModalOpen) return
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (previewCall) { closeCallPreview(); return }
        if (previewTicket) { closeTicketPreview(); return }
        if (previewLead) { closeLeadPreview(); return }
        if (previewCompany) { closeCompanyPreview(); return }
        if (previewClient) { closeClientPreview(); return }
        if (previewContact) { closePreviewPanel(); return }
        if (selectedDeal) { closeDealDrawer(); return }
        setModalMeeting(null)
        setModalDeal(null)
        setModalContact(null)
        setModalLead(null)
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [modalMeeting, modalDeal, modalContact, modalLead, selectedDeal, previewContact, previewClient, previewCompany, previewLead, previewTicket, previewCall])

  // Reset drawers when switching views (skip if navigating from search)
  const skipDrawerReset = useRef(false)
  useEffect(() => {
    if (skipDrawerReset.current) { skipDrawerReset.current = false; return }
    setSelectedDeal(null)
    setSelectedWorkgroup(null)
    // Close ALL preview sidebars and settings panel when navigating between views
    setPreviewClient(null)
    setPreviewClientClosing(false)
    setPreviewCompany(null)
    setPreviewCompanyClosing(false)
    setPreviewLead(null)
    setPreviewLeadClosing(false)
    setPreviewTicket(null)
    setPreviewTicketClosing(false)
    setPreviewCall(null)
    setPreviewCallClosing(false)
    setPreviewProject(null)
    setPreviewProjectClosing(false)
    setTableSettingsOpen(false)
    // Clear stale inline edits
    if (activeView !== 'contacts') {
      setCellEdits({})
      setActiveCell(null)
    }
  }, [activeView])

  // (reel auto-scroll handled in useEffect below after reelGroups is computed)

  const openPopup = (dayKey: string, e: React.MouseEvent) => {
    e.stopPropagation()
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    const popupWidth = 320
    const popupHeight = 320
    let x = rect.left + rect.width / 2 - popupWidth / 2
    let y = rect.bottom + 4
    if (x + popupWidth > window.innerWidth - 8) x = window.innerWidth - popupWidth - 8
    if (x < 8) x = 8
    if (y + popupHeight > window.innerHeight - 8) y = rect.top - popupHeight - 4
    setPopupDay(dayKey)
    setPopupPosition({ x, y })
  }

  // Fetch deal activities (merged with lead), linked project, linked lead,
  // linked client, and audit-log history when deal detail opens (Tier 6)
  useEffect(() => {
    if (!selectedDeal) { setDealActivities([]); setDealLinkedProjects([]); setDealAllocatedTotal(0); setDealLinkedLead(null); setDealLinkedLeadTried(false); setDealLinkedClient(null); setDealLinkedClientTried(false); setDealHistory([]); setDealTimelineLimit(5); setDealActFetchLimit(300); setTimeLaborSummary(null); return }
    setDealActFetchLimit(300)
    let cancelled = false
    setDealActivitiesLoading(true)
    setDealTimelineLimit(5)
    // Fetch audit-log history for this deal
    fetchDealHistory(Number(selectedDeal.id))
      .then((rows) => { if (!cancelled) setDealHistory(Array.isArray(rows) ? rows : []) })
      .catch(() => { if (!cancelled) setDealHistory([]) })
    // Labor summary for the Time card in the right sidebar.
    setTimeLaborLoading(true)
    fetchDealLaborSummary(Number(selectedDeal.id))
      .then(s => { if (!cancelled) setTimeLaborSummary(s) })
      .catch(() => { if (!cancelled) setTimeLaborSummary(null) })
      .finally(() => { if (!cancelled) setTimeLaborLoading(false) })
    // Fetch merged activities (includes lead activities with from_entity field)
    getDealActivitiesMerged(Number(selectedDeal.id))
      .then((acts) => { if (!cancelled) setDealActivities(acts) })
      .catch(() => { if (!cancelled) fetchDealActivities(Number(selectedDeal.id)).then(acts => { if (!cancelled) setDealActivities(acts) }).catch(() => {}) })
      .finally(() => { if (!cancelled) setDealActivitiesLoading(false) })
    // Fetch all projects for this deal
    fetch(`/api/crm/deals/${selectedDeal.id}/projects`).then(r => r.json())
      .then((data: any) => { if (!cancelled) { setDealLinkedProjects(data.projects || []); setDealAllocatedTotal(data.allocated_total || 0) } })
      .catch(() => { if (!cancelled) { setDealLinkedProjects([]); setDealAllocatedTotal(0) } })
    // Fetch addresses (many per deal)
    fetch(`/api/crm/deals/${selectedDeal.id}/addresses`).then(r => r.json())
      .then((list: any) => { if (!cancelled) setDealAddresses(Array.isArray(list) ? list : []) })
      .catch(() => { if (!cancelled) setDealAddresses([]) })
    // Fetch originating lead
    setDealLinkedLeadTried(false)
    if (selectedDeal.leadId) {
      getDealLead(Number(selectedDeal.id))
        .then((lead) => { if (!cancelled) { setDealLinkedLead(lead); setDealLinkedLeadTried(true) } })
        .catch(() => { if (!cancelled) { setDealLinkedLead(null); setDealLinkedLeadTried(true) } })
    } else {
      setDealLinkedLead(null)
      setDealLinkedLeadTried(true)
    }
    // Fetch linked client so the Contacts sidebar card can show name + phone + email.
    // Modern deals post-migration carry a bare numeric contactId (e.g. 71) whose canonical
    // unified_client_id is BX-padded (e.g. BX-00071). Legacy deals may also expose a bare
    // legacy contactId. Try the explicit unifiedClientId first, then fall back to the
    // bare contactId, then to the BX-padded form, so both id shapes resolve. If all attempts
    // 404 we still flip `tried` so the sidebar renders a name-only row instead of perpetual
    // "Loading…".
    setDealLinkedClientTried(false)
    {
      const candidates: string[] = []
      if ((selectedDeal as any).primaryContactId) candidates.push(String((selectedDeal as any).primaryContactId))
      if (selectedDeal.unifiedClientId && !candidates.includes(String(selectedDeal.unifiedClientId))) candidates.push(String(selectedDeal.unifiedClientId))
      if (selectedDeal.contactId != null) {
        const bare = String(selectedDeal.contactId)
        if (!candidates.includes(bare)) candidates.push(bare)
        const padded = 'BX-' + bare.padStart(5, '0')
        if (!candidates.includes(padded)) candidates.push(padded)
      }
      if (candidates.length === 0) {
        setDealLinkedClient(null)
        setDealLinkedClientTried(true)
      } else {
        ;(async () => {
          for (const cid of candidates) {
            try {
              const c = await fetchClient(cid)
              if (cancelled) return
              if (c) { setDealLinkedClient(c); setDealLinkedClientTried(true); return }
            } catch { /* try next candidate */ }
          }
          if (!cancelled) { setDealLinkedClient(null); setDealLinkedClientTried(true) }
        })()
      }
    }
    // Fetch all associated contacts (many-to-many)
    fetch(`/api/crm/deals/${selectedDeal.id}/contacts`).then(r => r.json())
      .then((list) => { if (!cancelled) setDealContacts(Array.isArray(list) ? list : []) })
      .catch(() => { if (!cancelled) setDealContacts([]) })
    return () => { cancelled = true }
  }, [selectedDeal?.id])

  // Fetch tickets linked to this deal (Task 15). Re-runs when tickets are
  // created/updated elsewhere via ticketsRefreshTick.
  useEffect(() => {
    if (!selectedDeal?.id) { setDealTickets([]); return }
    let cancelled = false
    fetchDealTickets(Number(selectedDeal.id))
      .then(list => { if (!cancelled) setDealTickets(Array.isArray(list) ? list : []) })
      .catch(() => { if (!cancelled) setDealTickets([]) })
    return () => { cancelled = true }
  }, [selectedDeal?.id, ticketsRefreshTick])

  // When navigating here with a #ticket-{id} anchor (e.g. from the contact
  // page's rollup), scroll to that ticket row and flash-highlight it.
  useEffect(() => {
    const match = window.location.hash.match(/#ticket-(\d+)$/)
    if (!match) return
    if (dealTickets.length === 0) return
    const el = document.getElementById(`ticket-${match[1]}`)
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    el.style.transition = 'background-color 600ms ease'
    el.style.backgroundColor = '#fef3c7'
    setTimeout(() => { el.style.backgroundColor = 'transparent' }, 1800)
  }, [dealTickets])

  // Fetch the emails belonging to the deal's pinned primary contact whenever
  // that contact changes. Falls back to the first associated contact when no
  // primary_contact_id is set, matching the Contact card's default selection.
  // Final fallback: legacy deals that were never wired through deal_contacts
  // still expose selectedDeal.unifiedClientId — use that so the card body
  // populates instead of staying perpetually blank.
  useEffect(() => {
    if (!selectedDeal) { setDealContactEmails([]); return }
    const associated = dealContacts.filter((dc: any) => dc.role !== 'company')
    const effectiveContactId =
      (selectedDeal as any).primaryContactId ||
      (associated[0]?.unified_client_id ?? null) ||
      (selectedDeal as any).unifiedClientId ||
      null
    if (!effectiveContactId) { setDealContactEmails([]); return }
    let cancelled = false
    fetch(`/api/crm/contacts/${encodeURIComponent(effectiveContactId)}/emails`)
      .then(r => r.ok ? r.json() : [])
      .then((list) => { if (!cancelled) setDealContactEmails(Array.isArray(list) ? list : []) })
      .catch(() => { if (!cancelled) setDealContactEmails([]) })
    return () => { cancelled = true }
  }, [selectedDeal?.id, (selectedDeal as any)?.primaryContactId, (selectedDeal as any)?.unifiedClientId, dealContacts])

  // Close the Key Information popover on outside click or Escape.
  // Also recompute its fixed position on scroll/resize so it stays anchored
  // to the row as the page scrolls. Popover uses position:fixed (see render
  // site) to escape .crp-card overflow:hidden + stacking contexts.
  useEffect(() => {
    if (!dealKeyInfoPopover) { setDealKeyInfoPopoverPos(null); return }
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null
      if (!target) { setDealKeyInfoPopover(null); return }
      // Clicks inside a crp-field that is currently anchoring a popover
      // are allowed — the popover manages its own internal clicks via
      // stopPropagation. The popover itself is rendered outside .crp-field
      // (position:fixed at document level), so allow clicks inside the
      // popover container too.
      if (target.closest('.crp-field')) return
      if (target.closest('[data-deal-keyinfo-popover]')) return
      setDealKeyInfoPopover(null)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDealKeyInfoPopover(null)
    }
    const recompute = () => {
      const anchor = dealKeyInfoAnchorRefs.current[dealKeyInfoPopover]
      if (!anchor) { setDealKeyInfoPopoverPos(null); return }
      const rect = anchor.getBoundingClientRect()
      const vw = window.innerWidth
      const vh = window.innerHeight
      const POPOVER_W = 280
      const POPOVER_MAX_H = 280
      let left = rect.left
      // Clamp to viewport right edge (with 8px margin).
      if (left + POPOVER_W + 8 > vw) left = Math.max(8, vw - POPOVER_W - 8)
      if (left < 8) left = 8
      // Flip upward if there isn't enough room below and there's more room above.
      // Read the popover's actual rendered height when available so tall content
      // still flips correctly; fall back to the CSS max-height (280).
      const popoverHeight = dealKeyInfoPopoverRef.current?.offsetHeight || POPOVER_MAX_H
      const spaceBelow = vh - rect.bottom
      const spaceAbove = rect.top
      const shouldFlip = spaceBelow < popoverHeight + 16 && spaceAbove > spaceBelow
      if (shouldFlip) {
        const top = Math.max(8, rect.top - popoverHeight - 4)
        // Constrain max-height so the flipped popover can't overflow off the top.
        const maxHeight = Math.max(0, spaceAbove - 16)
        setDealKeyInfoPopoverPos({ top, left, maxHeight })
      } else {
        setDealKeyInfoPopoverPos({ top: rect.bottom + 4, left })
      }
    }
    recompute()
    // Second pass on the next frame: once the popover DOM is mounted, its
    // real offsetHeight is available so the flip decision can refine.
    const raf = requestAnimationFrame(recompute)
    // Observe size changes on the popover itself so when the user clicks
    // "+ Add email" and an input row expands the popover, the flip/clamp
    // logic re-fires and the popover doesn't spill past the viewport edge.
    let ro: ResizeObserver | null = null
    const rafSecond = requestAnimationFrame(() => {
      if (dealKeyInfoPopoverRef.current && typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(() => recompute())
        ro.observe(dealKeyInfoPopoverRef.current)
      }
    })
    document.addEventListener('mousedown', onDocClick, true)
    document.addEventListener('keydown', onKey)
    window.addEventListener('scroll', recompute, true)
    window.addEventListener('resize', recompute)
    return () => {
      cancelAnimationFrame(raf)
      cancelAnimationFrame(rafSecond)
      if (ro) ro.disconnect()
      document.removeEventListener('mousedown', onDocClick, true)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', recompute, true)
      window.removeEventListener('resize', recompute)
    }
  }, [dealKeyInfoPopover])

  // Reset Key Info contact-popover sub-state whenever the popover closes,
  // switches rows, or the deal changes. Keeps stale search results /
  // half-filled add-new forms from showing when the user reopens.
  useEffect(() => {
    if (dealKeyInfoPopover !== 'contact') {
      setDealKeyInfoContactSearch('')
      setDealKeyInfoContactSearchResults([])
      setDealKeyInfoAddContactOpen(false)
      setDealKeyInfoAddContactForm({ name: '', email: '', phone: '' })
    }
  }, [dealKeyInfoPopover, selectedDeal?.id])

  // Debounced search across ALL contacts when the user types in the
  // "Search all contacts…" input inside the Contact popover.
  useEffect(() => {
    if (dealKeyInfoPopover !== 'contact') return
    const q = dealKeyInfoContactSearch.trim()
    if (!q) { setDealKeyInfoContactSearchResults([]); return }
    setDealKeyInfoContactSearching(true)
    const t = setTimeout(() => {
      fetchClients(1, 10, { search: q })
        .then((r: any) => setDealKeyInfoContactSearchResults(r.clients || r.data || []))
        .catch(() => setDealKeyInfoContactSearchResults([]))
        .finally(() => setDealKeyInfoContactSearching(false))
    }, 250)
    return () => clearTimeout(t)
  }, [dealKeyInfoPopover, dealKeyInfoContactSearch])

  // Reset the auto-pick guard when the selected deal changes so each deal
  // gets one auto-pick pass.
  useEffect(() => {
    dealKeyInfoAutoPickedRef.current = { contact: null, email: null }
  }, [selectedDeal?.id])

  // Auto-pick primary contact + primary email when there's exactly one
  // obvious choice and the deal has nothing pinned. Silent (no toast).
  // Guarded by dealKeyInfoAutoPickedRef so it runs once per load per deal
  // and doesn't fight the user if they manually unpin.
  useEffect(() => {
    if (!selectedDeal?.id) return
    const dealId = Number(selectedDeal.id)
    const assoc = dealContacts.filter((dc: any) => dc.role !== 'company')
    const legacyClientId = (selectedDeal as any).unifiedClientId || null
    const pinnedContactId = (selectedDeal as any).primaryContactId || null

    // --- Auto-pick contact ---
    if (!pinnedContactId && dealKeyInfoAutoPickedRef.current.contact !== dealId) {
      let target: string | null = null
      if (assoc.length === 1) target = assoc[0].unified_client_id
      else if (assoc.length === 0 && legacyClientId) target = legacyClientId
      if (target) {
        dealKeyInfoAutoPickedRef.current.contact = dealId
        // Silent PATCH; mirror persistDealFields without the alert.
        setSelectedDeal((d: any) => d ? { ...d, primaryContactId: target } : d)
        updateDeal(dealId, { primaryContactId: target })
          .then(() => fetchDeal(dealId))
          .then((fresh) => {
            setSelectedDeal(fresh)
            setLiveDeals(prev => prev ? prev.map(d => d.id === fresh.id ? fresh : d) : prev)
          })
          .catch(() => { /* silent */ })
      }
    }

    // --- Auto-pick primary email ---
    const currentPrimaryEmailId = (selectedDeal as any).primaryEmailId ?? null
    if (!currentPrimaryEmailId
        && dealContactEmails.length === 1
        && dealKeyInfoAutoPickedRef.current.email !== dealId) {
      const onlyEmailId = dealContactEmails[0].id
      dealKeyInfoAutoPickedRef.current.email = dealId
      setSelectedDeal((d: any) => d ? { ...d, primaryEmailId: onlyEmailId } : d)
      updateDeal(dealId, { primaryEmailId: onlyEmailId })
        .then(() => fetchDeal(dealId))
        .then((fresh) => {
          setSelectedDeal(fresh)
          setLiveDeals(prev => prev ? prev.map(d => d.id === fresh.id ? fresh : d) : prev)
        })
        .catch(() => { /* silent */ })
    }
  }, [selectedDeal?.id, dealContacts, dealContactEmails, (selectedDeal as any)?.primaryContactId, (selectedDeal as any)?.primaryEmailId, (selectedDeal as any)?.unifiedClientId])

  const popupDateLabel = popupDay
    ? new Intl.DateTimeFormat('en-US', { day: '2-digit', month: 'long' }).format(
        parseDateKey(popupDay),
      )
    : ''

  const isEventVisible = (m: Meeting) =>
    (m.category === 'holiday' || m.category === 'time_off')
      ? enabledCalendars.has(m.calendar)
      : enabledCalendars.has(m.calendar) && enabledEmployees.has(m.owner)

  const popupMeetings = popupDay
    ? (() => {
        const filtered = sortMeetings(meetings.filter((m) => {
          // Multi-day all-day spans show on every day in start..end (inclusive).
          const inRange = m.endDate && m.endDate > m.date
            ? m.date <= popupDay && popupDay <= m.endDate
            : m.date === popupDay
          return inRange && isEventVisible(m)
        }))
        const map = new Map<string, any>()
        for (const m of filtered) {
          const k = `${m.time}|${m.title}`
          if (map.has(k)) { const ex = map.get(k); if (m.owner && !(ex._allOwners as string[]).includes(m.owner)) (ex._allOwners as string[]).push(m.owner) }
          else map.set(k, { ...m, _allOwners: [m.owner].filter(Boolean) })
        }
        return [...map.values()]
      })()
    : []

  const toggleCalendar = (id: CalendarId) => {
    setEnabledCalendars((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleEmployee = (name: string) => {
    setEnabledEmployees((prev) => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }

  // Filter deals by search query + quick filters (owner, amount, close-date, stage)
  const filteredDeals = (() => {
    // Pre-compute date thresholds once per render
    const todayDate = new Date()
    const todayYmd = `${todayDate.getFullYear()}-${String(todayDate.getMonth()+1).padStart(2,'0')}-${String(todayDate.getDate()).padStart(2,'0')}`
    const tomorrowDate = new Date(); tomorrowDate.setDate(tomorrowDate.getDate()+1)
    const tomorrowYmd = `${tomorrowDate.getFullYear()}-${String(tomorrowDate.getMonth()+1).padStart(2,'0')}-${String(tomorrowDate.getDate()).padStart(2,'0')}`
    const weekEnd = new Date(); weekEnd.setDate(weekEnd.getDate() + 7)
    const weekEndYmd = `${weekEnd.getFullYear()}-${String(weekEnd.getMonth()+1).padStart(2,'0')}-${String(weekEnd.getDate()).padStart(2,'0')}`
    const monthEnd = new Date(); monthEnd.setMonth(monthEnd.getMonth() + 1)
    const monthEndYmd = `${monthEnd.getFullYear()}-${String(monthEnd.getMonth()+1).padStart(2,'0')}-${String(monthEnd.getDate()).padStart(2,'0')}`
    const filtered = (liveDeals ?? []).filter((deal) => {
      // Stage filter (table view pills)
      if (dealStageFilter.size > 0 && !dealStageFilter.has(deal.stage)) return false
      // Owner filter
      if (dealOwnerFilter.size > 0 && !dealOwnerFilter.has(String(deal.assignedTo || ''))) return false
      // Amount filter
      if (dealAmountFilter) {
        const amt = Number(deal.opportunity) || 0
        if (dealAmountFilter === 'lt5k' && amt >= 5000) return false
        if (dealAmountFilter === '5k-25k' && (amt < 5000 || amt >= 25000)) return false
        if (dealAmountFilter === '25k-100k' && (amt < 25000 || amt >= 100000)) return false
        if (dealAmountFilter === 'gt100k' && amt < 100000) return false
      }
      // Appt. Date filter — use the current appointment via the shared resolver
      // (blank for No-Show/Reschedule + Lost) so the filter matches what the
      // APPT. DATE column shows; a lingering close_date never resurrects it.
      if (dealCloseDateFilter) {
        const apptSrc = resolveApptDateSource(deal)
        if (!apptSrc) return false
        const apptYmd = String(apptSrc).slice(0, 10)
        if (dealCloseDateFilter === 'overdue' && apptYmd >= todayYmd) return false
        if (dealCloseDateFilter === 'today' && apptYmd !== todayYmd) return false
        if (dealCloseDateFilter === 'tomorrow' && apptYmd !== tomorrowYmd) return false
        if (dealCloseDateFilter === 'this-week' && (apptYmd < todayYmd || apptYmd > weekEndYmd)) return false
        if (dealCloseDateFilter === 'this-month' && (apptYmd < todayYmd || apptYmd > monthEndYmd)) return false
      }
      return true
    })
    const { field, order } = dealSort
    const dir = order === 'ASC' ? 1 : -1
    const get = (d: any) => {
      const v = d?.[field]
      if (v == null) return null
      if (field === 'opportunity' || field === 'project_count' || field === 'ticket_count') return Number(v) || 0
      if (field === 'closeDate') return String(v)
      return String(v).toLowerCase()
    }
    // PRIMARY KEY: float No-Show / Reschedule deals to the very top of the
    // Open Deals list regardless of the user-selected column sort — these deals
    // have a blanked appointment date (so a plain appt-date sort drops them to
    // the bottom) but need rescheduling and must be seen first. Within each
    // group, fall back to the existing column comparator below.
    const isNoShow = (d: any) => d?.stage === 'NO_SHOW_RESCHEDULE'
    return [...filtered].sort((a, b) => {
      const aNs = isNoShow(a), bNs = isNoShow(b)
      if (aNs !== bNs) return aNs ? -1 : 1
      const av = get(a), bv = get(b)
      if (av == null && bv == null) return 0
      if (av == null) return 1   // nulls always last
      if (bv == null) return -1
      if (av < bv) return -1 * dir
      if (av > bv) return 1 * dir
      return 0
    })
  })()

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  void ((liveLeads ?? []).filter((lead) => {
    if (!query) return true
    const value = `${lead.title} ${lead.name} ${lead.statusLabel} ${lead.assignedTo} ${lead.sourceLabel}`.toLowerCase()
    return value.includes(query.toLowerCase())
  }))

  // filteredWorkgroups removed — projects now use CRM projects API

  // Group deals by stage for kanban
  const dealStageOrder = livePipeline
    ? livePipeline.map((s) => s.stageId)
    : ['NEW', 'PREPAYMENT_INVOICE', 'EXECUTING', 'FINAL_INVOICE', 'UC_JWD2FR', 'UC_X1T387', 'UC_PY80KD']

  const dealStageLabels: Record<string, string> = {}
  if (livePipeline) {
    for (const s of livePipeline) dealStageLabels[s.stageId] = s.label
  }

  const dealsByStage = dealStageOrder.map((stageId) => {
    const stageDeals = filteredDeals.filter((d) => d.stage === stageId)
    const totalValue = stageDeals.reduce((sum, d) => sum + d.opportunity, 0)
    const stageInfo = allPipelineStages?.find((s) => s.stageId === stageId)
    return {
      stageId,
      label: dealStageLabels[stageId] || stageId,
      color: stageInfo?.color || STAGE_COLORS[stageId] || '#94a3b8',
      deals: stageDeals,
      count: stageDeals.length,
      totalValue,
    }
  })

  // Terminal stages (Won/Lost) — shown as bottom dropzones when dragging
  const terminalStages = (allPipelineStages ?? []).filter(isTerminalPipelineStage)

  const PIPELINE_NAMES: Record<number, string> = { 0: 'Sales' }

  // --- Kanban drag-and-drop handlers ---
  const handleDealDragStart = (e: React.DragEvent, dealId: string) => {
    setDraggedDealId(dealId)
    e.dataTransfer.effectAllowed = 'move'
    setTimeout(() => {
      const el = document.getElementById(`deal-card-${dealId}`)
      if (el) el.classList.add('opacity-40')
    }, 0)
  }

  const handleDealDragEnd = (_e: React.DragEvent, dealId: string) => {
    setDraggedDealId(null)
    setDragOverStageId(null)
    const el = document.getElementById(`deal-card-${dealId}`)
    if (el) el.classList.remove('opacity-40')
  }

  const handleDealDragOver = (e: React.DragEvent, targetStageId?: string) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (targetStageId && dragOverStageId !== targetStageId) {
      setDragOverStageId(targetStageId)
    }
  }

  const handleKanbanContainerDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    if (!kanbanScrollRef.current) return
    const container = kanbanScrollRef.current
    const rect = container.getBoundingClientRect()
    const mouseX = e.clientX - rect.left
    const SCROLL_ZONE = 80
    const SCROLL_SPEED = 12
    if (mouseX < SCROLL_ZONE) container.scrollLeft -= SCROLL_SPEED
    else if (mouseX > rect.width - SCROLL_ZONE) container.scrollLeft += SCROLL_SPEED
  }

  const handleDealDragLeave = (e: React.DragEvent, targetStageId?: string) => {
    e.preventDefault()
    if (targetStageId && dragOverStageId === targetStageId) setDragOverStageId(null)
  }

  const executeDealMove = async (dealId: string, targetStageId: string, opts?: { lossReason?: string; estimateAppointment?: string; newAppointment?: string; noShowReason?: string; sendConfirmationEmail?: boolean; newOwnerUserId?: number }) => {
    const deal = (liveDeals ?? []).find((d) => d.id === dealId)
    if (!deal || deal.stage === targetStageId) return

    const targetLabel = allPipelineStages?.find((s) => s.stageId === targetStageId)?.label ?? targetStageId

    // Optimistic update
    setLiveDeals((prev) =>
      (prev ?? []).map((d) =>
        d.id === dealId ? { ...d, stage: targetStageId, stageLabel: targetLabel } : d,
      ),
    )

    try {
      const result = await updateDealStage(dealId, targetStageId, opts) as { success: boolean; previousStage: string; newStage: string; newStageLabel?: string; projectCreated?: number }
      // Reconcile optimistic update with actual server state (e.g. NO_SHOW_RESCHEDULE with newAppointment moves to APPOINTMENT_SET)
      if (result.newStage !== targetStageId) {
        const actualLabel = allPipelineStages?.find((s) => s.stageId === result.newStage)?.label ?? result.newStageLabel ?? result.newStage
        setLiveDeals((prev) =>
          (prev ?? []).map((d) =>
            d.id === dealId ? { ...d, stage: result.newStage, stageLabel: actualLabel } : d,
          ),
        )
      }
      // Refetch the moved deal so the LIST row reflects the new appointment date,
      // stage, and close date immediately — no manual page refresh. The optimistic
      // update above only set stage/stageLabel; a NO_SHOW_RESCHEDULE clears
      // estimate_appointment and an APPOINTMENT_SET / reschedule changes it, so we
      // pull the authoritative row and merge it into liveDeals (filteredDeals then
      // re-derives + re-sorts, moving the row to its new position). Also refresh the
      // open detail pane when it's the same deal.
      fetchDeal(Number(dealId)).then((fresh) => {
        setLiveDeals((prev) => (prev ?? []).map((d) => (d.id === dealId ? fresh : d)))
        if (selectedDeal?.id === dealId) setSelectedDeal(fresh)
      }).catch(() => {})
      // Appointment-affecting moves also change the calendar — nudge it to refresh.
      if (targetStageId === 'APPOINTMENT_SET' || targetStageId === 'NO_SHOW_RESCHEDULE' || result.newStage === 'APPOINTMENT_SET' || result.newStage === 'NO_SHOW_RESCHEDULE') {
        setCalendarRefreshTick(t => t + 1)
      }
      if (targetStageId === 'CONTRACT_SIGNED' && result.projectCreated) {
        setDealToast({ id: Date.now(), message: `__PROJECT_CREATED__${result.projectCreated}`, type: 'success' })
      } else {
        const displayLabel = result.newStageLabel ?? targetLabel
        setDealToast({ id: Date.now(), message: `Moved to ${displayLabel}`, type: 'success' })
      }
      setPipelineRefreshTick(t => t + 1)
    } catch (err) {
      // Revert on failure
      setLiveDeals((prev) =>
        (prev ?? []).map((d) =>
          d.id === dealId ? { ...d, stage: deal.stage, stageLabel: deal.stageLabel } : d,
        ),
      )
      setDealToast({ id: Date.now(), message: `Failed to move deal`, type: 'error' })
    }
  }

  // Helper: open the Contract Signed modal and simultaneously fetch the deal's addresses
  // so the address-guard in the modal has up-to-date data regardless of whether the
  // deal detail panel is already open.
  const openContractSignedModal = (dealId: string, dealTitle: string) => {
    setContractSignedModal({ dealId, dealTitle })
    fetch(`/api/crm/deals/${dealId}/addresses`)
      .then(r => r.json())
      .then((list: any) => setDealAddresses(Array.isArray(list) ? list : []))
      .catch(() => setDealAddresses([]))
  }

  const handleDealDrop = (e: React.DragEvent, targetStageId: string) => {
    e.preventDefault()
    setDragOverStageId(null)
    if (!draggedDealId) return
    const deal = (liveDeals ?? []).find((d) => d.id === draggedDealId)
    if (!deal || deal.stage === targetStageId) { setDraggedDealId(null); return }

    const target = allPipelineStages?.find((s) => s.stageId === targetStageId)

    if (targetStageId === 'LOST') {
      setLossReasonModal({ dealId: deal.id, dealTitle: deal.title })
      setLossReasonValue('Price Too High')
      setLossReasonNotes('')
    } else if (targetStageId === 'NO_SHOW_RESCHEDULE') {
      setNoShowModal({ dealId: deal.id, dealTitle: deal.title })
      setNoShowReason('Customer no-show')
      setNoShowNewAppt('')
    } else if (targetStageId === 'CONTRACT_SIGNED') {
      setDealCreateProjectForm({ name: deal.title || '', contractor_price: String(deal.opportunity || ''), work_order_price: '', address: '', address_street: '', address_city: '', address_state: '', address_zip: '', services_requested: (deal as any).services_requested || '', customer_name: '' })
      openContractSignedModal(deal.id, deal.title)
    } else {
      const isTerminal = isTerminalPipelineStage(target)
      if (isTerminal) {
        setDealConfirmMove({ dealId: deal.id, dealTitle: deal.title, stageId: targetStageId, stageName: target?.label ?? targetStageId })
      } else {
        executeDealMove(deal.id, targetStageId)
      }
    }
    setDraggedDealId(null)
  }

  const saveDealField = (field: string, value: string) => {
    if (!selectedDeal) return
    const payload: Record<string, unknown> = {}
    if (field === 'opportunity') payload[field] = Number(value) || 0
    else payload[field] = value
    updateDeal(selectedDeal.id, payload).then((updated) => {
      setSelectedDeal(updated)
      // Also update in the live deals list
      setLiveDeals(prev => prev ? prev.map(d => d.id === updated.id ? updated : d) : prev)
    }).catch(() => {})
  }

  // Fetch lead activities when lead modal opens (Task 29b)
  useEffect(() => {
    if (!modalLead) { setLeadActivities([]); setLeadNotesEditing(false); return }
    setLeadActivitiesLoading(true)
    fetch(`/api/crm/leads/${modalLead.id}/activities`)
      .then(r => r.ok ? r.json() : [])
      .then(acts => setLeadActivities(Array.isArray(acts) ? acts : []))
      .catch(() => setLeadActivities([]))
      .finally(() => setLeadActivitiesLoading(false))
  }, [modalLead?.id])

  const saveLeadField = (field: string, value: string) => {
    if (!modalLead) return
    const payload: Record<string, unknown> = {}
    if (field === 'opportunity') payload[field] = Number(value) || 0
    else payload[field] = value
    updateLead(modalLead.id, payload).then((updated) => {
      setModalLead(updated)
      setLiveLeads(prev => prev ? prev.map(l => l.id === updated.id ? updated : l) : prev)
    }).catch(() => {})
  }

  const closeDealDrawer = () => {
    setDrawerClosing(true)
    setTimeout(() => {
      setSelectedDeal(null)
      setDrawerClosing(false)
    }, 250)
  }

  async function startDealDelete(deal: { id: string | number; title: string }, afterDelete?: () => void) {
    try {
      const preview = await fetchDealDeletePreview(Number(deal.id))
      if (preview.linked_projects === 0 && preview.linked_tickets === 0) {
        // No cascade — simple confirm
        if (!window.confirm(`Delete deal "${deal.title}"?`)) return
        await deleteDeal(deal.id)
        setLiveDeals(prev => prev ? prev.filter(d => String(d.id) !== String(deal.id)) : prev)
        setTicketsRefreshTick(t => t + 1)
        refetchCurrentDeals().then((res) => { setLiveDeals(res.deals); setDealTotal(res.total); setDealPages(res.pages) }).catch(() => {})
        afterDelete?.()
        return
      }
      setDealDeleteModal({ deal, preview, step: 1, typed: '', afterDelete })
    } catch (e: any) {
      alert('Failed to check deal: ' + (e?.message || String(e)))
    }
  }

  // Task 18: Single-confirm helper for deleting a project that may have linked tickets.
  // Backend cascades (nulls ticket.project_id while keeping deal + contact); this just
  // warns the user first. Returns true if delete proceeded, false otherwise.
  async function startProjectDelete(project: { id: string; name: string }): Promise<boolean> {
    try {
      const preview = await fetchProjectDeletePreview(project.id)
      const warning = preview.linked_tickets > 0
        ? `Delete project "${project.name}"? ${preview.linked_tickets} ticket${preview.linked_tickets > 1 ? 's' : ''} will lose their project link (deal + contact preserved).`
        : `Delete project "${project.name}"?`
      if (!window.confirm(warning)) return false
      await deleteProject(project.id)
      setTicketsRefreshTick(t => t + 1)
      return true
    } catch (e: any) {
      alert('Delete failed: ' + (e?.message || String(e)))
      return false
    }
  }

  // Task 18: Single-confirm helper for changing/removing a ticket's deal while a
  // project is attached. Backend auto-nulls project_id on deal change — this warns
  // the user first. Returns true if update succeeded.
  async function saveTicketDealChange(ticketId: number, newDealId: number | null, currentProjectId: string | null): Promise<boolean> {
    if (currentProjectId != null) {
      const msg = newDealId == null
        ? 'This will unlink the project attached to this ticket. Continue?'
        : 'The attached project belongs to the old deal and will be unlinked. Continue?'
      if (!window.confirm(msg)) return false
    }
    try {
      await updateTicket(ticketId, { dealId: newDealId })
      setTicketsRefreshTick(t => t + 1)
      return true
    } catch (e: any) {
      alert('Failed to update ticket: ' + (e?.message || String(e)))
      return false
    }
  }

  // --- Create form submit ---
  const handleCreateSubmit = async () => {
    setCreateSaving(true)
    setCreateError(null)
    // Reject a malformed contact/organization name before creating anything.
    // The server enforces the same spec (src/lib/contacts/nameNormalize); this
    // surfaces the "Last, First" / "&"-couple / email-shaped errors immediately.
    const createNameCheck = (createFormData.name || '').trim()
      ? validatePersonNameInput((createFormData.name || '').trim())
      : ((createFormData.company || '').trim() ? validateOrgNameInput((createFormData.company || '').trim()) : null)
    if (createNameCheck && !createNameCheck.ok) {
      setCreateError(createNameCheck.message)
      setCreateSaving(false)
      return
    }
    // Shared helper: resolve the unified_client_id this new entity should
    // belong to. Reuses a matched client if present, otherwise creates one
    // from whatever contact fields are filled in the smart-create panel.
    const resolveUnifiedClientId = async (): Promise<string | undefined> => {
      let id: string | undefined = (createFormData as any)._matched || undefined
      if (id) {
        await persistExtraAddresses(id, createExtraAddresses)
        return id
      }
      const hasContactFields = !!(
        createFormData.name ||
        createFormData.email ||
        createFormData.phone ||
        createFormData.company
      )
      if (!hasContactFields) return undefined
      try {
        const isCompanyOnly = !createFormData.name && !!createFormData.company
        const newClient = await createClient({
          preferredName: (createFormData.name || createFormData.company || '').toString(),
          preferredPhone: createFormData.phone || undefined,
          preferredEmail: createFormData.email || undefined,
          preferredStreet: createFormData.street || undefined,
          preferredCity: createFormData.city || undefined,
          preferredState: createFormData.state || undefined,
          preferredZip: createFormData.zip || undefined,
          community: createFormData.community || undefined,
          clientType: isCompanyOnly ? 'organization' : 'person',
        })
        await persistExtraAddresses(newClient.unified_client_id, createExtraAddresses)
        return newClient.unified_client_id
      } catch (e) {
        console.error('create: client create failed', e)
        return undefined
      }
    }
    try {
      if (showCreateModal === 'contact' || showCreateModal === 'company') {
        const created = await createClient({
          preferredName: createFormData.name || '',
          preferredPhone: createFormData.phone || undefined,
          preferredEmail: createFormData.email || undefined,
          preferredStreet: createFormData.street || undefined,
          preferredCity: createFormData.city || undefined,
          preferredState: createFormData.state || undefined,
          preferredZip: createFormData.zip || undefined,
          community: createFormData.community || undefined,
          clientType: showCreateModal === 'company' ? 'organization' : 'person',
        })
        await persistExtraAddresses(created.unified_client_id, createExtraAddresses)
        setShowCreateModal(null)
        // Navigate to the new client
        if (showCreateModal === 'company') { setActiveView('companies') } else { setActiveView('contacts') }
        // Refresh client list
        fetchClients(1, 50).then((res) => { setClientList(res.clients); setClientTotal(res.total); setClientPages(res.pages) }).catch(() => {})
      } else if (showCreateModal === 'lead') {
        const chosenStatus = createFormData.statusId || createFormData.leadStage
        if (!chosenStatus) {
          alert('Please choose a Status for the lead.')
          return
        }
        const unifiedClientId = await resolveUnifiedClientId()
        await createLead({
          title: createFormData.title || '',
          name: createFormData.name || undefined,
          phone: createFormData.phone || undefined,
          email: createFormData.email || undefined,
          address: createFormData.address || undefined,
          addressCity: createFormData.city || undefined,
          statusId: chosenStatus,
          ownerId: createFormData.ownerId ? Number(createFormData.ownerId) : undefined,
          notes: createFormData.notes || undefined,
          unifiedClientId: unifiedClientId || undefined,
        })
        closeCreatePanel()
        setActiveView('leads')
        fetchLeads().then((res) => { setLiveLeads(res.leads); setLeadTotal(res.total) }).catch(() => {})
      } else if (showCreateModal === 'deal') {
        const unifiedClientId = await resolveUnifiedClientId()
        const newDeal = await createDeal({
          title: createFormData.title || '',
          opportunity: createFormData.opportunity ? Number(createFormData.opportunity) : 0,
          categoryId: 0,
          stageId: createFormData.dealStage || 'APPOINTMENT_SET',
          closeDate: createFormData.closeDate || undefined,
          assignedById: createFormData.ownerId ? Number(createFormData.ownerId) : undefined,
          dealType: (createFormData as any).dealType || undefined,
          isHoa: (createFormData as any).isHoa ? 1 : 0,
          priority: createFormData.priority || undefined,
          comments: createFormData.comments || undefined,
          notes: createFormData.notes || undefined,
          estimateAppointment: createFormData.estimateAppointment || undefined,
          sendConfirmationEmail: createFormData.sendConfirmationEmail !== false,
          unifiedClientId: unifiedClientId || undefined,
        })
        setShowCreateModal(null)
        setDealCategory(0)
        setActiveView('deals')
        setSelectedDeal(newDeal)
        setDealDetailOpen(true)
        window.location.hash = `/deals/record/${newDeal.id}`
        refetchCurrentDeals().then((res) => { setLiveDeals(res.deals); setDealTotal(res.total); setDealPages(res.pages) }).catch(() => {})
        setPipelineRefreshTick(t => t + 1)
        // If an appointment was set, a calendar event was just created server-side —
        // refresh the calendar so it shows up without a manual page reload.
        if (createFormData.estimateAppointment) setCalendarRefreshTick(t => t + 1)
      } else if (showCreateModal === 'task') {
        const title = String(createFormData.title || '').trim()
        if (!title) {
          setCreateError('Task title is required.')
          setCreateSaving(false)
          return
        }
        const taskId = Number((createFormData as any).id || 0)
        const taskStatus = createFormData.status || 'open'
        const cfAny: any = createFormData
        const linkKind = cfAny.linkKind || 'contact'
        const chainLeadId = cfAny.linkLeadId ? String(cfAny.linkLeadId) : ''
        const chainProjectId = cfAny.linkProjectId ? String(cfAny.linkProjectId) : ''
        const chainDealId = cfAny.linkDealId ? String(cfAny.linkDealId) : ''
        const chainContactId = cfAny.linkContactId ? String(cfAny.linkContactId) : ''
        let derivedOwnerType: string | null = null
        let derivedOwnerId: string | null = null
        if (linkKind === 'lead' && chainLeadId) {
          derivedOwnerType = 'lead'; derivedOwnerId = chainLeadId
        } else if (linkKind === 'contact') {
          derivedOwnerType = chainProjectId ? 'project' : chainDealId ? 'deal' : chainContactId ? 'client' : null
          derivedOwnerId = chainProjectId || chainDealId || chainContactId || null
        }
        if (!derivedOwnerType) { derivedOwnerType = cfAny.ownerType || null; derivedOwnerId = cfAny.ownerId || null }
        const taskPayload = {
          title,
          body: createFormData.body || createFormData.description || null,
          status: taskStatus,
          priority: createFormData.priority || 'medium',
          dueDate: createFormData.dueDate || null,
          assignedTo: createFormData.assignedTo ? Number(createFormData.assignedTo) : null,
          ownerType: derivedOwnerType,
          ownerId: derivedOwnerId,
        }
        const savedTask = taskId ? await updateTask(taskId, taskPayload) : await createTask(taskPayload)
        const nextTasksFilter = tasksFilter === 'all' ? 'all' : taskStatus
        setShowCreateModal(null)
        setActiveView('tasks')
        setTasksFilter(nextTasksFilter)
        setTasksPage(1)
        setTaskSelectedRows(new Set())
        setTasksData(prev => taskId ? prev.map(t => (t.id === savedTask.id ? savedTask : t)) : [savedTask, ...prev])
        setTasksTotal(prev => taskId ? prev : prev + 1)
        fetchTasks({ page: 1, limit: tablePageSize, status: nextTasksFilter === 'all' ? undefined : nextTasksFilter })
          .then((res) => { setTasksData(res.tasks); setTasksTotal(res.total) })
          .catch(() => {})
      } else if (showCreateModal === 'ticket') {
        if (!createFormData.category) {
          setCreateError('A ticket type is required.')
          setCreateSaving(false)
          return
        }
        const unifiedClientId = await resolveUnifiedClientId()
        if (!unifiedClientId) {
          setCreateError('A contact is required to create a ticket.')
          setCreateSaving(false)
          return
        }
        await createTicket({
          title: createFormData.title || '',
          description: createFormData.description || undefined,
          status: createFormData.ticketStatus || 'open',
          priority: createFormData.priority || 'medium',
          category: createFormData.category || undefined,
          assignedTo: createFormData.assignedTo || createFormData.ownerId || '8',
          unifiedClientId,
          dealId: createFormData.ticketDealId ? Number(createFormData.ticketDealId) : undefined,
          projectId: createFormData.ticketProjectId || undefined,
        })
        setTicketCreateDeals([])
        setTicketCreateProjects([])
        setShowCreateModal(null)
        setActiveView('tickets')
        setTicketsFilter('all')
        setTicketsPage(1)
      } else if (showCreateModal === 'product') {
        await createProduct({
          name: createFormData.name || '',
          description: createFormData.description || undefined,
          unitPrice: createFormData.unitPrice ? Number(createFormData.unitPrice) : 0,
          unit: createFormData.unit || undefined,
          category: createFormData.category || undefined,
          sku: createFormData.sku || undefined,
        })
        setShowCreateModal(null)
        setActiveView('products')
      } else if (showCreateModal === 'quote') {
        await createQuote({
          title: createFormData.title || '',
          notes: createFormData.notes || undefined,
        })
        setShowCreateModal(null)
        setActiveView('quotes')
      }
      // Successful save — drop the in-progress draft if any.
      if (currentDraftId) {
        persistDrafts(createDrafts.filter(d => d.id !== currentDraftId))
        setCurrentDraftId(null)
      }
      if (smartCreateOpen) closeSmartCreate()
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create')
    } finally {
      setCreateSaving(false)
    }
  }

  // --- Note submit ---
  const handleNoteSubmit = async () => {
    if (!showNoteModal || !noteText.trim()) return
    setNoteSaving(true)
    try {
      await createNote({
        subject: noteText.trim().substring(0, 100),
        description: noteText.trim(),
        ownerTypeId: showNoteModal.ownerTypeId,
        ownerId: showNoteModal.ownerId,
      })
      setShowNoteModal(null)
      setNoteText('')
    } catch (err) {
      console.error('Note creation failed:', err)
    } finally {
      setNoteSaving(false)
    }
  }

  // Preferences dirty tracking
  const updatePref = (key: string, value: string) => {
    setPrefsValues((prev: typeof prefsBaseline) => {
      const next = { ...prev, [key]: value }
      let count = 0
      for (const k of Object.keys(prefsBaselineRef.current)) {
        if (next[k] !== prefsBaselineRef.current[k]) count++
      }
      if (prefsPhoto !== prefsPhotoBaselineRef.current) count++
      setPrefsDirtyCount(count)
      return next
    })
  }

  // File pick now routes through PhotoCropModal (crop/zoom) — the actual
  // upload happens in uploadPrefsPhoto once the user confirms the crop.
  const handlePrefsPhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (file.size > 25 * 1024 * 1024) { setPrefsPhotoError('Image too large (25MB max)'); return }
    setPrefsPhotoError(null)
    setPrefsCropFile(file)
  }

  // Click the CURRENT prefs photo → fetch it back into a File and reopen
  // the crop/zoom modal on it. Uses the stable (non-blob:) URL.
  const handleEditPrefsPhoto = async () => {
    const url = prefsPhoto && !prefsPhoto.startsWith('blob:') ? prefsPhoto : prefsPhotoBaselineRef.current
    if (!url) return
    try {
      setPrefsCropFile(await fileFromImageUrl(url, 'photo'))
    } catch (err: any) {
      setPrefsPhotoError(err?.message || 'Could not load the current photo')
    }
  }

  const uploadPrefsPhoto = async (file: File) => {
    // While impersonating, the upload POSTs to /api/hr/profile/photo which the
    // impersonation middleware resolves to the IMPERSONATED user — so it writes
    // THAT user's photo. Do NOT mirror it into the signed-in admin's prefsPhoto
    // / localStorage (that would clobber the admin's own avatar). Only the
    // impersonated user's `hrProfile` is updated below so the Profile card
    // reflects the new photo immediately.
    const previewUrl = URL.createObjectURL(file)
    if (!impersonating) setPrefsPhoto(previewUrl)
    setPrefsPhotoError(null)
    setPrefsPhotoUploading(true)
    try {
      const uploaded = await uploadHrProfilePhoto(file)
      const nextPhoto = uploaded.photoUrl
      if (!nextPhoto) throw new Error('Profile photo uploaded without a photo URL.')
      if (!impersonating) {
        setPrefsPhoto(nextPhoto)
        prefsPhotoBaselineRef.current = nextPhoto
        persistPrefsPhoto(nextPhoto, currentUser?.email)
      }
      setHrProfile(prev => prev ? { ...prev, profilePhotoKey: uploaded.key, photoUrl: nextPhoto } : prev)
      setHrProfileBaseline(prev => prev ? { ...prev, profilePhotoKey: uploaded.key, photoUrl: nextPhoto } : prev)
      // Versioned me-photo state (navAvatarSource step 2) — the top-right
      // trigger updates INSTANTLY on every route, self AND impersonating
      // (/api/me is impersonation-aware, so currentUser IS the effective user),
      // without any refetch. The prefsPhoto/localStorage writes above stay
      // guarded by !impersonating (display-only invariant).
      setCurrentUser(prev => prev ? { ...prev, photoUrl: nextPhoto, photoVersion: photoVersionFromKey(uploaded.key) || String(Date.now()) } : prev)
      setPrefsDirtyCount(() => {
        let count = 0
        for (const k of Object.keys(prefsBaselineRef.current)) {
          if (prefsValues[k] !== prefsBaselineRef.current[k]) count++
        }
        return count
      })
    } catch (err: any) {
      if (!impersonating) setPrefsPhoto(prefsPhotoBaselineRef.current)
      setPrefsPhotoError(err?.message || 'Could not upload profile photo.')
      throw err
    } finally {
      setPrefsPhotoUploading(false)
      URL.revokeObjectURL(previewUrl)
    }
  }

  const prefsSaveAll = async () => {
    const changedKeys: string[] = []
    for (const k of Object.keys(prefsBaselineRef.current)) {
      if (prefsValues[k] !== prefsBaselineRef.current[k]) changedKeys.push(k)
    }
    const stablePrefsPhoto = prefsPhoto && !prefsPhoto.startsWith('blob:') ? prefsPhoto : prefsPhotoBaselineRef.current
    setPrefsSaving(true)
    let generalOk = true
    let notifOk = true
    try {
      // Persist changed prefs to D1 API
      try {
        await Promise.all(changedKeys.map(k => setMyPreference(k, prefsValues[k])))
        prefsBaselineRef.current = { ...prefsValues }
        prefsPhotoBaselineRef.current = stablePrefsPhoto
        localStorage.setItem('prefsSettings', JSON.stringify(prefsValues))
        persistPrefsPhoto(stablePrefsPhoto || null, currentUser?.email)
        setPrefsDirtyCount(0)
      } catch (e) {
        generalOk = false
        console.error('prefsSaveAll: failed to persist to API', e)
        showCalToast('error', 'Some preferences failed to save — please try again.')
      }
      if (notifPrefsDirtyCountRef.current > 0) {
        setEmailNotifSaving(true)
        setEmailNotifSaveError(null)
        try {
          if (emailNotifChannel !== emailNotifBaselineRef.current) {
            await updateMyNotificationChannel(emailNotifChannel)
            emailNotifBaselineRef.current = emailNotifChannel
            localStorage.setItem('emailNotifChannel', emailNotifChannel)
          }
          if (emailNotifyScope !== emailNotifyScopeBaselineRef.current) {
            await updateMyEmailNotifyScope(emailNotifyScope)
            emailNotifyScopeBaselineRef.current = emailNotifyScope
            localStorage.setItem('emailNotifyScope', emailNotifyScope)
          }
          setNotifPrefsDirtyCount(0)
          setPrefsUnsavedBlocked(false)
          setPrefsUnsavedNudge(false)
        } catch (e: any) {
          notifOk = false
          console.error('prefsSaveAll: failed to persist notification prefs', e)
          setEmailNotifSaveError(e?.message || 'Could not save notification preference.')
        } finally {
          setEmailNotifSaving(false)
        }
      }
      if (generalOk && notifOk) showCalToast('success', 'Preferences saved.')
    } finally {
      setPrefsSaving(false)
    }
  }

  const prefsCancelAll = () => {
    setPrefsValues({ ...prefsBaselineRef.current })
    setPrefsPhoto(prefsPhotoBaselineRef.current)
    setPrefsDirtyCount(0)
    if (notifPrefsDirtyCountRef.current > 0) {
      const savedChannel = emailNotifBaselineRef.current
      setEmailNotifChannel(savedChannel)
      localStorage.setItem('emailNotifChannel', savedChannel)
      const savedScope = emailNotifyScopeBaselineRef.current
      setEmailNotifyScope(savedScope)
      localStorage.setItem('emailNotifyScope', savedScope)
      setNotifPrefsDirtyCount(0)
      setEmailNotifSaveError(null)
      setEmailNotifTestStatus('idle')
      setEmailNotifTestError(null)
    }
    setPrefsUnsavedBlocked(false)
    setPrefsUnsavedNudge(false)
  }

  // HR Profile helpers
  const computeHrDirty = (current: HrProfile | null, baseline: HrProfile | null): number => {
    if (!current || !baseline) return 0
    let count = 0
    const topKeys = ['firstName', 'lastName', 'preferredName', 'phone'] as const
    for (const k of topKeys) {
      if ((current as any)[k] !== (baseline as any)[k]) count++
    }
    const sectionKeys = ['personalInfo', 'emergencyContacts', 'employmentInfo', 'compensationInfo', 'taxInfo', 'bankingInfo', 'educationInfo', 'trainingInfo', 'equipmentInfo', 'licensingInfo', 'onboardingInfo', 'performanceInfo', 'dependents'] as const
    for (const k of sectionKeys) {
      if (JSON.stringify((current as any)[k]) !== JSON.stringify((baseline as any)[k])) count++
    }
    return count
  }

  const updateHrField = (section: string, field: string, value: any) => {
    setHrProfile(prev => {
      if (!prev) return prev
      const next = { ...prev }
      if (section === '_top') {
        ;(next as any)[field] = value
      } else {
        const sectionData = { ...((next as any)[section] || {}) }
        sectionData[field] = value
        ;(next as any)[section] = sectionData
      }
      setHrProfileDirty(computeHrDirty(next, hrProfileBaseline))
      return next
    })
  }

  const updateHrContacts = (index: number, field: string, value: string) => {
    setHrProfile(prev => {
      if (!prev) return prev
      const contacts = [...(prev.emergencyContacts || [{ name: null, phone: null, relationship: null }])]
      while (contacts.length <= index) contacts.push({ name: null, phone: null, relationship: null })
      contacts[index] = { ...contacts[index], [field]: value }
      const next = { ...prev, emergencyContacts: contacts }
      setHrProfileDirty(computeHrDirty(next, hrProfileBaseline))
      return next
    })
  }

  const updateHrDependents = (index: number, field: string, value: string) => {
    setHrProfile(prev => {
      if (!prev) return prev
      const deps = [...(prev.dependents || [])]
      while (deps.length <= index) deps.push({ name: '', relationship: '' })
      deps[index] = { ...deps[index], [field]: value }
      const next = { ...prev, dependents: deps }
      setHrProfileDirty(computeHrDirty(next, hrProfileBaseline))
      return next
    })
  }

  const hrSaveAll = async () => {
    if (!hrProfile || !hrProfileBaseline) return
    setHrProfileSaving(true)
    try {
      const sections: Record<string, any> = {}
      if (hrProfile.firstName !== hrProfileBaseline.firstName) sections.firstName = hrProfile.firstName
      if (hrProfile.lastName !== hrProfileBaseline.lastName) sections.lastName = hrProfile.lastName
      if (hrProfile.preferredName !== hrProfileBaseline.preferredName) sections.preferredName = hrProfile.preferredName
      if (hrProfile.phone !== hrProfileBaseline.phone) sections.phone = hrProfile.phone
      const sectionKeys = ['personalInfo', 'emergencyContacts', 'employmentInfo', 'compensationInfo', 'taxInfo', 'bankingInfo', 'educationInfo', 'trainingInfo', 'equipmentInfo', 'licensingInfo', 'onboardingInfo', 'performanceInfo', 'dependents'] as const
      for (const k of sectionKeys) {
        if (JSON.stringify((hrProfile as any)[k]) !== JSON.stringify((hrProfileBaseline as any)[k])) {
          sections[k] = (hrProfile as any)[k]
        }
      }
      const updated = await updateHrProfile(sections)
      setHrProfile(updated)
      setHrProfileBaseline(JSON.parse(JSON.stringify(updated)))
      setHrProfileDirty(0)
    } catch (e) {
      console.error('HR profile save failed', e)
    }
    setHrProfileSaving(false)
  }

  const hrCancelAll = () => {
    if (hrProfileBaseline) {
      setHrProfile(JSON.parse(JSON.stringify(hrProfileBaseline)))
      setHrProfileDirty(0)
    }
  }

  const toggleHrSection = (section: string) => {
    setHrCollapsed(prev => {
      const next = new Set(prev)
      if (next.has(section)) next.delete(section)
      else next.add(section)
      return next
    })
  }

  const openPrefsTab = (tab: PrefsTab) => {
    if (!setPrefsTab(tab)) return
    setPrefsMobileShowMain(true)
    if (tab === 'users') {
      setUserMgmtMode('list')
      setUserMgmtError('')
    }
  }

  const closePreviewPanel = () => {
    setPreviewClosing(true)
    setTimeout(() => {
      setPreviewContact(null)
      setPreviewClosing(false)
    }, 250)
  }

  const closeClientPreview = () => {
    setPreviewClientClosing(true)
    setDeleteConfirm(null)
    setTimeout(() => {
      setPreviewClient(null)
      setPreviewClientClosing(false)
      setPreviewInvoices([])
      setPreviewDocuments([])
      setPreviewJobs([])
    }, 250)
  }

  // Fetch financial data when a client preview opens
  useEffect(() => {
    if (!previewClient) return
    const id = previewClient.unified_client_id
    setFinancialLoading(true)
    Promise.all([
      fetchClientInvoices(id, { limit: 20 }).catch(() => ({ invoices: [], total: 0 })),
      fetchClientDocuments(id, { limit: 20 }).catch(() => ({ documents: [], total: 0 })),
      fetchClientJobs(id).catch(() => ({ jobs: [], total: 0 })),
    ]).then(([inv, doc, job]) => {
      setPreviewInvoices(inv.invoices)
      setPreviewDocuments(doc.documents)
      setPreviewJobs(job.jobs)
      setFinancialLoading(false)
    })
    listFinancialsByClient(id, { limit: 500 })
      .then(r => {
        const flat = (r.groups ?? []).flatMap(g => g.items)
        setPreviewFinancials({ groups: r.groups ?? [], flat })
      })
      .catch(() => setPreviewFinancials({ groups: [], flat: [] }))
  }, [previewClient?.unified_client_id])

  // Fetch related contacts (duplicate detection) when preview opens
  const [relatedContacts, setRelatedContacts] = useState<RelatedContact[]>([])
  useEffect(() => {
    if (!previewClient) { setRelatedContacts([]); return }
    fetchRelatedContacts(previewClient.unified_client_id)
      .then(setRelatedContacts)
      .catch(() => setRelatedContacts([]))
  }, [previewClient?.unified_client_id])

  // Fetch leads, deals, activities, projects, and attachments when contact detail page opens
  useEffect(() => {
    if (!contactDetailOpen || !previewClient) {
      setContactDetailLeads([])
      setContactDetailDeals([])
      setContactDetailActivities([])
      setContactDetailProjects([])
      setContactDetailJobs([])
      setContactDetailAttachments([])
      setContactDetailLoading(false)
      return
    }
    const id = previewClient.unified_client_id
    let cancelled = false
    setContactDetailLoading(true)
    setContactActFetchLimit(300)
    Promise.all([
      fetchClientLeads(id).catch(() => []),
      fetchClientDeals(id).catch(() => []),
      fetchClientActivities(id).catch(() => []),
      getClientProjects(id).catch(() => []),
      fetchClientJobs(id).catch(() => ({ jobs: [], total: 0 })),
      fetchClientAttachments(id).catch(() => []),
    ]).then(([leads, deals, activities, projects, jobs, attachments]) => {
      if (cancelled) return
      setContactDetailLeads(leads)
      setContactDetailDeals(deals)
      setContactDetailActivities(activities)
      setContactDetailProjects(Array.isArray(projects) ? projects : [])
      setContactDetailJobs(jobs.jobs)
      setContactDetailAttachments(Array.isArray(attachments) ? attachments : [])
      setContactDetailLoading(false)
    })
    return () => { cancelled = true }
  }, [contactDetailOpen, previewClient?.unified_client_id])

  // Org/Contact relationships + roles loaders (Tasks 14 + 15)
  const reloadOrgRelationships = (orgId: string, showPast: boolean) => {
    getOrgRelationships(orgId, showPast).then(r => setOrgRelationships(r)).catch(() => {})
  }
  const reloadOrgPeople = (orgId: string, showPast: boolean) => {
    getOrgPeople(orgId, showPast).then(r => setOrgPeople(r.data || [])).catch(() => {})
  }
  const reloadContactOrgRoles = (contactId: string, showPast: boolean) => {
    getContactOrgRoles(contactId, showPast).then(r => setContactOrgRoles(r.data || [])).catch(() => {})
  }
  useEffect(() => {
    if (!contactDetailOpen || !previewClient) {
      setOrgRelationships(null); setOrgPeople([]); setContactOrgRoles([])
      return
    }
    const id = previewClient.unified_client_id
    if (previewClient.client_type === 'organization') {
      reloadOrgRelationships(id, orgRelShowPast)
      reloadOrgPeople(id, orgPeopleShowPast)
      // Role-type options scoped to this org's sub-type
      listOrgRoleTypes((previewClient as any).org_primary_sub_type || undefined).then(r => setOrgRoleTypeOptions(r.data || [])).catch(() => {})
    } else {
      reloadContactOrgRoles(id, contactRoleShowPast)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactDetailOpen, previewClient?.unified_client_id, previewClient?.client_type, orgRelShowPast, orgPeopleShowPast, contactRoleShowPast])

  // Picker searches for the org/contact appoint/relationship modals
  useEffect(() => {
    if (!orgRelModalOpen || relOrgSearch.trim().length < 2) { setRelOrgResults([]); return }
    let cancelled = false
    const t = setTimeout(() => {
      fetchClients(1, 8, { search: relOrgSearch.trim(), type: 'organization' })
        .then(res => { if (!cancelled) setRelOrgResults(res.clients.filter(c => c.unified_client_id !== previewClient?.unified_client_id)) })
        .catch(() => {})
    }, 300)
    return () => { cancelled = true; clearTimeout(t) }
  }, [relOrgSearch, orgRelModalOpen, previewClient?.unified_client_id])
  useEffect(() => {
    if (!orgRoleModalOpen || appointSearch.trim().length < 2) { setAppointResults([]); return }
    let cancelled = false
    const t = setTimeout(() => {
      fetchClients(1, 8, { search: appointSearch.trim(), type: 'person' })
        .then(res => { if (!cancelled) setAppointResults(res.clients) })
        .catch(() => {})
    }, 300)
    return () => { cancelled = true; clearTimeout(t) }
  }, [appointSearch, orgRoleModalOpen])
  // Organization Owner contact picker search (people only)
  useEffect(() => {
    if (!ownerContactPickerOpen || ownerContactSearch.trim().length < 2) { setOwnerContactResults([]); return }
    let cancelled = false
    const t = setTimeout(() => {
      fetchClients(1, 8, { search: ownerContactSearch.trim(), type: 'person' })
        .then(res => { if (!cancelled) setOwnerContactResults(res.clients) })
        .catch(() => {})
    }, 300)
    return () => { cancelled = true; clearTimeout(t) }
  }, [ownerContactSearch, ownerContactPickerOpen])
  useEffect(() => {
    if (!appointOrgModalOpen || appointOrgSearch.trim().length < 2) { setAppointOrgResults([]); return }
    let cancelled = false
    const t = setTimeout(() => {
      fetchClients(1, 8, { search: appointOrgSearch.trim(), type: 'organization' })
        .then(res => { if (!cancelled) setAppointOrgResults(res.clients) })
        .catch(() => {})
    }, 300)
    return () => { cancelled = true; clearTimeout(t) }
  }, [appointOrgSearch, appointOrgModalOpen])
  // When picking an org in the contact-side appoint modal, scope role options to its sub-type
  useEffect(() => {
    if (!appointOrgModalOpen) return
    listOrgRoleTypes(appointOrgForm.org_subtype || undefined).then(r => setOrgRoleTypeOptions(r.data || [])).catch(() => {})
  }, [appointOrgModalOpen, appointOrgForm.org_subtype])

  // Job billing-company history loader (Task 16)
  useEffect(() => {
    if (!jcSelectedJob?.id) { setJobBillingPeriods(null); return }
    let cancelled = false
    getJobBillingPeriods(Number(jcSelectedJob.id)).then(r => { if (!cancelled) setJobBillingPeriods(r) }).catch(() => {})
    return () => { cancelled = true }
  }, [jcSelectedJob?.id])
  useEffect(() => {
    if (!billingChangeModalOpen || billingOrgSearch.trim().length < 2) { setBillingOrgResults([]); return }
    let cancelled = false
    const t = setTimeout(() => {
      fetchClients(1, 8, { search: billingOrgSearch.trim(), type: 'organization' })
        .then(res => { if (!cancelled) setBillingOrgResults(res.clients) })
        .catch(() => {})
    }, 300)
    return () => { cancelled = true; clearTimeout(t) }
  }, [billingOrgSearch, billingChangeModalOpen])

  const toggleClientSort = (field: string) => {
    setClientSort((prev) => prev.field === field
      ? { field, order: prev.order === 'ASC' ? 'DESC' : 'ASC' }
      : { field, order: 'ASC' },
    )
  }

  const toggleClientRow = (id: string) => {
    setClientSelectedRows((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const toggleAllClientRows = () => {
    if (clientSelectedRows.size === clientList.length) {
      setClientSelectedRows(new Set())
    } else {
      setClientSelectedRows(new Set(clientList.map((c) => c.unified_client_id)))
    }
  }

  const closeCompanyPreview = () => {
    setPreviewCompanyClosing(true)
    setTimeout(() => {
      setPreviewCompany(null)
      setPreviewCompanyClosing(false)
      setCompanyInvoices([])
      setCompanyDocuments([])
      setCompanyJobs([])
    }, 250)
  }

  // Fetch financial data when a company preview opens
  useEffect(() => {
    if (!previewCompany) return
    const id = previewCompany.unified_client_id
    setCompanyFinLoading(true)
    Promise.all([
      fetchClientInvoices(id, { limit: 20 }).catch(() => ({ invoices: [], total: 0 })),
      fetchClientDocuments(id, { limit: 20 }).catch(() => ({ documents: [], total: 0 })),
      fetchClientJobs(id).catch(() => ({ jobs: [], total: 0 })),
    ]).then(([inv, doc, job]) => {
      setCompanyInvoices(inv.invoices)
      setCompanyDocuments(doc.documents)
      setCompanyJobs(job.jobs)
      setCompanyFinLoading(false)
    })
    listFinancialsByClient(id, { limit: 500 })
      .then(r => {
        const flat = (r.groups ?? []).flatMap(g => g.items)
        setPreviewFinancials({ groups: r.groups ?? [], flat })
      })
      .catch(() => setPreviewFinancials({ groups: [], flat: [] }))
  }, [previewCompany?.unified_client_id])

  const toggleCompanySort = (field: string) => {
    setCompanySort((prev) => prev.field === field
      ? { field, order: prev.order === 'ASC' ? 'DESC' : 'ASC' }
      : { field, order: 'ASC' },
    )
  }

  const toggleCompanyRow = (id: string) => {
    setCompanySelectedRows((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const toggleAllCompanyRows = () => {
    if (companySelectedRows.size === companyList.length) {
      setCompanySelectedRows(new Set())
    } else {
      setCompanySelectedRows(new Set(companyList.map((c) => c.unified_client_id)))
    }
  }

  // Lead helpers
  const toggleLeadSort = (field: string) => {
    setLeadSort((prev) => prev.field === field
      ? { field, order: prev.order === 'ASC' ? 'DESC' : 'ASC' }
      : { field, order: field === 'title' || field === 'customer_name' ? 'ASC' : 'DESC' },
    )
  }

  const toggleLeadRow = (id: string) => {
    setLeadSelectedRows((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const toggleAllLeadRows = () => {
    if (leadSelectedRows.size === leadList.length) {
      setLeadSelectedRows(new Set())
    } else {
      setLeadSelectedRows(new Set(leadList.map((l) => l.id)))
    }
  }

  const toggleAllDealRows = () => {
    if (dealSelectedRows.size === filteredDeals.length) setDealSelectedRows(new Set())
    else setDealSelectedRows(new Set(filteredDeals.map(d => String(d.id))))
  }
  const toggleDealRow = (id: string) => {
    setDealSelectedRows(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })
  }

  const closeLeadPreview = () => {
    if (leadRoutingConfig.require_note_on_touch === 1 && !previewLeadNoteLoggedRef.current) {
      if (!window.confirm('No note was logged for this lead. Close without adding a note?')) return
    }
    previewLeadNoteLoggedRef.current = false
    setPreviewLeadClosing(true)
    setTimeout(() => {
      setPreviewLead(null)
      setPreviewLeadClosing(false)
    }, 250)
  }

  // Ticket helpers
  const toggleTicketSort = (field: string) => {
    setTicketsSort((prev) => prev.field === field
      ? { field, order: prev.order === 'ASC' ? 'DESC' : 'ASC' }
      : { field, order: field === 'title' ? 'ASC' : 'DESC' },
    )
  }

  const toggleTicketRow = (id: number) => {
    setTicketSelectedRows((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const toggleAllTicketRows = () => {
    if (ticketSelectedRows.size === ticketsData.length) {
      setTicketSelectedRows(new Set())
    } else {
      setTicketSelectedRows(new Set(ticketsData.map((t) => t.id)))
    }
  }

  const closeTicketPreview = () => {
    setPreviewTicketClosing(true)
    setTimeout(() => {
      setPreviewTicket(null)
      setPreviewTicketClosing(false)
    }, 250)
  }

  const TICKET_STATUS_COLORS: Record<string, string> = {
    open: '#2FC6F6', new: '#2FC6F6', in_progress: '#47B1E0', waiting: '#FFA726',
    escalated: '#EF5350', resolved: '#66BB6A', closed: '#BDBDBD',
  }

  const TICKET_PRIORITY_COLORS: Record<string, string> = {
    low: '#94a3b8', medium: '#f59e0b', high: '#ef4444', urgent: '#dc2626',
  }

  // Call helpers
  const toggleCallSort = (field: string) => {
    setCallsSort((prev) => prev.field === field
      ? { field, order: prev.order === 'ASC' ? 'DESC' : 'ASC' }
      : { field, order: field === 'subject' ? 'ASC' : 'DESC' },
    )
  }

  const onCallResizeStart = (key: string, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    resizingCallCol.current = { key, startX: e.clientX, startWidth: callColWidths[key] ?? 120 }
    const onMouseMove = (ev: MouseEvent) => {
      // TRAP: snapshot the drag ref BEFORE building the setState updater.
      // Queued functional updaters replay inside React's useState machinery
      // on the NEXT render — after mouseup may have nulled the ref. Re-reading
      // the ref with a non-null assertion inside the updater was the
      // 2026-07-01 #/calls blue-screen (TypeError: null.key). Guarded by
      // src/lib/calls/colWidths.test.ts (source scan).
      const drag = resizingCallCol.current
      if (!drag) return
      const delta = ev.clientX - drag.startX
      const newWidth = Math.max(60, drag.startWidth + delta)
      setCallColWidths((prev) => ({ ...prev, [drag.key]: newWidth }))
    }
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
      resizingCallCol.current = null
      const swallow = (ev: MouseEvent) => { ev.preventDefault(); ev.stopPropagation(); document.removeEventListener('click', swallow, true) }
      document.addEventListener('click', swallow, true)
      setTimeout(() => document.removeEventListener('click', swallow, true), 200)
    }
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }

  const toggleCallRow = (id: number) => {
    setCallSelectedRows((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const toggleAllCallRows = () => {
    if (callSelectedRows.size === callsData.length) {
      setCallSelectedRows(new Set())
    } else {
      setCallSelectedRows(new Set(callsData.map((c) => c.id)))
    }
  }

  const toggleMeetSort = (field: 'start_time' | 'subject' | 'created') => {
    setMeetsSort((prev) => prev.field === field
      ? { field, order: prev.order === 'ASC' ? 'DESC' : 'ASC' }
      : { field, order: field === 'subject' ? 'ASC' : 'DESC' },
    )
    setMeetsPage(1)
  }

  const toggleMeetingRow = (id: number) => {
    setMeetsSelectedRows((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const toggleAllMeetingRows = () => {
    if (meetsSelectedRows.size === meetsData.length) setMeetsSelectedRows(new Set())
    else setMeetsSelectedRows(new Set(meetsData.map((meeting) => meeting.id)))
  }

  const closeCallPreview = () => {
    setPreviewCallClosing(true)
    setTimeout(() => {
      setPreviewCall(null)
      setPreviewCallClosing(false)
    }, 250)
  }

  /* const formatCallDuration = (settings: string | null): string => {
    if (!settings) return '--'
    try {
      const s = JSON.parse(settings)
      const sec = Number(s.duration_seconds)
      if (!sec || sec <= 0) return '--'
      if (sec < 60) return `${sec}s`
      const min = Math.floor(sec / 60)
      const rem = sec % 60
      if (min >= 60) return `${Math.floor(min / 60)}h ${min % 60}m`
      return rem > 0 ? `${min}m ${rem}s` : `${min}m`
    } catch { return '--' }
  } */

  const CALL_DIRECTION_LABELS: Record<number, string> = { 1: 'Outbound', 2: 'Inbound' }
  const CALL_DIRECTION_COLORS: Record<number, string> = { 1: '#3b82f6', 2: '#22c55e' }

  // Format duration from duration_seconds field directly (Tier 2)
  const formatDurationSecs = (secs: number | null): string => {
    if (!secs || secs <= 0) return '--'
    if (secs < 60) return `${secs}s`
    const min = Math.floor(secs / 60)
    const rem = secs % 60
    if (min >= 60) return `${Math.floor(min / 60)}h ${min % 60}m`
    return rem > 0 ? `${min}m ${rem}s` : `${min}m`
  }

  // Get outcome color for a call outcome label (Tier 2)
  const getOutcomeColor = (label: string | null): string => {
    if (!label) return '#94a3b8'
    const found = rcOutcomes.find((o) => o.label.toLowerCase() === label.toLowerCase())
    return found?.color || '#94a3b8'
  }

  // Sentiment helpers (Tier 5). Colors back the badge background; the
  // SentimentIcon below renders a 10px SVG inside the badge. We used to
  // ship 👍 / ➖ / 👎 emoji here; emoji in a dense CRM table reads as
  // AI-generated, so the icons were switched to path-based SVG to match
  // the rest of the view.
  const SENTIMENT_COLORS: Record<string, string> = { positive: '#22c55e', neutral: '#94a3b8', negative: '#ef4444' }
  const SentimentIcon: React.FC<{ kind: string | null | undefined }> = ({ kind }) => {
    const common = { width: 10, height: 10, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const }
    if (kind === 'positive') return (<svg {...common}><path d="M14 9V5a3 3 0 0 0-6 0v4H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h11a3 3 0 0 0 3-3v-4a3 3 0 0 0-3-3h-2Z"/></svg>)
    if (kind === 'negative') return (<svg {...common}><path d="M10 15v4a3 3 0 0 0 6 0v-4h3a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2H8a3 3 0 0 0-3 3v4a3 3 0 0 0 3 3h2Z"/></svg>)
    return (<svg {...common}><line x1="5" y1="12" x2="19" y2="12"/></svg>)
  }

  // `currentUserId` is declared near the top of App() (see note there).
  // The server re-derives userId from the CF Access JWT on every RC endpoint,
  // so the UI value is only used to gate local interactions (which RC phone
  // mapping applies, which employee's calendar to show, etc.).
  const currentUserHasRc = rcMappings.some((m) => m.user_id === currentUserId)
  const rcClickEnabled = rcConfig['rc_click_to_call'] === '1' || rcConfig['rc_click_to_call'] === 'true'
  const showClickToCall = rcClickEnabled && currentUserHasRc

  // Calendar CRUD permissions
  const ADMIN_USER_IDS = new Set([8, 35, 42])
  const ESTIMATOR_USER_IDS = new Set([49, 41, 73, 48])
  // Hardcoded ID list predates the DB-driven role system — any super admin
  // also gets full calendar management (owner select, reassignment, delete).
  const isCalendarAdmin = ADMIN_USER_IDS.has(currentUserId) || effectiveCanonicalRole === 'super_admin'
  // Office-level users can edit any calendar event (matches the backend
  // canWriteCalendarOrSuperAdmin office allowance on PUT). Delete stays
  // admin/estimator-only — office gets edit, not delete.
  const isCalendarOffice = effectiveCanonicalRole === 'office'
  const isCalendarEstimator = ESTIMATOR_USER_IDS.has(currentUserId)
  // Admin-configurable calendar capabilities (matrix-backed). Layered as an
  // AND on top of the existing role logic: defaults preserve current behavior,
  // but a super admin can switch a capability off per role in settings.
  const calCapAdd = myCapabilities ? myCapabilities.calendar_add_event !== false : true
  const calCapEdit = myCapabilities ? myCapabilities.calendar_edit_event !== false : true
  const calCapDelete = myCapabilities ? myCapabilities.calendar_delete_event !== false : true
  const canCreateCalendarEvent = (isCalendarAdmin || isCalendarEstimator) && calCapAdd
  const allowedCalendarOwners = CALENDAR_USERS_RAW.filter(u => isCalendarAdmin ? true : u.userId === currentUserId)
  const openCreateCalendarEvent = (dateKey = selectedDate) => {
    // No default host and no pre-selected time: the user must click a slot in
    // the availability grid, which sets BOTH the host (estimatorId) and the
    // time via pickSlot. Keeps the date for grid navigation but leaves time
    // empty so nothing defaults to Maxwell / 09:00.
    setCalEventForm({ ownerUserId: null as number | null, eventName: '', clientName: '', address: '', date: dateKey, time: '', durationMinutes: 60, attendeeIds: [], notes: '', clientId: null, clientEmail: null, inviteClient: false })
    setCalEventType('appointment')
    setHolForm({ name: '', start_date: dateKey || todayKey, end_date: dateKey || todayKey, event_type: 'holiday', is_all_day: true, start_time: '09:00', end_time: '12:00', recurrence_freq: 'none', recurrence_end_date: '' })
    setHolError(null)
    setCalEventModal({ mode: 'create' })
    setCalEventError(null)
    setCalEventDeleteConfirm(false)
  }

  // ── Time-off / holiday role flags + loaders ──
  // Approver = admin or super-admin tier (matches backend canonicalRole gate on
  // /api/time-off approve/deny). isSuperAdmin = super_admin tier only (holiday
  // direct-create + holiday approval).
  const isCalendarApprover = effectiveCanonicalRole === 'super_admin' || effectiveCanonicalRole === 'admin'
  const isSuperAdmin = effectiveCanonicalRole === 'super_admin'

  const loadTimeOff = useCallback(async () => {
    try {
      const res = await listTimeOffRequests(isCalendarApprover ? { scope: 'all' } : { scope: 'mine' })
      setTimeOffRequests(res.data ?? [])
    } catch { /* leave prior list on transient error */ }
  }, [isCalendarApprover])

  const loadHolidays = useCallback(async () => {
    if (!isCalendarApprover) { setPendingHolidays([]); return }
    try {
      const res = await listHolidays('pending')
      setPendingHolidays(res.data ?? [])
    } catch { /* leave prior list on transient error */ }
  }, [isCalendarApprover])

  const loadRecurringEvents = useCallback(async () => {
    if (!isCalendarApprover) return
    setRecurringEventsLoading(true)
    try {
      const res = await listRecurringEvents()
      setRecurringEvents(res.data ?? [])
    } catch { /* best-effort */ } finally { setRecurringEventsLoading(false) }
  }, [isCalendarApprover])

  // Pending items the current user can action: pending time-off (any approver)
  // + pending holiday requests (super-admin only).
  const pendingTimeOffCount = isCalendarApprover ? timeOffRequests.filter(r => r.status === 'pending').length : 0
  const pendingHolidayCount = isSuperAdmin ? pendingHolidays.filter(h => h.status === 'pending').length : 0
  const approvalsBadgeCount = pendingTimeOffCount + pendingHolidayCount

  // Initial load when entering the calendar view OR the command/home view
  // (the command work-queue surfaces a "Pending time-off" count that needs
  // a fresh load even when the user never opens the calendar).
  useEffect(() => {
    if (activeView !== 'calendar' && activeView !== 'command') return
    loadTimeOff()
    loadHolidays()
    loadRecurringEvents()
  }, [activeView, loadTimeOff, loadHolidays, loadRecurringEvents])

  useEffect(() => {
    if (activeView === 'preferences' && prefsTab === 'company-events') {
      loadRecurringEvents()
    }
  }, [activeView, prefsTab, loadRecurringEvents])

  // Visible-only approver-badge poller — runs on the calendar view and on the
  // command/home view so the work-queue card stays fresh.
  useEffect(() => {
    if ((activeView !== 'calendar' && activeView !== 'command') || !isCalendarApprover) return
    return startManagedInterval({
      name: 'calendar-approvals',
      intervalMs: 60_000,
      run: async () => { await loadTimeOff(); await loadHolidays() },
    })
  }, [activeView, isCalendarApprover, loadTimeOff, loadHolidays])

  function parseDurationToMinutes(dur: string): number {
    if (!dur || dur === 'All day') return 60
    const h = dur.match(/(\d+)h/); const m = dur.match(/(\d+)m/)
    return (h ? parseInt(h[1]) * 60 : 0) + (m ? parseInt(m[1]) : 0) || 60
  }

  // === PHONE: ENGINE — RINGOUT === rings the user's PHYSICAL desk phone via RC RingOut; placed with
  // the user's OWN RC token (CMN-419: a shared app cannot dial for another extension). RingOut/desk
  // calls never enter the WebRTC callingState machine — the desk mirror owns their UI.
  // TRAP(phone/ringout-grace): the 'ringout:' sessionId prefix grants the optimistic desk mirror a 30s null-poll grace (deskMirrorPlacedAtRef) — rename it and the mirror tears down mid-ring.
  // Initiate click-to-call (Tier 4)
  // Place an outbound call via RingOut — RC rings the user's PHYSICAL desk phone
  // first, then bridges to the number. No browser mic needed. Shared by
  // deskphone-mode click-to-call, the keypad in deskphone mode, and the
  // no-microphone fallback from the WebRTC softphone path.
  const beginRingOut = (toNumber: string, dealId?: number, contactId?: string, contactName?: string) => {
    ringoutPollRef.current?.()
    ringoutPollRef.current = null
    // Seed the full-screen desk-call mirror the instant the user DIALS — not
    // a network round-trip later when RC accepts the RingOut — so the keypad
    // flips straight to the outgoing-call screen. The sessionId is tagged
    // "ringout:" so the webhook-fed /active-call poll (8s) treats this row as
    // optimistic (grace window instead of being wiped before the webhook
    // lands); .then upgrades the placeholder id, .catch tears the mirror down.
    deskMirrorPlacedAtRef.current = Date.now()
    deskMirrorDismissedRef.current = null // fresh dial — don't suppress this one
    // startTime from placement so the mirror's timer counts immediately
    // ("Calling… 0:05"), identical to the softphone screen; the webhook row
    // replaces it with RC's authoritative started_at when it lands.
    setDeskActiveCall({ direction: 'Outbound', phoneNumber: toNumber, name: contactName || null, startTime: new Date().toISOString(), sessionId: 'ringout:pending', result: 'Ringing' })
    startRingOut({ userId: currentUserId, toNumber, dealId, contactId })
      .then((res) => {
        setRingout({ ringoutId: res.ringoutId, status: res.status, callerStatus: 'Ringing', calleeStatus: 'Ringing', toNumber, contactName })
        setDeskActiveCall((prev) => prev && prev.sessionId.startsWith('ringout:') ? { ...prev, sessionId: `ringout:${res.ringoutId}` } : prev)
        ringoutPollRef.current = startManagedInterval({
          name: 'ringout-status',
          intervalMs: 2_000,
          run: async () => {
            const st = await getRingOutStatus(res.ringoutId)
            setRingout((prev) => prev ? { ...prev, status: st.status, callerStatus: st.callerStatus, calleeStatus: st.calleeStatus } : null)
            if (st.calleeStatus === 'Success') {
              // Callee answered — flip the optimistic mirror to connected and
              // start its timer. A real webhook row replaces this when it lands.
              setDeskActiveCall((prev) => prev && prev.sessionId.startsWith('ringout:')
                ? { ...prev, result: 'Answered', startTime: prev.startTime || new Date().toISOString() }
                : prev)
            }
            if (st.status === 'Disconnected' || st.status === 'NoAnswer' || st.status === 'Busy' || st.status === 'Error') {
              ringoutPollRef.current?.()
              ringoutPollRef.current = null
              setTimeout(() => setRingout(null), 3000)
              // Tear down the optimistic mirror too; a real webhook-fed row
              // (if one arrived) is owned by the /active-call poll instead.
              setDeskActiveCall((prev) => prev && prev.sessionId.startsWith('ringout:') ? null : prev)
            }
          },
        })
      })
      .catch((err: any) => {
        console.error('RingOut error:', err)
        // RingOut never started — tear down the optimistic mirror.
        setDeskActiveCall((prev) => prev && prev.sessionId.startsWith('ringout:') ? null : prev)
        if (err?.status === 409 || err?.code === 'RC_CONNECT_REQUIRED') {
          setRingout({ ringoutId: '', status: 'ConnectRequired', callerStatus: '', calleeStatus: '', toNumber, contactName: contactName || toNumber })
          setTimeout(() => setRingout(null), 8000)
        }
      })
  }

  // End a desk-routed (RingOut) call from the CRM. The audio lives on the
  // physical phone, but the user needs a way to clear the mirror — especially
  // a mis-dial or a RingOut that never resolves. Tear down the LOCAL mirror +
  // poller immediately (so the screen always clears), then best-effort cancel
  // the RingOut at RC. The ringout id can come from `ringout` state or, if that
  // hasn't populated, from the optimistic mirror's `ringout:<id>` sessionId.
  const endDeskMirrorCall = async () => {
    const sid = deskActiveCall?.sessionId || ''
    const ridFromSession = sid.startsWith('ringout:') && sid !== 'ringout:pending' ? sid.slice('ringout:'.length) : ''
    const rid = ringout?.ringoutId || ridFromSession
    // Remember the dismissed call so the 8s active-call poll won't re-seed the
    // mirror from the still-live rc_live_calls row (which made End look dead).
    deskMirrorDismissedRef.current = { phone: (deskActiveCall?.phoneNumber || '').replace(/\D/g, '').slice(-10), at: Date.now() }
    ringoutPollRef.current?.()
    ringoutPollRef.current = null
    setRingout(null)
    setDeskActiveCall(null)
    if (rid) { try { await cancelRingOut(rid) } catch { /* best-effort; mirror is already cleared */ } }
  }

  // === PHONE: ENGINE — CLICK-TO-CALL ENTRY === the CRM→phone seam: seeds pendingCallContextRef +
  // prefix-typed activeCallContact ids (lead-/deal-/contact-/client-, resolved by
  // src/lib/phone/callRouting.ts) so saveCallReview files against the right owner. Per-user
  // callingMode beats the global click-to-call default.
  // TRAP(phone/rcconfig-lazy): showClickToCall stays false until the Preferences view loads rcConfig — eager-loading rcConfig elsewhere CHANGES dial behavior.
  const handleClickToCall = (toNumber: string, dealId?: number, contactId?: string, contactName?: string, leadId?: number) => {
    // Stash context so the WebRTC call-start activity row links back to
    // the originating lead/deal/contact. Cleared after consumption.
    pendingCallContextRef.current = { leadId, dealId, contactId }
    // Also seed activeCallContact so the post-call review (notes +
    // outcome) gets saved against the right owner. Without this,
    // saveCallReview's `if (activeCallContact?.id)` gate skips the
    // note entirely when a call is initiated from a lead/deal preview.
    if (leadId) setActiveCallContact({ name: contactName || toNumber, phone: toNumber, id: `lead-${leadId}` })
    else if (dealId) setActiveCallContact({ name: contactName || toNumber, phone: toNumber, id: `deal-${dealId}` })
    else if (contactId) setActiveCallContact({ name: contactName || toNumber, phone: toNumber, id: `contact-${contactId}` })
    // OUTBOUND DEFAULTS TO THE SOFTPHONE (2026-06-17): route every click-to-call
    // through the in-browser panel → startCallingPanelCall, which owns the RingOut
    // fallbacks (impersonation = forced; softphone-can't-register / no-mic = auto).
    // Only impersonation skips the panel here, so beginRingOut keeps the
    // lead/deal/contact context. Per-user callingMode no longer routes OUTBOUND to
    // the desk phone — it still governs INBOUND ringing (deskphone/notify).
    const useSoftphonePanel = !impersonating
    if (useSoftphonePanel) {
      // Open calling panel on keypad tab with number pre-filled, keep dock state as-is
      const digits = toNumber.replace(/\D/g, '').replace(/^1(\d{10})$/, '$1')
      setDialerInput(digits)
      setCallingPanelTab('keypad')
      if (!callingPanelOpen) setCallingPanelOpen(true)
      // Auto-initiate the call after state settles
      setTimeout(() => { if (startCallRef.current) startCallRef.current() }, 300)
      return
    }
    beginRingOut(toNumber, dealId, contactId, contactName)
  }

  // --- Calling Panel helpers ---
  // Worker-aligned: /api/crm/rc/* lookups all require is_active = 1, so an
  // inactive rc_user_mappings row must read as "no mapping" here too —
  // otherwise the client attempts SIP provisioning and gets a deterministic
  // 404 ("No active RC extension mapping for this user").
  const callingUserMapping = rcMappings.find((m) => m.user_id === currentUserId && m.is_active)

  // Phase 2 — per-user Calling Mode gates whether the in-browser softphone
  // AUTO-registers for inbound. 'softphone'/'smart' register (calls ring here);
  // 'deskphone'/'notify' skip auto-registration so RC rings the desk phone /
  // only a CRM notification fires. (Outbound still inits on-demand in
  // startCallingPanelCall, so this never blocks placing a call.) Loaded from
  // /api/me; until it loads we hold off auto-registering to avoid a deskphone
  // user briefly registering on a load race.
  const [callingMode, setCallingMode] = useState<string>('smart')
  const [callingModeLoaded, setCallingModeLoaded] = useState(false)
  // RC linkage state from /api/me — drives auto-Connect-on-sign-in.
  const [rcSelfMapped, setRcSelfMapped] = useState(false)
  const [rcConnected, setRcConnected] = useState(false)
  const [rcOauthConfigured, setRcOauthConfigured] = useState(false)
  const [rcExtension, setRcExtension] = useState<string | null>(null)
  const [rcConnectFlash, setRcConnectFlash] = useState<{ ok: boolean; reason?: string } | null>(null)
  useEffect(() => {
    let cancelled = false
    const RETRIES = 2
    const applyMe = (me: any) => {
      const cm = String(me?.callingMode || 'smart').toLowerCase()
      if (['smart', 'softphone', 'deskphone', 'notify'].includes(cm)) setCallingMode(cm)
      setRcSelfMapped(me?.rcMapped === true)
      setRcConnected(me?.rcConnected === true)
      setRcOauthConfigured(me?.rcOauthConfigured === true)
      setRcExtension(me?.rcExtension != null ? String(me.rcExtension) : null)
    }
    const attempt = async (triesLeft: number): Promise<void> => {
      let httpOk = false
      let body: any = null
      try {
        const r = await fetch('/api/me', { credentials: 'include' })
        httpOk = r.ok
        body = await r.json().catch(() => null)
      } catch { httpOk = false; body = null }
      if (cancelled) return
      // Same resilience as the identity bootstrap: a transient non-OK / parse
      // blip must NOT default rcSelfMapped to false (that is the phone
      // "Set up a number" regression). Only a real authed body (or an
      // explicit signed-out) is trusted; otherwise retry, then PRESERVE the
      // current phone state. Reuses resolveMeResponse for the trust decision.
      const decision = resolveMeResponse(null, httpOk, body)
      if (decision.action === 'set') {
        applyMe(body)
        setCallingModeLoaded(true)
        return
      }
      if (decision.action === 'signed-out') {
        // Genuine sign-out: leave phone flags at defaults; <Root> routes to login.
        setCallingModeLoaded(true)
        return
      }
      // preserve: retry a transient blip, then leave RC state untouched.
      if (triesLeft > 0) {
        await new Promise(res => setTimeout(res, (RETRIES - triesLeft + 1) * 400))
        if (!cancelled) return attempt(triesLeft - 1)
      }
      if (!cancelled) setCallingModeLoaded(true)
    }
    attempt(RETRIES)
    return () => { cancelled = true }
  }, [])

  // === PHONE: capability-gated home-screen dock (PHONE_ARCHITECTURE §6.5) ===
  // The docked phone is a multi-app DEVICE; its home dock + calling-specific home
  // content render ONLY the RingCentral apps the current user actually has.
  //  - voiceCapable: has an active RC extension — the SAME signal that gates the
  //    "No phone number set up" empty state (callingUserMapping || rcSelfMapped).
  //    Drives the Phone icon + the home Favorites/speed-dial grid.
  //  - smsCapable: PROXY — gated on the SAME RC-extension-presence signal as voice.
  //    rc_user_mappings has no per-extension feature flags, so true SMS-only gating
  //    needs RingCentral's extension `features.permissions.sms` synced per user
  //    (see PHONE_ARCHITECTURE §6.5 / risk note). Until then SMS == voice.
  const voiceCapable = Boolean(callingUserMapping) || rcSelfMapped
  // smsCapable: proxy for voiceCapable until per-extension RC SMS features are synced.
  const smsCapable = voiceCapable
  const hasPhoneIcon = voiceCapable
  const hasSmsIcon = smsCapable
  const hasCallingHomeContent = voiceCapable

  // Auto-link RingCentral on sign-in ("simultaneous SSO"). The CRM session was
  // just established through Entra, and RingCentral SSO federates to the SAME
  // tenant — so one redirect hop (RC login → IdP, silent against the warm
  // Entra session → back here) connects the user's own RC account with zero
  // typing. The authorize URL steers RC's hosted login straight onto its SSO
  // route (prompt=sso + login_hint server-side; the #/ssoLogin fragment below
  // rides redirect fragment-inheritance) — landing on RC's password page is
  // what made the pre-2026-06-05 version feel jarring. Re-enabled 2026-06-09:
  // the product decision is per-user Connect (the phone app now gates on it).
  // Guards: mapped-but-unconnected users only, never while impersonating, never
  // on the OAuth bounce-back load, and at most one attempt per 24h
  // (localStorage) so a failing connect can't redirect-loop the app. The
  // Connect gate/buttons remain as the manual path.
  useEffect(() => {
    if (!callingModeLoaded || !rcOauthConfigured || !rcSelfMapped || rcConnected) return
    if (impersonating) return
    const hash = window.location.hash || ''
    if (/[?&]rc=(connected|error)/.test(hash)) return
    try {
      const last = Number(localStorage.getItem('rcAutoConnectAt') || 0)
      if (Date.now() - last < 24 * 60 * 60 * 1000) return
      localStorage.setItem('rcAutoConnectAt', String(Date.now()))
    } catch { return /* storage unavailable — leave Connect manual */ }
    window.location.assign('/api/crm/rc/oauth/authorize#/ssoLogin')
  }, [callingModeLoaded, rcOauthConfigured, rcSelfMapped, rcConnected, impersonating])

  // Reflect the result of a Connect bounce (manual or auto) and strip the ?rc=
  // marker so it can't re-trigger. Success -> mark connected; error -> leave the
  // manual Connect button (Settings -> Calling) as the fallback.
  useEffect(() => {
    const hash = window.location.hash || ''
    const m = hash.match(/[?&]rc=(connected|error)/)
    if (!m) return
    const reasonM = hash.match(/[?&]reason=([^&]*)/)
    const reason = reasonM ? decodeURIComponent(reasonM[1]) : undefined
    if (m[1] === 'connected') { setRcConnected(true); setRcConnectFlash({ ok: true }) }
    else setRcConnectFlash({ ok: false, reason })
    const cleaned = hash.replace(/[?&]rc=(connected|error)(&reason=[^&]*)?/g, '')
    try { history.replaceState(null, '', window.location.pathname + window.location.search + cleaned) } catch { /* ignore */ }
  }, [])
  // Auto-dismiss a SUCCESS flash; leave errors up until the user dismisses them
  // (they're usually an admin-fixable config issue worth reading).
  useEffect(() => {
    if (!rcConnectFlash?.ok) return
    const t = setTimeout(() => setRcConnectFlash(null), 6000)
    return () => clearTimeout(t)
  }, [rcConnectFlash])

  const softphoneModeActive = callingMode === 'softphone' || callingMode === 'smart'

  // === PHONE: ENGINE — DESK MIRROR POLL === webhook-fed D1 mirror of calls on the user's physical
  // extension ('rc-active-call', 8s — a D1 read, never a live RC poll); pauses while the WebRTC
  // softphone owns the screen. Future owner: useDeskMirror (moves ONLY as a unit with beginRingOut).
  // TRAP(phone/utc-normalize): SQLite timestamps carry no timezone — append 'Z' before diffing (see the elapsed-time derivation below) or elapsed jumps by the TZ offset.
  // Desk-phone live-call mirror. Polls /api/crm/rc/active-call so the emulated
  // phone reflects calls happening on the user's PHYSICAL desk phone (and any
  // other device on their extension) — the deskphone-sync request. Runs only
  // while the panel is visibly open + the user has a mapped extension; pauses
  // during an in-browser WebRTC call (which owns the screen). Visible-only via
  // startManagedInterval.
  const [deskActiveCall, setDeskActiveCall] = useState<RcActiveCall | null>(null)
  // When the mirror was seeded optimistically off a RingOut (sessionId tagged
  // "ringout:"), give the webhook up to 30s to materialize the real row before
  // letting a null poll clear the full-screen mirror.
  const deskMirrorPlacedAtRef = useRef(0)
  // After the user taps End on a desk-call mirror, remember which call they
  // dismissed (by last-10 of its number) so the 8s active-call poll below does NOT
  // re-seed the mirror from the still-live rc_live_calls row. RingOut has no API
  // hangup once it connects (RC exposes no outbound call control), so End is
  // "cancel-if-still-ringing + hide until the call actually ends."
  const deskMirrorDismissedRef = useRef<{ phone: string; at: number } | null>(null)
  useEffect(() => {
    if (!callingPanelOpen || callingPanelMinimized || !callingUserMapping) { setDeskActiveCall(null); return }
    let cancelled = false
    const poll = async () => {
      if (callingState === 'connected' || callingState === 'ringing' || inboundCallSession) return
      const r = await fetchRcActiveCall()
      if (cancelled) return
      setDeskActiveCall((prev) => {
        if (!r && prev && prev.sessionId.startsWith('ringout:') && Date.now() - deskMirrorPlacedAtRef.current < 30_000) return prev
        // Honor an End/dismiss: don't re-seed the call the user just dismissed
        // (same number, within 5 min) until it actually leaves rc_live_calls.
        const dismissed = deskMirrorDismissedRef.current
        if (dismissed) {
          if (!r) { deskMirrorDismissedRef.current = null }
          else {
            const rPhone = (r.phoneNumber || '').replace(/\D/g, '').slice(-10)
            if (rPhone && rPhone === dismissed.phone && Date.now() - dismissed.at < 5 * 60_000) return null
            deskMirrorDismissedRef.current = null
          }
        }
        return r
      })
    }
    poll()
    // 8s: /api/crm/rc/active-call is now webhook-fed (D1 read, no RC call), so a
    // faster poll is cheap and keeps the desk-phone mirror feeling live.
    const stop = startManagedInterval({ name: 'rc-active-call', intervalMs: 8_000, run: poll })
    return () => { cancelled = true; stop() }
  }, [callingPanelOpen, callingPanelMinimized, callingUserMapping, callingState, inboundCallSession])

  // Live timer for the desk-call mirror. Pure UI ticker (1s) while a mirrored
  // call has a start time — same pattern as callingTimerRef for softphone calls.
  const [deskMirrorNow, setDeskMirrorNow] = useState(0)
  useEffect(() => {
    if (!deskActiveCall?.startTime) { setDeskMirrorNow(0); return }
    setDeskMirrorNow(Date.now())
    const t = setInterval(() => setDeskMirrorNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [deskActiveCall?.startTime])
  // started_at can be SQLite "YYYY-MM-DD HH:MM:SS" (UTC, no marker) from the
  // webhook row or a full ISO string from the optimistic seed — normalize to
  // UTC so the elapsed time doesn't jump by the local timezone offset.
  const deskMirrorElapsed = (() => {
    const s = deskActiveCall?.startTime
    if (!s || !deskMirrorNow) return 0
    const iso = s.includes('T') ? s : s.replace(' ', 'T')
    const utc = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : iso + 'Z'
    const t0 = new Date(utc).getTime()
    if (!Number.isFinite(t0)) return 0
    return Math.max(0, Math.floor((deskMirrorNow - t0) / 1000))
  })()
  const deskMirrorAnswered = !!deskActiveCall && /answer|progress|success/i.test(deskActiveCall.result || '')
  // Full-screen mirror: any outgoing desk call (the user just placed it — it
  // should read like a CRM-placed call), or an inbound one once answered.
  // Inbound-while-ringing keeps the compact banner so the user can still
  // decide on the desk phone without the CRM taking over the screen.
  const deskMirrorFullscreen = !!deskActiveCall && callingState === 'idle' && !inboundCallSession &&
    (deskActiveCall.direction === 'Outbound' || deskMirrorAnswered)

  // ── iOS-style incoming-call presentation ──
  // Any inbound ring (softphone session OR desk-phone mirror) starts as a
  // floating banner (name over number, red/green circles). Swipe the banner
  // up → it disappears (call keeps ringing); tap it → the full incoming
  // screen, WITHOUT answering. Softphone buttons really answer/decline; for
  // desk calls the audio lives on the physical phone, so green opens the
  // full screen with an "answer on your desk phone" hint and red dismisses.
  const inboundDeskRinging = !!deskActiveCall && deskActiveCall.direction === 'Inbound' && !deskMirrorAnswered && callingState === 'idle' && !inboundCallSession
  // Poller-detected ring (SIP not co-registered / INVITE never reached this
  // tab): same iOS banner, kind 'remote' — there is no local session to
  // answer, the call is ringing on the user's other devices.
  const [pollerIncoming, setPollerIncoming] = useState<{ name: string; number: string } | null>(null)
  // Identity "reveal" gate: the banner/overlay hold until the CRM match for
  // the caller settles (or 450ms passes) so the identity paints ONCE instead
  // of flashing raw-callerID → CRM name.
  const [incomingReady, setIncomingReady] = useState(true)
  const incomingSeedRef = useRef<{ name: string; number: string } | null>(null)
  const iosIncoming = (incomingCallInfo && inboundCallSession)
    ? { kind: 'softphone' as const, name: incomingCallInfo.name || '', number: incomingCallInfo.number || '' }
    : (inboundDeskRinging && deskActiveCall)
      ? { kind: 'desk' as const, name: deskActiveCall.name || '', number: deskActiveCall.phoneNumber || '' }
      : (pollerIncoming && callingState === 'idle')
        ? { kind: 'remote' as const, name: pollerIncoming.name, number: pollerIncoming.number }
        : null
  const [incomingPresn, setIncomingPresn] = useState<'banner' | 'full' | 'hidden'>('banner')
  const [deskAnswerHint, setDeskAnswerHint] = useState(false)
  const iosBannerRef = useRef<HTMLDivElement>(null)
  const iosBannerDragRef = useRef<{ y0: number; dy: number; active: boolean }>({ y0: 0, dy: 0, active: false })
  // New softphone ring → reset to the banner presentation. The session
  // supersedes any poller-detected ring for the same call.
  useEffect(() => {
    if (inboundCallSession) { setIncomingPresn('banner'); setDeskAnswerHint(false); setPollerIncoming(null) }
  }, [inboundCallSession])
  // New desk ring (sessionId change) → same reset.
  useEffect(() => {
    if (inboundDeskRinging) { setIncomingPresn('banner'); setDeskAnswerHint(false) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inboundDeskRinging, deskActiveCall?.sessionId])
  // New poller-detected ring → same reset.
  useEffect(() => {
    if (pollerIncoming) { setIncomingPresn('banner'); setDeskAnswerHint(false) }
  }, [pollerIncoming])

  // === PHONE: ENGINE — INBOUND ANSWER/DECLINE === the ONE shared answer/decline pair — the in-phone
  // overlay, the iOS banner, and the root incoming toast all delegate here. Keep it that way.
  // TRAP(phone/inbound-no-timer): answered inbound calls never start the display timer and never bind disposed→review — a remote hangup strands callingState='connected'. Known gap, BUG-COMPAT: fix it as a flagged behavior PR, never inside a structure PR. docs/PHONE_ARCHITECTURE.md §divergences
  // Shared answer/decline so the banner and the full screen drive the same
  // logic (previously inlined in the overlay's buttons).
  const declineInboundCall = async () => {
    if (!inboundCallSession) return
    try { await (inboundCallSession.decline?.() ?? inboundCallSession.hangup?.()) } catch { /* ignore */ }
    setInboundCallSession(null); setIncomingCallToast(null); setIphoneNotif(null)
  }
  const answerInboundCall = async () => {
    if (!inboundCallSession) return
    try { await inboundCallSession.answer?.() } catch (err) { console.error('answer failed', err) }
    activeSessionRef.current = inboundCallSession
    // Seed the active-call contact so the connected screen mirrors the incoming UI
    if (incomingCallInfo) {
      setActiveCallContact({
        name: incomingCallInfo.name,
        phone: incomingCallInfo.number,
        id: incomingCallInfo.entityKind === 'contact' ? (incomingCallInfo.entityId || undefined) : undefined,
      })
    }
    setInboundCallSession(null)
    setIncomingCallToast(null)
    setIphoneNotif(null)
    // Answered call lands on a clean connected screen — no panels left
    // open from a previous call. Start un-held so a hold leaked from the
    // previous call can't show the Hold button stuck on "Resume" (white).
    setCallOnHold(false)
    setShowDialpad(false)
    setShowTransferPicker(false)
    setShowInCallNotes(false)
    setShowTranscript(false)
    setCallingState('connected')
    setCallingPanelOpen(true)
    setCallingPanelMinimized(false)
  }

  // === PHONE: ENGINE — WEBPHONE INIT (SIP) === DO NOT SPLIT — moves only as one unit with the
  // outbound engine (future usePhoneCall). v2 WebPhone emits ONLY inboundCall/outboundCall (no
  // unregistered/registrationFailed); a failed phone.start() leaks the instance (no SDK dispose).
  // TRAP(phone/sip-403-quiet): on a 403 'intercepted by another registration' the instanceId rotates ONCE, then degrades at console.info ON PURPOSE — reliability.ts mirrors error/warn to /api/client-events.
  // TRAP(phone/sip-secrets): never log sipInfo/provision objects — they hold live SIP credentials; log shape metadata only.
  // Initialize WebRTC Web Phone when calling panel opens and user has a mapping.
  //
  // Notes on the implementation:
  //  - `ringcentral-web-phone` is dynamically imported here. Users who never
  //    open the phone don't download the ~316KB gzipped SDK. Vite's
  //    manualChunks config names the chunk `vendor-phone`.
  //  - SIP credentials (username / password / authorizationId) are returned by
  //    /api/crm/rc/sip-provision. They MUST NOT be logged — they are effectively
  //    a live session secret. Previous versions dumped the entire `provision`
  //    object and the raw `sipInfo` into `console.log` / `console.error`. That
  //    leaked into DevTools + browser crash logs + any screen-share. This
  //    implementation logs only non-sensitive shape metadata (key names +
  //    boolean presence) on failure.
  //  - The old `phone.on('unregistered'|'registrationFailed', ...)` listeners
  //    were dead code — the SDK's EventEmitter only emits
  //    `inboundCall` / `outboundCall` at the WebPhone level. The handlers
  //    never fired, so "will re-init on next call" was misleading. They're
  //    removed here; detection of a stale SIP registration happens via the
  //    per-session `failed` event and on retry when `call()` throws.
  const initWebPhone = useCallback(async (force = false) => {
    if ((!force && webPhoneRef.current) || webPhoneInitingRef.current || !callingUserMapping) return
    // TRAP(phone/impersonation-identity): the softphone registers the REAL signed-in
    // user's line — the worker's impersonation middleware deliberately bypasses
    // sip-provision (delegated flow), so attempting init while impersonating just
    // earns the deterministic no-mapping 404 for the ADMIN's identity. Dials route
    // via RingOut instead, which the middleware DOES honor for the impersonated
    // extension (see startCallingPanelCall).
    if (impersonating) {
      console.info('[WebPhone] Impersonating — softphone stays off; dials route via RingOut on the impersonated extension')
      return
    }
    // If forcing, tear down the old instance properly (dispose stops the SIP
    // client + removes listeners). `stop()` was never a real method on the
    // WebPhone class — calling it silently threw which is why cleanup looked
    // broken.
    if (force && webPhoneRef.current) {
      try { await webPhoneRef.current.dispose?.() } catch { /* ignore */ }
      webPhoneRef.current = null
      setWebPhoneReady(false)
    }
    webPhoneInitingRef.current = true
    setWebPhoneError(null)

    // Dynamic import keeps the SDK out of the initial bundle.
    let WebPhoneCtor: typeof WebPhoneType
    try {
      const mod = await import('ringcentral-web-phone')
      WebPhoneCtor = mod.default
    } catch (err) {
      console.warn('[WebPhone] Failed to load SDK module')
      setWebPhoneError('Phone module failed to load — reload the page to retry')
      webPhoneInitingRef.current = false
      return
    }

    // No retries on the init loop — each failed `phone.start()` leaks an
    // already-constructed WebPhone instance (the SDK has no exposed dispose).
    // Auto-init runs once per session; manual retry is via clearing
    // webPhoneError. (User-reported OOM-after-idle suspect 2026-05-01:
    // multiple zombie SDK instances accumulate on retry, each with their own
    // SIP transport/audio resources that can't be GC'd.) See section 7 of
    // docs/ENTERPRISE_INVESTIGATION_TODO.md.
    const maxRetries = 1 // one retry — lets us rotate the SIP instanceId to co-register beside the desktop app
    let rotatedInstanceId = false
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const provision = await fetchSipProvision(currentUserId)
        if (!provision.sipInfo) {
          setWebPhoneError('SIP provisioning returned no info')
          webPhoneInitingRef.current = false
          return
        }
        const sipInfo = provision.sipInfo as Record<string, unknown>
        // Unique instance ID per browser so we don't collide with user's desktop app or other tabs
        let instanceId = localStorage.getItem('webPhoneInstanceId')
        if (!instanceId) {
          instanceId = (crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`)
          localStorage.setItem('webPhoneInstanceId', instanceId)
        }
        let phone: WebPhoneType
        try {
          phone = new WebPhoneCtor({ sipInfo: { ...provision.sipInfo, outboundProxyBackup: (provision.sipInfo as any).outboundProxyBackup || '', stunServers: provision.sipInfo.stunServers || [] }, instanceId, debug: false } as ConstructorParameters<typeof WebPhoneType>[0])
        } catch (ctorErr: unknown) {
          // NEVER log the sipInfo object — it contains the live SIP password.
          const msg = ctorErr instanceof Error ? ctorErr.message : String(ctorErr)
          console.error('[WebPhone] Constructor threw:', msg)
          setWebPhoneError(`Phone SDK crashed: ${msg} — tap to retry`)
          webPhoneInitingRef.current = false
          return
        }
        await phone.start()
        webPhoneRef.current = phone
        setWebPhoneReady(true)
        webPhoneInitingRef.current = false

        // Listen for incoming calls — surface banner + Answer/Decline IMMEDIATELY
        // (don't wait for 5s backend notification poller)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        phone.on('inboundCall', (session: any) => {
          const t0 = performance.now()
          // Ring sound FIRST (lowest latency, browser can play while we parse).
          // Use the real phone-ring tone — not the iMessage tri-tone — so
          // the user can distinguish call vs. SMS by ear.
          try { playPhoneRing() } catch { /* ignore */ }
          // Parse caller info from SIP From header (format: "NAME" <sip:+15551234567@server>;tag=...)
          const fromHeader: string = session?.remotePeer || session?.sipMessage?.headers?.From || ''
          const rcInfo = session?.rcApiCallInfo
          const queueCallerName = rcInfo?.callerIdName
          const fromMatch = typeof fromHeader === 'string' ? fromHeader.match(/(?:"([^"]+)"\s*)?<?(?:sip:)?(\+?\d[\d\-() ]*)/i) : null
          const derivedName = queueCallerName || fromMatch?.[1] || 'Incoming call'
          const derivedNumber = fromMatch?.[2] || (typeof fromHeader === 'string' ? fromHeader : '')
          // Batch state updates (React 18 auto-batches, but we explicitly co-locate them).
          // The iOS call banner is seeded DIRECTLY here, in the same commit as
          // the session — calls present through that ONE surface. The white
          // iphone-notif banner is SMS-only now (it used to flash for a frame
          // until the enrich effect seeded incomingCallInfo).
          incomingSeedRef.current = { name: queueCallerName || fromMatch?.[1] || '', number: derivedNumber }
          setIncomingReady(false) // hold the reveal until the CRM match settles (≤450ms)
          setInboundCallSession(session)
          setIncomingCallInfo({ name: incomingSeedRef.current.name, number: derivedNumber, photoUrl: null, entityKind: null, entityId: null, entityLabel: null })
          setIncomingCallToast({ title: `Incoming call: ${derivedName}`, body: derivedNumber || 'Unknown', linkUrl: '#/calls' })
          setCallingPanelOpen(true)
          // Fire a native Desktop Notification too so a user who tabbed away
          // still sees the incoming call. Permission requested lazily on first
          // phone-panel open (see useEffect below).
          try {
            if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
              const notif = new Notification('Incoming call', {
                body: `${derivedName}${derivedNumber ? ` · ${derivedNumber}` : ''}`,
                icon: '/bp-logo.webp',
                tag: 'rc-incoming-call',
                requireInteraction: false,
              })
              notif.onclick = () => {
                try { window.focus() } catch { /* ignore */ }
                setCallingPanelOpen(true)
                notif.close()
              }
              // Auto-close when the call ends or is answered
              try {
                session.on?.('answered', () => { try { notif.close() } catch { /* ignore */ } })
                session.on?.('disposed', () => { try { notif.close() } catch { /* ignore */ } })
              } catch { /* ignore */ }
            }
          } catch { /* browser blocked Notification or not supported */ }
          console.log(`[WebPhone] Inbound INVITE → UI in ${(performance.now() - t0).toFixed(1)}ms`)
          // CRM enrichment (name + entity link + toast title) happens in the
          // incomingCallInfo effect keyed on the session — ONE matchPhoneNumber
          // per ring, and the reveal gate holds the banner until it settles.
          // Clear when the session ends or is disposed
          try {
            session.on?.('disposed', () => { setInboundCallSession(null); setIncomingCallToast(null); setIphoneNotif(null) })
            session.on?.('terminated', () => { setInboundCallSession(null); setIncomingCallToast(null); setIphoneNotif(null) })
            session.on?.('answered', () => { setInboundCallSession(null); setIncomingCallToast(null); setIphoneNotif(null) })
          } catch { /* ignore */ }
        })
        // NOTE: WebPhone does NOT emit `unregistered` or `registrationFailed`.
        // Those were dead listeners in the previous version. The SDK detects
        // registration loss when the next `call()` throws, which we handle
        // via the retry path in `startCallingPanelCall`.
        return // success
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err)
        // A 403 "instance id is intercepted by another registration" means
        // another active registration (a second tab, or — most commonly — the
        // RingCentral desktop app) already holds THIS browser's SIP instanceId.
        // Previously we gave up here, which is exactly why the in-browser phone
        // never rang while the desktop app was open. Instead, rotate to a fresh
        // instanceId once and retry: RC then treats the browser as a separate
        // co-ringing endpoint, so inbound calls ring HERE and on the desktop app
        // simultaneously. If it still fails after rotating, fall back silently to
        // the server notification path (the 5s poller) — info level only so
        // reliability.ts doesn't mirror it to /api/client-events as a "crash".
        if (/intercepted by another registration/i.test(msg)) {
          if (!rotatedInstanceId) {
            rotatedInstanceId = true
            const fresh = (crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`)
            try { localStorage.setItem('webPhoneInstanceId', fresh) } catch { /* ignore */ }
            console.info('[WebPhone] SIP instanceId intercepted — rotating to co-register beside the active device')
            continue // retry registration with the fresh instanceId
          }
          console.info('[WebPhone] Could not co-register a second SIP endpoint; relying on server call notifications')
          setWebPhoneError(null)
          webPhoneInitingRef.current = false
          return
        }
        // "No active RC extension mapping" is a DETERMINISTIC 404 — this user
        // isn't provisioned for the softphone (sip-provision needs an active
        // rc_user_mappings row or a per-user RC OAuth token). Expected state,
        // not a fault: no retry, no error banner, info-level only so
        // reliability.ts doesn't mirror it to /api/client-events.
        if (/no active rc extension mapping/i.test(msg)) {
          console.info('[WebPhone] No active RC extension mapping — softphone stays off until one is assigned in Settings → Calling')
          setWebPhoneError(null)
          webPhoneInitingRef.current = false
          return
        }
        console.warn(`[WebPhone] Init attempt ${attempt + 1}/${maxRetries + 1} failed:`, msg)
        if (attempt === maxRetries) {
          console.error('[WebPhone] Init failed after retries:', msg)
          setWebPhoneError(msg)
          webPhoneInitingRef.current = false
        }
        // brief pause before retry
        await new Promise(r => setTimeout(r, 500))
      }
    }
  }, [callingUserMapping, currentUserId, impersonating])

  // Auto-init WebPhone as soon as user has an RC mapping — don't wait for panel to open.
  // This keeps SIP registered so inbound INVITEs arrive instantly instead of waiting for
  // the 5s notification poller.
  useEffect(() => {
    // Phase 2: only auto-register the softphone when the user's calling mode
    // wants inbound to ring here (softphone/smart). deskphone/notify skip it.
    if (callingUserMapping && !webPhoneRef.current && callingModeLoaded && softphoneModeActive) {
      setWebPhoneError(null)
      initWebPhone()
    }
  }, [callingUserMapping, initWebPhone, callingModeLoaded, softphoneModeActive])

  // Load the emulated iPhone's "Recents" from the user's OWN RC extension call
  // log (the same feed the deskphone / RC app show) whenever the phone panel is
  // open. Visible-only managed interval (src/polling.ts); refreshes every 30s so
  // a missed / just-ended call appears without waiting on the 5-min tenant sync.
  useEffect(() => {
    if (!callingPanelOpen || !callingUserMapping) return
    let cancelled = false
    const load = () => fetchRcExtensionCallLog({ perPage: 50 })
      .then(res => { if (!cancelled) setDeviceCallLog(res.calls || []) })
      .catch(() => { /* best-effort — keep the last good list */ })
    load() // immediate fetch on open
    const stop = startManagedInterval({
      name: 'phone-device-call-log',
      intervalMs: 30_000,
      run: load,
    })
    return () => { cancelled = true; stop() }
  }, [callingPanelOpen, callingUserMapping])

  // Collapse the incoming-call Forward/Reply pickers whenever the call ends.
  useEffect(() => {
    if (!inboundCallSession) { setShowIncomingForward(false); setShowIncomingReply(false) }
  }, [inboundCallSession])

  // Refs for keyboard handler to avoid stale closures
  const dialerInputRef = useRef(dialerInput)
  dialerInputRef.current = dialerInput
  const callingStateRef = useRef(callingState)
  callingStateRef.current = callingState
  const startCallRef = useRef<() => void>(() => {})

  // Capture keyboard input when focus is inside the phone device
  useEffect(() => {
    if (!callingPanelOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      const active = document.activeElement as HTMLElement | null
      // Must be focused inside the phone
      if (!active?.closest('.phone-device')) return
      // Don't intercept if typing in a phone input (like SMS compose)
      if (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA') return
      if (/^[0-9*#]$/.test(e.key)) {
        // Always ensure we're on the keypad screen
        setCallingPanelTab('keypad')
        setPhoneScreenMode('app')
        e.preventDefault()
        playDtmfTone(e.key)
        setDialerInput((d) => {
          const next = d + e.key
          const local = next.replace(/^1/, '')
          if (local.length > 10) return d
          setDialerSearch(formatPhoneDisplay(next))
          return next
        })
      } else if (e.key === 'Backspace') {
        e.preventDefault()
        setDialerInput((d) => { const next = d.slice(0, -1); setDialerSearch(next ? formatPhoneDisplay(next) : ''); return next })
      } else if (e.key === 'Enter') {
        e.preventDefault()
        const local = dialerInputRef.current.replace(/^1/, '')
        if (local.length === 10 && callingStateRef.current === 'idle') startCallRef.current()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [callingPanelOpen, callingPanelTab]) // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (webPhoneRef.current) {
        try { webPhoneRef.current.dispose() } catch { /* ignore */ }
        webPhoneRef.current = null
      }
    }
  }, [])

  // === PHONE: EFFECTS — ACTIVE-CALL UNLOAD GUARD === companion to the ESC-minimize
  // guard (grep 'Escape-key minimizes'). Wired ONLY while an in-browser WebRTC
  // softphone call is live — callingState is dialing/ringing/connected, which
  // models WebRTC ONLY (Commandment 2), so a genuine desk-phone / RingOut call
  // (never enters callingState) is intentionally NOT guarded: it lives on the
  // handset and survives a browser refresh untouched. Two listeners share the gate:
  //
  //   • beforeunload → WARN. Modern browsers hide the custom message (they show
  //     their own "Leave site?" prompt) but still require returnValue set truthy
  //     for the prompt to appear — so a refresh/close during a live call doesn't
  //     silently drop it.
  //   • pagehide → END CLEANLY. The WebRTC call leg + its media cannot survive a
  //     reload, but with no explicit teardown the WebPhone is never disposed, so
  //     RingCentral keeps the dead SIP session "active" for ~2 min — and on
  //     reload the desk-phone MIRROR poll finds that orphaned leg (no WebRTC
  //     session present) and MISLABELS it as a phantom "OUTGOING CALL · DESK
  //     PHONE". So here we best-effort hang up the live session(s) (activeSessionRef
  //     + consultSessionRef during an attended transfer) and dispose() the WebPhone
  //     (RingCentral's documented page-close/reload teardown): a SIP BYE goes out
  //     and the SIP slot frees IMMEDIATELY — no zombie, no phantom desk-phone on
  //     reload. Fire SYNCHRONOUSLY, never await (the page is going away). Refs are
  //     read at fire-time (always current) and the handler no-ops unless a real
  //     in-browser session is present, so only an actual softphone call is ever
  //     torn down. Unload contract → docs/PHONE_ARCHITECTURE.md TRAP(phone/esc-minimize).
  useEffect(() => {
    const isLive = callingState === 'dialing' || callingState === 'ringing' || callingState === 'connected'
    if (!isLive) return
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      const msg = 'You have an active call. Leaving this page will hang up — continue?'
      e.returnValue = msg // legacy browsers
      return msg
    }
    const onPageHide = () => {
      const active = activeSessionRef.current
      const consult = consultSessionRef.current
      // Guard: only tear down for an actual live in-browser softphone call.
      if (!active && !consult) return
      // Best-effort, synchronous BYE on each live leg (consult first, then the
      // caller), then dispose() to unregister the SIP device + free the slot.
      try { consult?.hangup?.() } catch { /* ignore */ }
      try { active?.hangup?.() } catch { /* ignore */ }
      try { webPhoneRef.current?.dispose?.() } catch { /* ignore */ }
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    window.addEventListener('pagehide', onPageHide)
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload)
      window.removeEventListener('pagehide', onPageHide)
    }
  }, [callingState])

  // P4: Request Desktop Notification permission the first time the phone
  // panel opens. Browsers require a user gesture; opening the panel IS a
  // user gesture. We never re-prompt if the user denies — respect their
  // choice. Granted permission means the `inboundCall` handler in
  // `initWebPhone` will fire a `new Notification(...)` for every ring.
  useEffect(() => {
    if (!callingPanelOpen) return
    if (typeof Notification === 'undefined') return
    if (Notification.permission === 'default') {
      Notification.requestPermission().catch(() => { /* user dismissed */ })
    }
  }, [callingPanelOpen])

  // === PHONE: POLLERS — PANEL DATA FEEDS === all via startManagedInterval. Registry:
  // phone-panel-calls-voicemail 60s · phone-device-call-log 30s · phone-sms-conversations 10s ·
  // phone-sms-messages 5s (+ rc-active-call 8s, ringout-status 2s, incoming-call-notifications 5s,
  // notif-unread-count 15s elsewhere). docs/PHONE_ARCHITECTURE.md §polling
  // TRAP(phone/calls-view-skip): this poller MUST NOT read, fetch, or write the #/calls list
  // state (callsData/callsTotal/callsPageCount). That state is OWNED SOLELY by the #/calls fetch
  // effect (calls-page-isolation). The docked phone's own surfaces use SEPARATE state — Recents +
  // missed-call badges read `deviceCallLog` (the phone-device-call-log poller), voicemails read
  // `voicemails` below — so this poller never needs the tenant-wide calls list.
  // History (2026-07-01): a vestigial refreshCalls here fetched page-1/limit-20 into the SHARED
  // callsData and, gated only by a runtime activeView check, overwrote the user's selected page on
  // /#/calls — clicking pagination moved the highlight but the rows snapped back to page-1 and the
  // page count ballooned to ceil(total/20). A runtime guard on a shared write is race-prone; the
  // durable fix REMOVED the shared write entirely (the phone loses nothing — it never read it).
  // Guarded by src/lib/calls/callsPaginationGuard.test.ts (this region must contain NO calls-view
  // setter) + src/lib/calls/callsViewOwnership.ts (the #/calls apply gate).
  // TRAP(phone/sameidseq-flash): the sameIdSeq identity guards stop a periodic full re-render flash in the docked panel — keep them on every list poller.
  // Pre-load voicemails when phone opens so badges populate without clicking tabs.
  // Also polls every 60s while phone is open so counts stay fresh.
  useEffect(() => {
    if (!callingPanelOpen || !currentUserId) return
    // Provisioning gate — an UNPROVISIONED user (no RC extension mapping, e.g.
    // Max/user 8 with no rc_user_mappings row) renders the "No phone number set
    // up" empty state, not the phone tabs; for them polling calls/voicemails is
    // futile (the endpoints return empty 200s). Gate on the SAME signal that
    // drives that empty state — `callingUserMapping || rcSelfMapped` — held until
    // both gate inputs are known (`rcMappingsLoaded && callingModeLoaded`) so we
    // don't poll during the bootstrap race. The backend still returns graceful
    // empties (defense-in-depth); this just stops the pointless 60s loop + the
    // on-mount refreshVoicemails for users who have no number.
    if (!rcMappingsLoaded || !callingModeLoaded) return
    if (!callingUserMapping && !rcSelfMapped) return
    const sameIdSeq = (a: any[] | undefined, b: any[] | undefined) => {
      if (!a || !b) return false
      if (a.length !== b.length) return false
      for (let i = 0; i < a.length; i++) if (a[i]?.id !== b[i]?.id) return false
      return true
    }
    // NOTE: intentionally NO refreshCalls here — see TRAP(phone/calls-view-skip)
    // above. The #/calls list state (callsData/callsTotal/callsPageCount) is owned
    // exclusively by its own fetch effect; this poller only keeps the phone's
    // voicemail feed fresh (Recents comes from deviceCallLog, a separate poller).
    const refreshVoicemails = () => {
      const hadData = voicemails.length > 0
      if (!hadData && callingPanelTab === 'voicemail') setVoicemailsLoading(true)
      fetchVoicemails(currentUserId, { perPage: 100 })
        .then(res => setVoicemails((prev: any[]) => sameIdSeq(prev, res.voicemails) ? prev : res.voicemails))
        .catch(err => { if (!(err instanceof TypeError)) console.error('[Voicemail] fetch failed:', err); if (!hadData) setVoicemails([]) }) // fetchVoicemails no longer throws for unprovisioned/empty (returns {voicemails:[]}); only genuine network/parse errors land here
        .finally(() => { if (!hadData) setVoicemailsLoading(false) })
    }
    refreshVoicemails()
    return startManagedInterval({
      name: 'phone-panel-calls-voicemail',
      intervalMs: 60_000,
      run: () => {
        refreshVoicemails()
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callingPanelOpen, currentUserId, rcMappingsLoaded, callingModeLoaded, callingUserMapping, rcSelfMapped])

  // No-RC users open to the HOME screen, not the Phone app's dead-end empty state.
  // Once both gate inputs are known and the user is confirmed un-provisioned,
  // redirect a default app-open ('app') to 'home'.
  useEffect(() => {
    if (!callingPanelOpen) return
    if (!rcMappingsLoaded || !callingModeLoaded) return
    if (callingUserMapping || rcSelfMapped) return
    setPhoneScreenMode((m) => (m === 'app' ? 'home' : m))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callingPanelOpen, rcMappingsLoaded, callingModeLoaded, callingUserMapping, rcSelfMapped])

  // Fetch phone contacts when contacts tab opens or search changes (keep existing list on refetch)
  useEffect(() => {
    if (!callingPanelOpen || callingPanelTab !== 'contacts') return
    if (phoneContactsDebounce.current) clearTimeout(phoneContactsDebounce.current)
    phoneContactsDebounce.current = setTimeout(() => {
      const hadData = phoneContactsList.length > 0
      if (!hadData) setPhoneContactsLoading(true)
      fetchClients(1, 2000, { search: phoneContactsSearch || undefined, phone: phoneContactsSearch ? undefined : '_has_', sort: 'preferred_name', order: 'ASC' })
        .then(res => {
          setPhoneContactsList(res.clients.filter(c => c.preferred_phone).map(c => ({
            id: c.unified_client_id,
            name: c.preferred_name,
            phone: c.preferred_phone,
            city: c.preferred_city,
          })))
        })
        .catch(() => {})
        .finally(() => { if (!hadData) setPhoneContactsLoading(false) })
    }, phoneContactsSearch ? 300 : 0)
    return () => { if (phoneContactsDebounce.current) clearTimeout(phoneContactsDebounce.current) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callingPanelOpen, callingPanelTab, phoneContactsSearch])

  // Fetch SMS conversations when SMS tab opens + poll every 10s
  useEffect(() => {
    if (!callingPanelOpen || callingPanelTab !== 'sms') return
    const loadConvos = (showLoading?: boolean) => {
      if (showLoading) setSmsLoading(true)
      fetchSmsConversations({ limit: 30 }).then(d => {
        setSmsConversations(d.conversations)
        const newUnread = d.conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0)
        // Detect new inbound message — trigger notification
        if (newUnread > lastSmsCountRef.current && lastSmsCountRef.current >= 0) {
          const newMsg = d.conversations.find(c => c.unread_count > 0)
          if (newMsg) {
            showIphoneNotification(
              newMsg.client_name || newMsg.subject || newMsg.phone_number,
              newMsg.latest_message_preview || 'New message'
            )
          }
        }
        lastSmsCountRef.current = newUnread
        setSmsTotalUnread(newUnread)
        if (showLoading) setSmsLoading(false)
      }).catch(() => { if (showLoading) setSmsLoading(false) })
    }
    loadConvos(true)
    return startManagedInterval({ name: 'phone-sms-conversations', intervalMs: 10_000, run: () => loadConvos() })
  }, [callingPanelOpen, callingPanelTab])

  // Load messages when a thread is selected + poll every 5s for new messages
  useEffect(() => {
    if (!callingPanelOpen || callingPanelTab !== 'sms' || !smsActiveThread) return
    const loadMessages = () => {
      fetchSmsMessages(smsActiveThread, 50).then(d => setSmsMessages(d.messages)).catch(() => {})
    }
    loadMessages()
    markSmsConversationRead(smsActiveThread).then(() => {
      setSmsConversations(prev => prev.map(c => c.id === smsActiveThread ? { ...c, unread_count: 0 } : c))
    }).catch(() => {})
    return startManagedInterval({ name: 'phone-sms-messages', intervalMs: 5_000, run: loadMessages })
  }, [callingPanelOpen, callingPanelTab, smsActiveThread])

  // === PHONE: ENGINE — OUTBOUND DIAL === DO NOT SPLIT — moves only as one unit with WEBPHONE INIT
  // (future usePhoneCall). Deskphone mode bails to beginRingOut and resets callingState to 'idle';
  // the no-mic catch self-heals to RingOut the same way.
  // TRAP(phone/v2-events): wp.call() resolves only AFTER answer — bind session listeners via wp.on('outboundCall') BEFORE call() and unbind in finally; v2 sessions emit ONLY ringing/answered/failed/disposed/mediaStreamSet; the SIP-403 short-circuit is reconciled post-await via session.state.
  // TRAP(phone/two-timers): the display timer counts from dial; the LOGGED duration anchors on callConnectedAtRef || callDialedAtRef — never unify the two.
  // overrideNumber: dial THIS number instead of the dialerInput state — used
  // by tap-to-call surfaces (Recents rows, Favorites tiles) that place the
  // call in the same tick they seed the dialer state, before React commits it.
  // TRAP(phone/handler-event-arg): never wire this directly as a DOM event handler —
  // React passes the MouseEvent as overrideNumber and `.trim()` throws, killing the
  // dial before the guard runs (keypad call button was dead for everyone, 2026-06-11).
  // The typeof check makes any non-string arg fall back to dialerInput.
  const startCallingPanelCall = async (overrideNumber?: string) => {
    const rawNumber = (typeof overrideNumber === 'string' ? overrideNumber : dialerInput).trim()
    if (!rawNumber || !callingUserMapping) return

    // Normalize to E.164
    const digits = rawNumber.replace(/\D/g, '')
    // Guard against mis-dials (e.g. a stray "95"): a placeable number needs at
    // least 10 digits. Without this, RingOut accepts junk like "+95" and the
    // desk-phone mirror sits "Calling…" forever with no real call to end.
    if (digits.length < 10) {
      setWebPhoneError('Enter a full phone number (at least 10 digits).')
      setCallingState('idle')
      return
    }
    const toNumber = digits.length === 10 ? `+1${digits}` : digits.length === 11 && digits.startsWith('1') ? `+${digits}` : `+${digits}`

    // IN-CALL CONTACT NAME: resolve the dialed number to CRM contact name(s) so
    // the in-call screen shows the contact instead of a bare number - parity with
    // the desk-mirror. One number can belong to several contacts, so we apply the
    // iPhone AMBIGUOUS rule via the shared ambiguousContactLabel helper over the
    // FULL matches array (1 -> "Adrian", 2 -> "Adrian or Barney", 3+ -> "Adrian or
    // N others") rather than silently picking one person. SAME data source +
    // helper as the keypad live lookup (GET /api/crm/contacts/lookup). The top
    // match still backs the activity owner id (existing attribution convention).
    // Fire-and-forget so it never blocks the dial; the formatted number stands
    // until/if a match resolves. We SKIP when activeCallContact already holds a
    // good (non-numeric) name - a tap-to-call from a CRM record or a keypad
    // suggestion already knows who it's calling, so re-resolving could downgrade
    // a known name.
    lookupContactsByPhone(toNumber).then(res => {
      const label = ambiguousContactLabel(res.matches)
      if (!label) return
      const topId = res.matches?.[0]?.id
      setActiveCallContact(prev => {
        // Don't clobber an existing good (non-numeric) name - the caller seeded it.
        const prevName = (prev?.name || '').trim()
        if (prevName && !/^[\s+\-().\d]+$/.test(prevName)) return prev
        return { name: label, phone: toNumber, id: topId ? `client-${topId}` : prev?.id }
      })
    }).catch(() => { /* ignore - the formatted number stands (current behavior) */ })

    // OUTBOUND DEFAULTS TO THE SOFTPHONE (2026-06-17): every outbound call tries
    // the in-browser WebRTC softphone first (live transcript + hold/transfer/mute),
    // REGARDLESS of callingMode. deskphone/notify still govern INBOUND (the desk
    // phone rings; the browser doesn't auto-register). RingOut is the fallback,
    // reached three ways: (1) impersonation, here — the softphone can't register as
    // the impersonated user (sip-provision keeps the real identity), but /rc/ringout
    // honors impersonation so the call places on the impersonated extension,
    // TRAP(phone/impersonation-identity); (2) softphone can't register — the init
    // guard below; (3) no usable mic — the call() catch.
    if (impersonating) {
      beginRingOut(toNumber)
      setDialerInput('')
      setCallingState('idle')
      return
    }

    // Softphone not registered yet → init on demand. If it CAN'T register (SIP 403
    // co-register with the RC desktop app, user not SIP-provisioned, etc.) fall back
    // to the desk phone via RingOut so the call still goes through — this is what
    // makes "softphone by default" safe for everyone, including deskphone-mode users
    // who never auto-register for inbound.
    if (!webPhoneRef.current) {
      setCallingState('dialing')
      try {
        await initWebPhone(true)
      } catch {
        console.info('[WebPhone] softphone could not register — falling back to RingOut')
        setWebPhoneError(null)
        setCallingState('idle')
        beginRingOut(toNumber)
        setDialerInput('')
        return
      }
    }

    if (!webPhoneRef.current) {
      console.info('[WebPhone] softphone not ready — falling back to RingOut')
      setWebPhoneError(null)
      setCallingState('idle')
      beginRingOut(toNumber)
      setDialerInput('')
      return
    }

    setWebPhoneError(null) // clear any previous error
    setCallingState('dialing')
    setActiveCallTimer(0)
    // The DISPLAY timer counts from dial ("Calling… 0:12"), matching the
    // desk-call mirror — and it can never get stuck at 0:00 if the SDK's
    // pickup event doesn't fire. Logged talk duration is unaffected: it
    // comes from callConnectedAtRef/callDialedAtRef, not this counter.
    if (callingTimerRef.current) clearInterval(callingTimerRef.current)
    callingTimerRef.current = setInterval(() => setActiveCallTimer((t) => t + 1), 1000)
    setCallMuted(false)
    // Fresh call begins un-held. A previous call may have left callOnHold true
    // (especially an attended transfer, which holds the caller via session.hold()),
    // and the hold UI state was never reset when that call ended — without this a
    // brand-new call would inherit the white "Resume" Hold button (stale-state leak).
    setCallOnHold(false)
    // Fresh call — close any expandable panels left open from the last one.
    setShowDialpad(false)
    setShowTransferPicker(false)
    setShowInCallNotes(false)
    setShowTranscript(false)

    // ── Call-outcome handlers, shared by SDK events + post-await checks ──
    const onAccepted = () => {
      setCallingState('connected')
      // Talk-time anchor for the LOGGED duration (ring time excluded — the
      // activity PATCH on hangup uses this ref, not the display counter).
      // The display timer keeps counting from dial, parity with the desk
      // mirror; just make sure it's alive in case anything cleared it.
      if (!callConnectedAtRef.current) callConnectedAtRef.current = Date.now()
      if (!callingTimerRef.current) callingTimerRef.current = setInterval(() => setActiveCallTimer((t) => t + 1), 1000)
    }
    let callEnded = false
    const onEnd = () => {
      if (callEnded) return // 'failed' disposes the session → 'disposed' fires too
      callEnded = true
      if (callingTimerRef.current) clearInterval(callingTimerRef.current)
      // Patch the activity row with the actual call duration. Prefer talk
      // time (post-pickup). Fall back to total session time (dial → hangup)
      // so unanswered calls still show ring length.
      const actId = currentCallActivityIdRef.current
      const connectedAt = callConnectedAtRef.current
      const dialedAt = callDialedAtRef.current
      if (actId && (connectedAt || dialedAt)) {
        const startedAt = connectedAt || dialedAt!
        const seconds = Math.max(0, Math.round((Date.now() - startedAt) / 1000))
        fetch(`/api/crm/rc/activities/${actId}/duration`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ durationSeconds: seconds }),
        }).catch(() => { /* best-effort */ })
      }
      // Hand the id off to the review path so saveCallReview can patch the
      // SAME row instead of creating a duplicate note.
      reviewableCallActivityIdRef.current = actId
      currentCallActivityIdRef.current = null
      callConnectedAtRef.current = null
      callDialedAtRef.current = null
    }
    const onSessionOver = () => {
      onEnd()
      activeSessionRef.current = null
      setCallingState('review')
    }

    // Log a local activity row immediately so the call appears in the
    // timeline without waiting for the call to end. No RC call here —
    // previously we called `startRingOut` which literally triggered a
    // SECOND ringout on the user's phone (the "double-ring" bug). This runs
    // BEFORE the SIP dial so callDialedAtRef really anchors at dial time
    // (webPhone.call() doesn't resolve until pickup — see below).
    {
      const ctx = pendingCallContextRef.current || {}
      pendingCallContextRef.current = null
      currentCallActivityIdRef.current = null
      callConnectedAtRef.current = null
      callDialedAtRef.current = Date.now()
      fetch('/api/crm/rc/activities/call-start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ toNumber: rawNumber, leadId: ctx.leadId, dealId: ctx.dealId, contactId: ctx.contactId }),
      }).then(r => r.ok ? r.json() : null).then(d => {
        if (d && typeof d.activityId === 'number') currentCallActivityIdRef.current = d.activityId
      }).catch(() => { /* best-effort; call still works */ })
    }

    // ringcentral-web-phone v2 GOTCHA: webPhone.call() resolves only AFTER
    // the callee answers (or the INVITE fails), and the session emits
    // 'ringing'/'answered'/'failed' BEFORE that promise settles. Listeners
    // bound on the returned session miss every pre-answer event — that bug
    // kept callingState stuck on 'ringing' for the whole call, which is why
    // Hold/Transfer (gated on 'connected') stayed disabled on live calls.
    // Bind through the 'outboundCall' hook instead: it fires synchronously
    // when the session is created, before the INVITE goes out. v2 sessions
    // emit ONLY: ringing, answered, failed, disposed, mediaStreamSet —
    // there is no 'accepted'/'established'/'terminated' (those were v1).
    const wp = webPhoneRef.current
    const wireOutbound = (session: any) => {
      session.on?.('ringing', () => setCallingState(s => (s === 'connected' ? s : 'ringing')))
      session.on?.('answered', onAccepted)
      session.on?.('failed', (subject: string) => {
        console.info('[WebPhone] call failed:', subject) // busy/declined — expected states
        onSessionOver()
      })
      session.on?.('disposed', onSessionOver)
    }
    wp.on?.('outboundCall', wireOutbound)

    try {
      const callerId = callingUserMapping.rc_phone_number || undefined
      const session = await wp.call(toNumber, callerId)
      activeSessionRef.current = session

      // Apply the user's currently-selected input device if it's not the
      // browser default. The SDK grabs the default mic on `init()`; we
      // switch to the chosen device here. Best-effort — errors shouldn't
      // block the call.
      if (selectedMic && selectedMic !== 'default') {
        try { await session.changeInputDevice?.(selectedMic) } catch (err) { console.warn('[WebPhone] changeInputDevice failed:', err) }
      }
      // Apply the selected speaker via setSinkId on the SDK's audioElement.
      // Some browsers (Firefox, older Safari) reject setSinkId — handle that
      // gracefully by falling back to the default output.
      if (selectedSpeaker && selectedSpeaker !== 'default') {
        try { await session.changeOutputDevice?.(selectedSpeaker) } catch (err) { console.warn('[WebPhone] changeOutputDevice failed:', err) }
      }

      // Connect remote audio to <audio> element (for our own visualiser /
      // volume control). The SDK already has its own hidden <audio> so audio
      // plays even if this second connection fails.
      if (session.remoteMediaStream && remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = session.remoteMediaStream
        remoteAudioRef.current.play().catch(() => {})
        // Route our UI audio element to the chosen speaker as well.
        if (selectedSpeaker && selectedSpeaker !== 'default' && (remoteAudioRef.current as any).setSinkId) {
          try { await (remoteAudioRef.current as any).setSinkId(selectedSpeaker) } catch (err) { console.warn('[WebPhone] remote audio setSinkId failed:', err) }
        }
      }

      // Listen for media stream changes (may come after initial setup)
      if (session.on) {
        session.on('remoteMediaStream', (stream: MediaStream) => {
          if (remoteAudioRef.current) {
            remoteAudioRef.current.srcObject = stream
            remoteAudioRef.current.play().catch(() => {})
          }
        })
      }

      // Reconcile against the session's actual state. On success wp.call()
      // resolves post-answer, so 'answered' has already fired through the
      // hook above and this is a no-op (handlers are idempotent). The one
      // path with NO events at all is the SDK's SIP-403 short-circuit,
      // where call() returns early and the session stays at state 'init' —
      // without this check that call would show "Ringing…" forever.
      const sessionState = (session as any)?.state
      if (sessionState === 'answered') onAccepted()
      else if (sessionState !== 'ringing') {
        console.info('[WebPhone] call ended without connecting (state:', sessionState, ')')
        onSessionOver()
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      // No usable microphone (desk PCs frequently have none) → fall back to
      // RingOut so the call still goes through on the physical desk phone
      // instead of failing outright.
      if (/device not found|notfounderror|requested device|getusermedia|permission denied|notallowed|no.*audio.*track|could not start audio/i.test(msg)) {
        console.warn('[WebPhone] No usable mic — falling back to RingOut:', msg)
        setWebPhoneError(null)
        setCallingState('idle')
        beginRingOut(toNumber)
        return
      }
      console.error('[WebPhone] Call failed:', msg)
      setCallingState('idle')
      setWebPhoneError(`Call failed: ${msg}`)
    } finally {
      // Per-call wiring only — leaving this bound would re-wire every
      // FUTURE outbound session with this call's stale closures.
      wp.off?.('outboundCall', wireOutbound)
    }
  }

  // TRAP(phone/startcallref-late): assigned HERE, after the definition — consumed EARLIER via refs by the keyboard auto-dial effect and click-to-call's 300ms timer; reordering reintroduces stale-closure dialing.
  startCallRef.current = startCallingPanelCall

  // === PHONE: ENGINE — ATTENDED TRANSFER === warm/consultative transfer over the
  // RC WebPhone v2 SDK (PHONE_ARCHITECTURE §6.6). The active call (activeSessionRef)
  // is HELD; a SECOND CallSession (consultSessionRef) dials the target so the agent
  // talks first; completeWarmTransfer REFERs the held caller INTO that already-live
  // consult dialog (SIP REFER with Replaces) — the agent drops out, caller + target
  // stay connected, with NO second dial of the target. BLIND transfer
  // (session.transfer) stays available via the same panel.
  // TRAP(phone/v2-events): bind the consult session via wp.on('outboundCall') BEFORE
  // wp.call() and unbind in finally — v2 sessions emit ONLY ringing/answered/failed/
  // disposed and fire BEFORE call() settles (call() resolves only AFTER answer).
  // TRAP(phone/two-timers): the consult leg is transient and logs NOTHING client-side;
  // the original call's display timer + duration PATCH are untouched.
  const consultStateRef = useRef(consultState)
  consultStateRef.current = consultState

  // Normalize a transfer target: a 10/11-digit number → E.164; a short string (an
  // internal extension like "8") is dialed verbatim, exactly like blind transfer.
  const normalizeTransferTarget = (raw: string): string => {
    const trimmed = (raw || '').trim()
    const digits = trimmed.replace(/\D/g, '')
    if (digits.length === 10) return `+1${digits}`
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`
    if (digits.length > 11) return `+${digits}`
    return trimmed // extension (or partial) — dial as-is, like the old blind path
  }

  // BLIND transfer — immediate SIP REFER to the target line; the agent drops now
  // and never hears whether the target picks up (the original transfer behavior).
  const blindTransfer = async (target: string) => {
    const session = activeSessionRef.current
    if (!session || !target) return
    try {
      await session.transfer(String(target))
      setShowTransferPicker(false)
      setTransferNumberInput('')
    } catch (err) {
      console.error('[WebPhone] Transfer failed:', err)
      setWebPhoneError(`Transfer failed: ${err instanceof Error ? err.message : String(err)}`)
    }
  }

  // ATTENDED transfer step 1 — hold the caller and place the consult call.
  const startAttendedTransfer = async (target: string, displayName: string) => {
    const callerSession = activeSessionRef.current
    const wp = webPhoneRef.current
    if (!callerSession || !wp || !target) return
    // completeWarmTransfer is the v2 WebRTC primitive; absence means this leg can't
    // do a REFER-with-Replaces (e.g. a non-WebRTC session) — don't strand the user.
    if (typeof callerSession.completeWarmTransfer !== 'function') {
      setConsultError('Consultative transfer is unavailable on this call — use Transfer now.')
      return
    }
    setConsultError(null)
    setConsultTarget({ name: displayName || target, number: target })
    setConsultState('dialing')
    // 1) Put the caller on hold (reuse the shared hold state so the UI shows it).
    try {
      if (!callOnHold) { await callerSession.hold(); setCallOnHold(true) }
    } catch (err) {
      console.error('[WebPhone] attended-transfer hold failed:', err)
      setConsultError('Could not place the caller on hold.')
      setConsultState('idle')
      setConsultTarget(null)
      return
    }
    // 2) Bind consult-session listeners BEFORE wp.call() — TRAP(phone/v2-events).
    const wireConsult = (sess: any) => {
      consultSessionRef.current = sess
      sess.on?.('ringing', () => setConsultState((s) => (s === 'connected' ? s : 'ringing')))
      sess.on?.('answered', () => setConsultState('connected'))
      sess.on?.('failed', (subject: string) => {
        console.info('[WebPhone] consult call failed:', subject) // busy/declined — expected
        consultSessionRef.current = null
        setConsultState('failed')
      })
      sess.on?.('disposed', () => {
        // Target hung up (or the leg died) before we completed — fall back to the
        // held caller via the failed state, unless we're mid-complete or done.
        consultSessionRef.current = null
        setConsultState((s) => (s === 'completing' || s === 'idle' ? s : 'failed'))
      })
    }
    wp.on?.('outboundCall', wireConsult)
    try {
      const callerId = callingUserMapping?.rc_phone_number || undefined
      const consultSession = await wp.call(String(target), callerId)
      consultSessionRef.current = consultSession
      // Apply the user's chosen audio devices to the consult leg (best-effort).
      if (selectedMic && selectedMic !== 'default') {
        try { await consultSession.changeInputDevice?.(selectedMic) } catch (err) { console.warn('[WebPhone] consult changeInputDevice failed:', err) }
      }
      if (selectedSpeaker && selectedSpeaker !== 'default') {
        try { await consultSession.changeOutputDevice?.(selectedSpeaker) } catch (err) { console.warn('[WebPhone] consult changeOutputDevice failed:', err) }
      }
      // Reconcile post-await: wp.call() resolves only AFTER answer/fail; the SDK's
      // SIP-403 short-circuit leaves state 'init' with no events at all.
      const st = consultSession?.state
      if (st === 'answered') setConsultState('connected')
      else if (st !== 'ringing') setConsultState((s) => (s === 'connected' ? s : 'failed'))
    } catch (err) {
      console.error('[WebPhone] consult dial failed:', err)
      setConsultState('failed')
    } finally {
      wp.off?.('outboundCall', wireConsult)
    }
  }

  // ATTENDED transfer step 2a — COMPLETE: REFER the held caller into the consult
  // dialog (Replaces). Both agent legs drop; caller ↔ target stay connected.
  const completeAttendedTransfer = async () => {
    const callerSession = activeSessionRef.current
    const consultSession = consultSessionRef.current
    if (!callerSession || !consultSession) return
    if (typeof callerSession.completeWarmTransfer !== 'function') {
      setConsultError('Consultative transfer is unavailable on this connection.')
      return
    }
    setConsultState('completing')
    try {
      await callerSession.completeWarmTransfer(consultSession)
      // REFER sent + the caller BYE'd us. The caller session's 'disposed' fires
      // onSessionOver → callingState 'review'; the teardown effect clears consult
      // state. Reset our local refs/UI now so nothing dangles.
      consultSessionRef.current = null
      setConsultTarget(null)
      setConsultError(null)
      setConsultState('idle')
      setShowTransferPicker(false)
      // Transfer done — the held caller was handed to the target and BYE'd us, so
      // the caller is gone: clear hold now so it reflects reality immediately (the
      // teardown effect also clears it once callingState reaches review).
      setCallOnHold(false)
      // Belt-and-suspenders: if the caller's 'disposed' didn't flip us to review
      // (rare), end our side so we still land on the post-call review sheet.
      if (callingStateRef.current !== 'review' && activeSessionRef.current) {
        try { activeSessionRef.current.hangup?.() } catch { /* ignore */ }
      }
    } catch (err) {
      console.error('[WebPhone] complete attended transfer failed:', err)
      setConsultError('Transfer failed — you are still connected to the target.')
      setConsultState('connected')
    }
  }

  // ATTENDED transfer step 2b — CANCEL / fallback: end the consult leg and RESUME
  // the held caller (back to the original 1:1 call). Used by Cancel and by the
  // failed-state "Back to caller" button.
  const cancelAttendedTransfer = async () => {
    const consultSession = consultSessionRef.current
    const callerSession = activeSessionRef.current
    consultSessionRef.current = null
    if (consultSession) {
      try {
        // Ringing outbound legs are torn down with CANCEL; answered ones with BYE.
        if (consultSession.state === 'answered') await consultSession.hangup()
        else if (typeof consultSession.cancel === 'function') await consultSession.cancel()
        else await consultSession.hangup()
      } catch (err) { console.info('[WebPhone] consult teardown:', err) }
    }
    // Resume the held caller.
    try {
      if (callerSession && callOnHold) { await callerSession.unhold(); setCallOnHold(false) }
    } catch (err) {
      console.error('[WebPhone] resume after cancel failed:', err)
      setWebPhoneError('Could not resume the caller — try the Hold button.')
    }
    setConsultState('idle')
    setConsultTarget(null)
    setConsultError(null)
  }

  // Tear down any lingering consult leg the moment the PRIMARY call ends (the held
  // caller hung up → onSessionOver, or the agent hit Hang up) so a second call
  // never dangles after the in-call screen is gone.
  useEffect(() => {
    if (callingState === 'review' || callingState === 'idle') {
      // The call left the in-call screen (ended, transferred, hung up, or panel
      // reset): clear hold so the NEXT call can never inherit a stale "Resume"
      // (white) Hold button. This is the catch-all teardown — every end path
      // (onSessionOver, hangupCallingPanel, completed transfer, remote disposed,
      // closeCallingPanel) routes callingState through review/idle.
      setCallOnHold(false)
      if (consultSessionRef.current) {
        try { consultSessionRef.current.hangup?.() } catch { /* ignore */ }
        consultSessionRef.current = null
      }
      if (consultStateRef.current !== 'idle') {
        setConsultState('idle')
        setConsultTarget(null)
        setConsultError(null)
      }
    }
  }, [callingState])

  // Route a chosen transfer target to the active mode (attended vs blind).
  const handleTransferTarget = (target: string, displayName: string) => {
    const normalized = normalizeTransferTarget(target)
    if (!normalized) return
    if (transferMode === 'blind') void blindTransfer(normalized)
    else void startAttendedTransfer(normalized, displayName)
  }

  const hangupCallingPanel = () => {
    if (activeSessionRef.current) {
      try { activeSessionRef.current.hangup() } catch { /* ignore */ }
      activeSessionRef.current = null
    }
    if (callingPollRef.current) clearInterval(callingPollRef.current)
    if (callingTimerRef.current) clearInterval(callingTimerRef.current)
    setCallingState('review')
  }

  const toggleMuteWebRTC = () => {
    if (!activeSessionRef.current) return
    try {
      if (callMuted) {
        activeSessionRef.current.unmute()
      } else {
        activeSessionRef.current.mute()
      }
      setCallMuted(!callMuted)
    } catch (err) {
      console.error('[WebPhone] Mute toggle failed:', err)
    }
  }

  const sendDtmfWebRTC = (digit: string) => {
    if (!activeSessionRef.current) return
    try {
      activeSessionRef.current.sendDtmf(digit)
    } catch (err) {
      console.error('[WebPhone] DTMF failed:', err)
    }
  }

  // === PHONE: ENGINE — AUDIO SYNTH === one shared AudioContext (dtmfCtxRef) powers playDtmfTone +
  // playIphoneDing (SMS tri-tone) + playPhoneRing (call ring, 440+480 Hz) — ding vs ring must stay
  // audibly distinct (product rule). Future home: src/lib/phone/audio.ts.
  // DTMF tone generator — each key is two frequencies mixed
  const dtmfCtxRef = useRef<AudioContext | null>(null)
  const ensureAudioCtx = useCallback(() => {
    if (!dtmfCtxRef.current) {
      dtmfCtxRef.current = new AudioContext()
      const buf = dtmfCtxRef.current.createBuffer(1, 1, 22050)
      const src = dtmfCtxRef.current.createBufferSource()
      src.buffer = buf
      src.connect(dtmfCtxRef.current.destination)
      src.start(0)
    } else if (dtmfCtxRef.current.state === 'suspended') {
      dtmfCtxRef.current.resume()
    }
    return dtmfCtxRef.current
  }, [])
  // Pre-warm on first click AND every time phone opens
  useEffect(() => {
    const warmUp = () => ensureAudioCtx()
    document.addEventListener('pointerdown', warmUp, { once: true })
    return () => document.removeEventListener('pointerdown', warmUp)
  }, [ensureAudioCtx])
  // Release the AudioContext on unmount — Chrome caps active contexts and an
  // unreleased one survives hot reloads during dev, producing the familiar
  // "AudioContext was not allowed to start" warning chain.
  useEffect(() => () => {
    const ctx = dtmfCtxRef.current
    dtmfCtxRef.current = null
    if (ctx && ctx.state !== 'closed') {
      ctx.close().catch(() => { /* already closing */ })
    }
  }, [])
  useEffect(() => {
    if (callingPanelOpen) {
      const warmOnGesture = () => { ensureAudioCtx(); document.removeEventListener('pointerdown', warmOnGesture) }
      document.addEventListener('pointerdown', warmOnGesture, { once: true })
      return () => document.removeEventListener('pointerdown', warmOnGesture)
    }
  }, [callingPanelOpen, ensureAudioCtx])
  const DTMF_FREQS: Record<string, [number, number]> = {
    '1': [697, 1209], '2': [697, 1336], '3': [697, 1477],
    '4': [770, 1209], '5': [770, 1336], '6': [770, 1477],
    '7': [852, 1209], '8': [852, 1336], '9': [852, 1477],
    '*': [941, 1209], '0': [941, 1336], '#': [941, 1477],
  }
  const playDtmfTone = useCallback((key: string) => {
    const pair = DTMF_FREQS[key]
    if (!pair) return
    try {
      const ctx = ensureAudioCtx()
      const gain = ctx.createGain()
      gain.gain.value = 0.15
      gain.connect(ctx.destination)
      const osc1 = ctx.createOscillator()
      const osc2 = ctx.createOscillator()
      osc1.frequency.value = pair[0]
      osc2.frequency.value = pair[1]
      osc1.connect(gain)
      osc2.connect(gain)
      osc1.start()
      osc2.start()
      osc1.stop(ctx.currentTime + 0.12)
      osc2.stop(ctx.currentTime + 0.12)
      gain.gain.setValueAtTime(0.15, ctx.currentTime + 0.09)
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.12)
    } catch {}
  }, [ensureAudioCtx])

  // iPhone tri-tone notification sound — used for SMS / chat / generic
  // notifications. NOT for inbound phone calls (use playPhoneRing instead).
  const playIphoneDing = useCallback(() => {
    try {
      const ctx = ensureAudioCtx()
      // Tri-tone: three ascending notes (A5, C#6, E6) — the classic iMessage sound
      const notes = [880, 1108.73, 1318.51]
      const t = ctx.currentTime
      notes.forEach((freq, i) => {
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.type = 'sine'
        osc.frequency.value = freq
        gain.gain.setValueAtTime(0.18, t + i * 0.12)
        gain.gain.exponentialRampToValueAtTime(0.001, t + i * 0.12 + 0.25)
        osc.connect(gain)
        gain.connect(ctx.destination)
        osc.start(t + i * 0.12)
        osc.stop(t + i * 0.12 + 0.25)
      })
    } catch {}
  }, [ensureAudioCtx])

  // Real phone-ring sound for inbound RingCentral calls. US standard
  // ring is 440 + 480 Hz dual-tone with 2s-on / 4s-off cadence; we
  // shorten the off period to 1s and play 2 cycles (~5s total) so the
  // user hears a recognizable ring within the 10s toast lifetime
  // without ringing forever in a backgrounded tab.
  const playPhoneRing = useCallback(() => {
    try {
      const ctx = ensureAudioCtx()
      const ringDur = 2.0       // seconds of tone per cycle
      const offDur = 1.0        // seconds of silence between cycles
      const cycles = 2
      const t0 = ctx.currentTime
      for (let i = 0; i < cycles; i++) {
        const startAt = t0 + i * (ringDur + offDur)
        const stopAt = startAt + ringDur
        const osc1 = ctx.createOscillator()
        const osc2 = ctx.createOscillator()
        const gain = ctx.createGain()
        osc1.type = 'sine'
        osc2.type = 'sine'
        osc1.frequency.value = 440
        osc2.frequency.value = 480
        // Soft ramp on/off avoids the click that dual-tone start/stop produces.
        gain.gain.setValueAtTime(0, startAt)
        gain.gain.linearRampToValueAtTime(0.12, startAt + 0.05)
        gain.gain.setValueAtTime(0.12, stopAt - 0.05)
        gain.gain.linearRampToValueAtTime(0, stopAt)
        osc1.connect(gain)
        osc2.connect(gain)
        gain.connect(ctx.destination)
        osc1.start(startAt)
        osc2.start(startAt)
        osc1.stop(stopAt)
        osc2.stop(stopAt)
      }
    } catch {}
  }, [ensureAudioCtx])

  // Show iPhone notification banner (SMS)
  const showIphoneNotification = useCallback((name: string, message: string) => {
    setIphoneNotif({ name, message, time: 'now', type: 'sms' })
    playIphoneDing()
    if (iphoneNotifTimer.current) clearTimeout(iphoneNotifTimer.current)
    iphoneNotifTimer.current = setTimeout(() => {}, 0) // persistent — stays until dismissed
  }, [playIphoneDing])

  // Format date string according to user's preferred date format
  const formatDatePref = (dateStr: string): string => {
    if (!dateStr) return ''
    // Normalize: handle "YYYY-MM-DD HH:MM:SS" (space separator from D1/SQLite)
    const normalized = dateStr.includes('T') ? dateStr : dateStr.includes(' ') ? dateStr.replace(' ', 'T') : dateStr + 'T00:00:00'
    const d = new Date(normalized)
    if (isNaN(d.getTime())) return dateStr
    const yyyy = d.getFullYear()
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    const fmt = prefsValues.dateFormat || 'MM/DD/YYYY'
    if (fmt === 'DD/MM/YYYY') return `${dd}/${mm}/${yyyy}`
    if (fmt === 'YYYY-MM-DD') return `${yyyy}-${mm}-${dd}`
    return `${mm}/${dd}/${yyyy}` // MM/DD/YYYY default
  }

  // Custom dropdown component for preferences (replaces native <select>)
  const PrefsSelect = ({ value, onChange, options, style }: {
    value: string; onChange: (v: string) => void;
    options: { value: string; label: string }[];
    style?: React.CSSProperties;
  }) => {
    const [open, setOpen] = useState(false)
    const ref = useRef<HTMLDivElement>(null)
    useEffect(() => {
      if (!open) return
      const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
      document.addEventListener('mousedown', h)
      return () => document.removeEventListener('mousedown', h)
    }, [open])
    const selected = options.find(o => o.value === value)
    return (
      <div className="prefs-dropdown" ref={ref} style={style}>
        <button type="button" className={`prefs-dropdown-trigger${open ? ' open' : ''}`} onClick={() => setOpen(!open)}>
          <span>{selected?.label || value || '—'}</span>
          <svg width="10" height="6" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
        </button>
        {open && (
          <div className="prefs-dropdown-panel">
            {options.map(o => (
              <button key={o.value} type="button" className={`prefs-dropdown-option${o.value === value ? ' active' : ''}`}
                onClick={() => { onChange(o.value); setOpen(false) }}>
                {o.label}
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }

  // Structured call-card metadata used by the Contact + Deal record
  // timelines. Returns everything a modern card header needs: direction
  // (label + color), outcome (label + color), duration, external party
  // phone (pre-formatted), and the BotPros employee who handled it.
  // Direction uses the rc-helpers.ts convention: 1 = Outbound, 2 = Inbound.
  //
  // Why this shape: the old card just said "Logged call" which forced
  // users to expand every row. The new header encodes all the at-a-glance
  // info (who, which way, what happened, how long, with whom) inline, with
  // minimal color for outcome — subtle pill tinting so the eye can scan a
  // day's calls and spot missed/voicemails without reading each row.
  type CallCardMeta = {
    direction: 'inbound' | 'outbound'
    directionLabel: 'Inbound' | 'Outbound'
    directionArrow: '↓' | '↑'
    directionColor: string       // fill for the small direction arrow pill
    outcome: string              // e.g. "Answered", "Left voicemail", "No answer"
    outcomeColor: string         // fill for the outcome dot / tint
    outcomeBg: string            // very-light bg for the outcome pill
    durationLabel: string | null
    partyPhone: string | null    // pre-formatted, e.g. "(954) 668-7648"
    partyDirection: 'from' | 'to'
    handledBy: string | null     // CRM user name, resolved via CALENDAR_USERS_MAP
  }

  const formatCallTitle = (act: CrmActivity, opts?: { isMissed?: boolean }): CallCardMeta => {
    const isOutbound = act.direction === 1
    const dur = act.duration_seconds
    const durationLabel = (dur != null && dur > 0) ? `${Math.floor(dur / 60)}m ${dur % 60}s` : null

    // RC call_result normalization. Missing/falsy falls back to the
    // MISSED_CALL settings flag the sync path writes, then to duration
    // heuristics (0 duration = missed, has duration = answered).
    const outcome = normalizeCallOutcome({ callResult: act.call_result, isOutbound, isMissed: opts?.isMissed, durationSeconds: dur })

    // Outcome tinting. Minimal — a small solid dot + a 4% bg on the pill.
    let outcomeColor = '#64748b' // slate default
    let outcomeBg = '#f1f5f9'
    if (outcome === 'Answered' || outcome === 'Accepted' || outcome === 'Connected') { outcomeColor = '#16a34a'; outcomeBg = '#ecfdf5' }
    else if (outcome === 'Left voicemail' || outcome === 'Voicemail') { outcomeColor = '#2563eb'; outcomeBg = '#eff6ff' }
    else if (outcome === 'Missed' || outcome === 'No answer') { outcomeColor = '#dc2626'; outcomeBg = '#fef2f2' }
    else if (outcome === 'Busy' || outcome === 'Wrong number') { outcomeColor = '#d97706'; outcomeBg = '#fffbeb' }
    else if (outcome === 'Fax') { outcomeColor = '#7c3aed'; outcomeBg = '#f5f3ff' }

    const external = isOutbound ? (act.phone_to || act.phone_from) : (act.phone_from || act.phone_to)
    const partyPhone = external ? (formatPhoneDisplay(external) || null) : null

    const handledBy = act.responsible_id && CALENDAR_USERS_MAP[act.responsible_id]
      ? CALENDAR_USERS_MAP[act.responsible_id]
      : null

    return {
      direction: isOutbound ? 'outbound' : 'inbound',
      directionLabel: isOutbound ? 'Outbound' : 'Inbound',
      directionArrow: isOutbound ? '↑' : '↓',
      directionColor: isOutbound ? '#2563eb' : '#16a34a',
      outcome,
      outcomeColor,
      outcomeBg,
      durationLabel,
      partyPhone,
      partyDirection: isOutbound ? 'to' : 'from',
      handledBy,
    }
  }

  // ------------------------------------------------------------------------
  // Unified phone drag handler (usePhoneDrag equivalent — kept inline per
  // single-file SPA pattern). Wires mousedown → move → mouseup on the
  // given phone-device panel. Includes a 3px click-vs-drag threshold so
  // clicks on the notch don't flicker, and a shared cleanup routine that
  // also fires on window blur (stops "stuck" drag state if alt-tab mid-drag).
  // Consumed by both the notch drag handle (dialer view) and the dark-strip
  // drag handle on the post-call review sheet.
  // ------------------------------------------------------------------------
  const startPhoneDrag = useCallback((_e: React.MouseEvent<HTMLElement>, _opts?: { forceUndock?: boolean }) => {
    // Phone is always docked (2026-05-01). Drag-to-undock retired; the notch
    // and active-call header still wire this onMouseDown — accept and ignore.
  }, [])

  // === PHONE: ASSIGN — post-call wrap-up deal/project picker (call-assign-link-v1) ===
  // Staging + search + lazy nested-project loading for the review sheet's
  // assignment row. See PHONE_ARCHITECTURE §6 (review-state) + §7 (Cluster A).
  // Two chips resolve to the same owner shape the review PATCH expects: a deal →
  // { ownerTypeId: 2, ownerId: <number> }; a project → { ownerTypeId: 3, ownerId:
  // <TEXT id verbatim> } (the sharp discriminator — owner_type_id 3 + a non-empty
  // string owner_id means PROJECT). Unassign stages { clearOwner: true }.
  const sameAssign = (a: CallAssignSelection, b: CallAssignSelection): boolean => {
    if (!a && !b) return true
    if (!a || !b) return false
    return a.type === b.type && String(a.id) === String(b.id)
  }
  const assignOwnerPatch = (sel: CallAssignSelection): Record<string, unknown> => {
    if (!sel) return { clearOwner: true }
    if (sel.type === 'project') return { ownerTypeId: 3, ownerId: String(sel.id) }
    const oid = Number(sel.id)
    return { ownerTypeId: 2, ownerId: Number.isFinite(oid) ? oid : sel.id }
  }
  // Reset the parent-owned staged assignment state on review entry / panel close.
  // The picker's internal search/probe/keyboard state resets itself (it unmounts
  // when the review sheet closes — see the shared CallAssignPicker).
  const resetCallAssignState = useCallback(() => {
    setCallAssignSelection(null)
    callAssignInitialRef.current = null
    setAssignPickerOpen(false)
    setAssignSuggestedDeals([])
    setAssignSuggestName('')
    setAssignSaveError(null)
    assignSuggestedFetchedRef.current = false
  }, [])

  const selectAssignRow = useCallback((row: AssignRow) => {
    setCallAssignSelection({ type: row.kind, id: String(row.id), label: row.label, sublabel: row.sublabel, parentDealId: row.parentDealId })
    setAssignPickerOpen(false)
    setAssignSaveError(null)
    setTimeout(() => assignTriggerRef.current?.focus(), 20)
  }, [])

  // Entering the review sheet: reset staging, pre-seed suggested deals from the
  // call's contact, and pre-fill the chip from any dial-time deal linkage so it's
  // visible + changeable. pendingCallContextRef is consumed at dial time, so the
  // resolveCallActivityOwner(activeCallContact.id) fallback recovers the contact/
  // deal at review time. Discarded entirely if the sheet is dismissed without Done.
  useEffect(() => {
    if (callingState !== 'review') return
    resetCallAssignState()
    const pctx = pendingCallContextRef.current
    const owner = activeCallContact?.id ? resolveCallActivityOwner(String(activeCallContact.id)) : null
    const contactUid = pctx?.contactId || owner?.unifiedClientId || ''
    setAssignSuggestName(activeCallContact?.name || '')
    if (contactUid) {
      assignSuggestedFetchedRef.current = true
      fetchClientDeals(contactUid)
        .then((deals) => setAssignSuggestedDeals(Array.isArray(deals) ? deals : []))
        .catch(() => setAssignSuggestedDeals([]))
    }
    const preDealId = pctx?.dealId || (owner?.ownerTypeId === 2 && owner.ownerId ? owner.ownerId : 0)
    if (preDealId) {
      fetchDeal(preDealId)
        .then((deal) => {
          const sel: CallAssignSelection = { type: 'deal', id: String(deal.id), label: deal.title || `Deal ${deal.id}`, sublabel: deal.communityName || deal.contact_name || undefined, parentDealId: undefined }
          // Race guard (review finding #5): the fetch round-trip can outlast the
          // user staging their own selection mid-flight. Only hydrate the dial-time
          // deal when nothing has been staged yet (prev === null) — never clobber a
          // user pick. Stamp the "initial" baseline only for the value we applied,
          // so the Done diff (sameAssign) still detects a real change.
          setCallAssignSelection((prev) => {
            if (prev) return prev
            callAssignInitialRef.current = sel
            return sel
          })
        })
        .catch(() => { /* leave unassigned */ })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callingState])

  const closeCallingPanel = (opts?: { keepOpen?: boolean }) => {
    if (callingPollRef.current) clearInterval(callingPollRef.current)
    if (callingTimerRef.current) clearInterval(callingTimerRef.current)
    // keepOpen: after saving a post-call review, reset to a fresh dialer but
    // leave the docked phone on screen instead of yanking the whole widget
    // away (which read as "the phone just disappeared" once notes were saved).
    if (!opts?.keepOpen) setCallingPanelOpen(false)
    setCallingState('idle')
    setDialerInput('')
    setDialerSearch('')
    setDialerSearchResults([])
    setActiveCallRingoutId('')
    setActiveCallTimer(0)
    setActiveCallContact(null)
    setCallNotes('')
    setCallOutcomeSelected('')
    setCallTypeSelected('')
    setCallMuted(false)
    setCallOnHold(false)
    // Fresh call — close any expandable panels left open from the last one.
    setShowDialpad(false)
    setShowTransferPicker(false)
    setShowInCallNotes(false)
    setShowTranscript(false)
    setShowDialpad(true)
    setCallSaved(false)
    setCallingPanelMinimized(false)
    setCallingPanelPos(null)
    setReviewTab('notes')
    setCallTranscriptText('')
    // Discard any staged post-call deal/project assignment (call-assign-link-v1).
    resetCallAssignState()
  }

  // ------------------------------------------------------------------------
  // Phone favorites API helpers — thin wrappers over src/api.ts functions
  // ------------------------------------------------------------------------
  const reloadPhoneFavorites = useCallback(async () => {
    setPhoneFavoritesLoading(true)
    try {
      const favs = await fetchPhoneFavorites()
      setPhoneFavorites(favs)
    } catch (err) {
      console.warn('[phone] Failed to load favorites', err)
    } finally {
      setPhoneFavoritesLoading(false)
    }
  }, [])
  // Sync the ref so earlier-declared effects (phone panel open) can invoke the
  // latest reloadPhoneFavorites without triggering a TDZ hoisting error.
  useEffect(() => { reloadPhoneFavoritesRef.current = reloadPhoneFavorites }, [reloadPhoneFavorites])

  const isPhoneFavorite = useCallback((identifier: string) => {
    if (!identifier) return false
    return phoneFavorites.some(f => f.contact_identifier === identifier)
  }, [phoneFavorites])

  const togglePhoneFavorite = useCallback(async (fav: {
    identifier: string
    displayName?: string
    phoneNumber?: string
    contactType?: 'crm_contact' | 'team_member' | 'external'
    linkedContactId?: string | number
    linkedUserEmail?: string
  }) => {
    const existing = phoneFavorites.find(f => f.contact_identifier === fav.identifier)
    try {
      if (existing) {
        // Optimistic remove
        setPhoneFavorites(prev => prev.filter(f => f.id !== existing.id))
        await deletePhoneFavorite(existing.id)
      } else {
        const res = await addPhoneFavorite({
          contactIdentifier: fav.identifier,
          displayName: fav.displayName,
          phoneNumber: fav.phoneNumber,
          contactType: fav.contactType || 'external',
          linkedContactId: fav.linkedContactId,
          linkedUserEmail: fav.linkedUserEmail,
        })
        if (res.success) await reloadPhoneFavorites()
      }
    } catch (err) {
      console.warn('[phone] Toggle favorite failed', err)
      // Reload to correct optimistic-update drift
      reloadPhoneFavorites()
    }
  }, [phoneFavorites, reloadPhoneFavorites])

  const dialPhoneFavorite = useCallback((fav: PhoneFavorite) => {
    // D1 rows are snake_case (phone_number). Fall back to identifier — for external
    // favorites the identifier IS the phone number in raw digits.
    const raw = fav.phone_number || fav.contact_identifier || ''
    const digits = raw.replace(/\D/g, '').replace(/^1/, '')
    if (!digits) return
    setDialerInput(digits)
    setDialerSearch(formatPhoneDisplay(digits))
    setActiveCallContact({
      name: fav.display_name || raw || digits,
      phone: raw || digits,
      id: fav.linked_contact_id || undefined,
    })
    setCallingPanelTab('keypad')
    setPhoneScreenMode('app')
    // Place the call immediately (iOS behavior) — the in-call screen mounts
    // on the spot. The explicit number sidesteps the just-seeded dialerInput
    // state, which hasn't committed yet in this tick.
    void startCallingPanelCall(digits)
  }, [startCallingPanelCall])

  // Drag-and-drop reorder for favorites. HTML5 DnD, persisted via API.
  const onFavoriteDragStart = useCallback((id: number) => { setPhoneFavoriteDragId(id) }, [])
  const onFavoriteDragEnd   = useCallback(() => { setPhoneFavoriteDragId(null) }, [])
  const onFavoriteDropBefore = useCallback(async (targetId: number) => {
    const sourceId = phoneFavoriteDragId
    setPhoneFavoriteDragId(null)
    if (!sourceId || sourceId === targetId) return
    const current = [...phoneFavorites]
    const fromIdx = current.findIndex(f => f.id === sourceId)
    const toIdx   = current.findIndex(f => f.id === targetId)
    if (fromIdx < 0 || toIdx < 0) return
    const [moved] = current.splice(fromIdx, 1)
    current.splice(toIdx, 0, moved)
    setPhoneFavorites(current)
    try {
      await reorderPhoneFavorites(current.map(f => f.id))
    } catch (err) {
      console.warn('[phone] Reorder failed; reloading', err)
      reloadPhoneFavorites()
    }
  }, [phoneFavoriteDragId, phoneFavorites, reloadPhoneFavorites])

  const saveCallReview = async () => {
    // Preferred path: if we have the call's own activity id from
    // /call-start, fold the review (description/outcome/type) into
    // that same row so the Calls section shows ONE entry per call.
    const callActId = reviewableCallActivityIdRef.current
    if (callActId) {
      reviewableCallActivityIdRef.current = null
      // Include the owner ONLY when the staged chip differs from the hydrated
      // initial value (call-assign-link-v1). Absent = owner untouched; a changed
      // chip sends { ownerTypeId, ownerId }; an unassign sends { clearOwner:true }.
      const assignStaged = !sameAssign(callAssignSelection, callAssignInitialRef.current)
      const ownerPatch = assignStaged ? assignOwnerPatch(callAssignSelection) : {}
      let ok = true
      try {
        const resp = await fetch(`/api/crm/rc/activities/${callActId}/review`, {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            description: callNotes || undefined,
            subject: `Call — ${callOutcomeSelected || 'Completed'}`,
            callOutcome: callOutcomeSelected || undefined,
            callType: callTypeSelected || undefined,
            ...ownerPatch,
          }),
        })
        ok = resp.ok
      } catch (err) { console.warn('[phone] saveCallReview PATCH failed:', err); ok = false }
      // A failed PATCH that carried a staged assignment surfaces an inline error
      // and keeps the sheet open (skip the 1200ms auto-close) so the assignment
      // isn't silently dropped. Restore the reviewable id so a retry re-folds
      // into the SAME row. Non-assignment failures keep the prior fire-and-forget
      // behaviour (the row's notes/outcome are best-effort).
      if (!ok && assignStaged) {
        reviewableCallActivityIdRef.current = callActId
        setAssignSaveError("Couldn't save assignment")
        return
      }
      setAssignSaveError(null)
      setCallSaved(true)
      setTimeout(() => closeCallingPanel({ keepOpen: true }), 1200)
      return
    }
    if (activeCallContact?.id || callAssignSelection) {
      // Route the activity to the correct owner based on the id prefix set
      // by whoever opened the phone (see `dialerSearchResults`, `matchPhoneNumber`,
      // `dialPhoneFavorite`, etc. — they prefix ids like `deal-123` / `lead-456`
      // / `contact-ABC` / `client-ABC`). Previous version hardcoded
      // ownerTypeId=1 (lead) and stripped non-digits from the id, so deals
      // and contacts got filed as leads with the wrong numeric owner.
      const base = activeCallContact?.id
        ? resolveCallActivityOwner(String(activeCallContact.id))
        : { ownerTypeId: 0, ownerId: 0 as number | string, unifiedClientId: undefined as string | undefined }
      let ownerTypeId: number = base.ownerTypeId
      let ownerId: number | string = base.ownerId
      const unifiedClientId = base.unifiedClientId
      // A staged wrap-up chip OVERRIDES the id-prefix owner (call-assign-link-v1);
      // unifiedClientId is kept for contact linkage. A project owner keeps its
      // TEXT id verbatim (createNote.ownerId is widened to number|string by BU-3).
      if (callAssignSelection) {
        if (callAssignSelection.type === 'project') { ownerTypeId = 3; ownerId = String(callAssignSelection.id) }
        else { ownerTypeId = 2; const oid = Number(callAssignSelection.id); ownerId = Number.isFinite(oid) ? oid : callAssignSelection.id }
      }
      if (ownerId || unifiedClientId) {
        await createNote({
          subject: `Call — ${callOutcomeSelected || 'Completed'}`,
          description: callNotes,
          providerType: 'CALL',
          ownerTypeId: ownerTypeId || undefined,
          ownerId: ownerId || undefined,
          unifiedClientId,
          callOutcome: callOutcomeSelected || undefined,
          callType: callTypeSelected || undefined,
        }).catch(err => console.warn('[phone] saveCallReview failed:', err))
      }
    }
    setAssignSaveError(null)
    setCallSaved(true)
    setTimeout(() => closeCallingPanel({ keepOpen: true }), 1200)
  }

  // calls-list-assign-v1 — assign/clear a call's deal/project owner directly from
  // the Calls list "DEAL OR PROJECT" cell. Reuses the review owner contract
  // (PATCH /api/crm/rc/activities/:id/review with an OWNER-ONLY body — no
  // description/subject/outcome, so review fields are untouched). Optimistically
  // patches the row (linked_* + owner_*) so the cell reflects the change without
  // a reload; rolls back + toasts on failure.
  const handleAssignCallOwner = useCallback(async (callId: number, sel: CallOwnerSelection): Promise<boolean> => {
    // Capture ONLY the prior row (not a whole-array snapshot) so a failed PATCH
    // rolls back this one row functionally — preserving any callsData change
    // (page/filter refetch, a second assignment) that landed in between.
    let prevRow: CrmActivity | undefined
    setCallsData((rows) => { prevRow = rows.find((r) => r.id === callId); return applyCallOwnerOptimistic(rows, callId, sel) })
    try {
      const resp = await fetch(`/api/crm/rc/activities/${callId}/review`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ownerPatchFromSelection(sel)),
      })
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      setDealToast({ id: Date.now(), type: 'success', message: sel ? `Call assigned to ${sel.type === 'project' ? 'project' : 'deal'} — ${sel.label}` : 'Call assignment cleared' })
      return true
    } catch (err) {
      console.warn('[calls] assign owner failed:', err)
      setCallsData((rows) => restoreCallOwnerRow(rows, callId, prevRow))
      setDealToast({ id: Date.now(), type: 'error', message: "Couldn't save assignment — reverted" })
      return false
    }
  }, [])

  // calls-list-assign-v1 — the deal/project linkage chip shown on a CALL card in
  // the contact / client 360 timeline when the call is owner-assigned. Reuses the
  // muted provenance-chip style; deep-links to the deal/project record. Degrades
  // to nothing for contact-only / lead-owned rows (matches the existing rollup
  // provenance chip). Prefers an enriched name (linked_label / rollup_source_name)
  // and falls back to a generic label so it works without extra API enrichment.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderCallOwnerChip = (act: any) => {
    const ot = Number(act?.owner_type_id)
    let hash = ''
    let label = ''
    if (isProjectOwnedRow(act)) {
      hash = `/projects/record/${encodeURIComponent(String(act.owner_id))}`
      label = act.rollup_source_name || act.linked_label || act.project_name || 'Project'
    } else if (ot === 2 && act?.owner_id) {
      hash = `/deals/record/${act.owner_id}`
      label = act.linked_label || `Deal #${act.owner_id}`
    } else {
      return null
    }
    return (
      <a
        href={`#${hash}`}
        onClick={(e) => e.stopPropagation()}
        data-marker="calls-list-assign-v1"
        title={label}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.72rem', fontWeight: 600, color: '#0a4f95', background: '#eff6ff', border: '1px solid #dbeafe', borderRadius: 999, padding: '1px 8px', maxWidth: 220, overflow: 'hidden', textDecoration: 'none' }}
      >
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M20 7h-3V5a2 2 0 0 0-2-2H9a2 2 0 0 0-2 2v2H4a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"/><path d="M9 7V5h6v2"/></svg>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      </a>
    )
  }

  // --- Inline cell editing helpers ---
  const totalUnsaved = Object.values(cellEdits).reduce((acc, fields) => acc + Object.keys(fields).length, 0)

  const startCellEdit = (clientId: string, field: string, currentValue: string) => {
    setActiveCell({ clientId, field, value: currentValue })
  }

  const commitActiveCell = () => {
    if (!activeCell) return
    const { clientId, field, value } = activeCell
    // Get the original value from the clientList
    const client = clientList.find((c) => c.unified_client_id === clientId)
    if (!client) { setActiveCell(null); return }
    const originalVal = String((client as Record<string, unknown>)[field] ?? '')
    if (value !== originalVal) {
      setCellEdits((prev) => ({
        ...prev,
        [clientId]: { ...(prev[clientId] ?? {}), [field]: value },
      }))
    }
    setActiveCell(null)
  }

  const cancelAllEdits = () => {
    setCellEdits({})
    setActiveCell(null)
  }

  const saveAllEdits = async () => {
    setSavingEdits(true)
    try {
      // Map DB field names to API camelCase keys
      const fieldMap: Record<string, string> = {
        preferred_name: 'preferredName',
        preferred_phone: 'preferredPhone',
        preferred_email: 'preferredEmail',
        preferred_city: 'preferredCity',
        preferred_street: 'preferredStreet',
        preferred_state: 'preferredState',
        preferred_zip: 'preferredZip',
      }
      await Promise.all(
        Object.entries(cellEdits).map(([clientId, fields]) => {
          const payload: Record<string, unknown> = {}
          for (const [field, val] of Object.entries(fields)) {
            const apiKey = fieldMap[field] ?? field
            payload[apiKey] = val
          }
          return updateClient(clientId, payload)
        })
      )
      // Update local state optimistically
      setClientList((prev) => prev.map((client) => {
        const edits = cellEdits[client.unified_client_id]
        if (!edits) return client
        return { ...client, ...edits } as CrmClient
      }))
      setCellEdits({})
    } catch (err) {
      console.error('Save failed:', err)
    } finally {
      setSavingEdits(false)
    }
  }

  // --- Resizable columns ---
  const onResizeStart = (key: string, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const def = CONTACT_COLUMN_MAP[key]
    resizingCol.current = { key, startX: e.clientX, startWidth: (colWidths as Record<string,number>)[key] ?? def?.width ?? 120 }
    const onMouseMove = (ev: MouseEvent) => {
      // Snapshot the drag ref — the queued updater replays after mouseup may
      // have nulled it (2026-07-01 #/calls P0; same race here).
      const drag = resizingCol.current
      if (!drag) return
      const delta = ev.clientX - drag.startX
      const newWidth = Math.max(60, drag.startWidth + delta)
      setColWidths((prev) => ({ ...prev, [drag.key]: newWidth }))
    }
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
      if (resizingCol.current) {
        setColWidths((prev) => {
          const next = { ...prev }
          localStorage.setItem('botpros-crm-column-widths', JSON.stringify(next))
          return next
        })
        resizingCol.current = null
      }
      const swallow = (ev: MouseEvent) => { ev.preventDefault(); ev.stopPropagation(); document.removeEventListener('click', swallow, true) }
      document.addEventListener('click', swallow, true)
      setTimeout(() => document.removeEventListener('click', swallow, true), 200)
    }
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }

  const confirmDealMoveAction = () => {
    if (dealConfirmMove) {
      executeDealMove(dealConfirmMove.dealId, dealConfirmMove.stageId)
      setDealConfirmMove(null)
    }
  }

  // Fallback: use mock accounts/projects when API data not loaded
  const filteredAccounts = accounts.filter((account) => {
    const value = `${account.company} ${account.contact} ${account.owner} ${account.stage} ${account.nextStep}`.toLowerCase()
    return value.includes(query.toLowerCase())
  })
  const _filteredProjects = projects.filter((project) => {
    const value = `${project.name} ${project.account} ${project.owner} ${project.phase} ${project.health}`.toLowerCase()
    return value.includes(query.toLowerCase())
  }); void _filteredProjects

  const calendarMeetings = calendarDataLoading ? [] : meetings
  const selectedMeetings = (() => {
    const filtered = sortMeetings(calendarMeetings.filter((m) => {
      // Multi-day all-day spans appear on every day from start..end (inclusive);
      // everything else only on its start day.
      const inRange = m.endDate && m.endDate > m.date
        ? m.date <= selectedDate && selectedDate <= m.endDate
        : m.date === selectedDate
      return inRange && isEventVisible(m)
    }))
    // Merge duplicate events (same title + time) — combine owners
    const map = new Map<string, any>()
    for (const m of filtered) {
      const key = `${m.time}|${m.title}`
      if (map.has(key)) {
        const existing = map.get(key)
        if (m.owner && !(existing._allOwners as string[]).includes(m.owner)) {
          (existing._allOwners as string[]).push(m.owner)
        }
      } else {
        map.set(key, { ...m, _allOwners: [m.owner].filter(Boolean) })
      }
    }
    return [...map.values()]
  })()
  const selectedMilestones = projects
    .filter((project) => project.dueDate >= selectedDate)
    .sort((left, right) => left.dueDate.localeCompare(right.dueDate))
    .slice(0, 4)

  // Auto-scroll timeline to current hour (or first event)
  useEffect(() => {
    if (!reelMode || activeView !== 'calendar') return
    const el = timelineRef.current
    if (!el) return
    requestAnimationFrame(() => {
      const hourRows = el.querySelectorAll('.cal-hour-row')
      if (selectedDate === todayKey) {
        // Scroll to current hour (offset by -2 so there's context above)
        const currentHour = Math.max(0, new Date().getHours() - 2)
        const targetRow = hourRows[currentHour] as HTMLElement | undefined
        targetRow?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      } else if (selectedMeetings.length > 0) {
        // Scroll to first event hour
        const firstHour = parseInt(selectedMeetings[0].time?.split(':')[0] || '0')
        const targetRow = hourRows[Math.max(0, firstHour - 1)] as HTMLElement | undefined
        targetRow?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    })
  }, [reelMode, selectedDate, activeView, selectedMeetings.length]) // eslint-disable-line react-hooks/exhaustive-deps

  const commandOwnerOptions = (() => {
    const names = new Set<string>()
    allEmployeeNames.forEach((name) => { if (name) names.add(name) })
    ;(liveDeals ?? []).forEach((deal) => { if (deal.assignedTo) names.add(deal.assignedTo) })
    meetings.forEach((meeting) => { if (meeting.owner) names.add(meeting.owner) })
    commandTasks.forEach((task) => {
      const name = task.assigned_to ? CALENDAR_USERS_MAP[task.assigned_to] : ''
      if (name) names.add(name)
    })
    return [{ key: 'all', label: 'All owners' }, ...Array.from(names).sort((a, b) => a.localeCompare(b)).map((name) => ({ key: name, label: name }))]
  })()
  const ownerNameMatches = (name?: string | null) => {
    if (commandOwnerFilter === 'all') return true
    if (!name) return false
    const selected = commandOwnerFilter.toLowerCase()
    const candidate = String(name).toLowerCase()
    return candidate === selected || candidate.includes(selected) || selected.includes(candidate)
  }
  const homeDeals = (liveDeals ?? []).filter((deal) => ownerNameMatches(deal.assignedTo))
  const commandDeals = homeDeals
  const homeStageSummary = (() => {
    const catalog = new Map<string, { stageId: string; label: string; color: string; sort: number; count: number; value: number }>()
    for (const stage of (homePipeline ?? []).filter((candidate) => !isTerminalPipelineStage(candidate))) {
      const stageDeals = homeDeals.filter((deal) => deal.stage === stage.stageId)
      catalog.set(stage.stageId, {
        stageId: stage.stageId,
        label: stage.label,
        color: stage.color || '#94a3b8',
        sort: stage.sort,
        count: commandOwnerFilter === 'all' ? stage.count : stageDeals.length,
        value: commandOwnerFilter === 'all' ? stage.value : stageDeals.reduce((sum, deal) => sum + (Number(deal.opportunity) || 0), 0),
      })
    }
    for (const deal of homeDeals) {
      if (catalog.has(deal.stage)) continue
      const stage = allPipelineStages?.find((candidate) => candidate.stageId === deal.stage)
      const existing = catalog.get(deal.stage)
      catalog.set(deal.stage, {
        stageId: deal.stage,
        label: deal.stageLabel || stage?.label || deal.stage,
        color: stage?.color || STAGE_COLORS[deal.stage] || '#94a3b8',
        sort: stage?.sort ?? 999,
        count: (existing?.count || 0) + 1,
        value: (existing?.value || 0) + (Number(deal.opportunity) || 0),
      })
    }
    return Array.from(catalog.values()).sort((left, right) => left.sort - right.sort || left.label.localeCompare(right.label))
  })()
  const todayMeetings = sortMeetings(meetings.filter((meeting) => meeting.date === todayKey && ownerNameMatches(meeting.owner)))
  const commandUpcomingMeetings = (days: number) => {
    const endDate = new Date(todayKey)
    endDate.setDate(endDate.getDate() + days)
    const endKey = endDate.toISOString().slice(0, 10)
    return sortMeetings(meetings.filter((meeting) => meeting.date > todayKey && meeting.date <= endKey && ownerNameMatches(meeting.owner)))
  }
  const isTaskClosed = (status?: string | null) => ['completed', 'closed', 'resolved'].includes(String(status || '').toLowerCase())
  const commandFilteredTasks = commandTasks.filter((task) => {
    if (isTaskClosed(task.status)) return false
    const taskOwner = task.assigned_to ? CALENDAR_USERS_MAP[task.assigned_to] : null
    return ownerNameMatches(taskOwner)
  })
  const commandTaskDueKey = (task: CrmTask) => String(task.due_date || '').slice(0, 10)
  const overdueCommandTasks = commandFilteredTasks.filter((task) => {
    const due = commandTaskDueKey(task)
    return due && due < todayKey
  })
  const dueTodayCommandTasks = commandFilteredTasks.filter((task) => commandTaskDueKey(task) === todayKey)
  const highPriorityCommandTasks = commandFilteredTasks.filter((task) => String(task.priority || '').toLowerCase() === 'high')
  const staleCommandDeals = commandDeals.filter((deal) => {
    const modified = deal.dateModified ? Date.parse(deal.dateModified) : NaN
    if (!Number.isFinite(modified)) return false
    return Date.now() - modified > 14 * 86400000
  })
  const unassignedCommandDeals = commandDeals.filter((deal) => !deal.assignedTo)
  const commandLoading = calendarDataLoading || liveDeals === null || homePipeline === null || commandTasksLoading
  const commandPipelineCount = homeStageSummary.reduce((sum, entry) => sum + entry.count, 0)
  const commandPipelineValue = homeStageSummary.reduce((sum, entry) => sum + entry.value, 0)
  const periodLabel = {
    today: 'Today',
    week: 'This Week',
    month: 'This Month',
    quarter: 'This Quarter',
    year: 'This Year',
    all: 'All Time',
  }[dashboardPeriod]
  const wonStagesFull = (homePipeline ?? []).filter(isSuccessPipelineStage)
  const commandWonCount = wonStagesFull.reduce((s, e) => s + (e.count ?? 0), 0)
  const commandWonValue = wonStagesFull.reduce((s, e) => s + (e.value ?? 0), 0)
  const commandWorkQueue = [
    // Pending approvals (time-off + company-event/holiday requests awaiting an
    // approver's action). Surfaced FIRST with a high-attention treatment when
    // there's anything pending so admins don't overlook it; subdued/hidden at 0.
    ...(isCalendarApprover && approvalsBadgeCount > 0
      ? [{ key: 'approvals', label: 'Pending approvals', value: approvalsBadgeCount, tone: 'attention', attention: true, action: () => setApprovalsOpen(true) }]
      : []),
    { key: 'overdue', label: 'Overdue tasks', value: overdueCommandTasks.length, tone: overdueCommandTasks.length > 0 ? 'bad' : 'neutral', action: () => { setTasksFilter('open'); setActiveView('tasks') } },
    { key: 'today', label: 'Due today', value: dueTodayCommandTasks.length, tone: dueTodayCommandTasks.length > 0 ? 'warn' : 'neutral', action: () => { setTasksFilter('open'); setActiveView('tasks') } },
    { key: 'high', label: 'High priority', value: highPriorityCommandTasks.length, tone: highPriorityCommandTasks.length > 0 ? 'bad' : 'neutral', action: () => { setTasksPriorityFilter('high'); setTasksFilter('open'); setActiveView('tasks') } },
    { key: 'stale', label: 'Stale deals', value: staleCommandDeals.length, tone: staleCommandDeals.length > 0 ? 'warn' : 'neutral', action: () => setActiveView('deals') },
    { key: 'unassigned', label: 'Unassigned deals', value: unassignedCommandDeals.length, tone: unassignedCommandDeals.length > 0 ? 'warn' : 'neutral', action: () => setActiveView('deals') },
  ]
  const dashboardLeaderboard = (dashboardData.leaderboard || []).filter((row: any) => {
    if (dashboardOwnerFilter === 'all') return true
    return String(row.user_id) === dashboardOwnerFilter || row.user_name === dashboardOwnerFilter
  })
  const dashboardOwnerOptions = [
    { key: 'all', label: 'All owners' },
    ...(dashboardData.leaderboard || []).map((row: any) => ({ key: String(row.user_id), label: row.user_name || `User ${row.user_id}` })),
  ]
  const dashboardRevenue = dashboardData.revenue || []
  const dashboardFunnelStages = (dashboardData.funnel?.stages || []).filter((stage: any) => (stage.deal_count || 0) > 0)
  const dashboardActivity = dashboardData.actByType || []
  const dashboardWonLost = dashboardData.wonLost || []
  const dashboardSavedCount = savedDashboards.length
  const dashboardDataQuality = [
    { label: 'Pipeline stages without deals', value: Math.max(0, (dashboardData.funnel?.stages || []).filter((stage: any) => !stage.deal_count).length) },
    { label: 'Leaderboard reps without won deals', value: (dashboardData.leaderboard || []).filter((row: any) => !row.deals_won).length },
    { label: 'Saved dashboards configured', value: dashboardSavedCount },
  ]

  const calendarDays = buildCalendar(calendarMonth, calendarMeetings)
  return (
    <div className={`app-shell${sidebarCollapsed ? ' sidebar-collapsed' : ''}${sidebarResizing ? ' sidebar-resizing' : ''}`}>
      <TutorialCreationModeBanner />
      {updateAvailable && (
        <div className="app-update-toast" role="status" aria-live="polite">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 12a9 9 0 1 1-3-6.7" /><path d="M21 3v6h-6" />
          </svg>
          <span className="app-update-toast-msg">A new version is available.</span>
          <button type="button" className="app-update-toast-action" onClick={() => location.reload()}>Reload</button>
          <button type="button" className="app-update-toast-close" onClick={() => setUpdateAvailable(false)} aria-label="Dismiss">&times;</button>
        </div>
      )}
      <header className="global-toolbar">
        <div className="toolbar-left">
          <img src={BP_LOGO_DARK} alt={BP_LOGO_ALT} className="toolbar-logo" data-marker={BP_BRANDING_VERSION} />
        </div>
        <div className="header-search-wrap">
          <div className={`header-search-bar${cmdOpen || cmdQuery ? ' active' : ''}`}>
            {!cmdQuery && <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>}
            <input
              ref={cmdInputRef}
              className="header-search-input"
              placeholder="Search everything..."
              value={cmdQuery}
              onChange={(e) => {
                const val = e.target.value
                setCmdQuery(val)
                if (val.length > 0 && !cmdOpen) { setCmdOpen(true); setCmdResults(null); setCmdSelected(0) }
              }}
              onFocus={() => { if (cmdQuery.length >= 2) { setCmdOpen(true); setCmdSelected(0) } }}
              onKeyDown={handleCmdKeyDown}
            />
            {cmdLoading && <div className="cmd-spinner" />}
            {cmdQuery && (
              <button
                type="button"
                className="cmd-close-btn"
                onClick={() => { setCmdOpen(false); setCmdQuery('') }}
                aria-label="Clear search"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
              </button>
            )}
          </div>
          {cmdOpen && (
            <>
              <div className="cmd-backdrop-light" onClick={() => { setCmdOpen(false) }} />
              <div className="cmd-dropdown">
                {(() => {
                  const allOn = cmdFilters.clients && cmdFilters.leads && cmdFilters.deals && cmdFilters.employees
                  // const activeCount = [cmdFilters.clients, cmdFilters.leads, cmdFilters.deals, cmdFilters.employees].filter(Boolean).length
                  const toggleFilter = (key: keyof typeof cmdFilters) => {
                    setCmdFilters(f => {
                      const allActive = f.clients && f.leads && f.deals && f.employees
                      if (allActive) {
                        // All selected → isolate this one
                        return { clients: false, leads: false, deals: false, employees: false, [key]: true }
                      }
                      if (f[key]) {
                        // Already active → deselect (unless it's the last one)
                        const count = [f.clients, f.leads, f.deals, f.employees].filter(Boolean).length
                        if (count <= 1) return f
                        return { ...f, [key]: false }
                      }
                      // Inactive → activate
                      return { ...f, [key]: true }
                    })
                  }
                  return (
                    <div className="cmd-filters">
                      <button type="button" className={`cmd-filter-pill${cmdFilters.clients ? ' active' : ''}`} onClick={() => toggleFilter('clients')}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                        Contacts
                      </button>
                      <button type="button" className={`cmd-filter-pill${cmdFilters.leads ? ' active' : ''}`} onClick={() => toggleFilter('leads')}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="m3 11 18-5v12L3 13v-2z"/><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/></svg>
                        Leads
                      </button>
                      <button type="button" className={`cmd-filter-pill${cmdFilters.deals ? ' active' : ''}`} onClick={() => toggleFilter('deals')}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                        Deals
                      </button>
                      <button type="button" className={`cmd-filter-pill${cmdFilters.employees ? ' active' : ''}`} onClick={() => toggleFilter('employees')}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                        Employees
                      </button>
                      {!allOn && (
                        <button type="button" className="cmd-filter-clear" onClick={() => setCmdFilters({ clients: true, leads: true, deals: true, employees: true })}>
                          Show all
                        </button>
                      )}
                    </div>
                  )
                })()}
                {(() => {
                  const clients = cmdFilters.clients ? (cmdResults?.results.clients ?? []) : []
                  const leads = cmdFilters.leads ? (cmdResults?.results.leads ?? []) : []
                  const deals = cmdFilters.deals ? (cmdResults?.results.deals ?? []) : []
                  const employees = cmdFilters.employees ? (cmdResults?.results.employees ?? []) : []
                  let runningIdx = 0
                  return (
                    <div className="cmd-results-scroll">
                      {clients.length > 0 && (
                        <div className="cmd-group">
                          <div className="cmd-group-label">Contacts</div>
                          {clients.map((c, ci) => {
                            const idx = runningIdx + ci
                            return (
                              <div
                                key={c.id}
                                className={`cmd-item${cmdSelected === idx ? ' selected' : ''}`}
                                onMouseEnter={() => setCmdSelected(idx)}
                                onClick={() => handleCmdSelect({ type: 'client', id: `client-${c.id}`, label: c.title, subtitle: c.subtitle })}
                              >
                                <div className="cmd-item-text">
                                  <span className="cmd-item-label">{c.title}</span>
                                  <span className="cmd-item-sub">{c.subtitle}</span>
                                </div>
                              </div>
                            )
                          })}
                          {(() => { runningIdx += clients.length; return null })()}
                        </div>
                      )}
                      {leads.length > 0 && (
                        <div className="cmd-group">
                          <div className="cmd-group-label">Leads</div>
                          {leads.map((l, li) => {
                            const idx = runningIdx + li
                            return (
                              <div
                                key={l.id}
                                className={`cmd-item${cmdSelected === idx ? ' selected' : ''}`}
                                onMouseEnter={() => setCmdSelected(idx)}
                                onClick={() => handleCmdSelect({ type: 'lead', id: `lead-${l.id}`, label: l.title, subtitle: l.subtitle })}
                              >
                                <div className="cmd-item-text">
                                  <span className="cmd-item-label">{l.title}</span>
                                  <span className="cmd-item-sub">{l.subtitle}</span>
                                </div>
                              </div>
                            )
                          })}
                          {(() => { runningIdx += leads.length; return null })()}
                        </div>
                      )}
                      {deals.length > 0 && (
                        <div className="cmd-group">
                          <div className="cmd-group-label">Deals</div>
                          {deals.map((d, di) => {
                            const idx = runningIdx + di
                            return (
                              <div
                                key={d.id}
                                className={`cmd-item${cmdSelected === idx ? ' selected' : ''}`}
                                onMouseEnter={() => setCmdSelected(idx)}
                                onClick={() => handleCmdSelect({ type: 'deal', id: `deal-${d.id}`, label: d.title, subtitle: d.subtitle })}
                              >
                                <div className="cmd-item-text">
                                  <span className="cmd-item-label">{d.title}</span>
                                  <span className="cmd-item-sub">{d.subtitle}</span>
                                </div>
                              </div>
                            )
                          })}
                          {(() => { runningIdx += deals.length; return null })()}
                        </div>
                      )}
                      {employees.length > 0 && (
                        <div className="cmd-group">
                          <div className="cmd-group-label">Employees</div>
                          {employees.map((emp, ei) => {
                            const idx = runningIdx + ei
                            return (
                              <div
                                key={emp.id}
                                className={`cmd-item${cmdSelected === idx ? ' selected' : ''}`}
                                onMouseEnter={() => setCmdSelected(idx)}
                                onClick={() => handleCmdSelect({ type: 'employee', id: `employee-${emp.id}`, label: emp.title, subtitle: emp.subtitle })}
                              >
                                <div className="cmd-item-text">
                                  <span className="cmd-item-label">{emp.title}</span>
                                  <span className="cmd-item-sub">{emp.subtitle}</span>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )}
                      {cmdQuery.length >= 2 && !cmdLoading && cmdResults && clients.length === 0 && leads.length === 0 && deals.length === 0 && employees.length === 0 && (
                        <div className="cmd-empty">No results for &ldquo;{cmdQuery}&rdquo;</div>
                      )}
                      {cmdLoading && !cmdResults && (
                        <div className="cmd-group">
                          <div className="cmd-skeleton" /><div className="cmd-skeleton" /><div className="cmd-skeleton" />
                        </div>
                      )}
                    </div>
                  )
                })()}
              </div>
            </>
          )}
        </div>
        <div className="create-menu-wrap" ref={createMenuRef}>
          <button
            className={`create-btn${createMenuOpen ? ' active' : ''}`}
            data-tour-id="create-new-btn"
            onClick={() => {
              setSmartCreateOpen(true)
              setCreateMenuOpen(false)
              setSmartCreateSearch('')
              setSmartCreateResults(null)
              setSmartCreateType(null)
              setCreateFormData({})
              setCreateError(null)
              setEmailTouched(false)
              setCreateEmails([''])
              setCreatePhones([{ type: 'Mobile', number: '', ext: '' }])
            }}
            aria-label="Create new"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor"><path d="M.807 6.769h4.427v4.45a.781.781 0 0 0 1.561 0V6.793h4.427a.781.781 0 0 0 0-1.561H6.795V.781a.781.781 0 0 0-1.561 0v4.427H.807a.781.781 0 0 0 0 1.561Z"/></svg>
          </button>
        </div>
        <div className="toolbar-right" ref={profileRef}>
          {/* === PHONE: JSX — TOPBAR PHONE BUTTON === */}
          {/* TRAP(phone/gate-complementarity): this gate is the EXACT INVERSE of the device-root
              gate (callingPanelOpen && !callingPanelMinimized && !sidebarCollapsed) — change both
              together or the phone becomes unreachable / double-rendered. */}
          {/* Calling Phone Button — visible whenever the docked phone is
           * NOT showing, so the user always has a way to open the phone.
           * Phone widget shows when callingPanelOpen && !callingPanelMinimized
           * && !sidebarCollapsed; this button shows the inverse — i.e. when
           * the sidebar is collapsed, OR the panel is closed/minimized. */}
          {(sidebarCollapsed || !callingPanelOpen || callingPanelMinimized) && (
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }} ref={callingPanelRef}>
            <button
              className={`hs-topbar-icon-btn har-calling-phone-btn${callingPanelOpen ? ' active' : ''}${callingState !== 'idle' && callingState !== 'review' ? ' in-call' : ''}${callingUserMapping ? ' rc-connected' : ''}`}
              title="Calling"
              onClick={(e) => {
                if (callingPanelOpen && callingPanelMinimized) {
                  setCallingPanelMinimized(false)
                  return
                }
                const rect = e.currentTarget.getBoundingClientRect()
                setCallingPanelOpen((p) => {
                  if (!p) {
                    const panelWidth = 336
                    const left = rect.left + rect.width / 2 - panelWidth / 2
                    setCallingPanelPos({ x: Math.max(16, left), y: rect.bottom + 8 })
                    setCallingPanelMinimized(false)
                  }
                  return !p
                })
              }}
              style={{ position: 'relative', color: '#fff', marginRight: '4px' }}
            >
              <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="m22.63,29.99c-1.01,0-2.09-.16-3.24-.48-1.11-.31-2.13-.69-3.11-1.16-.03-.02-.06-.03-.1-.05-2.74-1.36-5.18-3.13-7.27-5.26-2.17-2.13-3.94-4.59-5.28-7.31l-.08-.19c-.19-.37-.37-.8-.58-1.37-1.55-4.11-1.23-7.68.87-9.78l1.53-1.53c1.17-1.16,3.19-1.16,4.36,0l4.07,4.07c.58.58.9,1.36.9,2.19s-.32,1.6-.91,2.19l-2.97,2.97c-.1.1-.15.23-.15.36s.05.26.15.36l6.12,6.12c.19.19.53.19.72,0l2.97-2.97c1.17-1.17,3.21-1.17,4.37,0l4.09,4.08c.58.58.91,1.36.91,2.19s-.32,1.6-.9,2.19l-1.54,1.53c-1.23,1.23-2.92,1.86-4.92,1.86Zm-5.36-3.24c.83.39,1.67.7,2.62.96,2.74.77,4.99.45,6.34-.9l1.54-1.53c.23-.23.36-.54.36-.86s-.13-.63-.36-.87l-4.09-4.08c-.46-.46-1.27-.46-1.73,0l-2.97,2.97c-.9.89-2.46.9-3.36,0l-6.12-6.12c-.45-.45-.7-1.04-.7-1.68s.25-1.23.7-1.68l2.97-2.97c.23-.23.36-.54.36-.87s-.13-.64-.36-.87l-4.07-4.07c-.46-.45-1.26-.45-1.72,0l-1.52,1.52h0c-1.56,1.56-1.72,4.4-.44,7.8.19.5.35.88.53,1.23l.09.19c1.23,2.5,2.88,4.79,4.91,6.77,2,2.04,4.34,3.71,6.97,5,.02.01.05.02.07.04ZM4.5,5.06h0,0Z"/></svg>
              {(smsTotalUnread > 0 || incomingCallToast) && (
                <span style={{ position: 'absolute', top: -4, right: -4, minWidth: 16, height: 16, borderRadius: 8, background: '#ff3b30', color: '#fff', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px', lineHeight: 1, border: '2px solid #1e2a3a' }}>
                  {(smsTotalUnread || 0) + (incomingCallToast ? 1 : 0)}
                </span>
              )}
            </button>
          </div>
          )}
          <div ref={notifRef} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <button
              className="hs-topbar-icon-btn"
              title="Notifications"
              onClick={() => {
                const willOpen = !notifOpen
                if (willOpen) {
                  const r = notifRef.current?.getBoundingClientRect()
                  if (r) setNotifPos({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) })
                  if (currentUserId) {
                    // Pull 50 so the filter tabs have enough history to group meaningfully.
                    fetchNotifications({ limit: 50, userId: currentUserId }).then((res) => setNotifList(res.notifications)).catch(() => {})
                  }
                }
                setNotifOpen((p) => !p)
              }}
              style={{ position: 'relative', color: '#fff', marginRight: '4px' }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
              {(() => {
                // UNIFIED bell badge = the sum of what the dropdown's three tabs show:
                // Unread notifications + pending/unassigned Calls + unread inbox Email.
                // Each source loads eagerly (mount + 15s pollers above), so the badge is
                // accurate before the dropdown is opened. Capped at 99+ like the tabs.
                const bellTotal = notifCount + pendingAssignments.length + (notifEmailUnreadCount ?? 0)
                return bellTotal > 0 ? (
                  <span style={{ position: 'absolute', top: '-2px', right: '-2px', background: '#ff5252', color: '#fff', fontSize: '10px', fontWeight: 700, borderRadius: '50%', minWidth: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px' }}>
                    {bellTotal > 99 ? '99+' : bellTotal}
                  </span>
                ) : null
              })()}
            </button>
            {notifOpen && (() => {
              // Local helpers — kept inline to avoid bloating the module-level
              // API of App.tsx with one-off UI concerns.
              const classify = (title: string): { type: 'call' | 'voicemail' | 'email' | 'mention' | 'assign' | 'deal' | 'task' | 'file' | 'team' | 'other'; color: string; bg: string; icon: React.ReactNode } => {
                const t = (title || '').toLowerCase()
                if (t.includes('voicemail')) return { type: 'voicemail', color: '#7e22ce', bg: '#f3e8ff', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="5.5" cy="14" r="3.5"/><circle cx="18.5" cy="14" r="3.5"/><line x1="5.5" y1="17.5" x2="18.5" y2="17.5"/></svg> }
                if (t.startsWith('incoming call') || t.startsWith('missed call') || t.includes('call from') || t.includes('unassigned call')) return { type: 'call', color: '#b65815', bg: '#fff3e0', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.81.36 1.61.7 2.36a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.75.34 1.55.57 2.36.7A2 2 0 0 1 22 16.92z"/></svg> }
                if (t.includes('mention') || t.includes(' @')) return { type: 'mention', color: '#6d28d9', bg: '#ede9fe', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4"/><path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-3.92 7.94"/></svg> }
                if (t.includes('email') || t.includes('reply')) return { type: 'email', color: '#0a4f95', bg: '#e0e9f5', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg> }
                if (t.includes('assigned')) return { type: 'assign', color: '#0e7490', bg: '#cffafe', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg> }
                if (t.includes('deal') || t.includes('stage')) return { type: 'deal', color: '#166534', bg: '#dcfce7', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg> }
                if (t.includes('task')) return { type: 'task', color: '#b65815', bg: '#fff3e0', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg> }
                if (t.includes('upload') || t.includes(' file')) return { type: 'file', color: '#334155', bg: '#e2e8f0', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg> }
                if (t.includes('team') || t.includes('channel')) return { type: 'team', color: '#4338ca', bg: '#e0e7ff', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg> }
                return { type: 'other', color: '#64748b', bg: '#f1f5f9', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg> }
              }
              // Coalesce near-duplicate rows within a 1h window on (type + subject)
              const ONE_HOUR = 60 * 60 * 1000
              const coalesced: Array<CrmNotification & { count?: number; childIds?: number[] }> = []
              const groupMap = new Map<string, CrmNotification & { count?: number; childIds?: number[] }>()
              for (const n of notifList) {
                const meta = classify(n.title || '')
                const subject = (n.title || '').replace(/^(Incoming call: |Missed call: |Voicemail: )/i, '').trim().toLowerCase()
                const key = `${meta.type}:${subject}`
                const existing = groupMap.get(key)
                const nTime = n.created_at ? new Date(n.created_at).getTime() : 0
                const eTime = existing?.created_at ? new Date(existing.created_at).getTime() : 0
                if (existing && Math.abs(eTime - nTime) < ONE_HOUR) {
                  existing.count = (existing.count || 1) + 1
                  existing.childIds = [...(existing.childIds || [existing.id]), n.id]
                  if (!n.is_read) existing.is_read = 0
                } else {
                  const copy = { ...n, count: 1, childIds: [n.id] } as CrmNotification & { count?: number; childIds?: number[] }
                  coalesced.push(copy)
                  groupMap.set(key, copy)
                }
              }
              // Apply filter
              const filtered = coalesced.filter(n => {
                if (notifFilter === 'all') return true
                if (notifFilter === 'unread') return !n.is_read
                const m = classify(n.title || '').type
                if (notifFilter === 'call') return m === 'call' || m === 'voicemail'
                if (notifFilter === 'email') return m === 'email'
                if (notifFilter === 'mention') return m === 'mention'
                return true
              })
              // Group by time period
              const now = Date.now()
              const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0)
              const yesterdayStart = new Date(todayStart); yesterdayStart.setDate(yesterdayStart.getDate() - 1)
              const weekStart = new Date(todayStart); weekStart.setDate(weekStart.getDate() - 7)
              const groups: Array<{ label: string; items: typeof filtered }> = []
              const today: typeof filtered = [], yesterday: typeof filtered = [], thisWeek: typeof filtered = [], earlier: typeof filtered = []
              for (const n of filtered) {
                const t = n.created_at ? new Date(n.created_at).getTime() : 0
                if (t >= todayStart.getTime()) today.push(n)
                else if (t >= yesterdayStart.getTime()) yesterday.push(n)
                else if (t >= weekStart.getTime()) thisWeek.push(n)
                else earlier.push(n)
              }
              if (today.length) groups.push({ label: 'Today', items: today })
              if (yesterday.length) groups.push({ label: 'Yesterday', items: yesterday })
              if (thisWeek.length) groups.push({ label: 'This week', items: thisWeek })
              if (earlier.length) groups.push({ label: 'Earlier', items: earlier })

              const relTime = (iso?: string) => {
                if (!iso) return ''
                const diff = now - new Date(iso).getTime()
                if (diff < 60_000) return 'now'
                if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m`
                if (diff < 86400_000) return `${Math.floor(diff / 3600_000)}h`
                if (diff < 86400_000 * 2) return 'yesterday'
                if (diff < 86400_000 * 7) return `${Math.floor(diff / 86400_000)}d`
                return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
              }

              const displayTitle = (raw: string) => (raw || '').replace(/^(Incoming call: |Missed call: |Voicemail: )/i, '').trim()

              const tabs: Array<{ key: typeof notifFilter; label: string; count?: number }> = [
                { key: 'all', label: 'All' },
                { key: 'unread', label: 'Unread', count: coalesced.filter(n => !n.is_read).length },
                { key: 'mention', label: '@' },
                // Surface the pending/unassigned-call count on the Calls tab (replaces the
                // old standalone topbar bell + modal).
                { key: 'call', label: 'Calls', count: pendingAssignments.length },
                // Email tab count = UNREAD inbox count (Gmail). Uses the
                // folder unreadCount when we've fetched it, else the visible row
                // count. Capped to 99+ like the Calls tab.
                { key: 'email', label: 'Email', count: notifEmailUnreadCount ?? (notifEmailThreads ? notifEmailThreads.length : undefined) },
              ]

              // --- Unassigned (pending) calls, rendered as a "Needs attention" section
              //     inside the Calls tab. Source: pendingAssignments state (fetched on mount
              //     from GET /api/crm/rc/pending) — a DIFFERENT table than `notifications`,
              //     merged here in the frontend only. Caller display NEVER shows raw JSON:
              //     deal_ids is JSON.parsed and the call subject / formatted phone is shown. ---
              type ParsedPending = {
                pa: PendingCallAssignment
                caller: string
                sub: string
                when?: string
                dealIds: number[]
                needsContactReview: boolean
                // Stable per-caller group key (E.164 of the non-company-line party, or a
                // `solo:` fallback when no usable other-party number exists).
                groupPhone: string
                // The raw "other party" digits (or '') used for display fallback.
                otherDigits: string
              }
              // The main company line. The caller's number swaps between
              // phone_from/phone_to by call direction, so the stable caller key is the
              // OTHER party — whichever side is NOT this company line. (Placeholder —
              // set to the BotPros RingCentral main line when telephony is lit up.)
              const BOTPROS_COMPANY_LINE = '+10000000000'
              const e164 = (raw: string | null | undefined): string => {
                const d = (raw || '').replace(/[^\d]/g, '')
                if (!d) return ''
                // Normalize to +1XXXXXXXXXX where possible so both sides compare cleanly.
                if (d.length === 11 && d.startsWith('1')) return `+${d}`
                if (d.length === 10) return `+1${d}`
                return `+${d}`
              }
              const parsePending = (pa: PendingCallAssignment): ParsedPending => {
                // Caller label: prefer the activity subject; else a nicely-formatted phone number.
                const rawPhone = (pa.phone_from || pa.phone_to || '').replace(/[^\d]/g, '')
                const subject = (pa.call_subject || '').trim()
                const caller = subject || (rawPhone ? formatPhoneDisplay(rawPhone) : 'Unknown caller')
                // Group key = the "other party" (non-company-line) E.164 number. The caller's
                // number flips between from/to by direction; the company line is the constant.
                const fromE = e164(pa.phone_from)
                const toE = e164(pa.phone_to)
                let groupPhone = ''
                if (fromE && fromE !== BOTPROS_COMPANY_LINE) groupPhone = fromE
                else if (toE && toE !== BOTPROS_COMPANY_LINE) groupPhone = toE
                else groupPhone = fromE || toE || '' // both company-line or one null → use what we have
                const otherDigits = groupPhone.replace(/[^\d]/g, '')
                if (!groupPhone) groupPhone = `solo:pa-${pa.id}` // no usable number → own row
                // Parse possible deals. Two shapes: a numeric[] (multi-deal) or a
                // { type:'multi_contact', contact_ids:[...] } object (needs manual review).
                let dealIds: number[] = []
                let needsContactReview = false
                if (pa.deal_ids) {
                  try {
                    const parsed = JSON.parse(pa.deal_ids)
                    if (Array.isArray(parsed)) {
                      dealIds = parsed.map((d) => Number(d)).filter((n) => Number.isFinite(n))
                    } else if (parsed && typeof parsed === 'object' && parsed.type === 'multi_contact') {
                      needsContactReview = true
                    }
                  } catch {
                    // Legacy/garbage value — fall back to a manual-review affordance rather
                    // than rendering the raw string.
                    needsContactReview = true
                  }
                }
                const sub = needsContactReview
                  ? 'Unassigned call · needs contact review'
                  : dealIds.length > 0
                    ? `Unassigned call · ${dealIds.length} possible deal${dealIds.length === 1 ? '' : 's'}`
                    : 'Unassigned call · needs review'
                return { pa, caller, sub, when: pa.call_time || pa.created_at, dealIds, needsContactReview, groupPhone, otherDigits }
              }
              const pendingParsed = notifFilter === 'call' ? pendingAssignments.map(parsePending) : []
              // --- Group unassigned calls by caller (keyed on the normalized phone number).
              //     "SONG CINDY" + "BotPros - SONG CINDY" + the raw +1number for the
              //     same caller collapse into ONE group. Singletons render as a normal row. ---
              type PendingGroup = {
                key: string
                callerDisplay: string
                items: ParsedPending[]
                count: number
                latestWhen?: string
              }
              // Strip a redundant "BotPros - " prefix + padding, and reject
              // pure-number subjects (those aren't a real caller name).
              const cleanSubject = (s: string): string => {
                const t = (s || '').replace(/^BotPros\s*-\s*/i, '').trim()
                if (!t) return ''
                if (/^[+\d().\-\s]+$/.test(t)) return '' // pure phone-ish → not a name
                return t
              }
              const pendingGroups: PendingGroup[] = (() => {
                const byKey = new Map<string, ParsedPending[]>()
                for (const p of pendingParsed) {
                  const arr = byKey.get(p.groupPhone)
                  if (arr) arr.push(p)
                  else byKey.set(p.groupPhone, [p])
                }
                const groups: PendingGroup[] = []
                byKey.forEach((items, key) => {
                  // Cleanest caller display: a real (non-numeric) subject across the group,
                  // else the formatted other-party phone, else the raw subject / Unknown.
                  let callerDisplay = ''
                  for (const it of items) {
                    const cs = cleanSubject(it.pa.call_subject || '')
                    if (cs) { callerDisplay = cs; break }
                  }
                  if (!callerDisplay) {
                    const digits = items.find((it) => it.otherDigits)?.otherDigits
                    callerDisplay = digits ? formatPhoneDisplay(digits) : (items[0]?.caller || 'Unknown caller')
                  }
                  // Latest call time across the group (for sort + display).
                  let latestWhen: string | undefined
                  for (const it of items) {
                    const w = it.when
                    if (w && (!latestWhen || w > latestWhen)) latestWhen = w
                  }
                  groups.push({ key, callerDisplay, items, count: items.length, latestWhen })
                })
                // Sort by most-recent call first.
                groups.sort((a, b) => (b.latestWhen || '').localeCompare(a.latestWhen || ''))
                return groups
              })()
              const pendingGroupsVisible = pendingGroups.slice(0, pendingShowLimit)
              const togglePendingGroup = (key: string) => {
                setPendingExpanded((prev) => {
                  const next = new Set(prev)
                  if (next.has(key)) next.delete(key)
                  else next.add(key)
                  return next
                })
              }
              // Dismiss EVERY unassigned call from this caller-group (loop the single-id
              // endpoint; on success remove all of the group's rows from state).
              const dismissPendingGroup = (g: PendingGroup) => {
                const ids = g.items.map((it) => it.pa.id)
                Promise.allSettled(ids.map((id) => dismissPendingAssignment(id))).then(() => {
                  setPendingAssignments((prev) => prev.filter((p) => !ids.includes(p.id)))
                })
              }
              const pendingPhoneIcon = (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.81.36 1.61.7 2.36a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.75.34 1.55.57 2.36.7A2 2 0 0 1 22 16.92z"/></svg>
              )
              const resolvePending = (pa: PendingCallAssignment, dealId: number) => {
                resolvePendingAssignment(pa.id, dealId).then(() => {
                  setPendingAssignments((prev) => prev.filter((p) => p.id !== pa.id))
                }).catch(() => {})
              }
              const dismissPending = (pa: PendingCallAssignment) => {
                dismissPendingAssignment(pa.id).then(() => {
                  setPendingAssignments((prev) => prev.filter((p) => p.id !== pa.id))
                }).catch(() => {})
              }

              return createPortal((
                <div className="notif-dropdown" role="dialog" aria-label="Notifications" ref={notifPopRef} style={{ top: notifPos?.top ?? 56, right: notifPos?.right ?? 12 }}>
                  <div className="notif-header">
                    <div className="notif-header-title">
                      <span>Notifications</span>
                      {notifCount > 0 && <span className="notif-header-count">{notifCount > 99 ? '99+' : notifCount}</span>}
                    </div>
                    <div className="notif-header-actions">
                      {notifCount > 0 && (
                        <button
                          className="notif-markall-btn"
                          title="Mark all as read"
                          aria-label="Mark all as read"
                          disabled={notifMarkingAll}
                          onClick={async () => {
                            if (!currentUserId || notifMarkingAll) return
                            setNotifMarkingAll(true)
                            try {
                              await markAllNotificationsRead(currentUserId)
                              setNotifList(prev => prev.map(n => ({ ...n, is_read: 1 })))
                              setNotifCount(0)
                            } catch { /* ignore */ }
                            finally { setNotifMarkingAll(false) }
                          }}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                          Mark all read
                        </button>
                      )}
                      <button
                        className="notif-header-icon-btn"
                        title="Notification settings"
                        aria-label="Settings"
                        onClick={() => {
                          setNotifOpen(false)
                          setActiveView('preferences')
                          window.location.hash = '/preferences/notifications'
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                      </button>
                    </div>
                  </div>

                  <div className="notif-tabs" role="tablist">
                    {tabs.map(t => (
                      <button
                        key={t.key}
                        role="tab"
                        aria-selected={notifFilter === t.key}
                        className={`notif-tab${notifFilter === t.key ? ' active' : ''}`}
                        onClick={() => setNotifFilter(t.key)}
                      >
                        <span>{t.label}</span>
                        {typeof t.count === 'number' && t.count > 0 && <span className="notif-tab-count">{t.count > 99 ? '99+' : t.count}</span>}
                      </button>
                    ))}
                  </div>

                  <div className="notif-body">
                    {/* Unassigned (pending) calls — shown only on the Calls tab, above the
                        regular call notifications. Each row reuses .notif-item styling. */}
                    {notifFilter === 'call' && pendingGroups.length > 0 && (
                      <>
                        <div className="notif-group-label">Needs attention</div>
                        {pendingGroupsVisible.map((g) => {
                          const isGroup = g.count > 1
                          const expanded = pendingExpanded.has(g.key)
                          // Representative item for a group's "Review" action: the most-recent
                          // call (assigning the contact resolves the rest). For a singleton it's
                          // the only item.
                          const latest = g.items.reduce((best, it) =>
                            (!best || (it.when || '') > (best.when || '')) ? it : best, g.items[0])
                          const groupSub = isGroup
                            ? `${g.count} calls · needs review`
                            : latest.sub
                          return (
                            <React.Fragment key={`pgroup-${g.key}`}>
                              <div className="notif-item unread">
                                <div className="notif-item-icon" style={{ background: '#fff3e0', color: '#b65815' }}>
                                  {pendingPhoneIcon}
                                </div>
                                <div className="notif-item-body">
                                  <div className="notif-item-title">
                                    {isGroup ? (
                                      <button
                                        type="button"
                                        className="notif-pending-grouptoggle"
                                        aria-expanded={expanded}
                                        onClick={(e) => { e.stopPropagation(); togglePendingGroup(g.key) }}
                                        title={expanded ? 'Hide calls' : 'Show calls'}
                                      >
                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform .12s' }}><polyline points="9 18 15 12 9 6"/></svg>
                                        <span>{g.callerDisplay}</span>
                                        <span className="notif-pending-countbadge">{g.count}</span>
                                      </button>
                                    ) : (
                                      g.callerDisplay
                                    )}
                                  </div>
                                  <div className="notif-item-sub">{groupSub}</div>
                                  <div className="notif-pending-actions">
                                    {isGroup ? (
                                      <button
                                        type="button"
                                        className="notif-pending-chip notif-pending-chip-review"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          setNotifOpen(false)
                                          window.location.hash = `/calls?activity=${latest.pa.activity_id}`
                                        }}
                                      >
                                        Review caller
                                      </button>
                                    ) : (latest.needsContactReview || latest.dealIds.length === 0) ? (
                                      <button
                                        type="button"
                                        className="notif-pending-chip notif-pending-chip-review"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          setNotifOpen(false)
                                          window.location.hash = `/calls?activity=${latest.pa.activity_id}`
                                        }}
                                      >
                                        Review call
                                      </button>
                                    ) : (
                                      latest.dealIds.map((dId) => (
                                        <button
                                          key={dId}
                                          type="button"
                                          className="notif-pending-chip"
                                          onClick={(e) => { e.stopPropagation(); resolvePending(latest.pa, dId) }}
                                        >
                                          Assign to Deal #{dId}
                                        </button>
                                      ))
                                    )}
                                  </div>
                                </div>
                                <div className="notif-item-aside">
                                  <span className="notif-item-time">{relTime(g.latestWhen)}</span>
                                  <button
                                    type="button"
                                    className="notif-item-toggle"
                                    title={isGroup ? 'Dismiss all from this caller' : 'Dismiss'}
                                    aria-label={isGroup ? 'Dismiss all unassigned calls from this caller' : 'Dismiss unassigned call'}
                                    onClick={(e) => { e.stopPropagation(); isGroup ? dismissPendingGroup(g) : dismissPending(latest.pa) }}
                                  >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                  </button>
                                </div>
                              </div>
                              {isGroup && expanded && g.items.map(({ pa, sub, when, dealIds, needsContactReview }) => (
                                <div key={`pending-${pa.id}`} className="notif-item unread notif-pending-childitem">
                                  <div className="notif-item-icon" style={{ background: '#fff3e0', color: '#b65815' }}>
                                    {pendingPhoneIcon}
                                  </div>
                                  <div className="notif-item-body">
                                    <div className="notif-item-sub">{sub}</div>
                                    <div className="notif-pending-actions">
                                      {needsContactReview || dealIds.length === 0 ? (
                                        <button
                                          type="button"
                                          className="notif-pending-chip notif-pending-chip-review"
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            setNotifOpen(false)
                                            window.location.hash = `/calls?activity=${pa.activity_id}`
                                          }}
                                        >
                                          Review call
                                        </button>
                                      ) : (
                                        dealIds.map((dId) => (
                                          <button
                                            key={dId}
                                            type="button"
                                            className="notif-pending-chip"
                                            onClick={(e) => { e.stopPropagation(); resolvePending(pa, dId) }}
                                          >
                                            Assign to Deal #{dId}
                                          </button>
                                        ))
                                      )}
                                    </div>
                                  </div>
                                  <div className="notif-item-aside">
                                    <span className="notif-item-time">{relTime(when)}</span>
                                    <button
                                      type="button"
                                      className="notif-item-toggle"
                                      title="Dismiss"
                                      aria-label="Dismiss unassigned call"
                                      onClick={(e) => { e.stopPropagation(); dismissPending(pa) }}
                                    >
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </React.Fragment>
                          )
                        })}
                        {pendingGroups.length > pendingGroupsVisible.length && (
                          <button
                            type="button"
                            className="notif-pending-showmore"
                            onClick={() => setPendingShowLimit((l) => l + 30)}
                          >
                            Show {Math.min(30, pendingGroups.length - pendingGroupsVisible.length)} more callers
                          </button>
                        )}
                      </>
                    )}
                    {/* Email tab — UNREAD inbox emails from Gmail (not the
                        `notifications` table). Self-contained: loading / error /
                        rows / empty. Replaces the old (always-empty) email filter. */}
                    {notifFilter === 'email' && (
                      notifEmailLoading && !notifEmailThreads ? (
                        <div className="notif-empty">
                          <div className="notif-email-spinner" aria-hidden="true" />
                          <div className="notif-empty-sub">Loading your unread email…</div>
                        </div>
                      ) : notifEmailError ? (
                        <div className="notif-empty">
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                          <div className="notif-empty-title">Couldn't load email</div>
                          <div className="notif-empty-sub">{notifEmailError}</div>
                          <button
                            type="button"
                            className="notif-empty-cta"
                            onClick={() => { setNotifOpen(false); setActiveView('email') }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></svg>
                            Open Email
                          </button>
                        </div>
                      ) : (notifEmailThreads && notifEmailThreads.length > 0) ? (
                        <>
                          <div className="notif-group-label">Unread inbox</div>
                          {notifEmailThreads.map(t => {
                            const sender = t.participants && t.participants.length > 0
                              ? (t.participants[0].name || t.participants[0].email)
                              : 'Unknown sender'
                            const subj = (t.subject || '(no subject)').trim()
                            const snippet = (t.preview || '').trim()
                            const sub = snippet ? `${subj} — ${snippet}` : subj
                            return (
                              <div
                                key={`email-${t.id}`}
                                className="notif-item unread"
                                role="button"
                                tabIndex={0}
                                onClick={() => { setNotifOpen(false); setActiveView('email') }}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); (e.currentTarget as HTMLDivElement).click() } }}
                              >
                                <div className="notif-item-icon" style={{ background: '#fff3e0', color: '#b65815' }}>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></svg>
                                </div>
                                <div className="notif-item-body">
                                  <div className="notif-item-title">{sender}</div>
                                  <div className="notif-item-sub">{sub.length > 110 ? `${sub.substring(0, 110)}…` : sub}</div>
                                </div>
                                <div className="notif-item-aside">
                                  <span className="notif-item-time">{relTime(t.lastMessageAt)}</span>
                                </div>
                              </div>
                            )
                          })}
                          <button
                            type="button"
                            className="notif-pending-showmore"
                            onClick={() => { setNotifOpen(false); setActiveView('email') }}
                          >
                            View all in Email
                          </button>
                        </>
                      ) : (
                        <div className="notif-empty">
                          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>
                          <div className="notif-empty-title">You're all caught up</div>
                          <div className="notif-empty-sub">No unread emails.</div>
                          <button
                            type="button"
                            className="notif-empty-cta"
                            onClick={() => { setNotifOpen(false); setActiveView('email') }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></svg>
                            Open Email
                          </button>
                        </div>
                      )
                    )}
                    {notifFilter !== 'email' && (groups.length === 0 ? (
                      (notifFilter === 'call' && pendingParsed.length > 0) ? null : (
                        <div className="notif-empty">
                          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>
                          <div className="notif-empty-title">You're all caught up</div>
                          <div className="notif-empty-sub">
                            {notifFilter === 'unread' ? 'No unread notifications.' : notifFilter === 'all' ? 'Nothing new for now.' : `No ${notifFilter === 'call' ? 'calls' : 'mentions'} to show.`}
                          </div>
                        </div>
                      )
                    ) : groups.map(g => (
                      <React.Fragment key={g.label}>
                        <div className="notif-group-label">{g.label}</div>
                        {g.items.map(n => {
                          const meta = classify(n.title || '')
                          const title = displayTitle(n.title || '')
                          return (
                            <div
                              key={n.id}
                              className={`notif-item${!n.is_read ? ' unread' : ''}`}
                              role="button"
                              tabIndex={0}
                              onClick={() => {
                                // Mark the whole coalesced group as read
                                const ids = n.childIds || [n.id]
                                const unreadCount = ids.length
                                Promise.all(ids.map(id => markNotificationRead(id).catch(() => null))).then(() => {
                                  setNotifList(prev => prev.map(x => ids.includes(x.id) ? { ...x, is_read: 1 } : x))
                                  setNotifCount(c => Math.max(0, c - unreadCount))
                                })
                                if (n.link_url) {
                                  setNotifOpen(false)
                                  window.location.hash = n.link_url.startsWith('#') ? n.link_url.slice(1) : n.link_url
                                  // Time-off / company-event approval notifications: in addition to
                                  // navigating to the calendar, open the Approvals queue so the approver
                                  // lands on the request itself, not just the calendar. The notification
                                  // carries no request id today, so we open the pending list (all-pending);
                                  // the modal is gated on isCalendarApprover and is a no-op for others.
                                  if (/time-off request|approval|to approve|pending request/i.test(n.title || '')) {
                                    setApprovalsOpen(true)
                                  }
                                } else if (meta.type === 'email') {
                                  // Email notifications carry no link_url today, so they were a dead end.
                                  // Route to the Email view (#/email) — the same canonical in-app nav the
                                  // sidebar Email button uses — so the row actually takes you somewhere.
                                  setNotifOpen(false)
                                  setActiveView('email')
                                }
                              }}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); (e.currentTarget as HTMLDivElement).click() } }}
                            >
                              <div className="notif-item-icon" style={{ background: meta.bg, color: meta.color }}>
                                {meta.icon}
                              </div>
                              <div className="notif-item-body">
                                <div className="notif-item-title">
                                  {title || 'Notification'}
                                  {(n.count || 1) > 1 && <span className="notif-item-count">×{n.count}</span>}
                                </div>
                                {n.body && <div className="notif-item-sub">{n.body.substring(0, 90)}</div>}
                              </div>
                              <div className="notif-item-aside">
                                <span className="notif-item-time">{relTime(n.created_at)}</span>
                                <button
                                  className="notif-item-toggle"
                                  title={n.is_read ? 'Mark as unread' : 'Mark as read'}
                                  aria-label={n.is_read ? 'Mark as unread' : 'Mark as read'}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    const ids = n.childIds || [n.id]
                                    const makeRead = !n.is_read
                                    Promise.all(ids.map(id => markNotificationRead(id, makeRead).catch(() => null))).then(() => {
                                      setNotifList(prev => prev.map(x => ids.includes(x.id) ? { ...x, is_read: makeRead ? 1 : 0 } : x))
                                      setNotifCount(c => makeRead ? Math.max(0, c - ids.length) : c + ids.length)
                                    })
                                  }}
                                >
                                  {n.is_read
                                    ? <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true"><circle cx="12" cy="12" r="5"/></svg>
                                    : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>}
                                </button>
                              </div>
                            </div>
                          )
                        })}
                      </React.Fragment>
                    )))}
                  </div>

                  <div className="notif-footer">
                    {notifFilter === 'email' && (notifEmailThreads == null || notifEmailThreads.length === 0) && (
                      <button
                        className="notif-footer-btn"
                        onClick={() => { setNotifOpen(false); setActiveView('email') }}
                      >
                        View all in Email
                      </button>
                    )}
                    <button
                      className="notif-footer-btn"
                      onClick={() => {
                        setNotifOpen(false)
                        setActiveView('preferences')
                        window.location.hash = '/preferences/notifications'
                      }}
                    >
                      Notification preferences
                    </button>
                  </div>
                </div>
              ), document.body)
            })()}
          </div>
          {/* Knowledge Base / Help & Tutorials */}
          <button
            className="hs-topbar-icon-btn"
            title="Help & Tutorials"
            onClick={() => setKbOpen(o => !o)}
            style={{ position: 'relative', color: '#fff', marginRight: '4px' }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
          </button>
          {/* Support & feedback — lifebuoy (marker: support-hub-icon). Owns its
              own badge polling + body-portaled modal (lazy chunk); the modal
              overlay stacks at z 9600 — above content, below the docked phone
              (9998) and the >=10001 popover ladder. */}
          <SupportHub />
          {/* Unassigned calls were a standalone topbar bell + modal; they now live inside the
              notification-center "Calls" tab (see the notif-dropdown render below). */}
          <div className="toolbar-divider" />

          {/* Shift clock — separate from the deal timesheet clock. Clocking
              IN is one click; while on the clock the button opens the
              confirmation pop-out (lunch / break / clock out). */}
          <button
            className="hs-topbar-icon-btn"
            onClick={() => { if (!tcOpen) doTcPunch('in'); else setTcMenuOpen(true) }}
            disabled={tcPunching}
            title={!tcOpen
              ? 'Clock in for your shift'
              : tcOpen.kind === 'work'
                ? `Clocked in since ${new Date(tcOpen.clock_in).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
                : `On ${tcOpen.kind} since ${new Date(tcOpen.clock_in).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, width: 'auto', padding: '0 10px',
              borderRadius: 16, color: '#fff', marginLeft: 4,
              background: !tcOpen ? 'rgba(255,255,255,0.08)' : tcOpen.kind === 'work' ? 'rgba(34,197,94,0.25)' : 'rgba(245,158,11,0.3)',
              border: !tcOpen ? '1px solid transparent' : tcOpen.kind === 'work' ? '1px solid rgba(74,222,128,0.5)' : '1px solid rgba(251,191,36,0.6)',
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            <span style={{ fontSize: '0.74rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
              {!tcOpen ? 'Clock in' : tcOpen.kind === 'work'
                ? <TcElapsed since={tcOpen.clock_in} />
                : <>{tcOpen.kind === 'lunch' ? 'Lunch' : 'Break'} · <TcElapsed since={tcOpen.clock_in} /></>}
            </span>
            {tcOpen && <span style={{ width: 7, height: 7, borderRadius: '50%', background: tcOpen.kind === 'work' ? '#4ade80' : '#fbbf24', boxShadow: tcOpen.kind === 'work' ? '0 0 6px rgba(74,222,128,0.9)' : '0 0 6px rgba(251,191,36,0.9)' }} />}
          </button>

          {/* Forgot-to-clock-out prompt — files an approval-gated correction */}
          {tcForgotShow && tcOpen && (
            <ForgotClockOutModal
              open={{ id: tcOpen.id, clock_in: tcOpen.clock_in }}
              onDismiss={() => {
                try { sessionStorage.setItem(`tcForgotSnooze:${tcOpen.id}`, '1') } catch { /* ignore */ }
                tcForgotCheckedRef.current = tcOpen.id
                setTcForgotShow(false)
              }}
              onFiled={() => {
                try { sessionStorage.setItem(`tcForgotSnooze:${tcOpen.id}`, '1') } catch { /* ignore */ }
                tcForgotCheckedRef.current = tcOpen.id
                setTcForgotShow(false)
              }}
            />
          )}

          {/* Clock pop-out: confirm clock-out, take lunch/break, resume */}
          {tcMenuOpen && tcOpen && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 10010, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 90 }} onClick={() => !tcPunching && setTcMenuOpen(false)}>
              <div style={{ background: '#fff', borderRadius: 12, padding: 22, width: 330, boxShadow: '0 12px 36px rgba(0,0,0,0.3)' }} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: tcOpen.kind === 'work' ? '#22c55e' : '#f59e0b' }} />
                  <h3 style={{ margin: 0, fontSize: '1rem', color: '#1e293b' }}>
                    {tcOpen.kind === 'work' ? 'Clocked in' : tcOpen.kind === 'lunch' ? 'On lunch' : 'On break'}
                  </h3>
                </div>
                <p style={{ margin: '0 0 16px', fontSize: '12.5px', color: '#64748b' }}>
                  Since {new Date(tcOpen.clock_in).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} · <TcElapsed since={tcOpen.clock_in} /> elapsed
                  {tcOpen.kind !== 'work' && ' — your work time is paused'}
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {tcOpen.kind === 'work' ? (
                    <>
                      <button type="button" disabled={tcPunching} onClick={() => doTcPunch('lunch')}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, border: '1px solid #fde68a', background: '#fffbeb', color: '#92400e', fontWeight: 600, fontSize: '0.86rem', cursor: 'pointer' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 11h18"/><path d="M5 11v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>
                        Take lunch
                      </button>
                      <button type="button" disabled={tcPunching} onClick={() => doTcPunch('break')}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', fontWeight: 600, fontSize: '0.86rem', cursor: 'pointer' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17 8h1a4 4 0 1 1 0 8h-1"/><path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4Z"/></svg>
                        Take a break
                      </button>
                    </>
                  ) : (
                    <button type="button" disabled={tcPunching} onClick={() => doTcPunch('resume')}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, border: '1px solid #bbf7d0', background: '#f0fdf4', color: '#15803d', fontWeight: 700, fontSize: '0.86rem', cursor: 'pointer' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                      Back to work
                    </button>
                  )}
                  <button type="button" disabled={tcPunching} onClick={() => doTcPunch('out')}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, border: '1px solid #fecaca', background: '#fef2f2', color: '#b91c1c', fontWeight: 700, fontSize: '0.86rem', cursor: 'pointer' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                    {tcPunching ? 'Working…' : 'Clock out for the day'}
                  </button>
                  <button type="button" disabled={tcPunching} onClick={() => setTcMenuOpen(false)}
                    style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: 'none', color: '#64748b', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {isRealAdmin && (
            <div ref={impersonateRef} style={{ position: 'relative', display: 'flex', alignItems: 'center', marginLeft: 4 }}>
              {/* Standalone impersonation pill removed 2026-05-03 — the indicator
               * now lives ON the profile-trigger to the right of the user's
               * name, so we don't echo the impersonated user's first name twice. */}
              <button
                className="hs-topbar-icon-btn"
                title={impersonating ? `Acting as ${impersonating.name}` : 'Login as...'}
                onClick={() => {
                  if (!impersonatePickerOpen) {
                    const r = impersonateRef.current?.getBoundingClientRect()
                    if (r) setImpersonatePos({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) })
                  }
                  setImpersonatePickerOpen(p => !p); setImpersonateSearch(''); setProfileMenuOpen(false)
                }}
                style={{ position: 'relative', color: '#fff', marginRight: 4 }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><polyline points="17 11 19 13 23 9"/>
                </svg>
                {impersonating && <span style={{ position: 'absolute', top: -2, right: -2, width: 8, height: 8, borderRadius: '50%', background: '#f59e0b', border: '2px solid #1e2a3a' }} />}
              </button>
              {impersonatePickerOpen && createPortal((
                <div ref={impersonatePopRef} style={{ position: 'fixed', top: impersonatePos?.top ?? 52, right: impersonatePos?.right ?? 12, width: 300, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, boxShadow: '0 12px 32px rgba(0,0,0,0.18)', zIndex: 10001, overflow: 'hidden' }}>
                  <div style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6' }}>
                    <input type="text" placeholder="Search employees..." value={impersonateSearch} onChange={e => setImpersonateSearch(e.target.value)} autoFocus
                      style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 7, padding: '7px 10px', fontSize: '0.82rem', outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                  <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                    {impersonating && (
                      <button onClick={() => { /* IMPERSONATE-LANDS-HOME: return to Home, not the impersonated user's route */ window.location.hash = homeRouteHash(); localStorage.removeItem('crm_impersonate'); window.location.reload() }}
                        style={{ width: '100%', padding: '10px 12px', border: 'none', borderBottom: '1px solid #fef3c7', background: '#fffbeb', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8rem', color: '#92400e', fontWeight: 600, textAlign: 'left' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
                        Return to Admin ({impersonating?.adminName || 'Admin'})
                      </button>
                    )}
                    {impersonating && (
                      <div style={{ padding: '8px 12px', borderBottom: '1px solid #e5e7eb', background: '#f0fdf4' }}>
                        <div style={{ fontSize: '0.65rem', fontWeight: 600, color: '#166534', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Currently signed in as</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {(() => {
                            // "Currently signed in as" avatar in the impersonate
                            // picker — same single precedence source as the nav
                            // trigger + dropdown (navAvatarSource): loaded
                            // hrProfile photo → versioned /api/me photo (fresh on
                            // every route) → by-email+ver → initials. DISPLAY-ONLY
                            // (never the admin's prefsPhoto while impersonating).
                            const navPhoto = navAvatarSource({ impersonating: true, hrProfilePhotoUrl: hrProfile?.photoUrl, mePhotoUrl: currentUser?.photoUrl, mePhotoVersion: currentUser?.photoVersion, prefsPhoto })
                            return navPhoto.kind === 'img' ? (
                              <img src={navPhoto.src} alt="" style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} data-avatar-source={navPhoto.source} />
                            ) : (
                              <UserPhotoAvatar
                                email={impersonating.email}
                                version={navPhoto.version}
                                imgStyle={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                                fallback={<div style={{ width: 24, height: 24, borderRadius: '50%', background: '#bbf7d0', color: '#166534', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', fontWeight: 700, flexShrink: 0 }}>
                                  {(impersonating.name || 'U').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                                </div>}
                              />
                            )
                          })()}
                          <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1e293b' }}>{impersonating.name}</div>
                          <span style={{ fontSize: '0.6rem', fontWeight: 600, padding: '2px 6px', borderRadius: 4, background: '#dcfce7', color: '#166534', textTransform: 'uppercase', marginLeft: 'auto', flexShrink: 0 }}>{impersonating.role}</span>
                        </div>
                      </div>
                    )}
                    {(crmUsers || [])
                      .filter(u => u.email && u.name)
                      // You can't impersonate yourself — the signed-in account (the REAL
                      // session, not the impersonated one) stays out of the list. The
                      // "Return to Admin" row covers getting back.
                      .filter(u => (u.email || '').toLowerCase() !== (currentUser?.email || '').toLowerCase())
                      .filter(u => !impersonateSearch || u.name.toLowerCase().includes(impersonateSearch.toLowerCase()) || (u.email || '').toLowerCase().includes(impersonateSearch.toLowerCase()))
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map(u => (
                        <button key={u.id} onClick={() => {
                          const target = { userId: u.id, email: u.email!, name: u.name, role: u.role, isAdmin: (u as any).is_admin === 1 ? 1 : 0, adminName: impersonating ? impersonating.adminName : (currentUser?.name || ''), adminEmail: impersonating ? impersonating.adminEmail : (currentUser?.email || '') }
                          // IMPERSONATE-LANDS-HOME: navigate to Home BEFORE the reload so
                          // starting impersonation lands in a neutral context, never stranded
                          // on the admin's previously-viewed person-specific route. See
                          // lib/routing/homeRoute + docs/REGRESSIONS.md.
                          window.location.hash = homeRouteHash()
                          localStorage.setItem('crm_impersonate', JSON.stringify(target))
                          window.location.reload()
                        }}
                          style={{ width: '100%', padding: '8px 12px', border: 'none', borderBottom: '1px solid #f9fafb', background: impersonating?.userId === u.id ? '#eff6ff' : 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left' }}
                          onMouseEnter={e => { if (impersonating?.userId !== u.id) e.currentTarget.style.background = '#f9fafb' }}
                          onMouseLeave={e => { if (impersonating?.userId !== u.id) e.currentTarget.style.background = 'transparent' }}>
                          <UserPhotoAvatar
                            email={u.email}
                            imgStyle={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                            fallback={<div style={{ width: 28, height: 28, borderRadius: '50%', background: '#e0e7ff', color: '#4338ca', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 700, flexShrink: 0 }}>
                              {(u.initials || u.name.split(' ').map(w => w[0]).join('').slice(0, 2)).toUpperCase()}
                            </div>}
                          />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.name}</div>
                            <div style={{ fontSize: '0.68rem', color: '#9ca3af', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.email}</div>
                          </div>
                          <span style={{ fontSize: '0.6rem', fontWeight: 600, padding: '2px 6px', borderRadius: 4, background: u.role === 'admin' ? '#dbeafe' : '#f1f5f9', color: u.role === 'admin' ? '#1e40af' : '#64748b', textTransform: 'uppercase', flexShrink: 0 }}>{u.role}</span>
                        </button>
                      ))}
                  </div>
                </div>
              ), document.body)}
            </div>
          )}
          <div className="toolbar-divider" />
          <button
            data-tour-id="profile-trigger"
            className={`profile-trigger${impersonating ? ' profile-trigger-impersonating' : ''}`}
            onClick={() => {
              if (!profileMenuOpen) {
                const r = profileRef.current?.getBoundingClientRect()
                if (r) setProfilePos({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) })
              }
              setProfileMenuOpen((p) => !p)
              setImpersonatePickerOpen(false)
            }}
            title={impersonating ? `Acting as ${impersonating.name} (signed in as ${currentUser?.name || currentUser?.email || 'admin'})` : (currentUser?.email || '')}
          >
            {(() => {
              // Nav trigger avatar — SINGLE precedence source (navAvatarSource,
              // docs/REGRESSIONS.md 2026-07-01): (1) loaded hrProfile photo
              // (post-upload instant, self AND impersonated — the SAME source
              // the Profile card renders), (2) versioned /api/me photo (fresh
              // on EVERY route; impersonation-aware), (3) the admin's own
              // prefsPhoto (SELF ONLY — never rendered while impersonating),
              // (4) by-email UserPhotoAvatar with &ver= per-upload busting,
              // (5) initials. DISPLAY-ONLY: never writes prefsPhoto/localStorage.
              const navPhoto = navAvatarSource({ impersonating: !!impersonating, hrProfilePhotoUrl: hrProfile?.photoUrl, mePhotoUrl: currentUser?.photoUrl, mePhotoVersion: currentUser?.photoVersion, prefsPhoto })
              return navPhoto.kind === 'img' ? (
                <img src={navPhoto.src} alt="" className="profile-trigger-avatar-img" data-avatar-source={navPhoto.source} />
              ) : (
                <UserPhotoAvatar
                  email={impersonating ? impersonating.email : currentUser?.email}
                  version={navPhoto.version}
                  imgClassName="profile-trigger-avatar-img"
                  fallback={<div className="profile-trigger-avatar">{((impersonating ? impersonating.name : currentUser?.name) || 'U').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}</div>}
                />
              )
            })()}
            <span className="profile-trigger-name">
              {(impersonating ? impersonating.name : currentUser?.name) || currentUser?.email || 'Profile'}
            </span>
            {impersonating && (
              <>
                <svg className="profile-trigger-impersonate-bolt" width="11" height="11" viewBox="0 0 24 24" fill="#facc15" stroke="none"><path d="M13 2L3 14h9l-1 10 10-12h-9l1-10z"/></svg>
                <span
                  className="profile-trigger-impersonate-x"
                  role="button"
                  tabIndex={0}
                  title="Return to admin"
                  onClick={(e) => { e.stopPropagation(); /* IMPERSONATE-LANDS-HOME */ window.location.hash = homeRouteHash(); localStorage.removeItem('crm_impersonate'); window.location.reload() }}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); /* IMPERSONATE-LANDS-HOME */ window.location.hash = homeRouteHash(); localStorage.removeItem('crm_impersonate'); window.location.reload() } }}
                >×</span>
              </>
            )}
            <svg className="profile-trigger-caret" width="8" height="8" viewBox="0 0 8 8" fill="#fff"><path d="M0.668 1.75h6.667a.676.676 0 0 1 .47.197.676.676 0 0 1 0 .954L4.471 6.281a.676.676 0 0 1-.942 0L.195 2.901a.676.676 0 0 1 0-.954A.666.666 0 0 1 .668 1.75Z"/></svg>
          </button>
          {profileMenuOpen && createPortal((
            <div className="profile-dropdown" ref={profilePopRef} style={{ top: profilePos?.top ?? 52, right: profilePos?.right ?? 12 }}>
              {(!myCapabilities || myCapabilities.access_settings) ? (
                <a className="profile-dd-user" href="#" onClick={(e) => { e.preventDefault(); setActiveView('preferences'); setProfileMenuOpen(false) }}>
                  {(() => {
                    // Profile dropdown avatar — SAME single precedence source as
                    // the trigger (navAvatarSource, docs/REGRESSIONS.md
                    // 2026-07-01): loaded hrProfile photo → versioned /api/me
                    // photo (fresh on every route; impersonation-aware) →
                    // prefsPhoto (self only) → by-email+ver → initials.
                    // DISPLAY-ONLY: never the admin's prefsPhoto while impersonating.
                    const navPhoto = navAvatarSource({ impersonating: !!impersonating, hrProfilePhotoUrl: hrProfile?.photoUrl, mePhotoUrl: currentUser?.photoUrl, mePhotoVersion: currentUser?.photoVersion, prefsPhoto })
                    return navPhoto.kind === 'img' ? (
                      <img className="profile-dd-avatar profile-trigger-avatar-img" src={navPhoto.src} alt="" style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }} data-avatar-source={navPhoto.source} />
                    ) : (
                      <UserPhotoAvatar
                        email={impersonating ? impersonating.email : currentUser?.email}
                        version={navPhoto.version}
                        imgClassName="profile-dd-avatar profile-trigger-avatar-img"
                        imgStyle={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
                        fallback={<div className="profile-dd-avatar">{(effectiveUser?.name || 'U').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}</div>}
                      />
                    )
                  })()}
                  <div className="profile-dd-user-info">
                    <div className="profile-dd-name">{effectiveUser?.name || 'User'}</div>
                    <div className="profile-dd-email">{effectiveUser?.email || ''}</div>
                    <div className="profile-dd-link">Settings</div>
                  </div>
                </a>
              ) : (
                <div className="profile-dd-user" style={{ cursor: 'default' }}>
                  {(() => {
                    // Profile dropdown avatar — SAME single precedence source as
                    // the trigger (navAvatarSource, docs/REGRESSIONS.md
                    // 2026-07-01): loaded hrProfile photo → versioned /api/me
                    // photo (fresh on every route; impersonation-aware) →
                    // prefsPhoto (self only) → by-email+ver → initials.
                    // DISPLAY-ONLY: never the admin's prefsPhoto while impersonating.
                    const navPhoto = navAvatarSource({ impersonating: !!impersonating, hrProfilePhotoUrl: hrProfile?.photoUrl, mePhotoUrl: currentUser?.photoUrl, mePhotoVersion: currentUser?.photoVersion, prefsPhoto })
                    return navPhoto.kind === 'img' ? (
                      <img className="profile-dd-avatar profile-trigger-avatar-img" src={navPhoto.src} alt="" style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }} data-avatar-source={navPhoto.source} />
                    ) : (
                      <UserPhotoAvatar
                        email={impersonating ? impersonating.email : currentUser?.email}
                        version={navPhoto.version}
                        imgClassName="profile-dd-avatar profile-trigger-avatar-img"
                        imgStyle={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
                        fallback={<div className="profile-dd-avatar">{(effectiveUser?.name || 'U').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}</div>}
                      />
                    )
                  })()}
                  <div className="profile-dd-user-info">
                    <div className="profile-dd-name">{effectiveUser?.name || 'User'}</div>
                    <div className="profile-dd-email">{effectiveUser?.email || ''}</div>
                  </div>
                </div>
              )}
              <div className="profile-dd-divider" />
              <a className="profile-dd-item" href="#" onClick={(e) => {
                e.preventDefault()
                const id = effectiveUser?.email || currentUser?.email || ''
                setActiveViewRaw('profile')
                window.location.hash = `/profile/${encodeURIComponent(id)}`
                setProfileMenuOpen(false)
              }}>My profile</a>
              {/* External link to the employee document portal (HR Center
                  standalone app) — deliberately NOT the in-CRM #/my-documents
                  view, per Max 2026-06-10. ops.botpros.ai is the canonical
                  host; the original ops.botpros.ai no longer resolves (the
                  botpros.ai domain was retired 2026-04-22). */}
              {/* "My documents" external HR-portal link hidden in BotPros — no
                  standalone HR portal deployment exists on this account. */}
              <a className="profile-dd-item" href="#" onClick={(e) => {
                e.preventDefault()
                setActiveView('my-time')
                setProfileMenuOpen(false)
              }}>My time</a>
              <div className="profile-dd-divider" />
              <div className="profile-dd-account-label">Account</div>
              <div className="profile-dd-account-name">botpros.ai</div>
              <div className="profile-dd-divider" />
              <a className="profile-dd-item profile-dd-signout" href="#" onClick={(e) => { e.preventDefault(); localStorage.removeItem('userRole'); localStorage.removeItem('crm_impersonate'); persistPrefsPhoto(null, null); const returnTo = encodeURIComponent(window.location.origin + '/'); window.location.href = `/cdn-cgi/access/logout?returnTo=${returnTo}` }}>Sign out</a>
            </div>
          ), document.body)}
        </div>
      </header>

      {/* Click-to-Call Toast (Tier 4) */}
      {ringout && (
        <div
          className="har-ringout-toast"
          onClick={ringout.status === 'ConnectRequired' ? () => { ringoutPollRef.current?.(); ringoutPollRef.current = null; setRingout(null); if (currentUser?.email) window.location.hash = `/profile/${encodeURIComponent(currentUser.email)}` } : undefined}
          style={ringout.status === 'ConnectRequired' ? { cursor: 'pointer' } : undefined}
          title={ringout.status === 'ConnectRequired' ? 'Open your profile to connect RingCentral' : undefined}
        >
          <div className="har-ringout-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.81.36 1.61.7 2.36a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.75.34 1.55.57 2.36.7A2 2 0 0 1 22 16.92z"/></svg>
          </div>
          <div className="har-ringout-info">
            <div className="har-ringout-name">{ringout.contactName || ringout.toNumber}</div>
            <div className="har-ringout-status">
              {ringout.status === 'ConnectRequired' ? 'Connect RingCentral on your profile to place calls →' : ringout.status === 'Disconnected' ? 'Call ended' : ringout.status === 'NoAnswer' ? 'No answer' : ringout.status === 'Busy' ? 'Busy' : ringout.callerStatus === 'CallConnected' ? 'Connected' : 'Ringing...'}
            </div>
          </div>
          <button
            className="har-ringout-dismiss"
            onClick={(e) => { e.stopPropagation(); ringoutPollRef.current?.(); ringoutPollRef.current = null; setRingout(null) }}
          >&times;</button>
        </div>
      )}

      {/* RingCentral Connect result flash — surfaces the OAuth callback outcome
          (was silently swallowed). Error messages distinguish admin-fixable
          config problems (redirect URL / permissions) from user-retry cases. */}
      {rcConnectFlash && (
        <div className="har-ringout-toast" style={{ borderLeft: `3px solid ${rcConnectFlash.ok ? '#2e7d32' : '#b3261e'}` }}>
          <div className="har-ringout-icon" style={{ color: rcConnectFlash.ok ? '#2e7d32' : '#b3261e' }}>
            {rcConnectFlash.ok
              ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
              : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>}
          </div>
          <div className="har-ringout-info">
            <div className="har-ringout-name">{rcConnectFlash.ok ? 'RingCentral connected' : 'Couldn’t connect RingCentral'}</div>
            <div className="har-ringout-status">
              {rcConnectFlash.ok
                ? 'You can now place calls from the browser.'
                : (() => {
                    const r = rcConnectFlash.reason || ''
                    if (r === 'access_denied') return 'You declined the permission request — click Connect to try again.'
                    if (r === 'redirect_uri_mismatch') return 'RingCentral rejected the redirect URL — an admin must add the callback URL to the app.'
                    if (r === 'state_expired' || r === 'bad_state') return 'The connection link expired — please click Connect again.'
                    if (r === 'user_mismatch') return 'Sign in as yourself (not impersonating) and click Connect again.'
                    if (r === 'token_exchange' || r === 'invalid_client' || r === 'unauthorized_client' || r === 'invalid_scope') return 'Sign-in didn’t complete — the RingCentral app likely needs an admin fix (redirect URL or RingOut permission).'
                    return 'Sign-in didn’t complete — try again, or ask an admin to check the RingCentral app config.'
                  })()}
            </div>
          </div>
          <button className="har-ringout-dismiss" onClick={() => setRcConnectFlash(null)}>&times;</button>
        </div>
      )}

      {/* Hidden audio element for WebRTC remote audio */}
      <audio ref={remoteAudioRef} autoPlay style={{ display: 'none' }} />

      {/* === PHONE: JSX — DEVICE ROOT === contiguous device JSX from this gate to the minimized
          badge (.har-calling-minimized-badge); the gate must stay the exact inverse of the topbar
          phone button's — TRAP(phone/gate-complementarity). */}
      {/* TRAP(phone/remote-audio-outside-gate): the hidden <audio ref={remoteAudioRef}> just above
          stays OUTSIDE this gate forever — call audio must survive device unmount/minimize. */}
      {/* Calling Panel — Smartphone Emulator. Hidden whenever the sidebar
       * is collapsed: the docked phone visually lives inside the expanded
       * sidebar; with the sidebar at 56px wide there's no room for it, and
       * the user can still reach the calling UI via the topbar phone icon
       * (which only renders in the collapsed state). */}
      {callingPanelOpen && !callingPanelMinimized && !sidebarCollapsed && (
        <div tabIndex={-1} onMouseEnter={resetPhoneIdle} onMouseMove={resetPhoneIdle} onKeyDown={resetPhoneIdle} onInput={resetPhoneIdle} onMouseDown={resetPhoneIdle} className={`phone-device${phoneDocked ? ' phone-docked' : ''}${phoneUndocking ? ' phone-undocking' : ''}${phoneDragging ? ' phone-dragging' : ''}${phoneIdle ? ' phone-idle' : ''}`} style={!phoneDocked && callingPanelPos ? { position: 'fixed', left: callingPanelPos.x, top: callingPanelPos.y, right: 'auto', bottom: 'auto' } : undefined} onClick={(e) => { resetPhoneIdle(); if (callingSettingsOpen) setCallingSettingsOpen(false); const t = e.target as HTMLElement; if (t.tagName !== 'INPUT' && t.tagName !== 'TEXTAREA' && t.tagName !== 'SELECT') (e.currentTarget as HTMLElement).focus() }}>
          {/* === PHONE: JSX — IDLE SCREEN + OVERLAY STACK === in-screen z ladder (inside
              .phone-screen, isolation:isolate): home-dock 25 < notch 30 = desk-mirror 30 <
              notch-btn-wrap 31 < settings 40 = iphone-notif 40 < ios-call-banner 45 <
              incoming-overlay 50; equal z resolves by DOM order. docs/PHONE_ARCHITECTURE.md §z-registry */}
          {/* TRAP(phone/zoom-divide): this subtree renders under zoom — elements positioned from
              clientX/Y must divide by the device's computed zoom (see the .phone-touch-cursor
              mousemove below); .phone-touch-cursor stays the immediately-following sibling of
              .phone-screen. */}
          {/* ── DIALER STATE ── */}
          {callingState === 'idle' && (
            <>
              {/* Phone Screen — all-screen iPhone X style */}
              <div className="phone-screen"
                onMouseMove={e => {
                  if (phoneTouchRef.current) {
                    // The touch dot is position:fixed INSIDE the zoomed
                    // .phone-device subtree, so its left/top px get multiplied
                    // by the device's zoom (--phone-scale). Divide the viewport
                    // coords by the effective zoom or the dot lands offset from
                    // the real cursor whenever the phone is scaled up.
                    const dev = (e.currentTarget as HTMLElement).closest('.phone-device')
                    const z = dev ? parseFloat((getComputedStyle(dev) as any).zoom) || 1 : 1
                    phoneTouchRef.current.style.left = (e.clientX / z) + 'px'
                    phoneTouchRef.current.style.top = (e.clientY / z) + 'px'
                  }
                  setPhoneTouchVisible(true)
                }}
                onMouseLeave={() => setPhoneTouchVisible(false)}
                onMouseDown={() => setPhoneTouchPressed(true)}
                onMouseUp={() => setPhoneTouchPressed(false)}
              >

              {/* Notch — drag handle with settings/dock/minimize buttons. Drag logic
                  lives in startPhoneDrag (declared earlier) and includes a 3px
                  click-vs-drag threshold so button taps don't flicker the UI. */}
              <div className="phone-notch" onMouseDown={(e) => {
                if ((e.target as HTMLElement).closest('.phone-notch-btn')) return
                startPhoneDrag(e)
              }}>
                <div className="phone-notch-btn-wrap phone-notch-btn-wrap-settings">
                  <button className="phone-notch-btn notch-settings" title="Audio settings" aria-label="Audio settings" onClick={(e) => {
                    e.stopPropagation()
                    const opening = !callingSettingsOpen
                    setCallingSettingsOpen(opening)
                    setCallingSettingsView('main')
                    if (opening) {
                      navigator.mediaDevices?.enumerateDevices().then(devices => {
                        setAudioDevices({
                          inputs: devices.filter(d => d.kind === 'audioinput'),
                          outputs: devices.filter(d => d.kind === 'audiooutput'),
                        })
                      }).catch(() => {})
                    }
                  }}>
                    <svg width="14" height="10" viewBox="0 0 32 12" fill="currentColor"><circle cx="4" cy="6" r="2.5"/><circle cx="16" cy="6" r="2.5"/><circle cx="28" cy="6" r="2.5"/></svg>
                  </button>
                  {false && callingSettingsOpen && (
                    <div className="phone-settings-dropdown" onClick={e => e.stopPropagation()}>
                      {callingSettingsView === 'main' && (
                        <>
                          <div onClick={() => setCallingSettingsView('microphone')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }} onMouseEnter={e => (e.currentTarget.style.background = '#f5f8fa')} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 600, color: '#33475b' }}>Microphone</div>
                              <div style={{ fontSize: 10, color: '#7c98b6', marginTop: 2, maxWidth: 210, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{audioDevices.inputs.find(d => d.deviceId === selectedMic)?.label || 'Default microphone'}</div>
                            </div>
                            <svg width="12" height="12" viewBox="0 0 32 32" fill="#7c98b6"><path d="M11 27.71c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62l9.48-9.48-9.48-9.47a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0L22.1 15.48c.45.45.45 1.17 0 1.62L11.81 27.39c-.22.22-.52.33-.81.33Z"/></svg>
                          </div>
                          <div onClick={() => setCallingSettingsView('speaker')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }} onMouseEnter={e => (e.currentTarget.style.background = '#f5f8fa')} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 600, color: '#33475b' }}>Speaker</div>
                              <div style={{ fontSize: 10, color: '#7c98b6', marginTop: 2, maxWidth: 210, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{audioDevices.outputs.find(d => d.deviceId === selectedSpeaker)?.label || 'Default speaker'}</div>
                            </div>
                            <svg width="12" height="12" viewBox="0 0 32 32" fill="#7c98b6"><path d="M11 27.71c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62l9.48-9.48-9.48-9.47a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0L22.1 15.48c.45.45.45 1.17 0 1.62L11.81 27.39c-.22.22-.52.33-.81.33Z"/></svg>
                          </div>
                          {myCapabilities?.access_settings && (
                          <div onClick={() => { setCallingSettingsOpen(false); setActiveView('preferences'); openPrefsTab('calling') }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', cursor: 'pointer' }} onMouseEnter={e => (e.currentTarget.style.background = '#f5f8fa')} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 600, color: '#33475b' }}>Audio Test <span style={{ background: '#eaf0f6', color: '#516f90', fontSize: 9, padding: '1px 5px', borderRadius: 3, marginLeft: 4, fontWeight: 500 }}>New</span></div>
                              <div style={{ fontSize: 10, color: '#7c98b6', marginTop: 2 }}>Test your audio settings</div>
                            </div>
                            <svg width="12" height="12" viewBox="0 0 32 32" fill="#7c98b6"><path d="M11 27.71c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62l9.48-9.48-9.48-9.47a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0L22.1 15.48c.45.45.45 1.17 0 1.62L11.81 27.39c-.22.22-.52.33-.81.33Z"/></svg>
                          </div>
                          )}
                          <div style={{ borderTop: '1px solid #f1f5f9', padding: '8px 14px' }} onMouseEnter={e => (e.currentTarget.style.background = '#f5f8fa')} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                            {myCapabilities?.access_settings && (
                            <button onClick={() => { setCallingSettingsOpen(false); setActiveView('preferences'); openPrefsTab('calling') }} style={{ background: 'none', border: 'none', color: '#1976d2', fontSize: 11, cursor: 'pointer', padding: 0, fontWeight: 500 }}>Open calling settings</button>
                            )}
                          </div>
                        </>
                      )}
                      {callingSettingsView === 'microphone' && (
                        <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderBottom: '1px solid #f1f5f9' }}>
                              <button onClick={() => setCallingSettingsView('main')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: '#33475b' }}>
                                <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95"/></svg>
                              </button>
                              <span style={{ fontSize: 12, fontWeight: 600, color: '#33475b' }}>Microphone</span>
                            </div>
                            <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                              {audioDevices.inputs.length === 0 && (
                                <div style={{ padding: '12px 14px', fontSize: 11, color: '#7c98b6' }}>No microphones detected</div>
                              )}
                              {audioDevices.inputs.map(device => (
                                <div key={device.deviceId} onClick={() => setSelectedMic(device.deviceId)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', cursor: 'pointer', fontSize: 11, color: '#33475b' }} onMouseEnter={e => (e.currentTarget.style.background = '#f5f8fa')} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                                  <div style={{ width: 16, flexShrink: 0 }}>
                                    {selectedMic === device.deviceId && (
                                      <svg width="14" height="14" viewBox="0 0 32 32" fill="#00bda5"><path d="M11 26.43c-.3 0-.59-.12-.81-.33l-8-8.01a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l7.17 7.17L27.31 6.22c.43-.46 1.16-.49 1.62-.05.46.43.48 1.16.05 1.62L11.83 26.07c-.21.23-.51.36-.82.36h-.02Z"/></svg>
                                    )}
                                  </div>
                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{device.label || `Microphone ${device.deviceId.slice(0, 8)}`}</span>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                        {callingSettingsView === 'speaker' && (
                          <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderBottom: '1px solid #f1f5f9' }}>
                              <button onClick={() => setCallingSettingsView('main')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: '#33475b' }}>
                                <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95"/></svg>
                              </button>
                              <span style={{ fontSize: 12, fontWeight: 600, color: '#33475b' }}>Speaker</span>
                            </div>
                            <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                              {audioDevices.outputs.length === 0 && (
                                <div style={{ padding: '12px 14px', fontSize: 11, color: '#7c98b6' }}>No speakers detected</div>
                              )}
                              {audioDevices.outputs.map(device => (
                                <div key={device.deviceId} onClick={() => setSelectedSpeaker(device.deviceId)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', cursor: 'pointer', fontSize: 11, color: '#33475b' }} onMouseEnter={e => (e.currentTarget.style.background = '#f5f8fa')} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                                  <div style={{ width: 16, flexShrink: 0 }}>
                                    {selectedSpeaker === device.deviceId && (
                                      <svg width="14" height="14" viewBox="0 0 32 32" fill="#00bda5"><path d="M11 26.43c-.3 0-.59-.12-.81-.33l-8-8.01a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l7.17 7.17L27.31 6.22c.43-.46 1.16-.49 1.62-.05.46.43.48 1.16.05 1.62L11.83 26.07c-.21.23-.51.36-.82.36h-.02Z"/></svg>
                                    )}
                                  </div>
                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{device.label || `Speaker ${device.deviceId.slice(0, 8)}`}</span>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                {/* Phone is always docked (undock toggle retired 2026-05-01).
                    Only the minimize button is shown in the notch. */}
                <button className="phone-notch-btn notch-minimize" style={{ position: 'absolute', right: -22, bottom: 0 }} onClick={() => { setCallingPanelMinimized(true); setCallingSettingsOpen(false) }} title="Minimize" aria-label="Minimize phone">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>
                </button>
              </div>

              {/* Desk-phone live-call mirror. Outgoing (or answered) calls get
                  the FULL-SCREEN treatment below so a RingOut placed from the
                  CRM presents exactly like a CRM call; this compact banner
                  remains only for inbound-ringing, where the user still
                  decides on the desk phone itself. */}
              {deskMirrorFullscreen && deskActiveCall && (
                <div className="har-desk-mirror" role="status" aria-live="polite">
                  {/* Same header chrome as the softphone in-call screen, so a
                      desk-routed outgoing call looks identical (minus controls
                      the desk phone owns). */}
                  <div className="har-active-call-header" style={{ cursor: 'default' }}>
                    <div className="har-active-call-status-row">
                      <span className={`har-active-status-dot${deskMirrorAnswered ? ' connected' : ' ringing'}`} />
                      <span className="har-active-status-label">{deskMirrorAnswered ? 'Connected' : 'Calling...'}</span>
                      {!!deskActiveCall.startTime && <span className="har-active-timer">{formatCallTimer(deskMirrorElapsed)}</span>}
                      <div style={{ marginLeft: 'auto', display: 'flex', gap: 2 }}>
                        <button className="har-calling-header-btn light" onClick={() => setCallingPanelMinimized(true)} title="Minimize">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14"/></svg>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="har-active-contact-section" style={{ justifyContent: 'center' }}>
                    <div className="har-active-avatar">
                      {(() => {
                        const nm = (deskActiveCall.name || '').trim()
                        if (!nm) {
                          return <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.81.36 1.61.7 2.36a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.75.34 1.55.57 2.36.7A2 2 0 0 1 22 16.92z"/></svg>
                        }
                        const parts = nm.split(/\s+/).filter(Boolean)
                        if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
                        return parts[0].slice(0, 2).toUpperCase()
                      })()}
                    </div>
                    <div className="har-active-contact-info">
                      <div className="har-active-contact-name">{deskActiveCall.name || formatPhoneDisplay((deskActiveCall.phoneNumber || '').replace(/\D/g, '').replace(/^1/, '')) || 'Unknown'}</div>
                      {deskActiveCall.name && deskActiveCall.phoneNumber && (
                        <div className="har-active-contact-company">{formatPhoneDisplay(deskActiveCall.phoneNumber.replace(/\D/g, '').replace(/^1/, ''))}</div>
                      )}
                    </div>
                    <div className="har-desk-mirror-tag">{deskActiveCall.direction === 'Inbound' ? 'Incoming call' : 'Outgoing call'} · Desk phone</div>
                  </div>
                  <div className="har-desk-mirror-hint">Audio is on your desk phone — {deskActiveCall.direction === 'Outbound' ? 'hang up there to end the call' : 'end the call there'}.</div>
                  {/* INBOUND desk calls get a Dismiss (hide the informational mirror; the
                      call stays on the handset). OUTBOUND has no action here — end it on the
                      physical handset (see hint above) — so the row isn't rendered at all
                      rather than show an empty bar. The old "View Call" button was removed
                      (2026-06-18): jumping to the Calls list mid-call made no sense. */}
                  {deskActiveCall.direction !== 'Outbound' && (
                  <div className="har-active-secondary-row" style={{ marginTop: 0 }}>
                    <button className="har-active-secondary-btn" title="Hide this from your screen — the call stays on your desk phone" onClick={endDeskMirrorCall}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      <span>Dismiss</span>
                    </button>
                  </div>
                  )}
                </div>
              )}
              {deskActiveCall && !deskMirrorFullscreen && !iosIncoming && (
                <div className="phone-active-banner" style={{
                  margin: '10px 12px 0', padding: '10px 12px', borderRadius: 12,
                  background: deskActiveCall.direction === 'Inbound' ? 'linear-gradient(180deg,#ecfaf1,#dff5e8)' : 'linear-gradient(180deg,#eef4ff,#e0eafe)',
                  border: `1px solid ${deskActiveCall.direction === 'Inbound' ? '#bfe6cd' : '#c4d8f8'}`,
                  display: 'flex', alignItems: 'center', gap: 10,
                }}>
                  <span style={{
                    width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                    background: deskActiveCall.direction === 'Inbound' ? '#1f9d57' : '#1769d6',
                    boxShadow: `0 0 0 4px ${deskActiveCall.direction === 'Inbound' ? 'rgba(31,157,87,.18)' : 'rgba(23,105,214,.18)'}`,
                  }} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: .4, textTransform: 'uppercase', color: deskActiveCall.direction === 'Inbound' ? '#16804a' : '#1257ad' }}>
                      {deskActiveCall.direction === 'Inbound' ? 'Incoming call' : 'Outgoing call'} · desk phone
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1c2733', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 1 }}>
                      {deskActiveCall.name || deskActiveCall.phoneNumber || 'Unknown caller'}
                    </div>
                  </div>
                </div>
              )}

              {/* === iOS INCOMING-CALL BANNER === floats over whatever screen is
                  showing (home/app). Swipe up → dismiss (call keeps ringing);
                  tap → full incoming screen WITHOUT answering. Red/green act
                  immediately. Drag deltas are viewport px while the banner
                  lives in the zoomed .phone-device subtree, so divide by the
                  effective zoom before translating (same trap as PR #76). */}
              {iosIncoming && incomingReady && incomingPresn === 'banner' && (
                <div
                  ref={iosBannerRef}
                  className="ios-call-banner"
                  role="button"
                  tabIndex={0}
                  aria-label={`Incoming call from ${iosIncoming.name || iosIncoming.number || 'unknown caller'} — press Enter to open, swipe up to dismiss`}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIncomingPresn('full') } }}
                  onPointerDown={(e) => {
                    if ((e.target as HTMLElement).closest('.ios-call-banner-btn')) return
                    iosBannerDragRef.current = { y0: e.clientY, dy: 0, active: true }
                    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId) } catch { /* ignore */ }
                  }}
                  onPointerMove={(e) => {
                    const drag = iosBannerDragRef.current
                    if (!drag.active) return
                    drag.dy = e.clientY - drag.y0
                    const el = iosBannerRef.current
                    if (!el) return
                    const z = parseFloat((getComputedStyle(el.closest('.phone-device') as Element) as any).zoom) || 1
                    const dyLocal = Math.min(0, drag.dy) / z
                    el.style.transform = `translateY(${dyLocal}px)`
                    el.style.opacity = String(Math.max(0.2, 1 + dyLocal / 90))
                  }}
                  onPointerUp={() => {
                    const drag = iosBannerDragRef.current
                    if (!drag.active) return
                    drag.active = false
                    const el = iosBannerRef.current
                    if (!el) return
                    const z = parseFloat((getComputedStyle(el.closest('.phone-device') as Element) as any).zoom) || 1
                    if (drag.dy / z <= -36) {
                      el.style.transition = 'transform 160ms ease-in, opacity 160ms ease-in'
                      el.style.transform = 'translateY(-140%)'
                      el.style.opacity = '0'
                      window.setTimeout(() => setIncomingPresn('hidden'), 160)
                      return
                    }
                    el.style.transform = ''
                    el.style.opacity = ''
                    if (Math.abs(drag.dy) < 6) setIncomingPresn('full')
                  }}
                  onPointerCancel={() => {
                    const drag = iosBannerDragRef.current
                    if (!drag.active) return
                    drag.active = false
                    const el = iosBannerRef.current
                    if (el) { el.style.transform = ''; el.style.opacity = '' }
                  }}
                >
                  <div className="ios-call-banner-info">
                    <div className="ios-call-banner-name">{iosIncoming.name || 'Unknown caller'}</div>
                    <div className="ios-call-banner-number">
                      {iosIncoming.number ? (formatPhoneDisplay(iosIncoming.number.replace(/\D/g, '').replace(/^1/, '')) || iosIncoming.number) : 'Unknown number'}
                    </div>
                  </div>
                  <div className="ios-call-banner-actions">
                    <button
                      type="button"
                      className="ios-call-banner-btn reject"
                      aria-label={iosIncoming.kind === 'softphone' ? 'Decline call' : 'Dismiss'}
                      onClick={(e) => {
                        e.stopPropagation()
                        if (iosIncoming.kind === 'softphone') void declineInboundCall()
                        else setIncomingPresn('hidden')
                      }}
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor" style={{ transform: 'rotate(135deg)' }}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.11 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92Z"/></svg>
                    </button>
                    <button
                      type="button"
                      className="ios-call-banner-btn accept"
                      aria-label={iosIncoming.kind === 'softphone' ? 'Accept call' : 'Answer on desk phone'}
                      onClick={(e) => {
                        e.stopPropagation()
                        if (iosIncoming.kind === 'softphone') void answerInboundCall()
                        else { setDeskAnswerHint(true); setIncomingPresn('full') }
                      }}
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.11 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92Z"/></svg>
                    </button>
                  </div>
                </div>
              )}

              {/* Settings sheet — rendered inside phone-screen for proper containment */}
              {callingSettingsOpen && (
                <div className="phone-settings-dropdown" onClick={e => e.stopPropagation()}>
                  {/* Drag handle + header */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '8px 14px 4px', borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ width: 36, height: 4, borderRadius: 2, background: '#c7c7cc', marginBottom: 8, cursor: 'pointer' }} onClick={() => setCallingSettingsOpen(false)} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: '#1c1c1e' }}>Audio Settings</span>
                      <button onClick={() => setCallingSettingsOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', color: '#8e8e93' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </button>
                    </div>
                  </div>
                  {/* Stable-height body: identical tallness across main / Microphone /
                      Speaker so the sheet never jumps when navigating; inner content
                      scrolls (overflow-y:auto) when a view is shorter or taller. */}
                  <div style={{ height: 232, overflowY: 'auto' }}>
                  {callingSettingsView === 'main' && (
                    <>
                      <div onClick={() => setCallingSettingsView('microphone')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }} onMouseEnter={e => (e.currentTarget.style.background = '#f5f8fa')} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                        <div style={{ minWidth: 0, flex: 1, paddingRight: 8 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#33475b' }}>Microphone</div>
                          <div style={{ fontSize: 10, color: '#7c98b6', marginTop: 2, lineHeight: 1.35, wordBreak: 'break-word' }}>{audioDevices.inputs.find(d => d.deviceId === selectedMic)?.label || 'Default microphone'}</div>
                        </div>
                        <svg width="12" height="12" viewBox="0 0 32 32" fill="#7c98b6"><path d="M11 27.71c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62l9.48-9.48-9.48-9.47a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0L22.1 15.48c.45.45.45 1.17 0 1.62L11.81 27.39c-.22.22-.52.33-.81.33Z"/></svg>
                      </div>
                      <div onClick={() => setCallingSettingsView('speaker')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }} onMouseEnter={e => (e.currentTarget.style.background = '#f5f8fa')} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                        <div style={{ minWidth: 0, flex: 1, paddingRight: 8 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#33475b' }}>Speaker</div>
                          <div style={{ fontSize: 10, color: '#7c98b6', marginTop: 2, lineHeight: 1.35, wordBreak: 'break-word' }}>{audioDevices.outputs.find(d => d.deviceId === selectedSpeaker)?.label || 'Default speaker'}</div>
                        </div>
                        <svg width="12" height="12" viewBox="0 0 32 32" fill="#7c98b6"><path d="M11 27.71c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62l9.48-9.48-9.48-9.47a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0L22.1 15.48c.45.45.45 1.17 0 1.62L11.81 27.39c-.22.22-.52.33-.81.33Z"/></svg>
                      </div>
                      {myCapabilities?.access_settings && (
                      <div onClick={() => { setCallingSettingsOpen(false); setActiveView('preferences'); openPrefsTab('calling') }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', cursor: 'pointer' }} onMouseEnter={e => (e.currentTarget.style.background = '#f5f8fa')} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#33475b' }}>Audio Test <span style={{ background: '#eaf0f6', color: '#516f90', fontSize: 9, padding: '1px 5px', borderRadius: 3, marginLeft: 4, fontWeight: 500 }}>New</span></div>
                          <div style={{ fontSize: 10, color: '#7c98b6', marginTop: 2 }}>Test your audio settings</div>
                        </div>
                        <svg width="12" height="12" viewBox="0 0 32 32" fill="#7c98b6"><path d="M11 27.71c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62l9.48-9.48-9.48-9.47a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0L22.1 15.48c.45.45.45 1.17 0 1.62L11.81 27.39c-.22.22-.52.33-.81.33Z"/></svg>
                      </div>
                      )}
                      <div style={{ borderTop: '1px solid #f1f5f9', padding: '8px 14px' }} onMouseEnter={e => (e.currentTarget.style.background = '#f5f8fa')} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                        {myCapabilities?.access_settings && (
                        <button onClick={() => { setCallingSettingsOpen(false); setActiveView('preferences'); openPrefsTab('calling') }} style={{ background: 'none', border: 'none', color: '#1976d2', fontSize: 11, cursor: 'pointer', padding: 0, fontWeight: 500 }}>Open calling settings</button>
                        )}
                      </div>
                    </>
                  )}
                  {callingSettingsView === 'microphone' && (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderBottom: '1px solid #f1f5f9' }}>
                        <button onClick={() => setCallingSettingsView('main')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: '#33475b' }}>
                          <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95"/></svg>
                        </button>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#33475b' }}>Microphone</span>
                      </div>
                      <div>
                        {audioDevices.inputs.length === 0 && <div style={{ padding: '12px 14px', fontSize: 11, color: '#7c98b6' }}>No microphones detected</div>}
                        {audioDevices.inputs.map(device => (
                          <div key={device.deviceId} onClick={() => setSelectedMic(device.deviceId)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', cursor: 'pointer', fontSize: 11, color: '#33475b' }} onMouseEnter={e => (e.currentTarget.style.background = '#f5f8fa')} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                            <div style={{ width: 16, flexShrink: 0 }}>{selectedMic === device.deviceId && <svg width="14" height="14" viewBox="0 0 32 32" fill="#00bda5"><path d="M11 26.43c-.3 0-.59-.12-.81-.33l-8-8.01a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l7.17 7.17L27.31 6.22c.43-.46 1.16-.49 1.62-.05.46.43.48 1.16.05 1.62L11.83 26.07c-.21.23-.51.36-.82.36h-.02Z"/></svg>}</div>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{device.label || `Microphone ${device.deviceId.slice(0, 8)}`}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  {callingSettingsView === 'speaker' && (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderBottom: '1px solid #f1f5f9' }}>
                        <button onClick={() => setCallingSettingsView('main')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: '#33475b' }}>
                          <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95"/></svg>
                        </button>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#33475b' }}>Speaker</span>
                      </div>
                      <div>
                        {audioDevices.outputs.length === 0 && <div style={{ padding: '12px 14px', fontSize: 11, color: '#7c98b6' }}>No speakers detected</div>}
                        {audioDevices.outputs.map(device => (
                          <div key={device.deviceId} onClick={() => setSelectedSpeaker(device.deviceId)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', cursor: 'pointer', fontSize: 11, color: '#33475b' }} onMouseEnter={e => (e.currentTarget.style.background = '#f5f8fa')} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                            <div style={{ width: 16, flexShrink: 0 }}>{selectedSpeaker === device.deviceId && <svg width="14" height="14" viewBox="0 0 32 32" fill="#00bda5"><path d="M11 26.43c-.3 0-.59-.12-.81-.33l-8-8.01a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l7.17 7.17L27.31 6.22c.43-.46 1.16-.49 1.62-.05.46.43.48 1.16.05 1.62L11.83 26.07c-.21.23-.51.36-.82.36h-.02Z"/></svg>}</div>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{device.label || `Speaker ${device.deviceId.slice(0, 8)}`}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  </div>
                </div>
              )}

              {/* iPhone notification banner — supports calls + SMS.
                  Suppressed while an incoming ring is presenting — the iOS
                  call banner / incoming screens own that interaction. */}
              {iphoneNotif && !iosIncoming && (() => {
                const isCall = iphoneNotif.type === 'call' || (iphoneNotif.type !== 'sms' && (iphoneNotif.message?.includes('call') || iphoneNotif.message?.includes('invoiced') || iphoneNotif.message?.includes('contact')))
                return (
                <div className="iphone-notif-banner" onClick={() => {
                  if (isCall && incomingCallToast?.linkUrl) {
                    setIphoneNotif(null)
                    setIncomingCallToast(null)
                    window.location.hash = incomingCallToast.linkUrl
                  } else {
                    setCallingPanelTab('sms')
                    setPhoneScreenMode('app')
                    setIphoneNotif(null)
                  }
                }}>
                  <div className="iphone-notif-icon">
                    <div className={`iphone-home-icon ${isCall ? 'phone-app-icon-phone' : 'phone-app-icon-msg'}`} style={{ width: 28, height: 28, borderRadius: 7 }}>
                      {isCall ? (
                        <svg width="16" height="16" viewBox="15 15 70 70" fill="#fff"><path d="M25.7 44.3c-3.3-6-4.6-10.9-4.6-14.2s.8-4.2 1.6-5c.8-.8 4.5-3 5.2-3.5.6-.4 3-1.3 4.5.9s4.3 6.5 6.3 9.4c3.1 4.1.6 5.9-.2 7-1.5 2-2.3 2.5-2.3 4.9 0 2.4 6.8 9.4 8.4 11.1 1.6 1.7 8.4 7.6 10.5 8 2.1.3 5-1.9 5.6-2.5 3.1-2.4 4.8-.6 6.2.2 1.4.8 7.8 4.8 9.8 6.1 1.9 1.3 1.7 3.4 1.7 3.4s-1.3 3.9-4.3 6.8c-.7.7-2 1.6-5.1 1.6-3.1 0-6.5-.6-14.5-5-6.5-3.6-12.7-9.2-16-12.5-3.3-3.2-9-9.8-12.8-16.7z"/></svg>
                      ) : (
                        <svg width="16" height="16" viewBox="15 15 70 70" fill="#fff"><path d="M43.5 75.7c2.1.3 4.2.5 6.4.5 18.2 0 33-12.3 33-27.4S68.2 21.5 50 21.5 17 33.8 17 48.9c0 9.9 6.3 18.5 15.7 23.3v1c0 2.9-4.8 6.7-4.5 6.7 4.8 0 8.2-3 10.5-3.7 1.9-.5 3-.6 4.8-.5z"/></svg>
                      )}
                    </div>
                  </div>
                  <div className="iphone-notif-content">
                    <div className="iphone-notif-header">
                      <span className="iphone-notif-app">{isCall ? 'Phone' : 'Messages'}</span>
                      <span className="iphone-notif-time">{iphoneNotif.time}</span>
                    </div>
                    <div className="iphone-notif-name">{iphoneNotif.name}</div>
                    <div className="iphone-notif-msg">{iphoneNotif.message}</div>
                  </div>
                  <button className="iphone-notif-close" onClick={(e) => { e.stopPropagation(); setIphoneNotif(null) }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
                )})()}

              {/* === INCOMING CALL OVERLAY === iOS lock-screen style, covers phone-screen.
                  Banner-first: this full screen shows only after the user taps
                  the floating banner (or it's re-opened) — never auto-answers. */}
              {incomingCallInfo && inboundCallSession && incomingReady && incomingPresn === 'full' && (
                <div className="phone-incoming-overlay" role="dialog" aria-label="Incoming call" aria-live="assertive">
                  <div className="phone-incoming-header">Incoming call</div>
                  <div className="phone-incoming-avatar-wrap">
                    <div className="phone-incoming-avatar" aria-hidden>
                      {incomingCallInfo.photoUrl
                        ? <img src={incomingCallInfo.photoUrl} alt="" />
                        : (() => {
                            const parts = (incomingCallInfo.name || '?').split(/\s+/).filter(Boolean)
                            if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
                            if (parts[0]) return parts[0].slice(0, 2).toUpperCase()
                            return '?'
                          })()}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, width: '100%' }}>
                      <div className="phone-incoming-name">{incomingCallInfo.name}</div>
                      <div className="phone-incoming-sub">
                        {incomingCallInfo.number ? formatPhoneDisplay(incomingCallInfo.number.replace(/\D/g, '').replace(/^1/, '')) || incomingCallInfo.number : 'Unknown number'}
                        {incomingCallInfo.entityLabel && (
                          <>
                            {' · '}
                            <a
                              href="#"
                              className="phone-incoming-entity-link"
                              onClick={e => {
                                e.preventDefault()
                                if (incomingCallInfo.entityKind === 'deal' && incomingCallInfo.entityId) {
                                  window.location.hash = `/deals/${incomingCallInfo.entityId}`
                                } else if (incomingCallInfo.entityKind === 'contact' && incomingCallInfo.entityId) {
                                  window.location.hash = `/contacts/record/${incomingCallInfo.entityId}`
                                }
                              }}
                            >{incomingCallInfo.entityLabel}</a>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* iOS-style secondary actions: Forward (transfer the ringing
                      call) + Reply (text the caller back) — parity with the
                      RingCentral desktop popup's More → Forward / Reply. These
                      act on the live SIP session, which is present exactly when
                      this overlay shows (browser co-registered, Part 2). */}
                  <div className="phone-incoming-extra" style={{ marginTop: 'auto' }}>
                    {showIncomingForward && (
                      <div className="phone-incoming-picker">
                        <div className="phone-incoming-picker-title">Forward to</div>
                        {CALENDAR_USERS_RAW.filter(u => u.userId !== currentUserId).map(user => {
                          const mapping = rcMappings.find(m => m.user_id === user.userId && m.is_active)
                          const rcExt = mapping?.rc_extension_number
                          if (!rcExt) return null
                          return (
                            <button
                              key={user.userId}
                              type="button"
                              className="phone-incoming-picker-item"
                              onClick={async () => {
                                // forward() routes a still-ringing call; transfer()
                                // is the connected-call fallback. Feature-detected.
                                try {
                                  if (typeof inboundCallSession?.forward === 'function') await inboundCallSession.forward(String(rcExt))
                                  else if (typeof inboundCallSession?.transfer === 'function') await inboundCallSession.transfer(String(rcExt))
                                } catch (err) { console.warn('[WebPhone] forward failed:', err) }
                                setShowIncomingForward(false)
                                setInboundCallSession(null); setIncomingCallToast(null); setIphoneNotif(null)
                              }}
                            >
                              <span>{user.name}</span>
                              <span className="phone-incoming-picker-ext">ext {rcExt}</span>
                            </button>
                          )
                        })}
                      </div>
                    )}
                    {showIncomingReply && (
                      <div className="phone-incoming-picker">
                        <div className="phone-incoming-picker-title">Reply with a text</div>
                        {["Can't talk now — call you back", 'In a meeting', 'On my way', 'Please text me the details'].map(text => (
                          <button
                            key={text}
                            type="button"
                            className="phone-incoming-picker-item"
                            onClick={async () => {
                              const caller = incomingCallInfo?.number || ''
                              // Prefer the SDK's native reply-with-message (declines + texts via RC).
                              let sent = false
                              try {
                                if (typeof inboundCallSession?.replyWithMessage === 'function') { await inboundCallSession.replyWithMessage({ replyType: 'CustomMessage', text }); sent = true }
                                else if (typeof inboundCallSession?.reply === 'function') { await inboundCallSession.reply(text); sent = true }
                              } catch (err) { console.warn('[WebPhone] replyWithMessage failed:', err) }
                              // Fallback: decline, then send via our RC SMS API.
                              if (!sent) {
                                try { await (inboundCallSession?.decline?.() ?? inboundCallSession?.hangup?.()) } catch { /* ignore */ }
                                const digits = caller.replace(/\D/g, '')
                                if (digits.length >= 10) {
                                  try {
                                    const from = (await fetchSmsNumbers()).numbers?.[0]?.phoneNumber
                                    if (from) await sendSmsMessage(from, caller, text)
                                  } catch (err) { console.warn('[WebPhone] reply SMS fallback failed:', err) }
                                }
                              }
                              setShowIncomingReply(false)
                              setInboundCallSession(null); setIncomingCallToast(null); setIphoneNotif(null)
                            }}
                          >{text}</button>
                        ))}
                      </div>
                    )}
                    <div className="phone-incoming-mini-row">
                      <button
                        type="button"
                        className="phone-incoming-mini"
                        onClick={() => { setShowIncomingReply(false); setShowIncomingForward(v => !v) }}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 14 20 9 15 4"/><path d="M4 20v-7a4 4 0 0 1 4-4h12"/></svg>
                        Forward
                      </button>
                      <button
                        type="button"
                        className="phone-incoming-mini"
                        disabled={(incomingCallInfo?.number || '').replace(/\D/g, '').length < 10}
                        title={(incomingCallInfo?.number || '').replace(/\D/g, '').length < 10 ? 'No textable number for this caller' : 'Reply with a text'}
                        onClick={() => { setShowIncomingForward(false); setShowIncomingReply(v => !v) }}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                        Reply
                      </button>
                    </div>
                  </div>
                  <div className="phone-incoming-actions">
                    <div className="phone-incoming-btn-col">
                      <button
                        className="phone-incoming-btn reject"
                        aria-label="Decline call"
                        onClick={declineInboundCall}
                      >
                        <svg viewBox="0 0 24 24" fill="currentColor" style={{ transform: 'rotate(135deg)' }}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.11 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92Z"/></svg>
                      </button>
                      <span className="phone-incoming-btn-label">Decline</span>
                    </div>
                    <div className="phone-incoming-btn-col">
                      <button
                        className="phone-incoming-btn accept"
                        aria-label="Accept call"
                        onClick={answerInboundCall}
                      >
                        <svg viewBox="0 0 24 24" fill="currentColor"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.11 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92Z"/></svg>
                      </button>
                      <span className="phone-incoming-btn-label">Accept</span>
                    </div>
                  </div>
                </div>
              )}

              {/* === DESK / REMOTE INCOMING — full screen === opened by tapping
                  the iOS banner while the desk phone (kind 'desk') or another
                  device (kind 'remote', poller-detected) rings. The audio lives
                  on the physical phone (no Call Control API), so Answer just
                  surfaces the pick-up hint and Dismiss hides the ring UI. */}
              {iosIncoming && iosIncoming.kind !== 'softphone' && incomingPresn === 'full' && (
                <div className="phone-incoming-overlay" role="dialog" aria-label={iosIncoming.kind === 'desk' ? 'Incoming call on desk phone' : 'Incoming call on your other devices'} aria-live="assertive">
                  <div className="phone-incoming-header">{iosIncoming.kind === 'desk' ? 'Incoming call · Desk phone' : 'Incoming call'}</div>
                  <div className="phone-incoming-avatar-wrap">
                    <div className="phone-incoming-avatar" aria-hidden>
                      {(() => {
                        const parts = (iosIncoming.name || '').split(/\s+/).filter(Boolean)
                        if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
                        if (parts[0]) return parts[0].slice(0, 2).toUpperCase()
                        return <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.81.36 1.61.7 2.36a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.75.34 1.55.57 2.36.7A2 2 0 0 1 22 16.92z"/></svg>
                      })()}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, width: '100%' }}>
                      <div className="phone-incoming-name">{iosIncoming.name || 'Unknown caller'}</div>
                      <div className="phone-incoming-sub">
                        {iosIncoming.number ? formatPhoneDisplay(iosIncoming.number.replace(/\D/g, '').replace(/^1/, '')) || iosIncoming.number : 'Unknown number'}
                      </div>
                    </div>
                  </div>
                  <div className="phone-incoming-extra" style={{ marginTop: 'auto' }}>
                    {deskAnswerHint && (
                      <div className="phone-incoming-desk-hint">
                        {iosIncoming.kind === 'desk'
                          ? 'Ringing on your desk phone — lift the handset or answer there.'
                          : 'Ringing on your other devices — answer on one of them.'}
                      </div>
                    )}
                  </div>
                  <div className="phone-incoming-actions">
                    <div className="phone-incoming-btn-col">
                      <button
                        className="phone-incoming-btn reject"
                        aria-label="Dismiss"
                        onClick={() => setIncomingPresn('hidden')}
                      >
                        <svg viewBox="0 0 24 24" fill="currentColor" style={{ transform: 'rotate(135deg)' }}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.11 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92Z"/></svg>
                      </button>
                      <span className="phone-incoming-btn-label">Dismiss</span>
                    </div>
                    <div className="phone-incoming-btn-col">
                      <button
                        className="phone-incoming-btn accept"
                        aria-label="Answer on desk phone"
                        onClick={() => setDeskAnswerHint(true)}
                      >
                        <svg viewBox="0 0 24 24" fill="currentColor"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.11 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92Z"/></svg>
                      </button>
                      <span className="phone-incoming-btn-label">Answer</span>
                    </div>
                  </div>
                </div>
              )}

              {/* === HOME SCREEN === */}
              {(phoneScreenMode === 'home' || (phoneScreenMode as string) === 'lock') && (
                <div className="iphone-homescreen">
                  {/* Date & Time */}
                  <div className="iphone-home-datetime">
                    <div className="iphone-lock-date">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</div>
                    <div className="iphone-lock-time">{new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).replace(/ AM| PM/, '')}</div>
                  </div>
                  {/* App grid area — quick-dial tiles for top favorites + a Favorites tile.
                      Calling-specific content (TRAP(phone/tdz-favorites)): only render when
                      the user has voice capability, so a voiceless user doesn't see a
                      calling-only Favorites grid on the home screen. */}
                  <div className="iphone-home-grid" style={{ padding: '14px 4px 0' }}>
                    {hasCallingHomeContent && phoneFavorites.length > 0 && (
                      <>
                        <div style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.7)', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 8, paddingLeft: 4, textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>Favorites</div>
                        <div className="phone-quickdial-grid">
                          {phoneFavorites.slice(0, 6).map(fav => {
                            const initials = (fav.display_name || '?').split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '#'
                            return (
                              <button
                                key={fav.id}
                                className="phone-quickdial-tile"
                                onClick={() => dialPhoneFavorite(fav)}
                                title={`Call ${fav.display_name || fav.phone_number || ''}`}
                              >
                                <div className="phone-favorite-avatar">{initials}</div>
                                <div className="phone-quickdial-tile-name">{(fav.display_name || fav.phone_number || '').split(' ')[0] || '—'}</div>
                              </button>
                            )
                          })}
                          {/* Always show a tile that jumps to the full Favorites tab */}
                          <button
                            className="phone-quickdial-tile"
                            onClick={() => { setCallingPanelTab('favorites'); setPhoneScreenMode('app') }}
                            title="Open Favorites"
                          >
                            <div className="phone-favorite-avatar" style={{ background: 'linear-gradient(135deg, #64d2ff, #007AFF)' }}>
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff" stroke="none"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                            </div>
                            <div className="phone-quickdial-tile-name">All</div>
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                  {/* Dock — frosted glass bar at bottom. Each app icon is gated on the
                      current user's capability (PHONE_ARCHITECTURE §6.5): Phone on voice,
                      Messages on SMS (== voice proxy).
                      If the user has NO apps at all, fall through to the 'Set up a number'
                      CTA below so the screen isn't a bare empty dock. */}
                  {(hasPhoneIcon || hasSmsIcon) ? (
                  <div className="iphone-dock">
                    {hasPhoneIcon && (
                    <button className="iphone-home-app" onClick={() => { setCallingPanelTab('keypad'); setPhoneScreenMode('app') }}>
                      <div className="iphone-home-icon phone-app-icon-phone">
                        <svg width="30" height="30" viewBox="15 15 70 70" fill="#fff"><path d="M25.7 44.3c-3.3-6-4.6-10.9-4.6-14.2s.8-4.2 1.6-5c.8-.8 4.5-3 5.2-3.5.6-.4 3-1.3 4.5.9s4.3 6.5 6.3 9.4c3.1 4.1.6 5.9-.2 7-1.5 2-2.3 2.5-2.3 4.9 0 2.4 6.8 9.4 8.4 11.1 1.6 1.7 8.4 7.6 10.5 8 2.1.3 5-1.9 5.6-2.5 3.1-2.4 4.8-.6 6.2.2 1.4.8 7.8 4.8 9.8 6.1 1.9 1.3 1.7 3.4 1.7 3.4s-1.3 3.9-4.3 6.8c-.7.7-2 1.6-5.1 1.6-3.1 0-6.5-.6-14.5-5-6.5-3.6-12.7-9.2-16-12.5-3.3-3.2-9-9.8-12.8-16.7z"/></svg>
                        {(() => { const mc = deviceCallLog.filter(c => c.missed).length; return mc > 0 ? <span className="iphone-home-badge">{mc}</span> : null })()}
                      </div>
                    </button>
                    )}
                    {hasSmsIcon && (
                    <button className="iphone-home-app" onClick={() => { setCallingPanelTab('sms'); setPhoneScreenMode('app') }}>
                      <div className="iphone-home-icon phone-app-icon-msg" style={{ position: 'relative' }}>
                        <svg width="30" height="30" viewBox="15 15 70 70" fill="#fff"><path d="M43.5 75.7c2.1.3 4.2.5 6.4.5 18.2 0 33-12.3 33-27.4S68.2 21.5 50 21.5 17 33.8 17 48.9c0 9.9 6.3 18.5 15.7 23.3v1c0 2.9-4.8 6.7-4.5 6.7 4.8 0 8.2-3 10.5-3.7 1.9-.5 3-.6 4.8-.5z"/></svg>
                        {smsTotalUnread > 0 && <span className="iphone-home-badge">{smsTotalUnread}</span>}
                      </div>
                    </button>
                    )}
                  </div>
                  ) : (
                    /* No apps at all (no RC extension). Keep the existing
                       "Set up a number" CTA so the home screen isn't a bare empty dock. */
                    <div className="iphone-home-noapps">
                      <div className="iphone-home-noapps-title">No apps set up yet</div>
                      <button
                        className="iphone-home-noapps-cta"
                        onClick={() => { setCallingPanelTab('keypad'); setPhoneScreenMode('app') }}
                      >
                        Set up a number
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* === PHONE: JSX — APP GATE CASCADE === one calm loading frame holds until BOTH
                  rcMappingsLoaded && callingModeLoaded (deciding per-fetch flashed a gate slideshow);
                  the no-number empty state below is a TRUE render gate — the tabs must not mount
                  behind it. */}
              {/* === APP VIEW === */}
              {phoneScreenMode === 'app' && (<>
              {(!rcMappingsLoaded || !callingModeLoaded) ? (
                /* Hold ONE calm loading frame until every gate input is known
                   (mappings list + the /api/me calling fields). Deciding eagerly
                   per-fetch flashed a slideshow on every refresh — "no number" →
                   keypad → Connect gate — as each request landed, and the keypad
                   frame briefly exposed the gated app. rcSelfMapped comes from
                   /api/me (a server-side user↔mapping join), so the "no number"
                   decision also can't false-flash while the client-side user
                   bootstrap (effectiveUser → callingUserMapping) is still
                   resolving. */
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', color: '#94a3b8', fontSize: '0.85rem' }}>Loading…</div>
              ) : (!callingUserMapping && !rcSelfMapped) ? (<div className="phone-empty-state">
                  <div className="phone-empty-icon">
                    <svg width="40" height="40" viewBox="0 0 32 32" fill="none" stroke="#94a3b8" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M27.308 20.649 23.379 18.6a2.659 2.659 0 0 0-3.184.582l-1.406 1.656a.62.62 0 0 1-.74.15 17.66 17.66 0 0 1-5.188-3.85 17.634 17.634 0 0 1-3.847-5.184.621.621 0 0 1 .149-.74l1.656-1.404a2.66 2.66 0 0 0 .582-3.187L9.35 4.694A2.659 2.659 0 0 0 6.16 3.55l-1.97.772c-1.158.455-1.955 1.512-2.015 2.748-.492 10.111 8.71 19.31 18.819 18.819 1.236-.06 2.293-.857 2.748-2.016l.773-1.97a2.657 2.657 0 0 0-1.207-3.254Z"/>
                      <line x1="4" y1="4" x2="28" y2="28" stroke="#94a3b8" strokeWidth="1.5"/>
                    </svg>
                  </div>
                  <h3 className="phone-empty-title">No phone number set up</h3>
                  <p className="phone-empty-desc">Assign a RingCentral extension to your account to start making calls.</p>
                  <button
                    className="phone-empty-cta"
                    onClick={() => {
                      closeCallingPanel()
                      setActiveView('preferences')
                      openPrefsTab('calling')
                    }}
                  >Set up a number</button>
                </div>
              ) : (rcOauthConfigured && !rcConnected) ? (
                /* Mapped but NOT connected → full-screen Connect gate. Per the
                   product decision the WHOLE in-CRM phone is gated behind per-user
                   Connect: the tabs/keypad aren't rendered at all (a true
                   render-gate, so there's nothing to reach via devtools).
                   The OAuth result surfaces in the Connect flash. */
                <div className="phone-rc-connect-gate">
                  {/* Compact, centered stack SIZED TO FIT the docked screen so no
                      vertical scrollbar ever appears (see docs/REGRESSIONS.md —
                      docked-phone Connect-RingCentral gate must fit, no scroll).
                      margin:auto centers it; overflow:hidden on the gate is the
                      guaranteed backstop; the white bg fills the screen so text
                      never spills onto the dark sidebar behind the phone. */}
                  <div className="phone-rc-connect">
                    <div className="phone-rc-connect-icon">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0054a9" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.81.36 1.61.7 2.36a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.75.34 1.55.57 2.36.7A2 2 0 0 1 22 16.92z"/></svg>
                    </div>
                    <h3 className="phone-rc-connect-title">Connect RingCentral</h3>
                    <p className="phone-rc-connect-desc">{rcExtension ? `Extension ${rcExtension} is mapped. ` : ''}Sign in to RingCentral to call in the CRM — your desk phone still works.</p>
                    {/* #/ssoLogin survives the 302 chain (redirects without their
                        own fragment inherit the navigation's) and lands RC's
                        login SPA directly on its SSO route. */}
                    <button className="phone-rc-connect-cta" onClick={() => { window.location.assign('/api/crm/rc/oauth/authorize#/ssoLogin') }}>Connect RingCentral</button>
                  </div>
                </div>
              ) : (
              <>
              {/* App content */}
              <div className="phone-app-content">
              {/* Global search bar — hidden when tabs have their own headers */}
              {callingPanelTab !== 'sms' && callingPanelTab !== 'keypad' && callingPanelTab !== 'voicemail' && callingPanelTab !== 'contacts' && callingPanelTab !== 'recents' && callingPanelTab !== 'favorites' && <div className="phone-search-wrap">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" style={{ position: 'absolute', left: 22, top: 18, zIndex: 1 }}><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                <input
                  className="phone-search"
                  placeholder="Search contacts or dial..."
                  value={dialerSearch}
                  onChange={(e) => {
                    const raw = e.target.value
                    const digitsOnly = raw.replace(/[^\d]/g, '')
                    if (/^[\d\s\-()+]*$/.test(raw) && digitsOnly.length > 0) {
                      setDialerInput(digitsOnly)
                      setDialerSearch(formatPhoneDisplay(digitsOnly))
                    } else {
                      setDialerSearch(raw)
                      if (raw === '') setDialerInput('')
                    }
                  }}
                />
                {dialerSearch && (
                  <button className="phone-backspace-btn" style={{ position: 'absolute', right: 16, top: 12 }} onClick={() => { setDialerInput((d) => { const next = d.slice(0, -1); setDialerSearch(next ? formatPhoneDisplay(next) : ''); return next }) }} title="Backspace">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z"/><line x1="18" y1="9" x2="12" y2="15"/><line x1="12" y1="9" x2="18" y2="15"/></svg>
                  </button>
                )}
                {dialerSearchResults.length > 0 && (
                  <div className="phone-search-results">
                    {dialerSearchResults.slice(0, 5).map((r) => (
                      <div key={r.id} className="phone-search-result-item">
                        <div className="phone-result-avatar">{r.title.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}</div>
                        <div className="phone-result-info" onClick={() => { setActiveCallContact({ name: r.title, company: r.subtitle, id: r.id }); const ph = r.phone || r.subtitle?.replace(/\D/g, '') || ''; if (ph) { setDialerInput(ph.replace(/^1/, '')); setDialerSearch(formatPhoneDisplay(ph.replace(/^1/, ''))) }; setDialerSearchResults([]) }}>
                          <div className="phone-result-name">{r.title}</div>
                          <div className="phone-result-sub">{r.phone || r.subtitle}</div>
                        </div>
                        <button className="ios-search-call-btn" onClick={() => { const ph = r.phone || r.subtitle?.replace(/\D/g, '') || ''; if (ph) { setDialerInput(ph.replace(/^1/, '')); setDialerSearch(formatPhoneDisplay(ph.replace(/^1/, ''))); setActiveCallContact({ name: r.title, phone: ph }); startCallingPanelCall() } }} title="Call">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#007AFF" strokeWidth="1.8" strokeLinecap="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                        </button>
                      </div>
                    ))}
                    {dialerSearchResults.length > 5 && (
                      <div className="phone-search-result-item" style={{ justifyContent: 'flex-start', gap: 6, cursor: 'pointer', color: '#007AFF', fontSize: 12, fontWeight: 500 }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#007AFF" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                        <span>{dialerSearchResults.length - 5} More Results</span>
                      </div>
                    )}
                  </div>
                )}
              </div>}
              {webPhoneError && (
                <div className="phone-error-banner" onClick={() => { setWebPhoneError(null); initWebPhone() }} style={{ cursor: 'pointer' }} title="Tap to retry">
                  {webPhoneError}
                </div>
              )}
              {/* Main content area */}
                {callingPanelTab !== 'sms' ? (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    {/* iOS Phone app header */}
                    {callingPanelTab === 'recents' && (
                      <div className="ios-phone-header">
                        <div className="ios-header-row" style={{ justifyContent: 'center' }}>
                          <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>Recents</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 10px 6px' }}>
                          <div className="phone-segment" style={{ flex: 'none', width: 120 }}>
                            <button className={`phone-segment-btn${recentsFilter === 'all' ? ' active' : ''}`} onClick={() => setRecentsFilter('all')}>All</button>
                            <button className={`phone-segment-btn${recentsFilter === 'missed' ? ' active' : ''}`} onClick={() => setRecentsFilter('missed')}>Missed</button>
                          </div>
                        </div>
                      </div>
                    )}
                    {callingPanelTab === 'favorites' && (
                      <div className="ios-phone-header">
                        <div className="ios-header-row" style={{ justifyContent: 'center' }}>
                          <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>Favorites</span>
                        </div>
                      </div>
                    )}
                    {callingPanelTab === 'contacts' && (
                      <div className="ios-phone-header">
                        <div className="ios-header-row" style={{ justifyContent: 'center' }}>
                          <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>Contacts</span>
                        </div>
                        <div style={{ padding: '4px 10px 6px' }}>
                          <input
                            className="ios-msg-search"
                            type="text"
                            placeholder="Search contacts..."
                            value={phoneContactsSearch}
                            onChange={e => setPhoneContactsSearch(e.target.value)}
                            style={{ width: '100%', boxSizing: 'border-box' }}
                          />
                        </div>
                      </div>
                    )}
                    {callingPanelTab === 'voicemail' && (
                      <div className="ios-phone-header">
                        <div className="ios-header-row" style={{ justifyContent: 'center' }}>
                          <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>Voicemail</span>
                        </div>
                      </div>
                    )}

                    {/* Tab content */}
                    {callingPanelTab === 'keypad' ? (
                    <Suspense fallback={<PhoneTabSkeleton variant="keypad" />}>
                      <PhoneKeypad
                        dialerInput={dialerInput}
                        setDialerInput={setDialerInput}
                        setDialerSearch={setDialerSearch}
                        dialerSearchResults={dialerSearchResults}
                        setDialerSearchResults={setDialerSearchResults}
                        dialerSuggestExpanded={dialerSuggestExpanded}
                        setDialerSuggestExpanded={setDialerSuggestExpanded}
                        setActiveCallContact={setActiveCallContact}
                        callingState={callingState}
                        startCallingPanelCall={startCallingPanelCall}
                        playDtmfTone={playDtmfTone}
                        lookupContactsByPhone={lookupContactsByPhone}
                      />
                    </Suspense>
                ) : callingPanelTab === 'recents' ? (
                  <Suspense fallback={<PhoneTabSkeleton />}>
                    <PhoneRecents
                      calls={deviceCallLog}
                      recentsFilter={recentsFilter}
                      setDialerInput={setDialerInput}
                      setDialerSearch={setDialerSearch}
                      setActiveCallContact={setActiveCallContact}
                      setCallingPanelTab={setCallingPanelTab}
                      isPhoneFavorite={isPhoneFavorite}
                      togglePhoneFavorite={togglePhoneFavorite}
                      formatDatePref={formatDatePref}
                      placeCall={(num) => { void startCallingPanelCall(num) }}
                    />
                  </Suspense>
                ) : callingPanelTab === 'favorites' ? (
                      <Suspense fallback={<PhoneTabSkeleton />}>
                        <PhoneFavorites
                          phoneFavorites={phoneFavorites}
                          phoneFavoritesLoading={phoneFavoritesLoading}
                          phoneFavoriteDragId={phoneFavoriteDragId}
                          onFavoriteDragStart={onFavoriteDragStart}
                          onFavoriteDragEnd={onFavoriteDragEnd}
                          onFavoriteDropBefore={onFavoriteDropBefore}
                          dialPhoneFavorite={dialPhoneFavorite}
                          togglePhoneFavorite={togglePhoneFavorite}
                        />
                      </Suspense>
                    ) : callingPanelTab === 'contacts' ? (
                      <Suspense fallback={<PhoneTabSkeleton />}>
                        <PhoneContacts
                          phoneContactsList={phoneContactsList}
                          phoneContactsLoading={phoneContactsLoading}
                          phoneContactsSearch={phoneContactsSearch}
                          setDialerInput={setDialerInput}
                          setDialerSearch={setDialerSearch}
                          setActiveCallContact={setActiveCallContact}
                          setCallingPanelTab={setCallingPanelTab}
                          isPhoneFavorite={isPhoneFavorite}
                          togglePhoneFavorite={togglePhoneFavorite}
                        />
                      </Suspense>
                    ) : callingPanelTab === 'voicemail' ? (
                      <Suspense fallback={<PhoneTabSkeleton />}>
                        <PhoneVoicemail
                          voicemails={voicemails}
                          voicemailsLoading={voicemailsLoading}
                          voicemailExpandedId={voicemailExpandedId}
                          setVoicemailExpandedId={setVoicemailExpandedId}
                          voicemailPlayingId={voicemailPlayingId}
                          setVoicemailPlayingId={setVoicemailPlayingId}
                          voicemailAudioRef={voicemailAudioRef}
                          setVoicemails={setVoicemails}
                          currentUserId={currentUserId}
                          voicemailAudioUrl={voicemailAudioUrl}
                          updateVoicemailReadStatus={updateVoicemailReadStatus}
                          deleteVoicemail={deleteVoicemail}
                          setDialerInput={setDialerInput}
                          setDialerSearch={setDialerSearch}
                          setActiveCallContact={setActiveCallContact}
                          setCallingPanelTab={setCallingPanelTab}
                        />
                      </Suspense>
                    ) : null}

                    {/* iOS 5-tab bottom bar */}
                    <div className="ios-tab-bar">
                      <button className={`ios-tab${callingPanelTab === 'favorites' ? ' active' : ''}`} onClick={() => setCallingPanelTab('favorites')}>
                        {/* Star outer points reach the viewBox edges (radius ~10 of 12),
                            making it look bigger than the other tab icons whose shapes
                            leave a visible margin. Wrap in a <g> scaled to 0.85 around
                            the viewBox center so the star sits visually flush. */}
                        <svg width="22" height="22" viewBox="0 0 24 24" fill={callingPanelTab === 'favorites' ? '#007AFF' : 'none'} stroke={callingPanelTab === 'favorites' ? '#007AFF' : '#8e8e93'} strokeWidth="1.5">
                          <g transform="translate(12 12) scale(0.85) translate(-12 -12)">
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                          </g>
                        </svg>
                        <span>Favorites</span>
                      </button>
                      <button className={`ios-tab${callingPanelTab === 'recents' ? ' active' : ''}`} onClick={() => setCallingPanelTab('recents')} style={{ position: 'relative' }}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill={callingPanelTab === 'recents' ? '#007AFF' : 'none'} stroke={callingPanelTab === 'recents' ? '#007AFF' : '#8e8e93'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14" stroke={callingPanelTab === 'recents' ? '#fff' : '#8e8e93'} strokeWidth="2"/></svg>
                        <span>Recents</span>
                        {(() => { const mc = deviceCallLog.filter(c => c.missed).length; return mc > 0 ? <span style={{ position: 'absolute', top: -4, right: 4, minWidth: 14, height: 14, borderRadius: 7, background: '#ff3b30', color: '#fff', fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px', lineHeight: 1, border: '1.5px solid #fff' }}>{mc}</span> : null })()}
                      </button>
                      <button className={`ios-tab${callingPanelTab === 'contacts' ? ' active' : ''}`} onClick={() => setCallingPanelTab('contacts')}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill={callingPanelTab === 'contacts' ? '#007AFF' : 'none'} stroke={callingPanelTab === 'contacts' ? '#007AFF' : '#8e8e93'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                        <span>Contacts</span>
                      </button>
                      <button className={`ios-tab${callingPanelTab === 'keypad' ? ' active' : ''}`} onClick={() => setCallingPanelTab('keypad')}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={callingPanelTab === 'keypad' ? '#007AFF' : '#8e8e93'} strokeWidth="1.5"><circle cx="6" cy="6" r="1.5"/><circle cx="12" cy="6" r="1.5"/><circle cx="18" cy="6" r="1.5"/><circle cx="6" cy="12" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="18" cy="12" r="1.5"/><circle cx="6" cy="18" r="1.5"/><circle cx="12" cy="18" r="1.5"/><circle cx="18" cy="18" r="1.5"/></svg>
                        <span>Keypad</span>
                      </button>
                      <button className={`ios-tab${callingPanelTab === 'voicemail' ? ' active' : ''}`} onClick={() => setCallingPanelTab('voicemail')} style={{ position: 'relative' }}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={callingPanelTab === 'voicemail' ? '#007AFF' : '#8e8e93'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="5.5" cy="11.5" r="4.5"/><circle cx="18.5" cy="11.5" r="4.5"/><line x1="5.5" y1="16" x2="18.5" y2="16"/></svg>
                        <span>Voicemail</span>
                        {(() => { const unread = voicemails.filter(v => v.readStatus === 'Unread').length; return unread > 0 ? <span style={{ position: 'absolute', top: -4, right: 4, minWidth: 14, height: 14, borderRadius: 7, background: '#ff3b30', color: '#fff', fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px', lineHeight: 1, border: '1.5px solid #fff' }}>{unread}</span> : null })()}
                      </button>
                    </div>
                  </div>
                ) : (
                  /* SMS / Messages app */
                  <Suspense fallback={<PhoneTabSkeleton />}>
                    <PhoneSms
                      smsNewMode={smsNewMode}
                      setSmsNewMode={setSmsNewMode}
                      smsNewTo={smsNewTo}
                      setSmsNewTo={setSmsNewTo}
                      smsCompose={smsCompose}
                      setSmsCompose={setSmsCompose}
                      smsSelectedContactName={smsSelectedContactName}
                      setSmsSelectedContactName={setSmsSelectedContactName}
                      smsContactPicker={smsContactPicker}
                      setSmsContactPicker={setSmsContactPicker}
                      smsContactSearch={smsContactSearch}
                      setSmsContactSearch={setSmsContactSearch}
                      smsToResults={smsToResults}
                      setSmsToResults={setSmsToResults}
                      smsActiveThread={smsActiveThread}
                      setSmsActiveThread={setSmsActiveThread}
                      smsConversations={smsConversations}
                      setSmsConversations={setSmsConversations}
                      smsMessages={smsMessages}
                      setSmsMessages={setSmsMessages}
                      smsSearchQuery={smsSearchQuery}
                      setSmsSearchQuery={_setSmsSearchQuery}
                      smsLoading={smsLoading}
                      smsSending={smsSending}
                      setSmsSending={setSmsSending}
                      setSmsTotalUnread={setSmsTotalUnread}
                      setDialerInput={setDialerInput}
                      setDialerSearch={setDialerSearch}
                      setActiveCallContact={setActiveCallContact}
                      setCallingPanelTab={setCallingPanelTab}
                      setPhoneScreenMode={setPhoneScreenMode}
                      fetchClients={fetchClients}
                      fetchSmsConversations={fetchSmsConversations}
                      sendSmsMessage={sendSmsMessage}
                      formatDatePref={formatDatePref}
                    />
                  </Suspense>
                )}
              </div>
              </>
              )}
            </>)}

            {/* Home indicator dock line — only in apps, not on home screen */}
            {phoneScreenMode === 'app' && (
              <div className="iphone-home-dock" onClick={() => setPhoneScreenMode('home')}>
                <div className="iphone-dock-line" />
              </div>
            )}
            </div>{/* closes phone-screen */}
            <div ref={phoneTouchRef} className={`phone-touch-cursor${phoneTouchPressed ? ' pressing' : ''}`} style={{ opacity: phoneTouchVisible ? 1 : 0 }} />
            </>
          )}

          {/* === PHONE: JSX — IN-CALL SCREEN === overflow:hidden column; Notes/Dialpad
              float as absolute .phone-incall-panel overlays BEFORE the secondary row in the DOM.
              Transfer + Transcript are body-portaled right-of-device overlays (a consistent pair —
              PHONE_ARCHITECTURE §6.6/§7). Transfer sends rc_extension_number, never CRM user ids. */}
          {/* TRAP(phone/secondary-row-clip): .har-active-secondary-row is margin-top:auto-pinned
              LAST in the column — anything rendered AFTER it is clipped invisible (the historical
              'Transfer did nothing' bug). */}
          {/* ── ACTIVE CALL STATE (ringing / connected) ── */}
          {/* 'dialing' is part of the in-call screen (not the dialer): the
              moment a call is placed the outgoing-call UI mounts — no spinner
              phase on the keypad. */}
          {(callingState === 'dialing' || callingState === 'ringing' || callingState === 'connected') && (
            <div style={{ position: 'relative', background: 'linear-gradient(180deg, #1c1c1e 0%, #2c2c2e 40%, #1c1c1e 100%)', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden', borderRadius: 'inherit', animation: 'iosCallFadeIn 0.35s cubic-bezier(0.32, 0.72, 0, 1)' }}>
              <div
                className="har-active-call-header"
                onMouseDown={(e) => {
                  // Don't start a drag on the header's action buttons
                  if ((e.target as HTMLElement).closest('button')) return
                  startPhoneDrag(e, { forceUndock: true })
                }}
              >
                <div className="har-active-call-status-row">
                  <span className={`har-active-status-dot${callingState === 'connected' ? ' connected' : ' ringing'}`} />
                  <span className="har-active-status-label">{callingState === 'connected' ? 'Connected' : callingState === 'ringing' ? 'Ringing...' : 'Calling...'}</span>
                  <span className="har-active-timer">{formatCallTimer(activeCallTimer)}</span>
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: 2 }}>
                    <button className="har-calling-header-btn light" onClick={() => setCallingPanelMinimized(true)} title="Minimize">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14"/></svg>
                    </button>
                    <button className="har-calling-header-btn light" onClick={hangupCallingPanel} title="End call">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
                    </button>
                  </div>
                </div>
              </div>
              {/* Contact info. The avatar collapses while the in-call keypad is
                  open — iOS-style — so the whole screen always fits the docked
                  phone with zero scrolling. */}
              <div className="har-active-contact-section">
                {!showDialpad && <div className="har-active-avatar">
                  {(() => {
                    // Initials from the contact NAME only; number-only calls get
                    // the phone glyph — identical to the desk-call mirror.
                    const nm = (activeCallContact?.name || '').trim()
                    if (!nm || /^[\d\s\-()+.]+$/.test(nm)) {
                      return <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.81.36 1.61.7 2.36a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.75.34 1.55.57 2.36.7A2 2 0 0 1 22 16.92z"/></svg>
                    }
                    const parts = nm.split(/\s+/).filter(Boolean)
                    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
                    return parts[0].slice(0, 2).toUpperCase()
                  })()}
                </div>}
                <div className="har-active-contact-info">
                  <div className="har-active-contact-name">{activeCallContact?.name || formatPhoneDisplay(dialerInput.replace(/\D/g, '').replace(/^1/, '')) || dialerInput}</div>
                  {activeCallContact?.company ? (
                    <div className="har-active-contact-company">| {activeCallContact.company}</div>
                  ) : activeCallContact?.name && (activeCallContact?.phone || dialerInput) ? (
                    // Number under the name — same layout as the desk-call mirror.
                    <div className="har-active-contact-company">{formatPhoneDisplay((activeCallContact.phone || dialerInput).replace(/\D/g, '').replace(/^1/, ''))}</div>
                  ) : null}
                </div>
              </div>
              {/* Action buttons row */}
              {/* No manual Record button: RingCentral account-level automatic call
                  recording is ON, so startRecording() returns "automatic recording
                  is already active". Recording happens server-side for every call. */}
              <div className="har-active-actions-row">
                <button
                  className={`har-active-action-btn${callMuted ? ' active' : ''}`}
                  title="Mute"
                  onClick={toggleMuteWebRTC}
                >
                  {callMuted ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="1" y1="1" x2="23" y2="23"/><path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"/><path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
                  )}
                  <span>{callMuted ? 'Muted' : 'Mute'}</span>
                </button>
                <button
                  className={`har-active-action-btn${showDialpad ? ' active' : ''}`}
                  title="Keypad"
                  onClick={() => { setShowDialpad((s) => !s); setShowTransferPicker(false); setShowInCallNotes(false) }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="5" height="5" rx="1"/><rect x="9.5" y="3" width="5" height="5" rx="1"/><rect x="16" y="3" width="5" height="5" rx="1"/><rect x="3" y="9.5" width="5" height="5" rx="1"/><rect x="9.5" y="9.5" width="5" height="5" rx="1"/><rect x="16" y="9.5" width="5" height="5" rx="1"/><rect x="3" y="16" width="5" height="5" rx="1"/><rect x="9.5" y="16" width="5" height="5" rx="1"/><rect x="16" y="16" width="5" height="5" rx="1"/></svg>
                  <span>Keypad</span>
                </button>
                <button
                  className="har-active-action-btn hangup"
                  title="Hang up"
                  onClick={hangupCallingPanel}
                >
                  <svg width="18" height="18" viewBox="0 0 32 32" fill="currentColor" style={{ transform: 'rotate(135deg)' }}><path d="M27.308 20.649 23.379 18.6a2.659 2.659 0 0 0-3.184.582l-1.406 1.656a.62.62 0 0 1-.74.15 17.66 17.66 0 0 1-5.188-3.85 17.634 17.634 0 0 1-3.847-5.184.621.621 0 0 1 .149-.74l1.656-1.404a2.66 2.66 0 0 0 .582-3.187L9.35 4.694A2.659 2.659 0 0 0 6.16 3.55l-1.97.772c-1.158.455-1.955 1.512-2.015 2.748-.492 10.111 8.71 19.31 18.819 18.819 1.236-.06 2.293-.857 2.748-2.016l.773-1.97a2.657 2.657 0 0 0-1.207-3.254Z"/></svg>
                  <span>Hang up</span>
                </button>
              </div>
              {/* In-call dialpad (collapsed) */}
              {showDialpad && (
                <div className="phone-incall-keypad" style={{ marginTop: 8 }}>
                  {[
                    ['1', ''], ['2', 'ABC'], ['3', 'DEF'],
                    ['4', 'GHI'], ['5', 'JKL'], ['6', 'MNO'],
                    ['7', 'PQRS'], ['8', 'TUV'], ['9', 'WXYZ'],
                    ['*', ''], ['0', '+'], ['#', ''],
                  ].map(([num, letters]) => (
                    <button key={num} className="phone-incall-key" onClick={() => { playDtmfTone(num); sendDtmfWebRTC(num) }}>
                      <span className="phone-key-num">{num}</span>
                      {letters && <span className="phone-key-letters">{letters}</span>}
                    </button>
                  ))}
                </div>
              )}
              {/* In-device expandable panels (Notes / Dialpad) float as ABSOLUTE
                  overlays anchored above the pinned secondary row (.phone-incall-panel)
                  — opening one must never shift the Record/Mute/Keypad/Hangup or
                  Hold/Transfer rows; the popup simply covers them. (They also stay
                  BEFORE the secondary row in the DOM: the row is pinned with
                  margin-top:auto inside an overflow:hidden column, and in-flow content
                  after it clips invisible — the historical "Transfer does nothing" bug.) */}
              {/* TRANSFER popup — two modes (PHONE_ARCHITECTURE §6.6):
                  • "Ask first" (ATTENDED): holds the caller, dials the target, lets
                    the agent talk, then connects the held caller INTO that live
                    consult call (no re-dial). The panel switches to the consult view.
                  • "Transfer now" (BLIND): immediate REFER to the target line.
                  PRESENTATION: a body-portaled floating card that pops out to the
                  RIGHT of the docked phone — the interactive SIBLING of the live-
                  transcript overlay (same portal + right-of-device home + modern card
                  + open/close animation + z 9999). Connected-only (mirrors the
                  transcript gate) so it can't outlive the call; when the transcript
                  overlay is ALSO open it shifts right (--stacked) so the two never
                  collide. Functionality (attended/blind, consult dial/ring/connect,
                  Complete via completeWarmTransfer, Cancel/Back-to-caller, target
                  entry + teardown) is UNCHANGED — only placement moved out of the
                  in-device .phone-incall-panel. */}
              {callingState === 'connected' && showTransferPicker && createPortal((
                <div className={`phone-transfer-overlay${(showTranscript && phoneTranscriptCap?.enabled) ? ' phone-transfer-overlay--stacked' : ''}`}>
                  <div className="phone-transfer-overlay-head">
                    <span className="phone-transfer-overlay-title">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 14 20 9 15 4"/><path d="M4 20v-7a4 4 0 0 1 4-4h12"/></svg>
                      Transfer
                    </span>
                    {/* No dismiss mid-consult — like today, the live warm transfer is
                        resolved only via Cancel / Complete / Back-to-caller below. */}
                    {consultState === 'idle' && (
                      <button type="button" className="phone-transfer-overlay-close" title="Close transfer" onClick={() => setShowTransferPicker(false)}>×</button>
                    )}
                  </div>
                  <div className="phone-transfer-overlay-body">
                  {consultState === 'idle' ? (
                    <>
                      <div className="phone-transfer-seg" role="tablist" aria-label="Transfer type">
                        <button type="button" role="tab" aria-selected={transferMode === 'attended'} className={transferMode === 'attended' ? 'active' : ''} onClick={() => setTransferMode('attended')}>Ask first</button>
                        <button type="button" role="tab" aria-selected={transferMode === 'blind'} className={transferMode === 'blind' ? 'active' : ''} onClick={() => setTransferMode('blind')}>Transfer now</button>
                      </div>
                      <div className="phone-transfer-hint">
                        {transferMode === 'attended'
                          ? 'Call the person first and talk, then connect your caller.'
                          : 'Send the caller straight through — no consult.'}
                      </div>
                      <div className="phone-transfer-entry">
                        <input
                          type="text"
                          inputMode="tel"
                          value={transferNumberInput}
                          onChange={(e) => setTransferNumberInput(e.target.value)}
                          placeholder="Extension or number"
                          onKeyDown={(e) => { if (e.key === 'Enter' && transferNumberInput.trim()) handleTransferTarget(transferNumberInput.trim(), transferNumberInput.trim()) }}
                        />
                        <button type="button" className="phone-transfer-go" disabled={!transferNumberInput.trim()} onClick={() => handleTransferTarget(transferNumberInput.trim(), transferNumberInput.trim())}>
                          {transferMode === 'attended' ? 'Call' : 'Send'}
                        </button>
                      </div>
                      <div className="phone-transfer-label">Team</div>
                      <div className="phone-transfer-list">
                        {CALENDAR_USERS_RAW.filter(u => u.userId !== currentUserId).map(user => {
                          // RC extension comes from rc_user_mappings, NOT the CRM
                          // userId (Max=8, Carly=35…) — those aren't RC extensions.
                          const mapping = rcMappings.find(m => m.user_id === user.userId && m.is_active)
                          const rcExt = mapping?.rc_extension_number
                          const disabled = !rcExt
                          return (
                            <button
                              key={user.userId}
                              type="button"
                              disabled={disabled}
                              className="phone-transfer-target"
                              title={disabled ? 'No RingCentral extension mapped for this user' : (transferMode === 'attended' ? `Consult ext ${rcExt} first` : `Transfer to ext ${rcExt}`)}
                              onClick={() => { if (rcExt) handleTransferTarget(String(rcExt), user.name) }}
                            >
                              <div className="phone-transfer-avatar">{user.name.charAt(0)}</div>
                              <span className="phone-transfer-name">{user.name}</span>
                              <span className="phone-transfer-ext">{rcExt ? `ext ${rcExt}` : 'no extension'}</span>
                            </button>
                          )
                        })}
                      </div>
                    </>
                  ) : (
                    /* ── CONSULT IN PROGRESS (attended transfer live) ── */
                    <div className="phone-consult">
                      <div className="phone-consult-head">
                        <span className={`phone-consult-dot ${consultState === 'connected' ? 'on' : consultState === 'failed' ? 'off' : 'ring'}`} />
                        <span className="phone-consult-title">
                          {consultState === 'dialing' && `Calling ${consultTarget?.name || 'target'}…`}
                          {consultState === 'ringing' && `Ringing ${consultTarget?.name || 'target'}…`}
                          {consultState === 'connected' && `Connected to ${consultTarget?.name || 'target'}`}
                          {consultState === 'completing' && 'Connecting your caller…'}
                          {consultState === 'failed' && `${consultTarget?.name || 'Target'} unavailable`}
                        </span>
                      </div>
                      <div className="phone-consult-held">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                        <span>{activeCallContact?.name || 'Caller'} on hold</span>
                      </div>
                      {consultError && <div className="phone-consult-error">{consultError}</div>}
                      <div className="phone-consult-actions">
                        {consultState === 'failed' ? (
                          <button type="button" className="phone-consult-btn resume" onClick={() => { void cancelAttendedTransfer() }}>Back to caller</button>
                        ) : (
                          <>
                            <button type="button" className="phone-consult-btn cancel" disabled={consultState === 'completing'} onClick={() => { void cancelAttendedTransfer() }}>Cancel</button>
                            <button type="button" className="phone-consult-btn complete" disabled={consultState !== 'connected'} onClick={() => { void completeAttendedTransfer() }}>
                              {consultState === 'completing' ? 'Connecting…' : 'Complete transfer'}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                  </div>
                </div>
              ), document.body)}
              {/* In-call notes — bound to the SAME callNotes state as the
                  post-call review sheet, so anything typed mid-call carries
                  into the review and saves with the call activity. */}
              {showInCallNotes && (
                <div className="phone-incall-panel">
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginBottom: 6, fontWeight: 500 }}>Call notes</div>
                  <textarea
                    value={callNotes}
                    onChange={(e) => setCallNotes(e.target.value)}
                    placeholder="Type notes during the call — they carry into the post-call review."
                    rows={3}
                    autoFocus
                    style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#fff', fontSize: 12, lineHeight: 1.45, padding: '8px 10px', resize: 'none', fontFamily: 'inherit', outline: 'none' }}
                  />
                </div>
              )}
              {/* ── LIVE TRANSCRIPT PANEL ──
                  Toggled by the Transcript button in the secondary row (it used
                  to auto-appear whenever no other panel was open, which read as
                  a box "randomly showing up" mid-call). When RingCentral
                  real-time transcription is available we stream speaker + text
                  into phoneTranscript and the panel follows the conversation
                  (auto-scroll effect on phoneTranscript). When the feature isn't
                  provisioned the panel degrades to a friendly "not enabled"
                  stub. See GET /api/crm/phone/capabilities/live-transcription
                  and phoneTranscriptCap state. */}
              {callingState === 'connected' && showTranscript && (
                phoneTranscriptCap?.enabled ? (
                  // Transparent, minimal transcript overlay — floats beside the
                  // docked phone (portaled to body so it's not clipped by the
                  // phone panel). Just the words; hover reveals the opacity slider.
                  createPortal(
                    <div className="phone-transcript-overlay"
                      style={transcriptPos
                        ? { ['--tx-op' as any]: transcriptOpacity, left: transcriptPos.x, top: transcriptPos.y, right: 'auto', bottom: 'auto' }
                        : { ['--tx-op' as any]: transcriptOpacity }}>
                      <div className="phone-transcript-controls"
                        onPointerDown={(e) => {
                          // Drag from the controls strip (not the slider/close button).
                          if ((e.target as HTMLElement).closest('input,button')) return
                          const el = (e.currentTarget.parentElement as HTMLElement)
                          const r = el.getBoundingClientRect()
                          transcriptDragRef.current = { dx: e.clientX - r.left, dy: e.clientY - r.top }
                          const move = (ev: PointerEvent) => {
                            const d = transcriptDragRef.current; if (!d) return
                            const x = Math.max(4, Math.min(window.innerWidth - 80, ev.clientX - d.dx))
                            const y = Math.max(4, Math.min(window.innerHeight - 60, ev.clientY - d.dy))
                            setTranscriptPos({ x, y })
                          }
                          const up = () => {
                            window.removeEventListener('pointermove', move)
                            window.removeEventListener('pointerup', up)
                            transcriptDragRef.current = null
                            setTranscriptPos(pos => { if (pos) { try { localStorage.setItem('phone.transcriptOverlay.pos', JSON.stringify(pos)) } catch { /* */ } } return pos })
                          }
                          window.addEventListener('pointermove', move)
                          window.addEventListener('pointerup', up)
                        }}
                        style={{ cursor: 'move' }} title="Drag to move">
                        <span className="phone-transcript-live"><span className="dot" />Live</span>
                        <input
                          type="range" min={0.25} max={1} step={0.05} value={transcriptOpacity}
                          title="Overlay opacity"
                          onChange={(e) => { const v = parseFloat(e.target.value); setTranscriptOpacity(v); try { localStorage.setItem('phone.transcriptOverlay.opacity', String(v)) } catch { /* */ } }}
                        />
                        <button type="button" className="phone-transcript-close" title="Hide transcript" onClick={() => setShowTranscript(false)}>×</button>
                      </div>
                      <div className="phone-transcript-body" ref={phoneTranscriptRef} aria-live="polite">
                        {phoneTranscript.length === 0 ? (
                          <div className="phone-transcript-empty-note">Listening…</div>
                        ) : (
                          // Group CONSECUTIVE same-speaker segments so the speaker
                          // label paints ONCE per turn (never re-printed per chunk).
                          // You (ch0) → right, blue bubble; caller (ch1) → left,
                          // green bubble — the familiar 2-party-call read.
                          groupTranscript(phoneTranscript).map((group, gi) => (
                            <div key={gi} className={`phone-transcript-group ch${group.channel}${group.isSelf ? ' self' : ''}`}>
                              <span className="phone-transcript-speaker">{group.isSelf ? 'You' : (activeCallContact?.name || 'Caller')}</span>
                              <div className="phone-transcript-bubble">
                                {group.lines.map((ln, li) => (
                                  <span key={li} className={`phone-transcript-seg${ln.isFinal ? '' : ' interim'}`}>{ln.text}</span>
                                ))}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>,
                    document.body,
                  )
                ) : (
                  <div className="phone-incall-panel phone-transcript-panel empty disabled">
                    <div className="phone-transcript-title">Live transcript</div>
                    <div className="phone-transcript-empty-note">
                      {phoneTranscriptCap?.note || 'Live transcription not yet enabled — contact admin.'}
                    </div>
                  </div>
                )
              )}
              {/* Secondary actions — Hold + Transfer + Notes + Transcript.
                  Pinned to the bottom (margin-top:auto), so it stays LAST in
                  the DOM; expandable panels render above it. */}
              <div className="har-active-secondary-row">
                <button
                  className={`har-active-secondary-btn${callOnHold ? ' active' : ''}`}
                  title={callingState !== 'connected' ? 'Available once the call connects' : consultState !== 'idle' ? 'Caller is held for the transfer' : callOnHold ? 'Resume call' : 'Hold call'}
                  disabled={callingState !== 'connected' || consultState !== 'idle'}
                  style={callOnHold ? { background: '#fff', color: '#1a1a1a' } : {}}
                  onClick={async () => {
                    // SDK-native hold/unhold. Previously POST'd to
                    // /api/crm/rc/call-control/{hold,unhold} with the wrong
                    // param shape (`session.id` / `session.party?.id` — both
                    // undefined on WebRTC sessions), so RC returned 400 and
                    // the button did nothing. The SDK exposes hold()/unhold()
                    // on the CallSession directly; they operate over SIP.
                    const session = activeSessionRef.current
                    if (!session) return
                    try {
                      if (callOnHold) {
                        await session.unhold()
                        setCallOnHold(false)
                      } else {
                        await session.hold()
                        setCallOnHold(true)
                      }
                    } catch (err) {
                      console.error('[WebPhone] Hold toggle failed:', err)
                      setWebPhoneError(`Hold failed: ${err instanceof Error ? err.message : String(err)}`)
                    }
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                  <span>{callOnHold ? 'Resume' : 'Hold'}</span>
                </button>
                <button
                  className={`har-active-secondary-btn${showTransferPicker ? ' active' : ''}`}
                  title={callingState !== 'connected' ? 'Available once the call connects' : consultState !== 'idle' ? 'Transfer in progress' : 'Transfer call'}
                  disabled={callingState !== 'connected'}
                  style={showTransferPicker ? { background: '#fff', color: '#1a1a1a' } : {}}
                  onClick={() => {
                    // While a consult is live, keep the consult panel reachable —
                    // never silently drop a warm transfer by toggling the panel off.
                    // NOTE: opening Transfer must NOT close the live-transcript overlay —
                    // it floats beside the device (portaled to body) and persists on its
                    // own; ONLY the Transcript button toggles showTranscript.
                    if (consultState !== 'idle') { setShowTransferPicker(true); setShowInCallNotes(false); setShowDialpad(false); return }
                    setShowTransferPicker(!showTransferPicker); setShowInCallNotes(false); setShowDialpad(false)
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="15 14 20 9 15 4"/><path d="M4 20v-7a4 4 0 0 1 4-4h12"/></svg>
                  <span>{consultState !== 'idle' ? 'Consulting' : 'Transfer'}</span>
                </button>
                <button
                  className={`har-active-secondary-btn${showInCallNotes ? ' active' : ''}`}
                  title="Call notes"
                  onClick={() => { setShowInCallNotes(!showInCallNotes); setShowTransferPicker(false); setShowDialpad(false) }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                  <span>Notes</span>
                </button>
                <button
                  className={`har-active-secondary-btn${showTranscript ? ' active' : ''}`}
                  title={callingState !== 'connected' ? 'Available once the call connects' : 'Live transcript'}
                  disabled={callingState !== 'connected'}
                  style={showTranscript ? { background: '#fff', color: '#1a1a1a' } : {}}
                  onClick={() => { setShowTranscript(!showTranscript); setShowTransferPicker(false); setShowInCallNotes(false); setShowDialpad(false) }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><line x1="8" y1="9" x2="16" y2="9"/><line x1="8" y1="13" x2="13" y2="13"/></svg>
                  <span>Transcript</span>
                </button>
                {/* "View Call" removed (2026-06-18) — navigating to the Calls list
                    mid-call made no sense; the row is Hold / Transfer / Notes / Transcript. */}
              </div>
            </div>
          )}

          {/* === PHONE: JSX — REVIEW SHEET === the dark strip MUST stay the first child — docked CSS
              targets .ios-sheet-overlay > div:first-child. saveCallReview PATCHes the review INTO the
              call-start activity row (one Calls entry per call); closeCallingPanel({ keepOpen: true })
              is the 'phone vanished after saving' regression guard. */}
          {/* ── POST-CALL REVIEW STATE — iOS-style bottom sheet ── */}
          {callingState === 'review' && (() => {
            // Only show the Transcript tab when there is (or will be) a transcript:
            // a live-captured one, a polished server one, or one currently loading.
            // A desk/RingOut call with no live transcript shows Notes only — the old
            // empty "Transcript processing" stub is now simply the hidden-tab case.
            const showTranscriptTab = phoneTranscript.length > 0 || !!callTranscriptText || callTranscriptLoading
            // Never let a hidden Transcript tab strand an empty pane: fall back to Notes.
            const activeReviewTab = (reviewTab === 'transcript' && !showTranscriptTab) ? 'notes' : reviewTab
            return (
            <div className="ios-sheet-overlay" style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', borderRadius: 'inherit', overflow: 'hidden', zIndex: 5 }}>
              {/* Dark strip at top — tap to dismiss. Drag-to-undock retired
                  2026-05-01; the phone is now always docked. */}
              <div
                style={{ flexShrink: 0, height: 56, background: '#1c1c1e', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                onClick={() => {
                  if (phoneDragging) return
                  setCallingState('idle')
                }}
              >
                <div style={{ width: 36, height: 5, borderRadius: 3, background: 'rgba(255,255,255,0.3)' }} />
              </div>
              {/* Bottom sheet — slides up */}
              <div className="ios-sheet-panel" style={{ flex: 1, background: '#fff', borderRadius: '14px 14px 0 0', display: 'flex', flexDirection: 'column', overflow: 'auto', boxShadow: '0 -8px 30px rgba(0,0,0,0.4)' }}>
                {/* iOS grabber pill */}
                <div className="har-review-grabber-wrap" onClick={() => setCallingState('idle')}>
                  <div className="har-review-grabber" />
                </div>
              <div className="har-review-header">
                {/* iOS segmented control */}
                <div className="har-review-tabs" role="tablist">
                  <button className={`har-review-tab${activeReviewTab === 'notes' ? ' active' : ''}`} onClick={() => setReviewTab('notes')}>Notes</button>
                  {showTranscriptTab && (
                    <button className={`har-review-tab${activeReviewTab === 'transcript' ? ' active' : ''}`} onClick={() => setReviewTab('transcript')}>Transcript</button>
                  )}
                </div>
                <button
                  className="har-review-done-btn"
                  onClick={saveCallReview}
                >
                  {callSaved ? (
                    <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg> Saved</>
                  ) : 'Done'}
                </button>
              </div>
              <div className="har-review-progress-bar">
                <div className="har-review-progress-fill" style={{ width: '100%' }} />
              </div>
              {/* Outcome + type row */}
              <div className="har-review-dropdowns-row">
                <select
                  className="har-review-select"
                  value={callOutcomeSelected}
                  onChange={(e) => setCallOutcomeSelected(e.target.value)}
                >
                  <option value="">Outcome...</option>
                  {rcOutcomes.map((o) => (
                    <option key={o.id} value={o.label}>{o.label}</option>
                  ))}
                  {rcOutcomes.length === 0 && (
                    <>
                      <option value="Connected">Connected</option>
                      <option value="Left Voicemail">Left Voicemail</option>
                      <option value="No Answer">No Answer</option>
                      <option value="Busy">Busy</option>
                      <option value="Wrong Number">Wrong Number</option>
                    </>
                  )}
                </select>
                <select
                  className="har-review-select"
                  value={callTypeSelected}
                  onChange={(e) => setCallTypeSelected(e.target.value)}
                >
                  <option value="">Call type...</option>
                  <option value="Sales">Sales</option>
                  <option value="Support">Support</option>
                  <option value="Renewals">Renewals</option>
                  <option value="Follow-up">Follow-up</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              {/* === PHONE: JSX — REVIEW ASSIGN ROW (call-assign-link-v1) === deal/
                  project wrap-up assignment, staged in state and persisted only on
                  Done (dismiss discards it). Root carries the marker data-testid.
                  Unassigned → full-width ghost "+ Deal / Project"; assigned → a
                  removable chip (briefcase=deal / folder=project) that reopens the
                  picker on tap, × stages clearOwner. */}
              <div className="har-review-assign-row" data-testid="call-assign-link-v1">
                {callAssignSelection ? (
                  <div className={`har-review-assign-chip${callAssignSelection.type === 'project' ? ' project' : ''}`}>
                    <button type="button" className="har-review-assign-chip-body" title="Change assignment" onClick={() => setAssignPickerOpen(true)}>
                      {callAssignSelection.type === 'project' ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z"/></svg>
                      ) : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
                      )}
                      <span className="har-review-assign-chip-text">
                        <span className="har-review-assign-chip-label">{callAssignSelection.label}</span>
                        {callAssignSelection.type === 'project' && callAssignSelection.sublabel && (
                          <span className="har-review-assign-chip-sub">under {callAssignSelection.sublabel}</span>
                        )}
                      </span>
                    </button>
                    <button type="button" className="har-review-assign-chip-x" title="Unassign" aria-label="Unassign" onClick={() => { setCallAssignSelection(null); setAssignPickerOpen(false); setAssignSaveError(null) }}>×</button>
                  </div>
                ) : (
                  <button ref={assignTriggerRef} type="button" className="har-review-assign-btn" onClick={() => setAssignPickerOpen(true)}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
                    + Deal / Project
                  </button>
                )}
                {assignSaveError && <div className="har-review-assign-error">{assignSaveError}</div>}
              </div>
              {/* Notes area */}
              {activeReviewTab === 'notes' && (
                <div className="har-review-notes-section">
                  <textarea
                    className="har-review-notes-textarea"
                    placeholder="Add call notes..."
                    value={callNotes}
                    onChange={(e) => setCallNotes(e.target.value)}
                    rows={5}
                  />
                </div>
              )}
              {/* Transcript area */}
              {activeReviewTab === 'transcript' && (
                <div className="har-review-notes-section" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  {callTranscriptLoading ? (
                    <div style={{ padding: '32px 16px', textAlign: 'center', color: '#99acc2' }}>
                      <div className="hs-spinner" style={{ width: 20, height: 20, margin: '0 auto 8px' }} />
                      <div style={{ fontSize: 13 }}>Transcribing call...</div>
                      <div style={{ fontSize: 11, marginTop: 4, color: '#b0bec5' }}>This may take a minute</div>
                    </div>
                  ) : callTranscriptText ? (
                    <div style={{ flex: 1, overflow: 'auto' }}>
                      {callTranscriptSummary && (
                        <div style={{ padding: '10px 14px', background: '#f0fdf4', borderBottom: '1px solid #dcfce7', fontSize: 12, color: '#166534' }}>
                          <strong>Summary:</strong> {callTranscriptSummary}
                        </div>
                      )}
                      {callTranscriptSentiment && (
                        <div style={{ padding: '6px 14px', background: '#fafafa', borderBottom: '1px solid #f1f5f9', fontSize: 11, color: '#64748b' }}>
                          Sentiment: <span style={{ fontWeight: 600, color: callTranscriptSentiment.toLowerCase().includes('positive') ? '#16a34a' : callTranscriptSentiment.toLowerCase().includes('negative') ? '#dc2626' : '#64748b' }}>{callTranscriptSentiment}</span>
                        </div>
                      )}
                      <div style={{ padding: '12px 14px', fontSize: 13, lineHeight: 1.7, color: '#33475b', whiteSpace: 'pre-wrap' }}>
                        {callTranscriptText}
                      </div>
                    </div>
                  ) : phoneTranscript.length > 0 ? (
                    // No polished server transcript yet — show the transcript we
                    // captured live during the call so this tab is never empty
                    // post-call. RingSense's diarized/punctuated version replaces
                    // it when the "Check" fetch lands (typically 1–2 min).
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                      {/* Compact info icon (ⓘ) — frees the vertical space the
                          full-width banner used to eat. Full note on hover. */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '3px 10px', borderBottom: '1px solid #eef2f7', flexShrink: 0 }}>
                        <span
                          title="Live transcript captured during the call. A polished, diarized version from RingSense AI will replace it once ready."
                          aria-label="Live transcript captured during the call. A polished, diarized version from RingSense AI will replace it once ready."
                          style={{ display: 'inline-flex', alignItems: 'center', cursor: 'help', color: '#94a3b8' }}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                        </span>
                      </div>
                      <div style={{ flex: 1, overflow: 'auto', padding: '12px 14px' }}>
                        {/* Same grouping as the live overlay — one speaker label
                            per consecutive turn (never re-printed per segment). */}
                        {groupTranscript(phoneTranscript).map((group, gi) => (
                          <div key={gi} style={{ marginBottom: 10 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 2, color: group.isSelf ? '#2563eb' : '#16a34a' }}>{group.isSelf ? 'You' : (activeCallContact?.name || 'Caller')}</div>
                            {group.lines.map((ln, li) => (
                              <span key={li} style={{ display: 'block', fontSize: 13, lineHeight: 1.5, color: ln.isFinal ? '#33475b' : '#94a3b8', fontStyle: ln.isFinal ? 'normal' : 'italic' }}>{ln.text}</span>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div style={{ padding: '32px 16px', textAlign: 'center', color: '#99acc2' }}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 8px', display: 'block', opacity: 0.5 }}>
                        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                        <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                        <line x1="12" y1="19" x2="12" y2="23"/>
                        <line x1="8" y1="23" x2="16" y2="23"/>
                      </svg>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>Transcript processing</div>
                      <div style={{ fontSize: 12, marginTop: 4, color: '#7c98b6' }}>
                        The transcript will appear here once RingSense AI finishes processing. This typically takes 1-2 minutes.
                      </div>
                      <button style={{ marginTop: 12, padding: '6px 16px', fontSize: 12, fontWeight: 600, color: '#0091ae', background: '#e8f7fa', border: '1px solid #b6e0ea', borderRadius: 4, cursor: 'pointer' }} onClick={() => {
                        setCallTranscriptLoading(true)
                        // Try to find the activity and check its transcript
                        const phone = dialerInput.replace(/\D/g, '')
                        if (phone) {
                          fetch(`/api/crm/calls?phone=${phone}&limit=1`).then(r => r.json()).then((data: any) => {
                            const call = data?.calls?.[0]
                            if (call?.transcript) {
                              setCallTranscriptText(call.transcript)
                              setCallTranscriptSummary(call.summary || '')
                              setCallTranscriptSentiment(call.sentiment || '')
                            }
                            setCallTranscriptLoading(false)
                          }).catch(() => setCallTranscriptLoading(false))
                        } else {
                          setCallTranscriptLoading(false)
                        }
                      }}>
                        Check for transcript
                      </button>
                    </div>
                  )}
                </div>
              )}
              {/* Footer */}
              <div className="har-review-footer">
                <button className="har-review-followup-btn" onClick={() => {
                  setShowCreateModal('task')
                  setCreateFormData({ title: `Follow up — ${activeCallContact?.name || dialerInput}`, description: callNotes })
                  setCreateError(null)
                }}>
                  + Create Follow Up Task
                </button>
                {callSaved && (
                  <span className="har-review-saved-indicator">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#00bda5" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                    Saved
                  </span>
                )}
              </div>
              </div>
            </div>
            )
          })()}

          {/* === PHONE: JSX — ASSIGN PICKER OVERLAY (call-assign-link-v1) === deal/
              project picker for the post-call wrap-up. Now the SHARED
              CallAssignPicker (variant="phone"): body-portaled floating card to
              the RIGHT of the docked phone (frozen .phone-assign-overlay contract
              — left:336 / bottom:24 / z 9999). Mounted for the whole review so the
              query survives an open→close→open toggle; internal search/probe state
              lives in the component. Zero behavior change vs the pre-extraction
              inline overlay. */}
          {callingState === 'review' && (
            <CallAssignPicker
              variant="phone"
              open={assignPickerOpen}
              suggestedDeals={assignSuggestedDeals}
              suggestName={assignSuggestName}
              onClose={() => { setAssignPickerOpen(false); assignTriggerRef.current?.focus() }}
              onSelect={selectAssignRow}
            />
          )}
        </div>
      )}

      {/* Calling Panel Minimized Badge */}
      {callingPanelOpen && callingPanelMinimized && (callingState === 'dialing' || callingState === 'ringing' || callingState === 'connected') && (
        <div className="har-calling-minimized-badge" onClick={() => setCallingPanelMinimized(false)}>
          <span className={`har-active-status-dot${callingState === 'connected' ? ' connected' : ' ringing'}`} />
          <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.308 20.649 23.379 18.6a2.659 2.659 0 0 0-3.184.582l-1.406 1.656a.62.62 0 0 1-.74.15 17.66 17.66 0 0 1-5.188-3.85 17.634 17.634 0 0 1-3.847-5.184.621.621 0 0 1 .149-.74l1.656-1.404a2.66 2.66 0 0 0 .582-3.187L9.35 4.694A2.659 2.659 0 0 0 6.16 3.55l-1.97.772c-1.158.455-1.955 1.512-2.015 2.748-.492 10.111 8.71 19.31 18.819 18.819 1.236-.06 2.293-.857 2.748-2.016l.773-1.97a2.657 2.657 0 0 0-1.207-3.254Z"/></svg>
          <span>{formatCallTimer(activeCallTimer)}</span>
        </div>
      )}

      {/* Drag-resize handle — sibling of .sidebar (NOT a child) because
       * .sidebar has overflow-x: hidden which clips children at its right
       * edge to sub-pixel slivers. Position-fixed at var(--sidebar-width)
       * so it tracks the sidebar boundary live during drag. Hidden in
       * collapsed mode where the width is locked at 56px. */}
      {!sidebarCollapsed && (
        <div
          className={`sidebar-resize-handle${sidebarResizing ? ' dragging' : ''}`}
          onPointerDown={startSidebarResize}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize sidebar"
          title="Drag to resize sidebar"
        />
      )}
      <aside
        className={`sidebar${sidebarCollapsed ? ' collapsed' : ''}${phoneDocked && callingPanelOpen && !callingPanelMinimized ? ' sidebar-has-docked-phone' : ''}`}
        onMouseEnter={() => setSidebarHovered(true)}
        onMouseLeave={() => setSidebarHovered(false)}
        onClick={(e) => {
          if (e.target === e.currentTarget && secondaryMenu) {
            setSecondaryMenu(null)
            setFlyoutActivated(false)
          }
        }}
      >
        {/* Collapse / Expand toggle */}
        {sidebarHovered && (
          <button className="sidebar-toggle" onClick={toggleSidebar} title={sidebarCollapsed ? 'Expand' : 'Collapse'}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {sidebarCollapsed
                ? <><polyline points="13,17 18,12 13,7"/><polyline points="6,17 11,12 6,7"/></>
                : <><polyline points="11,17 6,12 11,7"/><polyline points="18,17 13,12 18,7"/></>
              }
            </svg>
          </button>
        )}

        <div className="sidebar-scroll-wrap" ref={sidebarScrollRef} onClick={(e) => {
          if (e.target === e.currentTarget && secondaryMenu) { setSecondaryMenu(null); setFlyoutActivated(false) }
        }}>
          <button
            className={`sidebar-scroll-arrow up${sidebarScrollUp ? ' visible' : ''}`}
            onMouseEnter={() => startSidebarScroll(-1)}
            onMouseLeave={stopSidebarScroll}
            onClick={() => navListRef.current?.scrollBy({ top: -120, behavior: 'smooth' })}
            aria-label="Scroll up"
          >
            <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor" style={{ transform: 'rotate(180deg)' }}><path d="M16.59 20.4c.103-.052.191-.108.273-.173l-.003.003.16-.1h.07l11-10.51c.312-.29.507-.702.507-1.16 0-.873-.708-1.582-1.582-1.582-.416 0-.794.16-1.076.422l.001-.001-9.94 9.47-9.94-9.47c-.289-.311-.701-.504-1.157-.504-.873 0-1.58.707-1.58 1.58 0 .482.215.913.555 1.203l.002.002 11 10.51h.07l.16.1c.079.062.167.118.261.166l.009.004c.083.027.182.048.283.059l.007.001c.087.024.19.042.295.05h.005c.11-.008.213-.026.311-.053l-.011.003.064.001c.09 0 .179-.008.265-.023l-.009.001z"/></svg>
          </button>
        <nav className="nav-list" ref={navListRef} aria-label="Workspace sections" onClick={(e) => {
          if (e.target === e.currentTarget && secondaryMenu) { setSecondaryMenu(null); setFlyoutActivated(false) }
        }}>
          {([
            { key: 'command', label: 'Home', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9,22 9,12 15,12 15,22"/></svg>, sub: null, sectionLabel: null },
            { key: 'calendar', label: 'Calendar', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>, sub: null, sectionLabel: null },
            // Inbox top-level nav entry removed — kept inside the CRM submenu
            // as a single source of truth. The submenu "Inbox" entry below now
            // routes to activeView='inbox' (same view this used to hit).
            // { key: 'inbox', label: 'Inbox', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>, sub: null, sectionLabel: null },
            { key: 'crm', label: 'CRM', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>, sub: 'crm', sectionLabel: null },
            /* bp-mkt-v1: Marketing Hub — re-exposes the wired `marketing` flyout (Workspaces + Channels) */
            { key: 'marketing', label: 'Marketing', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 11l18-5v12L3 14v-3z"/><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/></svg>, sub: 'marketing', sectionLabel: null },
            /* bp-devws-v1: Delivery (dev-workspace) top-level view → #/delivery */
            { key: 'delivery', label: 'Delivery', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91 0Z"/><path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2Z"/><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/></svg>, sub: null, sectionLabel: null },
            { key: 'reporting', label: 'Reporting', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21.21 15.89A10 10 0 1 1 8 2.83"/><path d="M22 12A10 10 0 0 0 12 2v10z"/></svg>, sub: 'reporting', sectionLabel: null },
            { key: 'accounting', label: 'Accounting', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z"/><path d="M8 10h8"/><path d="M8 14h4"/></svg>, sub: 'accounting', sectionLabel: null },
            // Team opens the Feed hub; the directory lives inside the feed sidebar.
            { key: 'feed', label: 'Social Feed', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><circle cx="4" cy="6" r="1.4" fill="currentColor"/><circle cx="4" cy="12" r="1.4" fill="currentColor"/><circle cx="4" cy="18" r="1.4" fill="currentColor"/></svg>, sub: null, sectionLabel: null },
            ...(myCapabilities && !myCapabilities.access_hr_center ? [] : [{ key: 'hr-center', label: 'HR Center', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>, sub: 'hr', sectionLabel: null }]),
          ] as { key: string; label: string; icon: React.ReactNode; sub: string | null; sectionLabel: string | null }[]).flatMap((item) => {
            // Per-role tab visibility (Settings → Roles & Permissions).
            // Home is never hideable.
            if (item.key !== 'command' && isNavHidden(item.key)) return []
            const rendered: React.ReactNode[] = []
            if (item.sectionLabel && !sidebarCollapsed) {
              rendered.push(
                <div key={`sec-${item.key}`} className="nav-section-label" aria-hidden="true">{item.sectionLabel}</div>
              )
            }
            rendered.push(
            <div
              key={item.key}
              className="nav-item-wrap"
              onMouseEnter={(e) => {
                if (hoverCloseTimer.current) { clearTimeout(hoverCloseTimer.current); hoverCloseTimer.current = null }
                if (item.sub && (sidebarCollapsed || secondaryMenu)) {
                  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
                  setSecondaryMenuTop(rect.top)
                  setSecondaryMenuFitTop(rect.top)
                  setSecondaryMenu(item.sub)
                }
              }}
              onMouseLeave={() => {
                if (item.sub && secondaryMenu === item.sub && sidebarCollapsed) {
                  hoverCloseTimer.current = setTimeout(() => { setSecondaryMenu(null); setFlyoutActivated(false) }, 150)
                }
              }}
            >
              <button
                type="button"
                className={`nav-item${
                  secondaryMenu === item.sub && item.sub ? ' active' :
                  // Team nav opens Feed, but remains active when legacy #/team is used for the org explorer.
                  !item.sub && item.key === 'feed' && (activeView === 'team' || activeView === 'feed') ? ' active' :
                  !item.sub && activeView === item.key ? ' active' :
                  item.sub && secondaryMenu !== item.sub && ({
                    crm: ['contacts','companies','leads','deals','tickets','email','calls','tasks','meets','snippets','templates','playbooks','projects'],
                    marketing: ['marketing','campaigns','marketing-email','ads','social','forms','landing-pages'],
                    sales: ['forecasts','payments','deals-board','target-accounts','sales-documents','activity-feed','meetings-scheduler','coaching-playlists','prospecting'],
                    service: ['service-tickets','knowledge-base','customer-portal','feedback-surveys'],
                    automation: ['workflows','sequences','chatflows'],
                    reporting: ['dashboards','reports','analytics'],
                    accounting: ['accounting'],
                    hr: ['hr-center', 'timesheets'],
                  } as Record<string, string[]>)[item.sub]?.includes(activeView) ? ' active' : ''
                }`}
                onClick={(e) => {
                  if (item.sub) {
                    if (secondaryMenu === item.sub) {
                      setSecondaryMenu(null)
                      setFlyoutActivated(false)
                    } else {
                      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
                      setSecondaryMenuTop(rect.top)
                      setSecondaryMenuFitTop(rect.top)
                      setSecondaryMenu(item.sub)
                      setFlyoutActivated(true)
                    }
                  } else {
                    setActiveView(item.key as ViewMode)
                  }
                }}
              >
                <span className="nav-icon">{item.icon}</span>
                {!sidebarCollapsed && <span className="nav-label">{item.label}</span>}
                {!sidebarCollapsed && item.sub && (
                  <span className={`nav-arrow${secondaryMenu === item.sub ? ' open' : ''}`}>
                    <svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M11 27.71c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62l9.48-9.48-9.48-9.47a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0L22.1 15.48c.45.45.45 1.17 0 1.62L11.81 27.39c-.22.22-.52.33-.81.33Z"/></svg>
                  </span>
                )}
              </button>
            </div>
            )
            return rendered
          })}
        </nav>
          <button
            className={`sidebar-scroll-arrow down${sidebarScrollDown ? ' visible' : ''}`}
            onMouseEnter={() => startSidebarScroll(1)}
            onMouseLeave={stopSidebarScroll}
            onClick={() => navListRef.current?.scrollBy({ top: 120, behavior: 'smooth' })}
            aria-label="Scroll down"
          >
            <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M16.59 20.4c.103-.052.191-.108.273-.173l-.003.003.16-.1h.07l11-10.51c.312-.29.507-.702.507-1.16 0-.873-.708-1.582-1.582-1.582-.416 0-.794.16-1.076.422l.001-.001-9.94 9.47-9.94-9.47c-.289-.311-.701-.504-1.157-.504-.873 0-1.58.707-1.58 1.58 0 .482.215.913.555 1.203l.002.002 11 10.51h.07l.16.1c.079.062.167.118.261.166l.009.004c.083.027.182.048.283.059l.007.001c.087.024.19.042.295.05h.005c.11-.008.213-.026.311-.053l-.011.003.064.001c.09 0 .179-.008.265-.023l-.009.001z"/></svg>
          </button>
        </div>

        {/* Phone dock zone */}
        {callingPanelOpen && !callingPanelMinimized && !phoneDocked && phoneDragging && (
          <div className={`phone-dock-zone${phoneDockHover ? ' dock-ready' : ''}`}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><rect x="5" y="2" width="14" height="20" rx="3"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>
            <span>Dock phone here</span>
          </div>
        )}

        {/* Docked phone — separator line */}
        {callingPanelOpen && !callingPanelMinimized && phoneDocked && (
          <div className="phone-dock-separator" />
        )}
      </aside>

      {secondaryMenu && (
        <div
          className={`secondary-menu${sidebarCollapsed ? ' sidebar-is-collapsed' : ''}`}
          ref={secondaryRef}
          style={{ top: secondaryMenuFitTop, maxHeight: `calc(100vh - ${secondaryMenuFitTop}px - 8px)` }}
          onMouseEnter={() => { if (hoverCloseTimer.current) { clearTimeout(hoverCloseTimer.current); hoverCloseTimer.current = null } }}
          onMouseLeave={() => { if (sidebarCollapsed) { hoverCloseTimer.current = setTimeout(() => { setSecondaryMenu(null); setFlyoutActivated(false) }, 150) } }}
        >
          <div className="secondary-menu-header">
            <h5 className="secondary-menu-heading">{
              secondaryMenu === 'crm' ? 'CRM' :
              secondaryMenu === 'marketing' ? 'Marketing' :
              secondaryMenu === 'sales' ? 'Sales' :
              secondaryMenu === 'service' ? 'Service' :
              secondaryMenu === 'automation' ? 'Automation' :
              secondaryMenu === 'reporting' ? 'Reporting' :
              secondaryMenu === 'accounting' ? 'Accounting' :
              secondaryMenu === 'hr' ? 'HR Center' : ''
            }</h5>
          </div>
          <ul className="secondary-menu-list">
            {secondaryMenu === 'crm' && <>
              {!isNavHidden('crm.contacts') && <li><button className={`secondary-menu-item${activeView === 'contacts' ? ' active' : ''}`} onClick={() => setActiveView('contacts')}><span>Contacts</span></button></li>}
              {!isNavHidden('crm.companies') && <li><button className={`secondary-menu-item${activeView === 'companies' ? ' active' : ''}`} onClick={() => setActiveView('companies')}><span>Clients</span></button></li>}
              {!isNavHidden('crm.leads') && <li><button className={`secondary-menu-item${activeView === 'leads' ? ' active' : ''}`} onClick={() => setActiveView('leads')}><span>Leads</span></button></li>}
              {!isNavHidden('crm.deals') && <li><button className={`secondary-menu-item${activeView === 'deals' ? ' active' : ''}`} onClick={() => setActiveView('deals')}><span>Deals</span></button></li>}
              {!isNavHidden('crm.projects') && <li><button className={`secondary-menu-item${activeView === 'projects' ? ' active' : ''}`} onClick={() => setActiveView('projects')}><span>Projects</span></button></li>}
              {!isNavHidden('crm.tickets') && <li><button className={`secondary-menu-item${activeView === 'tickets' ? ' active' : ''}`} onClick={() => setActiveView('tickets')}><span>Tickets</span></button></li>}
              <li role="separator"><hr className="secondary-menu-divider" /></li>
              {!isNavHidden('crm.email') && <li><button className={`secondary-menu-item${activeView === 'email' ? ' active' : ''}`} onClick={() => setActiveView('email')}><span>Email</span></button></li>}
              {!isNavHidden('crm.calls') && <li><button className={`secondary-menu-item${activeView === 'calls' ? ' active' : ''}`} onClick={() => setActiveView('calls')}><span>Calls</span></button></li>}
              {!isNavHidden('crm.tasks') && <li><button className={`secondary-menu-item${activeView === 'tasks' ? ' active' : ''}`} onClick={() => setActiveView('tasks')}><span>Tasks</span></button></li>}
              {!isNavHidden('crm.meets') && <li><button className={`secondary-menu-item${activeView === 'meets' ? ' active' : ''}`} onClick={() => setActiveView('meets')}><span>Meets</span></button></li>}
              {/* "My Documents" removed from the CRM submenu 2026-06-10 —
                  it lives in the profile dropdown now (links out to
                  ops.botpros.ai). The #/my-documents route still exists
                  and stays guarded by the crm.my-documents nav key. */}
              {/* Timesheets moved to the HR Center nav bar 2026-06-11 (user
                  request). The #/timesheets route + crm.timesheets nav key are
                  unchanged, so direct links and saved permissions still work. */}
            </>}
            {secondaryMenu === 'marketing' && (() => {
              // bp-mkt-v1 — Workspaces route to the single lazy `marketing` view via marketingSubView;
              // Channels are the existing D1-backed views absorbed under the Marketing flyout.
              const mktNav = (sv: MarketingSubView) => { setActiveViewRaw('marketing'); setMarketingSubView(sv); setSecondaryMenu(null); setFlyoutActivated(false); window.location.hash = sv === 'hub' ? '/marketing' : `/marketing/${sv}` }
              const campNav = () => { if (blockNotificationPrefsExit()) return; setActiveViewRaw('campaigns'); setCampaignsSubView('list'); setSecondaryMenu(null); setFlyoutActivated(false); window.location.hash = '/campaigns' }
              const grpLabel: React.CSSProperties = { padding: '8px 16px 4px', fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#64748b' }
              const wsItem = (sv: MarketingSubView, label: string) => (
                <li><button className={`secondary-menu-item${activeView === 'marketing' && marketingSubView === sv ? ' active' : ''}`} onClick={() => mktNav(sv)}><span>{label}</span></button></li>
              )
              return <>
                <li data-marker="bp-mkt-ia-v1" style={grpLabel} aria-hidden="true">Journey</li>
                {wsItem('hub', 'Ground')}
                {wsItem('strategy', 'Strategy')}
                {wsItem('content', 'Assets')}
                {wsItem('outbound', 'Distribution')}
                {wsItem('conversion', 'Conversion')}
                <li role="separator"><hr className="secondary-menu-divider" /></li>
                <li style={grpLabel} aria-hidden="true">Utility</li>
                {wsItem('library', 'Library')}
                <li role="separator"><hr className="secondary-menu-divider" /></li>
                <li style={grpLabel} aria-hidden="true">Channels</li>
                <li><button data-campaign-route={campaignsSubView} className={`secondary-menu-item${activeView === 'campaigns' ? ' active' : ''}`} onClick={campNav}><span>Campaigns</span></button></li>
                <li><button className={`secondary-menu-item${activeView === 'marketing-email' ? ' active' : ''}`} onClick={() => setActiveView('marketing-email')}><span>Email</span></button></li>
                <li><button className={`secondary-menu-item${activeView === 'ads' ? ' active' : ''}`} onClick={() => setActiveView('ads')}><span>Ads</span></button></li>
                <li><button className={`secondary-menu-item${activeView === 'social' ? ' active' : ''}`} onClick={() => setActiveView('social')}><span>Social</span></button></li>
                <li><button className={`secondary-menu-item${activeView === 'forms' ? ' active' : ''}`} onClick={() => setActiveView('forms')}><span>Forms</span></button></li>
                <li><button className={`secondary-menu-item${activeView === 'landing-pages' ? ' active' : ''}`} onClick={() => setActiveView('landing-pages')}><span>Landing Pages</span></button></li>
              </>
            })()}
            {secondaryMenu === 'sales' && <>
              <li><button className={`secondary-menu-item${activeView === 'prospecting' ? ' active' : ''}`} onClick={() => setActiveView('prospecting')}><span>Prospecting</span></button></li>
              <li><button className={`secondary-menu-item${activeView === 'deals' ? ' active' : ''}`} onClick={() => setActiveView('deals')}><span>Deals</span></button></li>
              <li><button className={`secondary-menu-item${activeView === 'forecasts' ? ' active' : ''}`} onClick={() => setActiveView('forecasts')}><span>Forecasts</span></button></li>
              <li><button className={`secondary-menu-item${activeView === 'target-accounts' ? ' active' : ''}`} onClick={() => setActiveView('target-accounts')}><span>Target Accounts</span></button></li>
              <li role="separator"><hr className="secondary-menu-divider" /></li>
              <li><button className={`secondary-menu-item${activeView === 'activity-feed' ? ' active' : ''}`} onClick={() => setActiveView('activity-feed')}><span>Activity Feed</span></button></li>
              <li><button className={`secondary-menu-item${activeView === 'sales-documents' ? ' active' : ''}`} onClick={() => setActiveView('sales-documents')}><span>Documents</span></button></li>
              <li><button className={`secondary-menu-item${activeView === 'meetings-scheduler' ? ' active' : ''}`} onClick={() => setActiveView('meetings-scheduler')}><span>Meetings</span></button></li>
              <li><button className={`secondary-menu-item${activeView === 'coaching-playlists' ? ' active' : ''}`} onClick={() => setActiveView('coaching-playlists')}><span>Coaching Playlists</span></button></li>
              <li role="separator"><hr className="secondary-menu-divider" /></li>
              <li><button className={`secondary-menu-item${activeView === 'payments' ? ' active' : ''}`} onClick={() => setActiveView('payments')}><span>Payments</span></button></li>
              <li><button className={`secondary-menu-item${activeView === 'quotes' ? ' active' : ''}`} onClick={() => setActiveView('quotes')}><span>Quotes</span></button></li>
              <li><button className={`secondary-menu-item${activeView === 'products' ? ' active' : ''}`} onClick={() => setActiveView('products')}><span>Products</span></button></li>
            </>}
            {secondaryMenu === 'service' && <>
              <li><button className={`secondary-menu-item${activeView === 'tickets' ? ' active' : ''}`} onClick={() => setActiveView('tickets')}><span>Tickets</span></button></li>
              <li><button className={`secondary-menu-item${activeView === 'knowledge-base' ? ' active' : ''}`} onClick={() => setActiveView('knowledge-base')}><span>Knowledge Base</span></button></li>
              <li><button className={`secondary-menu-item${activeView === 'customer-portal' ? ' active' : ''}`} onClick={() => setActiveView('customer-portal')}><span>Customer Portal</span></button></li>
              <li><button className={`secondary-menu-item${activeView === 'feedback-surveys' ? ' active' : ''}`} onClick={() => setActiveView('feedback-surveys')}><span>Feedback Surveys</span></button></li>
            </>}
            {secondaryMenu === 'automation' && <>
              <li><button className={`secondary-menu-item${activeView === 'workflows' ? ' active' : ''}`} onClick={() => setActiveView('workflows')}><span>Workflows</span></button></li>
              <li><button className={`secondary-menu-item${activeView === 'sequences' ? ' active' : ''}`} onClick={() => setActiveView('sequences')}><span>Sequences</span></button></li>
              <li><button className={`secondary-menu-item${activeView === 'chatflows' ? ' active' : ''}`} onClick={() => setActiveView('chatflows')}><span>Chatflows</span></button></li>
            </>}
            {secondaryMenu === 'reporting' && <>
              {!isNavHidden('reporting.dashboards') && <li><button className={`secondary-menu-item${activeView === 'dashboards' ? ' active' : ''}`} onClick={() => setActiveView('dashboards')}><span>Dashboards</span></button></li>}
              {!isNavHidden('reporting.reports') && <li><button className={`secondary-menu-item${activeView === 'reports' ? ' active' : ''}`} onClick={() => setActiveView('reports')}><span>Reports</span></button></li>}
              {!isNavHidden('reporting.analytics') && <li><button className={`secondary-menu-item${activeView === 'analytics' ? ' active' : ''}`} onClick={() => setActiveView('analytics')}><span>Analytics Tools</span></button></li>}
              {(!myCapabilities || myCapabilities.access_settings) && (
                <li><button className={`secondary-menu-item${activeView === 'preferences' ? ' active' : ''}`} onClick={() => setActiveView('preferences')}><span>Settings</span></button></li>
              )}
            </>}
            {secondaryMenu === 'accounting' && <>
              {!isNavHidden('accounting.overview') && <li><button className={`secondary-menu-item${activeView === 'accounting' && accountingSubView === 'overview' ? ' active' : ''}`} onClick={() => { setActiveViewRaw('accounting'); setAccountingSubView('overview'); setSecondaryMenu(null); window.location.hash = '/accounting/overview' }}><span>Overview</span></button></li>}
              {!isNavHidden('accounting.chart-of-accounts') && <li><button className={`secondary-menu-item${activeView === 'accounting' && accountingSubView === 'chart-of-accounts' ? ' active' : ''}`} onClick={() => { setActiveViewRaw('accounting'); setAccountingSubView('chart-of-accounts'); setSecondaryMenu(null); window.location.hash = '/accounting/chart-of-accounts' }}><span>Chart of Accounts</span></button></li>}
              {!isNavHidden('accounting.journal-entries') && <li><button className={`secondary-menu-item${activeView === 'accounting' && accountingSubView === 'journal-entries' ? ' active' : ''}`} onClick={() => { setActiveViewRaw('accounting'); setAccountingSubView('journal-entries'); setSecondaryMenu(null); window.location.hash = '/accounting/journal-entries' }}><span>Journal Entries</span></button></li>}
              <li role="separator"><hr className="secondary-menu-divider" /></li>
              {!isNavHidden('accounting.accounts-payable') && <li><button className={`secondary-menu-item${activeView === 'accounting' && accountingSubView === 'accounts-payable' ? ' active' : ''}`} onClick={() => { setActiveViewRaw('accounting'); setAccountingSubView('accounts-payable'); setSecondaryMenu(null); window.location.hash = '/accounting/accounts-payable' }}><span>Accounts Payable</span></button></li>}
              {!isNavHidden('accounting.vendors') && <li><button className={`secondary-menu-item${activeView === 'accounting' && accountingSubView === 'vendors' ? ' active' : ''}`} onClick={() => { setActiveViewRaw('accounting'); setAccountingSubView('vendors'); setSecondaryMenu(null); window.location.hash = '/accounting/vendors' }}><span>Vendors</span></button></li>}
              {!isNavHidden('accounting.accounts-receivable') && <li><button className={`secondary-menu-item${activeView === 'accounting' && accountingSubView === 'accounts-receivable' ? ' active' : ''}`} onClick={() => { setActiveViewRaw('accounting'); setAccountingSubView('accounts-receivable'); setSecondaryMenu(null); window.location.hash = '/accounting/accounts-receivable' }}><span>Accounts Receivable</span></button></li>}
              {!isNavHidden('accounting.expenses') && <li><button className={`secondary-menu-item${activeView === 'accounting' && accountingSubView === 'expenses' ? ' active' : ''}`} onClick={() => { setActiveViewRaw('accounting'); setAccountingSubView('expenses'); setSecondaryMenu(null); window.location.hash = '/accounting/expenses' }}><span>Expenses</span></button></li>}
              {!isNavHidden('accounting.bank-reconciliation') && <li><button className={`secondary-menu-item${activeView === 'accounting' && accountingSubView === 'bank-reconciliation' ? ' active' : ''}`} onClick={() => { setActiveViewRaw('accounting'); setAccountingSubView('bank-reconciliation'); setSecondaryMenu(null); window.location.hash = '/accounting/bank-reconciliation' }}><span>Bank Reconciliation</span></button></li>}
              {!isNavHidden('accounting.banking') && <li><button className={`secondary-menu-item${activeView === 'accounting' && accountingSubView === 'banking' ? ' active' : ''}`} onClick={() => { setActiveViewRaw('accounting'); setAccountingSubView('banking'); setSecondaryMenu(null); window.location.hash = '/accounting/banking' }}><span>Banking</span></button></li>}
              {!isNavHidden('accounting.feed-review') && <li><button className={`secondary-menu-item${activeView === 'accounting' && accountingSubView === 'feed-review' ? ' active' : ''}`} onClick={() => { setActiveViewRaw('accounting'); setAccountingSubView('feed-review'); setSecondaryMenu(null); window.location.hash = '/accounting/feed-review' }}><span>For Review</span></button></li>}
              <li role="separator"><hr className="secondary-menu-divider" /></li>
              {!isNavHidden('accounting.job-costing') && <li><button className={`secondary-menu-item${activeView === 'accounting' && accountingSubView === 'job-costing' ? ' active' : ''}`} onClick={() => { setActiveViewRaw('accounting'); setAccountingSubView('job-costing'); setSecondaryMenu(null); window.location.hash = '/accounting/job-costing' }}><span>Job Costing</span></button></li>}
              {!isNavHidden('accounting.wip-schedule') && <li><button className={`secondary-menu-item${activeView === 'accounting' && accountingSubView === 'wip-schedule' ? ' active' : ''}`} onClick={() => { setActiveViewRaw('accounting'); setAccountingSubView('wip-schedule'); setSecondaryMenu(null); window.location.hash = '/accounting/wip-schedule' }}><span>WIP Schedule</span></button></li>}
              <li role="separator"><hr className="secondary-menu-divider" /></li>
              {!isNavHidden('accounting.profit-loss') && <li><button className={`secondary-menu-item${activeView === 'accounting' && accountingSubView === 'profit-loss' ? ' active' : ''}`} onClick={() => { setActiveViewRaw('accounting'); setAccountingSubView('profit-loss'); setSecondaryMenu(null); window.location.hash = '/accounting/profit-loss' }}><span>Profit &amp; Loss</span></button></li>}
              {!isNavHidden('accounting.balance-sheet') && <li><button className={`secondary-menu-item${activeView === 'accounting' && accountingSubView === 'balance-sheet' ? ' active' : ''}`} onClick={() => { setActiveViewRaw('accounting'); setAccountingSubView('balance-sheet'); setSecondaryMenu(null); window.location.hash = '/accounting/balance-sheet' }}><span>Balance Sheet</span></button></li>}
              {!isNavHidden('accounting.trial-balance') && <li><button className={`secondary-menu-item${activeView === 'accounting' && accountingSubView === 'trial-balance' ? ' active' : ''}`} onClick={() => { setActiveViewRaw('accounting'); setAccountingSubView('trial-balance'); setSecondaryMenu(null); window.location.hash = '/accounting/trial-balance' }}><span>Trial Balance</span></button></li>}
              {!isNavHidden('accounting.ar-aging') && <li><button className={`secondary-menu-item${activeView === 'accounting' && accountingSubView === 'ar-aging' ? ' active' : ''}`} onClick={() => { setActiveViewRaw('accounting'); setAccountingSubView('ar-aging'); setSecondaryMenu(null); window.location.hash = '/accounting/ar-aging' }}><span>AR Aging</span></button></li>}
            </>}
            {secondaryMenu === 'hr' && <>
              {HR_TABS.filter(t => !t.navKey || !isNavHidden(t.navKey)).map(t => {
                const isActive = t.key === 'timesheets'
                  ? activeView === 'timesheets'
                  : (activeView === 'hr-center' && hrPortalView === t.key)
                return (
                  <li key={t.key}><button className={`secondary-menu-item${isActive ? ' active' : ''}`} onClick={() => navigateHrTab(t.key)}><span>{t.label}</span></button></li>
                )
              })}
            </>}
          </ul>
        </div>
      )}

      <main className="workspace">
        <div className="view-stack">
          {activeView === 'command' && (
            <div className="home-bento" data-marker={HOME_BENTO_MARKER} style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'auto', position: 'relative' }}>
            <div className="command-toolbar">
              <div className="command-filter-group">
                <select value={dashboardPeriod} onChange={(e) => setDashboardPeriod(e.target.value as DashboardPeriod)} className="command-select" aria-label="Period">
                  <option value="today">Today</option>
                  <option value="week">This week</option>
                  <option value="month">This month</option>
                  <option value="quarter">This quarter</option>
                  <option value="year">This year</option>
                  <option value="all">All time</option>
                </select>
                <select value={commandOwnerFilter} onChange={(e) => setCommandOwnerFilter(e.target.value)} className="command-select" aria-label="Owner">
                  {commandOwnerOptions.map((owner) => <option key={owner.key} value={owner.key}>{owner.label}</option>)}
                </select>
              </div>
              <div className="command-toolbar-actions">
                {commandLastUpdated && <span className="command-freshness">Updated {commandLastUpdated.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>}
                <button type="button" className="command-ghost-btn" onClick={() => setCommandRefreshTick((tick) => tick + 1)}>Refresh</button>
                <button
                  type="button"
                  className="command-primary-btn"
                  onClick={() => {
                    setCreateFormData({})
                    setCreateEmails([''])
                    setCreatePhones([{ type: 'Mobile', number: '', ext: '' }])
                    setEmailTouched(false)
                    setSmartCreateSearch('')
                    setSmartCreateResults(null)
                    setCreateError(null)
                    setSmartCreateType('task')
                    setShowCreateModal('task')
                    setSmartCreateOpen(true)
                  }}
                >New task</button>
                <button
                  type="button"
                  className="command-primary-btn"
                  onClick={() => {
                    setCreateError(null)
                    setEmailTouched(false)
                    setCreateEmails([''])
                    setCreatePhones([{ type: 'Mobile', number: '', ext: '' }])
                    const d = new Date(); d.setMonth(d.getMonth() + 1, 0)
                    setCreateFormData({ dealPipeline: '0', closeDate: d.toISOString().slice(0, 10), dealType: 'residential', sendConfirmationEmail: true })
                    setSmartCreateSearch('')
                    setSmartCreateResults(null)
                    setSmartCreateType('deal')
                    setShowCreateModal('deal')
                    setSmartCreateOpen(true)
                  }}
                >New deal</button>
              </div>
            </div>
            {commandError && <div className="command-alert">{commandError}</div>}
            {!isNavHidden('widget.kpis') && (
              <HomeKpiStrip
                loading={commandLoading || homeAccountingLoading}
                items={[
                  { key: 'active-deals', label: 'Active deals', value: commandPipelineCount.toLocaleString(), helper: commandOwnerFilter === 'all' ? 'Company pipeline' : commandOwnerFilter },
                  { key: 'pipeline-value', label: 'Pipeline value', value: currencyFormatter.format(commandPipelineValue), helper: 'Sales pipeline' },
                  { key: 'won', label: `Won ${periodLabel}`, value: commandWonCount.toLocaleString(), helper: currencyFormatter.format(commandWonValue) },
                  { key: 'open-ar', label: 'Open AR', value: homeAccounting ? currencyFormatter.format(homeAccounting.openAr) : homeAccountingError ? '—' : currencyFormatter.format(0), helper: homeAccounting ? 'Accounts receivable' : 'Accounting snapshot' },
                  { key: 'open-tasks', label: 'Open tasks', value: commandFilteredTasks.length.toLocaleString(), helper: `${overdueCommandTasks.length} overdue` },
                ].filter(kpi => !isNavHidden(`widget.kpis.${kpi.key}`))}
              />
            )}
            <div className="command-grid" style={{ flex: 1, height: 'auto' }}>
              {/* Row 1 — quarter-width signal badges (home-bento-v3). Click a
                  caret to open the full breakdown popover incl. zero chips. */}
              {myCapabilities && !myCapabilities.access_hr_center && (() => {
                const pending = myDocsAssignments.filter(a => a.status !== 'signed')
                const overdue = pending.filter(a => a.due_date && new Date(a.due_date) < new Date())
                const dueSoon = pending.filter(a => {
                  if (!a.due_date) return false
                  const d = new Date(a.due_date).getTime() - Date.now()
                  return d >= 0 && d < 7 * 86400000
                })
                const signedCount = myDocsAssignments.length - pending.length
                return (
                  <HomeSignalSection
                    title="My Documents"
                    subtitle="Documents assigned to you for review and signature."
                    openLabel="Open documents"
                    onOpen={() => setActiveView('my-documents')}
                    loading={myDocsLoading}
                    loaded={!myDocsLoading}
                    error={false}
                    chips={[
                      { key: 'my-pending', label: 'Pending signatures', value: pending.length, tone: pending.length > 0 ? 'warn' : 'neutral', attention: true, iconBg: '#ffedd5', iconColor: '#ea580c', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M9 15l2 2 4-4"/></svg>, action: () => setActiveView('my-documents') },
                      { key: 'my-overdue', label: 'Overdue', value: overdue.length, tone: overdue.length > 0 ? 'bad' : 'neutral', attention: true, iconBg: '#fee2e2', iconColor: '#dc2626', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>, action: () => setActiveView('my-documents') },
                      { key: 'my-due-soon', label: 'Due this week', value: dueSoon.length, tone: dueSoon.length > 0 ? 'warn' : 'neutral', attention: true, iconBg: '#fef3c7', iconColor: '#d97706', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>, action: () => setActiveView('my-documents') },
                      { key: 'my-signed', label: 'Signed', value: signedCount, tone: 'neutral', iconBg: '#dcfce7', iconColor: '#15803d', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>, action: () => setActiveView('my-documents') },
                    ]}
                  />
                )
              })()}
              {(!myCapabilities || myCapabilities.access_hr_center) && !isNavHidden('widget.hr-queue') && (
              <HomeSignalSection
                title="HR Center"
                subtitle="Employee documents and onboarding status."
                openLabel="Open HR"
                onOpen={() => setActiveView('hr-center')}
                loading={hrPortalDashboardLoading}
                loaded={hrPortalDashboard !== null}
                error={hrPortalDashboardError}
                onRetry={() => setCommandRefreshTick((t) => t + 1)}
                chips={[
                { key: 'employer-pending', label: 'Employer pending', value: hrPortalDashboard?.stats?.employerPending || 0, tone: (hrPortalDashboard?.stats?.employerPending || 0) > 0 ? 'warn' : 'neutral', iconBg: '#fef3c7', iconColor: '#d97706', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M9 15l2 2 4-4"/></svg>, action: () => { setActiveView('hr-center'); setHrPortalView('employer-signing' as any) } },
                { key: 'pending-sigs', label: 'Pending signatures', value: hrPortalDashboard?.stats?.pending || 0, tone: (hrPortalDashboard?.stats?.pending || 0) > 0 ? 'warn' : 'neutral', iconBg: '#ffedd5', iconColor: '#ea580c', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>, action: () => { setActiveView('hr-center'); setHrPortalView('assignments') } },
                { key: 'overdue', label: 'Overdue', value: hrPortalDashboard?.stats?.overdue || 0, tone: (hrPortalDashboard?.stats?.overdue || 0) > 0 ? 'warn' : 'neutral', iconBg: '#fee2e2', iconColor: '#dc2626', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>, action: () => { setActiveView('hr-center'); setHrPortalView('assignments'); setHrPortalAssignmentFilter2('overdue') } },
                { key: 'new-applicants', label: 'New applicants', value: hrPortalDashboard?.stats?.newApplicants || 0, tone: (hrPortalDashboard?.stats?.newApplicants || 0) > 0 ? 'warn' : 'neutral', iconBg: '#eff6ff', iconColor: '#3b82f6', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>, action: () => { setActiveView('hr-center'); setHrPortalView('pipelines'); setHrPortalActivePipeline('18') } },
              ].filter(item => !isNavHidden(`widget.hr-queue.${item.key}`))}
            />
            )}
            {!isNavHidden('widget.work-queue') && (
            <HomeSignalSection
              title="Today's work queue"
              subtitle="Tasks, stale records, and handoffs that need attention."
              openLabel="Open tasks"
              onOpen={() => setActiveView('tasks')}
              variant="plain"
              loading={commandLoading}
              loaded={!commandLoading}
              error={false}
              chips={commandWorkQueue
                .filter((item) => !isNavHidden(`widget.work-queue.${item.key}`))
                .map((item) => ({ ...item, attention: Boolean((item as any).attention) }))}
            />
            )}
            <HomeSignalSection
              title="Accounting snapshot"
              subtitle="Receivables, overdue invoices, and month-to-date revenue."
              openLabel="Open accounting"
              onOpen={() => { setActiveView('accounting'); window.location.hash = '/accounting' }}
              loading={homeAccountingLoading}
              loaded={homeAccounting !== null}
              error={homeAccountingError}
              onRetry={() => setCommandRefreshTick((tick) => tick + 1)}
              chips={[
                { key: 'open-ar', label: 'Open AR', value: homeAccounting?.openAr || 0, displayValue: homeAccounting ? currencyFormatter.format(homeAccounting.openAr) : '—', tone: (homeAccounting?.openAr || 0) > 0 ? 'active' : 'neutral', iconBg: '#e0f2fe', iconColor: '#0369a1', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M3 10h18M6 14h3M6 18h6"/><rect x="3" y="3" width="18" height="18" rx="2"/></svg>, action: () => { setActiveView('accounting'); window.location.hash = '/accounting' } },
                { key: 'overdue-invoices', label: 'Overdue invoices', value: homeAccounting?.overdueInvoices || 0, tone: (homeAccounting?.overdueInvoices || 0) > 0 ? 'warn' : 'neutral', attention: true, iconBg: '#fee2e2', iconColor: '#b91c1c', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>, action: () => { setActiveView('accounting'); window.location.hash = '/accounting' } },
                { key: 'mtd-revenue', label: 'MTD revenue', value: homeAccounting?.mtdRevenue || 0, displayValue: homeAccounting?.mtdRevenue != null ? currencyFormatter.format(homeAccounting.mtdRevenue) : '—', tone: (homeAccounting?.mtdRevenue || 0) > 0 ? 'active' : 'neutral', iconBg: '#dcfce7', iconColor: '#15803d', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 19V5M4 19h16"/><path d="m7 15 3-4 3 2 5-6"/></svg>, action: () => { setActiveView('accounting'); window.location.hash = '/accounting' } },
              ]}
            />

              {/* Sales pipeline spans cols 1–3, full height. */}
              {!isNavHidden('widget.pipeline-panel') && (
              <section
                className={`panel home-pipeline${expandedPanel === 'pipeline' ? ' is-expanded-overlay' : ''}`}
                style={{
                  display: expandedPanel && expandedPanel !== 'pipeline' ? 'none' : undefined,
                }}
              >
                <div className="panel-head">
                  {expandedPanel === 'pipeline' && (
                    <button type="button" className="panel-back-btn" onClick={() => setExpandedPanel(null)} title="Back to dashboard" aria-label="Back to dashboard">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
                    </button>
                  )}
                  <div>
                    <h2>Sales pipeline</h2>
                    <p>{liveDeals ? `${commandDeals.length} visible deals` : 'Loading deal activity'}</p>
                  </div>
                  {expandedPanel === 'pipeline' && (
                    <button type="button" className="panel-open-btn" style={{ marginLeft: 'auto' }} onClick={() => setActiveView('deals')}>Open deals</button>
                  )}
                  <button
                    type="button"
                    className={`panel-expand-btn${expandedPanel === 'pipeline' ? ' is-expanded' : ''}`}
                    onClick={() => setExpandedPanel(expandedPanel === 'pipeline' ? null : 'pipeline')}
                    title={expandedPanel === 'pipeline' ? 'Collapse panel' : 'Expand panel'}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, color: '#7c98b6', display: 'flex', alignItems: 'center' }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      {expandedPanel === 'pipeline' ? (
                        <><path d="M4 14h6v6"/><path d="M20 10h-6V4"/><path d="M14 10l7-7"/><path d="M3 21l7-7"/></>
                      ) : (
                        <><path d="M15 3h6v6"/><path d="M9 21H3v-6"/><path d="M21 3l-7 7"/><path d="M3 21l7-7"/></>
                      )}
                    </svg>
                  </button>
                </div>
                <div className="panel-body">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Deal</th>
                        <th>Owner</th>
                        <th>Stage</th>
                        <th>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {liveDeals === null ? (
                        Array.from({ length: 6 }).map((_, idx) => (
                          <tr key={`pipeline-skeleton-${idx}`}>
                            <td><span className="har-skeleton table-skeleton-wide" /></td>
                            <td><span className="har-skeleton table-skeleton-sm" /></td>
                            <td><span className="har-skeleton table-skeleton-chip" /></td>
                            <td><span className="har-skeleton table-skeleton-sm" /></td>
                          </tr>
                        ))
                      ) : commandDeals.length === 0 ? (
                        <tr>
                          <td colSpan={4} style={{ padding: '32px 20px', textAlign: 'center', color: '#7c98b6' }}>
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block', margin: '0 auto 12px' }}>
                              <path d="M3 4.5h18l-7 8.5v6l-4 2v-8z" />
                            </svg>
                            <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#33475b', marginBottom: 4 }}>No deals to show</div>
                            <div style={{ fontSize: '0.78rem' }}>New deals will appear here as the sales pipeline grows.</div>
                          </td>
                        </tr>
                      ) : (
                        (() => {
                          const groups = homeStageSummary
                            .map((stage) => ({ stage, deals: commandDeals.filter((deal) => deal.stage === stage.stageId) }))
                            .filter((group) => group.deals.length > 0)
                          return groups.map(({ stage, deals }) => (
                            <React.Fragment key={stage.stageId}>
                              <tr className="pipeline-stage-group">
                                <td colSpan={4} style={{ padding: '8px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
                                    <strong style={{ color: '#33475b', fontSize: '0.76rem' }}>{stage.label}</strong>
                                    <span style={{ color: '#7c98b6', fontSize: '0.72rem' }}>{stage.count} deals · {currencyFormatter.format(stage.value)}</span>
                                  </div>
                                </td>
                              </tr>
                              {(expandedPanel === 'pipeline' ? deals : deals.slice(0, 20)).map((deal) => (
                                <tr
                                  key={deal.id}
                                  className="clickable-row"
                                  onClick={() => {
                                    setActiveView('deals')
                                    setDealDetailOpen(true)
                                    setDealDetailTab('about')
                                    setSelectedDeal(deal)
                                    window.location.hash = `/deals/record/${deal.id}`
                                  }}
                                >
                                  <td data-label="Deal"><div className="primary-cell"><strong>{deal.title}</strong></div></td>
                                  <td data-label="Owner">{deal.assignedTo || 'Unassigned'}</td>
                                  <td data-label="Stage">
                                    <span className="status-chip" style={{ background: stage.color, color: '#fff', display: 'inline-flex', alignItems: 'center', opacity: 0.9 }}>{stage.label}</span>
                                  </td>
                                  <td data-label="Value">{Number(deal.opportunity) > 0 ? currencyFormatter.format(Number(deal.opportunity)) : '—'}</td>
                                </tr>
                              ))}
                            </React.Fragment>
                          ))
                        })()
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
              )}

              {/* Right rail: agenda, dynamic pipeline stages, delivery, and marketing. */}
              <div className="home-rail">
              {!isNavHidden('widget.schedule-panel') && (
              <section
                className={`panel panel-schedule${expandedPanel === 'schedule' ? ' is-expanded-overlay' : ''}`}
                style={{
                  display: expandedPanel && expandedPanel !== 'schedule' ? 'none' : undefined,
                }}
              >
                <div className="panel-head">
                  {expandedPanel === 'schedule' && (
                    <button type="button" className="panel-back-btn" onClick={() => setExpandedPanel(null)} title="Back to dashboard" aria-label="Back to dashboard">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
                    </button>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div>
                      <h2>Schedule</h2>
                      <p>{scheduleTab === 'today' ? longDateFormatter.format(parseDateKey(todayKey)) : 'Next 7 days'}</p>
                    </div>
                    <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 8, padding: 2 }}>
                      {(['today', 'upcoming'] as const).map(tab => (
                        <button key={tab} onClick={() => setScheduleTab(tab)}
                          style={{ padding: '4px 12px', borderRadius: 6, fontSize: '0.74rem', fontWeight: 600, border: 'none', cursor: 'pointer', background: scheduleTab === tab ? '#fff' : 'transparent', color: scheduleTab === tab ? '#1e293b' : '#64748b', boxShadow: scheduleTab === tab ? '0 1px 2px rgba(0,0,0,0.08)' : 'none', transition: 'all 0.15s' }}>
                          {tab === 'today' ? 'Today' : 'Next 7 days'}
                        </button>
                      ))}
                    </div>
                  </div>
                  {expandedPanel === 'schedule' && (
                    <button type="button" className="panel-open-btn" style={{ marginLeft: 'auto' }} onClick={() => setActiveView('calendar')}>Open calendar</button>
                  )}
                  <button
                    type="button"
                    className={`panel-expand-btn${expandedPanel === 'schedule' ? ' is-expanded' : ''}`}
                    onClick={() => setExpandedPanel(expandedPanel === 'schedule' ? null : 'schedule')}
                    title={expandedPanel === 'schedule' ? 'Collapse panel' : 'Expand panel'}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, color: '#7c98b6', display: 'flex', alignItems: 'center' }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      {expandedPanel === 'schedule' ? (
                        <><path d="M4 14h6v6"/><path d="M20 10h-6V4"/><path d="M14 10l7-7"/><path d="M3 21l7-7"/></>
                      ) : (
                        <><path d="M15 3h6v6"/><path d="M9 21H3v-6"/><path d="M21 3l-7 7"/><path d="M3 21l7-7"/></>
                      )}
                    </svg>
                  </button>
                </div>
                <div className="panel-body">
                  {calendarDataLoading ? (
                    <div className="agenda-skeleton-list">
                      {Array.from({ length: 4 }).map((_, idx) => (
                        <div className="agenda-skeleton-item" key={`today-skeleton-${idx}`}>
                          <span className="har-skeleton agenda-skeleton-time" />
                          <div>
                            <span className="har-skeleton agenda-skeleton-title" />
                            <span className="har-skeleton agenda-skeleton-copy" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : scheduleTab === 'today' ? (
                    todayMeetings.length === 0 ? (
                      <div style={{ padding: '32px 20px', textAlign: 'center', color: '#7c98b6' }}>
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block', margin: '0 auto 12px' }}>
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                        <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#33475b', marginBottom: '4px' }}>No events scheduled</div>
                        <div style={{ fontSize: '0.78rem' }}>Your calendar is clear for today.</div>
                      </div>
                    ) : (
                      <ul className="agenda-list">
                        {todayMeetings.map(
                          (meeting) => (
                            <li key={meeting.id} className="agenda-item" onClick={() => setModalMeeting(meeting)}>
                              <div className="agenda-time">
                                <strong>{mtpFmtDisplay(meeting.time) || meeting.time}</strong>
                                <span>{meeting.duration}</span>
                              </div>
                              <div>
                                <p className="agenda-title">{meeting.title}</p>
                                <p className="agenda-copy">
                                  {meeting.account} · {meeting.owner}
                                </p>
                                {meeting.location && <p className="agenda-copy"><MapsLink parts={[meeting.location]} style={{ color: '#0091ae', textDecoration: 'underline' }} /></p>}
                              </div>
                            </li>
                          ),
                        )}
                      </ul>
                    )
                  ) : (() => {
                    const tomorrowDate = new Date(todayKey)
                    tomorrowDate.setDate(tomorrowDate.getDate() + 1)
                    const tomorrowKey = tomorrowDate.toISOString().slice(0, 10)
                    const allInRange = commandUpcomingMeetings(7)
                    const allWithToday = [...todayMeetings, ...allInRange]
                    const displayMeetings = expandedPanel === 'schedule' ? allWithToday : allWithToday.slice(0, 8)
                    const hasMore = expandedPanel !== 'schedule' && allWithToday.length > 8
                    if (allWithToday.length === 0) return (
                      <div style={{ padding: '32px 20px', textAlign: 'center', color: '#7c98b6' }}>
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block', margin: '0 auto 12px' }}>
                          <rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4" /><path d="M8 2v4" /><path d="M3 10h18" />
                        </svg>
                        <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#33475b', marginBottom: 4 }}>No upcoming events</div>
                        <div style={{ fontSize: '0.78rem' }}>No events in the next 7 days.</div>
                      </div>
                    )
                    return (
                      <>
                        <ul className="agenda-list">
                          {displayMeetings.map((m) => {
                            const dateLabel = m.date === todayKey ? 'Today' : m.date === tomorrowKey ? 'Tomorrow' : new Date(m.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
                            return (
                              <li key={m.id} className="agenda-item" onClick={() => setModalMeeting(m)}>
                                <div className="agenda-time">
                                  <strong>{mtpFmtDisplay(m.time) || m.time}</strong>
                                  <span>{dateLabel}</span>
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <p className="agenda-title">{m.title}</p>
                                  <p className="agenda-copy">{m.account} · {m.owner}</p>
                                  {m.location && <p className="agenda-copy"><MapsLink parts={[m.location]} style={{ color: '#0091ae', fontSize: '0.78rem' }} /></p>}
                                </div>
                              </li>
                            )
                          })}
                        </ul>
                        {hasMore && (
                          <div style={{ padding: '8px 16px', textAlign: 'center', fontSize: '0.78rem', color: '#2563eb', cursor: 'pointer', fontWeight: 600 }}
                            onClick={() => setExpandedPanel('schedule')}>
                            View {allWithToday.length - 8} more
                          </div>
                        )}
                      </>
                    )
                  })()}
                </div>
              </section>
              )}

              {!isNavHidden('widget.stage-panel') && (
              <section
                className={`panel${expandedPanel === 'stages' ? ' is-expanded-overlay' : ''}`}
                style={{
                  display: expandedPanel && expandedPanel !== 'stages' ? 'none' : undefined,
                }}
              >
                <div className="panel-head">
                  {expandedPanel === 'stages' && (
                    <button type="button" className="panel-back-btn" onClick={() => setExpandedPanel(null)} title="Back to dashboard" aria-label="Back to dashboard">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
                    </button>
                  )}
                  <div>
                    <h2>Pipeline by stage</h2>
                    <p>{livePipeline ? 'Live pipeline data' : 'Weighted by account value'}</p>
                  </div>
                  {expandedPanel === 'stages' && (
                    <button type="button" className="panel-open-btn" style={{ marginLeft: 'auto' }} onClick={() => setActiveView('deals')}>Open deals</button>
                  )}
                  <button
                    type="button"
                    className={`panel-expand-btn${expandedPanel === 'stages' ? ' is-expanded' : ''}`}
                    onClick={() => setExpandedPanel(expandedPanel === 'stages' ? null : 'stages')}
                    title={expandedPanel === 'stages' ? 'Collapse panel' : 'Expand panel'}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, color: '#7c98b6', display: 'flex', alignItems: 'center' }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      {expandedPanel === 'stages' ? (
                        <><path d="M4 14h6v6"/><path d="M20 10h-6V4"/><path d="M14 10l7-7"/><path d="M3 21l7-7"/></>
                      ) : (
                        <><path d="M15 3h6v6"/><path d="M9 21H3v-6"/><path d="M21 3l-7 7"/><path d="M3 21l7-7"/></>
                      )}
                    </svg>
                  </button>
                </div>
                <div className="panel-body">
                  <ul className="stage-list">
                    {homeStageSummary.filter((entry) => entry.count > 0).length === 0 ? (
                      <li style={{ padding: '24px 16px', textAlign: 'center', color: '#7c98b6' }}>
                        <strong style={{ display: 'block', color: '#33475b', marginBottom: 4 }}>No deal stages yet</strong>
                        <span>Stage totals will appear as sales activity is added.</span>
                      </li>
                    ) : homeStageSummary.filter((entry) => entry.count > 0).map((entry) => (
                      <li key={entry.stageId} className="stage-row">
                        <div>
                          <strong>{entry.label}</strong>
                          <span>{entry.count} deals</span>
                        </div>
                        <strong>{currencyFormatter.format(entry.value)}</strong>
                      </li>
                    ))}
                  </ul>
                </div>
              </section>
              )}

              {!isNavHidden('widget.delivery-panel') && (
              <section
                className={`panel${expandedPanel === 'delivery' ? ' is-expanded-overlay' : ''}`}
                style={{
                  display: expandedPanel && expandedPanel !== 'delivery' ? 'none' : undefined,
                }}
              >
                <div className="panel-head">
                  {expandedPanel === 'delivery' && (
                    <button type="button" className="panel-back-btn" onClick={() => setExpandedPanel(null)} title="Back to dashboard" aria-label="Back to dashboard">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
                    </button>
                  )}
                  <div>
                    <h2>Delivery watch</h2>
                    <p>Active project health</p>
                  </div>
                  {expandedPanel === 'delivery' && (
                    <button type="button" className="panel-open-btn" style={{ marginLeft: 'auto' }} onClick={() => { setProjectsStageFilterSet(new Set(['P_IN_PROGRESS'])); setActiveView('projects'); window.location.hash = '#/projects' }}>Open projects</button>
                  )}
                  <button
                    type="button"
                    className={`panel-expand-btn${expandedPanel === 'delivery' ? ' is-expanded' : ''}`}
                    onClick={() => setExpandedPanel(expandedPanel === 'delivery' ? null : 'delivery')}
                    title={expandedPanel === 'delivery' ? 'Collapse panel' : 'Expand panel'}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, color: '#7c98b6', display: 'flex', alignItems: 'center' }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      {expandedPanel === 'delivery' ? (
                        <><path d="M4 14h6v6"/><path d="M20 10h-6V4"/><path d="M14 10l7-7"/><path d="M3 21l7-7"/></>
                      ) : (
                        <><path d="M15 3h6v6"/><path d="M9 21H3v-6"/><path d="M21 3l-7 7"/><path d="M3 21l7-7"/></>
                      )}
                    </svg>
                  </button>
                </div>
                <div className="panel-body">
                  {(() => {
                    const list = deliveryProjects || []
                    if (deliveryLoading && deliveryProjects === null) {
                      return (
                        <div className="agenda-skeleton-list">
                          {Array.from({ length: 5 }).map((_, idx) => (
                            <div className="delivery-skeleton-item" key={`delivery-skeleton-${idx}`}>
                              <span className="har-skeleton agenda-skeleton-title" />
                              <span className="har-skeleton agenda-skeleton-copy" />
                              <span className="har-skeleton delivery-skeleton-bar" />
                            </div>
                          ))}
                        </div>
                      )
                    }
                    if (list.length === 0) {
                      return (
                        <div style={{ padding: '32px 20px', textAlign: 'center', color: '#7c98b6' }}>
                          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block', margin: '0 auto 12px' }}>
                            <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18" /><path d="M9 21V9" />
                          </svg>
                          <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#33475b', marginBottom: '4px' }}>No active projects</div>
                          <div style={{ fontSize: '0.78rem' }}>Active delivery projects will appear here once tracked.</div>
                        </div>
                      )
                    }
                    const now = Date.now()
                    const shown = expandedPanel === 'delivery' ? list : list.slice(0, 8)
                    return (
                      <>
                        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                          {shown.map((p: any) => {
                            const startTs = p.start_date ? Date.parse(p.start_date) : NaN
                            const daysAgo = Number.isFinite(startTs) ? Math.max(0, Math.floor((now - startTs) / 86400000)) : null
                            const startedLabel = daysAgo === null ? '' : daysAgo === 0 ? 'Started today' : `Started ${daysAgo}d ago`
                            const progressRaw = typeof p.progress === 'number' ? p.progress : (p.progress != null ? Number(p.progress) : NaN)
                            const hasProgress = Number.isFinite(progressRaw)
                            const pct = hasProgress ? Math.max(0, Math.min(100, progressRaw)) : 0
                            const addr = p.address || ''
                            const risk = daysAgo !== null && daysAgo >= 14 && pct < 80 ? 'high' : daysAgo !== null && daysAgo >= 7 && pct < 50 ? 'watch' : 'normal'
                            const riskLabel = risk === 'high' ? 'At risk' : risk === 'watch' ? 'Watch' : 'On track'
                            return (
                              <li key={p.id} style={{ padding: '10px 16px', borderBottom: '1px solid #f1f5f9' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                                  <a href={`#/projects/record/${p.id}`}
                                    style={{ fontWeight: 600, fontSize: '0.86rem', color: '#2563eb', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}
                                    title={p.name || 'Untitled project'}>
                                    {p.name || 'Untitled project'}
                                  </a>
                                  {startedLabel && (
                                    <span style={{ fontSize: '0.72rem', color: '#7c98b6', whiteSpace: 'nowrap' }}>{startedLabel}</span>
                                  )}
                                </div>
                                {addr && (
                                  <p style={{ margin: '2px 0 0', fontSize: '0.76rem', color: '#7c98b6', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={addr}>{addr}</p>
                                )}
                                {p.owner_name && (
                                  <p style={{ margin: '2px 0 0', fontSize: '0.76rem', color: '#475569' }}>{p.owner_name}</p>
                                )}
                                <div className="delivery-risk-row">
                                  <span className={`delivery-risk-badge risk-${risk}`}>{riskLabel}</span>
                                  {hasProgress && <span>{pct}% complete</span>}
                                </div>
                                {hasProgress && (
                                  <div style={{ marginTop: 6, height: 4, background: '#e2e8f0', borderRadius: 2, overflow: 'hidden' }}>
                                    <div style={{ width: `${pct}%`, height: '100%', background: risk === 'high' ? '#dc2626' : risk === 'watch' ? '#d97706' : '#10b981' }} />
                                  </div>
                                )}
                              </li>
                            )
                          })}
                        </ul>
                        <div
                          style={{ padding: '8px 16px', textAlign: 'center', fontSize: '0.78rem', color: '#2563eb', cursor: 'pointer', fontWeight: 600 }}
                          onClick={() => {
                            setProjectsStageFilterSet(new Set(['P_IN_PROGRESS']))
                            setActiveView('projects')
                            window.location.hash = '#/projects'
                          }}>
                          View all
                        </div>
                      </>
                    )
                  })()}
                </div>
              </section>
              )}
              <HomeMarketingPulse
                items={marketingPulse}
                loading={marketingPulseLoading}
                error={marketingPulseError}
                onOpen={() => { setActiveView('marketing'); window.location.hash = '/marketing' }}
              />
              </div>

              {/* My Documents — caller's pending HR assignments. Full-width row
                  below the grid; hidden when there's nothing pending so
                  non-employees don't see an empty card. */}
              {(() => {
                const list = myDocsAssignments || []
                const pending = list.filter((a: any) => a.status !== 'signed')
                if (pending.length === 0) return null
                const now = Date.now()
                const overdue = pending.filter((a: any) => a.due_date && Date.parse(a.due_date) < now)
                const shown = pending.slice(0, 5)
                const fmt = (s?: string | null) => {
                  if (!s) return ''
                  const d = new Date(s); return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                }
                return (
                  <section className="panel home-docs-fullrow">
                    <div className="panel-head">
                      <div>
                        <h2>My documents</h2>
                        <p>{pending.length} pending{overdue.length > 0 ? ` · ${overdue.length} overdue` : ''}</p>
                      </div>
                      <button
                        type="button"
                        className="panel-expand-btn"
                        onClick={() => setActiveView('my-documents')}
                        title="Open My Documents"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, color: '#7c98b6', display: 'flex', alignItems: 'center' }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 17L17 7"/><path d="M7 7h10v10"/></svg>
                      </button>
                    </div>
                    <div className="panel-body">
                      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                        {shown.map((a: any) => {
                          const isOverdue = a.due_date && Date.parse(a.due_date) < now
                          return (
                            <li key={a.id} style={{ padding: '10px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <div style={{ fontWeight: 600, fontSize: '0.86rem', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={a.template_title || 'Untitled document'}>{a.template_title || 'Untitled document'}</div>
                                <div style={{ fontSize: '0.74rem', color: isOverdue ? '#b91c1c' : '#7c98b6', marginTop: 2 }}>
                                  {a.due_date ? `${isOverdue ? 'Overdue' : 'Due'} ${fmt(a.due_date)}` : 'No due date'}
                                  {a.category ? ` · ${a.category}` : ''}
                                </div>
                              </div>
                              <button
                                onClick={() => openHrSignModal(String(a.id), undefined, 'employee')}
                                style={{ fontSize: '0.76rem', padding: '5px 11px', border: 'none', background: '#0091ae', color: '#fff', borderRadius: 6, cursor: 'pointer', fontWeight: 600, flexShrink: 0 }}
                              >Sign</button>
                            </li>
                          )
                        })}
                      </ul>
                      {pending.length > shown.length && (
                        <div onClick={() => setActiveView('my-documents')} style={{ padding: '8px 16px', textAlign: 'center', fontSize: '0.78rem', color: '#2563eb', cursor: 'pointer', fontWeight: 600 }}>View all {pending.length}</div>
                      )}
                    </div>
                  </section>
                )
              })()}
            </div>

            </div>
          )}

          {activeView === 'calendar' && (
            <Suspense fallback={<div className="view-loading" />}>
              <Calendar
                calendarFilterMenu={calendarFilterMenu}
                setCalendarFilterMenu={setCalendarFilterMenu}
                enabledCalendars={enabledCalendars}
                toggleCalendar={toggleCalendar}
                enabledEmployees={enabledEmployees}
                toggleEmployee={toggleEmployee}
                allEmployeeNames={allEmployeeNames}
                calendarMonth={calendarMonth}
                goToPrevMonth={goToPrevMonth}
                goToNextMonth={goToNextMonth}
                goToToday={goToToday}
                canCreateCalendarEvent={canCreateCalendarEvent}
                openCreateCalendarEvent={openCreateCalendarEvent}
                isCalendarApprover={isCalendarApprover}
                isSuperAdmin={isSuperAdmin}
                approvalsBadgeCount={approvalsBadgeCount}
                onRequestTimeOff={() => { setToForm({ start_date: selectedDate || todayKey, end_date: selectedDate || todayKey, segment: 'full', start_time: '09:00', end_time: '12:00', reason: '' }); setToError(null); setTimeOffModalOpen(true) }}
                onOpenApprovals={() => setApprovalsOpen(true)}
                onAddHoliday={() => { setHolForm({ name: '', start_date: selectedDate || todayKey, end_date: selectedDate || todayKey, event_type: 'holiday', is_all_day: true, start_time: '09:00', end_time: '12:00', recurrence_freq: 'none', recurrence_end_date: '' }); setHolError(null); setHolidayModalOpen(true) }}
                calendarDataLoading={calendarDataLoading}
                liveMeetings={liveMeetings}
                meetings={meetings}
                calendarDays={calendarDays}
                isEventVisible={isEventVisible}
                todayKey={todayKey}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                popupDay={popupDay}
                setPopupDay={setPopupDay}
                popupPosition={popupPosition}
                setPopupPosition={setPopupPosition}
                popupRef={popupRef}
                popupDateLabel={popupDateLabel}
                popupMeetings={popupMeetings}
                openPopup={openPopup}
                modalMeeting={modalMeeting}
                setModalMeeting={setModalMeeting}
                modalMeetingRef={modalMeetingRef}
                reelMode={reelMode}
                tasksDueExpanded={tasksDueExpanded}
                setTasksDueExpanded={setTasksDueExpanded}
                timelineRef={timelineRef}
                nowLineTop={nowLineTop}
                selectedMeetings={selectedMeetings}
                selectedMilestones={selectedMilestones}
                formatDatePref={formatDatePref}
              />
            </Suspense>
          )}



          {/* Leads view is now handled in the contacts/companies HubSpot-style block below */}

          {/* ========== Projects Workspace ========== */}
          {activeView === 'projects' && projectWorkspaceId && (
            <Suspense fallback={PrefsLazyFallback}>
              <ProjectWorkspace
                projectId={projectWorkspaceId}
                onBack={() => {
                  const projectId = projectWorkspaceId
                  setProjectWorkspaceId(null)
                  setProjectDetailOpen(true)
                  window.location.hash = `/projects/record/${encodeURIComponent(projectId)}`
                }}
              />
            </Suspense>
          )}

          {/* ========== Projects View (HubSpot Contacts-style) ========== */}
          {/* Projects list region marker: {activeView === 'projects' && !projectDetailOpen && ( */}
          {activeView === 'projects' && !projectDetailOpen && !projectWorkspaceId && (
            <div className="hs-contacts">
              {/* Top bar: object switcher + view tabs + actions */}
              <div className="hs-topbar">
                <div className="hs-topbar-left">
                  {/* Object switcher */}
                  <div className="hs-obj-switcher-wrap" ref={objSwitcherRef}>
                    <button className={`hs-obj-switcher${objSwitcherOpen ? ' open' : ''}`} onClick={() => { setObjSwitcherOpen(!objSwitcherOpen); setObjSwitcherSearch('') }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
                      <span>Projects</span>
                      <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                    </button>
                    {objSwitcherOpen && (
                      <div className="hs-obj-panel">
                        <div className="hs-obj-search-wrap">
                          <svg className="hs-obj-search-icon" width="13" height="13" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                          <input
                            className="hs-obj-search"
                            placeholder="Search"
                            value={objSwitcherSearch}
                            onChange={(e) => setObjSwitcherSearch(e.target.value)}
                            autoFocus
                          />
                        </div>
                        <ul className="hs-obj-list" role="listbox">
                          {[
                            { label: 'Contacts', view: 'contacts' as ViewMode },
                            { label: 'Clients', view: 'companies' as ViewMode },
                            { label: 'Deals', view: 'deals' as ViewMode },
                            { label: 'Leads', view: 'leads' as ViewMode },
                            { label: 'Projects', view: 'projects' as ViewMode },
                            { label: 'Tickets', view: 'tickets' as ViewMode },
                            { label: 'Calls', view: 'calls' as ViewMode },
                          ]
                            .filter(item => !objSwitcherSearch || item.label.toLowerCase().includes(objSwitcherSearch.toLowerCase()))
                            .map(item => (
                              <li
                                key={item.view}
                                className={`hs-obj-item${item.view === activeView ? ' active' : ''}`}
                                role="option"
                                aria-selected={item.view === activeView}
                                onClick={() => {
                                  setActiveView(item.view)
                                  setObjSwitcherOpen(false)
                                  setObjSwitcherSearch('')
                                }}
                              >
                                {item.label}
                              </li>
                            ))
                          }
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Separator */}
                  <div className="hs-topbar-sep" />

                  {/* View tabs — lifecycle status filters. On Hold is a
                      pipeline-stage filter, not a portal_projects status. */}
                  <div className="hs-view-tabs">
                    {[
                      { key: '', label: 'All projects' },
                      { key: 'active', label: 'Active' },
                      { key: 'pending', label: 'Proposal' },
                      { key: 'on_hold', label: 'On Hold' },
                      { key: 'completed', label: 'Completed' },
                      { key: 'cancelled', label: 'Cancelled' },
                    ].filter(tab => {
                      if (tab.key === '') return true
                      const saved = savedViewTabs['projects'] || ['active', 'pending', 'on_hold', 'completed', 'cancelled']
                      return saved.includes(tab.key)
                    }).map(tab => {
                      const isActive = projectsStatusFilter === tab.key
                      return (
                        <div key={tab.key} className={`hs-view-tab${isActive ? ' active' : ''}`} ref={isActive ? tabEditRef : undefined} onClick={() => setProjectViewFilter(tab.key)}>
                          <span className="hs-tab-label">{tab.label}</span>
                          {isActive && projectsTotal > 0 && <span className="hs-tab-count">{projectsTotal.toLocaleString()}</span>}
                          {isActive && (
                            <span className="hs-tab-kebab" onClick={(e) => { e.stopPropagation(); setTabEditOpen(!tabEditOpen) }}>
                              <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>
                            </span>
                          )}
                          {tab.key !== '' && <span className="hs-tab-x" onClick={(e) => { e.stopPropagation(); handleDeleteViewTab(tab.key) }}>&times;</span>}
                          {isActive && tabEditOpen && (
                            <div className="hs-tab-edit-dropdown">
                              <button className="hs-tab-edit-item" disabled>Rename</button>
                              <button className="hs-tab-edit-item">Clone</button>
                              <button className="hs-tab-edit-item" disabled>Manage Sharing</button>
                              {tab.key !== '' && <button className="hs-tab-edit-item" onClick={() => handleDeleteViewTab(tab.key)}>Delete</button>}
                            </div>
                          )}
                        </div>
                      )
                    })}
                    {/* Custom user-created view tabs */}
                    {(customViews['projects'] || []).map(cv => {
                      const isActive = projectsStatusFilter === `custom:${cv.id}`
                      const isRenaming = renamingViewId === cv.id
                      return (
                        <div
                          key={cv.id}
                          className={`hs-view-tab${isActive ? ' active' : ''}`}
                          onClick={() => {
                            if (isRenaming) return
                            setProjectsStatusFilter(`custom:${cv.id}`)
                            setProjectsAssignedFilter(cv.filters?.assigned ?? '')
                            setProjectsStatusSet(new Set(cv.filters?.statuses ?? []))
                          }}
                        >
                          {isRenaming ? (
                            <input
                              ref={renamingViewRef}
                              className="hs-tab-rename-input"
                              value={renamingViewName}
                              onChange={(e) => setRenamingViewName(e.target.value)}
                              onFocus={(e) => e.target.select()}
                              onBlur={() => {
                                if (renamingViewName.trim()) {
                                  if (cv.serverId) updateSavedView(cv.serverId, { name: renamingViewName.trim() }).catch(() => {})
                                  setCustomViews(prev => ({
                                    ...prev,
                                    projects: (prev.projects || []).map(v => v.id === cv.id ? { ...v, label: renamingViewName.trim() } : v)
                                  }))
                                }
                                setRenamingViewId(null)
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
                                if (e.key === 'Escape') { setRenamingViewName(cv.label); setRenamingViewId(null) }
                              }}
                              onClick={(e) => e.stopPropagation()}
                              autoFocus
                            />
                          ) : (
                            <span className="hs-tab-label">{cv.label}</span>
                          )}
                          {isActive && !isRenaming && projectsTotal > 0 && <span className="hs-tab-count">{projectsTotal.toLocaleString()}</span>}
                          {isActive && !isRenaming && (
                            <span className="hs-tab-kebab" onClick={(e) => { e.stopPropagation(); setTabEditOpen(!tabEditOpen) }}>
                              <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>
                            </span>
                          )}
                          {isActive && tabEditOpen && !isRenaming && (
                            <div className="hs-tab-edit-dropdown">
                              <button className="hs-tab-edit-item" onClick={() => { setRenamingViewId(cv.id); setRenamingViewName(cv.label); setTabEditOpen(false) }}>Rename</button>
                              <button className="hs-tab-edit-item" onClick={() => {
                                const id = Date.now().toString(36)
                                setCustomViews(prev => ({
                                  ...prev,
                                  projects: [...(prev.projects || []), { id, label: `${cv.label} (clone)` }]
                                }))
                                setProjectsStatusFilter(`custom:${id}`)
                                setRenamingViewId(id)
                                setRenamingViewName(`${cv.label} (clone)`)
                                setTabEditOpen(false)
                              }}>Clone</button>
                              <button className="hs-tab-edit-item" onClick={() => {
                                if (cv.serverId) deleteSavedView(cv.serverId).catch(() => {})
                                setCustomViews(prev => ({
                                  ...prev,
                                  projects: (prev.projects || []).filter(v => v.id !== cv.id)
                                }))
                                setProjectsStatusFilter('')
                                setTabEditOpen(false)
                              }}>Delete</button>
                            </div>
                          )}
                          <span className="hs-tab-x" onClick={(e) => {
                            e.stopPropagation()
                            if (cv.serverId) deleteSavedView(cv.serverId).catch(() => {})
                            setCustomViews(prev => ({
                              ...prev,
                              projects: (prev.projects || []).filter(v => v.id !== cv.id)
                            }))
                            if (isActive) {
                              setProjectsStatusFilter('')
                            }
                          }}>&times;</span>
                        </div>
                      )
                    })}

                    {/* Quick-add view button — inline with tabs */}
                    <div className="hs-topbar-sep" />
                    <div className="hs-view-add-wrap" ref={viewAddMenuRef} style={{ position: 'relative', display: 'inline-flex', alignSelf: 'center' }}>
                      <button
                        className={`hs-quick-add-btn${viewAddMenuOpen ? ' active' : ''}`}
                        title="Create or add view"
                        type="button"
                        aria-label="Create or add view"
                        onClick={() => { setViewAddMenuOpen(!viewAddMenuOpen); setViewAddMenuMode('menu') }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                      </button>
                      {viewAddMenuOpen && viewAddMenuMode === 'menu' && (
                        <div className="hs-view-add-dropdown">
                          <button className="hs-view-add-item" onClick={() => setViewAddMenuMode('type')}>
                            Create new view
                          </button>
                          <button className="hs-view-add-item" onClick={() => {
                            setViewAddMenuOpen(false)
                            setViewAddMenuMode('menu')
                          }}>
                            Add view
                          </button>
                        </div>
                      )}
                      {viewAddMenuOpen && viewAddMenuMode === 'type' && (
                        <div className="hs-view-add-dropdown hs-view-type-picker">
                          <button className="hs-view-type-btn" onClick={() => {
                            setViewAddMenuOpen(false)
                            setViewAddMenuMode('menu')
                          }}>
                            <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                            <span>Table</span>
                          </button>
                          <button className="hs-view-type-btn" disabled style={{ opacity: 0.4 }}>
                            <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M11.43 11.43h-8a1.14 1.14 0 1 1 0-2.28h8a1.14 1.14 0 1 1 0 2.28M28.57 11.43h-8a1.14 1.14 0 1 1 0-2.28h8a1.14 1.14 0 1 1 0 2.28"/><path d="M9.14 29.71H5.71c-1.89 0-3.43-1.54-3.43-3.43V5.71c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v14.86c0 1.89-1.54 3.43-3.43 3.43h-3.43c-1.89 0-3.43-1.54-3.43-3.43v-1.14h-6.86v6.86c0 1.89-1.54 3.43-3.43 3.43Z"/></svg>
                            <span>Board</span>
                          </button>
                          <button className="hs-view-type-btn" disabled style={{ opacity: 0.4 }}>
                            <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M7.57 24.71c-.63 0-1.14-.51-1.14-1.14v-6.86a1.14 1.14 0 1 1 2.28 0v6.86c0 .63-.51 1.14-1.14 1.14M12.14 24.71c-.63 0-1.14-.51-1.14-1.14v-16c0-.63.51-1.14 1.14-1.14h4.57a1.14 1.14 0 1 1 0 2.28h-3.43v14.86c0 .63-.51 1.14-1.14 1.14"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v20.57c0 1.89-1.54 3.43-3.43 3.43Z"/></svg>
                            <span>Report</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Standalone Create-Project entry point removed:
                    projects are created from deals via the Contract Signed flow. */}
                <div className="hs-topbar-right" />
              </div>

              {/* Toolbar — HubSpot-style */}
              <div className="hs-toolbar">
                <div className="hs-toolbar-left">
                  {/* Search */}
                  <div className="hs-search-wrap">
                    <svg className="hs-search-icon" width="14" height="14" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M28.14 29.29c-.29 0-.58-.11-.81-.33l-7.58-7.58a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l7.58 7.58a1.14 1.14 0 0 1-.81 1.95"/><path d="M13.29 24.71c-6.3 0-11.43-5.13-11.43-11.43S6.98 1.86 13.29 1.86s11.43 5.13 11.43 11.43-5.13 11.43-11.43 11.43Zm0-20.57c-5.04 0-9.14 4.1-9.14 9.14s4.1 9.14 9.14 9.14 9.14-4.1 9.14-9.14-4.1-9.14-9.14-9.14"/></svg>
                    <input className="hs-search-input" placeholder="Search" value={projectsSearch} onChange={(e) => { setProjectsSearch(e.target.value); setProjectsPage(1) }} />
                    {projectsSearch && <button className="hs-search-clear" onClick={() => { setProjectsSearch(''); setProjectsPage(1) }}>&times;</button>}
                  </div>

                  <div className="hs-tb-sep" />

                  {/* View type + settings button group */}
                  <div className="hs-viewtype-group">
                    <div className="hs-viewtype-wrap" ref={viewTypeDropdownRef}>
                      <button
                        className={`hs-tb-btn hs-viewtype-btn${viewTypeDropdownOpen ? ' active' : ''}`}
                        onClick={() => setViewTypeDropdownOpen(!viewTypeDropdownOpen)}
                      >
                        <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                        <span>Table</span>
                        <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                      </button>
                      {viewTypeDropdownOpen && (
                        <div className="hs-viewtype-panel">
                          <div className="hs-viewtype-header">View type</div>
                          <button className="hs-viewtype-option active" onClick={() => setViewTypeDropdownOpen(false)}>
                            <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                            <span>Table</span>
                          </button>
                        </div>
                      )}
                    </div>
                    <button
                      className={`hs-tb-btn hs-settings-btn${tableSettingsOpen ? ' active' : ''}`}
                      onClick={() => setTableSettingsOpen(!tableSettingsOpen)}
                      title={tableSettingsOpen ? 'Close settings' : 'Table settings'}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                    </button>
                  </div>

                  <div className="hs-tb-sep" />

                  {/* Edit columns */}
                  <button className="hs-tb-btn hs-tb-btn-bordered" title="Edit columns"
                    onClick={() => { setEditColumnsOpen(true) }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="18" rx="1"/><rect x="14" y="3" width="7" height="18" rx="1"/></svg>
                    <span>Edit columns</span>
                  </button>

                  <div className="hs-tb-sep" />

                  {/* Filters */}
                  <button className={`hs-tb-btn hs-tb-btn-bordered${quickFiltersOpen ? ' hs-tb-btn-pressed' : ''}`} title="Filters" onClick={() => setQuickFiltersOpen(!quickFiltersOpen)}>
                    <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M30.71 16.86H13.57a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28M6.71 16.86H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M10.14 20.29c-2.52 0-4.57-2.05-4.57-4.57s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M6.71 6.57H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M30.71 6.57H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M18.14 6.57H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 10C19.05 10 17 7.95 17 5.43S19.05.86 21.57.86s4.57 2.05 4.57 4.57S24.09 10 21.57 10m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M30.71 27.14H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M18.14 27.14H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 30.57C19.05 30.57 17 28.52 17 26s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29"/></svg>
                    <span>Filters</span>
                  </button>

                  <div className="hs-tb-sep" />

                  {/* Completed (green) + Cancelled (red) toggles — mirror leads'
                      Converted + Disqualified pills. Each filter is independent,
                      off by default so the list focuses on active work. */}
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.78rem', fontWeight: 500, color: projectsIncludeCompleted ? '#2fb644' : '#7c98b6', cursor: 'pointer', whiteSpace: 'nowrap', padding: '4px 10px', borderRadius: 6, border: `1px solid ${projectsIncludeCompleted ? '#2fb644' : '#bbdfc4'}`, background: projectsIncludeCompleted ? '#f0fdf4' : '#f7fdf9', transition: 'all 0.15s' }}>
                    <input type="checkbox" checked={projectsIncludeCompleted} onChange={e => { setProjectsIncludeCompleted(e.target.checked); setProjectsPage(1) }} style={{ margin: 0, accentColor: '#2fb644' }} />
                    Completed
                  </label>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.78rem', fontWeight: 500, color: projectsIncludeCancelled ? '#dc2626' : '#7c98b6', cursor: 'pointer', whiteSpace: 'nowrap', padding: '4px 10px', borderRadius: 6, border: `1px solid ${projectsIncludeCancelled ? '#dc2626' : '#f0b8b8'}`, background: projectsIncludeCancelled ? '#fef2f2' : '#fef8f8', transition: 'all 0.15s' }}>
                    <input type="checkbox" checked={projectsIncludeCancelled} onChange={e => { setProjectsIncludeCancelled(e.target.checked); setProjectsPage(1) }} style={{ margin: 0, accentColor: '#dc2626' }} />
                    Cancelled
                  </label>

                  <div className="hs-tb-sep" />

                  {/* Export */}
                  <button
                    className={`hs-tb-btn hs-tb-btn-bordered${projectExporting ? ' hs-tb-btn-pressed' : ''}`}
                    title="Export current view as CSV"
                    disabled={projectExporting}
                    onClick={async () => {
                      setProjectExporting(true)
                      try {
                        const data = await fetchCrmProjects({
                          page: 1, pageSize: 10000,
                          search: projectsSearch || undefined,
                          status: projectsStatusFilter || undefined,
                          sortField: projectsSort.field, sortOrder: projectsSort.order,
                        })
                        const headers = ['Name', 'Contact', 'Status', 'Progress', 'Start Date', 'End Date', 'Value', 'Assigned To', 'Address', 'Created']
                        const escCsv = (v: unknown) => { const s = String(v ?? ''); return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s }
                        const rows = data.data.map((p: any) => [
                          p.name, p.client_name, p.status, p.progress, p.start_date, p.end_date, p.value, p.assigned_to, p.address, p.created_at,
                        ].map(escCsv).join(','))
                        const csv = [headers.join(','), ...rows].join('\n')
                        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
                        const url = URL.createObjectURL(blob)
                        const a = document.createElement('a')
                        a.href = url
                        a.download = `projects-export-${new Date().toISOString().slice(0, 10)}.csv`
                        document.body.appendChild(a); a.click(); document.body.removeChild(a)
                        URL.revokeObjectURL(url)
                      } catch (err) {
                        console.error('Export failed:', err)
                        alert('Export failed. Please try again.')
                      } finally {
                        setProjectExporting(false)
                      }
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    <span>{projectExporting ? 'Exporting...' : 'Export'}</span>
                  </button>
                </div>

                <div className="hs-toolbar-right">
                  <div className="hs-tb-sep" />
                  {/* Clone */}
                  <button className="hs-tb-btn hs-tb-btn-bordered" title="Clone view" onClick={() => {
                    const currentCustom = (customViews['projects'] || []).find(v => `custom:${v.id}` === projectsStatusFilter)
                    const baseName = currentCustom ? currentCustom.label : 'All projects'
                    const id = Date.now().toString(36)
                    const cloneName = `${baseName} (clone)`
                    setCustomViews(prev => ({
                      ...prev,
                      projects: [...(prev.projects || []), { id, label: cloneName }]
                    }))
                    setProjectsStatusFilter(`custom:${id}`)
                    setRenamingViewId(id)
                    setRenamingViewName(cloneName)
                  }}>
                    <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M25.86 29.29H12.15c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-1.89 1.54-3.43 3.43-3.43h13.71c1.89 0 3.43 1.54 3.43 3.43v13.71c0 1.89-1.54 3.43-3.43 3.43M12.14 11c-.63 0-1.14.51-1.14 1.14v13.71c0 .63.51 1.14 1.14 1.14h13.71c.63 0 1.14-.51 1.14-1.14V12.14c0-.63-.51-1.14-1.14-1.14z"/><path d="M5.29 22.43c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43H19c1.89 0 3.43 1.54 3.43 3.43a1.14 1.14 0 1 1-2.28 0c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14V19c0 .63.51 1.14 1.14 1.14a1.14 1.14 0 1 1 0 2.28Z"/></svg>
                  </button>
                  {/* Save */}
                  {(() => {
                    const isCustom = projectsStatusFilter.startsWith('custom:')
                    const hasFilters = quickFiltersOpen && (projectsAssignedFilter || projectsStatusFilter)
                    const canSave = isCustom || hasFilters
                    return (
                      <button
                        className={`hs-tb-btn hs-tb-btn-bordered${!canSave ? ' disabled' : ''}`}
                        data-marker="savedviews-producer-v1"
                        disabled={!canSave}
                        title="Save view"
                        onClick={() => {
                          if (!canSave) return
                          if (isCustom) {
                            const entry = (customViews.projects || []).find(v => `custom:${v.id}` === projectsStatusFilter)
                            const filters = { assigned: projectsAssignedFilter || undefined, statuses: Array.from(projectsStatusSet) }
                            if (entry?.serverId) updateSavedView(entry.serverId, { filtersJson: filters }).catch(() => {})
                            else if (entry) createSavedView({ name: entry.label, objectType: 'projects', filtersJson: filters }).then(sv => setCustomViews(prev => ({ ...prev, projects: (prev.projects || []).map(v => v.id === entry.id ? { ...v, serverId: sv.id } : v) }))).catch(() => {})
                            setCustomViews(prev => ({
                              ...prev,
                              projects: (prev.projects || []).map(v => `custom:${v.id}` === projectsStatusFilter
                                ? { ...v, filters: { assigned: projectsAssignedFilter || undefined, statuses: Array.from(projectsStatusSet) } }
                                : v)
                            }))
                          } else {
                            const id = Date.now().toString(36)
                            const label = `Custom view ${(customViews['projects'] || []).length + 1}`
                            const filters = { assigned: projectsAssignedFilter || undefined, statuses: Array.from(projectsStatusSet) }
                            setCustomViews(prev => ({
                              ...prev,
                              projects: [...(prev.projects || []), { id, label, filters: { assigned: projectsAssignedFilter || undefined, statuses: Array.from(projectsStatusSet) } }]
                            }))
                            setProjectsStatusFilter(`custom:${id}`)
                            setRenamingViewId(id)
                            setRenamingViewName(label)
                            createSavedView({ name: label, objectType: 'projects', filtersJson: filters }).then(sv => setCustomViews(prev => ({ ...prev, projects: (prev.projects || []).map(v => v.id === id ? { ...v, serverId: sv.id } : v) }))).catch(() => {})
                          }
                          const btn = document.querySelector('.hs-tb-btn[title="Save view"]') as HTMLElement
                          if (btn) { btn.style.color = '#22c55e'; setTimeout(() => { btn.style.color = '' }, 800) }
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                      </button>
                    )
                  })()}
                </div>
              </div>

              {/* Quick filters bar */}
              {quickFiltersOpen && <div className="hs-quick-filters">
                {/* Status — multi-select checkbox dropdown (drives projectsStatusSet → statuses param) */}
                {(() => {
                  const STATUS_OPTIONS = [
                    { value: 'pending',   label: 'Proposal', color: '#e65100' },
                    { value: 'active',    label: 'Active',   color: '#1565c0' },
                    { value: 'completed', label: 'Completed', color: '#2e7d32' },
                    { value: 'cancelled', label: 'Cancelled', color: '#b91c1c' },
                  ]
                  const isOpen = projectsQfPillOpen === 'status'
                  const count = projectsStatusSet.size
                  const toggle = (v: string) => {
                    setProjectsStatusSet(prev => {
                      const next = new Set(prev)
                      next.has(v) ? next.delete(v) : next.add(v)
                      setProjectsIncludeCompleted(next.has('completed'))
                      setProjectsIncludeCancelled(next.has('cancelled'))
                      return next
                    })
                    setProjectsStatusFilter('') // multi-select takes over from the single-status view tabs
                    setProjectsStageFilterSet(null)
                    setProjectsPage(1)
                  }
                  const clear = () => { setProjectsStatusSet(new Set()); setProjectsPage(1) }
                  return (
                    <div className="qf-pill-wrap" ref={isOpen ? projectsQfMultiRef : undefined}>
                      <button
                        className={`hs-qf-btn hs-qf-trigger${isOpen ? ' open' : ''}${count > 0 ? ' has-value' : ''}`}
                        onClick={() => setProjectsQfPillOpen(isOpen ? null : 'status')}
                      >
                        <span>Status</span>
                        {count > 0 && <span className="hs-qf-count">{count}</span>}
                        <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                      </button>
                      {count > 0 && (
                        <button className="hs-qf-clear-btn" title="Clear Status filter" onClick={(e) => { e.stopPropagation(); clear() }}>
                          <svg width="10" height="10" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                        </button>
                      )}
                      {isOpen && (
                        <div className="qf-pill-dropdown" ref={(el) => {
                          if (!el) return
                          const wrap = el.parentElement; if (!wrap) return
                          const btn = wrap.querySelector('button'); if (!btn) return
                          const rect = btn.getBoundingClientRect()
                          const popW = Math.min(280, window.innerWidth - 32)
                          let left = rect.left
                          if (left + popW > window.innerWidth - 16) left = window.innerWidth - 16 - popW
                          if (left < 16) left = 16
                          el.style.position = 'fixed'
                          el.style.top = `${rect.bottom + 4}px`
                          el.style.left = `${left}px`
                          el.style.right = 'auto'
                        }}>
                          <div className="qf-pill-list">
                            {count > 0 && (
                              <button className="qf-pill-date-option" style={{ color: '#d94c53', borderBottom: '1px solid #eaf0f6', marginBottom: 4, paddingBottom: 8 }} onClick={clear}>
                                <span className="qf-pill-date-label">Clear filter</span>
                              </button>
                            )}
                            {STATUS_OPTIONS.map(opt => (
                              <label key={opt.value} className="hs-owner-item">
                                <input type="checkbox" checked={projectsStatusSet.has(opt.value)} onChange={() => toggle(opt.value)} />
                                <span className="hs-owner-checkbox" />
                                <span className="hs-owner-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                                  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: opt.color, flexShrink: 0 }} />
                                  {opt.label}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })()}

                {/* Pipeline Stage — multi-select checkbox dropdown (drives projectsStageFilterSet → pipelineStages param) */}
                {(() => {
                  const isOpen = projectsQfPillOpen === 'stage'
                  const sel = projectsStageFilterSet // null = no filter (all stages); Set = only those stages
                  const count = sel ? sel.size : 0
                  const toggle = (id: string) => {
                    setProjectsStageFilterSet(prev => {
                      const next = new Set(prev ?? [])
                      next.has(id) ? next.delete(id) : next.add(id)
                      return next.size === 0 ? null : next // empty → null (no filter), avoids the "no results" empty-set state
                    })
                    setProjectsPage(1)
                  }
                  const clear = () => { setProjectsStageFilterSet(null); setProjectsPage(1) }
                  return (
                    <div className="qf-pill-wrap" ref={isOpen ? projectsQfMultiRef : undefined}>
                      <button
                        className={`hs-qf-btn hs-qf-trigger${isOpen ? ' open' : ''}${count > 0 ? ' has-value' : ''}`}
                        onClick={() => setProjectsQfPillOpen(isOpen ? null : 'stage')}
                      >
                        <span>Pipeline Stage</span>
                        {count > 0 && <span className="hs-qf-count">{count}</span>}
                        <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                      </button>
                      {count > 0 && (
                        <button className="hs-qf-clear-btn" title="Clear Pipeline Stage filter" onClick={(e) => { e.stopPropagation(); clear() }}>
                          <svg width="10" height="10" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                        </button>
                      )}
                      {isOpen && (
                        <div className="qf-pill-dropdown" ref={(el) => {
                          if (!el) return
                          const wrap = el.parentElement; if (!wrap) return
                          const btn = wrap.querySelector('button'); if (!btn) return
                          const rect = btn.getBoundingClientRect()
                          const popW = Math.min(280, window.innerWidth - 32)
                          let left = rect.left
                          if (left + popW > window.innerWidth - 16) left = window.innerWidth - 16 - popW
                          if (left < 16) left = 16
                          el.style.position = 'fixed'
                          el.style.top = `${rect.bottom + 4}px`
                          el.style.left = `${left}px`
                          el.style.right = 'auto'
                        }}>
                          <div className="qf-pill-list">
                            {count > 0 && (
                              <button className="qf-pill-date-option" style={{ color: '#d94c53', borderBottom: '1px solid #eaf0f6', marginBottom: 4, paddingBottom: 8 }} onClick={clear}>
                                <span className="qf-pill-date-label">Clear filter</span>
                              </button>
                            )}
                            {ALL_DELIVERY_STAGES.map(s => (
                              <label key={s.id} className="hs-owner-item">
                                <input type="checkbox" checked={!!sel && sel.has(s.id)} onChange={() => toggle(s.id)} />
                                <span className="hs-owner-checkbox" />
                                <span className="hs-owner-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                                  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
                                  {s.label}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })()}

                {/* Assigned To — owner multi-select dropdown (matches Contacts) */}
                <div className="hs-owner-dropdown" ref={ownerDropdownRef}>
                  <button
                    className={`hs-qf-btn hs-qf-trigger${ownerDropdownOpen ? ' open' : ''}${ownerSelected.size > 0 ? ' has-value' : ''}`}
                    onClick={() => setOwnerDropdownOpen(!ownerDropdownOpen)}
                  >
                    <span>Assigned to</span>
                    {ownerSelected.size > 0 && <span className="hs-qf-count">{ownerSelected.size}</span>}
                    <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                  </button>
                  {ownerDropdownOpen && (
                    <div className="hs-owner-panel">
                      <div className="hs-owner-search-wrap">
                        <svg className="hs-owner-search-icon" width="13" height="13" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                        <input
                          className="hs-owner-search"
                          placeholder="Search"
                          value={ownerSearch}
                          onChange={(e) => setOwnerSearch(e.target.value)}
                          autoFocus
                        />
                      </div>
                      <div className="hs-owner-list">
                        {/* "Me" shortcut */}
                        {(!ownerSearch || 'me'.includes(ownerSearch.toLowerCase()) || 'max ritchie'.includes(ownerSearch.toLowerCase())) && (
                          <label className="hs-owner-item hs-owner-me">
                            <input
                              type="checkbox"
                              checked={ownerSelected.has(8)}
                              onChange={() => {
                                setOwnerSelected(prev => {
                                  const next = new Set(prev)
                                  next.has(8) ? next.delete(8) : next.add(8)
                                  return next
                                })
                              }}
                            />
                            <span className="hs-owner-checkbox" />
                            <span className="hs-owner-label">Me</span>
                          </label>
                        )}
                        {/* All users */}
                        {crmUsers
                          .filter(u => !ownerSearch || u.name.toLowerCase().includes(ownerSearch.toLowerCase()))
                          .map(user => (
                            <label className="hs-owner-item" key={user.id}>
                              <input
                                type="checkbox"
                                checked={ownerSelected.has(user.id)}
                                onChange={() => {
                                  setOwnerSelected(prev => {
                                    const next = new Set(prev)
                                    next.has(user.id) ? next.delete(user.id) : next.add(user.id)
                                    return next
                                  })
                                }}
                              />
                              <span className="hs-owner-checkbox" />
                              <span className="hs-owner-avatar">{user.initials || user.name.charAt(0)}</span>
                              <span className="hs-owner-label">{user.name}</span>
                            </label>
                          ))
                        }
                      </div>
                      {/* Unassigned footer */}
                      {(!ownerSearch || 'unassigned'.includes(ownerSearch.toLowerCase())) && (
                        <div className="hs-owner-footer">
                          <label className="hs-owner-item">
                            <input
                              type="checkbox"
                              checked={ownerSelected.has(0)}
                              onChange={() => {
                                setOwnerSelected(prev => {
                                  const next = new Set(prev)
                                  next.has(0) ? next.delete(0) : next.add(0)
                                  return next
                                })
                              }}
                            />
                            <span className="hs-owner-checkbox" />
                            <span className="hs-owner-label">Unassigned</span>
                          </label>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Rich pill filters (parity with deals view) — Contract Value,
                    Start Date, End Date, City, Zip. Multi/single dropdowns +
                    text inputs render off a small declarative spec. */}
                {(() => {
                  type ProjPill =
                    | { key: 'value' | 'start' | 'end'; label: string; kind: 'single'; options: Array<{ value: string; label: string }> }
                    | { key: 'city' | 'zip'; label: string; kind: 'text'; placeholder: string }
                  const valueOptions = [
                    { value: 'lt5k',     label: 'Under $5k' },
                    { value: '5k-25k',   label: '$5k – $25k' },
                    { value: '25k-100k', label: '$25k – $100k' },
                    { value: 'gt100k',   label: 'Over $100k' },
                  ]
                  const dateOptions = [
                    { value: 'past',         label: 'Past due' },
                    { value: 'today',        label: 'Today' },
                    { value: 'this-week',    label: 'Next 7 days' },
                    { value: 'this-month',   label: 'Next 30 days' },
                    { value: 'this-quarter', label: 'Next 90 days' },
                    { value: 'unset',        label: 'Not set' },
                  ]
                  const pills: ProjPill[] = [
                    { key: 'value', label: 'Contract value', kind: 'single', options: valueOptions },
                    { key: 'start', label: 'Start date',     kind: 'single', options: dateOptions },
                    { key: 'end',   label: 'End date',       kind: 'single', options: dateOptions },
                    { key: 'city',  label: 'City',           kind: 'text',   placeholder: 'Filter by city' },
                    { key: 'zip',   label: 'Zip code',       kind: 'text',   placeholder: 'Filter by zip' },
                  ]
                  return pills.map((p) => {
                    const isOpen = projectsQfPillOpen === p.key
                    const currentValue =
                      p.key === 'value' ? projectsValueFilter :
                      p.key === 'start' ? projectsStartDateFilter :
                      p.key === 'end'   ? projectsEndDateFilter :
                      p.key === 'city'  ? projectsCityFilter :
                                          projectsZipFilter
                    const hasValue = !!currentValue
                    const labelOf = (v: string) => (p.kind === 'single' ? p.options.find(o => o.value === v)?.label || v : v)
                    const setVal = (v: string) => {
                      if (p.key === 'value') setProjectsValueFilter(v)
                      else if (p.key === 'start') setProjectsStartDateFilter(v)
                      else if (p.key === 'end') setProjectsEndDateFilter(v)
                      else if (p.key === 'city') setProjectsCityFilter(v)
                      else setProjectsZipFilter(v)
                      setProjectsPage(1)
                    }
                    const clear = () => { setVal(''); setProjectsQfPillOpen(null) }
                    return (
                      <div key={p.key} className="qf-pill-wrap">
                        <button
                          className={`hs-qf-btn hs-qf-trigger${isOpen ? ' open' : ''}${hasValue ? ' has-value' : ''}`}
                          onClick={() => setProjectsQfPillOpen(isOpen ? null : p.key)}
                        >
                          <span>{p.label}{hasValue && p.kind === 'single' ? `: ${labelOf(currentValue)}` : ''}</span>
                          {hasValue && p.kind === 'text' && <span className="hs-qf-count">1</span>}
                          <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                        </button>
                        {hasValue && (
                          <button className="hs-qf-clear-btn" title={`Clear ${p.label} filter`} onClick={(e) => { e.stopPropagation(); clear() }}>
                            <svg width="10" height="10" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                          </button>
                        )}
                        {isOpen && (
                          <div className="qf-pill-dropdown" ref={(el) => {
                            if (!el) return
                            const wrap = el.parentElement; if (!wrap) return
                            const btn = wrap.querySelector('button'); if (!btn) return
                            const rect = btn.getBoundingClientRect()
                            const popW = Math.min(280, window.innerWidth - 32)
                            let left = rect.left
                            if (left + popW > window.innerWidth - 16) left = window.innerWidth - 16 - popW
                            if (left < 16) left = 16
                            el.style.position = 'fixed'
                            el.style.top = `${rect.bottom + 4}px`
                            el.style.left = `${left}px`
                            el.style.right = 'auto'
                          }}>
                            <div className="qf-pill-list">
                              {hasValue && (
                                <button className="qf-pill-date-option" style={{ color: '#d94c53', borderBottom: '1px solid #eaf0f6', marginBottom: 4, paddingBottom: 8 }} onClick={clear}>
                                  <span className="qf-pill-date-label">Clear filter</span>
                                </button>
                              )}
                              {p.kind === 'single' ? p.options.map((opt) => (
                                <button
                                  key={opt.value}
                                  className={`qf-pill-date-option${currentValue === opt.value ? ' active' : ''}`}
                                  onClick={() => { setVal(currentValue === opt.value ? '' : opt.value); setProjectsQfPillOpen(null) }}
                                >
                                  <span className="qf-pill-date-label">{opt.label}</span>
                                </button>
                              )) : (
                                <div style={{ padding: '8px 10px' }}>
                                  <input
                                    type="text"
                                    autoFocus
                                    value={currentValue}
                                    placeholder={p.placeholder}
                                    onChange={(e) => setVal(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') setProjectsQfPillOpen(null) }}
                                    style={{ width: '100%', padding: '6px 8px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.82rem' }}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })
                })()}

                {/* Active quick filter pills (added via + More) */}
                {activeQuickFilters.map((fk) => {
                  const projectQuickFilterProperties = [
                    { category: 'Project details', items: [
                      { key: 'proj_status', label: 'Status', icon: 'enum' },
                      { key: 'proj_client', label: 'Contact', icon: 'text' },
                      { key: 'proj_address', label: 'Address', icon: 'text' },
                      { key: 'proj_assigned', label: 'Assigned to', icon: 'text' },
                    ]},
                    { category: 'Financial', items: [
                      { key: 'proj_value', label: 'Value', icon: 'number' },
                      { key: 'proj_progress', label: 'Progress', icon: 'number' },
                    ]},
                    { category: 'Dates', items: [
                      { key: 'proj_start', label: 'Start date', icon: 'date' },
                      { key: 'proj_end', label: 'End date', icon: 'date' },
                      { key: 'proj_created', label: 'Created', icon: 'date' },
                    ]},
                  ]
                  const allItems = projectQuickFilterProperties.flatMap(g => g.items)
                  const prop = allItems.find(i => i.key === fk)
                  if (!prop) return null
                  const selectedVals = qfPillValues[fk] || []
                  const isOpen = qfPillOpen === fk
                  return (
                    <div key={fk} className="qf-pill-wrap" ref={isOpen ? qfPillRef : undefined}>
                      <button
                        className={`hs-qf-btn hs-qf-trigger${isOpen ? ' open' : ''}${selectedVals.length > 0 ? ' has-value' : ''}`}
                        onClick={() => { setQfPillOpen(isOpen ? null : fk); setQfPillSearch('') }}
                      >
                        <span>{prop.label}</span>
                        {selectedVals.length > 0 && <span className="hs-qf-count">{selectedVals.length}</span>}
                        <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                      </button>
                      {selectedVals.length > 0 && (
                        <button
                          className="hs-qf-clear-btn"
                          title={`Clear ${prop.label} filter`}
                          onClick={(e) => { e.stopPropagation(); setQfPillValues(prev => ({ ...prev, [fk]: [] })); setQfPillOpen(null) }}
                        >
                          <svg width="10" height="10" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                        </button>
                      )}
                      {isOpen && (
                        <div className="qf-pill-dropdown" ref={(el) => {
                          if (!el) return
                          const wrap = el.parentElement
                          if (!wrap) return
                          const btn = wrap.querySelector('button')
                          if (!btn) return
                          const rect = btn.getBoundingClientRect()
                          const popW = Math.min(280, window.innerWidth - 32)
                          let left = rect.left
                          if (left + popW > window.innerWidth - 16) left = window.innerWidth - 16 - popW
                          if (left < 16) left = 16
                          el.style.position = 'fixed'
                          el.style.top = `${rect.bottom + 4}px`
                          el.style.left = `${left}px`
                          el.style.right = 'auto'
                        }}>
                          {/* Enum type — checkbox list */}
                          {prop.icon === 'enum' && qfEnumOptions[fk] && (
                            <div className="qf-pill-list">
                              {selectedVals.length > 0 && (
                                <button
                                  className="qf-pill-date-option"
                                  style={{ color: '#d94c53', borderBottom: '1px solid #eaf0f6', marginBottom: 4, paddingBottom: 8 }}
                                  onClick={() => { setQfPillValues(prev => ({ ...prev, [fk]: [] })); setQfPillOpen(null) }}
                                >
                                  <span className="qf-pill-date-label">Clear filter</span>
                                </button>
                              )}
                              {qfEnumOptions[fk]
                                .filter(o => !qfPillSearch || o.label.toLowerCase().includes(qfPillSearch.toLowerCase()))
                                .map(opt => (
                                  <label key={opt.value} className="hs-owner-item">
                                    <input
                                      type="checkbox"
                                      checked={selectedVals.includes(opt.value)}
                                      onChange={() => toggleQfPillValue(fk, opt.value)}
                                    />
                                    <span className="hs-owner-checkbox" />
                                    <span className="hs-owner-label">{opt.label}</span>
                                  </label>
                                ))
                              }
                            </div>
                          )}

                          {/* Date type — preset list */}
                          {prop.icon === 'date' && (
                            <div className="qf-pill-list">
                              {selectedVals.length > 0 && (
                                <button
                                  className="qf-pill-date-option"
                                  style={{ color: '#d94c53', borderBottom: '1px solid #eaf0f6', marginBottom: 4, paddingBottom: 8 }}
                                  onClick={() => { setQfPillValues(prev => ({ ...prev, [fk]: [] })); setQfPillOpen(null) }}
                                >
                                  <span className="qf-pill-date-label">Clear filter</span>
                                </button>
                              )}
                              {[
                                { label: 'Today', value: 'today' },
                                { label: 'Yesterday', value: 'yesterday' },
                                { label: 'This week', value: 'this_week' },
                                { label: 'Last week', value: 'last_week' },
                                { label: 'This month', value: 'this_month' },
                                { label: 'Last month', value: 'last_month' },
                                { label: 'This quarter', value: 'this_quarter' },
                                { label: 'This year', value: 'this_year' },
                              ].map(opt => (
                                <button key={opt.value} className={`qf-pill-date-option${selectedVals.includes(opt.value) ? ' active' : ''}`} onClick={() => { setQfPillValues(prev => ({ ...prev, [fk]: [opt.value] })); setQfPillOpen(null) }}>
                                  <span className="qf-pill-date-label">{opt.label}</span>
                                </button>
                              ))}
                            </div>
                          )}

                          {/* Text/number type — input */}
                          {(prop.icon === 'text' || prop.icon === 'number') && (
                            <div className="qf-pill-list" style={{ padding: '8px 12px' }}>
                              <input
                                type={prop.icon === 'number' ? 'number' : 'text'}
                                className="hs-owner-search"
                                placeholder={`Filter by ${prop.label.toLowerCase()}...`}
                                value={selectedVals[0] || ''}
                                onChange={(e) => setQfPillValues(prev => ({ ...prev, [fk]: e.target.value ? [e.target.value] : [] }))}
                                autoFocus
                              />
                              {selectedVals.length > 0 && (
                                <button
                                  style={{ marginTop: 6, fontSize: '0.78rem', color: '#d94c53', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                                  onClick={() => { setQfPillValues(prev => ({ ...prev, [fk]: [] })); setQfPillOpen(null) }}
                                >
                                  Clear filter
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}

                <div className="hs-qf-sep" />

                {/* "+ More" quick filter popover */}
                {(() => {
                  const projectQuickFilterProperties = [
                    { category: 'Project details', items: [
                      { key: 'proj_status', label: 'Status', icon: 'enum' },
                      { key: 'proj_client', label: 'Contact', icon: 'text' },
                      { key: 'proj_address', label: 'Address', icon: 'text' },
                      { key: 'proj_assigned', label: 'Assigned to', icon: 'text' },
                    ]},
                    { category: 'Financial', items: [
                      { key: 'proj_value', label: 'Value', icon: 'number' },
                      { key: 'proj_progress', label: 'Progress', icon: 'number' },
                    ]},
                    { category: 'Dates', items: [
                      { key: 'proj_start', label: 'Start date', icon: 'date' },
                      { key: 'proj_end', label: 'End date', icon: 'date' },
                      { key: 'proj_created', label: 'Created', icon: 'date' },
                    ]},
                  ]
                  return (
                <div className="hs-qf-more-wrap" ref={qfMoreRef}>
                  <button className={`hs-qf-btn hs-qf-more${qfMoreOpen ? ' open' : ''}`} onClick={() => { setQfMoreOpen(!qfMoreOpen); setQfMoreSearch(''); setQfMoreView('add') }}>
                    <svg width="10" height="10" viewBox="0 0 32 32" fill="currentColor"><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V3a1.14 1.14 0 1 1 2.28 0v25.14c0 .63-.51 1.14-1.14 1.14Z"/><path d="M28.14 16.71H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28"/></svg>
                    <span>More</span>
                  </button>
                  {qfMoreOpen && (
                    <div className="qf-more-popover" ref={(el) => {
                      if (!el) return
                      const btn = qfMoreRef.current?.querySelector('button')
                      const bar = document.querySelector('.hs-quick-filters')
                      if (!btn) return
                      const rect = btn.getBoundingClientRect()
                      const minLeft = bar ? bar.getBoundingClientRect().left : rect.left
                      const popW = Math.min(400, window.innerWidth - minLeft - 16)
                      let left = rect.left
                      if (left + popW > window.innerWidth - 16) left = window.innerWidth - 16 - popW
                      if (left < minLeft) left = minLeft
                      const spaceBelow = window.innerHeight - rect.bottom - 16
                      const maxH = Math.max(200, spaceBelow - 6)
                      el.style.position = 'fixed'
                      el.style.top = `${rect.bottom + 6}px`
                      el.style.left = `${left}px`
                      el.style.right = 'auto'
                      el.style.maxHeight = `${maxH}px`
                    }}>
                      {qfMoreView === 'add' ? (
                        <>
                          <div className="qf-more-top">
                            <button className="qf-more-edit-btn" onClick={() => setQfMoreView('edit')}>
                              <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M10.46 29.32H3c-.63 0-1.14-.51-1.14-1.14v-7.46c0-.3.12-.59.33-.81L19.24 2.86c.65-.65 1.51-1 2.42-1 .92 0 1.78.36 2.43 1.01l4.21 4.24a3.443 3.443 0 0 1 0 4.84L11.27 28.98c-.21.21-.5.33-.81.33Zm-6.32-2.29h5.84l16.7-16.7c.44-.44.45-1.17 0-1.61l-4.21-4.24c-.22-.22-.5-.34-.81-.34s-.59.12-.81.33L4.14 21.19z"/><path d="M25.01 14.76c-.29 0-.58-.11-.81-.33l-7.46-7.46c-.45-.45-.45-1.17 0-1.62s1.17-.45 1.62 0l7.46 7.46a1.14 1.14 0 0 1-.81 1.95"/></svg>
                              <span>Edit quick filters</span>
                            </button>
                          </div>
                          <div className="qf-more-body">
                            <h5 className="qf-more-heading">Add a quick filter</h5>
                            <div className="qf-more-search-wrap">
                              <svg className="qf-more-search-icon" width="14" height="14" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                              <input
                                className="qf-more-search"
                                placeholder="Search"
                                value={qfMoreSearch}
                                onChange={(e) => setQfMoreSearch(e.target.value)}
                                autoFocus
                              />
                            </div>
                          </div>
                          <div className="qf-more-list">
                            {projectQuickFilterProperties.map((group) => {
                              const filtered = group.items.filter(i =>
                                !activeQuickFilters.includes(i.key) &&
                                (!qfMoreSearch || i.label.toLowerCase().includes(qfMoreSearch.toLowerCase()))
                              )
                              if (filtered.length === 0) return null
                              return (
                                <div key={group.category}>
                                  <div className="qf-more-category">{group.category}</div>
                                  {filtered.map((item) => (
                                    <button
                                      key={item.key}
                                      className="qf-more-option"
                                      onClick={() => {
                                        setActiveQuickFilters(prev => [...prev, item.key])
                                        setQfMoreOpen(false)
                                        setQfMoreSearch('')
                                      }}
                                    >
                                      <span className="qf-more-option-icon">
                                        {item.icon === 'text' && (
                                          <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M5.53 10.52h2.84l4.55 12.05h-2.53l-.86-2.39H4.38l-.86 2.39H1zm3.27 7.59-1.84-5.15-1.84 5.15h3.69ZM16.28 21.33v1.24h-2.31V10h2.31v4.6c.59-.9 1.69-1.41 2.96-1.41 2.39 0 4.32 2 4.32 4.77s-1.93 4.79-4.32 4.79c-1.27 0-2.38-.52-2.96-1.41Zm5-3.35c0-1.55-1.1-2.74-2.55-2.74s-2.53 1.19-2.53 2.74 1.09 2.72 2.53 2.72 2.55-1.19 2.55-2.72M24.52 17.98c0-2.77 1.96-4.79 4.74-4.79.98 0 1.81.19 2.62.62v2.22c-.74-.5-1.46-.79-2.26-.79-1.65 0-2.77 1.17-2.77 2.74s1.12 2.72 2.77 2.72c.79 0 1.53-.34 2.26-.79v2.22c-.81.41-1.64.62-2.62.62-2.77 0-4.74-2.01-4.74-4.77"/></svg>
                                        )}
                                        {item.icon === 'number' && (
                                          <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M13.14 29.29c-.55 0-1.02-.39-1.12-.93l-1.37-7.22H4.57a1.14 1.14 0 1 1 0-2.28h5.63l-1.09-5.71H3a1.14 1.14 0 1 1 0-2.28h5.66L9.97 3.79a1.14 1.14 0 0 1 2.25-.38L13.67 11h5.71l-1.35-7.08a1.14 1.14 0 0 1 2.25-.38l1.44 7.46h5.71a1.14 1.14 0 1 1 0 2.28h-5.27l1.09 5.71H29a1.14 1.14 0 1 1 0 2.28h-5.31l1.34 7.08a1.14 1.14 0 0 1-2.25.38L21.34 21.14h-5.71l1.34 7.08a1.14 1.14 0 0 1-.93 1.32 1.22 1.22 0 0 1-.2.02h-.7zM14.11 13.14l1.09 5.71h5.71l-1.09-5.71z"/></svg>
                                        )}
                                        {item.icon === 'date' && (
                                          <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M9.86 8.71c-.63 0-1.14-.51-1.14-1.14V3A1.14 1.14 0 1 1 11 3v4.57c0 .63-.51 1.14-1.14 1.14M21.29 8.71c-.63 0-1.14-.51-1.14-1.14V3a1.14 1.14 0 1 1 2.28 0v4.57c0 .63-.51 1.14-1.14 1.14"/><path d="M25.86 29.28H5.29c-1.89 0-3.43-1.54-3.43-3.43V7.57c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v18.28c0 1.89-1.54 3.43-3.43 3.43ZM5.29 6.43c-.63 0-1.14.51-1.14 1.14v18.28c0 .63.51 1.14 1.14 1.14h20.57c.63 0 1.14-.51 1.14-1.14V7.57c0-.63-.51-1.14-1.14-1.14z"/><path d="M29 13.28H3A1.14 1.14 0 1 1 3 11h26a1.14 1.14 0 1 1 0 2.28"/></svg>
                                        )}
                                        {item.icon === 'enum' && (
                                          <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M23.57 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V7.57a1.14 1.14 0 1 1 2.28 0v18.29c0 .63.51 1.14 1.14 1.14h18.29a1.14 1.14 0 1 1 0 2.28Z"/><path d="M25.86 24.71h-16c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43h16c1.89 0 3.43 1.54 3.43 3.43v16c0 1.89-1.54 3.43-3.43 3.43Zm-16-20.57c-.63 0-1.14.51-1.14 1.14v16c0 .63.51 1.14 1.14 1.14h16c.63 0 1.14-.51 1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14h-16Z"/><path d="M16.84 17.48c-.3 0-.59-.12-.81-.33l-3.56-3.56a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l2.71 2.71 4.78-5.21c.43-.46 1.15-.5 1.61-.07.47.43.5 1.15.07 1.62l-5.59 6.09c-.21.23-.51.36-.82.37h-.02Z"/></svg>
                                        )}
                                      </span>
                                      <span className="qf-more-option-label">{item.label}</span>
                                    </button>
                                  ))}
                                </div>
                              )
                            })}
                            {projectQuickFilterProperties.every(g => g.items.every(i => activeQuickFilters.includes(i.key) || (qfMoreSearch && !i.label.toLowerCase().includes(qfMoreSearch.toLowerCase())))) && (
                              <div className="qf-more-empty">{activeQuickFilters.length > 0 && !qfMoreSearch ? 'All filters added' : 'No matching properties'}</div>
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          {/* Edit quick filters view */}
                          <div className="qf-edit-header">
                            <button className="qf-edit-back-btn" onClick={() => setQfMoreView('add')}>
                              <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95"/></svg>
                              <span>Back</span>
                            </button>
                            <button className="qf-edit-add-btn" onClick={() => setQfMoreView('add')}>
                              <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V3a1.14 1.14 0 1 1 2.28 0v25.14c0 .63-.51 1.14-1.14 1.14Z"/><path d="M28.14 16.71H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28"/></svg>
                              <span>Add</span>
                            </button>
                          </div>
                          <div className="qf-edit-title">Edit quick filters</div>
                          <div className="qf-edit-list">
                            {activeQuickFilters.map((fk, idx) => {
                              const allEditItems = projectQuickFilterProperties.flatMap(g => g.items)
                              const editProp = allEditItems.find(i => i.key === fk)
                              if (!editProp) return null
                              return (
                                <div
                                  key={fk}
                                  className={`qf-edit-item${qfDragIdx === idx ? ' dragging' : ''}${qfDragOverIdx === idx ? ' drag-over' : ''}`}
                                  draggable
                                  onDragStart={() => setQfDragIdx(idx)}
                                  onDragOver={(e) => { e.preventDefault(); setQfDragOverIdx(idx) }}
                                  onDragEnd={() => {
                                    if (qfDragIdx !== null && qfDragOverIdx !== null && qfDragIdx !== qfDragOverIdx) {
                                      setActiveQuickFilters(prev => {
                                        const next = [...prev]
                                        const [moved] = next.splice(qfDragIdx, 1)
                                        next.splice(qfDragOverIdx, 0, moved)
                                        return next
                                      })
                                    }
                                    setQfDragIdx(null)
                                    setQfDragOverIdx(null)
                                  }}
                                >
                                  <span className="qf-edit-drag">
                                    <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><circle cx="10.14" cy="4.14" r="2.29"/><circle cx="10.14" cy="15.57" r="2.29"/><circle cx="10.14" cy="27" r="2.29"/><circle cx="21.57" cy="4.14" r="2.29"/><circle cx="21.57" cy="15.57" r="2.29"/><circle cx="21.57" cy="27" r="2.29"/></svg>
                                  </span>
                                  <span className="qf-edit-label">{editProp.label}</span>
                                  <button className="qf-edit-delete" onClick={() => {
                                    setActiveQuickFilters(prev => prev.filter(k => k !== fk))
                                    setQfPillValues(prev => { const n = { ...prev }; delete n[fk]; return n })
                                  }}>
                                    <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M25 8H7c-.55 0-1-.45-1-1s.45-1 1-1h18c.55 0 1 .45 1 1s-.45 1-1 1M13 24c-.55 0-1-.45-1-1V13c0-.55.45-1 1-1s1 .45 1 1v10c0 .55-.45 1-1 1M19 24c-.55 0-1-.45-1-1V13c0-.55.45-1 1-1s1 .45 1 1v10c0 .55-.45 1-1 1"/><path d="M20 8h-8c-.55 0-1-.45-1-1V5c0-1.65 1.35-3 3-3h4c1.65 0 3 1.35 3 3v2c0 .55-.45 1-1 1m-7-2h6V5c0-.55-.45-1-1-1h-4c-.55 0-1 .45-1 1zM21 30H11c-2.76 0-5-2.24-5-5V11c0-.55.45-1 1-1s1 .45 1 1v14c0 1.65 1.35 3 3 3h10c1.65 0 3-1.35 3-3V11c0-.55.45-1 1-1s1 .45 1 1v14c0 2.76-2.24 5-5 5"/></svg>
                                  </button>
                                </div>
                              )
                            })}
                            {activeQuickFilters.length === 0 && (
                              <div className="qf-more-empty">No quick filters added</div>
                            )}
                          </div>
                          <div className="qf-edit-footer">
                            <small className="qf-edit-counter">{activeQuickFilters.length} of {projectQuickFilterProperties.reduce((n, g) => n + g.items.length, 0)} filters added</small>
                            {activeQuickFilters.length > 0 && (
                              <button className="qf-edit-delete-all" onClick={() => {
                                setActiveQuickFilters([])
                                setQfPillValues({})
                                setQfMoreOpen(false)
                                setQfMoreView('add')
                              }}>
                                <svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M25 8H7c-.55 0-1-.45-1-1s.45-1 1-1h18c.55 0 1 .45 1 1s-.45 1-1 1M13 24c-.55 0-1-.45-1-1V13c0-.55.45-1 1-1s1 .45 1 1v10c0 .55-.45 1-1 1M19 24c-.55 0-1-.45-1-1V13c0-.55.45-1 1-1s1 .45 1 1v10c0 .55-.45 1-1 1"/><path d="M20 8h-8c-.55 0-1-.45-1-1V5c0-1.65 1.35-3 3-3h4c1.65 0 3 1.35 3 3v2c0 .55-.45 1-1 1m-7-2h6V5c0-.55-.45-1-1-1h-4c-.55 0-1 .45-1 1zM21 30H11c-2.76 0-5-2.24-5-5V11c0-.55.45-1 1-1s1 .45 1 1v14c0 1.65 1.35 3 3 3h10c1.65 0 3-1.35 3-3V11c0-.55.45-1 1-1s1 .45 1 1v14c0 2.76-2.24 5-5 5"/></svg>
                                <span>Delete all quick filters</span>
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
                  )
                })()}
              </div>}

              {/* Table content */}
              <div className={`hs-content${previewProject ? ' with-preview' : ''}${tableSettingsOpen ? ' with-settings' : ''}`}>
                <div className="hs-table-area">
                  {/* Bulk actions bar */}
                  {projectSelectedRows.size > 0 && (
                    <div className="hs-selection-bar">
                      <span className="hs-sel-count">{projectSelectedRows.size} selected</span>
                      <div className="hs-sel-sep" />
                      <button className="hs-sel-btn" onClick={() => {
                        const firstId = [...projectSelectedRows][0]
                        const proj = projectsData.find(p => p.id === firstId)
                        if (proj) { setProjectForm(proj); setProjectError(null); setShowProjectPanel(true) }
                      }}>Edit</button>
                      <button className="hs-sel-btn" onClick={() => setProjectsDeleteConfirm(projectSelectedRows.size)}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 8v13H3V8"/><path d="M1 3h22v5H1z"/><path d="M10 12h4"/></svg>Archive</button>
                      {(myCapabilities ? myCapabilities.delete_project !== false : true) && (
                        <button className="hs-sel-btn" style={{ color: '#dc2626' }} onClick={async () => {
                          const ids = [...projectSelectedRows]
                          if (ids.length === 0) return
                          const n = ids.length
                          const msg = n === 1
                            ? 'Delete this project? It will be removed from the list and can be restored later.'
                            : `Delete ${n} projects? They will be removed from the list and can be restored later.`
                          if (!window.confirm(msg)) return
                          await Promise.all(ids.map(id => deleteCrmProject(id).catch(() => {})))
                          setProjectSelectedRows(new Set())
                          loadProjects()
                        }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                          Delete
                        </button>
                      )}
                      <button className="hs-sel-close" onClick={() => setProjectSelectedRows(new Set())} title="Deselect all">&times;</button>
                    </div>
                  )}

                  {projectsDeleteConfirm !== null && (
                    <div className="kanban-modal-overlay" onClick={() => { if (!projectsDeleteRunning) setProjectsDeleteConfirm(null) }}>
                      <div className="kanban-confirm-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="kanban-confirm-icon" style={{ background: '#fef3c7' }}>
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#92400e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 8v13H3V8"/><path d="M1 3h22v5H1z"/><path d="M10 12h4"/></svg>
                        </div>
                        <h3>Archive {projectsDeleteConfirm} project{projectsDeleteConfirm === 1 ? '' : 's'}?</h3>
                        <p>
                          You're about to archive <strong>{projectsDeleteConfirm} project{projectsDeleteConfirm === 1 ? '' : 's'}</strong>.
                          Archived projects are hidden from the list but can be restored later.
                        </p>
                        <div className="kanban-confirm-actions">
                          <button className="kanban-confirm-cancel" disabled={projectsDeleteRunning} onClick={() => setProjectsDeleteConfirm(null)}>Cancel</button>
                          <button
                            className="kanban-confirm-ok"
                            disabled={projectsDeleteRunning}
                            style={{ background: '#92400e' }}
                            onClick={async () => {
                              setProjectsDeleteRunning(true)
                              try {
                                await Promise.all([...projectSelectedRows].map(id => deleteCrmProject(id).catch(() => {})))
                                setProjectSelectedRows(new Set())
                                loadProjects()
                              } finally {
                                setProjectsDeleteRunning(false)
                                setProjectsDeleteConfirm(null)
                              }
                            }}
                          >
                            {projectsDeleteRunning ? 'Archiving…' : `Yes, archive ${projectsDeleteConfirm}`}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {projectsLoading && projectsData.length > 0 && (
                    <div className="hs-loading-bar" />
                  )}

                  <div className="hs-table-scroll">
                    <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                      <colgroup>
                        <col style={{ width: 40 }} />
                        <col style={{ width: projectColWidths.name ?? PROJECTS_COL_DEFAULTS.name }} />
                        <col style={{ width: projectColWidths.pipeline_stage ?? PROJECTS_COL_DEFAULTS.pipeline_stage }} />
                        <col style={{ width: projectColWidths.client_name ?? PROJECTS_COL_DEFAULTS.client_name }} />
                        <col style={{ width: projectColWidths.contract_value ?? PROJECTS_COL_DEFAULTS.contract_value }} />
                        <col style={{ width: projectColWidths.estimator ?? PROJECTS_COL_DEFAULTS.estimator }} />
                        <col style={{ width: projectColWidths.owner ?? PROJECTS_COL_DEFAULTS.owner }} />
                        <col style={{ width: projectColWidths.progress ?? PROJECTS_COL_DEFAULTS.progress }} />
                        <col style={{ width: projectColWidths.start_date ?? PROJECTS_COL_DEFAULTS.start_date }} />
                        <col style={{ width: projectColWidths.end_date ?? PROJECTS_COL_DEFAULTS.end_date }} />
                        <col style={{ width: projectColWidths.value ?? PROJECTS_COL_DEFAULTS.value }} />
                        <col style={{ width: projectColWidths.assigned_to ?? PROJECTS_COL_DEFAULTS.assigned_to }} />
                        <col style={{ width: projectColWidths.po_number ?? PROJECTS_COL_DEFAULTS.po_number }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th className="hs-th-check">
                            <input type="checkbox" checked={projectsData.length > 0 && projectSelectedRows.size === projectsData.length} onChange={toggleAllProjectRows} />
                          </th>
                          {[
                            { key: 'name', label: 'Project Name', sortField: 'name', frozen: true },
                            { key: 'pipeline_stage', label: 'Pipeline Stage', sortField: 'pipeline_stage' },
                            { key: 'client_name', label: 'Contact', sortField: 'client_name' },
                            { key: 'contract_value', label: 'Contract Value', sortField: 'contract_value' },
                            { key: 'estimator', label: 'Project Lead', sortField: 'estimator' },
                            { key: 'owner', label: 'Owner', sortField: 'owner_name' },
                            { key: 'progress', label: 'Progress', sortField: 'progress' },
                            { key: 'start_date', label: 'Start Date', sortField: 'start_date' },
                            { key: 'end_date', label: 'End Date', sortField: 'end_date' },
                            { key: 'value', label: 'Value', sortField: 'value' },
                            { key: 'assigned_to', label: 'Assigned To', sortField: 'assigned_to' },
                            { key: 'po_number', label: 'PO #', sortField: 'po_number' },
                          ].map(col => (
                            <th
                              key={col.key}
                              className={`hs-th-resizable hs-th-sortable${col.frozen ? ' hs-th-frozen' : ''}`}
                              style={col.frozen ? { position: 'sticky', left: 40, top: 0, zIndex: 15, background: '#f8fafc' } : undefined}
                              onClick={() => toggleProjectSort(col.sortField)}
                              onContextMenu={(e) => { e.preventDefault(); setColContextMenu({ field: col.sortField, x: e.clientX, y: e.clientY }) }}
                            >
                              <span className="hs-th-content">
                                {col.label}
                                {projectsSort.field === col.sortField && <span className="hs-sort-arrow">{projectsSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                                <span className="hs-th-hover-sort"><svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M29.38 21.96a1.147 1.147 0 0 0-1.617 0l-3.85 3.852V5.714a1.143 1.143 0 0 0-2.286 0v20.099l-3.853-3.852a1.144 1.144 0 0 0-1.615 1.617l5.803 5.802a1.143 1.143 0 0 0 1.616 0l5.802-5.802a1.144 1.144 0 0 0 0-1.617M15.84 8.422 10.039 2.62a1.145 1.145 0 0 0-1.616 0L2.62 8.422a1.144 1.144 0 0 0 1.617 1.617l3.85-3.851v20.098a1.143 1.143 0 0 0 2.286 0V6.187l3.853 3.852a1.14 1.14 0 0 0 1.616-.001 1.14 1.14 0 0 0-.001-1.616"/></svg></span>
                              </span>
                              {col.key === 'pipeline_stage' && (
                                <button className="hs-th-kebab" onClick={(e) => {
                                  e.stopPropagation()
                                  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
                                  setProjectsStageFilterPopover({ x: rect.left, y: rect.bottom + 4 })
                                }}>
                                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><circle cx="16" cy="4.14" r="2.29"/><circle cx="16" cy="15.57" r="2.29"/><circle cx="16" cy="27" r="2.29"/></svg>
                                </button>
                              )}
                              <div className="hs-col-resize-handle" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => onProjectResizeStart(col.key, e)} />
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {projectsLoading && projectsData.length === 0 ? (
                          <tr><td colSpan={13} className="hs-loading-cell"><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}><DotLottieReact src="/loading.lottie" loop autoplay style={{ width: 80, height: 80 }} /><span style={{ fontSize: '0.82rem', color: '#7c98b6', marginTop: 8 }}>Loading projects...</span></div></td></tr>
                        ) : projectsData.length === 0 ? (
                          <tr><td colSpan={13} className="hs-empty-cell">
                            <div style={{ padding: '40px', textAlign: 'center' }}>
                              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '12px' }}><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
                              <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#33475b', marginBottom: '6px' }}>Manage your projects</div>
                              <div style={{ fontSize: '0.82rem', color: '#7c98b6', maxWidth: '360px', margin: '0 auto' }}>Track BotPros projects from proposal through completion.</div>
                            </div>
                          </td></tr>
                        ) : projectsData.filter(p => {
                          const stage = (p as any).pipeline_stage
                          if (projectsStageFilterSet && !projectsStageFilterSet.has(stage)) return false
                          if (projectsPipelineStageFilter && stage !== projectsPipelineStageFilter) return false
                          return true
                        }).slice().sort((a, b) => {
                          const terminal = (s: string) => s === 'P_COMPLETED' || s === 'P_CANCELLED' ? 1 : 0
                          return terminal((a as any).pipeline_stage) - terminal((b as any).pipeline_stage)
                        }).map((proj) => {
                          const statusColors: Record<string, { bg: string; fg: string }> = {
                            pending: { bg: '#fff3e0', fg: '#e65100' },
                            active: { bg: '#e3f2fd', fg: '#1565c0' },
                            on_hold: { bg: '#f8fafc', fg: '#64748b' },
                            completed: { bg: '#e8f5e9', fg: '#2e7d32' },
                            cancelled: { bg: '#ffeaea', fg: '#d32f2f' },
                          }
                          const sc = statusColors[proj.status] || statusColors.pending
                          return (
                            <tr key={proj.id} className={`hs-row${projectSelectedRows.has(proj.id) ? ' selected' : ''}${previewProject?.id === proj.id ? ' is-previewed' : ''}`}>
                              <td className="hs-td-check">
                                <input type="checkbox" checked={projectSelectedRows.has(proj.id)} onChange={() => toggleProjectRow(proj.id)} />
                              </td>
                              <td className="hs-td-name" style={{ position: 'sticky', left: 40, zIndex: 2, background: projectSelectedRows.has(proj.id) ? '#eff6ff' : '#fff' }}>
                                <div className="hs-name-cell">
                                  <div className="hs-avatar" style={{ background: sc.fg }}>
                                    {(proj.name || '').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                                  </div>
                                  <span className="hs-name-link" onClick={(e) => {
                                    e.stopPropagation()
                                    setProjectDetailData(proj)
                                    setProjectDetailOpen(true)
                                    setProjectDetailTab('about')
                                    setPrjActivitySearch('')
                                    setPrjActivityFilter('all')
                                    setPrjExpandedCards(new Set())
                                    window.location.hash = `/projects/record/${proj.id}`
                                  }}>{proj.name}</span>
                                  <button className="hs-preview-btn" onClick={(e) => {
                                    e.stopPropagation()
                                    setTableSettingsOpen(false)
                                    if (previewProject?.id === proj.id) { closeProjectPreview() } else { setPreviewProject(proj) }
                                  }}>Preview</button>
                                </div>
                              </td>
                              <td onClick={(e) => e.stopPropagation()} style={{ overflow: 'visible' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                                  {(proj as any).pipeline_stage ? (
                                    <span
                                      className="hs-status-badge"
                                      style={{ background: DELIVERY_STAGE_COLORS[(proj as any).pipeline_stage] || '#94a3b8', color: '#fff', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
                                        setProjectStagePickerPos({ top: rect.bottom + 4, left: rect.left })
                                        setProjectStagePicker({ projectId: (proj as any).id, currentStage: (proj as any).pipeline_stage })
                                      }}
                                      title="Click to change stage"
                                    >
                                      {DELIVERY_STAGE_LABELS[(proj as any).pipeline_stage] || (proj as any).pipeline_stage}
                                      <svg width="8" height="8" viewBox="0 0 10 6" fill="currentColor" style={{ opacity: 0.8 }}><path d="M1 1l4 4 4-4"/></svg>
                                    </span>
                                  ) : <span className="cell-empty">--</span>}
                                  {(proj as any).pipeline_stage === 'P_ON_HOLD' && (proj as any).hold_type && (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '1px 6px', borderRadius: 8, background: '#f5f5f5', color: '#64748b', fontSize: '0.65rem', fontWeight: 600 }} title={(proj as any).hold_reason ? `${(proj as any).hold_type}: ${(proj as any).hold_reason}` : (proj as any).hold_type}>
                                      {HOLD_TYPES.find(ht => ht.value === (proj as any).hold_type)?.label || (proj as any).hold_type}
                                      {(proj as any).hold_reason && ` — ${(proj as any).hold_reason.substring(0, 30)}${(proj as any).hold_reason.length > 30 ? '…' : ''}`}
                                    </span>
                                  )}
                                </div>
                              </td>
                              {(() => {
                                // Trust client_id presence as the linkage signal. The deleted-contact
                                // case is caught by fetchClient's .catch alert below. (We previously
                                // depended on a crm_found flag on the row, but that turned out to
                                // misfire even when the API confirmed the contact existed.)
                                const hasClient = !!(proj as any).client_id
                                return (
                                  <td style={{ color: hasClient ? '#0091ae' : '#94a3b8', fontSize: '13px', cursor: hasClient ? 'pointer' : 'default' }} onClick={() => { if (hasClient) { fetchClient((proj as any).client_id).then(c => { if (applyClientOrRedirect(c)) return; setContactDetailOpen(true); setContactDetailTab('about'); setActiveView('contacts'); window.location.hash = `/contacts/record/${encodeURIComponent((proj as any).client_id)}` }).catch(() => alert('Contact not found in the CRM — it may have been deleted.')) } }}>{proj.client_name ? <span>{proj.client_name}</span> : <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}><span className="cell-empty">--</span><span style={{ display: 'inline-flex', width: 'fit-content', padding: '1px 6px', borderRadius: 8, fontSize: '0.6rem', fontWeight: 600, background: '#fee2e2', color: '#dc2626' }}>Not linked</span></div>}</td>
                                )
                              })()}
                              <td className="hs-td-num" style={{ fontWeight: 500 }}>{(proj as any).contract_value ? currencyFormatter.format((proj as any).contract_value) : <span className="cell-empty">--</span>}</td>
                              <td style={{ color: '#516f90', fontSize: '13px' }}>{(proj as any).estimator_name || <span className="cell-empty">--</span>}</td>
                              <td style={{ fontSize: '13px' }}>
                                {(() => {
                                  // MULTI-OWNER: the list query returns owner_names (comma-joined
                                  // GROUP_CONCAT of all active owner members), falling back to the
                                  // legacy scalar owner_name when no member rows exist. Show all
                                  // owners; if >2 collapse the tail into a "+N" chip (full list on hover).
                                  const ownerNamesRaw = (proj as any).owner_names as string | null
                                  const ownerList: string[] = (typeof ownerNamesRaw === 'string' && ownerNamesRaw.length > 0)
                                    ? ownerNamesRaw.split(',').map((s: string) => s.trim()).filter(Boolean)
                                    : ((proj as any).owner_name ? [String((proj as any).owner_name)] : [])
                                  const ownersShown = ownerList.slice(0, 2)
                                  const ownersExtra = ownerList.length - ownersShown.length
                                  return ownerList.length > 0 ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4, minWidth: 0 }}>
                                      <span style={{ color: '#33475b' }} title={ownerList.join(', ')}>{ownersShown.join(', ')}</span>
                                      {ownersExtra > 0 && (
                                        <span title={ownerList.join(', ')} style={{ fontSize: '0.65rem', fontWeight: 600, color: '#475569', background: '#e2e8f0', padding: '1px 6px', borderRadius: 8, cursor: 'default', whiteSpace: 'nowrap' }}>+{ownersExtra}</span>
                                      )}
                                    </div>
                                    {(() => {
                                      const status = (proj as any).owner_approval_status
                                      const unread = Number((proj as any).admin_unread_count || 0)
                                      const lastRole = (proj as any).latest_chat_user_role as string | null
                                      // "Pending response" = the owner spoke last and the admin
                                      // has not yet replied. Stays sticky even after admin opens
                                      // the chat — only an actual admin reply clears it.
                                      const ownerSpokeLast = !!lastRole && lastRole !== 'admin'
                                      const pid = String((proj as any).id)
                                      const openChat = (e: React.MouseEvent) => {
                                        e.stopPropagation()
                                        setSubPortalProjectId(pid)
                                        setSubChatModalOpen(true)
                                        fetchSubAdminProjectChat(pid).then((r:any) => { if (r?.success) setSubProjectChat(r.data.messages) }).catch(() => {})
                                        // Clear the row's unread immediately so the chip switches back
                                        setProjectsData((prev: any[]) => prev.map((p: any) => p.id === pid ? { ...p, admin_unread_count: 0 } : p))
                                      }
                                      const wrapStyle: React.CSSProperties = { whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.3, maxWidth: '100%' }
                                      if (status === 'denied') {
                                        const denyStyle = unread > 0
                                          ? { color: '#5b21b6', background: '#ede9fe' }
                                          : { color: '#991b1b', background: '#fee2e2' }
                                        return (
                                          <button
                                            type="button"
                                            onClick={unread > 0 ? openChat : undefined}
                                            style={{ fontSize: '0.68rem', padding: '2px 8px', borderRadius: 10, alignSelf: 'flex-start', border: 'none', cursor: unread > 0 ? 'pointer' : 'default', ...denyStyle, ...wrapStyle, textAlign: 'left', fontWeight: unread > 0 ? 600 : 500 }}
                                            onMouseEnter={unread > 0 ? (e) => { (e.currentTarget as HTMLButtonElement).style.filter = 'brightness(0.95)' } : undefined}
                                            onMouseLeave={unread > 0 ? (e) => { (e.currentTarget as HTMLButtonElement).style.filter = '' } : undefined}
                                            title={unread > 0 ? 'Open chat to reply' : undefined}
                                          >Owner denied{unread > 0 ? ` · ${unread} new` : ''}</button>
                                        )
                                      }
                                      // "Pending response" (approval-needed) ONLY shows pre-approval
                                      // (pending/denied — handled above). Once approved, the owner
                                      // speaking last is NOT an approval state — it falls through to
                                      // the read-tracked blue "unread" chip below, which clears when
                                      // the admin opens the chat (chat_admin_last_read_at updated).
                                      if (ownerSpokeLast && status !== 'denied' && status !== 'approved' && !(proj as any).started_at) {
                                        return (
                                          <button
                                            type="button"
                                            onClick={openChat}
                                            style={{ fontSize: '0.68rem', color: '#5b21b6', background: '#ede9fe', padding: '2px 8px', borderRadius: 10, alignSelf: 'flex-start', fontWeight: 600, border: 'none', cursor: 'pointer', ...wrapStyle, textAlign: 'left' }}
                                            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#ddd6fe' }}
                                            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#ede9fe' }}
                                            title={unread > 0 ? `Pending response — ${unread} new message${unread > 1 ? 's' : ''} from the owner` : 'Pending response — owner is awaiting your reply'}
                                          >💬 Pending response{unread > 0 ? ` · ${unread} new` : ''}</button>
                                        )
                                      }
                                      if (status === 'pending') {
                                        return <span style={{ fontSize: '0.68rem', color: '#b45309', background: '#fef3c7', padding: '2px 8px', borderRadius: 10, alignSelf: 'flex-start', ...wrapStyle }}>Awaiting approval</span>
                                      }
                                      // Owner approved but hasn't clicked Start Project yet
                                      // (project start is explicitly gated). Show as a
                                      // single readiness chip so the cell never has two.
                                      if (status === 'approved' && !(proj as any).started_at && (proj as any).status !== 'completed' && (proj as any).status !== 'cancelled') {
                                        return <span style={{ fontSize: '0.68rem', color: '#92400e', background: '#fef3c7', padding: '2px 8px', borderRadius: 10, alignSelf: 'flex-start', ...wrapStyle }} title="Owner approved — waiting to start the project">Awaiting Start</span>
                                      }
                                      if (unread > 0) {
                                        return (
                                          <button
                                            type="button"
                                            onClick={openChat}
                                            style={{ fontSize: '0.68rem', color: '#1d4ed8', background: '#dbeafe', padding: '2px 8px', borderRadius: 10, alignSelf: 'flex-start', fontWeight: 600, border: 'none', cursor: 'pointer', ...wrapStyle, textAlign: 'left' }}
                                            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#bfdbfe' }}
                                            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#dbeafe' }}
                                            title={`${unread} unread message${unread > 1 ? 's' : ''} — click to view`}
                                          >💬 {unread} unread</button>
                                        )
                                      }
                                      return null
                                    })()}
                                  </div>
                                ) : (
                                  <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Unassigned</span>
                                )
                                })()}
                              </td>
                              <td>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <div style={{ flex: 1, height: '6px', background: '#eaf0f6', borderRadius: '3px', overflow: 'hidden' }}>
                                    <div style={{ width: `${proj.progress}%`, height: '100%', background: proj.progress >= 100 ? '#2e7d32' : proj.progress >= 50 ? '#1565c0' : '#e65100', borderRadius: '3px', transition: 'width 0.3s' }} />
                                  </div>
                                  <span style={{ fontSize: '0.75rem', color: '#64748b', minWidth: '30px', textAlign: 'right' }}>{proj.progress}%</span>
                                </div>
                              </td>
                              {(() => {
                                const stage = (proj as any).pipeline_stage as string | undefined
                                const inProgress = stage === 'P_IN_PROGRESS' || stage === 'P_INSPECTION'
                                const finished = stage === 'P_COMPLETED' || stage === 'P_CANCELLED'
                                return <>
                                  <td style={{ color: '#516f90', fontSize: '13px' }}>
                                    {proj.start_date ? (
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
                                        <span>{formatDatePref(proj.start_date)}</span>
                                        {(() => {
                                          if (finished || inProgress) return null
                                          const days = Math.floor((Date.now() - Date.parse(proj.start_date)) / 86400000)
                                          if (days <= 0) return null
                                          return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '1px 6px', borderRadius: 8, background: '#fef3c7', color: '#92400e', fontSize: '0.65rem', fontWeight: 600 }}>{days}d past start</span>
                                        })()}
                                      </div>
                                    ) : <span className="cell-empty">--</span>}
                                  </td>
                                  <td style={{ color: '#516f90', fontSize: '13px' }}>
                                    {proj.end_date ? (
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
                                        <span>{formatDatePref(proj.end_date)}</span>
                                        {(() => {
                                          if (!inProgress) return null
                                          const days = Math.floor((Date.now() - Date.parse(proj.end_date)) / 86400000)
                                          if (days <= 0) return null
                                          return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '1px 6px', borderRadius: 8, background: '#fee2e2', color: '#991b1b', fontSize: '0.65rem', fontWeight: 600 }}>{days}d overdue</span>
                                        })()}
                                      </div>
                                    ) : <span className="cell-empty">--</span>}
                                  </td>
                                </>
                              })()}
                              <td className="hs-td-num" style={{ fontWeight: 500 }}>{proj.value ? currencyFormatter.format(proj.value) : <span className="cell-empty">--</span>}</td>
                              <td style={{ color: '#516f90', fontSize: '13px' }}>{proj.assigned_to || <span className="cell-empty">--</span>}</td>
                              <td style={{ color: '#516f90', fontSize: '13px', fontFamily: 'monospace' }}>{(proj as any).po_number || <span className="cell-empty">--</span>}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="hs-pagination">
                    <nav className="hs-paginator" aria-label="Pagination">
                      <button className="hs-paginator-btn" disabled={projectsPage <= 1} onClick={() => setProjectsPage(p => p - 1)} aria-label="Previous page">
                        <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95"/></svg>
                        <span>Prev</span>
                      </button>
                      {(() => {
                        const pages: number[] = []
                        const start = Math.max(1, projectsPage - 2)
                        const end = Math.min(projectsPageCount, start + 4)
                        for (let i = start; i <= end; i++) pages.push(i)
                        return pages.map(p => (
                          <button key={p} className={`hs-paginator-num${p === projectsPage ? ' active' : ''}`} onClick={() => setProjectsPage(p)}>{p}</button>
                        ))
                      })()}
                      <button className="hs-paginator-btn" disabled={projectsPage >= projectsPageCount} onClick={() => setProjectsPage(p => p + 1)} aria-label="Next page">
                        <span>Next</span>
                        <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M11 27.71c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62l9.48-9.48-9.48-9.47a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0L22.1 15.48c.45.45.45 1.17 0 1.62L11.81 27.39c-.22.22-.52.33-.81.33Z"/></svg>
                      </button>
                    </nav>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', marginLeft: 'auto', marginRight: 50 }}><select value={tablePageSize} onChange={e => { setTablePageSize(Number(e.target.value) as any); setProjectsPage(1) }} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #e2e8f0', fontSize: '0.82rem' }}><option value={100}>100</option><option value={200}>200</option><option value={500}>500</option></select><span>per page</span></div>
                  </div>
                </div>

                {/* Table settings panel — MUST be a direct child of .hs-content
                    (the panel is position:absolute, top:0 — anchored to the nearest
                    positioned ancestor). As a child of .hs-contacts it anchored to
                    the whole view and its header vanished under the topbar/toolbar
                    (tbl-settings-uniform-v1; guard: src/lib/project/settingsPanelPlacement.ts). */}
                {tableSettingsOpen && (
                  <div className="hs-settings-panel" data-tbl-settings="tbl-settings-uniform-v1">
                    <div className="hs-settings-header">
                      <span className="hs-settings-title">Table settings</span>
                      <button className="hs-settings-close" onClick={() => setTableSettingsOpen(false)}>
                        <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                      </button>
                    </div>
                    <div className="hs-settings-body">
                      <div className="hs-settings-group">
                        <label className="hs-settings-label">
                          Pagination
                          <span className="hs-info-icon" title="Number of rows shown per page">
                            <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg>
                          </span>
                        </label>
                        {([100, 200, 500] as const).map(size => (
                          <label className="hs-settings-radio" key={size}>
                            <input type="radio" name="projPageSize" checked={tablePageSize === size} onChange={() => { setTablePageSize(size); setProjectsPage(1) }} />
                            <span className="hs-radio-indicator" />
                            <span>{size} per page</span>
                          </label>
                        ))}
                      </div>
                      <hr className="hs-settings-divider" />
                      <div className="hs-settings-group">
                        <label className="hs-settings-label">
                          Row height
                          <span className="hs-info-icon" title="Adjust the vertical spacing of table rows">
                            <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg>
                          </span>
                        </label>
                        {([
                          { value: 'small' as const, label: 'Compact' },
                          { value: 'medium' as const, label: 'Default' },
                          { value: 'large' as const, label: 'Comfortable' },
                        ]).map(opt => (
                          <label className="hs-settings-radio" key={opt.value}>
                            <input type="radio" name="projRowHeight" checked={tableRowHeight === opt.value} onChange={() => setTableRowHeight(opt.value)} />
                            <span className="hs-radio-indicator" />
                            <span>{opt.label}</span>
                          </label>
                        ))}
                      </div>
                      <hr className="hs-settings-divider" />
                      <div className="hs-settings-group">
                        <div className="hs-settings-toggle-row">
                          <label className="hs-settings-label">
                            Zebra striping
                            <span className="hs-info-icon" title="Alternate row background colors for easier reading">
                              <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg>
                            </span>
                          </label>
                          <button
                            className={`hs-toggle${tableZebraStriping ? ' on' : ''}`}
                            onClick={() => setTableZebraStriping(!tableZebraStriping)}
                            role="switch"
                            aria-checked={tableZebraStriping}
                          >
                            <span className="hs-toggle-track">
                              <span className="hs-toggle-on">ON</span>
                              <span className="hs-toggle-off">OFF</span>
                              <span className="hs-toggle-handle" />
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Project preview sidebar */}
                {(previewProject || previewProjectClosing) && (() => {
                  const proj = previewProject
                  const statusColors: Record<string, { bg: string; fg: string }> = {
                    pending: { bg: '#fff3e0', fg: '#e65100' },
                    active: { bg: '#e3f2fd', fg: '#1565c0' },
                    on_hold: { bg: '#f8fafc', fg: '#64748b' },
                    completed: { bg: '#e8f5e9', fg: '#2e7d32' },
                    cancelled: { bg: '#ffeaea', fg: '#d32f2f' },
                  }
                  const sc = statusColors[proj?.status] || statusColors.pending
                  return (
                    <div className={`hs-preview${previewProjectClosing ? ' closing' : ''}`}>
                      <div className="hs-preview-header">
                        <strong className="hs-preview-name">{proj?.name}</strong>
                        <button className="hs-preview-close" onClick={closeProjectPreview}><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg></button>
                      </div>
                      <div className="hs-preview-body">
                        <div className="hs-preview-hero">
                          <div className="hs-preview-avatar-lg" style={{ background: sc.fg }}>
                            {(proj?.name || '?')[0].toUpperCase()}
                          </div>
                          <div className="hs-preview-hero-info">
                            <h3>{proj?.name}</h3>
                            <span className="hs-status-badge" style={{ background: sc.bg, color: sc.fg }}>
                              {proj?.status?.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                            </span>
                          </div>
                        </div>

                        <div className="hs-preview-actions">
                          <button className="hs-preview-action" title="Open record" onClick={() => {
                            closeProjectPreview()
                            setProjectDetailData(proj)
                            setProjectDetailOpen(true)
                            setProjectDetailTab('about')
                            window.location.hash = `/projects/record/${proj?.id}`
                          }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/></svg>
                          </button>
                          <button className="hs-preview-action" title="Edit" onClick={() => {
                            closeProjectPreview()
                            setProjectForm(proj)
                            setProjectError(null)
                            setShowProjectPanel(true)
                          }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg>
                          </button>
                          {(myCapabilities ? myCapabilities.delete_project !== false : true) && (
                          <button className="hs-preview-action" data-variant="danger" title="Delete" style={{ color: '#d94c53' }} onClick={async () => {
                            if (!proj?.id) return
                            const ok = await startProjectDelete({ id: String(proj.id), name: proj?.name || 'this project' })
                            if (ok) { closeProjectPreview(); loadProjects() }
                          }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                          </button>
                          )}
                        </div>

                        <div className="hs-preview-section">
                          <h4>Project Details</h4>
                          <div className="hs-preview-field">
                            <label>Contact</label>
                            <span>{proj?.client_name ? <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}><span>{proj.client_name}</span>{(proj as any).crm_found !== true && <span style={{ display: 'inline-flex', width: 'fit-content', padding: '1px 6px', borderRadius: 8, fontSize: '0.6rem', fontWeight: 600, background: '#fee2e2', color: '#dc2626' }}>Not linked</span>}</div> : '--'}</span>
                          </div>
                          <div className="hs-preview-field">
                            <label>Status</label>
                            <span><span className="hs-status-badge" style={{ background: sc.bg, color: sc.fg, fontSize: '0.72rem' }}>{proj?.status?.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}</span></span>
                          </div>
                          <div className="hs-preview-field">
                            <label>Progress</label>
                            <span>{(String(proj?.pipeline_stage || '') === 'P_COMPLETED' || String(proj?.status || '') === 'completed') ? 100 : (proj?.progress ?? 0)}%</span>
                          </div>
                          <div className="hs-preview-field">
                            <label>Assigned To</label>
                            <span>{proj?.assigned_to || '--'}</span>
                          </div>
                          <div className="hs-preview-field">
                            <label>Address</label>
                            <span>{proj?.address || '--'}</span>
                          </div>
                        </div>

                        <div className="hs-preview-section">
                          <h4>Financials & Dates</h4>
                          <div className="hs-preview-field">
                            <label>Value</label>
                            <span className="hs-preview-money">{proj?.value ? currencyFormatter.format(proj.value) : '--'}</span>
                          </div>
                          <div className="hs-preview-field">
                            <label>Start Date</label>
                            <span>{proj?.start_date ? formatDatePref(proj.start_date) : '--'}</span>
                          </div>
                          <div className="hs-preview-field">
                            <label>End Date</label>
                            <span>{proj?.end_date ? formatDatePref(proj.end_date) : '--'}</span>
                          </div>
                        </div>

                        {proj?.description && (
                          <div className="hs-preview-section">
                            <h4>Description</h4>
                            <p style={{ fontSize: '0.82rem', color: '#516f90', lineHeight: 1.5, margin: 0 }}>{proj.description}</p>
                          </div>
                        )}

                        <div className="hs-preview-section" style={{ textAlign: 'center', paddingTop: 12 }}>
                          <button
                            style={{ padding: '8px 20px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}
                            onClick={() => {
                              closeProjectPreview()
                              setProjectDetailData(proj)
                              setProjectDetailOpen(true)
                              setProjectDetailTab('about')
                              window.location.hash = `/projects/record/${proj?.id}`
                            }}
                          >
                            View full record
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                })()}
              </div>
            </div>
          )}

          {/* ========== Project Record Detail Page (Task 28b) ========== */}
          {activeView === 'projects' && projectDetailOpen && !projectWorkspaceId && !projectDetailData && (
            <div className="crp-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <DotLottieReact src="/loading.lottie" loop autoplay style={{ width: 120, height: 120 }} />
            </div>
          )}
          {activeView === 'projects' && projectDetailOpen && !projectWorkspaceId && projectDetailData && (() => {
            const fmtDate = (d: string | null | undefined) => {
              if (!d) return '--'
              try { return formatDatePref(d) }
              catch { return '--' }
            }
            const getPrjActType = (act: any) => {
              const pt = (act.provider_type || '').toUpperCase()
              if (pt === '2' || pt === 'CALL' || pt.includes('CALL')) return 'Call'
              if (pt === '4' || pt === 'EMAIL' || pt.includes('EMAIL')) return 'Email'
              if (pt === '3' || pt === 'TASK' || pt.includes('TASK')) return 'Task'
              if (pt.includes('MEETING')) return 'Meeting'
              return 'Note'
            }
            const prjActIcon = (type: string) => {
              switch (type) {
                case 'Email': return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                case 'Call': return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.58 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                case 'Task': return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                case 'Meeting': return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                default: return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
              }
            }
            const currentPipelineStage = projectDetailData.pipeline_stage || 'P_CONTRACT_SIGNED'
            const currentPipelineColor = DELIVERY_STAGE_COLORS[currentPipelineStage] || '#94a3b8'
            const currentPipelineLabel = DELIVERY_STAGE_LABELS[currentPipelineStage] || currentPipelineStage
            return (
            <div className="crp-page" data-marker="bp-proj-record-v1">
              {subConfirmDialog && (() => {
                // During a walkthrough the tutorial overlay (#kb-walk-root) sits at
                // z-index 999999 with a dim backdrop, which buries this confirm dialog
                // (.sub-admin-overlay is z-index 10000). Raise ONLY this instance above
                // the tutorial layer so the owner-assign confirm is visible/clickable
                // during a tour. Scoped to walkthrough-active — no effect on the shared
                // overlay elsewhere or on real (non-tutorial) usage.
                const kbWalkActive = typeof window !== 'undefined' && !!(window as any).__kbWalkthroughActive
                return (
                <div className="sub-admin-overlay" style={kbWalkActive ? { zIndex: 1000005 } : undefined} onClick={() => { subConfirmDialog.onCancel?.(); setSubConfirmDialog(null) }}>
                  <div className="sub-admin-dialog" onClick={e => e.stopPropagation()} style={{maxWidth:440}}>
                    <h3>{subConfirmDialog.title}</h3>
                    {subConfirmDialog.content
                      ? <div style={{ marginBottom: 20 }}>{subConfirmDialog.content}</div>
                      : subConfirmDialog.message
                        ? <p style={{fontSize:'0.85rem',color:'#6b7280',marginBottom:20}}>{subConfirmDialog.message}</p>
                        : null}
                    <div style={{display:'flex',justifyContent:'flex-end',gap:8}}>
                      <button onClick={() => { subConfirmDialog.onCancel?.(); setSubConfirmDialog(null) }} style={{padding:'8px 16px',borderRadius:6,border:'1px solid #e5e7eb',background:'#ffffff',cursor:'pointer',fontSize:'0.85rem'}}>Cancel</button>
                      <button onClick={() => { subConfirmDialog.onConfirm(); setSubConfirmDialog(null) }} style={{padding:'8px 16px',borderRadius:6,border:'none',background: subConfirmDialog.destructive ? '#dc2626' : '#2563eb',color:'#fff',cursor:'pointer',fontSize:'0.85rem',fontWeight:600}}>{subConfirmDialog.confirmLabel || (subConfirmDialog.destructive ? 'Delete' : 'Confirm')}</button>
                    </div>
                  </div>
                </div>
                )
              })()}
              {/* ---- LEFT SIDEBAR ---- */}
              <div className="crp-left">
                <div className="crp-left-scroll">
                  <div className="crp-card">
                    <div style={{ padding: '8px 16px 0', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#7c3aed' }}>Project Record</div>
                    <div className="crp-breadcrumb">
                      <button className="crp-back-btn" onClick={() => { setProjectDetailOpen(false); setProjectDetailData(null); window.location.hash = '/projects' }}>
                        <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95"/></svg>
                        <span>Projects</span>
                      </button>
                    </div>
                    <div className="crp-highlight">
                      <div className="crp-avatar-lg" style={{ background: '#e8f5e9', color: '#2e7d32' }}>
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 20a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8l-7-6H4a2 2 0 0 0-2 2Z"/><path d="M14 2v6h6"/></svg>
                      </div>
                      <div className="crp-highlight-info">
                        <h2 className="crp-name">{projectDetailData.name}</h2>
                        <p className="crp-subtitle">
                          <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 600, color: '#fff', background: currentPipelineColor, marginRight: 6 }}>{currentPipelineLabel}</span>
                        </p>
                        {(projectDetailData.contract_value > 0 || projectDetailData.value > 0) && <p className="crp-subtitle" style={{ fontWeight: 600, color: '#33475b' }}>{currencyFormatter.format(projectDetailData.contract_value || projectDetailData.value)}</p>}
                      </div>
                    </div>
                    <div className="crp-action-btns">
                      <button className="crp-action-btn" title="Note" onClick={() => setShowNoteModal({ ownerTypeId: 5, ownerId: projectDetailData.id as any })}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                        <span>Note</span>
                      </button>
                      <button className="crp-action-btn" title="Email" onClick={() => {
                        setComposeContext({
                          to: [],
                          subject: projectDetailData.name ? `Re: ${projectDetailData.name}` : '',
                          entityType: 'project',
                          entityId: projectDetailData.id as any,
                          entityLabel: `Project · ${projectDetailData.name}`,
                        })
                        setComposeOpen(true)
                      }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                        <span>Email</span>
                      </button>
                      <button className="crp-action-btn" title="Task" onClick={() => { window.location.hash = workspacePath(String(projectDetailData.id), 'tasks') }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                        <span>Task</span>
                      </button>
                      <button
                        className="crp-action-btn"
                        title="Open Workspace"
                        onClick={() => { window.location.hash = workspacePath(String(projectDetailData.id)) }}
                        style={{ maxWidth: 120, minWidth: 92, background: '#0891b2', borderColor: '#0891b2', color: '#fff' }}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M7 8h10M7 12h6M7 16h4"/><path d="m16 14 2 2 3-3"/></svg>
                        <span>Open Workspace</span>
                      </button>
                    </div>
                  </div>

                  {/* Key Information card */}
                  <div className="crp-card">
                    <div className="crp-card-header"><h3>Key information</h3></div>
                    <div className="crp-card-body">
                      {/* Deal link hidden — surface this elsewhere if needed. */}
                      {projectDetailData.client_name && (() => {
                        const crmFound = Array.isArray((projectDetailData as any).contacts) && (projectDetailData as any).contacts.some((cc: any) => cc.unified_client_id === projectDetailData.client_id && cc.crm_found)
                        return <div className="crp-field"><label>Contact</label><span style={{ color: crmFound ? '#0091ae' : '#94a3b8', cursor: crmFound ? 'pointer' : 'default' }} title={crmFound ? undefined : 'This contact was not found in the CRM'} onClick={() => {
                          if (!crmFound) return
                          if (projectDetailData.client_id) { fetchClient(projectDetailData.client_id).then(c => { if (applyClientOrRedirect(c)) { setProjectDetailOpen(false); return } setContactDetailOpen(true); setContactDetailTab('about'); setProjectDetailOpen(false); setActiveView('contacts'); window.location.hash = `/contacts/record/${encodeURIComponent(projectDetailData.client_id)}` }).catch(() => alert('Contact not found in the CRM — it may have been deleted.')) }
                        }}>{projectDetailData.client_name}{!crmFound && <span style={{ display: 'inline-flex', marginLeft: 6, padding: '1px 6px', borderRadius: 8, fontSize: '0.6rem', fontWeight: 600, background: '#fee2e2', color: '#dc2626' }}>Not linked</span>}</span></div>
                      })()}
                      <div className="crp-field">
                        <label>Project Lead</label>
                        {projectKeyInfoEditField === 'estimator' ? (
                          <select
                            autoFocus
                            value={projectDetailData.estimator_id ?? ''}
                            onChange={async (e) => {
                              const val = e.target.value
                              const newId = val === '' ? null : Number(val)
                              setProjectKeyInfoEditField(null)
                              try {
                                await updateCrmProject(String(projectDetailData.id), { estimator_id: newId } as any)
                                const fresh: any = await fetchCrmProject(String(projectDetailData.id))
                                setProjectDetailData(fresh)
                              } catch (err: any) { alert(err?.message || 'Failed to update estimator') }
                            }}
                            onBlur={() => setProjectKeyInfoEditField(null)}
                            style={{ padding: '3px 8px', fontSize: '0.82rem', borderRadius: 4, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer' }}
                          >
                            <option value="">— Unassigned —</option>
                            {(crmUsers || []).map((u: any) => (
                              <option key={u.id} value={u.id}>{u.name}</option>
                            ))}
                          </select>
                        ) : (
                          <div className="crp-field-editable" onClick={() => setProjectKeyInfoEditField('estimator')}>
                            <span>{projectDetailData.estimator_name || '--'}</span>
                            <svg className="crp-field-edit-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </div>
                        )}
                      </div>
                      <div className="crp-field">
                        <label>Start Date</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                          <span>{fmtDate(projectDetailData.start_date)}</span>
                          {(projectDetailData as any).pipeline_stage === 'P_ON_HOLD' && (projectDetailData as any).hold_type && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 8px', borderRadius: 8, background: '#f5f5f5', color: '#64748b', fontSize: '0.75rem', fontWeight: 600 }} title={(projectDetailData as any).hold_reason ? `${(projectDetailData as any).hold_type}: ${(projectDetailData as any).hold_reason}` : (projectDetailData as any).hold_type}>
                              {HOLD_TYPES.find(ht => ht.value === (projectDetailData as any).hold_type)?.label || (projectDetailData as any).hold_type}
                              {(projectDetailData as any).hold_reason && ` — ${(projectDetailData as any).hold_reason.substring(0, 40)}${(projectDetailData as any).hold_reason.length > 40 ? '…' : ''}`}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="crp-field"><label>End Date</label><span>{fmtDate(projectDetailData.end_date)}</span></div>
                      <div className="crp-field">
                        <label>Financials</label>
                        {(() => {
                          const contractValue = Number(projectDetailData.contract_value || projectDetailData.value || 0)
                          const invoiceTypes = new Set(['ar_invoice', 'statement', 'sales_receipt', 'credit_memo'])
                          const paymentTypes = new Set(['customer_payment'])
                          const invoiced = projectFinancialDocs
                            .filter(doc => invoiceTypes.has(String(doc.doc_type)))
                            .reduce((sum, doc) => sum + Number(doc.amount ?? 0), 0)
                          const paid = projectFinancialDocs
                            .filter(doc => paymentTypes.has(String(doc.doc_type)))
                            .reduce((sum, doc) => sum + Number(doc.amount ?? 0), 0)
                          const outstanding = invoiced - paid
                          const tiles = [
                            { label: 'Contract Value', value: contractValue },
                            { label: 'Invoiced', value: invoiced },
                            { label: 'Paid', value: paid },
                            { label: 'Outstanding', value: outstanding },
                          ]
                          return (
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(2, 1fr)',
                              gap: 4,
                              marginTop: 4,
                            }}>
                              {tiles.map(tile => (
                                <div key={tile.label} style={{ padding: '5px 8px', borderRadius: 6, background: '#f8fafc', border: '1px solid #cbd5e1' }}>
                                  <div style={{ fontSize: '0.62rem', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>{tile.label}</div>
                                  <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#33475b' }}>
                                    {projectFinancialsLoading ? 'Loading…' : currencyFormatter.format(tile.value)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )
                        })()}
                      </div>
                    </div>
                  </div>
                  {/* ===== Entity 360 (P4) — ADDITIVE relationship graph + rollups =====
                      Appends crp-cards at the bottom of the project record's left scroll.
                      Self-contained leaf: owns its own fetch/loading/error state, re-keyed
                      on the project id. On error/empty it renders NOTHING, so the existing
                      project record (record card, financial tiles, etc.) is never blocked.
                      Gated behind FLAG_ENTITY360. Same fail-safe contract as the org/deal
                      record. Purely additive — removing reverts cleanly. */}
                  {FLAG_ENTITY360 && projectDetailData?.id != null && (
                    <Suspense fallback={<div className="view-loading" />}>
                      <Entity360 entityType="project" id={String(projectDetailData.id)} />
                    </Suspense>
                  )}
                </div>
              </div>

              {/* ---- MIDDLE PANE ---- */}
              <div className="crp-middle">
                {/* Stage Progression Bar (Task 28c) */}
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #eef0f3', background: '#fafbfc' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                    {DELIVERY_PIPELINE_STAGES.map((stage, idx) => {
                      const isActive = currentPipelineStage === stage.id
                      const currentIdx = DELIVERY_PIPELINE_STAGES.findIndex(s => s.id === currentPipelineStage)
                      const isPast = currentIdx >= 0 && idx < currentIdx
                      const isTerminal = currentPipelineStage === 'P_ON_HOLD' || currentPipelineStage === 'P_CANCELLED'
                      return (
                        <div key={stage.id} style={{ flex: 1, textAlign: 'center', cursor: prjStageUpdating ? 'wait' : 'pointer', position: 'relative' }} title={`Move to ${stage.label}`}
                          onClick={() => {
                            if (prjStageUpdating || isActive) return
                            setPrjStageUpdating(true)
                            updateProjectStage(projectDetailData.id, stage.id).then(res => {
                              setProjectDetailData((prev: any) => ({ ...prev, pipeline_stage: res.newStage || stage.id }))
                            }).catch(() => {}).finally(() => setPrjStageUpdating(false))
                          }}>
                          <div style={{ height: 6, background: isActive ? stage.color : isPast && !isTerminal ? stage.color : '#e5e7eb', borderRadius: idx === 0 ? '3px 0 0 3px' : idx === DELIVERY_PIPELINE_STAGES.length - 1 ? '0 3px 3px 0' : 0, opacity: isPast ? 0.6 : 1, transition: 'background 0.2s' }} />
                          <div style={{ fontSize: '0.68rem', color: isActive ? stage.color : '#7c98b6', fontWeight: isActive ? 700 : 400, marginTop: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{stage.label}</div>
                        </div>
                      )
                    })}
                  </div>
                  {/* Terminal states */}
                  {(currentPipelineStage === 'P_ON_HOLD' || currentPipelineStage === 'P_CANCELLED') && (
                    <div style={{ marginTop: 6, fontSize: '0.78rem', fontWeight: 600, color: DELIVERY_STAGE_COLORS[currentPipelineStage], textAlign: 'center' }}>
                      {DELIVERY_STAGE_LABELS[currentPipelineStage]}
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 6 }}>
                    {currentPipelineStage !== 'P_ON_HOLD' && (
                      <button style={{ padding: '3px 10px', fontSize: '0.72rem', border: '1px solid #9e9e9e', borderRadius: 4, background: currentPipelineStage === 'P_ON_HOLD' ? '#9e9e9e' : '#fff', color: currentPipelineStage === 'P_ON_HOLD' ? '#fff' : '#9e9e9e', cursor: 'pointer', fontWeight: 500 }} onClick={() => {
                        // Show hold modal instead of directly updating stage
                        setProjectHoldModal({ projectId: projectDetailData.id })
                        setProjectHoldType('')
                        setProjectHoldReason('')
                      }}>On Hold</button>
                    )}
                    {currentPipelineStage !== 'P_CANCELLED' && (
                      <button style={{ padding: '3px 10px', fontSize: '0.72rem', border: '1px solid #d32f2f', borderRadius: 4, background: '#fff', color: '#d32f2f', cursor: 'pointer', fontWeight: 500 }} onClick={() => {
                        if (prjStageUpdating) return
                        setPrjStageUpdating(true)
                        updateProjectStage(projectDetailData.id, 'P_CANCELLED').then(res => { setProjectDetailData((prev: any) => ({ ...prev, pipeline_stage: 'P_CANCELLED' })) }).catch(() => {}).finally(() => setPrjStageUpdating(false))
                      }}>Cancel</button>
                    )}
                  </div>
                </div>

                <div className="hs-topbar" style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
                  <div className="hs-topbar-left">
                    <div className="hs-view-tabs">
                      <div className={`hs-view-tab${projectDetailTab === 'about' ? ' active' : ''}`} onClick={() => setProjectDetailTab('about')}>
                        <span className="hs-tab-label">About</span>
                      </div>
                      <div className={`hs-view-tab${projectDetailTab === 'activities' ? ' active' : ''}`} onClick={() => setProjectDetailTab('activities')}>
                        <span className="hs-tab-label">Activities</span>
                      </div>
                      <div className={`hs-view-tab${projectDetailTab === 'revenue' ? ' active' : ''}`} onClick={() => { setProjectDetailTab('revenue'); if (projectDetailData?.id) window.location.hash = `/projects/record/${projectDetailData.id}/revenue` }}>
                        <span className="hs-tab-label">Revenue</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="crp-middle-scroll">
                  {projectDetailTab === 'about' && (
                    <Suspense fallback={PrefsLazyFallback}>
                      <ProjectDetailAbout ctx={{ AddressInput: AddressInput, currencyFormatter: currencyFormatter, DELIVERY_PIPELINE_STAGES: DELIVERY_PIPELINE_STAGES, DELIVERY_STAGE_COLORS: DELIVERY_STAGE_COLORS, DELIVERY_STAGE_LABELS: DELIVERY_STAGE_LABELS, fmtDate: fmtDate, currentPipelineStage: currentPipelineStage, currentPipelineColor: currentPipelineColor, currentPipelineLabel: currentPipelineLabel, projectDetailData: projectDetailData, setProjectDetailData: setProjectDetailData, projectDetailTab: projectDetailTab, prjAssignableUsers: prjAssignableUsers, setPrjAssignableUsers: setPrjAssignableUsers, prjDateInputRef: prjDateInputRef, prjEditingField: prjEditingField, setPrjEditingField: setPrjEditingField, prjEditingValue: prjEditingValue, setPrjEditingValue: setPrjEditingValue, prjNotesEditing: prjNotesEditing, setPrjNotesEditing: setPrjNotesEditing, prjNotesValue: prjNotesValue, setPrjNotesValue: setPrjNotesValue, prjOwnerBtnRef: prjOwnerBtnRef, prjOwnerPending: prjOwnerPending, setPrjOwnerPending: setPrjOwnerPending, prjOwnerPickerOpen: prjOwnerPickerOpen, setPrjOwnerPickerOpen: setPrjOwnerPickerOpen, prjSeniorBtnRef: prjSeniorBtnRef, prjSeniorPickerOpen: prjSeniorPickerOpen, setPrjSeniorPickerOpen: setPrjSeniorPickerOpen, prjStaffBtnRef: prjStaffBtnRef, prjStaffPickerOpen: prjStaffPickerOpen, setPrjStaffPickerOpen: setPrjStaffPickerOpen, prjPendingChanges: prjPendingChanges, setPrjPendingChanges: setPrjPendingChanges, prjProjectCompanyId: prjProjectCompanyId, setPrjProjectCompanyId: setPrjProjectCompanyId, prjProjectCompanyName: prjProjectCompanyName, setPrjProjectCompanyName: setPrjProjectCompanyName, projectAddrExpandedId: projectAddrExpandedId, setProjectAddrExpandedId: setProjectAddrExpandedId, projectAddrImportOpen: projectAddrImportOpen, setProjectAddrImportOpen: setProjectAddrImportOpen, projectAddrImportSelected: projectAddrImportSelected, setProjectAddrImportSelected: setProjectAddrImportSelected, projectAddresses: projectAddresses, setProjectAddresses: setProjectAddresses, projectNewAddrForm: projectNewAddrForm, projectNewAddrFormOpen: projectNewAddrFormOpen, setProjectNewAddrForm: setProjectNewAddrForm, setProjectNewAddrFormOpen: setProjectNewAddrFormOpen, setProjectDetailRefreshTick: setProjectDetailRefreshTick, setProjectStagePicker: setProjectStagePicker, setProjectStagePickerPos: setProjectStagePickerPos, setProjectsData: setProjectsData, setSubChatModalOpen: setSubChatModalOpen, setSubConfirmDialog: setSubConfirmDialog, setSubPortalProjectId: setSubPortalProjectId, setSubProjectChat: setSubProjectChat, myCapabilities: myCapabilities, myRole: myRole, subAdminMe: subAdminMe, subAdminUsers: subAdminUsers, buildOptimisticOwnersMembers: buildOptimisticOwnersMembers, computeProjectOwnerGate: computeProjectOwnerGate, fetchCrmProject: fetchCrmProject, fetchSubAdminAssignableUsers: fetchSubAdminAssignableUsers, fetchSubAdminProjectChat: fetchSubAdminProjectChat, isOwnerSelectedFn: isOwnerSelectedFn, sameOwnerIdSet: sameOwnerIdSet, shouldUseOptimisticOwnerUpdate: shouldUseOptimisticOwnerUpdate, toggleOwnerPendingIds: toggleOwnerPendingIds, updateSubAdminProjectOwners: updateSubAdminProjectOwners, updateSubAdminProjectSeniors: updateSubAdminProjectSeniors, updateSubAdminProjectStaff: updateSubAdminProjectStaff }} />
                    </Suspense>
                  )}
                  {projectDetailTab === 'activities' && (() => {
                    const filteredPrjActs = projectDetailActivities.filter(act => {
                      const t = getPrjActType(act).toLowerCase()
                      if (prjActivityFilter === 'calls' && t !== 'call') return false
                      if (prjActivityFilter === 'emails' && t !== 'email') return false
                      if (prjActivityFilter === 'tasks' && t !== 'task') return false
                      if (prjActivityFilter === 'meetings' && t !== 'meeting') return false
                      if (prjActivityFilter === 'notes' && t !== 'note') return false
                      if (prjActivitySearch) {
                        const q = prjActivitySearch.toLowerCase()
                        return (act.subject || '').toLowerCase().includes(q) || (act.description || '').toLowerCase().includes(q)
                      }
                      return true
                    })
                    const prjActGroups: { label: string; items: typeof filteredPrjActs }[] = []
                    let lastPrjMonth = ''
                    for (const act of filteredPrjActs) {
                      const d = act.created ? new Date(act.created) : null
                      const ml = d ? d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'Unknown'
                      if (ml !== lastPrjMonth) { prjActGroups.push({ label: ml, items: [] }); lastPrjMonth = ml }
                      prjActGroups[prjActGroups.length - 1].items.push(act)
                    }
                    return (
                      <div className="crp-card" style={{ overflow: 'visible' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderBottom: '1px solid #eef0f3' }}>
                          <div className="hs-search-wrap" style={{ flex: 1 }}>
                            <svg className="hs-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                            <input className="hs-search-input" placeholder="Search activities" value={prjActivitySearch} onChange={e => setPrjActivitySearch(e.target.value)} />
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #eef0f3', padding: '0 16px' }}>
                          {([['all', 'Activity'], ['notes', 'Notes'], ['emails', 'Emails'], ['calls', 'Calls'], ['tasks', 'Tasks'], ['meetings', 'Meetings']] as const).map(([key, label]) => (
                            <button key={key} onClick={() => setPrjActivityFilter(key)} style={{ padding: '10px 14px', fontSize: '0.82rem', fontWeight: prjActivityFilter === key ? 600 : 400, color: prjActivityFilter === key ? '#1976d2' : '#516f90', background: 'none', border: 'none', cursor: 'pointer', borderBottom: prjActivityFilter === key ? '2px solid #1976d2' : '2px solid transparent', marginBottom: -1 }}>{label}</button>
                          ))}
                        </div>
                        <div className="crp-card-body" style={{ padding: '0 20px 20px' }}>
                          {projectDetailLoading && <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 20px' }}><DotLottieReact src="/loading.lottie" loop autoplay style={{ width: 80, height: 80 }} /><span style={{ fontSize: '0.82rem', color: '#7c98b6', marginTop: 8 }}>Loading activities...</span></div>}
                          {projectDetailData?.id && (prjActivityFilter === 'all' || prjActivityFilter === 'emails') && (
                            <LinkedEmailsSection entityType="project" entityId={projectDetailData.id} hideIfEmpty />
                          )}
                          {!projectDetailLoading && filteredPrjActs.length === 0 && (
                            <div style={{ padding: 20, color: '#7c98b6', textAlign: 'center' }}>No activities found{prjActivityFilter !== 'all' ? ` for "${prjActivityFilter}"` : ''}.</div>
                          )}
                          {prjActGroups.map(group => (
                            <div key={group.label}>
                              <div style={{ padding: '16px 0 8px', fontSize: '0.82rem', fontWeight: 700, color: '#33475b' }}>{group.label}</div>
                              {group.items.map(act => {
                                const t = getPrjActType(act)
                                // Gold-standard call card -- mirrors the Deal Activities tab's
                                // crp-activity-item block (outcome pill, direction+counterparty
                                // row, handled-by). Non-call types keep the pre-existing render
                                // below untouched.
                                if (t === 'Call') {
                                  const isMissed = ((act as any).settings || '').includes('MISSED_CALL')
                                  const callMeta = formatCallTitle(act as any, { isMissed })
                                  const typeLabel = `${callMeta.directionLabel} ${callMeta.directionArrow}`
                                  const iconColor = callMeta.directionColor || '#1976d2'
                                  const expanded = prjExpandedCards.has(act.id)
                                  return (
                                    <div key={act.id} className="crp-activity-item" style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 0, margin: '0 0 10px', background: '#fff', boxShadow: '0 1px 2px rgba(15, 23, 42, 0.03)' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: expanded ? '1px solid #f0f2f5' : 'none', cursor: 'pointer' }} onClick={() => setPrjExpandedCards(prev => { const n = new Set(prev); n.has(act.id) ? n.delete(act.id) : n.add(act.id); return n })}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#7c98b6" strokeWidth="2.5" style={{ flexShrink: 0, transition: 'transform 0.15s', transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}><path d="M9 18l6-6-6-6"/></svg>
                                          <div className="crp-activity-icon" style={{ margin: 0, width: 28, height: 28 }}>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.81.36 1.6.7 2.35a2 2 0 0 1-.45 2.11L8.09 9.45a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.75.34 1.54.57 2.35.7A2 2 0 0 1 22 16.92z"/></svg>
                                          </div>
                                          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, gap: 2 }}>
                                            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: act.status === 6 ? '#9ca3af' : '#0f172a', textDecoration: act.status === 6 ? 'line-through' : 'none', display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                              <span>{typeLabel}</span>
                                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 9px', borderRadius: 999, background: callMeta.outcomeBg, color: callMeta.outcomeColor, fontSize: '0.72rem', fontWeight: 700, letterSpacing: 0.1 }}>
                                                <span style={{ width: 6, height: 6, borderRadius: '50%', background: callMeta.outcomeColor, display: 'inline-block' }} />
                                                {callMeta.outcome}
                                              </span>
                                              {callMeta.durationLabel && (
                                                <span style={{ fontSize: '0.76rem', fontWeight: 500, color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>{callMeta.durationLabel}</span>
                                              )}
                                              {act.status === 6 && (
                                                <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '2px 8px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Canceled</span>
                                              )}
                                            </span>
                                            {(callMeta.partyPhone || callMeta.handledBy) && (
                                              <span style={{ fontSize: '0.75rem', color: '#64748b', display: 'inline-flex', alignItems: 'center', gap: 6, fontVariantNumeric: 'tabular-nums' }}>
                                                {callMeta.partyPhone && (
                                                  <span>{callMeta.partyDirection} {callMeta.partyPhone}</span>
                                                )}
                                                {callMeta.partyPhone && callMeta.handledBy && <span style={{ color: '#cbd5e1' }}>•</span>}
                                                {callMeta.handledBy && (
                                                  <span>handled by <span style={{ color: '#334155', fontWeight: 500 }}>{callMeta.handledBy}</span></span>
                                                )}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                          {act.from_entity && act.from_entity !== 'project' && (
                                            <span style={{ display: 'inline-block', padding: '1px 6px', borderRadius: 8, fontSize: '0.68rem', fontWeight: 500, color: act.from_entity === 'lead' ? '#7b1fa2' : '#1976d2', background: act.from_entity === 'lead' ? '#f3e5f5' : '#e3f2fd', whiteSpace: 'nowrap' }}>from {act.from_entity}</span>
                                          )}
                                          <span style={{ fontSize: '0.75rem', color: '#7c98b6', whiteSpace: 'nowrap' }}>
                                            {act.created ? fmtDate(act.created) : ''}
                                          </span>
                                        </div>
                                      </div>
                                      {expanded && (
                                        <div style={{ padding: '10px 14px' }}>
                                          {act.summary && <div style={{ fontSize: '0.82rem', color: '#516f90' }}>{act.summary}</div>}
                                          {!act.summary && act.description && <div style={{ fontSize: '0.82rem', color: '#516f90', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{act.description}</div>}
                                          {!act.summary && !act.description && <div style={{ fontSize: '0.82rem', color: '#7c98b6' }}>No additional details</div>}
                                        </div>
                                      )}
                                    </div>
                                  )
                                }
                                return (
                                  <div key={act.id} style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: 0, margin: '0 0 8px', background: '#fff' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: prjExpandedCards.has(act.id) ? '1px solid #f0f2f5' : 'none', cursor: 'pointer' }} onClick={() => setPrjExpandedCards(prev => { const n = new Set(prev); n.has(act.id) ? n.delete(act.id) : n.add(act.id); return n })}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#7c98b6" strokeWidth="2.5" style={{ flexShrink: 0, transition: 'transform 0.15s', transform: prjExpandedCards.has(act.id) ? 'rotate(90deg)' : 'rotate(0deg)' }}><path d="M9 18l6-6-6-6"/></svg>
                                        <div className="crp-activity-icon" style={{ margin: 0, width: 28, height: 28 }}>
                                          {prjActIcon(t)}
                                        </div>
                                        <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#33475b' }}>{t}</span>
                                        {!prjExpandedCards.has(act.id) && act.subject && (
                                          <span style={{ fontSize: '0.78rem', color: '#516f90', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300 }}>{act.subject}</span>
                                        )}
                                      </div>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        {act.from_entity && act.from_entity !== 'project' && (
                                          <span style={{ display: 'inline-block', padding: '1px 6px', borderRadius: 8, fontSize: '0.68rem', fontWeight: 500, color: act.from_entity === 'lead' ? '#7b1fa2' : '#1976d2', background: act.from_entity === 'lead' ? '#f3e5f5' : '#e3f2fd', whiteSpace: 'nowrap' }}>from {act.from_entity}</span>
                                        )}
                                        <span style={{ fontSize: '0.75rem', color: '#7c98b6', whiteSpace: 'nowrap' }}>
                                          {act.created ? fmtDate(act.created) : ''}
                                        </span>
                                      </div>
                                    </div>
                                    {prjExpandedCards.has(act.id) && (
                                      <div style={{ padding: '10px 14px' }}>
                                        {act.subject && <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#33475b', marginBottom: 6 }}>{act.subject}</div>}
                                        {act.description && <div style={{ fontSize: '0.82rem', color: '#516f90', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{act.description}</div>}
                                        {act.summary && <div style={{ fontSize: '0.82rem', color: '#516f90', marginTop: 4 }}>{act.summary}</div>}
                                        {!act.subject && !act.description && !act.summary && <div style={{ fontSize: '0.82rem', color: '#7c98b6' }}>No additional details</div>}
                                      </div>
                                    )}
                                  </div>
                                )
                              })}
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  })()}
                  {projectDetailTab === 'revenue' && projectDetailData?.id && (
                    <>
                      <FinancialsCards source={{ kind: 'project', subProjectId: projectDetailData.id }} />
                      {/* Attachments card — direct uploads + deal-activity files for this project. */}
                      <div className="crp-card">
                        <div className="crp-card-header"><h3>Attachments ({projectAttachments.length})</h3></div>
                        <div className="crp-card-body" style={{ padding: 0 }}>
                          {projectAttachmentsLoading ? (
                            <div className="crp-empty-card">Loading…</div>
                          ) : projectAttachments.length === 0 ? (
                            <div className="crp-empty-card">No attachments yet.</div>
                          ) : (
                            <table className="crp-mini-table">
                              <thead><tr>
                                <th>File</th>
                                <th>Source</th>
                                <th>Uploaded by</th>
                                <th>Date</th>
                              </tr></thead>
                              <tbody>
                                {projectAttachments.map((att: any) => (
                                  <tr key={String(att.id)}>
                                    <td>
                                      {att.download_url ? (
                                        <a href={att.download_url} target="_blank" rel="noopener noreferrer">{att.file_name || '--'}</a>
                                      ) : (att.file_name || '--')}
                                    </td>
                                    <td style={{ fontSize: '0.78rem', color: '#64748b' }}>{att.source === 'direct' ? 'Direct upload' : att.source === 'portal' ? 'Portal Files' : att.source === 'legacy' ? 'Legacy Documents' : att.source === 'note' ? 'Note' : att.source === 'email' ? 'Email' : (att.source || '--')}</td>
                                    <td style={{ fontSize: '0.78rem' }}>{att.uploader_name || '--'}</td>
                                    <td style={{ fontSize: '0.78rem' }}>{att.created_at ? formatDatePref(att.created_at) : '--'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* ---- RIGHT SIDEBAR ---- */}
              <div className="crp-right">
                <div className="crp-right-scroll">
                  {/* Contact card — merged: project's direct contact + deal's contacts */}
                  {(() => {
                    // Prefer the API-provided merged list; fall back to client_name
                    // for older payloads (defensive, never crashes the page).
                    const apiContacts = Array.isArray((projectDetailData as any).contacts)
                      ? (projectDetailData as any).contacts as Array<{
                          unified_client_id: string | null
                          preferred_name: string | null
                          preferred_email: string | null
                          preferred_phone: string | null
                          preferred_street?: string | null
                          preferred_city?: string | null
                          preferred_state?: string | null
                          role: string
                          source: 'project' | 'deal'
                        }>
                      : []
                    const fallback = apiContacts.length === 0 && projectDetailData.client_name
                      ? [{
                          unified_client_id: projectDetailData.client_id || null,
                          preferred_name: projectDetailData.client_name,
                          preferred_email: prjLinkedClientCache?.preferred_email ?? null,
                          preferred_phone: prjLinkedClientCache?.preferred_phone ?? null,
                          preferred_street: prjLinkedClientCache?.preferred_street ?? null,
                          preferred_city: prjLinkedClientCache?.preferred_city ?? null,
                          preferred_state: prjLinkedClientCache?.preferred_state ?? null,
                          role: 'primary',
                          source: 'project' as const,
                        }]
                      : []
                    const contactsList = apiContacts.length > 0 ? apiContacts : fallback
                    const hasDirect = !!projectDetailData.client_name
                    return (
                      <div className="crp-card">
                        <div className="crp-card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <h3>Contacts ({contactsList.length})</h3>
                          <button
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.78rem', color: '#0091ae', fontWeight: 500, padding: '2px 6px' }}
                            onClick={() => { setPrjContactSearch(''); setPrjContactResults([]); setPrjContactPickerOpen(true) }}
                          >
                            {hasDirect ? 'Change' : '+ Link'}
                          </button>
                        </div>
                        <div className="crp-card-body">
                          {contactsList.length > 0 ? contactsList.map((pc, idx) => {
                            const isDeal = pc.source === 'deal'
                            const subtitle = isDeal ? 'Inherited from deal' : 'Associated contact'
                            const crmFound = (pc as any).crm_found === true
                            return (
                              <div key={`${pc.source}-${pc.unified_client_id || idx}`} className="crp-assoc-item" style={{ cursor: crmFound ? 'pointer' : 'default', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ flex: 1, cursor: crmFound ? 'pointer' : 'default', minWidth: 0 }} onClick={() => {
                                  if (!crmFound) return
                                  if (pc.unified_client_id) {
                                    fetchClient(pc.unified_client_id).then(c => { if (applyClientOrRedirect(c)) { setProjectDetailOpen(false); return } setContactDetailOpen(true); setContactDetailTab('about'); setProjectDetailOpen(false); setActiveView('contacts'); window.location.hash = `/contacts/record/${encodeURIComponent(pc.unified_client_id!)}` }).catch(() => alert('Contact not found in the CRM — it may have been deleted.'))
                                  }
                                }}>
                                  <div className="crp-assoc-title" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
                                    {pc.preferred_name || pc.unified_client_id || 'Unknown'}
                                    {(pc as any).archived_at && <span style={{ display: 'inline-flex', padding: '1px 5px', borderRadius: 8, fontSize: '0.58rem', fontWeight: 600, background: '#fef3c7', color: '#92400e', flexShrink: 0 }}>Archived</span>}
                                    {!crmFound && <span style={{ display: 'inline-flex', padding: '1px 5px', borderRadius: 8, fontSize: '0.58rem', fontWeight: 600, background: '#fee2e2', color: '#dc2626', flexShrink: 0 }} title="This contact was not found in the CRM">Not found</span>}
                                  </div>
                                  <div className="crp-assoc-sub" style={{ fontSize: '0.72rem', display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {pc.preferred_phone && <span>{pc.preferred_phone}</span>}
                                    {pc.preferred_email && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pc.preferred_email}</span>}
                                    {!pc.preferred_phone && !pc.preferred_email && <span>{crmFound ? subtitle : 'Contact missing from CRM'}</span>}
                                  </div>
                                  {pc.preferred_street && <div style={{ fontSize: '0.7rem', color: '#7c98b6', marginTop: 2 }}>{[pc.preferred_street, pc.preferred_city, pc.preferred_state].filter(Boolean).join(', ')}</div>}
                                </div>
                                {!isDeal && (
                                  <button
                                    title="Unlink contact"
                                    onClick={async (e) => {
                                      e.stopPropagation()
                                      if (!confirm('Unlink this contact from the project?')) return
                                      try {
                                        await updateCrmProject(String(projectDetailData.id), { client_id: null, client_name: null })
                                        const fresh: any = await fetchCrmProject(String(projectDetailData.id))
                                        setProjectDetailData(fresh)
                                      } catch (err: any) { alert(err?.message || 'Failed to unlink contact') }
                                    }}
                                    style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '1rem', padding: 4 }}
                                  >&times;</button>
                                )}
                              </div>
                            )
                          }) : (
                            <div className="crp-empty-card">No contacts linked to this project or its deal.</div>
                          )}
                        </div>
                      </div>
                    )
                  })()}

                  {/* Deal card */}
                  <div className="crp-card">
                    <div className="crp-card-header"><h3>Deal {projectDetailData.deal_id ? '(1)' : '(0)'}</h3></div>
                    <div className="crp-card-body">
                      {projectDetailData.deal_id ? (
                        <div className="crp-assoc-item" style={{ cursor: 'pointer' }} onClick={() => {
                          fetchDeal(projectDetailData.deal_id).then(d => { setProjectDetailOpen(false); setSelectedDeal(d); setDealDetailOpen(true); setActiveView('deals'); window.location.hash = `/deals/record/${d.id}` }).catch(() => {})
                        }}>
                          <div className="crp-assoc-title">{prjLinkedDeal?.title || `Deal #${projectDetailData.deal_id}`}</div>
                          <div className="crp-assoc-sub">{prjLinkedDeal?.stageLabel || prjLinkedDeal?.stage || 'Associated deal'}</div>
                          {prjLinkedDeal?.opportunity > 0 && <div className="crp-assoc-amount">${prjLinkedDeal.opportunity.toLocaleString()}</div>}
                        </div>
                      ) : (
                        <div className="crp-empty-card">No deal linked to this project.</div>
                      )}
                    </div>
                  </div>

                  {/* Tickets (Task 16) */}
                  <div className="crp-card">
                    <div className="crp-card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <h3>Tickets ({projectTickets.length})</h3>
                      <button
                        onClick={() => {
                          const pContact = projectDetailData.contact_id || projectDetailData.client_id
                          setTicketPlusChoice({
                            kind: 'project',
                            id: String(projectDetailData.id),
                            name: projectDetailData.name,
                            createPayload: pContact ? {
                              contact: { unified_client_id: pContact },
                              deal: projectDetailData.deal_id ? { id: projectDetailData.deal_id, title: projectDetailData.deal_title || prjLinkedDeal?.title || 'Deal', unified_client_id: pContact } : undefined,
                              project: { id: String(projectDetailData.id), name: projectDetailData.name, deal_id: projectDetailData.deal_id },
                            } : null,
                          })
                        }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.78rem', color: '#0091ae', fontWeight: 500, padding: '2px 6px' }}
                      >+</button>
                    </div>
                    <div className="crp-card-body">
                      {projectTickets.length === 0 ? (
                        <div className="crp-empty-card">No tickets</div>
                      ) : projectTickets.map((t: any) => {
                        const isOverdue = t.due_date && new Date(t.due_date) < new Date() && t.status !== 'resolved' && t.status !== 'closed'
                        return (
                          <div
                            key={t.id}
                            id={`ticket-${t.id}`}
                            style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}
                          >
                            <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#1e293b' }}>{t.title}</div>
                            <div style={{ display: 'flex', gap: 8, fontSize: '0.72rem', color: '#7c98b6', marginTop: 2 }}>
                              <span>{t.status}</span>
                              {t.due_date && <span>Due {new Date(t.due_date).toLocaleDateString()}</span>}
                              {isOverdue && <span style={{ color: '#dc2626', fontWeight: 600 }}>Overdue</span>}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>



                  {/* Project Docs (admin-authored loose files) */}
                  <div className="crp-card">
                    <div className="crp-card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <h3>Project Docs {prjAdminDocs.length > 0 ? `(${prjAdminDocs.length})` : ''}</h3>
                      <button
                        onClick={() => {
                          setSubConfirmDialog({
                            title: 'Visible to Subcontractors',
                            message: 'Files uploaded here will be viewable by any subcontractor assigned to this project.',
                            confirmLabel: 'Continue',
                            onConfirm: () => {
                              setPrjDocsUploadFiles([])
                              setPrjDocsUploadProgress({})
                              setPrjDocsUploading(false)
                              setPrjDocsUploadOpen(true)
                            },
                          })
                        }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.78rem', color: '#0091ae', fontWeight: 600, padding: '2px 6px' }}
                      >+ Upload</button>
                    </div>
                    <div className="crp-card-body">
                      {prjAdminDocs.length === 0 ? (
                        <div className="crp-empty-card" style={{ fontSize: '0.74rem' }}>Proposals, SOWs, specs, reference docs…</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {prjAdminDocs.map((f: any) => renderPrjFileRow(f, () => reloadPrjFiles(projectDetailData.id)))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Project Docs Upload Modal */}
                  {prjDocsUploadOpen && (
                    <div className="bp-upload-overlay" role="dialog" aria-modal="true" aria-label="Upload project documents" onClick={(e) => { if (e.target === e.currentTarget && !prjDocsUploading) setPrjDocsUploadOpen(false) }}>
                      <div className="bp-upload-modal bp-upload-modal-compact">
                        <div className="bp-upload-header">
                          <div>
                            <h2 className="bp-upload-title">{prjDocsUploadFiles.length > 0 ? `Upload ${prjDocsUploadFiles.length} file${prjDocsUploadFiles.length === 1 ? '' : 's'}` : 'Upload Project Docs'}</h2>
                            <div className="bp-upload-subtitle">Select files to upload to this project.</div>
                          </div>
                          <button type="button" className="bp-upload-close" onClick={() => { if (!prjDocsUploading) setPrjDocsUploadOpen(false) }} disabled={prjDocsUploading} aria-label="Close">&times;</button>
                        </div>

                        <div className="bp-upload-body">
                          {/* Drop zone */}
                          <div
                            onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = '#0091ae'; e.currentTarget.style.background = '#f0f9ff' }}
                            onDragLeave={(e) => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.background = '#f8fafc' }}
                            onDrop={(e) => {
                              e.preventDefault()
                              e.currentTarget.style.borderColor = '#cbd5e1'
                              e.currentTarget.style.background = '#f8fafc'
                              const dropped = Array.from(e.dataTransfer.files)
                              if (dropped.length) setPrjDocsUploadFiles(prev => [...prev, ...dropped])
                            }}
                            onClick={() => prjAdminDocsInputRef.current?.click()}
                            style={{
                              border: '2px dashed #cbd5e1', borderRadius: 10, padding: '24px 16px', textAlign: 'center', cursor: 'pointer',
                              background: '#f8fafc', transition: 'border-color 150ms, background 150ms', marginBottom: prjDocsUploadFiles.length > 0 ? 12 : 0,
                            }}
                          >
                            <div style={{ fontSize: '1.6rem', marginBottom: 4 }}>
                              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                            </div>
                            <div style={{ fontSize: '0.82rem', color: '#475569', fontWeight: 500 }}>Drop files here or click to browse</div>
                            <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 2 }}>Proposals, SOWs, specs, reference docs…</div>
                          </div>
                          <input
                            ref={prjAdminDocsInputRef}
                            type="file"
                            multiple
                            style={{ display: 'none' }}
                            onChange={(e) => {
                              const picked = Array.from(e.target.files || [])
                              if (picked.length) setPrjDocsUploadFiles(prev => [...prev, ...picked])
                              if (prjAdminDocsInputRef.current) prjAdminDocsInputRef.current.value = ''
                            }}
                          />

                          {/* Mandatory document-type picker */}
                          <div style={{ marginTop: prjDocsUploadFiles.length > 0 ? 0 : 12, marginBottom: 12 }}>
                            <span className="upload-modal__field-label">Document type <span style={{ color: '#dc2626' }}>*</span></span>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              {[
                                { value: 'Work Order', label: 'SOW', tone: 'amber' as const },
                                { value: 'Work Order Related', label: 'Project docs', tone: 'teal' as const },
                              ].map(t => (
                                <button
                                  key={t.value}
                                  type="button"
                                  className={`upload-modal__type-chip${prjDocsUploadType === t.value ? ` is-active is-active--${t.tone}` : ''}`}
                                  onClick={() => setPrjDocsUploadType(t.value)}
                                  disabled={prjDocsUploading}
                                >
                                  {t.label}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* File list */}
                          {prjDocsUploadFiles.length > 0 && (
                            <div className="bp-upload-files" style={{ maxHeight: 260, overflowY: 'auto' }}>
                              {prjDocsUploadFiles.map((f, i) => {
                                const key = `${i}-${f.name}`
                                const status = prjDocsUploadProgress[key] || 'pending'
                                return (
                                  <div key={key} className={`bp-upload-file status-${status}`}>
                                    <div className="bp-upload-file-icon" style={{ background: '#475569', fontSize: '0.6rem', width: 32, height: 32, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, flexShrink: 0 }}>
                                      {f.name.split('.').pop()?.toUpperCase().slice(0, 4) || 'FILE'}
                                    </div>
                                    <div className="bp-upload-file-body">
                                      <div className="bp-upload-file-row">
                                        <div className="bp-upload-file-name" title={f.name}>{f.name}</div>
                                        <div className="bp-upload-file-meta">
                                          {status === 'uploading' && <span style={{ color: '#0091ae' }}>Uploading…</span>}
                                          {status === 'done' && <span className="bp-upload-file-ok">Uploaded</span>}
                                          {status === 'error' && <span className="bp-upload-file-err">Failed</span>}
                                          <span>{m365FormatBytes(f.size)}</span>
                                        </div>
                                      </div>
                                      {status === 'uploading' && (
                                        <div className="bp-upload-progress"><div className="bp-upload-progress-bar" style={{ width: '100%', animation: 'pulse 1.5s ease-in-out infinite' }} /></div>
                                      )}
                                      {status === 'done' && (
                                        <div className="bp-upload-progress"><div className="bp-upload-progress-bar" style={{ width: '100%' }} /></div>
                                      )}
                                    </div>
                                    <div className="bp-upload-file-actions">
                                      {status === 'pending' && !prjDocsUploading && (
                                        <button type="button" className="bp-upload-file-btn" onClick={() => setPrjDocsUploadFiles(prev => prev.filter((_, j) => j !== i))} title="Remove" aria-label="Remove file">&times;</button>
                                      )}
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          )}
                        </div>

                        <div className="bp-upload-footer">
                          <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                            {(() => {
                              const done = Object.values(prjDocsUploadProgress).filter(s => s === 'done').length
                              const errs = Object.values(prjDocsUploadProgress).filter(s => s === 'error').length
                              if (done > 0 && errs === 0 && done === prjDocsUploadFiles.length) return `All ${done} file${done === 1 ? '' : 's'} uploaded`
                              if (done > 0 || errs > 0) return `${done} uploaded${errs > 0 ? `, ${errs} failed` : ''}`
                              return `${prjDocsUploadFiles.length} file${prjDocsUploadFiles.length === 1 ? '' : 's'} selected`
                            })()}
                          </div>
                          <div className="bp-upload-footer-actions">
                            <button
                              type="button"
                              className="bp-upload-btn-secondary"
                              onClick={() => { if (!prjDocsUploading) setPrjDocsUploadOpen(false) }}
                              disabled={prjDocsUploading}
                            >{Object.values(prjDocsUploadProgress).some(s => s === 'done') ? 'Close' : 'Cancel'}</button>
                            <button
                              type="button"
                              className="bp-upload-btn-primary"
                              disabled={prjDocsUploadFiles.length === 0 || !prjDocsUploadType || prjDocsUploading || prjDocsUploadFiles.every((_, i) => prjDocsUploadProgress[`${i}-${prjDocsUploadFiles[i]?.name}`] === 'done')}
                              onClick={async () => {
                                setPrjDocsUploading(true)
                                const progress: Record<string, 'pending' | 'uploading' | 'done' | 'error'> = {}
                                for (let i = 0; i < prjDocsUploadFiles.length; i++) {
                                  const key = `${i}-${prjDocsUploadFiles[i].name}`
                                  if (prjDocsUploadProgress[key] === 'done') { progress[key] = 'done'; continue }
                                  progress[key] = 'uploading'
                                  setPrjDocsUploadProgress({ ...progress })
                                  try {
                                    await uploadProjectFile(projectDetailData.id, prjDocsUploadFiles[i], 'admin_docs', prjDocsUploadType ?? undefined)
                                    progress[key] = 'done'
                                  } catch {
                                    progress[key] = 'error'
                                  }
                                  setPrjDocsUploadProgress({ ...progress })
                                }
                                await reloadPrjFiles(projectDetailData.id)
                                setPrjDocsUploading(false)
                                const allDone = Object.values(progress).every(s => s === 'done')
                                if (allDone) setTimeout(() => setPrjDocsUploadOpen(false), 600)
                              }}
                            >
                              {prjDocsUploading ? 'Uploading…' : Object.values(prjDocsUploadProgress).some(s => s === 'error')
                                ? 'Retry failed'
                                : `Upload${prjDocsUploadFiles.length > 0 ? ` ${prjDocsUploadFiles.length}` : ''}`}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}


                  {/* Attachments — aggregated from direct uploads + deal notes + deal emails */}
                  <div className="crp-card">
                    <div className="crp-card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <h3>Attachments{projectAttachments.length > 0 ? ` (${projectAttachments.length})` : ''}</h3>
                      <button
                        onClick={() => setProjectUploadModal({ docType: 'Invoice', files: [] })}
                        disabled={projectAttachmentsUploading}
                        style={{ background: 'none', border: 'none', cursor: projectAttachmentsUploading ? 'not-allowed' : 'pointer', fontSize: '0.78rem', color: '#0091ae', fontWeight: 600, padding: '2px 6px', opacity: projectAttachmentsUploading ? 0.6 : 1 }}
                      >{projectAttachmentsUploading ? 'Uploading…' : '+ Upload'}</button>
                    </div>
                    <div className="crp-card-body">
                      {projectAttachmentsLoading ? (
                        <div className="crp-empty-card" style={{ fontSize: '0.74rem' }}>Loading…</div>
                      ) : projectAttachments.length === 0 ? (
                        <div className="crp-empty-card" style={{ fontSize: '0.74rem' }}>Direct uploads, note attachments on the deal, and email attachments all aggregate here.</div>
                      ) : (() => {
                        // Collapse-by-default: show 4 attachments + a "View
                        // more (N)" / "Show less" toggle. Uses the existing
                        // crpExpandedCard key shared with Leads/Deals cards.
                        const ATTACH_PREVIEW = 4
                        const isAttExpanded = crpExpandedCard === 'project-attachments'
                        const visibleAtts = isAttExpanded
                          ? projectAttachments
                          : projectAttachments.slice(0, ATTACH_PREVIEW)
                        const hiddenCount = projectAttachments.length - ATTACH_PREVIEW
                        return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {visibleAtts.map((att: any) => {
                            // Per-doc-type palette for legacy QB docs so each kind
                            // (Invoice, Payment Receipt, Receipts, Draw Request, ...)
                            // gets its own color. Falls back to a neutral chip for
                            // anything we don't have a dedicated style for.
                            const docTypeBadgeMap: Record<string, { bg: string; color: string; label: string }> = {
                              'Invoice':         { bg: '#e3f2fd', color: '#1565c0', label: 'Invoice' },
                              'Payment Receipt': { bg: '#e8f5e9', color: '#2e7d32', label: 'Payment Receipt' },
                              'Receipts':        { bg: '#e8f5e9', color: '#2e7d32', label: 'Receipts' },
                              'Receipt':         { bg: '#e8f5e9', color: '#2e7d32', label: 'Receipt' },
                              'Draw Request':    { bg: '#ede9fe', color: '#6d28d9', label: 'Draw Request' },
                              'Proposal':        { bg: '#fff3e0', color: '#e65100', label: 'Proposal' },
                              'Estimate':        { bg: '#fff3e0', color: '#e65100', label: 'Estimate' },
                              'Contract':        { bg: '#fce4ec', color: '#ad1457', label: 'Contract' },
                              'Work Order':         { bg: '#fef3c7', color: '#b45309', label: 'SOW' },
                              'Work Order Related': { bg: '#ccfbf1', color: '#0f766e', label: 'Project docs' },
                            }
                            const baseBadgeMap: Record<string, { bg: string; color: string; label: string }> = {
                              direct: { bg: '#e3f2fd', color: '#1565c0', label: 'Project' },
                              note:   { bg: '#fff3e0', color: '#ef6c00', label: 'Note' },
                              email:  { bg: '#e8f5e9', color: '#2e7d32', label: 'Email' },
                              portal: { bg: '#e3f2fd', color: '#1565c0', label: 'Project' },
                              legacy: { bg: '#f1f5f9', color: '#475569', label: 'Document' },
                            }
                            // Prefer doc_type (set on legacy QB docs AND direct uploads
                            // categorized via the Upload modal). Fall back to the per-source
                            // base palette only when no doc_type was recorded.
                            let b: { bg: string; color: string; label: string }
                            if (att.doc_type) {
                              const palette = docTypeBadgeMap[att.doc_type as string] || { bg: '#f1f5f9', color: '#475569', label: 'Document' }
                              b = palette
                            } else {
                              b = baseBadgeMap[att.source as keyof typeof baseBadgeMap] || baseBadgeMap.direct
                            }
                            return (
                              <div key={att.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, padding: '5px 6px', borderRadius: 4, border: '1px solid #e5e7eb', background: '#fff' }}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (!att.download_url) return
                                    // Hand the modal the WHOLE viewable list + the clicked index
                                    // so it can wire prev/next arrows + ←/→ keys to page through
                                    // without closing & re-opening once per file.
                                    const viewable = (projectAttachments as any[]).filter(a => a.download_url)
                                    const idx = viewable.findIndex(a => a.id === att.id)
                                    const items = viewable.map(a => ({ url: a.download_url, title: a.file_name || 'Attachment' }))
                                    setAttachmentPreview({ items, index: idx >= 0 ? idx : 0 })
                                  }}
                                  style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}
                                  title={att.file_name}
                                >
                                  <span style={{ fontSize: '0.78rem', color: '#33475b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>{att.file_name}</span>
                                  <span style={{ display: 'inline-block', padding: '1px 6px', fontSize: '0.6rem', fontWeight: 600, borderRadius: 8, background: b.bg, color: b.color, textTransform: 'uppercase', letterSpacing: 0.3 }}>{b.label}</span>
                                </button>
                                {att.source === 'direct' && (
                                  <button
                                    type="button"
                                    title="Delete attachment"
                                    onClick={async (e) => {
                                      e.stopPropagation()
                                      const pid = projectDetailData?.id
                                      if (!pid || !String(att.id).startsWith('direct-')) return
                                      if (!confirm('Delete this attachment?')) return
                                      try {
                                        await fetch(`/api/crm/projects/${encodeURIComponent(pid)}/attachments/${encodeURIComponent(att.id)}`, { method: 'DELETE' })
                                        setProjectAttachments(prev => prev.filter((a: any) => a.id !== att.id))
                                      } catch { alert('Delete failed') }
                                    }}
                                    style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 14, padding: 2, flexShrink: 0 }}
                                  >×</button>
                                )}
                              </div>
                            )
                          })}
                          {hiddenCount > 0 && (
                            <button
                              type="button"
                              onClick={() => setCrpExpandedCard(isAttExpanded ? null : 'project-attachments')}
                              style={{ background: 'none', border: 'none', color: '#0091ae', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer', padding: '6px 0 0', textAlign: 'left' }}
                            >
                              {isAttExpanded ? 'Show less' : `View more (${hiddenCount})`}
                            </button>
                          )}
                        </div>
                        )
                      })()}
                    </div>
                  </div>
                </div>
              </div>
              {/* Sticky Save/Cancel footer for all pending project edits.
                  Batches prjPendingChanges (name, address, contract_value,
                  services_requested, start_date, end_date, progress,
                  description) into one API call on Save. */}
              {(() => {
                const pendingCount = Object.keys(prjPendingChanges).length
                if (pendingCount === 0) return null
                const saving = prjSavingBatch
                return (
                  <div className="prefs-save-footer crp-save-footer">
                    <div className="prefs-save-footer-inner">
                      <div className="prefs-save-footer-btns">
                        <button
                          className="prefs-save-btn"
                          disabled={saving}
                          onClick={async () => {
                            setPrjSavingBatch(true)
                            try {
                              await updateCrmProject(String(projectDetailData.id), { ...prjPendingChanges } as any)
                              const fresh: any = await fetchCrmProject(String(projectDetailData.id))
                              setProjectDetailData(fresh)
                              setProjectsData(prev => prev.map((p: any) => p.id === projectDetailData.id ? { ...p, ...fresh } : p))
                              setPrjPendingChanges({})
                            } catch (err: any) {
                              alert('Failed to save: ' + (err?.message || String(err)))
                            } finally {
                              setPrjSavingBatch(false)
                            }
                          }}
                        >{saving ? 'Saving…' : 'Save'}</button>
                        <button
                          className="prefs-cancel-btn"
                          disabled={saving}
                          onClick={() => setPrjPendingChanges({})}
                        >Cancel</button>
                      </div>
                      <p className="prefs-save-footer-msg">You've changed {pendingCount} setting{pendingCount === 1 ? '' : 's'}.</p>
                    </div>
                  </div>
                )
              })()}
            </div>
            )
          })()}

          {(activeView === 'contacts' || activeView === 'companies' || activeView === 'leads' || activeView === 'deals' || activeView === 'tickets' || activeView === 'calls' || activeView === 'meets') && !((activeView === 'contacts' || activeView === 'companies') && contactDetailOpen) && !(activeView === 'deals' && dealDetailOpen) && (
            <CrmListShell
              activeView={activeView}
              hasPreview={!!(previewClient || previewCompany || previewLead || previewTicket || previewCall)}
              tableSettingsOpen={tableSettingsOpen}
              loadingBar={
                ((activeView === 'contacts' && clientLoading && clientList.length > 0) ||
                  (activeView === 'companies' && companyLoading && companyList.length > 0) ||
                  (activeView === 'meets' && meetsLoading && meetsData.length > 0))
                  ? <div className="hs-loading-bar" />
                  : null
              }
              topbarLeft={<>
                  {/* Object switcher */}
                  <div className="hs-obj-switcher-wrap" ref={objSwitcherRef}>
                    <button className={`hs-obj-switcher${objSwitcherOpen ? ' open' : ''}`} onClick={() => { setObjSwitcherOpen(!objSwitcherOpen); setObjSwitcherSearch('') }}>
                      {activeView === 'meets' ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="17" rx="2"/><path d="M8 2v4M16 2v4M3 10h18"/><path d="M8 14h3M8 18h5"/></svg>
                      ) : activeView === 'calls' ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                      ) : activeView === 'tickets' ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M15 5v2"/><path d="M15 11v2"/><path d="M15 17v2"/><path d="M5 5h14a2 2 0 0 1 2 2v3a2 2 0 0 0 0 4v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-3a2 2 0 0 0 0-4V7a2 2 0 0 1 2-2z"/></svg>
                      ) : activeView === 'deals' ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
                      ) : activeView === 'companies' ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18"/><path d="M5 21V7l7-4 7 4v14"/><path d="M9 21v-4h6v4"/><path d="M9 10h.01M15 10h.01M9 14h.01M15 14h.01"/></svg>
                      ) : activeView === 'leads' ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 32 32" fill="currentColor"><path d="M25.57 29.29h-16c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43h16C27.46 1.86 29 3.4 29 5.29v20.57c0 1.89-1.54 3.43-3.43 3.43m-16-25.15c-.63 0-1.14.51-1.14 1.14v20.57c0 .63.51 1.14 1.14 1.14h16c.63 0 1.14-.51 1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14h-16Z"/><path d="M9.57 11H5a1.14 1.14 0 1 1 0-2.28h4.57a1.14 1.14 0 1 1 0 2.28M9.57 16.71H5a1.14 1.14 0 1 1 0-2.28h4.57a1.14 1.14 0 1 1 0 2.28M9.57 22.43H5a1.14 1.14 0 1 1 0-2.28h4.57a1.14 1.14 0 1 1 0 2.28M17.75 15.57c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4m0-5.71a1.71 1.71 0 1 0-.002 3.418 1.71 1.71 0 0 0 .002-3.418M14.14 22.43a1.143 1.143 0 0 1-1.02-1.67c.91-1.74 2.68-2.82 4.62-2.82s3.72 1.08 4.62 2.82a1.143 1.143 0 0 1-2.03 1.05c-.51-.98-1.5-1.59-2.6-1.59s-2.08.61-2.6 1.59c-.2.39-.6.62-1.01.62Z"/></svg>
                      )}
                      <span>{activeView === 'meets' ? 'Meets' : activeView === 'calls' ? 'Calls' : activeView === 'tickets' ? 'Tickets' : activeView === 'deals' ? 'Deals' : activeView === 'companies' ? 'Clients' : activeView === 'leads' ? 'Leads' : 'Contacts'}</span>
                      <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                    </button>
                    {objSwitcherOpen && (
                      <div className="hs-obj-panel">
                        <div className="hs-obj-search-wrap">
                          <svg className="hs-obj-search-icon" width="13" height="13" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                          <input
                            className="hs-obj-search"
                            placeholder="Search"
                            value={objSwitcherSearch}
                            onChange={(e) => setObjSwitcherSearch(e.target.value)}
                            autoFocus
                          />
                        </div>
                        <ul className="hs-obj-list" role="listbox">
                          {[
                            { label: 'Contacts', view: 'contacts' as ViewMode },
                            { label: 'Clients', view: 'companies' as ViewMode },
                            { label: 'Deals', view: 'deals' as ViewMode },
                            { label: 'Leads', view: 'leads' as ViewMode },
                            { label: 'Projects', view: 'projects' as ViewMode },
                            { label: 'Tickets', view: 'tickets' as ViewMode },
                            { label: 'Calls', view: 'calls' as ViewMode },
                            { label: 'Meets', view: 'meets' as ViewMode },
                          ]
                            .filter(item => !objSwitcherSearch || item.label.toLowerCase().includes(objSwitcherSearch.toLowerCase()))
                            .map(item => (
                              <li
                                key={item.view}
                                className={`hs-obj-item${item.view === activeView ? ' active' : ''}`}
                                role="option"
                                aria-selected={item.view === activeView}
                                onClick={() => {
                                  setActiveView(item.view)
                                  setObjSwitcherOpen(false)
                                  setObjSwitcherSearch('')
                                }}
                              >
                                {item.label}
                              </li>
                            ))
                          }
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Separator */}
                  <div className="hs-topbar-sep" />

                  {/* View tabs */}
                  <div className="hs-view-tabs">
                    {(activeView === 'meets'
                      ? [{ key: 'all', label: 'All meetings' }, { key: 'upcoming', label: 'Upcoming' }, { key: 'past', label: 'Past' }]
                      : activeView === 'calls'
                      ? [{ key: '', label: 'All calls' }, { key: '1', label: 'Outbound' }, { key: '2', label: 'Inbound' }]
                      : activeView === 'tickets'
                      ? [{ key: 'all', label: 'All tickets' }, { key: 'open', label: 'Open' }, { key: 'in_progress', label: 'In Progress' }, { key: 'waiting', label: 'Waiting' }, { key: 'escalated', label: 'Escalated' }, { key: 'resolved', label: 'Resolved' }, { key: 'closed', label: 'Closed' }]
                      : activeView === 'deals'
                      ? [] /* Deals use the three-way Open/Closed/All scope tabs below instead of the generic "All deals" tab */
                      : activeView === 'leads'
                      ? [{ key: '', label: 'All leads' }]
                      : activeView === 'contacts'
                      ? [{ key: '', label: 'All contacts' }, { key: 'matched', label: 'Matched' }, { key: 'imported', label: 'Imported' }, { key: 'legacy_only', label: 'Legacy' }, { key: 'archived', label: 'Archived' }]
                      : [{ key: '', label: 'All clients' }, { key: 'matched', label: 'Matched' }, { key: 'imported', label: 'Imported' }, { key: 'legacy_only', label: 'Legacy' }, { key: 'archived', label: 'Archived' }]
                    ).filter(tab => {
                      const allKey = activeView === 'meets' ? 'all' : activeView === 'tickets' ? 'all' : activeView === 'deals' ? '-1' : '';
                      if (tab.key === allKey) return true;
                      if (activeView === 'meets') return true;
                      if (activeView === 'tickets' && (tab.key === 'open' || tab.key === 'closed')) return true;
                      if (activeView === 'leads') return true;
                      const saved = savedViewTabs[activeView] || [];
                      return saved.includes(tab.key);
                    }).map((tab) => {
                      const filter = activeView === 'meets' ? meetsWhen : activeView === 'calls' ? callsDirectionFilter : activeView === 'tickets' ? ticketsFilter : activeView === 'deals' ? '-1' : activeView === 'leads' ? leadStatusFilter : activeView === 'contacts' ? clientStatusFilter : companyStatusFilter
                      const rawSetFilter = activeView === 'meets'
                        ? ((value: string) => { setMeetsWhen(value as 'all' | 'upcoming' | 'past'); setMeetsPage(1) })
                        : activeView === 'calls' ? setCallsDirectionFilter : activeView === 'tickets' ? setTicketsFilter : activeView === 'deals' ? (() => {}) : activeView === 'leads' ? setLeadStatusFilter : activeView === 'contacts' ? setClientStatusFilter : setCompanyStatusFilter
                      const setFilter = activeView === 'leads'
                        ? ((v: string) => { setActiveLeadCustomViewId(null); rawSetFilter(v) })
                        : rawSetFilter
                      const isActive = filter === tab.key && (activeView !== 'leads' || !activeLeadCustomViewId)
                      const total = activeView === 'meets' ? meetsTotal : activeView === 'calls' ? callsTotal : activeView === 'tickets' ? ticketsTotal : activeView === 'deals' ? dealTotal : activeView === 'leads' ? leadPageTotal : activeView === 'contacts' ? clientTotal : companyTotal
                      return (
                        <div
                          key={tab.key}
                          className={`hs-view-tab${isActive ? ' active' : ''}`}
                          ref={isActive ? tabEditRef : undefined}
                          onClick={() => setFilter(tab.key)}
                        >
                          <span className="hs-tab-label">{tab.label}</span>
                          {activeView === 'tickets' && (tab.key === 'all' || tab.key === 'open' || tab.key === 'closed') ? (
                            (ticketTabCounts[tab.key as 'all' | 'open' | 'closed'] || 0) > 0 && <span className="hs-tab-count">{ticketTabCounts[tab.key as 'all' | 'open' | 'closed'].toLocaleString()}</span>
                          ) : (activeView === 'leads' && tab.key === '') ? (
                            // "All leads" always shows the (filtered) total even when a custom view tab is active or filters are applied.
                            total > 0 && <span className="hs-tab-count">{total.toLocaleString()}</span>
                          ) : (
                            isActive && total > 0 && <span className="hs-tab-count">{total.toLocaleString()}</span>
                          )}
                          {isActive && (
                            <span className="hs-tab-kebab" onClick={(e) => { e.stopPropagation(); setTabEditOpen(!tabEditOpen) }}>
                              <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>
                            </span>
                          )}
                          {activeView !== 'leads' && tab.key !== (activeView === 'meets' || activeView === 'tickets' ? 'all' : activeView === 'deals' ? '-1' : '') && <span className="hs-tab-x" onClick={(e) => { e.stopPropagation(); handleDeleteViewTab(tab.key) }}>&times;</span>}
                          {isActive && tabEditOpen && (
                            <div className="hs-tab-edit-dropdown">
                              <button className="hs-tab-edit-item" disabled>Rename</button>
                              <button className="hs-tab-edit-item">Clone</button>
                              <button className="hs-tab-edit-item" disabled>Manage Sharing</button>
                              {tab.key !== (activeView === 'meets' || activeView === 'tickets' ? 'all' : activeView === 'deals' ? '-1' : '') && <button className="hs-tab-edit-item" onClick={() => handleDeleteViewTab(tab.key)}>Delete</button>}
                            </div>
                          )}
                        </div>
                      )
                    })}
                    {/* Custom user-created view tabs */}
                    {(customViews[activeView] || []).map(cv => {
                      const isActive = activeView === 'leads'
                        ? activeLeadCustomViewId === cv.id
                        : (activeView === 'contacts' ? clientStatusFilter : activeView === 'companies' ? companyStatusFilter : activeView === 'tickets' ? ticketsFilter : callsDirectionFilter) === `custom:${cv.id}`
                      const isRenaming = renamingViewId === cv.id
                      return (
                        <div
                          key={cv.id}
                          className={`hs-view-tab${isActive ? ' active' : ''}`}
                          onClick={() => {
                            if (isRenaming) return
                            if (activeView === 'leads') {
                              const f = cv.filters || {}
                              setLeadStatusFilter(f.status || '')
                              if (f.sortField) setLeadSort({ field: f.sortField, order: f.sortOrder === 'ASC' ? 'ASC' : 'DESC' })
                              setLeadSearch(f.search || '')
                              setLeadIncludeConverted(!!f.includeConverted)
                              setLeadIncludeDisqualified(!!f.includeDisqualified)
                              setLeadPage(1)
                              setActiveLeadCustomViewId(cv.id)
                              return
                            }
                            const setFilter = activeView === 'contacts' ? setClientStatusFilter : activeView === 'companies' ? setCompanyStatusFilter : activeView === 'tickets' ? setTicketsFilter : setCallsDirectionFilter
                            setFilter(`custom:${cv.id}`)
                          }}
                        >
                          {isRenaming ? (
                            <input
                              ref={renamingViewRef}
                              className="hs-tab-rename-input"
                              value={renamingViewName}
                              onChange={(e) => setRenamingViewName(e.target.value)}
                              onFocus={(e) => e.target.select()}
                              onBlur={() => {
                                if (renamingViewName.trim()) {
                                  if (cv.serverId) updateSavedView(cv.serverId, { name: renamingViewName.trim() }).catch(() => {})
                                  setCustomViews(prev => ({
                                    ...prev,
                                    [activeView]: (prev[activeView] || []).map(v => v.id === cv.id ? { ...v, label: renamingViewName.trim() } : v)
                                  }))
                                }
                                setRenamingViewId(null)
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
                                if (e.key === 'Escape') { setRenamingViewName(cv.label); setRenamingViewId(null) }
                              }}
                              onClick={(e) => e.stopPropagation()}
                              autoFocus
                            />
                          ) : (
                            <span className="hs-tab-label">{cv.label}</span>
                          )}
                          {isActive && !isRenaming && (() => {
                            const total = activeView === 'contacts' ? clientTotal : activeView === 'companies' ? companyTotal : activeView === 'leads' ? leadPageTotal : activeView === 'tickets' ? ticketsTotal : callsTotal
                            return total > 0 ? <span className="hs-tab-count">{total.toLocaleString()}</span> : null
                          })()}
                          {isActive && !isRenaming && (
                            <span className="hs-tab-kebab" onClick={(e) => { e.stopPropagation(); setTabEditOpen(!tabEditOpen) }}>
                              <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>
                            </span>
                          )}
                          {isActive && tabEditOpen && !isRenaming && (
                            <div className="hs-tab-edit-dropdown">
                              <button className="hs-tab-edit-item" onClick={() => { setRenamingViewId(cv.id); setRenamingViewName(cv.label); setTabEditOpen(false) }}>Rename</button>
                              <button className="hs-tab-edit-item" onClick={() => {
                                const id = Date.now().toString(36)
                                setCustomViews(prev => ({
                                  ...prev,
                                  [activeView]: [...(prev[activeView] || []), { id, label: `${cv.label} (clone)`, ...(cv.filters ? { filters: { ...cv.filters } } : {}) }]
                                }))
                                if (activeView === 'leads') {
                                  setActiveLeadCustomViewId(id)
                                } else {
                                  const setFilter = activeView === 'contacts' ? setClientStatusFilter : activeView === 'companies' ? setCompanyStatusFilter : activeView === 'tickets' ? setTicketsFilter : setCallsDirectionFilter
                                  setFilter(`custom:${id}`)
                                }
                                setRenamingViewId(id)
                                setRenamingViewName(`${cv.label} (clone)`)
                                setTabEditOpen(false)
                              }}>Clone</button>
                              <button className="hs-tab-edit-item" onClick={() => {
                                if (cv.serverId) deleteSavedView(cv.serverId).catch(() => {})
                                setCustomViews(prev => ({
                                  ...prev,
                                  [activeView]: (prev[activeView] || []).filter(v => v.id !== cv.id)
                                }))
                                if (activeView === 'leads') {
                                  setActiveLeadCustomViewId(null)
                                  setLeadStatusFilter('')
                                } else {
                                  const setFilter = activeView === 'contacts' ? setClientStatusFilter : activeView === 'companies' ? setCompanyStatusFilter : activeView === 'tickets' ? setTicketsFilter : setCallsDirectionFilter
                                  setFilter('')
                                }
                                setTabEditOpen(false)
                              }}>Delete</button>
                            </div>
                          )}
                          <span className="hs-tab-x" onClick={(e) => {
                            e.stopPropagation()
                            if (cv.serverId) deleteSavedView(cv.serverId).catch(() => {})
                            setCustomViews(prev => ({
                              ...prev,
                              [activeView]: (prev[activeView] || []).filter(v => v.id !== cv.id)
                            }))
                            if (isActive) {
                              if (activeView === 'leads') {
                                setActiveLeadCustomViewId(null)
                                setLeadStatusFilter('')
                              } else {
                                const setFilter = activeView === 'contacts' ? setClientStatusFilter : activeView === 'companies' ? setCompanyStatusFilter : activeView === 'tickets' ? setTicketsFilter : setCallsDirectionFilter
                                setFilter('')
                              }
                            }
                          }}>&times;</span>
                        </div>
                      )
                    })}
                    {/* Deals scope tabs — fixed views that intentionally use
                        the same tab styling as Contacts. Closed + All force
                        table view + smaller page size because row counts can
                        reach ~14k. */}
                    {activeView === 'deals' && (() => {
                      const scopes: Array<{ key: 'open' | 'closed' | 'all'; label: string; title: string }> = [
                        { key: 'open',   label: 'Open Deals',   title: 'Active pipeline deals (excludes Contract Signed and Lost)' },
                        { key: 'closed', label: 'Signed Deals', title: 'Deals at Contract Signed — already advanced to projects' },
                        { key: 'all',    label: 'All Deals',    title: 'Every deal, including pre-migration historical' },
                      ]
                      return scopes.map((s) => {
                        const isActive = dealsScope === s.key
                        return (
                          <div
                            key={s.key}
                            className={`hs-view-tab${isActive ? ' active' : ''}`}
                            ref={isActive ? tabEditRef : undefined}
                            onClick={() => {
                              if (dealsScope === s.key) return
                              setDealsScope(s.key)
                              setDealStageFilter(new Set())
                              setDealOwnerFilter(new Set())
                              setDealAmountFilter('')
                              setDealCloseDateFilter('')
                              if (s.key !== 'open') {
                                setDealViewMode('table')
                                setDealPageSize(50)
                              } else {
                                setDealPageSize(ps => ps < 100 ? 100 : ps)
                              }
                              setDealPage(1)
                            }}
                            title={s.title}
                          >
                            <span className="hs-tab-label">{s.label}</span>
                            {isActive && dealTotal > 0 && (
                              <span className="hs-tab-count">{dealTotal.toLocaleString()}</span>
                            )}
                            {isActive && (
                              <span className="hs-tab-kebab" onClick={(e) => { e.stopPropagation(); setTabEditOpen(!tabEditOpen) }}>
                                <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>
                              </span>
                            )}
                            {isActive && tabEditOpen && (
                              <div className="hs-tab-edit-dropdown">
                                <button className="hs-tab-edit-item" disabled>Rename</button>
                                <button className="hs-tab-edit-item" disabled>Clone</button>
                                <button className="hs-tab-edit-item" disabled>Manage Sharing</button>
                              </div>
                            )}
                          </div>
                        )
                      })
                    })()}
                    {/* Quick-add view button — inline with tabs */}
                    <div className="hs-topbar-sep" />
                    <div className="hs-view-add-wrap" ref={viewAddMenuRef} style={{ position: 'relative', display: 'inline-flex', alignSelf: 'center' }}>
                      <button
                        className="hs-quick-add-btn"
                        title="Create new view from current filters"
                        type="button"
                        aria-label="Create new view"
                        onClick={() => {
                          // One-click create — Board/Report were never implemented so skip the type picker.
                          const id = Date.now().toString(36)
                          const viewName = `${activeView === 'contacts' ? 'All contacts' : activeView === 'companies' ? 'All clients' : activeView === 'leads' ? 'All leads' : activeView === 'tickets' ? 'All tickets' : activeView === 'deals' ? 'All deals' : 'All calls'}-${(customViews[activeView] || []).length + 1}`
                          const leadFilterSnapshot = activeView === 'leads'
                            ? {
                                status: leadStatusFilter && !leadStatusFilter.startsWith('custom:') ? leadStatusFilter : '',
                                sortField: leadSort.field,
                                sortOrder: leadSort.order,
                                search: leadSearch,
                                includeConverted: leadIncludeConverted,
                                includeDisqualified: leadIncludeDisqualified,
                              }
                            : undefined
                          setCustomViews(prev => ({
                            ...prev,
                            [activeView]: [...(prev[activeView] || []), { id, label: viewName, ...(leadFilterSnapshot ? { filters: leadFilterSnapshot } : {}) }]
                          }))
                          if (activeView === 'leads') {
                            setActiveLeadCustomViewId(id)
                          } else {
                            const setFilter = activeView === 'contacts' ? setClientStatusFilter : activeView === 'companies' ? setCompanyStatusFilter : activeView === 'tickets' ? setTicketsFilter : setCallsDirectionFilter
                            setFilter(`custom:${id}`)
                          }
                          setRenamingViewId(id)
                          setRenamingViewName(viewName)
                          createSavedView({ name: viewName, objectType: activeView, filtersJson: leadFilterSnapshot ?? {} }).then(sv => setCustomViews(prev => ({ ...prev, [activeView]: (prev[activeView] || []).map(v => v.id === id ? { ...v, serverId: sv.id } : v) }))).catch(() => {})
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                      </button>
                  </div>
                </div>
              </>}
              topbarRight={<>
                  {/* Add contacts/companies/leads/tickets — direct action (Import removed) */}
                  <button className="hs-add-contacts-btn" onClick={() => {
                    setCreateError(null)
                    setEmailTouched(false)
                    setCreateEmails([''])
                    setCreatePhones([{ type: 'Mobile', number: '', ext: '' }])
                    if (activeView === 'meets') {
                      setMeetsLogOpen(true)
                    } else if (activeView === 'deals') {
                      // Open the unified Create New panel with Deal pre-selected.
                      const d = new Date(); d.setMonth(d.getMonth() + 1, 0)
                      setCreateFormData({ dealPipeline: '0', closeDate: d.toISOString().slice(0, 10), dealType: 'residential', sendConfirmationEmail: true })
                      setSmartCreateSearch('')
                      setSmartCreateResults(null)
                      setSmartCreateType('deal')
                      setShowCreateModal('deal')
                      setSmartCreateOpen(true)
                    } else if (activeView === 'leads') {
                      // Open the unified Create New panel with Lead pre-selected.
                      setCreateFormData({})
                      setSmartCreateSearch('')
                      setSmartCreateResults(null)
                      setSmartCreateType('lead')
                      setShowCreateModal('lead')
                      setSmartCreateOpen(true)
                    } else if (activeView === 'tickets') {
                      // Open the unified Smart Create panel with the Ticket chip pre-selected.
                      openSmartCreateForTicket()
                    } else if (activeView === 'contacts' || activeView === 'companies') {
                      // Open the unified Create New panel with NO "Also create" sub-action pre-selected.
                      // For Companies, the unified panel already has a Company Name field —
                      // filling that in causes handleCreateSubmit to create an organization-typed client.
                      // Track which view triggered this so the panel can autofocus the right field
                      // (Company Name when launched from Companies view, Contact Name otherwise).
                      setCreateFormData({})
                      setSmartCreateSearch('')
                      setSmartCreateResults(null)
                      setSmartCreateType(activeView === 'companies' ? 'company' : null)
                      setShowCreateModal(null)
                      setSmartCreateOpen(true)
                    } else {
                      setCreateFormData({})
                      setShowCreateModal('contact')
                    }
                  }}>
                    {activeView === 'meets' ? 'Log meeting' : activeView === 'calls' ? 'Log call' : activeView === 'tickets' ? 'Create ticket' : activeView === 'deals' ? 'Add deal' : activeView === 'companies' ? 'Add client' : activeView === 'leads' ? 'Add lead' : 'Add contact'}
                  </button>
              </>}
              toolbarLeft={<>
                  {/* Search */}
                  <div className="hs-search-wrap">
                    <svg className="hs-search-icon" width="14" height="14" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M28.14 29.29c-.29 0-.58-.11-.81-.33l-7.58-7.58a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l7.58 7.58a1.14 1.14 0 0 1-.81 1.95"/><path d="M13.29 24.71c-6.3 0-11.43-5.13-11.43-11.43S6.98 1.86 13.29 1.86s11.43 5.13 11.43 11.43-5.13 11.43-11.43 11.43Zm0-20.57c-5.04 0-9.14 4.1-9.14 9.14s4.1 9.14 9.14 9.14 9.14-4.1 9.14-9.14-4.1-9.14-9.14-9.14"/></svg>
                    <input
                      className="hs-search-input"
                      placeholder="Search"
                      value={activeView === 'meets' ? meetsSearch : activeView === 'calls' ? callsSearch : activeView === 'tickets' ? ticketsSearch : activeView === 'deals' ? dealSearch : activeView === 'leads' ? leadSearch : activeView === 'companies' ? companySearch : clientSearch}
                      onChange={(e) => activeView === 'meets' ? setMeetsSearch(e.target.value) : activeView === 'calls' ? setCallsSearch(e.target.value) : activeView === 'tickets' ? setTicketsSearch(e.target.value) : activeView === 'deals' ? setDealSearch(e.target.value) : activeView === 'leads' ? setLeadSearch(e.target.value) : activeView === 'companies' ? setCompanySearch(e.target.value) : setClientSearch(e.target.value)}
                    />
                    {(activeView === 'meets' ? meetsSearch : activeView === 'calls' ? callsSearch : activeView === 'tickets' ? ticketsSearch : activeView === 'deals' ? dealSearch : activeView === 'leads' ? leadSearch : activeView === 'companies' ? companySearch : clientSearch) && (
                      <button className="hs-search-clear" onClick={() => activeView === 'meets' ? setMeetsSearch('') : activeView === 'calls' ? setCallsSearch('') : activeView === 'tickets' ? setTicketsSearch('') : activeView === 'deals' ? setDealSearch('') : activeView === 'leads' ? setLeadSearch('') : activeView === 'companies' ? setCompanySearch('') : setClientSearch('')}>&times;</button>
                    )}
                  </div>

                  <div className="hs-tb-sep" />

                  {/* View type + settings button group */}
                  <div className="hs-viewtype-group">
                    {activeView === 'deals' && (
                    <div className="hs-viewtype-wrap" ref={viewTypeDropdownRef}>
                      <button
                        className={`hs-tb-btn hs-viewtype-btn${viewTypeDropdownOpen ? ' active' : ''}`}
                        onClick={() => setViewTypeDropdownOpen(!viewTypeDropdownOpen)}
                      >
                        {(activeView === 'deals' ? dealViewMode : clientViewMode) === 'table' ? (
                          <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                        ) : (
                          <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M11.43 11.43h-8a1.14 1.14 0 1 1 0-2.28h8a1.14 1.14 0 1 1 0 2.28M28.57 11.43h-8a1.14 1.14 0 1 1 0-2.28h8a1.14 1.14 0 1 1 0 2.28"/><path d="M20.57 19.43c-.63 0-1.14-.51-1.14-1.14V3.43a1.14 1.14 0 1 1 2.28 0v14.86c0 .63-.51 1.14-1.14 1.14M11.43 19.43c-.63 0-1.14-.51-1.14-1.14V3.43a1.14 1.14 0 1 1 2.28 0v14.86c0 .63-.51 1.14-1.14 1.14"/><path d="M9.14 29.71H5.71c-1.89 0-3.43-1.54-3.43-3.43V5.71c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v14.86c0 1.89-1.54 3.43-3.43 3.43h-3.43c-1.89 0-3.43-1.54-3.43-3.43v-1.14h-6.86v6.86c0 1.89-1.54 3.43-3.43 3.43ZM5.71 4.57c-.63 0-1.14.51-1.14 1.14v20.57c0 .63.51 1.14 1.14 1.14h3.43c.63 0 1.14-.51 1.14-1.14v-8c0-.63.51-1.14 1.14-1.14h9.14c.63 0 1.14.51 1.14 1.14v2.29c0 .63.51 1.14 1.14 1.14h3.43c.63 0 1.14-.51 1.14-1.14V5.71c0-.63-.51-1.14-1.14-1.14z"/></svg>
                        )}
                        <span>{(activeView === 'deals' ? dealViewMode : clientViewMode) === 'table' ? 'Table' : 'Board'}</span>
                        <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                      </button>
                      {viewTypeDropdownOpen && (
                        <div className="hs-viewtype-panel">
                          <div className="hs-viewtype-header">View type</div>
                          <button
                            className={`hs-viewtype-option${(activeView === 'deals' ? dealViewMode : clientViewMode) === 'table' ? ' active' : ''}`}
                            onClick={() => { activeView === 'deals' ? setDealViewMode('table') : setClientViewMode('table'); setViewTypeDropdownOpen(false) }}
                          >
                            <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                            <span>Table</span>
                          </button>
                          {!(activeView === 'deals' && dealsArchiveMode) && (
                            <button
                              className={`hs-viewtype-option${(activeView === 'deals' ? dealViewMode : clientViewMode) === 'board' ? ' active' : ''}`}
                              onClick={() => { if (activeView === 'deals') { setDealViewMode('board'); if (dealCategory < 0) setDealCategory(0) } else { setClientViewMode('board') }; setViewTypeDropdownOpen(false) }}
                            >
                              <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M11.43 11.43h-8a1.14 1.14 0 1 1 0-2.28h8a1.14 1.14 0 1 1 0 2.28M28.57 11.43h-8a1.14 1.14 0 1 1 0-2.28h8a1.14 1.14 0 1 1 0 2.28"/><path d="M20.57 19.43c-.63 0-1.14-.51-1.14-1.14V3.43a1.14 1.14 0 1 1 2.28 0v14.86c0 .63-.51 1.14-1.14 1.14M11.43 19.43c-.63 0-1.14-.51-1.14-1.14V3.43a1.14 1.14 0 1 1 2.28 0v14.86c0 .63-.51 1.14-1.14 1.14"/><path d="M9.14 29.71H5.71c-1.89 0-3.43-1.54-3.43-3.43V5.71c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v14.86c0 1.89-1.54 3.43-3.43 3.43h-3.43c-1.89 0-3.43-1.54-3.43-3.43v-1.14h-6.86v6.86c0 1.89-1.54 3.43-3.43 3.43ZM5.71 4.57c-.63 0-1.14.51-1.14 1.14v20.57c0 .63.51 1.14 1.14 1.14h3.43c.63 0 1.14-.51 1.14-1.14v-8c0-.63.51-1.14 1.14-1.14h9.14c.63 0 1.14.51 1.14 1.14v2.29c0 .63.51 1.14 1.14 1.14h3.43c.63 0 1.14-.51 1.14-1.14V5.71c0-.63-.51-1.14-1.14-1.14z"/></svg>
                              <span>Board</span>
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    )}
                    <button
                      className={`hs-tb-btn hs-settings-btn${tableSettingsOpen ? ' active' : ''}`}
                      onClick={() => { if (!tableSettingsOpen) { setPreviewClient(null); setPreviewCompany(null); setPreviewLead(null); setPreviewTicket(null); setPreviewCall(null) } setTableSettingsOpen(!tableSettingsOpen) }}
                      title={tableSettingsOpen ? 'Close settings' : 'Table settings'}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                    </button>
                  </div>

                  {/* Edit columns (contacts/companies/deals/calls) */}
                  {(activeView === 'contacts' || activeView === 'companies' || activeView === 'deals' || activeView === 'calls') && (
                    <>
                      <div className="hs-tb-sep" />
                      <button
                        className="hs-tb-btn hs-tb-btn-bordered"
                        title="Edit columns"
                        onClick={() => {
                          if (activeView === 'deals') {
                            setDealEditColsDraft([...dealVisibleCols])
                            setDealEditColsSearch('')
                            setDealEditColsOpen(true)
                          } else if (activeView === 'calls') {
                            // Reuse the shared contacts popover — activeView switches
                            // its data source + Apply target to the calls columns.
                            setEditColsDraft([...callVisibleCols])
                            setEditColsSearch('')
                            setEditColumnsOpen(true)
                          } else {
                            setEditColsDraft([...contactVisibleCols])
                            setEditColsSearch('')
                            setEditColumnsOpen(true)
                          }
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="18" rx="1"/><rect x="14" y="3" width="7" height="18" rx="1"/></svg>
                        <span>Edit columns</span>
                      </button>
                    </>
                  )}

                  {/* Pipeline selector removed — single Sales pipeline */}

                  <div className="hs-tb-sep" />

                  {/* Filters */}
                  <button className={`hs-tb-btn hs-tb-btn-bordered${quickFiltersOpen ? ' hs-tb-btn-pressed' : ''}`} title="Filters" onClick={() => setQuickFiltersOpen(!quickFiltersOpen)}>
                    <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M30.71 16.86H13.57a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28M6.71 16.86H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M10.14 20.29c-2.52 0-4.57-2.05-4.57-4.57s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M6.71 6.57H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M30.71 6.57H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M18.14 6.57H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 10C19.05 10 17 7.95 17 5.43S19.05.86 21.57.86s4.57 2.05 4.57 4.57S24.09 10 21.57 10m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M30.71 27.14H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M18.14 27.14H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 30.57C19.05 30.57 17 28.52 17 26s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29"/></svg>
                    <span>Filters</span>
                  </button>

                  <div className="hs-tb-sep" />

                  {/* Export — CAPABILITY: export_contacts (super_admin only).
                      Button is rendered for everyone but disabled with a
                      tooltip when the user lacks the capability so the
                      affordance is visible even if greyed out. */}
                  <button
                    className={`hs-tb-btn hs-tb-btn-bordered${clientExporting ? ' hs-tb-btn-pressed' : ''}`}
                    title={(activeView === 'contacts' || activeView === 'companies') && myCapabilities && !myCapabilities.export_contacts
                      ? `Requires Super Admin (your role: ${myRole || 'unknown'})`
                      : 'Export current view as CSV'}
                    disabled={clientExporting || ((activeView === 'contacts' || activeView === 'companies') && myCapabilities ? !myCapabilities.export_contacts : false)}
                    onClick={async () => {
                      setClientExporting(true)
                      try {
                        const escCsv = (v: unknown) => { const s = String(v ?? ''); return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s }
                        let csv = ''
                        let filename = ''
                        const dateStr = new Date().toISOString().slice(0, 10)

                        if (activeView === 'leads') {
                          const headers = ['Title', 'Contact', 'Phone', 'Email', 'Status', 'Source', 'Assigned', 'Created', 'Modified']
                          const rows = leadList.map((l: any) => [l.title, l.name, l.phone, l.email, l.statusLabel, l.sourceLabel, l.assignedTo, l.dateCreated, l.dateModified].map(escCsv).join(','))
                          csv = [headers.join(','), ...rows].join('\n')
                          filename = `leads-export-${dateStr}.csv`
                        } else if (activeView === 'deals') {
                          const headers = ['Title', 'Stage', 'Pipeline', 'Amount', 'Contact', 'Assigned', 'Created', 'Modified']
                          const rows = (liveDeals || []).map((d: any) => [d.title, d.stageLabel, d.pipelineName, d.opportunity, d.contactName, d.assignedTo, d.dateCreated, d.dateModified].map(escCsv).join(','))
                          csv = [headers.join(','), ...rows].join('\n')
                          filename = `deals-export-${dateStr}.csv`
                        } else {
                          const isCompanies = activeView === 'companies'
                          const qfOpts = buildQuickFilterOpts(qfPillValues)
                          const data = await fetchClients(1, 10000, {
                            search: (isCompanies ? companySearchDebounced : clientSearchDebounced) || undefined,
                            status: (() => { const s = isCompanies ? companyStatusFilter : clientStatusFilter; return (s && !s.startsWith('custom:')) ? s : undefined })(),
                            type: isCompanies ? 'organization' : 'person',
                            sort: (isCompanies ? companySort : clientSort).field,
                            order: (isCompanies ? companySort : clientSort).order,
                            ...qfOpts,
                          })
                          const headers = ['Name', 'Email', 'Phone', 'Contact people', 'City', 'State', 'Zip', 'Type', 'Match Status', 'Data Quality', 'Deals', 'Leads', 'Total Invoiced', 'Total Paid', 'Balance']
                          const rows = data.clients.map((c: any) => {
                            // "Contact people" column — one row per client preserved (blast risk #7):
                            // real contact_persons if the row carries them, else the couple parse of
                            // the display name (e.g. "Alan & Karen Dorman" → "Alan Dorman; Karen Dorman").
                            const personsCell = (() => {
                              const raw = (c as any).contact_persons
                              if (Array.isArray(raw) && raw.length) {
                                return raw.map((p: any) => (typeof p === 'string' ? p : (p?.full_name || ''))).filter(Boolean).join('; ')
                              }
                              const sc = splitCoupleName(c.preferred_name || '')
                              return sc ? sc.persons.join('; ') : ''
                            })()
                            return [
                              c.preferred_name, c.preferred_email, c.preferred_phone, personsCell, c.preferred_city,
                              c.preferred_state, c.preferred_zip, c.client_type, c.match_status, c.data_quality,
                              c.deal_count, c.lead_count, c.legacy_total_invoiced, c.legacy_total_paid, c.legacy_balance,
                            ].map(escCsv).join(',')
                          })
                          csv = [headers.join(','), ...rows].join('\n')
                          filename = `${isCompanies ? 'companies' : 'contacts'}-export-${dateStr}.csv`
                        }

                        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
                        const url = URL.createObjectURL(blob)
                        const a = document.createElement('a')
                        a.href = url
                        a.download = filename
                        document.body.appendChild(a)
                        a.click()
                        document.body.removeChild(a)
                        URL.revokeObjectURL(url)
                      } catch (err) {
                        console.error('Export failed:', err)
                        alert('Export failed. Please try again.')
                      } finally {
                        setClientExporting(false)
                      }
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    <span>{clientExporting ? 'Exporting...' : 'Export'}</span>
                  </button>
              </>}
              toolbarRight={<>
                  {/* Calls: compact stats badge (calls-stats-badge) — total + missed%
                      always visible; hover/click expands the six-metric popover.
                      Deliberately in the toolbar row, OUTSIDE the table's x-scroll
                      container, so the stats can never ride the table's horizontal
                      scroll again (docs/REGRESSIONS.md 2026-07-01; the placement
                      guard greps for the scroll-container class token near
                      calls-analytics markup — keep that token out of comments). */}
                  {activeView === 'calls' && callAnalytics && (
                    <Suspense fallback={null}>
                      <CallsStatsBadge analytics={callAnalytics} />
                    </Suspense>
                  )}
                  {/* Clone + Save buttons hidden — redundant with the per-tab kebab menu */}
                  {false && <>
                  <div className="hs-tb-sep" />
                  {/* Clone */}
                  <button className="hs-tb-btn hs-tb-btn-bordered" title="Clone view" onClick={() => {
                    const currentFilter = activeView === 'contacts' ? clientStatusFilter : activeView === 'companies' ? companyStatusFilter : activeView === 'leads' ? leadStatusFilter : activeView === 'tickets' ? ticketsFilter : callsDirectionFilter
                    const currentCustom = (customViews[activeView] || []).find(v => `custom:${v.id}` === currentFilter)
                    const baseName = currentCustom ? currentCustom.label : (activeView === 'contacts' ? 'All contacts' : activeView === 'companies' ? 'All clients' : 'View')
                    const id = Date.now().toString(36)
                    const cloneName = `${baseName} (clone)`
                    setCustomViews(prev => ({
                      ...prev,
                      [activeView]: [...(prev[activeView] || []), { id, label: cloneName }]
                    }))
                    const setFilter = activeView === 'contacts' ? setClientStatusFilter : activeView === 'companies' ? setCompanyStatusFilter : activeView === 'leads' ? setLeadStatusFilter : activeView === 'tickets' ? setTicketsFilter : setCallsDirectionFilter
                    setFilter(`custom:${id}`)
                    setRenamingViewId(id)
                    setRenamingViewName(cloneName)
                  }}>
                    <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M25.86 29.29H12.15c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-1.89 1.54-3.43 3.43-3.43h13.71c1.89 0 3.43 1.54 3.43 3.43v13.71c0 1.89-1.54 3.43-3.43 3.43M12.14 11c-.63 0-1.14.51-1.14 1.14v13.71c0 .63.51 1.14 1.14 1.14h13.71c.63 0 1.14-.51 1.14-1.14V12.14c0-.63-.51-1.14-1.14-1.14z"/><path d="M5.29 22.43c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43H19c1.89 0 3.43 1.54 3.43 3.43a1.14 1.14 0 1 1-2.28 0c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14V19c0 .63.51 1.14 1.14 1.14a1.14 1.14 0 1 1 0 2.28Z"/></svg>
                  </button>
                  {/* Save */}
                  {(() => {
                    const currentFilter = activeView === 'contacts' ? clientStatusFilter : activeView === 'companies' ? companyStatusFilter : activeView === 'leads' ? leadStatusFilter : activeView === 'tickets' ? ticketsFilter : callsDirectionFilter
                    const isCustom = currentFilter.startsWith('custom:')
                    const hasFilters = quickFiltersOpen && Object.values(qfPillValues).some(v => v.length > 0)
                    const canSave = isCustom || hasFilters
                    return (
                      <button
                        className={`hs-tb-btn hs-tb-btn-bordered${!canSave ? ' disabled' : ''}`}
                        disabled={!canSave}
                        title="Save view"
                        onClick={() => {
                          if (!canSave) return
                          setSaveViewConfirm(true)
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                      </button>
                    )
                  })()}
                  {/* Save View Confirmation Modal */}
                  {saveViewConfirm && (() => {
                    const currentFilter = activeView === 'contacts' ? clientStatusFilter : activeView === 'companies' ? companyStatusFilter : activeView === 'leads' ? leadStatusFilter : activeView === 'tickets' ? ticketsFilter : callsDirectionFilter
                    const isCustom = currentFilter.startsWith('custom:')
                    const viewLabel = isCustom ? (customViews[activeView] || []).find(cv => `custom:${cv.id}` === currentFilter)?.label || 'this view' : 'the current view'
                    return (
                      <div className="create-panel-overlay" style={{zIndex:300}} onClick={() => setSaveViewConfirm(false)}>
                        <div className="je-modal" style={{maxWidth:400,margin:'20vh auto'}} onClick={e => e.stopPropagation()}>
                          <div className="je-modal-header">
                            <h3 style={{fontSize:'0.92rem',fontWeight:600,margin:0}}>Save view</h3>
                            <button onClick={() => setSaveViewConfirm(false)} style={{background:'none',border:'none',fontSize:'20px',cursor:'pointer',color:'#7c98b6'}}>&times;</button>
                          </div>
                          <div className="je-modal-body" style={{padding:'20px 24px'}}>
                            <p style={{fontSize:'0.85rem',color:'#334155',margin:0}}>
                              {isCustom
                                ? `Save the current filters and sort settings to "${viewLabel}"? This will overwrite the existing view configuration.`
                                : 'Save the current filters and sort settings as a new custom view?'}
                            </p>
                          </div>
                          <div style={{display:'flex',justifyContent:'flex-end',gap:8,padding:'12px 24px',borderTop:'1px solid #e5e7eb'}}>
                            <button className="cp-btn-secondary" onClick={() => setSaveViewConfirm(false)}>Cancel</button>
                            <button className="cp-btn-primary" disabled={saveViewSaving} onClick={() => {
                              setSaveViewSaving(true)
                              if (isCustom) {
                                const tabKey = `${activeView}:${currentFilter}`
                                setTabFilterStore(prev => ({ ...prev, [tabKey]: { ...qfPillValues } }))
                              } else {
                                const id = Date.now().toString(36)
                                const viewName = `Custom view ${(customViews[activeView] || []).length + 1}`
                                setCustomViews(prev => ({
                                  ...prev,
                                  [activeView]: [...(prev[activeView] || []), { id, label: viewName }]
                            }))
                            const tabKey = `${activeView}:custom:${id}`
                            setTabFilterStore(prev => ({ ...prev, [tabKey]: { ...qfPillValues } }))
                            const setFilter = activeView === 'contacts' ? setClientStatusFilter : activeView === 'companies' ? setCompanyStatusFilter : activeView === 'leads' ? setLeadStatusFilter : activeView === 'tickets' ? setTicketsFilter : setCallsDirectionFilter
                            setFilter(`custom:${id}`)
                            setRenamingViewId(id)
                            setRenamingViewName(viewName)
                          }
                              setTimeout(() => { setSaveViewConfirm(false); setSaveViewSaving(false) }, 500)
                            }}>{saveViewSaving ? 'Saving...' : isCustom ? 'Overwrite' : 'Save as new view'}</button>
                          </div>
                        </div>
                      </div>
                    )
                  })()}
                  </>}
              </>}
              quickFilters={quickFiltersOpen ? <>
                {/* Organizations: accounting-role quick filter */}
                {activeView === 'companies' && (
                  <>
                    <select
                      className="hs-qf-btn"
                      value={companyAccountingRoleFilter}
                      onChange={(e) => { setCompanyAccountingRoleFilter(e.target.value); setCompanyPage(1) }}
                      title="Filter by accounting role"
                    >
                      <option value="">All accounting roles</option>
                      <option value="customer">Customer</option>
                      <option value="vendor">Vendor</option>
                      <option value="both">Both</option>
                      <option value="neither">Neither</option>
                    </select>
                  </>
                )}
                {/* Contact owner — HubSpot-style typeahead with checkboxes */}
                <div className="hs-owner-dropdown" ref={ownerDropdownRef}>
                  <button
                    className={`hs-qf-btn hs-qf-trigger${ownerDropdownOpen ? ' open' : ''}${ownerSelected.size > 0 ? ' has-value' : ''}`}
                    onClick={() => setOwnerDropdownOpen(!ownerDropdownOpen)}
                  >
                    <span>{activeView === 'companies' ? 'Company owner' : activeView === 'calls' ? 'Call owner' : 'Contact owner'}</span>
                    {ownerSelected.size > 0 && <span className="hs-qf-count">{ownerSelected.size}</span>}
                    <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                  </button>
                  {ownerDropdownOpen && (
                    <div className="hs-owner-panel">
                      <div className="hs-owner-search-wrap">
                        <svg className="hs-owner-search-icon" width="13" height="13" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                        <input
                          className="hs-owner-search"
                          placeholder="Search"
                          value={ownerSearch}
                          onChange={(e) => setOwnerSearch(e.target.value)}
                          autoFocus
                        />
                      </div>
                      <div className="hs-owner-list">
                        {/* "Me" shortcut */}
                        {(!ownerSearch || 'me'.includes(ownerSearch.toLowerCase()) || 'max ritchie'.includes(ownerSearch.toLowerCase())) && (
                          <label className="hs-owner-item hs-owner-me">
                            <input
                              type="checkbox"
                              checked={ownerSelected.has(8)}
                              onChange={() => {
                                setOwnerSelected(prev => {
                                  const next = new Set(prev)
                                  next.has(8) ? next.delete(8) : next.add(8)
                                  return next
                                })
                              }}
                            />
                            <span className="hs-owner-checkbox" />
                            <span className="hs-owner-label">Me</span>
                          </label>
                        )}
                        {/* All users */}
                        {crmUsers
                          .filter(u => !ownerSearch || u.name.toLowerCase().includes(ownerSearch.toLowerCase()))
                          .map(user => (
                            <label className="hs-owner-item" key={user.id}>
                              <input
                                type="checkbox"
                                checked={ownerSelected.has(user.id)}
                                onChange={() => {
                                  setOwnerSelected(prev => {
                                    const next = new Set(prev)
                                    next.has(user.id) ? next.delete(user.id) : next.add(user.id)
                                    return next
                                  })
                                }}
                              />
                              <span className="hs-owner-checkbox" />
                              <span className="hs-owner-avatar">{user.initials || user.name.charAt(0)}</span>
                              <span className="hs-owner-label">{user.name}</span>
                            </label>
                          ))
                        }
                      </div>
                      {/* Unassigned footer */}
                      {(!ownerSearch || 'unassigned'.includes(ownerSearch.toLowerCase())) && (
                        <div className="hs-owner-footer">
                          <label className="hs-owner-item">
                            <input
                              type="checkbox"
                              checked={ownerSelected.has(0)}
                              onChange={() => {
                                setOwnerSelected(prev => {
                                  const next = new Set(prev)
                                  next.has(0) ? next.delete(0) : next.add(0)
                                  return next
                                })
                              }}
                            />
                            <span className="hs-owner-checkbox" />
                            <span className="hs-owner-label">Unassigned</span>
                          </label>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Call direction filter */}
                {activeView === 'calls' && (
                <button
                  className={`hs-qf-btn hs-qf-trigger${callsDirectionFilter ? ' has-value' : ''}`}
                  onClick={() => {
                    const vals = ['', '1', '2']
                    const idx = vals.indexOf(callsDirectionFilter)
                    setCallsDirectionFilter(vals[(idx + 1) % vals.length])
                    setCallsPage(1)
                  }}
                >
                  <span>{callsDirectionFilter === '1' ? 'Outbound' : callsDirectionFilter === '2' ? 'Inbound' : 'Call direction'}</span>
                  {callsDirectionFilter && (
                    <span className="hs-qf-clear" onClick={(e) => { e.stopPropagation(); setCallsDirectionFilter(''); setCallsPage(1) }}>&times;</span>
                  )}
                  {!callsDirectionFilter && <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>}
                </button>
                )}
                {/* Call outcome filter */}
                {activeView === 'calls' && rcOutcomes.length > 0 && (
                <button
                  className={`hs-qf-btn hs-qf-trigger${callsOutcomeFilter ? ' has-value' : ''}`}
                  onClick={() => {
                    const vals = ['', ...rcOutcomes.map(o => o.label)]
                    const idx = vals.indexOf(callsOutcomeFilter)
                    setCallsOutcomeFilter(vals[(idx + 1) % vals.length])
                    setCallsPage(1)
                  }}
                >
                  <span>{callsOutcomeFilter || 'Outcome'}</span>
                  {callsOutcomeFilter && (
                    <span className="hs-qf-clear" onClick={(e) => { e.stopPropagation(); setCallsOutcomeFilter(''); setCallsPage(1) }}>&times;</span>
                  )}
                  {!callsOutcomeFilter && <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>}
                </button>
                )}
                {/* Has recording toggle */}
                {activeView === 'calls' && (
                <button
                  className={`hs-qf-btn hs-qf-trigger${callsHasRecording ? ' has-value' : ''}`}
                  onClick={() => { setCallsHasRecording((p) => !p); setCallsPage(1) }}
                  title="Show only calls with recordings"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                  <span>{callsHasRecording ? 'Has recording' : 'Recording'}</span>
                  {callsHasRecording && <span className="hs-qf-clear" onClick={(e) => { e.stopPropagation(); setCallsHasRecording(false); setCallsPage(1) }}>&times;</span>}
                </button>
                )}
                {/* Has transcript toggle */}
                {activeView === 'calls' && (
                <button
                  className={`hs-qf-btn hs-qf-trigger${callsHasTranscript ? ' has-value' : ''}`}
                  onClick={() => { setCallsHasTranscript((p) => !p); setCallsPage(1) }}
                  title="Show only calls with transcripts"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                  <span>{callsHasTranscript ? 'Has transcript' : 'Transcript'}</span>
                  {callsHasTranscript && <span className="hs-qf-clear" onClick={(e) => { e.stopPropagation(); setCallsHasTranscript(false); setCallsPage(1) }}>&times;</span>}
                </button>
                )}
                {/* Status/priority filter */}
                {activeView === 'tickets' && (
                <button
                  className={`hs-qf-btn hs-qf-trigger${ticketsFilter !== 'all' ? ' has-value' : ''}`}
                  onClick={() => {
                    const vals = ['all', 'open', 'in_progress', 'waiting', 'escalated', 'resolved', 'closed']
                    const idx = vals.indexOf(ticketsFilter)
                    setTicketsFilter(vals[(idx + 1) % vals.length])
                    setTicketsPage(1)
                  }}
                >
                  <span>{ticketsFilter !== 'all' ? (ticketsFilter === 'in_progress' ? 'In Progress' : ticketsFilter.charAt(0).toUpperCase() + ticketsFilter.slice(1)) : 'Status'}</span>
                  {ticketsFilter !== 'all' && (
                    <span className="hs-qf-clear" onClick={(e) => { e.stopPropagation(); setTicketsFilter('all'); setTicketsPage(1) }}>&times;</span>
                  )}
                  {ticketsFilter === 'all' && <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>}
                </button>
                )}
                {activeView === 'leads' && (() => {
                  const selectedSet = new Set(leadStatusFilter.split(',').map(s => s.trim()).filter(Boolean))
                  const isSingleBuiltin = leadStatusFilter === 'CONVERTED' || leadStatusFilter === 'JUNK'
                  const label = (() => {
                    if (selectedSet.size === 0 || isSingleBuiltin) return 'Lead status'
                    if (selectedSet.size === 1) {
                      const only = Array.from(selectedSet)[0]
                      return leadStatuses.find(s => s.status_id === only)?.name || 'Lead status'
                    }
                    return 'Lead status'
                  })()
                  const showCount = selectedSet.size > 1 || (selectedSet.size === 1 && !isSingleBuiltin && !leadStatuses.find(s => s.status_id === Array.from(selectedSet)[0]))
                  const toggle = (id: string) => {
                    const next = new Set(selectedSet)
                    if (next.has(id)) next.delete(id); else next.add(id)
                    setLeadStatusFilter(Array.from(next).join(','))
                    setLeadPage(1)
                  }
                  return (
                    <div className="qf-pill-wrap" ref={leadStatusDropdownOpen ? leadStatusDropdownRef : undefined}>
                      <button
                        className={`hs-qf-btn hs-qf-trigger${selectedSet.size > 0 ? ' has-value' : ''}${leadStatusDropdownOpen ? ' open' : ''}`}
                        onClick={() => setLeadStatusDropdownOpen(o => !o)}
                      >
                        <span>{label}</span>
                        {showCount && <span className="hs-qf-count">{selectedSet.size}</span>}
                        {selectedSet.size > 0 && !isSingleBuiltin && (
                          <span className="hs-qf-clear" onClick={(e) => { e.stopPropagation(); setLeadStatusFilter(''); setLeadPage(1); setLeadStatusDropdownOpen(false) }}>&times;</span>
                        )}
                        {selectedSet.size === 0 && <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>}
                      </button>
                      {leadStatusDropdownOpen && (
                        <div className="qf-pill-dropdown" style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4, minWidth: 220, maxHeight: 360, overflowY: 'auto', background: '#fff', border: '1px solid #cbd6e2', borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', zIndex: 50, padding: 6 }}>
                          {selectedSet.size > 0 && (
                            <button
                              type="button"
                              onClick={() => { setLeadStatusFilter(''); setLeadPage(1) }}
                              style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 11, fontWeight: 600, cursor: 'pointer', padding: '4px 6px', display: 'block', width: '100%', textAlign: 'left' }}
                            >Clear all</button>
                          )}
                          {leadPipelineStatuses.map(s => (
                            <label key={s.status_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 4, cursor: 'pointer', fontSize: 12, color: '#0f172a' }}>
                              <input
                                type="checkbox"
                                checked={selectedSet.has(s.status_id)}
                                onChange={() => toggle(s.status_id)}
                              />
                              {s.name}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })()}
                {/* Lead status-badge multi-select (mirrors the title kebab filter) */}
                {activeView === 'leads' && (() => {
                  const BADGE_OPTS = [
                    { key: 'open-task', label: 'Open task' },
                    { key: 'no-contact', label: 'No contact info' },
                    { key: 'call-overdue', label: 'Call overdue' },
                    { key: 'email-overdue', label: 'Email overdue' },
                    { key: 'both-overdue', label: 'Call+Email overdue' },
                    { key: 'ok', label: 'Up to date' },
                  ]
                  return (
                    <div className="qf-pill-wrap" ref={leadBadgeDropdownOpen ? leadBadgeDropdownRef : undefined}>
                      <button
                        className={`hs-qf-btn hs-qf-trigger${leadBadgeFilter.size > 0 ? ' has-value' : ''}${leadBadgeDropdownOpen ? ' open' : ''}`}
                        onClick={() => setLeadBadgeDropdownOpen(o => !o)}
                      >
                        <span>Status badge</span>
                        {leadBadgeFilter.size > 0 && <span className="hs-qf-count">{leadBadgeFilter.size}</span>}
                        <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                      </button>
                      {leadBadgeDropdownOpen && (
                        <div className="qf-pill-dropdown" style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4, minWidth: 220, background: '#fff', border: '1px solid #cbd6e2', borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', zIndex: 50, padding: 6 }}>
                          {leadBadgeFilter.size > 0 && (
                            <button
                              type="button"
                              onClick={() => setLeadBadgeFilter(new Set())}
                              style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 11, fontWeight: 600, cursor: 'pointer', padding: '4px 6px', display: 'block', width: '100%', textAlign: 'left' }}
                            >Clear filter</button>
                          )}
                          {BADGE_OPTS.map(o => (
                            <label key={o.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 4, cursor: 'pointer', fontSize: 12, color: '#0f172a' }}>
                              <input
                                type="checkbox"
                                checked={leadBadgeFilter.has(o.key)}
                                onChange={() => {
                                  setLeadBadgeFilter(prev => {
                                    const next = new Set(prev)
                                    if (next.has(o.key)) next.delete(o.key); else next.add(o.key)
                                    return next
                                  })
                                }}
                              />
                              {o.label}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })()}

                {/* Deal-specific quick filter pills — match contacts dropdown look */}
                {activeView === 'deals' && (() => {
                  const ownerOptions = Array.from(new Set((liveDeals ?? []).map((d: any) => String(d.assignedTo || '')).filter(Boolean))).sort()
                  const stageOptions = (allPipelineStages ?? []).map((s: any) => ({ value: s.stageId, label: s.label, color: s.color }))
                  const amountOptions = [
                    { value: 'lt5k', label: '< $5K' },
                    { value: '5k-25k', label: '$5K – $25K' },
                    { value: '25k-100k', label: '$25K – $100K' },
                    { value: 'gt100k', label: '> $100K' },
                  ]
                  const dateOptions = [
                    { value: 'overdue', label: 'Overdue' },
                    { value: 'today', label: 'Today' },
                    { value: 'tomorrow', label: 'Tomorrow' },
                    { value: 'this-week', label: 'Next 7 days' },
                    { value: 'this-month', label: 'Next 30 days' },
                  ]
                  type Pill = { key: string; label: string; kind: 'multi'|'single'; options: any[] }
                  const pills: Pill[] = [
                    { key: 'owner',  label: 'Deal owner', kind: 'multi',  options: ownerOptions.map(o => ({ value: o, label: o })) },
                    { key: 'stage',  label: 'Stage',      kind: 'multi',  options: stageOptions },
                    { key: 'amount', label: 'Amount',     kind: 'single', options: amountOptions },
                    { key: 'close',  label: 'Appt. date', kind: 'single', options: dateOptions },
                  ]
                  return pills.map((p) => {
                    const isOpen = dealQfPillOpen === p.key
                    const selected: string[] =
                      p.key === 'owner'  ? Array.from(dealOwnerFilter) :
                      p.key === 'stage'  ? Array.from(dealStageFilter) :
                      p.key === 'amount' ? (dealAmountFilter ? [dealAmountFilter] : []) :
                                            (dealCloseDateFilter ? [dealCloseDateFilter] : [])
                    const hasValue = selected.length > 0
                    const labelOf = (v: string) => p.options.find((o: any) => o.value === v)?.label || v
                    const toggle = (v: string) => {
                      if (p.key === 'owner') {
                        setDealOwnerFilter(prev => { const n = new Set(prev); n.has(v) ? n.delete(v) : n.add(v); return n })
                      } else if (p.key === 'stage') {
                        setDealStageFilter(prev => { const n = new Set(prev); n.has(v) ? n.delete(v) : n.add(v); return n })
                      } else if (p.key === 'amount') {
                        setDealAmountFilter(dealAmountFilter === v ? '' : v); setDealQfPillOpen(null)
                      } else {
                        setDealCloseDateFilter(dealCloseDateFilter === v ? '' : v); setDealQfPillOpen(null)
                      }
                    }
                    const clear = () => {
                      if (p.key === 'owner') setDealOwnerFilter(new Set())
                      else if (p.key === 'stage') setDealStageFilter(new Set())
                      else if (p.key === 'amount') setDealAmountFilter('')
                      else setDealCloseDateFilter('')
                      setDealQfPillOpen(null)
                    }
                    return (
                      <div key={p.key} className="qf-pill-wrap" ref={isOpen ? dealQfPillRef : undefined}>
                        <button
                          className={`hs-qf-btn hs-qf-trigger${isOpen ? ' open' : ''}${hasValue ? ' has-value' : ''}`}
                          onClick={() => setDealQfPillOpen(isOpen ? null : p.key)}
                        >
                          <span>{p.label}{hasValue && p.kind === 'single' ? `: ${labelOf(selected[0])}` : ''}</span>
                          {hasValue && p.kind === 'multi' && <span className="hs-qf-count">{selected.length}</span>}
                          <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                        </button>
                        {hasValue && (
                          <button className="hs-qf-clear-btn" title={`Clear ${p.label} filter`} onClick={(e) => { e.stopPropagation(); clear() }}>
                            <svg width="10" height="10" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                          </button>
                        )}
                        {isOpen && (
                          <div className="qf-pill-dropdown" ref={(el) => {
                            if (!el) return
                            const wrap = el.parentElement
                            if (!wrap) return
                            const btn = wrap.querySelector('button')
                            if (!btn) return
                            const rect = btn.getBoundingClientRect()
                            const popW = Math.min(280, window.innerWidth - 32)
                            let left = rect.left
                            if (left + popW > window.innerWidth - 16) left = window.innerWidth - 16 - popW
                            if (left < 16) left = 16
                            el.style.position = 'fixed'
                            el.style.top = `${rect.bottom + 4}px`
                            el.style.left = `${left}px`
                            el.style.right = 'auto'
                          }}>
                            <div className="qf-pill-list">
                              {hasValue && (
                                <button className="qf-pill-date-option" style={{ color: '#d94c53', borderBottom: '1px solid #eaf0f6', marginBottom: 4, paddingBottom: 8 }} onClick={clear}>
                                  <span className="qf-pill-date-label">Clear filter</span>
                                </button>
                              )}
                              {p.options.map((opt: any) => (
                                p.kind === 'multi' ? (
                                  <label key={opt.value} className="hs-owner-item">
                                    <input type="checkbox" checked={selected.includes(opt.value)} onChange={() => toggle(opt.value)} />
                                    <span className="hs-owner-checkbox" />
                                    {opt.color && <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: opt.color, marginRight: 6 }} />}
                                    <span className="hs-owner-label">{opt.label}</span>
                                  </label>
                                ) : (
                                  <button key={opt.value} className={`qf-pill-date-option${selected.includes(opt.value) ? ' active' : ''}`} onClick={() => toggle(opt.value)}>
                                    <span className="qf-pill-date-label">{opt.label}</span>
                                  </button>
                                )
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })
                })()}

                {/* Sort by */}
                {activeView === 'calls' && (
                <button
                  className={`hs-qf-btn hs-qf-trigger${callsSort.field !== 'created' ? ' has-value' : ''}`}
                  onClick={() => {
                    const fields = ['created', 'subject', 'direction', 'responsible_id', 'last_updated']
                    const idx = fields.indexOf(callsSort.field)
                    const next = fields[(idx + 1) % fields.length]
                    setCallsSort({ field: next, order: next === 'subject' ? 'ASC' : 'DESC' })
                    setCallsPage(1)
                  }}
                >
                  <span>Sort: {{ created: 'Date', subject: 'Subject', direction: 'Direction', responsible_id: 'Assigned', last_updated: 'Updated' }[callsSort.field] || 'Date'}</span>
                  <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                </button>
                )}
                {activeView === 'tickets' && (
                <button
                  className={`hs-qf-btn hs-qf-trigger${ticketsSort.field !== 'created_at' ? ' has-value' : ''}`}
                  onClick={() => {
                    const fields = ['created_at', 'title', 'priority', 'updated_at', 'status']
                    const idx = fields.indexOf(ticketsSort.field)
                    const next = fields[(idx + 1) % fields.length]
                    setTicketsSort({ field: next, order: next === 'title' ? 'ASC' : 'DESC' })
                    setTicketsPage(1)
                  }}
                >
                  <span>Sort: {{ created_at: 'Created', title: 'Title', priority: 'Priority', updated_at: 'Updated', status: 'Status' }[ticketsSort.field] || 'Created'}</span>
                  <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                </button>
                )}
                {activeView === 'leads' && (() => {
                  const SORT_OPTIONS: { field: string; label: string; defaultOrder: 'ASC' | 'DESC' }[] = [
                    { field: 'date_modify', label: 'Modified', defaultOrder: 'DESC' },
                    { field: 'date_create', label: 'Created', defaultOrder: 'DESC' },
                    { field: 'title', label: 'Title', defaultOrder: 'ASC' },
                    { field: 'customer_name', label: 'Contact', defaultOrder: 'ASC' },
                  ]
                  const currentLabel = SORT_OPTIONS.find(o => o.field === leadSort.field)?.label || 'Modified'
                  return (
                    <div className="qf-pill-wrap" ref={leadSortDropdownOpen ? leadSortDropdownRef : undefined}>
                      <button
                        className={`hs-qf-btn hs-qf-trigger${leadSort.field !== 'date_modify' ? ' has-value' : ''}${leadSortDropdownOpen ? ' open' : ''}`}
                        onClick={() => setLeadSortDropdownOpen(o => !o)}
                      >
                        <span>Sort: {currentLabel} {leadSort.order === 'ASC' ? '↑' : '↓'}</span>
                        <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                      </button>
                      {leadSortDropdownOpen && (
                        <div className="qf-pill-dropdown" style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4, minWidth: 220, background: '#fff', border: '1px solid #cbd6e2', borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', zIndex: 50 }}>
                          <div className="qf-pill-list">
                            {SORT_OPTIONS.map(o => (
                              <React.Fragment key={o.field}>
                                <button
                                  className={`qf-pill-date-option${leadSort.field === o.field && leadSort.order === 'ASC' ? ' active' : ''}`}
                                  onClick={() => { setLeadSort({ field: o.field, order: 'ASC' }); setLeadPage(1); setLeadSortDropdownOpen(false) }}
                                >
                                  <span className="qf-pill-date-label">{o.label} ↑</span>
                                </button>
                                <button
                                  className={`qf-pill-date-option${leadSort.field === o.field && leadSort.order === 'DESC' ? ' active' : ''}`}
                                  onClick={() => { setLeadSort({ field: o.field, order: 'DESC' }); setLeadPage(1); setLeadSortDropdownOpen(false) }}
                                >
                                  <span className="qf-pill-date-label">{o.label} ↓</span>
                                </button>
                              </React.Fragment>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })()}

                {/* Active quick filter pills (added via + More) */}
                {activeQuickFilters.map((fk) => {
                  const allItems = quickFilterProperties.flatMap(g => g.items)
                  const prop = allItems.find(i => i.key === fk)
                  if (!prop) return null
                  const selectedVals = qfPillValues[fk] || []
                  const isOpen = qfPillOpen === fk
                  return (
                    <div key={fk} className="qf-pill-wrap" ref={isOpen ? qfPillRef : undefined}>
                      <button
                        className={`hs-qf-btn hs-qf-trigger${isOpen ? ' open' : ''}${selectedVals.length > 0 ? ' has-value' : ''}`}
                        onClick={() => { setQfPillOpen(isOpen ? null : fk); setQfPillSearch('') }}
                      >
                        <span>{prop.label}</span>
                        {selectedVals.length > 0 && <span className="hs-qf-count">{selectedVals.length}</span>}
                        <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                      </button>
                      {selectedVals.length > 0 && (
                        <button
                          className="hs-qf-clear-btn"
                          title={`Clear ${prop.label} filter`}
                          onClick={(e) => { e.stopPropagation(); setQfPillValues(prev => ({ ...prev, [fk]: [] })); setQfPillOpen(null) }}
                        >
                          <svg width="10" height="10" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                        </button>
                      )}
                      {isOpen && (
                        <div className="qf-pill-dropdown" ref={(el) => {
                          if (!el) return
                          const wrap = el.parentElement
                          if (!wrap) return
                          const btn = wrap.querySelector('button')
                          if (!btn) return
                          const rect = btn.getBoundingClientRect()
                          const popW = Math.min(280, window.innerWidth - 32)
                          let left = rect.left
                          if (left + popW > window.innerWidth - 16) left = window.innerWidth - 16 - popW
                          if (left < 16) left = 16
                          el.style.position = 'fixed'
                          el.style.top = `${rect.bottom + 4}px`
                          el.style.left = `${left}px`
                          el.style.right = 'auto'
                        }}>
                          {/* Only show search for large enum lists (not text filters — they have their own input below) */}
                          {(prop.icon === 'enum' && (qfEnumOptions[fk]?.length || 0) > 5) && (
                            <div className="qf-pill-search-wrap">
                              <svg className="qf-pill-search-icon" width="13" height="13" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                              <input
                                className="qf-pill-search"
                                placeholder="Search"
                                value={qfPillSearch}
                                onChange={(e) => setQfPillSearch(e.target.value)}
                                autoFocus
                              />
                            </div>
                          )}

                          {/* Enum type — checkbox list */}
                          {prop.icon === 'enum' && qfEnumOptions[fk] && (
                            <div className="qf-pill-list">
                              {selectedVals.length > 0 && (
                                <button
                                  className="qf-pill-date-option"
                                  style={{ color: '#d94c53', borderBottom: '1px solid #eaf0f6', marginBottom: 4, paddingBottom: 8 }}
                                  onClick={() => { setQfPillValues(prev => ({ ...prev, [fk]: [] })); setQfPillOpen(null) }}
                                >
                                  <span className="qf-pill-date-label">Clear filter</span>
                                </button>
                              )}
                              {qfEnumOptions[fk]
                                .filter(o => !qfPillSearch || o.label.toLowerCase().includes(qfPillSearch.toLowerCase()))
                                .map(opt => (
                                  <label key={opt.value} className="hs-owner-item">
                                    <input
                                      type="checkbox"
                                      checked={selectedVals.includes(opt.value)}
                                      onChange={() => toggleQfPillValue(fk, opt.value)}
                                    />
                                    <span className="hs-owner-checkbox" />
                                    <span className="hs-owner-label">{opt.label}</span>
                                  </label>
                                ))
                              }
                            </div>
                          )}

                          {/* Date type — preset list */}
                          {prop.icon === 'date' && (
                            <div className="qf-pill-list">
                              {selectedVals.length > 0 && (
                                <button
                                  className="qf-pill-date-option"
                                  style={{ color: '#d94c53', borderBottom: '1px solid #eaf0f6', marginBottom: 4, paddingBottom: 8 }}
                                  onClick={() => { setQfPillValues(prev => ({ ...prev, [fk]: [] })); setQfPillOpen(null) }}
                                >
                                  <span className="qf-pill-date-label">Clear filter</span>
                                </button>
                              )}
                              {qfDatePresets
                                .filter(p => (!pastOnlyDateFields.includes(fk) || !p.future) && (!qfPillSearch || p.label.toLowerCase().includes(qfPillSearch.toLowerCase())))
                                .map(preset => (
                                  <button
                                    key={preset.value}
                                    className={`qf-pill-date-option${selectedVals.includes(preset.value) ? ' active' : ''}`}
                                    onClick={() => {
                                      setQfPillValues(prev => ({ ...prev, [fk]: [preset.value] }))
                                      setQfPillOpen(null)
                                    }}
                                  >
                                    <span className="qf-pill-date-label">{preset.label}</span>
                                    <small className="qf-pill-date-desc">{preset.desc}</small>
                                  </button>
                                ))
                              }
                            </div>
                          )}

                          {/* Number type — preset ranges */}
                          {prop.icon === 'number' && (
                            <div className="qf-pill-list">
                              {selectedVals.length > 0 && (
                                <button
                                  className="qf-pill-date-option"
                                  style={{ color: '#d94c53', borderBottom: '1px solid #eaf0f6', marginBottom: 4, paddingBottom: 8 }}
                                  onClick={() => { setQfPillValues(prev => ({ ...prev, [fk]: [] })); setQfPillOpen(null) }}
                                >
                                  <span className="qf-pill-date-label">Clear filter</span>
                                </button>
                              )}
                              {((['legacy_total_invoiced','legacy_total_paid','legacy_balance'].includes(fk))
                                ? [
                                    { value: 'gt_0', label: 'Greater than $0' },
                                    { value: 'eq_0', label: 'Equal to $0' },
                                    { value: 'gt_1000', label: 'Greater than $1,000' },
                                    { value: 'gt_5000', label: 'Greater than $5,000' },
                                    { value: 'gt_10000', label: 'Greater than $10,000' },
                                    { value: 'gt_50000', label: 'Greater than $50,000' },
                                    { value: 'gt_100000', label: 'Greater than $100,000' },
                                  ]
                                : [
                                    { value: 'gt_0', label: 'Greater than 0' },
                                    { value: 'eq_0', label: 'Equal to 0' },
                                    { value: 'gte_1', label: '1 or more' },
                                    { value: 'gte_2', label: '2 or more' },
                                    { value: 'gte_3', label: '3 or more' },
                                    { value: 'gte_5', label: '5 or more' },
                                    { value: 'gte_10', label: '10 or more' },
                                    { value: 'gte_25', label: '25 or more' },
                                  ]
                              )
                                .filter(o => !qfPillSearch || o.label.toLowerCase().includes(qfPillSearch.toLowerCase()))
                                .map(opt => (
                                  <button
                                    key={opt.value}
                                    className={`qf-pill-date-option${selectedVals.includes(opt.value) ? ' active' : ''}`}
                                    onClick={() => {
                                      setQfPillValues(prev => ({ ...prev, [fk]: [opt.value] }))
                                      setQfPillOpen(null)
                                    }}
                                  >
                                    <span className="qf-pill-date-label">{opt.label}</span>
                                  </button>
                                ))
                              }
                            </div>
                          )}

                          {/* Text type — value input */}
                          {prop.icon === 'text' && (
                            <div className="qf-pill-list" style={{ padding: '8px 12px' }}>
                              <label style={{ fontSize: '0.78rem', color: '#516f90', marginBottom: 4, display: 'block' }}>Contains</label>
                              <input
                                className="qf-pill-text-input"
                                placeholder={`Filter by ${prop.label.toLowerCase()}...`}
                                value={selectedVals[0] || ''}
                                onChange={(e) => setQfPillValues(prev => ({ ...prev, [fk]: e.target.value ? [e.target.value] : [] }))}
                                onKeyDown={(e) => { if (e.key === 'Enter') setQfPillOpen(null) }}
                                autoFocus
                              />
                              {selectedVals.length > 0 && (
                                <button
                                  style={{ marginTop: 6, fontSize: '0.78rem', color: '#d94c53', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                                  onClick={() => { setQfPillValues(prev => ({ ...prev, [fk]: [] })); setQfPillOpen(null) }}
                                >
                                  Clear filter
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}

                {/* Clear-all-filters — only renders when any filter has a value */}
                {(() => {
                  const isLeads = activeView === 'leads'
                  const hasOwner = ownerSelected.size > 0
                  const hasPills = Object.values(qfPillValues).some(v => v && v.length > 0)
                  const hasLeadStatus = isLeads && !!leadStatusFilter && leadStatusFilter !== 'CONVERTED' && leadStatusFilter !== 'JUNK'
                  const hasLeadBadges = isLeads && leadBadgeFilter.size > 0
                  const hasLeadSort = isLeads && (leadSort.field !== 'date_modify' || leadSort.order !== 'DESC')
                  const hasIncToggles = isLeads && (leadIncludeConverted || leadIncludeDisqualified)
                  const anyActive = hasOwner || hasPills || hasLeadStatus || hasLeadBadges || hasLeadSort || hasIncToggles
                  if (!anyActive) return null
                  return (
                    <button
                      type="button"
                      onClick={() => {
                        setOwnerSelected(new Set())
                        setQfPillValues({})
                        if (isLeads) {
                          setLeadStatusFilter('')
                          setLeadBadgeFilter(new Set())
                          setLeadSort({ field: 'date_modify', order: 'DESC' })
                          setLeadIncludeConverted(false)
                          setLeadIncludeDisqualified(false)
                          setActiveLeadCustomViewId(null)
                          setLeadPage(1)
                        }
                      }}
                      style={{ padding: '4px 10px', fontSize: 12, fontWeight: 600, color: '#fff', background: '#dc2626', border: '1px solid #dc2626', borderRadius: 16, cursor: 'pointer' }}
                    >
                      × Clear all filters
                    </button>
                  )
                })()}

                <div className="hs-qf-sep" />

                {/* "+ More" quick filter popover */}
                <div className="hs-qf-more-wrap" ref={qfMoreRef}>
                  <button className={`hs-qf-btn hs-qf-more${qfMoreOpen ? ' open' : ''}`} onClick={() => { setQfMoreOpen(!qfMoreOpen); setQfMoreSearch(''); setQfMoreView('add') }}>
                    <svg width="10" height="10" viewBox="0 0 32 32" fill="currentColor"><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V3a1.14 1.14 0 1 1 2.28 0v25.14c0 .63-.51 1.14-1.14 1.14Z"/><path d="M28.14 16.71H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28"/></svg>
                    <span>More</span>
                  </button>
                  {qfMoreOpen && (
                    <div className="qf-more-popover" ref={(el) => {
                      if (!el) return
                      const btn = qfMoreRef.current?.querySelector('button')
                      const bar = document.querySelector('.hs-quick-filters')
                      if (!btn) return
                      const rect = btn.getBoundingClientRect()
                      const minLeft = bar ? bar.getBoundingClientRect().left : rect.left
                      const popW = Math.min(400, window.innerWidth - minLeft - 16)
                      let left = rect.left
                      if (left + popW > window.innerWidth - 16) left = window.innerWidth - 16 - popW
                      if (left < minLeft) left = minLeft
                      const spaceBelow = window.innerHeight - rect.bottom - 16
                      const maxH = Math.max(200, spaceBelow - 6)
                      el.style.position = 'fixed'
                      el.style.top = `${rect.bottom + 6}px`
                      el.style.left = `${left}px`
                      el.style.right = 'auto'
                      el.style.maxHeight = `${maxH}px`
                    }}>
                      {qfMoreView === 'add' ? (
                        <>
                          <div className="qf-more-top">
                            <button className="qf-more-edit-btn" onClick={() => setQfMoreView('edit')}>
                              <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M10.46 29.32H3c-.63 0-1.14-.51-1.14-1.14v-7.46c0-.3.12-.59.33-.81L19.24 2.86c.65-.65 1.51-1 2.42-1 .92 0 1.78.36 2.43 1.01l4.21 4.24a3.443 3.443 0 0 1 0 4.84L11.27 28.98c-.21.21-.5.33-.81.33Zm-6.32-2.29h5.84l16.7-16.7c.44-.44.45-1.17 0-1.61l-4.21-4.24c-.22-.22-.5-.34-.81-.34s-.59.12-.81.33L4.14 21.19z"/><path d="M25.01 14.76c-.29 0-.58-.11-.81-.33l-7.46-7.46c-.45-.45-.45-1.17 0-1.62s1.17-.45 1.62 0l7.46 7.46a1.14 1.14 0 0 1-.81 1.95"/></svg>
                              <span>Edit quick filters</span>
                            </button>
                          </div>
                          <div className="qf-more-body">
                            <h5 className="qf-more-heading">Add a quick filter</h5>
                            <div className="qf-more-search-wrap">
                              <svg className="qf-more-search-icon" width="14" height="14" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                              <input
                                className="qf-more-search"
                                placeholder="Search"
                                value={qfMoreSearch}
                                onChange={(e) => setQfMoreSearch(e.target.value)}
                                autoFocus
                              />
                            </div>
                          </div>
                          <div className="qf-more-list">
                            {quickFilterProperties.map((group) => {
                              const filtered = group.items.filter(i =>
                                !activeQuickFilters.includes(i.key) &&
                                (!qfMoreSearch || i.label.toLowerCase().includes(qfMoreSearch.toLowerCase()))
                              )
                              if (filtered.length === 0) return null
                              return (
                                <div key={group.category}>
                                  <div className="qf-more-category">{group.category}</div>
                                  {filtered.map((item) => (
                                    <button
                                      key={item.key}
                                      className="qf-more-option"
                                      onClick={() => {
                                        setActiveQuickFilters(prev => [...prev, item.key])
                                        setQfMoreOpen(false)
                                        setQfMoreSearch('')
                                      }}
                                    >
                                      <span className="qf-more-option-icon">
                                        {item.icon === 'text' && (
                                          <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M5.53 10.52h2.84l4.55 12.05h-2.53l-.86-2.39H4.38l-.86 2.39H1zm3.27 7.59-1.84-5.15-1.84 5.15h3.69ZM16.28 21.33v1.24h-2.31V10h2.31v4.6c.59-.9 1.69-1.41 2.96-1.41 2.39 0 4.32 2 4.32 4.77s-1.93 4.79-4.32 4.79c-1.27 0-2.38-.52-2.96-1.41Zm5-3.35c0-1.55-1.1-2.74-2.55-2.74s-2.53 1.19-2.53 2.74 1.09 2.72 2.53 2.72 2.55-1.19 2.55-2.72M24.52 17.98c0-2.77 1.96-4.79 4.74-4.79.98 0 1.81.19 2.62.62v2.22c-.74-.5-1.46-.79-2.26-.79-1.65 0-2.77 1.17-2.77 2.74s1.12 2.72 2.77 2.72c.79 0 1.53-.34 2.26-.79v2.22c-.81.41-1.64.62-2.62.62-2.77 0-4.74-2.01-4.74-4.77"/></svg>
                                        )}
                                        {item.icon === 'number' && (
                                          <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M13.14 29.29c-.55 0-1.02-.39-1.12-.93l-1.37-7.22H4.57a1.14 1.14 0 1 1 0-2.28h5.63l-1.09-5.71H3a1.14 1.14 0 1 1 0-2.28h5.66L9.97 3.79a1.14 1.14 0 0 1 2.25-.38L13.67 11h5.71l-1.35-7.08a1.14 1.14 0 0 1 2.25-.38l1.44 7.46h5.71a1.14 1.14 0 1 1 0 2.28h-5.27l1.09 5.71H29a1.14 1.14 0 1 1 0 2.28h-5.31l1.34 7.08a1.14 1.14 0 0 1-2.25.38L21.34 21.14h-5.71l1.34 7.08a1.14 1.14 0 0 1-.93 1.32 1.22 1.22 0 0 1-.2.02h-.7zM14.11 13.14l1.09 5.71h5.71l-1.09-5.71z"/></svg>
                                        )}
                                        {item.icon === 'date' && (
                                          <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M9.86 8.71c-.63 0-1.14-.51-1.14-1.14V3A1.14 1.14 0 1 1 11 3v4.57c0 .63-.51 1.14-1.14 1.14M21.29 8.71c-.63 0-1.14-.51-1.14-1.14V3a1.14 1.14 0 1 1 2.28 0v4.57c0 .63-.51 1.14-1.14 1.14"/><path d="M25.86 29.28H5.29c-1.89 0-3.43-1.54-3.43-3.43V7.57c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v18.28c0 1.89-1.54 3.43-3.43 3.43ZM5.29 6.43c-.63 0-1.14.51-1.14 1.14v18.28c0 .63.51 1.14 1.14 1.14h20.57c.63 0 1.14-.51 1.14-1.14V7.57c0-.63-.51-1.14-1.14-1.14z"/><path d="M29 13.28H3A1.14 1.14 0 1 1 3 11h26a1.14 1.14 0 1 1 0 2.28"/></svg>
                                        )}
                                        {item.icon === 'enum' && (
                                          <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M23.57 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V7.57a1.14 1.14 0 1 1 2.28 0v18.29c0 .63.51 1.14 1.14 1.14h18.29a1.14 1.14 0 1 1 0 2.28Z"/><path d="M25.86 24.71h-16c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43h16c1.89 0 3.43 1.54 3.43 3.43v16c0 1.89-1.54 3.43-3.43 3.43Zm-16-20.57c-.63 0-1.14.51-1.14 1.14v16c0 .63.51 1.14 1.14 1.14h16c.63 0 1.14-.51 1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14h-16Z"/><path d="M16.84 17.48c-.3 0-.59-.12-.81-.33l-3.56-3.56a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l2.71 2.71 4.78-5.21c.43-.46 1.15-.5 1.61-.07.47.43.5 1.15.07 1.62l-5.59 6.09c-.21.23-.51.36-.82.37h-.02Z"/></svg>
                                        )}
                                      </span>
                                      <span className="qf-more-option-label">{item.label}</span>
                                    </button>
                                  ))}
                                </div>
                              )
                            })}
                            {quickFilterProperties.every(g => g.items.every(i => activeQuickFilters.includes(i.key) || (qfMoreSearch && !i.label.toLowerCase().includes(qfMoreSearch.toLowerCase())))) && (
                              <div className="qf-more-empty">{activeQuickFilters.length > 0 && !qfMoreSearch ? 'All filters added' : 'No matching properties'}</div>
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          {/* Edit quick filters view */}
                          <div className="qf-edit-header">
                            <button className="qf-edit-back-btn" onClick={() => setQfMoreView('add')}>
                              <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95"/></svg>
                              <span>Back</span>
                            </button>
                            <button className="qf-edit-add-btn" onClick={() => setQfMoreView('add')}>
                              <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V3a1.14 1.14 0 1 1 2.28 0v25.14c0 .63-.51 1.14-1.14 1.14Z"/><path d="M28.14 16.71H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28"/></svg>
                              <span>Add</span>
                            </button>
                          </div>
                          <div className="qf-edit-title">Edit quick filters</div>
                          <div className="qf-edit-list">
                            {activeQuickFilters.map((fk, idx) => {
                              const allItems = quickFilterProperties.flatMap(g => g.items)
                              const prop = allItems.find(i => i.key === fk)
                              if (!prop) return null
                              return (
                                <div
                                  key={fk}
                                  className={`qf-edit-item${qfDragIdx === idx ? ' dragging' : ''}${qfDragOverIdx === idx ? ' drag-over' : ''}`}
                                  draggable
                                  onDragStart={() => setQfDragIdx(idx)}
                                  onDragOver={(e) => { e.preventDefault(); setQfDragOverIdx(idx) }}
                                  onDragEnd={() => {
                                    if (qfDragIdx !== null && qfDragOverIdx !== null && qfDragIdx !== qfDragOverIdx) {
                                      setActiveQuickFilters(prev => {
                                        const next = [...prev]
                                        const [moved] = next.splice(qfDragIdx, 1)
                                        next.splice(qfDragOverIdx, 0, moved)
                                        return next
                                      })
                                    }
                                    setQfDragIdx(null)
                                    setQfDragOverIdx(null)
                                  }}
                                >
                                  <span className="qf-edit-drag">
                                    <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><circle cx="10.14" cy="4.14" r="2.29"/><circle cx="10.14" cy="15.57" r="2.29"/><circle cx="10.14" cy="27" r="2.29"/><circle cx="21.57" cy="4.14" r="2.29"/><circle cx="21.57" cy="15.57" r="2.29"/><circle cx="21.57" cy="27" r="2.29"/></svg>
                                  </span>
                                  <span className="qf-edit-label">{prop.label}</span>
                                  <button className="qf-edit-delete" onClick={() => {
                                    setActiveQuickFilters(prev => prev.filter(k => k !== fk))
                                    setQfPillValues(prev => { const n = { ...prev }; delete n[fk]; return n })
                                  }}>
                                    <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M25 8H7c-.55 0-1-.45-1-1s.45-1 1-1h18c.55 0 1 .45 1 1s-.45 1-1 1M13 24c-.55 0-1-.45-1-1V13c0-.55.45-1 1-1s1 .45 1 1v10c0 .55-.45 1-1 1M19 24c-.55 0-1-.45-1-1V13c0-.55.45-1 1-1s1 .45 1 1v10c0 .55-.45 1-1 1"/><path d="M20 8h-8c-.55 0-1-.45-1-1V5c0-1.65 1.35-3 3-3h4c1.65 0 3 1.35 3 3v2c0 .55-.45 1-1 1m-7-2h6V5c0-.55-.45-1-1-1h-4c-.55 0-1 .45-1 1zM21 30H11c-2.76 0-5-2.24-5-5V11c0-.55.45-1 1-1s1 .45 1 1v14c0 1.65 1.35 3 3 3h10c1.65 0 3-1.35 3-3V11c0-.55.45-1 1-1s1 .45 1 1v14c0 2.76-2.24 5-5 5"/></svg>
                                  </button>
                                </div>
                              )
                            })}
                            {activeQuickFilters.length === 0 && (
                              <div className="qf-more-empty">No quick filters added</div>
                            )}
                          </div>
                          <div className="qf-edit-footer">
                            <small className="qf-edit-counter">{activeQuickFilters.length} of {quickFilterProperties.reduce((n, g) => n + g.items.length, 0)} filters added</small>
                            {activeQuickFilters.length > 0 && (
                              <button className="qf-edit-delete-all" onClick={() => {
                                setActiveQuickFilters([])
                                setQfPillValues({})
                                setQfMoreOpen(false)
                                setQfMoreView('add')
                              }}>
                                <svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M25 8H7c-.55 0-1-.45-1-1s.45-1 1-1h18c.55 0 1 .45 1 1s-.45 1-1 1M13 24c-.55 0-1-.45-1-1V13c0-.55.45-1 1-1s1 .45 1 1v10c0 .55-.45 1-1 1M19 24c-.55 0-1-.45-1-1V13c0-.55.45-1 1-1s1 .45 1 1v10c0 .55-.45 1-1 1"/><path d="M20 8h-8c-.55 0-1-.45-1-1V5c0-1.65 1.35-3 3-3h4c1.65 0 3 1.35 3 3v2c0 .55-.45 1-1 1m-7-2h6V5c0-.55-.45-1-1-1h-4c-.55 0-1 .45-1 1zM21 30H11c-2.76 0-5-2.24-5-5V11c0-.55.45-1 1-1s1 .45 1 1v14c0 1.65 1.35 3 3 3h10c1.65 0 3-1.35 3-3V11c0-.55.45-1 1-1s1 .45 1 1v14c0 2.76-2.24 5-5 5"/></svg>
                                <span>Delete all quick filters</span>
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </> : null}
              body={<>
                {activeView === 'deals' ? (
                  <Suspense fallback={<div className="hs-loading-cell" style={{ padding: '40px 0', textAlign: 'center' }}><DotLottieReact src="/loading.lottie" loop autoplay style={{ width: 80, height: 80, margin: '0 auto' }} /><div style={{ fontSize: '0.82rem', color: '#7c98b6', marginTop: 8 }}>Loading deals...</div></div>}>
                    <DealsList
                      liveDeals={liveDeals}
                      filteredDeals={filteredDeals}
                      dealsByStage={dealsByStage}
                      dealSelectedRows={dealSelectedRows}
                      setDealSelectedRows={setDealSelectedRows}
                      toggleAllDealRows={toggleAllDealRows}
                      toggleDealRow={toggleDealRow}
                      setLiveDeals={setLiveDeals}
                      setDealTotal={setDealTotal}
                      setDealPages={setDealPages}
                      allPipelineStages={allPipelineStages}
                      terminalStages={terminalStages}
                      STAGE_COLORS={STAGE_COLORS}
                      dealSort={dealSort}
                      setDealSort={setDealSort}
                      toggleDealSort={toggleDealSort}
                      DEAL_COL_DEFS={DEAL_COL_DEFS}
                      dealVisibleCols={dealVisibleCols}
                      dealColWidths={dealColWidths}
                      setDealColWidths={setDealColWidths}
                      onDealResizeStart={onDealResizeStart}
                      dealViewMode={dealViewMode}
                      dealsArchiveMode={dealsArchiveMode}
                      dealsScope={dealsScope}
                      dealPage={dealPage}
                      setDealPage={setDealPage}
                      dealPages={dealPages}
                      dealPageSize={dealPageSize}
                      setDealPageSize={setDealPageSize}
                      dealToast={dealToast}
                      setDealToast={setDealToast}
                      dealsDeleteConfirm={dealsDeleteConfirm}
                      setDealsDeleteConfirm={setDealsDeleteConfirm}
                      dealsDeleteRunning={dealsDeleteRunning}
                      setDealsDeleteRunning={setDealsDeleteRunning}
                      dealDeleteModal={dealDeleteModal}
                      setDealDeleteModal={setDealDeleteModal}
                      canDeleteDeal={myCapabilities ? myCapabilities.delete_deal !== false : true}
                      dealMoveStageOpen={dealMoveStageOpen}
                      setDealMoveStageOpen={setDealMoveStageOpen}
                      setDealsStageFilterPopover={setDealsStageFilterPopover}
                      colContextMenu={colContextMenu}
                      setColContextMenu={setColContextMenu}
                      setSelectedDeal={setSelectedDeal}
                      setDealDetailOpen={setDealDetailOpen}
                      kanbanScrollRef={kanbanScrollRef}
                      showLeftEar={showLeftEar}
                      showRightEar={showRightEar}
                      startEarScroll={startEarScroll}
                      stopEarScroll={stopEarScroll}
                      handleKanbanContainerDragOver={handleKanbanContainerDragOver}
                      handleDealDragStart={handleDealDragStart}
                      handleDealDragEnd={handleDealDragEnd}
                      handleDealDragOver={handleDealDragOver}
                      handleDealDragLeave={handleDealDragLeave}
                      handleDealDrop={handleDealDrop}
                      draggedDealId={draggedDealId}
                      dragOverStageId={dragOverStageId}
                      dealContextMenu={dealContextMenu}
                      setDealContextMenu={setDealContextMenu}
                      dealStagePicker={dealStagePicker}
                      setDealStagePicker={setDealStagePicker}
                      dealStageDropdownPos={dealStageDropdownPos}
                      setDealStageDropdownPos={setDealStageDropdownPos}
                      dealConfirmMove={dealConfirmMove}
                      setDealConfirmMove={setDealConfirmMove}
                      confirmDealMoveAction={confirmDealMoveAction}
                      lossReasonModal={lossReasonModal}
                      setLossReasonModal={setLossReasonModal}
                      lossReasonValue={lossReasonValue}
                      setLossReasonValue={setLossReasonValue}
                      lossReasonNotes={lossReasonNotes}
                      setLossReasonNotes={setLossReasonNotes}
                      noShowModal={noShowModal}
                      setNoShowModal={setNoShowModal}
                      noShowReason={noShowReason}
                      setNoShowReason={setNoShowReason}
                      noShowNewAppt={noShowNewAppt}
                      setNoShowNewAppt={setNoShowNewAppt}
                      noShowEstimatorId={noShowEstimatorId}
                      setNoShowEstimatorId={setNoShowEstimatorId}
                      apptSetModal={apptSetModal}
                      setApptSetModal={setApptSetModal}
                      apptSetDate={apptSetDate}
                      setApptSetDate={setApptSetDate}
                      apptSetEstimator={apptSetEstimator}
                      setApptSetEstimator={setApptSetEstimator}
                      apptShowAllUsers={apptShowAllUsers}
                      setApptShowAllUsers={setApptShowAllUsers}
                      stageConfirmModal={stageConfirmModal}
                      setStageConfirmModal={setStageConfirmModal}
                      setContractSignedModal={(v) => v && openContractSignedModal(v.dealId, v.dealTitle)}
                      setDealCreateProjectForm={setDealCreateProjectForm}
                      executeDealMove={executeDealMove}
                      updateDeal={updateDeal}
                      updateDealStage={updateDealStage}
                      deleteDeal={deleteDeal}
                      fetchDealDeletePreview={fetchDealDeletePreview}
                      refetchCurrentDeals={refetchCurrentDeals}
                      setTicketsRefreshTick={setTicketsRefreshTick}
                      isTerminalPipelineStage={isTerminalPipelineStage}
                      isSuccessPipelineStage={isSuccessPipelineStage}
                      setContactHoverCard={setContactHoverCard}
                      contactHoverCacheRef={contactHoverCacheRef}
                      contactHoverTimerRef={contactHoverTimerRef}
                      contactHoverCloseTimerRef={contactHoverCloseTimerRef}
                      fetchClient={fetchClient}
                      formatDatePref={formatDatePref}
                      currencyFormatter={currencyFormatter}
                      crmUsers={crmUsers}
                      AvailabilityDateTimePicker={AvailabilityDateTimePicker}
                      DotLottieReact={DotLottieReact}
                    />
                  </Suspense>
                ) : clientViewMode === 'table' ? (
                  <div className="hs-table-area">
                    {/* Unsaved-changes save/cancel bar (contacts only) */}
                    {activeView === 'contacts' && totalUnsaved > 0 && (

                    <div className={`hs-save-bar${totalUnsaved > 0 ? ' visible' : ''}`}>
                      <span className="hs-save-bar-msg">
                        {totalUnsaved === 1 ? '1 unsaved change' : `${totalUnsaved} unsaved changes`}
                      </span>
                      <div className="hs-save-bar-actions">
                        <button
                          className="hs-save-bar-cancel"
                          onClick={cancelAllEdits}
                          disabled={savingEdits}
                        >
                          Cancel
                        </button>
                        <button
                          className="hs-save-bar-save"
                          onClick={saveAllEdits}
                          disabled={savingEdits}
                        >
                          {savingEdits ? 'Saving…' : 'Save'}
                        </button>
                      </div>
                    </div>
                    )}

                    {/* Contacts bulk actions bar moved inside hs-table-scroll */}
                    {activeView === 'companies' && companySelectedRows.size > 0 && (
                      <div className="hs-selection-bar">
                        <span className="hs-sel-count">{companySelectedRows.size} selected</span>
                        <div className="hs-sel-sep" />
                        <button
                          className="hs-sel-btn"
                          // CAPABILITY: archive_contacts
                          disabled={myCapabilities ? !myCapabilities.archive_contacts : false}
                          title={myCapabilities && !myCapabilities.archive_contacts ? `Requires admin (your role: ${myRole || 'unknown'})` : ''}
                          onClick={async () => {
                          const ids = Array.from(companySelectedRows)
                          const isUnarchive = companyStatusFilter === 'archived'
                          try {
                            await archiveClients(ids, !isUnarchive)
                            setCompanySelectedRows(new Set())
                            const qfOpts = buildQuickFilterOpts(qfPillValues)
                            fetchClients(companyPage, tablePageSize, {
                              search: companySearchDebounced || undefined,
                              status: (companyStatusFilter && companyStatusFilter !== 'archived' && !companyStatusFilter.startsWith('custom:')) ? companyStatusFilter : undefined,
                              type: 'organization',
                              sort: companySort.field,
                              order: companySort.order,
                              archived: companyStatusFilter === 'archived' ? true : undefined,
                              ...qfOpts,
                            }).then(res => { setCompanyList(res.clients); setCompanyTotal(res.total); setCompanyPages(res.pages) })
                          } catch (e) { console.error('archive:', e) }
                        }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 8v13H3V8"/><path d="M1 3h22v5H1z"/><path d="M10 12h4"/></svg>
                          {companyStatusFilter === 'archived' ? 'Unarchive' : 'Archive'}
                        </button>
                        <button className="hs-sel-close" onClick={() => setCompanySelectedRows(new Set())} title="Deselect all">&times;</button>
                      </div>
                    )}
                    {activeView === 'leads' && leadsArchiveConfirm !== null && (
                      <div className="kanban-modal-overlay" onClick={() => { if (!leadsArchiveRunning) setLeadsArchiveConfirm(null) }}>
                        <div className="kanban-confirm-modal" onClick={(e) => e.stopPropagation()}>
                          <div className="kanban-confirm-icon" style={{ background: '#fef3c7' }}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#92400e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 8v13H3V8"/><path d="M1 3h22v5H1z"/><path d="M10 12h4"/></svg>
                          </div>
                          <h3>Archive {leadsArchiveConfirm} lead{leadsArchiveConfirm === 1 ? '' : 's'}?</h3>
                          <p>
                            You're about to archive <strong>{leadsArchiveConfirm} lead{leadsArchiveConfirm === 1 ? '' : 's'}</strong>.
                            Archived leads have their status set to <strong>Junk</strong> and are hidden from the default list. You can restore them later by toggling "Include disqualified".
                          </p>
                          <div className="kanban-confirm-actions">
                            <button className="kanban-confirm-cancel" disabled={leadsArchiveRunning} onClick={() => setLeadsArchiveConfirm(null)}>Cancel</button>
                            <button
                              className="kanban-confirm-ok"
                              disabled={leadsArchiveRunning}
                              style={{ background: '#92400e' }}
                              onClick={async () => {
                                setLeadsArchiveRunning(true)
                                try {
                                  const ids = Array.from(leadSelectedRows)
                                  await batchArchiveLeads(ids)
                                  setLeadSelectedRows(new Set())
                                  const res = await fetchLeadsPaginated(leadPage, tablePageSize, {
                                    search: leadSearchDebounced || undefined,
                                    status: leadStatusFilter || undefined,
                                    sort: leadSort.field,
                                    order: leadSort.order,
                                    includeConverted: leadIncludeConverted,
                                    includeJunk: leadIncludeDisqualified,
                                  })
                                  setLeadList(res.leads)
                                  setLeadPageTotal(res.total)
                                  setLeadPageCount(res.pages)
                                } catch (e) { console.error('lead bulk archive:', e) }
                                finally {
                                  setLeadsArchiveRunning(false)
                                  setLeadsArchiveConfirm(null)
                                }
                              }}
                            >
                              {leadsArchiveRunning ? 'Archiving…' : `Yes, archive ${leadsArchiveConfirm}`}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    {activeView === 'leads' && leadSelectedRows.size > 0 && (
                      <div className="hs-selection-bar">
                        <span className="hs-sel-count">{leadSelectedRows.size} selected</span>
                        <div className="hs-sel-sep" />
                        {(myCapabilities ? myCapabilities.delete_lead !== false : true) && (
                        <button className="hs-sel-btn" onClick={() => setLeadsArchiveConfirm(leadSelectedRows.size)}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 8v13H3V8"/><path d="M1 3h22v5H1z"/><path d="M10 12h4"/></svg>
                          Archive
                        </button>
                        )}
                        <button
                          className="hs-sel-btn"
                          style={{ color: '#dc2626' }}
                          onClick={() => {
                            const init: Record<string, string> = {}
                            leadSelectedRows.forEach(id => { init[id] = '' })
                            setBulkDisqualifyPerLead(init)
                            setBulkDisqualifyShared('')
                            setBulkDisqualifySameReason(true)
                            setBulkDisqualifyOpen(true)
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="m4.93 4.93 14.14 14.14"/></svg>
                          Disqualify
                        </button>
                        <button className="hs-sel-close" onClick={() => setLeadSelectedRows(new Set())} title="Deselect all">&times;</button>
                      </div>
                    )}
                    {activeView === 'tickets' && ticketSelectedRows.size > 0 && (
                      <div className="hs-selection-bar">
                        <span className="hs-sel-count">{ticketSelectedRows.size} selected</span>
                        <div className="hs-sel-sep" />
                        <button className="hs-sel-btn" style={{ color: '#dc2626' }} onClick={() => {
                          if (confirm(`Delete ${ticketSelectedRows.size} ticket(s)?`)) {
                            Promise.all([...ticketSelectedRows].map(id => deleteTicket(id))).then(() => {
                              setTicketsData(prev => prev.filter(t => !ticketSelectedRows.has(t.id)))
                              setTicketsTotal(prev => prev - ticketSelectedRows.size)
                              setTicketSelectedRows(new Set())
                            }).catch(() => {})
                          }
                        }}>Delete</button>
                        <button className="hs-sel-close" onClick={() => setTicketSelectedRows(new Set())} title="Deselect all">&times;</button>
                      </div>
                    )}
                    {activeView === 'calls' && callSelectedRows.size > 0 && (
                      <div className="hs-selection-bar" data-feature="calls-delete-gate-v1">
                        <span className="hs-sel-count">{callSelectedRows.size} selected</span>
                        <button className="hs-sel-close" onClick={() => setCallSelectedRows(new Set())} title="Deselect all">&times;</button>
                      </div>
                    )}
                    {activeView === 'meets' && meetsSelectedRows.size > 0 && (
                      <div className="hs-selection-bar" data-marker="bp-meets-v1-selection">
                        <span className="hs-sel-count">{meetsSelectedRows.size} selected</span>
                        <button className="hs-sel-close" onClick={() => setMeetsSelectedRows(new Set())} title="Deselect all">&times;</button>
                      </div>
                    )}

                    <div className="hs-table-scroll" ref={tableScrollRef}>
                      {/* Calls KPIs moved OUT of this x-scroll container into the
                          toolbar calls-stats-badge — inside here they scrolled
                          sideways with the table and trailed whitespace after
                          MISSED (docs/REGRESSIONS.md 2026-07-01). The table gets
                          the freed height. */}
                      {/* Selection bars moved inside table-scroll for alignment with preview panel */}
                      {activeView === 'contacts' && clientSelectedRows.size > 0 && (
                      <div className="hs-selection-bar" style={{ position: 'sticky', top: 0, zIndex: 5 }} data-marker="listbars-honest-v1">
                        <span className="hs-sel-count">{clientSelectedRows.size} selected</span>
                        <div className="hs-sel-sep" />
                        <button
                          className="hs-sel-btn"
                          // CAPABILITY: archive_contacts
                          disabled={myCapabilities ? !myCapabilities.archive_contacts : false}
                          title={myCapabilities && !myCapabilities.archive_contacts ? `Requires admin (your role: ${myRole || 'unknown'})` : ''}
                          onClick={async () => {
                          const ids = Array.from(clientSelectedRows)
                          const isUnarchive = clientStatusFilter === 'archived'
                          try {
                            await archiveClients(ids, !isUnarchive)
                            setClientSelectedRows(new Set())
                            const qfOpts = buildQuickFilterOpts(qfPillValues)
                            fetchClients(clientPage, tablePageSize, {
                              search: clientSearchDebounced || undefined,
                              status: (clientStatusFilter && clientStatusFilter !== 'archived' && !clientStatusFilter.startsWith('custom:')) ? clientStatusFilter : undefined,
                              type: 'person',
                              sort: clientSort.field,
                              order: clientSort.order,
                              archived: clientStatusFilter === 'archived' ? true : undefined,
                              ...qfOpts,
                            }).then(res => { setClientList(res.clients); setClientTotal(res.total); setClientPages(res.pages) })
                          } catch (e) { console.error('archive:', e) }
                        }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 8v13H3V8"/><path d="M1 3h22v5H1z"/><path d="M10 12h4"/></svg>
                          {clientStatusFilter === 'archived' ? 'Unarchive' : 'Archive'}
                        </button>
                        <button className="hs-sel-close" onClick={() => setClientSelectedRows(new Set())} title="Deselect all">&times;</button>
                      </div>
                      )}
                      {activeView === 'meets' ? (
                      <Suspense fallback={<table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}><tbody><tr><td colSpan={8} className="hs-loading-cell"><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}><DotLottieReact src="/loading.lottie" loop autoplay style={{ width: 80, height: 80 }} /><span style={{ fontSize: '0.82rem', color: '#7c98b6', marginTop: 8 }}>Loading meetings...</span></div></td></tr></tbody></table>}>
                        <MeetsList
                          meetingsData={meetsData}
                          meetingsLoading={meetsLoading}
                          meetingsError={meetsError}
                          onRetry={() => setMeetsReloadNonce((nonce) => nonce + 1)}
                          meetingSelectedRows={meetsSelectedRows}
                          toggleAllMeetingRows={toggleAllMeetingRows}
                          toggleMeetingRow={toggleMeetingRow}
                          meetsSort={meetsSort}
                          toggleMeetSort={toggleMeetSort}
                          tableRowHeight={tableRowHeight}
                          tableZebraStriping={tableZebraStriping}
                          formatDatePref={formatDatePref}
                          DotLottieReact={DotLottieReact}
                          logMeetingOpen={meetsLogOpen}
                          onCloseLogMeeting={() => setMeetsLogOpen(false)}
                          onMeetingLogged={() => setMeetsReloadNonce((nonce) => nonce + 1)}
                          responsibleId={currentUserId}
                        />
                      </Suspense>
                      ) : activeView === 'calls' ? (
                      <Suspense fallback={<table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}><tbody><tr><td colSpan={12} className="hs-loading-cell"><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}><DotLottieReact src="/loading.lottie" loop autoplay style={{ width: 80, height: 80 }} /><span style={{ fontSize: '0.82rem', color: '#7c98b6', marginTop: 8 }}>Loading calls...</span></div></td></tr></tbody></table>}>
                        <CallsList
                          callsData={callsData}
                          callsLoading={callsLoading}
                          callsStale={callsLoading && callsAppliedKey !== callsQueryKey({ page: callsPage, limit: tablePageSize, search: callsSearchDebounced || '', direction: callsDirectionFilter || '', outcome: callsOutcomeFilter || '', hasRecording: !!callsHasRecording, hasTranscript: !!callsHasTranscript, sortField: callsSort.field, sortOrder: callsSort.order })}
                          callsError={callsError}
                          onRetry={() => setCallsReloadNonce((n) => n + 1)}
                          callSelectedRows={callSelectedRows}
                          toggleAllCallRows={toggleAllCallRows}
                          toggleCallRow={toggleCallRow}
                          callsSort={callsSort}
                          toggleCallSort={toggleCallSort}
                          callColWidths={callColWidths}
                          onCallResizeStart={onCallResizeStart}
                          callVisibleCols={callVisibleCols}
                          tableRowHeight={tableRowHeight}
                          tableZebraStriping={tableZebraStriping}
                          highlightedCallActivityId={highlightedCallActivityId}
                          callCreateContactRowId={callCreateContactRowId}
                          setCallCreateContactRowId={setCallCreateContactRowId}
                          previewCall={previewCall}
                          setPreviewCall={setPreviewCall}
                          setTableSettingsOpen={setTableSettingsOpen}
                          onAssignCallOwner={handleAssignCallOwner}
                          CALL_DIRECTION_COLORS={CALL_DIRECTION_COLORS}
                          CALL_DIRECTION_LABELS={CALL_DIRECTION_LABELS}
                          SENTIMENT_COLORS={SENTIMENT_COLORS}
                          CALENDAR_USERS_MAP={CALENDAR_USERS_MAP}
                          formatDatePref={formatDatePref}
                          formatDurationSecs={formatDurationSecs}
                          formatPhoneDisplay={formatPhoneDisplay}
                          getOutcomeColor={getOutcomeColor}
                          SentimentIcon={SentimentIcon}
                          CallCreateContactForm={CallCreateContactForm}
                          DotLottieReact={DotLottieReact}
                        />
                      </Suspense>
                      ) : activeView === 'tickets' ? (
                      <Suspense fallback={<table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}><tbody><tr><td colSpan={9} className="hs-loading-cell"><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}><DotLottieReact src="/loading.lottie" loop autoplay style={{ width: 80, height: 80 }} /><span style={{ fontSize: '0.82rem', color: '#7c98b6', marginTop: 8 }}>Loading tickets...</span></div></td></tr></tbody></table>}>
                        <TicketsList
                          ticketsData={ticketsData}
                          ticketsLoading={ticketsLoading}
                          ticketSelectedRows={ticketSelectedRows}
                          toggleAllTicketRows={toggleAllTicketRows}
                          toggleTicketRow={toggleTicketRow}
                          ticketsSort={ticketsSort}
                          toggleTicketSort={toggleTicketSort}
                          tableRowHeight={tableRowHeight}
                          tableZebraStriping={tableZebraStriping}
                          previewTicket={previewTicket}
                          setPreviewTicket={setPreviewTicket}
                          setTableSettingsOpen={setTableSettingsOpen}
                          TICKET_STATUS_COLORS={TICKET_STATUS_COLORS}
                          TICKET_PRIORITY_COLORS={TICKET_PRIORITY_COLORS}
                          CALENDAR_USERS_MAP={CALENDAR_USERS_MAP}
                          formatDatePref={formatDatePref}
                          DotLottieReact={DotLottieReact}
                        />
                      </Suspense>
                      ) : activeView === 'leads' ? (
                      <Suspense fallback={<table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}><tbody><tr><td colSpan={7} className="hs-loading-cell"><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}><DotLottieReact src="/loading.lottie" loop autoplay style={{ width: 80, height: 80 }} /><span style={{ fontSize: '0.82rem', color: '#7c98b6', marginTop: 8 }}>Loading leads...</span></div></td></tr></tbody></table>}>
                        <LeadsList
                          leadList={leadList}
                          leadLoading={leadLoading}
                          leadSelectedRows={leadSelectedRows}
                          toggleAllLeadRows={toggleAllLeadRows}
                          toggleLeadRow={toggleLeadRow}
                          setLeadList={setLeadList}
                          leadSort={leadSort}
                          toggleLeadSort={toggleLeadSort}
                          tableRowHeight={tableRowHeight}
                          tableZebraStriping={tableZebraStriping}
                          leadRoutingConfig={leadRoutingConfig}
                          markLeadRead={markLeadRead}
                          inlineStatusLeadId={inlineStatusLeadId}
                          setInlineStatusLeadId={setInlineStatusLeadId}
                          inlineStatusPos={inlineStatusPos}
                          setInlineStatusPos={setInlineStatusPos}
                          leadPipelineStatuses={leadPipelineStatuses}
                          updateLead={updateLead}
                          setLeadConvertModal={setLeadConvertModal}
                          setLeadConvertAppt={setLeadConvertAppt}
                          setLeadConvertEstimator={setLeadConvertEstimator}
                          setDisqualifyModal={setDisqualifyModal}
                          setDisqualifyComment={setDisqualifyComment}
                          setPreviewLead={setPreviewLead}
                          previewLeadId={previewLead?.id ?? null}
                          setTableSettingsOpen={setTableSettingsOpen}
                          getLeadStatusColor={getLeadStatusColor}
                          formatDatePref={formatDatePref}
                          PhoneLink={PhoneLink}
                          DotLottieReact={DotLottieReact}
                          badgeFilter={leadBadgeFilter}
                          setBadgeFilter={setLeadBadgeFilter}
                          setColContextMenu={setColContextMenu}
                        />
                      </Suspense>
                      ) : activeView === 'companies' ? (
                      <Suspense fallback={<table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}><tbody><tr><td colSpan={contactVisibleCols.length + 1} className="hs-loading-cell"><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}><DotLottieReact src="/loading.lottie" loop autoplay style={{ width: 80, height: 80 }} /><span style={{ fontSize: '0.82rem', color: '#7c98b6', marginTop: 8 }}>Loading companies...</span></div></td></tr></tbody></table>}>
                        <CompaniesList
                          companyList={companyList}
                          companyLoading={companyLoading}
                          companySelectedRows={companySelectedRows}
                          toggleAllCompanyRows={toggleAllCompanyRows}
                          toggleCompanyRow={toggleCompanyRow}
                          companySort={companySort}
                          toggleCompanySort={toggleCompanySort}
                          tableRowHeight={tableRowHeight}
                          tableZebraStriping={tableZebraStriping}
                          contactVisibleCols={contactVisibleCols}
                          CONTACT_COLUMN_MAP={CONTACT_COLUMN_MAP}
                          colWidths={colWidths as Record<string, number>}
                          frozenCols={frozenCols}
                          getFrozenStyle={getFrozenStyle}
                          getFrozenThStyle={getFrozenThStyle}
                          onResizeStart={onResizeStart}
                          showClickToCall={showClickToCall}
                          handleClickToCall={handleClickToCall}
                          setColContextMenu={setColContextMenu}
                          previewCompany={previewCompany}
                          setPreviewClient={setPreviewClient}
                          setPreviewCompany={setPreviewCompany}
                          setContactDetailOpen={setContactDetailOpen}
                          setContactDetailTab={setContactDetailTab}
                          setTableSettingsOpen={setTableSettingsOpen}
                          DotLottieReact={DotLottieReact}
                        />
                      </Suspense>
                      ) : (
                      <Suspense fallback={<table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}><tbody><tr><td colSpan={contactVisibleCols.length + 1} className="hs-loading-cell"><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}><DotLottieReact src="/loading.lottie" loop autoplay style={{ width: 80, height: 80 }} /><span style={{ fontSize: '0.82rem', color: '#7c98b6', marginTop: 8 }}>Loading contacts...</span></div></td></tr></tbody></table>}>
                        <ContactsList
                          clientList={clientList}
                          clientLoading={clientLoading}
                          clientSelectedRows={clientSelectedRows}
                          toggleAllClientRows={toggleAllClientRows}
                          toggleClientRow={toggleClientRow}
                          clientSort={clientSort}
                          toggleClientSort={toggleClientSort}
                          tableRowHeight={tableRowHeight}
                          tableZebraStriping={tableZebraStriping}
                          contactVisibleCols={contactVisibleCols.filter(k => !CONTACTS_HIDDEN_COLS.has(k))}
                          CONTACT_COLUMN_MAP={CONTACT_COLUMN_MAP}
                          colWidths={colWidths as Record<string, number>}
                          frozenCols={frozenCols}
                          getFrozenStyle={getFrozenStyle}
                          getFrozenThStyle={getFrozenThStyle}
                          onResizeStart={onResizeStart}
                          cellEdits={cellEdits}
                          activeCell={activeCell}
                          setActiveCell={setActiveCell}
                          activeCellInputRef={activeCellInputRef}
                          startCellEdit={startCellEdit}
                          commitActiveCell={commitActiveCell}
                          setCellEditModal={setCellEditModal}
                          contactEmails={contactEmails}
                          setColContextMenu={setColContextMenu}
                          previewClient={previewClient}
                          setPreviewClient={setPreviewClient}
                          closeClientPreview={closeClientPreview}
                          setContactDetailOpen={setContactDetailOpen}
                          setContactDetailTab={setContactDetailTab}
                          setTableSettingsOpen={setTableSettingsOpen}
                          DotLottieReact={DotLottieReact}
                        />
                      </Suspense>
                      )}

                      {/* Column context menu */}
                      {colContextMenu && (
                        <div className="col-context-menu" style={{ position: 'fixed', top: colContextMenu.y, left: colContextMenu.x, zIndex: 500 }}
                          onMouseDown={(e) => e.stopPropagation()}>
                          <button className="col-ctx-item" onClick={() => {
                            if (activeView === 'companies') { setCompanySort({ field: colContextMenu.field, order: 'ASC' }); setCompanyPage(1) }
                            else if (activeView === 'leads') { setLeadSort({ field: colContextMenu.field, order: 'ASC' }); setLeadPage(1) }
                            else { setClientSort({ field: colContextMenu.field, order: 'ASC' }); setClientPage(1) }
                            setColContextMenu(null)
                          }}>
                            <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M16 4a1.14 1.14 0 0 1 .81.33l8 8a1.14 1.14 0 0 1-1.62 1.62L16 6.74l-7.19 7.21a1.14 1.14 0 0 1-1.62-1.62l8-8A1.14 1.14 0 0 1 16 4M16 28a1.14 1.14 0 0 1-.81-.33l-8-8a1.14 1.14 0 0 1 1.62-1.62L16 25.26l7.19-7.21a1.14 1.14 0 0 1 1.62 1.62l-8 8A1.14 1.14 0 0 1 16 28"/></svg>
                            Sort ascending
                          </button>
                          <button className="col-ctx-item" onClick={() => {
                            if (activeView === 'companies') { setCompanySort({ field: colContextMenu.field, order: 'DESC' }); setCompanyPage(1) }
                            else if (activeView === 'leads') { setLeadSort({ field: colContextMenu.field, order: 'DESC' }); setLeadPage(1) }
                            else { setClientSort({ field: colContextMenu.field, order: 'DESC' }); setClientPage(1) }
                            setColContextMenu(null)
                          }}>
                            <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M16 28a1.14 1.14 0 0 1-.81-.33l-8-8a1.14 1.14 0 0 1 1.62-1.62L16 25.26l7.19-7.21a1.14 1.14 0 0 1 1.62 1.62l-8 8A1.14 1.14 0 0 1 16 28M16 4a1.14 1.14 0 0 1 .81.33l8 8a1.14 1.14 0 0 1-1.62 1.62L16 6.74l-7.19 7.21a1.14 1.14 0 0 1-1.62-1.62l8-8A1.14 1.14 0 0 1 16 4"/></svg>
                            Sort descending
                          </button>
                          {/* Freeze / Add / Remove only meaningful for contacts/companies, which have
                              the column-visibility infra. Leads use hardcoded columns so we hide those. */}
                          {activeView !== 'leads' && <>
                          <hr className="col-ctx-sep" />
                          {(() => {
                            const field = colContextMenu.field
                            const colKey = CONTACT_COLUMN_DEFS.find(d => d.sortField === field || d.key === field)?.key || field
                            const colIdx = contactVisibleCols.indexOf(colKey)
                            const isFrozen = colIdx < freezeCount
                            const isFirstCol = colIdx === 0
                            const canUnfreeze = isFrozen && !isFirstCol
                            return (
                              <button className="col-ctx-item" disabled={isFrozen && isFirstCol} onClick={() => {
                                let newCount: number
                                if (isFrozen) {
                                  // Unfreeze: set boundary to this column's index (everything before stays frozen)
                                  newCount = Math.max(1, colIdx)
                                } else {
                                  // Freeze: set boundary to include this column and everything before it
                                  newCount = colIdx + 1
                                }
                                setFreezeCount(newCount)
                                localStorage.setItem('botpros-crm-freeze-count', String(newCount))
                                setColContextMenu(null)
                              }}>
                                <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.43 32H4.57A4.57 4.57 0 0 1 0 27.43V4.57A4.57 4.57 0 0 1 4.57 0h22.86A4.57 4.57 0 0 1 32 4.57v22.86A4.57 4.57 0 0 1 27.43 32M4.57 2.29a2.29 2.29 0 0 0-2.28 2.28v22.86a2.29 2.29 0 0 0 2.28 2.28h22.86a2.29 2.29 0 0 0 2.28-2.28V4.57a2.29 2.29 0 0 0-2.28-2.28zM12.57 2.29h2.29v27.42h-2.29z"/></svg>
                                {isFirstCol ? 'Name always frozen' : isFrozen ? 'Unfreeze column' : 'Freeze column'}
                              </button>
                            )
                          })()}
                          <button className="col-ctx-item" onClick={() => {
                            setEditColsDraft([...contactVisibleCols])
                            setEditColsSearch('')
                            setEditColumnsOpen(true)
                            setColContextMenu(null)
                          }}>
                            <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M16 32c-.63 0-1.14-.51-1.14-1.14V1.14a1.14 1.14 0 0 1 2.28 0v29.72c0 .63-.51 1.14-1.14 1.14"/><path d="M30.86 17.14H1.14a1.14 1.14 0 0 1 0-2.28h29.72a1.14 1.14 0 0 1 0 2.28"/></svg>
                            Add column
                          </button>
                          <hr className="col-ctx-sep" />
                          <button className="col-ctx-item" onClick={() => {
                            const field = colContextMenu.field
                            const colKey = CONTACT_COLUMN_DEFS.find(d => d.sortField === field || d.key === field)?.key || field
                            if (colKey !== 'name') {
                              setContactVisibleCols(prev => {
                                const next = prev.filter(k => k !== colKey)
                                localStorage.setItem('botpros-crm-visible-columns', JSON.stringify(next))
                                return next
                              })
                            }
                            setColContextMenu(null)
                          }}>
                            <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M30.86 17.14H1.14a1.14 1.14 0 0 1 0-2.28h29.72a1.14 1.14 0 0 1 0 2.28"/></svg>
                            Remove column
                          </button>
                          </>}
                        </div>
                      )}
                    </div>

                    {/* Pagination */}
                    <div className="hs-pagination">
                      <nav className="hs-paginator" aria-label="Pagination">
                        <button
                          className="hs-paginator-btn"
                          disabled={activeView === 'meets' ? meetsPage <= 1 : activeView === 'calls' ? callsPage <= 1 : activeView === 'tickets' ? ticketsPage <= 1 : activeView === 'leads' ? leadPage <= 1 : activeView === 'companies' ? companyPage <= 1 : clientPage <= 1}
                          onClick={() => activeView === 'meets' ? setMeetsPage((p) => p - 1) : activeView === 'calls' ? setCallsPage((p) => p - 1) : activeView === 'tickets' ? setTicketsPage((p) => p - 1) : activeView === 'leads' ? setLeadPage((p) => p - 1) : activeView === 'companies' ? setCompanyPage((p) => p - 1) : setClientPage((p) => p - 1)}
                          aria-label="Previous page"
                        >
                          <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95"/></svg>
                          <span>Prev</span>
                        </button>
                        {(() => {
                          const currentPage = activeView === 'meets' ? meetsPage : activeView === 'calls' ? callsPage : activeView === 'tickets' ? ticketsPage : activeView === 'leads' ? leadPage : activeView === 'companies' ? companyPage : clientPage
                          const totalPages = activeView === 'meets' ? meetsPageCount : activeView === 'calls' ? callsPageCount : activeView === 'tickets' ? ticketsPageCount : activeView === 'leads' ? leadPageCount : activeView === 'companies' ? companyPages : clientPages
                          const setPage = activeView === 'meets' ? setMeetsPage : activeView === 'calls' ? setCallsPage : activeView === 'tickets' ? setTicketsPage : activeView === 'leads' ? setLeadPage : activeView === 'companies' ? setCompanyPage : setClientPage
                          if (totalPages <= 7) {
                            const pages: number[] = []
                            for (let i = 1; i <= totalPages; i++) pages.push(i)
                            return pages.map(p => (
                              <button key={p} className={`hs-paginator-num${p === currentPage ? ' active' : ''}`} onClick={() => setPage(p)}>{p}</button>
                            ))
                          }
                          // Show: 1 ... middle window ... lastPage
                          const items: React.ReactNode[] = []
                          const addPage = (p: number) => items.push(
                            <button key={p} className={`hs-paginator-num${p === currentPage ? ' active' : ''}`} onClick={() => setPage(p)}>{p}</button>
                          )
                          const addEllipsis = (key: string) => items.push(
                            <span key={key} className="hs-paginator-ellipsis">...</span>
                          )
                          // Always show pages 1-4, then ellipsis, then last page
                          // When current page is in the middle, show window around it
                          const shown = new Set<number>()
                          // Always show first 4
                          for (let i = 1; i <= Math.min(4, totalPages); i++) shown.add(i)
                          // Always show last
                          shown.add(totalPages)
                          // Show window around current page
                          for (let i = Math.max(1, currentPage - 1); i <= Math.min(totalPages, currentPage + 1); i++) shown.add(i)
                          const sorted = [...shown].sort((a, b) => a - b)
                          for (let i = 0; i < sorted.length; i++) {
                            if (i > 0 && sorted[i] - sorted[i - 1] > 1) addEllipsis(`e${i}`)
                            addPage(sorted[i])
                          }
                          return items
                        })()}
                        <button
                          className="hs-paginator-btn"
                          disabled={activeView === 'meets' ? meetsPage >= meetsPageCount : activeView === 'calls' ? callsPage >= callsPageCount : activeView === 'tickets' ? ticketsPage >= ticketsPageCount : activeView === 'leads' ? leadPage >= leadPageCount : activeView === 'companies' ? companyPage >= companyPages : clientPage >= clientPages}
                          onClick={() => activeView === 'meets' ? setMeetsPage((p) => p + 1) : activeView === 'calls' ? setCallsPage((p) => p + 1) : activeView === 'tickets' ? setTicketsPage((p) => p + 1) : activeView === 'leads' ? setLeadPage((p) => p + 1) : activeView === 'companies' ? setCompanyPage((p) => p + 1) : setClientPage((p) => p + 1)}
                          aria-label="Next page"
                        >
                          <span>Next</span>
                          <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M11 27.71c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62l9.48-9.48-9.48-9.47a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0L22.1 15.48c.45.45.45 1.17 0 1.62L11.81 27.39c-.22.22-.52.33-.81.33Z"/></svg>
                        </button>
                      </nav>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', marginLeft: 'auto', marginRight: 50 }}><select value={tablePageSize} onChange={e => { setTablePageSize(Number(e.target.value) as any); if (activeView === 'meets') setMeetsPage(1); else if (activeView === 'calls') setCallsPage(1); else if (activeView === 'tickets') setTicketsPage(1); else if (activeView === 'leads') setLeadPage(1); else if (activeView === 'companies') setCompanyPage(1); else setClientPage(1) }} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #e2e8f0', fontSize: '0.82rem' }}><option value={100}>100</option><option value={200}>200</option><option value={500}>500</option></select><span>per page</span></div>
                    </div>
                  </div>
                ) : (
                  /* Board view: group by match_status */
                  <div className="hs-board">
                    {(['matched', 'imported', 'legacy_only'] as const).map((status) => {
                      const items = (activeView === 'companies' ? companyList : clientList).filter((c) => c.match_status === status)
                      const label = status === 'matched' ? 'Matched' : status === 'imported' ? 'Imported' : 'Legacy Only'
                      const color = status === 'matched' ? '#22c55e' : status === 'imported' ? '#3b82f6' : '#f59e0b'
                      return (
                        <div key={status} className="hs-board-col">
                          <div className="hs-board-col-header">
                            <span className="hs-board-col-dot" style={{ background: color }} />
                            <span className="hs-board-col-label">{label}</span>
                            <span className="hs-board-col-count">{items.length}</span>
                          </div>
                          <div className="hs-board-col-body">
                            {items.map((client) => (
                              <div
                                key={client.unified_client_id}
                                className="hs-board-card"
                                onClick={() => {
                                  setTableSettingsOpen(false)
                                  // Both companies and contacts open the full record page
                                  setPreviewClient(client)
                                  setContactDetailOpen(true)
                                  setContactDetailTab('about')
                                  const route = activeView === 'companies' ? 'clients' : 'contacts'
                                  window.location.hash = `/${route}/record/${encodeURIComponent(client.unified_client_id)}`
                                }}
                              >
                                <div className="hs-board-card-name">{client.preferred_name}</div>
                                <div className="hs-board-card-meta">
                                  {client.preferred_city && <span>{client.preferred_city}{client.preferred_state ? `, ${client.preferred_state}` : ''}</span>}
                                  {client.preferred_phone && <span>{client.preferred_phone}</span>}
                                </div>
                                {(client.deal_count > 0 || client.legacy_total_invoiced > 0) && (
                                  <div className="hs-board-card-stats">
                                    {client.deal_count > 0 && <span>{client.deal_count} deal{client.deal_count !== 1 ? 's' : ''}</span>}
                                    {client.legacy_total_invoiced > 0 && <span>{currencyFormatter.format(client.legacy_total_invoiced)}</span>}
                                  </div>
                                )}
                              </div>
                            ))}
                            {items.length === 0 && <div className="hs-board-empty">{activeView === 'companies' ? 'No clients' : 'No contacts'}</div>}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}

                {/* Company preview sidebar */}
                {(previewCompany || previewCompanyClosing) && (
                  <div className={`hs-preview${previewCompanyClosing ? ' closing' : ''}`}>
                    <div className="hs-preview-header">
                      <strong className="hs-preview-name">{previewCompany?.preferred_name}</strong>
                      <button className="hs-preview-close" onClick={closeCompanyPreview}><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg></button>
                    </div>
                    <div className="hs-preview-body">
                      <div className="hs-preview-hero">
                        <div className="hs-preview-avatar-lg hs-avatar-building-lg">
                          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18"/><path d="M5 21V7l7-4 7 4v14"/><path d="M9 21v-4h6v4"/><path d="M9 10h.01M15 10h.01M9 14h.01M15 14h.01"/></svg>
                        </div>
                        <div className="hs-preview-hero-info">
                          <h3>{previewCompany?.preferred_name}</h3>
                          <span className={`hs-status-badge ${previewCompany?.match_status}`}>
                            {previewCompany?.match_status === 'matched' ? 'Matched' : previewCompany?.match_status === 'imported' ? 'Imported' : 'Legacy'}
                          </span>
                        </div>
                      </div>

                      <div className="hs-preview-actions">
                        {previewCompany?.preferred_email && (
                          <a href={`mailto:${previewCompany.preferred_email}`} className="hs-preview-action" title="Email">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                          </a>
                        )}
                        {previewCompany?.preferred_phone && (
                            <button
                              className="hs-preview-action"
                              title="Click to call"
                              onClick={() => handleClickToCall(previewCompany!.preferred_phone!, undefined, previewCompany!.unified_client_id, previewCompany!.preferred_name)}
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                            </button>
                        )}
                      </div>

                      <div className="hs-preview-section">
                        <h4>Client Info</h4>
                        <div className="hs-preview-field">
                          <label>Email</label>
                          <span>{previewCompany?.preferred_email || '--'}</span>
                        </div>
                        <div className="hs-preview-field">
                          <label>Phone</label>
                          <span>{previewCompany?.preferred_phone || '--'}</span>
                        </div>
                        <div className="hs-preview-field">
                          <label>Address</label>
                          <span>{[previewCompany?.preferred_street, previewCompany?.preferred_city, previewCompany?.preferred_state, previewCompany?.preferred_zip].filter(Boolean).join(', ') || '--'}</span>
                        </div>
                        <div className="hs-preview-field">
                          <label>Type</label>
                          <span>Client</span>
                        </div>
                      </div>

                      <div className="hs-preview-section">
                        <h4>CRM Activity</h4>
                        <div className="hs-preview-field">
                          <label>Leads</label>
                          <span>{previewCompany?.lead_count ?? 0}</span>
                        </div>
                        <div className="hs-preview-field">
                          <label>Deals</label>
                          <span>{previewCompany?.deal_count ?? 0}</span>
                        </div>
                        {previewCompany?.latest_deal_title && (
                          <div className="hs-preview-field">
                            <label>Latest Deal</label>
                            <span>{previewCompany.latest_deal_title}</span>
                          </div>
                        )}
                        {previewCompany?.latest_lead_title && (
                          <div className="hs-preview-field">
                            <label>Latest Lead</label>
                            <span>{previewCompany.latest_lead_title}</span>
                          </div>
                        )}
                      </div>

                      {(previewCompany?.legacy_total_invoiced ?? 0) > 0 && (
                        <div className="hs-preview-section">
                          <h4>Financial Summary</h4>
                          <div className="hs-preview-field">
                            <label>Total Invoiced</label>
                            <span className="hs-preview-money">{currencyFormatter.format(previewCompany?.legacy_total_invoiced ?? 0)}</span>
                          </div>
                          <div className="hs-preview-field">
                            <label>Total Paid</label>
                            <span className="hs-preview-money">{currencyFormatter.format(previewCompany?.legacy_total_paid ?? 0)}</span>
                          </div>
                          {(previewCompany?.legacy_balance ?? 0) !== 0 && (
                            <div className="hs-preview-field">
                              <label>Balance</label>
                              <span className="hs-preview-money hs-preview-balance">{currencyFormatter.format(previewCompany?.legacy_balance ?? 0)}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Company Invoice Cards */}
                      {companyFinLoading ? (
                        <div className="hs-preview-section"><h4>Invoices</h4><p style={{ color: '#94a3b8', fontSize: 13 }}>Loading...</p></div>
                      ) : companyInvoices.length > 0 && (
                        <div className="hs-preview-section">
                          <h4>Invoices & Payments ({companyInvoices.length})</h4>
                          <div className="hs-invoice-list">
                            {companyInvoices.map((inv) => (
                              <div key={inv.id} className={`hs-invoice-card hs-invoice-${inv.txn_type?.toLowerCase().replace(/\s+/g, '-') ?? 'other'}`}>
                                <div className="hs-invoice-card-header">
                                  <span className={`hs-invoice-type-badge ${inv.txn_type === 'Invoice' ? 'invoice' : inv.txn_type === 'Payment' ? 'payment' : 'other'}`}>
                                    {inv.txn_type}
                                  </span>
                                  <span className="hs-invoice-amount">{currencyFormatter.format(inv.amount)}</span>
                                </div>
                                <div className="hs-invoice-card-details">
                                  {inv.ref_number && <span className="hs-invoice-ref">#{inv.ref_number}</span>}
                                  {inv.txn_date && <span className="hs-invoice-date">{formatDatePref(inv.txn_date)}</span>}
                                  {inv.payment_method && <span className="hs-invoice-method">{inv.payment_method}</span>}
                                </div>
                                {inv.job_name && <div className="hs-invoice-job">{inv.job_name}</div>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Company Document Cards */}
                      {!companyFinLoading && companyDocuments.length > 0 && (
                        <div className="hs-preview-section">
                          <h4>Documents ({companyDocuments.length})</h4>
                          <div className="hs-document-list">
                            {companyDocuments.map((doc) => (
                              <a
                                key={doc.id}
                                className="hs-document-card"
                                href={doc.document_url ?? '#'}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => { if (!doc.document_url) e.preventDefault() }}
                              >
                                <div className="hs-document-icon">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                    <polyline points="14 2 14 8 20 8"/>
                                  </svg>
                                </div>
                                <div className="hs-document-info">
                                  <span className="hs-document-type">{doc.doc_type}</span>
                                  {doc.doc_date && <span className="hs-document-date">{formatDatePref(doc.doc_date)}</span>}
                                  {doc.linked_qb_ref && <span className="hs-document-ref">Ref #{doc.linked_qb_ref}</span>}
                                  {doc.linked_qb_amount != null && <span className="hs-document-amount">{currencyFormatter.format(doc.linked_qb_amount)}</span>}
                                </div>
                                <div className="hs-document-link-icon">
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                                    <polyline points="15 3 21 3 21 9"/>
                                    <line x1="10" y1="14" x2="21" y2="3"/>
                                  </svg>
                                </div>
                              </a>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Company Projects */}
                      {!companyFinLoading && companyJobs.length > 0 && (
                        <div className="hs-preview-section">
                          <h4>{LABELS.projects} ({companyJobs.length})</h4>
                          <div className="hs-jobs-list">
                            {companyJobs.map((job) => (
                              <div key={job.id} className="hs-job-card">
                                {job.raw_entry && <div className="hs-job-description">{job.raw_entry}</div>}
                                <div className="hs-job-meta">
                                  {job.job_date && <span>{job.job_date}</span>}
                                  {job.job_type && <span>{job.job_type}</span>}
                                  {job.job_amount != null && job.job_amount > 0 && <span>{currencyFormatter.format(job.job_amount)}</span>}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {previewCompany?.aliases && (
                        <div className="hs-preview-section">
                          <h4>Aliases</h4>
                          <p className="hs-preview-aliases">{previewCompany.aliases}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Client preview sidebar */}
                {(previewClient || previewClientClosing) && (
                  <div className={`hs-preview${previewClientClosing ? ' closing' : ''}`}>
                    <div className="hs-preview-header">
                      <strong className="hs-preview-name">{clientDisplayNameClean(previewClient)}</strong>
                      <button className="hs-preview-close" onClick={closeClientPreview}><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg></button>
                    </div>
                    <div className="hs-preview-body">
                      <div className="hs-preview-hero">
                        <div className="hs-preview-avatar-lg">
                          {getClientInitial(previewClient)}
                        </div>
                        <div className="hs-preview-hero-info">
                          <h3>{clientDisplayNameClean(previewClient)}</h3>
                          <span className={`hs-status-badge ${previewClient?.match_status}`}>
                            {previewClient?.match_status === 'matched' ? 'Matched' : previewClient?.match_status === 'imported' ? 'Imported' : 'Legacy'}
                          </span>
                        </div>
                      </div>

                      <div className="hs-preview-actions">
                        {previewClient?.preferred_email && (
                          <a href={`mailto:${previewClient.preferred_email}`} className="hs-preview-action" title="Email">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                          </a>
                        )}
                        {previewClient?.preferred_phone && (
                          <button
                            className="hs-preview-action"
                            title="Click to call"
                            onClick={() => handleClickToCall(previewClient!.preferred_phone!, undefined, previewClient!.unified_client_id, previewClient!.preferred_name)}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                          </button>
                        )}
                        {previewClient && (previewClient.client_type === 'organization' ? (myCapabilities ? myCapabilities.delete_organization !== false : true) : (myCapabilities ? myCapabilities.delete_contact !== false : true)) && (
                          <button
                            className="hs-preview-action"
                            data-variant="danger"
                            title="Delete"
                            style={{ color: '#d94c53' }}
                            onClick={() => {
                              setBulkDeleteModal({ ids: new Set([previewClient.unified_client_id]), deleting: false })
                            }}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                          </button>
                        )}
                      </div>

                      <div className="hs-preview-section">
                        <h4>Contact Info</h4>
                        <div className="hs-preview-field">
                          <label>Email{contactEmails.length > 1 ? 's' : ''}</label>
                          <div className="crp-field-editable" style={{margin:0,padding:'2px 4px', flexDirection:'column', alignItems:'stretch', gap:3}} onClick={() => previewClient && setCellEditModal({
                            clientId: previewClient.unified_client_id,
                            field: 'email',
                            emails: contactEmails.map(r => r.address),
                            emailRows: contactEmails.length > 0
                              ? contactEmails.map(r => ({ id: r.id, address: r.address, is_primary: !!r.is_primary, is_billing: !!r.is_billing }))
                              : [{ address: previewClient?.preferred_email || '', is_primary: true, is_billing: false }],
                            phones: [],
                          })}>
                            {contactEmails.length === 0 ? (
                              <span>{previewClient?.preferred_email || '--'}</span>
                            ) : (
                              contactEmails.map(r => (
                                <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                                  <span>{r.address}</span>
                                  {!!r.is_primary && <span style={{ fontSize: '0.58rem', padding: '0 5px', borderRadius: 8, background: '#dcfce7', color: '#15803d', fontWeight: 600 }}>Primary</span>}
                                  {!!r.is_billing && <span style={{ fontSize: '0.58rem', padding: '0 5px', borderRadius: 8, background: '#e0f2fe', color: '#0369a1', fontWeight: 600 }}>Billing</span>}
                                </div>
                              ))
                            )}
                            <svg className="crp-field-edit-icon" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ alignSelf: 'flex-end' }}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </div>
                        </div>
                        <div className="hs-preview-field">
                          <label>Phone</label>
                          <div className="crp-field-editable" style={{margin:0,padding:'2px 4px'}} onClick={() => previewClient && setCellEditModal({ clientId: previewClient.unified_client_id, field: 'phone', emails: [], phones: [{ type: 'Mobile', number: previewClient.preferred_phone || '', ext: '' }] })}>
                            <span>{previewClient?.preferred_phone ? formatPhoneDisplay(previewClient.preferred_phone) : '--'}</span>
                            <svg className="crp-field-edit-icon" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </div>
                        </div>
                        <div className="hs-preview-field">
                          <label>Address</label>
                          <MapsLink parts={[previewClient?.preferred_street, previewClient?.preferred_city, previewClient?.preferred_state, previewClient?.preferred_zip]} style={{ fontSize: '0.82rem', color: '#0091ae' }} />
                        </div>
                        <div className="hs-preview-field">
                          <label>Type</label>
                          <span>{previewClient?.client_type === 'organization' ? 'Client' : 'Person'}</span>
                        </div>
                      </div>

                      {/* Contact people — compact roster under the contact-info section (cp-detail-persons-v1). */}
                      {contactPersons.length > 0 && (
                        <div className="hs-preview-section" data-cp-detail="cp-detail-persons-v1">
                          <h4>Contact people</h4>
                          {contactPersons.map(p => (
                            <div className="hs-preview-field" key={p.id}>
                              <label>{p.role_label || (p.is_primary ? 'Primary' : 'Person')}</label>
                              <span>{p.full_name}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="hs-preview-section">
                        <h4>CRM Activity</h4>
                        <div className="hs-preview-field">
                          <label>Leads</label>
                          <span>{previewClient?.lead_count ?? 0}</span>
                        </div>
                        <div className="hs-preview-field">
                          <label>Deals</label>
                          <span>{previewClient?.deal_count ?? 0}</span>
                        </div>
                        {previewClient?.latest_deal_title && (
                          <div className="hs-preview-field">
                            <label>Latest Deal</label>
                            <span>{previewClient.latest_deal_title}</span>
                          </div>
                        )}
                        {previewClient?.latest_lead_title && (
                          <div className="hs-preview-field">
                            <label>Latest Lead</label>
                            <span>{previewClient.latest_lead_title}</span>
                          </div>
                        )}
                      </div>

                      {(previewClient?.legacy_total_invoiced ?? 0) > 0 && (
                        <div className="hs-preview-section">
                          <h4>Financial Summary</h4>
                          <div className="hs-preview-field">
                            <label>Total Invoiced</label>
                            <span className="hs-preview-money">{currencyFormatter.format(previewClient?.legacy_total_invoiced ?? 0)}</span>
                          </div>
                          <div className="hs-preview-field">
                            <label>Total Paid</label>
                            <span className="hs-preview-money">{currencyFormatter.format(previewClient?.legacy_total_paid ?? 0)}</span>
                          </div>
                          {(previewClient?.legacy_balance ?? 0) !== 0 && (
                            <div className="hs-preview-field">
                              <label>Balance</label>
                              <span className="hs-preview-money hs-preview-balance">{currencyFormatter.format(previewClient?.legacy_balance ?? 0)}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Invoice Cards */}
                      {financialLoading ? (
                        <div className="hs-preview-section"><h4>Invoices</h4><p style={{ color: '#94a3b8', fontSize: 13 }}>Loading...</p></div>
                      ) : previewInvoices.length > 0 && (
                        <div className="hs-preview-section">
                          <h4>Invoices & Payments ({previewInvoices.length})</h4>
                          <div className="hs-invoice-list">
                            {previewInvoices.map((inv) => (
                              <div key={inv.id} className={`hs-invoice-card hs-invoice-${inv.txn_type?.toLowerCase().replace(/\s+/g, '-') ?? 'other'}`}>
                                <div className="hs-invoice-card-header">
                                  <span className={`hs-invoice-type-badge ${inv.txn_type === 'Invoice' ? 'invoice' : inv.txn_type === 'Payment' ? 'payment' : 'other'}`}>
                                    {inv.txn_type}
                                  </span>
                                  <span className="hs-invoice-amount">{currencyFormatter.format(inv.amount)}</span>
                                </div>
                                <div className="hs-invoice-card-details">
                                  {inv.ref_number && <span className="hs-invoice-ref">#{inv.ref_number}</span>}
                                  {inv.txn_date && <span className="hs-invoice-date">{formatDatePref(inv.txn_date)}</span>}
                                  {inv.payment_method && <span className="hs-invoice-method">{inv.payment_method}</span>}
                                </div>
                                {inv.job_name && <div className="hs-invoice-job">{inv.job_name}</div>}
                                {inv.memo && <div className="hs-invoice-memo">{inv.memo}</div>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Document Cards */}
                      {!financialLoading && previewDocuments.length > 0 && (
                        <div className="hs-preview-section">
                          <h4>Documents ({previewDocuments.length})</h4>
                          <div className="hs-document-list">
                            {previewDocuments.map((doc) => (
                              <a
                                key={doc.id}
                                className="hs-document-card"
                                href={doc.document_url ?? '#'}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => { if (!doc.document_url) e.preventDefault() }}
                              >
                                <div className="hs-document-icon">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                    <polyline points="14 2 14 8 20 8"/>
                                  </svg>
                                </div>
                                <div className="hs-document-info">
                                  <span className="hs-document-type">{doc.doc_type}</span>
                                  {doc.doc_date && <span className="hs-document-date">{formatDatePref(doc.doc_date)}</span>}
                                  {doc.linked_qb_ref && <span className="hs-document-ref">Ref #{doc.linked_qb_ref}</span>}
                                  {doc.linked_qb_amount != null && <span className="hs-document-amount">{currencyFormatter.format(doc.linked_qb_amount)}</span>}
                                </div>
                                <div className="hs-document-link-icon">
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                                    <polyline points="15 3 21 3 21 9"/>
                                    <line x1="10" y1="14" x2="21" y2="3"/>
                                  </svg>
                                </div>
                              </a>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Projects */}
                      {!financialLoading && previewJobs.length > 0 && (
                        <div className="hs-preview-section">
                          <h4>{LABELS.projects} ({previewJobs.length})</h4>
                          <div className="hs-jobs-list">
                            {previewJobs.map((job) => (
                              <div key={job.id} className="hs-job-card">
                                {job.raw_entry && <div className="hs-job-description">{job.raw_entry}</div>}
                                <div className="hs-job-meta">
                                  {job.job_date && <span>{job.job_date}</span>}
                                  {job.job_type && <span>{job.job_type}</span>}
                                  {job.job_amount != null && job.job_amount > 0 && <span>{currencyFormatter.format(job.job_amount)}</span>}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Related Contacts — Salesforce-style duplicate detection */}
                      {relatedContacts.length > 0 && (
                        <div className="hs-preview-section">
                          <h4>Related Contacts ({relatedContacts.length})</h4>
                          <div className="hs-related-list">
                            {relatedContacts.map((rc) => (
                              <div
                                key={rc.id}
                                className="hs-related-card"
                                onClick={() => {
                                  fetchClient(rc.id).then((c) => applyClientOrRedirect(c)).catch(() => {})
                                }}
                              >
                                <div className="hs-related-avatar">{(rc.name || '?')[0].toUpperCase()}</div>
                                <div className="hs-related-info">
                                  <span className="hs-related-name">{rc.name}</span>
                                  <span className="hs-related-id">{rc.id}</span>
                                  <div className="hs-related-reasons">
                                    {rc.reasons.map((r) => (
                                      <span key={r} className={`hs-related-tag ${r}`}>
                                        {reasonLabel(r)}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {previewClient?.aliases && (
                        <div className="hs-preview-section">
                          <h4>Aliases</h4>
                          <p className="hs-preview-aliases">{previewClient.aliases}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Lead preview sidebar — extracted to src/views/leads/PreviewPane.tsx (R8) */}
                {(previewLead || previewLeadClosing) && (
                  <Suspense fallback={<div className="hs-preview" />}>
                    <LeadPreviewPane
                      previewLead={previewLead}
                      previewLeadClosing={previewLeadClosing}
                      previewLeadEditing={previewLeadEditing}
                      setPreviewLeadEditing={setPreviewLeadEditing}
                      previewLeadForm={previewLeadForm}
                      setPreviewLeadForm={setPreviewLeadForm}
                      previewLeadSaving={previewLeadSaving}
                      setPreviewLeadSaving={setPreviewLeadSaving}
                      previewLeadActivities={previewLeadActivities}
                      setPreviewLeadActivities={setPreviewLeadActivities}
                      previewLeadNewComment={previewLeadNewComment}
                      setPreviewLeadNewComment={setPreviewLeadNewComment}
                      previewLeadCommentSaving={previewLeadCommentSaving}
                      setPreviewLeadCommentSaving={setPreviewLeadCommentSaving}
                      previewLeadNoteLoggedRef={previewLeadNoteLoggedRef}
                      setPreviewLead={setPreviewLead}
                      setLeadList={setLeadList}
                      setLiveLeads={setLiveLeads}
                      closeLeadPreview={closeLeadPreview}
                      updateLead={updateLead}
                      fetchLeadActivities={fetchLeadActivities}
                      createNote={createNote}
                      markLeadNoteLogged={markLeadNoteLogged}
                      handleClickToCall={handleClickToCall}
                      getLeadStatusColor={getLeadStatusColor}
                      getLeadStatusLabel={getLeadStatusLabel}
                      currencyFormatter={currencyFormatter}
                      formatDatePref={formatDatePref}
                      formatPhoneDisplay={formatPhoneDisplay}
                      PhoneLink={PhoneLink}
                      MapsLink={MapsLink}
                      onConvertLead={(lead) => setLeadConvertModal({ leadId: lead.id, leadTitle: lead.title || lead.name || `Lead #${lead.id}` })}
                      onDisqualifyLead={(lead) => setDisqualifyModal({ leadId: String(lead.id), leadTitle: lead.title || lead.name || `Lead #${lead.id}` })}
                      openCreateTaskForLead={(lead) => {
                        setCreateFormData({ linkKind: 'lead', linkLeadId: String(lead.id), linkLeadLabel: lead.title || (lead as any).name || `Lead #${lead.id}` } as any)
                        setShowCreateModal('task')
                      }}
                      leadPipelineStatuses={leadPipelineStatuses}
                    />
                  </Suspense>
                )}

                {/* Ticket preview sidebar */}
                {(previewTicket || previewTicketClosing) && (
                  <div className={`hs-preview${previewTicketClosing ? ' closing' : ''}`}>
                    <div className="hs-preview-header">
                      <strong className="hs-preview-name">{previewTicket?.title}</strong>
                      <button className="hs-preview-close" onClick={closeTicketPreview}><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg></button>
                    </div>
                    <div className="hs-preview-body">
                      <div className="hs-preview-hero">
                        <div className="hs-preview-avatar-lg" style={{ background: TICKET_STATUS_COLORS[previewTicket?.status ?? ''] || '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M15 5v2"/><path d="M15 11v2"/><path d="M15 17v2"/><path d="M5 5h14a2 2 0 0 1 2 2v3a2 2 0 0 0 0 4v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-3a2 2 0 0 0 0-4V7a2 2 0 0 1 2-2z"/></svg>
                        </div>
                        <div className="hs-preview-hero-info">
                          <h3>{previewTicket?.title}</h3>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <span className="status-chip" style={{ background: TICKET_STATUS_COLORS[previewTicket?.status ?? ''] || '#94a3b8', color: '#fff' }}>
                              {previewTicket?.status === 'in_progress' ? 'In Progress' : (previewTicket?.status ?? '').charAt(0).toUpperCase() + (previewTicket?.status ?? '').slice(1)}
                            </span>
                            <span className="status-chip" style={{ background: TICKET_PRIORITY_COLORS[previewTicket?.priority ?? ''] || '#94a3b8', color: '#fff' }}>
                              {(previewTicket?.priority ?? '').charAt(0).toUpperCase() + (previewTicket?.priority ?? '').slice(1)}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="hs-preview-section">
                        <h4 style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span>Ticket Info</span>
                          <button
                            type="button"
                            onClick={() => {
                              if (!previewTicket) return
                              const t = previewTicket as any
                              setTicketLinksDraft({
                                contactId: t.unified_client_id || null,
                                contactLabel: t.contact_name || t.unified_client_id || '',
                                dealId: t.deal_id ?? null,
                                dealLabel: t.deal_title || (t.deal_id ? `#${t.deal_id}` : ''),
                                projectId: t.project_id || null,
                                projectLabel: t.project_name || t.project_id || '',
                              })
                              setTicketLinksContactQuery('')
                              setTicketLinksDealQuery('')
                              setTicketLinksProjectQuery('')
                              setTicketLinksOpen(true)
                            }}
                            style={{ background: '#fff', border: '1px solid #cbd6e2', color: '#1976d2', fontSize: 11, fontWeight: 600, cursor: 'pointer', padding: '3px 10px', borderRadius: 4 }}
                          >Edit links</button>
                        </h4>
                        <div className="hs-preview-field">
                          <label>Status</label>
                          <span>{previewTicket?.status === 'in_progress' ? 'In Progress' : (previewTicket?.status ?? '').charAt(0).toUpperCase() + (previewTicket?.status ?? '').slice(1)}</span>
                        </div>
                        <div className="hs-preview-field">
                          <label>Priority</label>
                          <span>{(previewTicket?.priority ?? '').charAt(0).toUpperCase() + (previewTicket?.priority ?? '').slice(1)}</span>
                        </div>
                        {previewTicket?.category && (
                          <div className="hs-preview-field">
                            <label>Category</label>
                            <span>{previewTicket.category}</span>
                          </div>
                        )}
                        <div className="hs-preview-field">
                          <label>Assigned To</label>
                          <span>{previewTicket?.assigned_to ? (CALENDAR_USERS_MAP[previewTicket.assigned_to] || `User ${previewTicket.assigned_to}`) : '--'}</span>
                        </div>
                        {previewTicket?.unified_client_id && (
                          <div className="hs-preview-field">
                            <label>Contact</label>
                            <span>{(previewTicket as any).contact_name || previewTicket.unified_client_id}</span>
                          </div>
                        )}
                        {previewTicket?.deal_id && (
                          <div className="hs-preview-field">
                            <label>Deal</label>
                            <span>{(previewTicket as any).deal_title || `#${previewTicket.deal_id}`}</span>
                          </div>
                        )}
                        {(previewTicket as any)?.project_id && (
                          <div className="hs-preview-field">
                            <label>Project</label>
                            <span>{(previewTicket as any).project_name || (previewTicket as any).project_id}</span>
                          </div>
                        )}
                      </div>

                      <div className="hs-preview-section">
                        <h4>Timeline</h4>
                        <div className="hs-preview-field">
                          <label>Created</label>
                          <span>{previewTicket?.created_at ? formatDatePref(previewTicket.created_at) : '--'}</span>
                        </div>
                        <div className="hs-preview-field">
                          <label>Updated</label>
                          <span>{previewTicket?.updated_at ? formatDatePref(previewTicket.updated_at) : '--'}</span>
                        </div>
                        {previewTicket?.closed_at && (
                          <div className="hs-preview-field">
                            <label>Closed</label>
                            <span>{formatDatePref(previewTicket.closed_at)}</span>
                          </div>
                        )}
                      </div>

                      {previewTicket?.description && (
                        <div className="hs-preview-section">
                          <h4>Description</h4>
                          <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.5, margin: 0 }}>{previewTicket.description}</p>
                        </div>
                      )}

                      {(previewTicket?.resolution_category || previewTicket?.resolution) && (
                        <div className="hs-preview-section">
                          <h4>Resolution</h4>
                          {previewTicket?.resolution_category && (
                            <div className="hs-preview-field">
                              <label>Category</label>
                              <span>{previewTicket.resolution_category}</span>
                            </div>
                          )}
                          {previewTicket?.resolution && (
                            <div className="hs-preview-field">
                              <label>Note</label>
                              <span>{previewTicket.resolution}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {previewTicket && previewTicket.status !== 'closed' && previewTicket.status !== 'resolved' && (
                        <div className="hs-preview-section">
                          {!ticketCloseForm.open ? (
                            <button
                              onClick={() => setTicketCloseForm({ open: true, category: '', note: '', saving: false })}
                              style={{ width: '100%', padding: '8px 0', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}
                            >Close Ticket</button>
                          ) : (
                            <div ref={el => { if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50) }} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                              <h4 style={{ margin: 0 }}>Close Ticket</h4>
                              <div>
                                <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Resolution Category *</label>
                                <select
                                  value={ticketCloseForm.category}
                                  onChange={e => setTicketCloseForm(f => ({ ...f, category: e.target.value }))}
                                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.85rem' }}
                                >
                                  <option value="">Select...</option>
                                  <option value="Resolved">Resolved</option>
                                  <option value="Won't Fix">Won't Fix</option>
                                  <option value="Duplicate">Duplicate</option>
                                  <option value="No Response">No Response</option>
                                  <option value="Not a Bug">Not a Bug</option>
                                  <option value="Referred Out">Referred Out</option>
                                  <option value="Other">Other</option>
                                </select>
                              </div>
                              <div>
                                <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Note (optional)</label>
                                <textarea
                                  rows={3}
                                  value={ticketCloseForm.note}
                                  onChange={e => setTicketCloseForm(f => ({ ...f, note: e.target.value }))}
                                  placeholder="Describe how this was resolved..."
                                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.85rem', resize: 'vertical', boxSizing: 'border-box' }}
                                />
                              </div>
                              {!ticketCloseForm.category && (
                                <div style={{ padding: '6px 10px', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6, fontSize: '0.78rem', color: '#92400e' }}>
                                  A resolution category is required to close this ticket.
                                </div>
                              )}
                              <div style={{ display: 'flex', gap: 8 }}>
                                <button
                                  disabled={ticketCloseForm.saving}
                                  onClick={() => setTicketCloseForm({ open: false, category: '', note: '', saving: false })}
                                  style={{ flex: 1, padding: '8px 0', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: '0.82rem', cursor: 'pointer', color: '#475569' }}
                                >Cancel</button>
                                <button
                                  disabled={!ticketCloseForm.category || ticketCloseForm.saving}
                                  onClick={async () => {
                                    if (!previewTicket || !ticketCloseForm.category) return
                                    setTicketCloseForm(f => ({ ...f, saving: true }))
                                    try {
                                      const updated = await updateTicket(previewTicket.id, {
                                        status: 'closed',
                                        resolutionCategory: ticketCloseForm.category,
                                        resolution: ticketCloseForm.note.trim() || undefined,
                                      })
                                      setPreviewTicket(updated)
                                      setTicketCloseForm({ open: false, category: '', note: '', saving: false })
                                      fetchTickets({ page: ticketsPage, limit: tablePageSize, status: ticketsFilter === 'all' ? undefined : ticketsFilter, sort: ticketsSort.field, order: ticketsSort.order })
                                        .then(res => { setTicketsData(res.tickets); setTicketsTotal(res.total) }).catch(() => {})
                                    } catch (e: any) {
                                      alert('Failed to close ticket: ' + (e?.message || String(e)))
                                      setTicketCloseForm(f => ({ ...f, saving: false }))
                                    }
                                  }}
                                  style={{ flex: 1, padding: '8px 0', background: ticketCloseForm.category ? '#dc2626' : '#cbd5e1', color: '#fff', border: 'none', borderRadius: 6, fontSize: '0.82rem', fontWeight: 600, cursor: ticketCloseForm.category ? 'pointer' : 'not-allowed' }}
                                >{ticketCloseForm.saving ? 'Closing...' : 'Close Ticket'}</button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Ticket Edit Links modal */}
                {ticketLinksOpen && previewTicket && (
                  <div onClick={() => !ticketLinksSaving && setTicketLinksOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
                    <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, width: '100%', maxWidth: 460, padding: 18, boxShadow: '0 12px 30px rgba(0,0,0,0.2)' }}>
                      <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Edit ticket links</h3>
                      <p style={{ margin: 0, fontSize: 12, color: '#64748b', marginBottom: 14 }}>
                        Contact is required. Deal and Project are optional and may belong to a different contact.
                      </p>

                      {/* Contact picker */}
                      <div style={{ marginBottom: 12, position: 'relative' }}>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 4 }}>Contact *</label>
                        {ticketLinksDraft.contactId ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: 13, background: '#f8fafc' }}>
                            <span style={{ flex: 1 }}>{ticketLinksDraft.contactLabel || ticketLinksDraft.contactId}</span>
                            <button type="button" onClick={() => setTicketLinksDraft(d => ({ ...d, contactId: null, contactLabel: '' }))} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 14 }} title="Change contact">×</button>
                          </div>
                        ) : (
                          <input
                            autoFocus
                            value={ticketLinksContactQuery}
                            onChange={e => setTicketLinksContactQuery(e.target.value)}
                            placeholder="Search contacts by name or email…"
                            style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: 13, boxSizing: 'border-box' }}
                          />
                        )}
                        {!ticketLinksDraft.contactId && ticketLinksContactResults.length > 0 && (
                          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 2, background: '#fff', border: '1px solid #cbd6e2', borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', zIndex: 10, maxHeight: 220, overflowY: 'auto' }}>
                            {ticketLinksContactResults.map((c: any) => (
                              <button key={c.unified_client_id} type="button" onClick={() => { setTicketLinksDraft(d => ({ ...d, contactId: c.unified_client_id, contactLabel: c.preferred_name || c.unified_client_id })); setTicketLinksContactQuery('') }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px', background: 'none', border: 'none', borderBottom: '1px solid #f1f5f9', fontSize: 12, cursor: 'pointer' }}>
                                <div style={{ fontWeight: 600, color: '#0f172a' }}>{c.preferred_name || c.unified_client_id}</div>
                                <div style={{ color: '#64748b', fontSize: 11 }}>{[c.preferred_email, c.preferred_phone].filter(Boolean).join(' · ')}</div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Deal picker */}
                      <div style={{ marginBottom: 12, position: 'relative' }}>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 4 }}>Deal</label>
                        {ticketLinksDraft.dealId ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: 13, background: '#f8fafc' }}>
                            <span style={{ flex: 1 }}>{ticketLinksDraft.dealLabel}</span>
                            <button type="button" onClick={() => setTicketLinksDraft(d => ({ ...d, dealId: null, dealLabel: '' }))} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 14 }} title="Clear deal">×</button>
                          </div>
                        ) : (
                          <input
                            value={ticketLinksDealQuery}
                            onChange={e => setTicketLinksDealQuery(e.target.value)}
                            placeholder="Search deals by title…"
                            style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: 13, boxSizing: 'border-box' }}
                          />
                        )}
                        {!ticketLinksDraft.dealId && ticketLinksDealResults.length > 0 && (
                          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 2, background: '#fff', border: '1px solid #cbd6e2', borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', zIndex: 10, maxHeight: 220, overflowY: 'auto' }}>
                            {ticketLinksDealResults.map((d: any) => (
                              <button key={d.id} type="button" onClick={() => { setTicketLinksDraft(cur => ({ ...cur, dealId: d.id, dealLabel: d.title || `#${d.id}` })); setTicketLinksDealQuery('') }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px', background: 'none', border: 'none', borderBottom: '1px solid #f1f5f9', fontSize: 12, cursor: 'pointer' }}>
                                <div style={{ fontWeight: 600, color: '#0f172a' }}>{d.title || `#${d.id}`}</div>
                                <div style={{ color: '#64748b', fontSize: 11 }}>{d.customer_name || d.client_name || ''}</div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Project picker */}
                      <div style={{ marginBottom: 16, position: 'relative' }}>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 4 }}>Project</label>
                        {ticketLinksDraft.projectId ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: 13, background: '#f8fafc' }}>
                            <span style={{ flex: 1 }}>{ticketLinksDraft.projectLabel}</span>
                            <button type="button" onClick={() => setTicketLinksDraft(d => ({ ...d, projectId: null, projectLabel: '' }))} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 14 }} title="Clear project">×</button>
                          </div>
                        ) : (
                          <input
                            value={ticketLinksProjectQuery}
                            onChange={e => setTicketLinksProjectQuery(e.target.value)}
                            placeholder="Search projects by name…"
                            style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: 13, boxSizing: 'border-box' }}
                          />
                        )}
                        {!ticketLinksDraft.projectId && ticketLinksProjectResults.length > 0 && (
                          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 2, background: '#fff', border: '1px solid #cbd6e2', borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', zIndex: 10, maxHeight: 220, overflowY: 'auto' }}>
                            {ticketLinksProjectResults.map((p: any) => (
                              <button key={p.id} type="button" onClick={() => { setTicketLinksDraft(cur => ({ ...cur, projectId: p.id, projectLabel: p.name || p.id })); setTicketLinksProjectQuery('') }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px', background: 'none', border: 'none', borderBottom: '1px solid #f1f5f9', fontSize: 12, cursor: 'pointer' }}>
                                <div style={{ fontWeight: 600, color: '#0f172a' }}>{p.name || p.id}</div>
                                <div style={{ color: '#64748b', fontSize: 11 }}>{p.customer_name || ''}</div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <button type="button" disabled={ticketLinksSaving} onClick={() => setTicketLinksOpen(false)} style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, background: '#fff', color: '#475569', border: '1px solid #cbd6e2', borderRadius: 4, cursor: 'pointer' }}>Cancel</button>
                        <button
                          type="button"
                          disabled={ticketLinksSaving || !ticketLinksDraft.contactId}
                          onClick={async () => {
                            if (!previewTicket || !ticketLinksDraft.contactId) return
                            setTicketLinksSaving(true)
                            try {
                              const updated = await updateTicket(previewTicket.id as any, {
                                unifiedClientId: ticketLinksDraft.contactId,
                                dealId: ticketLinksDraft.dealId,
                                projectId: ticketLinksDraft.projectId,
                              })
                              // Merge in the labels from the draft so the preview
                              // immediately shows readable names instead of IDs
                              // (avoids a re-fetch for joined names).
                              setPreviewTicket({
                                ...(updated as any),
                                contact_name: ticketLinksDraft.contactLabel,
                                deal_title: ticketLinksDraft.dealLabel,
                                project_name: ticketLinksDraft.projectLabel,
                              } as any)
                              setTicketLinksOpen(false)
                            } catch (e: any) {
                              alert('Failed to update: ' + (e?.message || String(e)))
                            } finally {
                              setTicketLinksSaving(false)
                            }
                          }}
                          style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', opacity: (ticketLinksSaving || !ticketLinksDraft.contactId) ? 0.5 : 1 }}
                        >{ticketLinksSaving ? 'Saving…' : 'Save'}</button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Call preview sidebar */}
                {(previewCall || previewCallClosing) && (
                  <div className={`hs-preview${previewCallClosing ? ' closing' : ''}`}>
                    <div className="hs-preview-header">
                      <strong className="hs-preview-name">{previewCall?.subject}</strong>
                      <button className="hs-preview-close" onClick={closeCallPreview}><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg></button>
                    </div>
                    <div className="hs-preview-body">
                      <div className="hs-preview-hero">
                        <div className="hs-preview-avatar-lg" style={{ background: CALL_DIRECTION_COLORS[previewCall?.direction ?? 1] || '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.81.36 1.61.7 2.36a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.75.34 1.55.57 2.36.7A2 2 0 0 1 22 16.92z"/></svg>
                        </div>
                        <div className="hs-preview-hero-info">
                          <h3>{previewCall?.subject || 'Call'}</h3>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                            <span className="status-chip" style={{ background: CALL_DIRECTION_COLORS[previewCall?.direction ?? 1] || '#94a3b8', color: '#fff' }}>
                              {CALL_DIRECTION_LABELS[previewCall?.direction ?? 1] || 'Unknown'}
                            </span>
                            {previewCall?.call_outcome && (
                              <span className="hs-outcome-badge" style={{ background: getOutcomeColor(previewCall.call_outcome) }}>
                                {previewCall.call_outcome}
                              </span>
                            )}
                            {previewCall?.sentiment && (
                              <span className="hs-sentiment-badge" style={{ background: SENTIMENT_COLORS[previewCall.sentiment] || '#94a3b8' }}>
                                <SentimentIcon kind={previewCall.sentiment} /> {previewCall.sentiment}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Phone direction row */}
                      {(previewCall?.phone_from || previewCall?.phone_to) && (
                        <div className="hs-preview-section">
                          <div className="har-phone-direction">
                            <span className="har-phone-num">{previewCall?.phone_from || '--'}</span>
                            <span className="har-phone-arrow">{previewCall?.direction === 2 ? '←' : '→'}</span>
                            <span className="har-phone-num">{previewCall?.phone_to || '--'}</span>
                          </div>
                        </div>
                      )}

                      {/* Audio Player (Tier 3) */}
                      {previewCall?.rc_recording_id && (
                        <div className="hs-preview-section">
                          <h4>Recording</h4>
                          <div className="har-audio-wrap">
                            <RecordingPlayer recordingId={previewCall.rc_recording_id} />
                          </div>
                          {/*
                            Grok STT cron auto-submits every recording within
                            5 min, so the old "Request Transcription" button
                            was redundant. We only surface a manual trigger
                            when the last attempt failed, so users have an
                            explicit retry path without nagging every row.
                          */}
                          {!previewCall.transcript && (previewCall as any)?.settings && (() => {
                            let aiStatus: string | null = null
                            try { aiStatus = JSON.parse((previewCall as any).settings || '{}')?.ai_status || null } catch { aiStatus = null }
                            if (aiStatus !== 'failed') return null
                            return (
                              <button
                                className="har-transcribe-btn"
                                onClick={() => { submitTranscription(previewCall.id).catch(() => {}) }}
                              >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 0 1 15-6.7L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-15 6.7L3 16"/><path d="M3 21v-5h5"/></svg>
                                Retry transcription
                              </button>
                            )
                          })()}
                        </div>
                      )}

                      {/* AI Summary (Tier 5) */}
                      {previewCall?.summary && (
                        <div className="hs-preview-section">
                          <div className="har-ai-summary-card">
                            <div className="har-ai-summary-header">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
                              AI Summary
                            </div>
                            {/* calls-disposition-v1: the transcript-level summary can
                                hallucinate ("Leo left a voicemail") on calls that only
                                reached the callee's greeting — surface the AI-computed
                                disposition as a caveat above the summary text so the
                                reader doesn't trust a false positive. */}
                            {(previewCall?.ai_disposition === 'reached_voicemail_no_message' || previewCall?.ai_disposition === 'no_speech_or_ivr') && (
                              <div
                                className="har-ai-summary-caveat"
                                data-disposition={previewCall.ai_disposition}
                                data-feature={CALLS_DISPOSITION_MARKER}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, color: '#b45309', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 999, padding: '2px 8px', marginBottom: 6 }}
                              >
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
                                {previewCall.ai_disposition === 'reached_voicemail_no_message' ? 'Reached voicemail — no message left' : 'No message'}
                              </div>
                            )}
                            <p className="har-ai-summary-text">{previewCall.summary}</p>
                          </div>
                        </div>
                      )}

                      {/* Transcript (Tier 5) */}
                      {previewCall?.transcript && (
                        <div className="hs-preview-section">
                          <h4>Transcript</h4>
                          <div className="har-transcript-wrap">
                            {previewCall.transcript.split('\n').filter(Boolean).map((line, i) => {
                              const speakerMatch = line.match(/^(Speaker\s*\d+|Agent|Customer|Rep|Caller):\s*(.+)$/i)
                              if (speakerMatch) {
                                const isAgent = /agent|rep/i.test(speakerMatch[1])
                                return (
                                  <div key={i} className={`har-transcript-bubble ${isAgent ? 'har-bubble-agent' : 'har-bubble-caller'}`}>
                                    <span className="har-bubble-speaker">{speakerMatch[1]}</span>
                                    <span className="har-bubble-text">{speakerMatch[2]}</span>
                                  </div>
                                )
                              }
                              return <p key={i} className="har-transcript-line">{line}</p>
                            })}
                          </div>
                        </div>
                      )}

                      <div className="hs-preview-section">
                        <h4>Call Info</h4>
                        <div className="hs-preview-field">
                          <label>Direction</label>
                          <span>{CALL_DIRECTION_LABELS[previewCall?.direction ?? 1] || 'Unknown'}</span>
                        </div>
                        <div className="hs-preview-field">
                          <label>Duration</label>
                          <span>{formatDurationSecs(previewCall?.duration_seconds ?? null)}</span>
                        </div>
                        {previewCall?.call_result && (
                          <div className="hs-preview-field">
                            <label>Result</label>
                            <span>{previewCall.call_result}</span>
                          </div>
                        )}
                        {/* calls-disposition-v1: AI-computed display disposition,
                            shown alongside (never replacing) the raw carrier Result
                            row above. Only rendered for known enum values. */}
                        {previewCall?.ai_disposition && DISPOSITION_OUTCOME[previewCall.ai_disposition] && (
                          <div className="hs-preview-field" data-feature={CALLS_DISPOSITION_MARKER} data-disposition={previewCall.ai_disposition}>
                            <label>Disposition</label>
                            <span>{DISPOSITION_OUTCOME[previewCall.ai_disposition].label}</span>
                          </div>
                        )}
                        <div className="hs-preview-field">
                          <label>Assigned To</label>
                          <span>{previewCall?.responsible_id && CALENDAR_USERS_MAP[previewCall.responsible_id] ? CALENDAR_USERS_MAP[previewCall.responsible_id] : 'Unassigned'}</span>
                        </div>
                        {previewCall?.contact_id && (
                          <div className="hs-preview-field">
                            <label>Contact</label>
                            <span>{previewCall.contact_id}</span>
                          </div>
                        )}
                        {previewCall?.owner_id ? (
                          <div className="hs-preview-field">
                            <label>{previewCall.owner_type_id === 2 ? 'Deal' : previewCall.owner_type_id === 1 ? 'Lead' : 'Contact'}</label>
                            <span>#{previewCall.owner_id}</span>
                          </div>
                        ) : null}
                      </div>

                      <div className="hs-preview-section">
                        <h4>Timeline</h4>
                        <div className="hs-preview-field">
                          <label>Created</label>
                          <span>{previewCall?.created ? formatDatePref(previewCall.created) : '--'}</span>
                        </div>
                        <div className="hs-preview-field">
                          <label>Updated</label>
                          <span>{previewCall?.last_updated ? formatDatePref(previewCall.last_updated) : '--'}</span>
                        </div>
                        {previewCall?.start_time && (
                          <div className="hs-preview-field">
                            <label>Start Time</label>
                            <span>{formatDatePref(previewCall.start_time)}</span>
                          </div>
                        )}
                      </div>

                      {previewCall?.description && (
                        <div className="hs-preview-section">
                          <h4>Notes</h4>
                          <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.5, margin: 0 }}>{previewCall.description}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Table settings side panel */}
                {tableSettingsOpen && (
                  <div className="hs-settings-panel">
                    <div className="hs-settings-header">
                      <span className="hs-settings-title">Table settings</span>
                      <button className="hs-settings-close" onClick={() => setTableSettingsOpen(false)}>
                        <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                      </button>
                    </div>
                    <div className="hs-settings-body">
                      {/* Pagination */}
                      <div className="hs-settings-group">
                        <label className="hs-settings-label">
                          Pagination
                          <span className="hs-info-icon" title="Number of rows shown per page">
                            <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg>
                          </span>
                        </label>
                        {([100, 200, 500] as const).map(size => (
                          <label className="hs-settings-radio" key={size}>
                            <input
                              type="radio"
                              name="pageSize"
                              checked={tablePageSize === size}
                              onChange={() => { setTablePageSize(size); setClientPage(1); setCompanyPage(1) }}
                            />
                            <span className="hs-radio-indicator" />
                            <span>{size} per page</span>
                          </label>
                        ))}
                      </div>

                      <hr className="hs-settings-divider" />

                      {/* Row height */}
                      <div className="hs-settings-group">
                        <label className="hs-settings-label">
                          Row height
                          <span className="hs-info-icon" title="Adjust the vertical spacing of table rows">
                            <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg>
                          </span>
                        </label>
                        {([
                          { value: 'small' as const, label: 'Compact' },
                          { value: 'medium' as const, label: 'Default' },
                          { value: 'large' as const, label: 'Comfortable' },
                        ]).map(opt => (
                          <label className="hs-settings-radio" key={opt.value}>
                            <input
                              type="radio"
                              name="rowHeight"
                              checked={tableRowHeight === opt.value}
                              onChange={() => setTableRowHeight(opt.value)}
                            />
                            <span className="hs-radio-indicator" />
                            <span>{opt.label}</span>
                          </label>
                        ))}
                      </div>

                      <hr className="hs-settings-divider" />

                      {/* Zebra striping */}
                      <div className="hs-settings-group">
                        <div className="hs-settings-toggle-row">
                          <label className="hs-settings-label">
                            Zebra striping
                            <span className="hs-info-icon" title="Alternate row background colors for easier reading">
                              <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg>
                            </span>
                          </label>
                          <button
                            className={`hs-toggle${tableZebraStriping ? ' on' : ''}`}
                            onClick={() => setTableZebraStriping(!tableZebraStriping)}
                            role="switch"
                            aria-checked={tableZebraStriping}
                          >
                            <span className="hs-toggle-track">
                              <span className="hs-toggle-on">ON</span>
                              <span className="hs-toggle-off">OFF</span>
                              <span className="hs-toggle-handle" />
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>}
            />
          )}

          {activeView === 'preferences' && myCapabilities && !myCapabilities.access_settings && (
            <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
              <h2 style={{ color: '#33475b', marginBottom: 8 }}>Access denied</h2>
              <p>Settings are restricted to super-admin users.</p>
            </div>
          )}
          {activeView === 'preferences' && (!myCapabilities || myCapabilities.access_settings) && (
            <div className="prefs-page">
              <div className={`prefs-layout${prefsMobileShowMain ? ' prefs-mobile-main' : ''}`}>
                <aside className="prefs-sidebar" data-marker="prefs-ia-v2" aria-label="Settings navigation">
                  <div className="prefs-sidebar-inner">
                    <div className="prefs-sidebar-top">
                      <button type="button" className="prefs-backlink" onClick={() => setActiveView('command')}>
                        <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor" aria-hidden="true">
                          <path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95" />
                        </svg>
                        <span>Dashboard</span>
                      </button>
                      <label className="prefs-search" aria-label="Search Settings">
                        <svg width="15" height="15" viewBox="0 0 32 32" fill="currentColor" aria-hidden="true">
                          <path d="M28.14 29.29c-.29 0-.58-.11-.81-.33l-7.58-7.58a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l7.58 7.58a1.14 1.14 0 0 1-.81 1.95" />
                          <path d="M13.29 24.71c-6.3 0-11.43-5.13-11.43-11.43S6.98 1.86 13.29 1.86s11.43 5.13 11.43 11.43-5.13 11.43-11.43 11.43Zm0-20.57c-5.04 0-9.14 4.1-9.14 9.14s4.1 9.14 9.14 9.14 9.14-4.1 9.14-9.14-4.1-9.14-9.14-9.14" />
                        </svg>
                        <input
                          type="search"
                          aria-label="Search Settings"
                          role="searchbox"
                          placeholder="Search Settings"
                          value={prefsSearch}
                          onChange={(e) => setPrefsSearch(e.target.value)}
                        />
                      </label>
                    </div>

                    <div className="prefs-sidebar-scroll">
                      {visiblePrefsSections.map((section) => (
                        <nav key={section.title} className="prefs-nav-group" aria-label={section.title}>
                          <h2 className="prefs-nav-group-title">{section.title}</h2>
                          <ul className="prefs-nav-list">
                            {section.items.map((item) => {
                              const isActive = item.tab === prefsTab
                              const isExpanded = prefsExpandedSections.has(item.id)
                              return (
                                <li key={item.id}>
                                  <button
                                    type="button"
                                    className={`prefs-nav-item${isActive ? ' active' : ''}${item.disabled ? ' disabled' : ''}`}
                                    onClick={() => {
                                      if (item.expandable && item.children) {
                                        setPrefsExpandedSections(prev => {
                                          const next = new Set(prev)
                                          if (next.has(item.id)) next.delete(item.id)
                                          else next.add(item.id)
                                          return next
                                        })
                                      } else if (item.tab) {
                                        openPrefsTab(item.tab)
                                      }
                                    }}
                                    disabled={item.disabled}
                                    aria-current={isActive ? 'page' : undefined}
                                    aria-expanded={item.expandable ? isExpanded : undefined}
                                    title={item.label}
                                  >
                                    <span className="prefs-nav-item-label">{item.label}</span>
                                    {item.badge && (
                                      <span className="prefs-nav-item-badge">{item.badge}</span>
                                    )}
                                    {item.external && (
                                      <span className="prefs-nav-item-icon" aria-hidden="true">
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                          <path d="M13 5h6v6" />
                                          <path d="M10 14 19 5" />
                                          <path d="M19 13v6H5V5h6" />
                                        </svg>
                                      </span>
                                    )}
                                    {item.expandable && (
                                      <span className={`prefs-nav-item-icon prefs-nav-chevron${isExpanded ? ' expanded' : ''}`} aria-hidden="true">
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                          <path d="m9 6 6 6-6 6" />
                                        </svg>
                                      </span>
                                    )}
                                  </button>
                                  {item.expandable && item.children && isExpanded && (
                                    <ul className="prefs-nav-children">
                                      {item.children.map((child) => (
                                        <li key={child.id}>
                                          <button
                                            type="button"
                                            className={`prefs-nav-item prefs-nav-child${child.tab === prefsTab ? ' active' : ''}`}
                                            onClick={() => child.tab && openPrefsTab(child.tab)}
                                            title={child.label}
                                          >
                                            <span className="prefs-nav-item-label">{child.label}</span>
                                            {child.badge && (
                                              <span className="prefs-nav-item-badge">{child.badge}</span>
                                            )}
                                          </button>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </li>
                              )
                            })}
                          </ul>
                        </nav>
                      ))}
                      {visiblePrefsSections.length === 0 && (
                        <div className="prefs-nav-empty">No settings matched your search.</div>
                      )}
                    </div>
                  </div>
                </aside>

                <div className="prefs-main">
                  <button type="button" className="prefs-backlink prefs-mobile-back" onClick={() => setPrefsMobileShowMain(false)}>
                    <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor" aria-hidden="true">
                      <path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95" />
                    </svg>
                    <span>Settings menu</span>
                  </button>
                  {/* Sticky frosted title band — tab strips pin with it while
                      the settings cards scroll beneath (shared recipe + traps:
                      App.css glass-header region; guarded by
                      src/lib/shell/glassHeader). Pins against .prefs-main
                      (.prefs-page on <=920px). */}
                  <div className="prefs-header glass-header">
                    <h1 className="prefs-title">{activePrefsTitle}</h1>
                    {prefsTab === 'general' && (
                      <>
                        <div className="prefs-tabs" role="tablist" aria-label="General preferences tabs">
                          {GENERAL_SUB_TABS.map((tab) => (
                            <button
                              key={tab.key}
                              type="button"
                              role="tab"
                              aria-selected={generalSubTab === tab.key}
                              aria-current={generalSubTab === tab.key ? 'page' : undefined}
                              className={`prefs-tab${generalSubTab === tab.key ? ' active' : ''}`}
                              onClick={() => setGeneralSubTab(tab.key)}
                            >
                              {tab.label}
                            </button>
                          ))}
                        </div>
                        <p className="prefs-desc">{activePrefsDescription}</p>
                        <hr className="prefs-divider" />
                      </>
                    )}
                    {prefsTab === 'notifications' && (
                      <>
                        <div className="prefs-tabs" role="tablist" aria-label="Notification preferences tabs">
                          {NOTIF_SUB_TABS.map((tab) => (
                            <button
                              key={tab.key}
                              type="button"
                              role="tab"
                              aria-selected={notifSubTab === tab.key}
                              aria-current={notifSubTab === tab.key ? 'page' : undefined}
                              className={`prefs-tab${notifSubTab === tab.key ? ' active' : ''}`}
                              onClick={() => setNotifSubTab(tab.key)}
                            >
                              {tab.label}
                            </button>
                          ))}
                        </div>
                        <p className="prefs-desc">These preferences will only be applied to you.</p>
                        <hr className="prefs-divider" />
                      </>
                    )}
                    {prefsTab === 'account' && (
                      <>
                        <div className="prefs-tabs" role="tablist" aria-label="Account defaults tabs">
                          {ACCT_DEFAULTS_SUB_TABS.map((tab) => (
                            <button
                              key={tab.key}
                              type="button"
                              role="tab"
                              aria-selected={acctDefaultsSubTab === tab.key}
                              aria-current={acctDefaultsSubTab === tab.key ? 'page' : undefined}
                              className={`prefs-tab${acctDefaultsSubTab === tab.key ? ' active' : ''}`}
                              onClick={() => setAcctDefaultsSubTab(tab.key)}
                            >
                              {tab.label}
                            </button>
                          ))}
                        </div>
                        <p className="prefs-desc">{
                          acctDefaultsSubTab === 'general' ? 'These defaults will be applied to the entire account.' :
                          acctDefaultsSubTab === 'user_defaults' ? 'Set defaults for users in your account. To add or edit user permissions go to Users & Teams.' :
                          acctDefaultsSubTab === 'notification_profiles' ? 'These defaults will be used for user defaults and presets.' :
                          'These defaults will be applied to the entire account.'
                        }</p>
                        <hr className="prefs-divider" />
                      </>
                    )}
                    {prefsTab !== 'general' && prefsTab !== 'notifications' && prefsTab !== 'account' && prefsTab !== 'audit-log' && activePrefsDescription && (
                      <p className="prefs-desc">{activePrefsDescription}</p>
                    )}
                    {prefsTab === 'audit-log' && (
                      <Suspense fallback={null}>
                        <PrefsAuditLogHeader
                          auditLogTab={auditLogTab}
                          setAuditLogTab={setAuditLogTab}
                          auditCategoryFilter={auditCategoryFilter}
                          setAuditCategoryFilter={setAuditCategoryFilter}
                          auditDateFilter={auditDateFilter}
                          setAuditDateFilter={setAuditDateFilter}
                          auditPage={auditPage}
                          setAuditPage={setAuditPage}
                        />
                      </Suspense>
                    )}
                  </div>

                  {prefsTab === 'audit-log' && (
                    <Suspense fallback={PrefsLazyFallback}>
                      <PrefsAuditLog
                        auditLogTab={auditLogTab}
                        setAuditLogTab={setAuditLogTab}
                        auditCategoryFilter={auditCategoryFilter}
                        setAuditCategoryFilter={setAuditCategoryFilter}
                        auditDateFilter={auditDateFilter}
                        setAuditDateFilter={setAuditDateFilter}
                        auditPage={auditPage}
                        setAuditPage={setAuditPage}
                      />
                    </Suspense>
                  )}

                  {prefsTab === 'general' && (
                    <Suspense fallback={PrefsLazyFallback}>
                      <PrefsGeneral
                        generalSubTab={generalSubTab}
                        setPrefsTab={setPrefsTab}
                        prefsValues={prefsValues}
                        updatePref={updatePref}
                        prefsPhoto={prefsPhoto}
                        prefsPhotoRef={prefsPhotoRef}
                        prefsPhotoUploading={prefsPhotoUploading}
                        prefsPhotoError={prefsPhotoError}
                        handlePrefsPhotoChange={handlePrefsPhotoChange}
                        onEditPhoto={handleEditPrefsPhoto}
                        hrProfile={hrProfile}
                        setHrProfile={setHrProfile}
                        hrProfileLoading={hrProfileLoading}
                        hrProfileError={hrProfileError}
                        hrProfileDirty={hrProfileDirty}
                        hrProfileSaving={hrProfileSaving}
                        hrCancelAll={hrCancelAll}
                        hrSaveAll={hrSaveAll}
                        hrCollapsed={hrCollapsed}
                        toggleHrSection={toggleHrSection}
                        updateHrField={updateHrField}
                        updateHrContacts={updateHrContacts}
                        updateHrDependents={updateHrDependents}
                        revealHrPii={revealHrPii}
                        formatDatePref={formatDatePref}
                        currentUser={currentUser}
                        impersonating={impersonating}
                        emailSignaturesNode={<EmailSignaturesSection onToast={setComposeToast} />}
                        systemEmailSignatureNode={<SystemEmailSignatureSection onToast={setComposeToast} />}
                        emailTemplatesNode={<EmailTemplatesSection onToast={setComposeToast} userEmail={currentUser?.email || ''} />}
                        emailBccTokenNode={<EmailBccTokenSection onToast={setComposeToast} />}
                        emailScheduledListNode={<EmailScheduledListSection onToast={setComposeToast} />}
                      />
                    </Suspense>
                  )}

                {/* ========== Security Tab (top-level) ==========
                    Every card used to be a dead stub (Reset password, Set up passkeys, Set up 2FA,
                    Log Out of All Sessions, hardcoded "fake sessions" table, Remove/Delete user account).
                    BotPros Ops is 100% SSO via Cloudflare Access + Google, so identity management lives
                    in Google; user lifecycle lives in the Users admin tab. Replace with honest cards. */}
                {prefsTab === 'security' && (
                  <Suspense fallback={PrefsLazyFallback}>
                    <PrefsSecurity onOpenUsers={() => setPrefsTab('users')} currentUserEmail={currentUser?.email ?? null} />
                  </Suspense>
                )}

                {/* ========== Account Defaults Tab ==========
                    Extracted to its own lazy chunk in R5 stateful prefs round 2.
                    The npe (notification profile editor) full-page drawer remains
                    in App.tsx — it's gated by the activeView block above. */}
                {prefsTab === 'account' && (
                  <Suspense fallback={PrefsLazyFallback}>
                    <PrefsAccount
                      acctDefaultsSubTab={acctDefaultsSubTab}
                      acctDefaults={acctDefaults}
                      saveAcctDefaults={(next) => { setAcctDefaults(next); saveOrgSetting('acctDefaults', JSON.stringify(next)) }}
                      US_TIMEZONES={US_TIMEZONES}
                      FISCAL_YEAR_OPTIONS={FISCAL_YEAR_OPTIONS}
                      INDUSTRY_OPTIONS={INDUSTRY_OPTIONS}
                    />
                  </Suspense>
                )}

                {/* ========== Calling Tab (RingCentral) ========== */}
                {prefsTab === 'calling' && (
                  <Suspense fallback={PrefsLazyFallback}>
                    <PrefsCallingRoot
                      settingsLoading={settingsLoading}
                      rcConfig={rcConfig}
                      setRcConfig={setRcConfig}
                      rcSyncing={rcSyncing}
                      setRcSyncing={setRcSyncing}
                      rcSyncMsg={rcSyncMsg}
                      setRcSyncMsg={setRcSyncMsg}
                      rcBackfilling={rcBackfilling}
                      setRcBackfilling={setRcBackfilling}
                      rcMappings={rcMappings}
                      setRcMappings={setRcMappings}
                      rcMapUserId={rcMapUserId}
                      setRcMapUserId={setRcMapUserId}
                      rcMapExtId={rcMapExtId}
                      setRcMapExtId={setRcMapExtId}
                      rcMapExtNum={rcMapExtNum}
                      setRcMapExtNum={setRcMapExtNum}
                      rcMapPhone={rcMapPhone}
                      setRcMapPhone={setRcMapPhone}
                      setRcMapName={setRcMapName}
                      rcExtSearch={rcExtSearch}
                      setRcExtSearch={setRcExtSearch}
                      rcExtDropdownOpen={rcExtDropdownOpen}
                      setRcExtDropdownOpen={setRcExtDropdownOpen}
                      rcExtensions={rcExtensions}
                      setRcExtensions={setRcExtensions}
                      rcExtLoading={rcExtLoading}
                      setRcExtLoading={setRcExtLoading}
                      crmUsers={crmUsers}
                      rcOutcomes={rcOutcomes}
                      setRcOutcomes={setRcOutcomes}
                      rcOutcomeLabel={rcOutcomeLabel}
                      setRcOutcomeLabel={setRcOutcomeLabel}
                      rcOutcomeColor={rcOutcomeColor}
                      setRcOutcomeColor={setRcOutcomeColor}
                      rcBlockedNumbers={rcBlockedNumbers}
                      setRcBlockedNumbers={setRcBlockedNumbers}
                      rcBlockPhone={rcBlockPhone}
                      setRcBlockPhone={setRcBlockPhone}
                      rcBlockReason={rcBlockReason}
                      setRcBlockReason={setRcBlockReason}
                      formatDatePref={formatDatePref}
                    />
                  </Suspense>
                )}

                {/* ========== Users Tab ========== */}
                {prefsTab === 'users' && (
                  <>
                  <Suspense fallback={PrefsLazyFallback}>
                    <PrefsUsers
                      usersStatusDropdown={usersStatusDropdown}
                      setUsersStatusDropdown={setUsersStatusDropdown}
                      usersStatusFilter={usersStatusFilter}
                      setUsersStatusFilter={setUsersStatusFilter}
                      usersLastActiveDropdown={usersLastActiveDropdown}
                      setUsersLastActiveDropdown={setUsersLastActiveDropdown}
                      usersLastActiveFilter={usersLastActiveFilter}
                      setUsersLastActiveFilter={setUsersLastActiveFilter}
                      userMgmtMode={userMgmtMode}
                      setUserMgmtMode={setUserMgmtMode}
                      userMgmtForm={userMgmtForm}
                      setUserMgmtForm={setUserMgmtForm}
                      userMgmtError={userMgmtError}
                      setUserMgmtError={setUserMgmtError}
                      userMgmtTarget={userMgmtTarget}
                      setUserMgmtTarget={setUserMgmtTarget}
                      userMgmtSaving={userMgmtSaving}
                      setUserMgmtSaving={setUserMgmtSaving}
                      prefsSearch={prefsSearch}
                      setPrefsSearch={setPrefsSearch}
                      crmUsers={crmUsers}
                      setCrmUsers={setCrmUsers}
                      settingsLoading={settingsLoading}
                    />
                  </Suspense>
                  </>
                )}

                {/* ========== Notifications Tab ========== */}
                {prefsTab === 'notifications' && (
                  <Suspense fallback={PrefsLazyFallback}>
                    <PrefsNotifications
                      notifSubTab={notifSubTab}
                      setNotifSubTab={setNotifSubTab}
                      emailNotifChannel={emailNotifChannel}
                      setEmailNotifChannelDraft={setEmailNotifChannelDraft}
                      emailNotifyScope={emailNotifyScope}
                      setEmailNotifyScopeDraft={setEmailNotifyScopeDraft}
                      emailNotifSaving={emailNotifSaving}
                      emailNotifSaveError={emailNotifSaveError}
                      emailNotifTestStatus={emailNotifTestStatus}
                      setEmailNotifTestStatus={setEmailNotifTestStatus}
                      emailNotifTestError={emailNotifTestError}
                      setEmailNotifTestError={setEmailNotifTestError}
                      notifPrefsDirtyCount={notifPrefsDirtyCount}
                      NOTIF_CHANNEL_LABELS={NOTIF_CHANNEL_LABELS}
                      NOTIF_TOPIC_GROUPS={NOTIF_TOPIC_GROUPS}
                    />
                  </Suspense>
                )}

                {/* ========== Import & Export Tab (Tier 1) ========== */}
                {prefsTab === 'import-export' && (
                  <Suspense fallback={PrefsLazyFallback}>
                    <PrefsImportExport
                      importHistory={importHistory}
                      setImportHistory={setImportHistory}
                      importHistoryLoading={importHistoryLoading}
                      exportHistory={exportHistory}
                      setExportHistory={setExportHistory}
                      exportHistoryLoading={exportHistoryLoading}
                      importWizardOpen={importWizardOpen}
                      setImportWizardOpen={setImportWizardOpen}
                      importWizardStep={importWizardStep}
                      setImportWizardStep={setImportWizardStep}
                      importFile={importFile}
                      setImportFile={setImportFile}
                      importParsedData={importParsedData}
                      setImportParsedData={setImportParsedData}
                      importParsedHeaders={importParsedHeaders}
                      setImportParsedHeaders={setImportParsedHeaders}
                      importObjectType={importObjectType}
                      setImportObjectType={setImportObjectType}
                      importColumnMapping={importColumnMapping}
                      setImportColumnMapping={setImportColumnMapping}
                      importProcessing={importProcessing}
                      setImportProcessing={setImportProcessing}
                      importResult={importResult}
                      setImportResult={setImportResult}
                      exportObjectType={exportObjectType}
                      setExportObjectType={setExportObjectType}
                      exportFormat={exportFormat}
                      setExportFormat={setExportFormat}
                      exportProcessing={exportProcessing}
                      setExportProcessing={setExportProcessing}
                      formatDatePref={formatDatePref}
                    />
                  </Suspense>
                )}

                {/* ========== Backup & Restore Tab (Tier 1) ========== */}
                {prefsTab === 'backup' && (
                  <Suspense fallback={PrefsLazyFallback}>
                    <PrefsBackup
                      backups={backups}
                      setBackups={setBackups}
                      backupsLoading={backupsLoading}
                      backupProcessing={backupProcessing}
                      setBackupProcessing={setBackupProcessing}
                      restoreConfirmText={restoreConfirmText}
                      setRestoreConfirmText={setRestoreConfirmText}
                      formatDatePref={formatDatePref}
                    />
                  </Suspense>
                )}

                {/* ========== Properties Tab Enhanced (Tier 1) ========== */}
                {prefsTab === 'properties' && (
                  <>
                    <div className="prefs-card">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h2 className="prefs-card-title" style={{ marginBottom: 0 }}>Custom Properties</h2>
                        <button onClick={() => {
                          setPropEditing(null); setPropFormName(''); setPropFormDesc(''); setPropFormObject('contact'); setPropFormType('text'); setPropFormRequired(false); setPropFormOptions(''); setPropModalOpen(true)
                        }}
                          style={{ background: '#ff7a59', color: '#fff', border: 'none', borderRadius: '4px', padding: '9px 20px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
                          Create property
                        </button>
                      </div>

                      {/* Filters */}
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                        <PrefsSelect value={propFilterObject} onChange={v => setPropFilterObject(v)} options={[
                          { value: '', label: 'All Objects' },
                          { value: 'contact', label: 'Contacts' },
                          { value: 'company', label: 'Clients' },
                          { value: 'lead', label: 'Leads' },
                          { value: 'deal', label: 'Deals' },
                          { value: 'activity', label: 'Activities' },
                        ]} />
                        <input type="text" placeholder="Search properties..." value={propSearch} onChange={(e) => setPropSearch(e.target.value)}
                           />
                      </div>

                      {settingsLoading ? (
                        <div style={{ color: '#7c98b6', fontSize: '13px', padding: '12px 0' }}>Loading...</div>
                      ) : settingsProperties.filter((p: any) => {
                        if (propFilterObject && (p.object_type ?? '') !== propFilterObject) return false
                        if (propSearch && !(p.name ?? p.label ?? '').toLowerCase().includes(propSearch.toLowerCase())) return false
                        return true
                      }).length === 0 ? (
                        <div style={{ padding: '40px 0', textAlign: 'center', color: '#7c98b6', fontSize: '13px' }}>
                          {settingsProperties.length === 0 ? 'No custom properties defined.' : 'No properties match your filters.'}
                        </div>
                      ) : (
                        <table className="hs-table">
                          <thead><tr><th>Name</th><th>Type</th><th>Object</th><th>Required</th><th style={{ width: '80px' }}>Actions</th></tr></thead>
                          <tbody>
                            {settingsProperties.filter((p: any) => {
                              if (propFilterObject && (p.object_type ?? '') !== propFilterObject) return false
                              if (propSearch && !(p.name ?? p.label ?? '').toLowerCase().includes(propSearch.toLowerCase())) return false
                              return true
                            }).map((p: any) => (
                              <tr key={String(p.id)}>
                                <td style={{ fontWeight: 500, color: '#33475b' }}>{String(p.name ?? p.label ?? '')}</td>
                                <td style={{ color: '#516f90' }}>{String(p.field_type ?? p.type ?? '--')}</td>
                                <td style={{ color: '#516f90', textTransform: 'capitalize' }}>{String(p.object_type ?? '--')}</td>
                                <td>
                                  <span className="hs-status-badge" style={{ background: p.is_required ? '#e4f7e8' : '#f5f8fa', color: p.is_required ? '#1976d2' : '#7c98b6' }}>
                                    {p.is_required ? 'Yes' : 'No'}
                                  </span>
                                </td>
                                <td>
                                  <div style={{ display: 'flex', gap: '4px' }}>
                                    <button title="Edit" onClick={() => {
                                      setPropEditing(p)
                                      setPropFormName(String(p.name ?? p.label ?? ''))
                                      setPropFormDesc(String(p.description ?? ''))
                                      setPropFormObject(String(p.object_type ?? 'contact'))
                                      setPropFormType(String(p.field_type ?? p.type ?? 'text'))
                                      setPropFormRequired(!!p.is_required)
                                      setPropFormOptions(String(p.options ?? ''))
                                      setPropModalOpen(true)
                                    }}
                                      style={{ background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer', padding: '4px' }}>
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                    </button>
                                    <button title="Delete" onClick={() => {
                                      if (!confirm(`Delete property "${p.name ?? p.label}"?`)) return
                                      deleteProperty(String(p.object_type ?? 'contact'), Number(p.id))
                                        .then(() => setSettingsProperties(prev => prev.filter(x => x.id !== p.id)))
                                        .catch(() => alert('Failed to delete property'))
                                    }}
                                      style={{ background: 'none', border: 'none', color: '#f2545b', cursor: 'pointer', padding: '4px' }}>
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>

                    {/* Create/Edit Property Modal */}
                    {propModalOpen && (
                      <div className="prefs-modal-overlay" onClick={() => setPropModalOpen(false)}>
                        <div className="prefs-modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
                          <div className="prefs-modal-header">
                            <h2 style={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b', margin: 0 }}>{propEditing ? 'Edit Property' : 'Create Property'}</h2>
                            <button onClick={() => setPropModalOpen(false)} style={{ background: 'none', border: 'none', fontSize: '18px', color: '#64748b', cursor: 'pointer' }}>x</button>
                          </div>
                          <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                            <div className="prefs-field">
                              <label >Property Name *</label>
                              <input type="text" value={propFormName} onChange={(e) => setPropFormName(e.target.value)}
                                 />
                            </div>
                            <div className="prefs-field">
                              <label >Description</label>
                              <textarea value={propFormDesc} onChange={(e) => setPropFormDesc(e.target.value)} rows={2}
                                style={{ resize: 'vertical' }} />
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                              <div className="prefs-field">
                                <label >Object Type</label>
                                <PrefsSelect value={propFormObject} onChange={v => setPropFormObject(v)} options={[
                                  { value: 'contact', label: 'Contact' },
                                  { value: 'company', label: 'Clients' },
                                  { value: 'lead', label: 'Lead' },
                                  { value: 'deal', label: 'Deal' },
                                  { value: 'activity', label: 'Activity' },
                                ]} />
                              </div>
                              <div className="prefs-field">
                                <label >Field Type</label>
                                <PrefsSelect value={propFormType} onChange={v => setPropFormType(v)} options={[
                                  { value: 'text', label: 'Text' },
                                  { value: 'number', label: 'Number' },
                                  { value: 'date', label: 'Date' },
                                  { value: 'select', label: 'Select' },
                                  { value: 'multi-select', label: 'Multi-select' },
                                  { value: 'checkbox', label: 'Checkbox' },
                                  { value: 'email', label: 'Email' },
                                  { value: 'phone', label: 'Phone' },
                                  { value: 'url', label: 'URL' },
                                ]} />
                              </div>
                            </div>
                            {(propFormType === 'select' || propFormType === 'multi-select') && (
                              <div className="prefs-field">
                                <label >Options (comma-separated)</label>
                                <input type="text" value={propFormOptions} onChange={(e) => setPropFormOptions(e.target.value)}
                                  placeholder="Option 1, Option 2, Option 3"
                                   />
                              </div>
                            )}
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                              <input type="checkbox" checked={propFormRequired} onChange={(e) => setPropFormRequired(e.target.checked)} />
                              <span style={{ fontSize: '13px', color: '#33475b' }}>Required field</span>
                            </label>
                          </div>
                          <div style={{ padding: '12px 20px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                            <button onClick={() => setPropModalOpen(false)}
                              style={{ padding: '9px 16px', border: '1px solid #cbd6e2', borderRadius: '4px', background: '#fff', color: '#33475b', fontSize: '0.85rem', fontWeight: 500, cursor: 'pointer' }}>Cancel</button>
                            <button
                              disabled={!propFormName.trim() || propSaving}
                              onClick={async () => {
                                setPropSaving(true)
                                try {
                                  const fields: Record<string, unknown> = {
                                    name: propFormName.trim(),
                                    description: propFormDesc,
                                    field_type: propFormType,
                                    is_required: propFormRequired ? 1 : 0,
                                    options: propFormOptions || null,
                                  }
                                  if (propEditing) {
                                    const updated = await updateProperty(String(propEditing.object_type ?? propFormObject), Number(propEditing.id), fields)
                                    setSettingsProperties(prev => prev.map(p => p.id === propEditing.id ? { ...p, ...updated } : p))
                                  } else {
                                    const created = await createProperty(propFormObject, fields)
                                    setSettingsProperties(prev => [...prev, created])
                                  }
                                  setPropModalOpen(false)
                                } catch (err) {
                                  alert((propEditing ? 'Update' : 'Create') + ' failed: ' + (err instanceof Error ? err.message : 'Unknown error'))
                                } finally {
                                  setPropSaving(false)
                                }
                              }}
                              style={{ padding: '9px 20px', border: 'none', borderRadius: '4px', background: propFormName.trim() && !propSaving ? '#ff7a59' : '#cbd6e2', color: '#fff', fontSize: '0.85rem', fontWeight: 600, cursor: propFormName.trim() && !propSaving ? 'pointer' : 'not-allowed' }}>
                              {propSaving ? 'Saving...' : propEditing ? 'Update' : 'Create'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* ========== AI Settings Tab (Tier 2) ========== */}
                {/* ========== AI Tab ==========
                    Every ai_* toggle wrote to localStorage with no consumer. No OpenAI key is wired.
                    RC AI add-on (transcription/summaries/sentiment) is not provisioned — the Calling
                    tab already owns that story. AI drafts/suggestions aren't built. Honest card. */}
                {prefsTab === 'ai' && (
                  <Suspense fallback={PrefsLazyFallback}><PrefsAi/></Suspense>
                )}

                {/* ========== Meetings Tab ==========
                    Honest Google Calendar status card + coming-soon cards for meeting
                    defaults and public booking pages. BotPros runs on Google Calendar
                    (bp-gcal); the old copy claimed a Graph connection. */}
                {prefsTab === 'meetings' && (
                  <Suspense fallback={PrefsLazyFallback}><PrefsMeetings/></Suspense>
                )}

                {/* ========== BotPros admin & ops pages (lazy-loaded) ==========
                    Each page lives in src/views/preferences/admin/ and ships
                    as its own JS chunk loaded on demand. See ENTERPRISE_MIGRATION.md. */}
                {prefsTab === 'organization' && (
                  <Suspense fallback={PrefsLazyFallback}><PrefsOrganization/></Suspense>
                )}
                {prefsTab === 'permissions' && (
                  <Suspense fallback={PrefsLazyFallback}><PrefsPermissions/></Suspense>
                )}
                {prefsTab === 'territories' && (
                  <Suspense fallback={PrefsLazyFallback}><PrefsTerritories/></Suspense>
                )}
                {prefsTab === 'lead-routing' && (
                  <Suspense fallback={PrefsLazyFallback}><PrefsLeadRouting/></Suspense>
                )}
                {prefsTab === 'saved-views' && (
                  <Suspense fallback={PrefsLazyFallback}><PrefsSavedViews/></Suspense>
                )}
                {prefsTab === 'duplicates' && (
                  <Suspense fallback={PrefsLazyFallback}><PrefsDuplicates/></Suspense>
                )}
                {prefsTab === 'numbering' && (
                  <Suspense fallback={PrefsLazyFallback}><PrefsNumbering/></Suspense>
                )}
                {prefsTab === 'error-center' && (
                  <Suspense fallback={PrefsLazyFallback}><PrefsErrorCenter/></Suspense>
                )}

                {/* ========== Company Events — Recurring series management ========== */}
                {prefsTab === 'company-events' && (() => {
                  const FREQ_LABEL: Record<string, string> = { weekly: 'Weekly', monthly: 'Monthly', annual: 'Annual' }
                  const FREQ_OPTIONS: Array<'weekly' | 'monthly' | 'annual'> = ['weekly', 'monthly', 'annual']
                  const COMP_EVENT_TYPE_LABEL: Record<string, string> = { holiday: 'Holiday', day_off: 'Day Off', closure: 'Closure', maintenance: 'Maintenance', event: 'Event', other: 'Other' }
                  return (
                    <div style={{ maxWidth: 700 }}>
                      <div style={{ marginBottom: 20 }}>
                        <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>Company Events</h2>
                        <p style={{ fontSize: '0.85rem', color: '#64748b', margin: 0 }}>Manage recurring company event series — adjust cadence, end date, or delete a series entirely.</p>
                      </div>
                      {recurringEventsLoading && <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Loading…</div>}
                      {!recurringEventsLoading && recurringEvents.length === 0 && (
                        <div style={{ color: '#94a3b8', fontSize: '0.85rem', padding: '24px 0' }}>No recurring event series found. Create a repeating Company Event from the calendar to see it here.</div>
                      )}
                      {recurringEvents.map(rule => {
                        const isEditing = recurEditId === rule.id
                        return (
                          <div key={rule.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 14px', marginBottom: 10, background: '#fafafa' }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>{rule.name}</div>
                                <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 2 }}>
                                  <span style={{ marginRight: 10 }}>{COMP_EVENT_TYPE_LABEL[rule.event_type] || rule.event_type}</span>
                                  <span style={{ marginRight: 10 }}>{FREQ_LABEL[rule.recurrence_freq] || rule.recurrence_freq}</span>
                                  <span style={{ marginRight: 10 }}>Anchor: {rule.anchor_date}</span>
                                  {rule.recurrence_end_date && <span style={{ marginRight: 10 }}>Until: {rule.recurrence_end_date}</span>}
                                  <span>{rule.occurrence_count} occurrence{rule.occurrence_count !== 1 ? 's' : ''}</span>
                                  {rule.next_occurrence_date && <span style={{ marginLeft: 10, color: '#7c3aed' }}>Next: {rule.next_occurrence_date}</span>}
                                </div>
                              </div>
                              {!isEditing && (
                                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                                  <button
                                    style={{ padding: '5px 12px', fontSize: '0.78rem', fontWeight: 600, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', color: '#475569' }}
                                    onClick={() => { setRecurEditId(rule.id); setRecurEditForm({ recurrence_freq: rule.recurrence_freq, recurrence_end_date: rule.recurrence_end_date ?? '' }); setRecurEditError(null) }}
                                  >Edit</button>
                                  <button
                                    style={{ padding: '5px 12px', fontSize: '0.78rem', fontWeight: 600, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer', color: '#dc2626' }}
                                    onClick={async () => {
                                      if (!confirm(`Delete all events in "${rule.name}" series? This cannot be undone.`)) return
                                      try {
                                        await deleteRecurringEvent(rule.id, { scope: 'all' })
                                        loadRecurringEvents()
                                        showCalToast('success', 'Recurring series deleted')
                                      } catch (e: any) { showCalToast('error', e.message || 'Delete failed') }
                                    }}
                                  >Delete series</button>
                                </div>
                              )}
                            </div>
                            {isEditing && (
                              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #e5e7eb' }}>
                                {recurEditError && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '6px 10px', fontSize: '0.8rem', color: '#dc2626', marginBottom: 10 }}>{recurEditError}</div>}
                                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                  <div>
                                    <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#516f90', display: 'block', marginBottom: 3 }}>Cadence</label>
                                    <div style={{ position: 'relative' }}>
                                      <select
                                        value={recurEditForm.recurrence_freq}
                                        onChange={e => setRecurEditForm(f => ({ ...f, recurrence_freq: e.target.value as typeof f.recurrence_freq }))}
                                        style={{ padding: '7px 28px 7px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: '0.85rem', background: '#fff', appearance: 'none' }}
                                      >
                                        {FREQ_OPTIONS.map(f => <option key={f} value={f}>{FREQ_LABEL[f]}</option>)}
                                      </select>
                                      <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#64748b', fontSize: '0.7rem' }}>▾</span>
                                    </div>
                                  </div>
                                  <div>
                                    <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#516f90', display: 'block', marginBottom: 3 }}>End date <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional)</span></label>
                                    <input
                                      type="date"
                                      value={recurEditForm.recurrence_end_date}
                                      onChange={e => setRecurEditForm(f => ({ ...f, recurrence_end_date: e.target.value }))}
                                      style={{ padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: '0.85rem' }}
                                    />
                                  </div>
                                  <div style={{ display: 'flex', gap: 8 }}>
                                    <button
                                      disabled={recurEditSaving}
                                      style={{ padding: '7px 16px', fontSize: '0.82rem', fontWeight: 600, background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, cursor: recurEditSaving ? 'not-allowed' : 'pointer', opacity: recurEditSaving ? 0.7 : 1 }}
                                      onClick={async () => {
                                        setRecurEditSaving(true); setRecurEditError(null)
                                        try {
                                          await updateRecurringEvent(rule.id, {
                                            recurrence_freq: recurEditForm.recurrence_freq,
                                            recurrence_end_date: recurEditForm.recurrence_end_date || null,
                                          })
                                          setRecurEditId(null)
                                          loadRecurringEvents()
                                          showCalToast('success', 'Series updated')
                                        } catch (e: any) { setRecurEditError(e.message || 'Save failed') }
                                        finally { setRecurEditSaving(false) }
                                      }}
                                    >{recurEditSaving ? 'Saving…' : 'Save'}</button>
                                    <button
                                      disabled={recurEditSaving}
                                      style={{ padding: '7px 14px', fontSize: '0.82rem', fontWeight: 600, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer' }}
                                      onClick={() => { setRecurEditId(null); setRecurEditError(null) }}
                                    >Cancel</button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )
                })()}

                {/* ========== Connected Apps Tab ==========
                    Live roster — every badge comes from /api/admin/integrations-status
                    probes (Google / CF Access / RingCentral / n8n / QuickBooks). */}
                {prefsTab === 'connected-apps' && (
                  <Suspense fallback={PrefsLazyFallback}><PrefsConnectedApps/></Suspense>
                )}

                {/* ========== Deal Automation Tab ==========
                    Admin-only config for the "deal moves to Won" chain (consolidated activity + feed win post).
                    See api/src/index.ts `runDealWonAutomation`. */}
                {prefsTab === 'deal-automation' && (
                  <DealAutomationSettingsPanel />
                )}

                {/* ========== Object Settings Pages ========== */}
                {(prefsTab as string).startsWith('obj-') && (() => {
                  const objConfigs: Record<string, { plural: string; icon: string; desc: string; properties: string[] }> = {
                    'obj-contacts': { plural: 'Contacts', icon: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2m8-10a4 4 0 1 0 0-8 4 4 0 0 0 0 8', desc: 'People you do business with. Contacts are the foundation of your CRM.', properties: ['First Name', 'Last Name', 'Email', 'Phone', 'Job Title', 'Company', 'Address', 'City', 'State', 'ZIP', 'Source', 'Lifecycle Stage'] },
                    'obj-companies': { plural: 'Clients', icon: 'M3 21h18M3 7v14m18-14v14M6 11h.01M6 15h.01M6 19h.01M10 11h.01M10 15h.01M10 19h.01M14 11h.01M14 15h.01M14 19h.01M18 11h.01M18 15h.01M18 19h.01M3 7l9-4 9 4', desc: 'Clients and businesses you work with.', properties: ['Client Name', 'Domain', 'Industry', 'Phone', 'Address', 'City', 'State', 'ZIP', 'Employee Count', 'Annual Revenue', 'Owner'] },
                    'obj-leads': { plural: 'Leads', icon: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2m10-10a4 4 0 1 0 0-8 4 4 0 0 0 0 8m6 1l2 2 4-4', desc: 'Potential customers who have shown interest but not yet qualified.', properties: ['Name', 'Email', 'Phone', 'Source', 'Status', 'Assigned To', 'Budget', 'Notes'] },
                    'obj-deals': { plural: 'Deals', icon: 'M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6', desc: 'Revenue opportunities tracked through your sales pipeline.', properties: ['Deal Name', 'Amount', 'Stage', 'Est. Appointment', 'Pipeline', 'Owner', 'Contact', 'Company', 'Probability'] },
                    'obj-tickets': { plural: 'Tickets', icon: 'M15 5v14m-8-7h16M3 12l4-4m-4 4l4 4', desc: 'Customer support requests and service issues.', properties: ['Subject', 'Description', 'Status', 'Priority', 'Category', 'Assigned To', 'Contact', 'Company'] },
                    'obj-products': { plural: 'Products', icon: 'm7.5 4.27 9 5.15M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16ZM3.3 7l8.7 5 8.7-5M12 22V12', desc: 'Products and services you sell to customers.', properties: ['Name', 'SKU', 'Unit Price', 'Description', 'Category', 'Unit', 'Tax Rate'] },
                    'obj-quotes': { plural: 'Quotes', icon: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M16 13H8m8 4H8m2-8H8', desc: 'Price quotes and proposals sent to potential customers.', properties: ['Quote Number', 'Title', 'Deal', 'Contact', 'Expiration Date', 'Status', 'Total', 'Discount'] },
                    'obj-projects': { plural: 'Projects', icon: 'M2 20h20M5 20V10m4 10V4m4 16v-6m4 6V8m4 12v-4', desc: 'Construction and painting projects tracked from start to completion.', properties: ['Project Name', 'Contact', 'Address', 'Start Date', 'End Date', 'Status', 'Budget', 'Assigned Team'] },
                    'obj-services': { plural: 'Services', icon: 'M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z', desc: 'Service types offered by BotPros.', properties: ['Service Name', 'Category', 'Description', 'Rate Type', 'Default Rate', 'Status'] },
                    'obj-appointments': { plural: 'Appointments', icon: 'M16 2v4M8 2v4M3 10h18M3 6h18a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2', desc: 'Scheduled appointments with contacts and prospects.', properties: ['Title', 'Date/Time', 'Duration', 'Contact', 'Location', 'Type', 'Status', 'Notes'] },
                    'obj-invoices': { plural: 'Invoices', icon: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M12 18v-6m-3 3h6', desc: 'Invoices sent to contacts for services rendered.', properties: ['Invoice Number', 'Contact', 'Amount', 'Date Issued', 'Due Date', 'Status', 'Payment Terms', 'Line Items'] },
                    'obj-activities': { plural: 'Activities', icon: 'M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4 12 14.01l-3-3', desc: 'Logged calls, emails, meetings, tasks, and notes.', properties: ['Type', 'Subject', 'Date', 'Duration', 'Owner', 'Entity Type', 'Entity ID', 'Notes'] },
                    'obj-workflows': { plural: 'Workflows', icon: 'M9 5H2v7l6.29 6.29c.94.94 2.48.94 3.42 0l3.58-3.58c.94-.94.94-2.48 0-3.42zM6 9h.01', desc: 'Automated workflows for task management and process automation.', properties: ['Workflow Name', 'Trigger', 'Actions', 'Status', 'Last Run', 'Enrollment Count'] },
                  }
                  const cfg = objConfigs[prefsTab] || { plural: prefsTab.replace('obj-', ''), icon: 'M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5', desc: 'Configure this object type.', properties: [] }
                  return (
                    <>
                      <div className="prefs-card">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                          <div style={{ width: '40px', height: '40px', borderRadius: '8px', background: '#f0f4ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2e60e0" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d={cfg.icon}/></svg>
                          </div>
                          <div>
                            <h2 className="prefs-card-title" style={{ marginBottom: '2px' }}>{cfg.plural} Settings</h2>
                            <p style={{ fontSize: '13px', color: '#7c98b6', margin: 0 }}>{cfg.desc}</p>
                          </div>
                        </div>
                      </div>

                      {/* Field Definitions — backed by object_fields table (migration 099) */}
                      <Suspense fallback={<div className="prefs-card"><div style={{ color: '#7c98b6', fontSize: '13px', padding: '12px 0' }}>Loading fields…</div></div>}>
                        <PrefsObjectFields objectType={prefsTab.replace(/^obj-/, '')} pluralLabel={cfg.plural} />
                      </Suspense>


                    </>
                  )
                })()}


                {/* ========== Inboxes Tab ========== */}
                {/* Real shared-inbox config (replaces "Coming soon" / sample-data
                    stub). Lazy chunk; D1-backed via /api/admin/shared-inboxes. */}
                {prefsTab === 'inboxes' && (
                  <Suspense fallback={PrefsLazyFallback}><PrefsInboxes/></Suspense>
                )}

                {/* ========== Marketing > Forms Tab ========== */}
                {prefsTab === 'mkt-forms' && (
                  <>
                    <div className="prefs-card">
                      <h2 className="prefs-card-title">Form Settings</h2>
                      <p className="prefs-card-desc">Configure default behavior for forms embedded on your website.</p>
                      {[
                        { key: 'forms_create_contacts', label: 'Create contacts on submission', desc: 'Automatically create a CRM contact when a form is submitted.' },
                        { key: 'forms_notify_owner', label: 'Notify contact owner', desc: 'Send a notification to the assigned contact owner on form submission.' },
                        { key: 'forms_gdpr_consent', label: 'Include GDPR consent', desc: 'Add a consent checkbox to all forms for GDPR compliance.' },
                        { key: 'forms_captcha', label: 'Enable CAPTCHA', desc: 'Add CAPTCHA protection to prevent spam submissions.' },
                      ].map((setting) => {
                        const isOn = localStorage.getItem(`pref_${setting.key}`) !== '0'
                        return (
                          <div key={setting.key} className="pref-toggle-row">
                            <div><div className="pref-toggle-label">{setting.label}</div><div className="pref-toggle-desc">{setting.desc}</div></div>
                            <div onClick={() => { localStorage.setItem(`pref_${setting.key}`, isOn ? '0' : '1'); window.dispatchEvent(new Event('storage')) }} style={{ cursor: 'pointer', width: '40px', height: '22px', borderRadius: '11px', background: isOn ? '#1976d2' : '#cbd6e2', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                              <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: '#fff', position: 'absolute', top: '2px', left: isOn ? '20px' : '2px', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                            </div>
                          </div>
                        )
                      })}
                    </div>
                    <div className="prefs-card">
                      <h2 className="prefs-card-title">Form Submissions (defaults)</h2>
                      <p className="prefs-card-desc">Default values for new forms — each form below can override these.</p>
                      <div className="pref-toggle-row">
                        <div><div className="pref-toggle-label">Redirect URL</div><div className="pref-toggle-desc">Redirect users to this page after submission (leave empty for default thank-you message).</div></div>
                        <input type="url" placeholder="https://botpros.ai/thank-you" value={localStorage.getItem('pref_forms_redirect') || ''} onChange={(e) => saveOrgSetting('pref_forms_redirect', e.target.value)} style={{ width: '260px' }} />
                      </div>
                      <div className="pref-toggle-row" style={{ borderBottom: 'none' }}>
                        <div><div className="pref-toggle-label">Submission Notification Email</div><div className="pref-toggle-desc">Send form submission details to this email address.</div></div>
                        <input type="email" value={localStorage.getItem('pref_forms_notify_email') || 'mritchie@botpros.ai'} onChange={(e) => saveOrgSetting('pref_forms_notify_email', e.target.value)} style={{ width: '220px' }} />
                      </div>
                    </div>
                    <Suspense fallback={<div className="prefs-card"><div style={{ color: '#7c98b6', fontSize: '13px', padding: '12px 0' }}>Loading forms…</div></div>}>
                      <PrefsMarketingForms />
                    </Suspense>
                  </>
                )}

                {/* ========== Commerce > Tax Tab ========== */}
                {prefsTab === 'com-tax' && (
                  <>
                    <div className="prefs-card">
                      <h2 className="prefs-card-title">Tax Configuration</h2>
                      <p className="prefs-card-desc">Manage tax rates and configure automatic tax calculation.</p>
                      <div className="pref-toggle-row">
                        <div><div className="pref-toggle-label">Enable Tax Calculation</div><div className="pref-toggle-desc">Automatically calculate tax on invoices and quotes.</div></div>
                        <div onClick={() => { const k = 'pref_tax_enabled'; saveOrgSetting(k, localStorage.getItem(k) === '0' ? '1' : '0') }} style={{ cursor: 'pointer', width: '40px', height: '22px', borderRadius: '11px', background: localStorage.getItem('pref_tax_enabled') !== '0' ? '#1976d2' : '#cbd6e2', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                          <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: '#fff', position: 'absolute', top: '2px', left: localStorage.getItem('pref_tax_enabled') !== '0' ? '20px' : '2px', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                        </div>
                      </div>
                      <div className="pref-toggle-row" style={{ borderBottom: 'none' }}>
                        <div><div className="pref-toggle-label">Tax Included in Prices</div><div className="pref-toggle-desc">Prices already include tax (vs. tax added at checkout).</div></div>
                        <div onClick={() => { const k = 'pref_tax_inclusive'; saveOrgSetting(k, localStorage.getItem(k) === '1' ? '0' : '1') }} style={{ cursor: 'pointer', width: '40px', height: '22px', borderRadius: '11px', background: localStorage.getItem('pref_tax_inclusive') === '1' ? '#1976d2' : '#cbd6e2', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                          <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: '#fff', position: 'absolute', top: '2px', left: localStorage.getItem('pref_tax_inclusive') === '1' ? '20px' : '2px', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                        </div>
                      </div>
                    </div>
                    <div className="prefs-card">
                      <h2 className="prefs-card-title">Tax Rates</h2>
                      <p className="prefs-card-desc">Configure tax rates for different regions and product types.</p>
                      <div style={{ padding: 24, textAlign: 'center', background: '#f5f8fa', border: '1px dashed #cbd6e2', borderRadius: 6, color: '#516f90', fontSize: 13, lineHeight: 1.55 }}>
                        No tax rates configured. Invoices and quotes currently apply no automatic tax — rates will appear here once rate management ships.
                      </div>
                    </div>
                    <div className="prefs-card">
                      <h2 className="prefs-card-title">Tax ID</h2>
                      <p className="prefs-card-desc">Your business tax identification displayed on invoices.</p>
                      <div className="pref-toggle-row">
                        <div><div className="pref-toggle-label">Tax ID Number</div></div>
                        <input type="text" value={localStorage.getItem('pref_tax_id') || ''} onChange={(e) => saveOrgSetting('pref_tax_id', e.target.value)} placeholder="XX-XXXXXXX" style={{ width: '160px', fontFamily: 'monospace' }} />
                      </div>
                      <div className="pref-toggle-row" style={{ borderBottom: 'none' }}>
                        <div><div className="pref-toggle-label">Show on Invoices</div><div className="pref-toggle-desc">Display your Tax ID on invoice documents.</div></div>
                        <div onClick={() => { const k = 'pref_tax_show_invoices'; saveOrgSetting(k, localStorage.getItem(k) === '0' ? '1' : '0') }} style={{ cursor: 'pointer', width: '40px', height: '22px', borderRadius: '11px', background: localStorage.getItem('pref_tax_show_invoices') !== '0' ? '#1976d2' : '#cbd6e2', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                          <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: '#fff', position: 'absolute', top: '2px', left: localStorage.getItem('pref_tax_show_invoices') !== '0' ? '20px' : '2px', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* ========== Invoice Scanner Settings Tab ========== */}
                {prefsTab === ('invoice-scanner-settings' as PrefsTab) && (
                  <Suspense fallback={PrefsLazyFallback}><PrefsInvoiceScannerSettings/></Suspense>
                )}

                {/* ========== Document Management Settings Tab ========== */}
                {prefsTab === ('document-mgmt-settings' as PrefsTab) && (
                  <Suspense fallback={PrefsLazyFallback}><PrefsDocumentMgmtSettings/></Suspense>
                )}

                {/* ========== n8n Integration Settings Tab ========== */}
                {prefsTab === ('n8n-integration' as PrefsTab) && (
                  <Suspense fallback={PrefsLazyFallback}><PrefsN8nIntegration/></Suspense>
                )}

                {/* ========== Email Sending Settings Tab ========== */}
                {prefsTab === ('mkt-sending' as PrefsTab) && (
                  <Suspense fallback={PrefsLazyFallback}><PrefsMarketingSending/></Suspense>
                )}

                {/* ========== Social Connections Tab ========== */}
                {prefsTab === ('mkt-social' as PrefsTab) && (
                  <Suspense fallback={PrefsLazyFallback}><PrefsMarketingSocial/></Suspense>
                )}

                {/* ========== Accounting Defaults Tab ========== */}
                {prefsTab === ('com-accounting' as PrefsTab) && (
                  <Suspense fallback={PrefsLazyFallback}><PrefsAccountingDefaults/></Suspense>
                )}

                {/* ========== Report Defaults Tab ========== */}
                {prefsTab === ('report-defaults' as PrefsTab) && (
                  <Suspense fallback={PrefsLazyFallback}><PrefsReportDefaults/></Suspense>
                )}

                {/* ========== HR Documents Settings Tab ========== */}
                {prefsTab === ('hr-documents' as PrefsTab) && (
                  <Suspense fallback={PrefsLazyFallback}><PrefsHrDocuments/></Suspense>
                )}

                {/* ========== Automation Settings Tab ========== */}
                {prefsTab === ('automation-settings' as PrefsTab) && (
                  <Suspense fallback={PrefsLazyFallback}><PrefsAutomationSettings/></Suspense>
                )}

                {/* ========== Client Types Tab ========== */}
                {prefsTab === 'org-types' && (
                  <Suspense fallback={PrefsLazyFallback}><PrefsOrgTypes/></Suspense>
                )}

                {/* ========== Email Templates Tab ========== */}
                {prefsTab === 'email-templates' && (
                  <Suspense fallback={PrefsLazyFallback}><PrefsEmailTemplates/></Suspense>
                )}

                </div>
              </div>

              {/* Dynamic save footer — full width of prefs-page */}
              {prefsTotalDirtyCount > 0 && (
                <div data-marker="prefs-savefb-v1" className={`prefs-save-footer${prefsFooterWarningActive ? ' prefs-save-footer-unsaved-warning' : ''}${prefsUnsavedNudge ? ' prefs-save-footer-unsaved-shake' : ''}`}>
                  <div className="prefs-save-footer-inner">
                    <div className="prefs-save-footer-btns">
                      <button className="prefs-save-btn" onClick={prefsSaveAll} disabled={emailNotifSaving || prefsSaving}>{prefsSaving ? 'Saving…' : 'Save'}</button>
                      <button className="prefs-cancel-btn" onClick={prefsCancelAll} disabled={emailNotifSaving || prefsSaving}>Cancel</button>
                    </div>
                    <p className="prefs-save-footer-msg" role={prefsFooterWarningActive ? 'alert' : undefined}>
                      {prefsFooterWarningActive
                        ? 'Save or cancel notification changes before leaving.'
                        : `You've changed ${prefsTotalDirtyCount} setting${prefsTotalDirtyCount !== 1 ? 's' : ''}.`}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

        {/* ========== Timesheets View (admin-only) ========== */}
        {activeView === 'timesheets' && (() => {
          const email = (currentUser?.email || '').toLowerCase()
          const role = currentUser?.role || 'user'
          const isAdminUser = role === 'admin' || role === 'Super Admin' || ['mritchie@botpros.ai', 'agent@botpros.ai'].includes(email)
          // Same gate as the sidebar — estimators / PMs (Tony, Ira, Leo,
          // Drew, Nancy) get the view scoped to their own deals.
          const TIMESHEET_DEAL_OWNER_IDS = [8, 35, 49, 41, 48, 73, 42]
          const canSeeTimesheets = isAdminUser || TIMESHEET_DEAL_OWNER_IDS.includes(currentUserId)
          if (!canSeeTimesheets) {
            return (
              <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
                <h3 style={{ marginBottom: 6, color: '#334155' }}>Timesheets</h3>
                <div style={{ fontSize: '0.88rem' }}>This view is available to admins and deal owners only.</div>
              </div>
            )
          }
          const myPending = timesheetCounts?.myPendingCount ?? 0
          const myApprovedToday = timesheetCounts?.myApprovedTodayCount ?? 0
          const totalPendingAdmin = timesheetCounts?.totalPendingIfAdmin
          const headerTitle = isAdminUser ? 'Timesheets' : 'My deals’ timesheets'
          const headerSub = timesheetsSource === 'schedules'
            ? 'Per-employee schedules · free clock vs weekly schedule'
            : timesheetsSource === 'shift'
            ? 'Internal shift clock · live status, lunches & breaks, week view'
            : isAdminUser
              ? 'Every clock-in across every deal · bulk approve + CSV export'
              : 'Time entries on the deals you own or assigned. Bulk approve + CSV export.'
          return (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div style={{ padding: '14px 20px', borderBottom: '1px solid #e2e8f0', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <h2 style={{ margin: 0, fontSize: '1.15rem', color: '#0f172a' }}>{headerTitle}</h2>
                    {myPending > 0 && (
                      <span className={isAdminUser ? 'integ-timesheets-chip integ-timesheets-chip--admin' : 'integ-timesheets-chip'} title="Your queue">
                        {myPending} pending
                      </span>
                    )}
                    {isAdminUser && totalPendingAdmin != null && totalPendingAdmin > myPending && (
                      <span className="integ-timesheets-chip integ-timesheets-chip--zero" style={{ background: '#f8fafc', borderColor: '#e2e8f0', color: '#475569' }} title="Across the entire org">
                        {totalPendingAdmin} org-wide
                      </span>
                    )}
                    {myApprovedToday > 0 && (
                      <span className="integ-timesheets-chip integ-timesheets-chip--zero" style={{ background: '#ecfdf5', borderColor: '#a7f3d0', color: '#047857' }} title="Approved today by you">
                        {myApprovedToday} approved today
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 2 }}>{headerSub}</div>
                </div>
                {/* Source toggle — deal-based entries vs the internal shift clock.
                    The Schedules editor is settings-gated (same gate as the
                    "Schedules & corrections" button). */}
                <div style={{ display: 'inline-flex', gap: 0, background: '#f1f5f9', borderRadius: 6, padding: 2 }}>
                  {([
                    { k: 'deals', label: 'Deal time' },
                    { k: 'shift', label: 'Shift clock' },
                    ...((!myCapabilities || myCapabilities.access_settings) ? [{ k: 'schedules', label: 'Schedules' }] : []),
                  ] as const).map(t => (
                    <button
                      key={t.k}
                      type="button"
                      onClick={() => setTimesheetsSource(t.k as 'deals' | 'shift' | 'schedules')}
                      style={{
                        padding: '5px 12px', fontSize: '0.78rem', fontWeight: 600, borderRadius: 4, border: 'none', cursor: 'pointer',
                        background: timesheetsSource === t.k ? '#fff' : 'transparent',
                        color: timesheetsSource === t.k ? '#0f172a' : '#64748b',
                        boxShadow: timesheetsSource === t.k ? '0 1px 2px rgba(15,23,42,0.06)' : 'none',
                        transition: 'background 100ms cubic-bezier(0.4,0,0.2,1), color 100ms',
                      }}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                {/* Tab control — drives the `approved` filter inside the panel */}
                {timesheetsSource === 'deals' && (
                <div style={{ display: 'inline-flex', gap: 0, background: '#f1f5f9', borderRadius: 6, padding: 2 }}>
                  {([
                    { k: 'pending', label: 'Pending approval' },
                    { k: 'approved', label: 'Approved' },
                    { k: 'all', label: 'All' },
                  ] as const).map(t => (
                    <button
                      key={t.k}
                      type="button"
                      onClick={() => setTimesheetsTab(t.k)}
                      style={{
                        padding: '5px 12px',
                        fontSize: '0.78rem',
                        fontWeight: 600,
                        borderRadius: 4,
                        border: 'none',
                        cursor: 'pointer',
                        background: timesheetsTab === t.k ? '#fff' : 'transparent',
                        color: timesheetsTab === t.k ? '#0f172a' : '#64748b',
                        boxShadow: timesheetsTab === t.k ? '0 1px 2px rgba(15,23,42,0.06)' : 'none',
                        transition: 'background 100ms cubic-bezier(0.4,0,0.2,1), color 100ms',
                      }}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                )}
              </div>
              <div style={{ flex: 1, padding: 20, overflow: 'auto' }}>
                {timesheetsSource === 'schedules' ? (
                  (!myCapabilities || myCapabilities.access_settings) ? (
                    <Suspense fallback={PrefsLazyFallback}><TimesheetsScheduleEditor/></Suspense>
                  ) : (
                    <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>You don’t have access to employee schedules.</div>
                  )
                ) : timesheetsSource === 'shift' ? (
                  <ShiftClockPanel
                    onManage={(!myCapabilities || myCapabilities.access_settings)
                      ? () => { setTimesheetsSource('schedules') }
                      : undefined}
                  />
                ) : (
                <TimeEntriesInlinePanel
                  currentUserRole={role}
                  currentUserEmail={email}
                  currentUserId={currentUser?.userId || 0}
                  initialApproved={timesheetsTab}
                  refreshSignal={`${timesheetsTab}-${myPending}-${myApprovedToday}`}
                  onAfterApprovalChange={() => {
                    fetchTimeCountsByOwner().then(c => setTimesheetCounts(c)).catch(() => {})
                  }}
                />
                )}
              </div>
            </div>
          )
        })()}

        {/* ========== Tasks View ========== */}
        {activeView === 'tasks' && (() => {
          // Client-side search filtering
          const taskSearchLower = tasksSearch.toLowerCase()
          const filteredTasks = tasksData.filter((t) => {
            if (taskSearchLower) {
              const title = (t.title ?? '').toLowerCase()
              const body = (t.body ?? '').toLowerCase()
              if (!title.includes(taskSearchLower) && !body.includes(taskSearchLower)) return false
            }
            if (ownerSelected.size > 0) {
              const assignedId = t.assigned_to || 0
              if (!ownerSelected.has(assignedId)) return false
            }
            if (tasksPriorityFilter && (t.priority ?? '').toLowerCase() !== tasksPriorityFilter) return false
            return true
          })
          filteredTasks.sort((a: any, b: any) => {
            const field = tasksSort.field
            const order = tasksSort.order === 'ASC' ? 1 : -1
            const av = a[field] ?? ''
            const bv = b[field] ?? ''
            return av < bv ? -order : av > bv ? order : 0
          })
          const taskTotal = filteredTasks.length
          const taskTotalPages = Math.max(1, Math.ceil(taskTotal / tablePageSize))
          const taskCurrentPage = Math.min(tasksPage, taskTotalPages)
          const taskPagedData = filteredTasks.slice((taskCurrentPage - 1) * tablePageSize, taskCurrentPage * tablePageSize)
          const taskCurrentOwnerId = currentUserId || 8
          const taskCurrentOwnerName = currentUser?.name || CALENDAR_USERS_MAP[taskCurrentOwnerId] || 'Me'
          const toggleAllTaskRows = () => {
            if (taskSelectedRows.size === taskPagedData.length) {
              setTaskSelectedRows(new Set())
            } else {
              setTaskSelectedRows(new Set(taskPagedData.map((t) => t.id)))
            }
          }
          const toggleTaskSort = (field: string) => {
            setTasksSort(prev => prev.field === field ? { field, order: prev.order === 'ASC' ? 'DESC' : 'ASC' } : { field, order: field === 'title' ? 'ASC' : 'DESC' })
            setTasksPage(1)
          }
          const onTaskResizeStart = (col: string, e: React.MouseEvent) => {
            e.preventDefault()
            e.stopPropagation()
            const startX = e.clientX
            const startW = (taskColWidths as Record<string, number>)[col] || 140
            const onMove = (ev: MouseEvent) => {
              const diff = ev.clientX - startX
              setTaskColWidths(prev => ({ ...prev, [col]: Math.max(60, startW + diff) }))
            }
            const onUp = () => {
              document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp)
              const swallow = (ev: MouseEvent) => { ev.preventDefault(); ev.stopPropagation(); document.removeEventListener('click', swallow, true) }
              document.addEventListener('click', swallow, true)
              setTimeout(() => document.removeEventListener('click', swallow, true), 200)
            }
            document.addEventListener('mousemove', onMove)
            document.addEventListener('mouseup', onUp)
          }
          const openNewTaskModal = () => {
            setCreateFormData({
              title: '',
              body: '',
              status: 'open',
              priority: 'medium',
              dueDate: '',
              assignedTo: currentUserId ? String(currentUserId) : '',
              ownerType: '',
              ownerId: '',
            })
            setCreateError(null)
            setShowCreateModal('task')
          }
          const openTaskEditor = (task: CrmTask) => {
            setCreateFormData({
              id: String(task.id),
              title: task.title || '',
              body: task.body || '',
              status: task.status || 'open',
              priority: task.priority || 'medium',
              dueDate: task.due_date || '',
              assignedTo: task.assigned_to ? String(task.assigned_to) : '',
              ownerType: task.owner_type || '',
              ownerId: task.owner_id || '',
            })
            setCreateError(null)
            setShowCreateModal('task')
          }
          const taskSortArrowSvg = <svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M29.38 21.96a1.147 1.147 0 0 0-1.617 0l-3.85 3.852V5.714a1.143 1.143 0 0 0-2.286 0v20.099l-3.853-3.852a1.144 1.144 0 0 0-1.615 1.617l5.803 5.802a1.143 1.143 0 0 0 1.616 0l5.802-5.802a1.144 1.144 0 0 0 0-1.617M15.84 8.422 10.039 2.62a1.145 1.145 0 0 0-1.616 0L2.62 8.422a1.144 1.144 0 0 0 1.617 1.617l3.85-3.851v20.098a1.143 1.143 0 0 0 2.286 0V6.187l3.853 3.852a1.14 1.14 0 0 0 1.616-.001 1.14 1.14 0 0 0-.001-1.616"/></svg>
          return (
          <div className="hs-contacts">
            {/* Top bar: object switcher + view tabs + actions */}
            <div className="hs-topbar">
              <div className="hs-topbar-left">
                {/* Object switcher */}
                <div className="hs-obj-switcher-wrap" ref={objSwitcherRef}>
                  <button className={`hs-obj-switcher${objSwitcherOpen ? ' open' : ''}`} onClick={() => { setObjSwitcherOpen(!objSwitcherOpen); setObjSwitcherSearch('') }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                    <span>Tasks</span>
                    <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                  </button>
                  {objSwitcherOpen && (
                    <div className="hs-obj-panel">
                      <div className="hs-obj-search-wrap">
                        <svg className="hs-obj-search-icon" width="13" height="13" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                        <input
                          className="hs-obj-search"
                          placeholder="Search"
                          value={objSwitcherSearch}
                          onChange={(e) => setObjSwitcherSearch(e.target.value)}
                          autoFocus
                        />
                      </div>
                      <ul className="hs-obj-list" role="listbox">
                        {[
                          { label: 'Contacts', view: 'contacts' as ViewMode },
                            { label: 'Clients', view: 'companies' as ViewMode },
                          { label: 'Deals', view: 'deals' as ViewMode },
                          { label: 'Leads', view: 'leads' as ViewMode },
                          { label: 'Projects', view: 'projects' as ViewMode },
                          { label: 'Tickets', view: 'tickets' as ViewMode },
                          { label: 'Calls', view: 'calls' as ViewMode },
                        ]
                          .filter(item => !objSwitcherSearch || item.label.toLowerCase().includes(objSwitcherSearch.toLowerCase()))
                          .map(item => (
                            <li
                              key={item.view}
                              className={`hs-obj-item${item.view === activeView ? ' active' : ''}`}
                              role="option"
                              aria-selected={item.view === activeView}
                              onClick={() => {
                                setActiveView(item.view)
                                setObjSwitcherOpen(false)
                                setObjSwitcherSearch('')
                              }}
                            >
                              {item.label}
                            </li>
                          ))
                        }
                      </ul>
                    </div>
                  )}
                </div>

                {/* Separator */}
                <div className="hs-topbar-sep" />

                {/* View tabs */}
                <div className="hs-view-tabs">
                  {[
                    { key: 'all', label: 'All tasks' },
                    { key: 'open', label: 'Open' },
                    { key: 'in_progress', label: 'In Progress' },
                    { key: 'completed', label: 'Completed' },
                  ].map((tab) => {
                    const isActive = tasksFilter === tab.key
                    return (
                      <div
                        key={tab.key}
                        className={`hs-view-tab${isActive ? ' active' : ''}`}
                        onClick={() => { setTasksFilter(tab.key); setTasksPage(1); setTasksSearch(''); setTaskSelectedRows(new Set()) }}
                      >
                        <span className="hs-tab-label">{tab.label}</span>
                        {isActive && tasksTotal > 0 && <span className="hs-tab-count">{tasksTotal.toLocaleString()}</span>}
                        {isActive && (
                          <span className="hs-tab-kebab" onClick={(e) => { e.stopPropagation(); setTabEditOpen(!tabEditOpen) }}>
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>
                          </span>
                        )}
                        {tab.key !== 'all' && <span className="hs-tab-x" onClick={(e) => { e.stopPropagation(); setTasksFilter('all'); setTasksPage(1) }}>&times;</span>}
                        {isActive && tabEditOpen && (
                          <div className="hs-tab-edit-dropdown">
                            <button className="hs-tab-edit-item" disabled>Rename</button>
                            <button className="hs-tab-edit-item" disabled>Clone</button>
                            <button className="hs-tab-edit-item" disabled>Manage Sharing</button>
                            {tab.key !== 'all' && <button className="hs-tab-edit-item" onClick={() => { setTasksFilter('all'); setTasksPage(1); setTabEditOpen(false) }}>Delete</button>}
                          </div>
                        )}
                      </div>
                    )
                  })}

                  {/* Quick-add view button — inline with tabs */}
                  <div className="hs-topbar-sep" />
                  <div className="hs-view-add-wrap" ref={viewAddMenuRef} style={{ position: 'relative', display: 'inline-flex', alignSelf: 'center' }}>
                    <button
                      className={`hs-quick-add-btn${viewAddMenuOpen ? ' active' : ''}`}
                      title="Create or add view"
                      type="button"
                      aria-label="Create or add view"
                      onClick={() => { setViewAddMenuOpen(!viewAddMenuOpen); setViewAddMenuMode('menu') }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                    </button>
                    {viewAddMenuOpen && viewAddMenuMode === 'menu' && (
                      <div className="hs-view-add-dropdown">
                        <button className="hs-view-add-item" onClick={() => setViewAddMenuMode('type')}>
                          Create new view
                        </button>
                        <button className="hs-view-add-item" onClick={() => {
                          setViewAddMenuOpen(false)
                          setViewAddMenuMode('menu')
                        }}>
                          Add view
                        </button>
                      </div>
                    )}
                    {viewAddMenuOpen && viewAddMenuMode === 'type' && (
                      <div className="hs-view-add-dropdown hs-view-type-picker">
                        <button className="hs-view-type-btn" onClick={() => {
                          setViewAddMenuOpen(false)
                          setViewAddMenuMode('menu')
                        }}>
                          <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                          <span>Table</span>
                        </button>
                        <button className="hs-view-type-btn" disabled style={{ opacity: 0.4 }}>
                          <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M11.43 11.43h-8a1.14 1.14 0 1 1 0-2.28h8a1.14 1.14 0 1 1 0 2.28M28.57 11.43h-8a1.14 1.14 0 1 1 0-2.28h8a1.14 1.14 0 1 1 0 2.28"/><path d="M9.14 29.71H5.71c-1.89 0-3.43-1.54-3.43-3.43V5.71c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v14.86c0 1.89-1.54 3.43-3.43 3.43h-3.43c-1.89 0-3.43-1.54-3.43-3.43v-1.14h-6.86v6.86c0 1.89-1.54 3.43-3.43 3.43Z"/></svg>
                          <span>Board</span>
                        </button>
                        <button className="hs-view-type-btn" disabled style={{ opacity: 0.4 }}>
                          <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M7.57 24.71c-.63 0-1.14-.51-1.14-1.14v-6.86a1.14 1.14 0 1 1 2.28 0v6.86c0 .63-.51 1.14-1.14 1.14M12.14 24.71c-.63 0-1.14-.51-1.14-1.14v-16c0-.63.51-1.14 1.14-1.14h4.57a1.14 1.14 0 1 1 0 2.28h-3.43v14.86c0 .63-.51 1.14-1.14 1.14"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v20.57c0 1.89-1.54 3.43-3.43 3.43Z"/></svg>
                          <span>Report</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="hs-topbar-right">
                {/* Create task — direct action (Import removed) */}
                <button className="hs-add-contacts-btn" onClick={openNewTaskModal}>
                  Create task
                </button>
              </div>
            </div>

            {/* Toolbar — HubSpot-style */}
            <div className="hs-toolbar">
              <div className="hs-toolbar-left">
                {/* Search */}
                <div className="hs-search-wrap">
                  <svg className="hs-search-icon" width="14" height="14" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M28.14 29.29c-.29 0-.58-.11-.81-.33l-7.58-7.58a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l7.58 7.58a1.14 1.14 0 0 1-.81 1.95"/><path d="M13.29 24.71c-6.3 0-11.43-5.13-11.43-11.43S6.98 1.86 13.29 1.86s11.43 5.13 11.43 11.43-5.13 11.43-11.43 11.43Zm0-20.57c-5.04 0-9.14 4.1-9.14 9.14s4.1 9.14 9.14 9.14 9.14-4.1 9.14-9.14-4.1-9.14-9.14-9.14"/></svg>
                  <input
                    className="hs-search-input"
                    placeholder="Search tasks"
                    value={tasksSearch}
                    onChange={(e) => { setTasksSearch(e.target.value); setTasksPage(1) }}
                  />
                  {tasksSearch && (
                    <button className="hs-search-clear" onClick={() => { setTasksSearch(''); setTasksPage(1) }}>&times;</button>
                  )}
                </div>

                <div className="hs-tb-sep" />

                {/* View type + settings button group */}
                <div className="hs-viewtype-group">
                  <div className="hs-viewtype-wrap" ref={viewTypeDropdownRef}>
                    <button
                      className={`hs-tb-btn hs-viewtype-btn${viewTypeDropdownOpen ? ' active' : ''}`}
                      onClick={() => setViewTypeDropdownOpen(!viewTypeDropdownOpen)}
                    >
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                      <span>Table</span>
                      <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                    </button>
                    {viewTypeDropdownOpen && (
                      <div className="hs-viewtype-panel">
                        <div className="hs-viewtype-header">View type</div>
                        <button className="hs-viewtype-option active" onClick={() => setViewTypeDropdownOpen(false)}>
                          <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                          <span>Table</span>
                        </button>
                      </div>
                    )}
                  </div>
                  <button
                    className={`hs-tb-btn hs-settings-btn${tableSettingsOpen ? ' active' : ''}`}
                    onClick={() => setTableSettingsOpen(!tableSettingsOpen)}
                    title={tableSettingsOpen ? 'Close settings' : 'Table settings'}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                  </button>
                </div>

                <div className="hs-tb-sep" />

                {/* Filters */}
                <button className={`hs-tb-btn hs-tb-btn-bordered${quickFiltersOpen ? ' hs-tb-btn-pressed' : ''}`} title="Filters" onClick={() => setQuickFiltersOpen(!quickFiltersOpen)}>
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M30.71 16.86H13.57a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28M6.71 16.86H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M10.14 20.29c-2.52 0-4.57-2.05-4.57-4.57s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M6.71 6.57H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M30.71 6.57H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M18.14 6.57H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 10C19.05 10 17 7.95 17 5.43S19.05.86 21.57.86s4.57 2.05 4.57 4.57S24.09 10 21.57 10m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M30.71 27.14H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M18.14 27.14H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 30.57C19.05 30.57 17 28.52 17 26s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29"/></svg>
                  <span>Filters</span>
                </button>

                <div className="hs-tb-sep" />

                {/* Export */}
                <button
                  className="hs-tb-btn hs-tb-btn-bordered"
                  title="Export tasks as CSV"
                  onClick={() => {
                    try {
                      const headers = ['Title', 'Status', 'Priority', 'Due Date', 'Assigned To', 'Created']
                      const escCsv = (v: unknown) => { const s = String(v ?? ''); return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s }
                      const rows = filteredTasks.map((t: any) => [
                        t.title, t.status, t.priority, t.due_date || '', t.assigned_to ? (CALENDAR_USERS_MAP[t.assigned_to] || `User ${t.assigned_to}`) : '', t.created_at || '',
                      ].map(escCsv).join(','))
                      const csv = [headers.join(','), ...rows].join('\n')
                      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
                      const url = URL.createObjectURL(blob)
                      const a = document.createElement('a')
                      a.href = url
                      a.download = `tasks-export-${new Date().toISOString().slice(0, 10)}.csv`
                      document.body.appendChild(a)
                      a.click()
                      document.body.removeChild(a)
                      URL.revokeObjectURL(url)
                    } catch (err) {
                      console.error('Export failed:', err)
                      alert('Export failed. Please try again.')
                    }
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  <span>Export</span>
                </button>
              </div>

              <div className="hs-toolbar-right">
                <div className="hs-tb-sep" />
                {/* Clone */}
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Clone view" disabled style={{ opacity: 0.5 }}>
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M25.86 29.29H12.15c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-1.89 1.54-3.43 3.43-3.43h13.71c1.89 0 3.43 1.54 3.43 3.43v13.71c0 1.89-1.54 3.43-3.43 3.43M12.14 11c-.63 0-1.14.51-1.14 1.14v13.71c0 .63.51 1.14 1.14 1.14h13.71c.63 0 1.14-.51 1.14-1.14V12.14c0-.63-.51-1.14-1.14-1.14z"/><path d="M5.29 22.43c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43H19c1.89 0 3.43 1.54 3.43 3.43a1.14 1.14 0 1 1-2.28 0c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14V19c0 .63.51 1.14 1.14 1.14a1.14 1.14 0 1 1 0 2.28Z"/></svg>
                </button>
                {/* Save */}
                <button className="hs-tb-btn hs-tb-btn-bordered disabled" disabled title="Save view" style={{ opacity: 0.5 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                </button>
              </div>
            </div>

            {/* Quick filters bar — toggled by Filters button */}
            {quickFiltersOpen && <div className="hs-quick-filters">
              {/* Task owner — HubSpot-style typeahead with checkboxes */}
              <div className="hs-owner-dropdown" ref={ownerDropdownRef}>
                <button
                  className={`hs-qf-btn hs-qf-trigger${ownerDropdownOpen ? ' open' : ''}${ownerSelected.size > 0 ? ' has-value' : ''}`}
                  onClick={() => setOwnerDropdownOpen(!ownerDropdownOpen)}
                >
                  <span>Task owner</span>
                  {ownerSelected.size > 0 && <span className="hs-qf-count">{ownerSelected.size}</span>}
                  <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                </button>
                {ownerDropdownOpen && (
                  <div className="hs-owner-panel">
                    <div className="hs-owner-search-wrap">
                      <svg className="hs-owner-search-icon" width="13" height="13" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                      <input
                        className="hs-owner-search"
                        placeholder="Search"
                        value={ownerSearch}
                        onChange={(e) => setOwnerSearch(e.target.value)}
                        autoFocus
                      />
                    </div>
                    <div className="hs-owner-list">
                      {/* "Me" shortcut */}
                      {(!ownerSearch || 'me'.includes(ownerSearch.toLowerCase()) || taskCurrentOwnerName.toLowerCase().includes(ownerSearch.toLowerCase())) && (
                        <label className="hs-owner-item hs-owner-me">
                          <input
                            type="checkbox"
                            checked={ownerSelected.has(taskCurrentOwnerId)}
                            onChange={() => {
                              setOwnerSelected(prev => {
                                const next = new Set(prev)
                                next.has(taskCurrentOwnerId) ? next.delete(taskCurrentOwnerId) : next.add(taskCurrentOwnerId)
                                return next
                              })
                            }}
                          />
                          <span className="hs-owner-checkbox" />
                          <span className="hs-owner-label">Me</span>
                        </label>
                      )}
                      {/* All users */}
                      {crmUsers
                        .filter(u => !ownerSearch || u.name.toLowerCase().includes(ownerSearch.toLowerCase()))
                        .map(user => (
                          <label className="hs-owner-item" key={user.id}>
                            <input
                              type="checkbox"
                              checked={ownerSelected.has(user.id)}
                              onChange={() => {
                                setOwnerSelected(prev => {
                                  const next = new Set(prev)
                                  next.has(user.id) ? next.delete(user.id) : next.add(user.id)
                                  return next
                                })
                              }}
                            />
                            <span className="hs-owner-checkbox" />
                            <span className="hs-owner-avatar">{user.initials || user.name.charAt(0)}</span>
                            <span className="hs-owner-label">{user.name}</span>
                          </label>
                        ))
                      }
                    </div>
                    {/* Unassigned footer */}
                    {(!ownerSearch || 'unassigned'.includes(ownerSearch.toLowerCase())) && (
                      <div className="hs-owner-footer">
                        <label className="hs-owner-item">
                          <input
                            type="checkbox"
                            checked={ownerSelected.has(0)}
                            onChange={() => {
                              setOwnerSelected(prev => {
                                const next = new Set(prev)
                                next.has(0) ? next.delete(0) : next.add(0)
                                return next
                              })
                            }}
                          />
                          <span className="hs-owner-checkbox" />
                          <span className="hs-owner-label">Unassigned</span>
                        </label>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Priority filter — cycle toggle */}
              <button
                className={`hs-qf-btn hs-qf-trigger${tasksPriorityFilter ? ' has-value' : ''}`}
                onClick={() => {
                  const vals = ['', 'high', 'medium', 'low']
                  const idx = vals.indexOf(tasksPriorityFilter)
                  setTasksPriorityFilter(vals[(idx + 1) % vals.length])
                  setTasksPage(1)
                }}
              >
                <span>{tasksPriorityFilter ? tasksPriorityFilter.charAt(0).toUpperCase() + tasksPriorityFilter.slice(1) : 'Priority'}</span>
                {tasksPriorityFilter && (
                  <span className="hs-qf-clear" onClick={(e) => { e.stopPropagation(); setTasksPriorityFilter(''); setTasksPage(1) }}>&times;</span>
                )}
                {!tasksPriorityFilter && <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>}
              </button>

              {/* Sort by — cycle toggle */}
              <button
                className={`hs-qf-btn hs-qf-trigger${tasksSort.field !== 'due_date' ? ' has-value' : ''}`}
                onClick={() => {
                  const fields = ['due_date', 'title', 'priority', 'created_at']
                  const idx = fields.indexOf(tasksSort.field)
                  const next = fields[(idx + 1) % fields.length]
                  setTasksSort({ field: next, order: next === 'title' ? 'ASC' : 'DESC' })
                  setTasksPage(1)
                }}
              >
                <span>Sort: {{ due_date: 'Due Date', title: 'Title', priority: 'Priority', created_at: 'Created' }[tasksSort.field] || 'Due Date'}</span>
                <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
              </button>

            </div>}

            <div className={`hs-content${previewTask ? ' with-preview' : ''}${tableSettingsOpen ? ' with-settings' : ''}`}>
              <div className="hs-table-area">
                {/* Selection bar */}
                {taskSelectedRows.size > 0 && (
                  <div className="hs-selection-bar">
                    <span className="hs-sel-count">{taskSelectedRows.size} selected</span>
                    <div className="hs-sel-sep" />
                    {taskSelectedRows.size === 1 && <button className="hs-sel-btn" onClick={() => {
                      const firstId = [...taskSelectedRows][0]
                      const task = taskPagedData.find(t => t.id === firstId)
                      if (task) openTaskEditor(task)
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                      Edit
                    </button>}
                    <button className="hs-sel-btn" style={{ color: '#dc2626' }} onClick={() => setBulkDeleteTaskConfirm(taskSelectedRows.size)}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                      Delete
                    </button>
                    <div className="hs-sel-sep" />
                    <button className="hs-sel-close" onClick={() => setTaskSelectedRows(new Set())} title="Deselect all">&times;</button>
                  </div>
                )}

                {/* Loading bar (thin animated bar shown during refresh when data already exists) */}
                {tasksLoading && tasksData.length > 0 && (
                  <div className="hs-loading-bar" />
                )}

                <div className="hs-table-scroll">
                  {tasksLoading && tasksData.length === 0 ? (
                    <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                      <colgroup>
                        <col style={{ width: 40 }} />
                        <col style={{ width: taskColWidths.title }} />
                        <col style={{ width: taskColWidths.status }} />
                        <col style={{ width: taskColWidths.priority }} />
                        <col style={{ width: taskColWidths.due_date }} />
                        <col style={{ width: taskColWidths.assigned_to }} />
                        <col style={{ width: taskColWidths.linked_to }} />
                        <col style={{ width: taskColWidths.actions }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th className="hs-th-check"><input type="checkbox" disabled /></th>
                          <th className="hs-th-resizable hs-th-frozen" style={{ position: 'sticky', left: 40, zIndex: 15, background: '#f8fafc', boxShadow: '2px 0 4px rgba(0,0,0,0.06)' }}><span className="hs-th-content">Title</span></th>
                          <th className="hs-th-resizable"><span className="hs-th-content">Status</span></th>
                          <th className="hs-th-resizable"><span className="hs-th-content">Priority</span></th>
                          <th className="hs-th-resizable"><span className="hs-th-content">Due Date</span></th>
                          <th className="hs-th-resizable"><span className="hs-th-content">Assigned To</span></th>
                          <th className="hs-th-resizable"><span className="hs-th-content">Linked To</span></th>
                          <th className="hs-th-resizable"><span className="hs-th-content">Actions</span></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr><td colSpan={8} className="hs-loading-cell"><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}><DotLottieReact src="/loading.lottie" loop autoplay style={{ width: 80, height: 80 }} /><span style={{ fontSize: '0.82rem', color: '#7c98b6', marginTop: 8 }}>Loading tasks...</span></div></td></tr>
                      </tbody>
                    </table>
                  ) : taskPagedData.length === 0 ? (
                    <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                      <colgroup>
                        <col style={{ width: 40 }} />
                        <col style={{ width: taskColWidths.title }} />
                        <col style={{ width: taskColWidths.status }} />
                        <col style={{ width: taskColWidths.priority }} />
                        <col style={{ width: taskColWidths.due_date }} />
                        <col style={{ width: taskColWidths.assigned_to }} />
                        <col style={{ width: taskColWidths.linked_to }} />
                        <col style={{ width: taskColWidths.actions }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th className="hs-th-check"><input type="checkbox" disabled /></th>
                          <th className="hs-th-resizable hs-th-frozen" style={{ position: 'sticky', left: 40, zIndex: 15, background: '#f8fafc', boxShadow: '2px 0 4px rgba(0,0,0,0.06)' }}><span className="hs-th-content">Title</span></th>
                          <th className="hs-th-resizable"><span className="hs-th-content">Status</span></th>
                          <th className="hs-th-resizable"><span className="hs-th-content">Priority</span></th>
                          <th className="hs-th-resizable"><span className="hs-th-content">Due Date</span></th>
                          <th className="hs-th-resizable"><span className="hs-th-content">Assigned To</span></th>
                          <th className="hs-th-resizable"><span className="hs-th-content">Linked To</span></th>
                          <th className="hs-th-resizable"><span className="hs-th-content">Actions</span></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr><td colSpan={8} className="hs-empty-cell">
                          <div style={{ padding: '40px', textAlign: 'center' }}>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '12px' }}><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                            <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#33475b', marginBottom: '6px' }}>No tasks found</div>
                            <div style={{ fontSize: '0.82rem', color: '#7c98b6', maxWidth: '360px', margin: '0 auto' }}>Create a new task or adjust your search filters.</div>
                          </div>
                        </td></tr>
                      </tbody>
                    </table>
                  ) : (
                    <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                      <colgroup>
                        <col style={{ width: 40 }} />
                        <col style={{ width: taskColWidths.title }} />
                        <col style={{ width: taskColWidths.status }} />
                        <col style={{ width: taskColWidths.priority }} />
                        <col style={{ width: taskColWidths.due_date }} />
                        <col style={{ width: taskColWidths.assigned_to }} />
                        <col style={{ width: taskColWidths.linked_to }} />
                        <col style={{ width: taskColWidths.actions }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th className="hs-th-check">
                            <input type="checkbox" checked={taskPagedData.length > 0 && taskSelectedRows.size === taskPagedData.length} onChange={toggleAllTaskRows} />
                          </th>
                          {/* Title - frozen first column */}
                          <th
                            className="hs-th-resizable hs-th-sortable hs-th-frozen"
                            style={{ position: 'sticky', left: 40, top: 0, zIndex: 15, background: '#f8fafc', boxShadow: '2px 0 4px rgba(0,0,0,0.06)' }}
                            onClick={() => toggleTaskSort('title')}
                          >
                            <span className="hs-th-content">
                              Title
                              {tasksSort.field === 'title' && <span className="hs-sort-arrow">{tasksSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                              {tasksSort.field !== 'title' && <span className="hs-th-hover-sort">{taskSortArrowSvg}</span>}
                            </span>
                            <div className="hs-col-resize-handle" onMouseDown={(e) => onTaskResizeStart('title', e)} onClick={(e) => e.stopPropagation()} />
                          </th>
                          {/* Status */}
                          <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleTaskSort('status')}>
                            <span className="hs-th-content">
                              Status
                              {tasksSort.field === 'status' && <span className="hs-sort-arrow">{tasksSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                              {tasksSort.field !== 'status' && <span className="hs-th-hover-sort">{taskSortArrowSvg}</span>}
                            </span>
                            <div className="hs-col-resize-handle" onMouseDown={(e) => onTaskResizeStart('status', e)} onClick={(e) => e.stopPropagation()} />
                          </th>
                          {/* Priority */}
                          <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleTaskSort('priority')}>
                            <span className="hs-th-content">
                              Priority
                              {tasksSort.field === 'priority' && <span className="hs-sort-arrow">{tasksSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                              {tasksSort.field !== 'priority' && <span className="hs-th-hover-sort">{taskSortArrowSvg}</span>}
                            </span>
                            <div className="hs-col-resize-handle" onMouseDown={(e) => onTaskResizeStart('priority', e)} onClick={(e) => e.stopPropagation()} />
                          </th>
                          {/* Due Date */}
                          <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleTaskSort('due_date')}>
                            <span className="hs-th-content">
                              Due Date
                              {tasksSort.field === 'due_date' && <span className="hs-sort-arrow">{tasksSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                              {tasksSort.field !== 'due_date' && <span className="hs-th-hover-sort">{taskSortArrowSvg}</span>}
                            </span>
                            <div className="hs-col-resize-handle" onMouseDown={(e) => onTaskResizeStart('due_date', e)} onClick={(e) => e.stopPropagation()} />
                          </th>
                          {/* Assigned To */}
                          <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleTaskSort('assigned_to')}>
                            <span className="hs-th-content">
                              Assigned To
                              {tasksSort.field === 'assigned_to' && <span className="hs-sort-arrow">{tasksSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                              {tasksSort.field !== 'assigned_to' && <span className="hs-th-hover-sort">{taskSortArrowSvg}</span>}
                            </span>
                            <div className="hs-col-resize-handle" onMouseDown={(e) => onTaskResizeStart('assigned_to', e)} onClick={(e) => e.stopPropagation()} />
                          </th>
                          {/* Linked To */}
                          <th className="hs-th-resizable">
                            <span className="hs-th-content">Linked To</span>
                            <div className="hs-col-resize-handle" onMouseDown={(e) => onTaskResizeStart('linked_to', e)} onClick={(e) => e.stopPropagation()} />
                          </th>
                          {/* Actions */}
                          <th className="hs-th-resizable">
                            <span className="hs-th-content">Actions</span>
                            <div className="hs-col-resize-handle" onMouseDown={(e) => onTaskResizeStart('actions', e)} onClick={(e) => e.stopPropagation()} />
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {taskPagedData.map((task) => {
                          const isPreviewed = previewTask?.id === task.id
                          const isSelected = taskSelectedRows.has(task.id)
                          return (
                          <tr
                            key={task.id}
                            className={`hs-row${isSelected ? ' selected' : ''}${isPreviewed ? ' is-previewed' : ''}`}
                            onClick={(e) => {
                              const t = e.target as HTMLElement
                              if (t.closest('input, a, button, select, .status-chip, .hs-col-resize-handle')) return
                              setTableSettingsOpen(false)
                              setPreviewTask(task)
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            <td
                              className="hs-td-check"
                              onClick={(e) => { e.stopPropagation(); setTaskSelectedRows(prev => { const next = new Set(prev); next.has(task.id) ? next.delete(task.id) : next.add(task.id); return next }) }}
                              style={{ cursor: 'pointer' }}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => {
                                  setTaskSelectedRows(prev => {
                                    const next = new Set(prev)
                                    next.has(task.id) ? next.delete(task.id) : next.add(task.id)
                                    return next
                                  })
                                }}
                              />
                            </td>
                            {/* Title - frozen first column with preview button */}
                            <td className="hs-td-name" style={{ position: 'sticky', left: 40, zIndex: 4, boxShadow: '2px 0 4px rgba(0,0,0,0.06)', background: isPreviewed ? '#eff6ff' : (isSelected ? '#eef2ff' : '#fff') }}>
                              <div className="hs-name-cell">
                                <div className="hs-avatar" style={{ background: task.priority === 'high' ? '#d94c53' : task.priority === 'low' ? '#94a3b8' : '#e67e22' }}>
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                                </div>
                                <span className="hs-name-link" style={{ textDecoration: task.status === 'completed' ? 'line-through' : 'none', color: task.status === 'completed' ? '#7c98b6' : '#33475b' }}>
                                  {task.title || <span className="cell-empty">--</span>}
                                </span>
                                <button className="hs-preview-btn" onClick={(e) => { e.stopPropagation(); setTableSettingsOpen(false); setPreviewTask(task) }}>Preview</button>
                              </div>
                            </td>
                            <td>
                              <span className={`hs-status-badge ${task.status}`} style={{
                                background: task.status === 'completed' ? '#e4f7e8' : task.status === 'in_progress' ? '#e8f0fe' : '#f5f8fa',
                                color: task.status === 'completed' ? '#1976d2' : task.status === 'in_progress' ? '#2e60e0' : '#516f90',
                              }}>
                                {task.status === 'in_progress' ? 'In Progress' : task.status.charAt(0).toUpperCase() + task.status.slice(1)}
                              </span>
                            </td>
                            <td>
                              <span className="hs-status-badge" style={{
                                background: task.priority === 'high' ? '#fdedee' : task.priority === 'low' ? '#f5f8fa' : '#fff3e0',
                                color: task.priority === 'high' ? '#d94c53' : task.priority === 'low' ? '#7c98b6' : '#e67e22',
                              }}>
                                {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
                              </span>
                            </td>
                            <td style={{ color: task.due_date && new Date(task.due_date) < new Date() && task.status !== 'completed' ? '#d94c53' : '#33475b' }}>
                              {task.due_date ? formatDatePref(task.due_date) : '\u2014'}
                            </td>
                            <td style={{ color: '#516f90' }}>
                              {task.assigned_to ? (CALENDAR_USERS_MAP[task.assigned_to] || `User ${task.assigned_to}`) : '\u2014'}
                            </td>
                            <td>
                              {(() => {
                                const ot = (task as any).owner_type as string | null
                                const oid = (task as any).owner_id as string | null
                                const label = (task as any).owner_label as string | null
                                if (!ot || !oid) return <span className="cell-empty">--</span>
                                const typeLabel = ot === 'client' ? 'Contact' : ot === 'lead' ? 'Lead' : ot === 'deal' ? 'Deal' : ot === 'project' ? 'Project' : ot === 'ticket' ? 'Ticket' : ot
                                const typeColor: Record<string, { bg: string; fg: string }> = {
                                  lead: { bg: '#e8f4ff', fg: '#1565c0' },
                                  client: { bg: '#eef2f6', fg: '#475569' },
                                  deal: { bg: '#ecfdf5', fg: '#047857' },
                                  project: { bg: '#fef3c7', fg: '#a16207' },
                                  ticket: { bg: '#fef2f2', fg: '#b91c1c' },
                                }
                                const c = typeColor[ot] || { bg: '#f1f5f9', fg: '#475569' }
                                const hash = ot === 'lead' ? `/leads` : ot === 'deal' ? `/deals/record/${oid}` : ot === 'client' ? `/contacts/record/${oid}` : ot === 'project' ? `/projects/record/${oid}` : ot === 'ticket' ? `/tickets/record/${oid}` : ''
                                return (
                                  <a href={hash ? `#${hash}` : '#'} onClick={(e) => { if (!hash) e.preventDefault(); e.stopPropagation() }} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none', maxWidth: '100%' }}>
                                    <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 3, background: c.bg, color: c.fg, textTransform: 'uppercase', letterSpacing: '0.02em', flexShrink: 0 }}>{typeLabel}</span>
                                    <span style={{ color: '#1976d2', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label || `#${oid}`}</span>
                                  </a>
                                )
                              })()}
                            </td>
                            <td>
                              <button
                                title="Delete task"
                                onClick={(e) => { e.stopPropagation(); setDeleteTaskConfirm(task) }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = '#b91c1c'; e.currentTarget.style.borderColor = '#b91c1c' }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = '#dc2626'; e.currentTarget.style.borderColor = '#dc2626' }}
                                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, padding: 0, border: '1px solid #dc2626', borderRadius: 6, background: '#dc2626', color: '#fff', cursor: 'pointer', transition: 'background .15s, border-color .15s', boxShadow: '0 1px 2px rgba(220,38,38,0.25)' }}
                              >
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                              </button>
                            </td>
                          </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Pagination — always visible */}
                <div className="hs-pagination">
                  <nav className="hs-paginator" aria-label="Pagination">
                    <button
                      className="hs-paginator-btn"
                      disabled={taskCurrentPage <= 1}
                      onClick={() => setTasksPage((p) => p - 1)}
                      aria-label="Previous page"
                    >
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95"/></svg>
                      <span>Prev</span>
                    </button>
                    {(() => {
                      if (taskTotalPages <= 7) {
                        const pages: number[] = []
                        for (let i = 1; i <= taskTotalPages; i++) pages.push(i)
                        return pages.map(p => (
                          <button key={p} className={`hs-paginator-num${p === taskCurrentPage ? ' active' : ''}`} onClick={() => setTasksPage(p)}>{p}</button>
                        ))
                      }
                      const items: React.ReactNode[] = []
                      const addPage = (p: number) => items.push(
                        <button key={p} className={`hs-paginator-num${p === taskCurrentPage ? ' active' : ''}`} onClick={() => setTasksPage(p)}>{p}</button>
                      )
                      const addEllipsis = (key: string) => items.push(
                        <span key={key} className="hs-paginator-ellipsis">...</span>
                      )
                      addPage(1)
                      if (taskCurrentPage > 3) addEllipsis('el')
                      const start = Math.max(2, taskCurrentPage - 1)
                      const end = Math.min(taskTotalPages - 1, taskCurrentPage + 1)
                      for (let i = start; i <= end; i++) addPage(i)
                      if (taskCurrentPage < taskTotalPages - 2) addEllipsis('er')
                      addPage(taskTotalPages)
                      return items
                    })()}
                    <button
                      className="hs-paginator-btn"
                      disabled={taskCurrentPage >= taskTotalPages}
                      onClick={() => setTasksPage((p) => p + 1)}
                      aria-label="Next page"
                    >
                      <span>Next</span>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M11 27.71c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62l9.48-9.48-9.48-9.47a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0L22.1 15.48c.45.45.45 1.17 0 1.62L11.81 27.39c-.22.22-.52.33-.81.33Z"/></svg>
                    </button>
                  </nav>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', marginLeft: 'auto', marginRight: 50 }}><select value={tablePageSize} onChange={e => { setTablePageSize(Number(e.target.value) as any); setTasksPage(1) }} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #e2e8f0', fontSize: '0.82rem' }}><option value={100}>100</option><option value={200}>200</option><option value={500}>500</option></select><span>per page</span></div>
                </div>
              </div>
            </div>

            {/* Task preview sidebar */}
            {(previewTask || previewTaskClosing) && (() => {
              const task = previewTask
              const priorityColors: Record<string, { bg: string; fg: string }> = {
                high: { bg: '#fdedee', fg: '#d94c53' },
                medium: { bg: '#fff3e0', fg: '#e67e22' },
                low: { bg: '#f5f8fa', fg: '#7c98b6' },
              }
              const pc = priorityColors[task?.priority ?? 'medium'] || priorityColors.medium
              return (
                <div className={`hs-preview${previewTaskClosing ? ' closing' : ''}`}>
                  <div className="hs-preview-header">
                    <strong className="hs-preview-name">{task?.title}</strong>
                    <button className="hs-preview-close" onClick={closeTaskPreview}><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg></button>
                  </div>
                  <div className="hs-preview-body">
                    <div className="hs-preview-hero">
                      <div className="hs-preview-avatar-lg" style={{ background: pc.fg }}>
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                      </div>
                      <div className="hs-preview-hero-info">
                        <h3>{task?.title}</h3>
                        <span className="hs-status-badge" style={{ background: pc.bg, color: pc.fg }}>
                          {task?.priority?.charAt(0).toUpperCase() + (task?.priority?.slice(1) ?? '')}
                        </span>
                      </div>
                    </div>

                    <div className="hs-preview-actions">
                      <button className="hs-preview-action" title="Edit" onClick={() => {
                        closeTaskPreview()
                        if (task) openTaskEditor(task)
                      }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                      </button>
                      <button className="hs-preview-action" title="Delete" style={{ color: '#d94c53' }} onClick={() => { if (task) setDeleteTaskConfirm(task) }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                      </button>
                    </div>

                    <div className="hs-preview-section">
                      <h4>Task Details</h4>
                      <div className="hs-preview-field">
                        <label>Status</label>
                        <span><span className="hs-status-badge" style={{ background: task?.status === 'completed' ? '#e4f7e8' : task?.status === 'in_progress' ? '#e8f0fe' : '#f5f8fa', color: task?.status === 'completed' ? '#1976d2' : task?.status === 'in_progress' ? '#2e60e0' : '#516f90', fontSize: '0.72rem' }}>{task?.status === 'in_progress' ? 'In Progress' : (task?.status ?? '').charAt(0).toUpperCase() + (task?.status ?? '').slice(1)}</span></span>
                      </div>
                      <div className="hs-preview-field">
                        <label>Priority</label>
                        <span><span className="hs-status-badge" style={{ background: pc.bg, color: pc.fg, fontSize: '0.72rem' }}>{task?.priority?.charAt(0).toUpperCase() + (task?.priority?.slice(1) ?? '')}</span></span>
                      </div>
                      <div className="hs-preview-field">
                        <label>Due Date</label>
                        <span style={{ color: task?.due_date && new Date(task.due_date) < new Date() && task.status !== 'completed' ? '#d94c53' : undefined }}>{task?.due_date ? formatDatePref(task.due_date) : '--'}</span>
                      </div>
                      <div className="hs-preview-field">
                        <label>Assigned To</label>
                        <span>{task?.assigned_to ? (CALENDAR_USERS_MAP[task.assigned_to] || `User ${task.assigned_to}`) : '--'}</span>
                      </div>
                      <div className="hs-preview-field">
                        <label>Linked To</label>
                        <span>
                          {(() => {
                            const ot = (task as any)?.owner_type as string | null
                            const oid = (task as any)?.owner_id as string | null
                            const label = (task as any)?.owner_label as string | null
                            if (!ot || !oid) return '--'
                            const typeLabel = ot === 'client' ? 'Contact' : ot === 'lead' ? 'Lead' : ot === 'deal' ? 'Deal' : ot === 'project' ? 'Project' : ot === 'ticket' ? 'Ticket' : ot
                            const typeColor: Record<string, { bg: string; fg: string }> = {
                              lead: { bg: '#e8f4ff', fg: '#1565c0' },
                              client: { bg: '#eef2f6', fg: '#475569' },
                              deal: { bg: '#ecfdf5', fg: '#047857' },
                              project: { bg: '#fef3c7', fg: '#a16207' },
                              ticket: { bg: '#fef2f2', fg: '#b91c1c' },
                            }
                            const cc = typeColor[ot] || { bg: '#f1f5f9', fg: '#475569' }
                            const hash = ot === 'lead' ? `/leads` : ot === 'deal' ? `/deals/record/${oid}` : ot === 'client' ? `/contacts/record/${oid}` : ot === 'project' ? `/projects/record/${oid}` : ot === 'ticket' ? `/tickets/record/${oid}` : ''
                            return (
                              <a href={hash ? `#${hash}` : '#'} onClick={(e) => { if (!hash) e.preventDefault() }} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}>
                                <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 3, background: cc.bg, color: cc.fg, textTransform: 'uppercase', letterSpacing: '0.02em' }}>{typeLabel}</span>
                                <span style={{ color: '#1976d2' }}>{label || `#${oid}`}</span>
                              </a>
                            )
                          })()}
                        </span>
                      </div>
                    </div>

                    {task?.body && (
                      <div className="hs-preview-section">
                        <h4>Description</h4>
                        <p style={{ fontSize: '0.82rem', color: '#516f90', lineHeight: 1.5, margin: 0 }}>{task.body}</p>
                      </div>
                    )}
                  </div>
                </div>
              )
            })()}

            {/* Table settings panel (gear button) */}
            {tableSettingsOpen && (
              <div className="hs-settings-panel">
                <div className="hs-settings-header">
                  <span className="hs-settings-title">Table settings</span>
                  <button className="hs-settings-close" onClick={() => setTableSettingsOpen(false)}>
                    <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                  </button>
                </div>
                <div className="hs-settings-body">
                  <div className="hs-settings-group">
                    <label className="hs-settings-label">
                      Pagination
                      <span className="hs-info-icon" title="Number of rows shown per page">
                        <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg>
                      </span>
                    </label>
                    {([100, 200, 500] as const).map(size => (
                      <label className="hs-settings-radio" key={size}>
                        <input type="radio" name="taskPageSize" checked={tablePageSize === size} onChange={() => { setTablePageSize(size); setTasksPage(1) }} />
                        <span className="hs-radio-indicator" />
                        <span>{size} per page</span>
                      </label>
                    ))}
                  </div>
                  <hr className="hs-settings-divider" />
                  <div className="hs-settings-group">
                    <label className="hs-settings-label">
                      Row height
                      <span className="hs-info-icon" title="Adjust the vertical spacing of table rows">
                        <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg>
                      </span>
                    </label>
                    {([
                      { value: 'small' as const, label: 'Compact' },
                      { value: 'medium' as const, label: 'Default' },
                      { value: 'large' as const, label: 'Comfortable' },
                    ]).map(opt => (
                      <label className="hs-settings-radio" key={opt.value}>
                        <input type="radio" name="taskRowHeight" checked={tableRowHeight === opt.value} onChange={() => setTableRowHeight(opt.value)} />
                        <span className="hs-radio-indicator" />
                        <span>{opt.label}</span>
                      </label>
                    ))}
                  </div>
                  <hr className="hs-settings-divider" />
                  <div className="hs-settings-group">
                    <div className="hs-settings-toggle-row">
                      <label className="hs-settings-label">
                        Zebra striping
                        <span className="hs-info-icon" title="Alternate row background colors for easier reading">
                          <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg>
                        </span>
                      </label>
                      <button
                        className={`hs-toggle${tableZebraStriping ? ' on' : ''}`}
                        onClick={() => setTableZebraStriping(!tableZebraStriping)}
                        role="switch"
                        aria-checked={tableZebraStriping}
                      >
                        <span className="hs-toggle-track">
                          <span className="hs-toggle-on">ON</span>
                          <span className="hs-toggle-off">OFF</span>
                          <span className="hs-toggle-handle" />
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          )
        })()}

        {/* Delete-task confirmation modal — shared across views */}
        {deleteTaskConfirm && (
          <div onClick={() => !deleteTaskSaving && setDeleteTaskConfirm(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, width: '100%', maxWidth: 380, padding: 20, boxShadow: '0 12px 30px rgba(0,0,0,0.2)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                </div>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Delete this task?</h3>
              </div>
              <p style={{ margin: '0 0 14px 0', fontSize: 13, color: '#475569', lineHeight: 1.5 }}>
                <strong style={{ color: '#0f172a' }}>{deleteTaskConfirm.title || 'This task'}</strong> will be permanently removed. This can't be undone.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button type="button" disabled={deleteTaskSaving} onClick={() => setDeleteTaskConfirm(null)} style={{ padding: '7px 14px', borderRadius: 4, border: '1px solid #cbd6e2', background: '#fff', color: '#475569', fontSize: 13, fontWeight: 600, cursor: deleteTaskSaving ? 'not-allowed' : 'pointer' }}>Cancel</button>
                <button type="button" disabled={deleteTaskSaving} onClick={async () => {
                  if (!deleteTaskConfirm) return
                  setDeleteTaskSaving(true)
                  try {
                    await deleteTask(deleteTaskConfirm.id)
                    setTasksData(prev => prev.filter(t => t.id !== deleteTaskConfirm.id))
                    setTasksTotal(prev => Math.max(0, prev - 1))
                    if (previewTask?.id === deleteTaskConfirm.id) setPreviewTask(null)
                    setDeleteTaskConfirm(null)
                  } catch { /* keep modal open on error */ }
                  finally { setDeleteTaskSaving(false) }
                }} style={{ padding: '7px 14px', borderRadius: 4, border: 'none', background: '#dc2626', color: '#fff', fontSize: 13, fontWeight: 600, cursor: deleteTaskSaving ? 'not-allowed' : 'pointer', opacity: deleteTaskSaving ? 0.7 : 1 }}>
                  {deleteTaskSaving ? 'Deleting…' : 'Delete task'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk delete-task confirmation modal */}
        {bulkDeleteTaskConfirm !== null && (
          <div onClick={() => !bulkDeleteTaskSaving && setBulkDeleteTaskConfirm(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, width: '100%', maxWidth: 380, padding: 20, boxShadow: '0 12px 30px rgba(0,0,0,0.2)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                </div>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Delete {bulkDeleteTaskConfirm} task{bulkDeleteTaskConfirm === 1 ? '' : 's'}?</h3>
              </div>
              <p style={{ margin: '0 0 14px 0', fontSize: 13, color: '#475569', lineHeight: 1.5 }}>
                The selected task{bulkDeleteTaskConfirm === 1 ? '' : 's'} will be permanently removed. This can't be undone.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button type="button" disabled={bulkDeleteTaskSaving} onClick={() => setBulkDeleteTaskConfirm(null)} style={{ padding: '7px 14px', borderRadius: 4, border: '1px solid #cbd6e2', background: '#fff', color: '#475569', fontSize: 13, fontWeight: 600, cursor: bulkDeleteTaskSaving ? 'not-allowed' : 'pointer' }}>Cancel</button>
                <button type="button" disabled={bulkDeleteTaskSaving} onClick={async () => {
                  const ids = [...taskSelectedRows]
                  setBulkDeleteTaskSaving(true)
                  try {
                    await Promise.all(ids.map(id => deleteTask(id)))
                    setTaskSelectedRows(new Set())
                    setTasksData(prev => prev.filter(t => !ids.includes(t.id)))
                    setTasksTotal(prev => Math.max(0, prev - ids.length))
                    setBulkDeleteTaskConfirm(null)
                  } catch { /* keep modal open */ }
                  finally { setBulkDeleteTaskSaving(false) }
                }} style={{ padding: '7px 14px', borderRadius: 4, border: 'none', background: '#dc2626', color: '#fff', fontSize: 13, fontWeight: 600, cursor: bulkDeleteTaskSaving ? 'not-allowed' : 'pointer', opacity: bulkDeleteTaskSaving ? 0.7 : 1 }}>
                  {bulkDeleteTaskSaving ? 'Deleting…' : `Delete ${bulkDeleteTaskConfirm} task${bulkDeleteTaskConfirm === 1 ? '' : 's'}`}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tickets view is now handled in the contacts/companies/leads HubSpot-style block above */}

        {/* ========== My Documents View ========== */}
        {activeView === 'my-documents' && (
          <Suspense fallback={<div className="view-loading" />}>
            <MyDocuments openSignModal={(id, idx, mode) => openHrSignModal(id, idx, mode)} />
          </Suspense>
        )}

        {/* ========== My Time View (shift clock, employee-facing) ========== */}
        {activeView === 'my-time' && (
          <Suspense fallback={<div className="view-loading" />}>
            <MyTimeView />
          </Suspense>
        )}

        {/* ========== Products View ========== */}
        {activeView === 'products' && (
          <div className="hs-contacts">
            {/* Row 1: Topbar — Object switcher + view tabs + create button */}
            <div className="hs-topbar">
              <div className="hs-topbar-left">
                {/* Object switcher */}
                <div className="hs-obj-switcher-wrap" ref={objSwitcherRef}>
                  <button className={`hs-obj-switcher${objSwitcherOpen ? ' open' : ''}`} onClick={() => { setObjSwitcherOpen(!objSwitcherOpen); setObjSwitcherSearch('') }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="m7.5 4.27 9 5.15"/><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/></svg>
                    <span>Products</span>
                    <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                  </button>
                  {objSwitcherOpen && (
                    <div className="hs-obj-panel">
                      <div className="hs-obj-search-wrap">
                        <svg className="hs-obj-search-icon" width="13" height="13" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                        <input
                          className="hs-obj-search"
                          placeholder="Search"
                          value={objSwitcherSearch}
                          onChange={(e) => setObjSwitcherSearch(e.target.value)}
                          autoFocus
                        />
                      </div>
                      <ul className="hs-obj-list" role="listbox">
                        {[
                          { label: 'Prospecting', view: 'prospecting' as ViewMode },
                          { label: 'Deals', view: 'deals' as ViewMode },
                          { label: 'Forecasts', view: 'forecasts' as ViewMode },
                          { label: 'Target Accounts', view: 'target-accounts' as ViewMode },
                          { label: 'Activity Feed', view: 'activity-feed' as ViewMode },
                          { label: 'Documents', view: 'sales-documents' as ViewMode },
                          { label: 'Meetings', view: 'meetings-scheduler' as ViewMode },
                          { label: 'Coaching Playlists', view: 'coaching-playlists' as ViewMode },
                          { label: 'Payments', view: 'payments' as ViewMode },
                          { label: 'Quotes', view: 'quotes' as ViewMode },
                          { label: 'Products', view: 'products' as ViewMode },
                        ]
                          .filter(item => !objSwitcherSearch || item.label.toLowerCase().includes(objSwitcherSearch.toLowerCase()))
                          .map(item => (
                            <li
                              key={item.view}
                              className={`hs-obj-item${item.view === activeView ? ' active' : ''}`}
                              role="option"
                              aria-selected={item.view === activeView}
                              onClick={() => {
                                setActiveView(item.view)
                                setObjSwitcherOpen(false)
                                setObjSwitcherSearch('')
                              }}
                            >
                              {item.label}
                            </li>
                          ))
                        }
                      </ul>
                    </div>
                  )}
                </div>
                <div className="hs-topbar-sep" />
                {/* View tabs */}
                <div className="hs-view-tabs">
                  {[
                    { key: '', label: 'All products' },
                    { key: 'active', label: 'Active' },
                    { key: 'inactive', label: 'Inactive' },
                  ].map((tab) => {
                    const isActive = productsStatusFilter === tab.key
                    return (
                      <div
                        key={tab.key}
                        className={`hs-view-tab${isActive ? ' active' : ''}`}
                        onClick={() => { setProductsStatusFilter(tab.key); setProductsPage(1) }}
                      >
                        <span className="hs-tab-label">{tab.label}</span>
                        {isActive && productsTotal > 0 && <span className="hs-tab-count">{productsTotal.toLocaleString()}</span>}
                        {isActive && (
                          <span className="hs-tab-kebab" onClick={(e) => { e.stopPropagation(); setTabEditOpen(!tabEditOpen) }}>
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>
                          </span>
                        )}
                        {tab.key !== '' && <span className="hs-tab-x" onClick={(e) => { e.stopPropagation(); setProductsStatusFilter(''); setProductsPage(1) }}>&times;</span>}
                      </div>
                    )
                  })}
                  <div className="hs-topbar-sep" />
                  <div className="hs-view-add-wrap" style={{ position: 'relative', display: 'inline-flex', alignSelf: 'center' }}>
                    <button className="hs-quick-add-btn" title="Create or add view" type="button" aria-label="Create or add view" style={{ opacity: 0.4, cursor: 'default' }} disabled>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                    </button>
                  </div>
                </div>
              </div>
              <div className="hs-topbar-right">
                {/* Create product — direct action (Import removed) */}
                <button className="hs-add-contacts-btn" onClick={() => { setCreateFormData({}); setCreateError(null); setShowCreateModal('product') }}>
                  Create product
                </button>
              </div>
            </div>

            {/* Row 2: Toolbar — Search + View type/gear + Filters + Export + Clone/Save */}
            <div className="hs-toolbar">
              <div className="hs-toolbar-left">
                {/* Search */}
                <div className="hs-search-wrap">
                  <svg className="hs-search-icon" width="14" height="14" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M28.14 29.29c-.29 0-.58-.11-.81-.33l-7.58-7.58a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l7.58 7.58a1.14 1.14 0 0 1-.81 1.95"/><path d="M13.29 24.71c-6.3 0-11.43-5.13-11.43-11.43S6.98 1.86 13.29 1.86s11.43 5.13 11.43 11.43-5.13 11.43-11.43 11.43Zm0-20.57c-5.04 0-9.14 4.1-9.14 9.14s4.1 9.14 9.14 9.14 9.14-4.1 9.14-9.14-4.1-9.14-9.14-9.14"/></svg>
                  <input className="hs-search-input" placeholder="Search products" value={productsSearch} onChange={(e) => { setProductsSearch(e.target.value); setProductsPage(1) }} />
                  {productsSearch && <button className="hs-search-clear" onClick={() => { setProductsSearch(''); setProductsPage(1) }}>&times;</button>}
                </div>

                <div className="hs-tb-sep" />

                {/* View type + settings button group */}
                <div className="hs-viewtype-group">
                  <div className="hs-viewtype-wrap">
                    <button className="hs-tb-btn hs-viewtype-btn" style={{ cursor: 'default' }}>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                      <span>Table</span>
                    </button>
                  </div>
                  <button
                    className={`hs-tb-btn hs-settings-btn${tableSettingsOpen ? ' active' : ''}`}
                    onClick={() => setTableSettingsOpen(!tableSettingsOpen)}
                    title={tableSettingsOpen ? 'Close settings' : 'Table settings'}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                  </button>
                </div>

                <div className="hs-tb-sep" />

                {/* Edit columns (placeholder) */}
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Edit columns" style={{ opacity: 0.5, cursor: 'default' }} disabled>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="18" rx="1"/><rect x="14" y="3" width="7" height="18" rx="1"/></svg>
                  <span>Edit columns</span>
                </button>

                <div className="hs-tb-sep" />

                {/* Filters */}
                <button className={`hs-tb-btn hs-tb-btn-bordered${quickFiltersOpen ? ' hs-tb-btn-pressed' : ''}`} title="Filters" onClick={() => setQuickFiltersOpen(!quickFiltersOpen)}>
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M30.71 16.86H13.57a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28M6.71 16.86H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M10.14 20.29c-2.52 0-4.57-2.05-4.57-4.57s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M6.71 6.57H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M30.71 6.57H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M18.14 6.57H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 10C19.05 10 17 7.95 17 5.43S19.05.86 21.57.86s4.57 2.05 4.57 4.57S24.09 10 21.57 10m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M30.71 27.14H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M18.14 27.14H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 30.57C19.05 30.57 17 28.52 17 26s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29"/></svg>
                  <span>Filters</span>
                </button>

                <div className="hs-tb-sep" />

                {/* Export */}
                <button
                  className={`hs-tb-btn hs-tb-btn-bordered${productsExporting ? ' hs-tb-btn-pressed' : ''}`}
                  title="Export products as CSV"
                  disabled={productsExporting}
                  onClick={async () => {
                    setProductsExporting(true)
                    try {
                      const data = await fetchProducts({ limit: 10000, search: productsSearch || undefined, active: productsStatusFilter === 'active' ? '1' : productsStatusFilter === 'inactive' ? '0' : undefined })
                      const headers = ['Name', 'SKU', 'Category', 'Unit Price', 'Unit', 'Status', 'Created']
                      const escCsv = (v: unknown) => { const s = String(v ?? ''); return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s }
                      const rows = data.products.map((p: CrmProduct) => [
                        p.name, p.sku, p.category, p.unit_price, p.unit, p.is_active ? 'Active' : 'Inactive', p.created_at,
                      ].map(escCsv).join(','))
                      const csv = [headers.join(','), ...rows].join('\n')
                      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
                      const url = URL.createObjectURL(blob)
                      const a = document.createElement('a')
                      a.href = url
                      a.download = `products-export-${new Date().toISOString().slice(0, 10)}.csv`
                      document.body.appendChild(a)
                      a.click()
                      document.body.removeChild(a)
                      URL.revokeObjectURL(url)
                    } catch (err) {
                      console.error('Export failed:', err)
                      alert('Export failed. Please try again.')
                    } finally {
                      setProductsExporting(false)
                    }
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  <span>{productsExporting ? 'Exporting...' : 'Export'}</span>
                </button>
              </div>
              <div className="hs-toolbar-right">
                <div className="hs-tb-sep" />
                {/* Clone */}
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Clone view" style={{ opacity: 0.5, cursor: 'default' }} disabled>
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M25.86 29.29H12.15c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-1.89 1.54-3.43 3.43-3.43h13.71c1.89 0 3.43 1.54 3.43 3.43v13.71c0 1.89-1.54 3.43-3.43 3.43M12.14 11c-.63 0-1.14.51-1.14 1.14v13.71c0 .63.51 1.14 1.14 1.14h13.71c.63 0 1.14-.51 1.14-1.14V12.14c0-.63-.51-1.14-1.14-1.14z"/><path d="M5.29 22.43c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43H19c1.89 0 3.43 1.54 3.43 3.43a1.14 1.14 0 1 1-2.28 0c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14V19c0 .63.51 1.14 1.14 1.14a1.14 1.14 0 1 1 0 2.28Z"/></svg>
                </button>
                {/* Save */}
                <button className="hs-tb-btn hs-tb-btn-bordered disabled" disabled title="Save view">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                </button>
              </div>
            </div>

            {/* Row 3: Quick Filters — Status cycle + Sort + +More */}
            {quickFiltersOpen && <div className="hs-quick-filters">
              {/* Status cycle toggle */}
              <button
                className={`hs-qf-btn hs-qf-trigger${productsStatusFilter ? ' has-value' : ''}`}
                onClick={() => {
                  const vals = ['', 'active', 'inactive']
                  const idx = vals.indexOf(productsStatusFilter)
                  setProductsStatusFilter(vals[(idx + 1) % vals.length])
                  setProductsPage(1)
                }}
              >
                <span>{productsStatusFilter === 'active' ? 'Active' : productsStatusFilter === 'inactive' ? 'Inactive' : 'Status'}</span>
                {productsStatusFilter && (
                  <span className="hs-qf-clear" onClick={(e) => { e.stopPropagation(); setProductsStatusFilter(''); setProductsPage(1) }}>&times;</span>
                )}
                {!productsStatusFilter && <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>}
              </button>

              {/* Sort by */}
              <button
                className={`hs-qf-btn hs-qf-trigger${productsSort.field !== 'created_at' ? ' has-value' : ''}`}
                onClick={() => {
                  const fields = ['created_at', 'name', 'unit_price', 'category', 'sku']
                  const idx = fields.indexOf(productsSort.field)
                  const next = fields[(idx + 1) % fields.length]
                  setProductsSort({ field: next, order: next === 'name' || next === 'category' || next === 'sku' ? 'ASC' : 'DESC' })
                  setProductsPage(1)
                }}
              >
                <span>Sort: {{ created_at: 'Created', name: 'Name', unit_price: 'Price', category: 'Category', sku: 'SKU' }[productsSort.field] || 'Created'}</span>
                <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
              </button>

              <div className="hs-qf-sep" />

              {/* +More placeholder */}
              <button className="hs-qf-btn hs-qf-more" style={{ opacity: 0.5, cursor: 'default' }} disabled>
                <svg width="10" height="10" viewBox="0 0 32 32" fill="currentColor"><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V3a1.14 1.14 0 1 1 2.28 0v25.14c0 .63-.51 1.14-1.14 1.14Z"/><path d="M28.14 16.71H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28"/></svg>
                <span>More</span>
              </button>
            </div>}

            {/* Main content area */}
            <div className={`hs-content${previewProduct ? ' with-preview' : ''}${tableSettingsOpen ? ' with-settings' : ''}`}>
              <div className="hs-table-area">

                {/* Row 4: Selection Bar */}
                {productsSelectedRows.size > 0 && (
                  <div className="hs-selection-bar">
                    <span className="hs-sel-count">{productsSelectedRows.size} selected</span>
                    <div className="hs-sel-sep" />
                    <button className="hs-sel-btn">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                      Edit
                    </button>
                    <button className="hs-sel-btn" style={{ color: '#dc2626' }} onClick={() => {
                      if (confirm(`Delete ${productsSelectedRows.size} product(s)?`)) {
                        Promise.all([...productsSelectedRows].map(id => deleteProduct(id))).then(() => {
                          setProductsData(prev => prev.filter(p => !productsSelectedRows.has(p.id)))
                          setProductsTotal(prev => prev - productsSelectedRows.size)
                          setProductsSelectedRows(new Set())
                        }).catch(() => {})
                      }
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                      Delete
                    </button>
                    <button className="hs-sel-btn">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
                      Create tasks
                    </button>
                    <div className="hs-sel-sep" />
                    <button className="hs-sel-close" onClick={() => setProductsSelectedRows(new Set())} title="Deselect all">&times;</button>
                  </div>
                )}

                {/* Row 5: Data Table */}
                <div className="hs-table-scroll">
                  {(() => {
                    // Client-side sort (API returns paginated, we sort locally for display)
                    const sorted = [...productsData].sort((a, b) => {
                      const field = productsSort.field as keyof CrmProduct
                      const aVal = a[field] ?? ''
                      const bVal = b[field] ?? ''
                      if (typeof aVal === 'number' && typeof bVal === 'number') return productsSort.order === 'ASC' ? aVal - bVal : bVal - aVal
                      return productsSort.order === 'ASC' ? String(aVal).localeCompare(String(bVal)) : String(bVal).localeCompare(String(aVal))
                    })
                    const toggleProductSort = (field: string) => {
                      setProductsSort(prev => prev.field === field ? { field, order: prev.order === 'ASC' ? 'DESC' : 'ASC' } : { field, order: field === 'name' || field === 'category' || field === 'sku' ? 'ASC' : 'DESC' })
                    }
                    const toggleAllProductRows = () => {
                      if (productsSelectedRows.size === productsData.length) {
                        setProductsSelectedRows(new Set())
                      } else {
                        setProductsSelectedRows(new Set(productsData.map(p => p.id)))
                      }
                    }
                    const toggleProductRow = (id: number) => {
                      setProductsSelectedRows(prev => {
                        const next = new Set(prev)
                        next.has(id) ? next.delete(id) : next.add(id)
                        return next
                      })
                    }
                    const onProductResizeStart = (col: string, e: React.MouseEvent) => {
                      e.preventDefault()
                      e.stopPropagation()
                      const startX = e.clientX
                      const startW = (productColWidths as Record<string, number>)[col] || 140
                      const onMove = (ev: MouseEvent) => {
                        const diff = ev.clientX - startX
                        setProductColWidths(prev => ({ ...prev, [col]: Math.max(60, startW + diff) }))
                      }
                      const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp) }
                      document.addEventListener('mousemove', onMove)
                      document.addEventListener('mouseup', onUp)
                    }
                    const sortArrowSvg = <svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M29.38 21.96a1.147 1.147 0 0 0-1.617 0l-3.85 3.852V5.714a1.143 1.143 0 0 0-2.286 0v20.099l-3.853-3.852a1.144 1.144 0 0 0-1.615 1.617l5.803 5.802a1.143 1.143 0 0 0 1.616 0l5.802-5.802a1.144 1.144 0 0 0 0-1.617M15.84 8.422 10.039 2.62a1.145 1.145 0 0 0-1.616 0L2.62 8.422a1.144 1.144 0 0 0 1.617 1.617l3.85-3.851v20.098a1.143 1.143 0 0 0 2.286 0V6.187l3.853 3.852a1.14 1.14 0 0 0 1.616-.001 1.14 1.14 0 0 0-.001-1.616"/></svg>

                    return (
                      <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                        <colgroup>
                          <col style={{ width: 40 }} />
                          <col style={{ width: productColWidths.name }} />
                          <col style={{ width: productColWidths.sku }} />
                          <col style={{ width: productColWidths.category }} />
                          <col style={{ width: productColWidths.unit_price }} />
                          <col style={{ width: productColWidths.unit }} />
                          <col style={{ width: productColWidths.status }} />
                          <col style={{ width: productColWidths.created_at }} />
                        </colgroup>
                        <thead>
                          <tr>
                            <th className="hs-th-check">
                              <input
                                type="checkbox"
                                checked={productsData.length > 0 && productsSelectedRows.size === productsData.length}
                                onChange={toggleAllProductRows}
                              />
                            </th>
                            {/* Name — frozen first column */}
                            <th
                              className="hs-th-resizable hs-th-sortable hs-th-frozen"
                              style={{ position: 'sticky', left: 40, top: 0, zIndex: 15, background: '#f8fafc', boxShadow: '2px 0 4px rgba(0,0,0,0.06)' }}
                              onClick={() => toggleProductSort('name')}
                            >
                              <span className="hs-th-content">
                                Name
                                {productsSort.field === 'name' && <span className="hs-sort-arrow">{productsSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                                {productsSort.field !== 'name' && <span className="hs-th-hover-sort">{sortArrowSvg}</span>}
                              </span>
                              <div className="hs-col-resize-handle" onMouseDown={(e) => onProductResizeStart('name', e)} onClick={(e) => e.stopPropagation()} />
                            </th>
                            {/* SKU */}
                            <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleProductSort('sku')}>
                              <span className="hs-th-content">
                                SKU
                                {productsSort.field === 'sku' && <span className="hs-sort-arrow">{productsSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                                {productsSort.field !== 'sku' && <span className="hs-th-hover-sort">{sortArrowSvg}</span>}
                              </span>
                              <div className="hs-col-resize-handle" onMouseDown={(e) => onProductResizeStart('sku', e)} onClick={(e) => e.stopPropagation()} />
                            </th>
                            {/* Category */}
                            <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleProductSort('category')}>
                              <span className="hs-th-content">
                                Category
                                {productsSort.field === 'category' && <span className="hs-sort-arrow">{productsSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                                {productsSort.field !== 'category' && <span className="hs-th-hover-sort">{sortArrowSvg}</span>}
                              </span>
                              <div className="hs-col-resize-handle" onMouseDown={(e) => onProductResizeStart('category', e)} onClick={(e) => e.stopPropagation()} />
                            </th>
                            {/* Unit Price */}
                            <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleProductSort('unit_price')}>
                              <span className="hs-th-content">
                                Unit Price
                                {productsSort.field === 'unit_price' && <span className="hs-sort-arrow">{productsSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                                {productsSort.field !== 'unit_price' && <span className="hs-th-hover-sort">{sortArrowSvg}</span>}
                              </span>
                              <div className="hs-col-resize-handle" onMouseDown={(e) => onProductResizeStart('unit_price', e)} onClick={(e) => e.stopPropagation()} />
                            </th>
                            {/* Unit */}
                            <th className="hs-th-resizable">
                              <span className="hs-th-content">Unit</span>
                              <div className="hs-col-resize-handle" onMouseDown={(e) => onProductResizeStart('unit', e)} onClick={(e) => e.stopPropagation()} />
                            </th>
                            {/* Status */}
                            <th className="hs-th-resizable">
                              <span className="hs-th-content">Status</span>
                              <div className="hs-col-resize-handle" onMouseDown={(e) => onProductResizeStart('status', e)} onClick={(e) => e.stopPropagation()} />
                            </th>
                            {/* Created */}
                            <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleProductSort('created_at')}>
                              <span className="hs-th-content">
                                Created
                                {productsSort.field === 'created_at' && <span className="hs-sort-arrow">{productsSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                                {productsSort.field !== 'created_at' && <span className="hs-th-hover-sort">{sortArrowSvg}</span>}
                              </span>
                              <div className="hs-col-resize-handle" onMouseDown={(e) => onProductResizeStart('created_at', e)} onClick={(e) => e.stopPropagation()} />
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {productsLoading && productsData.length === 0 ? (
                            <tr><td colSpan={8} className="hs-loading-cell"><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}><DotLottieReact src="/loading.lottie" loop autoplay style={{ width: 80, height: 80 }} /><span style={{ fontSize: '0.82rem', color: '#7c98b6', marginTop: 8 }}>Loading products...</span></div></td></tr>
                          ) : productsData.length === 0 ? (
                            <tr><td colSpan={8} className="hs-empty-cell">No products found</td></tr>
                          ) : sorted.map((p) => (
                            <tr
                              key={p.id}
                              className={`hs-row${productsSelectedRows.has(p.id) ? ' selected' : ''}${previewProduct?.id === p.id ? ' is-previewed' : ''}`}
                            >
                              <td className="hs-td-check">
                                <input
                                  type="checkbox"
                                  checked={productsSelectedRows.has(p.id)}
                                  onChange={() => toggleProductRow(p.id)}
                                />
                              </td>
                              {/* Name — frozen */}
                              <td className="hs-td-name" style={{ position: 'sticky', left: 40, zIndex: 4, boxShadow: '2px 0 4px rgba(0,0,0,0.06)', background: productsSelectedRows.has(p.id) ? '#eef2ff' : '#fff' }}>
                                <div className="hs-name-cell">
                                  <div className="hs-avatar" style={{ background: '#7c3aed' }}>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m7.5 4.27 9 5.15"/><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/></svg>
                                  </div>
                                  <span className="hs-name-link">
                                    {p.name || <span className="cell-empty">--</span>}
                                  </span>
                                  <button className="hs-preview-btn" onClick={(e) => { e.stopPropagation(); setTableSettingsOpen(false); setPreviewProduct(p) }}>Preview</button>
                                </div>
                              </td>
                              <td style={{ color: '#516f90', fontFamily: 'monospace', fontSize: '13px' }}>{p.sku || <span className="cell-empty">--</span>}</td>
                              <td style={{ color: '#516f90' }}>{p.category || <span className="cell-empty">--</span>}</td>
                              <td style={{ fontWeight: 500, color: '#33475b' }}>{currencyFormatter.format(p.unit_price)}</td>
                              <td style={{ color: '#516f90' }}>{p.unit || <span className="cell-empty">--</span>}</td>
                              <td>
                                <span className="hs-status-badge" style={{
                                  background: p.is_active ? '#e4f7e8' : '#f5f8fa',
                                  color: p.is_active ? '#1976d2' : '#7c98b6',
                                }}>
                                  {p.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td style={{ color: '#516f90', fontSize: '13px' }}>{p.created_at ? formatDatePref(p.created_at) : <span className="cell-empty">--</span>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )
                  })()}
                </div>

                {/* Row 6: Pagination */}
                <div className="hs-pagination">
                  <nav className="hs-paginator" aria-label="Pagination">
                    <button
                      className="hs-paginator-btn"
                      disabled={productsPage <= 1}
                      onClick={() => setProductsPage((p) => p - 1)}
                      aria-label="Previous page"
                    >
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95"/></svg>
                      <span>Prev</span>
                    </button>
                    {(() => {
                      const totalPages = productsPages
                      if (totalPages <= 7) {
                        const pages: number[] = []
                        for (let i = 1; i <= totalPages; i++) pages.push(i)
                        return pages.map(p => (
                          <button key={p} className={`hs-paginator-num${p === productsPage ? ' active' : ''}`} onClick={() => setProductsPage(p)}>{p}</button>
                        ))
                      }
                      const items: React.ReactNode[] = []
                      const addPage = (p: number) => items.push(
                        <button key={p} className={`hs-paginator-num${p === productsPage ? ' active' : ''}`} onClick={() => setProductsPage(p)}>{p}</button>
                      )
                      const addEllipsis = (key: string) => items.push(
                        <span key={key} className="hs-paginator-ellipsis">...</span>
                      )
                      const shown = new Set<number>()
                      for (let i = 1; i <= Math.min(4, totalPages); i++) shown.add(i)
                      shown.add(totalPages)
                      for (let i = Math.max(1, productsPage - 1); i <= Math.min(totalPages, productsPage + 1); i++) shown.add(i)
                      const sortedPages = [...shown].sort((a, b) => a - b)
                      for (let i = 0; i < sortedPages.length; i++) {
                        if (i > 0 && sortedPages[i] - sortedPages[i - 1] > 1) addEllipsis(`e${i}`)
                        addPage(sortedPages[i])
                      }
                      return items
                    })()}
                    <button
                      className="hs-paginator-btn"
                      disabled={productsPage >= productsPages}
                      onClick={() => setProductsPage((p) => p + 1)}
                      aria-label="Next page"
                    >
                      <span>Next</span>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M11 27.71c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62l9.48-9.48-9.48-9.47a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0L22.1 15.48c.45.45.45 1.17 0 1.62L11.81 27.39c-.22.22-.52.33-.81.33Z"/></svg>
                    </button>
                  </nav>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', marginLeft: 'auto', marginRight: 50 }}><select value={tablePageSize} onChange={e => { setTablePageSize(Number(e.target.value) as any); setProductsPage(1) }} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #e2e8f0', fontSize: '0.82rem' }}><option value={100}>100</option><option value={200}>200</option><option value={500}>500</option></select><span>per page</span></div>
                </div>
              </div>

              {/* Product preview sidebar */}
              {(previewProduct || previewProductClosing) && (() => {
                const prod = previewProduct
                return (
                  <div className={`hs-preview${previewProductClosing ? ' closing' : ''}`}>
                    <div className="hs-preview-header">
                      <strong className="hs-preview-name">{prod?.name}</strong>
                      <button className="hs-preview-close" onClick={closeProductPreview}><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg></button>
                    </div>
                    <div className="hs-preview-body">
                      <div className="hs-preview-hero">
                        <div className="hs-preview-avatar-lg" style={{ background: '#7c3aed' }}>
                          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="m7.5 4.27 9 5.15"/><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/></svg>
                        </div>
                        <div className="hs-preview-hero-info">
                          <h3>{prod?.name}</h3>
                          <span className="hs-status-badge" style={{ background: prod?.is_active ? '#e4f7e8' : '#f5f8fa', color: prod?.is_active ? '#1976d2' : '#7c98b6' }}>
                            {prod?.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>

                      <div className="hs-preview-actions">
                        <button className="hs-preview-action" title="Edit" onClick={() => {
                          closeProductPreview()
                          if (prod) { setCreateFormData({ name: prod.name, sku: prod.sku, category: prod.category, unit_price: prod.unit_price, unit: prod.unit, description: prod.description }); setCreateError(null); setShowCreateModal('product') }
                        }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                        <button className="hs-preview-action" title="Delete" style={{ color: '#d94c53' }} onClick={() => {
                          if (prod && confirm(`Delete product "${prod.name}"?`)) {
                            deleteProduct(prod.id).then(() => { closeProductPreview(); setProductsData(prev => prev.filter(p => p.id !== prod.id)); setProductsTotal(prev => prev - 1) }).catch(() => {})
                          }
                        }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                        </button>
                      </div>

                      <div className="hs-preview-section">
                        <h4>Product Details</h4>
                        <div className="hs-preview-field"><label>SKU</label><span style={{ fontFamily: 'monospace' }}>{prod?.sku || '--'}</span></div>
                        <div className="hs-preview-field"><label>Category</label><span>{prod?.category || '--'}</span></div>
                        <div className="hs-preview-field"><label>Unit Price</label><span className="hs-preview-money">{prod?.unit_price ? currencyFormatter.format(prod.unit_price) : '--'}</span></div>
                        <div className="hs-preview-field"><label>Unit</label><span>{prod?.unit || '--'}</span></div>
                        <div className="hs-preview-field"><label>Status</label><span>{prod?.is_active ? 'Active' : 'Inactive'}</span></div>
                        <div className="hs-preview-field"><label>Created</label><span>{prod?.created_at ? formatDatePref(prod.created_at) : '--'}</span></div>
                      </div>

                      {prod?.description && (
                        <div className="hs-preview-section">
                          <h4>Description</h4>
                          <p style={{ fontSize: '0.82rem', color: '#516f90', lineHeight: 1.5, margin: 0 }}>{prod.description}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })()}

              {/* Row 7: Settings Panel */}
              {tableSettingsOpen && (
                <div className="hs-settings-panel">
                  <div className="hs-settings-header">
                    <span className="hs-settings-title">Table settings</span>
                    <button className="hs-settings-close" onClick={() => setTableSettingsOpen(false)}>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                    </button>
                  </div>
                  <div className="hs-settings-body">
                    <div className="hs-settings-group">
                      <label className="hs-settings-label">
                        Pagination
                        <span className="hs-info-icon" title="Number of rows shown per page">
                          <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg>
                        </span>
                      </label>
                      {([100, 200, 500] as const).map(size => (
                        <label className="hs-settings-radio" key={size}>
                          <input type="radio" name="prodPageSize" checked={tablePageSize === size} onChange={() => { setTablePageSize(size); setProductsPage(1) }} />
                          <span className="hs-radio-indicator" />
                          <span>{size} per page</span>
                        </label>
                      ))}
                    </div>
                    <hr className="hs-settings-divider" />
                    <div className="hs-settings-group">
                      <label className="hs-settings-label">
                        Row height
                        <span className="hs-info-icon" title="Adjust the vertical spacing of table rows">
                          <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg>
                        </span>
                      </label>
                      {([
                        { value: 'small' as const, label: 'Compact' },
                        { value: 'medium' as const, label: 'Default' },
                        { value: 'large' as const, label: 'Comfortable' },
                      ]).map(opt => (
                        <label className="hs-settings-radio" key={opt.value}>
                          <input type="radio" name="prodRowHeight" checked={tableRowHeight === opt.value} onChange={() => setTableRowHeight(opt.value)} />
                          <span className="hs-radio-indicator" />
                          <span>{opt.label}</span>
                        </label>
                      ))}
                    </div>
                    <hr className="hs-settings-divider" />
                    <div className="hs-settings-group">
                      <div className="hs-settings-toggle-row">
                        <label className="hs-settings-label">
                          Zebra striping
                          <span className="hs-info-icon" title="Alternate row background colors for easier reading">
                            <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg>
                          </span>
                        </label>
                        <button
                          className={`hs-toggle${tableZebraStriping ? ' on' : ''}`}
                          onClick={() => setTableZebraStriping(!tableZebraStriping)}
                          role="switch"
                          aria-checked={tableZebraStriping}
                        >
                          <span className="hs-toggle-thumb" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ========== Quotes View ========== */}
        {activeView === 'quotes' && (() => {
          const quotesSearchLower = quotesSearch.toLowerCase()
          const filteredQuotes = quotesData.filter(q => {
            if (quotesSearch && !(
              (q.title && q.title.toLowerCase().includes(quotesSearchLower)) ||
              (q.status && q.status.toLowerCase().includes(quotesSearchLower)) ||
              (q.unified_client_id && String(q.unified_client_id).toLowerCase().includes(quotesSearchLower))
            )) return false
            return true
          })
          // Client-side sort
          const sortedQuotes = [...filteredQuotes].sort((a, b) => {
            const f = quotesSort.field
            let va: any = (a as any)[f]
            let vb: any = (b as any)[f]
            if (va == null) va = ''
            if (vb == null) vb = ''
            if (typeof va === 'string') va = va.toLowerCase()
            if (typeof vb === 'string') vb = vb.toLowerCase()
            if (va < vb) return quotesSort.order === 'ASC' ? -1 : 1
            if (va > vb) return quotesSort.order === 'ASC' ? 1 : -1
            return 0
          })
          const quotesPageCount = Math.max(1, Math.ceil(quotesTotal / tablePageSize))
          const toggleQuoteSort = (field: string) => {
            setQuotesSort(prev => prev.field === field ? { field, order: prev.order === 'ASC' ? 'DESC' : 'ASC' } : { field, order: field === 'title' ? 'ASC' : 'DESC' })
            setQuotesPage(1)
          }
          const toggleAllQuoteRows = () => {
            if (quotesSelectedRows.size === sortedQuotes.length) {
              setQuotesSelectedRows(new Set())
            } else {
              setQuotesSelectedRows(new Set(sortedQuotes.map(q => q.id)))
            }
          }
          const toggleQuoteRow = (id: number) => {
            setQuotesSelectedRows(prev => {
              const next = new Set(prev)
              next.has(id) ? next.delete(id) : next.add(id)
              return next
            })
          }
          const onQuotesResizeStart = (col: string, e: React.MouseEvent) => {
            e.preventDefault()
            e.stopPropagation()
            const startX = e.clientX
            const startW = (quotesColWidths as any)[col] || 120
            const onMove = (ev: MouseEvent) => {
              const diff = ev.clientX - startX
              setQuotesColWidths(prev => ({ ...prev, [col]: Math.max(60, startW + diff) }))
            }
            const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp) }
            document.addEventListener('mousemove', onMove)
            document.addEventListener('mouseup', onUp)
          }
          const QUOTE_STATUS_COLORS: Record<string, { bg: string; fg: string }> = {
            draft: { bg: '#f5f8fa', fg: '#516f90' },
            sent: { bg: '#e8f0fe', fg: '#2e60e0' },
            approved: { bg: '#e4f7e8', fg: '#0b8043' },
            rejected: { bg: '#fdedee', fg: '#d94c53' },
            expired: { bg: '#fdedee', fg: '#d94c53' },
          }
          const sortArrowSvg = <svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M29.38 21.96a1.147 1.147 0 0 0-1.617 0l-3.85 3.852V5.714a1.143 1.143 0 0 0-2.286 0v20.099l-3.853-3.852a1.144 1.144 0 0 0-1.615 1.617l5.803 5.802a1.143 1.143 0 0 0 1.616 0l5.802-5.802a1.144 1.144 0 0 0 0-1.617M15.84 8.422 10.039 2.62a1.145 1.145 0 0 0-1.616 0L2.62 8.422a1.144 1.144 0 0 0 1.617 1.617l3.85-3.851v20.098a1.143 1.143 0 0 0 2.286 0V6.187l3.853 3.852a1.14 1.14 0 0 0 1.616-.001 1.14 1.14 0 0 0-.001-1.616"/></svg>
          return (
          <div className="hs-contacts">
            {/* ROW 1: Topbar */}
            <div className="hs-topbar">
              <div className="hs-topbar-left">
                {/* Object switcher */}
                <div className="hs-obj-switcher-wrap" ref={objSwitcherRef}>
                  <button className={`hs-obj-switcher${objSwitcherOpen ? ' open' : ''}`} onClick={() => { setObjSwitcherOpen(!objSwitcherOpen); setObjSwitcherSearch('') }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M16 13H8"/><path d="M16 17H8"/><path d="M10 9H8"/></svg>
                    <span>Quotes</span>
                    <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                  </button>
                  {objSwitcherOpen && (
                    <div className="hs-obj-panel">
                      <div className="hs-obj-search-wrap">
                        <svg className="hs-obj-search-icon" width="13" height="13" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                        <input
                          className="hs-obj-search"
                          placeholder="Search"
                          value={objSwitcherSearch}
                          onChange={(e) => setObjSwitcherSearch(e.target.value)}
                          autoFocus
                        />
                      </div>
                      <ul className="hs-obj-list" role="listbox">
                        {[
                          { label: 'Prospecting', view: 'prospecting' as ViewMode },
                          { label: 'Deals', view: 'deals' as ViewMode },
                          { label: 'Forecasts', view: 'forecasts' as ViewMode },
                          { label: 'Target Accounts', view: 'target-accounts' as ViewMode },
                          { label: 'Activity Feed', view: 'activity-feed' as ViewMode },
                          { label: 'Documents', view: 'sales-documents' as ViewMode },
                          { label: 'Meetings', view: 'meetings-scheduler' as ViewMode },
                          { label: 'Coaching Playlists', view: 'coaching-playlists' as ViewMode },
                          { label: 'Payments', view: 'payments' as ViewMode },
                          { label: 'Quotes', view: 'quotes' as ViewMode },
                          { label: 'Products', view: 'products' as ViewMode },
                        ]
                          .filter(item => !objSwitcherSearch || item.label.toLowerCase().includes(objSwitcherSearch.toLowerCase()))
                          .map(item => (
                            <li
                              key={item.view}
                              className={`hs-obj-item${item.view === activeView ? ' active' : ''}`}
                              role="option"
                              aria-selected={item.view === activeView}
                              onClick={() => {
                                setActiveView(item.view)
                                setObjSwitcherOpen(false)
                                setObjSwitcherSearch('')
                              }}
                            >
                              {item.label}
                            </li>
                          ))
                        }
                      </ul>
                    </div>
                  )}
                </div>
                <div className="hs-topbar-sep" />
                {/* View tabs */}
                <div className="hs-view-tabs">
                  {[
                    { key: '', label: 'All quotes' },
                    { key: 'draft', label: 'Draft' },
                    { key: 'sent', label: 'Sent' },
                    { key: 'approved', label: 'Approved' },
                    { key: 'rejected', label: 'Rejected' },
                    { key: 'expired', label: 'Expired' },
                  ].map(tab => {
                    const isActive = quotesStatusFilter === tab.key
                    return (
                      <div
                        key={tab.key}
                        className={`hs-view-tab${isActive ? ' active' : ''}`}
                        onClick={() => { setQuotesStatusFilter(tab.key); setQuotesPage(1) }}
                      >
                        <span className="hs-tab-label">{tab.label}</span>
                        {isActive && quotesTotal > 0 && <span className="hs-tab-count">{quotesTotal.toLocaleString()}</span>}
                        {isActive && (
                          <span className="hs-tab-kebab" onClick={(e) => { e.stopPropagation(); setTabEditOpen(!tabEditOpen) }}>
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>
                          </span>
                        )}
                        {tab.key !== '' && <span className="hs-tab-x" onClick={(e) => { e.stopPropagation(); setQuotesStatusFilter(''); setQuotesPage(1) }}>&times;</span>}
                        {isActive && tabEditOpen && (
                          <div className="hs-tab-edit-dropdown">
                            <button className="hs-tab-edit-item" disabled>Rename</button>
                            <button className="hs-tab-edit-item">Clone</button>
                            <button className="hs-tab-edit-item" disabled>Manage Sharing</button>
                          </div>
                        )}
                      </div>
                    )
                  })}
                  <div className="hs-topbar-sep" />
                  <div className="hs-view-add-wrap" style={{ position: 'relative', display: 'inline-flex', alignSelf: 'center' }}>
                    <button className="hs-quick-add-btn" title="Create or add view" type="button" aria-label="Create or add view">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                    </button>
                  </div>
                </div>
              </div>
              <div className="hs-topbar-right">
                {/* Create quote — direct action (Import removed) */}
                <button className="hs-add-contacts-btn" onClick={() => { setCreateFormData({}); setCreateError(null); setShowCreateModal('quote') }}>
                  Create quote
                </button>
              </div>
            </div>

            {/* ROW 2: Toolbar */}
            <div className="hs-toolbar">
              <div className="hs-toolbar-left">
                {/* Search */}
                <div className="hs-search-wrap">
                  <svg className="hs-search-icon" width="14" height="14" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M28.14 29.29c-.29 0-.58-.11-.81-.33l-7.58-7.58a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l7.58 7.58a1.14 1.14 0 0 1-.81 1.95"/><path d="M13.29 24.71c-6.3 0-11.43-5.13-11.43-11.43S6.98 1.86 13.29 1.86s11.43 5.13 11.43 11.43-5.13 11.43-11.43 11.43Zm0-20.57c-5.04 0-9.14 4.1-9.14 9.14s4.1 9.14 9.14 9.14 9.14-4.1 9.14-9.14-4.1-9.14-9.14-9.14"/></svg>
                  <input className="hs-search-input" placeholder="Search quotes" value={quotesSearch} onChange={(e) => setQuotesSearch(e.target.value)} />
                  {quotesSearch && <button className="hs-search-clear" onClick={() => setQuotesSearch('')}>&times;</button>}
                </div>

                <div className="hs-tb-sep" />

                {/* View type + settings button group */}
                <div className="hs-viewtype-group">
                  <div className="hs-viewtype-wrap">
                    <button className="hs-tb-btn hs-viewtype-btn">
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                      <span>Table</span>
                      <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                    </button>
                  </div>
                  <button
                    className={`hs-tb-btn hs-settings-btn${tableSettingsOpen ? ' active' : ''}`}
                    onClick={() => setTableSettingsOpen(!tableSettingsOpen)}
                    title={tableSettingsOpen ? 'Close settings' : 'Table settings'}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                  </button>
                </div>

                <div className="hs-tb-sep" />

                {/* Edit columns */}
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Edit columns">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="18" rx="1"/><rect x="14" y="3" width="7" height="18" rx="1"/></svg>
                  <span>Edit columns</span>
                </button>

                <div className="hs-tb-sep" />

                {/* Filters */}
                <button className={`hs-tb-btn hs-tb-btn-bordered${quickFiltersOpen ? ' hs-tb-btn-pressed' : ''}`} title="Filters" onClick={() => setQuickFiltersOpen(!quickFiltersOpen)}>
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M30.71 16.86H13.57a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28M6.71 16.86H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M10.14 20.29c-2.52 0-4.57-2.05-4.57-4.57s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M6.71 6.57H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M30.71 6.57H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M18.14 6.57H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 10C19.05 10 17 7.95 17 5.43S19.05.86 21.57.86s4.57 2.05 4.57 4.57S24.09 10 21.57 10m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M30.71 27.14H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M18.14 27.14H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 30.57C19.05 30.57 17 28.52 17 26s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29"/></svg>
                  <span>Filters</span>
                </button>

                <div className="hs-tb-sep" />

                {/* Export */}
                <button
                  className="hs-tb-btn hs-tb-btn-bordered"
                  title="Export current view as CSV"
                  onClick={() => {
                    const headers = ['Title', 'Status', 'Total', 'Deal ID', 'Contact ID', 'Expires', 'Created']
                    const escCsv = (v: unknown) => { const s = String(v ?? ''); return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s }
                    const rows = sortedQuotes.map(q => [q.title, q.status, q.total, q.deal_id || '', q.unified_client_id || '', q.expiration_date || '', q.created_at || ''].map(escCsv).join(','))
                    const csv = [headers.join(','), ...rows].join('\n')
                    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
                    const url = URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = `quotes-export-${new Date().toISOString().slice(0, 10)}.csv`
                    document.body.appendChild(a)
                    a.click()
                    document.body.removeChild(a)
                    URL.revokeObjectURL(url)
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  <span>Export</span>
                </button>
              </div>

              <div className="hs-toolbar-right">
                <div className="hs-tb-sep" />
                {/* Clone */}
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Clone view">
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M25.86 29.29H12.15c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-1.89 1.54-3.43 3.43-3.43h13.71c1.89 0 3.43 1.54 3.43 3.43v13.71c0 1.89-1.54 3.43-3.43 3.43M12.14 11c-.63 0-1.14.51-1.14 1.14v13.71c0 .63.51 1.14 1.14 1.14h13.71c.63 0 1.14-.51 1.14-1.14V12.14c0-.63-.51-1.14-1.14-1.14z"/><path d="M5.29 22.43c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43H19c1.89 0 3.43 1.54 3.43 3.43a1.14 1.14 0 1 1-2.28 0c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14V19c0 .63.51 1.14 1.14 1.14a1.14 1.14 0 1 1 0 2.28Z"/></svg>
                </button>
                {/* Save */}
                <button className="hs-tb-btn hs-tb-btn-bordered disabled" disabled title="Save view">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                </button>
              </div>
            </div>

            {/* ROW 3: Quick Filters */}
            {quickFiltersOpen && <div className="hs-quick-filters">
              {/* Status cycle toggle */}
              <button
                className={`hs-qf-btn hs-qf-trigger${quotesStatusFilter ? ' has-value' : ''}`}
                onClick={() => {
                  const vals = ['', 'draft', 'sent', 'approved', 'rejected', 'expired']
                  const idx = vals.indexOf(quotesStatusFilter)
                  setQuotesStatusFilter(vals[(idx + 1) % vals.length])
                  setQuotesPage(1)
                }}
              >
                <span>{quotesStatusFilter ? (quotesStatusFilter.charAt(0).toUpperCase() + quotesStatusFilter.slice(1)) : 'Status'}</span>
                {quotesStatusFilter && (
                  <span className="hs-qf-clear" onClick={(e) => { e.stopPropagation(); setQuotesStatusFilter(''); setQuotesPage(1) }}>&times;</span>
                )}
                {!quotesStatusFilter && <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>}
              </button>

              {/* Sort by */}
              <button
                className={`hs-qf-btn hs-qf-trigger${quotesSort.field !== 'created_at' ? ' has-value' : ''}`}
                onClick={() => {
                  const fields = ['created_at', 'title', 'total', 'expiration_date', 'status']
                  const idx = fields.indexOf(quotesSort.field)
                  const next = fields[(idx + 1) % fields.length]
                  setQuotesSort({ field: next, order: next === 'title' ? 'ASC' : 'DESC' })
                  setQuotesPage(1)
                }}
              >
                <span>Sort: {{ created_at: 'Created', title: 'Title', total: 'Total', expiration_date: 'Expires', status: 'Status' }[quotesSort.field] || 'Created'}</span>
                <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
              </button>

              <div className="hs-qf-sep" />

              {/* +More */}
              <button className="hs-qf-btn hs-qf-more">
                <svg width="10" height="10" viewBox="0 0 32 32" fill="currentColor"><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V3a1.14 1.14 0 1 1 2.28 0v25.14c0 .63-.51 1.14-1.14 1.14Z"/><path d="M28.14 16.71H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28"/></svg>
                <span>More</span>
              </button>
            </div>}

            {/* ROW 4+5+6: Content area */}
            <div className={`hs-content${previewQuote ? ' with-preview' : ''}${tableSettingsOpen ? ' with-settings' : ''}`}>
              <div className="hs-table-area">
                {/* ROW 4: Selection bar */}
                {quotesSelectedRows.size > 0 && (
                  <div className="hs-selection-bar">
                    <span className="hs-sel-count">{quotesSelectedRows.size} selected</span>
                    <div className="hs-sel-sep" />
                    <button className="hs-sel-btn">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                      Edit
                    </button>
                    <button className="hs-sel-btn">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                      Assign
                    </button>
                    <button className="hs-sel-btn" style={{ color: '#dc2626' }} onClick={() => {
                      if (confirm(`Delete ${quotesSelectedRows.size} quote(s)?`)) {
                        Promise.all([...quotesSelectedRows].map(id => deleteQuote(id))).then(() => {
                          setQuotesData(prev => prev.filter(q => !quotesSelectedRows.has(q.id)))
                          setQuotesTotal(prev => prev - quotesSelectedRows.size)
                          setQuotesSelectedRows(new Set())
                        }).catch(() => {})
                      }
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                      Delete
                    </button>
                    <button className="hs-sel-btn">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
                      Create tasks
                    </button>
                    <div className="hs-sel-sep" />
                    <button className="hs-sel-btn">More</button>
                    <button className="hs-sel-close" onClick={() => setQuotesSelectedRows(new Set())} title="Deselect all">&times;</button>
                  </div>
                )}

                {/* ROW 5: Data table */}
                <div className="hs-table-scroll">
                  <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                      <col style={{ width: 40 }} />
                      <col style={{ width: quotesColWidths.title }} />
                      <col style={{ width: quotesColWidths.status }} />
                      <col style={{ width: quotesColWidths.total }} />
                      <col style={{ width: quotesColWidths.deal }} />
                      <col style={{ width: quotesColWidths.client }} />
                      <col style={{ width: quotesColWidths.expires }} />
                      <col style={{ width: quotesColWidths.created }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th className="hs-th-check">
                          <input
                            type="checkbox"
                            checked={sortedQuotes.length > 0 && quotesSelectedRows.size === sortedQuotes.length}
                            onChange={toggleAllQuoteRows}
                          />
                        </th>
                        {/* Title - frozen */}
                        <th
                          className="hs-th-resizable hs-th-sortable hs-th-frozen"
                          style={{ position: 'sticky', left: 40, top: 0, zIndex: 15, background: '#f8fafc', boxShadow: '2px 0 4px rgba(0,0,0,0.06)' }}
                          onClick={() => toggleQuoteSort('title')}
                        >
                          <span className="hs-th-content">
                            Title
                            {quotesSort.field === 'title' && <span className="hs-sort-arrow">{quotesSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                            {quotesSort.field !== 'title' && <span className="hs-th-hover-sort">{sortArrowSvg}</span>}
                          </span>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onQuotesResizeStart('title', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                        {/* Status */}
                        <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleQuoteSort('status')}>
                          <span className="hs-th-content">
                            Status
                            {quotesSort.field === 'status' && <span className="hs-sort-arrow">{quotesSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                            {quotesSort.field !== 'status' && <span className="hs-th-hover-sort">{sortArrowSvg}</span>}
                          </span>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onQuotesResizeStart('status', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                        {/* Total */}
                        <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleQuoteSort('total')}>
                          <span className="hs-th-content">
                            Total
                            {quotesSort.field === 'total' && <span className="hs-sort-arrow">{quotesSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                            {quotesSort.field !== 'total' && <span className="hs-th-hover-sort">{sortArrowSvg}</span>}
                          </span>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onQuotesResizeStart('total', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                        {/* Deal */}
                        <th className="hs-th-resizable">
                          <span className="hs-th-content">Deal</span>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onQuotesResizeStart('deal', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                        {/* Contact */}
                        <th className="hs-th-resizable">
                          <span className="hs-th-content">Contact</span>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onQuotesResizeStart('client', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                        {/* Expires */}
                        <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleQuoteSort('expiration_date')}>
                          <span className="hs-th-content">
                            Expires
                            {quotesSort.field === 'expiration_date' && <span className="hs-sort-arrow">{quotesSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                            {quotesSort.field !== 'expiration_date' && <span className="hs-th-hover-sort">{sortArrowSvg}</span>}
                          </span>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onQuotesResizeStart('expires', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                        {/* Created */}
                        <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleQuoteSort('created_at')}>
                          <span className="hs-th-content">
                            Created
                            {quotesSort.field === 'created_at' && <span className="hs-sort-arrow">{quotesSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                            {quotesSort.field !== 'created_at' && <span className="hs-th-hover-sort">{sortArrowSvg}</span>}
                          </span>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onQuotesResizeStart('created', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {quotesLoading && sortedQuotes.length === 0 ? (
                        <tr><td colSpan={8} className="hs-loading-cell"><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}><DotLottieReact src="/loading.lottie" loop autoplay style={{ width: 80, height: 80 }} /><span style={{ fontSize: '0.82rem', color: '#7c98b6', marginTop: 8 }}>Loading quotes...</span></div></td></tr>
                      ) : sortedQuotes.length === 0 ? (
                        <tr><td colSpan={8} className="hs-empty-cell"><div><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block', margin: '0 auto 12px' }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M16 13H8"/><path d="M16 17H8"/><path d="M10 9H8"/></svg><div style={{ fontWeight: 600, color: '#33475b', marginBottom: 4 }}>No quotes found</div><div style={{ fontSize: '0.82rem' }}>Create a quote to send pricing proposals to your clients.</div></div></td></tr>
                      ) : sortedQuotes.map((q) => {
                        const sc = QUOTE_STATUS_COLORS[q.status] || QUOTE_STATUS_COLORS.draft
                        return (
                          <tr
                            key={q.id}
                            className={`hs-row${quotesSelectedRows.has(q.id) ? ' selected' : ''}${previewQuote?.id === q.id ? ' is-previewed' : ''}`}
                          >
                            <td className="hs-td-check">
                              <input
                                type="checkbox"
                                checked={quotesSelectedRows.has(q.id)}
                                onChange={() => toggleQuoteRow(q.id)}
                              />
                            </td>
                            <td className="hs-td-name" style={{ position: 'sticky', left: 40, zIndex: 4, boxShadow: '2px 0 4px rgba(0,0,0,0.06)', background: quotesSelectedRows.has(q.id) ? '#eef2ff' : '#fff' }}>
                              <div className="hs-name-cell">
                                <div className="hs-avatar" style={{ background: sc.fg }}>
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>
                                </div>
                                <span className="hs-name-link">
                                  {q.title || <span className="cell-empty">--</span>}
                                </span>
                                <button className="hs-preview-btn" onClick={(e) => { e.stopPropagation(); setTableSettingsOpen(false); setPreviewQuote(q) }}>Preview</button>
                              </div>
                            </td>
                            <td>
                              <span className="status-chip" style={{ background: sc.bg, color: sc.fg }}>
                                {q.status.charAt(0).toUpperCase() + q.status.slice(1)}
                              </span>
                              {q.status === 'draft' && (
                                <button
                                  className="hs-topbar-icon-btn"
                                  title="Approve quote"
                                  style={{ color: '#1976d2', marginLeft: 6, verticalAlign: 'middle' }}
                                  onClick={() => {
                                    approveQuote(q.id).then((updated) => {
                                      setQuotesData((prev) => prev.map((qq) => qq.id === q.id ? updated : qq))
                                    }).catch(() => {})
                                  }}
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M20 6 9 17l-5-5"/></svg>
                                </button>
                              )}
                            </td>
                            <td style={{ fontWeight: 500, color: '#33475b' }}>
                              {q.total > 0 ? currencyFormatter.format(q.total) : <span className="cell-empty">--</span>}
                            </td>
                            <td>
                              {q.deal_id ? <span style={{ color: '#2563eb', cursor: 'pointer' }}>Deal #{q.deal_id}</span> : <span className="cell-empty">--</span>}
                            </td>
                            <td>
                              {q.unified_client_id ? <span style={{ color: '#2563eb', cursor: 'pointer' }}>{q.unified_client_id}</span> : <span className="cell-empty">--</span>}
                            </td>
                            <td style={{ color: q.expiration_date && new Date(q.expiration_date) < new Date() ? '#d94c53' : undefined }}>
                              {q.expiration_date ? formatDatePref(q.expiration_date) : <span className="cell-empty">--</span>}
                            </td>
                            <td>
                              {q.created_at ? formatDatePref(q.created_at) : <span className="cell-empty">--</span>}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>

                {/* ROW 6: Pagination */}
                <div className="hs-pagination">
                  <nav className="hs-paginator" aria-label="Pagination">
                    <button
                      className="hs-paginator-btn"
                      disabled={quotesPage <= 1}
                      onClick={() => setQuotesPage((p) => p - 1)}
                      aria-label="Previous page"
                    >
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95"/></svg>
                      <span>Prev</span>
                    </button>
                    {(() => {
                      if (quotesPageCount <= 7) {
                        const pages: number[] = []
                        for (let i = 1; i <= quotesPageCount; i++) pages.push(i)
                        return pages.map(p => (
                          <button key={p} className={`hs-paginator-num${p === quotesPage ? ' active' : ''}`} onClick={() => setQuotesPage(p)}>{p}</button>
                        ))
                      }
                      const items: React.ReactNode[] = []
                      const addPage = (p: number) => items.push(
                        <button key={p} className={`hs-paginator-num${p === quotesPage ? ' active' : ''}`} onClick={() => setQuotesPage(p)}>{p}</button>
                      )
                      const addEllipsis = (key: string) => items.push(
                        <span key={key} className="hs-paginator-ellipsis">...</span>
                      )
                      const shown = new Set<number>()
                      for (let i = 1; i <= Math.min(4, quotesPageCount); i++) shown.add(i)
                      shown.add(quotesPageCount)
                      for (let i = Math.max(1, quotesPage - 1); i <= Math.min(quotesPageCount, quotesPage + 1); i++) shown.add(i)
                      const sorted = [...shown].sort((a, b) => a - b)
                      for (let i = 0; i < sorted.length; i++) {
                        if (i > 0 && sorted[i] - sorted[i - 1] > 1) addEllipsis(`e${i}`)
                        addPage(sorted[i])
                      }
                      return items
                    })()}
                    <button
                      className="hs-paginator-btn"
                      disabled={quotesPage >= quotesPageCount}
                      onClick={() => setQuotesPage((p) => p + 1)}
                      aria-label="Next page"
                    >
                      <span>Next</span>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M11 27.71c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62l9.48-9.48-9.48-9.47a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0L22.1 15.48c.45.45.45 1.17 0 1.62L11.81 27.39c-.22.22-.52.33-.81.33Z"/></svg>
                    </button>
                  </nav>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', marginLeft: 'auto', marginRight: 50 }}><select value={tablePageSize} onChange={e => { setTablePageSize(Number(e.target.value) as any); setQuotesPage(1) }} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #e2e8f0', fontSize: '0.82rem' }}><option value={100}>100</option><option value={200}>200</option><option value={500}>500</option></select><span>per page</span></div>
                </div>
              </div>

              {/* Quote preview sidebar */}
              {(previewQuote || previewQuoteClosing) && (() => {
                const q = previewQuote
                const QUOTE_STATUS_COLORS_P: Record<string, { bg: string; fg: string }> = {
                  draft: { bg: '#f5f8fa', fg: '#516f90' },
                  sent: { bg: '#e8f0fe', fg: '#2e60e0' },
                  approved: { bg: '#e4f7e8', fg: '#0b8043' },
                  rejected: { bg: '#fdedee', fg: '#d94c53' },
                  expired: { bg: '#fdedee', fg: '#d94c53' },
                }
                const sc = QUOTE_STATUS_COLORS_P[q?.status ?? 'draft'] || QUOTE_STATUS_COLORS_P.draft
                return (
                  <div className={`hs-preview${previewQuoteClosing ? ' closing' : ''}`}>
                    <div className="hs-preview-header">
                      <strong className="hs-preview-name">{q?.title}</strong>
                      <button className="hs-preview-close" onClick={closeQuotePreview}><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg></button>
                    </div>
                    <div className="hs-preview-body">
                      <div className="hs-preview-hero">
                        <div className="hs-preview-avatar-lg" style={{ background: sc.fg }}>
                          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>
                        </div>
                        <div className="hs-preview-hero-info">
                          <h3>{q?.title}</h3>
                          <span className="hs-status-badge" style={{ background: sc.bg, color: sc.fg }}>
                            {q?.status?.charAt(0).toUpperCase() + (q?.status?.slice(1) ?? '')}
                          </span>
                        </div>
                      </div>

                      <div className="hs-preview-actions">
                        <button className="hs-preview-action" title="Edit" onClick={() => {
                          closeQuotePreview()
                          if (q) { setCreateFormData({ title: q.title, status: q.status, total: q.total, deal_id: q.deal_id, unified_client_id: q.unified_client_id, expiration_date: q.expiration_date }); setCreateError(null); setShowCreateModal('quote') }
                        }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                        <button className="hs-preview-action" title="Delete" style={{ color: '#d94c53' }} onClick={() => {
                          if (q && confirm(`Delete quote "${q.title}"?`)) {
                            deleteQuote(q.id).then(() => { closeQuotePreview(); setQuotesData(prev => prev.filter(qq => qq.id !== q.id)); setQuotesTotal(prev => prev - 1) }).catch(() => {})
                          }
                        }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                        </button>
                      </div>

                      <div className="hs-preview-section">
                        <h4>Quote Details</h4>
                        <div className="hs-preview-field"><label>Status</label><span><span className="hs-status-badge" style={{ background: sc.bg, color: sc.fg, fontSize: '0.72rem' }}>{q?.status?.charAt(0).toUpperCase() + (q?.status?.slice(1) ?? '')}</span></span></div>
                        <div className="hs-preview-field"><label>Total</label><span className="hs-preview-money">{q?.total ? currencyFormatter.format(q.total) : '--'}</span></div>
                        <div className="hs-preview-field"><label>Deal</label><span>{q?.deal_id ? `Deal #${q.deal_id}` : '--'}</span></div>
                        <div className="hs-preview-field"><label>Contact</label><span>{q?.unified_client_id || '--'}</span></div>
                        <div className="hs-preview-field"><label>Expires</label><span style={{ color: q?.expiration_date && new Date(q.expiration_date) < new Date() ? '#d94c53' : undefined }}>{q?.expiration_date ? formatDatePref(q.expiration_date) : '--'}</span></div>
                        <div className="hs-preview-field"><label>Created</label><span>{q?.created_at ? formatDatePref(q.created_at) : '--'}</span></div>
                      </div>
                    </div>
                  </div>
                )
              })()}

              {/* ROW 7: Settings Panel */}
              {tableSettingsOpen && (
                <div className="hs-settings-panel">
                  <div className="hs-settings-header">
                    <span className="hs-settings-title">Table settings</span>
                    <button className="hs-settings-close" onClick={() => setTableSettingsOpen(false)}>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                    </button>
                  </div>
                  <div className="hs-settings-body">
                    <div className="hs-settings-group">
                      <label className="hs-settings-label">
                        Pagination
                        <span className="hs-info-icon" title="Number of rows shown per page">
                          <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg>
                        </span>
                      </label>
                      {([100, 200, 500] as const).map(size => (
                        <label className="hs-settings-radio" key={size}>
                          <input type="radio" name="quotesPageSize" checked={tablePageSize === size} onChange={() => { setTablePageSize(size); setQuotesPage(1) }} />
                          <span className="hs-radio-indicator" />
                          <span>{size} per page</span>
                        </label>
                      ))}
                    </div>
                    <hr className="hs-settings-divider" />
                    <div className="hs-settings-group">
                      <label className="hs-settings-label">
                        Row height
                        <span className="hs-info-icon" title="Adjust the vertical spacing of table rows">
                          <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg>
                        </span>
                      </label>
                      {([
                        { value: 'small' as const, label: 'Compact' },
                        { value: 'medium' as const, label: 'Default' },
                        { value: 'large' as const, label: 'Comfortable' },
                      ]).map(opt => (
                        <label className="hs-settings-radio" key={opt.value}>
                          <input type="radio" name="quotesRowHeight" checked={tableRowHeight === opt.value} onChange={() => setTableRowHeight(opt.value)} />
                          <span className="hs-radio-indicator" />
                          <span>{opt.label}</span>
                        </label>
                      ))}
                    </div>
                    <hr className="hs-settings-divider" />
                    <div className="hs-settings-group">
                      <div className="hs-settings-toggle-row">
                        <label className="hs-settings-label">
                          Zebra striping
                          <span className="hs-info-icon" title="Alternate row background colors for easier reading">
                            <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg>
                          </span>
                        </label>
                        <button
                          className={`hs-toggle${tableZebraStriping ? ' on' : ''}`}
                          onClick={() => setTableZebraStriping(!tableZebraStriping)}
                          role="switch"
                          aria-checked={tableZebraStriping}
                        >
                          <span className="hs-toggle-track">
                            <span className="hs-toggle-on">ON</span>
                            <span className="hs-toggle-off">OFF</span>
                            <span className="hs-toggle-handle" />
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          )
        })()}

        {/* ========== Team Feed (#/feed, legacy #/team) ========== */}
        {/* Feed is the primary team surface; the directory is embedded in the feed sidebar.
         * When impersonating, swap to the impersonated user's identity — name + email
         * + role — and DROP the photoUrl since prefsPhoto belongs to the actually-
         * authenticated user (so showing it under the impersonated person's name
         * would put Max's face above Ashley's name; bug reported 2026-05-03). The
         * left rail then falls back to initials via PersonAvatar's no-photo path. */}
        {(activeView === 'team' || activeView === 'feed') && (() => {
          const u = impersonating
            ? {
                email: impersonating.email,
                name: impersonating.name,
                role: impersonating.role,
                photoUrl: undefined,
              }
            : {
                email: currentUser?.email || 'mritchie@botpros.ai',
                name: currentUser?.name || '',
                role: currentUser?.role || 'user',
                photoUrl: prefsPhoto || undefined,
              }
          return (
            <FeedHubView
              currentUser={u}
              onOpenPerson={(emailOrId) => {
                setActiveViewRaw('profile')
                window.location.hash = `/profile/${encodeURIComponent(emailOrId)}`
              }}
            />
          )
        })()}

        {/* ========== Social: Profile (#/profile/:id) ========== */}
        {activeView === 'profile' && (() => {
          const segs = getHashSegments()
          const profileId = segs[1] ? decodeURIComponent(segs[1]) : (currentUser?.email || 'mritchie@botpros.ai')
          // Same admin gate as the Timesheets sidebar — keeps the FE
          // declaration of "who is admin" consistent across the app.
          const adminEmail = (currentUser?.email || '').toLowerCase()
          const adminRole = currentUser?.role || 'user'
          // Ashley / Office-role users carry is_admin=1 even though their
          // role string isn't literally "admin", so include the is_admin
          // flag from /api/me — otherwise they'd lose the HR Record jump.
          const profileIsAdmin = adminRole === 'admin' || adminRole === 'Super Admin' || currentUser?.isAdmin === 1 || ['mritchie@botpros.ai', 'agent@botpros.ai'].includes(adminEmail)
          return <ProfileHubView
            profileId={profileId}
            currentUserEmail={currentUser?.email || 'mritchie@botpros.ai'}
            isAdmin={profileIsAdmin}
            onSignAssignment={(assignmentId) => openHrSignModal(String(assignmentId), undefined, 'employee')}
            onMyPhotoChanged={(photoUrl) => {
              // Empty string = remove. Keep EVERY in-memory avatar source in
              // step (me-photo state + loaded hrProfile) so navAvatarSource
              // can't resurrect a removed/stale photo; prefsPhoto/localStorage
              // writes are self-only (display-only invariant while impersonating).
              if (photoUrl) {
                if (!impersonating) {
                  setPrefsPhoto(photoUrl)
                  prefsPhotoBaselineRef.current = photoUrl
                  persistPrefsPhoto(photoUrl, currentUser?.email)
                }
                setHrProfile(prev => prev ? { ...prev, photoUrl } : prev)
                setHrProfileBaseline(prev => prev ? { ...prev, photoUrl } : prev)
                setCurrentUser(prev => prev ? { ...prev, photoUrl, photoVersion: String(Date.now()) } : prev)
              } else {
                if (!impersonating) {
                  setPrefsPhoto(null)
                  prefsPhotoBaselineRef.current = null
                  persistPrefsPhoto(null, null)
                }
                setHrProfile(prev => prev ? { ...prev, profilePhotoKey: null, photoUrl: null } : prev)
                setHrProfileBaseline(prev => prev ? { ...prev, profilePhotoKey: null, photoUrl: null } : prev)
                setCurrentUser(prev => prev ? { ...prev, photoUrl: null, photoVersion: null } : prev)
              }
            }}
            onOpenHrRecord={(employeeId) => {
              // Switch to HR Center, target the Employees tab, and pass the
              // employeeId via the hash query so the deep-link parser inside the
              // HR Center mount logic auto-opens the detail. We set the hash
              // FIRST (so the parser fires) then the activeView is sourced
              // from the hash on next render.
              window.location.hash = `/hr-center?employee=${encodeURIComponent(employeeId)}`
            }}
            onOpenPerson={(emailOrId) => {
              window.location.hash = `/profile/${encodeURIComponent(emailOrId)}`
            }}
            onBack={() => {
              if (window.history.length > 1) window.history.back()
              else { setActiveView('feed'); window.location.hash = '/feed' }
            }}
          />
        })()}

        {/* ========== Email (#/email) ========== */}
        {activeView === 'email' && (
          <Suspense fallback={<div className="view-loading" />}>
            <Inbox
              userEmail={currentUser?.email || 'mritchie@botpros.ai'}
              userName={currentUser?.name || ''}
              onOpenCompose={(ctx) => { setComposeContext(ctx); setComposeOpen(true) }}
              GmailSearch={GmailSearch}
              ThreadCard={ThreadCard}
              MessageBubble={MessageBubble}
            />
          </Suspense>
        )}


        {/* ========== Dashboards View ========== */}
        {activeView === 'dashboards' && (
          <Suspense fallback={<div className="hs-contacts dashboard-modern" />}>
            <Dashboard
              dashboardData={dashboardData}
              dashboardLoading={dashboardLoading}
              dashboardError={dashboardError}
              savedDashboards={savedDashboards}
              dashboardLeaderboard={dashboardLeaderboard}
              dashboardOwnerOptions={dashboardOwnerOptions}
              dashboardRevenue={dashboardRevenue}
              dashboardFunnelStages={dashboardFunnelStages}
              dashboardActivity={dashboardActivity}
              dashboardWonLost={dashboardWonLost}
              dashboardSavedCount={dashboardSavedCount}
              dashboardDataQuality={dashboardDataQuality}
              dashboardView={dashboardView}
              setDashboardView={setDashboardView}
              dashboardOwnerFilter={dashboardOwnerFilter}
              setDashboardOwnerFilter={setDashboardOwnerFilter}
              reportDateRange={reportDateRange}
              setReportDateRange={setReportDateRange}
              reportWindow={reportWindow}
              setDashboardRefreshTick={setDashboardRefreshTick}
              summaryKPIs={summaryKPIs}
              setActiveView={setActiveView}
              setReportingTab={setReportingTab}
              setDealStageFilter={setDealStageFilter}
              currencyFormatter={currencyFormatter}
              getLeadStatusLabel={getLeadStatusLabel}
            />
          </Suspense>
        )}
        {activeView === 'dashboards' && false && (
          <div className="hs-contacts">
            <div className="hs-topbar">
              <div className="hs-topbar-left">
                <div style={{ display: 'flex', alignItems: 'center', padding: '0 12px', gap: '6px' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
                  <span style={{ fontWeight: 600, fontSize: '0.92rem', color: '#33475b' }}>Sales Dashboard</span>
                </div>
              </div>
              <div className="hs-topbar-right">
                <select value={reportDateRange} onChange={e => setReportDateRange(e.target.value as any)} style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.82rem', color: '#33475b', cursor: 'pointer', background: '#fff' }}>
                  <option value="this_month">This Month</option>
                  <option value="last_month">Last Month</option>
                  <option value="this_quarter">This Quarter</option>
                  <option value="this_year">This Year</option>
                </select>
              </div>
            </div>
            <div className="hs-content" style={{ padding: '20px', overflowY: 'auto' }}>
              {dashboardLoading ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#7c98b6' }}>Loading dashboard...</div>
              ) : (
                <>
                  {/* KPI Cards Row */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                    {[
                      { label: 'Total Revenue', value: currencyFormatter.format(summaryKPIs?.total_revenue ?? 0), sub: 'Closed won', color: '#2e7d32', bg: '#e8f5e9' },
                      { label: 'Deals Won', value: String(summaryKPIs?.deals_won ?? 0), sub: `${summaryKPIs?.win_rate ?? 0}% win rate`, color: '#1976d2', bg: '#e3f2fd' },
                      { label: 'Deals Lost', value: String(summaryKPIs?.deals_lost ?? 0), sub: 'Closed lost', color: '#d32f2f', bg: '#ffeaea' },
                      { label: 'Open Leads', value: String(summaryKPIs?.open_leads ?? 0), sub: 'In pipeline', color: '#ed6c02', bg: '#fff3e0' },
                      { label: 'Avg Deal Size', value: currencyFormatter.format(summaryKPIs?.avg_deal_size ?? 0), sub: 'Per deal', color: '#7b1fa2', bg: '#f3e5f5' },
                      { label: 'Activities', value: String(summaryKPIs?.total_activities ?? 0), sub: 'This period', color: '#0288d1', bg: '#e1f5fe' },
                      { label: 'Total Contacts', value: String(summaryKPIs?.total_clients ?? 0).replace(/\B(?=(\d{3})+(?!\d))/g, ','), sub: 'All time', color: '#33475b', bg: '#f1f5f9' },
                      { label: 'Total Deals', value: String(summaryKPIs?.total_deals ?? 0), sub: 'Created this period', color: '#00695c', bg: '#e0f2f1' },
                    ].map(kpi => (
                      <div key={kpi.label} style={{ background: '#fff', border: '1px solid #eaf0f6', borderRadius: '8px', padding: '16px', borderLeft: `4px solid ${kpi.color}` }}>
                        <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>{kpi.label}</div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 800, color: kpi.color }}>{kpi.value}</div>
                        <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '4px' }}>{kpi.sub}</div>
                      </div>
                    ))}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(440px, 1fr))', gap: '20px' }}>
                    {/* Deal Pipeline Funnel */}
                    <div style={{ background: '#fff', border: '1px solid #eaf0f6', borderRadius: '8px', padding: '20px' }}>
                      <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#33475b', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1976d2" strokeWidth="2"><polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46"/></svg>
                        Deal Pipeline Funnel
                      </h3>
                      {(dashboardData.funnel?.stages || []).length === 0 ? (
                        <div style={{ color: '#7c98b6', fontSize: '13px', padding: '12px 0' }}>No pipeline data</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {(dashboardData.funnel?.stages || []).filter((s: any) => s.deal_count > 0).map((stage: any, i: number) => {
                            const maxCount = Math.max(...(dashboardData.funnel?.stages || []).map((s: any) => s.deal_count || 0), 1)
                            const pct = Math.max((stage.deal_count / maxCount) * 100, 8)
                            const colors = ['#1976d2', '#2196f3', '#42a5f5', '#64b5f6', '#90caf9', '#bbdefb', '#e3f2fd']
                            return (
                              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{ width: '120px', fontSize: '0.75rem', fontWeight: 500, color: '#33475b', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={stage.stage_name}>{stage.stage_name}</div>
                                <div style={{ flex: 1, background: '#f1f5f9', borderRadius: '4px', height: '28px', position: 'relative', overflow: 'hidden' }}>
                                  <div style={{ width: `${pct}%`, height: '100%', background: colors[i % colors.length], borderRadius: '4px', transition: 'width 0.5s ease', display: 'flex', alignItems: 'center', paddingLeft: '8px' }}>
                                    <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#fff' }}>{stage.deal_count}</span>
                                  </div>
                                </div>
                                <div style={{ width: '70px', fontSize: '0.72rem', color: '#64748b', textAlign: 'right', flexShrink: 0 }}>{currencyFormatter.format(stage.total_value)}</div>
                              </div>
                            )
                          })}
                          <div style={{ borderTop: '1px solid #eaf0f6', marginTop: '8px', paddingTop: '8px', display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', fontWeight: 600, color: '#33475b' }}>
                            <span>Total: {dashboardData.funnel?.total ?? 0} deals</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Revenue Over Time (Bar Chart) */}
                    <div style={{ background: '#fff', border: '1px solid #eaf0f6', borderRadius: '8px', padding: '20px' }}>
                      <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#33475b', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2e7d32" strokeWidth="2"><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></svg>
                        Revenue Over Time
                      </h3>
                      {(dashboardData.revenue || []).length === 0 ? (
                        <div style={{ color: '#7c98b6', fontSize: '13px', padding: '12px 0' }}>No revenue data</div>
                      ) : (() => {
                        const data = dashboardData.revenue || []
                        const maxRev = Math.max(...data.map((r: any) => r.total_revenue || 0), 1)
                        return (
                          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: '180px', padding: '8px 0', borderBottom: '1px solid #eaf0f6' }}>
                            {data.slice(-12).map((row: any, i: number) => {
                              const h = Math.max((row.total_revenue / maxRev) * 160, 4)
                              return (
                                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                                  <div style={{ fontSize: '0.6rem', color: '#64748b', fontWeight: 500 }}>{row.deals_closed}</div>
                                  <div style={{ width: '100%', maxWidth: '36px', height: `${h}px`, background: 'linear-gradient(180deg, #2e7d32, #4caf50)', borderRadius: '3px 3px 0 0', transition: 'height 0.5s ease' }} title={`${row.period}: ${currencyFormatter.format(row.total_revenue)}`} />
                                  <div style={{ fontSize: '0.55rem', color: '#94a3b8', transform: 'rotate(-45deg)', transformOrigin: 'top left', whiteSpace: 'nowrap' }}>{row.period?.substring(5) || ''}</div>
                                </div>
                              )
                            })}
                          </div>
                        )
                      })()}
                    </div>

                    {/* Won vs Lost (Stacked bars) */}
                    <div style={{ background: '#fff', border: '1px solid #eaf0f6', borderRadius: '8px', padding: '20px' }}>
                      <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#33475b', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ed6c02" strokeWidth="2"><path d="M21.21 15.89A10 10 0 1 1 8 2.83"/><path d="M22 12A10 10 0 0 0 12 2v10z"/></svg>
                        Deals Won vs Lost
                      </h3>
                      {(dashboardData.wonLost || []).length === 0 ? (
                        <div style={{ color: '#7c98b6', fontSize: '13px', padding: '12px 0' }}>No deal data</div>
                      ) : (() => {
                        const data = dashboardData.wonLost || []
                        const maxVal = Math.max(...data.map((r: any) => (r.won || 0) + (r.lost || 0)), 1)
                        return (
                          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', height: '160px', padding: '8px 0' }}>
                            {data.slice(-12).map((row: any, i: number) => {
                              const wonH = Math.max(((row.won || 0) / maxVal) * 140, 2)
                              const lostH = Math.max(((row.lost || 0) / maxVal) * 140, 0)
                              return (
                                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0' }}>
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    {lostH > 0 && <div style={{ width: '18px', height: `${lostH}px`, background: '#ef5350', borderRadius: '3px 3px 0 0' }} title={`Lost: ${row.lost}`} />}
                                    <div style={{ width: '18px', height: `${wonH}px`, background: '#4caf50', borderRadius: lostH > 0 ? '0' : '3px 3px 0 0' }} title={`Won: ${row.won}`} />
                                  </div>
                                  <div style={{ fontSize: '0.55rem', color: '#94a3b8', marginTop: '4px' }}>{(row.period || '').substring(5)}</div>
                                </div>
                              )
                            })}
                          </div>
                        )
                      })()}
                      <div style={{ display: 'flex', gap: '16px', marginTop: '8px', fontSize: '0.72rem' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><span style={{ width: '10px', height: '10px', borderRadius: '2px', background: '#4caf50', display: 'inline-block' }} /> Won</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><span style={{ width: '10px', height: '10px', borderRadius: '2px', background: '#ef5350', display: 'inline-block' }} /> Lost</span>
                      </div>
                    </div>

                    {/* Activity by Type (Donut) */}
                    <div style={{ background: '#fff', border: '1px solid #eaf0f6', borderRadius: '8px', padding: '20px' }}>
                      <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#33475b', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7b1fa2" strokeWidth="2"><circle cx="12" cy="12" r="10"/></svg>
                        Activity Breakdown
                      </h3>
                      {(dashboardData.actByType || []).length === 0 ? (
                        <div style={{ color: '#7c98b6', fontSize: '13px', padding: '12px 0' }}>No activity data</div>
                      ) : (() => {
                        const data = dashboardData.actByType || []
                        const total = data.reduce((s: number, r: any) => s + (r.count || 0), 0)
                        const colors = ['#1976d2', '#2e7d32', '#ed6c02', '#d32f2f', '#7b1fa2', '#0288d1']
                        let cumPct = 0
                        const segments = data.map((r: any, i: number) => {
                          const pct = total > 0 ? (r.count / total) * 100 : 0
                          const start = cumPct
                          cumPct += pct
                          return { ...r, pct, start, color: colors[i % colors.length] }
                        })
                        const gradient = segments.map((s: any) => `${s.color} ${s.start}% ${s.start + s.pct}%`).join(', ')
                        return (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                            <div style={{ width: '140px', height: '140px', borderRadius: '50%', background: `conic-gradient(${gradient})`, position: 'relative', flexShrink: 0 }}>
                              <div style={{ position: 'absolute', top: '25%', left: '25%', width: '50%', height: '50%', borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', fontWeight: 700, color: '#33475b' }}>{total}</div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>
                              {segments.map((s: any, i: number) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.78rem' }}>
                                  <span style={{ width: '10px', height: '10px', borderRadius: '2px', background: s.color, display: 'inline-block', flexShrink: 0 }} />
                                  <span style={{ color: '#33475b', fontWeight: 500, flex: 1 }}>{s.activity_type}</span>
                                  <span style={{ color: '#64748b', fontWeight: 600 }}>{s.count}</span>
                                  <span style={{ color: '#94a3b8', fontSize: '0.7rem' }}>({s.pct.toFixed(1)}%)</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )
                      })()}
                    </div>

                    {/* Lead Funnel */}
                    <div style={{ background: '#fff', border: '1px solid #eaf0f6', borderRadius: '8px', padding: '20px' }}>
                      <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#33475b', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#00695c" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                        Lead Funnel
                      </h3>
                      <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                        {[
                          { label: 'Total Leads', value: dashboardData.leadFunnel?.total_leads ?? 0, color: '#1976d2' },
                          { label: 'Converted', value: dashboardData.leadFunnel?.converted_leads ?? 0, color: '#2e7d32' },
                          { label: 'Conv. Rate', value: `${dashboardData.leadFunnel?.conversion_rate ?? 0}%`, color: '#ed6c02' },
                        ].map(k => (
                          <div key={k.label} style={{ flex: 1, textAlign: 'center', padding: '12px', background: '#f8fafc', borderRadius: '6px' }}>
                            <div style={{ fontSize: '1.3rem', fontWeight: 700, color: k.color }}>{k.value}</div>
                            <div style={{ fontSize: '0.68rem', color: '#64748b', marginTop: '4px', fontWeight: 600, textTransform: 'uppercase' }}>{k.label}</div>
                          </div>
                        ))}
                      </div>
                      {(dashboardData.leadFunnel?.by_status || []).length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {(dashboardData.leadFunnel.by_status || []).map((s: any, i: number) => {
                            const maxC = Math.max(...(dashboardData.leadFunnel.by_status || []).map((x: any) => x.count), 1)
                            const w = Math.max((s.count / maxC) * 100, 6)
                            return (
                              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{ width: '100px', fontSize: '0.72rem', fontWeight: 500, color: '#33475b' }}>{getLeadStatusLabel(s.status_id, s.status_label)}</div>
                                <div style={{ flex: 1, background: '#f1f5f9', borderRadius: '4px', height: '22px', overflow: 'hidden' }}>
                                  <div style={{ width: `${w}%`, height: '100%', background: '#00695c', borderRadius: '4px', display: 'flex', alignItems: 'center', paddingLeft: '6px' }}>
                                    <span style={{ fontSize: '0.65rem', fontWeight: 600, color: '#fff' }}>{s.count}</span>
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>

                    {/* Revenue Forecast / Pipeline Value */}
                    <div style={{ background: '#fff', border: '1px solid #eaf0f6', borderRadius: '8px', padding: '20px' }}>
                      <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#33475b', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1565c0" strokeWidth="2"><polyline points="22,12 18,12 15,21 9,3 6,12 2,12"/></svg>
                        Revenue Forecast
                      </h3>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                        <div style={{ textAlign: 'center', padding: '14px', background: '#e3f2fd', borderRadius: '6px' }}>
                          <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#1565c0' }}>{currencyFormatter.format(dashboardData.forecast?.total_pipeline_value ?? 0)}</div>
                          <div style={{ fontSize: '0.68rem', color: '#64748b', marginTop: '4px', fontWeight: 600 }}>PIPELINE VALUE</div>
                        </div>
                        <div style={{ textAlign: 'center', padding: '14px', background: '#e8f5e9', borderRadius: '6px' }}>
                          <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#2e7d32' }}>{currencyFormatter.format(dashboardData.forecast?.total_won_revenue ?? 0)}</div>
                          <div style={{ fontSize: '0.68rem', color: '#64748b', marginTop: '4px', fontWeight: 600 }}>TOTAL WON</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                        <div style={{ flex: 1, textAlign: 'center', padding: '8px', background: '#f8fafc', borderRadius: '6px' }}>
                          <div style={{ fontSize: '1rem', fontWeight: 700, color: '#33475b' }}>{dashboardData.forecast?.total_open_deals ?? 0}</div>
                          <div style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 600 }}>OPEN DEALS</div>
                        </div>
                        <div style={{ flex: 1, textAlign: 'center', padding: '8px', background: '#f8fafc', borderRadius: '6px' }}>
                          <div style={{ fontSize: '1rem', fontWeight: 700, color: '#33475b' }}>{dashboardData.forecast?.total_won_deals ?? 0}</div>
                          <div style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 600 }}>WON DEALS</div>
                        </div>
                      </div>
                      {(dashboardData.forecast?.pipeline_stages || []).length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {(dashboardData.forecast.pipeline_stages || []).map((s: any, i: number) => {
                            const maxV = Math.max(...(dashboardData.forecast.pipeline_stages || []).map((x: any) => x.total_value || 0), 1)
                            const w = Math.max((s.total_value / maxV) * 100, 6)
                            return (
                              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{ width: '110px', fontSize: '0.72rem', fontWeight: 500, color: '#33475b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.stage_name}>{s.stage_name}</div>
                                <div style={{ flex: 1, background: '#f1f5f9', borderRadius: '4px', height: '20px', overflow: 'hidden' }}>
                                  <div style={{ width: `${w}%`, height: '100%', background: '#1565c0', borderRadius: '4px' }} />
                                </div>
                                <div style={{ fontSize: '0.7rem', color: '#64748b', width: '70px', textAlign: 'right' }}>{currencyFormatter.format(s.total_value)}</div>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>

                    {/* Sales Leaderboard */}
                    <div style={{ background: '#fff', border: '1px solid #eaf0f6', borderRadius: '8px', padding: '20px' }}>
                      <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#33475b', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ff7a59" strokeWidth="2"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5C7 4 7 8 12 8s5-4 7.5-4a2.5 2.5 0 0 1 0 5H18"/><path d="M6 9v9c0 1.1.9 2 2 2h8a2 2 0 0 0 2-2V9"/></svg>
                        Sales Leaderboard
                      </h3>
                      {(dashboardData.leaderboard || []).length === 0 ? (
                        <div style={{ color: '#7c98b6', fontSize: '13px', padding: '12px 0' }}>No leaderboard data</div>
                      ) : (
                        <table className="hs-table">
                          <thead><tr><th>#</th><th>Rep</th><th>Won</th><th>Revenue</th><th>Activities</th></tr></thead>
                          <tbody>
                            {(dashboardData.leaderboard || []).map((row: any, i: number) => (
                              <tr key={row.user_id}>
                                <td style={{ fontWeight: 700, color: i === 0 ? '#ff7a59' : i === 1 ? '#1976d2' : '#33475b' }}>{i + 1}</td>
                                <td style={{ fontWeight: 500 }}>{row.user_name}</td>
                                <td>{row.deals_won}</td>
                                <td>{currencyFormatter.format(row.revenue)}</td>
                                <td>{row.activities_count}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ========== Reports View (Enhanced) ========== */}
        {activeView === 'reports' && (
          <div className="hs-contacts">
            <div className="hs-topbar">
              <div className="hs-topbar-left">
                <div style={{ display: 'flex', alignItems: 'center', padding: '0 12px', gap: '6px' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21.21 15.89A10 10 0 1 1 8 2.83"/><path d="M22 12A10 10 0 0 0 12 2v10z"/></svg>
                  <span style={{ fontWeight: 600, fontSize: '0.92rem', color: '#33475b' }}>Reports</span>
                </div>
                <div className="hs-topbar-sep" />
                {(['overview', 'sales', 'deals', 'activities', 'leads', 'team', 'builder'] as const).map(tab => (
                  <span key={tab} className={`hs-view-tab${reportingTab === tab ? ' active' : ''}`} onClick={() => setReportingTab(tab)} style={{ cursor: 'pointer', textTransform: 'capitalize' }}>
                    {tab === 'builder' ? 'Report Builder' : tab}
                  </span>
                ))}
              </div>
            </div>
            <div className="hs-content" style={{ padding: '20px', overflowY: 'auto' }}>
              {reportsLoading ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#7c98b6' }}>Loading reports...</div>
              ) : (
                <>
                  {/* Overview Tab - Report Library */}
                  {reportingTab === 'overview' && (
                    <div>
                      <div style={{ marginBottom: '24px' }}>
                        <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#33475b', marginBottom: '4px' }}>Report Library</h2>
                        <p style={{ color: '#64748b', fontSize: '0.82rem' }}>Ready-made reports based on best practices. Click any report to view.</p>
                      </div>
                      <div style={{ marginBottom: '24px' }}>
                        <h3 style={{ fontSize: '0.82rem', fontWeight: 700, color: '#33475b', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Sales Reports</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
                          {[
                            { title: 'Pipeline Velocity', desc: 'Track deals through pipeline stages with conversion rates', tab: 'sales' as const, icon: 'funnel' },
                            { title: 'Revenue Over Time', desc: 'Monthly/weekly revenue trends and deal sizes', tab: 'sales' as const, icon: 'chart' },
                            { title: 'Sales Leaderboard', desc: 'Compare rep performance by deals won and revenue', tab: 'sales' as const, icon: 'trophy' },
                            { title: 'Lead Sources', desc: 'Which sources generate the most leads and conversions', tab: 'leads' as const, icon: 'source' },
                          ].map(r => (
                            <div key={r.title} onClick={() => setReportingTab(r.tab)} style={{ background: '#fff', border: '1px solid #eaf0f6', borderRadius: '8px', padding: '16px', cursor: 'pointer', transition: 'all 0.15s', borderLeft: '4px solid #1976d2' }} onMouseOver={e => (e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)')} onMouseOut={e => (e.currentTarget.style.boxShadow = 'none')}>
                              <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#33475b', marginBottom: '4px' }}>{r.title}</div>
                              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{r.desc}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div style={{ marginBottom: '24px' }}>
                        <h3 style={{ fontSize: '0.82rem', fontWeight: 700, color: '#33475b', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Deal Reports</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
                          {[
                            { title: 'Deals Created', desc: 'Track deal creation volume by period and rep', tab: 'deals' as const },
                            { title: 'Deal Funnel', desc: 'Visualize deal journey from creation to close', tab: 'deals' as const },
                            { title: 'Average Deal Size', desc: 'Compare deal sizes across reps and time periods', tab: 'deals' as const },
                            { title: 'Deals Won vs Lost', desc: 'Track win/loss trends over time', tab: 'deals' as const },
                          ].map(r => (
                            <div key={r.title} onClick={() => setReportingTab(r.tab)} style={{ background: '#fff', border: '1px solid #eaf0f6', borderRadius: '8px', padding: '16px', cursor: 'pointer', transition: 'all 0.15s', borderLeft: '4px solid #2e7d32' }} onMouseOver={e => (e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)')} onMouseOut={e => (e.currentTarget.style.boxShadow = 'none')}>
                              <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#33475b', marginBottom: '4px' }}>{r.title}</div>
                              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{r.desc}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div style={{ marginBottom: '24px' }}>
                        <h3 style={{ fontSize: '0.82rem', fontWeight: 700, color: '#33475b', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Activity Reports</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
                          {[
                            { title: 'Completed Activities', desc: 'Track calls, emails, meetings, tasks by period', tab: 'activities' as const },
                            { title: 'Activity Breakdown', desc: 'See distribution of activity types', tab: 'activities' as const },
                            { title: 'Team Activity Summary', desc: 'Full team performance table with all metrics', tab: 'team' as const },
                          ].map(r => (
                            <div key={r.title} onClick={() => setReportingTab(r.tab)} style={{ background: '#fff', border: '1px solid #eaf0f6', borderRadius: '8px', padding: '16px', cursor: 'pointer', transition: 'all 0.15s', borderLeft: '4px solid #ed6c02' }} onMouseOver={e => (e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)')} onMouseOut={e => (e.currentTarget.style.boxShadow = 'none')}>
                              <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#33475b', marginBottom: '4px' }}>{r.title}</div>
                              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{r.desc}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h3 style={{ fontSize: '0.82rem', fontWeight: 700, color: '#33475b', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Custom</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
                          <div onClick={() => setReportingTab('builder')} style={{ background: '#fff', border: '2px dashed #cbd5e1', borderRadius: '8px', padding: '16px', cursor: 'pointer', textAlign: 'center' }} onMouseOver={e => (e.currentTarget.style.borderColor = '#1976d2')} onMouseOut={e => (e.currentTarget.style.borderColor = '#cbd5e1')}>
                            <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#1976d2', marginBottom: '4px' }}>+ Create Custom Report</div>
                            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Build a report using your CRM data</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Sales Tab */}
                  {reportingTab === 'sales' && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(440px, 1fr))', gap: '20px' }}>
                      <div style={{ background: '#fff', border: '1px solid #eaf0f6', borderRadius: '8px', padding: '20px' }}>
                        <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#33475b', marginBottom: '16px' }}>Pipeline Velocity</h3>
                        {reportPipeline.length === 0 ? <div style={{ color: '#7c98b6', fontSize: '13px' }}>No data</div> : (
                          <table className="hs-table"><thead><tr><th>Stage</th><th>Deals</th><th>Avg Days</th><th>Value</th><th>Conv. %</th></tr></thead>
                            <tbody>{reportPipeline.map((row, i) => (<tr key={i}><td style={{ fontWeight: 500 }}>{row.stage_name}</td><td>{row.deals_entered}</td><td>{row.avg_days.toFixed(1)}</td><td>{currencyFormatter.format(row.total_value)}</td><td>{(row.conversion_rate * 100).toFixed(1)}%</td></tr>))}</tbody>
                          </table>
                        )}
                      </div>
                      <div style={{ background: '#fff', border: '1px solid #eaf0f6', borderRadius: '8px', padding: '20px' }}>
                        <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#33475b', marginBottom: '16px' }}>Revenue Over Time</h3>
                        {reportRevenue.length === 0 ? <div style={{ color: '#7c98b6', fontSize: '13px' }}>No data</div> : (
                          <table className="hs-table"><thead><tr><th>Period</th><th>Deals Closed</th><th>Total Revenue</th><th>Avg Deal Size</th></tr></thead>
                            <tbody>{reportRevenue.map((row, i) => (<tr key={i}><td style={{ fontWeight: 500 }}>{row.period}</td><td>{row.deals_closed}</td><td>{currencyFormatter.format(row.total_revenue)}</td><td>{currencyFormatter.format(row.avg_deal_size)}</td></tr>))}</tbody>
                          </table>
                        )}
                      </div>
                      <div style={{ background: '#fff', border: '1px solid #eaf0f6', borderRadius: '8px', padding: '20px' }}>
                        <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#33475b', marginBottom: '16px' }}>Sales Leaderboard</h3>
                        {reportLeaderboard.length === 0 ? <div style={{ color: '#7c98b6', fontSize: '13px' }}>No data</div> : (
                          <table className="hs-table"><thead><tr><th>#</th><th>Name</th><th>Deals Won</th><th>Revenue</th><th>Activities</th></tr></thead>
                            <tbody>{reportLeaderboard.map((row, i) => (<tr key={row.user_id}><td style={{ fontWeight: 700, color: i === 0 ? '#ff7a59' : '#33475b' }}>{i + 1}</td><td style={{ fontWeight: 500 }}>{row.user_name}</td><td>{row.deals_won}</td><td>{currencyFormatter.format(row.revenue)}</td><td>{row.activities_count}</td></tr>))}</tbody>
                          </table>
                        )}
                      </div>
                      <div style={{ background: '#fff', border: '1px solid #eaf0f6', borderRadius: '8px', padding: '20px' }}>
                        <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#33475b', marginBottom: '16px' }}>Lead Sources</h3>
                        {reportLeadSources.length === 0 ? <div style={{ color: '#7c98b6', fontSize: '13px' }}>No data</div> : (
                          <table className="hs-table"><thead><tr><th>Source</th><th>Leads</th><th>Conv. %</th><th>Avg Value</th></tr></thead>
                            <tbody>{reportLeadSources.map((row, i) => (<tr key={i}><td style={{ fontWeight: 500 }}>{row.source || 'Unknown'}</td><td>{row.lead_count}</td><td>{(row.conversion_rate * 100).toFixed(1)}%</td><td>{currencyFormatter.format(row.avg_value)}</td></tr>))}</tbody>
                          </table>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Deals Tab */}
                  {reportingTab === 'deals' && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(440px, 1fr))', gap: '20px' }}>
                      <div style={{ background: '#fff', border: '1px solid #eaf0f6', borderRadius: '8px', padding: '20px' }}>
                        <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#33475b', marginBottom: '16px' }}>Pipeline Velocity</h3>
                        {reportPipeline.length === 0 ? <div style={{ color: '#7c98b6', fontSize: '13px' }}>No data</div> : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {reportPipeline.map((row, i) => {
                              const maxV = Math.max(...reportPipeline.map(r => r.deals_entered || 0), 1)
                              const w = Math.max((row.deals_entered / maxV) * 100, 8)
                              const colors = ['#1976d2', '#2196f3', '#42a5f5', '#64b5f6', '#90caf9']
                              return (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                  <div style={{ width: '110px', fontSize: '0.73rem', fontWeight: 500, color: '#33475b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.stage_name}</div>
                                  <div style={{ flex: 1, background: '#f1f5f9', borderRadius: '4px', height: '26px', overflow: 'hidden' }}>
                                    <div style={{ width: `${w}%`, height: '100%', background: colors[i % colors.length], borderRadius: '4px', display: 'flex', alignItems: 'center', paddingLeft: '8px' }}>
                                      <span style={{ fontSize: '0.68rem', fontWeight: 600, color: '#fff' }}>{row.deals_entered} deals</span>
                                    </div>
                                  </div>
                                  <div style={{ width: '70px', fontSize: '0.7rem', color: '#64748b', textAlign: 'right' }}>{currencyFormatter.format(row.total_value)}</div>
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </div>
                      <div style={{ background: '#fff', border: '1px solid #eaf0f6', borderRadius: '8px', padding: '20px' }}>
                        <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#33475b', marginBottom: '16px' }}>Revenue Over Time</h3>
                        {reportRevenue.length === 0 ? <div style={{ color: '#7c98b6', fontSize: '13px' }}>No data</div> : (() => {
                          const maxR = Math.max(...reportRevenue.map(r => r.total_revenue), 1)
                          return (
                            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: '180px', padding: '8px 0', borderBottom: '1px solid #eaf0f6' }}>
                              {reportRevenue.slice(-12).map((row, i) => {
                                const h = Math.max((row.total_revenue / maxR) * 160, 4)
                                return (
                                  <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                                    <div style={{ fontSize: '0.58rem', color: '#64748b' }}>{currencyFormatter.format(row.total_revenue)}</div>
                                    <div style={{ width: '100%', maxWidth: '36px', height: `${h}px`, background: 'linear-gradient(180deg, #1976d2, #42a5f5)', borderRadius: '3px 3px 0 0' }} />
                                    <div style={{ fontSize: '0.58rem', color: '#94a3b8' }}>{row.period?.substring(5)}</div>
                                  </div>
                                )
                              })}
                            </div>
                          )
                        })()}
                      </div>
                      <div style={{ background: '#fff', border: '1px solid #eaf0f6', borderRadius: '8px', padding: '20px', gridColumn: 'span 2' }}>
                        <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#33475b', marginBottom: '16px' }}>Deal Details</h3>
                        <table className="hs-table"><thead><tr><th>Period</th><th>Deals Closed</th><th>Total Revenue</th><th>Avg Deal Size</th></tr></thead>
                          <tbody>{reportRevenue.map((row, i) => (<tr key={i}><td style={{ fontWeight: 500 }}>{row.period}</td><td>{row.deals_closed}</td><td>{currencyFormatter.format(row.total_revenue)}</td><td>{currencyFormatter.format(row.avg_deal_size)}</td></tr>))}</tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Activities Tab */}
                  {reportingTab === 'activities' && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(440px, 1fr))', gap: '20px' }}>
                      <div style={{ background: '#fff', border: '1px solid #eaf0f6', borderRadius: '8px', padding: '20px' }}>
                        <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#33475b', marginBottom: '16px' }}>Activities Over Time</h3>
                        {reportActivities.length === 0 ? <div style={{ color: '#7c98b6', fontSize: '13px' }}>No data</div> : (
                          <table className="hs-table"><thead><tr><th>Period</th><th>Notes</th><th>Emails</th><th>Calls</th><th>Meetings</th><th>Tasks</th></tr></thead>
                            <tbody>{reportActivities.map((row, i) => (<tr key={i}><td style={{ fontWeight: 500 }}>{row.period}</td><td>{row.notes}</td><td>{row.emails}</td><td>{row.calls}</td><td>{row.meetings}</td><td>{row.tasks_completed}</td></tr>))}</tbody>
                          </table>
                        )}
                      </div>
                      <div style={{ background: '#fff', border: '1px solid #eaf0f6', borderRadius: '8px', padding: '20px' }}>
                        <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#33475b', marginBottom: '16px' }}>Activity Trend</h3>
                        {reportActivities.length === 0 ? <div style={{ color: '#7c98b6', fontSize: '13px' }}>No data</div> : (() => {
                          const totals = reportActivities.map(r => r.notes + r.emails + r.calls + r.meetings + r.tasks_completed)
                          const maxT = Math.max(...totals, 1)
                          return (
                            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: '160px', borderBottom: '1px solid #eaf0f6' }}>
                              {reportActivities.slice(-12).map((row, i) => {
                                const total = row.notes + row.emails + row.calls + row.meetings + row.tasks_completed
                                const h = Math.max((total / maxT) * 140, 4)
                                return (
                                  <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                                    <div style={{ fontSize: '0.6rem', color: '#64748b', fontWeight: 600 }}>{total}</div>
                                    <div style={{ width: '100%', maxWidth: '30px', height: `${h}px`, background: 'linear-gradient(180deg, #ed6c02, #ff9800)', borderRadius: '3px 3px 0 0' }} />
                                    <div style={{ fontSize: '0.55rem', color: '#94a3b8' }}>{row.period?.substring(5)}</div>
                                  </div>
                                )
                              })}
                            </div>
                          )
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Leads Tab */}
                  {reportingTab === 'leads' && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(440px, 1fr))', gap: '20px' }}>
                      <div style={{ background: '#fff', border: '1px solid #eaf0f6', borderRadius: '8px', padding: '20px' }}>
                        <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#33475b', marginBottom: '16px' }}>Lead Sources</h3>
                        {reportLeadSources.length === 0 ? <div style={{ color: '#7c98b6', fontSize: '13px' }}>No data</div> : (() => {
                          const total = reportLeadSources.reduce((s, r) => s + r.lead_count, 0)
                          const colors = ['#1976d2', '#2e7d32', '#ed6c02', '#d32f2f', '#7b1fa2', '#0288d1', '#00695c']
                          let cum = 0
                          const segs = reportLeadSources.map((r, i) => { const p = total > 0 ? (r.lead_count / total) * 100 : 0; const s = cum; cum += p; return { ...r, pct: p, start: s, color: colors[i % colors.length] } })
                          const grad = segs.map(s => `${s.color} ${s.start}% ${s.start + s.pct}%`).join(', ')
                          return (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                              <div style={{ width: '140px', height: '140px', borderRadius: '50%', background: total > 0 ? `conic-gradient(${grad})` : '#f1f5f9', position: 'relative', flexShrink: 0 }}>
                                <div style={{ position: 'absolute', top: '25%', left: '25%', width: '50%', height: '50%', borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', fontWeight: 700, color: '#33475b' }}>{total}</div>
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                                {segs.map((s, i) => (
                                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.76rem' }}>
                                    <span style={{ width: '10px', height: '10px', borderRadius: '2px', background: s.color, display: 'inline-block' }} />
                                    <span style={{ color: '#33475b', fontWeight: 500, flex: 1 }}>{s.source || 'Unknown'}</span>
                                    <span style={{ color: '#64748b', fontWeight: 600 }}>{s.lead_count}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )
                        })()}
                      </div>
                      <div style={{ background: '#fff', border: '1px solid #eaf0f6', borderRadius: '8px', padding: '20px' }}>
                        <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#33475b', marginBottom: '16px' }}>Lead Conversion by Source</h3>
                        {reportLeadSources.length === 0 ? <div style={{ color: '#7c98b6', fontSize: '13px' }}>No data</div> : (
                          <table className="hs-table"><thead><tr><th>Source</th><th>Leads</th><th>Conv. Rate</th><th>Avg Value</th></tr></thead>
                            <tbody>{reportLeadSources.map((row, i) => (<tr key={i}><td style={{ fontWeight: 500 }}>{row.source || 'Unknown'}</td><td>{row.lead_count}</td><td><span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '12px', background: row.conversion_rate > 20 ? '#e8f5e9' : '#fff3e0', color: row.conversion_rate > 20 ? '#2e7d32' : '#ed6c02', fontWeight: 600, fontSize: '0.75rem' }}>{(row.conversion_rate * 100).toFixed(1)}%</span></td><td>{currencyFormatter.format(row.avg_value)}</td></tr>))}</tbody>
                          </table>
                        )}
                      </div>
                      {/* Leads by Estimator — per-estimator totals, converted count, and by-source breakdown */}
                      <div style={{ background: '#fff', border: '1px solid #eaf0f6', borderRadius: '8px', padding: '20px', gridColumn: 'span 2' }}>
                        <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#33475b', marginBottom: '6px' }}>Leads by Estimator</h3>
                        {(() => {
                          // Does any row carry assigner attribution yet? Until lead actions
                          // record the acting user, this stays empty and we show an honest note.
                          const anyAssigner = reportLeadsByEstimator.some(r => Array.isArray(r.by_assigner) && r.by_assigner.length > 0)
                          return !anyAssigner ? (
                            <p style={{ fontSize: '0.72rem', color: '#94a3b8', margin: '0 0 14px', lineHeight: 1.4 }}>
                              Assigner (who assigned each lead) is not tracked yet; attribution will appear here once lead actions record the acting user.
                            </p>
                          ) : null
                        })()}
                        {reportLeadsByEstimator.length === 0 ? <div style={{ color: '#7c98b6', fontSize: '13px' }}>No data</div> : (
                          <div style={{ overflowX: 'auto' }}>
                            <table className="hs-table">
                              <thead><tr><th>Estimator</th><th>Total Leads</th><th>Converted</th><th>Conv. Rate</th><th>By Source</th></tr></thead>
                              <tbody>{reportLeadsByEstimator.map((row, i) => {
                                const rate = row.conversion_rate != null
                                  ? row.conversion_rate
                                  : (row.total_leads > 0 ? (row.converted / row.total_leads) * 100 : 0)
                                const hasAssigner = Array.isArray(row.by_assigner) && row.by_assigner.length > 0
                                const cov = row.assigner_coverage
                                return (
                                  <tr key={row.estimator_id ?? row.estimator ?? i}>
                                    <td style={{ fontWeight: 600 }}>{row.estimator || 'Unassigned'}</td>
                                    <td>{row.total_leads}</td>
                                    <td><span style={{ fontWeight: 600, color: '#2e7d32' }}>{row.converted}</span></td>
                                    <td><span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '12px', background: rate > 20 ? '#e8f5e9' : '#fff3e0', color: rate > 20 ? '#2e7d32' : '#ed6c02', fontWeight: 600, fontSize: '0.75rem' }}>{rate.toFixed(1)}%</span></td>
                                    <td>
                                      {Array.isArray(row.by_source) && row.by_source.length > 0 ? (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                          {row.by_source.map((s, j) => (
                                            <span key={j} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '10px', background: '#f1f5f9', fontSize: '0.72rem', color: '#33475b' }}>
                                              <span style={{ fontWeight: 500 }}>{s.source || 'Unknown'}</span>
                                              <span style={{ fontWeight: 700, color: '#1976d2' }}>{s.count}</span>
                                            </span>
                                          ))}
                                          {hasAssigner && (
                                            <div style={{ flexBasis: '100%', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px', marginTop: '6px', paddingTop: '6px', borderTop: '1px dashed #eaf0f6' }}>
                                              <span style={{ fontSize: '0.66rem', fontWeight: 600, color: '#7c98b6', textTransform: 'uppercase', letterSpacing: '0.03em', marginRight: '2px' }}>Assigned by</span>
                                              {row.by_assigner!.map((a, j) => (
                                                <span key={j} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '10px', background: '#f1f5f9', fontSize: '0.72rem', color: '#33475b' }}>
                                                  <span style={{ fontWeight: 500 }}>{a.assigner_name || 'Unattributed'}</span>
                                                  <span style={{ fontWeight: 700, color: '#7e3ff2' }}>{a.count}</span>
                                                </span>
                                              ))}
                                              {cov && cov.total > 0 && cov.attributed < cov.total && (
                                                <span style={{ fontSize: '0.66rem', color: '#94a3b8', marginLeft: '2px' }}>{cov.attributed} of {cov.total} attributed</span>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      ) : <span style={{ color: '#94a3b8', fontSize: '0.72rem' }}>—</span>}
                                    </td>
                                  </tr>
                                )
                              })}</tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Team Tab */}
                  {reportingTab === 'team' && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px' }}>
                      <div style={{ background: '#fff', border: '1px solid #eaf0f6', borderRadius: '8px', padding: '20px' }}>
                        <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#33475b', marginBottom: '16px' }}>Team Activity Summary</h3>
                        <p style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '12px' }}>Performance metrics for each team member this year</p>
                        {reportLeaderboard.length === 0 ? <div style={{ color: '#7c98b6', fontSize: '13px' }}>No data</div> : (
                          <div style={{ overflowX: 'auto' }}>
                            <table className="hs-table">
                              <thead><tr><th>Rep</th><th>Deals Won</th><th>Revenue</th><th>Activities</th><th>Avg Revenue/Deal</th><th>Activity Score</th></tr></thead>
                              <tbody>{reportLeaderboard.map((row, i) => (
                                <tr key={row.user_id}>
                                  <td style={{ fontWeight: 600 }}><div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ width: '28px', height: '28px', borderRadius: '50%', background: i === 0 ? '#ff7a59' : '#e3f2fd', color: i === 0 ? '#fff' : '#1976d2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', fontWeight: 700, flexShrink: 0 }}>{(row.user_name || '?')[0]}</span>{row.user_name}</div></td>
                                  <td><span style={{ fontWeight: 600, color: '#2e7d32' }}>{row.deals_won}</span></td>
                                  <td>{currencyFormatter.format(row.revenue)}</td>
                                  <td>{row.activities_count}</td>
                                  <td>{row.deals_won > 0 ? currencyFormatter.format(row.revenue / row.deals_won) : '-'}</td>
                                  <td><div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><div style={{ width: '60px', height: '6px', background: '#f1f5f9', borderRadius: '3px', overflow: 'hidden' }}><div style={{ width: `${Math.min((row.activities_count / Math.max(...reportLeaderboard.map(r => r.activities_count), 1)) * 100, 100)}%`, height: '100%', background: '#1976d2', borderRadius: '3px' }} /></div><span style={{ fontSize: '0.7rem', color: '#64748b' }}>{row.activities_count}</span></div></td>
                                </tr>
                              ))}</tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Report Builder Tab */}
                  {reportingTab === 'builder' && (
                    <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '20px', minHeight: '500px' }}>
                      {/* Builder Sidebar */}
                      <div style={{ background: '#fff', border: '1px solid #eaf0f6', borderRadius: '8px', padding: '20px' }}>
                        <h3 style={{ fontSize: '0.88rem', fontWeight: 600, color: '#33475b', marginBottom: '16px' }}>Report Builder</h3>
                        <div style={{ marginBottom: '16px' }}>
                          <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' }}>Data Source</label>
                          <select value={builderSource} onChange={e => setBuilderSource(e.target.value as any)} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.82rem' }}>
                            <option value="deals">Deals</option>
                            <option value="leads">Leads</option>
                            <option value="contacts">Contacts</option>
                            <option value="activities">Activities</option>
                          </select>
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                          <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' }}>Chart Type</label>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '4px' }}>
                            {[
                              { id: 'bar' as const, icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="4" y="10" width="4" height="10"/><rect x="10" y="4" width="4" height="16"/><rect x="16" y="8" width="4" height="12"/></svg> },
                              { id: 'line' as const, icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polyline points="4,18 9,12 14,15 20,6"/></svg> },
                              { id: 'pie' as const, icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="8"/><path d="M12 4v8l6 4"/></svg> },
                              { id: 'donut' as const, icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="4"/></svg> },
                              { id: 'table' as const, icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="3" x2="9" y2="21"/></svg> },
                              { id: 'number' as const, icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><text x="6" y="17" fontSize="14" fill="currentColor" stroke="none" fontWeight="bold">#</text></svg> },
                              { id: 'funnel' as const, icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polygon points="20,4 4,4 10,12 10,18 14,20 14,12"/></svg> },
                            ].map(ct => (
                              <button key={ct.id} onClick={() => setBuilderChart(ct.id)} style={{ padding: '8px', borderRadius: '6px', border: `2px solid ${builderChart === ct.id ? '#1976d2' : '#e5e7eb'}`, background: builderChart === ct.id ? '#e3f2fd' : '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{ct.icon}</button>
                            ))}
                          </div>
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                          <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' }}>Metric</label>
                          <select value={builderMetric} onChange={e => setBuilderMetric(e.target.value as any)} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.82rem' }}>
                            <option value="count">Count</option>
                            <option value="sum">Sum (Amount)</option>
                            <option value="average">Average (Amount)</option>
                          </select>
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                          <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' }}>Group By</label>
                          <select value={builderGroupBy} onChange={e => setBuilderGroupBy(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.82rem' }}>
                            {builderSource === 'deals' && <><option value="stage">Stage</option><option value="owner">Owner</option><option value="month">Month</option><option value="pipeline">Pipeline</option></>}
                            {builderSource === 'leads' && <><option value="status">Status</option><option value="source">Source</option><option value="owner">Owner</option><option value="month">Month</option></>}
                            {builderSource === 'contacts' && <><option value="type">Type</option><option value="state">State</option><option value="month">Month Created</option></>}
                            {builderSource === 'activities' && <><option value="type">Type</option><option value="owner">Owner</option><option value="month">Month</option></>}
                          </select>
                        </div>
                        <button onClick={async () => {
                          setBuilderLoading(true)
                          try {
                            let data: any
                            if (builderSource === 'deals') {
                              if (builderGroupBy === 'stage') data = await fetchPipelineReport()
                              else if (builderGroupBy === 'owner') data = await fetchDealsCreated({ group_by: 'rep' })
                              else if (builderGroupBy === 'month') data = await fetchRevenueReport()
                              else data = await fetchPipelineReport()
                            } else if (builderSource === 'leads') {
                              data = await fetchLeadSourcesReport()
                            } else if (builderSource === 'activities') {
                              data = await fetchActivityByType()
                            } else {
                              const kpi = await fetchSummaryKPIs()
                              data = [kpi]
                            }
                            setBuilderResults(Array.isArray(data) ? data : data.results || data.by_rep || data.by_status || data.stages || [data])
                          } catch { setBuilderResults([]) }
                          setBuilderLoading(false)
                        }} style={{ width: '100%', padding: '10px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 600, fontSize: '0.84rem', cursor: 'pointer' }}>
                          Run Report
                        </button>
                      </div>

                      {/* Builder Results */}
                      <div style={{ background: '#fff', border: '1px solid #eaf0f6', borderRadius: '8px', padding: '20px' }}>
                        {builderLoading ? (
                          <div style={{ padding: '40px', textAlign: 'center', color: '#7c98b6' }}>Running report...</div>
                        ) : builderResults.length === 0 ? (
                          <div style={{ padding: '60px 20px', textAlign: 'center' }}>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" style={{ margin: '0 auto 12px' }}><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
                            <div style={{ color: '#64748b', fontSize: '0.88rem', fontWeight: 500 }}>Configure your report and click "Run Report"</div>
                            <div style={{ color: '#94a3b8', fontSize: '0.78rem', marginTop: '4px' }}>Select a data source, chart type, and grouping</div>
                          </div>
                        ) : (
                          <>
                            <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#33475b', marginBottom: '16px' }}>
                              {builderSource.charAt(0).toUpperCase() + builderSource.slice(1)} by {builderGroupBy} ({builderMetric})
                            </h3>
                            {/* Render based on chart type */}
                            {(builderChart === 'bar' || builderChart === 'line') && (() => {
                              const keys = Object.keys(builderResults[0] || {}).filter(k => typeof builderResults[0][k] === 'number')
                              const labelKey = Object.keys(builderResults[0] || {}).find(k => typeof builderResults[0][k] === 'string') || keys[0]
                              const valueKey = builderMetric === 'sum' ? (keys.find(k => k.includes('value') || k.includes('revenue') || k.includes('opportunity') || k.includes('total')) || keys[0]) : builderMetric === 'average' ? (keys.find(k => k.includes('avg') || k.includes('average')) || keys[0]) : (keys.find(k => k.includes('count') || k.includes('deals') || k.includes('lead') || k.includes('cnt')) || keys[0])
                              const maxVal = Math.max(...builderResults.map(r => Math.abs(r[valueKey] || 0)), 1)
                              return (
                                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: '240px', padding: '20px 0', borderBottom: '1px solid #eaf0f6' }}>
                                  {builderResults.slice(0, 20).map((row, i) => {
                                    const val = row[valueKey] || 0
                                    const h = Math.max((Math.abs(val) / maxVal) * 200, 4)
                                    return (
                                      <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                                        <div style={{ fontSize: '0.6rem', color: '#64748b', fontWeight: 500 }}>{typeof val === 'number' && val > 999 ? currencyFormatter.format(val) : val}</div>
                                        <div style={{ width: '100%', maxWidth: '40px', height: `${h}px`, background: builderChart === 'line' ? '#42a5f5' : 'linear-gradient(180deg, #1976d2, #42a5f5)', borderRadius: '3px 3px 0 0' }} />
                                        <div style={{ fontSize: '0.55rem', color: '#94a3b8', maxWidth: '50px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center' }} title={String(row[labelKey || ''] || '')}>{String(row[labelKey || ''] || '').substring(0, 10)}</div>
                                      </div>
                                    )
                                  })}
                                </div>
                              )
                            })()}
                            {(builderChart === 'pie' || builderChart === 'donut') && (() => {
                              const keys = Object.keys(builderResults[0] || {}).filter(k => typeof builderResults[0][k] === 'number')
                              const labelKey = Object.keys(builderResults[0] || {}).find(k => typeof builderResults[0][k] === 'string') || ''
                              const valueKey = keys.find(k => k.includes('count') || k.includes('deals') || k.includes('lead')) || keys[0]
                              const total = builderResults.reduce((s, r) => s + (r[valueKey] || 0), 0)
                              const colors = ['#1976d2', '#2e7d32', '#ed6c02', '#d32f2f', '#7b1fa2', '#0288d1', '#00695c', '#f57c00']
                              let cum = 0
                              const segs = builderResults.map((r, i) => { const p = total > 0 ? (r[valueKey] / total) * 100 : 0; const s = cum; cum += p; return { label: r[labelKey] || `Item ${i}`, value: r[valueKey], pct: p, start: s, color: colors[i % colors.length] } })
                              const grad = segs.map(s => `${s.color} ${s.start}% ${s.start + s.pct}%`).join(', ')
                              return (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '32px', justifyContent: 'center', padding: '20px 0' }}>
                                  <div style={{ width: '200px', height: '200px', borderRadius: '50%', background: `conic-gradient(${grad})`, position: 'relative' }}>
                                    {builderChart === 'donut' && <div style={{ position: 'absolute', top: '25%', left: '25%', width: '50%', height: '50%', borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', fontWeight: 700, color: '#33475b' }}>{total}</div>}
                                  </div>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    {segs.map((s, i) => (
                                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.78rem' }}>
                                        <span style={{ width: '12px', height: '12px', borderRadius: '2px', background: s.color, display: 'inline-block' }} />
                                        <span style={{ color: '#33475b', fontWeight: 500 }}>{s.label}</span>
                                        <span style={{ color: '#64748b' }}>{s.value} ({s.pct.toFixed(1)}%)</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )
                            })()}
                            {builderChart === 'number' && (() => {
                              const keys = Object.keys(builderResults[0] || {}).filter(k => typeof builderResults[0][k] === 'number')
                              return (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '16px', padding: '20px 0' }}>
                                  {keys.map(k => {
                                    const total = builderResults.reduce((s, r) => s + (r[k] || 0), 0)
                                    const avg = builderResults.length > 0 ? total / builderResults.length : 0
                                    return (
                                      <div key={k} style={{ textAlign: 'center', padding: '20px', background: '#f8fafc', borderRadius: '8px' }}>
                                        <div style={{ fontSize: '2rem', fontWeight: 800, color: '#1976d2' }}>{builderMetric === 'average' ? avg.toFixed(1) : total.toLocaleString()}</div>
                                        <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginTop: '4px' }}>{k.replace(/_/g, ' ')}</div>
                                      </div>
                                    )
                                  })}
                                </div>
                              )
                            })()}
                            {builderChart === 'funnel' && (() => {
                              const keys = Object.keys(builderResults[0] || {}).filter(k => typeof builderResults[0][k] === 'number')
                              const labelKey = Object.keys(builderResults[0] || {}).find(k => typeof builderResults[0][k] === 'string') || ''
                              const valueKey = keys.find(k => k.includes('count') || k.includes('deals')) || keys[0]
                              const maxVal = Math.max(...builderResults.map(r => r[valueKey] || 0), 1)
                              const colors = ['#1976d2', '#2196f3', '#42a5f5', '#64b5f6', '#90caf9', '#bbdefb']
                              return (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', padding: '20px 0', alignItems: 'center' }}>
                                  {builderResults.map((row, i) => {
                                    const w = Math.max((row[valueKey] / maxVal) * 100, 20)
                                    return (
                                      <div key={i} style={{ width: `${w}%`, background: colors[i % colors.length], padding: '10px 16px', borderRadius: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'width 0.5s ease' }}>
                                        <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#fff' }}>{row[labelKey] || `Stage ${i + 1}`}</span>
                                        <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#fff' }}>{row[valueKey]}</span>
                                      </div>
                                    )
                                  })}
                                </div>
                              )
                            })()}
                            {builderChart === 'table' && (
                              <div style={{ overflowX: 'auto' }}>
                                <table className="hs-table">
                                  <thead><tr>{Object.keys(builderResults[0] || {}).map(k => <th key={k}>{k.replace(/_/g, ' ')}</th>)}</tr></thead>
                                  <tbody>{builderResults.map((row, i) => (<tr key={i}>{Object.values(row).map((v: any, j) => <td key={j}>{typeof v === 'number' && v > 100 ? v.toLocaleString() : String(v)}</td>)}</tr>))}</tbody>
                                </table>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* ========== Analytics Tools View ========== */}
        {activeView === 'analytics' && (
          <div className="hs-contacts">
            <div className="hs-topbar">
              <div className="hs-topbar-left">
                <div style={{ display: 'flex', alignItems: 'center', padding: '0 12px', gap: '6px' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="22,12 18,12 15,21 9,3 6,12 2,12"/></svg>
                  <span style={{ fontWeight: 600, fontSize: '0.92rem', color: '#33475b' }}>Analytics Tools</span>
                </div>
                <div className="hs-topbar-sep" />
                {(['sales', 'activity', 'pipeline'] as const).map(tab => (
                  <span key={tab} className={`hs-view-tab${analyticsTab === tab ? ' active' : ''}`} onClick={() => setAnalyticsTab(tab)} style={{ cursor: 'pointer', textTransform: 'capitalize' }}>
                    {tab === 'sales' ? 'Sales Analytics' : tab === 'activity' ? 'Activity Analytics' : 'Pipeline Analytics'}
                  </span>
                ))}
              </div>
            </div>
            <div className="hs-content" style={{ padding: '20px', overflowY: 'auto' }}>
              {analyticsLoading ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#7c98b6' }}>Loading analytics...</div>
              ) : (
                <>
                  {/* Sales Analytics Tab */}
                  {analyticsTab === 'sales' && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(440px, 1fr))', gap: '20px' }}>
                      {/* Deal Velocity */}
                      <div style={{ background: '#fff', border: '1px solid #eaf0f6', borderRadius: '8px', padding: '20px' }}>
                        <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#33475b', marginBottom: '4px' }}>Deal Velocity</h3>
                        <p style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '16px' }}>How long does it take reps to close a deal?</p>
                        <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                          <div style={{ flex: 1, textAlign: 'center', padding: '14px', background: '#e3f2fd', borderRadius: '6px' }}>
                            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#1565c0' }}>{Math.max(0, analyticsData.velocity?.overall_avg_days ?? 0)}</div>
                            <div style={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 600, marginTop: '4px' }}>AVG DAYS TO CLOSE</div>
                          </div>
                          <div style={{ flex: 1, textAlign: 'center', padding: '14px', background: '#e8f5e9', borderRadius: '6px' }}>
                            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#2e7d32' }}>{analyticsData.velocity?.total_closed ?? 0}</div>
                            <div style={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 600, marginTop: '4px' }}>TOTAL CLOSED</div>
                          </div>
                        </div>
                        {(analyticsData.velocity?.by_rep || []).length > 0 && (
                          <table className="hs-table">
                            <thead><tr><th>Rep</th><th>Closed</th><th>Avg Days</th><th>Min</th><th>Max</th></tr></thead>
                            <tbody>{(analyticsData.velocity.by_rep || []).map((r: any, i: number) => (
                              <tr key={i}><td style={{ fontWeight: 500 }}>{r.rep_name}</td><td>{r.deals_closed}</td><td style={{ fontWeight: 600, color: '#1976d2' }}>{Math.max(0, r.avg_days_to_close)}</td><td>{Math.max(0, r.min_days)}d</td><td>{Math.max(0, r.max_days)}d</td></tr>
                            ))}</tbody>
                          </table>
                        )}
                      </div>

                      {/* Average Deal Size */}
                      <div style={{ background: '#fff', border: '1px solid #eaf0f6', borderRadius: '8px', padding: '20px' }}>
                        <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#33475b', marginBottom: '4px' }}>Average Deal Size</h3>
                        <p style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '16px' }}>Which reps have the highest average deal amount?</p>
                        <div style={{ textAlign: 'center', padding: '14px', background: '#f3e5f5', borderRadius: '6px', marginBottom: '16px' }}>
                          <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#7b1fa2' }}>{currencyFormatter.format(analyticsData.avgSize?.overall_avg ?? 0)}</div>
                          <div style={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 600, marginTop: '4px' }}>OVERALL AVERAGE</div>
                        </div>
                        {(analyticsData.avgSize?.by_rep || []).length > 0 && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {(analyticsData.avgSize.by_rep || []).map((r: any, i: number) => {
                              const maxAvg = Math.max(...(analyticsData.avgSize.by_rep || []).map((x: any) => x.avg_size || 0), 1)
                              const w = Math.max((r.avg_size / maxAvg) * 100, 8)
                              return (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                  <div style={{ width: '100px', fontSize: '0.73rem', fontWeight: 500, color: '#33475b' }}>{r.rep_name}</div>
                                  <div style={{ flex: 1, background: '#f1f5f9', borderRadius: '4px', height: '24px', overflow: 'hidden' }}>
                                    <div style={{ width: `${w}%`, height: '100%', background: '#7b1fa2', borderRadius: '4px', display: 'flex', alignItems: 'center', paddingLeft: '8px' }}>
                                      <span style={{ fontSize: '0.65rem', fontWeight: 600, color: '#fff' }}>{currencyFormatter.format(r.avg_size)}</span>
                                    </div>
                                  </div>
                                  <div style={{ fontSize: '0.7rem', color: '#64748b', width: '40px', textAlign: 'right' }}>{r.deal_count}</div>
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </div>

                      {/* Deals Created by Rep */}
                      <div style={{ background: '#fff', border: '1px solid #eaf0f6', borderRadius: '8px', padding: '20px' }}>
                        <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#33475b', marginBottom: '4px' }}>Deals Created by Rep</h3>
                        <p style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '16px' }}>How many deals were created this quarter?</p>
                        {(analyticsData.dealsCreated || []).length === 0 ? <div style={{ color: '#7c98b6', fontSize: '13px' }}>No data</div> : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {(analyticsData.dealsCreated || []).map((r: any, i: number) => {
                              const maxC = Math.max(...(analyticsData.dealsCreated || []).map((x: any) => x.deal_count || 0), 1)
                              const w = Math.max((r.deal_count / maxC) * 100, 8)
                              const colors = ['#1976d2', '#2e7d32', '#ed6c02', '#d32f2f', '#7b1fa2']
                              return (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                  <div style={{ width: '100px', fontSize: '0.73rem', fontWeight: 500, color: '#33475b' }}>{r.rep_name || 'Unknown'}</div>
                                  <div style={{ flex: 1, background: '#f1f5f9', borderRadius: '4px', height: '24px', overflow: 'hidden' }}>
                                    <div style={{ width: `${w}%`, height: '100%', background: colors[i % colors.length], borderRadius: '4px', display: 'flex', alignItems: 'center', paddingLeft: '8px' }}>
                                      <span style={{ fontSize: '0.65rem', fontWeight: 600, color: '#fff' }}>{r.deal_count}</span>
                                    </div>
                                  </div>
                                  <div style={{ fontSize: '0.7rem', color: '#64748b', width: '70px', textAlign: 'right' }}>{currencyFormatter.format(r.total_value)}</div>
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </div>

                      {/* Win Rate Gauge */}
                      <div style={{ background: '#fff', border: '1px solid #eaf0f6', borderRadius: '8px', padding: '20px' }}>
                        <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#33475b', marginBottom: '4px' }}>Win Rate</h3>
                        <p style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '16px' }}>Percentage of deals won vs total closed</p>
                        {(() => {
                          const won = analyticsData.velocity?.total_closed ?? 0
                          const pipeData = analyticsData.pipeline || []
                          const totalInPipe = pipeData.reduce((s: number, r: any) => s + (r.deals_entered || 0), 0)
                          const pct = totalInPipe > 0 ? Math.round((won / totalInPipe) * 100) : 0
                          const angle = (pct / 100) * 180
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 0' }}>
                              <div style={{ width: '200px', height: '100px', position: 'relative', overflow: 'hidden' }}>
                                <div style={{ width: '200px', height: '200px', borderRadius: '50%', background: `conic-gradient(#2e7d32 0deg ${angle}deg, #e0e0e0 ${angle}deg 180deg, transparent 180deg)`, position: 'absolute', top: 0, left: 0 }}>
                                  <div style={{ position: 'absolute', top: '20%', left: '20%', width: '60%', height: '60%', borderRadius: '50%', background: '#fff' }} />
                                </div>
                              </div>
                              <div style={{ fontSize: '2rem', fontWeight: 800, color: pct >= 50 ? '#2e7d32' : pct >= 25 ? '#ed6c02' : '#d32f2f', marginTop: '-10px' }}>{pct}%</div>
                              <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600 }}>WIN RATE</div>
                              <div style={{ display: 'flex', gap: '24px', marginTop: '12px', fontSize: '0.78rem' }}>
                                <span style={{ color: '#2e7d32' }}>{won} won</span>
                                <span style={{ color: '#64748b' }}>{totalInPipe} total pipeline</span>
                              </div>
                            </div>
                          )
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Activity Analytics Tab */}
                  {analyticsTab === 'activity' && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px' }}>
                      {/* Team Activity Table */}
                      <div style={{ background: '#fff', border: '1px solid #eaf0f6', borderRadius: '8px', padding: '20px' }}>
                        <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#33475b', marginBottom: '4px' }}>Team Activity Summary</h3>
                        <p style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '16px' }}>Full performance breakdown by team member (this year)</p>
                        {(analyticsData.teamAct || []).length === 0 ? <div style={{ color: '#7c98b6', fontSize: '13px' }}>No data</div> : (
                          <div style={{ overflowX: 'auto' }}>
                            <table className="hs-table">
                              <thead><tr><th>Team Member</th><th>Calls</th><th>Emails</th><th>Meetings</th><th>Tasks Done</th><th>Notes</th><th>Leads</th><th>Deals Won</th><th>Revenue</th></tr></thead>
                              <tbody>{(analyticsData.teamAct || []).map((r: any, i: number) => (
                                <tr key={i}>
                                  <td style={{ fontWeight: 600 }}><div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#e3f2fd', color: '#1976d2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', fontWeight: 700, flexShrink: 0 }}>{(r.user_name || '?')[0]}</span>{r.user_name}</div></td>
                                  <td>{r.calls_made}</td>
                                  <td>{r.emails_sent}</td>
                                  <td>{r.meetings_booked}</td>
                                  <td>{r.tasks_completed}</td>
                                  <td>{r.notes_created}</td>
                                  <td>{r.leads_assigned}</td>
                                  <td style={{ fontWeight: 600, color: '#2e7d32' }}>{r.deals_won}</td>
                                  <td style={{ fontWeight: 600 }}>{currencyFormatter.format(r.revenue)}</td>
                                </tr>
                              ))}</tbody>
                            </table>
                          </div>
                        )}
                      </div>

                      {/* Activity Timeline */}
                      <div style={{ background: '#fff', border: '1px solid #eaf0f6', borderRadius: '8px', padding: '20px' }}>
                        <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#33475b', marginBottom: '16px' }}>Activity Timeline (Monthly)</h3>
                        {(analyticsData.actTimeline || []).length === 0 ? <div style={{ color: '#7c98b6', fontSize: '13px' }}>No data</div> : (() => {
                          const data = analyticsData.actTimeline || []
                          const types = ['notes', 'emails', 'calls', 'meetings', 'tasks_completed'] as const
                          const colors: Record<string, string> = { notes: '#1976d2', emails: '#2e7d32', calls: '#ed6c02', meetings: '#7b1fa2', tasks_completed: '#0288d1' }
                          const maxTotal = Math.max(...data.map((r: any) => types.reduce((s, t) => s + (r[t] || 0), 0)), 1)
                          return (
                            <>
                              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', height: '200px', borderBottom: '1px solid #eaf0f6', paddingBottom: '8px' }}>
                                {data.slice(-12).map((row: any, i: number) => {
                                  const total = types.reduce((s, t) => s + (row[t] || 0), 0)
                                  return (
                                    <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                                        {types.map(t => {
                                          const h = total > 0 ? Math.max(((row[t] || 0) / maxTotal) * 180, 0) : 0
                                          return h > 0 ? <div key={t} style={{ width: '80%', maxWidth: '28px', height: `${h}px`, background: colors[t] }} title={`${t}: ${row[t]}`} /> : null
                                        })}
                                      </div>
                                      <div style={{ fontSize: '0.55rem', color: '#94a3b8', marginTop: '6px' }}>{row.period?.substring(5)}</div>
                                    </div>
                                  )
                                })}
                              </div>
                              <div style={{ display: 'flex', gap: '16px', marginTop: '12px', justifyContent: 'center' }}>
                                {types.map(t => (
                                  <span key={t} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.72rem' }}>
                                    <span style={{ width: '10px', height: '10px', borderRadius: '2px', background: colors[t], display: 'inline-block' }} />
                                    {t === 'tasks_completed' ? 'Tasks' : t.charAt(0).toUpperCase() + t.slice(1)}
                                  </span>
                                ))}
                              </div>
                            </>
                          )
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Pipeline Analytics Tab */}
                  {analyticsTab === 'pipeline' && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(440px, 1fr))', gap: '20px' }}>
                      <div style={{ background: '#fff', border: '1px solid #eaf0f6', borderRadius: '8px', padding: '20px' }}>
                        <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#33475b', marginBottom: '4px' }}>Pipeline Overview</h3>
                        <p style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '16px' }}>Current deal distribution across pipeline stages</p>
                        {(analyticsData.pipeline || []).length === 0 ? <div style={{ color: '#7c98b6', fontSize: '13px' }}>No data</div> : (
                          <table className="hs-table">
                            <thead><tr><th>Stage</th><th>Deals</th><th>Avg Days</th><th>Value</th><th>Conv. %</th></tr></thead>
                            <tbody>{(analyticsData.pipeline || []).map((row: any, i: number) => (
                              <tr key={i}>
                                <td style={{ fontWeight: 500 }}>{row.stage_name}</td>
                                <td>{row.deals_entered}</td>
                                <td>{(row.avg_days || 0).toFixed(1)}</td>
                                <td>{currencyFormatter.format(row.total_value || 0)}</td>
                                <td>{((row.conversion_rate || 0) * 100).toFixed(1)}%</td>
                              </tr>
                            ))}</tbody>
                          </table>
                        )}
                      </div>
                      <div style={{ background: '#fff', border: '1px solid #eaf0f6', borderRadius: '8px', padding: '20px' }}>
                        <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#33475b', marginBottom: '4px' }}>Pipeline Funnel</h3>
                        <p style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '16px' }}>Visual funnel of deal progression</p>
                        {(analyticsData.pipeline || []).length === 0 ? <div style={{ color: '#7c98b6', fontSize: '13px' }}>No data</div> : (() => {
                          const stages = (analyticsData.pipeline || []).filter((s: any) => s.deals_entered > 0)
                          const maxV = Math.max(...stages.map((s: any) => s.deals_entered), 1)
                          const colors = ['#0d47a1', '#1565c0', '#1976d2', '#1e88e5', '#2196f3', '#42a5f5', '#64b5f6', '#90caf9']
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center', padding: '10px 0' }}>
                              {stages.map((s: any, i: number) => {
                                const w = Math.max((s.deals_entered / maxV) * 100, 25)
                                return (
                                  <div key={i} style={{ width: `${w}%`, background: colors[i % colors.length], padding: '10px 16px', borderRadius: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'width 0.5s ease', minWidth: '120px' }}>
                                    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.stage_name}</span>
                                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#fff', flexShrink: 0, marginLeft: '8px' }}>{s.deals_entered}</span>
                                  </div>
                                )
                              })}
                            </div>
                          )
                        })()}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* ========== Workflows View (Enhanced — 6-row template) ========== */}
        {activeView === 'workflows' && editingWorkflowId === null && (() => {
          const wfSearchLower = wfSearch.toLowerCase()
          const wfFiltered = workflowsData
            .filter(w => {
              if (workflowTab === 'active' && !w.is_active) return false
              if (workflowTab === 'inactive' && w.is_active) return false
              if (wfStatusFilter === 'active' && !w.is_active) return false
              if (wfStatusFilter === 'inactive' && w.is_active) return false
              if (wfSearchLower && !(w.name || '').toLowerCase().includes(wfSearchLower) && !(w.description || '').toLowerCase().includes(wfSearchLower) && !(w.object_type || '').toLowerCase().includes(wfSearchLower)) return false
              return true
            })
            .sort((a, b) => {
              const f = wfSort.field
              const av = (a as any)[f] ?? ''
              const bv = (b as any)[f] ?? ''
              const cmp = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv))
              return wfSort.order === 'ASC' ? cmp : -cmp
            })
          const wfTotal = wfFiltered.length
          const wfPages = Math.max(1, Math.ceil(wfTotal / wfPageSize))
          const wfPageClamped = Math.min(wfPage, wfPages)
          const wfPageData = wfFiltered.slice((wfPageClamped - 1) * wfPageSize, wfPageClamped * wfPageSize)
          const toggleWfSort = (field: string) => setWfSort(prev => ({ field, order: prev.field === field && prev.order === 'ASC' ? 'DESC' : 'ASC' }))
          const toggleAllWfRows = () => {
            if (wfSelectedRows.size === wfPageData.length) setWfSelectedRows(new Set())
            else setWfSelectedRows(new Set(wfPageData.map(w => w.id)))
          }
          const toggleWfRow = (id: number) => setWfSelectedRows(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })
          const onWfResizeStart = (col: string, e: React.MouseEvent) => {
            e.preventDefault(); e.stopPropagation()
            const startX = e.clientX; const startW = (wfColWidths as any)[col]
            const onMove = (ev: MouseEvent) => { const diff = ev.clientX - startX; setWfColWidths(prev => ({ ...prev, [col]: Math.max(60, startW + diff) })) }
            const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp) }
            document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp)
          }
          const wfSortArrow = <svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M29.38 21.96a1.147 1.147 0 0 0-1.617 0l-3.85 3.852V5.714a1.143 1.143 0 0 0-2.286 0v20.099l-3.853-3.852a1.144 1.144 0 0 0-1.615 1.617l5.803 5.802a1.143 1.143 0 0 0 1.616 0l5.802-5.802a1.144 1.144 0 0 0 0-1.617M15.84 8.422 10.039 2.62a1.145 1.145 0 0 0-1.616 0L2.62 8.422a1.144 1.144 0 0 0 1.617 1.617l3.85-3.851v20.098a1.143 1.143 0 0 0 2.286 0V6.187l3.853 3.852a1.14 1.14 0 0 0 1.616-.001 1.14 1.14 0 0 0-.001-1.616"/></svg>

          return (
          <div className="hs-contacts">
            {/* ROW 1 — Topbar */}
            <div className="hs-topbar">
              <div className="hs-topbar-left">
                <div style={{ display: 'flex', alignItems: 'center', padding: '0 12px', gap: '6px' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="16,18 22,12 16,6"/><polyline points="8,6 2,12 8,18"/></svg>
                  <span style={{ fontWeight: 600, fontSize: '0.92rem', color: '#33475b' }}>Workflows</span>
                </div>
                <div className="hs-topbar-sep" />
                <div className="hs-view-tabs">
                  {(['all', 'active', 'inactive'] as const).map(t => {
                    const isActive = workflowTab === t
                    const tabCount = t === 'all' ? wfTotal : t === 'active' ? workflowsData.filter(w => w.is_active).length : workflowsData.filter(w => !w.is_active).length
                    return (
                      <div key={t} className={`hs-view-tab${isActive ? ' active' : ''}`} ref={isActive ? tabEditRef : undefined} onClick={() => { setWorkflowTab(t); setWfPage(1) }}>
                        <span className="hs-tab-label">{t === 'all' ? 'All workflows' : t.charAt(0).toUpperCase() + t.slice(1)}</span>
                        {isActive && tabCount > 0 && <span className="hs-tab-count">{tabCount.toLocaleString()}</span>}
                        {isActive && (
                          <span className="hs-tab-kebab" onClick={(e) => { e.stopPropagation(); setTabEditOpen(!tabEditOpen) }}>
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>
                          </span>
                        )}
                        {t !== 'all' && <span className="hs-tab-x" onClick={(e) => { e.stopPropagation(); setWorkflowTab('all'); setWfPage(1) }}>&times;</span>}
                        {isActive && tabEditOpen && (
                          <div className="hs-tab-edit-dropdown">
                            <button className="hs-tab-edit-item" disabled>Rename</button>
                            <button className="hs-tab-edit-item">Clone</button>
                            <button className="hs-tab-edit-item" disabled>Manage Sharing</button>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
                <div className="hs-topbar-sep" />
                <button className="hs-quick-add-btn" title="Add view" onClick={() => {}}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                </button>
              </div>
              <div className="hs-topbar-right">
                <button className="hs-add-contacts-btn" onClick={() => setShowCreateWorkflow(true)}>
                  Create workflow
                </button>
              </div>
            </div>

            {/* ROW 2 — Toolbar */}
            <div className="hs-toolbar">
              <div className="hs-toolbar-left">
                <div className="hs-search-wrap">
                  <svg className="hs-search-icon" width="14" height="14" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M28.14 29.29c-.29 0-.58-.11-.81-.33l-7.58-7.58a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l7.58 7.58a1.14 1.14 0 0 1-.81 1.95"/><path d="M13.29 24.71c-6.3 0-11.43-5.13-11.43-11.43S6.98 1.86 13.29 1.86s11.43 5.13 11.43 11.43-5.13 11.43-11.43 11.43Zm0-20.57c-5.04 0-9.14 4.1-9.14 9.14s4.1 9.14 9.14 9.14 9.14-4.1 9.14-9.14-4.1-9.14-9.14-9.14"/></svg>
                  <input className="hs-search-input" placeholder="Search workflows..." value={wfSearch} onChange={(e) => { setWfSearch(e.target.value); setWfPage(1) }} />
                  {wfSearch && <button className="hs-search-clear" onClick={() => { setWfSearch(''); setWfPage(1) }}>&times;</button>}
                </div>
                <div className="hs-tb-sep" />
                <div className="hs-viewtype-group">
                  <button className="hs-tb-btn hs-viewtype-btn">
                    <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                    <span>Table</span>
                  </button>
                  <button className={`hs-tb-btn hs-settings-btn${wfSettingsOpen ? ' active' : ''}`} onClick={() => { setWfSettingsOpen(!wfSettingsOpen); setWfPreview(null) }} title="Table settings">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                  </button>
                </div>
                <div className="hs-tb-sep" />
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Edit columns">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="18" rx="1"/><rect x="14" y="3" width="7" height="18" rx="1"/></svg>
                  <span>Edit columns</span>
                </button>
                <div className="hs-tb-sep" />
                <button className={`hs-tb-btn hs-tb-btn-bordered${wfQuickFiltersOpen ? ' hs-tb-btn-pressed' : ''}`} title="Filters" onClick={() => setWfQuickFiltersOpen(!wfQuickFiltersOpen)}>
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M30.71 16.86H13.57a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28M6.71 16.86H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M10.14 20.29c-2.52 0-4.57-2.05-4.57-4.57s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29"/></svg>
                  <span>Filters</span>
                </button>
                <div className="hs-tb-sep" />
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Export" onClick={() => {
                  const headers = ['Name','Status','Object Type','Trigger','Created','Updated']
                  const escCsv = (v: unknown) => { const s = String(v ?? ''); return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s }
                  const rows = wfFiltered.map(w => [w.name, w.is_active ? 'Active' : 'Inactive', w.object_type, w.trigger_type, w.created_at || '', w.updated_at || ''].map(escCsv).join(','))
                  const csv = [headers.join(','), ...rows].join('\n')
                  const blob = new Blob([csv], { type: 'text/csv' }); const url = URL.createObjectURL(blob)
                  const a = document.createElement('a'); a.href = url; a.download = `workflows-export-${new Date().toISOString().slice(0,10)}.csv`; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url)
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  <span>Export</span>
                </button>
              </div>
              <div className="hs-toolbar-right">
                <div className="hs-tb-sep" />
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Clone view">
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M25.86 29.29H12.15c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-1.89 1.54-3.43 3.43-3.43h13.71c1.89 0 3.43 1.54 3.43 3.43v13.71c0 1.89-1.54 3.43-3.43 3.43M12.14 11c-.63 0-1.14.51-1.14 1.14v13.71c0 .63.51 1.14 1.14 1.14h13.71c.63 0 1.14-.51 1.14-1.14V12.14c0-.63-.51-1.14-1.14-1.14z"/><path d="M5.29 22.43c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43H19c1.89 0 3.43 1.54 3.43 3.43a1.14 1.14 0 1 1-2.28 0c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14V19c0 .63.51 1.14 1.14 1.14a1.14 1.14 0 1 1 0 2.28Z"/></svg>
                </button>
                <button className="hs-tb-btn hs-tb-btn-bordered disabled" disabled title="Save view">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                </button>
              </div>
            </div>

            {/* ROW 3 — Quick filters */}
            {wfQuickFiltersOpen && (
            <div className="hs-quick-filters">
              <button
                className={`hs-qf-btn hs-qf-trigger${wfStatusFilter ? ' has-value' : ''}`}
                onClick={() => {
                  const vals = ['', 'active', 'inactive']
                  const idx = vals.indexOf(wfStatusFilter)
                  setWfStatusFilter(vals[(idx + 1) % vals.length])
                  setWfPage(1)
                }}
              >
                <span>{wfStatusFilter === 'active' ? 'Active' : wfStatusFilter === 'inactive' ? 'Inactive' : 'Status'}</span>
                {wfStatusFilter && <span className="hs-qf-clear" onClick={(e) => { e.stopPropagation(); setWfStatusFilter(''); setWfPage(1) }}>&times;</span>}
                {!wfStatusFilter && <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>}
              </button>
              <button
                className={`hs-qf-btn hs-qf-trigger${wfSort.field !== 'updated_at' ? ' has-value' : ''}`}
                onClick={() => {
                  const fields = ['updated_at', 'name', 'created_at', 'object_type']
                  const idx = fields.indexOf(wfSort.field)
                  const next = fields[(idx + 1) % fields.length]
                  setWfSort({ field: next, order: next === 'name' ? 'ASC' : 'DESC' })
                  setWfPage(1)
                }}
              >
                <span>Sort: {{ updated_at: 'Updated', name: 'Name', created_at: 'Created', object_type: 'Object' }[wfSort.field] || 'Updated'}</span>
                <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
              </button>
              <div className="hs-qf-sep" />
              <button className="hs-qf-btn hs-qf-more">
                <svg width="10" height="10" viewBox="0 0 32 32" fill="currentColor"><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V3a1.14 1.14 0 1 1 2.28 0v25.14c0 .63-.51 1.14-1.14 1.14Z"/><path d="M28.14 16.71H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28"/></svg>
                <span>More</span>
              </button>
            </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px', background: '#fffdf7', borderBottom: '1px solid #f1e8d8' }}>
              <span style={{ fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px', background: '#fff3e0', color: '#b65815', border: '1px solid #ffd9a8', textTransform: 'uppercase', letterSpacing: '0.5px', flexShrink: 0 }}>Coming soon</span>
              <span style={{ fontSize: '0.78rem', color: '#7c98b6' }}>Workflow execution isn&apos;t wired up yet — workflows don&apos;t run when their triggers fire, and a workflow can&apos;t be turned on. Building and editing works normally.</span>
            </div>
            {/* Main content area */}
            <div className={`hs-content${wfPreview ? ' with-preview' : ''}${wfSettingsOpen ? ' with-settings' : ''}`}>
              <div className="hs-table-area">
                {/* ROW 4 — Selection bar */}
                {wfSelectedRows.size > 0 && (
                  <div className="hs-selection-bar">
                    <span className="hs-sel-count">{wfSelectedRows.size} selected</span>
                    <div className="hs-sel-sep" />
                    <button className="hs-sel-btn" title="Deactivate selected — activation is coming soon (execution engine not wired up yet)" onClick={() => {
                      const ids = [...wfSelectedRows]
                      Promise.all(ids.map(id => updateWorkflow(id, { isActive: 0 }))).then(() => {
                        setWorkflowsData(prev => prev.map(w => wfSelectedRows.has(w.id) ? { ...w, is_active: 0 } : w))
                        setWfSelectedRows(new Set())
                      })
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                      Deactivate
                    </button>
                    <button className="hs-sel-btn" style={{ color: '#dc2626' }} onClick={() => {
                      if (confirm(`Delete ${wfSelectedRows.size} workflow(s)?`)) {
                        Promise.all([...wfSelectedRows].map(id => deleteWorkflow(id))).then(() => {
                          setWorkflowsData(prev => prev.filter(w => !wfSelectedRows.has(w.id)))
                          setWfSelectedRows(new Set())
                        })
                      }
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                      Delete
                    </button>
                    <button className="hs-sel-close" onClick={() => setWfSelectedRows(new Set())} title="Deselect all">&times;</button>
                  </div>
                )}

                {/* ROW 5 — Data table */}
                <div className="hs-table-scroll">
                  <table className={`hs-table hs-density-${wfRowHeight}${wfZebra ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                      <col style={{ width: 40 }} />
                      <col style={{ width: wfColWidths.name }} />
                      <col style={{ width: wfColWidths.status }} />
                      <col style={{ width: wfColWidths.object_type }} />
                      <col style={{ width: wfColWidths.trigger }} />
                      <col style={{ width: wfColWidths.created_at }} />
                      <col style={{ width: wfColWidths.updated_at }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th className="hs-th-check">
                          <input type="checkbox" checked={wfPageData.length > 0 && wfSelectedRows.size === wfPageData.length} onChange={toggleAllWfRows} />
                        </th>
                        <th className="hs-th-resizable hs-th-sortable" style={{ position: 'sticky', left: 40, top: 0, zIndex: 15, background: '#f8fafc', boxShadow: '2px 0 4px rgba(0,0,0,0.06)' }} onClick={() => toggleWfSort('name')}>
                          <span className="hs-th-content">
                            Name
                            {wfSort.field === 'name' && <span className="hs-sort-arrow">{wfSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                            {wfSort.field !== 'name' && <span className="hs-th-hover-sort">{wfSortArrow}</span>}
                          </span>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onWfResizeStart('name', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                        <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleWfSort('is_active')}>
                          <span className="hs-th-content">
                            Status
                            {wfSort.field === 'is_active' && <span className="hs-sort-arrow">{wfSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                            {wfSort.field !== 'is_active' && <span className="hs-th-hover-sort">{wfSortArrow}</span>}
                          </span>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onWfResizeStart('status', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                        <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleWfSort('object_type')}>
                          <span className="hs-th-content">
                            Object type
                            {wfSort.field === 'object_type' && <span className="hs-sort-arrow">{wfSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                            {wfSort.field !== 'object_type' && <span className="hs-th-hover-sort">{wfSortArrow}</span>}
                          </span>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onWfResizeStart('object_type', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                        <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleWfSort('trigger_type')}>
                          <span className="hs-th-content">
                            Trigger
                            {wfSort.field === 'trigger_type' && <span className="hs-sort-arrow">{wfSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                            {wfSort.field !== 'trigger_type' && <span className="hs-th-hover-sort">{wfSortArrow}</span>}
                          </span>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onWfResizeStart('trigger', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                        <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleWfSort('created_at')}>
                          <span className="hs-th-content">
                            Created
                            {wfSort.field === 'created_at' && <span className="hs-sort-arrow">{wfSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                            {wfSort.field !== 'created_at' && <span className="hs-th-hover-sort">{wfSortArrow}</span>}
                          </span>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onWfResizeStart('created_at', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                        <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleWfSort('updated_at')}>
                          <span className="hs-th-content">
                            Updated
                            {wfSort.field === 'updated_at' && <span className="hs-sort-arrow">{wfSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                            {wfSort.field !== 'updated_at' && <span className="hs-th-hover-sort">{wfSortArrow}</span>}
                          </span>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onWfResizeStart('updated_at', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {workflowsLoading ? (
                        <tr><td colSpan={8} className="hs-loading-cell"><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}><DotLottieReact src="/loading.lottie" loop autoplay style={{ width: 80, height: 80 }} /><span style={{ fontSize: '0.82rem', color: '#7c98b6', marginTop: 8 }}>Loading workflows...</span></div></td></tr>
                      ) : wfPageData.length === 0 ? (
                        <tr><td colSpan={8} className="hs-empty-cell">
                          <div style={{ padding: '60px 40px', textAlign: 'center' }}>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" style={{ margin: '0 auto 16px' }}><polyline points="16,18 22,12 16,6"/><polyline points="8,6 2,12 8,18"/></svg>
                            <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#33475b', margin: '0 0 8px' }}>Create your first workflow</h3>
                            <p style={{ fontSize: '0.82rem', color: '#7c98b6', margin: '0 0 20px', maxWidth: '400px', marginLeft: 'auto', marginRight: 'auto' }}>Automate repetitive tasks like sending emails, creating tasks, and updating records when specific events occur.</p>
                            <button className="hs-add-contacts-btn" onClick={() => setShowCreateWorkflow(true)}>Create workflow</button>
                          </div>
                        </td></tr>
                      ) : wfPageData.map((w) => (
                        <tr key={w.id} className={`hs-row${wfSelectedRows.has(w.id) ? ' selected' : ''}`}>
                          <td className="hs-td-check">
                            <input type="checkbox" checked={wfSelectedRows.has(w.id)} onChange={() => toggleWfRow(w.id)} />
                          </td>
                          <td className="hs-td-name" style={{ position: 'sticky', left: 40, zIndex: 4, boxShadow: '2px 0 4px rgba(0,0,0,0.06)', background: wfSelectedRows.has(w.id) ? '#eef2ff' : 'inherit' }}>
                            <div className="hs-name-cell">
                              <div className="hs-avatar" style={{ background: w.is_active ? '#f59e0b' : '#94a3b8' }}>
                                {(w.name || '?')[0].toUpperCase()}
                              </div>
                              <span className="hs-name-link" onClick={() => setEditingWorkflowId(w.id)}>
                                {w.name || <span className="cell-empty">--</span>}
                              </span>
                              <button className="hs-preview-btn" onClick={(e) => { e.stopPropagation(); setWfSettingsOpen(false); setWfPreview(wfPreview?.id === w.id ? null : w) }}>Preview</button>
                            </div>
                          </td>
                          <td>
                            <span className="hs-status-badge" style={{ background: w.is_active ? '#fff3e0' : '#f5f8fa', color: w.is_active ? '#b65815' : '#7c98b6' }}>
                              {w.is_active ? 'Not running' : 'Inactive'}
                            </span>
                          </td>
                          <td style={{ color: '#516f90', fontSize: '0.8rem' }}>{w.object_type || <span className="cell-empty">--</span>}</td>
                          <td style={{ color: '#516f90', fontSize: '0.8rem' }}>{w.trigger_type || <span className="cell-empty">--</span>}</td>
                          <td style={{ color: '#516f90', fontSize: '0.75rem' }}>{w.created_at ? formatDatePref(w.created_at) : <span className="cell-empty">--</span>}</td>
                          <td style={{ color: '#516f90', fontSize: '0.75rem' }}>{w.updated_at ? formatDatePref(w.updated_at) : <span className="cell-empty">--</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Preview panel */}
              {wfPreview && (
                <div className="hs-preview">
                  <div className="hs-preview-header">
                    <span className="hs-preview-title">{wfPreview.name}</span>
                    <button className="hs-preview-close" onClick={() => setWfPreview(null)}>&times;</button>
                  </div>
                  <div className="hs-preview-body">
                    <div className="hs-preview-field"><label>Status</label><span className="hs-status-badge" style={{ background: wfPreview.is_active ? '#fff3e0' : '#f5f8fa', color: wfPreview.is_active ? '#b65815' : '#7c98b6' }}>{wfPreview.is_active ? 'Not running' : 'Inactive'}</span></div>
                    <div className="hs-preview-field"><label>Object type</label><span>{wfPreview.object_type}</span></div>
                    <div className="hs-preview-field"><label>Trigger</label><span>{wfPreview.trigger_type}</span></div>
                    {wfPreview.description && <div className="hs-preview-field"><label>Description</label><span>{wfPreview.description}</span></div>}
                    <div className="hs-preview-field"><label>Created</label><span>{wfPreview.created_at ? formatDatePref(wfPreview.created_at) : '--'}</span></div>
                    <div className="hs-preview-field"><label>Updated</label><span>{wfPreview.updated_at ? formatDatePref(wfPreview.updated_at) : '--'}</span></div>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                      <button className="hs-add-contacts-btn" style={{ fontSize: '0.78rem', padding: '6px 14px' }} onClick={() => setEditingWorkflowId(wfPreview.id)}>Edit workflow</button>
                      <button disabled={!wfPreview.is_active} title={wfPreview.is_active ? undefined : "Coming soon — the workflow execution engine isn't wired up yet"} style={{ padding: '6px 14px', border: '1px solid #e2e8f0', borderRadius: '4px', background: '#fff', color: '#64748b', fontSize: '0.78rem', opacity: wfPreview.is_active ? 1 : 0.55, cursor: wfPreview.is_active ? 'pointer' : 'not-allowed' }}
                        onClick={() => { updateWorkflow(wfPreview.id, { isActive: !wfPreview.is_active }).then(() => { setWorkflowsData(prev => prev.map(x => x.id === wfPreview.id ? { ...x, is_active: x.is_active ? 0 : 1 } : x)); setWfPreview((p: any) => p ? { ...p, is_active: p.is_active ? 0 : 1 } : null) }) }}>
                        {wfPreview.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Settings panel */}
              {wfSettingsOpen && (
                <div className="hs-settings-panel">
                  <div className="hs-settings-header">
                    <span className="hs-settings-title">Table settings</span>
                    <button className="hs-settings-close" onClick={() => setWfSettingsOpen(false)}>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                    </button>
                  </div>
                  <div className="hs-settings-body">
                    <div className="hs-settings-group">
                      <label className="hs-settings-label">Pagination</label>
                      {([100, 200, 500] as const).map(size => (
                        <label className="hs-settings-radio" key={size}>
                          <input type="radio" name="wfPageSizeSettings" checked={wfPageSize === size} onChange={() => { setWfPageSize(size); setWfPage(1) }} />
                          <span className="hs-radio-indicator" />
                          <span>{size} per page</span>
                        </label>
                      ))}
                    </div>
                    <hr className="hs-settings-divider" />
                    <div className="hs-settings-group">
                      <label className="hs-settings-label">Row height</label>
                      {([{ value: 'small' as const, label: 'Compact' }, { value: 'medium' as const, label: 'Default' }, { value: 'large' as const, label: 'Comfortable' }]).map(opt => (
                        <label className="hs-settings-radio" key={opt.value}>
                          <input type="radio" name="wfRowHeightSettings" checked={wfRowHeight === opt.value} onChange={() => setWfRowHeight(opt.value)} />
                          <span className="hs-radio-indicator" />
                          <span>{opt.label}</span>
                        </label>
                      ))}
                    </div>
                    <hr className="hs-settings-divider" />
                    <div className="hs-settings-group">
                      <div className="hs-settings-toggle-row">
                        <label className="hs-settings-label">Zebra striping</label>
                        <button className={`hs-toggle${wfZebra ? ' on' : ''}`} onClick={() => setWfZebra(!wfZebra)} role="switch" aria-checked={wfZebra}>
                          <span className="hs-toggle-track">
                            <span className="hs-toggle-on">ON</span>
                            <span className="hs-toggle-off">OFF</span>
                            <span className="hs-toggle-handle" />
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ROW 6 — Pagination */}
            <div className="hs-pagination">
              <nav className="hs-paginator" aria-label="Pagination">
                <button className="hs-paginator-btn" disabled={wfPageClamped <= 1} onClick={() => setWfPage(p => p - 1)} aria-label="Previous page">
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95"/></svg>
                  <span>Prev</span>
                </button>
                {(() => {
                  if (wfPages <= 7) {
                    const pages: number[] = []; for (let i = 1; i <= wfPages; i++) pages.push(i)
                    return pages.map(p => <button key={p} className={`hs-paginator-num${p === wfPageClamped ? ' active' : ''}`} onClick={() => setWfPage(p)}>{p}</button>)
                  }
                  const items: React.ReactNode[] = []
                  const shown = new Set<number>()
                  for (let i = 1; i <= Math.min(4, wfPages); i++) shown.add(i)
                  shown.add(wfPages)
                  for (let i = Math.max(1, wfPageClamped - 1); i <= Math.min(wfPages, wfPageClamped + 1); i++) shown.add(i)
                  const sorted = [...shown].sort((a, b) => a - b)
                  for (let i = 0; i < sorted.length; i++) {
                    if (i > 0 && sorted[i] - sorted[i - 1] > 1) items.push(<span key={`e${i}`} className="hs-paginator-ellipsis">...</span>)
                    items.push(<button key={sorted[i]} className={`hs-paginator-num${sorted[i] === wfPageClamped ? ' active' : ''}`} onClick={() => setWfPage(sorted[i])}>{sorted[i]}</button>)
                  }
                  return items
                })()}
                <button className="hs-paginator-btn" disabled={wfPageClamped >= wfPages} onClick={() => setWfPage(p => p + 1)} aria-label="Next page">
                  <span>Next</span>
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M11 27.71c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62l9.48-9.48-9.48-9.47a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0L22.1 15.48c.45.45.45 1.17 0 1.62L11.81 27.39c-.22.22-.52.33-.81.33Z"/></svg>
                </button>
              </nav>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', marginLeft: 'auto', marginRight: 50 }}><select value={wfPageSize} onChange={e => { setWfPageSize(Number(e.target.value) as any); setWfPage(1) }} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #e2e8f0', fontSize: '0.82rem' }}><option value={100}>100</option><option value={200}>200</option><option value={500}>500</option></select><span>per page</span></div>
            </div>
          </div>
          )
        })()}

        {/* Workflow Canvas Editor */}
        {activeView === 'workflows' && editingWorkflowId !== null && (
          <div className="wf-editor-container">
            <div className="wf-editor-topbar">
              <div className="wf-editor-topbar-left">
                <button className="wf-editor-btn" onClick={() => { setEditingWorkflowId(null); setWorkflowEditorWorkflow(null); setWorkflowEditorActions([]); setWfSidebarMode(null); setWfSelectedNode(null) }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5"/><polyline points="12 19 5 12 12 5"/></svg> Back
                </button>
              </div>
              <div className="wf-editor-topbar-center">
                <span>{workflowEditorWorkflow?.name || 'New Workflow'}</span>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: workflowEditorWorkflow?.is_active ? '#f59e0b' : '#94a3b8', display: 'inline-block' }} />
              </div>
              <div className="wf-editor-topbar-right">
                <button className="wf-editor-btn" onClick={() => {
                  if (!workflowEditorWorkflow) return
                  saveWorkflowCanvas(workflowEditorWorkflow.id, {
                    actions: workflowEditorActions.map((a, i) => ({ ...a, action_order: i + 1 })),
                    name: workflowEditorWorkflow.name,
                  }).then((wf) => { setWorkflowEditorWorkflow(wf); setWorkflowEditorActions(wf.actions || []) })
                }}>Save</button>
                <button className="wf-editor-btn primary" disabled={!workflowEditorWorkflow?.is_active} title={workflowEditorWorkflow?.is_active ? undefined : "Coming soon — the workflow execution engine isn't wired up yet"} onClick={() => {
                  if (!workflowEditorWorkflow) return
                  updateWorkflow(workflowEditorWorkflow.id, { isActive: !workflowEditorWorkflow.is_active })
                    .then((wf) => setWorkflowEditorWorkflow(wf))
                }}>
                  {workflowEditorWorkflow?.is_active ? 'Turn off' : 'Turn on'}
                </button>
              </div>
            </div>
            <div className="wf-editor-body">
              {/* Left sidebar for action selection */}
              {wfSidebarMode === 'actions' && (
                <div className="wf-sidebar">
                  <div className="wf-sidebar-header">
                    <h3>Choose an action</h3>
                    <button style={{ border: 'none', background: 'none', color: '#64748b', cursor: 'pointer', fontSize: '0.78rem' }} onClick={() => setWfSidebarMode(null)}>Cancel</button>
                  </div>
                  <div className="wf-sidebar-body">
                    <div className="wf-quick-actions">
                      {[
                        { type: 'delay', label: 'Delay', icon: '\u23F1' },
                        { type: 'branch_logic', label: 'Branch', icon: '\u2B95' },
                        { type: 'send_email', label: 'Send email', icon: '\u2709' },
                        { type: 'create_task', label: 'Create task', icon: '\u2611' },
                      ].map(q => (
                        <div key={q.type} className="wf-quick-action-card" onClick={() => {
                          const newAction: WorkflowAction & { canvas_x?: number; canvas_y?: number } = { id: Date.now(), workflow_id: editingWorkflowId || 0, action_order: wfInsertIndex + 1, action_type: q.type, action_config_json: '{}' }
                          const updated = [...workflowEditorActions]
                          updated.splice(wfInsertIndex, 0, newAction)
                          setWorkflowEditorActions(updated)
                          setWfSidebarMode(null)
                        }}>
                          <span style={{ fontSize: '18px' }}>{q.icon}</span>
                          <span>{q.label}</span>
                        </div>
                      ))}
                    </div>
                    {/* Action categories */}
                    {[
                      { cat: 'Communications', color: '#f3e8ff', iconColor: '#7c3aed', items: [
                        { type: 'send_email', label: 'Send email' }, { type: 'send_notification', label: 'Send notification' },
                        { type: 'send_internal_email', label: 'Internal email' }, { type: 'send_sms', label: 'Send SMS' },
                      ]},
                      { cat: 'CRM', color: '#ecfdf5', iconColor: '#059669', items: [
                        { type: 'create_record', label: 'Create record' }, { type: 'create_task', label: 'Create task' },
                        { type: 'create_note', label: 'Create note' }, { type: 'edit_record', label: 'Edit record' },
                        { type: 'clear_property', label: 'Clear property' }, { type: 'rotate_owner', label: 'Rotate owner' },
                      ]},
                      { cat: 'Marketing', color: '#fef2f2', iconColor: '#dc2626', items: [
                        { type: 'set_lifecycle_stage', label: 'Set lifecycle stage' }, { type: 'set_lead_status', label: 'Set lead status' },
                        { type: 'add_to_list', label: 'Add to list' }, { type: 'remove_from_list', label: 'Remove from list' },
                      ]},
                      { cat: 'Data Ops', color: '#fffbeb', iconColor: '#d97706', items: [
                        { type: 'format_data', label: 'Format data' }, { type: 'webhook', label: 'Webhook' },
                        { type: 'calculate_value', label: 'Calculate value' },
                      ]},
                    ].map(cat => (
                      <div key={cat.cat} className="wf-action-category">
                        <div className="wf-action-category-header">
                          <div className="wf-action-category-icon" style={{ background: cat.color, color: cat.iconColor }}>{cat.cat[0]}</div>
                          <span>{cat.cat}</span>
                        </div>
                        {cat.items.map(item => (
                          <div key={item.type} className="wf-action-item" onClick={() => {
                            const newAction: WorkflowAction & { canvas_x?: number; canvas_y?: number } = { id: Date.now(), workflow_id: editingWorkflowId || 0, action_order: wfInsertIndex + 1, action_type: item.type, action_config_json: '{}' }
                            const updated = [...workflowEditorActions]
                            updated.splice(wfInsertIndex, 0, newAction)
                            setWorkflowEditorActions(updated)
                            setWfSidebarMode(null)
                          }}>
                            {item.label}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Action config panel */}
              {wfSidebarMode === 'config' && wfSelectedNode !== null && (() => {
                const action = workflowEditorActions.find(a => a.id === wfSelectedNode)
                if (!action) return null
                const config = action.action_config_json ? JSON.parse(action.action_config_json) : {}
                return (
                  <div className="wf-sidebar">
                    <div className="wf-sidebar-header">
                      <h3>{action.action_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</h3>
                      <button style={{ border: 'none', background: 'none', color: '#64748b', cursor: 'pointer', fontSize: '0.78rem' }} onClick={() => { setWfSidebarMode(null); setWfSelectedNode(null) }}>Close</button>
                    </div>
                    <div className="wf-sidebar-body">
                      {action.action_type === 'delay' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#33475b' }}>Delay duration</label>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <input type="number" min="0" defaultValue={config.days || 1} style={{ width: '80px', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', fontSize: '0.84rem' }}
                              onChange={(e) => {
                                const updated = workflowEditorActions.map(a => a.id === wfSelectedNode ? { ...a, action_config_json: JSON.stringify({ ...config, days: Number(e.target.value) }) } : a)
                                setWorkflowEditorActions(updated)
                              }} />
                            <select defaultValue={config.unit || 'days'} style={{ padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', fontSize: '0.84rem' }}
                              onChange={(e) => {
                                const updated = workflowEditorActions.map(a => a.id === wfSelectedNode ? { ...a, action_config_json: JSON.stringify({ ...config, unit: e.target.value }) } : a)
                                setWorkflowEditorActions(updated)
                              }}>
                              <option value="minutes">Minutes</option>
                              <option value="hours">Hours</option>
                              <option value="days">Days</option>
                              <option value="weeks">Weeks</option>
                            </select>
                          </div>
                        </div>
                      )}
                      {(action.action_type === 'send_email' || action.action_type === 'send_internal_email') && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#33475b' }}>Subject</label>
                          <input type="text" defaultValue={config.subject || ''} placeholder="Email subject..." style={{ padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', fontSize: '0.84rem' }}
                            onChange={(e) => {
                              const updated = workflowEditorActions.map(a => a.id === wfSelectedNode ? { ...a, action_config_json: JSON.stringify({ ...config, subject: e.target.value }) } : a)
                              setWorkflowEditorActions(updated)
                            }} />
                          <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#33475b' }}>Body</label>
                          <textarea defaultValue={config.body || ''} placeholder="Email body..." rows={6} style={{ padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', fontSize: '0.84rem', resize: 'vertical' }}
                            onChange={(e) => {
                              const updated = workflowEditorActions.map(a => a.id === wfSelectedNode ? { ...a, action_config_json: JSON.stringify({ ...config, body: e.target.value }) } : a)
                              setWorkflowEditorActions(updated)
                            }} />
                        </div>
                      )}
                      {action.action_type === 'create_task' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#33475b' }}>Task title</label>
                          <input type="text" defaultValue={config.title || ''} placeholder="Task title..." style={{ padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', fontSize: '0.84rem' }}
                            onChange={(e) => {
                              const updated = workflowEditorActions.map(a => a.id === wfSelectedNode ? { ...a, action_config_json: JSON.stringify({ ...config, title: e.target.value }) } : a)
                              setWorkflowEditorActions(updated)
                            }} />
                          <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#33475b' }}>Priority</label>
                          <select defaultValue={config.priority || 'medium'} style={{ padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', fontSize: '0.84rem' }}
                            onChange={(e) => {
                              const updated = workflowEditorActions.map(a => a.id === wfSelectedNode ? { ...a, action_config_json: JSON.stringify({ ...config, priority: e.target.value }) } : a)
                              setWorkflowEditorActions(updated)
                            }}>
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                          </select>
                        </div>
                      )}
                      {!['delay', 'send_email', 'send_internal_email', 'create_task'].includes(action.action_type) && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#33475b' }}>Configuration (JSON)</label>
                          <textarea defaultValue={action.action_config_json || '{}'} rows={8} style={{ padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', fontSize: '0.78rem', fontFamily: 'monospace', resize: 'vertical' }}
                            onChange={(e) => {
                              try { JSON.parse(e.target.value); const updated = workflowEditorActions.map(a => a.id === wfSelectedNode ? { ...a, action_config_json: e.target.value } : a); setWorkflowEditorActions(updated) } catch {}
                            }} />
                        </div>
                      )}
                    </div>
                  </div>
                )
              })()}
              {/* Canvas */}
              <div className="wf-canvas">
                <div className="wf-canvas-inner">
                  {/* Trigger node */}
                  <div className="wf-node wf-node-trigger" style={{ marginBottom: 0 }}>
                    <div className="wf-node-header">
                      <div className="wf-node-icon" style={{ background: '#f1f5f9', color: '#475569' }}>&#9873;</div>
                      <div className="wf-node-title">Trigger</div>
                    </div>
                    <div className="wf-node-desc">
                      {workflowEditorWorkflow?.trigger_type || 'manual'} enrollment for {workflowEditorWorkflow?.object_type || 'contacts'}
                    </div>
                  </div>
                  {/* Action nodes */}
                  {workflowEditorActions.map((action, idx) => {
                    const actionLabels: Record<string, string> = { delay: 'Delay', send_email: 'Send email', send_notification: 'Notification', send_internal_email: 'Internal email', send_sms: 'Send SMS', create_record: 'Create record', create_task: 'Create task', create_note: 'Create note', edit_record: 'Edit record', clear_property: 'Clear property', rotate_owner: 'Rotate owner', set_lifecycle_stage: 'Set lifecycle stage', set_lead_status: 'Set lead status', webhook: 'Webhook', branch_logic: 'Branch', format_data: 'Format data', calculate_value: 'Calculate value', add_to_list: 'Add to list', remove_from_list: 'Remove from list' }
                    const catColors: Record<string, { bg: string; fg: string }> = { delay: { bg: '#fffbeb', fg: '#d97706' }, branch_logic: { bg: '#f0f9ff', fg: '#0ea5e9' }, send_email: { bg: '#f3e8ff', fg: '#7c3aed' }, send_notification: { bg: '#f3e8ff', fg: '#7c3aed' }, send_internal_email: { bg: '#f3e8ff', fg: '#7c3aed' }, send_sms: { bg: '#f3e8ff', fg: '#7c3aed' }, create_record: { bg: '#ecfdf5', fg: '#059669' }, create_task: { bg: '#ecfdf5', fg: '#059669' }, create_note: { bg: '#ecfdf5', fg: '#059669' }, edit_record: { bg: '#ecfdf5', fg: '#059669' }, clear_property: { bg: '#ecfdf5', fg: '#059669' }, rotate_owner: { bg: '#ecfdf5', fg: '#059669' }, set_lifecycle_stage: { bg: '#fef2f2', fg: '#dc2626' }, set_lead_status: { bg: '#fef2f2', fg: '#dc2626' }, add_to_list: { bg: '#fef2f2', fg: '#dc2626' }, remove_from_list: { bg: '#fef2f2', fg: '#dc2626' }, webhook: { bg: '#fffbeb', fg: '#d97706' }, format_data: { bg: '#fffbeb', fg: '#d97706' }, calculate_value: { bg: '#fffbeb', fg: '#d97706' } }
                    const cc = catColors[action.action_type] || { bg: '#f1f5f9', fg: '#475569' }
                    const nodeClass = action.action_type === 'delay' ? 'wf-node wf-node-delay' : action.action_type.startsWith('branch') ? 'wf-node wf-node-branch' : 'wf-node'
                    return (
                      <div key={action.id}>
                        {/* Connector line with add button */}
                        <div className="wf-connector-line">
                          <button className="wf-connector-add" onClick={() => { setWfInsertIndex(idx); setWfSidebarMode('actions') }}>+</button>
                        </div>
                        {/* Node */}
                        <div className={`${nodeClass}${wfSelectedNode === action.id ? ' selected' : ''}`} onClick={() => { setWfSelectedNode(action.id); setWfSidebarMode('config') }}>
                          <button className="wf-node-remove" onClick={(e) => { e.stopPropagation(); setWorkflowEditorActions(prev => prev.filter(a => a.id !== action.id)); if (wfSelectedNode === action.id) { setWfSelectedNode(null); setWfSidebarMode(null) } }}>&times;</button>
                          <div className="wf-node-header">
                            <div className="wf-node-icon" style={{ background: cc.bg, color: cc.fg }}>{action.action_type === 'delay' ? '\u23F1' : action.action_type.includes('email') ? '\u2709' : action.action_type.includes('task') ? '\u2611' : action.action_type.includes('branch') ? '\u2B95' : '\u2699'}</div>
                            <div className="wf-node-title">{actionLabels[action.action_type] || action.action_type}</div>
                          </div>
                          <div className="wf-node-desc">
                            {action.action_type === 'delay' && (() => { try { const c = JSON.parse(action.action_config_json || '{}'); return `Wait ${c.days || 1} ${c.unit || 'days'}` } catch { return 'Configure delay' } })()}
                            {action.action_type === 'send_email' && (() => { try { const c = JSON.parse(action.action_config_json || '{}'); return c.subject || 'Configure email' } catch { return 'Configure email' } })()}
                            {action.action_type === 'create_task' && (() => { try { const c = JSON.parse(action.action_config_json || '{}'); return c.title || 'Configure task' } catch { return 'Configure task' } })()}
                            {!['delay', 'send_email', 'create_task'].includes(action.action_type) && 'Click to configure'}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                  {/* Add action at end */}
                  <div className="wf-connector-line">
                    <button className="wf-connector-add" onClick={() => { setWfInsertIndex(workflowEditorActions.length); setWfSidebarMode('actions') }}>+</button>
                  </div>
                  {/* End node */}
                  <div className="wf-node wf-node-end">End</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create Workflow Modal */}
        {showCreateWorkflow && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.35)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'fadeIn 150ms ease' }}
            onClick={() => setShowCreateWorkflow(false)}>
            <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: '440px', maxWidth: '90vw', boxShadow: '0 8px 30px rgba(0,0,0,0.18)' }} onClick={(e) => e.stopPropagation()}>
              <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1e293b', margin: '0 0 20px' }}>Create workflow</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#33475b', marginBottom: '6px' }}>Workflow name *</label>
                  <input type="text" value={newWorkflowName} onChange={(e) => setNewWorkflowName(e.target.value)} placeholder="e.g., New Lead Follow-Up" style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', fontSize: '0.88rem', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#33475b', marginBottom: '6px' }}>Object type *</label>
                  <select value={newWorkflowObjectType} onChange={(e) => setNewWorkflowObjectType(e.target.value)} style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', fontSize: '0.88rem' }}>
                    {['contact', 'deal', 'lead', 'company', 'ticket'].map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#33475b', marginBottom: '6px' }}>Trigger type *</label>
                  <select value={newWorkflowTrigger} onChange={(e) => setNewWorkflowTrigger(e.target.value)} style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', fontSize: '0.88rem' }}>
                    {[{ v: 'manual', l: 'Trigger manually' }, { v: 'on_create', l: 'Record created' }, { v: 'on_update', l: 'Property changed' }, { v: 'on_stage_change', l: 'Stage changed' }].map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '24px' }}>
                <button onClick={() => setShowCreateWorkflow(false)} style={{ padding: '9px 16px', border: '1px solid #cbd6e2', borderRadius: '4px', background: '#fff', color: '#33475b', fontSize: '0.85rem', fontWeight: 500, cursor: 'pointer' }}>Cancel</button>
                <button disabled={!newWorkflowName.trim()} onClick={() => {
                  createWorkflow({ name: newWorkflowName.trim(), objectType: newWorkflowObjectType, triggerType: newWorkflowTrigger })
                    .then((wf) => { setShowCreateWorkflow(false); setNewWorkflowName(''); setEditingWorkflowId(wf.id) })
                    .catch((err) => alert(err.message))
                }} style={{ padding: '9px 20px', border: 'none', borderRadius: '4px', background: '#ff7a59', color: '#fff', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', opacity: newWorkflowName.trim() ? 1 : 0.5 }}>Create</button>
              </div>
            </div>
          </div>
        )}

        {/* ========== Sequences View (6-row template) ========== */}
        {activeView === 'sequences' && editingSequenceId === null && (() => {
          const seqSearchLower = seqSearch.toLowerCase()
          const seqFiltered = sequencesData
            .filter(s => {
              if (sequenceTab === 'active' && !s.is_active) return false
              if (sequenceTab === 'drafts' && s.is_active) return false
              if (seqStatusFilter === 'active' && !s.is_active) return false
              if (seqStatusFilter === 'draft' && s.is_active) return false
              if (seqSearchLower && !(s.name || '').toLowerCase().includes(seqSearchLower) && !(s.description || '').toLowerCase().includes(seqSearchLower) && !(s.sequence_type || '').toLowerCase().includes(seqSearchLower)) return false
              return true
            })
            .sort((a, b) => {
              const f = seqSort.field
              const av = (a as any)[f] ?? ''
              const bv = (b as any)[f] ?? ''
              const cmp = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv))
              return seqSort.order === 'ASC' ? cmp : -cmp
            })
          const seqTotal = seqFiltered.length
          const seqPages = Math.max(1, Math.ceil(seqTotal / seqPageSize))
          const seqPageClamped = Math.min(seqPage, seqPages)
          const seqPageData = seqFiltered.slice((seqPageClamped - 1) * seqPageSize, seqPageClamped * seqPageSize)
          const toggleSeqSort = (field: string) => setSeqSort(prev => ({ field, order: prev.field === field && prev.order === 'ASC' ? 'DESC' : 'ASC' }))
          const toggleAllSeqRows = () => {
            if (seqSelectedRows.size === seqPageData.length) setSeqSelectedRows(new Set())
            else setSeqSelectedRows(new Set(seqPageData.map(s => s.id)))
          }
          const toggleSeqRow = (id: number) => setSeqSelectedRows(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })
          const onSeqResizeStart = (col: string, e: React.MouseEvent) => {
            e.preventDefault(); e.stopPropagation()
            const startX = e.clientX; const startW = (seqColWidths as any)[col]
            const onMove = (ev: MouseEvent) => { const diff = ev.clientX - startX; setSeqColWidths(prev => ({ ...prev, [col]: Math.max(60, startW + diff) })) }
            const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp) }
            document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp)
          }
          const seqSortArrow = <svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M29.38 21.96a1.147 1.147 0 0 0-1.617 0l-3.85 3.852V5.714a1.143 1.143 0 0 0-2.286 0v20.099l-3.853-3.852a1.144 1.144 0 0 0-1.615 1.617l5.803 5.802a1.143 1.143 0 0 0 1.616 0l5.802-5.802a1.144 1.144 0 0 0 0-1.617M15.84 8.422 10.039 2.62a1.145 1.145 0 0 0-1.616 0L2.62 8.422a1.144 1.144 0 0 0 1.617 1.617l3.85-3.851v20.098a1.143 1.143 0 0 0 2.286 0V6.187l3.853 3.852a1.14 1.14 0 0 0 1.616-.001 1.14 1.14 0 0 0-.001-1.616"/></svg>

          return (
          <div className="hs-contacts">
            {/* ROW 1 — Topbar */}
            <div className="hs-topbar">
              <div className="hs-topbar-left">
                <div style={{ display: 'flex', alignItems: 'center', padding: '0 12px', gap: '6px' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M2 12h20"/><circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/></svg>
                  <span style={{ fontWeight: 600, fontSize: '0.92rem', color: '#33475b' }}>Sequences</span>
                </div>
                <div className="hs-topbar-sep" />
                <div className="hs-view-tabs">
                  {(['all', 'active', 'drafts'] as const).map(t => {
                    const isActive = sequenceTab === t
                    const tabCount = t === 'all' ? seqTotal : t === 'active' ? sequencesData.filter(s => s.is_active).length : sequencesData.filter(s => !s.is_active).length
                    return (
                      <div key={t} className={`hs-view-tab${isActive ? ' active' : ''}`} ref={isActive ? tabEditRef : undefined} onClick={() => { setSequenceTab(t); setSeqPage(1) }}>
                        <span className="hs-tab-label">{t === 'all' ? 'All sequences' : t.charAt(0).toUpperCase() + t.slice(1)}</span>
                        {isActive && tabCount > 0 && <span className="hs-tab-count">{tabCount.toLocaleString()}</span>}
                        {isActive && (
                          <span className="hs-tab-kebab" onClick={(e) => { e.stopPropagation(); setTabEditOpen(!tabEditOpen) }}>
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>
                          </span>
                        )}
                        {t !== 'all' && <span className="hs-tab-x" onClick={(e) => { e.stopPropagation(); setSequenceTab('all'); setSeqPage(1) }}>&times;</span>}
                        {isActive && tabEditOpen && (
                          <div className="hs-tab-edit-dropdown">
                            <button className="hs-tab-edit-item" disabled>Rename</button>
                            <button className="hs-tab-edit-item">Clone</button>
                            <button className="hs-tab-edit-item" disabled>Manage Sharing</button>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
                <div className="hs-topbar-sep" />
                <button className="hs-quick-add-btn" title="Add view" onClick={() => {}}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                </button>
              </div>
              <div className="hs-topbar-right">
                <button className="hs-add-contacts-btn" onClick={() => setShowCreateSequence(true)}>Create sequence</button>
              </div>
            </div>

            {/* ROW 2 — Toolbar */}
            <div className="hs-toolbar">
              <div className="hs-toolbar-left">
                <div className="hs-search-wrap">
                  <svg className="hs-search-icon" width="14" height="14" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M28.14 29.29c-.29 0-.58-.11-.81-.33l-7.58-7.58a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l7.58 7.58a1.14 1.14 0 0 1-.81 1.95"/><path d="M13.29 24.71c-6.3 0-11.43-5.13-11.43-11.43S6.98 1.86 13.29 1.86s11.43 5.13 11.43 11.43-5.13 11.43-11.43 11.43Zm0-20.57c-5.04 0-9.14 4.1-9.14 9.14s4.1 9.14 9.14 9.14 9.14-4.1 9.14-9.14-4.1-9.14-9.14-9.14"/></svg>
                  <input className="hs-search-input" placeholder="Search sequences..." value={seqSearch} onChange={(e) => { setSeqSearch(e.target.value); setSeqPage(1) }} />
                  {seqSearch && <button className="hs-search-clear" onClick={() => { setSeqSearch(''); setSeqPage(1) }}>&times;</button>}
                </div>
                <div className="hs-tb-sep" />
                <div className="hs-viewtype-group">
                  <button className="hs-tb-btn hs-viewtype-btn">
                    <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                    <span>Table</span>
                  </button>
                  <button className={`hs-tb-btn hs-settings-btn${seqSettingsOpen ? ' active' : ''}`} onClick={() => { setSeqSettingsOpen(!seqSettingsOpen); setSeqPreview(null) }} title="Table settings">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                  </button>
                </div>
                <div className="hs-tb-sep" />
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Edit columns">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="18" rx="1"/><rect x="14" y="3" width="7" height="18" rx="1"/></svg>
                  <span>Edit columns</span>
                </button>
                <div className="hs-tb-sep" />
                <button className={`hs-tb-btn hs-tb-btn-bordered${seqQuickFiltersOpen ? ' hs-tb-btn-pressed' : ''}`} title="Filters" onClick={() => setSeqQuickFiltersOpen(!seqQuickFiltersOpen)}>
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M30.71 16.86H13.57a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28M6.71 16.86H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M10.14 20.29c-2.52 0-4.57-2.05-4.57-4.57s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29"/></svg>
                  <span>Filters</span>
                </button>
                <div className="hs-tb-sep" />
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Export" onClick={() => {
                  const headers = ['Name','Status','Type','Created','Updated']
                  const escCsv = (v: unknown) => { const s = String(v ?? ''); return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s }
                  const rows = seqFiltered.map(s => [s.name, s.is_active ? 'Active' : 'Draft', s.sequence_type, s.created_at || '', s.updated_at || ''].map(escCsv).join(','))
                  const csv = [headers.join(','), ...rows].join('\n')
                  const blob = new Blob([csv], { type: 'text/csv' }); const url = URL.createObjectURL(blob)
                  const a = document.createElement('a'); a.href = url; a.download = `sequences-export-${new Date().toISOString().slice(0,10)}.csv`; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url)
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  <span>Export</span>
                </button>
              </div>
              <div className="hs-toolbar-right">
                <div className="hs-tb-sep" />
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Clone view">
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M25.86 29.29H12.15c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-1.89 1.54-3.43 3.43-3.43h13.71c1.89 0 3.43 1.54 3.43 3.43v13.71c0 1.89-1.54 3.43-3.43 3.43M12.14 11c-.63 0-1.14.51-1.14 1.14v13.71c0 .63.51 1.14 1.14 1.14h13.71c.63 0 1.14-.51 1.14-1.14V12.14c0-.63-.51-1.14-1.14-1.14z"/><path d="M5.29 22.43c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43H19c1.89 0 3.43 1.54 3.43 3.43a1.14 1.14 0 1 1-2.28 0c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14V19c0 .63.51 1.14 1.14 1.14a1.14 1.14 0 1 1 0 2.28Z"/></svg>
                </button>
                <button className="hs-tb-btn hs-tb-btn-bordered disabled" disabled title="Save view">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                </button>
              </div>
            </div>

            {/* ROW 3 — Quick filters */}
            {seqQuickFiltersOpen && (
            <div className="hs-quick-filters">
              <button
                className={`hs-qf-btn hs-qf-trigger${seqStatusFilter ? ' has-value' : ''}`}
                onClick={() => {
                  const vals = ['', 'active', 'draft']
                  const idx = vals.indexOf(seqStatusFilter)
                  setSeqStatusFilter(vals[(idx + 1) % vals.length])
                  setSeqPage(1)
                }}
              >
                <span>{seqStatusFilter === 'active' ? 'Active' : seqStatusFilter === 'draft' ? 'Draft' : 'Status'}</span>
                {seqStatusFilter && <span className="hs-qf-clear" onClick={(e) => { e.stopPropagation(); setSeqStatusFilter(''); setSeqPage(1) }}>&times;</span>}
                {!seqStatusFilter && <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>}
              </button>
              <button
                className={`hs-qf-btn hs-qf-trigger${seqSort.field !== 'updated_at' ? ' has-value' : ''}`}
                onClick={() => {
                  const fields = ['updated_at', 'name', 'created_at', 'sequence_type']
                  const idx = fields.indexOf(seqSort.field)
                  const next = fields[(idx + 1) % fields.length]
                  setSeqSort({ field: next, order: next === 'name' ? 'ASC' : 'DESC' })
                  setSeqPage(1)
                }}
              >
                <span>Sort: {{ updated_at: 'Updated', name: 'Name', created_at: 'Created', sequence_type: 'Type' }[seqSort.field] || 'Updated'}</span>
                <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
              </button>
              <div className="hs-qf-sep" />
              <button className="hs-qf-btn hs-qf-more">
                <svg width="10" height="10" viewBox="0 0 32 32" fill="currentColor"><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V3a1.14 1.14 0 1 1 2.28 0v25.14c0 .63-.51 1.14-1.14 1.14Z"/><path d="M28.14 16.71H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28"/></svg>
                <span>More</span>
              </button>
            </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px', background: '#fffdf7', borderBottom: '1px solid #f1e8d8' }}>
              <span style={{ fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px', background: '#fff3e0', color: '#b65815', border: '1px solid #ffd9a8', textTransform: 'uppercase', letterSpacing: '0.5px', flexShrink: 0 }}>Coming soon</span>
              <span style={{ fontSize: '0.78rem', color: '#7c98b6' }}>Sequence sending isn&apos;t wired up yet — enrolled contacts don&apos;t advance through steps and no emails go out, and a sequence can&apos;t be activated. Authoring sequences and steps works normally.</span>
            </div>
            {/* Main content area */}
            <div className={`hs-content${seqPreview ? ' with-preview' : ''}${seqSettingsOpen ? ' with-settings' : ''}`}>
              <div className="hs-table-area">
                {/* ROW 4 — Selection bar */}
                {seqSelectedRows.size > 0 && (
                  <div className="hs-selection-bar">
                    <span className="hs-sel-count">{seqSelectedRows.size} selected</span>
                    <div className="hs-sel-sep" />
                    <button className="hs-sel-btn" style={{ color: '#dc2626' }} onClick={() => {
                      if (confirm(`Delete ${seqSelectedRows.size} sequence(s)?`)) {
                        Promise.all([...seqSelectedRows].map(id => deleteSequence(id))).then(() => {
                          setSequencesData(prev => prev.filter(s => !seqSelectedRows.has(s.id)))
                          setSeqSelectedRows(new Set())
                        })
                      }
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                      Delete
                    </button>
                    <button className="hs-sel-close" onClick={() => setSeqSelectedRows(new Set())} title="Deselect all">&times;</button>
                  </div>
                )}

                {/* ROW 5 — Data table */}
                <div className="hs-table-scroll">
                  <table className={`hs-table hs-density-${seqRowHeight}${seqZebra ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                      <col style={{ width: 40 }} />
                      <col style={{ width: seqColWidths.name }} />
                      <col style={{ width: seqColWidths.status }} />
                      <col style={{ width: seqColWidths.type }} />
                      <col style={{ width: seqColWidths.created_at }} />
                      <col style={{ width: seqColWidths.updated_at }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th className="hs-th-check">
                          <input type="checkbox" checked={seqPageData.length > 0 && seqSelectedRows.size === seqPageData.length} onChange={toggleAllSeqRows} />
                        </th>
                        <th className="hs-th-resizable hs-th-sortable" style={{ position: 'sticky', left: 40, top: 0, zIndex: 15, background: '#f8fafc', boxShadow: '2px 0 4px rgba(0,0,0,0.06)' }} onClick={() => toggleSeqSort('name')}>
                          <span className="hs-th-content">
                            Name
                            {seqSort.field === 'name' && <span className="hs-sort-arrow">{seqSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                            {seqSort.field !== 'name' && <span className="hs-th-hover-sort">{seqSortArrow}</span>}
                          </span>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onSeqResizeStart('name', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                        <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleSeqSort('is_active')}>
                          <span className="hs-th-content">
                            Status
                            {seqSort.field === 'is_active' && <span className="hs-sort-arrow">{seqSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                            {seqSort.field !== 'is_active' && <span className="hs-th-hover-sort">{seqSortArrow}</span>}
                          </span>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onSeqResizeStart('status', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                        <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleSeqSort('sequence_type')}>
                          <span className="hs-th-content">
                            Type
                            {seqSort.field === 'sequence_type' && <span className="hs-sort-arrow">{seqSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                            {seqSort.field !== 'sequence_type' && <span className="hs-th-hover-sort">{seqSortArrow}</span>}
                          </span>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onSeqResizeStart('type', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                        <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleSeqSort('created_at')}>
                          <span className="hs-th-content">
                            Created
                            {seqSort.field === 'created_at' && <span className="hs-sort-arrow">{seqSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                            {seqSort.field !== 'created_at' && <span className="hs-th-hover-sort">{seqSortArrow}</span>}
                          </span>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onSeqResizeStart('created_at', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                        <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleSeqSort('updated_at')}>
                          <span className="hs-th-content">
                            Updated
                            {seqSort.field === 'updated_at' && <span className="hs-sort-arrow">{seqSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                            {seqSort.field !== 'updated_at' && <span className="hs-th-hover-sort">{seqSortArrow}</span>}
                          </span>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onSeqResizeStart('updated_at', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sequencesLoading ? (
                        <tr><td colSpan={6} className="hs-loading-cell"><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}><DotLottieReact src="/loading.lottie" loop autoplay style={{ width: 80, height: 80 }} /><span style={{ fontSize: '0.82rem', color: '#7c98b6', marginTop: 8 }}>Loading sequences...</span></div></td></tr>
                      ) : seqPageData.length === 0 ? (
                        <tr><td colSpan={6} className="hs-empty-cell">
                          <div style={{ padding: '60px 40px', textAlign: 'center' }}>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" style={{ margin: '0 auto 16px' }}><path d="M12 2v20M2 12h20"/><circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/></svg>
                            <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#33475b', margin: '0 0 8px' }}>Create your first sequence</h3>
                            <p style={{ fontSize: '0.82rem', color: '#7c98b6', margin: '0 0 20px', maxWidth: '400px', marginLeft: 'auto', marginRight: 'auto' }}>Build time-sequenced outreach chains with automated emails and task reminders for your sales reps.</p>
                            <button className="hs-add-contacts-btn" onClick={() => setShowCreateSequence(true)}>Create sequence</button>
                          </div>
                        </td></tr>
                      ) : seqPageData.map((s) => (
                        <tr key={s.id} className={`hs-row${seqSelectedRows.has(s.id) ? ' selected' : ''}`}>
                          <td className="hs-td-check">
                            <input type="checkbox" checked={seqSelectedRows.has(s.id)} onChange={() => toggleSeqRow(s.id)} />
                          </td>
                          <td className="hs-td-name" style={{ position: 'sticky', left: 40, zIndex: 4, boxShadow: '2px 0 4px rgba(0,0,0,0.06)', background: seqSelectedRows.has(s.id) ? '#eef2ff' : 'inherit' }}>
                            <div className="hs-name-cell">
                              <div className="hs-avatar" style={{ background: s.is_active ? '#3b82f6' : '#94a3b8' }}>
                                {(s.name || '?')[0].toUpperCase()}
                              </div>
                              <span className="hs-name-link" onClick={() => setEditingSequenceId(s.id)}>
                                {s.name || <span className="cell-empty">--</span>}
                              </span>
                              <button className="hs-preview-btn" onClick={(e) => { e.stopPropagation(); setSeqSettingsOpen(false); setSeqPreview(seqPreview?.id === s.id ? null : s) }}>Preview</button>
                            </div>
                          </td>
                          <td>
                            <span className="hs-status-badge" style={{ background: s.is_active ? '#fff3e0' : '#f5f8fa', color: s.is_active ? '#b65815' : '#7c98b6' }}>
                              {s.is_active ? 'Not running' : 'Draft'}
                            </span>
                          </td>
                          <td style={{ color: '#516f90', fontSize: '0.8rem' }}>{s.sequence_type || <span className="cell-empty">--</span>}</td>
                          <td style={{ color: '#516f90', fontSize: '0.75rem' }}>{s.created_at ? formatDatePref(s.created_at) : <span className="cell-empty">--</span>}</td>
                          <td style={{ color: '#516f90', fontSize: '0.75rem' }}>{s.updated_at ? formatDatePref(s.updated_at) : <span className="cell-empty">--</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Preview panel */}
              {seqPreview && (
                <div className="hs-preview">
                  <div className="hs-preview-header">
                    <span className="hs-preview-title">{seqPreview.name}</span>
                    <button className="hs-preview-close" onClick={() => setSeqPreview(null)}>&times;</button>
                  </div>
                  <div className="hs-preview-body">
                    <div className="hs-preview-field"><label>Status</label><span className="hs-status-badge" style={{ background: seqPreview.is_active ? '#fff3e0' : '#f5f8fa', color: seqPreview.is_active ? '#b65815' : '#7c98b6' }}>{seqPreview.is_active ? 'Not running' : 'Draft'}</span></div>
                    <div className="hs-preview-field"><label>Type</label><span>{seqPreview.sequence_type}</span></div>
                    {seqPreview.description && <div className="hs-preview-field"><label>Description</label><span>{seqPreview.description}</span></div>}
                    <div className="hs-preview-field"><label>Created</label><span>{seqPreview.created_at ? formatDatePref(seqPreview.created_at) : '--'}</span></div>
                    <div className="hs-preview-field"><label>Updated</label><span>{seqPreview.updated_at ? formatDatePref(seqPreview.updated_at) : '--'}</span></div>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                      <button className="hs-add-contacts-btn" style={{ fontSize: '0.78rem', padding: '6px 14px' }} onClick={() => setEditingSequenceId(seqPreview.id)}>Edit sequence</button>
                      <button style={{ padding: '6px 14px', border: '1px solid #e2e8f0', borderRadius: '4px', background: '#fff', color: '#d32f2f', fontSize: '0.78rem', cursor: 'pointer' }}
                        onClick={() => { if (confirm('Delete this sequence?')) deleteSequence(seqPreview.id).then(() => { setSequencesData(prev => prev.filter(x => x.id !== seqPreview.id)); setSeqPreview(null) }) }}>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Settings panel */}
              {seqSettingsOpen && (
                <div className="hs-settings-panel">
                  <div className="hs-settings-header">
                    <span className="hs-settings-title">Table settings</span>
                    <button className="hs-settings-close" onClick={() => setSeqSettingsOpen(false)}>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                    </button>
                  </div>
                  <div className="hs-settings-body">
                    <div className="hs-settings-group">
                      <label className="hs-settings-label">Pagination</label>
                      {([100, 200, 500] as const).map(size => (
                        <label className="hs-settings-radio" key={size}>
                          <input type="radio" name="seqPageSizeSettings" checked={seqPageSize === size} onChange={() => { setSeqPageSize(size); setSeqPage(1) }} />
                          <span className="hs-radio-indicator" />
                          <span>{size} per page</span>
                        </label>
                      ))}
                    </div>
                    <hr className="hs-settings-divider" />
                    <div className="hs-settings-group">
                      <label className="hs-settings-label">Row height</label>
                      {([{ value: 'small' as const, label: 'Compact' }, { value: 'medium' as const, label: 'Default' }, { value: 'large' as const, label: 'Comfortable' }]).map(opt => (
                        <label className="hs-settings-radio" key={opt.value}>
                          <input type="radio" name="seqRowHeightSettings" checked={seqRowHeight === opt.value} onChange={() => setSeqRowHeight(opt.value)} />
                          <span className="hs-radio-indicator" />
                          <span>{opt.label}</span>
                        </label>
                      ))}
                    </div>
                    <hr className="hs-settings-divider" />
                    <div className="hs-settings-group">
                      <div className="hs-settings-toggle-row">
                        <label className="hs-settings-label">Zebra striping</label>
                        <button className={`hs-toggle${seqZebra ? ' on' : ''}`} onClick={() => setSeqZebra(!seqZebra)} role="switch" aria-checked={seqZebra}>
                          <span className="hs-toggle-track">
                            <span className="hs-toggle-on">ON</span>
                            <span className="hs-toggle-off">OFF</span>
                            <span className="hs-toggle-handle" />
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ROW 6 — Pagination */}
            <div className="hs-pagination">
              <nav className="hs-paginator" aria-label="Pagination">
                <button className="hs-paginator-btn" disabled={seqPageClamped <= 1} onClick={() => setSeqPage(p => p - 1)} aria-label="Previous page">
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95"/></svg>
                  <span>Prev</span>
                </button>
                {(() => {
                  if (seqPages <= 7) {
                    const pages: number[] = []; for (let i = 1; i <= seqPages; i++) pages.push(i)
                    return pages.map(p => <button key={p} className={`hs-paginator-num${p === seqPageClamped ? ' active' : ''}`} onClick={() => setSeqPage(p)}>{p}</button>)
                  }
                  const items: React.ReactNode[] = []
                  const shown = new Set<number>()
                  for (let i = 1; i <= Math.min(4, seqPages); i++) shown.add(i)
                  shown.add(seqPages)
                  for (let i = Math.max(1, seqPageClamped - 1); i <= Math.min(seqPages, seqPageClamped + 1); i++) shown.add(i)
                  const sorted = [...shown].sort((a, b) => a - b)
                  for (let i = 0; i < sorted.length; i++) {
                    if (i > 0 && sorted[i] - sorted[i - 1] > 1) items.push(<span key={`e${i}`} className="hs-paginator-ellipsis">...</span>)
                    items.push(<button key={sorted[i]} className={`hs-paginator-num${sorted[i] === seqPageClamped ? ' active' : ''}`} onClick={() => setSeqPage(sorted[i])}>{sorted[i]}</button>)
                  }
                  return items
                })()}
                <button className="hs-paginator-btn" disabled={seqPageClamped >= seqPages} onClick={() => setSeqPage(p => p + 1)} aria-label="Next page">
                  <span>Next</span>
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M11 27.71c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62l9.48-9.48-9.48-9.47a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0L22.1 15.48c.45.45.45 1.17 0 1.62L11.81 27.39c-.22.22-.52.33-.81.33Z"/></svg>
                </button>
              </nav>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', marginLeft: 'auto', marginRight: 50 }}><select value={seqPageSize} onChange={e => { setSeqPageSize(Number(e.target.value) as any); setSeqPage(1) }} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #e2e8f0', fontSize: '0.82rem' }}><option value={100}>100</option><option value={200}>200</option><option value={500}>500</option></select><span>per page</span></div>
            </div>
          </div>
          )
        })()}

        {/* Sequence Editor */}
        {activeView === 'sequences' && editingSequenceId !== null && editingSequence && (
          <div className="seq-editor-container">
            <div className="seq-editor-topbar">
              <div className="seq-editor-topbar-left">
                <button style={{ border: 'none', background: 'none', color: '#1976d2', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 500 }} onClick={() => { setEditingSequenceId(null); setEditingSequence(null); setSeqEditorTab('steps') }}>
                  &larr; Back to Sequences
                </button>
                <span style={{ fontSize: '0.92rem', fontWeight: 700, color: '#1e293b', marginLeft: '12px' }}>{editingSequence.name}</span>
                <span className="hs-status-badge" style={{ background: editingSequence.is_active ? '#fff3e0' : '#f5f8fa', color: editingSequence.is_active ? '#b65815' : '#7c98b6', marginLeft: '8px' }}>
                  {editingSequence.is_active ? 'Not running' : 'Draft'}
                </span>
              </div>
              <div className="seq-editor-topbar-right">
                <button disabled={!editingSequence.is_active} title={editingSequence.is_active ? undefined : "Coming soon — sequence sending isn't wired up yet"} style={{ padding: '6px 14px', border: '1px solid #cbd6e2', borderRadius: '4px', background: '#fff', color: '#33475b', fontSize: '0.82rem', fontWeight: 500, opacity: editingSequence.is_active ? 1 : 0.55, cursor: editingSequence.is_active ? 'pointer' : 'not-allowed' }}
                  onClick={() => updateSequence(editingSequence.id, { is_active: !editingSequence.is_active }).then(s => setEditingSequence({ ...editingSequence, ...s }))}>
                  {editingSequence.is_active ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            </div>
            <div className="seq-preview-strip">
              <div className="seq-preview-stat"><span className="seq-preview-stat-label">Total Steps</span><span className="seq-preview-stat-value">{editingSequence.steps.length}</span></div>
              <div className="seq-preview-stat"><span className="seq-preview-stat-label">Enrolled</span><span className="seq-preview-stat-value">{seqStats?.total_enrolled ?? 0}</span></div>
              <div className="seq-preview-stat"><span className="seq-preview-stat-label">Active</span><span className="seq-preview-stat-value">{seqStats?.active ?? 0}</span></div>
              <div className="seq-preview-stat"><span className="seq-preview-stat-label">Completed</span><span className="seq-preview-stat-value">{seqStats?.completed ?? 0}</span></div>
              <div className="seq-preview-stat"><span className="seq-preview-stat-label">Replied</span><span className="seq-preview-stat-value">{seqStats?.replied ?? 0}</span></div>
            </div>
            {/* Tabs */}
            <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', padding: '0 20px', background: '#fff' }}>
              {(['steps', 'enrollments', 'performance'] as const).map(t => (
                <button key={t} className={`cf-editor-tab${seqEditorTab === t ? ' active' : ''}`} onClick={() => setSeqEditorTab(t)}>
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
            {seqEditorTab === 'steps' && (
              <div className="seq-steps-container">
                {editingSequence.steps.length === 0 && (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#7c98b6' }}>
                    <p style={{ margin: '0 0 16px', fontSize: '0.84rem' }}>No steps yet. Add your first step below.</p>
                  </div>
                )}
                {editingSequence.steps.map((step, idx) => {
                  const stepIcons: Record<string, { bg: string; fg: string; label: string; cls: string }> = {
                    automated_email: { bg: '#fff3e0', fg: '#e65100', label: 'Automated email', cls: 'email' },
                    manual_email: { bg: '#fff3e0', fg: '#e65100', label: 'Manual email', cls: 'email' },
                    call_task: { bg: '#e8f5e9', fg: '#2e7d32', label: 'Call task', cls: 'call' },
                    general_task: { bg: '#e3f2fd', fg: '#1565c0', label: 'General task', cls: 'task' },
                    sms_task: { bg: '#f3e8ff', fg: '#7c3aed', label: 'SMS task', cls: 'sms' },
                  }
                  const si = stepIcons[step.step_type] || stepIcons.general_task
                  const config = step.config_json ? JSON.parse(step.config_json) : {}
                  return (
                    <div key={step.id}>
                      {idx > 0 && (
                        <div className="seq-delay-connector">
                          <span className="seq-delay-badge">{step.delay_amount} {step.delay_unit} after Step {idx}</span>
                        </div>
                      )}
                      <div className="seq-step">
                        <div className="seq-step-header">
                          <div className={`seq-step-icon ${si.cls}`}>
                            {step.step_type.includes('email') ? '\u2709' : step.step_type.includes('call') ? '\u260E' : step.step_type.includes('sms') ? '\uD83D\uDCAC' : '\u2611'}
                          </div>
                          <div className="seq-step-info">
                            <div className="seq-step-type">Step {idx + 1}: {si.label}</div>
                            <div className="seq-step-detail">{config.subject || config.title || config.message || 'Click to configure'}</div>
                          </div>
                          <div className="seq-step-actions">
                            <button onClick={() => setSeqEditingStep(step)}>Edit</button>
                            <button onClick={() => { deleteSequenceStep(editingSequence.id, step.id).then(() => setEditingSequence({ ...editingSequence, steps: editingSequence.steps.filter(s => s.id !== step.id) })) }}>Delete</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
                {/* Add step button */}
                <div className="seq-add-step">
                  <button className="seq-add-step-btn" onClick={() => setShowAddStep(!showAddStep)}>+</button>
                  {showAddStep && (
                    <div className="seq-step-type-selector">
                      {[
                        { type: 'automated_email', label: 'Automated email', icon: '\u2709', cls: 'email' },
                        { type: 'manual_email', label: 'Manual email', icon: '\u2709', cls: 'email' },
                        { type: 'call_task', label: 'Call task', icon: '\u260E', cls: 'call' },
                        { type: 'general_task', label: 'General task', icon: '\u2611', cls: 'task' },
                        { type: 'sms_task', label: 'SMS task', icon: '\uD83D\uDCAC', cls: 'sms' },
                      ].map(opt => (
                        <div key={opt.type} className="seq-step-type-option" onClick={() => {
                          addSequenceStep(editingSequence.id, { step_type: opt.type, delay_amount: editingSequence.steps.length > 0 ? 3 : 0, delay_unit: 'days', config_json: '{}' })
                            .then(() => fetchSequence(editingSequence.id).then(s => setEditingSequence(s)))
                          setShowAddStep(false)
                        }}>
                          <div className={`seq-step-icon ${opt.cls}`} style={{ width: '28px', height: '28px', fontSize: '14px' }}>{opt.icon}</div>
                          <span>{opt.label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            {seqEditorTab === 'enrollments' && (
              <div style={{ padding: '20px', overflow: 'auto' }}>
                {seqEnrollments.length === 0 ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#7c98b6', fontSize: '0.84rem' }}>No enrollments yet.</div>
                ) : (
                  <table className="hs-table">
                    <thead><tr><th>Contact</th><th>Status</th><th>Current Step</th><th>Enrolled</th></tr></thead>
                    <tbody>
                      {seqEnrollments.map(e => (
                        <tr key={e.id}>
                          <td style={{ fontWeight: 500, color: '#33475b' }}>{e.contact_name || `Contact #${e.contact_id}`}</td>
                          <td><span className="hs-status-badge" style={{ background: e.status === 'active' ? '#e8f5e9' : e.status === 'completed' ? '#e3f2fd' : e.status === 'replied' ? '#fff3e0' : '#f5f8fa', color: e.status === 'active' ? '#2e7d32' : e.status === 'completed' ? '#1565c0' : e.status === 'replied' ? '#e65100' : '#7c98b6' }}>{e.status}</span></td>
                          <td style={{ color: '#516f90' }}>Step {e.current_step + 1}</td>
                          <td style={{ color: '#516f90', fontSize: '0.75rem' }}>{e.enrolled_at ? formatDatePref(e.enrolled_at) : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
            {seqEditorTab === 'performance' && seqStats && (
              <div style={{ padding: '20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px' }}>
                {[
                  { label: 'Total enrolled', value: seqStats.total_enrolled, color: '#1e293b' },
                  { label: 'Active', value: seqStats.active, color: '#2e7d32' },
                  { label: 'Completed', value: seqStats.completed, color: '#1565c0' },
                  { label: 'Replied', value: seqStats.replied, color: '#e65100' },
                  { label: 'Bounced', value: seqStats.bounced, color: '#d32f2f' },
                ].map(m => (
                  <div key={m.label} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px' }}>
                    <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{m.label}</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 800, color: m.color, marginTop: '4px' }}>{m.value}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Create Sequence Modal */}
        {showCreateSequence && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.35)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'fadeIn 150ms ease' }}
            onClick={() => setShowCreateSequence(false)}>
            <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: '440px', maxWidth: '90vw', boxShadow: '0 8px 30px rgba(0,0,0,0.18)' }} onClick={(e) => e.stopPropagation()}>
              <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1e293b', margin: '0 0 20px' }}>Create sequence</h3>
              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#33475b', marginBottom: '6px' }}>Sequence name *</label>
                <input type="text" value={newSequenceName} onChange={(e) => setNewSequenceName(e.target.value)} placeholder="e.g., New Lead Follow-Up" style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', fontSize: '0.88rem', boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '24px' }}>
                <button onClick={() => setShowCreateSequence(false)} style={{ padding: '9px 16px', border: '1px solid #cbd6e2', borderRadius: '4px', background: '#fff', color: '#33475b', fontSize: '0.85rem', fontWeight: 500, cursor: 'pointer' }}>Cancel</button>
                <button disabled={!newSequenceName.trim()} onClick={() => {
                  createSequence({ name: newSequenceName.trim() })
                    .then((s) => { setShowCreateSequence(false); setNewSequenceName(''); setEditingSequenceId(s.id) })
                    .catch((err) => alert(err.message))
                }} style={{ padding: '9px 20px', border: 'none', borderRadius: '4px', background: '#ff7a59', color: '#fff', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', opacity: newSequenceName.trim() ? 1 : 0.5 }}>Create</button>
              </div>
            </div>
          </div>
        )}

        {/* Sequence Step Edit Panel */}
        {seqEditingStep && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.35)', zIndex: 200 }} onClick={() => setSeqEditingStep(null)}>
            <div className="wf-config-panel" onClick={(e) => e.stopPropagation()}>
              <div className="wf-config-panel-header">
                <h3 style={{ fontSize: '0.88rem', fontWeight: 700, color: '#1e293b', margin: 0 }}>Edit Step</h3>
                <button style={{ border: 'none', background: 'none', color: '#64748b', cursor: 'pointer' }} onClick={() => setSeqEditingStep(null)}>&times;</button>
              </div>
              <div className="wf-config-panel-body">
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#33475b', marginBottom: '6px' }}>Delay before this step</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input type="number" min="0" defaultValue={seqEditingStep.delay_amount} style={{ width: '80px', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', fontSize: '0.84rem' }}
                      onChange={(e) => setSeqEditingStep({ ...seqEditingStep, delay_amount: Number(e.target.value) })} />
                    <select defaultValue={seqEditingStep.delay_unit} style={{ padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', fontSize: '0.84rem' }}
                      onChange={(e) => setSeqEditingStep({ ...seqEditingStep, delay_unit: e.target.value })}>
                      <option value="minutes">Minutes</option><option value="hours">Hours</option><option value="days">Days</option><option value="business_days">Business days</option><option value="weeks">Weeks</option>
                    </select>
                  </div>
                </div>
                {seqEditingStep.step_type.includes('email') && (
                  <>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#33475b', marginBottom: '6px' }}>Subject</label>
                      <input type="text" defaultValue={(() => { try { return JSON.parse(seqEditingStep.config_json || '{}').subject || '' } catch { return '' } })()} style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', fontSize: '0.84rem', boxSizing: 'border-box' }}
                        onChange={(e) => { const c = seqEditingStep.config_json ? JSON.parse(seqEditingStep.config_json) : {}; c.subject = e.target.value; setSeqEditingStep({ ...seqEditingStep, config_json: JSON.stringify(c) }) }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#33475b', marginBottom: '6px' }}>Body</label>
                      <textarea defaultValue={(() => { try { return JSON.parse(seqEditingStep.config_json || '{}').body || '' } catch { return '' } })()} rows={6} style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', fontSize: '0.84rem', resize: 'vertical', boxSizing: 'border-box' }}
                        onChange={(e) => { const c = seqEditingStep.config_json ? JSON.parse(seqEditingStep.config_json) : {}; c.body = e.target.value; setSeqEditingStep({ ...seqEditingStep, config_json: JSON.stringify(c) }) }} />
                    </div>
                  </>
                )}
                {(seqEditingStep.step_type.includes('task') || seqEditingStep.step_type.includes('call')) && (
                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#33475b', marginBottom: '6px' }}>Task title</label>
                    <input type="text" defaultValue={(() => { try { return JSON.parse(seqEditingStep.config_json || '{}').title || '' } catch { return '' } })()} style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', fontSize: '0.84rem', boxSizing: 'border-box' }}
                      onChange={(e) => { const c = seqEditingStep.config_json ? JSON.parse(seqEditingStep.config_json) : {}; c.title = e.target.value; setSeqEditingStep({ ...seqEditingStep, config_json: JSON.stringify(c) }) }} />
                  </div>
                )}
              </div>
              <div className="wf-config-panel-footer">
                <button onClick={() => setSeqEditingStep(null)} style={{ padding: '9px 16px', border: '1px solid #cbd6e2', borderRadius: '4px', background: '#fff', color: '#33475b', fontSize: '0.85rem', cursor: 'pointer' }}>Cancel</button>
                <button onClick={() => {
                  if (!editingSequence) return
                  import('./api').then(({ updateSequenceStep }) => {
                    updateSequenceStep(editingSequence.id, seqEditingStep.id, { delay_amount: seqEditingStep.delay_amount, delay_unit: seqEditingStep.delay_unit, config_json: seqEditingStep.config_json })
                      .then(() => { fetchSequence(editingSequence.id).then(s => setEditingSequence(s)); setSeqEditingStep(null) })
                  })
                }} style={{ padding: '9px 20px', border: 'none', borderRadius: '4px', background: '#ff7a59', color: '#fff', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>Save</button>
              </div>
            </div>
          </div>
        )}

        {/* ========== Chatflows View ========== */}
        {activeView === 'chatflows' && editingChatflowId === null && (() => {
          // Client-side filtering + sorting + pagination
          const cfFilteredBySearch = cfSearch ? chatflowsData.filter(cf => cf.name.toLowerCase().includes(cfSearch.toLowerCase())) : chatflowsData
          const cfFilteredByTab = chatflowTab === 'web_chat' ? cfFilteredBySearch.filter(cf => cf.chatflow_type === 'live_chat') : chatflowTab === 'bot' ? cfFilteredBySearch.filter(cf => cf.chatflow_type === 'bot') : cfFilteredBySearch
          const cfFilteredByStatus = cfStatusFilter === 'active' ? cfFilteredByTab.filter(cf => cf.is_active) : cfStatusFilter === 'inactive' ? cfFilteredByTab.filter(cf => !cf.is_active) : cfFilteredByTab
          const cfSorted = [...cfFilteredByStatus].sort((a: any, b: any) => {
            const fa = a[cfSort.field] ?? ''
            const fb = b[cfSort.field] ?? ''
            const cmp = String(fa).localeCompare(String(fb), undefined, { sensitivity: 'base' })
            return cfSort.order === 'ASC' ? cmp : -cmp
          })
          const cfPageSize = tablePageSize
          const cfTotal = cfSorted.length
          const cfTotalPages = Math.max(1, Math.ceil(cfTotal / cfPageSize))
          const cfCurrentPage = Math.min(cfPage, cfTotalPages)
          const cfPagedData = cfSorted.slice((cfCurrentPage - 1) * cfPageSize, cfCurrentPage * cfPageSize)
          const toggleAllCfRows = () => {
            if (cfSelectedRows.size === cfPagedData.length) {
              setCfSelectedRows(new Set())
            } else {
              setCfSelectedRows(new Set(cfPagedData.map(cf => cf.id)))
            }
          }
          const toggleCfSort = (field: string) => {
            setCfSort(prev => prev.field === field ? { field, order: prev.order === 'ASC' ? 'DESC' : 'ASC' } : { field, order: field === 'name' ? 'ASC' : 'DESC' })
            setCfPage(1)
          }
          const cfColWidthsArr = [40, cfColWidths.name, cfColWidths.chatflow_type, cfColWidths.is_active, cfColWidths.language, cfColWidths.created_at, cfColWidths.actions]
          const onCfResizeStart = (col: string, e: React.MouseEvent) => {
            e.preventDefault(); e.stopPropagation()
            const startX = e.clientX; const startW = (cfColWidths as any)[col]
            const onMove = (ev: MouseEvent) => { const diff = ev.clientX - startX; setCfColWidths(prev => ({ ...prev, [col]: Math.max(60, startW + diff) })) }
            const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp) }
            document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp)
          }
          const cfSortArrow = <svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M29.38 21.96a1.147 1.147 0 0 0-1.617 0l-3.85 3.852V5.714a1.143 1.143 0 0 0-2.286 0v20.099l-3.853-3.852a1.144 1.144 0 0 0-1.615 1.617l5.803 5.802a1.143 1.143 0 0 0 1.616 0l5.802-5.802a1.144 1.144 0 0 0 0-1.617M15.84 8.422 10.039 2.62a1.145 1.145 0 0 0-1.616 0L2.62 8.422a1.144 1.144 0 0 0 1.617 1.617l3.85-3.851v20.098a1.143 1.143 0 0 0 2.286 0V6.187l3.853 3.852a1.14 1.14 0 0 0 1.616-.001 1.14 1.14 0 0 0-.001-1.616"/></svg>
          const cfHeaders = [
            { key: 'name', label: 'Name', sortable: true, frozen: true },
            { key: 'chatflow_type', label: 'Type', sortable: true, frozen: false },
            { key: 'is_active', label: 'Status', sortable: true, frozen: false },
            { key: 'language', label: 'Language', sortable: true, frozen: false },
            { key: 'created_at', label: 'Created', sortable: true, frozen: false },
            { key: 'actions', label: 'Actions', sortable: false, frozen: false },
          ]
          return (
          <div className="hs-contacts">
            {/* ROW 1: Topbar */}
            <div className="hs-topbar">
              <div className="hs-topbar-left">
                <div className="hs-obj-switcher-wrap" ref={objSwitcherRef}>
                  <button className={`hs-obj-switcher${objSwitcherOpen ? ' open' : ''}`} onClick={() => { setObjSwitcherOpen(!objSwitcherOpen); setObjSwitcherSearch('') }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                    <span>Chatflows</span>
                    <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                  </button>
                  {objSwitcherOpen && (
                    <div className="hs-obj-panel">
                      <div className="hs-obj-search-wrap">
                        <svg className="hs-obj-search-icon" width="13" height="13" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                        <input className="hs-obj-search" placeholder="Search" value={objSwitcherSearch} onChange={(e) => setObjSwitcherSearch(e.target.value)} autoFocus />
                      </div>
                      <ul className="hs-obj-list" role="listbox">
                        {[
                          { label: 'Chatflows', view: 'chatflows' as ViewMode },
                          { label: 'Snippets', view: 'snippets' as ViewMode },
                          { label: 'Templates', view: 'templates' as ViewMode },
                          { label: 'Sequences', view: 'sequences' as ViewMode },
                        ].filter(item => !objSwitcherSearch || item.label.toLowerCase().includes(objSwitcherSearch.toLowerCase())).map(item => (
                          <li key={item.view} className={`hs-obj-item${item.view === activeView ? ' active' : ''}`} role="option" aria-selected={item.view === activeView} onClick={() => { setActiveView(item.view); setObjSwitcherOpen(false); setObjSwitcherSearch('') }}>{item.label}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="hs-topbar-sep" />
                <div className="hs-view-tabs">
                  {[
                    { key: 'web_chat' as const, label: 'Web Chat' },
                    { key: 'bot' as const, label: 'Bot' },
                  ].map(tab => {
                    const isActive = chatflowTab === tab.key
                    return (
                      <div key={tab.key} className={`hs-view-tab${isActive ? ' active' : ''}`} ref={isActive ? tabEditRef : undefined} onClick={() => { setChatflowTab(tab.key); setCfPage(1) }}>
                        <span className="hs-tab-label">{tab.label}</span>
                        {isActive && cfTotal > 0 && <span className="hs-tab-count">{cfTotal.toLocaleString()}</span>}
                        {isActive && (
                          <span className="hs-tab-kebab" onClick={(e) => { e.stopPropagation(); setTabEditOpen(!tabEditOpen) }}>
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>
                          </span>
                        )}
                        <span className="hs-tab-x" onClick={(e) => { e.stopPropagation(); setChatflowTab(tab.key === 'web_chat' ? 'bot' : 'web_chat'); setCfPage(1) }}>&times;</span>
                        {isActive && tabEditOpen && (
                          <div className="hs-tab-edit-dropdown">
                            <button className="hs-tab-edit-item" disabled>Rename</button>
                            <button className="hs-tab-edit-item">Clone</button>
                            <button className="hs-tab-edit-item" disabled>Manage Sharing</button>
                          </div>
                        )}
                      </div>
                    )
                  })}
                  <div className="hs-topbar-sep" />
                  <div className="hs-view-add-wrap" ref={viewAddMenuRef} style={{ position: 'relative', display: 'inline-flex', alignSelf: 'center' }}>
                    <button className={`hs-quick-add-btn${viewAddMenuOpen ? ' active' : ''}`} title="Create or add view" type="button" aria-label="Create or add view" onClick={() => { setViewAddMenuOpen(!viewAddMenuOpen); setViewAddMenuMode('menu') }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                    </button>
                    {viewAddMenuOpen && viewAddMenuMode === 'menu' && (
                      <div className="hs-view-add-dropdown">
                        <button className="hs-view-add-item" onClick={() => setViewAddMenuMode('type')}>Create new view</button>
                        <button className="hs-view-add-item" onClick={() => { setViewAddMenuOpen(false); setViewAddMenuMode('menu') }}>Add view</button>
                      </div>
                    )}
                    {viewAddMenuOpen && viewAddMenuMode === 'type' && (
                      <div className="hs-view-add-dropdown hs-view-type-picker">
                        <button className="hs-view-type-btn" onClick={() => { setViewAddMenuOpen(false); setViewAddMenuMode('menu') }}>
                          <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                          <span>Table</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="hs-topbar-right">
                <div className="hs-add-btn-wrap" ref={addBtnRef}>
                  <button className="hs-add-contacts-btn" onClick={() => setShowCreateChatflow(true)}>
                    Create chatflow
                    <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                  </button>
                </div>
              </div>
            </div>

            {/* ROW 2: Toolbar */}
            <div className="hs-toolbar">
              <div className="hs-toolbar-left">
                <div className="hs-search-wrap">
                  <svg className="hs-search-icon" width="14" height="14" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M28.14 29.29c-.29 0-.58-.11-.81-.33l-7.58-7.58a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l7.58 7.58a1.14 1.14 0 0 1-.81 1.95"/><path d="M13.29 24.71c-6.3 0-11.43-5.13-11.43-11.43S6.98 1.86 13.29 1.86s11.43 5.13 11.43 11.43-5.13 11.43-11.43 11.43Zm0-20.57c-5.04 0-9.14 4.1-9.14 9.14s4.1 9.14 9.14 9.14 9.14-4.1 9.14-9.14-4.1-9.14-9.14-9.14"/></svg>
                  <input className="hs-search-input" placeholder="Search chatflows" value={cfSearch} onChange={(e) => { setCfSearch(e.target.value); setCfPage(1) }} />
                  {cfSearch && <button className="hs-search-clear" onClick={() => { setCfSearch(''); setCfPage(1) }}>&times;</button>}
                </div>
                <div className="hs-tb-sep" />
                <div className="hs-viewtype-group">
                  <div className="hs-viewtype-wrap" ref={viewTypeDropdownRef}>
                    <button className={`hs-tb-btn hs-viewtype-btn${viewTypeDropdownOpen ? ' active' : ''}`} onClick={() => setViewTypeDropdownOpen(!viewTypeDropdownOpen)}>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                      <span>Table</span>
                      <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                    </button>
                    {viewTypeDropdownOpen && (
                      <div className="hs-viewtype-panel">
                        <div className="hs-viewtype-header">View type</div>
                        <button className="hs-viewtype-option active" onClick={() => setViewTypeDropdownOpen(false)}>
                          <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                          <span>Table</span>
                        </button>
                      </div>
                    )}
                  </div>
                  <button className={`hs-tb-btn hs-settings-btn${tableSettingsOpen ? ' active' : ''}`} onClick={() => setTableSettingsOpen(!tableSettingsOpen)} title={tableSettingsOpen ? 'Close settings' : 'Table settings'}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                  </button>
                </div>
                <div className="hs-tb-sep" />
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Edit columns">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="18" rx="1"/><rect x="14" y="3" width="7" height="18" rx="1"/></svg>
                  <span>Edit columns</span>
                </button>
                <div className="hs-tb-sep" />
                <button className={`hs-tb-btn hs-tb-btn-bordered${quickFiltersOpen ? ' hs-tb-btn-pressed' : ''}`} title="Filters" onClick={() => setQuickFiltersOpen(!quickFiltersOpen)}>
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M30.71 16.86H13.57a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28M6.71 16.86H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M10.14 20.29c-2.52 0-4.57-2.05-4.57-4.57s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M6.71 6.57H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M30.71 6.57H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M18.14 6.57H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 10C19.05 10 17 7.95 17 5.43S19.05.86 21.57.86s4.57 2.05 4.57 4.57S24.09 10 21.57 10m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M30.71 27.14H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M18.14 27.14H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 30.57C19.05 30.57 17 28.52 17 26s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29"/></svg>
                  <span>Filters</span>
                </button>
                <div className="hs-tb-sep" />
                <button
                  className={`hs-tb-btn hs-tb-btn-bordered${cfExporting ? ' hs-tb-btn-pressed' : ''}`}
                  title="Export current view as CSV"
                  disabled={cfExporting}
                  onClick={async () => {
                    setCfExporting(true)
                    try {
                      const headers = ['Name', 'Type', 'Status', 'Language', 'Created']
                      const escCsv = (v: unknown) => { const s = String(v ?? ''); return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s }
                      const rows = cfSorted.map((cf: any) => [
                        cf.name, cf.chatflow_type === 'bot' ? 'Bot' : 'Live Chat', cf.is_active ? 'Active' : 'Inactive', cf.language, cf.created_at || '',
                      ].map(escCsv).join(','))
                      const csv = [headers.join(','), ...rows].join('\n')
                      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
                      const url = URL.createObjectURL(blob)
                      const a = document.createElement('a')
                      a.href = url
                      a.download = `chatflows-export-${new Date().toISOString().slice(0, 10)}.csv`
                      document.body.appendChild(a)
                      a.click()
                      document.body.removeChild(a)
                      URL.revokeObjectURL(url)
                    } catch (err) {
                      console.error('Export failed:', err)
                      alert('Export failed. Please try again.')
                    } finally {
                      setCfExporting(false)
                    }
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  <span>{cfExporting ? 'Exporting...' : 'Export'}</span>
                </button>
              </div>
              <div className="hs-toolbar-right">
                <div className="hs-tb-sep" />
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Clone view">
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M25.86 29.29H12.15c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-1.89 1.54-3.43 3.43-3.43h13.71c1.89 0 3.43 1.54 3.43 3.43v13.71c0 1.89-1.54 3.43-3.43 3.43M12.14 11c-.63 0-1.14.51-1.14 1.14v13.71c0 .63.51 1.14 1.14 1.14h13.71c.63 0 1.14-.51 1.14-1.14V12.14c0-.63-.51-1.14-1.14-1.14z"/><path d="M5.29 22.43c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43H19c1.89 0 3.43 1.54 3.43 3.43a1.14 1.14 0 1 1-2.28 0c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14V19c0 .63.51 1.14 1.14 1.14a1.14 1.14 0 1 1 0 2.28Z"/></svg>
                </button>
                <button className="hs-tb-btn hs-tb-btn-bordered disabled" disabled title="Save view">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                </button>
              </div>
            </div>

            {/* ROW 3: Quick filters */}
            {quickFiltersOpen && <div className="hs-quick-filters">
              <div className="hs-owner-dropdown" ref={ownerDropdownRef}>
                <button className={`hs-qf-btn hs-qf-trigger${ownerDropdownOpen ? ' open' : ''}${ownerSelected.size > 0 ? ' has-value' : ''}`} onClick={() => setOwnerDropdownOpen(!ownerDropdownOpen)}>
                  <span>Chatflow owner</span>
                  {ownerSelected.size > 0 && <span className="hs-qf-count">{ownerSelected.size}</span>}
                  <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                </button>
                {ownerDropdownOpen && (
                  <div className="hs-owner-panel">
                    <div className="hs-owner-search-wrap">
                      <svg className="hs-owner-search-icon" width="13" height="13" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                      <input className="hs-owner-search" placeholder="Search" value={ownerSearch} onChange={(e) => setOwnerSearch(e.target.value)} autoFocus />
                    </div>
                    <div className="hs-owner-list">
                      {(!ownerSearch || 'me'.includes(ownerSearch.toLowerCase()) || 'max ritchie'.includes(ownerSearch.toLowerCase())) && (
                        <label className="hs-owner-item hs-owner-me">
                          <input type="checkbox" checked={ownerSelected.has(8)} onChange={() => { setOwnerSelected(prev => { const next = new Set(prev); next.has(8) ? next.delete(8) : next.add(8); return next }) }} />
                          <span className="hs-owner-checkbox" />
                          <span className="hs-owner-label">Me</span>
                        </label>
                      )}
                      {crmUsers.filter(u => !ownerSearch || u.name.toLowerCase().includes(ownerSearch.toLowerCase())).map(user => (
                        <label className="hs-owner-item" key={user.id}>
                          <input type="checkbox" checked={ownerSelected.has(user.id)} onChange={() => { setOwnerSelected(prev => { const next = new Set(prev); next.has(user.id) ? next.delete(user.id) : next.add(user.id); return next }) }} />
                          <span className="hs-owner-checkbox" />
                          <span className="hs-owner-avatar">{user.initials || user.name.charAt(0)}</span>
                          <span className="hs-owner-label">{user.name}</span>
                        </label>
                      ))}
                    </div>
                    {(!ownerSearch || 'unassigned'.includes(ownerSearch.toLowerCase())) && (
                      <div className="hs-owner-footer">
                        <label className="hs-owner-item">
                          <input type="checkbox" checked={ownerSelected.has(0)} onChange={() => { setOwnerSelected(prev => { const next = new Set(prev); next.has(0) ? next.delete(0) : next.add(0); return next }) }} />
                          <span className="hs-owner-checkbox" />
                          <span className="hs-owner-label">Unassigned</span>
                        </label>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <button
                className={`hs-qf-btn hs-qf-trigger${cfStatusFilter !== 'all' ? ' has-value' : ''}`}
                onClick={() => {
                  const vals: Array<'all' | 'active' | 'inactive'> = ['all', 'active', 'inactive']
                  const idx = vals.indexOf(cfStatusFilter)
                  setCfStatusFilter(vals[(idx + 1) % vals.length])
                  setCfPage(1)
                }}
              >
                <span>{cfStatusFilter === 'active' ? 'Active' : cfStatusFilter === 'inactive' ? 'Inactive' : 'Status'}</span>
                {cfStatusFilter !== 'all' && <span className="hs-qf-clear" onClick={(e) => { e.stopPropagation(); setCfStatusFilter('all'); setCfPage(1) }}>&times;</span>}
                {cfStatusFilter === 'all' && <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>}
              </button>
              <button
                className={`hs-qf-btn hs-qf-trigger${cfSort.field !== 'created_at' ? ' has-value' : ''}`}
                onClick={() => {
                  const fields = ['created_at', 'name', 'chatflow_type', 'language']
                  const idx = fields.indexOf(cfSort.field)
                  const next = fields[(idx + 1) % fields.length]
                  setCfSort({ field: next, order: next === 'name' ? 'ASC' : 'DESC' })
                  setCfPage(1)
                }}
              >
                <span>Sort: {{ created_at: 'Created', name: 'Name', chatflow_type: 'Type', language: 'Language' }[cfSort.field] || 'Created'}</span>
                <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
              </button>
              <div className="hs-qf-sep" />
              <div className="hs-qf-more-wrap" ref={qfMoreRef}>
                <button className={`hs-qf-btn hs-qf-more${qfMoreOpen ? ' open' : ''}`} onClick={() => setQfMoreOpen(!qfMoreOpen)}>
                  <svg width="10" height="10" viewBox="0 0 32 32" fill="currentColor"><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V3a1.14 1.14 0 1 1 2.28 0v25.14c0 .63-.51 1.14-1.14 1.14Z"/><path d="M28.14 16.71H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28"/></svg>
                  <span>More</span>
                </button>
              </div>
            </div>}

            {/* automation-honesty-v1 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px', background: '#fffdf7', borderBottom: '1px solid #f1e8d8' }}>
              <span style={{ fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px', background: '#fff3e0', color: '#b65815', border: '1px solid #ffd9a8', textTransform: 'uppercase', letterSpacing: '0.5px', flexShrink: 0 }}>Coming soon</span>
              <span style={{ fontSize: '0.78rem', color: '#7c98b6' }}>The live-chat widget isn&apos;t wired up yet — chatflows never run on any site and can&apos;t be activated. Designing and editing chatflows works normally.</span>
            </div>
            {/* ROW 4+5+6: Content area */}
            <div className={`hs-content${tableSettingsOpen ? ' with-settings' : ''}`}>
              <div className="hs-table-area">
                {/* ROW 4: Selection bar */}
                {cfSelectedRows.size > 0 && (
                  <div className="hs-selection-bar">
                    <span className="hs-sel-count">{cfSelectedRows.size} selected</span>
                    <div className="hs-sel-sep" />
                    <button className="hs-sel-btn">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                      Edit
                    </button>
                    <button className="hs-sel-btn" style={{ color: '#dc2626' }} onClick={() => {
                      if (confirm(`Delete ${cfSelectedRows.size} chatflow(s)?`)) {
                        Promise.all([...cfSelectedRows].map(id => deleteChatflow(id))).then(() => {
                          setChatflowsData(prev => prev.filter(x => !cfSelectedRows.has(x.id)))
                          setCfSelectedRows(new Set())
                        }).catch(() => {})
                      }
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                      Delete
                    </button>
                    <button className="hs-sel-close" onClick={() => setCfSelectedRows(new Set())} title="Deselect all">&times;</button>
                  </div>
                )}

                {/* ROW 5: Data table */}
                <div className="hs-table-scroll" ref={tableScrollRef}>
                  <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                      {cfColWidthsArr.map((w, i) => <col key={i} style={{ width: w }} />)}
                    </colgroup>
                    <thead>
                      <tr>
                        <th className="hs-th-check">
                          <input type="checkbox" checked={cfPagedData.length > 0 && cfSelectedRows.size === cfPagedData.length} onChange={toggleAllCfRows} disabled={chatflowsLoading || cfPagedData.length === 0} />
                        </th>
                        {cfHeaders.map(h => (
                          <th
                            key={h.key}
                            className={`hs-th-resizable${h.sortable ? ' hs-th-sortable' : ''}${h.frozen ? ' hs-th-frozen' : ''}`}
                            style={h.frozen ? { position: 'sticky', left: 40, top: 0, zIndex: 15, background: '#f8fafc', boxShadow: '2px 0 4px rgba(0,0,0,0.06)' } : undefined}
                            onClick={h.sortable ? () => toggleCfSort(h.key) : undefined}
                          >
                            <span className="hs-th-content">
                              {h.label}
                              {h.sortable && cfSort.field === h.key && <span className="hs-sort-arrow">{cfSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                              {h.sortable && cfSort.field !== h.key && <span className="hs-th-hover-sort">{cfSortArrow}</span>}
                            </span>
                            <div className="hs-col-resize-handle" onMouseDown={(e) => onCfResizeStart(h.key, e)} onClick={(e) => e.stopPropagation()} />
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {chatflowsLoading ? (
                        <tr><td colSpan={8} className="hs-loading-cell"><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}><DotLottieReact src="/loading.lottie" loop autoplay style={{ width: 80, height: 80 }} /><span style={{ fontSize: '0.82rem', color: '#7c98b6', marginTop: 8 }}>Loading chatflows...</span></div></td></tr>
                      ) : cfPagedData.length === 0 ? (
                        <tr><td colSpan={8} className="hs-empty-cell">
                          <div style={{ padding: '60px 40px', textAlign: 'center' }}>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" style={{ margin: '0 auto 16px' }}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                            <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#33475b', marginBottom: '6px' }}>Create your first chatflow</div>
                            <div style={{ fontSize: '0.82rem', color: '#7c98b6', maxWidth: '360px', margin: '0 auto' }}>Build chat widgets and bots to engage visitors, qualify leads, and route conversations to your team.</div>
                          </div>
                        </td></tr>
                      ) : (
                        cfPagedData.map((cf) => (
                          <tr key={cf.id} className={`hs-row${cfSelectedRows.has(cf.id) ? ' selected' : ''}`}>
                            <td className="hs-td-check">
                              <input type="checkbox" checked={cfSelectedRows.has(cf.id)} onChange={() => { setCfSelectedRows(prev => { const next = new Set(prev); next.has(cf.id) ? next.delete(cf.id) : next.add(cf.id); return next }) }} />
                            </td>
                            <td className="hs-td-name" style={{ position: 'sticky', left: 40, zIndex: 4, boxShadow: '2px 0 4px rgba(0,0,0,0.06)', background: cfSelectedRows.has(cf.id) ? '#eef2ff' : 'inherit' }}>
                              <div className="hs-name-cell">
                                <div className="hs-avatar" style={{ background: cf.is_active ? '#f59e0b' : '#94a3b8' }}>
                                  {(cf.name || '?')[0].toUpperCase()}
                                </div>
                                <span className="hs-name-link" onClick={() => setEditingChatflowId(cf.id)}>{cf.name}</span>
                                <button className="hs-preview-btn" onClick={(e) => { e.stopPropagation(); setEditingChatflowId(cf.id) }}>Preview</button>
                              </div>
                            </td>
                            <td><span className="hs-status-badge" style={{ background: cf.chatflow_type === 'bot' ? '#f3e8ff' : '#e3f2fd', color: cf.chatflow_type === 'bot' ? '#7c3aed' : '#1565c0' }}>{cf.chatflow_type === 'bot' ? 'Bot' : 'Live Chat'}</span></td>
                            <td>
                              <button disabled={!cf.is_active} title={cf.is_active ? undefined : "Coming soon — the chat widget isn't wired up yet"} style={{ padding: '3px 10px', border: '1px solid #e2e8f0', borderRadius: '999px', background: cf.is_active ? '#fff3e0' : '#f5f8fa', color: cf.is_active ? '#b65815' : '#7c98b6', fontSize: '0.68rem', fontWeight: 600, cursor: cf.is_active ? 'pointer' : 'not-allowed' }}
                                onClick={(e) => { e.stopPropagation(); updateChatflow(cf.id, { is_active: !cf.is_active }).then(() => setChatflowsData(prev => prev.map(x => x.id === cf.id ? { ...x, is_active: x.is_active ? 0 : 1 } : x))) }}>
                                {cf.is_active ? 'Not running' : 'Inactive'}
                              </button>
                            </td>
                            <td style={{ color: '#516f90', fontSize: '0.8rem' }}>{cf.language || '--'}</td>
                            <td style={{ color: '#516f90', fontSize: '13px' }}>{cf.created_at ? formatDatePref(cf.created_at) : ''}</td>
                            <td onClick={(e) => e.stopPropagation()}>
                              <button onClick={() => { if (confirm('Delete this chatflow?')) deleteChatflow(cf.id).then(() => setChatflowsData(prev => prev.filter(x => x.id !== cf.id))) }}
                                style={{ background: 'none', border: 'none', color: '#7c98b6', cursor: 'pointer', fontSize: '0.78rem' }}
                                onMouseEnter={e => { e.currentTarget.style.color = '#d94c53' }}
                                onMouseLeave={e => { e.currentTarget.style.color = '#7c98b6' }}>
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* ROW 6: Pagination */}
                <div className="hs-pagination">
                  <nav className="hs-paginator" aria-label="Pagination">
                    <button className="hs-paginator-btn" disabled={cfCurrentPage <= 1} onClick={() => setCfPage((p) => p - 1)} aria-label="Previous page">
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95"/></svg>
                      <span>Prev</span>
                    </button>
                    {(() => {
                      if (cfTotalPages <= 7) {
                        const pages: number[] = []
                        for (let i = 1; i <= cfTotalPages; i++) pages.push(i)
                        return pages.map(p => (
                          <button key={p} className={`hs-paginator-num${p === cfCurrentPage ? ' active' : ''}`} onClick={() => setCfPage(p)}>{p}</button>
                        ))
                      }
                      const items: React.ReactNode[] = []
                      const addPage = (p: number) => items.push(
                        <button key={p} className={`hs-paginator-num${p === cfCurrentPage ? ' active' : ''}`} onClick={() => setCfPage(p)}>{p}</button>
                      )
                      const addEllipsis = (key: string) => items.push(
                        <span key={key} className="hs-paginator-ellipsis">...</span>
                      )
                      addPage(1)
                      if (cfCurrentPage > 3) addEllipsis('el')
                      const start = Math.max(2, cfCurrentPage - 1)
                      const end = Math.min(cfTotalPages - 1, cfCurrentPage + 1)
                      for (let i = start; i <= end; i++) addPage(i)
                      if (cfCurrentPage < cfTotalPages - 2) addEllipsis('er')
                      addPage(cfTotalPages)
                      return items
                    })()}
                    <button className="hs-paginator-btn" disabled={cfCurrentPage >= cfTotalPages} onClick={() => setCfPage((p) => p + 1)} aria-label="Next page">
                      <span>Next</span>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M11 27.71c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62l9.48-9.48-9.48-9.47a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0L22.1 15.48c.45.45.45 1.17 0 1.62L11.81 27.39c-.22.22-.52.33-.81.33Z"/></svg>
                    </button>
                  </nav>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', marginLeft: 'auto', marginRight: 50 }}><select value={tablePageSize} onChange={e => { setTablePageSize(Number(e.target.value) as any); setCfPage(1) }} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #e2e8f0', fontSize: '0.82rem' }}><option value={100}>100</option><option value={200}>200</option><option value={500}>500</option></select><span>per page</span></div>
                </div>

                <div style={{ padding: '8px 16px', fontSize: '0.82rem', color: '#7c98b6', borderTop: '1px solid #eaf0f6' }}>
                  {cfTotal} chatflow{cfTotal !== 1 ? 's' : ''}
                </div>
              </div>

              {/* Table settings panel */}
              {tableSettingsOpen && (
                <div className="hs-settings-panel">
                  <div className="hs-settings-header">
                    <span className="hs-settings-title">Table settings</span>
                    <button className="hs-settings-close" onClick={() => setTableSettingsOpen(false)}>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                    </button>
                  </div>
                  <div className="hs-settings-body">
                    <div className="hs-settings-group">
                      <label className="hs-settings-label">
                        Pagination
                        <span className="hs-info-icon" title="Number of rows shown per page">
                          <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg>
                        </span>
                      </label>
                      {([100, 200, 500] as const).map(size => (
                        <label className="hs-settings-radio" key={size}>
                          <input type="radio" name="cfPageSize" checked={tablePageSize === size} onChange={() => { setTablePageSize(size); setCfPage(1) }} />
                          <span className="hs-radio-indicator" />
                          <span>{size} per page</span>
                        </label>
                      ))}
                    </div>
                    <hr className="hs-settings-divider" />
                    <div className="hs-settings-group">
                      <label className="hs-settings-label">
                        Row height
                        <span className="hs-info-icon" title="Adjust the vertical spacing of table rows">
                          <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg>
                        </span>
                      </label>
                      {([
                        { value: 'small' as const, label: 'Compact' },
                        { value: 'medium' as const, label: 'Default' },
                        { value: 'large' as const, label: 'Comfortable' },
                      ]).map(opt => (
                        <label className="hs-settings-radio" key={opt.value}>
                          <input type="radio" name="cfRowHeight" checked={tableRowHeight === opt.value} onChange={() => setTableRowHeight(opt.value)} />
                          <span className="hs-radio-indicator" />
                          <span>{opt.label}</span>
                        </label>
                      ))}
                    </div>
                    <hr className="hs-settings-divider" />
                    <div className="hs-settings-group">
                      <div className="hs-settings-toggle-row">
                        <label className="hs-settings-label">
                          Zebra striping
                          <span className="hs-info-icon" title="Alternate row background colors for easier reading">
                            <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg>
                          </span>
                        </label>
                        <button className={`hs-toggle${tableZebraStriping ? ' on' : ''}`} onClick={() => setTableZebraStriping(!tableZebraStriping)} role="switch" aria-checked={tableZebraStriping}>
                          <span className="hs-toggle-track">
                            <span className="hs-toggle-on">ON</span>
                            <span className="hs-toggle-off">OFF</span>
                            <span className="hs-toggle-handle" />
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          )
        })()}

        {/* Chatflow Editor */}
        {activeView === 'chatflows' && editingChatflowId !== null && editingChatflow && (
          <div className="cf-editor-container">
            <div className="seq-editor-topbar">
              <div className="seq-editor-topbar-left">
                <button style={{ border: 'none', background: 'none', color: '#1976d2', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 500 }} onClick={() => { setEditingChatflowId(null); setEditingChatflow(null); setCfEditorTab('build') }}>
                  &larr; Back to Chatflows
                </button>
                <span style={{ fontSize: '0.92rem', fontWeight: 700, color: '#1e293b', marginLeft: '12px' }}>{editingChatflow.name}</span>
                <span className="hs-status-badge" style={{ background: editingChatflow.chatflow_type === 'bot' ? '#f3e8ff' : '#e3f2fd', color: editingChatflow.chatflow_type === 'bot' ? '#7c3aed' : '#1565c0', marginLeft: '8px' }}>
                  {editingChatflow.chatflow_type === 'bot' ? 'Bot' : 'Live Chat'}
                </span>
              </div>
              <div className="seq-editor-topbar-right">
                <button disabled={!editingChatflow.is_active} title={editingChatflow.is_active ? undefined : "Coming soon — the chat widget isn't wired up yet"} style={{ padding: '6px 14px', border: '1px solid #cbd6e2', borderRadius: '4px', background: '#fff', color: '#33475b', fontSize: '0.82rem', fontWeight: 500, opacity: editingChatflow.is_active ? 1 : 0.55, cursor: editingChatflow.is_active ? 'pointer' : 'not-allowed' }}
                  onClick={() => {
                    updateChatflow(editingChatflow.id, { is_active: !editingChatflow.is_active }).then(cf => setEditingChatflow({ ...editingChatflow, ...cf }))
                  }}>
                  {editingChatflow.is_active ? 'Deactivate' : 'Activate'}
                </button>
                <button className="hs-add-contacts-btn" onClick={() => {
                  updateChatflow(editingChatflow.id, {
                    welcome_message: editingChatflow.welcome_message,
                    targeting_json: editingChatflow.targeting_json,
                    appearance_json: editingChatflow.appearance_json,
                    availability_mode: editingChatflow.availability_mode,
                  }).then(() => {
                    if (editingChatflow.actions) {
                      replaceChatflowActions(editingChatflow.id, editingChatflow.actions.map((a, i) => ({ ...a, action_order: i + 1 })))
                    }
                  })
                }}>Save</button>
              </div>
            </div>
            <div className="cf-editor-tabs">
              {(['build', 'target', 'display', 'options'] as const).map(t => (
                <button key={t} className={`cf-editor-tab${cfEditorTab === t ? ' active' : ''}`} onClick={() => setCfEditorTab(t)}>
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
            <div className="cf-editor-body">
              {cfEditorTab === 'build' && (
                <div className="cf-bot-flow">
                  {/* Welcome message node */}
                  <div className="cf-bot-action" style={{ borderLeft: '3px solid #1976d2' }}>
                    <div className="cf-bot-action-header">
                      <div className="cf-bot-action-icon" style={{ background: '#e3f2fd', color: '#1565c0' }}>{'\uD83D\uDCAC'}</div>
                      <div className="cf-bot-action-title">Welcome message</div>
                    </div>
                    <div className="cf-bot-action-desc">
                      <input type="text" value={editingChatflow.welcome_message || ''} onChange={(e) => setEditingChatflow({ ...editingChatflow, welcome_message: e.target.value })}
                        style={{ width: '100%', padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.8rem', marginTop: '6px', boxSizing: 'border-box' }} placeholder="Hi! How can we help you today?" />
                    </div>
                  </div>
                  {/* Bot actions */}
                  {(editingChatflow.actions || []).map((action, idx) => {
                    const actionLabels: Record<string, string> = { send_message: 'Send message', ask_question: 'Ask question', quick_replies: 'Quick replies', set_contact_property: 'Set property', submit_ticket: 'Submit ticket', send_to_agent: 'Send to agent', book_meeting: 'Book meeting', if_then_branch: 'If/then branch', enroll_workflow: 'Enroll in workflow' }
                    const actionColors: Record<string, { bg: string; fg: string }> = { send_message: { bg: '#e3f2fd', fg: '#1565c0' }, ask_question: { bg: '#fff3e0', fg: '#e65100' }, quick_replies: { bg: '#ecfdf5', fg: '#059669' }, set_contact_property: { bg: '#f3e8ff', fg: '#7c3aed' }, submit_ticket: { bg: '#fef2f2', fg: '#dc2626' }, send_to_agent: { bg: '#ecfdf5', fg: '#059669' }, book_meeting: { bg: '#e3f2fd', fg: '#1565c0' }, if_then_branch: { bg: '#f0f9ff', fg: '#0ea5e9' }, enroll_workflow: { bg: '#fffbeb', fg: '#d97706' } }
                    const ac = actionColors[action.action_type] || { bg: '#f1f5f9', fg: '#475569' }
                    const config = action.config_json ? JSON.parse(action.config_json) : {}
                    return (
                      <div key={action.id || idx}>
                        <div className="cf-bot-connector" />
                        <div className="cf-bot-action" style={{ borderLeft: `3px solid ${ac.fg}` }}>
                          <button className="cf-bot-action-remove" onClick={() => {
                            setEditingChatflow({ ...editingChatflow, actions: editingChatflow.actions.filter((_, i) => i !== idx) })
                          }}>&times;</button>
                          <div className="cf-bot-action-header">
                            <div className="cf-bot-action-icon" style={{ background: ac.bg, color: ac.fg }}>
                              {action.action_type === 'send_message' ? '\uD83D\uDCAC' : action.action_type === 'ask_question' ? '?' : action.action_type === 'quick_replies' ? '\u2630' : action.action_type === 'send_to_agent' ? '\uD83D\uDC64' : action.action_type === 'book_meeting' ? '\uD83D\uDCC5' : action.action_type === 'submit_ticket' ? '\uD83C\uDFAB' : '\u2699'}
                            </div>
                            <div className="cf-bot-action-title">{actionLabels[action.action_type] || action.action_type}</div>
                          </div>
                          <div className="cf-bot-action-desc">
                            {action.action_type === 'send_message' && (
                              <input type="text" value={config.message || ''} onChange={(e) => {
                                const updated = [...editingChatflow.actions]
                                updated[idx] = { ...action, config_json: JSON.stringify({ ...config, message: e.target.value }) }
                                setEditingChatflow({ ...editingChatflow, actions: updated })
                              }} style={{ width: '100%', padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.78rem', marginTop: '6px', boxSizing: 'border-box' }} placeholder="Enter message..." />
                            )}
                            {action.action_type === 'ask_question' && (
                              <input type="text" value={config.question || ''} onChange={(e) => {
                                const updated = [...editingChatflow.actions]
                                updated[idx] = { ...action, config_json: JSON.stringify({ ...config, question: e.target.value }) }
                                setEditingChatflow({ ...editingChatflow, actions: updated })
                              }} style={{ width: '100%', padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.78rem', marginTop: '6px', boxSizing: 'border-box' }} placeholder="Enter question..." />
                            )}
                            {!['send_message', 'ask_question'].includes(action.action_type) && (
                              <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>Click to configure</span>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                  {/* Add action button */}
                  <div className="cf-bot-connector" />
                  <div className="cf-bot-add-action">
                    <div style={{ position: 'relative' }}>
                      <button className="seq-add-step-btn" onClick={() => {
                        const newAction: ChatflowBotAction = { id: Date.now(), chatflow_id: editingChatflow.id, action_order: (editingChatflow.actions || []).length + 1, action_type: 'send_message', config_json: '{}', parent_action_id: null, branch_label: null, next_action_id: null }
                        setEditingChatflow({ ...editingChatflow, actions: [...(editingChatflow.actions || []), newAction] })
                      }}>+</button>
                    </div>
                  </div>
                </div>
              )}
              {cfEditorTab === 'target' && (
                <div style={{ maxWidth: '600px', margin: '0 auto' }}>
                  <div className="cf-targeting-section">
                    <h4>URL Targeting</h4>
                    <p style={{ fontSize: '0.78rem', color: '#64748b', margin: '0 0 12px' }}>Show this chatflow when visitors are on specific pages.</p>
                    <select defaultValue="all" style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', fontSize: '0.84rem' }}>
                      <option value="all">All pages</option>
                      <option value="specific">Specific pages only</option>
                    </select>
                  </div>
                  <div className="cf-targeting-section">
                    <h4>Visitor Targeting</h4>
                    <select defaultValue="all" style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', fontSize: '0.84rem' }}>
                      <option value="all">All visitors</option>
                      <option value="known">Known contacts only</option>
                      <option value="unknown">Unknown visitors only</option>
                    </select>
                  </div>
                  <div className="cf-targeting-section">
                    <h4>Display Trigger</h4>
                    <select defaultValue="immediately" style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', fontSize: '0.84rem' }}>
                      <option value="immediately">Immediately on page load</option>
                      <option value="time">After time on page (seconds)</option>
                      <option value="scroll">On scroll percentage</option>
                      <option value="exit">On exit intent</option>
                    </select>
                  </div>
                </div>
              )}
              {cfEditorTab === 'display' && (
                <div className="cf-display-grid" style={{ maxWidth: '800px', margin: '0 auto' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#33475b', marginBottom: '6px' }}>Chat heading</label>
                      <input type="text" defaultValue={(() => { try { const a = JSON.parse(editingChatflow.appearance_json || '{}'); return a.heading || 'Chat with us' } catch { return 'Chat with us' } })()}
                        style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', fontSize: '0.84rem', boxSizing: 'border-box' }}
                        onChange={(e) => {
                          const a = editingChatflow.appearance_json ? JSON.parse(editingChatflow.appearance_json) : {}
                          a.heading = e.target.value
                          setEditingChatflow({ ...editingChatflow, appearance_json: JSON.stringify(a) })
                        }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#33475b', marginBottom: '6px' }}>Accent color</label>
                      <input type="color" defaultValue={(() => { try { const a = JSON.parse(editingChatflow.appearance_json || '{}'); return a.accent_color || '#1976d2' } catch { return '#1976d2' } })()}
                        style={{ width: '60px', height: '36px', border: '1px solid #cbd6e2', borderRadius: '4px', cursor: 'pointer' }}
                        onChange={(e) => {
                          const a = editingChatflow.appearance_json ? JSON.parse(editingChatflow.appearance_json) : {}
                          a.accent_color = e.target.value
                          setEditingChatflow({ ...editingChatflow, appearance_json: JSON.stringify(a) })
                        }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#33475b', marginBottom: '6px' }}>Widget position</label>
                      <select defaultValue={(() => { try { const a = JSON.parse(editingChatflow.appearance_json || '{}'); return a.position || 'right' } catch { return 'right' } })()}
                        style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', fontSize: '0.84rem' }}
                        onChange={(e) => {
                          const a = editingChatflow.appearance_json ? JSON.parse(editingChatflow.appearance_json) : {}
                          a.position = e.target.value
                          setEditingChatflow({ ...editingChatflow, appearance_json: JSON.stringify(a) })
                        }}>
                        <option value="right">Bottom right</option>
                        <option value="left">Bottom left</option>
                      </select>
                    </div>
                  </div>
                  {/* Chat widget preview */}
                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#33475b', marginBottom: '12px' }}>Preview</label>
                    <div className="cf-widget-preview">
                      <div className="cf-widget-header" style={{ background: (() => { try { return JSON.parse(editingChatflow.appearance_json || '{}').accent_color || '#1976d2' } catch { return '#1976d2' } })() }}>
                        <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>{'\uD83D\uDCAC'}</div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{(() => { try { return JSON.parse(editingChatflow.appearance_json || '{}').heading || 'Chat with us' } catch { return 'Chat with us' } })()}</div>
                          <div style={{ fontSize: '0.72rem', opacity: 0.8 }}>We typically reply in a few minutes</div>
                        </div>
                      </div>
                      <div className="cf-widget-messages">
                        <div className="cf-bubble-bot">{editingChatflow.welcome_message || 'Hi! How can we help you today?'}</div>
                      </div>
                      <div className="cf-widget-input">
                        <input type="text" placeholder="Type a message..." disabled />
                        <button disabled>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {cfEditorTab === 'options' && (
                <div style={{ maxWidth: '600px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div className="cf-targeting-section">
                    <h4>Language</h4>
                    <select value={editingChatflow.language} onChange={(e) => setEditingChatflow({ ...editingChatflow, language: e.target.value })}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', fontSize: '0.84rem' }}>
                      <option value="en">English</option>
                      <option value="es">Spanish</option>
                    </select>
                  </div>
                  <div className="cf-targeting-section">
                    <h4>Availability</h4>
                    <select value={editingChatflow.availability_mode} onChange={(e) => setEditingChatflow({ ...editingChatflow, availability_mode: e.target.value })}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', fontSize: '0.84rem' }}>
                      <option value="always">Always available</option>
                      <option value="business_hours">Custom hours</option>
                      <option value="team_availability">Based on team availability</option>
                    </select>
                  </div>
                  <div className="cf-targeting-section">
                    <h4>Away Message</h4>
                    <textarea rows={3} defaultValue="We are currently away. Please leave a message and we will get back to you."
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', fontSize: '0.84rem', resize: 'vertical', boxSizing: 'border-box' }} />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Create Chatflow Modal */}
        {showCreateChatflow && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.35)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'fadeIn 150ms ease' }}
            onClick={() => setShowCreateChatflow(false)}>
            <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: '520px', maxWidth: '90vw', boxShadow: '0 8px 30px rgba(0,0,0,0.18)' }} onClick={(e) => e.stopPropagation()}>
              <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1e293b', margin: '0 0 20px' }}>Create chatflow</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#33475b', marginBottom: '6px' }}>Name *</label>
                  <input type="text" value={newChatflowName} onChange={(e) => setNewChatflowName(e.target.value)} placeholder="e.g., Website Support Bot" style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', fontSize: '0.88rem', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#33475b', marginBottom: '10px' }}>Type</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    {([
                      { type: 'live_chat' as const, title: 'Live Chat', desc: 'Connect visitors to your team for real-time conversations' },
                      { type: 'bot' as const, title: 'Bot', desc: 'Build an automated chatbot to qualify leads and route inquiries' },
                    ]).map(opt => (
                      <div key={opt.type} onClick={() => setNewChatflowType(opt.type)} style={{ border: `2px solid ${newChatflowType === opt.type ? '#1976d2' : '#e2e8f0'}`, borderRadius: '8px', padding: '16px', cursor: 'pointer', background: newChatflowType === opt.type ? '#f0f7ff' : '#fff', transition: 'all 0.15s' }}>
                        <div style={{ fontSize: '0.84rem', fontWeight: 700, color: '#1e293b', marginBottom: '4px' }}>{opt.title}</div>
                        <div style={{ fontSize: '0.72rem', color: '#64748b', lineHeight: 1.4 }}>{opt.desc}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '24px' }}>
                <button onClick={() => setShowCreateChatflow(false)} style={{ padding: '9px 16px', border: '1px solid #cbd6e2', borderRadius: '4px', background: '#fff', color: '#33475b', fontSize: '0.85rem', fontWeight: 500, cursor: 'pointer' }}>Cancel</button>
                <button disabled={!newChatflowName.trim()} onClick={() => {
                  createChatflow({ name: newChatflowName.trim(), chatflow_type: newChatflowType })
                    .then((cf) => { setShowCreateChatflow(false); setNewChatflowName(''); setEditingChatflowId(cf.id) })
                    .catch((err) => alert(err.message))
                }} style={{ padding: '9px 20px', border: 'none', borderRadius: '4px', background: '#ff7a59', color: '#fff', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', opacity: newChatflowName.trim() ? 1 : 0.5 }}>Create</button>
              </div>
            </div>
          </div>
        )}

        {/* ========== Snippets View ========== */}
        {activeView === 'snippets' && (() => {
          // Client-side sorting + pagination
          const snipSorted = [...snippetsData].sort((a: any, b: any) => {
            const fa = a[snipSort.field] ?? ''
            const fb = b[snipSort.field] ?? ''
            const cmp = String(fa).localeCompare(String(fb), undefined, { sensitivity: 'base' })
            return snipSort.order === 'ASC' ? cmp : -cmp
          })
          const snipPageSize = tablePageSize
          const snipTotal = snipSorted.length
          const snipTotalPages = Math.max(1, Math.ceil(snipTotal / snipPageSize))
          const snipCurrentPage = Math.min(snipPage, snipTotalPages)
          const snipPagedData = snipSorted.slice((snipCurrentPage - 1) * snipPageSize, snipCurrentPage * snipPageSize)
          const toggleAllSnipRows = () => {
            if (snipSelectedRows.size === snipPagedData.length) {
              setSnipSelectedRows(new Set())
            } else {
              setSnipSelectedRows(new Set(snipPagedData.map(s => s.id)))
            }
          }
          const toggleSnipSort = (field: string) => {
            setSnipSort(prev => prev.field === field ? { field, order: prev.order === 'ASC' ? 'DESC' : 'ASC' } : { field, order: field === 'name' ? 'ASC' : 'DESC' })
            setSnipPage(1)
          }
          const snipColWidthsArr = [40, snipColWidths.name, snipColWidths.shortcut, snipColWidths.content, snipColWidths.updated_at, snipColWidths.actions]
          const onSnipResizeStart = (col: string, e: React.MouseEvent) => {
            e.preventDefault(); e.stopPropagation()
            const startX = e.clientX; const startW = (snipColWidths as any)[col]
            const onMove = (ev: MouseEvent) => { const diff = ev.clientX - startX; setSnipColWidths(prev => ({ ...prev, [col]: Math.max(60, startW + diff) })) }
            const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp) }
            document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp)
          }
          const snipSortArrow = <svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M29.38 21.96a1.147 1.147 0 0 0-1.617 0l-3.85 3.852V5.714a1.143 1.143 0 0 0-2.286 0v20.099l-3.853-3.852a1.144 1.144 0 0 0-1.615 1.617l5.803 5.802a1.143 1.143 0 0 0 1.616 0l5.802-5.802a1.144 1.144 0 0 0 0-1.617M15.84 8.422 10.039 2.62a1.145 1.145 0 0 0-1.616 0L2.62 8.422a1.144 1.144 0 0 0 1.617 1.617l3.85-3.851v20.098a1.143 1.143 0 0 0 2.286 0V6.187l3.853 3.852a1.14 1.14 0 0 0 1.616-.001 1.14 1.14 0 0 0-.001-1.616"/></svg>
          const snipHeaders = [
            { key: 'name', label: 'Name', sortable: true, frozen: true },
            { key: 'shortcut', label: 'Shortcut', sortable: true, frozen: false },
            { key: 'content', label: 'Content Preview', sortable: false, frozen: false },
            { key: 'updated_at', label: 'Last Modified', sortable: true, frozen: false },
            { key: 'actions', label: 'Actions', sortable: false, frozen: false },
          ]
          return (
          <div className="hs-contacts">
            {/* ROW 1: Topbar */}
            <div className="hs-topbar">
              <div className="hs-topbar-left">
                <div className="hs-obj-switcher-wrap" ref={objSwitcherRef}>
                  <button className={`hs-obj-switcher${objSwitcherOpen ? ' open' : ''}`} onClick={() => { setObjSwitcherOpen(!objSwitcherOpen); setObjSwitcherSearch('') }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="m18 16 4-4-4-4"/><path d="m6 8-4 4 4 4"/><path d="m14.5 4-5 16"/></svg>
                    <span>Snippets</span>
                    <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                  </button>
                  {objSwitcherOpen && (
                    <div className="hs-obj-panel">
                      <div className="hs-obj-search-wrap">
                        <svg className="hs-obj-search-icon" width="13" height="13" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                        <input className="hs-obj-search" placeholder="Search" value={objSwitcherSearch} onChange={(e) => setObjSwitcherSearch(e.target.value)} autoFocus />
                      </div>
                      <ul className="hs-obj-list" role="listbox">
                        {[
                          { label: 'Chatflows', view: 'chatflows' as ViewMode },
                          { label: 'Snippets', view: 'snippets' as ViewMode },
                          { label: 'Templates', view: 'templates' as ViewMode },
                          { label: 'Sequences', view: 'sequences' as ViewMode },
                        ].filter(item => !objSwitcherSearch || item.label.toLowerCase().includes(objSwitcherSearch.toLowerCase())).map(item => (
                          <li key={item.view} className={`hs-obj-item${item.view === activeView ? ' active' : ''}`} role="option" aria-selected={item.view === activeView} onClick={() => { setActiveView(item.view); setObjSwitcherOpen(false); setObjSwitcherSearch('') }}>{item.label}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="hs-topbar-sep" />
                <div className="hs-view-tabs">
                  {[
                    { key: '', label: 'All snippets' },
                  ].map(tab => {
                    const isActive = true
                    return (
                      <div key={tab.key} className={`hs-view-tab${isActive ? ' active' : ''}`} ref={isActive ? tabEditRef : undefined}>
                        <span className="hs-tab-label">{tab.label}</span>
                        {isActive && snipTotal > 0 && <span className="hs-tab-count">{snipTotal.toLocaleString()}</span>}
                        {isActive && (
                          <span className="hs-tab-kebab" onClick={(e) => { e.stopPropagation(); setTabEditOpen(!tabEditOpen) }}>
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>
                          </span>
                        )}
                        {isActive && tabEditOpen && (
                          <div className="hs-tab-edit-dropdown">
                            <button className="hs-tab-edit-item" disabled>Rename</button>
                            <button className="hs-tab-edit-item">Clone</button>
                            <button className="hs-tab-edit-item" disabled>Manage Sharing</button>
                          </div>
                        )}
                      </div>
                    )
                  })}
                  <div className="hs-topbar-sep" />
                  <div className="hs-view-add-wrap" ref={viewAddMenuRef} style={{ position: 'relative', display: 'inline-flex', alignSelf: 'center' }}>
                    <button className={`hs-quick-add-btn${viewAddMenuOpen ? ' active' : ''}`} title="Create or add view" type="button" aria-label="Create or add view" onClick={() => { setViewAddMenuOpen(!viewAddMenuOpen); setViewAddMenuMode('menu') }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                    </button>
                    {viewAddMenuOpen && viewAddMenuMode === 'menu' && (
                      <div className="hs-view-add-dropdown">
                        <button className="hs-view-add-item" onClick={() => setViewAddMenuMode('type')}>Create new view</button>
                        <button className="hs-view-add-item" onClick={() => { setViewAddMenuOpen(false); setViewAddMenuMode('menu') }}>Add view</button>
                      </div>
                    )}
                    {viewAddMenuOpen && viewAddMenuMode === 'type' && (
                      <div className="hs-view-add-dropdown hs-view-type-picker">
                        <button className="hs-view-type-btn" onClick={() => { setViewAddMenuOpen(false); setViewAddMenuMode('menu') }}>
                          <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                          <span>Table</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="hs-topbar-right">
                <div className="hs-add-btn-wrap" ref={addBtnRef}>
                  <button className="hs-add-contacts-btn" onClick={() => { setSnippetForm({ name: '', content: '', shortcut: '' }); setSnippetError(null); setEditingSnippet(null); setShowSnippetModal('create') }}>
                    Create snippet
                    <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                  </button>
                </div>
              </div>
            </div>

            {/* ROW 2: Toolbar */}
            <div className="hs-toolbar">
              <div className="hs-toolbar-left">
                <div className="hs-search-wrap">
                  <svg className="hs-search-icon" width="14" height="14" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M28.14 29.29c-.29 0-.58-.11-.81-.33l-7.58-7.58a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l7.58 7.58a1.14 1.14 0 0 1-.81 1.95"/><path d="M13.29 24.71c-6.3 0-11.43-5.13-11.43-11.43S6.98 1.86 13.29 1.86s11.43 5.13 11.43 11.43-5.13 11.43-11.43 11.43Zm0-20.57c-5.04 0-9.14 4.1-9.14 9.14s4.1 9.14 9.14 9.14 9.14-4.1 9.14-9.14-4.1-9.14-9.14-9.14"/></svg>
                  <input className="hs-search-input" placeholder="Search snippets" value={snippetsSearch} onChange={(e) => { setSnippetsSearch(e.target.value); setSnipPage(1) }} />
                  {snippetsSearch && <button className="hs-search-clear" onClick={() => { setSnippetsSearch(''); setSnipPage(1) }}>&times;</button>}
                </div>
                <div className="hs-tb-sep" />
                <div className="hs-viewtype-group">
                  <div className="hs-viewtype-wrap" ref={viewTypeDropdownRef}>
                    <button className={`hs-tb-btn hs-viewtype-btn${viewTypeDropdownOpen ? ' active' : ''}`} onClick={() => setViewTypeDropdownOpen(!viewTypeDropdownOpen)}>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                      <span>Table</span>
                      <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                    </button>
                    {viewTypeDropdownOpen && (
                      <div className="hs-viewtype-panel">
                        <div className="hs-viewtype-header">View type</div>
                        <button className="hs-viewtype-option active" onClick={() => setViewTypeDropdownOpen(false)}>
                          <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                          <span>Table</span>
                        </button>
                      </div>
                    )}
                  </div>
                  <button className={`hs-tb-btn hs-settings-btn${tableSettingsOpen ? ' active' : ''}`} onClick={() => setTableSettingsOpen(!tableSettingsOpen)} title={tableSettingsOpen ? 'Close settings' : 'Table settings'}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                  </button>
                </div>
                <div className="hs-tb-sep" />
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Edit columns">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="18" rx="1"/><rect x="14" y="3" width="7" height="18" rx="1"/></svg>
                  <span>Edit columns</span>
                </button>
                <div className="hs-tb-sep" />
                <button className={`hs-tb-btn hs-tb-btn-bordered${quickFiltersOpen ? ' hs-tb-btn-pressed' : ''}`} title="Filters" onClick={() => setQuickFiltersOpen(!quickFiltersOpen)}>
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M30.71 16.86H13.57a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28M6.71 16.86H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M10.14 20.29c-2.52 0-4.57-2.05-4.57-4.57s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M6.71 6.57H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M30.71 6.57H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M18.14 6.57H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 10C19.05 10 17 7.95 17 5.43S19.05.86 21.57.86s4.57 2.05 4.57 4.57S24.09 10 21.57 10m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M30.71 27.14H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M18.14 27.14H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 30.57C19.05 30.57 17 28.52 17 26s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29"/></svg>
                  <span>Filters</span>
                </button>
                <div className="hs-tb-sep" />
                <button
                  className={`hs-tb-btn hs-tb-btn-bordered${snipExporting ? ' hs-tb-btn-pressed' : ''}`}
                  title="Export current view as CSV"
                  disabled={snipExporting}
                  onClick={async () => {
                    setSnipExporting(true)
                    try {
                      const headers = ['Name', 'Shortcut', 'Content', 'Last Modified']
                      const escCsv = (v: unknown) => { const s = String(v ?? ''); return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s }
                      const rows = snipSorted.map((s: any) => [
                        s.name, s.shortcut, s.content.replace(/<[^>]*>/g, '').substring(0, 200), s.updated_at || '',
                      ].map(escCsv).join(','))
                      const csv = [headers.join(','), ...rows].join('\n')
                      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
                      const url = URL.createObjectURL(blob)
                      const a = document.createElement('a')
                      a.href = url
                      a.download = `snippets-export-${new Date().toISOString().slice(0, 10)}.csv`
                      document.body.appendChild(a)
                      a.click()
                      document.body.removeChild(a)
                      URL.revokeObjectURL(url)
                    } catch (err) {
                      console.error('Export failed:', err)
                      alert('Export failed. Please try again.')
                    } finally {
                      setSnipExporting(false)
                    }
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  <span>{snipExporting ? 'Exporting...' : 'Export'}</span>
                </button>
              </div>
              <div className="hs-toolbar-right">
                <div className="hs-tb-sep" />
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Clone view">
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M25.86 29.29H12.15c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-1.89 1.54-3.43 3.43-3.43h13.71c1.89 0 3.43 1.54 3.43 3.43v13.71c0 1.89-1.54 3.43-3.43 3.43M12.14 11c-.63 0-1.14.51-1.14 1.14v13.71c0 .63.51 1.14 1.14 1.14h13.71c.63 0 1.14-.51 1.14-1.14V12.14c0-.63-.51-1.14-1.14-1.14z"/><path d="M5.29 22.43c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43H19c1.89 0 3.43 1.54 3.43 3.43a1.14 1.14 0 1 1-2.28 0c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14V19c0 .63.51 1.14 1.14 1.14a1.14 1.14 0 1 1 0 2.28Z"/></svg>
                </button>
                <button className="hs-tb-btn hs-tb-btn-bordered disabled" disabled title="Save view">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                </button>
              </div>
            </div>

            {/* ROW 3: Quick filters */}
            {quickFiltersOpen && <div className="hs-quick-filters">
              <div className="hs-owner-dropdown" ref={ownerDropdownRef}>
                <button className={`hs-qf-btn hs-qf-trigger${ownerDropdownOpen ? ' open' : ''}${ownerSelected.size > 0 ? ' has-value' : ''}`} onClick={() => setOwnerDropdownOpen(!ownerDropdownOpen)}>
                  <span>Snippet owner</span>
                  {ownerSelected.size > 0 && <span className="hs-qf-count">{ownerSelected.size}</span>}
                  <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                </button>
                {ownerDropdownOpen && (
                  <div className="hs-owner-panel">
                    <div className="hs-owner-search-wrap">
                      <svg className="hs-owner-search-icon" width="13" height="13" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                      <input className="hs-owner-search" placeholder="Search" value={ownerSearch} onChange={(e) => setOwnerSearch(e.target.value)} autoFocus />
                    </div>
                    <div className="hs-owner-list">
                      {(!ownerSearch || 'me'.includes(ownerSearch.toLowerCase()) || 'max ritchie'.includes(ownerSearch.toLowerCase())) && (
                        <label className="hs-owner-item hs-owner-me">
                          <input type="checkbox" checked={ownerSelected.has(8)} onChange={() => { setOwnerSelected(prev => { const next = new Set(prev); next.has(8) ? next.delete(8) : next.add(8); return next }) }} />
                          <span className="hs-owner-checkbox" />
                          <span className="hs-owner-label">Me</span>
                        </label>
                      )}
                      {crmUsers.filter(u => !ownerSearch || u.name.toLowerCase().includes(ownerSearch.toLowerCase())).map(user => (
                        <label className="hs-owner-item" key={user.id}>
                          <input type="checkbox" checked={ownerSelected.has(user.id)} onChange={() => { setOwnerSelected(prev => { const next = new Set(prev); next.has(user.id) ? next.delete(user.id) : next.add(user.id); return next }) }} />
                          <span className="hs-owner-checkbox" />
                          <span className="hs-owner-avatar">{user.initials || user.name.charAt(0)}</span>
                          <span className="hs-owner-label">{user.name}</span>
                        </label>
                      ))}
                    </div>
                    {(!ownerSearch || 'unassigned'.includes(ownerSearch.toLowerCase())) && (
                      <div className="hs-owner-footer">
                        <label className="hs-owner-item">
                          <input type="checkbox" checked={ownerSelected.has(0)} onChange={() => { setOwnerSelected(prev => { const next = new Set(prev); next.has(0) ? next.delete(0) : next.add(0); return next }) }} />
                          <span className="hs-owner-checkbox" />
                          <span className="hs-owner-label">Unassigned</span>
                        </label>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <button
                className={`hs-qf-btn hs-qf-trigger${snipSort.field !== 'updated_at' ? ' has-value' : ''}`}
                onClick={() => {
                  const fields = ['updated_at', 'name', 'shortcut', 'created_at']
                  const idx = fields.indexOf(snipSort.field)
                  const next = fields[(idx + 1) % fields.length]
                  setSnipSort({ field: next, order: next === 'name' || next === 'shortcut' ? 'ASC' : 'DESC' })
                  setSnipPage(1)
                }}
              >
                <span>Sort: {{ updated_at: 'Last Modified', name: 'Name', shortcut: 'Shortcut', created_at: 'Created' }[snipSort.field] || 'Last Modified'}</span>
                <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
              </button>
              <div className="hs-qf-sep" />
              <div className="hs-qf-more-wrap" ref={qfMoreRef}>
                <button className={`hs-qf-btn hs-qf-more${qfMoreOpen ? ' open' : ''}`} onClick={() => setQfMoreOpen(!qfMoreOpen)}>
                  <svg width="10" height="10" viewBox="0 0 32 32" fill="currentColor"><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V3a1.14 1.14 0 1 1 2.28 0v25.14c0 .63-.51 1.14-1.14 1.14Z"/><path d="M28.14 16.71H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28"/></svg>
                  <span>More</span>
                </button>
              </div>
            </div>}

            {/* ROW 4+5+6: Content area */}
            <div className={`hs-content${tableSettingsOpen ? ' with-settings' : ''}`}>
              <div className="hs-table-area">
                {/* ROW 4: Selection bar */}
                {snipSelectedRows.size > 0 && (
                  <div className="hs-selection-bar">
                    <span className="hs-sel-count">{snipSelectedRows.size} selected</span>
                    <div className="hs-sel-sep" />
                    <button className="hs-sel-btn">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                      Edit
                    </button>
                    <button className="hs-sel-btn" style={{ color: '#dc2626' }} onClick={() => {
                      if (confirm(`Delete ${snipSelectedRows.size} snippet(s)?`)) {
                        Promise.all([...snipSelectedRows].map(id => deleteSnippet(id))).then(() => {
                          loadSnippets()
                          setSnipSelectedRows(new Set())
                        }).catch(() => {})
                      }
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                      Delete
                    </button>
                    <button className="hs-sel-close" onClick={() => setSnipSelectedRows(new Set())} title="Deselect all">&times;</button>
                  </div>
                )}

                {/* ROW 5: Data table */}
                <div className="hs-table-scroll" ref={tableScrollRef}>
                  <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                      {snipColWidthsArr.map((w, i) => <col key={i} style={{ width: w }} />)}
                    </colgroup>
                    <thead>
                      <tr>
                        <th className="hs-th-check">
                          <input type="checkbox" checked={snipPagedData.length > 0 && snipSelectedRows.size === snipPagedData.length} onChange={toggleAllSnipRows} disabled={snippetsLoading || snipPagedData.length === 0} />
                        </th>
                        {snipHeaders.map(h => (
                          <th
                            key={h.key}
                            className={`hs-th-resizable${h.sortable ? ' hs-th-sortable' : ''}${h.frozen ? ' hs-th-frozen' : ''}`}
                            style={h.frozen ? { position: 'sticky', left: 40, top: 0, zIndex: 15, background: '#f8fafc', boxShadow: '2px 0 4px rgba(0,0,0,0.06)' } : undefined}
                            onClick={h.sortable ? () => toggleSnipSort(h.key) : undefined}
                          >
                            <span className="hs-th-content">
                              {h.label}
                              {h.sortable && snipSort.field === h.key && <span className="hs-sort-arrow">{snipSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                              {h.sortable && snipSort.field !== h.key && <span className="hs-th-hover-sort">{snipSortArrow}</span>}
                            </span>
                            <div className="hs-col-resize-handle" onMouseDown={(e) => onSnipResizeStart(h.key, e)} onClick={(e) => e.stopPropagation()} />
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {snippetsLoading ? (
                        <tr><td colSpan={6} className="hs-loading-cell"><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}><DotLottieReact src="/loading.lottie" loop autoplay style={{ width: 80, height: 80 }} /><span style={{ fontSize: '0.82rem', color: '#7c98b6', marginTop: 8 }}>Loading snippets...</span></div></td></tr>
                      ) : snipPagedData.length === 0 ? (
                        <tr><td colSpan={6} className="hs-empty-cell">
                          <div style={{ padding: '60px 40px', textAlign: 'center' }}>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '12px' }}><path d="m18 16 4-4-4-4"/><path d="m6 8-4 4 4 4"/><path d="m14.5 4-5 16"/></svg>
                            <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#33475b', marginBottom: '6px' }}>Save time writing emails and taking notes</div>
                            <div style={{ fontSize: '0.82rem', color: '#7c98b6', maxWidth: '360px', margin: '0 auto' }}>Create reusable text blocks that can be quickly inserted using the # keyboard shortcut.</div>
                          </div>
                        </td></tr>
                      ) : (
                        snipPagedData.map((s) => (
                          <tr key={s.id} className={`hs-row${snipSelectedRows.has(s.id) ? ' selected' : ''}`}>
                            <td className="hs-td-check">
                              <input type="checkbox" checked={snipSelectedRows.has(s.id)} onChange={() => { setSnipSelectedRows(prev => { const next = new Set(prev); next.has(s.id) ? next.delete(s.id) : next.add(s.id); return next }) }} />
                            </td>
                            <td className="hs-td-name" style={{ position: 'sticky', left: 40, zIndex: 4, boxShadow: '2px 0 4px rgba(0,0,0,0.06)', background: snipSelectedRows.has(s.id) ? '#eef2ff' : 'inherit' }}>
                              <div className="hs-name-cell">
                                <div className="hs-avatar" style={{ background: '#7c3aed' }}>
                                  {(s.name || '?')[0].toUpperCase()}
                                </div>
                                <span className="hs-name-link" onClick={() => { setEditingSnippet(s); setSnippetForm({ name: s.name, content: s.content, shortcut: s.shortcut }); setSnippetError(null); setShowSnippetModal('edit') }}>{s.name}</span>
                                <button className="hs-preview-btn" onClick={(e) => { e.stopPropagation(); setEditingSnippet(s); setSnippetForm({ name: s.name, content: s.content, shortcut: s.shortcut }); setSnippetError(null); setShowSnippetModal('edit') }}>Preview</button>
                              </div>
                            </td>
                            <td><span style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: '#1976d2', background: '#e3f2fd', padding: '2px 6px', borderRadius: '4px' }}>#{s.shortcut}</span></td>
                            <td style={{ color: '#516f90', fontSize: '0.78rem', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.content.replace(/<[^>]*>/g, '').substring(0, 60)}</td>
                            <td style={{ color: '#516f90', fontSize: '13px' }}>{s.updated_at ? formatDatePref(s.updated_at) : ''}</td>
                            <td>
                              <button onClick={() => { if (confirm('Delete this snippet?')) deleteSnippet(s.id).then(() => loadSnippets()).catch(() => {}) }}
                                style={{ background: 'none', border: 'none', color: '#7c98b6', cursor: 'pointer', fontSize: '0.78rem' }}
                                onMouseEnter={e => { e.currentTarget.style.color = '#d94c53' }}
                                onMouseLeave={e => { e.currentTarget.style.color = '#7c98b6' }}>
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* ROW 6: Pagination */}
                <div className="hs-pagination">
                  <nav className="hs-paginator" aria-label="Pagination">
                    <button className="hs-paginator-btn" disabled={snipCurrentPage <= 1} onClick={() => setSnipPage((p) => p - 1)} aria-label="Previous page">
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95"/></svg>
                      <span>Prev</span>
                    </button>
                    {(() => {
                      if (snipTotalPages <= 7) {
                        const pages: number[] = []
                        for (let i = 1; i <= snipTotalPages; i++) pages.push(i)
                        return pages.map(p => (
                          <button key={p} className={`hs-paginator-num${p === snipCurrentPage ? ' active' : ''}`} onClick={() => setSnipPage(p)}>{p}</button>
                        ))
                      }
                      const items: React.ReactNode[] = []
                      const addPage = (p: number) => items.push(
                        <button key={p} className={`hs-paginator-num${p === snipCurrentPage ? ' active' : ''}`} onClick={() => setSnipPage(p)}>{p}</button>
                      )
                      const addEllipsis = (key: string) => items.push(
                        <span key={key} className="hs-paginator-ellipsis">...</span>
                      )
                      addPage(1)
                      if (snipCurrentPage > 3) addEllipsis('el')
                      const start = Math.max(2, snipCurrentPage - 1)
                      const end = Math.min(snipTotalPages - 1, snipCurrentPage + 1)
                      for (let i = start; i <= end; i++) addPage(i)
                      if (snipCurrentPage < snipTotalPages - 2) addEllipsis('er')
                      addPage(snipTotalPages)
                      return items
                    })()}
                    <button className="hs-paginator-btn" disabled={snipCurrentPage >= snipTotalPages} onClick={() => setSnipPage((p) => p + 1)} aria-label="Next page">
                      <span>Next</span>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M11 27.71c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62l9.48-9.48-9.48-9.47a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0L22.1 15.48c.45.45.45 1.17 0 1.62L11.81 27.39c-.22.22-.52.33-.81.33Z"/></svg>
                    </button>
                  </nav>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', marginLeft: 'auto' }}><select value={tablePageSize} onChange={e => { setTablePageSize(Number(e.target.value) as any); setSnipPage(1) }} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #e2e8f0', fontSize: '0.82rem' }}><option value={100}>100</option><option value={200}>200</option><option value={500}>500</option></select><span>per page</span></div>
                </div>

                <div style={{ padding: '8px 16px', fontSize: '0.82rem', color: '#7c98b6', borderTop: '1px solid #eaf0f6' }}>
                  {snipTotal} snippet{snipTotal !== 1 ? 's' : ''}
                </div>
              </div>

              {/* Table settings panel */}
              {tableSettingsOpen && (
                <div className="hs-settings-panel">
                  <div className="hs-settings-header">
                    <span className="hs-settings-title">Table settings</span>
                    <button className="hs-settings-close" onClick={() => setTableSettingsOpen(false)}>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                    </button>
                  </div>
                  <div className="hs-settings-body">
                    <div className="hs-settings-group">
                      <label className="hs-settings-label">
                        Pagination
                        <span className="hs-info-icon" title="Number of rows shown per page">
                          <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg>
                        </span>
                      </label>
                      {([100, 200, 500] as const).map(size => (
                        <label className="hs-settings-radio" key={size}>
                          <input type="radio" name="snipPageSize" checked={tablePageSize === size} onChange={() => { setTablePageSize(size); setSnipPage(1) }} />
                          <span className="hs-radio-indicator" />
                          <span>{size} per page</span>
                        </label>
                      ))}
                    </div>
                    <hr className="hs-settings-divider" />
                    <div className="hs-settings-group">
                      <label className="hs-settings-label">
                        Row height
                        <span className="hs-info-icon" title="Adjust the vertical spacing of table rows">
                          <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg>
                        </span>
                      </label>
                      {([
                        { value: 'small' as const, label: 'Compact' },
                        { value: 'medium' as const, label: 'Default' },
                        { value: 'large' as const, label: 'Comfortable' },
                      ]).map(opt => (
                        <label className="hs-settings-radio" key={opt.value}>
                          <input type="radio" name="snipRowHeight" checked={tableRowHeight === opt.value} onChange={() => setTableRowHeight(opt.value)} />
                          <span className="hs-radio-indicator" />
                          <span>{opt.label}</span>
                        </label>
                      ))}
                    </div>
                    <hr className="hs-settings-divider" />
                    <div className="hs-settings-group">
                      <div className="hs-settings-toggle-row">
                        <label className="hs-settings-label">
                          Zebra striping
                          <span className="hs-info-icon" title="Alternate row background colors for easier reading">
                            <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg>
                          </span>
                        </label>
                        <button className={`hs-toggle${tableZebraStriping ? ' on' : ''}`} onClick={() => setTableZebraStriping(!tableZebraStriping)} role="switch" aria-checked={tableZebraStriping}>
                          <span className="hs-toggle-track">
                            <span className="hs-toggle-on">ON</span>
                            <span className="hs-toggle-off">OFF</span>
                            <span className="hs-toggle-handle" />
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          )
        })()}

        {/* ========== Message Templates View ========== */}
        {activeView === 'templates' && (() => {
          // Client-side sorting + pagination
          const tmplSorted = [...templatesData].sort((a: any, b: any) => {
            const fa = a[tmplSort.field] ?? ''
            const fb = b[tmplSort.field] ?? ''
            if (tmplSort.field === 'use_count') return tmplSort.order === 'ASC' ? (fa as number) - (fb as number) : (fb as number) - (fa as number)
            const cmp = String(fa).localeCompare(String(fb), undefined, { sensitivity: 'base' })
            return tmplSort.order === 'ASC' ? cmp : -cmp
          })
          const tmplPageSize = tablePageSize
          const tmplTotal = tmplSorted.length
          const tmplTotalPages = Math.max(1, Math.ceil(tmplTotal / tmplPageSize))
          const tmplCurrentPage = Math.min(tmplPage, tmplTotalPages)
          const tmplPagedData = tmplSorted.slice((tmplCurrentPage - 1) * tmplPageSize, tmplCurrentPage * tmplPageSize)
          const toggleAllTmplRows = () => {
            if (tmplSelectedRows.size === tmplPagedData.length) {
              setTmplSelectedRows(new Set())
            } else {
              setTmplSelectedRows(new Set(tmplPagedData.map(t => t.id)))
            }
          }
          const toggleTmplSort = (field: string) => {
            setTmplSort(prev => prev.field === field ? { field, order: prev.order === 'ASC' ? 'DESC' : 'ASC' } : { field, order: field === 'name' ? 'ASC' : 'DESC' })
            setTmplPage(1)
          }
          const tmplColWidths = [40, 260, 100, 250, 70, 140, 80]
          const tmplHeaders = [
            { key: 'name', label: 'Name', sortable: true, frozen: true },
            { key: 'category', label: 'Category', sortable: true, frozen: false },
            { key: 'subject', label: 'Subject', sortable: true, frozen: false },
            { key: 'use_count', label: 'Uses', sortable: true, frozen: false },
            { key: 'updated_at', label: 'Last Modified', sortable: true, frozen: false },
            { key: 'actions', label: 'Actions', sortable: false, frozen: false },
          ]
          return (
          <div className="hs-contacts">
            {/* ROW 1: Top bar — object switcher + view tabs + actions */}
            <div className="hs-topbar">
              <div className="hs-topbar-left">
                {/* 1A: Object switcher */}
                <div className="hs-obj-switcher-wrap" ref={objSwitcherRef}>
                  <button className={`hs-obj-switcher${objSwitcherOpen ? ' open' : ''}`} onClick={() => { setObjSwitcherOpen(!objSwitcherOpen); setObjSwitcherSearch('') }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                    <span>Templates</span>
                    <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                  </button>
                  {objSwitcherOpen && (
                    <div className="hs-obj-panel">
                      <div className="hs-obj-search-wrap">
                        <svg className="hs-obj-search-icon" width="13" height="13" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                        <input
                          className="hs-obj-search"
                          placeholder="Search"
                          value={objSwitcherSearch}
                          onChange={(e) => setObjSwitcherSearch(e.target.value)}
                          autoFocus
                        />
                      </div>
                      <ul className="hs-obj-list" role="listbox">
                        {[
                          { label: 'Contacts', view: 'contacts' as ViewMode },
                            { label: 'Clients', view: 'companies' as ViewMode },
                          { label: 'Deals', view: 'deals' as ViewMode },
                          { label: 'Leads', view: 'leads' as ViewMode },
                          { label: 'Projects', view: 'projects' as ViewMode },
                          { label: 'Tickets', view: 'tickets' as ViewMode },
                          { label: 'Calls', view: 'calls' as ViewMode },
                        ]
                          .filter(item => !objSwitcherSearch || item.label.toLowerCase().includes(objSwitcherSearch.toLowerCase()))
                          .map(item => (
                            <li
                              key={item.view}
                              className={`hs-obj-item${item.view === activeView ? ' active' : ''}`}
                              role="option"
                              aria-selected={item.view === activeView}
                              onClick={() => {
                                setActiveView(item.view)
                                setObjSwitcherOpen(false)
                                setObjSwitcherSearch('')
                              }}
                            >
                              {item.label}
                            </li>
                          ))
                        }
                      </ul>
                    </div>
                  )}
                </div>

                {/* 1B: Separator */}
                <div className="hs-topbar-sep" />

                {/* 1C: View tabs */}
                <div className="hs-view-tabs">
                  {[
                    { key: '', label: 'All' },
                    { key: 'email', label: 'Email' },
                    { key: 'sms', label: 'SMS' },
                    { key: 'note', label: 'Note' },
                  ].map(tab => {
                    const isActive = templatesCategoryFilter === tab.key
                    return (
                      <div
                        key={tab.key}
                        className={`hs-view-tab${isActive ? ' active' : ''}`}
                        ref={isActive ? tabEditRef : undefined}
                        onClick={() => { setTemplatesCategoryFilter(tab.key); setTmplPage(1) }}
                      >
                        <span className="hs-tab-label">{tab.label}</span>
                        {isActive && tmplTotal > 0 && <span className="hs-tab-count">{tmplTotal.toLocaleString()}</span>}
                        {isActive && (
                          <span className="hs-tab-kebab" onClick={(e) => { e.stopPropagation(); setTabEditOpen(!tabEditOpen) }}>
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>
                          </span>
                        )}
                        {tab.key !== '' && <span className="hs-tab-x" onClick={(e) => { e.stopPropagation(); setTemplatesCategoryFilter(''); setTmplPage(1) }}>&times;</span>}
                        {isActive && tabEditOpen && (
                          <div className="hs-tab-edit-dropdown">
                            <button className="hs-tab-edit-item" disabled>Rename</button>
                            <button className="hs-tab-edit-item">Clone</button>
                            <button className="hs-tab-edit-item" disabled>Manage Sharing</button>
                            {tab.key !== '' && <button className="hs-tab-edit-item" onClick={() => { setTemplatesCategoryFilter(''); setTmplPage(1); setTabEditOpen(false) }}>Delete</button>}
                          </div>
                        )}
                      </div>
                    )
                  })}

                  {/* Custom view tabs */}
                  {(customViews['templates'] || []).map(cv => {
                    const isActive = templatesCategoryFilter === `custom:${cv.id}`
                    const isRenaming = renamingViewId === cv.id
                    return (
                      <div
                        key={cv.id}
                        className={`hs-view-tab${isActive ? ' active' : ''}`}
                        onClick={() => { if (!isRenaming) setTemplatesCategoryFilter(`custom:${cv.id}`) }}
                      >
                        {isRenaming ? (
                          <input
                            ref={renamingViewRef}
                            className="hs-tab-rename-input"
                            value={renamingViewName}
                            onChange={(e) => setRenamingViewName(e.target.value)}
                            onFocus={(e) => e.target.select()}
                            onBlur={() => {
                              if (renamingViewName.trim()) {
                                setCustomViews(prev => ({
                                  ...prev,
                                  templates: (prev['templates'] || []).map(v => v.id === cv.id ? { ...v, label: renamingViewName.trim() } : v)
                                }))
                              }
                              setRenamingViewId(null)
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
                              if (e.key === 'Escape') { setRenamingViewName(cv.label); setRenamingViewId(null) }
                            }}
                            onClick={(e) => e.stopPropagation()}
                            autoFocus
                          />
                        ) : (
                          <span className="hs-tab-label">{cv.label}</span>
                        )}
                        {isActive && !isRenaming && tmplTotal > 0 && <span className="hs-tab-count">{tmplTotal.toLocaleString()}</span>}
                        {isActive && !isRenaming && (
                          <span className="hs-tab-kebab" onClick={(e) => { e.stopPropagation(); setTabEditOpen(!tabEditOpen) }}>
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>
                          </span>
                        )}
                        {isActive && tabEditOpen && !isRenaming && (
                          <div className="hs-tab-edit-dropdown">
                            <button className="hs-tab-edit-item" onClick={() => { setRenamingViewId(cv.id); setRenamingViewName(cv.label); setTabEditOpen(false) }}>Rename</button>
                            <button className="hs-tab-edit-item" onClick={() => {
                              const id = Date.now().toString(36)
                              setCustomViews(prev => ({
                                ...prev,
                                templates: [...(prev['templates'] || []), { id, label: `${cv.label} (clone)` }]
                              }))
                              setTemplatesCategoryFilter(`custom:${id}`)
                              setRenamingViewId(id)
                              setRenamingViewName(`${cv.label} (clone)`)
                              setTabEditOpen(false)
                            }}>Clone</button>
                            <button className="hs-tab-edit-item" onClick={() => {
                              setCustomViews(prev => ({
                                ...prev,
                                templates: (prev['templates'] || []).filter(v => v.id !== cv.id)
                              }))
                              setTemplatesCategoryFilter('')
                              setTabEditOpen(false)
                            }}>Delete</button>
                          </div>
                        )}
                        <span className="hs-tab-x" onClick={(e) => {
                          e.stopPropagation()
                          setCustomViews(prev => ({
                            ...prev,
                            templates: (prev['templates'] || []).filter(v => v.id !== cv.id)
                          }))
                          if (isActive) setTemplatesCategoryFilter('')
                        }}>&times;</span>
                      </div>
                    )
                  })}

                  {/* 1D: Quick-add view button */}
                  <div className="hs-topbar-sep" />
                  <div className="hs-view-add-wrap" ref={viewAddMenuRef} style={{ position: 'relative', display: 'inline-flex', alignSelf: 'center' }}>
                    <button
                      className={`hs-quick-add-btn${viewAddMenuOpen ? ' active' : ''}`}
                      title="Create or add view"
                      type="button"
                      aria-label="Create or add view"
                      onClick={() => { setViewAddMenuOpen(!viewAddMenuOpen); setViewAddMenuMode('menu') }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                    </button>
                    {viewAddMenuOpen && viewAddMenuMode === 'menu' && (
                      <div className="hs-view-add-dropdown">
                        <button className="hs-view-add-item" onClick={() => setViewAddMenuMode('type')}>
                          Create new view
                        </button>
                        <button className="hs-view-add-item" onClick={() => {
                          setViewAddMenuOpen(false)
                          setViewAddMenuMode('menu')
                        }}>
                          Add view ({(customViews['templates'] || []).length}/50)
                        </button>
                      </div>
                    )}
                    {viewAddMenuOpen && viewAddMenuMode === 'type' && (
                      <div className="hs-view-add-dropdown hs-view-type-picker">
                        <button className="hs-view-type-btn" onClick={() => {
                          const id = Date.now().toString(36)
                          const viewName = `All templates-${(customViews['templates'] || []).length + 1}`
                          setCustomViews(prev => ({
                            ...prev,
                            templates: [...(prev['templates'] || []), { id, label: viewName }]
                          }))
                          setTemplatesCategoryFilter(`custom:${id}`)
                          setRenamingViewId(id)
                          setRenamingViewName(viewName)
                          setViewAddMenuOpen(false)
                          setViewAddMenuMode('menu')
                        }}>
                          <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                          <span>Table</span>
                        </button>
                        <button className="hs-view-type-btn" disabled style={{ opacity: 0.4 }}>
                          <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M11.43 11.43h-8a1.14 1.14 0 1 1 0-2.28h8a1.14 1.14 0 1 1 0 2.28M28.57 11.43h-8a1.14 1.14 0 1 1 0-2.28h8a1.14 1.14 0 1 1 0 2.28"/><path d="M9.14 29.71H5.71c-1.89 0-3.43-1.54-3.43-3.43V5.71c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v14.86c0 1.89-1.54 3.43-3.43 3.43h-3.43c-1.89 0-3.43-1.54-3.43-3.43v-1.14h-6.86v6.86c0 1.89-1.54 3.43-3.43 3.43Z"/></svg>
                          <span>Board</span>
                        </button>
                        <button className="hs-view-type-btn" disabled style={{ opacity: 0.4 }}>
                          <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M7.57 24.71c-.63 0-1.14-.51-1.14-1.14v-6.86a1.14 1.14 0 1 1 2.28 0v6.86c0 .63-.51 1.14-1.14 1.14M12.14 24.71c-.63 0-1.14-.51-1.14-1.14v-16c0-.63.51-1.14 1.14-1.14h4.57a1.14 1.14 0 1 1 0 2.28h-3.43v14.86c0 .63-.51 1.14-1.14 1.14"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v20.57c0 1.89-1.54 3.43-3.43 3.43Z"/></svg>
                          <span>Report</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 1E: Right side — New template (direct action, Import removed) */}
              <div className="hs-topbar-right">
                <button className="hs-add-contacts-btn" onClick={() => { setTemplateForm({ name: '', category: 'email', subject: '', body: '' }); setTemplateError(null); setEditingTemplate(null); setShowTemplateModal('create') }}>
                  New template
                </button>
              </div>
            </div>

            {/* ROW 2: Toolbar */}
            <div className="hs-toolbar">
              <div className="hs-toolbar-left">
                {/* 2A: Search */}
                <div className="hs-search-wrap">
                  <svg className="hs-search-icon" width="14" height="14" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M28.14 29.29c-.29 0-.58-.11-.81-.33l-7.58-7.58a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l7.58 7.58a1.14 1.14 0 0 1-.81 1.95"/><path d="M13.29 24.71c-6.3 0-11.43-5.13-11.43-11.43S6.98 1.86 13.29 1.86s11.43 5.13 11.43 11.43-5.13 11.43-11.43 11.43Zm0-20.57c-5.04 0-9.14 4.1-9.14 9.14s4.1 9.14 9.14 9.14 9.14-4.1 9.14-9.14-4.1-9.14-9.14-9.14"/></svg>
                  <input
                    className="hs-search-input"
                    placeholder="Search templates"
                    value={templatesSearch}
                    onChange={(e) => { setTemplatesSearch(e.target.value); setTmplPage(1) }}
                  />
                  {templatesSearch && (
                    <button className="hs-search-clear" onClick={() => { setTemplatesSearch(''); setTmplPage(1) }}>&times;</button>
                  )}
                </div>

                <div className="hs-tb-sep" />

                {/* 2B: View type group (Table dropdown + Settings gear) */}
                <div className="hs-viewtype-group">
                  <div className="hs-viewtype-wrap" ref={viewTypeDropdownRef}>
                    <button
                      className={`hs-tb-btn hs-viewtype-btn${viewTypeDropdownOpen ? ' active' : ''}`}
                      onClick={() => setViewTypeDropdownOpen(!viewTypeDropdownOpen)}
                    >
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                      <span>Table</span>
                      <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                    </button>
                    {viewTypeDropdownOpen && (
                      <div className="hs-viewtype-panel">
                        <div className="hs-viewtype-header">View type</div>
                        <button className="hs-viewtype-option active" onClick={() => setViewTypeDropdownOpen(false)}>
                          <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                          <span>Table</span>
                        </button>
                      </div>
                    )}
                  </div>
                  <button
                    className={`hs-tb-btn hs-settings-btn${tableSettingsOpen ? ' active' : ''}`}
                    onClick={() => setTableSettingsOpen(!tableSettingsOpen)}
                    title={tableSettingsOpen ? 'Close settings' : 'Table settings'}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                  </button>
                </div>

                {/* 2C: Edit columns */}
                <div className="hs-tb-sep" />
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Edit columns">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="18" rx="1"/><rect x="14" y="3" width="7" height="18" rx="1"/></svg>
                  <span>Edit columns</span>
                </button>

                <div className="hs-tb-sep" />

                {/* 2D: Filters toggle */}
                <button className={`hs-tb-btn hs-tb-btn-bordered${quickFiltersOpen ? ' hs-tb-btn-pressed' : ''}`} title="Filters" onClick={() => setQuickFiltersOpen(!quickFiltersOpen)}>
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M30.71 16.86H13.57a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28M6.71 16.86H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M10.14 20.29c-2.52 0-4.57-2.05-4.57-4.57s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M6.71 6.57H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M30.71 6.57H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M18.14 6.57H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 10C19.05 10 17 7.95 17 5.43S19.05.86 21.57.86s4.57 2.05 4.57 4.57S24.09 10 21.57 10m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M30.71 27.14H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M18.14 27.14H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 30.57C19.05 30.57 17 28.52 17 26s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29"/></svg>
                  <span>Filters</span>
                </button>

                <div className="hs-tb-sep" />

                {/* 2E: Export */}
                <button
                  className={`hs-tb-btn hs-tb-btn-bordered${tmplExporting ? ' hs-tb-btn-pressed' : ''}`}
                  title="Export current view as CSV"
                  disabled={tmplExporting}
                  onClick={async () => {
                    setTmplExporting(true)
                    try {
                      const headers = ['Name', 'Category', 'Subject', 'Uses', 'Last Modified']
                      const escCsv = (v: unknown) => { const s = String(v ?? ''); return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s }
                      const rows = tmplSorted.map((t: any) => [
                        t.name, t.category, t.subject || '', t.use_count, t.updated_at || '',
                      ].map(escCsv).join(','))
                      const csv = [headers.join(','), ...rows].join('\n')
                      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
                      const url = URL.createObjectURL(blob)
                      const a = document.createElement('a')
                      a.href = url
                      a.download = `templates-export-${new Date().toISOString().slice(0, 10)}.csv`
                      document.body.appendChild(a)
                      a.click()
                      document.body.removeChild(a)
                      URL.revokeObjectURL(url)
                    } catch (err) {
                      console.error('Export failed:', err)
                      alert('Export failed. Please try again.')
                    } finally {
                      setTmplExporting(false)
                    }
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  <span>{tmplExporting ? 'Exporting...' : 'Export'}</span>
                </button>
              </div>

              {/* Right side toolbar */}
              <div className="hs-toolbar-right">
                <div className="hs-tb-sep" />
                {/* 2F: Clone view */}
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Clone view" onClick={() => {
                  const currentCustom = (customViews['templates'] || []).find(v => `custom:${v.id}` === templatesCategoryFilter)
                  const baseName = currentCustom ? currentCustom.label : 'All templates'
                  const id = Date.now().toString(36)
                  const cloneName = `${baseName} (clone)`
                  setCustomViews(prev => ({
                    ...prev,
                    templates: [...(prev['templates'] || []), { id, label: cloneName }]
                  }))
                  setTemplatesCategoryFilter(`custom:${id}`)
                  setRenamingViewId(id)
                  setRenamingViewName(cloneName)
                }}>
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M25.86 29.29H12.15c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-1.89 1.54-3.43 3.43-3.43h13.71c1.89 0 3.43 1.54 3.43 3.43v13.71c0 1.89-1.54 3.43-3.43 3.43M12.14 11c-.63 0-1.14.51-1.14 1.14v13.71c0 .63.51 1.14 1.14 1.14h13.71c.63 0 1.14-.51 1.14-1.14V12.14c0-.63-.51-1.14-1.14-1.14z"/><path d="M5.29 22.43c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43H19c1.89 0 3.43 1.54 3.43 3.43a1.14 1.14 0 1 1-2.28 0c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14V19c0 .63.51 1.14 1.14 1.14a1.14 1.14 0 1 1 0 2.28Z"/></svg>
                </button>
                {/* 2G: Save view */}
                {(() => {
                  const isCustom = templatesCategoryFilter.startsWith('custom:')
                  return (
                    <button
                      className={`hs-tb-btn hs-tb-btn-bordered${!isCustom ? ' disabled' : ''}`}
                      disabled={!isCustom}
                      title="Save view"
                      onClick={() => {
                        if (!isCustom) return
                        const btn = document.querySelector('.hs-tb-btn[title="Save view"]') as HTMLElement
                        if (btn) { btn.style.color = '#22c55e'; setTimeout(() => { btn.style.color = '' }, 800) }
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                    </button>
                  )
                })()}
              </div>
            </div>

            {/* ROW 3: Quick filters bar */}
            {quickFiltersOpen && <div className="hs-quick-filters">
              {/* 3A: Owner/primary filter dropdown */}
              <div className="hs-owner-dropdown" ref={ownerDropdownRef}>
                <button
                  className={`hs-qf-btn hs-qf-trigger${ownerDropdownOpen ? ' open' : ''}${ownerSelected.size > 0 ? ' has-value' : ''}`}
                  onClick={() => setOwnerDropdownOpen(!ownerDropdownOpen)}
                >
                  <span>Template owner</span>
                  {ownerSelected.size > 0 && <span className="hs-qf-count">{ownerSelected.size}</span>}
                  <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                </button>
                {ownerDropdownOpen && (
                  <div className="hs-owner-panel">
                    <div className="hs-owner-search-wrap">
                      <svg className="hs-owner-search-icon" width="13" height="13" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                      <input
                        className="hs-owner-search"
                        placeholder="Search"
                        value={ownerSearch}
                        onChange={(e) => setOwnerSearch(e.target.value)}
                        autoFocus
                      />
                    </div>
                    <div className="hs-owner-list">
                      {(!ownerSearch || 'me'.includes(ownerSearch.toLowerCase()) || 'max ritchie'.includes(ownerSearch.toLowerCase())) && (
                        <label className="hs-owner-item hs-owner-me">
                          <input
                            type="checkbox"
                            checked={ownerSelected.has(8)}
                            onChange={() => {
                              setOwnerSelected(prev => {
                                const next = new Set(prev)
                                next.has(8) ? next.delete(8) : next.add(8)
                                return next
                              })
                            }}
                          />
                          <span className="hs-owner-checkbox" />
                          <span className="hs-owner-label">Me</span>
                        </label>
                      )}
                      {crmUsers
                        .filter(u => !ownerSearch || u.name.toLowerCase().includes(ownerSearch.toLowerCase()))
                        .map(user => (
                          <label className="hs-owner-item" key={user.id}>
                            <input
                              type="checkbox"
                              checked={ownerSelected.has(user.id)}
                              onChange={() => {
                                setOwnerSelected(prev => {
                                  const next = new Set(prev)
                                  next.has(user.id) ? next.delete(user.id) : next.add(user.id)
                                  return next
                                })
                              }}
                            />
                            <span className="hs-owner-checkbox" />
                            <span className="hs-owner-avatar">{user.initials || user.name.charAt(0)}</span>
                            <span className="hs-owner-label">{user.name}</span>
                          </label>
                        ))
                      }
                    </div>
                    {(!ownerSearch || 'unassigned'.includes(ownerSearch.toLowerCase())) && (
                      <div className="hs-owner-footer">
                        <label className="hs-owner-item">
                          <input
                            type="checkbox"
                            checked={ownerSelected.has(0)}
                            onChange={() => {
                              setOwnerSelected(prev => {
                                const next = new Set(prev)
                                next.has(0) ? next.delete(0) : next.add(0)
                                return next
                              })
                            }}
                          />
                          <span className="hs-owner-checkbox" />
                          <span className="hs-owner-label">Unassigned</span>
                        </label>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* 3B: Category cycle-toggle */}
              <button
                className={`hs-qf-btn hs-qf-trigger${templatesCategoryFilter && !templatesCategoryFilter.startsWith('custom:') ? ' has-value' : ''}`}
                onClick={() => {
                  const vals = ['', 'email', 'sms', 'note']
                  const idx = vals.indexOf(templatesCategoryFilter)
                  const next = vals[(idx + 1) % vals.length]
                  setTemplatesCategoryFilter(next)
                  setTmplPage(1)
                }}
              >
                <span>{templatesCategoryFilter === 'email' ? 'Email' : templatesCategoryFilter === 'sms' ? 'SMS' : templatesCategoryFilter === 'note' ? 'Note' : 'Category'}</span>
                {templatesCategoryFilter && !templatesCategoryFilter.startsWith('custom:') && (
                  <span className="hs-qf-clear" onClick={(e) => { e.stopPropagation(); setTemplatesCategoryFilter(''); setTmplPage(1) }}>&times;</span>
                )}
                {(!templatesCategoryFilter || templatesCategoryFilter.startsWith('custom:')) && <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>}
              </button>

              {/* 3C: Sort by cycle-toggle */}
              <button
                className={`hs-qf-btn hs-qf-trigger${tmplSort.field !== 'updated_at' ? ' has-value' : ''}`}
                onClick={() => {
                  const fields = ['updated_at', 'name', 'use_count', 'created_at']
                  const idx = fields.indexOf(tmplSort.field)
                  const next = fields[(idx + 1) % fields.length]
                  setTmplSort({ field: next, order: next === 'name' ? 'ASC' : 'DESC' })
                  setTmplPage(1)
                }}
              >
                <span>Sort: {{ updated_at: 'Last Modified', name: 'Name', use_count: 'Uses', created_at: 'Created' }[tmplSort.field] || 'Last Modified'}</span>
                <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
              </button>

              {/* 3E: Separator + 3F: +More popover */}
              <div className="hs-qf-sep" />

              <div className="hs-qf-more-wrap" ref={qfMoreRef}>
                <button className={`hs-qf-btn hs-qf-more${qfMoreOpen ? ' open' : ''}`} onClick={() => setQfMoreOpen(!qfMoreOpen)}>
                  <svg width="10" height="10" viewBox="0 0 32 32" fill="currentColor"><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V3a1.14 1.14 0 1 1 2.28 0v25.14c0 .63-.51 1.14-1.14 1.14Z"/><path d="M28.14 16.71H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28"/></svg>
                  <span>More</span>
                </button>
              </div>
            </div>}

            {/* ROW 4+5+6: Content area */}
            <div className={`hs-content${tableSettingsOpen ? ' with-settings' : ''}`}>
              <div className="hs-table-area">
                {/* ROW 4: Selection bar */}
                {tmplSelectedRows.size > 0 && (
                  <div className="hs-selection-bar">
                    <span className="hs-sel-count">{tmplSelectedRows.size} selected</span>
                    <div className="hs-sel-sep" />
                    <button className="hs-sel-btn">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                      Edit
                    </button>
                    <button className="hs-sel-btn">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                      Assign
                    </button>
                    <button className="hs-sel-btn" style={{ color: '#dc2626' }} onClick={() => {
                      if (confirm(`Delete ${tmplSelectedRows.size} template(s)?`)) {
                        Promise.all([...tmplSelectedRows].map(id => deleteTemplate(id))).then(() => {
                          loadTemplates()
                          setTmplSelectedRows(new Set())
                        }).catch(() => {})
                      }
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                      Delete
                    </button>
                    <button className="hs-sel-btn">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
                      Create tasks
                    </button>
                    <button className="hs-sel-close" onClick={() => setTmplSelectedRows(new Set())} title="Deselect all">&times;</button>
                  </div>
                )}

                {/* ROW 5: Data table */}
                <div className="hs-table-scroll" ref={tableScrollRef}>
                  <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                      {tmplColWidths.map((w, i) => <col key={i} style={{ width: w }} />)}
                    </colgroup>
                    <thead>
                      <tr>
                        <th className="hs-th-check">
                          <input type="checkbox" checked={tmplPagedData.length > 0 && tmplSelectedRows.size === tmplPagedData.length} onChange={toggleAllTmplRows} disabled={templatesLoading || tmplPagedData.length === 0} />
                        </th>
                        {tmplHeaders.map(h => (
                          <th
                            key={h.key}
                            className={`hs-th-resizable${h.sortable ? ' hs-th-sortable' : ''}${h.frozen ? ' hs-th-frozen' : ''}`}
                            style={h.frozen ? { position: 'sticky', left: 40, top: 0, zIndex: 15, background: '#f8fafc' } : undefined}
                            onClick={h.sortable ? () => toggleTmplSort(h.key) : undefined}
                            onContextMenu={h.sortable ? (e) => { e.preventDefault(); setColContextMenu({ field: h.key, x: e.clientX, y: e.clientY }) } : undefined}
                          >
                            <span className="hs-th-content">
                              {h.label}
                              {h.sortable && tmplSort.field === h.key && <span className="hs-sort-arrow">{tmplSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                              {h.sortable && <span className="hs-th-hover-sort"><svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M29.38 21.96a1.147 1.147 0 0 0-1.617 0l-3.85 3.852V5.714a1.143 1.143 0 0 0-2.286 0v20.099l-3.853-3.852a1.144 1.144 0 0 0-1.615 1.617l5.803 5.802a1.143 1.143 0 0 0 1.616 0l5.802-5.802a1.144 1.144 0 0 0 0-1.617M15.84 8.422 10.039 2.62a1.145 1.145 0 0 0-1.616 0L2.62 8.422a1.144 1.144 0 0 0 1.617 1.617l3.85-3.851v20.098a1.143 1.143 0 0 0 2.286 0V6.187l3.853 3.852a1.14 1.14 0 0 0 1.616-.001 1.14 1.14 0 0 0-.001-1.616"/></svg></span>}
                            </span>
                            {h.sortable && <button className="hs-th-kebab" onClick={(e) => { e.stopPropagation(); setColContextMenu({ field: h.key, x: e.clientX, y: e.clientY }) }}>
                              <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><circle cx="16" cy="4.14" r="2.29"/><circle cx="16" cy="15.57" r="2.29"/><circle cx="16" cy="27" r="2.29"/></svg>
                            </button>}
                            <div className="hs-col-resize-handle" onClick={(e) => e.stopPropagation()} />
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {templatesLoading ? (
                        <tr><td colSpan={8} className="hs-loading-cell"><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}><DotLottieReact src="/loading.lottie" loop autoplay style={{ width: 80, height: 80 }} /><span style={{ fontSize: '0.82rem', color: '#7c98b6', marginTop: 8 }}>Loading templates...</span></div></td></tr>
                      ) : tmplPagedData.length === 0 ? (
                        <tr><td colSpan={8} className="hs-empty-cell">
                          <div style={{ padding: '60px 40px', textAlign: 'center' }}>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '12px' }}><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                            <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#33475b', marginBottom: '6px' }}>Send your most repetitive emails in seconds</div>
                            <div style={{ fontSize: '0.82rem', color: '#7c98b6', maxWidth: '360px', margin: '0 auto' }}>Create reusable email templates with personalization tokens to save time on outreach.</div>
                          </div>
                        </td></tr>
                      ) : (
                        tmplPagedData.map((t) => (
                          <tr key={t.id} className={`hs-row${tmplSelectedRows.has(t.id) ? ' selected' : ''}`}>
                            <td className="hs-td-check">
                              <input
                                type="checkbox"
                                checked={tmplSelectedRows.has(t.id)}
                                onChange={() => {
                                  setTmplSelectedRows(prev => {
                                    const next = new Set(prev)
                                    next.has(t.id) ? next.delete(t.id) : next.add(t.id)
                                    return next
                                  })
                                }}
                              />
                            </td>
                            <td className="hs-td-name" style={{ position: 'sticky', left: 40, zIndex: 4, background: tmplSelectedRows.has(t.id) ? '#eef3ff' : '#fff' }}>
                              <div className="hs-name-cell">
                                <span className="hs-name-link"
                                  onClick={() => { setEditingTemplate(t); setTemplateForm({ name: t.name, category: t.category, subject: t.subject || '', body: t.body }); setTemplateError(null); setShowTemplateModal('edit') }}>
                                  {t.name}
                                </span>
                                <button className="hs-preview-btn" title="Preview" onClick={(e) => { e.stopPropagation(); setEditingTemplate(t); setTemplateForm({ name: t.name, category: t.category, subject: t.subject || '', body: t.body }); setTemplateError(null); setShowTemplateModal('edit') }}>Preview</button>
                              </div>
                            </td>
                            <td>
                              <span className="hs-status-badge" style={{
                                background: t.category === 'email' ? '#e3f2fd' : t.category === 'sms' ? '#e8f5e9' : '#f5f8fa',
                                color: t.category === 'email' ? '#1565c0' : t.category === 'sms' ? '#2e7d32' : '#516f90',
                              }}>
                                {t.category.charAt(0).toUpperCase() + t.category.slice(1)}
                              </span>
                            </td>
                            <td style={{ color: '#516f90', fontSize: '0.78rem', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.subject || '--'}</td>
                            <td style={{ color: '#516f90', fontSize: '13px' }}>{t.use_count}</td>
                            <td style={{ color: '#516f90', fontSize: '13px' }}>{t.updated_at ? formatDatePref(t.updated_at) : ''}</td>
                            <td>
                              <button onClick={() => { if (confirm('Delete this template?')) deleteTemplate(t.id).then(() => loadTemplates()).catch(() => {}) }}
                                style={{ background: 'none', border: 'none', color: '#7c98b6', cursor: 'pointer', fontSize: '0.78rem' }}
                                onMouseEnter={e => { e.currentTarget.style.color = '#d94c53' }}
                                onMouseLeave={e => { e.currentTarget.style.color = '#7c98b6' }}>
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* ROW 6: Pagination */}
                <div className="hs-pagination">
                  <nav className="hs-paginator" aria-label="Pagination">
                    <button
                      className="hs-paginator-btn"
                      disabled={tmplCurrentPage <= 1}
                      onClick={() => setTmplPage((p) => p - 1)}
                      aria-label="Previous page"
                    >
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95"/></svg>
                      <span>Prev</span>
                    </button>
                    {(() => {
                      if (tmplTotalPages <= 7) {
                        const pages: number[] = []
                        for (let i = 1; i <= tmplTotalPages; i++) pages.push(i)
                        return pages.map(p => (
                          <button key={p} className={`hs-paginator-num${p === tmplCurrentPage ? ' active' : ''}`} onClick={() => setTmplPage(p)}>{p}</button>
                        ))
                      }
                      const items: React.ReactNode[] = []
                      const addPage = (p: number) => items.push(
                        <button key={p} className={`hs-paginator-num${p === tmplCurrentPage ? ' active' : ''}`} onClick={() => setTmplPage(p)}>{p}</button>
                      )
                      const addEllipsis = (key: string) => items.push(
                        <span key={key} className="hs-paginator-ellipsis">...</span>
                      )
                      addPage(1)
                      if (tmplCurrentPage > 3) addEllipsis('el')
                      const start = Math.max(2, tmplCurrentPage - 1)
                      const end = Math.min(tmplTotalPages - 1, tmplCurrentPage + 1)
                      for (let i = start; i <= end; i++) addPage(i)
                      if (tmplCurrentPage < tmplTotalPages - 2) addEllipsis('er')
                      addPage(tmplTotalPages)
                      return items
                    })()}
                    <button
                      className="hs-paginator-btn"
                      disabled={tmplCurrentPage >= tmplTotalPages}
                      onClick={() => setTmplPage((p) => p + 1)}
                      aria-label="Next page"
                    >
                      <span>Next</span>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M11 27.71c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62l9.48-9.48-9.48-9.47a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0L22.1 15.48c.45.45.45 1.17 0 1.62L11.81 27.39c-.22.22-.52.33-.81.33Z"/></svg>
                    </button>
                  </nav>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', marginLeft: 'auto' }}><select value={tablePageSize} onChange={e => { setTablePageSize(Number(e.target.value) as any); setTmplPage(1) }} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #e2e8f0', fontSize: '0.82rem' }}><option value={100}>100</option><option value={200}>200</option><option value={500}>500</option></select><span>per page</span></div>
                </div>

                {/* Record count */}
                <div style={{ padding: '8px 16px', fontSize: '0.82rem', color: '#7c98b6', borderTop: '1px solid #eaf0f6' }}>
                  {tmplTotal} template{tmplTotal !== 1 ? 's' : ''}
                </div>
              </div>

              {/* Table settings panel */}
              {tableSettingsOpen && (
                <div className="hs-settings-panel">
                  <div className="hs-settings-header">
                    <span className="hs-settings-title">Table settings</span>
                    <button className="hs-settings-close" onClick={() => setTableSettingsOpen(false)}>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                    </button>
                  </div>
                  <div className="hs-settings-body">
                    <div className="hs-settings-group">
                      <label className="hs-settings-label">
                        Pagination
                        <span className="hs-info-icon" title="Number of rows shown per page">
                          <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg>
                        </span>
                      </label>
                      {([100, 200, 500] as const).map(size => (
                        <label className="hs-settings-radio" key={size}>
                          <input type="radio" name="tmplPageSize" checked={tablePageSize === size} onChange={() => { setTablePageSize(size); setTmplPage(1) }} />
                          <span className="hs-radio-indicator" />
                          <span>{size} per page</span>
                        </label>
                      ))}
                    </div>
                    <hr className="hs-settings-divider" />
                    <div className="hs-settings-group">
                      <label className="hs-settings-label">
                        Row height
                        <span className="hs-info-icon" title="Adjust the vertical spacing of table rows">
                          <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg>
                        </span>
                      </label>
                      {([
                        { value: 'small' as const, label: 'Compact' },
                        { value: 'medium' as const, label: 'Default' },
                        { value: 'large' as const, label: 'Comfortable' },
                      ]).map(opt => (
                        <label className="hs-settings-radio" key={opt.value}>
                          <input type="radio" name="tmplRowHeight" checked={tableRowHeight === opt.value} onChange={() => setTableRowHeight(opt.value)} />
                          <span className="hs-radio-indicator" />
                          <span>{opt.label}</span>
                        </label>
                      ))}
                    </div>
                    <hr className="hs-settings-divider" />
                    <div className="hs-settings-group">
                      <div className="hs-settings-toggle-row">
                        <label className="hs-settings-label">
                          Zebra striping
                          <span className="hs-info-icon" title="Alternate row background colors for easier reading">
                            <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg>
                          </span>
                        </label>
                        <button
                          className={`hs-toggle${tableZebraStriping ? ' on' : ''}`}
                          onClick={() => setTableZebraStriping(!tableZebraStriping)}
                          role="switch"
                          aria-checked={tableZebraStriping}
                        >
                          <span className="hs-toggle-track">
                            <span className="hs-toggle-on">ON</span>
                            <span className="hs-toggle-off">OFF</span>
                            <span className="hs-toggle-handle" />
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          )
        })()}

        {/* ========== Playbooks View ========== */}
        {activeView === 'playbooks' && !playbookDetailOpen && (() => {
          // Client-side filtering + sorting + pagination
          const pbFilteredByTab = pbViewTab === 'published' ? playbooksData.filter((pb: any) => pb.status === 'published') : pbViewTab === 'draft' ? playbooksData.filter((pb: any) => pb.status === 'draft') : playbooksData
          const pbFilteredByStatus = pbStatusFilter ? pbFilteredByTab.filter((pb: any) => pb.status === pbStatusFilter) : pbFilteredByTab
          const pbFilteredByEng = pbEngagementFilter ? pbFilteredByStatus.filter((pb: any) => pb.engagement_type === pbEngagementFilter) : pbFilteredByStatus
          const pbFiltered = pbFilteredByEng
          const pbSorted = [...pbFiltered].sort((a: any, b: any) => {
            const fa = a[pbSort.field] ?? ''
            const fb = b[pbSort.field] ?? ''
            if (pbSort.field === 'total_views') return pbSort.order === 'ASC' ? (fa as number) - (fb as number) : (fb as number) - (fa as number)
            const cmp = String(fa).localeCompare(String(fb), undefined, { sensitivity: 'base' })
            return pbSort.order === 'ASC' ? cmp : -cmp
          })
          const pbPageSize = tablePageSize
          const pbTotal = pbSorted.length
          const pbTotalPages = Math.max(1, Math.ceil(pbTotal / pbPageSize))
          const pbCurrentPage = Math.min(pbPage, pbTotalPages)
          const pbPagedData = pbSorted.slice((pbCurrentPage - 1) * pbPageSize, pbCurrentPage * pbPageSize)
          const toggleAllPbRows = () => {
            if (pbSelectedRows.size === pbPagedData.length) {
              setPbSelectedRows(new Set())
            } else {
              setPbSelectedRows(new Set(pbPagedData.map(pb => pb.id)))
            }
          }
          const pbSortHeader = (field: string) => {
            if (pbSort.field === field) {
              setPbSort({ field, order: pbSort.order === 'ASC' ? 'DESC' : 'ASC' })
            } else {
              setPbSort({ field, order: field === 'title' ? 'ASC' : 'DESC' })
            }
            setPbPage(1)
          }
          const pbSortArrow = (field: string) => pbSort.field === field ? (pbSort.order === 'ASC' ? ' \u2191' : ' \u2193') : ''
          return (
          <div className="hs-contacts">
            {/* Row 1 — Topbar: object switcher + view tabs + actions */}
            <div className="hs-topbar">
              <div className="hs-topbar-left">
                {/* Object switcher */}
                <div className="hs-obj-switcher-wrap" ref={objSwitcherRef}>
                  <button className={`hs-obj-switcher${objSwitcherOpen ? ' open' : ''}`} onClick={() => { setObjSwitcherOpen(!objSwitcherOpen); setObjSwitcherSearch('') }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"/><path d="M8 7h6"/><path d="M8 11h8"/></svg>
                    <span>Playbooks</span>
                    <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                  </button>
                  {objSwitcherOpen && (
                    <div className="hs-obj-panel">
                      <div className="hs-obj-search-wrap">
                        <svg className="hs-obj-search-icon" width="13" height="13" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                        <input
                          className="hs-obj-search"
                          placeholder="Search"
                          value={objSwitcherSearch}
                          onChange={(e) => setObjSwitcherSearch(e.target.value)}
                          autoFocus
                        />
                      </div>
                      <ul className="hs-obj-list" role="listbox">
                        {[
                          { label: 'Contacts', view: 'contacts' as ViewMode },
                            { label: 'Clients', view: 'companies' as ViewMode },
                          { label: 'Deals', view: 'deals' as ViewMode },
                          { label: 'Leads', view: 'leads' as ViewMode },
                          { label: 'Projects', view: 'projects' as ViewMode },
                          { label: 'Tickets', view: 'tickets' as ViewMode },
                          { label: 'Calls', view: 'calls' as ViewMode },
                        ]
                          .filter(item => !objSwitcherSearch || item.label.toLowerCase().includes(objSwitcherSearch.toLowerCase()))
                          .map(item => (
                            <li
                              key={item.view}
                              className={`hs-obj-item${item.view === activeView ? ' active' : ''}`}
                              role="option"
                              aria-selected={item.view === activeView}
                              onClick={() => {
                                setActiveView(item.view)
                                setObjSwitcherOpen(false)
                                setObjSwitcherSearch('')
                              }}
                            >
                              {item.label}
                            </li>
                          ))
                        }
                      </ul>
                    </div>
                  )}
                </div>

                {/* Separator */}
                <div className="hs-topbar-sep" />

                {/* View tabs */}
                <div className="hs-view-tabs">
                  {[
                    { key: '', label: 'All playbooks' },
                    { key: 'published', label: 'Published' },
                    { key: 'draft', label: 'Drafts' },
                  ].map((tab) => {
                    const isActive = pbViewTab === tab.key
                    return (
                      <div
                        key={tab.key}
                        className={`hs-view-tab${isActive ? ' active' : ''}`}
                        ref={isActive ? tabEditRef : undefined}
                        onClick={() => { setPbViewTab(tab.key); setPbPage(1) }}
                      >
                        <span className="hs-tab-label">{tab.label}</span>
                        {isActive && pbTotal > 0 && <span className="hs-tab-count">{pbTotal.toLocaleString()}</span>}
                        {isActive && (
                          <span className="hs-tab-kebab" onClick={(e) => { e.stopPropagation(); setTabEditOpen(!tabEditOpen) }}>
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>
                          </span>
                        )}
                        {tab.key !== '' && <span className="hs-tab-x" onClick={(e) => { e.stopPropagation(); setPbViewTab(''); setPbPage(1) }}>&times;</span>}
                        {isActive && tabEditOpen && (
                          <div className="hs-tab-edit-dropdown">
                            <button className="hs-tab-edit-item" disabled>Rename</button>
                            <button className="hs-tab-edit-item" onClick={() => {
                              const id = Date.now().toString(36)
                              setCustomViews(prev => ({
                                ...prev,
                                playbooks: [...(prev.playbooks || []), { id, label: `${tab.label} (clone)` }]
                              }))
                              setTabEditOpen(false)
                            }}>Clone</button>
                            <button className="hs-tab-edit-item" disabled>Manage Sharing</button>
                            {tab.key !== '' && <button className="hs-tab-edit-item" onClick={() => { setPbViewTab(''); setPbPage(1); setTabEditOpen(false) }}>Delete</button>}
                          </div>
                        )}
                      </div>
                    )
                  })}
                  {/* Custom user-created view tabs */}
                  {(customViews.playbooks || []).map(cv => {
                    const isActive = pbViewTab === `custom:${cv.id}`
                    const isRenaming = renamingViewId === cv.id
                    return (
                      <div
                        key={cv.id}
                        className={`hs-view-tab${isActive ? ' active' : ''}`}
                        onClick={() => {
                          if (isRenaming) return
                          setPbViewTab(`custom:${cv.id}`)
                          setPbPage(1)
                        }}
                      >
                        {isRenaming ? (
                          <input
                            ref={renamingViewRef}
                            className="hs-tab-rename-input"
                            value={renamingViewName}
                            onChange={(e) => setRenamingViewName(e.target.value)}
                            onFocus={(e) => e.target.select()}
                            onBlur={() => {
                              if (renamingViewName.trim()) {
                                setCustomViews(prev => ({
                                  ...prev,
                                  playbooks: (prev.playbooks || []).map(v => v.id === cv.id ? { ...v, label: renamingViewName.trim() } : v)
                                }))
                              }
                              setRenamingViewId(null)
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
                              if (e.key === 'Escape') { setRenamingViewName(cv.label); setRenamingViewId(null) }
                            }}
                            onClick={(e) => e.stopPropagation()}
                            autoFocus
                          />
                        ) : (
                          <span className="hs-tab-label">{cv.label}</span>
                        )}
                        {isActive && !isRenaming && pbTotal > 0 && <span className="hs-tab-count">{pbTotal.toLocaleString()}</span>}
                        {isActive && !isRenaming && (
                          <span className="hs-tab-kebab" onClick={(e) => { e.stopPropagation(); setTabEditOpen(!tabEditOpen) }}>
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>
                          </span>
                        )}
                        {isActive && tabEditOpen && !isRenaming && (
                          <div className="hs-tab-edit-dropdown">
                            <button className="hs-tab-edit-item" onClick={() => { setRenamingViewId(cv.id); setRenamingViewName(cv.label); setTabEditOpen(false) }}>Rename</button>
                            <button className="hs-tab-edit-item" onClick={() => {
                              const id = Date.now().toString(36)
                              setCustomViews(prev => ({
                                ...prev,
                                playbooks: [...(prev.playbooks || []), { id, label: `${cv.label} (clone)` }]
                              }))
                              setPbViewTab(`custom:${id}`)
                              setRenamingViewId(id)
                              setRenamingViewName(`${cv.label} (clone)`)
                              setTabEditOpen(false)
                            }}>Clone</button>
                            <button className="hs-tab-edit-item" onClick={() => {
                              setCustomViews(prev => ({
                                ...prev,
                                playbooks: (prev.playbooks || []).filter(v => v.id !== cv.id)
                              }))
                              setPbViewTab('')
                              setPbPage(1)
                              setTabEditOpen(false)
                            }}>Delete</button>
                          </div>
                        )}
                        <span className="hs-tab-x" onClick={(e) => {
                          e.stopPropagation()
                          setCustomViews(prev => ({
                            ...prev,
                            playbooks: (prev.playbooks || []).filter(v => v.id !== cv.id)
                          }))
                          if (isActive) { setPbViewTab(''); setPbPage(1) }
                        }}>&times;</span>
                      </div>
                    )
                  })}
                  {/* Quick-add view button */}
                  <div className="hs-topbar-sep" />
                  <div className="hs-view-add-wrap" ref={viewAddMenuRef} style={{ position: 'relative', display: 'inline-flex', alignSelf: 'center' }}>
                    <button
                      className={`hs-quick-add-btn${viewAddMenuOpen ? ' active' : ''}`}
                      title="Create or add view"
                      type="button"
                      aria-label="Create or add view"
                      onClick={() => { setViewAddMenuOpen(!viewAddMenuOpen); setViewAddMenuMode('menu') }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                    </button>
                    {viewAddMenuOpen && viewAddMenuMode === 'menu' && (
                      <div className="hs-view-add-dropdown">
                        <button className="hs-view-add-item" onClick={() => setViewAddMenuMode('type')}>
                          Create new view
                        </button>
                        <button className="hs-view-add-item" onClick={() => {
                          setViewAddMenuOpen(false)
                          setViewAddMenuMode('menu')
                        }}>
                          Add view ({(customViews.playbooks || []).length}/50)
                        </button>
                      </div>
                    )}
                    {viewAddMenuOpen && viewAddMenuMode === 'type' && (
                      <div className="hs-view-add-dropdown hs-view-type-picker">
                        <button className="hs-view-type-btn" onClick={() => {
                          const id = Date.now().toString(36)
                          const viewName = `All playbooks-${(customViews.playbooks || []).length + 1}`
                          setCustomViews(prev => ({
                            ...prev,
                            playbooks: [...(prev.playbooks || []), { id, label: viewName }]
                          }))
                          setPbViewTab(`custom:${id}`)
                          setRenamingViewId(id)
                          setRenamingViewName(viewName)
                          setViewAddMenuOpen(false)
                          setViewAddMenuMode('menu')
                        }}>
                          <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                          <span>Table</span>
                        </button>
                        <button className="hs-view-type-btn" disabled style={{ opacity: 0.4 }}>
                          <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M11.43 11.43h-8a1.14 1.14 0 1 1 0-2.28h8a1.14 1.14 0 1 1 0 2.28M28.57 11.43h-8a1.14 1.14 0 1 1 0-2.28h8a1.14 1.14 0 1 1 0 2.28"/><path d="M9.14 29.71H5.71c-1.89 0-3.43-1.54-3.43-3.43V5.71c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v14.86c0 1.89-1.54 3.43-3.43 3.43h-3.43c-1.89 0-3.43-1.54-3.43-3.43v-1.14h-6.86v6.86c0 1.89-1.54 3.43-3.43 3.43Z"/></svg>
                          <span>Board</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="hs-topbar-right">
                {/* Create playbook — direct action (Import removed) */}
                <button className="hs-add-contacts-btn" onClick={() => { setPlaybookForm({ title: '', content: '', engagement_type: '' }); setPlaybookError(null); setShowPlaybookModal('create') }}>
                  Create playbook
                </button>
              </div>
            </div>

            {/* Row 2 — Toolbar */}
            <div className="hs-toolbar">
              <div className="hs-toolbar-left">
                {/* Search */}
                <div className="hs-search-wrap">
                  <svg className="hs-search-icon" width="14" height="14" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M28.14 29.29c-.29 0-.58-.11-.81-.33l-7.58-7.58a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l7.58 7.58a1.14 1.14 0 0 1-.81 1.95"/><path d="M13.29 24.71c-6.3 0-11.43-5.13-11.43-11.43S6.98 1.86 13.29 1.86s11.43 5.13 11.43 11.43-5.13 11.43-11.43 11.43Zm0-20.57c-5.04 0-9.14 4.1-9.14 9.14s4.1 9.14 9.14 9.14 9.14-4.1 9.14-9.14-4.1-9.14-9.14-9.14"/></svg>
                  <input
                    className="hs-search-input"
                    placeholder="Search playbooks"
                    value={playbooksSearch}
                    onChange={(e) => { setPlaybooksSearch(e.target.value); setPbPage(1) }}
                  />
                  {playbooksSearch && (
                    <button className="hs-search-clear" onClick={() => { setPlaybooksSearch(''); setPbPage(1) }}>&times;</button>
                  )}
                </div>

                <div className="hs-tb-sep" />

                {/* View type + settings button group */}
                <div className="hs-viewtype-group">
                  <div className="hs-viewtype-wrap" ref={viewTypeDropdownRef}>
                    <button
                      className={`hs-tb-btn hs-viewtype-btn${viewTypeDropdownOpen ? ' active' : ''}`}
                      onClick={() => setViewTypeDropdownOpen(!viewTypeDropdownOpen)}
                    >
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                      <span>Table</span>
                      <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                    </button>
                    {viewTypeDropdownOpen && (
                      <div className="hs-viewtype-panel">
                        <div className="hs-viewtype-header">View type</div>
                        <button className="hs-viewtype-option active" onClick={() => setViewTypeDropdownOpen(false)}>
                          <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                          <span>Table</span>
                        </button>
                      </div>
                    )}
                  </div>
                  <button
                    className={`hs-tb-btn hs-settings-btn${tableSettingsOpen ? ' active' : ''}`}
                    onClick={() => { if (!tableSettingsOpen) { setPbPreview(null) } setTableSettingsOpen(!tableSettingsOpen) }}
                    title={tableSettingsOpen ? 'Close settings' : 'Table settings'}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                  </button>
                </div>

                <div className="hs-tb-sep" />

                {/* Edit columns */}
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Edit columns" disabled style={{ opacity: 0.5 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="18" rx="1"/><rect x="14" y="3" width="7" height="18" rx="1"/></svg>
                  <span>Edit columns</span>
                </button>

                <div className="hs-tb-sep" />

                {/* Filters */}
                <button className={`hs-tb-btn hs-tb-btn-bordered${quickFiltersOpen ? ' hs-tb-btn-pressed' : ''}`} title="Filters" onClick={() => setQuickFiltersOpen(!quickFiltersOpen)}>
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M30.71 16.86H13.57a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28M6.71 16.86H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M10.14 20.29c-2.52 0-4.57-2.05-4.57-4.57s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M6.71 6.57H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M30.71 6.57H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M18.14 6.57H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 10C19.05 10 17 7.95 17 5.43S19.05.86 21.57.86s4.57 2.05 4.57 4.57S24.09 10 21.57 10m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M30.71 27.14H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M18.14 27.14H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 30.57C19.05 30.57 17 28.52 17 26s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29"/></svg>
                  <span>Filters</span>
                </button>

                <div className="hs-tb-sep" />

                {/* Export */}
                <button
                  className={`hs-tb-btn hs-tb-btn-bordered${pbExporting ? ' hs-tb-btn-pressed' : ''}`}
                  title="Export current view as CSV"
                  disabled={pbExporting}
                  onClick={() => {
                    setPbExporting(true)
                    try {
                      const headers = ['Name', 'Status', 'Engagement Type', 'Views', 'Last Viewed', 'Modified', 'Created']
                      const escCsv = (v: unknown) => { const s = String(v ?? ''); return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s }
                      const rows = pbSorted.map((pb: any) => [
                        pb.title, pb.status, pb.engagement_type || '', pb.total_views,
                        pb.last_viewed_at || '', pb.updated_at || '', pb.created_at || '',
                      ].map(escCsv).join(','))
                      const csv = [headers.join(','), ...rows].join('\n')
                      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
                      const url = URL.createObjectURL(blob)
                      const a = document.createElement('a')
                      a.href = url
                      a.download = `playbooks-export-${new Date().toISOString().slice(0, 10)}.csv`
                      document.body.appendChild(a)
                      a.click()
                      document.body.removeChild(a)
                      URL.revokeObjectURL(url)
                    } catch (err) {
                      console.error('Export failed:', err)
                      alert('Export failed. Please try again.')
                    } finally {
                      setPbExporting(false)
                    }
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  <span>{pbExporting ? 'Exporting...' : 'Export'}</span>
                </button>
              </div>

              <div className="hs-toolbar-right">
                <div className="hs-tb-sep" />
                {/* Clone view */}
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Clone view" onClick={() => {
                  const id = Date.now().toString(36)
                  const baseName = pbViewTab.startsWith('custom:') ? ((customViews.playbooks || []).find(v => `custom:${v.id}` === pbViewTab)?.label || 'All playbooks') : 'All playbooks'
                  const cloneName = `${baseName} (clone)`
                  setCustomViews(prev => ({
                    ...prev,
                    playbooks: [...(prev.playbooks || []), { id, label: cloneName }]
                  }))
                  setPbViewTab(`custom:${id}`)
                  setRenamingViewId(id)
                  setRenamingViewName(cloneName)
                }}>
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M25.86 29.29H12.15c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-1.89 1.54-3.43 3.43-3.43h13.71c1.89 0 3.43 1.54 3.43 3.43v13.71c0 1.89-1.54 3.43-3.43 3.43M12.14 11c-.63 0-1.14.51-1.14 1.14v13.71c0 .63.51 1.14 1.14 1.14h13.71c.63 0 1.14-.51 1.14-1.14V12.14c0-.63-.51-1.14-1.14-1.14z"/><path d="M5.29 22.43c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43H19c1.89 0 3.43 1.54 3.43 3.43a1.14 1.14 0 1 1-2.28 0c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14V19c0 .63.51 1.14 1.14 1.14a1.14 1.14 0 1 1 0 2.28Z"/></svg>
                </button>
                {/* Save view */}
                <button
                  className={`hs-tb-btn hs-tb-btn-bordered${!pbViewTab.startsWith('custom:') ? ' disabled' : ''}`}
                  disabled={!pbViewTab.startsWith('custom:')}
                  title="Save view"
                  onClick={() => {
                    if (!pbViewTab.startsWith('custom:')) return
                    const btn = document.querySelector('.hs-tb-btn[title="Save view"]') as HTMLElement
                    if (btn) { btn.style.color = '#22c55e'; setTimeout(() => { btn.style.color = '' }, 800) }
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                </button>
              </div>
            </div>

            {/* Row 3 — Quick filters bar */}
            {quickFiltersOpen && <div className="hs-quick-filters">
              {/* Owner dropdown */}
              <div className="hs-owner-dropdown" ref={ownerDropdownRef}>
                <button
                  className={`hs-qf-btn hs-qf-trigger${ownerDropdownOpen ? ' open' : ''}${ownerSelected.size > 0 ? ' has-value' : ''}`}
                  onClick={() => setOwnerDropdownOpen(!ownerDropdownOpen)}
                >
                  <span>Owner</span>
                  {ownerSelected.size > 0 && <span className="hs-qf-count">{ownerSelected.size}</span>}
                  <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                </button>
                {ownerDropdownOpen && (
                  <div className="hs-owner-panel">
                    <div className="hs-owner-search-wrap">
                      <svg className="hs-owner-search-icon" width="13" height="13" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                      <input
                        className="hs-owner-search"
                        placeholder="Search"
                        value={ownerSearch}
                        onChange={(e) => setOwnerSearch(e.target.value)}
                        autoFocus
                      />
                    </div>
                    <div className="hs-owner-list">
                      {/* "Me" shortcut */}
                      {(!ownerSearch || 'me'.includes(ownerSearch.toLowerCase()) || 'max ritchie'.includes(ownerSearch.toLowerCase())) && (
                        <label className="hs-owner-item hs-owner-me">
                          <input
                            type="checkbox"
                            checked={ownerSelected.has(8)}
                            onChange={() => {
                              setOwnerSelected(prev => {
                                const next = new Set(prev)
                                next.has(8) ? next.delete(8) : next.add(8)
                                return next
                              })
                            }}
                          />
                          <span className="hs-owner-checkbox" />
                          <span className="hs-owner-label">Me</span>
                        </label>
                      )}
                      {/* All users */}
                      {crmUsers
                        .filter(u => !ownerSearch || u.name.toLowerCase().includes(ownerSearch.toLowerCase()))
                        .map(user => (
                          <label className="hs-owner-item" key={user.id}>
                            <input
                              type="checkbox"
                              checked={ownerSelected.has(user.id)}
                              onChange={() => {
                                setOwnerSelected(prev => {
                                  const next = new Set(prev)
                                  next.has(user.id) ? next.delete(user.id) : next.add(user.id)
                                  return next
                                })
                              }}
                            />
                            <span className="hs-owner-checkbox" />
                            <span className="hs-owner-avatar">{user.initials || user.name.charAt(0)}</span>
                            <span className="hs-owner-label">{user.name}</span>
                          </label>
                        ))
                      }
                    </div>
                    {/* Unassigned footer */}
                    {(!ownerSearch || 'unassigned'.includes(ownerSearch.toLowerCase())) && (
                      <div className="hs-owner-footer">
                        <label className="hs-owner-item">
                          <input
                            type="checkbox"
                            checked={ownerSelected.has(0)}
                            onChange={() => {
                              setOwnerSelected(prev => {
                                const next = new Set(prev)
                                next.has(0) ? next.delete(0) : next.add(0)
                                return next
                              })
                            }}
                          />
                          <span className="hs-owner-checkbox" />
                          <span className="hs-owner-label">Unassigned</span>
                        </label>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Status filter cycle */}
              <button
                className={`hs-qf-btn hs-qf-trigger${pbStatusFilter ? ' has-value' : ''}`}
                onClick={() => {
                  const vals = ['', 'published', 'draft', 'archived']
                  const idx = vals.indexOf(pbStatusFilter)
                  setPbStatusFilter(vals[(idx + 1) % vals.length])
                  setPbPage(1)
                }}
              >
                <span>{pbStatusFilter ? pbStatusFilter.charAt(0).toUpperCase() + pbStatusFilter.slice(1) : 'Status'}</span>
                {pbStatusFilter && (
                  <span className="hs-qf-clear" onClick={(e) => { e.stopPropagation(); setPbStatusFilter(''); setPbPage(1) }}>&times;</span>
                )}
                {!pbStatusFilter && <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>}
              </button>

              {/* Engagement type filter cycle */}
              <button
                className={`hs-qf-btn hs-qf-trigger${pbEngagementFilter ? ' has-value' : ''}`}
                onClick={() => {
                  const vals = ['', 'call', 'email', 'meeting', 'note']
                  const idx = vals.indexOf(pbEngagementFilter)
                  setPbEngagementFilter(vals[(idx + 1) % vals.length])
                  setPbPage(1)
                }}
              >
                <span>{pbEngagementFilter ? pbEngagementFilter.charAt(0).toUpperCase() + pbEngagementFilter.slice(1) : 'Engagement type'}</span>
                {pbEngagementFilter && (
                  <span className="hs-qf-clear" onClick={(e) => { e.stopPropagation(); setPbEngagementFilter(''); setPbPage(1) }}>&times;</span>
                )}
                {!pbEngagementFilter && <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>}
              </button>

              {/* Sort by */}
              <button
                className={`hs-qf-btn hs-qf-trigger${pbSort.field !== 'updated_at' ? ' has-value' : ''}`}
                onClick={() => {
                  const fields = ['updated_at', 'title', 'total_views', 'last_viewed_at', 'created_at']
                  const idx = fields.indexOf(pbSort.field)
                  const next = fields[(idx + 1) % fields.length]
                  setPbSort({ field: next, order: next === 'title' ? 'ASC' : 'DESC' })
                  setPbPage(1)
                }}
              >
                <span>Sort: {{ updated_at: 'Modified', title: 'Name', total_views: 'Views', last_viewed_at: 'Last Viewed', created_at: 'Created' }[pbSort.field] || 'Modified'}</span>
                <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
              </button>

              {/* Active filter pills */}
              {pbStatusFilter && (
                <div className="qf-pill-wrap">
                  <button className="hs-qf-btn hs-qf-trigger has-value" onClick={() => { setPbStatusFilter(''); setPbPage(1) }}>
                    <span>Status: {pbStatusFilter.charAt(0).toUpperCase() + pbStatusFilter.slice(1)}</span>
                    <span className="hs-qf-count">1</span>
                  </button>
                  <button className="hs-qf-clear-btn" title="Clear Status filter" onClick={() => { setPbStatusFilter(''); setPbPage(1) }}>
                    <svg width="10" height="10" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                  </button>
                </div>
              )}
              {pbEngagementFilter && (
                <div className="qf-pill-wrap">
                  <button className="hs-qf-btn hs-qf-trigger has-value" onClick={() => { setPbEngagementFilter(''); setPbPage(1) }}>
                    <span>Type: {pbEngagementFilter.charAt(0).toUpperCase() + pbEngagementFilter.slice(1)}</span>
                    <span className="hs-qf-count">1</span>
                  </button>
                  <button className="hs-qf-clear-btn" title="Clear Engagement filter" onClick={() => { setPbEngagementFilter(''); setPbPage(1) }}>
                    <svg width="10" height="10" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                  </button>
                </div>
              )}

              <div className="hs-qf-sep" />

              {/* +More popover */}
              <div className="hs-qf-more-wrap" ref={qfMoreRef}>
                <button className={`hs-qf-btn hs-qf-more${qfMoreOpen ? ' open' : ''}`} onClick={() => setQfMoreOpen(!qfMoreOpen)}>
                  <svg width="10" height="10" viewBox="0 0 32 32" fill="currentColor"><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V3a1.14 1.14 0 1 1 2.28 0v25.14c0 .63-.51 1.14-1.14 1.14Z"/><path d="M28.14 16.71H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28"/></svg>
                  <span>More</span>
                </button>
                {qfMoreOpen && (
                  <div className="qf-more-popover" ref={(el) => {
                    if (!el) return
                    const btn = qfMoreRef.current?.querySelector('button')
                    if (!btn) return
                    const rect = btn.getBoundingClientRect()
                    el.style.position = 'fixed'
                    el.style.top = `${rect.bottom + 6}px`
                    el.style.left = `${rect.left}px`
                    el.style.right = 'auto'
                  }}>
                    <div className="qf-more-body">
                      <h5 className="qf-more-heading">Add a quick filter</h5>
                    </div>
                    <div className="qf-more-list">
                      <div className="qf-more-category">Playbook properties</div>
                      {!pbStatusFilter && (
                        <button className="qf-more-option" onClick={() => { setPbStatusFilter('published'); setPbPage(1); setQfMoreOpen(false) }}>
                          <span className="qf-more-option-icon"><svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M23.57 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V7.57a1.14 1.14 0 1 1 2.28 0v18.29c0 .63.51 1.14 1.14 1.14h18.29a1.14 1.14 0 1 1 0 2.28Z"/><path d="M25.86 24.71h-16c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43h16c1.89 0 3.43 1.54 3.43 3.43v16c0 1.89-1.54 3.43-3.43 3.43Zm-16-20.57c-.63 0-1.14.51-1.14 1.14v16c0 .63.51 1.14 1.14 1.14h16c.63 0 1.14-.51 1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14h-16Z"/><path d="M16.84 17.48c-.3 0-.59-.12-.81-.33l-3.56-3.56a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l2.71 2.71 4.78-5.21c.43-.46 1.15-.5 1.61-.07.47.43.5 1.15.07 1.62l-5.59 6.09c-.21.23-.51.36-.82.37h-.02Z"/></svg></span>
                          <span className="qf-more-option-label">Status</span>
                        </button>
                      )}
                      {!pbEngagementFilter && (
                        <button className="qf-more-option" onClick={() => { setPbEngagementFilter('call'); setPbPage(1); setQfMoreOpen(false) }}>
                          <span className="qf-more-option-icon"><svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M23.57 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V7.57a1.14 1.14 0 1 1 2.28 0v18.29c0 .63.51 1.14 1.14 1.14h18.29a1.14 1.14 0 1 1 0 2.28Z"/><path d="M25.86 24.71h-16c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43h16c1.89 0 3.43 1.54 3.43 3.43v16c0 1.89-1.54 3.43-3.43 3.43Zm-16-20.57c-.63 0-1.14.51-1.14 1.14v16c0 .63.51 1.14 1.14 1.14h16c.63 0 1.14-.51 1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14h-16Z"/><path d="M16.84 17.48c-.3 0-.59-.12-.81-.33l-3.56-3.56a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l2.71 2.71 4.78-5.21c.43-.46 1.15-.5 1.61-.07.47.43.5 1.15.07 1.62l-5.59 6.09c-.21.23-.51.36-.82.37h-.02Z"/></svg></span>
                          <span className="qf-more-option-label">Engagement type</span>
                        </button>
                      )}
                      {pbStatusFilter && pbEngagementFilter && (
                        <div className="qf-more-empty">All filters added</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>}

            {/* Row 4 — Selection bar */}
            {pbSelectedRows.size > 0 && (
              <div className="hs-selection-bar">
                <span className="hs-sel-count">{pbSelectedRows.size} selected</span>
                <div className="hs-sel-sep" />
                <button className="hs-sel-btn">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  Edit
                </button>
                <button className="hs-sel-btn">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                  Assign
                </button>
                <button className="hs-sel-btn" style={{ color: '#dc2626' }} onClick={() => {
                  if (confirm(`Delete ${pbSelectedRows.size} playbook(s)?`)) {
                    Promise.all([...pbSelectedRows].map(id => deletePlaybook(id))).then(() => {
                      loadPlaybooks()
                      setPbSelectedRows(new Set())
                    }).catch(() => {})
                  }
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                  Delete
                </button>
                <button className="hs-sel-btn">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
                  Create tasks
                </button>
                <div className="hs-sel-sep" />
                <button className="hs-sel-close" onClick={() => setPbSelectedRows(new Set())} title="Deselect all">&times;</button>
              </div>
            )}

            {/* Row 5 — Data table + Row 6 — Pagination */}
            <div className={`hs-content${pbPreview ? ' with-preview' : ''}${tableSettingsOpen ? ' with-settings' : ''}`}>
              <div className="hs-table-area">
                <div className="hs-table-scroll">
                  {playbooksLoading ? (
                    <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                      <colgroup>
                        <col style={{ width: 40 }} />
                        <col style={{ width: 280 }} />
                        <col style={{ width: 120 }} />
                        <col style={{ width: 120 }} />
                        <col style={{ width: 80 }} />
                        <col style={{ width: 140 }} />
                        <col style={{ width: 140 }} />
                        <col style={{ width: 120 }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th className="hs-th-check"><input type="checkbox" disabled /></th>
                          <th className="hs-th-resizable hs-th-sortable hs-th-frozen" style={{ position: 'sticky', left: 40, top: 0, zIndex: 15, background: '#f8fafc' }}><span className="hs-th-content">Name</span><div className="hs-col-resize-handle" /></th>
                          <th className="hs-th-resizable hs-th-sortable"><span className="hs-th-content">Status</span><div className="hs-col-resize-handle" /></th>
                          <th className="hs-th-resizable hs-th-sortable"><span className="hs-th-content">Engagement</span><div className="hs-col-resize-handle" /></th>
                          <th className="hs-th-resizable hs-th-sortable"><span className="hs-th-content">Views</span><div className="hs-col-resize-handle" /></th>
                          <th className="hs-th-resizable hs-th-sortable"><span className="hs-th-content">Last Viewed</span><div className="hs-col-resize-handle" /></th>
                          <th className="hs-th-resizable hs-th-sortable"><span className="hs-th-content">Modified</span><div className="hs-col-resize-handle" /></th>
                          <th className="hs-th-resizable"><span className="hs-th-content">Actions</span><div className="hs-col-resize-handle" /></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr><td colSpan={8} className="hs-loading-cell"><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}><DotLottieReact src="/loading.lottie" loop autoplay style={{ width: 80, height: 80 }} /><span style={{ fontSize: '0.82rem', color: '#7c98b6', marginTop: 8 }}>Loading playbooks...</span></div></td></tr>
                      </tbody>
                    </table>
                  ) : pbPagedData.length === 0 ? (
                    <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                      <colgroup>
                        <col style={{ width: 40 }} />
                        <col style={{ width: 280 }} />
                        <col style={{ width: 120 }} />
                        <col style={{ width: 120 }} />
                        <col style={{ width: 80 }} />
                        <col style={{ width: 140 }} />
                        <col style={{ width: 140 }} />
                        <col style={{ width: 120 }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th className="hs-th-check"><input type="checkbox" disabled /></th>
                          <th className="hs-th-resizable hs-th-sortable hs-th-frozen" style={{ position: 'sticky', left: 40, top: 0, zIndex: 15, background: '#f8fafc' }}><span className="hs-th-content">Name</span><div className="hs-col-resize-handle" /></th>
                          <th className="hs-th-resizable hs-th-sortable"><span className="hs-th-content">Status</span><div className="hs-col-resize-handle" /></th>
                          <th className="hs-th-resizable hs-th-sortable"><span className="hs-th-content">Engagement</span><div className="hs-col-resize-handle" /></th>
                          <th className="hs-th-resizable hs-th-sortable"><span className="hs-th-content">Views</span><div className="hs-col-resize-handle" /></th>
                          <th className="hs-th-resizable hs-th-sortable"><span className="hs-th-content">Last Viewed</span><div className="hs-col-resize-handle" /></th>
                          <th className="hs-th-resizable hs-th-sortable"><span className="hs-th-content">Modified</span><div className="hs-col-resize-handle" /></th>
                          <th className="hs-th-resizable"><span className="hs-th-content">Actions</span><div className="hs-col-resize-handle" /></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr><td colSpan={8} className="hs-empty-cell">
                          <div style={{ padding: '60px 40px', textAlign: 'center' }}>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '12px' }}><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"/><path d="M8 7h6"/><path d="M8 11h8"/></svg>
                            <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#33475b', marginBottom: '6px' }}>Create playbooks for your team</div>
                            <div style={{ fontSize: '0.82rem', color: '#7c98b6', maxWidth: '360px', margin: '0 auto' }}>Build structured scripts and guides that reps follow during calls, meetings, or outreach.</div>
                          </div>
                        </td></tr>
                      </tbody>
                    </table>
                  ) : (
                    <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                      <colgroup>
                        <col style={{ width: 40 }} />
                        <col style={{ width: 280 }} />
                        <col style={{ width: 120 }} />
                        <col style={{ width: 120 }} />
                        <col style={{ width: 80 }} />
                        <col style={{ width: 140 }} />
                        <col style={{ width: 140 }} />
                        <col style={{ width: 120 }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th className="hs-th-check">
                            <input type="checkbox" checked={pbPagedData.length > 0 && pbSelectedRows.size === pbPagedData.length} onChange={toggleAllPbRows} />
                          </th>
                          {[
                            { key: 'title', label: 'Name', sortField: 'title', frozen: true },
                            { key: 'status', label: 'Status', sortField: 'status', frozen: false },
                            { key: 'engagement_type', label: 'Engagement', sortField: 'engagement_type', frozen: false },
                            { key: 'total_views', label: 'Views', sortField: 'total_views', frozen: false },
                            { key: 'last_viewed_at', label: 'Last Viewed', sortField: 'last_viewed_at', frozen: false },
                            { key: 'updated_at', label: 'Modified', sortField: 'updated_at', frozen: false },
                            { key: 'actions', label: 'Actions', sortField: '', frozen: false },
                          ].map(col => (
                            <th
                              key={col.key}
                              className={`hs-th-resizable${col.sortField ? ' hs-th-sortable' : ''}${col.frozen ? ' hs-th-frozen' : ''}`}
                              style={col.frozen ? { position: 'sticky', left: 40, top: 0, zIndex: 15, background: '#f8fafc' } : undefined}
                              onClick={col.sortField ? () => pbSortHeader(col.sortField) : undefined}
                              onContextMenu={col.sortField ? (e) => { e.preventDefault(); setColContextMenu({ field: col.sortField, x: e.clientX, y: e.clientY }) } : undefined}
                            >
                              <span className="hs-th-content">
                                {col.label}
                                {col.sortField && pbSort.field === col.sortField && <span className="hs-sort-arrow">{pbSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                                {col.sortField && <span className="hs-th-hover-sort"><svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M29.38 21.96a1.147 1.147 0 0 0-1.617 0l-3.85 3.852V5.714a1.143 1.143 0 0 0-2.286 0v20.099l-3.853-3.852a1.144 1.144 0 0 0-1.615 1.617l5.803 5.802a1.143 1.143 0 0 0 1.616 0l5.802-5.802a1.144 1.144 0 0 0 0-1.617M15.84 8.422 10.039 2.62a1.145 1.145 0 0 0-1.616 0L2.62 8.422a1.144 1.144 0 0 0 1.617 1.617l3.85-3.851v20.098a1.143 1.143 0 0 0 2.286 0V6.187l3.853 3.852a1.14 1.14 0 0 0 1.616-.001 1.14 1.14 0 0 0-.001-1.616"/></svg></span>}
                              </span>
                              {col.sortField && <button className="hs-th-kebab" onClick={(e) => { e.stopPropagation(); setColContextMenu({ field: col.sortField, x: e.clientX, y: e.clientY }) }}>
                                <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><circle cx="16" cy="4.14" r="2.29"/><circle cx="16" cy="15.57" r="2.29"/><circle cx="16" cy="27" r="2.29"/></svg>
                              </button>}
                              <div className="hs-col-resize-handle" onClick={(e) => e.stopPropagation()} />
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {pbPagedData.map((pb) => (
                          <tr key={pb.id} className={`hs-row${pbSelectedRows.has(pb.id) ? ' selected' : ''}`}>
                            <td className="hs-td-check">
                              <input
                                type="checkbox"
                                checked={pbSelectedRows.has(pb.id)}
                                onChange={() => {
                                  setPbSelectedRows(prev => {
                                    const next = new Set(prev)
                                    next.has(pb.id) ? next.delete(pb.id) : next.add(pb.id)
                                    return next
                                  })
                                }}
                              />
                            </td>
                            <td className="hs-td-name" style={{ position: 'sticky', left: 40, zIndex: 4, background: pbSelectedRows.has(pb.id) ? '#eff6ff' : '#fff' }}>
                              <div className="hs-name-cell">
                                <div className="hs-avatar" style={{ background: '#eff6ff', color: '#3b82f6' }}>
                                  {(pb.title || '?')[0].toUpperCase()}
                                </div>
                                <span className="hs-name-link"
                                  onClick={() => { fetchPlaybook(pb.id).then(detail => { setPlaybookDetail(detail); setPlaybookDetailOpen(true) }).catch(() => {}) }}>
                                  {pb.title}
                                </span>
                                <button className="hs-preview-btn" onClick={(e) => { e.stopPropagation(); setTableSettingsOpen(false); setPbPreview(pbPreview?.id === pb.id ? null : pb) }}>Preview</button>
                              </div>
                            </td>
                            <td>
                              <span className="hs-status-badge" style={{
                                background: pb.status === 'published' ? '#e8f5e9' : pb.status === 'archived' ? '#ffeaea' : '#f5f8fa',
                                color: pb.status === 'published' ? '#2e7d32' : pb.status === 'archived' ? '#d32f2f' : '#7c98b6',
                              }}>
                                {pb.status.charAt(0).toUpperCase() + pb.status.slice(1)}
                              </span>
                            </td>
                            <td style={{ color: '#516f90', fontSize: '0.8rem' }}>{pb.engagement_type ? pb.engagement_type.charAt(0).toUpperCase() + pb.engagement_type.slice(1) : <span className="cell-empty">--</span>}</td>
                            <td style={{ color: '#516f90', fontSize: '0.8rem' }}>{pb.total_views}</td>
                            <td style={{ color: '#516f90', fontSize: '0.8rem' }}>{pb.last_viewed_at ? formatDatePref(pb.last_viewed_at) : <span className="cell-empty">--</span>}</td>
                            <td style={{ color: '#516f90', fontSize: '0.8rem' }}>{pb.updated_at ? formatDatePref(pb.updated_at) : ''}</td>
                            <td>
                              <div style={{ display: 'flex', gap: '6px' }}>
                                {pb.status === 'draft' ? (
                                  <button onClick={() => publishPlaybook(pb.id).then(() => loadPlaybooks()).catch(() => {})}
                                    style={{ background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer', fontSize: '0.78rem' }}>Publish</button>
                                ) : (
                                  <button onClick={() => unpublishPlaybook(pb.id).then(() => loadPlaybooks()).catch(() => {})}
                                    style={{ background: 'none', border: 'none', color: '#7c98b6', cursor: 'pointer', fontSize: '0.78rem' }}>Unpublish</button>
                                )}
                                <button onClick={() => { if (confirm('Delete this playbook?')) deletePlaybook(pb.id).then(() => loadPlaybooks()).catch(() => {}) }}
                                  style={{ background: 'none', border: 'none', color: '#7c98b6', cursor: 'pointer', fontSize: '0.78rem' }}
                                  onMouseEnter={e => { e.currentTarget.style.color = '#d94c53' }}
                                  onMouseLeave={e => { e.currentTarget.style.color = '#7c98b6' }}>
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Row 6 — Pagination */}
                <div className="hs-pagination">
                  <nav className="hs-paginator" aria-label="Pagination">
                    <button
                      className="hs-paginator-btn"
                      disabled={pbCurrentPage <= 1}
                      onClick={() => setPbPage((p) => p - 1)}
                      aria-label="Previous page"
                    >
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95"/></svg>
                      <span>Prev</span>
                    </button>
                    {(() => {
                      if (pbTotalPages <= 7) {
                        const pages: number[] = []
                        for (let i = 1; i <= pbTotalPages; i++) pages.push(i)
                        return pages.map(p => (
                          <button key={p} className={`hs-paginator-num${p === pbCurrentPage ? ' active' : ''}`} onClick={() => setPbPage(p)}>{p}</button>
                        ))
                      }
                      const items: React.ReactNode[] = []
                      const addPage = (p: number) => items.push(
                        <button key={p} className={`hs-paginator-num${p === pbCurrentPage ? ' active' : ''}`} onClick={() => setPbPage(p)}>{p}</button>
                      )
                      const addEllipsis = (key: string) => items.push(
                        <span key={key} className="hs-paginator-ellipsis">...</span>
                      )
                      const shown = new Set<number>()
                      for (let i = 1; i <= Math.min(4, pbTotalPages); i++) shown.add(i)
                      shown.add(pbTotalPages)
                      for (let i = Math.max(1, pbCurrentPage - 1); i <= Math.min(pbTotalPages, pbCurrentPage + 1); i++) shown.add(i)
                      const sorted = [...shown].sort((a, b) => a - b)
                      for (let i = 0; i < sorted.length; i++) {
                        if (i > 0 && sorted[i] - sorted[i - 1] > 1) addEllipsis(`e${i}`)
                        addPage(sorted[i])
                      }
                      return items
                    })()}
                    <button
                      className="hs-paginator-btn"
                      disabled={pbCurrentPage >= pbTotalPages}
                      onClick={() => setPbPage((p) => p + 1)}
                      aria-label="Next page"
                    >
                      <span>Next</span>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M11 27.71c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62l9.48-9.48-9.48-9.47a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0L22.1 15.48c.45.45.45 1.17 0 1.62L11.81 27.39c-.22.22-.52.33-.81.33Z"/></svg>
                    </button>
                  </nav>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', marginLeft: 'auto' }}><select value={tablePageSize} onChange={e => { setTablePageSize(Number(e.target.value) as any); setPbPage(1) }} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #e2e8f0', fontSize: '0.82rem' }}><option value={100}>100</option><option value={200}>200</option><option value={500}>500</option></select><span>per page</span></div>
                </div>
              </div>

              {/* Settings panel (gear button) */}
              {tableSettingsOpen && (
                <div className="hs-settings-panel">
                  <div className="hs-settings-header">
                    <span className="hs-settings-title">Table settings</span>
                    <button className="hs-settings-close" onClick={() => setTableSettingsOpen(false)}>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                    </button>
                  </div>
                  <div className="hs-settings-body">
                    <div className="hs-settings-group">
                      <label className="hs-settings-label">
                        Pagination
                        <span className="hs-info-icon" title="Number of rows shown per page">
                          <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg>
                        </span>
                      </label>
                      {([100, 200, 500] as const).map(size => (
                        <label className="hs-settings-radio" key={size}>
                          <input type="radio" name="pbPageSize" checked={tablePageSize === size} onChange={() => { setTablePageSize(size); setPbPage(1) }} />
                          <span className="hs-radio-indicator" />
                          <span>{size} per page</span>
                        </label>
                      ))}
                    </div>
                    <hr className="hs-settings-divider" />
                    <div className="hs-settings-group">
                      <label className="hs-settings-label">
                        Row height
                        <span className="hs-info-icon" title="Adjust the vertical spacing of table rows">
                          <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg>
                        </span>
                      </label>
                      {([
                        { value: 'small' as const, label: 'Compact' },
                        { value: 'medium' as const, label: 'Default' },
                        { value: 'large' as const, label: 'Comfortable' },
                      ]).map(opt => (
                        <label className="hs-settings-radio" key={opt.value}>
                          <input type="radio" name="pbRowHeight" checked={tableRowHeight === opt.value} onChange={() => setTableRowHeight(opt.value)} />
                          <span className="hs-radio-indicator" />
                          <span>{opt.label}</span>
                        </label>
                      ))}
                    </div>
                    <hr className="hs-settings-divider" />
                    <div className="hs-settings-group">
                      <div className="hs-settings-toggle-row">
                        <label className="hs-settings-label">
                          Zebra striping
                          <span className="hs-info-icon" title="Alternate row background colors for easier reading">
                            <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg>
                          </span>
                        </label>
                        <button
                          className={`hs-toggle${tableZebraStriping ? ' on' : ''}`}
                          onClick={() => setTableZebraStriping(!tableZebraStriping)}
                          role="switch"
                          aria-checked={tableZebraStriping}
                        >
                          <span className="hs-toggle-track">
                            <span className="hs-toggle-on">ON</span>
                            <span className="hs-toggle-off">OFF</span>
                            <span className="hs-toggle-handle" />
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Preview sidebar */}
              {pbPreview && (
                <div className="hs-preview">
                  <div className="hs-preview-header">
                    <span style={{ fontWeight: 600, fontSize: '0.88rem', color: '#33475b' }}>{pbPreview.title}</span>
                    <button className="hs-preview-close" onClick={() => setPbPreview(null)}>&times;</button>
                  </div>
                  <div className="hs-preview-body" style={{ padding: '16px', fontSize: '0.82rem', color: '#516f90' }}>
                    <div style={{ marginBottom: '12px' }}>
                      <span className="hs-status-badge" style={{ background: pbPreview.status === 'published' ? '#e8f5e9' : '#f5f8fa', color: pbPreview.status === 'published' ? '#2e7d32' : '#7c98b6' }}>
                        {pbPreview.status.charAt(0).toUpperCase() + pbPreview.status.slice(1)}
                      </span>
                    </div>
                    {pbPreview.engagement_type && <div style={{ marginBottom: '8px' }}><strong>Type:</strong> {pbPreview.engagement_type.charAt(0).toUpperCase() + pbPreview.engagement_type.slice(1)}</div>}
                    <div style={{ marginBottom: '8px' }}><strong>Views:</strong> {pbPreview.total_views}</div>
                    {pbPreview.last_viewed_at && <div style={{ marginBottom: '8px' }}><strong>Last viewed:</strong> {formatDatePref(pbPreview.last_viewed_at)}</div>}
                    {pbPreview.updated_at && <div style={{ marginBottom: '8px' }}><strong>Modified:</strong> {formatDatePref(pbPreview.updated_at)}</div>}
                    {pbPreview.created_at && <div style={{ marginBottom: '12px' }}><strong>Created:</strong> {formatDatePref(pbPreview.created_at)}</div>}
                    <button className="hs-add-contacts-btn" style={{ width: '100%', marginTop: '8px' }} onClick={() => { fetchPlaybook(pbPreview.id).then(detail => { setPlaybookDetail(detail); setPlaybookDetailOpen(true); setPbPreview(null) }).catch(() => {}) }}>Open playbook</button>
                  </div>
                </div>
              )}
            </div>
          </div>
          )
        })()}

        {/* ========== Playbook Detail/Editor ========== */}
        {activeView === 'playbooks' && playbookDetailOpen && playbookDetail && (
          <div className="hs-contacts" style={{ display: 'flex', flexDirection: 'column' }}>
            <div className="hs-topbar">
              <div className="hs-topbar-left">
                <button style={{ background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer', fontSize: '0.82rem', padding: '0 12px', fontWeight: 500 }}
                  onClick={() => { setPlaybookDetailOpen(false); setPlaybookDetail(null); loadPlaybooks() }}>
                  &larr; Back to playbooks
                </button>
                <div className="hs-topbar-sep" />
                <span style={{ fontWeight: 600, fontSize: '0.92rem', color: '#33475b', padding: '0 8px' }}>{playbookDetail.title}</span>
                <span className="hs-status-badge" style={{ background: playbookDetail.status === 'published' ? '#e8f5e9' : '#f5f8fa', color: playbookDetail.status === 'published' ? '#2e7d32' : '#7c98b6', marginLeft: '8px' }}>
                  {playbookDetail.status.charAt(0).toUpperCase() + playbookDetail.status.slice(1)}
                </span>
              </div>
              <div className="hs-topbar-right">
                {playbookDetail.status === 'draft' ? (
                  <button className="hs-add-contacts-btn" onClick={() => publishPlaybook(playbookDetail.id).then(_u => { setPlaybookDetail({ ...playbookDetail, status: 'published' }) }).catch(() => {})}>Publish</button>
                ) : (
                  <button className="hs-add-contacts-btn" style={{ background: '#fff', color: '#33475b', border: '1px solid #cbd6e2' }} onClick={() => unpublishPlaybook(playbookDetail.id).then(_u => { setPlaybookDetail({ ...playbookDetail, status: 'draft' }) }).catch(() => {})}>Unpublish</button>
                )}
              </div>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: '20px', display: 'flex', gap: '24px' }}>
              {/* Main content editor */}
              <div style={{ flex: 1, maxWidth: '700px' }}>
                {/* Content section */}
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#33475b', marginBottom: '8px', display: 'block' }}>Content</label>
                  <div
                    contentEditable
                    suppressContentEditableWarning
                    style={{ minHeight: '120px', border: '1px solid #cbd6e2', borderRadius: '4px', padding: '12px', fontSize: '0.88rem', color: '#33475b', outline: 'none', lineHeight: 1.6 }}
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(playbookDetail.content || '<p>Add your playbook content here...</p>') }}
                    onBlur={(e) => { const html = e.currentTarget.innerHTML; updatePlaybook(playbookDetail.id, { content: html }).then(_u => setPlaybookDetail({ ...playbookDetail, content: html })).catch(() => {}) }}
                  />
                </div>

                {/* Questions section */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#33475b' }}>Questions</label>
                  </div>
                  {(playbookDetail.questions || []).map((q: CrmPlaybookQuestion) => (
                    <div key={q.id} className="playbook-question-card">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#33475b', marginBottom: '8px' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px', verticalAlign: '-2px' }}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                            {q.question_text}
                          </div>
                          <div style={{ padding: '8px 10px', background: '#f5f8fa', borderRadius: '4px', fontSize: '0.82rem', color: '#94a3b8', fontStyle: 'italic' }}>Notes</div>
                        </div>
                        <button onClick={() => deletePlaybookQuestion(playbookDetail.id, q.id).then(() => fetchPlaybook(playbookDetail.id).then(d => setPlaybookDetail(d))).catch(() => {})}
                          style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '16px', marginLeft: '8px' }}
                          onMouseEnter={e => { e.currentTarget.style.color = '#d94c53' }}
                          onMouseLeave={e => { e.currentTarget.style.color = '#94a3b8' }}>
                          &times;
                        </button>
                      </div>
                    </div>
                  ))}
                  {/* Add question form */}
                  <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                    <input type="text" value={newQuestionText} onChange={e => setNewQuestionText(e.target.value)} placeholder="Enter a question..." style={{ flex: 1, padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', fontSize: '0.85rem' }} />
                    <button onClick={() => {
                      if (!newQuestionText.trim()) return
                      addPlaybookQuestion(playbookDetail.id, { question_text: newQuestionText.trim() }).then(() => {
                        setNewQuestionText('')
                        fetchPlaybook(playbookDetail.id).then(d => setPlaybookDetail(d))
                      }).catch(() => {})
                    }} style={{ padding: '8px 16px', borderRadius: '4px', border: 'none', background: '#1976d2', color: '#fff', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 500, whiteSpace: 'nowrap' }}>
                      Add question
                    </button>
                  </div>
                </div>
              </div>

              {/* Settings sidebar */}
              <div style={{ width: '260px', flexShrink: 0 }}>
                <div style={{ background: '#f5f8fa', borderRadius: '8px', padding: '16px' }}>
                  <h4 style={{ fontSize: '0.82rem', fontWeight: 700, color: '#33475b', marginBottom: '12px' }}>Settings</h4>
                  <label style={{ fontSize: '0.78rem', color: '#33475b', display: 'block', marginBottom: '8px' }}>
                    Engagement Type
                    <select value={playbookDetail.engagement_type || ''} onChange={e => {
                      const val = e.target.value
                      updatePlaybook(playbookDetail.id, { engagement_type: val || null }).then(() => setPlaybookDetail({ ...playbookDetail, engagement_type: val || null })).catch(() => {})
                    }} style={{ display: 'block', width: '100%', padding: '6px 8px', border: '1px solid #cbd6e2', borderRadius: '4px', marginTop: '4px', fontSize: '0.82rem' }}>
                      <option value="">None</option>
                      <option value="call">Call</option>
                      <option value="email">Email</option>
                      <option value="meeting">Meeting</option>
                      <option value="note">Note</option>
                    </select>
                  </label>
                  <div style={{ fontSize: '0.72rem', color: '#7c98b6', marginTop: '12px' }}>
                    Views: {playbookDetail.total_views}<br />
                    Created: {playbookDetail.created_at ? formatDatePref(playbookDetail.created_at) : '--'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========== Orders View ========== */}
        {activeView === 'orders' && (
          <div className="hs-contacts">
            <div className="hs-topbar">
              <div className="hs-topbar-left">
                <div style={{ display: 'flex', alignItems: 'center', padding: '0 12px', gap: '6px' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5H2v7l6.29 6.29c.94.94 2.48.94 3.42 0l3.58-3.58c.94-.94.94-2.48 0-3.42L9 5Z"/><path d="M6 9.01V9"/><path d="m15 5 6.3 6.3a2.4 2.4 0 0 1 0 3.4L17 19"/></svg>
                  <span style={{ fontWeight: 600, fontSize: '0.92rem', color: '#33475b' }}>Orders</span>
                </div>
                <div className="hs-topbar-sep" />
                <span className={`hs-view-tab${!ordersStatusFilter ? ' active' : ''}`} onClick={() => setOrdersStatusFilter('')} style={{ cursor: 'pointer' }}>All ({ordersTotal})</span>
                <span className={`hs-view-tab${ordersStatusFilter === 'in_progress' ? ' active' : ''}`} onClick={() => setOrdersStatusFilter('in_progress')} style={{ cursor: 'pointer' }}>Active</span>
                <span className={`hs-view-tab${ordersStatusFilter === 'completed' ? ' active' : ''}`} onClick={() => setOrdersStatusFilter('completed')} style={{ cursor: 'pointer' }}>Completed</span>
                <span className={`hs-view-tab${ordersStatusFilter === 'invoiced' ? ' active' : ''}`} onClick={() => setOrdersStatusFilter('invoiced')} style={{ cursor: 'pointer' }}>Invoiced</span>
              </div>
              <div className="hs-topbar-right">
                <div className="hs-search-wrap" style={{ marginRight: '8px' }}>
                  <svg className="hs-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  <input className="hs-search-input" placeholder="Search orders" value={ordersSearch} onChange={e => setOrdersSearch(e.target.value)} />
                  {ordersSearch && <button className="hs-search-clear" onClick={() => setOrdersSearch('')}>&times;</button>}
                </div>
                <button
                  className="hs-add-contacts-btn"
                  onClick={() => { setOrderForm({ title: '', status: 'draft' }); setOrderLineItems([]); setOrderError(null); setShowOrderPanel(true) }}
                >
                  Create order
                </button>
              </div>
            </div>
            <div className="hs-content">
              <div className="hs-table-area">
                <div className="hs-table-scroll">
                  {ordersLoading ? (
                    <div style={{ padding: '40px', textAlign: 'center', color: '#7c98b6' }}>Loading orders...</div>
                  ) : ordersData.length === 0 ? (
                    <div style={{ padding: '60px 40px', textAlign: 'center' }}>
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '12px' }}><path d="M9 5H2v7l6.29 6.29c.94.94 2.48.94 3.42 0l3.58-3.58c.94-.94.94-2.48 0-3.42L9 5Z"/><path d="M6 9.01V9"/><path d="m15 5 6.3 6.3a2.4 2.4 0 0 1 0 3.4L17 19"/></svg>
                      <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#33475b', marginBottom: '6px' }}>Manage your work orders</div>
                      <div style={{ fontSize: '0.82rem', color: '#7c98b6', maxWidth: '360px', margin: '0 auto' }}>Track jobs from estimate through completion and final invoicing with work orders.</div>
                    </div>
                  ) : (
                    <table className="hs-table">
                      <thead>
                        <tr>
                          <th>Order #</th>
                          <th>Title</th>
                          <th>Contact</th>
                          <th>Status</th>
                          <th>Total</th>
                          <th>Start Date</th>
                          <th>Created</th>
                          <th style={{ width: '80px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {ordersData.map((o) => {
                          const statusColors: Record<string, { bg: string; fg: string }> = {
                            draft: { bg: '#f8fafc', fg: '#64748b' },
                            sent: { bg: '#e3f2fd', fg: '#1565c0' },
                            approved: { bg: '#e8f5e9', fg: '#2e7d32' },
                            in_progress: { bg: '#fff3e0', fg: '#e65100' },
                            completed: { bg: '#e8f5e9', fg: '#2e7d32' },
                            invoiced: { bg: '#eff6ff', fg: '#3b82f6' },
                            paid: { bg: '#e8f5e9', fg: '#1b5e20' },
                            cancelled: { bg: '#ffeaea', fg: '#d32f2f' },
                          }
                          const sc = statusColors[o.status] || statusColors.draft
                          return (
                            <tr key={o.id}>
                              <td><span style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: '#1976d2' }}>{o.order_number}</span></td>
                              <td style={{ fontWeight: 500, color: '#33475b' }}>{o.title}</td>
                              <td style={{ color: '#516f90' }}>{o.client_name || o.unified_client_id || '--'}</td>
                              <td>
                                <span className="hs-status-badge" style={{ background: sc.bg, color: sc.fg }}>
                                  {o.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </span>
                              </td>
                              <td style={{ fontWeight: 500, color: '#33475b' }}>{currencyFormatter.format(o.total)}</td>
                              <td style={{ color: '#516f90', fontSize: '13px' }}>{o.start_date ? formatDatePref(o.start_date) : '--'}</td>
                              <td style={{ color: '#516f90', fontSize: '13px' }}>{o.created_at ? formatDatePref(o.created_at) : ''}</td>
                              <td>
                                <button onClick={() => { if (confirm('Delete this order?')) deleteOrder(o.id).then(() => loadOrders()).catch(() => {}) }}
                                  style={{ background: 'none', border: 'none', color: '#7c98b6', cursor: 'pointer', fontSize: '0.78rem' }}
                                  onMouseEnter={e => { e.currentTarget.style.color = '#d94c53' }}
                                  onMouseLeave={e => { e.currentTarget.style.color = '#7c98b6' }}>
                                  Delete
                                </button>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* bp-camp-v1 — Campaigns list/create shell with lazy flow editor */}
        {activeView === 'campaigns' && (
          <Suspense fallback={PrefsLazyFallback}>
            <CampaignsView />
          </Suspense>
        )}

        {/* ========== Conversations View ========== */}
        {activeView === 'conversations' && (() => {
          // Client-side filtering
          const convSearchLower = convSearch.toLowerCase()
          const filteredConversations = conversationsData.filter((c: Record<string, unknown>) => {
            // Status filter
            if (convFilter === 'open' && c.status !== 'open') return false
            if (convFilter === 'closed' && c.status !== 'closed') return false
            // Channel filter
            if (convChannelFilter && String(c.channel ?? '').toLowerCase() !== convChannelFilter) return false
            // Search filter
            if (convSearchLower) {
              const subject = String(c.subject ?? '').toLowerCase()
              const channel = String(c.channel ?? '').toLowerCase()
              const clientId = String(c.client_id ?? '').toLowerCase()
              if (!subject.includes(convSearchLower) && !channel.includes(convSearchLower) && !clientId.includes(convSearchLower)) return false
            }
            return true
          })
          // Sort
          filteredConversations.sort((a: any, b: any) => {
            const field = convSort.field
            const order = convSort.order === 'ASC' ? 1 : -1
            const av = String(a[field] ?? '')
            const bv = String(b[field] ?? '')
            return av < bv ? -order : av > bv ? order : 0
          })
          const convTotal = filteredConversations.length
          const convPageSize = 50
          const convTotalPages = Math.max(1, Math.ceil(convTotal / convPageSize))
          const convCurrentPage = Math.min(convPage, convTotalPages)
          const convPagedData = filteredConversations.slice((convCurrentPage - 1) * convPageSize, convCurrentPage * convPageSize)
          const toggleAllConvRows = () => {
            if (convSelectedRows.size === convPagedData.length) {
              setConvSelectedRows(new Set())
            } else {
              setConvSelectedRows(new Set(convPagedData.map((c: Record<string, unknown>) => Number(c.id))))
            }
          }
          return (
          <div className="hs-contacts">
            {/* Top bar: object switcher + view tabs + actions */}
            <div className="hs-topbar">
              <div className="hs-topbar-left">
                {/* Object switcher */}
                <div className="hs-obj-switcher-wrap" ref={objSwitcherRef}>
                  <button className={`hs-obj-switcher${objSwitcherOpen ? ' open' : ''}`} onClick={() => { setObjSwitcherOpen(!objSwitcherOpen); setObjSwitcherSearch('') }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                    <span>Conversations</span>
                    <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                  </button>
                  {objSwitcherOpen && (
                    <div className="hs-obj-panel">
                      <div className="hs-obj-search-wrap">
                        <svg className="hs-obj-search-icon" width="13" height="13" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                        <input
                          className="hs-obj-search"
                          placeholder="Search"
                          value={objSwitcherSearch}
                          onChange={(e) => setObjSwitcherSearch(e.target.value)}
                          autoFocus
                        />
                      </div>
                      <ul className="hs-obj-list" role="listbox">
                        {[
                          { label: 'Contacts', view: 'contacts' as ViewMode },
                            { label: 'Clients', view: 'companies' as ViewMode },
                          { label: 'Deals', view: 'deals' as ViewMode },
                          { label: 'Leads', view: 'leads' as ViewMode },
                          { label: 'Projects', view: 'projects' as ViewMode },
                          { label: 'Tickets', view: 'tickets' as ViewMode },
                          { label: 'Calls', view: 'calls' as ViewMode },
                        ]
                          .filter(item => !objSwitcherSearch || item.label.toLowerCase().includes(objSwitcherSearch.toLowerCase()))
                          .map(item => (
                            <li
                              key={item.view}
                              className={`hs-obj-item${item.view === activeView ? ' active' : ''}`}
                              role="option"
                              aria-selected={item.view === activeView}
                              onClick={() => {
                                setActiveView(item.view)
                                setObjSwitcherOpen(false)
                                setObjSwitcherSearch('')
                              }}
                            >
                              {item.label}
                            </li>
                          ))
                        }
                      </ul>
                    </div>
                  )}
                </div>

                {/* Separator */}
                <div className="hs-topbar-sep" />

                {/* View tabs */}
                <div className="hs-view-tabs">
                  {[
                    { key: '', label: 'All conversations' },
                    { key: 'open', label: 'Open' },
                    { key: 'closed', label: 'Closed' },
                  ].map((tab) => {
                    const isActive = convFilter === tab.key
                    return (
                      <div
                        key={tab.key}
                        className={`hs-view-tab${isActive ? ' active' : ''}`}
                        onClick={() => { setConvFilter(tab.key); setConvPage(1) }}
                      >
                        <span className="hs-tab-label">{tab.label}</span>
                        {isActive && convTotal > 0 && <span className="hs-tab-count">{convTotal.toLocaleString()}</span>}
                      </div>
                    )
                  })}
                </div>
              </div>

              <div className="hs-topbar-right">
                {/* Create conversation — direct action (Import removed) */}
                <button className="hs-add-contacts-btn" onClick={() => { /* TODO: open create-conversation flow */ }}>
                  Create conversation
                </button>
              </div>
            </div>

            {/* Toolbar */}
            <div className="hs-toolbar">
              <div className="hs-toolbar-left">
                {/* Search */}
                <div className="hs-search-wrap">
                  <svg className="hs-search-icon" width="14" height="14" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M28.14 29.29c-.29 0-.58-.11-.81-.33l-7.58-7.58a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l7.58 7.58a1.14 1.14 0 0 1-.81 1.95"/><path d="M13.29 24.71c-6.3 0-11.43-5.13-11.43-11.43S6.98 1.86 13.29 1.86s11.43 5.13 11.43 11.43-5.13 11.43-11.43 11.43Zm0-20.57c-5.04 0-9.14 4.1-9.14 9.14s4.1 9.14 9.14 9.14 9.14-4.1 9.14-9.14-4.1-9.14-9.14-9.14"/></svg>
                  <input
                    className="hs-search-input"
                    placeholder="Search"
                    value={convSearch}
                    onChange={(e) => { setConvSearch(e.target.value); setConvPage(1) }}
                  />
                  {convSearch && (
                    <button className="hs-search-clear" onClick={() => { setConvSearch(''); setConvPage(1) }}>&times;</button>
                  )}
                </div>
                <div className="hs-tb-sep" />
                <button className={`hs-tb-btn hs-tb-btn-bordered${quickFiltersOpen ? ' hs-tb-btn-pressed' : ''}`} title="Filters" onClick={() => setQuickFiltersOpen(!quickFiltersOpen)}>
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M30.71 16.86H13.57a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28M6.71 16.86H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M10.14 20.29c-2.52 0-4.57-2.05-4.57-4.57s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M6.71 6.57H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M30.71 6.57H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M18.14 6.57H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 10C19.05 10 17 7.95 17 5.43S19.05.86 21.57.86s4.57 2.05 4.57 4.57S24.09 10 21.57 10m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M30.71 27.14H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M18.14 27.14H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 30.57C19.05 30.57 17 28.52 17 26s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29"/></svg>
                  <span>Filters</span>
                </button>
              </div>
            </div>

            {/* Quick filters bar */}
            {quickFiltersOpen && <div className="hs-quick-filters">
              {/* Channel filter */}
              <button
                className={`hs-qf-btn hs-qf-trigger${convChannelFilter ? ' has-value' : ''}`}
                onClick={() => {
                  const vals = ['', 'email', 'sms', 'chat', 'phone']
                  const idx = vals.indexOf(convChannelFilter)
                  setConvChannelFilter(vals[(idx + 1) % vals.length])
                  setConvPage(1)
                }}
              >
                <span>{convChannelFilter ? convChannelFilter.charAt(0).toUpperCase() + convChannelFilter.slice(1) : 'Channel'}</span>
                {convChannelFilter && (
                  <span className="hs-qf-clear" onClick={(e) => { e.stopPropagation(); setConvChannelFilter(''); setConvPage(1) }}>&times;</span>
                )}
                {!convChannelFilter && <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>}
              </button>

              {/* Sort by */}
              <button
                className={`hs-qf-btn hs-qf-trigger${convSort.field !== 'last_message_at' ? ' has-value' : ''}`}
                onClick={() => {
                  const fields = ['last_message_at', 'subject', 'created_at', 'channel']
                  const idx = fields.indexOf(convSort.field)
                  const next = fields[(idx + 1) % fields.length]
                  setConvSort({ field: next, order: next === 'subject' || next === 'channel' ? 'ASC' : 'DESC' })
                  setConvPage(1)
                }}
              >
                <span>Sort: {{ last_message_at: 'Last Message', subject: 'Subject', created_at: 'Created', channel: 'Channel' }[convSort.field] || 'Last Message'}</span>
                <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
              </button>

              <div className="hs-qf-sep" />

              <div className="hs-qf-more-wrap">
                <button className="hs-qf-btn hs-qf-more">
                  <svg width="10" height="10" viewBox="0 0 32 32" fill="currentColor"><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V3a1.14 1.14 0 1 1 2.28 0v25.14c0 .63-.51 1.14-1.14 1.14Z"/><path d="M28.14 16.71H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28"/></svg>
                  <span>More</span>
                </button>
              </div>
            </div>}

            <div className="hs-content">
              <div className="hs-table-area">
                <div className="hs-table-scroll">
                  {conversationsLoading ? (
                    <table className={`hs-table hs-density-medium`} style={{ tableLayout: 'fixed' }}>
                      <colgroup>
                        <col style={{ width: 40 }} />
                        <col style={{ width: 220 }} />
                        <col style={{ width: 100 }} />
                        <col style={{ width: 110 }} />
                        <col style={{ width: 160 }} />
                        <col style={{ width: 130 }} />
                        <col style={{ width: 140 }} />
                        <col style={{ width: 130 }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th className="hs-th-check"><input type="checkbox" disabled /></th>
                          <th className="hs-th-resizable" style={{ position: 'sticky', left: 40, zIndex: 2, background: '#f5f8fa' }}><span>Subject</span></th>
                          <th className="hs-th-resizable"><span>Status</span></th>
                          <th className="hs-th-resizable"><span>Channel</span></th>
                          <th className="hs-th-resizable"><span>Contact</span></th>
                          <th className="hs-th-resizable"><span>Assigned To</span></th>
                          <th className="hs-th-resizable"><span>Last Message</span></th>
                          <th className="hs-th-resizable"><span>Created</span></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr><td colSpan={8} className="hs-loading-cell"><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}><DotLottieReact src="/loading.lottie" loop autoplay style={{ width: 80, height: 80 }} /><span style={{ fontSize: '0.82rem', color: '#7c98b6', marginTop: 8 }}>Loading conversations...</span></div></td></tr>
                      </tbody>
                    </table>
                  ) : convPagedData.length === 0 ? (
                    <table className={`hs-table hs-density-medium`} style={{ tableLayout: 'fixed' }}>
                      <colgroup>
                        <col style={{ width: 40 }} />
                        <col style={{ width: 220 }} />
                        <col style={{ width: 100 }} />
                        <col style={{ width: 110 }} />
                        <col style={{ width: 160 }} />
                        <col style={{ width: 130 }} />
                        <col style={{ width: 140 }} />
                        <col style={{ width: 130 }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th className="hs-th-check"><input type="checkbox" disabled /></th>
                          <th className="hs-th-resizable" style={{ position: 'sticky', left: 40, zIndex: 2, background: '#f5f8fa' }}><span>Subject</span></th>
                          <th className="hs-th-resizable"><span>Status</span></th>
                          <th className="hs-th-resizable"><span>Channel</span></th>
                          <th className="hs-th-resizable"><span>Contact</span></th>
                          <th className="hs-th-resizable"><span>Assigned To</span></th>
                          <th className="hs-th-resizable"><span>Last Message</span></th>
                          <th className="hs-th-resizable"><span>Created</span></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr><td colSpan={8} className="hs-empty-cell">
                          <div style={{ padding: '40px', textAlign: 'center' }}>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '12px' }}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                            <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#33475b', marginBottom: '6px' }}>No conversations found</div>
                            <div style={{ fontSize: '0.82rem', color: '#7c98b6', maxWidth: '360px', margin: '0 auto' }}>Start a conversation from a contact or deal record, or adjust your search filters.</div>
                          </div>
                        </td></tr>
                      </tbody>
                    </table>
                  ) : (
                    <table className={`hs-table hs-density-medium`} style={{ tableLayout: 'fixed' }}>
                      <colgroup>
                        <col style={{ width: 40 }} />
                        <col style={{ width: 220 }} />
                        <col style={{ width: 100 }} />
                        <col style={{ width: 110 }} />
                        <col style={{ width: 160 }} />
                        <col style={{ width: 130 }} />
                        <col style={{ width: 140 }} />
                        <col style={{ width: 130 }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th className="hs-th-check">
                            <input type="checkbox" checked={convPagedData.length > 0 && convSelectedRows.size === convPagedData.length} onChange={toggleAllConvRows} />
                          </th>
                          <th className="hs-th-resizable" style={{ position: 'sticky', left: 40, zIndex: 2, background: '#f5f8fa' }}><span>Subject</span></th>
                          <th className="hs-th-resizable"><span>Status</span></th>
                          <th className="hs-th-resizable"><span>Channel</span></th>
                          <th className="hs-th-resizable"><span>Contact</span></th>
                          <th className="hs-th-resizable"><span>Assigned To</span></th>
                          <th className="hs-th-resizable"><span>Last Message</span></th>
                          <th className="hs-th-resizable"><span>Created</span></th>
                        </tr>
                      </thead>
                      <tbody>
                        {convPagedData.map((c: Record<string, unknown>) => (
                          <tr key={String(c.id)} className={`hs-row${convSelectedRows.has(Number(c.id)) ? ' selected' : ''}`}>
                            <td className="hs-td-check">
                              <input
                                type="checkbox"
                                checked={convSelectedRows.has(Number(c.id))}
                                onChange={() => {
                                  setConvSelectedRows(prev => {
                                    const next = new Set(prev)
                                    next.has(Number(c.id)) ? next.delete(Number(c.id)) : next.add(Number(c.id))
                                    return next
                                  })
                                }}
                              />
                            </td>
                            <td style={{ fontWeight: 500, color: '#33475b', position: 'sticky', left: 40, zIndex: 1, background: '#fff' }}>{String(c.subject ?? '(No subject)')}</td>
                            <td>
                              <span className="hs-status-badge" style={{
                                background: c.status === 'open' ? '#fff3e0' : c.status === 'closed' ? '#f5f8fa' : '#e8f0fe',
                                color: c.status === 'open' ? '#e67e22' : c.status === 'closed' ? '#7c98b6' : '#2e60e0',
                              }}>
                                {String(c.status ?? 'open').charAt(0).toUpperCase() + String(c.status ?? 'open').slice(1)}
                              </span>
                            </td>
                            <td style={{ color: '#516f90' }}>{String(c.channel ?? 'email')}</td>
                            <td style={{ color: '#516f90' }}>{String(c.client_id ?? '\u2014')}</td>
                            <td style={{ color: '#516f90' }}>{c.assigned_to ? (CALENDAR_USERS_MAP[Number(c.assigned_to)] || `User ${c.assigned_to}`) : '\u2014'}</td>
                            <td style={{ color: '#516f90', fontSize: '13px' }}>{c.last_message_at ? formatDatePref(String(c.last_message_at)) : '\u2014'}</td>
                            <td style={{ color: '#516f90', fontSize: '13px' }}>{c.created_at ? formatDatePref(String(c.created_at)) : '\u2014'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Pagination */}
                {convTotalPages > 1 && (
                  <div className="hs-pagination">
                    <nav className="hs-paginator" aria-label="Pagination">
                      <button
                        className="hs-paginator-btn"
                        disabled={convCurrentPage <= 1}
                        onClick={() => setConvPage((p) => p - 1)}
                        aria-label="Previous page"
                      >
                        <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95"/></svg>
                        <span>Prev</span>
                      </button>
                      {(() => {
                        if (convTotalPages <= 7) {
                          const pages: number[] = []
                          for (let i = 1; i <= convTotalPages; i++) pages.push(i)
                          return pages.map(p => (
                            <button key={p} className={`hs-paginator-num${p === convCurrentPage ? ' active' : ''}`} onClick={() => setConvPage(p)}>{p}</button>
                          ))
                        }
                        const items: React.ReactNode[] = []
                        const addPage = (p: number) => items.push(
                          <button key={p} className={`hs-paginator-num${p === convCurrentPage ? ' active' : ''}`} onClick={() => setConvPage(p)}>{p}</button>
                        )
                        const addEllipsis = (key: string) => items.push(
                          <span key={key} className="hs-paginator-ellipsis">...</span>
                        )
                        addPage(1)
                        if (convCurrentPage > 3) addEllipsis('el')
                        const start = Math.max(2, convCurrentPage - 1)
                        const end = Math.min(convTotalPages - 1, convCurrentPage + 1)
                        for (let i = start; i <= end; i++) addPage(i)
                        if (convCurrentPage < convTotalPages - 2) addEllipsis('er')
                        addPage(convTotalPages)
                        return items
                      })()}
                      <button
                        className="hs-paginator-btn"
                        disabled={convCurrentPage >= convTotalPages}
                        onClick={() => setConvPage((p) => p + 1)}
                        aria-label="Next page"
                      >
                        <span>Next</span>
                        <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M11 27.71c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62l9.48-9.48-9.48-9.47a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0L22.1 15.48c.45.45.45 1.17 0 1.62L11.81 27.39c-.22.22-.52.33-.81.33Z"/></svg>
                      </button>
                    </nav>
                  </div>
                )}

                {/* Record count */}
                <div style={{ padding: '8px 16px', fontSize: '0.82rem', color: '#7c98b6', borderTop: '1px solid #eaf0f6' }}>
                  {convTotal} conversation{convTotal !== 1 ? 's' : ''}
                </div>
              </div>
            </div>
          </div>
          )
        })()}


        {/* ========== Marketing Email View ========== */}
        {activeView === 'marketing-email' && (
          <div className="hs-contacts">
            {/* ROW 1: TOPBAR */}
            <div className="hs-topbar">
              <div className="hs-topbar-left">
                {/* 1A: Object Switcher */}
                <div className="hs-obj-switcher-wrap" ref={objSwitcherRef}>
                  <button className={`hs-obj-switcher${objSwitcherOpen ? ' open' : ''}`} onClick={() => { setObjSwitcherOpen(!objSwitcherOpen); setObjSwitcherSearch('') }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                    <span>Marketing Email</span>
                    <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                  </button>
                  {objSwitcherOpen && (
                    <div className="hs-obj-panel">
                      <div className="hs-obj-search-wrap">
                        <svg className="hs-obj-search-icon" width="13" height="13" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                        <input
                          className="hs-obj-search"
                          placeholder="Search"
                          value={objSwitcherSearch}
                          onChange={(e) => setObjSwitcherSearch(e.target.value)}
                          autoFocus
                        />
                      </div>
                      <ul className="hs-obj-list" role="listbox">
                        {[
                          { label: 'Email', view: 'marketing-email' as ViewMode },
                          { label: 'Ads', view: 'ads' as ViewMode },
                          { label: 'Social', view: 'social' as ViewMode },
                          { label: 'Forms', view: 'forms' as ViewMode },
                          { label: 'Campaigns', view: 'campaigns' as ViewMode },
                          { label: 'Landing Pages', view: 'landing-pages' as ViewMode },
                        ]
                          .filter(item => !objSwitcherSearch || item.label.toLowerCase().includes(objSwitcherSearch.toLowerCase()))
                          .map(item => (
                            <li
                              key={item.view}
                              className={`hs-obj-item${item.view === activeView ? ' active' : ''}`}
                              role="option"
                              aria-selected={item.view === activeView}
                              onClick={() => {
                                setActiveView(item.view)
                                setObjSwitcherOpen(false)
                                setObjSwitcherSearch('')
                              }}
                            >
                              {item.label}
                            </li>
                          ))
                        }
                      </ul>
                    </div>
                  )}
                </div>

                {/* 1B: Separator */}
                <div className="hs-topbar-sep" />

                {/* 1C: View tabs */}
                <div className="hs-view-tabs">
                  <div
                    className={`hs-view-tab${mktgEmailTab === 'manage' ? ' active' : ''}`}
                    onClick={() => setMktgEmailTab('manage')}
                  >
                    <span className="hs-tab-label">Manage</span>
                    {mktgEmailTab === 'manage' && mktgEmailsTotal > 0 && <span className="hs-tab-count">{mktgEmailsTotal.toLocaleString()}</span>}
                    {mktgEmailTab === 'manage' && (
                      <span className="hs-tab-kebab" onClick={(e) => { e.stopPropagation(); setTabEditOpen(!tabEditOpen) }}>
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>
                      </span>
                    )}
                    {mktgEmailTab === 'manage' && tabEditOpen && (
                      <div className="hs-tab-edit-dropdown">
                        <button className="hs-tab-edit-item" disabled>Rename</button>
                        <button className="hs-tab-edit-item" disabled>Clone</button>
                        <button className="hs-tab-edit-item" disabled>Manage Sharing</button>
                      </div>
                    )}
                  </div>
                  <div
                    className={`hs-view-tab${mktgEmailTab === 'analyze' ? ' active' : ''}`}
                    onClick={() => setMktgEmailTab('analyze')}
                  >
                    <span className="hs-tab-label">Analyze</span>
                    {mktgEmailTab === 'analyze' && (
                      <span className="hs-tab-kebab" onClick={(e) => { e.stopPropagation(); setTabEditOpen(!tabEditOpen) }}>
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>
                      </span>
                    )}
                    <span className="hs-tab-x" onClick={(e) => { e.stopPropagation(); setMktgEmailTab('manage') }}>&times;</span>
                    {mktgEmailTab === 'analyze' && tabEditOpen && (
                      <div className="hs-tab-edit-dropdown">
                        <button className="hs-tab-edit-item" disabled>Rename</button>
                        <button className="hs-tab-edit-item" disabled>Clone</button>
                        <button className="hs-tab-edit-item" disabled>Manage Sharing</button>
                      </div>
                    )}
                  </div>

                  {/* 1D: Quick-add view button */}
                  <div className="hs-topbar-sep" />
                  <div className="hs-view-add-wrap" ref={viewAddMenuRef} style={{ position: 'relative', display: 'inline-flex', alignSelf: 'center' }}>
                    <button
                      className={`hs-quick-add-btn${viewAddMenuOpen ? ' active' : ''}`}
                      title="Create or add view"
                      type="button"
                      aria-label="Create or add view"
                      onClick={() => { setViewAddMenuOpen(!viewAddMenuOpen); setViewAddMenuMode('menu') }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                    </button>
                    {viewAddMenuOpen && viewAddMenuMode === 'menu' && (
                      <div className="hs-view-add-dropdown">
                        <button className="hs-view-add-item" onClick={() => setViewAddMenuMode('type')}>
                          Create new view
                        </button>
                        <button className="hs-view-add-item" onClick={() => {
                          setViewAddMenuOpen(false)
                          setViewAddMenuMode('menu')
                        }}>
                          Add view
                        </button>
                      </div>
                    )}
                    {viewAddMenuOpen && viewAddMenuMode === 'type' && (
                      <div className="hs-view-add-dropdown hs-view-type-picker">
                        <button className="hs-view-type-btn" onClick={() => {
                          setViewAddMenuOpen(false)
                          setViewAddMenuMode('menu')
                        }}>
                          <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                          <span>Table</span>
                        </button>
                        <button className="hs-view-type-btn" disabled style={{ opacity: 0.4 }}>
                          <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M11.43 11.43h-8a1.14 1.14 0 1 1 0-2.28h8a1.14 1.14 0 1 1 0 2.28M28.57 11.43h-8a1.14 1.14 0 1 1 0-2.28h8a1.14 1.14 0 1 1 0 2.28"/><path d="M9.14 29.71H5.71c-1.89 0-3.43-1.54-3.43-3.43V5.71c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v14.86c0 1.89-1.54 3.43-3.43 3.43h-3.43c-1.89 0-3.43-1.54-3.43-3.43v-1.14h-6.86v6.86c0 1.89-1.54 3.43-3.43 3.43Z"/></svg>
                          <span>Board</span>
                        </button>
                        <button className="hs-view-type-btn" disabled style={{ opacity: 0.4 }}>
                          <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M7.57 24.71c-.63 0-1.14-.51-1.14-1.14v-6.86a1.14 1.14 0 1 1 2.28 0v6.86c0 .63-.51 1.14-1.14 1.14M12.14 24.71c-.63 0-1.14-.51-1.14-1.14v-16c0-.63.51-1.14 1.14-1.14h4.57a1.14 1.14 0 1 1 0 2.28h-3.43v14.86c0 .63-.51 1.14-1.14 1.14"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v20.57c0 1.89-1.54 3.43-3.43 3.43Z"/></svg>
                          <span>Report</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 1E: Right side — Create email (direct action, Import removed) */}
              <div className="hs-topbar-right">
                <button className="hs-add-contacts-btn" onClick={() => { setNewEmailName(''); setNewEmailSubject(''); setNewEmailBody(''); setNewEmailType('simple'); setShowCreateEmail(true) }}>
                  Create email
                </button>
              </div>
            </div>

            {/* === MANAGE TAB — full 6-row view template === */}
            {mktgEmailTab === 'manage' && (<>

              {/* ROW 2: TOOLBAR */}
              <div className="hs-toolbar">
                <div className="hs-toolbar-left">
                  {/* 2A: Search */}
                  <div className="hs-search-wrap">
                    <svg className="hs-search-icon" width="14" height="14" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M28.14 29.29c-.29 0-.58-.11-.81-.33l-7.58-7.58a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l7.58 7.58a1.14 1.14 0 0 1-.81 1.95"/><path d="M13.29 24.71c-6.3 0-11.43-5.13-11.43-11.43S6.98 1.86 13.29 1.86s11.43 5.13 11.43 11.43-5.13 11.43-11.43 11.43Zm0-20.57c-5.04 0-9.14 4.1-9.14 9.14s4.1 9.14 9.14 9.14 9.14-4.1 9.14-9.14-4.1-9.14-9.14-9.14"/></svg>
                    <input
                      className="hs-search-input"
                      placeholder="Search"
                      value={mktgEmailSearch}
                      onChange={(e) => { setMktgEmailSearch(e.target.value); setMktgEmailPage(1) }}
                    />
                    {mktgEmailSearch && (
                      <button className="hs-search-clear" onClick={() => { setMktgEmailSearch(''); setMktgEmailPage(1) }}>&times;</button>
                    )}
                  </div>

                  <div className="hs-tb-sep" />

                  {/* 2B: View type + settings button group */}
                  <div className="hs-viewtype-group">
                    <div className="hs-viewtype-wrap" ref={viewTypeDropdownRef}>
                      <button
                        className={`hs-tb-btn hs-viewtype-btn${viewTypeDropdownOpen ? ' active' : ''}`}
                        onClick={() => setViewTypeDropdownOpen(!viewTypeDropdownOpen)}
                      >
                        <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                        <span>Table</span>
                        <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                      </button>
                      {viewTypeDropdownOpen && (
                        <div className="hs-viewtype-panel">
                          <div className="hs-viewtype-header">View type</div>
                          <button className="hs-viewtype-option active" onClick={() => setViewTypeDropdownOpen(false)}>
                            <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                            <span>Table</span>
                          </button>
                        </div>
                      )}
                    </div>
                    <button
                      className={`hs-tb-btn hs-settings-btn${tableSettingsOpen ? ' active' : ''}`}
                      onClick={() => setTableSettingsOpen(!tableSettingsOpen)}
                      title={tableSettingsOpen ? 'Close settings' : 'Table settings'}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                    </button>
                  </div>

                  {/* 2C: Edit columns */}
                  <div className="hs-tb-sep" />
                  <button className="hs-tb-btn hs-tb-btn-bordered disabled" title="Edit columns is not available yet" disabled>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="18" rx="1"/><rect x="14" y="3" width="7" height="18" rx="1"/></svg>
                    <span>Edit columns</span>
                  </button>

                  {/* 2D: Filters */}
                  <div className="hs-tb-sep" />
                  <button className={`hs-tb-btn hs-tb-btn-bordered${quickFiltersOpen ? ' hs-tb-btn-pressed' : ''}`} title="Filters" onClick={() => setQuickFiltersOpen(!quickFiltersOpen)}>
                    <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M30.71 16.86H13.57a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28M6.71 16.86H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M10.14 20.29c-2.52 0-4.57-2.05-4.57-4.57s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M6.71 6.57H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M30.71 6.57H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M18.14 6.57H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 10C19.05 10 17 7.95 17 5.43S19.05.86 21.57.86s4.57 2.05 4.57 4.57S24.09 10 21.57 10m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M30.71 27.14H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M18.14 27.14H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 30.57C19.05 30.57 17 28.52 17 26s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29"/></svg>
                    <span>Filters</span>
                  </button>

                  {/* 2E: Export */}
                  <div className="hs-tb-sep" />
                  <button
                    className={`hs-tb-btn hs-tb-btn-bordered${mktgEmailExporting ? ' hs-tb-btn-pressed' : ''}`}
                    title="Export current view as CSV"
                    disabled={mktgEmailExporting}
                    onClick={async () => {
                      setMktgEmailExporting(true)
                      try {
                        const data = await fetchMarketingEmails({
                          status: mktgEmailFilter !== 'all' ? mktgEmailFilter : undefined,
                          search: mktgEmailSearch || undefined,
                        })
                        const headers = ['Name', 'Subject', 'Status', 'Type', 'Recipients', 'Opened', 'Clicked', 'Sent Date', 'Updated']
                        const escCsv = (v: unknown) => { const s = String(v ?? ''); return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s }
                        const rows = data.emails.map((em: any) => [
                          em.name, em.subject, em.status, em.template_type,
                          em.total_recipients, em.total_opened, em.total_clicked,
                          em.sent_at || '', em.updated_at || '',
                        ].map(escCsv).join(','))
                        const csv = [headers.join(','), ...rows].join('\n')
                        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
                        const url = URL.createObjectURL(blob)
                        const a = document.createElement('a')
                        a.href = url
                        a.download = `marketing-emails-export-${new Date().toISOString().slice(0, 10)}.csv`
                        document.body.appendChild(a)
                        a.click()
                        document.body.removeChild(a)
                        URL.revokeObjectURL(url)
                      } catch (err) {
                        console.error('Export failed:', err)
                        alert('Export failed. Please try again.')
                      } finally {
                        setMktgEmailExporting(false)
                      }
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    <span>{mktgEmailExporting ? 'Exporting...' : 'Export'}</span>
                  </button>
                </div>

                <div className="hs-toolbar-right">
                  {/* 2F: Clone view */}
                  <div className="hs-tb-sep" />
                  <button className="hs-tb-btn hs-tb-btn-bordered disabled" title="Clone view is not available yet" disabled>
                    <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M25.86 29.29H12.15c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-1.89 1.54-3.43 3.43-3.43h13.71c1.89 0 3.43 1.54 3.43 3.43v13.71c0 1.89-1.54 3.43-3.43 3.43M12.14 11c-.63 0-1.14.51-1.14 1.14v13.71c0 .63.51 1.14 1.14 1.14h13.71c.63 0 1.14-.51 1.14-1.14V12.14c0-.63-.51-1.14-1.14-1.14z"/><path d="M5.29 22.43c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43H19c1.89 0 3.43 1.54 3.43 3.43a1.14 1.14 0 1 1-2.28 0c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14V19c0 .63.51 1.14 1.14 1.14a1.14 1.14 0 1 1 0 2.28Z"/></svg>
                  </button>
                  {/* 2G: Save view */}
                  <button className="hs-tb-btn hs-tb-btn-bordered disabled" disabled title="Save view">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                  </button>
                </div>
              </div>

              {/* ROW 3: QUICK FILTERS */}
              {quickFiltersOpen && <div className="hs-quick-filters">
                {/* Status filter — cycle toggle */}
                <button
                  className={`hs-qf-btn hs-qf-trigger${mktgEmailFilter !== 'all' ? ' has-value' : ''}`}
                  onClick={() => {
                    const vals = ['all', 'draft', 'scheduled', 'sent', 'archived']
                    const idx = vals.indexOf(mktgEmailFilter)
                    setMktgEmailFilter(vals[(idx + 1) % vals.length])
                    setMktgEmailPage(1)
                  }}
                >
                  <span>{mktgEmailFilter !== 'all' ? mktgEmailFilter.charAt(0).toUpperCase() + mktgEmailFilter.slice(1) : 'Status'}</span>
                  {mktgEmailFilter !== 'all' && (
                    <span className="hs-qf-clear" onClick={(e) => { e.stopPropagation(); setMktgEmailFilter('all'); setMktgEmailPage(1) }}>&times;</span>
                  )}
                  {mktgEmailFilter === 'all' && <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>}
                </button>

                {/* Sort by */}
                <button
                  className={`hs-qf-btn hs-qf-trigger${mktgEmailSort.field !== 'updated_at' ? ' has-value' : ''}`}
                  onClick={() => {
                    const fields = ['updated_at', 'name', 'status', 'total_recipients', 'created_at']
                    const idx = fields.indexOf(mktgEmailSort.field)
                    const next = fields[(idx + 1) % fields.length]
                    setMktgEmailSort({ field: next, order: next === 'name' ? 'ASC' : 'DESC' })
                    setMktgEmailPage(1)
                  }}
                >
                  <span>Sort: {{ updated_at: 'Updated', name: 'Name', status: 'Status', total_recipients: 'Recipients', created_at: 'Created' }[mktgEmailSort.field] || 'Updated'}</span>
                  <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                </button>

                <div className="hs-qf-sep" />

                {/* +More */}
                <button className="hs-qf-btn hs-qf-more" disabled style={{ opacity: 0.5 }}>
                  <svg width="10" height="10" viewBox="0 0 32 32" fill="currentColor"><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V3a1.14 1.14 0 1 1 2.28 0v25.14c0 .63-.51 1.14-1.14 1.14Z"/><path d="M28.14 16.71H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28"/></svg>
                  <span>More</span>
                </button>
              </div>}

              {/* ROW 5: CONTENT (table area) */}
              <div className={`hs-content${tableSettingsOpen ? ' with-settings' : ''}`}>
                <div className="hs-table-area">
                  {/* ROW 4: SELECTION BAR */}
                  {mktgEmailSelectedRows.size > 0 && (
                    <div className="hs-selection-bar" data-marker="mkt-honest-v1">
                      <span className="hs-sel-count">{mktgEmailSelectedRows.size} selected</span>
                      <div className="hs-sel-sep" />
                      <button className="hs-sel-btn" style={{ color: '#dc2626' }} onClick={() => {
                        if (confirm(`Delete ${mktgEmailSelectedRows.size} email(s)?`)) {
                          Promise.all([...mktgEmailSelectedRows].map(id => deleteMarketingEmail(id))).then(() => {
                            setMktgEmails(prev => prev.filter((em: any) => !mktgEmailSelectedRows.has(Number(em.id))))
                            setMktgEmailsTotal(prev => prev - mktgEmailSelectedRows.size)
                            setMktgEmailSelectedRows(new Set())
                          }).catch(() => {})
                        }
                      }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                        Delete
                      </button>
                      <button className="hs-sel-close" onClick={() => setMktgEmailSelectedRows(new Set())} title="Deselect all">&times;</button>
                    </div>
                  )}

                  <div className="hs-table-scroll">
                    <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                      <colgroup>
                        <col style={{ width: 40 }} />
                        <col style={{ width: 220 }} />
                        <col style={{ width: 100 }} />
                        <col style={{ width: 220 }} />
                        <col style={{ width: 90 }} />
                        <col style={{ width: 90 }} />
                        <col style={{ width: 110 }} />
                        <col style={{ width: 60 }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th className="hs-th-check">
                            <input
                              type="checkbox"
                              checked={(() => {
                                const pageEmails = mktgEmails.slice((mktgEmailPage - 1) * tablePageSize, mktgEmailPage * tablePageSize)
                                return pageEmails.length > 0 && pageEmails.every((em: any) => mktgEmailSelectedRows.has(Number(em.id)))
                              })()}
                              onChange={() => {
                                const pageEmails = mktgEmails.slice((mktgEmailPage - 1) * tablePageSize, mktgEmailPage * tablePageSize)
                                const allSelected = pageEmails.length > 0 && pageEmails.every((em: any) => mktgEmailSelectedRows.has(Number(em.id)))
                                if (allSelected) {
                                  setMktgEmailSelectedRows(new Set())
                                } else {
                                  setMktgEmailSelectedRows(new Set(pageEmails.map((em: any) => Number(em.id))))
                                }
                              }}
                            />
                          </th>
                          {[
                            { key: 'name', label: 'Name', sortField: 'name', frozen: true },
                            { key: 'status', label: 'Status', sortField: 'status', frozen: false },
                            { key: 'subject', label: 'Subject', sortField: 'subject', frozen: false },
                            { key: 'total_opened', label: 'Open Rate', sortField: 'total_opened', frozen: false },
                            { key: 'total_clicked', label: 'Click Rate', sortField: 'total_clicked', frozen: false },
                            { key: 'updated_at', label: 'Sent Date', sortField: 'updated_at', frozen: false },
                            { key: 'actions', label: 'Actions', sortField: '', frozen: false },
                          ].map(col => (
                            <th
                              key={col.key}
                              className={`hs-th-resizable${col.sortField ? ' hs-th-sortable' : ''}${col.frozen ? ' hs-th-frozen' : ''}`}
                              style={col.frozen ? { position: 'sticky', left: 40, top: 0, zIndex: 15, background: '#f8fafc' } : undefined}
                              onClick={col.sortField ? () => { setMktgEmailSort(prev => ({ field: col.sortField, order: prev.field === col.sortField ? (prev.order === 'ASC' ? 'DESC' : 'ASC') : (col.key === 'name' || col.key === 'status' || col.key === 'subject' ? 'ASC' : 'DESC') })); setMktgEmailPage(1) } : undefined}
                              onContextMenu={col.sortField ? (e) => { e.preventDefault(); setColContextMenu({ field: col.sortField, x: e.clientX, y: e.clientY }) } : undefined}
                            >
                              <span className="hs-th-content">
                                {col.label}
                                {col.sortField && mktgEmailSort.field === col.sortField && <span className="hs-sort-arrow">{mktgEmailSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                                {col.sortField && <span className="hs-th-hover-sort"><svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M29.38 21.96a1.147 1.147 0 0 0-1.617 0l-3.85 3.852V5.714a1.143 1.143 0 0 0-2.286 0v20.099l-3.853-3.852a1.144 1.144 0 0 0-1.615 1.617l5.803 5.802a1.143 1.143 0 0 0 1.616 0l5.802-5.802a1.144 1.144 0 0 0 0-1.617M15.84 8.422 10.039 2.62a1.145 1.145 0 0 0-1.616 0L2.62 8.422a1.144 1.144 0 0 0 1.617 1.617l3.85-3.851v20.098a1.143 1.143 0 0 0 2.286 0V6.187l3.853 3.852a1.14 1.14 0 0 0 1.616-.001 1.14 1.14 0 0 0-.001-1.616"/></svg></span>}
                              </span>
                              {col.sortField && <button className="hs-th-kebab" onClick={(e) => { e.stopPropagation(); setColContextMenu({ field: col.sortField, x: e.clientX, y: e.clientY }) }}>
                                <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><circle cx="16" cy="4.14" r="2.29"/><circle cx="16" cy="15.57" r="2.29"/><circle cx="16" cy="27" r="2.29"/></svg>
                              </button>}
                              <div className="hs-col-resize-handle" onClick={(e) => e.stopPropagation()} />
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {mktgEmailsLoading && mktgEmails.length === 0 ? (
                          <tr><td colSpan={8} className="hs-loading-cell"><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}><DotLottieReact src="/loading.lottie" loop autoplay style={{ width: 80, height: 80 }} /><span style={{ fontSize: '0.82rem', color: '#7c98b6', marginTop: 8 }}>Loading emails...</span></div></td></tr>
                        ) : mktgEmails.length === 0 ? (
                          <tr><td colSpan={8} className="hs-empty-cell">
                            <div style={{ padding: '60px 40px', textAlign: 'center' }}>
                              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" style={{ marginBottom: 12 }}><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                              <div style={{ color: '#64748b', fontSize: '0.88rem', fontWeight: 600, marginBottom: 4 }}>No marketing emails yet</div>
                              <div style={{ color: '#94a3b8', fontSize: '0.78rem' }}>Create your first email to get started.</div>
                            </div>
                          </td></tr>
                        ) : (() => {
                          // Client-side sort
                          const sorted = [...mktgEmails].sort((a: any, b: any) => {
                            const field = mktgEmailSort.field
                            const aVal = a[field] ?? ''
                            const bVal = b[field] ?? ''
                            const cmp = typeof aVal === 'number' && typeof bVal === 'number' ? aVal - bVal : String(aVal).localeCompare(String(bVal))
                            return mktgEmailSort.order === 'ASC' ? cmp : -cmp
                          })
                          // Client-side pagination
                          const pageEmails = sorted.slice((mktgEmailPage - 1) * tablePageSize, mktgEmailPage * tablePageSize)
                          return pageEmails.map((em: any) => {
                            const status = String(em.status || 'draft')
                            const statusBg = status === 'sent' ? '#e8f5e9' : status === 'scheduled' ? '#fff3e0' : status === 'archived' ? '#f1f5f9' : '#f1f5f9'
                            const statusColor = status === 'sent' ? '#2e7d32' : status === 'scheduled' ? '#e65100' : status === 'archived' ? '#94a3b8' : '#64748b'
                            const isSelected = mktgEmailSelectedRows.has(Number(em.id))
                            return (
                              <tr key={String(em.id)} className={`hs-row${isSelected ? ' selected' : ''}`}>
                                <td className="hs-td-check">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => {
                                      setMktgEmailSelectedRows(prev => {
                                        const next = new Set(prev)
                                        next.has(Number(em.id)) ? next.delete(Number(em.id)) : next.add(Number(em.id))
                                        return next
                                      })
                                    }}
                                  />
                                </td>
                                <td className="hs-td-name" style={{ position: 'sticky', left: 40, zIndex: 2, background: isSelected ? '#eff6ff' : '#fff' }}>
                                  <div className="hs-name-cell">
                                    <div className="hs-avatar" style={{ background: status === 'sent' ? '#22c55e' : status === 'scheduled' ? '#f59e0b' : '#94a3b8' }}>
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                                    </div>
                                    <span className="hs-name-link">{String(em.name || '')}</span>
                                  </div>
                                </td>
                                <td>
                                  <span className="status-chip" style={{ background: statusBg, color: statusColor, fontSize: '0.7rem', fontWeight: 700, padding: '0 7px', borderRadius: 6, minHeight: 20, display: 'inline-flex', alignItems: 'center' }}>
                                    {status.charAt(0).toUpperCase() + status.slice(1)}
                                  </span>
                                </td>
                                <td style={{ color: '#516f90', fontSize: '0.8rem' }}>{String(em.subject || '--')}</td>
                                <td style={{ color: '#33475b' }}><span title="No tracking connected yet">—</span></td>
                                <td style={{ color: '#33475b' }}><span title="No tracking connected yet">—</span></td>
                                <td style={{ color: '#516f90', fontSize: '0.75rem' }}>{em.sent_at ? String(em.sent_at).slice(0, 10) : em.updated_at ? String(em.updated_at).slice(0, 10) : '--'}</td>
                                <td>
                                  <button style={{ background: 'none', border: 'none', color: '#d32f2f', cursor: 'pointer', fontSize: '0.72rem' }} onClick={() => { if (confirm('Delete this email?')) deleteMarketingEmail(Number(em.id)).then(() => { setMktgEmails(prev => prev.filter((e2: any) => Number(e2.id) !== Number(em.id))); setMktgEmailsTotal(prev => prev - 1) }) }}>Delete</button>
                                </td>
                              </tr>
                            )
                          })
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Settings panel */}
                {tableSettingsOpen && (
                  <div className="hs-settings-panel">
                    <div className="hs-settings-header">
                      <span className="hs-settings-title">Table settings</span>
                      <button className="hs-settings-close" onClick={() => setTableSettingsOpen(false)}>
                        <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                      </button>
                    </div>
                    <div className="hs-settings-body">
                      <div className="hs-settings-group">
                        <label className="hs-settings-label">
                          Pagination
                          <span className="hs-info-icon" title="Number of rows shown per page">
                            <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg>
                          </span>
                        </label>
                        {([100, 200, 500] as const).map(size => (
                          <label className="hs-settings-radio" key={size}>
                            <input type="radio" name="mktgPageSize" checked={tablePageSize === size} onChange={() => { setTablePageSize(size); setMktgEmailPage(1) }} />
                            <span className="hs-radio-indicator" />
                            <span>{size} per page</span>
                          </label>
                        ))}
                      </div>
                      <hr className="hs-settings-divider" />
                      <div className="hs-settings-group">
                        <label className="hs-settings-label">
                          Row height
                          <span className="hs-info-icon" title="Adjust the vertical spacing of table rows">
                            <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg>
                          </span>
                        </label>
                        {([
                          { value: 'small' as const, label: 'Compact' },
                          { value: 'medium' as const, label: 'Default' },
                          { value: 'large' as const, label: 'Comfortable' },
                        ]).map(opt => (
                          <label className="hs-settings-radio" key={opt.value}>
                            <input type="radio" name="mktgRowHeight" checked={tableRowHeight === opt.value} onChange={() => setTableRowHeight(opt.value)} />
                            <span className="hs-radio-indicator" />
                            <span>{opt.label}</span>
                          </label>
                        ))}
                      </div>
                      <hr className="hs-settings-divider" />
                      <div className="hs-settings-group">
                        <div className="hs-settings-toggle-row">
                          <label className="hs-settings-label">
                            Zebra striping
                            <span className="hs-info-icon" title="Alternate row background colors for easier reading">
                              <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg>
                            </span>
                          </label>
                          <button
                            className={`hs-toggle${tableZebraStriping ? ' on' : ''}`}
                            onClick={() => setTableZebraStriping(!tableZebraStriping)}
                            role="switch"
                            aria-checked={tableZebraStriping}
                          >
                            <span className="hs-toggle-track">
                              <span className="hs-toggle-on">ON</span>
                              <span className="hs-toggle-off">OFF</span>
                              <span className="hs-toggle-handle" />
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* ROW 6: PAGINATION */}
              {(() => {
                const totalPages = Math.max(1, Math.ceil(mktgEmailsTotal / tablePageSize))
                return (
                  <div className="hs-pagination">
                    <nav className="hs-paginator" aria-label="Pagination">
                      <button
                        className="hs-paginator-btn"
                        disabled={mktgEmailPage <= 1}
                        onClick={() => setMktgEmailPage(p => p - 1)}
                        aria-label="Previous page"
                      >
                        <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95"/></svg>
                        <span>Prev</span>
                      </button>
                      {(() => {
                        if (totalPages <= 7) {
                          const pages: number[] = []
                          for (let i = 1; i <= totalPages; i++) pages.push(i)
                          return pages.map(p => (
                            <button key={p} className={`hs-paginator-num${p === mktgEmailPage ? ' active' : ''}`} onClick={() => setMktgEmailPage(p)}>{p}</button>
                          ))
                        }
                        const items: React.ReactNode[] = []
                        const addPage = (p: number) => items.push(
                          <button key={p} className={`hs-paginator-num${p === mktgEmailPage ? ' active' : ''}`} onClick={() => setMktgEmailPage(p)}>{p}</button>
                        )
                        const addEllipsis = (key: string) => items.push(
                          <span key={key} className="hs-paginator-ellipsis">...</span>
                        )
                        const shown = new Set<number>()
                        for (let i = 1; i <= Math.min(4, totalPages); i++) shown.add(i)
                        shown.add(totalPages)
                        for (let i = Math.max(1, mktgEmailPage - 1); i <= Math.min(totalPages, mktgEmailPage + 1); i++) shown.add(i)
                        const sorted = [...shown].sort((a, b) => a - b)
                        for (let i = 0; i < sorted.length; i++) {
                          if (i > 0 && sorted[i] - sorted[i - 1] > 1) addEllipsis(`e${i}`)
                          addPage(sorted[i])
                        }
                        return items
                      })()}
                      <button
                        className="hs-paginator-btn"
                        disabled={mktgEmailPage >= totalPages}
                        onClick={() => setMktgEmailPage(p => p + 1)}
                        aria-label="Next page"
                      >
                        <span>Next</span>
                        <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M11 27.71c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62l9.48-9.48-9.48-9.47a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0L22.1 15.48c.45.45.45 1.17 0 1.62L11.81 27.39c-.22.22-.52.33-.81.33Z"/></svg>
                      </button>
                    </nav>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', marginLeft: 'auto' }}><select value={tablePageSize} onChange={e => { setTablePageSize(Number(e.target.value) as any); setMktgEmailPage(1) }} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #e2e8f0', fontSize: '0.82rem' }}><option value={100}>100</option><option value={200}>200</option><option value={500}>500</option></select><span>per page</span></div>
                  </div>
                )
              })()}
            </>)}

            {/* === ANALYZE TAB — preserved as-is === */}
            {mktgEmailTab === 'analyze' && (
              <div className="hs-content" style={{ padding: 20 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 20 }}>
                  {[{ label: 'SENT', value: mktgAnalytics.total_sent || 0 }, { label: 'OPEN RATE', value: mktgAnalytics.total_sent > 0 ? ((mktgAnalytics.total_opened / mktgAnalytics.total_sent) * 100).toFixed(1) + '%' : '0%' }, { label: 'CLICK RATE', value: mktgAnalytics.total_sent > 0 ? ((mktgAnalytics.total_clicked / mktgAnalytics.total_sent) * 100).toFixed(1) + '%' : '0%' }, { label: 'DELIVERED', value: mktgAnalytics.total_delivered || 0 }].map(kpi => (
                    <div key={kpi.label} className="kpi" style={{ textAlign: 'center' }}><span style={{ display: 'block', fontSize: '0.68rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{kpi.label}</span><strong style={{ display: 'block', fontSize: '1.1rem', marginTop: 4 }}>{kpi.value}</strong></div>
                  ))}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16 }}>
                  {[{ label: 'BOUNCED', value: mktgAnalytics.total_bounced || 0 }, { label: 'UNSUBSCRIBED', value: mktgAnalytics.total_unsubscribed || 0 }, { label: 'SPAM REPORTS', value: mktgAnalytics.total_spam || 0 }, { label: 'TOTAL EMAILS', value: mktgAnalytics.total_emails || 0 }].map(kpi => (
                    <div key={kpi.label} className="kpi" style={{ textAlign: 'center' }}><span style={{ display: 'block', fontSize: '0.68rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{kpi.label}</span><strong style={{ display: 'block', fontSize: '1.1rem', marginTop: 4 }}>{kpi.value}</strong></div>
                  ))}
                </div>
              </div>
            )}

            {/* Create email panel */}
            {showCreateEmail && (<>
              <div className="create-panel-overlay" onClick={() => setShowCreateEmail(false)} />
              <div className="create-panel" onClick={(e) => e.stopPropagation()}>
                <div className="create-panel-header">
                  <h2>Create Marketing Email</h2>
                  <button className="create-panel-close" onClick={() => setShowCreateEmail(false)} aria-label="Close">
                    <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                  </button>
                </div>
                <div className="create-panel-body">
                  <div className="create-panel-field">
                    <label>Email Name *</label>
                    <input value={newEmailName} onChange={e => setNewEmailName(e.target.value)} placeholder="e.g. Spring Newsletter 2026" />
                  </div>
                  <div className="create-panel-field">
                    <label>Subject Line</label>
                    <input value={newEmailSubject} onChange={e => setNewEmailSubject(e.target.value)} placeholder="e.g. Exciting news!" />
                  </div>
                  <div className="create-panel-field">
                    <label>Template Type</label>
                    <select value={newEmailType} onChange={e => setNewEmailType(e.target.value)}>
                      <option value="simple">Simple</option>
                      <option value="newsletter">Newsletter</option>
                      <option value="promotion">Promotion</option>
                      <option value="custom">Custom HTML</option>
                    </select>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div className="create-panel-field">
                      <label>From Name</label>
                      <input value={newEmailFrom} onChange={e => setNewEmailFrom(e.target.value)} />
                    </div>
                    <div className="create-panel-field">
                      <label>From Email</label>
                      <input value={newEmailFromAddr} onChange={e => setNewEmailFromAddr(e.target.value)} />
                    </div>
                  </div>
                  <div className="create-panel-field">
                    <label>Email Body</label>
                    <div style={{ border: '1px solid #cbd6e2', borderRadius: 4, overflow: 'hidden' }}>
                      <textarea rows={8} value={newEmailBody} onChange={e => setNewEmailBody(e.target.value)} placeholder="Write your email content here..." style={{ border: 'none', borderRadius: 0, resize: 'vertical', width: '100%', padding: '8px 10px', fontSize: '0.88rem', color: '#1e293b', fontFamily: 'inherit' }} />
                    </div>
                  </div>
                </div>
                <div className="create-panel-footer">
                  <button className="cp-btn-primary" disabled={!newEmailName.trim()} onClick={() => { createMarketingEmail({ name: newEmailName, subject: newEmailSubject, from_name: newEmailFrom, from_email: newEmailFromAddr, template_type: newEmailType, html_body: newEmailBody, status: 'draft' }).then(() => { setShowCreateEmail(false); { const opts: { status?: string; search?: string } = {}; if (mktgEmailFilter !== 'all') opts.status = mktgEmailFilter; if (mktgEmailSearch) opts.search = mktgEmailSearch; fetchMarketingEmails(opts).then(res => { setMktgEmails(res.emails); setMktgEmailsTotal(res.total) }).catch(() => {}) } }) }}>Create email</button>
                  <button className="cp-btn-secondary" onClick={() => setShowCreateEmail(false)}>Cancel</button>
                </div>
              </div>
            </>)}
          </div>
        )}

        {/* ========== Ads View ========== */}
        {activeView === 'ads' && (() => {
          /* Derived data for Ads manage tab */
          const adsSearchLower = adsSearch.toLowerCase()
          const filteredAds = adCampaignsData
            .filter((c: Record<string, unknown>) => !adsSearchLower || String(c.name || '').toLowerCase().includes(adsSearchLower))
            .sort((a: Record<string, unknown>, b: Record<string, unknown>) => {
              const f = adsSort.field
              const av = a[f], bv = b[f]
              const cmp = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av || '').localeCompare(String(bv || ''))
              return adsSort.order === 'ASC' ? cmp : -cmp
            })
          const adsPageCount = Math.max(1, Math.ceil(adCampaignsTotal / tablePageSize))
          const adsSortFields: Record<string, string> = { created_at: 'Created', name: 'Name', network: 'Platform', status: 'Status', budget: 'Budget', actual_spend: 'Spend' }
          const networkColor = (n: string) => n === 'google' ? '#4285F4' : n === 'meta' ? '#1877F2' : '#0A66C2'
          const statusColor = (s: string) => s === 'active' ? { bg: '#e8f5e9', fg: '#2e7d32' } : s === 'paused' ? { bg: '#fff3e0', fg: '#e65100' } : s === 'completed' ? { bg: '#e3f2fd', fg: '#1565c0' } : { bg: '#f1f5f9', fg: '#64748b' }
          const toggleAdsSort = (field: string) => setAdsSort(prev => prev.field === field ? { field, order: prev.order === 'ASC' ? 'DESC' : 'ASC' } : { field, order: field === 'name' ? 'ASC' : 'DESC' })
          const toggleAllAdsRows = () => { if (adsSelectedRows.size === filteredAds.length) { setAdsSelectedRows(new Set()) } else { setAdsSelectedRows(new Set(filteredAds.map((c: Record<string, unknown>) => Number(c.id)))) } }
          const toggleAdsRow = (id: number) => { setAdsSelectedRows(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next }) }
          const adsCols = [
            { key: 'name', label: 'Campaign Name', width: 240, frozen: true, sortField: 'name' },
            { key: 'network', label: 'Platform', width: 120, sortField: 'network' },
            { key: 'status', label: 'Status', width: 110, sortField: 'status' },
            { key: 'budget', label: 'Budget', width: 110, sortField: 'budget' },
            { key: 'actual_spend', label: 'Spend', width: 110, sortField: 'actual_spend' },
            { key: 'impressions', label: 'Impressions', width: 120, sortField: 'impressions' },
            { key: 'clicks', label: 'Clicks', width: 100, sortField: 'clicks' },
            { key: 'leads_generated', label: 'Conversions', width: 110, sortField: 'leads_generated' },
            { key: 'actions', label: '', width: 70, sortField: '' },
          ]
          return (
          <div className="hs-contacts">
            {/* ROW 1: TOPBAR */}
            <div className="hs-topbar">
              <div className="hs-topbar-left">
                {/* Object switcher */}
                <div className="hs-obj-switcher-wrap" ref={objSwitcherRef}>
                  <button className={`hs-obj-switcher${objSwitcherOpen ? ' open' : ''}`} onClick={() => { setObjSwitcherOpen(!objSwitcherOpen); setObjSwitcherSearch('') }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/></svg>
                    <span>Ads</span>
                    <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                  </button>
                  {objSwitcherOpen && (
                    <div className="hs-obj-panel">
                      <div className="hs-obj-search-wrap">
                        <svg className="hs-obj-search-icon" width="13" height="13" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                        <input className="hs-obj-search" placeholder="Search" value={objSwitcherSearch} onChange={(e) => setObjSwitcherSearch(e.target.value)} autoFocus />
                      </div>
                      <ul className="hs-obj-list" role="listbox">
                        {[
                          { label: 'Email', view: 'marketing-email' as ViewMode },
                          { label: 'Ads', view: 'ads' as ViewMode },
                          { label: 'Social', view: 'social' as ViewMode },
                          { label: 'Forms', view: 'forms' as ViewMode },
                          { label: 'Campaigns', view: 'campaigns' as ViewMode },
                          { label: 'Landing Pages', view: 'landing-pages' as ViewMode },
                        ].filter(item => !objSwitcherSearch || item.label.toLowerCase().includes(objSwitcherSearch.toLowerCase()))
                          .map(item => (
                            <li key={item.view} className={`hs-obj-item${item.view === activeView ? ' active' : ''}`} role="option" aria-selected={item.view === activeView} onClick={() => { setActiveView(item.view); setObjSwitcherOpen(false); setObjSwitcherSearch('') }}>
                              {item.label}
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}
                </div>

                <div className="hs-topbar-sep" />

                {/* View tabs */}
                <div className="hs-view-tabs">
                  <div className={`hs-view-tab${adsTab === 'manage' ? ' active' : ''}`} onClick={() => setAdsTab('manage')}>
                    <span className="hs-tab-label">Manage</span>
                    {adsTab === 'manage' && adCampaignsTotal > 0 && <span className="hs-tab-count">{adCampaignsTotal.toLocaleString()}</span>}
                    {adsTab === 'manage' && (
                      <span className="hs-tab-kebab" onClick={(e) => { e.stopPropagation(); setTabEditOpen(!tabEditOpen) }}>
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>
                      </span>
                    )}
                    {adsTab === 'manage' && tabEditOpen && (
                      <div className="hs-tab-edit-dropdown">
                        <button className="hs-tab-edit-item" disabled>Rename</button>
                        <button className="hs-tab-edit-item" onClick={() => setTabEditOpen(false)}>Clone</button>
                        <button className="hs-tab-edit-item" disabled>Manage Sharing</button>
                      </div>
                    )}
                  </div>
                  <div className={`hs-view-tab${adsTab === 'analyze' ? ' active' : ''}`} onClick={() => setAdsTab('analyze')}>
                    <span className="hs-tab-label">Analyze</span>
                    {adsTab === 'analyze' && (
                      <span className="hs-tab-kebab" onClick={(e) => { e.stopPropagation() }}>
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>
                      </span>
                    )}
                    <span className="hs-tab-x" onClick={(e) => { e.stopPropagation(); setAdsTab('manage') }}>&times;</span>
                  </div>

                  {/* Quick-add view button */}
                  <div className="hs-topbar-sep" />
                  <div className="hs-view-add-wrap" ref={viewAddMenuRef} style={{ position: 'relative', display: 'inline-flex', alignSelf: 'center' }}>
                    <button className={`hs-quick-add-btn${viewAddMenuOpen ? ' active' : ''}`} title="Create or add view" type="button" aria-label="Create or add view" onClick={() => { setViewAddMenuOpen(!viewAddMenuOpen); setViewAddMenuMode('menu') }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                    </button>
                    {viewAddMenuOpen && viewAddMenuMode === 'menu' && (
                      <div className="hs-view-add-dropdown">
                        <button className="hs-view-add-item" onClick={() => setViewAddMenuMode('type')}>Create new view</button>
                        <button className="hs-view-add-item" onClick={() => { setViewAddMenuOpen(false); setViewAddMenuMode('menu') }}>Add view</button>
                      </div>
                    )}
                    {viewAddMenuOpen && viewAddMenuMode === 'type' && (
                      <div className="hs-view-add-dropdown hs-view-type-picker">
                        <button className="hs-view-type-btn" onClick={() => { setViewAddMenuOpen(false); setViewAddMenuMode('menu') }}>
                          <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                          <span>Table</span>
                        </button>
                        <button className="hs-view-type-btn" disabled style={{ opacity: 0.4 }}>
                          <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M11.43 11.43h-8a1.14 1.14 0 1 1 0-2.28h8a1.14 1.14 0 1 1 0 2.28M28.57 11.43h-8a1.14 1.14 0 1 1 0-2.28h8a1.14 1.14 0 1 1 0 2.28"/><path d="M9.14 29.71H5.71c-1.89 0-3.43-1.54-3.43-3.43V5.71c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v14.86c0 1.89-1.54 3.43-3.43 3.43h-3.43c-1.89 0-3.43-1.54-3.43-3.43v-1.14h-6.86v6.86c0 1.89-1.54 3.43-3.43 3.43Z"/></svg>
                          <span>Board</span>
                        </button>
                        <button className="hs-view-type-btn" disabled style={{ opacity: 0.4 }}>
                          <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M7.57 24.71c-.63 0-1.14-.51-1.14-1.14v-6.86a1.14 1.14 0 1 1 2.28 0v6.86c0 .63-.51 1.14-1.14 1.14M12.14 24.71c-.63 0-1.14-.51-1.14-1.14v-16c0-.63.51-1.14 1.14-1.14h4.57a1.14 1.14 0 1 1 0 2.28h-3.43v14.86c0 .63-.51 1.14-1.14 1.14"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v20.57c0 1.89-1.54 3.43-3.43 3.43Z"/></svg>
                          <span>Report</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="hs-topbar-right">
                <div className="hs-add-btn-wrap" ref={adsAddBtnRef}>
                  <button className="hs-add-contacts-btn" onClick={() => setAdsAddBtnOpen(!adsAddBtnOpen)}>
                    Create campaign
                    <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                  </button>
                  {adsAddBtnOpen && (
                    <div className="hs-add-btn-dropdown">
                      <button className="hs-add-btn-item" onClick={() => { setAdsAddBtnOpen(false); setNewAdName(''); setNewAdNetwork('google'); setNewAdBudget(''); setNewAdObjective('traffic'); setShowCreateAd(true) }}>Create new</button>
                      <button className="hs-add-btn-item" onClick={() => { setAdsAddBtnOpen(false); setNewAdAcctNetwork('google'); setNewAdAcctName(''); setNewAdAcctId(''); setShowConnectAd(true) }}>Add account (manual)</button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ROW 2: TOOLBAR (only on Manage tab) */}
            {adsTab === 'manage' && (
            <div className="hs-toolbar">
              <div className="hs-toolbar-left">
                {/* Search */}
                <div className="hs-search-wrap">
                  <svg className="hs-search-icon" width="14" height="14" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M28.14 29.29c-.29 0-.58-.11-.81-.33l-7.58-7.58a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l7.58 7.58a1.14 1.14 0 0 1-.81 1.95"/><path d="M13.29 24.71c-6.3 0-11.43-5.13-11.43-11.43S6.98 1.86 13.29 1.86s11.43 5.13 11.43 11.43-5.13 11.43-11.43 11.43Zm0-20.57c-5.04 0-9.14 4.1-9.14 9.14s4.1 9.14 9.14 9.14 9.14-4.1 9.14-9.14-4.1-9.14-9.14-9.14"/></svg>
                  <input className="hs-search-input" placeholder="Search" value={adsSearch} onChange={(e) => { setAdsSearch(e.target.value); setAdsPage(1) }} />
                  {adsSearch && <button className="hs-search-clear" onClick={() => { setAdsSearch(''); setAdsPage(1) }}>&times;</button>}
                </div>

                <div className="hs-tb-sep" />

                {/* View type + settings gear */}
                <div className="hs-viewtype-group">
                  <div className="hs-viewtype-wrap" ref={viewTypeDropdownRef}>
                    <button className={`hs-tb-btn hs-viewtype-btn${viewTypeDropdownOpen ? ' active' : ''}`} onClick={() => setViewTypeDropdownOpen(!viewTypeDropdownOpen)}>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                      <span>Table</span>
                      <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                    </button>
                    {viewTypeDropdownOpen && (
                      <div className="hs-viewtype-panel">
                        <div className="hs-viewtype-header">View type</div>
                        <button className="hs-viewtype-option active" onClick={() => setViewTypeDropdownOpen(false)}>
                          <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                          <span>Table</span>
                        </button>
                      </div>
                    )}
                  </div>
                  <button className={`hs-tb-btn hs-settings-btn${tableSettingsOpen ? ' active' : ''}`} onClick={() => setTableSettingsOpen(!tableSettingsOpen)} title={tableSettingsOpen ? 'Close settings' : 'Table settings'}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                  </button>
                </div>

                <div className="hs-tb-sep" />

                {/* Edit columns */}
                <button className="hs-tb-btn hs-tb-btn-bordered disabled" title="Edit columns is not available yet" disabled>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="18" rx="1"/><rect x="14" y="3" width="7" height="18" rx="1"/></svg>
                  <span>Edit columns</span>
                </button>

                <div className="hs-tb-sep" />

                {/* Filters toggle */}
                <button className={`hs-tb-btn hs-tb-btn-bordered${quickFiltersOpen ? ' hs-tb-btn-pressed' : ''}`} title="Filters" onClick={() => setQuickFiltersOpen(!quickFiltersOpen)}>
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M30.71 16.86H13.57a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28M6.71 16.86H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M10.14 20.29c-2.52 0-4.57-2.05-4.57-4.57s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M6.71 6.57H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M30.71 6.57H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M18.14 6.57H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 10C19.05 10 17 7.95 17 5.43S19.05.86 21.57.86s4.57 2.05 4.57 4.57S24.09 10 21.57 10m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M30.71 27.14H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M18.14 27.14H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 30.57C19.05 30.57 17 28.52 17 26s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29"/></svg>
                  <span>Filters</span>
                </button>

                <div className="hs-tb-sep" />

                {/* Export */}
                <button className={`hs-tb-btn hs-tb-btn-bordered${adsExporting ? ' hs-tb-btn-pressed' : ''}`} title="Export current view as CSV" disabled={adsExporting} onClick={async () => {
                  setAdsExporting(true)
                  try {
                    const data = await fetchAdCampaigns({ page: 1, pageSize: 10000 })
                    const headers = ['Campaign Name','Platform','Status','Budget','Spend','Impressions','Clicks','Conversions']
                    const escCsv = (v: unknown) => { const s = String(v ?? ''); return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s }
                    const rows = data.campaigns.map((c: Record<string, unknown>) => [c.name, c.network, c.status, c.budget, c.actual_spend, c.impressions, c.clicks, c.leads_generated].map(escCsv).join(','))
                    const csv = [headers.join(','), ...rows].join('\n')
                    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
                    const url = URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = `ads-export-${new Date().toISOString().slice(0, 10)}.csv`
                    document.body.appendChild(a)
                    a.click()
                    document.body.removeChild(a)
                    URL.revokeObjectURL(url)
                  } catch (err) { console.error('Export failed:', err); alert('Export failed. Please try again.') }
                  finally { setAdsExporting(false) }
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  <span>{adsExporting ? 'Exporting...' : 'Export'}</span>
                </button>
              </div>

              <div className="hs-toolbar-right">
                <div className="hs-tb-sep" />
                {/* Clone view */}
                <button className="hs-tb-btn hs-tb-btn-bordered disabled" title="Clone view is not available yet" disabled>
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M25.86 29.29H12.15c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-1.89 1.54-3.43 3.43-3.43h13.71c1.89 0 3.43 1.54 3.43 3.43v13.71c0 1.89-1.54 3.43-3.43 3.43M12.14 11c-.63 0-1.14.51-1.14 1.14v13.71c0 .63.51 1.14 1.14 1.14h13.71c.63 0 1.14-.51 1.14-1.14V12.14c0-.63-.51-1.14-1.14-1.14z"/><path d="M5.29 22.43c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43H19c1.89 0 3.43 1.54 3.43 3.43a1.14 1.14 0 1 1-2.28 0c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14V19c0 .63.51 1.14 1.14 1.14a1.14 1.14 0 1 1 0 2.28Z"/></svg>
                </button>
                {/* Save view */}
                <button className="hs-tb-btn hs-tb-btn-bordered disabled" disabled title="Save view">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                </button>
              </div>
            </div>
            )}

            {/* ROW 3: QUICK FILTERS (only on Manage tab) */}
            {adsTab === 'manage' && quickFiltersOpen && (
            <div className="hs-quick-filters">
              {/* Platform filter */}
              <button className={`hs-qf-btn hs-qf-trigger${adsPlatformFilter ? ' has-value' : ''}`} onClick={() => {
                const vals = ['', 'google', 'meta', 'linkedin']
                const idx = vals.indexOf(adsPlatformFilter)
                setAdsPlatformFilter(vals[(idx + 1) % vals.length])
                setAdsPage(1)
              }}>
                <span>{adsPlatformFilter ? (adsPlatformFilter.charAt(0).toUpperCase() + adsPlatformFilter.slice(1)) : 'Platform'}</span>
                {adsPlatformFilter && <span className="hs-qf-clear" onClick={(e) => { e.stopPropagation(); setAdsPlatformFilter(''); setAdsPage(1) }}>&times;</span>}
                {!adsPlatformFilter && <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>}
              </button>

              {/* Status filter */}
              <button className={`hs-qf-btn hs-qf-trigger${adsStatusFilter ? ' has-value' : ''}`} onClick={() => {
                const vals = ['', 'active', 'paused', 'completed', 'draft']
                const idx = vals.indexOf(adsStatusFilter)
                setAdsStatusFilter(vals[(idx + 1) % vals.length])
                setAdsPage(1)
              }}>
                <span>{adsStatusFilter ? (adsStatusFilter.charAt(0).toUpperCase() + adsStatusFilter.slice(1)) : 'Status'}</span>
                {adsStatusFilter && <span className="hs-qf-clear" onClick={(e) => { e.stopPropagation(); setAdsStatusFilter(''); setAdsPage(1) }}>&times;</span>}
                {!adsStatusFilter && <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>}
              </button>

              {/* Sort by */}
              <button className={`hs-qf-btn hs-qf-trigger${adsSort.field !== 'created_at' ? ' has-value' : ''}`} onClick={() => {
                const fields = Object.keys(adsSortFields)
                const idx = fields.indexOf(adsSort.field)
                const next = fields[(idx + 1) % fields.length]
                setAdsSort({ field: next, order: next === 'name' ? 'ASC' : 'DESC' })
                setAdsPage(1)
              }}>
                <span>Sort: {adsSortFields[adsSort.field] || 'Created'}</span>
                <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
              </button>

              <div className="hs-qf-sep" />

              {/* +More */}
              <button className="hs-qf-btn hs-qf-more" disabled style={{ opacity: 0.5 }}>
                <svg width="10" height="10" viewBox="0 0 32 32" fill="currentColor"><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V3a1.14 1.14 0 1 1 2.28 0v25.14c0 .63-.51 1.14-1.14 1.14Z"/><path d="M28.14 16.71H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28"/></svg>
                <span>More</span>
              </button>
            </div>
            )}

            {/* Manage tab content */}
            {adsTab === 'manage' && (
            <div className={`hs-content${tableSettingsOpen ? ' with-settings' : ''}`}>
              {/* Connected accounts strip */}
              {adAccounts.length > 0 && (
                <div className="ads-connected-strip">
                  <span className="ads-connected-label">Connected accounts</span>
                  {adAccounts.map((a: Record<string, unknown>) => {
                    const network = String(a.network || '')
                    const color = networkColor(network)
                    return (
                      <div key={String(a.id)} className="ads-connected-chip" style={{ '--chip-color': color } as React.CSSProperties}>
                        <span className="ads-connected-chip-dot" />
                        <span className="ads-connected-chip-network">{network.charAt(0).toUpperCase() + network.slice(1)}</span>
                        <span className="ads-connected-chip-name">{String(a.account_name || a.account_id || '')}</span>
                        <button className="ads-connected-chip-remove" onClick={() => { if (confirm('Disconnect this ad account?')) deleteAdAccount(Number(a.id)).then(() => setAdsTab('manage')) }} aria-label="Disconnect">&times;</button>
                      </div>
                    )
                  })}
                </div>
              )}

              <div className="hs-table-area">
                {/* ROW 4: SELECTION BAR */}
                {adsSelectedRows.size > 0 && (
                  <div className="hs-selection-bar">
                    <span className="hs-sel-count">{adsSelectedRows.size} selected</span>
                    <div className="hs-sel-sep" />
                    <button className="hs-sel-btn" onClick={() => {
                      const ids = [...adsSelectedRows]
                      if (ids.length === 1) {
                        const c = adCampaignsData.find((x: Record<string, unknown>) => Number(x.id) === ids[0])
                        if (c) { setNewAdName(String(c.name || '')); setNewAdNetwork(String(c.network || 'google')); setNewAdBudget(String(c.budget || '')); setNewAdObjective(String(c.objective || 'traffic')); setShowCreateAd(true) }
                      }
                    }}>Edit</button>
                    <button className="hs-sel-btn" onClick={() => {
                      const ids = [...adsSelectedRows]
                      Promise.all(ids.map(id => updateAdCampaign(id, { status: 'paused' }).catch(() => {})))
                        .then(() => { setAdsSelectedRows(new Set()); setAdsTab('manage'); { const opts: { network?: string; status?: string; page?: number; pageSize?: number } = { page: adsPage, pageSize: tablePageSize }; if (adsPlatformFilter) opts.network = adsPlatformFilter; if (adsStatusFilter) opts.status = adsStatusFilter; Promise.all([fetchAdAccounts(), fetchAdCampaigns(opts)]).then(([accts, camps]) => { setAdAccounts(accts.accounts); setAdCampaignsData(camps.campaigns); setAdCampaignsTotal(camps.total) }).catch(() => {}) } })
                    }}>Pause</button>
                    <button className="hs-sel-btn" style={{ color: '#dc2626' }} onClick={() => {
                      if (confirm(`Delete ${adsSelectedRows.size} campaign(s)?`)) {
                        Promise.all([...adsSelectedRows].map(id => deleteAdCampaign(id))).then(() => { setAdsSelectedRows(new Set()); setAdsTab('manage'); { const opts: { network?: string; status?: string; page?: number; pageSize?: number } = { page: adsPage, pageSize: tablePageSize }; if (adsPlatformFilter) opts.network = adsPlatformFilter; if (adsStatusFilter) opts.status = adsStatusFilter; Promise.all([fetchAdAccounts(), fetchAdCampaigns(opts)]).then(([accts, camps]) => { setAdAccounts(accts.accounts); setAdCampaignsData(camps.campaigns); setAdCampaignsTotal(camps.total) }).catch(() => {}) } }).catch(() => {})
                      }
                    }}>Delete</button>
                    <button className="hs-sel-close" onClick={() => setAdsSelectedRows(new Set())} title="Deselect all">&times;</button>
                  </div>
                )}

                {adsLoading && adCampaignsData.length > 0 && <div className="hs-loading-bar" />}

                {/* Empty state — no accounts connected and no campaigns */}
                {!adsLoading && adAccounts.length === 0 && adCampaignsData.length === 0 ? (
                  <div className="ads-empty-state">
                    <div className="ads-empty-icon">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/></svg>
                    </div>
                    <div className="ads-empty-networks">
                      {([
                        { color: '#4285F4', icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>) },
                        { color: '#1877F2', icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>) },
                        { color: '#0A66C2', icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="#0A66C2"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>) },
                      ]).map((n, i) => (
                        <div key={i} className="ads-empty-network-badge" style={{ '--badge-color': n.color } as React.CSSProperties}>{n.icon}</div>
                      ))}
                    </div>
                    <h3 className="ads-empty-title">Track and optimize your ad campaigns</h3>
                    <p className="ads-empty-desc">Connect your Google, Meta, or LinkedIn ad accounts to view ROI, optimize targeting with CRM data, and automatically follow up with new leads.</p>
                    <button className="hs-add-contacts-btn" onClick={() => setShowConnectAd(true)}>Connect ad account</button>
                  </div>
                ) : (
                  <>
                  {/* ROW 5: DATA TABLE */}
                  <div className="hs-table-scroll">
                    <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                      <colgroup>
                        <col style={{ width: 40 }} />
                        {adsCols.map(col => <col key={col.key} style={{ width: col.width }} />)}
                      </colgroup>
                      <thead>
                        <tr>
                          <th className="hs-th-check">
                            <input type="checkbox" checked={filteredAds.length > 0 && adsSelectedRows.size === filteredAds.length} onChange={toggleAllAdsRows} />
                          </th>
                          {adsCols.map(col => (
                            <th
                              key={col.key}
                              className={`hs-th-resizable${col.sortField ? ' hs-th-sortable' : ''}${col.frozen ? ' hs-th-frozen' : ''}`}
                              style={col.frozen ? { position: 'sticky', left: 40, top: 0, zIndex: 15, background: '#f8fafc' } : undefined}
                              onClick={() => col.sortField && toggleAdsSort(col.sortField)}
                              onContextMenu={col.sortField ? (e) => { e.preventDefault(); setColContextMenu({ field: col.sortField, x: e.clientX, y: e.clientY }) } : undefined}
                            >
                              <span className="hs-th-content">
                                {col.label}
                                {adsSort.field === col.sortField && <span className="hs-sort-arrow">{adsSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                                {col.sortField && <span className="hs-th-hover-sort"><svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M29.38 21.96a1.147 1.147 0 0 0-1.617 0l-3.85 3.852V5.714a1.143 1.143 0 0 0-2.286 0v20.099l-3.853-3.852a1.144 1.144 0 0 0-1.615 1.617l5.803 5.802a1.143 1.143 0 0 0 1.616 0l5.802-5.802a1.144 1.144 0 0 0 0-1.617M15.84 8.422 10.039 2.62a1.145 1.145 0 0 0-1.616 0L2.62 8.422a1.144 1.144 0 0 0 1.617 1.617l3.85-3.851v20.098a1.143 1.143 0 0 0 2.286 0V6.187l3.853 3.852a1.14 1.14 0 0 0 1.616-.001 1.14 1.14 0 0 0-.001-1.616"/></svg></span>}
                              </span>
                              {col.sortField && <button className="hs-th-kebab" onClick={(e) => { e.stopPropagation(); setColContextMenu({ field: col.sortField, x: e.clientX, y: e.clientY }) }}>
                                <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><circle cx="16" cy="4.14" r="2.29"/><circle cx="16" cy="15.57" r="2.29"/><circle cx="16" cy="27" r="2.29"/></svg>
                              </button>}
                              <div className="hs-col-resize-handle" onClick={(e) => e.stopPropagation()} />
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {adsLoading && adCampaignsData.length === 0 ? (
                          <tr><td colSpan={adsCols.length + 1} className="hs-loading-cell"><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}><DotLottieReact src="/loading.lottie" loop autoplay style={{ width: 80, height: 80 }} /><span style={{ fontSize: '0.82rem', color: '#7c98b6', marginTop: 8 }}>Loading campaigns...</span></div></td></tr>
                        ) : filteredAds.length === 0 ? (
                          <tr><td colSpan={adsCols.length + 1} className="hs-empty-cell">
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}>
                              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/></svg>
                              <span style={{ fontSize: '0.88rem', fontWeight: 600, color: '#33475b', marginTop: 12 }}>No campaigns found</span>
                              <span style={{ fontSize: '0.78rem', color: '#7c98b6', marginTop: 4 }}>{adsSearch ? 'Try adjusting your search or filters.' : 'Create a new campaign to get started.'}</span>
                            </div>
                          </td></tr>
                        ) : filteredAds.map((c: Record<string, unknown>) => {
                          const id = Number(c.id)
                          const net = String(c.network || '')
                          const sc = statusColor(String(c.status || 'draft'))
                          return (
                            <tr key={id} className={`hs-row${adsSelectedRows.has(id) ? ' selected' : ''}`}>
                              <td className="hs-td-check"><input type="checkbox" checked={adsSelectedRows.has(id)} onChange={() => toggleAdsRow(id)} /></td>
                              <td className="hs-td-name" style={{ position: 'sticky', left: 40, zIndex: 2, background: adsSelectedRows.has(id) ? '#eff6ff' : '#fff' }}>
                                <div className="hs-name-cell">
                                  <div className="hs-avatar" style={{ background: networkColor(net) }}>{(String(c.name || '?'))[0].toUpperCase()}</div>
                                  <span className="hs-name-link">{String(c.name || '')}</span>
                                </div>
                              </td>
                              <td><span style={{ padding: '2px 8px', borderRadius: 4, fontSize: '0.68rem', fontWeight: 600, background: net === 'google' ? '#e8f0fe' : net === 'meta' ? '#e7f3ff' : '#e8f4fd', color: networkColor(net) }}>{net.charAt(0).toUpperCase() + net.slice(1)}</span></td>
                              <td><span className="status-chip" style={{ background: sc.bg, color: sc.fg }}>{String(c.status || 'draft').charAt(0).toUpperCase() + String(c.status || 'draft').slice(1)}</span></td>
                              <td style={{ color: '#33475b', fontWeight: 500 }}>{typeof c.budget === 'number' ? `$${Number(c.budget).toLocaleString()}` : <span className="cell-empty">--</span>}</td>
                              <td style={{ color: '#33475b' }}><span title="No tracking connected yet">—</span></td>
                              <td style={{ color: '#516f90' }}><span title="No tracking connected yet">—</span></td>
                              <td style={{ color: '#516f90' }}><span title="No tracking connected yet">—</span></td>
                              <td style={{ color: '#516f90' }}><span title="No tracking connected yet">—</span></td>
                              <td>
                                <button style={{ background: 'none', border: 'none', color: '#d32f2f', cursor: 'pointer', fontSize: '0.72rem' }} onClick={() => { if (confirm('Delete?')) deleteAdCampaign(id).then(() => setAdsTab('manage')) }}>Delete</button>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* ROW 6: PAGINATION */}
                  <div className="hs-pagination">
                    <nav className="hs-paginator" aria-label="Pagination">
                      <button className="hs-paginator-btn" disabled={adsPage <= 1} onClick={() => setAdsPage(p => p - 1)} aria-label="Previous page">
                        <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95"/></svg>
                        <span>Prev</span>
                      </button>
                      {(() => {
                        const pages: number[] = []
                        const start = Math.max(1, adsPage - 2)
                        const end = Math.min(adsPageCount, start + 4)
                        for (let i = start; i <= end; i++) pages.push(i)
                        return pages.map(p => (
                          <button key={p} className={`hs-paginator-num${p === adsPage ? ' active' : ''}`} onClick={() => setAdsPage(p)}>{p}</button>
                        ))
                      })()}
                      <button className="hs-paginator-btn" disabled={adsPage >= adsPageCount} onClick={() => setAdsPage(p => p + 1)} aria-label="Next page">
                        <span>Next</span>
                        <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M11 27.71c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62l9.48-9.48-9.48-9.47a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0L22.1 15.48c.45.45.45 1.17 0 1.62L11.81 27.39c-.22.22-.52.33-.81.33Z"/></svg>
                      </button>
                    </nav>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', marginLeft: 'auto' }}><select value={tablePageSize} onChange={e => { setTablePageSize(Number(e.target.value) as any); setAdsPage(1) }} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #e2e8f0', fontSize: '0.82rem' }}><option value={100}>100</option><option value={200}>200</option><option value={500}>500</option></select><span>per page</span></div>
                  </div>
                  </>
                )}
              </div>

              {/* Table settings panel */}
              {tableSettingsOpen && (
                <div className="hs-settings-panel">
                  <div className="hs-settings-header">
                    <span className="hs-settings-title">Table settings</span>
                    <button className="hs-settings-close" onClick={() => setTableSettingsOpen(false)}>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                    </button>
                  </div>
                  <div className="hs-settings-body">
                    <div className="hs-settings-group">
                      <label className="hs-settings-label">
                        Pagination
                        <span className="hs-info-icon" title="Number of rows shown per page">
                          <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg>
                        </span>
                      </label>
                      {([100, 200, 500] as const).map(size => (
                        <label className="hs-settings-radio" key={size}>
                          <input type="radio" name="adsPageSize" checked={tablePageSize === size} onChange={() => { setTablePageSize(size); setAdsPage(1) }} />
                          <span className="hs-radio-indicator" />
                          <span>{size} per page</span>
                        </label>
                      ))}
                    </div>
                    <hr className="hs-settings-divider" />
                    <div className="hs-settings-group">
                      <label className="hs-settings-label">
                        Row height
                        <span className="hs-info-icon" title="Adjust the vertical spacing of table rows">
                          <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg>
                        </span>
                      </label>
                      {([
                        { value: 'small' as const, label: 'Compact' },
                        { value: 'medium' as const, label: 'Default' },
                        { value: 'large' as const, label: 'Comfortable' },
                      ]).map(opt => (
                        <label className="hs-settings-radio" key={opt.value}>
                          <input type="radio" name="adsRowHeight" checked={tableRowHeight === opt.value} onChange={() => setTableRowHeight(opt.value)} />
                          <span className="hs-radio-indicator" />
                          <span>{opt.label}</span>
                        </label>
                      ))}
                    </div>
                    <hr className="hs-settings-divider" />
                    <div className="hs-settings-group">
                      <div className="hs-settings-toggle-row">
                        <label className="hs-settings-label">
                          Zebra striping
                          <span className="hs-info-icon" title="Alternate row background colors for easier reading">
                            <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg>
                          </span>
                        </label>
                        <button className={`hs-toggle${tableZebraStriping ? ' on' : ''}`} onClick={() => setTableZebraStriping(!tableZebraStriping)} role="switch" aria-checked={tableZebraStriping}>
                          <span className="hs-toggle-track">
                            <span className="hs-toggle-on">ON</span>
                            <span className="hs-toggle-off">OFF</span>
                            <span className="hs-toggle-handle" />
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            )}

            {/* Analyze tab */}
            {adsTab === 'analyze' && (
              <div className="hs-content" style={{ padding: 20 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16 }}>
                  {[
                    { label: 'CAMPAIGNS', value: adCampaignsTotal },
                    { label: 'TOTAL SPEND', value: '—', title: 'No ad-network connection — metrics unavailable' },
                    { label: 'TOTAL CLICKS', value: '—', title: 'No ad-network connection — metrics unavailable' },
                    { label: 'IMPRESSIONS', value: '—', title: 'No ad-network connection — metrics unavailable' },
                    { label: 'TOTAL LEADS', value: '—', title: 'No ad-network connection — metrics unavailable' },
                    { label: 'AVG CTR', value: '—', title: 'No ad-network connection — metrics unavailable' },
                  ].map(kpi => (
                    <div key={kpi.label} className="kpi" title={(kpi as any).title} style={{ textAlign: 'center' }}>
                      <span style={{ display: 'block', fontSize: '0.68rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{kpi.label}</span>
                      <strong style={{ display: 'block', fontSize: '1.1rem', marginTop: 4 }}>{kpi.value}</strong>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Connect ad account panel */}
            {showConnectAd && (<>
              <div className="create-panel-overlay" onClick={() => setShowConnectAd(false)} />
              <div className="create-panel" onClick={(e) => e.stopPropagation()}>
                <div className="create-panel-header">
                  <h2>Add ad account (manual)</h2>
                  <button className="create-panel-close" onClick={() => setShowConnectAd(false)} aria-label="Close">
                    <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                  </button>
                </div>
                <div className="create-panel-body">
                  <div className="create-panel-field">
                    <label>Ad Network *</label>
                    <div className="ads-network-cards">
                      {([
                        { key: 'google', label: 'Google Ads', color: '#4285F4', icon: (<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>) },
                        { key: 'meta', label: 'Meta Ads', color: '#1877F2', icon: (<svg width="20" height="20" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>) },
                        { key: 'linkedin', label: 'LinkedIn Ads', color: '#0A66C2', icon: (<svg width="20" height="20" viewBox="0 0 24 24" fill="#0A66C2"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>) },
                      ] as const).map(n => (
                        <button key={n.key} className={`ads-network-card${newAdAcctNetwork === n.key ? ' selected' : ''}`} style={{ '--network-color': n.color } as React.CSSProperties} onClick={() => setNewAdAcctNetwork(n.key)}>
                          <div className="ads-network-card-icon">{n.icon}</div>
                          <span className="ads-network-card-label">{n.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="create-panel-field">
                    <label>Account Name</label>
                    <input value={newAdAcctName} onChange={e => setNewAdAcctName(e.target.value)} placeholder="e.g. BotPros - Google Ads" />
                  </div>
                  <div className="create-panel-field">
                    <label>Account ID</label>
                    <input value={newAdAcctId} onChange={e => setNewAdAcctId(e.target.value)} placeholder="e.g. 123-456-7890" />
                  </div>
                </div>
                <div className="create-panel-footer">
                  <div style={{ fontSize: '0.75rem', color: '#7c98b6', marginTop: 8 }}>Manual tracking record — no ad-network connection is made.</div>
                  <button className="cp-btn-primary" disabled={!newAdAcctNetwork} onClick={() => { createAdAccount({ network: newAdAcctNetwork, account_name: newAdAcctName, account_id: newAdAcctId }).then(() => { setShowConnectAd(false); setAdsTab('manage'); { const opts: { network?: string; status?: string; page?: number; pageSize?: number } = { page: adsPage, pageSize: tablePageSize }; if (adsPlatformFilter) opts.network = adsPlatformFilter; if (adsStatusFilter) opts.status = adsStatusFilter; Promise.all([fetchAdAccounts(), fetchAdCampaigns(opts)]).then(([accts, camps]) => { setAdAccounts(accts.accounts); setAdCampaignsData(camps.campaigns); setAdCampaignsTotal(camps.total) }).catch(() => {}) } }) }}>Add account</button>
                  <button className="cp-btn-secondary" onClick={() => setShowConnectAd(false)}>Cancel</button>
                </div>
              </div>
            </>)}

            {/* Create campaign panel */}
            {showCreateAd && (<>
              <div className="create-panel-overlay" onClick={() => setShowCreateAd(false)} />
              <div className="create-panel" onClick={(e) => e.stopPropagation()}>
                <div className="create-panel-header">
                  <h2>Create Ad Campaign</h2>
                  <button className="create-panel-close" onClick={() => setShowCreateAd(false)} aria-label="Close">
                    <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                  </button>
                </div>
                <div className="create-panel-body">
                  <div className="create-panel-field">
                    <label>Campaign Name *</label>
                    <input value={newAdName} onChange={e => setNewAdName(e.target.value)} placeholder="e.g. Spring 2026 Awareness" autoFocus />
                  </div>
                  <div className="create-panel-field">
                    <label>Network</label>
                    <select value={newAdNetwork} onChange={e => setNewAdNetwork(e.target.value)}>
                      <option value="google">Google Ads</option>
                      <option value="meta">Meta</option>
                      <option value="linkedin">LinkedIn</option>
                    </select>
                  </div>
                  <div className="create-panel-field">
                    <label>Objective</label>
                    <select value={newAdObjective} onChange={e => setNewAdObjective(e.target.value)}>
                      <option value="awareness">Brand Awareness</option>
                      <option value="traffic">Website Traffic</option>
                      <option value="leads">Lead Generation</option>
                      <option value="conversions">Conversions</option>
                    </select>
                  </div>
                  <div className="create-panel-field">
                    <label>Budget ($)</label>
                    <input type="number" value={newAdBudget} onChange={e => setNewAdBudget(e.target.value)} placeholder="0.00" />
                  </div>
                </div>
                <div className="create-panel-footer">
                  <button className="cp-btn-primary" disabled={!newAdName.trim()} onClick={() => { createAdCampaign({ name: newAdName, network: newAdNetwork, objective: newAdObjective, budget: parseFloat(newAdBudget) || 0, status: 'draft' }).then(() => { setShowCreateAd(false); setAdsTab('manage'); { const opts: { network?: string; status?: string; page?: number; pageSize?: number } = { page: adsPage, pageSize: tablePageSize }; if (adsPlatformFilter) opts.network = adsPlatformFilter; if (adsStatusFilter) opts.status = adsStatusFilter; Promise.all([fetchAdAccounts(), fetchAdCampaigns(opts)]).then(([accts, camps]) => { setAdAccounts(accts.accounts); setAdCampaignsData(camps.campaigns); setAdCampaignsTotal(camps.total) }).catch(() => {}) } }) }}>Create campaign</button>
                  <button className="cp-btn-secondary" onClick={() => setShowCreateAd(false)}>Cancel</button>
                </div>
              </div>
            </>)}
          </div>
          )
        })()}

        {/* ========== Social Media View ========== */}
        {activeView === 'social' && (() => {
          // Client-side filtering + sorting + pagination
          const socFilteredByTab = socialViewTab === 'draft' ? socialPosts.filter((p: any) => p.status === 'draft') : socialViewTab === 'scheduled' ? socialPosts.filter((p: any) => p.status === 'scheduled') : socialViewTab === 'published' ? socialPosts.filter((p: any) => p.status === 'published') : socialPosts
          const socFilteredByStatus = socialStatusFilter ? socFilteredByTab.filter((p: any) => p.status === socialStatusFilter) : socFilteredByTab
          const socFilteredByPlatform = socialPlatformFilter ? socFilteredByStatus.filter((p: any) => String(p.platforms || '').includes(socialPlatformFilter)) : socFilteredByStatus
          const socFilteredBySearch = socialSearch ? socFilteredByPlatform.filter((p: any) => String(p.content || '').toLowerCase().includes(socialSearch.toLowerCase())) : socFilteredByPlatform
          const socFiltered = socFilteredBySearch
          const socSorted = [...socFiltered].sort((a: any, b: any) => {
            const fa = a[socialSort.field] ?? ''
            const fb = b[socialSort.field] ?? ''
            if (socialSort.field === 'likes' || socialSort.field === 'comments') return socialSort.order === 'ASC' ? (Number(fa) - Number(fb)) : (Number(fb) - Number(fa))
            const cmp = String(fa).localeCompare(String(fb), undefined, { sensitivity: 'base' })
            return socialSort.order === 'ASC' ? cmp : -cmp
          })
          const socPageSize = tablePageSize
          const socTotal = socSorted.length
          const socTotalPages = Math.max(1, Math.ceil(socTotal / socPageSize))
          const socCurrentPage = Math.min(socialPage, socTotalPages)
          const socPagedData = socSorted.slice((socCurrentPage - 1) * socPageSize, socCurrentPage * socPageSize)
          const toggleAllSocRows = () => {
            if (socialSelectedRows.size === socPagedData.length) {
              setSocialSelectedRows(new Set())
            } else {
              setSocialSelectedRows(new Set(socPagedData.map((p: any) => p.id)))
            }
          }
          const socSortHeader = (field: string) => {
            if (socialSort.field === field) {
              setSocialSort({ field, order: socialSort.order === 'ASC' ? 'DESC' : 'ASC' })
            } else {
              setSocialSort({ field, order: field === 'content' ? 'ASC' : 'DESC' })
            }
            setSocialPage(1)
          }
          const socSortArrow = (field: string) => socialSort.field === field ? (socialSort.order === 'ASC' ? ' \u2191' : ' \u2193') : ''
          const platformColors: Record<string, string> = { facebook: '#1877F2', instagram: '#E4405F', linkedin: '#0A66C2', twitter: '#1DA1F2' }
          return (
          <div className="hs-contacts">
            {/* Row 1 -- Topbar: object switcher + view tabs + actions */}
            <div className="hs-topbar">
              <div className="hs-topbar-left">
                {/* Object switcher */}
                <div className="hs-obj-switcher-wrap" ref={objSwitcherRef}>
                  <button className={`hs-obj-switcher${objSwitcherOpen ? ' open' : ''}`} onClick={() => { setObjSwitcherOpen(!objSwitcherOpen); setObjSwitcherSearch('') }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2H4z"/><circle cx="12" cy="12" r="3"/><path d="M16.5 7.5v.001"/></svg>
                    <span>Social</span>
                    <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                  </button>
                  {objSwitcherOpen && (
                    <div className="hs-obj-panel">
                      <div className="hs-obj-search-wrap">
                        <svg className="hs-obj-search-icon" width="13" height="13" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                        <input
                          className="hs-obj-search"
                          placeholder="Search"
                          value={objSwitcherSearch}
                          onChange={(e) => setObjSwitcherSearch(e.target.value)}
                          autoFocus
                        />
                      </div>
                      <ul className="hs-obj-list" role="listbox">
                        {[
                          { label: 'Email', view: 'marketing-email' as ViewMode },
                          { label: 'Ads', view: 'ads' as ViewMode },
                          { label: 'Social', view: 'social' as ViewMode },
                          { label: 'Forms', view: 'forms' as ViewMode },
                          { label: 'Campaigns', view: 'campaigns' as ViewMode },
                          { label: 'Landing Pages', view: 'landing-pages' as ViewMode },
                        ]
                          .filter(item => !objSwitcherSearch || item.label.toLowerCase().includes(objSwitcherSearch.toLowerCase()))
                          .map(item => (
                            <li
                              key={item.view}
                              className={`hs-obj-item${item.view === activeView ? ' active' : ''}`}
                              role="option"
                              aria-selected={item.view === activeView}
                              onClick={() => {
                                setActiveView(item.view)
                                setObjSwitcherOpen(false)
                                setObjSwitcherSearch('')
                              }}
                            >
                              {item.label}
                            </li>
                          ))
                        }
                      </ul>
                    </div>
                  )}
                </div>

                {/* Separator */}
                <div className="hs-topbar-sep" />

                {/* View tabs */}
                <div className="hs-view-tabs">
                  {[
                    { key: '', label: 'All posts' },
                    { key: 'published', label: 'Published' },
                    { key: 'scheduled', label: 'Scheduled' },
                    { key: 'draft', label: 'Drafts' },
                  ].map((tab) => {
                    const isActive = socialViewTab === tab.key
                    return (
                      <div
                        key={tab.key}
                        className={`hs-view-tab${isActive ? ' active' : ''}`}
                        ref={isActive ? tabEditRef : undefined}
                        onClick={() => { setSocialViewTab(tab.key); setSocialPage(1) }}
                      >
                        <span className="hs-tab-label">{tab.label}</span>
                        {isActive && socTotal > 0 && <span className="hs-tab-count">{socTotal.toLocaleString()}</span>}
                        {isActive && (
                          <span className="hs-tab-kebab" onClick={(e) => { e.stopPropagation(); setTabEditOpen(!tabEditOpen) }}>
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>
                          </span>
                        )}
                        {tab.key !== '' && <span className="hs-tab-x" onClick={(e) => { e.stopPropagation(); setSocialViewTab(''); setSocialPage(1) }}>&times;</span>}
                        {isActive && tabEditOpen && (
                          <div className="hs-tab-edit-dropdown">
                            <button className="hs-tab-edit-item" disabled>Rename</button>
                            <button className="hs-tab-edit-item" onClick={() => {
                              const id = Date.now().toString(36)
                              setCustomViews(prev => ({
                                ...prev,
                                social: [...(prev.social || []), { id, label: `${tab.label} (clone)` }]
                              }))
                              setTabEditOpen(false)
                            }}>Clone</button>
                            <button className="hs-tab-edit-item" disabled>Manage Sharing</button>
                            {tab.key !== '' && <button className="hs-tab-edit-item" onClick={() => { setSocialViewTab(''); setSocialPage(1); setTabEditOpen(false) }}>Delete</button>}
                          </div>
                        )}
                      </div>
                    )
                  })}
                  {/* Custom user-created view tabs */}
                  {(customViews.social || []).map(cv => {
                    const isActive = socialViewTab === `custom:${cv.id}`
                    const isRenaming = renamingViewId === cv.id
                    return (
                      <div
                        key={cv.id}
                        className={`hs-view-tab${isActive ? ' active' : ''}`}
                        onClick={() => {
                          if (isRenaming) return
                          setSocialViewTab(`custom:${cv.id}`)
                          setSocialPage(1)
                        }}
                      >
                        {isRenaming ? (
                          <input
                            ref={renamingViewRef}
                            className="hs-tab-rename-input"
                            value={renamingViewName}
                            onChange={(e) => setRenamingViewName(e.target.value)}
                            onFocus={(e) => e.target.select()}
                            onBlur={() => {
                              if (renamingViewName.trim()) {
                                setCustomViews(prev => ({
                                  ...prev,
                                  social: (prev.social || []).map(v => v.id === cv.id ? { ...v, label: renamingViewName.trim() } : v)
                                }))
                              }
                              setRenamingViewId(null)
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
                              if (e.key === 'Escape') { setRenamingViewName(cv.label); setRenamingViewId(null) }
                            }}
                            onClick={(e) => e.stopPropagation()}
                            autoFocus
                          />
                        ) : (
                          <span className="hs-tab-label">{cv.label}</span>
                        )}
                        {isActive && !isRenaming && socTotal > 0 && <span className="hs-tab-count">{socTotal.toLocaleString()}</span>}
                        {isActive && !isRenaming && (
                          <span className="hs-tab-kebab" onClick={(e) => { e.stopPropagation(); setTabEditOpen(!tabEditOpen) }}>
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>
                          </span>
                        )}
                        {isActive && tabEditOpen && !isRenaming && (
                          <div className="hs-tab-edit-dropdown">
                            <button className="hs-tab-edit-item" onClick={() => { setRenamingViewId(cv.id); setRenamingViewName(cv.label); setTabEditOpen(false) }}>Rename</button>
                            <button className="hs-tab-edit-item" onClick={() => {
                              const id = Date.now().toString(36)
                              setCustomViews(prev => ({
                                ...prev,
                                social: [...(prev.social || []), { id, label: `${cv.label} (clone)` }]
                              }))
                              setSocialViewTab(`custom:${id}`)
                              setRenamingViewId(id)
                              setRenamingViewName(`${cv.label} (clone)`)
                              setTabEditOpen(false)
                            }}>Clone</button>
                            <button className="hs-tab-edit-item" onClick={() => {
                              setCustomViews(prev => ({
                                ...prev,
                                social: (prev.social || []).filter(v => v.id !== cv.id)
                              }))
                              setSocialViewTab('')
                              setSocialPage(1)
                              setTabEditOpen(false)
                            }}>Delete</button>
                          </div>
                        )}
                        <span className="hs-tab-x" onClick={(e) => {
                          e.stopPropagation()
                          setCustomViews(prev => ({
                            ...prev,
                            social: (prev.social || []).filter(v => v.id !== cv.id)
                          }))
                          if (isActive) { setSocialViewTab(''); setSocialPage(1) }
                        }}>&times;</span>
                      </div>
                    )
                  })}
                  {/* Quick-add view button */}
                  <div className="hs-topbar-sep" />
                  <div className="hs-view-add-wrap" ref={viewAddMenuRef} style={{ position: 'relative', display: 'inline-flex', alignSelf: 'center' }}>
                    <button
                      className={`hs-quick-add-btn${viewAddMenuOpen ? ' active' : ''}`}
                      title="Create or add view"
                      type="button"
                      aria-label="Create or add view"
                      onClick={() => { setViewAddMenuOpen(!viewAddMenuOpen); setViewAddMenuMode('menu') }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                    </button>
                    {viewAddMenuOpen && viewAddMenuMode === 'menu' && (
                      <div className="hs-view-add-dropdown">
                        <button className="hs-view-add-item" onClick={() => setViewAddMenuMode('type')}>
                          Create new view
                        </button>
                        <button className="hs-view-add-item" onClick={() => {
                          setViewAddMenuOpen(false)
                          setViewAddMenuMode('menu')
                        }}>
                          Add view ({(customViews.social || []).length}/50)
                        </button>
                      </div>
                    )}
                    {viewAddMenuOpen && viewAddMenuMode === 'type' && (
                      <div className="hs-view-add-dropdown hs-view-type-picker">
                        <button className="hs-view-type-btn" onClick={() => {
                          const id = Date.now().toString(36)
                          const viewName = `All posts-${(customViews.social || []).length + 1}`
                          setCustomViews(prev => ({
                            ...prev,
                            social: [...(prev.social || []), { id, label: viewName }]
                          }))
                          setSocialViewTab(`custom:${id}`)
                          setRenamingViewId(id)
                          setRenamingViewName(viewName)
                          setViewAddMenuOpen(false)
                          setViewAddMenuMode('menu')
                        }}>
                          <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                          <span>Table</span>
                        </button>
                        <button className="hs-view-type-btn" disabled style={{ opacity: 0.4 }}>
                          <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M11.43 11.43h-8a1.14 1.14 0 1 1 0-2.28h8a1.14 1.14 0 1 1 0 2.28M28.57 11.43h-8a1.14 1.14 0 1 1 0-2.28h8a1.14 1.14 0 1 1 0 2.28"/><path d="M9.14 29.71H5.71c-1.89 0-3.43-1.54-3.43-3.43V5.71c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v14.86c0 1.89-1.54 3.43-3.43 3.43h-3.43c-1.89 0-3.43-1.54-3.43-3.43v-1.14h-6.86v6.86c0 1.89-1.54 3.43-3.43 3.43Z"/></svg>
                          <span>Board</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="hs-topbar-right">
                {/* Create post — direct action (Import removed) */}
                <button className="hs-add-contacts-btn" onClick={() => { setNewPostContent(''); setNewPostPlatforms([]); setNewPostSchedule(''); setShowCreatePost(true) }}>
                  Create post
                </button>
              </div>
            </div>

            {/* Row 2 -- Toolbar */}
            <div className="hs-toolbar">
              <div className="hs-toolbar-left">
                {/* Search */}
                <div className="hs-search-wrap">
                  <svg className="hs-search-icon" width="14" height="14" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M28.14 29.29c-.29 0-.58-.11-.81-.33l-7.58-7.58a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l7.58 7.58a1.14 1.14 0 0 1-.81 1.95"/><path d="M13.29 24.71c-6.3 0-11.43-5.13-11.43-11.43S6.98 1.86 13.29 1.86s11.43 5.13 11.43 11.43-5.13 11.43-11.43 11.43Zm0-20.57c-5.04 0-9.14 4.1-9.14 9.14s4.1 9.14 9.14 9.14 9.14-4.1 9.14-9.14-4.1-9.14-9.14-9.14"/></svg>
                  <input
                    className="hs-search-input"
                    placeholder="Search posts"
                    value={socialSearch}
                    onChange={(e) => { setSocialSearch(e.target.value); setSocialPage(1) }}
                  />
                  {socialSearch && (
                    <button className="hs-search-clear" onClick={() => { setSocialSearch(''); setSocialPage(1) }}>&times;</button>
                  )}
                </div>

                <div className="hs-tb-sep" />

                {/* View type + settings button group */}
                <div className="hs-viewtype-group">
                  <div className="hs-viewtype-wrap" ref={viewTypeDropdownRef}>
                    <button
                      className={`hs-tb-btn hs-viewtype-btn${viewTypeDropdownOpen ? ' active' : ''}`}
                      onClick={() => setViewTypeDropdownOpen(!viewTypeDropdownOpen)}
                    >
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                      <span>Table</span>
                      <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                    </button>
                    {viewTypeDropdownOpen && (
                      <div className="hs-viewtype-panel">
                        <div className="hs-viewtype-header">View type</div>
                        <button className="hs-viewtype-option active" onClick={() => setViewTypeDropdownOpen(false)}>
                          <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                          <span>Table</span>
                        </button>
                      </div>
                    )}
                  </div>
                  <button
                    className={`hs-tb-btn hs-settings-btn${tableSettingsOpen ? ' active' : ''}`}
                    onClick={() => { if (!tableSettingsOpen) { setSocialPreview(null) } setTableSettingsOpen(!tableSettingsOpen) }}
                    title={tableSettingsOpen ? 'Close settings' : 'Table settings'}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                  </button>
                </div>

                <div className="hs-tb-sep" />

                {/* Edit columns */}
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Edit columns" disabled style={{ opacity: 0.5 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="18" rx="1"/><rect x="14" y="3" width="7" height="18" rx="1"/></svg>
                  <span>Edit columns</span>
                </button>

                <div className="hs-tb-sep" />

                {/* Filters */}
                <button className={`hs-tb-btn hs-tb-btn-bordered${quickFiltersOpen ? ' hs-tb-btn-pressed' : ''}`} title="Filters" onClick={() => setQuickFiltersOpen(!quickFiltersOpen)}>
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M30.71 16.86H13.57a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28M6.71 16.86H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M10.14 20.29c-2.52 0-4.57-2.05-4.57-4.57s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M6.71 6.57H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M30.71 6.57H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M18.14 6.57H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 10C19.05 10 17 7.95 17 5.43S19.05.86 21.57.86s4.57 2.05 4.57 4.57S24.09 10 21.57 10m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M30.71 27.14H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M18.14 27.14H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 30.57C19.05 30.57 17 28.52 17 26s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29"/></svg>
                  <span>Filters</span>
                </button>

                <div className="hs-tb-sep" />

                {/* Export */}
                <button
                  className={`hs-tb-btn hs-tb-btn-bordered${socialExporting ? ' hs-tb-btn-pressed' : ''}`}
                  title="Export current view as CSV"
                  disabled={socialExporting}
                  onClick={() => {
                    setSocialExporting(true)
                    try {
                      const headers = ['Content', 'Platforms', 'Status', 'Likes', 'Comments', 'Published At', 'Scheduled At', 'Created At']
                      const escCsv = (v: unknown) => { const s = String(v ?? ''); return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s }
                      const rows = socSorted.map((p: any) => [
                        p.content, p.platforms || '', p.status, p.likes || 0, p.comments || 0,
                        p.published_at || '', p.scheduled_at || '', p.created_at || '',
                      ].map(escCsv).join(','))
                      const csv = [headers.join(','), ...rows].join('\n')
                      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
                      const url = URL.createObjectURL(blob)
                      const a = document.createElement('a')
                      a.href = url
                      a.download = `social-posts-export-${new Date().toISOString().slice(0, 10)}.csv`
                      document.body.appendChild(a)
                      a.click()
                      document.body.removeChild(a)
                      URL.revokeObjectURL(url)
                    } catch (err) {
                      console.error('Export failed:', err)
                      alert('Export failed. Please try again.')
                    } finally {
                      setSocialExporting(false)
                    }
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  <span>{socialExporting ? 'Exporting...' : 'Export'}</span>
                </button>
              </div>

              <div className="hs-toolbar-right">
                <div className="hs-tb-sep" />
                {/* Clone view */}
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Clone view" onClick={() => {
                  const id = Date.now().toString(36)
                  const baseName = socialViewTab.startsWith('custom:') ? ((customViews.social || []).find(v => `custom:${v.id}` === socialViewTab)?.label || 'All posts') : 'All posts'
                  const cloneName = `${baseName} (clone)`
                  setCustomViews(prev => ({
                    ...prev,
                    social: [...(prev.social || []), { id, label: cloneName }]
                  }))
                  setSocialViewTab(`custom:${id}`)
                  setRenamingViewId(id)
                  setRenamingViewName(cloneName)
                }}>
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M25.86 29.29H12.15c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-1.89 1.54-3.43 3.43-3.43h13.71c1.89 0 3.43 1.54 3.43 3.43v13.71c0 1.89-1.54 3.43-3.43 3.43M12.14 11c-.63 0-1.14.51-1.14 1.14v13.71c0 .63.51 1.14 1.14 1.14h13.71c.63 0 1.14-.51 1.14-1.14V12.14c0-.63-.51-1.14-1.14-1.14z"/><path d="M5.29 22.43c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43H19c1.89 0 3.43 1.54 3.43 3.43a1.14 1.14 0 1 1-2.28 0c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14V19c0 .63.51 1.14 1.14 1.14a1.14 1.14 0 1 1 0 2.28Z"/></svg>
                </button>
                {/* Save view */}
                <button
                  className={`hs-tb-btn hs-tb-btn-bordered${!socialViewTab.startsWith('custom:') ? ' disabled' : ''}`}
                  disabled={!socialViewTab.startsWith('custom:')}
                  title="Save view"
                  onClick={() => {
                    if (!socialViewTab.startsWith('custom:')) return
                    const btn = document.querySelector('.hs-tb-btn[title="Save view"]') as HTMLElement
                    if (btn) { btn.style.color = '#22c55e'; setTimeout(() => { btn.style.color = '' }, 800) }
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                </button>
              </div>
            </div>

            {/* Row 3 -- Quick filters bar */}
            {quickFiltersOpen && <div className="hs-quick-filters">
              {/* Platform filter cycle */}
              <button
                className={`hs-qf-btn hs-qf-trigger${socialPlatformFilter ? ' has-value' : ''}`}
                onClick={() => {
                  const vals = ['', 'facebook', 'instagram', 'linkedin', 'twitter']
                  const idx = vals.indexOf(socialPlatformFilter)
                  setSocialPlatformFilter(vals[(idx + 1) % vals.length])
                  setSocialPage(1)
                }}
              >
                <span>{socialPlatformFilter ? socialPlatformFilter.charAt(0).toUpperCase() + socialPlatformFilter.slice(1) : 'Platform'}</span>
                {socialPlatformFilter && (
                  <span className="hs-qf-clear" onClick={(e) => { e.stopPropagation(); setSocialPlatformFilter(''); setSocialPage(1) }}>&times;</span>
                )}
                {!socialPlatformFilter && <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>}
              </button>

              {/* Status filter cycle */}
              <button
                className={`hs-qf-btn hs-qf-trigger${socialStatusFilter ? ' has-value' : ''}`}
                onClick={() => {
                  const vals = ['', 'published', 'scheduled', 'draft']
                  const idx = vals.indexOf(socialStatusFilter)
                  setSocialStatusFilter(vals[(idx + 1) % vals.length])
                  setSocialPage(1)
                }}
              >
                <span>{socialStatusFilter ? socialStatusFilter.charAt(0).toUpperCase() + socialStatusFilter.slice(1) : 'Status'}</span>
                {socialStatusFilter && (
                  <span className="hs-qf-clear" onClick={(e) => { e.stopPropagation(); setSocialStatusFilter(''); setSocialPage(1) }}>&times;</span>
                )}
                {!socialStatusFilter && <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>}
              </button>

              {/* Sort by */}
              <button
                className={`hs-qf-btn hs-qf-trigger${socialSort.field !== 'created_at' ? ' has-value' : ''}`}
                onClick={() => {
                  const fields = ['created_at', 'content', 'likes', 'comments', 'published_at']
                  const idx = fields.indexOf(socialSort.field)
                  const next = fields[(idx + 1) % fields.length]
                  setSocialSort({ field: next, order: next === 'content' ? 'ASC' : 'DESC' })
                  setSocialPage(1)
                }}
              >
                <span>Sort: {{ created_at: 'Created', content: 'Content', likes: 'Likes', comments: 'Comments', published_at: 'Published' }[socialSort.field] || 'Created'}</span>
                <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
              </button>

              {/* Active filter pills */}
              {socialPlatformFilter && (
                <div className="qf-pill-wrap">
                  <button className="hs-qf-btn hs-qf-trigger has-value" onClick={() => { setSocialPlatformFilter(''); setSocialPage(1) }}>
                    <span>Platform: {socialPlatformFilter.charAt(0).toUpperCase() + socialPlatformFilter.slice(1)}</span>
                    <span className="hs-qf-count">1</span>
                  </button>
                  <button className="hs-qf-clear-btn" title="Clear Platform filter" onClick={() => { setSocialPlatformFilter(''); setSocialPage(1) }}>
                    <svg width="10" height="10" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                  </button>
                </div>
              )}
              {socialStatusFilter && (
                <div className="qf-pill-wrap">
                  <button className="hs-qf-btn hs-qf-trigger has-value" onClick={() => { setSocialStatusFilter(''); setSocialPage(1) }}>
                    <span>Status: {socialStatusFilter.charAt(0).toUpperCase() + socialStatusFilter.slice(1)}</span>
                    <span className="hs-qf-count">1</span>
                  </button>
                  <button className="hs-qf-clear-btn" title="Clear Status filter" onClick={() => { setSocialStatusFilter(''); setSocialPage(1) }}>
                    <svg width="10" height="10" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                  </button>
                </div>
              )}

              <div className="hs-qf-sep" />

              {/* +More popover */}
              <div className="hs-qf-more-wrap" ref={qfMoreRef}>
                <button className={`hs-qf-btn hs-qf-more${qfMoreOpen ? ' open' : ''}`} onClick={() => setQfMoreOpen(!qfMoreOpen)}>
                  <svg width="10" height="10" viewBox="0 0 32 32" fill="currentColor"><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V3a1.14 1.14 0 1 1 2.28 0v25.14c0 .63-.51 1.14-1.14 1.14Z"/><path d="M28.14 16.71H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28"/></svg>
                  <span>More</span>
                </button>
                {qfMoreOpen && (
                  <div className="qf-more-popover" ref={(el) => {
                    if (!el) return
                    const btn = qfMoreRef.current?.querySelector('button')
                    if (!btn) return
                    const rect = btn.getBoundingClientRect()
                    el.style.position = 'fixed'
                    el.style.top = `${rect.bottom + 6}px`
                    el.style.left = `${rect.left}px`
                    el.style.right = 'auto'
                  }}>
                    <div className="qf-more-body">
                      <h5 className="qf-more-heading">Add a quick filter</h5>
                    </div>
                    <div className="qf-more-list">
                      <div className="qf-more-category">Post properties</div>
                      {!socialPlatformFilter && (
                        <button className="qf-more-option" onClick={() => { setSocialPlatformFilter('facebook'); setSocialPage(1); setQfMoreOpen(false) }}>
                          <span className="qf-more-option-icon"><svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M23.57 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V7.57a1.14 1.14 0 1 1 2.28 0v18.29c0 .63.51 1.14 1.14 1.14h18.29a1.14 1.14 0 1 1 0 2.28Z"/><path d="M25.86 24.71h-16c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43h16c1.89 0 3.43 1.54 3.43 3.43v16c0 1.89-1.54 3.43-3.43 3.43Zm-16-20.57c-.63 0-1.14.51-1.14 1.14v16c0 .63.51 1.14 1.14 1.14h16c.63 0 1.14-.51 1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14h-16Z"/><path d="M16.84 17.48c-.3 0-.59-.12-.81-.33l-3.56-3.56a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l2.71 2.71 4.78-5.21c.43-.46 1.15-.5 1.61-.07.47.43.5 1.15.07 1.62l-5.59 6.09c-.21.23-.51.36-.82.37h-.02Z"/></svg></span>
                          <span className="qf-more-option-label">Platform</span>
                        </button>
                      )}
                      {!socialStatusFilter && (
                        <button className="qf-more-option" onClick={() => { setSocialStatusFilter('published'); setSocialPage(1); setQfMoreOpen(false) }}>
                          <span className="qf-more-option-icon"><svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M23.57 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V7.57a1.14 1.14 0 1 1 2.28 0v18.29c0 .63.51 1.14 1.14 1.14h18.29a1.14 1.14 0 1 1 0 2.28Z"/><path d="M25.86 24.71h-16c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43h16c1.89 0 3.43 1.54 3.43 3.43v16c0 1.89-1.54 3.43-3.43 3.43Zm-16-20.57c-.63 0-1.14.51-1.14 1.14v16c0 .63.51 1.14 1.14 1.14h16c.63 0 1.14-.51 1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14h-16Z"/><path d="M16.84 17.48c-.3 0-.59-.12-.81-.33l-3.56-3.56a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l2.71 2.71 4.78-5.21c.43-.46 1.15-.5 1.61-.07.47.43.5 1.15.07 1.62l-5.59 6.09c-.21.23-.51.36-.82.37h-.02Z"/></svg></span>
                          <span className="qf-more-option-label">Status</span>
                        </button>
                      )}
                      {socialPlatformFilter && socialStatusFilter && (
                        <div className="qf-more-empty">All filters added</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>}

            {/* Row 4 -- Selection bar */}
            {socialSelectedRows.size > 0 && (
              <div className="hs-selection-bar">
                <span className="hs-sel-count">{socialSelectedRows.size} selected</span>
                <div className="hs-sel-sep" />

                <button className="hs-sel-btn" style={{ color: '#dc2626' }} onClick={() => {
                  if (confirm(`Delete ${socialSelectedRows.size} post(s)?`)) {
                    Promise.all([...socialSelectedRows].map(id => deleteSocialPost(id))).then(() => {
                      { const opts: { status?: string } = {}; if (socialFilter !== 'all') opts.status = socialFilter; fetchSocialPosts(opts).then(res => { setSocialPosts(res.posts); setSocialPostsTotal(res.total) }).catch(() => {}) }
                      setSocialSelectedRows(new Set())
                    }).catch(() => {})
                  }
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                  Delete
                </button>

                <button className="hs-sel-close" onClick={() => setSocialSelectedRows(new Set())} title="Deselect all">&times;</button>
              </div>
            )}

            {/* Row 5 -- Data table */}
            <div className="hs-content">
              <div className="hs-table-area">
                <div className="hs-table-scroll">
                  {socialLoading ? (
                    <div className="hs-loading-wrap">
                      <div className="hs-loading-bar" />
                      <div style={{ padding: '60px 40px', textAlign: 'center', color: '#7c98b6' }}>Loading posts...</div>
                    </div>
                  ) : socPagedData.length === 0 ? (
                    <div style={{ padding: '60px 40px', textAlign: 'center' }}>
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" style={{ marginBottom: 12 }}><path d="M4 4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2H4z"/><circle cx="12" cy="12" r="3"/></svg>
                      <div style={{ color: '#64748b', fontSize: '0.88rem', fontWeight: 600, marginBottom: 4 }}>No social posts found</div>
                      <div style={{ color: '#94a3b8', fontSize: '0.78rem' }}>Create your first social post or adjust your filters.</div>
                    </div>
                  ) : (
                    <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                      <thead>
                        <tr>
                          <th className="hs-th-check" style={{ width: 40 }}>
                            <input type="checkbox" checked={socPagedData.length > 0 && socialSelectedRows.size === socPagedData.length} onChange={toggleAllSocRows} />
                          </th>
                          {[
                            { key: 'content', label: 'Content', sortField: 'content', frozen: true },
                            { key: 'platforms', label: 'Platforms', sortField: 'platforms', frozen: false },
                            { key: 'status', label: 'Status', sortField: 'status', frozen: false },
                            { key: 'likes', label: 'Likes', sortField: 'likes', frozen: false },
                            { key: 'comments', label: 'Comments', sortField: 'comments', frozen: false },
                            { key: 'published_at', label: 'Date', sortField: 'published_at', frozen: false },
                            { key: 'actions', label: 'Actions', sortField: '', frozen: false },
                          ].map(col => (
                            <th
                              key={col.key}
                              className={`hs-th-resizable${col.sortField ? ' hs-th-sortable' : ''}${col.frozen ? ' hs-th-frozen' : ''}`}
                              style={col.frozen ? { position: 'sticky', left: 40, top: 0, zIndex: 15, background: '#f8fafc' } : undefined}
                              onClick={col.sortField ? () => socSortHeader(col.sortField) : undefined}
                              onContextMenu={col.sortField ? (e) => { e.preventDefault(); setColContextMenu({ field: col.sortField, x: e.clientX, y: e.clientY }) } : undefined}
                            >
                              <span className="hs-th-content">
                                {col.label}
                                {col.sortField && socialSort.field === col.sortField && <span className="hs-sort-arrow">{socialSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                                {col.sortField && <span className="hs-th-hover-sort"><svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M29.38 21.96a1.147 1.147 0 0 0-1.617 0l-3.85 3.852V5.714a1.143 1.143 0 0 0-2.286 0v20.099l-3.853-3.852a1.144 1.144 0 0 0-1.615 1.617l5.803 5.802a1.143 1.143 0 0 0 1.616 0l5.802-5.802a1.144 1.144 0 0 0 0-1.617M15.84 8.422 10.039 2.62a1.145 1.145 0 0 0-1.616 0L2.62 8.422a1.144 1.144 0 0 0 1.617 1.617l3.85-3.851v20.098a1.143 1.143 0 0 0 2.286 0V6.187l3.853 3.852a1.14 1.14 0 0 0 1.616-.001 1.14 1.14 0 0 0-.001-1.616"/></svg></span>}
                              </span>
                              {col.sortField && <button className="hs-th-kebab" onClick={(e) => { e.stopPropagation(); setColContextMenu({ field: col.sortField, x: e.clientX, y: e.clientY }) }}>
                                <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><circle cx="16" cy="4.14" r="2.29"/><circle cx="16" cy="15.57" r="2.29"/><circle cx="16" cy="27" r="2.29"/></svg>
                              </button>}
                              <div className="hs-col-resize-handle" onClick={(e) => e.stopPropagation()} />
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {socPagedData.map((p: any) => {
                          const platforms = String(p.platforms || '').split(',').filter(Boolean)
                          return (
                          <tr key={p.id} className={`hs-row${socialSelectedRows.has(p.id) ? ' selected' : ''}`}>
                            <td className="hs-td-check">
                              <input
                                type="checkbox"
                                checked={socialSelectedRows.has(p.id)}
                                onChange={() => {
                                  setSocialSelectedRows(prev => {
                                    const next = new Set(prev)
                                    next.has(p.id) ? next.delete(p.id) : next.add(p.id)
                                    return next
                                  })
                                }}
                              />
                            </td>
                            <td className="hs-td-name" style={{ position: 'sticky', left: 40, zIndex: 4, background: socialSelectedRows.has(p.id) ? '#eff6ff' : '#fff' }}>
                              <div className="hs-name-cell">
                                <div className="hs-avatar" style={{ background: '#eff6ff', color: '#3b82f6' }}>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2H4z"/></svg>
                                </div>
                                <span className="hs-name-link" onClick={() => setSocialPreview(socialPreview?.id === p.id ? null : p)}>
                                  {String(p.content || '').substring(0, 60) || 'Untitled post'}
                                </span>
                                <button className="hs-preview-btn" onClick={(e) => { e.stopPropagation(); setTableSettingsOpen(false); setSocialPreview(socialPreview?.id === p.id ? null : p) }}>Preview</button>
                              </div>
                            </td>
                            <td>
                              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                {platforms.map(pl => (
                                  <span key={pl} style={{ padding: '1px 6px', borderRadius: 4, fontSize: '0.62rem', fontWeight: 600, background: `${platformColors[pl] || '#64748b'}20`, color: platformColors[pl] || '#64748b' }}>
                                    {pl.charAt(0).toUpperCase() + pl.slice(1)}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td>
                              <span className="hs-status-badge" style={{
                                background: p.status === 'published' ? '#e8f5e9' : p.status === 'scheduled' ? '#fff3e0' : '#f1f5f9',
                                color: p.status === 'published' ? '#2e7d32' : p.status === 'scheduled' ? '#e65100' : '#64748b',
                              }}>
                                {String(p.status || 'draft').charAt(0).toUpperCase() + String(p.status || 'draft').slice(1)}
                              </span>
                            </td>
                            <td style={{ color: '#516f90', fontSize: '0.8rem' }}><span title="No tracking connected yet">—</span></td>
                            <td style={{ color: '#516f90', fontSize: '0.8rem' }}><span title="No tracking connected yet">—</span></td>
                            <td style={{ color: '#516f90', fontSize: '0.8rem' }}>{p.published_at ? formatDatePref(p.published_at) : p.scheduled_at ? formatDatePref(p.scheduled_at) : <span className="cell-empty">--</span>}</td>
                            <td>
                              <div style={{ display: 'flex', gap: '6px' }}>
                                {p.status === 'draft' ? (
                                  <button title="Updates status only — does not post to any network" onClick={() => publishSocialPost(Number(p.id)).then(() => { const opts: { status?: string } = {}; if (socialFilter !== 'all') opts.status = socialFilter; fetchSocialPosts(opts).then(res => { setSocialPosts(res.posts); setSocialPostsTotal(res.total) }).catch(() => {}) }).catch(() => {})}
                                    style={{ background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer', fontSize: '0.78rem' }}>Mark published</button>
                                ) : (
                                  <button onClick={() => updateSocialPost(Number(p.id), { status: 'draft' }).then(() => { const opts: { status?: string } = {}; if (socialFilter !== 'all') opts.status = socialFilter; fetchSocialPosts(opts).then(res => { setSocialPosts(res.posts); setSocialPostsTotal(res.total) }).catch(() => {}) }).catch(() => {})}
                                    style={{ background: 'none', border: 'none', color: '#7c98b6', cursor: 'pointer', fontSize: '0.78rem' }}>Back to draft</button>
                                )}
                                <button onClick={() => { if (confirm('Delete this post?')) deleteSocialPost(Number(p.id)).then(() => { const opts: { status?: string } = {}; if (socialFilter !== 'all') opts.status = socialFilter; fetchSocialPosts(opts).then(res => { setSocialPosts(res.posts); setSocialPostsTotal(res.total) }).catch(() => {}) }).catch(() => {}) }}
                                  style={{ background: 'none', border: 'none', color: '#7c98b6', cursor: 'pointer', fontSize: '0.78rem' }}
                                  onMouseEnter={e => { e.currentTarget.style.color = '#d94c53' }}
                                  onMouseLeave={e => { e.currentTarget.style.color = '#7c98b6' }}>
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              {/* Row 6 -- Pagination */}
              <div className="hs-pagination">
                <nav className="hs-paginator" aria-label="Pagination">
                  <button
                    className="hs-paginator-btn"
                    disabled={socCurrentPage <= 1}
                    onClick={() => setSocialPage((p) => p - 1)}
                    aria-label="Previous page"
                  >
                    <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95"/></svg>
                    <span>Prev</span>
                  </button>
                  {(() => {
                    if (socTotalPages <= 7) {
                      const pages: number[] = []
                      for (let i = 1; i <= socTotalPages; i++) pages.push(i)
                      return pages.map(p => (
                        <button key={p} className={`hs-paginator-num${p === socCurrentPage ? ' active' : ''}`} onClick={() => setSocialPage(p)}>{p}</button>
                      ))
                    }
                    const items: React.ReactNode[] = []
                    const addPage = (p: number) => items.push(
                      <button key={p} className={`hs-paginator-num${p === socCurrentPage ? ' active' : ''}`} onClick={() => setSocialPage(p)}>{p}</button>
                    )
                    const addEllipsis = (key: string) => items.push(
                      <span key={key} className="hs-paginator-ellipsis">...</span>
                    )
                    const shown = new Set<number>()
                    for (let i = 1; i <= Math.min(4, socTotalPages); i++) shown.add(i)
                    shown.add(socTotalPages)
                    for (let i = Math.max(1, socCurrentPage - 1); i <= Math.min(socTotalPages, socCurrentPage + 1); i++) shown.add(i)
                    const sorted = [...shown].sort((a, b) => a - b)
                    for (let i = 0; i < sorted.length; i++) {
                      if (i > 0 && sorted[i] - sorted[i - 1] > 1) addEllipsis(`e${i}`)
                      addPage(sorted[i])
                    }
                    return items
                  })()}
                  <button
                    className="hs-paginator-btn"
                    disabled={socCurrentPage >= socTotalPages}
                    onClick={() => setSocialPage((p) => p + 1)}
                    aria-label="Next page"
                  >
                    <span>Next</span>
                    <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M11 27.71c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62l9.48-9.48-9.48-9.47a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0L22.1 15.48c.45.45.45 1.17 0 1.62L11.81 27.39c-.22.22-.52.33-.81.33Z"/></svg>
                  </button>
                </nav>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', marginLeft: 'auto' }}><select value={tablePageSize} onChange={e => { setTablePageSize(Number(e.target.value) as any); setSocialPage(1) }} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #e2e8f0', fontSize: '0.82rem' }}><option value={100}>100</option><option value={200}>200</option><option value={500}>500</option></select><span>per page</span></div>
              </div>
            </div>

            {/* Settings panel (gear button) */}
            {tableSettingsOpen && (
              <div className="hs-settings-panel">
                <div className="hs-settings-header">
                  <span className="hs-settings-title">Table settings</span>
                  <button className="hs-settings-close" onClick={() => setTableSettingsOpen(false)}>
                    <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                  </button>
                </div>
                <div className="hs-settings-body">
                  <div className="hs-settings-group">
                    <label className="hs-settings-label">
                      Pagination
                      <span className="hs-info-icon" title="Number of rows shown per page">
                        <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg>
                      </span>
                    </label>
                    {([100, 200, 500] as const).map(size => (
                      <label className="hs-settings-radio" key={size}>
                        <input type="radio" name="socPageSize" checked={tablePageSize === size} onChange={() => { setTablePageSize(size); setSocialPage(1) }} />
                        <span className="hs-radio-indicator" />
                        <span>{size} per page</span>
                      </label>
                    ))}
                  </div>
                  <hr className="hs-settings-divider" />
                  <div className="hs-settings-group">
                    <label className="hs-settings-label">
                      Row height
                      <span className="hs-info-icon" title="Adjust the vertical spacing of table rows">
                        <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg>
                      </span>
                    </label>
                    {([
                      { value: 'small' as const, label: 'Compact' },
                      { value: 'medium' as const, label: 'Default' },
                      { value: 'large' as const, label: 'Comfortable' },
                    ]).map(opt => (
                      <label className="hs-settings-radio" key={opt.value}>
                        <input type="radio" name="socRowHeight" checked={tableRowHeight === opt.value} onChange={() => setTableRowHeight(opt.value)} />
                        <span className="hs-radio-indicator" />
                        <span>{opt.label}</span>
                      </label>
                    ))}
                  </div>
                  <hr className="hs-settings-divider" />
                  <div className="hs-settings-group">
                    <div className="hs-settings-toggle-row">
                      <label className="hs-settings-label">
                        Zebra striping
                        <span className="hs-info-icon" title="Alternate row background colors for easier reading">
                          <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg>
                        </span>
                      </label>
                      <button
                        className={`hs-toggle${tableZebraStriping ? ' on' : ''}`}
                        onClick={() => setTableZebraStriping(!tableZebraStriping)}
                        role="switch"
                        aria-checked={tableZebraStriping}
                      >
                        <span className="hs-toggle-track">
                          <span className="hs-toggle-on">ON</span>
                          <span className="hs-toggle-off">OFF</span>
                          <span className="hs-toggle-handle" />
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Preview sidebar */}
            {socialPreview && (
              <div className="hs-preview">
                <div className="hs-preview-header">
                  <span style={{ fontWeight: 600, fontSize: '0.88rem', color: '#33475b' }}>{String(socialPreview.content || '').substring(0, 40) || 'Untitled post'}</span>
                  <button className="hs-preview-close" onClick={() => setSocialPreview(null)}>&times;</button>
                </div>
                <div className="hs-preview-body">
                  <div className="hs-preview-section">
                    <h4>Post Details</h4>
                    <div className="hs-preview-field">
                      <label>Content</label>
                      <span>{String(socialPreview.content || '--')}</span>
                    </div>
                    <div className="hs-preview-field">
                      <label>Platforms</label>
                      <span>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {String(socialPreview.platforms || '').split(',').filter(Boolean).map((pl: string) => (
                            <span key={pl} style={{ padding: '1px 6px', borderRadius: 4, fontSize: '0.62rem', fontWeight: 600, background: `${platformColors[pl] || '#64748b'}20`, color: platformColors[pl] || '#64748b' }}>
                              {pl.charAt(0).toUpperCase() + pl.slice(1)}
                            </span>
                          ))}
                        </div>
                      </span>
                    </div>
                    <div className="hs-preview-field">
                      <label>Status</label>
                      <span>
                        <span className="hs-status-badge" style={{
                          background: socialPreview.status === 'published' ? '#e8f5e9' : socialPreview.status === 'scheduled' ? '#fff3e0' : '#f1f5f9',
                          color: socialPreview.status === 'published' ? '#2e7d32' : socialPreview.status === 'scheduled' ? '#e65100' : '#64748b',
                        }}>
                          {String(socialPreview.status || 'draft').charAt(0).toUpperCase() + String(socialPreview.status || 'draft').slice(1)}
                        </span>
                      </span>
                    </div>
                    <div className="hs-preview-field">
                      <label>Likes</label>
                      <span title="No tracking connected yet">—</span>
                    </div>
                    <div className="hs-preview-field">
                      <label>Comments</label>
                      <span title="No tracking connected yet">—</span>
                    </div>
                    <div className="hs-preview-field">
                      <label>Published At</label>
                      <span>{socialPreview.published_at ? formatDatePref(socialPreview.published_at) : '--'}</span>
                    </div>
                    <div className="hs-preview-field">
                      <label>Scheduled At</label>
                      <span>{socialPreview.scheduled_at ? formatDatePref(socialPreview.scheduled_at) : '--'}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Create post modal */}
            {showCreatePost && (<>
              <div className="create-panel-overlay" onClick={() => setShowCreatePost(false)} />
              <div className="create-panel" onClick={(e) => e.stopPropagation()}>
                <div className="create-panel-header">
                  <h2>Create Social Post</h2>
                  <button className="create-panel-close" onClick={() => setShowCreatePost(false)} aria-label="Close">
                    <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                  </button>
                </div>
                <div className="create-panel-body">
                  <div className="create-panel-field">
                    <label>Platforms *</label>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {['facebook', 'instagram', 'linkedin', 'twitter'].map(pl => {
                        const isActive = newPostPlatforms.includes(pl);
                        const colors: Record<string, string> = { facebook: '#1877F2', instagram: '#E4405F', linkedin: '#0A66C2', twitter: '#1DA1F2' };
                        return (
                          <button key={pl} style={{ padding: '6px 14px', borderRadius: 6, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', border: isActive ? `2px solid ${colors[pl]}` : '1px solid #e2e8f0', background: isActive ? `${colors[pl]}15` : '#fff', color: isActive ? colors[pl] : '#64748b' }} onClick={() => setNewPostPlatforms(prev => isActive ? prev.filter(x => x !== pl) : [...prev, pl])}>
                            {pl.charAt(0).toUpperCase() + pl.slice(1)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="create-panel-field">
                    <label>Content *</label>
                    <textarea rows={6} value={newPostContent} onChange={e => setNewPostContent(e.target.value)} placeholder="Write your post content..." style={{ resize: 'vertical' }} />
                    <div style={{ textAlign: 'right', fontSize: '0.68rem', color: newPostContent.length > 280 ? '#d32f2f' : '#94a3b8', marginTop: 4 }}>{newPostContent.length} chars</div>
                  </div>
                  <div className="create-panel-field">
                    <label>Schedule (optional)</label>
                    <input type="datetime-local" value={newPostSchedule} onChange={e => setNewPostSchedule(e.target.value)} />
                  </div>
                </div>
                <div className="create-panel-footer">
                  <button className="cp-btn-primary" disabled={!newPostContent.trim() || newPostPlatforms.length === 0} onClick={() => { createSocialPost({ content: newPostContent, platforms: newPostPlatforms.join(','), status: newPostSchedule ? 'scheduled' : 'draft', scheduled_at: newPostSchedule || null }).then(() => { setShowCreatePost(false); { const opts: { status?: string } = {}; if (socialFilter !== 'all') opts.status = socialFilter; fetchSocialPosts(opts).then(res => { setSocialPosts(res.posts); setSocialPostsTotal(res.total) }).catch(() => {}) } }) }}>{newPostSchedule ? 'Schedule post' : 'Save as draft'}</button>
                  <button className="cp-btn-secondary" onClick={() => setShowCreatePost(false)}>Cancel</button>
                </div>
              </div>
            </>)}
          </div>
          )
        })()}

        {/* ========== Forms View (Full 6-Row Template) ========== */}
        {activeView === 'forms' && !showFormBuilder && (() => {
          const formsPageSize = tablePageSize
          const formsPageCount = Math.max(1, Math.ceil(formsTotal / formsPageSize))
          const formsPagedData = formsData.slice((formsPage - 1) * formsPageSize, formsPage * formsPageSize)
          const allFormsSelected = formsPagedData.length > 0 && formSelectedRows.size === formsPagedData.length
          const toggleAllFormRows = () => {
            if (allFormsSelected) { setFormSelectedRows(new Set()) }
            else { setFormSelectedRows(new Set(formsPagedData.map((f: any) => Number(f.id)))) }
          }
          const toggleFormRow = (id: number) => {
            setFormSelectedRows(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })
          }
          const toggleFormsSort = (field: string) => {
            setFormsSort(prev => prev.field === field ? { field, order: prev.order === 'ASC' ? 'DESC' : 'ASC' } : { field, order: field === 'name' ? 'ASC' : 'DESC' })
            setFormsPage(1)
          }
          const onFormsResizeStart = (col: string, e: React.MouseEvent) => {
            e.preventDefault(); e.stopPropagation()
            const startX = e.clientX; const startW = (formsColWidths as any)[col]
            const onMove = (ev: MouseEvent) => { const w = Math.max(60, startW + ev.clientX - startX); setFormsColWidths(prev => ({ ...prev, [col]: w })) }
            const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp) }
            document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp)
          }
          const sortArrowSvg = <svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M29.38 21.96a1.147 1.147 0 0 0-1.617 0l-3.85 3.852V5.714a1.143 1.143 0 0 0-2.286 0v20.099l-3.853-3.852a1.144 1.144 0 0 0-1.615 1.617l5.803 5.802a1.143 1.143 0 0 0 1.616 0l5.802-5.802a1.144 1.144 0 0 0 0-1.617M15.84 8.422 10.039 2.62a1.145 1.145 0 0 0-1.616 0L2.62 8.422a1.144 1.144 0 0 0 1.617 1.617l3.85-3.851v20.098a1.143 1.143 0 0 0 2.286 0V6.187l3.853 3.852a1.14 1.14 0 0 0 1.616-.001 1.14 1.14 0 0 0-.001-1.616"/></svg>
          return (
          <div className="hs-contacts">
            {/* Row 1: Topbar */}
            <div className="hs-topbar">
              <div className="hs-topbar-left">
                <div className="hs-obj-switcher-wrap" ref={objSwitcherRef}>
                  <button className={`hs-obj-switcher${objSwitcherOpen ? ' open' : ''}`} onClick={() => { setObjSwitcherOpen(!objSwitcherOpen); setObjSwitcherSearch('') }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M3 15h18"/><path d="M9 3v18"/></svg>
                    <span>Forms</span>
                    <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                  </button>
                  {objSwitcherOpen && (
                    <div className="hs-obj-panel">
                      <div className="hs-obj-search-wrap">
                        <svg className="hs-obj-search-icon" width="13" height="13" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                        <input className="hs-obj-search" placeholder="Search" value={objSwitcherSearch} onChange={(e) => setObjSwitcherSearch(e.target.value)} autoFocus />
                      </div>
                      <ul className="hs-obj-list" role="listbox">
                        {[
                          { label: 'Email', view: 'marketing-email' as ViewMode },
                          { label: 'Ads', view: 'ads' as ViewMode },
                          { label: 'Social', view: 'social' as ViewMode },
                          { label: 'Forms', view: 'forms' as ViewMode },
                          { label: 'Campaigns', view: 'campaigns' as ViewMode },
                          { label: 'Landing Pages', view: 'landing-pages' as ViewMode },
                        ].filter(item => !objSwitcherSearch || item.label.toLowerCase().includes(objSwitcherSearch.toLowerCase())).map(item => (
                          <li key={item.view} className={`hs-obj-item${item.view === activeView ? ' active' : ''}`} role="option" aria-selected={item.view === activeView}
                            onClick={() => { setActiveView(item.view); setObjSwitcherOpen(false); setObjSwitcherSearch('') }}>
                            {item.label}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="hs-topbar-sep" />
                <div className="hs-view-tabs">
                  <div className="hs-view-tab active">
                    <span className="hs-tab-label">All forms</span>
                    {formsTotal > 0 && <span className="hs-tab-count">{formsTotal.toLocaleString()}</span>}
                    <span className="hs-tab-kebab" onClick={(e) => { e.stopPropagation() }}>
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>
                    </span>
                  </div>
                  <div className="hs-topbar-sep" />
                  <div className="hs-view-add-wrap" ref={viewAddMenuRef} style={{ position: 'relative', display: 'inline-flex', alignSelf: 'center' }}>
                    <button className={`hs-quick-add-btn${viewAddMenuOpen ? ' active' : ''}`} title="Create or add view" type="button" aria-label="Create or add view"
                      onClick={() => { setViewAddMenuOpen(!viewAddMenuOpen); setViewAddMenuMode('menu') }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                    </button>
                    {viewAddMenuOpen && viewAddMenuMode === 'menu' && (
                      <div className="hs-view-add-dropdown">
                        <button className="hs-view-add-item" onClick={() => setViewAddMenuMode('type')}>Create new view</button>
                        <button className="hs-view-add-item" onClick={() => { setViewAddMenuOpen(false); setViewAddMenuMode('menu') }}>Add view</button>
                      </div>
                    )}
                    {viewAddMenuOpen && viewAddMenuMode === 'type' && (
                      <div className="hs-view-add-dropdown hs-view-type-picker">
                        <button className="hs-view-type-btn" onClick={() => { setViewAddMenuOpen(false); setViewAddMenuMode('menu') }}>
                          <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                          <span>Table</span>
                        </button>
                        <button className="hs-view-type-btn" disabled style={{ opacity: 0.4 }}><svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M11.43 11.43h-8a1.14 1.14 0 1 1 0-2.28h8a1.14 1.14 0 1 1 0 2.28M28.57 11.43h-8a1.14 1.14 0 1 1 0-2.28h8a1.14 1.14 0 1 1 0 2.28"/><path d="M9.14 29.71H5.71c-1.89 0-3.43-1.54-3.43-3.43V5.71c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v14.86c0 1.89-1.54 3.43-3.43 3.43h-3.43c-1.89 0-3.43-1.54-3.43-3.43v-1.14h-6.86v6.86c0 1.89-1.54 3.43-3.43 3.43Z"/></svg><span>Board</span></button>
                        <button className="hs-view-type-btn" disabled style={{ opacity: 0.4 }}><svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M7.57 24.71c-.63 0-1.14-.51-1.14-1.14v-6.86a1.14 1.14 0 1 1 2.28 0v6.86c0 .63-.51 1.14-1.14 1.14M12.14 24.71c-.63 0-1.14-.51-1.14-1.14v-16c0-.63.51-1.14 1.14-1.14h4.57a1.14 1.14 0 1 1 0 2.28h-3.43v14.86c0 .63-.51 1.14-1.14 1.14"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v20.57c0 1.89-1.54 3.43-3.43 3.43Z"/></svg><span>Report</span></button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="hs-topbar-right">
                {/* Create form — direct action (Import removed) */}
                <button className="hs-add-contacts-btn" onClick={() => { setEditingFormId(null);setFormBuilderName('Untitled Form');setFormBuilderDesc('');setFormBuilderFields([{id:'f1',type:'text',label:'First Name',placeholder:'Enter first name',required:true,width:'half'},{id:'f2',type:'text',label:'Last Name',placeholder:'Enter last name',required:true,width:'half'},{id:'f3',type:'email',label:'Email',placeholder:'Enter email address',required:true,width:'full'}]);setFormBuilderSelected(null);setFormThankYou('Thank you for your submission!');setFormNotifyEmails('');setFormBuilderTab('fields');setShowFormBuilder(true) }}>
                  Create form
                </button>
              </div>
            </div>

            {/* Row 2: Toolbar */}
            <div className="hs-toolbar">
              <div className="hs-toolbar-left">
                <div className="hs-search-wrap">
                  <svg className="hs-search-icon" width="14" height="14" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M28.14 29.29c-.29 0-.58-.11-.81-.33l-7.58-7.58a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l7.58 7.58a1.14 1.14 0 0 1-.81 1.95"/><path d="M13.29 24.71c-6.3 0-11.43-5.13-11.43-11.43S6.98 1.86 13.29 1.86s11.43 5.13 11.43 11.43-5.13 11.43-11.43 11.43Zm0-20.57c-5.04 0-9.14 4.1-9.14 9.14s4.1 9.14 9.14 9.14 9.14-4.1 9.14-9.14-4.1-9.14-9.14-9.14"/></svg>
                  <input className="hs-search-input" placeholder="Search" value={formsSearch} onChange={(e) => setFormsSearch(e.target.value)} />
                  {formsSearch && <button className="hs-search-clear" onClick={() => setFormsSearch('')}>&times;</button>}
                </div>
                <div className="hs-tb-sep" />
                <div className="hs-viewtype-group">
                  <button className="hs-tb-btn hs-viewtype-btn" disabled title="Table view">
                    <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                    <span>Table</span>
                  </button>
                  <button className={`hs-tb-btn hs-settings-btn${formsSettingsOpen ? ' active' : ''}`} onClick={() => setFormsSettingsOpen(!formsSettingsOpen)} title={formsSettingsOpen ? 'Close settings' : 'Table settings'}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                  </button>
                </div>
                <div className="hs-tb-sep" />
                <button className="hs-tb-btn hs-tb-btn-bordered disabled" disabled title="Edit columns is not available yet">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="18" rx="1"/><rect x="14" y="3" width="7" height="18" rx="1"/></svg>
                  <span>Edit columns</span>
                </button>
                <div className="hs-tb-sep" />
                <button className="hs-tb-btn hs-tb-btn-bordered disabled" disabled title="Filters is not available yet">
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M30.71 16.86H13.57a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28M6.71 16.86H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M10.14 20.29c-2.52 0-4.57-2.05-4.57-4.57s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M6.71 6.57H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M30.71 6.57H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M18.14 6.57H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 10C19.05 10 17 7.95 17 5.43S19.05.86 21.57.86s4.57 2.05 4.57 4.57S24.09 10 21.57 10m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M30.71 27.14H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M18.14 27.14H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 30.57C19.05 30.57 17 28.52 17 26s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29"/></svg>
                  <span>Filters</span>
                </button>
                <div className="hs-tb-sep" />
                <button className="hs-tb-btn hs-tb-btn-bordered disabled" disabled title="Export is not available yet">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  <span>Export</span>
                </button>
              </div>
              <div className="hs-toolbar-right">
                <div className="hs-tb-sep" />
                <button className="hs-tb-btn hs-tb-btn-bordered disabled" disabled title="Clone view is not available yet">
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M25.86 29.29H12.15c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-1.89 1.54-3.43 3.43-3.43h13.71c1.89 0 3.43 1.54 3.43 3.43v13.71c0 1.89-1.54 3.43-3.43 3.43M12.14 11c-.63 0-1.14.51-1.14 1.14v13.71c0 .63.51 1.14 1.14 1.14h13.71c.63 0 1.14-.51 1.14-1.14V12.14c0-.63-.51-1.14-1.14-1.14z"/><path d="M5.29 22.43c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43H19c1.89 0 3.43 1.54 3.43 3.43a1.14 1.14 0 1 1-2.28 0c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14V19c0 .63.51 1.14 1.14 1.14a1.14 1.14 0 1 1 0 2.28Z"/></svg>
                </button>
                <button className="hs-tb-btn hs-tb-btn-bordered disabled" disabled title="Save view">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                </button>
              </div>
            </div>

            {/* (Division filter for the scanner moved into the scanner filter
    toolbar as a multi-select chip+popover — see the Vendor/Project pickers.) */}
{/* Row 3: Quick Filters */}
            <div className="hs-quick-filters">
              <button className={`hs-qf-btn hs-qf-trigger${formsFilter !== 'all' ? ' has-value' : ''}`}
                onClick={() => { const vals = ['all', 'active', 'inactive']; const idx = vals.indexOf(formsFilter); setFormsFilter(vals[(idx + 1) % vals.length]); setFormsPage(1) }}>
                <span>{formsFilter === 'active' ? 'Active' : formsFilter === 'inactive' ? 'Inactive' : 'Status'}</span>
                {formsFilter !== 'all' && <span className="hs-qf-clear" onClick={(e) => { e.stopPropagation(); setFormsFilter('all'); setFormsPage(1) }}>&times;</span>}
                {formsFilter === 'all' && <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>}
              </button>
              <button className={`hs-qf-btn hs-qf-trigger${formsSort.field !== 'created_at' ? ' has-value' : ''}`}
                onClick={() => { const fields = ['created_at', 'name', 'submissions_count']; const idx = fields.indexOf(formsSort.field); const next = fields[(idx + 1) % fields.length]; setFormsSort({ field: next, order: next === 'name' ? 'ASC' : 'DESC' }); setFormsPage(1) }}>
                <span>Sort: {{ created_at: 'Created', name: 'Name', submissions_count: 'Submissions' }[formsSort.field] || 'Created'}</span>
                <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
              </button>
            </div>

            {/* Row 4-6: Content with table + settings */}
            <div className={`hs-content${formsSettingsOpen ? ' with-settings' : ''}`}>
              <div className="hs-table-area">
                {/* Selection bar */}
                {formSelectedRows.size > 0 && (
                  <div className="hs-selection-bar">
                    <span className="hs-sel-count">{formSelectedRows.size} selected</span>
                    <div className="hs-sel-sep" />

                    <button className="hs-sel-btn" style={{ color: '#dc2626' }} onClick={() => {
                      if (confirm(`Delete ${formSelectedRows.size} form(s)?`)) {
                        Promise.all([...formSelectedRows].map(id => deleteForm(id))).then(() => { { const opts: { status?: string; search?: string } = {}; if (formsFilter !== 'all') opts.status = formsFilter; if (formsSearch) opts.search = formsSearch; fetchForms(opts).then(res => { setFormsData(res.forms); setFormsTotal(res.total) }).catch(() => {}) }; setFormSelectedRows(new Set()) }).catch(() => {})
                      }
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                      Delete
                    </button>
                    <button className="hs-sel-close" onClick={() => setFormSelectedRows(new Set())} title="Deselect all">&times;</button>
                  </div>
                )}
                <div className="hs-table-scroll">
                  <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                      <col style={{ width: 40 }} />
                      <col style={{ width: formsColWidths.name }} />
                      <col style={{ width: formsColWidths.submissions }} />
                      <col style={{ width: formsColWidths.status }} />
                      <col style={{ width: formsColWidths.type }} />
                      <col style={{ width: formsColWidths.created }} />
                      <col style={{ width: formsColWidths.actions }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th className="hs-th-check"><input type="checkbox" checked={allFormsSelected} onChange={toggleAllFormRows} /></th>
                        <th className="hs-th-resizable hs-th-sortable hs-th-frozen" style={{ position: 'sticky', left: 40, top: 0, zIndex: 15, background: '#f8fafc', boxShadow: '2px 0 4px rgba(0,0,0,0.06)' }} onClick={() => toggleFormsSort('name')} onContextMenu={(e) => { e.preventDefault(); setColContextMenu({ field: 'name', x: e.clientX, y: e.clientY }) }}>
                          <span className="hs-th-content">
                            Form Name
                            {formsSort.field === 'name' && <span className="hs-sort-arrow">{formsSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                            <span className="hs-th-hover-sort">{sortArrowSvg}</span>
                          </span>
                          <button className="hs-th-kebab" onClick={(e) => { e.stopPropagation(); setColContextMenu({ field: 'name', x: e.clientX, y: e.clientY }) }}>
                            <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><circle cx="16" cy="4.14" r="2.29"/><circle cx="16" cy="15.57" r="2.29"/><circle cx="16" cy="27" r="2.29"/></svg>
                          </button>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onFormsResizeStart('name', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                        <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleFormsSort('submissions_count')} onContextMenu={(e) => { e.preventDefault(); setColContextMenu({ field: 'submissions_count', x: e.clientX, y: e.clientY }) }}>
                          <span className="hs-th-content">
                            Submissions
                            {formsSort.field === 'submissions_count' && <span className="hs-sort-arrow">{formsSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                            <span className="hs-th-hover-sort">{sortArrowSvg}</span>
                          </span>
                          <button className="hs-th-kebab" onClick={(e) => { e.stopPropagation(); setColContextMenu({ field: 'submissions_count', x: e.clientX, y: e.clientY }) }}>
                            <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><circle cx="16" cy="4.14" r="2.29"/><circle cx="16" cy="15.57" r="2.29"/><circle cx="16" cy="27" r="2.29"/></svg>
                          </button>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onFormsResizeStart('submissions', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                        <th className="hs-th-resizable">
                          <span className="hs-th-content">Status</span>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onFormsResizeStart('status', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                        <th className="hs-th-resizable">
                          <span className="hs-th-content">Type</span>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onFormsResizeStart('type', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                        <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleFormsSort('created_at')} onContextMenu={(e) => { e.preventDefault(); setColContextMenu({ field: 'created_at', x: e.clientX, y: e.clientY }) }}>
                          <span className="hs-th-content">
                            Created
                            {formsSort.field === 'created_at' && <span className="hs-sort-arrow">{formsSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                            <span className="hs-th-hover-sort">{sortArrowSvg}</span>
                          </span>
                          <button className="hs-th-kebab" onClick={(e) => { e.stopPropagation(); setColContextMenu({ field: 'created_at', x: e.clientX, y: e.clientY }) }}>
                            <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><circle cx="16" cy="4.14" r="2.29"/><circle cx="16" cy="15.57" r="2.29"/><circle cx="16" cy="27" r="2.29"/></svg>
                          </button>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onFormsResizeStart('created', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                        <th><span className="hs-th-content">Actions</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      {formsLoading ? (
                        <tr><td colSpan={8} className="hs-loading-cell"><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}><DotLottieReact src="/loading.lottie" loop autoplay style={{ width: 80, height: 80 }} /><span style={{ fontSize: '0.82rem', color: '#7c98b6', marginTop: 8 }}>Loading forms...</span></div></td></tr>
                      ) : formsPagedData.length === 0 ? (
                        <tr><td colSpan={8}>
                          <div style={{ padding: '60px 40px', textAlign: 'center' }}>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" style={{ marginBottom: 12 }}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/></svg>
                            <div style={{ color: '#64748b', fontSize: '0.88rem', fontWeight: 600, marginBottom: 4 }}>No forms yet</div>
                            <div style={{ color: '#94a3b8', fontSize: '0.78rem' }}>Create your first form to start capturing leads.</div>
                          </div>
                        </td></tr>
                      ) : formsPagedData.map((f: any) => (
                        <tr key={String(f.id)} className={`hs-row${formSelectedRows.has(Number(f.id)) ? ' selected' : ''}`}>
                          <td className="hs-td-check"><input type="checkbox" checked={formSelectedRows.has(Number(f.id))} onChange={() => toggleFormRow(Number(f.id))} /></td>
                          <td className="hs-td-name" style={{ position: 'sticky', left: 40, zIndex: 2, background: formSelectedRows.has(Number(f.id)) ? '#eff6ff' : '#fff' }}>
                            <div className="hs-name-cell">
                              <span className="hs-name-link" onClick={() => {
                                setEditingFormId(Number(f.id)); setFormBuilderName(String(f.name || '')); setFormBuilderDesc(String(f.description || ''));
                                try { setFormBuilderFields(JSON.parse(String(f.fields_json || '[]'))) } catch { setFormBuilderFields([]) }
                                setFormThankYou(String(f.thank_you_message || 'Thank you!')); setFormNotifyEmails(String(f.notification_emails || ''));
                                setFormBuilderSelected(null); setFormBuilderTab('fields'); setShowFormBuilder(true)
                              }}>{String(f.name || '')}</span>
                            </div>
                          </td>
                          <td style={{ fontWeight: 500, color: '#33475b' }}>{Number(f.submissions_count || 0)}</td>
                          <td><span className="hs-status-badge" style={{ background: f.is_active ? '#e8f5e9' : '#f1f5f9', color: f.is_active ? '#2e7d32' : '#64748b' }}>{f.is_active ? 'Active' : 'Inactive'}</span></td>
                          <td style={{ color: '#516f90', fontSize: '0.78rem' }}>Form</td>
                          <td style={{ color: '#516f90', fontSize: '0.75rem' }}>{f.created_at ? formatDatePref(String(f.created_at)) : '--'}</td>
                          <td>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button style={{ background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer', fontSize: '0.72rem' }} onClick={() => {
                                setFormSubmissionsFormId(Number(f.id)); setShowFormSubmissions(true);
                                fetchFormSubmissions(Number(f.id)).then(res => { setFormSubmissionsData(res.submissions); setFormSubmissionsTotal(res.total) })
                              }}>Submissions</button>
                              <button style={{ background: 'none', border: 'none', color: '#d32f2f', cursor: 'pointer', fontSize: '0.72rem' }} onClick={() => {
                                if (confirm('Delete?')) deleteForm(Number(f.id)).then(() => { const opts: { status?: string; search?: string } = {}; if (formsFilter !== 'all') opts.status = formsFilter; if (formsSearch) opts.search = formsSearch; fetchForms(opts).then(res => { setFormsData(res.forms); setFormsTotal(res.total) }).catch(() => {}) })
                              }}>Delete</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="hs-pagination">
                  <nav className="hs-paginator" aria-label="Pagination">
                    <button className="hs-paginator-btn" disabled={formsPage <= 1} onClick={() => setFormsPage(p => p - 1)} aria-label="Previous page">
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95"/></svg>
                      <span>Prev</span>
                    </button>
                    {(() => {
                      if (formsPageCount <= 7) {
                        const pages: number[] = []; for (let i = 1; i <= formsPageCount; i++) pages.push(i)
                        return pages.map(p => <button key={p} className={`hs-paginator-num${p === formsPage ? ' active' : ''}`} onClick={() => setFormsPage(p)}>{p}</button>)
                      }
                      const items: React.ReactNode[] = []
                      const shown = new Set<number>()
                      for (let i = 1; i <= Math.min(4, formsPageCount); i++) shown.add(i)
                      shown.add(formsPageCount)
                      for (let i = Math.max(1, formsPage - 1); i <= Math.min(formsPageCount, formsPage + 1); i++) shown.add(i)
                      const sorted = [...shown].sort((a, b) => a - b)
                      for (let i = 0; i < sorted.length; i++) {
                        if (i > 0 && sorted[i] - sorted[i - 1] > 1) items.push(<span key={`e${i}`} className="hs-paginator-ellipsis">...</span>)
                        items.push(<button key={sorted[i]} className={`hs-paginator-num${sorted[i] === formsPage ? ' active' : ''}`} onClick={() => setFormsPage(sorted[i])}>{sorted[i]}</button>)
                      }
                      return items
                    })()}
                    <button className="hs-paginator-btn" disabled={formsPage >= formsPageCount} onClick={() => setFormsPage(p => p + 1)} aria-label="Next page">
                      <span>Next</span>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M11 27.71c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62l9.48-9.48-9.48-9.47a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0L22.1 15.48c.45.45.45 1.17 0 1.62L11.81 27.39c-.22.22-.52.33-.81.33Z"/></svg>
                    </button>
                  </nav>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', marginLeft: 'auto' }}><select value={tablePageSize} onChange={e => { setTablePageSize(Number(e.target.value) as any); setFormsPage(1) }} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #e2e8f0', fontSize: '0.82rem' }}><option value={100}>100</option><option value={200}>200</option><option value={500}>500</option></select><span>per page</span></div>
                </div>
              </div>

              {/* Settings Panel */}
              {formsSettingsOpen && (
                <div className="hs-settings-panel">
                  <div className="hs-settings-header">
                    <span className="hs-settings-title">Table settings</span>
                    <button className="hs-settings-close" onClick={() => setFormsSettingsOpen(false)}>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                    </button>
                  </div>
                  <div className="hs-settings-body">
                    <div className="hs-settings-group">
                      <label className="hs-settings-label">Pagination
                        <span className="hs-info-icon" title="Number of rows shown per page"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span>
                      </label>
                      {([100, 200, 500] as const).map(size => (
                        <label className="hs-settings-radio" key={size}>
                          <input type="radio" name="formsPageSize" checked={tablePageSize === size} onChange={() => { setTablePageSize(size); setFormsPage(1) }} />
                          <span className="hs-radio-indicator" /><span>{size} per page</span>
                        </label>
                      ))}
                    </div>
                    <hr className="hs-settings-divider" />
                    <div className="hs-settings-group">
                      <label className="hs-settings-label">Row height
                        <span className="hs-info-icon" title="Adjust the vertical spacing of table rows"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span>
                      </label>
                      {([{ value: 'small' as const, label: 'Compact' }, { value: 'medium' as const, label: 'Default' }, { value: 'large' as const, label: 'Comfortable' }]).map(opt => (
                        <label className="hs-settings-radio" key={opt.value}>
                          <input type="radio" name="formsRowHeight" checked={tableRowHeight === opt.value} onChange={() => setTableRowHeight(opt.value)} />
                          <span className="hs-radio-indicator" /><span>{opt.label}</span>
                        </label>
                      ))}
                    </div>
                    <hr className="hs-settings-divider" />
                    <div className="hs-settings-group">
                      <div className="hs-settings-toggle-row">
                        <label className="hs-settings-label">Zebra striping
                          <span className="hs-info-icon" title="Alternate row background colors for easier reading"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span>
                        </label>
                        <button className={`hs-toggle${tableZebraStriping ? ' on' : ''}`} onClick={() => setTableZebraStriping(!tableZebraStriping)} role="switch" aria-checked={tableZebraStriping}>
                          <span className="hs-toggle-track"><span className="hs-toggle-on">ON</span><span className="hs-toggle-off">OFF</span><span className="hs-toggle-handle" /></span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Form Submissions modal */}
            {showFormSubmissions && (<>
              <div className="create-panel-overlay" onClick={() => setShowFormSubmissions(false)} />
              <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: '80%', maxWidth: 900, maxHeight: 'calc(100vh - 60px)', background: '#fff', borderRadius: 12, boxShadow: '0 8px 30px rgba(0,0,0,0.18)', zIndex: 201, overflow: 'hidden', display: 'flex', flexDirection: 'column', animation: 'fadeIn 150ms ease' }}>
                <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 style={{ fontSize: '0.92rem', fontWeight: 700, color: '#1e293b' }}>Form Submissions ({formSubmissionsTotal})</h3>
                  <button className="create-panel-close" onClick={() => setShowFormSubmissions(false)} aria-label="Close">
                    <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                  </button>
                </div>
                <div style={{ flex: 1, overflow: 'auto' }}>
                  {formSubmissionsData.length === 0 ? <div style={{ padding: 40, textAlign: 'center', color: '#7c98b6' }}>No submissions yet.</div> : (
                    <table className="hs-table" style={{ width: '100%' }}><thead><tr><th>Submitted</th><th>Name</th><th>Email</th><th>Data</th><th>Status</th></tr></thead><tbody>{formSubmissionsData.map((s: Record<string, unknown>) => { let data: Record<string, unknown> = {}; try { data = JSON.parse(String(s.submission_data_json || '{}')) } catch {}; return (<tr key={String(s.id)}><td style={{ fontSize: '0.75rem', color: '#516f90' }}>{s.submitted_at ? String(s.submitted_at).slice(0, 16).replace('T', ' ') : '--'}</td><td style={{ fontWeight: 500, color: '#33475b' }}>{String(s.preferred_name || data['First Name'] || data['first_name'] || '--')}</td><td style={{ color: '#1976d2', fontSize: '0.78rem' }}>{String(s.preferred_email || data['Email'] || data['email'] || '--')}</td><td style={{ fontSize: '0.72rem', color: '#64748b', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{Object.entries(data).map(([k, v]) => `${k}: ${v}`).join(', ')}</td><td><span className="hs-status-badge" style={{ background: '#e3f2fd', color: '#1565c0' }}>{String(s.status || 'new').charAt(0).toUpperCase() + String(s.status || 'new').slice(1)}</span></td></tr>) })}</tbody></table>
                  )}
                </div>
              </div>
            </>)}
          </div>
          )
        })()}

        {/* ========== Form Builder ========== */}
        {activeView === 'forms' && showFormBuilder && (
          <div style={{display:'flex',flexDirection:'column',height:'100%',background:'#f5f5f5'}}>
            <div style={{display:'flex',alignItems:'center',padding:'0 16px',height:48,background:'#fff',borderBottom:'1px solid #e2e8f0',gap:12,flexShrink:0}}>
              <button style={{background:'none',border:'1px solid #cbd5e1',borderRadius:4,padding:'5px 12px',fontSize:'0.8rem',color:'#33475b',cursor:'pointer'}} onClick={()=>setShowFormBuilder(false)}>Back</button>
              <div style={{flex:1,textAlign:'center'}}><input style={{border:'none',textAlign:'center',fontSize:'0.88rem',fontWeight:600,color:'#1e293b',background:'transparent',outline:'none',width:300}} value={formBuilderName} onChange={e=>setFormBuilderName(e.target.value)}/></div>
              <button style={{background:'none',border:'1px solid #cbd5e1',borderRadius:4,padding:'5px 12px',fontSize:'0.8rem',color:'#33475b',cursor:'pointer'}} onClick={()=>{const payload={name:formBuilderName,description:formBuilderDesc,fields_json:JSON.stringify(formBuilderFields),thank_you_message:formThankYou,notification_emails:formNotifyEmails};const action=editingFormId?updateForm(editingFormId,payload):createForm(payload);action.then(()=>{setShowFormBuilder(false);{ const opts: { status?: string; search?: string } = {}; if (formsFilter !== 'all') opts.status = formsFilter; if (formsSearch) opts.search = formsSearch; fetchForms(opts).then(res => { setFormsData(res.forms); setFormsTotal(res.total) }).catch(() => {}) }})}}>Save</button>
              <button title="No public form endpoint yet — submissions arrive only via the internal viewer" style={{background:'#ff7a59',color:'#fff',border:'none',borderRadius:4,padding:'5px 16px',fontSize:'0.8rem',fontWeight:600,cursor:'pointer'}} onClick={()=>{const payload={name:formBuilderName,description:formBuilderDesc,fields_json:JSON.stringify(formBuilderFields),thank_you_message:formThankYou,notification_emails:formNotifyEmails,is_active:1};const action=editingFormId?updateForm(editingFormId,payload):createForm(payload);action.then(()=>{setShowFormBuilder(false);{ const opts: { status?: string; search?: string } = {}; if (formsFilter !== 'all') opts.status = formsFilter; if (formsSearch) opts.search = formsSearch; fetchForms(opts).then(res => { setFormsData(res.forms); setFormsTotal(res.total) }).catch(() => {}) }})}}>Save & activate</button>
            </div>
            <div style={{display:'flex',flex:1,overflow:'hidden'}}>
              <div style={{width:280,background:'#fff',borderRight:'1px solid #e2e8f0',display:'flex',flexDirection:'column',overflow:'hidden'}}>
                <div style={{display:'flex',borderBottom:'1px solid #e2e8f0'}}><button style={{flex:1,padding:'10px 0',fontSize:'0.78rem',fontWeight:600,border:'none',cursor:'pointer',borderBottom:formBuilderTab==='fields'?'2px solid var(--accent)':'2px solid transparent',background:'none',color:formBuilderTab==='fields'?'var(--accent)':'#64748b'}} onClick={()=>setFormBuilderTab('fields')}>Fields</button><button style={{flex:1,padding:'10px 0',fontSize:'0.78rem',fontWeight:600,border:'none',cursor:'pointer',borderBottom:formBuilderTab==='settings'?'2px solid var(--accent)':'2px solid transparent',background:'none',color:formBuilderTab==='settings'?'var(--accent)':'#64748b'}} onClick={()=>setFormBuilderTab('settings')}>Settings</button></div>
                <div style={{flex:1,overflowY:'auto',padding:12}}>
                  {formBuilderTab==='fields'&&(<><div style={{fontSize:'0.68rem',fontWeight:700,color:'#94a3b8',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:8}}>Add Field</div><div style={{display:'flex',flexDirection:'column',gap:6}}>{[{type:'text',label:'Single-line Text',icon:'Aa'},{type:'email',label:'Email',icon:'@'},{type:'number',label:'Number',icon:'#'},{type:'tel',label:'Phone',icon:'Ph'},{type:'textarea',label:'Multi-line Text',icon:'T+'},{type:'select',label:'Dropdown',icon:'v'},{type:'radio',label:'Radio Buttons',icon:'o'},{type:'checkbox',label:'Checkbox',icon:'[]'},{type:'date',label:'Date',icon:'D'},{type:'file',label:'File Upload',icon:'F'}].map(ft=>(<button key={ft.type} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 10px',border:'1px dashed #cbd5e1',borderRadius:6,background:'#fafbfc',cursor:'pointer',fontSize:'0.78rem',color:'#33475b',fontWeight:500,textAlign:'left'}} onClick={()=>{setFormBuilderFields([...formBuilderFields,{id:'f'+Date.now(),type:ft.type,label:ft.label,placeholder:'',required:false,width:'full',options:ft.type==='select'||ft.type==='radio'?['Option 1','Option 2']:undefined}])}}><span style={{width:24,height:24,borderRadius:4,background:'#e3f2fd',color:'#1976d2',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'0.68rem',fontWeight:700,flexShrink:0}}>{ft.icon}</span>{ft.label}</button>))}</div><div style={{marginTop:16,fontSize:'0.68rem',fontWeight:700,color:'#94a3b8',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:8}}>Elements</div><div style={{display:'flex',flexDirection:'column',gap:6}}>{[{type:'heading',label:'Heading',icon:'H'},{type:'paragraph',label:'Paragraph',icon:'P'},{type:'divider',label:'Divider',icon:'--'}].map(ft=>(<button key={ft.type} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 10px',border:'1px dashed #cbd5e1',borderRadius:6,background:'#fafbfc',cursor:'pointer',fontSize:'0.78rem',color:'#33475b',fontWeight:500,textAlign:'left'}} onClick={()=>{setFormBuilderFields([...formBuilderFields,{id:'f'+Date.now(),type:ft.type,label:ft.type==='heading'?'Heading':ft.type==='paragraph'?'Enter text here...':'',placeholder:'',required:false,width:'full'}])}}><span style={{width:24,height:24,borderRadius:4,background:'#fff3e0',color:'#e65100',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'0.68rem',fontWeight:700,flexShrink:0}}>{ft.icon}</span>{ft.label}</button>))}</div></>)}
                  {formBuilderTab==='settings'&&(<><div className="create-panel-field" style={{marginBottom:12}}><label>Form Name</label><input value={formBuilderName} onChange={e=>setFormBuilderName(e.target.value)}/></div><div className="create-panel-field" style={{marginBottom:12}}><label>Description</label><textarea rows={3} value={formBuilderDesc} onChange={e=>setFormBuilderDesc(e.target.value)}/></div><div className="create-panel-field" style={{marginBottom:12}}><label>Thank You Message</label><textarea rows={3} value={formThankYou} onChange={e=>setFormThankYou(e.target.value)}/></div><div className="create-panel-field" style={{marginBottom:12}}><label>Notification Emails</label><input value={formNotifyEmails} onChange={e=>setFormNotifyEmails(e.target.value)} placeholder="email@example.com, ..."/><div style={{fontSize:'0.68rem',color:'#94a3b8',marginTop:4}}>Comma-separated emails to notify on submission</div></div>{editingFormId&&<div className="create-panel-field" style={{marginBottom:12}}><label>Embed Code</label><textarea rows={3} readOnly value={`<iframe src="https://ops.botpros.ai/api/crm/forms/embed/${editingFormId}" width="100%" height="500" frameborder="0"></iframe>`} style={{fontSize:'0.72rem',fontFamily:'monospace'}} onClick={e=>(e.target as HTMLTextAreaElement).select()}/></div>}</>)}
                </div>
              </div>
              <div style={{flex:1,overflow:'auto',padding:30,display:'flex',justifyContent:'center'}}>
                <div style={{width:'100%',maxWidth:620,background:'#fff',borderRadius:8,border:'1px solid #e2e8f0',padding:32,boxShadow:'0 1px 3px rgba(0,0,0,0.04)'}}>
                  <h2 style={{fontSize:'1.1rem',fontWeight:800,color:'#1e293b',marginBottom:4}}>{formBuilderName}</h2>
                  {formBuilderDesc&&<p style={{fontSize:'0.82rem',color:'#64748b',marginBottom:20}}>{formBuilderDesc}</p>}
                  <div style={{display:'flex',flexDirection:'column',gap:16}}>
                    {formBuilderFields.map((field:any,idx:number)=>{const isSelected=formBuilderSelected===field.id;return(<div key={field.id} style={{position:'relative',padding:12,borderRadius:6,border:isSelected?'2px solid var(--accent)':'1px solid transparent',cursor:'pointer',background:isSelected?'#fafbfc':'transparent',transition:'border 0.1s'}} onClick={()=>setFormBuilderSelected(isSelected?null:field.id)}>
                      {field.type==='divider'?<hr style={{border:'none',borderTop:'1px solid #e2e8f0',margin:'8px 0'}}/>:field.type==='heading'?(isSelected?<input style={{fontSize:'1rem',fontWeight:700,color:'#1e293b',border:'none',outline:'none',width:'100%',background:'transparent'}} value={field.label} onChange={e=>{const u=[...formBuilderFields];u[idx]={...field,label:e.target.value};setFormBuilderFields(u)}}/>:<h3 style={{fontSize:'1rem',fontWeight:700,color:'#1e293b'}}>{field.label}</h3>):field.type==='paragraph'?(isSelected?<textarea style={{fontSize:'0.82rem',color:'#64748b',border:'none',outline:'none',width:'100%',background:'transparent',resize:'none'}} rows={2} value={field.label} onChange={e=>{const u=[...formBuilderFields];u[idx]={...field,label:e.target.value};setFormBuilderFields(u)}}/>:<p style={{fontSize:'0.82rem',color:'#64748b'}}>{field.label}</p>):(<div><label style={{display:'block',fontSize:'0.78rem',fontWeight:600,color:'#33475b',marginBottom:4}}>{isSelected?<input style={{fontSize:'0.78rem',fontWeight:600,color:'#33475b',border:'none',outline:'none',background:'transparent'}} value={field.label} onChange={e=>{const u=[...formBuilderFields];u[idx]={...field,label:e.target.value};setFormBuilderFields(u)}} onClick={e=>e.stopPropagation()}/>:field.label}{field.required&&<span style={{color:'#d32f2f'}}> *</span>}</label>{field.type==='textarea'?<textarea rows={3} placeholder={field.placeholder||''} readOnly style={{width:'100%',resize:'none',padding:'8px 10px',border:'1px solid #cbd6e2',borderRadius:4,fontSize:'0.88rem',color:'#1e293b',fontFamily:'inherit'}}/>:field.type==='select'?<select style={{width:'100%',padding:'8px 10px',border:'1px solid #cbd6e2',borderRadius:4,fontSize:'0.88rem',color:'#1e293b',fontFamily:'inherit'}} disabled><option>{field.placeholder||'Select...'}</option>{(field.options||[]).map((o:string,i:number)=><option key={i}>{o}</option>)}</select>:field.type==='radio'?<div style={{display:'flex',flexDirection:'column',gap:6,marginTop:4}}>{(field.options||['Option 1']).map((o:string,i:number)=><label key={i} style={{display:'flex',alignItems:'center',gap:6,fontSize:'0.8rem',color:'#33475b'}}><input type="radio" name={`radio-${field.id}`} disabled/> {o}</label>)}</div>:field.type==='checkbox'?<label style={{display:'flex',alignItems:'center',gap:6,fontSize:'0.8rem',color:'#33475b',marginTop:4}}><input type="checkbox" disabled/> {field.placeholder||'Check this box'}</label>:<input type={field.type||'text'} placeholder={field.placeholder||''} readOnly style={{width:'100%',padding:'8px 10px',border:'1px solid #cbd6e2',borderRadius:4,fontSize:'0.88rem',color:'#1e293b',fontFamily:'inherit'}}/>}</div>)}
                      {isSelected&&<div style={{position:'absolute',top:-10,right:-10,display:'flex',gap:4}}>{idx>0&&<button style={{width:22,height:22,borderRadius:4,border:'1px solid #e2e8f0',background:'#fff',cursor:'pointer',fontSize:'0.62rem',display:'flex',alignItems:'center',justifyContent:'center'}} onClick={e=>{e.stopPropagation();const u=[...formBuilderFields];[u[idx-1],u[idx]]=[u[idx],u[idx-1]];setFormBuilderFields(u)}}>^</button>}{idx<formBuilderFields.length-1&&<button style={{width:22,height:22,borderRadius:4,border:'1px solid #e2e8f0',background:'#fff',cursor:'pointer',fontSize:'0.62rem',display:'flex',alignItems:'center',justifyContent:'center'}} onClick={e=>{e.stopPropagation();const u=[...formBuilderFields];[u[idx],u[idx+1]]=[u[idx+1],u[idx]];setFormBuilderFields(u)}}>v</button>}<button style={{width:22,height:22,borderRadius:4,border:'1px solid #e2e8f0',background:'#fff',cursor:'pointer',fontSize:'0.62rem',color:'#d32f2f',display:'flex',alignItems:'center',justifyContent:'center'}} onClick={e=>{e.stopPropagation();setFormBuilderFields(formBuilderFields.filter((_:any,i:number)=>i!==idx));setFormBuilderSelected(null)}}>x</button></div>}
                      {isSelected&&field.type!=='divider'&&field.type!=='heading'&&field.type!=='paragraph'&&(<div style={{marginTop:10,paddingTop:10,borderTop:'1px solid #e2e8f0',display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}} onClick={e=>e.stopPropagation()}><label style={{display:'flex',alignItems:'center',gap:4,fontSize:'0.72rem',color:'#64748b'}}><input type="checkbox" checked={field.required||false} onChange={e=>{const u=[...formBuilderFields];u[idx]={...field,required:e.target.checked};setFormBuilderFields(u)}}/> Required</label><select style={{padding:'2px 6px',fontSize:'0.72rem',border:'1px solid #e2e8f0',borderRadius:4}} value={field.width||'full'} onChange={e=>{const u=[...formBuilderFields];u[idx]={...field,width:e.target.value};setFormBuilderFields(u)}}><option value="full">Full width</option><option value="half">Half width</option></select><input style={{padding:'2px 6px',fontSize:'0.72rem',border:'1px solid #e2e8f0',borderRadius:4,flex:1,minWidth:100}} placeholder="Placeholder text" value={field.placeholder||''} onChange={e=>{const u=[...formBuilderFields];u[idx]={...field,placeholder:e.target.value};setFormBuilderFields(u)}}/>{(field.type==='select'||field.type==='radio')&&<input style={{padding:'2px 6px',fontSize:'0.72rem',border:'1px solid #e2e8f0',borderRadius:4,width:'100%'}} placeholder="Options (comma-separated)" value={(field.options||[]).join(', ')} onChange={e=>{const u=[...formBuilderFields];u[idx]={...field,options:e.target.value.split(',').map((s:string)=>s.trim()).filter(Boolean)};setFormBuilderFields(u)}}/>}</div>)}
                    </div>)})}
                  </div>
                  {formBuilderFields.length>0&&<div style={{marginTop:24}}><button disabled title="Preview only" style={{background:'#ff7a59',color:'#fff',border:'none',borderRadius:4,padding:'10px 28px',fontSize:'0.85rem',fontWeight:600,cursor:'default',width:'100%'}}>Submit</button></div>}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========== Landing Pages View (Full 6-Row Template) ========== */}
        {activeView === 'landing-pages' && (() => {
          const lpPageSize = tablePageSize
          const lpPageCount = Math.max(1, Math.ceil(landingPagesTotal / lpPageSize))
          const lpPagedData = landingPagesData.slice((lpPage - 1) * lpPageSize, lpPage * lpPageSize)
          const allLpSelected = lpPagedData.length > 0 && lpSelectedRows.size === lpPagedData.length
          const toggleAllLpRows = () => {
            if (allLpSelected) { setLpSelectedRows(new Set()) }
            else { setLpSelectedRows(new Set(lpPagedData.map((lp: any) => Number(lp.id)))) }
          }
          const toggleLpRow = (id: number) => {
            setLpSelectedRows(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })
          }
          const toggleLpSort = (field: string) => {
            setLpSort(prev => prev.field === field ? { field, order: prev.order === 'ASC' ? 'DESC' : 'ASC' } : { field, order: field === 'name' ? 'ASC' : 'DESC' })
            setLpPage(1)
          }
          const onLpResizeStart = (col: string, e: React.MouseEvent) => {
            e.preventDefault(); e.stopPropagation()
            const startX = e.clientX; const startW = (lpColWidths as any)[col]
            const onMove = (ev: MouseEvent) => { const w = Math.max(60, startW + ev.clientX - startX); setLpColWidths(prev => ({ ...prev, [col]: w })) }
            const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp) }
            document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp)
          }
          const lpSortArrowSvg = <svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M29.38 21.96a1.147 1.147 0 0 0-1.617 0l-3.85 3.852V5.714a1.143 1.143 0 0 0-2.286 0v20.099l-3.853-3.852a1.144 1.144 0 0 0-1.615 1.617l5.803 5.802a1.143 1.143 0 0 0 1.616 0l5.802-5.802a1.144 1.144 0 0 0 0-1.617M15.84 8.422 10.039 2.62a1.145 1.145 0 0 0-1.616 0L2.62 8.422a1.144 1.144 0 0 0 1.617 1.617l3.85-3.851v20.098a1.143 1.143 0 0 0 2.286 0V6.187l3.853 3.852a1.14 1.14 0 0 0 1.616-.001 1.14 1.14 0 0 0-.001-1.616"/></svg>
          return (
          <div className="hs-contacts">
            {/* Row 1: Topbar */}
            <div className="hs-topbar">
              <div className="hs-topbar-left">
                <div className="hs-obj-switcher-wrap" ref={objSwitcherRef}>
                  <button className={`hs-obj-switcher${objSwitcherOpen ? ' open' : ''}`} onClick={() => { setObjSwitcherOpen(!objSwitcherOpen); setObjSwitcherSearch('') }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8"/><path d="M12 17v4"/></svg>
                    <span>Landing Pages</span>
                    <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                  </button>
                  {objSwitcherOpen && (
                    <div className="hs-obj-panel">
                      <div className="hs-obj-search-wrap">
                        <svg className="hs-obj-search-icon" width="13" height="13" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                        <input className="hs-obj-search" placeholder="Search" value={objSwitcherSearch} onChange={(e) => setObjSwitcherSearch(e.target.value)} autoFocus />
                      </div>
                      <ul className="hs-obj-list" role="listbox">
                        {[
                          { label: 'Email', view: 'marketing-email' as ViewMode },
                          { label: 'Ads', view: 'ads' as ViewMode },
                          { label: 'Social', view: 'social' as ViewMode },
                          { label: 'Forms', view: 'forms' as ViewMode },
                          { label: 'Campaigns', view: 'campaigns' as ViewMode },
                          { label: 'Landing Pages', view: 'landing-pages' as ViewMode },
                        ].filter(item => !objSwitcherSearch || item.label.toLowerCase().includes(objSwitcherSearch.toLowerCase())).map(item => (
                          <li key={item.view} className={`hs-obj-item${item.view === activeView ? ' active' : ''}`} role="option" aria-selected={item.view === activeView}
                            onClick={() => { setActiveView(item.view); setObjSwitcherOpen(false); setObjSwitcherSearch('') }}>
                            {item.label}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="hs-topbar-sep" />
                <div className="hs-view-tabs">
                  <div className="hs-view-tab active">
                    <span className="hs-tab-label">All pages</span>
                    {landingPagesTotal > 0 && <span className="hs-tab-count">{landingPagesTotal.toLocaleString()}</span>}
                    <span className="hs-tab-kebab" onClick={(e) => { e.stopPropagation() }}>
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>
                    </span>
                  </div>
                  <div className="hs-topbar-sep" />
                  <div className="hs-view-add-wrap" ref={viewAddMenuRef} style={{ position: 'relative', display: 'inline-flex', alignSelf: 'center' }}>
                    <button className={`hs-quick-add-btn${viewAddMenuOpen ? ' active' : ''}`} title="Create or add view" type="button" aria-label="Create or add view"
                      onClick={() => { setViewAddMenuOpen(!viewAddMenuOpen); setViewAddMenuMode('menu') }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                    </button>
                    {viewAddMenuOpen && viewAddMenuMode === 'menu' && (
                      <div className="hs-view-add-dropdown">
                        <button className="hs-view-add-item" onClick={() => setViewAddMenuMode('type')}>Create new view</button>
                        <button className="hs-view-add-item" onClick={() => { setViewAddMenuOpen(false); setViewAddMenuMode('menu') }}>Add view</button>
                      </div>
                    )}
                    {viewAddMenuOpen && viewAddMenuMode === 'type' && (
                      <div className="hs-view-add-dropdown hs-view-type-picker">
                        <button className="hs-view-type-btn" onClick={() => { setViewAddMenuOpen(false); setViewAddMenuMode('menu') }}>
                          <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                          <span>Table</span>
                        </button>
                        <button className="hs-view-type-btn" disabled style={{ opacity: 0.4 }}><svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M11.43 11.43h-8a1.14 1.14 0 1 1 0-2.28h8a1.14 1.14 0 1 1 0 2.28M28.57 11.43h-8a1.14 1.14 0 1 1 0-2.28h8a1.14 1.14 0 1 1 0 2.28"/><path d="M9.14 29.71H5.71c-1.89 0-3.43-1.54-3.43-3.43V5.71c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v14.86c0 1.89-1.54 3.43-3.43 3.43h-3.43c-1.89 0-3.43-1.54-3.43-3.43v-1.14h-6.86v6.86c0 1.89-1.54 3.43-3.43 3.43Z"/></svg><span>Board</span></button>
                        <button className="hs-view-type-btn" disabled style={{ opacity: 0.4 }}><svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M7.57 24.71c-.63 0-1.14-.51-1.14-1.14v-6.86a1.14 1.14 0 1 1 2.28 0v6.86c0 .63-.51 1.14-1.14 1.14M12.14 24.71c-.63 0-1.14-.51-1.14-1.14v-16c0-.63.51-1.14 1.14-1.14h4.57a1.14 1.14 0 1 1 0 2.28h-3.43v14.86c0 .63-.51 1.14-1.14 1.14"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v20.57c0 1.89-1.54 3.43-3.43 3.43Z"/></svg><span>Report</span></button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="hs-topbar-right">
                {/* Create page — direct action (Import removed) */}
                <button className="hs-add-contacts-btn" onClick={() => { setNewLpName('');setNewLpSlug('');setNewLpTitle('');setNewLpMeta('');setShowCreateLP(true) }}>
                  Create page
                </button>
              </div>
            </div>

            {/* Row 2: Toolbar */}
            <div className="hs-toolbar">
              <div className="hs-toolbar-left">
                <div className="hs-search-wrap">
                  <svg className="hs-search-icon" width="14" height="14" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M28.14 29.29c-.29 0-.58-.11-.81-.33l-7.58-7.58a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l7.58 7.58a1.14 1.14 0 0 1-.81 1.95"/><path d="M13.29 24.71c-6.3 0-11.43-5.13-11.43-11.43S6.98 1.86 13.29 1.86s11.43 5.13 11.43 11.43-5.13 11.43-11.43 11.43Zm0-20.57c-5.04 0-9.14 4.1-9.14 9.14s4.1 9.14 9.14 9.14 9.14-4.1 9.14-9.14-4.1-9.14-9.14-9.14"/></svg>
                  <input className="hs-search-input" placeholder="Search" value={lpSearch} onChange={(e) => setLpSearch(e.target.value)} />
                  {lpSearch && <button className="hs-search-clear" onClick={() => setLpSearch('')}>&times;</button>}
                </div>
                <div className="hs-tb-sep" />
                <div className="hs-viewtype-group">
                  <button className="hs-tb-btn hs-viewtype-btn" disabled title="Table view">
                    <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                    <span>Table</span>
                  </button>
                  <button className={`hs-tb-btn hs-settings-btn${lpSettingsOpen ? ' active' : ''}`} onClick={() => setLpSettingsOpen(!lpSettingsOpen)} title={lpSettingsOpen ? 'Close settings' : 'Table settings'}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                  </button>
                </div>
                <div className="hs-tb-sep" />
                <button className="hs-tb-btn hs-tb-btn-bordered disabled" disabled title="Edit columns is not available yet">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="18" rx="1"/><rect x="14" y="3" width="7" height="18" rx="1"/></svg>
                  <span>Edit columns</span>
                </button>
                <div className="hs-tb-sep" />
                <button className="hs-tb-btn hs-tb-btn-bordered disabled" disabled title="Filters is not available yet">
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M30.71 16.86H13.57a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28M6.71 16.86H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M10.14 20.29c-2.52 0-4.57-2.05-4.57-4.57s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M6.71 6.57H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M30.71 6.57H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M18.14 6.57H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 10C19.05 10 17 7.95 17 5.43S19.05.86 21.57.86s4.57 2.05 4.57 4.57S24.09 10 21.57 10m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M30.71 27.14H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M18.14 27.14H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 30.57C19.05 30.57 17 28.52 17 26s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29"/></svg>
                  <span>Filters</span>
                </button>
                <div className="hs-tb-sep" />
                <button className="hs-tb-btn hs-tb-btn-bordered disabled" disabled title="Export is not available yet">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  <span>Export</span>
                </button>
              </div>
              <div className="hs-toolbar-right">
                <div className="hs-tb-sep" />
                <button className="hs-tb-btn hs-tb-btn-bordered disabled" disabled title="Clone view is not available yet">
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M25.86 29.29H12.15c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-1.89 1.54-3.43 3.43-3.43h13.71c1.89 0 3.43 1.54 3.43 3.43v13.71c0 1.89-1.54 3.43-3.43 3.43M12.14 11c-.63 0-1.14.51-1.14 1.14v13.71c0 .63.51 1.14 1.14 1.14h13.71c.63 0 1.14-.51 1.14-1.14V12.14c0-.63-.51-1.14-1.14-1.14z"/><path d="M5.29 22.43c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43H19c1.89 0 3.43 1.54 3.43 3.43a1.14 1.14 0 1 1-2.28 0c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14V19c0 .63.51 1.14 1.14 1.14a1.14 1.14 0 1 1 0 2.28Z"/></svg>
                </button>
                <button className="hs-tb-btn hs-tb-btn-bordered disabled" disabled title="Save view">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                </button>
              </div>
            </div>

            {/* Row 3: Quick Filters */}
            <div className="hs-quick-filters">
              <button className={`hs-qf-btn hs-qf-trigger${lpFilter !== 'all' ? ' has-value' : ''}`}
                onClick={() => { const vals = ['all', 'published', 'draft', 'archived']; const idx = vals.indexOf(lpFilter); setLpFilter(vals[(idx + 1) % vals.length]); setLpPage(1) }}>
                <span>{lpFilter === 'published' ? 'Published' : lpFilter === 'draft' ? 'Drafts' : lpFilter === 'archived' ? 'Archived' : 'Status'}</span>
                {lpFilter !== 'all' && <span className="hs-qf-clear" onClick={(e) => { e.stopPropagation(); setLpFilter('all'); setLpPage(1) }}>&times;</span>}
                {lpFilter === 'all' && <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>}
              </button>
              <button className={`hs-qf-btn hs-qf-trigger${lpSort.field !== 'updated_at' ? ' has-value' : ''}`}
                onClick={() => { const fields = ['updated_at', 'name', 'total_views', 'created_at']; const idx = fields.indexOf(lpSort.field); const next = fields[(idx + 1) % fields.length]; setLpSort({ field: next, order: next === 'name' ? 'ASC' : 'DESC' }); setLpPage(1) }}>
                <span>Sort: {{ updated_at: 'Updated', name: 'Name', total_views: 'Views', created_at: 'Created' }[lpSort.field] || 'Updated'}</span>
                <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
              </button>
            </div>

            {/* Row 4-6: Content with table + settings */}
            <div className={`hs-content${lpSettingsOpen ? ' with-settings' : ''}`}>
              <div className="hs-table-area">
                {/* Selection bar */}
                {lpSelectedRows.size > 0 && (
                  <div className="hs-selection-bar">
                    <span className="hs-sel-count">{lpSelectedRows.size} selected</span>
                    <div className="hs-sel-sep" />

                    <button className="hs-sel-btn" style={{ color: '#dc2626' }} onClick={() => {
                      if (confirm(`Delete ${lpSelectedRows.size} page(s)?`)) {
                        Promise.all([...lpSelectedRows].map(id => deleteLandingPage(id))).then(() => { { const opts: { status?: string; search?: string } = {}; if (lpFilter !== 'all') opts.status = lpFilter; if (lpSearch) opts.search = lpSearch; fetchLandingPages(opts).then(res => { setLandingPagesData(res.pages); setLandingPagesTotal(res.total) }).catch(() => {}) }; setLpSelectedRows(new Set()) }).catch(() => {})
                      }
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                      Delete
                    </button>
                    <button className="hs-sel-close" onClick={() => setLpSelectedRows(new Set())} title="Deselect all">&times;</button>
                  </div>
                )}
                <div className="hs-table-scroll">
                  <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                      <col style={{ width: 40 }} />
                      <col style={{ width: lpColWidths.name }} />
                      <col style={{ width: lpColWidths.slug }} />
                      <col style={{ width: lpColWidths.status }} />
                      <col style={{ width: lpColWidths.views }} />
                      <col style={{ width: lpColWidths.submissions }} />
                      <col style={{ width: lpColWidths.updated }} />
                      <col style={{ width: lpColWidths.actions }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th className="hs-th-check"><input type="checkbox" checked={allLpSelected} onChange={toggleAllLpRows} /></th>
                        <th className="hs-th-resizable hs-th-sortable hs-th-frozen" style={{ position: 'sticky', left: 40, top: 0, zIndex: 15, background: '#f8fafc', boxShadow: '2px 0 4px rgba(0,0,0,0.06)' }} onClick={() => toggleLpSort('name')} onContextMenu={(e) => { e.preventDefault(); setColContextMenu({ field: 'name', x: e.clientX, y: e.clientY }) }}>
                          <span className="hs-th-content">
                            Page Name
                            {lpSort.field === 'name' && <span className="hs-sort-arrow">{lpSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                            <span className="hs-th-hover-sort">{lpSortArrowSvg}</span>
                          </span>
                          <button className="hs-th-kebab" onClick={(e) => { e.stopPropagation(); setColContextMenu({ field: 'name', x: e.clientX, y: e.clientY }) }}>
                            <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><circle cx="16" cy="4.14" r="2.29"/><circle cx="16" cy="15.57" r="2.29"/><circle cx="16" cy="27" r="2.29"/></svg>
                          </button>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onLpResizeStart('name', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                        <th className="hs-th-resizable">
                          <span className="hs-th-content">URL Slug</span>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onLpResizeStart('slug', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                        <th className="hs-th-resizable">
                          <span className="hs-th-content">Status</span>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onLpResizeStart('status', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                        <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleLpSort('total_views')} onContextMenu={(e) => { e.preventDefault(); setColContextMenu({ field: 'total_views', x: e.clientX, y: e.clientY }) }}>
                          <span className="hs-th-content">
                            Views
                            {lpSort.field === 'total_views' && <span className="hs-sort-arrow">{lpSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                            <span className="hs-th-hover-sort">{lpSortArrowSvg}</span>
                          </span>
                          <button className="hs-th-kebab" onClick={(e) => { e.stopPropagation(); setColContextMenu({ field: 'total_views', x: e.clientX, y: e.clientY }) }}>
                            <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><circle cx="16" cy="4.14" r="2.29"/><circle cx="16" cy="15.57" r="2.29"/><circle cx="16" cy="27" r="2.29"/></svg>
                          </button>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onLpResizeStart('views', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                        <th className="hs-th-resizable">
                          <span className="hs-th-content">Submissions</span>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onLpResizeStart('submissions', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                        <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleLpSort('updated_at')} onContextMenu={(e) => { e.preventDefault(); setColContextMenu({ field: 'updated_at', x: e.clientX, y: e.clientY }) }}>
                          <span className="hs-th-content">
                            Updated
                            {lpSort.field === 'updated_at' && <span className="hs-sort-arrow">{lpSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                            <span className="hs-th-hover-sort">{lpSortArrowSvg}</span>
                          </span>
                          <button className="hs-th-kebab" onClick={(e) => { e.stopPropagation(); setColContextMenu({ field: 'updated_at', x: e.clientX, y: e.clientY }) }}>
                            <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><circle cx="16" cy="4.14" r="2.29"/><circle cx="16" cy="15.57" r="2.29"/><circle cx="16" cy="27" r="2.29"/></svg>
                          </button>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onLpResizeStart('updated', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                        <th><span className="hs-th-content">Actions</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      {landingPagesLoading ? (
                        <tr><td colSpan={8} className="hs-loading-cell"><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}><DotLottieReact src="/loading.lottie" loop autoplay style={{ width: 80, height: 80 }} /><span style={{ fontSize: '0.82rem', color: '#7c98b6', marginTop: 8 }}>Loading pages...</span></div></td></tr>
                      ) : lpPagedData.length === 0 ? (
                        <tr><td colSpan={8}>
                          <div style={{ padding: '60px 40px', textAlign: 'center' }}>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" style={{ marginBottom: 12 }}><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8"/><path d="M12 17v4"/></svg>
                            <div style={{ color: '#64748b', fontSize: '0.88rem', fontWeight: 600, marginBottom: 4 }}>No landing pages yet</div>
                            <div style={{ color: '#94a3b8', fontSize: '0.78rem' }}>Create your first landing page to drive conversions.</div>
                          </div>
                        </td></tr>
                      ) : lpPagedData.map((lp: any) => (
                        <tr key={String(lp.id)} className={`hs-row${lpSelectedRows.has(Number(lp.id)) ? ' selected' : ''}`}>
                          <td className="hs-td-check"><input type="checkbox" checked={lpSelectedRows.has(Number(lp.id))} onChange={() => toggleLpRow(Number(lp.id))} /></td>
                          <td className="hs-td-name" style={{ position: 'sticky', left: 40, zIndex: 2, background: lpSelectedRows.has(Number(lp.id)) ? '#eff6ff' : '#fff' }}>
                            <div className="hs-name-cell">
                              <span className="hs-name-link">{String(lp.name || '')}</span>
                            </div>
                          </td>
                          <td>{lp.slug ? <span style={{ fontSize: '0.75rem', color: '#1976d2', fontFamily: 'monospace' }}>/p/{String(lp.slug)}</span> : <span style={{ color: '#94a3b8' }}>--</span>}</td>
                          <td><span className="hs-status-badge" style={{ background: lp.status === 'published' ? '#e8f5e9' : '#f1f5f9', color: lp.status === 'published' ? '#2e7d32' : '#64748b' }}>{String(lp.status || 'draft').charAt(0).toUpperCase() + String(lp.status || 'draft').slice(1)}</span></td>
                          <td style={{ color: '#33475b', fontWeight: 500 }}>{Number(lp.total_views || 0)}</td>
                          <td style={{ color: '#33475b' }}>{Number(lp.total_submissions || 0)}</td>
                          <td style={{ color: '#516f90', fontSize: '0.75rem' }}>{lp.updated_at ? formatDatePref(String(lp.updated_at)) : '--'}</td>
                          <td>
                            <div style={{ display: 'flex', gap: 8 }}>
                              {lp.status !== 'published' ? (
                                <button title="Status only — no public page exists yet" style={{ background: 'none', border: 'none', color: '#2e7d32', cursor: 'pointer', fontSize: '0.72rem' }} onClick={() => publishLandingPage(Number(lp.id)).then(() => { const opts: { status?: string; search?: string } = {}; if (lpFilter !== 'all') opts.status = lpFilter; if (lpSearch) opts.search = lpSearch; fetchLandingPages(opts).then(res => { setLandingPagesData(res.pages); setLandingPagesTotal(res.total) }).catch(() => {}) })}>Mark published</button>
                              ) : (
                                <a href={`/p/${String(lp.slug)}`} target="_blank" rel="noreferrer" style={{ color: '#1976d2', fontSize: '0.72rem' }}>View</a>
                              )}
                              <button style={{ background: 'none', border: 'none', color: '#d32f2f', cursor: 'pointer', fontSize: '0.72rem' }} onClick={() => { if (confirm('Delete?')) deleteLandingPage(Number(lp.id)).then(() => { const opts: { status?: string; search?: string } = {}; if (lpFilter !== 'all') opts.status = lpFilter; if (lpSearch) opts.search = lpSearch; fetchLandingPages(opts).then(res => { setLandingPagesData(res.pages); setLandingPagesTotal(res.total) }).catch(() => {}) }) }}>Delete</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="hs-pagination">
                  <nav className="hs-paginator" aria-label="Pagination">
                    <button className="hs-paginator-btn" disabled={lpPage <= 1} onClick={() => setLpPage(p => p - 1)} aria-label="Previous page">
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95"/></svg>
                      <span>Prev</span>
                    </button>
                    {(() => {
                      if (lpPageCount <= 7) {
                        const pages: number[] = []; for (let i = 1; i <= lpPageCount; i++) pages.push(i)
                        return pages.map(p => <button key={p} className={`hs-paginator-num${p === lpPage ? ' active' : ''}`} onClick={() => setLpPage(p)}>{p}</button>)
                      }
                      const items: React.ReactNode[] = []
                      const shown = new Set<number>()
                      for (let i = 1; i <= Math.min(4, lpPageCount); i++) shown.add(i)
                      shown.add(lpPageCount)
                      for (let i = Math.max(1, lpPage - 1); i <= Math.min(lpPageCount, lpPage + 1); i++) shown.add(i)
                      const sorted = [...shown].sort((a, b) => a - b)
                      for (let i = 0; i < sorted.length; i++) {
                        if (i > 0 && sorted[i] - sorted[i - 1] > 1) items.push(<span key={`e${i}`} className="hs-paginator-ellipsis">...</span>)
                        items.push(<button key={sorted[i]} className={`hs-paginator-num${sorted[i] === lpPage ? ' active' : ''}`} onClick={() => setLpPage(sorted[i])}>{sorted[i]}</button>)
                      }
                      return items
                    })()}
                    <button className="hs-paginator-btn" disabled={lpPage >= lpPageCount} onClick={() => setLpPage(p => p + 1)} aria-label="Next page">
                      <span>Next</span>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M11 27.71c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62l9.48-9.48-9.48-9.47a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0L22.1 15.48c.45.45.45 1.17 0 1.62L11.81 27.39c-.22.22-.52.33-.81.33Z"/></svg>
                    </button>
                  </nav>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', marginLeft: 'auto' }}><select value={tablePageSize} onChange={e => { setTablePageSize(Number(e.target.value) as any); setLpPage(1) }} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #e2e8f0', fontSize: '0.82rem' }}><option value={100}>100</option><option value={200}>200</option><option value={500}>500</option></select><span>per page</span></div>
                </div>
              </div>

              {/* Settings Panel */}
              {lpSettingsOpen && (
                <div className="hs-settings-panel">
                  <div className="hs-settings-header">
                    <span className="hs-settings-title">Table settings</span>
                    <button className="hs-settings-close" onClick={() => setLpSettingsOpen(false)}>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                    </button>
                  </div>
                  <div className="hs-settings-body">
                    <div className="hs-settings-group">
                      <label className="hs-settings-label">Pagination
                        <span className="hs-info-icon" title="Number of rows shown per page"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span>
                      </label>
                      {([100, 200, 500] as const).map(size => (
                        <label className="hs-settings-radio" key={size}>
                          <input type="radio" name="lpPageSize" checked={tablePageSize === size} onChange={() => { setTablePageSize(size); setLpPage(1) }} />
                          <span className="hs-radio-indicator" /><span>{size} per page</span>
                        </label>
                      ))}
                    </div>
                    <hr className="hs-settings-divider" />
                    <div className="hs-settings-group">
                      <label className="hs-settings-label">Row height
                        <span className="hs-info-icon" title="Adjust the vertical spacing of table rows"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span>
                      </label>
                      {([{ value: 'small' as const, label: 'Compact' }, { value: 'medium' as const, label: 'Default' }, { value: 'large' as const, label: 'Comfortable' }]).map(opt => (
                        <label className="hs-settings-radio" key={opt.value}>
                          <input type="radio" name="lpRowHeight" checked={tableRowHeight === opt.value} onChange={() => setTableRowHeight(opt.value)} />
                          <span className="hs-radio-indicator" /><span>{opt.label}</span>
                        </label>
                      ))}
                    </div>
                    <hr className="hs-settings-divider" />
                    <div className="hs-settings-group">
                      <div className="hs-settings-toggle-row">
                        <label className="hs-settings-label">Zebra striping
                          <span className="hs-info-icon" title="Alternate row background colors for easier reading"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span>
                        </label>
                        <button className={`hs-toggle${tableZebraStriping ? ' on' : ''}`} onClick={() => setTableZebraStriping(!tableZebraStriping)} role="switch" aria-checked={tableZebraStriping}>
                          <span className="hs-toggle-track"><span className="hs-toggle-on">ON</span><span className="hs-toggle-off">OFF</span><span className="hs-toggle-handle" /></span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Create Landing Page modal */}
            {showCreateLP && (<>
              <div className="create-panel-overlay" onClick={() => setShowCreateLP(false)} />
              <div className="create-panel" onClick={(e) => e.stopPropagation()}>
                <div className="create-panel-header">
                  <h2>Create Landing Page</h2>
                  <button className="create-panel-close" onClick={() => setShowCreateLP(false)} aria-label="Close">
                    <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                  </button>
                </div>
                <div className="create-panel-body">
                  <div className="create-panel-field">
                    <label>Page Name *</label>
                    <input value={newLpName} onChange={e => setNewLpName(e.target.value)} placeholder="e.g. Free Estimate Request" />
                  </div>
                  <div className="create-panel-field">
                    <label>URL Slug</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ fontSize: '0.78rem', color: '#64748b' }}>/p/</span>
                      <input style={{ flex: 1 }} value={newLpSlug} onChange={e => setNewLpSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))} placeholder="free-estimate" />
                    </div>
                  </div>
                  <div className="create-panel-field">
                    <label>Page Title</label>
                    <input value={newLpTitle} onChange={e => setNewLpTitle(e.target.value)} placeholder="e.g. Get Your Free Estimate - BotPros" />
                  </div>
                  <div className="create-panel-field">
                    <label>Meta Description</label>
                    <textarea rows={3} value={newLpMeta} onChange={e => setNewLpMeta(e.target.value)} placeholder="Description for search engines..." />
                  </div>
                  <div style={{ padding: 12, background: '#f5f8fa', borderRadius: 6, fontSize: '0.78rem', color: '#7c98b6' }}>After creating, you can add sections using the page builder.</div>
                </div>
                <div className="create-panel-footer">
                  <button className="cp-btn-primary" disabled={!newLpName.trim()} onClick={() => { createLandingPage({ name: newLpName, slug: newLpSlug || null, page_title: newLpTitle || null, meta_description: newLpMeta || null, status: 'draft' }).then(() => { setShowCreateLP(false); { const opts: { status?: string; search?: string } = {}; if (lpFilter !== 'all') opts.status = lpFilter; if (lpSearch) opts.search = lpSearch; fetchLandingPages(opts).then(res => { setLandingPagesData(res.pages); setLandingPagesTotal(res.total) }).catch(() => {}) } }) }}>Create page</button>
                  <button className="cp-btn-secondary" onClick={() => setShowCreateLP(false)}>Cancel</button>
                </div>
              </div>
            </>)}
          </div>
          )
        })()}


        {/* ========== Prospecting Workspace ========== */}
        {activeView === 'prospecting' && (() => {
          const prospLeads: any[] = prospectingData?.leads || []
          const prospSearchLower = prospectingSearch.toLowerCase()
          const filteredProspLeads = prospLeads.filter((l: any) => {
            if (prospectingViewTab === 'new' && l.status !== 'new') return false
            if (prospectingViewTab === 'contacted' && l.status !== 'contacted') return false
            if (prospectingViewTab === 'qualified' && l.status !== 'qualified') return false
            if (prospSearchLower) {
              const t = String(l.title ?? '').toLowerCase()
              const cn = String(l.client_name ?? '').toLowerCase()
              const src = String(l.source ?? '').toLowerCase()
              if (!t.includes(prospSearchLower) && !cn.includes(prospSearchLower) && !src.includes(prospSearchLower)) return false
            }
            return true
          })
          filteredProspLeads.sort((a: any, b: any) => {
            const f = prospectingSort.field
            const o = prospectingSort.order === 'ASC' ? 1 : -1
            const av = String(a[f] ?? '')
            const bv = String(b[f] ?? '')
            return av < bv ? -o : av > bv ? o : 0
          })
          const prospTotal = filteredProspLeads.length
          const prospPageSize = tablePageSize
          const prospTotalPages = Math.max(1, Math.ceil(prospTotal / prospPageSize))
          const prospCurrentPage = Math.min(prospectingPage, prospTotalPages)
          const prospPagedData = filteredProspLeads.slice((prospCurrentPage - 1) * prospPageSize, prospCurrentPage * prospPageSize)
          const toggleAllProspRows = () => {
            if (prospectingSelectedRows.size === prospPagedData.length) {
              setProspectingSelectedRows(new Set())
            } else {
              setProspectingSelectedRows(new Set(prospPagedData.map((l: any) => Number(l.id))))
            }
          }
          const toggleProspSort = (field: string) => {
            setProspectingSort(prev => prev.field === field ? { field, order: prev.order === 'ASC' ? 'DESC' : 'ASC' } : { field, order: 'ASC' })
            setProspectingPage(1)
          }
          const onProspResizeStart = (col: string, e: React.MouseEvent) => {
            e.preventDefault(); e.stopPropagation()
            const startX = e.clientX; const startW = prospColWidths[col] || 120
            const onMove = (ev: MouseEvent) => setProspColWidths(prev => ({ ...prev, [col]: Math.max(60, startW + ev.clientX - startX) }))
            const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp) }
            document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp)
          }
          return (
          <div className="hs-contacts">
            {/* Row 1: Topbar */}
            <div className="hs-topbar">
              <div className="hs-topbar-left">
                <div className="hs-obj-switcher-wrap" ref={objSwitcherRef}>
                  <button className={`hs-obj-switcher${objSwitcherOpen ? ' open' : ''}`} onClick={() => { setObjSwitcherOpen(!objSwitcherOpen); setObjSwitcherSearch('') }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                    <span>Prospecting</span>
                    <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                  </button>
                  {objSwitcherOpen && (
                    <div className="hs-obj-panel">
                      <div className="hs-obj-search-wrap">
                        <svg className="hs-obj-search-icon" width="13" height="13" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                        <input className="hs-obj-search" placeholder="Search" value={objSwitcherSearch} onChange={(e) => setObjSwitcherSearch(e.target.value)} autoFocus />
                      </div>
                      <ul className="hs-obj-list" role="listbox">
                        {[
                          { label: 'Prospecting', view: 'prospecting' as ViewMode },
                          { label: 'Deals', view: 'deals' as ViewMode },
                          { label: 'Forecasts', view: 'forecasts' as ViewMode },
                          { label: 'Target Accounts', view: 'target-accounts' as ViewMode },
                          { label: 'Activity Feed', view: 'activity-feed' as ViewMode },
                          { label: 'Documents', view: 'sales-documents' as ViewMode },
                          { label: 'Meetings', view: 'meetings-scheduler' as ViewMode },
                          { label: 'Coaching Playlists', view: 'coaching-playlists' as ViewMode },
                          { label: 'Payments', view: 'payments' as ViewMode },
                          { label: 'Quotes', view: 'quotes' as ViewMode },
                          { label: 'Products', view: 'products' as ViewMode },
                        ].filter(item => !objSwitcherSearch || item.label.toLowerCase().includes(objSwitcherSearch.toLowerCase())).map(item => (
                          <li key={item.view} className={`hs-obj-item${item.view === activeView ? ' active' : ''}`} role="option" aria-selected={item.view === activeView} onClick={() => { setActiveView(item.view); setObjSwitcherOpen(false); setObjSwitcherSearch('') }}>{item.label}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="hs-topbar-sep" />
                <div className="hs-view-tabs">
                  {[
                    { key: '', label: 'All leads' },
                    { key: 'new', label: 'New' },
                    { key: 'contacted', label: 'Contacted' },
                    { key: 'qualified', label: 'Qualified' },
                  ].map((tab) => {
                    const isActive = prospectingViewTab === tab.key
                    return (
                      <div key={tab.key} className={`hs-view-tab${isActive ? ' active' : ''}`} onClick={() => { setProspectingViewTab(tab.key); setProspectingPage(1) }}>
                        <span className="hs-tab-label">{tab.label}</span>
                        {isActive && prospTotal > 0 && <span className="hs-tab-count">{prospTotal.toLocaleString()}</span>}
                        {isActive && (
                          <span className="hs-tab-kebab" onClick={(e) => { e.stopPropagation(); setTabEditOpen(!tabEditOpen) }}>
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>
                          </span>
                        )}
                        {tab.key !== '' && <span className="hs-tab-x" onClick={(e) => { e.stopPropagation(); setProspectingViewTab(''); setProspectingPage(1) }}>&times;</span>}
                      </div>
                    )
                  })}
                  <div className="hs-topbar-sep" />
                  <button className="hs-quick-add-btn" title="Add view"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg></button>
                </div>
              </div>
              <div className="hs-topbar-right">
                {/* Add prospect — direct action: Create new lead (Import removed) */}
                <button className="hs-add-contacts-btn" onClick={() => { setActiveView('leads') }}>
                  Create new lead
                </button>
              </div>
            </div>

            {/* Row 2: Toolbar */}
            <div className="hs-toolbar">
              <div className="hs-toolbar-left">
                <div className="hs-search-wrap">
                  <svg className="hs-search-icon" width="14" height="14" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M28.14 29.29c-.29 0-.58-.11-.81-.33l-7.58-7.58a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l7.58 7.58a1.14 1.14 0 0 1-.81 1.95"/><path d="M13.29 24.71c-6.3 0-11.43-5.13-11.43-11.43S6.98 1.86 13.29 1.86s11.43 5.13 11.43 11.43-5.13 11.43-11.43 11.43Zm0-20.57c-5.04 0-9.14 4.1-9.14 9.14s4.1 9.14 9.14 9.14 9.14-4.1 9.14-9.14-4.1-9.14-9.14-9.14"/></svg>
                  <input className="hs-search-input" placeholder="Search" value={prospectingSearch} onChange={(e) => { setProspectingSearch(e.target.value); setProspectingPage(1) }} />
                  {prospectingSearch && <button className="hs-search-clear" onClick={() => { setProspectingSearch(''); setProspectingPage(1) }}>&times;</button>}
                </div>
                <div className="hs-tb-sep" />
                <div className="hs-viewtype-group">
                  <button className="hs-tb-btn hs-viewtype-btn">
                    <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                    <span>Table</span>
                  </button>
                  <button className={`hs-tb-btn hs-settings-btn${tableSettingsOpen ? ' active' : ''}`} onClick={() => setTableSettingsOpen(!tableSettingsOpen)} title={tableSettingsOpen ? 'Close settings' : 'Table settings'}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                  </button>
                </div>
                <div className="hs-tb-sep" />
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Edit columns">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="18" rx="1"/><rect x="14" y="3" width="7" height="18" rx="1"/></svg>
                  <span>Edit columns</span>
                </button>
                <div className="hs-tb-sep" />
                <button className={`hs-tb-btn hs-tb-btn-bordered${quickFiltersOpen ? ' hs-tb-btn-pressed' : ''}`} title="Filters" onClick={() => setQuickFiltersOpen(!quickFiltersOpen)}>
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M30.71 16.86H13.57a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28M6.71 16.86H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M10.14 20.29c-2.52 0-4.57-2.05-4.57-4.57s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M6.71 6.57H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M30.71 6.57H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M18.14 6.57H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 10C19.05 10 17 7.95 17 5.43S19.05.86 21.57.86s4.57 2.05 4.57 4.57S24.09 10 21.57 10m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M30.71 27.14H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M18.14 27.14H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 30.57C19.05 30.57 17 28.52 17 26s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29"/></svg>
                  <span>Filters</span>
                </button>
                <div className="hs-tb-sep" />
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Export">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  <span>Export</span>
                </button>
              </div>
              <div className="hs-toolbar-right">
                <div className="hs-tb-sep" />
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Clone view">
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M25.86 29.29H12.15c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-1.89 1.54-3.43 3.43-3.43h13.71c1.89 0 3.43 1.54 3.43 3.43v13.71c0 1.89-1.54 3.43-3.43 3.43M12.14 11c-.63 0-1.14.51-1.14 1.14v13.71c0 .63.51 1.14 1.14 1.14h13.71c.63 0 1.14-.51 1.14-1.14V12.14c0-.63-.51-1.14-1.14-1.14z"/><path d="M5.29 22.43c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43H19c1.89 0 3.43 1.54 3.43 3.43a1.14 1.14 0 1 1-2.28 0c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14V19c0 .63.51 1.14 1.14 1.14a1.14 1.14 0 1 1 0 2.28Z"/></svg>
                </button>
                <button className="hs-tb-btn hs-tb-btn-bordered disabled" disabled title="Save view">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                </button>
              </div>
            </div>

            {/* Row 3: Quick Filters */}
            {quickFiltersOpen && <div className="hs-quick-filters">
              <div className="hs-owner-dropdown" ref={ownerDropdownRef}>
                <button className={`hs-qf-btn hs-qf-trigger${ownerDropdownOpen ? ' open' : ''}${ownerSelected.size > 0 ? ' has-value' : ''}`} onClick={() => setOwnerDropdownOpen(!ownerDropdownOpen)}>
                  <span>Lead owner</span>
                  {ownerSelected.size > 0 && <span className="hs-qf-count">{ownerSelected.size}</span>}
                  <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                </button>
                {ownerDropdownOpen && (
                  <div className="hs-owner-panel">
                    <div className="hs-owner-search-wrap">
                      <svg className="hs-owner-search-icon" width="13" height="13" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                      <input className="hs-owner-search" placeholder="Search" value={ownerSearch} onChange={(e) => setOwnerSearch(e.target.value)} autoFocus />
                    </div>
                    <div className="hs-owner-list">
                      {(!ownerSearch || 'me'.includes(ownerSearch.toLowerCase())) && (
                        <label className="hs-owner-item hs-owner-me">
                          <input type="checkbox" checked={ownerSelected.has(8)} onChange={() => setOwnerSelected(prev => { const n = new Set(prev); n.has(8) ? n.delete(8) : n.add(8); return n })} />
                          <span className="hs-owner-checkbox" /><span className="hs-owner-label">Me</span>
                        </label>
                      )}
                      {crmUsers.filter(u => !ownerSearch || u.name.toLowerCase().includes(ownerSearch.toLowerCase())).map(user => (
                        <label className="hs-owner-item" key={user.id}>
                          <input type="checkbox" checked={ownerSelected.has(user.id)} onChange={() => setOwnerSelected(prev => { const n = new Set(prev); n.has(user.id) ? n.delete(user.id) : n.add(user.id); return n })} />
                          <span className="hs-owner-checkbox" /><span className="hs-owner-avatar">{user.initials || user.name.charAt(0)}</span><span className="hs-owner-label">{user.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <button className={`hs-qf-btn hs-qf-trigger${prospectingViewTab ? ' has-value' : ''}`} onClick={() => { const vals = ['', 'new', 'contacted', 'qualified']; const idx = vals.indexOf(prospectingViewTab); setProspectingViewTab(vals[(idx + 1) % vals.length]); setProspectingPage(1) }}>
                <span>{prospectingViewTab ? prospectingViewTab.charAt(0).toUpperCase() + prospectingViewTab.slice(1) : 'Status'}</span>
                {prospectingViewTab ? <span className="hs-qf-clear" onClick={(e) => { e.stopPropagation(); setProspectingViewTab(''); setProspectingPage(1) }}>&times;</span> : <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>}
              </button>
              <button className={`hs-qf-btn hs-qf-trigger${prospectingSort.field !== 'date_create' ? ' has-value' : ''}`} onClick={() => { const fields = ['date_create', 'title', 'source', 'status']; const idx = fields.indexOf(prospectingSort.field); const next = fields[(idx + 1) % fields.length]; setProspectingSort({ field: next, order: next === 'title' || next === 'source' ? 'ASC' : 'DESC' }); setProspectingPage(1) }}>
                <span>Sort: {{ date_create: 'Created', title: 'Title', source: 'Source', status: 'Status' }[prospectingSort.field] || 'Created'}</span>
                <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
              </button>
              <div className="hs-qf-sep" />
              <div className="hs-qf-more-wrap">
                <button className="hs-qf-btn hs-qf-more">
                  <svg width="10" height="10" viewBox="0 0 32 32" fill="currentColor"><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V3a1.14 1.14 0 1 1 2.28 0v25.14c0 .63-.51 1.14-1.14 1.14Z"/><path d="M28.14 16.71H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28"/></svg>
                  <span>More</span>
                </button>
              </div>
            </div>}

            {/* Row 4: Selection Bar */}
            <div className={`hs-content${tableSettingsOpen ? ' with-settings' : ''}`}>
              {prospectingSelectedRows.size > 0 && (
                <div className="hs-selection-bar">
                  <span className="hs-sel-count">{prospectingSelectedRows.size} selected</span>
                  <div className="hs-sel-sep" />
                  <button className="hs-sel-btn">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    Edit
                  </button>
                  <button className="hs-sel-btn" style={{ color: '#dc2626' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                    Delete
                  </button>
                  <button className="hs-sel-close" onClick={() => setProspectingSelectedRows(new Set())} title="Deselect all">&times;</button>
                </div>
              )}

              {/* Row 5: Data Table */}
              <div className="hs-table-scroll">
                <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: 40 }} />
                    <col style={{ width: prospColWidths.title }} />
                    <col style={{ width: prospColWidths.status }} />
                    <col style={{ width: prospColWidths.source }} />
                    <col style={{ width: prospColWidths.contact }} />
                    <col style={{ width: prospColWidths.assigned_to }} />
                    <col style={{ width: prospColWidths.created }} />
                    <col style={{ width: prospColWidths.updated }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="hs-th-check"><input type="checkbox" checked={prospPagedData.length > 0 && prospectingSelectedRows.size === prospPagedData.length} onChange={toggleAllProspRows} /></th>
                      <th className="hs-th-resizable hs-th-sortable hs-th-frozen" style={{ position: 'sticky', left: 40, top: 0, zIndex: 15, background: '#f8fafc', boxShadow: '2px 0 4px rgba(0,0,0,0.06)' }} onClick={() => toggleProspSort('title')}>
                        <span className="hs-th-content">Title{prospectingSort.field === 'title' && <span className="hs-sort-arrow">{prospectingSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}{prospectingSort.field !== 'title' && <span className="hs-th-hover-sort"><svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M29.38 21.96a1.147 1.147 0 0 0-1.617 0l-3.85 3.852V5.714a1.143 1.143 0 0 0-2.286 0v20.099l-3.853-3.852a1.144 1.144 0 0 0-1.615 1.617l5.803 5.802a1.143 1.143 0 0 0 1.616 0l5.802-5.802a1.144 1.144 0 0 0 0-1.617M15.84 8.422 10.039 2.62a1.145 1.145 0 0 0-1.616 0L2.62 8.422a1.144 1.144 0 0 0 1.617 1.617l3.85-3.851v20.098a1.143 1.143 0 0 0 2.286 0V6.187l3.853 3.852a1.14 1.14 0 0 0 1.616-.001 1.14 1.14 0 0 0-.001-1.616"/></svg></span>}</span>
                        <div className="hs-col-resize-handle" onMouseDown={(e) => onProspResizeStart('title', e)} onClick={(e) => e.stopPropagation()} />
                      </th>
                      <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleProspSort('status')}><span className="hs-th-content">Status{prospectingSort.field === 'status' && <span className="hs-sort-arrow">{prospectingSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}{prospectingSort.field !== 'status' && <span className="hs-th-hover-sort"><svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M29.38 21.96a1.147 1.147 0 0 0-1.617 0l-3.85 3.852V5.714a1.143 1.143 0 0 0-2.286 0v20.099l-3.853-3.852a1.144 1.144 0 0 0-1.615 1.617l5.803 5.802a1.143 1.143 0 0 0 1.616 0l5.802-5.802a1.144 1.144 0 0 0 0-1.617M15.84 8.422 10.039 2.62a1.145 1.145 0 0 0-1.616 0L2.62 8.422a1.144 1.144 0 0 0 1.617 1.617l3.85-3.851v20.098a1.143 1.143 0 0 0 2.286 0V6.187l3.853 3.852a1.14 1.14 0 0 0 1.616-.001 1.14 1.14 0 0 0-.001-1.616"/></svg></span>}</span><div className="hs-col-resize-handle" onMouseDown={(e) => onProspResizeStart('status', e)} onClick={(e) => e.stopPropagation()} /></th>
                      <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleProspSort('source')}><span className="hs-th-content">Source{prospectingSort.field === 'source' && <span className="hs-sort-arrow">{prospectingSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}{prospectingSort.field !== 'source' && <span className="hs-th-hover-sort"><svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M29.38 21.96a1.147 1.147 0 0 0-1.617 0l-3.85 3.852V5.714a1.143 1.143 0 0 0-2.286 0v20.099l-3.853-3.852a1.144 1.144 0 0 0-1.615 1.617l5.803 5.802a1.143 1.143 0 0 0 1.616 0l5.802-5.802a1.144 1.144 0 0 0 0-1.617M15.84 8.422 10.039 2.62a1.145 1.145 0 0 0-1.616 0L2.62 8.422a1.144 1.144 0 0 0 1.617 1.617l3.85-3.851v20.098a1.143 1.143 0 0 0 2.286 0V6.187l3.853 3.852a1.14 1.14 0 0 0 1.616-.001 1.14 1.14 0 0 0-.001-1.616"/></svg></span>}</span><div className="hs-col-resize-handle" onMouseDown={(e) => onProspResizeStart('source', e)} onClick={(e) => e.stopPropagation()} /></th>
                      <th className="hs-th-resizable"><span className="hs-th-content">Contact</span><div className="hs-col-resize-handle" onMouseDown={(e) => onProspResizeStart('contact', e)} onClick={(e) => e.stopPropagation()} /></th>
                      <th className="hs-th-resizable"><span className="hs-th-content">Assigned To</span><div className="hs-col-resize-handle" onMouseDown={(e) => onProspResizeStart('assigned_to', e)} onClick={(e) => e.stopPropagation()} /></th>
                      <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleProspSort('date_create')}><span className="hs-th-content">Created{prospectingSort.field === 'date_create' && <span className="hs-sort-arrow">{prospectingSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}{prospectingSort.field !== 'date_create' && <span className="hs-th-hover-sort"><svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M29.38 21.96a1.147 1.147 0 0 0-1.617 0l-3.85 3.852V5.714a1.143 1.143 0 0 0-2.286 0v20.099l-3.853-3.852a1.144 1.144 0 0 0-1.615 1.617l5.803 5.802a1.143 1.143 0 0 0 1.616 0l5.802-5.802a1.144 1.144 0 0 0 0-1.617M15.84 8.422 10.039 2.62a1.145 1.145 0 0 0-1.616 0L2.62 8.422a1.144 1.144 0 0 0 1.617 1.617l3.85-3.851v20.098a1.143 1.143 0 0 0 2.286 0V6.187l3.853 3.852a1.14 1.14 0 0 0 1.616-.001 1.14 1.14 0 0 0-.001-1.616"/></svg></span>}</span><div className="hs-col-resize-handle" onMouseDown={(e) => onProspResizeStart('created', e)} onClick={(e) => e.stopPropagation()} /></th>
                      <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleProspSort('date_modify')}><span className="hs-th-content">Last Updated{prospectingSort.field === 'date_modify' && <span className="hs-sort-arrow">{prospectingSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}{prospectingSort.field !== 'date_modify' && <span className="hs-th-hover-sort"><svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M29.38 21.96a1.147 1.147 0 0 0-1.617 0l-3.85 3.852V5.714a1.143 1.143 0 0 0-2.286 0v20.099l-3.853-3.852a1.144 1.144 0 0 0-1.615 1.617l5.803 5.802a1.143 1.143 0 0 0 1.616 0l5.802-5.802a1.144 1.144 0 0 0 0-1.617M15.84 8.422 10.039 2.62a1.145 1.145 0 0 0-1.616 0L2.62 8.422a1.144 1.144 0 0 0 1.617 1.617l3.85-3.851v20.098a1.143 1.143 0 0 0 2.286 0V6.187l3.853 3.852a1.14 1.14 0 0 0 1.616-.001 1.14 1.14 0 0 0-.001-1.616"/></svg></span>}</span><div className="hs-col-resize-handle" onMouseDown={(e) => onProspResizeStart('updated', e)} onClick={(e) => e.stopPropagation()} /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {prospectingLoading ? (
                      <tr><td colSpan={8} className="hs-loading-cell"><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}><DotLottieReact src="/loading.lottie" loop autoplay style={{ width: 80, height: 80 }} /><span style={{ fontSize: '0.82rem', color: '#7c98b6', marginTop: 8 }}>Loading prospects...</span></div></td></tr>
                    ) : prospPagedData.length === 0 ? (
                      <tr><td colSpan={8} className="hs-empty-cell">
                        <div style={{ padding: '40px', textAlign: 'center' }}>
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '12px' }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                          <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#33475b', marginBottom: '6px' }}>No prospects found</div>
                          <div style={{ fontSize: '0.82rem', color: '#7c98b6', maxWidth: '360px', margin: '0 auto' }}>Create a new lead or adjust your search and filter criteria.</div>
                        </div>
                      </td></tr>
                    ) : prospPagedData.map((lead: any) => (
                      <tr key={lead.id} className={`hs-row${prospectingSelectedRows.has(Number(lead.id)) ? ' selected' : ''}`}>
                        <td className="hs-td-check"><input type="checkbox" checked={prospectingSelectedRows.has(Number(lead.id))} onChange={() => setProspectingSelectedRows(prev => { const n = new Set(prev); n.has(Number(lead.id)) ? n.delete(Number(lead.id)) : n.add(Number(lead.id)); return n })} /></td>
                        <td className="hs-td-name" style={{ position: 'sticky', left: 40, zIndex: 2, background: prospectingSelectedRows.has(Number(lead.id)) ? '#eff6ff' : '#fff' }}>
                          <div className="hs-name-cell">
                            <span className="hs-name-link" onClick={() => setActiveView('leads')}>{lead.title || lead.client_name || 'Untitled'}</span>
                            <button className="hs-preview-btn" onClick={(e) => { e.stopPropagation() }}>Preview</button>
                          </div>
                        </td>
                        <td><span className="hs-status-badge" style={{ background: lead.status === 'new' ? '#eff6ff' : lead.status === 'contacted' ? '#ecfdf5' : lead.status === 'qualified' ? '#f5f3ff' : '#f8fafc', color: lead.status === 'new' ? '#2563eb' : lead.status === 'contacted' ? '#059669' : lead.status === 'qualified' ? '#7c3aed' : '#64748b' }}>{lead.status || '--'}</span></td>
                        <td style={{ color: '#516f90', fontSize: '0.82rem' }}>{lead.source || '--'}</td>
                        <td style={{ color: '#33475b', fontSize: '0.82rem' }}>{lead.client_name || '--'}</td>
                        <td style={{ color: '#516f90', fontSize: '0.82rem' }}>{lead.assigned_to || '--'}</td>
                        <td style={{ color: '#516f90', fontSize: '0.8rem' }}>{lead.date_create ? formatDatePref(lead.date_create) : '--'}</td>
                        <td style={{ color: '#516f90', fontSize: '0.8rem' }}>{lead.date_modify ? formatDatePref(lead.date_modify) : '--'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Row 6: Pagination */}
              <div className="hs-pagination">
                <nav className="hs-paginator" aria-label="Pagination">
                  <button className="hs-paginator-btn" disabled={prospCurrentPage <= 1} onClick={() => setProspectingPage(p => p - 1)} aria-label="Previous page">
                    <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95"/></svg>
                    <span>Prev</span>
                  </button>
                  {(() => {
                    if (prospTotalPages <= 7) {
                      const pages: number[] = []; for (let i = 1; i <= prospTotalPages; i++) pages.push(i)
                      return pages.map(p => <button key={p} className={`hs-paginator-num${p === prospCurrentPage ? ' active' : ''}`} onClick={() => setProspectingPage(p)}>{p}</button>)
                    }
                    const items: React.ReactNode[] = []
                    const addPage = (p: number) => items.push(<button key={p} className={`hs-paginator-num${p === prospCurrentPage ? ' active' : ''}`} onClick={() => setProspectingPage(p)}>{p}</button>)
                    const addEllipsis = (key: string) => items.push(<span key={key} className="hs-paginator-ellipsis">...</span>)
                    const shown = new Set<number>()
                    for (let i = 1; i <= Math.min(4, prospTotalPages); i++) shown.add(i)
                    shown.add(prospTotalPages)
                    for (let i = Math.max(1, prospCurrentPage - 1); i <= Math.min(prospTotalPages, prospCurrentPage + 1); i++) shown.add(i)
                    const sorted = [...shown].sort((a, b) => a - b)
                    for (let i = 0; i < sorted.length; i++) {
                      if (i > 0 && sorted[i] - sorted[i - 1] > 1) addEllipsis(`e${i}`)
                      addPage(sorted[i])
                    }
                    return items
                  })()}
                  <button className="hs-paginator-btn" disabled={prospCurrentPage >= prospTotalPages} onClick={() => setProspectingPage(p => p + 1)} aria-label="Next page">
                    <span>Next</span>
                    <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M11 27.71c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62l9.48-9.48-9.48-9.47a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0L22.1 15.48c.45.45.45 1.17 0 1.62L11.81 27.39c-.22.22-.52.33-.81.33Z"/></svg>
                  </button>
                </nav>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', marginLeft: 'auto' }}><select value={tablePageSize} onChange={e => { setTablePageSize(Number(e.target.value) as any); setProspectingPage(1) }} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #e2e8f0', fontSize: '0.82rem' }}><option value={100}>100</option><option value={200}>200</option><option value={500}>500</option></select><span>per page</span></div>
              </div>

              {/* Row 7: Settings Panel */}
              {tableSettingsOpen && (
                <div className="hs-settings-panel">
                  <div className="hs-settings-header">
                    <span className="hs-settings-title">Table settings</span>
                    <button className="hs-settings-close" onClick={() => setTableSettingsOpen(false)}>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                    </button>
                  </div>
                  <div className="hs-settings-body">
                    <div className="hs-settings-group">
                      <label className="hs-settings-label">Pagination<span className="hs-info-icon" title="Number of rows shown per page"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span></label>
                      {([100, 200, 500] as const).map(size => (
                        <label className="hs-settings-radio" key={size}>
                          <input type="radio" name="prospPageSize" checked={tablePageSize === size} onChange={() => { setTablePageSize(size); setProspectingPage(1) }} />
                          <span className="hs-radio-indicator" /><span>{size} per page</span>
                        </label>
                      ))}
                    </div>
                    <hr className="hs-settings-divider" />
                    <div className="hs-settings-group">
                      <label className="hs-settings-label">Row height<span className="hs-info-icon" title="Adjust the vertical spacing of table rows"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span></label>
                      {([{ value: 'small' as const, label: 'Compact' }, { value: 'medium' as const, label: 'Default' }, { value: 'large' as const, label: 'Comfortable' }]).map(opt => (
                        <label className="hs-settings-radio" key={opt.value}>
                          <input type="radio" name="prospRowHeight" checked={tableRowHeight === opt.value} onChange={() => setTableRowHeight(opt.value)} />
                          <span className="hs-radio-indicator" /><span>{opt.label}</span>
                        </label>
                      ))}
                    </div>
                    <hr className="hs-settings-divider" />
                    <div className="hs-settings-group">
                      <div className="hs-settings-toggle-row">
                        <label className="hs-settings-label">Zebra striping<span className="hs-info-icon" title="Alternate row background colors"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span></label>
                        <button className={`hs-toggle${tableZebraStriping ? ' on' : ''}`} onClick={() => setTableZebraStriping(!tableZebraStriping)} role="switch" aria-checked={tableZebraStriping}>
                          <span className="hs-toggle-track"><span className="hs-toggle-on">ON</span><span className="hs-toggle-off">OFF</span><span className="hs-toggle-handle" /></span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          )
        })()}

        {/* ========== Target Accounts View ========== */}
        {activeView === 'target-accounts' && (() => {
          const taSearchLower = targetAccountsSearch.toLowerCase()
          const filteredTa = targetAccountsData.filter(a => {
            if (targetAccountsViewTab === 'active' && a.target_status !== 'active') return false
            if (targetAccountsViewTab === 'nurturing' && a.target_status !== 'nurturing') return false
            if (targetAccountsViewTab === 'won' && a.target_status !== 'won') return false
            if (targetAccountsViewTab === 'lost' && a.target_status !== 'lost') return false
            if (targetTierFilter && String(a.target_tier) !== targetTierFilter) return false
            if (targetStatusFilter && a.target_status !== targetStatusFilter) return false
            if (taSearchLower) {
              const n = String(a.name ?? a.company_name ?? '').toLowerCase()
              const c = String(a.city ?? '').toLowerCase()
              if (!n.includes(taSearchLower) && !c.includes(taSearchLower)) return false
            }
            return true
          })
          filteredTa.sort((a: any, b: any) => {
            const f = targetAccountsSort.field
            const o = targetAccountsSort.order === 'ASC' ? 1 : -1
            const av = f === 'engagement_score' || f === 'deal_count' || f === 'total_deal_value' || f === 'activity_count' || f === 'target_tier' ? Number(a[f] ?? 0) : String(a[f] ?? '')
            const bv = f === 'engagement_score' || f === 'deal_count' || f === 'total_deal_value' || f === 'activity_count' || f === 'target_tier' ? Number(b[f] ?? 0) : String(b[f] ?? '')
            return av < bv ? -o : av > bv ? o : 0
          })
          const taTotal = filteredTa.length
          const taPageSize = tablePageSize
          const taTotalPages = Math.max(1, Math.ceil(taTotal / taPageSize))
          const taCurrentPage = Math.min(targetAccountsPage, taTotalPages)
          const taPagedData = filteredTa.slice((taCurrentPage - 1) * taPageSize, taCurrentPage * taPageSize)
          const toggleAllTaRows = () => {
            if (targetAccountsSelectedRows.size === taPagedData.length) {
              setTargetAccountsSelectedRows(new Set())
            } else {
              setTargetAccountsSelectedRows(new Set(taPagedData.map(a => Number(a.id))))
            }
          }
          const toggleTaSort = (field: string) => {
            setTargetAccountsSort(prev => prev.field === field ? { field, order: prev.order === 'ASC' ? 'DESC' : 'ASC' } : { field, order: field === 'name' || field === 'target_status' ? 'ASC' : 'DESC' })
            setTargetAccountsPage(1)
          }
          const onTaResizeStart = (col: string, e: React.MouseEvent) => {
            e.preventDefault(); e.stopPropagation()
            const startX = e.clientX; const startW = taColWidths[col] || 120
            const onMove = (ev: MouseEvent) => setTaColWidths(prev => ({ ...prev, [col]: Math.max(60, startW + ev.clientX - startX) }))
            const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp) }
            document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp)
          }
          return (
          <div className="hs-contacts">
            {/* Row 1: Topbar */}
            <div className="hs-topbar">
              <div className="hs-topbar-left">
                <div className="hs-obj-switcher-wrap" ref={objSwitcherRef}>
                  <button className={`hs-obj-switcher${objSwitcherOpen ? ' open' : ''}`} onClick={() => { setObjSwitcherOpen(!objSwitcherOpen); setObjSwitcherSearch('') }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>
                    <span>Target Accounts</span>
                    <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                  </button>
                  {objSwitcherOpen && (
                    <div className="hs-obj-panel">
                      <div className="hs-obj-search-wrap">
                        <svg className="hs-obj-search-icon" width="13" height="13" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                        <input className="hs-obj-search" placeholder="Search" value={objSwitcherSearch} onChange={(e) => setObjSwitcherSearch(e.target.value)} autoFocus />
                      </div>
                      <ul className="hs-obj-list" role="listbox">
                        {[
                          { label: 'Prospecting', view: 'prospecting' as ViewMode },
                          { label: 'Deals', view: 'deals' as ViewMode },
                          { label: 'Forecasts', view: 'forecasts' as ViewMode },
                          { label: 'Target Accounts', view: 'target-accounts' as ViewMode },
                          { label: 'Activity Feed', view: 'activity-feed' as ViewMode },
                          { label: 'Documents', view: 'sales-documents' as ViewMode },
                          { label: 'Meetings', view: 'meetings-scheduler' as ViewMode },
                          { label: 'Coaching Playlists', view: 'coaching-playlists' as ViewMode },
                          { label: 'Payments', view: 'payments' as ViewMode },
                          { label: 'Quotes', view: 'quotes' as ViewMode },
                          { label: 'Products', view: 'products' as ViewMode },
                        ].filter(item => !objSwitcherSearch || item.label.toLowerCase().includes(objSwitcherSearch.toLowerCase())).map(item => (
                          <li key={item.view} className={`hs-obj-item${item.view === activeView ? ' active' : ''}`} role="option" aria-selected={item.view === activeView} onClick={() => { setActiveView(item.view); setObjSwitcherOpen(false); setObjSwitcherSearch('') }}>{item.label}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="hs-topbar-sep" />
                <div className="hs-view-tabs">
                  {[
                    { key: '', label: 'All accounts' },
                    { key: 'active', label: 'Active' },
                    { key: 'nurturing', label: 'Nurturing' },
                    { key: 'won', label: 'Won' },
                    { key: 'lost', label: 'Lost' },
                  ].map((tab) => {
                    const isActive = targetAccountsViewTab === tab.key
                    return (
                      <div key={tab.key} className={`hs-view-tab${isActive ? ' active' : ''}`} onClick={() => { setTargetAccountsViewTab(tab.key); setTargetAccountsPage(1) }}>
                        <span className="hs-tab-label">{tab.label}</span>
                        {isActive && taTotal > 0 && <span className="hs-tab-count">{taTotal.toLocaleString()}</span>}
                        {isActive && (
                          <span className="hs-tab-kebab" onClick={(e) => { e.stopPropagation(); setTabEditOpen(!tabEditOpen) }}>
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>
                          </span>
                        )}
                        {tab.key !== '' && <span className="hs-tab-x" onClick={(e) => { e.stopPropagation(); setTargetAccountsViewTab(''); setTargetAccountsPage(1) }}>&times;</span>}
                      </div>
                    )
                  })}
                  <div className="hs-topbar-sep" />
                  <button className="hs-quick-add-btn" title="Add view"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg></button>
                </div>
              </div>
              <div className="hs-topbar-right">
                <div className="hs-add-btn-wrap" ref={addBtnRef}>
                  <button className="hs-add-contacts-btn" onClick={() => setAddBtnOpen(!addBtnOpen)}>
                    Add target account
                    <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                  </button>
                  {addBtnOpen && (
                    <div className="hs-add-btn-dropdown">
                      <button className="hs-add-btn-item" onClick={() => { setAddBtnOpen(false); setMarkTargetClientId(''); setMarkTargetTier(2); setShowMarkTargetModal(true) }}>Add new target</button>
                      <button className="hs-add-btn-item" onClick={() => { setAddBtnOpen(false); recalculateEngagementScores().then(() => { setTargetAccountsLoading(true); fetchTargetAccounts().then(r => { setTargetAccountsData(r.data); setTargetAccountsLoading(false) }) }).catch(console.error) }}>Recalculate scores</button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Row 2: Toolbar */}
            <div className="hs-toolbar">
              <div className="hs-toolbar-left">
                <div className="hs-search-wrap">
                  <svg className="hs-search-icon" width="14" height="14" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M28.14 29.29c-.29 0-.58-.11-.81-.33l-7.58-7.58a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l7.58 7.58a1.14 1.14 0 0 1-.81 1.95"/><path d="M13.29 24.71c-6.3 0-11.43-5.13-11.43-11.43S6.98 1.86 13.29 1.86s11.43 5.13 11.43 11.43-5.13 11.43-11.43 11.43Zm0-20.57c-5.04 0-9.14 4.1-9.14 9.14s4.1 9.14 9.14 9.14 9.14-4.1 9.14-9.14-4.1-9.14-9.14-9.14"/></svg>
                  <input className="hs-search-input" placeholder="Search" value={targetAccountsSearch} onChange={(e) => { setTargetAccountsSearch(e.target.value); setTargetAccountsPage(1) }} />
                  {targetAccountsSearch && <button className="hs-search-clear" onClick={() => { setTargetAccountsSearch(''); setTargetAccountsPage(1) }}>&times;</button>}
                </div>
                <div className="hs-tb-sep" />
                <div className="hs-viewtype-group">
                  <button className="hs-tb-btn hs-viewtype-btn">
                    <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                    <span>Table</span>
                  </button>
                  <button className={`hs-tb-btn hs-settings-btn${tableSettingsOpen ? ' active' : ''}`} onClick={() => setTableSettingsOpen(!tableSettingsOpen)} title={tableSettingsOpen ? 'Close settings' : 'Table settings'}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                  </button>
                </div>
                <div className="hs-tb-sep" />
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Edit columns">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="18" rx="1"/><rect x="14" y="3" width="7" height="18" rx="1"/></svg>
                  <span>Edit columns</span>
                </button>
                <div className="hs-tb-sep" />
                <button className={`hs-tb-btn hs-tb-btn-bordered${quickFiltersOpen ? ' hs-tb-btn-pressed' : ''}`} title="Filters" onClick={() => setQuickFiltersOpen(!quickFiltersOpen)}>
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M30.71 16.86H13.57a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28M6.71 16.86H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M10.14 20.29c-2.52 0-4.57-2.05-4.57-4.57s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M6.71 6.57H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M30.71 6.57H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M18.14 6.57H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 10C19.05 10 17 7.95 17 5.43S19.05.86 21.57.86s4.57 2.05 4.57 4.57S24.09 10 21.57 10m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M30.71 27.14H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M18.14 27.14H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 30.57C19.05 30.57 17 28.52 17 26s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29"/></svg>
                  <span>Filters</span>
                </button>
                <div className="hs-tb-sep" />
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Export">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  <span>Export</span>
                </button>
              </div>
              <div className="hs-toolbar-right">
                <div className="hs-tb-sep" />
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Clone view">
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M25.86 29.29H12.15c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-1.89 1.54-3.43 3.43-3.43h13.71c1.89 0 3.43 1.54 3.43 3.43v13.71c0 1.89-1.54 3.43-3.43 3.43M12.14 11c-.63 0-1.14.51-1.14 1.14v13.71c0 .63.51 1.14 1.14 1.14h13.71c.63 0 1.14-.51 1.14-1.14V12.14c0-.63-.51-1.14-1.14-1.14z"/><path d="M5.29 22.43c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43H19c1.89 0 3.43 1.54 3.43 3.43a1.14 1.14 0 1 1-2.28 0c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14V19c0 .63.51 1.14 1.14 1.14a1.14 1.14 0 1 1 0 2.28Z"/></svg>
                </button>
                <button className="hs-tb-btn hs-tb-btn-bordered disabled" disabled title="Save view">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                </button>
              </div>
            </div>

            {/* Row 3: Quick Filters */}
            {quickFiltersOpen && <div className="hs-quick-filters">
              <div className="hs-owner-dropdown" ref={ownerDropdownRef}>
                <button className={`hs-qf-btn hs-qf-trigger${ownerDropdownOpen ? ' open' : ''}${ownerSelected.size > 0 ? ' has-value' : ''}`} onClick={() => setOwnerDropdownOpen(!ownerDropdownOpen)}>
                  <span>Account owner</span>
                  {ownerSelected.size > 0 && <span className="hs-qf-count">{ownerSelected.size}</span>}
                  <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                </button>
                {ownerDropdownOpen && (
                  <div className="hs-owner-panel">
                    <div className="hs-owner-search-wrap">
                      <svg className="hs-owner-search-icon" width="13" height="13" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                      <input className="hs-owner-search" placeholder="Search" value={ownerSearch} onChange={(e) => setOwnerSearch(e.target.value)} autoFocus />
                    </div>
                    <div className="hs-owner-list">
                      {(!ownerSearch || 'me'.includes(ownerSearch.toLowerCase())) && (
                        <label className="hs-owner-item hs-owner-me">
                          <input type="checkbox" checked={ownerSelected.has(8)} onChange={() => setOwnerSelected(prev => { const n = new Set(prev); n.has(8) ? n.delete(8) : n.add(8); return n })} />
                          <span className="hs-owner-checkbox" /><span className="hs-owner-label">Me</span>
                        </label>
                      )}
                      {crmUsers.filter(u => !ownerSearch || u.name.toLowerCase().includes(ownerSearch.toLowerCase())).map(user => (
                        <label className="hs-owner-item" key={user.id}>
                          <input type="checkbox" checked={ownerSelected.has(user.id)} onChange={() => setOwnerSelected(prev => { const n = new Set(prev); n.has(user.id) ? n.delete(user.id) : n.add(user.id); return n })} />
                          <span className="hs-owner-checkbox" /><span className="hs-owner-avatar">{user.initials || user.name.charAt(0)}</span><span className="hs-owner-label">{user.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <button className={`hs-qf-btn hs-qf-trigger${targetTierFilter ? ' has-value' : ''}`} onClick={() => { const vals = ['', '1', '2', '3']; const idx = vals.indexOf(targetTierFilter); setTargetTierFilter(vals[(idx + 1) % vals.length]); setTargetAccountsPage(1) }}>
                <span>{targetTierFilter ? `Tier ${targetTierFilter}` : 'Tier'}</span>
                {targetTierFilter ? <span className="hs-qf-clear" onClick={(e) => { e.stopPropagation(); setTargetTierFilter(''); setTargetAccountsPage(1) }}>&times;</span> : <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>}
              </button>
              <button className={`hs-qf-btn hs-qf-trigger${targetAccountsSort.field !== 'engagement_score' ? ' has-value' : ''}`} onClick={() => { const fields = ['engagement_score', 'name', 'target_tier', 'total_deal_value', 'activity_count']; const idx = fields.indexOf(targetAccountsSort.field); const next = fields[(idx + 1) % fields.length]; setTargetAccountsSort({ field: next, order: next === 'name' ? 'ASC' : 'DESC' }); setTargetAccountsPage(1) }}>
                <span>Sort: {{ engagement_score: 'Engagement', name: 'Name', target_tier: 'Tier', total_deal_value: 'Deal Value', activity_count: 'Activities' }[targetAccountsSort.field] || 'Engagement'}</span>
                <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
              </button>
              <div className="hs-qf-sep" />
              <div className="hs-qf-more-wrap">
                <button className="hs-qf-btn hs-qf-more">
                  <svg width="10" height="10" viewBox="0 0 32 32" fill="currentColor"><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V3a1.14 1.14 0 1 1 2.28 0v25.14c0 .63-.51 1.14-1.14 1.14Z"/><path d="M28.14 16.71H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28"/></svg>
                  <span>More</span>
                </button>
              </div>
            </div>}

            {/* Row 4: Selection Bar + Data Table + Pagination + Settings */}
            <div className={`hs-content${tableSettingsOpen ? ' with-settings' : ''}`}>
              {targetAccountsSelectedRows.size > 0 && (
                <div className="hs-selection-bar">
                  <span className="hs-sel-count">{targetAccountsSelectedRows.size} selected</span>
                  <div className="hs-sel-sep" />
                  <button className="hs-sel-btn">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    Edit
                  </button>
                  <button className="hs-sel-btn" style={{ color: '#dc2626' }} onClick={() => {
                    if (confirm(`Remove ${targetAccountsSelectedRows.size} target account(s)?`)) {
                      Promise.all([...targetAccountsSelectedRows].map(id => unmarkTargetAccount(id))).then(() => {
                        setTargetAccountsData(prev => prev.filter(a => !targetAccountsSelectedRows.has(a.id)))
                        setTargetAccountsSelectedRows(new Set())
                      }).catch(() => {})
                    }
                  }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                    Delete
                  </button>
                  <button className="hs-sel-close" onClick={() => setTargetAccountsSelectedRows(new Set())} title="Deselect all">&times;</button>
                </div>
              )}

              {/* Row 5: Data Table */}
              <div className="hs-table-scroll">
                <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: 40 }} />
                    <col style={{ width: taColWidths.name }} />
                    <col style={{ width: taColWidths.tier }} />
                    <col style={{ width: taColWidths.status }} />
                    <col style={{ width: taColWidths.engagement }} />
                    <col style={{ width: taColWidths.deals }} />
                    <col style={{ width: taColWidths.deal_value }} />
                    <col style={{ width: taColWidths.activities }} />
                    <col style={{ width: taColWidths.last_activity }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="hs-th-check"><input type="checkbox" checked={taPagedData.length > 0 && targetAccountsSelectedRows.size === taPagedData.length} onChange={toggleAllTaRows} /></th>
                      <th className="hs-th-resizable hs-th-sortable hs-th-frozen" style={{ position: 'sticky', left: 40, top: 0, zIndex: 15, background: '#f8fafc', boxShadow: '2px 0 4px rgba(0,0,0,0.06)' }} onClick={() => toggleTaSort('name')}>
                        <span className="hs-th-content">Company{targetAccountsSort.field === 'name' && <span className="hs-sort-arrow">{targetAccountsSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}{targetAccountsSort.field !== 'name' && <span className="hs-th-hover-sort"><svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M29.38 21.96a1.147 1.147 0 0 0-1.617 0l-3.85 3.852V5.714a1.143 1.143 0 0 0-2.286 0v20.099l-3.853-3.852a1.144 1.144 0 0 0-1.615 1.617l5.803 5.802a1.143 1.143 0 0 0 1.616 0l5.802-5.802a1.144 1.144 0 0 0 0-1.617M15.84 8.422 10.039 2.62a1.145 1.145 0 0 0-1.616 0L2.62 8.422a1.144 1.144 0 0 0 1.617 1.617l3.85-3.851v20.098a1.143 1.143 0 0 0 2.286 0V6.187l3.853 3.852a1.14 1.14 0 0 0 1.616-.001 1.14 1.14 0 0 0-.001-1.616"/></svg></span>}</span>
                        <div className="hs-col-resize-handle" onMouseDown={(e) => onTaResizeStart('name', e)} onClick={(e) => e.stopPropagation()} />
                      </th>
                      <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleTaSort('target_tier')}><span className="hs-th-content">Tier{targetAccountsSort.field === 'target_tier' && <span className="hs-sort-arrow">{targetAccountsSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}{targetAccountsSort.field !== 'target_tier' && <span className="hs-th-hover-sort"><svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M29.38 21.96a1.147 1.147 0 0 0-1.617 0l-3.85 3.852V5.714a1.143 1.143 0 0 0-2.286 0v20.099l-3.853-3.852a1.144 1.144 0 0 0-1.615 1.617l5.803 5.802a1.143 1.143 0 0 0 1.616 0l5.802-5.802a1.144 1.144 0 0 0 0-1.617M15.84 8.422 10.039 2.62a1.145 1.145 0 0 0-1.616 0L2.62 8.422a1.144 1.144 0 0 0 1.617 1.617l3.85-3.851v20.098a1.143 1.143 0 0 0 2.286 0V6.187l3.853 3.852a1.14 1.14 0 0 0 1.616-.001 1.14 1.14 0 0 0-.001-1.616"/></svg></span>}</span><div className="hs-col-resize-handle" onMouseDown={(e) => onTaResizeStart('tier', e)} onClick={(e) => e.stopPropagation()} /></th>
                      <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleTaSort('target_status')}><span className="hs-th-content">Status{targetAccountsSort.field === 'target_status' && <span className="hs-sort-arrow">{targetAccountsSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}{targetAccountsSort.field !== 'target_status' && <span className="hs-th-hover-sort"><svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M29.38 21.96a1.147 1.147 0 0 0-1.617 0l-3.85 3.852V5.714a1.143 1.143 0 0 0-2.286 0v20.099l-3.853-3.852a1.144 1.144 0 0 0-1.615 1.617l5.803 5.802a1.143 1.143 0 0 0 1.616 0l5.802-5.802a1.144 1.144 0 0 0 0-1.617M15.84 8.422 10.039 2.62a1.145 1.145 0 0 0-1.616 0L2.62 8.422a1.144 1.144 0 0 0 1.617 1.617l3.85-3.851v20.098a1.143 1.143 0 0 0 2.286 0V6.187l3.853 3.852a1.14 1.14 0 0 0 1.616-.001 1.14 1.14 0 0 0-.001-1.616"/></svg></span>}</span><div className="hs-col-resize-handle" onMouseDown={(e) => onTaResizeStart('status', e)} onClick={(e) => e.stopPropagation()} /></th>
                      <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleTaSort('engagement_score')}><span className="hs-th-content">Engagement{targetAccountsSort.field === 'engagement_score' && <span className="hs-sort-arrow">{targetAccountsSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}{targetAccountsSort.field !== 'engagement_score' && <span className="hs-th-hover-sort"><svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M29.38 21.96a1.147 1.147 0 0 0-1.617 0l-3.85 3.852V5.714a1.143 1.143 0 0 0-2.286 0v20.099l-3.853-3.852a1.144 1.144 0 0 0-1.615 1.617l5.803 5.802a1.143 1.143 0 0 0 1.616 0l5.802-5.802a1.144 1.144 0 0 0 0-1.617M15.84 8.422 10.039 2.62a1.145 1.145 0 0 0-1.616 0L2.62 8.422a1.144 1.144 0 0 0 1.617 1.617l3.85-3.851v20.098a1.143 1.143 0 0 0 2.286 0V6.187l3.853 3.852a1.14 1.14 0 0 0 1.616-.001 1.14 1.14 0 0 0-.001-1.616"/></svg></span>}</span><div className="hs-col-resize-handle" onMouseDown={(e) => onTaResizeStart('engagement', e)} onClick={(e) => e.stopPropagation()} /></th>
                      <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleTaSort('deal_count')}><span className="hs-th-content">Deals{targetAccountsSort.field === 'deal_count' && <span className="hs-sort-arrow">{targetAccountsSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}{targetAccountsSort.field !== 'deal_count' && <span className="hs-th-hover-sort"><svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M29.38 21.96a1.147 1.147 0 0 0-1.617 0l-3.85 3.852V5.714a1.143 1.143 0 0 0-2.286 0v20.099l-3.853-3.852a1.144 1.144 0 0 0-1.615 1.617l5.803 5.802a1.143 1.143 0 0 0 1.616 0l5.802-5.802a1.144 1.144 0 0 0 0-1.617M15.84 8.422 10.039 2.62a1.145 1.145 0 0 0-1.616 0L2.62 8.422a1.144 1.144 0 0 0 1.617 1.617l3.85-3.851v20.098a1.143 1.143 0 0 0 2.286 0V6.187l3.853 3.852a1.14 1.14 0 0 0 1.616-.001 1.14 1.14 0 0 0-.001-1.616"/></svg></span>}</span><div className="hs-col-resize-handle" onMouseDown={(e) => onTaResizeStart('deals', e)} onClick={(e) => e.stopPropagation()} /></th>
                      <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleTaSort('total_deal_value')}><span className="hs-th-content">Deal Value{targetAccountsSort.field === 'total_deal_value' && <span className="hs-sort-arrow">{targetAccountsSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}{targetAccountsSort.field !== 'total_deal_value' && <span className="hs-th-hover-sort"><svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M29.38 21.96a1.147 1.147 0 0 0-1.617 0l-3.85 3.852V5.714a1.143 1.143 0 0 0-2.286 0v20.099l-3.853-3.852a1.144 1.144 0 0 0-1.615 1.617l5.803 5.802a1.143 1.143 0 0 0 1.616 0l5.802-5.802a1.144 1.144 0 0 0 0-1.617M15.84 8.422 10.039 2.62a1.145 1.145 0 0 0-1.616 0L2.62 8.422a1.144 1.144 0 0 0 1.617 1.617l3.85-3.851v20.098a1.143 1.143 0 0 0 2.286 0V6.187l3.853 3.852a1.14 1.14 0 0 0 1.616-.001 1.14 1.14 0 0 0-.001-1.616"/></svg></span>}</span><div className="hs-col-resize-handle" onMouseDown={(e) => onTaResizeStart('deal_value', e)} onClick={(e) => e.stopPropagation()} /></th>
                      <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleTaSort('activity_count')}><span className="hs-th-content">Activities{targetAccountsSort.field === 'activity_count' && <span className="hs-sort-arrow">{targetAccountsSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}{targetAccountsSort.field !== 'activity_count' && <span className="hs-th-hover-sort"><svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M29.38 21.96a1.147 1.147 0 0 0-1.617 0l-3.85 3.852V5.714a1.143 1.143 0 0 0-2.286 0v20.099l-3.853-3.852a1.144 1.144 0 0 0-1.615 1.617l5.803 5.802a1.143 1.143 0 0 0 1.616 0l5.802-5.802a1.144 1.144 0 0 0 0-1.617M15.84 8.422 10.039 2.62a1.145 1.145 0 0 0-1.616 0L2.62 8.422a1.144 1.144 0 0 0 1.617 1.617l3.85-3.851v20.098a1.143 1.143 0 0 0 2.286 0V6.187l3.853 3.852a1.14 1.14 0 0 0 1.616-.001 1.14 1.14 0 0 0-.001-1.616"/></svg></span>}</span><div className="hs-col-resize-handle" onMouseDown={(e) => onTaResizeStart('activities', e)} onClick={(e) => e.stopPropagation()} /></th>
                      <th className="hs-th-resizable"><span className="hs-th-content">Last Activity</span><div className="hs-col-resize-handle" onMouseDown={(e) => onTaResizeStart('last_activity', e)} onClick={(e) => e.stopPropagation()} /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {targetAccountsLoading ? (
                      <tr><td colSpan={9} className="hs-loading-cell"><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}><DotLottieReact src="/loading.lottie" loop autoplay style={{ width: 80, height: 80 }} /><span style={{ fontSize: '0.82rem', color: '#7c98b6', marginTop: 8 }}>Loading target accounts...</span></div></td></tr>
                    ) : taPagedData.length === 0 ? (
                      <tr><td colSpan={9} className="hs-empty-cell">
                        <div style={{ padding: '40px', textAlign: 'center' }}>
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '12px' }}><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>
                          <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#33475b', marginBottom: '6px' }}>No target accounts found</div>
                          <div style={{ fontSize: '0.82rem', color: '#7c98b6', maxWidth: '360px', margin: '0 auto' }}>Mark clients as target accounts to track engagement and prioritize outreach.</div>
                        </div>
                      </td></tr>
                    ) : taPagedData.map(acct => (
                      <tr key={acct.id} className={`hs-row${targetAccountsSelectedRows.has(Number(acct.id)) ? ' selected' : ''}`}>
                        <td className="hs-td-check"><input type="checkbox" checked={targetAccountsSelectedRows.has(Number(acct.id))} onChange={() => setTargetAccountsSelectedRows(prev => { const n = new Set(prev); n.has(Number(acct.id)) ? n.delete(Number(acct.id)) : n.add(Number(acct.id)); return n })} /></td>
                        <td className="hs-td-name" style={{ position: 'sticky', left: 40, zIndex: 2, background: targetAccountsSelectedRows.has(Number(acct.id)) ? '#eff6ff' : '#fff' }}>
                          <div className="hs-name-cell">
                            <span className="hs-name-link" onClick={() => setActiveView('companies')}>{acct.name || acct.company_name}</span>
                            <button className="hs-preview-btn" onClick={(e) => { e.stopPropagation() }}>Preview</button>
                          </div>
                          <div style={{ fontSize: '0.72rem', color: '#7c98b6', marginTop: '2px' }}>{[acct.city, acct.state].filter(Boolean).join(', ')}</div>
                        </td>
                        <td><span className="hs-status-badge" style={{ background: acct.target_tier === 1 ? '#fef3c7' : acct.target_tier === 2 ? '#e0e7ff' : '#f1f5f9', color: acct.target_tier === 1 ? '#92400e' : acct.target_tier === 2 ? '#3730a3' : '#475569' }}>{acct.target_tier === 1 ? 'Tier 1' : acct.target_tier === 2 ? 'Tier 2' : 'Tier 3'}</span></td>
                        <td><span className="hs-status-badge" style={{ background: acct.target_status === 'active' ? '#e8f5e9' : acct.target_status === 'won' ? '#e3f2fd' : acct.target_status === 'lost' ? '#fbe9e7' : '#f5f8fa', color: acct.target_status === 'active' ? '#2e7d32' : acct.target_status === 'won' ? '#1565c0' : acct.target_status === 'lost' ? '#c62828' : '#7c98b6' }}>{acct.target_status || 'active'}</span></td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ width: '60px', height: '6px', background: '#e2e8f0', borderRadius: '3px', overflow: 'hidden' }}>
                              <div style={{ width: `${acct.engagement_score}%`, height: '100%', background: acct.engagement_score > 60 ? '#16a34a' : acct.engagement_score > 30 ? '#f59e0b' : '#dc2626', borderRadius: '3px' }} />
                            </div>
                            <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#33475b' }}>{acct.engagement_score}</span>
                          </div>
                        </td>
                        <td style={{ fontSize: '0.8rem', color: '#516f90' }}>{acct.deal_count}</td>
                        <td style={{ fontSize: '0.8rem', color: '#516f90', fontWeight: 600 }}>${Number(acct.total_deal_value || 0).toLocaleString()}</td>
                        <td style={{ fontSize: '0.8rem', color: '#516f90' }}>{acct.activity_count}</td>
                        <td style={{ fontSize: '0.75rem', color: '#516f90' }}>{acct.last_activity ? formatDatePref(acct.last_activity) : '--'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Row 6: Pagination */}
              <div className="hs-pagination">
                <nav className="hs-paginator" aria-label="Pagination">
                  <button className="hs-paginator-btn" disabled={taCurrentPage <= 1} onClick={() => setTargetAccountsPage(p => p - 1)} aria-label="Previous page">
                    <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95"/></svg>
                    <span>Prev</span>
                  </button>
                  {(() => {
                    if (taTotalPages <= 7) {
                      const pages: number[] = []; for (let i = 1; i <= taTotalPages; i++) pages.push(i)
                      return pages.map(p => <button key={p} className={`hs-paginator-num${p === taCurrentPage ? ' active' : ''}`} onClick={() => setTargetAccountsPage(p)}>{p}</button>)
                    }
                    const items: React.ReactNode[] = []
                    const addPage = (p: number) => items.push(<button key={p} className={`hs-paginator-num${p === taCurrentPage ? ' active' : ''}`} onClick={() => setTargetAccountsPage(p)}>{p}</button>)
                    const addEllipsis = (key: string) => items.push(<span key={key} className="hs-paginator-ellipsis">...</span>)
                    const shown = new Set<number>()
                    for (let i = 1; i <= Math.min(4, taTotalPages); i++) shown.add(i)
                    shown.add(taTotalPages)
                    for (let i = Math.max(1, taCurrentPage - 1); i <= Math.min(taTotalPages, taCurrentPage + 1); i++) shown.add(i)
                    const sorted = [...shown].sort((a, b) => a - b)
                    for (let i = 0; i < sorted.length; i++) {
                      if (i > 0 && sorted[i] - sorted[i - 1] > 1) addEllipsis(`e${i}`)
                      addPage(sorted[i])
                    }
                    return items
                  })()}
                  <button className="hs-paginator-btn" disabled={taCurrentPage >= taTotalPages} onClick={() => setTargetAccountsPage(p => p + 1)} aria-label="Next page">
                    <span>Next</span>
                    <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M11 27.71c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62l9.48-9.48-9.48-9.47a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0L22.1 15.48c.45.45.45 1.17 0 1.62L11.81 27.39c-.22.22-.52.33-.81.33Z"/></svg>
                  </button>
                </nav>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', marginLeft: 'auto' }}><select value={tablePageSize} onChange={e => { setTablePageSize(Number(e.target.value) as any); setTargetAccountsPage(1) }} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #e2e8f0', fontSize: '0.82rem' }}><option value={100}>100</option><option value={200}>200</option><option value={500}>500</option></select><span>per page</span></div>
              </div>

              {/* Row 7: Settings Panel */}
              {tableSettingsOpen && (
                <div className="hs-settings-panel">
                  <div className="hs-settings-header">
                    <span className="hs-settings-title">Table settings</span>
                    <button className="hs-settings-close" onClick={() => setTableSettingsOpen(false)}>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                    </button>
                  </div>
                  <div className="hs-settings-body">
                    <div className="hs-settings-group">
                      <label className="hs-settings-label">Pagination<span className="hs-info-icon" title="Number of rows shown per page"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span></label>
                      {([100, 200, 500] as const).map(size => (
                        <label className="hs-settings-radio" key={size}>
                          <input type="radio" name="taPageSize" checked={tablePageSize === size} onChange={() => { setTablePageSize(size); setTargetAccountsPage(1) }} />
                          <span className="hs-radio-indicator" /><span>{size} per page</span>
                        </label>
                      ))}
                    </div>
                    <hr className="hs-settings-divider" />
                    <div className="hs-settings-group">
                      <label className="hs-settings-label">Row height<span className="hs-info-icon" title="Adjust the vertical spacing of table rows"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span></label>
                      {([{ value: 'small' as const, label: 'Compact' }, { value: 'medium' as const, label: 'Default' }, { value: 'large' as const, label: 'Comfortable' }]).map(opt => (
                        <label className="hs-settings-radio" key={opt.value}>
                          <input type="radio" name="taRowHeight" checked={tableRowHeight === opt.value} onChange={() => setTableRowHeight(opt.value)} />
                          <span className="hs-radio-indicator" /><span>{opt.label}</span>
                        </label>
                      ))}
                    </div>
                    <hr className="hs-settings-divider" />
                    <div className="hs-settings-group">
                      <div className="hs-settings-toggle-row">
                        <label className="hs-settings-label">Zebra striping<span className="hs-info-icon" title="Alternate row background colors"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span></label>
                        <button className={`hs-toggle${tableZebraStriping ? ' on' : ''}`} onClick={() => setTableZebraStriping(!tableZebraStriping)} role="switch" aria-checked={tableZebraStriping}>
                          <span className="hs-toggle-track"><span className="hs-toggle-on">ON</span><span className="hs-toggle-off">OFF</span><span className="hs-toggle-handle" /></span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* Mark Target Account Modal */}
            {showMarkTargetModal && (
              <div className="modal-overlay" onClick={() => setShowMarkTargetModal(false)}>
                <div className="modal-card" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
                  <button type="button" className="modal-close" onClick={() => setShowMarkTargetModal(false)}>&times;</button>
                  <div className="modal-body">
                    <h3 className="modal-title">Add Target Account</h3>
                    <p style={{ fontSize: '0.82rem', color: '#64748b', marginBottom: 16 }}>Enter the Contact ID of the client to mark as a target account.</p>
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 500, color: '#33475b', marginBottom: 4 }}>Contact ID</label>
                      <input type="number" value={markTargetClientId} onChange={(e) => setMarkTargetClientId(e.target.value)} placeholder="e.g. 1234" style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem' }} />
                    </div>
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 500, color: '#33475b', marginBottom: 4 }}>Tier</label>
                      <select value={markTargetTier} onChange={(e) => setMarkTargetTier(parseInt(e.target.value))} style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem' }}>
                        <option value={1}>Tier 1 - Strategic</option>
                        <option value={2}>Tier 2 - Growth</option>
                        <option value={3}>Tier 3 - Explore</option>
                      </select>
                    </div>
                    <button className="hs-add-contacts-btn" onClick={() => {
                      if (!markTargetClientId) return
                      markTargetAccount(parseInt(markTargetClientId), { tier: markTargetTier, status: 'active' })
                        .then(() => { setShowMarkTargetModal(false); setTargetAccountsLoading(true); fetchTargetAccounts().then(r => { setTargetAccountsData(r.data); setTargetAccountsLoading(false) }) })
                        .catch(e => alert('Error: ' + e.message))
                    }}>Add Target</button>
                  </div>
                </div>
              </div>
            )}
          </div>
          )
        })()}

        {/* ========== Sales Documents View ========== */}
        {activeView === 'sales-documents' && (() => {
          const sdSearchLower = salesDocsSearch.toLowerCase()
          const filteredDocs = salesDocsData.filter(doc => {
            if (salesDocsTypeFilter && doc.doc_type !== salesDocsTypeFilter) return false
            if (!sdSearchLower) return true
            return (doc.title || '').toLowerCase().includes(sdSearchLower) ||
              (doc.description || '').toLowerCase().includes(sdSearchLower) ||
              (doc.created_by || '').toLowerCase().includes(sdSearchLower)
          })
          const sortedDocs = [...filteredDocs].sort((a: any, b: any) => {
            const f = salesDocsSort.field
            const av = f === 'views_count' ? (a[f] || 0) : String(a[f] || '').toLowerCase()
            const bv = f === 'views_count' ? (b[f] || 0) : String(b[f] || '').toLowerCase()
            if (av < bv) return salesDocsSort.order === 'ASC' ? -1 : 1
            if (av > bv) return salesDocsSort.order === 'ASC' ? 1 : -1
            return 0
          })
          const sdPageSize = tablePageSize
          const sdTotalPages = Math.max(1, Math.ceil(sortedDocs.length / sdPageSize))
          const sdPagedDocs = sortedDocs.slice((salesDocsPage - 1) * sdPageSize, salesDocsPage * sdPageSize)
          const toggleSdSort = (field: string) => setSalesDocsSort(prev => prev.field === field ? { field, order: prev.order === 'ASC' ? 'DESC' : 'ASC' } : { field, order: 'ASC' })
          const onSdResizeStart = (col: string, e: React.MouseEvent) => {
            e.preventDefault(); e.stopPropagation()
            const startX = e.clientX; const startW = salesDocsColWidths[col] || 150
            const onMove = (ev: MouseEvent) => setSalesDocsColWidths(prev => ({ ...prev, [col]: Math.max(60, startW + ev.clientX - startX) }))
            const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp) }
            document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp)
          }
          const toggleAllSdRows = () => {
            if (salesDocsSelectedRows.size === sdPagedDocs.length) setSalesDocsSelectedRows(new Set())
            else setSalesDocsSelectedRows(new Set(sdPagedDocs.map(d => d.id)))
          }
          const toggleSdRow = (id: number) => {
            setSalesDocsSelectedRows(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next })
          }
          const sortArrowSvg = <svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M29.38 21.96a1.147 1.147 0 0 0-1.617 0l-3.85 3.852V5.714a1.143 1.143 0 0 0-2.286 0v20.099l-3.853-3.852a1.144 1.144 0 0 0-1.615 1.617l5.803 5.802a1.143 1.143 0 0 0 1.616 0l5.802-5.802a1.144 1.144 0 0 0 0-1.617M15.84 8.422 10.039 2.62a1.145 1.145 0 0 0-1.616 0L2.62 8.422a1.144 1.144 0 0 0 1.617 1.617l3.85-3.851v20.098a1.143 1.143 0 0 0 2.286 0V6.187l3.853 3.852a1.14 1.14 0 0 0 1.616-.001 1.14 1.14 0 0 0-.001-1.616"/></svg>
          return (
          <div className="hs-contacts">
            {/* ROW 1: TOPBAR */}
            <div className="hs-topbar">
              <div className="hs-topbar-left">
                {/* 1A: Object Switcher */}
                <div className="hs-obj-switcher-wrap" ref={objSwitcherRef}>
                  <button className={`hs-obj-switcher${objSwitcherOpen ? ' open' : ''}`} onClick={() => { setObjSwitcherOpen(!objSwitcherOpen); setObjSwitcherSearch('') }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M16 13H8"/><path d="M16 17H8"/></svg>
                    <span>Documents</span>
                    <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                  </button>
                  {objSwitcherOpen && (
                    <div className="hs-obj-panel">
                      <div className="hs-obj-search-wrap">
                        <svg className="hs-obj-search-icon" width="13" height="13" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                        <input className="hs-obj-search" placeholder="Search" value={objSwitcherSearch} onChange={(e) => setObjSwitcherSearch(e.target.value)} autoFocus />
                      </div>
                      <ul className="hs-obj-list" role="listbox">
                        {[
                          { label: 'Prospecting', view: 'prospecting' as ViewMode },
                          { label: 'Deals', view: 'deals' as ViewMode },
                          { label: 'Forecasts', view: 'forecasts' as ViewMode },
                          { label: 'Target Accounts', view: 'target-accounts' as ViewMode },
                          { label: 'Activity Feed', view: 'activity-feed' as ViewMode },
                          { label: 'Documents', view: 'sales-documents' as ViewMode },
                          { label: 'Meetings', view: 'meetings-scheduler' as ViewMode },
                          { label: 'Coaching Playlists', view: 'coaching-playlists' as ViewMode },
                          { label: 'Payments', view: 'payments' as ViewMode },
                          { label: 'Quotes', view: 'quotes' as ViewMode },
                          { label: 'Products', view: 'products' as ViewMode },
                        ]
                          .filter(item => !objSwitcherSearch || item.label.toLowerCase().includes(objSwitcherSearch.toLowerCase()))
                          .map(item => (
                            <li key={item.view} className={`hs-obj-item${item.view === activeView ? ' active' : ''}`} role="option" aria-selected={item.view === activeView} onClick={() => { setActiveView(item.view); setObjSwitcherOpen(false); setObjSwitcherSearch('') }}>
                              {item.label}
                            </li>
                          ))
                        }
                      </ul>
                    </div>
                  )}
                </div>

                {/* 1B: Separator */}
                <div className="hs-topbar-sep" />

                {/* 1C: View tabs */}
                <div className="hs-view-tabs">
                  {[
                    { key: 'all', label: 'All documents' },
                    { key: 'proposal', label: 'Proposals' },
                    { key: 'contract', label: 'Contracts' },
                    { key: 'pricing', label: 'Pricing' },
                  ].map(tab => {
                    const isActive = salesDocsViewTab === tab.key
                    return (
                      <div key={tab.key} className={`hs-view-tab${isActive ? ' active' : ''}`} onClick={() => { setSalesDocsViewTab(tab.key); setSalesDocsTypeFilter(tab.key === 'all' ? '' : tab.key); setSalesDocsPage(1) }}>
                        <span className="hs-tab-label">{tab.label}</span>
                        {isActive && filteredDocs.length > 0 && <span className="hs-tab-count">{filteredDocs.length.toLocaleString()}</span>}
                        {isActive && (
                          <span className="hs-tab-kebab" onClick={(e) => { e.stopPropagation(); setTabEditOpen(!tabEditOpen) }}>
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>
                          </span>
                        )}
                        {isActive && tabEditOpen && (
                          <div className="hs-tab-edit-dropdown">
                            <button className="hs-tab-edit-item" disabled>Rename</button>
                            <button className="hs-tab-edit-item">Clone</button>
                            <button className="hs-tab-edit-item" disabled>Manage Sharing</button>
                          </div>
                        )}
                        {tab.key !== 'all' && <span className="hs-tab-x" onClick={(e) => { e.stopPropagation(); setSalesDocsViewTab('all'); setSalesDocsTypeFilter(''); setSalesDocsPage(1) }}>&times;</span>}
                      </div>
                    )
                  })}

                  {/* 1D: Quick-add view button */}
                  <div className="hs-topbar-sep" />
                  <div className="hs-view-add-wrap" ref={viewAddMenuRef} style={{ position: 'relative', display: 'inline-flex', alignSelf: 'center' }}>
                    <button className={`hs-quick-add-btn${viewAddMenuOpen ? ' active' : ''}`} title="Create or add view" type="button" aria-label="Create or add view" onClick={() => { setViewAddMenuOpen(!viewAddMenuOpen); setViewAddMenuMode('menu') }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                    </button>
                    {viewAddMenuOpen && viewAddMenuMode === 'menu' && (
                      <div className="hs-view-add-dropdown">
                        <button className="hs-view-add-item" onClick={() => setViewAddMenuMode('type')}>Create new view</button>
                        <button className="hs-view-add-item" onClick={() => { setViewAddMenuOpen(false); setViewAddMenuMode('menu') }}>Add view</button>
                      </div>
                    )}
                    {viewAddMenuOpen && viewAddMenuMode === 'type' && (
                      <div className="hs-view-add-dropdown hs-view-type-picker">
                        <button className="hs-view-type-btn" onClick={() => { setViewAddMenuOpen(false); setViewAddMenuMode('menu') }}>
                          <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                          <span>Table</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="hs-topbar-right">
                <div className="hs-add-btn-wrap" ref={addBtnRef}>
                  <button className="hs-add-contacts-btn" onClick={() => { setSalesDocForm({ title: '', doc_type: 'proposal', description: '' }); setShowCreateSalesDoc(true) }}>
                    Upload document
                    <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                  </button>
                </div>
              </div>
            </div>

            {/* ROW 2: TOOLBAR */}
            <div className="hs-toolbar">
              <div className="hs-toolbar-left">
                {/* Search */}
                <div className="hs-search-wrap">
                  <svg className="hs-search-icon" width="14" height="14" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M28.14 29.29c-.29 0-.58-.11-.81-.33l-7.58-7.58a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l7.58 7.58a1.14 1.14 0 0 1-.81 1.95"/><path d="M13.29 24.71c-6.3 0-11.43-5.13-11.43-11.43S6.98 1.86 13.29 1.86s11.43 5.13 11.43 11.43-5.13 11.43-11.43 11.43Zm0-20.57c-5.04 0-9.14 4.1-9.14 9.14s4.1 9.14 9.14 9.14 9.14-4.1 9.14-9.14-4.1-9.14-9.14-9.14"/></svg>
                  <input className="hs-search-input" placeholder="Search documents..." value={salesDocsSearch} onChange={(e) => { setSalesDocsSearch(e.target.value); setSalesDocsPage(1) }} />
                  {salesDocsSearch && <button className="hs-search-clear" onClick={() => { setSalesDocsSearch(''); setSalesDocsPage(1) }}>&times;</button>}
                </div>

                <div className="hs-tb-sep" />

                {/* View type + settings */}
                <div className="hs-viewtype-group">
                  <div className="hs-viewtype-wrap">
                    <button className="hs-tb-btn hs-viewtype-btn">
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                      <span>Table</span>
                    </button>
                  </div>
                  <button className={`hs-tb-btn hs-settings-btn${tableSettingsOpen ? ' active' : ''}`} onClick={() => setTableSettingsOpen(!tableSettingsOpen)} title={tableSettingsOpen ? 'Close settings' : 'Table settings'}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                  </button>
                </div>

                <div className="hs-tb-sep" />

                {/* Edit columns */}
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Edit columns">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="18" rx="1"/><rect x="14" y="3" width="7" height="18" rx="1"/></svg>
                  <span>Edit columns</span>
                </button>

                <div className="hs-tb-sep" />

                {/* Filters */}
                <button className={`hs-tb-btn hs-tb-btn-bordered${salesDocsQfOpen ? ' hs-tb-btn-pressed' : ''}`} title="Filters" onClick={() => setSalesDocsQfOpen(!salesDocsQfOpen)}>
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M30.71 16.86H13.57a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28M6.71 16.86H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M10.14 20.29c-2.52 0-4.57-2.05-4.57-4.57s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29"/></svg>
                  <span>Filters</span>
                </button>

                <div className="hs-tb-sep" />

                {/* Export */}
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Export">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  <span>Export</span>
                </button>
              </div>

              <div className="hs-toolbar-right">
                <div className="hs-tb-sep" />
                {/* Clone */}
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Clone view">
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M25.86 29.29H12.15c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-1.89 1.54-3.43 3.43-3.43h13.71c1.89 0 3.43 1.54 3.43 3.43v13.71c0 1.89-1.54 3.43-3.43 3.43M12.14 11c-.63 0-1.14.51-1.14 1.14v13.71c0 .63.51 1.14 1.14 1.14h13.71c.63 0 1.14-.51 1.14-1.14V12.14c0-.63-.51-1.14-1.14-1.14z"/><path d="M5.29 22.43c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43H19c1.89 0 3.43 1.54 3.43 3.43a1.14 1.14 0 1 1-2.28 0c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14V19c0 .63.51 1.14 1.14 1.14a1.14 1.14 0 1 1 0 2.28Z"/></svg>
                </button>
                {/* Save */}
                <button className="hs-tb-btn hs-tb-btn-bordered disabled" disabled title="Save view">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                </button>
              </div>
            </div>

            {/* ROW 3: Quick Filters */}
            {salesDocsQfOpen && <div className="hs-quick-filters">
              {/* Document owner */}
              <div className="hs-owner-dropdown" ref={ownerDropdownRef}>
                <button className={`hs-qf-btn hs-qf-trigger${ownerDropdownOpen ? ' open' : ''}${ownerSelected.size > 0 ? ' has-value' : ''}`} onClick={() => setOwnerDropdownOpen(!ownerDropdownOpen)}>
                  <span>Document owner</span>
                  {ownerSelected.size > 0 && <span className="hs-qf-count">{ownerSelected.size}</span>}
                  <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                </button>
                {ownerDropdownOpen && (
                  <div className="hs-owner-panel">
                    <div className="hs-owner-search-wrap">
                      <svg className="hs-owner-search-icon" width="13" height="13" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                      <input className="hs-owner-search" placeholder="Search" value={ownerSearch} onChange={(e) => setOwnerSearch(e.target.value)} autoFocus />
                    </div>
                    <div className="hs-owner-list">
                      {(!ownerSearch || 'me'.includes(ownerSearch.toLowerCase()) || 'max ritchie'.includes(ownerSearch.toLowerCase())) && (
                        <label className="hs-owner-item hs-owner-me">
                          <input type="checkbox" checked={ownerSelected.has(8)} onChange={() => { setOwnerSelected(prev => { const next = new Set(prev); next.has(8) ? next.delete(8) : next.add(8); return next }) }} />
                          <span className="hs-owner-checkbox" />
                          <span className="hs-owner-label">Me</span>
                        </label>
                      )}
                      {crmUsers.filter(u => !ownerSearch || u.name.toLowerCase().includes(ownerSearch.toLowerCase())).map(user => (
                        <label className="hs-owner-item" key={user.id}>
                          <input type="checkbox" checked={ownerSelected.has(user.id)} onChange={() => { setOwnerSelected(prev => { const next = new Set(prev); next.has(user.id) ? next.delete(user.id) : next.add(user.id); return next }) }} />
                          <span className="hs-owner-checkbox" />
                          <span className="hs-owner-avatar">{user.initials || user.name.charAt(0)}</span>
                          <span className="hs-owner-label">{user.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Type filter toggle */}
              <select className="hs-qf-btn" value={salesDocsTypeFilter} onChange={(e) => { setSalesDocsTypeFilter(e.target.value); setSalesDocsPage(1) }} style={{ border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.78rem', padding: '4px 8px' }}>
                <option value="">All Types</option>
                <option value="proposal">Proposal</option>
                <option value="one-pager">One-Pager</option>
                <option value="case-study">Case Study</option>
                <option value="pricing">Pricing Sheet</option>
                <option value="contract">Contract</option>
                <option value="presentation">Presentation</option>
              </select>

              {/* Sort */}
              <button className="hs-qf-btn" onClick={() => setSalesDocsSort(prev => ({ ...prev, order: prev.order === 'ASC' ? 'DESC' : 'ASC' }))}>
                <svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M29.38 21.96a1.147 1.147 0 0 0-1.617 0l-3.85 3.852V5.714a1.143 1.143 0 0 0-2.286 0v20.099l-3.853-3.852a1.144 1.144 0 0 0-1.615 1.617l5.803 5.802a1.143 1.143 0 0 0 1.616 0l5.802-5.802a1.144 1.144 0 0 0 0-1.617M15.84 8.422 10.039 2.62a1.145 1.145 0 0 0-1.616 0L2.62 8.422a1.144 1.144 0 0 0 1.617 1.617l3.85-3.851v20.098a1.143 1.143 0 0 0 2.286 0V6.187l3.853 3.852a1.14 1.14 0 0 0 1.616-.001 1.14 1.14 0 0 0-.001-1.616"/></svg>
                <span>{salesDocsSort.order === 'ASC' ? 'Ascending' : 'Descending'}</span>
              </button>

              <div className="hs-qf-sep" />

              {/* +More */}
              <button className="hs-qf-btn hs-qf-more">
                <svg width="10" height="10" viewBox="0 0 32 32" fill="currentColor"><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V3a1.14 1.14 0 1 1 2.28 0v25.14c0 .63-.51 1.14-1.14 1.14Z"/><path d="M28.14 16.71H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28"/></svg>
                <span>More</span>
              </button>
            </div>}

            {/* ROW 4: Selection Bar */}
            {salesDocsSelectedRows.size > 0 && (
              <div className="hs-selection-bar">
                <span className="hs-sel-count">{salesDocsSelectedRows.size} selected</span>
                <div className="hs-sel-sep" />
                <button className="hs-sel-btn">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  Edit
                </button>
                <button className="hs-sel-btn" style={{ color: '#dc2626' }} onClick={() => {
                  if (confirm(`Delete ${salesDocsSelectedRows.size} document(s)?`)) {
                    Promise.all([...salesDocsSelectedRows].map(id => deleteSalesDocument(id))).then(() => {
                      setSalesDocsData(prev => prev.filter(d => !salesDocsSelectedRows.has(d.id)))
                      setSalesDocsSelectedRows(new Set())
                    }).catch(() => {})
                  }
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                  Delete
                </button>
                <button className="hs-sel-close" onClick={() => setSalesDocsSelectedRows(new Set())} title="Deselect all">&times;</button>
              </div>
            )}

            {/* Main content area */}
            <div className={`hs-content${tableSettingsOpen ? ' with-settings' : ''}`}>
              <div className="hs-table-area">
                {/* ROW 5: Data Table */}
                <div className="hs-table-scroll">
                  <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                      <col style={{ width: 40 }} />
                      <col style={{ width: salesDocsColWidths.title }} />
                      <col style={{ width: salesDocsColWidths.type }} />
                      <col style={{ width: salesDocsColWidths.views }} />
                      <col style={{ width: salesDocsColWidths.last_viewed }} />
                      <col style={{ width: salesDocsColWidths.created_by }} />
                      <col style={{ width: salesDocsColWidths.share }} />
                      <col style={{ width: salesDocsColWidths.created }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th className="hs-th-check">
                          <input type="checkbox" checked={sdPagedDocs.length > 0 && salesDocsSelectedRows.size === sdPagedDocs.length} onChange={toggleAllSdRows} />
                        </th>
                        {/* Document Name — frozen */}
                        <th className="hs-th-resizable hs-th-sortable hs-th-frozen" style={{ position: 'sticky', left: 40, top: 0, zIndex: 15, background: '#f8fafc', boxShadow: '2px 0 4px rgba(0,0,0,0.06)' }} onClick={() => toggleSdSort('title')}>
                          <span className="hs-th-content">
                            Document Name
                            {salesDocsSort.field === 'title' && <span className="hs-sort-arrow">{salesDocsSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                            {salesDocsSort.field !== 'title' && <span className="hs-th-hover-sort">{sortArrowSvg}</span>}
                          </span>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onSdResizeStart('title', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                        <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleSdSort('doc_type')}>
                          <span className="hs-th-content">
                            Type
                            {salesDocsSort.field === 'doc_type' && <span className="hs-sort-arrow">{salesDocsSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                            {salesDocsSort.field !== 'doc_type' && <span className="hs-th-hover-sort">{sortArrowSvg}</span>}
                          </span>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onSdResizeStart('type', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                        <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleSdSort('views_count')}>
                          <span className="hs-th-content">
                            Views
                            {salesDocsSort.field === 'views_count' && <span className="hs-sort-arrow">{salesDocsSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                            {salesDocsSort.field !== 'views_count' && <span className="hs-th-hover-sort">{sortArrowSvg}</span>}
                          </span>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onSdResizeStart('views', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                        <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleSdSort('last_viewed_at')}>
                          <span className="hs-th-content">
                            Last Viewed
                            {salesDocsSort.field === 'last_viewed_at' && <span className="hs-sort-arrow">{salesDocsSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                            {salesDocsSort.field !== 'last_viewed_at' && <span className="hs-th-hover-sort">{sortArrowSvg}</span>}
                          </span>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onSdResizeStart('last_viewed', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                        <th className="hs-th-resizable">
                          <span className="hs-th-content">Created By</span>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onSdResizeStart('created_by', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                        <th className="hs-th-resizable">
                          <span className="hs-th-content">Share Link</span>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onSdResizeStart('share', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                        <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleSdSort('created_at')}>
                          <span className="hs-th-content">
                            Created
                            {salesDocsSort.field === 'created_at' && <span className="hs-sort-arrow">{salesDocsSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                            {salesDocsSort.field !== 'created_at' && <span className="hs-th-hover-sort">{sortArrowSvg}</span>}
                          </span>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onSdResizeStart('created', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {salesDocsLoading ? (
                        <tr><td colSpan={8} className="hs-loading-cell"><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}><DotLottieReact src="/loading.lottie" loop autoplay style={{ width: 80, height: 80 }} /><span style={{ fontSize: '0.82rem', color: '#7c98b6', marginTop: 8 }}>Loading documents...</span></div></td></tr>
                      ) : sdPagedDocs.length === 0 ? (
                        <tr><td colSpan={8} className="hs-empty-cell">
                          <div style={{ padding: '40px', textAlign: 'center' }}>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" style={{ margin: '0 auto 16px' }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>
                            <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#33475b', margin: '0 0 8px' }}>No documents found</h3>
                            <p style={{ fontSize: '0.82rem', color: '#7c98b6', margin: '0 0 20px', maxWidth: '400px', marginLeft: 'auto', marginRight: 'auto' }}>Upload proposals, one-pagers, and case studies with trackable share links.</p>
                            <button className="hs-add-contacts-btn" onClick={() => { setSalesDocForm({ title: '', doc_type: 'proposal', description: '' }); setShowCreateSalesDoc(true) }}>Upload document</button>
                          </div>
                        </td></tr>
                      ) : sdPagedDocs.map(doc => (
                        <tr key={doc.id} className={`hs-row${salesDocsSelectedRows.has(doc.id) ? ' selected' : ''}`}>
                          <td className="hs-td-check">
                            <input type="checkbox" checked={salesDocsSelectedRows.has(doc.id)} onChange={() => toggleSdRow(doc.id)} />
                          </td>
                          <td className="hs-td-name" style={{ position: 'sticky', left: 40, zIndex: 4, boxShadow: '2px 0 4px rgba(0,0,0,0.06)', background: salesDocsSelectedRows.has(doc.id) ? '#eef2ff' : 'inherit' }}>
                            <div className="hs-name-cell">
                              <div className="hs-avatar" style={{ background: doc.doc_type === 'proposal' ? '#2563eb' : doc.doc_type === 'contract' ? '#92400e' : '#16a34a' }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>
                              </div>
                              <div>
                                <span className="hs-name-link" style={{ fontWeight: 600, color: '#1976d2', fontSize: '0.82rem' }}>{doc.title}</span>
                                {doc.description && <div style={{ fontSize: '0.72rem', color: '#7c98b6', marginTop: '2px' }}>{doc.description.substring(0, 60)}</div>}
                              </div>
                              <button className="hs-preview-btn" onClick={(e) => { e.stopPropagation() }}>Preview</button>
                            </div>
                          </td>
                          <td>
                            <span className="hs-status-badge" style={{
                              background: doc.doc_type === 'proposal' ? '#eff6ff' : doc.doc_type === 'contract' ? '#fef3c7' : '#f0fdf4',
                              color: doc.doc_type === 'proposal' ? '#2563eb' : doc.doc_type === 'contract' ? '#92400e' : '#16a34a',
                            }}>{doc.doc_type}</span>
                          </td>
                          <td style={{ fontSize: '0.82rem', fontWeight: 600, color: '#33475b' }}>{doc.views_count || 0}</td>
                          <td style={{ fontSize: '0.75rem', color: '#516f90' }}>{doc.last_viewed_at ? formatDatePref(doc.last_viewed_at) : 'Never'}</td>
                          <td style={{ fontSize: '0.8rem', color: '#516f90' }}>{doc.created_by}</td>
                          <td>
                            <button style={{ padding: '2px 8px', border: '1px solid #cbd6e2', borderRadius: '4px', background: '#f8fafc', fontSize: '0.68rem', cursor: 'pointer', color: '#2563eb' }}
                              onClick={() => { navigator.clipboard.writeText(`https://ops.botpros.ai/doc/${doc.share_slug}`) }}>
                              Copy Link
                            </button>
                          </td>
                          <td style={{ fontSize: '0.75rem', color: '#516f90' }}>{doc.created_at ? formatDatePref(doc.created_at) : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* ROW 6: Pagination */}
                <div className="hs-pagination">
                  <nav className="hs-paginator" aria-label="Pagination">
                    <button className="hs-paginator-btn" disabled={salesDocsPage <= 1} onClick={() => setSalesDocsPage(p => p - 1)} aria-label="Previous page">
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95"/></svg>
                      <span>Prev</span>
                    </button>
                    {(() => {
                      if (sdTotalPages <= 7) {
                        const pages: number[] = []
                        for (let i = 1; i <= sdTotalPages; i++) pages.push(i)
                        return pages.map(p => (
                          <button key={p} className={`hs-paginator-num${p === salesDocsPage ? ' active' : ''}`} onClick={() => setSalesDocsPage(p)}>{p}</button>
                        ))
                      }
                      const items: React.ReactNode[] = []
                      const addPage = (p: number) => items.push(
                        <button key={p} className={`hs-paginator-num${p === salesDocsPage ? ' active' : ''}`} onClick={() => setSalesDocsPage(p)}>{p}</button>
                      )
                      const addEllipsis = (key: string) => items.push(
                        <span key={key} className="hs-paginator-ellipsis">...</span>
                      )
                      const shown = new Set<number>()
                      for (let i = 1; i <= Math.min(4, sdTotalPages); i++) shown.add(i)
                      shown.add(sdTotalPages)
                      for (let i = Math.max(1, salesDocsPage - 1); i <= Math.min(sdTotalPages, salesDocsPage + 1); i++) shown.add(i)
                      const sorted = [...shown].sort((a, b) => a - b)
                      for (let i = 0; i < sorted.length; i++) {
                        if (i > 0 && sorted[i] - sorted[i - 1] > 1) addEllipsis(`e${i}`)
                        addPage(sorted[i])
                      }
                      return items
                    })()}
                    <button className="hs-paginator-btn" disabled={salesDocsPage >= sdTotalPages} onClick={() => setSalesDocsPage(p => p + 1)} aria-label="Next page">
                      <span>Next</span>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M11 27.71c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62l9.48-9.48-9.48-9.47a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0L22.1 15.48c.45.45.45 1.17 0 1.62L11.81 27.39c-.22.22-.52.33-.81.33Z"/></svg>
                    </button>
                  </nav>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', marginLeft: 'auto' }}><select value={tablePageSize} onChange={e => { setTablePageSize(Number(e.target.value) as any); setSalesDocsPage(1) }} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #e2e8f0', fontSize: '0.82rem' }}><option value={100}>100</option><option value={200}>200</option><option value={500}>500</option></select><span>per page</span></div>
                </div>
              </div>

              {/* Settings panel */}
              {tableSettingsOpen && (
                <div className="hs-settings-panel">
                  <div className="hs-settings-header">
                    <span className="hs-settings-title">Table settings</span>
                    <button className="hs-settings-close" onClick={() => setTableSettingsOpen(false)}>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                    </button>
                  </div>
                  <div className="hs-settings-body">
                    <div className="hs-settings-group">
                      <label className="hs-settings-label">Pagination <span className="hs-info-icon" title="Number of rows shown per page"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span></label>
                      {([100, 200, 500] as const).map(size => (
                        <label className="hs-settings-radio" key={size}>
                          <input type="radio" name="sdPageSize" checked={tablePageSize === size} onChange={() => { setTablePageSize(size); setSalesDocsPage(1) }} />
                          <span className="hs-radio-indicator" />
                          <span>{size} per page</span>
                        </label>
                      ))}
                    </div>
                    <hr className="hs-settings-divider" />
                    <div className="hs-settings-group">
                      <label className="hs-settings-label">Row height <span className="hs-info-icon" title="Adjust the vertical spacing of table rows"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span></label>
                      {([{ value: 'small' as const, label: 'Compact' }, { value: 'medium' as const, label: 'Default' }, { value: 'large' as const, label: 'Comfortable' }]).map(opt => (
                        <label className="hs-settings-radio" key={opt.value}>
                          <input type="radio" name="sdRowHeight" checked={tableRowHeight === opt.value} onChange={() => setTableRowHeight(opt.value)} />
                          <span className="hs-radio-indicator" />
                          <span>{opt.label}</span>
                        </label>
                      ))}
                    </div>
                    <hr className="hs-settings-divider" />
                    <div className="hs-settings-group">
                      <div className="hs-settings-toggle-row">
                        <label className="hs-settings-label">Zebra striping <span className="hs-info-icon" title="Alternate row background colors for easier reading"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span></label>
                        <button className={`hs-toggle${tableZebraStriping ? ' on' : ''}`} onClick={() => setTableZebraStriping(!tableZebraStriping)} role="switch" aria-checked={tableZebraStriping}>
                          <span className="hs-toggle-track"><span className="hs-toggle-on">ON</span><span className="hs-toggle-off">OFF</span><span className="hs-toggle-handle" /></span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Create Sales Document Modal */}
            {showCreateSalesDoc && (
              <div className="modal-overlay" onClick={() => setShowCreateSalesDoc(false)}>
                <div className="modal-card" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
                  <button type="button" className="modal-close" onClick={() => setShowCreateSalesDoc(false)}>&times;</button>
                  <div className="modal-body">
                    <h3 className="modal-title">Create Sales Document</h3>
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 500, color: '#33475b', marginBottom: 4 }}>Title *</label>
                      <input type="text" value={salesDocForm.title} onChange={(e) => setSalesDocForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Q1 Proposal for ABC Corp" style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem' }} />
                    </div>
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 500, color: '#33475b', marginBottom: 4 }}>Type</label>
                      <select value={salesDocForm.doc_type} onChange={(e) => setSalesDocForm(p => ({ ...p, doc_type: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem' }}>
                        <option value="proposal">Proposal</option>
                        <option value="one-pager">One-Pager</option>
                        <option value="case-study">Case Study</option>
                        <option value="pricing">Pricing Sheet</option>
                        <option value="contract">Contract</option>
                        <option value="presentation">Presentation</option>
                      </select>
                    </div>
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 500, color: '#33475b', marginBottom: 4 }}>Description</label>
                      <textarea value={salesDocForm.description} onChange={(e) => setSalesDocForm(p => ({ ...p, description: e.target.value }))} rows={3} placeholder="Brief description..." style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem', resize: 'vertical' }} />
                    </div>
                    <button className="hs-add-contacts-btn" disabled={salesDocSaving || !salesDocForm.title} onClick={() => {
                      setSalesDocSaving(true)
                      createSalesDocument(salesDocForm)
                        .then(() => { setShowCreateSalesDoc(false); setSalesDocsLoading(true); fetchSalesDocuments().then(r => { setSalesDocsData(r.data); setSalesDocsLoading(false) }) })
                        .catch(e => alert('Error: ' + e.message))
                        .finally(() => setSalesDocSaving(false))
                    }}>{salesDocSaving ? 'Creating...' : 'Create Document'}</button>
                  </div>
                </div>
              </div>
            )}
          </div>
          )
        })()}


        {/* ========== Activity Feed View ========== */}
        {activeView === 'activity-feed' && (() => {
          const activityTypeIcon: Record<string, { color: string; bg: string; icon: React.ReactNode; label: string }> = {
            'EMAIL': { color: '#2563eb', bg: '#eff6ff', label: 'Email', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg> },
            'EMAIL_COMPRESSED': { color: '#2563eb', bg: '#eff6ff', label: 'Email', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg> },
            'CALL': { color: '#059669', bg: '#ecfdf5', label: 'Call', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.58 2.81.7A2 2 0 0 1 22 16.92z"/></svg> },
            'MEETING': { color: '#7c3aed', bg: '#f5f3ff', label: 'Meeting', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg> },
            'NOTE': { color: '#ea580c', bg: '#fff7ed', label: 'Note', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg> },
            'TASK': { color: '#0891b2', bg: '#ecfeff', label: 'Task', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg> },
            'TASKS_TASK': { color: '#0891b2', bg: '#ecfeff', label: 'Task', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg> },
            'TODO': { color: '#f59e0b', bg: '#fffbeb', label: 'To-Do', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 12l2 2 4-4"/></svg> },
          }
          const feedSearchLower = activityFeedSearch.toLowerCase()
          const filteredFeed = feedSearchLower ? activityFeedData.filter((act: any) => {
            const sub = (act.subject || '').toLowerCase()
            const client = (act.client_name || '').toLowerCase()
            const company = (act.client_company || '').toLowerCase()
            const owner = (act.owner || '').toLowerCase()
            const body = (act.body || '').toLowerCase()
            return sub.includes(feedSearchLower) || client.includes(feedSearchLower) || company.includes(feedSearchLower) || owner.includes(feedSearchLower) || body.includes(feedSearchLower)
          }) : activityFeedData
          const afPageSize = tablePageSize
          const afTotalPages = Math.max(1, Math.ceil(activityFeedTotal / afPageSize))
          const toggleAfSort = (field: string) => setActivityFeedSort(prev => prev.field === field ? { field, order: prev.order === 'ASC' ? 'DESC' : 'ASC' } : { field, order: 'ASC' })
          const onAfResizeStart = (col: string, e: React.MouseEvent) => {
            e.preventDefault(); e.stopPropagation()
            const startX = e.clientX; const startW = activityFeedColWidths[col] || 150
            const onMove = (ev: MouseEvent) => setActivityFeedColWidths(prev => ({ ...prev, [col]: Math.max(60, startW + ev.clientX - startX) }))
            const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp) }
            document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp)
          }
          const toggleAllAfRows = () => {
            if (activityFeedSelectedRows.size === filteredFeed.length) setActivityFeedSelectedRows(new Set())
            else setActivityFeedSelectedRows(new Set(filteredFeed.map((a: any) => a.id)))
          }
          const toggleAfRow = (id: number) => {
            setActivityFeedSelectedRows(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next })
          }
          const sortArrowSvg = <svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M29.38 21.96a1.147 1.147 0 0 0-1.617 0l-3.85 3.852V5.714a1.143 1.143 0 0 0-2.286 0v20.099l-3.853-3.852a1.144 1.144 0 0 0-1.615 1.617l5.803 5.802a1.143 1.143 0 0 0 1.616 0l5.802-5.802a1.144 1.144 0 0 0 0-1.617M15.84 8.422 10.039 2.62a1.145 1.145 0 0 0-1.616 0L2.62 8.422a1.144 1.144 0 0 0 1.617 1.617l3.85-3.851v20.098a1.143 1.143 0 0 0 2.286 0V6.187l3.853 3.852a1.14 1.14 0 0 0 1.616-.001 1.14 1.14 0 0 0-.001-1.616"/></svg>
          return (
          <div className="hs-contacts">
            {/* ROW 1: TOPBAR */}
            <div className="hs-topbar">
              <div className="hs-topbar-left">
                {/* 1A: Object Switcher */}
                <div className="hs-obj-switcher-wrap" ref={objSwitcherRef}>
                  <button className={`hs-obj-switcher${objSwitcherOpen ? ' open' : ''}`} onClick={() => { setObjSwitcherOpen(!objSwitcherOpen); setObjSwitcherSearch('') }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="22,12 18,12 15,21 9,3 6,12 2,12"/></svg>
                    <span>Activity Feed</span>
                    <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                  </button>
                  {objSwitcherOpen && (
                    <div className="hs-obj-panel">
                      <div className="hs-obj-search-wrap">
                        <svg className="hs-obj-search-icon" width="13" height="13" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                        <input className="hs-obj-search" placeholder="Search" value={objSwitcherSearch} onChange={(e) => setObjSwitcherSearch(e.target.value)} autoFocus />
                      </div>
                      <ul className="hs-obj-list" role="listbox">
                        {[
                          { label: 'Prospecting', view: 'prospecting' as ViewMode },
                          { label: 'Deals', view: 'deals' as ViewMode },
                          { label: 'Forecasts', view: 'forecasts' as ViewMode },
                          { label: 'Target Accounts', view: 'target-accounts' as ViewMode },
                          { label: 'Activity Feed', view: 'activity-feed' as ViewMode },
                          { label: 'Documents', view: 'sales-documents' as ViewMode },
                          { label: 'Meetings', view: 'meetings-scheduler' as ViewMode },
                          { label: 'Coaching Playlists', view: 'coaching-playlists' as ViewMode },
                          { label: 'Payments', view: 'payments' as ViewMode },
                          { label: 'Quotes', view: 'quotes' as ViewMode },
                          { label: 'Products', view: 'products' as ViewMode },
                        ]
                          .filter(item => !objSwitcherSearch || item.label.toLowerCase().includes(objSwitcherSearch.toLowerCase()))
                          .map(item => (
                            <li key={item.view} className={`hs-obj-item${item.view === activeView ? ' active' : ''}`} role="option" aria-selected={item.view === activeView} onClick={() => { setActiveView(item.view); setObjSwitcherOpen(false); setObjSwitcherSearch('') }}>
                              {item.label}
                            </li>
                          ))
                        }
                      </ul>
                    </div>
                  )}
                </div>

                {/* 1B: Separator */}
                <div className="hs-topbar-sep" />

                {/* 1C: View tabs */}
                <div className="hs-view-tabs">
                  {[
                    { key: 'all', label: 'All activities' },
                    { key: 'EMAIL', label: 'Emails' },
                    { key: 'CALL', label: 'Calls' },
                    { key: 'MEETING', label: 'Meetings' },
                    { key: 'TASK', label: 'Tasks' },
                  ].map(tab => {
                    const isActive = activityFeedViewTab === tab.key
                    return (
                      <div key={tab.key} className={`hs-view-tab${isActive ? ' active' : ''}`} onClick={() => { setActivityFeedViewTab(tab.key); setActivityFeedTypeFilter(tab.key === 'all' ? '' : tab.key); setActivityFeedPage(1) }}>
                        <span className="hs-tab-label">{tab.label}</span>
                        {isActive && activityFeedTotal > 0 && <span className="hs-tab-count">{activityFeedTotal.toLocaleString()}</span>}
                        {isActive && (
                          <span className="hs-tab-kebab" onClick={(e) => { e.stopPropagation(); setTabEditOpen(!tabEditOpen) }}>
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>
                          </span>
                        )}
                        {isActive && tabEditOpen && (
                          <div className="hs-tab-edit-dropdown">
                            <button className="hs-tab-edit-item" disabled>Rename</button>
                            <button className="hs-tab-edit-item">Clone</button>
                            <button className="hs-tab-edit-item" disabled>Manage Sharing</button>
                          </div>
                        )}
                        {tab.key !== 'all' && <span className="hs-tab-x" onClick={(e) => { e.stopPropagation(); setActivityFeedViewTab('all'); setActivityFeedTypeFilter(''); setActivityFeedPage(1) }}>&times;</span>}
                      </div>
                    )
                  })}

                  {/* 1D: Quick-add view button */}
                  <div className="hs-topbar-sep" />
                  <div className="hs-view-add-wrap" ref={viewAddMenuRef} style={{ position: 'relative', display: 'inline-flex', alignSelf: 'center' }}>
                    <button className={`hs-quick-add-btn${viewAddMenuOpen ? ' active' : ''}`} title="Create or add view" type="button" aria-label="Create or add view" onClick={() => { setViewAddMenuOpen(!viewAddMenuOpen); setViewAddMenuMode('menu') }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                    </button>
                    {viewAddMenuOpen && viewAddMenuMode === 'menu' && (
                      <div className="hs-view-add-dropdown">
                        <button className="hs-view-add-item" onClick={() => setViewAddMenuMode('type')}>Create new view</button>
                        <button className="hs-view-add-item" onClick={() => { setViewAddMenuOpen(false); setViewAddMenuMode('menu') }}>Add view</button>
                      </div>
                    )}
                    {viewAddMenuOpen && viewAddMenuMode === 'type' && (
                      <div className="hs-view-add-dropdown hs-view-type-picker">
                        <button className="hs-view-type-btn" onClick={() => { setViewAddMenuOpen(false); setViewAddMenuMode('menu') }}>
                          <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                          <span>Table</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="hs-topbar-right">
              </div>
            </div>

            {/* ROW 2: TOOLBAR */}
            <div className="hs-toolbar">
              <div className="hs-toolbar-left">
                {/* Search */}
                <div className="hs-search-wrap">
                  <svg className="hs-search-icon" width="14" height="14" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M28.14 29.29c-.29 0-.58-.11-.81-.33l-7.58-7.58a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l7.58 7.58a1.14 1.14 0 0 1-.81 1.95"/><path d="M13.29 24.71c-6.3 0-11.43-5.13-11.43-11.43S6.98 1.86 13.29 1.86s11.43 5.13 11.43 11.43-5.13 11.43-11.43 11.43Zm0-20.57c-5.04 0-9.14 4.1-9.14 9.14s4.1 9.14 9.14 9.14 9.14-4.1 9.14-9.14-4.1-9.14-9.14-9.14"/></svg>
                  <input className="hs-search-input" placeholder="Search activities..." value={activityFeedSearch} onChange={(e) => setActivityFeedSearch(e.target.value)} />
                  {activityFeedSearch && <button className="hs-search-clear" onClick={() => setActivityFeedSearch('')}>&times;</button>}
                </div>

                <div className="hs-tb-sep" />

                {/* View type + settings */}
                <div className="hs-viewtype-group">
                  <div className="hs-viewtype-wrap">
                    <button className="hs-tb-btn hs-viewtype-btn">
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                      <span>Table</span>
                    </button>
                  </div>
                  <button className={`hs-tb-btn hs-settings-btn${tableSettingsOpen ? ' active' : ''}`} onClick={() => setTableSettingsOpen(!tableSettingsOpen)} title={tableSettingsOpen ? 'Close settings' : 'Table settings'}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                  </button>
                </div>

                <div className="hs-tb-sep" />

                {/* Edit columns */}
                <button className="hs-tb-btn hs-tb-btn-bordered disabled" disabled title="Edit columns">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="18" rx="1"/><rect x="14" y="3" width="7" height="18" rx="1"/></svg>
                  <span>Edit columns</span>
                </button>

                <div className="hs-tb-sep" />

                {/* Filters */}
                <button className={`hs-tb-btn hs-tb-btn-bordered${activityFeedQfOpen ? ' hs-tb-btn-pressed' : ''}`} title="Filters" onClick={() => setActivityFeedQfOpen(!activityFeedQfOpen)}>
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M30.71 16.86H13.57a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28M6.71 16.86H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M10.14 20.29c-2.52 0-4.57-2.05-4.57-4.57s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29"/></svg>
                  <span>Filters</span>
                </button>

                <div className="hs-tb-sep" />

                {/* Export */}
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Export">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  <span>Export</span>
                </button>
              </div>

              <div className="hs-toolbar-right">
                <div className="hs-tb-sep" />
                {/* Clone */}
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Clone view">
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M25.86 29.29H12.15c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-1.89 1.54-3.43 3.43-3.43h13.71c1.89 0 3.43 1.54 3.43 3.43v13.71c0 1.89-1.54 3.43-3.43 3.43M12.14 11c-.63 0-1.14.51-1.14 1.14v13.71c0 .63.51 1.14 1.14 1.14h13.71c.63 0 1.14-.51 1.14-1.14V12.14c0-.63-.51-1.14-1.14-1.14z"/><path d="M5.29 22.43c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43H19c1.89 0 3.43 1.54 3.43 3.43a1.14 1.14 0 1 1-2.28 0c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14V19c0 .63.51 1.14 1.14 1.14a1.14 1.14 0 1 1 0 2.28Z"/></svg>
                </button>
                {/* Save */}
                <button className="hs-tb-btn hs-tb-btn-bordered disabled" disabled title="Save view">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                </button>
              </div>
            </div>

            {/* ROW 3: Quick Filters */}
            {activityFeedQfOpen && <div className="hs-quick-filters">
              {/* Activity owner */}
              <div className="hs-owner-dropdown" ref={ownerDropdownRef}>
                <button className={`hs-qf-btn hs-qf-trigger${ownerDropdownOpen ? ' open' : ''}${ownerSelected.size > 0 ? ' has-value' : ''}`} onClick={() => setOwnerDropdownOpen(!ownerDropdownOpen)}>
                  <span>Activity owner</span>
                  {ownerSelected.size > 0 && <span className="hs-qf-count">{ownerSelected.size}</span>}
                  <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                </button>
                {ownerDropdownOpen && (
                  <div className="hs-owner-panel">
                    <div className="hs-owner-search-wrap">
                      <svg className="hs-owner-search-icon" width="13" height="13" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                      <input className="hs-owner-search" placeholder="Search" value={ownerSearch} onChange={(e) => setOwnerSearch(e.target.value)} autoFocus />
                    </div>
                    <div className="hs-owner-list">
                      {(!ownerSearch || 'me'.includes(ownerSearch.toLowerCase()) || 'max ritchie'.includes(ownerSearch.toLowerCase())) && (
                        <label className="hs-owner-item hs-owner-me">
                          <input type="checkbox" checked={ownerSelected.has(8)} onChange={() => { setOwnerSelected(prev => { const next = new Set(prev); next.has(8) ? next.delete(8) : next.add(8); return next }) }} />
                          <span className="hs-owner-checkbox" />
                          <span className="hs-owner-label">Me</span>
                        </label>
                      )}
                      {crmUsers.filter(u => !ownerSearch || u.name.toLowerCase().includes(ownerSearch.toLowerCase())).map(user => (
                        <label className="hs-owner-item" key={user.id}>
                          <input type="checkbox" checked={ownerSelected.has(user.id)} onChange={() => { setOwnerSelected(prev => { const next = new Set(prev); next.has(user.id) ? next.delete(user.id) : next.add(user.id); return next }) }} />
                          <span className="hs-owner-checkbox" />
                          <span className="hs-owner-avatar">{user.initials || user.name.charAt(0)}</span>
                          <span className="hs-owner-label">{user.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Type filter */}
              <select className="hs-qf-btn" value={activityFeedTypeFilter} onChange={(e) => { setActivityFeedTypeFilter(e.target.value); setActivityFeedPage(1) }} style={{ border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.78rem', padding: '4px 8px' }}>
                <option value="">All Types</option>
                <option value="EMAIL">Email</option>
                <option value="CALL">Calls</option>
                <option value="MEETING">Meetings</option>
                <option value="NOTE">Notes</option>
                <option value="TASK">Tasks</option>
                <option value="TODO">To-Dos</option>
              </select>

              {/* Sort */}
              <button className="hs-qf-btn" onClick={() => setActivityFeedSort(prev => ({ ...prev, order: prev.order === 'ASC' ? 'DESC' : 'ASC' }))}>
                <svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M29.38 21.96a1.147 1.147 0 0 0-1.617 0l-3.85 3.852V5.714a1.143 1.143 0 0 0-2.286 0v20.099l-3.853-3.852a1.144 1.144 0 0 0-1.615 1.617l5.803 5.802a1.143 1.143 0 0 0 1.616 0l5.802-5.802a1.144 1.144 0 0 0 0-1.617M15.84 8.422 10.039 2.62a1.145 1.145 0 0 0-1.616 0L2.62 8.422a1.144 1.144 0 0 0 1.617 1.617l3.85-3.851v20.098a1.143 1.143 0 0 0 2.286 0V6.187l3.853 3.852a1.14 1.14 0 0 0 1.616-.001 1.14 1.14 0 0 0-.001-1.616"/></svg>
                <span>{activityFeedSort.order === 'ASC' ? 'Ascending' : 'Descending'}</span>
              </button>

              <div className="hs-qf-sep" />

              {/* +More */}
              <button className="hs-qf-btn hs-qf-more">
                <svg width="10" height="10" viewBox="0 0 32 32" fill="currentColor"><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V3a1.14 1.14 0 1 1 2.28 0v25.14c0 .63-.51 1.14-1.14 1.14Z"/><path d="M28.14 16.71H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28"/></svg>
                <span>More</span>
              </button>
            </div>}

            {/* ROW 4: Selection Bar */}
            {activityFeedSelectedRows.size > 0 && (
              <div className="hs-selection-bar">
                <span className="hs-sel-count">{activityFeedSelectedRows.size} selected</span>
                <button className="hs-sel-close" onClick={() => setActivityFeedSelectedRows(new Set())} title="Deselect all">&times;</button>
              </div>
            )}

            {/* Main content area */}
            <div className={`hs-content${tableSettingsOpen ? ' with-settings' : ''}`}>
              <div className="hs-table-area">
                {/* ROW 5: Data Table */}
                <div className="hs-table-scroll">
                  <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                      <col style={{ width: 40 }} />
                      <col style={{ width: activityFeedColWidths.subject }} />
                      <col style={{ width: activityFeedColWidths.type }} />
                      <col style={{ width: activityFeedColWidths.contact }} />
                      <col style={{ width: activityFeedColWidths.date }} />
                      <col style={{ width: activityFeedColWidths.duration }} />
                      <col style={{ width: activityFeedColWidths.owner }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th className="hs-th-check">
                          <input type="checkbox" checked={filteredFeed.length > 0 && activityFeedSelectedRows.size === filteredFeed.length} onChange={toggleAllAfRows} />
                        </th>
                        {/* Subject — frozen */}
                        <th className="hs-th-resizable hs-th-sortable hs-th-frozen" style={{ position: 'sticky', left: 40, top: 0, zIndex: 15, background: '#f8fafc', boxShadow: '2px 0 4px rgba(0,0,0,0.06)' }} onClick={() => toggleAfSort('subject')}>
                          <span className="hs-th-content">
                            Subject
                            {activityFeedSort.field === 'subject' && <span className="hs-sort-arrow">{activityFeedSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                            {activityFeedSort.field !== 'subject' && <span className="hs-th-hover-sort">{sortArrowSvg}</span>}
                          </span>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onAfResizeStart('subject', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                        <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleAfSort('type')}>
                          <span className="hs-th-content">
                            Type
                            {activityFeedSort.field === 'type' && <span className="hs-sort-arrow">{activityFeedSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                            {activityFeedSort.field !== 'type' && <span className="hs-th-hover-sort">{sortArrowSvg}</span>}
                          </span>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onAfResizeStart('type', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                        <th className="hs-th-resizable">
                          <span className="hs-th-content">Contact / Company</span>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onAfResizeStart('contact', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                        <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleAfSort('created')}>
                          <span className="hs-th-content">
                            Date
                            {activityFeedSort.field === 'created' && <span className="hs-sort-arrow">{activityFeedSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                            {activityFeedSort.field !== 'created' && <span className="hs-th-hover-sort">{sortArrowSvg}</span>}
                          </span>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onAfResizeStart('date', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                        <th className="hs-th-resizable">
                          <span className="hs-th-content">Duration</span>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onAfResizeStart('duration', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                        <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleAfSort('owner')}>
                          <span className="hs-th-content">
                            Owner
                            {activityFeedSort.field === 'owner' && <span className="hs-sort-arrow">{activityFeedSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                            {activityFeedSort.field !== 'owner' && <span className="hs-th-hover-sort">{sortArrowSvg}</span>}
                          </span>
                          <div className="hs-col-resize-handle" onMouseDown={(e) => onAfResizeStart('owner', e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {activityFeedLoading ? (
                        <tr><td colSpan={8} className="hs-loading-cell"><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}><DotLottieReact src="/loading.lottie" loop autoplay style={{ width: 80, height: 80 }} /><span style={{ fontSize: '0.82rem', color: '#7c98b6', marginTop: 8 }}>Loading activities...</span></div></td></tr>
                      ) : filteredFeed.length === 0 ? (
                        <tr><td colSpan={8} className="hs-empty-cell">
                          <div style={{ padding: '40px', textAlign: 'center' }}>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" style={{ margin: '0 auto 16px' }}><polyline points="22,12 18,12 15,21 9,3 6,12 2,12"/></svg>
                            <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#33475b', margin: '0 0 8px' }}>No activities found</h3>
                            <p style={{ fontSize: '0.82rem', color: '#7c98b6' }}>Activities will appear here as your team logs calls, emails, meetings, and notes.</p>
                          </div>
                        </td></tr>
                      ) : filteredFeed.map((act: any, idx: number) => {
                        const ti = activityTypeIcon[act.type] || { color: '#64748b', bg: '#f1f5f9', label: act.type, icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg> }
                        const rowId = act.id || idx
                        return (
                          <tr key={rowId} className={`hs-row${activityFeedSelectedRows.has(rowId) ? ' selected' : ''}`}>
                            <td className="hs-td-check">
                              <input type="checkbox" checked={activityFeedSelectedRows.has(rowId)} onChange={() => toggleAfRow(rowId)} />
                            </td>
                            <td className="hs-td-name" style={{ position: 'sticky', left: 40, zIndex: 4, boxShadow: '2px 0 4px rgba(0,0,0,0.06)', background: activityFeedSelectedRows.has(rowId) ? '#eef2ff' : 'inherit' }}>
                              <div className="hs-name-cell">
                                <div className="hs-avatar" style={{ background: ti.bg, color: ti.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  {ti.icon}
                                </div>
                                <span className="hs-name-link" style={{ fontWeight: 600, fontSize: '0.82rem', color: '#1e293b' }}>{act.subject || act.type}</span>
                                <button className="hs-preview-btn" onClick={(e) => { e.stopPropagation() }}>Preview</button>
                              </div>
                            </td>
                            <td>
                              <span className="hs-status-badge" style={{ background: ti.bg, color: ti.color, fontSize: '0.65rem' }}>{ti.label}</span>
                            </td>
                            <td style={{ fontSize: '0.78rem', color: '#2563eb' }}>{act.client_name || '--'}{act.client_company ? ` - ${act.client_company}` : ''}</td>
                            <td style={{ fontSize: '0.78rem', color: '#516f90' }}>{act.created ? new Date(act.created).toLocaleString() : '--'}</td>
                            <td style={{ fontSize: '0.78rem', color: '#516f90' }}>{act.duration_seconds ? `${Math.round(act.duration_seconds / 60)}m` : '--'}</td>
                            <td style={{ fontSize: '0.78rem', color: '#516f90' }}>{act.owner || '--'}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>

                {/* ROW 6: Pagination */}
                <div className="hs-pagination">
                  <nav className="hs-paginator" aria-label="Pagination">
                    <button className="hs-paginator-btn" disabled={activityFeedPage <= 1} onClick={() => setActivityFeedPage(p => p - 1)} aria-label="Previous page">
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95"/></svg>
                      <span>Prev</span>
                    </button>
                    {(() => {
                      if (afTotalPages <= 7) {
                        const pages: number[] = []
                        for (let i = 1; i <= afTotalPages; i++) pages.push(i)
                        return pages.map(p => (
                          <button key={p} className={`hs-paginator-num${p === activityFeedPage ? ' active' : ''}`} onClick={() => setActivityFeedPage(p)}>{p}</button>
                        ))
                      }
                      const items: React.ReactNode[] = []
                      const addPage = (p: number) => items.push(
                        <button key={p} className={`hs-paginator-num${p === activityFeedPage ? ' active' : ''}`} onClick={() => setActivityFeedPage(p)}>{p}</button>
                      )
                      const addEllipsis = (key: string) => items.push(
                        <span key={key} className="hs-paginator-ellipsis">...</span>
                      )
                      const shown = new Set<number>()
                      for (let i = 1; i <= Math.min(4, afTotalPages); i++) shown.add(i)
                      shown.add(afTotalPages)
                      for (let i = Math.max(1, activityFeedPage - 1); i <= Math.min(afTotalPages, activityFeedPage + 1); i++) shown.add(i)
                      const sorted = [...shown].sort((a, b) => a - b)
                      for (let i = 0; i < sorted.length; i++) {
                        if (i > 0 && sorted[i] - sorted[i - 1] > 1) addEllipsis(`e${i}`)
                        addPage(sorted[i])
                      }
                      return items
                    })()}
                    <button className="hs-paginator-btn" disabled={activityFeedPage >= afTotalPages} onClick={() => setActivityFeedPage(p => p + 1)} aria-label="Next page">
                      <span>Next</span>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M11 27.71c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62l9.48-9.48-9.48-9.47a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0L22.1 15.48c.45.45.45 1.17 0 1.62L11.81 27.39c-.22.22-.52.33-.81.33Z"/></svg>
                    </button>
                  </nav>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', marginLeft: 'auto' }}><select value={tablePageSize} onChange={e => { setTablePageSize(Number(e.target.value) as any); setActivityFeedPage(1) }} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #e2e8f0', fontSize: '0.82rem' }}><option value={100}>100</option><option value={200}>200</option><option value={500}>500</option></select><span>per page</span></div>
                </div>
              </div>

              {/* Settings panel */}
              {tableSettingsOpen && (
                <div className="hs-settings-panel">
                  <div className="hs-settings-header">
                    <span className="hs-settings-title">Table settings</span>
                    <button className="hs-settings-close" onClick={() => setTableSettingsOpen(false)}>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                    </button>
                  </div>
                  <div className="hs-settings-body">
                    <div className="hs-settings-group">
                      <label className="hs-settings-label">Pagination <span className="hs-info-icon" title="Number of rows shown per page"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span></label>
                      {([100, 200, 500] as const).map(size => (
                        <label className="hs-settings-radio" key={size}>
                          <input type="radio" name="afPageSize" checked={tablePageSize === size} onChange={() => { setTablePageSize(size); setActivityFeedPage(1) }} />
                          <span className="hs-radio-indicator" />
                          <span>{size} per page</span>
                        </label>
                      ))}
                    </div>
                    <hr className="hs-settings-divider" />
                    <div className="hs-settings-group">
                      <label className="hs-settings-label">Row height <span className="hs-info-icon" title="Adjust the vertical spacing of table rows"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span></label>
                      {([{ value: 'small' as const, label: 'Compact' }, { value: 'medium' as const, label: 'Default' }, { value: 'large' as const, label: 'Comfortable' }]).map(opt => (
                        <label className="hs-settings-radio" key={opt.value}>
                          <input type="radio" name="afRowHeight" checked={tableRowHeight === opt.value} onChange={() => setTableRowHeight(opt.value)} />
                          <span className="hs-radio-indicator" />
                          <span>{opt.label}</span>
                        </label>
                      ))}
                    </div>
                    <hr className="hs-settings-divider" />
                    <div className="hs-settings-group">
                      <div className="hs-settings-toggle-row">
                        <label className="hs-settings-label">Zebra striping <span className="hs-info-icon" title="Alternate row background colors for easier reading"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span></label>
                        <button className={`hs-toggle${tableZebraStriping ? ' on' : ''}`} onClick={() => setTableZebraStriping(!tableZebraStriping)} role="switch" aria-checked={tableZebraStriping}>
                          <span className="hs-toggle-track"><span className="hs-toggle-on">ON</span><span className="hs-toggle-off">OFF</span><span className="hs-toggle-handle" /></span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          )
        })()}


        {/* ========== Meetings Scheduler View ========== */}
        {activeView === 'meetings-scheduler' && (() => {
          const mtSearchLower = meetingsSearch.toLowerCase()
          const filteredMeetingTypes = mtSearchLower ? meetingTypesData.filter(mt =>
            (mt.name || '').toLowerCase().includes(mtSearchLower) ||
            (mt.location || '').toLowerCase().includes(mtSearchLower) ||
            (mt.description || '').toLowerCase().includes(mtSearchLower)
          ) : meetingTypesData
          const filteredBookings = mtSearchLower ? meetingBookingsData.filter((bk: any) =>
            (bk.guest_name || '').toLowerCase().includes(mtSearchLower) ||
            (bk.guest_email || '').toLowerCase().includes(mtSearchLower) ||
            (bk.meeting_type_name || '').toLowerCase().includes(mtSearchLower)
          ) : meetingBookingsData
          // Sort
          const sortedMeetingTypes = [...filteredMeetingTypes].sort((a: any, b: any) => {
            const f = mtSort.field; const av = a[f] ?? ''; const bv = b[f] ?? ''
            const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv))
            return mtSort.order === 'ASC' ? cmp : -cmp
          })
          const sortedBookings = [...filteredBookings].sort((a: any, b: any) => {
            const f = bkSort.field; const av = a[f] ?? ''; const bv = b[f] ?? ''
            const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv))
            return bkSort.order === 'ASC' ? cmp : -cmp
          })
          // Paginate
          const mtTotal = sortedMeetingTypes.length
          const mtPageCount = Math.max(1, Math.ceil(mtTotal / tablePageSize))
          const mtPaged = sortedMeetingTypes.slice((mtPage - 1) * tablePageSize, mtPage * tablePageSize)
          const bkTotal = sortedBookings.length
          const bkPageCount = Math.max(1, Math.ceil(bkTotal / tablePageSize))
          const bkPaged = sortedBookings.slice((bkPage - 1) * tablePageSize, bkPage * tablePageSize)
          const toggleMtSort = (field: string) => setMtSort(prev => ({ field, order: prev.field === field && prev.order === 'ASC' ? 'DESC' : 'ASC' }))
          const toggleBkSort = (field: string) => setBkSort(prev => ({ field, order: prev.field === field && prev.order === 'ASC' ? 'DESC' : 'ASC' }))
          const toggleAllMtRows = () => setMtSelectedRows(prev => prev.size === mtPaged.length ? new Set() : new Set(mtPaged.map(m => m.id)))
          const toggleAllBkRows = () => setBkSelectedRows(prev => prev.size === bkPaged.length ? new Set() : new Set(bkPaged.map((b: any) => b.id)))
          const onMtResizeStart = (col: string, e: React.MouseEvent) => { e.preventDefault(); const startX = e.clientX; const startW = (mtColWidths as any)[col]; const onMove = (ev: MouseEvent) => { setMtColWidths(prev => ({ ...prev, [col]: Math.max(60, startW + ev.clientX - startX) })) }; const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp) }; document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp) }
          const onBkResizeStart = (col: string, e: React.MouseEvent) => { e.preventDefault(); const startX = e.clientX; const startW = (bkColWidths as any)[col]; const onMove = (ev: MouseEvent) => { setBkColWidths(prev => ({ ...prev, [col]: Math.max(60, startW + ev.clientX - startX) })) }; const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp) }; document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp) }
          const sortArrowSvg = <svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M29.38 21.96a1.147 1.147 0 0 0-1.617 0l-3.85 3.852V5.714a1.143 1.143 0 0 0-2.286 0v20.099l-3.853-3.852a1.144 1.144 0 0 0-1.615 1.617l5.803 5.802a1.143 1.143 0 0 0 1.616 0l5.802-5.802a1.144 1.144 0 0 0 0-1.617M15.84 8.422 10.039 2.62a1.145 1.145 0 0 0-1.616 0L2.62 8.422a1.144 1.144 0 0 0 1.617 1.617l3.85-3.851v20.098a1.143 1.143 0 0 0 2.286 0V6.187l3.853 3.852a1.14 1.14 0 0 0 1.616-.001 1.14 1.14 0 0 0-.001-1.616"/></svg>
          return (
          <div className="hs-contacts">
            {/* Row 1: Topbar */}
            <div className="hs-topbar">
              <div className="hs-topbar-left">
                <div className="hs-obj-switcher-wrap" ref={objSwitcherRef}>
                  <button className={`hs-obj-switcher${objSwitcherOpen ? ' open' : ''}`} onClick={() => { setObjSwitcherOpen(!objSwitcherOpen); setObjSwitcherSearch('') }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                    <span>Meetings</span>
                    <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                  </button>
                  {objSwitcherOpen && (
                    <div className="hs-obj-panel">
                      <div className="hs-obj-search-wrap">
                        <svg className="hs-obj-search-icon" width="13" height="13" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                        <input className="hs-obj-search" placeholder="Search" value={objSwitcherSearch} onChange={(e) => setObjSwitcherSearch(e.target.value)} autoFocus />
                      </div>
                      <ul className="hs-obj-list" role="listbox">
                        {[
                          { label: 'Prospecting', view: 'prospecting' as ViewMode },
                          { label: 'Deals', view: 'deals' as ViewMode },
                          { label: 'Forecasts', view: 'forecasts' as ViewMode },
                          { label: 'Target Accounts', view: 'target-accounts' as ViewMode },
                          { label: 'Activity Feed', view: 'activity-feed' as ViewMode },
                          { label: 'Documents', view: 'sales-documents' as ViewMode },
                          { label: 'Meetings', view: 'meetings-scheduler' as ViewMode },
                          { label: 'Coaching Playlists', view: 'coaching-playlists' as ViewMode },
                          { label: 'Payments', view: 'payments' as ViewMode },
                          { label: 'Quotes', view: 'quotes' as ViewMode },
                          { label: 'Products', view: 'products' as ViewMode },
                        ].filter(item => !objSwitcherSearch || item.label.toLowerCase().includes(objSwitcherSearch.toLowerCase())).map(item => (
                          <li key={item.view} className={`hs-obj-item${item.view === activeView ? ' active' : ''}`} role="option" aria-selected={item.view === activeView} onClick={() => { setActiveView(item.view); setObjSwitcherOpen(false); setObjSwitcherSearch('') }}>{item.label}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="hs-topbar-sep" />
                <div className="hs-view-tabs">
                  {(['types', 'bookings'] as const).map(t => {
                    const isActive = meetingSchedulerTab === t
                    const count = t === 'types' ? mtTotal : bkTotal
                    return (
                      <div key={t} className={`hs-view-tab${isActive ? ' active' : ''}`} onClick={() => { setMeetingSchedulerTab(t); setMeetingsSearch(''); setMtPage(1); setBkPage(1); setMtSelectedRows(new Set()); setBkSelectedRows(new Set()) }}>
                        <span className="hs-tab-label">{t === 'types' ? 'Meeting Types' : 'Bookings'}</span>
                        {isActive && count > 0 && <span className="hs-tab-count">{count.toLocaleString()}</span>}
                      </div>
                    )
                  })}
                </div>
              </div>
              <div className="hs-topbar-right">
                <button className="hs-add-contacts-btn" onClick={() => { setMeetingTypeForm({ name: '', duration_minutes: 30, description: '', location: 'Office', color: '#2563eb' }); setShowCreateMeetingType(true) }}>Create meeting type</button>
              </div>
            </div>

            {/* Row 2: Toolbar */}
            <div className="hs-toolbar">
              <div className="hs-toolbar-left">
                <div className="hs-search-wrap">
                  <svg className="hs-search-icon" width="14" height="14" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M28.14 29.29c-.29 0-.58-.11-.81-.33l-7.58-7.58a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l7.58 7.58a1.14 1.14 0 0 1-.81 1.95"/><path d="M13.29 24.71c-6.3 0-11.43-5.13-11.43-11.43S6.98 1.86 13.29 1.86s11.43 5.13 11.43 11.43-5.13 11.43-11.43 11.43Zm0-20.57c-5.04 0-9.14 4.1-9.14 9.14s4.1 9.14 9.14 9.14 9.14-4.1 9.14-9.14-4.1-9.14-9.14-9.14"/></svg>
                  <input className="hs-search-input" placeholder={meetingSchedulerTab === 'types' ? 'Search meeting types' : 'Search bookings'} value={meetingsSearch} onChange={(e) => setMeetingsSearch(e.target.value)} />
                  {meetingsSearch && <button className="hs-search-clear" onClick={() => setMeetingsSearch('')}>&times;</button>}
                </div>
                <div className="hs-tb-sep" />
                <div className="hs-viewtype-group">
                  <div className="hs-viewtype-wrap">
                    <button className="hs-tb-btn hs-viewtype-btn" disabled>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                      <span>Table</span>
                    </button>
                  </div>
                  <button className={`hs-tb-btn hs-settings-btn${tableSettingsOpen ? ' active' : ''}`} onClick={() => setTableSettingsOpen(!tableSettingsOpen)} title={tableSettingsOpen ? 'Close settings' : 'Table settings'}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                  </button>
                </div>
                <div className="hs-tb-sep" />
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Filters" onClick={() => setQuickFiltersOpen(!quickFiltersOpen)}>
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M30.71 16.86H13.57a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28M6.71 16.86H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M10.14 20.29c-2.52 0-4.57-2.05-4.57-4.57s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29"/></svg>
                  <span>Filters</span>
                </button>
                <div className="hs-tb-sep" />
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Export">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  <span>Export</span>
                </button>
              </div>
              <div className="hs-toolbar-right">
                <div className="hs-tb-sep" />
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Clone view">
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M25.86 29.29H12.15c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-1.89 1.54-3.43 3.43-3.43h13.71c1.89 0 3.43 1.54 3.43 3.43v13.71c0 1.89-1.54 3.43-3.43 3.43M12.14 11c-.63 0-1.14.51-1.14 1.14v13.71c0 .63.51 1.14 1.14 1.14h13.71c.63 0 1.14-.51 1.14-1.14V12.14c0-.63-.51-1.14-1.14-1.14z"/><path d="M5.29 22.43c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43H19c1.89 0 3.43 1.54 3.43 3.43a1.14 1.14 0 1 1-2.28 0c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14V19c0 .63.51 1.14 1.14 1.14a1.14 1.14 0 1 1 0 2.28Z"/></svg>
                </button>
                <button className="hs-tb-btn hs-tb-btn-bordered disabled" disabled title="Save view">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                </button>
              </div>
            </div>

            {/* Row 3: Quick Filters */}
            {quickFiltersOpen && <div className="hs-quick-filters">
              {meetingSchedulerTab === 'types' && (
                <button className={`hs-qf-btn hs-qf-trigger`} onClick={() => {
                  const fields = ['name', 'duration_minutes', 'location', 'is_active']
                  const idx = fields.indexOf(mtSort.field)
                  const next = fields[(idx + 1) % fields.length]
                  setMtSort({ field: next, order: next === 'name' || next === 'location' ? 'ASC' : 'DESC' })
                  setMtPage(1)
                }}>
                  <span>Sort: {{ name: 'Name', duration_minutes: 'Duration', location: 'Location', is_active: 'Status' }[mtSort.field] || 'Name'}</span>
                  <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                </button>
              )}
              {meetingSchedulerTab === 'bookings' && (
                <button className={`hs-qf-btn hs-qf-trigger`} onClick={() => {
                  const fields = ['booking_date', 'guest_name', 'meeting_type_name', 'status']
                  const idx = fields.indexOf(bkSort.field)
                  const next = fields[(idx + 1) % fields.length]
                  setBkSort({ field: next, order: next === 'guest_name' || next === 'meeting_type_name' ? 'ASC' : 'DESC' })
                  setBkPage(1)
                }}>
                  <span>Sort: {{ booking_date: 'Date', guest_name: 'Guest', meeting_type_name: 'Type', status: 'Status' }[bkSort.field] || 'Date'}</span>
                  <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                </button>
              )}
            </div>}

            {/* Main content */}
            <div className={`hs-content${tableSettingsOpen ? ' with-settings' : ''}`}>
              {meetingSchedulerTab === 'types' && (
                <div className="hs-table-area">
                  {/* Row 4: Selection bar */}
                  {mtSelectedRows.size > 0 && (
                    <div className="hs-selection-bar">
                      <span className="hs-sel-count">{mtSelectedRows.size} selected</span>
                      <div className="hs-sel-sep" />
                      <button className="hs-sel-btn" style={{ color: '#dc2626' }} onClick={() => {
                        if (confirm(`Delete ${mtSelectedRows.size} meeting type(s)?`)) {
                          Promise.all([...mtSelectedRows].map(id => deleteMeetingType(id))).then(() => {
                            setMeetingTypesData(prev => prev.filter(m => !mtSelectedRows.has(m.id)))
                            setMtSelectedRows(new Set())
                          }).catch(() => {})
                        }
                      }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                        Delete
                      </button>
                      <button className="hs-sel-close" onClick={() => setMtSelectedRows(new Set())} title="Deselect all">&times;</button>
                    </div>
                  )}

                  {/* Row 5: Data table */}
                  <div className="hs-table-scroll">
                    {meetingTypesLoading ? (
                      <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                        <tbody><tr><td colSpan={7} className="hs-loading-cell"><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}><DotLottieReact src="/loading.lottie" loop autoplay style={{ width: 80, height: 80 }} /><span style={{ fontSize: '0.82rem', color: '#7c98b6', marginTop: 8 }}>Loading meeting types...</span></div></td></tr></tbody>
                      </table>
                    ) : meetingTypesData.length === 0 ? (
                      <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                        <tbody><tr><td colSpan={7} className="hs-empty-cell">
                          <div style={{ padding: '60px 40px', textAlign: 'center' }}>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" style={{ margin: '0 auto 16px' }}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                            <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#33475b', margin: '0 0 8px' }}>Create your first meeting type</h3>
                            <p style={{ fontSize: '0.82rem', color: '#7c98b6', margin: '0 0 20px', maxWidth: '400px', marginLeft: 'auto', marginRight: 'auto' }}>Set up meeting types with custom durations, availability, and shareable booking links. Like Calendly, built right into your CRM.</p>
                            <button className="hs-add-contacts-btn" onClick={() => { setMeetingTypeForm({ name: '', duration_minutes: 30, description: '', location: 'Office', color: '#2563eb' }); setShowCreateMeetingType(true) }}>Create meeting type</button>
                          </div>
                        </td></tr></tbody>
                      </table>
                    ) : filteredMeetingTypes.length === 0 ? (
                      <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                        <tbody><tr><td colSpan={7} className="hs-empty-cell">
                          <div style={{ padding: '60px 40px', textAlign: 'center' }}>
                            <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#33475b', margin: '0 0 8px' }}>No matching meeting types</h3>
                            <p style={{ fontSize: '0.82rem', color: '#7c98b6' }}>Try adjusting your search term.</p>
                          </div>
                        </td></tr></tbody>
                      </table>
                    ) : (
                      <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                        <colgroup>
                          <col style={{ width: 40 }} />
                          <col style={{ width: mtColWidths.name }} />
                          <col style={{ width: mtColWidths.duration }} />
                          <col style={{ width: mtColWidths.location }} />
                          <col style={{ width: mtColWidths.status }} />
                          <col style={{ width: mtColWidths.bookings }} />
                          <col style={{ width: mtColWidths.actions }} />
                        </colgroup>
                        <thead>
                          <tr>
                            <th className="hs-th-check"><input type="checkbox" checked={mtPaged.length > 0 && mtSelectedRows.size === mtPaged.length} onChange={toggleAllMtRows} /></th>
                            <th className="hs-th-resizable hs-th-sortable hs-th-frozen" style={{ position: 'sticky', left: 40, top: 0, zIndex: 15, background: '#f8fafc', boxShadow: '2px 0 4px rgba(0,0,0,0.06)' }} onClick={() => toggleMtSort('name')}>
                              <span className="hs-th-content">Name{mtSort.field === 'name' && <span className="hs-sort-arrow">{mtSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}{mtSort.field !== 'name' && <span className="hs-th-hover-sort">{sortArrowSvg}</span>}</span>
                              <div className="hs-col-resize-handle" onMouseDown={(e) => onMtResizeStart('name', e)} onClick={(e) => e.stopPropagation()} />
                            </th>
                            <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleMtSort('duration_minutes')}>
                              <span className="hs-th-content">Duration{mtSort.field === 'duration_minutes' && <span className="hs-sort-arrow">{mtSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}{mtSort.field !== 'duration_minutes' && <span className="hs-th-hover-sort">{sortArrowSvg}</span>}</span>
                              <div className="hs-col-resize-handle" onMouseDown={(e) => onMtResizeStart('duration', e)} onClick={(e) => e.stopPropagation()} />
                            </th>
                            <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleMtSort('location')}>
                              <span className="hs-th-content">Location{mtSort.field === 'location' && <span className="hs-sort-arrow">{mtSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}{mtSort.field !== 'location' && <span className="hs-th-hover-sort">{sortArrowSvg}</span>}</span>
                              <div className="hs-col-resize-handle" onMouseDown={(e) => onMtResizeStart('location', e)} onClick={(e) => e.stopPropagation()} />
                            </th>
                            <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleMtSort('is_active')}>
                              <span className="hs-th-content">Status{mtSort.field === 'is_active' && <span className="hs-sort-arrow">{mtSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}{mtSort.field !== 'is_active' && <span className="hs-th-hover-sort">{sortArrowSvg}</span>}</span>
                              <div className="hs-col-resize-handle" onMouseDown={(e) => onMtResizeStart('status', e)} onClick={(e) => e.stopPropagation()} />
                            </th>
                            <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleMtSort('bookings_count')}>
                              <span className="hs-th-content">Bookings{mtSort.field === 'bookings_count' && <span className="hs-sort-arrow">{mtSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}{mtSort.field !== 'bookings_count' && <span className="hs-th-hover-sort">{sortArrowSvg}</span>}</span>
                              <div className="hs-col-resize-handle" onMouseDown={(e) => onMtResizeStart('bookings', e)} onClick={(e) => e.stopPropagation()} />
                            </th>
                            <th className="hs-th-resizable"><span className="hs-th-content">Actions</span><div className="hs-col-resize-handle" onMouseDown={(e) => onMtResizeStart('actions', e)} onClick={(e) => e.stopPropagation()} /></th>
                          </tr>
                        </thead>
                        <tbody>
                          {mtPaged.map(mt => (
                            <tr key={mt.id} className={`hs-row${mtSelectedRows.has(mt.id) ? ' selected' : ''}`}>
                              <td className="hs-td-check"><input type="checkbox" checked={mtSelectedRows.has(mt.id)} onChange={() => setMtSelectedRows(prev => { const n = new Set(prev); n.has(mt.id) ? n.delete(mt.id) : n.add(mt.id); return n })} /></td>
                              <td className="hs-td-name" style={{ position: 'sticky', left: 40, zIndex: 2, background: mtSelectedRows.has(mt.id) ? '#eff6ff' : '#fff' }}>
                                <div className="hs-name-cell">
                                  <div style={{ width: '4px', height: '28px', borderRadius: '2px', background: mt.color || '#2563eb', flexShrink: 0 }} />
                                  <div>
                                    <span className="hs-name-link" style={{ fontWeight: 600, fontSize: '0.82rem', color: '#1e293b' }}>{mt.name}</span>
                                    {mt.description && <div style={{ fontSize: '0.72rem', color: '#64748b', maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{mt.description}</div>}
                                  </div>
                                  <button className="hs-preview-btn" onClick={(e) => { e.stopPropagation() }}>Preview</button>
                                </div>
                              </td>
                              <td style={{ fontSize: '0.82rem', color: '#516f90' }}>{mt.duration_minutes} min</td>
                              <td style={{ fontSize: '0.82rem', color: '#516f90' }}>{mt.location || '--'}</td>
                              <td><span className="hs-status-badge" style={{ background: mt.is_active ? '#e8f5e9' : '#f5f8fa', color: mt.is_active ? '#2e7d32' : '#7c98b6' }}>{mt.is_active ? 'Active' : 'Inactive'}</span></td>
                              <td style={{ fontSize: '0.82rem', color: '#516f90' }}>{mt.bookings_count || 0}</td>
                              <td>
                                <div style={{ display: 'flex', gap: '6px' }}>
                                  <button style={{ padding: '3px 8px', border: '1px solid #cbd6e2', borderRadius: '4px', background: '#f8fafc', fontSize: '0.72rem', cursor: 'pointer', color: '#2563eb' }} onClick={() => { navigator.clipboard.writeText(`https://ops.botpros.ai/meet/${mt.slug}`) }}>Copy Link</button>
                                  <button style={{ padding: '3px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', background: '#fff', color: '#d32f2f', fontSize: '0.72rem', cursor: 'pointer' }} onClick={() => { if (confirm('Delete this meeting type?')) deleteMeetingType(mt.id).then(() => setMeetingTypesData(prev => prev.filter(m => m.id !== mt.id))) }}>Delete</button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* Row 6: Pagination */}
                  <div className="hs-pagination">
                    <nav className="hs-paginator" aria-label="Pagination">
                      <button className="hs-paginator-btn" disabled={mtPage <= 1} onClick={() => setMtPage(p => p - 1)} aria-label="Previous page">
                        <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95"/></svg>
                        <span>Prev</span>
                      </button>
                      {(() => { const pages: number[] = []; const start = Math.max(1, mtPage - 2); const end = Math.min(mtPageCount, start + 4); for (let i = start; i <= end; i++) pages.push(i); return pages.map(p => (<button key={p} className={`hs-paginator-num${p === mtPage ? ' active' : ''}`} onClick={() => setMtPage(p)}>{p}</button>)) })()}
                      <button className="hs-paginator-btn" disabled={mtPage >= mtPageCount} onClick={() => setMtPage(p => p + 1)} aria-label="Next page">
                        <span>Next</span>
                        <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M11 27.71c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62l9.48-9.48-9.48-9.47a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0L22.1 15.48c.45.45.45 1.17 0 1.62L11.81 27.39c-.22.22-.52.33-.81.33Z"/></svg>
                      </button>
                    </nav>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', marginLeft: 'auto' }}><select value={tablePageSize} onChange={e => { setTablePageSize(Number(e.target.value) as any); setMtPage(1) }} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #e2e8f0', fontSize: '0.82rem' }}><option value={100}>100</option><option value={200}>200</option><option value={500}>500</option></select><span>per page</span></div>
                  </div>
                </div>
              )}
              {meetingSchedulerTab === 'bookings' && (
                <div className="hs-table-area">
                  {/* Row 4: Selection bar */}
                  {bkSelectedRows.size > 0 && (
                    <div className="hs-selection-bar">
                      <span className="hs-sel-count">{bkSelectedRows.size} selected</span>
                      <button className="hs-sel-close" onClick={() => setBkSelectedRows(new Set())} title="Deselect all">&times;</button>
                    </div>
                  )}

                  {/* Row 5: Data table */}
                  <div className="hs-table-scroll">
                    {filteredBookings.length === 0 ? (
                      <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                        <tbody><tr><td colSpan={8} className="hs-empty-cell">
                          <div style={{ padding: '60px 40px', textAlign: 'center' }}>
                            <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#33475b', margin: '0 0 8px' }}>{meetingsSearch ? 'No matching bookings' : 'No bookings yet'}</h3>
                            <p style={{ fontSize: '0.82rem', color: '#7c98b6' }}>{meetingsSearch ? 'Try adjusting your search term.' : 'When contacts book meetings using your links, they will appear here.'}</p>
                          </div>
                        </td></tr></tbody>
                      </table>
                    ) : (
                      <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                        <colgroup>
                          <col style={{ width: 40 }} />
                          <col style={{ width: bkColWidths.guest }} />
                          <col style={{ width: bkColWidths.email }} />
                          <col style={{ width: bkColWidths.type }} />
                          <col style={{ width: bkColWidths.date }} />
                          <col style={{ width: bkColWidths.time }} />
                          <col style={{ width: bkColWidths.status }} />
                          <col style={{ width: bkColWidths.notes }} />
                        </colgroup>
                        <thead>
                          <tr>
                            <th className="hs-th-check"><input type="checkbox" checked={bkPaged.length > 0 && bkSelectedRows.size === bkPaged.length} onChange={toggleAllBkRows} /></th>
                            <th className="hs-th-resizable hs-th-sortable hs-th-frozen" style={{ position: 'sticky', left: 40, top: 0, zIndex: 15, background: '#f8fafc', boxShadow: '2px 0 4px rgba(0,0,0,0.06)' }} onClick={() => toggleBkSort('guest_name')}>
                              <span className="hs-th-content">Guest{bkSort.field === 'guest_name' && <span className="hs-sort-arrow">{bkSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}{bkSort.field !== 'guest_name' && <span className="hs-th-hover-sort">{sortArrowSvg}</span>}</span>
                              <div className="hs-col-resize-handle" onMouseDown={(e) => onBkResizeStart('guest', e)} onClick={(e) => e.stopPropagation()} />
                            </th>
                            <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleBkSort('guest_email')}>
                              <span className="hs-th-content">Email{bkSort.field === 'guest_email' && <span className="hs-sort-arrow">{bkSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}{bkSort.field !== 'guest_email' && <span className="hs-th-hover-sort">{sortArrowSvg}</span>}</span>
                              <div className="hs-col-resize-handle" onMouseDown={(e) => onBkResizeStart('email', e)} onClick={(e) => e.stopPropagation()} />
                            </th>
                            <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleBkSort('meeting_type_name')}>
                              <span className="hs-th-content">Meeting Type{bkSort.field === 'meeting_type_name' && <span className="hs-sort-arrow">{bkSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}{bkSort.field !== 'meeting_type_name' && <span className="hs-th-hover-sort">{sortArrowSvg}</span>}</span>
                              <div className="hs-col-resize-handle" onMouseDown={(e) => onBkResizeStart('type', e)} onClick={(e) => e.stopPropagation()} />
                            </th>
                            <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleBkSort('booking_date')}>
                              <span className="hs-th-content">Date{bkSort.field === 'booking_date' && <span className="hs-sort-arrow">{bkSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}{bkSort.field !== 'booking_date' && <span className="hs-th-hover-sort">{sortArrowSvg}</span>}</span>
                              <div className="hs-col-resize-handle" onMouseDown={(e) => onBkResizeStart('date', e)} onClick={(e) => e.stopPropagation()} />
                            </th>
                            <th className="hs-th-resizable"><span className="hs-th-content">Time</span><div className="hs-col-resize-handle" onMouseDown={(e) => onBkResizeStart('time', e)} onClick={(e) => e.stopPropagation()} /></th>
                            <th className="hs-th-resizable hs-th-sortable" onClick={() => toggleBkSort('status')}>
                              <span className="hs-th-content">Status{bkSort.field === 'status' && <span className="hs-sort-arrow">{bkSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}{bkSort.field !== 'status' && <span className="hs-th-hover-sort">{sortArrowSvg}</span>}</span>
                              <div className="hs-col-resize-handle" onMouseDown={(e) => onBkResizeStart('status', e)} onClick={(e) => e.stopPropagation()} />
                            </th>
                            <th className="hs-th-resizable"><span className="hs-th-content">Notes</span><div className="hs-col-resize-handle" onMouseDown={(e) => onBkResizeStart('notes', e)} onClick={(e) => e.stopPropagation()} /></th>
                          </tr>
                        </thead>
                        <tbody>
                          {bkPaged.map((bk: any) => (
                            <tr key={bk.id} className={`hs-row${bkSelectedRows.has(bk.id) ? ' selected' : ''}`}>
                              <td className="hs-td-check"><input type="checkbox" checked={bkSelectedRows.has(bk.id)} onChange={() => setBkSelectedRows(prev => { const n = new Set(prev); n.has(bk.id) ? n.delete(bk.id) : n.add(bk.id); return n })} /></td>
                              <td className="hs-td-name" style={{ position: 'sticky', left: 40, zIndex: 2, background: bkSelectedRows.has(bk.id) ? '#eff6ff' : '#fff' }}><div className="hs-name-cell"><span className="hs-name-link" style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>{bk.guest_name}</span><button className="hs-preview-btn" onClick={(e) => { e.stopPropagation() }}>Preview</button></div></td>
                              <td style={{ fontSize: '0.78rem', color: '#2563eb' }}>{bk.guest_email}</td>
                              <td style={{ fontSize: '0.8rem', color: '#516f90' }}>{bk.meeting_type_name}</td>
                              <td style={{ fontSize: '0.78rem', color: '#516f90' }}>{bk.booking_date ? formatDatePref(bk.booking_date) : ''}</td>
                              <td style={{ fontSize: '0.78rem', color: '#516f90' }}>{bk.start_time} - {bk.end_time}</td>
                              <td><span className="hs-status-badge" style={{ background: bk.status === 'confirmed' ? '#e8f5e9' : bk.status === 'cancelled' ? '#fbe9e7' : '#f5f8fa', color: bk.status === 'confirmed' ? '#2e7d32' : bk.status === 'cancelled' ? '#c62828' : '#7c98b6' }}>{bk.status}</span></td>
                              <td style={{ fontSize: '0.72rem', color: '#94a3b8', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{bk.notes || '--'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* Row 6: Pagination */}
                  <div className="hs-pagination">
                    <nav className="hs-paginator" aria-label="Pagination">
                      <button className="hs-paginator-btn" disabled={bkPage <= 1} onClick={() => setBkPage(p => p - 1)} aria-label="Previous page">
                        <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95"/></svg>
                        <span>Prev</span>
                      </button>
                      {(() => { const pages: number[] = []; const start = Math.max(1, bkPage - 2); const end = Math.min(bkPageCount, start + 4); for (let i = start; i <= end; i++) pages.push(i); return pages.map(p => (<button key={p} className={`hs-paginator-num${p === bkPage ? ' active' : ''}`} onClick={() => setBkPage(p)}>{p}</button>)) })()}
                      <button className="hs-paginator-btn" disabled={bkPage >= bkPageCount} onClick={() => setBkPage(p => p + 1)} aria-label="Next page">
                        <span>Next</span>
                        <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M11 27.71c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62l9.48-9.48-9.48-9.47a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0L22.1 15.48c.45.45.45 1.17 0 1.62L11.81 27.39c-.22.22-.52.33-.81.33Z"/></svg>
                      </button>
                    </nav>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', marginLeft: 'auto' }}><select value={tablePageSize} onChange={e => { setTablePageSize(Number(e.target.value) as any); setBkPage(1) }} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #e2e8f0', fontSize: '0.82rem' }}><option value={100}>100</option><option value={200}>200</option><option value={500}>500</option></select><span>per page</span></div>
                  </div>
                </div>
              )}

              {/* Row 7: Settings panel */}
              {tableSettingsOpen && (
                <div className="hs-settings-panel">
                  <div className="hs-settings-header">
                    <span className="hs-settings-title">Table settings</span>
                    <button className="hs-settings-close" onClick={() => setTableSettingsOpen(false)}>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                    </button>
                  </div>
                  <div className="hs-settings-body">
                    <div className="hs-settings-group">
                      <label className="hs-settings-label">Pagination<span className="hs-info-icon" title="Number of rows shown per page"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span></label>
                      {([100, 200, 500] as const).map(size => (<label className="hs-settings-radio" key={size}><input type="radio" name="mtPageSize" checked={tablePageSize === size} onChange={() => { setTablePageSize(size); setMtPage(1); setBkPage(1) }} /><span className="hs-radio-indicator" /><span>{size} per page</span></label>))}
                    </div>
                    <hr className="hs-settings-divider" />
                    <div className="hs-settings-group">
                      <label className="hs-settings-label">Row height<span className="hs-info-icon" title="Adjust the vertical spacing of table rows"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span></label>
                      {([{ value: 'small' as const, label: 'Compact' }, { value: 'medium' as const, label: 'Default' }, { value: 'large' as const, label: 'Comfortable' }]).map(opt => (<label className="hs-settings-radio" key={opt.value}><input type="radio" name="mtRowHeight" checked={tableRowHeight === opt.value} onChange={() => setTableRowHeight(opt.value)} /><span className="hs-radio-indicator" /><span>{opt.label}</span></label>))}
                    </div>
                    <hr className="hs-settings-divider" />
                    <div className="hs-settings-group">
                      <div className="hs-settings-toggle-row">
                        <label className="hs-settings-label">Zebra striping<span className="hs-info-icon" title="Alternate row background colors for easier reading"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span></label>
                        <button className={`hs-toggle${tableZebraStriping ? ' on' : ''}`} onClick={() => setTableZebraStriping(!tableZebraStriping)} role="switch" aria-checked={tableZebraStriping}>
                          <span className="hs-toggle-track"><span className="hs-toggle-on">ON</span><span className="hs-toggle-off">OFF</span><span className="hs-toggle-handle" /></span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* Create Meeting Type Modal */}
            {showCreateMeetingType && (
              <div className="modal-overlay" onClick={() => setShowCreateMeetingType(false)}>
                <div className="modal-card" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
                  <button type="button" className="modal-close" onClick={() => setShowCreateMeetingType(false)}>&times;</button>
                  <div className="modal-body">
                    <h3 className="modal-title">Create Meeting Type</h3>
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 500, color: '#33475b', marginBottom: 4 }}>Name *</label>
                      <input type="text" value={meetingTypeForm.name} onChange={(e) => setMeetingTypeForm((p: any) => ({ ...p, name: e.target.value }))} placeholder="e.g. 30 Minute Consultation" style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem' }} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 500, color: '#33475b', marginBottom: 4 }}>Duration</label>
                        <select value={meetingTypeForm.duration_minutes} onChange={(e) => setMeetingTypeForm((p: any) => ({ ...p, duration_minutes: parseInt(e.target.value) }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem' }}>
                          <option value={15}>15 minutes</option>
                          <option value={30}>30 minutes</option>
                          <option value={45}>45 minutes</option>
                          <option value={60}>60 minutes</option>
                          <option value={90}>90 minutes</option>
                        </select>
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 500, color: '#33475b', marginBottom: 4 }}>Location</label>
                        <input type="text" value={meetingTypeForm.location} onChange={(e) => setMeetingTypeForm((p: any) => ({ ...p, location: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem' }} />
                      </div>
                    </div>
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 500, color: '#33475b', marginBottom: 4 }}>Description</label>
                      <textarea value={meetingTypeForm.description} onChange={(e) => setMeetingTypeForm((p: any) => ({ ...p, description: e.target.value }))} rows={2} placeholder="Describe this meeting type..." style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem', resize: 'vertical' }} />
                    </div>
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 500, color: '#33475b', marginBottom: 4 }}>Color</label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {['#2563eb', '#059669', '#7c3aed', '#ea580c', '#dc2626', '#0891b2'].map(c => (
                          <div key={c} onClick={() => setMeetingTypeForm((p: any) => ({ ...p, color: c }))} style={{ width: '28px', height: '28px', borderRadius: '50%', background: c, cursor: 'pointer', border: meetingTypeForm.color === c ? '3px solid #1e293b' : '2px solid transparent' }} />
                        ))}
                      </div>
                    </div>
                    <button className="hs-add-contacts-btn" disabled={meetingTypeSaving || !meetingTypeForm.name} onClick={() => {
                      setMeetingTypeSaving(true)
                      createMeetingType(meetingTypeForm)
                        .then(() => { setShowCreateMeetingType(false); setMeetingTypesLoading(true); fetchMeetingTypes().then(r => { setMeetingTypesData(r.data); setMeetingTypesLoading(false) }) })
                        .catch(e => alert('Error: ' + e.message))
                        .finally(() => setMeetingTypeSaving(false))
                    }}>{meetingTypeSaving ? 'Creating...' : 'Create Meeting Type'}</button>
                  </div>
                </div>
              </div>
            )}
          </div>
          )
        })()}

        {/* ========== Coaching Playlists View ========== */}
        {activeView === 'coaching-playlists' && editingPlaylistId === null && (() => {
          const plSearchLower = playlistsSearch.toLowerCase()
          const filteredPlaylists = plSearchLower ? playlistsData.filter(pl => (pl.name && pl.name.toLowerCase().includes(plSearchLower)) || (pl.description && pl.description.toLowerCase().includes(plSearchLower))) : playlistsData
          const sortedPlaylists = [...filteredPlaylists].sort((a: any, b: any) => { const f = plSort.field; const av = a[f] ?? ''; const bv = b[f] ?? ''; const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv)); return plSort.order === 'ASC' ? cmp : -cmp })
          const plTotal = sortedPlaylists.length
          const plPageCount = Math.max(1, Math.ceil(plTotal / tablePageSize))
          const plPaged = sortedPlaylists.slice((plPage - 1) * tablePageSize, plPage * tablePageSize)
          const togglePlSort = (field: string) => setPlSort(prev => ({ field, order: prev.field === field && prev.order === 'ASC' ? 'DESC' : 'ASC' }))
          const toggleAllPlRows = () => setPlSelectedRows(prev => prev.size === plPaged.length ? new Set() : new Set(plPaged.map(p => p.id)))
          const onPlResizeStart = (col: string, e: React.MouseEvent) => { e.preventDefault(); const startX = e.clientX; const startW = (plColWidths as any)[col]; const onMove = (ev: MouseEvent) => { setPlColWidths(prev => ({ ...prev, [col]: Math.max(60, startW + ev.clientX - startX) })) }; const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp) }; document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp) }
          const plSortArrowSvg = <svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M29.38 21.96a1.147 1.147 0 0 0-1.617 0l-3.85 3.852V5.714a1.143 1.143 0 0 0-2.286 0v20.099l-3.853-3.852a1.144 1.144 0 0 0-1.615 1.617l5.803 5.802a1.143 1.143 0 0 0 1.616 0l5.802-5.802a1.144 1.144 0 0 0 0-1.617M15.84 8.422 10.039 2.62a1.145 1.145 0 0 0-1.616 0L2.62 8.422a1.144 1.144 0 0 0 1.617 1.617l3.85-3.851v20.098a1.143 1.143 0 0 0 2.286 0V6.187l3.853 3.852a1.14 1.14 0 0 0 1.616-.001 1.14 1.14 0 0 0-.001-1.616"/></svg>
          return (
          <div className="hs-contacts">
            {/* Row 1: Topbar */}
            <div className="hs-topbar">
              <div className="hs-topbar-left">
                <div className="hs-obj-switcher-wrap" ref={objSwitcherRef}>
                  <button className={`hs-obj-switcher${objSwitcherOpen ? ' open' : ''}`} onClick={() => { setObjSwitcherOpen(!objSwitcherOpen); setObjSwitcherSearch('') }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="5,3 19,12 5,21 5,3"/></svg>
                    <span>Coaching Playlists</span>
                    <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                  </button>
                  {objSwitcherOpen && (
                    <div className="hs-obj-panel">
                      <div className="hs-obj-search-wrap">
                        <svg className="hs-obj-search-icon" width="13" height="13" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                        <input className="hs-obj-search" placeholder="Search" value={objSwitcherSearch} onChange={(e) => setObjSwitcherSearch(e.target.value)} autoFocus />
                      </div>
                      <ul className="hs-obj-list" role="listbox">
                        {[
                          { label: 'Prospecting', view: 'prospecting' as ViewMode },
                          { label: 'Deals', view: 'deals' as ViewMode },
                          { label: 'Forecasts', view: 'forecasts' as ViewMode },
                          { label: 'Target Accounts', view: 'target-accounts' as ViewMode },
                          { label: 'Activity Feed', view: 'activity-feed' as ViewMode },
                          { label: 'Documents', view: 'sales-documents' as ViewMode },
                          { label: 'Meetings', view: 'meetings-scheduler' as ViewMode },
                          { label: 'Coaching Playlists', view: 'coaching-playlists' as ViewMode },
                          { label: 'Payments', view: 'payments' as ViewMode },
                          { label: 'Quotes', view: 'quotes' as ViewMode },
                          { label: 'Products', view: 'products' as ViewMode },
                        ].filter(item => !objSwitcherSearch || item.label.toLowerCase().includes(objSwitcherSearch.toLowerCase())).map(item => (
                          <li key={item.view} className={`hs-obj-item${item.view === activeView ? ' active' : ''}`} role="option" aria-selected={item.view === activeView} onClick={() => { setActiveView(item.view); setObjSwitcherOpen(false); setObjSwitcherSearch('') }}>{item.label}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="hs-topbar-sep" />
                <div className="hs-view-tabs">
                  <div className="hs-view-tab active">
                    <span className="hs-tab-label">All playlists</span>
                    {plTotal > 0 && <span className="hs-tab-count">{plTotal.toLocaleString()}</span>}
                  </div>
                </div>
              </div>
              <div className="hs-topbar-right">
                <button className="hs-add-contacts-btn" onClick={() => { setPlaylistForm({ name: '', description: '' }); setShowCreatePlaylist(true) }}>Create playlist</button>
              </div>
            </div>

            {/* Row 2: Toolbar */}
            <div className="hs-toolbar">
              <div className="hs-toolbar-left">
                <div className="hs-search-wrap">
                  <svg className="hs-search-icon" width="14" height="14" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M28.14 29.29c-.29 0-.58-.11-.81-.33l-7.58-7.58a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l7.58 7.58a1.14 1.14 0 0 1-.81 1.95"/><path d="M13.29 24.71c-6.3 0-11.43-5.13-11.43-11.43S6.98 1.86 13.29 1.86s11.43 5.13 11.43 11.43-5.13 11.43-11.43 11.43Zm0-20.57c-5.04 0-9.14 4.1-9.14 9.14s4.1 9.14 9.14 9.14 9.14-4.1 9.14-9.14-4.1-9.14-9.14-9.14"/></svg>
                  <input className="hs-search-input" placeholder="Search playlists" value={playlistsSearch} onChange={(e) => setPlaylistsSearch(e.target.value)} />
                  {playlistsSearch && <button className="hs-search-clear" onClick={() => setPlaylistsSearch('')}>&times;</button>}
                </div>
                <div className="hs-tb-sep" />
                <div className="hs-viewtype-group">
                  <div className="hs-viewtype-wrap">
                    <button className="hs-tb-btn hs-viewtype-btn" disabled>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                      <span>Table</span>
                    </button>
                  </div>
                  <button className={`hs-tb-btn hs-settings-btn${tableSettingsOpen ? ' active' : ''}`} onClick={() => setTableSettingsOpen(!tableSettingsOpen)} title={tableSettingsOpen ? 'Close settings' : 'Table settings'}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                  </button>
                </div>
                <div className="hs-tb-sep" />
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Filters" onClick={() => setQuickFiltersOpen(!quickFiltersOpen)}>
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M30.71 16.86H13.57a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28M6.71 16.86H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M10.14 20.29c-2.52 0-4.57-2.05-4.57-4.57s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29"/></svg>
                  <span>Filters</span>
                </button>
                <div className="hs-tb-sep" />
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Export">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  <span>Export</span>
                </button>
              </div>
              <div className="hs-toolbar-right">
                <div className="hs-tb-sep" />
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Clone view">
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M25.86 29.29H12.15c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-1.89 1.54-3.43 3.43-3.43h13.71c1.89 0 3.43 1.54 3.43 3.43v13.71c0 1.89-1.54 3.43-3.43 3.43M12.14 11c-.63 0-1.14.51-1.14 1.14v13.71c0 .63.51 1.14 1.14 1.14h13.71c.63 0 1.14-.51 1.14-1.14V12.14c0-.63-.51-1.14-1.14-1.14z"/><path d="M5.29 22.43c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43H19c1.89 0 3.43 1.54 3.43 3.43a1.14 1.14 0 1 1-2.28 0c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14V19c0 .63.51 1.14 1.14 1.14a1.14 1.14 0 1 1 0 2.28Z"/></svg>
                </button>
                <button className="hs-tb-btn hs-tb-btn-bordered disabled" disabled title="Save view">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                </button>
              </div>
            </div>

            {/* Row 3: Quick Filters */}
            {quickFiltersOpen && <div className="hs-quick-filters">
              <button className={`hs-qf-btn hs-qf-trigger`} onClick={() => {
                const fields = ['created_at', 'name', 'items_count', 'created_by']
                const idx = fields.indexOf(plSort.field)
                const next = fields[(idx + 1) % fields.length]
                setPlSort({ field: next, order: next === 'name' || next === 'created_by' ? 'ASC' : 'DESC' })
                setPlPage(1)
              }}>
                <span>Sort: {{ created_at: 'Created', name: 'Name', items_count: 'Items', created_by: 'Created By' }[plSort.field] || 'Created'}</span>
                <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
              </button>
            </div>}

            {/* Main content area */}
            <div className={`hs-content${tableSettingsOpen ? ' with-settings' : ''}`}>
              <div className="hs-table-area">
                {/* Row 4: Selection bar */}
                {plSelectedRows.size > 0 && (
                  <div className="hs-selection-bar">
                    <span className="hs-sel-count">{plSelectedRows.size} selected</span>
                    <div className="hs-sel-sep" />
                    <button className="hs-sel-btn" style={{ color: '#dc2626' }} onClick={() => {
                      if (confirm(`Delete ${plSelectedRows.size} playlist(s)?`)) {
                        Promise.all([...plSelectedRows].map(id => deleteCoachingPlaylist(id))).then(() => {
                          setPlaylistsData(prev => prev.filter(p => !plSelectedRows.has(p.id)))
                          setPlSelectedRows(new Set())
                        }).catch(() => {})
                      }
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                      Delete
                    </button>
                    <button className="hs-sel-close" onClick={() => setPlSelectedRows(new Set())} title="Deselect all">&times;</button>
                  </div>
                )}

                {/* Row 5: Data table */}
                <div className="hs-table-scroll">
                  {playlistsLoading ? (
                    <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                      <tbody><tr><td colSpan={7} className="hs-loading-cell"><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}><DotLottieReact src="/loading.lottie" loop autoplay style={{ width: 80, height: 80 }} /><span style={{ fontSize: '0.82rem', color: '#7c98b6', marginTop: 8 }}>Loading playlists...</span></div></td></tr></tbody>
                    </table>
                  ) : playlistsData.length === 0 ? (
                    <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                      <tbody><tr><td colSpan={7} className="hs-empty-cell">
                        <div style={{ padding: '60px 40px', textAlign: 'center' }}>
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" style={{ margin: '0 auto 16px' }}><polygon points="5,3 19,12 5,21 5,3"/></svg>
                          <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#33475b', margin: '0 0 8px' }}>Create your first coaching playlist</h3>
                          <p style={{ fontSize: '0.82rem', color: '#7c98b6', margin: '0 0 20px', maxWidth: '400px', marginLeft: 'auto', marginRight: 'auto' }}>Curate collections of recorded sales calls for training and onboarding. Help your team learn from the best examples.</p>
                          <button className="hs-add-contacts-btn" onClick={() => { setPlaylistForm({ name: '', description: '' }); setShowCreatePlaylist(true) }}>Create playlist</button>
                        </div>
                      </td></tr></tbody>
                    </table>
                  ) : filteredPlaylists.length === 0 ? (
                    <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                      <tbody><tr><td colSpan={7} className="hs-empty-cell">
                        <div style={{ padding: '40px', textAlign: 'center', color: '#7c98b6' }}>No playlists found.</div>
                      </td></tr></tbody>
                    </table>
                  ) : (
                    <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                      <colgroup>
                        <col style={{ width: 40 }} />
                        <col style={{ width: plColWidths.name }} />
                        <col style={{ width: plColWidths.items }} />
                        <col style={{ width: plColWidths.duration }} />
                        <col style={{ width: plColWidths.created_by }} />
                        <col style={{ width: plColWidths.created_at }} />
                        <col style={{ width: plColWidths.actions }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th className="hs-th-check"><input type="checkbox" checked={plPaged.length > 0 && plSelectedRows.size === plPaged.length} onChange={toggleAllPlRows} /></th>
                          <th className="hs-th-resizable hs-th-sortable hs-th-frozen" style={{ position: 'sticky', left: 40, top: 0, zIndex: 15, background: '#f8fafc', boxShadow: '2px 0 4px rgba(0,0,0,0.06)' }} onClick={() => togglePlSort('name')}>
                            <span className="hs-th-content">Playlist Name{plSort.field === 'name' && <span className="hs-sort-arrow">{plSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}{plSort.field !== 'name' && <span className="hs-th-hover-sort">{plSortArrowSvg}</span>}</span>
                            <div className="hs-col-resize-handle" onMouseDown={(e) => onPlResizeStart('name', e)} onClick={(e) => e.stopPropagation()} />
                          </th>
                          <th className="hs-th-resizable hs-th-sortable" onClick={() => togglePlSort('items_count')}>
                            <span className="hs-th-content">Items{plSort.field === 'items_count' && <span className="hs-sort-arrow">{plSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}{plSort.field !== 'items_count' && <span className="hs-th-hover-sort">{plSortArrowSvg}</span>}</span>
                            <div className="hs-col-resize-handle" onMouseDown={(e) => onPlResizeStart('items', e)} onClick={(e) => e.stopPropagation()} />
                          </th>
                          <th className="hs-th-resizable hs-th-sortable" onClick={() => togglePlSort('total_duration_seconds')}>
                            <span className="hs-th-content">Duration{plSort.field === 'total_duration_seconds' && <span className="hs-sort-arrow">{plSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}{plSort.field !== 'total_duration_seconds' && <span className="hs-th-hover-sort">{plSortArrowSvg}</span>}</span>
                            <div className="hs-col-resize-handle" onMouseDown={(e) => onPlResizeStart('duration', e)} onClick={(e) => e.stopPropagation()} />
                          </th>
                          <th className="hs-th-resizable hs-th-sortable" onClick={() => togglePlSort('created_by')}>
                            <span className="hs-th-content">Created By{plSort.field === 'created_by' && <span className="hs-sort-arrow">{plSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}{plSort.field !== 'created_by' && <span className="hs-th-hover-sort">{plSortArrowSvg}</span>}</span>
                            <div className="hs-col-resize-handle" onMouseDown={(e) => onPlResizeStart('created_by', e)} onClick={(e) => e.stopPropagation()} />
                          </th>
                          <th className="hs-th-resizable hs-th-sortable" onClick={() => togglePlSort('created_at')}>
                            <span className="hs-th-content">Created{plSort.field === 'created_at' && <span className="hs-sort-arrow">{plSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}{plSort.field !== 'created_at' && <span className="hs-th-hover-sort">{plSortArrowSvg}</span>}</span>
                            <div className="hs-col-resize-handle" onMouseDown={(e) => onPlResizeStart('created_at', e)} onClick={(e) => e.stopPropagation()} />
                          </th>
                          <th className="hs-th-resizable"><span className="hs-th-content">Actions</span><div className="hs-col-resize-handle" onMouseDown={(e) => onPlResizeStart('actions', e)} onClick={(e) => e.stopPropagation()} /></th>
                        </tr>
                      </thead>
                      <tbody>
                        {plPaged.map(pl => (
                          <tr key={pl.id} className={`hs-row${plSelectedRows.has(pl.id) ? ' selected' : ''}`} style={{ cursor: 'pointer' }} onClick={() => setEditingPlaylistId(pl.id)}>
                            <td className="hs-td-check" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={plSelectedRows.has(pl.id)} onChange={() => setPlSelectedRows(prev => { const n = new Set(prev); n.has(pl.id) ? n.delete(pl.id) : n.add(pl.id); return n })} /></td>
                            <td className="hs-td-name" style={{ position: 'sticky', left: 40, zIndex: 2, background: plSelectedRows.has(pl.id) ? '#eff6ff' : '#fff' }}>
                              <div className="hs-name-cell">
                                <span className="hs-name-link" style={{ fontWeight: 600, color: '#1976d2', fontSize: '0.82rem' }}>{pl.name}</span>
                                <button className="hs-preview-btn" onClick={(e) => { e.stopPropagation() }}>Preview</button>
                              </div>
                              {pl.description && <div style={{ fontSize: '0.72rem', color: '#7c98b6', marginTop: '2px' }}>{pl.description.substring(0, 60)}</div>}
                            </td>
                            <td style={{ fontSize: '0.82rem', color: '#516f90' }}>{pl.items_count || 0} items</td>
                            <td style={{ fontSize: '0.8rem', color: '#516f90' }}>{pl.total_duration_seconds ? `${Math.floor(pl.total_duration_seconds / 60)}m` : '--'}</td>
                            <td style={{ fontSize: '0.8rem', color: '#516f90' }}>{pl.created_by}</td>
                            <td style={{ fontSize: '0.75rem', color: '#516f90' }}>{pl.created_at ? formatDatePref(pl.created_at) : ''}</td>
                            <td onClick={(e) => e.stopPropagation()}>
                              <button style={{ padding: '3px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', background: '#fff', color: '#d32f2f', fontSize: '0.68rem', cursor: 'pointer' }}
                                onClick={() => { if (confirm('Delete this playlist?')) deleteCoachingPlaylist(pl.id).then(() => setPlaylistsData(prev => prev.filter(p => p.id !== pl.id))) }}>Delete</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Row 6: Pagination */}
                <div className="hs-pagination">
                  <nav className="hs-paginator" aria-label="Pagination">
                    <button className="hs-paginator-btn" disabled={plPage <= 1} onClick={() => setPlPage(p => p - 1)} aria-label="Previous page">
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95"/></svg>
                      <span>Prev</span>
                    </button>
                    {(() => { const pages: number[] = []; const start = Math.max(1, plPage - 2); const end = Math.min(plPageCount, start + 4); for (let i = start; i <= end; i++) pages.push(i); return pages.map(p => (<button key={p} className={`hs-paginator-num${p === plPage ? ' active' : ''}`} onClick={() => setPlPage(p)}>{p}</button>)) })()}
                    <button className="hs-paginator-btn" disabled={plPage >= plPageCount} onClick={() => setPlPage(p => p + 1)} aria-label="Next page">
                      <span>Next</span>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M11 27.71c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62l9.48-9.48-9.48-9.47a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0L22.1 15.48c.45.45.45 1.17 0 1.62L11.81 27.39c-.22.22-.52.33-.81.33Z"/></svg>
                    </button>
                  </nav>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', marginLeft: 'auto' }}><select value={tablePageSize} onChange={e => { setTablePageSize(Number(e.target.value) as any); setPlPage(1) }} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #e2e8f0', fontSize: '0.82rem' }}><option value={100}>100</option><option value={200}>200</option><option value={500}>500</option></select><span>per page</span></div>
                </div>
              </div>

              {/* Row 7: Settings panel */}
              {tableSettingsOpen && (
                <div className="hs-settings-panel">
                  <div className="hs-settings-header">
                    <span className="hs-settings-title">Table settings</span>
                    <button className="hs-settings-close" onClick={() => setTableSettingsOpen(false)}>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                    </button>
                  </div>
                  <div className="hs-settings-body">
                    <div className="hs-settings-group">
                      <label className="hs-settings-label">Pagination<span className="hs-info-icon" title="Number of rows shown per page"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span></label>
                      {([100, 200, 500] as const).map(size => (<label className="hs-settings-radio" key={size}><input type="radio" name="plPageSize" checked={tablePageSize === size} onChange={() => { setTablePageSize(size); setPlPage(1) }} /><span className="hs-radio-indicator" /><span>{size} per page</span></label>))}
                    </div>
                    <hr className="hs-settings-divider" />
                    <div className="hs-settings-group">
                      <label className="hs-settings-label">Row height<span className="hs-info-icon" title="Adjust the vertical spacing of table rows"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span></label>
                      {([{ value: 'small' as const, label: 'Compact' }, { value: 'medium' as const, label: 'Default' }, { value: 'large' as const, label: 'Comfortable' }]).map(opt => (<label className="hs-settings-radio" key={opt.value}><input type="radio" name="plRowHeight" checked={tableRowHeight === opt.value} onChange={() => setTableRowHeight(opt.value)} /><span className="hs-radio-indicator" /><span>{opt.label}</span></label>))}
                    </div>
                    <hr className="hs-settings-divider" />
                    <div className="hs-settings-group">
                      <div className="hs-settings-toggle-row">
                        <label className="hs-settings-label">Zebra striping<span className="hs-info-icon" title="Alternate row background colors for easier reading"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span></label>
                        <button className={`hs-toggle${tableZebraStriping ? ' on' : ''}`} onClick={() => setTableZebraStriping(!tableZebraStriping)} role="switch" aria-checked={tableZebraStriping}>
                          <span className="hs-toggle-track"><span className="hs-toggle-on">ON</span><span className="hs-toggle-off">OFF</span><span className="hs-toggle-handle" /></span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* Create Playlist Modal */}
            {showCreatePlaylist && (
              <div className="modal-overlay" onClick={() => setShowCreatePlaylist(false)}>
                <div className="modal-card" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
                  <button type="button" className="modal-close" onClick={() => setShowCreatePlaylist(false)}>&times;</button>
                  <div className="modal-body">
                    <h3 className="modal-title">Create Coaching Playlist</h3>
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 500, color: '#33475b', marginBottom: 4 }}>Name *</label>
                      <input type="text" value={playlistForm.name} onChange={(e) => setPlaylistForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Top Closing Calls" style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem' }} />
                    </div>
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 500, color: '#33475b', marginBottom: 4 }}>Description</label>
                      <textarea value={playlistForm.description} onChange={(e) => setPlaylistForm(p => ({ ...p, description: e.target.value }))} rows={3} placeholder="What this playlist is for..." style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem', resize: 'vertical' }} />
                    </div>
                    <button className="hs-add-contacts-btn" disabled={playlistSaving || !playlistForm.name} onClick={() => {
                      setPlaylistSaving(true)
                      createCoachingPlaylist(playlistForm)
                        .then(() => { setShowCreatePlaylist(false); setPlaylistsLoading(true); fetchCoachingPlaylists().then(r => { setPlaylistsData(r.data); setPlaylistsLoading(false) }) })
                        .catch(e => alert('Error: ' + e.message))
                        .finally(() => setPlaylistSaving(false))
                    }}>{playlistSaving ? 'Creating...' : 'Create Playlist'}</button>
                  </div>
                </div>
              </div>
            )}
          </div>
          )
        })()}

        {/* Coaching Playlist Detail/Editor */}
        {activeView === 'coaching-playlists' && editingPlaylistId !== null && editingPlaylistData && (
          <div className="hs-contacts">
            <div className="hs-topbar">
              <div className="hs-topbar-left">
                <button style={{ padding: '4px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', background: '#fff', fontSize: '0.78rem', cursor: 'pointer', marginRight: '8px' }} onClick={() => { setEditingPlaylistId(null); setEditingPlaylistData(null) }}>
                  &larr; Back
                </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polygon points="5,3 19,12 5,21 5,3"/></svg>
                  <span style={{ fontWeight: 600, fontSize: '0.92rem', color: '#33475b' }}>{editingPlaylistData.name}</span>
                </div>
              </div>
              <div className="hs-topbar-right">
                <button className="hs-add-contacts-btn" onClick={() => { setPlaylistItemForm({ title: '', recording_url: '', duration_seconds: 0, notes: '' }); setShowAddPlaylistItem(true) }}>Add Item</button>
              </div>
            </div>
            {editingPlaylistData.description && (
              <div style={{ padding: '12px 20px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', fontSize: '0.82rem', color: '#64748b' }}>
                {editingPlaylistData.description}
              </div>
            )}
            <div className="hs-content" style={{ padding: '0' }}>
              {(editingPlaylistData.items || []).length === 0 ? (
                <div style={{ padding: '60px 40px', textAlign: 'center' }}>
                  <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#33475b', margin: '0 0 8px' }}>No items in this playlist</h3>
                  <p style={{ fontSize: '0.82rem', color: '#7c98b6', margin: '0 0 16px' }}>Add call recordings and training clips to this playlist.</p>
                  <button className="hs-add-contacts-btn" onClick={() => { setPlaylistItemForm({ title: '', recording_url: '', duration_seconds: 0, notes: '' }); setShowAddPlaylistItem(true) }}>Add Item</button>
                </div>
              ) : (
                <div>
                  {(editingPlaylistData.items || []).map((item, idx) => (
                    <div key={item.id} style={{ display: 'flex', gap: '12px', padding: '14px 20px', borderBottom: '1px solid #f1f5f9', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#94a3b8', width: '24px', textAlign: 'center' }}>{idx + 1}</span>
                      <div style={{ width: '36px', height: '36px', borderRadius: '6px', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2563eb', flexShrink: 0 }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><polygon points="5,3 19,12 5,21 5,3"/></svg>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>{item.title}</div>
                        <div style={{ display: 'flex', gap: '12px', fontSize: '0.72rem', color: '#94a3b8', marginTop: '2px' }}>
                          {item.duration_seconds > 0 && <span>{Math.floor(item.duration_seconds / 60)}:{String(item.duration_seconds % 60).padStart(2, '0')}</span>}
                          {item.added_by && <span>Added by {item.added_by}</span>}
                        </div>
                        {item.notes && <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '4px' }}>{item.notes}</div>}
                      </div>
                      {item.recording_url && (
                        <a href={item.recording_url} target="_blank" rel="noopener noreferrer" style={{ padding: '3px 8px', border: '1px solid #cbd6e2', borderRadius: '4px', background: '#f8fafc', fontSize: '0.68rem', color: '#2563eb', textDecoration: 'none' }}>Play</a>
                      )}
                      <button style={{ padding: '3px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', background: '#fff', color: '#d32f2f', fontSize: '0.68rem', cursor: 'pointer' }}
                        onClick={() => { if (confirm('Remove this item?')) removePlaylistItem(editingPlaylistId!, item.id).then(() => { fetchCoachingPlaylist(editingPlaylistId!).then(r => setEditingPlaylistData(r.data)) }) }}>Remove</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Add Playlist Item Modal */}
            {showAddPlaylistItem && (
              <div className="modal-overlay" onClick={() => setShowAddPlaylistItem(false)}>
                <div className="modal-card" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
                  <button type="button" className="modal-close" onClick={() => setShowAddPlaylistItem(false)}>&times;</button>
                  <div className="modal-body">
                    <h3 className="modal-title">Add Item to Playlist</h3>
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 500, color: '#33475b', marginBottom: 4 }}>Title *</label>
                      <input type="text" value={playlistItemForm.title} onChange={(e) => setPlaylistItemForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Great discovery call with ABC Corp" style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem' }} />
                    </div>
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 500, color: '#33475b', marginBottom: 4 }}>Recording URL</label>
                      <input type="url" value={playlistItemForm.recording_url} onChange={(e) => setPlaylistItemForm(p => ({ ...p, recording_url: e.target.value }))} placeholder="https://..." style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem' }} />
                    </div>
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 500, color: '#33475b', marginBottom: 4 }}>Duration (seconds)</label>
                      <input type="number" value={playlistItemForm.duration_seconds} onChange={(e) => setPlaylistItemForm(p => ({ ...p, duration_seconds: parseInt(e.target.value) || 0 }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem' }} />
                    </div>
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 500, color: '#33475b', marginBottom: 4 }}>Notes</label>
                      <textarea value={playlistItemForm.notes} onChange={(e) => setPlaylistItemForm(p => ({ ...p, notes: e.target.value }))} rows={2} placeholder="Key moments, learning points..." style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem', resize: 'vertical' }} />
                    </div>
                    <button className="hs-add-contacts-btn" disabled={!playlistItemForm.title} onClick={() => {
                      addPlaylistItem(editingPlaylistId!, playlistItemForm)
                        .then(() => { setShowAddPlaylistItem(false); fetchCoachingPlaylist(editingPlaylistId!).then(r => setEditingPlaylistData(r.data)) })
                        .catch(e => alert('Error: ' + e.message))
                    }}>Add Item</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ========== Forecasts View ========== */}
        {activeView === 'forecasts' && (() => {
          const fcDeals = (forecastData?.deals || []) as any[]
          const fcSearched = fcDeals.filter((d: any) => !forecastSearch || (d.title || '').toLowerCase().includes(forecastSearch.toLowerCase()) || (d.owner || '').toLowerCase().includes(forecastSearch.toLowerCase()))
          const fcFiltered = forecastStatusFilter ? fcSearched.filter((d: any) => d.forecast_category === forecastStatusFilter) : fcSearched
          const fcSorted = [...fcFiltered].sort((a: any, b: any) => {
            const f = forecastSort.field
            const va = f === 'opportunity' || f === 'probability' || f === 'weighted_amount' ? (a[f] || 0) : String(a[f] || '')
            const vb = f === 'opportunity' || f === 'probability' || f === 'weighted_amount' ? (b[f] || 0) : String(b[f] || '')
            if (va < vb) return forecastSort.order === 'ASC' ? -1 : 1
            if (va > vb) return forecastSort.order === 'ASC' ? 1 : -1
            return 0
          })
          const fcTotal = fcSorted.length
          const fcTotalPages = Math.max(1, Math.ceil(fcTotal / tablePageSize))
          const fcCurrentPage = Math.min(forecastPage, fcTotalPages)
          const fcPaged = fcSorted.slice((fcCurrentPage - 1) * tablePageSize, fcCurrentPage * tablePageSize)
          const toggleAllForecastRows = () => {
            if (forecastSelectedRows.size === fcPaged.length) setForecastSelectedRows(new Set())
            else setForecastSelectedRows(new Set(fcPaged.map((d: any) => d.id)))
          }
          const toggleFcSort = (field: string) => {
            setForecastSort(prev => ({ field, order: prev.field === field && prev.order === 'ASC' ? 'DESC' : 'ASC' }))
            setForecastPage(1)
          }
          return (
          <div className="hs-contacts">
            {/* Row 1: Topbar */}
            <div className="hs-topbar">
              <div className="hs-topbar-left">
                <div className="hs-obj-switcher-wrap" ref={objSwitcherRef}>
                  <button className={`hs-obj-switcher${objSwitcherOpen ? ' open' : ''}`} onClick={() => { setObjSwitcherOpen(!objSwitcherOpen); setObjSwitcherSearch('') }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 3v18h18"/><path d="m19 9-5 5-4-4-3 3"/></svg>
                    <span>Forecasts</span>
                    <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                  </button>
                  {objSwitcherOpen && (
                    <div className="hs-obj-panel">
                      <div className="hs-obj-search-wrap">
                        <svg className="hs-obj-search-icon" width="13" height="13" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                        <input className="hs-obj-search" placeholder="Search" value={objSwitcherSearch} onChange={(e) => setObjSwitcherSearch(e.target.value)} autoFocus />
                      </div>
                      <ul className="hs-obj-list" role="listbox">
                        {[
                          { label: 'Prospecting', view: 'prospecting' as ViewMode },
                          { label: 'Deals', view: 'deals' as ViewMode },
                          { label: 'Forecasts', view: 'forecasts' as ViewMode },
                          { label: 'Target Accounts', view: 'target-accounts' as ViewMode },
                          { label: 'Activity Feed', view: 'activity-feed' as ViewMode },
                          { label: 'Documents', view: 'sales-documents' as ViewMode },
                          { label: 'Meetings', view: 'meetings-scheduler' as ViewMode },
                          { label: 'Coaching Playlists', view: 'coaching-playlists' as ViewMode },
                          { label: 'Payments', view: 'payments' as ViewMode },
                          { label: 'Quotes', view: 'quotes' as ViewMode },
                          { label: 'Products', view: 'products' as ViewMode },
                        ].filter(item => !objSwitcherSearch || item.label.toLowerCase().includes(objSwitcherSearch.toLowerCase())).map(item => (
                          <li key={item.view} className={`hs-obj-item${item.view === activeView ? ' active' : ''}`} role="option" aria-selected={item.view === activeView} onClick={() => { setActiveView(item.view); setObjSwitcherOpen(false); setObjSwitcherSearch('') }}>{item.label}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="hs-topbar-sep" />
                {/* View tabs */}
                <div className="hs-view-tabs">
                  {[
                    { key: '', label: 'All forecasts' },
                    { key: 'pipeline', label: 'Pipeline' },
                    { key: 'best_case', label: 'Best Case' },
                    { key: 'commit', label: 'Commit' },
                    { key: 'closed_won', label: 'Closed Won' },
                    { key: 'closed_lost', label: 'Closed Lost' },
                  ].map(tab => {
                    const isActive = forecastStatusFilter === tab.key
                    return (
                      <div key={tab.key} className={`hs-view-tab${isActive ? ' active' : ''}`} onClick={() => { setForecastStatusFilter(tab.key); setForecastPage(1) }}>
                        <span className="hs-tab-label">{tab.label}</span>
                        {isActive && fcTotal > 0 && <span className="hs-tab-count">{fcTotal.toLocaleString()}</span>}
                        {isActive && (
                          <span className="hs-tab-kebab" onClick={(e) => { e.stopPropagation(); setTabEditOpen(!tabEditOpen) }}>
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>
                          </span>
                        )}
                        {tab.key !== '' && <span className="hs-tab-x" onClick={(e) => { e.stopPropagation(); setForecastStatusFilter(''); setForecastPage(1) }}>&times;</span>}
                      </div>
                    )
                  })}
                </div>
              </div>
              <div className="hs-topbar-right">
                <div className="hs-add-btn-wrap">
                  <button className="hs-add-contacts-btn" onClick={() => { setGoalAmount(''); setShowGoalModal(true) }}>
                    Set Goals
                    <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Row 2: Toolbar */}
            <div className="hs-toolbar">
              <div className="hs-toolbar-left">
                <div className="hs-search-wrap">
                  <svg className="hs-search-icon" width="14" height="14" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M28.14 29.29c-.29 0-.58-.11-.81-.33l-7.58-7.58a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l7.58 7.58a1.14 1.14 0 0 1-.81 1.95"/><path d="M13.29 24.71c-6.3 0-11.43-5.13-11.43-11.43S6.98 1.86 13.29 1.86s11.43 5.13 11.43 11.43-5.13 11.43-11.43 11.43Zm0-20.57c-5.04 0-9.14 4.1-9.14 9.14s4.1 9.14 9.14 9.14 9.14-4.1 9.14-9.14-4.1-9.14-9.14-9.14"/></svg>
                  <input className="hs-search-input" placeholder="Search forecasts" value={forecastSearch} onChange={(e) => { setForecastSearch(e.target.value); setForecastPage(1) }} />
                  {forecastSearch && <button className="hs-search-clear" onClick={() => { setForecastSearch(''); setForecastPage(1) }}>&times;</button>}
                </div>
                <div className="hs-tb-sep" />
                <div className="hs-viewtype-group">
                  <div className="hs-viewtype-wrap">
                    <button className="hs-tb-btn hs-viewtype-btn">
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                      <span>Table</span>
                    </button>
                  </div>
                  <button className={`hs-tb-btn hs-settings-btn${tableSettingsOpen ? ' active' : ''}`} onClick={() => setTableSettingsOpen(!tableSettingsOpen)} title={tableSettingsOpen ? 'Close settings' : 'Table settings'}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                  </button>
                </div>
                <div className="hs-tb-sep" />
                {/* Filters */}
                <button className={`hs-tb-btn hs-tb-btn-bordered${forecastQuickFiltersOpen ? ' hs-tb-btn-pressed' : ''}`} title="Filters" onClick={() => setForecastQuickFiltersOpen(!forecastQuickFiltersOpen)}>
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M30.71 16.86H13.57a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28M6.71 16.86H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M10.14 20.29c-2.52 0-4.57-2.05-4.57-4.57s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29"/></svg>
                  <span>Filters</span>
                </button>
                <div className="hs-tb-sep" />
                {/* Export */}
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Export">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  <span>Export</span>
                </button>
              </div>
              <div className="hs-toolbar-right">
                <div className="hs-tb-sep" />
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Clone view">
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M25.86 29.29H12.15c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-1.89 1.54-3.43 3.43-3.43h13.71c1.89 0 3.43 1.54 3.43 3.43v13.71c0 1.89-1.54 3.43-3.43 3.43M12.14 11c-.63 0-1.14.51-1.14 1.14v13.71c0 .63.51 1.14 1.14 1.14h13.71c.63 0 1.14-.51 1.14-1.14V12.14c0-.63-.51-1.14-1.14-1.14z"/><path d="M5.29 22.43c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43H19c1.89 0 3.43 1.54 3.43 3.43a1.14 1.14 0 1 1-2.28 0c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14V19c0 .63.51 1.14 1.14 1.14a1.14 1.14 0 1 1 0 2.28Z"/></svg>
                </button>
                <button className="hs-tb-btn hs-tb-btn-bordered disabled" disabled title="Save view">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                </button>
              </div>
            </div>

            {/* Row 3: Quick Filters */}
            {forecastQuickFiltersOpen && <div className="hs-quick-filters">
              {/* Pipeline selector */}
              <select value={forecastPipeline} onChange={(e) => { setForecastPipeline(e.target.value); setForecastPage(1) }} className="hs-qf-btn hs-qf-trigger" style={{ appearance: 'auto', cursor: 'pointer' }}>
                {['Estimation', 'Production', 'Maintenance', 'Commercial'].map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              {/* Period toggles */}
              {['monthly', 'quarterly'].map(p => (
                <button key={p} className={`hs-qf-btn hs-qf-trigger${forecastPeriod === p ? ' has-value' : ''}`} onClick={() => setForecastPeriod(p as any)}>{p.charAt(0).toUpperCase() + p.slice(1)}</button>
              ))}
              {/* Month navigator */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <button className="hs-qf-btn hs-qf-trigger" style={{ padding: '0 6px' }} onClick={() => { const [y, m] = forecastMonth.split('-').map(Number); const d = new Date(y, m - 2, 1); setForecastMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`); setForecastPage(1) }}>&lsaquo;</button>
                <span style={{ fontSize: '0.82rem', fontWeight: 600, minWidth: 110, textAlign: 'center', color: '#33475b' }}>{new Date(forecastMonth + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
                <button className="hs-qf-btn hs-qf-trigger" style={{ padding: '0 6px' }} onClick={() => { const [y, m] = forecastMonth.split('-').map(Number); const d = new Date(y, m, 1); setForecastMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`); setForecastPage(1) }}>&rsaquo;</button>
              </div>
              {/* Sort */}
              <button className={`hs-qf-btn hs-qf-trigger${forecastSort.field !== 'close_date' ? ' has-value' : ''}`} onClick={() => {
                const fields = ['close_date', 'title', 'opportunity', 'probability', 'owner']
                const idx = fields.indexOf(forecastSort.field)
                const next = fields[(idx + 1) % fields.length]
                setForecastSort({ field: next, order: next === 'title' || next === 'owner' ? 'ASC' : 'DESC' })
                setForecastPage(1)
              }}>
                <span>Sort: {{ close_date: 'Close Date', title: 'Deal Name', opportunity: 'Amount', probability: 'Probability', owner: 'Owner' }[forecastSort.field] || 'Close Date'}</span>
                <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
              </button>
              {/* Team view toggle */}
              <button className={`hs-qf-btn hs-qf-trigger${forecastTeamView ? ' has-value' : ''}`} onClick={() => setForecastTeamView(!forecastTeamView)}>
                <span>{forecastTeamView ? 'Team View ON' : 'Team View'}</span>
                {forecastTeamView && <span className="hs-qf-clear" onClick={(e) => { e.stopPropagation(); setForecastTeamView(false) }}>&times;</span>}
              </button>
              <div className="hs-qf-sep" />
              <div className="hs-qf-more-wrap">
                <button className="hs-qf-btn hs-qf-more">
                  <svg width="10" height="10" viewBox="0 0 32 32" fill="currentColor"><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V3a1.14 1.14 0 1 1 2.28 0v25.14c0 .63-.51 1.14-1.14 1.14Z"/><path d="M28.14 16.71H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28"/></svg>
                  <span>More</span>
                </button>
              </div>
            </div>}

            <div className={`hs-content${tableSettingsOpen ? ' with-settings' : ''}`}>
              <div className="hs-table-area">
                {/* KPI Summary row */}
                {forecastData?.summary && (
                  <div style={{ display: 'flex', gap: 8, padding: '10px 12px', borderBottom: '1px solid var(--line)', flexWrap: 'wrap' }}>
                    {[{ label: 'GOAL', value: forecastData.summary.goal, color: 'var(--ink)' }, { label: 'CLOSED WON', value: forecastData.summary.closed_won, color: 'var(--success)' }, { label: 'COMMIT', value: forecastData.summary.commit, color: '#3b82f6' }, { label: 'BEST CASE', value: forecastData.summary.best_case, color: '#f59e0b' }, { label: 'PIPELINE', value: forecastData.summary.pipeline, color: '#94a3b8' }, { label: 'GAP TO GOAL', value: forecastData.summary.gap, color: forecastData.summary.gap > 0 ? 'var(--danger)' : 'var(--success)' }].map(kpi => (
                      <div key={kpi.label} style={{ flex: '1 1 110px', padding: '4px 8px', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 6 }}>
                        <span style={{ fontSize: '0.65rem', color: 'var(--muted)', textTransform: 'uppercase', display: 'block' }}>{kpi.label}</span>
                        <strong style={{ fontSize: '0.76rem', color: kpi.color }}>{currencyFormatter.format(Math.abs(kpi.value))}</strong>
                      </div>
                    ))}
                  </div>
                )}

                {/* Team breakdown (conditional) */}
                {forecastTeamView && forecastData?.by_user && forecastData.by_user.length > 0 && (
                  <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--line)', background: '#fafbfc' }}>
                    <strong style={{ fontSize: '0.78rem', marginBottom: 4, display: 'block' }}>Team Breakdown</strong>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 6 }}>
                      {forecastData.by_user.map((u: any) => (
                        <div key={u.user_id} style={{ padding: '4px 8px', background: '#fff', border: '1px solid var(--line)', borderRadius: 4, fontSize: '0.72rem' }}>
                          <strong style={{ color: '#33475b' }}>{u.name}</strong>
                          <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                            <span style={{ color: '#166534' }}>{currencyFormatter.format(u.closed_won)}</span>
                            <span style={{ color: '#2563eb' }}>{currencyFormatter.format(u.commit)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Row 4: Selection bar */}
                {forecastSelectedRows.size > 0 && (
                  <div className="hs-selection-bar">
                    <span className="hs-sel-count">{forecastSelectedRows.size} selected</span>
                    <button className="hs-sel-close" onClick={() => setForecastSelectedRows(new Set())} title="Deselect all">&times;</button>
                  </div>
                )}

                {/* Loading bar */}
                {forecastLoading && fcDeals.length > 0 && <div className="hs-loading-bar" />}

                {/* Row 5: Data Table */}
                <div className="hs-table-scroll">
                  {forecastLoading && fcDeals.length === 0 ? (
                    <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                      <colgroup><col style={{ width: 40 }} /><col style={{ width: 220 }} /><col style={{ width: 120 }} /><col style={{ width: 110 }} /><col style={{ width: 130 }} /><col style={{ width: 140 }} /><col style={{ width: 120 }} /><col style={{ width: 90 }} /><col style={{ width: 110 }} /></colgroup>
                      <thead><tr>
                        <th className="hs-th-check"><input type="checkbox" disabled /></th>
                        <th className="hs-th-resizable" style={{ position: 'sticky', left: 40, zIndex: 2, background: '#f5f8fa' }}><span>Deal Name</span></th>
                        <th className="hs-th-resizable hs-th-sortable"><span className="hs-th-content">Close Date</span><div className="hs-col-resize-handle" /></th>
                        <th className="hs-th-resizable hs-th-sortable"><span className="hs-th-content">Amount</span><div className="hs-col-resize-handle" /></th>
                        <th className="hs-th-resizable hs-th-sortable"><span className="hs-th-content">Stage</span><div className="hs-col-resize-handle" /></th>
                        <th className="hs-th-resizable hs-th-sortable"><span className="hs-th-content">Category</span><div className="hs-col-resize-handle" /></th>
                        <th className="hs-th-resizable hs-th-sortable"><span className="hs-th-content">Owner</span><div className="hs-col-resize-handle" /></th>
                        <th className="hs-th-resizable hs-th-sortable"><span className="hs-th-content">Prob %</span><div className="hs-col-resize-handle" /></th>
                        <th className="hs-th-resizable hs-th-sortable"><span className="hs-th-content">Weighted</span><div className="hs-col-resize-handle" /></th>
                      </tr></thead>
                      <tbody><tr><td colSpan={9} className="hs-loading-cell"><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}><DotLottieReact src="/loading.lottie" loop autoplay style={{ width: 80, height: 80 }} /><span style={{ fontSize: '0.82rem', color: '#7c98b6', marginTop: 8 }}>Loading forecasts...</span></div></td></tr></tbody>
                    </table>
                  ) : fcPaged.length === 0 ? (
                    <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                      <colgroup><col style={{ width: 40 }} /><col style={{ width: 220 }} /><col style={{ width: 120 }} /><col style={{ width: 110 }} /><col style={{ width: 130 }} /><col style={{ width: 140 }} /><col style={{ width: 120 }} /><col style={{ width: 90 }} /><col style={{ width: 110 }} /></colgroup>
                      <thead><tr>
                        <th className="hs-th-check"><input type="checkbox" disabled /></th>
                        <th className="hs-th-resizable" style={{ position: 'sticky', left: 40, zIndex: 2, background: '#f5f8fa' }}><span>Deal Name</span></th>
                        <th className="hs-th-resizable"><span>Close Date</span></th>
                        <th className="hs-th-resizable"><span>Amount</span></th>
                        <th className="hs-th-resizable"><span>Stage</span></th>
                        <th className="hs-th-resizable"><span>Category</span></th>
                        <th className="hs-th-resizable"><span>Owner</span></th>
                        <th className="hs-th-resizable"><span>Prob %</span></th>
                        <th className="hs-th-resizable"><span>Weighted</span></th>
                      </tr></thead>
                      <tbody><tr><td colSpan={9} className="hs-empty-cell">
                        <div style={{ padding: '40px', textAlign: 'center' }}>
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '12px' }}><path d="M3 3v18h18"/><path d="m19 9-5 5-4-4-3 3"/></svg>
                          <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#33475b', marginBottom: '6px' }}>{forecastSearch ? 'No matching deals' : 'No deals in this period'}</div>
                          <div style={{ fontSize: '0.82rem', color: '#7c98b6', maxWidth: '360px', margin: '0 auto' }}>{forecastSearch ? 'Try a different search term.' : 'Adjust pipeline, period, or date range to see forecast data.'}</div>
                        </div>
                      </td></tr></tbody>
                    </table>
                  ) : (
                    <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                      <colgroup><col style={{ width: 40 }} /><col style={{ width: 220 }} /><col style={{ width: 120 }} /><col style={{ width: 110 }} /><col style={{ width: 130 }} /><col style={{ width: 140 }} /><col style={{ width: 120 }} /><col style={{ width: 90 }} /><col style={{ width: 110 }} /></colgroup>
                      <thead><tr>
                        <th className="hs-th-check"><input type="checkbox" checked={fcPaged.length > 0 && forecastSelectedRows.size === fcPaged.length} onChange={toggleAllForecastRows} /></th>
                        {[
                          { key: 'title', label: 'Deal Name', sortField: 'title', frozen: true },
                          { key: 'close_date', label: 'Close Date', sortField: 'close_date' },
                          { key: 'opportunity', label: 'Amount', sortField: 'opportunity' },
                          { key: 'stage_label', label: 'Stage', sortField: 'stage_label' },
                          { key: 'forecast_category', label: 'Category', sortField: 'forecast_category' },
                          { key: 'owner', label: 'Owner', sortField: 'owner' },
                          { key: 'probability', label: 'Prob %', sortField: 'probability' },
                          { key: 'weighted_amount', label: 'Weighted', sortField: 'weighted_amount' },
                        ].map(col => (
                          <th key={col.key} className={`hs-th-resizable hs-th-sortable${col.frozen ? ' hs-th-frozen' : ''}`} style={col.frozen ? { position: 'sticky', left: 40, top: 0, zIndex: 15, background: '#f8fafc' } : undefined} onClick={() => toggleFcSort(col.sortField)}>
                            <span className="hs-th-content">
                              {col.label}
                              {forecastSort.field === col.sortField && <span className="hs-sort-arrow">{forecastSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                              {forecastSort.field !== col.sortField && <span className="hs-th-hover-sort"><svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M29.38 21.96a1.147 1.147 0 0 0-1.617 0l-3.85 3.852V5.714a1.143 1.143 0 0 0-2.286 0v20.099l-3.853-3.852a1.144 1.144 0 0 0-1.615 1.617l5.803 5.802a1.143 1.143 0 0 0 1.616 0l5.802-5.802a1.144 1.144 0 0 0 0-1.617M15.84 8.422 10.039 2.62a1.145 1.145 0 0 0-1.616 0L2.62 8.422a1.144 1.144 0 0 0 1.617 1.617l3.85-3.851v20.098a1.143 1.143 0 0 0 2.286 0V6.187l3.853 3.852a1.14 1.14 0 0 0 1.616-.001 1.14 1.14 0 0 0-.001-1.616"/></svg></span>}
                            </span>
                            <div className="hs-col-resize-handle" onClick={(e) => e.stopPropagation()} />
                          </th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {fcPaged.map((d: any) => (
                          <tr key={d.id} className={`hs-row${forecastSelectedRows.has(d.id) ? ' selected' : ''}`}>
                            <td className="hs-td-check"><input type="checkbox" checked={forecastSelectedRows.has(d.id)} onChange={() => { setForecastSelectedRows(prev => { const n = new Set(prev); n.has(d.id) ? n.delete(d.id) : n.add(d.id); return n }) }} /></td>
                            <td className="hs-td-name" style={{ position: 'sticky', left: 40, zIndex: 2, background: forecastSelectedRows.has(d.id) ? '#eff6ff' : '#fff' }}>
                              <div className="hs-name-cell">
                                <span className="hs-name-link" style={{ fontWeight: 500, color: '#1976d2' }}>{d.title}</span>
                                <button className="hs-preview-btn" onClick={(e) => { e.stopPropagation() }}>Preview</button>
                              </div>
                            </td>
                            <td style={{ fontSize: '0.8rem', color: '#516f90' }}>{d.close_date ? formatDatePref(d.close_date) : '--'}</td>
                            <td style={{ fontWeight: 500 }}>{currencyFormatter.format(d.opportunity)}</td>
                            <td><span className="hs-status-badge" style={{ background: '#e3f2fd', color: '#1565c0' }}>{d.stage_label}</span></td>
                            <td><span className="hs-status-badge" style={{ background: d.forecast_category === 'closed_won' ? '#f0fdf4' : d.forecast_category === 'commit' ? '#eff6ff' : d.forecast_category === 'best_case' ? '#fff7ed' : d.forecast_category === 'closed_lost' ? '#fef2f2' : '#f8fafc', color: d.forecast_category === 'closed_won' ? '#166534' : d.forecast_category === 'commit' ? '#2563eb' : d.forecast_category === 'best_case' ? '#92400e' : d.forecast_category === 'closed_lost' ? '#dc2626' : '#64748b' }}>{(d.forecast_category || '').replace(/_/g, ' ')}</span></td>
                            <td style={{ fontSize: '0.8rem' }}>{d.owner || '--'}</td>
                            <td style={{ fontSize: '0.8rem' }}>{d.probability}%</td>
                            <td style={{ fontWeight: 500 }}>{currencyFormatter.format(d.weighted_amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Row 6: Pagination */}
                <div className="hs-pagination">
                  <nav className="hs-paginator" aria-label="Pagination">
                    <button className="hs-paginator-btn" disabled={fcCurrentPage <= 1} onClick={() => setForecastPage(p => p - 1)} aria-label="Previous page">
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95"/></svg>
                      <span>Prev</span>
                    </button>
                    {(() => {
                      if (fcTotalPages <= 7) {
                        const pages: number[] = []; for (let i = 1; i <= fcTotalPages; i++) pages.push(i)
                        return pages.map(p => <button key={p} className={`hs-paginator-num${p === fcCurrentPage ? ' active' : ''}`} onClick={() => setForecastPage(p)}>{p}</button>)
                      }
                      const items: React.ReactNode[] = []
                      const addPage = (p: number) => items.push(<button key={p} className={`hs-paginator-num${p === fcCurrentPage ? ' active' : ''}`} onClick={() => setForecastPage(p)}>{p}</button>)
                      const addEllipsis = (key: string) => items.push(<span key={key} className="hs-paginator-ellipsis">...</span>)
                      addPage(1)
                      if (fcCurrentPage > 3) addEllipsis('el')
                      const start = Math.max(2, fcCurrentPage - 1), end = Math.min(fcTotalPages - 1, fcCurrentPage + 1)
                      for (let i = start; i <= end; i++) addPage(i)
                      if (fcCurrentPage < fcTotalPages - 2) addEllipsis('er')
                      addPage(fcTotalPages)
                      return items
                    })()}
                    <button className="hs-paginator-btn" disabled={fcCurrentPage >= fcTotalPages} onClick={() => setForecastPage(p => p + 1)} aria-label="Next page">
                      <span>Next</span>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M11 27.71c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62l9.48-9.48-9.48-9.47a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0L22.1 15.48c.45.45.45 1.17 0 1.62L11.81 27.39c-.22.22-.52.33-.81.33Z"/></svg>
                    </button>
                  </nav>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', marginLeft: 'auto' }}><select value={tablePageSize} onChange={e => { setTablePageSize(Number(e.target.value) as any); setForecastPage(1) }} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #e2e8f0', fontSize: '0.82rem' }}><option value={100}>100</option><option value={200}>200</option><option value={500}>500</option></select><span>per page</span></div>
                </div>
              </div>

              {/* Settings panel */}
              {tableSettingsOpen && (
                <div className="hs-settings-panel">
                  <div className="hs-settings-header">
                    <span className="hs-settings-title">Table settings</span>
                    <button className="hs-settings-close" onClick={() => setTableSettingsOpen(false)}>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                    </button>
                  </div>
                  <div className="hs-settings-body">
                    <div className="hs-settings-group">
                      <label className="hs-settings-label">Pagination <span className="hs-info-icon" title="Number of rows shown per page"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span></label>
                      {([100, 200, 500] as const).map(size => (
                        <label className="hs-settings-radio" key={size}><input type="radio" name="fcPageSize" checked={tablePageSize === size} onChange={() => { setTablePageSize(size); setForecastPage(1) }} /><span className="hs-radio-indicator" /><span>{size} per page</span></label>
                      ))}
                    </div>
                    <hr className="hs-settings-divider" />
                    <div className="hs-settings-group">
                      <label className="hs-settings-label">Row height <span className="hs-info-icon" title="Adjust the vertical spacing of table rows"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span></label>
                      {[{ value: 'small' as const, label: 'Compact' }, { value: 'medium' as const, label: 'Default' }, { value: 'large' as const, label: 'Comfortable' }].map(opt => (
                        <label className="hs-settings-radio" key={opt.value}><input type="radio" name="fcRowHeight" checked={tableRowHeight === opt.value} onChange={() => setTableRowHeight(opt.value)} /><span className="hs-radio-indicator" /><span>{opt.label}</span></label>
                      ))}
                    </div>
                    <hr className="hs-settings-divider" />
                    <div className="hs-settings-group">
                      <div className="hs-settings-toggle-row">
                        <label className="hs-settings-label">Zebra striping <span className="hs-info-icon" title="Alternate row background colors for easier reading"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span></label>
                        <button className={`hs-toggle${tableZebraStriping ? ' on' : ''}`} onClick={() => setTableZebraStriping(!tableZebraStriping)} role="switch" aria-checked={tableZebraStriping}>
                          <span className="hs-toggle-track"><span className="hs-toggle-on">ON</span><span className="hs-toggle-off">OFF</span><span className="hs-toggle-handle" /></span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Goal modal */}
            {showGoalModal && (<div className="modal-overlay" onClick={() => setShowGoalModal(false)}><div className="modal-card" style={{ maxWidth: 400 }} onClick={(e) => e.stopPropagation()}><button type="button" className="modal-close" onClick={() => setShowGoalModal(false)}>&times;</button><div className="modal-body"><h3 className="modal-title">Set Revenue Goal</h3><p style={{ fontSize: '0.82rem', color: '#64748b', marginBottom: 16 }}>{forecastPipeline} - {new Date(forecastMonth + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p><div style={{ marginBottom: 16 }}><label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 500, color: '#33475b', marginBottom: 4 }}>Goal Amount ($)</label><input type="number" value={goalAmount} onChange={(e) => setGoalAmount(e.target.value)} placeholder="0.00" style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem' }} /></div><button className="hs-add-contacts-btn" onClick={() => { const [y, m] = forecastMonth.split('-').map(Number); const ld = new Date(y, m, 0).getDate(); saveForecastGoal({ user_id: 1, pipeline_name: forecastPipeline, period_type: forecastPeriod, period_start: `${forecastMonth}-01`, period_end: `${forecastMonth}-${String(ld).padStart(2, '0')}`, goal_amount: parseFloat(goalAmount) || 0 }).then(() => { setShowGoalModal(false); setForecastLoading(true); fetchForecasts({ pipeline: forecastPipeline, period_start: `${forecastMonth}-01`, period_end: `${forecastMonth}-${String(ld).padStart(2, '0')}` }).then(setForecastData).finally(() => setForecastLoading(false)) }).catch(console.error) }}>Save Goal</button></div></div></div>)}
          </div>
          )
        })()}

        {/* ========== Payments View ========== */}
        {activeView === 'payments' && (() => {
          const pmIsLinks = paymentsSubView === 'links'
          const pmRaw = pmIsLinks ? paymentLinksData : paymentsData
          const pmSearched = pmRaw.filter((r: any) => {
            if (!paymentsSearch) return true
            const s = paymentsSearch.toLowerCase()
            if (pmIsLinks) return (r.name || '').toLowerCase().includes(s)
            return (r.client_name || r.link_name || '').toLowerCase().includes(s) || (r.payment_number || '').toLowerCase().includes(s)
          })
          const pmSorted = [...pmSearched].sort((a: any, b: any) => {
            const f = paymentSort.field
            const va = f === 'amount' || f === 'total_amount' || f === 'total_revenue' || f === 'total_views' || f === 'total_payments' ? (a[f] || 0) : String(a[f] || '')
            const vb = f === 'amount' || f === 'total_amount' || f === 'total_revenue' || f === 'total_views' || f === 'total_payments' ? (b[f] || 0) : String(b[f] || '')
            if (va < vb) return paymentSort.order === 'ASC' ? -1 : 1
            if (va > vb) return paymentSort.order === 'ASC' ? 1 : -1
            return 0
          })
          const pmTotal = pmSorted.length
          const pmTotalPages = Math.max(1, Math.ceil(pmTotal / tablePageSize))
          const pmCurrentPage = Math.min(paymentPage, pmTotalPages)
          const pmPaged = pmSorted.slice((pmCurrentPage - 1) * tablePageSize, pmCurrentPage * tablePageSize)
          const pmLoading = pmIsLinks ? paymentLinksLoading : paymentsLoading
          const toggleAllPaymentRows = () => {
            if (paymentSelectedRows.size === pmPaged.length) setPaymentSelectedRows(new Set())
            else setPaymentSelectedRows(new Set(pmPaged.map((r: any) => r.id)))
          }
          const togglePmSort = (field: string) => {
            setPaymentSort(prev => ({ field, order: prev.field === field && prev.order === 'ASC' ? 'DESC' : 'ASC' }))
            setPaymentPage(1)
          }
          const linkCols = [
            { key: 'name', label: 'Name', sortField: 'name', frozen: true },
            { key: 'total_amount', label: 'Amount', sortField: 'total_amount' },
            { key: 'link_type', label: 'Type', sortField: 'link_type' },
            { key: 'status', label: 'Status', sortField: 'status' },
            { key: 'total_views', label: 'Views', sortField: 'total_views' },
            { key: 'total_payments', label: 'Payments', sortField: 'total_payments' },
            { key: 'total_revenue', label: 'Revenue', sortField: 'total_revenue' },
            { key: 'created_at', label: 'Created', sortField: 'created_at' },
          ]
          const historyCols = [
            { key: 'payment_number', label: 'Payment #', sortField: 'payment_number', frozen: true },
            { key: 'client_name', label: 'Contact', sortField: 'client_name' },
            { key: 'amount', label: 'Amount', sortField: 'amount' },
            { key: 'payment_method', label: 'Method', sortField: 'payment_method' },
            { key: 'status', label: 'Status', sortField: 'status' },
            { key: 'payment_date', label: 'Date', sortField: 'payment_date' },
            { key: 'reference_number', label: 'Reference', sortField: 'reference_number' },
          ]
          const cols = pmIsLinks ? linkCols : historyCols
          const colCount = cols.length + 1
          return (
          <div className="hs-contacts">
            {/* Row 1: Topbar */}
            <div className="hs-topbar">
              <div className="hs-topbar-left">
                <div className="hs-obj-switcher-wrap" ref={objSwitcherRef}>
                  <button className={`hs-obj-switcher${objSwitcherOpen ? ' open' : ''}`} onClick={() => { setObjSwitcherOpen(!objSwitcherOpen); setObjSwitcherSearch('') }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="1" y="4" width="22" height="16" rx="2"/><path d="M1 10h22"/></svg>
                    <span>Payments</span>
                    <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                  </button>
                  {objSwitcherOpen && (
                    <div className="hs-obj-panel">
                      <div className="hs-obj-search-wrap">
                        <svg className="hs-obj-search-icon" width="13" height="13" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                        <input className="hs-obj-search" placeholder="Search" value={objSwitcherSearch} onChange={(e) => setObjSwitcherSearch(e.target.value)} autoFocus />
                      </div>
                      <ul className="hs-obj-list" role="listbox">
                        {[
                          { label: 'Prospecting', view: 'prospecting' as ViewMode },
                          { label: 'Deals', view: 'deals' as ViewMode },
                          { label: 'Forecasts', view: 'forecasts' as ViewMode },
                          { label: 'Target Accounts', view: 'target-accounts' as ViewMode },
                          { label: 'Activity Feed', view: 'activity-feed' as ViewMode },
                          { label: 'Documents', view: 'sales-documents' as ViewMode },
                          { label: 'Meetings', view: 'meetings-scheduler' as ViewMode },
                          { label: 'Coaching Playlists', view: 'coaching-playlists' as ViewMode },
                          { label: 'Payments', view: 'payments' as ViewMode },
                          { label: 'Quotes', view: 'quotes' as ViewMode },
                          { label: 'Products', view: 'products' as ViewMode },
                        ].filter(item => !objSwitcherSearch || item.label.toLowerCase().includes(objSwitcherSearch.toLowerCase())).map(item => (
                          <li key={item.view} className={`hs-obj-item${item.view === activeView ? ' active' : ''}`} role="option" aria-selected={item.view === activeView} onClick={() => { setActiveView(item.view); setObjSwitcherOpen(false); setObjSwitcherSearch('') }}>{item.label}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="hs-topbar-sep" />
                {/* View tabs */}
                <div className="hs-view-tabs">
                  <div className={`hs-view-tab${paymentsSubView === 'links' ? ' active' : ''}`} onClick={() => { setPaymentsSubView('links'); setPaymentPage(1); setPaymentSelectedRows(new Set()) }}>
                    <span className="hs-tab-label">Payment Links</span>
                    {paymentsSubView === 'links' && paymentLinksTotal > 0 && <span className="hs-tab-count">{paymentLinksTotal}</span>}
                  </div>
                  <div className={`hs-view-tab${paymentsSubView === 'history' ? ' active' : ''}`} onClick={() => { setPaymentsSubView('history'); setPaymentPage(1); setPaymentSelectedRows(new Set()) }}>
                    <span className="hs-tab-label">Payment History</span>
                    {paymentsSubView === 'history' && paymentsTotal > 0 && <span className="hs-tab-count">{paymentsTotal}</span>}
                    {paymentsSubView === 'history' && <span className="hs-tab-x" onClick={(e) => { e.stopPropagation(); setPaymentsSubView('links'); setPaymentPage(1); setPaymentSelectedRows(new Set()) }}>&times;</span>}
                  </div>
                </div>
              </div>
              <div className="hs-topbar-right">
                <div className="hs-add-btn-wrap">
                  <button className="hs-add-contacts-btn" onClick={() => { setNewLinkName(''); setNewLinkDesc(''); setNewLinkItems([{ name: '', quantity: 1, unit_price: 0 }]); setShowCreatePaymentLink(true) }}>
                    Create payment link
                  </button>
                </div>
              </div>
            </div>

            {/* Row 2: Toolbar */}
            <div className="hs-toolbar">
              <div className="hs-toolbar-left">
                <div className="hs-search-wrap">
                  <svg className="hs-search-icon" width="14" height="14" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M28.14 29.29c-.29 0-.58-.11-.81-.33l-7.58-7.58a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l7.58 7.58a1.14 1.14 0 0 1-.81 1.95"/><path d="M13.29 24.71c-6.3 0-11.43-5.13-11.43-11.43S6.98 1.86 13.29 1.86s11.43 5.13 11.43 11.43-5.13 11.43-11.43 11.43Zm0-20.57c-5.04 0-9.14 4.1-9.14 9.14s4.1 9.14 9.14 9.14 9.14-4.1 9.14-9.14-4.1-9.14-9.14-9.14"/></svg>
                  <input className="hs-search-input" placeholder={pmIsLinks ? 'Search payment links...' : 'Search payments...'} value={paymentsSearch} onChange={(e) => { setPaymentsSearch(e.target.value); setPaymentPage(1) }} />
                  {paymentsSearch && <button className="hs-search-clear" onClick={() => { setPaymentsSearch(''); setPaymentPage(1) }}>&times;</button>}
                </div>
                <div className="hs-tb-sep" />
                <div className="hs-viewtype-group">
                  <div className="hs-viewtype-wrap">
                    <button className="hs-tb-btn hs-viewtype-btn">
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                      <span>Table</span>
                    </button>
                  </div>
                  <button className={`hs-tb-btn hs-settings-btn${tableSettingsOpen ? ' active' : ''}`} onClick={() => setTableSettingsOpen(!tableSettingsOpen)} title={tableSettingsOpen ? 'Close settings' : 'Table settings'}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                  </button>
                </div>
                <div className="hs-tb-sep" />
                <button className={`hs-tb-btn hs-tb-btn-bordered${paymentQuickFiltersOpen ? ' hs-tb-btn-pressed' : ''}`} title="Filters" onClick={() => setPaymentQuickFiltersOpen(!paymentQuickFiltersOpen)}>
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M30.71 16.86H13.57a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28M6.71 16.86H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M10.14 20.29c-2.52 0-4.57-2.05-4.57-4.57s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29"/></svg>
                  <span>Filters</span>
                </button>
                <div className="hs-tb-sep" />
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Export">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  <span>Export</span>
                </button>
              </div>
              <div className="hs-toolbar-right">
                <div className="hs-tb-sep" />
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Clone view">
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M25.86 29.29H12.15c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-1.89 1.54-3.43 3.43-3.43h13.71c1.89 0 3.43 1.54 3.43 3.43v13.71c0 1.89-1.54 3.43-3.43 3.43M12.14 11c-.63 0-1.14.51-1.14 1.14v13.71c0 .63.51 1.14 1.14 1.14h13.71c.63 0 1.14-.51 1.14-1.14V12.14c0-.63-.51-1.14-1.14-1.14z"/><path d="M5.29 22.43c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43H19c1.89 0 3.43 1.54 3.43 3.43a1.14 1.14 0 1 1-2.28 0c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14V19c0 .63.51 1.14 1.14 1.14a1.14 1.14 0 1 1 0 2.28Z"/></svg>
                </button>
                <button className="hs-tb-btn hs-tb-btn-bordered disabled" disabled title="Save view">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                </button>
              </div>
            </div>

            {/* Row 3: Quick Filters */}
            {paymentQuickFiltersOpen && <div className="hs-quick-filters">
              <button className={`hs-qf-btn hs-qf-trigger${paymentSort.field !== 'created_at' ? ' has-value' : ''}`} onClick={() => {
                const fields = pmIsLinks ? ['created_at', 'name', 'total_amount', 'total_revenue', 'status'] : ['created_at', 'payment_number', 'amount', 'status', 'payment_date']
                const idx = fields.indexOf(paymentSort.field)
                const next = fields[(idx + 1) % fields.length]
                setPaymentSort({ field: next, order: next === 'name' || next === 'payment_number' ? 'ASC' : 'DESC' })
                setPaymentPage(1)
              }}>
                <span>Sort: {{ created_at: 'Created', name: 'Name', total_amount: 'Amount', total_revenue: 'Revenue', status: 'Status', payment_number: 'Payment #', amount: 'Amount', payment_date: 'Date' }[paymentSort.field] || 'Created'}</span>
                <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
              </button>
              <div className="hs-qf-sep" />
              <div className="hs-qf-more-wrap">
                <button className="hs-qf-btn hs-qf-more">
                  <svg width="10" height="10" viewBox="0 0 32 32" fill="currentColor"><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V3a1.14 1.14 0 1 1 2.28 0v25.14c0 .63-.51 1.14-1.14 1.14Z"/><path d="M28.14 16.71H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28"/></svg>
                  <span>More</span>
                </button>
              </div>
            </div>}

            <div className={`hs-content${tableSettingsOpen ? ' with-settings' : ''}`}>
              <div className="hs-table-area">
                {/* Row 4: Selection bar */}
                {paymentSelectedRows.size > 0 && (
                  <div className="hs-selection-bar">
                    <span className="hs-sel-count">{paymentSelectedRows.size} selected</span>
                    <button className="hs-sel-close" onClick={() => setPaymentSelectedRows(new Set())} title="Deselect all">&times;</button>
                  </div>
                )}

                {/* Loading bar */}
                {pmLoading && pmRaw.length > 0 && <div className="hs-loading-bar" />}

                {/* Row 5: Data Table */}
                <div className="hs-table-scroll">
                  {pmLoading && pmRaw.length === 0 ? (
                    <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                      <colgroup><col style={{ width: 40 }} />{cols.map(c => <col key={c.key} style={{ width: c.frozen ? 200 : 120 }} />)}</colgroup>
                      <thead><tr>
                        <th className="hs-th-check"><input type="checkbox" disabled /></th>
                        {cols.map(c => <th key={c.key} className={`hs-th-resizable hs-th-sortable${c.frozen ? ' hs-th-frozen' : ''}`} style={c.frozen ? { position: 'sticky', left: 40, top: 0, zIndex: 15, background: '#f8fafc' } : undefined}><span className="hs-th-content">{c.label}</span><div className="hs-col-resize-handle" /></th>)}
                      </tr></thead>
                      <tbody><tr><td colSpan={colCount} className="hs-loading-cell"><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}><DotLottieReact src="/loading.lottie" loop autoplay style={{ width: 80, height: 80 }} /><span style={{ fontSize: '0.82rem', color: '#7c98b6', marginTop: 8 }}>Loading {pmIsLinks ? 'payment links' : 'payments'}...</span></div></td></tr></tbody>
                    </table>
                  ) : pmPaged.length === 0 ? (
                    <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                      <colgroup><col style={{ width: 40 }} />{cols.map(c => <col key={c.key} style={{ width: c.frozen ? 200 : 120 }} />)}</colgroup>
                      <thead><tr>
                        <th className="hs-th-check"><input type="checkbox" disabled /></th>
                        {cols.map(c => <th key={c.key} className="hs-th-resizable" style={c.frozen ? { position: 'sticky', left: 40, zIndex: 2, background: '#f5f8fa' } : undefined}><span>{c.label}</span></th>)}
                      </tr></thead>
                      <tbody><tr><td colSpan={colCount} className="hs-empty-cell">
                        <div style={{ padding: '40px', textAlign: 'center' }}>
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '12px' }}><rect x="1" y="4" width="22" height="16" rx="2"/><path d="M1 10h22"/></svg>
                          <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#33475b', marginBottom: '6px' }}>{paymentsSearch ? `No matching ${pmIsLinks ? 'payment links' : 'payments'}` : pmIsLinks ? 'Accept payments online' : 'No payments yet'}</div>
                          <div style={{ fontSize: '0.82rem', color: '#7c98b6', maxWidth: '360px', margin: '0 auto' }}>{paymentsSearch ? 'Try a different search term.' : pmIsLinks ? 'Create payment links to collect one-time or recurring payments from your contacts.' : 'Payments will appear here once contacts make payments.'}</div>
                        </div>
                      </td></tr></tbody>
                    </table>
                  ) : (
                    <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                      <colgroup><col style={{ width: 40 }} />{cols.map(c => <col key={c.key} style={{ width: c.frozen ? 200 : 120 }} />)}</colgroup>
                      <thead><tr>
                        <th className="hs-th-check"><input type="checkbox" checked={pmPaged.length > 0 && paymentSelectedRows.size === pmPaged.length} onChange={toggleAllPaymentRows} /></th>
                        {cols.map(col => (
                          <th key={col.key} className={`hs-th-resizable hs-th-sortable${col.frozen ? ' hs-th-frozen' : ''}`} style={col.frozen ? { position: 'sticky', left: 40, top: 0, zIndex: 15, background: '#f8fafc' } : undefined} onClick={() => togglePmSort(col.sortField)}>
                            <span className="hs-th-content">
                              {col.label}
                              {paymentSort.field === col.sortField && <span className="hs-sort-arrow">{paymentSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                              {paymentSort.field !== col.sortField && <span className="hs-th-hover-sort"><svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M29.38 21.96a1.147 1.147 0 0 0-1.617 0l-3.85 3.852V5.714a1.143 1.143 0 0 0-2.286 0v20.099l-3.853-3.852a1.144 1.144 0 0 0-1.615 1.617l5.803 5.802a1.143 1.143 0 0 0 1.616 0l5.802-5.802a1.144 1.144 0 0 0 0-1.617M15.84 8.422 10.039 2.62a1.145 1.145 0 0 0-1.616 0L2.62 8.422a1.144 1.144 0 0 0 1.617 1.617l3.85-3.851v20.098a1.143 1.143 0 0 0 2.286 0V6.187l3.853 3.852a1.14 1.14 0 0 0 1.616-.001 1.14 1.14 0 0 0-.001-1.616"/></svg></span>}
                            </span>
                            <div className="hs-col-resize-handle" onClick={(e) => e.stopPropagation()} />
                          </th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {pmIsLinks ? pmPaged.map((l: any) => (
                          <tr key={l.id} className={`hs-row${paymentSelectedRows.has(l.id) ? ' selected' : ''}`}>
                            <td className="hs-td-check"><input type="checkbox" checked={paymentSelectedRows.has(l.id)} onChange={() => { setPaymentSelectedRows(prev => { const n = new Set(prev); n.has(l.id) ? n.delete(l.id) : n.add(l.id); return n }) }} /></td>
                            <td className="hs-td-name" style={{ position: 'sticky', left: 40, zIndex: 2, background: paymentSelectedRows.has(l.id) ? '#eff6ff' : '#fff' }}><div className="hs-name-cell"><span className="hs-name-link" style={{ fontWeight: 500, color: '#33475b' }}>{l.name}</span><button className="hs-preview-btn" onClick={(e) => { e.stopPropagation() }}>Preview</button></div></td>
                            <td style={{ fontWeight: 500 }}>{currencyFormatter.format(l.total_amount)}</td>
                            <td><span className="hs-status-badge" style={{ background: l.link_type === 'recurring' ? '#eff6ff' : '#f0fdf4', color: l.link_type === 'recurring' ? '#2563eb' : '#166534' }}>{l.link_type === 'recurring' ? 'Recurring' : 'One-time'}</span></td>
                            <td><span className="hs-status-badge" style={{ background: l.status === 'active' ? '#f0fdf4' : '#f1f5f9', color: l.status === 'active' ? '#166534' : '#64748b' }}>{l.status}</span></td>
                            <td style={{ color: '#516f90' }}>{l.total_views}</td>
                            <td style={{ color: '#516f90' }}>{l.total_payments}</td>
                            <td style={{ fontWeight: 500, color: '#166534' }}>{currencyFormatter.format(l.total_revenue)}</td>
                            <td style={{ color: '#516f90', fontSize: 13 }}>{l.created_at ? formatDatePref(l.created_at) : ''}</td>
                          </tr>
                        )) : pmPaged.map((p: any) => (
                          <tr key={p.id} className={`hs-row${paymentSelectedRows.has(p.id) ? ' selected' : ''}`}>
                            <td className="hs-td-check"><input type="checkbox" checked={paymentSelectedRows.has(p.id)} onChange={() => { setPaymentSelectedRows(prev => { const n = new Set(prev); n.has(p.id) ? n.delete(p.id) : n.add(p.id); return n }) }} /></td>
                            <td className="hs-td-name" style={{ position: 'sticky', left: 40, zIndex: 2, background: paymentSelectedRows.has(p.id) ? '#eff6ff' : '#fff' }}><div className="hs-name-cell"><span className="hs-name-link" style={{ fontWeight: 500, fontFamily: 'monospace', color: '#33475b' }}>{p.payment_number}</span><button className="hs-preview-btn" onClick={(e) => { e.stopPropagation() }}>Preview</button></div></td>
                            <td>{p.client_name || p.link_name || '--'}</td>
                            <td style={{ fontWeight: 500, color: '#166534' }}>{currencyFormatter.format(p.amount)}</td>
                            <td style={{ color: '#516f90', textTransform: 'capitalize' }}>{(p.payment_method || '').replace('_', ' ')}</td>
                            <td><span className="hs-status-badge" style={{ background: p.status === 'completed' ? '#f0fdf4' : '#fef3c7', color: p.status === 'completed' ? '#166534' : '#92400e' }}>{p.status}</span></td>
                            <td style={{ color: '#516f90', fontSize: 13 }}>{p.payment_date || '--'}</td>
                            <td style={{ color: '#516f90', fontSize: 13 }}>{p.reference_number || '--'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Row 6: Pagination */}
                <div className="hs-pagination">
                  <nav className="hs-paginator" aria-label="Pagination">
                    <button className="hs-paginator-btn" disabled={pmCurrentPage <= 1} onClick={() => setPaymentPage(p => p - 1)} aria-label="Previous page">
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95"/></svg>
                      <span>Prev</span>
                    </button>
                    {(() => {
                      if (pmTotalPages <= 7) {
                        const pages: number[] = []; for (let i = 1; i <= pmTotalPages; i++) pages.push(i)
                        return pages.map(p => <button key={p} className={`hs-paginator-num${p === pmCurrentPage ? ' active' : ''}`} onClick={() => setPaymentPage(p)}>{p}</button>)
                      }
                      const items: React.ReactNode[] = []
                      const addPage = (p: number) => items.push(<button key={p} className={`hs-paginator-num${p === pmCurrentPage ? ' active' : ''}`} onClick={() => setPaymentPage(p)}>{p}</button>)
                      const addEllipsis = (key: string) => items.push(<span key={key} className="hs-paginator-ellipsis">...</span>)
                      addPage(1)
                      if (pmCurrentPage > 3) addEllipsis('el')
                      const start = Math.max(2, pmCurrentPage - 1), end = Math.min(pmTotalPages - 1, pmCurrentPage + 1)
                      for (let i = start; i <= end; i++) addPage(i)
                      if (pmCurrentPage < pmTotalPages - 2) addEllipsis('er')
                      addPage(pmTotalPages)
                      return items
                    })()}
                    <button className="hs-paginator-btn" disabled={pmCurrentPage >= pmTotalPages} onClick={() => setPaymentPage(p => p + 1)} aria-label="Next page">
                      <span>Next</span>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M11 27.71c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62l9.48-9.48-9.48-9.47a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0L22.1 15.48c.45.45.45 1.17 0 1.62L11.81 27.39c-.22.22-.52.33-.81.33Z"/></svg>
                    </button>
                  </nav>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', marginLeft: 'auto' }}><select value={tablePageSize} onChange={e => { setTablePageSize(Number(e.target.value) as any); setPaymentPage(1) }} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #e2e8f0', fontSize: '0.82rem' }}><option value={100}>100</option><option value={200}>200</option><option value={500}>500</option></select><span>per page</span></div>
                </div>
              </div>

              {/* Settings panel */}
              {tableSettingsOpen && (
                <div className="hs-settings-panel">
                  <div className="hs-settings-header">
                    <span className="hs-settings-title">Table settings</span>
                    <button className="hs-settings-close" onClick={() => setTableSettingsOpen(false)}>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                    </button>
                  </div>
                  <div className="hs-settings-body">
                    <div className="hs-settings-group">
                      <label className="hs-settings-label">Pagination <span className="hs-info-icon" title="Number of rows shown per page"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span></label>
                      {([100, 200, 500] as const).map(size => (
                        <label className="hs-settings-radio" key={size}><input type="radio" name="pmPageSize" checked={tablePageSize === size} onChange={() => { setTablePageSize(size); setPaymentPage(1) }} /><span className="hs-radio-indicator" /><span>{size} per page</span></label>
                      ))}
                    </div>
                    <hr className="hs-settings-divider" />
                    <div className="hs-settings-group">
                      <label className="hs-settings-label">Row height <span className="hs-info-icon" title="Adjust the vertical spacing of table rows"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span></label>
                      {[{ value: 'small' as const, label: 'Compact' }, { value: 'medium' as const, label: 'Default' }, { value: 'large' as const, label: 'Comfortable' }].map(opt => (
                        <label className="hs-settings-radio" key={opt.value}><input type="radio" name="pmRowHeight" checked={tableRowHeight === opt.value} onChange={() => setTableRowHeight(opt.value)} /><span className="hs-radio-indicator" /><span>{opt.label}</span></label>
                      ))}
                    </div>
                    <hr className="hs-settings-divider" />
                    <div className="hs-settings-group">
                      <div className="hs-settings-toggle-row">
                        <label className="hs-settings-label">Zebra striping <span className="hs-info-icon" title="Alternate row background colors for easier reading"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span></label>
                        <button className={`hs-toggle${tableZebraStriping ? ' on' : ''}`} onClick={() => setTableZebraStriping(!tableZebraStriping)} role="switch" aria-checked={tableZebraStriping}>
                          <span className="hs-toggle-track"><span className="hs-toggle-on">ON</span><span className="hs-toggle-off">OFF</span><span className="hs-toggle-handle" /></span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Create payment link slide-over */}
            {showCreatePaymentLink && (<div className="modal-overlay" onClick={() => setShowCreatePaymentLink(false)}><div style={{ width: 480, position: 'fixed', right: 0, top: 0, bottom: 0, background: '#fff', zIndex: 201, boxShadow: '-4px 0 24px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', animation: 'slideInRight 250ms ease' }} onClick={(e) => e.stopPropagation()}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><h2 style={{ fontSize: '0.84rem', fontWeight: 700 }}>Create Payment Link</h2><button onClick={() => setShowCreatePaymentLink(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#94a3b8' }}>&times;</button></div>
              <div style={{ flex: 1, overflow: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div><label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 500, color: '#33475b', marginBottom: 4 }}>Link Name *</label><input value={newLinkName} onChange={(e) => setNewLinkName(e.target.value)} style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem' }} placeholder="e.g. Exterior Painting Deposit" /></div>
                <div><label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 500, color: '#33475b', marginBottom: 4 }}>Description</label><textarea value={newLinkDesc} onChange={(e) => setNewLinkDesc(e.target.value)} rows={2} style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem', resize: 'vertical' }} /></div>
                <div><label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 500, color: '#33475b', marginBottom: 8 }}>Line Items</label>
                  {newLinkItems.map((item, idx) => (<div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 60px 90px 30px', gap: 8, marginBottom: 8 }}><input placeholder="Item name" value={item.name} onChange={(e) => { const items = [...newLinkItems]; items[idx] = { ...items[idx], name: e.target.value }; setNewLinkItems(items) }} style={{ padding: '6px 8px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.82rem' }} /><input type="number" value={item.quantity} onChange={(e) => { const items = [...newLinkItems]; items[idx] = { ...items[idx], quantity: parseInt(e.target.value) || 1 }; setNewLinkItems(items) }} style={{ padding: '6px 8px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.82rem' }} /><input type="number" value={item.unit_price || ''} onChange={(e) => { const items = [...newLinkItems]; items[idx] = { ...items[idx], unit_price: parseFloat(e.target.value) || 0 }; setNewLinkItems(items) }} style={{ padding: '6px 8px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.82rem' }} /><button onClick={() => setNewLinkItems(newLinkItems.filter((_, i) => i !== idx))} style={{ background: 'none', border: 'none', color: '#d94c53', cursor: 'pointer' }}>&times;</button></div>))}
                  <button onClick={() => setNewLinkItems([...newLinkItems, { name: '', quantity: 1, unit_price: 0 }])} style={{ background: 'none', border: 'none', color: '#1976d2', fontSize: '0.78rem', cursor: 'pointer' }}>+ Add line item</button>
                  <div style={{ marginTop: 8, textAlign: 'right', fontSize: '0.82rem', fontWeight: 600 }}>Total: {currencyFormatter.format(newLinkItems.reduce((s, i) => s + i.quantity * i.unit_price, 0))}</div>
                </div>
              </div>
              <div style={{ padding: '12px 20px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 8 }}><button onClick={() => setShowCreatePaymentLink(false)} style={{ padding: '9px 16px', border: '1px solid #cbd6e2', borderRadius: 4, background: '#fff', color: '#33475b', fontSize: '0.85rem', cursor: 'pointer' }}>Cancel</button><button disabled={!newLinkName.trim()} onClick={() => { createPaymentLink({ name: newLinkName, description: newLinkDesc, line_items: newLinkItems.filter(i => i.name.trim()) }).then(() => { setShowCreatePaymentLink(false); fetchPaymentLinks({}).then((r) => { setPaymentLinksData(r.data); setPaymentLinksTotal(r.total) }) }).catch((e) => alert(e.message)) }} style={{ padding: '9px 20px', border: 'none', borderRadius: 4, background: '#ff7a59', color: '#fff', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', opacity: newLinkName.trim() ? 1 : 0.5 }}>Create link</button></div>
            </div></div>)}
          </div>
          )
        })()}

        {/* ========== Knowledge Base View ========== */}
        {/* bp-devws-v1: Delivery (dev-workspace) view mount — lazy chunk under Suspense */}
        {activeView === 'delivery' && (
          <Suspense fallback={<div className="view-loading" />}>
            <DeliveryView currentUser={currentUser} />
          </Suspense>
        )}
        {activeView === 'knowledge-base' && !showKbEditor && (() => {
          const kbTotalPages = Math.max(1, Math.ceil(kbArticlesTotal / tablePageSize))
          const kbCurrentPage = Math.min(kbPage, kbTotalPages)
          const toggleAllKbRows = () => {
            if (kbSelectedRows.size === kbArticles.length) {
              setKbSelectedRows(new Set())
            } else {
              setKbSelectedRows(new Set(kbArticles.map((a: any) => a.id)))
            }
          }
          return (
          <div className="hs-contacts">
            {/* Row 1: Topbar */}
            <div className="hs-topbar">
              <div className="hs-topbar-left">
                {/* Object switcher */}
                <div className="hs-obj-switcher-wrap" ref={objSwitcherRef}>
                  <button className={`hs-obj-switcher${objSwitcherOpen ? ' open' : ''}`} onClick={() => { setObjSwitcherOpen(!objSwitcherOpen); setObjSwitcherSearch('') }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>
                    <span>Knowledge Base</span>
                    <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                  </button>
                  {objSwitcherOpen && (
                    <div className="hs-obj-panel">
                      <div className="hs-obj-search-wrap">
                        <svg className="hs-obj-search-icon" width="13" height="13" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                        <input className="hs-obj-search" placeholder="Search" value={objSwitcherSearch} onChange={(e) => setObjSwitcherSearch(e.target.value)} autoFocus />
                      </div>
                      <ul className="hs-obj-list" role="listbox">
                        {[
                          { label: 'Contacts', view: 'contacts' as ViewMode },
                            { label: 'Clients', view: 'companies' as ViewMode },
                          { label: 'Deals', view: 'deals' as ViewMode },
                          { label: 'Leads', view: 'leads' as ViewMode },
                          { label: 'Projects', view: 'projects' as ViewMode },
                          { label: 'Tickets', view: 'tickets' as ViewMode },
                          { label: 'Calls', view: 'calls' as ViewMode },
                        ].filter(item => !objSwitcherSearch || item.label.toLowerCase().includes(objSwitcherSearch.toLowerCase())).map(item => (
                          <li key={item.view} className={`hs-obj-item${item.view === activeView ? ' active' : ''}`} role="option" aria-selected={item.view === activeView} onClick={() => { setActiveView(item.view); setObjSwitcherOpen(false); setObjSwitcherSearch('') }}>{item.label}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <div className="hs-topbar-sep" />

                {/* View tabs */}
                <div className="hs-view-tabs">
                  {[
                    { key: '', label: 'All articles' },
                    { key: 'published', label: 'Published' },
                    { key: 'draft', label: 'Drafts' },
                  ].map((tab) => {
                    const isActive = kbStatusFilter === tab.key
                    return (
                      <div key={tab.key} className={`hs-view-tab${isActive ? ' active' : ''}`} onClick={() => { setKbStatusFilter(tab.key); setKbPage(1); setKbSelectedRows(new Set()) }}>
                        <span className="hs-tab-label">{tab.label}</span>
                        {isActive && kbArticlesTotal > 0 && <span className="hs-tab-count">{kbArticlesTotal.toLocaleString()}</span>}
                        {isActive && (
                          <span className="hs-tab-kebab" onClick={(e) => e.stopPropagation()}>
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>
                          </span>
                        )}
                        {tab.key !== '' && <span className="hs-tab-x" onClick={(e) => { e.stopPropagation(); setKbStatusFilter(''); setKbPage(1); setKbSelectedRows(new Set()) }}>&times;</span>}
                      </div>
                    )
                  })}

                  <div className="hs-topbar-sep" />
                  <div className="hs-view-add-wrap" style={{ position: 'relative', display: 'inline-flex', alignSelf: 'center' }}>
                    <button className="hs-quick-add-btn" title="Create or add view" type="button" aria-label="Create or add view" disabled style={{ opacity: 0.5 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                    </button>
                  </div>
                </div>
              </div>

              <div className="hs-topbar-right">
                <div className="hs-add-btn-wrap" ref={addBtnRef}>
                  <button className="hs-add-contacts-btn" onClick={() => setAddBtnOpen(!addBtnOpen)}>
                    Create article
                    <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                  </button>
                  {addBtnOpen && (
                    <div className="hs-add-btn-dropdown">
                      <button className="hs-add-btn-item" onClick={() => { setAddBtnOpen(false); setKbEditingArticle(null); setKbArticleTitle(''); setKbArticleBody(''); setKbArticleCategory(null); setKbArticleTags(''); setKbArticleStatus('draft'); setShowKbEditor(true) }}>Create new</button>
                      <button className="hs-add-btn-item" onClick={() => { setAddBtnOpen(false); setShowKbCategoryModal(true) }}>Manage categories</button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Row 2: Toolbar */}
            <div className="hs-toolbar">
              <div className="hs-toolbar-left">
                <div className="hs-search-wrap">
                  <svg className="hs-search-icon" width="14" height="14" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M28.14 29.29c-.29 0-.58-.11-.81-.33l-7.58-7.58a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l7.58 7.58a1.14 1.14 0 0 1-.81 1.95"/><path d="M13.29 24.71c-6.3 0-11.43-5.13-11.43-11.43S6.98 1.86 13.29 1.86s11.43 5.13 11.43 11.43-5.13 11.43-11.43 11.43Zm0-20.57c-5.04 0-9.14 4.1-9.14 9.14s4.1 9.14 9.14 9.14 9.14-4.1 9.14-9.14-4.1-9.14-9.14-9.14"/></svg>
                  <input className="hs-search-input" placeholder="Search articles" value={kbSearch} onChange={(e) => { setKbSearch(e.target.value); setKbPage(1) }} />
                  {kbSearch && <button className="hs-search-clear" onClick={() => { setKbSearch(''); setKbPage(1) }}>&times;</button>}
                </div>

                <div className="hs-tb-sep" />

                <div className="hs-viewtype-group">
                  <div className="hs-viewtype-wrap">
                    <button className="hs-tb-btn hs-viewtype-btn">
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                      <span>Table</span>
                      <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                    </button>
                  </div>
                  <button className={`hs-tb-btn hs-settings-btn${tableSettingsOpen ? ' active' : ''}`} onClick={() => setTableSettingsOpen(!tableSettingsOpen)} title={tableSettingsOpen ? 'Close settings' : 'Table settings'}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                  </button>
                </div>

                <div className="hs-tb-sep" />

                {/* Edit columns */}
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Edit columns" disabled style={{ opacity: 0.5 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="18" rx="1"/><rect x="14" y="3" width="7" height="18" rx="1"/></svg>
                  <span>Edit columns</span>
                </button>

                <div className="hs-tb-sep" />

                {/* Filters */}
                <button className={`hs-tb-btn hs-tb-btn-bordered${quickFiltersOpen ? ' hs-tb-btn-pressed' : ''}`} title="Filters" onClick={() => setQuickFiltersOpen(!quickFiltersOpen)}>
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M30.71 16.86H13.57a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28M6.71 16.86H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M10.14 20.29c-2.52 0-4.57-2.05-4.57-4.57s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M6.71 6.57H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M30.71 6.57H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M18.14 6.57H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 10C19.05 10 17 7.95 17 5.43S19.05.86 21.57.86s4.57 2.05 4.57 4.57S24.09 10 21.57 10m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M30.71 27.14H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M18.14 27.14H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 30.57C19.05 30.57 17 28.52 17 26s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29"/></svg>
                  <span>Filters</span>
                </button>

                <div className="hs-tb-sep" />

                {/* Export */}
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Export articles as CSV" onClick={() => {
                  try {
                    const headers = ['Title', 'Category', 'Status', 'Author', 'Views', 'Helpful Yes', 'Helpful No', 'Updated']
                    const escCsv = (v: unknown) => { const s = String(v ?? ''); return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s }
                    const rows = kbArticles.map((a: any) => [a.title, a.category_name || '', a.status, a.author_name || '', a.view_count, a.helpful_yes, a.helpful_no, a.updated_at || ''].map(escCsv).join(','))
                    const csv = [headers.join(','), ...rows].join('\n')
                    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
                    const url = URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = `kb-articles-export-${new Date().toISOString().slice(0, 10)}.csv`
                    document.body.appendChild(a)
                    a.click()
                    document.body.removeChild(a)
                    URL.revokeObjectURL(url)
                  } catch (err) { console.error('Export failed:', err); alert('Export failed.') }
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  <span>Export</span>
                </button>
              </div>

              <div className="hs-toolbar-right">
                <div className="hs-tb-sep" />
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Clone view" disabled style={{ opacity: 0.5 }}>
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M25.86 29.29H12.15c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-1.89 1.54-3.43 3.43-3.43h13.71c1.89 0 3.43 1.54 3.43 3.43v13.71c0 1.89-1.54 3.43-3.43 3.43M12.14 11c-.63 0-1.14.51-1.14 1.14v13.71c0 .63.51 1.14 1.14 1.14h13.71c.63 0 1.14-.51 1.14-1.14V12.14c0-.63-.51-1.14-1.14-1.14z"/><path d="M5.29 22.43c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43H19c1.89 0 3.43 1.54 3.43 3.43a1.14 1.14 0 1 1-2.28 0c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14V19c0 .63.51 1.14 1.14 1.14a1.14 1.14 0 1 1 0 2.28Z"/></svg>
                </button>
                <button className="hs-tb-btn hs-tb-btn-bordered disabled" disabled title="Save view" style={{ opacity: 0.5 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                </button>
              </div>
            </div>

            {/* Row 3: Quick filters */}
            {quickFiltersOpen && <div className="hs-quick-filters">
              <div className="hs-owner-dropdown" ref={ownerDropdownRef}>
                <button className={`hs-qf-btn hs-qf-trigger${ownerDropdownOpen ? ' open' : ''}`} onClick={() => setOwnerDropdownOpen(!ownerDropdownOpen)}>
                  <span>Author</span>
                  <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                </button>
              </div>

              {/* Category filter cycle toggle */}
              <button className="hs-qf-btn hs-qf-trigger" onClick={() => {
                const catIds = kbCategories.map((c: any) => c.id)
                if (!catIds.length) return
                // cycle categories
              }}>
                <span>Category</span>
                <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
              </button>

              {/* Sort by */}
              <button className={`hs-qf-btn hs-qf-trigger${kbSort.field !== 'updated_at' ? ' has-value' : ''}`} onClick={() => {
                const fields = ['updated_at', 'title', 'view_count', 'created_at']
                const idx = fields.indexOf(kbSort.field)
                const next = fields[(idx + 1) % fields.length]
                setKbSort({ field: next, order: next === 'title' ? 'ASC' : 'DESC' })
                setKbPage(1)
              }}>
                <span>Sort: {{ updated_at: 'Updated', title: 'Title', view_count: 'Views', created_at: 'Created' }[kbSort.field] || 'Updated'}</span>
                <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
              </button>

              <div className="hs-qf-sep" />

              <div className="hs-qf-more-wrap">
                <button className="hs-qf-btn hs-qf-more">
                  <svg width="10" height="10" viewBox="0 0 32 32" fill="currentColor"><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V3a1.14 1.14 0 1 1 2.28 0v25.14c0 .63-.51 1.14-1.14 1.14Z"/><path d="M28.14 16.71H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28"/></svg>
                  <span>More</span>
                </button>
              </div>
            </div>}

            <div className={`hs-content${tableSettingsOpen ? ' with-settings' : ''}`}>
              <div className="hs-table-area">
                {/* Row 4: Selection bar */}
                {kbSelectedRows.size > 0 && (
                  <div className="hs-selection-bar">
                    <span className="hs-sel-count">{kbSelectedRows.size} selected</span>
                    <div className="hs-sel-sep" />
                    <button className="hs-sel-btn" onClick={() => {
                      const firstId = [...kbSelectedRows][0]
                      const article = kbArticles.find((a: any) => a.id === firstId)
                      if (article) { setKbEditingArticle(article); setKbArticleTitle(article.title); setKbArticleBody(article.body || ''); setKbArticleCategory(article.category_id); setKbArticleTags(article.tags || ''); setKbArticleStatus(article.status); setShowKbEditor(true) }
                    }}>Edit</button>
                    <button className="hs-sel-btn" style={{ color: '#dc2626' }} onClick={() => {
                      if (confirm(`Delete ${kbSelectedRows.size} article(s)?`)) {
                        Promise.all([...kbSelectedRows].map(id => deleteKbArticle(id))).then(() => {
                          setKbSelectedRows(new Set())
                          fetchKbArticles({ search: kbSearch || undefined, status: kbStatusFilter || undefined, page: kbPage, pageSize: tablePageSize }).then((r) => { setKbArticles(r.data); setKbArticlesTotal(r.total) })
                        }).catch(() => {})
                      }
                    }}>Delete</button>
                    <button className="hs-sel-close" onClick={() => setKbSelectedRows(new Set())} title="Deselect all">&times;</button>
                  </div>
                )}

                {/* Loading bar */}
                {kbArticlesLoading && kbArticles.length > 0 && <div className="hs-loading-bar" />}

                {/* Row 5: Data table */}
                <div className="hs-table-scroll">
                  {kbArticlesLoading && kbArticles.length === 0 ? (
                    <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                      <colgroup><col style={{ width: 40 }} /><col style={{ width: 280 }} /><col style={{ width: 140 }} /><col style={{ width: 100 }} /><col style={{ width: 120 }} /><col style={{ width: 80 }} /><col style={{ width: 90 }} /><col style={{ width: 130 }} /></colgroup>
                      <thead><tr>
                        <th className="hs-th-check"><input type="checkbox" disabled /></th>
                        <th className="hs-th-resizable" style={{ position: 'sticky', left: 40, zIndex: 2, background: '#f5f8fa' }}><span>Title</span></th>
                        <th className="hs-th-resizable"><span>Category</span></th>
                        <th className="hs-th-resizable"><span>Status</span></th>
                        <th className="hs-th-resizable"><span>Author</span></th>
                        <th className="hs-th-resizable"><span>Views</span></th>
                        <th className="hs-th-resizable"><span>Helpful</span></th>
                        <th className="hs-th-resizable"><span>Updated</span></th>
                      </tr></thead>
                      <tbody>
                        <tr><td colSpan={8} className="hs-loading-cell"><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}><DotLottieReact src="/loading.lottie" loop autoplay style={{ width: 80, height: 80 }} /><span style={{ fontSize: '0.82rem', color: '#7c98b6', marginTop: 8 }}>Loading articles...</span></div></td></tr>
                      </tbody>
                    </table>
                  ) : kbArticles.length === 0 ? (
                    <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                      <colgroup><col style={{ width: 40 }} /><col style={{ width: 280 }} /><col style={{ width: 140 }} /><col style={{ width: 100 }} /><col style={{ width: 120 }} /><col style={{ width: 80 }} /><col style={{ width: 90 }} /><col style={{ width: 130 }} /></colgroup>
                      <thead><tr>
                        <th className="hs-th-check"><input type="checkbox" disabled /></th>
                        <th className="hs-th-resizable" style={{ position: 'sticky', left: 40, zIndex: 2, background: '#f5f8fa' }}><span>Title</span></th>
                        <th className="hs-th-resizable"><span>Category</span></th>
                        <th className="hs-th-resizable"><span>Status</span></th>
                        <th className="hs-th-resizable"><span>Author</span></th>
                        <th className="hs-th-resizable"><span>Views</span></th>
                        <th className="hs-th-resizable"><span>Helpful</span></th>
                        <th className="hs-th-resizable"><span>Updated</span></th>
                      </tr></thead>
                      <tbody>
                        <tr><td colSpan={8} className="hs-empty-cell">
                          <div style={{ padding: '40px', textAlign: 'center' }}>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '12px' }}><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>
                            <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#33475b', marginBottom: '6px' }}>No articles found</div>
                            <div style={{ fontSize: '0.82rem', color: '#7c98b6', maxWidth: '360px', margin: '0 auto' }}>Create a new article or adjust your search filters.</div>
                          </div>
                        </td></tr>
                      </tbody>
                    </table>
                  ) : (
                    <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                      <colgroup><col style={{ width: 40 }} /><col style={{ width: 280 }} /><col style={{ width: 140 }} /><col style={{ width: 100 }} /><col style={{ width: 120 }} /><col style={{ width: 80 }} /><col style={{ width: 90 }} /><col style={{ width: 130 }} /></colgroup>
                      <thead><tr>
                        <th className="hs-th-check"><input type="checkbox" checked={kbArticles.length > 0 && kbSelectedRows.size === kbArticles.length} onChange={toggleAllKbRows} /></th>
                        {[
                          { key: 'title', label: 'Title', sortField: 'title', frozen: true },
                          { key: 'category', label: 'Category', sortField: '' },
                          { key: 'status', label: 'Status', sortField: 'status' },
                          { key: 'author', label: 'Author', sortField: '' },
                          { key: 'views', label: 'Views', sortField: 'view_count' },
                          { key: 'helpful', label: 'Helpful', sortField: '' },
                          { key: 'updated', label: 'Updated', sortField: 'updated_at' },
                        ].map(col => (
                          <th key={col.key} className={`hs-th-resizable${col.sortField ? ' hs-th-sortable' : ''}${col.frozen ? ' hs-th-frozen' : ''}`}
                            style={col.frozen ? { position: 'sticky', left: 40, top: 0, zIndex: 15, background: '#f8fafc' } : undefined}
                            onClick={() => {
                              if (!col.sortField) return
                              setKbSort(prev => ({ field: col.sortField, order: prev.field === col.sortField && prev.order === 'ASC' ? 'DESC' : 'ASC' }))
                              setKbPage(1)
                            }}>
                            <span className="hs-th-content">
                              {col.label}
                              {col.sortField && kbSort.field === col.sortField && <span className="hs-sort-arrow">{kbSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                              {col.sortField && kbSort.field !== col.sortField && <span className="hs-th-hover-sort"><svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M29.38 21.96a1.147 1.147 0 0 0-1.617 0l-3.85 3.852V5.714a1.143 1.143 0 0 0-2.286 0v20.099l-3.853-3.852a1.144 1.144 0 0 0-1.615 1.617l5.803 5.802a1.143 1.143 0 0 0 1.616 0l5.802-5.802a1.144 1.144 0 0 0 0-1.617M15.84 8.422 10.039 2.62a1.145 1.145 0 0 0-1.616 0L2.62 8.422a1.144 1.144 0 0 0 1.617 1.617l3.85-3.851v20.098a1.143 1.143 0 0 0 2.286 0V6.187l3.853 3.852a1.14 1.14 0 0 0 1.616-.001 1.14 1.14 0 0 0-.001-1.616"/></svg></span>}
                            </span>
                            <div className="hs-col-resize-handle" onClick={(e) => e.stopPropagation()} />
                          </th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {kbArticles.map((a: any) => (
                          <tr key={a.id} className={`hs-row${kbSelectedRows.has(a.id) ? ' selected' : ''}`} style={{ cursor: 'pointer' }} onClick={() => { setKbEditingArticle(a); setKbArticleTitle(a.title); setKbArticleBody(a.body || ''); setKbArticleCategory(a.category_id); setKbArticleTags(a.tags || ''); setKbArticleStatus(a.status); setShowKbEditor(true) }}>
                            <td className="hs-td-check" onClick={(e) => e.stopPropagation()}>
                              <input type="checkbox" checked={kbSelectedRows.has(a.id)} onChange={() => {
                                setKbSelectedRows(prev => { const next = new Set(prev); next.has(a.id) ? next.delete(a.id) : next.add(a.id); return next })
                              }} />
                            </td>
                            <td className="hs-td-name" style={{ position: 'sticky', left: 40, zIndex: 2, background: kbSelectedRows.has(a.id) ? '#eff6ff' : '#fff' }}>
                              <div className="hs-name-cell">
                                <span className="hs-name-link" style={{ fontWeight: 600, fontSize: '0.82rem', color: '#1976d2' }}>{a.title}</span>
                                <button className="hs-preview-btn" onClick={(e) => { e.stopPropagation() }}>Preview</button>
                              </div>
                            </td>
                            <td>{a.category_name ? <span className="hs-status-badge" style={{ background: '#eff6ff', color: '#2563eb' }}>{a.category_name}</span> : <span className="cell-empty">--</span>}</td>
                            <td><span className="hs-status-badge" style={{ background: a.status === 'published' ? '#f0fdf4' : '#f1f5f9', color: a.status === 'published' ? '#166534' : '#64748b' }}>{a.status}</span></td>
                            <td style={{ fontSize: '0.8rem', color: '#516f90' }}>{a.author_name || <span className="cell-empty">--</span>}</td>
                            <td style={{ fontSize: '0.8rem', color: '#516f90' }}>{a.view_count}</td>
                            <td style={{ fontSize: '0.8rem', color: '#516f90' }}>{a.helpful_yes}/{a.helpful_yes + a.helpful_no}</td>
                            <td style={{ fontSize: '0.8rem', color: '#516f90' }}>{a.updated_at ? formatDatePref(a.updated_at) : ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Row 6: Pagination */}
                <div className="hs-pagination">
                  <nav className="hs-paginator" aria-label="Pagination">
                    <button className="hs-paginator-btn" disabled={kbCurrentPage <= 1} onClick={() => setKbPage(p => p - 1)} aria-label="Previous page">
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95"/></svg>
                      <span>Prev</span>
                    </button>
                    {(() => {
                      if (kbTotalPages <= 7) {
                        const pages: number[] = []
                        for (let i = 1; i <= kbTotalPages; i++) pages.push(i)
                        return pages.map(p => (<button key={p} className={`hs-paginator-num${p === kbCurrentPage ? ' active' : ''}`} onClick={() => setKbPage(p)}>{p}</button>))
                      }
                      const items: React.ReactNode[] = []
                      const addPage = (p: number) => items.push(<button key={p} className={`hs-paginator-num${p === kbCurrentPage ? ' active' : ''}`} onClick={() => setKbPage(p)}>{p}</button>)
                      const addEllipsis = (key: string) => items.push(<span key={key} className="hs-paginator-ellipsis">...</span>)
                      addPage(1)
                      if (kbCurrentPage > 3) addEllipsis('el')
                      const start = Math.max(2, kbCurrentPage - 1)
                      const end = Math.min(kbTotalPages - 1, kbCurrentPage + 1)
                      for (let i = start; i <= end; i++) addPage(i)
                      if (kbCurrentPage < kbTotalPages - 2) addEllipsis('er')
                      addPage(kbTotalPages)
                      return items
                    })()}
                    <button className="hs-paginator-btn" disabled={kbCurrentPage >= kbTotalPages} onClick={() => setKbPage(p => p + 1)} aria-label="Next page">
                      <span>Next</span>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M11 27.71c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62l9.48-9.48-9.48-9.47a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0L22.1 15.48c.45.45.45 1.17 0 1.62L11.81 27.39c-.22.22-.52.33-.81.33Z"/></svg>
                    </button>
                  </nav>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', marginLeft: 'auto' }}><select value={tablePageSize} onChange={e => { setTablePageSize(Number(e.target.value) as any); setKbPage(1) }} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #e2e8f0', fontSize: '0.82rem' }}><option value={100}>100</option><option value={200}>200</option><option value={500}>500</option></select><span>per page</span></div>
                </div>
              </div>
            </div>

            {/* Row 7: Settings panel */}
            {tableSettingsOpen && (
              <div className="hs-settings-panel">
                <div className="hs-settings-header">
                  <span className="hs-settings-title">Table settings</span>
                  <button className="hs-settings-close" onClick={() => setTableSettingsOpen(false)}>
                    <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                  </button>
                </div>
                <div className="hs-settings-body">
                  <div className="hs-settings-group">
                    <label className="hs-settings-label">Pagination
                      <span className="hs-info-icon" title="Number of rows shown per page"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span>
                    </label>
                    {([100, 200, 500] as const).map(size => (
                      <label className="hs-settings-radio" key={size}>
                        <input type="radio" name="kbPageSize" checked={tablePageSize === size} onChange={() => { setTablePageSize(size); setKbPage(1) }} />
                        <span className="hs-radio-indicator" /><span>{size} per page</span>
                      </label>
                    ))}
                  </div>
                  <hr className="hs-settings-divider" />
                  <div className="hs-settings-group">
                    <label className="hs-settings-label">Row height
                      <span className="hs-info-icon" title="Adjust the vertical spacing of table rows"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span>
                    </label>
                    {([{ value: 'small' as const, label: 'Compact' }, { value: 'medium' as const, label: 'Default' }, { value: 'large' as const, label: 'Comfortable' }]).map(opt => (
                      <label className="hs-settings-radio" key={opt.value}>
                        <input type="radio" name="kbRowHeight" checked={tableRowHeight === opt.value} onChange={() => setTableRowHeight(opt.value)} />
                        <span className="hs-radio-indicator" /><span>{opt.label}</span>
                      </label>
                    ))}
                  </div>
                  <hr className="hs-settings-divider" />
                  <div className="hs-settings-group">
                    <div className="hs-settings-toggle-row">
                      <label className="hs-settings-label">Zebra striping
                        <span className="hs-info-icon" title="Alternate row background colors for easier reading"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span>
                      </label>
                      <button className={`hs-toggle${tableZebraStriping ? ' on' : ''}`} onClick={() => setTableZebraStriping(!tableZebraStriping)} role="switch" aria-checked={tableZebraStriping}>
                        <span className="hs-toggle-track"><span className="hs-toggle-on">ON</span><span className="hs-toggle-off">OFF</span><span className="hs-toggle-handle" /></span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {showKbCategoryModal && (<div className="modal-overlay" onClick={() => setShowKbCategoryModal(false)}><div className="modal-card" style={{ maxWidth: 500 }} onClick={(e) => e.stopPropagation()}><button type="button" className="modal-close" onClick={() => setShowKbCategoryModal(false)}>&times;</button><div className="modal-body"><h3 className="modal-title">Manage Categories</h3><div style={{ marginBottom: 16 }}>{kbCategories.map((cat: any) => (<div key={cat.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f2f5' }}><span style={{ fontSize: '0.82rem', fontWeight: 500 }}>{cat.name} ({cat.article_count})</span><button onClick={() => { deleteKbCategory(cat.id).then(() => fetchKbCategories().then((r) => setKbCategories(r.categories))).catch((e) => alert(e.message)) }} style={{ background: 'none', border: 'none', color: '#d94c53', cursor: 'pointer', fontSize: '0.75rem' }}>Delete</button></div>))}</div><div style={{ display: 'flex', gap: 8 }}><input placeholder="Category name" value={newCategoryName} onChange={(e) => setNewCategoryName(e.target.value)} style={{ flex: 1, padding: '6px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.82rem' }} /><button disabled={!newCategoryName.trim()} onClick={() => { createKbCategory({ name: newCategoryName }).then(() => { setNewCategoryName(''); fetchKbCategories().then((r) => setKbCategories(r.categories)) }) }} style={{ padding: '6px 12px', background: '#ff7a59', color: '#fff', border: 'none', borderRadius: 4, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', opacity: newCategoryName.trim() ? 1 : 0.5 }}>+ Add</button></div></div></div></div>)}
          </div>
          )
        })()}

        {/* ========== KB Editor ========== */}
        {activeView === 'knowledge-base' && showKbEditor && (
          <div className="hs-contacts" style={{ display: 'flex', flexDirection: 'column' }}>
            <div className="hs-topbar"><div className="hs-topbar-left"><button style={{ background: 'none', border: 'none', color: '#1976d2', fontSize: '0.82rem', cursor: 'pointer', padding: '0 12px' }} onClick={() => { setShowKbEditor(false); fetchKbArticles({ status: kbStatusFilter || undefined }).then((r) => { setKbArticles(r.data); setKbArticlesTotal(r.total) }) }}>&larr; Back</button></div><div className="hs-topbar-right" style={{ display: 'flex', gap: 8 }}>{kbEditingArticle && kbArticleStatus === 'draft' && <button className="hs-add-contacts-btn" onClick={() => { publishKbArticle(kbEditingArticle.id).then(() => { setShowKbEditor(false); fetchKbArticles({}).then((r) => { setKbArticles(r.data); setKbArticlesTotal(r.total) }) }) }}>Publish</button>}{kbEditingArticle && kbArticleStatus === 'published' && <button style={{ padding: '7px 14px', border: '1px solid #cbd6e2', borderRadius: 4, background: '#fff', color: '#33475b', fontSize: '0.82rem', cursor: 'pointer' }} onClick={() => { unpublishKbArticle(kbEditingArticle.id).then(() => { setShowKbEditor(false); fetchKbArticles({}).then((r) => { setKbArticles(r.data); setKbArticlesTotal(r.total) }) }) }}>Unpublish</button>}<button className="hs-add-contacts-btn" onClick={() => { const d = { title: kbArticleTitle, body: kbArticleBody, category_id: kbArticleCategory, tags: kbArticleTags, status: kbArticleStatus }; (kbEditingArticle ? updateKbArticle(kbEditingArticle.id, d) : createKbArticle(d)).then(() => { setShowKbEditor(false); fetchKbArticles({}).then((r) => { setKbArticles(r.data); setKbArticlesTotal(r.total) }) }).catch((e) => alert(e.message)) }}>{kbEditingArticle ? 'Save' : 'Create'}</button></div></div>
            <div style={{ flex: 1, overflow: 'auto', display: 'grid', gridTemplateColumns: '1fr 280px' }}>
              <div style={{ padding: 20, borderRight: '1px solid var(--line)' }}><input value={kbArticleTitle} onChange={(e) => setKbArticleTitle(e.target.value)} placeholder="Enter article title..." style={{ width: '100%', border: 'none', fontSize: '1.1rem', fontWeight: 800, color: 'var(--ink)', marginBottom: 12, outline: 'none' }} /><div style={{ border: '1px solid var(--line)', borderRadius: 8, minHeight: 400 }}><textarea value={kbArticleBody} onChange={(e) => setKbArticleBody(e.target.value)} placeholder="Write your article..." style={{ width: '100%', minHeight: 360, padding: 20, border: 'none', fontSize: '0.88rem', lineHeight: 1.7, resize: 'vertical', outline: 'none', fontFamily: 'inherit' }} /></div></div>
              <div style={{ padding: 20, background: '#fafbfc' }}><div style={{ marginBottom: 16 }}><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 500, color: '#33475b', marginBottom: 4 }}>Status</label><select value={kbArticleStatus} onChange={(e) => setKbArticleStatus(e.target.value)} style={{ width: '100%', padding: '6px 8px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.82rem' }}><option value="draft">Draft</option><option value="published">Published</option><option value="archived">Archived</option></select></div><div style={{ marginBottom: 16 }}><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 500, color: '#33475b', marginBottom: 4 }}>Category</label><select value={kbArticleCategory || ''} onChange={(e) => setKbArticleCategory(e.target.value ? parseInt(e.target.value) : null)} style={{ width: '100%', padding: '6px 8px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.82rem' }}><option value="">Select...</option>{kbCategories.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div><div style={{ marginBottom: 16 }}><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 500, color: '#33475b', marginBottom: 4 }}>Tags</label><input value={kbArticleTags} onChange={(e) => setKbArticleTags(e.target.value)} placeholder="painting, prep" style={{ width: '100%', padding: '6px 8px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.82rem' }} /></div></div>
            </div>
          </div>
        )}

        {/* ========== Customer Portal View ========== */}
        {activeView === 'customer-portal' && (() => {
          const ptTotalPages = Math.max(1, Math.ceil(portalTicketsTotal / tablePageSize))
          const ptCurrentPage = Math.min(portalTicketPage, ptTotalPages)
          const toggleAllPortalRows = () => {
            if (portalTicketSelectedRows.size === portalTickets.length) {
              setPortalTicketSelectedRows(new Set())
            } else {
              setPortalTicketSelectedRows(new Set(portalTickets.map((t: any) => t.id)))
            }
          }
          return (
          <div className="hs-contacts">
            {/* Row 1: Topbar */}
            <div className="hs-topbar">
              <div className="hs-topbar-left">
                <div className="hs-obj-switcher-wrap" ref={objSwitcherRef}>
                  <button className={`hs-obj-switcher${objSwitcherOpen ? ' open' : ''}`} onClick={() => { setObjSwitcherOpen(!objSwitcherOpen); setObjSwitcherSearch('') }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/></svg>
                    <span>Customer Portal</span>
                    <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                  </button>
                  {objSwitcherOpen && (
                    <div className="hs-obj-panel">
                      <div className="hs-obj-search-wrap">
                        <svg className="hs-obj-search-icon" width="13" height="13" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                        <input className="hs-obj-search" placeholder="Search" value={objSwitcherSearch} onChange={(e) => setObjSwitcherSearch(e.target.value)} autoFocus />
                      </div>
                      <ul className="hs-obj-list" role="listbox">
                        {[
                          { label: 'Contacts', view: 'contacts' as ViewMode },
                            { label: 'Clients', view: 'companies' as ViewMode },
                          { label: 'Deals', view: 'deals' as ViewMode },
                          { label: 'Leads', view: 'leads' as ViewMode },
                          { label: 'Projects', view: 'projects' as ViewMode },
                          { label: 'Tickets', view: 'tickets' as ViewMode },
                          { label: 'Calls', view: 'calls' as ViewMode },
                        ].filter(item => !objSwitcherSearch || item.label.toLowerCase().includes(objSwitcherSearch.toLowerCase())).map(item => (
                          <li key={item.view} className={`hs-obj-item${item.view === activeView ? ' active' : ''}`} role="option" aria-selected={item.view === activeView} onClick={() => { setActiveView(item.view); setObjSwitcherOpen(false); setObjSwitcherSearch('') }}>{item.label}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <div className="hs-topbar-sep" />

                <div className="hs-view-tabs">
                  {[
                    { key: 'tickets', label: 'All tickets' },
                    { key: 'new', label: 'New' },
                    { key: 'in_progress', label: 'In Progress' },
                    { key: 'resolved', label: 'Resolved' },
                    { key: 'settings', label: 'Settings' },
                  ].map((tab) => {
                    const isActive = (tab.key === 'settings' ? portalSubView === 'settings' : (portalSubView === 'tickets' && portalTicketStatusFilter === (tab.key === 'tickets' ? '' : tab.key)))
                    return (
                      <div key={tab.key} className={`hs-view-tab${isActive ? ' active' : ''}`} onClick={() => {
                        if (tab.key === 'settings') { setPortalSubView('settings') }
                        else { setPortalSubView('tickets'); setPortalTicketStatusFilter(tab.key === 'tickets' ? '' : tab.key); setPortalTicketPage(1); setPortalTicketSelectedRows(new Set()) }
                      }}>
                        <span className="hs-tab-label">{tab.label}</span>
                        {isActive && tab.key !== 'settings' && portalTicketsTotal > 0 && <span className="hs-tab-count">{portalTicketsTotal.toLocaleString()}</span>}
                        {isActive && (
                          <span className="hs-tab-kebab" onClick={(e) => e.stopPropagation()}>
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>
                          </span>
                        )}
                        {tab.key !== 'tickets' && <span className="hs-tab-x" onClick={(e) => { e.stopPropagation(); setPortalSubView('tickets'); setPortalTicketStatusFilter(''); setPortalTicketPage(1); setPortalTicketSelectedRows(new Set()) }}>&times;</span>}
                      </div>
                    )
                  })}

                  <div className="hs-topbar-sep" />
                  <div className="hs-view-add-wrap" style={{ position: 'relative', display: 'inline-flex', alignSelf: 'center' }}>
                    <button className="hs-quick-add-btn" title="Create or add view" type="button" disabled style={{ opacity: 0.5 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                    </button>
                  </div>
                </div>
              </div>

              <div className="hs-topbar-right">
                {/* Create ticket — direct action (Import removed) */}
                <button className="hs-add-contacts-btn" onClick={() => { setCreateFormData({}); setCreateError(null); setShowCreateModal('ticket') }}>
                  Create ticket
                </button>
              </div>
            </div>

            {portalSubView === 'settings' ? (
              <div className="hs-content" style={{ padding: '16px 20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, maxWidth: 900 }}><div><h3 style={{ fontSize: '0.84rem', fontWeight: 700, marginBottom: 12 }}>Portal Settings</h3>{[{ key: 'portal_name', label: 'Portal Name' }, { key: 'welcome_message', label: 'Welcome Message' }, { key: 'notification_email', label: 'Notification Email' }].map(f => (<div key={f.key} style={{ marginBottom: 12 }}><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 500, color: '#33475b', marginBottom: 4 }}>{f.label}</label><input value={portalSettings[f.key] || ''} onChange={(e) => setPortalSettingsData({ ...portalSettings, [f.key]: e.target.value })} style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.82rem' }} /></div>))}{['portal_enabled', 'allow_self_registration', 'show_knowledge_base'].map(key => (<div key={key} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}><span style={{ fontSize: '0.82rem', color: '#33475b' }}>{key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span><input type="checkbox" checked={portalSettings[key] === '1'} onChange={(e) => setPortalSettingsData({ ...portalSettings, [key]: e.target.checked ? '1' : '0' })} /></div>))}<button className="hs-add-contacts-btn" style={{ marginTop: 12 }} onClick={() => updatePortalSettings(portalSettings).catch(console.error)}>Save Settings</button></div><div><h3 style={{ fontSize: '0.84rem', fontWeight: 700, marginBottom: 12 }}>Branding</h3><div style={{ marginBottom: 12 }}><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 500, color: '#33475b', marginBottom: 4 }}>Primary Color</label><div style={{ display: 'flex', gap: 8 }}><input type="color" value={portalSettings.primary_color || '#1976d2'} onChange={(e) => setPortalSettingsData({ ...portalSettings, primary_color: e.target.value })} style={{ width: 40, height: 32, border: 'none', cursor: 'pointer' }} /><input value={portalSettings.primary_color || '#1976d2'} onChange={(e) => setPortalSettingsData({ ...portalSettings, primary_color: e.target.value })} style={{ flex: 1, padding: '6px 8px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.82rem' }} /></div></div><div style={{ marginTop: 24, padding: 16, border: '1px solid var(--line)', borderRadius: 8, background: '#f8fafc' }}><div style={{ fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 600, marginBottom: 8 }}>Preview</div><div style={{ background: portalSettings.primary_color || '#1976d2', color: '#fff', padding: 16, borderRadius: 6, textAlign: 'center' }}><div style={{ fontWeight: 700, fontSize: '0.88rem' }}>{portalSettings.portal_name || 'Customer Portal'}</div></div></div></div></div>
              </div>
            ) : (<>
            {/* Row 2: Toolbar */}
            <div className="hs-toolbar">
              <div className="hs-toolbar-left">
                <div className="hs-search-wrap">
                  <svg className="hs-search-icon" width="14" height="14" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M28.14 29.29c-.29 0-.58-.11-.81-.33l-7.58-7.58a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l7.58 7.58a1.14 1.14 0 0 1-.81 1.95"/><path d="M13.29 24.71c-6.3 0-11.43-5.13-11.43-11.43S6.98 1.86 13.29 1.86s11.43 5.13 11.43 11.43-5.13 11.43-11.43 11.43Zm0-20.57c-5.04 0-9.14 4.1-9.14 9.14s4.1 9.14 9.14 9.14 9.14-4.1 9.14-9.14-4.1-9.14-9.14-9.14"/></svg>
                  <input className="hs-search-input" placeholder="Search tickets" value={portalTicketSearch} onChange={(e) => { setPortalTicketSearch(e.target.value); setPortalTicketPage(1) }} />
                  {portalTicketSearch && <button className="hs-search-clear" onClick={() => { setPortalTicketSearch(''); setPortalTicketPage(1) }}>&times;</button>}
                </div>

                <div className="hs-tb-sep" />

                <div className="hs-viewtype-group">
                  <div className="hs-viewtype-wrap">
                    <button className="hs-tb-btn hs-viewtype-btn">
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                      <span>Table</span>
                      <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                    </button>
                  </div>
                  <button className={`hs-tb-btn hs-settings-btn${tableSettingsOpen ? ' active' : ''}`} onClick={() => setTableSettingsOpen(!tableSettingsOpen)} title={tableSettingsOpen ? 'Close settings' : 'Table settings'}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                  </button>
                </div>

                <div className="hs-tb-sep" />

                <button className="hs-tb-btn hs-tb-btn-bordered" title="Edit columns" disabled style={{ opacity: 0.5 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="18" rx="1"/><rect x="14" y="3" width="7" height="18" rx="1"/></svg>
                  <span>Edit columns</span>
                </button>

                <div className="hs-tb-sep" />

                <button className={`hs-tb-btn hs-tb-btn-bordered${quickFiltersOpen ? ' hs-tb-btn-pressed' : ''}`} title="Filters" onClick={() => setQuickFiltersOpen(!quickFiltersOpen)}>
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M30.71 16.86H13.57a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28M6.71 16.86H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M10.14 20.29c-2.52 0-4.57-2.05-4.57-4.57s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M6.71 6.57H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M30.71 6.57H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M18.14 6.57H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 10C19.05 10 17 7.95 17 5.43S19.05.86 21.57.86s4.57 2.05 4.57 4.57S24.09 10 21.57 10m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M30.71 27.14H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M18.14 27.14H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 30.57C19.05 30.57 17 28.52 17 26s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29"/></svg>
                  <span>Filters</span>
                </button>

                <div className="hs-tb-sep" />

                <button className="hs-tb-btn hs-tb-btn-bordered" title="Export tickets as CSV" onClick={() => {
                  try {
                    const headers = ['Ticket #', 'Subject', 'Contact', 'Category', 'Priority', 'Status', 'Assigned', 'Created']
                    const escCsv = (v: unknown) => { const s = String(v ?? ''); return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s }
                    const rows = portalTickets.map((t: any) => [t.ticket_number, t.subject, t.client_name || '', t.category || '', t.priority, t.status, t.assigned_name || '', t.created_at || ''].map(escCsv).join(','))
                    const csv = [headers.join(','), ...rows].join('\n')
                    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
                    const url = URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = `portal-tickets-export-${new Date().toISOString().slice(0, 10)}.csv`
                    document.body.appendChild(a)
                    a.click()
                    document.body.removeChild(a)
                    URL.revokeObjectURL(url)
                  } catch (err) { console.error('Export failed:', err); alert('Export failed.') }
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  <span>Export</span>
                </button>
              </div>

              <div className="hs-toolbar-right">
                <div className="hs-tb-sep" />
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Clone view" disabled style={{ opacity: 0.5 }}>
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M25.86 29.29H12.15c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-1.89 1.54-3.43 3.43-3.43h13.71c1.89 0 3.43 1.54 3.43 3.43v13.71c0 1.89-1.54 3.43-3.43 3.43M12.14 11c-.63 0-1.14.51-1.14 1.14v13.71c0 .63.51 1.14 1.14 1.14h13.71c.63 0 1.14-.51 1.14-1.14V12.14c0-.63-.51-1.14-1.14-1.14z"/><path d="M5.29 22.43c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43H19c1.89 0 3.43 1.54 3.43 3.43a1.14 1.14 0 1 1-2.28 0c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14V19c0 .63.51 1.14 1.14 1.14a1.14 1.14 0 1 1 0 2.28Z"/></svg>
                </button>
                <button className="hs-tb-btn hs-tb-btn-bordered disabled" disabled title="Save view" style={{ opacity: 0.5 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                </button>
              </div>
            </div>

            {/* Row 3: Quick filters */}
            {quickFiltersOpen && <div className="hs-quick-filters">
              <div className="hs-owner-dropdown" ref={ownerDropdownRef}>
                <button className={`hs-qf-btn hs-qf-trigger${ownerDropdownOpen ? ' open' : ''}`} onClick={() => setOwnerDropdownOpen(!ownerDropdownOpen)}>
                  <span>Assigned to</span>
                  <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                </button>
              </div>

              <button className="hs-qf-btn hs-qf-trigger" onClick={() => {}}>
                <span>Priority</span>
                <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
              </button>

              <button className={`hs-qf-btn hs-qf-trigger${portalTicketSort.field !== 'created_at' ? ' has-value' : ''}`} onClick={() => {
                const fields = ['created_at', 'subject', 'priority', 'status']
                const idx = fields.indexOf(portalTicketSort.field)
                const next = fields[(idx + 1) % fields.length]
                setPortalTicketSort({ field: next, order: next === 'subject' ? 'ASC' : 'DESC' })
                setPortalTicketPage(1)
              }}>
                <span>Sort: {{ created_at: 'Created', subject: 'Subject', priority: 'Priority', status: 'Status' }[portalTicketSort.field] || 'Created'}</span>
                <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
              </button>

              <div className="hs-qf-sep" />

              <div className="hs-qf-more-wrap">
                <button className="hs-qf-btn hs-qf-more">
                  <svg width="10" height="10" viewBox="0 0 32 32" fill="currentColor"><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V3a1.14 1.14 0 1 1 2.28 0v25.14c0 .63-.51 1.14-1.14 1.14Z"/><path d="M28.14 16.71H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28"/></svg>
                  <span>More</span>
                </button>
              </div>
            </div>}

            <div className={`hs-content${tableSettingsOpen ? ' with-settings' : ''}`}>
              <div className="hs-table-area">
                {/* Row 4: Selection bar */}
                {portalTicketSelectedRows.size > 0 && (
                  <div className="hs-selection-bar">
                    <span className="hs-sel-count">{portalTicketSelectedRows.size} selected</span>
                    <button className="hs-sel-close" onClick={() => setPortalTicketSelectedRows(new Set())} title="Deselect all">&times;</button>
                  </div>
                )}

                {portalTicketsLoading && portalTickets.length > 0 && <div className="hs-loading-bar" />}

                {/* Row 5: Data table */}
                <div className="hs-table-scroll">
                  {portalTicketsLoading && portalTickets.length === 0 ? (
                    <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                      <colgroup><col style={{ width: 40 }} /><col style={{ width: 110 }} /><col style={{ width: 220 }} /><col style={{ width: 130 }} /><col style={{ width: 110 }} /><col style={{ width: 100 }} /><col style={{ width: 110 }} /><col style={{ width: 120 }} /><col style={{ width: 130 }} /></colgroup>
                      <thead><tr>
                        <th className="hs-th-check"><input type="checkbox" disabled /></th>
                        <th className="hs-th-resizable" style={{ position: 'sticky', left: 40, zIndex: 2, background: '#f5f8fa' }}><span>Ticket #</span></th>
                        <th className="hs-th-resizable"><span>Subject</span></th>
                        <th className="hs-th-resizable"><span>Contact</span></th>
                        <th className="hs-th-resizable"><span>Category</span></th>
                        <th className="hs-th-resizable"><span>Priority</span></th>
                        <th className="hs-th-resizable"><span>Status</span></th>
                        <th className="hs-th-resizable"><span>Assigned</span></th>
                        <th className="hs-th-resizable"><span>Created</span></th>
                      </tr></thead>
                      <tbody>
                        <tr><td colSpan={9} className="hs-loading-cell"><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}><DotLottieReact src="/loading.lottie" loop autoplay style={{ width: 80, height: 80 }} /><span style={{ fontSize: '0.82rem', color: '#7c98b6', marginTop: 8 }}>Loading tickets...</span></div></td></tr>
                      </tbody>
                    </table>
                  ) : portalTickets.length === 0 ? (
                    <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                      <colgroup><col style={{ width: 40 }} /><col style={{ width: 110 }} /><col style={{ width: 220 }} /><col style={{ width: 130 }} /><col style={{ width: 110 }} /><col style={{ width: 100 }} /><col style={{ width: 110 }} /><col style={{ width: 120 }} /><col style={{ width: 130 }} /></colgroup>
                      <thead><tr>
                        <th className="hs-th-check"><input type="checkbox" disabled /></th>
                        <th className="hs-th-resizable" style={{ position: 'sticky', left: 40, zIndex: 2, background: '#f5f8fa' }}><span>Ticket #</span></th>
                        <th className="hs-th-resizable"><span>Subject</span></th>
                        <th className="hs-th-resizable"><span>Contact</span></th>
                        <th className="hs-th-resizable"><span>Category</span></th>
                        <th className="hs-th-resizable"><span>Priority</span></th>
                        <th className="hs-th-resizable"><span>Status</span></th>
                        <th className="hs-th-resizable"><span>Assigned</span></th>
                        <th className="hs-th-resizable"><span>Created</span></th>
                      </tr></thead>
                      <tbody>
                        <tr><td colSpan={9} className="hs-empty-cell">
                          <div style={{ padding: '40px', textAlign: 'center' }}>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '12px' }}><path d="M15 5v2"/><path d="M15 11v2"/><path d="M15 17v2"/><path d="M5 5h14a2 2 0 0 1 2 2v3a2 2 0 0 0 0 4v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-3a2 2 0 0 0 0-4V7a2 2 0 0 1 2-2z"/></svg>
                            <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#33475b', marginBottom: '6px' }}>No tickets found</div>
                            <div style={{ fontSize: '0.82rem', color: '#7c98b6', maxWidth: '360px', margin: '0 auto' }}>No portal tickets yet. Create one or adjust your filters.</div>
                          </div>
                        </td></tr>
                      </tbody>
                    </table>
                  ) : (
                    <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                      <colgroup><col style={{ width: 40 }} /><col style={{ width: 110 }} /><col style={{ width: 220 }} /><col style={{ width: 130 }} /><col style={{ width: 110 }} /><col style={{ width: 100 }} /><col style={{ width: 110 }} /><col style={{ width: 120 }} /><col style={{ width: 130 }} /></colgroup>
                      <thead><tr>
                        <th className="hs-th-check"><input type="checkbox" checked={portalTickets.length > 0 && portalTicketSelectedRows.size === portalTickets.length} onChange={toggleAllPortalRows} /></th>
                        {[
                          { key: 'ticket_number', label: 'Ticket #', sortField: 'ticket_number', frozen: true },
                          { key: 'subject', label: 'Subject', sortField: 'subject' },
                          { key: 'client', label: 'Contact', sortField: '' },
                          { key: 'category', label: 'Category', sortField: '' },
                          { key: 'priority', label: 'Priority', sortField: 'priority' },
                          { key: 'status', label: 'Status', sortField: 'status' },
                          { key: 'assigned', label: 'Assigned', sortField: '' },
                          { key: 'created', label: 'Created', sortField: 'created_at' },
                        ].map(col => (
                          <th key={col.key} className={`hs-th-resizable${col.sortField ? ' hs-th-sortable' : ''}${col.frozen ? ' hs-th-frozen' : ''}`}
                            style={col.frozen ? { position: 'sticky', left: 40, top: 0, zIndex: 15, background: '#f8fafc' } : undefined}
                            onClick={() => {
                              if (!col.sortField) return
                              setPortalTicketSort(prev => ({ field: col.sortField, order: prev.field === col.sortField && prev.order === 'ASC' ? 'DESC' : 'ASC' }))
                              setPortalTicketPage(1)
                            }}>
                            <span className="hs-th-content">
                              {col.label}
                              {col.sortField && portalTicketSort.field === col.sortField && <span className="hs-sort-arrow">{portalTicketSort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                              {col.sortField && portalTicketSort.field !== col.sortField && <span className="hs-th-hover-sort"><svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M29.38 21.96a1.147 1.147 0 0 0-1.617 0l-3.85 3.852V5.714a1.143 1.143 0 0 0-2.286 0v20.099l-3.853-3.852a1.144 1.144 0 0 0-1.615 1.617l5.803 5.802a1.143 1.143 0 0 0 1.616 0l5.802-5.802a1.144 1.144 0 0 0 0-1.617M15.84 8.422 10.039 2.62a1.145 1.145 0 0 0-1.616 0L2.62 8.422a1.144 1.144 0 0 0 1.617 1.617l3.85-3.851v20.098a1.143 1.143 0 0 0 2.286 0V6.187l3.853 3.852a1.14 1.14 0 0 0 1.616-.001 1.14 1.14 0 0 0-.001-1.616"/></svg></span>}
                            </span>
                            <div className="hs-col-resize-handle" onClick={(e) => e.stopPropagation()} />
                          </th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {portalTickets.map((t: any) => (
                          <tr key={t.id} className={`hs-row${portalTicketSelectedRows.has(t.id) ? ' selected' : ''}`} style={{ cursor: 'pointer' }} onClick={() => { fetchPortalTicket(t.id).then((r) => { setPortalTicketDetail(r.ticket); setPortalTicketComments(r.comments) }) }}>
                            <td className="hs-td-check" onClick={(e) => e.stopPropagation()}>
                              <input type="checkbox" checked={portalTicketSelectedRows.has(t.id)} onChange={() => {
                                setPortalTicketSelectedRows(prev => { const next = new Set(prev); next.has(t.id) ? next.delete(t.id) : next.add(t.id); return next })
                              }} />
                            </td>
                            <td className="hs-td-name" style={{ position: 'sticky', left: 40, zIndex: 2, background: portalTicketSelectedRows.has(t.id) ? '#eff6ff' : '#fff' }}>
                              <div className="hs-name-cell">
                                <span className="hs-name-link" style={{ fontWeight: 500, fontFamily: 'monospace', color: '#1976d2' }}>{t.ticket_number}</span>
                                <button className="hs-preview-btn" onClick={(e) => { e.stopPropagation() }}>Preview</button>
                              </div>
                            </td>
                            <td style={{ fontWeight: 500 }}>{t.subject}</td>
                            <td style={{ fontSize: '0.8rem' }}>{t.client_name || <span className="cell-empty">--</span>}</td>
                            <td><span className="hs-status-badge" style={{ background: '#f1f5f9', color: '#475569', textTransform: 'capitalize' }}>{(t.category || '').replace('_', ' ')}</span></td>
                            <td><span className="hs-status-badge" style={{ background: t.priority === 'urgent' ? '#dc2626' : t.priority === 'high' ? '#fef2f2' : '#f1f5f9', color: t.priority === 'urgent' ? '#fff' : t.priority === 'high' ? '#dc2626' : '#64748b' }}>{t.priority}</span></td>
                            <td><span className="hs-status-badge" style={{ background: t.status === 'resolved' ? '#f0fdf4' : t.status === 'new' ? '#eff6ff' : '#fff7ed', color: t.status === 'resolved' ? '#166534' : t.status === 'new' ? '#2563eb' : '#c2410c' }}>{t.status.replace(/_/g, ' ')}</span></td>
                            <td style={{ fontSize: '0.8rem', color: '#516f90' }}>{t.assigned_name || <span className="cell-empty">--</span>}</td>
                            <td style={{ fontSize: '0.8rem', color: '#516f90' }}>{t.created_at ? formatDatePref(t.created_at) : ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Row 6: Pagination */}
                <div className="hs-pagination">
                  <nav className="hs-paginator" aria-label="Pagination">
                    <button className="hs-paginator-btn" disabled={ptCurrentPage <= 1} onClick={() => setPortalTicketPage(p => p - 1)} aria-label="Previous page">
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95"/></svg>
                      <span>Prev</span>
                    </button>
                    {(() => {
                      if (ptTotalPages <= 7) {
                        const pages: number[] = []
                        for (let i = 1; i <= ptTotalPages; i++) pages.push(i)
                        return pages.map(p => (<button key={p} className={`hs-paginator-num${p === ptCurrentPage ? ' active' : ''}`} onClick={() => setPortalTicketPage(p)}>{p}</button>))
                      }
                      const items: React.ReactNode[] = []
                      const addPage = (p: number) => items.push(<button key={p} className={`hs-paginator-num${p === ptCurrentPage ? ' active' : ''}`} onClick={() => setPortalTicketPage(p)}>{p}</button>)
                      const addEllipsis = (key: string) => items.push(<span key={key} className="hs-paginator-ellipsis">...</span>)
                      addPage(1)
                      if (ptCurrentPage > 3) addEllipsis('el')
                      const start = Math.max(2, ptCurrentPage - 1)
                      const end = Math.min(ptTotalPages - 1, ptCurrentPage + 1)
                      for (let i = start; i <= end; i++) addPage(i)
                      if (ptCurrentPage < ptTotalPages - 2) addEllipsis('er')
                      addPage(ptTotalPages)
                      return items
                    })()}
                    <button className="hs-paginator-btn" disabled={ptCurrentPage >= ptTotalPages} onClick={() => setPortalTicketPage(p => p + 1)} aria-label="Next page">
                      <span>Next</span>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M11 27.71c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62l9.48-9.48-9.48-9.47a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0L22.1 15.48c.45.45.45 1.17 0 1.62L11.81 27.39c-.22.22-.52.33-.81.33Z"/></svg>
                    </button>
                  </nav>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', marginLeft: 'auto' }}><select value={tablePageSize} onChange={e => { setTablePageSize(Number(e.target.value) as any); setPortalTicketPage(1) }} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #e2e8f0', fontSize: '0.82rem' }}><option value={100}>100</option><option value={200}>200</option><option value={500}>500</option></select><span>per page</span></div>
                </div>
              </div>
            </div>

            {/* Row 7: Settings panel */}
            {tableSettingsOpen && (
              <div className="hs-settings-panel">
                <div className="hs-settings-header">
                  <span className="hs-settings-title">Table settings</span>
                  <button className="hs-settings-close" onClick={() => setTableSettingsOpen(false)}>
                    <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                  </button>
                </div>
                <div className="hs-settings-body">
                  <div className="hs-settings-group">
                    <label className="hs-settings-label">Pagination
                      <span className="hs-info-icon" title="Number of rows shown per page"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span>
                    </label>
                    {([100, 200, 500] as const).map(size => (
                      <label className="hs-settings-radio" key={size}>
                        <input type="radio" name="ptPageSize" checked={tablePageSize === size} onChange={() => { setTablePageSize(size); setPortalTicketPage(1) }} />
                        <span className="hs-radio-indicator" /><span>{size} per page</span>
                      </label>
                    ))}
                  </div>
                  <hr className="hs-settings-divider" />
                  <div className="hs-settings-group">
                    <label className="hs-settings-label">Row height
                      <span className="hs-info-icon" title="Adjust the vertical spacing of table rows"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span>
                    </label>
                    {([{ value: 'small' as const, label: 'Compact' }, { value: 'medium' as const, label: 'Default' }, { value: 'large' as const, label: 'Comfortable' }]).map(opt => (
                      <label className="hs-settings-radio" key={opt.value}>
                        <input type="radio" name="ptRowHeight" checked={tableRowHeight === opt.value} onChange={() => setTableRowHeight(opt.value)} />
                        <span className="hs-radio-indicator" /><span>{opt.label}</span>
                      </label>
                    ))}
                  </div>
                  <hr className="hs-settings-divider" />
                  <div className="hs-settings-group">
                    <div className="hs-settings-toggle-row">
                      <label className="hs-settings-label">Zebra striping
                        <span className="hs-info-icon" title="Alternate row background colors for easier reading"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span>
                      </label>
                      <button className={`hs-toggle${tableZebraStriping ? ' on' : ''}`} onClick={() => setTableZebraStriping(!tableZebraStriping)} role="switch" aria-checked={tableZebraStriping}>
                        <span className="hs-toggle-track"><span className="hs-toggle-on">ON</span><span className="hs-toggle-off">OFF</span><span className="hs-toggle-handle" /></span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            </>)}

            {portalTicketDetail && (<div className="modal-overlay" onClick={() => setPortalTicketDetail(null)}><div className="modal-card" style={{ maxWidth: 700, maxHeight: 'calc(100vh - 60px)', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}><button className="modal-close" onClick={() => setPortalTicketDetail(null)}>&times;</button><div className="modal-body"><h3 className="modal-title">{portalTicketDetail.ticket_number}: {portalTicketDetail.subject}</h3><div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: 12 }}>{portalTicketDetail.client_name} &middot; {portalTicketDetail.client_email}</div><div style={{ fontSize: '0.82rem', marginBottom: 16, padding: 12, background: '#f8fafc', borderRadius: 6 }}>{portalTicketDetail.description}</div><h4 style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 8 }}>Conversation</h4><div style={{ maxHeight: 300, overflow: 'auto', marginBottom: 12 }}>{portalTicketComments.map((c: any) => (<div key={c.id} style={{ padding: '8px 12px', marginBottom: 8, background: c.author_type === 'agent' ? '#eff6ff' : '#f8fafc', borderRadius: 6, borderLeft: `3px solid ${c.author_type === 'agent' ? '#1976d2' : '#94a3b8'}` }}><div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: 4 }}><strong>{c.author_name}</strong>{c.is_internal ? ' (Internal)' : ''}</div><div style={{ fontSize: '0.82rem' }}>{c.body}</div></div>))}</div><div style={{ display: 'flex', gap: 8 }}><textarea value={portalNewComment} onChange={(e) => setPortalNewComment(e.target.value)} placeholder="Reply..." rows={2} style={{ flex: 1, padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.82rem' }} /><button disabled={!portalNewComment.trim()} onClick={() => { addPortalTicketComment(portalTicketDetail.id, { body: portalNewComment, author_type: 'agent', author_name: 'Admin' }).then(() => { setPortalNewComment(''); fetchPortalTicket(portalTicketDetail.id).then((r) => setPortalTicketComments(r.comments)) }) }} style={{ padding: '8px 16px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', alignSelf: 'flex-end', opacity: portalNewComment.trim() ? 1 : 0.5 }}>Send</button></div></div></div></div>)}
          </div>
          )
        })()}

        {/* ========== Feedback Surveys View ========== */}
        {activeView === 'feedback-surveys' && !surveyDetail && (() => {
          const svTotalPages = Math.max(1, Math.ceil(surveysTotal / tablePageSize))
          const svCurrentPage = Math.min(surveyPage, svTotalPages)
          const toggleAllSurveyRows = () => {
            if (surveySelectedRows.size === surveysData.length) {
              setSurveySelectedRows(new Set())
            } else {
              setSurveySelectedRows(new Set(surveysData.map((s: any) => s.id)))
            }
          }
          return (
          <div className="hs-contacts">
            {/* Row 1: Topbar */}
            <div className="hs-topbar">
              <div className="hs-topbar-left">
                <div className="hs-obj-switcher-wrap" ref={objSwitcherRef}>
                  <button className={`hs-obj-switcher${objSwitcherOpen ? ' open' : ''}`} onClick={() => { setObjSwitcherOpen(!objSwitcherOpen); setObjSwitcherSearch('') }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                    <span>Feedback Surveys</span>
                    <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                  </button>
                  {objSwitcherOpen && (
                    <div className="hs-obj-panel">
                      <div className="hs-obj-search-wrap">
                        <svg className="hs-obj-search-icon" width="13" height="13" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="13.29" cy="13.29" r="9.14"/><path d="M20.19 21.38l7.58 7.58"/></svg>
                        <input className="hs-obj-search" placeholder="Search" value={objSwitcherSearch} onChange={(e) => setObjSwitcherSearch(e.target.value)} autoFocus />
                      </div>
                      <ul className="hs-obj-list" role="listbox">
                        {[
                          { label: 'Contacts', view: 'contacts' as ViewMode },
                            { label: 'Clients', view: 'companies' as ViewMode },
                          { label: 'Deals', view: 'deals' as ViewMode },
                          { label: 'Leads', view: 'leads' as ViewMode },
                          { label: 'Projects', view: 'projects' as ViewMode },
                          { label: 'Tickets', view: 'tickets' as ViewMode },
                          { label: 'Calls', view: 'calls' as ViewMode },
                        ].filter(item => !objSwitcherSearch || item.label.toLowerCase().includes(objSwitcherSearch.toLowerCase())).map(item => (
                          <li key={item.view} className={`hs-obj-item${item.view === activeView ? ' active' : ''}`} role="option" aria-selected={item.view === activeView} onClick={() => { setActiveView(item.view); setObjSwitcherOpen(false); setObjSwitcherSearch('') }}>{item.label}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <div className="hs-topbar-sep" />

                <div className="hs-view-tabs">
                  {[
                    { key: '', label: 'All surveys' },
                    { key: 'nps', label: 'NPS' },
                    { key: 'csat', label: 'CSAT' },
                    { key: 'ces', label: 'CES' },
                  ].map((tab) => {
                    const isActive = surveyTypeFilter === tab.key
                    return (
                      <div key={tab.key} className={`hs-view-tab${isActive ? ' active' : ''}`} onClick={() => { setSurveyTypeFilter(tab.key); setSurveyPage(1); setSurveySelectedRows(new Set()) }}>
                        <span className="hs-tab-label">{tab.label}</span>
                        {isActive && surveysTotal > 0 && <span className="hs-tab-count">{surveysTotal.toLocaleString()}</span>}
                        {isActive && (
                          <span className="hs-tab-kebab" onClick={(e) => e.stopPropagation()}>
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>
                          </span>
                        )}
                        {tab.key !== '' && <span className="hs-tab-x" onClick={(e) => { e.stopPropagation(); setSurveyTypeFilter(''); setSurveyPage(1); setSurveySelectedRows(new Set()) }}>&times;</span>}
                      </div>
                    )
                  })}

                  <div className="hs-topbar-sep" />
                  <div className="hs-view-add-wrap" style={{ position: 'relative', display: 'inline-flex', alignSelf: 'center' }}>
                    <button className="hs-quick-add-btn" title="Create or add view" type="button" disabled style={{ opacity: 0.5 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                    </button>
                  </div>
                </div>
              </div>

              <div className="hs-topbar-right">
                {/* Create survey — direct action (Import removed) */}
                <button className="hs-add-contacts-btn" onClick={() => { setNewSurveyName(''); setNewSurveyType('nps'); setNewSurveyTitle('How likely are you to recommend us to a friend or colleague?'); setShowCreateSurvey(true) }}>
                  Create survey
                </button>
              </div>
            </div>

            {/* Row 2: Toolbar */}
            <div className="hs-toolbar">
              <div className="hs-toolbar-left">
                <div className="hs-search-wrap">
                  <svg className="hs-search-icon" width="14" height="14" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M28.14 29.29c-.29 0-.58-.11-.81-.33l-7.58-7.58a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l7.58 7.58a1.14 1.14 0 0 1-.81 1.95"/><path d="M13.29 24.71c-6.3 0-11.43-5.13-11.43-11.43S6.98 1.86 13.29 1.86s11.43 5.13 11.43 11.43-5.13 11.43-11.43 11.43Zm0-20.57c-5.04 0-9.14 4.1-9.14 9.14s4.1 9.14 9.14 9.14 9.14-4.1 9.14-9.14-4.1-9.14-9.14-9.14"/></svg>
                  <input className="hs-search-input" placeholder="Search surveys" value={surveySearch} onChange={(e) => { setSurveySearch(e.target.value); setSurveyPage(1) }} />
                  {surveySearch && <button className="hs-search-clear" onClick={() => { setSurveySearch(''); setSurveyPage(1) }}>&times;</button>}
                </div>

                <div className="hs-tb-sep" />

                <div className="hs-viewtype-group">
                  <div className="hs-viewtype-wrap">
                    <button className="hs-tb-btn hs-viewtype-btn">
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M28.14 21.29H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28M28.14 13.29c-.63 0-1.14-.51-1.14-1.14V5.29c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14v6.86a1.14 1.14 0 1 1-2.28 0V5.29c0-1.89 1.54-3.43 3.43-3.43h20.57c1.89 0 3.43 1.54 3.43 3.43v6.86c0 .63-.51 1.14-1.14 1.14Z"/><path d="M25.86 29.29H5.29c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-.63.51-1.14 1.14-1.14h25.14c.63 0 1.14.51 1.14 1.14v13.71c0 1.89-1.54 3.43-3.43 3.43Zm-21.72-16v12.57c0 .31.12.59.33.81s.5.33.81.33h20.57c.63 0 1.14-.51 1.14-1.14V13.29z"/><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V12.14a1.14 1.14 0 1 1 2.28 0v16c0 .63-.51 1.14-1.14 1.14Z"/></svg>
                      <span>Table</span>
                      <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                    </button>
                  </div>
                  <button className={`hs-tb-btn hs-settings-btn${tableSettingsOpen ? ' active' : ''}`} onClick={() => setTableSettingsOpen(!tableSettingsOpen)} title={tableSettingsOpen ? 'Close settings' : 'Table settings'}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                  </button>
                </div>

                <div className="hs-tb-sep" />

                <button className="hs-tb-btn hs-tb-btn-bordered" title="Edit columns" disabled style={{ opacity: 0.5 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="18" rx="1"/><rect x="14" y="3" width="7" height="18" rx="1"/></svg>
                  <span>Edit columns</span>
                </button>

                <div className="hs-tb-sep" />

                <button className={`hs-tb-btn hs-tb-btn-bordered${quickFiltersOpen ? ' hs-tb-btn-pressed' : ''}`} title="Filters" onClick={() => setQuickFiltersOpen(!quickFiltersOpen)}>
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M30.71 16.86H13.57a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28M6.71 16.86H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M10.14 20.29c-2.52 0-4.57-2.05-4.57-4.57s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M6.71 6.57H1a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M30.71 6.57H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28"/><path d="M18.14 6.57H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 10C19.05 10 17 7.95 17 5.43S19.05.86 21.57.86s4.57 2.05 4.57 4.57S24.09 10 21.57 10m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29M30.71 27.14H25a1.14 1.14 0 1 1 0-2.28h5.71a1.14 1.14 0 1 1 0 2.28M18.14 27.14H1a1.14 1.14 0 1 1 0-2.28h17.14a1.14 1.14 0 1 1 0 2.28"/><path d="M21.57 30.57C19.05 30.57 17 28.52 17 26s2.05-4.57 4.57-4.57 4.57 2.05 4.57 4.57-2.05 4.57-4.57 4.57m0-6.86c-1.26 0-2.29 1.03-2.29 2.29s1.03 2.29 2.29 2.29 2.29-1.03 2.29-2.29-1.03-2.29-2.29-2.29"/></svg>
                  <span>Filters</span>
                </button>

                <div className="hs-tb-sep" />

                <button className="hs-tb-btn hs-tb-btn-bordered" title="Export surveys as CSV" onClick={() => {
                  try {
                    const headers = ['Name', 'Type', 'Status', 'Delivery', 'Responses', 'Score', 'Rate', 'Created']
                    const escCsv = (v: unknown) => { const s = String(v ?? ''); return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s }
                    const rows = surveysData.map((s: any) => [s.name, s.survey_type, s.status, s.delivery_method || '', s.total_responses, s.nps_score || s.avg_score || 0, s.total_sent > 0 ? `${Math.round(s.total_responses / s.total_sent * 100)}%` : '', s.created_at || ''].map(escCsv).join(','))
                    const csv = [headers.join(','), ...rows].join('\n')
                    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
                    const url = URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = `surveys-export-${new Date().toISOString().slice(0, 10)}.csv`
                    document.body.appendChild(a)
                    a.click()
                    document.body.removeChild(a)
                    URL.revokeObjectURL(url)
                  } catch (err) { console.error('Export failed:', err); alert('Export failed.') }
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  <span>Export</span>
                </button>
              </div>

              <div className="hs-toolbar-right">
                <div className="hs-tb-sep" />
                <button className="hs-tb-btn hs-tb-btn-bordered" title="Clone view" disabled style={{ opacity: 0.5 }}>
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M25.86 29.29H12.15c-1.89 0-3.43-1.54-3.43-3.43V12.15c0-1.89 1.54-3.43 3.43-3.43h13.71c1.89 0 3.43 1.54 3.43 3.43v13.71c0 1.89-1.54 3.43-3.43 3.43M12.14 11c-.63 0-1.14.51-1.14 1.14v13.71c0 .63.51 1.14 1.14 1.14h13.71c.63 0 1.14-.51 1.14-1.14V12.14c0-.63-.51-1.14-1.14-1.14z"/><path d="M5.29 22.43c-1.89 0-3.43-1.54-3.43-3.43V5.29c0-1.89 1.54-3.43 3.43-3.43H19c1.89 0 3.43 1.54 3.43 3.43a1.14 1.14 0 1 1-2.28 0c0-.63-.51-1.14-1.14-1.14H5.29c-.63 0-1.14.51-1.14 1.14V19c0 .63.51 1.14 1.14 1.14a1.14 1.14 0 1 1 0 2.28Z"/></svg>
                </button>
                <button className="hs-tb-btn hs-tb-btn-bordered disabled" disabled title="Save view" style={{ opacity: 0.5 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                </button>
              </div>
            </div>

            {/* Row 3: Quick filters */}
            {quickFiltersOpen && <div className="hs-quick-filters">
              <div className="hs-owner-dropdown" ref={ownerDropdownRef}>
                <button className={`hs-qf-btn hs-qf-trigger${ownerDropdownOpen ? ' open' : ''}`} onClick={() => setOwnerDropdownOpen(!ownerDropdownOpen)}>
                  <span>Survey owner</span>
                  <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                </button>
              </div>

              <button className="hs-qf-btn hs-qf-trigger" onClick={() => {}}>
                <span>Status</span>
                <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
              </button>

              <button className={`hs-qf-btn hs-qf-trigger${surveySort.field !== 'created_at' ? ' has-value' : ''}`} onClick={() => {
                const fields = ['created_at', 'name', 'total_responses', 'status']
                const idx = fields.indexOf(surveySort.field)
                const next = fields[(idx + 1) % fields.length]
                setSurveySort({ field: next, order: next === 'name' ? 'ASC' : 'DESC' })
                setSurveyPage(1)
              }}>
                <span>Sort: {{ created_at: 'Created', name: 'Name', total_responses: 'Responses', status: 'Status' }[surveySort.field] || 'Created'}</span>
                <svg className="hs-qf-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
              </button>

              <div className="hs-qf-sep" />

              <div className="hs-qf-more-wrap">
                <button className="hs-qf-btn hs-qf-more">
                  <svg width="10" height="10" viewBox="0 0 32 32" fill="currentColor"><path d="M15.57 29.29c-.63 0-1.14-.51-1.14-1.14V3a1.14 1.14 0 1 1 2.28 0v25.14c0 .63-.51 1.14-1.14 1.14Z"/><path d="M28.14 16.71H3a1.14 1.14 0 1 1 0-2.28h25.14a1.14 1.14 0 1 1 0 2.28"/></svg>
                  <span>More</span>
                </button>
              </div>
            </div>}

            <div className={`hs-content${tableSettingsOpen ? ' with-settings' : ''}`}>
              <div className="hs-table-area">
                {/* Row 4: Selection bar */}
                {surveySelectedRows.size > 0 && (
                  <div className="hs-selection-bar">
                    <span className="hs-sel-count">{surveySelectedRows.size} selected</span>
                    <button className="hs-sel-close" onClick={() => setSurveySelectedRows(new Set())} title="Deselect all">&times;</button>
                  </div>
                )}

                {surveysLoading && surveysData.length > 0 && <div className="hs-loading-bar" />}

                {/* Row 5: Data table */}
                <div className="hs-table-scroll">
                  {surveysLoading && surveysData.length === 0 ? (
                    <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                      <colgroup><col style={{ width: 40 }} /><col style={{ width: 200 }} /><col style={{ width: 80 }} /><col style={{ width: 90 }} /><col style={{ width: 100 }} /><col style={{ width: 100 }} /><col style={{ width: 80 }} /><col style={{ width: 80 }} /><col style={{ width: 130 }} /></colgroup>
                      <thead><tr>
                        <th className="hs-th-check"><input type="checkbox" disabled /></th>
                        <th className="hs-th-resizable" style={{ position: 'sticky', left: 40, zIndex: 2, background: '#f5f8fa' }}><span>Name</span></th>
                        <th className="hs-th-resizable"><span>Type</span></th>
                        <th className="hs-th-resizable"><span>Status</span></th>
                        <th className="hs-th-resizable"><span>Delivery</span></th>
                        <th className="hs-th-resizable"><span>Responses</span></th>
                        <th className="hs-th-resizable"><span>Score</span></th>
                        <th className="hs-th-resizable"><span>Rate</span></th>
                        <th className="hs-th-resizable"><span>Created</span></th>
                      </tr></thead>
                      <tbody>
                        <tr><td colSpan={9} className="hs-loading-cell"><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}><DotLottieReact src="/loading.lottie" loop autoplay style={{ width: 80, height: 80 }} /><span style={{ fontSize: '0.82rem', color: '#7c98b6', marginTop: 8 }}>Loading surveys...</span></div></td></tr>
                      </tbody>
                    </table>
                  ) : surveysData.length === 0 ? (
                    <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                      <colgroup><col style={{ width: 40 }} /><col style={{ width: 200 }} /><col style={{ width: 80 }} /><col style={{ width: 90 }} /><col style={{ width: 100 }} /><col style={{ width: 100 }} /><col style={{ width: 80 }} /><col style={{ width: 80 }} /><col style={{ width: 130 }} /></colgroup>
                      <thead><tr>
                        <th className="hs-th-check"><input type="checkbox" disabled /></th>
                        <th className="hs-th-resizable" style={{ position: 'sticky', left: 40, zIndex: 2, background: '#f5f8fa' }}><span>Name</span></th>
                        <th className="hs-th-resizable"><span>Type</span></th>
                        <th className="hs-th-resizable"><span>Status</span></th>
                        <th className="hs-th-resizable"><span>Delivery</span></th>
                        <th className="hs-th-resizable"><span>Responses</span></th>
                        <th className="hs-th-resizable"><span>Score</span></th>
                        <th className="hs-th-resizable"><span>Rate</span></th>
                        <th className="hs-th-resizable"><span>Created</span></th>
                      </tr></thead>
                      <tbody>
                        <tr><td colSpan={9} className="hs-empty-cell">
                          <div style={{ padding: '40px', textAlign: 'center' }}>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '12px' }}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                            <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#33475b', marginBottom: '6px' }}>Collect feedback with surveys</div>
                            <div style={{ fontSize: '0.82rem', color: '#7c98b6', maxWidth: '360px', margin: '0 auto' }}>Create NPS, CSAT, or CES surveys to measure customer satisfaction and gather actionable feedback.</div>
                          </div>
                        </td></tr>
                      </tbody>
                    </table>
                  ) : (
                    <table className={`hs-table hs-density-${tableRowHeight}${tableZebraStriping ? ' hs-zebra' : ''}`} style={{ tableLayout: 'fixed' }}>
                      <colgroup><col style={{ width: 40 }} /><col style={{ width: 200 }} /><col style={{ width: 80 }} /><col style={{ width: 90 }} /><col style={{ width: 100 }} /><col style={{ width: 100 }} /><col style={{ width: 80 }} /><col style={{ width: 80 }} /><col style={{ width: 130 }} /></colgroup>
                      <thead><tr>
                        <th className="hs-th-check"><input type="checkbox" checked={surveysData.length > 0 && surveySelectedRows.size === surveysData.length} onChange={toggleAllSurveyRows} /></th>
                        {[
                          { key: 'name', label: 'Name', sortField: 'name', frozen: true },
                          { key: 'type', label: 'Type', sortField: 'survey_type' },
                          { key: 'status', label: 'Status', sortField: 'status' },
                          { key: 'delivery', label: 'Delivery', sortField: '' },
                          { key: 'responses', label: 'Responses', sortField: 'total_responses' },
                          { key: 'score', label: 'Score', sortField: '' },
                          { key: 'rate', label: 'Rate', sortField: '' },
                          { key: 'created', label: 'Created', sortField: 'created_at' },
                        ].map(col => (
                          <th key={col.key} className={`hs-th-resizable${col.sortField ? ' hs-th-sortable' : ''}${col.frozen ? ' hs-th-frozen' : ''}`}
                            style={col.frozen ? { position: 'sticky', left: 40, top: 0, zIndex: 15, background: '#f8fafc' } : undefined}
                            onClick={() => {
                              if (!col.sortField) return
                              setSurveySort(prev => ({ field: col.sortField, order: prev.field === col.sortField && prev.order === 'ASC' ? 'DESC' : 'ASC' }))
                              setSurveyPage(1)
                            }}>
                            <span className="hs-th-content">
                              {col.label}
                              {col.sortField && surveySort.field === col.sortField && <span className="hs-sort-arrow">{surveySort.order === 'ASC' ? '\u2191' : '\u2193'}</span>}
                              {col.sortField && surveySort.field !== col.sortField && <span className="hs-th-hover-sort"><svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><path d="M29.38 21.96a1.147 1.147 0 0 0-1.617 0l-3.85 3.852V5.714a1.143 1.143 0 0 0-2.286 0v20.099l-3.853-3.852a1.144 1.144 0 0 0-1.615 1.617l5.803 5.802a1.143 1.143 0 0 0 1.616 0l5.802-5.802a1.144 1.144 0 0 0 0-1.617M15.84 8.422 10.039 2.62a1.145 1.145 0 0 0-1.616 0L2.62 8.422a1.144 1.144 0 0 0 1.617 1.617l3.85-3.851v20.098a1.143 1.143 0 0 0 2.286 0V6.187l3.853 3.852a1.14 1.14 0 0 0 1.616-.001 1.14 1.14 0 0 0-.001-1.616"/></svg></span>}
                            </span>
                            <div className="hs-col-resize-handle" onClick={(e) => e.stopPropagation()} />
                          </th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {surveysData.map((s: any) => (
                          <tr key={s.id} className={`hs-row${surveySelectedRows.has(s.id) ? ' selected' : ''}`} style={{ cursor: 'pointer' }} onClick={() => { fetchSurvey(s.id).then((r) => { setSurveyDetail(r.survey); setSurveyQuestions(r.questions); setSurveyStats(r.stats); setSurveyDetailTab('overview') }); fetchSurveyResponses(s.id).then((r) => setSurveyResponses(r.data)); fetchSurveyAnalytics(s.id).then(setSurveyAnalytics) }}>
                            <td className="hs-td-check" onClick={(e) => e.stopPropagation()}>
                              <input type="checkbox" checked={surveySelectedRows.has(s.id)} onChange={() => {
                                setSurveySelectedRows(prev => { const next = new Set(prev); next.has(s.id) ? next.delete(s.id) : next.add(s.id); return next })
                              }} />
                            </td>
                            <td className="hs-td-name" style={{ position: 'sticky', left: 40, zIndex: 2, background: surveySelectedRows.has(s.id) ? '#eff6ff' : '#fff' }}>
                              <div className="hs-name-cell">
                                <span className="hs-name-link" style={{ fontWeight: 500, color: '#33475b' }}>{s.name}</span>
                                <button className="hs-preview-btn" onClick={(e) => { e.stopPropagation() }}>Preview</button>
                              </div>
                            </td>
                            <td><span className="hs-status-badge" style={{ background: s.survey_type === 'nps' ? '#eff6ff' : s.survey_type === 'csat' ? '#f0fdf4' : s.survey_type === 'ces' ? '#fdf4ff' : '#fef3c7', color: s.survey_type === 'nps' ? '#2563eb' : s.survey_type === 'csat' ? '#166534' : s.survey_type === 'ces' ? '#7c3aed' : '#92400e' }}>{s.survey_type.toUpperCase()}</span></td>
                            <td><span className="hs-status-badge" style={{ background: s.status === 'active' ? '#f0fdf4' : '#f1f5f9', color: s.status === 'active' ? '#166534' : '#64748b' }}>{s.status}</span></td>
                            <td style={{ color: '#516f90', textTransform: 'capitalize' }}>{s.delivery_method}</td>
                            <td style={{ color: '#516f90' }}>{s.total_responses}</td>
                            <td style={{ fontWeight: 500 }}>{s.nps_score || s.avg_score || 0}</td>
                            <td style={{ color: '#516f90' }}>{s.total_sent > 0 ? `${Math.round(s.total_responses / s.total_sent * 100)}%` : '--'}</td>
                            <td style={{ color: '#516f90', fontSize: 13 }}>{s.created_at ? formatDatePref(s.created_at) : ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Row 6: Pagination */}
                <div className="hs-pagination">
                  <nav className="hs-paginator" aria-label="Pagination">
                    <button className="hs-paginator-btn" disabled={svCurrentPage <= 1} onClick={() => setSurveyPage(p => p - 1)} aria-label="Previous page">
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95"/></svg>
                      <span>Prev</span>
                    </button>
                    {(() => {
                      if (svTotalPages <= 7) {
                        const pages: number[] = []
                        for (let i = 1; i <= svTotalPages; i++) pages.push(i)
                        return pages.map(p => (<button key={p} className={`hs-paginator-num${p === svCurrentPage ? ' active' : ''}`} onClick={() => setSurveyPage(p)}>{p}</button>))
                      }
                      const items: React.ReactNode[] = []
                      const addPage = (p: number) => items.push(<button key={p} className={`hs-paginator-num${p === svCurrentPage ? ' active' : ''}`} onClick={() => setSurveyPage(p)}>{p}</button>)
                      const addEllipsis = (key: string) => items.push(<span key={key} className="hs-paginator-ellipsis">...</span>)
                      addPage(1)
                      if (svCurrentPage > 3) addEllipsis('el')
                      const start = Math.max(2, svCurrentPage - 1)
                      const end = Math.min(svTotalPages - 1, svCurrentPage + 1)
                      for (let i = start; i <= end; i++) addPage(i)
                      if (svCurrentPage < svTotalPages - 2) addEllipsis('er')
                      addPage(svTotalPages)
                      return items
                    })()}
                    <button className="hs-paginator-btn" disabled={svCurrentPage >= svTotalPages} onClick={() => setSurveyPage(p => p + 1)} aria-label="Next page">
                      <span>Next</span>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M11 27.71c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62l9.48-9.48-9.48-9.47a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0L22.1 15.48c.45.45.45 1.17 0 1.62L11.81 27.39c-.22.22-.52.33-.81.33Z"/></svg>
                    </button>
                  </nav>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', marginLeft: 'auto' }}><select value={tablePageSize} onChange={e => { setTablePageSize(Number(e.target.value) as any); setSurveyPage(1) }} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #e2e8f0', fontSize: '0.82rem' }}><option value={100}>100</option><option value={200}>200</option><option value={500}>500</option></select><span>per page</span></div>
                </div>
              </div>
            </div>

            {/* Row 7: Settings panel */}
            {tableSettingsOpen && (
              <div className="hs-settings-panel">
                <div className="hs-settings-header">
                  <span className="hs-settings-title">Table settings</span>
                  <button className="hs-settings-close" onClick={() => setTableSettingsOpen(false)}>
                    <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                  </button>
                </div>
                <div className="hs-settings-body">
                  <div className="hs-settings-group">
                    <label className="hs-settings-label">Pagination
                      <span className="hs-info-icon" title="Number of rows shown per page"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span>
                    </label>
                    {([100, 200, 500] as const).map(size => (
                      <label className="hs-settings-radio" key={size}>
                        <input type="radio" name="svPageSize" checked={tablePageSize === size} onChange={() => { setTablePageSize(size); setSurveyPage(1) }} />
                        <span className="hs-radio-indicator" /><span>{size} per page</span>
                      </label>
                    ))}
                  </div>
                  <hr className="hs-settings-divider" />
                  <div className="hs-settings-group">
                    <label className="hs-settings-label">Row height
                      <span className="hs-info-icon" title="Adjust the vertical spacing of table rows"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span>
                    </label>
                    {([{ value: 'small' as const, label: 'Compact' }, { value: 'medium' as const, label: 'Default' }, { value: 'large' as const, label: 'Comfortable' }]).map(opt => (
                      <label className="hs-settings-radio" key={opt.value}>
                        <input type="radio" name="svRowHeight" checked={tableRowHeight === opt.value} onChange={() => setTableRowHeight(opt.value)} />
                        <span className="hs-radio-indicator" /><span>{opt.label}</span>
                      </label>
                    ))}
                  </div>
                  <hr className="hs-settings-divider" />
                  <div className="hs-settings-group">
                    <div className="hs-settings-toggle-row">
                      <label className="hs-settings-label">Zebra striping
                        <span className="hs-info-icon" title="Alternate row background colors for easier reading"><svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M15.86 31.86c-8.83 0-16-7.18-16-16s7.17-16 16-16 16 7.18 16 16-7.18 16-16 16m0-29.72C8.3 2.14 2.15 8.29 2.15 15.85S8.3 29.56 15.86 29.56s13.71-6.15 13.71-13.71S23.42 2.14 15.86 2.14"/><path d="M15.86 25c-.63 0-1.14-.51-1.14-1.14V13.57a1.14 1.14 0 1 1 2.28 0v10.29c0 .63-.51 1.14-1.14 1.14"/><circle cx="15.86" cy="8.43" r="1.71"/></svg></span>
                      </label>
                      <button className={`hs-toggle${tableZebraStriping ? ' on' : ''}`} onClick={() => setTableZebraStriping(!tableZebraStriping)} role="switch" aria-checked={tableZebraStriping}>
                        <span className="hs-toggle-track"><span className="hs-toggle-on">ON</span><span className="hs-toggle-off">OFF</span><span className="hs-toggle-handle" /></span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {showCreateSurvey && (<div className="modal-overlay" onClick={() => setShowCreateSurvey(false)}><div className="modal-card" style={{ maxWidth: 500 }} onClick={(e) => e.stopPropagation()}><button className="modal-close" onClick={() => setShowCreateSurvey(false)}>&times;</button><div className="modal-body"><h3 className="modal-title">Create Survey</h3><div style={{ marginBottom: 16 }}><label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 500, color: '#33475b', marginBottom: 4 }}>Type</label><div style={{ display: 'flex', gap: 8 }}>{(['nps', 'csat', 'ces', 'custom'] as const).map(t => (<button key={t} className="cmd-filter-pill" style={{ background: newSurveyType === t ? '#eff6ff' : '#f8fafc', borderColor: newSurveyType === t ? '#93c5fd' : '#e2e8f0', color: newSurveyType === t ? '#2563eb' : '#64748b', textTransform: 'uppercase', fontSize: '0.72rem', fontWeight: 600 }} onClick={() => { setNewSurveyType(t); setNewSurveyTitle(t === 'nps' ? 'How likely are you to recommend us?' : t === 'csat' ? 'How satisfied were you?' : t === 'ces' ? 'How easy was it to get help?' : '') }}>{t}</button>))}</div></div><div style={{ marginBottom: 16 }}><label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 500, color: '#33475b', marginBottom: 4 }}>Name *</label><input value={newSurveyName} onChange={(e) => setNewSurveyName(e.target.value)} style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem' }} /></div><div style={{ marginBottom: 16 }}><label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 500, color: '#33475b', marginBottom: 4 }}>Title</label><input value={newSurveyTitle} onChange={(e) => setNewSurveyTitle(e.target.value)} style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem' }} /></div>{newSurveyType === 'nps' && (<div style={{ padding: 16, background: '#f8fafc', borderRadius: 8, textAlign: 'center', marginBottom: 16 }}><div style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 12 }}>{newSurveyTitle}</div><div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.68rem', color: '#94a3b8', marginBottom: 4 }}><span>Not likely</span><span>Extremely likely</span></div><div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>{Array.from({ length: 11 }, (_, i) => (<div key={i} style={{ width: 30, height: 30, borderRadius: 4, border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', fontWeight: 600, background: i <= 6 ? '#fef2f2' : i <= 8 ? '#fef3c7' : '#f0fdf4', color: i <= 6 ? '#ef4444' : i <= 8 ? '#f59e0b' : '#22c55e' }}>{i}</div>))}</div></div>)}{newSurveyType === 'csat' && (<div style={{ padding: 16, background: '#f8fafc', borderRadius: 8, textAlign: 'center', marginBottom: 16 }}><div style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 12 }}>{newSurveyTitle}</div><div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>{[{emoji:'\u{1F621}',label:'Very Unsatisfied',bg:'#fef2f2'},{emoji:'\u{1F61F}',label:'Unsatisfied',bg:'#fff7ed'},{emoji:'\u{1F610}',label:'Neutral',bg:'#fefce8'},{emoji:'\u{1F642}',label:'Satisfied',bg:'#f0fdf4'},{emoji:'\u{1F604}',label:'Very Satisfied',bg:'#ecfdf5'}].map((item,i)=>(<div key={i} style={{display:'flex',flexDirection:'column',alignItems:'center',gap:4,cursor:'pointer'}}><div style={{width:40,height:40,borderRadius:'50%',background:item.bg,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'1.4rem',border:'2px solid transparent'}}>{item.emoji}</div><span style={{fontSize:'0.58rem',color:'#64748b',maxWidth:60,lineHeight:1.2}}>{item.label}</span></div>))}</div></div>)}{newSurveyType === 'ces' && (<div style={{ padding: 16, background: '#f8fafc', borderRadius: 8, textAlign: 'center', marginBottom: 16 }}><div style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 12 }}>{newSurveyTitle}</div><div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.68rem', color: '#94a3b8', marginBottom: 4 }}><span>Strongly Disagree</span><span>Strongly Agree</span></div><div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>{Array.from({ length: 7 }, (_, i) => (<div key={i} style={{ width: 36, height: 30, borderRadius: 4, border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', fontWeight: 600, background: i <= 1 ? '#fef2f2' : i <= 2 ? '#fff7ed' : i === 3 ? '#fefce8' : i <= 4 ? '#f0fdf4' : '#ecfdf5', color: i <= 1 ? '#ef4444' : i <= 2 ? '#f97316' : i === 3 ? '#eab308' : i <= 4 ? '#22c55e' : '#059669' }}>{i + 1}</div>))}</div></div>)}<div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><button onClick={() => setShowCreateSurvey(false)} style={{ padding: '9px 16px', border: '1px solid #cbd6e2', borderRadius: 4, background: '#fff', color: '#33475b', fontSize: '0.85rem', cursor: 'pointer' }}>Cancel</button><button disabled={!newSurveyName.trim()} onClick={() => { const dq = newSurveyType === 'nps' ? [{ question_type: 'nps_scale', question_text: newSurveyTitle, is_required: true, scale_min: 0, scale_max: 10 }, { question_type: 'comment_box', question_text: 'Reason for your score?', is_required: false }] : newSurveyType === 'csat' ? [{ question_type: 'csat_scale', question_text: newSurveyTitle, is_required: true }] : newSurveyType === 'ces' ? [{ question_type: 'ces_scale', question_text: newSurveyTitle, is_required: true, scale_min: 1, scale_max: 7 }] : []; createSurvey({ name: newSurveyName, survey_type: newSurveyType, title: newSurveyTitle, delivery_method: 'link', questions: dq }).then(() => { setShowCreateSurvey(false); fetchSurveys({}).then((r) => { setSurveysData(r.data); setSurveysTotal(r.total) }) }).catch((e) => alert(e.message)) }} style={{ padding: '9px 20px', border: 'none', borderRadius: 4, background: '#ff7a59', color: '#fff', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', opacity: newSurveyName.trim() ? 1 : 0.5 }}>Create</button></div></div></div></div>)}
          </div>
          )
        })()}

        {/* ========== Survey Detail ========== */}
        {activeView === 'feedback-surveys' && surveyDetail && (
          <div className="hs-contacts" style={{ display: 'flex', flexDirection: 'column' }}>
            <div className="hs-topbar"><div className="hs-topbar-left"><button style={{ background: 'none', border: 'none', color: '#1976d2', fontSize: '0.82rem', cursor: 'pointer', padding: '0 12px' }} onClick={() => { setSurveyDetail(null); fetchSurveys({}).then((r) => { setSurveysData(r.data); setSurveysTotal(r.total) }) }}>&larr; Back</button><div className="hs-topbar-sep" /><span style={{ fontWeight: 600, fontSize: '0.88rem', color: '#33475b', padding: '0 8px' }}>{surveyDetail.name}</span><span className="hs-status-badge" style={{ background: surveyDetail.survey_type === 'nps' ? '#eff6ff' : '#f0fdf4', color: surveyDetail.survey_type === 'nps' ? '#2563eb' : '#166534' }}>{surveyDetail.survey_type.toUpperCase()}</span></div><div className="hs-topbar-right" style={{ display: 'flex', gap: 8 }}>{surveyDetail.status === 'draft' && <button className="hs-add-contacts-btn" onClick={() => activateSurvey(surveyDetail.id).then(() => setSurveyDetail({ ...surveyDetail, status: 'active' }))}>Activate</button>}{surveyDetail.status === 'active' && <button style={{ padding: '7px 14px', border: '1px solid #cbd6e2', borderRadius: 4, background: '#fff', color: '#33475b', fontSize: '0.82rem', cursor: 'pointer' }} onClick={() => pauseSurvey(surveyDetail.id).then(() => setSurveyDetail({ ...surveyDetail, status: 'paused' }))}>Pause</button>}</div></div>
            <div style={{ borderBottom: '1px solid var(--line)', display: 'flex', padding: '0 12px' }}>{(['overview', 'responses', 'settings'] as const).map(tab => (<button key={tab} className={`hs-view-tab${surveyDetailTab === tab ? ' active' : ''}`} onClick={() => setSurveyDetailTab(tab)} style={{ textTransform: 'capitalize' }}>{tab}</button>))}</div>
            <div className="hs-content" style={{ flex: 1, overflow: 'auto', padding: '16px 20px' }}>
              {surveyDetailTab === 'overview' && surveyStats && (<><div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}><div className="kpi" style={{ flex: '1 1 100px', padding: '5px 10px', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 8 }}><span style={{ fontSize: '0.7rem', color: 'var(--muted)', textTransform: 'uppercase', display: 'block' }}>RESPONSES</span><strong style={{ fontSize: '0.78rem' }}>{surveyStats.total_responses}</strong></div>{surveyDetail.survey_type === 'nps' && <><div className="kpi" style={{ flex: '1 1 100px', padding: '5px 10px', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 8 }}><span style={{ fontSize: '0.7rem', color: 'var(--muted)', textTransform: 'uppercase', display: 'block' }}>NPS</span><strong style={{ fontSize: '0.78rem', color: surveyStats.nps_score >= 50 ? '#22c55e' : surveyStats.nps_score >= 0 ? '#f59e0b' : '#ef4444' }}>{surveyStats.nps_score}</strong></div><div className="kpi" style={{ flex: '1 1 100px', padding: '5px 10px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8 }}><span style={{ fontSize: '0.7rem', color: '#166534', textTransform: 'uppercase', display: 'block' }}>PROMOTERS</span><strong style={{ fontSize: '0.78rem', color: '#22c55e' }}>{surveyStats.promoters}</strong></div><div className="kpi" style={{ flex: '1 1 100px', padding: '5px 10px', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8 }}><span style={{ fontSize: '0.7rem', color: '#92400e', textTransform: 'uppercase', display: 'block' }}>PASSIVES</span><strong style={{ fontSize: '0.78rem', color: '#f59e0b' }}>{surveyStats.passives}</strong></div><div className="kpi" style={{ flex: '1 1 100px', padding: '5px 10px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8 }}><span style={{ fontSize: '0.7rem', color: '#dc2626', textTransform: 'uppercase', display: 'block' }}>DETRACTORS</span><strong style={{ fontSize: '0.78rem', color: '#ef4444' }}>{surveyStats.detractors}</strong></div></>}</div>{surveyDetail.survey_type === 'nps' && surveyAnalytics?.score_distribution?.length > 0 && (<div style={{ marginBottom: 24 }}><h3 style={{ fontSize: '0.84rem', fontWeight: 700, marginBottom: 12 }}>Score Distribution</h3><div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>{Array.from({ length: 11 }, (_, i) => 10 - i).map(score => { const item = surveyAnalytics.score_distribution.find((d: any) => d.score === score); const count = item?.count || 0; const maxC = Math.max(...surveyAnalytics.score_distribution.map((d: any) => d.count), 1); const bc = score >= 9 ? '#22c55e' : score >= 7 ? '#f59e0b' : '#ef4444'; return (<div key={score} style={{ display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ width: 24, textAlign: 'right', fontSize: '0.75rem', fontWeight: 600, color: '#64748b' }}>{score}</span><div style={{ flex: 1, height: 20, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}><div style={{ height: '100%', width: `${(count / maxC) * 100}%`, background: bc, borderRadius: 4, minWidth: count > 0 ? 4 : 0 }} /></div><span style={{ fontSize: '0.72rem', color: '#64748b', width: 60 }}>{count} ({item?.percent || 0}%)</span></div>) })}</div></div>)}{surveyAnalytics?.recent_comments?.length > 0 && (<div><h3 style={{ fontSize: '0.84rem', fontWeight: 700, marginBottom: 8 }}>Recent Comments</h3>{surveyAnalytics.recent_comments.map((c: any, i: number) => (<div key={i} style={{ padding: '8px 12px', marginBottom: 6, background: '#f8fafc', borderRadius: 6, borderLeft: `3px solid ${(c.nps_score ?? 10) >= 9 ? '#22c55e' : (c.nps_score ?? 5) >= 7 ? '#f59e0b' : '#ef4444'}` }}><div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: 2 }}><strong>{c.respondent_name || 'Anonymous'}</strong> - Score: {c.nps_score ?? '--'}</div><div style={{ fontSize: '0.82rem' }}>{c.follow_up_comment}</div></div>))}</div>)}</>)}
              {surveyDetailTab === 'responses' && (<div className="hs-table-scroll"><table className="hs-table"><thead><tr><th>RESPONDENT</th><th>SCORE</th><th>CATEGORY</th><th>COMMENT</th><th>DATE</th></tr></thead><tbody>{surveyResponses.map((r: any) => (<tr key={r.id}><td style={{ fontWeight: 500, fontSize: '0.82rem' }}>{r.respondent_name || 'Anonymous'}</td><td><span style={{ fontWeight: 700, fontSize: '0.88rem', color: (r.nps_score ?? 10) >= 9 ? '#22c55e' : (r.nps_score ?? 5) >= 7 ? '#f59e0b' : '#ef4444' }}>{r.nps_score ?? r.csat_score ?? r.ces_score ?? '--'}</span></td><td>{r.nps_category ? <span className="hs-status-badge" style={{ background: r.nps_category === 'promoter' ? '#f0fdf4' : r.nps_category === 'passive' ? '#fef3c7' : '#fef2f2', color: r.nps_category === 'promoter' ? '#166534' : r.nps_category === 'passive' ? '#92400e' : '#dc2626' }}>{r.nps_category}</span> : '--'}</td><td style={{ fontSize: '0.8rem', color: '#516f90', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.follow_up_comment || '--'}</td><td style={{ fontSize: '0.8rem', color: '#516f90' }}>{r.submitted_at ? formatDatePref(r.submitted_at) : ''}</td></tr>))}{surveyResponses.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', color: '#7c98b6', padding: 20 }}>No responses yet</td></tr>}</tbody></table></div>)}
              {surveyDetailTab === 'settings' && (<div style={{ maxWidth: 500 }}><div style={{ marginBottom: 12 }}><label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 500, color: '#33475b', marginBottom: 4 }}>Name</label><input value={surveyDetail.name} onChange={(e) => setSurveyDetail({ ...surveyDetail, name: e.target.value })} style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem' }} /></div><div style={{ display: 'flex', gap: 8, marginTop: 16 }}><button className="hs-add-contacts-btn" onClick={() => updateSurvey(surveyDetail.id, { name: surveyDetail.name })}>Save</button>{surveyDetail.status !== 'closed' && <button style={{ padding: '7px 14px', border: '1px solid #dc2626', borderRadius: 4, background: '#fff', color: '#dc2626', fontSize: '0.82rem', cursor: 'pointer' }} onClick={() => closeSurvey(surveyDetail.id).then(() => setSurveyDetail({ ...surveyDetail, status: 'closed' }))}>Close Survey</button>}</div></div>)}
            </div>
          </div>
        )}

        {/* ========== Contact / Company Record Loading State ========== */}
        {(activeView === 'contacts' || activeView === 'companies') && contactDetailOpen && !previewClient && (
          <div className="crp-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <DotLottieReact src="/loading.lottie" loop autoplay style={{ width: 120, height: 120 }} />
              <span style={{ fontSize: '0.88rem', color: '#7c98b6', marginTop: 12 }}>Loading {activeView === 'companies' ? 'client' : 'contact'}...</span>
            </div>
          </div>
        )}

        {/* ========== Contact Record Detail Page (HubSpot-Style) ========== */}
        {contactDetailOpen && previewClient && (
          <div className="crp-page">
            {/* ---- LEFT SIDEBAR ---- */}
            <div className="crp-left">
              <div className="crp-left-scroll">
                {/* Highlight Card (breadcrumb + avatar + actions) */}
                <div className="crp-card">
                  <div className="crp-header-strip" data-bp-marker="bp-orgrec-v2">
                    <span style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#0091ae' }}>{previewClient.client_type === 'organization' ? 'Client' : 'Contact Record'}</span>
                  </div>
                  <div className="crp-breadcrumb">
                    <button className="crp-back-btn" onClick={() => { setContactDetailOpen(false); window.location.hash = contactDetailReturnHash.current }}>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95"/></svg>
                      <span>Back</span>
                    </button>
                    <div className="crp-actions-wrap" style={{ position: 'relative' }}>
                      <button className="crp-actions-label" onClick={() => setCrpActionsOpen(!crpActionsOpen)}>
                        Actions
                        <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                      </button>
                      {crpActionsOpen && (
                        <>
                          <div className="cmd-backdrop-light" onClick={() => setCrpActionsOpen(false)} />
                          <div className="hs-viewtype-panel" style={{ position: 'fixed', right: 'auto', left: (() => { const el = document.querySelector('.crp-actions-wrap'); if (el) { const r = el.getBoundingClientRect(); return r.left } return 100 })(), top: (() => { const el = document.querySelector('.crp-actions-wrap'); if (el) { const r = el.getBoundingClientRect(); return r.bottom + 4 } return 60 })(), minWidth: 200 }}>
                            <button className="hs-viewtype-option" onClick={() => { if (previewClient.preferred_name) window.open(`https://google.com/search?q=${encodeURIComponent(previewClient.preferred_name)}`, '_blank'); setCrpActionsOpen(false) }}>
                              Search in Google
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginLeft: 4 }}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                            </button>
                            <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '4px 0' }} />
                            <button
                              className="hs-viewtype-option"
                              onClick={async () => {
                                const archive = !previewClient.archived_at
                                setCrpActionsOpen(false)
                                try {
                                  await archiveClients([previewClient.unified_client_id], archive)
                                  setPreviewClient({ ...previewClient, archived_at: archive ? new Date().toISOString() : null })
                                } catch (e: any) {
                                  alert(`Failed to ${archive ? 'archive' : 'unarchive'} contact: ` + (e?.message || String(e)))
                                }
                              }}
                            >
                              {previewClient.archived_at ? 'Unarchive contact' : 'Archive contact'}
                            </button>
                            {(previewClient.client_type === 'organization' ? (myCapabilities ? myCapabilities.delete_organization !== false : true) : (myCapabilities ? myCapabilities.delete_contact !== false : true)) && (
                            <button
                              className="hs-viewtype-option"
                              style={{ color: '#dc2626' }}
                              onClick={() => {
                                setCrpActionsOpen(false)
                                setBulkDeleteModal({ ids: new Set([previewClient.unified_client_id]), deleting: false })
                              }}
                            >
                              {previewClient.client_type === 'organization' ? 'Delete client' : 'Delete contact'}
                            </button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="crp-highlight">
                    {previewClient.client_type === 'organization' ? (() => {
                      const orgLogoUrl = previewClient.logo_url?.trim() || null
                      const orgLogoShape = previewClient.logo_shape === 'rect' ? 'rect' : 'circle'
                      const cacheBustLogo = (url: string) => `${url}${url.includes('?') ? '&' : '?'}_t=${Date.now()}`
                      const stageOrgLogoFile = (event: React.ChangeEvent<HTMLInputElement>) => {
                        const file = event.currentTarget.files?.[0]
                        event.currentTarget.value = ''
                        if (!file) return
                        if (file.size > 25 * 1024 * 1024) {
                          alert('Image too large (25MB max)')
                          return
                        }
                        setOrgLogoCropFile(file)
                      }
                      const adjustCurrentOrgLogo = async () => {
                        if (!orgLogoUrl) return
                        try {
                          setOrgLogoCropFile(await fileFromImageUrl(orgLogoUrl, 'organization-logo'))
                        } catch (error: any) {
                          alert(error?.message || 'Could not load the current client logo')
                        }
                      }
                      const orgId = previewClient.unified_client_id
                      return (
                        <>
                        <div className={`crp-avatar-lg crp-org-avatar${orgLogoUrl && orgLogoShape === 'rect' ? ' crp-org-avatar--rect' : ''}`} aria-label="Client logo">
                          {orgLogoUrl ? (
                            <img src={orgLogoUrl} alt={`${clientDisplayNameClean(previewClient)} logo`} />
                          ) : (
                            getClientInitial(previewClient)
                          )}
                          <div className="crp-org-avatar-overlay" aria-label="Client logo actions">
                            {orgLogoUrl ? (
                              <button
                                type="button"
                                className="crp-org-avatar-action"
                                aria-label="Adjust client logo"
                                title="Adjust client logo"
                                onClick={() => void adjustCurrentOrgLogo()}
                              >
                                <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
                              </button>
                            ) : (
                              <label
                                className="crp-org-avatar-action"
                                aria-label="Upload client logo"
                                title="Upload client logo"
                              >
                                <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/></svg>
                                <input type="file" accept="image/*" onChange={stageOrgLogoFile} />
                              </label>
                            )}
                            {orgLogoUrl && (
                              <button
                                type="button"
                                className="crp-org-avatar-action crp-org-avatar-action--remove"
                                aria-label="Remove client logo"
                                title="Remove client logo"
                                onClick={async () => {
                                  if (!confirm('Remove this client logo?')) return
                                  try {
                                    const removed = await deleteOrgLogo(orgId)
                                    if (!removed.ok) throw new Error('The client logo could not be removed.')
                                    setPreviewClient((current) => current && current.unified_client_id === orgId
                                      ? { ...current, logo_url: null, logo_shape: 'circle' }
                                      : current)
                                  } catch (error: any) {
                                    alert(error?.message || 'Failed to remove client logo')
                                  }
                                }}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </div>
                        {orgLogoCropFile && (
                          <PhotoCropModal
                            key={`${orgId}:${orgLogoCropFile.name}:${orgLogoCropFile.lastModified}`}
                            file={orgLogoCropFile}
                              title="Adjust client logo"
                            aspect={orgLogoShape === 'rect' ? 'wide' : 'square'}
                            allowAspectToggle
                            round
                            onCancel={() => setOrgLogoCropFile(null)}
                            onSave={async (blob, name, selectedAspect) => {
                              const shape = selectedAspect === 'wide' ? 'rect' : 'circle'
                              const uploaded = await uploadOrgLogo(orgId, new File([blob], `organization-logo${name.endsWith('.webp') ? '.webp' : '.jpg'}`, { type: blob.type }), shape)
                              if (!uploaded.ok) throw new Error('The client logo could not be uploaded.')
                              const nextLogoUrl = typeof uploaded.logo_url === 'string' ? uploaded.logo_url.trim() : ''
                              setPreviewClient((current) => current && current.unified_client_id === orgId
                                ? { ...current, logo_url: nextLogoUrl ? cacheBustLogo(nextLogoUrl) : null, logo_shape: shape }
                                : current)
                              setOrgLogoCropFile(null)
                            }}
                          />
                        )}
                        </>
                      )
                    })() : (
                      <div className="crp-avatar-lg">{getClientInitial(previewClient)}</div>
                    )}
                    <div className="crp-highlight-info">
                      {/* Editable name --------------------------------------------------- */}
                      {crpEditingField === 'name' ? (
                        <>
                        <input
                          autoFocus
                          className={`crp-name${crpNameError ? ' cp-input-error' : ''}`}
                          style={{ width: '100%', border: '1px solid #cbd6e2', borderRadius: 4, padding: '2px 6px', font: 'inherit', color: 'inherit', background: '#fff' }}
                          value={crpEditDraft}
                          disabled={crpEditSaving}
                          onChange={(e) => { setCrpEditDraft(e.target.value); if (crpNameError) setCrpNameError(null) }}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') { setCrpEditingField(null); setCrpEditDraft(''); setCrpNameError(null) }
                            else if (e.key === 'Enter') { e.currentTarget.blur() }
                          }}
                          onBlur={async () => {
                            const next = crpEditDraft.trim()
                            const prev = previewClient.preferred_name || ''
                            if (!next || next === prev) { setCrpEditingField(null); setCrpEditDraft(''); setCrpNameError(null); return }
                            // Validate by the SUBMITTED type (never the dirty stored type):
                            // orgs permit '&'/' and '/commas; persons reject them with a
                            // swap (comma) or split ('&'/' and ') affordance.
                            const v = previewClient.client_type === 'organization'
                              ? validateOrgNameInput(next)
                              : validatePersonNameInput(next)
                            if (!v.ok) { setCrpNameError({ message: v.message, suggestion: v.suggestion }); return }
                            await commitContactName(previewClient, next)
                          }}
                        />
                        {crpNameError && (
                          <div className="cp-validation-error" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginTop: 3 }}>
                            <span>{crpNameError.message}</span>
                            {crpNameError.suggestion?.kind === 'swap' && (
                              <button
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => { const val = (crpNameError.suggestion as any).value as string; setCrpEditDraft(val); commitContactName(previewClient, val) }}
                                style={{ background: '#0091ae', color: '#fff', border: 'none', borderRadius: 5, padding: '2px 8px', fontSize: '0.72rem', cursor: 'pointer' }}
                              >Use “{(crpNameError.suggestion as any).value}”</button>
                            )}
                            {crpNameError.suggestion?.kind === 'split' && (
                              <button
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => splitContactNameIntoPersons(previewClient, (crpNameError.suggestion as any).persons as string[])}
                                style={{ background: '#0091ae', color: '#fff', border: 'none', borderRadius: 5, padding: '2px 8px', fontSize: '0.72rem', cursor: 'pointer' }}
                              >Split into {(crpNameError.suggestion as any).persons.length} contact people</button>
                            )}
                          </div>
                        )}
                        </>
                      ) : (
                        <h2
                          className="crp-name"
                          style={{ cursor: 'pointer' }}
                          title="Click to edit legal name"
                          onClick={() => { setCrpEditingField('name'); setCrpEditDraft(previewClient.preferred_name || ''); setCrpNameError(null) }}
                        >
                          {clientDisplayNameClean(previewClient)}
                          {crpEditSaving && crpEditingField === null && <span style={{ fontSize: '0.7rem', fontWeight: 400, color: '#7c98b6', marginLeft: 6 }}>saving...</span>}
                        </h2>
                      )}

                      {/* Contact type subtitle removed 2026-04-23 — a contact is
                          always a person in this CRM (organizations live on the
                          Companies view). The click-to-change dropdown was a
                          leftover from the unified-client model. */}

                      {previewClient.archived_at && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 10, fontSize: '0.68rem', fontWeight: 600, background: '#fef3c7', color: '#92400e', marginTop: 2 }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 8v13H3V8"/><path d="M1 3h22v5H1z"/><path d="M10 12h4"/></svg>
                          Archived
                        </span>
                      )}

                      {/* Editable primary email — persons only. Organizations edit
                          email in Key Information below, so the title-box "No email" +
                          pencil affordance is hidden for organizations. */}
                      {previewClient.client_type !== 'organization' && (crpEditingField === 'email' ? (
                        <input
                          autoFocus
                          type="email"
                          className="crp-email-link"
                          style={{ width: '100%', border: '1px solid #cbd6e2', borderRadius: 4, padding: '2px 6px', font: 'inherit', color: 'inherit', background: '#fff' }}
                          value={crpEditDraft}
                          disabled={crpEditSaving}
                          onChange={(e) => setCrpEditDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') { setCrpEditingField(null); setCrpEditDraft('') }
                            else if (e.key === 'Enter') { e.currentTarget.blur() }
                          }}
                          onBlur={async () => {
                            const next = crpEditDraft.trim()
                            const prev = previewClient.preferred_email || ''
                            if (next === prev) { setCrpEditingField(null); setCrpEditDraft(''); return }
                            const original = previewClient
                            const originalEmails = contactEmails
                            setCrpEditSaving(true)
                            // Optimistic cache update on the contact spine
                            setPreviewClient({ ...original, preferred_email: next || null as any })
                            try {
                              const primaryRow = contactEmails.find(r => r.is_primary) || null
                              if (primaryRow) {
                                const resp = await fetch(`/api/crm/contacts/${encodeURIComponent(original.unified_client_id)}/emails/${primaryRow.id}`, {
                                  method: 'PATCH',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ address: next }),
                                })
                                if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
                              } else if (next) {
                                const resp = await fetch(`/api/crm/contacts/${encodeURIComponent(original.unified_client_id)}/emails`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ address: next, is_primary: true }),
                                })
                                if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
                              }
                              await refreshContactEmails(original.unified_client_id)
                            } catch (err: any) {
                              setPreviewClient(original)
                              setContactEmails(originalEmails)
                              alert('Failed to save email: ' + (err?.message || String(err)))
                            } finally {
                              setCrpEditSaving(false)
                              setCrpEditingField(null)
                              setCrpEditDraft('')
                            }
                          }}
                        />
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {previewClient.preferred_email ? (
                            <a className="crp-email-link" href={`mailto:${previewClient.preferred_email}`}>{previewClient.preferred_email}</a>
                          ) : (
                            <span className="crp-email-link" style={{ color: '#7c98b6', fontStyle: 'italic' }}>No email</span>
                          )}
                          <button
                            type="button"
                            title="Edit primary email"
                            onClick={() => { setCrpEditingField('email'); setCrpEditDraft(previewClient.preferred_email || '') }}
                            style={{ background: 'none', border: 'none', padding: 2, cursor: 'pointer', color: '#7c98b6', display: 'inline-flex', alignItems: 'center' }}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="crp-action-btns">
                  <button
                    className="crp-action-btn"
                    title="Note"
                    onClick={() => {
                      setContactDetailTab('activities')
                      setCrpActivityFilter('notes')
                      setContactTaskOpen(false)
                      setContactMeetingOpen(false)
                      setContactNoteOpen(true)
                      setContactNoteText('')
                      setContactNoteFiles([])
                      window.location.hash = `/${activeView === 'companies' ? 'clients' : 'contacts'}/record/${encodeURIComponent(previewClient.unified_client_id)}/activities`
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                    <span>Note</span>
                  </button>
                  <button className="crp-action-btn" title="Email" onClick={() => {
                    setComposeContext({
                      to: previewClient.preferred_email ? [previewClient.preferred_email] : [],
                      entityType: 'contact',
                      entityId: previewClient.unified_client_id,
                      entityLabel: `Contact · ${previewClient.preferred_name || previewClient.preferred_email || 'unknown'}`,
                    })
                    setComposeOpen(true)
                  }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                    <span>Email</span>
                  </button>
                  <button className="crp-action-btn" title="Call" onClick={() => { if (previewClient?.preferred_phone) handleClickToCall(previewClient.preferred_phone, undefined, previewClient.unified_client_id, previewClient.preferred_name) }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                    <span>Call</span>
                  </button>
                  <button
                    className="crp-action-btn"
                    title="Task"
                    onClick={() => {
                      setContactDetailTab('activities')
                      setCrpActivityFilter('tasks')
                      setContactNoteOpen(false)
                      setContactMeetingOpen(false)
                      setContactTaskOpen(true)
                      setContactTaskForm({ title: '', body: '', priority: 'medium', dueDate: '', assignedTo: null })
                      window.location.hash = `/${activeView === 'companies' ? 'clients' : 'contacts'}/record/${encodeURIComponent(previewClient.unified_client_id)}/activities`
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                    <span>Task</span>
                  </button>
                  </div>
                </div>

                {/* Key Information Card */}
                <div className="crp-card">
                  <div className="crp-card-header">
                    <h3>Key information</h3>
                  </div>
                  <div className="crp-card-body">
                    {/* Multi-email field: list each row with role badges; click to edit all at once. */}
                    <div className="crp-field">
                      <label>Email{contactEmails.length > 1 ? 's' : ''}</label>
                      <div
                        className="crp-field-editable"
                        onClick={() => previewClient && setCellEditModal({
                          clientId: previewClient.unified_client_id,
                          field: 'email',
                          emails: contactEmails.map(r => r.address),
                          emailRows: contactEmails.length > 0
                            ? contactEmails.map(r => ({ id: r.id, address: r.address, is_primary: !!r.is_primary, is_billing: !!r.is_billing }))
                            : [{ address: previewClient?.preferred_email || '', is_primary: true, is_billing: false }],
                          phones: [],
                        })}
                        style={{ flexDirection: 'column', alignItems: 'stretch', gap: 4 }}
                      >
                        {contactEmails.length === 0 ? (
                          <span>{previewClient?.preferred_email || '--'}</span>
                        ) : (
                          contactEmails.map(r => (
                            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                              <span>{r.address}</span>
                              {!!r.is_primary && <span style={{ fontSize: '0.62rem', padding: '1px 6px', borderRadius: 8, background: '#dcfce7', color: '#15803d', fontWeight: 600 }}>Primary</span>}
                              {!!r.is_billing && <span style={{ fontSize: '0.62rem', padding: '1px 6px', borderRadius: 8, background: '#e0f2fe', color: '#0369a1', fontWeight: 600 }}>Billing</span>}
                            </div>
                          ))
                        )}
                        <svg className="crp-field-edit-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ alignSelf: 'flex-end' }}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                      </div>
                    </div>
                    <div className="crp-field">
                      <label>Phone</label>
                      {(() => {
                        // Coherence seam: show the primary contact_phones row (fallback to legacy
                        // preferred_phone) and edit via the multi-phone cell-edit modal, so this
                        // row can never contradict the About card's phone list. The modal's save
                        // path rewrites contact_phones and the backend re-syncs preferred_phone.
                        const sortedPhones = [...(contactPhones || [])].sort((a: any, b: any) => Number(b.is_primary) - Number(a.is_primary))
                        const keyInfoPhone = sortedPhones[0]?.number || previewClient.preferred_phone || ''
                        return (
                          <div className="crp-field-editable" onClick={() => previewClient && setCellEditModal({
                            clientId: previewClient.unified_client_id,
                            field: 'phone',
                            emails: [],
                            phones: sortedPhones.length > 0
                              ? sortedPhones.map((p: any) => ({ type: p.label || 'Mobile', number: p.number, ext: p.ext || '' }))
                              : [{ type: 'Mobile', number: previewClient.preferred_phone || '', ext: '' }],
                          })}>
                            <span>{keyInfoPhone ? formatPhoneField(keyInfoPhone) : '--'}</span>
                            <svg className="crp-field-edit-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </div>
                        )
                      })()}
                    </div>
                    {/* Type row removed 2026-04-23 — contacts are always people
                        in this CRM (Companies view covers organizations). */}
                    {previewClient.client_type !== 'organization' && <div className="crp-field"><label>Source</label><span>{humanizeMatchStatus(previewClient.match_status)}</span></div>}
                  </div>
                </div>

                {previewClient.client_type === 'organization' && (
                  <Suspense fallback={null}>
                    <ClientPortalCard orgId={previewClient.unified_client_id} variant="sidebar" people={orgPeople} />
                  </Suspense>
                )}
              </div>
            </div>

            {/* ---- MIDDLE PANE ---- */}
            <div className="crp-middle">
              {/* ===== Entity 360 (P2) — ADDITIVE relationship graph + rollups =====
                  Appends one or more crp-cards below the stat tiles + inline P1
                  disclosure, inside crp-left-scroll. Self-contained leaf: owns its
                  own fetch/loading/error state, re-keyed on unified_client_id. On
                  error/empty it renders NOTHING, so the highlight card, stat tiles,
                  inline people/org-rels disclosure, About grid and right-rail all
                  stay intact. Gated behind FLAG_ENTITY360 (ship OFF, flip after
                  tester PASS). Removing this block reverts the record with zero
                  residue. */}
              {previewClient && FLAG_ENTITY360 && (
                <Suspense fallback={<div className="view-loading" />}>
                  <Entity360
                    entityType={previewClient.client_type === 'organization' ? 'org' : 'person'}
                    id={previewClient.unified_client_id}
                  />
                </Suspense>
              )}
              {/* Topbar with tabs */}
              <div className="hs-topbar" style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
                <div className="hs-topbar-left">
                  <div className="hs-view-tabs">
                    <div className={`hs-view-tab${contactDetailTab === 'about' ? ' active' : ''}`} onClick={() => { setContactDetailTab('about'); if (previewClient) window.location.hash = `/${activeView === 'companies' ? 'clients' : 'contacts'}/record/${encodeURIComponent(previewClient.unified_client_id)}` }}>
                      <span className="hs-tab-label">About</span>
                    </div>
                    <div className={`hs-view-tab${contactDetailTab === 'activities' ? ' active' : ''}`} onClick={() => { setContactDetailTab('activities'); if (previewClient) window.location.hash = `/${activeView === 'companies' ? 'clients' : 'contacts'}/record/${encodeURIComponent(previewClient.unified_client_id)}/activities` }}>
                      <span className="hs-tab-label">Activities</span>
                    </div>
                    <div className={`hs-view-tab${contactDetailTab === 'revenue' ? ' active' : ''}`} onClick={() => { setContactDetailTab('revenue'); if (previewClient) window.location.hash = `/${activeView === 'companies' ? 'clients' : 'contacts'}/record/${encodeURIComponent(previewClient.unified_client_id)}/revenue` }}>
                      <span className="hs-tab-label">Revenue</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="crp-middle-scroll">
                {contactDetailTab === 'about' && (<Suspense fallback={PrefsLazyFallback}><ContactDetailAbout ctx={{
                  crpStatClusterNode,
                  previewClient,
                  setPreviewClient,
                  setContactDetailTab,
                  recordInsight,
                  recordInsightLoading,
                  contactDetailProjects,
                  contactDetailDeals,
                  contactDetailLeads,
                  contactDetailActivities,
                  contactPendingChanges,
                  setContactPendingChanges,
                  contactPersons,
                  editingField,
                  setEditingField,
                  editingValue,
                  setEditingValue,
                  contactAddresses,
                  contactAddrAdding,
                  setContactAddrAdding,
                  contactAddrEditId,
                  setContactAddrEditId,
                  contactAddrDraft,
                  setContactAddrDraft,
                  contactAddrExpandedGroups,
                  setContactAddrExpandedGroups,
                  contactPhones,
                  contactPhoneAdding,
                  setContactPhoneAdding,
                  contactPhoneEditId,
                  setContactPhoneEditId,
                  contactPhoneDraft,
                  setContactPhoneDraft,
                  contactEmails,
                  setCellEditModal,
                  orgPeople,
                  orgPeopleShowPast,
                  setAppointForm,
                  setAppointSearch,
                  setAppointResults,
                  setOrgRoleModalOpen,
                  setRelRosterModal,
                  contactPersonEditId,
                  setContactPersonEditId,
                  contactPersonEditDraft,
                  setContactPersonEditDraft,
                  contactPersonBusy,
                  setContactPersonBusy,
                  contactPersonAdding,
                  setContactPersonAdding,
                  contactPersonDraft,
                  setContactPersonDraft,
                  contactOrgRoles,
                  crmUsers,
                  orgRelationships,
                  orgRoleTypeOptions,
                  ownerContactPickerOpen,
                  setOwnerContactPickerOpen,
                  ownerContactSearch,
                  setOwnerContactSearch,
                  ownerContactResults,
                  setOwnerContactResults,
                  ownerContactSaving,
                  setOwnerContactSaving,
                  AddressInput,
                  applyClientOrRedirect,
                  formatDatePref,
                  refreshContactAddresses,
                  refreshContactPersons,
                  refreshContactPhones,
                  regenerateRecordInsight,
                  reloadOrgPeople,
                  splitContactNameIntoPersons
                }} /></Suspense>)}

                {contactDetailTab === 'activities' && (<Suspense fallback={PrefsLazyFallback}><ContactDetailActivities ctx={{
                  contactDetailActivities, setContactDetailActivities, contactDetailLeads, contactDetailDeals, previewClient,
                  crpActivityFilter, setCrpActivityFilter, crpActivitySearch, setCrpActivitySearch, crpFilterChecks, setCrpFilterChecks,
                  crpFilterDropdownOpen, setCrpFilterDropdownOpen, crpSelectedUser, setCrpSelectedUser, crpUsersDropdownOpen, setCrpUsersDropdownOpen,
                  CRP_ACTIVITY_FILTER_OPTIONS, CALENDAR_USERS_MAP, groupActivitiesWithEmailThreads, isEmailThread,
                  contactNoteOpen, setContactNoteOpen, contactTaskOpen, setContactTaskOpen, contactMeetingOpen, setContactMeetingOpen,
                  contactNoteText, setContactNoteText, contactNoteFiles, setContactNoteFiles, contactNoteFileInputRef, contactNoteSaving, setContactNoteSaving,
                  contactTaskForm, setContactTaskForm, contactTaskError, setContactTaskError, contactTaskSuccess, setContactTaskSuccess, contactTaskSaving, setContactTaskSaving,
                  contactMeetingForm, setContactMeetingForm, contactMeetingSaving, setContactMeetingSaving, meetingFormEntityRef, currentUserId,
                  setCrpCommentOpenId, crpCommentOpenId, setCrpCommentVal, crpCommentVal, crpThreadExpanded, setCrpThreadExpanded,
                  crpExpandedCards, setCrpExpandedCards, highlightedCallActivityId, setCrpEditingActivityId, crpEditingActivityId,
                  crpEditingActivityVal, setCrpEditingActivityVal, crpEditingDescId, setCrpEditingDescId, crpEditingDescVal, setCrpEditingDescVal,
                  crpActivitySavingId, saveCrpActivityPatch,
                  taskCalDates, taskCalOpenId, setTaskCalDates, setTaskCalOpenId, taskCalMonth, setTaskCalMonth, taskCalYear, setTaskCalYear, taskCalRef,
                  contactActFetchLimit, contactActFetchBusy, setContactActFetchLimit, setContactActFetchBusy, contactDetailLoading,
                  fetchClientActivities, createNote, createTask, createCalendarEvent, uploadDealAttachments, updateActivity, formatDatePref, formatCallTitle, emailSenderFromActivity, emailSnippetFromActivity,
                  renderCallOwnerChip, linkifyText, getRecordingUrl, AvailabilityDateTimePicker, LinkedEmailsSection, DotLottieReact,
                  SentimentIcon, SENTIMENT_COLORS, CallPlayback, Calendar, CALENDAR_USERS_RAW, setDealToast, currencyFormatter
                }} /></Suspense>)}
                {contactDetailTab === 'revenue' && (
                  <>
                    {/* Financial Summary */}
                    <div className="crp-card">
                      <div className="crp-card-header"><h3>Financial summary</h3></div>
                      <div className="crp-card-body">
                        <div className="crp-stats-row">
                          <div className="crp-stat"><span className="crp-stat-val">${(previewClient.legacy_total_invoiced || 0).toLocaleString()}</span><span className="crp-stat-label">Invoiced</span></div>
                          <div className="crp-stat"><span className="crp-stat-val">${(previewClient.legacy_total_paid || 0).toLocaleString()}</span><span className="crp-stat-label">Paid</span></div>
                          <div className="crp-stat"><span className="crp-stat-val" style={{ color: previewClient.legacy_balance > 0 ? '#dc2626' : '#16a34a' }}>${(previewClient.legacy_balance || 0).toLocaleString()}</span><span className="crp-stat-label">Balance</span></div>
                          <div className="crp-stat"><span className="crp-stat-val">{previewClient.legacy_job_count || 0}</span><span className="crp-stat-label">{LABELS.projects}</span></div>
                        </div>
                      </div>
                    </div>

                    {/* Invoices */}
                    <div className="crp-card">
                      <div className="crp-card-header"><h3>Invoices & Payments ({(() => {
                        const txnTypes = new Set(['ar_invoice','customer_payment','ap_bill','ap_scan','bill_payment'])
                        return previewFinancials.flat.filter(d => txnTypes.has(d.doc_type) || (d.source === 'invoices_legacy')).length
                      })()})</h3></div>
                      <div className="crp-card-body" style={{ padding: 0, overflowX: 'auto' }}>
                        {(() => {
                          const txnTypes = new Set(['ar_invoice','customer_payment','ap_bill','ap_scan','bill_payment'])
                          const rows = previewFinancials.flat
                            .filter(d => txnTypes.has(d.doc_type) || d.source === 'invoices_legacy')
                            .sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''))
                          const TYPE_LABEL: Record<string, string> = {
                            ar_invoice: 'AR Invoice', customer_payment: 'Payment In',
                            ap_bill: 'AP Bill', ap_scan: 'AP Scan', bill_payment: 'Payment Out',
                            payment: 'Payment', other: 'Other',
                          }
                          if (rows.length === 0) return <div className="crp-empty-card">No invoices or payments found for this contact.</div>
                          return (
                            <table className="crp-mini-table">
                              <thead><tr>
                                <th>Type</th><th>Counterparty</th><th>Date</th>
                                <th style={{ textAlign: 'right' }}>Amount</th>
                                <th style={{ textAlign: 'right' }}>Balance</th>
                              </tr></thead>
                              <tbody>
                                {rows.map(r => (
                                  <tr key={`${r.source}:${r.id}`}>
                                    <td>{TYPE_LABEL[r.doc_type] ?? r.doc_type}</td>
                                    <td>{r.counterparty ?? '--'}</td>
                                    <td>{r.date ? formatDatePref(r.date) : '--'}</td>
                                    <td style={{ textAlign: 'right' }}>${(r.amount ?? 0).toLocaleString()}</td>
                                    <td style={{ textAlign: 'right' }}>{r.balance != null ? `$${r.balance.toLocaleString()}` : '--'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )
                        })()}
                      </div>
                    </div>

                    {/* Documents */}
                    <div className="crp-card">
                      <div className="crp-card-header"><h3>Documents ({previewDocuments.length + previewFinancials.flat.filter(d => d.source === 'acct_receipt' || d.doc_type === 'receipt' || d.doc_type === 'other').length})</h3></div>
                      <div className="crp-card-body" style={{ padding: 0, overflowX: 'auto' }}>
                        {(() => {
                          const receipts = previewFinancials.flat.filter(d => d.source === 'acct_receipt' || d.doc_type === 'receipt' || d.doc_type === 'other')
                          const totalCount = previewDocuments.length + receipts.length
                          if (totalCount === 0) return <div className="crp-empty-card">No documents found for this contact.</div>
                          return (
                            <table className="crp-mini-table">
                              <thead><tr><th>Type</th><th>Date</th><th>Source</th><th>Pages</th></tr></thead>
                              <tbody>
                                {previewDocuments.map((doc: any) => (
                                  <tr key={`crm:${doc.id}`}>
                                    <td>{doc.doc_type || '--'}</td>
                                    <td>{doc.doc_date ? formatDatePref(doc.doc_date) : '--'}</td>
                                    <td>{doc.division || 'CRM'}</td>
                                    <td>{doc.pages}</td>
                                  </tr>
                                ))}
                                {receipts.map(r => (
                                  <tr key={`${r.source}:${r.id}`}>
                                    <td>Receipt</td>
                                    <td>{r.date ? formatDatePref(r.date) : '--'}</td>
                                    <td>{r.counterparty || 'Manual upload'}</td>
                                    <td>--</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )
                        })()}
                      </div>
                    </div>

                    {/* Projects */}
                    <div className="crp-card">
                      <div className="crp-card-header"><h3>{LABELS.projects} ({previewFinancials.groups.filter(g => g.sub_project_id != null).length})</h3></div>
                      <div className="crp-card-body" style={{ padding: 0, overflowX: 'auto' }}>
                        {(() => {
                          const real = previewFinancials.groups.filter(g => g.sub_project_id != null)
                          if (real.length === 0) return <div className="crp-empty-card">No {LABELS.projects.toLowerCase()} found for this contact.</div>
                          return (
                            <table className="crp-mini-table">
                              <thead><tr>
                                <th>{LABELS.project}</th>
                                <th style={{ textAlign: 'right' }}>Docs</th>
                                <th style={{ textAlign: 'right' }}>Total</th>
                              </tr></thead>
                              <tbody>
                                {real.map(g => {
                                  const total = g.items.reduce((s, x) => s + (x.amount ?? 0), 0)
                                  return (
                                    <tr key={String(g.sub_project_id)}>
                                      <td>{g.name}</td>
                                      <td style={{ textAlign: 'right' }}>{g.items.length}</td>
                                      <td style={{ textAlign: 'right' }}>${total.toLocaleString()}</td>
                                    </tr>
                                  )
                                })}
                              </tbody>
                            </table>
                          )
                        })()}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* ---- RIGHT SIDEBAR ---- */}
            {(() => {
              // Reusable "+" button shown in each card header on the right sidebar.
              // Different cards route to different UIs — heavy records (Leads/Deals)
              // open the slide-out, lighter ones (Ticket/Project/Attachment) use
              // a quick-create popup modal.
              const addBtn = (onClick: () => void, label: string, tourId?: string) => (
                <button
                  type="button"
                  data-tour-id={tourId}
                  onClick={(e) => { e.stopPropagation(); onClick() }}
                  title={label}
                  aria-label={label}
                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, border: 'none', borderRadius: 4, background: 'transparent', color: '#516f90', cursor: 'pointer', padding: 0 }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#eaf0f6')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <svg width="14" height="14" viewBox="0 0 12 12" fill="currentColor"><path d="M.807 6.769h4.427v4.45a.781.781 0 0 0 1.561 0V6.793h4.427a.781.781 0 0 0 0-1.561H6.795V.781a.781.781 0 0 0-1.561 0v4.427H.807a.781.781 0 0 0 0 1.561Z"/></svg>
                </button>
              )
              // Per-deal + per-project ticket counts — used to render 🎫 N pills on the
              // Deals and Projects rollup cards. Derived from the same contactTickets
              // fetch used by the Tickets rollup card.
              const ticketsByDeal = new Map<string, number>()
              const ticketsByProject = new Map<string, number>()
              for (const t of contactTickets) {
                if (t.deal_id != null) ticketsByDeal.set(String(t.deal_id), (ticketsByDeal.get(String(t.deal_id)) ?? 0) + 1)
                if (t.project_id != null) ticketsByProject.set(String(t.project_id), (ticketsByProject.get(String(t.project_id)) ?? 0) + 1)
              }
              const contactProjectItems = [
                ...contactDetailProjects.map((project: any) => ({ kind: 'project' as const, project })),
                ...contactDetailJobs.map((job) => ({ kind: 'legacy_job' as const, job })),
              ]
              const visibleContactProjectItems = crpExpandedCard === 'projects'
                ? contactProjectItems
                : contactProjectItems.slice(0, 3)
              return (
            <div className="crp-right">
              <div className="crp-right-scroll">
                {/* Leads Card */}
                <div className="crp-card">
                  <div className="crp-card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <h3>Leads ({contactDetailLeads.length})</h3>
                    {addBtn(() => previewClient && openSmartCreateForClient(previewClient, 'lead'), 'Create lead')}
                  </div>
                  <div className="crp-card-body">
                    {contactDetailLeads.length === 0 && !contactDetailLoading && (
                      <div className="crp-empty-card">No leads linked to this contact.</div>
                    )}
                    {(crpExpandedCard === 'leads' ? contactDetailLeads : contactDetailLeads.slice(0, 3)).map((lead) => (
                      <div key={lead.id} className="crp-assoc-item" onClick={() => { setContactDetailOpen(false); setActiveView('leads'); setModalLead(lead) }}>
                        <div className="crp-assoc-title">{lead.title}</div>
                        <div className="crp-assoc-sub">{lead.statusLabel || lead.status} {lead.sourceLabel ? `· ${lead.sourceLabel}` : ''}</div>
                      </div>
                    ))}
                    {contactDetailLeads.length > 3 && (
                      <button onClick={() => setCrpExpandedCard(crpExpandedCard === 'leads' ? null : 'leads')} style={{ background: 'none', border: 'none', color: '#0091ae', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer', padding: '6px 0 0', width: '100%', textAlign: 'left' }}>
                        {crpExpandedCard === 'leads' ? 'Show less' : `View all (${contactDetailLeads.length})`}
                      </button>
                    )}
                  </div>
                </div>

                {/* Deals Card */}
                <div className="crp-card" data-crp-card="deals">
                  <div className="crp-card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <h3>Deals ({contactDetailDeals.length})</h3>
                    {addBtn(() => previewClient && openSmartCreateForClient(previewClient, 'deal'), 'Create deal', 'deal-360-add')}
                  </div>
                  <div className="crp-card-body">
                    {contactDetailDeals.length === 0 && !contactDetailLoading && (
                      <div className="crp-empty-card">Track the revenue opportunities associated with this contact.</div>
                    )}
                    {(crpExpandedCard === 'deals' ? contactDetailDeals : contactDetailDeals.slice(0, 3)).map((deal) => (
                      <div key={deal.id} className="crp-assoc-item" onClick={() => { setContactDetailOpen(false); setActiveView('deals'); if (deal.categoryId !== dealCategory) setDealCategory(deal.categoryId); setSelectedDeal(deal); setDealDetailOpen(true); window.location.hash = `/deals/record/${deal.id}` }}>
                        <div className="crp-assoc-title">
                          {deal.title}
                          {ticketsByDeal.get(deal.id) && (
                            <span
                              style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 10, background: '#fef2f2', color: '#dc2626', fontSize: '0.68rem', fontWeight: 700 }}
                              title={`${ticketsByDeal.get(deal.id)} ticket${ticketsByDeal.get(deal.id)! > 1 ? 's' : ''} linked to this deal`}
                            >🎫 {ticketsByDeal.get(deal.id)}</span>
                          )}
                        </div>
                        <div className="crp-assoc-sub">{deal.stageLabel || deal.stage} · {deal.pipelineName || 'Estimation'}</div>
                        {deal.opportunity > 0 && <div className="crp-assoc-amount">${deal.opportunity.toLocaleString()}</div>}
                      </div>
                    ))}
                    {contactDetailDeals.length > 3 && (
                      <button onClick={() => setCrpExpandedCard(crpExpandedCard === 'deals' ? null : 'deals')} style={{ background: 'none', border: 'none', color: '#0091ae', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer', padding: '6px 0 0', width: '100%', textAlign: 'left' }}>
                        {crpExpandedCard === 'deals' ? 'Show less' : `View all (${contactDetailDeals.length})`}
                      </button>
                    )}
                  </div>
                </div>


                {/* Projects Card (Task 27) */}
                <div className="crp-card" data-crp-card="projects">
                  <div className="crp-card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <h3>Projects ({contactProjectItems.length})</h3>
                    {addBtn(() => { setQcProjectForm({ name: '', dealId: contactDetailDeals[0]?.id || '', status: 'active', value: '' }); setQuickCreate('project') }, 'Create project')}
                  </div>
                  <div className="crp-card-body">
                    {contactProjectItems.length === 0 && !contactDetailLoading && (
                      <div className="crp-empty-card">No projects</div>
                    )}
                    {visibleContactProjectItems.map((item: any) => item.kind === 'project' ? (
                      <div key={`project-${item.project.id}`} className="crp-assoc-item" style={{ cursor: 'pointer' }} onClick={() => {
                        setContactDetailOpen(false)
                        setProjectDetailData(item.project); setProjectDetailOpen(true); setProjectDetailTab('about')
                        setActiveView('projects'); window.location.hash = `/projects/record/${item.project.id}`
                      }}>
                        <div className="crp-assoc-title">
                          {item.project.name}
                          {ticketsByProject.get(item.project.id) && (
                            <span
                              style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 10, background: '#fef2f2', color: '#dc2626', fontSize: '0.68rem', fontWeight: 700 }}
                              title={`${ticketsByProject.get(item.project.id)} ticket${ticketsByProject.get(item.project.id)! > 1 ? 's' : ''} linked to this project`}
                            >🎫 {ticketsByProject.get(item.project.id)}</span>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                          <span
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 600, color: '#fff', background: DELIVERY_STAGE_COLORS[item.project.pipeline_stage] || '#94a3b8', cursor: 'pointer' }}
                            title="Click to change stage"
                            onClick={(e) => {
                              e.stopPropagation()
                              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
                              setProjectStagePickerPos({ top: rect.bottom + 4, left: rect.left })
                              setProjectStagePicker({ projectId: item.project.id, currentStage: item.project.pipeline_stage || 'P_CONTRACT_SIGNED' })
                            }}
                          >
                            {DELIVERY_STAGE_LABELS[item.project.pipeline_stage] || item.project.pipeline_stage || item.project.status || 'Unknown'}
                            <svg width="8" height="8" viewBox="0 0 10 6" fill="currentColor" style={{ opacity: 0.8 }}><path d="M1 1l4 4 4-4"/></svg>
                          </span>
                          {item.project.pipeline_stage === 'P_ON_HOLD' && item.project.hold_type && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 6px', borderRadius: 6, background: '#f5f5f5', color: '#64748b', fontSize: '0.68rem', fontWeight: 600 }} title={item.project.hold_reason ? `${item.project.hold_type}: ${item.project.hold_reason}` : item.project.hold_type}>
                              {HOLD_TYPES.find(ht => ht.value === item.project.hold_type)?.label || item.project.hold_type}
                            </span>
                          )}
                          {item.project.contract_value > 0 && <span style={{ fontSize: '0.78rem', fontWeight: 500, color: '#33475b' }}>{currencyFormatter.format(item.project.contract_value)}</span>}
                          {!item.project.contract_value && item.project.value > 0 && <span style={{ fontSize: '0.78rem', fontWeight: 500, color: '#33475b' }}>{currencyFormatter.format(item.project.value)}</span>}
                        </div>
                      </div>
                    ) : (
                      <div key={`legacy-job-${item.job.id}`} className="crp-assoc-item">
                        <div className="crp-assoc-title">{item.job.raw_entry || item.job.job_type || `Legacy job #${item.job.id}`}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 600, color: '#334155', background: '#e2e8f0' }}>Legacy work</span>
                          {item.job.job_date && <span style={{ fontSize: '0.78rem', color: '#516f90' }}>{item.job.job_date}</span>}
                          {item.job.job_type && <span style={{ fontSize: '0.78rem', color: '#516f90' }}>{item.job.job_type}</span>}
                          {item.job.job_amount != null && item.job.job_amount > 0 && <span style={{ fontSize: '0.78rem', fontWeight: 500, color: '#33475b' }}>{currencyFormatter.format(item.job.job_amount)}</span>}
                        </div>
                      </div>
                    ))}
                    {contactProjectItems.length > 3 && (
                      <button onClick={() => setCrpExpandedCard(crpExpandedCard === 'projects' ? null : 'projects')} style={{ background: 'none', border: 'none', color: '#0091ae', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer', padding: '6px 0 0', width: '100%', textAlign: 'left' }}>
                        {crpExpandedCard === 'projects' ? 'Show less' : `View all (${contactProjectItems.length})`}
                      </button>
                    )}
                  </div>
                </div>

                {/* Tickets Card (Task 13) — hide-empty (Org 360 P1): a zero-ticket record
                    collapses this NON-CORE card instead of rendering an empty shell. */}
                {contactTickets.length > 0 && (
                <div className="crp-card" data-crp-card="tickets">
                  <div className="crp-card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <h3>Tickets ({contactTickets.length})</h3>
                    <button
                      onClick={() => {
                        if (!previewClient?.unified_client_id) return
                        openSmartCreateForTicket({ contact: previewClient })
                      }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.78rem', color: '#0091ae', fontWeight: 500, padding: '2px 6px' }}
                    >+</button>
                  </div>
                  <div className="crp-card-body">
                    {contactTickets.length === 0 ? (
                      <div className="crp-empty-card">No tickets</div>
                    ) : (crpExpandedCard === 'tickets' ? contactTickets : contactTickets.slice(0, 3)).map((t: any) => {
                      const phase = t.project_id ? 'project' : t.deal_id ? 'deal' : 'contact'
                      const chipLabel = phase === 'project' ? `🏗 Project: ${t.project_name || t.project_id}`
                                      : phase === 'deal' ? `💼 Deal: ${t.deal_title || t.deal_id}`
                                      : '📇 Contact'
                      const chipBg = phase === 'project' ? '#dbeafe' : phase === 'deal' ? '#fef3c7' : '#e2e8f0'
                      const chipFg = phase === 'project' ? '#1e40af' : phase === 'deal' ? '#92400e' : '#334155'
                      const isOverdue = t.due_date && new Date(t.due_date) < new Date() && t.status !== 'resolved' && t.status !== 'closed'
                      const onClick = () => {
                        if (phase === 'project' && t.project_id) {
                          window.location.hash = `/projects/record/${encodeURIComponent(t.project_id)}#ticket-${t.id}`
                        } else if (phase === 'deal' && t.deal_id) {
                          window.location.hash = `/deals/record/${t.deal_id}#ticket-${t.id}`
                        } else {
                          setActiveTicketDrawerId(t.id)
                        }
                      }
                      return (
                        <div
                          key={t.id}
                          onClick={onClick}
                          style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                            <span style={{ display: 'inline-block', padding: '1px 6px', borderRadius: 3, fontSize: '0.68rem', fontWeight: 600, background: chipBg, color: chipFg }}>{chipLabel}</span>
                            {isOverdue && <span style={{ display: 'inline-block', padding: '1px 6px', borderRadius: 3, fontSize: '0.68rem', fontWeight: 600, background: '#fee2e2', color: '#991b1b' }}>Overdue</span>}
                          </div>
                          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</div>
                          <div style={{ display: 'flex', gap: 8, fontSize: '0.72rem', color: '#7c98b6', marginTop: 2 }}>
                            <span>{t.status}</span>
                            {t.due_date && <span>Due {new Date(t.due_date).toLocaleDateString()}</span>}
                          </div>
                        </div>
                      )
                    })}
                    {contactTickets.length > 3 && (
                      <button onClick={() => setCrpExpandedCard(crpExpandedCard === 'tickets' ? null : 'tickets')} style={{ background: 'none', border: 'none', color: '#0091ae', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer', padding: '6px 0 0', width: '100%', textAlign: 'left' }}>
                        {crpExpandedCard === 'tickets' ? 'Show less' : `View all (${contactTickets.length})`}
                      </button>
                    )}
                  </div>
                </div>
                )}

                {/* Attachments Card — hide-empty (Org 360 P1): collapses when the record has
                    no files instead of showing an empty NON-CORE card. */}
                {contactDetailAttachments.length > 0 && (
                <div className="crp-card">
                  <div className="crp-card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <h3>Attachments ({contactDetailAttachments.length})</h3>
                    {addBtn(() => { setQcAttachmentFiles([]); setQcAttachmentNote(''); setQcAttachmentDealId(contactDetailDeals[0]?.id || ''); setQuickCreate('attachment') }, 'Upload files')}
                  </div>
                  <div className="crp-card-body">
                    {contactDetailAttachments.length === 0 && !contactDetailLoading && (
                      <div className="crp-empty-card">See the files attached to your activities or uploaded to this record.</div>
                    )}
                    {contactDetailAttachments.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {(crpExpandedCard === 'attachments' ? contactDetailAttachments : contactDetailAttachments.slice(0, 3)).map((att: any, i: number) => {
                          const isDocument = att.source === 'document'
                          const href = isDocument
                            ? (att.documentUrl || (att.ebridgeSourceId ? `/api/crm/documents/${encodeURIComponent(att.ebridgeSourceId)}/pdf` : '#'))
                            : `/api/crm/files?key=${encodeURIComponent(att.r2Key)}`
                          const sourceLabel = isDocument ? 'Document' : att.source === 'deal' ? 'Deal' : 'Lead'
                          return (
                            <a
                              key={`${att.source || 'file'}:${att.documentId || att.activityId || i}:${att.name || ''}`}
                              href={href}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => { if (href === '#') e.preventDefault() }}
                              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, textDecoration: 'none', color: '#33475b', fontSize: '0.82rem' }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d={isDocument ? 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' : 'M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48'} />{isDocument && <polyline points="14 2 14 8 20 8" />}</svg>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}>{att.name}</div>
                                <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                                  {sourceLabel} · {att.activitySubject || att.docType || 'File'}
                                  {att.size ? ` · ${(att.size / 1024).toFixed(0)}kb` : ''}
                                </div>
                              </div>
                            </a>
                          )
                        })}
                        {contactDetailAttachments.length > 3 && (
                          <button onClick={() => setCrpExpandedCard(crpExpandedCard === 'attachments' ? null : 'attachments')} style={{ background: 'none', border: 'none', color: '#0091ae', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer', padding: '6px 0 0', width: '100%', textAlign: 'left' }}>
                            {crpExpandedCard === 'attachments' ? 'Show less' : `View all (${contactDetailAttachments.length})`}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                )}

                {/* Organization records use the contact_org_roles-backed People list;
                    person records keep the existing duplicate-detection heuristic. */}
                {previewClient.client_type === 'organization' ? (
                  <div className="crp-card">
                    <div className="crp-card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <h3>Contacts ({orgPeople.length})</h3>
                      {addBtn(() => { setAppointForm({ contact_id: '', contact_name: '', role_key: '', role_label: '', start_date: '', is_primary: false, note: '' }); setAppointSearch(''); setAppointResults([]); setOrgRoleModalOpen(true) }, 'Add contact')}
                    </div>
                    <div className="crp-card-body">
                      {orgPeople.length === 0 ? (
                        <div className="crp-empty-card">No company contacts</div>
                      ) : (
                        <>
                          {(crpExpandedCard === 'company-contacts' ? orgPeople : orgPeople.slice(0, 8)).map(person => (
                            <div key={person.id} className="crp-assoc-item" onClick={() => { fetchClient(person.contact_id).then(c => { if (c) { setRelRosterModal(null); if (applyClientOrRedirect(c)) return; setContactDetailTab('about'); window.location.hash = `/contacts/record/${encodeURIComponent(person.contact_id)}` } }).catch(() => {}) }}>
                              <div className="crp-assoc-title">{person.contact_name || person.contact_id}</div>
                              <div className="crp-assoc-sub">{person.role_label || 'Role unset'}</div>
                            </div>
                          ))}
                          {orgPeople.length > 8 && (
                            <button onClick={() => setCrpExpandedCard(crpExpandedCard === 'company-contacts' ? null : 'company-contacts')} style={{ background: 'none', border: 'none', color: '#0091ae', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer', padding: '6px 0 0', width: '100%', textAlign: 'left' }}>
                              {crpExpandedCard === 'company-contacts' ? 'Show less' : `Show all ${orgPeople.length}`}
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ) : relatedContacts.length > 0 && (
                  <div className="crp-card">
                    <div className="crp-card-header"><h3>Related contacts ({relatedContacts.length})</h3></div>
                    <div className="crp-card-body">
                      {relatedContacts.map((rc) => (
                        <div key={rc.id} className="crp-assoc-item" onClick={() => { fetchClient(rc.id).then(c => { if (applyClientOrRedirect(c)) return; setContactDetailTab('about'); window.location.hash = `/contacts/record/${encodeURIComponent(rc.id)}` }).catch(() => {}) }}>
                          <div className="crp-assoc-title">{rc.name}</div>
                          <div className="crp-assoc-sub">{rc.reasons.map(reasonLabel).join(', ')}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
              )
            })()}

            {/* ===== Org relationship modal (Task 14) ===== */}
            {orgRelModalOpen && previewClient && (
              /* zIndex 10010 keeps this nested add modal ABOVE the Org-relationships modal (.modal-overlay = --modal-z 10000) it opens from */
              <div className="modal-overlay" style={{ zIndex: 10010 }} onClick={() => !orgRelSaving && setOrgRelModalOpen(false)}>
                <div className="modal-card" style={{ width: 460, padding: 0 }} onClick={e => e.stopPropagation()}>
                  <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb' }}>
                    <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#33475b' }}>Add client relationship</h3>
                  </div>
                  <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{ position: 'relative' }}>
                      <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569' }}>Related client *</label>
                      <input className="crp-field-input" placeholder="Search clients…" value={relForm.to_org_name || relOrgSearch} onChange={e => { setRelForm(f => ({ ...f, to_org_id: '', to_org_name: '' })); setRelOrgSearch(e.target.value) }} />
                      {relOrgResults.length > 0 && !relForm.to_org_id && (
                        <div style={{ position: 'absolute', zIndex: 10, left: 0, right: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, marginTop: 2, maxHeight: 180, overflowY: 'auto' }}>
                          {relOrgResults.map(o => (
                            <div key={o.unified_client_id} style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '0.82rem' }} onClick={() => { setRelForm(f => ({ ...f, to_org_id: o.unified_client_id, to_org_name: o.preferred_name })); setRelOrgResults([]); setRelOrgSearch('') }}>{o.preferred_name}</div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
                      <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569' }}>Relationship type</label>
                      <select className="crp-field-input" value={relForm.rel_type} onChange={e => setRelForm(f => ({ ...f, rel_type: e.target.value }))}>
                        <option value="managed_by">Managed by</option>
                        <option value="part_of">Part of</option>
                        <option value="parent_of">Parent of</option>
                        <option value="affiliated_with">Affiliated with</option>
                      </select>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem' }}>
                      <input type="checkbox" checked={relForm.is_billing_master} onChange={e => setRelForm(f => ({ ...f, is_billing_master: e.target.checked }))} /> Billing master
                    </label>
                  </div>
                  <div style={{ padding: '12px 20px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <button className="btn-secondary" onClick={() => setOrgRelModalOpen(false)} disabled={orgRelSaving}>Cancel</button>
                    <button className="btn-primary" disabled={!relForm.to_org_id || orgRelSaving} onClick={async () => {
                      const orgId = previewClient.unified_client_id
                      setOrgRelSaving(true)
                      try {
                        await createOrgRelationship(orgId, { to_org_id: relForm.to_org_id, rel_type: relForm.rel_type, start_date: relForm.start_date || undefined, is_billing_master: relForm.is_billing_master })
                        setOrgRelModalOpen(false)
                        reloadOrgRelationships(orgId, orgRelShowPast)
                      } catch (e: any) { alert(e.message || 'Failed to add relationship') }
                      finally { setOrgRelSaving(false) }
                    }}>Add</button>
                  </div>
                </div>
              </div>
            )}

            {/* ===== Appoint person to org modal (Task 14) ===== */}
            {orgRoleModalOpen && previewClient && createPortal(
              /* Slide-out Add-contact panel (matches the Leads/Deals create-panel chrome), portaled to body so z beats the toolbar — .crp-page (z 20) is a stacking context.
                 Inline z 10010/10011 keep it ABOVE the People/Org-relationships modal
                 (.modal-overlay = --modal-z 10000) — it is also opened from inside that modal. */
              <>
                <div className="create-panel-overlay" style={{ zIndex: 10010 }} onClick={() => !orgRoleSaving && setOrgRoleModalOpen(false)} />
                <div className="create-panel" style={{ zIndex: 10011 }}>
                  <div className="create-panel-header">
                    <h2>Add contact</h2>
                    <button className="create-panel-close" onClick={() => !orgRoleSaving && setOrgRoleModalOpen(false)} aria-label="Close">
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                    </button>
                  </div>
                  <div className="create-panel-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{ position: 'relative' }}>
                      <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569' }}>Contact *</label>
                      <input className="crp-field-input" placeholder="Search people…" value={appointForm.contact_name || appointSearch} onChange={e => { setAppointForm(f => ({ ...f, contact_id: '', contact_name: '' })); setAppointSearch(e.target.value) }} />
                      {appointResults.length > 0 && !appointForm.contact_id && (
                        <div style={{ position: 'absolute', zIndex: 10, left: 0, right: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, marginTop: 2, maxHeight: 180, overflowY: 'auto' }}>
                          {appointResults.map(p => (
                            <div key={p.unified_client_id} style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '0.82rem' }} onClick={() => { setAppointForm(f => ({ ...f, contact_id: p.unified_client_id, contact_name: p.preferred_name })); setAppointResults([]); setAppointSearch('') }}>{p.preferred_name}</div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
                      <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569' }}>Role *</label>
                      <select className="crp-field-input" value={appointForm.role_key} onChange={e => { const k = e.target.value; const def = orgRoleTypeOptions.find(o => o.key === k); setAppointForm(f => ({ ...f, role_key: k, role_label: k === '__custom__' ? '' : (def?.label || '') })) }}>
                        <option value="">Select a role…</option>
                        {orgRoleTypeOptions.map(o => <option key={o.id} value={o.key}>{o.label}</option>)}
                        <option value="__custom__">Custom…</option>
                      </select>
                      {appointForm.role_key === '__custom__' && (
                        <input className="crp-field-input" style={{ marginTop: 6 }} placeholder="Custom role label" value={appointForm.role_label} onChange={e => setAppointForm(f => ({ ...f, role_label: e.target.value }))} />
                      )}
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem' }}>
                      <input type="checkbox" checked={appointForm.is_primary} onChange={e => setAppointForm(f => ({ ...f, is_primary: e.target.checked }))} /> Primary role
                    </label>
                  </div>
                  <div className="create-panel-footer" style={{ justifyContent: 'flex-end' }}>
                    <button className="btn-secondary" onClick={() => setOrgRoleModalOpen(false)} disabled={orgRoleSaving}>Cancel</button>
                    <button className="btn-primary" disabled={!appointForm.contact_id || !appointForm.role_label || orgRoleSaving} onClick={async () => {
                      const orgId = previewClient.unified_client_id
                      setOrgRoleSaving(true)
                      try {
                        await createContactOrgRole({ contact_id: appointForm.contact_id, org_id: orgId, role_label: appointForm.role_label, role_key: appointForm.role_key && appointForm.role_key !== '__custom__' ? appointForm.role_key : undefined, is_primary: appointForm.is_primary, note: appointForm.note || undefined })
                        setOrgRoleModalOpen(false)
                        reloadOrgPeople(orgId, orgPeopleShowPast)
                      } catch (e: any) { alert(e.message || 'Failed to appoint') }
                      finally { setOrgRoleSaving(false) }
                    }}>Appoint</button>
                  </div>
                </div>
              </>,
              document.body
            )}

            {/* ===== Appoint contact to organization modal (Task 15) ===== */}
            {appointOrgModalOpen && previewClient && (
              <div className="modal-overlay" style={{ zIndex: 300 }} onClick={() => !appointOrgSaving && setAppointOrgModalOpen(false)}>
                <div className="modal-card" style={{ width: 460, padding: 0 }} onClick={e => e.stopPropagation()}>
                  <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb' }}>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#33475b' }}>Appoint to client</h3>
                  </div>
                  <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{ position: 'relative' }}>
                      <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569' }}>Client *</label>
                      <input className="crp-field-input" placeholder="Search clients…" value={appointOrgForm.org_name || appointOrgSearch} onChange={e => { setAppointOrgForm(f => ({ ...f, org_id: '', org_name: '', org_subtype: '' })); setAppointOrgSearch(e.target.value) }} />
                      {appointOrgResults.length > 0 && !appointOrgForm.org_id && (
                        <div style={{ position: 'absolute', zIndex: 10, left: 0, right: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, marginTop: 2, maxHeight: 180, overflowY: 'auto' }}>
                          {appointOrgResults.map(o => (
                            <div key={o.unified_client_id} style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '0.82rem' }} onClick={() => { setAppointOrgForm(f => ({ ...f, org_id: o.unified_client_id, org_name: o.preferred_name, org_subtype: (o as any).org_primary_sub_type || '' })); setAppointOrgResults([]); setAppointOrgSearch('') }}>{o.preferred_name}</div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
                      <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569' }}>Role *</label>
                      <select className="crp-field-input" value={appointOrgForm.role_key} onChange={e => { const k = e.target.value; const def = orgRoleTypeOptions.find(o => o.key === k); setAppointOrgForm(f => ({ ...f, role_key: k, role_label: k === '__custom__' ? '' : (def?.label || '') })) }}>
                        <option value="">Select a role…</option>
                        {orgRoleTypeOptions.map(o => <option key={o.id} value={o.key}>{o.label}</option>)}
                        <option value="__custom__">Custom…</option>
                      </select>
                      {appointOrgForm.role_key === '__custom__' && (
                        <input className="crp-field-input" style={{ marginTop: 6 }} placeholder="Custom role label" value={appointOrgForm.role_label} onChange={e => setAppointOrgForm(f => ({ ...f, role_label: e.target.value }))} />
                      )}
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem' }}>
                      <input type="checkbox" checked={appointOrgForm.is_primary} onChange={e => setAppointOrgForm(f => ({ ...f, is_primary: e.target.checked }))} /> Primary role
                    </label>
                  </div>
                  <div style={{ padding: '12px 20px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <button className="btn-secondary" onClick={() => setAppointOrgModalOpen(false)} disabled={appointOrgSaving}>Cancel</button>
                    <button className="btn-primary" disabled={!appointOrgForm.org_id || !appointOrgForm.role_label || appointOrgSaving} onClick={async () => {
                      const contactId = previewClient.unified_client_id
                      setAppointOrgSaving(true)
                      try {
                        await createContactOrgRole({ contact_id: contactId, org_id: appointOrgForm.org_id, role_label: appointOrgForm.role_label, role_key: appointOrgForm.role_key && appointOrgForm.role_key !== '__custom__' ? appointOrgForm.role_key : undefined, is_primary: appointOrgForm.is_primary, note: appointOrgForm.note || undefined })
                        setAppointOrgModalOpen(false)
                        reloadContactOrgRoles(contactId, contactRoleShowPast)
                      } catch (e: any) { alert(e.message || 'Failed to appoint') }
                      finally { setAppointOrgSaving(false) }
                    }}>Appoint</button>
                  </div>
                </div>
              </div>
            )}

            {/* ===== Relationship roster management modal ===== */}
            {/* 'people' = org's people roster; 'orgs' = contact's org roster; 'org-rels' = org-to-org relationships */}
            {relRosterModal && previewClient && (() => {
              const isPeople = relRosterModal === 'people'
              const isOrgRels = relRosterModal === 'org-rels'
              const orgId = previewClient.unified_client_id
              const contactId = previewClient.unified_client_id

              // Org-rels modal — separate return for full management
              if (isOrgRels) {
                const REL_LABELS: Record<string, string> = { managed_by: 'Managed by', part_of: 'Part of', parent_of: 'Parent of', affiliated_with: 'Affiliated with' }
                const REL_INVERSE: Record<string, string> = { managed_by: 'Manages', part_of: 'Contains', parent_of: 'Child of', affiliated_with: 'Affiliated with' }
                const outbound = orgRelationships?.outbound || []
                const inbound = orgRelationships?.inbound || []
                return (
                  <div className="modal-overlay" onClick={() => setRelRosterModal(null)}>
                    <div className="modal-card" style={{ width: 560, padding: 0, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
                      {/* Header */}
                      <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#33475b' }}>Org relationships — {previewClient.preferred_name}</h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <button
                            className="btn-primary"
                            style={{ fontSize: '0.8rem', padding: '5px 12px' }}
                            onClick={() => { setRelForm({ to_org_id: '', to_org_name: '', rel_type: 'managed_by', start_date: '', note: '', is_billing_master: false }); setRelOrgSearch(''); setRelOrgResults([]); setOrgRelModalOpen(true) }}
                          >+ Add relationship</button>
                          <button onClick={() => setRelRosterModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4, borderRadius: 4 }} aria-label="Close">
                            <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                          </button>
                        </div>
                      </div>
                      {/* Show past toggle */}
                      <div style={{ padding: '10px 20px', borderBottom: '1px solid #f1f5f9', flexShrink: 0 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', color: '#64748b', cursor: 'pointer' }}>
                          <input type="checkbox" checked={orgRelShowPast} onChange={e => { setOrgRelShowPast(e.target.checked); reloadOrgRelationships(orgId, e.target.checked) }} /> Show past
                        </label>
                      </div>
                      {/* Roster list */}
                      <div style={{ overflowY: 'auto', flex: 1, padding: '8px 0' }}>
                        {/* People at the org — people-with-roles restored here (Homeowner, etc.).
                            Reuses orgPeople (loaded by reloadOrgPeople in the org-detail effect) —
                            NO rolled invoiced/paid/balance financial rollup is shown. */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 20px 4px' }}>
                          <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>People at {previewClient.preferred_name}</div>
                          <button style={{ background: 'none', border: 'none', color: '#0091ae', fontSize: '0.72rem', cursor: 'pointer', padding: 0 }} onClick={() => { setAppointForm({ contact_id: '', contact_name: '', role_key: '', role_label: '', start_date: '', is_primary: false, note: '' }); setAppointSearch(''); setAppointResults([]); setOrgRoleModalOpen(true) }}>+ Add person</button>
                        </div>
                        {orgPeople.length === 0 ? (
                          <div style={{ padding: '8px 20px 12px', color: '#94a3b8', fontSize: '0.8rem' }}>No people linked.</div>
                        ) : (
                          orgPeople.map(p => (
                            <div key={`p${p.id}`} className="org-roster-row" style={{ opacity: p.is_active ? 1 : 0.6 }}>
                              <div className="org-roster-main">
                                <div className="org-roster-name-line">
                                  <span className="hs-name-link" style={{ fontWeight: 600, fontSize: '0.85rem', color: '#33475b' }} onClick={() => { fetchClient(p.contact_id).then(cl => { if (cl) { setRelRosterModal(null); if (applyClientOrRedirect(cl)) return; setContactDetailTab('about') } }).catch(() => {}) }}>{p.contact_name || p.contact_id}</span>
                                  {!!p.is_primary && <span className="org-roster-badge" style={{ background: '#dcfce7', color: '#16a34a' }}>Primary</span>}
                                  {!p.is_active && <span className="org-roster-badge" style={{ background: '#f1f5f9', color: '#94a3b8' }}>Past</span>}
                                </div>
                                <div className="org-roster-sub">{p.role_label || 'Role unset'}</div>
                              </div>
                              {!!p.is_active && (
                                <div className="org-roster-actions">
                                  {!p.is_primary && <button className="org-roster-iconbtn is-primary" title="Set as primary" aria-label="Set as primary" onClick={() => updateContactOrgRole(p.id, { is_primary: 1 }).then(() => reloadOrgPeople(orgId, orgPeopleShowPast))}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg></button>}
                                  <button className="org-roster-iconbtn is-danger" title="Remove from client" aria-label="Remove from client" onClick={() => updateContactOrgRole(p.id, { is_active: 0, end_date: 'now' }).then(() => reloadOrgPeople(orgId, orgPeopleShowPast))}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg></button>
                                </div>
                              )}
                            </div>
                          ))
                        )}
                        {/* Org-to-org relationships */}
                        <div style={{ padding: '14px 20px 4px', fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', borderTop: '1px solid #f1f5f9', marginTop: 6 }}>Related clients</div>
                        {outbound.length === 0 && inbound.length === 0 ? (
                          <div style={{ padding: '8px 20px 16px', color: '#94a3b8', fontSize: '0.8rem' }}>No related clients.</div>
                        ) : (
                          <>
                            {outbound.length > 0 && (
                              <>
                                <div style={{ padding: '6px 20px 4px', fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Outbound</div>
                                {outbound.map(r => (
                                  <div key={`o${r.id}`} className="org-roster-row" style={{ opacity: r.is_active ? 1 : 0.6 }}>
                                    <div className="org-roster-main">
                                      <div className="org-roster-name-line">
                                        <span className="hs-name-link" style={{ fontWeight: 600, fontSize: '0.85rem', color: '#33475b' }} onClick={() => { fetchClient(r.to_org_id).then(cl => { if (cl) { setRelRosterModal(null); if (applyClientOrRedirect(cl)) return; setContactDetailTab('about') } }).catch(() => {}) }}>{r.to_org_name || r.to_org_id}</span>
                                        {!!r.is_billing_master && <span className="org-roster-badge" style={{ background: '#fef9c3', color: '#a16207' }}>⭐ Billing master</span>}
                                        {!r.is_active && <span className="org-roster-badge" style={{ background: '#f1f5f9', color: '#94a3b8' }}>Past</span>}
                                      </div>
                                      <div className="org-roster-sub">{REL_LABELS[r.rel_type] || r.rel_type} · {[r.start_date, r.end_date].filter(Boolean).join(' → ') || 'No dates'}</div>
                                    </div>
                                    {!!r.is_active && (
                                      <div className="org-roster-actions">
                                        {!r.is_billing_master && <button className="org-roster-iconbtn is-primary" title="Set as billing master" aria-label="Set as billing master" onClick={() => updateOrgRelationship(r.id, { is_billing_master: 1 }).then(() => reloadOrgRelationships(orgId, orgRelShowPast))}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg></button>}
                                        <button className="org-roster-iconbtn is-danger" title="End relationship" aria-label="End relationship" onClick={() => endOrgRelationship(r.id).then(() => reloadOrgRelationships(orgId, orgRelShowPast))}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg></button>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </>
                            )}
                            {inbound.length > 0 && (
                              <>
                                <div style={{ padding: '6px 20px 4px', fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Inbound</div>
                                {inbound.map(r => (
                                  <div key={`i${r.id}`} className="org-roster-row" style={{ opacity: r.is_active ? 1 : 0.6 }}>
                                    <div className="org-roster-main">
                                      <div className="org-roster-name-line">
                                        <span className="hs-name-link" style={{ fontWeight: 600, fontSize: '0.85rem', color: '#33475b' }} onClick={() => { fetchClient(r.from_org_id).then(cl => { if (cl) { setRelRosterModal(null); if (applyClientOrRedirect(cl)) return; setContactDetailTab('about') } }).catch(() => {}) }}>{r.from_org_name || r.from_org_id}</span>
                                        {!r.is_active && <span className="org-roster-badge" style={{ background: '#f1f5f9', color: '#94a3b8' }}>Past</span>}
                                      </div>
                                      <div className="org-roster-sub">{REL_INVERSE[r.rel_type] || r.rel_type} · {[r.start_date, r.end_date].filter(Boolean).join(' → ') || 'No dates'}</div>
                                    </div>
                                  </div>
                                ))}
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )
              }

              return (
                <div className="modal-overlay" onClick={() => { setRelRosterModal(null); setRoleDateEdit(null) }}>
                  <div className="modal-card" style={{ width: 560, padding: 0, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
                    {/* Header */}
                    <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                      <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#33475b' }}>
                        {isPeople ? `People & roles — ${previewClient.preferred_name}` : `Clients & roles — ${previewClient.preferred_name}`}
                      </h3>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <button
                          className="btn-primary"
                          style={{ fontSize: '0.8rem', padding: '5px 12px' }}
                          onClick={() => {
                            if (isPeople) {
                              setAppointForm({ contact_id: '', contact_name: '', role_key: '', role_label: '', start_date: '', is_primary: false, note: '' })
                              setAppointSearch(''); setAppointResults([])
                              setOrgRoleModalOpen(true)
                            } else {
                              setAppointOrgForm({ org_id: '', org_name: '', org_subtype: '', role_key: '', role_label: '', start_date: '', is_primary: false, note: '' })
                              setAppointOrgSearch(''); setAppointOrgResults([])
                              setAppointOrgModalOpen(true)
                            }
                          }}
                        >
                          {isPeople ? '+ Appoint person' : '+ Appoint to org'}
                        </button>
                        <button onClick={() => { setRelRosterModal(null); setRoleDateEdit(null) }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4, borderRadius: 4 }} aria-label="Close">
                          <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                        </button>
                      </div>
                    </div>
                    {/* Show past toggle */}
                    <div style={{ padding: '10px 20px', borderBottom: '1px solid #f1f5f9', flexShrink: 0 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', color: '#64748b', cursor: 'pointer' }}>
                        {isPeople
                          ? <><input type="checkbox" checked={orgPeopleShowPast} onChange={e => { setOrgPeopleShowPast(e.target.checked); reloadOrgPeople(orgId, e.target.checked) }} /> Show past roles</>
                          : <><input type="checkbox" checked={contactRoleShowPast} onChange={e => { setContactRoleShowPast(e.target.checked); reloadContactOrgRoles(contactId, e.target.checked) }} /> Show past roles</>
                        }
                      </label>
                    </div>
                    {/* Roster list */}
                    <div style={{ overflowY: 'auto', flex: 1, padding: '8px 0' }}>
                      {isPeople ? (
                        orgPeople.length === 0
                          ? <div style={{ padding: '24px 20px', color: '#94a3b8', fontSize: '0.85rem', textAlign: 'center' }}>No people appointed.</div>
                          : orgPeople.map(p => {
                            const editing = !!(roleDateEdit && roleDateEdit.id === p.id)
                            return (
                            <div key={p.id} className="org-roster-row" style={{ opacity: p.is_active ? 1 : 0.6 }}>
                              <div className="org-roster-main">
                                <div className="org-roster-name-line">
                                  <span className="hs-name-link" style={{ fontWeight: 600, fontSize: '0.85rem', color: '#33475b' }} onClick={() => { fetchClient(p.contact_id).then(cl => { if (cl) { setRelRosterModal(null); if (applyClientOrRedirect(cl)) return; setContactDetailTab('about') } }).catch(() => {}) }}>{p.contact_name || p.contact_id}</span>
                                  {!!p.is_primary && <span className="org-roster-badge" style={{ background: '#dcfce7', color: '#16a34a' }}>Primary</span>}
                                  {!p.is_active && <span className="org-roster-badge" style={{ background: '#f1f5f9', color: '#94a3b8' }}>Past</span>}
                                </div>
                                <div className="org-roster-sub">{p.role_label} · {p.end_date ? `Assigned ${p.start_date || '—'} → ${p.end_date}` : `Assigned ${p.start_date || '—'}`}</div>
                                {editing && (
                                  <div className="org-roster-dateform">
                                    <label style={{ fontSize: '0.7rem', color: '#64748b' }}>Assigned<input type="date" value={roleDateEdit!.start_date} onChange={e => setRoleDateEdit(d => d ? { ...d, start_date: e.target.value } : d)} style={{ display: 'block', fontSize: '0.72rem', padding: '3px 6px', border: '1px solid #cbd6e2', borderRadius: 5 }} /></label>
                                    <label style={{ fontSize: '0.7rem', color: '#64748b' }}>End<input type="date" value={roleDateEdit!.end_date} onChange={e => setRoleDateEdit(d => d ? { ...d, end_date: e.target.value } : d)} style={{ display: 'block', fontSize: '0.72rem', padding: '3px 6px', border: '1px solid #cbd6e2', borderRadius: 5 }} /></label>
                                    <button disabled={roleDateSaving} className="btn-primary" style={{ fontSize: '0.72rem', padding: '4px 12px' }} onClick={async () => { setRoleDateSaving(true); try { await updateContactOrgRole(p.id, { start_date: roleDateEdit!.start_date || null, end_date: roleDateEdit!.end_date || null }); setRoleDateEdit(null); reloadOrgPeople(orgId, orgPeopleShowPast) } catch (e: any) { alert(e.message || 'Failed to save dates') } finally { setRoleDateSaving(false) } }}>Save</button>
                                    <button disabled={roleDateSaving} style={{ background: 'none', border: '1px solid #cbd6e2', borderRadius: 5, color: '#64748b', fontSize: '0.72rem', cursor: 'pointer', padding: '4px 12px' }} onClick={() => setRoleDateEdit(null)}>Cancel</button>
                                  </div>
                                )}
                              </div>
                              {!editing && (
                                <div className="org-roster-actions">
                                  {!!p.is_active && !p.is_primary && <button className="org-roster-iconbtn is-primary" title="Set as primary" aria-label="Set as primary" onClick={() => updateContactOrgRole(p.id, { is_primary: 1 }).then(() => reloadOrgPeople(orgId, orgPeopleShowPast))}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg></button>}
                                  <button className="org-roster-iconbtn" title="Edit dates" aria-label="Edit dates" onClick={() => setRoleDateEdit({ id: p.id, start_date: p.start_date || '', end_date: p.end_date || '' })}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg></button>
                                  {!!p.is_active && <button className="org-roster-iconbtn is-danger" title={`No longer with ${previewClient.preferred_name}`} aria-label={`No longer with ${previewClient.preferred_name}`} onClick={() => updateContactOrgRole(p.id, { is_active: 0, end_date: 'now' }).then(() => reloadOrgPeople(orgId, orgPeopleShowPast))}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg></button>}
                                </div>
                              )}
                            </div>
                            )
                          })
                      ) : (
                        contactOrgRoles.length === 0
                          ? <div style={{ padding: '24px 20px', color: '#94a3b8', fontSize: '0.85rem', textAlign: 'center' }}>Not appointed to any client.</div>
                          : contactOrgRoles.map(p => {
                            const editing = !!(roleDateEdit && roleDateEdit.id === p.id)
                            return (
                            <div key={p.id} className="org-roster-row" style={{ opacity: p.is_active ? 1 : 0.6 }}>
                              <div className="org-roster-main">
                                <div className="org-roster-name-line">
                                  <span className="hs-name-link" style={{ fontWeight: 600, fontSize: '0.85rem', color: '#33475b' }} onClick={() => { fetchClient(p.org_id).then(cl => { if (cl) { setRelRosterModal(null); if (applyClientOrRedirect(cl)) return; setContactDetailTab('about') } }).catch(() => {}) }}>{p.org_name || p.org_id}</span>
                                  {!!p.is_primary && <span className="org-roster-badge" style={{ background: '#dcfce7', color: '#16a34a' }}>Primary</span>}
                                  {!p.is_active && <span className="org-roster-badge" style={{ background: '#f1f5f9', color: '#94a3b8' }}>Past</span>}
                                </div>
                                <div className="org-roster-sub">{p.role_label} · {p.end_date ? `Assigned ${p.start_date || '—'} → ${p.end_date}` : `Assigned ${p.start_date || '—'}`}</div>
                                {editing && (
                                  <div className="org-roster-dateform">
                                    <label style={{ fontSize: '0.7rem', color: '#64748b' }}>Assigned<input type="date" value={roleDateEdit!.start_date} onChange={e => setRoleDateEdit(d => d ? { ...d, start_date: e.target.value } : d)} style={{ display: 'block', fontSize: '0.72rem', padding: '3px 6px', border: '1px solid #cbd6e2', borderRadius: 5 }} /></label>
                                    <label style={{ fontSize: '0.7rem', color: '#64748b' }}>End<input type="date" value={roleDateEdit!.end_date} onChange={e => setRoleDateEdit(d => d ? { ...d, end_date: e.target.value } : d)} style={{ display: 'block', fontSize: '0.72rem', padding: '3px 6px', border: '1px solid #cbd6e2', borderRadius: 5 }} /></label>
                                    <button disabled={roleDateSaving} className="btn-primary" style={{ fontSize: '0.72rem', padding: '4px 12px' }} onClick={async () => { setRoleDateSaving(true); try { await updateContactOrgRole(p.id, { start_date: roleDateEdit!.start_date || null, end_date: roleDateEdit!.end_date || null }); setRoleDateEdit(null); reloadContactOrgRoles(contactId, contactRoleShowPast) } catch (e: any) { alert(e.message || 'Failed to save dates') } finally { setRoleDateSaving(false) } }}>Save</button>
                                    <button disabled={roleDateSaving} style={{ background: 'none', border: '1px solid #cbd6e2', borderRadius: 5, color: '#64748b', fontSize: '0.72rem', cursor: 'pointer', padding: '4px 12px' }} onClick={() => setRoleDateEdit(null)}>Cancel</button>
                                  </div>
                                )}
                              </div>
                              {!editing && (
                                <div className="org-roster-actions">
                                  {!!p.is_active && !p.is_primary && <button className="org-roster-iconbtn is-primary" title="Set as primary" aria-label="Set as primary" onClick={() => updateContactOrgRole(p.id, { is_primary: 1 }).then(() => reloadContactOrgRoles(contactId, contactRoleShowPast))}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg></button>}
                                  <button className="org-roster-iconbtn" title="Edit dates" aria-label="Edit dates" onClick={() => setRoleDateEdit({ id: p.id, start_date: p.start_date || '', end_date: p.end_date || '' })}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg></button>
                                  {!!p.is_active && <button className="org-roster-iconbtn is-danger" title={`No longer with ${p.org_name || 'org'}`} aria-label={`No longer with ${p.org_name || 'org'}`} onClick={() => updateContactOrgRole(p.id, { is_active: 0, end_date: 'now' }).then(() => reloadContactOrgRoles(contactId, contactRoleShowPast))}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg></button>}
                                </div>
                              )}
                            </div>
                            )
                          })
                      )}
                    </div>
                  </div>
                </div>
              )
            })()}

            {/* ===== Stackable mini-360 quick-peek host =====
                Mounted once; renders only the top-of-stack peek (lower entries =
                breadcrumb). "Open full record" closes the stack and navigates to
                the entity's full record (orgs/people route through the merged-
                loser-safe applyClientOrRedirect; deal/lead/project set the hash). */}
            <EntityQuickPeekHost
              controller={entityPeek}
              onOpenFull={(e: PeekEntity) => {
                entityPeek.reset()
                if (e.type === 'org' || e.type === 'person') {
                  const segRoot: 'contacts' | 'clients' = e.type === 'org' ? 'clients' : 'contacts'
                  setContactDetailOpen(true)
                  setContactDetailTab('about')
                  setActiveView(e.type === 'org' ? 'companies' : 'contacts')
                  fetchClient(e.id).then(c => { if (applyClientOrRedirect(c, { segRoot })) return; window.location.hash = `/${segRoot}/record/${encodeURIComponent(e.id)}` }).catch(() => {})
                } else if (e.type === 'deal') {
                  window.location.hash = `/deals/record/${encodeURIComponent(e.id)}`
                } else if (e.type === 'lead') {
                  // No lead record page — open the leads list + preview pane.
                  setActiveView('leads')
                  window.location.hash = '/leads'
                  fetchLead(Number(e.id)).then(l => { if (l) setPreviewLead(l) }).catch(() => {})
                } else if (e.type === 'project') {
                  window.location.hash = `/projects/record/${encodeURIComponent(e.id)}`
                }
              }}
            />

            {/* ========== Quick-create popup modals ========== */}
            {quickCreate && (
              <div className="modal-overlay" onClick={() => !quickCreateSaving && setQuickCreate(null)}>
                <div className="modal-card" style={{ width: 460, padding: 0 }} onClick={e => e.stopPropagation()}>
                  <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#33475b' }}>
                      {quickCreate === 'ticket' && 'Create ticket'}
                      {quickCreate === 'project' && 'Create project'}
                      {quickCreate === 'attachment' && 'Upload attachments'}
                    </h3>
                    <button onClick={() => !quickCreateSaving && setQuickCreate(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4, borderRadius: 4 }} aria-label="Close">
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                    </button>
                  </div>
                  <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>

                    {/* ---- Ticket form ---- */}
                    {quickCreate === 'ticket' && (<>
                      <div className="create-panel-field" style={{ marginBottom: 0 }}>
                        <label>Title *</label>
                        <input autoFocus placeholder="What's the issue?" value={qcTicketForm.title} onChange={e => setQcTicketForm({ ...qcTicketForm, title: e.target.value })} />
                      </div>
                      <div className="create-panel-field" style={{ marginBottom: 0 }}>
                        <label>Description</label>
                        <textarea rows={3} placeholder="Describe the ticket..." value={qcTicketForm.description} onChange={e => setQcTicketForm({ ...qcTicketForm, description: e.target.value })} style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem', resize: 'vertical', fontFamily: 'inherit' }} />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <div className="create-panel-field" style={{ marginBottom: 0 }}>
                          <label>Priority</label>
                          <select value={qcTicketForm.priority} onChange={e => setQcTicketForm({ ...qcTicketForm, priority: e.target.value })} style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem' }}>
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                          </select>
                        </div>
                        <div className="create-panel-field" style={{ marginBottom: 0 }}>
                          <label>Status</label>
                          <select value={qcTicketForm.status} onChange={e => setQcTicketForm({ ...qcTicketForm, status: e.target.value })} style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem' }}>
                            <option value="open">Open</option>
                            <option value="in_progress">In progress</option>
                            <option value="closed">Closed</option>
                          </select>
                        </div>
                      </div>
                    </>)}

                    {/* ---- Project form ---- */}
                    {quickCreate === 'project' && (<>
                      {contactDetailDeals.length === 0 && (
                        <div style={{ padding: '10px 12px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 6, fontSize: '0.82rem', color: '#9a3412' }}>
                          This contact has no deals yet. Projects spawn from deals — create a deal first.
                        </div>
                      )}
                      <div className="create-panel-field" style={{ marginBottom: 0 }}>
                        <label>Project name *</label>
                        <input autoFocus placeholder="Project name" value={qcProjectForm.name} onChange={e => setQcProjectForm({ ...qcProjectForm, name: e.target.value })} />
                      </div>
                      {contactDetailDeals.length > 0 && (
                        <div className="create-panel-field" style={{ marginBottom: 0 }}>
                          <label>Linked deal</label>
                          <select value={qcProjectForm.dealId} onChange={e => setQcProjectForm({ ...qcProjectForm, dealId: e.target.value })} style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem' }}>
                            {contactDetailDeals.map(d => (
                              <option key={d.id} value={d.id}>{d.title}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <div className="create-panel-field" style={{ marginBottom: 0 }}>
                          <label>Status</label>
                          <select value={qcProjectForm.status} onChange={e => setQcProjectForm({ ...qcProjectForm, status: e.target.value })} style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem' }}>
                            <option value="bidding">Bidding</option>
                            <option value="active">Active</option>
                            <option value="on_hold">On hold</option>
                            <option value="completed">Completed</option>
                          </select>
                        </div>
                        <div className="create-panel-field" style={{ marginBottom: 0 }}>
                          <label>Value</label>
                          <input type="text" inputMode="decimal" placeholder="0" value={qcProjectForm.value} onChange={e => setQcProjectForm({ ...qcProjectForm, value: e.target.value.replace(/[^0-9.]/g, '') })} />
                        </div>
                      </div>
                    </>)}

                    {/* ---- Attachment form ---- */}
                    {quickCreate === 'attachment' && (<>
                      {contactDetailDeals.length === 0 && contactDetailLeads.length === 0 ? (
                        <div style={{ padding: '10px 12px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 6, fontSize: '0.82rem', color: '#9a3412' }}>
                          Attachments are stored on a deal's activity timeline. Create a deal or lead for this contact first, then upload files from there.
                        </div>
                      ) : (<>
                        {contactDetailDeals.length > 1 && (
                          <div className="create-panel-field" style={{ marginBottom: 0 }}>
                            <label>Attach to deal</label>
                            <select value={qcAttachmentDealId} onChange={e => setQcAttachmentDealId(e.target.value)} style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem' }}>
                              {contactDetailDeals.map(d => (
                                <option key={d.id} value={d.id}>{d.title}</option>
                              ))}
                            </select>
                          </div>
                        )}
                        {contactDetailDeals.length === 1 && (
                          <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
                            Attaching to deal: <strong>{contactDetailDeals[0].title}</strong>
                          </div>
                        )}
                        <input
                          type="file"
                          multiple
                          ref={qcAttachmentFileInputRef}
                          style={{ display: 'none' }}
                          onChange={e => {
                            const picked = Array.from(e.target.files || [])
                            if (picked.length) setQcAttachmentFiles(prev => [...prev, ...picked])
                            e.target.value = ''
                          }}
                        />
                        <button
                          onClick={() => qcAttachmentFileInputRef.current?.click()}
                          style={{ padding: '10px 16px', fontSize: '0.85rem', border: '1.5px dashed #cbd6e2', borderRadius: 6, background: '#f8fafc', cursor: 'pointer', color: '#33475b', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                          Choose files
                        </button>
                        {qcAttachmentFiles.length > 0 && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {qcAttachmentFiles.map((f, i) => (
                              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#f1f5f9', borderRadius: 4, fontSize: '0.78rem', color: '#33475b' }}>
                                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                                <span style={{ color: '#94a3b8', fontSize: '0.72rem' }}>{(f.size / 1024).toFixed(0)}kb</span>
                                <button onClick={() => setQcAttachmentFiles(prev => prev.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: 14, padding: 0 }}>&times;</button>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="create-panel-field" style={{ marginBottom: 0 }}>
                          <label>Note (optional)</label>
                          <textarea rows={2} placeholder="Add a note about these files..." value={qcAttachmentNote} onChange={e => setQcAttachmentNote(e.target.value)} style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem', resize: 'vertical', fontFamily: 'inherit' }} />
                        </div>
                      </>)}
                    </>)}

                  </div>
                  <div style={{ padding: '12px 20px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <button
                      className="prefs-cancel-btn"
                      disabled={quickCreateSaving}
                      onClick={() => setQuickCreate(null)}
                    >Cancel</button>
                    <button
                      className="prefs-save-btn"
                      disabled={
                        quickCreateSaving ||
                        (quickCreate === 'ticket' && !qcTicketForm.title.trim()) ||
                        (quickCreate === 'project' && !qcProjectForm.name.trim()) ||
                        (quickCreate === 'attachment' && (qcAttachmentFiles.length === 0 || (contactDetailDeals.length === 0 && contactDetailLeads.length === 0)))
                      }
                      onClick={async () => {
                        if (!previewClient) return
                        setQuickCreateSaving(true)
                        try {
                          if (quickCreate === 'ticket') {
                            await createTicket({
                              title: qcTicketForm.title.trim(),
                              description: qcTicketForm.description.trim() || undefined,
                              priority: qcTicketForm.priority,
                              status: qcTicketForm.status,
                              unifiedClientId: previewClient.unified_client_id,
                            })
                            // Refresh the right-sidebar Tickets card and stat row.
                            const tickets = await fetchContactTickets(previewClient.unified_client_id)
                            setContactTickets(Array.isArray(tickets) ? tickets : [])
                            setTicketsRefreshTick(t => t + 1)
                          } else if (quickCreate === 'project') {
                            await createCrmProject({
                              name: qcProjectForm.name.trim(),
                              client_id: previewClient.unified_client_id,
                              client_name: previewClient.preferred_name,
                              contact_id: previewClient.unified_client_id,
                              deal_id: qcProjectForm.dealId ? Number(qcProjectForm.dealId) : undefined,
                              status: qcProjectForm.status,
                              contract_value: qcProjectForm.value ? Number(qcProjectForm.value) : 0,
                              value: qcProjectForm.value ? Number(qcProjectForm.value) : 0,
                            })
                            // Refresh projects
                            const projects = await getClientProjects(previewClient.unified_client_id)
                            setContactDetailProjects(Array.isArray(projects) ? projects : [])
                          } else if (quickCreate === 'attachment') {
                            const dealId = qcAttachmentDealId || contactDetailDeals[0]?.id
                            if (!dealId) throw new Error('No deal selected')
                            await uploadDealAttachments(dealId, qcAttachmentFiles, qcAttachmentNote.trim() || undefined)
                            // Refresh attachments
                            const attachments = await fetchClientAttachments(previewClient.unified_client_id)
                            setContactDetailAttachments(Array.isArray(attachments) ? attachments : [])
                          }
                          setQuickCreate(null)
                        } catch (e: any) {
                          alert('Failed to create: ' + (e?.message || String(e)))
                        } finally {
                          setQuickCreateSaving(false)
                        }
                      }}
                    >
                      {quickCreateSaving ? 'Saving…' : quickCreate === 'attachment' ? 'Upload' : 'Create'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Sticky "Save changes" footer — mirrors the preferences page pattern */}
            {Object.keys(contactPendingChanges).length > 0 && (
              <div className="prefs-save-footer crp-save-footer">
                <div className="prefs-save-footer-inner">
                  <div className="prefs-save-footer-btns">
                    <button
                      className="prefs-save-btn"
                      disabled={contactSavingChanges}
                      onClick={async () => {
                        if (!previewClient) return
                        setContactSavingChanges(true)
                        try {
                          const updated = await updateClient(previewClient.unified_client_id, contactPendingChanges)
                          setPreviewClient(updated)
                          setContactPendingChanges({})
                        } catch (e: any) {
                          alert('Failed to save: ' + (e?.message || String(e)))
                        } finally {
                          setContactSavingChanges(false)
                        }
                      }}
                    >{contactSavingChanges ? 'Saving…' : 'Save'}</button>
                    <button
                      className="prefs-cancel-btn"
                      disabled={contactSavingChanges}
                      onClick={() => setContactPendingChanges({})}
                    >Cancel</button>
                  </div>
                  <p className="prefs-save-footer-msg">
                    You've changed {Object.keys(contactPendingChanges).length} setting{Object.keys(contactPendingChanges).length === 1 ? '' : 's'}.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Lead full-page record removed — lead rows open the PREVIEW SIDE PANEL only. */}

        {/* ========== Deal Record Loading State ========== */}
        {activeView === 'deals' && dealDetailOpen && !selectedDeal && (
          <div className="crp-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <DotLottieReact src="/loading.lottie" loop autoplay style={{ width: 120, height: 120 }} />
          </div>
        )}

        {/* ========== Deal Record Detail Page ========== */}
        {activeView === 'deals' && dealDetailOpen && selectedDeal && (() => {
          const fmtDate = (d: string | null | undefined) => {
            if (!d) return '--'
            try { return formatDatePref(d) }
            catch { return '--' }
          }
          const getDealActType = (act: CrmActivity) => {
            const pt = (act.provider_type || '').toUpperCase()
            if (pt === '2' || pt === 'CALL' || pt.includes('CALL')) return 'Call'
            if (pt === '4' || pt === 'EMAIL' || pt.includes('EMAIL')) return 'Email'
            if (pt === '3' || pt === 'TASK' || pt.includes('TASK')) return 'Task'
            if (pt.includes('MEETING')) return 'Meeting'
            return 'Note'
          }
          const dealActIcon = (type: string) => {
            switch (type) {
              case 'Email': return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
              case 'Call': return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.58 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
              case 'Task': return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
              case 'Meeting': return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              default: return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
            }
          }
          // Pipeline stepper data
          const dealDetailStages = (allPipelineStages ?? []).filter(s => !isTerminalPipelineStage(s))
          const stepperStages = dealDetailStages.length > 0
            ? dealDetailStages.map(s => ({ id: s.stageId, label: s.label, color: s.color }))
            : PIPELINE_STAGES
          const currentDealStageIdx = stepperStages.findIndex(s => s.id === selectedDeal.stage)
          const currentDealStageColor = stepperStages[currentDealStageIdx]?.color || STAGE_COLORS[selectedDeal.stage] || '#94a3b8'
          return (
          <div className="crp-page">
            {/* ---- LEFT SIDEBAR ---- */}
            <div className="crp-left">
              <div className="crp-left-scroll">
                {/* Highlight Card (breadcrumb + avatar + actions) — matching contact detail */}
                <div className="crp-card">
                  <div style={{ padding: '8px 16px 0', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#f59e0b' }}>Deal Record</div>
                  <div className="crp-breadcrumb">
                    <button className="crp-back-btn" onClick={() => { setDealDetailOpen(false); setSelectedDeal(null); window.location.hash = '/deals' }}>
                      <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M21.29 27.71c-.29 0-.58-.11-.81-.33L10.19 17.09a1.14 1.14 0 0 1 0-1.62L20.48 5.18a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62l-9.48 9.48 9.48 9.48a1.14 1.14 0 0 1-.81 1.95"/></svg>
                      <span>Deals</span>
                    </button>
                    <div className="crp-actions-wrap" style={{ position: 'relative' }}>
                      <button className="crp-actions-label" onClick={() => setDealActionsOpen(!dealActionsOpen)}>
                        Actions
                        <svg className="hs-tb-caret" width="8" height="8" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
                      </button>
                      {dealActionsOpen && (
                        <>
                          <div className="cmd-backdrop-light" onClick={() => setDealActionsOpen(false)} />
                          <div className="hs-viewtype-panel" style={{ position: 'fixed', right: 'auto', left: (() => { const el = document.querySelector('.crp-actions-wrap'); if (el) { const r = el.getBoundingClientRect(); return r.left } return 100 })(), top: (() => { const el = document.querySelector('.crp-actions-wrap'); if (el) { const r = el.getBoundingClientRect(); return r.bottom + 4 } return 60 })(), minWidth: 200 }}>
                            <button
                              className="hs-viewtype-option"
                              style={{ color: '#dc2626' }}
                              onClick={() => {
                                setDealActionsOpen(false)
                                startDealDelete(
                                  { id: Number(selectedDeal.id), title: selectedDeal.title },
                                  () => { setDealDetailOpen(false); setSelectedDeal(null); window.location.hash = '/deals' }
                                )
                              }}
                            >
                              Delete deal
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="crp-highlight">
                    <div className="crp-avatar-lg" style={{ background: '#e8f0fe', color: '#1a73e8' }}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                    </div>
                    <div className="crp-highlight-info" style={{ minWidth: 0 }}>
                      {dealTitleEditing ? (
                        <input
                          autoFocus
                          className="crp-name"
                          style={{ width: '100%', border: '1px solid #cbd6e2', borderRadius: 4, padding: '2px 6px', font: 'inherit', color: 'inherit', background: '#fff' }}
                          value={dealTitleDraft}
                          onChange={(e) => setDealTitleDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') { setDealTitleEditing(false); setDealTitleDraft('') }
                            else if (e.key === 'Enter') { e.currentTarget.blur() }
                          }}
                          onBlur={() => {
                            // Stage into dealPendingChanges (parity with the About-card
                            // name editor) so the Save/Cancel bar appears — do NOT auto-save.
                            const next = dealTitleDraft.trim()
                            if (next) {
                              setDealPendingChanges(prev => {
                                const n = { ...prev }
                                if (next === (selectedDeal.title || '')) delete n.title
                                else n.title = next
                                return n
                              })
                            }
                            setDealTitleEditing(false)
                            setDealTitleDraft('')
                          }}
                        />
                      ) : (
                        <h2
                          className="crp-name crp-name--editable"
                          title="Click to edit deal name"
                          onClick={() => { setDealTitleDraft((('title' in dealPendingChanges ? (dealPendingChanges as any).title : selectedDeal.title) as string) || ''); setDealTitleEditing(true) }}
                        >
                          <span className="crp-name-text">
                            {('title' in dealPendingChanges ? (dealPendingChanges as any).title : selectedDeal.title) as string}
                            {'title' in dealPendingChanges && <span style={{ fontSize: '0.7rem', fontWeight: 400, color: '#b45309', marginLeft: 6 }}>pending</span>}
                          </span>
                          <span className="crp-name-edit-chip" aria-hidden>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                          </span>
                        </h2>
                      )}
                      <p className="crp-subtitle">{selectedDeal.pipelineName || 'Deal'} &middot; {selectedDeal.stageLabel || selectedDeal.stage || ''}</p>
                      {Number(selectedDeal.opportunity) > 0 && (
                        <p className="crp-subtitle" style={{ fontWeight: 600, color: '#33475b' }}>
                          {currencyFormatter.format(Number(selectedDeal.opportunity))}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="crp-action-btns">
                    <button className="crp-action-btn" title="Note" onClick={() => setShowNoteModal({ ownerTypeId: 2, ownerId: Number(selectedDeal.id) })}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                      <span>Note</span>
                    </button>
                    <button
                      className="crp-action-btn"
                      title="Email"
                      onClick={() => {
                        // Open the functional Compose modal (Mail.Send via Graph).
                        // Prefill the recipient from the deal's pinned/derived
                        // primary email if present. Log as an activity on send.
                        const eff = (selectedDeal as any).effective_primary_email
                        const addr = eff?.address ? String(eff.address) : ''
                        setComposeContext({
                          to: addr ? [addr] : [],
                          subject: selectedDeal.title ? `Re: ${selectedDeal.title}` : '',
                          entityType: 'deal',
                          entityId: Number(selectedDeal.id),
                          entityLabel: `Deal #${selectedDeal.id} · ${selectedDeal.title}`,
                        })
                        setComposeOpen(true)
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                      <span>Email</span>
                    </button>
                    <button className="crp-action-btn" title="Call" onClick={() => { setCallingPanelTab('keypad'); if (!callingPanelOpen) setCallingPanelOpen(true) }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                      <span>Call</span>
                    </button>
                    <button className="crp-action-btn" title="Task">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                      <span>Task</span>
                    </button>
                    {/* Clock-in / clock-out quick action hidden per user request —
                        labor/time is tracked on projects, not deals. */}
                  </div>
                </div>

                {/* Key Information card — includes compact Contact / Primary Email /
                    Billing Email / Phone rows with anchored popovers. The standalone
                    Contact card was folded into this card for a tighter sidebar. */}
                {(() => {
                  const rawAssociated = dealContacts.filter((dc: any) => dc.role !== 'company')
                  // Fall back through unifiedClientId → dealLinkedClient → BX-padded
                  // contactId so pre-unified migrated deals (where only bare
                  // contact_id is set) still surface a synthetic associated
                  // entry instead of collapsing to "Pick a contact first".
                  const rawContactId = (selectedDeal as any).contactId
                  const legacyClientId = (selectedDeal as any).primaryContactId
                    || (selectedDeal as any).unifiedClientId
                    || dealLinkedClient?.unified_client_id
                    || (rawContactId != null ? `BX-${String(rawContactId).padStart(5, '0')}` : null)
                  const legacyAlreadyAssociated = legacyClientId
                    ? rawAssociated.some((dc: any) => dc.unified_client_id === legacyClientId)
                    : false
                  const associated = (rawAssociated.length === 0 && legacyClientId && dealLinkedClient && !legacyAlreadyAssociated)
                    ? [{
                        link_id: `legacy-${legacyClientId}`,
                        unified_client_id: legacyClientId,
                        role: 'associated',
                        preferred_name: dealLinkedClient.preferred_name || legacyClientId,
                        preferred_phone: dealLinkedClient.preferred_phone || '',
                        preferred_email: dealLinkedClient.preferred_email || '',
                      }]
                    : rawAssociated
                  const pinnedContactId = (selectedDeal as any).primaryContactId || null
                  const selectedContactId = pinnedContactId || (associated[0]?.unified_client_id ?? null)
                  const selectedAssoc = associated.find((dc: any) => dc.unified_client_id === selectedContactId) || null
                  const currentPrimaryEmailId = (selectedDeal as any).primaryEmailId ?? null
                  const currentBillingEmailId = (selectedDeal as any).billingEmailId ?? null
                  const effectivePrimary = (selectedDeal as any).effective_primary_email || null
                  const effectiveBilling = (selectedDeal as any).effective_billing_email || null
                  const hasPrimaryFallback = !!effectivePrimary
                  const hasBillingFallback = !!effectiveBilling
                  const primaryMissing = !hasPrimaryFallback && !currentPrimaryEmailId

                  const persistDealFields = async (patch: Record<string, unknown>) => {
                    setSelectedDeal((d: any) => d ? { ...d, ...Object.fromEntries(Object.entries(patch).map(([k, v]) => [k, v])) } : d)
                    try {
                      await updateDeal(Number(selectedDeal.id), patch)
                      const fresh = await fetchDeal(Number(selectedDeal.id))
                      setSelectedDeal(fresh)
                      setLiveDeals(prev => prev ? prev.map(d => d.id === fresh.id ? fresh : d) : prev)
                    } catch (e: any) {
                      alert('Failed to save: ' + (e?.message || String(e)))
                    }
                  }

                  const pickContact = async (cid: string) => {
                    setDealKeyInfoPopover(null)
                    if (!cid || cid === selectedContactId) return
                    await persistDealFields({
                      primaryContactId: cid,
                      primaryEmailId: null,
                      billingEmailId: null,
                    })
                  }
                  const pinEmail = async (emailId: number, kind: 'primary' | 'billing') => {
                    setDealKeyInfoPopover(null)
                    if (kind === 'primary') {
                      await persistDealFields({ primaryEmailId: emailId })
                    } else {
                      await persistDealFields({ billingEmailId: emailId })
                    }
                  }
                  const addEmailAndPin = async (address: string, kind: 'primary' | 'billing') => {
                    const addr = address.trim()
                    if (!addr || !selectedContactId) return
                    try {
                      const resp = await fetch(`/api/crm/contacts/${encodeURIComponent(selectedContactId)}/emails`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ address: addr }),
                      })
                      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
                      const { id: newId } = await resp.json()
                      // Refresh contact emails list
                      const listResp = await fetch(`/api/crm/contacts/${encodeURIComponent(selectedContactId)}/emails`)
                      if (listResp.ok) setDealContactEmails(await listResp.json())
                      if (newId) {
                        await persistDealFields(kind === 'primary' ? { primaryEmailId: newId } : { billingEmailId: newId })
                      }
                      setDealKeyInfoAddEmail('')
                      setDealKeyInfoAddEmailOpen(false)
                      setDealKeyInfoPopover(null)
                    } catch (e: any) {
                      alert('Failed to add email: ' + (e?.message || String(e)))
                    }
                  }
                  const savePhone = async (next: string) => {
                    const trimmed = next.trim()
                    const current = selectedAssoc?.preferred_phone || ''
                    setDealKeyInfoPopover(null)
                    if (!selectedContactId || trimmed === current) return
                    try {
                      await updateClient(selectedContactId, { preferredPhone: trimmed || null })
                      // Refresh dealLinkedClient / dealContacts so the row value updates.
                      if (dealLinkedClient && dealLinkedClient.unified_client_id === selectedContactId) {
                        setDealLinkedClient({ ...dealLinkedClient, preferred_phone: trimmed })
                      }
                      setDealContacts(prev => prev.map((dc: any) =>
                        dc.unified_client_id === selectedContactId ? { ...dc, preferred_phone: trimmed } : dc
                      ))
                    } catch (e: any) {
                      alert('Failed to save phone: ' + (e?.message || String(e)))
                    }
                  }

                  // Refresh dealContacts from the API after associating a new
                  // contact via the search/add-new flows. Mirrors the GET in
                  // the deal-contacts effect elsewhere; kept inline so we can
                  // await it before pinning primary_contact_id.
                  const refreshDealContacts = async (): Promise<any[]> => {
                    try {
                      const r = await fetch(`/api/crm/deals/${Number(selectedDeal.id)}/contacts`)
                      if (!r.ok) return []
                      const list = await r.json() as any[]
                      setDealContacts(Array.isArray(list) ? list : [])
                      return Array.isArray(list) ? list : []
                    } catch { return [] }
                  }

                  // Search-result row → associate (if not already linked) and pin.
                  const pickFromSearch = async (cid: string) => {
                    if (!cid) return
                    const already = dealContacts.some((dc: any) => dc.unified_client_id === cid)
                    try {
                      if (!already) {
                        const r = await fetch(`/api/crm/deals/${Number(selectedDeal.id)}/contacts`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ unified_client_id: cid }),
                        })
                        if (!r.ok) throw new Error(`HTTP ${r.status}`)
                        await refreshDealContacts()
                      }
                      await persistDealFields({
                        primaryContactId: cid,
                        primaryEmailId: null,
                        billingEmailId: null,
                      })
                      setDealKeyInfoContactSearch('')
                      setDealKeyInfoContactSearchResults([])
                      setDealKeyInfoPopover(null)
                    } catch (e: any) {
                      alert('Failed to add contact to deal: ' + (e?.message || String(e)))
                    }
                  }

                  // Add-new-contact form submit → create client → associate → pin
                  // → optionally add the typed email as is_primary on that contact.
                  const submitNewContact = async () => {
                    const name = dealKeyInfoAddContactForm.name.trim()
                    if (!name) return
                    // Reject "Last, First" / "&"-couple / email-shaped names before
                    // creating (server enforces the same spec as a backstop).
                    const nameCheck = validatePersonNameInput(name)
                    if (!nameCheck.ok) { alert(nameCheck.message); return }
                    setDealKeyInfoAddContactSaving(true)
                    try {
                      const createResp = await fetch('/api/crm/clients', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          preferredName: name,
                          preferredEmail: dealKeyInfoAddContactForm.email.trim() || null,
                          preferredPhone: dealKeyInfoAddContactForm.phone.trim() || null,
                          clientType: 'person',
                        }),
                      })
                      if (!createResp.ok) throw new Error(`HTTP ${createResp.status}`)
                      const created = await createResp.json() as any
                      const newCid = created?.unified_client_id
                      if (!newCid) throw new Error('Missing unified_client_id from create response')

                      // Associate to deal
                      const linkResp = await fetch(`/api/crm/deals/${Number(selectedDeal.id)}/contacts`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ unified_client_id: newCid }),
                      })
                      if (!linkResp.ok) throw new Error(`Associate failed: HTTP ${linkResp.status}`)
                      await refreshDealContacts()

                      // Pin primary contact + reset email pins (let auto-pick or
                      // the explicit email-add below choose primary email).
                      await persistDealFields({
                        primaryContactId: newCid,
                        primaryEmailId: null,
                        billingEmailId: null,
                      })

                      // If an email was provided, add it as the contact's primary
                      // email; the auto-pick effect will then pin it as the deal's
                      // primary_email_id since it'll be the only email on the contact.
                      const emailAddr = dealKeyInfoAddContactForm.email.trim()
                      if (emailAddr) {
                        try {
                          await fetch(`/api/crm/contacts/${encodeURIComponent(newCid)}/emails`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ address: emailAddr, is_primary: true }),
                          })
                        } catch { /* non-fatal — contact already created+linked */ }
                      }

                      setDealKeyInfoAddContactForm({ name: '', email: '', phone: '' })
                      setDealKeyInfoAddContactOpen(false)
                      setDealKeyInfoContactSearch('')
                      setDealKeyInfoContactSearchResults([])
                      setDealKeyInfoPopover(null)
                    } catch (e: any) {
                      alert('Failed to create contact: ' + (e?.message || String(e)))
                    } finally {
                      setDealKeyInfoAddContactSaving(false)
                    }
                  }

                  // Popover uses position:fixed so it escapes .crp-card
                  // overflow:hidden and any ancestor stacking context. Top/left
                  // are computed from the anchor span's getBoundingClientRect()
                  // in the Key Info popover useEffect above. z-index 9000 sits
                  // above sidebar cards (which use default auto / low values)
                  // but below modal overlays (which use 200+).
                  const popoverPos = dealKeyInfoPopoverPos
                  const popoverStyle: React.CSSProperties = {
                    position: 'fixed',
                    top: popoverPos?.top ?? -9999,
                    left: popoverPos?.left ?? -9999,
                    width: 280,
                    // When flipped upward, recompute() clamps maxHeight to
                    // spaceAbove - 16 so the popover can't overflow off the top
                    // of the viewport.
                    maxHeight: popoverPos?.maxHeight ?? 280,
                    overflowY: 'auto',
                    background: '#fff',
                    border: '1px solid #cbd6e2',
                    borderRadius: 6,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    padding: 6,
                    zIndex: 9000,
                    visibility: popoverPos ? 'visible' : 'hidden',
                  }
                  const valueClickable: React.CSSProperties = {
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '2px 4px',
                    marginLeft: -4,
                    borderRadius: 3,
                    transition: 'background 100ms',
                  }
                  // Hover handlers added to each value span — only tint
                  // background when the panel is in edit mode (so static
                  // display-mode values don't move when hovered).
                  const onValueHoverEnter = (e: React.MouseEvent<HTMLSpanElement>) => {
                    if (!dealContactPanelEditing) return
                    ;(e.currentTarget as HTMLSpanElement).style.background = '#dbeafe'
                  }
                  const onValueHoverLeave = (e: React.MouseEvent<HTMLSpanElement>) => {
                    ;(e.currentTarget as HTMLSpanElement).style.background = 'transparent'
                  }

                  // Shared dedup-hint card — amber-toned, avatar + name/value
                  // summary, right-aligned primary action that OPENS the confirm
                  // modal (does not immediately switch). Used by all 4 hint
                  // sites (add-email, phone, new-contact-email, new-contact-phone).
                  const renderDedupHint = (opts: {
                    match: DedupMatch
                    field: 'email' | 'phone'
                    value: string
                    source: DedupConfirm['source']
                    isSelf: boolean
                  }) => {
                    const { match, field, value, source, isSelf } = opts
                    const display = field === 'email' ? (match.email || value) : (match.phone || value)
                    const actionLabel = source === 'new-contact-email' || source === 'new-contact-phone' ? 'Use instead' : 'Review'
                    return (
                      <div
                        style={{
                          marginTop: 6,
                          padding: '6px 10px',
                          background: isSelf ? '#ecfeff' : '#fffbeb',
                          border: isSelf ? '1px solid #a5f3fc' : '1px solid #fde68a',
                          borderRadius: 6,
                          fontSize: '0.76rem',
                          color: '#78350f',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                        }}
                        title={isSelf
                          ? `This ${field} is already on the current contact`
                          : `This ${field} already exists on another contact — click to review options`
                        }
                      >
                        {/* Info icon */}
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={isSelf ? '#0891b2' : '#b45309'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                          <circle cx="12" cy="12" r="10" />
                          <line x1="12" y1="16" x2="12" y2="12" />
                          <line x1="12" y1="8" x2="12.01" y2="8" />
                        </svg>
                        {/* Avatar circle */}
                        <span
                          style={{
                            width: 22, height: 22, borderRadius: '50%',
                            background: isSelf ? '#06b6d4' : '#f59e0b',
                            color: '#fff',
                            fontSize: '0.64rem',
                            fontWeight: 700,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >{getInitials(match.name) || '?'}</span>
                        {/* Name + matched value */}
                        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
                          <span style={{ fontWeight: 600, color: isSelf ? '#155e75' : '#78350f', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {match.name}
                            {isSelf ? <span style={{ fontSize: '0.62rem', fontWeight: 600, color: '#0891b2', marginLeft: 6 }}>(current)</span> : null}
                          </span>
                          {display ? (
                            <span style={{ fontSize: '0.68rem', color: isSelf ? '#0e7490' : '#92400e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{display}</span>
                          ) : null}
                        </div>
                        {/* Primary action (opens confirm modal). Hidden when
                            match is the current contact — there's nothing to
                            do, just informational. */}
                        {!isSelf && (
                          <button
                            type="button"
                            onMouseDown={e => e.preventDefault()}
                            onClick={() => {
                              setDedupConfirm({
                                match,
                                field,
                                value,
                                source,
                                currentContactId: selectedContactId,
                              })
                            }}
                            style={{
                              flexShrink: 0,
                              background: '#fff',
                              border: '1px solid #fbbf24',
                              color: '#78350f',
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              padding: '3px 10px',
                              borderRadius: 999,
                              cursor: 'pointer',
                              lineHeight: 1.3,
                            }}
                            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#fef3c7' }}
                            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = '#fff' }}
                          >{actionLabel}</button>
                        )}
                      </div>
                    )
                  }

                  // Handles the 3 confirm-modal button paths. Closes the popover
                  // / inline form appropriately on success. `pickFromSearch`
                  // (above) is the "switch deal primary_contact_id" path.
                  const handleDedupConfirm = async (action: 'add-to-current' | 'switch' | 'use-existing') => {
                    if (!dedupConfirm) return
                    const { match, field, value, source, currentContactId } = dedupConfirm
                    setDedupConfirmSaving(true)
                    try {
                      if (action === 'switch' || action === 'use-existing') {
                        // Close originating popover/form; pickFromSearch pins
                        // match.id as deal primary contact.
                        if (source === 'add-email') {
                          setDealKeyInfoAddEmail(''); setDealKeyInfoAddEmailOpen(false); setDealKeyInfoAddEmailMatch(null)
                        } else if (source === 'phone') {
                          setDealKeyInfoPhoneMatch(null); setDealKeyInfoPopover(null)
                        } else if (source === 'new-contact-email' || source === 'new-contact-phone') {
                          setDealKeyInfoAddContactOpen(false)
                          setDealKeyInfoAddContactForm({ name: '', email: '', phone: '' })
                          setDealKeyInfoAddContactEmailMatch(null)
                          setDealKeyInfoAddContactPhoneMatch(null)
                        }
                        await pickFromSearch(match.id)
                      } else if (action === 'add-to-current') {
                        // Add the typed value to the CURRENT contact as an
                        // additional row (not a switch). Only valid for the
                        // add-email / phone flows — new-contact flows don't
                        // have a "current" to add to (swap to 'use-existing').
                        if (!currentContactId) return
                        if (field === 'email') {
                          const resp = await fetch(`/api/crm/contacts/${encodeURIComponent(currentContactId)}/emails`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ address: value }),
                          })
                          if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
                          // Refresh deal contact emails list so the new row appears
                          try {
                            const r = await fetch(`/api/crm/contacts/${encodeURIComponent(currentContactId)}/emails`)
                            if (r.ok) {
                              const list = await r.json() as any[]
                              setDealContactEmails(Array.isArray(list) ? list : [])
                            }
                          } catch { /* non-fatal */ }
                          setDealKeyInfoAddEmail('')
                          setDealKeyInfoAddEmailOpen(false)
                          setDealKeyInfoAddEmailMatch(null)
                        } else {
                          const digits = value.replace(/\D/g, '')
                          const resp = await fetch(`/api/crm/contacts/${encodeURIComponent(currentContactId)}/phones`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ number: digits }),
                          })
                          if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
                          try {
                            const r = await fetch(`/api/crm/contacts/${encodeURIComponent(currentContactId)}/phones`)
                            if (r.ok) {
                              const list = await r.json() as any[]
                              setContactPhones(Array.isArray(list) ? list : [])
                            }
                          } catch { /* non-fatal */ }
                          setDealKeyInfoPhoneMatch(null)
                          setDealKeyInfoPopover(null)
                        }
                      }
                      setDedupConfirm(null)
                    } catch (e: any) {
                      alert('Failed: ' + (e?.message || String(e)))
                    } finally {
                      setDedupConfirmSaving(false)
                    }
                  }

                  const renderEmailPopover = (kind: 'primary' | 'billing') => (
                    <div ref={dealKeyInfoPopoverRef} data-deal-keyinfo-popover style={popoverStyle} onClick={e => e.stopPropagation()}>
                      {dealContactEmails.length === 0 ? (
                        <div style={{ fontSize: '0.75rem', color: '#94a3b8', padding: '6px 8px' }}>No emails on this contact</div>
                      ) : (
                        dealContactEmails.map((em: any) => {
                          const pinned = kind === 'primary' ? currentPrimaryEmailId === em.id : currentBillingEmailId === em.id
                          return (
                            <div
                              key={em.id}
                              onClick={() => pinEmail(em.id, kind)}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 6,
                                padding: '6px 8px', cursor: 'pointer', borderRadius: 4,
                                background: pinned ? '#ecfdf5' : 'transparent',
                                fontSize: '0.78rem', color: '#33475b',
                              }}
                              onMouseEnter={e => { if (!pinned) (e.currentTarget as HTMLDivElement).style.background = '#f1f5f9' }}
                              onMouseLeave={e => { if (!pinned) (e.currentTarget as HTMLDivElement).style.background = 'transparent' }}
                            >
                              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{em.address}</span>
                              {em.is_primary ? <span title="Contact primary" style={{ fontSize: '0.6rem', fontWeight: 700, color: '#065f46', background: '#d1fae5', padding: '1px 5px', borderRadius: 8 }}>P</span> : null}
                              {em.is_billing ? <span title="Contact billing" style={{ fontSize: '0.6rem', fontWeight: 700, color: '#1e40af', background: '#dbeafe', padding: '1px 5px', borderRadius: 8 }}>B</span> : null}
                              {pinned ? <span style={{ fontSize: '0.6rem', fontWeight: 700, color: '#047857' }}>✓</span> : null}
                            </div>
                          )
                        })
                      )}
                      <div style={{ borderTop: '1px solid #e2e8f0', marginTop: 4, paddingTop: 4 }}>
                        {dealKeyInfoAddEmailOpen ? (
                          <>
                            <input
                              autoFocus
                              type="email"
                              placeholder="new@example.com"
                              value={dealKeyInfoAddEmail}
                              onChange={e => setDealKeyInfoAddEmail(e.target.value)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') { e.preventDefault(); addEmailAndPin(dealKeyInfoAddEmail, kind) }
                                else if (e.key === 'Escape') { e.preventDefault(); setDealKeyInfoAddEmail(''); setDealKeyInfoAddEmailOpen(false) }
                              }}
                              style={{ width: '100%', fontSize: '0.78rem', padding: '5px 8px', border: '1px solid #cbd6e2', borderRadius: 4, outline: 'none' }}
                            />
                            {dealKeyInfoAddEmailMatch && (() => {
                              const isSelf = dealKeyInfoAddEmailMatch.id === ((selectedDeal as any)?.primaryContactId || null)
                              return renderDedupHint({
                                match: dealKeyInfoAddEmailMatch,
                                field: 'email',
                                value: dealKeyInfoAddEmail.trim(),
                                source: 'add-email',
                                isSelf,
                              })
                            })()}
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={() => { setDealKeyInfoAddEmail(''); setDealKeyInfoAddEmailOpen(true) }}
                            disabled={!selectedContactId}
                            style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '6px 8px', fontSize: '0.78rem', color: selectedContactId ? '#0369a1' : '#94a3b8', cursor: selectedContactId ? 'pointer' : 'not-allowed', borderRadius: 4 }}
                          >+ Add email</button>
                        )}
                      </div>
                    </div>
                  )

                  return (
                    <>
                    <div className="crp-card">
                      <div className="crp-card-header"><h3>Key information</h3></div>
                      <div className="crp-card-body">
                        <div className="crp-field"><label>Pipeline</label><span>{selectedDeal.pipelineName || '--'}</span></div>
                        <div className="crp-field"><label>Amount</label><span>{selectedDeal.opportunity ? currencyFormatter.format(Number(selectedDeal.opportunity)) : '--'}</span></div>
                        <div className="crp-field"><label>Stage</label><span>{selectedDeal.stageLabel || selectedDeal.stage || '--'}</span></div>
                        <div className="crp-field"><label>Est. Appointment</label><span>{(() => { const a = resolveApptDateSource(selectedDeal); return a ? fmtDate(a) : '--' })()}</span></div>
                        <div className="crp-field"><label>Assigned To</label>
                          <span>
                            {selectedDeal.assignedById ? (
                              <button
                                type="button"
                                className="hr-profile-trigger"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  openHrProfileDrawer(Number(selectedDeal.assignedById), selectedDeal.assignedTo)
                                }}
                                title="View HR profile"
                              >{selectedDeal.assignedTo || '--'}</button>
                            ) : (selectedDeal.assignedTo || '--')}
                          </span>
                        </div>
                        <div className="crp-field"><label>Created</label><span>{fmtDate(selectedDeal.dateCreated)}</span></div>

                        {/* Deal Contact sub-panel — visually distinguishes the 4
                            contact-related rows (Contact / Primary / Billing /
                            Phone) from the deal-metadata rows above. Tinted
                            background + border-top + tight section header. */}
                        <div style={{
                          marginTop: 10,
                          paddingTop: 6,
                          borderTop: '1px solid #e2e8f0',
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            margin: '0 0 4px 0',
                          }}>
                            <h4 style={{
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              color: '#7c98b6',
                              textTransform: 'uppercase',
                              letterSpacing: '0.4px',
                              margin: 0,
                            }}>Deal Contact</h4>
                            <button
                              type="button"
                              onClick={() => {
                                setDealContactPanelEditing(v => {
                                  // Leaving edit mode also closes any open popover.
                                  if (v) setDealKeyInfoPopover(null)
                                  return !v
                                })
                              }}
                              title={dealContactPanelEditing ? 'Exit edit mode' : 'Edit deal contact details'}
                              style={{
                                background: 'none',
                                border: 'none',
                                padding: 2,
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                color: '#1d4ed8',
                                transition: 'color 120ms ease, transform 120ms ease',
                              }}
                              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = '#1e3a8a'; (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.12)' }}
                              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = '#1d4ed8'; (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)' }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                            </button>
                            {dealContactPanelEditing && (
                              <button
                                type="button"
                                onClick={() => { setDealContactPanelEditing(false); setDealKeyInfoPopover(null) }}
                                title="Exit edit mode"
                                style={{
                                  marginLeft: 'auto',
                                  background: '#fef3c7',
                                  border: '1px solid #fcd34d',
                                  color: '#92400e',
                                  fontSize: '0.62rem',
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.3px',
                                  padding: '2px 8px',
                                  borderRadius: 999,
                                  cursor: 'pointer',
                                  lineHeight: 1.3,
                                }}
                              >Done</button>
                            )}
                          </div>
                          <div style={{
                            background: dealContactPanelEditing ? '#dbeafe' : '#eff6ff',
                            border: dealContactPanelEditing ? '1px solid #3b82f6' : '1px solid #bfdbfe',
                            borderRadius: 6,
                            padding: '4px 10px',
                            boxShadow: dealContactPanelEditing ? 'inset 0 0 0 1px rgba(59, 130, 246, 0.15)' : 'none',
                            transition: 'background 120ms ease, border-color 120ms ease',
                          }}>

                        {/* Row 1: Contact */}
                        <div className="crp-field">
                          <label>Contact</label>
                          <span
                            ref={el => { dealKeyInfoAnchorRefs.current.contact = el }}
                            onClick={() => {
                              if (!dealContactPanelEditing) return
                              setDealKeyInfoPopover(dealKeyInfoPopover === 'contact' ? null : 'contact')
                            }}
                            title={dealContactPanelEditing ? (associated.length === 0 ? 'Add or search for a contact' : 'Change primary contact') : undefined}
                            onMouseEnter={onValueHoverEnter}
                            onMouseLeave={onValueHoverLeave}
                            style={{ ...valueClickable, color: associated.length === 0 ? '#94a3b8' : '#33475b', cursor: dealContactPanelEditing ? 'pointer' : 'default' }}
                          >
                            {selectedAssoc?.preferred_name || dealLinkedClient?.preferred_name || '—'}
                          </span>
                          {(selectedAssoc?.archived_at || dealLinkedClient?.archived_at) && (
                            <span style={{ display: 'inline-flex', padding: '1px 5px', borderRadius: 8, fontSize: '0.58rem', fontWeight: 600, background: '#fef3c7', color: '#92400e', marginLeft: 4 }}>Archived</span>
                          )}
                          {dealKeyInfoPopover === 'contact' && (
                            <div ref={dealKeyInfoPopoverRef} data-deal-keyinfo-popover style={popoverStyle} onClick={e => e.stopPropagation()}>
                              {/* (a) Already-associated contacts */}
                              {associated.length === 0 ? (
                                <div style={{ fontSize: '0.72rem', color: '#94a3b8', padding: '4px 8px 6px' }}>No contacts linked yet</div>
                              ) : (
                                associated.map((dc: any) => {
                                  const isCurrent = dc.unified_client_id === selectedContactId
                                  return (
                                    <div
                                      key={dc.link_id}
                                      onClick={() => pickContact(dc.unified_client_id)}
                                      style={{
                                        padding: '6px 8px', cursor: 'pointer', borderRadius: 4,
                                        fontSize: '0.78rem', color: '#33475b',
                                        background: isCurrent ? '#ecfdf5' : 'transparent',
                                        display: 'flex', alignItems: 'center', gap: 6,
                                      }}
                                      onMouseEnter={e => { if (!isCurrent) (e.currentTarget as HTMLDivElement).style.background = '#f1f5f9' }}
                                      onMouseLeave={e => { if (!isCurrent) (e.currentTarget as HTMLDivElement).style.background = 'transparent' }}
                                    >
                                      <span style={{ flex: 1 }}>{dc.preferred_name || dc.unified_client_id}</span>
                                      {isCurrent ? <span style={{ fontSize: '0.6rem', fontWeight: 700, color: '#047857' }}>✓</span> : null}
                                    </div>
                                  )
                                })
                              )}

                              {/* (b) Search all contacts */}
                              <div style={{ borderTop: '1px solid #e2e8f0', marginTop: 4, paddingTop: 6 }}>
                                <input
                                  type="text"
                                  placeholder="Search all contacts…"
                                  value={dealKeyInfoContactSearch}
                                  onChange={e => setDealKeyInfoContactSearch(e.target.value)}
                                  style={{ width: '100%', fontSize: '0.78rem', padding: '5px 8px', border: '1px solid #cbd6e2', borderRadius: 4, outline: 'none' }}
                                />
                                {dealKeyInfoContactSearch.trim() && (
                                  <div style={{ marginTop: 4 }}>
                                    {dealKeyInfoContactSearching ? (
                                      <div style={{ fontSize: '0.72rem', color: '#94a3b8', padding: '4px 8px' }}>Searching…</div>
                                    ) : dealKeyInfoContactSearchResults.length === 0 ? (
                                      <div style={{ fontSize: '0.72rem', color: '#94a3b8', padding: '4px 8px' }}>No matches</div>
                                    ) : (
                                      dealKeyInfoContactSearchResults.map((c: any) => (
                                        <div
                                          key={c.unified_client_id}
                                          onClick={() => pickFromSearch(c.unified_client_id)}
                                          style={{
                                            padding: '5px 8px', cursor: 'pointer', borderRadius: 4,
                                            fontSize: '0.76rem', color: '#33475b',
                                            display: 'flex', flexDirection: 'column', gap: 1,
                                          }}
                                          onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = '#f1f5f9' }}
                                          onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = 'transparent' }}
                                        >
                                          <span style={{ fontWeight: 500 }}>{c.preferred_name || c.unified_client_id}</span>
                                          {(c.preferred_email || c.preferred_phone) && (
                                            <span style={{ fontSize: '0.68rem', color: '#7c98b6', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                              {c.preferred_email || c.preferred_phone}
                                            </span>
                                          )}
                                        </div>
                                      ))
                                    )}
                                  </div>
                                )}
                              </div>

                              {/* (c) Add new contact inline */}
                              <div style={{ borderTop: '1px solid #e2e8f0', marginTop: 6, paddingTop: 4 }}>
                                {dealKeyInfoAddContactOpen ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '4px 2px' }}>
                                    <input
                                      autoFocus
                                      type="text"
                                      placeholder="Name (required)"
                                      value={dealKeyInfoAddContactForm.name}
                                      onChange={e => setDealKeyInfoAddContactForm(f => ({ ...f, name: e.target.value }))}
                                      style={{ width: '100%', fontSize: '0.78rem', padding: '5px 8px', border: '1px solid #cbd6e2', borderRadius: 4, outline: 'none' }}
                                    />
                                    <input
                                      type="email"
                                      placeholder="Email (optional)"
                                      value={dealKeyInfoAddContactForm.email}
                                      onChange={e => setDealKeyInfoAddContactForm(f => ({ ...f, email: e.target.value }))}
                                      style={{ width: '100%', fontSize: '0.78rem', padding: '5px 8px', border: '1px solid #cbd6e2', borderRadius: 4, outline: 'none' }}
                                    />
                                    {dealKeyInfoAddContactEmailMatch && renderDedupHint({
                                      match: dealKeyInfoAddContactEmailMatch,
                                      field: 'email',
                                      value: dealKeyInfoAddContactForm.email.trim(),
                                      source: 'new-contact-email',
                                      isSelf: false,
                                    })}
                                    <input
                                      type="tel"
                                      placeholder="Phone (optional)"
                                      value={dealKeyInfoAddContactForm.phone}
                                      onChange={e => setDealKeyInfoAddContactForm(f => ({ ...f, phone: formatPhoneField(e.target.value) }))}
                                      style={{ width: '100%', fontSize: '0.78rem', padding: '5px 8px', border: '1px solid #cbd6e2', borderRadius: 4, outline: 'none' }}
                                    />
                                    {dealKeyInfoAddContactPhoneMatch && renderDedupHint({
                                      match: dealKeyInfoAddContactPhoneMatch,
                                      field: 'phone',
                                      value: dealKeyInfoAddContactForm.phone,
                                      source: 'new-contact-phone',
                                      isSelf: false,
                                    })}
                                    <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                                      <button
                                        type="button"
                                        onClick={() => { setDealKeyInfoAddContactOpen(false); setDealKeyInfoAddContactForm({ name: '', email: '', phone: '' }) }}
                                        disabled={dealKeyInfoAddContactSaving}
                                        style={{ background: 'none', border: '1px solid #cbd6e2', padding: '4px 10px', fontSize: '0.72rem', color: '#516f90', cursor: 'pointer', borderRadius: 4 }}
                                      >Cancel</button>
                                      <button
                                        type="button"
                                        onClick={submitNewContact}
                                        disabled={!dealKeyInfoAddContactForm.name.trim() || dealKeyInfoAddContactSaving}
                                        style={{ background: '#0091ae', border: '1px solid #0091ae', padding: '4px 10px', fontSize: '0.72rem', color: '#fff', cursor: dealKeyInfoAddContactForm.name.trim() ? 'pointer' : 'not-allowed', borderRadius: 4, opacity: dealKeyInfoAddContactForm.name.trim() ? 1 : 0.5 }}
                                      >{dealKeyInfoAddContactSaving ? 'Saving…' : 'Save'}</button>
                                    </div>
                                  </div>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => setDealKeyInfoAddContactOpen(true)}
                                    style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '6px 8px', fontSize: '0.78rem', color: '#0369a1', cursor: 'pointer', borderRadius: 4 }}
                                  >+ Add new contact</button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Row 2: Primary Email */}
                        <div className="crp-field">
                          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <span>Primary Email</span>
                            {primaryMissing ? <span title="No primary email" style={{ width: 7, height: 7, borderRadius: '50%', background: '#dc2626', display: 'inline-block' }} /> : null}
                          </label>
                          <span
                            ref={el => { dealKeyInfoAnchorRefs.current.primary = el }}
                            onClick={() => {
                              if (!dealContactPanelEditing || !selectedContactId) return
                              setDealKeyInfoAddEmailOpen(false)
                              setDealKeyInfoAddEmail('')
                              setDealKeyInfoPopover(dealKeyInfoPopover === 'primary' ? null : 'primary')
                            }}
                            title={dealContactPanelEditing ? (selectedContactId ? 'Pick primary email' : 'Pick a contact first') : undefined}
                            onMouseEnter={onValueHoverEnter}
                            onMouseLeave={onValueHoverLeave}
                            style={{ ...valueClickable, color: effectivePrimary ? '#33475b' : '#94a3b8', cursor: dealContactPanelEditing && selectedContactId ? 'pointer' : 'default', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}
                          >
                            {effectivePrimary?.address || '—'}
                          </span>
                          {dealKeyInfoPopover === 'primary' && renderEmailPopover('primary')}
                        </div>

                        {/* Row 3: Billing Email */}
                        <div className="crp-field">
                          <label>Billing Email</label>
                          <span
                            ref={el => { dealKeyInfoAnchorRefs.current.billing = el }}
                            onClick={() => {
                              if (!dealContactPanelEditing || !selectedContactId) return
                              setDealKeyInfoAddEmailOpen(false)
                              setDealKeyInfoAddEmail('')
                              setDealKeyInfoPopover(dealKeyInfoPopover === 'billing' ? null : 'billing')
                            }}
                            title={dealContactPanelEditing ? (selectedContactId ? 'Pick billing email' : 'Pick a contact first') : undefined}
                            onMouseEnter={onValueHoverEnter}
                            onMouseLeave={onValueHoverLeave}
                            style={{ ...valueClickable, color: effectiveBilling ? '#33475b' : '#94a3b8', cursor: dealContactPanelEditing && selectedContactId ? 'pointer' : 'default', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}
                          >
                            {effectiveBilling?.address || '—'}
                          </span>
                          {dealKeyInfoPopover === 'billing' && renderEmailPopover('billing')}
                        </div>

                        {/* Row 4: Phone */}
                        <div className="crp-field">
                          <label>Phone</label>
                          {(() => {
                            // selectedAssoc from deal_contacts doesn't include
                            // preferred_phone — fall back to dealLinkedClient
                            // when it matches the selected contact, then to the
                            // first primary row in the contactPhones list.
                            const phoneFromAssoc = selectedAssoc?.preferred_phone
                            const phoneFromLinked = dealLinkedClient?.unified_client_id === selectedContactId ? dealLinkedClient?.preferred_phone : null
                            const primaryPhoneRow = (contactPhones || []).find((p: any) => Number(p.is_primary) === 1)
                            const phoneFromList = primaryPhoneRow?.number || null
                            const phone = phoneFromAssoc || phoneFromLinked || phoneFromList || ''
                            return (
                              <span
                                ref={el => { dealKeyInfoAnchorRefs.current.phone = el }}
                                onClick={() => {
                                  if (!dealContactPanelEditing || !selectedContactId) return
                                  setDealKeyInfoPhoneDraft(phone)
                                  setDealKeyInfoPopover(dealKeyInfoPopover === 'phone' ? null : 'phone')
                                }}
                                title={dealContactPanelEditing ? (selectedContactId ? 'Edit phone' : 'Pick a contact first') : undefined}
                                onMouseEnter={onValueHoverEnter}
                                onMouseLeave={onValueHoverLeave}
                                style={{ ...valueClickable, color: phone ? '#33475b' : '#94a3b8', cursor: dealContactPanelEditing && selectedContactId ? 'pointer' : 'default' }}
                              >
                                {phone ? formatPhoneField(phone) : '—'}
                              </span>
                            )
                          })()}
                          {dealKeyInfoPopover === 'phone' && (
                            <div ref={dealKeyInfoPopoverRef} data-deal-keyinfo-popover style={popoverStyle} onClick={e => e.stopPropagation()}>
                              <input
                                autoFocus
                                type="tel"
                                placeholder="555-555-5555"
                                value={dealKeyInfoPhoneDraft}
                                onChange={e => setDealKeyInfoPhoneDraft(formatPhoneField(e.target.value))}
                                onBlur={() => {
                                  // Don't save-on-blur if the user is clicking the
                                  // dedup hint "switch" link — the onMouseDown below
                                  // handles that path. We just let savePhone run;
                                  // the dedup hint action runs before blur triggers
                                  // the save if the user clicks it via mousedown.
                                  savePhone(dealKeyInfoPhoneDraft)
                                }}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') { e.preventDefault(); savePhone(dealKeyInfoPhoneDraft) }
                                  else if (e.key === 'Escape') { e.preventDefault(); setDealKeyInfoPopover(null) }
                                }}
                                style={{ width: '100%', fontSize: '0.82rem', padding: '6px 8px', border: '1px solid #cbd6e2', borderRadius: 4, outline: 'none' }}
                              />
                              {dealKeyInfoPhoneMatch && (() => {
                                const isSelf = dealKeyInfoPhoneMatch.id === ((selectedDeal as any)?.primaryContactId || null)
                                return renderDedupHint({
                                  match: dealKeyInfoPhoneMatch,
                                  field: 'phone',
                                  value: dealKeyInfoPhoneDraft,
                                  source: 'phone',
                                  isSelf,
                                })
                              })()}
                            </div>
                          )}
                        </div>
                        {/* /Deal Contact sub-panel (opens above Row 1) */}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Dedup confirm modal — centered overlay. Shown when the
                        user clicks "Review" on any of the 4 dedup hint cards.
                        Three choices: add to current (not shown for the
                        new-contact flows which have no current), switch deal
                        primary to the matched contact, or cancel. Close on
                        outside click is allowed. */}
                    {dedupConfirm && (() => {
                      const { match, field, source, currentContactId } = dedupConfirm
                      const fieldLabel = field === 'email' ? 'email' : 'phone number'
                      const matchedValue = field === 'email' ? (match.email || '') : (match.phone || '')
                      const isNewContactFlow = source === 'new-contact-email' || source === 'new-contact-phone'
                      // Modal is locked — clicking the overlay does NOT dismiss.
                      // User MUST pick an explicit option (Add / Switch / Cancel)
                      // so the dedup decision is intentional, not accidental.
                      const OptionRow = ({ icon, iconBg, iconColor, title, sub, primary, disabled, onClick }: { icon: string; iconBg: string; iconColor: string; title: React.ReactNode; sub?: React.ReactNode; primary?: boolean; disabled?: boolean; onClick: () => void }) => (
                        <button
                          type="button"
                          disabled={disabled}
                          onClick={onClick}
                          onMouseEnter={e => { if (!disabled) (e.currentTarget as HTMLButtonElement).style.borderColor = primary ? '#0e7490' : '#94a3b8' }}
                          onMouseLeave={e => { if (!disabled) (e.currentTarget as HTMLButtonElement).style.borderColor = primary ? '#0091ae' : '#cbd6e2' }}
                          style={{
                            display: 'flex', alignItems: 'flex-start', gap: 10, width: '100%',
                            padding: '12px 14px', textAlign: 'left',
                            border: `1px solid ${primary ? '#0091ae' : '#cbd6e2'}`,
                            borderRadius: 8, background: primary ? '#ecfeff' : '#fff',
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            opacity: disabled ? 0.55 : 1, transition: 'border-color 100ms',
                          }}
                        >
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 28, height: 28, flexShrink: 0, borderRadius: 6,
                            background: iconBg, color: iconColor, fontSize: 16, fontWeight: 700,
                          }}>{icon}</span>
                          <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
                            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#33475b' }}>{title}</span>
                            {sub && <span style={{ fontSize: '0.72rem', color: '#7c98b6', wordBreak: 'break-word' }}>{sub}</span>}
                          </span>
                        </button>
                      )
                      return (
                        <div className="kanban-modal-overlay">
                          <div className="kanban-confirm-modal" style={{ maxWidth: 460, textAlign: 'left' }} onClick={e => e.stopPropagation()}>
                            <div className="kanban-confirm-icon" style={{ background: '#fffbeb', margin: '0 auto 14px' }}>
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="8" x2="12" y2="12" />
                                <line x1="12" y1="16" x2="12.01" y2="16" />
                              </svg>
                            </div>
                            <h3 style={{ textAlign: 'center', margin: '0 0 6px' }}>Use existing contact?</h3>
                            <p style={{ textAlign: 'center', margin: '0 0 18px', fontSize: '0.85rem', color: '#516f90' }}>
                              <strong>{match.name}</strong> already has this {fieldLabel}{matchedValue ? <>: <code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: 3, fontSize: '0.92em' }}>{matchedValue}</code></> : null}.
                            </p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                              {isNewContactFlow ? (
                                <OptionRow
                                  icon="↳" iconBg="#cffafe" iconColor="#0e7490" primary
                                  disabled={dedupConfirmSaving}
                                  onClick={() => handleDedupConfirm('use-existing')}
                                  title={<>Use <strong>{match.name}</strong> instead</>}
                                  sub={<>Don't create a new contact — link this deal to {match.name}{matchedValue ? ` (${matchedValue})` : ''}.</>}
                                />
                              ) : (
                                <>
                                  <OptionRow
                                    icon="+" iconBg="#cffafe" iconColor="#0e7490" primary
                                    disabled={dedupConfirmSaving || !currentContactId}
                                    onClick={() => handleDedupConfirm('add-to-current')}
                                    title={<>Add this {fieldLabel} to the current contact</>}
                                    sub={<>Keep the deal on its current contact and append <code style={{ background: '#f1f5f9', padding: '1px 4px', borderRadius: 3 }}>{matchedValue || `this ${fieldLabel}`}</code> as another row.</>}
                                  />
                                  <OptionRow
                                    icon="↔" iconBg="#fef3c7" iconColor="#b45309"
                                    disabled={dedupConfirmSaving}
                                    onClick={() => handleDedupConfirm('switch')}
                                    title={<>Switch deal contact to <strong>{match.name}</strong></>}
                                    sub={<>Replace the current deal contact with {match.name}{matchedValue ? ` (${fieldLabel}: ${matchedValue})` : ''}.</>}
                                  />
                                </>
                              )}
                              <button
                                type="button"
                                disabled={dedupConfirmSaving}
                                onClick={() => setDedupConfirm(null)}
                                style={{
                                  padding: '8px 14px', marginTop: 4, fontSize: '0.82rem', fontWeight: 500,
                                  border: 'none', borderRadius: 8,
                                  background: 'transparent', color: '#64748b',
                                  cursor: dedupConfirmSaving ? 'not-allowed' : 'pointer',
                                  textAlign: 'center',
                                }}
                              >Cancel</button>
                            </div>
                          </div>
                        </div>
                      )
                    })()}
                    </>
                  )
                })()}

                {/* ===== Entity 360 (P4) — ADDITIVE relationship graph + rollups =====
                    Appends crp-cards at the bottom of the deal record's left scroll.
                    Self-contained leaf: owns its own fetch/loading/error state,
                    re-keyed on the deal id. On error/empty it renders NOTHING, so the
                    existing deal record (highlight, key-info, projects, etc.) is never
                    blocked. Gated behind FLAG_ENTITY360. Same fail-safe contract as the
                    org/contact record. Purely additive — removing reverts cleanly. */}
                {FLAG_ENTITY360 && selectedDeal?.id != null && (
                  <Suspense fallback={<div className="view-loading" />}>
                    <Entity360 entityType="deal" id={String(selectedDeal.id)} />
                  </Suspense>
                )}
              </div>
            </div>

            {/* ---- MIDDLE PANE ---- */}
            <div className="crp-middle">
              <div className="hs-topbar" style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
                <div className="hs-topbar-left">
                  <div className="hs-view-tabs">
                    <div className={`hs-view-tab${dealDetailTab === 'about' ? ' active' : ''}`} onClick={() => setDealDetailTab('about')}>
                      <span className="hs-tab-label">Overview</span>
                    </div>
                    <div className={`hs-view-tab${dealDetailTab === 'activities' ? ' active' : ''}`} onClick={() => setDealDetailTab('activities')}>
                      <span className="hs-tab-label">Activities</span>
                    </div>
                    <div className={`hs-view-tab${dealDetailTab === 'financials' ? ' active' : ''}`} onClick={() => setDealDetailTab('financials')}>
                      <span className="hs-tab-label">Revenue</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="crp-middle-scroll">
                 {dealDetailTab === 'about' && (<Suspense fallback={PrefsLazyFallback}><DealDetailAbout ctx={{ AddressInput, AvailabilityDateTimePicker, MapsLink, allPipelineStages, createNote, crmUsers, crpCommentOpenId, crpCommentVal, crpThreadExpanded, currencyFormatter, currentDealStageColor, dealActFetchBusy, dealActFetchLimit, dealActIcon, dealActivities, dealActivitiesLoading, dealAddrExpandedId, dealAddrImportOpen, dealAddrImportSelected, dealAddresses, dealAllocatedTotal, dealContacts, dealEditingField, dealEditingValue, dealHistory, dealLinkedClient, dealNewAddrForm, dealNewAddrFormOpen, dealNotesEditing, dealNotesFileInputRef, dealNotesFiles, dealNotesUploading, dealNotesValue, dealPendingChanges, dealSavingField, dealTimelineLimit, fetchDealHistory, fmtDate, getDealActType, getDealActivitiesMerged, openContractSignedModal, openHrProfileDrawer, resolveApptDateSource, selectedDeal, setCalendarRefreshTick, setContractSignedEmail, setCrpCommentOpenId, setCrpCommentVal, setCrpThreadExpanded, setDealActFetchBusy, setDealActFetchLimit, setDealActivities, setDealAddrExpandedId, setDealAddrImportOpen, setDealAddrImportSelected, setDealAddresses, setDealCreateProjectForm, setDealDetailNoShowModal, setDealDetailNoShowNewAppt, setDealDetailNoShowReason, setDealEditingField, setDealEditingValue, setDealHistory, setDealNewAddrForm, setDealNewAddrFormOpen, setDealNotesEditing, setDealNotesFiles, setDealNotesUploading, setDealNotesValue, setDealPendingChanges, setDealSavingField, setDealTimelineLimit, setLiveDeals, setPipelineRefreshTick, setSelectedDeal, setSubConfirmDialog, updateDeal }} /></Suspense>)}
                    {dealDetailTab === 'activities' && (<Suspense fallback={PrefsLazyFallback}><DealDetailActivities ctx={{ dealActivities, getDealActType, crpActivityFilter, crpFilterChecks, crpSelectedUser, CRP_ACTIVITY_FILTER_OPTIONS, groupActivitiesWithEmailThreads, isEmailThread, selectedDeal, crpActivitySearch, setCrpActivitySearch, setCrpActivityFilter, crpFilterDropdownOpen, setCrpFilterDropdownOpen, CALENDAR_USERS_MAP, crpUsersDropdownOpen, setCrpUsersDropdownOpen, setCrpSelectedUser, setCrpFilterChecks, contactNoteOpen, contactTaskOpen, contactMeetingOpen, setContactTaskOpen, setContactMeetingOpen, setShowNoteModal, setContactNoteOpen, crpEditingDescId, crpEditingDescVal, setCrpEditingDescVal, crpActivitySavingId, saveCrpActivityPatch, setCrpEditingDescId, crpCommentOpenId, setCrpCommentOpenId, crpThreadExpanded, setCrpThreadExpanded, crpCommentVal, setCrpCommentVal, createNote, getDealActivitiesMerged, setDealActivities, dealActivitiesLoading, fmtDate, linkifyText, SENTIMENT_COLORS, SentimentIcon, contactTaskForm, setContactTaskForm, contactTaskError, setContactTaskError, contactTaskSuccess, setContactTaskSuccess, contactTaskSaving, setContactTaskSaving, contactMeetingForm, setContactMeetingForm, contactMeetingSaving, setContactMeetingSaving, meetingFormEntityRef, currentUserId, crpExpandedCards, setCrpExpandedCards, crpEditingActivityId, setCrpEditingActivityId, crpEditingActivityVal, setCrpEditingActivityVal, taskCalDates, setTaskCalDates, taskCalOpenId, setTaskCalOpenId, taskCalMonth, setTaskCalMonth, taskCalYear, setTaskCalYear, taskCalRef, calendarMonth, setLiveMeetings, formatDatePref, formatCallTitle, emailSenderFromActivity, emailSnippetFromActivity, AvailabilityDateTimePicker, LinkedEmailsSection, DotLottieReact, CALENDAR_USERS_RAW, createTask, createCalendarEvent, fetchCalendarEvents }} /></Suspense>)}
                {dealDetailTab === 'financials' && selectedDeal?.id != null && (
                  <FinancialsCards source={{ kind: 'deal', dealId: Number(selectedDeal.id) }} />
                )}
              </div>
            </div>

            {/* ---- RIGHT SIDEBAR ---- */}
            <div className="crp-right">
              <div className="crp-right-scroll">
                {/* Deal Won Automation Card — surfaces the most recent deal_automation_runs
                    row for this deal. Shows action links when a run succeeded (partial too),
                    step-by-step status + re-run when it failed, and an "Automation didn't run"
                    prompt for admins when the deal's in a won stage but no run exists yet. */}
                <DealAutomationCard deal={selectedDeal} />
                {/* Contacts — consolidated view with single Edit All action */}
                {(() => {
                  const clientId = selectedDeal.unifiedClientId || (selectedDeal as any).primaryContactId || null
                  const navToContact = () => {
                    if (!clientId) return
                    fetchClient(clientId).then(c => { if (applyClientOrRedirect(c)) return; setContactDetailTab('about'); window.location.hash = `/contacts/record/${encodeURIComponent(clientId)}` }).catch(() => {})
                  }
                  const openEditAll = () => {
                    setDealContactEditAllForm({
                      name: dealLinkedClient?.preferred_name || '',
                      phone: dealLinkedClient?.preferred_phone || '',
                      email: dealLinkedClient?.preferred_email || '',
                      street: dealLinkedClient?.preferred_street || '',
                      city: dealLinkedClient?.preferred_city || '',
                      state: dealLinkedClient?.preferred_state || '',
                      zip: dealLinkedClient?.preferred_zip || '',
                    })
                    setDealContactEditAll(true)
                  }
                  const closeEditAll = () => setDealContactEditAll(false)
                  const saveAll = async () => {
                    if (!clientId) { closeEditAll(); return }
                    setDealContactSaving(true)
                    try {
                      // Only send fields that actually changed to keep the audit trail clean.
                      const f = dealContactEditAllForm
                      const payload: Record<string, string | null> = {}
                      if ((f.name.trim() || null) !== (dealLinkedClient?.preferred_name || null)) payload.preferredName = f.name.trim() || null
                      if ((f.phone.trim() || null) !== (dealLinkedClient?.preferred_phone || null)) payload.preferredPhone = f.phone.trim() || null
                      if ((f.email.trim() || null) !== (dealLinkedClient?.preferred_email || null)) payload.preferredEmail = f.email.trim() || null
                      if ((f.street.trim() || null) !== (dealLinkedClient?.preferred_street || null)) payload.preferredStreet = f.street.trim() || null
                      if ((f.city.trim() || null) !== (dealLinkedClient?.preferred_city || null)) payload.preferredCity = f.city.trim() || null
                      if ((f.state.trim() || null) !== (dealLinkedClient?.preferred_state || null)) payload.preferredState = f.state.trim() || null
                      if ((f.zip.trim() || null) !== (dealLinkedClient?.preferred_zip || null)) payload.preferredZip = f.zip.trim() || null
                      if (Object.keys(payload).length === 0) { closeEditAll(); return }
                      const updated = await updateClient(clientId, payload)
                      setDealLinkedClient(updated)
                      closeEditAll()
                    } catch (e: any) {
                      alert('Failed to save: ' + (e?.message || String(e)))
                    } finally {
                      setDealContactSaving(false)
                    }
                  }
                  // Compose address for display
                  const street = dealLinkedClient?.preferred_street || ''
                  const cityStateZip = [dealLinkedClient?.preferred_city, dealLinkedClient?.preferred_state].filter(Boolean).join(', ')
                  const zipPart = dealLinkedClient?.preferred_zip ? ' ' + dealLinkedClient.preferred_zip : ''
                  const addressLine = [street, cityStateZip + zipPart].filter(s => s.trim()).join(', ')
                  const hasPhone = !!dealLinkedClient?.preferred_phone
                  const hasEmail = !!dealLinkedClient?.preferred_email
                  // Legacy count fallback only counts the deal's unifiedClient/contactId
                  // as an implicit contact when that client is actually a person. If it's
                  // an organization (or there's already an explicit org row in
                  // deal_contacts for the same id), the Companies card owns it — so the
                  // Contacts count should drop to 0 instead of falsely showing "1".
                  const legacyClientIsOrg = (dealLinkedClient as any)?.client_type === 'organization'
                    || dealContacts.some((dc: any) => dc.unified_client_id === clientId && (dc.role === 'company' || dc.client_type === 'organization'))
                  const legacyContactImplied = (clientId || selectedDeal.contactId) && !legacyClientIsOrg
                  const contactCount = dealContacts.filter((dc: any) => !(dc.role === 'company' || dc.client_type === 'organization')).length
                    || (legacyContactImplied ? 1 : 0)
                  return (
                    <div className="crp-card">
                      <div className="crp-card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <h3>Contacts ({contactCount})</h3>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {clientId && !dealContactEditAll && (
                            <button
                              type="button"
                              aria-label="Edit contact"
                              onClick={openEditAll}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#64748b', display: 'inline-flex', alignItems: 'center' }}
                            >
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                              </svg>
                            </button>
                          )}
                          <button
                            type="button"
                            aria-label="Add contact"
                            onClick={() => { setDealContactSearch(''); setDealContactResults([]); setDealContactPickerOpen(true) }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.78rem', color: '#0091ae', fontWeight: 500, padding: '2px 6px' }}
                          >
                            +
                          </button>
                        </div>
                      </div>
                      <div className="crp-card-body">
                        {/* Show associated contacts. Anything with role='company' OR
                            whose underlying client is client_type='organization' belongs
                            in the Companies card, not here — covers legacy 'associated'
                            links to org records. */}
                        {dealContacts.filter((dc: any) => !(dc.role === 'company' || dc.client_type === 'organization')).length > 0 && dealContacts.filter((dc: any) => !(dc.role === 'company' || dc.client_type === 'organization')).map((dc: any) => (
                          <div key={dc.link_id} className="crp-assoc-item" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                            <div style={{ cursor: 'pointer', flex: 1, minWidth: 0 }} onClick={() => { if (dc.unified_client_id) { window.location.hash = `/contacts/record/${encodeURIComponent(dc.unified_client_id)}` } }}>
                              <div className="crp-assoc-title" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
                                {dc.preferred_name || dc.unified_client_id}
                                {dc.archived_at && <span style={{ display: 'inline-flex', padding: '1px 5px', borderRadius: 8, fontSize: '0.58rem', fontWeight: 600, background: '#fef3c7', color: '#92400e', flexShrink: 0 }}>Archived</span>}
                              </div>
                              <div className="crp-assoc-sub" style={{ fontSize: '0.72rem', display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {dc.preferred_phone && <span>{dc.preferred_phone}</span>}
                                {dc.preferred_email && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{dc.preferred_email}</span>}
                              </div>
                              {dc.preferred_street && <div style={{ fontSize: '0.7rem', color: '#7c98b6', marginTop: 2 }}>{[dc.preferred_street, dc.preferred_city, dc.preferred_state].filter(Boolean).join(', ')}</div>}
                            </div>
                            <button
                              title="Remove contact from deal"
                              onClick={(e) => {
                                e.stopPropagation()
                                const displayName = dc.preferred_name || 'this contact'
                                setSubConfirmDialog({
                                  title: 'Remove contact?',
                                  message: `Unlink ${displayName} from this deal? The contact record itself is not deleted.`,
                                  destructive: true,
                                  onConfirm: async () => {
                                    try {
                                      await fetch(`/api/crm/deals/${selectedDeal.id}/contacts/${dc.link_id}`, { method: 'DELETE' })
                                      setDealContacts(prev => prev.filter(x => x.link_id !== dc.link_id))
                                    } catch { /* noop */ }
                                  },
                                })
                              }}
                              style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '1rem', padding: 4, flexShrink: 0 }}
                            >&times;</button>
                          </div>
                        ))}
                        {dealContacts.filter((dc: any) => !(dc.role === 'company' || dc.client_type === 'organization')).length === 0 && !clientId && !selectedDeal.contactId && (
                          <div className="crp-empty-card" style={{ color: '#94a3b8', fontSize: '0.78rem' }}>Click + to link a contact</div>
                        )}
                        {/* Legacy fallback: if junction table is empty but unified_client_id exists */}
                        {dealContacts.length === 0 && clientId ? (
                          dealContactEditAll ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                              <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: '0.7rem', color: '#7c98b6', fontWeight: 500 }}>
                                Name
                                <input
                                  autoFocus
                                  disabled={dealContactSaving}
                                  value={dealContactEditAllForm.name}
                                  onChange={e => setDealContactEditAllForm(f => ({ ...f, name: e.target.value }))}
                                  style={{ padding: '6px 8px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.82rem', fontFamily: 'inherit' }}
                                />
                              </label>
                              <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: '0.7rem', color: '#7c98b6', fontWeight: 500 }}>
                                Phone
                                <input
                                  disabled={dealContactSaving}
                                  placeholder="(555) 555-5555"
                                  value={dealContactEditAllForm.phone}
                                  onChange={e => setDealContactEditAllForm(f => ({ ...f, phone: formatPhoneDisplay(e.target.value) }))}
                                  style={{ padding: '6px 8px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.82rem', fontFamily: 'inherit' }}
                                />
                              </label>
                              <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: '0.7rem', color: '#7c98b6', fontWeight: 500 }}>
                                Email
                                <input
                                  type="email"
                                  disabled={dealContactSaving}
                                  placeholder="name@example.com"
                                  value={dealContactEditAllForm.email}
                                  onChange={e => setDealContactEditAllForm(f => ({ ...f, email: e.target.value }))}
                                  style={{ padding: '6px 8px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.82rem', fontFamily: 'inherit' }}
                                />
                              </label>
                              <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: '0.7rem', color: '#7c98b6', fontWeight: 500 }}>
                                Street
                                <input
                                  disabled={dealContactSaving}
                                  value={dealContactEditAllForm.street}
                                  onChange={e => setDealContactEditAllForm(f => ({ ...f, street: e.target.value }))}
                                  style={{ padding: '6px 8px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.82rem', fontFamily: 'inherit' }}
                                />
                              </label>
                              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 6 }}>
                                <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: '0.7rem', color: '#7c98b6', fontWeight: 500, minWidth: 0 }}>
                                  City
                                  <input
                                    disabled={dealContactSaving}
                                    value={dealContactEditAllForm.city}
                                    onChange={e => setDealContactEditAllForm(f => ({ ...f, city: e.target.value }))}
                                    style={{ padding: '6px 8px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.82rem', fontFamily: 'inherit', width: '100%', minWidth: 0, boxSizing: 'border-box' }}
                                  />
                                </label>
                                <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: '0.7rem', color: '#7c98b6', fontWeight: 500, minWidth: 0 }}>
                                  State
                                  <input
                                    disabled={dealContactSaving}
                                    placeholder="ST"
                                    value={dealContactEditAllForm.state}
                                    onChange={e => setDealContactEditAllForm(f => ({ ...f, state: e.target.value.toUpperCase().slice(0, 2) }))}
                                    style={{ padding: '6px 8px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.82rem', fontFamily: 'inherit', width: '100%', minWidth: 0, boxSizing: 'border-box' }}
                                  />
                                </label>
                                <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: '0.7rem', color: '#7c98b6', fontWeight: 500, minWidth: 0 }}>
                                  ZIP
                                  <input
                                    disabled={dealContactSaving}
                                    value={dealContactEditAllForm.zip}
                                    onChange={e => setDealContactEditAllForm(f => ({ ...f, zip: e.target.value }))}
                                    style={{ padding: '6px 8px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.82rem', fontFamily: 'inherit', width: '100%', minWidth: 0, boxSizing: 'border-box' }}
                                  />
                                </label>
                              </div>
                              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 2 }}>
                                <button
                                  type="button"
                                  disabled={dealContactSaving}
                                  onClick={closeEditAll}
                                  style={{ padding: '5px 12px', fontSize: '0.78rem', border: '1px solid #cbd6e2', borderRadius: 4, background: '#fff', cursor: 'pointer' }}
                                >Cancel</button>
                                <button
                                  type="button"
                                  disabled={dealContactSaving}
                                  onClick={saveAll}
                                  style={{ padding: '5px 12px', fontSize: '0.78rem', border: 'none', borderRadius: 4, background: '#1976d2', color: '#fff', cursor: dealContactSaving ? 'not-allowed' : 'pointer', fontWeight: 600, opacity: dealContactSaving ? 0.7 : 1 }}
                                >{dealContactSaving ? 'Saving…' : 'Save'}</button>
                              </div>
                            </div>
                          ) : (
                            <div className="crp-assoc-item" style={{ cursor: 'default' }}>
                              {/* Name — clickable to navigate */}
                              <div
                                className="crp-assoc-title"
                                style={{ cursor: 'pointer' }}
                                onClick={navToContact}
                              >
                                {dealLinkedClient?.preferred_name || selectedDeal.title?.split(/\s*[-–]\s*/)[0]?.trim() || clientId}
                              </div>
                              {/* Phone · Email on one line with dot separator */}
                              {(hasPhone || hasEmail) && (
                                <div className="crp-assoc-sub" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.72rem', marginTop: 4 }}>
                                  {hasPhone && (
                                    <span style={{ flexShrink: 0 }}>{formatPhoneDisplay(dealLinkedClient!.preferred_phone!)}</span>
                                  )}
                                  {hasPhone && hasEmail && (
                                    <span style={{ color: '#cbd5e1', flexShrink: 0 }}>·</span>
                                  )}
                                  {hasEmail && (
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{dealLinkedClient!.preferred_email}</span>
                                  )}
                                </div>
                              )}
                              {/* Address if present */}
                              {addressLine && (
                                <div style={{ fontSize: '0.72rem', color: '#7c98b6', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {addressLine}
                                </div>
                              )}
                            </div>
                          )
                        ) : dealContacts.length === 0 && selectedDeal.contactId ? (
                          // Legacy fallback: deal has no unified_client_id but does have a legacy numeric contactId.
                          // Mirror the rich row layout used by the populated dealContacts path so legacy deals
                          // look identical to new ones (name + phone/email stack + address).
                          dealLinkedClient ? (
                            <div className="crp-assoc-item" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                              <div
                                style={{ cursor: 'pointer', flex: 1, minWidth: 0 }}
                                onClick={() => {
                                  const cid = dealLinkedClient.unified_client_id || ('BX-' + String(selectedDeal.contactId).padStart(5, '0'))
                                  window.location.hash = `/contacts/record/${encodeURIComponent(cid)}`
                                }}
                              >
                                <div className="crp-assoc-title" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {dealLinkedClient.preferred_name || (selectedDeal as any).contact_name || `Contact #${selectedDeal.contactId}`}
                                </div>
                                {(dealLinkedClient.preferred_phone || dealLinkedClient.preferred_email) && (
                                  <div className="crp-assoc-sub" style={{ fontSize: '0.72rem', display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {dealLinkedClient.preferred_phone && <span>{formatPhoneDisplay(dealLinkedClient.preferred_phone)}</span>}
                                    {dealLinkedClient.preferred_email && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{dealLinkedClient.preferred_email}</span>}
                                  </div>
                                )}
                                {dealLinkedClient.preferred_street && (
                                  <div style={{ fontSize: '0.7rem', color: '#7c98b6', marginTop: 2 }}>
                                    {[dealLinkedClient.preferred_street, dealLinkedClient.preferred_city, dealLinkedClient.preferred_state].filter(Boolean).join(', ')}
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : dealLinkedClientTried ? (
                            // Fetch completed without finding a matching clients row — show a bare
                            // name-only card derived from the deal title / contactId so the user
                            // isn't stuck staring at "Loading…" forever.
                            <div
                              className="crp-assoc-item"
                              style={{ cursor: 'pointer', padding: '6px 0' }}
                              onClick={() => {
                                const cid = selectedDeal.contactId != null ? String(selectedDeal.contactId) : ''
                                if (cid) window.location.hash = `/contacts/record/${encodeURIComponent(cid)}`
                              }}
                            >
                              <div className="crp-assoc-title" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {(selectedDeal as any).contact_name || selectedDeal.title?.split(/\s*[-–]\s*/)[0]?.trim() || `Contact #${selectedDeal.contactId}`}
                              </div>
                            </div>
                          ) : (
                            <div className="crp-assoc-item" style={{ cursor: 'default', color: '#94a3b8', fontSize: '0.78rem', padding: '6px 0' }}>
                              Loading…
                            </div>
                          )
                        ) : null}
                      </div>
                    </div>
                  )
                })()}

                {/* Companies */}
                {(() => {
                  const companies = dealContacts.filter((dc: any) => dc.role === 'company' || dc.client_type === 'organization')
                  return (
                    <div className="crp-card">
                      <div className="crp-card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <h3>Clients ({companies.length})</h3>
                        <button
                          type="button"
                          onClick={() => { setDealCompanySearch(''); setDealCompanyResults([]); setDealCompanyPickerOpen(true) }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.78rem', color: '#0091ae', fontWeight: 500, padding: '2px 6px' }}
                        >+</button>
                      </div>
                      <div className="crp-card-body">
                        {companies.length > 0 ? companies.map((dc: any) => (
                          <div key={dc.link_id} className="crp-assoc-item" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                            <div style={{ cursor: 'pointer', flex: 1, minWidth: 0 }} onClick={() => { if (dc.unified_client_id) window.location.hash = `/contacts/record/${encodeURIComponent(dc.unified_client_id)}` }}>
                              <div className="crp-assoc-title" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{dc.preferred_name || dc.unified_client_id}</div>
                              {dc.preferred_phone && <div className="crp-assoc-sub" style={{ fontSize: '0.72rem' }}>{dc.preferred_phone}</div>}
                            </div>
                            <button
                              title="Remove client from deal"
                              onClick={(e) => {
                                e.stopPropagation()
                                const displayName = dc.preferred_name || 'this client'
                                setSubConfirmDialog({
                                  title: 'Remove client?',
                                  message: `Unlink ${displayName} from this deal? The client record itself is not deleted.`,
                                  destructive: true,
                                  onConfirm: async () => {
                                    try {
                                      await fetch(`/api/crm/deals/${selectedDeal.id}/contacts/${dc.link_id}`, { method: 'DELETE' })
                                      setDealContacts(prev => prev.filter(x => x.link_id !== dc.link_id))
                                    } catch { /* noop */ }
                                  },
                                })
                              }}
                              style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '1rem', padding: 4, flexShrink: 0 }}
                            >&times;</button>
                          </div>
                        )) : (
                          <div className="crp-empty-card" style={{ color: '#94a3b8', fontSize: '0.78rem' }}>Click + to link a client</div>
                        )}
                      </div>
                    </div>
                  )
                })()}

                {/* Projects — one deal can have multiple projects */}
                <div className="crp-card">
                  <div className="crp-card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <h3>Projects ({dealLinkedProjects.length})</h3>
                    <button
                      onClick={() => {
                        const remaining = Math.max(0, Number(selectedDeal.opportunity || 0) - dealAllocatedTotal)
                        const primaryJobSite = dealAddresses.find((a: any) => a.is_primary_job_site) || dealAddresses.find((a: any) => a.is_job_site)
                        setDealCreateProjectForm({
                          name: selectedDeal.title || '',
                          contractor_price: remaining > 0 ? String(remaining) : String(selectedDeal.opportunity || ''),
                          work_order_price: '',
                          address: primaryJobSite?.formatted || dealLinkedClient?.preferred_street || '',
                          address_street: primaryJobSite?.street || dealLinkedClient?.preferred_street || '',
                          address_city: primaryJobSite?.city || dealLinkedClient?.preferred_city || '',
                          address_state: primaryJobSite?.state || dealLinkedClient?.preferred_state || '',
                          address_zip: primaryJobSite?.zip || dealLinkedClient?.preferred_zip || '',
                          services_requested: (selectedDeal as any).services_requested || '',
                          customer_name: dealLinkedClient?.preferred_name || '',
                        })
                        setDealCreateProjectOpen(true)
                      }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.78rem', color: '#0091ae', fontWeight: 500, padding: '2px 6px' }}
                    >+</button>
                  </div>
                  <div className="crp-card-body">
                    {dealLinkedProjects.length > 0 ? (
                      <>
                        {dealLinkedProjects.map((p: any) => (
                          <div key={p.id} className="crp-assoc-item" style={{ cursor: 'pointer', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }} onClick={() => {
                            setDealDetailOpen(false); setSelectedDeal(null)
                            setProjectDetailData(p); setProjectDetailOpen(true); setProjectDetailTab('about')
                            setActiveView('projects'); window.location.hash = `/projects/record/${p.id}`
                          }}>
                            <div className="crp-assoc-title">{p.name}</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                              <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 600, color: '#fff', background: DELIVERY_STAGE_COLORS[p.pipeline_stage] || '#94a3b8' }}>
                                {DELIVERY_STAGE_LABELS[p.pipeline_stage] || p.pipeline_stage || 'New'}
                              </span>
                              {p.pipeline_stage === 'P_ON_HOLD' && p.hold_type && (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 6px', borderRadius: 6, background: '#f5f5f5', color: '#64748b', fontSize: '0.68rem', fontWeight: 600 }} title={p.hold_reason ? `${p.hold_type}: ${p.hold_reason}` : p.hold_type}>
                                  {HOLD_TYPES.find(ht => ht.value === p.hold_type)?.label || p.hold_type}
                                </span>
                              )}
                              {Number(p.contract_value) > 0 && <span style={{ fontSize: '0.78rem', fontWeight: 500, color: '#33475b' }}>{currencyFormatter.format(p.contract_value)}</span>}
                            </div>
                          </div>
                        ))}
                        {Number(selectedDeal.opportunity || 0) > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: '#7c98b6', padding: '8px 0 2px', borderTop: '1px solid #e5e7eb', marginTop: 4 }}>
                            <span>Allocated: {currencyFormatter.format(dealAllocatedTotal)}</span>
                            <span style={{ fontWeight: 600, color: Number(selectedDeal.opportunity) - dealAllocatedTotal < 0 ? '#dc2626' : '#33475b' }}>
                              Remaining: {currencyFormatter.format(Math.max(0, Number(selectedDeal.opportunity) - dealAllocatedTotal))}
                            </span>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="crp-empty-card" style={{ color: '#94a3b8', fontSize: '0.78rem' }}>Click + to create a project</div>
                    )}
                  </div>
                </div>

                {/* Lead (Task 26b) — only surface when the source lead is still OPEN.
                    Once converted, the deal IS the next state of the lead, so showing
                    a "Lead (1)" card here just duplicates the deal itself (and a
                    converted lead is already filtered out of the contact's Leads card).
                    /api/crm/deals/:id/lead returns the raw DB row, so the converted
                    flags live on snake_case columns (is_converted, status_id) not the
                    camelCase ones produced by the leads-list mapper. */}
                {(selectedDeal.leadId || dealLinkedLead) && dealLinkedLead && !((dealLinkedLead as any).is_converted) && !((dealLinkedLead as any).isConverted) && (((dealLinkedLead as any).status_id || dealLinkedLead.status || '').toString().toUpperCase() !== 'CONVERTED') && (
                  <div className="crp-card">
                    <div className="crp-card-header"><h3>Lead (1)</h3></div>
                    <div className="crp-card-body">
                      {dealLinkedLead ? (
                        <div className="crp-assoc-item" style={{ cursor: 'pointer' }} onClick={() => {
                          setDealDetailOpen(false); setSelectedDeal(null)
                          setActiveView('leads'); setModalLead(dealLinkedLead)
                        }}>
                          <div className="crp-assoc-title">{dealLinkedLead.title || dealLinkedLead.name}</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                            <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 600, color: '#fff', background: getLeadStatusColor(dealLinkedLead.status) }}>
                              {getLeadStatusLabel(dealLinkedLead.status, dealLinkedLead.statusLabel)}
                            </span>
                            {dealLinkedLead.name && <span style={{ fontSize: '0.78rem', color: '#516f90' }}>{dealLinkedLead.name}</span>}
                          </div>
                        </div>
                      ) : dealLinkedLeadTried ? (
                        <div
                          className="crp-assoc-item"
                          style={{ cursor: 'pointer', padding: '6px 0' }}
                          onClick={() => {
                            const lid = selectedDeal.leadId != null ? String(selectedDeal.leadId) : ''
                            if (!lid) return
                            setDealDetailOpen(false); setSelectedDeal(null)
                            setActiveView('leads')
                            window.location.hash = '/leads'
                            fetchLead(Number(lid)).then(l => { if (l) setPreviewLead(l) }).catch(() => {})
                          }}
                        >
                          <div className="crp-assoc-title">Lead #{selectedDeal.leadId}</div>
                          <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 2 }}>Source lead no longer available</div>
                        </div>
                      ) : (
                        <div className="crp-empty-card">Loading lead...</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Quotes */}
                <div className="crp-card">
                  <div className="crp-card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <h3>Quotes (0)</h3>
                    <button
                      onClick={() => {
                        setCreateFormData({ deal_id: Number(selectedDeal.id), unified_client_id: selectedDeal.unifiedClientId || null, title: selectedDeal.title || '' })
                        setCreateError(null)
                        setShowCreateModal('quote')
                      }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.78rem', color: '#0091ae', fontWeight: 500, padding: '2px 6px' }}
                    >+</button>
                  </div>
                  <div className="crp-card-body">
                    <div className="crp-empty-card" style={{ color: '#94a3b8', fontSize: '0.78rem' }}>Click + to create a quote</div>
                  </div>
                </div>

                {/* Tickets (Task 15) */}
                <div className="crp-card">
                  <div className="crp-card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <h3>Tickets ({dealTickets.length})</h3>
                    <button
                      onClick={() => {
                        const dContact = selectedDeal.unifiedClientId || (selectedDeal.contactId != null ? `BX-${String(selectedDeal.contactId).padStart(5, '0')}` : null)
                        setTicketPlusChoice({
                          kind: 'deal',
                          id: Number(selectedDeal.id),
                          name: selectedDeal.title,
                          createPayload: dContact ? {
                            contact: dealLinkedClient || { unified_client_id: dContact, preferred_name: (selectedDeal as any).contact_name || undefined },
                            deal: { id: Number(selectedDeal.id), title: selectedDeal.title, unified_client_id: dContact },
                          } : null,
                        })
                      }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.78rem', color: '#0091ae', fontWeight: 500, padding: '2px 6px' }}
                    >+</button>
                  </div>
                  <div className="crp-card-body">
                    {dealTickets.length === 0 ? (
                      <div className="crp-empty-card">No tickets</div>
                    ) : dealTickets.map((t: any) => {
                      const isOverdue = t.due_date && new Date(t.due_date) < new Date() && t.status !== 'resolved' && t.status !== 'closed'
                      return (
                        <div
                          key={t.id}
                          id={`ticket-${t.id}`}
                          style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}
                        >
                          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#1e293b' }}>{t.title}</div>
                          <div style={{ display: 'flex', gap: 8, fontSize: '0.72rem', color: '#7c98b6', marginTop: 2 }}>
                            <span>{t.status}</span>
                            {t.project_name && <span>🏗 {t.project_name}</span>}
                            {t.due_date && <span>Due {new Date(t.due_date).toLocaleDateString()}</span>}
                            {isOverdue && <span style={{ color: '#dc2626', fontWeight: 600 }}>Overdue</span>}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Attachments — aggregated from every activity's files column */}
                {(() => {
                  type DealFile = { r2Key: string; name: string; size?: number; type?: string; activityId?: number; created?: string }
                  const allFiles: DealFile[] = []
                  for (const act of dealActivities) {
                    const raw = (act as any).files
                    if (!raw) continue
                    try {
                      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
                      if (Array.isArray(parsed)) {
                        for (const f of parsed) {
                          if (f && f.r2Key && f.name) allFiles.push({ ...f, activityId: act.id, created: act.created })
                        }
                      }
                    } catch { /* ignore malformed */ }
                  }
                  return (
                    <div className="crp-card">
                      <div className="crp-card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <h3>Attachments{allFiles.length > 0 ? ` (${allFiles.length})` : ''}</h3>
                        <label
                          title="Upload files to this deal"
                          style={{ background: 'none', border: 'none', cursor: dealAttachUploading ? 'wait' : 'pointer', fontSize: '0.78rem', color: '#0091ae', fontWeight: 500, padding: '2px 6px' }}
                        >
                          {dealAttachUploading ? 'Uploading…' : '+'}
                          <input
                            type="file"
                            multiple
                            style={{ display: 'none' }}
                            disabled={dealAttachUploading}
                            onChange={async (e) => {
                              const files = Array.from(e.target.files || [])
                              if (files.length === 0) return
                              setDealAttachUploading(true)
                              try {
                                await uploadDealAttachments(Number(selectedDeal.id), files, 'Attachment')
                                // Refresh activities so the new file-note rows appear
                                const acts = await getDealActivitiesMerged(Number(selectedDeal.id)).catch(() => null)
                                if (acts) setDealActivities(acts)
                              } catch (err: any) {
                                alert(err?.message || 'Upload failed')
                              } finally {
                                setDealAttachUploading(false)
                                e.target.value = ''
                              }
                            }}
                          />
                        </label>
                      </div>
                      <div className="crp-card-body">
                        {allFiles.length === 0 ? (
                          <div className="crp-empty-card" style={{ color: '#94a3b8', fontSize: '0.78rem' }}>Click + to upload a file</div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {allFiles.map((f, i) => (
                              <a
                                key={`${f.r2Key}-${i}`}
                                href={`/api/crm/files?key=${encodeURIComponent(f.r2Key)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: '0.78rem', color: '#1d4ed8', textDecoration: 'none' }}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                                <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}>{f.name}</div>
                                  <div style={{ color: '#7c98b6', fontSize: '0.7rem', marginTop: 1 }}>
                                    {f.size ? `${Math.round(f.size / 1024)}kb` : ''}
                                    {f.size && f.created ? ' · ' : ''}
                                    {f.created ? new Date(f.created).toLocaleDateString() : ''}
                                  </div>
                                </div>
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })()}
              </div>
            </div>

            {/* Sticky "Save changes" footer — mirrors the Contact record's crp-save-footer */}
            {Object.keys(dealPendingChanges).length > 0 && (
              <div className="prefs-save-footer crp-save-footer">
                <div className="prefs-save-footer-inner">
                  <div className="prefs-save-footer-btns">
                    <button
                      className="prefs-save-btn"
                      disabled={dealSavingChanges}
                      onClick={async () => {
                        if (!selectedDeal) return
                        setDealSavingChanges(true)
                        try {
                          const updated = await updateDeal(Number(selectedDeal.id), dealPendingChanges)
                          setSelectedDeal(updated)
                          setLiveDeals(prev => prev ? prev.map(d => d.id === updated.id ? updated : d) : prev)
                          setDealPendingChanges({})
                          // Refresh the Activity & History timeline so new audit entries appear
                          fetchDealHistory(Number(selectedDeal.id))
                            .then((rows) => setDealHistory(Array.isArray(rows) ? rows : []))
                            .catch(() => { /* non-fatal */ })
                          // Refresh dashboard pipeline panel; assignee edits move calendar events
                          setPipelineRefreshTick(t => t + 1)
                          if ('assignedById' in dealPendingChanges) setCalendarRefreshTick(t => t + 1)
                        } catch (e: any) {
                          alert('Failed to save: ' + (e?.message || String(e)))
                        } finally {
                          setDealSavingChanges(false)
                        }
                      }}
                    >{dealSavingChanges ? 'Saving…' : 'Save'}</button>
                    <button
                      className="prefs-cancel-btn"
                      disabled={dealSavingChanges}
                      onClick={() => setDealPendingChanges({})}
                    >Cancel</button>
                  </div>
                  <p className="prefs-save-footer-msg">
                    You've changed {Object.keys(dealPendingChanges).length} setting{Object.keys(dealPendingChanges).length === 1 ? '' : 's'}.
                  </p>
                </div>
              </div>
            )}
          </div>
          )
        })()}

        {/* Deal → Contact picker modal */}
        {dealContactPickerOpen && selectedDeal && (
          <>
            <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(15, 23, 42, 0.32)' }} onClick={() => setDealContactPickerOpen(false)} />
            <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10000, background: '#fff', border: '1px solid #dfe3eb', borderRadius: 8, boxShadow: '0 16px 48px rgba(0,0,0,0.2)', width: 420, maxHeight: '70vh', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 600, color: '#33475b' }}>Link a contact to this deal</h3>
                <button onClick={() => setDealContactPickerOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: '#94a3b8', padding: 0 }}>&times;</button>
              </div>
              <div style={{ padding: '10px 16px', borderBottom: '1px solid #f1f5f9' }}>
                <input
                  autoFocus
                  placeholder="Search by name, email, phone, or company…"
                  value={dealContactSearch}
                  onChange={e => setDealContactSearch(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.85rem', outline: 'none' }}
                />
              </div>
              <div style={{ overflowY: 'auto', flex: 1 }}>
                {dealContactSearch.trim() === '' ? (
                  <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: '0.82rem' }}>Start typing to search contacts</div>
                ) : dealContactSearching ? (
                  <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: '0.82rem' }}>Searching…</div>
                ) : dealContactResults.length === 0 ? (
                  <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: '0.82rem' }}>No contacts found</div>
                ) : (
                  dealContactResults.map((c: any) => (
                    <div
                      key={c.unified_client_id || c.id}
                      onClick={async () => {
                        const cid = c.unified_client_id || c.id
                        if (dealContacts.some((dc: any) => dc.unified_client_id === cid)) {
                          alert('This contact is already linked to this deal.')
                          return
                        }
                        try {
                          await fetch(`/api/crm/deals/${selectedDeal.id}/contacts`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ unified_client_id: cid }),
                          })
                          // Also set as primary unified_client_id if deal has none.
                          if (!selectedDeal.unifiedClientId) {
                            await updateDeal(Number(selectedDeal.id), { unifiedClientId: cid })
                            const fresh = await fetchDeal(Number(selectedDeal.id))
                            setSelectedDeal(fresh)
                            fetchClient(cid).then(fc => setDealLinkedClient(fc)).catch(() => {})
                          }
                          // Refresh the contacts list
                          const list = await fetch(`/api/crm/deals/${selectedDeal.id}/contacts`).then(r => r.json())
                          setDealContacts(Array.isArray(list) ? list : [])
                          setDealContactPickerOpen(false)
                        } catch (err: any) {
                          alert(err?.message || 'Failed to link contact')
                        }
                      }}
                      style={{ padding: '10px 16px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', transition: 'background 0.1s' }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#33475b' }}>{c.preferred_name || c.name || c.full_name || c.legal_name || 'Unnamed contact'}</div>
                      <div style={{ fontSize: '0.72rem', color: '#7c98b6', marginTop: 2 }}>
                        {[c.preferred_email, c.preferred_phone, c.legal_name].filter(Boolean).join(' • ') || '—'}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </>
        )}

        {/* Deal → Add Address modal */}
        {dealAddressAddOpen && selectedDeal && (
          <>
            <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(15, 23, 42, 0.32)' }} onClick={() => setDealAddressAddOpen(false)} />
            <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10000, background: '#fff', border: '1px solid #dfe3eb', borderRadius: 8, boxShadow: '0 16px 48px rgba(0,0,0,0.2)', width: 420 }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>
                <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 600 }}>Add address</h3>
              </div>
              <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: '0.78rem', fontWeight: 500, color: '#475569' }}>
                  Label <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional — e.g. "Primary", "Shop")</span>
                  <input value={dealNewAddrForm.label} onChange={e => setDealNewAddrForm(f => ({ ...f, label: e.target.value }))} style={{ padding: '7px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.85rem' }} />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: '0.78rem', fontWeight: 500, color: '#475569' }}>
                  Street Address
                  <AddressInput
                    value={dealNewAddrForm.street}
                    onChange={v => setDealNewAddrForm(f => ({ ...f, street: v }))}
                    onAddressPicked={addr => setDealNewAddrForm(f => ({
                      ...f,
                      street: addr.street,
                      city: addr.city,
                      state: addr.state,
                      zip: addr.zip,
                      formatted: [addr.street, addr.city, addr.state].filter(Boolean).join(', ') + (addr.zip ? ` ${addr.zip}` : ''),
                    }))}
                    placeholder="Start typing…"
                    style={{ padding: '7px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.85rem' }}
                  />
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8 }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: '0.78rem', fontWeight: 500, color: '#475569' }}>
                    City
                    <input value={dealNewAddrForm.city} onChange={e => setDealNewAddrForm(f => ({ ...f, city: e.target.value }))} style={{ padding: '7px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.85rem' }} />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: '0.78rem', fontWeight: 500, color: '#475569' }}>
                    State
                    <input value={dealNewAddrForm.state} onChange={e => setDealNewAddrForm(f => ({ ...f, state: e.target.value.toUpperCase().slice(0, 2) }))} style={{ padding: '7px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.85rem' }} />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: '0.78rem', fontWeight: 500, color: '#475569' }}>
                    ZIP
                    <input value={dealNewAddrForm.zip} onChange={e => setDealNewAddrForm(f => ({ ...f, zip: e.target.value }))} style={{ padding: '7px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.85rem' }} />
                  </label>
                </div>
              </div>
              <div style={{ padding: '12px 16px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={() => setDealAddressAddOpen(false)} style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: '0.85rem' }}>Cancel</button>
                <button
                  disabled={!dealNewAddrForm.street.trim() && !dealNewAddrForm.formatted.trim()}
                  onClick={async () => {
                    const body = { ...dealNewAddrForm }
                    body.formatted = body.formatted || [body.street, body.city, body.state].filter(Boolean).join(', ') + (body.zip ? ` ${body.zip}` : '')
                    try {
                      await fetch(`/api/crm/deals/${selectedDeal.id}/addresses`, {
                        method: 'POST', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(body),
                      })
                      const list = await fetch(`/api/crm/deals/${selectedDeal.id}/addresses`).then(r => r.json())
                      setDealAddresses(Array.isArray(list) ? list : [])
                      setDealAddressAddOpen(false)
                    } catch (err: any) {
                      alert(err?.message || 'Failed to add address')
                    }
                  }}
                  style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: '#1976d2', color: '#fff', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, opacity: (!dealNewAddrForm.street.trim() && !dealNewAddrForm.formatted.trim()) ? 0.6 : 1 }}
                >Add</button>
              </div>
            </div>
          </>
        )}

        {/* Contract Signed modal — top-level so it renders from any stage-change
            path (kanban drag, stage picker, deal record inline edit, etc.) */}
        {contractSignedModal && (() => {
          const csForm = dealCreateProjectForm
          const csSetForm = setDealCreateProjectForm
          return (
            <div className="kanban-modal-overlay">
              <div className="kanban-confirm-modal" style={{ maxWidth: 500, textAlign: 'left' }} onClick={(e) => e.stopPropagation()}>
                <div className="kanban-confirm-icon" style={{ background: '#fce7f3' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ec4899" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                </div>
                <h3 style={{ textAlign: 'center' }}>Contract Signed — Create Project</h3>
                <p style={{ fontSize: 13, color: '#516f90', marginBottom: 12, textAlign: 'center' }}>
                  Moving <strong>{contractSignedModal.dealTitle}</strong> to Contract Signed.
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: '0.78rem', fontWeight: 500, color: '#475569' }}>
                    Project Name *
                    <input value={csForm.name} onChange={e => csSetForm(f => ({ ...f, name: e.target.value }))} placeholder="Project name" style={{ padding: '7px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.85rem' }} />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: '0.78rem', fontWeight: 500, color: '#475569' }}>
                    Contract Value ($)
                    <input type="number" min="0" step="0.01" value={csForm.contractor_price} onChange={e => csSetForm(f => ({ ...f, contractor_price: e.target.value }))} placeholder="0.00" style={{ padding: '7px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.85rem' }} />
                    {(() => {
                      const dealTotal = Number(selectedDeal?.opportunity || 0)
                      if (dealTotal <= 0) return null
                      const allocated = dealAllocatedTotal
                      const typed = Number(csForm.contractor_price || 0)
                      const remainingBefore = Math.max(0, dealTotal - allocated)
                      const remainingAfter = dealTotal - allocated - (Number.isFinite(typed) ? typed : 0)
                      return (
                        <div style={{ fontSize: '0.68rem', color: remainingAfter < 0 ? '#dc2626' : '#7c98b6', marginTop: 2, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <span>Deal: {currencyFormatter.format(dealTotal)}</span>
                          {allocated > 0 && <span>Allocated: {currencyFormatter.format(allocated)}</span>}
                          <span style={{ fontWeight: 600 }}>Remaining{typed > 0 ? ' after' : ''}: {currencyFormatter.format(typed > 0 ? remainingAfter : remainingBefore)}</span>
                        </div>
                      )
                    })()}
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: '0.78rem', fontWeight: 500, color: '#475569' }}>
                    Address
                    {(() => {
                      const jobSites = dealAddresses.filter((a: any) => a.is_job_site)
                      if (jobSites.length === 0) {
                        return (
                          <div
                            title="Open the deal to add a job-site address"
                            onClick={() => {
                              setContractSignedModal(null)
                              setDealDetailOpen(true)
                              window.location.hash = '/deals/record/' + contractSignedModal!.dealId
                            }}
                            onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = '#fde68a' }}
                            onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = '#fef3c7' }}
                            style={{ padding: '8px 10px', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 4, fontSize: '0.78rem', color: '#92400e', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
                          >
                            <span>No job-site addresses on this deal. Add one on the deal record first.</span>
                            <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <line x1="7" y1="17" x2="17" y2="7"/>
                              <polyline points="7 7 17 7 17 17"/>
                            </svg>
                          </div>
                        )
                      }
                      return (
                        <select
                          required
                          value={jobSites.find(a => (a.formatted || '') === csForm.address)?.id || ''}
                          onChange={e => {
                            const picked = jobSites.find(a => String(a.id) === e.target.value)
                            if (picked) csSetForm(f => ({ ...f, address: picked.formatted || [picked.street, picked.city, picked.state].filter(Boolean).join(', ') }))
                            else csSetForm(f => ({ ...f, address: '' }))
                          }}
                          style={{ padding: '7px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.85rem' }}
                        >
                          <option value="">— Select a job site —</option>
                          {jobSites.map((a: any) => (
                            <option key={a.id} value={a.id}>
                              {a.is_primary_job_site ? '★ ' : ''}{a.label ? `${a.label}: ` : ''}{a.formatted || [a.street, a.city, a.state].filter(Boolean).join(', ')}
                            </option>
                          ))}
                        </select>
                      )
                    })()}
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: '0.78rem', fontWeight: 500, color: '#475569' }}>
                    Services Requested
                    <input value={csForm.services_requested} onChange={e => csSetForm(f => ({ ...f, services_requested: e.target.value }))} placeholder="e.g. Interior painting" style={{ padding: '7px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.85rem' }} />
                  </label>
                </div>

                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13, color: '#33475b', cursor: 'pointer', marginBottom: 14, padding: '10px 12px', background: '#f8fafc', borderRadius: 6, border: '1px solid #e2e8f0' }}>
                  <input type="checkbox" checked={contractSignedEmail} onChange={(e) => setContractSignedEmail(e.target.checked)} style={{ marginTop: 2, flexShrink: 0, cursor: 'pointer' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>Send confirmation email to client</div>
                    <div style={{ fontSize: 11, color: '#7c98b6', marginTop: 2 }}>from noreply@botpros.ai</div>
                  </div>
                </label>

                <div className="kanban-confirm-actions">
                  <button className="kanban-confirm-cancel" onClick={() => { setContractSignedModal(null); setContractSignedEmail(true) }}>Cancel</button>
                  <button className="kanban-confirm-ok" style={{ background: '#ec4899' }} disabled={!csForm.name.trim() || dealAddresses.filter((a: any) => a.is_job_site).length === 0} onClick={async () => {
                    const dealId = contractSignedModal.dealId
                    // 1) Create the project first. If this fails, do NOT move the
                    //    deal stage — leave the user in the modal with a toast.
                    let projectCreated = false
                    try {
                      const resp = await fetch(`/api/crm/deals/${dealId}/projects`, {
                        method: 'POST', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          name: csForm.name.trim(),
                          contractor_price: csForm.contractor_price ? Number(csForm.contractor_price) : 0,
                          address: csForm.address || null,
                          services_requested: csForm.services_requested || null,
                          customer_name: csForm.customer_name || null,
                        }),
                      })
                      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
                      projectCreated = true
                    } catch (err) {
                      console.error('contract-signed-create-project:', err)
                      setDealToast({ id: Date.now(), message: 'Failed to create project — deal stage NOT changed', type: 'error' })
                      return
                    }

                    // 1.5) If the user typed a Contract Value AND the deal's
                    //      current opportunity is empty/0, set the deal's amount
                    //      to the contract value. Never overwrite a non-zero
                    //      opportunity — the user can edit that on the deal
                    //      record. If this fails, abort before changing stage.
                    const typedContractValue = csForm.contractor_price ? Number(csForm.contractor_price) : 0
                    const existingDeal = (liveDeals || []).find((d) => String(d.id) === String(dealId))
                      || (selectedDeal && String(selectedDeal.id) === String(dealId) ? selectedDeal : null)
                    const existingOpportunity = Number(existingDeal?.opportunity || 0)
                    if (typedContractValue > 0 && (!existingOpportunity || existingOpportunity <= 0)) {
                      try {
                        await updateDeal(dealId, { opportunity: typedContractValue })
                        setLiveDeals((prev) => prev ? prev.map((d) => d.id === dealId ? { ...d, opportunity: typedContractValue } : d) : prev)
                        if (selectedDeal?.id && String(selectedDeal.id) === String(dealId)) {
                          setSelectedDeal({ ...selectedDeal, opportunity: typedContractValue } as any)
                        }
                      } catch (err) {
                        console.error('contract-signed-update-opportunity:', err)
                        setDealToast({ id: Date.now(), message: 'Project created, but failed to set deal amount — stage NOT changed', type: 'error' })
                        return
                      }
                    }

                    // 2) Project created — now move the deal to CONTRACT_SIGNED.
                    //    Call the stage API directly (don't rely on executeDealMove's
                    //    liveDeals-based guard, which silently no-ops when the deal
                    //    isn't in the current paginated list).
                    try {
                      const result = await updateDealStage(dealId, 'CONTRACT_SIGNED', { sendConfirmationEmail: contractSignedEmail }) as { success: boolean; previousStage: string; newStage: string; newStageLabel?: string; projectCreated?: number }
                      const targetLabel = allPipelineStages?.find((s) => s.stageId === 'CONTRACT_SIGNED')?.label ?? result.newStageLabel ?? 'Contract Signed'
                      // Update kanban / table optimistically
                      setLiveDeals((prev) => prev ? prev.map((d) => d.id === dealId ? { ...d, stage: 'CONTRACT_SIGNED', stageLabel: targetLabel } : d) : prev)
                      // Update the open deal record if it's this deal
                      if (selectedDeal?.id && String(selectedDeal.id) === String(dealId)) {
                        setSelectedDeal({ ...selectedDeal, stage: 'CONTRACT_SIGNED', stageLabel: targetLabel } as any)
                      }
                      setPipelineRefreshTick(t => t + 1)
                      setDealToast({ id: Date.now(), message: `Moved to ${targetLabel} · project created`, type: 'success' })
                    } catch (err) {
                      console.error('contract-signed-stage-update:', err)
                      // Project was created but stage move failed — surface this
                      // clearly so the user knows the kanban won't reflect it.
                      setDealToast({ id: Date.now(), message: 'Project created, but failed to move deal stage', type: 'error' })
                    }

                    // 3) Refresh the project list on the deal record if open
                    if (projectCreated && selectedDeal?.id && String(selectedDeal.id) === String(dealId)) {
                      try {
                        const data = await fetch(`/api/crm/deals/${dealId}/projects`).then(r => r.json())
                        setDealLinkedProjects(data.projects || [])
                        setDealAllocatedTotal(data.allocated_total || 0)
                      } catch { /* non-fatal */ }
                    }

                    setContractSignedModal(null); setContractSignedEmail(true)
                  }}>Confirm &amp; Create Project</button>
                </div>
              </div>
            </div>
          )
        })()}

        {/* Deal → Create Project modal */}
        {dealCreateProjectOpen && selectedDeal && (
          <>
            <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(15, 23, 42, 0.32)' }} onClick={() => { if (!dealCreateProjectSaving) setDealCreateProjectOpen(false) }} />
            <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10000, background: '#fff', border: '1px solid #dfe3eb', borderRadius: 8, boxShadow: '0 16px 48px rgba(0,0,0,0.2)', width: 480, display: 'flex', flexDirection: 'column', overflow: 'visible' }}>
              <div style={{ padding: '14px 16px', borderBottom: '1px solid #e5e7eb' }}>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#33475b' }}>Create project from deal</h3>
                <p style={{ margin: '4px 0 0', fontSize: '0.78rem', color: '#7c98b6' }}>
                  Deal: {selectedDeal.title} — {currencyFormatter.format(selectedDeal.opportunity || 0)}
                  {dealLinkedProjects.length > 0 && ` (${currencyFormatter.format(Math.max(0, Number(selectedDeal.opportunity || 0) - dealAllocatedTotal))} remaining)`}
                </p>
              </div>
              <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10, overflow: 'visible' }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: '0.78rem', fontWeight: 500, color: '#475569' }}>
                  Project Name *
                  <input value={dealCreateProjectForm.name} onChange={e => setDealCreateProjectForm(f => ({ ...f, name: e.target.value }))} placeholder="Project name" style={{ padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.85rem' }} />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: '0.78rem', fontWeight: 500, color: '#475569' }}>
                  Contract Value ($)
                  <input type="number" min="0" step="0.01" value={dealCreateProjectForm.contractor_price} onChange={e => setDealCreateProjectForm(f => ({ ...f, contractor_price: e.target.value }))} placeholder="0.00" style={{ padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.85rem' }} />
                  {(() => {
                    const dealTotal = Number(selectedDeal?.opportunity || 0)
                    if (dealTotal <= 0) return null
                    const allocated = dealAllocatedTotal
                    const typed = Number(dealCreateProjectForm.contractor_price || 0)
                    const remainingBefore = Math.max(0, dealTotal - allocated)
                    const remainingAfter = dealTotal - allocated - (Number.isFinite(typed) ? typed : 0)
                    return (
                      <div style={{ fontSize: '0.68rem', color: remainingAfter < 0 ? '#dc2626' : '#7c98b6', marginTop: 2, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <span>Deal: {currencyFormatter.format(dealTotal)}</span>
                        {allocated > 0 && <span>Allocated: {currencyFormatter.format(allocated)}</span>}
                        <span style={{ fontWeight: 600 }}>Remaining{typed > 0 ? ' after' : ''}: {currencyFormatter.format(typed > 0 ? remainingAfter : remainingBefore)}</span>
                      </div>
                    )
                  })()}
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: '0.78rem', fontWeight: 500, color: '#475569' }}>
                  Work Order Price ($)
                  <input type="number" min="0" step="0.01" value={dealCreateProjectForm.work_order_price} onChange={e => setDealCreateProjectForm(f => ({ ...f, work_order_price: e.target.value }))} placeholder="Optional" style={{ padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.85rem' }} />
                  <div style={{ fontSize: '0.68rem', color: '#7c98b6', marginTop: 2 }}>Optional — overrides the subcontractor's price. Leave blank to fall back to Contract Value minus profit.</div>
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: '0.78rem', fontWeight: 500, color: '#475569' }}>
                  Address
                  <AddressInput
                    value={dealCreateProjectForm.address}
                    onChange={v => setDealCreateProjectForm(f => ({ ...f, address: v }))}
                    onAddressPicked={addr => setDealCreateProjectForm(f => ({ ...f, address: [addr.street, addr.city, addr.state].filter(Boolean).join(', ') + (addr.zip ? ` ${addr.zip}` : ''), address_street: addr.street || '', address_city: addr.city || '', address_state: addr.state || '', address_zip: addr.zip || '' }))}
                    placeholder="Start typing an address…"
                    style={{ padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.85rem' }}
                  />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: '0.78rem', fontWeight: 500, color: '#475569' }}>
                  Services Requested
                  <input value={dealCreateProjectForm.services_requested} onChange={e => setDealCreateProjectForm(f => ({ ...f, services_requested: e.target.value }))} placeholder="e.g. Interior painting, pressure washing" style={{ padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.85rem' }} />
                </label>
              </div>
              <div style={{ padding: '12px 16px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button
                  disabled={dealCreateProjectSaving}
                  onClick={() => setDealCreateProjectOpen(false)}
                  style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: '0.85rem' }}
                >Cancel</button>
                <button
                  disabled={dealCreateProjectSaving || !dealCreateProjectForm.name.trim()}
                  onClick={async () => {
                    setDealCreateProjectSaving(true)
                    try {
                      const createResp = await fetch(`/api/crm/deals/${selectedDeal.id}/projects`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          name: dealCreateProjectForm.name.trim(),
                          contractor_price: dealCreateProjectForm.contractor_price ? Number(dealCreateProjectForm.contractor_price) : 0,
                          work_order_price: dealCreateProjectForm.work_order_price ? Number(dealCreateProjectForm.work_order_price) : null,
                          address: dealCreateProjectForm.address || null,
                          address_street: dealCreateProjectForm.address_street || null,
                          address_city: dealCreateProjectForm.address_city || null,
                          address_state: dealCreateProjectForm.address_state || null,
                          address_zip: dealCreateProjectForm.address_zip || null,
                          services_requested: dealCreateProjectForm.services_requested || null,
                          customer_name: dealCreateProjectForm.customer_name || null,
                        }),
                      })
                      const createResult = await createResp.json() as any
                      // If backend auto-advanced the deal to Contract Signed, reflect it in UI
                      if (createResult.dealStageUpdated) {
                        const newStage = createResult.dealStage || 'CONTRACT_SIGNED'
                        const newLabel = createResult.dealStageLabel || 'Contract Signed'
                        setLiveDeals((prev: any) => prev ? prev.map((d: any) => String(d.id) === String(selectedDeal.id) ? { ...d, stage: newStage, stageLabel: newLabel } : d) : prev)
                        setSelectedDeal((prev: any) => prev ? { ...prev, stage: newStage, stageLabel: newLabel } : prev)
                        setPipelineRefreshTick((t: number) => t + 1)
                      }
                      // Refresh projects list
                      const data = await fetch(`/api/crm/deals/${selectedDeal.id}/projects`).then(r => r.json())
                      setDealLinkedProjects(data.projects || [])
                      setDealAllocatedTotal(data.allocated_total || 0)
                      setDealCreateProjectOpen(false)
                    } catch (err: any) {
                      alert(err?.message || 'Failed to create project')
                    } finally {
                      setDealCreateProjectSaving(false)
                    }
                  }}
                  style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: '#1976d2', color: '#fff', cursor: dealCreateProjectSaving ? 'wait' : 'pointer', fontSize: '0.85rem', fontWeight: 600, opacity: (!dealCreateProjectForm.name.trim() || dealCreateProjectSaving) ? 0.6 : 1 }}
                >{dealCreateProjectSaving ? 'Creating…' : 'Create Project'}</button>
              </div>
            </div>
          </>
        )}

        {/* Deal → Company picker modal */}
        {dealCompanyPickerOpen && selectedDeal && (
          <>
            <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(15, 23, 42, 0.32)' }} onClick={() => setDealCompanyPickerOpen(false)} />
            <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10000, background: '#fff', border: '1px solid #dfe3eb', borderRadius: 8, boxShadow: '0 16px 48px rgba(0,0,0,0.2)', width: 420, maxHeight: '70vh', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 600, color: '#33475b' }}>Link a client to this deal</h3>
                <button onClick={() => setDealCompanyPickerOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: '#94a3b8', padding: 0 }}>&times;</button>
              </div>
              <div style={{ padding: '10px 16px', borderBottom: '1px solid #f1f5f9' }}>
                <input
                  autoFocus
                  placeholder="Search clients by name…"
                  value={dealCompanySearch}
                  onChange={e => setDealCompanySearch(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.85rem', outline: 'none' }}
                />
              </div>
              <div style={{ overflowY: 'auto', flex: 1 }}>
                {dealCompanySearch.trim() === '' ? (
                  <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: '0.82rem' }}>Start typing to search clients</div>
                ) : dealCompanySearching ? (
                  <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: '0.82rem' }}>Searching…</div>
                ) : dealCompanyResults.length === 0 ? (
                  <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: '0.82rem' }}>No clients found</div>
                ) : (
                  dealCompanyResults.map((c: any) => (
                    <div
                      key={c.unified_client_id || c.id}
                      onClick={async () => {
                        const cid = c.unified_client_id || c.id
                        if (dealContacts.some((dc: any) => dc.unified_client_id === cid)) {
                          alert('This client is already linked to this deal.')
                          return
                        }
                        try {
                          await fetch(`/api/crm/deals/${selectedDeal.id}/contacts`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ unified_client_id: cid, role: 'company' }),
                          })
                          const list = await fetch(`/api/crm/deals/${selectedDeal.id}/contacts`).then(r => r.json())
                          setDealContacts(Array.isArray(list) ? list : [])
                          setDealCompanyPickerOpen(false)
                        } catch (err: any) {
                          alert(err?.message || 'Failed to link client')
                        }
                      }}
                      style={{ padding: '10px 16px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', transition: 'background 0.1s' }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#33475b' }}>{c.preferred_name || c.name || 'Unnamed client'}</div>
                      <div style={{ fontSize: '0.72rem', color: '#7c98b6', marginTop: 2 }}>
                        {[c.preferred_phone, c.preferred_email].filter(Boolean).join(' • ') || '—'}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </>
        )}

        {/* ========== HR CENTER PORTAL ========== */}
        {activeView === 'hr-center' && myCapabilities && !myCapabilities.access_hr_center && (
          <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
            <h2 style={{ color: '#33475b', marginBottom: 8 }}>Access denied</h2>
            <p>The HR Center is restricted to super-admin users.</p>
          </div>
        )}
        {activeView === 'hr-center' && (!myCapabilities || myCapabilities.access_hr_center) && (
          <div className="hr-portal-container">
            {/* Decorative HR portal header (title/subtitle/admin-badge/user-name) removed
                2026-06-20 — purely cosmetic; the global top-right app header already shows
                the signed-in user. */}
            {/* In-view tab strip removed 2026-06-20 — the CRM-style flyout secondary
                nav + deep hash routing (navigateHrTab) now drive HR tab navigation,
                and the flyout's Settings entry replaces the redundant gear.
                All tabs (Dashboard/Templates/Forms/Assignments/Pipelines/Employees/
                Change Requests/Timesheets/Settings) remain reachable from the flyout. */}

            {/* Portal Content */}
            <div className="hr-portal-content">
              {/* ===== Dashboard Tab ===== */}
              {hrPortalView === 'dashboard' && (
                <Suspense fallback={PrefsLazyFallback}>
                  <HrDashboard
                    dashboard={hrPortalDashboard}
                    loading={hrPortalDashboardLoading}
                    activityExpanded={hrPortalActivityExpanded}
                    setActivityExpanded={setHrPortalActivityExpanded}
                    onNavigate={(target) => setHrPortalView(target as any)}
                    onNavigateAssignments={(filter) => { setHrPortalView('assignments'); setHrPortalAssignmentFilter2(filter) }}
                    onNavigatePipelines={(pipelineId) => { setHrPortalView('pipelines'); setHrPortalActivePipeline(pipelineId) }}
                  />
                </Suspense>
              )}

              {/* ===== Pipelines / Kanban Tab ===== */}
              {hrPortalView === 'pipelines' && (
                <Suspense fallback={PrefsLazyFallback}>
                  <HrPipelines
                    pipelines={hrPortalPipelines}
                    stages={hrPortalStages}
                    profiles={hrPortalProfiles}
                    activePipeline={hrPortalActivePipeline}
                    setActivePipeline={setHrPortalActivePipeline}
                    loading={hrPortalPipelinesLoading}
                    search={hrPortalPipelineSearch}
                    setSearch={setHrPortalPipelineSearch}
                    draggedId={hrPortalDraggedId}
                    setDraggedId={setHrPortalDraggedId}
                    dragOverStage={hrPortalDragOverStage}
                    setDragOverStage={setHrPortalDragOverStage}
                    cardMenuOpen={hrPortalCardMenuOpen}
                    setCardMenuOpen={setHrPortalCardMenuOpen}
                    setViewProfileId={setHrPortalViewProfileId}
                    setProfiles={setHrPortalProfiles}
                    openItProvisioningModal={(profileId: string, stageId: string) => {
                      const prof = hrPortalProfiles.find((p: any) => String(p.id || p.employee_id) === String(profileId))
                      const firstName = (prof?.first_name || prof?.name?.split(' ')[0] || '').toLowerCase()
                      setItProvForm({ needsEmail: true, emailPrefix: firstName, tablet: false, tabletType: 'iPad', phone: false, phoneType: 'iPhone', laptop: false })
                      setItEmailCheck(null)
                      setItProvModal({ profileId, firstName, stageId })
                    }}
                  />
                </Suspense>
              )}

              {/* ===== Forms (drag-drop form builder) Tab ===== */}
              {hrPortalView === 'forms' && (
                <Suspense fallback={PrefsLazyFallback}>
                  <HrForms
                    activeFormKey={hrPortalFormKey}
                    setActiveFormKey={setHrPortalFormKey}
                    audienceFilter="employee"
                  />
                </Suspense>
              )}

              {/* Subcontractors pipeline moved to the dedicated #/subcontractors section
                  (rendered as the Applications tab in src/views/subcontractors/Subcontractors.tsx). */}

              {/* ===== Profile Detail Panel (View Details from Kanban) ===== */}
              {hrPortalViewProfileId && (() => {
                const pd = hrPortalViewProfileData
                const decHtml = (s: string|null|undefined) => (s || '').replace(/&#x2F;/gi, '/').replace(/&#x27;/gi, "'").replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"')
                return (
                  <div style={{position:'fixed',inset:0,zIndex:9998,display:'flex'}}>
                    <div style={{flex:1,background:'rgba(0,0,0,0.3)'}} onClick={() => setHrPortalViewProfileId(null)} />
                    <div style={{width:620,maxWidth:'100vw',background:'#fff',boxShadow:'-8px 0 32px rgba(0,0,0,0.15)',display:'flex',flexDirection:'column',overflow:'hidden'}}>
                      {/* Header */}
                      <div style={{padding:'16px 20px',borderBottom:'1px solid #e5e7eb',display:'flex',alignItems:'center',gap:12,flexShrink:0}}>
                        <button onClick={() => setHrPortalViewProfileId(null)} style={{background:'none',border:'none',cursor:'pointer',color:'#6b7280',display:'flex',padding:4}}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                        </button>
                        <div style={{flex:1}}>
                          <div style={{fontSize:'1.05rem',fontWeight:700,color:'#111827'}}>
                            {pd?.profile ? `${pd.profile.first_name || ''} ${pd.profile.last_name || ''}`.trim() || 'Profile' : 'Loading...'}
                          </div>
                          {pd?.profile && (
                            <div style={{fontSize:'0.78rem',color:'#6b7280',marginTop:2}}>
                              {decHtml(pd.profile.position)}{pd.profile.email ? ` · ${pd.profile.email}` : ''}{pd.profile.phone ? ` · ${pd.profile.phone}` : ''}
                            </div>
                          )}
                        </div>
                      </div>
                      {/* Tabs */}
                      <div style={{display:'flex',gap:0,borderBottom:'1px solid #e5e7eb',flexShrink:0,padding:'0 20px'}}>
                        {(['application','documents','onboarding'] as const).map(tab => (
                          <button key={tab} onClick={() => setHrPortalViewProfileTab(tab)}
                            style={{padding:'10px 16px',border:'none',background:'none',fontSize:'0.82rem',fontWeight:600,color:hrPortalViewProfileTab===tab?'#2563eb':'#6b7280',
                              borderBottom:hrPortalViewProfileTab===tab?'2px solid #2563eb':'2px solid transparent',cursor:'pointer',textTransform:'capitalize'}}>
                            {tab === 'application' ? 'Application' : tab === 'documents' ? 'Documents' : 'Onboarding'}
                          </button>
                        ))}
                      </div>
                      {/* Body */}
                      <div style={{flex:1,overflowY:'auto',padding:20}}>
                        {hrPortalViewProfileLoading ? (
                          <div style={{textAlign:'center',padding:40,color:'#9ca3af'}}>Loading...</div>
                        ) : !pd ? (
                          <div style={{textAlign:'center',padding:40,color:'#9ca3af'}}>No data available</div>
                        ) : hrPortalViewProfileTab === 'application' ? (
                          <div>
                            {pd.application ? (
                              <>
                                <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:16}}>
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/></svg>
                                  <span style={{fontSize:'0.85rem',fontWeight:600,color:'#374151'}}>Application Submission</span>
                                  {pd.application.submitted_at && <span style={{fontSize:'0.72rem',color:'#9ca3af',marginLeft:'auto'}}>{new Date(pd.application.submitted_at).toLocaleDateString()}</span>}
                                </div>
                                {/* Resume / Cover Letter downloads */}
                                {(pd.application.resume_url || pd.application.cover_letter_url) && (
                                  <div style={{display:'flex',gap:8,marginBottom:16,flexWrap:'wrap'}}>
                                    {pd.application.resume_url && (
                                      <button onClick={() => { fetchHrAdminFileDownload(pd.application.resume_url).then(blob => { const u = URL.createObjectURL(blob); window.open(u, '_blank') }).catch(() => alert('Failed to download resume')) }}
                                        style={{display:'flex',alignItems:'center',gap:6,padding:'8px 14px',border:'1px solid #e5e7eb',borderRadius:8,background:'#f9fafb',fontSize:'0.78rem',fontWeight:600,color:'#374151',cursor:'pointer'}}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>
                                        Resume
                                      </button>
                                    )}
                                    {pd.application.cover_letter_url && (
                                      <button onClick={() => { fetchHrAdminFileDownload(pd.application.cover_letter_url).then(blob => { const u = URL.createObjectURL(blob); window.open(u, '_blank') }).catch(() => alert('Failed to download cover letter')) }}
                                        style={{display:'flex',alignItems:'center',gap:6,padding:'8px 14px',border:'1px solid #e5e7eb',borderRadius:8,background:'#f9fafb',fontSize:'0.78rem',fontWeight:600,color:'#374151',cursor:'pointer'}}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>
                                        Cover Letter
                                      </button>
                                    )}
                                  </div>
                                )}
                                {/* Form fields grid */}
                                {pd.application.form_data && (() => {
                                  const fmtLabel = (k: string) => k.replace(/([A-Z])/g, ' $1').replace(/([a-z])(\d)/g, '$1 $2').replace(/^./, s => s.toUpperCase()).trim()
                                  const fmtVal = (v: any): string => {
                                    if (v === true) return 'Yes'
                                    if (v === false) return 'No'
                                    if (v === null || v === undefined || v === '') return '—'
                                    return decHtml(String(v))
                                  }
                                  const exclude = new Set(['resume','coverLetter','turnstileToken','g-recaptcha-response','token'])
                                  const entries = Object.entries(pd.application.form_data as Record<string,any>).filter(([k]) => !exclude.has(k))
                                  const scalar = entries.filter(([,v]) => !Array.isArray(v))
                                  const arrays = entries.filter(([,v]) => Array.isArray(v))
                                  return (
                                    <>
                                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'1px',background:'#e5e7eb',borderRadius:8,overflow:'hidden',border:'1px solid #e5e7eb'}}>
                                        {scalar.map(([key, val]) => (
                                          <div key={key} style={{padding:'10px 14px',background:'#fff'}}>
                                            <div style={{fontSize:'0.68rem',fontWeight:600,color:'#9ca3af',textTransform:'uppercase',letterSpacing:'0.03em',marginBottom:3}}>{fmtLabel(key)}</div>
                                            <div style={{fontSize:'0.82rem',color:'#111827',wordBreak:'break-word'}}>{fmtVal(val)}</div>
                                          </div>
                                        ))}
                                      </div>
                                      {arrays.map(([key, arr]) => (
                                        <div key={key} style={{marginTop:16}}>
                                          <div style={{fontSize:'0.78rem',fontWeight:700,color:'#374151',marginBottom:8,textTransform:'uppercase',letterSpacing:'0.03em'}}>{fmtLabel(key)}</div>
                                          <div style={{display:'flex',flexDirection:'column',gap:8}}>
                                            {(arr as any[]).map((item, i) => (
                                              <div key={i} style={{padding:'10px 14px',border:'1px solid #e5e7eb',borderRadius:8,background:'#f9fafb'}}>
                                                {typeof item === 'object' && item !== null ? (
                                                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'6px 16px'}}>
                                                    {Object.entries(item).map(([ik, iv]) => (
                                                      <div key={ik}>
                                                        <div style={{fontSize:'0.65rem',fontWeight:600,color:'#9ca3af',textTransform:'uppercase',marginBottom:1}}>{fmtLabel(ik)}</div>
                                                        <div style={{fontSize:'0.8rem',color:'#111827'}}>{fmtVal(iv)}</div>
                                                      </div>
                                                    ))}
                                                  </div>
                                                ) : <div style={{fontSize:'0.82rem',color:'#111827'}}>{fmtVal(item)}</div>}
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      ))}
                                    </>
                                  )
                                })()}
                              </>
                            ) : (
                              <div style={{textAlign:'center',padding:40,color:'#9ca3af'}}>
                                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" style={{margin:'0 auto 12px'}}><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/></svg>
                                <div style={{fontSize:'0.85rem',fontWeight:600}}>No application on file</div>
                                <div style={{fontSize:'0.78rem',marginTop:4}}>This profile doesn't have an application submission yet.</div>
                              </div>
                            )}
                          </div>
                        ) : hrPortalViewProfileTab === 'documents' ? (
                          <div>
                            {/* Signed onboarding docs */}
                            {(pd.signedDocuments || []).length > 0 && (
                              <div style={{marginBottom:20}}>
                                <div style={{fontSize:'0.78rem',fontWeight:700,color:'#374151',marginBottom:10,textTransform:'uppercase',letterSpacing:'0.03em'}}>Signed Documents</div>
                                <div style={{display:'flex',flexDirection:'column',gap:8}}>
                                  {(pd.signedDocuments || []).map((doc: any) => (
                                    <div key={doc.id} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',border:'1px solid #e5e7eb',borderRadius:8,background:'#f9fafb'}}>
                                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2"><path d="M9 12l2 2 4-4"/><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/></svg>
                                      <div style={{flex:1}}>
                                        <div style={{fontSize:'0.82rem',fontWeight:600,color:'#111827',textTransform:'uppercase'}}>{doc.document_type}</div>
                                        <div style={{fontSize:'0.7rem',color:'#9ca3af'}}>{doc.signed_at ? `Signed ${new Date(doc.signed_at).toLocaleDateString()}` : 'Signed'}</div>
                                      </div>
                                      {doc.signed_document_url && (
                                        <button onClick={() => { fetchHrAdminFileDownload(doc.signed_document_url).then(blob => { const u = URL.createObjectURL(blob); window.open(u, '_blank') }).catch(() => alert('Failed to download')) }}
                                          style={{background:'none',border:'1px solid #d1d5db',borderRadius:6,padding:'4px 10px',fontSize:'0.72rem',fontWeight:600,color:'#374151',cursor:'pointer'}}>
                                          View
                                        </button>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {/* Document assignments */}
                            {(pd.assignments || []).length > 0 && (
                              <div>
                                <div style={{fontSize:'0.78rem',fontWeight:700,color:'#374151',marginBottom:10,textTransform:'uppercase',letterSpacing:'0.03em'}}>Assigned Documents</div>
                                <div style={{display:'flex',flexDirection:'column',gap:8}}>
                                  {(pd.assignments || []).map((a: any) => {
                                    const statusColors: Record<string,{bg:string,fg:string}> = { completed:{bg:'#dcfce7',fg:'#166534'}, signed:{bg:'#dcfce7',fg:'#166534'}, sent:{bg:'#dbeafe',fg:'#1e40af'}, assigned:{bg:'#fef3c7',fg:'#92400e'}, pending:{bg:'#fef3c7',fg:'#92400e'} }
                                    const sc = statusColors[a.status] || {bg:'#f1f5f9',fg:'#64748b'}
                                    return (
                                      <div key={a.id} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',border:'1px solid #e5e7eb',borderRadius:8,background:'#fff'}}>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>
                                        <div style={{flex:1}}>
                                          <div style={{fontSize:'0.82rem',fontWeight:600,color:'#111827'}}>{a.template_title || 'Document'}</div>
                                          <div style={{fontSize:'0.7rem',color:'#9ca3af'}}>
                                            {a.category && <span>{a.category} · </span>}
                                            {a.assigned_at ? `Assigned ${new Date(a.assigned_at).toLocaleDateString()}` : ''}
                                            {a.due_date ? ` · Due ${new Date(a.due_date).toLocaleDateString()}` : ''}
                                          </div>
                                        </div>
                                        <span style={{fontSize:'0.68rem',fontWeight:600,padding:'3px 8px',borderRadius:4,background:sc.bg,color:sc.fg,textTransform:'uppercase'}}>{a.status}</span>
                                        {a.signed_document_url && (
                                          <button onClick={() => { fetchHrAdminFileDownload(a.signed_document_url).then(blob => { const u = URL.createObjectURL(blob); window.open(u, '_blank') }).catch(() => alert('Failed to download')) }}
                                            style={{background:'none',border:'1px solid #d1d5db',borderRadius:6,padding:'4px 10px',fontSize:'0.72rem',fontWeight:600,color:'#374151',cursor:'pointer'}}>
                                            View
                                          </button>
                                        )}
                                      </div>
                                    )
                                  })}
                                </div>
                              </div>
                            )}
                            {(pd.signedDocuments || []).length === 0 && (pd.assignments || []).length === 0 && (
                              <div style={{textAlign:'center',padding:40,color:'#9ca3af'}}>
                                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" style={{margin:'0 auto 12px'}}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>
                                <div style={{fontSize:'0.85rem',fontWeight:600}}>No documents</div>
                                <div style={{fontSize:'0.78rem',marginTop:4}}>No document assignments or signed documents for this profile.</div>
                              </div>
                            )}
                          </div>
                        ) : hrPortalViewProfileTab === 'onboarding' ? (
                          <div>
                            {pd.onboarding ? (
                              <>
                                <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:16}}>
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/><path d="M9 14l2 2 4-4"/></svg>
                                  <span style={{fontSize:'0.85rem',fontWeight:600,color:'#374151'}}>Onboarding Questionnaire</span>
                                  {pd.onboarding.submitted_at && <span style={{fontSize:'0.72rem',color:'#9ca3af',marginLeft:'auto'}}>{new Date(pd.onboarding.submitted_at).toLocaleDateString()}</span>}
                                </div>
                                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'1px',background:'#e5e7eb',borderRadius:8,overflow:'hidden',border:'1px solid #e5e7eb'}}>
                                  {Object.entries(pd.onboarding.data as Record<string,any>)
                                    .filter(([k]) => !['resume','coverLetter','turnstileToken','g-recaptcha-response','token'].includes(k))
                                    .map(([key, val]) => (
                                      <div key={key} style={{padding:'10px 14px',background:'#fff'}}>
                                        <div style={{fontSize:'0.68rem',fontWeight:600,color:'#9ca3af',textTransform:'uppercase',letterSpacing:'0.03em',marginBottom:3}}>
                                          {key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim()}
                                        </div>
                                        <div style={{fontSize:'0.82rem',color:'#111827',wordBreak:'break-word'}}>
                                          {val === true ? 'Yes' : val === false ? 'No' : val === null || val === '' ? '—' : typeof val === 'object' ? JSON.stringify(val) : String(val)}
                                        </div>
                                      </div>
                                    ))}
                                </div>
                              </>
                            ) : (
                              <div style={{textAlign:'center',padding:40,color:'#9ca3af'}}>
                                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" style={{margin:'0 auto 12px'}}><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/></svg>
                                <div style={{fontSize:'0.85rem',fontWeight:600}}>No onboarding data</div>
                                <div style={{fontSize:'0.78rem',marginTop:4}}>This profile hasn't completed the onboarding questionnaire yet.</div>
                              </div>
                            )}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                )
              })()}

              {/* ===== Employees Tab ===== */}
              {hrPortalView === 'employees' && !hrPortalSelectedId && (
                <Suspense fallback={PrefsLazyFallback}>
                  <HrEmployeesList
                    employees={hrPortalEmployees}
                    total={hrPortalEmployeesTotal}
                    loading={hrPortalEmployeesLoading}
                    search={hrPortalSearch}
                    setSearch={setHrPortalSearch}
                    deptFilter={hrPortalDeptFilter}
                    setDeptFilter={setHrPortalDeptFilter}
                    statusFilter={hrPortalStatusFilter}
                    setStatusFilter={setHrPortalStatusFilter}
                    page={hrPortalPage}
                    setPage={setHrPortalPage}
                    setSelectedId={setHrPortalSelectedId}
                    rcExtMap={hrPortalRcExtMap}
                    ensureRcExtension={ensureRcExtension}
                  />
                </Suspense>
              )}

              {/* ===== Employee Detail View ===== */}
              {/* ===== Employee Detail View ===== */}
              {hrPortalView === 'employees' && hrPortalSelectedId && (
                <Suspense fallback={PrefsLazyFallback}>
                  <HrEmployeesDetail
                    selectedId={hrPortalSelectedId}
                    detail={hrPortalDetail}
                    setDetail={setHrPortalDetail}
                    detailLoading={hrPortalDetailLoading}
                    detailTab={hrPortalDetailTab}
                    setDetailTab={setHrPortalDetailTab}
                    fieldSearch={hrPortalFieldSearch}
                    setFieldSearch={setHrPortalFieldSearch}
                    saving={hrPortalSaving}
                    setSaving={setHrPortalSaving}
                    editingField={hrPortalEditingField}
                    setEditingField={setHrPortalEditingField}
                    alerts={hrPortalAlerts}
                    docs={hrPortalDocs}
                    setDocs={setHrPortalDocs}
                    activityRollup={hrPortalActivityRollup}
                    activityLoading={hrPortalActivityLoading}
                    expandedSections={hrPortalExpandedSections}
                    setExpandedSections={setHrPortalExpandedSections}
                    setSelectedId={setHrPortalSelectedId}
                    setConfirmModal={setHrPortalConfirmModal}
                    itEmailCheck={itEmailCheck}
                    setItEmailCheck={setItEmailCheck}
                    itEmailChecking={itEmailChecking}
                    setItEmailChecking={setItEmailChecking}
                    itSending={itSending}
                    setItSending={setItSending}
                    itToast={itToast}
                    setItToast={setItToast}
                    setDocUploadModal={setHrDocUploadModal}
                    setDocUploadType={setHrDocUploadType}
                    setDocUploadCustom={setHrDocUploadCustom}
                    setDocUploadFile={setHrDocUploadFile}
                  />
                </Suspense>
              )}

              {/* ===== Assignments Tab ===== */}
              {hrPortalView === 'assignments' && (
                <Suspense fallback={PrefsLazyFallback}>
                  <HrAssignments
                    assignments={hrPortalAssignmentsList}
                    setAssignments={setHrPortalAssignmentsList}
                    loading={hrPortalAssignmentsLoading2}
                    filter={hrPortalAssignmentFilter2}
                    setFilter={setHrPortalAssignmentFilter2}
                    search={hrPortalSearch}
                    setSearch={setHrPortalSearch}
                    selectedIds={hrPortalAssignSelectedIds}
                    setSelectedIds={setHrPortalAssignSelectedIds}
                    expandedEmployees={hrPortalAssignExpandedEmployees}
                    setExpandedEmployees={setHrPortalAssignExpandedEmployees}
                    setConfirmModal={setHrPortalConfirmModal}
                    pendingEmployer={hrPortalPendingEmployer}
                    onOpenEmployerSigning={() => setHrPortalView('employer-signing' as any)}
                    openSignModal={(id) => openHrSignModal(id)}
                  />
                </Suspense>
              )}

              {/* ===== Change Requests Tab — admin queue ===== */}
              {hrPortalView === 'change-requests' && (
                <div className="hr-portal-content">
                  <div className="hr-portal-section-header">
                    <div className="hr-portal-section-header-row">
                      <div>
                        <h1>Change Requests</h1>
                        <p>Employee-submitted requests to change fields they can't edit directly.</p>
                      </div>
                    </div>
                  </div>

                  {/* status tabs */}
                  <div style={{ display: 'flex', gap: 4, padding: '0 24px', borderBottom: '1px solid var(--line)', marginBottom: 12 }}>
                    {(['pending', 'approved', 'denied', 'cancelled'] as const).map(s => (
                      <button
                        key={s}
                        onClick={() => setHrChangeRequestsTab(s)}
                        style={{
                          padding: '8px 14px', border: 'none', background: 'none', cursor: 'pointer',
                          fontSize: '0.86rem', fontWeight: 600,
                          color: hrChangeRequestsTab === s ? '#0091ae' : '#64748b',
                          borderBottom: hrChangeRequestsTab === s ? '2px solid #0091ae' : '2px solid transparent',
                          marginBottom: -1, textTransform: 'capitalize',
                        }}
                      >{s}</button>
                    ))}
                  </div>

                  {hrChangeRequestsError && (
                    <div style={{ margin: '0 24px 12px', padding: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#991b1b', fontSize: '0.86rem' }}>
                      {hrChangeRequestsError}
                    </div>
                  )}

                  {hrChangeRequestsLoading ? (
                    <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading…</div>
                  ) : hrChangeRequests.length === 0 ? (
                    <div style={{ padding: 60, margin: '0 24px', textAlign: 'center', color: '#94a3b8', border: '1px dashed var(--line)', borderRadius: 8 }}>
                      {hrChangeRequestsTab === 'pending' ? 'No pending requests.' : `No ${hrChangeRequestsTab} requests.`}
                    </div>
                  ) : (
                    <div style={{ padding: '0 24px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {hrChangeRequests.map((cr: any) => {
                        const acting = hrChangeRequestActing === cr.id
                        const humanize = (k: string) => String(k || '').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                        const fieldLabel = humanize(String(cr.field_key).split('.').pop() || cr.field_key)
                        const sectionLabel = humanize(cr.section)
                        return (
                          <div key={cr.id} style={{ background: '#fff', border: '1px solid var(--line)', borderRadius: 8, padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
                              <div>
                                <div style={{ fontWeight: 600, fontSize: '0.92rem', color: '#0f172a' }}>{cr.employee_name || cr.employee_email}</div>
                                <div style={{ fontSize: '0.74rem', color: '#64748b' }}>{cr.employee_email}</div>
                              </div>
                              <span style={{ fontSize: '0.72rem', color: '#64748b' }}>{new Date(cr.created_at).toLocaleString()}</span>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, padding: '8px 12px', background: '#f8fafc', borderRadius: 6 }}>
                              <div>
                                <div style={{ fontSize: '0.68rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{sectionLabel} · {fieldLabel}</div>
                                <div style={{ fontSize: '0.82rem', color: '#94a3b8', marginTop: 2, textDecoration: 'line-through' }}>{cr.current_value || '—'}</div>
                              </div>
                              <div>
                                <div style={{ fontSize: '0.68rem', fontWeight: 600, color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Requested</div>
                                <div style={{ fontSize: '0.86rem', color: '#0f172a', marginTop: 2, fontWeight: 600 }}>{cr.requested_value}</div>
                              </div>
                            </div>
                            {cr.reason && (
                              <div style={{ fontSize: '0.78rem', color: '#475569', fontStyle: 'italic' }}>
                                "{cr.reason}"
                              </div>
                            )}
                            {cr.status === 'pending' && (
                              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                <button
                                  disabled={acting}
                                  className="bp-btn bp-btn-secondary"
                                  style={{ fontSize: '0.78rem', padding: '5px 12px', color: '#b91c1c' }}
                                  onClick={() => {
                                    setHrDenyNote('')
                                    setHrDenyModal({
                                      id: cr.id,
                                      employeeName: cr.employee_name || cr.employee_email,
                                      field: `${sectionLabel} · ${fieldLabel}`,
                                      requestedValue: String(cr.requested_value),
                                    })
                                  }}
                                >{acting ? '…' : 'Deny'}</button>
                                <button
                                  disabled={acting}
                                  className="bp-btn bp-btn-primary"
                                  style={{ fontSize: '0.78rem', padding: '5px 12px' }}
                                  onClick={async () => {
                                    setHrChangeRequestActing(cr.id)
                                    try {
                                      const r = await fetch(`/api/hr-admin/change-requests/${cr.id}/approve`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) })
                                      if (!r.ok) throw new Error(`HTTP ${r.status}`)
                                      await loadHrChangeRequests(hrChangeRequestsTab)
                                    } catch (e: any) {
                                      alert(e?.message || 'Failed to approve')
                                    } finally {
                                      setHrChangeRequestActing(null)
                                    }
                                  }}
                                >{acting ? '…' : 'Approve'}</button>
                              </div>
                            )}
                            {cr.status !== 'pending' && (
                              <div style={{ fontSize: '0.72rem', color: '#64748b', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                                <span>
                                  <strong style={{ textTransform: 'capitalize', color: cr.status === 'approved' ? '#15803d' : cr.status === 'denied' ? '#b91c1c' : '#94a3b8' }}>{cr.status}</strong>
                                  {cr.reviewer_email ? ` by ${cr.reviewer_email}` : ''}
                                  {cr.decided_at ? ` on ${new Date(cr.decided_at).toLocaleDateString()}` : ''}
                                </span>
                                {cr.review_note && <span style={{ fontStyle: 'italic' }}>"{cr.review_note}"</span>}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Deny-reason modal */}
              {hrDenyModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }} onClick={() => hrChangeRequestActing == null && setHrDenyModal(null)}>
                  <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 480, boxShadow: '0 20px 50px rgba(15,23,42,0.25)' }}>
                    <h3 style={{ margin: '0 0 4px', fontSize: '1rem', color: '#0f172a' }}>Deny change request</h3>
                    <p style={{ margin: '0 0 16px', fontSize: '0.82rem', color: '#64748b' }}>
                      Denying <strong>{hrDenyModal.employeeName}</strong>'s request for <strong>{hrDenyModal.field}</strong>.
                    </p>
                    <div style={{ marginBottom: 14, padding: '6px 10px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6 }}>
                      <div style={{ fontSize: '0.68rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>Requested value</div>
                      <div style={{ fontSize: '0.86rem', color: '#0f172a' }}>{hrDenyModal.requestedValue}</div>
                    </div>
                    <div style={{ marginBottom: 18 }}>
                      <label style={{ display: 'block', fontSize: '0.68rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Reason for denial</label>
                      <textarea
                        autoFocus
                        rows={3}
                        value={hrDenyNote}
                        onChange={e => setHrDenyNote(e.target.value)}
                        placeholder="Explain why this change is being denied. The employee will see this note."
                        style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: '0.86rem', resize: 'vertical' }}
                      />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                      <button className="bp-btn bp-btn-secondary" disabled={hrChangeRequestActing != null} onClick={() => setHrDenyModal(null)}>Cancel</button>
                      <button
                        className="bp-btn bp-btn-primary"
                        style={{ background: '#dc2626', borderColor: '#dc2626' }}
                        disabled={hrChangeRequestActing != null || !hrDenyNote.trim()}
                        onClick={async () => {
                          const id = hrDenyModal.id
                          setHrChangeRequestActing(id)
                          try {
                            const r = await fetch(`/api/hr-admin/change-requests/${id}/deny`, {
                              method: 'POST', credentials: 'include',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ note: hrDenyNote.trim() }),
                            })
                            if (!r.ok) throw new Error(`HTTP ${r.status}`)
                            setHrDenyModal(null)
                            await loadHrChangeRequests(hrChangeRequestsTab)
                          } catch (e: any) {
                            alert(e?.message || 'Failed to deny')
                          } finally {
                            setHrChangeRequestActing(null)
                          }
                        }}
                      >{hrChangeRequestActing != null ? 'Denying…' : 'Deny request'}</button>
                    </div>
                  </div>
                </div>
              )}

              {/* ===== Employer Signing Tab ===== */}
              {hrPortalView === 'employer-signing' && (() => {
                const pendingDocs = hrPortalPendingEmployer
                const selectedDoc = hrPortalSelectedSignDoc || pendingDocs[0] || null
                return (<>
                  <div className="hr-portal-section-header">
                    <div className="hr-portal-section-header-row">
                      <div>
                        <h1>Employer Signing</h1>
                        <p>Complete employer sections for signed W-4 and I-9 documents</p>
                      </div>
                      <button className="hr-portal-btn" style={{padding:'6px 14px',fontSize:'0.78rem',background:'var(--card)',border:'1px solid var(--line)',color:'var(--text)'}} onClick={() => setHrPortalView('assignments')}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5m7-7-7 7 7 7"/></svg>
                        Back to Assignments
                      </button>
                    </div>
                  </div>
                  {hrPortalPendingEmployerLoading ? (
                    <div className="hr-portal-loading">Loading pending documents...</div>
                  ) : pendingDocs.length === 0 ? (
                    <div className="hr-portal-empty">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" style={{color:'var(--line)'}}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                      <span style={{fontWeight:600,fontSize:'0.95rem',color:'var(--text)'}}>All caught up!</span>
                      <span>No pending employer sections</span>
                    </div>
                  ) : (
                    <div style={{display:'flex',gap:16,padding:'0 24px 16px',minHeight:0,flex:1}}>
                      {/* Left: Queue */}
                      <div style={{width:260,flexShrink:0,display:'flex',flexDirection:'column',gap:8}}>
                        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:4}}>
                          <span style={{fontSize:'0.82rem',fontWeight:700,color:'var(--text)'}}>Pending Queue</span>
                          <span style={{fontSize:'0.72rem',fontWeight:600,padding:'2px 8px',borderRadius:99,background: pendingDocs.length > 0 ? '#fff7ed' : '#f0fdf4', color: pendingDocs.length > 0 ? '#c2410c' : '#16a34a'}}>{pendingDocs.length} pending</span>
                        </div>
                        {pendingDocs.map((doc: any) => (
                          <button key={doc.id} onClick={() => setHrPortalSelectedSignDoc(doc)}
                            style={{width:'100%',textAlign:'left',borderRadius:10,border: selectedDoc?.id === doc.id ? '1.5px solid #f97316' : '1px solid var(--line)',background: selectedDoc?.id === doc.id ? '#fff7ed' : 'var(--card)',padding:'10px 12px',cursor:'pointer',transition:'all 0.15s'}}>
                            <div style={{display:'flex',alignItems:'start',gap:8}}>
                              <div style={{padding:4,borderRadius:6,marginTop:2,background: doc.document_type === 'i9' ? '#dbeafe' : '#f3e8ff'}}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={doc.document_type === 'i9' ? '#2563eb' : '#9333ea'} strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>
                              </div>
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{fontSize:'0.82rem',fontWeight:600,color:'var(--text)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{doc.template_title || doc.document_type?.toUpperCase()}</div>
                                <div style={{fontSize:'0.72rem',color:'var(--muted)',marginTop:2,display:'flex',alignItems:'center',gap:4}}>
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
                                  {doc.employee_name || 'Unknown'}
                                </div>
                                <div style={{fontSize:'0.65rem',color:'var(--muted)',marginTop:2}}>Signed {doc.signed_at ? new Date(doc.signed_at).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : '—'}</div>
                              </div>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2" style={{flexShrink:0,marginTop:2}}><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                            </div>
                          </button>
                        ))}
                      </div>
                      {/* Right: Selected doc details */}
                      <div style={{flex:1,minWidth:0,display:'flex',flexDirection:'column',gap:12}}>
                        {selectedDoc ? (<>
                          <div style={{background:'var(--card)',borderRadius:10,border:'1px solid var(--line)',padding:'14px 18px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                            <div>
                              <div style={{fontSize:'0.88rem',fontWeight:700,color:'var(--text)'}}>
                                {selectedDoc.template_title || selectedDoc.document_type?.toUpperCase()}
                                <span style={{marginLeft:8,fontSize:'0.72rem',fontWeight:500,padding:'2px 8px',borderRadius:99,background:'#fff7ed',color:'#c2410c',border:'1px solid #fed7aa'}}>Employer Section Pending</span>
                              </div>
                              <div style={{fontSize:'0.75rem',color:'var(--muted)',marginTop:4}}>
                                Employee: <span style={{fontWeight:600,color:'var(--text)'}}>{selectedDoc.employee_name || 'Unknown'}</span>
                              </div>
                            </div>
                            <div style={{display:'flex',gap:8,alignItems:'center'}}>
                              <button
                                className="hr-portal-btn"
                                disabled={hrEmployerCancelBusy}
                                style={{padding:'8px 14px',fontSize:'0.82rem',background:'#fef2f2',border:'1px solid #fecaca',color:'#b91c1c'}}
                                onClick={() => setHrPortalConfirmModal({
                                  title: 'Cancel document',
                                  message: `Remove this ${selectedDoc.template_title || selectedDoc.document_type?.toUpperCase()} for ${selectedDoc.employee_name || 'this employee'} from the employer queue? The employee's signed copy is discarded and they would need to be re-assigned the document if it's still required.`,
                                  destructive: true,
                                  onConfirm: async () => {
                                    setHrEmployerCancelBusy(true)
                                    try {
                                      await dismissEmployerDoc(selectedDoc.id)
                                      setHrPortalPendingEmployer(prev => prev.filter((d: any) => d.id !== selectedDoc.id))
                                      setHrPortalSelectedSignDoc(null)
                                      setHrEmployerPreviewUrl(null)
                                    } catch (e: any) {
                                      alert(e?.message || 'Failed to cancel document')
                                    } finally {
                                      setHrEmployerCancelBusy(false)
                                    }
                                  },
                                })}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                {hrEmployerCancelBusy ? 'Cancelling…' : 'Cancel Document'}
                              </button>
                              <button className="hr-portal-btn primary" style={{padding:'8px 18px',fontSize:'0.82rem'}} onClick={() => openHrEmployerSignModal(selectedDoc)}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>
                                Fill Employer Section
                              </button>
                            </div>
                          </div>
                          <div style={{flex:1,background:'#111827',borderRadius:10,display:'flex',alignItems:'center',justifyContent:'center',minHeight:300,overflow:'auto',padding:12}}>
                            {hrEmployerPreviewLoading ? (
                              <div style={{textAlign:'center',color:'#9ca3af'}}>
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="animate-pulse" style={{margin:'0 auto 12px'}}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>
                                <div style={{fontSize:'0.82rem'}}>Loading document preview...</div>
                              </div>
                            ) : hrEmployerPreviewUrl ? (
                              <img src={hrEmployerPreviewUrl} alt="Document preview" style={{maxWidth:'100%',maxHeight:'100%',borderRadius:6,boxShadow:'0 4px 16px rgba(0,0,0,0.3)'}} />
                            ) : (
                              <div style={{textAlign:'center',color:'#9ca3af'}}>
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" style={{margin:'0 auto 12px'}}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>
                                <div style={{fontSize:'0.82rem'}}>Preview unavailable</div>
                              </div>
                            )}
                          </div>
                        </>) : (
                          <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',background:'var(--card)',borderRadius:10,border:'1px solid var(--line)'}}>
                            <div style={{textAlign:'center',color:'var(--muted)'}}>
                              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" style={{margin:'0 auto 12px',color:'var(--line)'}}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>
                              <span style={{fontSize:'0.85rem'}}>Select a document from the queue</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>)
              })()}

              {hrPortalView === 'templates' && (
                <Suspense fallback={PrefsLazyFallback}>
                  <HrTemplates
                    templates={hrPortalTemplates}
                    setTemplates={setHrPortalTemplates}
                    templatesLoading={hrPortalTemplatesLoading}
                    search={hrPortalTplSearch}
                    setSearch={setHrPortalTplSearch}
                    catFilter={hrPortalTplCatFilter}
                    setCatFilter={setHrPortalTplCatFilter}
                    previews={hrPortalTplPreviews}
                    uploadOpen={hrPortalTemplateUploadOpen}
                    setUploadOpen={setHrPortalTemplateUploadOpen}
                    onOpenFieldEditor={(id) => openHrFieldEditor(id)}
                  />
                </Suspense>
              )}

              {/* ===== Settings Tab (Stacked Sections — matches standalone portal) ===== */}
              {hrPortalView === 'settings' && (
                <Suspense fallback={PrefsLazyFallback}>
                  <HrSettings
                    HR_FIELD_CATALOG={HR_FIELD_CATALOG}
                    HR_FIELD_SECTIONS={HR_FIELD_SECTIONS}
                    loading={hrPortalSettingsLoading}
                    admins={hrPortalAdmins}
                    setAdmins={setHrPortalAdmins}
                    allEmployees={hrPortalAllEmployees}
                    addAdminOpen={hrPortalAddAdminOpen}
                    setAddAdminOpen={setHrPortalAddAdminOpen}
                    addAdminSearch={hrPortalAddAdminSearch}
                    setAddAdminSearch={setHrPortalAddAdminSearch}
                    crmUsers={hrPortalCrmUsers}
                    setCrmUsers={setHrPortalCrmUsers}
                    crmUsersOpen={hrPortalCrmUsersOpen}
                    setCrmUsersOpen={setHrPortalCrmUsersOpen}
                    crmUsersSearch={hrPortalCrmUsersSearch}
                    setCrmUsersSearch={setHrPortalCrmUsersSearch}
                    crmUsersModal={hrPortalCrmUsersModal}
                    setCrmUsersModal={setHrPortalCrmUsersModal}
                    editCrmUser={hrPortalEditCrmUser}
                    setEditCrmUser={setHrPortalEditCrmUser}
                    deactivateUser={hrPortalDeactivateUser}
                    setDeactivateUser={setHrPortalDeactivateUser}
                    deactivateReassignTo={hrPortalDeactivateReassignTo}
                    setDeactivateReassignTo={setHrPortalDeactivateReassignTo}
                    deactivateReassignSearch={hrPortalDeactivateReassignSearch}
                    setDeactivateReassignSearch={setHrPortalDeactivateReassignSearch}
                    deactivateError={hrPortalDeactivateError}
                    setDeactivateError={setHrPortalDeactivateError}
                    deactivateSaving={hrPortalDeactivateSaving}
                    setDeactivateSaving={setHrPortalDeactivateSaving}
                    employerSignature={hrEmployerSignature}
                    setEmployerSignature={setHrEmployerSignature}
                    employerInitials={hrEmployerInitials}
                    setEmployerInitials={setHrEmployerInitials}
                    onOpenSignaturePad={(kind) => {
                      setHrSignActiveField({type: kind, id: kind === 'signature' ? 'employer-sig' : 'employer-init'} as any)
                      setHrSignPopupOpen(true)
                      setHrSignPopupTab('draw')
                    }}
                    templates={hrPortalTemplates}
                    setTemplates={setHrPortalTemplates}
                    automationTab={hrPortalAutomationTab}
                    setAutomationTab={setHrPortalAutomationTab}
                    showAddDoc={hrPortalShowAddDoc}
                    setShowAddDoc={setHrPortalShowAddDoc}
                    togglingTemplate={hrPortalTogglingTemplate}
                    setTogglingTemplate={setHrPortalTogglingTemplate}
                    emailTemplates={hrPortalEmailTemplates}
                    setEmailTemplates={setHrPortalEmailTemplates}
                    expandedEmail={hrPortalExpandedEmail}
                    setExpandedEmail={setHrPortalExpandedEmail}
                    editingEmail={hrPortalEditingEmail}
                    setEditingEmail={setHrPortalEditingEmail}
                    savingEmail={hrPortalSavingEmail}
                    setSavingEmail={setHrPortalSavingEmail}
                    editEmailSubject={hrPortalEditEmailSubject}
                    setEditEmailSubject={setHrPortalEditEmailSubject}
                    editEmailBody={hrPortalEditEmailBody}
                    setEditEmailBody={setHrPortalEditEmailBody}
                    emailSettings={hrPortalEmailSettings}
                    setEmailSettings={setHrPortalEmailSettings}
                    emailStatsDays={hrPortalEmailStatsDays}
                    setEmailStatsDays={setHrPortalEmailStatsDays}
                    fieldAlertForm={hrPortalFieldAlertForm}
                    setFieldAlertForm={setHrPortalFieldAlertForm}
                    fieldAlertError={hrPortalFieldAlertError}
                    setFieldAlertError={setHrPortalFieldAlertError}
                    fieldAlertSaving={hrPortalFieldAlertSaving}
                    setFieldAlertSaving={setHrPortalFieldAlertSaving}
                    fieldRules={hrPortalFieldRules}
                    setFieldRules={setHrPortalFieldRules}
                    positions={hrPortalPositions}
                  />
                </Suspense>
              )}
            </div>
          </div>
        )}

        {/* ===== CUSTOM CONFIRM MODAL ===== */}
        {hrPortalConfirmModal && (
          <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',zIndex:10000,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={() => setHrPortalConfirmModal(null)}>
            <div style={{background:'#fff',borderRadius:12,padding:24,width:400,maxWidth:'90vw',boxShadow:'0 12px 40px rgba(0,0,0,0.2)'}} onClick={e => e.stopPropagation()}>
              <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:16}}>
                <div style={{width:40,height:40,borderRadius:10,background: hrPortalConfirmModal.destructive ? '#fef2f2' : '#eff6ff',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                  {hrPortalConfirmModal.destructive ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  )}
                </div>
                <div>
                  <h3 style={{margin:0,fontSize:'1rem',fontWeight:700,color:'#1e293b'}}>{hrPortalConfirmModal.title}</h3>
                </div>
              </div>
              <p style={{fontSize:'0.85rem',color:'#6b7280',lineHeight:1.6,margin:'0 0 20px'}}>{hrPortalConfirmModal.message}</p>
              <div style={{display:'flex',justifyContent:'flex-end',gap:8}}>
                <button onClick={() => setHrPortalConfirmModal(null)}
                  style={{padding:'8px 20px',borderRadius:8,border:'1px solid #d1d5db',background:'#fff',color:'#374151',fontSize:'0.82rem',fontWeight:600,cursor:'pointer'}}>Cancel</button>
                <button onClick={async () => { await hrPortalConfirmModal.onConfirm(); setHrPortalConfirmModal(null) }}
                  style={{padding:'8px 20px',borderRadius:8,border:'none',background: hrPortalConfirmModal.destructive ? '#dc2626' : '#2563eb',color:'#fff',fontSize:'0.82rem',fontWeight:600,cursor:'pointer'}}>
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ===== Document Upload Type Modal ===== */}
        {hrDocUploadModal && (
          <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',zIndex:10000,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={() => { if (!hrDocUploading) { setHrDocUploadModal(false); setHrDocUploadFile(null) } }}>
            <div style={{background:'#fff',borderRadius:12,padding:24,width:420,maxWidth:'90vw',boxShadow:'0 12px 40px rgba(0,0,0,0.2)'}} onClick={e => e.stopPropagation()}>
              <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:16}}>
                <div style={{width:40,height:40,borderRadius:10,background:'#eff6ff',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                </div>
                <h3 style={{margin:0,fontSize:'1rem',fontWeight:700,color:'#1e293b'}}>Upload Document</h3>
              </div>
              {/* Step 1: Select type */}
              <div style={{fontSize:'0.82rem',fontWeight:600,color:'#374151',marginBottom:8}}>1. Select document type</div>
              <select value={hrDocUploadType} onChange={e => setHrDocUploadType(e.target.value)}
                style={{width:'100%',padding:'8px 12px',borderRadius:6,border:'1px solid #d1d5db',fontSize:'0.82rem',marginBottom:12,boxSizing:'border-box',background:'#fff',color:'#1e293b'}}>
                <option value="">Select a type...</option>
                <option value="ufCrm6Resume">Resume</option>
                <option value="ufCrm_6_UF_W4_FILE">W-4</option>
                <option value="ufCrm_6_UF_I9_FILE">I-9</option>
                <option value="ufCrm6DirectDeposit">Direct Deposit Form</option>
                <option value="ufCrm6W9">W-9</option>
                <option value="ufCrm6HandbookAck">Handbook Acknowledgment</option>
                <option value="ufCrm6WorkVisa">Work Visa</option>
                <option value="ufCrm6UfUsr1747966315398">Driver's License</option>
                <option value="ufCrm6UfUsr1737120327618">Auto Insurance</option>
                <option value="__other__">Other</option>
              </select>
              {hrDocUploadType === '__other__' && (
                <input type="text" placeholder="Document name (e.g. Background Check)" value={hrDocUploadCustom} onChange={e => setHrDocUploadCustom(e.target.value)}
                  style={{width:'100%',padding:'8px 12px',borderRadius:6,border:'1px solid #d1d5db',fontSize:'0.82rem',marginBottom:12,boxSizing:'border-box'}}/>
              )}
              {/* Step 2: Choose file (only after type selected) */}
              {(hrDocUploadType && (hrDocUploadType !== '__other__' || hrDocUploadCustom.trim())) && (
                <div style={{marginBottom:12}}>
                  <div style={{fontSize:'0.82rem',fontWeight:600,color:'#374151',marginBottom:8}}>2. Choose file</div>
                  {hrDocUploadFile ? (
                    <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:6}}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2"><path d="M20 6L9 17l-5-5"/></svg>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:'0.78rem',fontWeight:600,color:'#1e293b',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{hrDocUploadFile.name}</div>
                        <div style={{fontSize:'0.65rem',color:'#94a3b8'}}>{(hrDocUploadFile.size / 1024).toFixed(0)} KB</div>
                      </div>
                      <button onClick={() => setHrDocUploadFile(null)} style={{padding:'2px 8px',borderRadius:4,border:'1px solid #d1d5db',background:'#fff',color:'#6b7280',fontSize:'0.65rem',cursor:'pointer'}}>Change</button>
                    </div>
                  ) : (
                    <label style={{display:'flex',alignItems:'center',justifyContent:'center',gap:8,padding:'16px',borderRadius:8,border:'2px dashed #cbd5e1',background:'#fafbfc',color:'#64748b',fontSize:'0.78rem',fontWeight:500,cursor:'pointer',transition:'all 0.15s'}}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                      Click to select file
                      <input type="file" hidden onChange={e => { const f = e.target.files?.[0]; if (f) setHrDocUploadFile(f); e.target.value = '' }}/>
                    </label>
                  )}
                </div>
              )}
              <div style={{display:'flex',justifyContent:'flex-end',gap:8}}>
                <button disabled={hrDocUploading} onClick={() => { setHrDocUploadModal(false); setHrDocUploadFile(null) }}
                  style={{padding:'8px 20px',borderRadius:8,border:'1px solid #d1d5db',background:'#fff',color:'#374151',fontSize:'0.82rem',fontWeight:600,cursor:'pointer',opacity:hrDocUploading?0.6:1}}>Cancel</button>
                <button disabled={hrDocUploading || !hrDocUploadFile || !hrDocUploadType || (hrDocUploadType === '__other__' && !hrDocUploadCustom.trim())} onClick={async () => {
                  const fieldKey = hrDocUploadType === '__other__' ? 'ufCrm6OtherDocs' : hrDocUploadType
                  const docLabel = hrDocUploadType === '__other__' ? hrDocUploadCustom.trim() : undefined
                  if (!fieldKey || !hrPortalSelectedId || !hrDocUploadFile) return
                  setHrDocUploading(true)
                  try {
                    await uploadHrAdminFile(hrPortalSelectedId, fieldKey, hrDocUploadFile, docLabel)
                    const r = await fetchHrAdminEmployeeDocuments(hrPortalSelectedId)
                    if (r?.success) setHrPortalDocs(r.data)
                    setHrDocUploadModal(false); setHrDocUploadFile(null)
                  } catch (err: any) {
                    alert('Upload failed: ' + err.message)
                  } finally { setHrDocUploading(false) }
                }} style={{padding:'8px 20px',borderRadius:8,border:'none',background:'#2563eb',color:'#fff',fontSize:'0.82rem',fontWeight:600,cursor: (hrDocUploading || !hrDocUploadFile || !hrDocUploadType) ? 'default' : 'pointer',opacity:(hrDocUploading || !hrDocUploadFile || !hrDocUploadType || (hrDocUploadType === '__other__' && !hrDocUploadCustom.trim()))?0.5:1}}>
                  {hrDocUploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ===== IT PROVISIONING MODAL (kanban drop intercept) ===== */}
        {itProvModal && (
          <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',zIndex:10000,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={() => setItProvModal(null)}>
            <div style={{background:'#fff',borderRadius:14,width:480,maxWidth:'92vw',maxHeight:'90vh',overflow:'auto',boxShadow:'0 16px 48px rgba(0,0,0,0.22)'}} onClick={e => e.stopPropagation()}>
              <div style={{padding:'20px 24px 0',display:'flex',alignItems:'center',gap:12}}>
                <div style={{width:40,height:40,borderRadius:10,background:'#e0f2fe',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
                </div>
                <div>
                  <h3 style={{margin:0,fontSize:'1.05rem',fontWeight:700,color:'#1e293b'}}>IT Provisioning</h3>
                  <p style={{margin:0,fontSize:'0.78rem',color:'#64748b'}}>Set up email and devices for this employee</p>
                </div>
                <button onClick={() => setItProvModal(null)} style={{marginLeft:'auto',background:'none',border:'none',cursor:'pointer',padding:4,color:'#94a3b8',fontSize:'1.2rem',lineHeight:1}}>×</button>
              </div>
              <div style={{padding:'20px 24px',display:'flex',flexDirection:'column',gap:20}}>
                {/* Company email toggle */}
                <div>
                  <label style={{display:'flex',alignItems:'center',gap:12,cursor:'pointer',userSelect:'none'}}>
                    <span style={{position:'relative',display:'inline-block',width:40,height:22}}>
                      <input type="checkbox" checked={itProvForm.needsEmail} onChange={e => setItProvForm(f => ({...f, needsEmail: e.target.checked}))} style={{opacity:0,width:0,height:0}} />
                      <span style={{position:'absolute',inset:0,background: itProvForm.needsEmail ? '#0284c7' : '#cbd5e1',borderRadius:22,transition:'120ms',cursor:'pointer'}}/>
                      <span style={{position:'absolute',top:2,left: itProvForm.needsEmail ? 20 : 2,width:18,height:18,background:'#fff',borderRadius:'50%',transition:'120ms',boxShadow:'0 1px 3px rgba(0,0,0,0.2)'}}/>
                    </span>
                    <span style={{fontSize:'0.88rem',fontWeight:600,color:'#1e293b'}}>Company email</span>
                    <span style={{fontSize:'0.72rem',color:'#64748b',marginLeft:'auto'}}>(default: yes)</span>
                  </label>
                </div>
                {/* Email prefix */}
                {itProvForm.needsEmail && (
                  <div>
                    <label style={{display:'block',fontSize:'0.72rem',fontWeight:600,color:'#475569',marginBottom:6,textTransform:'uppercase',letterSpacing:'0.04em'}}>BotPros email</label>
                    <div style={{display:'flex',alignItems:'stretch',border:'1px solid #cbd5e1',borderRadius:6,overflow:'hidden',background:'#fff'}}>
                      <input type="text" value={itProvForm.emailPrefix} placeholder={itProvModal.firstName || 'firstname'}
                        style={{flex:1,border:'none',outline:'none',padding:'10px 12px',fontSize:'0.92rem',color:'#1e293b'}}
                        onChange={e => {
                          const v = e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g,'')
                          setItProvForm(f => ({...f, emailPrefix: v}))
                        }}/>
                      <span style={{display:'flex',alignItems:'center',padding:'0 12px',background:'#f1f5f9',color:'#64748b',fontSize:'0.88rem',borderLeft:'1px solid #e2e8f0'}}>@botpros.ai</span>
                    </div>
                  </div>
                )}
                {/* Devices */}
                <div>
                  <label style={{display:'block',fontSize:'0.72rem',fontWeight:600,color:'#475569',marginBottom:10,textTransform:'uppercase',letterSpacing:'0.04em'}}>Devices</label>
                  <div style={{display:'flex',flexDirection:'column',gap:8}}>
                    {/* Tablet */}
                    <div style={{padding:'10px 14px',border:'1px solid #e2e8f0',borderRadius:8,background: itProvForm.tablet ? '#f8fafc' : '#fff'}}>
                      <label style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer'}}>
                        <input type="checkbox" checked={itProvForm.tablet} onChange={e => setItProvForm(f => ({...f, tablet: e.target.checked}))} style={{accentColor:'#0284c7'}} />
                        <span style={{fontSize:'0.88rem',fontWeight: itProvForm.tablet ? 600 : 500,color:'#1e293b'}}>Tablet</span>
                      </label>
                      {itProvForm.tablet && (
                        <div style={{display:'flex',gap:6,marginTop:8,marginLeft:26}}>
                          {['iPad','Samsung'].map(t => (
                            <button key={t} type="button" onClick={() => setItProvForm(f => ({...f, tabletType: t}))} style={{
                              padding:'5px 14px',borderRadius:6,fontSize:'0.78rem',fontWeight:600,cursor:'pointer',
                              border: `1.5px solid ${itProvForm.tabletType === t ? '#0284c7' : '#cbd5e1'}`,
                              background: itProvForm.tabletType === t ? '#e0f2fe' : '#fff',
                              color: itProvForm.tabletType === t ? '#0369a1' : '#475569',
                            }}>{t}</button>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Cell Phone */}
                    <div style={{padding:'10px 14px',border:'1px solid #e2e8f0',borderRadius:8,background: itProvForm.phone ? '#f8fafc' : '#fff'}}>
                      <label style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer'}}>
                        <input type="checkbox" checked={itProvForm.phone} onChange={e => setItProvForm(f => ({...f, phone: e.target.checked}))} style={{accentColor:'#0284c7'}} />
                        <span style={{fontSize:'0.88rem',fontWeight: itProvForm.phone ? 600 : 500,color:'#1e293b'}}>Cell Phone</span>
                      </label>
                      {itProvForm.phone && (
                        <div style={{display:'flex',gap:6,marginTop:8,marginLeft:26}}>
                          {['iPhone','Samsung'].map(t => (
                            <button key={t} type="button" onClick={() => setItProvForm(f => ({...f, phoneType: t}))} style={{
                              padding:'5px 14px',borderRadius:6,fontSize:'0.78rem',fontWeight:600,cursor:'pointer',
                              border: `1.5px solid ${itProvForm.phoneType === t ? '#0284c7' : '#cbd5e1'}`,
                              background: itProvForm.phoneType === t ? '#e0f2fe' : '#fff',
                              color: itProvForm.phoneType === t ? '#0369a1' : '#475569',
                            }}>{t}</button>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Laptop */}
                    <div style={{padding:'10px 14px',border:'1px solid #e2e8f0',borderRadius:8,background: itProvForm.laptop ? '#f8fafc' : '#fff'}}>
                      <label style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer'}}>
                        <input type="checkbox" checked={itProvForm.laptop} onChange={e => setItProvForm(f => ({...f, laptop: e.target.checked}))} style={{accentColor:'#0284c7'}} />
                        <span style={{fontSize:'0.88rem',fontWeight: itProvForm.laptop ? 600 : 500,color:'#1e293b'}}>Laptop</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              {/* Footer */}
              <div style={{padding:'16px 24px',borderTop:'1px solid #e2e8f0',display:'flex',justifyContent:'flex-end',gap:8}}>
                <button onClick={() => setItProvModal(null)} style={{padding:'9px 20px',borderRadius:8,border:'1px solid #d1d5db',background:'#fff',color:'#374151',fontSize:'0.82rem',fontWeight:600,cursor:'pointer'}}>Cancel</button>
                <button
                  onClick={async () => {
                    try {
                      await updateHrAdminProfileStage(itProvModal.profileId, itProvModal.stageId)
                      fetchHrAdminProfiles(hrPortalActivePipeline).then((r: any) => {
                        if (r?.success) setHrPortalProfiles(r.data.profiles)
                      })
                    } catch {}
                    setItProvModal(null)
                  }}
                  style={{padding:'9px 20px',borderRadius:8,border:'1px solid #cbd5e1',background:'#f1f5f9',color:'#475569',fontSize:'0.82rem',fontWeight:600,cursor:'pointer'}}
                >
                  Skip & Move
                </button>
                <button onClick={async () => {
                  const form = itProvForm
                  const devices: any = {}
                  if (form.tablet) devices.tablet = form.tabletType
                  if (form.phone) devices.phone = form.phoneType
                  if (form.laptop) devices.computer = 'new'
                  const itData = { needsEmail: form.needsEmail, emailPrefix: form.needsEmail ? (form.emailPrefix || itProvModal.firstName) : '', devices }
                  try {
                    await fetch(`/api/hr/employees/${itProvModal.profileId}`, {
                      method: 'PUT', credentials: 'include',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ itProvisioning: itData }),
                    })
                    await updateHrAdminProfileStage(itProvModal.profileId, itProvModal.stageId)
                    fetchHrAdminProfiles(hrPortalActivePipeline).then((r: any) => {
                      if (r?.success) setHrPortalProfiles(r.data.profiles)
                    })
                    setItProvModal(null)
                  } catch {}
                }} style={{padding:'9px 20px',borderRadius:8,border:'none',background:'#0284c7',color:'#fff',fontSize:'0.82rem',fontWeight:600,cursor:'pointer',display:'inline-flex',alignItems:'center',gap:8}}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                  Submit &amp; Move to IT Provisioning
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ===== DOCUMENT SIGNING MODAL (ported from NativeSignatureModal) ===== */}
        {hrSignModalOpen && (() => {
          // advanceToNextField: find next unfilled field, scroll to it, open it (ported exactly from NativeSignatureModal)
          const advanceToNextField = (mapToUse: Map<string, any>, currentFieldId?: string, onlyRequired: boolean = false) => {
            const allSignerFields = hrSignConvertedFields.filter((f: any) =>
              (f.signerIndex === undefined || f.signerIndex === hrSignCurrentSigner)
            ).sort((a: any, b: any) => {
              if (a.page !== b.page) return a.page - b.page
              if (Math.abs(a.y - b.y) > 3) return a.y - b.y
              return a.x - b.x
            })
            let targetField: any = null
            if (currentFieldId) {
              const currentIndex = allSignerFields.findIndex((f: any) => f.id === currentFieldId)
              if (currentIndex !== -1) {
                for (let i = currentIndex + 1; i < allSignerFields.length; i++) {
                  if (!mapToUse.has(allSignerFields[i].id) && (!onlyRequired || allSignerFields[i].required)) { targetField = allSignerFields[i]; break }
                }
                if (!targetField) {
                  for (let i = 0; i < currentIndex; i++) {
                    if (!mapToUse.has(allSignerFields[i].id) && (!onlyRequired || allSignerFields[i].required)) { targetField = allSignerFields[i]; break }
                  }
                }
              }
            }
            if (!targetField) {
              targetField = allSignerFields.find((f: any) => !mapToUse.has(f.id) && (!onlyRequired || f.required)) || null
            }
            if (!targetField || targetField.id === currentFieldId) return
            const el = document.getElementById(`hr-sign-field-${targetField.id}`)
            if (el) {
              const scrollContainer = el.closest('.hr-sign-pdf-area')
              if (scrollContainer) {
                const elRect = el.getBoundingClientRect()
                const containerRect = scrollContainer.getBoundingClientRect()
                const scrollTop = scrollContainer.scrollTop + (elRect.top - containerRect.top) - containerRect.height / 3
                scrollContainer.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' })
              }
            }
            setTimeout(() => handleSignFieldClick(targetField, true), 150)
          }
          // Field click handler (ported from NativeSignatureModal.handleFieldClick)
          const handleSignFieldClick = (field: any, advance: boolean = false) => {
            if (field.staticValue) { if (advance) advanceToNextField(hrSignFilledFields, field.id); return }
            const isOtherSigner = field.signerIndex !== undefined && field.signerIndex !== hrSignCurrentSigner
            if (isOtherSigner) return
            const isFilled = hrSignFilledFields.has(field.id)
            // Checkbox: toggle directly, enforce checkboxGroupMax
            if (field.type === 'checkbox') {
              const newMap = new Map(hrSignFilledFields)
              if (isFilled) newMap.delete(field.id)
              else {
                if (field.checkboxGroup) {
                  const grpFields = hrSignConvertedFields.filter((f: any) => f.checkboxGroup === field.checkboxGroup)
                  const maxAllowed = grpFields[0]?.checkboxGroupMax ?? 0
                  if (maxAllowed > 0) {
                    const checkedIds = grpFields.filter((f: any) => f.id !== field.id && newMap.has(f.id)).map((f: any) => f.id)
                    while (checkedIds.length >= maxAllowed) { newMap.delete(checkedIds.shift()!) }
                  }
                }
                newMap.set(field.id, { fieldId: field.id, type: 'checkbox', data: 'true', label: field.label })
              }
              setHrSignFilledFields(newMap)
              if (advance && !isFilled) advanceToNextField(newMap, field.id)
              return
            }
            // Text/Date: inline editing
            if (field.type === 'text' || field.type === 'date') {
              let existingVal = hrSignFilledFields.get(field.id)?.data ?? ''
              if (!existingVal && field.type === 'date' && field.defaultToToday) {
                const t = new Date()
                existingVal = `${String(t.getMonth()+1).padStart(2,'0')}/${String(t.getDate()).padStart(2,'0')}/${t.getFullYear()}`
              }
              setHrSignActiveField(field)
              setHrSignEditingTextField(field.id)
              setHrSignEditingTextValue(existingVal)
              return
            }
            // Signature / Initials: open popup
            if (field.type === 'signature' || field.type === 'initials') {
              setHrSignActiveField(field)
              setHrSignTempSig(null)
              setHrSignSelectedFont(-1)
              setHrSignUploadedSig(null)
              setHrSignPopupTab('style')
              setHrSignPopupOpen(true)
            }
          }
          // Save inline text (ported from NativeSignatureModal.handleSaveInlineText)
          const handleSaveInlineText = (fieldId: string, field: any, advance: boolean = false) => {
            if (hrSignEditingTextField !== fieldId) return
            const trimmed = hrSignEditingTextValue.trim()
            if (advance && field.required && !trimmed) {
              setHrSignError('This field is required. Please enter a value.')
              return
            }
            const valToSave = (!trimmed && field.type === 'date' && field.defaultToToday)
              ? (() => { const t = new Date(); return `${t.getMonth()+1}/${t.getDate()}/${t.getFullYear()}` })()
              : trimmed
            const newMap = new Map(hrSignFilledFields)
            if (valToSave) newMap.set(fieldId, { fieldId, type: field.type, data: valToSave, label: field.label })
            else newMap.delete(fieldId)
            setHrSignFilledFields(newMap)
            setHrSignEditingTextField(null)
            setHrSignEditingTextValue('')
            if (advance) advanceToNextField(newMap, fieldId)
          }
          // Submit handler (ported from NativeSignatureModal.handleSubmit)
          const handleSignSubmit = async () => {
            const reqFields = hrSignConvertedFields.filter((f: any) =>
              f.required !== false && !(f.type === 'checkbox' && f.checkboxGroup) &&
              (f.signerIndex === undefined || f.signerIndex === hrSignCurrentSigner)
            )
            if (!reqFields.every((f: any) => hrSignFilledFields.has(f.id))) {
              setHrSignError('Please fill all required fields before submitting')
              return
            }
            // Validate checkbox groups (ported from NativeSignatureModal)
            const signerCbFields = hrSignConvertedFields.filter((f: any) => f.type === 'checkbox' && f.checkboxGroup && (f.signerIndex === undefined || f.signerIndex === hrSignCurrentSigner))
            const groups = Array.from(new Set(signerCbFields.map((f: any) => f.checkboxGroup)))
            for (const group of groups) {
              const grpFields = signerCbFields.filter((f: any) => f.checkboxGroup === group)
              const minReq = grpFields[0]?.checkboxGroupMin ?? 1
              if (minReq === 0) continue
              const checked = grpFields.filter((f: any) => hrSignFilledFields.has(f.id)).length
              if (checked < minReq) {
                setHrSignError(`Please select at least ${minReq} option in the "${group}" group`)
                return
              }
              const maxAllowed = grpFields[0]?.checkboxGroupMax ?? 0
              if (maxAllowed > 0 && checked > maxAllowed) {
                setHrSignError(`Please select at most ${maxAllowed} option in the "${group}" group`)
                return
              }
            }
            setHrSignSubmitting(true)
            setHrSignError(null)
            try {
              // Collect signature/initials fields for current signer (use parsedFields for raw coords)
              const sigFields = hrSignParsedFields.filter((f: any) =>
                (f.type === 'signature' || f.type === 'initials') &&
                (f.signerIndex === undefined || f.signerIndex === hrSignCurrentSigner) &&
                hrSignFilledFields.has(f.id || `field-${hrSignParsedFields.indexOf(f)}`)
              )
              let sigDataUrl = ''
              if (sigFields.length > 0) {
                const sigId = sigFields[0].id || `field-${hrSignParsedFields.indexOf(sigFields[0])}`
                const sigData = hrSignFilledFields.get(sigId)
                if (sigData) sigDataUrl = sigData.data
              }
              // Check if signer has signature fields assigned and none are filled
              const signerHasSigFields = hrSignParsedFields.some((f: any) =>
                (f.type === 'signature' || f.type === 'initials') && (f.signerIndex === undefined || f.signerIndex === hrSignCurrentSigner)
              )
              if (signerHasSigFields && sigFields.length === 0) throw new Error('At least one signature is required for your role')
              // Convert sig fields to PDF coords with pageWidth/pageHeight
              const pdfSigFields = sigFields.map((f: any) => ({
                page: f.page, x: Math.round(f.x), y: Math.round(f.y),
                width: Math.round(f.width), height: Math.round(f.height),
                pageWidth: f.pageWidth, pageHeight: f.pageHeight,
              }))
              // Text fields
              const pdfTextFields = hrSignParsedFields
                .filter((f: any) => (f.type === 'text' || f.type === 'date') && hrSignFilledFields.has(f.id || `field-${hrSignParsedFields.indexOf(f)}`))
                .map((f: any) => {
                  const id = f.id || `field-${hrSignParsedFields.indexOf(f)}`
                  return { page: f.page || f.pageNumber || 1, x: Math.round(f.x), y: Math.round(f.y), width: Math.round(f.width), height: Math.round(f.height), text: String(hrSignFilledFields.get(id)?.data ?? ''), pageWidth: f.pageWidth, pageHeight: f.pageHeight }
                })
              // Checkbox fields
              const pdfCbFields = hrSignParsedFields
                .filter((f: any) => f.type === 'checkbox' && hrSignFilledFields.has(f.id || `field-${hrSignParsedFields.indexOf(f)}`))
                .map((f: any) => ({ page: f.page || f.pageNumber || 1, x: Math.round(f.x), y: Math.round(f.y), width: Math.round(f.width), height: Math.round(f.height), checked: true, pageWidth: f.pageWidth, pageHeight: f.pageHeight }))

              const signPayload = {
                assignmentId: Number(hrSignAssignmentId),
                signatureDataUrl: sigDataUrl,
                signatureFields: pdfSigFields,
                textFields: pdfTextFields,
                checkboxFields: pdfCbFields,
                currentSignerIndex: hrSignCurrentSigner,
              }
              const result = hrEmployerSignSubmitDocId
                ? await submitHrAdminEmployerSign(String(hrEmployerSignSubmitDocId), signPayload)
                : hrSignMode === 'employee'
                  ? await signMyDocument(signPayload)
                  : await signHrAdminDocument(signPayload)
              if (result?.success) {
                setHrSignSuccess(true)
                setTimeout(() => {
                  setHrSignModalOpen(false)
                  setHrSignSuccess(false)
                  setHrSignFilledFields(new Map())
                  setHrEmployerSignSubmitDocId(null)
                  if (hrPortalView === 'employer-signing') {
                    fetchHrAdminPendingEmployer().then((r: any) => {
                      if (r?.success) setHrPortalPendingEmployer(r.data.documents || [])
                    })
                  } else if (hrPortalView === 'assignments') {
                    fetchHrAdminAssignments(hrPortalAssignmentFilter2 || undefined).then((r: any) => {
                      if (r?.success) setHrPortalAssignmentsList(r.data.assignments)
                    })
                  }
                }, 2000)
              }
            } catch (e: any) {
              setHrSignError(e.message || 'Failed to sign document')
            } finally {
              setHrSignSubmitting(false)
            }
          }
          // Check all required filled (excluding checkbox groups — validated separately)
          const allReqFilled = hrSignConvertedFields
            .filter((f: any) => f.required !== false && !(f.type === 'checkbox' && f.checkboxGroup) && (f.signerIndex === undefined || f.signerIndex === hrSignCurrentSigner))
            .every((f: any) => hrSignFilledFields.has(f.id))
          // Next unfilled field (ported from NativeSignatureModal — includes checkbox group satisfaction logic)
          const satisfiedGroups = new Set<string>()
          const groupedCbs = hrSignConvertedFields.filter((f: any) => f.type === 'checkbox' && f.checkboxGroup)
          const cbGroupNames = Array.from(new Set(groupedCbs.map((f: any) => f.checkboxGroup)))
          for (const g of cbGroupNames) {
            const members = groupedCbs.filter((f: any) => f.checkboxGroup === g)
            const min = members[0]?.checkboxGroupMin ?? 1
            const checked = members.filter((f: any) => hrSignFilledFields.has(f.id)).length
            if (checked >= min) satisfiedGroups.add(g)
          }
          const nextUnfilled = hrSignConvertedFields
            .filter((f: any) =>
              (f.signerIndex === undefined || f.signerIndex === hrSignCurrentSigner) &&
              !hrSignFilledFields.has(f.id) &&
              !(f.type === 'checkbox' && f.checkboxGroup && satisfiedGroups.has(f.checkboxGroup))
            )
            .sort((a: any, b: any) => { if (a.page !== b.page) return a.page - b.page; if (Math.abs(a.y - b.y) > 3) return a.y - b.y; return a.x - b.x })[0] || null
          const filledCount = hrSignFilledFields.size
          const totalCount = hrSignConvertedFields.filter((f: any) => f.signerIndex === undefined || f.signerIndex === hrSignCurrentSigner).length

          // Compute display width for PDF pages — cap at 800px within the viewport
          const pdfDisplayWidth = Math.min(800, window.innerWidth - 80)

          return (
            <div className="hr-sign-overlay">
              <div className="hr-sign-container">
              {/* Header (matches NativeSignatureModal compact header) */}
              <div className="hr-sign-header">
                <div style={{display:'flex',alignItems:'center',gap:12,minWidth:0,flex:1}}>
                  <div style={{width:36,height:36,background:'#2563eb',borderRadius:8,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  </div>
                  <div style={{minWidth:0,flex:1}}>
                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                      <div className="hr-sign-header-title">{hrSignAssignment?.assignment?.template_title || 'Document'}</div>
                      {hrSignNumPages > 0 && <span style={{fontSize:'0.72rem',color:'#94a3b8',whiteSpace:'nowrap'}}>{hrSignNumPages} {hrSignNumPages === 1 ? 'page' : 'pages'}</span>}
                    </div>
                    <div className="hr-sign-header-sub" style={{color: filledCount === totalCount && totalCount > 0 ? '#22c55e' : '#94a3b8', fontWeight: filledCount === totalCount && totalCount > 0 ? 600 : 400}}>
                      {filledCount}/{totalCount} fields completed
                    </div>
                  </div>
                </div>
                <div className="hr-sign-header-actions">
                  <button className="hr-sign-header-btn close" onClick={() => { if (!hrSignSubmitting) { setHrSignModalOpen(false); setHrEmployerSignSubmitDocId(null) } }} title="Close">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="hr-sign-body" style={{flexDirection:'column'}}>
                {hrSignLoading ? (
                  <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',color:'#fff'}}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{animation:'spin 1s linear infinite',marginRight:8}}><path d="M21 12a9 9 0 11-6.219-8.56"/></svg>
                    Loading document...
                  </div>
                ) : hrSignSuccess ? (
                  <div className="hr-sign-success">
                    <div className="hr-sign-success-check">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><path d="M5 13l4 4L19 7"/></svg>
                    </div>
                    <h3>Document Signed!</h3>
                    <p>Your signature has been recorded.</p>
                  </div>
                ) : hrSignError && hrSignPages.length === 0 ? (
                  <div style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',color:'#fff'}}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    <p style={{marginTop:12,color:'#ef4444'}}>{hrSignError}</p>
                  </div>
                ) : (
                  <>
                    {hrSignError && (
                      <div style={{padding:'8px 20px',background:'#fef2f2',borderBottom:'1px solid #fca5a5',color:'#dc2626',fontSize:'0.78rem',display:'flex',alignItems:'center',gap:8,flexShrink:0}}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                        {hrSignError}
                      </div>
                    )}
                    <div className="hr-sign-pdf-area" style={{flex:1}}>
                      {hrSignPages.map((page: any, pageIdx: number) => {
                        const ratio = page.height / page.width
                        const displayW = pdfDisplayWidth
                        const displayH = displayW * ratio
                        return (
                          <div key={pageIdx} className="hr-sign-page-wrap" style={{width:'100%',maxWidth:displayW,position:'relative'}}>
                            {/* Page number indicator */}
                            <div style={{position:'absolute',top:8,right:8,background:'rgba(0,0,0,0.65)',color:'#fff',fontSize:'0.68rem',padding:'2px 8px',borderRadius:4,zIndex:20,pointerEvents:'none'}}>
                              Page {pageIdx + 1} of {hrSignNumPages}
                            </div>
                            <img src={page.dataUrl} alt={`Page ${pageIdx+1}`} className="hr-sign-page-canvas" style={{width:'100%',height:'auto',display:'block'}} />
                            {/* Field overlay - positioned using percentage coordinates (EXACT same as NativeSignatureModal) */}
                            <div className="hr-sign-field-overlay" style={{width:displayW,height:displayH}}>
                              {hrSignConvertedFields.filter((f: any) => f.page === pageIdx + 1).map((field: any) => {
                                const isFilled = hrSignFilledFields.has(field.id)
                                const filledData = hrSignFilledFields.get(field.id)
                                const isOtherSigner = field.signerIndex !== undefined && field.signerIndex !== hrSignCurrentSigner
                                if (isOtherSigner && hrEmployerSignSubmitDocId) return null
                                const isEditing = (field.type === 'text' || field.type === 'date') && hrSignEditingTextField === field.id && !isOtherSigner

                                // Inline text editing (ported exactly)
                                if (isEditing) {
                                  return (
                                    <div key={field.id} style={{
                                      position:'absolute', left:`${field.x}%`, top:`${field.y}%`,
                                      width:`${field.width}%`, minHeight:`${field.height}%`,
                                      border:'2px solid #2563eb', background:'#fff', borderRadius:3,
                                      boxShadow:'0 2px 8px rgba(0,0,0,0.15)', pointerEvents:'auto', zIndex:50,
                                    }} onClick={e => e.stopPropagation()}>
                                      <input type="text" autoFocus
                                        value={hrSignEditingTextValue}
                                        maxLength={field.maxLength || (field.type === 'date' ? 10 : undefined)}
                                        onChange={e => {
                                          let val = e.target.value
                                          if (field.inputRestriction === 'alpha') val = val.replace(/[^a-zA-Z\s]/g, '')
                                          else if (field.inputRestriction === 'numeric') val = val.replace(/[^0-9]/g, '')
                                          else if (field.inputRestriction === 'alphanumeric') val = val.replace(/[^a-zA-Z0-9\s]/g, '')
                                          const mask = field.formatMask || (field.type === 'date' ? 'date' : undefined)
                                          setHrSignEditingTextValue(mask ? hrSignApplyFormatMask(val, mask) : val)
                                        }}
                                        placeholder={field.type === 'date' ? 'MM/DD/YYYY' : field.formatMask === 'ssn' ? '###-##-####' : field.formatMask === 'phone' ? '###-###-####' : field.formatMask === 'date' ? '##/##/####' : undefined}
                                        onBlur={() => handleSaveInlineText(field.id, field)}
                                        onKeyDown={e => {
                                          if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); handleSaveInlineText(field.id, field, true) }
                                          else if (e.key === 'Escape') { setHrSignEditingTextField(null); setHrSignEditingTextValue('') }
                                        }}
                                        style={{width:'100%',height:'100%',padding:'0 4px',fontSize:'0.72rem',fontWeight:600,border:'none',outline:'none',background:'transparent',textAlign:field.maxLength && field.maxLength <= 2 ? 'center' : 'left'}}
                                      />
                                    </div>
                                  )
                                }

                                // Signature / initials fields (ported from NativeSignatureModal.renderFieldOverlay)
                                if (field.type === 'signature' || field.type === 'initials') {
                                  const isReqUnfilled = field.required && !isFilled
                                  return (
                                    <div key={field.id} id={`hr-sign-field-${field.id}`}
                                      tabIndex={isOtherSigner ? -1 : 0}
                                      className="hr-sign-field-sig"
                                      style={{
                                        position:'absolute',
                                        left:`${field.x}%`, top:`${field.y}%`,
                                        width:`${field.width}%`, height:`${field.height}%`,
                                        pointerEvents: isOtherSigner ? 'none' : 'auto',
                                        border: `2px ${isOtherSigner ? 'dashed' : 'solid'} ${isOtherSigner ? '#9ca3af' : isFilled ? '#22c55e' : isReqUnfilled ? '#ef4444' : '#3b82f6'}`,
                                        background: isOtherSigner ? 'rgba(156,163,175,0.08)' : isFilled ? 'transparent' : isReqUnfilled ? 'rgba(239,68,68,0.05)' : 'rgba(59,130,246,0.08)',
                                        borderRadius:4, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', overflow:'hidden',
                                        cursor: isOtherSigner ? 'default' : 'pointer', transition:'all 0.15s',
                                      }}
                                      onClick={() => { if (!isOtherSigner) handleSignFieldClick(field, true) }}
                                      onKeyDown={e => {
                                        if (!isOtherSigner && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); handleSignFieldClick(field, true) }
                                        else if (!isOtherSigner && e.key === 'Tab') { e.preventDefault(); advanceToNextField(hrSignFilledFields, field.id) }
                                      }}
                                    >
                                      {isFilled ? (
                                        <div style={{width:'100%',height:'100%',display:'flex',flexDirection:'column',overflow:'hidden',position:'relative'}}>
                                          <div style={{flex:1,background:'rgba(255,255,255,0.95)',padding:2,cursor:'pointer',overflow:'hidden',display:'flex',alignItems:'center',justifyContent:'center'}}>
                                            <img src={filledData?.data} alt={field.label} style={{width:'100%',height:'100%',objectFit:'contain',opacity:isOtherSigner ? 0.6 : 1}} />
                                          </div>
                                          {!isOtherSigner && (
                                            <div style={{position:'absolute',top:-4,right:-4,width:18,height:18,background:'#22c55e',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',boxShadow:'0 1px 3px rgba(0,0,0,0.2)'}}>
                                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><path d="M5 13l4 4L19 7"/></svg>
                                            </div>
                                          )}
                                        </div>
                                      ) : isOtherSigner ? (
                                        <div style={{background:'rgba(229,231,235,0.8)',color:'#6b7280',padding:'4px 10px',borderRadius:6,display:'flex',alignItems:'center',gap:4,fontSize:'0.62rem',fontWeight:700}}>
                                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 19l7-7 3 3-7 7-3-3z"/><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/></svg>
                                          <span>{field.signerRole ? `Signed by ${field.signerRole}` : field.signerIndex === 0 ? 'Signed by Employee' : field.signerIndex === 1 ? 'Signed by Employer' : `Signed by Signer ${(field.signerIndex || 0) + 1}`}</span>
                                        </div>
                                      ) : (
                                        <button style={{background:'linear-gradient(135deg,#2563eb,#4f46e5)',color:'#fff',border:'none',borderRadius:6,padding:'4px 10px',fontSize:'0.68rem',fontWeight:700,cursor:'pointer',display:'flex',alignItems:'center',gap:4,boxShadow:'0 2px 6px rgba(37,99,235,0.3)',transition:'transform 0.15s'}}>
                                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 19l7-7 3 3-7 7-3-3z"/><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/></svg>
                                          {field.type === 'signature' ? 'Sign' : 'Initial'}
                                        </button>
                                      )}
                                    </div>
                                  )
                                }

                                // Checkbox / date / text fields (ported from NativeSignatureModal.renderFieldOverlay)
                                const isReqUnfilled = field.checkboxGroup
                                  ? !isFilled && ((field.checkboxGroupMin ?? 1) >= 1) && hrSignConvertedFields.filter((f: any) => f.checkboxGroup === field.checkboxGroup && hrSignFilledFields.has(f.id)).length < (field.checkboxGroupMin ?? 1)
                                  : field.required && !isFilled
                                const fieldBorderColor = isOtherSigner ? '#9ca3af' : isFilled ? '#22c55e' : isReqUnfilled ? '#ef4444' : '#3b82f6'
                                const fieldBg = isOtherSigner ? 'rgba(249,250,251,0.3)' : isFilled ? 'rgba(34,197,94,0.12)' : isReqUnfilled ? 'rgba(239,68,68,0.08)' : 'rgba(59,130,246,0.08)'
                                return (
                                  <div key={field.id} id={`hr-sign-field-${field.id}`}
                                    tabIndex={isOtherSigner ? -1 : 0}
                                    style={{
                                      position:'absolute', left:`${field.x}%`, top:`${field.y}%`,
                                      width:`${field.width}%`, height:`${field.height}%`,
                                      pointerEvents: isOtherSigner ? 'none' : 'auto',
                                      border: `2px ${isOtherSigner ? 'dashed' : 'solid'} ${fieldBorderColor}`,
                                      background: fieldBg, borderRadius:3,
                                      cursor: isOtherSigner ? 'default' : 'pointer', transition:'all 0.15s',
                                      display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden',
                                    }}
                                    onClick={() => { if (!isOtherSigner) handleSignFieldClick(field, true) }}
                                    onKeyDown={e => {
                                      if (!isOtherSigner && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); handleSignFieldClick(field, true) }
                                      else if (!isOtherSigner && e.key === 'Tab') { e.preventDefault(); advanceToNextField(hrSignFilledFields, field.id) }
                                    }}
                                  >
                                    {isFilled && field.type === 'checkbox' && (
                                      <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="#15803d" strokeWidth="3"><path d="M5 13l4 4L19 7"/></svg>
                                    )}
                                    {isFilled && (field.type === 'text' || field.type === 'date') && (
                                      <span style={{fontSize: filledData?.data?.length <= 2 ? '0.72rem' : '0.62rem', fontWeight:600, padding:'0 2px', wordBreak:'break-all', textAlign:'center', lineHeight:1}}>{filledData?.data}</span>
                                    )}
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </>
                )}
              </div>

              {/* Footer (ported from NativeSignatureModal - matches original layout with Next/Cancel/Submit) */}
              {!hrSignSuccess && !hrSignLoading && hrSignPages.length > 0 && (
                <div className="hr-sign-footer">
                  <div style={{display:'flex',alignItems:'center',gap:12,flex:1,flexWrap:'wrap'}}>
                    {hrSignError && (
                      <div style={{display:'flex',alignItems:'center',gap:6,fontSize:'0.75rem',color:'#dc2626',background:'#fef2f2',padding:'6px 10px',borderRadius:8}}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                        <span>{hrSignError}</span>
                      </div>
                    )}
                    {nextUnfilled && (
                      <button className="hr-sign-footer-btn next" onClick={() => advanceToNextField(hrSignFilledFields, undefined, true)}>
                        {filledCount === 0 ? 'Start' : 'Next Required'}
                      </button>
                    )}
                  </div>
                  <div style={{display:'flex',gap:10,flexShrink:0}}>
                    <button className="hr-sign-footer-btn cancel" onClick={() => { if (!hrSignSubmitting) { setHrSignModalOpen(false); setHrEmployerSignSubmitDocId(null) } }} disabled={hrSignSubmitting}>Cancel</button>
                    <button className="hr-sign-footer-btn submit" disabled={hrSignSubmitting || !allReqFilled} onClick={handleSignSubmit}>
                      {hrSignSubmitting ? (
                        <span style={{display:'flex',alignItems:'center',gap:6}}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{animation:'spin 1s linear infinite'}}><path d="M21 12a9 9 0 11-6.219-8.56"/></svg>
                          Submitting...
                        </span>
                      ) : (
                        <span style={{display:'flex',alignItems:'center',gap:6}}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 19l7-7 3 3-7 7-3-3z"/><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/></svg>
                          {nextUnfilled ? 'Finish Later' : 'Submit Document'}
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              )}
              </div>
            </div>
          )
        })()}

        {/* ===== SIGNATURE CAPTURE POPUP (ported from NativeSignatureModal) ===== */}
        {hrSignPopupOpen && hrSignActiveField && (
          <div className="hr-sign-popup-overlay" onClick={() => { setHrSignPopupOpen(false); setHrSignTempSig(null) }}>
            <div className="hr-sign-popup" tabIndex={-1} onClick={e => e.stopPropagation()} onKeyDown={e => {
              if ((e.target as HTMLElement).tagName === 'INPUT' || (e.target as HTMLElement).tagName === 'TEXTAREA') return
              if (e.key === 'Enter' && hrSignTempSig && hrSignActiveField) {
                e.preventDefault()
                // Trigger the same logic as the Place button
                if (hrSignActiveField.id === 'employer-sig') {
                  setHrEmployerSignature(hrSignTempSig)
                  saveHrAdminEmployerSignature({ signature: hrSignTempSig }).catch(() => {})
                  try { sessionStorage.setItem('_hr_employer_sig', hrSignTempSig) } catch {}
                  setHrSignPopupOpen(false); setHrSignActiveField(null); setHrSignTempSig(null); setHrSignUploadedSig(null)
                } else if (hrSignActiveField.id === 'employer-init') {
                  setHrEmployerInitials(hrSignTempSig)
                  saveHrAdminEmployerSignature({ initials: hrSignTempSig }).catch(() => {})
                  try { sessionStorage.setItem('_hr_employer_init', hrSignTempSig) } catch {}
                  setHrSignPopupOpen(false); setHrSignActiveField(null); setHrSignTempSig(null); setHrSignUploadedSig(null)
                } else {
                  const newMap = new Map(hrSignFilledFields)
                  newMap.set(hrSignActiveField.id, { fieldId: hrSignActiveField.id, type: hrSignActiveField.type, data: hrSignTempSig, label: hrSignActiveField.label })
                  setHrSignFilledFields(newMap)
                  setHrSignPopupOpen(false); setHrSignActiveField(null); setHrSignTempSig(null); setHrSignUploadedSig(null)
                }
              } else if (e.key === 'Escape') {
                setHrSignPopupOpen(false); setHrSignTempSig(null); setHrSignUploadedSig(null)
              }
            }}>
              {/* Header with gradient (matches NativeSignatureModal exactly) */}
              <div style={{background:'linear-gradient(135deg,#2563eb,#4f46e5)',color:'#fff',padding:'12px 20px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                <div style={{display:'flex',alignItems:'center',gap:8,flex:1}}>
                  <div style={{width:32,height:32,background:'rgba(255,255,255,0.2)',borderRadius:8,display:'flex',alignItems:'center',justifyContent:'center'}}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 19l7-7 3 3-7 7-3-3z"/><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/></svg>
                  </div>
                  <div>
                    <div style={{fontWeight:700,fontSize:'0.9rem'}}>{hrSignActiveField.label || (hrSignActiveField.type === 'initials' ? 'Add Initials' : 'Add Signature')}</div>
                    <div style={{fontSize:'0.68rem',opacity:0.9}}>Secure Electronic Signature</div>
                  </div>
                </div>
                <button onClick={() => { setHrSignPopupOpen(false); setHrSignTempSig(null); setHrSignCustomName(hrSignCustomName) }} style={{border:'none',background:'none',cursor:'pointer',color:'rgba(255,255,255,0.8)',padding:4,marginLeft:8}}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                </button>
              </div>

              {/* Method Selector Tabs - DocuSign Style (matches NativeSignatureModal exactly) */}
              <div style={{padding:'12px 16px 0',background:'#f9fafb'}}>
                <div style={{display:'flex',gap:4,background:'#e5e7eb',padding:4,borderRadius:8}}>
                  <button onClick={() => { setHrSignPopupTab('style'); setHrSignTempSig(null); setHrSignUploadedSig(null) }}
                    style={{flex:1,padding:'8px 12px',borderRadius:6,fontWeight:600,fontSize:'0.78rem',border:'none',cursor:'pointer',transition:'all 0.15s',
                      background:hrSignPopupTab==='style'?'#fff':'transparent', color:hrSignPopupTab==='style'?'#2563eb':'#6b7280',
                      boxShadow:hrSignPopupTab==='style'?'0 1px 3px rgba(0,0,0,0.1)':'none',
                    }}>
                    <span style={{display:'flex',alignItems:'center',gap:4,justifyContent:'center'}}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="4 7 4 4 20 4 20 7"/><line x1="9" y1="20" x2="15" y2="20"/><line x1="12" y1="4" x2="12" y2="20"/></svg>
                      Select Style
                    </span>
                  </button>
                  <button onClick={() => { setHrSignPopupTab('draw'); setHrSignTempSig(null); setHrSignUploadedSig(null) }}
                    style={{flex:1,padding:'8px 12px',borderRadius:6,fontWeight:600,fontSize:'0.78rem',border:'none',cursor:'pointer',transition:'all 0.15s',
                      background:hrSignPopupTab==='draw'?'#fff':'transparent', color:hrSignPopupTab==='draw'?'#2563eb':'#6b7280',
                      boxShadow:hrSignPopupTab==='draw'?'0 1px 3px rgba(0,0,0,0.1)':'none',
                    }}>
                    <span style={{display:'flex',alignItems:'center',gap:4,justifyContent:'center'}}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 19l7-7 3 3-7 7-3-3z"/><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/></svg>
                      Draw
                    </span>
                  </button>
                  <button onClick={() => { setHrSignPopupTab('upload'); setHrSignTempSig(null) }}
                    style={{flex:1,padding:'8px 12px',borderRadius:6,fontWeight:600,fontSize:'0.78rem',border:'none',cursor:'pointer',transition:'all 0.15s',
                      background:hrSignPopupTab==='upload'?'#fff':'transparent', color:hrSignPopupTab==='upload'?'#2563eb':'#6b7280',
                      boxShadow:hrSignPopupTab==='upload'?'0 1px 3px rgba(0,0,0,0.1)':'none',
                    }}>
                    <span style={{display:'flex',alignItems:'center',gap:4,justifyContent:'center'}}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                      Upload
                    </span>
                  </button>
                </div>
              </div>

              {/* Content Area (matches NativeSignatureModal exactly) */}
              <div className="hr-sign-popup-body" style={{background:'#f9fafb',maxHeight:420,overflowY:hrSignPopupTab==='draw'?'hidden':'auto',touchAction:hrSignPopupTab==='draw'?'none':'auto'}}>
                {/* Hidden file input */}
                <input ref={hrSignFileInputRef} type="file" accept="image/png,image/jpeg,image/gif,image/bmp" style={{display:'none'}} onChange={e => {
                  const file = e.target.files?.[0]
                  if (!file) return
                  const validTypes = ['image/png','image/jpeg','image/gif','image/bmp']
                  if (!validTypes.includes(file.type)) { alert('Please upload a valid image file (PNG, JPG, GIF, or BMP)'); return }
                  if (file.size > 3 * 1024 * 1024) { alert('File size must be less than 3MB'); return }
                  const reader = new FileReader()
                  reader.onload = () => { const du = reader.result as string; setHrSignUploadedSig(du); setHrSignTempSig(du) }
                  reader.readAsDataURL(file)
                }} />

                {hrSignPopupTab === 'style' ? (
                  /* SELECT STYLE - Font Selection Grid (ported exactly from NativeSignatureModal) */
                  <div>
                    {/* Editable name input */}
                    <div style={{marginBottom:12}}>
                      <label style={{display:'block',fontSize:'0.78rem',fontWeight:600,color:'#374151',marginBottom:4}}>
                        {hrSignActiveField.type === 'initials' ? 'Your Name (for initials)' : 'Your Full Name'}
                      </label>
                      <input type="text" value={hrSignCustomName}
                        onChange={e => {
                          const newName = e.target.value
                          setHrSignCustomName(newName)
                          if (hrSignSelectedFont >= 0 && newName.trim()) {
                            const isInit = hrSignActiveField.type === 'initials'
                            const previewText = isInit ? newName.split(/\s+/).map((w: string) => w[0]||'').join('').toUpperCase() : newName
                            setHrSignTempSig(hrSignGenerateTyped(previewText, isInit, hrSignSelectedFont))
                          } else if (!newName.trim()) setHrSignTempSig(null)
                        }}
                        onKeyDown={e => {
                          if ((e.key === 'Enter' || e.key === 'Tab') && hrSignSelectedFont >= 0 && hrSignCustomName.trim() && hrSignTempSig) {
                            e.preventDefault()
                            const newMap = new Map(hrSignFilledFields)
                            newMap.set(hrSignActiveField.id, { fieldId: hrSignActiveField.id, type: hrSignActiveField.type, data: hrSignTempSig, label: hrSignActiveField.label })
                            setHrSignFilledFields(newMap)
                            setHrSignPopupOpen(false)
                            setHrSignTempSig(null)
                          }
                        }}
                        placeholder="Enter your full name"
                        style={{width:'100%',padding:'8px 12px',border:'1px solid #d1d5db',borderRadius:8,fontSize:'0.82rem',outline:'none'}}
                      />
                    </div>
                    <p style={{fontSize:'0.78rem',color:'#6b7280',textAlign:'center',marginBottom:12}}>
                      Choose a style for your {hrSignActiveField.type === 'initials' ? 'initials' : 'signature'}
                    </p>
                    <div style={{display:'flex',flexDirection:'column',gap:10}}>
                      {[
                        { name: 'Dancing Script', cls: 'hr-sign-font-dancing' },
                        { name: 'Great Vibes', cls: 'hr-sign-font-great-vibes' },
                        { name: 'Allura', cls: 'hr-sign-font-allura' },
                      ].map((font, idx) => {
                        const previewName = (() => {
                          const nameToUse = hrSignCustomName.trim() || hrSignAssignment?.assignment?.assignee_name || 'Your Name'
                          if (hrSignActiveField.type === 'initials') {
                            const parts = nameToUse.split(/\s+/)
                            const first = parts[0]?.[0] || ''
                            const last = parts[parts.length - 1]?.[0] || ''
                            return `${first}${last}`.toUpperCase() || 'JD'
                          }
                          return nameToUse
                        })()
                        return (
                          <button key={idx}
                            onClick={() => {
                              setHrSignSelectedFont(idx)
                              const isInit = hrSignActiveField.type === 'initials'
                              setHrSignTempSig(hrSignGenerateTyped(previewName, isInit, idx))
                            }}
                            style={{
                              position:'relative', borderRadius:8, overflow:'hidden', border:'2px solid', transition:'all 0.15s', cursor:'pointer', background:'transparent',
                              borderColor: hrSignSelectedFont === idx ? '#2563eb' : '#e5e7eb',
                              ...(hrSignSelectedFont === idx ? { boxShadow:'0 0 0 2px #2563eb, 0 0 0 4px rgba(37,99,235,0.15)' } : {}),
                            }}>
                            {hrSignSelectedFont === idx && (
                              <div style={{position:'absolute',top:8,right:8,zIndex:10,width:22,height:22,background:'#2563eb',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',boxShadow:'0 2px 4px rgba(0,0,0,0.2)'}}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><path d="M5 13l4 4L19 7"/></svg>
                              </div>
                            )}
                            <div style={{display:'flex',alignItems:'center',justifyContent:'center',background:'#fff',padding:'16px 8px'}}>
                              <span className={font.cls} style={{position:'relative'}}>{previewName}</span>
                            </div>
                            <div style={{fontSize:'0.68rem',padding:'6px 0',textAlign:'center',
                              background: hrSignSelectedFont === idx ? '#eff6ff' : '#f3f4f6',
                              color: hrSignSelectedFont === idx ? '#2563eb' : '#6b7280',
                              fontWeight: hrSignSelectedFont === idx ? 600 : 400,
                            }}>{font.name}</div>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                ) : hrSignPopupTab === 'draw' ? (
                  /* DRAW - Canvas Drawing (ported from SignatureCanvas with proper touch handling) */
                  <div>
                    <p style={{fontSize:'0.72rem',color:'#6b7280',textAlign:'center',marginBottom:8}}>
                      {hrSignActiveField.type === 'initials' ? 'Draw your initials below' : 'Draw your signature below'}
                    </p>
                    <div style={{display:'flex',alignItems:'center',justifyContent:'center'}}>
                      <div className="hr-sign-canvas-wrap" style={{position:'relative',width:hrSignActiveField.type === 'initials' ? 300 : 400,maxWidth:'100%',height:hrSignActiveField.type === 'initials' ? 150 : 200,touchAction:'none'}}>
                        <canvas id="hr-sign-draw-canvas"
                          width={hrSignActiveField.type === 'initials' ? 300 : 400}
                          height={hrSignActiveField.type === 'initials' ? 150 : 200}
                          className="hr-sign-canvas"
                          style={{width:'100%',height:'100%',touchAction:'none'}}
                          ref={(canvas) => {
                            if (!canvas) return
                            if ((canvas as any)._hrInit) return
                            ;(canvas as any)._hrInit = true
                            const ctx = canvas.getContext('2d')!
                            ctx.strokeStyle = '#000'
                            ctx.lineWidth = 2.5
                            ctx.lineCap = 'round'
                            ctx.lineJoin = 'round'
                            ctx.clearRect(0, 0, canvas.width, canvas.height)
                            let drawing = false
                            const getCoords = (e: MouseEvent | TouchEvent) => {
                              const rect = canvas.getBoundingClientRect()
                              const scX = canvas.width / rect.width
                              const scY = canvas.height / rect.height
                              if ('touches' in e) {
                                const t = (e as TouchEvent).touches[0]
                                return { x: (t.clientX - rect.left) * scX, y: (t.clientY - rect.top) * scY }
                              }
                              return { x: ((e as MouseEvent).clientX - rect.left) * scX, y: ((e as MouseEvent).clientY - rect.top) * scY }
                            }
                            const start = (e: MouseEvent | TouchEvent) => {
                              e.preventDefault()
                              drawing = true
                              const c = getCoords(e)
                              ctx.beginPath()
                              ctx.moveTo(c.x, c.y)
                            }
                            const move = (e: MouseEvent | TouchEvent) => {
                              e.preventDefault()
                              if (!drawing) return
                              const c = getCoords(e)
                              ctx.lineTo(c.x, c.y)
                              ctx.stroke()
                            }
                            const stop = () => {
                              if (!drawing) return
                              drawing = false
                              const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height)
                              if (imgData.data.some((_v, i) => i % 4 === 3 && imgData.data[i] > 0)) {
                                setHrSignTempSig(hrSignTrimCanvas(canvas))
                              }
                            }
                            canvas.addEventListener('mousedown', start)
                            canvas.addEventListener('mousemove', move)
                            canvas.addEventListener('mouseup', stop)
                            canvas.addEventListener('mouseleave', stop)
                            canvas.addEventListener('touchstart', start, { passive: false })
                            canvas.addEventListener('touchmove', move, { passive: false })
                            canvas.addEventListener('touchend', stop)
                            ;(canvas as any).clearSignature = () => {
                              ctx.clearRect(0, 0, canvas.width, canvas.height)
                              ctx.strokeStyle = '#000'; ctx.lineWidth = 2.5
                              setHrSignTempSig(null)
                            }
                          }}
                        />
                        <button className="hr-sign-canvas-clear" onClick={() => {
                          const c = document.getElementById('hr-sign-draw-canvas') as any
                          if (c?.clearSignature) c.clearSignature()
                        }}>Clear</button>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* UPLOAD - File Upload (ported exactly from NativeSignatureModal) */
                  <div>
                    <div className={`hr-sign-upload-area${hrSignUploadedSig ? ' has-file' : ''}`}
                      onClick={() => hrSignFileInputRef.current?.click()}>
                      {hrSignUploadedSig ? (
                        <div style={{textAlign:'center'}}>
                          <img src={hrSignUploadedSig} alt="Uploaded signature" className="hr-sign-upload-preview" />
                          <p style={{fontSize:'0.78rem',color:'#16a34a',fontWeight:600,marginTop:8}}>Signature uploaded! Click to change.</p>
                        </div>
                      ) : (
                        <div style={{color:'#6b7280',textAlign:'center'}}>
                          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{margin:'0 auto 10px',display:'block',color:'#9ca3af'}}><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                          <p style={{fontSize:'0.82rem',fontWeight:600}}>UPLOAD YOUR SIGNATURE</p>
                          <p style={{fontSize:'0.72rem',marginTop:4}}>Accepted: GIF, JPG, PNG, BMP. Max 3MB.</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer with security notice + buttons (matches NativeSignatureModal exactly) */}
              <div style={{padding:'12px 16px',borderTop:'1px solid #e5e7eb',background:'#fff'}}>
                {/* Security Notice */}
                <div style={{display:'flex',alignItems:'flex-start',gap:6,fontSize:'0.68rem',color:'#6b7280',background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:8,padding:8,marginBottom:10}}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" style={{flexShrink:0,marginTop:1}}><path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/></svg>
                  Your signature is encrypted and legally binding per the ESIGN Act.
                </div>
                {/* Buttons (Clear / Cancel / Place Signature) */}
                <div style={{display:'flex',gap:8}}>
                  <button onClick={() => {
                    if (hrSignPopupTab === 'draw') {
                      const c = document.getElementById('hr-sign-draw-canvas') as any
                      if (c?.clearSignature) c.clearSignature()
                    } else if (hrSignPopupTab === 'upload') {
                      setHrSignUploadedSig(null)
                    } else {
                      setHrSignCustomName('')
                    }
                    setHrSignTempSig(null)
                  }} disabled={!hrSignTempSig && !hrSignUploadedSig}
                    style={{padding:'8px 16px',background:'#f3f4f6',color:'#374151',borderRadius:8,border:'none',fontWeight:600,fontSize:'0.82rem',cursor:'pointer',opacity:(!hrSignTempSig && !hrSignUploadedSig)?0.5:1,transition:'all 0.15s'}}>
                    Clear
                  </button>
                  <button onClick={() => { setHrSignPopupOpen(false); setHrSignTempSig(null); setHrSignUploadedSig(null) }}
                    style={{flex:1,padding:'8px 16px',background:'#fff',border:'2px solid #d1d5db',color:'#374151',borderRadius:8,fontWeight:600,fontSize:'0.82rem',cursor:'pointer',transition:'all 0.15s'}}>
                    Cancel
                  </button>
                  <button disabled={!hrSignTempSig} onClick={() => {
                    if (hrSignTempSig && hrSignActiveField) {
                      // Check if this is an employer signature/initials from Settings page
                      if (hrSignActiveField.id === 'employer-sig') {
                        setHrEmployerSignature(hrSignTempSig)
                        saveHrAdminEmployerSignature({ signature: hrSignTempSig }).catch(() => {})
                        // Save to sessionStorage for persistence
                        try { sessionStorage.setItem('_hr_employer_sig', hrSignTempSig) } catch {}
                        setHrSignPopupOpen(false); setHrSignActiveField(null); setHrSignTempSig(null); setHrSignUploadedSig(null)
                        return
                      }
                      if (hrSignActiveField.id === 'employer-init') {
                        setHrEmployerInitials(hrSignTempSig)
                        saveHrAdminEmployerSignature({ initials: hrSignTempSig }).catch(() => {})
                        try { sessionStorage.setItem('_hr_employer_init', hrSignTempSig) } catch {}
                        setHrSignPopupOpen(false); setHrSignActiveField(null); setHrSignTempSig(null); setHrSignUploadedSig(null)
                        return
                      }
                      const newMap = new Map(hrSignFilledFields)
                      newMap.set(hrSignActiveField.id, { fieldId: hrSignActiveField.id, type: hrSignActiveField.type, data: hrSignTempSig, label: hrSignActiveField.label })
                      setHrSignFilledFields(newMap)
                      setHrSignPopupOpen(false)
                      setHrSignActiveField(null)
                      setHrSignTempSig(null)
                      setHrSignUploadedSig(null)
                    }
                  }}
                    style={{flex:1,padding:'8px 16px',background:hrSignTempSig?'linear-gradient(135deg,#2563eb,#4f46e5)':'#e5e7eb',color:hrSignTempSig?'#fff':'#9ca3af',borderRadius:8,border:'none',fontWeight:700,fontSize:'0.82rem',cursor:hrSignTempSig?'pointer':'default',boxShadow:hrSignTempSig?'0 2px 8px rgba(37,99,235,0.3)':'none',transition:'all 0.15s'}}>
                    Place {hrSignActiveField.type === 'initials' ? 'Initials' : 'Signature'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===== TEMPLATE FIELD EDITOR MODAL (rebuilt to match standalone portal FieldEditorModal) ===== */}
        {hrFieldEditorOpen && (() => {
          const fieldDims: Record<string, {w:number,h:number}> = {
            signature: {w:20,h:5}, initials: {w:10,h:5}, checkbox: {w:1.8,h:1.8},
            date: {w:15,h:5}, text: {w:20,h:5}, dropdown: {w:20,h:5},
          }
          const feTemplate = hrPortalTemplates.find((t: any) => String(t.id) === hrFieldEditorTemplateId)
          const feFieldTypes = [
            { type: 'signature', label: 'Signature', icon: 'M', color: '#3b82f6', bgClass: 'bg-blue-600', example: 'John Doe', exStyle: 'font-family:serif;font-style:italic;font-size:14px' },
            { type: 'initials', label: 'Initials', icon: 'O', color: '#6366f1', bgClass: 'bg-indigo-600', example: 'JD', exStyle: 'font-family:serif;font-style:italic;font-weight:bold;font-size:13px' },
            { type: 'checkbox', label: 'Checkbox', icon: 'C', color: '#475569', bgClass: 'bg-slate-600', example: '\u2611', exStyle: 'font-size:20px' },
            { type: 'date', label: 'Date', icon: 'D', color: '#0e7490', bgClass: 'bg-cyan-700', example: new Date().toLocaleDateString(), exStyle: 'font-size:11px' },
            { type: 'text', label: 'Text', icon: 'T', color: '#6b7280', bgClass: 'bg-gray-600', example: 'Enter text...', exStyle: 'font-size:11px;font-style:italic;opacity:0.8' },
            { type: 'dropdown', label: 'Dropdown', icon: 'V', color: '#d97706', bgClass: 'bg-amber-600', example: 'Select \u25BE', exStyle: 'font-size:11px;font-weight:bold' },
          ]
          const signerBgColors = ['rgba(37,99,235,0.4)', 'rgba(22,163,74,0.4)', 'rgba(147,51,234,0.4)', 'rgba(234,88,12,0.4)', 'rgba(236,72,153,0.4)']
          const signerBorderColors = ['#2563eb', '#16a34a', '#9333ea', '#ea580c', '#ec4899']
          const typeBgColors: Record<string,string> = {
            signature: 'rgba(37,99,235,0.4)', initials: 'rgba(79,70,229,0.4)',
            date: 'rgba(14,116,144,0.4)', text: 'rgba(75,85,99,0.4)', checkbox: 'rgba(71,85,105,0.4)',
            dropdown: 'rgba(217,119,6,0.4)',
          }
          const getFieldBg = (field: any) => {
            if (field.signerIndex !== undefined && field.signerIndex >= 0 && signerBgColors[field.signerIndex]) {
              return signerBgColors[field.signerIndex]
            }
            return typeBgColors[field.type] || 'rgba(107,114,128,0.4)'
          }
          const getFieldBorder = (field: any) => {
            if (field.signerIndex !== undefined && field.signerIndex >= 0 && signerBorderColors[field.signerIndex]) {
              return signerBorderColors[field.signerIndex]
            }
            return undefined
          }
          const zoom = hrFieldEditorZoom
          const toggleFeSection = (key: string) => setHrFieldEditorSidebarSection((prev: Record<string,boolean>) => {
            const isOpening = !prev[key]
            if (isOpening) {
              const next: Record<string,boolean> = {}
              for (const k of Object.keys(prev)) next[k] = k === key
              return next
            }
            return { ...prev, [key]: false }
          })
          // Mouse handlers for drag/resize (ported from FieldEditorModal)
          const handleEditorMouseMove = (e: React.MouseEvent) => {
            if (hrFieldEditorDragging) {
              const pageEl = document.getElementById(`hr-fe-page-${hrFieldEditorDragging.pageNumber}`)
              if (!pageEl) return
              const rect = pageEl.getBoundingClientRect()
              const deltaX = ((e.clientX - hrFieldEditorDragging.startX) / rect.width) * 100
              const deltaY = ((e.clientY - hrFieldEditorDragging.startY) / rect.height) * 100
              setHrFieldEditorFields((prev: any[]) => prev.map((f: any) =>
                f.id === hrFieldEditorDragging.id ? {
                  ...f,
                  x: Math.max(0, Math.min(100 - f.width, hrFieldEditorDragging.fieldX + deltaX)),
                  y: Math.max(0, Math.min(100 - f.height, hrFieldEditorDragging.fieldY + deltaY)),
                } : f
              ))
            } else if (hrFieldEditorResizing) {
              const pageEl = document.getElementById(`hr-fe-page-${hrFieldEditorResizing.pageNumber}`)
              if (!pageEl) return
              const rect = pageEl.getBoundingClientRect()
              const deltaX = ((e.clientX - hrFieldEditorResizing.startX) / rect.width) * 100
              const deltaY = ((e.clientY - hrFieldEditorResizing.startY) / rect.height) * 100
              setHrFieldEditorFields((prev: any[]) => prev.map((f: any) =>
                f.id === hrFieldEditorResizing.id ? {
                  ...f,
                  width: Math.max(1, Math.min(50, hrFieldEditorResizing.startWidth + deltaX)),
                  height: Math.max(0.8, Math.min(20, hrFieldEditorResizing.startHeight + deltaY)),
                } : f
              ))
            }
            if (hrFieldEditorMarquee) {
              const pageEl = document.getElementById(`hr-fe-page-${hrFieldEditorMarquee.pageIdx + 1}`)
              if (!pageEl) return
              const rect = pageEl.getBoundingClientRect()
              const currentX = ((e.clientX - rect.left) / rect.width) * 100
              const currentY = ((e.clientY - rect.top) / rect.height) * 100
              setHrFieldEditorMarquee(prev => prev ? { ...prev, currentX, currentY } : null)
            }
          }
          const handleEditorMouseUp = () => {
            if (hrFieldEditorMarquee) {
              const m = hrFieldEditorMarquee
              const minX = Math.min(m.startX, m.currentX)
              const maxX = Math.max(m.startX, m.currentX)
              const minY = Math.min(m.startY, m.currentY)
              const maxY = Math.max(m.startY, m.currentY)
              // Only process if dragged more than 1% (not just a click)
              if (maxX - minX > 1 && maxY - minY > 1) {
                const selectedIds = hrFieldEditorFields
                  .filter((f: any) => {
                    if (f.type !== 'checkbox') return false
                    if ((f.pageNumber || f.page) !== m.pageIdx + 1) return false
                    const fRight = f.x + f.width
                    const fBottom = f.y + f.height
                    return f.x < maxX && fRight > minX && f.y < maxY && fBottom > minY
                  })
                  .map((f: any) => f.id)
                if (selectedIds.length >= 2) {
                  setHrFieldEditorMarqueeSelected(selectedIds)
                  setHrFieldEditorGroupPrompt(true)
                  setHrFieldEditorNewGroupName('')
                }
              }
              setHrFieldEditorMarquee(null)
              return
            }
            if (hrFieldEditorDragging || hrFieldEditorResizing) {
              // Push current state to history when drag/resize ends (state was saved at mouseDown)
            }
            setHrFieldEditorDragging(null)
            setHrFieldEditorResizing(null)
          }
          // Save handler
          const handleSaveEditorFields = async () => {
            if (!hrFieldEditorTemplateId) return
            setHrFieldEditorSaving(true)
            try {
              const fieldPositions = hrFieldEditorFields.map((field: any) => {
                const pageIndex = (field.pageNumber || field.page || 1) - 1
                const pg = hrFieldEditorPages[pageIndex]
                if (!pg) return field
                const pageWidth = pg.nativeWidth || 800
                const pageHeight = pg.nativeHeight || 1132
                const pdfX = (field.x / 100) * pageWidth
                const pdfY = pageHeight - (((field.y + field.height) / 100) * pageHeight)
                const pdfWidth = (field.width / 100) * pageWidth
                const pdfHeight = (field.height / 100) * pageHeight
                return {
                  type: field.type, x: pdfX, y: pdfY, width: pdfWidth, height: pdfHeight,
                  page: field.pageNumber || field.page || 1,
                  label: field.label, required: field.required ?? true,
                  signerIndex: field.signerIndex, signerRole: field.signerRole,
                  pageHeight, pageWidth,
                  ...(field.checkboxGroup && { checkboxGroup: field.checkboxGroup }),
                  ...(field.checkboxGroup && field.checkboxGroupMin !== undefined && { checkboxGroupMin: field.checkboxGroupMin }),
                  ...(field.checkboxGroup && field.checkboxGroupMax !== undefined && { checkboxGroupMax: field.checkboxGroupMax }),
                  ...(field.defaultToToday !== undefined && { defaultToToday: field.defaultToToday }),
                  ...(field.maxLength && { maxLength: field.maxLength }),
                  ...(field.staticValue && { staticValue: field.staticValue }),
                  ...(field.formatMask && { formatMask: field.formatMask }),
                  ...(field.inputRestriction && { inputRestriction: field.inputRestriction }),
                  ...(field.options && field.options.length > 0 && { options: field.options }),
                }
              })
              await updateHrAdminTemplateFields(hrFieldEditorTemplateId, fieldPositions)
              setHrPortalTemplates((prev: any[]) => prev.map((t: any) =>
                String(t.id) === hrFieldEditorTemplateId ? { ...t, field_positions: fieldPositions } : t
              ))
              setHrFieldEditorOpen(false)
            } catch (e: any) {
              alert('Failed to save: ' + e.message)
            } finally {
              setHrFieldEditorSaving(false)
            }
          }
          const selectedField = hrFieldEditorSelected ? hrFieldEditorFields.find((f: any) => f.id === hrFieldEditorSelected) : null
          const requiredCount = hrFieldEditorFields.filter((f: any) => f.required !== false).length
          const optionalCount = hrFieldEditorFields.length - requiredCount
          const checkboxGroups = Array.from(new Set(hrFieldEditorFields.filter((f: any) => f.type === 'checkbox' && f.checkboxGroup && !f.checkboxGroup.startsWith('split-field') && !f.checkboxGroup.startsWith('field_')).map((f: any) => f.checkboxGroup!)))

          return (
            <div className="hr-sign-overlay" style={{flexDirection:'column',alignItems:'stretch',justifyContent:'stretch',padding:0}} onMouseMove={handleEditorMouseMove} onMouseUp={handleEditorMouseUp} onMouseLeave={handleEditorMouseUp}>
              {/* Full-screen editor container */}
              <div style={{display:'flex',flexDirection:'column',width:'100%',height:'100%',background:'#fff'}}>

                {/* ===== PRIMARY HEADER BAR ===== */}
                <nav style={{display:'flex',width:'100%',justifyContent:'space-between',alignItems:'center',borderBottom:'1px solid #d1d5db',background:'#fff',flexShrink:0}}>
                  {/* Left: Brand mark + Document Info */}
                  <div style={{display:'flex',alignItems:'center'}}>
                    {/* Brand Mark */}
                    <div style={{display:'flex',height:50,width:50,flexShrink:0,alignItems:'center',justifyContent:'center',background:'#2563eb'}}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/></svg>
                    </div>
                    {/* Title + Status */}
                    <div style={{display:'flex',flexDirection:'column',padding:'4px 12px'}}>
                      <div style={{display:'flex',alignItems:'center',gap:8}}>
                        {hrFieldEditorEditingTitle ? (<>
                          <input
                            autoFocus
                            value={hrFieldEditorTitleDraft}
                            onChange={e => setHrFieldEditorTitleDraft(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Escape') setHrFieldEditorEditingTitle(false)
                              if (e.key === 'Enter') {
                                const trimmed = hrFieldEditorTitleDraft.trim()
                                if (!trimmed || !hrFieldEditorTemplateId) return
                                setHrFieldEditorEditingTitle(false)
                                if (trimmed === feTemplate?.title) return
                                updateHrAdminTemplate(hrFieldEditorTemplateId, { title: trimmed })
                                  .then(() => setHrPortalTemplates((prev: any[]) => prev.map((t: any) => String(t.id) === hrFieldEditorTemplateId ? { ...t, title: trimmed } : t)))
                                  .catch((err: any) => alert('Failed to rename: ' + err.message))
                              }
                            }}
                            style={{fontSize:'0.85rem',fontWeight:600,color:'#1e293b',margin:0,border:'1px solid #2563eb',borderRadius:4,padding:'1px 6px',outline:'none',background:'#eff6ff',width:220}}
                          />
                          <button onClick={() => setHrFieldEditorEditingTitle(false)} style={{padding:'2px 8px',fontSize:'0.72rem',fontWeight:500,borderRadius:4,border:'1px solid #d1d5db',background:'#fff',color:'#64748b',cursor:'pointer'}}>Cancel</button>
                          <button onClick={() => {
                            const trimmed = hrFieldEditorTitleDraft.trim()
                            if (!trimmed || !hrFieldEditorTemplateId) return
                            setHrFieldEditorEditingTitle(false)
                            if (trimmed === feTemplate?.title) return
                            updateHrAdminTemplate(hrFieldEditorTemplateId, { title: trimmed })
                              .then(() => setHrPortalTemplates((prev: any[]) => prev.map((t: any) => String(t.id) === hrFieldEditorTemplateId ? { ...t, title: trimmed } : t)))
                              .catch((err: any) => alert('Failed to rename: ' + err.message))
                          }} style={{padding:'2px 8px',fontSize:'0.72rem',fontWeight:600,borderRadius:4,border:'none',background:'#2563eb',color:'#fff',cursor:'pointer'}}>Save</button>
                        </>) : (
                          <h2
                            style={{fontSize:'0.85rem',fontWeight:600,color:'#1e293b',margin:0,maxWidth:300,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',cursor:'pointer',borderRadius:4,padding:'1px 4px'}}
                            title="Click to rename"
                            onClick={() => { setHrFieldEditorTitleDraft(feTemplate?.title || ''); setHrFieldEditorEditingTitle(true) }}
                            onMouseEnter={e => (e.currentTarget.style.background = '#f1f5f9')}
                            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                          >
                            {feTemplate?.title || 'Template'}
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" style={{marginLeft:6,verticalAlign:'middle'}}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </h2>
                        )}
                      </div>
                      <div style={{display:'flex',alignItems:'center',gap:8,fontSize:'0.72rem',color:'#6b7280'}}>
                        <span>Edit Mode</span>
                        {hrFieldEditorFields.length > 0 && <span>{'\u2022'} {hrFieldEditorFields.length} field{hrFieldEditorFields.length !== 1 ? 's' : ''}</span>}
                      </div>
                    </div>
                  </div>
                  {/* Right: AI AutoMap + Settings + Close */}
                  <div style={{display:'flex',alignItems:'center',gap:8,paddingRight:12}}>
                    {/* AI AutoMap */}
                    <button onClick={async () => {
                      if (!hrFieldEditorTemplateId || hrFieldEditorAutoMapping) return
                      setHrFieldEditorAutoMapping(true)
                      setHrFieldEditorAutoMapStatus('Starting AI analysis...')
                      try {
                        const resp = await fetch(`/api/hr-admin/templates/${hrFieldEditorTemplateId}/automap`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
                        if (!resp.ok && resp.headers.get('content-type')?.includes('text/event-stream') === false) { const err = await resp.json().catch(() => ({})); throw new Error((err as any).error || 'AutoMap failed') }
                        const reader = resp.body?.getReader()
                        if (!reader) throw new Error('No stream')
                        const decoder = new TextDecoder()
                        let buf = ''
                        while (true) {
                          const { done, value } = await reader.read()
                          if (done) break
                          buf += decoder.decode(value, { stream: true })
                          const lines = buf.split('\n')
                          buf = lines.pop() || ''
                          for (const line of lines) {
                            if (line.startsWith('data: ')) {
                              try {
                                const parsed = JSON.parse(line.slice(6))
                                if (parsed.stage) setHrFieldEditorAutoMapStatus(parsed.message || parsed.stage)
                                if (parsed.success && parsed.fields) {
                                  setHrFieldEditorHistory((h: any[][]) => [...h, hrFieldEditorFields])
                                  setHrFieldEditorFuture([])
                                  setHrFieldEditorFields(parsed.fields)
                                  setHrFieldEditorAutoMapStatus(`Mapped ${parsed.stats?.totalMapped || parsed.fields.length} fields`)
                                }
                                if (parsed.error) { setHrFieldEditorAutoMapStatus('Error: ' + parsed.error); return }
                              } catch (pe) { console.error('SSE parse:', pe) }
                            }
                          }
                        }
                      } catch (e: any) {
                        setHrFieldEditorAutoMapStatus('Error: ' + e.message)
                        setTimeout(() => setHrFieldEditorAutoMapStatus(''), 4000)
                      } finally {
                        setHrFieldEditorAutoMapping(false)
                        setTimeout(() => setHrFieldEditorAutoMapStatus(''), 3000)
                      }
                    }} disabled={hrFieldEditorAutoMapping}
                      style={{display:'inline-flex',alignItems:'center',gap:6,padding:'6px 14px',fontSize:'0.78rem',fontWeight:600,borderRadius:6,border:'1px solid #c084fc',color: hrFieldEditorAutoMapping ? '#a78bfa' : '#7c3aed',background: hrFieldEditorAutoMapping ? '#f5f3ff' : '#faf5ff',cursor: hrFieldEditorAutoMapping ? 'wait' : 'pointer',boxShadow:'0 1px 2px rgba(0,0,0,0.05)',opacity: hrFieldEditorAutoMapping ? 0.7 : 1}}
                      onMouseEnter={e => { if (!hrFieldEditorAutoMapping) { e.currentTarget.style.background='#ede9fe'; e.currentTarget.style.borderColor='#a78bfa' } }}
                      onMouseLeave={e => { e.currentTarget.style.background= hrFieldEditorAutoMapping ? '#f5f3ff' : '#faf5ff'; e.currentTarget.style.borderColor='#c084fc' }}>
                      {hrFieldEditorAutoMapping ? (
                        <div style={{width:14,height:14,border:'2px solid #c084fc',borderTop:'2px solid #7c3aed',borderRadius:'50%',animation:'spin 1s linear infinite'}} />
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
                      )}
                      <span>{hrFieldEditorAutoMapping ? hrFieldEditorAutoMapStatus || 'Analyzing...' : 'AI AutoMap'}</span>
                    </button>
                    {/* Settings */}
                    <button onClick={() => { setHrFieldEditorShowSettings(!hrFieldEditorShowSettings); if (!hrFieldEditorShowSettings) { const t = feTemplate; setHrFieldEditorSettingsForm({ title: t?.title || '', description: t?.description || '', category: t?.category || '', requiresSignature: !!(t?.requires_signature), requiredForOnboarding: !!(t?.required_for_onboarding) }) } }}
                      style={{display:'inline-flex',alignItems:'center',gap:6,padding:'6px 14px',fontSize:'0.78rem',fontWeight:600,borderRadius:6,border: hrFieldEditorShowSettings ? '1px solid #2563eb' : '1px solid #d1d5db',color: hrFieldEditorShowSettings ? '#2563eb' : '#1e293b',background: hrFieldEditorShowSettings ? '#eff6ff' : '#fff',cursor:'pointer',boxShadow:'0 1px 2px rgba(0,0,0,0.05)'}}
                      onMouseEnter={e => { if (!hrFieldEditorShowSettings) { e.currentTarget.style.background='#f1f5f9'; e.currentTarget.style.borderColor='#9ca3af' } }}
                      onMouseLeave={e => { if (!hrFieldEditorShowSettings) { e.currentTarget.style.background='#fff'; e.currentTarget.style.borderColor='#d1d5db' } }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                      <span>Settings</span>
                    </button>
                    {/* Close */}
                    <button onClick={() => setHrFieldEditorOpen(false)} style={{display:'inline-flex',alignItems:'center',gap:6,padding:'6px 14px',fontSize:'0.78rem',fontWeight:600,borderRadius:6,border:'1px solid #d1d5db',color:'#1e293b',background:'#fff',cursor:'pointer',boxShadow:'0 1px 2px rgba(0,0,0,0.05)'}}
                      onMouseEnter={e => { e.currentTarget.style.background='#f1f5f9'; e.currentTarget.style.borderColor='#9ca3af' }}
                      onMouseLeave={e => { e.currentTarget.style.background='#fff'; e.currentTarget.style.borderColor='#d1d5db' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                      <span>Close</span>
                    </button>
                  </div>
                </nav>

                {/* ===== SECONDARY TOOLBAR ===== */}
                <div style={{display:'flex',flexWrap:'wrap',alignItems:'center',gap:4,padding:'4px 8px',borderBottom:'1px solid #d1d5db',background:'linear-gradient(to bottom, #f9fafb, #f3f4f6)',flexShrink:0}}>
                  {/* Page Info */}
                  {hrFieldEditorNumPages > 0 && (
                    <div style={{display:'flex',alignItems:'center',padding:'4px 8px',fontSize:'0.82rem',color:'#6b7280'}}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" style={{marginRight:6}}><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/></svg>
                      <span>{hrFieldEditorNumPages} {hrFieldEditorNumPages === 1 ? 'page' : 'pages'}</span>
                    </div>
                  )}
                  {/* Divider */}
                  <div style={{height:20,width:1,background:'#d1d5db',margin:'0 4px'}} />
                  {/* Undo */}
                  <button disabled={hrFieldEditorHistory.length === 0} onClick={() => {
                    if (hrFieldEditorHistory.length === 0) return
                    const prev = hrFieldEditorHistory[hrFieldEditorHistory.length - 1]
                    setHrFieldEditorFuture((f: any[][]) => [hrFieldEditorFields, ...f])
                    setHrFieldEditorFields(prev)
                    setHrFieldEditorHistory((h: any[][]) => h.slice(0, -1))
                    setHrFieldEditorSelected(null)
                    setHrFieldEditorSidebarSection((p: Record<string,boolean>) => ({ ...p, fieldTypes: true }))
                  }} style={{display:'flex',height:28,minWidth:28,alignItems:'center',justifyContent:'center',borderRadius:4,border:'1px solid transparent',padding:'0 6px',color: hrFieldEditorHistory.length > 0 ? '#374151' : '#d1d5db',background:'transparent',cursor: hrFieldEditorHistory.length > 0 ? 'pointer' : 'not-allowed'}} title="Undo">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
                  </button>
                  {/* Redo */}
                  <button disabled={hrFieldEditorFuture.length === 0} onClick={() => {
                    if (hrFieldEditorFuture.length === 0) return
                    const next = hrFieldEditorFuture[0]
                    setHrFieldEditorHistory((h: any[][]) => [...h, hrFieldEditorFields])
                    setHrFieldEditorFields(next)
                    setHrFieldEditorFuture((f: any[][]) => f.slice(1))
                    setHrFieldEditorSelected(null)
                    setHrFieldEditorSidebarSection((p: Record<string,boolean>) => ({ ...p, fieldTypes: true }))
                  }} style={{display:'flex',height:28,minWidth:28,alignItems:'center',justifyContent:'center',borderRadius:4,border:'1px solid transparent',padding:'0 6px',color: hrFieldEditorFuture.length > 0 ? '#374151' : '#d1d5db',background:'transparent',cursor: hrFieldEditorFuture.length > 0 ? 'pointer' : 'not-allowed'}} title="Redo">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.13-9.36L23 10"/></svg>
                  </button>
                  {/* Divider */}
                  <div style={{height:20,width:1,background:'#d1d5db',margin:'0 4px'}} />
                  {/* Mode indicator */}
                  <div style={{display:'flex',alignItems:'center',gap:6,padding:'4px 10px',borderRadius:4,fontSize:'0.72rem',fontWeight:500,background:'rgba(37,99,235,0.1)',color:'#2563eb'}}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    Editing
                  </div>
                  {/* Spacer */}
                  <div style={{flex:1}} />
                  {/* Keyboard hints */}
                  <div style={{display:'flex',alignItems:'center',gap:8,fontSize:'0.7rem',color:'#9ca3af',paddingRight:4}}>
                    <span>{'\u2191\u2193\u2190\u2192'} nudge</span>
                    <span>{'\u2022'}</span>
                    <span>Del remove</span>
                    <span>{'\u2022'}</span>
                    <span>Ctrl+D duplicate</span>
                  </div>
                </div>

                {/* ===== MAIN CONTENT: SETTINGS (left) + PDF (center) + SIDEBAR (right) ===== */}
                <div style={{flex:1,display:'flex',overflow:'hidden'}}>

                  {/* ===== LEFT SETTINGS PANEL ===== */}
                  {hrFieldEditorShowSettings && (
                    <div style={{width:320,borderRight:'1px solid #e5e7eb',background:'#fff',display:'flex',flexDirection:'column',flexShrink:0,overflowY:'auto'}}>
                      <div style={{padding:20}}>
                        <h3 style={{margin:'0 0 16px',fontSize:'0.95rem',fontWeight:700,color:'#1e293b'}}>Template Settings</h3>
                        {/* Title */}
                        <div style={{marginBottom:14}}>
                          <label style={{display:'block',fontSize:'0.72rem',fontWeight:600,color:'#6b7280',marginBottom:4}}>Title</label>
                          <input type="text" value={hrFieldEditorSettingsForm.title || ''} onChange={e => setHrFieldEditorSettingsForm((p: any) => ({ ...p, title: e.target.value }))}
                            style={{width:'100%',padding:'8px 10px',border:'1px solid #d1d5db',borderRadius:6,fontSize:'0.82rem',boxSizing:'border-box'}} />
                        </div>
                        {/* Description */}
                        <div style={{marginBottom:14}}>
                          <label style={{display:'block',fontSize:'0.72rem',fontWeight:600,color:'#6b7280',marginBottom:4}}>Description</label>
                          <textarea value={hrFieldEditorSettingsForm.description || ''} onChange={e => setHrFieldEditorSettingsForm((p: any) => ({ ...p, description: e.target.value }))}
                            rows={2} style={{width:'100%',padding:'8px 10px',border:'1px solid #d1d5db',borderRadius:6,fontSize:'0.82rem',resize:'vertical',boxSizing:'border-box'}} />
                        </div>
                        {/* Category */}
                        <div style={{marginBottom:14}}>
                          <label style={{display:'block',fontSize:'0.72rem',fontWeight:600,color:'#6b7280',marginBottom:4}}>Category</label>
                          <select value={hrFieldEditorSettingsForm.category || ''} onChange={e => setHrFieldEditorSettingsForm((p: any) => ({ ...p, category: e.target.value }))}
                            style={{width:'100%',padding:'8px 10px',border:'1px solid #d1d5db',borderRadius:6,fontSize:'0.82rem',background:'#fff',boxSizing:'border-box'}}>
                            <option value="">Select category</option>
                            {['W-4','W-9','I-9','NDA','Handbook','Direct Deposit','Drug Test','Background Check','Non-Compete','Policy','Training','Benefits','Performance','Termination','Other'].map(c => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                          </select>
                        </div>
                        {/* Toggles */}
                        <div style={{marginBottom:14,display:'flex',flexDirection:'column',gap:10}}>
                          <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',padding:'8px 10px',border:'1px solid #e5e7eb',borderRadius:8,background: hrFieldEditorSettingsForm.requiresSignature ? '#eff6ff' : '#fff'}}>
                            <input type="checkbox" checked={hrFieldEditorSettingsForm.requiresSignature || false} onChange={e => setHrFieldEditorSettingsForm((p: any) => ({ ...p, requiresSignature: e.target.checked }))}
                              style={{accentColor:'#2563eb',width:16,height:16}} />
                            <div>
                              <div style={{fontSize:'0.78rem',fontWeight:600,color:'#1e293b'}}>Requires Signature</div>
                              <div style={{fontSize:'0.65rem',color:'#6b7280'}}>Enable signing workflow</div>
                            </div>
                          </label>
                          <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',padding:'8px 10px',border:'1px solid #e5e7eb',borderRadius:8,background: hrFieldEditorSettingsForm.requiredForOnboarding ? '#f0fdf4' : '#fff'}}>
                            <input type="checkbox" checked={hrFieldEditorSettingsForm.requiredForOnboarding || false} onChange={e => setHrFieldEditorSettingsForm((p: any) => ({ ...p, requiredForOnboarding: e.target.checked }))}
                              style={{accentColor:'#16a34a',width:16,height:16}} />
                            <div>
                              <div style={{fontSize:'0.78rem',fontWeight:600,color:'#1e293b'}}>Required for Onboarding</div>
                              <div style={{fontSize:'0.65rem',color:'#6b7280'}}>Auto-assign to new hires</div>
                            </div>
                          </label>
                        </div>
                        {/* Workflow Configuration */}
                        {hrFieldEditorSettingsForm.requiresSignature && (
                          <div style={{marginBottom:14,border:'1px solid #e5e7eb',borderRadius:8,overflow:'hidden'}}>
                            <div style={{padding:'10px 12px',background:'#f9fafb',borderBottom:'1px solid #e5e7eb'}}>
                              <div style={{fontSize:'0.78rem',fontWeight:700,color:'#1e293b',marginBottom:6}}>Workflow Configuration</div>
                              <div style={{display:'flex',gap:4}}>
                                <button onClick={() => setHrFieldEditorWorkflowMode('single_signer')}
                                  style={{flex:1,padding:'6px 0',fontSize:'0.72rem',fontWeight:600,borderRadius:4,border:'1px solid',cursor:'pointer',
                                    background: hrFieldEditorWorkflowMode === 'single_signer' ? '#2563eb' : '#fff',
                                    color: hrFieldEditorWorkflowMode === 'single_signer' ? '#fff' : '#6b7280',
                                    borderColor: hrFieldEditorWorkflowMode === 'single_signer' ? '#2563eb' : '#d1d5db'}}>
                                  Single Signer
                                </button>
                                <button onClick={() => { setHrFieldEditorWorkflowMode('multi_signer'); if (hrFieldEditorWorkflowSigners.length === 0) setHrFieldEditorWorkflowSigners([{ order: 1, signerType: 'assignee', roleName: 'Employee' }, { order: 2, signerType: 'creating_admin', roleName: 'HR Admin' }]) }}
                                  style={{flex:1,padding:'6px 0',fontSize:'0.72rem',fontWeight:600,borderRadius:4,border:'1px solid',cursor:'pointer',
                                    background: hrFieldEditorWorkflowMode === 'multi_signer' ? '#2563eb' : '#fff',
                                    color: hrFieldEditorWorkflowMode === 'multi_signer' ? '#fff' : '#6b7280',
                                    borderColor: hrFieldEditorWorkflowMode === 'multi_signer' ? '#2563eb' : '#d1d5db'}}>
                                  Multi-Signer
                                </button>
                              </div>
                            </div>
                            {hrFieldEditorWorkflowMode === 'multi_signer' && (
                              <div style={{padding:12}}>
                                {hrFieldEditorWorkflowSigners.map((s, i) => (
                                  <div key={i} style={{display:'flex',alignItems:'center',gap:6,marginBottom:8,padding:'8px 10px',border:'1px solid #e5e7eb',borderRadius:6,background:'#f9fafb'}}>
                                    <span style={{width:20,height:20,borderRadius:'50%',background:signerBorderColors[i] || '#9ca3af',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:'0.6rem',fontWeight:700,flexShrink:0}}>{i+1}</span>
                                    <div style={{flex:1}}>
                                      <input type="text" value={s.roleName} onChange={e => setHrFieldEditorWorkflowSigners(prev => prev.map((ws, wi) => wi === i ? { ...ws, roleName: e.target.value } : ws))}
                                        style={{width:'100%',padding:'3px 6px',border:'1px solid #d1d5db',borderRadius:4,fontSize:'0.72rem',marginBottom:4,boxSizing:'border-box'}} />
                                      <select value={s.signerType} onChange={e => setHrFieldEditorWorkflowSigners(prev => prev.map((ws, wi) => wi === i ? { ...ws, signerType: e.target.value } : ws))}
                                        style={{width:'100%',padding:'3px 6px',border:'1px solid #d1d5db',borderRadius:4,fontSize:'0.68rem',background:'#fff',boxSizing:'border-box'}}>
                                        <option value="assignee">Assignee (Employee)</option>
                                        <option value="creating_admin">Creating Admin (HR)</option>
                                        <option value="assignees_manager">Assignee's Manager</option>
                                        <option value="specific_person">Specific Person</option>
                                      </select>
                                    </div>
                                    {hrFieldEditorWorkflowSigners.length > 1 && (
                                      <button onClick={() => setHrFieldEditorWorkflowSigners(prev => prev.filter((_, wi) => wi !== i).map((ws, wi) => ({ ...ws, order: wi + 1 })))}
                                        style={{border:'none',background:'none',cursor:'pointer',color:'#d1d5db',padding:2}} title="Remove">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                                      </button>
                                    )}
                                  </div>
                                ))}
                                {hrFieldEditorWorkflowSigners.length < 4 && (
                                  <button onClick={() => setHrFieldEditorWorkflowSigners(prev => [...prev, { order: prev.length + 1, signerType: 'assignee', roleName: `Signer ${prev.length + 1}` }])}
                                    style={{width:'100%',padding:'6px 0',border:'1px dashed #d1d5db',borderRadius:6,background:'#fff',color:'#6b7280',fontSize:'0.72rem',fontWeight:600,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:4}}
                                    onMouseEnter={e => { e.currentTarget.style.borderColor='#2563eb'; e.currentTarget.style.color='#2563eb' }}
                                    onMouseLeave={e => { e.currentTarget.style.borderColor='#d1d5db'; e.currentTarget.style.color='#6b7280' }}>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                                    Add Signer
                                  </button>
                                )}
                                {/* Preview */}
                                <div style={{marginTop:10,padding:'8px 10px',background:'#f0f9ff',border:'1px solid #bae6fd',borderRadius:6}}>
                                  <div style={{fontSize:'0.65rem',fontWeight:600,color:'#0369a1',marginBottom:4}}>Signing Order</div>
                                  <div style={{fontSize:'0.72rem',color:'#0c4a6e'}}>{hrFieldEditorWorkflowSigners.map(s => s.roleName).join(' → ')}</div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        {/* Save / Cancel */}
                        <div style={{display:'flex',gap:8,marginTop:8}}>
                          <button onClick={() => setHrFieldEditorShowSettings(false)}
                            style={{flex:1,padding:'8px 0',borderRadius:6,border:'1px solid #d1d5db',background:'#fff',color:'#6b7280',fontSize:'0.78rem',fontWeight:600,cursor:'pointer'}}>Cancel</button>
                          <button disabled={hrFieldEditorSavingSettings} onClick={async () => {
                            if (!hrFieldEditorTemplateId) return
                            setHrFieldEditorSavingSettings(true)
                            try {
                              const payload: any = {
                                title: hrFieldEditorSettingsForm.title,
                                description: hrFieldEditorSettingsForm.description,
                                category: hrFieldEditorSettingsForm.category,
                                requires_signature: hrFieldEditorSettingsForm.requiresSignature,
                                required_for_onboarding: hrFieldEditorSettingsForm.requiredForOnboarding,
                              }
                              if (hrFieldEditorSettingsForm.requiresSignature) {
                                payload.default_signer_config = { mode: hrFieldEditorWorkflowMode, signers: hrFieldEditorWorkflowMode === 'multi_signer' ? hrFieldEditorWorkflowSigners : undefined }
                              }
                              await updateHrAdminTemplate(hrFieldEditorTemplateId, payload)
                              setHrPortalTemplates((prev: any[]) => prev.map((t: any) =>
                                String(t.id) === hrFieldEditorTemplateId ? { ...t, ...payload, requires_signature: payload.requires_signature ? 1 : 0, required_for_onboarding: payload.required_for_onboarding ? 1 : 0 } : t
                              ))
                              setHrFieldEditorShowSettings(false)
                            } catch (e: any) {
                              alert('Failed to save settings: ' + e.message)
                            } finally {
                              setHrFieldEditorSavingSettings(false)
                            }
                          }} style={{flex:1,padding:'8px 0',borderRadius:6,border:'none',background: hrFieldEditorSavingSettings ? '#93c5fd' : '#2563eb',color:'#fff',fontSize:'0.78rem',fontWeight:600,cursor: hrFieldEditorSavingSettings ? 'wait' : 'pointer'}}>
                            {hrFieldEditorSavingSettings ? 'Saving...' : 'Save Settings'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* PDF Viewer Container */}
                  <div style={{flex:1,position:'relative'}}>
                    {/* Floating Zoom Controls */}
                    <div style={{position:'absolute',bottom:16,left:16,zIndex:30,display:'flex',alignItems:'center'}}>
                      <div style={{display:'flex',alignItems:'center',background:'#fff',borderRadius:6,border:'1px solid #d1d5db',boxShadow:'0 4px 6px -1px rgba(0,0,0,0.1)'}}>
                        <button onClick={() => setHrFieldEditorZoom(Math.max(0.5, zoom - 0.25))} disabled={zoom <= 0.5}
                          style={{display:'inline-flex',alignItems:'center',justifyContent:'center',padding:8,color:'#6b7280',background:'transparent',border:'none',borderRight:'1px solid #e5e7eb',borderRadius:'6px 0 0 6px',cursor: zoom <= 0.5 ? 'not-allowed' : 'pointer',opacity: zoom <= 0.5 ? 0.4 : 1}}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14"/></svg>
                        </button>
                        <span style={{padding:'0 12px',fontSize:'0.72rem',fontWeight:500,color:'#374151',minWidth:52,textAlign:'center',userSelect:'none'}}>
                          {Math.round(zoom * 100)}%
                        </span>
                        <button onClick={() => setHrFieldEditorZoom(Math.min(2.0, zoom + 0.25))} disabled={zoom >= 2.0}
                          style={{display:'inline-flex',alignItems:'center',justifyContent:'center',padding:8,color:'#6b7280',background:'transparent',border:'none',borderLeft:'1px solid #e5e7eb',borderRadius:'0 6px 6px 0',cursor: zoom >= 2.0 ? 'not-allowed' : 'pointer',opacity: zoom >= 2.0 ? 0.4 : 1}}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                        </button>
                      </div>
                    </div>

                    {/* Scrollable PDF Area */}
                    <div style={{position:'absolute',inset:0,background:'#e5e7eb',overflow:'auto'}}>
                      {hrFieldEditorLoading ? (
                        <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%',color:'#6b7280'}}>
                          <div style={{textAlign:'center'}}>
                            <div style={{width:48,height:48,border:'3px solid #e5e7eb',borderTop:'3px solid #2563eb',borderRadius:'50%',animation:'spin 1s linear infinite',margin:'0 auto 16px'}} />
                            <p>Loading PDF...</p>
                          </div>
                        </div>
                      ) : (
                        <div style={{padding:16,display:'inline-block',minWidth:'100%',boxSizing:'border-box'}}>
                          <div style={{width: `${zoom * 100}%`,maxWidth: zoom > 1 ? 'none' : '100%',margin:'0 auto'}}>
                            {hrFieldEditorPages.map((page: any, pageIdx: number) => (
                              <div key={pageIdx} id={`hr-fe-page-${pageIdx + 1}`}
                                style={{position:'relative',marginBottom:16,boxShadow:'0 2px 16px rgba(0,0,0,0.2)',borderRadius:4,width:'100%',cursor: hrFieldEditorTool ? 'crosshair' : 'default'}}
                                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy' }}
                                onDrop={(e) => {
                                  e.preventDefault()
                                  const droppedType = e.dataTransfer.getData('fieldType')
                                  if (!droppedType) return
                                  const rect = e.currentTarget.getBoundingClientRect()
                                  const dropXPct = ((e.clientX - rect.left) / rect.width) * 100
                                  const dropYPct = ((e.clientY - rect.top) / rect.height) * 100
                                  const dim = fieldDims[droppedType] || {w:15,h:5}
                                  const x = Math.max(0, Math.min(100 - dim.w, dropXPct - dim.w / 2))
                                  const y = Math.max(0, Math.min(100 - dim.h, dropYPct - dim.h / 2))
                                  const newField = {
                                    id: `field_${Date.now()}_${Math.random().toString(36).slice(2,6)}`,
                                    type: droppedType,
                                    pageNumber: pageIdx + 1, x, y,
                                    width: dim.w, height: dim.h,
                                    label: droppedType.charAt(0).toUpperCase() + droppedType.slice(1) + ' ' + (hrFieldEditorFields.filter((f: any) => f.type === droppedType).length + 1),
                                    required: true,
                                  }
                                  setHrFieldEditorHistory((h: any[][]) => [...h, hrFieldEditorFields])
                                  setHrFieldEditorFuture([])
                                  setHrFieldEditorFields((prev: any[]) => [...prev, newField])
                                  setHrFieldEditorSelected(newField.id)
                                  setHrFieldEditorTool(null)
                                  setHrFieldEditorSidebarSection((prev: Record<string,boolean>) => { const next: Record<string,boolean> = {}; for (const k of Object.keys(prev)) next[k] = k === 'properties'; return next })
                                }}
                                onClick={(e) => {
                                  if (!hrFieldEditorTool) { setHrFieldEditorSelected(null); setHrFieldEditorSidebarSection((p: Record<string,boolean>) => ({ ...p, fieldTypes: true })); return }
                                  const rect = e.currentTarget.getBoundingClientRect()
                                  const clickXPct = ((e.clientX - rect.left) / rect.width) * 100
                                  const clickYPct = ((e.clientY - rect.top) / rect.height) * 100
                                  const dim = fieldDims[hrFieldEditorTool] || {w:15,h:5}
                                  const x = Math.max(0, Math.min(100 - dim.w, clickXPct - dim.w / 2))
                                  const y = Math.max(0, Math.min(100 - dim.h, clickYPct - dim.h / 2))
                                  const newField = {
                                    id: `field_${Date.now()}_${Math.random().toString(36).slice(2,6)}`,
                                    type: hrFieldEditorTool,
                                    pageNumber: pageIdx + 1, x, y,
                                    width: dim.w, height: dim.h,
                                    label: hrFieldEditorTool.charAt(0).toUpperCase() + hrFieldEditorTool.slice(1) + ' ' + (hrFieldEditorFields.filter((f: any) => f.type === hrFieldEditorTool).length + 1),
                                    required: true,
                                  }
                                  setHrFieldEditorHistory((h: any[][]) => [...h, hrFieldEditorFields])
                                  setHrFieldEditorFuture([])
                                  setHrFieldEditorFields((prev: any[]) => [...prev, newField])
                                  setHrFieldEditorSelected(newField.id)
                                  setHrFieldEditorSidebarSection((prev: Record<string,boolean>) => { const next: Record<string,boolean> = {}; for (const k of Object.keys(prev)) next[k] = k === 'properties'; return next })
                                }}
                                onMouseDown={(e) => {
                                  if (hrFieldEditorTool) return
                                  if ((e.target as HTMLElement).closest('.hr-sign-field')) return
                                  const rect = e.currentTarget.getBoundingClientRect()
                                  const x = ((e.clientX - rect.left) / rect.width) * 100
                                  const y = ((e.clientY - rect.top) / rect.height) * 100
                                  setHrFieldEditorMarquee({ startX: x, startY: y, currentX: x, currentY: y, pageIdx })
                                }}>
                                {/* Page number badge */}
                                <div style={{position:'absolute',top:8,right:8,background:'rgba(0,0,0,0.6)',color:'#fff',fontSize:'0.68rem',padding:'2px 8px',borderRadius:4,zIndex:20,pointerEvents:'none'}}>
                                  Page {pageIdx + 1} of {hrFieldEditorNumPages}
                                </div>
                                <img src={page.dataUrl} alt={`Page ${pageIdx+1}`} style={{width:'100%',height:'auto',display:'block',borderRadius:4}} />
                                {/* Field overlays */}
                                <div style={{position:'absolute',top:0,left:0,width:'100%',height:'100%',pointerEvents:'auto'}}>
                                  {hrFieldEditorFields.filter((f: any) => (f.pageNumber || f.page) === pageIdx + 1).map((field: any) => {
                                    const isSelected = hrFieldEditorSelected === field.id
                                    const isMarqueeHighlighted = hrFieldEditorMarquee && hrFieldEditorMarquee.pageIdx === pageIdx && field.type === 'checkbox' && (() => {
                                      const m = hrFieldEditorMarquee!
                                      const minX = Math.min(m.startX, m.currentX), maxX = Math.max(m.startX, m.currentX)
                                      const minY = Math.min(m.startY, m.currentY), maxY = Math.max(m.startY, m.currentY)
                                      return field.x < maxX && (field.x + field.width) > minX && field.y < maxY && (field.y + field.height) > minY
                                    })()
                                    // Separator fields render as plain text
                                    if (field.staticValue) {
                                      return (
                                        <div key={field.id} style={{position:'absolute',left:`${field.x}%`,top:`${field.y}%`,width:`${field.width}%`,height:`${field.height}%`,display:'flex',alignItems:'center',justifyContent:'center',pointerEvents:'none',userSelect:'none'}}>
                                          <span style={{color:'#1e293b',fontWeight:700,fontSize:'0.82rem',lineHeight:1}}>{field.staticValue}</span>
                                        </div>
                                      )
                                    }
                                    return (
                                      <div key={field.id} className="hr-sign-field"
                                        style={{
                                          position:'absolute',
                                          left:`${field.x}%`, top:`${field.y}%`,
                                          width:`${field.width}%`, height:`${field.height}%`,
                                          background: getFieldBg(field),
                                          border: isSelected ? `2px solid ${getFieldBorder(field) || '#2563eb'}` : (field.checkboxGroup ? (field.checkboxGroupMin ?? 1) >= 1 : field.required !== false) ? '2.5px solid #ef4444' : `1.5px solid ${getFieldBorder(field) || 'rgba(255,255,255,0.7)'}`,
                                          borderRadius: 4,
                                          opacity: isSelected ? 0.7 : (isMarqueeHighlighted ? 0.8 : 0.5),
                                          cursor: 'move',
                                          userSelect: 'none',
                                          transition: 'opacity 0.15s, box-shadow 0.15s, outline 0.1s',
                                          boxShadow: isSelected ? `0 0 0 3px ${getFieldBg(field)}` : 'none',
                                          outline: isMarqueeHighlighted ? '3px solid #7c3aed' : 'none',
                                          outlineOffset: isMarqueeHighlighted ? 1 : 0,
                                          zIndex: isSelected ? 20 : isMarqueeHighlighted ? 15 : 10,
                                          overflow: 'visible',
                                        }}
                                        onClick={(e) => { e.stopPropagation(); setHrFieldEditorSelected(field.id); setHrFieldEditorSidebarSection((prev: Record<string,boolean>) => { const next: Record<string,boolean> = {}; for (const k of Object.keys(prev)) next[k] = k === 'properties'; return next }) }}
                                        onMouseDown={(e) => {
                                          e.stopPropagation()
                                          setHrFieldEditorSelected(field.id)
                                          setHrFieldEditorHistory((h: any[][]) => [...h, hrFieldEditorFields])
                                          setHrFieldEditorFuture([])
                                          setHrFieldEditorDragging({
                                            id: field.id, startX: e.clientX, startY: e.clientY,
                                            fieldX: field.x, fieldY: field.y,
                                            pageNumber: field.pageNumber || field.page || 1,
                                          })
                                        }}
                                        onMouseEnter={e => { if (!isSelected) e.currentTarget.style.opacity = '0.65' }}
                                        onMouseLeave={e => { if (!isSelected) e.currentTarget.style.opacity = '0.5' }}
                                      >
                                        {/* Field content — only show label if box is big enough */}
                                        <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',pointerEvents:'none',overflow:'hidden',padding:'0 2px'}}>
                                          {field.type === 'checkbox' ? (
                                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" style={{filter:'drop-shadow(0 1px 1px rgba(0,0,0,0.4))'}}><polyline points="20 6 9 17 4 12"/></svg>
                                          ) : (
                                            <span style={{color:'#fff',textShadow:'0 1px 2px rgba(0,0,0,0.5)',fontSize:'0.55rem',fontFamily:(field.type==='signature'||field.type==='initials')?'serif':'inherit',fontStyle:(field.type==='signature'||field.type==='initials')?'italic':'normal',fontWeight:field.type==='initials'?700:400,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',maxWidth:'100%'}}>
                                              {field.label || field.type}
                                            </span>
                                          )}
                                        </div>
                                        {/* Delete button — only when selected */}
                                        {isSelected && (
                                          <button onClick={(e) => {
                                            e.stopPropagation()
                                            setHrFieldEditorHistory((prev: any[][]) => [...prev, hrFieldEditorFields])
                                            setHrFieldEditorFuture([])
                                            setHrFieldEditorFields((prev: any[]) => prev.filter((f: any) => f.id !== field.id))
                                            setHrFieldEditorSelected(null)
                                            setHrFieldEditorSidebarSection((p: Record<string,boolean>) => ({ ...p, fieldTypes: true }))
                                          }} style={{position:'absolute',top: field.height < 3 ? -14 : -8, right: field.width < 5 ? -14 : -8, width:16,height:16,background:'#ef4444',color:'#fff',borderRadius:'50%',border:'2px solid #fff',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',zIndex:50,lineHeight:1,padding:0,boxShadow:'0 1px 3px rgba(0,0,0,0.3)'}}
                                            title="Delete field"
                                          >
                                            <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="4"><path d="M18 6L6 18M6 6l12 12"/></svg>
                                          </button>
                                        )}
                                        {/* Resize handle */}
                                        <div style={{position:'absolute',bottom:-2,right:-2,width:8,height:8,background:'#fff',border:'1px solid #6b7280',borderRadius:'2px 0 0 0',cursor:'nwse-resize',opacity: isSelected ? 1 : 0,transition:'opacity 0.15s',zIndex:10}}
                                          onMouseDown={(e) => {
                                            e.stopPropagation()
                                            setHrFieldEditorHistory((h: any[][]) => [...h, hrFieldEditorFields])
                                            setHrFieldEditorFuture([])
                                            setHrFieldEditorResizing({
                                              id: field.id, startX: e.clientX, startY: e.clientY,
                                              startWidth: field.width, startHeight: field.height,
                                              pageNumber: field.pageNumber || field.page || 1,
                                            })
                                          }} />
                                      </div>
                                    )
                                  })}
                                  {/* Checkbox group bounding boxes */}
                                  {checkboxGroups.map(groupName => {
                                    const groupFields = hrFieldEditorFields.filter((f: any) => f.checkboxGroup === groupName && (f.pageNumber || f.page) === pageIdx + 1)
                                    if (groupFields.length === 0) return null
                                    const pad = 0.5
                                    const left = Math.max(0, Math.min(...groupFields.map((f: any) => f.x)) - pad)
                                    const top = Math.max(0, Math.min(...groupFields.map((f: any) => f.y)) - pad)
                                    const right = Math.min(100, Math.max(...groupFields.map((f: any) => f.x + f.width)) + pad)
                                    const bottom = Math.min(100, Math.max(...groupFields.map((f: any) => f.y + f.height)) + pad)
                                    const minReq = groupFields[0]?.checkboxGroupMin ?? 0
                                    return (
                                      <div key={groupName} style={{position:'absolute',left:`${left}%`,top:`${top}%`,width:`${right-left}%`,height:`${bottom-top}%`,border:'2px dashed #a855f7',borderRadius:4,background:'rgba(168,85,247,0.05)',pointerEvents:'none',zIndex:5}}>
                                        <span style={{position:'absolute',top:-14,left:2,fontSize:'0.55rem',fontWeight:700,color:'#7e22ce',background:'#fff',border:'1px solid #d8b4fe',borderRadius:3,padding:'0 4px',whiteSpace:'nowrap'}}>
                                          {groupName}{minReq > 0 ? ` (min ${minReq})` : ''}
                                        </span>
                                      </div>
                                    )
                                  })}
                                  {/* Marquee selection rectangle */}
                                  {hrFieldEditorMarquee && hrFieldEditorMarquee.pageIdx === pageIdx && (() => {
                                    const m = hrFieldEditorMarquee
                                    const mLeft = Math.min(m.startX, m.currentX)
                                    const mTop = Math.min(m.startY, m.currentY)
                                    const mWidth = Math.abs(m.currentX - m.startX)
                                    const mHeight = Math.abs(m.currentY - m.startY)
                                    return (
                                      <div style={{
                                        position:'absolute',
                                        left:`${mLeft}%`, top:`${mTop}%`,
                                        width:`${mWidth}%`, height:`${mHeight}%`,
                                        border:'2px dashed #7c3aed',
                                        background:'rgba(124,58,237,0.08)',
                                        borderRadius:3,
                                        pointerEvents:'none',
                                        zIndex:100,
                                      }} />
                                    )
                                  })()}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* ===== RIGHT SIDEBAR ===== */}
                  <div style={{width:264,borderLeft:'1px solid #e5e7eb',background:'#f8fafc',display:'flex',flexDirection:'column',flexShrink:0,overflowY:'auto'}}>

                    {/* Signer Groups — always visible color legend */}
                    <div style={{borderBottom:'1px solid #e5e7eb'}}>
                      <button onClick={() => toggleFeSection('signerGroups')} style={{width:'100%',display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 16px',background:'#fff',border:'none',cursor:'pointer'}}
                        onMouseEnter={e => e.currentTarget.style.background='#f9fafb'}
                        onMouseLeave={e => e.currentTarget.style.background='#fff'}>
                        <span style={{fontWeight:600,color:'#1e293b',fontSize:'0.82rem'}}>Signer Groups</span>
                        <span style={{color:'#9ca3af',fontSize:'0.7rem'}}>{hrFieldEditorSidebarSection.signerGroups ? '\u25B2' : '\u25BC'}</span>
                      </button>
                      {hrFieldEditorSidebarSection.signerGroups && (
                        <div style={{padding:'8px 16px 12px',background:'#fff'}}>
                          {[
                            { idx: 0, label: 'Employee', color: '#2563eb' },
                            { idx: 1, label: 'Employer / HR Admin', color: '#16a34a' },
                            { idx: 2, label: 'Preparer / Translator', color: '#9333ea' },
                            ...hrFieldEditorCustomSigners,
                          ].map(sg => {
                            const count = hrFieldEditorFields.filter((f:any) => f.signerIndex === sg.idx).length
                            const isCustom = sg.idx >= 3
                            return (
                              <div key={sg.idx} style={{display:'flex',alignItems:'center',gap:8,padding:'5px 0',borderBottom:'1px solid #f3f4f6'}}>
                                <span style={{width:12,height:12,borderRadius:3,background:sg.color,flexShrink:0}} />
                                <span style={{fontSize:'0.78rem',fontWeight:600,color:'#1e293b',flex:1}}>{sg.label}</span>
                                <span style={{fontSize:'0.68rem',fontWeight:600,color:count > 0 ? sg.color : '#d1d5db',background:count > 0 ? `${sg.color}15` : '#f9fafb',padding:'1px 8px',borderRadius:10}}>{count}</span>
                                {isCustom && (
                                  <button onClick={() => {
                                    setHrFieldEditorCustomSigners(prev => prev.filter(s => s.idx !== sg.idx))
                                    setHrFieldEditorFields((prev: any[]) => prev.map((f: any) => f.signerIndex === sg.idx ? { ...f, signerIndex: undefined } : f))
                                  }} style={{border:'none',background:'none',cursor:'pointer',color:'#d1d5db',padding:0,display:'flex'}} title="Remove group">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                                  </button>
                                )}
                              </div>
                            )
                          })}
                          {(() => {
                            const unassigned = hrFieldEditorFields.filter((f:any) => f.signerIndex === undefined || f.signerIndex === null).length
                            return unassigned > 0 ? (
                              <div style={{display:'flex',alignItems:'center',gap:8,padding:'5px 0',borderBottom:'1px solid #f3f4f6'}}>
                                <span style={{width:12,height:12,borderRadius:3,background:'#9ca3af',flexShrink:0}} />
                                <span style={{fontSize:'0.78rem',color:'#6b7280',flex:1}}>Unassigned</span>
                                <span style={{fontSize:'0.68rem',fontWeight:600,color:'#9ca3af',background:'#f3f4f6',padding:'1px 8px',borderRadius:10}}>{unassigned}</span>
                              </div>
                            ) : null
                          })()}
                          {/* Add Group */}
                          {hrFieldEditorAddSignerOpen ? (
                            <div style={{marginTop:8,display:'flex',gap:4}}>
                              <input type="text" value={hrFieldEditorNewSignerName} onChange={e => setHrFieldEditorNewSignerName(e.target.value)}
                                placeholder="Group name..." autoFocus
                                onKeyDown={e => {
                                  if (e.key === 'Enter' && hrFieldEditorNewSignerName.trim()) {
                                    const colors = ['#ea580c','#ec4899','#0891b2','#65a30d','#dc2626','#7c3aed']
                                    const nextIdx = 3 + hrFieldEditorCustomSigners.length
                                    const color = colors[hrFieldEditorCustomSigners.length % colors.length]
                                    setHrFieldEditorCustomSigners(prev => [...prev, { idx: nextIdx, label: hrFieldEditorNewSignerName.trim(), color }])
                                    setHrFieldEditorNewSignerName('')
                                    setHrFieldEditorAddSignerOpen(false)
                                  } else if (e.key === 'Escape') {
                                    setHrFieldEditorAddSignerOpen(false)
                                    setHrFieldEditorNewSignerName('')
                                  }
                                }}
                                style={{flex:1,padding:'4px 8px',border:'1px solid #d1d5db',borderRadius:4,fontSize:'0.72rem'}} />
                              <button onClick={() => {
                                if (!hrFieldEditorNewSignerName.trim()) return
                                const colors = ['#ea580c','#ec4899','#0891b2','#65a30d','#dc2626','#7c3aed']
                                const nextIdx = 3 + hrFieldEditorCustomSigners.length
                                const color = colors[hrFieldEditorCustomSigners.length % colors.length]
                                setHrFieldEditorCustomSigners(prev => [...prev, { idx: nextIdx, label: hrFieldEditorNewSignerName.trim(), color }])
                                setHrFieldEditorNewSignerName('')
                                setHrFieldEditorAddSignerOpen(false)
                              }} style={{padding:'4px 8px',border:'none',background:'#2563eb',color:'#fff',borderRadius:4,fontSize:'0.68rem',fontWeight:600,cursor:'pointer'}}>Add</button>
                            </div>
                          ) : (
                            <button onClick={() => setHrFieldEditorAddSignerOpen(true)}
                              style={{marginTop:8,width:'100%',display:'flex',alignItems:'center',justifyContent:'center',gap:4,padding:'6px 0',border:'1px dashed #d1d5db',borderRadius:6,background:'#fff',color:'#6b7280',fontSize:'0.72rem',fontWeight:600,cursor:'pointer',transition:'all 0.15s'}}
                              onMouseEnter={e => { e.currentTarget.style.borderColor='#2563eb'; e.currentTarget.style.color='#2563eb' }}
                              onMouseLeave={e => { e.currentTarget.style.borderColor='#d1d5db'; e.currentTarget.style.color='#6b7280' }}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                              Add Group
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Checkbox Groups section (collapsible) */}
                    {checkboxGroups.length > 0 && (
                      <div style={{borderBottom:'1px solid #e5e7eb'}}>
                        <button onClick={() => toggleFeSection('checkboxGroups')} style={{width:'100%',display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 16px',background:'#fff',border:'none',cursor:'pointer',transition:'background 0.15s'}}
                          onMouseEnter={e => e.currentTarget.style.background='#f9fafb'}
                          onMouseLeave={e => e.currentTarget.style.background='#fff'}>
                          <span style={{fontWeight:600,color:'#1e293b',fontSize:'0.82rem'}}>Checkbox Groups <span style={{color:'#9ca3af',fontWeight:400}}>({checkboxGroups.length})</span></span>
                          <span style={{color:'#9ca3af',fontSize:'0.7rem'}}>{hrFieldEditorSidebarSection.checkboxGroups ? '\u25B2' : '\u25BC'}</span>
                        </button>
                        {hrFieldEditorSidebarSection.checkboxGroups && (
                          <div style={{padding:'0 12px 12px',background:'#fff'}}>
                            {checkboxGroups.map(groupName => {
                              const members = hrFieldEditorFields.filter((f: any) => f.checkboxGroup === groupName)
                              const min = members[0]?.checkboxGroupMin ?? 1
                              const max = members[0]?.checkboxGroupMax ?? 0
                              return (
                                <div key={groupName} style={{border:'1px solid #e9d5ff',borderRadius:8,background:'#faf5ff',marginBottom:8,overflow:'hidden'}}>
                                  <button onClick={() => setHrFieldEditorSidebarSection((prev: Record<string,boolean>) => ({ ...prev, [`cbg_${groupName}`]: !prev[`cbg_${groupName}`] }))}
                                    style={{width:'100%',display:'flex',alignItems:'center',justifyContent:'space-between',padding:'8px 10px',border:'none',background:'transparent',cursor:'pointer'}}>
                                    <div style={{display:'flex',alignItems:'center',gap:6}}>
                                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#7e22ce" strokeWidth="2.5"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                                      <span style={{fontSize:'0.75rem',fontWeight:600,color:'#7e22ce'}}>{groupName}</span>
                                    </div>
                                    <div style={{display:'flex',alignItems:'center',gap:6}}>
                                      <span style={{fontSize:'0.6rem',fontWeight:600,padding:'1px 6px',borderRadius:9999,background: min >= 1 ? '#fee2e2' : '#dcfce7',color: min >= 1 ? '#b91c1c' : '#15803d',border: `1px solid ${min >= 1 ? '#fca5a5' : '#86efac'}`}}>{min >= 1 ? 'Required' : 'Optional'}</span>
                                      <span style={{fontSize:'0.6rem',color:'#9ca3af',fontWeight:500}}>{members.length}</span>
                                      <svg width="10" height="10" viewBox="0 0 10 6" fill="#9ca3af" style={{transform: hrFieldEditorSidebarSection[`cbg_${groupName}`] ? 'rotate(180deg)' : 'none',transition:'transform 0.2s'}}><path d="M1 1l4 4 4-4"/></svg>
                                    </div>
                                  </button>
                                  {hrFieldEditorSidebarSection[`cbg_${groupName}`] && (
                                    <div style={{padding:'0 10px 8px'}}>
                                      <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:6,padding:'4px 0'}} onClick={e => e.stopPropagation()}>
                                        <button onClick={e => {
                                          e.stopPropagation()
                                          const newMin = min >= 1 ? 0 : 1
                                          setHrFieldEditorFields((prev: any[]) => prev.map((f: any) => f.checkboxGroup === groupName ? { ...f, checkboxGroupMin: newMin, required: newMin >= 1 } : f))
                                        }} style={{display:'flex',alignItems:'center',gap:4,padding:'3px 10px',borderRadius:9999,fontSize:'0.65rem',fontWeight:700,border:`1px solid ${min >= 1 ? '#fca5a5' : '#86efac'}`,background: min >= 1 ? '#fee2e2' : '#dcfce7',color: min >= 1 ? '#b91c1c' : '#15803d',cursor:'pointer',transition:'all 0.15s'}}>
                                          <span style={{width:6,height:6,borderRadius:'50%',background: min >= 1 ? '#ef4444' : '#22c55e'}} />
                                          {min >= 1 ? 'Required' : 'Optional'}
                                        </button>
                                      </div>
                                      <div style={{display:'flex',gap:8,marginBottom:6,padding:'4px 0'}} onClick={e => e.stopPropagation()}>
                                        <div style={{flex:1}}>
                                          <label style={{fontSize:'0.6rem',fontWeight:600,color:'#6b7280',display:'block',marginBottom:2}}>Min</label>
                                          <input type="number" min={0} max={members.length} value={min} onClick={e => e.stopPropagation()} onChange={e => {
                                            const v = Number(e.target.value)
                                            setHrFieldEditorFields((prev: any[]) => prev.map((f: any) => f.checkboxGroup === groupName ? { ...f, checkboxGroupMin: v, required: v >= 1 } : f))
                                          }} style={{width:'100%',padding:'3px 6px',border:'1px solid #d8b4fe',borderRadius:4,fontSize:'0.68rem',background:'#fff'}} />
                                        </div>
                                        <div style={{flex:1}}>
                                          <label style={{fontSize:'0.6rem',fontWeight:600,color:'#6b7280',display:'block',marginBottom:2}}>Max</label>
                                          <input type="number" min={0} max={members.length} value={max} onClick={e => e.stopPropagation()} onChange={e => {
                                            const v = Number(e.target.value)
                                            setHrFieldEditorFields((prev: any[]) => prev.map((f: any) => f.checkboxGroup === groupName ? { ...f, checkboxGroupMax: v } : f))
                                          }} style={{width:'100%',padding:'3px 6px',border:'1px solid #d8b4fe',borderRadius:4,fontSize:'0.68rem',background:'#fff'}} />
                                        </div>
                                      </div>
                                      {members.map((m: any) => (
                                        <div key={m.id} style={{display:'flex',alignItems:'center',gap:4,marginBottom:2}}>
                                          <button onClick={() => { setHrFieldEditorSelected(m.id); setHrFieldEditorSidebarSection((prev: Record<string,boolean>) => { const next: Record<string,boolean> = {}; for (const k of Object.keys(prev)) next[k] = k === 'properties'; return next }) }}
                                            style={{flex:1,textAlign:'left',fontSize:'0.65rem',padding:'3px 8px',borderRadius:4,border:'none',background: hrFieldEditorSelected === m.id ? '#7e22ce' : 'transparent',color: hrFieldEditorSelected === m.id ? '#fff' : '#7e22ce',cursor:'pointer',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',transition:'background 0.1s'}}>
                                            {m.label || m.id}
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Field Properties section (when field is selected) */}
                    {selectedField && (
                      <div style={{borderBottom:'1px solid #e5e7eb'}}>
                        <button onClick={() => toggleFeSection('properties')} style={{width:'100%',display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 16px',background:'#fff',border:'none',cursor:'pointer'}}
                          onMouseEnter={e => e.currentTarget.style.background='#f9fafb'}
                          onMouseLeave={e => e.currentTarget.style.background='#fff'}>
                          <span style={{fontWeight:600,color:'#1e293b',fontSize:'0.82rem'}}>Field Properties</span>
                          <span style={{color:'#9ca3af',fontSize:'0.7rem'}}>{hrFieldEditorSidebarSection.properties ? '\u25B2' : '\u25BC'}</span>
                        </button>
                        {hrFieldEditorSidebarSection.properties && (
                          <div style={{padding:16,background:'#fff'}}>
                            <div style={{display:'flex',justifyContent:'flex-end',marginBottom:4}}>
                              <button onClick={() => { setHrFieldEditorSelected(null); setHrFieldEditorSidebarSection((p: Record<string,boolean>) => ({ ...p, fieldTypes: true })) }} title="Deselect" style={{border:'none',background:'none',cursor:'pointer',color:'#9ca3af',padding:2}}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                              </button>
                            </div>
                            {/* Label */}
                            <div style={{marginBottom:10}}>
                              <label style={{fontSize:'0.72rem',fontWeight:600,color:'#6b7280',marginBottom:4,display:'block'}}>Label</label>
                              <input type="text" value={selectedField.label || ''} onChange={e => {
                                setHrFieldEditorFields((prev: any[]) => prev.map((f: any) => f.id === selectedField.id ? { ...f, label: e.target.value } : f))
                              }} style={{width:'100%',padding:'6px 8px',border:'1px solid #d1d5db',borderRadius:4,fontSize:'0.75rem'}} placeholder="Field label" />
                            </div>
                            {/* Type + Required */}
                            <div style={{marginBottom:10,display:'flex',alignItems:'center',gap:8}}>
                              <span style={{fontSize:'0.72rem',color:'#6b7280'}}>Type:</span>
                              <span style={{fontSize:'0.72rem',fontWeight:600,color:'#1e293b',textTransform:'capitalize'}}>{selectedField.type}</span>
                              <button onClick={() => setHrFieldEditorFields((prev: any[]) => prev.map((f: any) => f.id === selectedField.id ? { ...f, required: !f.required } : f))}
                                style={{marginLeft:'auto',display:'flex',alignItems:'center',gap:4,fontSize:'0.68rem',padding:'3px 10px',borderRadius:12,fontWeight:700,border:'1px solid',cursor:'pointer',
                                  background: selectedField.required !== false ? '#fef2f2' : '#f3f4f6',
                                  color: selectedField.required !== false ? '#dc2626' : '#6b7280',
                                  borderColor: selectedField.required !== false ? '#fca5a5' : '#d1d5db',
                                }}>
                                {selectedField.required !== false ? 'Required' : 'Optional'}
                              </button>
                            </div>
                            {/* Signer Assignment */}
                            <div style={{marginBottom:12}}>
                              <label style={{display:'block',fontSize:'0.68rem',fontWeight:600,color:'#6b7280',marginBottom:4,textTransform:'uppercase'}}>Assigned To</label>
                              <div style={{display:'flex',flexDirection:'column',gap:4}}>
                                {[
                                  { idx: 0, label: 'Employee', color: '#2563eb' },
                                  { idx: 1, label: 'Employer / HR Admin', color: '#16a34a' },
                                  { idx: 2, label: 'Preparer / Translator', color: '#9333ea' },
                                  ...hrFieldEditorCustomSigners,
                                ].map(signer => {
                                  const isActive = selectedField?.signerIndex === signer.idx
                                  return (
                                    <button key={signer.idx} onClick={() => {
                                      setHrFieldEditorFields((prev: any[]) => prev.map((f: any) =>
                                        f.id === hrFieldEditorSelected ? { ...f, signerIndex: isActive ? undefined : signer.idx } : f
                                      ))
                                    }} style={{
                                      display:'flex',alignItems:'center',gap:8,padding:'6px 10px',
                                      borderRadius:6,border: isActive ? `2px solid ${signer.color}` : '1px solid #e5e7eb',
                                      background: isActive ? `${signer.color}10` : '#fff',
                                      cursor:'pointer',fontSize:'0.78rem',fontWeight: isActive ? 600 : 400,
                                      color: isActive ? signer.color : '#374151',
                                    }}>
                                      <span style={{width:10,height:10,borderRadius:'50%',background:signer.color,flexShrink:0}} />
                                      {signer.label}
                                      {isActive && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={signer.color} strokeWidth="3" style={{marginLeft:'auto'}}><polyline points="20 6 9 17 4 12"/></svg>}
                                    </button>
                                  )
                                })}
                              </div>
                            </div>
                            {/* Max Characters */}
                            {selectedField.type === 'text' && (
                              <div style={{marginBottom:10}}>
                                <label style={{fontSize:'0.72rem',fontWeight:600,color:'#6b7280',marginBottom:4,display:'block'}}>Max Characters</label>
                                <div style={{display:'flex',alignItems:'center',gap:6}}>
                                  <input type="number" min={0} max={999} value={selectedField.maxLength || ''} onChange={e => {
                                    const val = e.target.value === '' ? undefined : Math.max(0, parseInt(e.target.value) || 0)
                                    setHrFieldEditorFields((prev: any[]) => prev.map((f: any) => f.id === selectedField.id ? { ...f, maxLength: val || undefined } : f))
                                  }} style={{width:70,padding:'4px 8px',border:'1px solid #d1d5db',borderRadius:4,fontSize:'0.72rem'}} placeholder="None" />
                                  <span style={{fontSize:'0.62rem',color:'#9ca3af'}}>{selectedField.maxLength ? `${selectedField.maxLength} char max` : 'Unlimited'}</span>
                                </div>
                              </div>
                            )}
                            {/* Format Mask */}
                            {selectedField.type === 'text' && (
                              <div style={{marginBottom:10}}>
                                <label style={{fontSize:'0.72rem',fontWeight:600,color:'#6b7280',marginBottom:4,display:'block'}}>Auto-Format</label>
                                <div style={{display:'flex',flexWrap:'wrap',gap:4}}>
                                  {[
                                    { label: 'SSN', mask: 'ssn', hint: '###-##-####' },
                                    { label: 'Phone', mask: 'phone', hint: '###-###-####' },
                                    { label: 'Date', mask: 'date', hint: '##/##/####' },
                                  ].map(preset => {
                                    const isCurrent = selectedField.formatMask === preset.mask
                                    return (
                                      <button key={preset.mask} onClick={() => {
                                        setHrFieldEditorFields((prev: any[]) => prev.map((f: any) =>
                                          f.id === selectedField.id ? { ...f, formatMask: isCurrent ? undefined : preset.mask, maxLength: isCurrent ? undefined : preset.hint.length } : f
                                        ))
                                      }} style={{fontSize:'0.68rem',padding:'3px 8px',borderRadius:4,border:'1px solid',cursor:'pointer',
                                        background: isCurrent ? '#2563eb' : '#fff', color: isCurrent ? '#fff' : '#6b7280', borderColor: isCurrent ? '#2563eb' : '#d1d5db',
                                      }}>
                                        {preset.label} <span style={{opacity:0.6}}>{preset.hint}</span>
                                      </button>
                                    )
                                  })}
                                  {selectedField.formatMask && (
                                    <button onClick={() => setHrFieldEditorFields((prev: any[]) => prev.map((f: any) => f.id === selectedField.id ? { ...f, formatMask: undefined } : f))}
                                      style={{fontSize:'0.68rem',padding:'3px 8px',borderRadius:4,border:'1px solid #fca5a5',color:'#dc2626',background:'transparent',cursor:'pointer'}}>
                                      Clear
                                    </button>
                                  )}
                                </div>
                              </div>
                            )}
                            {/* Input Restriction */}
                            {selectedField.type === 'text' && (
                              <div style={{marginBottom:10}}>
                                <label style={{fontSize:'0.72rem',fontWeight:600,color:'#6b7280',marginBottom:4,display:'block'}}>Input Type</label>
                                <div style={{display:'flex',flexWrap:'wrap',gap:4}}>
                                  {[
                                    { label: 'ABC', value: 'alpha', hint: 'Letters only' },
                                    { label: '123', value: 'numeric', hint: 'Numbers only' },
                                    { label: 'A1B', value: 'alphanumeric', hint: 'Letters & numbers' },
                                  ].map(preset => {
                                    const isCurrent = selectedField.inputRestriction === preset.value
                                    return (
                                      <button key={preset.value} onClick={() => {
                                        setHrFieldEditorFields((prev: any[]) => prev.map((f: any) =>
                                          f.id === selectedField.id ? { ...f, inputRestriction: isCurrent ? undefined : preset.value } : f
                                        ))
                                      }} style={{fontSize:'0.68rem',padding:'3px 8px',borderRadius:4,border:'1px solid',cursor:'pointer',
                                        background: isCurrent ? '#2563eb' : '#fff', color: isCurrent ? '#fff' : '#6b7280', borderColor: isCurrent ? '#2563eb' : '#d1d5db',
                                      }}>
                                        {preset.label} <span style={{opacity:0.6}}>{preset.hint}</span>
                                      </button>
                                    )
                                  })}
                                  {selectedField.inputRestriction && (
                                    <button onClick={() => setHrFieldEditorFields((prev: any[]) => prev.map((f: any) => f.id === selectedField.id ? { ...f, inputRestriction: undefined } : f))}
                                      style={{fontSize:'0.68rem',padding:'3px 8px',borderRadius:4,border:'1px solid #fca5a5',color:'#dc2626',background:'transparent',cursor:'pointer'}}>
                                      Clear
                                    </button>
                                  )}
                                </div>
                              </div>
                            )}
                            {/* Static Value */}
                            {selectedField.type === 'text' && (
                              <div style={{marginBottom:10}}>
                                <label style={{fontSize:'0.72rem',fontWeight:600,color:'#6b7280',marginBottom:4,display:'block'}}>Static Value</label>
                                <input type="text" value={selectedField.staticValue || ''} onChange={e => {
                                  setHrFieldEditorFields((prev: any[]) => prev.map((f: any) => f.id === selectedField.id ? { ...f, staticValue: e.target.value || undefined } : f))
                                }} style={{width:'100%',padding:'4px 8px',border:'1px solid #d1d5db',borderRadius:4,fontSize:'0.72rem'}} placeholder='e.g. "-" or "/"' />
                                <span style={{fontSize:'0.6rem',color:'#9ca3af',marginTop:2,display:'block'}}>Pre-filled, non-editable value</span>
                              </div>
                            )}
                            {/* Date default */}
                            {selectedField.type === 'date' && (
                              <div style={{marginBottom:10,display:'flex',alignItems:'center',justifyContent:'space-between',padding:10,background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:8}}>
                                <div>
                                  <p style={{fontSize:'0.72rem',fontWeight:700,color:'#1e293b',margin:0}}>Auto-fill today</p>
                                  <p style={{fontSize:'0.62rem',color:'#6b7280',margin:'2px 0 0'}}>
                                    {selectedField.defaultToToday ? "Pre-filled with today's date" : 'User must enter manually'}
                                  </p>
                                </div>
                                <button onClick={() => setHrFieldEditorFields((prev: any[]) => prev.map((f: any) => f.id === selectedField.id ? { ...f, defaultToToday: !f.defaultToToday } : f))}
                                  style={{padding:'3px 10px',borderRadius:12,fontWeight:700,fontSize:'0.68rem',border:'1px solid',cursor:'pointer',flexShrink:0,marginLeft:8,
                                    background: selectedField.defaultToToday ? '#2563eb' : '#fff', color: selectedField.defaultToToday ? '#fff' : '#6b7280', borderColor: selectedField.defaultToToday ? '#1d4ed8' : '#d1d5db',
                                  }}>
                                  {selectedField.defaultToToday ? 'On' : 'Off'}
                                </button>
                              </div>
                            )}
                            {/* Checkbox Group */}
                            {selectedField.type === 'checkbox' && (
                              <div style={{marginBottom:10}}>
                                <label style={{fontSize:'0.72rem',fontWeight:600,color:'#6b7280',marginBottom:4,display:'block'}}>Checkbox Group</label>
                                <input type="text" value={selectedField.checkboxGroup || ''} onChange={e => {
                                  setHrFieldEditorFields((prev: any[]) => prev.map((f: any) => f.id === selectedField.id ? { ...f, checkboxGroup: e.target.value || undefined } : f))
                                }} style={{width:'100%',padding:'4px 8px',border:'1px solid #d1d5db',borderRadius:4,fontSize:'0.72rem'}} placeholder="e.g. Filing Status" />
                                {selectedField.checkboxGroup && (
                                  <p style={{fontSize:'0.62rem',color:'#9ca3af',margin:'4px 0 0'}}>Min/Max set in Checkbox Groups above</p>
                                )}
                              </div>
                            )}
                            {/* Dropdown Options */}
                            {selectedField.type === 'dropdown' && (
                              <div style={{marginBottom:10}}>
                                <label style={{fontSize:'0.72rem',fontWeight:600,color:'#6b7280',marginBottom:4,display:'block'}}>Dropdown Options</label>
                                {(selectedField.options || []).map((opt: string, oi: number) => (
                                  <div key={oi} style={{display:'flex',alignItems:'center',gap:4,marginBottom:4}}>
                                    <input type="text" value={opt} onChange={e => {
                                      const newOpts = [...(selectedField.options || [])]
                                      newOpts[oi] = e.target.value
                                      setHrFieldEditorFields((prev: any[]) => prev.map((f: any) => f.id === selectedField.id ? { ...f, options: newOpts } : f))
                                    }} style={{flex:1,padding:'4px 8px',border:'1px solid #d1d5db',borderRadius:4,fontSize:'0.72rem'}} />
                                    <button onClick={() => {
                                      const newOpts = (selectedField.options || []).filter((_: any, i: number) => i !== oi)
                                      setHrFieldEditorFields((prev: any[]) => prev.map((f: any) => f.id === selectedField.id ? { ...f, options: newOpts } : f))
                                    }} style={{border:'none',background:'none',cursor:'pointer',color:'#d1d5db',padding:2}} title="Remove">
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                                    </button>
                                  </div>
                                ))}
                                <button onClick={() => {
                                  const newOpts = [...(selectedField.options || []), `Option ${(selectedField.options || []).length + 1}`]
                                  setHrFieldEditorFields((prev: any[]) => prev.map((f: any) => f.id === selectedField.id ? { ...f, options: newOpts } : f))
                                }} style={{width:'100%',padding:'4px 0',border:'1px dashed #d1d5db',borderRadius:4,background:'#fff',color:'#6b7280',fontSize:'0.68rem',fontWeight:600,cursor:'pointer',marginTop:4}}
                                  onMouseEnter={e => { e.currentTarget.style.borderColor='#2563eb'; e.currentTarget.style.color='#2563eb' }}
                                  onMouseLeave={e => { e.currentTarget.style.borderColor='#d1d5db'; e.currentTarget.style.color='#6b7280' }}>
                                  + Add Option
                                </button>
                              </div>
                            )}
                            {/* Position & Size */}
                            <div style={{borderTop:'1px solid #e5e7eb',paddingTop:10,marginTop:6}}>
                              <label style={{fontWeight:600,color:'#6b7280',marginBottom:6,display:'block',fontSize:'0.68rem',textTransform:'uppercase',letterSpacing:'0.5px'}}>Position & Size</label>
                              <div style={{display:'flex',gap:6,marginBottom:6}}>
                                <div style={{flex:1}}><label style={{fontWeight:600,color:'#6b7280',marginBottom:2,display:'block',fontSize:'0.68rem'}}>X (%)</label>
                                  <input type="number" step="0.1" value={selectedField.x?.toFixed(1)} onChange={e => setHrFieldEditorFields((prev: any[]) => prev.map((f: any) => f.id === selectedField.id ? { ...f, x: Number(e.target.value) } : f))} style={{width:'100%',padding:'4px 8px',border:'1px solid #d1d5db',borderRadius:4,fontSize:'0.72rem'}} /></div>
                                <div style={{flex:1}}><label style={{fontWeight:600,color:'#6b7280',marginBottom:2,display:'block',fontSize:'0.68rem'}}>Y (%)</label>
                                  <input type="number" step="0.1" value={selectedField.y?.toFixed(1)} onChange={e => setHrFieldEditorFields((prev: any[]) => prev.map((f: any) => f.id === selectedField.id ? { ...f, y: Number(e.target.value) } : f))} style={{width:'100%',padding:'4px 8px',border:'1px solid #d1d5db',borderRadius:4,fontSize:'0.72rem'}} /></div>
                              </div>
                              <div style={{display:'flex',gap:6,marginBottom:6}}>
                                <div style={{flex:1}}><label style={{fontWeight:600,color:'#6b7280',marginBottom:2,display:'block',fontSize:'0.68rem'}}>W (%)</label>
                                  <input type="number" step="0.1" value={selectedField.width?.toFixed(1)} onChange={e => setHrFieldEditorFields((prev: any[]) => prev.map((f: any) => f.id === selectedField.id ? { ...f, width: Number(e.target.value) } : f))} style={{width:'100%',padding:'4px 8px',border:'1px solid #d1d5db',borderRadius:4,fontSize:'0.72rem'}} /></div>
                                <div style={{flex:1}}><label style={{fontWeight:600,color:'#6b7280',marginBottom:2,display:'block',fontSize:'0.68rem'}}>H (%)</label>
                                  <input type="number" step="0.1" value={selectedField.height?.toFixed(1)} onChange={e => setHrFieldEditorFields((prev: any[]) => prev.map((f: any) => f.id === selectedField.id ? { ...f, height: Number(e.target.value) } : f))} style={{width:'100%',padding:'4px 8px',border:'1px solid #d1d5db',borderRadius:4,fontSize:'0.72rem'}} /></div>
                              </div>
                              <div style={{fontSize:'0.68rem',color:'#9ca3af'}}>Page {selectedField.pageNumber || selectedField.page || 1}</div>
                            </div>
                            {/* Delete */}
                            <button style={{width:'100%',padding:'6px 12px',border:'1px solid #fca5a5',borderRadius:6,background:'#fef2f2',color:'#dc2626',fontSize:'0.75rem',fontWeight:600,cursor:'pointer',marginTop:10}}
                              onClick={() => {
                                setHrFieldEditorFields((prev: any[]) => prev.filter((f: any) => f.id !== selectedField.id))
                                setHrFieldEditorSelected(null)
                                setHrFieldEditorSidebarSection((p: Record<string,boolean>) => ({ ...p, fieldTypes: true }))
                              }}>
                              Delete Field
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Field Types section (collapsible) */}
                    <div style={{borderBottom:'1px solid #e5e7eb'}}>
                      <button onClick={() => toggleFeSection('fieldTypes')} style={{width:'100%',display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 16px',background:'#fff',border:'none',cursor:'pointer'}}
                        onMouseEnter={e => e.currentTarget.style.background='#f9fafb'}
                        onMouseLeave={e => e.currentTarget.style.background='#fff'}>
                        <span style={{fontWeight:600,color:'#1e293b',fontSize:'0.82rem'}}>Field Types</span>
                        <span style={{color:'#9ca3af',fontSize:'0.7rem'}}>{hrFieldEditorSidebarSection.fieldTypes ? '\u25B2' : '\u25BC'}</span>
                      </button>
                    </div>
                    {hrFieldEditorSidebarSection.fieldTypes && (
                      <div style={{flex:1,overflowY:'auto',padding:16}}>
                        <div style={{display:'flex',flexDirection:'column',gap:8}}>
                          {feFieldTypes.map(ft => {
                            const isActive = hrFieldEditorTool === ft.type
                            return (
                              <div key={ft.type}
                                draggable
                                onDragStart={(e) => {
                                  e.dataTransfer.effectAllowed = 'copy'
                                  e.dataTransfer.setData('fieldType', ft.type)
                                  setHrFieldEditorTool(ft.type)
                                }}
                                onDragEnd={() => {}}
                                onClick={() => setHrFieldEditorTool(isActive ? null : ft.type)}
                                style={{
                                  position:'relative',
                                  border: isActive ? `2px solid ${ft.color}` : '2px dashed #d1d5db',
                                  background: isActive ? 'rgba(37,99,235,0.05)' : '#fff',
                                  padding:12,
                                  borderRadius:8,
                                  cursor:'grab',
                                  boxShadow: isActive ? `0 0 0 2px ${ft.color}33` : '0 1px 2px rgba(0,0,0,0.05)',
                                  transition:'all 0.2s',
                                  display:'flex',
                                  flexDirection:'column',
                                  gap:10,
                                }}>
                                {/* Top row: icon + label */}
                                <div style={{display:'flex',alignItems:'center',gap:10}}>
                                  <div style={{width:28,height:28,borderRadius:6,background:ft.color,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                                    {ft.type === 'signature' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>}
                                    {ft.type === 'initials' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><circle cx="12" cy="12" r="10"/></svg>}
                                    {ft.type === 'checkbox' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>}
                                    {ft.type === 'date' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>}
                                    {ft.type === 'text' && <span style={{color:'#fff',fontWeight:700,fontSize:14}}>T</span>}
                                    {ft.type === 'dropdown' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>}
                                  </div>
                                  <div>
                                    <div style={{fontWeight:500,fontSize:'0.82rem',color:'#1e293b'}}>{ft.label}</div>
                                    <div style={{fontSize:'0.68rem',color: isActive ? ft.color : '#6b7280'}}>{isActive ? 'Click on PDF to place' : 'Drag to place'}</div>
                                  </div>
                                </div>
                                {/* Preview box */}
                                <div style={{background:'#f9fafb',borderRadius:6,padding:'8px 12px',textAlign:'center',border:'1px solid #e5e7eb'}}>
                                  <span style={{fontSize: ft.type === 'checkbox' ? '1.2rem' : '0.85rem',color:'#6b7280',fontFamily: (ft.type === 'signature' || ft.type === 'initials') ? 'serif' : 'inherit',fontStyle: (ft.type === 'signature' || ft.type === 'initials') ? 'italic' : 'normal',fontWeight: ft.type === 'initials' ? 700 : 400}}>
                                    {ft.example}
                                  </span>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* ===== FOOTER BAR ===== */}
                <div style={{padding:'12px 20px',borderTop:'1px solid #e5e7eb',background:'#f9fafb',display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
                  <div style={{fontSize:'0.82rem',color:'#6b7280'}}>
                    <span style={{fontWeight:500}}>{hrFieldEditorFields.length}</span> field{hrFieldEditorFields.length !== 1 ? 's' : ''} placed
                    {hrFieldEditorFields.length > 0 && (
                      <>
                        {' \u2022 '}
                        <span style={{color:'#dc2626',fontWeight:500}}>{requiredCount} required</span>
                        {' \u2022 '}
                        <span style={{color:'#6b7280'}}>{optionalCount} optional</span>
                      </>
                    )}
                  </div>
                  <button onClick={handleSaveEditorFields} disabled={hrFieldEditorFields.length === 0 || hrFieldEditorSaving}
                    style={{display:'inline-flex',alignItems:'center',gap:8,padding:'8px 20px',background: (hrFieldEditorFields.length === 0 || hrFieldEditorSaving) ? '#d1d5db' : '#2563eb',color:'#fff',border:'none',borderRadius:8,fontWeight:600,fontSize:'0.82rem',cursor: (hrFieldEditorFields.length === 0 || hrFieldEditorSaving) ? 'not-allowed' : 'pointer',transition:'background 0.15s'}}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                    {hrFieldEditorSaving ? 'Saving...' : 'Save Fields'}
                  </button>
                </div>
              </div>
            </div>
          )
        })()}

        {/* ========== Marquee Checkbox Group Prompt ========== */}
        {hrFieldEditorGroupPrompt && (
          <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',zIndex:10001,display:'flex',alignItems:'center',justifyContent:'center'}}
            onClick={() => { setHrFieldEditorGroupPrompt(false); setHrFieldEditorMarqueeSelected([]) }}>
            <div style={{background:'#fff',borderRadius:12,padding:24,width:360,boxShadow:'0 12px 40px rgba(0,0,0,0.2)'}} onClick={e => e.stopPropagation()}>
              <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:16}}>
                <div style={{width:36,height:36,borderRadius:8,background:'#f3e8ff',display:'flex',alignItems:'center',justifyContent:'center'}}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                </div>
                <div>
                  <h3 style={{margin:0,fontSize:'0.95rem',fontWeight:700,color:'#1e293b'}}>Create Checkbox Group</h3>
                  <p style={{margin:0,fontSize:'0.72rem',color:'#6b7280'}}>{hrFieldEditorMarqueeSelected.length} checkboxes selected</p>
                </div>
              </div>
              <div style={{marginBottom:16}}>
                <label style={{display:'block',fontSize:'0.72rem',fontWeight:600,color:'#6b7280',marginBottom:4}}>Group Name</label>
                <input type="text" value={hrFieldEditorNewGroupName} onChange={e => setHrFieldEditorNewGroupName(e.target.value)}
                  placeholder="e.g. Federal Tax Classification" autoFocus
                  onKeyDown={e => { if (e.key === 'Enter' && hrFieldEditorNewGroupName.trim()) {
                    setHrFieldEditorHistory((h: any[][]) => [...h, hrFieldEditorFields])
                    setHrFieldEditorFuture([])
                    setHrFieldEditorFields((prev: any[]) => prev.map((f: any) =>
                      hrFieldEditorMarqueeSelected.includes(f.id) ? { ...f, checkboxGroup: hrFieldEditorNewGroupName.trim(), checkboxGroupMin: 1, checkboxGroupMax: 1 } : f
                    ))
                    setHrFieldEditorGroupPrompt(false)
                    setHrFieldEditorMarqueeSelected([])
                  }}}
                  style={{width:'100%',padding:'8px 12px',border:'1px solid #d1d5db',borderRadius:6,fontSize:'0.82rem',boxSizing:'border-box'}} />
              </div>
              <div style={{marginBottom:16}}>
                <label style={{display:'block',fontSize:'0.72rem',fontWeight:600,color:'#6b7280',marginBottom:4}}>Minimum Required</label>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <input type="number" min={0} max={hrFieldEditorMarqueeSelected.length} defaultValue={1}
                    style={{width:60,padding:'6px 8px',border:'1px solid #d1d5db',borderRadius:4,fontSize:'0.82rem'}} />
                  <span style={{fontSize:'0.72rem',color:'#9ca3af'}}>of {hrFieldEditorMarqueeSelected.length} must be checked</span>
                </div>
              </div>
              {/* Existing groups to add to */}
              {(() => {
                const existingGroups = Array.from(new Set(hrFieldEditorFields.filter((f:any) => f.checkboxGroup && !f.checkboxGroup.startsWith('split-field') && !f.checkboxGroup.startsWith('field_')).map((f:any) => f.checkboxGroup)))
                if (existingGroups.length === 0) return null
                return (
                  <div style={{marginBottom:16}}>
                    <label style={{display:'block',fontSize:'0.72rem',fontWeight:600,color:'#6b7280',marginBottom:4}}>Or add to existing group</label>
                    <div style={{display:'flex',flexWrap:'wrap',gap:4}}>
                      {existingGroups.map((g: string) => (
                        <button key={g} onClick={() => {
                          setHrFieldEditorHistory((h: any[][]) => [...h, hrFieldEditorFields])
                          setHrFieldEditorFuture([])
                          setHrFieldEditorFields((prev: any[]) => prev.map((f: any) =>
                            hrFieldEditorMarqueeSelected.includes(f.id) ? { ...f, checkboxGroup: g } : f
                          ))
                          setHrFieldEditorGroupPrompt(false)
                          setHrFieldEditorMarqueeSelected([])
                        }} style={{padding:'4px 10px',borderRadius:12,border:'1px solid #d8b4fe',background:'#faf5ff',color:'#7e22ce',fontSize:'0.72rem',fontWeight:600,cursor:'pointer'}}>
                          {g}
                        </button>
                      ))}
                    </div>
                  </div>
                )
              })()}
              <div style={{display:'flex',justifyContent:'flex-end',gap:8}}>
                <button onClick={() => { setHrFieldEditorGroupPrompt(false); setHrFieldEditorMarqueeSelected([]) }}
                  style={{padding:'8px 16px',borderRadius:6,border:'1px solid #d1d5db',background:'#fff',color:'#6b7280',fontSize:'0.78rem',fontWeight:600,cursor:'pointer'}}>Cancel</button>
                <button disabled={!hrFieldEditorNewGroupName.trim()} onClick={() => {
                  setHrFieldEditorHistory((h: any[][]) => [...h, hrFieldEditorFields])
                  setHrFieldEditorFuture([])
                  setHrFieldEditorFields((prev: any[]) => prev.map((f: any) =>
                    hrFieldEditorMarqueeSelected.includes(f.id) ? { ...f, checkboxGroup: hrFieldEditorNewGroupName.trim(), checkboxGroupMin: 1, checkboxGroupMax: 1 } : f
                  ))
                  setHrFieldEditorGroupPrompt(false)
                  setHrFieldEditorMarqueeSelected([])
                }}
                  style={{padding:'8px 16px',borderRadius:6,border:'none',background: hrFieldEditorNewGroupName.trim() ? '#7c3aed' : '#d1d5db',color:'#fff',fontSize:'0.78rem',fontWeight:600,cursor: hrFieldEditorNewGroupName.trim() ? 'pointer' : 'default'}}>
                  Create Group
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ========== Edit Deal Columns Modal — mirrors the contacts modal look ========== */}
        {dealEditColsOpen && (
          <div className="ec-overlay" onClick={() => setDealEditColsOpen(false)}>
            <div className="ec-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="ec-header">
                <h2 className="ec-title">Choose which columns you see</h2>
                <button className="ec-close" onClick={() => setDealEditColsOpen(false)}>
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                </button>
              </div>
              <div className="ec-body">
                <div className="ec-left">
                  <div className="ec-search-wrap">
                    <svg className="ec-search-icon" width="14" height="14" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M28.14 29.29c-.29 0-.58-.11-.81-.33l-7.58-7.58a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l7.58 7.58a1.14 1.14 0 0 1-.81 1.95"/><path d="M13.29 24.71c-6.3 0-11.43-5.13-11.43-11.43S6.98 1.86 13.29 1.86s11.43 5.13 11.43 11.43-5.13 11.43-11.43 11.43Zm0-20.57c-5.04 0-9.14 4.1-9.14 9.14s4.1 9.14 9.14 9.14 9.14-4.1 9.14-9.14-4.1-9.14-9.14-9.14"/></svg>
                    <input
                      className="ec-search-input"
                      placeholder="Search columns..."
                      value={dealEditColsSearch}
                      onChange={(e) => setDealEditColsSearch(e.target.value)}
                      autoFocus
                    />
                    {dealEditColsSearch && (
                      <button className="ec-search-clear" onClick={() => setDealEditColsSearch('')}>&times;</button>
                    )}
                  </div>
                  <div className="ec-props-list">
                    <div className="ec-category">
                      <div className="ec-category-label">Deal Properties</div>
                      {DEAL_COL_DEFS.filter(c => c.key !== 'title' && (!dealEditColsSearch || c.label.toLowerCase().includes(dealEditColsSearch.toLowerCase()))).map(col => (
                        <label key={col.key} className="ec-prop-item">
                          <input
                            type="checkbox"
                            checked={dealEditColsDraft.includes(col.key)}
                            onChange={() => {
                              setDealEditColsDraft(prev =>
                                prev.includes(col.key) ? prev.filter(k => k !== col.key) : [...prev, col.key]
                              )
                            }}
                          />
                          <span className="ec-prop-check">
                            <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M3 8l3.5 3.5L13 5"/></svg>
                          </span>
                          <span className="ec-prop-label">{col.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="ec-right">
                  <div className="ec-right-header">Selected columns ({dealEditColsDraft.length})</div>
                  <div className="ec-selected-list">
                    {/* Deal name is always frozen first */}
                    <div className="ec-selected-item ec-frozen">
                      <span className="ec-drag-handle ec-drag-disabled">
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><circle cx="5" cy="3" r="1.3"/><circle cx="11" cy="3" r="1.3"/><circle cx="5" cy="8" r="1.3"/><circle cx="11" cy="8" r="1.3"/><circle cx="5" cy="13" r="1.3"/><circle cx="11" cy="13" r="1.3"/></svg>
                      </span>
                      <span className="ec-selected-label">Deal name</span>
                      <span className="ec-frozen-badge">Frozen</span>
                    </div>
                    {dealEditColsDraft.filter(k => k !== 'title').map((colKey) => {
                      const def = DEAL_COL_DEFS.find(d => d.key === colKey)
                      if (!def) return null
                      return (
                        <div key={colKey} className="ec-selected-item">
                          <span className="ec-drag-handle">
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><circle cx="5" cy="3" r="1.3"/><circle cx="11" cy="3" r="1.3"/><circle cx="5" cy="8" r="1.3"/><circle cx="11" cy="8" r="1.3"/><circle cx="5" cy="13" r="1.3"/><circle cx="11" cy="13" r="1.3"/></svg>
                          </span>
                          <span className="ec-selected-label">{def.label}</span>
                          <button
                            className="ec-remove-btn"
                            onClick={() => setDealEditColsDraft(prev => prev.filter(k => k !== colKey))}
                            title={`Remove ${def.label}`}
                          >
                            <svg width="10" height="10" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                          </button>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
              <div className="ec-footer">
                <div className="ec-footer-left">
                  <button
                    className="ec-apply-btn"
                    onClick={() => {
                      const final = dealEditColsDraft.includes('title') ? dealEditColsDraft : ['title', ...dealEditColsDraft]
                      // Preserve canonical order from DEAL_COL_DEFS so drag-reorder isn't required
                      const ordered = DEAL_COL_DEFS.map(c => c.key).filter(k => final.includes(k))
                      setDealVisibleCols(ordered)
                      try { localStorage.setItem('botpros-deals-visible-cols', JSON.stringify(ordered)) } catch { /* ignore */ }
                      setDealEditColsOpen(false)
                    }}
                  >Apply</button>
                  <button className="ec-cancel-btn" onClick={() => setDealEditColsOpen(false)}>Cancel</button>
                </div>
                <button className="ec-remove-all" onClick={() => setDealEditColsDraft(['title'])}>Remove all columns</button>
              </div>
            </div>
          </div>
        )}

        {/* ========== Edit Columns Modal ========== */}
        {editColumnsOpen && (() => {
          // Shared gold-standard popover — contacts/companies use the CONTACT
          // columns; calls swaps in its own defs, frozen key ('subject' vs
          // 'name'), and persistence target. (calls-edit-columns-v1)
          const ecIsCalls = activeView === 'calls'
          const ecColDefs: { key: string; label: string; category: string }[] = ecIsCalls ? CALL_COLUMN_DEFS : CONTACT_COLUMN_DEFS
          const ecColMap: Record<string, { label: string } | undefined> = ecIsCalls ? CALL_COLUMN_MAP : CONTACT_COLUMN_MAP
          const ecCategories = ecIsCalls ? CALL_COL_CATEGORIES : CONTACT_COL_CATEGORIES
          const ecFrozenKey = ecIsCalls ? 'subject' : 'name'
          const ecFrozenLabel = ecIsCalls ? 'Subject' : 'Name'
          // bp-contacts-hide-invoiced: on the Contacts (people) view, hide Total Invoiced
          // from the picker (available + selected lists + count) without mutating the shared
          // editColsDraft, so Apply preserves it for the Companies view.
          const ecHiddenKeys: Set<string> = (!ecIsCalls && activeView === 'contacts') ? CONTACTS_HIDDEN_COLS : new Set<string>()
          return (
          <div className="ec-overlay" onClick={() => setEditColumnsOpen(false)}>
            <div className="ec-dialog" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="ec-header">
                <h2 className="ec-title">Choose which columns you see</h2>
                <button className="ec-close" onClick={() => setEditColumnsOpen(false)}>
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                </button>
              </div>
              {/* Body — two columns */}
              <div className="ec-body">
                {/* Left: available properties */}
                <div className="ec-left">
                  <div className="ec-search-wrap">
                    <svg className="ec-search-icon" width="14" height="14" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M28.14 29.29c-.29 0-.58-.11-.81-.33l-7.58-7.58a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l7.58 7.58a1.14 1.14 0 0 1-.81 1.95"/><path d="M13.29 24.71c-6.3 0-11.43-5.13-11.43-11.43S6.98 1.86 13.29 1.86s11.43 5.13 11.43 11.43-5.13 11.43-11.43 11.43Zm0-20.57c-5.04 0-9.14 4.1-9.14 9.14s4.1 9.14 9.14 9.14 9.14-4.1 9.14-9.14-4.1-9.14-9.14-9.14"/></svg>
                    <input
                      className="ec-search-input"
                      placeholder="Search columns..."
                      value={editColsSearch}
                      onChange={(e) => setEditColsSearch(e.target.value)}
                      autoFocus
                    />
                    {editColsSearch && (
                      <button className="ec-search-clear" onClick={() => setEditColsSearch('')}>&times;</button>
                    )}
                  </div>
                  <div className="ec-props-list">
                    {ecCategories.map(cat => {
                      const cols = ecColDefs.filter(d =>
                        d.category === cat && d.key !== ecFrozenKey && !ecHiddenKeys.has(d.key) &&
                        (!editColsSearch || d.label.toLowerCase().includes(editColsSearch.toLowerCase()))
                      )
                      if (cols.length === 0) return null
                      return (
                        <div key={cat} className="ec-category">
                          <div className="ec-category-label">{cat}</div>
                          {cols.map(col => (
                            <label key={col.key} className="ec-prop-item">
                              <input
                                type="checkbox"
                                checked={editColsDraft.includes(col.key)}
                                onChange={() => {
                                  setEditColsDraft(prev =>
                                    prev.includes(col.key)
                                      ? prev.filter(k => k !== col.key)
                                      : [...prev, col.key]
                                  )
                                }}
                              />
                              <span className="ec-prop-check">
                                <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M3 8l3.5 3.5L13 5"/></svg>
                              </span>
                              <span className="ec-prop-label">{col.label}</span>
                            </label>
                          ))}
                        </div>
                      )
                    })}
                  </div>
                </div>
                {/* Right: selected columns */}
                <div className="ec-right">
                  <div className="ec-right-header">
                    Selected columns ({editColsDraft.filter(k => !ecHiddenKeys.has(k)).length})
                  </div>
                  <div className="ec-selected-list">
                    {/* Name is always frozen first */}
                    <div className="ec-selected-item ec-frozen">
                      <span className="ec-drag-handle ec-drag-disabled">
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><circle cx="5" cy="3" r="1.3"/><circle cx="11" cy="3" r="1.3"/><circle cx="5" cy="8" r="1.3"/><circle cx="11" cy="8" r="1.3"/><circle cx="5" cy="13" r="1.3"/><circle cx="11" cy="13" r="1.3"/></svg>
                      </span>
                      <span className="ec-selected-label">{ecFrozenLabel}</span>
                      <span className="ec-frozen-badge">Frozen</span>
                    </div>
                    {editColsDraft.filter(k => k !== ecFrozenKey && !ecHiddenKeys.has(k)).map((colKey, idx) => {
                      const def = ecColMap[colKey]
                      if (!def) return null
                      return (
                        <div
                          key={colKey}
                          className={`ec-selected-item${editColsDragIdx === idx ? ' ec-dragging' : ''}${editColsDragOverIdx === idx ? ' ec-drag-over' : ''}`}
                          draggable
                          onDragStart={() => setEditColsDragIdx(idx)}
                          onDragOver={(e) => { e.preventDefault(); setEditColsDragOverIdx(idx) }}
                          onDragEnd={() => {
                            if (editColsDragIdx !== null && editColsDragOverIdx !== null && editColsDragIdx !== editColsDragOverIdx) {
                              setEditColsDraft(prev => {
                                const withoutName = prev.filter(k => k !== ecFrozenKey)
                                const visible = withoutName.filter(k => !ecHiddenKeys.has(k))
                                const dragged = visible[editColsDragIdx!]
                                const reorderedVisible = [...visible]
                                reorderedVisible.splice(editColsDragIdx!, 1)
                                reorderedVisible.splice(editColsDragOverIdx!, 0, dragged)
                                let visibleIdx = 0
                                const next = withoutName.map(k => ecHiddenKeys.has(k) ? k : reorderedVisible[visibleIdx++])
                                return [ecFrozenKey, ...next]
                              })
                            }
                            setEditColsDragIdx(null)
                            setEditColsDragOverIdx(null)
                          }}
                          onDrop={(e) => e.preventDefault()}
                        >
                          <span className="ec-drag-handle">
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><circle cx="5" cy="3" r="1.3"/><circle cx="11" cy="3" r="1.3"/><circle cx="5" cy="8" r="1.3"/><circle cx="11" cy="8" r="1.3"/><circle cx="5" cy="13" r="1.3"/><circle cx="11" cy="13" r="1.3"/></svg>
                          </span>
                          <span className="ec-selected-label">{def.label}</span>
                          <button
                            className="ec-remove-btn"
                            onClick={() => setEditColsDraft(prev => prev.filter(k => k !== colKey))}
                            title={`Remove ${def.label}`}
                          >
                            <svg width="10" height="10" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
                          </button>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
              {/* Footer */}
              <div className="ec-footer">
                <div className="ec-footer-left">
                  <button
                    className="ec-apply-btn"
                    onClick={() => {
                      const final = editColsDraft.includes(ecFrozenKey) ? editColsDraft : [ecFrozenKey, ...editColsDraft]
                      if (ecIsCalls) {
                        setCallVisibleCols(final)
                        localStorage.setItem('botpros-crm-calls-visible-columns', JSON.stringify(final))
                      } else {
                        setContactVisibleCols(final)
                        localStorage.setItem('botpros-crm-visible-columns', JSON.stringify(final))
                      }
                      setEditColumnsOpen(false)
                    }}
                  >
                    Apply
                  </button>
                  <button className="ec-cancel-btn" onClick={() => setEditColumnsOpen(false)}>
                    Cancel
                  </button>
                </div>
                <button
                  className="ec-remove-all"
                  onClick={() => setEditColsDraft(prev => [ecFrozenKey, ...prev.filter(k => ecHiddenKeys.has(k))])}
                >
                  Remove all columns
                </button>
              </div>
            </div>
          </div>
          )
        })()}

        {/* ========== Cell Edit Modal (Email/Phone) ========== */}
        {cellEditModal && (
          <div className="cell-edit-overlay">
            <div className="cell-edit-modal">
              <div className="cell-edit-header">
                <h3>{cellEditModal.field === 'email' ? 'Edit Emails' : 'Edit Phone Numbers'}</h3>
                <button className="cell-edit-close" onClick={() => setCellEditModal(null)}>&times;</button>
              </div>
              <div className="cell-edit-body">
                {cellEditModal.field === 'email' ? (() => {
                  // Hydrate rows: use explicit emailRows when supplied; otherwise
                  // promote legacy `emails: string[]` into rows (first = primary).
                  const rows: ContactEmailRow[] = cellEditModal.emailRows
                    ? cellEditModal.emailRows
                    : (cellEditModal.emails.length > 0 ? cellEditModal.emails : ['']).map((addr, i) => ({
                        address: addr,
                        is_primary: i === 0,
                        is_billing: false,
                      }))
                  const ensurePrimary = (list: ContactEmailRow[]): ContactEmailRow[] => {
                    if (list.length === 0) return list
                    return list.some(r => r.is_primary) ? list : list.map((r, i) => ({ ...r, is_primary: i === 0 }))
                  }
                  const updateRows = (updater: (prev: ContactEmailRow[]) => ContactEmailRow[]) => {
                    setCellEditModal(prev => {
                      if (!prev) return prev
                      const current = prev.emailRows ?? rows
                      const next = ensurePrimary(updater(current))
                      return { ...prev, emailRows: next, emails: next.map(r => r.address) }
                    })
                  }
                  const setAddress = (i: number, v: string) => updateRows(list => list.map((r, j) => j === i ? { ...r, address: v } : r))
                  const togglePrimary = (i: number) => updateRows(list => list.map((r, j) => ({ ...r, is_primary: j === i })))
                  const toggleBilling = (i: number) => updateRows(list => {
                    const target = list[i]
                    if (!target) return list
                    // If row was already billing, just uncheck it (no billing row remains).
                    // Otherwise set this row as billing and clear billing on every other row
                    // (cross-row uniqueness per role — mirrors Primary behavior).
                    if (target.is_billing) {
                      return list.map((r, j) => j === i ? { ...r, is_billing: false } : r)
                    }
                    return list.map((r, j) => ({ ...r, is_billing: j === i }))
                  })
                  const removeRow = (i: number) => updateRows(list => list.filter((_, j) => j !== i))
                  const addRow = () => updateRows(list => [...list, { address: '', is_primary: list.length === 0, is_billing: false }])
                  const anyInvalid = rows.some(r => r.address && !/^[^@]+@[^@]+\.[^@]+$/.test(r.address))
                  return (
                    <div className="create-panel-field">
                      <label>Emails</label>
                      {rows.map((row, i) => {
                        const invalid = !!row.address && !/^[^@]+@[^@]+\.[^@]+$/.test(row.address)
                        return (
                          <div key={row.id ?? `new-${i}`} className="cp-multi-row" style={{ flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                            <input
                              type="email"
                              placeholder={i === 0 ? 'Email' : 'Additional email'}
                              className={invalid ? 'cp-input-error' : ''}
                              value={row.address}
                              onChange={(e) => setAddress(i, e.target.value)}
                              style={{ flex: 1, minWidth: 140 }}
                            />
                            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', color: '#15803d', fontWeight: 500, cursor: 'pointer', flexShrink: 0 }}>
                              <input type="checkbox" checked={!!row.is_primary} onChange={() => togglePrimary(i)} /> Primary
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', color: '#0369a1', fontWeight: 500, cursor: 'pointer', flexShrink: 0 }}>
                              <input type="checkbox" checked={!!row.is_billing} onChange={() => toggleBilling(i)} /> Billing
                            </label>
                            {!!row.is_primary && <span className="cp-primary-badge" style={{ background: '#dcfce7', color: '#15803d' }}>Primary</span>}
                            {!!row.is_billing && <span className="cp-primary-badge" style={{ background: '#e0f2fe', color: '#0369a1' }}>Billing</span>}
                            <button type="button" className="cp-remove-btn" onClick={() => removeRow(i)} title="Remove" style={{ marginLeft: 'auto', flexShrink: 0 }}>&times;</button>
                          </div>
                        )
                      })}
                      {anyInvalid && (
                        <small className="cp-validation-error">One or more emails are not valid</small>
                      )}
                      <button type="button" className="cp-add-link" onClick={addRow}>+ Add email</button>
                    </div>
                  )
                })() : (
                  <div className="create-panel-field">
                    <label>Phone number</label>
                    {cellEditModal.phones.map((phone, i) => (
                      <div key={i} className="cp-multi-row cp-phone-row">
                        <select value={phone.type} onChange={(e) => setCellEditModal(prev => {
                          if (!prev) return prev
                          const next = [...prev.phones]
                          next[i] = { ...next[i], type: e.target.value }
                          return { ...prev, phones: next }
                        })}>
                          <option value="Mobile">Mobile</option>
                          <option value="Office">Office</option>
                          <option value="Home">Home</option>
                          <option value="Fax">Fax</option>
                          <option value="Other">Other</option>
                        </select>
                        <input type="text" placeholder="555-555-5555" value={phone.number}
                          onChange={(e) => setCellEditModal(prev => {
                            if (!prev) return prev
                            const next = [...prev.phones]
                            next[i] = { ...next[i], number: formatPhoneField(e.target.value) }
                            return { ...prev, phones: next }
                          })}
                        />
                        <input type="text" placeholder="Ext." className="cp-ext-input" value={phone.ext}
                          onChange={(e) => {
                            const digits = e.target.value.replace(/[^\d]/g, '')
                            setCellEditModal(prev => {
                              if (!prev) return prev
                              const next = [...prev.phones]
                              next[i] = { ...next[i], ext: digits }
                              return { ...prev, phones: next }
                            })
                          }}
                        />
                        {i === 0 && <span className="cp-primary-badge">Primary</span>}
                        {i > 0 && <button type="button" className="cp-remove-btn" onClick={() => setCellEditModal(prev => prev ? { ...prev, phones: prev.phones.filter((_, j) => j !== i) } : prev)}>&times;</button>}
                      </div>
                    ))}
                    {cellEditModal.phones[0]?.number && (
                      <button type="button" className="cp-add-link" onClick={() => setCellEditModal(prev => prev ? { ...prev, phones: [...prev.phones, { type: 'Mobile', number: '', ext: '' }] } : prev)}>+ Add another phone number</button>
                    )}
                  </div>
                )}
              </div>
              <div className="cell-edit-footer">
                <button className="cp-btn-primary" disabled={
                  cellEditModal.field === 'email'
                    ? (() => {
                        const rs = cellEditModal.emailRows ?? cellEditModal.emails.map((a, i) => ({ address: a, is_primary: i === 0, is_billing: false }))
                        return rs.some(r => r.address && !/^[^@]+@[^@]+\.[^@]+$/.test(r.address))
                      })()
                    : false
                } onClick={async () => {
                  // Capture modal state before clearing — closure-safe.
                  const modal = cellEditModal
                  if (modal.field === 'phone') {
                    // Persist multi-row phones via contact_phones endpoints.
                    // Strategy: DELETE all existing rows then POST the new list in order
                    // (first row = is_primary). Mirrors the emails pattern.
                    const phoneRows = modal.phones
                      .map(p => ({ ...p, number: (p.number || '').trim(), ext: (p.ext || '').trim(), type: p.type || 'Mobile' }))
                      .filter(p => p.number)
                    const newPrimary = phoneRows[0]?.number || ''
                    const originalClient = clientList.find(c => c.unified_client_id === modal.clientId)
                    const originalVal = originalClient?.preferred_phone ?? null
                    const originalPhones = contactPhones
                    setCellEditModal(null)
                    setClientList(prev => prev.map(c =>
                      c.unified_client_id === modal.clientId
                        ? ({ ...c, preferred_phone: newPrimary } as CrmClient)
                        : c
                    ))
                    try {
                      const listRes = await fetch(`/api/crm/contacts/${encodeURIComponent(modal.clientId)}/phones`)
                      const existing: Array<{ id: number }> = await listRes.json().catch(() => [])
                      // Delete all existing rows
                      for (const row of existing) {
                        await fetch(`/api/crm/contacts/${encodeURIComponent(modal.clientId)}/phones/${row.id}`, { method: 'DELETE' })
                      }
                      // Re-insert in order (first row = primary). Backend auto-syncs
                      // contacts.preferred_phone from the is_primary=1 row.
                      for (let i = 0; i < phoneRows.length; i++) {
                        const p = phoneRows[i]
                        await fetch(`/api/crm/contacts/${encodeURIComponent(modal.clientId)}/phones`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            number: p.number,
                            label: p.type || null,
                            ext: p.ext || null,
                            is_primary: i === 0,
                          }),
                        })
                      }
                      await refreshContactPhones(modal.clientId)
                    } catch (err) {
                      console.error('contact-phones save failed:', err)
                      setContactPhones(originalPhones)
                      setClientList(prev => prev.map(c =>
                        c.unified_client_id === modal.clientId
                          ? ({ ...c, preferred_phone: originalVal } as CrmClient)
                          : c
                      ))
                      alert('Failed to save phone numbers. Please try again.')
                    }
                    return
                  }
                  // Email field: persist multi-row emails via contact_emails endpoints.
                  // Strategy: DELETE all existing rows then POST the new list in order
                  // (primary first). Low-volume per contact so diffing isn't worth it.
                  const rows = (modal.emailRows ?? modal.emails.map((a, i) => ({ address: a, is_primary: i === 0, is_billing: false })))
                    .map(r => ({ ...r, address: (r.address || '').trim() }))
                    .filter(r => r.address)
                  // Snapshot for optimistic cache update + rollback.
                  const newPrimary = rows.find(r => r.is_primary)?.address || rows[0]?.address || ''
                  const originalClient = clientList.find(c => c.unified_client_id === modal.clientId)
                  const originalVal = originalClient?.preferred_email ?? null
                  const originalEmails = contactEmails
                  setCellEditModal(null)
                  setClientList(prev => prev.map(c =>
                    c.unified_client_id === modal.clientId
                      ? ({ ...c, preferred_email: newPrimary } as CrmClient)
                      : c
                  ))
                  try {
                    const listRes = await fetch(`/api/crm/contacts/${encodeURIComponent(modal.clientId)}/emails`)
                    const existing: Array<{ id: number }> = await listRes.json().catch(() => [])
                    // Delete all existing rows
                    for (const row of existing) {
                      await fetch(`/api/crm/contacts/${encodeURIComponent(modal.clientId)}/emails/${row.id}`, { method: 'DELETE' })
                    }
                    // Re-insert in order (primary-marked rows keep their role)
                    for (const r of rows) {
                      await fetch(`/api/crm/contacts/${encodeURIComponent(modal.clientId)}/emails`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ address: r.address, is_primary: !!r.is_primary, is_billing: !!r.is_billing }),
                      })
                    }
                    await refreshContactEmails(modal.clientId)
                  } catch (err) {
                    console.error('contact-emails save failed:', err)
                    setContactEmails(originalEmails)
                    setClientList(prev => prev.map(c =>
                      c.unified_client_id === modal.clientId
                        ? ({ ...c, preferred_email: originalVal } as CrmClient)
                        : c
                    ))
                  }
                }}>Save</button>
                <button className="cp-btn-secondary" onClick={() => setCellEditModal(null)}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Draft delete confirmation — z-index must stay above the Smart Create panel
            (overlay 10000 / panel 10001) or its overlay swallows the Delete click. */}
        {draftDeleteConfirmId && (() => {
          const d = createDrafts.find(x => x.id === draftDeleteConfirmId)
          const summary = d ? (d.formData?.title || d.formData?.name || d.formData?.company || d.formData?.email || `Draft ${new Date(d.createdAt).toLocaleString()}`) : 'this draft'
          return (
            <div onClick={() => setDraftDeleteConfirmId(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 10100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
              <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, width: '100%', maxWidth: 380, padding: 18, boxShadow: '0 12px 30px rgba(0,0,0,0.2)' }}>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>Delete draft?</h3>
                <p style={{ margin: 0, fontSize: 13, color: '#334155', marginBottom: 16 }}>
                  Permanently delete <strong>{String(summary)}</strong>? This can't be undone.
                </p>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => setDraftDeleteConfirmId(null)}
                    style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, background: '#fff', color: '#475569', border: '1px solid #cbd6e2', borderRadius: 4, cursor: 'pointer' }}
                  >Cancel</button>
                  <button
                    type="button"
                    onClick={() => {
                      if (draftDeleteConfirmId) deleteDraft(draftDeleteConfirmId)
                      setDraftDeleteConfirmId(null)
                    }}
                    style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, background: '#dc2626', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                  >Delete</button>
                </div>
              </div>
            </div>
          )
        })()}

        {/* Attach-or-create chooser — shown when "+" is clicked on a Project/Deal Tickets card */}
        {ticketPlusChoice && (
          <div onClick={() => setTicketPlusChoice(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, width: '100%', maxWidth: 360, padding: 18, boxShadow: '0 12px 30px rgba(0,0,0,0.2)' }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>Add ticket</h3>
              <p style={{ margin: 0, fontSize: 12, color: '#64748b', marginBottom: 14 }}>
                Link this {ticketPlusChoice.kind} to a ticket you already have, or create a brand new one.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => {
                    const t = ticketPlusChoice
                    setTicketPlusChoice(null)
                    setAttachTicketQuery('')
                    setAttachTicketTarget({ kind: t.kind, id: t.id, name: t.name })
                  }}
                  style={{ padding: '10px 14px', fontSize: 13, fontWeight: 600, background: '#fff', color: '#1976d2', border: '1px solid #93c5fd', borderRadius: 6, cursor: 'pointer', textAlign: 'left' }}
                >
                  <div>Attach existing ticket</div>
                  <div style={{ fontSize: 11, color: '#64748b', fontWeight: 400, marginTop: 2 }}>Pick a ticket that isn't linked to a {ticketPlusChoice.kind} yet.</div>
                </button>
                <button
                  type="button"
                  disabled={!ticketPlusChoice.createPayload}
                  onClick={() => {
                    const t = ticketPlusChoice
                    setTicketPlusChoice(null)
                    if (t.createPayload) openSmartCreateForTicket(t.createPayload)
                  }}
                  style={{ padding: '10px 14px', fontSize: 13, fontWeight: 600, background: ticketPlusChoice.createPayload ? '#1976d2' : '#cbd6e2', color: '#fff', border: 'none', borderRadius: 6, cursor: ticketPlusChoice.createPayload ? 'pointer' : 'not-allowed', textAlign: 'left' }}
                >
                  <div>Create new ticket</div>
                  <div style={{ fontSize: 11, color: ticketPlusChoice.createPayload ? '#dbeafe' : '#94a3b8', fontWeight: 400, marginTop: 2 }}>Pre-fills the contact and {ticketPlusChoice.kind}.</div>
                </button>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                <button type="button" onClick={() => setTicketPlusChoice(null)} style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, background: 'none', color: '#475569', border: 'none', cursor: 'pointer' }}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Attach existing ticket modal — opened from Project Record or Deal Record */}
        {attachTicketTarget && (
          <div onClick={() => attachTicketSaving == null && setAttachTicketTarget(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, width: '100%', maxWidth: 520, maxHeight: '80vh', padding: 18, boxShadow: '0 12px 30px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column' }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>
                Attach ticket to {attachTicketTarget.kind === 'project' ? 'project' : 'deal'}
              </h3>
              <p style={{ margin: 0, fontSize: 12, color: '#64748b', marginBottom: 12 }}>
                Showing tickets not yet linked to a {attachTicketTarget.kind === 'project' ? 'project' : 'deal'}.
                Selecting one will link it to <strong>{attachTicketTarget.name || `#${attachTicketTarget.id}`}</strong>.
              </p>
              <input
                autoFocus
                value={attachTicketQuery}
                onChange={e => setAttachTicketQuery(e.target.value)}
                placeholder="Search by title, description, or category…"
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: 13, marginBottom: 10, boxSizing: 'border-box' }}
              />
              <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 4 }}>
                {attachTicketResults.length === 0 ? (
                  <div style={{ padding: 16, textAlign: 'center', fontSize: 13, color: '#94a3b8' }}>
                    {attachTicketQuery ? 'No matching tickets.' : 'No unlinked tickets — type to search.'}
                  </div>
                ) : attachTicketResults.map((t: any) => (
                  <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{t.title || `Ticket #${t.id}`}</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>
                        {t.status ? t.status : ''}
                        {t.priority ? ` · ${t.priority}` : ''}
                        {t.category ? ` · ${t.category}` : ''}
                        {t.deal_title ? ` · Deal: ${t.deal_title}` : ''}
                      </div>
                    </div>
                    <button
                      type="button"
                      disabled={attachTicketSaving != null}
                      onClick={async () => {
                        if (!attachTicketTarget) return
                        setAttachTicketSaving(t.id)
                        try {
                          const body: Record<string, unknown> = attachTicketTarget.kind === 'project'
                            ? { projectId: String(attachTicketTarget.id) }
                            : { dealId: Number(attachTicketTarget.id) }
                          await updateTicket(t.id, body)
                          // Refresh whichever side panel we're attached to.
                          if (attachTicketTarget.kind === 'project') {
                            try {
                              const r = await fetch(`/api/crm/projects/${attachTicketTarget.id}/tickets`)
                              const d = r.ok ? await r.json() : []
                              setProjectTickets(Array.isArray(d) ? d : [])
                            } catch { /* non-fatal */ }
                          } else {
                            try {
                              const r = await fetch(`/api/crm/deals/${attachTicketTarget.id}/tickets`)
                              const d = r.ok ? await r.json() : []
                              setDealTickets(Array.isArray(d) ? d : [])
                            } catch { /* non-fatal */ }
                          }
                          setAttachTicketResults(prev => prev.filter(x => x.id !== t.id))
                        } catch (e: any) {
                          alert('Failed to attach: ' + (e?.message || String(e)))
                        } finally {
                          setAttachTicketSaving(null)
                        }
                      }}
                      style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, background: attachTicketSaving === t.id ? '#cbd6e2' : '#1976d2', color: '#fff', border: 'none', borderRadius: 4, cursor: attachTicketSaving != null ? 'wait' : 'pointer', opacity: attachTicketSaving != null && attachTicketSaving !== t.id ? 0.5 : 1 }}
                    >{attachTicketSaving === t.id ? 'Attaching…' : 'Attach'}</button>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                <button type="button" onClick={() => setAttachTicketTarget(null)} style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, background: '#fff', color: '#475569', border: '1px solid #cbd6e2', borderRadius: 4, cursor: 'pointer' }}>Done</button>
              </div>
            </div>
          </div>
        )}

        {/* ========== Unified Smart Create Panel ========== */}
        {smartCreateOpen && (<>
          <div className={`create-panel-overlay${smartCreateClosing ? ' closing' : ''}`} style={{ zIndex: 10000 }} onClick={closeSmartCreate} />
          <div className={`create-panel${smartCreateClosing ? ' closing' : ''}`} style={{ width: 520, zIndex: 10001 }} onClick={(e) => e.stopPropagation()}>
            <div className="create-panel-header" style={{ position: 'relative' }}>
              <h2>Create New</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ position: 'relative' }}>
                  <button
                    type="button"
                    onClick={() => setDraftsMenuOpen(o => !o)}
                    style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, color: createDrafts.length > 0 ? '#1d4ed8' : '#94a3b8', background: createDrafts.length > 0 ? '#dbeafe' : '#f1f5f9', border: '1px solid', borderColor: createDrafts.length > 0 ? '#93c5fd' : '#e2e8f0', borderRadius: 4, cursor: createDrafts.length > 0 ? 'pointer' : 'default' }}
                    disabled={createDrafts.length === 0}
                  >
                    Drafts ({createDrafts.length})
                  </button>
                  {draftsMenuOpen && createDrafts.length > 0 && (
                    <div
                      onMouseLeave={() => setDraftsMenuOpen(false)}
                      style={{ position: 'absolute', top: '100%', right: 0, marginTop: 6, width: 340, maxHeight: 360, overflowY: 'auto', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, boxShadow: '0 6px 20px rgba(15,23,42,0.12)', zIndex: 60, padding: 6 }}
                    >
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#475569', padding: '4px 6px 8px' }}>
                        Drafts auto-save for 3 days. Click one to resume.
                      </div>
                      {createDrafts.map(d => {
                        const fd = d.formData || {}
                        const summary = fd.title || fd.name || fd.company || fd.email || fd.phone || `Draft ${new Date(d.createdAt).toLocaleString()}`
                        const typeLabel = d.type ? d.type.charAt(0).toUpperCase() + d.type.slice(1) : 'Contact'
                        const age = Math.max(0, Math.floor((Date.now() - d.createdAt) / (60 * 60 * 1000)))
                        return (
                          <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: 6, borderRadius: 4, background: d.id === currentDraftId ? '#eff6ff' : 'transparent' }}>
                            <button
                              type="button"
                              onClick={() => restoreDraft(d)}
                              style={{ flex: 1, minWidth: 0, textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                            >
                              <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{summary}</div>
                              <div style={{ fontSize: 10, color: '#64748b' }}>{typeLabel} · {age < 1 ? 'just now' : age < 24 ? `${age}h ago` : `${Math.floor(age / 24)}d ago`}</div>
                            </button>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setDraftDeleteConfirmId(d.id) }}
                              title="Delete draft"
                              style={{ background: '#fee2e2', border: '1px solid #fecaca', color: '#dc2626', fontSize: 18, lineHeight: 1, cursor: 'pointer', padding: 0, width: 26, height: 26, borderRadius: 4, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                            >×</button>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              <button className="create-panel-close" onClick={closeSmartCreate} aria-label="Close">
                <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
              </button>
              </div>
            </div>
            <div className="create-panel-body" style={{ display: 'flex', flexDirection: 'column', gap: 0, overflowY: 'auto', paddingBottom: 80 }}>

              {/* Form fields — these ARE the search */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '4px 0 12px' }}>

                {/* Company Name with autocomplete */}
                <div style={{ position: 'relative' }}>
                  <div className="create-panel-field" style={{ marginBottom: 0 }}>
                    <label>Client Name (optional)</label>
                    <input
                      ref={smartCreateCompanyInputRef}
                      placeholder="Client (optional)"
                      value={createFormData.company || ''}
                      onChange={(e) => {
                        const v = e.target.value.replace(/(^|\s)(\p{L})/gu, (_, sp, ch) => sp + ch.toUpperCase())
                        setCreateFormData(p => {
                          const snapCompany = ((p as any)._matchSnapshot?.company as string) || ''
                          if ((p as any)._matched && snapCompany && v.trim() !== snapCompany.trim()) {
                            // Same rule as the contact name: renaming a matched
                            // organization breaks the match.
                            const { _matched, _matchSnapshot, _autoFilled, ...rest } = p as any
                            return { ...rest, company: v }
                          }
                          return { ...p, company: v }
                        })
                        setSmartCreateSearch(v)
                        setSmartCreateActiveField('company')
                      }}
                      // Autofocus when the panel was opened as a Company creation
                      // (the effect above also calls focus()/select() to ensure typed text is highlighted).
                      autoFocus={smartCreateType === 'company'}
                      onFocus={() => { setSmartCreateActiveField('company'); if ((createFormData.company || '').length >= 2) setSmartCreateSearch(createFormData.company || '') }}
                      onBlur={() => setTimeout(() => setSmartCreateActiveField(null), 160)}
                      style={undefined}
                    />
                  </div>
                  {/* Autocomplete dropdown — companies (organizations) */}
                  {smartCreateActiveField === 'company' && smartCreateResults && smartCreateResults.contacts.length > 0 && smartCreateSearch.length >= 2 && (
                    <div data-tour-id="create-company-name-suggestions" style={{ marginTop: 4, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: 200, overflowY: 'auto' }}>
                      {smartCreateResults.contacts.filter(c => c.client_type === 'organization').length > 0
                        ? smartCreateResults.contacts.filter(c => c.client_type === 'organization').map((c) => (
                          <div
                            key={c.unified_client_id}
                            style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '0.82rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = '#f1f5f9')}
                            onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}
                            onClick={() => {
                              const apply = () => {
                                setCreateFormData((prev) => ({
                                  ...prev,
                                  company: c.preferred_name || '',
                                  name: prev.name || '',
                                  email: c.preferred_email || '',
                                  phone: c.preferred_phone || '',
                                  street: c.preferred_street || '',
                                  city: c.preferred_city || '',
                                  state: c.preferred_state || '',
                                  zip: '',
                                  clientType: 'organization',
                                  _matched: c.unified_client_id,
                                  _matchedOrgSubType: (c as any).org_primary_sub_type || '',
                                  _matchSnapshot: { name: c.preferred_name || '', company: c.client_type === 'organization' ? (c.preferred_name || '') : '', email: c.preferred_email || '', phone: c.preferred_phone || '', street: c.preferred_street || '', city: c.preferred_city || '', state: c.preferred_state || '', zip: '' } as any,
                                  _autoFilled: { email: !!c.preferred_email, phone: !!c.preferred_phone, street: !!c.preferred_street, city: !!c.preferred_city, state: !!c.preferred_state } as any,
                                }))
                                setSmartCreateSearch('')
                                setSmartCreateResults(null)
                                setSmartCreateActiveField(null)
                              }
                              confirmMatchSwitch(c.preferred_name || 'this company', c.unified_client_id, apply)
                            }}
                          >
                            <div>
                              <div style={{ fontWeight: 600, color: '#1e293b' }}>{c.preferred_name}</div>
                              <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                                {[c.preferred_email, c.preferred_phone, c.preferred_city].filter(Boolean).join(' \u00b7 ')}
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                              <span style={{ fontSize: '0.6rem', padding: '1px 4px', borderRadius: 3, background: '#e0f2fe', color: '#0369a1' }}>Client</span>
                              {c.deal_count > 0 && <span style={{ fontSize: '0.6rem', padding: '1px 4px', borderRadius: 3, background: '#ede9fe', color: '#6d28d9' }}>{c.deal_count}D</span>}
                            </div>
                          </div>
                        ))
                        : smartCreateResults.contacts.map((c) => (
                          <div
                            key={c.unified_client_id}
                            style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '0.82rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = '#f1f5f9')}
                            onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}
                            onClick={() => {
                              const apply = () => {
                                setCreateFormData((prev) => ({
                                  ...prev,
                                  company: c.preferred_name || '',
                                  name: prev.name || '',
                                  email: c.preferred_email || '',
                                  phone: c.preferred_phone || '',
                                  street: c.preferred_street || '',
                                  city: c.preferred_city || '',
                                  state: c.preferred_state || '',
                                  zip: '',
                                  clientType: c.client_type === 'organization' ? 'organization' : 'person',
                                  _matched: c.unified_client_id,
                                  _matchedOrgSubType: c.client_type === 'organization' ? ((c as any).org_primary_sub_type || '') : '',
                                  _matchSnapshot: { name: c.preferred_name || '', company: c.client_type === 'organization' ? (c.preferred_name || '') : '', email: c.preferred_email || '', phone: c.preferred_phone || '', street: c.preferred_street || '', city: c.preferred_city || '', state: c.preferred_state || '', zip: '' } as any,
                                  _autoFilled: { email: !!c.preferred_email, phone: !!c.preferred_phone, street: !!c.preferred_street, city: !!c.preferred_city, state: !!c.preferred_state } as any,
                                }))
                                setSmartCreateSearch('')
                                setSmartCreateResults(null)
                                setSmartCreateActiveField(null)
                              }
                              confirmMatchSwitch(c.preferred_name || 'this contact', c.unified_client_id, apply)
                            }}
                          >
                            <div>
                              <div style={{ fontWeight: 600, color: '#1e293b' }}>{c.preferred_name}</div>
                              <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                                {[c.preferred_email, c.preferred_phone, c.preferred_city].filter(Boolean).join(' \u00b7 ')}
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                              <span style={{ fontSize: '0.6rem', padding: '1px 4px', borderRadius: 3, background: c.client_type === 'organization' ? '#e0f2fe' : '#dbeafe', color: c.client_type === 'organization' ? '#0369a1' : '#1d4ed8' }}>{c.client_type === 'organization' ? 'Organization' : 'Person'}</span>
                            </div>
                          </div>
                        ))
                      }
                    </div>
                  )}
                </div>

                {/* Organization type — contact panel. When the typed org matches an
                    existing organization, show its primary sub-type as a read-only
                    colored token. When it's a NEW org being created inline, offer a
                    classification <select> so org_primary_sub_type gets set on create.
                    Hidden in the dedicated company-create panel (it has its own
                    Primary Sub-type field below). */}
                {smartCreateType !== 'company' && (createFormData.company || '').trim().length > 0 && (() => {
                  const matchedOrg = !!(createFormData as any)._matched && (createFormData as any).clientType === 'organization'
                  if (matchedOrg) {
                    const stKey = ((createFormData as any)._matchedOrgSubType as string) || ''
                    const stDef = orgSubTypes.find(st => st.key === stKey)
                    const stColors = orgSubTypeColors(stKey)
                    return (
                      <div className="create-panel-field" style={{ marginBottom: 0 }}>
                        <label>Client type</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 600, background: stKey ? stColors.bg : '#f1f5f9', color: stKey ? stColors.fg : '#64748b' }}>
                            {stDef ? stDef.label : (stKey || 'Unclassified')}
                          </span>
                          <span style={{ fontSize: '0.68rem', color: '#94a3b8' }}>from existing client</span>
                        </div>
                      </div>
                    )
                  }
                  return (
                    <div className="create-panel-field" style={{ marginBottom: 0 }}>
                      <label>Client type(s)</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', padding: '4px 0' }}>
                        {orgSubTypes.filter(st => st.is_active).map(st => (
                          <label key={st.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.8rem' }}>
                            <input
                              type="checkbox"
                              checked={createOrgSubType.includes(st.key)}
                              onChange={() => setCreateOrgSubType(prev => prev.includes(st.key) ? prev.filter(k => k !== st.key) : [...prev, st.key])}
                            />
                            <span>{st.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )
                })()}

                {/* Organization classification — only when creating an Organization */}
                {smartCreateType === 'company' && (
                  <>
                    <div className="create-panel-field" style={{ marginBottom: 0 }}>
                      <label>Type(s) <span style={{ color: '#dc2626' }}>*</span></label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', padding: '4px 0' }}>
                        {orgSubTypes.filter(st => st.is_active).map(st => (
                          <label key={st.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.8rem' }}>
                            <input
                              type="checkbox"
                              checked={createOrgSubType.includes(st.key)}
                              onChange={() => setCreateOrgSubType(prev => prev.includes(st.key) ? prev.filter(k => k !== st.key) : [...prev, st.key])}
                            />
                            <span>{st.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="create-panel-field" style={{ marginBottom: 0 }}>
                      <label>Accounting Role</label>
                      <select
                        value={createOrgAccountingRole}
                        onChange={(e) => setCreateOrgAccountingRole(e.target.value)}
                      >
                        <option value="customer">Customer</option>
                        <option value="vendor">Vendor</option>
                        <option value="both">Both</option>
                        <option value="neither">Neither</option>
                      </select>
                    </div>
                  </>
                )}

                {/* Contact people — UNIFORM PERSON CARDS (markers cp-create-persons-v1 +
                    cp-create-cards-v1). Card 0 is the PRIMARY contact for the record — its
                    Full name/Email/Phone are the old top-level fields (autocomplete, dedup
                    warnings and matched-contact behavior preserved verbatim); every added
                    card mirrors the exact same rows. Each named added card becomes a
                    contact_persons child on create/update (org mode adds a role label).
                    Exactly ONE Primary email chip + ONE Primary phone chip across ALL
                    cards — those values become the record's preferred_email/phone. */}
                {(() => {
                  const isOrgMode = smartCreateType === 'company' || !!(createFormData.company || '').trim()
                  const primaryEmailAt = effectiveCreatePrimary('email', createPrimaryEmailAt)
                  const primaryPhoneAt = effectiveCreatePrimary('phone', createPrimaryPhoneAt)
                  return (
                    <div className="create-panel-field" data-cp="cp-create-persons-v1" data-cp-cards={CREATE_CARDS_MARKER} data-cp-main-marker={CREATE_MAIN_MARKER} style={{ marginBottom: 0 }}>
                      <label>Contact people</label>
                      {/* ── Card 0 — removable ONLY while Main sits on another card
                          (cp-create-main-v1: the main card is the non-removable one) ── */}
                      <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 6, background: '#f8fafc', marginBottom: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>{isOrgMode ? 'Primary contact' : 'Contact'}</span>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            {renderCreateMainBadge(0)}
                            {createMainIndex !== 0 && createPersons.length > 0 && (
                              <button type="button" className="cp-remove-btn" onClick={removeCreateCard0}>&times;</button>
                            )}
                          </span>
                        </div>
                {/* Contact Name with autocomplete */}
                <div style={{ position: 'relative' }}>
                  <div className="create-panel-field" style={{ marginBottom: 0 }}>
                    <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}><span>Full name <span style={{ color: '#dc2626' }}>*</span></span>{(createFormData as any)._matched && (
                      <button
                        onClick={() => { closeSmartCreate(); window.location.hash = `/contacts/record/${(createFormData as any)._matched}` }}
                        title="Open contact record"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600, padding: 0 }}
                      ><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>Open contact</button>
                    )}</label>
                    <input
                      data-tour-id="create-contact-name"
                      placeholder="Start typing a name..."
                      className={validatePersonNameInput(createFormData.name || '').ok ? undefined : 'cp-input-error'}
                      value={createFormData.name || ''}
                      onChange={(e) => {
                        const v = e.target.value.replace(/(^|\s)(\p{L})/gu, (_, sp, ch) => sp + ch.toUpperCase())
                        setCreateFormData(p => {
                          const snapName = ((p as any)._matchSnapshot?.name as string) || ''
                          if ((p as any)._matched && v.trim() !== snapName.trim()) {
                            // Renaming away from the matched contact means this is a
                            // different person — break the match (submit would have
                            // silently ignored the edit) and let the dedup warnings
                            // re-evaluate against the fields that remain.
                            const { _matched, _matchSnapshot, _autoFilled, ...rest } = p as any
                            return { ...rest, name: v }
                          }
                          return { ...p, name: v }
                        })
                        setSmartCreateSearch(v)
                        setSmartCreateType(null)
                        setSmartCreateActiveField('contact')
                      }}
                      // Autofocus Contact Name unless the panel was launched as a Company creation
                      // (in which case the Company Name input below gets focus via ref + effect).
                      autoFocus={smartCreateType !== 'company'}
                      style={undefined}
                      onFocus={() => { setSmartCreateActiveField('contact'); if ((createFormData.name || '').length >= 2) setSmartCreateSearch(createFormData.name || '') }}
                      onBlur={() => setTimeout(() => setSmartCreateActiveField(null), 160)}
                    />
                  </div>
                  {/* Live person-name validation for person #1 (comma → swap,
                      "&"/" and " couple → split into two contact people). */}
                  {renderPersonNameValidation(
                    createFormData.name || '',
                    (swapped) => setCreateFormData(p => {
                      const snapName = ((p as any)._matchSnapshot?.name as string) || ''
                      if ((p as any)._matched && swapped.trim() !== snapName.trim()) {
                        const { _matched, _matchSnapshot, _autoFilled, ...rest } = p as any
                        return { ...rest, name: swapped }
                      }
                      return { ...p, name: swapped }
                    }),
                    (persons) => {
                      const [firstPerson, ...others] = persons
                      setCreateFormData(p => {
                        const snapName = ((p as any)._matchSnapshot?.name as string) || ''
                        const nextName = firstPerson || ''
                        if ((p as any)._matched && nextName.trim() !== snapName.trim()) {
                          const { _matched, _matchSnapshot, _autoFilled, ...rest } = p as any
                          return { ...rest, name: nextName }
                        }
                        return { ...p, name: nextName }
                      })
                      if (others.length > 0) {
                        setCreatePersons(prev => [...prev, ...others.map(n => newCreatePersonCard(n))])
                      }
                    },
                  )}
                  {/* Autocomplete dropdown — contacts (persons). SUPPRESSED while the
                      Full-name field carries an ACTIVE validation error (cp-create-main-v1):
                      the error + its one-click fix take the space; suggestions return the
                      moment the error clears. */}
                  {smartCreateActiveField === 'contact' && !(createFormData as any)._matched && validatePersonNameInput(createFormData.name || '').ok && smartCreateResults && smartCreateResults.contacts.length > 0 && smartCreateSearch.length >= 2 && (
                    <div data-tour-id="create-contact-name-suggestions" data-cp-suggest={CREATE_SUGGEST_ORGMAP_MARKER} style={{ marginTop: 4, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: 200, overflowY: 'auto' }}>
                      {smartCreateResults.contacts.map((c) => (
                        <div
                          key={c.unified_client_id}
                          style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '0.82rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = '#f1f5f9')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}
                          onClick={() => {
                            const apply = () => {
                              // Type-aware mapping (cp-suggest-orgmap-v1): a PERSON
                              // record fills the card exactly as before; an
                              // ORGANIZATION record routes its display name to the
                              // Organization Name field (never the person Full name)
                              // and prefills the card from the org's primary contact
                              // person when one is on file - else the typed name
                              // stays and record-level email/phone apply.
                              setCreateFormData((prev) => {
                                const sel = mapSuggestionSelection(c, prev.name || '')
                                return {
                                  ...prev,  // preserve unrelated fields (estimateAppointment, title, ownerId, closeDate, etc.)
                                  ...(sel.company != null ? { company: sel.company } : {}),
                                  name: sel.fields.name,
                                  email: sel.fields.email,
                                  phone: sel.fields.phone,
                                  street: sel.fields.street,
                                  city: sel.fields.city,
                                  state: sel.fields.state,
                                  zip: sel.fields.zip,
                                  clientType: sel.fields.clientType,
                                  _matched: c.unified_client_id,
                                  ...(sel.isOrg ? { _matchedOrgSubType: sel.orgSubType } : {}),
                                  _matchSnapshot: sel.matchSnapshot as any,
                                  _autoFilled: sel.autoFilled as any,
                                }
                              })
                              setSmartCreateSearch('')
                              setSmartCreateResults(null)
                            }
                            confirmMatchSwitch(c.preferred_name || 'this contact', c.unified_client_id, apply)
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: 600, color: '#1e293b' }}>{c.preferred_name}</div>
                            <div style={{ fontSize: '0.68rem', color: '#64748b', marginTop: 1 }}>
                              {[c.preferred_phone, c.preferred_email].filter(Boolean).join(' \u00b7 ')}
                            </div>
                            {(c.preferred_street || c.preferred_city) && (
                              <div style={{ fontSize: '0.65rem', color: '#94a3b8', marginTop: 1 }}>
                                {[c.preferred_street, c.preferred_city, c.preferred_state].filter(Boolean).join(', ')}
                              </div>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                            {/* Org records are visibly tagged so they can't be mistaken
                                for people (cp-suggest-orgmap-v1) - same muted chip idiom
                                as the Organization Name dropdown. */}
                            {c.client_type === 'organization' && <span style={{ fontSize: '0.6rem', padding: '1px 4px', borderRadius: 3, background: '#e0f2fe', color: '#0369a1' }}>Client</span>}
                            {c.deal_count > 0 && <span style={{ fontSize: '0.6rem', padding: '1px 4px', borderRadius: 3, background: '#ede9fe', color: '#6d28d9' }}>{c.deal_count}D</span>}
                            {c.lead_count > 0 && <span style={{ fontSize: '0.6rem', padding: '1px 4px', borderRadius: 3, background: '#fef3c7', color: '#92400e' }}>{c.lead_count}L</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Email — multi-value with Primary badge + "Add another" */}
                <div className="create-panel-field" style={{ marginBottom: 0 }}>
                  <label>Email</label>
                  {(createEmails.length > 0 ? createEmails : ['']).map((email, i) => {
                    const isPrimary = i === 0
                    const emailValid = !email || /^[^@]+@[^@]+\.[^@]+$/.test(email)
                    const showErr = isPrimary && emailTouched && email && !emailValid
                    return (
                      <div key={i} className="cp-multi-row">
                        <input
                          type="email"
                          data-tour-id={isPrimary ? 'create-contact-email' : undefined}
                          placeholder={isPrimary ? 'Email' : 'Additional email'}
                          className={showErr ? 'cp-input-error' : ''}
                          value={email}
                          onChange={(e) => {
                            const v = e.target.value
                            setCreateEmails(prev => {
                              const next = prev.length > 0 ? [...prev] : ['']
                              next[i] = v
                              return next
                            })
                            if (isPrimary) {
                              setCreateFormData(p => ({ ...p, email: v, _autoFilled: { ...(p as any)._autoFilled, email: false } }))
                              setEmailTouched(false)
                              if (emailDebounceRef.current) clearTimeout(emailDebounceRef.current)
                              emailDebounceRef.current = setTimeout(() => setEmailTouched(true), 1500)
                              if (v.length >= 3 && !createFormData.name) setSmartCreateSearch(v)
                            }
                          }}
                          onBlur={() => { if (isPrimary) { if (emailDebounceRef.current) clearTimeout(emailDebounceRef.current); setEmailTouched(true) } }}
                          style={undefined}
                        />
                        {renderCreatePrimaryChip(
                          primaryEmailAt.card === 0 && primaryEmailAt.idx === i,
                          () => setCreatePrimaryEmailAt({ card: 0, idx: i }),
                        )}
                        {i > 0 && (
                          <button type="button" className="cp-remove-btn" onClick={() => {
                            setCreateEmails(prev => prev.filter((_, j) => j !== i))
                            adjustPrimaryOnRowRemove(setCreatePrimaryEmailAt, 0, i)
                          }}>&times;</button>
                        )}
                      </div>
                    )
                  })}
                  {emailTouched && createEmails[0] && !/^[^@]+@[^@]+\.[^@]+$/.test(createEmails[0]) && (
                    <small className="cp-validation-error">Not a valid email address</small>
                  )}
                  {/* Always-visible add affordance (cp-create-main-v1) — matches the
                      '+ Add another person'/'+ Add another address' link idiom. */}
                  <button type="button" className="cp-add-link" onClick={() => setCreateEmails(prev => [...prev, ''])}>+ Add another email</button>
                  {smartCreateDupEmail && (
                    <div style={{ marginTop: 4, fontSize: '0.7rem', color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 4, padding: '3px 8px', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span>⚠ This email is already on file for <strong>{smartCreateDupEmail.name}</strong></span>
                      <button
                        type="button"
                        onClick={() => applyDedupMatch(smartCreateDupEmail.id, smartCreateDupEmail.name)}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '0.7rem', fontWeight: 700, color: '#1d4ed8', textDecoration: 'underline' }}
                      >Use this contact</button>
                    </div>
                  )}
                </div>

                {/* Phone — multi-value with type/ext/Primary badge + autocomplete */}
                <div style={{ position: 'relative' }}>
                <div className="create-panel-field" style={{ marginBottom: 0 }}>
                  <label>Phone</label>
                  {(createPhones.length > 0 ? createPhones : [{ type: 'Mobile', number: '', ext: '' }]).map((phone, i) => {
                    const isPrimary = i === 0
                    return (
                      <div key={i} className="cp-multi-row cp-phone-row">
                        <select value={phone.type} onChange={(e) => {
                          setCreatePhones(prev => {
                            const next = prev.length > 0 ? [...prev] : [{ type: 'Mobile', number: '', ext: '' }]
                            next[i] = { ...next[i], type: e.target.value }
                            return next
                          })
                        }}>
                          <option value="Mobile">Mobile</option>
                          <option value="Office">Office</option>
                          <option value="Home">Home</option>
                          <option value="Fax">Fax</option>
                          <option value="Other">Other</option>
                        </select>
                        <input
                          type="text"
                          data-tour-id={isPrimary ? 'create-contact-phone' : undefined}
                          placeholder="(555) 555-5555"
                          value={phone.number}
                          onChange={(e) => {
                            const formatted = formatPhoneDisplay(e.target.value)
                            setCreatePhones(prev => {
                              const next = prev.length > 0 ? [...prev] : [{ type: 'Mobile', number: '', ext: '' }]
                              next[i] = { ...next[i], number: formatted }
                              return next
                            })
                            if (isPrimary) {
                              // NOTE: keep _matched intact — editing a matched
                              // contact's phone should switch the Save button
                              // to "Update Contact", not wipe the match.
                              // Clicking a different result in the dropdown
                              // will trigger the confirm-replace modal.
                              setCreateFormData(p => ({ ...p, phone: formatted, _autoFilled: { ...(p as any)._autoFilled, phone: false } }))
                              const digits = formatted.replace(/\D/g, '')
                              if (digits.length >= 3) {
                                setSmartCreateSearch(formatted)
                                setSmartCreateActiveField('phone' as any)
                              } else {
                                setSmartCreateSearch('')
                                setSmartCreateResults(null)
                                setSmartCreateActiveField(null)
                              }
                            }
                          }}
                          onFocus={() => {
                            if (!isPrimary) return
                            const digits = (phone.number || '').replace(/\D/g, '')
                            if (digits.length >= 3) { setSmartCreateSearch(phone.number || ''); setSmartCreateActiveField('phone' as any) }
                          }}
                          onBlur={() => { if (isPrimary) setTimeout(() => setSmartCreateActiveField(null), 160) }}
                          style={undefined}
                        />
                        <input
                          type="text"
                          placeholder="Ext."
                          className="cp-ext-input"
                          value={phone.ext}
                          onChange={(e) => {
                            const digits = e.target.value.replace(/[^\d]/g, '')
                            setCreatePhones(prev => {
                              const next = prev.length > 0 ? [...prev] : [{ type: 'Mobile', number: '', ext: '' }]
                              next[i] = { ...next[i], ext: digits }
                              return next
                            })
                          }}
                        />
                        {renderCreatePrimaryChip(
                          primaryPhoneAt.card === 0 && primaryPhoneAt.idx === i,
                          () => setCreatePrimaryPhoneAt({ card: 0, idx: i }),
                        )}
                        {i > 0 && (
                          <button type="button" className="cp-remove-btn" onClick={() => {
                            setCreatePhones(prev => prev.filter((_, j) => j !== i))
                            adjustPrimaryOnRowRemove(setCreatePrimaryPhoneAt, 0, i)
                          }}>&times;</button>
                        )}
                      </div>
                    )
                  })}
                  {/* Always-visible add affordance (cp-create-main-v1). */}
                  <button type="button" className="cp-add-link" onClick={() => setCreatePhones(prev => [...prev, { type: 'Mobile', number: '', ext: '' }])}>+ Add another phone number</button>
                </div>
                {/* Phone autocomplete dropdown */}
                {(smartCreateActiveField as any) === 'phone' && smartCreateResults && smartCreateResults.contacts.length > 0 && (createFormData.phone || '').replace(/\D/g, '').length >= 3 && !((() => {
                  const typedDigits = (createFormData.phone || '').replace(/\D/g, '')
                  const matchedPhone = ((createFormData as any)._matchSnapshot?.phone as string) || ''
                  const matchedDigits = matchedPhone.replace(/\D/g, '')
                  return (createFormData as any)._matched && typedDigits.length >= 7 && matchedDigits.length >= 7 && (typedDigits === matchedDigits || typedDigits.endsWith(matchedDigits) || matchedDigits.endsWith(typedDigits))
                })()) && (
                  <div style={{ marginTop: 4, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: 240, overflowY: 'auto' }}>
                    {(createFormData as any)._matched && (() => {
                      const currentName =
                        ((createFormData as any)._matchSnapshot?.name as string) ||
                        ((createFormData as any)._matchSnapshot?.company as string) ||
                        createFormData.name ||
                        createFormData.company ||
                        'current contact'
                      return (<>
                        <div
                          style={{ padding: '10px 12px', cursor: 'pointer', fontSize: '0.82rem', borderBottom: '1px solid #e5e7eb', background: '#f0fdf4' }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = '#dcfce7')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = '#f0fdf4')}
                          onClick={() => {
                            setCreateFormData(p => ({ ...p, _matchSnapshot: { ...(p as any)._matchSnapshot, phone: p.phone } }))
                            setSmartCreateSearch(''); setSmartCreateResults(null); setSmartCreateActiveField(null)
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, color: '#166534' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 1l4 4-4 4"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/></svg>
                            Replace phone on {currentName}
                          </div>
                          <div style={{ fontSize: '0.68rem', color: '#15803d', marginTop: 2 }}>
                            Overwrite the existing number with this one
                          </div>
                        </div>
                        <div
                          style={{ padding: '10px 12px', cursor: 'pointer', fontSize: '0.82rem', borderBottom: '1px solid #e5e7eb', background: '#eff6ff' }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = '#dbeafe')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = '#eff6ff')}
                          onClick={() => {
                            const newNum = createFormData.phone || ''
                            const existingNum = ((createFormData as any)._matchSnapshot?.phone as string) || ''
                            setCreatePhones(prev => {
                              const updated = [...prev]
                              updated[0] = { ...updated[0], number: existingNum }
                              if (!updated.some(p => p.number.replace(/\D/g, '') === newNum.replace(/\D/g, ''))) {
                                updated.push({ type: 'Mobile', number: newNum, ext: '' })
                              }
                              return updated
                            })
                            setCreateFormData(p => ({ ...p, phone: existingNum }))
                            setSmartCreateSearch(''); setSmartCreateResults(null); setSmartCreateActiveField(null)
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, color: '#1d4ed8' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                            Add as additional phone
                          </div>
                          <div style={{ fontSize: '0.68rem', color: '#2563eb', marginTop: 2 }}>
                            Keep the existing number and add this as a second number
                          </div>
                        </div>
                      </>)
                    })()}
                    {smartCreateResults.contacts.filter(c => c.unified_client_id !== (createFormData as any)._matched).map((c) => (
                      <div key={c.unified_client_id} style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '0.82rem' }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = '#f1f5f9')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}
                        onClick={() => {
                          const apply = () => {
                            setCreateFormData((prev) => ({
                              ...prev,
                              name: c.preferred_name || '',
                              email: c.preferred_email || '',
                              phone: c.preferred_phone || prev.phone || '',
                              street: c.preferred_street || '',
                              city: c.preferred_city || '',
                              state: c.preferred_state || '',
                              zip: '',
                              clientType: c.client_type === 'organization' ? 'organization' : 'person',
                              _matched: c.unified_client_id,
                              _matchSnapshot: { name: c.preferred_name || '', company: c.client_type === 'organization' ? (c.preferred_name || '') : '', email: c.preferred_email || '', phone: c.preferred_phone || '', street: c.preferred_street || '', city: c.preferred_city || '', state: c.preferred_state || '', zip: '' } as any,
                              _autoFilled: { name: true, email: !!c.preferred_email, street: !!c.preferred_street, city: !!c.preferred_city, state: !!c.preferred_state } as any,
                            }))
                            setSmartCreateSearch('')
                            setSmartCreateResults(null)
                            setSmartCreateActiveField(null)
                          }
                          confirmMatchSwitch(c.preferred_name || 'this contact', c.unified_client_id, apply)
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ fontWeight: 600, color: '#1e293b' }}>{c.preferred_name}</div>
                          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                            {c.deal_count > 0 && <span style={{ fontSize: '0.6rem', padding: '1px 4px', borderRadius: 3, background: '#ede9fe', color: '#6d28d9' }}>{c.deal_count}D</span>}
                            {c.lead_count > 0 && <span style={{ fontSize: '0.6rem', padding: '1px 4px', borderRadius: 3, background: '#fef3c7', color: '#92400e' }}>{c.lead_count}L</span>}
                          </div>
                        </div>
                        <div style={{ fontSize: '0.68rem', color: '#64748b', marginTop: 2 }}>
                          {[c.preferred_phone, c.preferred_email].filter(Boolean).join(' \u00b7 ')}
                        </div>
                        {(c.preferred_street || c.preferred_city) && (
                          <div style={{ fontSize: '0.65rem', color: '#94a3b8', marginTop: 1 }}>
                            {[c.preferred_street, c.preferred_city, c.preferred_state].filter(Boolean).join(', ')}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                  {smartCreateDupPhone && (
                    <div style={{ marginTop: 4, fontSize: '0.7rem', color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 4, padding: '3px 8px', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span>⚠ This number is already on file for <strong>{smartCreateDupPhone.name}</strong></span>
                      <button
                        type="button"
                        onClick={() => applyDedupMatch(smartCreateDupPhone.id, smartCreateDupPhone.name)}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '0.7rem', fontWeight: 700, color: '#1d4ed8', textDecoration: 'underline' }}
                      >Use this contact</button>
                    </div>
                  )}
                </div>

                      </div>

                      {/* ── Added person cards — identical rows to card 0 ── */}
                      {createPersons.map((person, i) => {
                        const nameInvalid = !validatePersonNameInput(person.name || '').ok
                        const cardNo = i + 1
                        return (
                          <div key={i} style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 6, background: '#f8fafc', marginBottom: 6 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>Person {i + 2}</span>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                {renderCreateMainBadge(cardNo)}
                                {createMainIndex !== cardNo && (
                                  <button type="button" className="cp-remove-btn" onClick={() => removeCreatePersonCard(i)}>&times;</button>
                                )}
                              </span>
                            </div>
                            <div className="create-panel-field" style={{ marginBottom: 0 }}>
                              <label>Full name</label>
                              <input
                                type="text"
                                placeholder="Full name"
                                className={nameInvalid ? 'cp-input-error' : undefined}
                                value={person.name}
                                onChange={(e) => {
                                  const v = e.target.value.replace(/(^|\s)(\p{L})/gu, (_, sp, ch) => sp + ch.toUpperCase())
                                  setCreatePersons(prev => prev.map((pp, j) => (j === i ? { ...pp, name: v } : pp)))
                                }}
                              />
                            </div>
                            {renderPersonNameValidation(
                              person.name || '',
                              (swapped) => setCreatePersons(prev => prev.map((pp, j) => (j === i ? { ...pp, name: swapped } : pp))),
                              (persons) => setCreatePersons(prev => {
                                const next = [...prev]
                                const [firstPerson, ...others] = persons
                                next[i] = { ...next[i], name: firstPerson || '' }
                                next.splice(i + 1, 0, ...others.map(n => newCreatePersonCard(n)))
                                return next
                              }),
                            )}
                            {isOrgMode && (
                              <div className="create-panel-field" style={{ marginBottom: 0 }}>
                                <label>Role / title</label>
                                <input
                                  type="text"
                                  placeholder="Role / title (optional)"
                                  value={person.roleLabel || ''}
                                  onChange={(e) => setCreatePersons(prev => prev.map((pp, j) => (j === i ? { ...pp, roleLabel: e.target.value } : pp)))}
                                  style={{ width: '100%', minWidth: 0, boxSizing: 'border-box' }}
                                />
                              </div>
                            )}
                            <div className="create-panel-field" style={{ marginBottom: 0 }}>
                              <label>Email</label>
                              {(person.emails.length > 0 ? person.emails : ['']).map((email, ei) => (
                                <div key={ei} className="cp-multi-row">
                                  <input
                                    type="email"
                                    placeholder={ei === 0 ? 'Email' : 'Additional email'}
                                    value={email}
                                    onChange={(e) => {
                                      const v = e.target.value
                                      setCreatePersons(prev => prev.map((pp, j) => {
                                        if (j !== i) return pp
                                        const next = pp.emails.length > 0 ? [...pp.emails] : ['']
                                        next[ei] = v
                                        return { ...pp, emails: next }
                                      }))
                                    }}
                                  />
                                  {renderCreatePrimaryChip(
                                    primaryEmailAt.card === cardNo && primaryEmailAt.idx === ei,
                                    () => setCreatePrimaryEmailAt({ card: cardNo, idx: ei }),
                                  )}
                                  {ei > 0 && (
                                    <button type="button" className="cp-remove-btn" onClick={() => {
                                      setCreatePersons(prev => prev.map((pp, j) => (j === i ? { ...pp, emails: pp.emails.filter((_, k) => k !== ei) } : pp)))
                                      adjustPrimaryOnRowRemove(setCreatePrimaryEmailAt, cardNo, ei)
                                    }}>&times;</button>
                                  )}
                                </div>
                              ))}
                              {/* Always-visible add affordance (cp-create-main-v1). */}
                              <button type="button" className="cp-add-link" onClick={() => setCreatePersons(prev => prev.map((pp, j) => (j === i ? { ...pp, emails: [...pp.emails, ''] } : pp)))}>+ Add another email</button>
                            </div>
                            <div className="create-panel-field" style={{ marginBottom: 0 }}>
                              <label>Phone</label>
                              {(person.phones.length > 0 ? person.phones : [{ type: 'Mobile', number: '', ext: '' }]).map((phone, pi) => (
                                <div key={pi} className="cp-multi-row cp-phone-row">
                                  <select value={phone.type} onChange={(e) => {
                                    setCreatePersons(prev => prev.map((pp, j) => {
                                      if (j !== i) return pp
                                      const next = pp.phones.length > 0 ? [...pp.phones] : [{ type: 'Mobile', number: '', ext: '' }]
                                      next[pi] = { ...next[pi], type: e.target.value }
                                      return { ...pp, phones: next }
                                    }))
                                  }}>
                                    <option value="Mobile">Mobile</option>
                                    <option value="Office">Office</option>
                                    <option value="Home">Home</option>
                                    <option value="Fax">Fax</option>
                                    <option value="Other">Other</option>
                                  </select>
                                  <input
                                    type="text"
                                    placeholder="(555) 555-5555"
                                    value={phone.number}
                                    onChange={(e) => {
                                      const formatted = formatPhoneDisplay(e.target.value)
                                      setCreatePersons(prev => prev.map((pp, j) => {
                                        if (j !== i) return pp
                                        const next = pp.phones.length > 0 ? [...pp.phones] : [{ type: 'Mobile', number: '', ext: '' }]
                                        next[pi] = { ...next[pi], number: formatted }
                                        return { ...pp, phones: next }
                                      }))
                                    }}
                                  />
                                  <input
                                    type="text"
                                    placeholder="Ext."
                                    className="cp-ext-input"
                                    value={phone.ext}
                                    onChange={(e) => {
                                      const digits = e.target.value.replace(/[^\d]/g, '')
                                      setCreatePersons(prev => prev.map((pp, j) => {
                                        if (j !== i) return pp
                                        const next = pp.phones.length > 0 ? [...pp.phones] : [{ type: 'Mobile', number: '', ext: '' }]
                                        next[pi] = { ...next[pi], ext: digits }
                                        return { ...pp, phones: next }
                                      }))
                                    }}
                                  />
                                  {renderCreatePrimaryChip(
                                    primaryPhoneAt.card === cardNo && primaryPhoneAt.idx === pi,
                                    () => setCreatePrimaryPhoneAt({ card: cardNo, idx: pi }),
                                  )}
                                  {pi > 0 && (
                                    <button type="button" className="cp-remove-btn" onClick={() => {
                                      setCreatePersons(prev => prev.map((pp, j) => (j === i ? { ...pp, phones: pp.phones.filter((_, k) => k !== pi) } : pp)))
                                      adjustPrimaryOnRowRemove(setCreatePrimaryPhoneAt, cardNo, pi)
                                    }}>&times;</button>
                                  )}
                                </div>
                              ))}
                              {/* Always-visible add affordance (cp-create-main-v1). */}
                              <button type="button" className="cp-add-link" onClick={() => setCreatePersons(prev => prev.map((pp, j) => (j === i ? { ...pp, phones: [...pp.phones, { type: 'Mobile', number: '', ext: '' }] } : pp)))}>+ Add another phone number</button>
                            </div>
                          </div>
                        )
                      })}
                      <button type="button" className="cp-add-link" onClick={() => setCreatePersons(prev => [...prev, newCreatePersonCard()])}>+ Add another person</button>
                    </div>
                  )
                })()}

                {/* Address — Google Places Autocomplete fills city/state/zip
                    on selection. Free typing still works for weird rural
                    addresses Google can't match. */}
                <div className="create-panel-field" style={{ marginBottom: 0 }}>
                  <label>Street Address</label>
                  <AddressInput
                    dataTourId="create-contact-address"
                    placeholder="Start typing an address…"
                    value={createFormData.street || ''}
                    onChange={(v) => setCreateFormData(p => ({ ...p, street: v, _autoFilled: { ...(p as any)._autoFilled, street: false } }))}
                    onAddressPicked={(addr) => setCreateFormData(p => ({
                      ...p,
                      street: addr.street || p.street,
                      city: addr.city || p.city,
                      state: addr.state || p.state,
                      zip: addr.zip || p.zip,
                      _autoFilled: { ...(p as any)._autoFilled, street: true, city: true, state: true },
                    }))}
                  />
                  {smartCreateDupAddress && (
                    <div style={{ marginTop: 4, fontSize: '0.7rem', color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 4, padding: '3px 8px' }}>
                      ⚠ This address is already on file for <strong>{smartCreateDupAddress.name}</strong>
                    </div>
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8 }}>
                  <div className="create-panel-field" style={{ marginBottom: 0 }}>
                    <label>City</label>
                    <input placeholder="City" value={createFormData.city || ''} onChange={(e) => { const v = e.target.value.replace(/(^|\s)(\p{L})/gu, (_, sp, ch) => sp + ch.toUpperCase()); setCreateFormData(p => ({ ...p, city: v, _autoFilled: { ...(p as any)._autoFilled, city: false } })) }} style={undefined} />
                  </div>
                  <div className="create-panel-field" style={{ marginBottom: 0 }}>
                    <label>State</label>
                    <input placeholder="ST" maxLength={2} value={createFormData.state || ''} onChange={(e) => setCreateFormData(p => ({ ...p, state: e.target.value.toUpperCase(), _autoFilled: { ...(p as any)._autoFilled, state: false } }))} style={undefined} />
                  </div>
                  <div className="create-panel-field" style={{ marginBottom: 0 }}>
                    <label>ZIP</label>
                    <input placeholder="ZIP" value={createFormData.zip || ''} onChange={(e) => setCreateFormData(p => ({ ...p, zip: e.target.value }))} />
                  </div>
                </div>

                {/* Additional addresses — saved to the contact (matched or newly
                    created) as extra contact_addresses rows on submit. */}
                {createExtraAddresses.map((addr, i) => (
                  <div key={i} style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 8, background: '#f8fafc' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>Additional address</span>
                      <button type="button" className="cp-remove-btn" onClick={() => setCreateExtraAddresses(prev => prev.filter((_, j) => j !== i))}>&times;</button>
                    </div>
                    <AddressInput
                      placeholder="Start typing an address…"
                      value={addr.street}
                      onChange={(v) => setCreateExtraAddresses(prev => prev.map((a, j) => j === i ? { ...a, street: v } : a))}
                      onAddressPicked={(picked) => setCreateExtraAddresses(prev => prev.map((a, j) => j === i ? {
                        street: picked.street || a.street,
                        city: picked.city || a.city,
                        state: picked.state || a.state,
                        zip: picked.zip || a.zip,
                      } : a))}
                      style={{ width: '100%', boxSizing: 'border-box' }}
                    />
                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr)', gap: 8 }}>
                      <input placeholder="City" value={addr.city} onChange={(e) => setCreateExtraAddresses(prev => prev.map((a, j) => j === i ? { ...a, city: e.target.value } : a))} style={{ width: '100%', minWidth: 0, boxSizing: 'border-box' }} />
                      <input placeholder="ST" maxLength={2} value={addr.state} onChange={(e) => setCreateExtraAddresses(prev => prev.map((a, j) => j === i ? { ...a, state: e.target.value.toUpperCase() } : a))} style={{ width: '100%', minWidth: 0, boxSizing: 'border-box' }} />
                      <input placeholder="ZIP" value={addr.zip} onChange={(e) => setCreateExtraAddresses(prev => prev.map((a, j) => j === i ? { ...a, zip: e.target.value } : a))} style={{ width: '100%', minWidth: 0, boxSizing: 'border-box' }} />
                    </div>
                  </div>
                ))}
                <button type="button" className="cp-add-link" style={{ alignSelf: 'flex-start' }} onClick={() => setCreateExtraAddresses(prev => [...prev, { street: '', city: '', state: '', zip: '' }])}>+ Add another address</button>

                {/* Notes — always visible */}
                <div className="create-panel-field" style={{ marginBottom: 0 }}>
                  <label>Notes</label>
                  <textarea rows={2} placeholder="Notes..." value={createFormData.notes || ''} onChange={(e) => { const v = e.target.value.replace(/(^|[.!?]\s+)(\p{L})/gu, (_, pre, ch) => pre + ch.toUpperCase()); setCreateFormData(p => ({ ...p, notes: v })) }} style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem', resize: 'vertical' }} />
                  {(createFormData as any)._matched && (<>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6 }}>
                      {createFormData.notes?.trim() && (
                        <button
                          disabled={createSaving}
                          onClick={async () => {
                            const matchedId = (createFormData as any)._matched
                            if (!matchedId || !createFormData.notes?.trim()) return
                            setCreateSaving(true)
                            try {
                              await createNote({ subject: createFormData.notes.trim().substring(0, 100), description: createFormData.notes.trim(), unifiedClientId: matchedId })
                              setCreateFormData((p: any) => ({ ...p, notes: '' }))
                              setDealToast({ id: Date.now(), message: 'Note added to contact', type: 'success' })
                              if (smartCreateNotesOpen) {
                                try { const acts = await fetchClientActivities(matchedId); setSmartCreateNotesList(acts.filter((a: any) => (a.provider_type || '').toUpperCase() === 'NOTE')) } catch { /* ignore */ }
                              }
                            } catch (e: any) {
                              setCreateError(e.message || 'Failed to add note')
                            } finally {
                              setCreateSaving(false)
                            }
                          }}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', fontSize: '0.75rem', fontWeight: 600, border: '1px solid #bfdbfe', borderRadius: 4, background: '#eff6ff', color: '#1d4ed8', cursor: 'pointer' }}
                        ><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>{createSaving ? 'Adding…' : 'Add Note'}</button>
                      )}
                      <button onClick={async () => {
                        if (smartCreateNotesOpen) { setSmartCreateNotesOpen(false); return }
                        const matchedId = (createFormData as any)._matched
                        if (!matchedId) return
                        setSmartCreateNotesOpen(true)
                        setSmartCreateNotesLoading(true)
                        try {
                          const acts = await fetchClientActivities(matchedId)
                          setSmartCreateNotesList(acts.filter((a: any) => (a.provider_type || '').toUpperCase() === 'NOTE'))
                        } catch { setSmartCreateNotesList([]) }
                        finally { setSmartCreateNotesLoading(false) }
                      }} style={{ background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, padding: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ transition: 'transform 0.15s', transform: smartCreateNotesOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}><path d="M9 18l6-6-6-6"/></svg>
                        {smartCreateNotesOpen ? 'Hide notes' : 'View all notes'}
                        {smartCreateNotesList.length > 0 && <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 16, height: 16, borderRadius: 8, background: '#1976d2', color: '#fff', fontSize: '0.6rem', fontWeight: 700, padding: '0 4px' }}>{smartCreateNotesList.length}</span>}
                      </button>
                    </div>
                    {smartCreateNotesOpen && (
                      <div style={{ marginTop: 6 }}>
                        {smartCreateNotesLoading ? (
                          <div style={{ padding: 12, textAlign: 'center', fontSize: '0.78rem', color: '#94a3b8', border: '1px solid #e2e8f0', borderRadius: 6, background: '#f8fafc' }}>Loading…</div>
                        ) : smartCreateNotesList.length === 0 ? (
                          <div style={{ padding: 12, textAlign: 'center', fontSize: '0.78rem', color: '#94a3b8', border: '1px solid #e2e8f0', borderRadius: 6, background: '#f8fafc' }}>No notes yet</div>
                        ) : (() => {
                          const contactNotes = smartCreateNotesList.filter((n: any) => !n.owner_type_id || n.owner_type_id === 0)
                          const dealGroups: Record<number, { title: string; notes: any[] }> = {}
                          const leadGroups: Record<number, { title: string; notes: any[] }> = {}
                          smartCreateNotesList.forEach((n: any) => {
                            if (n.owner_type_id === 2 && n.owner_id) {
                              if (!dealGroups[n.owner_id]) {
                                const deal = createPanelDeals.find((d: any) => Number(d.id) === Number(n.owner_id))
                                dealGroups[n.owner_id] = { title: deal?.title || `Deal #${n.owner_id}`, notes: [] }
                              }
                              dealGroups[n.owner_id].notes.push(n)
                            } else if (n.owner_type_id === 1 && n.owner_id) {
                              if (!leadGroups[n.owner_id]) {
                                const lead = createPanelLeads.find((l: any) => Number(l.id) === Number(n.owner_id))
                                leadGroups[n.owner_id] = { title: lead?.title || `Lead #${n.owner_id}`, notes: [] }
                              }
                              leadGroups[n.owner_id].notes.push(n)
                            }
                          })
                          const sections: { key: string; label: string; icon: string; color: string; notes: any[] }[] = []
                          if (contactNotes.length > 0) sections.push({ key: 'contact', label: 'Contact', icon: 'user', color: '#22c55e', notes: contactNotes })
                          Object.entries(dealGroups).forEach(([id, g]) => sections.push({ key: `deal-${id}`, label: g.title, icon: 'deal', color: '#0091ae', notes: g.notes }))
                          Object.entries(leadGroups).forEach(([id, g]) => sections.push({ key: `lead-${id}`, label: g.title, icon: 'lead', color: '#f59e0b', notes: g.notes }))
                          const renderNote = (n: any) => (
                            <div key={n.id} style={{ padding: '6px 10px', borderBottom: '1px solid #f1f5f9', fontSize: '0.75rem' }}>
                              {smartCreateNoteEditId === n.id ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                                  <input type="text" value={smartCreateNoteEditSubject} onChange={e => setSmartCreateNoteEditSubject(e.target.value)} placeholder="Subject" autoFocus style={{ width: '100%', padding: '4px 7px', fontSize: '0.75rem', fontWeight: 600, border: '2px solid #0091ae', borderRadius: 4, outline: 'none', background: '#fff', color: '#33475b' }} />
                                  <textarea value={smartCreateNoteEditDesc} onChange={e => setSmartCreateNoteEditDesc(e.target.value)} placeholder="Description" rows={3} style={{ width: '100%', padding: '4px 7px', fontSize: '0.75rem', border: '1px solid #cbd6e2', borderRadius: 4, outline: 'none', background: '#fff', color: '#64748b', resize: 'vertical', lineHeight: 1.4 }} />
                                  <div style={{ display: 'flex', gap: 6 }}>
                                    <button disabled={smartCreateNoteEditSaving} onClick={async () => {
                                      setSmartCreateNoteEditSaving(true)
                                      try {
                                        await updateActivity(n.id, { subject: smartCreateNoteEditSubject.trim() || null, description: smartCreateNoteEditDesc.trim() || null })
                                        setSmartCreateNotesList(prev => prev.map(x => x.id === n.id ? { ...x, subject: smartCreateNoteEditSubject.trim() || null, description: smartCreateNoteEditDesc.trim() || null } : x))
                                        setSmartCreateNoteEditId(null)
                                      } catch (e: any) { alert('Failed to save: ' + (e?.message || String(e))) }
                                      finally { setSmartCreateNoteEditSaving(false) }
                                    }} style={{ padding: '3px 10px', fontSize: '0.7rem', fontWeight: 600, background: '#0091ae', color: '#fff', border: 'none', borderRadius: 4, cursor: smartCreateNoteEditSaving ? 'not-allowed' : 'pointer', opacity: smartCreateNoteEditSaving ? 0.7 : 1 }}>{smartCreateNoteEditSaving ? 'Saving…' : 'Save'}</button>
                                    <button onClick={() => setSmartCreateNoteEditId(null)} style={{ padding: '3px 10px', fontSize: '0.7rem', fontWeight: 500, background: '#fff', color: '#64748b', border: '1px solid #cbd6e2', borderRadius: 4, cursor: 'pointer' }}>Cancel</button>
                                  </div>
                                </div>
                              ) : (
                                <div style={{ cursor: 'pointer' }} onClick={() => { setSmartCreateNoteEditId(n.id); setSmartCreateNoteEditSubject(n.subject || ''); setSmartCreateNoteEditDesc(n.description || '') }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
                                    <span style={{ fontWeight: 600, color: '#33475b', fontSize: '0.75rem' }}>{n.subject || '(No subject)'}</span>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0, marginLeft: 8 }}>
                                      <span style={{ fontSize: '0.65rem', color: '#94a3b8' }}>{n.created ? new Date(n.created).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</span>
                                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#b0bec5" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                    </div>
                                  </div>
                                  {n.description && n.description !== n.subject && (
                                    <div style={{ color: '#64748b', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.35, fontSize: '0.72rem' }}>{n.description.length > 150 ? n.description.slice(0, 150) + '…' : n.description}</div>
                                  )}
                                </div>
                              )}
                            </div>
                          )
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {sections.map((sec, si) => {
                                const isOpen = smartCreateNoteCollapsed.size === 0 ? si === 0 : !smartCreateNoteCollapsed.has(sec.key)
                                return (
                                  <div key={sec.key} style={{ border: '1px solid #e2e8f0', borderRadius: 6, background: '#f8fafc', overflow: 'hidden' }}>
                                    <button
                                      type="button"
                                      onClick={() => { const allKeys = new Set(sections.map(s => s.key)); if (isOpen) { setSmartCreateNoteCollapsed(allKeys) } else { allKeys.delete(sec.key); setSmartCreateNoteCollapsed(allKeys) } }}
                                      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.73rem', fontWeight: 600, color: '#33475b', textAlign: 'left' }}
                                    >
                                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={sec.color} strokeWidth="2.5" style={{ transition: 'transform 0.15s', transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)', flexShrink: 0 }}><path d="M9 18l6-6-6-6"/></svg>
                                      <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: sec.color, flexShrink: 0 }} />
                                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {sec.icon === 'deal' ? 'Deal: ' : sec.icon === 'lead' ? 'Lead: ' : ''}{sec.label}
                                      </span>
                                      <span style={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 500, flexShrink: 0 }}>{sec.notes.length}</span>
                                    </button>
                                    {isOpen && (() => {
                                      const showAll = smartCreateNoteShowAll.has(sec.key)
                                      const visible = showAll ? sec.notes : sec.notes.slice(0, 3)
                                      const remaining = sec.notes.length - 3
                                      return (
                                        <div style={{ borderTop: '1px solid #e2e8f0' }}>
                                          {visible.map(renderNote)}
                                          {!showAll && remaining > 0 && (
                                            <button type="button" onClick={() => setSmartCreateNoteShowAll(prev => { const n = new Set(prev); n.add(sec.key); return n })} style={{ width: '100%', padding: '5px 10px', background: 'none', border: 'none', borderTop: '1px solid #f1f5f9', color: '#1976d2', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}>View {remaining} more</button>
                                          )}
                                          {showAll && sec.notes.length > 3 && (
                                            <button type="button" onClick={() => setSmartCreateNoteShowAll(prev => { const n = new Set(prev); n.delete(sec.key); return n })} style={{ width: '100%', padding: '5px 10px', background: 'none', border: 'none', borderTop: '1px solid #f1f5f9', color: '#1976d2', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}>Show less</button>
                                          )}
                                        </div>
                                      )
                                    })()}
                                  </div>
                                )
                              })}
                            </div>
                          )
                        })()}
                      </div>
                    )}
                  </>)}
                </div>
              </div>

              {/* Matched contact info — shown whenever a contact has been
                  selected from the autocomplete dropdown. */}
              {(createFormData as any)._matched && (
                <div style={{ padding: '8px 0 12px', borderTop: '1px solid #e5e7eb', fontSize: '0.72rem', color: '#64748b' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                    <span style={{ color: '#22c55e', fontWeight: 600 }}>Matched existing contact</span>
                    <button onClick={() => setCreateFormData(p => ({ name: p.name, _matched: null, _matchSnapshot: null, _autoFilled: null } as any))} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '0.68rem', textDecoration: 'underline' }}>Clear match</button>
                  </div>
                </div>
              )}

              {/* Divider + Action buttons */}
              <div style={{ padding: '12px 0 0', borderTop: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column' }}>
                {/* Smart save — auto-detect contact vs company vs both.
                    When a matched contact is loaded, the button is hidden
                    until the user actually changes something, at which
                    point it becomes "Update Contact" (PATCH the match)
                    with a secondary "Create New" option. */}
                {(() => {
                  const matched = (createFormData as any)._matched as string | undefined
                  const snap = (createFormData as any)._matchSnapshot as Record<string, string> | undefined
                  // Field-by-field diff — also used to decide which ones to PATCH
                  const diffs: Record<string, [string, string]> = {}
                  if (matched && snap) {
                    const cur = (k: string) => (createFormData as any)[k] || ''
                    const sv = (k: string) => snap[k] || ''
                    for (const k of ['name', 'company', 'email', 'phone', 'street', 'city', 'state', 'zip']) {
                      if (cur(k) !== sv(k)) diffs[k] = [sv(k), cur(k)]
                    }
                  }
                  const fieldDirty = Object.keys(diffs).length > 0
                  // Only FILLED extra rows count as dirty — the add-row links are
                  // always visible now (cp-create-main-v1), so an empty appended
                  // row must not flip a matched contact to "Update Contact".
                  const extraRows = createEmails.slice(1).some(v => (v || '').trim() !== '') || createPhones.slice(1).some(p => (p.number || '').trim() !== '') || createExtraAddresses.some(a => a.street.trim() || a.city.trim() || a.zip.trim()) || createPersons.some(p => (p.name || '').trim())
                  const isDirty = !!(matched && snap) && (fieldDirty || extraRows)
                  // Matched and untouched → nothing to save (notes have their own Add Note button)
                  if (matched && !isDirty) return null
                  // Not matched yet and nothing entered → hide
                  if (!matched && !(createFormData.name || createFormData.company)) return null
                  // Hide when another entity type is being created via the chip
                  if (showCreateModal) return null

                  // Assemble the uniform person cards (cp-create-cards-v1) for the
                  // payload mapper: card 0 = createFormData.name +
                  // createEmails/createPhones, cards 1..N = createPersons. Explicit
                  // chip clicks (non-null pointers) stamp exactly one primary email
                  // and one primary phone across ALL cards; with the chips untouched
                  // no row is flagged and the mapper's mainIndex default (the MAIN
                  // card's first filled row, cp-create-main-v1) applies.
                  const assembleCreateCards = () => {
                    const pe = createPrimaryEmailAt ? effectiveCreatePrimary('email', createPrimaryEmailAt) : null
                    const pp = createPrimaryPhoneAt ? effectiveCreatePrimary('phone', createPrimaryPhoneAt) : null
                    const card0 = {
                      name: createFormData.name || '',
                      emails: createEmails.map((v, i) => ({ value: v || '', primary: pe != null && pe.card === 0 && pe.idx === i })),
                      phones: createPhones.map((p, i) => ({ value: p.number || '', type: p.type, ext: p.ext, primary: pp != null && pp.card === 0 && pp.idx === i })),
                    }
                    const rest = createPersons.map((p, ci) => ({
                      name: p.name,
                      roleLabel: p.roleLabel,
                      emails: p.emails.map((v, i) => ({ value: v || '', primary: pe != null && pe.card === ci + 1 && pe.idx === i })),
                      phones: p.phones.map((ph, i) => ({ value: ph.number || '', type: ph.type, ext: ph.ext, primary: pp != null && pp.card === ci + 1 && pp.idx === i })),
                    }))
                    return [card0, ...rest]
                  }
                  const cardsPayload = buildCreatePersonsPayload(assembleCreateCards(), { orgName: (createFormData.company || '').trim() || undefined, mainIndex: createMainIndex })
                  // Persist card 0's EXTRA email/phone rows — the historical
                  // silent-drop where only index 0 became preferred_email/
                  // preferred_phone and rows 1..N were dropped. person_id stays
                  // NULL (client-level), same as before; a primary-flagged extra
                  // row is sent with is_primary so the chip's pick sticks. Skipped
                  // when card 0 rides inside persons[] (multi-card create) — its
                  // rows persist person-attributed there instead.
                  const persistExtraEmailsPhones = async (cid: string | undefined) => {
                    if (!cid) return
                    // Explicit chip clicks only (null = untouched → the default
                    // primary already rode in as preferredEmail/preferredPhone).
                    const pe = createPrimaryEmailAt ? effectiveCreatePrimary('email', createPrimaryEmailAt) : null
                    const pp = createPrimaryPhoneAt ? effectiveCreatePrimary('phone', createPrimaryPhoneAt) : null
                    for (let i = 1; i < createEmails.length; i++) {
                      const a = (createEmails[i] || '').trim()
                      if (!a) continue
                      const isPrimaryRow = pe != null && pe.card === 0 && pe.idx === i
                      try { await createContactEmail(cid, { address: a, ...(isPrimaryRow ? { is_primary: true } : {}) }) } catch { /* non-fatal — the record itself is already saved */ }
                    }
                    for (let i = 1; i < createPhones.length; i++) {
                      const ph = createPhones[i]
                      const num = (ph.number || '').trim()
                      if (!num) continue
                      const isPrimaryRow = pp != null && pp.card === 0 && pp.idx === i
                      try { await createContactPhone(cid, { number: num, label: ph.type || undefined, ext: ph.ext || undefined, ...(isPrimaryRow ? { is_primary: true } : {}) }) } catch { /* non-fatal */ }
                    }
                  }
                  // Persist the ADDED person cards as contact_persons children
                  // (each with its own email + phone rows) — used on the
                  // update-matched path; the create path sends persons[] inside
                  // the create request itself (cp-create-cards-api-v1).
                  const persistCreatePersons = async (cid: string | undefined) => {
                    if (!cid) return
                    for (const person of createPersons) {
                      const nm = (person.name || '').trim()
                      if (!nm) continue
                      const input: CreateContactPersonInput = { full_name: nm }
                      if (person.roleLabel && person.roleLabel.trim()) input.role_label = person.roleLabel.trim()
                      const emails = person.emails.map(e => (e || '').trim()).filter(Boolean)
                      if (emails.length > 0) input.emails = emails
                      const phones = person.phones
                        .filter(p => (p.number || '').trim())
                        .map(p => ({ number: p.number.trim(), label: p.type || undefined, ext: p.ext || undefined }))
                      if (phones.length > 0) input.phones = phones
                      try { await createContactPerson(cid, input) } catch { /* non-fatal — the client itself is already saved */ }
                    }
                  }

                  const saveNew = async () => {
                    setCreateSaving(true)
                    setCreateError(null)
                    try {
                      // Record naming rule (cp-create-main-v1): the person record
                      // is named from the MAIN card's full name (card 0 unless the
                      // user moved the Main badge; couples passthrough verbatim);
                      // an Organization Name creates the org record named after
                      // the org. The primary-chip email/phone become the record's
                      // preferred_email/preferred_phone; the named cards ride in
                      // persons[] (cp-create-cards-api-v1) with is_primary on the
                      // main card's entry.
                      const mainCardName = (createMainIndex > 0 && createMainIndex <= createPersons.length
                        ? (createPersons[createMainIndex - 1]?.name || '').trim()
                        : '') || createFormData.name
                      let newClientId: string | undefined
                      let personsAttached = false
                      if (createFormData.name) {
                        const newClient = await createClient({
                          preferredName: mainCardName,
                          preferredPhone: cardsPayload.primaryPhone || undefined,
                          preferredEmail: cardsPayload.primaryEmail || undefined,
                          preferredStreet: createFormData.street || undefined,
                          preferredCity: createFormData.city || undefined,
                          preferredState: createFormData.state || undefined,
                          preferredZip: createFormData.zip || undefined,
                          community: createFormData.community || undefined,
                          clientType: 'person',
                          persons: cardsPayload.persons.length > 0 ? cardsPayload.persons : undefined,
                        })
                        newClientId = newClient.unified_client_id
                        personsAttached = cardsPayload.persons.length > 0
                      }
                      if (createFormData.company) {
                        const newCo = await createClient({
                          preferredName: createFormData.company,
                          preferredPhone: !createFormData.name ? (cardsPayload.primaryPhone || undefined) : undefined,
                          preferredEmail: !createFormData.name ? (cardsPayload.primaryEmail || undefined) : undefined,
                          preferredStreet: createFormData.street || undefined,
                          preferredCity: createFormData.city || undefined,
                          preferredState: createFormData.state || undefined,
                          preferredZip: createFormData.zip || undefined,
                          community: createFormData.community || undefined,
                          clientType: 'organization',
                          org_sub_types: createOrgSubType,
                          org_primary_sub_type: createOrgSubType[0] || undefined,
                          accounting_role: createOrgAccountingRole || undefined,
                          persons: !personsAttached && cardsPayload.persons.length > 0 ? cardsPayload.persons : undefined,
                        })
                        if (!newClientId) newClientId = newCo.unified_client_id
                        if (cardsPayload.persons.length > 0) personsAttached = true
                      }
                      await persistExtraAddresses(newClientId, createExtraAddresses)
                      // Card 0's extra rows already persisted person-attributed when
                      // card 0 rode inside persons[]; otherwise persist them
                      // client-level exactly as before.
                      if (!(personsAttached && cardsPayload.card1InPersons)) {
                        await persistExtraEmailsPhones(newClientId)
                      }
                      // Create a note activity so it shows in the contact's Activities tab
                      if (createFormData.notes?.trim() && newClientId) {
                        try {
                          await createNote({ subject: createFormData.notes.trim().substring(0, 100), description: createFormData.notes.trim(), unifiedClientId: newClientId })
                        } catch { /* non-fatal */ }
                      }
                      closeSmartCreate()
                      fetchClients(1, 50).then((res) => { setClientList(res.clients); setClientTotal(res.total); setClientPages(res.pages) }).catch(() => {})
                    } catch (e: any) {
                      setCreateError(e.message || 'Failed to save')
                    } finally {
                      setCreateSaving(false)
                    }
                  }

                  const updateMatched = async () => {
                    if (!matched || !snap) return
                    setCreateSaving(true)
                    setCreateError(null)
                    try {
                      // Only send fields that actually changed. Email/phone come
                      // from the primary-chip pick (defaults to card 0 row 0 —
                      // identical to the old top-level fields).
                      const payload: Record<string, string | null> = {}
                      if ((createFormData.name || '') !== (snap.name || '')) payload.preferredName = createFormData.name || null
                      if ((cardsPayload.primaryEmail || '') !== (snap.email || '')) payload.preferredEmail = cardsPayload.primaryEmail || null
                      if ((cardsPayload.primaryPhone || '') !== (snap.phone || '')) payload.preferredPhone = cardsPayload.primaryPhone || null
                      if ((createFormData.street || '') !== (snap.street || '')) payload.preferredStreet = createFormData.street || null
                      if ((createFormData.city || '') !== (snap.city || '')) payload.preferredCity = createFormData.city || null
                      if ((createFormData.state || '') !== (snap.state || '')) payload.preferredState = createFormData.state || null
                      if ((createFormData.zip || '') !== (snap.zip || '')) payload.preferredZip = createFormData.zip || null
                      if (Object.keys(payload).length > 0) {
                        await updateClient(matched, payload)
                      }
                      await persistExtraAddresses(matched, createExtraAddresses)
                      await persistExtraEmailsPhones(matched)
                      await persistCreatePersons(matched)
                      // Create a note activity so it shows in the contact's Activities tab
                      if (createFormData.notes?.trim()) {
                        try {
                          await createNote({ subject: createFormData.notes.trim().substring(0, 100), description: createFormData.notes.trim(), unifiedClientId: matched })
                        } catch { /* non-fatal */ }
                      }
                      closeSmartCreate()
                      fetchClients(1, 50).then((res) => { setClientList(res.clients); setClientTotal(res.total); setClientPages(res.pages) }).catch(() => {})
                    } catch (e: any) {
                      setCreateError(e.message || 'Failed to update')
                    } finally {
                      setCreateSaving(false)
                    }
                  }

                  const primaryLabel = matched
                    ? (createSaving ? 'Updating…' : 'Update Contact')
                    : (createSaving ? 'Saving…' : (createFormData.name && createFormData.company ? 'Save Contact + Client' : createFormData.company ? 'Save Client' : 'Save Contact'))

                  return (
                    <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                      <button
                        className="hs-add-contacts-btn"
                        data-tour-id="create-contact-save"
                        style={{ flex: 1, justifyContent: 'center', fontSize: '0.82rem' }}
                        disabled={createSaving}
                        onClick={matched ? updateMatched : saveNew}
                      >{primaryLabel}</button>
                      {matched && (
                        <button
                          className="hs-add-contacts-btn"
                          style={{ flex: '0 0 auto', justifyContent: 'center', fontSize: '0.82rem', background: '#fff', color: '#33475b', border: '1px solid #cbd6e2' }}
                          disabled={createSaving}
                          onClick={saveNew}
                          title="Create a new contact instead of updating the matched one"
                        >Create New</button>
                      )}
                    </div>
                  )
                })()}

                {createError && !showCreateModal && <div style={{ padding: '8px 12px', background: '#fdedee', color: '#d94c53', borderRadius: '4px', fontSize: '13px', marginBottom: 8 }}>{createError}</div>}

                <div style={{ order: -1, marginBottom: 12 }}>
                <div style={{ fontSize: '0.68rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
                  Also create
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {(['deal', 'lead', 'task', 'ticket'] as const).map(t => (
                    <button
                      key={t}
                      data-tour-id={`create-${t}-chip`}
                      onClick={() => {
                        if (showCreateModal === t) {
                          setSmartCreateType(null)
                          setShowCreateModal(null)
                        } else {
                          setSmartCreateType(t)
                          setShowCreateModal(t)
                          if (t === 'deal') {
                            const dd = new Date(); dd.setMonth(dd.getMonth() + 1, 0)
                            setCreateFormData(p => ({ ...p, ...autoTitlePatch(p), dealPipeline: '0', closeDate: dd.toISOString().slice(0, 10) }))
                          } else if (t === 'lead') {
                            setCreateFormData(p => ({ ...p, ...autoTitlePatch(p) }))
                          } else if (t === 'task') {
                            setCreateFormData(p => ({ ...p, title: p.name || p.company || '', _titleAuto: p.name || p.company || '' }))
                          } else {
                            setCreateFormData(p => ({ ...p, title: '' }))
                          }
                        }
                      }}
                      style={{ fontSize: '0.72rem', padding: '5px 10px', border: `1.5px solid ${showCreateModal === t ? '#3b82f6' : '#e2e8f0'}`, borderRadius: 6, background: showCreateModal === t ? '#eff6ff' : '#fff', color: showCreateModal === t ? '#3b82f6' : '#475569', cursor: 'pointer', fontWeight: 500, transition: 'all 0.1s' }}
                    >+ {t.charAt(0).toUpperCase() + t.slice(1)}</button>
                  ))}
                </div>
                </div>

                {/* Existing records for matched contact */}
                {(createFormData as any)._matched && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                    {([
                      { key: 'deals' as const, label: 'Deals', count: createPanelDeals.length, color: '#0091ae', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg> },
                      { key: 'leads' as const, label: 'Leads', count: createPanelLeads.length, color: '#f59e0b', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg> },
                      { key: 'tasks' as const, label: 'Tasks', count: createPanelTasks.length, color: '#7c98b6', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg> },
                      { key: 'tickets' as const, label: 'Tickets', count: createPanelTickets.length, color: '#ef4444', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 0 0-2 2v3a2 2 0 1 1 0 4v3a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-3a2 2 0 1 1 0-4V7a2 2 0 0 0-2-2H5z"/></svg> },
                      { key: 'projects' as const, label: 'Projects', count: createPanelProjects.length, color: '#7c3aed', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg> },
                    ] as const).map(sec => {
                      const isOpen = createPanelExpanded === sec.key
                      return (
                        <button
                          key={sec.key}
                          type="button"
                          onClick={() => setCreatePanelExpanded(isOpen ? null : sec.key)}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5,
                            fontSize: '0.72rem', padding: '5px 10px',
                            border: `1.5px solid ${isOpen ? sec.color : '#e2e8f0'}`,
                            borderRadius: 6,
                            background: isOpen ? `${sec.color}10` : '#fff',
                            color: isOpen ? sec.color : '#475569',
                            cursor: 'pointer', fontWeight: 600, transition: 'all 0.1s',
                            opacity: sec.count === 0 ? 0.5 : 1,
                          }}
                          disabled={sec.count === 0}
                          title={`${sec.count} open ${sec.label.toLowerCase()}`}
                        >
                          {sec.icon}
                          {sec.label}
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            minWidth: 18, height: 18, borderRadius: 9,
                            background: sec.count > 0 ? sec.color : '#cbd5e1',
                            color: '#fff', fontSize: '0.65rem', fontWeight: 700,
                            padding: '0 5px',
                          }}>{sec.count}</span>
                        </button>
                      )
                    })}
                  </div>
                )}

                {/* Expanded entity list */}
                {(createFormData as any)._matched && createPanelExpanded === 'deals' && createPanelDeals.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                    {createPanelDeals.map((d: any) => (
                      <div
                        key={d.id}
                        onClick={() => { closeSmartCreate(); setActiveView('deals'); if (d.categoryId !== dealCategory) setDealCategory(d.categoryId); setSelectedDeal(d); setDealDetailOpen(true); window.location.hash = `/deals/record/${d.id}` }}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', transition: 'background 0.1s' }}
                        onMouseEnter={e => (e.currentTarget.style.background = '#f1f5f9')}
                        onMouseLeave={e => (e.currentTarget.style.background = '#f8fafc')}
                      >
                        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: '#0091ae' }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#33475b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.title || `Deal #${d.id}`}</div>
                          <div style={{ fontSize: '0.68rem', color: '#7c98b6' }}>{d.stageLabel || d.stage || 'Pipeline'}{d.opportunity > 0 ? ` · $${d.opportunity.toLocaleString()}` : ''}</div>
                        </div>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
                      </div>
                    ))}
                  </div>
                )}
                {(createFormData as any)._matched && createPanelExpanded === 'leads' && createPanelLeads.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                    {createPanelLeads.map((l: any) => (
                      <div
                        key={l.id}
                        onClick={() => { closeSmartCreate(); setActiveView('leads'); setModalLead(l) }}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', transition: 'background 0.1s' }}
                        onMouseEnter={e => (e.currentTarget.style.background = '#f1f5f9')}
                        onMouseLeave={e => (e.currentTarget.style.background = '#f8fafc')}
                      >
                        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: '#f59e0b' }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#33475b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.title || `Lead #${l.id}`}</div>
                          <div style={{ fontSize: '0.68rem', color: '#7c98b6' }}>{l.statusLabel || l.status || 'New'}{l.sourceLabel ? ` · ${l.sourceLabel}` : ''}</div>
                        </div>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
                      </div>
                    ))}
                  </div>
                )}
                {(createFormData as any)._matched && createPanelExpanded === 'tasks' && createPanelTasks.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                    {createPanelTasks.map((tk: any) => (
                      <div
                        key={tk.id}
                        onClick={() => { setCreateFormData({ title: tk.title, body: tk.body, priority: tk.priority, due_date: tk.due_date, dueDate: tk.due_date, status: tk.status, assignedTo: tk.assigned_to ? String(tk.assigned_to) : '', id: String(tk.id) } as any); setCreateError(null); setSmartCreateType('task'); setShowCreateModal('task') }}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', transition: 'background 0.1s' }}
                        onMouseEnter={e => (e.currentTarget.style.background = '#f1f5f9')}
                        onMouseLeave={e => (e.currentTarget.style.background = '#f8fafc')}
                      >
                        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: tk.status === 'open' ? '#7c98b6' : tk.status === 'in_progress' ? '#3b82f6' : '#94a3b8' }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#33475b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tk.title || `Task #${tk.id}`}</div>
                          <div style={{ fontSize: '0.68rem', color: '#7c98b6' }}>{tk.status === 'in_progress' ? 'In Progress' : (tk.status || 'open').charAt(0).toUpperCase() + (tk.status || 'open').slice(1)}{tk.priority ? ` · ${tk.priority.charAt(0).toUpperCase() + tk.priority.slice(1)}` : ''}</div>
                        </div>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
                      </div>
                    ))}
                  </div>
                )}
                {(createFormData as any)._matched && createPanelExpanded === 'tickets' && createPanelTickets.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                    {createPanelTickets.map((t: any) => (
                      <div
                        key={t.id}
                        onClick={() => setActiveTicketDrawerId(t.id)}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', transition: 'background 0.1s' }}
                        onMouseEnter={e => (e.currentTarget.style.background = '#f1f5f9')}
                        onMouseLeave={e => (e.currentTarget.style.background = '#f8fafc')}
                      >
                        <span style={{
                          display: 'inline-block', width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                          background: t.status === 'open' || t.status === 'new' ? '#2FC6F6' : t.status === 'in_progress' ? '#47B1E0' : t.status === 'waiting' ? '#FFA726' : t.status === 'escalated' ? '#EF5350' : '#94a3b8',
                        }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#33475b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.subject || t.title || `Ticket #${t.ticket_number || t.id}`}</div>
                          <div style={{ fontSize: '0.68rem', color: '#7c98b6' }}>
                            {t.status === 'in_progress' ? 'In Progress' : (t.status || 'open').charAt(0).toUpperCase() + (t.status || 'open').slice(1)}
                            {t.priority && <> &middot; {t.priority.charAt(0).toUpperCase() + t.priority.slice(1)}</>}
                            {t.category && <> &middot; {t.category}</>}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            updateTicket(t.id, { status: 'closed' })
                              .then(() => setCreatePanelTickets(prev => prev.filter(tk => tk.id !== t.id)))
                              .catch((err: any) => alert('Failed to close ticket: ' + (err?.message || String(err))))
                          }}
                          style={{ padding: '3px 8px', fontSize: '0.68rem', fontWeight: 600, border: '1px solid #cbd5e1', borderRadius: 4, background: '#fff', color: '#64748b', cursor: 'pointer', whiteSpace: 'nowrap' }}
                          title="Close this ticket"
                        >Close</button>
                      </div>
                    ))}
                  </div>
                )}
                {(createFormData as any)._matched && createPanelExpanded === 'projects' && createPanelProjects.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                    {createPanelProjects.map((p: any) => {
                      const stageLabels: Record<string, string> = { P_CREATED: 'Created', P_CONTRACT_SIGNED: 'Contract Signed', P_STARTED: 'Started', P_PAUSED: 'Paused', P_COMPLETION_REQUESTED: 'Completion Requested', P_COMPLETED: 'Completed' }
                      return (
                        <div
                          key={p.id}
                          onClick={() => { closeSmartCreate(); window.location.hash = `/projects/record/${p.id}` }}
                          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', transition: 'background 0.1s' }}
                          onMouseEnter={e => (e.currentTarget.style.background = '#f1f5f9')}
                          onMouseLeave={e => (e.currentTarget.style.background = '#f8fafc')}
                        >
                          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: '#7c3aed' }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#33475b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name || p.deal_title || `Project #${p.id}`}</div>
                            <div style={{ fontSize: '0.68rem', color: '#7c98b6' }}>{stageLabels[p.pipeline_stage] || p.pipeline_stage || 'New'}{p.contract_value > 0 ? ` · $${Number(p.contract_value).toLocaleString()}` : ''}</div>
                          </div>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

              {/* Extra form fields for non-contact types */}
              {showCreateModal && showCreateModal !== 'contact' && showCreateModal !== 'company' && (
                <div style={{ padding: '10px 0 0' }}>
                  {showCreateModal === 'deal' && (<>
                    {(!(createFormData as any)._matched && !createFormData.name && !createFormData.email && !createFormData.phone && !createFormData.company) && (
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 10px', marginBottom: 8, background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 6, fontSize: 12, color: '#9a3412', lineHeight: 1.4 }}>
                        <span style={{ fontSize: 14, lineHeight: 1, flexShrink: 0 }}>⚠</span>
                        <span>No customer linked. This deal won&apos;t appear when searching by customer name — add the customer&apos;s name, email, or phone above to link them.</span>
                      </div>
                    )}
                    <div className="create-panel-field"><label>Deal Title *</label><input placeholder="Deal title" value={createFormData.title || ''} onChange={(e) => setCreateFormData(p => ({ ...p, title: e.target.value }))} /></div>
                    <div className="create-panel-field">
                      <label>Deal Type</label>
                      <select
                        value={(createFormData as any).dealType || 'residential'}
                        onChange={(e) => setCreateFormData((p: any) => ({ ...p, dealType: e.target.value }))}
                        style={{ padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem', background: '#fff', width: '100%' }}
                      >
                        <option value="residential">Residential</option>
                        <option value="commercial">Commercial</option>
                        <option value="community">Community</option>
                        <option value="public_sector">Public Sector</option>
                      </select>
                    </div>
                    {(createFormData as any).dealType === 'community' && (
                      <div className="create-panel-field">
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 500 }}>
                          <input
                            type="checkbox"
                            checked={!!(createFormData as any).isHoa}
                            onChange={(e) => setCreateFormData((p: any) => ({ ...p, isHoa: e.target.checked }))}
                          />
                          HOA-managed community
                        </label>
                      </div>
                    )}
                    <div className="create-panel-field">
                      <label>Appointment Date &amp; Time</label>
                      <AvailabilityDateTimePicker
                        dataTourId="create-deal-datetime"
                        value={createFormData.estimateAppointment || ''}
                        onChange={(v) => setCreateFormData((p) => ({ ...p, estimateAppointment: v, closeDate: v ? v.slice(0, 10) : '' }))}
                        estimatorId={createFormData.ownerId ? parseInt(String(createFormData.ownerId)) : null}
                        onEstimatorChange={(id) => setCreateFormData((p) => ({ ...p, ownerId: String(id) }))}
                        attendeeIds={createFormAttendees}
                        onAttendeesChange={setCreateFormAttendees}
                        // Green-highlight the estimator(s) whose territory covers
                        // this estimate's address. Commercial deals match the
                        // commercial roster; everything else (residential /
                        // community / public-sector) matches the residential one.
                        territoryLocation={{ city: createFormData.city, state: createFormData.state, zip: createFormData.zip }}
                        territorySegment={(createFormData as any).dealType === 'commercial' ? 'commercial' : 'residential'}
                      />
                    </div>
                    {createFormData.estimateAppointment ? (
                      <div className="create-panel-field">
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 500 }}>
                          <input
                            type="checkbox"
                            style={{ width: 16, height: 16, margin: 0, padding: 0, flex: '0 0 auto' }}
                            checked={createFormData.sendConfirmationEmail !== false}
                            onChange={(e) => setCreateFormData((p) => ({ ...p, sendConfirmationEmail: e.target.checked }))}
                          />
                          Email appointment confirmation to client
                        </label>
                      </div>
                    ) : null}
                    <div className="create-panel-field">
                      <label>Estimator *</label>
                      {(() => {
                        // Calendar-driven only: the estimator is picked by
                        // clicking a slot on the availability grid above —
                        // there is no manual roster here. This field just
                        // shows the result (primary blue, attendees green).
                        // Clicking an attendee pill promotes them to primary.
                        const selected = ESTIMATOR_USERS.filter(u => String(createFormData.ownerId) === String(u.userId) || createFormAttendees.includes(u.userId))
                        if (selected.length === 0) {
                          return <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>Pick a slot on the calendar above to assign the estimator.</span>
                        }
                        return (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                            {selected.map(u => {
                              const isPrimary = String(createFormData.ownerId) === String(u.userId)
                              const isAttendee = !isPrimary && createFormAttendees.includes(u.userId)
                              const bg = isPrimary ? '#2563eb' : isAttendee ? '#10b981' : '#f8fafc'
                              const color = isPrimary || isAttendee ? '#fff' : '#516f90'
                              return (
                                <button
                                  key={u.userId}
                                  type="button"
                                  onClick={() => {
                                    // Attendee pill click → promote to primary.
                                    if (isPrimary) return
                                    setCreateFormData((p) => ({ ...p, ownerId: String(u.userId) }))
                                    if (isAttendee) setCreateFormAttendees((prev) => prev.filter((id) => id !== u.userId))
                                  }}
                                  style={{ padding: '4px 10px', borderRadius: 20, fontSize: '0.76rem', fontWeight: 600, cursor: isPrimary ? 'default' : 'pointer', border: (isPrimary || isAttendee) ? 'none' : '1px solid #e5e7eb', background: bg, color, transition: 'all 0.12s' }}
                                >{u.name.split(' ')[0]}</button>
                              )
                            })}
                          </div>
                        )
                      })()}
                    </div>
                  </>)}
                  {showCreateModal === 'lead' && (<>
                    {existingLeadMatches.length > 0 && (
                      <div style={{ padding: '8px 10px', background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 6, marginBottom: 8, fontSize: 12, color: '#854d0e' }}>
                        <div style={{ fontWeight: 700, marginBottom: 6 }}>
                          {existingLeadMatches.length} existing lead{existingLeadMatches.length === 1 ? '' : 's'} match this contact
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {existingLeadMatches.slice(0, 4).map((l: any) => (
                            <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <span style={{ fontWeight: 600 }}>{l.title || `Lead #${l.id}`}</span>
                                {l.statusLabel ? ` — ${l.statusLabel}` : ''}
                                {l.email ? ` · ${l.email}` : ''}
                                {l.phone ? ` · ${l.phone}` : ''}
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  closeCreatePanel()
                                  setActiveView('leads')
                                  setModalLead(l)
                                }}
                                style={{ flexShrink: 0, padding: '2px 8px', fontSize: 11, fontWeight: 600, background: '#ffffff', color: '#854d0e', border: '1px solid #fde68a', borderRadius: 4, cursor: 'pointer' }}
                              >Open</button>
                            </div>
                          ))}
                          {existingLeadMatches.length > 4 && (
                            <div style={{ fontStyle: 'italic' }}>+ {existingLeadMatches.length - 4} more…</div>
                          )}
                        </div>
                      </div>
                    )}
                    <div className="create-panel-field"><label>Lead Title *</label><input placeholder="Lead title" value={createFormData.title || ''} onChange={(e) => setCreateFormData(p => ({ ...p, title: e.target.value }))} /></div>
                    <div className="create-panel-field">
                      <label>Status *</label>
                      <select
                        required
                        value={createFormData.statusId || ''}
                        onChange={(e) => setCreateFormData(p => ({ ...p, statusId: e.target.value }))}
                        style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem' }}
                      >
                        <option value="" disabled>Select status…</option>
                        {leadPipelineStatuses.map(s => (
                          <option key={s.status_id} value={s.status_id}>{s.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="create-panel-field"><label>Notes</label><textarea rows={2} placeholder="Notes..." value={createFormData.notes || ''} onChange={(e) => setCreateFormData(p => ({ ...p, notes: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem', resize: 'vertical' }} /></div>
                  </>)}
                  {showCreateModal === 'task' && (<>
                    <div className="create-panel-field"><label>Task Title *</label><input placeholder="Task title" value={createFormData.title || ''} onChange={(e) => setCreateFormData(p => ({ ...p, title: e.target.value }))} /></div>
                    <div className="create-panel-field"><label>Description</label><textarea rows={2} placeholder="Describe the task..." value={createFormData.body || createFormData.description || ''} onChange={(e) => setCreateFormData(p => ({ ...p, body: e.target.value, description: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem', resize: 'vertical' }} /></div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <div className="create-panel-field"><label>Status</label><select value={createFormData.status || 'open'} onChange={(e) => setCreateFormData(p => ({ ...p, status: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem' }}><option value="open">Open</option><option value="in_progress">In Progress</option><option value="completed">Completed</option></select></div>
                      <div className="create-panel-field"><label>Priority</label><select value={createFormData.priority || 'medium'} onChange={(e) => setCreateFormData(p => ({ ...p, priority: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem' }}><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option></select></div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <div className="create-panel-field"><label>Due Date</label><input type="date" value={createFormData.dueDate || ''} onChange={(e) => setCreateFormData(p => ({ ...p, dueDate: e.target.value }))} /></div>
                      <div className="create-panel-field"><label>Assigned To</label><select value={createFormData.assignedTo || ''} onChange={(e) => setCreateFormData(p => ({ ...p, assignedTo: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem' }}><option value="">Unassigned</option>{CALENDAR_USERS_RAW.map(u => <option key={u.userId} value={String(u.userId)}>{u.name}</option>)}</select></div>
                    </div>
                    {(() => {
                      const cf: any = createFormData
                      if (!cf.linkKind) cf.linkKind = 'contact'
                      const kind = cf.linkKind as 'lead' | 'contact'
                      const pillStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, marginTop: 4, fontSize: 13, background: '#f8fafc' }
                      const inputStyle: React.CSSProperties = { display: 'block', width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, marginTop: 4, fontSize: 13, boxSizing: 'border-box' }
                      const listStyle: React.CSSProperties = { marginTop: 4, background: '#fff', border: '1px solid #cbd6e2', borderRadius: 4, maxHeight: 180, overflowY: 'auto' }
                      const btnStyle: React.CSSProperties = { display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px', background: 'none', border: 'none', borderBottom: '1px solid #f1f5f9', fontSize: 12, cursor: 'pointer' }
                      // "Import from form above" hint: if user filled contact name and hasn't picked a link, offer to use that name as the linked contact.
                      const formContactName: string = (cf.name || '').trim()
                      const showImport = kind === 'contact' && !cf.linkContactId && !cf.linkDealId && !cf.linkProjectId && !!formContactName
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 10, background: '#f8fafc', borderRadius: 4, border: '1px solid #e2e8f0' }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>Link to (optional)</div>
                          <div style={{ display: 'inline-flex', padding: 3, background: '#eef2f6', borderRadius: 999, alignSelf: 'flex-start', gap: 2 }}>
                            <button type="button" onClick={() => setCreateFormData(p => ({ ...p, linkKind: 'lead', linkContactId: '', linkContactLabel: '', linkDealId: '', linkDealLabel: '', linkProjectId: '', linkProjectLabel: '' }))} style={{ padding: '5px 14px', fontSize: 12, fontWeight: 600, background: kind === 'lead' ? '#fff' : 'transparent', color: kind === 'lead' ? '#0f172a' : '#64748b', border: 'none', borderRadius: 999, cursor: 'pointer', boxShadow: kind === 'lead' ? '0 1px 3px rgba(15,23,42,0.12)' : 'none', transition: 'all .15s' }}>Lead</button>
                            <button type="button" onClick={() => setCreateFormData(p => ({ ...p, linkKind: 'contact', linkLeadId: '', linkLeadLabel: '' }))} style={{ padding: '5px 14px', fontSize: 12, fontWeight: 600, background: kind === 'contact' ? '#fff' : 'transparent', color: kind === 'contact' ? '#0f172a' : '#64748b', border: 'none', borderRadius: 999, cursor: 'pointer', boxShadow: kind === 'contact' ? '0 1px 3px rgba(15,23,42,0.12)' : 'none', transition: 'all .15s' }}>Contact</button>
                          </div>
                          {showImport && (
                            <button type="button" onClick={() => {
                              const matchedId = cf._matched as string | undefined
                              if (matchedId) {
                                setCreateFormData(p => ({ ...p, linkContactId: String(matchedId), linkContactLabel: formContactName, __useFormContact: false }))
                              } else {
                                setCreateFormData(p => ({ ...p, linkContactLabel: formContactName, __useFormContact: true }))
                              }
                            }} style={{ alignSelf: 'flex-start', padding: '5px 10px', fontSize: 11, fontWeight: 600, background: '#e0f2fe', color: '#075985', border: '1px solid #7dd3fc', borderRadius: 999, cursor: 'pointer' }}>
                              Use “{formContactName}” from the form above{cf._matched ? '' : ' (will create)'}
                            </button>
                          )}
                          {cf.__useFormContact && !cf.linkContactId && (
                            <div style={pillStyle}>
                              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formContactName} <span style={{ color: '#94a3b8', fontSize: 11 }}>(new contact)</span></span>
                              <button type="button" onClick={() => setCreateFormData(p => ({ ...p, __useFormContact: false, linkContactLabel: '' }))} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 14 }} title="Clear">×</button>
                            </div>
                          )}
                          {kind === 'lead' && (
                            <div>
                              <label style={{ fontSize: 13, color: '#33475b' }}>Lead</label>
                              {cf.linkLeadId ? (
                                <div style={pillStyle}>
                                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cf.linkLeadLabel || cf.linkLeadId}</span>
                                  <button type="button" onClick={() => setCreateFormData(p => ({ ...p, linkLeadId: '', linkLeadLabel: '' }))} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 14 }} title="Clear">×</button>
                                </div>
                              ) : (
                                <input type="text" placeholder="Search leads…" value={taskChainLeadQuery} onChange={e => setTaskChainLeadQuery(e.target.value)} style={inputStyle} />
                              )}
                              {!cf.linkLeadId && taskChainLeadResults.length > 0 && (
                                <div style={listStyle}>
                                  {taskChainLeadResults.map((r: any) => (
                                    <button key={String(r.id)} type="button" onClick={() => { setCreateFormData(p => ({ ...p, linkLeadId: String(r.id), linkLeadLabel: r.title || `Lead #${r.id}` })); setTaskChainLeadQuery('') }} style={btnStyle}>
                                      <div style={{ fontWeight: 600, color: '#0f172a' }}>{r.title || `Lead #${r.id}`}</div>
                                      {(r.email || r.phone) && <div style={{ color: '#64748b', fontSize: 11 }}>{[r.email, r.phone].filter(Boolean).join(' · ')}</div>}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                          {kind === 'contact' && (<>
                          {!cf.__useFormContact && (
                          <div>
                            <label style={{ fontSize: 13, color: '#33475b' }}>Contact</label>
                            {cf.linkContactId ? (
                              <div style={pillStyle}>
                                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cf.linkContactLabel || cf.linkContactId}</span>
                                <button type="button" onClick={() => setCreateFormData(p => ({ ...p, linkContactId: '', linkContactLabel: '', linkDealId: '', linkDealLabel: '', linkProjectId: '', linkProjectLabel: '' }))} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 14 }} title="Clear">×</button>
                              </div>
                            ) : (
                              <input type="text" placeholder="Search contacts…" value={taskChainContactQuery} onChange={e => setTaskChainContactQuery(e.target.value)} style={inputStyle} />
                            )}
                            {!cf.linkContactId && taskChainContactResults.length > 0 && (
                              <div style={listStyle}>
                                {taskChainContactResults.map((r: any) => (
                                  <button key={String(r.unified_client_id)} type="button" onClick={() => { setCreateFormData(p => ({ ...p, linkContactId: String(r.unified_client_id), linkContactLabel: r.preferred_name || r.unified_client_id })); setTaskChainContactQuery('') }} style={btnStyle}>
                                    <div style={{ fontWeight: 600, color: '#0f172a' }}>{r.preferred_name || r.unified_client_id}</div>
                                    {(r.preferred_email || r.preferred_phone) && <div style={{ color: '#64748b', fontSize: 11 }}>{[r.preferred_email, r.preferred_phone].filter(Boolean).join(' · ')}</div>}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          )}
                          <div>
                            <label style={{ fontSize: 13, color: '#33475b' }}>Deal {cf.linkContactId && <span style={{ fontWeight: 400, color: '#94a3b8' }}>(filtered by contact)</span>}</label>
                            {cf.linkDealId ? (
                              <div style={pillStyle}>
                                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cf.linkDealLabel || `Deal #${cf.linkDealId}`}</span>
                                <button type="button" onClick={() => setCreateFormData(p => ({ ...p, linkDealId: '', linkDealLabel: '', linkProjectId: '', linkProjectLabel: '' }))} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 14 }} title="Clear">×</button>
                              </div>
                            ) : (
                              <input type="text" placeholder={cf.linkContactId ? "Filter this contact's deals…" : 'Search deals…'} value={taskChainDealQuery} onChange={e => setTaskChainDealQuery(e.target.value)} style={inputStyle} />
                            )}
                            {!cf.linkDealId && taskChainDealResults.length > 0 && (
                              <div style={listStyle}>
                                {taskChainDealResults.map((r: any) => (
                                  <button key={String(r.id)} type="button" onClick={() => { setCreateFormData(p => ({ ...p, linkDealId: String(r.id), linkDealLabel: r.title || `Deal #${r.id}`, linkContactId: p.linkContactId || (r.unified_client_id ? String(r.unified_client_id) : ''), linkContactLabel: p.linkContactLabel || r.customer_name || r.client_name || p.linkContactLabel })); setTaskChainDealQuery('') }} style={btnStyle}>
                                    <div style={{ fontWeight: 600, color: '#0f172a' }}>{r.title || `Deal #${r.id}`}</div>
                                    {(r.customer_name || r.client_name) && <div style={{ color: '#64748b', fontSize: 11 }}>{r.customer_name || r.client_name}</div>}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          <div>
                            <label style={{ fontSize: 13, color: '#33475b' }}>Project {cf.linkDealId && <span style={{ fontWeight: 400, color: '#94a3b8' }}>(filtered by deal)</span>}</label>
                            {cf.linkProjectId ? (
                              <div style={pillStyle}>
                                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cf.linkProjectLabel || cf.linkProjectId}</span>
                                <button type="button" onClick={() => setCreateFormData(p => ({ ...p, linkProjectId: '', linkProjectLabel: '' }))} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 14 }} title="Clear">×</button>
                              </div>
                            ) : (
                              <input type="text" placeholder={cf.linkDealId ? "Pick a project on this deal…" : 'Pick a deal first, or search projects…'} value={taskChainProjectQuery} onChange={e => setTaskChainProjectQuery(e.target.value)} style={inputStyle} />
                            )}
                            {!cf.linkProjectId && taskChainProjectResults.length > 0 && (
                              <div style={listStyle}>
                                {taskChainProjectResults.map((r: any) => (
                                  <button key={String(r.id)} type="button" onClick={() => { setCreateFormData(p => ({ ...p, linkProjectId: String(r.id), linkProjectLabel: r.name || r.id })); setTaskChainProjectQuery('') }} style={btnStyle}>
                                    <div style={{ fontWeight: 600, color: '#0f172a' }}>{r.name || r.id}</div>
                                    {r.customer_name && <div style={{ color: '#64748b', fontSize: 11 }}>{r.customer_name}</div>}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          </>)}
                        </div>
                      )
                    })()}
                  </>)}
                  {showCreateModal === 'ticket' && (<>
                    {(() => {
                      const hasContact = !!(createFormData as any)._matched || !!createFormData.name || !!createFormData.email || !!createFormData.phone || !!createFormData.company
                      const hasTitle = !!createFormData.title?.trim()
                      const hasType = !!createFormData.category
                      if (!hasContact) return (
                        <div style={{ padding: '8px 12px', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6, fontSize: '0.8rem', color: '#92400e', marginBottom: 4 }}>
                          A contact is required to create a ticket. Fill in the contact fields above or search for an existing contact.
                        </div>
                      )
                      if (!hasTitle) return (
                        <div style={{ padding: '8px 12px', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6, fontSize: '0.8rem', color: '#92400e', marginBottom: 4 }}>
                          A ticket title is required.
                        </div>
                      )
                      if (!hasType) return (
                        <div style={{ padding: '8px 12px', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6, fontSize: '0.8rem', color: '#92400e', marginBottom: 4 }}>
                          A ticket type is required.
                        </div>
                      )
                      return null
                    })()}
                    <div className="create-panel-field"><label>Ticket Title *</label><input placeholder="Ticket title" value={createFormData.title || ''} onChange={(e) => setCreateFormData(p => ({ ...p, title: e.target.value }))} /></div>
                    <div className="create-panel-field"><label>Description</label><textarea rows={2} placeholder="Describe..." value={createFormData.description || ''} onChange={(e) => setCreateFormData(p => ({ ...p, description: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem', resize: 'vertical' }} /></div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <div className="create-panel-field"><label>Priority</label><select value={createFormData.priority || 'medium'} onChange={(e) => setCreateFormData(p => ({ ...p, priority: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem' }}><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="urgent">Urgent</option></select></div>
                      <div className="create-panel-field"><label>Type *</label><select value={createFormData.category || ''} onChange={(e) => setCreateFormData(p => ({ ...p, category: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem' }}><option value="">Select type...</option><option value="Complaint">Complaint</option><option value="Warranty">Warranty</option><option value="Service Request">Service Request</option><option value="Callback">Callback</option><option value="General Inquiry">General Inquiry</option><option value="Other">Other</option></select></div>
                    </div>
                    <div className="create-panel-field">
                      <label>Assigned To</label>
                      <select
                        value={createFormData.assignedTo || ''}
                        onChange={(e) => setCreateFormData(p => ({ ...p, assignedTo: e.target.value }))}
                        style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem' }}
                      >
                        <option value="">Unassigned</option>
                        {CALENDAR_USERS_RAW.map(u => (
                          <option key={u.userId} value={String(u.userId)}>{u.name}</option>
                        ))}
                      </select>
                    </div>
                    {/* Deal typeahead — search any deal across all contacts */}
                    <div className="create-panel-field" style={{ position: 'relative' }}>
                      <label>Link to Deal <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional)</span></label>
                      {createFormData.ticketDealId ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: 13, background: '#f8fafc' }}>
                          <span style={{ flex: 1 }}>{(createFormData as any).ticketDealLabel || `Deal #${createFormData.ticketDealId}`}</span>
                          <button type="button" onClick={() => setCreateFormData(p => ({ ...p, ticketDealId: '', ticketDealLabel: '' }))} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 14 }} title="Clear">×</button>
                        </div>
                      ) : (<>
                        {ticketCreateDeals.length > 0 && (
                          <div style={{ marginBottom: 6 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>This contact's deals</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                              {ticketCreateDeals.map((d: any) => (
                                <button key={d.id} type="button" onClick={() => setCreateFormData(p => ({ ...p, ticketDealId: String(d.id), ticketDealLabel: d.title || `Deal #${d.id}` }))} style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 12, cursor: 'pointer' }}>
                                  {d.title || `Deal #${d.id}`}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        <input
                          value={ticketCreateDealQuery}
                          onChange={e => setTicketCreateDealQuery(e.target.value)}
                          placeholder="Search any deal by title, contact, company, or address…"
                          style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem', boxSizing: 'border-box' }}
                        />
                      </>)}
                      {!createFormData.ticketDealId && ticketCreateDealResults.length > 0 && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 2, background: '#fff', border: '1px solid #cbd6e2', borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', zIndex: 30, maxHeight: 220, overflowY: 'auto' }}>
                          {ticketCreateDealResults.map((d: any) => (
                            <button key={d.id} type="button" onClick={() => { setCreateFormData(p => ({ ...p, ticketDealId: String(d.id), ticketDealLabel: d.title || `Deal #${d.id}` })); setTicketCreateDealQuery('') }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px', background: 'none', border: 'none', borderBottom: '1px solid #f1f5f9', fontSize: 12, cursor: 'pointer' }}>
                              <div style={{ fontWeight: 600, color: '#0f172a' }}>{d.title || `#${d.id}`}</div>
                              <div style={{ color: '#64748b', fontSize: 11 }}>{d.customer_name || d.client_name || ''}</div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Project typeahead — search any project across all contacts */}
                    <div className="create-panel-field" style={{ position: 'relative' }}>
                      <label>Link to Project <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional)</span></label>
                      {createFormData.ticketProjectId ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: 13, background: '#f8fafc' }}>
                          <span style={{ flex: 1 }}>{(createFormData as any).ticketProjectLabel || createFormData.ticketProjectId}</span>
                          <button type="button" onClick={() => setCreateFormData(p => ({ ...p, ticketProjectId: '', ticketProjectLabel: '' }))} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 14 }} title="Clear">×</button>
                        </div>
                      ) : (<>
                        {ticketCreateContactProjects.length > 0 && (
                          <div style={{ marginBottom: 6 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>This contact's projects</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                              {ticketCreateContactProjects.map((p: any) => (
                                <button key={p.id} type="button" onClick={() => setCreateFormData(prev => ({ ...prev, ticketProjectId: p.id, ticketProjectLabel: p.name || p.id }))} style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 12, cursor: 'pointer' }}>
                                  {p.name || p.id}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        <input
                          value={ticketCreateProjectQuery}
                          onChange={e => setTicketCreateProjectQuery(e.target.value)}
                          placeholder="Search any project by name, customer, or address…"
                          style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem', boxSizing: 'border-box' }}
                        />
                      </>)}
                      {!createFormData.ticketProjectId && ticketCreateProjectResults.length > 0 && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 2, background: '#fff', border: '1px solid #cbd6e2', borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', zIndex: 30, maxHeight: 220, overflowY: 'auto' }}>
                          {ticketCreateProjectResults.map((p: any) => (
                            <button key={p.id} type="button" onClick={() => { setCreateFormData(prev => ({ ...prev, ticketProjectId: p.id, ticketProjectLabel: p.name || p.id })); setTicketCreateProjectQuery('') }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px', background: 'none', border: 'none', borderBottom: '1px solid #f1f5f9', fontSize: 12, cursor: 'pointer' }}>
                              <div style={{ fontWeight: 600, color: '#0f172a' }}>{p.name || p.id}</div>
                              <div style={{ color: '#64748b', fontSize: 11 }}>{p.customer_name || ''}</div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </>)}
                </div>
              )}

              {/* Submit */}
              {showCreateModal && (<>
                {createError && <div style={{ padding: '8px 12px', background: '#fdedee', color: '#d94c53', borderRadius: 4, fontSize: '0.8rem', marginBottom: 4 }}>{createError}</div>}
                <div style={{ padding: '10px 0', display: 'flex', gap: 8 }}>
                  <button
                    className="hs-add-contacts-btn"
                    style={{ flex: 1, justifyContent: 'center', ...(createSaving || (showCreateModal === 'ticket' && (!(createFormData as any)._matched && !createFormData.name && !createFormData.email && !createFormData.phone && !createFormData.company || !createFormData.title?.trim())) ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}
                    disabled={createSaving || (showCreateModal === 'ticket' && (!(createFormData as any)._matched && !createFormData.name && !createFormData.email && !createFormData.phone && !createFormData.company || !createFormData.title?.trim()))}
                    onClick={handleCreateSubmit}
                  >
                    {createSaving
                      ? (showCreateModal === 'task' && (createFormData as any).id ? 'Saving...' : 'Creating...')
                      : (showCreateModal === 'task' && (createFormData as any).id ? 'Save changes' : `Create ${showCreateModal.charAt(0).toUpperCase() + showCreateModal.slice(1)}`)}
                  </button>
                </div>
              </>)}

            </div>
          </div>
        </>)}

        {/* Standalone Create Company Slide-in Panel REMOVED — Create Company now opens the unified Smart Create panel. */}

        {/* Standalone Create Ticket Slide-in Panel REMOVED — Create Ticket now opens the unified Smart Create panel with Ticket pre-selected. */}

        {/* ========== Create Modal (tasks, products, quotes) ========== */}
        {!smartCreateOpen && showCreateModal && showCreateModal !== 'contact' && showCreateModal !== 'company' && showCreateModal !== 'ticket' && showCreateModal !== 'deal' && showCreateModal !== 'lead' && (
          <div className="modal-overlay" onClick={() => setShowCreateModal(null)}>
            <div className="modal-card" style={{ maxWidth: '480px' }} onClick={(e) => e.stopPropagation()}>
              <div className="modal-color-bar" style={{ background: showCreateModal === 'task' ? '#7c98b6' : showCreateModal === 'product' ? '#8b5cf6' : showCreateModal === 'quote' ? '#06b6d4' : '#1976d2' }} />
              <button type="button" className="modal-close" onClick={() => setShowCreateModal(null)} aria-label="Close">&times;</button>
              <div className="modal-body">
                <h3 className="modal-title" style={{ marginBottom: '16px' }}>
                  {showCreateModal === 'task' && (createFormData as any).id ? 'Edit Task' : `Create ${showCreateModal.charAt(0).toUpperCase() + showCreateModal.slice(1)}`}
                </h3>
                {createError && <div style={{ padding: '8px 12px', background: '#fdedee', color: '#d94c53', borderRadius: '4px', fontSize: '13px', marginBottom: '12px' }}>{createError}</div>}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {showCreateModal === 'task' && <>
                    <label style={{ fontSize: '13px', color: '#33475b' }}>
                      Task Title *
                      <input type="text" value={createFormData.title || ''} onChange={(e) => setCreateFormData((p) => ({ ...p, title: e.target.value }))}
                        style={{ display: 'block', width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', marginTop: '4px', fontSize: '14px' }} autoFocus />
                    </label>
                    <label style={{ fontSize: '13px', color: '#33475b' }}>
                      Description
                      <textarea value={createFormData.body || ''} onChange={(e) => setCreateFormData((p) => ({ ...p, body: e.target.value }))}
                        style={{ display: 'block', width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', marginTop: '4px', fontSize: '14px', minHeight: '60px', resize: 'vertical' }} />
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <label style={{ fontSize: '13px', color: '#33475b' }}>
                        Status
                        <select value={createFormData.status || 'open'} onChange={(e) => setCreateFormData((p) => ({ ...p, status: e.target.value }))}
                          style={{ display: 'block', width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', marginTop: '4px', fontSize: '14px' }}>
                          <option value="open">Open</option>
                          <option value="in_progress">In Progress</option>
                          <option value="completed">Completed</option>
                        </select>
                      </label>
                      <label style={{ fontSize: '13px', color: '#33475b' }}>
                        Priority
                        <select value={createFormData.priority || 'medium'} onChange={(e) => setCreateFormData((p) => ({ ...p, priority: e.target.value }))}
                          style={{ display: 'block', width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', marginTop: '4px', fontSize: '14px' }}>
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                        </select>
                      </label>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <label style={{ fontSize: '13px', color: '#33475b' }}>
                        Due Date
                        <input type="date" value={createFormData.dueDate || ''} onChange={(e) => setCreateFormData((p) => ({ ...p, dueDate: e.target.value }))}
                          style={{ display: 'block', width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', marginTop: '4px', fontSize: '14px' }} />
                      </label>
                      <label style={{ fontSize: '13px', color: '#33475b' }}>
                        Assigned To
                        <select value={createFormData.assignedTo || ''} onChange={(e) => setCreateFormData((p) => ({ ...p, assignedTo: e.target.value }))}
                          style={{ display: 'block', width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', marginTop: '4px', fontSize: '14px' }}>
                          <option value="">Unassigned</option>
                          {CALENDAR_USERS_RAW.map(u => <option key={u.userId} value={String(u.userId)}>{u.name}</option>)}
                        </select>
                      </label>
                    </div>
                    {(() => {
                      const cf: any = createFormData
                      // Backfill on first render from legacy ownerType/ownerId so editing an existing task shows its link.
                      if (!cf.__chainHydrated && cf.ownerId) {
                        if (cf.ownerType === 'lead') { cf.linkKind = 'lead'; cf.linkLeadId = String(cf.ownerId); cf.linkLeadLabel = cf.ownerLabel || `Lead #${cf.ownerId}` }
                        else if (cf.ownerType === 'client') { cf.linkKind = 'contact'; cf.linkContactId = String(cf.ownerId); cf.linkContactLabel = cf.ownerLabel || cf.ownerId }
                        else if (cf.ownerType === 'deal') { cf.linkKind = 'contact'; cf.linkDealId = String(cf.ownerId); cf.linkDealLabel = cf.ownerLabel || `Deal #${cf.ownerId}` }
                        else if (cf.ownerType === 'project') { cf.linkKind = 'contact'; cf.linkProjectId = String(cf.ownerId); cf.linkProjectLabel = cf.ownerLabel || cf.ownerId }
                        cf.__chainHydrated = true
                      }
                      if (!cf.linkKind) cf.linkKind = 'contact'
                      const kind = cf.linkKind as 'lead' | 'contact'
                      const pillStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, marginTop: 4, fontSize: 14, background: '#f8fafc' }
                      const inputStyle: React.CSSProperties = { display: 'block', width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, marginTop: 4, fontSize: 14, boxSizing: 'border-box' }
                      const listStyle: React.CSSProperties = { marginTop: 4, background: '#fff', border: '1px solid #cbd6e2', borderRadius: 4, maxHeight: 180, overflowY: 'auto' }
                      const btnStyle: React.CSSProperties = { display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px', background: 'none', border: 'none', borderBottom: '1px solid #f1f5f9', fontSize: 12, cursor: 'pointer' }
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 10, background: '#f8fafc', borderRadius: 4, border: '1px solid #e2e8f0' }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>Link to (optional)</div>
                          <div style={{ display: 'inline-flex', padding: 3, background: '#eef2f6', borderRadius: 999, alignSelf: 'flex-start', gap: 2 }}>
                            <button type="button" onClick={() => setCreateFormData(p => ({ ...p, linkKind: 'lead', linkContactId: '', linkContactLabel: '', linkDealId: '', linkDealLabel: '', linkProjectId: '', linkProjectLabel: '' }))} style={{ padding: '5px 14px', fontSize: 12, fontWeight: 600, background: kind === 'lead' ? '#fff' : 'transparent', color: kind === 'lead' ? '#0f172a' : '#64748b', border: 'none', borderRadius: 999, cursor: 'pointer', boxShadow: kind === 'lead' ? '0 1px 3px rgba(15,23,42,0.12)' : 'none', transition: 'all .15s' }}>Lead</button>
                            <button type="button" onClick={() => setCreateFormData(p => ({ ...p, linkKind: 'contact', linkLeadId: '', linkLeadLabel: '' }))} style={{ padding: '5px 14px', fontSize: 12, fontWeight: 600, background: kind === 'contact' ? '#fff' : 'transparent', color: kind === 'contact' ? '#0f172a' : '#64748b', border: 'none', borderRadius: 999, cursor: 'pointer', boxShadow: kind === 'contact' ? '0 1px 3px rgba(15,23,42,0.12)' : 'none', transition: 'all .15s' }}>Contact</button>
                          </div>
                          {kind === 'lead' && (
                            <div>
                              <label style={{ fontSize: 13, color: '#33475b' }}>Lead</label>
                              {cf.linkLeadId ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, marginTop: 4, fontSize: 14, background: '#f8fafc' }}>
                                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cf.linkLeadLabel || cf.linkLeadId}</span>
                                  <button type="button" onClick={() => setCreateFormData(p => ({ ...p, linkLeadId: '', linkLeadLabel: '' }))} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 14 }} title="Clear">×</button>
                                </div>
                              ) : (
                                <input type="text" placeholder="Search leads…" value={taskChainLeadQuery} onChange={e => setTaskChainLeadQuery(e.target.value)} style={{ display: 'block', width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, marginTop: 4, fontSize: 14, boxSizing: 'border-box' }} />
                              )}
                              {!cf.linkLeadId && taskChainLeadResults.length > 0 && (
                                <div style={{ marginTop: 4, background: '#fff', border: '1px solid #cbd6e2', borderRadius: 4, maxHeight: 180, overflowY: 'auto' }}>
                                  {taskChainLeadResults.map((r: any) => (
                                    <button key={String(r.id)} type="button" onClick={() => { setCreateFormData(p => ({ ...p, linkLeadId: String(r.id), linkLeadLabel: r.title || `Lead #${r.id}` })); setTaskChainLeadQuery('') }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px', background: 'none', border: 'none', borderBottom: '1px solid #f1f5f9', fontSize: 12, cursor: 'pointer' }}>
                                      <div style={{ fontWeight: 600, color: '#0f172a' }}>{r.title || `Lead #${r.id}`}</div>
                                      {(r.email || r.phone) && <div style={{ color: '#64748b', fontSize: 11 }}>{[r.email, r.phone].filter(Boolean).join(' · ')}</div>}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                          {kind === 'contact' && (<>
                          {/* Contact */}
                          <div>
                            <label style={{ fontSize: 13, color: '#33475b' }}>Contact</label>
                            {cf.linkContactId ? (
                              <div style={pillStyle}>
                                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cf.linkContactLabel || cf.linkContactId}</span>
                                <button type="button" onClick={() => setCreateFormData(p => ({ ...p, linkContactId: '', linkContactLabel: '', linkDealId: '', linkDealLabel: '', linkProjectId: '', linkProjectLabel: '' }))} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 14 }} title="Clear">×</button>
                              </div>
                            ) : (
                              <input type="text" placeholder="Search contacts…" value={taskChainContactQuery} onChange={e => setTaskChainContactQuery(e.target.value)} style={inputStyle} />
                            )}
                            {!cf.linkContactId && taskChainContactResults.length > 0 && (
                              <div style={listStyle}>
                                {taskChainContactResults.map((r: any) => (
                                  <button key={String(r.unified_client_id)} type="button" onClick={() => { setCreateFormData(p => ({ ...p, linkContactId: String(r.unified_client_id), linkContactLabel: r.preferred_name || r.unified_client_id })); setTaskChainContactQuery('') }} style={btnStyle}>
                                    <div style={{ fontWeight: 600, color: '#0f172a' }}>{r.preferred_name || r.unified_client_id}</div>
                                    {(r.preferred_email || r.preferred_phone) && <div style={{ color: '#64748b', fontSize: 11 }}>{[r.preferred_email, r.preferred_phone].filter(Boolean).join(' · ')}</div>}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          {/* Deal */}
                          <div>
                            <label style={{ fontSize: 13, color: '#33475b' }}>Deal {cf.linkContactId && <span style={{ fontWeight: 400, color: '#94a3b8' }}>(filtered by contact)</span>}</label>
                            {cf.linkDealId ? (
                              <div style={pillStyle}>
                                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cf.linkDealLabel || `Deal #${cf.linkDealId}`}</span>
                                <button type="button" onClick={() => setCreateFormData(p => ({ ...p, linkDealId: '', linkDealLabel: '', linkProjectId: '', linkProjectLabel: '' }))} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 14 }} title="Clear">×</button>
                              </div>
                            ) : (
                              <input type="text" placeholder={cf.linkContactId ? "Filter this contact's deals…" : 'Search deals…'} value={taskChainDealQuery} onChange={e => setTaskChainDealQuery(e.target.value)} style={inputStyle} />
                            )}
                            {!cf.linkDealId && taskChainDealResults.length > 0 && (
                              <div style={listStyle}>
                                {taskChainDealResults.map((r: any) => (
                                  <button key={String(r.id)} type="button" onClick={() => { setCreateFormData(p => ({ ...p, linkDealId: String(r.id), linkDealLabel: r.title || `Deal #${r.id}`, linkContactId: p.linkContactId || (r.unified_client_id ? String(r.unified_client_id) : ''), linkContactLabel: p.linkContactLabel || r.customer_name || r.client_name || p.linkContactLabel })); setTaskChainDealQuery('') }} style={btnStyle}>
                                    <div style={{ fontWeight: 600, color: '#0f172a' }}>{r.title || `Deal #${r.id}`}</div>
                                    {(r.customer_name || r.client_name) && <div style={{ color: '#64748b', fontSize: 11 }}>{r.customer_name || r.client_name}</div>}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          {/* Project */}
                          <div>
                            <label style={{ fontSize: 13, color: '#33475b' }}>Project {cf.linkDealId && <span style={{ fontWeight: 400, color: '#94a3b8' }}>(filtered by deal)</span>}</label>
                            {cf.linkProjectId ? (
                              <div style={pillStyle}>
                                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cf.linkProjectLabel || cf.linkProjectId}</span>
                                <button type="button" onClick={() => setCreateFormData(p => ({ ...p, linkProjectId: '', linkProjectLabel: '' }))} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 14 }} title="Clear">×</button>
                              </div>
                            ) : (
                              <input type="text" placeholder={cf.linkDealId ? "Pick a project on this deal…" : 'Pick a deal first, or search projects…'} value={taskChainProjectQuery} onChange={e => setTaskChainProjectQuery(e.target.value)} style={inputStyle} />
                            )}
                            {!cf.linkProjectId && taskChainProjectResults.length > 0 && (
                              <div style={listStyle}>
                                {taskChainProjectResults.map((r: any) => (
                                  <button key={String(r.id)} type="button" onClick={() => { setCreateFormData(p => ({ ...p, linkProjectId: String(r.id), linkProjectLabel: r.name || r.id })); setTaskChainProjectQuery('') }} style={btnStyle}>
                                    <div style={{ fontWeight: 600, color: '#0f172a' }}>{r.name || r.id}</div>
                                    {r.customer_name && <div style={{ color: '#64748b', fontSize: 11 }}>{r.customer_name}</div>}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          </>)}
                        </div>
                      )
                    })()}
                  </>}
                  {showCreateModal === 'product' && <>
                    <label style={{ fontSize: '13px', color: '#33475b' }}>
                      Product Name *
                      <input type="text" value={createFormData.name || ''} onChange={(e) => setCreateFormData((p) => ({ ...p, name: e.target.value }))}
                        style={{ display: 'block', width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', marginTop: '4px', fontSize: '14px' }} autoFocus />
                    </label>
                    <label style={{ fontSize: '13px', color: '#33475b' }}>
                      Description
                      <textarea value={createFormData.description || ''} onChange={(e) => setCreateFormData((p) => ({ ...p, description: e.target.value }))}
                        style={{ display: 'block', width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', marginTop: '4px', fontSize: '14px', minHeight: '60px', resize: 'vertical' }} />
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <label style={{ fontSize: '13px', color: '#33475b' }}>
                        Unit Price
                        <input type="number" value={createFormData.unitPrice || ''} onChange={(e) => setCreateFormData((p) => ({ ...p, unitPrice: e.target.value }))}
                          style={{ display: 'block', width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', marginTop: '4px', fontSize: '14px' }} step="0.01" />
                      </label>
                      <label style={{ fontSize: '13px', color: '#33475b' }}>
                        Unit
                        <input type="text" value={createFormData.unit || ''} onChange={(e) => setCreateFormData((p) => ({ ...p, unit: e.target.value }))} placeholder="e.g. sq ft, gallon, each"
                          style={{ display: 'block', width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', marginTop: '4px', fontSize: '14px' }} />
                      </label>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <label style={{ fontSize: '13px', color: '#33475b' }}>
                        SKU
                        <input type="text" value={createFormData.sku || ''} onChange={(e) => setCreateFormData((p) => ({ ...p, sku: e.target.value }))}
                          style={{ display: 'block', width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', marginTop: '4px', fontSize: '14px' }} />
                      </label>
                      <label style={{ fontSize: '13px', color: '#33475b' }}>
                        Category
                        <input type="text" value={createFormData.category || ''} onChange={(e) => setCreateFormData((p) => ({ ...p, category: e.target.value }))}
                          style={{ display: 'block', width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', marginTop: '4px', fontSize: '14px' }} />
                      </label>
                    </div>
                  </>}
                  {showCreateModal === 'quote' && <>
                    <label style={{ fontSize: '13px', color: '#33475b' }}>
                      Quote Title *
                      <input type="text" value={createFormData.title || ''} onChange={(e) => setCreateFormData((p) => ({ ...p, title: e.target.value }))}
                        style={{ display: 'block', width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', marginTop: '4px', fontSize: '14px' }} autoFocus />
                    </label>
                    <label style={{ fontSize: '13px', color: '#33475b' }}>
                      Notes
                      <textarea value={createFormData.notes || ''} onChange={(e) => setCreateFormData((p) => ({ ...p, notes: e.target.value }))}
                        style={{ display: 'block', width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', marginTop: '4px', fontSize: '14px', minHeight: '60px', resize: 'vertical' }} />
                    </label>
                  </>}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
                  <button onClick={() => setShowCreateModal(null)} style={{ padding: '8px 16px', borderRadius: '4px', border: '1px solid #cbd6e2', background: '#fff', cursor: 'pointer', fontSize: '14px' }}>Cancel</button>
                  <button
                    onClick={handleCreateSubmit}
                    disabled={createSaving}
                    style={{ padding: '8px 16px', borderRadius: '4px', border: 'none', background: '#ff7a59', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: 600, opacity: createSaving ? 0.6 : 1 }}
                  >
                    {createSaving ? (showCreateModal === 'task' && (createFormData as any).id ? 'Saving...' : 'Creating...') : (showCreateModal === 'task' && (createFormData as any).id ? 'Save changes' : 'Create')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========== Global Compose Modal (inbox + Email quick-actions) ==========
            Lazy-loaded — TipTap (~80KB gz) only fetched when the user opens
            compose. `null` fallback keeps the modal invisible while it loads,
            which is the right UX since the user explicitly requested it. */}
        <Suspense fallback={null}>
          <ComposeModal
            open={composeOpen}
            onClose={() => { setComposeOpen(false); setComposeContext(undefined) }}
            userEmail={currentUser?.email || 'mritchie@botpros.ai'}
            userName={currentUser?.name || ''}
            context={composeContext}
            onSent={() => { /* success toast handled by UndoSendToast */ }}
            onToast={(msg) => setComposeToast(msg)}
          />
        </Suspense>
        {/* 8-second Undo Send toast — classic Gmail/Superhuman UX. Intercepts
            every send from ComposeModal, shows a countdown, reopens compose
            with content intact on Undo. */}
        <UndoSendToast
          onUndoRestore={(ctx) => { setComposeContext(ctx); setComposeOpen(true) }}
          onToast={(msg) => setComposeToast(msg)}
        />
        {composeToast && (
          <div className="inbox-toast-global" role="status">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
            {composeToast}
          </div>
        )}

        {/* ========== Cascade Ticket-Create Modal REMOVED — Create Ticket now opens the unified Smart Create panel with Ticket pre-selected via openSmartCreateForTicket(...). ========== */}

        {/* ========== Ticket Drawer (contact-only tickets) ========== */}
        {activeTicketDrawerId !== null && (
          <div
            onClick={closeTicketDrawer}
            style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{ background: '#fff', borderRadius: 8, width: 560, maxHeight: '90vh', overflow: 'auto', padding: 20, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#1e293b' }}>
                  {ticketDrawerData ? `Ticket #${ticketDrawerData.id}` : 'Loading…'}
                </h3>
                <button onClick={closeTicketDrawer} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: '1.2rem' }}>×</button>
              </div>

              {!ticketDrawerData ? (
                <div style={{ padding: '32px 0', textAlign: 'center', color: '#7c98b6' }}>Loading…</div>
              ) : (
                <>
                  {/* Linkage block */}
                  <div style={{ padding: '10px 12px', background: '#f8fafc', borderRadius: 6, marginBottom: 12, fontSize: '0.82rem', color: '#334155' }}>
                    <div style={{ marginBottom: 4 }}>
                      <strong>Contact:</strong>{' '}
                      <a href={`#/contacts/record/${encodeURIComponent(ticketDrawerData.unified_client_id)}`} style={{ color: '#2563eb' }}>
                        {ticketDrawerData.contact_name || ticketDrawerData.unified_client_id}
                      </a>
                    </div>
                    {ticketDrawerData.deal_id ? (
                      <div style={{ marginBottom: 4 }}>
                        <strong>Deal:</strong>{' '}
                        <a href={`#/deals/record/${ticketDrawerData.deal_id}`} style={{ color: '#2563eb' }}>💼 {ticketDrawerData.deal_title || ticketDrawerData.deal_id}</a>
                      </div>
                    ) : null}
                    {ticketDrawerData.project_id ? (
                      <div style={{ marginBottom: 4 }}>
                        <strong>Project:</strong>{' '}
                        <a href={`#/projects/record/${encodeURIComponent(ticketDrawerData.project_id)}`} style={{ color: '#2563eb' }}>🏗 {ticketDrawerData.project_name || ticketDrawerData.project_id}</a>
                      </div>
                    ) : null}
                    {!ticketDrawerData.deal_id && !ticketDrawerData.project_id && (
                      <div style={{ color: '#64748b', fontStyle: 'italic' }}>
                        Not linked to a deal or project ·{' '}
                        <a
                          href="#"
                          onClick={e => { e.preventDefault(); setTicketLinkPickerOpen(o => !o); setTicketLinkPickerQuery('') }}
                          style={{ color: '#2563eb', fontStyle: 'normal' }}
                        >{ticketLinkPickerOpen ? 'Cancel' : 'Link to a deal…'}</a>
                      </div>
                    )}
                    {ticketLinkPickerOpen && !ticketDrawerData.deal_id && (
                      <div style={{ marginTop: 10, background: '#fff', border: '1px solid #cbd6e2', borderRadius: 6, padding: 8, boxShadow: '0 2px 6px rgba(15, 23, 42, 0.06)' }}>
                        <input
                          autoFocus
                          type="text"
                          value={ticketLinkPickerQuery}
                          onChange={e => setTicketLinkPickerQuery(e.target.value)}
                          placeholder="Search deals by title or client name…"
                          style={{ width: '100%', padding: '6px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.82rem', boxSizing: 'border-box' }}
                        />
                        <div style={{ maxHeight: 220, overflowY: 'auto', marginTop: 6 }}>
                          {ticketLinkPickerLoading && <div style={{ padding: '8px 10px', color: '#7c98b6', fontSize: '0.78rem' }}>Loading…</div>}
                          {!ticketLinkPickerLoading && ticketLinkPickerResults.length === 0 && (
                            <div style={{ padding: '8px 10px', color: '#7c98b6', fontSize: '0.78rem', fontStyle: 'italic' }}>
                              {ticketLinkPickerQuery ? 'No matching deals' : 'No deals linked to this contact'}
                            </div>
                          )}
                          {!ticketLinkPickerLoading && ticketLinkPickerResults.map(d => (
                            <button
                              key={d.id}
                              type="button"
                              onClick={() => linkTicketToDeal(d.id)}
                              style={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', padding: '6px 10px', background: 'transparent', border: 'none', borderRadius: 4, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}
                              onMouseEnter={e => (e.currentTarget.style.background = '#f1f5f9')}
                              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                            >
                              <span style={{ fontSize: '0.82rem', color: '#0f172a', fontWeight: 500 }}>{d.title || `Deal #${d.id}`}</span>
                              <span style={{ fontSize: '0.72rem', color: '#64748b' }}>
                                {d.customer_name ? `${d.customer_name} · ` : ''}{d.stage_label || 'Deal'} · #{d.id}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Title */}
                  <div style={{ marginBottom: 10 }}>
                    <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: '#7c98b6', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>Title</label>
                    <input
                      value={ticketDrawerDraft?.title ?? ticketDrawerData.title ?? ''}
                      onChange={e => setTicketDrawerDraft(prev => prev ? { ...prev, title: e.target.value } : prev)}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.95rem', fontWeight: 500, boxSizing: 'border-box' }}
                    />
                  </div>

                  {/* Description */}
                  <div style={{ marginBottom: 10 }}>
                    <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: '#7c98b6', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>Description</label>
                    <textarea
                      value={ticketDrawerDraft?.description ?? ticketDrawerData.description ?? ''}
                      onChange={e => setTicketDrawerDraft(prev => prev ? { ...prev, description: e.target.value } : prev)}
                      rows={4}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }}
                    />
                  </div>

                  {/* Status + Priority + Due date + Category */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8, marginBottom: 14 }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: '#7c98b6', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>Status</label>
                      <select value={ticketDrawerDraft?.status ?? ticketDrawerData.status} onChange={e => setTicketDrawerDraft(prev => prev ? { ...prev, status: e.target.value } : prev)} style={{ width: '100%', padding: '6px 8px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.82rem', background: '#fff' }}>
                        <option value="open">Open</option>
                        <option value="in_progress">In Progress</option>
                        <option value="waiting">Waiting</option>
                        <option value="escalated">Escalated</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: '#7c98b6', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>Priority</label>
                      <select value={ticketDrawerDraft?.priority ?? ticketDrawerData.priority ?? 'medium'} onChange={e => setTicketDrawerDraft(prev => prev ? { ...prev, priority: e.target.value } : prev)} style={{ width: '100%', padding: '6px 8px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.82rem', background: '#fff' }}>
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: '#7c98b6', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>Due date</label>
                      <input
                        type="date"
                        value={ticketDrawerDraft?.due_date ? String(ticketDrawerDraft.due_date).slice(0, 10) : ticketDrawerData.due_date ? String(ticketDrawerData.due_date).slice(0, 10) : ''}
                        onChange={e => setTicketDrawerDraft(prev => prev ? { ...prev, due_date: e.target.value || null } : prev)}
                        style={{ width: '100%', padding: '6px 8px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.82rem', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: '#7c98b6', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>Category</label>
                      <input
                        value={ticketDrawerDraft?.category ?? ticketDrawerData.category ?? ''}
                        onChange={e => setTicketDrawerDraft(prev => prev ? { ...prev, category: e.target.value } : prev)}
                        style={{ width: '100%', padding: '6px 8px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.82rem', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>

                  {/* Timestamps */}
                  <div style={{ fontSize: '0.72rem', color: '#94a3b8', display: 'flex', gap: 12, paddingTop: 8, borderTop: '1px solid #f1f5f9' }}>
                    <span>Created {ticketDrawerData.created_at ? new Date(ticketDrawerData.created_at).toLocaleString() : '—'}</span>
                    <span>Updated {ticketDrawerData.updated_at ? new Date(ticketDrawerData.updated_at).toLocaleString() : '—'}</span>
                  </div>

                  {/* Save / Cancel */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14, paddingTop: 12, borderTop: '1px solid #e5e7eb' }}>
                    <button
                      type="button"
                      onClick={closeTicketDrawer}
                      style={{ padding: '7px 16px', fontSize: '0.82rem', fontWeight: 600, border: '1px solid #cbd6e2', borderRadius: 6, background: '#fff', color: '#475569', cursor: 'pointer' }}
                    >Cancel</button>
                    <button
                      type="button"
                      disabled={ticketDrawerSaving === 'bulk'}
                      onClick={async () => {
                        if (!ticketDrawerData || !ticketDrawerDraft) return
                        const changes: Record<string, any> = {}
                        for (const key of ['title', 'description', 'status', 'priority', 'due_date', 'category'] as const) {
                          const draftVal = ticketDrawerDraft[key] ?? null
                          const origVal = (ticketDrawerData as any)[key] ?? null
                          if (String(draftVal ?? '') !== String(origVal ?? '')) {
                            const apiKey = key === 'due_date' ? 'dueDate' : key
                            changes[apiKey] = typeof draftVal === 'string' ? (draftVal.trim() || null) : draftVal
                          }
                        }
                        if (Object.keys(changes).length === 0) { closeTicketDrawer(); return }
                        setTicketDrawerSaving('bulk')
                        try {
                          const updated = await updateTicket(ticketDrawerData.id, changes)
                          setTicketDrawerData(updated)
                          setTicketsRefreshTick(t => t + 1)
                          closeTicketDrawer()
                        } catch (e: any) {
                          alert('Save failed: ' + (e?.message || String(e)))
                        } finally {
                          setTicketDrawerSaving(null)
                        }
                      }}
                      style={{ padding: '7px 20px', fontSize: '0.82rem', fontWeight: 600, border: 'none', borderRadius: 6, background: '#0091ae', color: '#fff', cursor: ticketDrawerSaving === 'bulk' ? 'not-allowed' : 'pointer', opacity: ticketDrawerSaving === 'bulk' ? 0.7 : 1 }}
                    >{ticketDrawerSaving === 'bulk' ? 'Saving…' : 'Save'}</button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ========== Note Modal ========== */}
        {showNoteModal && (
          <div className="modal-overlay" onClick={() => { setShowNoteModal(null); setNoteText('') }}>
            <div className="modal-card" style={{ maxWidth: '440px' }} onClick={(e) => e.stopPropagation()}>
              <div className="modal-color-bar" style={{ background: '#7c98b6' }} />
              <button type="button" className="modal-close" onClick={() => { setShowNoteModal(null); setNoteText('') }} aria-label="Close">&times;</button>
              <div className="modal-body">
                <h3 className="modal-title" style={{ marginBottom: '12px' }}>Add Note</h3>
                <textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Write a note..."
                  autoFocus
                  style={{ width: '100%', minHeight: '100px', padding: '10px', border: '1px solid #cbd6e2', borderRadius: '4px', fontSize: '14px', resize: 'vertical' }}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '12px' }}>
                  <button onClick={() => { setShowNoteModal(null); setNoteText('') }} style={{ padding: '8px 16px', borderRadius: '4px', border: '1px solid #cbd6e2', background: '#fff', cursor: 'pointer', fontSize: '14px' }}>Cancel</button>
                  <button
                    onClick={handleNoteSubmit}
                    disabled={noteSaving || !noteText.trim()}
                    style={{ padding: '8px 16px', borderRadius: '4px', border: 'none', background: '#ff7a59', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: 600, opacity: noteSaving || !noteText.trim() ? 0.6 : 1 }}
                  >
                    {noteSaving ? 'Saving...' : 'Save Note'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Delete Modal */}
        {bulkDeleteModal && (
          <div className="modal-overlay" onClick={() => !bulkDeleteModal.deleting && setBulkDeleteModal(null)}>
            <div className="modal-card" style={{ maxWidth: 400 }} onClick={(e) => e.stopPropagation()}>
              <div className="modal-color-bar" style={{ background: '#dc2626' }} />
              <button type="button" className="modal-close" onClick={() => !bulkDeleteModal.deleting && setBulkDeleteModal(null)} aria-label="Close">&times;</button>
              <div className="modal-body" style={{ textAlign: 'center', padding: '32px 24px' }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 16px' }}><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>Delete {bulkDeleteModal.ids.size} contact{bulkDeleteModal.ids.size > 1 ? 's' : ''}?</h3>
                <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: 20 }}>This action cannot be undone. All associated data will be permanently removed.</p>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                  <button style={{ padding: '8px 20px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }} onClick={() => setBulkDeleteModal(null)} disabled={bulkDeleteModal.deleting}>Cancel</button>
                  <button style={{ padding: '8px 20px', borderRadius: 6, border: 'none', background: '#dc2626', color: '#fff', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', opacity: bulkDeleteModal.deleting ? 0.6 : 1 }} disabled={bulkDeleteModal.deleting} onClick={async () => {
                    setBulkDeleteModal({ ...bulkDeleteModal, deleting: true })
                    for (const id of bulkDeleteModal.ids) {
                      try { await deleteClient(id) } catch { /* skip */ }
                    }
                    setBulkDeleteModal(null)
                    setClientSelectedRows(new Set())
                    setPreviewClient(null)
                    fetchClients(clientPage, tablePageSize, { search: clientSearchDebounced || undefined, status: (clientStatusFilter && !clientStatusFilter.startsWith('custom:')) ? clientStatusFilter : undefined, type: 'person', sort: clientSort.field, order: clientSort.order }).then((res) => { setClientList(res.clients); setClientTotal(res.total); setClientPages(res.pages) }).catch(() => {})
                  }}>{bulkDeleteModal.deleting ? 'Deleting...' : 'Delete permanently'}</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* === PHONE: JSX — INCOMING TOAST (root) === page-level, fixed top-right at z 99999;
            renders only when the in-phone overlay can't. Answer/Decline delegate to the SHARED
            answerInboundCall/declineInboundCall — the old inline answer left callingState 'idle'
            and the desk-mirror poll hijacked the user's own call; keep the delegation. */}
        {/* Session-expired gate — NOT dismissible: every API call is dead
            until the user re-authenticates through Cloudflare Access. The
            redirect mirrors LoginPage's sign-in (path + hash preserved). */}
        {sessionExpired && (
          <div className="modal-overlay" style={{ zIndex: 10400 }}>
            <div className="modal-card" style={{ width: 400, padding: '28px 28px 24px', textAlign: 'center' }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 14px', display: 'block' }}><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              <h3 style={{ margin: '0 0 8px', fontSize: '1.05rem', fontWeight: 700, color: '#1e293b' }}>Your session expired</h3>
              <p style={{ margin: '0 0 18px', fontSize: '0.85rem', color: '#64748b', lineHeight: 1.55 }}>
                Sign back in to keep working — you'll come right back to this page.
                Signing in reloads the app, so if you have unsaved text in a form,
                copy it first.
              </p>
              <button
                type="button"
                style={{ width: '100%', padding: '10px 16px', borderRadius: 8, border: 'none', background: 'var(--accent, #1976d2)', color: '#fff', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                onClick={() => {
                  const dest = (window.location.pathname + window.location.hash) || '/'
                  window.location.href = `/auth/trigger?redirect_url=${encodeURIComponent(dest)}`
                }}
              >
                Sign back in
              </button>
            </div>
          </div>
        )}

        {/* Team chat — floating pill + panel (def ~4343). Fully self-contained:
            fetches /api/chat/* itself, swallows failures, inline-styled, fixed
            bottom-right at z 9000. Renders once globally per its own contract. */}
        <TeamChatWidget />

        {/* Crop/zoom for the Preferences > Profile photo — upload happens on Save */}
        {prefsCropFile && (
          <PhotoCropModal
            file={prefsCropFile}
            title="Adjust profile photo"
            round
            onCancel={() => setPrefsCropFile(null)}
            onSave={async (blob, name) => {
              await uploadPrefsPhoto(new File([blob], name, { type: blob.type }))
              setPrefsCropFile(null)
            }}
          />
        )}

        {/* Incoming call toast notification — shown only when the full-screen
            phone overlay is NOT visible (i.e. phone panel is closed or minimized).
            When the panel is open, the in-phone overlay handles accept/reject. */}
        {incomingCallToast && !(callingPanelOpen && !callingPanelMinimized && incomingCallInfo && inboundCallSession) && (
          <div style={{
            position: 'fixed', top: 16, right: 16, zIndex: 99999, width: 340,
            background: '#1c1c1e', borderRadius: 16, padding: '14px 16px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)', color: '#fff',
            display: 'flex', gap: 12, alignItems: 'center', cursor: 'pointer',
            animation: 'iosSheetSlideUp 0.3s cubic-bezier(0.32, 0.72, 0, 1)',
          }} onClick={() => { window.location.hash = incomingCallToast.linkUrl; setIncomingCallToast(null) }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="#fff" stroke="none"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.11 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92Z"/></svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>{incomingCallToast.title.replace('Incoming call: ', '')}</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>{incomingCallToast.body}</div>
            </div>
            {inboundCallSession ? (
              <>
                <button title="Decline" style={{ width: 36, height: 36, background: '#ef4444', border: 'none', borderRadius: '50%', color: '#fff', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={(e) => {
                  e.stopPropagation()
                  void declineInboundCall()
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff" style={{ transform: 'rotate(135deg)' }}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.11 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92Z"/></svg>
                </button>
                <button title="Answer" style={{ width: 36, height: 36, background: '#22c55e', border: 'none', borderRadius: '50%', color: '#fff', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={(e) => {
                  e.stopPropagation()
                  // Shared handler — the old inline copy answered the session
                  // but never set callingState('connected'), so the machine
                  // stayed 'idle' and the desk-mirror poll could hijack the
                  // screen mid-call ("Audio is on your desk phone").
                  void answerInboundCall()
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.11 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92Z"/></svg>
                </button>
                <button style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', padding: 4 }} onClick={(e) => { e.stopPropagation(); setIncomingCallToast(null) }}>&times;</button>
              </>
            ) : (
              <>
                <button style={{ background: '#22c55e', border: 'none', borderRadius: 8, padding: '6px 12px', color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }} onClick={(e) => { e.stopPropagation(); window.location.hash = incomingCallToast.linkUrl; setIncomingCallToast(null) }}>View</button>
                <button style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', padding: 4 }} onClick={(e) => { e.stopPropagation(); setIncomingCallToast(null) }}>&times;</button>
              </>
            )}
          </div>
        )}


        {/* Match-switch confirmation — guards against accidentally wiping
            a matched contact / typed info by clicking a new autocomplete result. */}
        {pendingMatchSwitch && (
          <div className="kanban-modal-overlay" style={{ zIndex: 10001 }} onClick={() => setPendingMatchSwitch(null)}>
            <div className="kanban-confirm-modal" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
              <div className="kanban-confirm-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              </div>
              <h3>Replace current contact?</h3>
              <p style={{ fontSize: 13, color: '#516f90', marginBottom: 16, lineHeight: 1.5 }}>
                You already have <strong>{pendingMatchSwitch.currentLabel}</strong> in the form.
                Selecting <strong>{pendingMatchSwitch.newName}</strong> will replace the current name, email, phone, and address.
              </p>
              <div className="kanban-confirm-actions">
                <button className="kanban-confirm-cancel" onClick={() => setPendingMatchSwitch(null)}>Cancel</button>
                <button
                  className="kanban-confirm-ok"
                  style={{ background: '#f59e0b' }}
                  onClick={() => { pendingMatchSwitch.apply(); setPendingMatchSwitch(null) }}
                >Replace</button>
              </div>
            </div>
          </div>
        )}

        {/* Projects Pipeline Stage column filter — multi-select of which
            stages are visible. Opened from the Pipeline Stage column kebab. */}
        {projectsStageFilterPopover && (() => {
          const activeSet = projectsStageFilterSet ?? new Set(ALL_DELIVERY_STAGES.map(s => s.id))
          const allChecked = activeSet.size === ALL_DELIVERY_STAGES.length
          const toggleStage = (id: string) => {
            const next = new Set(activeSet)
            if (next.has(id)) next.delete(id); else next.add(id)
            setProjectsStageFilterSet(next.size === ALL_DELIVERY_STAGES.length ? null : next)
            setProjectsPage(1)
          }
          const selectAll = () => { setProjectsStageFilterSet(null); setProjectsPage(1) }
          const clearAll = () => { setProjectsStageFilterSet(new Set()); setProjectsPage(1) }
          return (
            <div
              className="projects-stage-filter-popover"
              style={{
                position: 'fixed',
                top: projectsStageFilterPopover.y,
                left: projectsStageFilterPopover.x,
                zIndex: 10000,
                background: '#fff',
                border: '1px solid #dfe3eb',
                borderRadius: 8,
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                minWidth: 240,
                padding: '8px 0',
              }}
            >
              <div style={{ padding: '6px 14px 8px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Filter stages</span>
                <button
                  type="button"
                  onClick={allChecked ? clearAll : selectAll}
                  style={{ background: 'none', border: 'none', color: '#0091ae', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', padding: 0 }}
                >
                  {allChecked ? 'Clear all' : 'Select all'}
                </button>
              </div>
              <div style={{ padding: '4px 0', maxHeight: 320, overflowY: 'auto' }}>
                {ALL_DELIVERY_STAGES.map(s => {
                  const checked = activeSet.has(s.id)
                  const count = projectsStageCounts[s.id]
                  // For P_ON_HOLD, determine the parent checkbox state based on selected hold types
                  const isOnHoldStage = s.id === 'P_ON_HOLD'
                  const holdParentState = isOnHoldStage ? getParentHoldCheckState(projectsHoldTypesFilter) : null
                  const shouldShowHoldChildren = isOnHoldStage && checked

                  return (
                    <div key={s.id}>
                      <label
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 14px', cursor: 'pointer', fontSize: '0.85rem', color: '#33475b' }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = '#f8fafc')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                      >
                        <input
                          ref={el => {
                            if (el) (el as HTMLInputElement).indeterminate = isOnHoldStage && holdParentState === 'some'
                          }}
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            toggleStage(s.id)
                            // When unchecking P_ON_HOLD, clear the hold types filter
                            if (checked && isOnHoldStage) {
                              setProjectsHoldTypesFilter(new Set())
                            }
                          }}
                        />
                        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
                        <span style={{ flex: 1 }}>{s.label}</span>
                        {typeof count === 'number' && (
                          <span style={{ fontSize: '0.76rem', color: '#7c98b6', fontWeight: 500 }}>({count.toLocaleString()})</span>
                        )}
                      </label>

                      {/* Hold type sub-checkboxes shown when P_ON_HOLD is selected */}
                      {shouldShowHoldChildren && (
                        <div style={{ background: '#f9fafb', paddingLeft: 0 }}>
                          {[
                            { value: 'needs_paint_sub', label: 'Needs Paint' },
                            { value: 'customer_hold', label: 'Customer Hold' },
                            { value: 'other', label: 'Other' },
                          ].map(holdType => (
                            <label
                              key={holdType.value}
                              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 14px 4px 40px', cursor: 'pointer', fontSize: '0.8rem', color: '#64748b' }}
                              onMouseEnter={(e) => (e.currentTarget.style.background = '#f1f5f9')}
                              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                            >
                              <input
                                type="checkbox"
                                checked={projectsHoldTypesFilter.has(holdType.value)}
                                onChange={() => {
                                  const next = toggleHoldType(projectsHoldTypesFilter, holdType.value)
                                  setProjectsHoldTypesFilter(next)
                                  setProjectsPage(1)
                                }}
                              />
                              <span style={{ flex: 1 }}>{holdType.label}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
              <div style={{ padding: '8px 14px 4px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setProjectsStageFilterPopover(null)}
                  style={{ background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, padding: '5px 14px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}
                >
                  Done
                </button>
              </div>
            </div>
          )
        })()}

        {/* Deals Pipeline Stage column filter — multi-select of which
            stages are visible. Opened from the Stage column kebab. */}
        {dealsStageFilterPopover && allPipelineStages && (() => {
          const allChecked = dealStageFilter.size === 0
          const toggleStage = (id: string) => {
            const next = new Set(dealStageFilter)
            if (allChecked) {
              allPipelineStages.forEach(s => { if (s.stageId !== id) next.add(s.stageId) })
            } else if (next.has(id)) {
              next.delete(id)
            } else {
              next.add(id)
            }
            if (next.size === allPipelineStages.length) next.clear()
            setDealStageFilter(next)
            setDealPage(1)
          }
          const selectAll = () => { setDealStageFilter(new Set()); setDealPage(1) }
          const clearAll = () => { setDealStageFilter(new Set(['__none__'])); setDealPage(1) }
          return (
            <div
              className="deals-stage-filter-popover"
              style={{
                position: 'fixed',
                top: dealsStageFilterPopover.y,
                left: dealsStageFilterPopover.x,
                zIndex: 10000,
                background: '#fff',
                border: '1px solid #dfe3eb',
                borderRadius: 8,
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                minWidth: 240,
                padding: '8px 0',
              }}
            >
              <div style={{ padding: '6px 14px 8px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Filter stages</span>
                <button
                  type="button"
                  onClick={allChecked ? clearAll : selectAll}
                  style={{ background: 'none', border: 'none', color: '#0091ae', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', padding: 0 }}
                >
                  {allChecked ? 'Clear all' : 'Select all'}
                </button>
              </div>
              <div style={{ padding: '4px 0', maxHeight: 320, overflowY: 'auto' }}>
                {allPipelineStages.map(s => {
                  const checked = allChecked || dealStageFilter.has(s.stageId)
                  return (
                    <label
                      key={s.stageId}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 14px', cursor: 'pointer', fontSize: '0.85rem', color: '#33475b' }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = '#f8fafc')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      <input type="checkbox" checked={checked} onChange={() => toggleStage(s.stageId)} />
                      <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
                      <span style={{ flex: 1 }}>{s.label}</span>
                      {typeof s.count === 'number' && (
                        <span style={{ fontSize: '0.76rem', color: '#7c98b6', fontWeight: 500 }}>({s.count.toLocaleString()})</span>
                      )}
                    </label>
                  )
                })}
              </div>
              <div style={{ padding: '8px 14px 4px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setDealsStageFilterPopover(null)}
                  style={{ background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, padding: '5px 14px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}
                >
                  Done
                </button>
              </div>
            </div>
          )
        })()}

        {/* Project Record → Work Order create/edit modal */}
        {prjWoModalOpen && projectDetailData && (() => {
          const isEdit = !!prjWoEditing
          const status: string = prjWoEditing?.status || 'issued'
          const statusSteps = ['issued', 'acknowledged', 'complete'] as const
          const statusColors: Record<string, { bg: string; fg: string; label: string }> = {
            issued: { bg: '#e0f2fe', fg: '#075985', label: 'Issued' },
            acknowledged: { bg: '#fef3c7', fg: '#92400e', label: 'Acknowledged' },
            complete: { bg: '#dcfce7', fg: '#166534', label: 'Complete' },
          }
          return (
            <>
              <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(15, 23, 42, 0.32)' }} onClick={() => !prjWoSaving && setPrjWoModalOpen(false)} />
              <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10000, background: '#fff', border: '1px solid #dfe3eb', borderRadius: 8, boxShadow: '0 16px 48px rgba(0,0,0,0.2)', width: 560, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
                <div style={{ padding: '14px 18px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                  <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#33475b' }}>{isEdit ? 'Work Order' : 'New Work Order'}</h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {isEdit && (
                      <button
                        type="button"
                        title={prjWoEditing?.is_featured ? 'Unfeature this work order' : 'Feature this work order — its first image attachment becomes the project cover photo'}
                        onClick={async () => {
                          try {
                            const next = !prjWoEditing?.is_featured
                            const updated = await setWorkOrderFeatured(projectDetailData.id, prjWoEditing.id, next)
                            setPrjWoEditing(updated)
                            await reloadPrjWorkOrders(projectDetailData.id)
                          } catch (err: any) { alert(err?.message || 'Failed to update featured') }
                        }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 5,
                          background: prjWoEditing?.is_featured ? '#fef3c7' : '#fff',
                          color: prjWoEditing?.is_featured ? '#92400e' : '#64748b',
                          border: '1px solid ' + (prjWoEditing?.is_featured ? '#f59e0b' : '#cbd5e1'),
                          borderRadius: 6, padding: '4px 10px', fontSize: '0.76rem', fontWeight: 600, cursor: 'pointer',
                        }}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill={prjWoEditing?.is_featured ? '#f59e0b' : 'none'} stroke={prjWoEditing?.is_featured ? '#f59e0b' : 'currentColor'} strokeWidth="1.8"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                        {prjWoEditing?.is_featured ? 'Featured' : 'Feature'}
                      </button>
                    )}
                    <button onClick={() => !prjWoSaving && setPrjWoModalOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.3rem', color: '#94a3b8', padding: 0 }}>&times;</button>
                  </div>
                </div>
                <div style={{ padding: '14px 18px', overflowY: 'auto', flex: 1 }}>
                  {isEdit && (
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Status</div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {statusSteps.map(s => {
                          const sc = statusColors[s]
                          const active = status === s
                          return (
                            <button
                              key={s}
                              onClick={async () => {
                                if (status === s) return
                                try {
                                  const updated = await updateProjectWorkOrder(projectDetailData.id, prjWoEditing.id, { status: s })
                                  setPrjWoEditing(updated)
                                  await reloadPrjWorkOrders(projectDetailData.id)
                                } catch (err: any) { alert(err?.message || 'Failed to update status') }
                              }}
                              style={{ flex: 1, padding: '6px 10px', fontSize: '0.74rem', fontWeight: 600, border: '1px solid ' + (active ? sc.fg : '#e2e8f0'), background: active ? sc.bg : '#fff', color: active ? sc.fg : '#64748b', borderRadius: 6, cursor: 'pointer' }}
                            >{sc.label}</button>
                          )
                        })}
                      </div>
                    </div>
                  )}
                  <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Title *</label>
                  <input
                    autoFocus
                    value={prjWoForm.title}
                    onChange={e => setPrjWoForm(f => ({ ...f, title: e.target.value }))}
                    placeholder="e.g. Exterior paint — north elevation"
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem', outline: 'none', marginBottom: 14 }}
                  />
                  <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Description / Scope</label>
                  <textarea
                    value={prjWoForm.description}
                    onChange={e => setPrjWoForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="Scope of work, materials, specs, acceptance criteria…"
                    rows={5}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.85rem', outline: 'none', resize: 'vertical', fontFamily: 'inherit' }}
                  />
                  {isEdit && (
                    <div style={{ marginTop: 18 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                        <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Attachments {prjWoFiles.length > 0 ? `(${prjWoFiles.length})` : ''}</div>
                        <button
                          onClick={() => prjWoFileInputRef.current?.click()}
                          style={{ background: 'none', border: 'none', color: '#0091ae', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}
                        >+ Upload</button>
                        <input
                          ref={prjWoFileInputRef}
                          type="file"
                          multiple
                          style={{ display: 'none' }}
                          onChange={async (e) => {
                            const files = Array.from(e.target.files || [])
                            if (files.length === 0) return
                            try {
                              for (const f of files) { await uploadWorkOrderFile(projectDetailData.id, prjWoEditing.id, f) }
                              const { files: refreshed } = await getProjectWorkOrder(projectDetailData.id, prjWoEditing.id)
                              setPrjWoFiles(refreshed || [])
                              await reloadPrjWorkOrders(projectDetailData.id)
                            } catch (err: any) { alert(err?.message || 'Upload failed') }
                            finally { if (prjWoFileInputRef.current) prjWoFileInputRef.current.value = '' }
                          }}
                        />
                      </div>
                      {prjWoFiles.length === 0 ? (
                        <div style={{ padding: '10px 12px', border: '1px dashed #cbd6e2', borderRadius: 6, fontSize: '0.76rem', color: '#94a3b8', textAlign: 'center' }}>No files attached</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {prjWoFiles.map((f: any) => renderPrjFileRow(f, async () => {
                            const { files: refreshed } = await getProjectWorkOrder(projectDetailData.id, prjWoEditing.id)
                            setPrjWoFiles(refreshed || [])
                            await reloadPrjWorkOrders(projectDetailData.id)
                          }))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ padding: '12px 18px', borderTop: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                  {isEdit ? (
                    <button
                      onClick={async () => {
                        if (!confirm('Delete this work order? Attached files will be removed.')) return
                        setPrjWoSaving(true)
                        try {
                          await deleteProjectWorkOrder(projectDetailData.id, prjWoEditing.id)
                          await reloadPrjWorkOrders(projectDetailData.id)
                          setPrjWoModalOpen(false)
                        } catch (err: any) { alert(err?.message || 'Failed to delete') }
                        finally { setPrjWoSaving(false) }
                      }}
                      disabled={prjWoSaving}
                      style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}
                    >Delete</button>
                  ) : <span />}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => !prjWoSaving && setPrjWoModalOpen(false)}
                      disabled={prjWoSaving}
                      style={{ background: '#fff', border: '1px solid #cbd6e2', color: '#33475b', borderRadius: 4, padding: '7px 16px', fontSize: '0.85rem', fontWeight: 500, cursor: 'pointer' }}
                    >Cancel</button>
                    <button
                      onClick={async () => {
                        const title = prjWoForm.title.trim()
                        if (!title) { alert('Title is required'); return }
                        setPrjWoSaving(true)
                        try {
                          if (isEdit) {
                            const updated = await updateProjectWorkOrder(projectDetailData.id, prjWoEditing.id, { title, description: prjWoForm.description })
                            setPrjWoEditing(updated)
                          } else {
                            await createProjectWorkOrder(projectDetailData.id, { title, description: prjWoForm.description })
                          }
                          await reloadPrjWorkOrders(projectDetailData.id)
                          setPrjWoModalOpen(false)
                        } catch (err: any) { alert(err?.message || 'Save failed') }
                        finally { setPrjWoSaving(false) }
                      }}
                      disabled={prjWoSaving || !prjWoForm.title.trim()}
                      style={{ background: prjWoForm.title.trim() && !prjWoSaving ? '#0891b2' : '#94a3b8', color: '#fff', border: 'none', borderRadius: 4, padding: '7px 18px', fontSize: '0.85rem', fontWeight: 600, cursor: prjWoForm.title.trim() && !prjWoSaving ? 'pointer' : 'not-allowed' }}
                    >{prjWoSaving ? 'Saving…' : isEdit ? 'Save' : 'Create'}</button>
                  </div>
                </div>
              </div>
            </>
          )
        })()}

        {/* Project file hover preview tooltip */}
        {prjFileHover && (() => {
          const f = prjFileHover.file
          const isImg = (f.content_type || '').startsWith('image/')
          const tooltipW = isImg ? 240 : 200
          const vw = typeof window !== 'undefined' ? window.innerWidth : 1024
          const vh = typeof window !== 'undefined' ? window.innerHeight : 768
          const left = Math.min(prjFileHover.x + 14, vw - tooltipW - 12)
          const top = Math.min(prjFileHover.y + 14, vh - 180)
          return (
            <div style={{ position: 'fixed', top, left, zIndex: 10001, width: tooltipW, background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, boxShadow: '0 8px 24px rgba(15,23,42,0.18)', padding: 8, pointerEvents: 'none' }}>
              {isImg ? (
                <img src={f.file_url} alt={f.file_name} style={{ width: '100%', height: 'auto', maxHeight: 160, objectFit: 'contain', borderRadius: 4, background: '#f1f5f9', display: 'block' }} />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 4px' }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="1.8"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  <div style={{ fontSize: '0.72rem', color: '#64748b' }}>{f.content_type || 'file'}</div>
                </div>
              )}
              <div style={{ marginTop: 6, fontSize: '0.72rem', fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.file_name}</div>
            </div>
          )
        })()}

        {/* Project file preview modal */}
        {prjFilePreview && (() => {
          const f = prjFilePreview
          const isImg = (f.content_type || '').startsWith('image/')
          const isPdf = (f.content_type || '') === 'application/pdf'
          const isDocx = /\.docx?$/i.test(f.file_name || '')
          const canRename = !!projectDetailData
          return (
            <>
              <div style={{ position: 'fixed', inset: 0, zIndex: 10005, background: 'rgba(15, 23, 42, 0.75)' }} onClick={() => setPrjFilePreview(null)} />
              <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10006, background: '#fff', borderRadius: 8, boxShadow: '0 24px 64px rgba(0,0,0,0.4)', width: (isPdf || isDocx) ? '85vw' : 'auto', maxWidth: '92vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
                <div style={{ padding: '10px 14px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  {canRename && prjFileRenaming === f.id ? (
                    <input
                      autoFocus
                      value={prjFileRenameValue}
                      onChange={e => setPrjFileRenameValue(e.target.value)}
                      onKeyDown={async e => {
                        if (e.key === 'Enter') {
                          const nextName = prjFileRenameValue.trim()
                          if (!nextName || nextName === f.file_name) { setPrjFileRenaming(null); return }
                          try {
                            await renameProjectFile(projectDetailData.id, f.id, nextName)
                            setPrjFilePreview({ ...f, file_name: nextName })
                            await reloadPrjFiles(projectDetailData.id)
                            await reloadPrjWorkOrders(projectDetailData.id)
                            if (prjWoEditing) {
                              try { const { files: refreshed } = await getProjectWorkOrder(projectDetailData.id, prjWoEditing.id); setPrjWoFiles(refreshed || []) } catch {}
                            }
                          } catch (err: any) { alert(err?.message || 'Rename failed') }
                          finally { setPrjFileRenaming(null) }
                        } else if (e.key === 'Escape') { setPrjFileRenaming(null) }
                      }}
                      onBlur={() => setPrjFileRenaming(null)}
                      style={{ flex: 1, fontSize: '0.9rem', fontWeight: 600, color: '#0f172a', padding: '4px 8px', border: '1px solid #0891b2', borderRadius: 4, outline: 'none' }}
                    />
                  ) : canRename ? (
                    <div
                      onClick={() => { setPrjFileRenaming(f.id); setPrjFileRenameValue(f.file_name) }}
                      title="Click to rename"
                      style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, cursor: 'text', overflow: 'hidden' }}
                    >
                      <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.file_name}</span>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    </div>
                  ) : (
                    <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }} title={f.file_name}>{f.file_name}</span>
                  )}
                  <a
                    href={f.file_url}
                    download={f.file_name}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: '0.78rem', fontWeight: 600, color: '#0891b2', textDecoration: 'none', padding: '4px 10px', border: '1px solid #0891b2', borderRadius: 4 }}
                  >Download</a>
                  <button onClick={() => setPrjFilePreview(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.4rem', color: '#94a3b8', padding: 0, lineHeight: 1 }}>&times;</button>
                </div>
                <div style={{ flex: 1, overflow: 'auto', background: '#f1f5f9', display: 'flex', alignItems: (isPdf || isDocx) ? 'flex-start' : 'center', justifyContent: 'center', minHeight: 200 }}>
                  {isImg ? (
                    <img src={f.file_url} alt={f.file_name} style={{ maxWidth: '88vw', maxHeight: '80vh', objectFit: 'contain', display: 'block' }} />
                  ) : (isPdf || isDocx) ? (
                    // Native PDFs AND .doc/.docx both render through the SAME
                    // pdf.js canvas path (the effect above fetches the resolved
                    // PDF URL — .docx via the server-side → PDF conversion route —
                    // and paints each page to canvas). This actually issues the
                    // network request, unlike an inert <object data>. The Word
                    // download card shows ONLY on a genuine load/convert failure.
                    prjPreviewLoadError ? (
                      <div style={{ padding: 48, textAlign: 'center' }}>
                        <div style={{ width: 72, height: 72, margin: '0 auto 20px', borderRadius: 14, background: '#2b579a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ color: '#fff', fontSize: '2.2rem', fontWeight: 700 }}>{isDocx ? 'W' : 'PDF'}</span>
                        </div>
                        <div style={{ fontSize: '1rem', fontWeight: 600, color: '#334155', marginBottom: 6 }}>{f.file_name}</div>
                        <div style={{ fontSize: '0.82rem', color: '#94a3b8', marginBottom: 14 }}>Preview not available — download to view.</div>
                        <a href={f.file_url} download={f.file_name} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.82rem', fontWeight: 600, color: '#fff', background: '#0891b2', textDecoration: 'none', padding: '7px 16px', borderRadius: 6 }}>Download</a>
                      </div>
                    ) : (
                      // Container stays mounted (the render effect needs its ref);
                      // while the cold docx→PDF conversion / fetch is in flight we
                      // overlay a "Generating preview…" spinner so the wait never
                      // looks blank or broken.
                      <div style={{ width: '100%', position: 'relative', minHeight: 200, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        {prjPreviewLoading && (
                          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, color: '#475569', zIndex: 1 }}>
                            <div style={{ width: 32, height: 32, border: '3px solid #cbd5e1', borderTopColor: '#0891b2', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                            <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>Generating preview…</div>
                          </div>
                        )}
                        <div ref={pdfPreviewContainerRef} style={{ width: '100%', padding: '14px 0', display: 'flex', flexDirection: 'column', alignItems: 'center' }} />
                      </div>
                    )
                  ) : (
                    <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" style={{ margin: '0 auto 12px', display: 'block' }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                      <div style={{ fontSize: '0.85rem', marginBottom: 4 }}>Preview not available for this file type.</div>
                      <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{f.content_type || 'unknown'}</div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )
        })()}

        {/* Project → Contact picker modal — opened from the Project Record
            Contact card "+ Link" / "Change" buttons. */}
        {prjContactPickerOpen && projectDetailData && (
          <>
            <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(15, 23, 42, 0.32)' }} onClick={() => setPrjContactPickerOpen(false)} />
            <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10000, background: '#fff', border: '1px solid #dfe3eb', borderRadius: 8, boxShadow: '0 16px 48px rgba(0,0,0,0.2)', width: 420, maxHeight: '70vh', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 600, color: '#33475b' }}>Link a contact</h3>
                <button onClick={() => setPrjContactPickerOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: '#94a3b8', padding: 0 }}>&times;</button>
              </div>
              <div style={{ padding: '10px 16px', borderBottom: '1px solid #f1f5f9' }}>
                <input
                  autoFocus
                  placeholder="Search by name, email, phone, or company…"
                  value={prjContactSearch}
                  onChange={e => setPrjContactSearch(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.85rem', outline: 'none' }}
                />
              </div>
              <div style={{ overflowY: 'auto', flex: 1 }}>
                {prjContactSearch.trim() === '' ? (
                  <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: '0.82rem' }}>Start typing to search contacts</div>
                ) : prjContactSearching ? (
                  <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: '0.82rem' }}>Searching…</div>
                ) : prjContactResults.length === 0 ? (
                  <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: '0.82rem' }}>No contacts found</div>
                ) : (
                  prjContactResults.map((c: any) => (
                    <div
                      key={c.unified_client_id || c.id}
                      onClick={async () => {
                        const cid = c.unified_client_id || c.id
                        const cname = c.preferred_name || c.name || c.full_name || c.legal_name || ''
                        try {
                          await updateCrmProject(String(projectDetailData.id), { client_id: cid, client_name: cname })
                          const fresh: any = await fetchCrmProject(String(projectDetailData.id))
                          setProjectDetailData(fresh)
                          setPrjContactPickerOpen(false)
                        } catch (err: any) {
                          alert(err?.message || 'Failed to link contact')
                        }
                      }}
                      style={{ padding: '10px 16px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', transition: 'background 0.1s' }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#33475b' }}>{c.preferred_name || c.name || c.full_name || c.legal_name || 'Unnamed contact'}</div>
                      <div style={{ fontSize: '0.72rem', color: '#7c98b6', marginTop: 2 }}>
                        {[c.preferred_email, c.preferred_phone, c.legal_name].filter(Boolean).join(' • ') || '—'}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </>
        )}

        {/* Project stage picker — opened from any project stage badge (Projects table,
            Contact Record sidebar, Deal Record sidebar). Rendered at top level so it
            appears regardless of the current view. */}
        {projectStagePicker && (
          <>
            <div style={{ position: 'fixed', inset: 0, zIndex: 9999 }} onClick={() => !projectStageSaving && setProjectStagePicker(null)} />
            <div style={{ position: 'fixed', top: projectStagePickerPos.top, left: projectStagePickerPos.left, zIndex: 10000, background: '#fff', border: '1px solid #dfe3eb', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', minWidth: 210, paddingBlock: 6 }}>
              <div style={{ padding: '6px 14px 8px', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Move to</span>
              </div>
              {ALL_DELIVERY_STAGES.map((s) => {
                const isTerminal = s.id === 'P_ON_HOLD' || s.id === 'P_CANCELLED'
                const isFirstTerminal = s.id === 'P_ON_HOLD'
                const isCurrent = s.id === projectStagePicker.currentStage
                return (
                  <React.Fragment key={s.id}>
                    {isFirstTerminal && (
                      <div style={{ borderTop: '2px solid #e2e8f0', margin: '6px 10px 4px' }} />
                    )}
                    <div
                      onClick={async () => {
                        if (projectStageSaving || isCurrent) return
                        const projectId = projectStagePicker.projectId
                        const targetStage = s.id
                        if (targetStage === 'P_COMPLETED') {
                          const proj: any = projectsData.find((p: any) => p.id === projectId) || projectDetailData
                          if (proj?.owner_id && proj?.owner_approval_status !== 'approved') {
                            alert('Cannot mark complete — owner has not approved this project yet.')
                            return
                          }
                        }
                        // Intercept P_ON_HOLD to show the hold-type modal instead
                        if (targetStage === 'P_ON_HOLD') {
                          setProjectHoldModal({ projectId })
                          setProjectHoldType('')
                          setProjectHoldReason('')
                          setProjectStagePicker(null)
                          return
                        }
                        const statusFromStage = targetStage === 'P_COMPLETED' ? 'completed' : targetStage === 'P_CANCELLED' ? 'cancelled' : targetStage === 'P_ON_HOLD' ? 'on_hold' : 'active'
                        setProjectStageSaving(true)
                        setProjectsData(prev => prev.map(p => p.id === projectId ? ({ ...p, pipeline_stage: targetStage, status: statusFromStage } as any) : p))
                        setContactDetailProjects(prev => prev.map((p: any) => p.id === projectId ? ({ ...p, pipeline_stage: targetStage, status: statusFromStage }) : p))
                        setDealLinkedProjects((prev: any[]) => prev.map((p: any) => p.id === projectId ? ({ ...p, pipeline_stage: targetStage, status: statusFromStage }) : p))
                        setProjectDetailData((prev: any) => prev && prev.id === projectId ? ({ ...prev, pipeline_stage: targetStage, status: statusFromStage }) : prev)
                        try {
                          await updateProjectStage(projectId, targetStage)
                          setProjectStagePicker(null)
                        } catch (err) {
                          console.error('project stage update failed:', err)
                          loadProjects()
                          alert('Could not update project stage. Please try again.')
                        } finally {
                          setProjectStageSaving(false)
                        }
                      }}
                      style={{
                        padding: '8px 14px', fontSize: '0.8rem',
                        cursor: projectStageSaving ? 'wait' : (isCurrent ? 'default' : 'pointer'),
                        display: 'flex', alignItems: 'center', gap: 10,
                        background: isCurrent ? '#f1f5f9' : 'transparent',
                        fontWeight: isCurrent ? 600 : (isTerminal ? 600 : 400),
                        color: s.id === 'P_CANCELLED' ? '#d32f2f' : '#33475b',
                        opacity: projectStageSaving && !isCurrent ? 0.6 : 1,
                      }}
                      onMouseEnter={(e) => { if (!isCurrent && !projectStageSaving) e.currentTarget.style.background = s.id === 'P_CANCELLED' ? '#fee2e2' : '#f1f5f9' }}
                      onMouseLeave={(e) => (e.currentTarget.style.background = isCurrent ? '#f1f5f9' : 'transparent')}
                    >
                      <span style={{ width: 10, height: 10, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
                      {s.label}
                      {isCurrent && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2.5" style={{ marginLeft: 'auto' }}><polyline points="20 6 9 17 4 12"/></svg>}
                    </div>
                  </React.Fragment>
                )
              })}
            </div>
          </>
        )}

        {/* Project hold type modal — fired when transitioning a project to P_ON_HOLD stage.
            Asks for hold type (Needs Paint Sub, Customer Hold, Other) and optional/required reason. */}
        {projectHoldModal && (
          <div className="kanban-modal-overlay" onClick={() => !projectHoldSaving && setProjectHoldModal(null)}>
            <div className="kanban-confirm-modal" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
              <div className="kanban-confirm-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#9e9e9e" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
              </div>
              <h3>Place Project On Hold</h3>
              <p style={{ fontSize: 13, color: '#516f90', marginBottom: 16 }}>What type of hold?</p>
              <div style={{ textAlign: 'left', marginBottom: 14 }}>
                {HOLD_TYPES.map((ht) => (
                  <label key={ht.value} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 13, cursor: 'pointer' }}>
                    <input type="radio" name="projectHoldType" value={ht.value} checked={projectHoldType === ht.value} onChange={(e) => setProjectHoldType(e.target.value)} />
                    {ht.label}
                  </label>
                ))}
              </div>
              {projectHoldType && (
                <div style={{ textAlign: 'left', marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#33475b' }}>
                    Reason
                    {projectHoldType === 'other' && <span style={{ fontWeight: 400, color: '#dc2626' }}> (required)</span>}
                    {projectHoldType !== 'other' && <span style={{ fontWeight: 400, color: '#7c98b6' }}> (optional)</span>}
                  </label>
                  <textarea
                    value={projectHoldReason}
                    onChange={(e) => setProjectHoldReason(e.target.value)}
                    placeholder="Enter reason for hold..."
                    style={{
                      width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 4,
                      fontSize: '0.88rem', fontFamily: 'inherit', resize: 'vertical', minHeight: 80,
                    }}
                  />
                </div>
              )}
              <div className="kanban-confirm-actions">
                <button className="kanban-confirm-cancel" disabled={projectHoldSaving} onClick={() => setProjectHoldModal(null)}>Cancel</button>
                <button
                  className="kanban-confirm-ok"
                  style={{ background: projectHoldSaving || !projectHoldType || (projectHoldType === 'other' && !projectHoldReason.trim()) ? '#9e9e9e' : '#2563eb' }}
                  disabled={projectHoldSaving || !projectHoldType || (projectHoldType === 'other' && !projectHoldReason.trim())}
                  onClick={async () => {
                    if (!projectHoldModal?.projectId || !projectHoldType) return
                    setProjectHoldSaving(true)
                    try {
                      const projectId = projectHoldModal.projectId
                      const statusFromStage = 'on_hold'
                      setProjectsData(prev => prev.map(p => p.id === projectId ? ({ ...p, pipeline_stage: 'P_ON_HOLD', status: statusFromStage, hold_type: projectHoldType, hold_reason: projectHoldReason } as any) : p))
                      setContactDetailProjects(prev => prev.map((p: any) => p.id === projectId ? ({ ...p, pipeline_stage: 'P_ON_HOLD', status: statusFromStage, hold_type: projectHoldType, hold_reason: projectHoldReason }) : p))
                      setDealLinkedProjects((prev: any[]) => prev.map((p: any) => p.id === projectId ? ({ ...p, pipeline_stage: 'P_ON_HOLD', status: statusFromStage, hold_type: projectHoldType, hold_reason: projectHoldReason }) : p))
                      setProjectDetailData((prev: any) => prev && prev.id === projectId ? ({ ...prev, pipeline_stage: 'P_ON_HOLD', status: statusFromStage, hold_type: projectHoldType, hold_reason: projectHoldReason }) : prev)
                      await updateProjectStageWithHold(projectId, 'P_ON_HOLD', projectHoldType, projectHoldReason)
                      setProjectHoldModal(null)
                    } catch (err) {
                      console.error('project hold update failed:', err)
                      loadProjects()
                      alert('Could not place project on hold. Please try again.')
                    } finally {
                      setProjectHoldSaving(false)
                    }
                  }}
                >
                  Place On Hold
                </button>
              </div>
            </div>
          </div>
        )}

        {/* No-Show / Reschedule confirmation modal — fired from the deal
            record page's About-card stage dropdown. */}
        {dealDetailNoShowModal && (
          <div className="kanban-modal-overlay" onClick={() => !dealDetailNoShowSaving && setDealDetailNoShowModal(null)}>
            <div className="kanban-confirm-modal" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
              <div className="kanban-confirm-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#9c27b0" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              </div>
              <h3>No Show / Reschedule</h3>
              <p style={{ fontSize: 13, color: '#516f90', marginBottom: 12 }}><strong>{dealDetailNoShowModal.dealTitle}</strong></p>
              <div style={{ textAlign: 'left', marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#33475b' }}>What happened?</label>
                {['Customer no-show', "Estimator couldn't make it", 'Customer rescheduled'].map((opt) => (
                  <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 13, cursor: 'pointer' }}>
                    <input type="radio" name="dealDetailNoShowReason" value={opt} checked={dealDetailNoShowReason === opt} onChange={(e) => setDealDetailNoShowReason(e.target.value)} />
                    {opt}
                  </label>
                ))}
              </div>
              <div style={{ textAlign: 'left', marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, color: '#33475b' }}>New appointment <span style={{ fontWeight: 400, color: '#7c98b6' }}>(optional)</span></label>
                <AvailabilityDateTimePicker
                  value={dealDetailNoShowNewAppt}
                  onChange={setDealDetailNoShowNewAppt}
                  estimatorId={dealDetailNoShowEstimatorId}
                  onEstimatorChange={setDealDetailNoShowEstimatorId}
                />
                {dealDetailNoShowNewAppt && (
                  <button
                    type="button"
                    onClick={() => setDealDetailNoShowNewAppt('')}
                    style={{ marginTop: 6, background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 11, textDecoration: 'underline' }}
                  >Clear</button>
                )}
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#33475b', marginBottom: 16, textAlign: 'left' }}>
                <input type="checkbox" checked={dealDetailNoShowSendEmail} onChange={(e) => setDealDetailNoShowSendEmail(e.target.checked)} />
                {dealDetailNoShowNewAppt.trim() ? 'Send reschedule email to client' : 'Send cancellation email to client'}
              </label>
              <div className="kanban-confirm-actions">
                <button className="kanban-confirm-cancel" disabled={dealDetailNoShowSaving} onClick={() => setDealDetailNoShowModal(null)}>Cancel</button>
                <button
                  className="kanban-confirm-ok"
                  style={{ background: '#9c27b0' }}
                  disabled={dealDetailNoShowSaving}
                  onClick={async () => {
                    const dealId = dealDetailNoShowModal.dealId
                    const appt = dealDetailNoShowNewAppt.trim()
                    const reason = dealDetailNoShowReason
                    setDealDetailNoShowSaving(true)
                    try {
                      await updateDealStage(dealId, 'NO_SHOW_RESCHEDULE', {
                        noShowReason: reason,
                        newAppointment: appt || undefined,
                        newOwnerUserId: dealDetailNoShowEstimatorId ?? undefined,
                        sendConfirmationEmail: dealDetailNoShowSendEmail,
                      })
                      // Refresh the deal + history + live deals list so the UI reflects the change
                      // (note: the backend will auto-promote back to APPOINTMENT_SET if newAppointment is provided)
                      try {
                        const freshDeal = await fetchDeal(dealId)
                        setSelectedDeal(freshDeal)
                        setLiveDeals(prev => prev ? prev.map(d => d.id === freshDeal.id ? freshDeal : d) : prev)
                      } catch { /* non-fatal */ }
                      try {
                        const rows = await fetchDealHistory(dealId)
                        setDealHistory(Array.isArray(rows) ? rows : [])
                      } catch { /* non-fatal */ }
                      setPipelineRefreshTick(t => t + 1)
                      setCalendarRefreshTick(t => t + 1)
                      setDealDetailNoShowModal(null)
                    } catch (e: any) {
                      alert('Failed to update stage: ' + (e?.message || String(e)))
                    } finally {
                      setDealDetailNoShowSaving(false)
                    }
                  }}
                >{dealDetailNoShowSaving ? 'Saving…' : 'Confirm'}</button>
              </div>
            </div>
          </div>
        )}

        {/* Project chat modal — top-level so it's reachable from any view
            (sub-admin project detail, main Projects table, or record page).
            State is component-root: subChatModalOpen + subPortalProjectId +
            subProjectChat. */}
        {subChatModalOpen && (
          <div className="sub-admin-overlay" onClick={() => setSubChatModalOpen(false)}>
            <div className="sub-admin-dialog" onClick={e => e.stopPropagation()} style={{maxWidth:480,maxHeight:'80vh',display:'flex',flexDirection:'column'}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
                <h3 style={{display:'flex',alignItems:'center',gap:6,margin:0}}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"/></svg>Project Chat</h3>
                <button onClick={() => { if (subPortalProjectId) fetchSubAdminProjectChat(subPortalProjectId).then((r:any) => { if (r?.success) setSubProjectChat(r.data.messages) }) }} style={{padding:'4px 8px',borderRadius:4,border:'1px solid #e5e7eb',background:'#ffffff',cursor:'pointer',fontSize:'0.72rem',color:'#6b7280'}}>Refresh</button>
              </div>
              <div style={{flex:1,overflowY:'auto',display:'flex',flexDirection:'column',gap:6,padding:'8px 0',minHeight:200,maxHeight:400}}>
                {subProjectChat.length === 0 ? <div style={{textAlign:'center',padding:30,color:'#9ca3af',fontSize:'0.85rem'}}>No messages yet</div> : subProjectChat.map((m:any) => {
                  const roleColor = m.author_role === 'admin' ? '#7c3aed' : m.author_role === 'owner' ? '#1d4ed8' : m.author_role === 'senior' ? '#b45309' : '#6b7280'
                  const roleBg = m.author_role === 'admin' ? '#ede9fe' : m.author_role === 'owner' ? '#dbeafe' : m.author_role === 'senior' ? '#fef3c7' : '#f3f4f6'
                  const roleLabel = m.author_role === 'admin' ? 'Admin' : m.author_role === 'owner' ? 'Owner' : m.author_role === 'senior' ? 'Supervisor' : 'Staff'
                  return (
                    <div key={m.id} style={{padding:'8px 10px',borderRadius:8,background:'#f9fafb',border:'1px solid #f3f4f6'}}>
                      <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:3}}>
                        <span style={{fontWeight:600,fontSize:'0.82rem'}}>{m.author_name}</span>
                        <span style={{padding:'1px 6px',borderRadius:8,fontSize:'0.62rem',fontWeight:600,background:roleBg,color:roleColor}}>{roleLabel}</span>
                        <span style={{fontSize:'0.68rem',color:'#9ca3af',marginLeft:'auto'}}>{new Date(m.date).toLocaleString()}</span>
                      </div>
                      <div style={{fontSize:'0.85rem',color:'#374151'}}>{m.text}</div>
                    </div>
                  )
                })}
              </div>
              <div style={{display:'flex',gap:8,marginTop:8,borderTop:'1px solid #e5e7eb',paddingTop:10}}>
                {(() => {
                  const send = () => {
                    if (!subPortalProjectId || !subChatMessage.trim()) return
                    const pid = subPortalProjectId
                    const asOwner = !!subPreviewUserId
                    sendSubAdminChatMessage(pid, { message: subChatMessage, ...(subPreviewUserId ? { user_id: subPreviewUserId } : {}) }).then(() => {
                      setSubChatMessage('')
                      // Close the modal — the admin's reply is sent, the chip will
                      // flip to amber, and there's no reason to keep the popup open.
                      setSubChatModalOpen(false)
                      // Server has already (a) inserted the message, (b) marked
                      // the sender's read-timestamp, and (c) reverted
                      // owner_approval_status from 'denied' → 'pending' when the
                      // sender is an admin. Refresh from the source of truth.
                      fetchSubAdminProjectChat(pid).then((r: any) => { if (r?.success) setSubProjectChat(r.data.messages) })
                      // Optimistically clear the sender's unread on list rows AND
                      // patch latest_chat_user_role so the chip flips immediately:
                      //  - admin reply → role='admin' → chip flips back to amber "Awaiting approval"
                      //  - owner reply (preview-as) → role='owner' → chip flips to violet "Pending response"
                      const senderKey = asOwner ? 'owner_unread_count' : 'admin_unread_count'
                      const newRole = asOwner ? 'owner' : 'admin'
                      const nowIso = new Date().toISOString()
                      // Compare via String() — projectDetailData.id can come back
                      // as a number for legacy rows (e.g. 76), while pid is always
                      // a string from subPortalProjectId. Strict === would fail
                      // and the chip would stay stale until next page fetch.
                      const idsMatch = (a: any, b: any) => String(a) === String(b)
                      const patch = (p: any) => idsMatch(p.id, pid)
                        ? { ...p, [senderKey]: 0, latest_chat_user_role: newRole, latest_chat_message_at: nowIso, chat_message_count: Number(p.chat_message_count || 0) + 1 }
                        : p
                      setProjectsData((prev: any[]) => prev.map(patch))
                      setProjectDetailData((p: any) => p && idsMatch(p.id, pid)
                        ? { ...p, [senderKey]: 0, latest_chat_user_role: newRole, latest_chat_message_at: nowIso }
                        : p)
                      // Also refetch projectDetailData from the source of truth so
                      // the Project Record page chip definitely reflects the latest
                      // server state — defense in depth against any stale field.
                      if (projectDetailData?.id && idsMatch(projectDetailData.id, pid)) {
                        fetchCrmProject(pid).then((fresh: any) => setProjectDetailData(fresh)).catch(() => {})
                      }
                    })
                  }
                  return (<>
                    <input value={subChatMessage} onChange={e => setSubChatMessage(e.target.value)} placeholder="Type a message..." onKeyDown={e => { if (e.key === 'Enter') send() }} style={{flex:1,padding:'8px 12px',borderRadius:6,border:'1px solid #e5e7eb',fontSize:'0.85rem',background:'#ffffff',color:'#111827'}} />
                    <button disabled={!subChatMessage.trim()} onClick={send} style={{padding:'8px 16px',borderRadius:6,border:'none',background:'#2563eb',color:'#fff',cursor:'pointer',fontSize:'0.85rem',fontWeight:600}}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                    </button>
                  </>)
                })()}
              </div>
            </div>
          </div>
        )}

        {modalMeeting && (
          <div className="modal-overlay" onClick={(e) => { e.stopPropagation(); modalMeetingRef.current = null; setModalMeeting(null) }}>
            <div className="modal-card" onClick={(e) => e.stopPropagation()}>
              <div className="modal-color-bar" style={{ background: getEventColor(modalMeeting) }} />
              <button
                type="button"
                className="modal-close"
                onClick={(e) => { e.stopPropagation(); modalMeetingRef.current = null; setModalMeeting(null) }}
                aria-label="Close"
              >
                &times;
              </button>
              <div className="modal-body">
                <div className="modal-tags">
                  {(() => {
                    // The host is the appointed organizer of the event, not the
                    // scanned mailbox(es). Show a single host chip.
                    // Company events (category=holiday) always attribute to BotPros.
                    const isCompanyEvent = modalMeeting.category === 'holiday'
                    const hostName = isCompanyEvent ? 'BotPros' : (modalMeeting.host || modalMeeting.owner || '')
                    if (!hostName) return null
                    const firstName = hostName.split(' ')[0]
                    const ownerColor = isCompanyEvent ? '#db2777' : (estimatorColors[firstName] || estimatorColors[hostName] || '#475569')
                    return (
                      <span className="modal-tag" style={{ background: ownerColor }} title="Host">
                        {hostName}
                      </span>
                    )
                  })()}
                </div>
                <h3 className="modal-title">{modalMeeting.title}</h3>
                {modalMeeting.account && modalMeeting.account !== modalMeeting.title && (
                  <p className="modal-subtitle">{modalMeeting.account}</p>
                )}
                <div className="modal-info-grid">
                  <div className="modal-info-item">
                    <span className="modal-info-icon">&#128197;</span>
                    <div>
                      <span className="modal-info-primary">{longDateFormatter.format(parseDateKey(modalMeeting.date))}</span>
                      <span className="modal-info-secondary">{formatTime12h(modalMeeting.time)} · {modalMeeting.duration}</span>
                    </div>
                  </div>
                  {(modalMeeting.location || modalMeeting.parsedAddress) && (() => {
                    const addr = modalMeeting.parsedAddress || modalMeeting.location
                    return (
                      <a
                        className="modal-info-item modal-info-link"
                        href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addr)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span className="modal-info-icon">&#128205;</span>
                        <div>
                          <span className="modal-info-primary">{addr}</span>
                          <span className="modal-info-secondary">Get directions</span>
                        </div>
                      </a>
                    )
                  })()}
                </div>

                {/* Description / notes */}
                {modalMeeting.description && (
                  <div style={{ marginTop: 12, padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6 }}>
                    <div style={{ fontSize: '0.72rem', color: '#7c98b6', fontWeight: 600, marginBottom: 4 }}>Notes</div>
                    <div style={{ fontSize: '0.82rem', color: '#33475b', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{decodeHexUtf8(modalMeeting.description)}</div>
                  </div>
                )}

                {/* Attendees — everyone other than the host. */}
                {modalMeeting.attendeeEmails && modalMeeting.attendeeEmails.length > 0 && (
                  <div style={{ marginTop: 12, padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6 }}>
                    <div style={{ fontSize: '0.72rem', color: '#7c98b6', fontWeight: 600, marginBottom: 6 }}>Attendees</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {modalMeeting.attendeeEmails.map((email, ai) => (
                        <span key={ai} className="modal-tag" style={{ background: '#64748b' }} title={email}>
                          {email}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Linked deal — shown when event was spawned from a deal.
                    The whole row is clickable and navigates to the deal record. */}
                {modalMeeting.dealId && (
                  <div
                    onClick={() => {
                      const dealId = modalMeeting.dealId!
                      setModalMeeting(null)
                      setActiveView('deals')
                      setDealDetailOpen(true)
                      setDealDetailTab('about')
                      fetchDeal(dealId).then(d => { setSelectedDeal(d) }).catch(() => {})
                      window.location.hash = `/deals/record/${dealId}`
                    }}
                    role="button"
                    tabIndex={0}
                    style={{ marginTop: 10, padding: '8px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#eef2ff'; e.currentTarget.style.borderColor = '#c7d2fe' }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#e2e8f0' }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                    <div style={{ flex: 1, minWidth: 0, fontSize: '0.82rem', fontWeight: 600, color: '#33475b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      View deal · {(liveDeals || []).find(d => d.id === String(modalMeeting.dealId))?.title || modalMeeting.account || `Deal #${modalMeeting.dealId}`}
                    </div>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><polyline points="9 18 15 12 9 6"/></svg>
                  </div>
                )}

                {/* Client info section — shown when event matched a client */}
                {modalMeeting.matchedClientId && (() => {
                  const cachedClient = previewClient?.unified_client_id === modalMeeting.matchedClientId ? previewClient : null
                  return (
                    <div style={{ borderTop: '1px solid #eaf0f6', padding: '16px 0 0', marginTop: 12 }}>
                      <div style={{ fontSize: '0.72rem', color: '#7c98b6', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: 8 }}>Linked Client</div>
                      {cachedClient ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          <div style={{ fontWeight: 600, color: '#33475b' }}>{cachedClient.preferred_name}</div>
                          {cachedClient.preferred_phone && <div style={{ fontSize: '0.85rem' }}><PhoneLink phone={cachedClient.preferred_phone} style={{ color: '#0091ae' }} /></div>}
                          <div style={{ display: 'flex', gap: 16, fontSize: '0.78rem', color: '#516f90' }}>
                            <span>Invoiced: ${(cachedClient.legacy_total_invoiced || 0).toLocaleString()}</span>
                            <span>{LABELS.projects}: {cachedClient.legacy_job_count || 0}</span>
                            <span style={{ color: cachedClient.legacy_balance > 0 ? '#dc2626' : '#16a34a' }}>Balance: ${(cachedClient.legacy_balance || 0).toLocaleString()}</span>
                          </div>
                        </div>
                      ) : (
                        <div style={{ fontWeight: 500, color: '#33475b' }}>{modalMeeting.account || 'Contact'}</div>
                      )}
                      <button
                        style={{ marginTop: 10, padding: '6px 14px', fontSize: '0.82rem', fontWeight: 600, color: '#fff', background: '#0091ae', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                        onClick={() => {
                          const clientId = modalMeeting.matchedClientId!
                          contactDetailReturnHash.current = window.location.hash || '#/calendar'
                          setModalMeeting(null)
                          // Pre-set detail state before hash change to avoid contacts table flash
                          setContactDetailOpen(true)
                          setContactDetailTab('about')
                          fetchClient(clientId).then(c => applyClientOrRedirect(c)).catch(() => {})
                          window.location.hash = `/contacts/record/${encodeURIComponent(clientId)}`
                        }}
                      >
                        View Contact Record
                      </button>
                    </div>
                  )
                })()}
                {/* Company event (holiday category) delete — admin only, with recurring scope selector */}
                {modalMeeting.category === 'holiday' && isSuperAdmin && (() => {
                  // Find matching recurring rule by event name — if a match exists,
                  // offer This / Future / All scope; otherwise plain single delete.
                  const matchingRule = recurringEvents.find(r => r.name.trim().toLowerCase() === (modalMeeting.title || '').trim().toLowerCase())
                  if (holRecurDeleteScope && holRecurDeleteScope.parentId === (matchingRule?.id ?? -1) && holRecurDeleteScope.occurrenceDate === modalMeeting.date) {
                    // Scope selector is open for this event
                    return (
                      <div style={{ marginTop: 12, padding: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6 }}>
                        <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#991b1b', marginBottom: 8 }}>Delete recurring event</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {([['this', 'This event'], ['future', 'This and all future events'], ['all', 'All events in this series']] as const).map(([scope, label]) => (
                            <button
                              key={scope}
                              style={{ padding: '8px 12px', fontSize: '0.82rem', fontWeight: 600, textAlign: 'left', background: '#fff', border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer', color: '#991b1b' }}
                              onClick={async () => {
                                const { parentId, occurrenceDate } = holRecurDeleteScope
                                setHolRecurDeleteScope(null)
                                setModalMeeting(null)
                                try {
                                  await deleteRecurringEvent(parentId, { scope, occurrence_date: occurrenceDate })
                                  loadRecurringEvents()
                                  const { gridStart, gridEnd } = calendarGridBounds(calendarMonth)
                                  const from = toYmd(gridStart)
                                  const to = toYmd(gridEnd)
                                  fetchCalendarEvents(from, to).then(m => setLiveMeetings(m)).catch(() => {})
                                  showCalToast('success', 'Event deleted')
                                } catch (e: any) { showCalToast('error', e.message || 'Delete failed') }
                              }}
                            >{label}</button>
                          ))}
                          <button
                            style={{ padding: '6px 12px', fontSize: '0.82rem', fontWeight: 600, textAlign: 'left', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', color: '#475569', marginTop: 4 }}
                            onClick={() => setHolRecurDeleteScope(null)}
                          >Cancel</button>
                        </div>
                      </div>
                    )
                  }
                  if (calEventDeleteConfirm && !matchingRule) {
                    return (
                      <div style={{ marginTop: 12, padding: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6 }}>
                        <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#991b1b', marginBottom: 4 }}>Delete this company event?</div>
                        <div style={{ fontSize: '0.78rem', color: '#7f1d1d', marginBottom: 12 }}>This cannot be undone.</div>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                          <button style={{ padding: '6px 14px', fontSize: '0.82rem', fontWeight: 600, background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', color: '#33475b' }} onClick={() => setCalEventDeleteConfirm(false)}>Cancel</button>
                          <button
                            style={{ padding: '6px 14px', fontSize: '0.82rem', fontWeight: 600, background: '#dc2626', border: '1px solid #b91c1c', borderRadius: 6, cursor: 'pointer', color: '#ffffff' }}
                            onClick={async () => {
                              const meetingId = modalMeeting.id
                              setCalEventDeleteConfirm(false)
                              setModalMeeting(null)
                              try {
                                // Non-recurring company event: call DELETE /api/holidays/:id which
                                // Deletes every attendee fanout copy, soft-deletes the calendar
                                // banner, and hard-deletes the company_holidays row + link rows.
                                const d1Id = meetingId.startsWith('d1-') ? Number(meetingId.slice(3)) : null
                                if (d1Id) {
                                  await deleteHoliday(d1Id)
                                }
                              } catch (e: any) { showCalToast('error', e.message || 'Delete failed'); return }
                              const { gridStart, gridEnd } = calendarGridBounds(calendarMonth)
                              const from = toYmd(gridStart)
                              const to = toYmd(gridEnd)
                              fetchCalendarEvents(from, to).then(m => setLiveMeetings(m)).catch(() => {})
                              showCalToast('success', 'Event deleted')
                            }}
                          >Delete</button>
                        </div>
                      </div>
                    )
                  }
                  return (
                    <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                      <button
                        style={{ padding: '6px 14px', fontSize: '0.82rem', fontWeight: 600, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer', color: '#dc2626' }}
                        onClick={() => {
                          if (matchingRule) {
                            setHolRecurDeleteScope({ parentId: matchingRule.id, name: matchingRule.name, occurrenceDate: modalMeeting.date })
                          } else {
                            setCalEventDeleteConfirm(true)
                          }
                        }}
                      >Delete</button>
                    </div>
                  )
                })()}
                {(() => {
                  const rawId = modalMeeting.id.startsWith('BX-') ? modalMeeting.id.slice(3) : null
                  const ownerUser = CALENDAR_USERS_RAW.find(u => u.name === modalMeeting.owner || u.name.split(' ')[0] === modalMeeting.owner)
                  // Edit is allowed for admins, office, and estimators-of-own-event.
                  // Delete is gated more tightly below (admin / estimator-own only).
                  const canEditEvent = (isCalendarAdmin || isCalendarOffice || (isCalendarEstimator && ownerUser?.userId === currentUserId)) && calCapEdit
                  const canDeleteEvent = (isCalendarAdmin || (isCalendarEstimator && ownerUser?.userId === currentUserId)) && calCapDelete
                  if (!rawId || !ownerUser || !canEditEvent) return null

                  const runDelete = () => {
                    const deletedMeetingId = modalMeeting.id
                    setLiveMeetings(prev => prev ? prev.filter(m => m.id !== deletedMeetingId) : prev)
                    setCalEventDeleteConfirm(false)
                    setModalMeeting(null)
                    deleteCalendarEvent(rawId, ownerUser.userId).then(() => {
                      const { gridStart, gridEnd } = calendarGridBounds(calendarMonth)
                      const from = toYmd(gridStart)
                      const to = toYmd(gridEnd)
                      fetchCalendarEvents(from, to).then(m => setLiveMeetings(m)).catch(() => {})
                    }).catch(e => {
                      alert('Delete failed: ' + e.message)
                      const { gridStart, gridEnd } = calendarGridBounds(calendarMonth)
                      const from = toYmd(gridStart)
                      const to = toYmd(gridEnd)
                      fetchCalendarEvents(from, to).then(m => setLiveMeetings(m)).catch(() => {})
                    })
                  }

                  if (calEventDeleteConfirm && canDeleteEvent) {
                    return (
                      <div style={{ marginTop: 12, padding: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6 }}>
                        <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#991b1b', marginBottom: 4 }}>
                          Delete this calendar event?
                        </div>
                        <div style={{ fontSize: '0.78rem', color: '#7f1d1d', marginBottom: 12 }}>
                          This removes the event from the calendar and cannot be undone.
                        </div>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                          <button
                            style={{ padding: '6px 14px', fontSize: '0.82rem', fontWeight: 600, background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', color: '#33475b' }}
                            onClick={() => setCalEventDeleteConfirm(false)}
                          >Cancel</button>
                          <button
                            style={{ padding: '6px 14px', fontSize: '0.82rem', fontWeight: 600, background: '#dc2626', border: '1px solid #b91c1c', borderRadius: 6, cursor: 'pointer', color: '#ffffff' }}
                            onClick={runDelete}
                          >Delete</button>
                        </div>
                      </div>
                    )
                  }

                  return (
                    <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                      <button
                        style={{ padding: '6px 14px', fontSize: '0.82rem', fontWeight: 600, background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', color: '#33475b' }}
                        onClick={() => {
                          // Parse the title back into (eventName, clientName).
                          // classifyEvent() strips the "(XX) - " prefix for display, so a
                          // reschedule sees title = "Name - Address" (new dash form) or the
                          // legacy "Name, Address" comma form. Disambiguate system-generated
                          // titles (no human event name) from genuine custom names by
                          // comparing against the server-resolved known client + location.
                          // If the title IS the system composite (either separator), blank
                          // Event Name so the server rebuilds the clean
                          // "(XX) - Name - Address" — preventing #18 duplication while
                          // letting real custom names through. Legacy comma titles auto-heal.
                          const fullTitle = modalMeeting.title || ''
                          const knownClient = (modalMeeting.clientName || '').trim()
                          const knownLoc = (modalMeeting.location || '').trim()
                          const norm = (s: string) => (s || '').trim().replace(/\s+/g, ' ').toLowerCase()
                          let parsedEventName = fullTitle
                          let parsedClientName = ''
                          const sysNew = knownLoc ? `${knownClient} - ${knownLoc}` : knownClient
                          const sysOld = knownLoc ? `${knownClient}, ${knownLoc}` : knownClient
                          const nt = norm(fullTitle)
                          if (knownClient && (nt === norm(knownClient) || nt === norm(sysNew) || nt === norm(sysOld))) {
                            parsedEventName = ''
                            parsedClientName = knownClient
                          } else {
                            const sep = fullTitle.indexOf(' - ')
                            if (sep > 0) {
                              parsedEventName = fullTitle.slice(0, sep).trim()
                              const rest = fullTitle.slice(sep + 3).trim()
                              const commaIdx = rest.indexOf(',')
                              const dashIdx = rest.indexOf(' - ')
                              const cut = dashIdx > 0 ? dashIdx : (commaIdx > 0 ? commaIdx : rest.length)
                              parsedClientName = rest.slice(0, cut).trim()
                            }
                          }
                          // Map attendee emails back to CALENDAR_USERS_RAW user IDs
                          // so the chip buttons start in the "selected" state.
                          const attendeeIds = (modalMeeting.attendeeEmails ?? [])
                            .map(email => CALENDAR_USERS_RAW.find(u => u.email.toLowerCase() === email.toLowerCase())?.userId)
                            .filter((id): id is number => typeof id === 'number')
                          setModalMeeting(null)
                          setCalEventForm({
                            ownerUserId: ownerUser.userId,
                            eventName: parsedEventName,
                            // Prefer the server-resolved (id-linked) contact name;
                            // fall back to the name parsed out of the event title.
                            clientName: modalMeeting.clientName ?? parsedClientName,
                            address: modalMeeting.location || '',
                            date: modalMeeting.date,
                            time: modalMeeting.time || '09:00',
                            durationMinutes: parseDurationToMinutes(modalMeeting.duration),
                            attendeeIds,
                            notes: '',
                            clientId: modalMeeting.storedClientId ?? null,
                            // Server-resolved linked-contact email enables the invite checkbox.
                            clientEmail: modalMeeting.clientEmail ?? null,
                            inviteClient: !!modalMeeting.clientInvited,
                          })
                          setCalEventModal({ mode: 'edit', eventId: rawId, originalTitle: fullTitle })
                          setCalEventError(null)
                          setCalEventDeleteConfirm(false)
                        }}
                      >Edit event</button>
                      {canDeleteEvent && (
                        <button
                          style={{ padding: '6px 14px', fontSize: '0.82rem', fontWeight: 600, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer', color: '#dc2626' }}
                          onClick={() => setCalEventDeleteConfirm(true)}
                        >Delete</button>
                      )}
                    </div>
                  )
                })()}
              </div>
            </div>
          </div>
        )}

        {calEventModal && (
          <div
            className="modal-overlay"
            onMouseDown={(e) => {
              // Only close if the mousedown actually started on the overlay itself,
              // not on a child (e.g. dragging to select text inside an input).
              if (e.target === e.currentTarget && !calEventSaving) setCalEventModal(null)
            }}
          >
            <div className="modal-card" style={{ maxWidth: 480 }} onMouseDown={e => e.stopPropagation()}>
              <div className="modal-color-bar" style={{ background: '#1976d2' }} />
              <button className="modal-close" onClick={() => setCalEventModal(null)}>&times;</button>
              <div className="modal-body">
                <h3 className="modal-title" style={{ marginBottom: 16 }}>{calEventModal.mode === 'create' ? (calEventType === 'holiday' ? 'New Company Event' : 'New Calendar Event') : 'Edit Calendar Event'}</h3>

                {/* Event-type toggle — only in create mode, and the Company
                    Holiday option only when the user has the holiday capability. */}
                {calEventModal.mode === 'create' && (myCapabilities as Record<string, boolean> | null)?.manage_company_holidays && (
                  <div style={{ display: 'inline-flex', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, padding: 3, marginBottom: 16 }}>
                    {([['appointment', 'Appointment'], ['holiday', 'Company Event']] as const).map(([val, lbl]) => (
                      <button
                        key={val}
                        type="button"
                        onClick={() => { setCalEventType(val); setCalEventError(null); setHolError(null) }}
                        style={{ padding: '6px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: '0.84rem', fontWeight: 600, background: calEventType === val ? '#ffffff' : 'transparent', color: calEventType === val ? '#1976d2' : '#64748b', boxShadow: calEventType === val ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}
                      >{lbl}</button>
                    ))}
                  </div>
                )}

                {(calEventType === 'holiday' ? holError : calEventError) && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '8px 12px', fontSize: '0.85rem', color: '#dc2626', marginBottom: 12 }}>{calEventType === 'holiday' ? holError : calEventError}</div>}

                {calEventType === 'holiday' ? (
                  <>
                    {/* Event name */}
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#516f90', display: 'block', marginBottom: 4 }}>Event name</label>
                      <input type="text" value={holForm.name} onChange={e => setHolForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Memorial Day, Office Closed, Maintenance, Parade" style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box' }} />
                    </div>
                    {/* Event type */}
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#516f90', display: 'block', marginBottom: 4 }}>Type</label>
                      <select value={holForm.event_type} onChange={e => setHolForm(f => ({ ...f, event_type: e.target.value as typeof f.event_type }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box' }}>
                        <option value="holiday">Holiday</option>
                        <option value="day_off">Day Off</option>
                        <option value="closure">Closure</option>
                        <option value="maintenance">Maintenance</option>
                        <option value="event">Event</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    {/* Event dates */}
                    <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#516f90', display: 'block', marginBottom: 4 }}>Start date</label>
                        <input type="date" value={holForm.start_date} onChange={e => setHolForm(f => { const sd = e.target.value; return { ...f, start_date: sd, end_date: sd } })} style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box' }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#516f90', display: 'block', marginBottom: 4 }}>End date</label>
                        <input type="date" min={holForm.start_date} value={holForm.end_date} onChange={e => setHolForm(f => ({ ...f, end_date: e.target.value, is_all_day: e.target.value === f.start_date ? f.is_all_day : true }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box' }} />
                      </div>
                    </div>
                    {/* All-day / timed toggle — timed only for a single day */}
                    {(() => {
                      const singleDay = holForm.start_date === holForm.end_date
                      const isTimed = !holForm.is_all_day && singleDay
                      const timeOptions: string[] = []
                      for (let mins = 8 * 60; mins <= 17 * 60; mins += 15) {
                        timeOptions.push(`${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`)
                      }
                      return (
                        <>
                          <div style={{ marginBottom: 12 }}>
                            <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#516f90', display: 'block', marginBottom: 4 }}>Duration</label>
                            <div style={{ display: 'inline-flex', border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
                              <button type="button" onClick={() => setHolForm(f => ({ ...f, is_all_day: true }))} style={{ padding: '7px 16px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', border: 'none', background: holForm.is_all_day ? '#7c3aed' : '#fff', color: holForm.is_all_day ? '#fff' : '#475569' }}>All day</button>
                              <button type="button" disabled={!singleDay} onClick={() => singleDay && setHolForm(f => ({ ...f, is_all_day: false }))} title={singleDay ? '' : 'Timed events are only available for a single-day event'} style={{ padding: '7px 16px', fontSize: '0.85rem', fontWeight: 600, cursor: singleDay ? 'pointer' : 'not-allowed', border: 'none', borderLeft: '1px solid #e5e7eb', background: isTimed ? '#7c3aed' : '#fff', color: isTimed ? '#fff' : (singleDay ? '#475569' : '#cbd5e1') }}>Timed</button>
                            </div>
                          </div>
                          {isTimed && (
                            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                              <div style={{ flex: 1 }}>
                                <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#516f90', display: 'block', marginBottom: 4 }}>Start time</label>
                                <ModernTimePicker ariaLabel="Start time" value={holForm.start_time} options={timeOptions.slice(0, -1)} onChange={v => setHolForm(f => ({ ...f, start_time: v, end_time: f.end_time > v ? f.end_time : (timeOptions.filter(t => t > v)[0] || f.end_time) }))} style={{ width: '100%' }} />
                              </div>
                              <div style={{ flex: 1 }}>
                                <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#516f90', display: 'block', marginBottom: 4 }}>End time</label>
                                <ModernTimePicker ariaLabel="End time" value={holForm.end_time} options={timeOptions.filter(t => t > holForm.start_time)} onChange={v => setHolForm(f => ({ ...f, end_time: v }))} style={{ width: '100%' }} />
                              </div>
                            </div>
                          )}
                        </>
                      )
                    })()}
                    {/* Repeat / recurrence */}
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#516f90', display: 'block', marginBottom: 4 }}>Repeat</label>
                      <div style={{ position: 'relative' }}>
                        <select value={holForm.recurrence_freq} onChange={e => setHolForm(f => ({ ...f, recurrence_freq: e.target.value as typeof f.recurrence_freq, recurrence_end_date: '' }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box', appearance: 'none', background: '#fff' }}>
                          <option value="none">Does not repeat</option>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                          <option value="annual">Annual</option>
                        </select>
                        <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#64748b' }}>▾</span>
                      </div>
                    </div>
                    {holForm.recurrence_freq !== 'none' && (
                      <div style={{ marginBottom: 12 }}>
                        <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#516f90', display: 'block', marginBottom: 4 }}>Repeat until <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional)</span></label>
                        <input type="date" min={holForm.start_date} value={holForm.recurrence_end_date} onChange={e => setHolForm(f => ({ ...f, recurrence_end_date: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box' }} />
                      </div>
                    )}
                  </>
                ) : (
                <>


                {/* Host/Owner dropdown removed 2026-06-12 — the availability
                    grid is the single control: clicking a slot on a person's
                    row makes them the host, same-time clicks toggle attendees. */}

                {/* Event name */}
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#516f90', display: 'block', marginBottom: 4 }}>Event Name</label>
                  <input type="text" value={calEventForm.eventName} onChange={e => setCalEventForm(f => ({ ...f, eventName: e.target.value }))} placeholder="e.g. Estimate appointment, Site visit..." style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box' }} />
                </div>

                {/* Client name (with contact autocomplete) */}
                <div style={{ marginBottom: 12, position: 'relative' }}>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#516f90', display: 'block', marginBottom: 4 }}>Client Name</label>
                  <input
                    type="text"
                    value={calEventForm.clientName}
                    onChange={e => setCalEventForm(f => ({ ...f, clientName: e.target.value, clientId: null, clientEmail: null }))}
                    onFocus={() => setCalClientSearchFocused(true)}
                    onBlur={() => { setTimeout(() => setCalClientSearchFocused(false), 150) }}
                    placeholder="Search clients or type a new name..."
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box' }}
                  />
                  {calClientSearchFocused && (calEventForm.clientName ?? '').length >= 2 && (
                    <div style={{ position: 'absolute', left: 0, right: 0, top: '100%', marginTop: 2, background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 6, boxShadow: '0 6px 16px rgba(0,0,0,0.08)', zIndex: 10, maxHeight: 220, overflowY: 'auto' }}>
                      {calClientSearchLoading && <div style={{ padding: '8px 12px', fontSize: '0.82rem', color: '#94a3b8' }}>Searching...</div>}
                      {!calClientSearchLoading && calClientSearchResults && calClientSearchResults.contacts.length === 0 && (
                        <div style={{ padding: '8px 12px', fontSize: '0.82rem', color: '#94a3b8' }}>No matching contacts — press Save to use "{calEventForm.clientName}" as entered.</div>
                      )}
                      {!calClientSearchLoading && calClientSearchResults && calClientSearchResults.contacts.slice(0, 8).map(ct => (
                        <button
                          key={ct.unified_client_id}
                          type="button"
                          onMouseDown={(e) => {
                            // Prevent input blur before click fires
                            e.preventDefault()
                            setCalEventForm(f => ({
                              ...f,
                              clientName: ct.preferred_name,
                              address: ct.preferred_street || f.address,
                              clientId: ct.unified_client_id,
                              clientEmail: ct.preferred_email ?? null,
                            }))
                            setCalClientSearchFocused(false)
                          }}
                          style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '0.85rem', borderBottom: '1px solid #f1f5f9' }}
                        >
                          <div style={{ fontWeight: 600, color: '#33475b' }}>{ct.preferred_name}</div>
                          {(ct.preferred_street || ct.preferred_phone || ct.preferred_email) && (
                            <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 2 }}>
                              {[ct.preferred_street, ct.preferred_phone, ct.preferred_email].filter(Boolean).join(' • ')}
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Email invite to client — only when a real contact is linked */}
                {calEventForm.clientId && (
                  <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      id="cal-invite-client"
                      checked={!!calEventForm.inviteClient}
                      disabled={!calEventForm.clientEmail}
                      onChange={e => setCalEventForm(f => ({ ...f, inviteClient: e.target.checked }))}
                      style={{ width: 15, height: 15, cursor: calEventForm.clientEmail ? 'pointer' : 'not-allowed' }}
                    />
                    <label htmlFor="cal-invite-client" style={{ fontSize: '0.82rem', color: calEventForm.clientEmail ? '#33475b' : '#94a3b8', cursor: calEventForm.clientEmail ? 'pointer' : 'default' }}>
                      {calEventForm.clientEmail
                        ? <>Email invite to client <span style={{ color: '#64748b' }}>({calEventForm.clientEmail})</span></>
                        : 'Email invite to client — no email on file for this contact'}
                    </label>
                  </div>
                )}

                {/* Address */}
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#516f90', display: 'block', marginBottom: 4 }}>Address</label>
                  <AddressInput
                    value={calEventForm.address}
                    onChange={v => setCalEventForm(f => ({ ...f, address: v }))}
                    onAddressPicked={(addr) => {
                      const full = [addr.street, addr.city, addr.state].filter(Boolean).join(', ') + (addr.zip ? ` ${addr.zip}` : '')
                      setCalEventForm(f => ({ ...f, address: full.trim() }))
                    }}
                    placeholder="Start typing an address…"
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box' }}
                  />
                </div>

                {/* Date + Time — availability grid is the primary picker: it
                    shows every calendar's busy blocks, and clicking a slot on a
                    different person's row reassigns the host. Manual date/time
                    pickers stay underneath for quick exact entry. */}
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#516f90', display: 'block', marginBottom: 4 }}>Date &amp; Time <span style={{ fontWeight: 400, color: '#7c98b6' }}>(pick from availability — clicking another row changes the host)</span></label>
                  <AvailabilityDateTimePicker
                    value={calEventForm.date && calEventForm.time ? `${calEventForm.date}T${calEventForm.time}` : ''}
                    onChange={(v) => {
                      // Empty value = the user clicked their selected slot to
                      // unselect it. Clear the time (keeps the date for the
                      // manual pickers); save still requires a time.
                      if (!v) { setCalEventForm(f => ({ ...f, time: '' })); return }
                      const [d, t] = v.split('T')
                      if (d && t) setCalEventForm(f => ({ ...f, date: d, time: t.slice(0, 5) }))
                    }}
                    estimatorId={calEventForm.ownerUserId || null}
                    onEstimatorChange={(id) => setCalEventForm(f => ({ ...f, ownerUserId: id || null }))}
                    durationMinutes={calEventForm.durationMinutes}
                    onDurationChange={(m) => setCalEventForm(f => ({ ...f, durationMinutes: m }))}
                    estimatorIds={CALENDAR_USERS_RAW.map(u => u.userId)}
                    attendeeIds={calEventForm.attendeeIds}
                    onAttendeesChange={(ids) => setCalEventForm(f => ({ ...f, attendeeIds: ids }))}
                    excludeTitle={calEventModal.mode === 'edit' ? calEventModal.originalTitle : undefined}
                  />
                </div>
                {/* Duration */}
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#516f90', display: 'block', marginBottom: 4 }}>Duration</label>
                  <select value={calEventForm.durationMinutes} onChange={e => setCalEventForm(f => ({ ...f, durationMinutes: Number(e.target.value) }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: '0.9rem' }}>
                    <option value={30}>30 min</option><option value={45}>45 min</option><option value={60}>1 hour</option><option value={90}>1.5 hours</option><option value={120}>2 hours</option><option value={180}>3 hours</option><option value={240}>4 hours</option>
                  </select>
                </div>

                {/* Attendees — owner is the event host, not an attendee, so exclude them from the list */}
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#516f90', display: 'block', marginBottom: 4 }}>Attendees</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                    {!calEventForm.ownerUserId && (
                      <span style={{ fontSize: '0.74rem', color: '#94a3b8' }}>Pick a slot in the grid above to set the host.</span>
                    )}
                    {(() => {
                      const host = CALENDAR_USERS_RAW.find(u => u.userId === calEventForm.ownerUserId)
                      if (!host) return null
                      // bp-cal-roster-v1: stable per-teammate color (override or hash)
                      const personColor = resolveEmployeeColor(host.name, employeeColors)
                      return (
                        <span title="The host owns the event — change it by clicking a slot on another row in the availability grid" style={{ padding: '4px 10px', borderRadius: 20, fontSize: '0.78rem', fontWeight: 600, background: personColor, color: '#fff', opacity: 0.85, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          {host.name.split(' ')[0]}<span style={{ fontSize: '0.65rem', fontWeight: 700, opacity: 0.85, textTransform: 'uppercase', letterSpacing: 0.4 }}>· Host</span>
                        </span>
                      )
                    })()}
                    {calEventForm.attendeeIds.map(id => {
                      const u = CALENDAR_USERS_RAW.find(x => x.userId === id)
                      if (!u || id === calEventForm.ownerUserId) return null
                      // bp-cal-roster-v1: stable per-teammate color (override or hash)
                      const personColor = resolveEmployeeColor(u.name, employeeColors)
                      return (
                        <button key={id} type="button" title="Click to remove this attendee" onClick={() => setCalEventForm(f => ({ ...f, attendeeIds: f.attendeeIds.filter(x => x !== id) }))} style={{ padding: '4px 10px', borderRadius: 20, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', border: 'none', background: personColor, color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          {u.name.split(' ')[0]}<span style={{ fontSize: '0.7rem', opacity: 0.8 }}>&times;</span>
                        </button>
                      )
                    })}
                    {calEventForm.ownerUserId && calEventForm.attendeeIds.filter(id => id !== calEventForm.ownerUserId).length === 0 && (
                      <span style={{ fontSize: '0.74rem', color: '#94a3b8' }}>None — in the availability grid, click another row at the same time to add someone.</span>
                    )}
                  </div>
                </div>

                {/* Notes */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#516f90', display: 'block', marginBottom: 4 }}>Notes</label>
                  <textarea value={calEventForm.notes} onChange={e => setCalEventForm(f => ({ ...f, notes: e.target.value }))} rows={2} placeholder="Optional notes..." style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: '0.9rem', resize: 'vertical', boxSizing: 'border-box' }} />
                </div>
                </>
                )}

                {/* Actions */}
                {calEventType === 'holiday' ? (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button onClick={() => setCalEventModal(null)} disabled={holSaving} style={{ padding: '8px 16px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: '0.88rem' }}>Cancel</button>
                  {(() => {
                    const holSubmitDisabled = holSaving || !holForm.name.trim() || holForm.end_date < holForm.start_date || (!holForm.is_all_day && holForm.start_date === holForm.end_date && !(holForm.end_time && holForm.end_time > holForm.start_time))
                    return (
                      <button
                        disabled={holSubmitDisabled}
                        onClick={async () => {
                          if (!holForm.name.trim()) { setHolError('Event name required'); return }
                          if (holForm.end_date < holForm.start_date) { setHolError('End date must be on or after the start date'); return }
                          const isTimed = !holForm.is_all_day && holForm.start_date === holForm.end_date
                          if (isTimed && !(holForm.end_time && holForm.end_time > holForm.start_time)) { setHolError('End time must be after start time'); return }
                          setHolSaving(true); setHolError(null)
                          try {
                            await createHoliday({ name: holForm.name.trim(), start_date: holForm.start_date, end_date: holForm.end_date, event_type: holForm.event_type, is_all_day: !isTimed, ...(isTimed ? { start_time: holForm.start_time, end_time: holForm.end_time } : {}), ...(holForm.recurrence_freq !== 'none' ? { recurrence_freq: holForm.recurrence_freq, ...(holForm.recurrence_end_date ? { recurrence_end_date: holForm.recurrence_end_date } : {}) } : {}) })
                            setCalEventModal(null)
                            showCalToast('success', isSuperAdmin ? "Event created — adding to everyone's calendar" : 'Event requested for approval')
                            setHolForm({ name: '', start_date: todayKey, end_date: todayKey, event_type: 'holiday', is_all_day: true, start_time: '09:00', end_time: '12:00', recurrence_freq: 'none', recurrence_end_date: '' })
                            const { gridStart, gridEnd } = calendarGridBounds(calendarMonth)
                            const from = toYmd(gridStart)
                            const to = toYmd(gridEnd)
                            fetchCalendarEvents(from, to).then(m => setLiveMeetings(m)).catch(() => {})
                            loadHolidays()
                          } catch (e: any) { setHolError(e.message || 'Failed') }
                          finally { setHolSaving(false) }
                        }}
                        style={{ padding: '8px 20px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, cursor: holSubmitDisabled ? 'not-allowed' : 'pointer', fontSize: '0.88rem', fontWeight: 600, opacity: holSubmitDisabled ? 0.6 : 1 }}
                      >{holSaving ? 'Saving...' : isSuperAdmin ? 'Create Event' : 'Submit Request'}</button>
                    )
                  })()}
                </div>
                ) : (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button onClick={() => setCalEventModal(null)} disabled={calEventSaving} style={{ padding: '8px 16px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: '0.88rem' }}>Cancel</button>
                  <button
                    disabled={calEventSaving || (!(calEventForm.eventName ?? '').trim() && !(calEventForm.clientName ?? '').trim()) || (calEventModal.mode === 'create' && (!calEventForm.ownerUserId || !calEventForm.time))}
                    onClick={async () => {
                      if (!(calEventForm.eventName ?? '').trim() && !(calEventForm.clientName ?? '').trim()) { setCalEventError('Event name or client name required'); return }
                      if (!calEventForm.ownerUserId || !calEventForm.date || !calEventForm.time) { setCalEventError('Pick a slot in the availability grid to set the host and time'); return }
                      setCalEventSaving(true); setCalEventError(null)
                      try {
                        if (calEventModal.mode === 'create') {
                          await createCalendarEvent({ ...calEventForm, ownerUserId: calEventForm.ownerUserId!, dealId: calEventModal.dealId })
                        } else {
                          await updateCalendarEvent(calEventModal.eventId!, { ...calEventForm, ownerUserId: calEventForm.ownerUserId! })
                        }
                        setCalEventModal(null)
                        const { gridStart, gridEnd } = calendarGridBounds(calendarMonth)
                        const from = toYmd(gridStart)
                        const to = toYmd(gridEnd)
                        fetchCalendarEvents(from, to).then(m => setLiveMeetings(m)).catch(() => {})
                      } catch (e: any) { setCalEventError(e.message || 'Failed') }
                      finally { setCalEventSaving(false) }
                    }}
                    style={{ padding: '8px 20px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600, opacity: calEventSaving ? 0.7 : 1 }}
                  >{calEventSaving ? 'Saving...' : calEventModal.mode === 'create' ? 'Create Event' : 'Save Changes'}</button>
                </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Request Time Off modal ── */}
        {timeOffModalOpen && (() => {
          const singleDay = toForm.start_date === toForm.end_date
          const isPartial = toForm.segment === 'partial' && singleDay
          // 08:00–17:00 on 15-min steps.
          const timeOptions: string[] = []
          for (let mins = 8 * 60; mins <= 17 * 60; mins += 15) {
            timeOptions.push(`${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`)
          }
          const partialInvalid = isPartial && !(toForm.end_time > toForm.start_time)
          const datesInvalid = toForm.end_date < toForm.start_date
          const submitDisabled = toSaving || datesInvalid || partialInvalid
          return (
          <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !toSaving) setTimeOffModalOpen(false) }}>
            <div className="modal-card" style={{ maxWidth: 460 }} onMouseDown={e => e.stopPropagation()}>
              <div className="modal-color-bar" style={{ background: '#0ea5e9' }} />
              <button className="modal-close" onClick={() => setTimeOffModalOpen(false)}>&times;</button>
              <div className="modal-body">
                <h3 className="modal-title" style={{ marginBottom: 16 }}>Request Time Off</h3>
                {toError && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '8px 12px', fontSize: '0.85rem', color: '#dc2626', marginBottom: 12 }}>{toError}</div>}
                <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#516f90', display: 'block', marginBottom: 4 }}>Start date</label>
                    <input type="date" value={toForm.start_date} onChange={e => setToForm(f => { const sd = e.target.value; return { ...f, start_date: sd, end_date: f.end_date < sd ? sd : f.end_date } })} style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#516f90', display: 'block', marginBottom: 4 }}>End date</label>
                    <input type="date" min={toForm.start_date} value={toForm.end_date} onChange={e => setToForm(f => ({ ...f, end_date: e.target.value, segment: e.target.value === f.start_date ? f.segment : 'full' }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box' }} />
                  </div>
                </div>
                {/* Full / Partial toggle — Partial only for a single day */}
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#516f90', display: 'block', marginBottom: 4 }}>Duration</label>
                  <div style={{ display: 'inline-flex', border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
                    <button type="button" onClick={() => setToForm(f => ({ ...f, segment: 'full' }))} style={{ padding: '7px 16px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', border: 'none', background: toForm.segment === 'full' ? '#0ea5e9' : '#fff', color: toForm.segment === 'full' ? '#fff' : '#475569' }}>Full day</button>
                    <button type="button" disabled={!singleDay} onClick={() => singleDay && setToForm(f => ({ ...f, segment: 'partial' }))} title={singleDay ? '' : 'Partial day is only available for a single-day request'} style={{ padding: '7px 16px', fontSize: '0.85rem', fontWeight: 600, cursor: singleDay ? 'pointer' : 'not-allowed', border: 'none', borderLeft: '1px solid #e5e7eb', background: isPartial ? '#0ea5e9' : '#fff', color: isPartial ? '#fff' : (singleDay ? '#475569' : '#cbd5e1') }}>Partial day</button>
                  </div>
                </div>
                {isPartial && (
                  <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#516f90', display: 'block', marginBottom: 4 }}>Start time</label>
                      <select value={toForm.start_time} onChange={e => setToForm(f => ({ ...f, start_time: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: '0.9rem' }}>
                        {timeOptions.slice(0, -1).map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#516f90', display: 'block', marginBottom: 4 }}>End time</label>
                      <select value={toForm.end_time} onChange={e => setToForm(f => ({ ...f, end_time: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: '0.9rem' }}>
                        {timeOptions.filter(t => t > toForm.start_time).map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>
                )}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#516f90', display: 'block', marginBottom: 4 }}>Reason</label>
                  <textarea value={toForm.reason} onChange={e => setToForm(f => ({ ...f, reason: e.target.value }))} rows={2} placeholder="Optional reason..." style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: '0.9rem', resize: 'vertical', boxSizing: 'border-box' }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button onClick={() => setTimeOffModalOpen(false)} disabled={toSaving} style={{ padding: '8px 16px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: '0.88rem' }}>Cancel</button>
                  <button
                    disabled={submitDisabled}
                    onClick={async () => {
                      setToSaving(true); setToError(null)
                      try {
                        await createTimeOffRequest({
                          start_date: toForm.start_date, end_date: toForm.end_date, segment: isPartial ? 'partial' : 'full',
                          ...(isPartial ? { start_time: toForm.start_time, end_time: toForm.end_time } : {}),
                          reason: toForm.reason.trim() || undefined,
                        })
                        setTimeOffModalOpen(false)
                        showCalToast('success', 'Request submitted for approval')
                        loadTimeOff()
                      } catch (e: any) { setToError(e.message || 'Failed to submit request') }
                      finally { setToSaving(false) }
                    }}
                    style={{ padding: '8px 20px', background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: 6, cursor: submitDisabled ? 'not-allowed' : 'pointer', fontSize: '0.88rem', fontWeight: 600, opacity: submitDisabled ? 0.6 : 1 }}
                  >{toSaving ? 'Submitting...' : 'Submit request'}</button>
                </div>
              </div>
            </div>
          </div>
          )
        })()}

        {/* ── Add / Request Company Event modal ── */}
        {holidayModalOpen && (() => {
          const datesInvalid = holForm.end_date < holForm.start_date
          const singleDay = holForm.start_date === holForm.end_date
          const isTimed = !holForm.is_all_day && singleDay
          const timeOptions: string[] = []
          for (let mins = 8 * 60; mins <= 17 * 60; mins += 15) {
            timeOptions.push(`${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`)
          }
          const timedInvalid = isTimed && !(holForm.end_time && holForm.end_time > holForm.start_time)
          const submitDisabled = holSaving || !holForm.name.trim() || datesInvalid || timedInvalid
          return (
          <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !holSaving) setHolidayModalOpen(false) }}>
            <div className="modal-card" style={{ maxWidth: 440 }} onMouseDown={e => e.stopPropagation()}>
              <div className="modal-color-bar" style={{ background: '#7c3aed' }} />
              <button className="modal-close" onClick={() => setHolidayModalOpen(false)}>&times;</button>
              <div className="modal-body">
                <h3 className="modal-title" style={{ marginBottom: 16 }}>{isSuperAdmin ? 'Add Company Event' : 'Request Event'}</h3>
                {holError && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '8px 12px', fontSize: '0.85rem', color: '#dc2626', marginBottom: 12 }}>{holError}</div>}
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#516f90', display: 'block', marginBottom: 4 }}>Event name</label>
                  <input type="text" value={holForm.name} onChange={e => setHolForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Memorial Day, Office Closed, Maintenance, Parade" style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box' }} />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#516f90', display: 'block', marginBottom: 4 }}>Type</label>
                  <select value={holForm.event_type} onChange={e => setHolForm(f => ({ ...f, event_type: e.target.value as typeof f.event_type }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box' }}>
                    <option value="holiday">Holiday</option>
                    <option value="day_off">Day Off</option>
                    <option value="closure">Closure</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="event">Event</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#516f90', display: 'block', marginBottom: 4 }}>Start date</label>
                    <input type="date" value={holForm.start_date} onChange={e => setHolForm(f => { const sd = e.target.value; return { ...f, start_date: sd, end_date: sd } })} style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#516f90', display: 'block', marginBottom: 4 }}>End date</label>
                    <input type="date" min={holForm.start_date} value={holForm.end_date} onChange={e => setHolForm(f => ({ ...f, end_date: e.target.value, is_all_day: e.target.value === f.start_date ? f.is_all_day : true }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box' }} />
                  </div>
                </div>
                {/* All-day / timed toggle — timed only for a single day */}
                <div style={{ marginBottom: isTimed ? 12 : 16 }}>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#516f90', display: 'block', marginBottom: 4 }}>Duration</label>
                  <div style={{ display: 'inline-flex', border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
                    <button type="button" onClick={() => setHolForm(f => ({ ...f, is_all_day: true }))} style={{ padding: '7px 16px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', border: 'none', background: holForm.is_all_day ? '#7c3aed' : '#fff', color: holForm.is_all_day ? '#fff' : '#475569' }}>All day</button>
                    <button type="button" disabled={!singleDay} onClick={() => singleDay && setHolForm(f => ({ ...f, is_all_day: false }))} title={singleDay ? '' : 'Timed events are only available for a single-day event'} style={{ padding: '7px 16px', fontSize: '0.85rem', fontWeight: 600, cursor: singleDay ? 'pointer' : 'not-allowed', border: 'none', borderLeft: '1px solid #e5e7eb', background: isTimed ? '#7c3aed' : '#fff', color: isTimed ? '#fff' : (singleDay ? '#475569' : '#cbd5e1') }}>Timed</button>
                  </div>
                </div>
                {isTimed && (
                  <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#516f90', display: 'block', marginBottom: 4 }}>Start time</label>
                      <ModernTimePicker ariaLabel="Start time" value={holForm.start_time} options={timeOptions.slice(0, -1)} onChange={v => setHolForm(f => ({ ...f, start_time: v, end_time: f.end_time > v ? f.end_time : (timeOptions.filter(t => t > v)[0] || f.end_time) }))} style={{ width: '100%' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#516f90', display: 'block', marginBottom: 4 }}>End time</label>
                      <ModernTimePicker ariaLabel="End time" value={holForm.end_time} options={timeOptions.filter(t => t > holForm.start_time)} onChange={v => setHolForm(f => ({ ...f, end_time: v }))} style={{ width: '100%' }} />
                    </div>
                  </div>
                )}
                {/* Repeat / recurrence */}
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#516f90', display: 'block', marginBottom: 4 }}>Repeat</label>
                  <div style={{ position: 'relative' }}>
                    <select value={holForm.recurrence_freq} onChange={e => setHolForm(f => ({ ...f, recurrence_freq: e.target.value as typeof f.recurrence_freq, recurrence_end_date: '' }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box', appearance: 'none', background: '#fff' }}>
                      <option value="none">Does not repeat</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="annual">Annual</option>
                    </select>
                    <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#64748b' }}>▾</span>
                  </div>
                </div>
                {holForm.recurrence_freq !== 'none' && (
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#516f90', display: 'block', marginBottom: 4 }}>Repeat until <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional)</span></label>
                    <input type="date" min={holForm.start_date} value={holForm.recurrence_end_date} onChange={e => setHolForm(f => ({ ...f, recurrence_end_date: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box' }} />
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button onClick={() => setHolidayModalOpen(false)} disabled={holSaving} style={{ padding: '8px 16px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: '0.88rem' }}>Cancel</button>
                  <button
                    disabled={submitDisabled}
                    onClick={async () => {
                      setHolSaving(true); setHolError(null)
                      try {
                        await createHoliday({ name: holForm.name.trim(), start_date: holForm.start_date, end_date: holForm.end_date, event_type: holForm.event_type, is_all_day: !isTimed, ...(isTimed ? { start_time: holForm.start_time, end_time: holForm.end_time } : {}), ...(holForm.recurrence_freq !== 'none' ? { recurrence_freq: holForm.recurrence_freq, ...(holForm.recurrence_end_date ? { recurrence_end_date: holForm.recurrence_end_date } : {}) } : {}) })
                        setHolidayModalOpen(false)
                        showCalToast('success', isSuperAdmin ? "Event created — adding to everyone's calendar" : 'Event requested for approval')
                        setHolForm({ name: '', start_date: todayKey, end_date: todayKey, event_type: 'holiday', is_all_day: true, start_time: '09:00', end_time: '12:00', recurrence_freq: 'none', recurrence_end_date: '' })
                        if (isSuperAdmin) {
                          const { gridStart, gridEnd } = calendarGridBounds(calendarMonth)
                          const from = toYmd(gridStart)
                          const to = toYmd(gridEnd)
                          fetchCalendarEvents(from, to).then(m => setLiveMeetings(m)).catch(() => {})
                        }
                        loadHolidays()
                      } catch (e: any) { setHolError(e.message || 'Failed') }
                      finally { setHolSaving(false) }
                    }}
                    style={{ padding: '8px 20px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, cursor: submitDisabled ? 'not-allowed' : 'pointer', fontSize: '0.88rem', fontWeight: 600, opacity: submitDisabled ? 0.6 : 1 }}
                  >{holSaving ? 'Saving...' : isSuperAdmin ? 'Create event' : 'Submit request'}</button>
                </div>
              </div>
            </div>
          </div>
          )
        })()}

        {/* ── Approvals inbox (approvers only) ── */}
        {approvalsOpen && isCalendarApprover && (() => {
          const pendingTO = timeOffRequests.filter(r => r.status === 'pending')
          const pendingHol = isSuperAdmin ? pendingHolidays.filter(h => h.status === 'pending') : []
          const refreshAfter = async () => { await loadTimeOff(); await loadHolidays() }
          return (
          <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setApprovalsOpen(false) }}>
            <div className="modal-card" style={{ maxWidth: 560 }} onMouseDown={e => e.stopPropagation()}>
              <div className="modal-color-bar" style={{ background: '#f59e0b' }} />
              <button className="modal-close" onClick={() => setApprovalsOpen(false)}>&times;</button>
              <div className="modal-body">
                <h3 className="modal-title" style={{ marginBottom: 16 }}>Approvals</h3>
                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>Time off ({pendingTO.length})</div>
                {pendingTO.length === 0 && <div style={{ fontSize: '0.85rem', color: '#94a3b8', marginBottom: 16 }}>No pending time-off requests.</div>}
                {pendingTO.map(r => {
                  const busy = approvalBusy === `to:${r.id}`
                  const range = r.start_date + (r.end_date !== r.start_date ? ` – ${r.end_date}` : '') + (r.segment === 'partial' && r.start_time && r.end_time ? ` · ${r.start_time}–${r.end_time}` : '')
                  return (
                    <div key={r.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1e293b' }}>{r.requester_name}</div>
                      <div style={{ fontSize: '0.82rem', color: '#475569', marginTop: 2 }}>{range}</div>
                      {r.reason && <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 4, fontStyle: 'italic' }}>{r.reason}</div>}
                      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                        <button
                          disabled={busy}
                          onClick={async () => {
                            setApprovalBusy(`to:${r.id}`)
                            try { await approveTimeOff(r.id); showCalToast('success', 'Time off approved'); await refreshAfter() }
                            catch (e: any) { showCalToast('error', e.message || 'Approve failed') }
                            finally { setApprovalBusy(null) }
                          }}
                          style={{ padding: '6px 14px', fontSize: '0.82rem', fontWeight: 600, background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1 }}
                        >{busy ? '…' : 'Approve'}</button>
                        <button
                          disabled={busy}
                          onClick={async () => {
                            const note = window.prompt('Reason for denial (optional):') ?? undefined
                            setApprovalBusy(`to:${r.id}`)
                            try { await denyTimeOff(r.id, note); showCalToast('success', 'Time off denied'); await refreshAfter() }
                            catch (e: any) { showCalToast('error', e.message || 'Deny failed') }
                            finally { setApprovalBusy(null) }
                          }}
                          style={{ padding: '6px 14px', fontSize: '0.82rem', fontWeight: 600, background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1 }}
                        >Deny</button>
                      </div>
                    </div>
                  )
                })}
                {isSuperAdmin && (
                  <>
                    <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '16px 0 8px' }}>Holiday requests ({pendingHol.length})</div>
                    {pendingHol.length === 0 && <div style={{ fontSize: '0.85rem', color: '#94a3b8' }}>No pending holiday requests.</div>}
                    {pendingHol.map(h => {
                      const busy = approvalBusy === `hol:${h.id}`
                      return (
                        <div key={h.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
                          <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1e293b', display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span>{h.name}</span>
                            {h.event_type && h.event_type !== 'holiday' && (
                              <span style={{ fontSize: '0.66rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#7c3aed', background: '#f3e8ff', borderRadius: 4, padding: '1px 6px' }}>{COMPANY_EVENT_TYPE_LABEL[h.event_type] || h.event_type}</span>
                            )}
                          </div>
                          <div style={{ fontSize: '0.82rem', color: '#475569', marginTop: 2 }}>{h.start_date}{h.end_date !== h.start_date ? ` – ${h.end_date}` : ''}</div>
                          <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: 2 }}>Requested by {h.requested_by_email}</div>
                          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                            <button
                              disabled={busy}
                              onClick={async () => {
                                setApprovalBusy(`hol:${h.id}`)
                                try { await approveHoliday(h.id); showCalToast('success', "Holiday approved — adding to everyone's calendar"); await refreshAfter() }
                                catch (e: any) { showCalToast('error', e.message || 'Approve failed') }
                                finally { setApprovalBusy(null) }
                              }}
                              style={{ padding: '6px 14px', fontSize: '0.82rem', fontWeight: 600, background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1 }}
                            >{busy ? '…' : 'Approve'}</button>
                          </div>
                        </div>
                      )
                    })}
                  </>
                )}
              </div>
            </div>
          </div>
          )
        })()}

        {/* ── Calendar-feature toast ── */}
        {calToast && (
          <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 10050, padding: '10px 18px', borderRadius: 8, fontSize: '0.88rem', fontWeight: 600, color: '#fff', boxShadow: '0 8px 24px rgba(0,0,0,0.18)', background: calToast.kind === 'error' ? '#dc2626' : '#16a34a' }}>
            {calToast.message}
          </div>
        )}

        {modalDeal && (() => {
          // Resolve the pipeline stage color so the modal's color bar +
          // stage chip reflect the actual stage, not a hard-coded orange.
          const stageInfo = allPipelineStages?.find(s => s.stageId === modalDeal.stage)
          const stageColor = stageInfo?.color || STAGE_COLORS[modalDeal.stage] || '#94a3b8'
          return (
          <div className="modal-overlay" onClick={() => setModalDeal(null)}>
            <div className="modal-card" onClick={(e) => e.stopPropagation()}>
              <div className="modal-color-bar" style={{ background: stageColor }} />
              <button
                type="button"
                className="modal-close"
                onClick={() => setModalDeal(null)}
                aria-label="Close"
              >
                &times;
              </button>
              <div className="modal-body">
                <div className="modal-tags">
                  {modalDeal.assignedTo && (
                    <span className="modal-tag" style={{ background: stageColor }}>{modalDeal.assignedTo}</span>
                  )}
                  <span
                    className="modal-tag-outline"
                    style={{ color: stageColor, borderColor: stageColor }}
                  >{modalDeal.stageLabel}</span>
                </div>
                <h3 className="modal-title">{modalDeal.title}</h3>
                <div className="modal-row">
                  <div className="modal-field">
                    <span className="modal-field-label">Stage</span>
                    <span className="modal-field-value">{modalDeal.stageLabel}</span>
                  </div>
                  <div className="modal-field">
                    <span className="modal-field-label">Opportunity</span>
                    <span className="modal-field-value">
                      {modalDeal.opportunity > 0 ? currencyFormatter.format(modalDeal.opportunity) : '—'}
                    </span>
                  </div>
                </div>
                <div className="modal-row">
                  <div className="modal-field">
                    <span className="modal-field-label">Assigned to</span>
                    <span className="modal-field-value">{modalDeal.assignedTo}</span>
                  </div>
                  <div className="modal-field">
                    <span className="modal-field-label">Est. Appointment</span>
                    <span className="modal-field-value">
                      {(() => { const a = resolveApptDateSource(modalDeal); return a ? longDateFormatter.format(new Date(a)) : '—' })()}
                    </span>
                  </div>
                </div>
                <div className="modal-row">
                  <div className="modal-field">
                    <span className="modal-field-label">Created</span>
                    <span className="modal-field-value">
                      {modalDeal.dateCreated ? formatDatePref(modalDeal.dateCreated) : '—'}
                    </span>
                  </div>
                  <div className="modal-field">
                    <span className="modal-field-label">Last modified</span>
                    <span className="modal-field-value">
                      {modalDeal.dateModified ? formatDatePref(modalDeal.dateModified) : '—'}
                    </span>
                  </div>
                </div>
                {/* Go to Deal — jumps to the full deal record page */}
                <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => {
                      const id = modalDeal.id
                      setModalDeal(null)
                      setActiveView('deals')
                      setDealDetailOpen(true)
                      setDealDetailTab('about')
                      fetchDeal(Number(id)).then(d => setSelectedDeal(d)).catch(() => {})
                      window.location.hash = `/deals/record/${id}`
                    }}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', fontSize: '0.82rem', fontWeight: 600, background: stageColor, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
                  >
                    Go to Deal
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
          )
        })()}

        {modalLead && (
          <div className="modal-overlay" onClick={() => setModalLead(null)}>
            <div className="modal-card" onClick={(e) => e.stopPropagation()}>
              <div className="modal-color-bar" style={{ background: getLeadStatusColor(modalLead.status) }} />
              <button
                type="button"
                className="modal-close"
                onClick={() => setModalLead(null)}
                aria-label="Close"
              >
                &times;
              </button>
              <div className="modal-body">
                <div className="modal-tags">
                  <span className="modal-tag" style={{ background: getLeadStatusColor(modalLead.status) }}>
                    {getLeadStatusLabel(modalLead.status, modalLead.statusLabel)}
                  </span>
                  <span className="modal-tag-outline">{modalLead.assignedTo}</span>
                </div>
                <h3 className="modal-title">
                  <EditableField value={modalLead.title} onSave={(v) => saveLeadField('title', v)} placeholder="Lead title" />
                </h3>
                {modalLead.name && <p className="modal-subtitle">
                  <EditableField value={modalLead.name} onSave={(v) => saveLeadField('name', v)} placeholder="Contact name" />
                </p>}
                <div className="modal-row">
                  <div className="modal-field">
                    <span className="modal-field-label">Phone</span>
                    <span className="modal-field-value">
                      <EditableField value={modalLead.phone || ''} onSave={(v) => saveLeadField('phone', v)} placeholder="Add phone" />
                    </span>
                  </div>
                  <div className="modal-field">
                    <span className="modal-field-label">Email</span>
                    <span className="modal-field-value">
                      <EditableField value={modalLead.email || ''} onSave={(v) => saveLeadField('email', v)} placeholder="Add email" />
                    </span>
                  </div>
                </div>
                <div className="modal-row">
                  <div className="modal-field">
                    <span className="modal-field-label">Source</span>
                    <span className="modal-field-value">{modalLead.sourceLabel || '—'}</span>
                  </div>
                  <div className="modal-field">
                    <span className="modal-field-label">Value</span>
                    <span className="modal-field-value">
                      <EditableField
                        value={modalLead.opportunity}
                        type="currency"
                        display={modalLead.opportunity > 0 ? currencyFormatter.format(modalLead.opportunity) : ''}
                        onSave={(v) => saveLeadField('opportunity', v)}
                        placeholder="$0"
                      />
                    </span>
                  </div>
                </div>
                <div className="modal-field">
                  <span className="modal-field-label">Created</span>
                  <span className="modal-field-value">{modalLead.dateCreated ? formatDatePref(modalLead.dateCreated) : '—'}</span>
                </div>

                {/* Notes section (Task 29a) */}
                <div style={{ marginTop: 16, borderTop: '1px solid #eaf0f6', paddingTop: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#33475b' }}>Notes</span>
                    {!leadNotesEditing && (
                      <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem', color: '#0091ae', fontWeight: 500 }} onClick={() => { setLeadNotesEditing(true); setLeadNotesValue(modalLead.comments || '') }}>
                        {modalLead.comments ? 'Edit' : '+ Add'}
                      </button>
                    )}
                  </div>
                  {leadNotesEditing ? (
                    <div>
                      <textarea value={leadNotesValue} onChange={e => setLeadNotesValue(e.target.value)} rows={3} style={{ width: '100%', padding: '6px 8px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.82rem', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} autoFocus placeholder="Add notes about this lead..." />
                      <div style={{ display: 'flex', gap: 6, marginTop: 4, justifyContent: 'flex-end' }}>
                        <button style={{ padding: '3px 10px', fontSize: '0.75rem', border: '1px solid #cbd6e2', borderRadius: 4, background: '#fff', cursor: 'pointer' }} onClick={() => setLeadNotesEditing(false)}>Cancel</button>
                        <button style={{ padding: '3px 10px', fontSize: '0.75rem', border: 'none', borderRadius: 4, background: '#1976d2', color: '#fff', cursor: 'pointer', fontWeight: 600 }} onClick={() => {
                          updateLead(modalLead.id, { comments: leadNotesValue }).then(updated => {
                            setModalLead(updated); setLiveLeads(prev => prev ? prev.map(l => l.id === updated.id ? updated : l) : prev)
                          }).catch(() => {})
                          setLeadNotesEditing(false)
                        }}>Save</button>
                      </div>
                    </div>
                  ) : modalLead.comments ? (
                    <div style={{ fontSize: '0.82rem', color: '#516f90', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.5, cursor: 'pointer', padding: '4px 0' }} onClick={() => { setLeadNotesEditing(true); setLeadNotesValue(modalLead.comments || '') }}>{modalLead.comments}</div>
                  ) : (
                    <div style={{ fontSize: '0.82rem', color: '#7c98b6', fontStyle: 'italic' }}>No notes yet</div>
                  )}
                </div>

                {/* Linked email threads — only renders when the backend endpoint
                    is live AND there are links. No visual noise otherwise. */}
                {modalLead?.id && (
                  <div style={{ marginTop: 12, borderTop: '1px solid #eaf0f6', paddingTop: 12 }}>
                    <LinkedEmailsSection entityType="lead" entityId={modalLead.id} hideIfEmpty />
                  </div>
                )}

                {/* Activities timeline (Task 29b)
                    Same email-thread grouping as the Contact + Deal records
                    so a long email exchange shows as one collapsible row. */}
                <div style={{ marginTop: 12, borderTop: '1px solid #eaf0f6', paddingTop: 12 }}>
                  <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#33475b', marginBottom: 8 }}>Activity Timeline</div>
                  {leadActivitiesLoading ? (
                    <div style={{ textAlign: 'center', padding: '12px 0', color: '#7c98b6', fontSize: '0.82rem' }}>Loading...</div>
                  ) : leadActivities.length === 0 ? (
                    <div style={{ fontSize: '0.82rem', color: '#7c98b6', fontStyle: 'italic' }}>No activities recorded</div>
                  ) : (
                    <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                      {groupActivitiesWithEmailThreads(leadActivities.slice(0, 25)).slice(0, 10).map((entry) => {
                        if (isEmailThread(entry) && entry.count > 1) {
                          const latest = entry.latest
                          const expanded = crpExpandedCards.has(latest.id)
                          const sender = emailSenderFromActivity(latest)
                          const snippet = emailSnippetFromActivity(latest)
                          return (
                            <div key={`thread-${entry.conversationId}`} style={{ borderBottom: '1px solid #f5f5f5', fontSize: '0.78rem' }}>
                              <div
                                style={{ display: 'flex', gap: 8, padding: '6px 0', alignItems: 'flex-start', cursor: 'pointer' }}
                                onClick={() => setCrpExpandedCards(prev => { const n = new Set(prev); n.has(latest.id) ? n.delete(latest.id) : n.add(latest.id); return n })}
                              >
                                <div style={{ width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', color: '#1565c0', flexShrink: 0, fontSize: '0.65rem', fontWeight: 700 }}>E</div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 500, color: '#33475b' }}>
                                    <span>Email thread{latest.subject ? `: ${latest.subject}` : ''}</span>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', padding: '0 6px', borderRadius: 9, background: '#f3e8ff', color: '#6d28d9', fontSize: '0.62rem', fontWeight: 700, lineHeight: '14px' }}>{entry.count} msgs</span>
                                  </div>
                                  <div style={{ color: '#7c98b6', fontSize: '0.72rem', marginTop: 1 }}>{latest.created ? formatDatePref(latest.created) : ''}</div>
                                  {!expanded && (sender || snippet) && (
                                    <div style={{ color: '#516f90', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                      {sender && <strong>{sender}: </strong>}{snippet}
                                    </div>
                                  )}
                                </div>
                              </div>
                              {expanded && (
                                <div style={{ paddingLeft: 30, paddingBottom: 6 }}>
                                  {entry.messages.map((msg) => {
                                    const dirArrow = msg.direction === 1 ? '→' : '←'
                                    const dirColor = msg.direction === 1 ? '#0a4f95' : '#7c3aed'
                                    return (
                                      <div key={msg.id} style={{ padding: '6px 0', borderTop: '1px solid #f8fafc', fontSize: '0.74rem' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, marginBottom: 2 }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                            <span style={{ display: 'inline-flex', width: 14, height: 14, borderRadius: 7, background: msg.direction === 1 ? '#e8f0fe' : '#f3e8ff', color: dirColor, alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700 }}>{dirArrow}</span>
                                            <span style={{ color: '#33475b', fontWeight: 500 }}>{emailSenderFromActivity(msg) || (msg.direction === 1 ? 'Sent' : 'Received')}</span>
                                          </div>
                                          <span style={{ color: '#94a3b8', fontSize: '0.68rem' }}>{msg.created ? new Date(msg.created).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</span>
                                        </div>
                                        <div style={{ color: '#516f90', lineHeight: 1.4 }}>{emailSnippetFromActivity(msg) || '(no preview)'}</div>
                                      </div>
                                    )
                                  })}
                                </div>
                              )}
                            </div>
                          )
                        }
                        const act = isEmailThread(entry) ? entry.latest : entry
                        const pt = (act.provider_type || '').toUpperCase()
                        const type = pt.includes('CALL') ? 'Call' : pt.includes('EMAIL') ? 'Email' : pt.includes('TASK') ? 'Task' : pt.includes('MEETING') ? 'Meeting' : 'Note'
                        const colors: Record<string, string> = { Call: '#2e7d32', Email: '#1565c0', Task: '#e65100', Meeting: '#7b1fa2', Note: '#616161' }
                        return (
                          <div key={act.id} style={{ display: 'flex', gap: 8, padding: '6px 0', borderBottom: '1px solid #f5f5f5', fontSize: '0.78rem', alignItems: 'flex-start' }}>
                            <div style={{ width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', color: colors[type], flexShrink: 0, fontSize: '0.65rem', fontWeight: 700 }}>
                              {type[0]}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 500, color: '#33475b' }}>{type}{act.subject ? `: ${act.subject}` : ''}</div>
                              <div style={{ color: '#7c98b6', fontSize: '0.72rem', marginTop: 1 }}>{act.created ? formatDatePref(act.created) : ''}</div>
                              {act.summary && <div style={{ color: '#516f90', marginTop: 2 }}>{act.summary}</div>}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '8px', marginTop: '16px', borderTop: '1px solid #eaf0f6', paddingTop: '12px' }}>
                  <button
                    style={{ padding: '6px 14px', fontSize: '13px', borderRadius: '4px', border: '1px solid #cbd6e2', background: '#fff', cursor: 'pointer' }}
                    onClick={() => setShowNoteModal({ ownerTypeId: 1, ownerId: Number(modalLead.id) })}
                  >
                    + Note
                  </button>
                  {modalLead.status !== 'CONVERTED' && (
                    <button
                      style={{ padding: '6px 14px', fontSize: '13px', borderRadius: '4px', border: 'none', background: '#ff7a59', color: '#fff', cursor: 'pointer', fontWeight: 600 }}
                      onClick={() => {
                        setLeadConvertModal({ leadId: modalLead.id, leadTitle: modalLead.title || modalLead.name || `Lead #${modalLead.id}` })
                        setLeadConvertAppt('')
                        setLeadConvertEstimator(null)
                        setLeadConvertAttendees([])
                      }}
                    >
                      Convert to Deal
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bulk disqualify modal — multi-select on Leads list */}
        {bulkDisqualifyOpen && (
          <div className="kanban-modal-overlay" onClick={() => { if (!bulkDisqualifySaving) setBulkDisqualifyOpen(false) }}>
            <div className="kanban-confirm-modal" style={{ maxWidth: 560, width: 'calc(100% - 32px)', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
              {(() => { const disqOne = leadSelectedRows.size === 1; return (<>
              <div className="kanban-confirm-header">
                <div className="kanban-confirm-title">Disqualify {leadSelectedRows.size} lead{disqOne ? '' : 's'}</div>
              </div>
              <div className="kanban-confirm-body" style={{ overflowY: 'auto', flex: 1 }}>
                <p style={{ fontSize: '0.85rem', color: '#516f90', marginBottom: 12 }}>
                  {disqOne
                    ? <>This lead will be marked as <strong>Disqualified</strong>. Add a reason as the comment.</>
                    : <>All selected leads will be marked as <strong>Disqualified</strong>. Add a reason as the comment for each lead.</>}
                </p>
                {!disqOne && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, fontSize: '0.85rem', color: '#33475b', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={bulkDisqualifySameReason}
                    onChange={e => setBulkDisqualifySameReason(e.target.checked)}
                  />
                  <span>Apply the same reason to all</span>
                </label>
                )}
                {(bulkDisqualifySameReason || disqOne) ? (
                  <textarea
                    autoFocus
                    rows={4}
                    value={bulkDisqualifyShared}
                    onChange={e => setBulkDisqualifyShared(e.target.value)}
                    placeholder={disqOne ? "Why is this lead being disqualified?" : "Why are these leads being disqualified?"}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.85rem', resize: 'vertical', fontFamily: 'inherit' }}
                  />
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {Array.from(leadSelectedRows).map(id => {
                      const lead = leadList.find((l:any) => String(l.id) === String(id)) as any
                      const title = lead?.title || lead?.name || `Lead #${id}`
                      return (
                        <div key={id} style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: 10, background: '#fafbfc' }}>
                          <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#33475b', marginBottom: 6 }}>{title}</div>
                          <textarea
                            rows={2}
                            value={bulkDisqualifyPerLead[id] || ''}
                            onChange={e => setBulkDisqualifyPerLead(prev => ({ ...prev, [id]: e.target.value }))}
                            placeholder="Reason for this lead (optional)"
                            style={{ width: '100%', padding: '6px 8px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.82rem', resize: 'vertical', fontFamily: 'inherit' }}
                          />
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
              <div className="kanban-confirm-actions">
                <button className="kanban-confirm-cancel" disabled={bulkDisqualifySaving} onClick={() => setBulkDisqualifyOpen(false)}>Cancel</button>
                <button
                  className="kanban-confirm-ok"
                  style={{ background: '#dc2626' }}
                  disabled={bulkDisqualifySaving}
                  onClick={async () => {
                    setBulkDisqualifySaving(true)
                    const ids = Array.from(leadSelectedRows)
                    const sharedReason = bulkDisqualifyShared.trim()
                    let failed = 0
                    await Promise.all(ids.map(async (id) => {
                      const reason = (bulkDisqualifySameReason || disqOne) ? sharedReason : (bulkDisqualifyPerLead[id] || '').trim()
                      const updates: Record<string, unknown> = { statusId: 'JUNK', statusLabel: 'Disqualified' }
                      if (reason) updates.comments = reason
                      try {
                        await updateLead(String(id), updates)
                      } catch (e) {
                        console.error('Bulk disqualify failed for lead', id, e)
                        failed++
                      }
                    }))
                    setLeadList(prev => prev.map((l:any) => leadSelectedRows.has(String(l.id)) ? { ...l, status: 'JUNK', statusLabel: 'Disqualified' } : l))
                    setLeadSelectedRows(new Set())
                    setBulkDisqualifyOpen(false)
                    setBulkDisqualifySaving(false)
                    if (failed > 0) alert(`Disqualified ${ids.length - failed} lead(s). ${failed} failed — see console.`)
                  }}
                >
                  {bulkDisqualifySaving ? 'Saving…' : `Disqualify ${leadSelectedRows.size}`}
                </button>
              </div>
              </>) })()}
            </div>
          </div>
        )}

        {/* Lead Conversion + Disqualify modals — extracted to src/views/leads/Modals.tsx (R8) */}
        {(leadConvertModal || disqualifyModal) && (
          <Suspense fallback={null}>
            <LeadModals
              leadConvertModal={leadConvertModal}
              setLeadConvertModal={setLeadConvertModal}
              leadConvertAppt={leadConvertAppt}
              setLeadConvertAppt={setLeadConvertAppt}
              leadConvertEstimator={leadConvertEstimator}
              setLeadConvertEstimator={setLeadConvertEstimator}
              leadConvertAttendees={leadConvertAttendees}
              setLeadConvertAttendees={setLeadConvertAttendees}
              disqualifyModal={disqualifyModal}
              setDisqualifyModal={setDisqualifyModal}
              disqualifyComment={disqualifyComment}
              setDisqualifyComment={setDisqualifyComment}
              disqualifySaving={disqualifySaving}
              setDisqualifySaving={setDisqualifySaving}
              convertLead={convertLead}
              fetchLeads={fetchLeads}
              refetchCurrentDeals={refetchCurrentDeals}
              updateLead={updateLead}
              setModalLead={setModalLead}
              setLiveLeads={setLiveLeads}
              setLeadTotal={setLeadTotal}
              setLiveDeals={setLiveDeals}
              setDealTotal={setDealTotal}
              setDealPages={setDealPages}
              setSelectedDeal={setSelectedDeal}
              setDealDetailOpen={setDealDetailOpen}
              setActiveView={setActiveView}
              setLeadList={setLeadList}
              AvailabilityDateTimePicker={AvailabilityDateTimePicker}
              estimators={ESTIMATOR_USERS.map(u => ({ email: CALENDAR_USERS_RAW.find(r => r.userId === u.userId)?.email ?? '', userId: u.userId, name: u.name }))}
              estimatorIds={ESTIMATOR_IDS}
            />
          </Suspense>
        )}


        {modalContact && (
          <div className="modal-overlay" onClick={() => setModalContact(null)}>
            <div className="modal-card" onClick={(e) => e.stopPropagation()}>
              <div className="modal-color-bar" style={{ background: '#1976d2' }} />
              <button
                type="button"
                className="modal-close"
                onClick={() => setModalContact(null)}
                aria-label="Close"
              >
                &times;
              </button>
              <div className="modal-body">
                <div className="modal-tags">
                  <span className="modal-tag" style={{ background: '#1976d2' }}>Contact #{modalContact.id}</span>
                  <span className="modal-tag-outline">{modalContact.assignedTo}</span>
                </div>
                <h3 className="modal-title">{modalContact.name}</h3>
                <div className="modal-row">
                  <div className="modal-field">
                    <span className="modal-field-label">Phone</span>
                    <span className="modal-field-value">{modalContact.phone || '—'}</span>
                  </div>
                  <div className="modal-field">
                    <span className="modal-field-label">Email</span>
                    <span className="modal-field-value">{modalContact.email || '—'}</span>
                  </div>
                </div>
                <div className="modal-field">
                  <span className="modal-field-label">Assigned to</span>
                  <span className="modal-field-value">{modalContact.assignedTo}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========== Snippet Modal ========== */}
        {showSnippetModal && (
          <div className="modal-overlay" onClick={() => setShowSnippetModal(null)}>
            <div className="modal-card" style={{ maxWidth: '540px' }} onClick={e => e.stopPropagation()}>
              <div className="modal-color-bar" style={{ background: '#1976d2' }} />
              <button type="button" className="modal-close" onClick={() => setShowSnippetModal(null)} aria-label="Close">&times;</button>
              <div className="modal-body">
                <h3 className="modal-title" style={{ marginBottom: '8px' }}>{showSnippetModal === 'create' ? 'New snippet' : 'Edit snippet'}</h3>
                <p style={{ fontSize: '0.78rem', color: '#7c98b6', marginBottom: '16px' }}>Snippets allow you to create reusable blocks of text that you can access quickly through keyboard shortcuts.</p>
                {snippetError && <div style={{ padding: '8px 12px', background: '#fdedee', color: '#d94c53', borderRadius: '4px', fontSize: '13px', marginBottom: '12px' }}>{snippetError}</div>}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <label style={{ fontSize: '13px', color: '#33475b' }}>Internal name *<input type="text" value={snippetForm.name} onChange={e => setSnippetForm(p => ({ ...p, name: e.target.value }))} placeholder="Give your snippet a name" style={{ display: 'block', width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', marginTop: '4px', fontSize: '14px' }} autoFocus /></label>
                  <label style={{ fontSize: '13px', color: '#33475b' }}>Snippet text *<textarea value={snippetForm.content} onChange={e => setSnippetForm(p => ({ ...p, content: e.target.value }))} placeholder="Add the content of your snippet" style={{ display: 'block', width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', marginTop: '4px', fontSize: '14px', minHeight: '100px', resize: 'vertical' }} /></label>
                  <label style={{ fontSize: '13px', color: '#33475b' }}>Shortcut *<div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}><span style={{ fontFamily: 'monospace', fontSize: '16px', color: '#1976d2', fontWeight: 700 }}>#</span><input type="text" value={snippetForm.shortcut} onChange={e => setSnippetForm(p => ({ ...p, shortcut: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') }))} placeholder="Type a word to use as a shortcut" style={{ flex: 1, padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', fontSize: '14px' }} /></div><span style={{ fontSize: '0.72rem', color: '#7c98b6', marginTop: '4px', display: 'block' }}>To use a snippet, type the # symbol followed by the shortcut above.</span></label>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
                  <button onClick={() => setShowSnippetModal(null)} style={{ padding: '8px 16px', borderRadius: '4px', border: '1px solid #cbd6e2', background: '#fff', cursor: 'pointer', fontSize: '14px' }}>Cancel</button>
                  <button disabled={snippetSaving || !snippetForm.name.trim() || !snippetForm.content.trim() || !snippetForm.shortcut.trim()} onClick={async () => { setSnippetSaving(true); setSnippetError(null); try { if (showSnippetModal === 'edit' && editingSnippet) { await updateSnippet(editingSnippet.id, snippetForm) } else { await createSnippet(snippetForm) } setShowSnippetModal(null); loadSnippets() } catch (err: any) { setSnippetError(err.message) } finally { setSnippetSaving(false) } }} style={{ padding: '8px 16px', borderRadius: '4px', border: 'none', background: '#ff7a59', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: 600, opacity: (snippetSaving || !snippetForm.name.trim() || !snippetForm.content.trim() || !snippetForm.shortcut.trim()) ? 0.6 : 1 }}>{snippetSaving ? 'Saving...' : 'Save snippet'}</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========== Template Modal ========== */}
        {showTemplateModal && (
          <div className="modal-overlay" onClick={() => setShowTemplateModal(null)}>
            <div className="modal-card" style={{ maxWidth: '680px' }} onClick={e => e.stopPropagation()}>
              <div className="modal-color-bar" style={{ background: '#1976d2' }} />
              <button type="button" className="modal-close" onClick={() => setShowTemplateModal(null)} aria-label="Close">&times;</button>
              <div className="modal-body">
                <h3 className="modal-title" style={{ marginBottom: '16px' }}>{showTemplateModal === 'create' ? 'New template' : 'Edit template'}</h3>
                {templateError && <div style={{ padding: '8px 12px', background: '#fdedee', color: '#d94c53', borderRadius: '4px', fontSize: '13px', marginBottom: '12px' }}>{templateError}</div>}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <label style={{ fontSize: '13px', color: '#33475b' }}>Template name *<input type="text" value={templateForm.name} onChange={e => setTemplateForm(p => ({ ...p, name: e.target.value }))} style={{ display: 'block', width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', marginTop: '4px', fontSize: '14px' }} autoFocus /></label>
                    <label style={{ fontSize: '13px', color: '#33475b' }}>Category<select value={templateForm.category} onChange={e => setTemplateForm(p => ({ ...p, category: e.target.value }))} style={{ display: 'block', width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', marginTop: '4px', fontSize: '14px' }}><option value="email">Email</option><option value="sms">SMS</option><option value="note">Note</option></select></label>
                  </div>
                  <label style={{ fontSize: '13px', color: '#33475b' }}>Subject<input type="text" value={templateForm.subject} onChange={e => setTemplateForm(p => ({ ...p, subject: e.target.value }))} placeholder="Email subject line" style={{ display: 'block', width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', marginTop: '4px', fontSize: '14px' }} /></label>
                  <label style={{ fontSize: '13px', color: '#33475b' }}>Body *<textarea value={templateForm.body} onChange={e => setTemplateForm(p => ({ ...p, body: e.target.value }))} placeholder="Template body content" style={{ display: 'block', width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', marginTop: '4px', fontSize: '14px', minHeight: '200px', resize: 'vertical' }} /></label>
                  <div style={{ fontSize: '0.72rem', color: '#7c98b6', background: '#f5f8fa', padding: '8px 12px', borderRadius: '4px' }}>Available tokens: {'{{contact.name}}'}, {'{{contact.email}}'}, {'{{contact.phone}}'}, {'{{deal.title}}'}, {'{{deal.stage}}'}, {'{{deal.amount}}'}, {'{{owner.name}}'}, {'{{today}}'}</div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
                  <button onClick={() => setShowTemplateModal(null)} style={{ padding: '8px 16px', borderRadius: '4px', border: '1px solid #cbd6e2', background: '#fff', cursor: 'pointer', fontSize: '14px' }}>Cancel</button>
                  <button disabled={templateSaving || !templateForm.name.trim() || !templateForm.body.trim()} onClick={async () => { setTemplateSaving(true); setTemplateError(null); try { if (showTemplateModal === 'edit' && editingTemplate) { await updateTemplate(editingTemplate.id, templateForm) } else { await createTemplate(templateForm) } setShowTemplateModal(null); loadTemplates() } catch (err: any) { setTemplateError(err.message) } finally { setTemplateSaving(false) } }} style={{ padding: '8px 16px', borderRadius: '4px', border: 'none', background: '#ff7a59', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: 600, opacity: (templateSaving || !templateForm.name.trim() || !templateForm.body.trim()) ? 0.6 : 1 }}>{templateSaving ? 'Saving...' : 'Save template'}</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========== Playbook Create Modal ========== */}
        {showPlaybookModal && (
          <div className="modal-overlay" onClick={() => setShowPlaybookModal(null)}>
            <div className="modal-card" style={{ maxWidth: '500px' }} onClick={e => e.stopPropagation()}>
              <div className="modal-color-bar" style={{ background: '#8b5cf6' }} />
              <button type="button" className="modal-close" onClick={() => setShowPlaybookModal(null)} aria-label="Close">&times;</button>
              <div className="modal-body">
                <h3 className="modal-title" style={{ marginBottom: '16px' }}>Create playbook</h3>
                {playbookError && <div style={{ padding: '8px 12px', background: '#fdedee', color: '#d94c53', borderRadius: '4px', fontSize: '13px', marginBottom: '12px' }}>{playbookError}</div>}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <label style={{ fontSize: '13px', color: '#33475b' }}>Playbook title *<input type="text" value={playbookForm.title} onChange={e => setPlaybookForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Discovery Call Playbook" style={{ display: 'block', width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', marginTop: '4px', fontSize: '14px' }} autoFocus /></label>
                  <label style={{ fontSize: '13px', color: '#33475b' }}>Engagement type<select value={playbookForm.engagement_type} onChange={e => setPlaybookForm(p => ({ ...p, engagement_type: e.target.value }))} style={{ display: 'block', width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', marginTop: '4px', fontSize: '14px' }}><option value="">None</option><option value="call">Call</option><option value="email">Email</option><option value="meeting">Meeting</option><option value="note">Note</option></select></label>
                  <label style={{ fontSize: '13px', color: '#33475b' }}>Initial content<textarea value={playbookForm.content} onChange={e => setPlaybookForm(p => ({ ...p, content: e.target.value }))} placeholder="Add instructions, talking points, or checklists..." style={{ display: 'block', width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', marginTop: '4px', fontSize: '14px', minHeight: '100px', resize: 'vertical' }} /></label>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
                  <button onClick={() => setShowPlaybookModal(null)} style={{ padding: '8px 16px', borderRadius: '4px', border: '1px solid #cbd6e2', background: '#fff', cursor: 'pointer', fontSize: '14px' }}>Cancel</button>
                  <button disabled={playbookSaving || !playbookForm.title.trim()} onClick={async () => { setPlaybookSaving(true); setPlaybookError(null); try { const pb = await createPlaybook({ title: playbookForm.title, content: playbookForm.content || '', engagement_type: playbookForm.engagement_type || null }); setShowPlaybookModal(null); const detail = await fetchPlaybook(pb.id); setPlaybookDetail(detail); setPlaybookDetailOpen(true); loadPlaybooks() } catch (err: any) { setPlaybookError(err.message) } finally { setPlaybookSaving(false) } }} style={{ padding: '8px 16px', borderRadius: '4px', border: 'none', background: '#ff7a59', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: 600, opacity: (playbookSaving || !playbookForm.title.trim()) ? 0.6 : 1 }}>{playbookSaving ? 'Creating...' : 'Create playbook'}</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========== Project Create Panel ========== */}
        {showProjectPanel && (<>
          <div className="create-panel-overlay" onClick={() => setShowProjectPanel(false)} />
          <div className="create-panel" style={{ zIndex: 201 }}>
            <div className="create-panel-header">
              <h2>Create Project</h2>
              <button className="create-panel-close" onClick={() => setShowProjectPanel(false)} aria-label="Close">
                <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"/><path d="M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"/></svg>
              </button>
            </div>
            <div className="create-panel-body">
              {projectError && <div style={{ padding: '8px 12px', background: '#fdedee', color: '#d94c53', borderRadius: '4px', fontSize: '13px', marginBottom: '12px' }}>{projectError}</div>}
              <div className="create-panel-field">
                <label>Project Name *</label>
                <input type="text" value={projectForm.name || ''} onChange={e => setProjectForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. 123 Main St - Interior Painting" autoFocus />
              </div>
              <div className="create-panel-field">
                <label>Contact</label>
                <input type="text" value={projectForm.client_name || ''} onChange={e => setProjectForm(p => ({ ...p, client_name: e.target.value }))} placeholder="Contact name" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="create-panel-field">
                  <label>Status</label>
                  <select value={projectForm.status || 'bidding'} onChange={e => setProjectForm(p => ({ ...p, status: e.target.value }))}>
                    <option value="bidding">Bidding</option>
                    <option value="active">Active</option>
                    <option value="on_hold">On Hold</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div className="create-panel-field">
                  <label>Progress (%)</label>
                  <input type="number" min="0" max="100" value={projectForm.progress || 0} onChange={e => setProjectForm(p => ({ ...p, progress: parseInt(e.target.value) || 0 }))} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="create-panel-field">
                  <label>Start Date</label>
                  {/* CAPABILITY: set_actual_schedule — only super_admin/admin/office can pick the actual scheduled start. */}
                  <input
                    type="date"
                    value={projectForm.start_date || ''}
                    disabled={myCapabilities ? !myCapabilities.set_actual_schedule : false}
                    title={myCapabilities && !myCapabilities.set_actual_schedule ? `Requires admin or office (your role: ${myRole || 'unknown'})` : ''}
                    onChange={e => setProjectForm(p => ({ ...p, start_date: e.target.value }))}
                  />
                </div>
                <div className="create-panel-field">
                  <label>End Date</label>
                  {/* CAPABILITY: set_actual_schedule */}
                  <input
                    type="date"
                    value={projectForm.end_date || ''}
                    disabled={myCapabilities ? !myCapabilities.set_actual_schedule : false}
                    title={myCapabilities && !myCapabilities.set_actual_schedule ? `Requires admin or office (your role: ${myRole || 'unknown'})` : ''}
                    onChange={e => setProjectForm(p => ({ ...p, end_date: e.target.value }))}
                  />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="create-panel-field">
                  <label>Value</label>
                  <input type="number" step="0.01" value={projectForm.value || ''} onChange={e => setProjectForm(p => ({ ...p, value: parseFloat(e.target.value) || 0 }))} placeholder="0.00" />
                </div>
                <div className="create-panel-field">
                  <label>Assigned To</label>
                  <input type="text" value={projectForm.assigned_to || ''} onChange={e => setProjectForm(p => ({ ...p, assigned_to: e.target.value }))} placeholder="e.g. Max Ritchie" />
                </div>
              </div>
              <div className="create-panel-field">
                <label>Address</label>
                <input type="text" value={projectForm.address || ''} onChange={e => setProjectForm(p => ({ ...p, address: e.target.value }))} placeholder="Project address" />
              </div>
              <div className="create-panel-field">
                <label>Description</label>
                <textarea rows={3} value={projectForm.description || ''} onChange={e => setProjectForm(p => ({ ...p, description: e.target.value }))} placeholder="Project notes and description..." style={{ resize: 'vertical' }} />
              </div>
            </div>
            <div className="create-panel-footer">
              <button className="cp-btn-primary" disabled={projectSaving || !(projectForm.name || '').trim()} onClick={async () => {
                setProjectSaving(true); setProjectError(null)
                try {
                  await createCrmProject(projectForm)
                  setShowProjectPanel(false)
                  loadProjects()
                } catch (err: any) { setProjectError(err.message) } finally { setProjectSaving(false) }
              }}>{projectSaving ? 'Creating...' : 'Create project'}</button>
              <button className="cp-btn-secondary" onClick={() => setShowProjectPanel(false)}>Cancel</button>
            </div>
          </div>
        </>)}

        {/* ========== Order Create Panel ========== */}
        {showOrderPanel && (<>
          <div className="create-panel-overlay" onClick={() => setShowOrderPanel(false)} />
          <div className="create-panel" style={{ zIndex: 201 }}>
            <div className="create-panel-header">
              <h3 style={{ fontSize: '0.92rem', fontWeight: 600, color: '#33475b', margin: 0 }}>Create order</h3>
              <button type="button" onClick={() => setShowOrderPanel(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#7c98b6' }}>&times;</button>
            </div>
            <div className="create-panel-body">
              {orderError && <div style={{ padding: '8px 12px', background: '#fdedee', color: '#d94c53', borderRadius: '4px', fontSize: '13px' }}>{orderError}</div>}
              <label style={{ fontSize: '13px', color: '#33475b' }}>Order Title *<input type="text" value={orderForm.title || ''} onChange={e => setOrderForm((p: any) => ({ ...p, title: e.target.value }))} placeholder="e.g. Interior Painting" style={{ display: 'block', width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', marginTop: '4px', fontSize: '14px' }} autoFocus /></label>
              <label style={{ fontSize: '13px', color: '#33475b' }}>Contact ID<input type="text" value={orderForm.unified_client_id || ''} onChange={e => setOrderForm((p: any) => ({ ...p, unified_client_id: e.target.value }))} placeholder="Unified contact ID" style={{ display: 'block', width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', marginTop: '4px', fontSize: '14px' }} /></label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <label style={{ fontSize: '13px', color: '#33475b' }}>Status<select value={orderForm.status || 'draft'} onChange={e => setOrderForm((p: any) => ({ ...p, status: e.target.value }))} style={{ display: 'block', width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', marginTop: '4px', fontSize: '14px' }}><option value="draft">Draft</option><option value="sent">Sent</option><option value="approved">Approved</option><option value="in_progress">In Progress</option><option value="completed">Completed</option><option value="invoiced">Invoiced</option><option value="paid">Paid</option><option value="cancelled">Cancelled</option></select></label>
                <label style={{ fontSize: '13px', color: '#33475b' }}>Deal ID<input type="number" value={orderForm.deal_id || ''} onChange={e => setOrderForm((p: any) => ({ ...p, deal_id: e.target.value ? Number(e.target.value) : null }))} placeholder="Link to deal" style={{ display: 'block', width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', marginTop: '4px', fontSize: '14px' }} /></label>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <label style={{ fontSize: '13px', color: '#33475b' }}>Start Date<input type="date" value={orderForm.start_date || ''} onChange={e => setOrderForm((p: any) => ({ ...p, start_date: e.target.value }))} style={{ display: 'block', width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', marginTop: '4px', fontSize: '14px' }} /></label>
                <label style={{ fontSize: '13px', color: '#33475b' }}>End Date<input type="date" value={orderForm.end_date || ''} onChange={e => setOrderForm((p: any) => ({ ...p, end_date: e.target.value }))} style={{ display: 'block', width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', marginTop: '4px', fontSize: '14px' }} /></label>
              </div>
              <label style={{ fontSize: '13px', color: '#33475b' }}>Notes<textarea value={orderForm.notes || ''} onChange={e => setOrderForm((p: any) => ({ ...p, notes: e.target.value }))} style={{ display: 'block', width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: '4px', marginTop: '4px', fontSize: '14px', minHeight: '60px', resize: 'vertical' }} /></label>
              <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#33475b' }}>Line Items</span>
                  <button onClick={() => setOrderLineItems(prev => [...prev, { description: '', quantity: 1, unit_price: 0 }])} style={{ background: 'none', border: 'none', color: '#1976d2', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer' }}>+ Add line item</button>
                </div>
                {orderLineItems.map((li, idx) => (<div key={idx} className="order-line-item-row"><input type="text" placeholder="Description" value={li.description} onChange={e => { const items = [...orderLineItems]; items[idx] = { ...items[idx], description: e.target.value }; setOrderLineItems(items) }} style={{ flex: 1, padding: '6px 8px', border: '1px solid #cbd6e2', borderRadius: '4px', fontSize: '13px' }} /><input type="number" placeholder="Qty" value={li.quantity} onChange={e => { const items = [...orderLineItems]; items[idx] = { ...items[idx], quantity: Number(e.target.value) || 0 }; setOrderLineItems(items) }} style={{ width: '60px', padding: '6px 8px', border: '1px solid #cbd6e2', borderRadius: '4px', fontSize: '13px', textAlign: 'right' }} /><input type="number" placeholder="Price" value={li.unit_price} onChange={e => { const items = [...orderLineItems]; items[idx] = { ...items[idx], unit_price: Number(e.target.value) || 0 }; setOrderLineItems(items) }} step="0.01" style={{ width: '90px', padding: '6px 8px', border: '1px solid #cbd6e2', borderRadius: '4px', fontSize: '13px', textAlign: 'right' }} /><span style={{ fontSize: '0.82rem', fontWeight: 500, color: '#33475b', minWidth: '70px', textAlign: 'right' }}>{currencyFormatter.format(li.quantity * li.unit_price)}</span><button onClick={() => setOrderLineItems(prev => prev.filter((_, i) => i !== idx))} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '16px' }}>&times;</button></div>))}
                {orderLineItems.length > 0 && (<div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 0', borderTop: '1px solid #e2e8f0', marginTop: '8px' }}><span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#33475b' }}>Total: {currencyFormatter.format(orderLineItems.reduce((sum, li) => sum + li.quantity * li.unit_price, 0))}</span></div>)}
              </div>
            </div>
            <div className="create-panel-footer">
              <button className="cp-btn-primary" disabled={orderSaving || !(orderForm.title || '').trim()} onClick={async () => { setOrderSaving(true); setOrderError(null); try { const subtotal = orderLineItems.reduce((sum, li) => sum + li.quantity * li.unit_price, 0); await createOrder({ title: orderForm.title, unified_client_id: orderForm.unified_client_id || null, deal_id: orderForm.deal_id || null, status: orderForm.status || 'draft', start_date: orderForm.start_date || null, end_date: orderForm.end_date || null, notes: orderForm.notes || null, subtotal, total: subtotal, line_items: orderLineItems.filter(li => li.description.trim()) }); setShowOrderPanel(false); loadOrders() } catch (err: any) { setOrderError(err.message) } finally { setOrderSaving(false) } }}>{orderSaving ? 'Creating...' : 'Create'}</button>
              <button className="cp-btn-secondary" onClick={() => setShowOrderPanel(false)}>Cancel</button>
            </div>
          </div>
        </>)}

        {/* ========== ACCOUNTING MODULE ========== */}
        {activeView === 'accounting' && (
          <Suspense fallback={<div className="view-loading" />}>
            <Accounting
              accountingSubView={accountingSubView} debounceAcctSearch={debounceAcctSearch} effectiveCanonicalRole={effectiveCanonicalRole} formatDatePref={formatDatePref}
              isNavHidden={isNavHidden} objSwitcherOpen={objSwitcherOpen} objSwitcherRef={objSwitcherRef} objSwitcherSearch={objSwitcherSearch} quickFiltersOpen={quickFiltersOpen}
              setAccountingSubView={setAccountingSubView} setActiveView={setActiveView} setCompanyAccountingRoleFilter={setCompanyAccountingRoleFilter} setCompanyPage={setCompanyPage}
              setCompanySearch={setCompanySearch} setObjSwitcherOpen={setObjSwitcherOpen} setObjSwitcherSearch={setObjSwitcherSearch}
              setQuickFiltersOpen={setQuickFiltersOpen} setTablePageSize={setTablePageSize} setTableRowHeight={setTableRowHeight} setTableSettingsOpen={setTableSettingsOpen}
              setTableZebraStriping={setTableZebraStriping} tablePageSize={tablePageSize} tableRowHeight={tableRowHeight} tableSettingsOpen={tableSettingsOpen}
              tableZebraStriping={tableZebraStriping}
              acctAccounts={acctAccounts} acctAccountsLoading={acctAccountsLoading} acctCreateOpen={acctCreateOpen} acctEditItem={acctEditItem} acctPage={acctPage}
              acctQuickFiltersOpen={acctQuickFiltersOpen} acctSearch={acctSearch} acctSelectedRows={acctSelectedRows} acctSettingsOpen={acctSettingsOpen} acctSort={acctSort}
              acctSummary={acctSummary} acctTypeFilter={acctTypeFilter} setAcctAccounts={setAcctAccounts} setAcctAccountsLoading={setAcctAccountsLoading}
              setAcctCreateOpen={setAcctCreateOpen} setAcctEditItem={setAcctEditItem} setAcctPage={setAcctPage} setAcctQuickFiltersOpen={setAcctQuickFiltersOpen}
              setAcctSearch={setAcctSearch} setAcctSelectedRows={setAcctSelectedRows} setAcctSettingsOpen={setAcctSettingsOpen} setAcctSort={setAcctSort} setAcctSummary={setAcctSummary}
              setAcctTypeFilter={setAcctTypeFilter}
              jeCreateOpen={jeCreateOpen} jeDetailId={jeDetailId} jeEntries={jeEntries} jeLoading={jeLoading} jePage={jePage} jeSearch={jeSearch} jeSelectedRows={jeSelectedRows} jeSort={jeSort}
              jeStatusFilter={jeStatusFilter} ledgerTarget={ledgerTarget} setJeCreateOpen={setJeCreateOpen} setJeDetailId={setJeDetailId} setJeEntries={setJeEntries} setJeLoading={setJeLoading} setJePage={setJePage} setJeSearch={setJeSearch}
              setJeSelectedRows={setJeSelectedRows} setJeSort={setJeSort} setJeStatusFilter={setJeStatusFilter} setLedgerTarget={setLedgerTarget}
              apBills={apBills} apBillSort={apBillSort}
              apBillVisibleCols={apBillVisibleCols} apCreateOpen={apCreateOpen} apCreateType={apCreateType} apLoading={apLoading} apPage={apPage} apScannerVisibleCols={apScannerVisibleCols}
              apSearch={apSearch} apSelectedRows={apSelectedRows} apStatusFilter={apStatusFilter} apSummary={apSummary} apTab={apTab} apVendors={apVendors} apVendorSort={apVendorSort}
              apVendorVisibleCols={apVendorVisibleCols} apEditColsDraft={apEditColsDraft} apEditColsDragIdx={apEditColsDragIdx} apEditColsDragOverIdx={apEditColsDragOverIdx} apEditColsSearch={apEditColsSearch} apEditColumnsOpen={apEditColumnsOpen} setApBillVisibleCols={setApBillVisibleCols} setApEditColsDragIdx={setApEditColsDragIdx} setApEditColsDragOverIdx={setApEditColsDragOverIdx} setApScannerVisibleCols={setApScannerVisibleCols} setApVendorVisibleCols={setApVendorVisibleCols} setApBills={setApBills} setApBillSort={setApBillSort} setApCreateOpen={setApCreateOpen} setApCreateType={setApCreateType}
              setApEditColsDraft={setApEditColsDraft} setApEditColsSearch={setApEditColsSearch} setApEditColumnsOpen={setApEditColumnsOpen} setApLoading={setApLoading} setApPage={setApPage}
              setApSearch={setApSearch} setApSelectedRows={setApSelectedRows} setApStatusFilter={setApStatusFilter} setApSummary={setApSummary} setApTab={setApTab}
              setApVendors={setApVendors} setApVendorSort={setApVendorSort}
              scanApproving={scanApproving} scanBatchApproving={scanBatchApproving} scanCreatingVendor={scanCreatingVendor} scanDateOpen={scanDateOpen} scanDateRange={scanDateRange}
              scanDetailItem={scanDetailItem} scanDetailOpen={scanDetailOpen} scanDirty={scanDirty} scanDivisionFilters={scanDivisionFilters} scanDivisionPickerOpen={scanDivisionPickerOpen}
              scanJobFilters={scanJobFilters} scanJobPickerOpen={scanJobPickerOpen} scanJobPickerQuery={scanJobPickerQuery} scanLeftRef={scanLeftRef} scanLinkContact={scanLinkContact}
              scanLinkContactQuery={scanLinkContactQuery} scanLinkContactResults={scanLinkContactResults} scanLinkDeal={scanLinkDeal} scanLinkDealQuery={scanLinkDealQuery}
              scanLinkDealResults={scanLinkDealResults} scanLinkProjectQuery={scanLinkProjectQuery} scanLoading={scanLoading} scanOriginal={scanOriginal} scanPdfPreview={scanPdfPreview}
              scanPickerProjects={scanPickerProjects} scanReloadQueue={scanReloadQueue} scanResizeCleanup={scanResizeCleanup} scanRevertNonce={scanRevertNonce}
              scanSaving={scanSaving} scanSearch={scanSearch} scanShellRef={scanShellRef} scanSort={scanSort} scanSplitPct={scanSplitPct} scanStatusFilters={scanStatusFilters}
              scanStatusOpen={scanStatusOpen} scanVendorEdit={scanVendorEdit} scanVendorFilters={scanVendorFilters} scanVendorManageOpen={scanVendorManageOpen}
              scanVendorManageSearch={scanVendorManageSearch} scanVendorPickerOpen={scanVendorPickerOpen} scanVendorPickerQuery={scanVendorPickerQuery} scanVendorSaving={scanVendorSaving}
              setScanApproving={setScanApproving} setScanAutoSelectKey={setScanAutoSelectKey} setScanBatchApproving={setScanBatchApproving} setScanCreatingVendor={setScanCreatingVendor}
              setScanDateOpen={setScanDateOpen} setScanDateRange={setScanDateRange} setScanDetailItem={setScanDetailItem} setScanDetailOpen={setScanDetailOpen} setScanDirty={setScanDirty}
              setScanDivisionFilters={setScanDivisionFilters} setScanDivisionPickerOpen={setScanDivisionPickerOpen} setScanIncludeApproved={setScanIncludeApproved}
              setScanIncludeRejected={setScanIncludeRejected} setScanJobFilters={setScanJobFilters} setScanJobPickerOpen={setScanJobPickerOpen} setScanJobPickerQuery={setScanJobPickerQuery}
              setScanLinkContact={setScanLinkContact} setScanLinkContactQuery={setScanLinkContactQuery} setScanLinkContactResults={setScanLinkContactResults}
              setScanLinkDeal={setScanLinkDeal} setScanLinkDealQuery={setScanLinkDealQuery} setScanLinkDealResults={setScanLinkDealResults} setScanLinkProjectQuery={setScanLinkProjectQuery}
              setScanOriginal={setScanOriginal} setScanPdfPreview={setScanPdfPreview} setScanRevertNonce={setScanRevertNonce} setScanSaving={setScanSaving}
              setScanSearch={setScanSearch} setScanSort={setScanSort} setScanSplitPct={setScanSplitPct} setScanStatusFilters={setScanStatusFilters} setScanStatusOpen={setScanStatusOpen}
              setScanSummary={setScanSummary} setScanVendorEdit={setScanVendorEdit} setScanVendorFilters={setScanVendorFilters} setScanVendorManageOpen={setScanVendorManageOpen}
              setScanVendorManageSearch={setScanVendorManageSearch} setScanVendorPickerOpen={setScanVendorPickerOpen} setScanVendorPickerQuery={setScanVendorPickerQuery}
              setScanVendorSaving={setScanVendorSaving}
              arCreateOpen={arCreateOpen} arInvoices={arInvoices} arLoading={arLoading} arNewLines={arNewLines} arPage={arPage} arSearch={arSearch} arSelectedRows={arSelectedRows}
              arSort={arSort} arStatusFilter={arStatusFilter} arSummary={arSummary} setArCreateOpen={setArCreateOpen} setArInvoices={setArInvoices} setArLoading={setArLoading}
              setArNewLines={setArNewLines} setArPage={setArPage} setArSearch={setArSearch} setArSelectedRows={setArSelectedRows} setArSort={setArSort} setArStatusFilter={setArStatusFilter}
              setArSummary={setArSummary}
              bankAccounts={bankAccounts} bankClearedIds={bankClearedIds} bankFilterOpen={bankFilterOpen} bankPage={bankPage} bankReconAccount={bankReconAccount} bankReconId={bankReconId}
              bankSearch={bankSearch} bankSelectedRows={bankSelectedRows} bankSettingsOpen={bankSettingsOpen} bankStmtBalance={bankStmtBalance} bankStmtDate={bankStmtDate}
              bankTxns={bankTxns} setBankAccounts={setBankAccounts} setBankClearedIds={setBankClearedIds} setBankFilterOpen={setBankFilterOpen} setBankPage={setBankPage}
              setBankReconAccount={setBankReconAccount} setBankReconId={setBankReconId} setBankSearch={setBankSearch} setBankSelectedRows={setBankSelectedRows}
              setBankSettingsOpen={setBankSettingsOpen} setBankStmtBalance={setBankStmtBalance} setBankStmtDate={setBankStmtDate} setBankTxns={setBankTxns}
              billingChangeForm={billingChangeForm} billingChangeModalOpen={billingChangeModalOpen} billingChangeSaving={billingChangeSaving} billingOrgResults={billingOrgResults}
              billingOrgSearch={billingOrgSearch} jcCreateOpen={jcCreateOpen} jcFilterOpen={jcFilterOpen} jcJobs={jcJobs} jcLoading={jcLoading} jcPage={jcPage} jcSearch={jcSearch}
              jcSelectedJob={jcSelectedJob} jcSelectedRows={jcSelectedRows} jcSettingsOpen={jcSettingsOpen} jcSort={jcSort} jcStatusFilter={jcStatusFilter}
              jobBillingPeriods={jobBillingPeriods} setBillingChangeForm={setBillingChangeForm} setBillingChangeModalOpen={setBillingChangeModalOpen}
              setBillingChangeSaving={setBillingChangeSaving} setBillingOrgResults={setBillingOrgResults} setBillingOrgSearch={setBillingOrgSearch} setJcCreateOpen={setJcCreateOpen}
              setJcFilterOpen={setJcFilterOpen} setJcJobs={setJcJobs} setJcLoading={setJcLoading} setJcPage={setJcPage} setJcSearch={setJcSearch} setJcSelectedJob={setJcSelectedJob}
              setJcSelectedRows={setJcSelectedRows} setJcSettingsOpen={setJcSettingsOpen} setJcSort={setJcSort} setJcStatusFilter={setJcStatusFilter}
              setJobBillingPeriods={setJobBillingPeriods}
              setWipFilterOpen={setWipFilterOpen} setWipIncludeCompleted={setWipIncludeCompleted} setWipPage={setWipPage} setWipSearch={setWipSearch} setWipSelectedRows={setWipSelectedRows}
              setWipSettingsOpen={setWipSettingsOpen} setWipSort={setWipSort} setWipStatusFilter={setWipStatusFilter} wipFilterOpen={wipFilterOpen}
              wipData={wipData} wipIncludeCompleted={wipIncludeCompleted} wipPage={wipPage} wipSearch={wipSearch} wipSelectedRows={wipSelectedRows} wipSettingsOpen={wipSettingsOpen} wipSort={wipSort}
              wipStatusFilter={wipStatusFilter}
              agingData={agingData} agingDate={agingDate} agingExpandedClients={agingExpandedClients} agingLoading={agingLoading} agingPeriodPreset={agingPeriodPreset}
              agingSearch={agingSearch} agingSort={agingSort} bsData={bsData} bsDate={bsDate} bsLoading={bsLoading} bsPeriodPreset={bsPeriodPreset} bsSearch={bsSearch} plData={plData}
              plEndDate={plEndDate} plLoading={plLoading} plPeriodPreset={plPeriodPreset} plSearch={plSearch} plStartDate={plStartDate} qboCollapsed={qboCollapsed}
              qboExportOpen={qboExportOpen} setAgingData={setAgingData} setAgingDate={setAgingDate} setAgingExpandedClients={setAgingExpandedClients} setAgingLoading={setAgingLoading}
              setAgingPeriodPreset={setAgingPeriodPreset} setAgingSearch={setAgingSearch} setBsData={setBsData} setBsDate={setBsDate} setBsLoading={setBsLoading}
              setBsPeriodPreset={setBsPeriodPreset} setBsSearch={setBsSearch} setPlData={setPlData} setPlEndDate={setPlEndDate} setPlLoading={setPlLoading}
              setPlPeriodPreset={setPlPeriodPreset} setPlSearch={setPlSearch} setPlStartDate={setPlStartDate} setQboCollapsed={setQboCollapsed} setQboExportOpen={setQboExportOpen}
              setTbData={setTbData} setTbDate={setTbDate} setTbLoading={setTbLoading} setTbPeriodPreset={setTbPeriodPreset} setTbSearch={setTbSearch} setTbShowZero={setTbShowZero}
              tbData={tbData} tbDate={tbDate} tbLoading={tbLoading} tbPeriodPreset={tbPeriodPreset} tbSearch={tbSearch} tbShowZero={tbShowZero} tbSort={tbSort}
            />
          </Suspense>
        )}

        {/* bp-mkt-v1 — Marketing Hub (lazy). UI-CORE ships the full router keyed on marketingSubView. */}
        {activeView === 'marketing' && (
          <Suspense fallback={PrefsLazyFallback}>
            <MarketingView
              marketingSubView={marketingSubView}
            />
          </Suspense>
        )}

        </div>{/* end view-stack */}
      </main>

      {/* Deal-row contact hover card popover */}
      {contactHoverCard && (() => {
        const c = contactHoverCard.client
        const name = c?.preferred_name || ''
        const phoneRaw = c?.preferred_phone || ''
        const phone = phoneRaw ? formatPhoneDisplay(phoneRaw) : ''
        const email = c?.preferred_email || ''
        const street = c?.preferred_street || ''
        const cityStateZip = [c?.preferred_city, c?.preferred_state].filter(Boolean).join(', ') + (c?.preferred_zip ? ` ${c.preferred_zip}` : '')
        const hasAddress = !!(street || (c?.preferred_city) || (c?.preferred_state) || (c?.preferred_zip))
        const rowStyle: React.CSSProperties = { display: 'flex', alignItems: 'flex-start', gap: 8, padding: '7px 0' }
        const labelStyle: React.CSSProperties = { fontSize: '0.68rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.03em', width: 56, flexShrink: 0, paddingTop: 2 }
        const valueStyle: React.CSSProperties = { fontSize: '0.82rem', color: '#33475b', wordBreak: 'break-word', flex: 1, lineHeight: 1.35 }
        const dimStyle: React.CSSProperties = { ...valueStyle, color: '#cbd5e1' }
        const dividerStyle: React.CSSProperties = { borderTop: '1px solid #f1f5f9' }
        return (
          <div
            style={{ position: 'fixed', left: contactHoverCard.x, top: contactHoverCard.y, width: 280, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: '10px 14px', fontSize: '0.82rem', zIndex: 200, pointerEvents: 'auto' }}
            onMouseEnter={() => { if (contactHoverCloseTimerRef.current) { clearTimeout(contactHoverCloseTimerRef.current); contactHoverCloseTimerRef.current = null } }}
            onMouseLeave={() => {
              if (contactHoverCloseTimerRef.current) clearTimeout(contactHoverCloseTimerRef.current)
              contactHoverCloseTimerRef.current = setTimeout(() => setContactHoverCard(null), 150)
            }}
          >
            {!c ? (
              <div style={{ fontSize: '0.8rem', color: '#94a3b8', padding: '6px 0' }}>Loading contact...</div>
            ) : (
              <div>
                <div style={rowStyle}>
                  <div style={labelStyle}>Name</div>
                  <div style={name ? valueStyle : dimStyle}>{name || '\u2014'}</div>
                </div>
                <div style={dividerStyle} />
                <div style={rowStyle}>
                  <div style={labelStyle}>Phone</div>
                  <div style={phone ? valueStyle : dimStyle}>{phone || '\u2014'}</div>
                </div>
                <div style={dividerStyle} />
                <div style={rowStyle}>
                  <div style={labelStyle}>Email</div>
                  <div style={email ? valueStyle : dimStyle}>{email || '\u2014'}</div>
                </div>
                <div style={dividerStyle} />
                <div style={rowStyle}>
                  <div style={labelStyle}>Address</div>
                  <div style={hasAddress ? valueStyle : dimStyle}>
                    {hasAddress ? (
                      <>
                        {street && <div>{street}</div>}
                        {cityStateZip.trim() && <div>{cityStateZip}</div>}
                      </>
                    ) : '\u2014'}
                  </div>
                </div>
              </div>
            )}
          </div>
        )
      })()}

      {/* ========================================================= */}
      {/* Time-tracking — floating sticky pill                        */}
      {/* Visible whenever an entry is open. Shown regardless of the  */}
      {/* active view so navigating away from a deal still lets the   */}
      {/* user clock out with one click.                              */}
      {/* ========================================================= */}
      {timeOpenEntry && (
        <RunningClockBadge
          entry={timeOpenEntry}
          disabled={!!timeClocking}
          onOpenDeal={() => {
            if (timeOpenEntry.deal_id) {
              window.location.hash = `/deals/record/${timeOpenEntry.deal_id}`
              setActiveView('deals')
            }
          }}
          onClockOut={() => {
            setTimeClockOutPrompt({ entryId: timeOpenEntry.id, defaultCategory: timeOpenEntry.payroll_category })
            setTimeClockOutNotes('')
          }}
        />
      )}

      {/* ========================================================= */}
      {/* Time-tracking — toast + clock-out prompt + entries modal    */}
      {/* ========================================================= */}
      {timeToast && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed', right: 20, bottom: 20, zIndex: 10010,
            background: '#1f2937', color: '#fff', padding: '10px 14px', borderRadius: 6,
            fontSize: '0.82rem', boxShadow: '0 6px 16px rgba(0,0,0,0.18)',
            maxWidth: 360,
          }}
        >
          {timeToast}
        </div>
      )}

      {/* Clock-out prompt — small modal that captures optional notes before
          the actual POST. GPS is captured automatically on submit. */}
      {timeClockOutPrompt && (
        <div className="modal-overlay" onClick={() => { if (!timeClocking) { setTimeClockOutPrompt(null); setTimeClockOutNotes('') } }}>
          <div className="modal-card" style={{ maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
            <button type="button" className="modal-close" onClick={() => { if (!timeClocking) { setTimeClockOutPrompt(null); setTimeClockOutNotes('') } }}>&times;</button>
            <div className="modal-body">
              <h3 className="modal-title">Clock out</h3>
              <p style={{ fontSize: '0.82rem', color: '#64748b', marginBottom: 10 }}>
                Add a quick note about what you worked on (optional). Your location will be captured automatically.
              </p>
              <textarea
                autoFocus
                value={timeClockOutNotes}
                onChange={(e) => setTimeClockOutNotes(e.target.value)}
                placeholder="e.g., Completed trim detail on west elevation"
                rows={4}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.88rem', fontFamily: 'inherit', resize: 'vertical' }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
                <button
                  type="button"
                  onClick={() => { setTimeClockOutPrompt(null); setTimeClockOutNotes('') }}
                  disabled={!!timeClocking}
                  style={{ padding: '8px 14px', background: '#fff', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.82rem', cursor: 'pointer', color: '#475569' }}
                >Cancel</button>
                <button
                  type="button"
                  onClick={() => handleClockOutSubmit(timeClockOutNotes.trim() || null)}
                  disabled={!!timeClocking}
                  style={{ padding: '8px 14px', background: '#c5221f', border: '1px solid #c5221f', borderRadius: 4, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', color: '#fff' }}
                >{timeClocking === 'out' ? 'Clocking out…' : 'Clock out'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Time entries modal — filtered list with per-row edit/approve/delete
          and CSV export. Re-used by the Deal Record card and the global
          Timesheets view. `dealId = null` means global (admin-only). */}
      {timeEntriesModalOpen && (
        <TimeEntriesModal
          onClose={() => setTimeEntriesModalOpen(null)}
          initialDealId={timeEntriesModalOpen.dealId}
          title={timeEntriesModalOpen.title ? `Time entries · ${timeEntriesModalOpen.title}` : 'Time entries'}
          currentUserRole={currentUser?.role || 'user'}
          currentUserEmail={currentUser?.email || ''}
          currentUserId={currentUser?.userId || 0}
          onAfterChange={(dealId) => {
            if (dealId) {
              fetchDealLaborSummary(dealId).then(setTimeLaborSummary).catch(() => {})
            }
          }}
        />
      )}

      {/* Project-attachment upload modal — categorizes the file so the
          resulting badge matches the legacy QB doc types. Styling tracks the
          CRM design system (see .claude/docs/UI_DESIGN_SYSTEM.md). */}
      {projectUploadModal && (() => {
        const DOC_TYPES: { value: string; label: string; tone: 'blue' | 'green' | 'purple' | 'orange' | 'pink' | 'slate' | 'amber' | 'teal' }[] = [
          { value: 'Invoice',            label: 'Invoice',         tone: 'blue' },
          { value: 'Payment Receipt',    label: 'Payment Receipt', tone: 'green' },
          { value: 'Receipts',           label: 'Receipts',        tone: 'green' },
          { value: 'Draw Request',       label: 'Draw Request',    tone: 'purple' },
          { value: 'Proposal',           label: 'Proposal',        tone: 'orange' },
          { value: 'Estimate',           label: 'Estimate',        tone: 'orange' },
          { value: 'Contract',           label: 'Contract',        tone: 'pink' },
          { value: 'Work Order',         label: 'SOW',             tone: 'amber' },
          { value: 'Work Order Related', label: 'Project docs',    tone: 'teal' },
          { value: 'Other',              label: 'Other',           tone: 'slate' },
        ]
        const { docType, files } = projectUploadModal
        const close = () => { if (!projectAttachmentsUploading) setProjectUploadModal(null) }
        const toneClass = (() => {
          const found = DOC_TYPES.find(t => t.value === docType)
          if (!found) return ''
          return found.tone === 'blue' ? '' : `is-active--${found.tone}`
        })()
        const fmtSize = (n?: number | null) => {
          if (!n) return ''
          if (n < 1024) return `${n} B`
          if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
          return `${(n / 1024 / 1024).toFixed(1)} MB`
        }
        return (
          <div className="modal-overlay" onClick={close}>
            <div className="modal-card upload-modal" onClick={e => e.stopPropagation()}>
              <div className="upload-modal__bar" />
              <button type="button" className="modal-close" onClick={close} aria-label="Close">&times;</button>
              <div className="upload-modal__head">
                <h3 className="upload-modal__title">Upload attachment</h3>
                <p className="upload-modal__hint">Pick a document type — the file is tagged so its row gets the matching badge color.</p>
              </div>
              <div className="upload-modal__body">
                <div>
                  <span className="upload-modal__field-label">Document type</span>
                  <div className="upload-modal__type-grid">
                    {DOC_TYPES.map(t => (
                      <button
                        key={t.value}
                        type="button"
                        className={`upload-modal__type-chip${docType === t.value ? ' is-active' : ''}${docType === t.value && t.tone !== 'blue' ? ` is-active--${t.tone}` : ''}`}
                        onClick={() => setProjectUploadModal(m => m ? { ...m, docType: t.value } : m)}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <span className="upload-modal__field-label">File{files.length === 1 ? '' : 's'}</span>
                  <label className="upload-modal__drop">
                    <input
                      type="file"
                      multiple
                      onChange={e => {
                        const list = e.target.files ? Array.from(e.target.files) : []
                        setProjectUploadModal(m => m ? { ...m, files: list } : m)
                      }}
                    />
                    <svg className="upload-modal__drop-icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                    <span className="upload-modal__drop-primary">Click to choose file{files.length === 1 ? '' : 's'}</span>
                    <span className="upload-modal__drop-secondary">or drag a PDF / image here</span>
                  </label>
                  {files.length > 0 && (
                    <div className="upload-modal__files">
                      {files.map((f, i) => (
                        <div key={`${f.name}:${i}`} className="upload-modal__file-row">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                          <span className="upload-modal__file-name">{f.name}</span>
                          <span className="upload-modal__file-size">{fmtSize(f.size)}</span>
                          <button
                            type="button"
                            className="upload-modal__file-remove"
                            aria-label={`Remove ${f.name}`}
                            onClick={() => setProjectUploadModal(m => m ? { ...m, files: m.files.filter((_, j) => j !== i) } : m)}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="upload-modal__footer">
                <button type="button" className="upload-modal__btn-cancel" onClick={close} disabled={projectAttachmentsUploading}>Cancel</button>
                <button
                  type="button"
                  className="upload-modal__btn-submit"
                  disabled={projectAttachmentsUploading || files.length === 0}
                  onClick={async () => {
                    const pid = projectDetailData?.id
                    if (!pid || files.length === 0) return
                    setProjectAttachmentsUploading(true)
                    const form = new FormData()
                    form.append('doc_type', docType)
                    for (const f of files) form.append('files', f)
                    try {
                      const r = await fetch(`/api/crm/projects/${encodeURIComponent(pid)}/attachments`, { method: 'POST', body: form })
                      if (!r.ok) throw new Error('Upload failed')
                      const r2 = await fetch(`/api/crm/projects/${encodeURIComponent(pid)}/attachments`)
                      const d = await r2.json()
                      setProjectAttachments(Array.isArray(d.attachments) ? d.attachments : [])
                      setProjectUploadModal(null)
                    } catch (err: any) {
                      alert(err?.message || 'Upload failed')
                    } finally {
                      setProjectAttachmentsUploading(false)
                    }
                  }}
                >
                  {projectAttachmentsUploading ? (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M21 12a9 9 0 1 1-6.219-8.56" style={{ animation: 'cmdSpin 0.7s linear infinite', transformOrigin: 'center' }}/></svg>
                      Uploading…
                    </>
                  ) : (
                    <>
                      Upload {files.length > 0 ? `${files.length} file${files.length > 1 ? 's' : ''}` : ''}
                    </>
                  )}
                </button>
              </div>
              {/* tone class applied at root level for any future surface that wants it */}
              <div style={{ display: 'none' }}>{toneClass}</div>
            </div>
          </div>
        )
      })()}

      {/* Generic attachment preview modal — opened from the project record's
          attachments card. Reuses the .pdf-preview-* styles. Prev/Next arrows
          + ←/→ keys (handled in the useEffect above) page through the full
          visible list without closing & reopening once per file. */}
      {attachmentPreview && (() => {
        const { items, index } = attachmentPreview
        const current = items[index]
        if (!current) return null
        const total = items.length
        // Classify by extension so each file type gets the right viewer
        // (browsers download .docx and blank-iframe non-renderables otherwise).
        const ext = (current.title || current.url).split('?')[0].split('.').pop()?.toLowerCase() || ''
        const isPdf = ext === 'pdf'
        const isImage = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(ext)
        const isDocx = ext === 'docx' || ext === 'doc'
        // Explicit-download URL — appends ?download=1 so the serve route sends
        // Content-Disposition: attachment (works alongside an existing query).
        const downloadUrl = current.url + (current.url.includes('?') ? '&' : '?') + 'download=1'
        // For .doc/.docx we render server-converted PDF instead of lossy mammoth
        // HTML. items[].url is like `/api/crm/files?key=<enc>` (optionally with
        // `&download=1`); swap the path to the dedicated PDF-conversion route so
        // the same R2 key + query string flow through. Native PDFs use url as-is.
        const viewerUrl = isDocx
          ? current.url.replace('/api/crm/files?', '/api/crm/files/pdf?')
          : current.url
        const fallbackCard = (msg: string) => (
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, color: '#64748b', textAlign: 'center', padding: 24 }}>
            <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            <div style={{ fontSize: '0.9rem', fontWeight: 500 }}>{msg}</div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" onClick={() => window.open(current.url, '_blank', 'noopener')} style={{ padding: '7px 14px', borderRadius: 7, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', fontSize: '0.82rem', cursor: 'pointer' }}>Open in new tab</button>
              <a href={downloadUrl} style={{ padding: '7px 14px', borderRadius: 7, border: '1px solid #2563eb', background: '#2563eb', color: '#fff', fontSize: '0.82rem', textDecoration: 'none' }}>Download</a>
            </div>
          </div>
        )
        const goto = (delta: number) => {
          setAttachmentPreview(prev => {
            if (!prev) return prev
            const len = prev.items.length
            if (len < 2) return prev
            const nextIdx = (prev.index + delta + len) % len
            return { ...prev, index: nextIdx }
          })
        }
        const navBtnStyle: React.CSSProperties = {
          position: 'absolute',
          top: '50%',
          transform: 'translateY(-50%)',
          width: 44,
          height: 44,
          display: 'grid',
          placeItems: 'center',
          border: 'none',
          borderRadius: '50%',
          background: 'rgba(15, 23, 42, 0.55)',
          color: '#fff',
          cursor: total > 1 ? 'pointer' : 'not-allowed',
          opacity: total > 1 ? 1 : 0.4,
          zIndex: 3,
          transition: 'background 120ms ease',
        }
        return (
          <div className="pdf-preview-overlay" onClick={() => setAttachmentPreview(null)}>
            <div className="pdf-preview-modal" onClick={(e) => e.stopPropagation()} style={{ position: 'relative' }}>
              <div className="pdf-preview-header">
                <h3 className="pdf-preview-title">
                  {current.title}
                  {total > 1 && (
                    <span style={{ marginLeft: 10, fontSize: '0.74rem', fontWeight: 500, color: '#94a3b8' }}>
                      {index + 1} / {total}
                    </span>
                  )}
                </h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <a
                    href={downloadUrl}
                    title="Download"
                    aria-label="Download attachment"
                    style={{ display: 'grid', placeItems: 'center', width: 30, height: 30, borderRadius: 6, color: '#64748b', textDecoration: 'none' }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = '#f1f5f9' }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = 'transparent' }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  </a>
                  <button className="pdf-preview-close" onClick={() => setAttachmentPreview(null)} aria-label="Close preview">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
              </div>
              {(isPdf || isDocx) ? (
                // Native PDFs stream directly; .doc/.docx stream through the
                // server-side PDF conversion route (viewerUrl). If conversion
                // fails the <object> falls back to a clean Open/Download card —
                // no broken image, no blank iframe.
                <object key={viewerUrl} data={viewerUrl} type="application/pdf" className="pdf-preview-iframe">
                  {fallbackCard('Unable to display preview')}
                </object>
              ) : isImage ? (
                <div className="pdf-preview-iframe" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'auto', background: '#0f172a' }}>
                  <img src={current.url} alt={current.title} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                </div>
              ) : (
                fallbackCard('Preview not available for this file type')
              )}
              {total > 1 && (
                <>
                  <button
                    type="button"
                    aria-label="Previous attachment"
                    title="Previous (←)"
                    onClick={() => goto(-1)}
                    style={{ ...navBtnStyle, left: 12 }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(15, 23, 42, 0.78)' }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(15, 23, 42, 0.55)' }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                  </button>
                  <button
                    type="button"
                    aria-label="Next attachment"
                    title="Next (→)"
                    onClick={() => goto(1)}
                    style={{ ...navBtnStyle, right: 12 }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(15, 23, 42, 0.78)' }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(15, 23, 42, 0.55)' }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                  </button>
                </>
              )}
            </div>
          </div>
        )
      })()}

      {/* HR Profile drawer — opened from any clickable user-name trigger
          (deal owner / estimator / PM, etc.). Slides in from the right. */}
      {hrProfileDrawer && (
        <>
          <div
            className="hr-profile-drawer-backdrop"
            onClick={() => setHrProfileDrawer(null)}
            role="presentation"
          />
          <div
            className="hr-profile-drawer"
            role="dialog"
            aria-label="HR profile"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="hr-profile-drawer-head">
              <span className="hr-profile-drawer-title">HR profile</span>
              <button
                className="hr-profile-drawer-close"
                onClick={() => setHrProfileDrawer(null)}
                aria-label="Close"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="hr-profile-drawer-body">
              {hrProfileDrawer.loading ? (
                <div style={{padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: '0.82rem'}}>
                  <DotLottieReact src="/loading.lottie" loop autoplay style={{ width: 56, height: 56, margin: '0 auto 10px' }} />
                  Loading profile…
                </div>
              ) : hrProfileDrawer.notFound || !hrProfileDrawer.profile ? (
                <div style={{padding: 24, textAlign: 'center', color: '#64748b', fontSize: '0.82rem'}}>
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{color: '#cbd5e1', marginBottom: 10}}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  <div style={{fontWeight: 600, color: '#334155', marginBottom: 4}}>No HR profile linked</div>
                  <div>This user doesn't have a linked HR profile.</div>
                </div>
              ) : (() => {
                const p = hrProfileDrawer.profile!
                const initials = (p.fullName || '?').split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase()
                return (
                  <>
                    <div className="hr-profile-drawer-hero">
                      {p.photoUrl ? (
                        <img src={p.photoUrl} alt={p.fullName} className="hr-profile-drawer-avatar-img" />
                      ) : (
                        <div className="hr-profile-drawer-avatar">{initials}</div>
                      )}
                      <div className="hr-profile-drawer-hero-text">
                        <div className="hr-profile-drawer-name">{p.fullName}</div>
                        {p.position && <div className="hr-profile-drawer-position">{p.position}</div>}
                        {p.department && <div className="hr-profile-drawer-dept">{p.department}</div>}
                      </div>
                    </div>
                    <div className="hr-profile-drawer-fields">
                      {p.email && (
                        <div className="hr-profile-drawer-field">
                          <span className="hr-profile-drawer-field-label">Email</span>
                          <a href={`mailto:${p.email}`} className="hr-profile-drawer-field-value hr-profile-drawer-link">{p.email}</a>
                        </div>
                      )}
                      {p.phone && (
                        <div className="hr-profile-drawer-field">
                          <span className="hr-profile-drawer-field-label">Phone</span>
                          <a href={`tel:${p.phone}`} className="hr-profile-drawer-field-value hr-profile-drawer-link">{p.phone}</a>
                        </div>
                      )}
                      {p.department && (
                        <div className="hr-profile-drawer-field">
                          <span className="hr-profile-drawer-field-label">Department</span>
                          <span className="hr-profile-drawer-field-value">{p.department}</span>
                        </div>
                      )}
                      {p.position && (
                        <div className="hr-profile-drawer-field">
                          <span className="hr-profile-drawer-field-label">Title</span>
                          <span className="hr-profile-drawer-field-value">{p.position}</span>
                        </div>
                      )}
                    </div>
                    <div className="hr-profile-drawer-footer">
                      <a
                        href="#/hr-center"
                        onClick={(e) => {
                          e.preventDefault()
                          const bid = p.employeeId
                          setHrProfileDrawer(null)
                          // Switch to HR view + employees tab + preselect the employee.
                          setActiveView('hr-center')
                          setHrPortalView('employees')
                          setHrPortalSelectedId(String(bid))
                          setHrPortalDetailTab('overview')
                          window.location.hash = `/hr-center?employee=${bid}`
                        }}
                        className="hr-profile-drawer-link hr-profile-drawer-open-btn"
                      >
                        Open full HR profile
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                      </a>
                    </div>
                  </>
                )
              })()}
            </div>
          </div>
        </>
      )}

      <KnowledgeBasePanel open={kbOpen} onClose={() => setKbOpen(false)} isSuperAdmin={effectiveCanonicalRole === 'super_admin'} />
      {kbAdminRoute && (
        <div style={{ position: 'fixed', top: 48, left: 'var(--sidebar-width)', right: 0, bottom: 0, background: '#fff', zIndex: 50, overflow: 'auto' }}>
          <Suspense fallback={<div className="view-loading" />}>
            <AdminKnowledgeBase />
          </Suspense>
        </div>
      )}

      {/* Shell-level toast fallback (canonical-client-redirect-v2). `dealToast`
          is rendered inside DealsList (.kanban-toast), which only mounts on the
          deals view. Many setDealToast() call sites fire from other views — most
          importantly applyClientOrRedirect()'s "Merged record — showing the
          current one." on the Contacts/Organizations record page — so mirror the
          toast here at the always-mounted shell level whenever DealsList is NOT
          mounted (activeView !== 'deals'), avoiding a double render. Fixed
          positioning (vs the class's `absolute`) keeps it visible on any view. */}
      {dealToast && activeView !== 'deals' && (
        <div className={`kanban-toast ${dealToast.type}`} style={{ position: 'fixed', top: 64, right: 24, zIndex: 10050 }}>
          {dealToast.type === 'success' && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 13l4 4L19 7"/></svg>}
          {dealToast.type === 'error' && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M6 18L18 6M6 6l12 12"/></svg>}
          {dealToast.message.startsWith('__PROJECT_CREATED__') ? (
            <span>
              Deal won! Project #{dealToast.message.replace('__PROJECT_CREATED__', '')} created.{' '}
              <a
                href={`#/projects/record/${dealToast.message.replace('__PROJECT_CREATED__', '')}`}
                style={{ color: 'inherit', textDecoration: 'underline', fontWeight: 600 }}
                onClick={() => setDealToast(null)}
              >
                View Project
              </a>
            </span>
          ) : dealToast.message}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// TimeEntriesModal — shared by the Deal Record labor card and the global
// Timesheets view. Filter chips (category + approval status), date range,
// per-row edit/approve/delete, and CSV export. Admins get bulk-approve +
// delete; non-admins see only their own actionable rows.
// ---------------------------------------------------------------------------
function TimeEntriesModal(props: {
  onClose: () => void
  initialDealId: number | null
  title: string
  currentUserRole: string
  currentUserEmail: string
  currentUserId: number
  onAfterChange?: (dealId: number | null) => void
}) {
  const { onClose, initialDealId, title, currentUserRole, currentUserEmail, currentUserId, onAfterChange } = props
  const isAdmin = currentUserRole === 'admin' || currentUserRole === 'Super Admin' || (
    typeof currentUserEmail === 'string' && ['mritchie@botpros.ai', 'agent@botpros.ai'].includes(currentUserEmail.toLowerCase())
  )

  // Filter state. `from` / `to` are YYYY-MM-DD strings; we translate to
  // ISO midnight on send so the server clamps on the clock_in_at column.
  const [filterActorType, setFilterActorType] = useState<'' | 'employee' | 'subcontractor'>('')
  const [filterCategory, setFilterCategory] = useState<'' | TimeCategory>('')
  const [filterApproved, setFilterApproved] = useState<'all' | 'pending' | 'approved'>('all')
  const [filterFrom, setFilterFrom] = useState<string>('')
  const [filterTo, setFilterTo] = useState<string>('')
  const [entries, setEntries] = useState<TimeEntry[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const pageSize = 25
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editDraft, setEditDraft] = useState<{ notes: string; payroll_category: TimeCategory; billable: boolean }>({ notes: '', payroll_category: 'field', billable: true })
  const [err, setErr] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<number | null>(null)

  const buildFilters = (): TimeListFilters => ({
    deal_id: initialDealId,
    actor_type: filterActorType || null,
    payroll_category: filterCategory || null,
    approved: filterApproved,
    from: filterFrom ? `${filterFrom}T00:00:00` : null,
    to: filterTo ? `${filterTo}T23:59:59` : null,
    limit: pageSize,
    offset: page * pageSize,
  })

  const load = async () => {
    setLoading(true)
    setErr(null)
    try {
      const r = await listTimeEntries(buildFilters())
      setEntries(r.entries || [])
      setTotal(r.total || 0)
    } catch (e: any) {
      setErr(e?.message || 'Failed to load')
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { load() }, [filterActorType, filterCategory, filterApproved, filterFrom, filterTo, page, initialDealId])

  const startEdit = (e: TimeEntry) => {
    setEditingId(e.id)
    setEditDraft({ notes: e.notes || '', payroll_category: e.payroll_category, billable: !!e.billable })
  }
  const saveEdit = async () => {
    if (editingId == null) return
    setBusyId(editingId)
    try {
      const updated = await patchTimeEntry(editingId, editDraft)
      setEntries(list => list.map(x => x.id === updated.id ? updated : x))
      setEditingId(null)
      onAfterChange?.(updated.deal_id)
    } catch (e: any) {
      setErr(e?.message || 'Failed to save')
    } finally {
      setBusyId(null)
    }
  }
  const toggleApprove = async (entry: TimeEntry) => {
    setBusyId(entry.id)
    try {
      const updated = entry.approved_at
        ? await unapproveTimeEntry(entry.id)
        : await approveTimeEntry(entry.id, null)
      setEntries(list => list.map(x => x.id === updated.id ? updated : x))
      onAfterChange?.(updated.deal_id)
    } catch (e: any) {
      setErr(e?.message || 'Failed')
    } finally {
      setBusyId(null)
    }
  }
  const remove = async (entry: TimeEntry) => {
    if (!window.confirm('Delete this time entry? This cannot be undone.')) return
    setBusyId(entry.id)
    try {
      await deleteTimeEntry(entry.id)
      setEntries(list => list.filter(x => x.id !== entry.id))
      setSelectedIds(s => { const n = new Set(s); n.delete(entry.id); return n })
      onAfterChange?.(entry.deal_id)
    } catch (e: any) {
      setErr(e?.message || 'Failed')
    } finally {
      setBusyId(null)
    }
  }
  const bulkApprove = async () => {
    const ids = [...selectedIds]
    if (ids.length === 0) return
    try {
      await bulkApproveTime(ids)
      setSelectedIds(new Set())
      load()
      onAfterChange?.(initialDealId)
    } catch (e: any) {
      setErr(e?.message || 'Bulk approve failed')
    }
  }
  const downloadCsv = () => {
    // Browser-trigger a download via synthetic anchor — no fetch needed.
    const url = timeExportCsvUrl(buildFilters())
    const a = document.createElement('a')
    a.href = url
    a.rel = 'noopener'
    document.body.appendChild(a)
    a.click()
    a.remove()
  }

  const pageCount = Math.max(1, Math.ceil(total / pageSize))

  // Helper used by edit-permission check inside the table row.
  const canEditRow = (e: TimeEntry): boolean => {
    if (isAdmin) return true
    if (e.actor_type === 'employee' && Number(e.actor_id) === Number(currentUserId)) return true
    return false
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" style={{ maxWidth: 1100, width: '95vw', maxHeight: 'calc(100vh - 60px)', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-close" onClick={onClose}>&times;</button>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
          <h3 className="modal-title" style={{ marginBottom: 12 }}>{title}</h3>

          {/* Filter row */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 12 }}>
            <select value={filterActorType} onChange={e => { setFilterActorType(e.target.value as any); setPage(0) }} style={{ padding: '4px 8px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.82rem' }}>
              <option value="">All actors</option>
              <option value="employee">Employees</option>
              <option value="subcontractor">Subcontractors</option>
            </select>
            <select value={filterCategory} onChange={e => { setFilterCategory(e.target.value as any); setPage(0) }} style={{ padding: '4px 8px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.82rem' }}>
              <option value="">All categories</option>
              {(['field','admin','drive-time','pto','overtime','other'] as TimeCategory[]).map(c => (
                <option key={c} value={c}>{TIME_CATEGORY_LABELS[c]}</option>
              ))}
            </select>
            <select value={filterApproved} onChange={e => { setFilterApproved(e.target.value as any); setPage(0) }} style={{ padding: '4px 8px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.82rem' }}>
              <option value="all">All statuses</option>
              <option value="pending">Pending approval</option>
              <option value="approved">Approved</option>
            </select>
            <label style={{ fontSize: '0.78rem', color: '#64748b' }}>From <input type="date" value={filterFrom} onChange={e => { setFilterFrom(e.target.value); setPage(0) }} style={{ padding: '3px 6px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.82rem', marginLeft: 4 }} /></label>
            <label style={{ fontSize: '0.78rem', color: '#64748b' }}>To <input type="date" value={filterTo} onChange={e => { setFilterTo(e.target.value); setPage(0) }} style={{ padding: '3px 6px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.82rem', marginLeft: 4 }} /></label>
            <div style={{ flex: 1 }} />
            <button type="button" onClick={downloadCsv} style={{ padding: '6px 10px', background: '#fff', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.78rem', cursor: 'pointer', color: '#334155', fontWeight: 500 }}>Export CSV</button>
            {isAdmin && selectedIds.size > 0 && (
              <button type="button" onClick={bulkApprove} style={{ padding: '6px 10px', background: '#0b8f3a', border: '1px solid #0b8f3a', borderRadius: 4, fontSize: '0.78rem', cursor: 'pointer', color: '#fff', fontWeight: 600 }}>
                Approve {selectedIds.size}
              </button>
            )}
          </div>

          {err && <div style={{ padding: '8px 10px', background: '#fde7e9', color: '#c5221f', borderRadius: 4, fontSize: '0.78rem', marginBottom: 10 }}>{err}</div>}

          <div style={{ flex: 1, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff' }}>
            <table className="hs-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', fontSize: '0.72rem', textTransform: 'uppercase', color: '#64748b', letterSpacing: 0.3, fontWeight: 600 }}>
                  {isAdmin && (
                    <th style={{ width: 30, padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>
                      <input
                        type="checkbox"
                        checked={entries.length > 0 && entries.every(e => selectedIds.has(e.id))}
                        onChange={e => {
                          const next = new Set(selectedIds)
                          if (e.target.checked) entries.forEach(x => next.add(x.id))
                          else entries.forEach(x => next.delete(x.id))
                          setSelectedIds(next)
                        }}
                      />
                    </th>
                  )}
                  <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Date</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Actor</th>
                  {initialDealId == null && <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Deal</th>}
                  <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>In / Out</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>Duration</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Category</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Notes</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Status</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={isAdmin ? 10 : 9} style={{ padding: 24, textAlign: 'center', color: '#64748b', fontSize: '0.82rem' }}>Loading…</td></tr>
                )}
                {!loading && entries.length === 0 && (
                  <tr><td colSpan={isAdmin ? 10 : 9} style={{ padding: 36, textAlign: 'center', color: '#64748b', fontSize: '0.82rem' }}>No time entries match these filters.</td></tr>
                )}
                {!loading && entries.map(e => {
                  const editing = editingId === e.id
                  const cat = TIME_CATEGORY_COLORS[e.payroll_category] || TIME_CATEGORY_COLORS.other
                  const canEditThisRow = canEditRow(e)
                  const open = !e.clock_out_at
                  return (
                    <tr key={e.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      {isAdmin && (
                        <td style={{ padding: '8px 10px' }}>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(e.id)}
                            onChange={ev => setSelectedIds(s => { const n = new Set(s); if (ev.target.checked) n.add(e.id); else n.delete(e.id); return n })}
                          />
                        </td>
                      )}
                      <td style={{ padding: '8px 10px', fontSize: '0.78rem', color: '#334155', whiteSpace: 'nowrap' }}>{formatDateShort(e.clock_in_at)}</td>
                      <td style={{ padding: '8px 10px', fontSize: '0.78rem' }}>
                        <div style={{ color: '#0f172a', fontWeight: 500 }}>{e.actor_name || 'Unknown'}</div>
                        <div style={{ fontSize: '0.68rem', color: '#94a3b8' }}>{e.actor_type === 'subcontractor' ? 'Subcontractor' : 'Employee'}</div>
                      </td>
                      {initialDealId == null && (
                        <td style={{ padding: '8px 10px', fontSize: '0.78rem', color: '#475569' }}>
                          {e.deal_id ? <span>#{e.deal_id} {e.deal_title || ''}</span> : <span style={{ color: '#94a3b8' }}>—</span>}
                        </td>
                      )}
                      <td style={{ padding: '8px 10px', fontSize: '0.78rem', color: '#475569', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                        {formatTimeOfDay(e.clock_in_at)} → {e.clock_out_at ? formatTimeOfDay(e.clock_out_at) : <span style={{ color: '#137333', fontWeight: 600 }}>open</span>}
                      </td>
                      <td style={{ padding: '8px 10px', fontSize: '0.78rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {e.duration_minutes != null ? formatMinutesLabel(e.duration_minutes) : <span style={{ color: '#94a3b8' }}>—</span>}
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        {editing ? (
                          <select value={editDraft.payroll_category} onChange={ev => setEditDraft(d => ({ ...d, payroll_category: ev.target.value as TimeCategory }))} style={{ padding: '2px 4px', fontSize: '0.78rem' }}>
                            {(['field','admin','drive-time','pto','overtime','other'] as TimeCategory[]).map(c => <option key={c} value={c}>{TIME_CATEGORY_LABELS[c]}</option>)}
                          </select>
                        ) : (
                          <span style={{ padding: '2px 8px', background: cat.bg, color: cat.fg, borderRadius: 10, fontSize: '0.7rem', fontWeight: 500 }}>{TIME_CATEGORY_LABELS[e.payroll_category]}</span>
                        )}
                      </td>
                      <td style={{ padding: '8px 10px', fontSize: '0.78rem', color: '#475569', maxWidth: 260 }}>
                        {editing ? (
                          <input value={editDraft.notes} onChange={ev => setEditDraft(d => ({ ...d, notes: ev.target.value }))} style={{ width: '100%', padding: '3px 6px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.78rem' }} />
                        ) : (
                          <span title={e.notes || ''} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.notes || <span style={{ color: '#94a3b8' }}>—</span>}</span>
                        )}
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        {open ? (
                          <span style={{ padding: '2px 8px', background: '#fef3c7', color: '#92400e', borderRadius: 10, fontSize: '0.7rem', fontWeight: 500 }}>Running</span>
                        ) : e.approved_at ? (
                          <span style={{ padding: '2px 8px', background: '#e6f4ea', color: '#137333', borderRadius: 10, fontSize: '0.7rem', fontWeight: 500 }}>Approved</span>
                        ) : (
                          <span style={{ padding: '2px 8px', background: '#fff4e5', color: '#b06000', borderRadius: 10, fontSize: '0.7rem', fontWeight: 500 }}>Pending</span>
                        )}
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {editing ? (
                          <>
                            <button onClick={saveEdit} disabled={busyId === e.id} style={{ padding: '3px 8px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, fontSize: '0.72rem', cursor: 'pointer', marginRight: 4 }}>Save</button>
                            <button onClick={() => setEditingId(null)} style={{ padding: '3px 8px', background: 'none', color: '#64748b', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.72rem', cursor: 'pointer' }}>Cancel</button>
                          </>
                        ) : (
                          <>
                            {canEditThisRow && !open && (
                              <button onClick={() => startEdit(e)} disabled={busyId === e.id} title="Edit" style={{ padding: '3px 6px', background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', marginRight: 2 }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                              </button>
                            )}
                            {!open && (isAdmin || (e.deal_id && !e.approved_at)) && (
                              <button onClick={() => toggleApprove(e)} disabled={busyId === e.id} title={e.approved_at ? 'Unapprove' : 'Approve'} style={{ padding: '3px 8px', background: e.approved_at ? '#fff' : '#0b8f3a', color: e.approved_at ? '#64748b' : '#fff', border: `1px solid ${e.approved_at ? '#cbd6e2' : '#0b8f3a'}`, borderRadius: 4, fontSize: '0.72rem', cursor: 'pointer', fontWeight: 500, marginRight: 4 }}>
                                {e.approved_at ? 'Unapprove' : 'Approve'}
                              </button>
                            )}
                            {isAdmin && (
                              <button onClick={() => remove(e)} disabled={busyId === e.id} title="Delete" style={{ padding: '3px 6px', background: 'none', border: 'none', cursor: 'pointer', color: '#c5221f' }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"/></svg>
                              </button>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, fontSize: '0.78rem', color: '#64748b' }}>
            <div>{total} entries</div>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={{ padding: '4px 10px', background: '#fff', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.78rem', cursor: page === 0 ? 'not-allowed' : 'pointer', opacity: page === 0 ? 0.5 : 1 }}>Prev</button>
              <span>Page {page + 1} of {pageCount}</span>
              <button onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))} disabled={page >= pageCount - 1} style={{ padding: '4px 10px', background: '#fff', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.78rem', cursor: page >= pageCount - 1 ? 'not-allowed' : 'pointer', opacity: page >= pageCount - 1 ? 0.5 : 1 }}>Next</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Shared inline panel — reuses the modal's data-fetch + filter + action
// layout but strips the modal chrome so it can embed inside the full
// Timesheets view. Implementation mirrors TimeEntriesModal.
// wave-2: small inline form rendered under a highlighted call row when the
// caller has a phone but no linked contact. Single-purpose, kept inline so
// it doesn't sprawl into a stand-alone component file.
function CallCreateContactForm(props: { phone: string; onCancel: () => void; onCreated: (clientId: string) => void }) {
  const [name, setName] = React.useState('')
  const [busy, setBusy] = React.useState(false)
  const [err, setErr] = React.useState<string | null>(null)
  return (
    <>
      <input
        autoFocus
        placeholder="Contact name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Escape') props.onCancel() }}
        disabled={busy}
      />
      <button
        disabled={busy || !name.trim()}
        onClick={async () => {
          setBusy(true); setErr(null)
          // Reject "Last, First" / "&"-couple / email-shaped names before create
          // (server enforces the same spec as a backstop).
          const nameCheck = validatePersonNameInput(name.trim())
          if (!nameCheck.ok) { setErr(nameCheck.message); setBusy(false); return }
          const res = await createContactFromCall({ name: name.trim(), phone: props.phone })
          if (!res?.unified_client_id) {
            setBusy(false)
            setErr('Could not create contact — try the Contacts view')
            return
          }
          props.onCreated(res.unified_client_id)
        }}
      >{busy ? 'Creating…' : 'Save'}</button>
      <button className="integ-call-create-cancel" disabled={busy} onClick={props.onCancel}>Cancel</button>
      {err && <span style={{ color: '#7f1d1d', fontSize: '0.74rem' }}>{err}</span>}
    </>
  )
}

// ShiftClockPanel — the internal shift-clock side of the Timesheets view.
// Live "who's on the clock" board (work = green, lunch/break = amber) plus
// the week's punch segments. Read-only here; corrections + schedules live
// in Settings → Time Clock (onManage). Data comes from the admin/office-
// gated /api/crm/time-clock/overview — others see a permission notice.
function ShiftClockPanel(props: { onManage?: () => void }) {
  const NY = 'America/New_York'
  const [weekOffset, setWeekOffset] = useState(0)
  // Collapsible per-day groups in the week table — start with EVERY day
  // collapsed (empty Set). Click a day header to expand its punch rows.
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set())
  const toggleDay = (k: string) => setExpandedDays(s => {
    const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n
  })
  // NY-tz day bucket key ("YYYY-MM-DD") — must be computed in NY wall time,
  // not the browser's, or punches near midnight land in the wrong day.
  const nyDayKey = (iso: string) => new Intl.DateTimeFormat('en-CA', {
    timeZone: NY, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date(iso))
  const [punches, setPunches] = useState<TimeClockPunch[]>([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  // Pending correction requests from employees (My Time page) awaiting
  // admin review. Approving applies the fix server-side.
  const [corrections, setCorrections] = useState<TimeClockCorrectionRequest[]>([])
  const [reviewBusy, setReviewBusy] = useState<number | null>(null)
  const [correctionsTick, setCorrectionsTick] = useState(0)
  // Inline punch correction — same editor as Settings → Time Clock so an
  // admin can fix an over-long / wrong entry right from the week table.
  const [editPunch, setEditPunch] = useState<TimeClockPunch | null>(null)
  const [editIn, setEditIn] = useState('')
  const [editOut, setEditOut] = useState('')
  // Duration in hours, kept in sync with in/out both ways: editing in/out
  // recomputes it; typing a new number moves the clock-out to in + hours.
  const [editHours, setEditHours] = useState('')
  const [editNote, setEditNote] = useState('')
  const [editSaving, setEditSaving] = useState(false)
  const [editErr, setEditErr] = useState<string | null>(null)

  /** ISO → value for <input type="datetime-local"> in NY wall time. */
  const isoToNyLocal = (iso: string): string => {
    const p = new Intl.DateTimeFormat('en-CA', {
      timeZone: NY, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(new Date(iso))
    const g = (t: string) => p.find(x => x.type === t)?.value || '00'
    const hh = g('hour') === '24' ? '00' : g('hour')
    return `${g('year')}-${g('month')}-${g('day')}T${hh}:${g('minute')}`
  }
  /** NY wall time "YYYY-MM-DDTHH:mm" → UTC ISO. Iterates against the
   *  Intl-based isoToNyLocal so it's correct in ANY browser timezone — the
   *  old `new Date(d.toLocaleString(tz))` trick measured the offset against
   *  the BROWSER's zone, so in a Florida browser it returned the wall time
   *  as UTC (a silent 4–5h shift on every saved correction). Mirrors
   *  nyLocalToIso in TimeClock.tsx + MyTime.tsx. */
  const nyLocalToIso = (local: string): string => {
    const [datePart, timePart] = local.split('T')
    const [y, m, d] = datePart.split('-').map(Number)
    const [hh, mi] = timePart.split(':').map(Number)
    const wallUtc = Date.UTC(y, m - 1, d, hh, mi)
    let guess = wallUtc
    for (let i = 0; i < 3; i++) {
      const back = isoToNyLocal(new Date(guess).toISOString())
      const [bd, bt] = back.split('T')
      const [by, bm, bdd] = bd.split('-').map(Number)
      const [bh, bmi] = bt.split(':').map(Number)
      const diff = wallUtc - Date.UTC(by, bm - 1, bdd, bh, bmi)
      if (diff === 0) break
      guess += diff
    }
    return new Date(guess).toISOString()
  }

  const nyWeekStart = (offsetWeeks: number): Date => {
    const now = new Date()
    const ny = new Date(now.toLocaleString('en-US', { timeZone: NY }))
    const offsetMs = now.getTime() - ny.getTime()
    ny.setHours(0, 0, 0, 0)
    ny.setDate(ny.getDate() - ny.getDay() + offsetWeeks * 7)
    return new Date(ny.getTime() + offsetMs)
  }
  const weekStart = nyWeekStart(weekOffset)
  const weekEnd = new Date(weekStart.getTime() + 7 * 86400_000)
  const fmtT = (iso: string) => new Date(iso).toLocaleTimeString('en-US', { timeZone: NY, hour: 'numeric', minute: '2-digit' })
  const fmtD = (iso: string) => new Date(iso).toLocaleDateString('en-US', { timeZone: NY, weekday: 'short', month: 'short', day: 'numeric' })

  useEffect(() => {
    let alive = true
    setLoading(true)
    tcOverview(weekStart.toISOString(), weekEnd.toISOString())
      .then(r => { if (!alive) return; setPunches(r.punches); setErr(null) })
      .catch(e => { if (alive) setErr(String(e?.message || e)) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekOffset])

  useEffect(() => {
    let alive = true
    tcListCorrections('pending')
      .then(r => { if (alive) setCorrections(r.requests) })
      .catch(() => {})
    return () => { alive = false }
  }, [correctionsTick])

  const reloadPunches = () =>
    tcOverview(weekStart.toISOString(), weekEnd.toISOString()).then(r => setPunches(r.punches)).catch(() => {})

  const reviewCorrection = async (id: number, approve: boolean) => {
    if (reviewBusy != null) return
    const note = approve ? undefined : (window.prompt('Reason for denying (shown to the employee):') || undefined)
    if (!approve && note === undefined) return
    setReviewBusy(id)
    try {
      await tcReviewCorrection(id, approve, note)
      setCorrectionsTick(t => t + 1)
      // Re-pull punches too — an approval may have changed them.
      reloadPunches()
    } catch (e: any) {
      setErr(e?.message || 'Review failed')
    } finally {
      setReviewBusy(null)
    }
  }

  /** Hours between two datetime-local values (DST-correct via ISO), as a
   *  display string ("8" / "8.5" / "20.98"), or '' when not computable. */
  const hoursBetween = (inLocal: string, outLocal: string): string => {
    if (!inLocal || !outLocal) return ''
    try {
      const h = (Date.parse(nyLocalToIso(outLocal)) - Date.parse(nyLocalToIso(inLocal))) / 3600000
      if (!Number.isFinite(h)) return ''
      return String(Math.round(h * 100) / 100)
    } catch { return '' }
  }
  const setInSynced = (v: string) => { setEditIn(v); setEditHours(hoursBetween(v, editOut)) }
  const setOutSynced = (v: string) => { setEditOut(v); setEditHours(hoursBetween(editIn, v)) }

  // Split date + time fields instead of a native datetime-local — the
  // browser's combined picker (calendar + minute wheel) is clunky. Value
  // stays "YYYY-MM-DDTHH:mm"; clearing either part clears the whole value
  // (for clock-out that reads as "still clocked in").
  // Render helper (NOT a component — a component defined inside the panel
  // would remount the inputs on every keystroke and drop focus).
  const tcFieldStyle: React.CSSProperties = { padding: '7px 9px', border: '1px solid #cbd6e2', borderRadius: 6, fontSize: '0.82rem', color: '#1e293b', background: '#fff', minWidth: 0, fontFamily: 'inherit' }
  const renderDateTime = (value: string, onChange: (v: string) => void, fallbackDate: string, fallbackTime: string) => {
    const [d, t] = value ? value.split('T') : ['', '']
    return (
      <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
        <input
          type="date"
          value={d}
          onChange={e => { const nd = e.target.value; onChange(nd ? `${nd}T${t || fallbackTime}` : '') }}
          style={{ ...tcFieldStyle, flex: 1.3 }}
        />
        <input
          type="time"
          value={t}
          onChange={e => { const nt = e.target.value; onChange(nt ? `${d || fallbackDate}T${nt}` : '') }}
          style={{ ...tcFieldStyle, flex: 1 }}
        />
      </div>
    )
  }
  const setHoursSynced = (v: string) => {
    setEditHours(v)
    const h = parseFloat(v)
    if (!editIn || !Number.isFinite(h) || h < 0) return
    try { setEditOut(isoToNyLocal(new Date(Date.parse(nyLocalToIso(editIn)) + h * 3600000).toISOString())) } catch { /* keep prior out */ }
  }

  const openPunchEditor = (p: TimeClockPunch) => {
    setEditErr(null)
    setEditPunch(p)
    const inLocal = isoToNyLocal(p.clock_in)
    const outLocal = p.clock_out ? isoToNyLocal(p.clock_out) : ''
    setEditIn(inLocal)
    setEditOut(outLocal)
    setEditHours(hoursBetween(inLocal, outLocal))
    setEditNote(p.note || '')
  }
  const savePunchEdit = async () => {
    if (!editPunch || editSaving) return
    if (!editIn) { setEditErr('Clock in date & time are required.'); return }
    setEditSaving(true)
    setEditErr(null)
    try {
      await tcUpdateEntry(editPunch.id, {
        clockIn: nyLocalToIso(editIn),
        clockOut: editOut ? nyLocalToIso(editOut) : null,
        note: editNote || null,
      })
      setEditPunch(null)
      reloadPunches()
    } catch (e: any) {
      setEditErr(e?.message || 'Save failed')
    } finally {
      setEditSaving(false)
    }
  }
  const deletePunchEdit = async () => {
    if (!editPunch || editSaving) return
    if (!window.confirm('Delete this punch entirely?')) return
    setEditSaving(true)
    try {
      await tcDeleteEntry(editPunch.id)
      setEditPunch(null)
      reloadPunches()
    } catch (e: any) {
      setEditErr(e?.message || 'Delete failed')
    } finally {
      setEditSaving(false)
    }
  }

  const KIND_CHIP: Record<string, { label: string; bg: string; fg: string }> = {
    lunch: { label: 'Lunch', bg: '#fef3c7', fg: '#92400e' },
    break: { label: 'Break', bg: '#dbeafe', fg: '#1d4ed8' },
  }
  const FLAGS: Record<string, { label: string; bg: string; fg: string }> = {
    late: { label: 'Late', bg: '#fee2e2', fg: '#b91c1c' },
    early: { label: 'Early', bg: '#dbeafe', fg: '#1d4ed8' },
    early_out: { label: 'Early out', bg: '#fef3c7', fg: '#92400e' },
    overtime: { label: 'Overtime', bg: '#ede9fe', fg: '#6d28d9' },
    off_schedule: { label: 'Off schedule', bg: '#e2e8f0', fg: '#475569' },
  }
  const chip = (m: { label: string; bg: string; fg: string } | undefined) =>
    m ? <span style={{ fontSize: '0.64rem', fontWeight: 700, padding: '1px 6px', borderRadius: 3, background: m.bg, color: m.fg, marginLeft: 4, whiteSpace: 'nowrap' }}>{m.label}</span> : null

  // Per-punch location: a Google-Maps link + "±Nm" accuracy badge. Coords are
  // mandatory on every shift event (migration 183); old rows show nothing.
  const geoLink = (lat: number | null | undefined, lng: number | null | undefined, acc: number | null | undefined, label: string) => {
    const url = punchMapsUrl(lat, lng)
    if (!url) return null
    const ac = punchAccuracyLabel(acc)
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
        title="View this punch's location on Google Maps"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: '0.66rem', color: '#2563eb', textDecoration: 'none', whiteSpace: 'nowrap' }}>
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
        {label}{ac ? ` ${ac}` : ''}
      </a>
    )
  }

  const live = punches.filter(p => !p.clock_out)
  const rows = [...punches].sort((a, b) => b.clock_in.localeCompare(a.clock_in))
  // Group the (already newest-first) rows into per-day buckets. Because rows
  // are sorted desc by clock_in, days come out newest-first too.
  const dayGroups: { key: string; label: string; punches: TimeClockPunch[]; totalMins: number }[] = []
  for (const p of rows) {
    const key = nyDayKey(p.clock_in)
    let g = dayGroups.find(x => x.key === key)
    if (!g) { g = { key, label: fmtD(p.clock_in), punches: [], totalMins: 0 }; dayGroups.push(g) }
    g.punches.push(p)
    if (p.clock_out) g.totalMins += Math.max(0, Math.round((Date.parse(p.clock_out) - Date.parse(p.clock_in)) / 60000))
  }
  const fmtMins = (mins: number) => `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, '0')}m`
  const weekLabel = `${weekStart.toLocaleDateString('en-US', { timeZone: NY, month: 'short', day: 'numeric' })} – ${new Date(weekEnd.getTime() - 86400_000).toLocaleDateString('en-US', { timeZone: NY, month: 'short', day: 'numeric' })}`

  if (err && /403|permission/i.test(err)) {
    return <div style={{ padding: 36, textAlign: 'center', color: '#64748b', fontSize: '0.84rem' }}>The shift clock board is visible to admins and office staff only.</div>
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Live board */}
      <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', padding: '12px 14px' }}>
        <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.3, fontWeight: 600, color: '#64748b', marginBottom: 8 }}>On the clock right now</div>
        {loading && live.length === 0 ? (
          <div style={{ color: '#94a3b8', fontSize: '0.82rem' }}>Loading…</div>
        ) : live.length === 0 ? (
          <div style={{ color: '#94a3b8', fontSize: '0.82rem' }}>No one is clocked in right now.</div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {live.map(p => {
              const onBreak = p.kind === 'lunch' || p.kind === 'break'
              return (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', borderRadius: 20, border: `1px solid ${onBreak ? '#fde68a' : '#bbf7d0'}`, background: onBreak ? '#fffbeb' : '#f0fdf4' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: onBreak ? '#f59e0b' : '#22c55e' }} />
                  <span style={{ fontWeight: 600, fontSize: '0.82rem', color: '#1e293b' }}>{p.user_name || `User #${p.user_id}`}</span>
                  <span style={{ fontSize: '0.74rem', color: '#64748b' }}>
                    {onBreak ? (p.kind === 'lunch' ? 'lunch' : 'break') : 'in'} since {fmtT(p.clock_in)} · <TcElapsed since={p.clock_in} />
                  </span>
                  {chip(FLAGS[p.in_flag || ''])}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Pending correction requests (from employees' My Time page) */}
      {corrections.length > 0 && (
        <div style={{ border: '1px solid #fde68a', borderRadius: 6, background: '#fffbeb', padding: '12px 14px' }}>
          <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.3, fontWeight: 600, color: '#92400e', marginBottom: 8 }}>
            Correction requests · {corrections.length} pending
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {corrections.map(r => (
              <div key={r.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, background: '#fff', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 12px', fontSize: '0.82rem' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: '#1e293b' }}>
                    {r.user_name || `User #${r.user_id}`} · {r.punch_id ? 'fix punch' : 'missed punch'} → {fmtD(r.requested_clock_in)} {fmtT(r.requested_clock_in)}{r.requested_clock_out ? ` – ${fmtT(r.requested_clock_out)}` : ''}
                  </div>
                  {r.punch_id && r.punch_clock_in && (
                    <div style={{ color: '#94a3b8', fontSize: '0.74rem', marginTop: 1 }}>
                      Currently: {fmtT(r.punch_clock_in)} – {r.punch_clock_out ? fmtT(r.punch_clock_out) : 'open'}
                    </div>
                  )}
                  <div style={{ color: '#64748b', marginTop: 2 }}>{r.reason}</div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button type="button" disabled={reviewBusy != null} onClick={() => reviewCorrection(r.id, true)}
                    style={{ padding: '5px 12px', borderRadius: 5, border: 'none', background: '#2fb644', color: '#fff', fontWeight: 600, fontSize: '0.76rem', cursor: 'pointer' }}>
                    {reviewBusy === r.id ? '…' : 'Approve'}
                  </button>
                  <button type="button" disabled={reviewBusy != null} onClick={() => reviewCorrection(r.id, false)}
                    style={{ padding: '5px 12px', borderRadius: 5, border: '1px solid #fecaca', background: '#fef2f2', color: '#b91c1c', fontWeight: 600, fontSize: '0.76rem', cursor: 'pointer' }}>
                    Deny
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Week controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button type="button" onClick={() => setWeekOffset(w => w - 1)} style={{ padding: '4px 10px', border: '1px solid #cbd6e2', borderRadius: 4, background: '#fff', cursor: 'pointer', fontWeight: 600 }}>‹</button>
        <strong style={{ fontSize: '0.84rem', color: '#33475b', minWidth: 150, textAlign: 'center' }}>{weekLabel}</strong>
        <button type="button" onClick={() => setWeekOffset(w => w + 1)} disabled={weekOffset >= 0} style={{ padding: '4px 10px', border: '1px solid #cbd6e2', borderRadius: 4, background: weekOffset >= 0 ? '#f1f5f9' : '#fff', cursor: weekOffset >= 0 ? 'default' : 'pointer', fontWeight: 600, color: weekOffset >= 0 ? '#94a3b8' : undefined }}>›</button>
        {loading && <span style={{ color: '#94a3b8', fontSize: '0.78rem' }}>Loading…</span>}
        <div style={{ flex: 1 }} />
        {dayGroups.length > 0 && (() => {
          const allExpanded = dayGroups.every(g => expandedDays.has(g.key))
          return (
            <button
              type="button"
              onClick={() => allExpanded
                ? setExpandedDays(new Set())
                : setExpandedDays(new Set(dayGroups.map(g => g.key)))}
              style={{ padding: '4px 10px', background: '#fff', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.75rem', cursor: 'pointer', color: '#475569', fontWeight: 500 }}
            >
              {allExpanded ? 'Collapse all' : 'Expand all'}
            </button>
          )
        })()}
        {props.onManage && (
          <button type="button" onClick={props.onManage} style={{ padding: '6px 10px', background: '#fff', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.78rem', cursor: 'pointer', color: '#334155', fontWeight: 500 }}>
            Schedules &amp; corrections
          </button>
        )}
      </div>

      {err && !/403|permission/i.test(err) && (
        <div style={{ padding: '8px 12px', background: '#fdedee', color: '#d94c53', borderRadius: 4, fontSize: '0.8rem' }}>{err}</div>
      )}

      {/* Week punches */}
      <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', overflow: 'auto' }}>
        <table className="hs-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8fafc', fontSize: '0.72rem', textTransform: 'uppercase', color: '#64748b', letterSpacing: 0.3, fontWeight: 600 }}>
              <th style={{ padding: '8px 10px', borderBottom: '1px solid #e2e8f0', width: 28 }} />
              <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Employee</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Type</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>In / Out</th>
              <th style={{ padding: '8px 10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>Duration</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Flags</th>
              <th style={{ padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }} />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={7} style={{ padding: 36, textAlign: 'center', color: '#64748b', fontSize: '0.82rem' }}>No shift punches this week.</td></tr>
            ) : dayGroups.map(g => {
              const open = expandedDays.has(g.key)
              return (
                <React.Fragment key={g.key}>
                  {/* Day header — click to expand/collapse this day's punches */}
                  <tr
                    onClick={() => toggleDay(g.key)}
                    style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', cursor: 'pointer', userSelect: 'none' }}
                  >
                    <td colSpan={7} style={{ padding: '8px 10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transform: open ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s ease' }}><polyline points="9 18 15 12 9 6" /></svg>
                        <span style={{ fontWeight: 700, fontSize: '0.82rem', color: '#1e293b' }}>{g.label}</span>
                        <span style={{ fontSize: '0.74rem', color: '#94a3b8', fontWeight: 500 }}>· {g.punches.length} {g.punches.length === 1 ? 'entry' : 'entries'}</span>
                        <div style={{ flex: 1 }} />
                        <span style={{ fontSize: '0.78rem', color: '#475569', fontWeight: 600, whiteSpace: 'nowrap' }}>{fmtMins(g.totalMins)}</span>
                      </div>
                    </td>
                  </tr>
                  {open && g.punches.map(p => {
                    const mins = p.clock_out ? Math.max(0, Math.round((Date.parse(p.clock_out) - Date.parse(p.clock_in)) / 60000)) : null
                    return (
                      <tr key={p.id} style={{ borderBottom: '1px solid #f1f5f9', fontSize: '0.82rem' }}>
                        <td style={{ padding: '7px 10px', width: 28 }} />
                        <td style={{ padding: '7px 10px', fontWeight: 600, color: '#1e293b' }}>{p.user_name || `User #${p.user_id}`}{p.edited_at && <span title="Corrected by an admin" style={{ marginLeft: 4, color: '#94a3b8' }}>✎</span>}</td>
                        <td style={{ padding: '7px 10px' }}>{p.kind === 'lunch' || p.kind === 'break' ? chip(KIND_CHIP[p.kind]) : <span style={{ color: '#475569' }}>Work</span>}</td>
                        <td style={{ padding: '7px 10px', whiteSpace: 'nowrap' }}>
                          <div>{fmtT(p.clock_in)} – {p.clock_out ? fmtT(p.clock_out) : <span style={{ color: '#16a34a', fontWeight: 700 }}>now</span>}</div>
                          {(punchMapsUrl(p.clock_in_lat, p.clock_in_lng) || punchMapsUrl(p.clock_out_lat, p.clock_out_lng)) && (
                            <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                              {geoLink(p.clock_in_lat, p.clock_in_lng, p.clock_in_accuracy_m, 'In')}
                              {geoLink(p.clock_out_lat, p.clock_out_lng, p.clock_out_accuracy_m, 'Out')}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '7px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>{mins != null ? fmtMins(mins) : '—'}</td>
                        <td style={{ padding: '7px 10px' }}>{chip(FLAGS[p.in_flag || ''])}{chip(FLAGS[p.out_flag || ''])}</td>
                        <td style={{ padding: '7px 10px', textAlign: 'right' }}>
                          <button type="button" onClick={() => openPunchEditor(p)} title="Correct this punch" style={{ padding: '3px 6px', background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </React.Fragment>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Punch correction modal — same editor as Settings → Time Clock.
          No backdrop-click close — drag-selecting text in an input and
          releasing outside the card must NOT dismiss the modal. Close is
          explicit: Save / Delete / Cancel. */}
      {editPunch && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)', zIndex: 10010, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: 20, width: 380, boxShadow: '0 10px 30px rgba(0,0,0,0.25)' }}>
            <h3 style={{ margin: '0 0 4px', fontSize: '1rem', color: '#33475b' }}>Correct punch — {editPunch.user_name || `User #${editPunch.user_id}`}</h3>
            <p style={{ margin: '0 0 12px', fontSize: '11.5px', color: '#64748b' }}>Times are Florida time. Corrections are audit-logged and re-flagged against the employee's schedule.</p>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: 10 }}>
              Clock in
              {renderDateTime(editIn, setInSynced, isoToNyLocal(new Date().toISOString()).split('T')[0], '08:00')}
            </div>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: 10 }}>
              Clock out <span style={{ fontWeight: 400, color: '#94a3b8' }}>(clear the time = still clocked in)</span>
              {renderDateTime(editOut, setOutSynced, editIn ? editIn.split('T')[0] : isoToNyLocal(new Date().toISOString()).split('T')[0], '17:00')}
            </div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: 10 }}>
              Duration (hours) <span style={{ fontWeight: 400, color: '#94a3b8' }}>— changing this moves the clock-out</span>
              <input type="number" min="0" step="0.25" value={editHours} onChange={(e) => setHoursSynced(e.target.value)} placeholder="e.g. 8" style={{ ...tcFieldStyle, display: 'block', width: '100%', marginTop: 4 }} />
            </label>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: 12 }}>
              Note
              <input type="text" value={editNote} onChange={(e) => setEditNote(e.target.value)} placeholder="Why was this corrected?" style={{ ...tcFieldStyle, display: 'block', width: '100%', marginTop: 4 }} />
            </label>
            {editErr && <div style={{ marginBottom: 10, fontSize: '12px', color: '#dc2626' }}>{editErr}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={savePunchEdit} disabled={editSaving} style={{ flex: 1, padding: '8px 0', borderRadius: 5, border: 'none', background: '#2fb644', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>{editSaving ? 'Saving…' : 'Save'}</button>
              <button type="button" onClick={deletePunchEdit} disabled={editSaving} style={{ padding: '8px 12px', borderRadius: 5, border: '1px solid #fecaca', background: '#fef2f2', color: '#b91c1c', fontWeight: 600, cursor: 'pointer' }}>Delete</button>
              <button type="button" onClick={() => setEditPunch(null)} disabled={editSaving} style={{ padding: '8px 12px', borderRadius: 5, border: '1px solid #cbd6e2', background: '#fff', color: '#516f90', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function TimeEntriesInlinePanel(props: {
  currentUserRole: string
  currentUserEmail: string
  currentUserId: number
  // Drives the inline filter dropdown — when supplied, the parent's tab control
  // owns the value. Defaults to "pending" to match prior behavior.
  initialApproved?: 'all' | 'pending' | 'approved'
  // Stringified marker the parent bumps to ask the panel to refresh (e.g.
  // after the parent fetches new counts). Cheap deps array entry.
  refreshSignal?: string
  // Fired after every approve / unapprove / bulk-approve so the parent can
  // re-fetch the sidebar chip count without polling.
  onAfterApprovalChange?: () => void
}) {
  const { currentUserRole, currentUserEmail, currentUserId, initialApproved, refreshSignal, onAfterApprovalChange } = props
  const isAdmin = currentUserRole === 'admin' || currentUserRole === 'Super Admin' || (
    typeof currentUserEmail === 'string' && ['mritchie@botpros.ai', 'agent@botpros.ai'].includes(currentUserEmail.toLowerCase())
  )
  const [filterActorType, setFilterActorType] = useState<'' | 'employee' | 'subcontractor'>('')
  const [filterCategory, setFilterCategory] = useState<'' | TimeCategory>('')
  const [filterApproved, setFilterApproved] = useState<'all' | 'pending' | 'approved'>(initialApproved || 'pending')
  // Keep the inline filter in sync with the parent tab control.
  useEffect(() => {
    if (initialApproved && initialApproved !== filterApproved) {
      setFilterApproved(initialApproved)
      setPage(0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialApproved])
  const [filterFrom, setFilterFrom] = useState<string>('')
  const [filterTo, setFilterTo] = useState<string>('')
  const [entries, setEntries] = useState<TimeEntry[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const pageSize = 50
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editDraft, setEditDraft] = useState<{ notes: string; payroll_category: TimeCategory; billable: boolean }>({ notes: '', payroll_category: 'field', billable: true })
  const [err, setErr] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<number | null>(null)

  const buildFilters = (): TimeListFilters => ({
    actor_type: filterActorType || null,
    payroll_category: filterCategory || null,
    approved: filterApproved,
    from: filterFrom ? `${filterFrom}T00:00:00` : null,
    to: filterTo ? `${filterTo}T23:59:59` : null,
    limit: pageSize,
    offset: page * pageSize,
  })
  const load = async () => {
    setLoading(true); setErr(null)
    try {
      const r = await listTimeEntries(buildFilters())
      setEntries(r.entries || [])
      setTotal(r.total || 0)
    } catch (e: any) {
      setErr(e?.message || 'Failed to load')
    } finally { setLoading(false) }
  }
  // Reload on any filter change OR when the parent's refreshSignal flips.
  useEffect(() => { load() }, [filterActorType, filterCategory, filterApproved, filterFrom, filterTo, page, refreshSignal]) // eslint-disable-line react-hooks/exhaustive-deps

  const startEdit = (e: TimeEntry) => { setEditingId(e.id); setEditDraft({ notes: e.notes || '', payroll_category: e.payroll_category, billable: !!e.billable }) }
  const saveEdit = async () => {
    if (editingId == null) return
    setBusyId(editingId)
    try {
      const updated = await patchTimeEntry(editingId, editDraft)
      setEntries(list => list.map(x => x.id === updated.id ? updated : x))
      setEditingId(null)
    } catch (e: any) { setErr(e?.message || 'Failed to save') } finally { setBusyId(null) }
  }
  const toggleApprove = async (entry: TimeEntry) => {
    setBusyId(entry.id)
    try {
      const updated = entry.approved_at ? await unapproveTimeEntry(entry.id) : await approveTimeEntry(entry.id, null)
      setEntries(list => list.map(x => x.id === updated.id ? updated : x))
      onAfterApprovalChange?.()
    } catch (e: any) { setErr(e?.message || 'Failed') } finally { setBusyId(null) }
  }
  const remove = async (entry: TimeEntry) => {
    if (!window.confirm('Delete this time entry? This cannot be undone.')) return
    setBusyId(entry.id)
    try {
      await deleteTimeEntry(entry.id)
      setEntries(list => list.filter(x => x.id !== entry.id))
      setSelectedIds(s => { const n = new Set(s); n.delete(entry.id); return n })
      onAfterApprovalChange?.()
    } catch (e: any) { setErr(e?.message || 'Failed') } finally { setBusyId(null) }
  }
  const bulkApprove = async () => {
    const ids = [...selectedIds]
    if (ids.length === 0) return
    try {
      await bulkApproveTime(ids)
      setSelectedIds(new Set())
      load()
      onAfterApprovalChange?.()
    } catch (e: any) { setErr(e?.message || 'Bulk approve failed') }
  }
  const downloadCsv = () => {
    const url = timeExportCsvUrl(buildFilters())
    const a = document.createElement('a'); a.href = url; a.rel = 'noopener'
    document.body.appendChild(a); a.click(); a.remove()
  }
  const pageCount = Math.max(1, Math.ceil(total / pageSize))
  const canEditRow = (e: TimeEntry): boolean => {
    if (isAdmin) return true
    if (e.actor_type === 'employee' && Number(e.actor_id) === Number(currentUserId)) return true
    return false
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Filter row */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <select value={filterActorType} onChange={e => { setFilterActorType(e.target.value as any); setPage(0) }} style={{ padding: '4px 8px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.82rem' }}>
          <option value="">All actors</option>
          <option value="employee">Employees</option>
          <option value="subcontractor">Subcontractors</option>
        </select>
        <select value={filterCategory} onChange={e => { setFilterCategory(e.target.value as any); setPage(0) }} style={{ padding: '4px 8px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.82rem' }}>
          <option value="">All categories</option>
          {(['field','admin','drive-time','pto','overtime','other'] as TimeCategory[]).map(c => <option key={c} value={c}>{TIME_CATEGORY_LABELS[c]}</option>)}
        </select>
        <select value={filterApproved} onChange={e => { setFilterApproved(e.target.value as any); setPage(0) }} style={{ padding: '4px 8px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.82rem' }}>
          <option value="all">All statuses</option>
          <option value="pending">Pending approval</option>
          <option value="approved">Approved</option>
        </select>
        <label style={{ fontSize: '0.78rem', color: '#64748b' }}>From <input type="date" value={filterFrom} onChange={e => { setFilterFrom(e.target.value); setPage(0) }} style={{ padding: '3px 6px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.82rem', marginLeft: 4 }} /></label>
        <label style={{ fontSize: '0.78rem', color: '#64748b' }}>To <input type="date" value={filterTo} onChange={e => { setFilterTo(e.target.value); setPage(0) }} style={{ padding: '3px 6px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.82rem', marginLeft: 4 }} /></label>
        <div style={{ flex: 1 }} />
        <button onClick={downloadCsv} style={{ padding: '6px 10px', background: '#fff', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.78rem', cursor: 'pointer', color: '#334155', fontWeight: 500 }}>Export CSV</button>
        {isAdmin && selectedIds.size > 0 && (
          <button onClick={bulkApprove} style={{ padding: '6px 10px', background: '#0b8f3a', border: '1px solid #0b8f3a', borderRadius: 4, fontSize: '0.78rem', cursor: 'pointer', color: '#fff', fontWeight: 600 }}>Approve {selectedIds.size}</button>
        )}
      </div>

      {err && <div style={{ padding: '8px 10px', background: '#fde7e9', color: '#c5221f', borderRadius: 4, fontSize: '0.78rem' }}>{err}</div>}

      <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', overflow: 'auto' }}>
        <table className="hs-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8fafc', fontSize: '0.72rem', textTransform: 'uppercase', color: '#64748b', letterSpacing: 0.3, fontWeight: 600 }}>
              {isAdmin && (
                <th style={{ width: 30, padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>
                  <input type="checkbox" checked={entries.length > 0 && entries.every(e => selectedIds.has(e.id))} onChange={e => {
                    const next = new Set(selectedIds)
                    if (e.target.checked) entries.forEach(x => next.add(x.id))
                    else entries.forEach(x => next.delete(x.id))
                    setSelectedIds(next)
                  }} />
                </th>
              )}
              <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Date</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Actor</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Deal</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>In / Out</th>
              <th style={{ padding: '8px 10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>Duration</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Category</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Notes</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Status</th>
              <th style={{ padding: '8px 10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={isAdmin ? 10 : 9} style={{ padding: 24, textAlign: 'center', color: '#64748b', fontSize: '0.82rem' }}>Loading…</td></tr>}
            {!loading && entries.length === 0 && (
              <tr><td colSpan={isAdmin ? 10 : 9} style={{ padding: 36, textAlign: 'center', color: '#64748b', fontSize: '0.82rem' }}>
                {filterApproved === 'pending' && !isAdmin
                  ? 'You have no pending approvals on your deals right now. Nice.'
                  : filterApproved === 'pending' && isAdmin
                    ? 'No pending approvals across the org. Nice.'
                    : !isAdmin
                      ? "No time entries on the deals you own match these filters."
                      : 'No time entries match these filters.'}
              </td></tr>
            )}
            {!loading && entries.map(e => {
              const editing = editingId === e.id
              const cat = TIME_CATEGORY_COLORS[e.payroll_category] || TIME_CATEGORY_COLORS.other
              const canEditThisRow = canEditRow(e)
              const open = !e.clock_out_at
              return (
                <tr key={e.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  {isAdmin && (
                    <td style={{ padding: '8px 10px' }}>
                      <input type="checkbox" checked={selectedIds.has(e.id)} onChange={ev => setSelectedIds(s => { const n = new Set(s); if (ev.target.checked) n.add(e.id); else n.delete(e.id); return n })} />
                    </td>
                  )}
                  <td style={{ padding: '8px 10px', fontSize: '0.78rem', color: '#334155', whiteSpace: 'nowrap' }}>{formatDateShort(e.clock_in_at)}</td>
                  <td style={{ padding: '8px 10px', fontSize: '0.78rem' }}>
                    <div style={{ color: '#0f172a', fontWeight: 500 }}>{e.actor_name || 'Unknown'}</div>
                    <div style={{ fontSize: '0.68rem', color: '#94a3b8' }}>{e.actor_type === 'subcontractor' ? 'Subcontractor' : 'Employee'}</div>
                  </td>
                  <td style={{ padding: '8px 10px', fontSize: '0.78rem', color: '#475569' }}>
                    {e.deal_id ? (
                      <button onClick={() => { window.location.hash = `/deals/record/${e.deal_id}` }} style={{ background: 'none', border: 'none', padding: 0, color: '#1976d2', cursor: 'pointer', fontSize: 'inherit' }}>
                        #{e.deal_id} {e.deal_title || ''}
                      </button>
                    ) : <span style={{ color: '#94a3b8' }}>—</span>}
                  </td>
                  <td style={{ padding: '8px 10px', fontSize: '0.78rem', color: '#475569', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {formatTimeOfDay(e.clock_in_at)} → {e.clock_out_at ? formatTimeOfDay(e.clock_out_at) : <span style={{ color: '#137333', fontWeight: 600 }}>open</span>}
                  </td>
                  <td style={{ padding: '8px 10px', fontSize: '0.78rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {e.duration_minutes != null ? formatMinutesLabel(e.duration_minutes) : <span style={{ color: '#94a3b8' }}>—</span>}
                  </td>
                  <td style={{ padding: '8px 10px' }}>
                    {editing ? (
                      <select value={editDraft.payroll_category} onChange={ev => setEditDraft(d => ({ ...d, payroll_category: ev.target.value as TimeCategory }))} style={{ padding: '2px 4px', fontSize: '0.78rem' }}>
                        {(['field','admin','drive-time','pto','overtime','other'] as TimeCategory[]).map(c => <option key={c} value={c}>{TIME_CATEGORY_LABELS[c]}</option>)}
                      </select>
                    ) : (
                      <span style={{ padding: '2px 8px', background: cat.bg, color: cat.fg, borderRadius: 10, fontSize: '0.7rem', fontWeight: 500 }}>{TIME_CATEGORY_LABELS[e.payroll_category]}</span>
                    )}
                  </td>
                  <td style={{ padding: '8px 10px', fontSize: '0.78rem', color: '#475569', maxWidth: 280 }}>
                    {editing ? (
                      <input value={editDraft.notes} onChange={ev => setEditDraft(d => ({ ...d, notes: ev.target.value }))} style={{ width: '100%', padding: '3px 6px', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.78rem' }} />
                    ) : (
                      <span title={e.notes || ''} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.notes || <span style={{ color: '#94a3b8' }}>—</span>}</span>
                    )}
                  </td>
                  <td style={{ padding: '8px 10px' }}>
                    {open ? (
                      <span style={{ padding: '2px 8px', background: '#fef3c7', color: '#92400e', borderRadius: 10, fontSize: '0.7rem', fontWeight: 500 }}>Running</span>
                    ) : e.approved_at ? (
                      <span style={{ padding: '2px 8px', background: '#e6f4ea', color: '#137333', borderRadius: 10, fontSize: '0.7rem', fontWeight: 500 }}>Approved</span>
                    ) : (
                      <span style={{ padding: '2px 8px', background: '#fff4e5', color: '#b06000', borderRadius: 10, fontSize: '0.7rem', fontWeight: 500 }}>Pending</span>
                    )}
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {editing ? (
                      <>
                        <button onClick={saveEdit} disabled={busyId === e.id} style={{ padding: '3px 8px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, fontSize: '0.72rem', cursor: 'pointer', marginRight: 4 }}>Save</button>
                        <button onClick={() => setEditingId(null)} style={{ padding: '3px 8px', background: 'none', color: '#64748b', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.72rem', cursor: 'pointer' }}>Cancel</button>
                      </>
                    ) : (
                      <>
                        {canEditThisRow && !open && (
                          <button onClick={() => startEdit(e)} disabled={busyId === e.id} title="Edit" style={{ padding: '3px 6px', background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', marginRight: 2 }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </button>
                        )}
                        {!open && (isAdmin || (e.deal_id && !e.approved_at)) && (
                          <button onClick={() => toggleApprove(e)} disabled={busyId === e.id} style={{ padding: '3px 8px', background: e.approved_at ? '#fff' : '#0b8f3a', color: e.approved_at ? '#64748b' : '#fff', border: `1px solid ${e.approved_at ? '#cbd6e2' : '#0b8f3a'}`, borderRadius: 4, fontSize: '0.72rem', cursor: 'pointer', fontWeight: 500, marginRight: 4 }}>
                            {e.approved_at ? 'Unapprove' : 'Approve'}
                          </button>
                        )}
                        {isAdmin && (
                          <button onClick={() => remove(e)} disabled={busyId === e.id} title="Delete" style={{ padding: '3px 6px', background: 'none', border: 'none', cursor: 'pointer', color: '#c5221f' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"/></svg>
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.78rem', color: '#64748b' }}>
        <div>{total} entries</div>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={{ padding: '4px 10px', background: '#fff', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.78rem', cursor: page === 0 ? 'not-allowed' : 'pointer', opacity: page === 0 ? 0.5 : 1 }}>Prev</button>
          <span>Page {page + 1} of {pageCount}</span>
          <button onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))} disabled={page >= pageCount - 1} style={{ padding: '4px 10px', background: '#fff', border: '1px solid #cbd6e2', borderRadius: 4, fontSize: '0.78rem', cursor: page >= pageCount - 1 ? 'not-allowed' : 'pointer', opacity: page >= pageCount - 1 ? 0.5 : 1 }}>Next</button>
        </div>
      </div>
    </div>
  )
}

function AppWithErrorBoundary() {
  return <AppErrorBoundary><App /></AppErrorBoundary>
}
export default AppWithErrorBoundary

function buildCalendar(month: Date, entries: Meeting[]): CalendarDay[] {
  // Grid bounds come from calendarGridBounds — the SAME helper the calendar
  // data-fetch effect uses for its from/to range — so the rendered grid and the
  // fetched range share one source of truth and can never drift. gridStart is
  // the first cell (a Sunday, usually in the previous month); walk totalCells
  // days from it. Output (keys, dayNumber, inMonth, meetings) is unchanged.
  const { gridStart, totalCells } = calendarGridBounds(month)
  const calendar: CalendarDay[] = []

  for (let index = 0; index < totalCells; index += 1) {
    const date = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + index)
    const key = toDateKey(date)
    calendar.push({
      key,
      dayNumber: date.getDate(),
      inMonth: date.getMonth() === month.getMonth(),
      isToday: key === todayKey,
      // A multi-day ALL-DAY span (time_off / holiday / company-event with an
      // inclusive `endDate` after its start) renders on EVERY day in start..end.
      // Single-day and timed events stay keyed to their start day only.
      meetings: entries.filter((entry) =>
        entry.endDate && entry.endDate > entry.date
          ? entry.date <= key && key <= entry.endDate
          : entry.date === key,
      ),
    })
  }

  return calendar
}

function sortMeetings(items: Meeting[]) {
  return [...items].sort((left, right) => {
    const leftValue = `${left.date}T${left.time}:00`
    const rightValue = `${right.date}T${right.time}:00`
    return leftValue.localeCompare(rightValue)
  })
}

function parseDateKey(value: string) {
  return new Date(`${value}T00:00:00`)
}

function toDateKey(value: Date) {
  const year = value.getFullYear()
  const month = `${value.getMonth() + 1}`.padStart(2, '0')
  const day = `${value.getDate()}`.padStart(2, '0')
  return `${year}-${month}-${day}`
}


function decodeHexUtf8(text: string): string {
  return text.replace(/:([0-9a-f]{4,16}):/g, (match, hex) => {
    if (hex.length % 2 !== 0) return match
    try {
      const bytes = new Uint8Array(hex.length / 2)
      for (let i = 0; i < hex.length; i += 2) {
        bytes[i / 2] = parseInt(hex.substr(i, 2), 16)
      }
      return new TextDecoder().decode(bytes)
    } catch {
      return match
    }
  })
}

function formatTime12h(time: string) {
  const [h, m] = time.split(':').map(Number)
  const suffix = h >= 12 ? 'pm' : 'am'
  const hour = h % 12 || 12
  return m === 0 ? `${hour}${suffix}` : `${hour}:${String(m).padStart(2, '0')}${suffix}`
}

function healthClass(health: ProjectHealth) {
  if (health === 'On track') return 'stable'
  if (health === 'At risk') return 'watch'
  return 'urgent'
}

/* function timeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number)
  return h * 60 + m
} */
